/* eslint-disable no-unused-vars */

/* eslint-disable react-hooks/exhaustive-deps */

/* eslint-disable no-undef */

/* eslint-disable react-hooks/rules-of-hooks */

import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, createContext, useContext, useCallback } from 'react';

// Storage layer extracted into `src/storage/` as Session 1 of the Convex migration.

// The hook's return shape + save method signatures are unchanged from pre-extraction;

// every consumer of useStorage in this file continues to work identically.

// See CONVEX_MIGRATION.md §16 + §24.1 for the design + playbook.

import { useStorage } from './storage/useStorage';
import { loadCampaignSandboxState } from './storage/campaignSandboxStorage';
import { buildStorageSubscriptionPaths } from './storage/subscriptionManifests';
import { getEffectiveCampaignFeatures, getEffectiveCampaignHomebrew } from './campaignFeatureAccess';
import {
  RUNTIME_SCOPE_CAMPAIGN_SANDBOX,
  STORAGE_MODE_LOCAL_SANDBOX,
} from './campaignSandboxContract';
import {
  SANDBOX_DEMO_PRESET_CATEGORIES,
  buildCampaignSandboxBlankPersona,
  buildCampaignSandboxDemoPersona,
  normalizeCampaignSandboxSeedId,
  normalizeSandboxPresetCategories,
} from './campaignSandboxSeedBuilders';
import {
  CAMPAIGN_CHARACTER_TARGET_MODE_PLAYERS,
  CAMPAIGN_CHARACTER_TARGET_MODE_TEST,
  getAvailableCampaignCharacterTargetModes,
  getCampaignCharacterTargetIdsForMode,
  getCampaignCharacterTargetModeCopy,
  getCampaignCharacterTargetsForMode,
  isCampaignTestCharacter,
  pruneTargetIdsForMode,
  splitCampaignCharacterTargets,
} from './campaignCharacterTargets';
import {
  getActiveJournalSession,
  findJournalEntryByCategoryName,
  listJournalSessions,
  mergeJournalEntrySources,
  normalizeJournalNameKey,
  searchJournalEntries,
} from './journal/rowBackedJournalHelpers';

// Shared icon library — fetches the `iconLibrary` Convex table once and

// caches it module-wide. Replaces the inline base64 RPG_ICON_PACK; icons

// now stream from Convex storage at full resolution.

import { useIconLibrary, getIconByKey } from './storage/useIconLibrary';

// 2026-04-29: mobile redesign Phase A.0 — pilot primitives. AdjustButton

// is the canonical pattern for any +/- adjuster (HP, mana, food, item qty,

// ability uses); TweenedNumber animates HP/mana/etc. on change. Both

// safe to import from any panel — they no-op gracefully on desktop and

// respect prefers-reduced-motion.

import AdjustButton from './mobile/AdjustButton';

import TweenedNumber from './mobile/TweenedNumber';

import MobileStickyHeader from './mobile/MobileStickyHeader';

import MobileBottomNav from './mobile/MobileBottomNav';

import MobileBottomSheet from './mobile/MobileBottomSheet';

import MobileFAB from './mobile/MobileFAB';

import useChromeOffset from './mobile/useChromeOffset';

// 2026-05-11: mobile redesign Phase A.2a — long-press receipts (P5
// source-decomposed values made touch-first). useLongPress is a
// generic 400 ms hold detector; ReceiptSheet renders the decomposition
// inside a MobileBottomSheet. Imported here so ResourceBar's HP bar
// can be the pilot; other stat consumers (HomeCharacterSheet ability
// scores, AC, save mods) adopt incrementally in A.2 and A.3.

import useLongPress from './mobile/useLongPress';

import ReceiptSheet from './mobile/ReceiptSheet';

// 2026-05-11 — Session 7: PartialQtySheet replaces window.prompt() for
// item quantity input (the three transferXxx/depositXxx/takeXxx flows
// in InventoryPanelV2). useSwipeReveal is the gesture primitive for
// swipe-left → action panel on inventory rows (integration is
// scaffolded; visual action panel lands in 7.5 follow-up).

import PartialQtySheet from './mobile/PartialQtySheet';

// 2026-05-11 — Session 8: intent glyphs above combat tokens (Slay the
// Spire pattern) + pull-to-refresh on the Journal scroll container.
// IntentGlyph carries the kind-coded badge; computeIntents() derives
// the list of active intents per combatant from existing data
// (concentratingOn, pendingDamage, effectGroups). usePullToRefresh
// drives a proportional 360° indicator on vertical pull.

import IntentGlyph, { computeIntents } from './mobile/IntentGlyph';

import PullToRefreshContainer from './mobile/PullToRefreshContainer';

// 2026-05-11 — Session 11 polish: TweenedCurrency animates the shop
// header's wallet display so purchases feel like gold leaving the
// pocket, not a hard snap. Tweens the base integer and reformats
// per frame via the supplied formatter.

import TweenedCurrency from './mobile/TweenedCurrency';

import CrossFade from './mobile/CrossFade';

// 2026-04-29: bug-report system (beta feedback). First feature-sliced

// module under src/features/. The floating button mounts next to

// FloatingCombatIndicator at three view branches; pushBreadcrumb is

// called on view changes so the form's breadcrumb panel has UI history

// the audit log doesn't capture (view changes, modal opens, tab switches).

import { QuickReportButton, pushBreadcrumb, MyReportsPage as BugReportsPage, DevDashboardPage, DevReportsPage, DevAccessLink, useBugReportDrawerOpen, useFeatureFlags, useBumpViewCounter } from './features/feedback';
import CharacterImportReviewModal from './features/character-import';
import { assertImportWriteSafeValue } from './features/character-import/applyImportPayload';
import { SandboxCampaignSettingsModal } from './features/dashboard/components/chrome/SandboxCampaignSettingsModal';
import { SandboxPartyModal } from './features/dashboard/components/chrome/SandboxPartyModal';
import {
  applyShopCheckoutToCharAndShop,
  applyLootDropAwardToChar,
  applyPartyTakeToCharAndPool,
  applyCraftOutputToCharInventory,
  applyCraftOutputToPartyPool,
  applyCraftingTableRowToCharInventory,
  applyCraftingTableRowToPartyPool,
  applyCraftResultTransferToPartyPool,
  applyCharInventoryItemAdd,
  applyCharInventoryItemFavoriteToggle,
  applyCharInventoryItemRemove,
  applyCharInventoryItemUpdate,
  applyLegacyInventoryTransferToCraftingTable,
  applyLegacyInventoryTransferToPartyPool,
  applyLegacyInventoryTransferToTargetInventory,
  activateLegacyInventoryAbilityRuntime,
  buildShopTemplateDisplayMap,
  buildUnifiedItemCatalogDisplayMap,
  claimLootDropInEncounter,
  consumeLegacyInventoryItemRuntime,
  createLootEncounterRuntime,
  getLegacyInventoryAbilityName,
  hasLegacyInventoryItem,
  applyCraftResultToCraftingTable,
  getCraftResultTableStackKey,
  materializeLegacyInventoryItemFromCraftResult,
  materializeLegacyInventoryItemFromLootCatalogItem,
  normalizeLootEncounterDrops,
  parseLegacyAbilityEffectLine,
  parseLegacyConsumableResourceEffect,
  reconcileLootEncounterDropIdsFromPublishResult,
  revealAllLootDropsInEncounter,
  resetLegacyInventoryUsesForRest,
  setLootDropVisibilityInEncounter,
  stackLegacyInventoryItem,
  toggleLegacyInventoryItemEquipRuntime,
  awardLootDropInEncounter,
  withdrawLootClaimInEncounter,
} from './items';

import { applyResolvedDamageMitigation } from './features/effects/damageResolution';

import {

  removeLegacyEffectToRecent,

  restoreRecentlyRemovedLegacyEffect,

} from './features/effects/archiveAdapters';

import { buildLegacyEffectCollections, buildLegacyEffectEntries, getCanonicalEffects, getRenderableEffects } from './features/effects/effectModel';

import { getActiveLegacyEffectNames, getActiveLegacyStatDelta } from './features/effects/legacySelectors';

import { buildEffectInstanceLifecycleArgs, isEffectInstanceLifecycleAuthorityCandidate } from './features/effects/statusLifecycle';

import { buildLegacyStatusEffectView } from './features/effects/statusViewModel';

import {

  addDiseaseState,

  applyDietCompletionRewardState,

  applyWellFedState,

  buildDiseaseState,

  clearFoodEffectState,

  removeDiseaseState,

  updateDiseaseState,

} from './features/effects/sourceStateAdapters';

import {

  createRuntimeEffectRef,

  getRuntimeEffectRefKey,

  getRuntimeTickKey,

  removeQueuedConcentrationItems,

  removeLinkedConcentrationState,

  removeRuntimeEffectInstance,

} from './features/effects/runtimeLifecycle';

import { addLegacyEffect, buildLegacyEffectMetadata, buildSystemLegacyEffectUpdates, removeLegacyEffect, removeLegacyEffectAt } from './features/effects/writeAdapters';

import {

  formatResolvedSaveBreakdown,

  getCombatAdvantageScopeConfig,

  getInitiativeAdvantageConfig,

  getResolvedAttackBonusContext,

  getResolvedArmorClassContext,

  getResolvedAdvantageContext,

  getEquippedCombatBonusesFromInventory,

  getResolvedMaxHpContext,

  getResolvedPassiveSkillContext,

  getResolvedSaveContext,

  getSaveAdvantageConfig,

  getSkillAdvantageConfig,

  getResolvedSkillBreakdownParts,

  getResolvedSkillContext,

  getResolvedSkillTooltipLines,

  getResolvedSpellContext,

  getStatCheckAdvantageConfig,

  getResolvedWardsaveContext,

} from './features/effects/resolvedContext';

import { CommaListField, CompactNumberField, ControlledMiniForm, DebouncedTextField, NumberField, QuantityStepper, TextAreaField } from './features/inputs';
import SystemDescriptionFields from './features/systems/components/SystemDescriptionFields';
import SystemTagsField from './features/systems/components/SystemTagsField';
import SystemVisibilityNotesFields from './features/systems/components/SystemVisibilityNotesFields';
import { resolveEnemyKilledSideEffects } from './features/systems/enemyKillSideEffects';

import { formatSpellResourceReadout, getSpellResourceUiModel, SPELL_RESOURCE_SURFACE_ANY } from './features/spells/spellResourceCompatibility';

import {

  hasPreAppliedTargetDebuffPercent,

  resolveInitialSpellOutcomes,

  resolveSpellAttackOutcome,

  resolveInitialTargetChecks,

  SPELL_INITIAL_CHECK_ATTACK,

  SPELL_INITIAL_CHECK_SAVE,

} from './features/spells/spellCombatResolution';

import {

  resolveImmediateHealingOutcome,

  resolveImmediateTempHpOutcome,

} from './features/spells/spellImmediateOutcomes';

import {

  buildSpellRuntimeLifecycleEntries,

  buildSpellRuntimeLifecycleMap,

  buildSpellRuntimeEffectIdentity,

  extendSpellRuntimeDuration,

  removeSingleSpellRuntimeCondition,

  resolveSpellBeneficialRuntimeOwner,

} from './features/spells/spellRuntimeEffects';

import {

  createPendingSpellSaveContext,

  getPendingSpellSaveCharsState,

  getPendingSpellSaveCombatState,

} from './features/spells/spellSaveWorkflow';

import {

  finalizeSpellCastCommit,

  resetSpellResourcesForRest,

} from './features/spells/spellCastEngine';

import {

  formatSpellCastInfoLabel,

  getSpellCastLevelOptions,

  getSurfaceSpellCastInfo,

  isSpellCastInfoBlocked,

  resolveSelectedSpellCastLevel,

  shiftSelectedSpellCastLevel,

} from './features/spells/spellCastSupport';

import {
  getCanonicalSpellSystem,
  getScalarSpellProgressionMaxCastLevel,
  isManaSpellSystem,
  syncSpellSettings,
} from './features/spells/spellSystem';

import { calculateMaxMana, getMaxSpellLevel } from './features/spells/sheetCasting';

import {

  SPELL_BINDING_SURFACE_ANY,

  SPELL_BINDING_SURFACE_BOTH,

  SPELL_BINDING_SURFACE_COMBAT,

  SPELL_BINDING_SURFACE_SHEET,

  buildSystemPresetResourceModel,

  createSystemDirectSpellBinding,

  createDirectSpellBinding,

  createResourceProfile,

  duplicateSystemResourceModel,

  getCharacterCustomResourceTrackers,

  getBindableResourceProfiles,

  getDisplayableSystemResourceRows,

  getLegacyOnlyCharacterCustomResourceTrackers,

  normalizeSystemResourceModel,

  normalizeDirectSpellBinding,

  normalizeSystemsMap,

  remapSystemReferenceGraph,

  syncCharacterCustomResources,

} from './features/systems/resourceEngine';

import { drainResourceEventQueue } from './features/systems/resourceEventQueue';



// ============================================================================

// THEME SYSTEM - Color Themes with Variations (from BMG Theme Demo v9)

// ============================================================================



const THEMES = {

  'fantasy-classic': {

    id: 'fantasy-classic',

    name: 'Classic Fantasy',

    description: 'The original BMG look — parchment & gold',

    category: 'fantasy',

    preview: '🏰',

    order: 1,

    colors: {

      bgPrimary: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',

      bgSecondary: '#1a1a2e',

      bgCard: 'linear-gradient(180deg, rgba(42,32,24,0.98) 0%, rgba(32,24,18,0.99) 100%)',

      bgCardSolid: '#2a2018',

      bgCardHover: '#3a3028',

      bgInput: 'rgba(18,14,10,0.95)',

      bgHover: 'rgba(201,162,39,0.2)',

      bgModal: 'rgba(0,0,0,0.85)',

      accent: '#c9a227',

      accentLight: '#ffd700',

      accentDark: '#8b6914',

      accentGlow: 'rgba(201,162,39,0.4)',

      success: '#27c95a',

      successBg: 'rgba(39,201,90,0.25)',

      danger: '#c92727',

      dangerBg: 'rgba(201,39,39,0.25)',

      warning: '#c9a227',

      warningBg: 'rgba(201,162,39,0.25)',

      info: '#4a90d9',

      infoBg: 'rgba(74,144,217,0.25)',

      mana: '#4a90d9',

      manaBg: 'rgba(26,74,139,0.4)',

      textPrimary: '#e8d5b7',

      textSecondary: '#b89a70',

      textMuted: '#7a6a55',

      textOnAccent: '#000000',

      borderPrimary: '#5c4a2a',

      borderLight: '#3a3020',

      borderAccent: '#c9a227',

      hpHigh: '#27c95a',

      hpMid: '#c9a227',

      hpLow: '#c92727',

      buffBg: 'rgba(39,201,90,0.35)',

      buffBorder: '#27c95a',

      buffText: '#7dff7d',

      debuffBg: 'rgba(201,39,39,0.35)',

      debuffBorder: '#c92727',

      debuffText: '#ff7d7d',

    },

    typography: {

      fontFamily: 'Georgia, "Times New Roman", serif',

      headingFamily: 'Georgia, serif',

      monoFamily: '"Courier New", monospace',

    },

    effects: {

      cardShadow: '0 8px 32px rgba(0,0,0,0.6), 0 0 1px rgba(201,162,39,0.3)',

      cardShadowHover: '0 12px 40px rgba(0,0,0,0.7), 0 0 20px rgba(201,162,39,0.2)',

      buttonShadow: '0 2px 8px rgba(0,0,0,0.4)',

      glowAccent: '0 0 20px rgba(201,162,39,0.4)',

      textShadow: '0 1px 2px rgba(0,0,0,0.5)',

      headingTextShadow: '0 2px 4px rgba(0,0,0,0.6)',

      borderRadius: '8px',

      borderRadiusLg: '12px',

      borderRadiusSm: '4px',

      borderWidth: '2px',

      cardBorder: '2px solid #5c4a2a',

      inputBorder: '1px solid #3a3020',

      transition: 'all 0.2s ease',

    },

  },



  'fantasy-dark': {

    id: 'fantasy-dark',

    name: 'Dark Arcana',

    description: 'Mystical purples and deep shadows',

    category: 'fantasy',

    preview: '🔮',

    colors: {

      bgPrimary: '#000001',

      bgSecondary: '#000000',

      bgCard: 'linear-gradient(180deg, rgba(58,38,82,0.98) 0%, rgba(45,28,65,0.99) 100%)',

      bgCardSolid: '#1c1028',

      bgCardHover: '#281838',

      bgInput: 'rgba(25,15,38,0.95)',

      bgHover: 'rgba(155,89,182,0.2)',

      bgModal: 'rgba(0,0,0,0.9)',

      accent: '#9b59b6',

      accentLight: '#d4a5ff',

      accentDark: '#6b3fa0',

      accentGlow: 'rgba(155,89,182,0.5)',

      success: '#00d9a0',

      successBg: 'rgba(0,217,160,0.25)',

      danger: '#ff4757',

      dangerBg: 'rgba(255,71,87,0.25)',

      warning: '#ffa502',

      warningBg: 'rgba(255,165,2,0.25)',

      info: '#70a1ff',

      infoBg: 'rgba(112,161,255,0.25)',

      mana: '#9b59b6',

      manaBg: 'rgba(155,89,182,0.4)',

      textPrimary: '#d8d0e8',

      textSecondary: '#a090c0',

      textMuted: '#7060a0',

      textOnAccent: '#ffffff',

      borderPrimary: '#7b4fa6',

      borderLight: '#4d2070',

      borderAccent: '#b06ed8',

      hpHigh: '#00d9a0',

      hpMid: '#ffa502',

      hpLow: '#ff4757',

      buffBg: 'rgba(0,217,160,0.35)',

      buffBorder: '#00d9a0',

      buffText: '#7dffda',

      debuffBg: 'rgba(255,71,87,0.35)',

      debuffBorder: '#ff4757',

      debuffText: '#ff9da4',

    },

    typography: {

      fontFamily: '"Palatino Linotype", "Book Antiqua", Palatino, serif',

      headingFamily: '"Palatino Linotype", serif',

      monoFamily: '"Fira Code", monospace',

    },

    effects: {

      cardShadow: '0 8px 32px rgba(0,0,0,0.6), 0 0 30px rgba(155,89,182,0.15)',

      cardShadowHover: '0 12px 40px rgba(0,0,0,0.7), 0 0 40px rgba(155,89,182,0.25)',

      buttonShadow: '0 2px 8px rgba(155,89,182,0.3)',

      glowAccent: '0 0 30px rgba(155,89,182,0.5)',

      textShadow: '0 1px 2px rgba(0,0,0,0.5)',

      headingTextShadow: '0 2px 4px rgba(0,0,0,0.6)',

      borderRadius: '12px',

      borderRadiusLg: '16px',

      borderRadiusSm: '6px',

      borderWidth: '2px',

      cardBorder: '2px solid #7b4fa6',

      inputBorder: '1px solid #4d2070',

      transition: 'all 0.25s ease',

    },

  },



  'fantasy-nature': {

    id: 'fantasy-nature',

    name: 'Verdant Grove',

    description: 'Forest greens and warm earth tones',

    category: 'fantasy',

    preview: '🌿',

    colors: {

      bgPrimary: 'linear-gradient(135deg, #140804 0%, #201008 50%, #180a04 100%)',

      bgSecondary: '#0a0402',

      bgCard: 'linear-gradient(180deg, rgba(60,45,30,0.98) 0%, rgba(48,35,22,0.99) 100%)',

      bgCardSolid: '#3c2d1e',

      bgCardHover: '#4d3c2a',

      bgInput: 'rgba(30,22,14,0.95)',

      bgHover: 'rgba(224,136,48,0.2)',

      bgModal: 'rgba(0,0,0,0.85)',

      accent: '#e08830',

      accentLight: '#f0a850',

      accentDark: '#b06820',

      accentGlow: 'rgba(224,136,48,0.4)',

      success: '#7ddb8a',

      successBg: 'rgba(125,219,138,0.25)',

      danger: '#e74c3c',

      dangerBg: 'rgba(231,76,60,0.25)',

      warning: '#d4a84b',

      warningBg: 'rgba(212,168,75,0.25)',

      info: '#5dade2',

      infoBg: 'rgba(93,173,226,0.25)',

      mana: '#5dade2',

      manaBg: 'rgba(93,173,226,0.35)',

      textPrimary: '#fff0d0',

      textSecondary: '#e8c080',

      textMuted: '#a08050',

      textOnAccent: '#000000',

      borderPrimary: '#6a4a2a',

      borderLight: '#4a3520',

      borderAccent: '#e08830',

      hpHigh: '#7ddb8a',

      hpMid: '#d4a84b',

      hpLow: '#e74c3c',

      buffBg: 'rgba(125,219,138,0.35)',

      buffBorder: '#7ddb8a',

      buffText: '#a5ffb0',

      debuffBg: 'rgba(231,76,60,0.35)',

      debuffBorder: '#e74c3c',

      debuffText: '#ff9d94',

    },

    typography: {

      fontFamily: '"Trebuchet MS", "Lucida Sans", Arial, sans-serif',

      headingFamily: '"Trebuchet MS", sans-serif',

      monoFamily: 'Consolas, monospace',

    },

    effects: {

      cardShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 20px rgba(224,136,48,0.1)',

      cardShadowHover: '0 12px 40px rgba(0,0,0,0.6), 0 0 30px rgba(224,136,48,0.15)',

      buttonShadow: '0 2px 8px rgba(0,0,0,0.3)',

      glowAccent: '0 0 20px rgba(224,136,48,0.4)',

      textShadow: '0 1px 2px rgba(0,0,0,0.5)',

      headingTextShadow: '0 2px 4px rgba(0,0,0,0.6)',

      borderRadius: '10px',

      borderRadiusLg: '14px',

      borderRadiusSm: '5px',

      borderWidth: '2px',

      cardBorder: '2px solid #6a4a2a',

      inputBorder: '1px solid #4a3520',

      transition: 'all 0.2s ease',

    },

  },



  'sunset-dusk': {

    id: 'sunset-dusk',

    name: 'Sunset Dusk',

    description: 'Warm oranges flowing into cool purples',

    category: 'multicolor',

    preview: '🌅',

    colors: {

      bgPrimary: 'linear-gradient(135deg, #080406 0%, #0c0608 50%, #080504 100%)',

      bgSecondary: '#050304',

      bgCard: 'linear-gradient(145deg, rgba(60,30,50,0.98) 0%, rgba(50,25,35,0.99) 100%)',

      bgCardSolid: '#3c1e32',

      bgCardHover: '#4d2840',

      bgInput: 'rgba(30,15,25,0.95)',

      bgHover: 'rgba(255,140,90,0.2)',

      bgModal: 'rgba(0,0,0,0.9)',

      accent: '#ff8c5a',

      accentLight: '#ffb088',

      accentDark: '#d96830',

      accentGlow: 'rgba(255,140,90,0.4)',

      success: '#7dd87d',

      successBg: 'rgba(125,216,125,0.25)',

      danger: '#ff6b8a',

      dangerBg: 'rgba(255,107,138,0.25)',

      warning: '#ffc145',

      warningBg: 'rgba(255,193,69,0.25)',

      info: '#a78bfa',

      infoBg: 'rgba(167,139,250,0.25)',

      mana: '#c084fc',

      manaBg: 'rgba(192,132,252,0.3)',

      textPrimary: '#fef0e8',

      textSecondary: '#e8c0b8',

      textMuted: '#a88090',

      textOnAccent: '#1a0a10',

      borderPrimary: '#8b4060',

      borderLight: '#5a2840',

      borderAccent: '#ff8c5a',

      hpHigh: '#7dd87d',

      hpMid: '#ffc145',

      hpLow: '#ff6b8a',

      buffBg: 'rgba(125,216,125,0.35)',

      buffBorder: '#7dd87d',

      buffText: '#a8f0a8',

      debuffBg: 'rgba(255,107,138,0.35)',

      debuffBorder: '#ff6b8a',

      debuffText: '#ffa0b8',

    },

    typography: {

      fontFamily: '"Segoe UI", Tahoma, Geneva, sans-serif',

      headingFamily: '"Segoe UI", sans-serif',

      monoFamily: '"Cascadia Code", monospace',

    },

    effects: {

      cardShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 20px rgba(255,140,90,0.1)',

      cardShadowHover: '0 12px 40px rgba(0,0,0,0.6), 0 0 30px rgba(255,140,90,0.2)',

      buttonShadow: '0 2px 8px rgba(0,0,0,0.4)',

      glowAccent: '0 0 25px rgba(255,140,90,0.4)',

      textShadow: '0 1px 2px rgba(0,0,0,0.5)',

      headingTextShadow: '0 2px 4px rgba(0,0,0,0.6)',

      borderRadius: '10px',

      borderRadiusLg: '14px',

      borderRadiusSm: '6px',

      borderWidth: '2px',

      cardBorder: '2px solid #8b4060',

      inputBorder: '1px solid #5a2840',

      transition: 'all 0.2s ease',

    },

  },



  'ocean-depths': {

    id: 'ocean-depths',

    name: 'Ocean Depths',

    description: 'Deep navy with teal and coral accents',

    category: 'multicolor',

    preview: '🌊',

    colors: {

      bgPrimary: 'linear-gradient(135deg, #030a12 0%, #051520 50%, #0a1a25 100%)',

      bgSecondary: '#020810',

      bgCard: 'linear-gradient(145deg, rgba(15,50,70,0.98) 0%, rgba(10,35,55,0.99) 100%)',

      bgCardSolid: '#0f3246',

      bgCardHover: '#154055',

      bgInput: 'rgba(8,25,40,0.95)',

      bgHover: 'rgba(80,220,200,0.15)',

      bgModal: 'rgba(0,5,10,0.9)',

      accent: '#50dcc8',

      accentLight: '#80ffe8',

      accentDark: '#30b0a0',

      accentGlow: 'rgba(80,220,200,0.4)',

      success: '#50dcc8',

      successBg: 'rgba(80,220,200,0.25)',

      danger: '#ff7088',

      dangerBg: 'rgba(255,112,136,0.25)',

      warning: '#ffd070',

      warningBg: 'rgba(255,208,112,0.25)',

      info: '#70b0ff',

      infoBg: 'rgba(112,176,255,0.25)',

      mana: '#70b0ff',

      manaBg: 'rgba(112,176,255,0.3)',

      textPrimary: '#f0fcff',

      textSecondary: '#c0e8f8',

      textMuted: '#80c0d8',

      textOnAccent: '#051520',

      borderPrimary: '#206080',

      borderLight: '#104060',

      borderAccent: '#50dcc8',

      hpHigh: '#50dcc8',

      hpMid: '#ffd070',

      hpLow: '#ff7088',

      buffBg: 'rgba(80,220,200,0.35)',

      buffBorder: '#50dcc8',

      buffText: '#a0fff0',

      debuffBg: 'rgba(255,112,136,0.35)',

      debuffBorder: '#ff7088',

      debuffText: '#ffa8b8',

    },

    typography: {

      fontFamily: '"Source Sans Pro", "Helvetica Neue", sans-serif',

      headingFamily: '"Source Sans Pro", sans-serif',

      monoFamily: '"Source Code Pro", monospace',

    },

    effects: {

      cardShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 20px rgba(80,220,200,0.08)',

      cardShadowHover: '0 12px 40px rgba(0,0,0,0.6), 0 0 35px rgba(80,220,200,0.15)',

      buttonShadow: '0 2px 8px rgba(0,0,0,0.4)',

      glowAccent: '0 0 25px rgba(80,220,200,0.4)',

      textShadow: '0 1px 2px rgba(0,0,0,0.5)',

      headingTextShadow: '0 2px 4px rgba(0,0,0,0.6)',

      borderRadius: '10px',

      borderRadiusLg: '14px',

      borderRadiusSm: '5px',

      borderWidth: '2px',

      cardBorder: '2px solid #206080',

      inputBorder: '1px solid #104060',

      transition: 'all 0.2s ease',

    },

  },



  'northern-lights': {

    id: 'northern-lights',

    name: 'Northern Lights',

    description: 'Aurora greens, blues, and magentas',

    category: 'multicolor',

    preview: '🌌',

    colors: {

      bgPrimary: 'linear-gradient(135deg, #020510 0%, #050818 50%, #080515 100%)',

      bgSecondary: '#010308',

      bgCard: 'linear-gradient(145deg, rgba(20,35,60,0.97) 0%, rgba(15,25,45,0.98) 100%)',

      bgCardSolid: '#14233c',

      bgCardHover: '#1c3050',

      bgInput: 'rgba(10,18,35,0.95)',

      bgHover: 'rgba(100,255,180,0.15)',

      bgModal: 'rgba(0,2,8,0.92)',

      accent: '#64ffb4',

      accentLight: '#a0ffd0',

      accentDark: '#40d090',

      accentGlow: 'rgba(100,255,180,0.4)',

      success: '#64ffb4',

      successBg: 'rgba(100,255,180,0.25)',

      danger: '#ff70a0',

      dangerBg: 'rgba(255,112,160,0.25)',

      warning: '#ffe064',

      warningBg: 'rgba(255,224,100,0.25)',

      info: '#64b4ff',

      infoBg: 'rgba(100,180,255,0.25)',

      mana: '#c890ff',

      manaBg: 'rgba(200,144,255,0.3)',

      textPrimary: '#f0faff',

      textSecondary: '#b0d8e8',

      textMuted: '#7098b0',

      textOnAccent: '#051510',

      borderPrimary: '#305080',

      borderLight: '#203858',

      borderAccent: '#64ffb4',

      hpHigh: '#64ffb4',

      hpMid: '#ffe064',

      hpLow: '#ff70a0',

      buffBg: 'rgba(100,255,180,0.35)',

      buffBorder: '#64ffb4',

      buffText: '#a8ffd8',

      debuffBg: 'rgba(255,112,160,0.35)',

      debuffBorder: '#ff70a0',

      debuffText: '#ffa8c8',

    },

    typography: {

      fontFamily: 'Raleway, "Segoe UI", sans-serif',

      headingFamily: 'Raleway, sans-serif',

      monoFamily: '"Fira Code", monospace',

    },

    effects: {

      cardShadow: '0 8px 32px rgba(0,0,0,0.6), 0 0 25px rgba(100,255,180,0.08)',

      cardShadowHover: '0 12px 40px rgba(0,0,0,0.7), 0 0 40px rgba(100,255,180,0.15)',

      buttonShadow: '0 2px 8px rgba(0,0,0,0.4)',

      glowAccent: '0 0 30px rgba(100,255,180,0.5)',

      textShadow: '0 1px 3px rgba(0,0,0,0.6)',

      headingTextShadow: '0 2px 4px rgba(0,0,0,0.7)',

      borderRadius: '12px',

      borderRadiusLg: '16px',

      borderRadiusSm: '6px',

      borderWidth: '2px',

      cardBorder: '2px solid #305080',

      inputBorder: '1px solid #203858',

      transition: 'all 0.25s ease',

    },

  },



  'copper-teal': {

    id: 'copper-teal',

    name: 'Copper & Teal',

    description: 'Steampunk-inspired warm copper with cool teal',

    category: 'multicolor',

    preview: '⚙️',

    colors: {

      bgPrimary: 'linear-gradient(135deg, #0a0806 0%, #100c08 50%, #0c0a06 100%)',

      bgSecondary: '#060504',

      bgCard: 'linear-gradient(145deg, rgba(35,55,55,0.98) 0%, rgba(25,42,42,0.99) 100%)',

      bgCardSolid: '#233737',

      bgCardHover: '#2e4545',

      bgInput: 'rgba(15,28,28,0.95)',

      bgHover: 'rgba(210,140,90,0.2)',

      bgModal: 'rgba(5,10,10,0.9)',

      accent: '#d28c5a',

      accentLight: '#e8b088',

      accentDark: '#a86830',

      accentGlow: 'rgba(210,140,90,0.4)',

      success: '#5ac8b8',

      successBg: 'rgba(90,200,184,0.25)',

      danger: '#d86060',

      dangerBg: 'rgba(216,96,96,0.25)',

      warning: '#d2a85a',

      warningBg: 'rgba(210,168,90,0.25)',

      info: '#5aa8d2',

      infoBg: 'rgba(90,168,210,0.25)',

      mana: '#5ac8b8',

      manaBg: 'rgba(90,200,184,0.3)',

      textPrimary: '#f0ece8',

      textSecondary: '#c8beb8',

      textMuted: '#8a8580',

      textOnAccent: '#0a0805',

      borderPrimary: '#4a6868',

      borderLight: '#304545',

      borderAccent: '#d28c5a',

      hpHigh: '#5ac8b8',

      hpMid: '#d2a85a',

      hpLow: '#d86060',

      buffBg: 'rgba(90,200,184,0.35)',

      buffBorder: '#5ac8b8',

      buffText: '#98e8d8',

      debuffBg: 'rgba(216,96,96,0.35)',

      debuffBorder: '#d86060',

      debuffText: '#f0a0a0',

    },

    typography: {

      fontFamily: '"Crimson Text", Georgia, serif',

      headingFamily: '"Crimson Text", serif',

      monoFamily: '"Courier Prime", monospace',

    },

    effects: {

      cardShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 15px rgba(210,140,90,0.08)',

      cardShadowHover: '0 12px 40px rgba(0,0,0,0.6), 0 0 25px rgba(210,140,90,0.15)',

      buttonShadow: '0 2px 8px rgba(0,0,0,0.4)',

      glowAccent: '0 0 20px rgba(210,140,90,0.4)',

      textShadow: '0 1px 2px rgba(0,0,0,0.5)',

      headingTextShadow: '0 2px 4px rgba(0,0,0,0.6)',

      borderRadius: '6px',

      borderRadiusLg: '10px',

      borderRadiusSm: '4px',

      borderWidth: '2px',

      cardBorder: '2px solid #4a6868',

      inputBorder: '1px solid #304545',

      transition: 'all 0.2s ease',

    },

  },



  'sakura': {

    id: 'sakura',

    name: 'Sakura',

    description: 'Soft cherry blossom pinks with lavender accents',

    category: 'multicolor',

    preview: '🌸',

    colors: {

      bgPrimary: 'linear-gradient(135deg, #0c0610 0%, #100814 50%, #0c060f 100%)',

      bgSecondary: '#080408',

      bgCard: 'linear-gradient(145deg, rgba(55,30,50,0.98) 0%, rgba(45,25,42,0.99) 100%)',

      bgCardSolid: '#3a2035',

      bgCardHover: '#482840',

      bgInput: 'rgba(28,15,25,0.95)',

      bgHover: 'rgba(255,180,200,0.2)',

      bgModal: 'rgba(10,5,12,0.9)',

      accent: '#ffb4c8',

      accentLight: '#ffd8e4',

      accentDark: '#d88aa0',

      accentGlow: 'rgba(255,180,200,0.4)',

      success: '#98d8a8',

      successBg: 'rgba(152,216,168,0.25)',

      danger: '#e07080',

      dangerBg: 'rgba(224,112,128,0.25)',

      warning: '#e8c878',

      warningBg: 'rgba(232,200,120,0.25)',

      info: '#b8a8e8',

      infoBg: 'rgba(184,168,232,0.25)',

      mana: '#c8a8e8',

      manaBg: 'rgba(200,168,232,0.3)',

      textPrimary: '#f8f0ff',

      textSecondary: '#e0c8e8',

      textMuted: '#a888b8',

      textOnAccent: '#200810',

      borderPrimary: '#804058',

      borderLight: '#502838',

      borderAccent: '#ffb4c8',

      hpHigh: '#98d8a8',

      hpMid: '#e8c878',

      hpLow: '#e07080',

      buffBg: 'rgba(152,216,168,0.35)',

      buffBorder: '#98d8a8',

      buffText: '#c0f0c8',

      debuffBg: 'rgba(224,112,128,0.35)',

      debuffBorder: '#e07080',

      debuffText: '#f8a0b0',

    },

    typography: {

      fontFamily: '"Noto Serif", Georgia, serif',

      headingFamily: '"Noto Serif", serif',

      monoFamily: '"Noto Sans Mono", monospace',

    },

    effects: {

      cardShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 20px rgba(255,180,200,0.08)',

      cardShadowHover: '0 12px 40px rgba(0,0,0,0.6), 0 0 30px rgba(255,180,200,0.15)',

      buttonShadow: '0 2px 8px rgba(0,0,0,0.4)',

      glowAccent: '0 0 25px rgba(255,180,200,0.4)',

      textShadow: '0 1px 2px rgba(0,0,0,0.5)',

      headingTextShadow: '0 2px 4px rgba(0,0,0,0.6)',

      borderRadius: '12px',

      borderRadiusLg: '16px',

      borderRadiusSm: '6px',

      borderWidth: '2px',

      cardBorder: '2px solid #804058',

      inputBorder: '1px solid #502838',

      transition: 'all 0.2s ease',

    },

  },



  'royal-court': {

    id: 'royal-court',

    name: 'Royal Court',

    description: 'Regal navy and gold with burgundy accents',

    category: 'multicolor',

    preview: '👑',

    colors: {

      bgPrimary: 'linear-gradient(135deg, #06060f 0%, #080814 50%, #0a050c 100%)',

      bgSecondary: '#040408',

      bgCard: 'linear-gradient(145deg, rgba(30,30,65,0.98) 0%, rgba(22,22,50,0.99) 100%)',

      bgCardSolid: '#1e1e41',

      bgCardHover: '#282855',

      bgInput: 'rgba(15,15,35,0.95)',

      bgHover: 'rgba(218,175,85,0.2)',

      bgModal: 'rgba(5,5,15,0.92)',

      accent: '#daaf55',

      accentLight: '#f0d080',

      accentDark: '#b08830',

      accentGlow: 'rgba(218,175,85,0.4)',

      success: '#55b888',

      successBg: 'rgba(85,184,136,0.25)',

      danger: '#c85565',

      dangerBg: 'rgba(200,85,101,0.25)',

      warning: '#daaf55',

      warningBg: 'rgba(218,175,85,0.25)',

      info: '#5588c8',

      infoBg: 'rgba(85,136,200,0.25)',

      mana: '#8870c8',

      manaBg: 'rgba(136,112,200,0.3)',

      textPrimary: '#f8f5f0',

      textSecondary: '#c8c0b8',

      textMuted: '#888088',

      textOnAccent: '#0a0810',

      borderPrimary: '#484878',

      borderLight: '#303055',

      borderAccent: '#daaf55',

      hpHigh: '#55b888',

      hpMid: '#daaf55',

      hpLow: '#c85565',

      buffBg: 'rgba(85,184,136,0.35)',

      buffBorder: '#55b888',

      buffText: '#90e8b8',

      debuffBg: 'rgba(200,85,101,0.35)',

      debuffBorder: '#c85565',

      debuffText: '#f0a0a8',

    },

    typography: {

      fontFamily: '"Libre Baskerville", "Times New Roman", serif',

      headingFamily: '"Libre Baskerville", serif',

      monoFamily: '"IBM Plex Mono", monospace',

    },

    effects: {

      cardShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 15px rgba(218,175,85,0.1)',

      cardShadowHover: '0 12px 40px rgba(0,0,0,0.6), 0 0 30px rgba(218,175,85,0.18)',

      buttonShadow: '0 2px 8px rgba(0,0,0,0.4)',

      glowAccent: '0 0 25px rgba(218,175,85,0.45)',

      textShadow: '0 1px 2px rgba(0,0,0,0.5)',

      headingTextShadow: '0 2px 4px rgba(0,0,0,0.6)',

      borderRadius: '8px',

      borderRadiusLg: '12px',

      borderRadiusSm: '4px',

      borderWidth: '2px',

      cardBorder: '2px solid #484878',

      inputBorder: '1px solid #303055',

      transition: 'all 0.2s ease',

    },

  },



  'ember-frost': {

    id: 'ember-frost',

    name: 'Ember & Frost',

    description: 'Fire reds against icy blues',

    category: 'multicolor',

    preview: '🔥',

    colors: {

      bgPrimary: 'linear-gradient(135deg, #080608 0%, #0c080a 50%, #08080c 100%)',

      bgSecondary: '#040405',

      bgCard: 'linear-gradient(145deg, rgba(40,32,45,0.98) 0%, rgba(30,25,35,0.99) 100%)',

      bgCardSolid: '#28202d',

      bgCardHover: '#352a3a',

      bgInput: 'rgba(22,18,28,0.95)',

      bgHover: 'rgba(255,120,70,0.2)',

      bgModal: 'rgba(5,5,8,0.92)',

      accent: '#ff7846',

      accentLight: '#ffa080',

      accentDark: '#d05020',

      accentGlow: 'rgba(255,120,70,0.4)',

      success: '#46d8b8',

      successBg: 'rgba(70,216,184,0.25)',

      danger: '#ff6070',

      dangerBg: 'rgba(255,96,112,0.25)',

      warning: '#ffb846',

      warningBg: 'rgba(255,184,70,0.25)',

      info: '#46a8ff',

      infoBg: 'rgba(70,168,255,0.25)',

      mana: '#46a8ff',

      manaBg: 'rgba(70,168,255,0.3)',

      textPrimary: '#f8f4f8',

      textSecondary: '#d0c0d0',

      textMuted: '#9080a0',

      textOnAccent: '#100505',

      borderPrimary: '#605070',

      borderLight: '#403548',

      borderAccent: '#ff7846',

      hpHigh: '#46d8b8',

      hpMid: '#ffb846',

      hpLow: '#ff6070',

      buffBg: 'rgba(70,216,184,0.35)',

      buffBorder: '#46d8b8',

      buffText: '#88f8e0',

      debuffBg: 'rgba(255,96,112,0.35)',

      debuffBorder: '#ff6070',

      debuffText: '#ffa0a8',

    },

    typography: {

      fontFamily: '"Inter", -apple-system, sans-serif',

      headingFamily: '"Inter", sans-serif',

      monoFamily: '"JetBrains Mono", monospace',

    },

    effects: {

      cardShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 15px rgba(255,120,70,0.08)',

      cardShadowHover: '0 12px 40px rgba(0,0,0,0.6), 0 0 25px rgba(255,120,70,0.15)',

      buttonShadow: '0 2px 8px rgba(0,0,0,0.4)',

      glowAccent: '0 0 25px rgba(255,120,70,0.45)',

      textShadow: '0 1px 2px rgba(0,0,0,0.5)',

      headingTextShadow: '0 2px 4px rgba(0,0,0,0.6)',

      borderRadius: '10px',

      borderRadiusLg: '14px',

      borderRadiusSm: '5px',

      borderWidth: '2px',

      cardBorder: '2px solid #605070',

      inputBorder: '1px solid #403548',

      transition: 'all 0.2s ease',

    },

  },



  'desert-oasis': {

    id: 'desert-oasis',

    name: 'Desert Oasis',

    description: 'Warm sandy tans with cool turquoise pools',

    category: 'multicolor',

    preview: '🏜️',

    colors: {

      bgPrimary: 'linear-gradient(135deg, #080a08 0%, #0c100c 50%, #0a0e0a 100%)',

      bgSecondary: '#050605',

      bgCard: 'linear-gradient(145deg, rgba(55,48,40,0.98) 0%, rgba(45,40,35,0.99) 100%)',

      bgCardSolid: '#3d3530',

      bgCardHover: '#4d4540',

      bgInput: 'rgba(30,26,22,0.95)',

      bgHover: 'rgba(80,200,180,0.2)',

      bgModal: 'rgba(0,0,0,0.9)',

      accent: '#50c8b4',

      accentLight: '#78e0d0',

      accentDark: '#30a090',

      accentGlow: 'rgba(80,200,180,0.4)',

      success: '#78c890',

      successBg: 'rgba(120,200,144,0.25)',

      danger: '#d87868',

      dangerBg: 'rgba(216,120,104,0.25)',

      warning: '#e0b860',

      warningBg: 'rgba(224,184,96,0.25)',

      info: '#60a8c8',

      infoBg: 'rgba(96,168,200,0.25)',

      mana: '#60a8c8',

      manaBg: 'rgba(96,168,200,0.3)',

      textPrimary: '#f8f4e8',

      textSecondary: '#d8c8b0',

      textMuted: '#a09080',

      textOnAccent: '#101210',

      borderPrimary: '#686050',

      borderLight: '#484038',

      borderAccent: '#50c8b4',

      hpHigh: '#78c890',

      hpMid: '#e0b860',

      hpLow: '#d87868',

      buffBg: 'rgba(120,200,144,0.35)',

      buffBorder: '#78c890',

      buffText: '#a8f0c0',

      debuffBg: 'rgba(216,120,104,0.35)',

      debuffBorder: '#d87868',

      debuffText: '#f0b0a0',

    },

    typography: {

      fontFamily: '"Merriweather Sans", "Segoe UI", sans-serif',

      headingFamily: '"Merriweather Sans", sans-serif',

      monoFamily: '"Inconsolata", monospace',

    },

    effects: {

      cardShadow: '0 8px 32px rgba(0,0,0,0.45), 0 0 15px rgba(80,200,180,0.06)',

      cardShadowHover: '0 12px 40px rgba(0,0,0,0.55), 0 0 25px rgba(80,200,180,0.12)',

      buttonShadow: '0 2px 8px rgba(80,200,180,0.2)',

      glowAccent: '0 0 20px rgba(80,200,180,0.35)',

      textShadow: '0 1px 2px rgba(0,0,0,0.4)',

      headingTextShadow: '0 2px 4px rgba(0,0,0,0.5)',

      borderRadius: '8px',

      borderRadiusLg: '12px',

      borderRadiusSm: '4px',

      borderWidth: '2px',

      cardBorder: '2px solid #686050',

      inputBorder: '1px solid #484038',

      transition: 'all 0.2s ease',

    },

  },



  'enchanted-forest': {

    id: 'enchanted-forest',

    name: 'Enchanted Forest',

    description: 'Deep teals with mystical purples and gold fireflies',

    category: 'multicolor',

    preview: '🧚',

    colors: {

      bgPrimary: 'linear-gradient(135deg, #060808 0%, #080c0c 50%, #06090a 100%)',

      bgSecondary: '#040505',

      bgCard: 'linear-gradient(145deg, rgba(25,48,50,0.98) 0%, rgba(22,40,45,0.99) 100%)',

      bgCardSolid: '#1e3840',

      bgCardHover: '#284850',

      bgInput: 'rgba(15,28,30,0.95)',

      bgHover: 'rgba(200,180,100,0.2)',

      bgModal: 'rgba(0,0,0,0.9)',

      accent: '#c8b464',

      accentLight: '#e0d090',

      accentDark: '#a08840',

      accentGlow: 'rgba(200,180,100,0.45)',

      success: '#50c0a0',

      successBg: 'rgba(80,192,160,0.25)',

      danger: '#c86880',

      dangerBg: 'rgba(200,104,128,0.25)',

      warning: '#c8b464',

      warningBg: 'rgba(200,180,100,0.25)',

      info: '#6890c8',

      infoBg: 'rgba(104,144,200,0.25)',

      mana: '#9078c8',

      manaBg: 'rgba(144,120,200,0.3)',

      textPrimary: '#e8f0f0',

      textSecondary: '#a8c8c8',

      textMuted: '#689090',

      textOnAccent: '#101510',

      borderPrimary: '#386058',

      borderLight: '#284540',

      borderAccent: '#c8b464',

      hpHigh: '#50c0a0',

      hpMid: '#c8b464',

      hpLow: '#c86880',

      buffBg: 'rgba(80,192,160,0.35)',

      buffBorder: '#50c0a0',

      buffText: '#88f0d0',

      debuffBg: 'rgba(200,104,128,0.35)',

      debuffBorder: '#c86880',

      debuffText: '#f0a8b8',

    },

    typography: {

      fontFamily: '"Cinzel", Georgia, serif',

      headingFamily: '"Cinzel", serif',

      monoFamily: '"Fira Code", monospace',

    },

    effects: {

      cardShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 20px rgba(200,180,100,0.08)',

      cardShadowHover: '0 12px 40px rgba(0,0,0,0.6), 0 0 35px rgba(200,180,100,0.18)',

      buttonShadow: '0 2px 8px rgba(200,180,100,0.3)',

      glowAccent: '0 0 25px rgba(200,180,100,0.45)',

      textShadow: '0 1px 3px rgba(0,0,0,0.5)',

      headingTextShadow: '0 2px 4px rgba(0,0,0,0.6)',

      borderRadius: '10px',

      borderRadiusLg: '14px',

      borderRadiusSm: '5px',

      borderWidth: '2px',

      cardBorder: '2px solid #386058',

      inputBorder: '1px solid #284540',

      transition: 'all 0.25s ease',

    },

  },



  'mint-chocolate': {

    id: 'mint-chocolate',

    name: 'Mint Chocolate',

    description: 'Fresh cool mints with warm chocolatey browns',

    category: 'multicolor',

    preview: '🍫',

    colors: {

      bgPrimary: 'linear-gradient(135deg, #060808 0%, #080c0c 50%, #060909 100%)',

      bgSecondary: '#040505',

      bgCard: 'linear-gradient(145deg, rgba(48,38,32,0.98) 0%, rgba(40,32,28,0.99) 100%)',

      bgCardSolid: '#362a24',

      bgCardHover: '#463830',

      bgInput: 'rgba(28,22,18,0.95)',

      bgHover: 'rgba(100,220,180,0.2)',

      bgModal: 'rgba(0,0,0,0.9)',

      accent: '#64dcb4',

      accentLight: '#90f0d0',

      accentDark: '#40b890',

      accentGlow: 'rgba(100,220,180,0.4)',

      success: '#64dcb4',

      successBg: 'rgba(100,220,180,0.25)',

      danger: '#dc7878',

      dangerBg: 'rgba(220,120,120,0.25)',

      warning: '#dcc878',

      warningBg: 'rgba(220,200,120,0.25)',

      info: '#78b4dc',

      infoBg: 'rgba(120,180,220,0.25)',

      mana: '#78b4dc',

      manaBg: 'rgba(120,180,220,0.3)',

      textPrimary: '#f8f4f0',

      textSecondary: '#d8c8c0',

      textMuted: '#a09088',

      textOnAccent: '#101410',

      borderPrimary: '#5a4840',

      borderLight: '#3a302a',

      borderAccent: '#64dcb4',

      hpHigh: '#64dcb4',

      hpMid: '#dcc878',

      hpLow: '#dc7878',

      buffBg: 'rgba(100,220,180,0.35)',

      buffBorder: '#64dcb4',

      buffText: '#a0ffd8',

      debuffBg: 'rgba(220,120,120,0.35)',

      debuffBorder: '#dc7878',

      debuffText: '#f8b8b8',

    },

    typography: {

      fontFamily: '"Poppins", "Segoe UI", sans-serif',

      headingFamily: '"Poppins", sans-serif',

      monoFamily: '"Fira Code", monospace',

    },

    effects: {

      cardShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 15px rgba(100,220,180,0.06)',

      cardShadowHover: '0 12px 40px rgba(0,0,0,0.6), 0 0 25px rgba(100,220,180,0.12)',

      buttonShadow: '0 2px 8px rgba(100,220,180,0.25)',

      glowAccent: '0 0 22px rgba(100,220,180,0.4)',

      textShadow: '0 1px 2px rgba(0,0,0,0.45)',

      headingTextShadow: '0 2px 4px rgba(0,0,0,0.55)',

      borderRadius: '12px',

      borderRadiusLg: '16px',

      borderRadiusSm: '6px',

      borderWidth: '2px',

      cardBorder: '2px solid #5a4840',

      inputBorder: '1px solid #3a302a',

      transition: 'all 0.2s ease',

    },

  },



  'pro-dark': {

    id: 'pro-dark',

    name: 'Professional Dark',

    description: 'Sleek dark mode for focused sessions',

    category: 'professional',

    preview: '🌙',

    colors: {

      bgPrimary: '#020204',

      bgSecondary: '#010102',

      bgCard: '#0c0c10',

      bgCardSolid: '#101018',

      bgCardHover: '#181820',

      bgInput: '#08080c',

      bgHover: 'rgba(59,130,246,0.15)',

      bgModal: 'rgba(0,0,0,0.9)',

      accent: '#3b82f6',

      accentLight: '#60a5fa',

      accentDark: '#2563eb',

      accentGlow: 'rgba(59,130,246,0.3)',

      success: '#34d399',

      successBg: 'rgba(52,211,153,0.2)',

      danger: '#f87171',

      dangerBg: 'rgba(248,113,113,0.2)',

      warning: '#fbbf24',

      warningBg: 'rgba(251,191,36,0.2)',

      info: '#60a5fa',

      infoBg: 'rgba(96,165,250,0.2)',

      mana: '#a78bfa',

      manaBg: 'rgba(167,139,250,0.2)',

      textPrimary: '#f8fafc',

      textSecondary: '#b0c4de',

      textMuted: '#7a9bc0',

      textOnAccent: '#ffffff',

      borderPrimary: '#282830',

      borderLight: '#1c1c24',

      borderAccent: '#3b82f6',

      hpHigh: '#34d399',

      hpMid: '#fbbf24',

      hpLow: '#f87171',

      buffBg: 'rgba(52,211,153,0.25)',

      buffBorder: '#34d399',

      buffText: '#6ee7b7',

      debuffBg: 'rgba(248,113,113,0.25)',

      debuffBorder: '#f87171',

      debuffText: '#fca5a5',

    },

    typography: {

      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',

      headingFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',

      monoFamily: 'ui-monospace, "SF Mono", Monaco, monospace',

    },

    effects: {

      cardShadow: '0 4px 16px rgba(0,0,0,0.4), 0 0 1px rgba(59,130,246,0.1)',

      cardShadowHover: '0 8px 24px rgba(0,0,0,0.5), 0 0 20px rgba(59,130,246,0.1)',

      buttonShadow: '0 2px 4px rgba(0,0,0,0.3)',

      glowAccent: '0 0 0 3px rgba(59,130,246,0.25)',

      textShadow: '0 1px 2px rgba(0,0,0,0.4)',

      headingTextShadow: '0 1px 3px rgba(0,0,0,0.5)',

      borderRadius: '12px',

      borderRadiusLg: '16px',

      borderRadiusSm: '8px',

      borderWidth: '1px',

      cardBorder: '1px solid #334155',

      inputBorder: '1px solid #475569',

      transition: 'all 0.15s ease',

    },

  },



  'pro-midnight': {

    id: 'pro-midnight',

    name: 'Midnight',

    description: 'True black for OLED screens',

    category: 'professional',

    preview: '🌑',

    colors: {

      bgPrimary: '#000000',

      bgSecondary: '#000000',

      bgCard: '#1a1a1a',

      bgCardSolid: '#1c1c1c',

      bgCardHover: '#282828',

      bgInput: '#0c0c0c',

      bgHover: 'rgba(59,130,246,0.2)',

      bgModal: '#000000',

      accent: '#3b82f6',

      accentLight: '#60a5fa',

      accentDark: '#2563eb',

      accentGlow: 'rgba(59,130,246,0.4)',

      success: '#22c55e',

      successBg: 'rgba(34,197,94,0.2)',

      danger: '#ef4444',

      dangerBg: 'rgba(239,68,68,0.2)',

      warning: '#eab308',

      warningBg: 'rgba(234,179,8,0.2)',

      info: '#3b82f6',

      infoBg: 'rgba(59,130,246,0.2)',

      mana: '#8b5cf6',

      manaBg: 'rgba(139,92,246,0.2)',

      textPrimary: '#fafafa',

      textSecondary: '#a1a1aa',

      textMuted: '#71717a',

      textOnAccent: '#ffffff',

      borderPrimary: '#2a2a2a',

      borderLight: '#1a1a1a',

      borderAccent: '#3b82f6',

      hpHigh: '#22c55e',

      hpMid: '#eab308',

      hpLow: '#ef4444',

      buffBg: 'rgba(34,197,94,0.25)',

      buffBorder: '#22c55e',

      buffText: '#86efac',

      debuffBg: 'rgba(239,68,68,0.25)',

      debuffBorder: '#ef4444',

      debuffText: '#fca5a5',

    },

    typography: {

      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',

      headingFamily: 'Inter, sans-serif',

      monoFamily: '"JetBrains Mono", monospace',

    },

    effects: {

      cardShadow: '0 0 0 1px #2a2a2a',

      cardShadowHover: '0 0 0 1px #3b82f6, 0 0 20px rgba(59,130,246,0.15)',

      buttonShadow: '0 1px 2px rgba(0,0,0,0.8)',

      glowAccent: '0 0 0 2px rgba(59,130,246,0.5)',

      textShadow: '0 1px 2px rgba(0,0,0,0.6)',

      headingTextShadow: '0 1px 3px rgba(0,0,0,0.7)',

      borderRadius: '8px',

      borderRadiusLg: '12px',

      borderRadiusSm: '6px',

      borderWidth: '1px',

      cardBorder: '1px solid #2a2a2a',

      inputBorder: '1px solid #3f3f46',

      transition: 'all 0.1s ease',

    },

  },



  'eldritch-horror': {

    id: 'eldritch-horror',

    name: 'Eldritch Horror',

    description: 'Cosmic dread and unknowable terrors',

    category: 'fantasy',

    preview: '🐙',

    colors: {

      bgPrimary: 'linear-gradient(135deg, #0a0a0f 0%, #0d1018 50%, #080510 100%)',

      bgSecondary: '#060608',

      bgCard: 'linear-gradient(180deg, rgba(20,25,35,0.98) 0%, rgba(15,18,28,0.99) 100%)',

      bgCardSolid: '#141923',

      bgCardHover: '#1c2430',

      bgInput: 'rgba(12,15,22,0.95)',

      bgHover: 'rgba(120,180,140,0.15)',

      bgModal: 'rgba(0,0,0,0.95)',

      accent: '#78b48c',

      accentLight: '#a8d8b8',

      accentDark: '#4a8860',

      accentGlow: 'rgba(120,180,140,0.4)',

      success: '#78b48c',

      successBg: 'rgba(120,180,140,0.25)',

      danger: '#b84a68',

      dangerBg: 'rgba(184,74,104,0.25)',

      warning: '#b8a848',

      warningBg: 'rgba(184,168,72,0.25)',

      info: '#6888b8',

      infoBg: 'rgba(104,136,184,0.25)',

      mana: '#8868a8',

      manaBg: 'rgba(136,104,168,0.35)',

      textPrimary: '#c8d0d8',

      textSecondary: '#8898a8',

      textMuted: '#586878',

      textOnAccent: '#0a0a0f',

      borderPrimary: '#2a3848',

      borderLight: '#1a2530',

      borderAccent: '#78b48c',

      hpHigh: '#78b48c',

      hpMid: '#b8a848',

      hpLow: '#b84a68',

      buffBg: 'rgba(120,180,140,0.30)',

      buffBorder: '#78b48c',

      buffText: '#a8e8b8',

      debuffBg: 'rgba(184,74,104,0.30)',

      debuffBorder: '#b84a68',

      debuffText: '#e8a8b8',

    },

    typography: {

      fontFamily: '"Crimson Text", Georgia, serif',

      headingFamily: '"Crimson Text", serif',

      monoFamily: '"Fira Code", monospace',

    },

    effects: {

      cardShadow: '0 8px 32px rgba(0,0,0,0.7), 0 0 40px rgba(120,180,140,0.08)',

      cardShadowHover: '0 12px 40px rgba(0,0,0,0.8), 0 0 50px rgba(120,180,140,0.12)',

      buttonShadow: '0 2px 8px rgba(0,0,0,0.5)',

      glowAccent: '0 0 30px rgba(120,180,140,0.3)',

      textShadow: '0 1px 3px rgba(0,0,0,0.7)',

      headingTextShadow: '0 2px 6px rgba(0,0,0,0.8)',

      borderRadius: '6px',

      borderRadiusLg: '10px',

      borderRadiusSm: '4px',

      borderWidth: '2px',

      cardBorder: '2px solid #2a3848',

      inputBorder: '1px solid #1a2530',

      transition: 'all 0.3s ease',

    },

  },



  'high-contrast': {

    id: 'high-contrast',

    name: 'High Contrast',

    description: 'Maximum readability for accessibility',

    category: 'accessibility',

    preview: '👁',

    colors: {

      bgPrimary: '#000000',

      bgSecondary: '#000000',

      bgCard: '#1a1a1a',

      bgCardSolid: '#1a1a1a',

      bgCardHover: '#2a2a2a',

      bgInput: '#0a0a0a',

      bgHover: 'rgba(255,255,0,0.3)',

      bgModal: '#000000',

      accent: '#ffff00',

      accentLight: '#ffff66',

      accentDark: '#cccc00',

      accentGlow: 'rgba(255,255,0,0.5)',

      success: '#00ff00',

      successBg: 'rgba(0,255,0,0.25)',

      danger: '#ff0000',

      dangerBg: 'rgba(255,0,0,0.25)',

      warning: '#ffff00',

      warningBg: 'rgba(255,255,0,0.25)',

      info: '#00ffff',

      infoBg: 'rgba(0,255,255,0.25)',

      mana: '#00ffff',

      manaBg: 'rgba(0,255,255,0.25)',

      textPrimary: '#ffffff',

      textSecondary: '#ffffff',

      textMuted: '#cccccc',

      textOnAccent: '#000000',

      borderPrimary: '#ffffff',

      borderLight: '#888888',

      borderAccent: '#ffff00',

      hpHigh: '#00ff00',

      hpMid: '#ffff00',

      hpLow: '#ff0000',

      buffBg: 'rgba(0,255,0,0.35)',

      buffBorder: '#00ff00',

      buffText: '#00ff00',

      debuffBg: 'rgba(255,0,0,0.35)',

      debuffBorder: '#ff0000',

      debuffText: '#ff0000',

    },

    typography: {

      fontFamily: 'Arial, Helvetica, sans-serif',

      headingFamily: 'Arial, sans-serif',

      monoFamily: '"Courier New", Courier, monospace',

    },

    effects: {

      cardShadow: 'none',

      cardShadowHover: '0 0 0 3px #ffff00',

      buttonShadow: 'none',

      glowAccent: 'none',

      textShadow: 'none',

      headingTextShadow: 'none',

      borderRadius: '4px',

      borderRadiusLg: '4px',

      borderRadiusSm: '2px',

      borderWidth: '3px',

      cardBorder: '3px solid #ffffff',

      inputBorder: '2px solid #ffffff',

      transition: 'none',

    },

  },

};


export const QuickCounterFallback = ({ value = 0, onCommit }) => (

  <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>

    <span style={{ fontSize: '0.85rem' }}>Quick counter:</span>

    <QuantityStepper
      value={Number.isFinite(value) ? value : 0}
      onCommit={onCommit}
      step={1}
      allowNegative
      emptyValue={0}
      inputLabel="Quick counter value"
      decrementLabel="-"
      incrementLabel="+"
      style={{ gap: '8px', flexWrap: 'wrap' }}
      buttonStyle={{ ...S.btn, ...S.btnXs, marginBottom: 0 }}
      inputStyle={{ width: '60px', minWidth: '60px', padding: '6px', fontWeight: 'bold' }}
    />

  </div>

);


export const TravelDayWeekControls = ({ currentDay, currentWeekNumber, onCommitDay, onCommitWeek }) => (

  <div style={S.flex}>

    <div>

      <label style={{ ...S.label, fontSize: '0.8rem' }}>Day</label>

      <CompactNumberField
        aria-label="Travel day"
        value={currentDay}
        onCommit={onCommitDay}
        min={1}
        emptyValue={1}
        style={{ width: '60px', minWidth: '60px', padding: '6px' }}
      />

    </div>

    <div>

      <label style={{ ...S.label, fontSize: '0.8rem' }}>Week #</label>

      <CompactNumberField
        aria-label="Travel week number"
        value={currentWeekNumber || 1}
        onCommit={onCommitWeek}
        min={1}
        emptyValue={1}
        style={{ width: '60px', minWidth: '60px', padding: '6px' }}
      />

    </div>

  </div>

);



// ============================================================================

// THEME VARIATIONS - 3+ variations per main theme

// Each variation overrides specific color properties

// ============================================================================



const THEME_VARIATIONS = {

  'fantasy-classic': [

    { id: 'default', name: 'Classic' },

    { id: 'void', name: 'Void', colors: { 

      bgPrimary: '#010102', 

      bgSecondary: '#000001', 

      bgCardSolid: '#1a1810', 

      bgCardHover: '#2a2820', 

      textPrimary: '#d0c8b0', 

      textSecondary: '#a89870' 

    }},

  ],

  'fantasy-dark': [

    { id: 'default', name: 'Abyss' },

    { id: 'mystic', name: 'Mystic', colors: { bgPrimary: 'linear-gradient(135deg, #050008 0%, #0a0412 50%, #08060f 100%)', bgSecondary: '#030005', textPrimary: '#f4eeff', textSecondary: '#d0c0e8' }},

    { id: 'blood', name: 'Blood Magic', colors: { bgPrimary: 'linear-gradient(135deg, #100408 0%, #1c0810 50%, #140610 100%)', bgSecondary: '#080204', textPrimary: '#ffe0e8', textSecondary: '#e8a0b0', accent: '#ff4080', accentLight: '#ff70a0' }},

    { id: 'cosmic', name: 'Cosmic', colors: { bgPrimary: 'linear-gradient(135deg, #040818 0%, #081030 50%, #061028 100%)', bgSecondary: '#020410', textPrimary: '#e0f0ff', textSecondary: '#90c0f8', accent: '#60a0ff', accentLight: '#90c8ff' }},

  ],

  'fantasy-nature': [

    { id: 'default', name: 'Autumn' },

    { id: 'grove', name: 'Grove', colors: { 

      bgPrimary: 'linear-gradient(135deg, #040a04 0%, #081208 50%, #050f08 100%)', 

      bgSecondary: '#020502', 

      bgCardSolid: '#2d4630',

      bgCardHover: '#3a5a3e',

      textPrimary: '#e8f5e0', 

      textSecondary: '#b8d8a8',

      textMuted: '#7aa868',

      accent: '#4a9c5d',

      accentLight: '#7ddb8a',

      borderPrimary: '#3d7a45',

      borderLight: '#2a5530',

    }},

    { id: 'toxic', name: 'Toxic', colors: { bgPrimary: 'linear-gradient(135deg, #041004 0%, #082008 50%, #061806 100%)', bgSecondary: '#020802', textPrimary: '#c0ffc0', textSecondary: '#70e070', accent: '#40ff40', accentLight: '#80ff80' }},

    { id: 'frost', name: 'Frost', colors: { bgPrimary: 'linear-gradient(135deg, #040a10 0%, #081420 50%, #061018 100%)', bgSecondary: '#020508', textPrimary: '#e8f8ff', textSecondary: '#a0d8f0', accent: '#60c8e8', accentLight: '#90e0f8' }},

  ],

  'sunset-dusk': [

    { id: 'default', name: 'Classic' },

    { id: 'violet', name: 'Violet', colors: { bgPrimary: 'linear-gradient(135deg, #0c0410 0%, #140820 50%, #100618 100%)', bgSecondary: '#060208', textPrimary: '#f8f0ff', textSecondary: '#d8c0f0', accent: '#c880ff', accentLight: '#e0a8ff' }},

    { id: 'inferno', name: 'Inferno', colors: { bgPrimary: 'linear-gradient(135deg, #180404 0%, #280808 50%, #200606 100%)', bgSecondary: '#0c0202', textPrimary: '#fff0e0', textSecondary: '#f0c090', accent: '#ff7030', accentLight: '#ff9050' }},

    { id: 'neon', name: 'Neon', colors: { bgPrimary: 'linear-gradient(135deg, #080420 0%, #140830 50%, #100628 100%)', bgSecondary: '#040210', textPrimary: '#f8e0ff', textSecondary: '#e0a0f0', accent: '#ff40e0', accentLight: '#ff80f0' }},

  ],

  'ocean-depths': [

    { id: 'default', name: 'Classic' },

    { id: 'abyss', name: 'Abyss', colors: { bgPrimary: 'linear-gradient(135deg, #040210 0%, #080418 50%, #060314 100%)', bgSecondary: '#020108', textPrimary: '#d8e0ff', textSecondary: '#90a8e0', accent: '#8080ff', accentLight: '#a8a8ff' }},

    { id: 'tropical', name: 'Tropical', colors: { bgPrimary: 'linear-gradient(135deg, #021008 0%, #042010 50%, #03180c 100%)', bgSecondary: '#010804', textPrimary: '#d0fff0', textSecondary: '#70f0c8', accent: '#40f0a0', accentLight: '#80f8c0' }},

    { id: 'arctic', name: 'Arctic', colors: { bgPrimary: 'linear-gradient(135deg, #081018 0%, #102030 50%, #0c1828 100%)', bgSecondary: '#040810', textPrimary: '#f8ffff', textSecondary: '#c8e8f8', accent: '#a0e0ff', accentLight: '#c8f0ff' }},

  ],

  'northern-lights': [

    { id: 'default', name: 'Classic' },

    { id: 'solar', name: 'Solar Storm', colors: { bgPrimary: 'linear-gradient(135deg, #100408 0%, #200c18 50%, #180810 100%)', bgSecondary: '#080204', textPrimary: '#fff0f8', textSecondary: '#f0a8c8', accent: '#ff60b0', accentLight: '#ff90d0' }},

    { id: 'forest', name: 'Forest', colors: { bgPrimary: 'linear-gradient(135deg, #020c04 0%, #041808 50%, #031406 100%)', bgSecondary: '#010602', textPrimary: '#d8ffe8', textSecondary: '#80f0a8', accent: '#40ff70', accentLight: '#80ffa0' }},

    { id: 'frozen', name: 'Frozen', colors: { bgPrimary: 'linear-gradient(135deg, #040c18 0%, #082030 50%, #061828 100)', bgSecondary: '#020610', textPrimary: '#f0ffff', textSecondary: '#a8e8ff', accent: '#60d8ff', accentLight: '#a0f0ff' }},

  ],

  'copper-teal': [

    { id: 'default', name: 'Classic' },

    { id: 'forge', name: 'Forge', colors: { bgPrimary: 'linear-gradient(135deg, #140804 0%, #201008 50%, #180a06 100%)', bgSecondary: '#0a0402', textPrimary: '#fff8e0', textSecondary: '#f0c890', accent: '#ff8040', accentLight: '#ffa060' }},

    { id: 'patina', name: 'Patina', colors: { bgPrimary: 'linear-gradient(135deg, #041010 0%, #082020 50%, #061818 100%)', bgSecondary: '#020808', textPrimary: '#e0ffff', textSecondary: '#90e8e8', accent: '#40d8d8', accentLight: '#70f0f0' }},

    { id: 'rust', name: 'Rust', colors: { bgPrimary: 'linear-gradient(135deg, #0c0604 0%, #180c08 50%, #100806 100%)', bgSecondary: '#060302', textPrimary: '#f0e0d0', textSecondary: '#c8a080', bgCardSolid: '#302418', bgCardHover: '#403420' }},

  ],

  'sakura': [

    { id: 'default', name: 'Classic' },

    { id: 'spring', name: 'Spring', colors: { bgPrimary: 'linear-gradient(135deg, #061008 0%, #0c2010 50%, #08180c 100%)', bgSecondary: '#030804', textPrimary: '#e8ffe8', textSecondary: '#a0f0a0', accent: '#60e060', accentLight: '#90f890' }},

    { id: 'sunset', name: 'Sunset', colors: { bgPrimary: 'linear-gradient(135deg, #140808 0%, #201010 50%, #180c0c 100%)', bgSecondary: '#0a0404', textPrimary: '#fff0e8', textSecondary: '#f8c8a0', accent: '#f89060', accentLight: '#ffb080' }},

    { id: 'moonlit', name: 'Moonlit', colors: { bgPrimary: 'linear-gradient(135deg, #060810 0%, #0c1020 50%, #080c18 100)', bgSecondary: '#030408', textPrimary: '#f0f8ff', textSecondary: '#b0d0f0', accent: '#a0c0ff', accentLight: '#c0d8ff' }},

  ],

  'royal-court': [

    { id: 'default', name: 'Classic' },

    { id: 'crimson', name: 'Crimson', colors: { bgPrimary: 'linear-gradient(135deg, #180408 0%, #280810 50%, #200610 100%)', bgSecondary: '#0c0204', textPrimary: '#fff0f0', textSecondary: '#f0c0c0', accent: '#e07070', accentLight: '#f09090' }},

    { id: 'obsidian', name: 'Obsidian', colors: { bgPrimary: '#020204', bgSecondary: '#010102', bgCardSolid: '#181820', bgCardHover: '#282830', textPrimary: '#e0e0e8', textSecondary: '#a0a0b0' }},

    { id: 'emerald', name: 'Emerald', colors: { bgPrimary: 'linear-gradient(135deg, #040c08 0%, #081810 50%, #06140c 100)', bgSecondary: '#020604', textPrimary: '#e8fff0', textSecondary: '#90e8b0', accent: '#50d880', accentLight: '#80f0a8' }},

  ],

  'ember-frost': [

    { id: 'default', name: 'Classic' },

    { id: 'volcano', name: 'Volcano', colors: { bgPrimary: 'linear-gradient(135deg, #200404 0%, #300808 50%, #280606 100%)', bgSecondary: '#100202', textPrimary: '#fff8e8', textSecondary: '#ffc8a0', accent: '#ff5020', accentLight: '#ff8050', bgCardSolid: '#402018', bgCardHover: '#503020' }},

    { id: 'glacier', name: 'Glacier', colors: { bgPrimary: 'linear-gradient(135deg, #040c18 0%, #082030 50%, #061828 100%)', bgSecondary: '#020610', textPrimary: '#e8ffff', textSecondary: '#a0e8ff', accent: '#40c8ff', accentLight: '#80e0ff', bgCardSolid: '#102838', bgCardHover: '#183848' }},

    { id: 'chaos', name: 'Chaos', colors: { bgPrimary: 'linear-gradient(135deg, #100410 0%, #200820 50%, #180618 100%)', bgSecondary: '#080208', textPrimary: '#ffe8ff', textSecondary: '#e0a0e0', accent: '#d060d0', accentLight: '#e890e8' }},

  ],

  'desert-oasis': [

    { id: 'default', name: 'Classic' },

    { id: 'scorched', name: 'Scorched', colors: { bgPrimary: 'linear-gradient(135deg, #180a04 0%, #281408 50%, #201006 100%)', bgSecondary: '#0c0502', textPrimary: '#fffce0', textSecondary: '#f0d890', accent: '#e8b040', accentLight: '#f8d060' }},

    { id: 'night', name: 'Night', colors: { bgPrimary: 'linear-gradient(135deg, #040610 0%, #081020 50%, #060c18 100%)', bgSecondary: '#020308', textPrimary: '#e8f0ff', textSecondary: '#a8c8f0', accent: '#7090d0', accentLight: '#90b0e8' }},

    { id: 'mirage', name: 'Mirage', colors: { bgPrimary: 'linear-gradient(135deg, #0c080c 0%, #181018 50%, #140c14 100%)', bgSecondary: '#060406', textPrimary: '#f8f0f8', textSecondary: '#d8c0d8', accent: '#c090c0', accentLight: '#e0b0e0' }},

  ],

  'enchanted-forest': [

    { id: 'default', name: 'Classic' },

    { id: 'biolume', name: 'Biolume', colors: { bgPrimary: 'linear-gradient(135deg, #020a06 0%, #041808 50%, #031206 100%)', bgSecondary: '#010503', textPrimary: '#c0fff0', textSecondary: '#60f0c0', accent: '#30ffa0', accentLight: '#70ffc0' }},

    { id: 'twilight', name: 'Twilight', colors: { bgPrimary: 'linear-gradient(135deg, #0a0610 0%, #140c20 50%, #100818 100%)', bgSecondary: '#050308', textPrimary: '#f0e8ff', textSecondary: '#c8a8f0', accent: '#a070e8', accentLight: '#c098f8' }},

    { id: 'ancient', name: 'Ancient', colors: { bgPrimary: 'linear-gradient(135deg, #0a0804 0%, #141008 50%, #100c06 100)', bgSecondary: '#050402', textPrimary: '#f0e8d8', textSecondary: '#c8b890', bgCardSolid: '#283020', bgCardHover: '#384030' }},

  ],

  'mint-chocolate': [

    { id: 'default', name: 'Classic' },

    { id: 'dark', name: 'Dark', colors: { bgPrimary: '#020202', bgSecondary: '#010101', bgCardSolid: '#201810', bgCardHover: '#302820', textPrimary: '#f0e8e0', textSecondary: '#c0b0a0' }},

    { id: 'fresh', name: 'Fresh', colors: { bgPrimary: 'linear-gradient(135deg, #021008 0%, #042010 50%, #031808 100%)', bgSecondary: '#010804', textPrimary: '#d0fff0', textSecondary: '#80f8d0', accent: '#40f8a0', accentLight: '#80ffc0' }},

    { id: 'caramel', name: 'Caramel', colors: { bgPrimary: 'linear-gradient(135deg, #120a04 0%, #1c1006 50%, #160c05 100%)', bgSecondary: '#090502', textPrimary: '#fff8e0', textSecondary: '#e8d0a0', accent: '#e0a050', accentLight: '#f0c080' }},

  ],

  'pro-dark': [

    { id: 'default', name: 'Pitch' },

    { id: 'navy', name: 'Navy', colors: { bgPrimary: '#080d18', bgSecondary: '#050a12', bgCard: '#1a2538', bgCardSolid: '#1e2d42', bgCardHover: '#283a52', borderPrimary: '#334155', borderLight: '#1e293b' }},

    { id: 'marine', name: 'Marine', colors: { bgPrimary: '#041018', bgSecondary: '#020810', textPrimary: '#e0f4ff', textSecondary: '#90c8e8', accent: '#40a0d8', accentLight: '#70c0f0' }},

    { id: 'wine', name: 'Wine', colors: { bgPrimary: '#100810', bgSecondary: '#080408', textPrimary: '#f8f0f4', textSecondary: '#d8b0c8', accent: '#c870a0', accentLight: '#e098c0' }},

  ],

  'pro-midnight': [

    { id: 'default', name: 'Classic' },

    { id: 'ember', name: 'Ember', colors: { bgPrimary: '#020100', bgSecondary: '#010100', accent: '#ff5030', accentLight: '#ff8060', accentGlow: 'rgba(255,80,48,0.5)', textSecondary: '#c09080' }},

    { id: 'matrix', name: 'Matrix', colors: { bgPrimary: '#000200', bgSecondary: '#000100', accent: '#00ff40', accentLight: '#60ff80', accentGlow: 'rgba(0,255,64,0.5)', textPrimary: '#c0ffc8', textSecondary: '#70c878' }},

    { id: 'cyber', name: 'Cyber', colors: { bgPrimary: '#000204', bgSecondary: '#000102', accent: '#00d0ff', accentLight: '#60e8ff', accentGlow: 'rgba(0,208,255,0.5)', textPrimary: '#d8f8ff', textSecondary: '#80c8e0' }},

  ],

  'eldritch-horror': [

    { id: 'default', name: 'Cthulhu' },

    { id: 'void', name: 'Void', colors: { 

      bgPrimary: '#000000', 

      bgSecondary: '#000000', 

      bgCardSolid: '#0a0a10',

      bgCardHover: '#101018',

      textPrimary: '#a0a0b8', 

      textSecondary: '#6868a0',

      accent: '#6858a8',

      accentLight: '#9080d0',

      accentGlow: 'rgba(104,88,168,0.4)',

    }},

    { id: 'madness', name: 'Madness', colors: { 

      bgPrimary: 'linear-gradient(135deg, #10080a 0%, #0a1008 50%, #080810 100%)', 

      bgSecondary: '#080808',

      textPrimary: '#d8d0a0', 

      textSecondary: '#a8a060',

      accent: '#c8b830',

      accentLight: '#e8d850',

      accentGlow: 'rgba(200,184,48,0.4)',

      warning: '#c8b830',

    }},

    { id: 'deep-one', name: 'Deep One', colors: { 

      bgPrimary: 'linear-gradient(135deg, #04080a 0%, #061012 50%, #081418 100%)', 

      bgSecondary: '#040808',

      bgCardSolid: '#0c1820',

      bgCardHover: '#142028',

      textPrimary: '#b0d0d8', 

      textSecondary: '#6898a8',

      accent: '#40a8a0',

      accentLight: '#68d0c8',

      accentGlow: 'rgba(64,168,160,0.4)',

      mana: '#4888a8',

      borderPrimary: '#204048',

    }},

    { id: 'cosmic', name: 'Cosmic', colors: { 

      bgPrimary: 'linear-gradient(135deg, #080410 0%, #100820 50%, #0c0618 100%)', 

      bgSecondary: '#060410',

      bgCardSolid: '#181028',

      bgCardHover: '#201838',

      textPrimary: '#d8d0f0', 

      textSecondary: '#a090d0',

      accent: '#a070e0',

      accentLight: '#c8a0ff',

      accentGlow: 'rgba(160,112,224,0.5)',

      mana: '#8060c0',

      borderPrimary: '#3a2860',

    }},

  ],

  'high-contrast': [

    { id: 'default', name: 'Classic' },

    { id: 'amber', name: 'Amber', colors: { accent: '#ffa000', accentLight: '#ffc040', warning: '#ffa000', borderAccent: '#ffa000', textOnAccent: '#000000' }},

    { id: 'matrix', name: 'Matrix', colors: { accent: '#00ff00', accentLight: '#80ff80', warning: '#00ff00', borderAccent: '#00ff00', textPrimary: '#00ff00', textSecondary: '#00c000', success: '#00ff00' }},

  ],

};



// Theme display order (matching demo)

const THEME_ORDER = [

  'fantasy-classic', 'northern-lights', 'sunset-dusk', 'royal-court', 

  'enchanted-forest', 'desert-oasis', 'sakura', 'mint-chocolate', 'copper-teal',

  'ocean-depths', 'ember-frost', 'fantasy-dark', 'eldritch-horror', 'fantasy-nature',

  'pro-dark', 'pro-midnight'

];



// ============================================================================

// LAYOUT DENSITY OPTIONS

// ============================================================================



const DENSITY_MODES = {

  'compact': {

    id: 'compact', name: 'Compact', description: 'Maximum information density', icon: '▪️',

    spacing: { cardPadding: '12px', cardGap: '10px', sectionGap: '10px', itemGap: '4px' },

    typography: { baseFontSize: '13px', headingSize: '1rem', labelSize: '0.7rem', badgeSize: '0.65rem' },

    layout: { gridMinWidth: '220px', badgePadding: '3px 8px', buttonPadding: '6px 10px', inputPadding: '8px' },

  },

  'comfortable': {

    id: 'comfortable', name: 'Comfortable', description: 'Balanced readability', icon: '◾',

    spacing: { cardPadding: '20px', cardGap: '16px', sectionGap: '16px', itemGap: '8px' },

    typography: { baseFontSize: '15px', headingSize: '1.2rem', labelSize: '0.8rem', badgeSize: '0.75rem' },

    layout: { gridMinWidth: '280px', badgePadding: '5px 12px', buttonPadding: '10px 16px', inputPadding: '12px' },

  },

  'spacious': {

    id: 'spacious', name: 'Spacious', description: 'Easy reading', icon: '⬛',

    spacing: { cardPadding: '28px', cardGap: '24px', sectionGap: '24px', itemGap: '12px' },

    typography: { baseFontSize: '17px', headingSize: '1.4rem', labelSize: '0.85rem', badgeSize: '0.8rem' },

    layout: { gridMinWidth: '320px', badgePadding: '6px 14px', buttonPadding: '12px 20px', inputPadding: '14px' },

  },

};



// ============================================================================

// CARD STYLE OPTIONS

// ============================================================================



const CARD_STYLES = {

  'elevated': {

    id: 'elevated', name: 'Elevated', icon: '📦',

    getStyle: (theme, hovered) => ({

      background: theme.colors.bgCard,

      border: hovered ? `1px solid ${theme.colors.accent}50` : 'none',

      boxShadow: hovered 

        ? `0 16px 48px rgba(0,0,0,0.6), 0 0 25px ${theme.colors.accentGlow || 'rgba(255,215,0,0.3)'}` 

        : (theme.effects?.cardShadow || '0 8px 32px rgba(0,0,0,0.6)'),

      borderRadius: theme.effects?.borderRadiusLg || '12px',

    }),

  },

  'bordered': {

    id: 'bordered', name: 'Bordered', icon: '🖼️',

    getStyle: (theme, hovered) => ({

      background: hovered ? theme.colors.bgCardHover : theme.colors.bgCardSolid,

      border: `3px solid ${hovered ? theme.colors.accent : theme.colors.borderPrimary}`,

      boxShadow: hovered ? `0 8px 24px rgba(0,0,0,0.4), 0 0 15px ${theme.colors.accentGlow || 'rgba(255,215,0,0.3)'}` : 'none',

      borderRadius: theme.effects?.borderRadius || '8px',

    }),

  },

  'flat': {

    id: 'flat', name: 'Flat', icon: '📄',

    getStyle: (theme, hovered) => ({

      background: hovered ? theme.colors.bgCardHover : theme.colors.bgCardSolid,

      border: hovered ? `1px solid ${theme.colors.accent}40` : 'none',

      boxShadow: hovered ? '0 4px 12px rgba(0,0,0,0.3)' : 'none',

      borderRadius: theme.effects?.borderRadiusSm || '4px',

    }),

  },

  'glass': {

    id: 'glass', name: 'Glass', icon: '✨',

    getStyle: (theme, hovered) => ({

      background: `linear-gradient(135deg, ${theme.colors.bgCardSolid}cc 0%, ${theme.colors.bgCardSolid}99 100%)`,

      backdropFilter: 'blur(16px)',

      border: `1px solid ${hovered ? theme.colors.accent : theme.colors.borderLight}`,

      boxShadow: hovered 

        ? `0 16px 48px rgba(0,0,0,0.4), 0 0 40px ${theme.colors.accentGlow || 'rgba(255,215,0,0.4)'}` 

        : `0 4px 16px rgba(0,0,0,0.2)`,

      borderRadius: theme.effects?.borderRadiusLg || '12px',

    }),

  },

};



// Apply variation to base theme

const applyVariation = (baseTheme, variationId) => {

  if (!variationId || variationId === 'default') return baseTheme;

  

  const variations = THEME_VARIATIONS[baseTheme.id] || [];

  const variation = variations.find(v => v.id === variationId);

  if (!variation || !variation.colors) return baseTheme;

  

  return {

    ...baseTheme,

    colors: { ...baseTheme.colors, ...variation.colors },

  };

};



// Theme Context

const ThemeContext = createContext(null);



const useAppTheme = () => {

  const context = useContext(ThemeContext);

  if (!context) {

    return { theme: THEMES['fantasy-classic'], themeId: 'fantasy-classic', variationId: 'default', S: null };

  }

  return context;

};



// Generate dynamic styles from theme with density and card style support

const generateStyles = (theme, densityId = 'comfortable', cardStyleId = 'elevated', enableHoverEffects = true) => {

  const c = theme.colors;

  const e = theme.effects || {

    cardShadow: '0 8px 32px rgba(0,0,0,0.6)',

    cardShadowHover: '0 12px 40px rgba(0,0,0,0.7)',

    borderRadius: '8px',

    borderRadiusLg: '12px',

    borderRadiusSm: '4px',

    cardBorder: `2px solid ${c.borderPrimary}`,

    inputBorder: `1px solid ${c.borderLight}`,

    transition: 'all 0.2s ease',

  };

  const t = theme.typography || {

    fontFamily: 'Georgia, serif',

    headingFamily: 'Georgia, serif',

  };

  

  // Get density settings

  const density = DENSITY_MODES[densityId] || DENSITY_MODES['comfortable'];

  const d = density.spacing;

  const dt = density.typography;

  const dl = density.layout;

  

  // Get card style

  const cardStyle = CARD_STYLES[cardStyleId] || CARD_STYLES['elevated'];

  const getCardStyle = (hovered = false) => {

    // If hover effects disabled, always return non-hovered style

    if (!enableHoverEffects) return cardStyle.getStyle(theme, false);

    return cardStyle.getStyle(theme, hovered);

  };

  

  return {

    container: { minHeight: '100vh', background: c.bgPrimary, fontFamily: t.fontFamily, color: c.textPrimary, padding: d.cardPadding, boxSizing: 'border-box', fontSize: dt.baseFontSize },

    card: { ...getCardStyle(false), padding: d.cardPadding, marginBottom: d.cardGap, transition: e.transition },

    cardHovered: { ...getCardStyle(true), padding: d.cardPadding, marginBottom: d.cardGap, transition: e.transition },

    title: { fontSize: '2rem', color: c.accentLight, textShadow: e.headingTextShadow || '2px 2px 4px rgba(0,0,0,0.8)', marginBottom: d.sectionGap, textAlign: 'center' },

    subtitle: { fontSize: dt.headingSize, color: c.accent, marginBottom: d.sectionGap, borderBottom: `1px solid ${c.borderLight}`, paddingBottom: d.itemGap },

    input: { width: '100%', padding: dl.inputPadding, background: c.bgInput, border: e.inputBorder, borderRadius: e.borderRadiusSm || '4px', color: c.textPrimary, fontSize: dt.baseFontSize, marginBottom: d.itemGap, boxSizing: 'border-box' },

    select: { width: '100%', padding: dl.inputPadding, background: c.bgInput, border: e.inputBorder, borderRadius: e.borderRadiusSm || '4px', color: c.textPrimary, fontSize: dt.baseFontSize, marginBottom: d.itemGap, boxSizing: 'border-box' },

    btn: { padding: dl.buttonPadding, background: `linear-gradient(180deg, ${c.accentDark} 0%, ${c.borderLight} 100%)`, border: `1px solid ${c.accent}`, borderRadius: e.borderRadiusSm || '4px', color: '#fff', fontSize: dt.baseFontSize, cursor: 'pointer', marginRight: d.itemGap, marginBottom: d.itemGap, transition: e.transition },

    btnSm: { padding: dl.buttonPadding, fontSize: dt.labelSize }, 

    btnXs: { padding: '4px 8px', fontSize: dt.badgeSize },

    btnDanger: { background: `linear-gradient(180deg, ${c.danger} 0%, #5c1f1f 100%)`, border: `1px solid ${c.danger}` },

    btnSuccess: { background: `linear-gradient(180deg, ${c.success} 0%, #1f5c2e 100%)`, border: `1px solid ${c.success}` },

    btnDisabled: { background: '#4a4a4a', cursor: 'not-allowed', opacity: 0.6 },

    btnEdit: { background: `linear-gradient(180deg, ${c.info} 0%, #2a4a6b 100%)`, border: `1px solid ${c.info}` },

    btnWarn: { background: `linear-gradient(180deg, ${c.warning}aa 0%, ${c.borderLight} 100%)`, border: `1px solid ${c.warning}` },

    btnMana: { background: `linear-gradient(180deg, ${c.mana} 0%, #0f2a5c 100%)`, border: `1px solid ${c.mana}` },

    grid: { display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${dl.gridMinWidth}, 1fr))`, gap: d.cardGap },

    flex: { display: 'flex', flexWrap: 'wrap', gap: d.itemGap, alignItems: 'center' },

    label: { display: 'block', marginBottom: '6px', color: c.accent, fontSize: dt.labelSize },

    hpBar: { height: '28px', background: c.bgInput, borderRadius: '14px', overflow: 'hidden', border: `2px solid ${c.borderLight}`, position: 'relative' },

    manaBar: { height: '20px', background: c.bgInput, borderRadius: '10px', overflow: 'hidden', border: `2px solid ${c.mana}`, position: 'relative' },

    badge: { display: 'inline-block', padding: dl.badgePadding, borderRadius: '20px', fontSize: dt.badgeSize, marginRight: '6px', marginBottom: '6px', cursor: 'default', position: 'relative' },

    badgeBuff: { background: c.buffBg, border: `1px solid ${c.buffBorder}`, color: c.buffText },

    badgeBuffPerm: { background: 'rgba(20,80,40,0.6)', border: `1px solid ${c.buffText}`, color: c.buffText, boxShadow: `0 0 6px ${c.buffBorder}50` },

    badgeDebuff: { background: c.debuffBg, border: `1px solid ${c.debuffBorder}`, color: c.debuffText },

    badgeDebuffPerm: { background: 'rgba(80,20,20,0.6)', border: `1px solid ${c.debuffText}`, color: c.debuffText, boxShadow: `0 0 6px ${c.debuffBorder}50` },

    badgeDisease: { background: 'rgba(139,31,139,0.3)', border: '1px solid #8b1f8b', color: '#d47dd4' },

    badgeSystem: { background: c.infoBg, border: `1px solid ${c.info}`, color: c.info },

    badgeNeutral: { background: c.infoBg, border: `1px solid ${c.info}`, color: c.info },

    badgeFood: { background: c.warningBg, border: `1px solid ${c.warning}`, color: c.accentLight },

    badgeAbility: { background: c.infoBg, border: `1px solid ${c.info}`, color: '#87ceeb' },

    badgeFeat: { background: c.warningBg, border: `1px solid ${c.warning}`, color: c.accentLight },

    badgeUnknown: { background: 'rgba(100,100,100,0.3)', border: '1px solid #666', color: '#aaa' },

    badgeMystery: { background: 'rgba(138,43,226,0.25)', border: '1px solid #9932cc', color: '#da70d6', boxShadow: '0 0 8px rgba(138,43,226,0.3)' },

    badgeMysteryBuff: { background: 'rgba(75,0,130,0.3)', border: '1px solid #9932cc', color: '#98fb98', boxShadow: '0 0 8px rgba(138,43,226,0.3)' },

    badgeMysteryDebuff: { background: 'rgba(138,43,226,0.25)', border: '1px solid #9932cc', color: '#ff6b6b', boxShadow: '0 0 8px rgba(138,43,226,0.3)' },

    portrait: { width: '120px', height: '120px', borderRadius: '50%', border: `3px solid ${c.accent}`, background: c.bgInput, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem', margin: '0 auto 16px', overflow: 'hidden', boxShadow: `0 0 15px ${c.accentGlow}` },

    dayNum: { fontSize: '3rem', color: c.accentLight, textAlign: 'center', marginBottom: d.itemGap },

    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: c.bgModal, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: d.cardPadding },

    modalContent: { ...getCardStyle(false), padding: d.cardPadding, maxWidth: '700px', width: '100%', maxHeight: '90vh', overflowY: 'auto' },

    tabs: { display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: d.sectionGap, borderBottom: `2px solid ${c.borderLight}`, paddingBottom: d.itemGap },

    tab: { padding: dl.buttonPadding, background: 'transparent', border: 'none', color: c.textMuted, cursor: 'pointer', fontSize: dt.labelSize, transition: e.transition },

    tabActive: { background: c.bgHover, color: c.accentLight },

    statsPanel: { position: 'fixed', bottom: '20px', right: '20px', zIndex: 1000 },

    statsPopup: { ...getCardStyle(false), padding: d.cardPadding, minWidth: '320px', overflow: 'visible' },

    tooltip: { position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', background: 'rgba(20,15,10,0.98)', border: `1px solid ${c.accent}`, borderRadius: '6px', padding: '8px 12px', fontSize: dt.badgeSize, whiteSpace: 'pre-wrap', zIndex: 10000, marginBottom: '4px', maxWidth: '300px', textAlign: 'left', boxShadow: '0 4px 12px rgba(0,0,0,0.4)' },

    subCard: { background: 'rgba(0,0,0,0.2)', borderRadius: e.borderRadiusSm || '4px', padding: d.cardPadding, marginTop: d.itemGap, border: `1px solid ${c.borderPrimary}30` },

    statBox: { background: `${c.accentDark}30`, border: `2px solid ${c.accentDark}`, borderRadius: e.borderRadius || '8px', padding: d.itemGap, textAlign: 'center', minWidth: '70px' },

    skillRow: { display: 'flex', alignItems: 'center', padding: `${d.itemGap} ${dl.inputPadding}`, borderRadius: e.borderRadiusSm || '4px', marginBottom: '2px', background: 'rgba(0,0,0,0.2)' },

    statusWindow: { background: 'rgba(0,0,0,0.3)', border: `1px solid ${c.borderLight}`, borderRadius: e.borderRadius || '8px', padding: d.cardPadding, marginBottom: d.sectionGap },

    // Hover effect helper styles

    hoverGlow: { boxShadow: e.glowAccent || `0 0 20px ${c.accentGlow}` },

    hoverLift: { transform: 'translateY(-2px)' },

    transition: { transition: e.transition },

    _colors: c,

    _effects: e,

    _density: density,

    _cardStyle: cardStyleId,

    _getCardStyle: getCardStyle,

    _enableHoverEffects: enableHoverEffects,

    // 2026-04-29: motion primitives for the mobile redesign. Theme-independent

    // because spring physics is about *feel*, not visual theme — using these

    // gives mobile-chrome elements the same human cadence regardless of dark/

    // light/fantasy/etc. Callers should still respect prefers-reduced-motion

    // (see useReducedMotion); these are the timing curves to use when motion

    // is allowed. cubic-bezier values are tuned to feel like a soft spring

    // overshoot — the difference between "ease-out (linear deceleration)"

    // and these is the difference between "computer" and "human."

    _motion: {

      // Default spring for tab indicator slides, FAB morphs, sheet slides.

      // 0.34, 1.56, 0.64, 1 = subtle overshoot, lands at rest cleanly.

      spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',

      // Snappier spring for press feedback / tap acks. Less overshoot.

      springSnap: 'cubic-bezier(0.34, 1.20, 0.64, 1)',

      // Standard ease for non-feel transitions (theme fades, cross-fades).

      ease: 'cubic-bezier(0.4, 0, 0.2, 1)',

      // Durations.

      durFast: '120ms',

      durBase: '200ms',

      durSlow: '320ms',

      // Press-state primitives — apply on :active and on touchstart.

      pressActive: { transform: 'scale(0.97)', filter: 'brightness(1.1)' },

    },

  };

};



// Default styles (classic fantasy theme with comfortable density and elevated cards)

// 2026-04-27: default theme flipped to Dark Arcana (fantasy-dark). The

// runtime theme system replaces these styles per the user's localStorage

// preference; this baseline only matters for the brief first paint and

// for any module that imports S before the runtime theme has kicked in.

const S = generateStyles(THEMES['fantasy-dark'], 'comfortable', 'elevated');



// Helper to update S in place (preserves object reference)

const updateStylesInPlace = (newStyles) => {

  Object.keys(S).forEach(key => delete S[key]);

  Object.assign(S, newStyles);

};



// ============================================================================

// HOVER-AWARE COMPONENTS - Use these for hover/glow effects

// ============================================================================



// Card component with hover effects based on current card style

const HoverCard = ({ children, style = {}, className = '' }) => {

  const [hovered, setHovered] = useState(false);

  

  // Check if hover effects are enabled (default to true if not set)

  const hoverEnabled = S._enableHoverEffects !== false;

  

  // Determine if we should show hover state

  const showHoverState = hovered && hoverEnabled;

  

  // Get the appropriate card style

  let cardStyle;

  if (typeof S._getCardStyle === 'function') {

    cardStyle = S._getCardStyle(showHoverState);

  } else if (showHoverState && S.cardHovered) {

    cardStyle = S.cardHovered;

  } else {

    cardStyle = S.card;

  }

  

  return (

    <div 

      className={className}

      onMouseEnter={() => setHovered(true)}

      onMouseLeave={() => setHovered(false)}

      style={{

        ...cardStyle,

        padding: S._density?.spacing?.cardPadding || '20px',

        marginBottom: S._density?.spacing?.cardGap || '16px',

        transition: 'background 0.25s ease, border 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease',

        position: 'relative',

        zIndex: hovered ? 10 : 'auto',

        ...style

      }}

    >

      {children}

    </div>

  );

};



// Debounced text input / textarea. Buffers local keystrokes and only commits upstream

// (Firebase save, audit-event write) after the user pauses typing. Without this, every

// keystroke in a notes textarea fires a full-collection write and subscription fan-out.

// Usage: <DebouncedInput as="textarea" value={s.dmNotes} onCommit={v => save(v)} delay={500} />

//

// `value` is the canonical upstream value. Local keystrokes update internal state only.

// onCommit fires after `delay` ms of no typing, or on blur. If the upstream value changes

// while we're not mid-edit (timerRef is null), we sync local state to it so external updates

// (e.g. another DM writing at the same time) aren't lost.

const DebouncedInput = DebouncedTextField;



// Dashboard Section component with reorder controls

const DashboardSection = ({ id, title, icon, children, collapsed, onToggleCollapse, onMoveUp, onMoveDown, canMoveUp, canMoveDown, showControls = true, asCard = false }) => {

  const [showReorder, setShowReorder] = useState(false);

  

  const cardStyle = asCard ? {

    ...S.card,

    padding: '12px',

    marginBottom: '16px',

  } : {

    marginBottom: '16px',

  };

  

  return (

    <div style={cardStyle}>

      <div 

        style={{ 

          display: 'flex', 

          alignItems: 'center', 

          justifyContent: 'space-between',

          marginBottom: collapsed ? '0' : '12px',

          padding: asCard ? '8px' : '8px 12px',

          background: asCard ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.2)',

          borderRadius: '8px',

          cursor: 'pointer',

        }}

        onClick={() => onToggleCollapse && onToggleCollapse(id)}

        onMouseEnter={() => setShowReorder(true)}

        onMouseLeave={() => setShowReorder(false)}

      >

        <h2 style={{ ...S.subtitle, margin: 0, display: 'flex', alignItems: 'center', gap: '8px', fontSize: asCard ? '0.95rem' : '1rem' }}>

          {icon} {title}

          <span style={{ fontSize: '0.7rem', opacity: 0.5 }}>{collapsed ? '▼' : '▲'}</span>

        </h2>

        {showControls && showReorder && (

          <div style={{ display: 'flex', gap: '4px' }} onClick={e => e.stopPropagation()}>

            <button 

              style={{ ...S.btn, ...S.btnXs, opacity: canMoveUp ? 1 : 0.3, marginBottom: 0, padding: '2px 6px' }}

              disabled={!canMoveUp}

              onClick={() => onMoveUp && onMoveUp(id)}

              title="Move section up"

            >↑</button>

            <button 

              style={{ ...S.btn, ...S.btnXs, opacity: canMoveDown ? 1 : 0.3, marginBottom: 0, padding: '2px 6px' }}

              disabled={!canMoveDown}

              onClick={() => onMoveDown && onMoveDown(id)}

              title="Move section down"

            >↓</button>

          </div>

        )}

      </div>

      {!collapsed && children}

    </div>

  );

};



// Player Preview component for system editing

const PlayerPreviewBadge = ({ systemData, system, unknownText = UNKNOWN_PLACEHOLDER }) => {

  if (!system) return null;

  

  // Build what the player would see

  const showName = systemData?.showName;

  const showDescription = systemData?.showDescription;

  const placeholderName = systemData?.placeholderName || '';

  const placeholderDescription = systemData?.placeholderDescription || '';

  const visibleClues = systemData?.visibleClues || [];

  const showEffects = systemData?.showEffects;

  const showCure = systemData?.showCure;

  

  // Custom badge color. When `useGlowColorForBadge` is set on the instance, the badge

  // color is derived from the glow color instead of badgeColor — lets the DM theme the

  // pill to match the system's notification glow effect (e.g., blood magic pill in crimson).

  const effectiveBadgeColorId = systemData?.useGlowColorForBadge

    ? (systemData?.glowColorId || null)

    : (systemData?.badgeColor || null);

  const customBadgeColor = effectiveBadgeColorId;

  const colorOpt = customBadgeColor ? BUFF_COLOR_OPTIONS.find(c => c.id === customBadgeColor) : null;



  const displayName = showName ? (system.publicName || system.name) : (placeholderName || unknownText);

  const typeMeta = SYSTEM_TYPE_OPTIONS.find(t => t.value === system.type);
  const playerSafeDescription = getPlayerSafeSystemPublicDescription(system);

  const icon = showName ? (typeMeta?.icon || '🔮') : '';

  

  const hasAnyVisible = showName || showDescription || (visibleClues.length > 0) || showEffects || showCure;

  

  // Badge style with custom color

  const baseBadgeStyle = hasAnyVisible ? S.badgeSystem : S.badgeUnknown;

  const badgeStyle = colorOpt ? {

    ...baseBadgeStyle,

    background: colorOpt.bgColor,

    borderColor: colorOpt.color,

    color: colorOpt.color,

  } : baseBadgeStyle;

  

  return (

    <div style={{ 

      padding: '12px', 

      background: 'rgba(0,0,0,0.3)', 

      borderRadius: '8px', 

      border: '1px dashed rgba(201,162,39,0.4)',

      marginTop: '12px',

    }}>

      <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '8px' }}>👁 Player Preview:</div>

      

      {/* Badge preview */}

      <div style={{ marginBottom: '8px' }}>

        <span style={{ 

          ...S.badge, 

          ...badgeStyle,

          padding: '4px 10px',

        }}>

          {icon} {displayName}

        </span>

      </div>

      

      {/* Tooltip preview */}

      <div style={{ 

        padding: '10px', 

        background: 'rgba(0,0,0,0.5)', 

        borderRadius: '6px',

        fontSize: '0.85rem',

        maxWidth: '300px',

      }}>

        {!hasAnyVisible && !placeholderDescription ? (

          <div style={{ color: '#888', fontStyle: 'italic' }}>Something is affecting you...</div>

        ) : (

          <>

            {(showName || placeholderName) && (

              <div style={{ fontWeight: 'bold', marginBottom: '4px', color: showName ? '#fff' : '#c9a227' }}>

                {icon} {displayName}

              </div>

            )}

            

            {!showDescription && placeholderDescription && (

              <div style={{ fontStyle: 'italic', color: '#c9a227', marginBottom: '4px' }}>{placeholderDescription}</div>

            )}

            

            {showDescription && playerSafeDescription && (

              <div style={{ marginBottom: '4px' }}>{playerSafeDescription}</div>

            )}

            

            {visibleClues.length > 0 && system.clues?.length > 0 && (

              <div style={{ marginTop: '6px' }}>

                <div style={{ color: '#888', fontSize: '0.8rem' }}>{systemData?.cluesLabel || 'Clues'}:</div>

                <ul style={{ margin: '2px 0 0 16px', padding: 0, fontSize: '0.8rem' }}>

                  {visibleClues.map(i => (

                    <li key={i}>{system.clues[i]}</li>

                  ))}

                </ul>

              </div>

            )}

            

            {showEffects && system.effects?.length > 0 && (

              <div style={{ marginTop: '6px' }}>

                <div style={{ color: '#888', fontSize: '0.8rem' }}>Effects:</div>

                <div style={{ fontSize: '0.8rem' }}>{system.effects.map(e => typeof e === 'string' ? e : e.displayText).join(', ')}</div>

              </div>

            )}

            

            {showCure && system.cures?.length > 0 && (

              <div style={{ marginTop: '6px' }}>

                <div style={{ color: '#888', fontSize: '0.8rem' }}>Cure:</div>

                <div style={{ fontSize: '0.8rem' }}>{system.cures.map(c => `${c.quantity}x ${c.item}`).join(', ')}</div>

              </div>

            )}

          </>

        )}

      </div>

      

      {/* Glow preview if enabled */}

      {systemData?.notifyGlow && (

        <div style={{ marginTop: '8px', fontSize: '0.75rem', color: '#888' }}>

          ✨ Glow effect enabled ({BUFF_COLOR_OPTIONS.find(c => c.id === (systemData?.glowColorId || 'gold'))?.name || 'Gold'})

        </div>

      )}

    </div>

  );

};



// Button component with hover effects

const HoverButton = ({ children, variant = 'default', style = {}, ...props }) => {

  const [hovered, setHovered] = useState(false);

  const c = S._colors || {};

  

  const baseStyle = variant === 'danger' ? S.btnDanger : 

                    variant === 'success' ? S.btnSuccess :

                    variant === 'edit' ? S.btnEdit :

                    variant === 'warn' ? S.btnWarn :

                    variant === 'mana' ? S.btnMana : {};

  

  return (

    <button

      onMouseEnter={() => setHovered(true)}

      onMouseLeave={() => setHovered(false)}

      style={{

        ...S.btn,

        ...baseStyle,

        opacity: hovered ? 0.85 : 1,

        transform: hovered ? 'translateY(-1px)' : 'none',

        boxShadow: hovered ? (S._effects?.glowAccent || `0 0 15px ${c.accentGlow}`) : undefined,

        ...style

      }}

      {...props}

    >

      {children}

    </button>

  );

};



const DM_PASSWORD = 'dmdemo';

const generateId = () => Math.random().toString(36).substr(2, 9);



const DEFAULT_FOOD_TYPES = ['Meat', 'Carbs', 'Plants'];



// Canonical placeholder string for unrevealed / unknown labels across the privacy model.

// Previously this was a free-floating literal ('??') duplicated across ~12 sites.

// User-configurable via SystemInstance.unknownText (which may be '??', 'Unknown',

// '::::::::::', etc.), but this constant is the default fallback wherever that field is unset.

const UNKNOWN_PLACEHOLDER = '??';
const HIDDEN_LABEL_PLACEHOLDER = '???';

export const CRIMSON_ARTS_PUBLIC_DESCRIPTION = 'You have learned a disciplined art for focusing inner energy into mystical techniques.';

export const isCrimsonArtsSystem = (system) => {
  if (!system) return false;
  const id = String(system.id || system.systemId || '').toLowerCase();
  const name = String(system.name || '').toLowerCase();
  const publicName = String(system.publicName || '').toLowerCase();
  return id === 'blood-magic' || name === 'blood magic' || publicName === 'crimson arts';
};

export const getPlayerSafeSystemPublicDescription = (system) => {
  if (!system) return '';
  if (isCrimsonArtsSystem(system)) return CRIMSON_ARTS_PUBLIC_DESCRIPTION;
  return system.publicDescription || system.description || '';
};

export const shouldHideTechniqueRequirementReason = (technique, system) => {
  if (technique?.hidden?.requirements !== false) return true;
  if (!isCrimsonArtsSystem(system)) return false;
  return (technique?.requirements || []).some(req => req?.trackerId === 'addiction-level');
};

export const getTechniqueDisplayIcon = (technique, system, fallback = '') => {
  if (isCrimsonArtsSystem(system)) return '';
  return technique?.icon || fallback;
};

export const cloneCharForSystemEdit = (char = {}) => ({
  ...char,
  systemList: Array.isArray(char.systemList) ? [...char.systemList] : [],
  removedSystems: Array.isArray(char.removedSystems) ? [...char.removedSystems] : char.removedSystems,
});

const makeSystemEditableChars = (chars = {}, charId, fallbackChar = {}) => ({
  ...chars,
  [charId]: cloneCharForSystemEdit(chars?.[charId] || fallbackChar || { id: charId }),
});

const cloneCharForSystemAuthority = (char = {}) => ({
  ...cloneCharForSystemEdit(char),
  glowingSystems: char?.glowingSystems && typeof char.glowingSystems === 'object' && !Array.isArray(char.glowingSystems)
    ? { ...char.glowingSystems }
    : char?.glowingSystems,
});



// ============== SPELL & MANA SYSTEM ==============



// Spell schools — now carry icons to match the shape of DAMAGE_TYPES / SYSTEM_TYPE_OPTIONS /

// EFFECT_CATEGORIES. Stored character spell data references these by name (spell.school === 'Evocation'),

// so id === name preserves compatibility with existing records.

const SPELL_SCHOOLS = [

  { id: 'Abjuration',    name: 'Abjuration',    icon: '🛡️' },

  { id: 'Conjuration',   name: 'Conjuration',   icon: '✨' },

  { id: 'Divination',    name: 'Divination',    icon: '🔮' },

  { id: 'Enchantment',   name: 'Enchantment',   icon: '💫' },

  { id: 'Evocation',     name: 'Evocation',     icon: '🔥' },

  { id: 'Illusion',      name: 'Illusion',      icon: '🎭' },

  { id: 'Necromancy',    name: 'Necromancy',    icon: '💀' },

  { id: 'Transmutation', name: 'Transmutation', icon: '🔄' },

];



// Dice rolling utility

const rollDice = (notation) => {

  // Parse dice notation like "2d8+4", "1d20", "4d6", "1d8+mod"

  if (!notation) return { total: 0, rolls: [], modifier: 0, formula: notation };

  

  const cleanNotation = notation.toLowerCase().replace(/\s/g, '');

  const parts = cleanNotation.split(/([+-])/);

  let total = 0;

  let allRolls = [];

  let modifier = 0;

  let currentSign = 1;

  

  parts.forEach(part => {

    if (part === '+') { currentSign = 1; return; }

    if (part === '-') { currentSign = -1; return; }

    if (!part) return;

    

    // Check if it's a dice roll (e.g., "2d8")

    const diceMatch = part.match(/^(\d+)?d(\d+)$/);

    if (diceMatch) {

      const numDice = parseInt(diceMatch[1]) || 1;

      const dieSize = parseInt(diceMatch[2]);

      const rolls = [];

      for (let i = 0; i < numDice; i++) {

        const roll = Math.floor(Math.random() * dieSize) + 1;

        rolls.push(roll);

        total += roll * currentSign;

      }

      allRolls.push({ dice: `${numDice}d${dieSize}`, rolls, sign: currentSign });

    } else if (part === 'mod') {

      // Placeholder for modifier - will be added by caller

      modifier = currentSign;

    } else {

      // Static number

      const num = parseInt(part);

      if (!isNaN(num)) {

        total += num * currentSign;

        modifier = num * currentSign;

      }

    }

  });

  

  return { total, rolls: allRolls, modifier, formula: notation };

};



// 2026-04-27: parse a consumable onUse effect like "+10 HP", "+2d4+2 HP",

// "-5 Mana", "+1d6 Dura". Rolls dice if present, returns null if the

// effect doesn't address a known resource. The breakdown string is built

// in the same grammar used by other receipts (P5/P7) so the UI can

// surface it next to the resulting HP/Mana/Dura change.

//   raw: original effect text

//   resource: 'hp' | 'mana' | 'dura'

//   amount: signed integer applied to the resource (post-roll)

//   hadDice: whether dice were involved (pure flat numbers don't need a roll receipt)

//   breakdown: human-readable receipt e.g. "2d4+2: [3, 1] + 2 = 6"

const parseHealEffect = (effect) => {
  return parseLegacyConsumableResourceEffect(effect);
};



// Format roll result for display

const formatRollResult = (result, spellMod = 0) => {

  if (!result || result.rolls.length === 0) return result.total.toString();

  

  const parts = result.rolls.map(r => {

    const sign = r.sign > 0 ? '' : '-';

    return `${sign}(${r.rolls.join(' + ')})`;

  });

  

  let formula = parts.join(' ');

  let finalTotal = result.total;

  

  if (result.modifier !== 0 && result.formula.includes('mod')) {

    formula += ` + ${spellMod}`;

    finalTotal += spellMod;

  }

  

  return `${formula} = ${finalTotal}`;

};



// Casting time abbreviations

const CASTING_TIMES = {

  '1A': '1 action',

  '1BA': '1 bonus action',

  '1R': '1 reaction',

  '1m': '1 minute',

  '10m': '10 minutes',

  '1h': '1 hour',

  '8h': '8 hours',

  '24h': '24 hours',

};



// Default spell structure

const createSpell = (overrides = {}) => ({

  id: generateId(),

  name: 'New Spell',

  level: 0, // 0 = cantrip, 1-9 = spell levels

  school: 'Evocation',

  ritual: false,

  castingTime: '1A', // 1A, 1BA, 1R, 1m, 10m, 1h, 8h, 24h

  reactionTrigger: '', // text for reaction spells

  range: '30 ft',

  // Components: supports both legacy string 'V, S, M (item)' and structured {v,s,m}

  components: 'V, S',

  materialConsumed: false,

  materialGoldCost: 0,

  duration: 'Instantaneous',

  concentration: false,

  description: '',

  // Targeting

  targetType: 'single', // 'self','touch','single','multi','aoe','special'

  targetFilter: 'any', // 'any','willing','hostile','creature','object'

  maxTargets: 1, // 0 = unlimited/AoE

  // Effect & Automation

  effectType: '', // 'damage','healing','buff','debuff','temp_hp','utility','control','summon'

  dice: '', // e.g., '1d8', '4d6'

  damageType: '', // 'fire','cold','radiant', etc.

  addMod: false, // add spellcasting modifier

  // Attack / Initial Save

  attackType: 'none', // 'none','melee','ranged','save'

  saveAbility: '', // 'str','dex','con','int','wis','cha' (for initial save)

  saveEffect: 'negates', // 'half','negates','special','none'

  // Conditions

  conditionsApplied: [], // ['Paralyzed','Blinded','Charmed', etc.]

  conditionSaveEnds: false, // target can save each turn to end

  // Buff/Debuff text (for pills/labels)

  buffText: '', // 'Blessed (+1d4 attacks/saves)'

  buffAppliesTo: [], // array: ['attack_rolls','saving_throws','damage','healing_done','healing_received','ac','ability_checks','speed','hp_max','custom']

  // Scaling

  upcastPattern: 'none', // 'none','dice_per_level','targets_per_level','flat_per_level','cantrip','custom'

  upcastDice: '', // e.g., '1d8' per level above base

  upcastTargets: 0, // +N targets per level above base

  upcastFlat: 0, // +N flat bonus per level

  upcastFlatField: '', // which field the flat applies to (e.g., 'bonusHp')

  // Per-level overrides (for cantrip scaling or complex/custom)

  levelData: {},

  // Legacy compat

  effect: '', // display string e.g., '1d8+mod HP'

  hitDC: '', // legacy: 'attack', 'save:DEX', '+6', 'WIS 14'

  higherLevels: '', // text: 'At Higher Levels...'

  tags: [],

  source: '',

  prepared: true,

  // Class association (legacy, prefer class definitions)

  spellClass: '',

  learnAtLevel: 0,

  // Buff/Mark/Debuff legacy

  spellType: '', // 'damage','healing','buff','mark','debuff' (legacy, maps to effectType)

  applyBuff: '',

  bonusDice: '',

  percentIncrease: 0,

  casterOnly: true,

  debuffDuration: 0,

  // Over-time effects

  dotDice: '',

  dotDamageType: 'fire',

  hotDice: '',

  hotTempHpAmount: 0,

  effectRounds: 0,

  tickTiming: 'start_of_target_turn',

  // Recurring saves (save each turn to end effect)

  saveType: '', // recurring save ability

  saveDC: 0,

  saveTiming: 'end_of_target_turn',

  saveRemovesEffect: true,

  ...overrides

});

const formatZeroAsBlank = (value) => (value ? String(value) : '');



// Migrate string components to structured format

const migrateComponents = (comp) => {

  if (!comp) return { v: false, s: false, m: '' };

  if (typeof comp === 'object' && comp !== null) return comp;

  const str = String(comp);

  const v = /\bV\b/i.test(str);

  const s = /\bS\b/i.test(str);

  // 2026-04-27: Don't trim() the captured material text — trimming strips trailing

  // spaces during typing so "abc " round-trips to "abc", making it impossible to

  // type a space at the end of the field and then continue typing.

  const mMatch = str.match(/M\s*\(([^)]+)\)/i);

  return { v, s, m: mMatch ? mMatch[1] : (/\bM\b/.test(str) ? 'material' : '') };

};



// Format structured components back to display string

const formatComponents = (comp) => {

  if (typeof comp === 'string') return comp;

  const parts = [];

  if (comp.v) parts.push('V');

  if (comp.s) parts.push('S');

  if (comp.m) parts.push(`M (${comp.m})`);

  else if (!comp.v && !comp.s) return 'None';

  return parts.join(', ') || 'None';

};



// Sample spells based on the uploaded images

const SAMPLE_SPELLS = [

  createSpell({

    name: 'Fire Bolt',

    level: 0,

    school: 'Evocation',

    castingTime: '1A',

    range: '120 ft',

    components: 'V, S',

    duration: 'Instantaneous',

    description: 'You hurl a mote of fire at a creature or object. Make a ranged spell attack. On hit, deal fire damage.',

    effect: '1d10 fire',

    dice: '1d10',

    damageType: 'fire',

    effectType: 'damage',

    spellType: 'damage',

    targetType: 'single',

    attackType: 'ranged',

    hitDC: 'attack',

    upcastPattern: 'cantrip',

    levelData: { 5: { dice: '2d10', effect: '2d10 fire' }, 11: { dice: '3d10', effect: '3d10 fire' }, 17: { dice: '4d10', effect: '4d10 fire' } },

    tags: ['Damage', 'Fire', 'Cantrip'],

    source: 'PHB p.242'

  }),

  createSpell({

    name: 'Sacred Flame',

    level: 0,

    school: 'Evocation',

    castingTime: '1A',

    range: '60 ft',

    components: 'V, S',

    duration: 'Instantaneous',

    description: 'Flame-like radiance descends on a creature. Target must make a DEX save or take radiant damage.',

    effect: '1d8 radiant',

    dice: '1d8',

    damageType: 'radiant',

    effectType: 'damage',

    spellType: 'damage',

    targetType: 'single',

    attackType: 'save',

    saveAbility: 'dex',

    saveEffect: 'negates',

    hitDC: 'save:DEX',

    upcastPattern: 'cantrip',

    levelData: { 5: { dice: '2d8', effect: '2d8 radiant' }, 11: { dice: '3d8', effect: '3d8 radiant' }, 17: { dice: '4d8', effect: '4d8 radiant' } },

    tags: ['Damage', 'Radiant', 'Cantrip'],

    source: 'PHB p.272'

  }),

  createSpell({

    name: 'Cure Wounds',

    level: 1,

    school: 'Evocation',

    castingTime: '1A',

    range: 'Touch',

    components: 'V, S',

    duration: 'Instantaneous',

    description: 'A creature you touch regains hit points equal to 1d8 + your spellcasting modifier.',

    effect: '1d8+mod HP',

    dice: '1d8',

    effectType: 'healing',

    spellType: 'healing',

    targetType: 'touch',

    targetFilter: 'willing',

    addMod: true,

    upcastPattern: 'dice_per_level',

    upcastDice: '1d8',

    higherLevels: '+1d8 per level above 1st',

    tags: ['Healing'],

    source: 'PHB p.230'

  }),

  createSpell({

    name: 'Healing Word',

    level: 1,

    school: 'Evocation',

    castingTime: '1BA',

    range: '60 ft',

    components: 'V',

    duration: 'Instantaneous',

    description: 'A creature you can see within range regains hit points equal to 1d4 + your spellcasting modifier.',

    effect: '1d4+mod HP',

    dice: '1d4',

    spellType: 'healing',

    targetType: 'single',

    addMod: true,

    higherLevels: '+1d4 per level above 1st',

    upcastDice: '1d4',

    tags: ['Healing'],

    source: 'PHB p.250'

  }),

  createSpell({

    name: 'Guiding Bolt',

    level: 1,

    school: 'Evocation',

    castingTime: '1A',

    range: '120 ft',

    components: 'V, S',

    duration: 'Instantaneous',

    description: 'A flash of light streaks toward a creature. Make a ranged spell attack. On hit, 4d6 radiant damage and next attack against target has advantage.',

    effect: '4d6 radiant',

    dice: '4d6',

    damageType: 'radiant',

    spellType: 'damage',

    targetType: 'single',

    hitDC: 'attack',

    applyBuff: 'Guiding Bolt (next attack has advantage)',

    buffTarget: 'enemy', // Apply debuff to enemy

    higherLevels: '+1d6 per level above 1st',

    upcastDice: '1d6',

    tags: ['Damage', 'Radiant'],

    source: 'PHB p.248'

  }),

  createSpell({

    name: 'Bless',

    level: 1,

    school: 'Enchantment',

    castingTime: '1A',

    range: '30 ft',

    components: 'V, S, M (holy water)',

    duration: 'Concentration, 1 minute',

    concentration: true,

    description: 'You bless up to three creatures. Whenever a target makes an attack roll or saving throw, they can add 1d4 to the roll.',

    effect: 'Buff (+1d4)',

    dice: '1d4',

    effectType: 'buff',

    spellType: 'buff',

    targetType: 'multi',

    targetFilter: 'willing',

    maxTargets: 3,

    spellClass: 'Cleric',

    learnAtLevel: 1,

    buffText: 'Bless (+1d4 attack rolls, saving throws)',

    applyBuff: 'Bless (+1d4 attack rolls, saving throws)',

    buffAppliesTo: ['attack_rolls', 'saving_throws'],

    upcastPattern: 'targets_per_level',

    upcastTargets: 1,

    higherLevels: '+1 target per level above 1st',

    tags: ['Buff'],

    source: 'PHB p.219'

  }),

  createSpell({

    name: 'Shield of Faith',

    level: 1,

    school: 'Abjuration',

    castingTime: '1BA',

    range: '60 ft',

    components: 'V, S, M (parchment with holy text)',

    duration: 'Concentration, 10 minutes',

    concentration: true,

    description: 'A shimmering field appears around a creature, granting +2 bonus to AC.',

    effect: '+2 AC',

    effectType: 'buff',

    spellType: 'buff',

    targetType: 'single',

    targetFilter: 'willing',

    buffText: 'Shield of Faith (+2 AC)',

    applyBuff: 'Shield of Faith (+2 AC)',

    buffAppliesTo: 'ac',

    tags: ['Buff', 'Defense'],

    source: 'PHB p.275'

  }),

  createSpell({

    name: 'Burning Hands',

    level: 1,

    school: 'Evocation',

    castingTime: '1A',

    range: 'Self (15-foot cone)',

    components: 'V, S',

    duration: 'Instantaneous',

    description: 'A thin sheet of flames shoots forth from your fingertips. Each creature in a 15-foot cone must make a DEX save. On failure, takes 3d6 fire damage, half on success.',

    effect: '3d6 fire',

    dice: '3d6',

    damageType: 'fire',

    effectType: 'damage',

    spellType: 'damage',

    targetType: 'aoe',

    attackType: 'save',

    saveAbility: 'dex',

    saveEffect: 'half',

    hitDC: 'save:DEX',

    halfOnSave: true,

    upcastPattern: 'dice_per_level',

    upcastDice: '1d6',

    higherLevels: '+1d6 per level above 1st',

    tags: ['Damage', 'Fire', 'AoE'],

    source: 'PHB p.220'

  }),

  createSpell({

    name: 'Magic Missile',

    level: 1,

    school: 'Evocation',

    castingTime: '1A',

    range: '120 ft',

    components: 'V, S',

    duration: 'Instantaneous',

    description: 'You create three glowing darts of magical force. Each dart hits a creature dealing 1d4+1 force damage. Darts can target same or different creatures.',

    effect: '3x (1d4+1) force',

    dice: '1d4+1',

    damageType: 'force',

    spellType: 'damage',

    targetType: 'multi',

    maxTargets: 3,

    autoHit: true, // No attack roll needed

    higherLevels: '+1 dart per level above 1st',

    tags: ['Damage', 'Force'],

    source: 'PHB p.257'

  }),

  createSpell({

    name: 'Fireball',

    level: 3,

    school: 'Evocation',

    castingTime: '1A',

    range: '150 ft',

    components: 'V, S, M (bat guano and sulfur)',

    duration: 'Instantaneous',

    description: 'A bright streak flashes and explodes into a 20-foot radius sphere. Each creature must make a DEX save or take 8d6 fire damage, half on success.',

    effect: '8d6 fire',

    dice: '8d6',

    damageType: 'fire',

    effectType: 'damage',

    spellType: 'damage',

    targetType: 'aoe',

    attackType: 'save',

    saveAbility: 'dex',

    saveEffect: 'half',

    hitDC: 'save:DEX',

    halfOnSave: true,

    upcastPattern: 'dice_per_level',

    upcastDice: '1d6',

    higherLevels: '+1d6 per level above 3rd',

    tags: ['Damage', 'Fire', 'AoE'],

    source: 'PHB p.241'

  }),

  createSpell({

    name: 'Mass Healing Word',

    level: 3,

    school: 'Evocation',

    castingTime: '1BA',

    range: '60 ft',

    components: 'V',

    duration: 'Instantaneous',

    description: 'Up to six creatures of your choice regain hit points equal to 1d4 + your spellcasting modifier.',

    effect: '1d4+mod HP (6 targets)',

    dice: '1d4',

    spellType: 'healing',

    targetType: 'multi',

    maxTargets: 6,

    addMod: true,

    higherLevels: '+1d4 per level above 3rd',

    upcastDice: '1d4',

    tags: ['Healing', 'AoE'],

    source: 'PHB p.258'

  }),

];



// Standard 5e spell slots by level (for reference/traditional mode)

const SPELL_SLOTS_BY_LEVEL = {

  1: { 1: 2 },

  2: { 1: 3 },

  3: { 1: 4, 2: 2 },

  4: { 1: 4, 2: 3 },

  5: { 1: 4, 2: 3, 3: 2 },

  6: { 1: 4, 2: 3, 3: 3 },

  7: { 1: 4, 2: 3, 3: 3, 4: 1 },

  8: { 1: 4, 2: 3, 3: 3, 4: 2 },

  9: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 1 },

  10: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2 },

  11: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2, 6: 1 },

  12: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2, 6: 1 },

  13: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2, 6: 1, 7: 1 },

  14: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2, 6: 1, 7: 1 },

  15: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2, 6: 1, 7: 1, 8: 1 },

  16: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2, 6: 1, 7: 1, 8: 1 },

  17: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2, 6: 1, 7: 1, 8: 1, 9: 1 },

  18: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 3, 6: 1, 7: 1, 8: 1, 9: 1 },

  19: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 3, 6: 2, 7: 1, 8: 1, 9: 1 },

  20: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 3, 6: 2, 7: 2, 8: 1, 9: 1 },

};



// Parse spell import text (supports multiple formats)

const parseSpellImport = (text) => {

  const spells = [];

  const lines = text.trim().split('\n');

  

  // Check if it's a simple CSV/TSV format (first line has headers)

  const firstLine = lines[0].toLowerCase();

  if (firstLine.includes('name') && (firstLine.includes('|') || firstLine.includes('\t') || firstLine.includes(','))) {

    // Determine delimiter

    const delimiter = firstLine.includes('|') ? '|' : firstLine.includes('\t') ? '\t' : ',';

    const headers = lines[0].split(delimiter).map(h => h.trim().toLowerCase());

    

    for (let i = 1; i < lines.length; i++) {

      if (!lines[i].trim()) continue;

      const values = lines[i].split(delimiter).map(v => v.trim());

      const spell = createSpell();

      

      headers.forEach((header, idx) => {

        const value = values[idx] || '';

        switch (header) {

          case 'name': spell.name = value; break;

          case 'level': spell.level = parseInt(value) || 0; break;

          case 'school': spell.school = value || 'Evocation'; break;

          case 'time': case 'castingtime': case 'casting time': spell.castingTime = value || '1A'; break;

          case 'range': spell.range = value || 'Self'; break;

          case 'components': case 'comp': spell.components = value || 'V, S'; break;

          case 'duration': case 'dur': spell.duration = value || 'Instantaneous'; break;

          case 'effect': spell.effect = value; break;

          case 'dice': spell.dice = value; break;

          case 'hit': case 'dc': case 'hit/dc': case 'hitdc': spell.hitDC = value; break;

          case 'description': case 'desc': spell.description = value; break;

          case 'higher': case 'higherlevels': case 'higher levels': spell.higherLevels = value; break;

          case 'tags': spell.tags = value.split(',').map(t => t.trim()).filter(Boolean); break;

          case 'source': spell.source = value; break;

          case 'concentration': case 'conc': spell.concentration = value.toLowerCase() === 'yes' || value.toLowerCase() === 'true' || value === '1'; break;

        }

      });

      

      if (spell.name && spell.name !== 'New Spell') {

        // Auto-detect concentration from duration

        if (spell.duration.toLowerCase().includes('concentration')) {

          spell.concentration = true;

        }

        spells.push(spell);

      }

    }

  } 

  // Block format (=== SPELL ===)

  else if (text.includes('===') || text.toLowerCase().includes('name:')) {

    const blocks = text.split(/={3,}|\n\n+/).filter(b => b.trim());

    

    for (const block of blocks) {

      if (!block.toLowerCase().includes('name')) continue;

      

      const spell = createSpell();

      const lines = block.split('\n');

      

      for (const line of lines) {

        const match = line.match(/^([^:]+):\s*(.+)$/);

        if (!match) continue;

        

        const [, key, value] = match;

        const keyLower = key.trim().toLowerCase();

        const val = value.trim();

        

        switch (keyLower) {

          case 'name': spell.name = val; break;

          case 'level': spell.level = parseInt(val) || 0; break;

          case 'school': spell.school = val; break;

          case 'time': case 'castingtime': case 'casting time': spell.castingTime = val; break;

          case 'range': spell.range = val; break;

          case 'components': case 'comp': spell.components = val; break;

          case 'duration': case 'dur': spell.duration = val; break;

          case 'effect': spell.effect = val; break;

          case 'dice': spell.dice = val; break;

          case 'hit': case 'dc': case 'hit/dc': spell.hitDC = val; break;

          case 'description': case 'desc': spell.description = val; break;

          case 'higher': case 'higher levels': case 'higherlevels': spell.higherLevels = val; break;

          case 'tags': spell.tags = val.split(',').map(t => t.trim()).filter(Boolean); break;

          case 'source': spell.source = val; break;

          case 'concentration': case 'conc': spell.concentration = val.toLowerCase() === 'yes' || val.toLowerCase() === 'true'; break;

        }

      }

      

      if (spell.name && spell.name !== 'New Spell') {

        if (spell.duration.toLowerCase().includes('concentration')) {

          spell.concentration = true;

        }

        spells.push(spell);

      }

    }

  }

  

  return spells;

};



// Export spells to text format

const exportSpellsToText = (spells, format = 'csv') => {

  if (format === 'csv') {

    const headers = ['Name', 'Level', 'School', 'Time', 'Range', 'Components', 'Duration', 'Effect', 'Dice', 'Hit/DC', 'Description', 'Higher Levels', 'Tags', 'Source', 'Concentration'];

    const rows = spells.map(s => [

      s.name, s.level, s.school, s.castingTime, s.range, s.components, s.duration,

      s.effect, s.dice, s.hitDC, s.description, s.higherLevels, 

      (s.tags || []).join(';'), s.source, s.concentration ? 'Yes' : 'No'

    ].map(v => `"${(v || '').toString().replace(/"/g, '""')}"`).join(','));

    

    return [headers.join(','), ...rows].join('\n');

  }

  

  // Block format

  return spells.map(s => `=== SPELL ===

Name: ${s.name}

Level: ${s.level}

School: ${s.school}

Time: ${s.castingTime}

Range: ${s.range}

Components: ${s.components}

Duration: ${s.duration}

Effect: ${s.effect}

Dice: ${s.dice}

Hit/DC: ${s.hitDC}

Description: ${s.description}

Higher Levels: ${s.higherLevels}

Tags: ${(s.tags || []).join(', ')}

Source: ${s.source}

Concentration: ${s.concentration ? 'Yes' : 'No'}

`).join('\n');

};



// ============== END SPELL SYSTEM ==============



// Hunger Level System - daily points

const HUNGER_LEVELS = {

  full: { name: 'Full', points: 0.5, mealsRequired: 3, description: '3 meals eaten' },

  noHunger: { name: 'No Hunger', points: 0, mealsRequired: 2, description: '2 meals eaten' },

  lightHunger: { name: 'Light Hunger', points: -1, mealsRequired: 1, description: '1 meal eaten' },

  mediumHunger: { name: 'Medium Hunger', points: -2, mealsRequired: 0, description: '0 meals eaten' },

};



// Get hunger level based on meals eaten

const getHungerLevel = (mealsToday) => {

  if (mealsToday >= 3) return 'full';

  if (mealsToday >= 2) return 'noHunger';

  if (mealsToday >= 1) return 'lightHunger';

  return 'mediumHunger';

};



// Weekly hunger consequences

const WEEKLY_HUNGER_CONSEQUENCES = {

  mild: { min: -2, max: 0, effect: 'diseaseConPenalty', value: 2, description: 'Con -2 for disease checks this month' },

  moderate: { min: -6, max: -3, effect: 'instantDiseaseCheck', conPenalty: 4, description: 'Instant disease check with Con -4' },

  severe: { min: -Infinity, max: -7, effect: 'instantDisease', permConLoss: 1, description: 'Instantly gain disease + permanent -1 Con' },

};



// Check if a day's meals meet diet requirements

const checkDietDayRequirements = (mealsToday, mealTypes, diet) => {

  if (!diet) return false;

  

  // Check minimum meals

  if (mealsToday < diet.minMealsPerDay) return false;

  

  // Check meal types

  if (diet.allMealsMustMatch) {

    // All meals must be from required types

    return mealTypes.every(type => diet.requiredMealTypes.includes(type));

  } else {

    // Any category is allowed (for Mixed and Full Stomach)

    return true;

  }

};



// Get effective Con for disease checks (includes all penalties)

const getEffectiveConForDiseaseCheck = (char, diseases) => {

  const baseStats = char.baseStats || { con: 10 };

  const effectiveBuffs = getEffectiveBuffs(char);

  let effectiveCon = baseStats.con + getActiveLegacyStatDelta({

    legacyState: {

      buffList: [],

      debuffList: [],

      permBuffs: effectiveBuffs.permBuffs,

      permDebuffs: effectiveBuffs.permDebuffs,

      buffData: char.buffData,

      buffSources: effectiveBuffs.buffSources,

      buffNotes: char.buffNotes,

    },

    stat: 'con',

    includeTemporary: false,

  });



  // Apply disease check penalty from mild weekly hunger

  effectiveCon -= (char.diseaseCheckConPenalty || 0);



  return effectiveCon;

};



const getTechniqueRequirementConditionNames = (char) => {

  if (!char) return [];

  const effectiveBuffs = getEffectiveBuffs(char);

  return [

    ...(char.conditions || []),

    ...getActiveLegacyEffectNames({

      legacyState: {

        buffList: effectiveBuffs.buffList,

        debuffList: effectiveBuffs.debuffList,

        permBuffs: [],

        permDebuffs: [],

        buffData: char.buffData,

        buffSources: effectiveBuffs.buffSources,

        buffNotes: char.buffNotes,

      },

      includePermanent: false,

    }),

  ];

};



// Process end of week - returns updates object and events array

const processWeekEnd = (char, diseases, diets, options = {}) => {

  const updates = {};

  const events = [];
  const hungerEnabled = options.hungerEnabled !== false;
  const dietsEnabled = options.dietsEnabled !== false;
  const diseasesEnabled = options.diseasesEnabled !== false;

  const weekScore = char.weeklyHungerScore || 0;

  const weekBonus = char.weeklyStarvationBonus || 0;

  const adjustedScore = weekScore + weekBonus;

  

  updates.statusHistory = [...(char.statusHistory || [])];

  

  // Record week end

  updates.statusHistory.unshift({

    timestamp: new Date().toISOString(),

    day: char.currentDay,

    event: 'week-end',

    description: `Week ${char.currentWeekNumber || 1} ended with hunger score: ${adjustedScore.toFixed(1)}`

  });

  

  if (hungerEnabled && adjustedScore < 0) {

    // Negative week consequences

    updates.consecutiveNegativeWeeks = (char.consecutiveNegativeWeeks || 0) + 1;

    

    // Check for death (4 consecutive negative weeks)

    if (updates.consecutiveNegativeWeeks >= 4) {

      events.push({ char: char.name, event: 'death', reason: '4 consecutive weeks of negative hunger' });

      updates.statusHistory.unshift({

        timestamp: new Date().toISOString(),

        day: char.currentDay,

        event: 'death',

        description: `💀 DEATH: 4 consecutive weeks of starvation`

      });

    }

    // Check for stat loss (3+ consecutive negative weeks)

    else if (updates.consecutiveNegativeWeeks >= 3) {

      const penaltyAmount = 1 + (char.negativeWeekStreak || 0);

      updates.negativeWeekStreak = (char.negativeWeekStreak || 0) + 1;

      

      // Apply permanent stat loss to all stats

      const newBaseStats = { ...(char.baseStats || { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 }) };

      ['str', 'dex', 'con', 'int', 'wis', 'cha'].forEach(stat => {

        newBaseStats[stat] = Math.max(1, newBaseStats[stat] - penaltyAmount);

      });

      updates.baseStats = newBaseStats;

      

      events.push({ char: char.name, event: 'stat-loss', amount: penaltyAmount });

      updates.statusHistory.unshift({

        timestamp: new Date().toISOString(),

        day: char.currentDay,

        event: 'stat-loss',

        description: `⚠️ STARVATION: -${penaltyAmount} to ALL stats (${updates.consecutiveNegativeWeeks} negative weeks)`

      });

    }

    

    // Apply weekly consequence based on score

    if (adjustedScore >= -2) {

      // Mild: -2 Con for disease checks this month

      if (diseasesEnabled) {
        updates.diseaseCheckConPenalty = (char.diseaseCheckConPenalty || 0) + 2;
        events.push({ char: char.name, event: 'disease-vulnerability', penalty: 2 });
      }

      updates.statusHistory.unshift({

        timestamp: new Date().toISOString(),

        day: char.currentDay,

        event: 'hunger-mild',

        description: `🤒 Weakened: Con -2 for disease checks this month`

      });

    } else if (adjustedScore >= -6) {

      // Moderate: Instant disease check with Con -4

      if (diseasesEnabled) events.push({ char: char.name, event: 'disease-check', conPenalty: 4 });

      updates.statusHistory.unshift({

        timestamp: new Date().toISOString(),

        day: char.currentDay,

        event: 'hunger-moderate',

        description: `🦠 Disease check required (Con -4 for this check)`

      });

    } else {

      // Severe: Instant disease + permanent -1 Con

      const newBaseStats = { ...(char.baseStats || { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 }) };

      newBaseStats.con = Math.max(1, newBaseStats.con - 1);

      updates.baseStats = newBaseStats;

      

      // Give random disease

      const diseaseKeys = diseasesEnabled ? Object.keys(diseases) : [];

      if (diseaseKeys.length > 0) {

        const randomDisease = diseases[diseaseKeys[Math.floor(Math.random() * diseaseKeys.length)]];

        if (!(char.diseaseImmunityList || []).includes(randomDisease.id)) {

          const beforeDiseaseState = { ...char, ...updates };

          const afterDiseaseState = addDiseaseState(beforeDiseaseState, buildDiseaseState({

            diseaseId: randomDisease.id,

            id: generateId(),

            contractedDate: new Date().toISOString(),

          }));

          if (afterDiseaseState.diseaseList !== beforeDiseaseState.diseaseList) {

            updates.diseaseList = afterDiseaseState.diseaseList;

          }

          events.push({ char: char.name, event: 'disease', disease: randomDisease.name, permConLoss: true });

        }

      }

      

      updates.statusHistory.unshift({

        timestamp: new Date().toISOString(),

        day: char.currentDay,

        event: 'hunger-severe',

        description: `💀 Severe starvation: Permanent -1 Con and gained disease`

      });

    }

    

    // Reset diet progress on negative week

    if (dietsEnabled && char.activeDiet) {

      updates.dietWeekProgress = 0;

      updates.dietDayStreak = 0;

      updates.statusHistory.unshift({

        timestamp: new Date().toISOString(),

        day: char.currentDay,

        event: 'diet-broken',

        description: `❌ Diet "${char.activeDiet}" broken due to negative hunger week`

      });

    }

  } else {

    // Positive/neutral week - reset consecutive counter

    updates.consecutiveNegativeWeeks = 0;

    

    // Check diet week completion

    if (dietsEnabled && char.activeDiet && diets[char.activeDiet]) {

      const diet = diets[char.activeDiet];

      // If they met requirements all 7 days this week

      if ((char.dietDayStreak || 0) >= 7) {

        const newWeekProgress = (char.dietWeekProgress || 0) + 1;

        

        if (newWeekProgress >= diet.durationWeeks) {

          // Diet completed!

          const completionCount = (char.dietCompletionCounts?.[char.activeDiet] || 0) + 1;

          

          if (completionCount <= diet.maxCompletions) {

            // Apply rewards

            updates.dietCompletionCounts = { ...(char.dietCompletionCounts || {}), [char.activeDiet]: completionCount };

            updates.completedDiets = [...(char.completedDiets || []), {

              dietName: diet.name,

              date: new Date().toISOString(),

              buffs: diet.completionBuffs,

              completionNumber: completionCount

            }];

            

            const beforeDietRewardState = { ...char, ...updates };

            const afterDietRewardState = applyDietCompletionRewardState(beforeDietRewardState, diet, completionCount);

            ['permanentBuffs', 'buffSources', 'buffData', 'buffNotes', 'weeklyStarvationBonus', 'skillCheckBonus'].forEach((fieldName) => {

              if (afterDietRewardState[fieldName] !== beforeDietRewardState[fieldName]) {

                updates[fieldName] = afterDietRewardState[fieldName];

              }

            });

            

            events.push({ char: char.name, event: 'diet-complete', diet: diet.name, completion: completionCount });

            updates.statusHistory.unshift({

              timestamp: new Date().toISOString(),

              day: char.currentDay,

              event: 'diet-complete',

              description: `🎉 Completed ${diet.name} diet (#${completionCount}/${diet.maxCompletions})!`

            });

          }

          

          // Reset diet

          updates.activeDiet = null;

          updates.dietWeekProgress = 0;

        } else {

          updates.dietWeekProgress = newWeekProgress;

          updates.statusHistory.unshift({

            timestamp: new Date().toISOString(),

            day: char.currentDay,

            event: 'diet-progress',

            description: `📊 ${diet.name} diet: Week ${newWeekProgress}/${diet.durationWeeks} complete`

          });

        }

      } else {

        // Failed to meet daily requirements - diet broken

        updates.dietWeekProgress = 0;

        updates.statusHistory.unshift({

          timestamp: new Date().toISOString(),

          day: char.currentDay,

          event: 'diet-broken',

          description: `❌ Diet "${char.activeDiet}" broken - didn't meet daily requirements`

        });

      }

    }

  }

  

  // Reset for new week

  updates.weeklyHungerScore = hungerEnabled ? (char.weeklyStarvationBonus || 0) : 0; // Start with bonus if any

  updates.currentWeekNumber = (char.currentWeekNumber || 1) + 1;

  updates.dietDayStreak = 0;

  

  return { updates, events };

};



const DEFAULT_LOCATIONS = ['Swamp', 'Forest', 'Mountain', 'Desert', 'Tundra', 'Cave'];



// Armor System Defaults

const DEFAULT_ARMOR_TYPES = {

  none: { 

    name: 'No Armor', 

    buffs: [], 

    debuffs: [],

    baseAC: 10,

    description: 'No armor equipped'

  },

  light: { 

    name: 'Light Armor', 

    buffs: ['+5 MS', '+1 Dex Saves'], 

    debuffs: [],

    baseAC: 11,

    description: 'Padded, Leather, Studded Leather'

  },

  medium: { 

    name: 'Medium Armor', 

    buffs: ['+1 AC'], 

    debuffs: ['-5 MS'],

    baseAC: 13,

    description: 'Hide, Chain Shirt, Scale Mail, Breastplate, Half Plate'

  },

  heavy: { 

    name: 'Heavy Armor', 

    buffs: ['+2 AC', '+1 Con Saves'], 

    debuffs: ['-10 MS', 'Disadvantage Stealth'],

    baseAC: 16,

    description: 'Ring Mail, Chain Mail, Splint, Plate'

  }

};



// ============== COMPREHENSIVE DURABILITY SYSTEM ==============



// Maintenance levels affect max dura decay

const MAINTENANCE_LEVELS = {

  great: { name: 'Great', maxDuraLossPerMonth: 0.5, armorMultiplier: 2, description: 'Lose 1 max dura every 2 months (x2 for armor)' },

  basic: { name: 'Basic', maxDuraLossPerMonth: 1, armorMultiplier: 2, description: 'Lose 1 max dura every month (x2 for armor)' },

  low: { name: 'Low/None', maxDuraLossPerMonth: 3, armorMultiplier: 2, description: 'Lose 3 max dura per month (x2 for armor)' },

};



// Weapon type definitions with durability effects

const WEAPON_TYPES = {

  slashing: {

    name: 'Slashing',

    icon: '🗡️',

    baseDura: 22,

    potentialExtraDura: [2, 8], // min, max

    // Damage taken from hitting armor types

    duraDamageFrom: { heavy: 2, medium: 1, blocking: [1, 4] }, // blocking is range

    effects: [

      { threshold: 80, type: 'above', effect: '+10% Damage', buff: '+10% Damage' },

      { threshold: 60, type: 'above', effect: '+5% Block Chance', buff: '+5% Block' },

      { threshold: 50, type: 'above', effect: '+5% Damage', buff: '+5% Damage' },

      { threshold: 50, type: 'below', effect: '-15% Damage', debuff: '-15% Damage' },

      { threshold: 30, type: 'below', effect: '-5% Damage', debuff: '-5% Damage' },

      { threshold: 10, type: 'below', effect: '-15% Wardsave', debuff: '-15% Wardsave' },

      { threshold: 0, type: 'at', effect: 'BREAKS', broken: true },

    ],

  },

  piercing: {

    name: 'Piercing',

    icon: '🔪',

    baseDura: 18,

    potentialExtraDura: [6, 6],

    duraDamageFrom: { heavy: 3, medium: 0.5 },

    effects: [

      { threshold: 90, type: 'above', effect: '+20% Armor Pen', buff: '+20% Armor Pen' },

      { threshold: 80, type: 'above', effect: '+10% Armor Pen', buff: '+10% Armor Pen' },

      { threshold: 70, type: 'above', effect: '+5% Damage', buff: '+5% Damage' },

      { threshold: 30, type: 'below', effect: '-4 To Hit', debuff: '-4 To Hit' },

      { threshold: 10, type: 'below', effect: '+30% Damage Taken', debuff: '+30% Damage Taken' },

      { threshold: 0, type: 'at', effect: 'BREAKS', broken: true },

    ],

  },

  smashing: {

    name: 'Smashing',

    icon: '🔨',

    baseDura: 18,

    potentialExtraDura: [2, 2],

    duraDamageFrom: { heavy: 2, medium: 0.25 },

    effects: [

      { threshold: 60, type: 'above', effect: '+1d6 Wounds Dice', buff: '+1d6 Wounds' },

      { threshold: 20, type: 'below', effect: '+30% Damage / +50% Damage Taken', debuff: '+50% Damage Taken' },

      { threshold: 0, type: 'at', effect: 'BREAKS', broken: true },

    ],

  },

  shooting: {

    name: 'Shooting',

    icon: '🏹',

    baseDura: 15,

    potentialExtraDura: [1, 12],

    duraDamageFrom: { shooting: 0.5 }, // loses dura when shooting

    effects: [

      { threshold: 70, type: 'above', effect: '+2 To Hit', buff: '+2 To Hit' },

      { threshold: 50, type: 'above', effect: '+10% Damage', buff: '+10% Damage' },

      { threshold: 30, type: 'below', effect: 'x2 Dura Damage', special: 'doubleDuraDamage' },

      { threshold: 20, type: 'below', effect: '-25% Damage', debuff: '-25% Damage' },

      { threshold: 0, type: 'at', effect: 'BREAKS', broken: true },

    ],

  },

};



// Armor type definitions with durability effects

const ARMOR_TYPES_DURA = {

  light: {

    name: 'Light Armor',

    icon: '🥋',

    baseDura: 25,

    potentialExtraDura: [1, 5],

    // Damage taken from damage types

    duraDamageFrom: { slashing: 1, piercing: 1, fire: 4, frost: 2, acid: 8 },

    noArmorPenalty: 'x3 Damage Taken',

    effects: [

      { threshold: 90, type: 'above', effect: '+10ft MS', buff: '+10ft MS' },

      { threshold: 70, type: 'above', effect: '+40% Decadence', buff: '+40% Decadence' },

      { threshold: 60, type: 'above', effect: '+10% Damage', buff: '+10% Damage' },

      { threshold: 50, type: 'below', effect: 'x2 Fire Damage', debuff: 'x2 Fire Damage' },

      { threshold: 20, type: 'below', effect: '+25% Slash/Pierce Damage', debuff: '+25% Slash/Pierce Taken' },

      { threshold: 0, type: 'at', effect: '50% Break / 50% Recover 10% Max', breakChance: 50, recoverPercent: 10 },

    ],

  },

  medium: {

    name: 'Medium Armor',

    icon: '🛡️',

    baseDura: 30,

    potentialExtraDura: [2, 8],

    duraDamageFrom: { piercing: 1, fire: 2, frost: 2, acid: 6 },

    noArmorPenalty: 'x3 Damage Taken',

    effects: [

      { threshold: 90, type: 'above', effect: '+30% Mech Dodge', buff: '+30% Mech Dodge' },

      { threshold: 30, type: 'above', effect: '+15% Wardsave', buff: '+15% Wardsave' },

      { threshold: 10, type: 'below', effect: '-10ft MS', debuff: '-10ft MS' },

      { threshold: 0, type: 'at', effect: '25% Recover 20% Max / 75% Break', breakChance: 75, recoverPercent: 20 },

    ],

  },

  heavy: {

    name: 'Heavy Armor',

    icon: '🪖',

    baseDura: 40,

    potentialExtraDura: [5, 10],

    duraDamageFrom: { bludgeoning: 3, fire: 2, frost: 2, acid: 4, slashing: 0.5 },

    noArmorPenalty: 'x3 Damage Taken',

    effects: [

      { threshold: 70, type: 'above', effect: '+25% Wardsave & Will', buff: '+25% Wardsave', buff2: '+25% Will' },

      { threshold: 50, type: 'above', effect: '+10% Block Chance', buff: '+10% Block' },

      { threshold: 30, type: 'above', effect: '+10% Mech Resist', buff: '+10% Mech Resist' },

      { threshold: 25, type: 'below', effect: '-5ft MS', debuff: '-5ft MS' },

      { threshold: 20, type: 'below', effect: '-5ft MS', debuff: '-5ft MS' },

      { threshold: 15, type: 'below', effect: '-5ft MS', debuff: '-5ft MS' },

      { threshold: 10, type: 'below', effect: '-5ft MS', debuff: '-5ft MS' },

      { threshold: 5, type: 'below', effect: '-5ft MS', debuff: '-5ft MS' },

      { threshold: 0, type: 'at', effect: 'BREAKS (Unrepairable)', broken: true, unrepairable: true },

    ],

  },

  none: {

    name: 'No Armor',

    icon: '👤',

    baseDura: 0,

    potentialExtraDura: [0, 0],

    duraDamageFrom: {},

    noArmorPenalty: 'x3 Damage Taken (unless Monk/Barbarian)',

    effects: [],

  },

};



// Shield type definitions

const SHIELD_TYPES = {

  none: {

    name: 'No Shield',

    icon: '✋',

    baseDura: 0,

    potentialExtraDura: [0, 0],

    size: 'none',

    msPenalty: 0,

    restriction: null,

    duraDamageFrom: {},

    effects: [],

    canRepairWithoutBlacksmith: false,

  },

  buckler: {

    name: 'Buckler (Small)',

    icon: '🛡️',

    baseDura: 16,

    potentialExtraDura: [5, 7],

    size: 'small',

    msPenalty: 0,

    restriction: null,

    // Uses light armor dura damage table, half normally, double when blocking

    duraDamageFrom: { slashing: 0.5, piercing: 0.5, fire: 2, frost: 1, acid: 4 },

    blockingMultiplier: 2,

    effects: [

      { threshold: 80, type: 'above', effect: '+30% Retribution', buff: '+30% Retribution' },

      { threshold: 60, type: 'above', effect: '+7% Block Chance', buff: '+7% Block' },

      { threshold: 40, type: 'above', effect: '+10% Retribution', buff: '+10% Retribution' },

      { threshold: 30, type: 'below', effect: '-1 AC', debuff: '-1 AC' },

      { threshold: 30, type: 'below', effect: '20% Instant Break on Heavy Hit', special: 'heavyHitBreak20' },

    ],

    canRepairWithoutBlacksmith: true,

  },

  blacksteel: {

    name: 'Blacksteel (Medium)',

    icon: '🛡️',

    baseDura: 22,

    potentialExtraDura: [0, 0],

    size: 'medium',

    msPenalty: 5,

    restriction: null,

    duraDamageFrom: { piercing: 0.5, fire: 1, frost: 1, acid: 3 },

    blockingMultiplier: 2,

    effects: [

      { threshold: 90, type: 'above', effect: '+1 AC', buff: '+1 AC' },

      { threshold: 80, type: 'above', effect: '+10% Will, +5% Wardsave', buff: '+10% Will', buff2: '+5% Wardsave' },

      { threshold: 40, type: 'above', effect: '+12% Block Chance', buff: '+12% Block' },

      { threshold: 30, type: 'above', effect: '+1 AC', buff: '+1 AC' },

      { threshold: 10, type: 'below', effect: '-5 AC', debuff: '-5 AC' },

    ],

    canRepairWithoutBlacksmith: false,

  },

  hclass: {

    name: 'H-Class (Large)',

    icon: '🛡️',

    baseDura: 28,

    potentialExtraDura: [0, 0],

    size: 'large',

    msPenalty: 10,

    restriction: 'Light one-handed weapons only',

    duraDamageFrom: { bludgeoning: 1.5, fire: 1, frost: 1, acid: 2, slashing: 0.25 },

    blockingMultiplier: 2,

    effects: [

      { threshold: 80, type: 'above', effect: '+10% Will & Wardsave', buff: '+10% Will', buff2: '+10% Wardsave' },

      { threshold: 70, type: 'above', effect: '+5% Will & Wardsave', buff: '+5% Will', buff2: '+5% Wardsave' },

      { threshold: 60, type: 'above', effect: '+20% Block Chance', buff: '+20% Block' },

      { threshold: 50, type: 'above', effect: '+2 Flat Pre-Damage Mitigation', buff: '+2 Flat Mitigation' },

      { threshold: 50, type: 'below', effect: 'Cannot Be Repaired', special: 'unrepairable' },

      { threshold: 15, type: 'below', effect: 'Cannot Be Used', special: 'unusable' },

    ],

    canRepairWithoutBlacksmith: false,

  },

};



// Damage types for durability calculations

const DURA_DAMAGE_TYPES = ['slashing', 'piercing', 'bludgeoning', 'fire', 'frost', 'acid'];



// Create equipment with durability tracking

const createEquipmentDura = (name, type, subtype, overrides = {}) => {

  let typeData;

  if (type === 'weapon') typeData = WEAPON_TYPES[subtype];

  else if (type === 'armor') typeData = ARMOR_TYPES_DURA[subtype];

  else if (type === 'shield') typeData = SHIELD_TYPES[subtype];

  else return null;

  

  if (!typeData) return null;

  

  // Roll potential extra durability

  const [minExtra, maxExtra] = typeData.potentialExtraDura || [0, 0];

  const rolledExtra = minExtra + Math.floor(Math.random() * (maxExtra - minExtra + 1));

  const maxDura = typeData.baseDura + rolledExtra;

  

  return {

    id: generateId(),

    name,

    type, // 'weapon', 'armor', 'shield'

    subtype, // 'slashing', 'light', 'buckler', etc.

    currentDura: maxDura,

    maxDura,

    baseMaxDura: typeData.baseDura,

    bonusDura: rolledExtra,

    potentialDura: typeData.potentialExtraDura,

    maintenance: 'basic', // 'great', 'basic', 'low'

    equipped: false,

    broken: false,

    unrepairable: false,

    notes: '',

  dmNotes: '',

    ...overrides,

  };

};



// Calculate durability percentage

const getDuraPercent = (current, max) => max > 0 ? Math.round((current / max) * 100) : 0;



// Get active effects based on durability percentage

const getActiveDuraEffects = (equipment) => {

  if (!equipment || equipment.broken) return { buffs: [], debuffs: [], specials: [] };

  

  let typeData;

  if (equipment.type === 'weapon') typeData = WEAPON_TYPES[equipment.subtype];

  else if (equipment.type === 'armor') typeData = ARMOR_TYPES_DURA[equipment.subtype];

  else if (equipment.type === 'shield') typeData = SHIELD_TYPES[equipment.subtype];

  

  if (!typeData || !typeData.effects) return { buffs: [], debuffs: [], specials: [] };

  

  const percent = getDuraPercent(equipment.currentDura, equipment.maxDura);

  const buffs = [];

  const debuffs = [];

  const specials = [];

  

  for (const effect of typeData.effects) {

    let active = false;

    if (effect.type === 'above' && percent > effect.threshold) active = true;

    else if (effect.type === 'below' && percent < effect.threshold && percent > 0) active = true;

    else if (effect.type === 'at' && percent === effect.threshold) active = true;

    

    if (active) {

      if (effect.buff) buffs.push(effect.buff);

      if (effect.buff2) buffs.push(effect.buff2);

      if (effect.debuff) debuffs.push(effect.debuff);

      if (effect.special) specials.push({ type: effect.special, threshold: effect.threshold });

      if (effect.broken) specials.push({ type: 'broken' });

    }

  }

  

  return { buffs, debuffs, specials };

};



// Calculate durability damage from an attack

const calculateDuraDamage = (equipment, damageType, isBlocking = false) => {

  if (!equipment) return 0;

  

  let typeData;

  if (equipment.type === 'weapon') typeData = WEAPON_TYPES[equipment.subtype];

  else if (equipment.type === 'armor') typeData = ARMOR_TYPES_DURA[equipment.subtype];

  else if (equipment.type === 'shield') typeData = SHIELD_TYPES[equipment.subtype];

  

  if (!typeData || !typeData.duraDamageFrom) return 0;

  

  let damage = typeData.duraDamageFrom[damageType] || 0;

  

  // Shields take half normally, double when blocking

  if (equipment.type === 'shield') {

    if (isBlocking) {

      damage *= typeData.blockingMultiplier || 2;

    }

  }

  

  // Check for double dura damage special (shooting weapons below 30%)

  const effects = getActiveDuraEffects(equipment);

  if (effects.specials.some(s => s.type === 'doubleDuraDamage')) {

    damage *= 2;

  }

  

  return Math.ceil(damage);

};



// Apply durability damage and check for breaking

const applyDuraDamage = (equipment, damage) => {

  if (!equipment || equipment.broken || damage <= 0) return { ...equipment, events: [] };

  

  const events = [];

  let newDura = Math.max(0, equipment.currentDura - damage);

  let broken = equipment.broken;

  let unrepairable = equipment.unrepairable;

  

  // Check for 0% effects

  if (newDura === 0) {

    let typeData;

    if (equipment.type === 'weapon') typeData = WEAPON_TYPES[equipment.subtype];

    else if (equipment.type === 'armor') typeData = ARMOR_TYPES_DURA[equipment.subtype];

    else if (equipment.type === 'shield') typeData = SHIELD_TYPES[equipment.subtype];

    

    const zeroEffect = typeData?.effects?.find(e => e.threshold === 0 && e.type === 'at');

    

    if (zeroEffect) {

      if (zeroEffect.broken) {

        broken = true;

        events.push(`${equipment.name} has BROKEN!`);

        if (zeroEffect.unrepairable) {

          unrepairable = true;

          events.push(`${equipment.name} is unrepairable!`);

        }

      } else if (zeroEffect.breakChance) {

        const roll = Math.random() * 100;

        if (roll < zeroEffect.breakChance) {

          broken = true;

          events.push(`${equipment.name} has BROKEN! (rolled ${Math.round(roll)})`);

        } else {

          // Recover some max dura

          const recoveredMax = Math.floor(equipment.maxDura * (zeroEffect.recoverPercent / 100));

          newDura = recoveredMax;

          events.push(`${equipment.name} survived! Recovered to ${recoveredMax} dura.`);

        }

      }

    }

  }

  

  return {

    ...equipment,

    currentDura: newDura,

    broken,

    unrepairable,

    events,

  };

};



// Get color for durability display

const getDuraColor = (percent) => {

  if (percent > 80) return '#27c95a'; // Green

  if (percent > 60) return '#7db5ff'; // Blue

  if (percent > 40) return '#c9a227'; // Yellow

  if (percent > 20) return '#d97429'; // Orange

  return '#c92727'; // Red

};



// Legacy armor durability (kept for backward compatibility)

const DEFAULT_ARMOR_DURABILITY = {

  maxDurability: 25,

  damagedThreshold: 15, // At or below this = damaged

  brokenThreshold: 5,   // At or below this = broken

  damagedDebuffs: ['-1 Str'],

  brokenDebuffs: ['-2 Str', '-1 AC'],

};



// ============== END DURABILITY SYSTEM ==============



// ============== JOURNAL SYSTEM ==============



// ============================================================

// Journal Quick-Add Tag System

// ============================================================



// Tag code → journal category mapping

const JOURNAL_TAG_CODES = {

  M: { category: 'creatures', label: 'Monster/Creature', color: '#c92727' },

  N: { category: 'npcs', label: 'NPC', color: '#4a90d9' },

  S: { category: 'npcs', label: 'Shopkeeper NPC', color: '#4a90d9', extraTags: ['shopkeeper'] },

  I: { category: 'items', label: 'Item', color: '#d97429' },

  Q: { category: 'quests', label: 'Quest', color: '#9b59b6' },

  P: { category: 'plants', label: 'Plant', color: '#27c95a' },

  R: { category: 'recipes', label: 'Recipe', color: '#17a589' },

  D: { category: 'diseases', label: 'Disease', color: '#8e44ad' },

  L: { category: null, label: 'Location (tag)', color: '#2ecc71' }, // Not a category - adds locationTag

  PL: { category: 'players', label: 'Player', color: '#e91e63', dmOnly: true },

  CH: { category: 'characters', label: 'Character', color: '#00bcd4', dmOnly: true },

};



// DM-only tag codes (extended set for DM journal)

const DM_JOURNAL_TAG_CODES = {

  ...JOURNAL_TAG_CODES,

  PL: { category: 'players', label: 'Player', color: '#e91e63', dmOnly: true },

  CH: { category: 'characters', label: 'Character', color: '#00bcd4', dmOnly: true },

};





// Default quick-add settings

const DEFAULT_QUICKADD_SETTINGS = {

  scopeMode: 'after',  // 'after' | 'before' | 'all' — global default

  delimiter: ' - ',     // Text separator between name and description

  categoryScopeModes: {}, // Per-category overrides: { creatures: 'after', quests: 'all', ... }

  tabDelimiter: 'tick', // 'tick' (paragraph scope) or 'dash' (text-after scope)

  autocompleteDelimiter: 'tick', // 'tick' or 'dash' — what autocomplete inserts

};



// Get effective scope mode for a category

const getTagScopeMode = (settings, categoryCode) => {

  const cat = JOURNAL_TAG_CODES[categoryCode]?.category;

  return (settings.categoryScopeModes || {})[cat] || settings.scopeMode || 'after';

};



// ---- Parser ----



// End delimiters: tick (') and backtick (`) both terminate names and mark paragraph scope

const isEndDelimiter = (ch) => ch === "'" || ch === '`' || ch === '\u2019' || ch === '\u2018' || ch === '\u201D' || ch === '\u201C';

const findEndDelimiter = (text, from) => {

  for (let i = from; i < text.length; i++) {

    if (isEndDelimiter(text[i])) return i;

  }

  return -1;

};



// Parse a single tag: #X@Name or #X@"Name" or #X@'Name'

// Returns { code, name, nameStart, nameEnd, tagStart, tagEnd, quoted }

//

// 2026-04-27: visibility grammar swapped to be prefix-driven.

//   #N@Name / #N#Name        → VISIBLE (single # = show)

//   ##N@Name / ##N#Name      → HIDDEN (double # = hide)

//   @N@Name / @N#Name        → HIDDEN (legacy from earlier swap)

// The suffix (#/@) is now decorative — kept in the regex for back-compat

// with users who already typed `#N#Name` expecting it to render. The

// previous "suffix=# means hidden" rule turned canonical NPC tags like

// `#N#Gilgamesh` into stripped text.

const parseTag = (text, pos, delimiter) => {

  // Match ##X[#/@], #X[#/@], or @X[#/@] where X is one or two letters.

  // The `##` form must come first inside the alternation so it matches

  // before the single `#` form.

  // 2026-04-27: case-insensitive on the code letters — users were typing

  // `#n@bob` and getting nothing because we only matched uppercase. Match

  // any case, then uppercase before the JOURNAL_TAG_CODES lookup.

  const tagMatch = text.substring(pos).match(/^(##|#|@)([A-Za-z]{1,2})([#@])/);

  if (!tagMatch) return null;



  const prefix = tagMatch[1]; // #, ##, or @

  const code = tagMatch[2].toUpperCase();

  const suffix = tagMatch[3]; // # or @ (decorative)

  if (!JOURNAL_TAG_CODES[code]) return null;



  const tagVisibility = (prefix === '##' || prefix === '@') ? 'hidden' : 'visible';

  

  const nameStart = pos + tagMatch[0].length;

  let name = '';

  let nameEnd = nameStart;

  let quoted = false;

  

  // Check for quoted name — supports smart quotes

  const firstChar = text[nameStart];

  const isQuoteChar = (ch) => ch === '"' || ch === "'" || ch === '\u2018' || ch === '\u2019' || ch === '\u201C' || ch === '\u201D';

  if (isQuoteChar(firstChar)) {

    // Find matching close quote (any quote char)

    let closeIdx = -1;

    for (let qi = nameStart + 1; qi < text.length; qi++) {

      if (isQuoteChar(text[qi])) { closeIdx = qi; break; }

    }

    if (closeIdx !== -1) {

      name = text.substring(nameStart + 1, closeIdx);

      nameEnd = closeIdx + 1;

      quoted = true;

    }

  }

  

  // Unquoted name: runs until end delimiter ('/`), dash delimiter, newline, next tag, @ref, #ref, or end

  if (!quoted) {

    let end = text.length;

    // Find end delimiter (tick or backtick)

    const tickIdx = findEndDelimiter(text, nameStart);

    if (tickIdx !== -1 && tickIdx < end) end = tickIdx;

    // Find dash delimiter

    const delimIdx = text.indexOf(delimiter, nameStart);

    if (delimIdx !== -1 && delimIdx < end) end = delimIdx;

    // Find newline

    const nlIdx = text.indexOf('\n', nameStart);

    if (nlIdx !== -1 && nlIdx < end) end = nlIdx;

    // Find next entry tag (#X@, @X@, #X#) — case-insensitive

    const nextTagMatch = text.substring(nameStart).search(/[#@][A-Za-z]{1,2}[#@]/);

    if (nextTagMatch > 0) end = Math.min(end, nameStart + nextTagMatch);

    // Find @ref after whitespace

    const nextAtRef = text.substring(nameStart).search(/\s@(?!\s)/);

    if (nextAtRef > 0) end = Math.min(end, nameStart + nextAtRef);

    // Find #ref after whitespace (# not followed by tag pattern, case-insensitive)

    const nextHashRef = text.substring(nameStart).search(/\s#(?![A-Za-z]{1,2}@)(?![Tt][Oo][Dd][Oo]\b)(?!\s)/);

    if (nextHashRef > 0) end = Math.min(end, nameStart + nextHashRef);

    // Find #todo

    const nextTodo = text.substring(nameStart).search(/#todo\b/i);

    if (nextTodo > 0) end = Math.min(end, nameStart + nextTodo);

    

    name = text.substring(nameStart, end).trim();

    nameEnd = end;

  }

  

  // Check if end delimiter follows the name

  const hasTick = !quoted && nameEnd < text.length && isEndDelimiter(text[nameEnd]);

  const tagEnd = hasTick ? nameEnd + 1 : nameEnd;

  // Check if dash delimiter follows

  const hasDash = !quoted && !hasTick && text.substring(nameEnd).startsWith(delimiter);

  

  return {

    code,

    prefix,

    suffix,

    tagVisibility,

    name: name.trim(),

    nameStart,

    nameEnd,

    tagStart: pos,

    tagEnd,

    quoted,

    hasTick,

    hasDash,

    category: JOURNAL_TAG_CODES[code].category,

    extraTags: JOURNAL_TAG_CODES[code].extraTags || [],

  };

};



// Find all tags in a text string (includes #X@Name tags, @name references, and #todo items)

const findAllTags = (text, delimiter = ' - ') => {

  const tags = [];

  let i = 0;

  while (i < text.length) {

    // Check for #todo or @todo first (before # and @ specific handlers)

    if ((text[i] === '#' || text[i] === '@') && text.substring(i + 1, i + 5).toLowerCase() === 'todo' && (i + 5 >= text.length || /[\s'`]/.test(text[i + 5]))) {

      const todoPrefix = text[i]; // '#' or '@'

      const textStart = i + 5;

      // Skip whitespace

      let start = textStart;

      while (start < text.length && text[start] === ' ') start++;

      // Todo text runs until tick ', newline, next #tag, @ref, or another todo

      let end = text.length;

      const tickIdx = findEndDelimiter(text, start);

      if (tickIdx !== -1) end = Math.min(end, tickIdx);

      const nlIdx = text.indexOf('\n', start);

      if (nlIdx !== -1) end = Math.min(end, nlIdx);

      // Stop at next marker

      for (let j = start + 1; j < end; j++) {

        if ((text[j] === '#' || text[j] === '@') && (j === 0 || /\s/.test(text[j-1]))) {

          if (text.substring(j + 1, j + 5).toLowerCase() === 'todo') { end = j; break; }

          if (text.substring(j).match(/^[#@][A-Z]{1,2}[#@]/)) { end = j; break; }

          if (text[j] === '@' && j + 1 < text.length && /\S/.test(text[j+1])) { end = j; break; }

          if (text[j] === '#' && j + 1 < text.length && /[a-zA-Z"']/.test(text[j+1])) { end = j; break; }

        }

      }

      

      const todoText = text.substring(start, end).trim();

      const todoTagEnd = (end < text.length && isEndDelimiter(text[end])) ? end + 1 : end;

      if (todoText) {

        tags.push({

          type: 'todo',

          prefix: todoPrefix,

          text: todoText,

          tagStart: i,

          tagEnd: todoTagEnd,

        });

      }

      i = todoTagEnd;

      continue;

    }

    

    if (text[i] === '#') {



      // Check for ##X[#/@]Name or #X[#/@]Name formal tag.

      const tag = parseTag(text, i, delimiter);

      if (tag) {

        tag.type = 'tag';

        tags.push(tag);

        i = tag.tagEnd;

        continue;

      }



      // 2026-04-27: also support ##Name bare ref (hidden). The parseTag

      // path handles formal tags; here we handle the bare-ref short form

      // where the user just wants to mark a name as hidden without a

      // category code. The bare-#-ref path below handles the visible

      // single-# form. We only enter this branch if the second char is

      // also '#' followed by a name char, otherwise the loop falls

      // through to the single-# branch.

      if (text[i + 1] === '#' && /[a-zA-Z"']/.test(text[i + 2] || '')) {

        const ddNameStart = i + 2;

        let ddName = '';

        let ddNameEnd = ddNameStart;

        let ddQuoted = false;

        const ddFc = text[ddNameStart];

        if (ddFc === '"' || ddFc === "'") {

          const ci = text.indexOf(ddFc, ddNameStart + 1);

          if (ci !== -1) { ddName = text.substring(ddNameStart + 1, ci); ddNameEnd = ci + 1; ddQuoted = true; }

        }

        if (!ddQuoted) {

          let end = text.length;

          const tickIdx = findEndDelimiter(text, ddNameStart);

          if (tickIdx !== -1 && tickIdx < end) end = tickIdx;

          const nextTag = text.substring(ddNameStart).search(/[#@][A-Z]{1,2}[#@]|#todo\b/i);

          if (nextTag > 0) end = Math.min(end, ddNameStart + nextTag);

          const nextHash = text.substring(ddNameStart).search(/[\s]#(?![#\s])/);

          if (nextHash > 0) end = Math.min(end, ddNameStart + nextHash);

          const nextRef = text.substring(ddNameStart).search(/[\s]@(?!\s)/);

          if (nextRef > 0) end = Math.min(end, ddNameStart + nextRef);

          const delimIdx = text.indexOf(delimiter, ddNameStart);

          if (delimIdx !== -1 && delimIdx < end) end = delimIdx;

          const nlIdx = text.indexOf('\n', ddNameStart);

          if (nlIdx !== -1 && nlIdx < end) end = nlIdx;

          ddName = text.substring(ddNameStart, end).trim();

          ddNameEnd = end;

        }

        let ddTagEnd = ddNameEnd;

        const ddHasTick = ddNameEnd < text.length && isEndDelimiter(text[ddNameEnd]);

        if (ddHasTick) ddTagEnd = ddNameEnd + 1;

        if (ddName) {

          tags.push({

            type: 'ref',

            prefix: '##',

            name: ddName.trim(),

            nameStart: ddNameStart,

            nameEnd: ddNameEnd,

            tagStart: i,

            tagEnd: ddTagEnd,

            quoted: ddQuoted,

            hasTick: ddHasTick,

            category: null,

            code: null,

          });

          i = ddTagEnd;

          continue;

        }

      }



      // #name reference (single #, visible)

      const hashNameStart = i + 1;

      if (hashNameStart < text.length && /[a-zA-Z"']/.test(text[hashNameStart])) {

        let hName = '';

        let hNameEnd = hashNameStart;

        let hQuoted = false;

        

        const hfc = text[hashNameStart];

        if (hfc === '"' || hfc === "'") {

          const ci = text.indexOf(hfc, hashNameStart + 1);

          if (ci !== -1) { hName = text.substring(hashNameStart + 1, ci); hNameEnd = ci + 1; hQuoted = true; }

        }

        if (!hQuoted) {

          // Name runs until ' delimiter, next tag/ref, whitespace #/@ ref, newline, or ` - `

          let end = text.length;

          const tickIdx = findEndDelimiter(text, hashNameStart);

          if (tickIdx !== -1 && tickIdx < end) end = tickIdx;

          const nextTag = text.substring(hashNameStart).search(/[#@][A-Z]{1,2}[#@]|#todo\b/i);

          if (nextTag > 0) end = Math.min(end, hashNameStart + nextTag);

          const nextHash = text.substring(hashNameStart).search(/[\s]#(?![#\s])/);

          if (nextHash > 0) end = Math.min(end, hashNameStart + nextHash);

          const nextRef = text.substring(hashNameStart).search(/[\s]@(?!\s)/);

          if (nextRef > 0) end = Math.min(end, hashNameStart + nextRef);

          const delimIdx = text.indexOf(delimiter, hashNameStart);

          if (delimIdx !== -1 && delimIdx < end) end = delimIdx;

          const nlIdx = text.indexOf('\n', hashNameStart);

          if (nlIdx !== -1 && nlIdx < end) end = nlIdx;

          hName = text.substring(hashNameStart, end).trim();

          hNameEnd = end;

        }

        // If next char is ' (tick delimiter), advance tagEnd past it

        let hTagEnd = hNameEnd;

        const hHasTick = hNameEnd < text.length && isEndDelimiter(text[hNameEnd]);

        if (hHasTick) hTagEnd = hNameEnd + 1;

        if (hName) {

          tags.push({

            type: 'ref',

            prefix: '#',

            name: hName.trim(),

            nameStart: hashNameStart,

            nameEnd: hNameEnd,

            tagStart: i,

            tagEnd: hTagEnd,

            quoted: hQuoted,

            hasTick: hHasTick,

            category: null,

            code: null,

          });

          i = hTagEnd;

          continue;

        }

      }

    }

    

    // Check for @name reference or @X@Name tag (must be at start of text or after whitespace)

    if (text[i] === '@' && (i === 0 || /\s/.test(text[i-1]))) {

      // Check for @X@Name tag first

      const atTag = parseTag(text, i, delimiter);

      if (atTag) {

        atTag.type = 'tag';

        tags.push(atTag);

        i = atTag.tagEnd;

        continue;

      }

      const nameStart = i + 1;

      if (nameStart < text.length && !/\s/.test(text[nameStart])) {

        let name = '';

        let nameEnd = nameStart;

        let quoted = false;

        

        // Check for quoted name: @"Multi Word" or @'Multi Word'

        const firstChar = text[nameStart];

        if (firstChar === '"' || firstChar === "'") {

          const closeIdx = text.indexOf(firstChar, nameStart + 1);

          if (closeIdx !== -1) {

            name = text.substring(nameStart + 1, closeIdx);

            nameEnd = closeIdx + 1;

            quoted = true;

          }

        }

        

        if (!quoted) {

          // Name runs until ' delimiter, next tag/ref, newline, ` - `, or end

          let end = text.length;

          const tickIdx = findEndDelimiter(text, nameStart);

          if (tickIdx !== -1 && tickIdx < end) end = tickIdx;

          const nextTag = text.substring(nameStart).search(/[#@][A-Z]{1,2}[#@]|#todo\b/i);

          if (nextTag > 0) end = Math.min(end, nameStart + nextTag);

          const nextRef = text.substring(nameStart).search(/(?:[\s])@(?![\s])/);

          if (nextRef > 0) end = Math.min(end, nameStart + nextRef);

          const delimIdx = text.indexOf(delimiter, nameStart);

          if (delimIdx !== -1 && delimIdx < end) end = delimIdx;

          const nlIdx = text.indexOf('\n', nameStart);

          if (nlIdx !== -1 && nlIdx < end) end = nlIdx;

          

          name = text.substring(nameStart, end).trim();

          nameEnd = end;

        }

        

        // If next char is ' (tick delimiter), advance tagEnd past it

        let atTagEnd = nameEnd;

        const atHasTick = !quoted && nameEnd < text.length && isEndDelimiter(text[nameEnd]);

        if (atHasTick) atTagEnd = nameEnd + 1;

        

        if (name) {

          tags.push({

            type: 'ref',

            prefix: '@',

            name: name.trim(),

            nameStart,

            nameEnd,

            tagStart: i,

            tagEnd: atTagEnd,

            quoted,

            hasTick: atHasTick,

            category: null,

            code: null,

          });

          i = atTagEnd;

          continue;

        }

      }

    }

    

    i++;

  }

  return tags;

};



// Parse full quick-add input into structured blocks

// Returns array of { entries: [...], sessionText: string, locationTags: [...] }

const parseQuickAddInput = (rawText, settings = DEFAULT_QUICKADD_SETTINGS) => {

  const delimiter = settings.delimiter || ' - ';

  const customRules = settings.tagVisibilityRules || {};

  

  // Resolve tag visibility using custom rules

  const resolveVisibility = (tag) => {

    if (!tag || tag.type !== 'tag') return tag;

    const ruleId = tag.prefix === '#' && tag.suffix === '@' ? 'hash_at' : tag.prefix === '@' && tag.suffix === '@' ? 'at_at' : tag.prefix === '#' && tag.suffix === '#' ? 'hash_hash' : null;

    if (ruleId && customRules[ruleId]) {

      return { ...tag, tagVisibility: customRules[ruleId] };

    }

    return tag;

  };

  

  // Split on double line breaks into independent blocks

  const blocks = rawText.split(/\n\n+/).filter(b => b.trim());

  

  const results = [];

  

  for (const blockText of blocks) {

    const block = { entries: [], sessionText: '', locationTags: [], todos: [], rawText: blockText };

    const rawTags = findAllTags(blockText, delimiter);

    const tags = rawTags.map(resolveVisibility);

    block.parsedTags = tags; // Store for cleanup in applyQuickAddResults

    

    if (tags.length === 0) {

      // No tags — entire block is session text

      block.sessionText = blockText.trim();

      results.push(block);

      continue;

    }

    

    // Collect todos (preserve prefix: # = quest only, @ = quest + session)

    block.todos = tags.filter(t => t.type === 'todo').map(t => ({ text: t.text, prefix: t.prefix || '#' }));

    

    // Collect location tags

    const locationTags = tags.filter(t => t.type === 'tag' && t.code === 'L');

    block.locationTags = locationTags.map(t => t.name);

    

    // Get entry-creating tags (#X@Name) and @name references — exclude locations, todos

    const entryTags = tags.filter(t => 

      (t.type === 'tag' && t.code !== 'L') || t.type === 'ref'

    );

    

    if (entryTags.length === 0) {

      // Only location tags — text goes to session

      block.sessionText = cleanTagMarkup(blockText, tags).trim();

      results.push(block);

      continue;

    }

    

    // Text before first tag is session text

    const firstTagPos = tags[0].tagStart;

    if (firstTagPos > 0) {

      block.sessionText = blockText.substring(0, firstTagPos).trim();

    }

    

    // Process the primary entry (first non-location tag or @ref)

    const primaryTag = entryTags[0];

    const isRef = primaryTag.type === 'ref';

    // Tick delimiter ' = all paragraph text; dash delimiter - = text after only

    const useParagraphScope = primaryTag.hasTick;

    const scopeMode = useParagraphScope ? 'all' : (isRef ? 'after' : getTagScopeMode(settings, primaryTag.code));

    

    // The primary entry's full text scope depends on mode

    let primaryDescText = '';

    

    // Strip #todo lines from description text

    const stripTodos = (text) => text.replace(/[#@]todo\b[^\n]*/gi, '').replace(/\n{2,}/g, '\n').trim();

    

    if (scopeMode === 'all') {

      // Entire block text minus all tag markup (paragraph scope)

      primaryDescText = stripTodos(blockText).trim();

    } else if (scopeMode === 'after') {

      // Text after the tag name to end of block

      let textStart = primaryTag.tagEnd;

      // Skip ` - ` delimiter if present right after name

      if (blockText.substring(textStart).startsWith(delimiter)) {

        textStart += delimiter.length;

      }

      primaryDescText = stripTodos(blockText.substring(textStart)).trim();

    } else if (scopeMode === 'before') {

      primaryDescText = stripTodos(blockText.substring(0, primaryTag.tagStart)).trim();

    }

    

    // Clean markup from the description for storage

    const cleanDesc = cleanTagMarkup(primaryDescText, tags.filter(t => t.type === 'tag'));

    // Also clean @ref markup

    const cleanDescFinal = cleanRefMarkup(cleanDesc);

    

    block.entries.push({

      code: primaryTag.code || null,

      category: primaryTag.category || null,

      name: primaryTag.name,

      description: cleanDescFinal,

      extraTags: primaryTag.extraTags || [],

      isPrimary: true,

      isReference: isRef,

      linkedNames: entryTags.filter(t => t !== primaryTag).map(t => ({ name: t.name, category: t.category, code: t.code })),

      locationTags: block.locationTags,

    });

    

    // Process secondary tags within this block

    // Determine if this block has a quest (for quest-item linking)

    const blockHasQuest = entryTags.some(t => t.type === 'tag' && t.code === 'Q');

    

    for (let i = 1; i < entryTags.length; i++) {

      const tag = entryTags[i];

      const isSecRef = tag.type === 'ref';

      const secUseParagraph = tag.hasTick;

      const tagScope = secUseParagraph ? 'all' : (isSecRef ? 'after' : getTagScopeMode(settings, tag.code));

      

      let secondaryDesc = '';

      

      if (tagScope === 'all') {

        // Paragraph scope — all block text minus tag markup

        secondaryDesc = stripTodos(blockText).trim();

      } else if (tagScope === 'after') {

        // Text after this tag to end of block

        let textStart = tag.tagEnd;

        if (blockText.substring(textStart).startsWith(delimiter)) {

          textStart += delimiter.length;

        }

        secondaryDesc = stripTodos(blockText.substring(textStart)).trim();

      } else if (tagScope === 'before') {

        const prevTag = entryTags[i - 1] || { tagEnd: 0 };

        secondaryDesc = stripTodos(blockText.substring(prevTag.tagEnd, tag.tagStart)).trim();

        if (secondaryDesc.startsWith(delimiter.trim())) {

          secondaryDesc = secondaryDesc.substring(delimiter.trim().length).trim();

        }

      }

      

      const cleanSecondary = cleanRefMarkup(cleanTagMarkup(secondaryDesc, findAllTags(secondaryDesc, delimiter).filter(t => t.type === 'tag')));

      

      // Determine if this item is a quest sub-item

      const isQuestItem = tag.type === 'tag' && tag.code === 'I' && blockHasQuest;

      

      block.entries.push({

        code: tag.code || null,

        category: tag.category || null,

        name: tag.name,

        description: cleanSecondary || cleanDescFinal,

        extraTags: tag.extraTags || [],

        isPrimary: false,

        isReference: isSecRef,

        isQuestItem,

        linkedNames: [{ name: primaryTag.name, category: primaryTag.category, code: primaryTag.code }],

        locationTags: block.locationTags,

      });

    }

    

    results.push(block);

  }

  

  return results;

};



// Strip #X@Name markup from text, keeping just the name for display

const cleanTagMarkup = (text, tags) => {

  if (!text || !tags || tags.length === 0) return text;

  // Sort tags by position descending so replacements don't shift indices

  const sorted = [...tags].sort((a, b) => b.tagStart - a.tagStart);

  let result = text;

  for (const tag of sorted) {

    if (tag.type === 'ref' || tag.type === 'todo') continue; // Skip refs and todos

    // Try to find this tag pattern in the text (supports #X@, @X@, #X#)

    const patterns = [`#${tag.code}@`, `@${tag.code}@`, `#${tag.code}#`];

    let relStart = -1;

    for (const p of patterns) {

      relStart = result.indexOf(p);

      if (relStart !== -1) break;

    }

    if (relStart === -1) continue;

    // Find the full tag extent in this text

    const localTag = parseTag(result, relStart, ' - ');

    if (localTag) {

      // Hidden (##): remove entire tag+name; Visible (@@, #@): replace with name

      const replacement = localTag.tagVisibility === 'hidden' ? '' : localTag.name;

      result = result.substring(0, relStart) + replacement + result.substring(localTag.tagEnd);

    }

  }

  // Clean up double spaces from removals

  return result.replace(/  +/g, ' ');

};



// Strip @name references from text, leaving just the name

const cleanRefMarkup = (text) => {

  if (!text) return text;

  // 2026-04-27: visibility swapped per user request, then 2026-04-27 (later):

  //   #refs    → name VISIBLE in rendered text (keep, single hash = show)

  //   ##refs   → name HIDDEN in rendered text (strip, double hash = hide)

  //   @refs    → name HIDDEN (legacy hidden marker)

  // ORDER MATTERS: the ## patterns must run before the # patterns, because

  // a single-# regex would otherwise match the second # of a ## sequence

  // (the first # is preceded by whitespace; the second # is preceded by #,

  // which is fine for a regex anchored on whitespace-or-start).



  // Handle quoted refs: ##"Name" → (removed), #"Name" → Name (visible),

  // @"Name" → (removed). ## must run first.

  let result = text.replace(/##["“”]([^"“”]+)["“”]/g, '');

  result = result.replace(/#["“”]([^"“”]+)["“”]/g, '$1');

  result = result.replace(/@["“”]([^"“”]+)["“”]/g, '');



  // Handle tick-delimited refs: ##name' / #name' / @name'.

  result = result.replace(/(^|[\s])##([^\s'’‘]+)['’‘]/g, '$1');

  result = result.replace(/(^|[\s])#([^\s'’‘#]+)['’‘]/g, '$1$2 ');

  result = result.replace(/(^|[\s])@([^\s'’‘]+)['’‘]/g, '$1');



  // Handle dash-delimited refs: ##name - / #name - / @name -.

  result = result.replace(/(^|[\s])##([^\s'#@]+) - /g, '$1');

  result = result.replace(/(^|[\s])#([^\s'#@]+) - /g, '$1$2 ');

  result = result.replace(/(^|[\s])@([^\s'#@]+) - /g, '$1');



  // Handle standalone refs: ##name (hide) / #name (show) / @name (hide).

  // The visible-# regex excludes # from the name class so it can't eat

  // the second # of a leftover ## that earlier patterns missed.

  result = result.replace(/(^|[\s])##([^\s'#@]+)/g, '$1');

  result = result.replace(/(^|[\s])#([^\s'#@]+)/g, '$1$2');

  result = result.replace(/(^|[\s])@([^\s']+)/g, '$1');



  result = result.replace(/  +/g, ' ').trim();

  return result;

};



// ---- Colorizer: produces spans for the overlay display ----

const colorizeQuickAddText = (text, settings = DEFAULT_QUICKADD_SETTINGS) => {

  if (!text) return [];

  

  const delimiter = settings.delimiter || ' - ';

  const blocks = text.split(/(\n\n+)/);

  const spans = [];

  let globalPos = 0;

  

  for (const blockText of blocks) {

    // Double-newline separators rendered as plain text

    if (/^\n\n+$/.test(blockText)) {

      spans.push({ text: blockText, color: null, type: 'break' });

      globalPos += blockText.length;

      continue;

    }

    

    const tags = findAllTags(blockText, delimiter);

    

    if (tags.length === 0) {

      // No tags: session text (default color)

      spans.push({ text: blockText, color: '#e8d5b7', type: 'session' });

      globalPos += blockText.length;

      continue;

    }

    

    // Walk through the block text and assign colors

    let pos = 0;

    const entryTags = tags.filter(t => t.code !== 'L');

    const primaryTag = entryTags[0] || tags[0];

    const primaryColor = JOURNAL_TAG_CODES[primaryTag.code]?.color || '#e8d5b7';

    

    for (const tag of tags) {

      const tagColor = JOURNAL_TAG_CODES[tag.code]?.color || '#e8d5b7';

      

      // Text before this tag

      if (tag.tagStart > pos) {

        const beforeText = blockText.substring(pos, tag.tagStart);

        // This text belongs to the closest preceding entry tag

        const ownerColor = entryTags.find(t => t.tagStart < tag.tagStart) 

          ? JOURNAL_TAG_CODES[entryTags.filter(t => t.tagStart < tag.tagStart).pop()?.code]?.color 

          : null;

        spans.push({ 

          text: beforeText, 

          color: ownerColor ? `${ownerColor}88` : '#e8d5b7', 

          type: ownerColor ? 'owned' : 'session' 

        });

      }

      

      // The tag itself (#X@, @X@, #X#)

      spans.push({ text: `${tag.prefix || '#'}${tag.code}${tag.suffix || '@'}`, color: tagColor, type: 'tag', bold: true });

      

      // The name

      const nameText = blockText.substring(tag.nameStart, tag.nameEnd);

      spans.push({ text: nameText, color: tagColor, type: 'name', bold: true });

      

      pos = tag.tagEnd;

    }

    

    // Remaining text after last tag

    if (pos < blockText.length) {

      const lastEntryTag = entryTags[entryTags.length - 1];

      const ownerColor = lastEntryTag ? JOURNAL_TAG_CODES[lastEntryTag.code]?.color : null;

      

      let remaining = blockText.substring(pos);

      // Check if it starts with delimiter

      if (remaining.startsWith(delimiter)) {

        spans.push({ text: delimiter, color: '#666', type: 'delimiter' });

        remaining = remaining.substring(delimiter.length);

        pos += delimiter.length;

      }

      

      if (remaining) {

        spans.push({ 

          text: remaining, 

          color: ownerColor ? `${ownerColor}99` : '#e8d5b7', 

          type: 'owned' 

        });

      }

    }

    

    globalPos += blockText.length;

  }

  

  return spans;

};



// ---- Quick-Add Input Component with inline color coding ----

const QuickAddInput = ({ value, onChange, onSubmit, onCancel, settings, placeholder, autoFocus, entries: journalEntries, focusRef, onEditorFocus, onAutocompleteSearch }) => {

  const qaSettings = { ...DEFAULT_QUICKADD_SETTINGS, ...settings };

  const editorRef = React.useRef(null);

  const isComposingRef = React.useRef(false); // Track IME composition

  const lastPlainTextRef = React.useRef(value || '');

  

  // Expose focus method to parent

  React.useImperativeHandle(focusRef, () => ({

    focus: () => { if (editorRef.current) editorRef.current.focus(); }

  }));

  

  // Autocomplete state

  const [acResults, setAcResults] = React.useState([]);

  const [acIndex, setAcIndex] = React.useState(0);

  const [acPrefix, setAcPrefix] = React.useState(''); // '@' or '#'

  const [acQuery, setAcQuery] = React.useState('');

  const [acStartPos, setAcStartPos] = React.useState(-1); // char position where ref starts

  const acSearchSeqRef = React.useRef(0);

  const acSearchTimerRef = React.useRef(null);

  const acContextRef = React.useRef({ prefix: '', query: '', startPos: -1 });

  const clearAutocompleteSearchTimer = () => {
    if (acSearchTimerRef.current) {
      clearTimeout(acSearchTimerRef.current);
      acSearchTimerRef.current = null;
    }
  };

  const normalizeAutocompleteResults = (entries) => (
    (Array.isArray(entries) ? entries : [])
      .map(entry => entry?.entry || entry)
      .filter(entry => entry?.id && entry?.name && entry?.category && entry.category !== 'session')
      .map(entry => ({ id: entry.id, name: entry.name, category: entry.category }))
  );

  const closeAutocomplete = () => {
    acSearchSeqRef.current += 1;
    clearAutocompleteSearchTimer();
    setAcResults([]);
  };

  React.useEffect(() => () => {
    acSearchSeqRef.current += 1;
    clearAutocompleteSearchTimer();
  }, []);

  

  // Autocomplete: detect if cursor is inside a @ref or #ref

  const checkAutocomplete = () => {

    const localJournalEntries = Array.isArray(journalEntries) ? journalEntries : [];

    if (!editorRef.current || (!localJournalEntries.length && typeof onAutocompleteSearch !== 'function')) {

      closeAutocomplete();

      return;

    }

    const plainText = getPlainText();

    const cursorPos = getCursorOffset();

    

    // Walk backwards from cursor to find @ or # prefix

    let prefixPos = -1;

    let prefix = '';

    for (let j = cursorPos - 1; j >= 0; j--) {

      const ch = plainText[j];

      if (ch === '@' || ch === '#') {

        // Check it's not part of #X@ tag pattern

        if (ch === '#') {

          const after = plainText.substring(j);

          if (/^[#@][A-Z]{1,2}[#@]/.test(after) || /^#todo\b/i.test(after)) break;

        }

        // Must be at start or after whitespace

        if (j === 0 || /\s/.test(plainText[j - 1])) {

          prefixPos = j;

          prefix = ch;

        }

        break;

      }

      // Stop if we hit whitespace, newline, or tick delimiter (name already ended)

      if (/[\s'\n]/.test(ch)) break;

    }

    

    if (prefixPos === -1) {

      closeAutocomplete();

      return;

    }

    

    const query = plainText.substring(prefixPos + 1, cursorPos).toLowerCase();

    setAcPrefix(prefix);

    setAcQuery(query);

    setAcStartPos(prefixPos);

    acContextRef.current = { prefix, query, startPos: prefixPos };

    

    if (!query) {

      acSearchSeqRef.current += 1;

      clearAutocompleteSearchTimer();

      // Show all entries (capped) when just @ or # typed

      const results = localJournalEntries

        .filter(e => e.category !== 'session' && e.name)

        .slice(0, 8)

        .map(e => ({ id: e.id, name: e.name, category: e.category }));

      setAcResults(results);

      setAcIndex(0);

      return;

    }

    

    // Filter entries by name match

    const results = localJournalEntries

      .filter(e => e.category !== 'session' && e.name && e.name.toLowerCase().includes(query))

      .sort((a, b) => {

        // Prioritize starts-with matches

        const aStarts = a.name.toLowerCase().startsWith(query) ? 0 : 1;

        const bStarts = b.name.toLowerCase().startsWith(query) ? 0 : 1;

        return aStarts - bStarts || a.name.localeCompare(b.name);

      })

      .slice(0, 8)

      .map(e => ({ id: e.id, name: e.name, category: e.category }));

    

    setAcResults(results);

    setAcIndex(0);

    if (typeof onAutocompleteSearch === 'function' && query.trim().length >= 3) {

      const requestId = acSearchSeqRef.current + 1;

      acSearchSeqRef.current = requestId;

      clearAutocompleteSearchTimer();

      acSearchTimerRef.current = setTimeout(async () => {

        try {

          const remoteEntries = await onAutocompleteSearch({ query, prefix, limit: 8 });

          if (acSearchSeqRef.current !== requestId) return;

          const context = acContextRef.current || {};

          if (context.query !== query || context.prefix !== prefix || context.startPos !== prefixPos) return;

          const remoteResults = normalizeAutocompleteResults(remoteEntries);

          if (remoteResults.length === 0) return;

          setAcResults(prev => {

            const merged = new Map();

            remoteResults.forEach(entry => {

              if (!merged.has(entry.id)) merged.set(entry.id, entry);

            });

            normalizeAutocompleteResults(prev).forEach(entry => {

              if (!merged.has(entry.id)) merged.set(entry.id, entry);

            });

            return Array.from(merged.values()).slice(0, 8);

          });

          setAcIndex(0);

        } catch (error) {

          if (acSearchSeqRef.current === requestId) {

            console.error('Quick Journal autocomplete discovery failed:', error);

          }

        }

      }, 180);

    } else {

      acSearchSeqRef.current += 1;

      clearAutocompleteSearchTimer();

    }

  };

  

  // Insert autocomplete selection. Clears any pending colorize timer so a stale rebuild

  // doesn't fire on top of ours and drift the cursor.

  const insertAutocomplete = (entry) => {

    if (!editorRef.current) return;

    if (editorRef.current._colorizeTimer) {

      clearTimeout(editorRef.current._colorizeTimer);

      editorRef.current._colorizeTimer = null;

    }

    const plainText = getPlainText();

    const cursorPos = getCursorOffset();



    // Build the replacement: name with configured delimiter

    const hasSpaces = entry.name.includes(' ');

    const insertName = hasSpaces ? `"${entry.name}"` : entry.name;

    const acDelim = qaSettings.autocompleteDelimiter === 'dash' ? (qaSettings.delimiter || ' - ') : "'";

    const replacement = `${acPrefix}${insertName}${acDelim}`;



    // Replace from prefix position to cursor

    const before = plainText.substring(0, acStartPos);

    const after = plainText.substring(cursorPos);

    const newText = before + replacement + after;



    editorRef.current.innerHTML = buildColoredHTML(newText);

    lastPlainTextRef.current = newText;

    onChange(newText);

    setCursorOffset(acStartPos + replacement.length);

    closeAutocomplete();

  };

  

  // All three of these functions (getPlainText, getCursorOffset, setCursorOffset) must agree

  // on how DOM structure maps to character offsets — otherwise cursor drifts after rebuilds.

  // The shared model: a BR element represents exactly one '\n' character; a DIV whose only

  // child is a BR (Chrome's idiom for "one Shift+Enter in an empty paragraph") also counts

  // as one '\n' — NOT two. A DIV containing real content contributes one '\n' before its

  // content (as a block-level separator). A BR with `data-placeholder="true"` is IGNORED

  // entirely — it's a visual-only marker that makes its preceding BR render in Chrome.



  const isPlaceholderBr = (node) =>

    node.nodeName === 'BR' &&

    node.getAttribute &&

    node.getAttribute('data-placeholder') === 'true';



  // Returns true if the DIV should be treated as a lone-BR wrapper (one newline, skip recursion).

  const isLoneBrDiv = (node) =>

    node.nodeName === 'DIV' &&

    node.childNodes.length === 1 &&

    node.firstChild.nodeName === 'BR';



  // Get cursor offset as character count from start, counting BRs and DIV-newlines so the

  // result matches getPlainText's view of the text.

  const getCursorOffset = () => {

    const sel = window.getSelection();

    if (!sel.rangeCount || !editorRef.current) return 0;

    const range = sel.getRangeAt(0);

    const stopNode = range.startContainer;

    const stopOffset = range.startOffset;



    let charCount = 0;

    let done = false;



    const walk = (node) => {

      if (done) return;

      // If this IS the node the cursor is in, stop at the caret position.

      if (node === stopNode) {

        if (node.nodeType === 3) {

          charCount += stopOffset;

        } else {

          // Caret is between children at index `stopOffset` — count children up to that index

          for (let i = 0; i < stopOffset && i < node.childNodes.length; i++) {

            walk(node.childNodes[i]);

            if (done) return;

          }

        }

        done = true;

        return;

      }

      if (node.nodeType === 3) {

        charCount += node.textContent.length;

      } else if (isPlaceholderBr(node)) {

        // placeholder BR — visual-only, doesn't count

      } else if (node.nodeName === 'BR') {

        charCount += 1; // BR = '\n'

      } else if (isLoneBrDiv(node)) {

        charCount += 1; // lone-BR div (Chrome's Shift+Enter) = one '\n'

      } else if (node.nodeName === 'DIV' || node.nodeName === 'P') {

        // Block element: contribute leading '\n' if we've already emitted content that

        // didn't end with one. (Consistent with getPlainText.)

        if (charCount > 0) charCount += 1;

        for (const child of node.childNodes) {

          walk(child);

          if (done) return;

        }

      } else {

        for (const child of node.childNodes) {

          walk(child);

          if (done) return;

        }

      }

    };



    walk(editorRef.current);

    return charCount;

  };



  // Restore cursor to character offset (matches getCursorOffset's char model exactly).

  const setCursorOffset = (offset) => {

    const el = editorRef.current;

    if (!el) return;

    const sel = window.getSelection();

    const range = document.createRange();



    let charCount = 0;

    let found = false;



    const walk = (node) => {

      if (found) return;

      if (node.nodeType === 3) {

        const len = node.textContent.length;

        if (charCount + len >= offset) {

          range.setStart(node, offset - charCount);

          range.collapse(true);

          found = true;

          return;

        }

        charCount += len;

        return;

      }

      if (isPlaceholderBr(node)) {

        // placeholder BR — doesn't contribute to char offset, skip

        return;

      }

      if (node.nodeName === 'BR') {

        // BR contributes 1 char. If the offset falls on this BR, place caret right after it.

        if (charCount + 1 >= offset) {

          const parent = node.parentNode;

          const idx = Array.from(parent.childNodes).indexOf(node);

          // offset === charCount means caret BEFORE the BR; offset === charCount+1 means AFTER.

          range.setStart(parent, offset === charCount ? idx : idx + 1);

          range.collapse(true);

          found = true;

          return;

        }

        charCount += 1;

        return;

      }

      if (isLoneBrDiv(node)) {

        // One newline. Place caret inside this DIV if the offset falls here.

        if (charCount + 1 >= offset) {

          range.setStart(node, 0);

          range.collapse(true);

          found = true;

          return;

        }

        charCount += 1;

        return;

      }

      if (node.nodeName === 'DIV' || node.nodeName === 'P') {

        if (charCount > 0) {

          // Leading newline. If the offset lands exactly on this newline, place caret at

          // start of the block.

          if (charCount + 1 >= offset && charCount + 1 > offset) {

            range.setStart(node, 0);

            range.collapse(true);

            found = true;

            return;

          }

          charCount += 1;

        }

        for (const child of node.childNodes) {

          walk(child);

          if (found) return;

        }

        return;

      }

      for (const child of node.childNodes) {

        walk(child);

        if (found) return;

      }

    };



    walk(el);

    if (!found) {

      range.selectNodeContents(el);

      range.collapse(false);

    }

    sel.removeAllRanges();

    sel.addRange(range);

  };

  

  // Build colored HTML from plain text

  const buildColoredHTML = (text) => {

    if (!text) return '';

    const delimiter = qaSettings.delimiter || ' - ';

    const tags = findAllTags(text, delimiter);

    

    // Trailing <br> doesn't render without a following sibling in Chrome — append a

    // placeholder BR so a trailing newline is visible. getPlainText skips these.

    const trailingPlaceholder = text.endsWith('\n') ? '<br data-placeholder="true">' : '';

    if (tags.length === 0) {

      return escapeHtml(text).replace(/\n/g, '<br>') + trailingPlaceholder;

    }

    

    let html = '';

    let pos = 0;

    

    // Determine which entry tag owns each region

    const entryTags = tags.filter(t => (t.type === 'tag' && t.code !== 'L') || t.type === 'ref');

    

    for (const tag of tags) {

      // Text before this tag

      if (tag.tagStart > pos) {

        const before = text.substring(pos, tag.tagStart);

        const owner = entryTags.filter(t => t.tagEnd <= tag.tagStart).pop();

        const ownerColor = owner ? (JOURNAL_TAG_CODES[owner.code]?.color || '#c9a227') : null;

        html += `<span style="color:${ownerColor ? ownerColor + '99' : '#e8d5b7'}">${escapeHtml(before).replace(/\n/g, '<br>')}</span>`;

      }

      

      if (tag.type === 'todo') {

        // #todo/@todo in green

        html += `<span style="color:#27c95a;font-weight:bold">${tag.prefix || '#'}todo</span>`;

        html += `<span style="color:#27c95a99"> ${escapeHtml(tag.text)}</span>`;

        // Show tick if present at end

        if (tag.tagEnd > 0 && text[tag.tagEnd - 1] === "'") {

          html += `<span style="color:#666">'</span>`;

        }

      } else if (tag.type === 'ref') {

        // 2026-04-27: visible = '#', hidden = '##' or '@'.

        const isHidden = tag.prefix === '##' || tag.prefix === '@';

        const refColor = isHidden ? '#888' : '#c9a227';

        const refStyle = isHidden 

          ? `color:${refColor};font-style:italic;opacity:0.5` 

          : `color:${refColor};font-weight:bold`;

        html += `<span style="${refStyle}">${tag.prefix || '@'}</span>`;

        const nameText = text.substring(tag.nameStart, tag.nameEnd);

        html += `<span style="${refStyle}">${escapeHtml(nameText)}</span>`;

        // Show tick delimiter if present

        if (tag.tagEnd > tag.nameEnd) {

          html += `<span style="color:#666">'</span>`;

        }

      } else if (tag.type === 'tag') {

        const info = JOURNAL_TAG_CODES[tag.code];

        if (info) {

          const tagPrefix = tag.prefix || '#';

          const tagSuffix = tag.suffix || '@';

          const isHidden = tag.tagVisibility === 'hidden';

          html += `<span style="color:${info.color};font-weight:bold">${tagPrefix}${tag.code}${tagSuffix}</span>`;

          const nameText = text.substring(tag.nameStart, tag.nameEnd);

          html += `<span style="color:${info.color};font-weight:bold${isHidden ? ';opacity:0.5;font-style:italic' : ''}">${escapeHtml(nameText)}</span>`;

          // Show tick delimiter if present

          if (tag.hasTick) {

            html += `<span style="color:#666">'</span>`;

          }

        }

      }

      

      pos = tag.tagEnd;

    }

    

    // Text after last tag

    if (pos < text.length) {

      const remaining = text.substring(pos);

      const lastEntry = entryTags[entryTags.length - 1];

      const ownerColor = lastEntry ? (JOURNAL_TAG_CODES[lastEntry.code]?.color || '#c9a227') : null;

      

      if (remaining.startsWith(delimiter)) {

        html += `<span style="color:#666">${escapeHtml(delimiter)}</span>`;

        const afterDelim = remaining.substring(delimiter.length);

        html += `<span style="color:${ownerColor ? ownerColor + '99' : '#e8d5b7'}">${escapeHtml(afterDelim).replace(/\n/g, '<br>')}</span>`;

      } else {

        html += `<span style="color:${ownerColor ? ownerColor + '99' : '#e8d5b7'}">${escapeHtml(remaining).replace(/\n/g, '<br>')}</span>`;

      }

    }



    return html + trailingPlaceholder;

  };

  

  const escapeHtml = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  

  // Get plain text from editor. Must match getCursorOffset's character-counting model:

  //   - BR = '\n'

  //   - <div><br></div> (Chrome's Shift+Enter in an empty paragraph) = one '\n'

  //   - <div>content</div> = leading '\n' before content (block separator)

  // See comment above getCursorOffset for rationale.

  const getPlainText = () => {

    if (!editorRef.current) return '';

    let text = '';

    const walk = (node) => {

      if (node.nodeType === 3) {

        text += node.textContent;

      } else if (isPlaceholderBr(node)) {

        // placeholder BR — visual-only, skip

      } else if (node.nodeName === 'BR') {

        text += '\n';

      } else if (isLoneBrDiv(node)) {

        text += '\n';

      } else if (node.nodeName === 'DIV' || node.nodeName === 'P') {

        if (text.length > 0 && !text.endsWith('\n')) text += '\n';

        for (const child of node.childNodes) walk(child);

      } else {

        for (const child of node.childNodes) walk(child);

      }

    };

    walk(editorRef.current);

    return text;

  };

  

  // Colorize the editor content. Called on blur, on Tab/commit events, and when the user

  // just typed a tag-closing delimiter. Rebuilds innerHTML from plain text with colored

  // spans, then restores the cursor. Skipped during IME composition and when no tag

  // markers are present (nothing to color; leaving DOM alone avoids unnecessary cursor moves).

  const colorize = () => {

    if (!editorRef.current || isComposingRef.current) return;

    if (editorRef.current._colorizeTimer) {

      clearTimeout(editorRef.current._colorizeTimer);

      editorRef.current._colorizeTimer = null;

    }

    const plainText = getPlainText();

    lastPlainTextRef.current = plainText;



    // No tags → no rebuild needed.

    if (!/[#@][A-Z]{1,2}[#@]/.test(plainText) && !/@\S/.test(plainText) && !/#todo\b/i.test(plainText) && !/#[a-zA-Z]/.test(plainText)) return;



    const cursorPos = getCursorOffset();

    const newHtml = buildColoredHTML(plainText);

    // Cheap no-op guard: skip DOM write if the HTML is already identical.

    if (editorRef.current.innerHTML === newHtml) return;

    editorRef.current.innerHTML = newHtml;

    setCursorOffset(cursorPos);

  };

  

  // Clear any pending colorize timer. Called before any explicit innerHTML manipulation so a

  // stale debounce doesn't fire on top of a fresh rebuild and clobber the cursor.

  const clearColorizeTimer = () => {

    if (editorRef.current && editorRef.current._colorizeTimer) {

      clearTimeout(editorRef.current._colorizeTimer);

      editorRef.current._colorizeTimer = null;

    }

  };



  // Handle input events. We NO LONGER debounce-colorize on every keystroke — that pattern

  // rebuilt innerHTML 300ms after each input and stole the cursor mid-typing. Instead:

  //   - Text state updates on every input (onChange fires, autocomplete refreshes)

  //   - Colorization runs on blur, on Tab/Enter commits, and when the user types a delimiter

  //     that completes a tag (e.g. closing tick `'` or a whitespace after a ref)

  // This eliminates cursor-jumps in the typing path without giving up live colorization.

  const handleInput = () => {

    if (isComposingRef.current) return;

    const plainText = getPlainText();

    const prev = lastPlainTextRef.current || '';

    lastPlainTextRef.current = plainText;

    onChange(plainText);

    checkAutocomplete();



    // Colorize only when the user just typed a tag-ending character — this signals a

    // complete token and is a safe moment to rebuild. We still skip rebuild during IME

    // composition and when no tag markers are present.

    const lastChar = plainText.slice(-1);

    const typedEndMarker = plainText.length > prev.length && (lastChar === "'" || lastChar === '`' || lastChar === ' ' || lastChar === '\n');

    const hasTagMarkers = /[#@][A-Z]{1,2}[#@]|@\S|#todo\b|#[a-zA-Z]/.test(plainText);

    if (typedEndMarker && hasTagMarkers) {

      clearColorizeTimer();

      // Run synchronously — if we defer, the next keystroke may arrive before we rebuild

      // and we're back to the cursor-theft pattern.

      colorize();

    }

  };



  // Insert text at cursor position. When the text contains no tag markers we insert via the

  // Selection API (no innerHTML rebuild, no cursor walk — much cheaper and drift-free). When

  // tags exist we fall back to the full rebuild + setCursorOffset path.

  const insertTextAtCursor = (insertStr) => {

    if (!editorRef.current) return;

    clearColorizeTimer();

    const plainText = getPlainText();

    const cursorPos = getCursorOffset();

    const newText = plainText.substring(0, cursorPos) + insertStr + plainText.substring(cursorPos);

    const needsRebuild = /[#@][A-Z]{1,2}[#@]|@\S|#todo\b|#[a-zA-Z]/.test(newText);



    if (needsRebuild) {

      editorRef.current.innerHTML = buildColoredHTML(newText);

      lastPlainTextRef.current = newText;

      onChange(newText);

      setCursorOffset(cursorPos + insertStr.length);

      return;

    }



    // Fast path: no tags. Use Selection API to insert in place without DOM rebuild.

    const sel = window.getSelection();

    if (!sel.rangeCount) {

      editorRef.current.innerHTML = escapeHtml(newText).replace(/\n/g, '<br>');

      lastPlainTextRef.current = newText;

      onChange(newText);

      setCursorOffset(cursorPos + insertStr.length);

      return;

    }

    const range = sel.getRangeAt(0);

    range.deleteContents();



    // A trailing <br> at the end of contentEditable DOESN'T render in Chrome/Safari unless

    // it has a following sibling. So a single Shift+Enter at the end produces "hello<br>"

    // which visually looks unchanged — the user has to press Shift+Enter AGAIN to get the

    // newline to appear. Fix: when we're inserting a newline at the end of the editor, also

    // append a placeholder <br data-placeholder="true"> so the real BR has something after

    // it and renders. getPlainText / getCursorOffset / setCursorOffset skip placeholder BRs

    // so they don't count toward the text model.

    const isAtEnd = (() => {

      const tail = document.createRange();

      tail.selectNodeContents(editorRef.current);

      tail.setStart(range.endContainer, range.endOffset);

      return tail.toString() === '';

    })();



    // Split insertStr on \n — each \n becomes a BR, each run of text becomes a TextNode.

    const parts = insertStr.split('\n');

    const frag = document.createDocumentFragment();

    parts.forEach((part, i) => {

      if (part.length > 0) frag.appendChild(document.createTextNode(part));

      if (i < parts.length - 1) frag.appendChild(document.createElement('br'));

    });



    // If we're inserting at end and the text ends with a newline, the final BR needs a

    // visible sibling. Append a placeholder BR.

    let placeholderBr = null;

    if (isAtEnd && insertStr.endsWith('\n')) {

      placeholderBr = document.createElement('br');

      placeholderBr.setAttribute('data-placeholder', 'true');

      frag.appendChild(placeholderBr);

    }



    // Cursor target: the last REAL inserted node (not the placeholder).

    const cursorTarget = placeholderBr ? placeholderBr.previousSibling : frag.lastChild;

    range.insertNode(frag);

    if (cursorTarget) {

      const r = document.createRange();

      r.setStartAfter(cursorTarget);

      r.collapse(true);

      sel.removeAllRanges();

      sel.addRange(r);

    }

    lastPlainTextRef.current = newText;

    onChange(newText);

  };



  // Handle keydown

  const handleKeyDown = (e) => {

    // Autocomplete navigation

    if (acResults.length > 0) {

      if (e.key === 'ArrowDown') {

        e.preventDefault();

        setAcIndex(prev => Math.min(prev + 1, acResults.length - 1));

        return;

      }

      if (e.key === 'ArrowUp') {

        e.preventDefault();

        setAcIndex(prev => Math.max(prev - 1, 0));

        return;

      }

      if (e.key === 'Tab') {

        e.preventDefault();

        insertAutocomplete(acResults[acIndex]);

        return;

      }

      if (e.key === 'Enter' && !e.shiftKey) {

        e.preventDefault();

        insertAutocomplete(acResults[acIndex]);

        return;

      }

      if (e.key === 'Escape') {

        e.preventDefault();

        closeAutocomplete();

        return;

      }

    }



    // Shift+Enter: insert a plain '\n'. Intercepted explicitly so we don't inherit

    // browser-specific behavior (Chrome: <div><br></div>, Firefox: <br>, Safari: <br><br>).

    // The insertTextAtCursor fast path produces a clean BR and consistent cursor placement.

    if ((e.key === 'Enter' || e.keyCode === 13) && e.shiftKey) {

      e.preventDefault();

      e.stopPropagation();

      insertTextAtCursor('\n');

      return;

    }



    // Enter (no shift): submit

    if ((e.key === 'Enter' || e.keyCode === 13) && !e.shiftKey) {

      e.preventDefault();

      e.stopPropagation();

      const text = getPlainText();

      if (text.trim()) onSubmit(text);

      return;

    }

    if (e.key === 'Tab') {

      e.preventDefault();

      insertTextAtCursor(qaSettings.tabDelimiter === 'dash' ? (qaSettings.delimiter || ' - ') : "'");

      // Autocomplete should close now that the tag is delimited.

      closeAutocomplete();

      return;

    }

    if (e.key === 'Escape') {

      onCancel();

      return;

    }

  };

  

  // Handle paste - strip formatting

  const handlePaste = (e) => {

    e.preventDefault();

    const text = e.clipboardData.getData('text/plain');

    insertTextAtCursor(text);

  };

  

  // Initialize with value

  React.useEffect(() => {

    if (editorRef.current && value !== undefined) {

      const currentText = getPlainText();

      if (currentText !== value) {

        editorRef.current.innerHTML = value ? buildColoredHTML(value) : '';

        lastPlainTextRef.current = value;

      }

    }

  }, []);

  

  // When value is cleared externally (after submit)

  React.useEffect(() => {

    if (value === '' && editorRef.current && getPlainText() !== '') {

      editorRef.current.innerHTML = '';

      lastPlainTextRef.current = '';

      closeAutocomplete();

    }

  }, [value]);

  

  // Auto-focus

  React.useEffect(() => {

    if (autoFocus && editorRef.current) {

      editorRef.current.focus();

    }

  }, [autoFocus]);

  

  // Parse tags for chip preview

  const tags = value ? findAllTags(value, qaSettings.delimiter) : [];

  

  return (

    <div style={{ width: '100%', position: 'relative' }}>

      {/* ContentEditable editor */}

      <div

        ref={editorRef}

        contentEditable

        suppressContentEditableWarning

        onInput={handleInput}

        onKeyDown={handleKeyDown}

        onPaste={handlePaste}

        onCompositionStart={() => { isComposingRef.current = true; }}

        onCompositionEnd={() => { isComposingRef.current = false; handleInput(); }}

        onBlur={colorize}

        onFocus={onEditorFocus}

        data-placeholder={placeholder || 'Quick add... (use #N@ #M@ #I@ #Q@ tags)'}

        style={{

          width: '100%',

          minHeight: '60px',

          maxHeight: '40vh',

          padding: '8px 12px',

          fontFamily: 'inherit',

          fontSize: '0.9rem',

          lineHeight: '1.5',

          background: 'rgba(0,0,0,0.3)',

          border: '1px solid rgba(201,162,39,0.3)',

          borderRadius: '6px',

          color: '#e8d5b7',

          outline: 'none',

          overflowY: 'auto',

          whiteSpace: 'pre-wrap',

          wordBreak: 'break-word',

          boxSizing: 'border-box',

          position: 'relative',

        }}

      />

      

      {/* Autocomplete dropdown */}

      {acResults.length > 0 && (

        <div style={{

          position: 'absolute',

          left: 0,

          right: 0,

          marginTop: '2px',

          background: '#1a1a2e',

          border: '1px solid rgba(201,162,39,0.4)',

          borderRadius: '6px',

          maxHeight: '200px',

          overflowY: 'auto',

          zIndex: 1000,

          boxShadow: '0 4px 12px rgba(0,0,0,0.5)',

        }}>

          {acResults.map((entry, idx) => {

            const catInfo = JOURNAL_CATEGORIES[entry.category];

            const catColor = catInfo?.color || '#888';

            return (

              <div

                key={entry.id}

                onMouseDown={(e) => { e.preventDefault(); insertAutocomplete(entry); }}

                onMouseEnter={() => setAcIndex(idx)}

                style={{

                  padding: '6px 10px',

                  display: 'flex',

                  alignItems: 'center',

                  gap: '8px',

                  cursor: 'pointer',

                  background: idx === acIndex ? 'rgba(201,162,39,0.15)' : 'transparent',

                  borderLeft: idx === acIndex ? '3px solid #c9a227' : '3px solid transparent',

                  fontSize: '0.85rem',

                }}

              >

                <span style={{ fontSize: '0.9rem' }}>{catInfo?.icon || '📄'}</span>

                <span style={{ color: '#e8d5b7', flex: 1 }}>{entry.name}</span>

                <span style={{ color: catColor, fontSize: '0.7rem', opacity: 0.7 }}>{catInfo?.name || entry.category}</span>

              </div>

            );

          })}

        </div>

      )}

      

      {/* CSS for placeholder */}

      <style>{`

        [data-placeholder]:empty:before {

          content: attr(data-placeholder);

          color: #666;

          pointer-events: none;

        }

      `}</style>

      

      {/* Tag chips preview */}

      {tags.length > 0 && (

        <div style={{ 

          marginTop: '6px', 

          display: 'flex', 

          flexWrap: 'wrap', 

          gap: '4px', 

          alignItems: 'center',

        }}>

          {tags.map((tag, i) => {

            if (tag.type === 'todo') {

              return (

                <span key={i} style={{ padding: '2px 8px', background: 'rgba(39,201,90,0.15)', border: '1px solid rgba(39,201,90,0.4)', borderRadius: '4px', fontSize: '0.72rem', color: '#27c95a', display: 'flex', alignItems: 'center', gap: '4px' }}>

                  <span style={{ fontWeight: 'bold' }}>☐</span>

                  <span>{tag.text}</span>

                  {tag.prefix === '@' && <span style={{ opacity: 0.5, fontSize: '0.6rem' }} title="Also appears in session">📋</span>}

                </span>

              );

            }

            if (tag.type === 'ref') {

              // 2026-04-27: visible = '#', hidden = '##' or '@'.

              const isHidden = tag.prefix === '##' || tag.prefix === '@';

              return (

                <span key={i} style={{ 

                  padding: '2px 8px', 

                  background: isHidden ? 'rgba(136,136,136,0.15)' : 'rgba(201,162,39,0.15)', 

                  border: `1px solid ${isHidden ? 'rgba(136,136,136,0.4)' : 'rgba(201,162,39,0.4)'}`, 

                  borderRadius: '4px', fontSize: '0.72rem', 

                  color: isHidden ? '#888' : '#c9a227', 

                  display: 'flex', alignItems: 'center', gap: '4px',

                  opacity: isHidden ? 0.6 : 1,

                }}>

                  <span style={{ fontWeight: 'bold' }}>{tag.prefix || '@'}</span>

                  <span>{tag.name || '...'}</span>

                  <span style={{ opacity: 0.6 }}>{isHidden ? '👻' : '🔗'}</span>

                </span>

              );

            }

            const info = JOURNAL_TAG_CODES[tag.code];

            if (!info) return null;

            const isHiddenTag = tag.tagVisibility === 'hidden';

            return (

              <span key={i} style={{ padding: '2px 8px', background: `${info.color}20`, border: `1px solid ${info.color}50`, borderRadius: '4px', fontSize: '0.72rem', color: info.color, display: 'flex', alignItems: 'center', gap: '4px', opacity: isHiddenTag ? 0.7 : 1 }}>

                <span style={{ fontWeight: 'bold' }}>{tag.prefix || '#'}{tag.code}</span>

                <span style={isHiddenTag ? { fontStyle: 'italic', opacity: 0.6 } : {}}>{tag.name || '...'}</span>

                {tag.code === 'L' && <span style={{ opacity: 0.6 }}>📍</span>}

                {isHiddenTag && <span style={{ opacity: 0.5, fontSize: '0.6rem' }} title="Name hidden from text">👻</span>}

              </span>

            );

          })}

        </div>

      )}

      

      {/* Bottom hints */}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '0.65rem', color: '#666' }}>

        <span>Enter to submit • Shift+Enter new line • ⏎⏎ separate entries</span>

        <span>{tags.length > 0 ? `${tags.length} tag${tags.length !== 1 ? 's' : ''}` : ''}</span>

      </div>

    </div>

  );

};



// ---- Tag Cheat Sheet Component ----

const QuickAddCheatSheet = ({ show, onClose, settings }) => {

  if (!show) return null;

  const qaS = { ...DEFAULT_QUICKADD_SETTINGS, ...(settings || {}) };

  const delim = qaS.delimiter || ' - ';

  const customRules = qaS.tagVisibilityRules || {};

  const hashAtVis = customRules.hash_at || 'visible';

  const atAtVis = customRules.at_at || 'visible';

  const hashHashVis = customRules.hash_hash || 'hidden';

  

  return (

    <div style={{ 

      padding: '10px 14px', 

      background: 'rgba(0,0,0,0.4)', 

      border: '1px solid rgba(201,162,39,0.2)', 

      borderRadius: '6px', 

      marginTop: '8px',

      fontSize: '0.75rem',

      color: '#bbb',

      lineHeight: '1.6',

    }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>

        <strong style={{ color: '#c9a227' }}>📓 Quick Journal Help</strong>

        <span onClick={onClose} style={{ cursor: 'pointer', color: '#888' }}>✕</span>

      </div>

      

      {/* Tag codes */}

      <div style={{ fontSize: '0.68rem', color: '#c9a227', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Entry Tags</div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 16px', marginBottom: '8px' }}>

        {Object.entries(JOURNAL_TAG_CODES).map(([code, info]) => (

          <div key={code} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>

            <code style={{ color: info.color, fontWeight: 'bold', minWidth: '28px' }}>@{code}@</code>

            <span>{info.label}</span>

          </div>

        ))}

      </div>

      

      {/* Visibility rules */}

      {/* 2026-04-27: visibility help refreshed for the new prefix-driven

          grammar. # = visible, ## = hidden, @ = hidden (legacy). The

          customRules controls still feed in via hashAtVis etc.; their

          mapping to the new # / ## prefixes is via parseQuickAddInput's

          resolveVisibility (see App.js:3621). */}

      <div style={{ fontSize: '0.68rem', color: '#c9a227', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Visibility</div>

      <div style={{ marginBottom: '8px', fontSize: '0.7rem', lineHeight: '1.5', color: '#888' }}>

        <div><code style={{ color: '#27c95a' }}>#Name</code> or <code style={{ color: '#27c95a' }}>#N@Name</code> — 👁 name visible in text</div>

        <div><code style={{ color: '#c92727' }}>##Name</code> or <code style={{ color: '#c92727' }}>##N@Name</code> — 🔒 name hidden from text</div>

        <div><code style={{ color: '#c92727' }}>@Name</code> or <code style={{ color: '#c92727' }}>@N@Name</code> — 🔒 name hidden (legacy)</div>

      </div>



      {/* Syntax examples */}

      <div style={{ fontSize: '0.68rem', color: '#c9a227', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Syntax</div>

      <div style={{ fontSize: '0.7rem', lineHeight: '1.5', color: '#888' }}>

        <div><code style={{ color: '#c9a227' }}>#N@Viego{delim}</code>A mysterious stranger — NPC + description, name visible</div>

        <div><code style={{ color: '#c9a227' }}>##N@Viego{delim}</code>A mysterious stranger — same, but name hidden in rendered text</div>

        <div><code style={{ color: '#c9a227' }}>#viego</code> — reference existing entry (visible)</div>

        <div><code style={{ color: '#c9a227' }}>##viego</code> — reference existing entry (hidden)</div>

        <div><code style={{ color: '#c9a227' }}>#N@"Elder John"</code> — quote multi-word names</div>

        <div><code style={{ color: '#27c95a' }}>#todo find the key</code> — quest objective only</div>

        <div><code style={{ color: '#27c95a' }}>@todo find the key</code> — quest + session note</div>

        <div><code style={{ color: '#c9a227' }}>'</code> or <code style={{ color: '#c9a227' }}>{String.fromCharCode(96)}</code> — end name / paragraph scope (all text in block)</div>

        <div><code style={{ color: '#c9a227' }}>{delim}</code> — text delimiter (text after only)</div>

        <div>Double line break = separate entries</div>

        <div>Tab = accept autocomplete or insert delimiter</div>

      </div>

    </div>

  );

};



// Journal entry types

// Journal categories - order: session, quests, creatures, npcs, items, recipes, diseases, plants

const JOURNAL_CATEGORIES = {

  session: { 

    name: 'Sessions', 

    icon: '📖', 

    color: '#c9a227',

    description: 'Session Journal',

    fields: ['sessionNumber', 'sessionDate', 'summary', 'keyEvents', 'npcsEncountered', 'creaturesEncountered', 'itemsFound'],

    order: 0

  },

  quests: { 

    name: 'Quests', 

    icon: '📜', 

    color: '#9b59b6',

    description: 'Quest Log',

    fields: ['questGiver', 'status', 'priority', 'objectives', 'rewards', 'relatedNpcs', 'relatedLocations', 'relatedItems'],

    order: 1

  },

  creatures: { 

    name: 'Creatures', 

    icon: '🐾', 

    color: '#c92727',

    description: 'Creatures & Monsters',

    fields: ['type', 'size', 'cr', 'habitat', 'weaknesses', 'resistances', 'abilities', 'loot', 'encounters'],

    order: 2

  },

  npcs: { 

    name: 'NPCs', 

    icon: '👤', 

    color: '#4a90d9',

    description: 'NPCs & Characters',

    fields: ['title', 'location', 'faction', 'disposition', 'appearance', 'personality', 'quests', 'sells', 'lastSeen'],

    order: 3

  },

  items: { 

    name: 'Items', 

    icon: '🎒', 

    color: '#d97429',

    description: 'Items & Materials',

    fields: ['itemType', 'rarity', 'locations', 'soldAt', 'usedInRecipes', 'relatedQuests', 'effects'],

    order: 4

  },

  recipes: { 

    name: 'Recipes', 

    icon: '📋', 

    color: '#17a589',

    description: 'Crafting & Cooking',

    fields: ['recipeType', 'ingredients', 'result', 'effects', 'curesDiseases', 'craftingDC', 'craftingTime'],

    order: 5

  },

  diseases: { 

    name: 'Diseases', 

    icon: '☣', 

    color: '#8e44ad',

    description: 'Ailments & Cures',

    fields: ['symptoms', 'severity', 'transmission', 'locations', 'curedBy', 'effects', 'progression'],

    order: 6

  },

  plants: { 

    name: 'Plants', 

    icon: '🌿', 

    color: '#27c95a',

    description: 'Plants & Herbs',

    fields: ['plantType', 'rarity', 'habitat', 'effects', 'harvesting', 'usedInRecipes'],

    order: 7

  },

  locations: { 

    name: 'Locations', 

    icon: '📍', 

    color: '#e67e22',

    description: 'Places & Landmarks',

    fields: [],

    order: 8

  },

};



// Default category order (can be customized by user)

const DEFAULT_JOURNAL_ORDER = ['session', 'quests', 'creatures', 'npcs', 'items', 'recipes', 'diseases', 'plants', 'locations'];

// DM journal categories (extends player categories)

const DM_JOURNAL_CATEGORIES = {

  ...JOURNAL_CATEGORIES,

  players: { name: 'Players', icon: '🎮', color: '#e91e63', description: 'Player Notes (DM Only)', fields: ['preferences', 'playstyle', 'availability'], order: 10 },

  characters: { name: 'Characters', icon: '🧙', color: '#00bcd4', description: 'Character Notes (DM Only)', fields: ['arc', 'secrets', 'development'], order: 11 },

  encounters: { name: 'Encounters', icon: '⚔️', color: '#c92727', description: 'Encounter Plans', fields: ['terrain', 'tactics', 'difficulty'], order: 12 },

  worldbuilding: { name: 'World', icon: '🌍', color: '#2ecc71', description: 'World Building', fields: [], order: 13 },

};



const DM_JOURNAL_ORDER = [...DEFAULT_JOURNAL_ORDER, 'players', 'characters', 'encounters', 'worldbuilding'];



// ============== NPC / ENEMY LIBRARY ==============

const NPC_DISPOSITIONS = [

  { id: 'friendly', label: 'Friendly', name: 'Friendly', icon: '💚', color: '#27c95a' },

  { id: 'neutral', label: 'Neutral', name: 'Neutral', icon: '💛', color: '#c9a227' },

  { id: 'hostile', label: 'Hostile', name: 'Hostile', icon: '❤️', color: '#c92727' },

  { id: 'unknown', label: 'Unknown', name: 'Unknown', icon: '❓', color: '#888' },

];



const NPC_STATUSES = [

  { id: 'alive', label: 'Alive', color: '#27c95a' },

  { id: 'dead', label: 'Dead', color: '#c92727' },

  { id: 'missing', label: 'Missing', color: '#d97429' },

  { id: 'unknown', label: 'Unknown', color: '#888' },

];



const createNPC = (overrides = {}) => ({

  id: generateId(),

  name: '',

  title: '',  // e.g. "Captain of the Guard"

  disposition: 'neutral',  // friendly/hostile/neutral/unknown

  status: 'alive',

  // Optional stat block

  statBlock: null,  // { hp, maxHp, ac, str, dex, con, int, wis, cha, speed, cr, saves: {}, skills: {}, senses: '' }

  abilities: [],  // [{ id, name, description, recharge, damage }]

  // RP & Personality

  personality: '',

  ideals: '',

  bonds: '',

  flaws: '',

  appearance: '',

  voiceNotes: '',  // How to RP them

  // World

  affiliations: [],  // [{ name, role }]

  currentLocation: '',

  tags: [],

  campaigns: [],

  // Notes (rich text with @entity linking)

  notes: '',

  dmSecretNotes: '',  // Never revealed

  // Hostile-only

  lootTableId: null,

  xpReward: 0,

  // Visual

  spritePath: '',

  iconRef: null,  // RPG icon from pack

  // Metadata

  createdAt: new Date().toISOString(),

  updatedAt: new Date().toISOString(),

  revealItems: [],  // [{ id, text, field, visibility: 'dm-only'|'revealable'|'revealed', revealedAt }]

  cinematics: [],  // [{ id, title, sequence: [{ text, delay }], theme, imagePath, bgOpacity, font, transition, linkedEntries: [] }]

  encounterHistory: [],  // [{ sessionId, date, notes }]

  ...overrides,

});



const createStatBlock = () => ({

  hp: 10, maxHp: 10, ac: 10,

  str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10,

  speed: '30 ft.', cr: '0',

  saves: {}, skills: {}, senses: '', languages: '',

});



const REVEAL_FIELDS = [

  { id: 'profile', label: 'Profile', icon: '🎭', desc: 'Core identity, appearance, personality, backstory' },

  { id: 'session', label: 'Session Log', icon: '📖', desc: 'What happened during play' },

  { id: 'secrets', label: 'Secrets', icon: '🔒', desc: 'DM-only plans, true motives, hidden info' },

  { id: 'relationships', label: 'Relationships', icon: '🤝', desc: 'Ties to PCs, NPCs, and factions' },

  { id: 'development', label: 'Development', icon: '📈', desc: 'Post-session arc progression, status changes' },

  { id: 'inventory', label: 'Inventory', icon: '🎒', desc: 'Items, resources, property' },

  { id: 'hooks', label: 'Plot Hooks', icon: '🎣', desc: 'Story threads, quest connections, upcoming plans' },

];



// 2026-04-27: parse a DM-revealed NPC description back into per-section

// structure. The reveal flow joins each REVEAL_FIELDS section into one

// description string with "{icon} {Label}:\n{content}\n\n" separators

// (see the reveal callsite around App.js:41549). The player view used

// to render that whole blob under one "Session Notes:" label, which

// made section headers like "🎒 Inventory:" look like part of the

// session notes. This parser splits the description back into its

// per-section parts so the player view can render each as its own

// strong-label field.

//

// Returns { leading, sections: { [fieldId]: { fieldInfo, content } } }

// or null when no canonical markers were found (player-typed or

// legacy entries — caller falls back to a single-block render).

const parseRevealedDescription = (description) => {

  if (!description || typeof description !== 'string') return null;

  const markers = REVEAL_FIELDS.map(f => {

    const marker = `${f.icon} ${f.label}:`;

    const idx = description.indexOf(marker);

    return idx >= 0 ? { id: f.id, fieldInfo: f, marker, idx } : null;

  }).filter(Boolean).sort((a, b) => a.idx - b.idx);

  if (markers.length === 0) return null;

  const leading = description.substring(0, markers[0].idx).trim();

  const sections = {};

  markers.forEach((m, i) => {

    const start = m.idx + m.marker.length;

    const end = i + 1 < markers.length ? markers[i + 1].idx : description.length;

    sections[m.id] = { fieldInfo: m.fieldInfo, content: description.substring(start, end).trim() };

  });

  return { leading, sections };

};



const PLAYER_NOTE_CATEGORIES = [

  { id: 'notes', label: 'Notes', icon: '📝' },

  { id: 'profile', label: 'Info', icon: '🎭' },

  { id: 'encounters', label: 'Encounters', icon: '⚔️' },

  { id: 'personality', label: 'Personality', icon: '💬' },

  { id: 'quests', label: 'Quest', icon: '📜' },

];



const PLAYER_BLOCK_FIELDS = [

  { id: 'profile', label: 'Profile', icon: '🎭' },

  { id: 'quests', label: 'Quest', icon: '📜' },

  { id: 'story', label: 'Story', icon: '📈' },

  { id: 'personality', label: 'Personality', icon: '💬' },

  { id: 'affiliations', label: 'Affiliations', icon: '⚜️' },

];



const QUICK_NOTE_CATEGORIES = [

  { id: 'session', label: 'Session', icon: '📖' },

  { id: 'profile', label: 'Info', icon: '🎭' },

  { id: 'inventory', label: 'Inventory', icon: '🎒' },

  { id: 'development', label: 'Story', icon: '📈' },

];



const DEMO_NPCS = {

  npc_malcazar: createNPC({ id: 'npc_malcazar', name: 'Malcazar the Burnt', title: 'Infernal Merchant', disposition: 'neutral', status: 'alive', personality: 'Calculating and scarred. Speaks in riddles when nervous.', appearance: 'Heavily scarred face, one eye milky white. Wears charred leather.', voiceNotes: 'Low gravelly voice. Pauses before answering. Occasionally twitches.', currentLocation: 'The Last Shop Before Hell', affiliations: [{ name: 'Avernus Trading Co.', role: 'Owner' }], tags: ['merchant', 'infernal'], notes: 'Sells rare infernal supplies. Has connections in the Nine Hells.',

    revealItems: [

      { id: 'ri_m1', text: 'Heavily scarred face, one eye milky white. Wears charred leather armor.', field: 'profile', visibility: 'revealable' },

      { id: 'ri_m2', text: 'Polite and businesslike, but clearly hiding something beneath the surface.', field: 'profile', visibility: 'revealable' },

      { id: 'ri_m3', text: 'Was once a paladin who fell from grace after a deal with a pit fiend.', field: 'profile', visibility: 'revealable' },

      { id: 'ri_m4', text: 'Secretly practices infernal magic — can cast Hellish Rebuke at will.', field: 'profile', visibility: 'revealable' },

      { id: 'ri_m5', text: 'Will betray the party to Zariel if the price is right. His loyalty is to coin, not people.', field: 'secrets', visibility: 'dm-only' },

      { id: 'ri_m6', text: 'Has a brother imprisoned in the second layer of the Nine Hells — his true motivation.', field: 'hooks', visibility: 'dm-only' },

    ] }),

  npc_aria: createNPC({ id: 'npc_aria', name: 'Aria Silverwind', title: 'Elven Scout', disposition: 'friendly', status: 'alive', personality: 'Quick to laugh, slow to trust. Fiercely loyal once befriended.', appearance: 'Tall elf with silver-streaked hair and keen green eyes.', voiceNotes: 'Light, musical voice. Speaks fast when excited.', currentLocation: 'Krin Hall', affiliations: [{ name: 'Rangers of the North', role: 'Scout' }], tags: ['ally', 'ranger'], notes: 'Met the party in Session 3. Guided them through the Whispering Woods.' }),

  npc_shadow_drake: createNPC({ id: 'npc_shadow_drake', name: 'Shadow Drake', disposition: 'hostile', status: 'alive', personality: 'Cunning predator. Toys with prey.', statBlock: { ...createStatBlock(), hp: 85, maxHp: 85, ac: 16, str: 18, dex: 14, con: 16, int: 8, wis: 12, cha: 10, speed: '40 ft., fly 60 ft.', cr: '5' }, abilities: [{ id: 'a1', name: 'Shadow Breath', description: 'Cone of necrotic shadow. DC 14 Con save, 6d6 necrotic on fail.', recharge: '5-6', damage: '6d6 necrotic' }, { id: 'a2', name: 'Shadow Step', description: 'Teleport up to 30 ft. to unoccupied space in dim light or darkness.', recharge: null }], lootTableId: null, xpReward: 1800, tags: ['dragon', 'underdark'], notes: 'Lair in the Shadowfen Caves. Weakness to radiant damage.' }),

};

// ============== END NPC LIBRARY CONSTANTS ==============





// Creature types for creatures category

const CREATURE_TYPES = [

  'Aberration', 'Beast', 'Celestial', 'Construct', 'Dragon', 'Elemental',

  'Fey', 'Fiend', 'Giant', 'Humanoid', 'Monstrosity', 'Ooze', 'Plant', 'Undead'

];



// Creature sizes

const CREATURE_SIZES = ['Tiny', 'Small', 'Medium', 'Large', 'Huge', 'Gargantuan'];



// Plant types for plants category

const PLANT_TYPES = ['Flower', 'Root', 'Fungus', 'Tree', 'Shrub', 'Vine', 'Moss', 'Algae', 'Herb', 'Other'];



// Item types

const ITEM_TYPES = ['Material', 'Consumable', 'Equipment', 'Weapon', 'Component', 'Quest Item', 'Treasure', 'Tool', 'Ingredient', 'Other'];



// Recipe types

const RECIPE_TYPES = ['Medicine', 'Potion', 'Food', 'Poison', 'Craft', 'Enchantment', 'Other'];



// Disease severity levels

const DISEASE_SEVERITIES = [

  { id: 'mild', name: 'Mild', color: '#27c95a', description: 'Minor inconvenience' },

  { id: 'moderate', name: 'Moderate', color: '#c9a227', description: 'Noticeable effects' },

  { id: 'severe', name: 'Severe', color: '#d97429', description: 'Debilitating' },

  { id: 'deadly', name: 'Deadly', color: '#c92727', description: 'Life-threatening' },

];



// Rarity levels

const RARITY_LEVELS = [

  { id: 'common', name: 'Common', color: '#888888' },

  { id: 'uncommon', name: 'Uncommon', color: '#27c95a' },

  { id: 'rare', name: 'Rare', color: '#4a90d9' },

  { id: 'veryRare', name: 'Very Rare', color: '#9b59b6' },

  { id: 'legendary', name: 'Legendary', color: '#c9a227' },

  { id: 'artifact', name: 'Artifact', color: '#c92727' },

];



// Quest statuses

const QUEST_STATUSES = [

  { id: 'active', name: 'Active', color: '#4a90d9', icon: '⚔️' },

  { id: 'completed', name: 'Completed', color: '#27c95a', icon: '✅' },

  { id: 'failed', name: 'Failed', color: '#c92727', icon: '❌' },

  { id: 'onHold', name: 'On Hold', color: '#c9a227', icon: '⏸️' },

];



// Quest priorities

const QUEST_PRIORITIES = [

  { id: 'main', name: 'Main Quest', color: '#c9a227', icon: '⭐' },

  { id: 'side', name: 'Side Quest', color: '#4a90d9', icon: '📋' },

  { id: 'personal', name: 'Personal', color: '#9b59b6', icon: '💜' },

  { id: 'guild', name: 'Guild/Faction', color: '#27c95a', icon: '🏛️' },

];



// Visibility options

const VISIBILITY_OPTIONS = [

  { id: 'private', name: 'Private', icon: '🔒', description: 'Only you can see' },

  { id: 'shared', name: 'Shared (All)', icon: '👥', description: 'All party members' },

  { id: 'select', name: 'Select Members', icon: '👤', description: 'Choose who sees' },

  { id: 'dm', name: 'DM Only', icon: '🎭', description: 'Only DM can see' },

];



// Dashboard section definitions for DM dashboard

const DASHBOARD_SECTIONS = {

  'characters': { id: 'characters', label: 'Characters', icon: '👥', description: 'Character cards and management' },

  'combat': { id: 'combat', label: 'Combat', icon: '⚔️', description: 'Combat quick access panel' },

  'systems-overview': { id: 'systems-overview', label: 'Systems Overview', icon: '🔒', description: 'Overview of all applied systems' },

  'food': { id: 'food', label: 'Food & Inventory', icon: '🍖', description: 'Food pool and inventory management' },

  'travel-log': { id: 'travel-log', label: 'Travel Log', icon: '📜', description: 'Group travel history' },

  'messages': { id: 'messages', label: 'Message Composer', icon: '✉️', description: 'Send messages to players' },

  'session-mode': { id: 'session-mode', label: 'Session Mode', icon: '🎯', description: 'Session tracking and timeline' },

  'shop': { id: 'shop', label: 'Shop Manager', icon: '🏪', description: 'Create and manage shops for players' },

  'crafting': { id: 'crafting', label: 'Crafting Table', icon: '⚒️', description: 'Manage recipes, items, and crafting for players' },

  'loot': { id: 'loot', label: 'Loot System', icon: '⚜️', description: 'Enemy loot tables, rolling, and distribution' },

};



// Custom buff/debuff color options

// textColor is for badge text (brighter for readability), color is for borders/accents

const DEFAULT_BUFF_COLOR_OPTIONS = [

  { id: 'default-buff', name: 'Default Buff (Green)', color: '#27c95a', textColor: '#6ee7a0', bgColor: 'rgba(39,201,90,0.15)', isDefault: true },

  { id: 'default-debuff', name: 'Default Debuff (Red)', color: '#ff6961', textColor: '#ffa5a0', bgColor: 'rgba(255,105,97,0.15)', isDefault: true },

  { id: 'gold', name: 'Gold (Divine)', color: '#ffd700', textColor: '#ffeb66', bgColor: 'rgba(255,215,0,0.18)' },

  { id: 'purple', name: 'Purple (Arcane)', color: '#9b59b6', textColor: '#d4a5eb', bgColor: 'rgba(155,89,182,0.18)' },

  { id: 'blue', name: 'Blue (Water/Ice)', color: '#4a90d9', textColor: '#9ec5f0', bgColor: 'rgba(74,144,217,0.18)' },

  { id: 'orange', name: 'Orange (Fire)', color: '#d97429', textColor: '#ffb366', bgColor: 'rgba(217,116,41,0.18)' },

  { id: 'cyan', name: 'Cyan (Lightning)', color: '#00bcd4', textColor: '#66e0f0', bgColor: 'rgba(0,188,212,0.18)' },

  { id: 'pink', name: 'Pink (Psychic)', color: '#e91e63', textColor: '#f78dab', bgColor: 'rgba(233,30,99,0.18)' },

  { id: 'gray', name: 'Gray (Neutral)', color: '#888888', textColor: '#c0c0c0', bgColor: 'rgba(136,136,136,0.15)' },

  { id: 'teal', name: 'Teal (Nature)', color: '#009688', textColor: '#66ccbf', bgColor: 'rgba(0,150,136,0.18)' },

  { id: 'dark-red', name: 'Dark Red (Oath/Blood)', color: '#c92727', textColor: '#ff8080', bgColor: 'rgba(201,39,39,0.25)' },

  { id: 'silver', name: 'Silver (Moon)', color: '#a0a0a0', textColor: '#e0e0e0', bgColor: 'rgba(192,192,192,0.18)' },

  { id: 'void', name: 'Void (Dark)', color: '#cc0000', textColor: '#ff4444', bgColor: 'rgba(0,0,0,0.4)', glowColor: '#ff0000' },

];



// Helper to get merged color options (default + custom)

const getBuffColorOptions = (customColors = []) => {

  const merged = [...DEFAULT_BUFF_COLOR_OPTIONS];

  // Add custom colors, replacing any with same id

  customColors.forEach(custom => {

    const existingIdx = merged.findIndex(c => c.id === custom.id);

    if (existingIdx >= 0) {

      merged[existingIdx] = { ...merged[existingIdx], ...custom };

    } else {

      merged.push(custom);

    }

  });

  return merged;

};



// For backward compatibility

const BUFF_COLOR_OPTIONS = DEFAULT_BUFF_COLOR_OPTIONS;

// Icon library — single shared library held in Convex storage; useIconLibrary

// loads it once and caches module-wide. Icons are referenced by `key`

// (matching the source filename, e.g. 'sword_basic_red'); item.iconRef holds

// the same key. Categories are arrays so a single icon can show up under

// multiple filters (e.g. mushroom is both food and ingredient).



const RpgIcon = ({ iconKey, size = 32 }) => {

  const lib = useIconLibrary();

  const icon = lib.byKey[iconKey];

  if (!icon || !icon.url) return null;

  return <img src={icon.url} alt={icon.name} width={size} height={size} style={{ display: 'block', borderRadius: 4, flexShrink: 0 }} />;

};



// Default icon for each item category (DM can override per-shop or per-item)

const DEFAULT_CATEGORY_ICONS = {

  potion: 'bottle_standard_red', weapon: null, armor: null, scroll: 'scroll_emblem',

  ingredient: 'mushroom_small_green', gear: 'medallion_gold', food: 'meat_grilled',

  misc: null, currency: 'medallion_gold',

};



// Smart icon resolver: item override > category default > emoji fallback.

// Falls through to the emoji while the library is still loading or when an

// iconRef points at a key the library doesn't have.

const ItemIcon = ({ item, size = 32, categoryDefaults, useCustomArt = true }) => {

  const lib = useIconLibrary();

  if (!useCustomArt) return <span style={{ fontSize: size * 0.6 }}>{LOOT_CATEGORIES[item?.category]?.icon || '📦'}</span>;

  const iconKey = item?.iconRef || (categoryDefaults || DEFAULT_CATEGORY_ICONS)[item?.category];

  const icon = iconKey ? lib.byKey[iconKey] : null;

  if (icon && icon.url) return <img src={icon.url} alt={icon.name} width={size} height={size} style={{ display: 'block', borderRadius: 4, flexShrink: 0 }} />;

  return <span style={{ fontSize: size * 0.6 }}>{LOOT_CATEGORIES[item?.category]?.icon || '📦'}</span>;

};



// Reusable icon picker. Searches across name, key, subcategory, and tags.

// Category chips filter by the multi-category array (an icon shows up under

// every category it belongs to). Sorted by count descending so the biggest

// buckets surface first.

const RpgIconPicker = ({ value, onChange, onClose, sc }) => {

  const lib = useIconLibrary();

  const [search, setSearch] = useState('');

  const [filterCat, setFilterCat] = useState('all');



  const catCounts = useMemo(() => {

    const c = {};

    for (const icon of lib.allIcons) {

      for (const cat of icon.categories) c[cat] = (c[cat] || 0) + 1;

    }

    return c;

  }, [lib.allIcons]);



  const filtered = useMemo(() => {

    const q = search.trim().toLowerCase();

    return lib.allIcons.filter(icon => {

      if (filterCat !== 'all' && !icon.categories.includes(filterCat)) return false;

      if (!q) return true;

      if (icon.name.toLowerCase().includes(q)) return true;

      if (icon.key.includes(q)) return true;

      if (icon.subcategory.includes(q)) return true;

      if (icon.tags.some(t => t.includes(q))) return true;

      return false;

    });

  }, [lib.allIcons, filterCat, search]);



  const bgCard = sc?.bgCard || '#2a2018';

  const border = sc?.border || '#3a3020';

  const borderMed = sc?.borderMed || '#5c4a2a';

  const text = sc?.text || '#e8d5b7';

  const textMuted = sc?.textMuted || '#7a6a55';

  const accent = sc?.accent || '#c9a227';

  const accentLight = sc?.accentLight || '#ffd700';

  const green = sc?.green || '#27c95a';



  return (

    <div style={{ position: 'fixed', inset: 0, zIndex: 10001, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>

      <div onClick={e => e.stopPropagation()} style={{ background: bgCard, border: `2px solid ${borderMed}`, borderRadius: 12, padding: 16, maxWidth: 520, width: '92%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>

          <span style={{ fontSize: '0.85rem', fontWeight: 700, color: accentLight }}>Select Icon</span>

          <div style={{ display: 'flex', gap: 6 }}>

            {value && <button onClick={() => { onChange(null); onClose(); }} style={{ padding: '3px 10px', fontSize: '0.68rem', background: 'rgba(201,39,39,0.15)', border: '1px solid rgba(201,39,39,0.4)', borderRadius: 6, color: '#ff6961', cursor: 'pointer' }}>Clear</button>}

            <button onClick={onClose} style={{ padding: '3px 10px', fontSize: '0.68rem', background: 'rgba(255,255,255,0.05)', border: `1px solid ${border}`, borderRadius: 6, color: textMuted, cursor: 'pointer' }}>Cancel</button>

          </div>

        </div>

        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search icons..." style={{ width: '100%', padding: '6px 10px', background: 'rgba(0,0,0,0.3)', border: `1px solid ${border}`, borderRadius: 6, color: text, fontSize: '0.78rem', outline: 'none', marginBottom: 8, boxSizing: 'border-box' }} autoFocus />

        <div style={{ display: 'flex', gap: 3, marginBottom: 8, flexWrap: 'wrap' }}>

          <button onClick={() => setFilterCat('all')} style={{ padding: '2px 8px', fontSize: '0.62rem', borderRadius: 4, background: filterCat === 'all' ? `${accent}20` : 'transparent', border: `1px solid ${filterCat === 'all' ? accent + '60' : border}`, color: filterCat === 'all' ? accentLight : textMuted, cursor: 'pointer' }}>All ({lib.allIcons.length})</button>

          {Object.entries(catCounts).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (

            <button key={cat} onClick={() => setFilterCat(cat)} style={{ padding: '2px 8px', fontSize: '0.62rem', borderRadius: 4, background: filterCat === cat ? `${accent}20` : 'transparent', border: `1px solid ${filterCat === cat ? accent + '60' : border}`, color: filterCat === cat ? accentLight : textMuted, cursor: 'pointer', textTransform: 'capitalize' }}>{cat} ({count})</button>

          ))}

        </div>

        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>

          {!lib.ready && <div style={{ textAlign: 'center', padding: '20px 0', color: textMuted, fontSize: '0.8rem' }}>Loading icons…</div>}

          {lib.ready && (

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(68px, 1fr))', gap: 6 }}>

              {filtered.map(icon => {

                const isSelected = value === icon.key;

                return (

                  <div key={icon.key} onClick={() => { onChange(icon.key); onClose(); }} title={icon.name} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: 6, background: isSelected ? `${green}20` : 'rgba(255,255,255,0.02)', border: `2px solid ${isSelected ? green + '80' : 'transparent'}`, borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s' }} onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }} onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}>

                    <img src={icon.url} alt={icon.name} width={40} height={40} style={{ borderRadius: 4 }} loading="lazy" />

                    <span style={{ fontSize: '0.5rem', color: textMuted, textAlign: 'center', lineHeight: 1.2, maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{icon.name}</span>

                  </div>

                );

              })}

            </div>

          )}

          {lib.ready && filtered.length === 0 && <div style={{ textAlign: 'center', padding: '20px 0', color: textMuted, fontSize: '0.8rem' }}>No icons match "{search}"</div>}

        </div>

      </div>

    </div>

  );

};



// Compact icon select button (opens picker)

const IconSelectButton = ({ value, onChange, sc, size = 32 }) => {

  const lib = useIconLibrary();

  const [showPicker, setShowPicker] = useState(false);

  const icon = value ? lib.byKey[value] : null;

  return (

    <>

      <button onClick={() => setShowPicker(true)} title={icon ? icon.name : 'Select icon'} style={{ width: size + 12, height: size + 12, display: 'flex', alignItems: 'center', justifyContent: 'center', background: icon ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.02)', border: `1.5px dashed ${icon ? (sc?.accent || '#c9a227') + '60' : (sc?.border || '#3a3020')}`, borderRadius: 8, cursor: 'pointer', padding: 0 }}>

        {icon && icon.url ? <img src={icon.url} alt={icon.name} width={size} height={size} style={{ borderRadius: 4 }} /> : <span style={{ fontSize: '0.65rem', color: sc?.textMuted || '#7a6a55' }}>🖼️</span>}

      </button>

      {showPicker && <RpgIconPicker value={value} onChange={onChange} onClose={() => setShowPicker(false)} sc={sc} />}

    </>

  );

};





// Special effect types - quick presets for common DM effects

const EFFECT_TYPE_PRESETS = [

  { id: 'buff', name: 'Buff', icon: '✨', color: 'default-buff', description: 'Standard positive effect' },

  { id: 'debuff', name: 'Debuff', icon: '⚠️', color: 'default-debuff', description: 'Standard negative effect' },

  { id: 'oath', name: 'Oath', icon: '⛓️', color: 'dark-red', description: 'Binding vow or restriction' },

  { id: 'curse', name: 'Curse', icon: '💀', color: 'purple', description: 'Magical affliction' },

  { id: 'blessing', name: 'Blessing', icon: '🌟', color: 'gold', description: 'Divine favor' },

  { id: 'mark', name: 'Mark', icon: '👁', color: 'cyan', description: 'Supernatural sign' },

  { id: 'pact', name: 'Pact', icon: '📜', color: 'orange', description: 'Bargain or deal' },

  { id: 'geas', name: 'Geas', icon: '🔗', color: 'pink', description: 'Magical compulsion' },

  { id: 'disease', name: 'Disease', icon: '☣', color: 'purple', description: 'Illness or infection', svgIcon: 'biohazard' },

  { id: 'secret', name: 'Secret', icon: '🤫', color: 'gray', description: 'Hidden knowledge' },

  { id: 'fey', name: 'Fey Touch', icon: '🧚', color: 'silver', description: 'Fey influence' },

];



// Whisper display style options

const WHISPER_STYLES = [

  { 

    id: 'toast', 

    name: 'Toast', 

    icon: '📋', 

    description: 'Slide-in corner notification',

    position: 'bottom-right',

  },

  { 

    id: 'overlay', 

    name: 'Overlay', 

    icon: '🎭', 

    description: 'Centered semi-transparent overlay',

    position: 'center',

  },

  { 

    id: 'bubble', 

    name: 'Bubble', 

    icon: '💭', 

    description: 'Floating chat bubble near top',

    position: 'top-center',

  },

  { 

    id: 'inline', 

    name: 'Inline', 

    icon: '💬', 

    description: 'In message feed only',

    position: 'feed',

  },

];



// Whisper sender styles - supports both "X whispers:" and "X:" formats

const WHISPER_SENDERS = [

  { id: 'anonymous', name: 'Anonymous', prefix: 'A voice', useWhispers: true, icon: '🤫', color: '#c9a0dc' },

  { id: 'dm', name: 'DM', prefix: 'DM', useWhispers: true, icon: '👑', color: '#ffd700' },

  { id: 'system', name: 'System', prefix: 'System', useWhispers: false, icon: '⚙️', color: '#4a90d9' },

  { id: 'internal', name: 'Internal Thought', prefix: 'You think to yourself', useWhispers: false, icon: '💭', color: '#888' },

  { id: 'divine', name: 'Divine', prefix: 'A divine voice', useWhispers: true, icon: '✨', color: '#f5d742' },

  { id: 'warning', name: 'Warning', prefix: 'Your instincts', useWhispers: false, icon: '⚠️', color: '#ff6961' },

  { id: 'custom', name: 'Custom NPC', prefix: '', useWhispers: true, icon: '👤', color: '#c9a0dc' },

];



// Message format options

const MESSAGE_FORMATS = [

  { id: 'whisper', name: 'Whispers', example: 'Nzoth whispers: You will give in...' },

  { id: 'says', name: 'Says', example: 'Nzoth says: You will give in...' },

  { id: 'direct', name: 'Direct', example: 'Nzoth: You will give in...' },

  { id: 'emote', name: 'Emote', example: '*Nzoth\'s voice echoes* You will give in...' },

];



// Whisper delivery styles (animation)

const WHISPER_DELIVERY = [

  { id: 'instant', name: 'Instant', description: 'Appears immediately' },

  { id: 'gentle', name: 'Gentle', description: 'Fades in smoothly (0.5s)' },

  { id: 'dramatic', name: 'Dramatic', description: 'Typewriter effect' },

];



// Message display modes - how the message appears to the player

const MESSAGE_DISPLAY_MODES = [

  { 

    id: 'toast', 

    name: 'Toast', 

    icon: '📬',

    description: 'Slide-in corner notification',

    position: 'bottom-right',

  },

  { 

    id: 'overlay', 

    name: 'Overlay', 

    icon: '🎭',

    description: 'Centered semi-transparent overlay with blur',

    position: 'center',

  },

  { 

    id: 'inline', 

    name: 'Inline', 

    icon: '💬',

    description: 'Appears in message feed/panel',

    position: 'panel',

  },

  { 

    id: 'banner', 

    name: 'Banner', 

    icon: '📜',

    description: 'Top banner that slides down',

    position: 'top',

  },

];



// Message urgency levels

const MESSAGE_URGENCY = [

  { id: 'normal', name: 'Normal', color: '#c9a227', autoDismiss: 0 },

  { id: 'info', name: 'Info', color: '#4a90d9', autoDismiss: 10000 },

  { id: 'warning', name: 'Warning', color: '#f5a623', autoDismiss: 0 },

  { id: 'urgent', name: 'Urgent', color: '#ff6961', autoDismiss: 0, pulse: true },

];



// Message type styles for rendering

const MESSAGE_TYPE_STYLES = {

  'dm-message': {

    name: 'DM Message',

    icon: '👑',

    border: '2px solid #c9a227',

    background: 'linear-gradient(135deg, rgba(201,162,39,0.15) 0%, rgba(0,0,0,0) 100%)',

    textColor: '#ffd700',

  },

  'whisper': {

    name: 'Whisper',

    icon: '🤫',

    border: '1px solid rgba(155,89,182,0.5)',

    background: 'rgba(155,89,182,0.1)',

    textColor: '#c9a0dc',

    fontStyle: 'italic',

  },

  'status': {

    name: 'Status Update',

    icon: 'ℹ️',

    border: '1px solid rgba(74,144,217,0.5)',

    background: 'rgba(74,144,217,0.1)',

    textColor: '#7db5ff',

  },

  'urgent': {

    name: 'Urgent',

    icon: '🔴',

    border: '2px solid #ff6961',

    background: 'rgba(255,105,97,0.15)',

    textColor: '#ff6961',

    animation: 'pulse 2s infinite',

  },

  'ambient': {

    name: 'Ambient',

    icon: '🌫️',

    border: 'none',

    background: 'transparent',

    textColor: '#888',

    fontStyle: 'italic',

  },

};



// Resource tracker presets (like Qi, Ki, Sorcery Points, etc.)

const RESOURCE_TRACKER_PRESETS = [

  {

    id: 'qi-cultivation',

    name: 'Qi Cultivation',

    description: 'Track cultivation progress in years and months',

    icon: '☯️',

    fields: [

      { id: 'qi-years', label: 'Years of Cultivation', type: 'number', value: 0, min: 0, max: 1000 },

      { id: 'qi-months', label: 'Months', type: 'counter', value: 0, min: 0, max: 11, step: 1 },

      { id: 'realm', label: 'Cultivation Realm', type: 'stage', stages: ['Mortal', 'Qi Gathering', 'Foundation', 'Core Formation', 'Nascent Soul', 'Deity'] },

    ],

    thresholds: [

      { value: 10, effect: '+1 to meditation checks' },

      { value: 50, effect: '+2 Con, Slow Aging' },

      { value: 100, effect: '+1 All Stats, Ageless' },

    ],

  },

  {

    id: 'ki-points',

    name: 'Ki Points',

    description: 'Monk ki point tracking',

    icon: '👊',

    fields: [

      { id: 'ki-current', label: 'Current Ki', type: 'progress', value: 0, max: 20, color: '#4a90d9' },

    ],

  },

  {

    id: 'sorcery-points',

    name: 'Sorcery Points',

    description: 'Sorcerer metamagic resource',

    icon: '✨',

    fields: [

      { id: 'sp-current', label: 'Sorcery Points', type: 'progress', value: 0, max: 20, color: '#9b59b6' },

    ],

  },

  {

    id: 'rage-uses',

    name: 'Rage Uses',

    description: 'Barbarian rage tracking',

    icon: '💢',

    fields: [

      { id: 'rage-current', label: 'Rages Remaining', type: 'counter', value: 3, min: 0, max: 6, color: '#c92727' },

    ],

  },

  {

    id: 'inspiration',

    name: 'Bardic Inspiration',

    description: 'Bard inspiration dice',

    icon: '🎵',

    fields: [

      { id: 'insp-current', label: 'Inspiration Dice', type: 'counter', value: 3, min: 0, max: 10, color: '#27c95a' },

    ],

  },

  {

    id: 'custom',

    name: 'Custom Resource',

    description: 'Create your own resource tracker',

    icon: '⚙️',

    fields: [],

  },

];



// Create a journal entry

const createJournalEntry = (category, overrides = {}) => ({

  id: generateId(),

  category,

  name: '',

  description: '',

  image: null, // Base64 or URL

  tags: [], // Custom tags

  locationTags: [], // Location-specific tags

  visibility: 'private',

  sharedWith: [], // Character IDs if visibility is 'select'

  createdBy: null, // Character ID

  createdAt: new Date().toISOString(),

  updatedAt: new Date().toISOString(),

  pinned: false,

  isStub: false, // Quick-add stub that needs details filled in

  linkedEntries: [], // { entryId, fieldName, autoLinked } - linked journal entries

  sourceEncounter: null, // { encounterType, encounterId } - if auto-created from encounter

  // Category-specific fields

  ...getCategoryDefaults(category),

  ...overrides,

});



// Get default fields for each category

const getCategoryDefaults = (category) => {

  switch (category) {

    case 'creatures':

      return {

        creatureType: 'Beast',

        size: 'Medium',

        challengeRating: '1',

        habitat: '',

        weaknesses: [],

        resistances: [],

        immunities: [],

        abilities: '',

        loot: '',

        encounters: [], // { date, outcome, notes, partyMembers }

        isBoss: false,

      };

    case 'plants':

      return {

        plantType: 'Herb',

        rarity: 'common',

        habitat: '',

        effects: '',

        harvesting: '',

        usedInRecipes: [], // Recipe names/IDs that use this plant

        soldAt: [], // Shop/NPC names

      };

    case 'npcs':

      return {

        title: '',

        currentLocation: '',

        faction: '',

        disposition: 'unknown',

        appearance: '',

        personality: '',

        knownInfo: '',

        questConnections: [], // Quest names

        sells: [], // Items this NPC sells

        lastSeen: '',

        isAlive: true,

      };

    case 'quests':

      return {

        questGiver: '', // NPC name or ID

        questGiverContact: null, // NPC entry ID

        status: 'active',

        priority: '', // empty = unset; DM picks from QUEST_PRIORITIES (main/side/personal/guild)

        objectives: [], // { text, completed }

        rewards: '',

        relatedNpcs: [], // NPC names

        relatedItems: [], // Item names needed/rewarded

        relatedLocations: [],

        startDate: '',

        completionDate: '',

      };

    case 'session':

      return {

        sessionNumber: 1,

        sessionDate: new Date().toISOString().split('T')[0],

        summary: '',

        keyEvents: [], // { event, important }

        npcsEncountered: [], // Names or NPC IDs

        creaturesEncountered: [], // Names or Creature IDs

        itemsFound: [],

        playerNotes: '',

      };

    case 'items':

      return {

        itemType: 'Material',

        rarity: 'common',

        value: '', // e.g., "5 gp"

        weight: '',

        locations: [], // Where to find it

        soldAt: [], // NPCs/shops that sell it

        usedInRecipes: [], // Recipe names that use this

        relatedQuests: [], // Quests involving this item

        effects: '', // What it does if consumable

        isQuestItem: false,

      };

    case 'recipes':

      return {

        recipeType: 'Medicine',

        ingredients: [], // { name, quantity, optional }

        result: '', // What it creates

        resultQuantity: 1,

        effects: '', // Buffs, healing, etc.

        duration: '', // How long effects last

        curesDiseases: [], // Disease names this cures

        craftingDC: '', // DC to craft

        craftingTime: '', // How long to craft

        craftingTools: '', // Tools needed

        learnedFrom: '', // Where/who taught this

      };

    case 'diseases':

      return {

        severity: 'mild',

        symptoms: [], // List of symptoms

        transmission: '', // How you catch it

        incubation: '', // Time before symptoms

        progression: '', // How it gets worse

        effects: [], // Debuffs it causes

        curedBy: [], // Recipe/item names that cure it

        locations: [], // Where it's common

        creatures: [], // Creatures that spread it

        notes: '',

      };

    default:

      return {};

  }

};



// Apply parsed quick-add results to journal entries

// Returns { updatedJournal, summary } where summary describes what was created/updated

// 2026-04-27 (later): added optional `opts.skipNpcDescriptionAppend`.

// When the user picks a block-category pill (Profile, Quest, Story…)

// before submitting, the body text should land in the chosen player

// block on each referenced NPC — NOT in the NPC's description (which

// renders as the NPC's "Session Notes" panel). Without this flag the

// text was getting written to BOTH places, surprising the user.

//

// The session journal entry still gets its summary line (block.sessionText)

// regardless, since the user explicitly said "session notes" should still

// fire — they meant the global session entry, not the per-NPC panel.

const applyQuickAddResults = (parsedBlocks, journal, activeSessionId, currentCharId, opts = {}) => {

  const { skipNpcDescriptionAppend = false } = opts;

  let entries = [...journal];

  const summary = { created: [], updated: [], sessionUpdated: false };

  

  // Find or validate active session

  let activeSession = activeSessionId ? entries.find(e => e.id === activeSessionId) : null;

  

  for (const block of parsedBlocks) {

    // Collect all location tags from this block

    const blockLocations = block.locationTags || [];

    

    // Resolve @ref entries by looking up names in existing entries

    for (const entryData of block.entries) {

      if (entryData.isReference && !entryData.category) {

        // Look up by name (case-insensitive) across all categories

        const match = entries.find(e => 

          e.name && e.name.toLowerCase() === entryData.name.toLowerCase()

        );

        if (match) {

          entryData.category = match.category;

          // Reverse-lookup the code from category

          const codeEntry = Object.entries(JOURNAL_TAG_CODES).find(([_, info]) => info.category === match.category);

          entryData.code = codeEntry ? codeEntry[0] : null;

        }

        // If no match found, category stays null and will be skipped below

      }

    }

    

    // Process entry tags

    // Check if this block has a quest (for quest-item linking)

    const blockHasQuest = block.entries.some(e => e.category === 'quests');

    

    for (const entryData of block.entries) {

      if (!entryData.category) continue; // Skip unresolved refs, #L tags

      if (!entryData.name || !entryData.name.trim()) continue; // Skip entries with no name

      

      // Quest items (#I within a quest block) don't create standalone entries

      // They get added to the quest's relatedItems instead

      if (entryData.isQuestItem) {

        summary.created.push({ name: entryData.name, category: 'items', isQuestItem: true });

        continue;

      }

      

      // Check if entry already exists (case-insensitive name match in same category)

      const existing = entries.find(e => 

        e.category === entryData.category && 

        e.name.toLowerCase() === entryData.name.toLowerCase()

      );

      

      if (existing) {

        // 2026-04-27: when the user picked a player-block category pill,

        // route the body text to the chosen block (handled later by

        // addPlayerNoteBlocks) and DON'T append it to the NPC's

        // description here. Without this gate, the same text ended up

        // both as a Session Notes line on the NPC and as a Profile/Quest

        // block entry — duplicate visibility the user flagged.

        const skipDescAppend = skipNpcDescriptionAppend && entryData.category === 'npcs';

        const sessionNum = activeSession ? (entries.find(e => e.id === activeSession.id) || {}).sessionNumber : null;

        let newDesc = existing.description;

        if (!skipDescAppend) {

          let separator = '\n';

          if (sessionNum && existing.description) {

            const lastSessionMatch = existing.description.match(/── Session #(\d+) ──/g);

            const lastSessionNum = lastSessionMatch

              ? parseInt(lastSessionMatch[lastSessionMatch.length - 1].match(/\d+/)[0])

              : null;

            if (lastSessionNum !== sessionNum) {

              separator = `\n\n── Session #${sessionNum} ──\n`;

            }

          } else if (sessionNum && !existing.description) {

            separator = `── Session #${sessionNum} ──\n`;

          }

          newDesc = existing.description

            ? existing.description + separator + entryData.description

            : entryData.description;

        }



        const updatedEntry = {

          ...existing,

          description: newDesc,

          updatedAt: new Date().toISOString(),

          isStub: false, // Clear stub if it was one

          locationTags: [...new Set([...(existing.locationTags || []), ...blockLocations, ...(entryData.locationTags || [])])],

          tags: [...new Set([...(existing.tags || []), ...(entryData.extraTags || [])])],

        };



        entries = entries.map(e => e.id === existing.id ? updatedEntry : e);

        summary.updated.push({ name: entryData.name, category: entryData.category });

      } else {

        // Create new entry. 2026-04-27: when the player-block category

        // pill is set, the body text is destined for that block — leave

        // the description empty for new NPCs so it doesn't double up.

        const skipDescAppend = skipNpcDescriptionAppend && entryData.category === 'npcs';

        const newEntry = createJournalEntry(entryData.category, {

          name: entryData.name,

          description: skipDescAppend ? '' : (entryData.description || ''),

          createdBy: currentCharId,

          visibility: 'shared',

          isStub: skipDescAppend ? true : !entryData.description, // Stub when description is intentionally empty

          locationTags: [...new Set([...blockLocations, ...(entryData.locationTags || [])])],

          tags: [...(entryData.extraTags || [])],

        });

        

        // Category-specific field population

        if (entryData.category === 'quests') {

          newEntry.status = 'active';

          // Link related NPCs

          const relatedNpcs = block.entries

            .filter(e => e.category === 'npcs' && e.name !== entryData.name)

            .map(e => e.name);

          // Quest items from #I@ tags in this block

          const questItems = block.entries

            .filter(e => e.isQuestItem || (e.category === 'items' && e.name !== entryData.name))

            .map(e => e.name);

          if (relatedNpcs.length) {

            newEntry.relatedNpcs = relatedNpcs;

            newEntry.questGiver = relatedNpcs[0]; // First NPC is quest giver

          }

          if (questItems.length) newEntry.relatedItems = questItems;

        }

        if (entryData.category === 'items' && !entryData.isQuestItem) {

          const relatedQuests = block.entries

            .filter(e => e.category === 'quests' && e.name !== entryData.name)

            .map(e => e.name);

          if (relatedQuests.length) newEntry.relatedQuests = relatedQuests;

        }

        if (entryData.category === 'npcs') {

          if (blockLocations.length) newEntry.currentLocation = blockLocations[0];

          const questLinks = block.entries

            .filter(e => e.category === 'quests')

            .map(e => e.name);

          if (questLinks.length) newEntry.questConnections = questLinks;

        }

        

        entries.push(newEntry);

        summary.created.push({ name: entryData.name, category: entryData.category });

      }

    }

    

    // Apply todo objectives to quest entries in this block

    // #todo = quest objectives only (session if no quest)

    // @todo = quest objectives AND session checkboxes

    if ((block.todos || []).length > 0) {

      console.log('[QuickAdd Todo] Block todos:', JSON.stringify(block.todos));

      // Find the LAST mentioned quest in block (most relevant)

      const questEntries = block.entries.filter(e => e.category === 'quests');

      const questName = questEntries.length > 0 ? questEntries[questEntries.length - 1].name : null;

      console.log('[QuickAdd Todo] Quest entries in block:', questEntries.map(e => e.name), 'Last quest name:', questName);

      

      const questTodos = block.todos.map(t => typeof t === 'string' ? { text: t, prefix: '#' } : t);

      

      // Add objectives to quest (both #todo and @todo go to quest if one exists)

      if (questName) {

        const questEntry = entries.find(e => 

          e.category === 'quests' && e.name.toLowerCase() === questName.toLowerCase()

        );

        console.log('[QuickAdd Todo] Found quest in entries:', questEntry ? questEntry.name : 'NOT FOUND', 'id:', questEntry?.id);

        if (questEntry) {

          const newObjectives = [

            ...(questEntry.objectives || []),

            ...questTodos.map(t => ({ text: t.text, completed: false }))

          ];

          console.log('[QuickAdd Todo] Adding objectives:', newObjectives);

          entries = entries.map(e => e.id === questEntry.id 

            ? { ...e, objectives: newObjectives, updatedAt: new Date().toISOString() } 

            : e

          );

          // Verify the update

          const verified = entries.find(e => e.id === questEntry.id);

          console.log('[QuickAdd Todo] Verified objectives:', verified?.objectives);

        }

      }

      

      // @todos ALWAYS go to session as checkbox items

      // #todos go to session ONLY if there's no quest in the block

      const sessionTodos = questTodos.filter(t => t.prefix === '@' || !questName);

      if (sessionTodos.length > 0) {

        const todoText = sessionTodos.map(t => `☐ ${t.text}`).join('\n');

        if (block.sessionText) {

          block.sessionText += '\n' + todoText;

        } else {

          block.sessionText = todoText;

        }

      }

    }

    

    // Add session text and references to active session

    // The session always gets the full block narrative (cleaned of markup) plus NPC/item/creature links

    if (activeSession && (block.entries.length > 0 || block.sessionText)) {

      const sessionEntry = entries.find(e => e.id === activeSession.id);

      if (sessionEntry) {

        // Build full narrative: clean all tag/ref markup from entire block text

        const blockText = block.rawText || '';

        const blockTags = block.parsedTags || [];

        // Strip #todo/@todo lines and #L@ location tags FIRST (before ref cleanup mangles them)

        let fullNarrative = blockText.replace(/[#@]todo\b[^\n]*/gi, '').replace(/#L@\S+/g, '');

        // Then clean tag markup (#X@Name → Name) and ref markup (@name → name)

        fullNarrative = cleanRefMarkup(cleanTagMarkup(fullNarrative, blockTags.filter(t => t.type === 'tag')));

        // Clean up excessive whitespace

        fullNarrative = fullNarrative.replace(/\n{3,}/g, '\n\n').trim();

        

        if (fullNarrative) {

          sessionEntry.summary = sessionEntry.summary 

            ? sessionEntry.summary + '\n\n' + fullNarrative 

            : fullNarrative;

        }

        

        // Add NPCs, creatures, items encountered

        const npcs = block.entries.filter(e => e.category === 'npcs').map(e => e.name);

        const creatures = block.entries.filter(e => e.category === 'creatures').map(e => e.name);

        const items = block.entries.filter(e => e.category === 'items').map(e => e.name);

        

        if (npcs.length) sessionEntry.npcsEncountered = [...new Set([...(sessionEntry.npcsEncountered || []), ...npcs])];

        if (creatures.length) sessionEntry.creaturesEncountered = [...new Set([...(sessionEntry.creaturesEncountered || []), ...creatures])];

        if (items.length) sessionEntry.itemsFound = [...new Set([...(sessionEntry.itemsFound || []), ...items])];

        if (blockLocations.length) sessionEntry.locationTags = [...new Set([...(sessionEntry.locationTags || []), ...blockLocations])];

        

        sessionEntry.updatedAt = new Date().toISOString();

        entries = entries.map(e => e.id === sessionEntry.id ? sessionEntry : e);

        summary.sessionUpdated = true;

      }

    }

  }

  

  return { updatedJournal: entries, summary };

};



// Find matching journal entries by name (for auto-linking)

const findMatchingEntries = (text, entries, excludeId = null) => {

  if (!text || !entries) return [];

  const matches = [];

  const lowerText = text.toLowerCase();

  

  entries.forEach(entry => {

    if (entry.id === excludeId) return;

    if (entry.name && lowerText.includes(entry.name.toLowerCase())) {

      matches.push({

        entryId: entry.id,

        entryName: entry.name,

        category: entry.category,

        matchedText: entry.name

      });

    }

  });

  

  return matches;

};



// Render text with clickable links to other entries

const renderLinkedText = (text, entries, onClickEntry, linkedEntries = [], unlinkedNames = [], options = {}) => {

  if (!text || !entries || entries.length === 0) return text;

  

  const { onHover, onLeave, firstOccurrenceOnly = false, alreadyLinked = new Set() } = options;

  

  // Build a map of entry names to entries (case-insensitive)

  const nameMap = {};

  entries.forEach(entry => {

    if (entry.name) {

      nameMap[entry.name.toLowerCase()] = entry;

    }

  });

  

  // Find all potential matches (sort by length descending to match longer names first)

  const entryNames = Object.keys(nameMap).sort((a, b) => b.length - a.length);

  

  // Check if a name is explicitly unlinked

  const isUnlinked = (name) => unlinkedNames.some(u => u.toLowerCase() === name.toLowerCase());

  

  // Track which names we've already linked (for first-occurrence-only mode)

  const linkedInThisText = new Set(alreadyLinked);

  

  // Simple approach: find matches and mark positions

  const matches = [];

  entryNames.forEach(name => {

    if (isUnlinked(name)) return;

    const regex = new RegExp(`\\b${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');

    let match;

    let foundFirst = false;

    while ((match = regex.exec(text)) !== null) {

      // If first-occurrence-only and we've already linked this name, skip

      if (firstOccurrenceOnly && (linkedInThisText.has(name) || foundFirst)) continue;

      matches.push({ start: match.index, end: match.index + match[0].length, name: match[0], entry: nameMap[name], entryName: name });

      foundFirst = true;

      linkedInThisText.add(name);

    }

  });

  

  // Sort by position and remove overlaps

  matches.sort((a, b) => a.start - b.start);

  const filteredMatches = [];

  let lastEnd = 0;

  matches.forEach(m => {

    if (m.start >= lastEnd) {

      filteredMatches.push(m);

      lastEnd = m.end;

    }

  });

  

  // Helper to convert text with newlines into React elements

  const textWithBreaks = (str, keyPrefix) => {

    if (!str.includes('\n')) return str;

    const parts = str.split('\n');

    return parts.map((p, i) => (

      React.createElement(React.Fragment, { key: `${keyPrefix}-${i}` },

        p,

        i < parts.length - 1 ? React.createElement('br') : null

      )

    ));

  };

  

  // Build result

  if (filteredMatches.length === 0) return textWithBreaks(text, 'tw');

  

  const elements = [];

  let currentIndex = 0;

  

  filteredMatches.forEach((m, i) => {

    if (m.start > currentIndex) {

      elements.push(textWithBreaks(text.substring(currentIndex, m.start), `pre-${i}`));

    }

    elements.push(

      <span 

        key={`link-${i}`}

        style={{ 

          color: JOURNAL_CATEGORIES[m.entry.category]?.color || '#4a90d9',

          cursor: 'pointer', 

          textDecoration: 'underline',

          textDecorationStyle: 'dotted',

          textDecorationColor: `${JOURNAL_CATEGORIES[m.entry.category]?.color || '#4a90d9'}55`,

          textUnderlineOffset: '3px',

          textDecorationThickness: '1px',

        }}

        onClick={(e) => { e.stopPropagation(); onClickEntry(m.entry); }}

        onMouseEnter={onHover ? (e) => onHover(m.entry, e) : undefined}

        onMouseLeave={onLeave}

        title={!onHover ? `View ${JOURNAL_CATEGORIES[m.entry.category]?.name}: ${m.entry.name}` : undefined}

      >

        {m.name}

      </span>

    );

    currentIndex = m.end;

  });

  

  if (currentIndex < text.length) {

    elements.push(textWithBreaks(text.substring(currentIndex), 'post'));

  }

  

  return { elements, linkedNames: linkedInThisText };

};



// Helper to render linked text and track across multiple fields

const renderLinkedTextSimple = (text, entries, onClickEntry, unlinkedNames = [], options = {}) => {

  const result = renderLinkedText(text, entries, onClickEntry, [], unlinkedNames, options);

  return result.elements || result;

};



// ── Private Text System ──

// Inline private text: {{PRIVATE:charId}}secret text{{/PRIVATE}}

// Private notes: stored in entry.privateNotes array



const PRIVATE_REGEX = /\{\{PRIVATE:([^}]+)\}\}([\s\S]*?)\{\{\/PRIVATE\}\}/g;



// Check if a viewer can see a private section (supports comma-separated owner ids)

const canViewPrivate = (ownerField, viewerCharId, isDM) => {

  if (!viewerCharId && !isDM) return false;

  const noDM = ownerField.includes('!noDM');

  const cleanField = ownerField.replace('!noDM', '').replace(/,\s*$/, '');

  if (isDM && !noDM) return true;

  const owners = cleanField.split(',').map(s => s.trim());

  return owners.includes(viewerCharId);

};



// Strip private sections not belonging to the viewer

const stripPrivateForViewer = (text, viewerCharId, isDM) => {

  if (!text) return text;

  return text.replace(PRIVATE_REGEX, (match, ownerField, content) => {

    if (canViewPrivate(ownerField, viewerCharId, isDM)) return match; // Keep for authorized

    return ''; // Remove for others

  }).replace(/\n{3,}/g, '\n\n').trim();

};



// Strip ALL private markers (for display as clean text to the owner)

const stripPrivateMarkers = (text) => {

  if (!text) return text;

  return text.replace(PRIVATE_REGEX, '$2');

};



// Check if text has any private content

const hasPrivateContent = (text) => {

  if (!text) return false;

  return /\{\{PRIVATE:[^}]+\}\}/.test(text);

};



// Render text with private sections styled differently

// Returns React elements with private sections highlighted for the owner

const renderTextWithPrivacy = (text, viewerCharId, isDM, entries, onClickEntry, options = {}, charMap = {}) => {

  if (!text) return null;

  

  // First strip private text not for this viewer

  const viewableText = stripPrivateForViewer(text, viewerCharId, isDM);

  if (!viewableText) return null;

  

  // Split into segments: public and private

  const segments = [];

  let lastIndex = 0;

  const regex = /\{\{PRIVATE:([^}]+)\}\}([\s\S]*?)\{\{\/PRIVATE\}\}/g;

  let match;

  

  while ((match = regex.exec(viewableText)) !== null) {

    // Public text before this private block

    if (match.index > lastIndex) {

      segments.push({ type: 'public', text: viewableText.substring(lastIndex, match.index) });

    }

    segments.push({ type: 'private', text: match[2].replace(/^\n+/, ''), ownerId: match[1] });

    lastIndex = match.index + match[0].length;

  }

  // Remaining public text

  if (lastIndex < viewableText.length) {

    segments.push({ type: 'public', text: viewableText.substring(lastIndex) });

  }

  

  // Trim trailing newlines from public segments before private ones

  // and leading newlines from public segments after private ones

  for (let si = 0; si < segments.length; si++) {

    if (segments[si].type === 'public' && si + 1 < segments.length && segments[si + 1].type === 'private') {

      segments[si] = { ...segments[si], text: segments[si].text.replace(/\n+$/, '') };

    }

    if (segments[si].type === 'public' && si > 0 && segments[si - 1].type === 'private') {

      segments[si] = { ...segments[si], text: segments[si].text.replace(/^\n+/, '') };

    }

  }

  

  // If no private markers at all, just render normally

  if (segments.length === 1 && segments[0].type === 'public') {

    return entries 

      ? renderLinkedTextSimple(segments[0].text, entries, onClickEntry, [], options)

      : segments[0].text;

  }

  

  // Check if any segment is shared (has 👥 icon) — if so, pad all lines for alignment

  const hasShared = segments.some(s => s.type === 'private' && s.ownerId && s.ownerId.includes(','));

  const padStyle = hasShared ? { paddingLeft: 'calc(0.65em + 8px)' } : {};

  

  // Wrap everything in a div so we don't put divs inside <p> tags

  return (

    <div style={{ whiteSpace: 'pre-wrap' }}>

      {segments.map((seg, i) => {

        if (seg.type === 'public') {

          const content = entries 

            ? renderLinkedTextSimple(seg.text, entries, onClickEntry, [], options)

            : seg.text;

          return <div key={i} style={{ whiteSpace: 'pre-wrap', lineHeight: '1.5', ...padStyle }}>{content}</div>;

        } else {

          // Private segment - purple for solo, teal for shared

          const content = entries 

            ? renderLinkedTextSimple(seg.text, entries, onClickEntry, [], options)

            : seg.text;

          const noDM = seg.ownerId && seg.ownerId.includes('!noDM');

          const cleanOwnerId = (seg.ownerId || '').replace('!noDM', '').replace(/,\s*$/, '');

          const isSharedPrivate = cleanOwnerId && cleanOwnerId.includes(',');

          const privColor = isSharedPrivate ? '#8fd4d4' : '#c4a8ff';

          const sharedNames = isSharedPrivate 

            ? cleanOwnerId.split(',').map(id => id.trim()).filter(id => id !== viewerCharId).map(id => charMap[id]?.name || id)

            : [];

          const dmNote = noDM ? '' : (isDM ? '' : ' and DM');

          const tooltipText = isSharedPrivate 

            ? `Shared with: ${sharedNames.join(', ')}${dmNote}`

            : (noDM ? 'Private — only visible to you (DM excluded)' : 'Private — only visible to you and DM');

          const prevSeg = i > 0 ? segments[i - 1] : null;

          const prevIsPrivate = prevSeg && prevSeg.type === 'private';

          const prevSameType = prevIsPrivate && (prevSeg.ownerId === seg.ownerId);

          const showEmoji = isSharedPrivate && !prevSameType;

          return (

            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: showEmoji ? '4px' : '0px', color: privColor, lineHeight: '1.5', whiteSpace: 'normal' }}>

              {showEmoji ? (

                <Tooltip text={tooltipText} special borderColor={'#6bbfbf'}>

                  <span style={{ cursor: 'help', opacity: 0.85, fontSize: '0.65em', flexShrink: 0 }}>👥</span>

                </Tooltip>

              ) : null}

              <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word', ...(!showEmoji ? padStyle : {}) }}>{content}</span>

            </div>

          );

        }

      })}

    </div>

  );

};

const SAMPLE_JOURNAL_ENTRIES = [

  // === LOCATIONS ===

  createJournalEntry('locations', {

    id: 'journal-krin-hall',

    name: 'Krin Hall',

    description: 'The central city. Krin Hall has currency and it is possible to pick up professions here.',

    tags: ['City', 'Trade'],

    visibility: 'shared',

  }),

  createJournalEntry('locations', {

    id: 'journal-the-north',

    name: 'The North',

    description: 'Do not go here. DO NOT GO HERE.',

    tags: ['Dangerous'],

    visibility: 'shared',

  }),

  createJournalEntry('locations', {

    id: 'journal-remoliths-cave',

    name: "Remolith's Cave",

    description: "The location of the Remolith (now deceased) and the Spore Druids.",

    tags: ['Cave', 'Dungeon'],

    visibility: 'shared',

  }),



  // === NPCs ===

  createJournalEntry('npcs', {

    id: 'journal-khan',

    name: 'Khan',

    description: 'Profession: Monster Hunter. Khan has traveled with us for a long time and has faced many perils alongside us. He possesses 3 summons: Baskerville, Demon of Gray Lake, and ??.',

    isAlive: true,

    tags: ['Ally', 'Monster Hunter'],

    visibility: 'shared',

  }),

  createJournalEntry('npcs', {

    id: 'journal-mr-necromancer',

    name: 'Mr. Necromancer',

    description: 'Our friend. Giver of the quest "Kill the Druids".',

    isAlive: true,

    questConnections: ['Kill the Druids'],

    tags: ['Ally', 'Quest Giver'],

    visibility: 'shared',

  }),

  createJournalEntry('npcs', {

    id: 'journal-evil-bees',

    name: 'Evil Bees',

    description: 'RIP to the North.',

    isAlive: false,

    locationTags: ['The North'],

    tags: ['Deceased'],

    visibility: 'shared',

  }),



  // === ITEMS ===

  createJournalEntry('items', {

    id: 'journal-mysterious-dagger',

    name: 'Mysterious Dagger',

    description: "Found in the Remolith's Cave. It was hidden along with a note to find the owner's daughter.",

    itemType: 'Weapon',

    rarity: 'rare',

    isQuestItem: true,

    locationTags: ["Remolith's Cave"],

    tags: ['Quest Item', 'Mystery'],

    visibility: 'shared',

  }),

  createJournalEntry('items', {

    id: 'journal-dragon-scales',

    name: 'Dragon Scales',

    description: 'Scales shed from dragons. Used in curing Sharentia and Scremio, and in protective mixtures.',

    itemType: 'Component',

    rarity: 'rare',

    curesDiseases: ['Sharentia', 'Scremio'],

    tags: ['Cure Component', 'Rare'],

    visibility: 'shared',

  }),

  createJournalEntry('items', {

    id: 'journal-hakan-mountain-berries',

    name: 'Hakan Mountain Berries',

    description: 'Berries found in the Hakan Mountains. Mixed with alcohol, they cure Sharentia.',

    itemType: 'Component',

    rarity: 'uncommon',

    curesDiseases: ['Sharentia'],

    tags: ['Cure Component'],

    visibility: 'shared',

  }),

  createJournalEntry('items', {

    id: 'journal-corento-flowers',

    name: 'Corento Flowers',

    description: 'Delicate flowers used in medicinal mixtures. Combined with trace silver and water, they cure Scremio.',

    itemType: 'Component',

    rarity: 'uncommon',

    curesDiseases: ['Scremio'],

    tags: ['Cure Component'],

    visibility: 'shared',

  }),

  createJournalEntry('items', {

    id: 'journal-trace-silver',

    name: 'Trace Silver',

    description: 'Small amounts of purified silver used in alchemical cures. Part of the Scremio cure when mixed with Corento Flowers and water.',

    itemType: 'Component',

    rarity: 'common',

    curesDiseases: ['Scremio'],

    tags: ['Cure Component'],

    visibility: 'shared',

  }),

  createJournalEntry('items', {

    id: 'journal-cactus-juices',

    name: 'Cactus Juices',

    description: 'Extracted from desert cacti. Mixed with wine, it serves as a cure for Crenosis.',

    itemType: 'Component',

    rarity: 'uncommon',

    curesDiseases: ['Crenosis'],

    tags: ['Cure Component'],

    visibility: 'shared',

  }),

  createJournalEntry('items', {

    id: 'journal-gold-dust',

    name: 'Gold',

    description: 'Small amounts of gold used in alchemical mixtures. A key ingredient in cures for Sharentia and Genorona. Should be avoided when treating Scremio and Crenosis.',

    itemType: 'Component',

    rarity: 'common',

    curesDiseases: ['Sharentia', 'Genorona'],

    tags: ['Cure Component', 'Currency'],

    visibility: 'shared',

  }),

  createJournalEntry('items', {

    id: 'journal-mushroom',

    name: 'Mushroom',

    description: 'A common fungus used in recipes.',

    itemType: 'Component',

    rarity: 'common',

    usedInRecipes: ['Cold Potion'],

    tags: ['Ingredient'],

    visibility: 'shared',

  }),



  // === QUESTS ===

  createJournalEntry('quests', {

    id: 'journal-kill-the-druids',

    name: 'Kill the Druids',

    description: 'We hate druids. They are very very evil and wanted to harm the innocent Mr. Necromancer.',

    status: 'active',

    priority: 'high',

    questGiver: 'Mr. Necromancer',

    tags: ['Combat', 'Main'],

    visibility: 'shared',

  }),

  createJournalEntry('quests', {

    id: 'journal-mysterious-dagger-quest',

    name: 'Mysterious Dagger',

    description: "Find the owner's daughter. Given by unknown.",

    status: 'active',

    priority: 'medium',

    questGiver: 'Unknown',

    relatedItems: ['Mysterious Dagger'],

    tags: ['Mystery'],

    visibility: 'shared',

  }),

  createJournalEntry('quests', {

    id: 'journal-steal-wyvern-fang',

    name: 'Steal the Wyvern Fang',

    description: 'Steal the fang from the sleeping Wyvern. Given by the Evil Bees.',

    status: 'active',

    priority: 'medium',

    questGiver: 'Evil Bees',

    tags: ['Stealth', 'Dangerous'],

    visibility: 'shared',

  }),



  // === CREATURES ===

  createJournalEntry('creatures', {

    id: 'journal-wyvern',

    name: 'Wyvern',

    description: 'Very dangerous. Fangs are poisonous.',

    creatureType: 'Dragon',

    size: 'Large',

    abilities: 'Poisonous fangs',

    tags: ['Dangerous', 'Dragon'],

    visibility: 'shared',

  }),

  createJournalEntry('creatures', {

    id: 'journal-demon-gray-lake',

    name: 'Demon of Gray Lake',

    description: "Khan hesitates to summon him, as he might be more dangerous than whatever is trying to kill us.",

    creatureType: 'Fiend',

    size: 'Large',

    tags: ['Summon', 'Dangerous'],

    visibility: 'shared',

  }),

  createJournalEntry('creatures', {

    id: 'journal-baskerville',

    name: 'Baskerville',

    description: "A loyal hound that is Khan's primary summon.",

    creatureType: 'Beast',

    size: 'Medium',

    tags: ['Summon', 'Ally'],

    visibility: 'shared',

  }),



  // === PLANTS ===

  createJournalEntry('plants', {

    id: 'journal-mandrake-root',

    name: 'Mandrake Root',

    description: 'Used in many recipes to cure diseases.',

    plantType: 'Herb',

    rarity: 'uncommon',

    effects: 'Medicinal — key ingredient in cures for Crenosis and Genorona.',

    usedInRecipes: ['Cold Potion'],

    tags: ['Medicinal', 'Cure Component'],

    visibility: 'shared',

  }),



  // === DISEASES ===

  createJournalEntry('diseases', {

    id: 'journal-sharentia',

    name: 'Sharentia',

    description: 'A disease causing stiffened muscles and mood swings. If left untreated, it shortens lifespan by 3-12 years. Passes in 1-8 weeks but must be cured within that window.',

    severity: 'moderate',

    symptoms: ['Very Stiffened Muscles', 'Mood Swings'],

    effects: ['Shortening of lifespan (3-12 years if untreated)'],

    transmission: 'Unknown',

    progression: 'Passes in 1-8 weeks. Must be cured within that time to maintain full lifespan.',

    curedBy: ['Alcohol mixed with Hakan Mountain Berries'],

    creatures: [],

    tags: ['Curable', 'Time-Limited'],

    visibility: 'shared',

  }),

  createJournalEntry('diseases', {

    id: 'journal-scremio',

    name: 'Scremio',

    description: 'A disease causing nosebleeds, necrotic prayers, thinned blood, and high heart rate. Weakens intellect and causes permanent sudden drowsiness if untreated within 1 month. Avoid gold, fruits, hot places, and cold places.',

    severity: 'severe',

    symptoms: ['Blood Coming Out Of Nose', 'Praying Necrotic Prayers', 'Thinned Blood', 'High Heart Rate'],

    effects: ['Weakening of intellect and general thinking abilities', 'Permanent sudden drowsiness if untreated in 1 month'],

    transmission: 'Unknown',

    progression: 'Must be treated within 1 month or effects become permanent.',

    curedBy: ['Dragon Scales', 'Blessing of any Temple', 'Mixture of Corento Flowers, Trace Silver, and Water'],

    creatures: [],

    tags: ['Curable', 'Severe'],

    visibility: 'shared',

  }),

  createJournalEntry('diseases', {

    id: 'journal-crenosis',

    name: 'Crenosis',

    description: 'A deadly necrotic disease causing prayers to dark powers, tissue necrosis, hallucinations of the dead, and visions between the material and immaterial planes. Fatal within a week, with the victim rising as undead. Avoid mixtures with gold.',

    severity: 'deadly',

    symptoms: ['Praying Necrotic Prayers', 'Necrosis', 'Hallucinations (Often of the dead)', 'Seeing between the material and immaterial'],

    effects: ['Death within a week', 'Subsequent resurrection as undead'],

    transmission: 'Unknown',

    progression: 'Death within a week, followed by resurrection as undead.',

    curedBy: ['Mandrake Roots', 'Cactus Juices mixed with Wine'],

    creatures: [],

    tags: ['Deadly', 'Necrotic', 'Urgent'],

    visibility: 'shared',

  }),

  createJournalEntry('diseases', {

    id: 'journal-genorona',

    name: 'Genorona',

    description: 'A mild disease causing mood swings, thinned blood, and extreme happiness. Harmlessly passes in 1 week. Avoid alcohol.',

    severity: 'mild',

    symptoms: ['Mood Swings', 'Thinned Blood', 'Extreme Happiness'],

    effects: ['Harmlessly passes in 1 week'],

    transmission: 'Unknown',

    progression: 'Passes harmlessly in 1 week.',

    curedBy: ['Mandrake Roots', 'Any mixture involving gold'],

    creatures: [],

    tags: ['Mild', 'Self-Resolving'],

    visibility: 'shared',

  }),



  // === RECIPES ===

  createJournalEntry('recipes', {

    id: 'journal-cold-potion',

    name: 'Cold Potion',

    description: 'A simple potion brewed from mushrooms.',

    ingredients: [{ name: 'Mushroom', quantity: 3 }],

    tags: ['Potion', 'Simple'],

    visibility: 'shared',

  }),

];





// ============== END JOURNAL SYSTEM ==============



// Item System

const ITEM_CATEGORIES = {

  misc: { name: 'Misc', icon: '📦', color: '#888', description: 'General items' },

  combat: { name: 'Combat', icon: '⚔️', color: '#5a9fd4', description: 'Weapons and combat gear' },

  quest: { name: 'Quest', icon: '📜', color: '#9b59b6', description: 'Quest-related items' },

  consumable: { name: 'Consumable', icon: '🧪', color: '#27c95a', description: 'Single or limited use items' },

  component: { name: 'Component', icon: '🔧', color: '#c9a227', description: 'Crafting materials' },

};



// Uses reset types

// 2026-04-27: copy revised. "None" / "Permanent" was ambiguous — players

// couldn't tell if it meant "the ability is permanent (always available)"

// or "the uses are permanent (can never be restored)". The new labels

// describe what the *uses* do after depletion.

const USES_RESET_TYPES = {

  none: { name: "Doesn't reset", description: 'Once the uses run out, the ability is gone — no rest restores it.' },

  longRest: { name: 'Long Rest', description: 'Uses refresh after a long rest.' },

  shortRest: { name: 'Short Rest', description: 'Uses refresh after a short or long rest.' },

};



// Create a "uses" object for tracking limited uses on items/abilities

const createUsesTracker = (maxUses, resetType = 'none') => ({

  current: maxUses,

  max: maxUses,

  resetType, // 'permanent', 'longRest', 'shortRest'

});



// Create an ability with optional uses

const createAbility = (name, description = '', effects = [], usesMax = null, usesResetType = 'longRest') => ({

  id: generateId(),

  name,

  description,

  effects, // e.g., ['+1 HP', '+2 Str']

  uses: usesMax ? createUsesTracker(usesMax, usesResetType) : null, // null = unlimited

});



// Combat bonus types for items - advanced integration

const COMBAT_BONUS_TYPES = [

  // Raw ability checks only (not skill checks)

  { type: 'advantage', scope: 'str_checks', label: 'Advantage on STR ability checks (raw only)' },

  { type: 'advantage', scope: 'dex_checks', label: 'Advantage on DEX ability checks (raw only)' },

  { type: 'advantage', scope: 'con_checks', label: 'Advantage on CON ability checks (raw only)' },

  { type: 'advantage', scope: 'int_checks', label: 'Advantage on INT ability checks (raw only)' },

  { type: 'advantage', scope: 'wis_checks', label: 'Advantage on WIS ability checks (raw only)' },

  { type: 'advantage', scope: 'cha_checks', label: 'Advantage on CHA ability checks (raw only)' },

  // All skills under an ability (includes skill checks)

  { type: 'advantage', scope: 'str_skills', label: 'Advantage on all STR-based skills (Athletics)' },

  { type: 'advantage', scope: 'dex_skills', label: 'Advantage on all DEX-based skills (Acrobatics, Stealth, etc.)' },

  { type: 'advantage', scope: 'int_skills', label: 'Advantage on all INT-based skills (Arcana, History, etc.)' },

  { type: 'advantage', scope: 'wis_skills', label: 'Advantage on all WIS-based skills (Perception, Insight, etc.)' },

  { type: 'advantage', scope: 'cha_skills', label: 'Advantage on all CHA-based skills (Persuasion, Deception, etc.)' },

  { type: 'advantage', scope: 'str_saves', label: 'Advantage on STR saves' },

  { type: 'advantage', scope: 'dex_saves', label: 'Advantage on DEX saves' },

  { type: 'advantage', scope: 'con_saves', label: 'Advantage on CON saves' },

  { type: 'advantage', scope: 'int_saves', label: 'Advantage on INT saves' },

  { type: 'advantage', scope: 'wis_saves', label: 'Advantage on WIS saves' },

  { type: 'advantage', scope: 'cha_saves', label: 'Advantage on CHA saves' },

  { type: 'advantage', scope: 'death_saves', label: 'Advantage on Death saves' },

  { type: 'advantage', scope: 'concentration', label: 'Advantage on Concentration saves' },

  { type: 'advantage', scope: 'initiative', label: 'Advantage on Initiative' },

  { type: 'advantage', scope: 'attack_rolls', label: 'Advantage on Attack rolls' },

  { type: 'advantage', scope: 'ranged_attacks', label: 'Advantage on Ranged attacks' },

  { type: 'advantage', scope: 'melee_attacks', label: 'Advantage on Melee attacks' },

  { type: 'advantage', scope: 'stealth', label: 'Advantage on Stealth' },

  { type: 'advantage', scope: 'perception', label: 'Advantage on Perception' },

  { type: 'advantage', scope: 'athletics', label: 'Advantage on Athletics' },

  { type: 'advantage', scope: 'acrobatics', label: 'Advantage on Acrobatics' },

  { type: 'advantage', scope: 'insight', label: 'Advantage on Insight' },

  { type: 'advantage', scope: 'intimidation', label: 'Advantage on Intimidation' },

  { type: 'advantage', scope: 'persuasion', label: 'Advantage on Persuasion' },

  { type: 'advantage', scope: 'deception', label: 'Advantage on Deception' },

  { type: 'advantage', scope: 'survival', label: 'Advantage on Survival' },

  { type: 'advantage', scope: 'investigation', label: 'Advantage on Investigation' },

  { type: 'disadvantage', scope: 'str_checks', label: 'Disadvantage on STR ability checks (raw only)' },

  { type: 'disadvantage', scope: 'dex_checks', label: 'Disadvantage on DEX ability checks (raw only)' },

  { type: 'disadvantage', scope: 'con_checks', label: 'Disadvantage on CON ability checks (raw only)' },

  { type: 'disadvantage', scope: 'int_checks', label: 'Disadvantage on INT ability checks (raw only)' },

  { type: 'disadvantage', scope: 'wis_checks', label: 'Disadvantage on WIS ability checks (raw only)' },

  { type: 'disadvantage', scope: 'cha_checks', label: 'Disadvantage on CHA ability checks (raw only)' },

  { type: 'disadvantage', scope: 'str_saves', label: 'Disadvantage on STR saves' },

  { type: 'disadvantage', scope: 'dex_saves', label: 'Disadvantage on DEX saves' },

  { type: 'disadvantage', scope: 'con_saves', label: 'Disadvantage on CON saves' },

  { type: 'disadvantage', scope: 'int_saves', label: 'Disadvantage on INT saves' },

  { type: 'disadvantage', scope: 'wis_saves', label: 'Disadvantage on WIS saves' },

  { type: 'disadvantage', scope: 'cha_saves', label: 'Disadvantage on CHA saves' },

  { type: 'disadvantage', scope: 'death_saves', label: 'Disadvantage on Death saves' },

  { type: 'disadvantage', scope: 'initiative', label: 'Disadvantage on Initiative' },

  { type: 'disadvantage', scope: 'attack_rolls', label: 'Disadvantage on Attack rolls' },

  { type: 'disadvantage', scope: 'ranged_attacks', label: 'Disadvantage on Ranged attacks' },

  { type: 'disadvantage', scope: 'melee_attacks', label: 'Disadvantage on Melee attacks' },

  { type: 'disadvantage', scope: 'concentration', label: 'Disadvantage on Concentration saves' },

  { type: 'disadvantage', scope: 'stealth', label: 'Disadvantage on Stealth' },

  { type: 'disadvantage', scope: 'perception', label: 'Disadvantage on Perception' },

  { type: 'disadvantage', scope: 'athletics', label: 'Disadvantage on Athletics' },

  { type: 'disadvantage', scope: 'acrobatics', label: 'Disadvantage on Acrobatics' },

  { type: 'disadvantage', scope: 'insight', label: 'Disadvantage on Insight' },

  { type: 'disadvantage', scope: 'intimidation', label: 'Disadvantage on Intimidation' },

  { type: 'disadvantage', scope: 'persuasion', label: 'Disadvantage on Persuasion' },

  { type: 'disadvantage', scope: 'deception', label: 'Disadvantage on Deception' },

  { type: 'disadvantage', scope: 'survival', label: 'Disadvantage on Survival' },

  { type: 'disadvantage', scope: 'investigation', label: 'Disadvantage on Investigation' },

  // 2026-04-27: Supreme advantage (roll 3, take best). Item-equipped sources may

  // grant this; the roller honors it via getBuffAdvantage's supreme flag below.

  { type: 'supreme_advantage', scope: 'attack_rolls', label: 'Supreme Advantage on Attack rolls' },

  { type: 'supreme_advantage', scope: 'all_saves', label: 'Supreme Advantage on All Saves' },

  { type: 'supreme_advantage', scope: 'str_saves', label: 'Supreme Advantage on STR saves' },

  { type: 'supreme_advantage', scope: 'dex_saves', label: 'Supreme Advantage on DEX saves' },

  { type: 'supreme_advantage', scope: 'con_saves', label: 'Supreme Advantage on CON saves' },

  { type: 'supreme_advantage', scope: 'int_saves', label: 'Supreme Advantage on INT saves' },

  { type: 'supreme_advantage', scope: 'wis_saves', label: 'Supreme Advantage on WIS saves' },

  { type: 'supreme_advantage', scope: 'cha_saves', label: 'Supreme Advantage on CHA saves' },

  { type: 'supreme_advantage', scope: 'death_saves', label: 'Supreme Advantage on Death saves' },

  { type: 'supreme_advantage', scope: 'concentration', label: 'Supreme Advantage on Concentration saves' },

  { type: 'supreme_advantage', scope: 'initiative', label: 'Supreme Advantage on Initiative' },

  { type: 'supreme_advantage', scope: 'all_checks', label: 'Supreme Advantage on All Checks' },

  { type: 'supreme_advantage', scope: 'str_skills', label: 'Supreme Advantage on all STR-based skills (Athletics)' },

  { type: 'supreme_advantage', scope: 'dex_skills', label: 'Supreme Advantage on all DEX-based skills (Acrobatics, Stealth, etc.)' },

  { type: 'supreme_advantage', scope: 'int_skills', label: 'Supreme Advantage on all INT-based skills (Arcana, History, etc.)' },

  { type: 'supreme_advantage', scope: 'wis_skills', label: 'Supreme Advantage on all WIS-based skills (Perception, Insight, etc.)' },

  { type: 'supreme_advantage', scope: 'cha_skills', label: 'Supreme Advantage on all CHA-based skills (Persuasion, Deception, etc.)' },

  { type: 'supreme_advantage', scope: 'stealth', label: 'Supreme Advantage on Stealth' },

  { type: 'supreme_advantage', scope: 'perception', label: 'Supreme Advantage on Perception' },

  { type: 'save_bonus', scope: 'all_saves', value: 1, label: '+1 to All Saves' },

  { type: 'save_bonus', scope: 'all_saves', value: 2, label: '+2 to All Saves' },

  { type: 'save_bonus', scope: 'wis_saves', value: 2, label: '+2 to WIS Saves' },

  { type: 'save_bonus', scope: 'con_saves', value: 2, label: '+2 to CON Saves' },

  { type: 'save_bonus', scope: 'dex_saves', value: 2, label: '+2 to DEX Saves' },

  { type: 'passive', scope: 'ac', value: 1, label: '+1 AC' },

  { type: 'passive', scope: 'ac', value: 2, label: '+2 AC' },

  { type: 'passive', scope: 'passive_perception', value: 1, label: '+1 Passive Perception' },

  { type: 'passive', scope: 'passive_perception', value: 2, label: '+2 Passive Perception' },

  { type: 'passive', scope: 'passive_perception', value: 3, label: '+3 Passive Perception' },

  { type: 'passive', scope: 'passive_perception', value: 5, label: '+5 Passive Perception' },

  { type: 'passive', scope: 'passive_investigation', value: 5, label: '+5 Passive Investigation' },

  { type: 'passive', scope: 'passive_insight', value: 5, label: '+5 Passive Insight' },

  { type: 'passive', scope: 'spell_save_dc', value: 1, label: '+1 Spell Save DC' },

  { type: 'passive', scope: 'spell_save_dc', value: 2, label: '+2 Spell Save DC' },

  { type: 'passive', scope: 'spell_attack', value: 1, label: '+1 Spell Attack' },

  { type: 'passive', scope: 'spell_attack', value: 2, label: '+2 Spell Attack' },

  { type: 'passive', scope: 'attack_rolls', value: 1, label: '+1 Attack rolls' },

  { type: 'passive', scope: 'attack_rolls', value: 2, label: '+2 Attack rolls' },

  { type: 'passive', scope: 'melee_attacks', value: 1, label: '+1 Melee attacks' },

  { type: 'passive', scope: 'melee_attacks', value: 2, label: '+2 Melee attacks' },

  { type: 'passive', scope: 'ranged_attacks', value: 1, label: '+1 Ranged attacks' },

  { type: 'passive', scope: 'ranged_attacks', value: 2, label: '+2 Ranged attacks' },

  { type: 'passive', scope: 'initiative', value: 1, label: '+1 Initiative' },

  { type: 'passive', scope: 'initiative', value: 2, label: '+2 Initiative' },

  { type: 'passive', scope: 'initiative', value: 5, label: '+5 Initiative' },

  { type: 'resistance', scope: 'fire', label: 'Resistance: Fire' },

  { type: 'resistance', scope: 'cold', label: 'Resistance: Cold' },

  { type: 'resistance', scope: 'lightning', label: 'Resistance: Lightning' },

  { type: 'resistance', scope: 'poison', label: 'Resistance: Poison' },

  { type: 'resistance', scope: 'necrotic', label: 'Resistance: Necrotic' },

  { type: 'immunity', scope: 'frightened', label: 'Immunity: Frightened' },

  { type: 'immunity', scope: 'charmed', label: 'Immunity: Charmed' },

  { type: 'immunity', scope: 'poisoned', label: 'Immunity: Poisoned' },

];

const AUTHORED_ITEM_MECHANIC_WARNING_TEXT = 'Some authored combat bonuses are preserved on items but are not fully applied by combat yet.';
const ATTACK_ROUTINE_ATTACK_TYPES = [
  { value: 'generic', label: 'Any' },
  { value: 'melee', label: 'Melee' },
  { value: 'ranged', label: 'Ranged' },
];
const normalizeAttackRoutineAttackType = (attackType) => {
  const normalized = String(attackType || '').trim().toLowerCase();
  return normalized === 'melee' || normalized === 'ranged' ? normalized : 'generic';
};
const getAttackRoutineAdvantageScope = (attackType) => {
  const normalized = normalizeAttackRoutineAttackType(attackType);
  if (normalized === 'melee') return 'melee_attacks';
  if (normalized === 'ranged') return 'ranged_attacks';
  return 'attack';
};
const getAttackRoutineTypeLabel = (attackType) => (
  ATTACK_ROUTINE_ATTACK_TYPES.find(option => option.value === normalizeAttackRoutineAttackType(attackType))?.label || 'Any'
);
const SUPPORTED_ITEM_ADVANTAGE_SCOPES = new Set([
  'all_checks',
  'str_checks',
  'dex_checks',
  'con_checks',
  'int_checks',
  'wis_checks',
  'cha_checks',
  'str_skills',
  'dex_skills',
  'int_skills',
  'wis_skills',
  'cha_skills',
  'str_saves',
  'dex_saves',
  'con_saves',
  'int_saves',
  'wis_saves',
  'cha_saves',
  'all_saves',
  'concentration',
  'death_saves',
  'initiative',
  'attack_rolls',
  'ranged_attacks',
  'melee_attacks',
  'stealth',
  'perception',
  'athletics',
  'acrobatics',
  'insight',
  'intimidation',
  'persuasion',
  'deception',
  'survival',
  'investigation',
]);
const SUPPORTED_ITEM_SAVE_BONUS_SCOPES = new Set(['all_saves', 'str_saves', 'dex_saves', 'con_saves', 'int_saves', 'wis_saves', 'cha_saves']);
const SUPPORTED_ITEM_PASSIVE_SCOPES = new Set([
  'ac',
  'passive_perception',
  'passive_investigation',
  'passive_insight',
  'spell_save_dc',
  'spell_attack',
  'attack_rolls',
  'melee_attacks',
  'ranged_attacks',
  'initiative',
]);
const SUPPORTED_ITEM_SUPREME_ADVANTAGE_SCOPES = new Set([
  'all_saves',
  'initiative',
  'attack_rolls',
  'concentration',
  'death_saves',
  'str_saves',
  'dex_saves',
  'con_saves',
  'int_saves',
  'wis_saves',
  'cha_saves',
  'all_checks',
  'str_skills',
  'dex_skills',
  'int_skills',
  'wis_skills',
  'cha_skills',
  'stealth',
  'perception',
]);
const isFullySupportedItemCombatBonus = (bonus = {}) => {
  const type = String(bonus?.type || '').trim().toLowerCase();
  const scope = String(bonus?.scope || '').trim().toLowerCase();
  if (type === 'advantage' || type === 'disadvantage') {
    return SUPPORTED_ITEM_ADVANTAGE_SCOPES.has(scope);
  }
  if (type === 'save_bonus') return SUPPORTED_ITEM_SAVE_BONUS_SCOPES.has(scope);
  if (type === 'passive') return SUPPORTED_ITEM_PASSIVE_SCOPES.has(scope);
  if (type === 'supreme_advantage') return SUPPORTED_ITEM_SUPREME_ADVANTAGE_SCOPES.has(scope);
  return false;
};
const hasPartiallySupportedItemCombatBonuses = (bonuses) => (
  Array.isArray(bonuses) && bonuses.some(bonus => !isFullySupportedItemCombatBonus(bonus))
);



// Create a new item with defaults. `itemId` links an inventory entry back to a library item

// definition (DEFAULT_ITEM_TEMPLATES) for P13 compliance. DM-typed custom items leave it null —

// the stacking logic treats null-itemId entries as legacy/custom and matches them by name,

// while items with itemId match each other by itemId (the Tier A foundation for later shape

// unification in Phase 2 types).

const createItem = (name, category = 'misc', itemId = null) => ({

  id: generateId(),

  itemId,

  name,

  category,

  quantity: 1,

  equipped: false,

  favorited: false,

  buffs: [],

  debuffs: [],

  buffsApplyOnUse: false,

  notes: '',

  uses: null,

  abilities: [],

  questDetails: '',

  questComplete: false,

  onUseEffects: [],

  combatBonuses: [], // Advanced combat integration bonuses

  addedDate: new Date().toISOString(),

});



// Local practice character seed data

const createTestCharacter = () => {

  const testChar = {

    id: 'test-char',

    name: "Ventyl",

    race: 'Half-Elf',

    charClass: 'Ranger',

    level: 5,

    // Session 9 (2026-04-20): default password so prod testers can actually

    // log in as Ventyl without configuring one first. Matches what the demo

    // instructions will tell Kickstarter testers ("password: ventyl").

    password: 'ventyl',

    maxHp: 42,

    currentHp: 38,

    currentMana: 8,

    maxMana: 12,

    buffList: ['+1 Perception', '+1 Str', '+2 Damage'],

    debuffList: ['-1 Dex'],

    permanentBuffs: ['+2 Survival'],

    permanentDebuffs: [],

    buffSources: { '+1 Perception': 'Keen Senses', '+2 Survival': 'Outlander Background', '+1 Str': 'Phantom Greatsword', '+2 Damage': 'Phantom Greatsword', '-1 Dex': 'Phantom Greatsword' },

    buffNotes: {},

    image: null,

    starving: false,

    diseaseList: [

      { diseaseId: 'mind-plague', contractedDate: new Date().toISOString(), showName: false, visibleSymptoms: [0], showEffects: true, showCure: false }

    ],

    diseaseImmunityList: [],

    daysWithoutFood: 0,

    daysToStarvation: 7,

    // New weekly hunger system

    weeklyHungerScore: 1, // Started week well

    currentWeekNumber: 1,

    consecutiveNegativeWeeks: 0,

    negativeWeekStreak: 0,

    weeklyStarvationBonus: 0,

    skillCheckBonus: 0,

    diseaseCheckConPenalty: 0,

    mealLog: [

      { day: 1, mealType: 'Meat', note: 'Rabbit stew' },

      { day: 1, mealType: 'Plants', note: 'Foraged greens' },

      { day: 2, mealType: 'Meat', note: 'River trout' },

      { day: 2, mealType: 'Plants', note: 'Wild berries' },

    ],

    currentDay: 2,

    mealsToday: 2,

    activeDiet: null,

    dietWeekProgress: 0,

    dietDayStreak: 0,

    dietCompletionCounts: {},

    completedDiets: [],

    actionHistory: [],

    abilities: ['Favored Enemy (Beasts)', 'Natural Explorer'],

    feats: ['Sharpshooter'],

    skillProficiencies: ['Stealth', 'Perception', 'Survival', 'Nature', 'Athletics'],

    skillExpertise: ['Stealth'],

    skillMiscMods: { 'Perception': 2 },

    savingThrowProficiencies: ['str', 'dex'],

    armorClass: 15,

    initiative: 0,

    speed: 35,

    armorType: 'medium',

    armorDurability: 22,

    armorName: 'Studded Leather of the Wild',

    inventory: [

      // CONSUMABLE: Health Potion - 3 uses, permanent (consumed)

      {

        id: 'test-health-potion',

        name: 'Health Potion',

        category: 'consumable',

        quantity: 3,

        equipped: false,

        favorited: true,

        buffs: [],

        debuffs: [],

        buffsApplyOnUse: false,

        notes: 'Standard healing potion from the village apothecary',

        uses: { current: 1, max: 1, resetType: 'none' },

        abilities: [],

        questDetails: '',

        questComplete: false,

        onUseEffects: ['+10 HP'],

        addedDate: new Date().toISOString(),

      },

      // CONSUMABLE: Elixir of Focus - grants buff on use

      {

        id: 'test-elixir-focus',

        name: 'Elixir of Focus',

        category: 'consumable',

        quantity: 5,

        equipped: false,

        favorited: true,

        buffs: ['+2 Int', '+1 Wis'],

        debuffs: [],

        buffsApplyOnUse: true,

        notes: 'Lasts until next long rest',

        uses: { current: 1, max: 1, resetType: 'none' },

        abilities: [],

        questDetails: '',

        questComplete: false,

        onUseEffects: ['+5 Mana'],

        addedDate: new Date().toISOString(),

      },

      // CONSUMABLE: Rations - simple consumable

      {

        id: 'test-rations',

        name: 'Trail Rations',

        category: 'consumable',

        quantity: 5,

        equipped: false,

        favorited: false,

        buffs: [],

        debuffs: [],

        buffsApplyOnUse: false,

        notes: 'Dried meat and hardtack',

        uses: { current: 1, max: 1, resetType: 'none' },

        abilities: [],

        questDetails: '',

        questComplete: false,

        onUseEffects: [],

        addedDate: new Date().toISOString(),

      },

      // COMBAT: Greatsword with ability that has uses

      {

        id: 'test-greatsword',

        name: 'Phantom Greatsword',

        category: 'combat',

        quantity: 1,

        equipped: true,

        favorited: true,

        buffs: ['+1 Str', '+2 Damage'],

        debuffs: ['-1 Dex'],

        buffsApplyOnUse: false,

        notes: 'An ethereal blade that phases through armor',

        uses: null,

        abilities: [

          {

            id: 'ability-phantom-strike',

            name: 'Phantom Strike',

            description: 'Phase through enemy armor, ignoring AC bonus from armor',

            effects: ['+5 Damage'],

            uses: { current: 2, max: 2, resetType: 'shortRest' },

          },

          {

            id: 'ability-soul-drain',

            name: 'Soul Drain',

            description: 'Drain life force from a wounded enemy',

            effects: ['+3 HP'],

            uses: { current: 1, max: 1, resetType: 'longRest' },

          },

        ],

        questDetails: '',

        questComplete: false,

        onUseEffects: [],

        addedDate: new Date().toISOString(),

      },

      // COMBAT: Longbow - simple weapon with equip bonus

      {

        id: 'test-longbow',

        name: 'Yew Longbow',

        category: 'combat',

        quantity: 1,

        equipped: false,

        favorited: false,

        buffs: ['+1 Dex'],

        debuffs: [],

        buffsApplyOnUse: false,

        notes: 'Finely crafted elven bow',

        uses: null,

        abilities: [],

        questDetails: '',

        questComplete: false,

        onUseEffects: [],

        addedDate: new Date().toISOString(),

      },

      // COMBAT: Shield with defensive ability

      {

        id: 'test-shield',

        name: 'Bulwark Shield',

        category: 'combat',

        quantity: 1,

        equipped: false,

        favorited: false,

        buffs: ['+2 AC'],

        debuffs: ['-5 MS'],

        buffsApplyOnUse: false,

        notes: 'Heavy tower shield',

        uses: null,

        abilities: [

          {

            id: 'ability-shield-wall',

            name: 'Shield Wall',

            description: 'Brace for impact, reducing all damage taken',

            effects: ['+4 AC', '-10 MS'],

            uses: { current: 3, max: 3, resetType: 'longRest' },

          },

        ],

        questDetails: '',

        questComplete: false,

        onUseEffects: [],

        addedDate: new Date().toISOString(),

      },

      // QUEST: Main story quest item

      {

        id: 'test-quest-amulet',

        name: 'Amulet of the Lost King',

        category: 'quest',

        quantity: 1,

        equipped: false,

        favorited: false,

        buffs: [],

        debuffs: [],

        buffsApplyOnUse: false,

        notes: 'Glows faintly when near undead',

        uses: null,

        abilities: [],

        questDetails: 'Find the tomb of King Aldric in the Shadowed Vale. The amulet is the key to opening the inner sanctum. Speak with the ghost of Sir Brennan at the ruined watchtower for directions.',

        questComplete: false,

        onUseEffects: [],

        addedDate: new Date().toISOString(),

      },

      // QUEST: Completed quest item

      {

        id: 'test-quest-letter',

        name: 'Sealed Letter',

        category: 'quest',

        quantity: 1,

        equipped: false,

        favorited: false,

        buffs: [],

        debuffs: [],

        buffsApplyOnUse: false,

        notes: 'Delivered to Commander Vex',

        uses: null,

        abilities: [],

        questDetails: 'Delivered the urgent message from the frontier outpost.',

        questComplete: true,

        onUseEffects: [],

        addedDate: new Date().toISOString(),

      },

      // COMPONENT: Crafting material

      {

        id: 'test-monster-core',

        name: 'Beast Core',

        category: 'component',

        quantity: 3,

        equipped: false,

        favorited: false,

        buffs: [],

        debuffs: [],

        buffsApplyOnUse: false,

        notes: 'Harvested from magical beasts. Used in enchanting.',

        uses: null,

        abilities: [],

        questDetails: '',

        questComplete: false,

        onUseEffects: [],

        addedDate: new Date().toISOString(),

      },

      // COMPONENT: Metal

      {

        id: 'test-iron-ingot',

        name: 'Iron Ingot',

        category: 'component',

        quantity: 5,

        equipped: false,

        favorited: false,

        buffs: [],

        debuffs: [],

        buffsApplyOnUse: false,

        notes: 'Standard smithing material',

        uses: null,

        abilities: [],

        questDetails: '',

        questComplete: false,

        onUseEffects: [],

        addedDate: new Date().toISOString(),

      },

      // MISC: General item

      {

        id: 'test-rope',

        name: 'Silk Rope',

        category: 'misc',

        quantity: 1,

        equipped: false,

        favorited: false,

        buffs: [],

        debuffs: [],

        buffsApplyOnUse: false,

        notes: '50 feet of strong, lightweight rope',

        uses: null,

        abilities: [],

        questDetails: '',

        questComplete: false,

        onUseEffects: [],

        addedDate: new Date().toISOString(),

      },

      // MISC: Interesting misc item with uses

      {

        id: 'test-grappling-hook',

        name: 'Enchanted Grappling Hook',

        category: 'misc',

        quantity: 1,

        equipped: false,

        favorited: false,

        buffs: [],

        debuffs: [],

        buffsApplyOnUse: false,

        notes: 'Magically returns to hand when tugged twice',

        uses: null,

        abilities: [

          {

            id: 'ability-auto-climb',

            name: 'Auto-Climb',

            description: 'The rope magically pulls you up',

            effects: ['+10 Athletics (climbing)'],

            uses: { current: 2, max: 2, resetType: 'longRest' },

          },

        ],

        questDetails: '',

        questComplete: false,

        onUseEffects: [],

        addedDate: new Date().toISOString(),

      },

    ],

    currentLocation: 'Swamp',

    statusHistory: [

      { timestamp: new Date().toISOString(), day: 2, event: 'well-fed', description: "Day 2: Li'ka is well fed" },

      { timestamp: new Date(Date.now() - 86400000).toISOString(), day: 1, event: 'disease', description: 'Day 1: You feel unwell...' },

      { timestamp: new Date(Date.now() - 86400000).toISOString(), day: 1, event: 'created', description: "Day 1: Li'ka begins her journey" },

    ],

    settings: { 

      ...DEFAULT_SETTINGS, 

      manaEnabled: true, 

      maxMana: 12,

      spellSystem: 'mana',

      showSpellPanel: true,

    },

    immuneToday: false,

    baseStats: { str: 14, dex: 18, con: 12, int: 10, wis: 14, cha: 11 },

    wallet: { copper: 5, silver: 10, gold: 100, platinum: 0 },

    walletVisible: true,

    // ===== SPELL SYSTEM =====

    spells: [

      { ...SAMPLE_SPELLS[0], id: 'spell-bless', prepared: true }, // Bless

      { ...SAMPLE_SPELLS[1], id: 'spell-cure', prepared: true }, // Cure Wounds  

      { ...SAMPLE_SPELLS[2], id: 'spell-heal-word', prepared: true }, // Healing Word

      { ...SAMPLE_SPELLS[3], id: 'spell-guiding', prepared: false }, // Guiding Bolt

      { ...SAMPLE_SPELLS[4], id: 'spell-firebolt', prepared: true }, // Fire Bolt (cantrip)

      createSpell({

        id: 'spell-hunters-mark',

        name: "Hunter's Mark",

        level: 1,

        school: 'Divination',

        castingTime: '1BA',

        range: '90 ft',

        components: 'V',

        duration: 'Concentration, 1 hour',

        concentration: true,

        description: 'You mark a creature as your quarry. Until the spell ends, you deal an extra 1d6 damage to the target whenever you hit it with a weapon attack.',

        effect: '+1d6 damage to marked target',

        dice: '1d6',

        spellType: 'mark',

        targetType: 'single',

        bonusDice: '1d6', // Extra damage dice when attacking marked target

        casterOnly: true, // Only Li'ka gets the bonus damage, not other party members

        higherLevels: 'Duration increases to 8 hours at 3rd, 24 hours at 5th',

        tags: ['Mark', 'Damage'],

        source: 'PHB p.251',

        prepared: true

      }),

      // Example debuff spell that affects damage from all attackers

      createSpell({

        id: 'spell-faerie-fire',

        name: "Faerie Fire",

        level: 1,

        school: 'Evocation',

        castingTime: '1A',

        range: '60 ft',

        components: 'V',

        duration: 'Concentration, 1 minute',

        concentration: true,

        description: 'Each object in a 20-foot cube is outlined in light. Any creature in the area is also outlined if it fails a DEX save. Attacks against affected creatures have advantage.',

        effect: 'Outlined, attacks have advantage',

        spellType: 'debuff',

        targetType: 'single',

        percentIncrease: 15, // +15% damage taken (simulating advantage)

        casterOnly: false, // Everyone benefits from this debuff

        debuffDuration: null, // Lasts until concentration ends

        hitDC: 'save:dex',

        tags: ['Debuff', 'Light'],

        source: 'PHB p.239',

        prepared: true

      }),

    ],

    spellSlots: { 1: 4, 2: 3 }, // Level 5 Ranger-ish

    usedSpellSlots: { 1: 1, 2: 0 }, // Used 1 first level slot

    freeCastsUsedToday: 0,

    manaSickness: false,

    manaSicknessRounds: 0,

    manaDrought: false,

    exhaustionLevel: 0,

    spellcastingModifier: 3, // +3 from Wis 14 + proficiency

    concentratingOn: null,

    concentrationEffectGroupId: null,

    spellHistory: [

      { spellId: 'spell-hunters-mark', castAt: new Date(Date.now() - 3600000).toISOString(), level: 1, manaCost: 1 }

    ],

    // ===== EQUIPMENT DURABILITY =====

    equipment: [

      {

        id: 'equip-longsword',

        name: 'Phantom Greatsword',

        type: 'weapon',

        subtype: 'slashing',

        currentDura: 18,

        maxDura: 26,

        baseMaxDura: 22,

        bonusDura: 4,

        potentialDura: [2, 8],

        maintenance: 'basic',

        equipped: false,

        broken: false,

        unrepairable: false,

        notes: 'Glows faintly purple in moonlight',

      },

      {

        id: 'equip-bow',

        name: 'Elven Longbow',

        type: 'weapon',

        subtype: 'shooting',

        currentDura: 22,

        maxDura: 24,

        baseMaxDura: 15,

        bonusDura: 9,

        potentialDura: [1, 12],

        maintenance: 'great',

        equipped: false,

        broken: false,

        unrepairable: false,

        notes: 'Crafted by master bowyer',

      },

      {

        id: 'equip-studded',

        name: 'Studded Leather of the Wild',

        type: 'armor',

        subtype: 'light',

        currentDura: 22,

        maxDura: 28,

        baseMaxDura: 25,

        bonusDura: 3,

        potentialDura: [1, 5],

        maintenance: 'basic',

        equipped: false,

        broken: false,

        unrepairable: false,

        notes: 'Decorated with forest motifs',

      },

      {

        id: 'equip-buckler',

        name: 'Ranger Buckler',

        type: 'shield',

        subtype: 'buckler',

        currentDura: 14,

        maxDura: 21,

        baseMaxDura: 16,

        bonusDura: 5,

        potentialDura: [5, 7],

        maintenance: 'basic',

        equipped: false,

        broken: false,

        unrepairable: false,

        notes: 'Small wooden shield',

      },

    ],

    equippedWeapon: 'equip-longsword',

    equippedArmor: 'equip-studded',

    equippedShield: null,

    isMonkOrBarbarian: false,

    currentMonth: 1,

  };

  return testChar;

};

const LEGACY_DASHBOARD_TEST_PRESET_KEY = 'ventyl';
const LEGACY_DASHBOARD_TEST_PRESET_CATEGORIES = Object.freeze({
  systems: true,
  items: true,
  crafting: true,
  professions: true,
  buffsDebuffs: true,
  combat: true,
});

const isCampaignTestCharacterPreset = (candidate, presetKey) => {
  if (!isCampaignTestCharacter(candidate)) return false;
  return !presetKey || candidate?.testPresetKey === presetKey;
};



// New Diet System - weekly tracking

const DEFAULT_DIETS = {

  'Carnivore': { 

    name: 'Carnivore', 

    durationWeeks: 10, 

    minMealsPerDay: 2,

    requiredMealTypes: ['Meat'], // All meals must be this type

    allMealsMustMatch: true, // Both meals must be from required types

    completionBuffs: ['+1 Str'],

    maxCompletions: 3,

    description: 'Eat 2+ meals/day, all must be Meat. 10 weeks for +1 Str (max 3x)',

    deletable: false 

  },

  'Vegan': { 

    name: 'Vegan', 

    durationWeeks: 8, 

    minMealsPerDay: 1,

    requiredMealTypes: ['Plants'],

    allMealsMustMatch: true,

    completionBuffs: ['+1 Int/Wis'], // Player chooses

    maxCompletions: 3,

    description: 'Eat 1+ meal/day, all must be Plants. 8 weeks for +1 Int or Wis (max 3 total)',

    deletable: false 

  },

  'Vegetarian': { 

    name: 'Vegetarian', 

    durationWeeks: 5, 

    minMealsPerDay: 2,

    requiredMealTypes: ['Carbs', 'Plants'],

    allMealsMustMatch: true,

    completionBuffs: ['+2 Max HP'],

    maxCompletions: 5,

    description: 'Eat 2+ meals/day, all must be Carbs or Plants. 5 weeks for +2 Max HP (max 5x)',

    deletable: false 

  },

  'Mixed': { 

    name: 'Mixed', 

    durationWeeks: 12, 

    minMealsPerDay: 3,

    requiredMealTypes: ['Meat', 'Carbs', 'Plants'], // Any category allowed

    allMealsMustMatch: false,

    completionBuffs: ['+1 Weekly Starvation Bonus'],

    maxCompletions: 2,

    description: 'Eat 3+ meals/day (any type). 12 weeks for +1 weekly starvation bonus (max +2)',

    deletable: false 

  },

  'Blended': { 

    name: 'Blended', 

    durationWeeks: 16, 

    minMealsPerDay: 2,

    requiredMealTypes: ['Meat', 'Plants'],

    allMealsMustMatch: true,

    completionBuffs: ['+1 All Skill Checks'],

    maxCompletions: 3,

    description: 'Eat 2+ meals/day, all must be Meat or Plants. 16 weeks for +1 skill checks (max 3x)',

    deletable: false 

  },

  'Full Stomach': { 

    name: 'Full Stomach', 

    durationWeeks: 10, 

    minMealsPerDay: 3,

    requiredMealTypes: ['Meat', 'Carbs', 'Plants'],

    allMealsMustMatch: false,

    completionBuffs: ['+3 Max HP', '+1 Con'],

    maxCompletions: 3,

    description: 'Eat 3 meals/day (any type). 10 weeks for +3 Max HP and +1 Con (max 3x)',

    deletable: false 

  }

};



const DEFAULT_DISEASES = {

  'starvation-fever': { id: 'starvation-fever', name: 'Starvation Fever', symptoms: ['Weakness', 'Hallucinations'], effects: ['-2 Str'], cures: [{ item: 'Healing Herb', quantity: 2 }], locations: ['Swamp', 'Forest'] },

  'swamp-rot': { id: 'swamp-rot', name: 'Swamp Rot', symptoms: ['Skin Lesions', 'Fatigue'], effects: ['-1 Con', '-1 Cha'], cures: [{ item: 'Swamp Moss', quantity: 1 }], locations: ['Swamp'] },

  'mind-plague': { id: 'mind-plague', name: 'Mind Plague', symptoms: ['Confusion', 'Memory Loss'], effects: ['-2 Int', '-1 Wis'], cures: [{ item: 'Moonflower', quantity: 1 }], locations: ['Cave', 'Mountain'] }

};





const SYSTEM_TYPE_OPTIONS = [

  { value: 'disease', label: 'Disease', icon: '☣', description: 'Illness, infection, or poison with symptoms and cure' },

  { value: 'curse', label: 'Curse', icon: '💀', description: 'Hex, haunting, or magical affliction' },

  { value: 'blessing', label: 'Blessing', icon: '✨', description: 'Boon, divine favor, or magical enhancement' },

  { value: 'tracker', label: 'Tracker', icon: '📊', description: 'Count or track any value over time' },

  { value: 'secret', label: 'Secret', icon: '🔒', description: 'Hidden information with optional effects' },

  { value: 'oath', label: 'Oath / Restriction', icon: '⛓️', description: 'Behavioral constraint, geas, or binding vow' },

  { value: 'spell-resource', label: 'Spell Resource', icon: '🔮', description: 'Custom magic system with resource-based casting' },

  { value: 'note', label: 'Note Only', icon: '📝', description: 'Text notes with selective reveal - no mechanics' },

  { value: 'item-property', label: 'Item Property', icon: '📦', description: 'Hidden properties attached to an item' },

  { value: 'mark', label: 'Mark / Omen', icon: '👁', description: 'Subtle sign or supernatural indicator' },

  { value: 'condition', label: 'Condition', icon: '🧷', description: 'Status effect or ongoing state' },

  { value: 'affliction', label: 'Affliction', icon: '🩸', description: 'Generic harmful condition' },

  { value: 'boon', label: 'Boon', icon: '🌟', description: 'Generic beneficial effect' },

  { value: 'custom', label: 'Custom', icon: '⚙️', description: 'Build from scratch with full control' },

];



// ============================================================================

// UNIVERSAL SYSTEM CREATOR - EFFECT TYPES & MECHANICS

// ============================================================================



// Effect categories for the Quick Builder

const EFFECT_CATEGORIES = [

  { id: 'stat', label: 'Stat Modifier', icon: '📊', description: 'Modify ability scores or modifiers' },

  { id: 'skill', label: 'Skill Check', icon: '🎯', description: 'Bonus/penalty or advantage/disadvantage on skills' },

  { id: 'save', label: 'Saving Throw', icon: '🛡️', description: 'Modify saving throws' },

  { id: 'attack', label: 'Attack Roll', icon: '⚔️', description: 'Modify attack rolls' },

  { id: 'ac', label: 'Armor Class', icon: '🛡️', description: 'Modify AC' },

  { id: 'hp', label: 'Hit Points', icon: '❤️', description: 'Modify HP (max, current, temp)' },

  { id: 'speed', label: 'Speed', icon: '🏃', description: 'Modify movement speed' },

  { id: 'damage', label: 'Damage', icon: '💥', description: 'Resistance, vulnerability, or immunity' },

  { id: 'condition', label: 'Condition', icon: '⚠️', description: 'Apply standard 5e conditions' },

  { id: 'custom', label: 'Text Only', icon: '📝', description: 'Display text without mechanical effect' },

];



// Ability scores

const ABILITY_SCORES = ['str', 'dex', 'con', 'int', 'wis', 'cha'];

const ABILITY_LABELS = { str: 'Strength', dex: 'Dexterity', con: 'Constitution', int: 'Intelligence', wis: 'Wisdom', cha: 'Charisma' };



// All D&D 5e skills mapped to their ability

const SKILL_ABILITY_MAP = {

  'Acrobatics': 'dex', 'Animal Handling': 'wis', 'Arcana': 'int', 'Athletics': 'str',

  'Deception': 'cha', 'History': 'int', 'Insight': 'wis', 'Intimidation': 'cha',

  'Investigation': 'int', 'Medicine': 'wis', 'Nature': 'int', 'Perception': 'wis',

  'Performance': 'cha', 'Persuasion': 'cha', 'Religion': 'int', 'Sleight of Hand': 'dex',

  'Stealth': 'dex', 'Survival': 'wis'

};



// Damage types for effects

const EFFECT_DAMAGE_TYPES = [

  'Acid', 'Bludgeoning', 'Cold', 'Fire', 'Force', 'Lightning', 

  'Necrotic', 'Piercing', 'Poison', 'Psychic', 'Radiant', 'Slashing', 'Thunder'

];



// Default death save configuration

const DEFAULT_DEATH_SAVE_CONFIG = {

  successesNeeded: 3,

  failuresNeeded: 3,

  nat20Behavior: 'revive',     // 'revive' | 'two_successes' | 'one_success'

  nat20ReviveHp: 1,

  nat1Behavior: 'two_failures', // 'two_failures' | 'one_failure'

  damageAtZeroFailures: 1,

  critAtZeroFailures: 2,

  reviveHpMode: 'heal_amount', // 'heal_amount' | 'fixed'

  fixedReviveHp: 15,

  autoApplyConditions: true,

  concentrationCheckOnDamage: true,

  hotTickTiming: 'per_spell',  // 'per_spell'|'start_of_round'|'end_of_round'|'start_of_caster_turn'|'end_of_caster_turn'|'start_of_target_turn'|'end_of_target_turn'

  dotTickTiming: 'per_spell',  // same options

  healingCheck: {

      enabled: true,

      shieldName: "Death's Barrier",

      shieldHp: 10,

      maxChecks: 1,

      optional: false,

      checkReplacesDeathSave: true,

    },

};



// Standard D&D conditions

const DND_CONDITIONS = [

  { id: 'blinded', name: 'Blinded', description: "Can't see, auto-fail sight checks, attacks have disadvantage, attacks against have advantage" },

  { id: 'charmed', name: 'Charmed', description: "Can't attack charmer, charmer has advantage on social checks" },

  { id: 'deafened', name: 'Deafened', description: "Can't hear, auto-fail hearing checks" },

  { id: 'frightened', name: 'Frightened', description: "Disadvantage on checks/attacks while source visible, can't willingly move closer" },

  { id: 'grappled', name: 'Grappled', description: "Speed is 0, ends if grappler incapacitated or forced apart" },

  { id: 'incapacitated', name: 'Incapacitated', description: "Can't take actions or reactions" },

  { id: 'invisible', name: 'Invisible', description: "Impossible to see without magic, attacks against have disadvantage, attacks have advantage" },

  { id: 'paralyzed', name: 'Paralyzed', description: "Incapacitated, can't move or speak, auto-fail STR/DEX saves, attacks have advantage, hits within 5ft are crits" },

  { id: 'petrified', name: 'Petrified', description: "Transformed to stone, incapacitated, unaware, resistance to all damage" },

  { id: 'poisoned', name: 'Poisoned', description: "Disadvantage on attack rolls and ability checks" },

  { id: 'prone', name: 'Prone', description: "Crawl to move, disadvantage on attacks, attacks within 5ft have advantage, ranged attacks have disadvantage" },

  { id: 'restrained', name: 'Restrained', description: "Speed 0, attacks have disadvantage, attacks against have advantage, disadvantage on DEX saves" },

  { id: 'stunned', name: 'Stunned', description: "Incapacitated, can't move, speak falteringly, auto-fail STR/DEX saves, attacks have advantage" },

  { id: 'unconscious', name: 'Unconscious', description: "Incapacitated, can't move/speak, unaware, drop held items, fall prone, auto-fail STR/DEX saves, attacks have advantage, hits within 5ft are crits" },

];



// Operation types for effects

const EFFECT_OPERATIONS = {

  bonus: { label: 'Bonus (+/-)', symbol: '+/-' },

  advantage: { label: 'Advantage', symbol: 'ADV' },

  disadvantage: { label: 'Disadvantage', symbol: 'DIS' },

  set: { label: 'Set to value', symbol: '=' },

  resistance: { label: 'Resistance (half)', symbol: '½' },

  vulnerability: { label: 'Vulnerability (double)', symbol: '×2' },

  immunity: { label: 'Immunity', symbol: '∅' },

};



// Create a mechanical effect object

const createMechanicalEffect = (category, target, operation, value = null, condition = null) => ({

  type: category,

  target,

  operation,

  value,

  condition,

});



// Parse effect syntax into mechanical effect (for power users)

const parseEffectSyntax = (syntax) => {

  const s = syntax.trim().toLowerCase();

  const effects = [];

  

  // Split by comma for multiple effects

  const parts = s.split(',').map(p => p.trim()).filter(Boolean);

  

  for (const part of parts) {

    // Stat modifiers: str+2, str-2, str=10

    const statMatch = part.match(/^(str|dex|con|int|wis|cha)([\+\-\=])(\d+)$/);

    if (statMatch) {

      const [, stat, op, val] = statMatch;

      effects.push(createMechanicalEffect('stat', stat, op === '=' ? 'set' : 'bonus', op === '-' ? -parseInt(val) : parseInt(val)));

      continue;

    }

    

    // Skill bonus: perception+5, stealth-2

    const skillBonusMatch = part.match(/^([a-z\s]+)([\+\-])(\d+)$/);

    if (skillBonusMatch) {

      const [, skill, op, val] = skillBonusMatch;

      const skillName = Object.keys(SKILL_ABILITY_MAP).find(sk => sk.toLowerCase().includes(skill));

      if (skillName) {

        effects.push(createMechanicalEffect('skill', skillName, 'bonus', op === '-' ? -parseInt(val) : parseInt(val)));

        continue;

      }

    }

    

    // Skill advantage/disadvantage: perception.adv, stealth.dis

    const skillAdvMatch = part.match(/^([a-z\s]+)\.(adv|dis)$/);

    if (skillAdvMatch) {

      const [, skill, advDis] = skillAdvMatch;

      const skillName = Object.keys(SKILL_ABILITY_MAP).find(sk => sk.toLowerCase().includes(skill));

      if (skillName) {

        effects.push(createMechanicalEffect('skill', skillName, advDis === 'adv' ? 'advantage' : 'disadvantage'));

        continue;

      }

    }

    

    // Save modifiers: save.con+2, save.all-1, save.all.dis

    const saveMatch = part.match(/^save\.(str|dex|con|int|wis|cha|all)([\+\-])(\d+)$/);

    if (saveMatch) {

      const [, stat, op, val] = saveMatch;

      effects.push(createMechanicalEffect('save', stat, 'bonus', op === '-' ? -parseInt(val) : parseInt(val)));

      continue;

    }

    const saveAdvMatch = part.match(/^save\.(str|dex|con|int|wis|cha|all)\.(adv|dis)$/);

    if (saveAdvMatch) {

      const [, stat, advDis] = saveAdvMatch;

      effects.push(createMechanicalEffect('save', stat, advDis === 'adv' ? 'advantage' : 'disadvantage'));

      continue;

    }

    

    // Attack modifiers: atk.all-1, atk.melee.adv

    const atkMatch = part.match(/^atk\.(all|melee|ranged|spell)([\+\-])(\d+)$/);

    if (atkMatch) {

      const [, target, op, val] = atkMatch;

      effects.push(createMechanicalEffect('attack', target, 'bonus', op === '-' ? -parseInt(val) : parseInt(val)));

      continue;

    }

    const atkAdvMatch = part.match(/^atk\.(all|melee|ranged|spell)\.(adv|dis)$/);

    if (atkAdvMatch) {

      const [, target, advDis] = atkAdvMatch;

      effects.push(createMechanicalEffect('attack', target, advDis === 'adv' ? 'advantage' : 'disadvantage'));

      continue;

    }

    

    // AC: ac+2, ac-1

    const acMatch = part.match(/^ac([\+\-])(\d+)$/);

    if (acMatch) {

      const [, op, val] = acMatch;

      effects.push(createMechanicalEffect('ac', 'base', 'bonus', op === '-' ? -parseInt(val) : parseInt(val)));

      continue;

    }

    

    // Speed: speed-10, speed=0

    const speedMatch = part.match(/^speed([\+\-\=])(\d+)$/);

    if (speedMatch) {

      const [, op, val] = speedMatch;

      effects.push(createMechanicalEffect('speed', 'walk', op === '=' ? 'set' : 'bonus', op === '-' ? -parseInt(val) : parseInt(val)));

      continue;

    }

    

    // Damage resistance/vulnerability/immunity: dmg.fire.resist, dmg.cold.vuln, dmg.poison.immune

    const dmgMatch = part.match(/^dmg\.([a-z]+)\.(resist|vuln|immune)$/);

    if (dmgMatch) {

      const [, dmgType, mod] = dmgMatch;

      const dmgName = EFFECT_DAMAGE_TYPES.find(d => d.toLowerCase().startsWith(dmgType));

      if (dmgName) {

        const op = mod === 'resist' ? 'resistance' : mod === 'vuln' ? 'vulnerability' : 'immunity';

        effects.push(createMechanicalEffect('damage', dmgName, op));

        continue;

      }

    }

    

    // Conditions: cond.poisoned, cond.prone

    const condMatch = part.match(/^cond\.([a-z]+)$/);

    if (condMatch) {

      const [, condName] = condMatch;

      const condition = DND_CONDITIONS.find(c => c.id.startsWith(condName));

      if (condition) {

        effects.push(createMechanicalEffect('condition', condition.id, 'apply'));

        continue;

      }

    }

    

    // Exhaustion: exhaust=2, exhaust+1

    const exhaustMatch = part.match(/^exhaust([\+\-\=])(\d+)$/);

    if (exhaustMatch) {

      const [, op, val] = exhaustMatch;

      effects.push(createMechanicalEffect('condition', 'exhaustion', op === '=' ? 'set' : 'bonus', parseInt(val)));

      continue;

    }

  }

  

  return effects;

};



// Format mechanical effect for display

const formatEffectDisplay = (effect) => {

  if (!effect) return '';

  const { type, target, operation, value } = effect;

  

  switch (type) {

    case 'stat':

      const statName = ABILITY_LABELS[target] || target.toUpperCase();

      if (operation === 'set') return `${statName} = ${value}`;

      return `${value >= 0 ? '+' : ''}${value} ${statName}`;

    

    case 'skill':

      if (operation === 'advantage') return `Advantage on ${target}`;

      if (operation === 'disadvantage') return `Disadvantage on ${target}`;

      return `${value >= 0 ? '+' : ''}${value} to ${target}`;

    

    case 'save':

      const saveTarget = target === 'all' ? 'all saving throws' : `${target.toUpperCase()} saves`;

      if (operation === 'advantage') return `Advantage on ${saveTarget}`;

      if (operation === 'disadvantage') return `Disadvantage on ${saveTarget}`;

      return `${value >= 0 ? '+' : ''}${value} to ${saveTarget}`;

    

    case 'attack':

      const atkTarget = target === 'all' ? 'attack rolls' : `${target} attacks`;

      if (operation === 'advantage') return `Advantage on ${atkTarget}`;

      if (operation === 'disadvantage') return `Disadvantage on ${atkTarget}`;

      return `${value >= 0 ? '+' : ''}${value} to ${atkTarget}`;

    

    case 'ac':

      if (operation === 'set') return `AC = ${value}`;

      return `${value >= 0 ? '+' : ''}${value} AC`;

    

    case 'hp':

      if (target === 'max') return `${value >= 0 ? '+' : ''}${value} Max HP`;

      if (target === 'temp') return `+${value} Temp HP`;

      return `${value >= 0 ? '+' : ''}${value} HP`;

    

    case 'speed':

      if (operation === 'set') return `Speed = ${value} ft`;

      return `${value >= 0 ? '+' : ''}${value} ft Speed`;

    

    case 'damage':

      if (operation === 'resistance') return `Resistance to ${target} damage`;

      if (operation === 'vulnerability') return `Vulnerability to ${target} damage`;

      if (operation === 'immunity') return `Immunity to ${target} damage`;

      return `${value >= 0 ? '+' : ''}${value} ${target} damage`;

    

    case 'condition':

      if (target === 'exhaustion') return `Exhaustion ${operation === 'set' ? '=' : '+'}${value}`;

      const cond = DND_CONDITIONS.find(c => c.id === target);

      return cond ? cond.name : target;

    

    default:

      return effect.displayText || 'Unknown effect';

  }

};



// Preset templates for quick system creation

const SYSTEM_PRESETS = {

  disease: {

    type: 'disease',

    defaultSections: [

      { id: 'description', label: 'Description', kind: 'text', text: '', dmOnly: false },

      { id: 'symptoms', label: 'Symptoms', kind: 'list', items: [], dmOnly: false },

      { id: 'dm-notes', label: 'DM Notes', kind: 'text', text: '', dmOnly: true },

    ],

    defaultVisibility: { showName: false, showDescription: false, showEffects: false, showCure: false },

    hasEffects: true,

    hasCure: true,

    hasClues: true,

  },

  curse: {

    type: 'curse',

    defaultSections: [

      { id: 'description', label: 'Description', kind: 'text', text: '', dmOnly: false },

      { id: 'origin', label: 'Origin', kind: 'text', text: '', dmOnly: false },

      { id: 'dm-notes', label: 'DM Notes', kind: 'text', text: '', dmOnly: true },

    ],

    defaultVisibility: { showName: true, showDescription: false, showEffects: false, showCure: false },

    hasEffects: true,

    hasCure: true,

    hasClues: true,

  },

  blessing: {

    type: 'blessing',

    defaultSections: [

      { id: 'description', label: 'Description', kind: 'text', text: '', dmOnly: false },

      { id: 'source', label: 'Source', kind: 'text', text: '', dmOnly: false },

    ],

    defaultVisibility: { showName: true, showDescription: true, showEffects: true, showCure: false },

    hasEffects: true,

    hasCure: false,

    hasClues: false,

  },

  tracker: {

    type: 'tracker',

    defaultSections: [

      { id: 'description', label: 'Description', kind: 'text', text: '', dmOnly: false },

      { id: 'thresholds', label: 'Threshold Effects', kind: 'list', items: [], dmOnly: true },

    ],

    defaultTrackerFields: [

      { id: 'main-counter', type: 'counter', label: 'Count', value: 0, min: 0, max: null, step: 1, showToPlayer: true, color: '#c9a227' }

    ],

    defaultVisibility: { showName: true, showDescription: true, showEffects: true, showCure: false },

    hasEffects: true,

    hasCure: false,

    hasClues: false,

    hasCustomData: true,

  },

  secret: {

    type: 'secret',

    defaultSections: [

      { id: 'dm-notes', label: 'DM Notes', kind: 'text', text: '', dmOnly: true },

      { id: 'revelation', label: 'Revelation Text', kind: 'text', text: '', dmOnly: false },

    ],

    defaultVisibility: { showName: false, showDescription: false, showEffects: false, showCure: false, playerVisible: false },

    hasEffects: true,

    hasCure: false,

    hasClues: true,

  },

  note: {

    type: 'note',

    defaultSections: [

      { id: 'dm-notes', label: 'DM Notes', kind: 'text', text: '', dmOnly: true },

      { id: 'player-notes', label: 'Player Notes', kind: 'text', text: '', dmOnly: false },

    ],

    defaultVisibility: { showName: false, showDescription: false, showEffects: false, showCure: false, playerVisible: false },

    hasEffects: false,

    hasCure: false,

    hasClues: true,

  },

  condition: {

    type: 'condition',

    defaultSections: [

      { id: 'description', label: 'Description', kind: 'text', text: '', dmOnly: false },

      { id: 'duration', label: 'Duration / End Condition', kind: 'text', text: '', dmOnly: false },

      { id: 'dm-notes', label: 'DM Notes', kind: 'text', text: '', dmOnly: true },

    ],

    defaultVisibility: { showName: true, showDescription: true, showEffects: true, showCure: false },

    hasEffects: true,

    hasCure: false,

    hasClues: false,

  },

  boon: {

    type: 'boon',

    defaultSections: [

      { id: 'description', label: 'Description', kind: 'text', text: '', dmOnly: false },

      { id: 'source', label: 'Source', kind: 'text', text: '', dmOnly: false },

      { id: 'dm-notes', label: 'DM Notes', kind: 'text', text: '', dmOnly: true },

    ],

    defaultVisibility: { showName: true, showDescription: true, showEffects: true, showCure: false },

    hasEffects: true,

    hasCure: false,

    hasClues: false,

  },

  affliction: {

    type: 'affliction',

    defaultSections: [

      { id: 'description', label: 'Description', kind: 'text', text: '', dmOnly: false },

      { id: 'stages', label: 'Progression Stages', kind: 'stages', items: [

        'Stage 1: Onset | Mild symptoms begin to appear | -1 Con',

        'Stage 2: Worsening | Symptoms intensify significantly | -2 Con',

        'Stage 3: Critical | Life-threatening condition | -3 Con, Exhaustion 1',

      ], currentStage: 0, dmOnly: false },

      { id: 'dm-notes', label: 'DM Notes', kind: 'text', text: '', dmOnly: true },

    ],

    defaultVisibility: { showName: false, showDescription: false, showEffects: false, showCure: false },

    hasEffects: true,

    hasCure: true,

    hasClues: true,

    hasStages: true,

  },

  'item-property': {

    type: 'item-property',

    defaultSections: [

      { id: 'description', label: 'Description', kind: 'text', text: '', dmOnly: false },

      { id: 'reveal-condition', label: 'Reveal Condition', kind: 'text', text: '', dmOnly: true },

    ],

    defaultVisibility: { showName: false, showDescription: false, showEffects: false, showCure: false },

    hasEffects: true,

    hasCure: false,

    hasClues: true,

    attachTo: 'item',

  },

  mark: {

    type: 'mark',

    defaultSections: [

      { id: 'description', label: 'Description', kind: 'text', text: '', dmOnly: false },

      { id: 'dm-notes', label: 'DM Notes', kind: 'text', text: '', dmOnly: true },

    ],

    defaultVisibility: { showName: false, showDescription: false, showEffects: false, showCure: false },

    hasEffects: true,

    hasCure: true,

    hasClues: true,

  },

  custom: {

    type: 'custom',

    defaultSections: [],

    defaultVisibility: { showName: true, showDescription: true, showEffects: true, showCure: true },

    hasEffects: true,

    hasCure: true,

    hasClues: true,

    hasCustomData: true,

  },

  oath: {

    type: 'oath',

    defaultSections: [

      { id: 'terms', label: 'Oath Terms', kind: 'text', text: '', dmOnly: false },

      { id: 'restrictions', label: 'Restrictions', kind: 'list', items: [], dmOnly: false },

      { id: 'consequences', label: 'Breaking Consequences', kind: 'text', text: '', dmOnly: true },

      { id: 'dm-notes', label: 'DM Notes', kind: 'text', text: '', dmOnly: true },

    ],

    defaultVisibility: { showName: true, showDescription: false, showEffects: false, showCure: false },

    hasEffects: true,

    hasCure: true,

    hasClues: false,

    hasOathTerms: true,

  },

  'spell-resource': {

    type: 'spell-resource',

    defaultSections: [

      { id: 'description', label: 'Description', kind: 'text', text: '', dmOnly: false },

      { id: 'casting-rules', label: 'Casting Rules', kind: 'text', text: '', dmOnly: false },

      { id: 'spell-list', label: 'Available Spells', kind: 'list', items: [], dmOnly: false },

      { id: 'dm-notes', label: 'DM Notes', kind: 'text', text: '', dmOnly: true },

    ],

    defaultTrackerFields: [

      { id: 'resource-pool', type: 'progress', label: 'Power Reserve', value: 0, min: 0, max: 100, step: 1, showToPlayer: true, color: '#9b59b6' }

    ],

    defaultResourceProfiles: [

      {

        id: 'profile-resource-pool',

        kind: 'scalar',

        name: 'Spell Resource',

        visibleLabel: 'Resource',

        scalar: {

          trackerId: 'resource-pool',

          min: 0,

          maxSource: 'tracker',

        },

      },

    ],

    defaultDirectSpellBindings: [

      {

        id: 'binding-all-spells',

        assignmentMode: 'all-spells',

        resourceProfileId: 'profile-resource-pool',

        castSurface: 'both',

      },

    ],

    defaultVisibility: { showName: true, showDescription: true, showEffects: true, showCure: false },

    hasEffects: true,

    hasCure: false,

    hasClues: false,

    hasCustomData: true,

    hasSpells: true,

  },

};



// ============================================================================

// AUTOMATION RULES ENGINE - Triggers, Conditions, Actions

// ============================================================================



// Available triggers for automation rules

const RULE_TRIGGERS = [

  { id: 'on-apply', label: 'When Applied', description: 'Fires when system is first applied to character' },

  { id: 'on-remove', label: 'When Removed', description: 'Fires when system is removed from character' },

  { id: 'on-rest-short', label: 'On Short Rest', description: 'Fires when character takes a short rest' },

  { id: 'on-rest-long', label: 'On Long Rest', description: 'Fires when character takes a long rest' },

  { id: 'on-day-start', label: 'On Day Start', description: 'Fires at the start of each day' },

  { id: 'on-week-end', label: 'On Week End', description: 'Fires at the end of each week' },

  { id: 'on-damage', label: 'On Damage Taken', description: 'Fires when character takes damage' },

  { id: 'on-heal', label: 'On Healing', description: 'Fires when character is healed' },

  { id: 'on-tracker-change', label: 'On Tracker Value Change', description: 'Fires when a tracker value changes' },

  { id: 'on-threshold', label: 'On Threshold Crossed', description: 'Fires when tracker crosses a threshold' },

  { id: 'manual', label: 'Manual Trigger', description: 'Fires only when DM clicks a button' },

  // --- Spell-resource architecture (Session 2). No callers fire these yet; Session 4 wires them.

  // See docs/spell-resource-architecture.md §4.1.

  { id: 'on-cast', label: 'On Technique Cast', description: 'Fires when a bound technique is cast (any outcome)' },

  { id: 'on-cast-success', label: 'On Cast Success', description: 'Fires when a cast lands (attack hit or save failed)' },

  { id: 'on-cast-fail', label: 'On Cast Fail', description: 'Fires when a cast misses or target saves' },

  { id: 'on-condition-gained', label: 'On Condition Gained', description: 'Fires when a condition/debuff is added' },

  { id: 'on-condition-lost', label: 'On Condition Lost', description: 'Fires when a condition/debuff is removed' },

  { id: 'on-resource-spend', label: 'On Resource Spent', description: 'Fires when a resource is consumed' },

  { id: 'on-resource-gain', label: 'On Resource Gained', description: 'Fires when a resource is gained' },

  // --- Cross-subsystem event emission (Systems expansion Session 2).

  // See docs/systems-design-expansion.md §Session-2.

  // `on-spell-cast` is distinct from `on-cast` (which is technique-bound); fires from the

  // spell-panel cast flow with triggerContext { spellId, spellName, castLevel }.

  // `on-enemy-killed` fires once per kill with triggerContext { name, type, enemyId }.

  // `on-combat-end` fires once per party char with triggerContext { killedEnemies, won, surrendered }.

  { id: 'on-spell-cast', label: 'On Spell Cast', description: 'Fires when character casts a spell from their spell list (distinct from technique casts)' },

  { id: 'on-enemy-killed', label: 'On Enemy Killed', description: 'Fires when an enemy combatant drops to 0 HP' },

  { id: 'on-combat-end', label: 'On Combat End', description: 'Fires once per party member when combat ends' },

  // --- System lifecycle (Systems expansion Session 4).

  // on-turn-end fires for the char whose turn just ended; on-round-end fires for every

  // party char at round boundary. Dispatched from CombatPage.nextTurn.

  { id: 'on-turn-end', label: 'On Turn End', description: 'Fires when the owning character\'s combat turn ends' },

  { id: 'on-round-end', label: 'On Round End', description: 'Fires for every party member at the end of each combat round' },

  // --- NPC state events (Systems expansion Session 5).

  // Fires for every party char whose systems might care, with triggerContext { npcId, npcName, prevStatus }.

  { id: 'on-npc-death', label: 'On NPC Death', description: 'Fires for every party character when an NPC\'s status transitions to dead' },

];



// Set of triggers that expect `context.triggerContext.techniqueId` and should gather rules

// from every technique's `rules[]` in addition to the system's `automationRules[]`.

// processAllSystemRulesForTrigger consults this set. Keep this in sync with RULE_TRIGGERS.

const CAST_RELATED_TRIGGERS = new Set(['on-cast', 'on-cast-success', 'on-cast-fail']);



// Available conditions for automation rules

const RULE_CONDITIONS = [

  { id: 'always', label: 'Always', description: 'No condition - always fires' },

  { id: 'stat-check', label: 'Stat Check', description: 'Compare an ability score', params: ['stat', 'operator', 'value'] },

  { id: 'hp-percent', label: 'HP Percentage', description: 'Check current HP %', params: ['operator', 'value'] },

  { id: 'tracker-value', label: 'Tracker Value', description: 'Check a tracker value', params: ['trackerId', 'operator', 'value'] },

  { id: 'has-system', label: 'Has System', description: 'Check if character has another system', params: ['systemId'] },

  { id: 'roll-check', label: 'Roll Check', description: 'Make a roll (e.g., DC 15 Con save)', params: ['rollType', 'dc', 'stat'] },

  { id: 'random-chance', label: 'Random Chance', description: 'Percentage chance', params: ['percent'] },

  // --- Spell-resource architecture (Session 2). See docs/spell-resource-architecture.md §4.2.

  { id: 'has-resource', label: 'Has Resource', description: 'Check resource availability (HP, mana, tracker). Supports cross-system trackers via systemId.', params: ['resource', 'amount', 'operator', 'systemId', 'trackerId'] },

  { id: 'technique-is', label: 'Technique Is', description: 'For on-cast rules: check which technique triggered', params: ['techniqueId'] },

  { id: 'cast-time-of-day', label: 'Time of Day', description: 'Gate by world time of day at cast', params: ['timeOfDay'] },

  { id: 'environment-is', label: 'Environment Is', description: 'Gate by currentLocation', params: ['environment'] },

  { id: 'concentrating-on', label: 'Concentrating', description: 'Check if character is concentrating (any, or on a specific spell)', params: ['spellName', 'any'] },

  // --- Cross-cutting condition primitives (Systems expansion Session 1).

  // See docs/systems-design-expansion.md §Session-1. Read from char collections

  // and worldContext (chars, journal) threaded in at dispatch sites.

  { id: 'has-disease', label: 'Has Disease', description: 'Character has the named disease active', params: ['diseaseId'] },

  { id: 'has-item', label: 'Has Item', description: 'Character has item in inventory (by name or definition id, optionally equipped)', params: ['itemName', 'itemId', 'equipped'] },

  { id: 'quest-status', label: 'Quest Status', description: 'A quest entry in the journal has the specified status', params: ['questName', 'status'] },

  { id: 'party-has-system', label: 'Party Has System', description: 'Another party member has the named system applied', params: ['systemId', 'excludeSelf'] },

  // --- Cross-subsystem conditions (Systems expansion Session 2).

  // Reads from triggerContext populated by on-enemy-killed / on-combat-end emitters.

  { id: 'enemy-name-matches', label: 'Enemy Name Matches', description: 'For on-enemy-killed / on-combat-end rules: check the killed enemy by name', params: ['name'] },

  // --- NPC state conditions (Systems expansion Session 5).

  // Read from worldContext.npcLibrary. NPC matching supports either npcId or npcName.

  { id: 'npc-alive', label: 'NPC Alive', description: 'An NPC (by id or name) has status alive — or optionally matches a specific status', params: ['npcId', 'npcName', 'status'] },

  { id: 'npc-disposition', label: 'NPC Disposition', description: 'An NPC\'s disposition compared numerically (hostile=1, neutral=2, friendly=3, unknown=0)', params: ['npcId', 'npcName', 'operator', 'value'] },

];



// Resource lookup helper — reads the current value of a resource for condition/action code.

// Used by `has-resource` condition and `consume-resource` action for consistent semantics.

// resource types: 'hp' | 'temp-hp' | 'mana' | 'tracker' | 'tracker-stack' | 'exhaustion' | 'hit-die'.

// For 'tracker' types, systemId + trackerId are looked up on char.systemList. If systemId is

// omitted, the current system in scope (from context.systemInstance) is used.

const getResourceValue = (char, resource, { systemId, trackerId, systemList, currentInstance } = {}) => {

  if (!char) return 0;

  switch (resource) {

    case 'hp':        return char.currentHp ?? char.hp ?? 0;

    case 'temp-hp':   return char.tempHp ?? 0;

    case 'mana':      return char.currentMana ?? 0;

    case 'exhaustion': return char.exhaustionLevel ?? 0;

    case 'hit-die':   return (char.hitDice?.current) ?? char.hitDiceRemaining ?? 0;

    case 'tracker':

    case 'tracker-stack': {

      // If caller specified a systemId, find that instance in systemList. Otherwise use the

      // current instance from context (same-system tracker access).

      const list = systemList || char.systemList || [];

      const inst = systemId

        ? list.find(s => (s.systemId || s.id) === systemId)

        : currentInstance;

      const tv = inst?.trackerValues || {};

      return tv[trackerId] ?? 0;

    }

    default: return 0;

  }

};



// Available actions for automation rules

const RULE_ACTIONS = [

  { id: 'add-effect', label: 'Add Effect', description: 'Add a buff/debuff effect (optional kind: buff/debuff, permanent: true for permanent lists)', params: ['effect', 'duration', 'kind', 'permanent'] },

  { id: 'remove-effect', label: 'Remove Effect', description: 'Remove a specific effect (mirrors add-effect routing via kind/permanent)', params: ['effect', 'kind', 'permanent'] },

  { id: 'modify-stat', label: 'Modify Stat', description: 'Temporarily modify an ability score', params: ['stat', 'value'] },

  { id: 'deal-damage', label: 'Deal Damage', description: 'Deal damage to the character', params: ['amount', 'type'] },

  { id: 'heal', label: 'Heal', description: 'Heal the character', params: ['amount'] },

  { id: 'add-exhaustion', label: 'Add Exhaustion', description: 'Add exhaustion levels', params: ['levels'] },

  { id: 'remove-exhaustion', label: 'Remove Exhaustion', description: 'Remove exhaustion levels', params: ['levels'] },

  { id: 'change-tracker', label: 'Change Tracker', description: 'Modify a tracker value', params: ['trackerId', 'operation', 'value'] },

  { id: 'reveal-clue', label: 'Reveal Clue', description: 'Reveal the next hidden clue', params: [] },

  { id: 'reveal-section', label: 'Reveal Section', description: 'Reveal a section or item', params: ['sectionId', 'itemIndex'] },

  { id: 'advance-stage', label: 'Advance Stage', description: 'Move to next progression stage', params: [] },

  { id: 'remove-system', label: 'Remove System', description: 'Remove this system from the character', params: [] },

  { id: 'apply-system', label: 'Apply System', description: 'Apply another system to the character. Optional `slots` bag fills template instanceSlots programmatically.', params: ['systemId', 'slots'] },

  { id: 'notify', label: 'Send Notification', description: 'Show a notification message', params: ['message', 'toPlayer'] },

  { id: 'log', label: 'Add to Log', description: 'Add an entry to the audit log', params: ['message'] },

  // --- Spell-resource architecture (Session 2). Architecture doc §4.3.

  { id: 'consume-resource', label: 'Consume Resource', description: 'Unified cost application (HP, mana, tracker, exhaustion). Supports cross-system trackers via systemId.', params: ['resource', 'amount', 'percent', 'remaining', 'level', 'systemId', 'trackerId', 'source'] },

  { id: 'gain-stacks', label: 'Gain Stacks', description: 'Add stacks to a tracker. stackName surfaces in UI as pill badge.', params: ['systemId', 'trackerId', 'amount', 'stackName'] },

  { id: 'require-save', label: 'Require Save', description: 'Enqueue a save prompt; resolves into onPass/onFail sub-actions', params: ['ability', 'dc', 'reason', 'onPass', 'onFail', 'mode'] },

  { id: 'set-cooldown', label: 'Set Cooldown', description: 'Mark a technique unavailable for a scope (turn/round/short-rest/long-rest/day/encounter)', params: ['techniqueId', 'scope', 'count'] },

  { id: 'clear-cooldown', label: 'Clear Cooldown', description: 'Reset a technique\'s cooldown', params: ['techniqueId'] },

  { id: 'break-concentration', label: 'Break Concentration', description: 'End current concentration effect', params: [] },

  // --- NPC state actions (Systems expansion Session 5).

  // Emits a pendingNpcUpdates entry; resolved by dispatch sites that have access to saveNpcLibrary.

  { id: 'set-npc-disposition', label: 'Set NPC Disposition', description: 'Change an NPC\'s disposition (friendly/neutral/hostile/unknown). Matches by npcId or npcName.', params: ['npcId', 'npcName', 'disposition'] },

  // --- Narrative utility actions (Systems expansion Session 6).

  { id: 'roll-table', label: 'Roll on Table', description: 'Roll on a rolltable section of this system and notify with result. Placeholders: {{roll.result}}, {{roll.value}}, {{roll.dice}}.', params: ['sectionId', 'notifyPlayer', 'template'] },

];



// Create an automation rule

const createAutomationRule = (trigger = 'manual') => ({

  id: `rule-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,

  name: 'New Rule',

  enabled: true,

  trigger: trigger,

  triggerParams: {},

  conditions: [{ type: 'always', params: {} }],

  conditionLogic: 'all', // 'all' or 'any'

  actions: [],

  order: 0,

});


const isTruthyRuleParam = (value) => {
  if (value === true || value === 1) return true;
  if (typeof value !== 'string') return false;
  const normalized = value.trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on';
};



// ============================================================================

// RULE EXECUTION ENGINE - Evaluate conditions and execute actions

// ============================================================================



// Evaluate a single condition

export const evaluateCondition = (condition, context) => {

  const { char, systemInstance, systemTemplate, trackerValues = {} } = context;

  const params = condition.params || {};

  

  switch (condition.type) {

    case 'always':

      return true;

      

    case 'stat-check': {

      const statValue = char?.[params.stat] || 10;

      const targetValue = parseInt(params.value) || 10;

      switch (params.operator) {

        case 'gte': return statValue >= targetValue;

        case 'lte': return statValue <= targetValue;

        case 'gt': return statValue > targetValue;

        case 'lt': return statValue < targetValue;

        case 'eq': return statValue === targetValue;

        default: return statValue >= targetValue;

      }

    }

    

    case 'hp-percent': {

      const currentHp = char?.hp || 0;

      const maxHp = char?.maxHp || 1;

      const hpPercent = (currentHp / maxHp) * 100;

      const targetPercent = parseInt(params.value) || 50;

      switch (params.operator) {

        case 'gte': return hpPercent >= targetPercent;

        case 'lte': return hpPercent <= targetPercent;

        case 'gt': return hpPercent > targetPercent;

        case 'lt': return hpPercent < targetPercent;

        default: return hpPercent <= targetPercent;

      }

    }

    

    case 'tracker-value': {

      const trackerId = params.trackerId;

      const currentValue = trackerValues[trackerId] ?? 0;

      const targetValue = parseInt(params.value) || 0;

      switch (params.operator) {

        case 'gte': return currentValue >= targetValue;

        case 'lte': return currentValue <= targetValue;

        case 'gt': return currentValue > targetValue;

        case 'lt': return currentValue < targetValue;

        case 'eq': return currentValue === targetValue;

        default: return currentValue >= targetValue;

      }

    }

    

    case 'has-system': {

      const systemId = params.systemId;

      return (char?.systemList || []).some(s => s.systemId === systemId && s.active !== false);

    }

    

    case 'roll-check': {

      // Roll a d20 + modifier vs DC

      const dc = parseInt(params.dc) || 10;

      const stat = params.stat || 'con';

      const statValue = char?.[stat] || 10;

      const modifier = Math.floor((statValue - 10) / 2);

      const roll = Math.floor(Math.random() * 20) + 1;

      const total = roll + modifier;

      // Natural 20 always succeeds, natural 1 always fails

      if (roll === 20) return true;

      if (roll === 1) return false;

      return total >= dc;

    }

    

    case 'random-chance': {

      const percent = parseInt(params.percent) || 50;

      return Math.random() * 100 < percent;

    }



    // --- Spell-resource architecture (Session 2). Architecture doc §4.2.

    // These read from context.triggerContext (cast events, condition events, resource events)

    // and context.worldContext (time-of-day, currentLocation). Both are optional; graceful

    // degrade returns false when absent.



    case 'has-resource': {

      const resource = params.resource || 'hp';

      const amount = parseFloat(params.amount) || 0;

      const operator = params.operator || 'gte';

      const value = getResourceValue(char, resource, {

        systemId: params.systemId,

        trackerId: params.trackerId,

        systemList: char?.systemList,

        currentInstance: systemInstance,

      });

      switch (operator) {

        case 'gte': return value >= amount;

        case 'lte': return value <= amount;

        case 'gt':  return value > amount;

        case 'lt':  return value < amount;

        case 'eq':  return value === amount;

        default:    return value >= amount;

      }

    }



    case 'technique-is': {

      const expected = params.techniqueId;

      const triggered = context.triggerContext?.techniqueId;

      if (!expected || !triggered) return false;

      return expected === triggered;

    }



    case 'cast-time-of-day': {

      const expected = params.timeOfDay;

      const world = context.worldContext;

      if (!expected || !world?.timeOfDay) return false;

      return world.timeOfDay === expected;

    }



    case 'environment-is': {

      const expected = params.environment;

      const world = context.worldContext;

      if (!expected || !world?.currentLocation) return false;

      return world.currentLocation === expected;

    }



    case 'concentrating-on': {

      const concentratingOn = char?.concentratingOn;

      if (!concentratingOn) return false;

      if (params.any) return true;

      if (params.spellName) return concentratingOn === params.spellName || concentratingOn?.name === params.spellName;

      return !!concentratingOn;

    }



    // --- Cross-cutting condition primitives (Systems expansion Session 1).

    // See docs/systems-design-expansion.md §Session-1 for the design rationale.

    // All four are defensive: if the relevant collection is missing (e.g.

    // worldContext.chars/journal wasn't threaded at a dispatch site), they

    // return false rather than throwing, so rules fail closed.



    case 'has-disease': {

      const diseaseId = params.diseaseId;

      if (!diseaseId) return false;

      const list = char?.diseaseList || [];

      return list.some(d => {

        if (typeof d === 'string') return d === diseaseId;

        return d?.diseaseId === diseaseId;

      });

    }



    case 'has-item': {

      const itemName = params.itemName || '';

      const itemId = params.itemId;

      const requireEquipped = !!params.equipped;

      if (!itemName && !itemId) return false;

      return hasLegacyInventoryItem(char?.inventory || [], {
        itemId,
        itemName,
        requireEquipped,
      });

    }



    case 'quest-status': {

      const questName = (params.questName || '').trim().toLowerCase();

      const status = params.status;

      if (!questName || !status) return false;

      const journal = context.worldContext?.journal || [];

      return journal.some(j =>

        j?.category === 'quests' &&

        (j.name || '').toLowerCase() === questName &&

        j.status === status

      );

    }



    case 'party-has-system': {

      const systemId = params.systemId;

      if (!systemId) return false;

      const chars = context.worldContext?.chars || {};

      const selfKey = char?.id || char?.name;

      return Object.entries(chars).some(([key, c]) => {

        if (!c) return false;

        if (params.excludeSelf) {

          if (key === selfKey || c.id === selfKey || c.name === selfKey) return false;

        }

        return (c.systemList || []).some(s => s.systemId === systemId && s.active !== false);

      });

    }



    // --- Cross-subsystem conditions (Systems expansion Session 2).

    // Reads the enemy name from triggerContext. For on-enemy-killed, triggerContext.name is

    // the single killed enemy. For on-combat-end, triggerContext.killedEnemies is the list

    // and this condition matches if ANY killed enemy matches the expected name (case-insensitive).

    case 'enemy-name-matches': {

      const expected = (params.name || '').trim().toLowerCase();

      if (!expected) return false;

      const tc = context.triggerContext || {};

      if (typeof tc.name === 'string' && tc.name.toLowerCase() === expected) return true;

      if (Array.isArray(tc.killedEnemies)) {

        return tc.killedEnemies.some(n => typeof n === 'string' && n.toLowerCase() === expected);

      }

      return false;

    }



    // --- NPC state conditions (Systems expansion Session 5). ---

    // Reads worldContext.npcLibrary (object keyed by npcId). Supports matching by either

    // npcId (exact) or npcName (case-insensitive). Fails closed on missing npcLibrary.

    case 'npc-alive': {

      const library = context.worldContext?.npcLibrary || {};

      const npcId = params.npcId;

      const npcName = (params.npcName || '').trim().toLowerCase();

      if (!npcId && !npcName) return false;

      const target = npcId

        ? library[npcId]

        : Object.values(library).find(n => n?.name && n.name.toLowerCase() === npcName);

      if (!target) return false;

      const expected = params.status || 'alive';

      return (target.status || 'alive') === expected;

    }



    case 'npc-disposition': {

      const library = context.worldContext?.npcLibrary || {};

      const npcId = params.npcId;

      const npcName = (params.npcName || '').trim().toLowerCase();

      if (!npcId && !npcName) return false;

      const target = npcId

        ? library[npcId]

        : Object.values(library).find(n => n?.name && n.name.toLowerCase() === npcName);

      if (!target) return false;

      // Numeric mapping matches NPC_DISPOSITIONS ordering intent. hostile < neutral < friendly.

      // unknown is treated as 0 (below hostile) so gte checks don't erroneously pass.

      const DISP_RANK = { unknown: 0, hostile: 1, neutral: 2, friendly: 3 };

      const currentRank = DISP_RANK[target.disposition || 'unknown'] ?? 0;

      const targetRank = parseInt(params.value);

      if (Number.isNaN(targetRank)) return false;

      switch (params.operator) {

        case 'gte': return currentRank >= targetRank;

        case 'lte': return currentRank <= targetRank;

        case 'gt': return currentRank > targetRank;

        case 'lt': return currentRank < targetRank;

        case 'eq': return currentRank === targetRank;

        default: return currentRank >= targetRank;

      }

    }



    default:

      return true;

  }

};



// Evaluate all conditions for a rule

const evaluateRuleConditions = (rule, context) => {

  const conditions = rule.conditions || [];

  if (conditions.length === 0) return true;

  

  if (rule.conditionLogic === 'any') {

    return conditions.some(c => evaluateCondition(c, context));

  }

  return conditions.every(c => evaluateCondition(c, context));

};



// Execute a single action (returns updates to apply).

// Results shape additions (Session 2 — spell-resource architecture):

//   pendingSaves: [{ ability, dc, reason, onPass: Action[], onFail: Action[], techniqueId?, source? }]

//     Enqueued by `require-save`. Caller (cast flow — Session 4) resolves and re-dispatches.

//   resourceEvents: [{ kind: 'spend'|'gain', resource, amount, source, systemId?, trackerId? }]

//     Emitted by `consume-resource` / `gain-stacks`. Lets higher-level dispatchers fire

//     `on-resource-spend` / `on-resource-gain` triggers after the atomic change lands.

const executeAction = (action, context) => {

  const { char, systemInstance, systemTemplate, trackerValues = {} } = context;

  // Resolve {{SLOT_KEY}} placeholders in string params against the instance's slot values

  // (Systems expansion Session 3). Non-string params pass through; unresolved keys remain

  // visible as `{{KEY}}` rather than collapsing to empty, so authoring gaps surface early.

  const params = resolveActionParamSlots(action.params || {}, systemInstance, systemTemplate);

  const results = { charUpdates: {}, systemUpdates: {}, notifications: [], statusMessages: [], logEntries: [], pendingSaves: [], resourceEvents: [], pendingApplySystems: [], pendingNpcUpdates: [] };



  switch (action.type) {

    case 'add-effect': {

      // Session 6: params.kind ('buff'|'debuff', default 'buff') and params.permanent

      // (bool, default false) select one of four target arrays:

      //   buff + transient  → char.buffList      (+ char.buffSources for attribution)

      //   buff + permanent  → char.permanentBuffs

      //   debuff + transient→ char.debuffList

      //   debuff + permanent→ char.permanentDebuffs

      // Permanent routing skips source attribution because permanent arrays don't track

      // per-entry source in the current schema; revisit if permanent effects grow audit needs.

      const effect = params.effect;

      if (effect) {

        Object.assign(results.charUpdates, buildSystemLegacyEffectUpdates(char || {}, {

          effect,

          kind: params.kind === 'debuff' ? 'debuff' : 'buff',

          permanent: !!params.permanent,

          sourceName: systemTemplate?.name || 'System',

        }));

      }

      break;

    }



    case 'remove-effect': {

      // Session 6: mirror the add-effect routing so removal can reach any of the four

      // arrays. Defaults to the transient buffList for backward compatibility.

      const effect = params.effect;

      if (effect) {

        Object.assign(results.charUpdates, buildSystemLegacyEffectUpdates(char || {}, {

          effect,

          kind: params.kind === 'debuff' ? 'debuff' : 'buff',

          permanent: params.permanent === undefined ? false : !!params.permanent,

          remove: true,

        }));

      }

      break;

    }

    

    case 'deal-damage': {

      // Rule-engine damage. Writes to `currentHp` — the canonical field used by modifyHp,

      // the combat damage pipeline, and all status displays. (Previously wrote to `hp` which

      // was a phantom field nothing else read — pre-existing bug fixed in Session 5.)

      const amount = parseInt(params.amount) || 0;

      const currentHp = char?.currentHp ?? char?.hp ?? 0;

      if (amount > 0 && (currentHp > 0 || char?.maxHp > 0)) {

        results.charUpdates.currentHp = Math.max(0, currentHp - amount);

        results.notifications.push(`${char?.name || 'Character'} took ${amount} ${params.type || ''} damage from ${systemTemplate?.name || 'system'}`);

      }

      break;

    }



    case 'heal': {

      const amount = parseInt(params.amount) || 0;

      const currentHp = char?.currentHp ?? char?.hp ?? 0;

      const maxHp = char?.maxHp ?? currentHp;

      if (amount > 0) {

        results.charUpdates.currentHp = Math.min(maxHp, currentHp + amount);

        results.notifications.push(`${char?.name || 'Character'} healed ${amount} HP from ${systemTemplate?.name || 'system'}`);

      }

      break;

    }

    

    case 'add-exhaustion': {

      const levels = parseInt(params.levels) || 1;

      const current = char?.exhaustionLevel || 0;

      results.charUpdates.exhaustionLevel = Math.min(6, current + levels);

      break;

    }

    

    case 'remove-exhaustion': {

      const levels = parseInt(params.levels) || 1;

      const current = char?.exhaustionLevel || 0;

      results.charUpdates.exhaustionLevel = Math.max(0, current - levels);

      break;

    }

    

    case 'change-tracker': {

      const trackerId = params.trackerId;

      const operation = params.operation || 'add';

      const value = parseInt(params.value) || 1;

      const current = trackerValues[trackerId] ?? 0;

      let newValue = current;

      switch (operation) {

        case 'add': newValue = current + value; break;

        case 'subtract': newValue = current - value; break;

        case 'set': newValue = value; break;

        case 'multiply': newValue = current * value; break;

      }

      results.systemUpdates.trackerValues = { [trackerId]: newValue };

      break;

    }

    

    case 'advance-stage': {

      const currentStage = systemInstance?.currentStage ?? 0;

      const stagesSection = (systemTemplate?.sections || []).find(s => s.kind === 'stages');

      if (stagesSection) {

        const maxStage = (stagesSection.items || []).length - 1;

        results.systemUpdates.currentStage = Math.min(maxStage, currentStage + 1);

      }

      break;

    }

    

    case 'reveal-clue': {

      const clues = systemTemplate?.clues || [];

      const visibleClues = systemInstance?.visibleClues || [];

      const nextClueIndex = visibleClues.length < clues.length ? visibleClues.length : null;

      if (nextClueIndex !== null) {

        results.systemUpdates.visibleClues = [...visibleClues, nextClueIndex];

      }

      break;

    }

    

    case 'remove-system': {

      results.systemUpdates.active = false;

      results.systemUpdates.removedAt = new Date().toISOString();

      break;

    }



    // Systems expansion Session 4 — programmatic apply. Emits a pending-apply record

    // that processAllSystemRulesForTrigger collects and applySystemRuleResults resolves

    // into a new systemList entry. parentSystemId is seeded from the firing instance so

    // cascadeOnRemove (Session 4) + future on-remove flows can find children.

    //

    // Params:

    //   systemId: the template to apply.

    //   slots:    optional { KEY: value } bag for template instance slots.

    //   expiresIn: optional { turns | rounds | days | rests } for time-boxing.

    case 'apply-system': {

      const targetSystemId = params.systemId;

      if (targetSystemId) {

        const parentInstanceId = systemInstance?.id || systemInstance?.systemId || null;

        results.pendingApplySystems.push({

          targetSystemId,

          slots: (params.slots && typeof params.slots === 'object') ? params.slots : {},

          expiresIn: (params.expiresIn && typeof params.expiresIn === 'object') ? params.expiresIn : null,

          parentSystemId: parentInstanceId,

          source: systemTemplate?.name || 'System',

        });

      }

      break;

    }



    case 'notify': {

      const message = params.message || '';

      if (message) {

        results.notifications.push(message);
        if (isTruthyRuleParam(params.toPlayer)) {
          results.statusMessages.push({
            content: message,
            text: message,
            category: 'systems',
            type: 'system-rule',
            fromDM: true,
            visibility: char?.id ? 'private' : 'public',
            recipients: char?.id ? [char.id] : [],
            characterId: char?.id || null,
            characterName: char?.name || null,
            systemId: systemTemplate?.id || systemInstance?.systemId || null,
            systemName: systemTemplate?.name || null,
            source: systemTemplate?.name || 'System',
            timestamp: new Date().toISOString(),
            dismissed: false,
          });
        }

      }

      break;

    }

    

    case 'log': {

      const message = params.message || '';

      if (message) {

        results.logEntries.push({

          timestamp: new Date().toISOString(),

          message,

          systemId: systemTemplate?.id,

          charId: char?.id,

        });

      }

      break;

    }



    // --- Spell-resource architecture (Session 2). Architecture doc §4.3 / §4.5.



    case 'consume-resource': {

      // Unified cost dispatcher. Routes by `resource` to the appropriate state field.

      // NOTE: this produces RULE-ENGINE-LEVEL updates (charUpdates / systemUpdates / resourceEvents).

      // Session 4's cast flow routes high-impact costs (hp) through the real `modifyHp` path

      // instead so the damage breakdown surfaces in the combat damage log (P5). This action

      // is the fallback for rule-driven costs and works correctly for mana/trackers/exhaustion.

      const resource = params.resource || 'hp';

      const explicitSystemId = params.systemId;

      const trackerId = params.trackerId;

      // Resolve amount — direct value, percent of max HP, or "drop to `remaining`" (e.g. Exsanguinate)

      let amount = parseFloat(params.amount);

      if (isNaN(amount) || amount === 0) {

        if (params.percent != null && resource === 'hp') {

          amount = Math.floor(((char?.maxHp || 0) * parseFloat(params.percent)) / 100);

        } else if (params.remaining != null && (resource === 'hp-all-but' || resource === 'hp')) {

          const cur = char?.currentHp ?? char?.hp ?? 0;

          amount = Math.max(0, cur - parseFloat(params.remaining));

        } else {

          amount = 0;

        }

      }

      const source = params.source || systemTemplate?.name || 'System';

      switch (resource) {

        case 'hp':

        case 'hp-all-but':

        case 'hp-percent': {

          const cur = char?.currentHp ?? char?.hp ?? 0;

          results.charUpdates.currentHp = Math.max(0, cur - amount);

          break;

        }

        case 'temp-hp': {

          const cur = char?.tempHp ?? 0;

          results.charUpdates.tempHp = Math.max(0, cur - amount);

          break;

        }

        case 'mana': {

          const cur = char?.currentMana ?? 0;

          results.charUpdates.currentMana = Math.max(0, cur - amount);

          break;

        }

        case 'exhaustion': {

          const cur = char?.exhaustionLevel ?? 0;

          results.charUpdates.exhaustionLevel = Math.min(6, cur + amount);

          break;

        }

        case 'hit-die': {

          const cur = char?.hitDiceRemaining ?? 0;

          results.charUpdates.hitDiceRemaining = Math.max(0, cur - amount);

          break;

        }

        case 'tracker':

        case 'tracker-stack': {

          // Same-system by default; cross-system if explicitSystemId is set and differs.

          if (!explicitSystemId || explicitSystemId === (systemTemplate?.id || systemInstance?.systemId)) {

            const cur = trackerValues[trackerId] ?? 0;

            results.systemUpdates.trackerValues = { [trackerId]: Math.max(0, cur - amount) };

          } else {

            // Cross-system tracker update — stashed as a crossSystemUpdate so the dispatcher

            // can apply it to the correct instance. Multi-instance-aware callers read this.

            results.crossSystemUpdates = results.crossSystemUpdates || [];

            results.crossSystemUpdates.push({ targetSystemId: explicitSystemId, trackerId, delta: -amount });

          }

          break;

        }

        default: break;

      }

      results.resourceEvents.push({ kind: 'spend', resource, amount, source, systemId: explicitSystemId, trackerId });

      break;

    }



    case 'gain-stacks': {

      // Add stacks to a tracker. Same arithmetic as change-tracker add, with optional

      // stackName metadata so the UI can render pills like "Rage × 3". Crucial difference

      // from change-tracker: this emits a resource-gain event for downstream `on-resource-gain`

      // triggers. Used by on-cast rules like "gain 1 addiction stack per Blood Magic cast."

      const trackerId = params.trackerId;

      const amount = parseFloat(params.amount) || 1;

      const explicitSystemId = params.systemId;

      if (!explicitSystemId || explicitSystemId === (systemTemplate?.id || systemInstance?.systemId)) {

        const cur = trackerValues[trackerId] ?? 0;

        results.systemUpdates.trackerValues = { [trackerId]: cur + amount };

      } else {

        results.crossSystemUpdates = results.crossSystemUpdates || [];

        results.crossSystemUpdates.push({ targetSystemId: explicitSystemId, trackerId, delta: amount, stackName: params.stackName });

      }

      results.resourceEvents.push({

        kind: 'gain', resource: 'tracker-stack', amount,

        source: params.stackName || systemTemplate?.name || 'System',

        systemId: explicitSystemId, trackerId,

      });

      break;

    }



    case 'require-save': {

      // Enqueue a save prompt. The cast flow (Session 4) surfaces this to the DM/player as

      // a roll prompt and then re-dispatches the winning sub-action chain via

      // `resolvePendingSave` below. This is the mechanism for Blood Magic's addiction check

      // at addiction ≥ 10 (DC 12 Wis save → onFail adds Compelled condition).

      results.pendingSaves.push({

        id: generateId(),

        ability: params.ability || 'wis',

        dc: parseInt(params.dc) || 10,

        reason: params.reason || '',

        onPass: Array.isArray(params.onPass) ? params.onPass : [],

        onFail: Array.isArray(params.onFail) ? params.onFail : [],

        mode: params.mode || 'prompt', // 'prompt' = DM clicks roll; 'auto' = engine rolls

        techniqueId: context.triggerContext?.techniqueId,

        source: systemTemplate?.name || 'System',

        characterId: char?.id || null,

        characterName: char?.name || null,

        systemId: systemTemplate?.id || systemInstance?.systemId || null,

        systemInstanceId: systemInstance?.id || systemInstance?.systemId || null,

      });

      break;

    }



    case 'set-cooldown': {

      // Stash cooldown on the system instance. Availability engine (Session 4) reads these

      // and interprets `scope` to decide when the cooldown clears (turn → combat turn;

      // short-rest → on next short rest; etc.).

      const techniqueId = params.techniqueId;

      if (techniqueId) {

        const existing = systemInstance?.techniqueCooldowns || {};

        results.systemUpdates.techniqueCooldowns = {

          ...existing,

          [techniqueId]: {

            scope: params.scope || 'encounter',

            count: parseInt(params.count) || 1,

            setAt: new Date().toISOString(),

          },

        };

      }

      break;

    }



    case 'clear-cooldown': {

      const techniqueId = params.techniqueId;

      if (techniqueId) {

        const existing = { ...(systemInstance?.techniqueCooldowns || {}) };

        delete existing[techniqueId];

        results.systemUpdates.techniqueCooldowns = existing;

      }

      break;

    }



    // Systems expansion Session 6 — roll-table action. Rolls on a kind:'rolltable'

    // section of the currently firing system's template and injects the result text

    // into a notification (or log). Uses a distinct placeholder namespace ({{roll.*}})

    // so slot keys can't collide with the roll's built-ins.

    //

    // Params:

    //   sectionId: required. Id of the rolltable section on this system.

    //   notifyPlayer: optional bool (default true). Push to the player-visible notifications channel.

    //   template: optional string. Placeholders: {{roll.result}}, {{roll.value}}, {{roll.dice}}.

    //             Default: "🎲 {{roll.result}} ({{roll.dice}}: {{roll.value}})".

    case 'roll-table': {

      const sectionId = params.sectionId;

      if (!sectionId || !systemTemplate) break;

      const section = (systemTemplate.sections || []).find(s => s?.id === sectionId && s.kind === 'rolltable');

      if (!section) break;

      const rollResult = rollOnTable(section);

      if (!rollResult) break;

      const resultText = typeof rollResult.result === 'string'

        ? rollResult.result

        : (rollResult.result?.text || rollResult.result?.label || String(rollResult.result || ''));

      const tmpl = params.template || '🎲 {{roll.result}} ({{roll.dice}}: {{roll.value}})';

      const message = tmpl

        .replace(/\{\{\s*roll\.result\s*\}\}/g, resultText)

        .replace(/\{\{\s*roll\.value\s*\}\}/g, String(rollResult.roll))

        .replace(/\{\{\s*roll\.dice\s*\}\}/g, rollResult.diceType);

      if (params.notifyPlayer !== false) {

        results.notifications.push(message);

      }

      results.logEntries.push({

        timestamp: new Date().toISOString(),

        message,

        systemId: systemTemplate?.id,

        charId: char?.id,

        rollTable: { sectionId, diceType: rollResult.diceType, roll: rollResult.roll, result: resultText },

      });

      break;

    }



    // Systems expansion Session 5 — NPC state write. The action emits a pending update

    // that dispatch sites resolve by calling saveNpcLibrary. Supports matching by

    // npcId (preferred) or npcName (case-insensitive fallback).

    case 'set-npc-disposition': {

      const disposition = params.disposition;

      const validDispositions = ['friendly', 'neutral', 'hostile', 'unknown'];

      if (!disposition || !validDispositions.includes(disposition)) break;

      if (!params.npcId && !params.npcName) break;

      results.pendingNpcUpdates.push({

        npcId: params.npcId || null,

        npcName: params.npcName || null,

        patch: { disposition },

        source: systemTemplate?.name || 'System',

      });

      break;

    }



    case 'break-concentration': {

      if (char?.concentratingOn) {

        results.charUpdates.concentratingOn = null;

        results.charUpdates.concentrationEffectGroupId = null;

        results.notifications.push(`Concentration broken on ${char.concentratingOn?.name || char.concentratingOn}`);

      }

      break;

    }



    default:

      break;

  }



  return results;

};



// Merge per-action results into a rule-level combined results object. Extracted so both

// `executeRuleActions` and `resolvePendingSave` share exactly the same merge semantics.

const mergeSystemUpdatePatch = (target = {}, patch = {}) => {
  Object.entries(patch || {}).forEach(([key, value]) => {
    if (key === 'trackerValues' && value && typeof value === 'object' && !Array.isArray(value)) {
      target.trackerValues = { ...(target.trackerValues || {}), ...value };
      return;
    }
    if (key === 'techniqueCooldowns' && value && typeof value === 'object' && !Array.isArray(value)) {
      target.techniqueCooldowns = { ...(target.techniqueCooldowns || {}), ...value };
      return;
    }
    target[key] = value;
  });
  return target;
};

const applySystemInstanceUpdatePatch = (instance = {}, patch = {}) => (
  mergeSystemUpdatePatch({ ...instance }, patch)
);

const applyRuleExecutionContextUpdates = (context = {}, results = {}) => {
  let nextChar = context.char;
  let nextSystemInstance = context.systemInstance;

  if (results?.charUpdates && Object.keys(results.charUpdates).length > 0) {
    nextChar = { ...(nextChar || {}), ...results.charUpdates };
  }

  if (results?.systemUpdates && Object.keys(results.systemUpdates).length > 0) {
    nextSystemInstance = applySystemInstanceUpdatePatch(nextSystemInstance || {}, results.systemUpdates);
  }

  return {
    ...context,
    char: nextChar,
    systemInstance: nextSystemInstance,
    trackerValues: nextSystemInstance?.trackerValues || context.trackerValues || {},
  };
};

const mergeActionResults = (combined, results) => {

  if (!combined.charUpdates) combined.charUpdates = {};
  if (!combined.systemUpdates) combined.systemUpdates = {};
  if (!combined.notifications) combined.notifications = [];
  if (!combined.statusMessages) combined.statusMessages = [];
  if (!combined.logEntries) combined.logEntries = [];
  if (!combined.pendingSaves) combined.pendingSaves = [];
  if (!combined.resourceEvents) combined.resourceEvents = [];
  if (!combined.crossSystemUpdates) combined.crossSystemUpdates = [];
  if (!combined.pendingApplySystems) combined.pendingApplySystems = [];
  if (!combined.pendingNpcUpdates) combined.pendingNpcUpdates = [];

  Object.assign(combined.charUpdates, results?.charUpdates || {});

  mergeSystemUpdatePatch(combined.systemUpdates, results?.systemUpdates || {});

  combined.notifications.push(...(results?.notifications || []));

  combined.statusMessages.push(...(results?.statusMessages || []));

  combined.logEntries.push(...(results?.logEntries || []));

  if (results?.pendingSaves?.length) combined.pendingSaves.push(...results.pendingSaves);

  if (results?.resourceEvents?.length) combined.resourceEvents.push(...results.resourceEvents);

  if (results?.crossSystemUpdates?.length) {

    combined.crossSystemUpdates = combined.crossSystemUpdates.concat(results.crossSystemUpdates);

  }

  if (results?.pendingApplySystems?.length) {

    combined.pendingApplySystems = combined.pendingApplySystems.concat(results.pendingApplySystems);

  }

  if (results?.pendingNpcUpdates?.length) {

    combined.pendingNpcUpdates = combined.pendingNpcUpdates.concat(results.pendingNpcUpdates);

  }

};



// Execute a flat action array with a shared context. Used by rule action lists AND by

// `resolvePendingSave` to dispatch onPass/onFail sub-action chains (architecture §4.3).

const executeActions = (actions, context) => {

  const combined = {

    charUpdates: {}, systemUpdates: {}, notifications: [], statusMessages: [], logEntries: [],

    pendingSaves: [], resourceEvents: [], pendingApplySystems: [], pendingNpcUpdates: [],

  };

  let workingContext = context;

  (actions || []).forEach(action => {
    const results = executeAction(action, workingContext);
    mergeActionResults(combined, results);
    workingContext = applyRuleExecutionContextUpdates(workingContext, results);
  });

  return combined;

};



// Execute all actions for a rule and return combined results

const executeRuleActions = (rule, context) => executeActions(rule.actions || [], context);



// Resolve a pending save from `results.pendingSaves`. The caller passes the save result

// (`'pass' | 'fail'`) and this dispatches the matching sub-action chain in the same context.

// Used by the cast flow (Session 4) after the DM rolls the save. Keeps the require-save

// sub-action tree evaluation co-located with the rest of the engine.

const resolvePendingSave = (pendingSave, outcome, context) => {

  const actions = outcome === 'pass' ? pendingSave.onPass : pendingSave.onFail;

  return executeActions(actions, context);

};



// Find and execute rules for a specific trigger. For cast-related triggers, gather rules

// from every technique's `rules[]` in addition to the system's `automationRules[]` — both

// sources see the same context and conditions filter via `technique-is` when scoping

// matters (architecture §4.4).

const executeRulesForTrigger = (trigger, context, systems) => {

  const { systemTemplate } = context;

  const systemRules = systemTemplate?.automationRules || [];

  let combined = systemRules;

  if (CAST_RELATED_TRIGGERS.has(trigger)) {

    // getSystemTechniques (defined elsewhere) normalizes legacy `spells[]` too. Each

    // technique's `rules[]` follows the same shape as system-wide rules.

    //

    // Scope-by-technique: when `triggerContext.techniqueId` is known (normal cast flow),

    // only gather rules from THAT technique — otherwise casting Blood Bolt would also fire

    // Exsanguinate's on-cast rule. When techniqueId is absent (generic dispatch, tooling),

    // fall back to gathering all technique rules and let `technique-is` conditions filter.

    const techniques = (typeof getSystemTechniques === 'function') ? getSystemTechniques(systemTemplate) : [];

    const triggeringId = context.triggerContext?.techniqueId;

    const source = triggeringId ? techniques.filter(t => t.id === triggeringId) : techniques;

    source.forEach(t => { if (Array.isArray(t.rules)) combined = combined.concat(t.rules); });

  }



  const applicableRules = combined

    .filter(r => r.enabled !== false && r.trigger === trigger)

    .sort((a, b) => (a.order || 0) - (b.order || 0));



  const allResults = {

    charUpdates: {}, systemUpdates: {}, notifications: [], statusMessages: [], logEntries: [],

    pendingSaves: [], resourceEvents: [], pendingApplySystems: [], pendingNpcUpdates: [],

  };



  let workingContext = context;

  applicableRules.forEach(rule => {

    if (evaluateRuleConditions(rule, workingContext)) {

      const results = executeRuleActions(rule, workingContext);

      mergeActionResults(allResults, results);

      workingContext = applyRuleExecutionContextUpdates(workingContext, results);

    }

  });



  return allResults;

};



// Process all systems on a character for a given trigger.

// Returns: { charUpdates, systemListUpdates, notifications, logEntries, pendingSaves, resourceEvents }

// `triggerContext` (optional) flows through to condition evaluators — cast triggers pass

// `{ techniqueId, targets, castLevel, costPaid }`, condition/resource triggers pass their

// event payload. `worldContext` (optional) carries time-of-day, currentLocation, combat for

// environment/concentration conditions. Both are purely passthrough in Session 2; Session 4

// wires actual callers.

const processAllSystemRulesForTrigger = (trigger, char, systems, triggerContext = null, worldContext = null) => {

  const systemList = char?.systemList || [];
  let workingChar = char;

  const combinedResults = {

    charUpdates: {},

    systemListUpdates: [], // Array of { index, updates } for system instances

    notifications: [],

    statusMessages: [],

    logEntries: [],

    pendingSaves: [],

    resourceEvents: [],

    crossSystemUpdates: [],

    pendingApplySystems: [], // Session 4 — programmatic apply-system dispatch

    pendingNpcUpdates: [],   // Session 5 — set-npc-disposition dispatch

  };



  systemList.forEach((originalSysInstance, idx) => {

    const sysInstance = (workingChar?.systemList || [])[idx] || originalSysInstance;

    if (sysInstance.active === false) return;



    const systemTemplate = systems?.[sysInstance.systemId] || sysInstance.template;

    if (!systemTemplate) return;



    const context = {

      char: workingChar,

      systemInstance: sysInstance,

      systemTemplate,

      trackerValues: sysInstance.trackerValues || {},

      triggerContext,

      worldContext,

    };



    const results = executeRulesForTrigger(trigger, context, systems);



    // Merge character updates

    Object.assign(combinedResults.charUpdates, results.charUpdates);



    // Collect system instance updates

    if (Object.keys(results.systemUpdates).length > 0) {

      combinedResults.systemListUpdates.push({ index: idx, updates: results.systemUpdates });

    }



    combinedResults.notifications.push(...results.notifications);

    if (results.statusMessages?.length) combinedResults.statusMessages.push(...results.statusMessages);

    combinedResults.logEntries.push(...results.logEntries);

    if (results.pendingSaves?.length) combinedResults.pendingSaves.push(...results.pendingSaves);

    if (results.resourceEvents?.length) combinedResults.resourceEvents.push(...results.resourceEvents);

    if (results.crossSystemUpdates?.length) combinedResults.crossSystemUpdates.push(...results.crossSystemUpdates);

    if (results.pendingApplySystems?.length) combinedResults.pendingApplySystems.push(...results.pendingApplySystems);

    if (results.pendingNpcUpdates?.length) combinedResults.pendingNpcUpdates.push(...results.pendingNpcUpdates);

    const perSystemResults = {
      ...results,
      systemListUpdates: Object.keys(results.systemUpdates).length > 0
        ? [{ index: idx, updates: results.systemUpdates }]
        : [],
    };
    const workingUpdates = applySystemRuleResultsBase(workingChar, perSystemResults, systems);
    if (Object.keys(workingUpdates).length > 0) {
      workingChar = { ...workingChar, ...workingUpdates };
    }

  });



  return combinedResults;

};



const createDispatchResultAccumulator = () => ({

  charUpdates: {},

  systemUpdates: {},

  systemListUpdates: [],

  notifications: [],

  statusMessages: [],

  logEntries: [],

  pendingSaves: [],

  resourceEvents: [],

  crossSystemUpdates: [],

  pendingApplySystems: [],

  pendingNpcUpdates: [],

});

const createEmptySystemRuleEvaluationResult = (char = null) => ({
  ...createDispatchResultAccumulator(),
  nextChar: char,
  removedIds: [],
  removedEntries: [],
});



const mergeDispatchResultAccumulator = (combined, results = {}) => {

  if (!combined.charUpdates) combined.charUpdates = {};
  if (!combined.systemUpdates) combined.systemUpdates = {};
  if (!combined.systemListUpdates) combined.systemListUpdates = [];
  if (!combined.notifications) combined.notifications = [];
  if (!combined.statusMessages) combined.statusMessages = [];
  if (!combined.logEntries) combined.logEntries = [];
  if (!combined.pendingSaves) combined.pendingSaves = [];
  if (!combined.resourceEvents) combined.resourceEvents = [];
  if (!combined.crossSystemUpdates) combined.crossSystemUpdates = [];
  if (!combined.pendingApplySystems) combined.pendingApplySystems = [];
  if (!combined.pendingNpcUpdates) combined.pendingNpcUpdates = [];

  if (results?.charUpdates) Object.assign(combined.charUpdates, results.charUpdates);

  if (results?.systemUpdates) mergeSystemUpdatePatch(combined.systemUpdates, results.systemUpdates);

  if (results?.systemListUpdates?.length) combined.systemListUpdates.push(...results.systemListUpdates);

  if (results?.notifications?.length) combined.notifications.push(...results.notifications);

  if (results?.statusMessages?.length) combined.statusMessages.push(...results.statusMessages);

  if (results?.logEntries?.length) combined.logEntries.push(...results.logEntries);

  if (results?.pendingSaves?.length) combined.pendingSaves.push(...results.pendingSaves);

  if (results?.resourceEvents?.length) combined.resourceEvents.push(...results.resourceEvents);

  if (results?.crossSystemUpdates?.length) combined.crossSystemUpdates.push(...results.crossSystemUpdates);

  if (results?.pendingApplySystems?.length) combined.pendingApplySystems.push(...results.pendingApplySystems);

  if (results?.pendingNpcUpdates?.length) combined.pendingNpcUpdates.push(...results.pendingNpcUpdates);

  return combined;

};



const appendResourceEventSuppression = (results, suppressedEvents = []) => {

  if (!results || suppressedEvents.length === 0) return;

  const message = `Suppressed ${suppressedEvents.length} repeating resource event${suppressedEvents.length === 1 ? '' : 's'} to prevent a rule loop.`;

  results.notifications = [...(results.notifications || []), message];

  results.logEntries = [

    ...(results.logEntries || []),

    {

      timestamp: new Date().toISOString(),

      message,

      category: 'systems',

      action: 'resource-event.suppressed',

      details: suppressedEvents,

    },

  ];

};

const materializePendingApplySystems = (systemList = [], pendingApplySystems = [], systems = null) => {

  if (!systems || !pendingApplySystems?.length) return systemList;

  let nextSystemList = [...systemList];

  pendingApplySystems.forEach(pa => {

    const tpl = systems[pa.targetSystemId];

    if (!tpl) return;

    const parentSystemId = pa.parentSystemId || null;
    const seededSlotValues = seedSystemInstanceSlotValues(tpl, pa.slots || {});
    const existingIndex = nextSystemList.findIndex(s => (
      systemInstanceMatchesApplyTarget(s, pa.targetSystemId, tpl, seededSlotValues, parentSystemId)
    ));

    if (existingIndex >= 0) {

      const existing = nextSystemList[existingIndex];

      if (existing?.active !== false) return;

      const reactivatedAt = new Date().toISOString();

      nextSystemList[existingIndex] = {

        ...existing,

        active: true,

        reactivatedAt,

        history: [

          ...(Array.isArray(existing.history) ? existing.history : []),

          {

            action: 'reactivated',

            timestamp: reactivatedAt,

            details: pa.parentSystemId ? `Rule reactivated by parent system ${pa.parentSystemId}` : 'Rule reactivated system',

          },

        ],

      };

      return;

    }

    const newInstance = buildAppliedInstance(tpl, {

      parentSystemId,

      slots: pa.slots || {},

      expiresIn: pa.expiresIn || null,

    });

    if (newInstance) nextSystemList.push(newInstance);

  });

  return nextSystemList;

};

const buildSingleInstanceRuleResults = (char, instanceKey, results) => {

  if (!results?.systemUpdates || Object.keys(results.systemUpdates).length === 0) return results;

  const systemList = char?.systemList || [];

  const index = systemList.findIndex(s => (s.id || s.systemId) === instanceKey);

  if (index < 0) return results;

  return {

    ...results,

    systemUpdates: {},

    systemListUpdates: [

      ...(results.systemListUpdates || []),

      { index, updates: results.systemUpdates },

    ],

  };

};



const applySystemRuleResultsBase = (char, results, systems = null) => {

  const updates = { ...(results?.charUpdates || {}) };

  const hasIndexed = results?.systemListUpdates?.length > 0;

  const hasCross = results?.crossSystemUpdates?.length > 0;

  const hasPendingApply = (results?.pendingApplySystems?.length > 0) && !!systems;

  if (!hasIndexed && !hasCross && !hasPendingApply) return updates;



  let newSystemList = [...(char.systemList || [])];

  if (hasIndexed) {

    results.systemListUpdates.forEach(({ index, updates: sysUpdates }) => {

      newSystemList[index] = applySystemInstanceUpdatePatch(newSystemList[index], sysUpdates);

    });

  }



  if (hasCross) {

    newSystemList = newSystemList.map(inst => {

      const sysId = inst.systemId || inst.id;

      const applicable = results.crossSystemUpdates.filter(u => u.targetSystemId === sysId);

      if (applicable.length === 0) return inst;

      const tv = { ...(inst.trackerValues || {}) };

      applicable.forEach(u => { tv[u.trackerId] = (tv[u.trackerId] ?? 0) + (u.delta || 0); });

      return { ...inst, trackerValues: tv };

    });

  }



  if (hasPendingApply) {
    newSystemList = materializePendingApplySystems(newSystemList, results.pendingApplySystems, systems);
  }



  updates.systemList = newSystemList;

  return updates;

};



const applyRuleDispatchToCharBase = (char, instanceKey, results, systems = null) => {

  const targetedResults = buildSingleInstanceRuleResults(char, instanceKey, results);

  const updates = applySystemRuleResultsBase(char, targetedResults, systems);

  return Object.keys(updates).length > 0 ? { ...char, ...updates } : char;

};



const redispatchRuleResourceEvents = ({

  char,

  results,

  systems = null,

  worldContext = null,

}) => {

  if (!results || results.__resourceEventsRedispatched || !systems || !(results.resourceEvents || []).length) {

    return results;

  }



  const initialChar = { ...char, ...applySystemRuleResultsBase(char, results, systems) };

  const drained = drainResourceEventQueue({

    initialChar,

    initialEvents: results.resourceEvents,

    dispatchEvent: (currentChar, event) => {

      const trigger = event?.kind === 'gain' ? 'on-resource-gain' : 'on-resource-spend';

      const followUpResults = processAllSystemRulesForTrigger(trigger, currentChar, systems, event, worldContext);

      const followUpUpdates = applySystemRuleResultsBase(currentChar, followUpResults, systems);

      return {

        nextChar: Object.keys(followUpUpdates).length > 0 ? { ...currentChar, ...followUpUpdates } : currentChar,

        dispatchedResult: followUpResults,

        emittedEvents: followUpResults.resourceEvents || [],

      };

    },

  });



  const accumulated = createDispatchResultAccumulator();

  drained.dispatched.forEach(({ dispatchedResult }) => mergeDispatchResultAccumulator(accumulated, dispatchedResult));

  mergeDispatchResultAccumulator(results, accumulated);

  appendResourceEventSuppression(results, drained.suppressedEvents);

  results.__resourceEventsRedispatched = true;

  return results;

};



export const applySpellResourceFollowUpsWithResults = ({

  startingChar,

  resourceEvents,

  systems = null,

  worldContext = null,

}) => {

  if (!systems || !resourceEvents?.length || !(startingChar?.systemList || []).length) {

    return { nextChar: startingChar, results: null };

  }



  const dispatchResults = createDispatchResultAccumulator();

  dispatchResults.resourceEvents = [...resourceEvents];

  redispatchRuleResourceEvents({

    char: startingChar,

    results: dispatchResults,

    systems,

    worldContext,

  });

  const followUpUpdates = applySystemRuleResultsBase(startingChar, dispatchResults, systems);

  const nextChar = Object.keys(followUpUpdates).length > 0

    ? { ...startingChar, ...followUpUpdates }

    : startingChar;

  return { nextChar, results: dispatchResults };

};

const applySpellResourceFollowUps = (args) => applySpellResourceFollowUpsWithResults(args).nextChar;



// Apply the results from processAllSystemRulesForTrigger to a character.

// `systems` (optional) is the global systems map, required for Session 4's programmatic

// apply-system resolution. Without it, pending applies are skipped (graceful degrade).

const applySystemRuleResultsLegacy = (char, results, systems = null) => {

  const updates = { ...results.charUpdates };

  const hasIndexed = results.systemListUpdates?.length > 0;

  const hasCross = results.crossSystemUpdates?.length > 0;

  const hasPendingApply = (results.pendingApplySystems?.length > 0) && !!systems;

  if (!hasIndexed && !hasCross && !hasPendingApply) return updates;



  // Apply indexed system-list updates (per-instance patches from processAll*)

  let newSystemList = [...(char.systemList || [])];

  if (hasIndexed) {

    results.systemListUpdates.forEach(({ index, updates: sysUpdates }) => {

      newSystemList[index] = { ...newSystemList[index], ...sysUpdates };

    });

  }



  // Apply cross-system tracker deltas (Session 2 — consume-resource / gain-stacks to a

  // different system than the one that fired the rule). Resolved by systemId match.

  if (hasCross) {

    newSystemList = newSystemList.map(inst => {

      const sysId = inst.systemId || inst.id;

      const applicable = results.crossSystemUpdates.filter(u => u.targetSystemId === sysId);

      if (applicable.length === 0) return inst;

      const tv = { ...(inst.trackerValues || {}) };

      applicable.forEach(u => { tv[u.trackerId] = (tv[u.trackerId] ?? 0) + (u.delta || 0); });

      return { ...inst, trackerValues: tv };

    });

  }



  // Systems expansion Session 4 — resolve pendingApplySystems into fresh instances.

  // Skips targets that are already applied (active) to avoid stacking duplicates.

  if (hasPendingApply) {
    newSystemList = materializePendingApplySystems(newSystemList, results.pendingApplySystems, systems);
  }



  updates.systemList = newSystemList;

  return updates;

};



const applySystemRuleResults = (char, results, systems = null, worldContext = null) => {

  redispatchRuleResourceEvents({ char, results, systems, worldContext });

  return applySystemRuleResultsBase(char, results, systems);

};



// ============================================================================

// CAST FLOW (Session 4 — spell-resource architecture §5 / §6)

// ============================================================================

// These are pure functions: they compute availability and produce result bundles. They do

// NOT mutate state; the caller applies updates via the existing apply* helpers and persists

// via saveChars. See docs/spell-resource-architecture.md §6 for the full pipeline.



// Can the character pay every cost in `costs`? Returns reasons for any failures so the

// caller can surface them in availability tooltips and confirmation modals.

// 2026-04-29 — BUG-REPORTS.md #6: optional `isPlayer` flag picks the tracker's

// publicLabel (or '???' fallback) over the real label for player-facing reasons,

// preserving tracker-level P3 even when hidden.requirements is false.

const canPayCost = (char, costs, { systemInstance, systemList, isPlayer = false } = {}) => {

  const reasons = [];

  (costs || []).forEach(cost => {

    const resource = cost.resource || 'hp';

    const amount = parseFloat(cost.amount) || 0;

    switch (resource) {

      case 'hp':

        if ((char?.currentHp ?? 0) < amount) reasons.push(`Need ${amount} HP (have ${char?.currentHp ?? 0})`);

        break;

      case 'hp-percent': {

        const needed = Math.floor((char?.maxHp || 0) * (parseFloat(cost.percent) || 0) / 100);

        if ((char?.currentHp ?? 0) < needed) reasons.push(`Need ${needed} HP (${cost.percent}% of max)`);

        break;

      }

      case 'hp-all-but': {

        const keep = parseFloat(cost.remaining) || 0;

        if ((char?.currentHp ?? 0) <= keep) reasons.push(`HP already at ${keep} or below`);

        break;

      }

      case 'temp-hp':

        if ((char?.tempHp ?? 0) < amount) reasons.push(`Need ${amount} Temp HP`);

        break;

      case 'mana':

        if ((char?.currentMana ?? 0) < amount) reasons.push(`Need ${amount} Mana`);

        break;

      case 'exhaustion':

        if ((char?.exhaustionLevel ?? 0) + amount > 6) reasons.push('Would die from exhaustion');

        break;

      case 'hit-die':

        if ((char?.hitDiceRemaining ?? 0) < amount) reasons.push(`Need ${amount} hit die`);

        break;

      case 'spell-slot':

        // MVP: don't block. Full slot check is a Session-5 polish.

        break;

      case 'tracker':

      case 'tracker-stack': {

        const list = char?.systemList || systemList || [];

        const inst = cost.systemId

          ? list.find(s => (s.systemId || s.id) === cost.systemId)

          : systemInstance;

        const current = inst?.trackerValues?.[cost.trackerId] ?? 0;

        if (current < amount) {

          const trackerField = (inst?.template?.trackerFields || []).find(f => f.id === cost.trackerId);

          const label = isPlayer

            ? (trackerField?.publicLabel || trackerField?.label || cost.trackerId)

            : (trackerField?.label || cost.trackerId);

          reasons.push(`Need ${amount} ${label || 'tracker'}`);

        }

        break;

      }

      default: break;

    }

  });

  return { payable: reasons.length === 0, reasons };

};



// Evaluate a single technique-requirement in the character's current state. Returns

// `{ pass, reason }`. Requirement types come from REQUIREMENT_OPTIONS.

// 2026-04-29 — BUG-REPORTS.md #6: optional `isPlayer` flag picks the tracker's

// publicLabel (or '???' fallback) over the real label for player-facing reasons,

// preserving tracker-level P3 even when hidden.requirements is false.

export const evaluateTechniqueRequirement = (req, char, systemInstance, systems = null, isPlayer = false) => {

  switch (req?.type) {

    case 'tracker-gte':

    case 'tracker-lte': {

      const list = char?.systemList || [];

      const inst = req.systemId

        ? list.find(s => (s.systemId || s.id) === req.systemId)

        : systemInstance;

      const cur = inst?.trackerValues?.[req.trackerId] ?? 0;

      const target = parseFloat(req.value) || 0;

      // 2026-04-27: resolve tracker id → human-readable label so the

      // player sees "Requires Qi Reserve ≥ 5" instead of the raw id or

      // the literal placeholder "tracker". Looks at the systems map

      // first; falls back to the instance's bound template; falls back

      // to the id; finally to the legacy string.

      const tplFromSystems = systems?.[req.systemId || inst?.systemId || inst?.id];

      const tplFromInst = inst?.template;

      const tpl = tplFromSystems || tplFromInst;

      const trackerField = (tpl?.trackerFields || []).find(f => f.id === req.trackerId);

      const trackerLabel = isPlayer

        ? (trackerField?.publicLabel || trackerField?.label || req.trackerId || 'tracker')

        : (trackerField?.label || req.trackerId || 'tracker');

      if (req.type === 'tracker-gte') {

        return cur >= target ? { pass: true } : { pass: false, reason: `Requires ${trackerLabel} ≥ ${target}` };

      }

      return cur <= target ? { pass: true } : { pass: false, reason: `Requires ${trackerLabel} ≤ ${target}` };

    }

    case 'stage-gte': {

      // MVP: gates on currentStage index matching stageId equality. Real tier comparison

      // is Session-5 polish once stages have canonical ordering metadata.

      const list = char?.systemList || [];

      const inst = req.systemId

        ? list.find(s => (s.systemId || s.id) === req.systemId)

        : systemInstance;

      return inst ? { pass: true } : { pass: false, reason: `Requires ${req.systemId || 'a system'}` };

    }

    case 'has-condition': {

      const cond = req.name;

      if (!cond) return { pass: true };

      const all = getTechniqueRequirementConditionNames(char);

      return all.includes(cond) ? { pass: true } : { pass: false, reason: `Requires ${cond}` };

    }

    case 'lacks-condition': {

      const cond = req.name;

      if (!cond) return { pass: true };

      const all = getTechniqueRequirementConditionNames(char);

      return !all.includes(cond) ? { pass: true } : { pass: false, reason: `Cannot be ${cond}` };

    }

    case 'has-item': {

      const itemName = String(req.itemName || '').trim();

      const itemId = String(req.itemId || '').trim();

      if (!itemName && !itemId) return { pass: true };

      const requireEquipped = req.equipped === true;

      const hasItem = hasLegacyInventoryItem(char?.inventory || [], {
        itemId,
        itemName,
        requireEquipped,
      });

      if (hasItem) return { pass: true };

      const itemLabel = itemName || itemId || 'item';

      return {
        pass: false,
        reason: `Requires ${requireEquipped ? 'equipped ' : ''}${itemLabel}`,
      };

    }

    case 'hp-lte-percent': {

      const pct = ((char?.currentHp ?? 0) / (char?.maxHp || 1)) * 100;

      const target = parseFloat(req.percent) || 100;

      return pct <= target ? { pass: true } : { pass: false, reason: `HP must be ≤ ${target}%` };

    }

    default: return { pass: true };

  }

};



// Gate check for a technique. Evaluates requirements → availability flags → cooldown → cost.

// Returns `{ available, reason }` where `reason` is the FIRST failing check's message

// (null when available). Surfaces in UI as greyed-out state with tooltip.

const isTechniqueAvailable = (technique, char, systemInstance, worldContext = {}, systems = null, isPlayer = false) => {

  if (!technique) return { available: false, reason: 'No technique' };

  // 1. Requirements — hard gates. Pass `systems` so requirement

  // formatters can resolve trackerId → tracker label (e.g. "Qi Reserve").

  for (const req of (technique.requirements || [])) {

    const check = evaluateTechniqueRequirement(req, char, systemInstance, systems, isPlayer);

    if (!check.pass) return { available: false, reason: check.reason };

  }

  // 2. Availability flags

  const av = technique.availability || {};

  if (av.combatOnly && !worldContext.combat?.active) return { available: false, reason: 'Combat only' };

  if (av.restOnly && worldContext.combat?.active) return { available: false, reason: 'Not available in combat' };

  if (av.timeOfDay && worldContext.timeOfDay !== av.timeOfDay) return { available: false, reason: `Only at ${av.timeOfDay}` };

  if ((av.environments || []).length > 0 && !av.environments.includes(worldContext.currentLocation)) {

    return { available: false, reason: `Wrong environment (${worldContext.currentLocation || 'unknown'})` };

  }

  // 3. Cooldown

  if (av.cooldown?.scope) {

    const cd = systemInstance?.techniqueCooldowns?.[technique.id];

    if (cd) return { available: false, reason: `On cooldown (${cd.scope})` };

  }

  // 4. Cost affordability. If the technique has `forceCast: true`, it can be cast even when

  // the cost can't be paid in full — the cast proceeds with whatever HP/mana/etc. is left,

  // and a follow-on rule (e.g. Blood Magic's overcast-exhaustion) applies the penalty.

  // UI surfaces the shortfall as a `warning` so the confirmation modal can show it without

  // blocking the button.

  const cost = canPayCost(char, technique.costs, { systemInstance, systemList: char?.systemList, isPlayer });

  if (!cost.payable) {

    if (technique.forceCast) return { available: true, reason: null, warning: cost.reasons[0] || 'Overcast penalty' };

    return { available: false, reason: cost.reasons[0] || 'Cannot pay cost' };

  }

  return { available: true, reason: null };

};



// Orchestrate a technique cast. Pure function — returns the combined results bundle plus

// an audit event shape. Caller applies results via applyRuleDispatchToChar / saveChars and

// pushes the audit event via pushAuditEvent. Does NOT include spell resolution (dice rolls,

// attack, save vs targets) — that's Session 5's combat integration. The on-cast rule fires

// with the post-cost state so consequences (addiction + total-casts increments) run against

// accurate values.

export const orchestrateTechniqueCast = ({

  technique,

  char,

  instanceKey,

  systemInstance,

  systemTemplate,

  systems,

  worldContext = {},

  castLevel = null,

  targets = [],

}) => {

  // Build trigger context — `on-cast` rules see this via context.triggerContext

  const triggerContext = {

    techniqueId: technique.id,

    techniqueName: technique.name,

    targets,

    castLevel: castLevel ?? (technique.inlineSpell?.level ?? 1),

    costPaid: [...(technique.costs || [])],

  };



  // Synthesize consume-resource actions from each cost entry. Runs them as a flat action

  // chain so the results carry the proper charUpdates/systemUpdates/resourceEvents shape.

  const costSource = `${systemTemplate?.name || 'System'}: ${technique.name || 'Technique'}`;

  const costActions = (technique.costs || []).map(cost => ({

    type: 'consume-resource',

    params: { ...cost, source: costSource },

  }));

  const costContext = {

    char,

    systemInstance,

    systemTemplate,

    trackerValues: systemInstance?.trackerValues || {},

    triggerContext,

    worldContext,

  };

  const costResults = executeActions(costActions, costContext);



  // Apply costs to a WORKING copy of the char so on-cast rules see the post-cost state.

  // (An on-cast rule that checks `has-resource HP ≥ 5` after Blood Bolt's 5 HP cost should

  //  see currentHp reduced.)

  const charPostCost = applyRuleDispatchToChar(char, instanceKey, costResults, systems, worldContext);



  // Fire on-cast rules across ALL applied systems. Each system's executeRulesForTrigger

  // only pulls the TRIGGERING technique's rules (our scoping fix), so cross-technique

  // pollution is prevented. System-wide on-cast rules still fire everywhere for observers.

  const castResults = processAllSystemRulesForTrigger('on-cast', charPostCost, systems, triggerContext, worldContext);



  // Apply cost + on-cast to a working char so threshold detection sees the final tracker

  // values. The gain-stacks action in on-cast rules bumps trackers (addiction-level,

  // total-casts, etc.), which may cross configured thresholds.

  const charAfterCast = { ...charPostCost, ...applySystemRuleResults(charPostCost, castResults, systems, worldContext) };



  // Threshold dispatch: for each changed tracker on each system instance, detect threshold

  // crossings and fire `on-threshold` rules. Mirrors the existing pattern in

  // updateSystemVisibility (App.js:~53497) but applied inline to the cast-flow results.

  // `addiction-threshold-save` (the system-wide Blood Magic rule) is the primary consumer.

  const thresholdResults = {

    charUpdates: {}, systemListUpdates: [], notifications: [], statusMessages: [], logEntries: [],

    pendingSaves: [], resourceEvents: [], crossSystemUpdates: [],

  };

  let charAfterThresholds = charAfterCast;

  const prevInstances = char?.systemList || [];

  const nextInstances = charAfterCast?.systemList || [];

  nextInstances.forEach((inst, idx) => {

    if (inst.active === false) return;

    const prev = prevInstances.find(p => (p.id || p.systemId) === (inst.id || inst.systemId));

    const tpl = systems?.[inst.systemId] || inst.template;

    if (!tpl) return;

    const oldTv = prev?.trackerValues || {};

    const newTv = inst.trackerValues || {};

    const changed = Object.keys(newTv).filter(k => (newTv[k] ?? 0) !== (oldTv[k] ?? 0));

    if (changed.length === 0) return;

    const fieldsById = {};

    (tpl.trackerFields || []).forEach(f => { fieldsById[f.id] = f; });

    changed.forEach(fieldId => {

      const field = fieldsById[fieldId];

      if (!field) return;

      const oldV = oldTv[fieldId] ?? 0;

      const newV = newTv[fieldId] ?? 0;

      (field.thresholds || []).forEach(t => {

        const tv = parseInt(t.value) || 0;

        const crossed = (oldV < tv && newV >= tv) || (oldV >= tv && newV < tv);

        if (!crossed) return;

        const direction = newV >= tv ? 'up' : 'down';

        const thCtx = {

          char: charAfterThresholds,

          systemInstance: inst,

          systemTemplate: tpl,

          trackerValues: newTv,

          triggerContext: { trackerId: fieldId, threshold: tv, direction, thresholdLabel: t.label },

          worldContext,

        };

        const thRes = executeRulesForTrigger('on-threshold', thCtx, systems);

        mergeActionResults(thresholdResults, thRes);

        charAfterThresholds = applyRuleDispatchToChar(charAfterThresholds, inst.id || inst.systemId, thRes, systems, worldContext);

      });

    });

  });



  // 2026-04-29 — BUG-REPORTS.md #5: auto-stamp the technique's declared

  // cooldown (from availability.cooldown) onto the system instance after a

  // successful cast. Without this, the editor's "Cooldown: turn/round/…"

  // dropdown was a no-op — only techniques with an explicit set-cooldown

  // rule action got cooldowns. Explicit set-cooldown rules ran above

  // (in on-cast / threshold) and may have already written this key; we

  // skip when the entry exists so the rule wins.

  const cooldownConfig = technique?.availability?.cooldown;

  if (cooldownConfig?.scope) {

    const targetInst = (charAfterThresholds.systemList || []).find(

      s => (s.id || s.systemId) === instanceKey

    );

    const existingCooldowns = targetInst?.techniqueCooldowns || {};

    if (!existingCooldowns[technique.id]) {

      charAfterThresholds = applyRuleDispatchToChar(charAfterThresholds, instanceKey, {

        systemUpdates: {

          techniqueCooldowns: {

            ...existingCooldowns,

            [technique.id]: {

              scope: cooldownConfig.scope,

              count: parseInt(cooldownConfig.count) || 1,

              setAt: new Date().toISOString(),

            },

          },

        },

      }, systems, worldContext);

    }

  }



  // Compose an audit-event shape for the caller to push

  const costSummary = (technique.costs || []).map(c => {

    const r = c.resource;

    if (r === 'hp' || r === 'temp-hp' || r === 'mana' || r === 'exhaustion' || r === 'hit-die') return `${c.amount}${r === 'hp' ? ' HP' : r === 'mana' ? ' Mana' : ' ' + r}`;

    if (r === 'hp-percent') return `${c.percent}% HP`;

    if (r === 'hp-all-but') return `HP → ${c.remaining}`;

    if (r === 'tracker' || r === 'tracker-stack') return `${c.amount} ${c.trackerId || 'tracker'}`;

    if (r === 'spell-slot') return `Lv${c.level} slot`;

    return r;

  }).filter(Boolean).join(', ');
  const auditTechniqueIcon = getTechniqueDisplayIcon(technique, systemTemplate, '🔮');
  const auditTechniquePrefix = auditTechniqueIcon ? `${auditTechniqueIcon} ` : '';



  return {

    triggerContext,

    costResults,

    castResults,

    thresholdResults,

    charPostCost,

    charFinal: charAfterThresholds,

    auditEvent: {

      category: 'combat',

      action: 'technique.cast',

      actor: { type: 'player', charId: char?.id, name: char?.name },

      target: { techniqueId: technique.id, techniqueName: technique.name, targets },

      summary: `${auditTechniquePrefix}${char?.name || 'Character'} cast ${technique.name}${costSummary ? ` — ${costSummary}` : ''}`,

      details: {

        systemId: systemTemplate?.id,

        systemName: systemTemplate?.name,

        techniqueId: technique.id,

        techniqueName: technique.name,

        costs: technique.costs || [],

        targets,

        notifications: [

          ...(costResults.notifications || []),

          ...(castResults.notifications || []),

          ...(thresholdResults.notifications || []),

        ],

        resourceEvents: [

          ...(costResults.resourceEvents || []),

          ...(castResults.resourceEvents || []),

        ],

      },

    },

  };

};



// Scope hierarchy for technique cooldowns. An event at a longer scope clears everything

// at or below its own scope. `short-rest` clears turn/round/encounter/short-rest cooldowns;

// `long-rest` clears all of those plus long-rest and day; `day` (e.g. an in-world day

// passing outside of rests) clears turn/round/encounter/day but not short-rest/long-rest.

const COOLDOWN_SCOPE_ORDER = ['turn', 'round', 'encounter', 'short-rest', 'day', 'long-rest'];

const scopesClearedBy = (triggeringScope) => {

  switch (triggeringScope) {

    case 'turn':       return new Set(['turn']);

    case 'round':      return new Set(['turn', 'round']);

    case 'encounter':  return new Set(['turn', 'round', 'encounter']);

    case 'short-rest': return new Set(['turn', 'round', 'encounter', 'short-rest']);

    case 'long-rest':  return new Set(['turn', 'round', 'encounter', 'short-rest', 'long-rest', 'day']);

    case 'day':        return new Set(['turn', 'round', 'encounter', 'day']);

    default:           return new Set();

  }

};



// Returns a new systemList with any technique cooldowns matching `clearedSet` decremented.

// Pure — no side effects. Caller applies the result via saveChars / onUpdate.

export const clearCooldownsAtScopes = (systemList, clearedSet) => {

  if (!Array.isArray(systemList) || systemList.length === 0 || !clearedSet || clearedSet.size === 0) return systemList;

  let changed = false;

  const next = systemList.map(inst => {

    const cds = inst.techniqueCooldowns;

    if (!cds) return inst;

    const remaining = {};

    let touched = false;

    Object.keys(cds).forEach(k => {

      const cd = cds[k];

      if (cd?.scope && clearedSet.has(cd.scope)) {
        touched = true;
        const currentCount = Number.isFinite(Number(cd.count)) ? Math.max(1, Number(cd.count)) : 1;
        const nextCount = currentCount - 1;
        if (nextCount > 0) {
          remaining[k] = { ...cd, count: nextCount };
        }
        return;
      }

      remaining[k] = cd;

    });

    if (!touched) return inst;

    changed = true;

    return { ...inst, techniqueCooldowns: Object.keys(remaining).length > 0 ? remaining : undefined };

  });

  return changed ? next : systemList;

};



// Apply single-system rule-dispatch results to a character, returning the updated character.

// Used by on-apply, on-remove, on-tracker-change, on-threshold, and manual-trigger paths

// that target ONE specific system instance. Contrast applySystemRuleResults above, which

// handles the multi-system broadcast case (processAllSystemRulesForTrigger) where

// systemUpdates is keyed by instance index. Here systemUpdates is a flat field patch

// applied to the one instance identified by instanceId.

const applyRuleDispatchToCharLegacy = (char, instanceKey, results) => {

  let updated = char;

  if (results?.charUpdates && Object.keys(results.charUpdates).length > 0) {

    updated = { ...updated, ...results.charUpdates };

  }

  if (results?.systemUpdates && Object.keys(results.systemUpdates).length > 0) {

    updated = {

      ...updated,

      // Match by (s.id || s.systemId) because demo-data entries sometimes carry only

      // systemId (not id), and the rest of the codebase resolves instance keys that way.

      systemList: (updated.systemList || []).map(s =>

        (s.id || s.systemId) === instanceKey ? applySystemInstanceUpdatePatch(s, results.systemUpdates) : s

      ),

    };

  }

  // Cross-system tracker deltas (Session 2 — architecture §4.3 / §4.5). Apply by systemId.

  if (results?.crossSystemUpdates?.length > 0) {

    updated = {

      ...updated,

      systemList: (updated.systemList || []).map(s => {

        const sysId = s.systemId || s.id;

        const applicable = results.crossSystemUpdates.filter(u => u.targetSystemId === sysId);

        if (applicable.length === 0) return s;

        const tv = { ...(s.trackerValues || {}) };

        applicable.forEach(u => { tv[u.trackerId] = (tv[u.trackerId] ?? 0) + (u.delta || 0); });

        return { ...s, trackerValues: tv };

      }),

    };

  }

  return updated;

};



const applyRuleDispatchToChar = (char, instanceKey, results, systems = null, worldContext = null) => {

  let updated = applyRuleDispatchToCharBase(char, instanceKey, results, systems);

  if (!results || results.__resourceEventsRedispatched || !systems || !(results.resourceEvents || []).length) {

    return updated;

  }



  const drained = drainResourceEventQueue({

    initialChar: updated,

    initialEvents: results.resourceEvents,

    dispatchEvent: (currentChar, event) => {

      const trigger = event?.kind === 'gain' ? 'on-resource-gain' : 'on-resource-spend';

      const followUpResults = processAllSystemRulesForTrigger(trigger, currentChar, systems, event, worldContext);

      const followUpUpdates = applySystemRuleResultsBase(currentChar, followUpResults, systems);

      return {

        nextChar: Object.keys(followUpUpdates).length > 0 ? { ...currentChar, ...followUpUpdates } : currentChar,

        dispatchedResult: followUpResults,

        emittedEvents: followUpResults.resourceEvents || [],

      };

    },

  });



  const accumulated = createDispatchResultAccumulator();

  drained.dispatched.forEach(({ dispatchedResult }) => mergeDispatchResultAccumulator(accumulated, dispatchedResult));

  mergeDispatchResultAccumulator(results, accumulated);

  appendResourceEventSuppression(results, drained.suppressedEvents);

  results.__resourceEventsRedispatched = true;

  updated = drained.nextChar;

  return updated;

};

const getSystemInstanceKey = (instance) => instance?.id || instance?.systemId;

const getSystemTemplateForInstance = (instance, systems = {}) => (
  (instance?.systemId && systems?.[instance.systemId]) || instance?.template || null
);

const systemInstanceMatchesRemovalKey = (instance, key) => (
  !!instance
  && !!key
  && (
    getSystemInstanceKey(instance) === key
    || instance.systemId === key
    || instance.template?.id === key
  )
);

const collectSystemLifecycleRemovalTargets = (systemList = [], systemKeys = [], systems = {}) => {
  const list = Array.isArray(systemList) ? systemList : [];
  const keys = Array.isArray(systemKeys) ? systemKeys.filter(Boolean) : [systemKeys].filter(Boolean);
  const queued = [];
  const removedKeys = new Set();
  const targetMeta = new Map();

  const enqueue = (instance, meta = {}) => {
    const key = getSystemInstanceKey(instance);
    if (!key || removedKeys.has(key)) return;
    removedKeys.add(key);
    targetMeta.set(key, meta);
    queued.push(instance);
  };

  keys.forEach(key => {
    list
      .filter(instance => systemInstanceMatchesRemovalKey(instance, key))
      .forEach(instance => enqueue(instance, { reason: 'direct' }));
  });

  for (let i = 0; i < queued.length; i += 1) {
    const instance = queued[i];
    const instanceKey = getSystemInstanceKey(instance);
    const template = getSystemTemplateForInstance(instance, systems);
    if (!template?.cascadeOnRemove || !instanceKey) continue;
    list
      .filter(candidate => candidate?.parentSystemId === instanceKey)
      .forEach(child => enqueue(child, {
        reason: 'cascade',
        parentKey: instanceKey,
        parentName: template?.name || instance?.systemId || instanceKey,
      }));
  }

  return {
    targets: queued,
    removedKeys,
    targetMeta,
  };
};

const appendRemovedSystemHistory = (removedSystems = [], removedEntries = [], limit = 20) => {
  if (!removedEntries.length) return removedSystems;
  const keep = Math.max(0, limit - removedEntries.length);
  return [...(Array.isArray(removedSystems) ? removedSystems.slice(-keep) : []), ...removedEntries];
};

const buildRemovedSystemEntry = (instance, meta = {}, removedAt) => {
  const cascade = meta.reason === 'cascade';
  const details = cascade
    ? `Cascade-removed by parent ${meta.parentName || meta.parentKey || 'system'}`
    : (meta.details || 'System removed');
  return {
    ...instance,
    removedAt,
    history: [
      ...(Array.isArray(instance?.history) ? instance.history : []),
      { action: 'removed', timestamp: removedAt, details },
    ],
  };
};

const buildChangedCharacterFields = (before = {}, after = {}) => {
  const patch = {};
  Object.keys(after || {}).forEach(key => {
    if (after[key] !== before?.[key]) patch[key] = after[key];
  });
  return patch;
};

const SYSTEM_STATE_AUTHORITY_FIELDS = ['systemList', 'removedSystems', 'glowingSystems'];
const SYSTEM_STATE_AUTHORITY_FIELD_SET = new Set(SYSTEM_STATE_AUTHORITY_FIELDS);
const CHARACTER_VITAL_AUTHORITY_FIELDS = ['currentHp', 'tempHp', 'currentMana'];
const SYSTEM_AND_VITAL_AUTHORITY_FIELD_SET = new Set([
  ...SYSTEM_STATE_AUTHORITY_FIELDS,
  ...CHARACTER_VITAL_AUTHORITY_FIELDS,
]);
const CHARACTER_RULE_RESOURCE_AUTHORITY_FIELDS = [
  ...CHARACTER_VITAL_AUTHORITY_FIELDS,
  'exhaustionLevel',
  'hitDiceRemaining',
];
const SYSTEM_AND_RULE_RESOURCE_AUTHORITY_FIELD_SET = new Set([
  ...SYSTEM_STATE_AUTHORITY_FIELDS,
  ...CHARACTER_RULE_RESOURCE_AUTHORITY_FIELDS,
]);
const CHARACTER_RULE_EFFECT_AUTHORITY_FIELDS = [
  'buffList',
  'debuffList',
  'permanentBuffs',
  'permanentDebuffs',
  'buffSources',
  'concentratingOn',
  'concentrationEffectGroupId',
];
const SYSTEM_AND_RULE_EFFECT_AUTHORITY_FIELD_SET = new Set([
  ...SYSTEM_STATE_AUTHORITY_FIELDS,
  ...CHARACTER_RULE_EFFECT_AUTHORITY_FIELDS,
]);

const systemStateJsonEqual = (a, b) => {
  try { return JSON.stringify(a) === JSON.stringify(b); }
  catch (e) { return a === b; }
};

const normalizeSystemStateAuthorityField = (field, value) => {
  if (field === 'systemList' || field === 'removedSystems') return Array.isArray(value) ? value : [];
  if (field === 'glowingSystems') {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  }
  return value;
};

const buildSystemStateAuthorityPayload = (beforeChar = {}, afterChar = {}) => {
  const changedKeys = new Set([
    ...Object.keys(beforeChar || {}),
    ...Object.keys(afterChar || {}),
  ].filter(key => !systemStateJsonEqual(beforeChar?.[key], afterChar?.[key])));
  if ([...changedKeys].some(key => !SYSTEM_STATE_AUTHORITY_FIELD_SET.has(key))) return null;

  const updates = {};
  const expected = {};
  SYSTEM_STATE_AUTHORITY_FIELDS.forEach(field => {
    const beforeValue = normalizeSystemStateAuthorityField(field, beforeChar?.[field]);
    const afterValue = normalizeSystemStateAuthorityField(field, afterChar?.[field]);
    if (!systemStateJsonEqual(beforeValue, afterValue)) {
      updates[field] = afterValue;
      expected[field] = beforeValue;
    }
  });
  return Object.keys(updates).length > 0 ? { updates, expected } : null;
};

const normalizeVitalAuthorityField = (value) => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
};

const buildSystemStateAndVitalsAuthorityPayload = (beforeChar = {}, afterChar = {}) => {
  const changedKeys = new Set([
    ...Object.keys(beforeChar || {}),
    ...Object.keys(afterChar || {}),
  ].filter(key => {
    if (SYSTEM_STATE_AUTHORITY_FIELD_SET.has(key)) {
      return !systemStateJsonEqual(
        normalizeSystemStateAuthorityField(key, beforeChar?.[key]),
        normalizeSystemStateAuthorityField(key, afterChar?.[key])
      );
    }
    if (CHARACTER_VITAL_AUTHORITY_FIELDS.includes(key)) {
      return normalizeVitalAuthorityField(beforeChar?.[key]) !== normalizeVitalAuthorityField(afterChar?.[key]);
    }
    return !systemStateJsonEqual(beforeChar?.[key], afterChar?.[key]);
  }));
  if ([...changedKeys].some(key => !SYSTEM_AND_VITAL_AUTHORITY_FIELD_SET.has(key))) return null;

  const systemUpdates = {};
  const systemExpected = {};
  SYSTEM_STATE_AUTHORITY_FIELDS.forEach(field => {
    const beforeValue = normalizeSystemStateAuthorityField(field, beforeChar?.[field]);
    const afterValue = normalizeSystemStateAuthorityField(field, afterChar?.[field]);
    if (!systemStateJsonEqual(beforeValue, afterValue)) {
      systemUpdates[field] = afterValue;
      systemExpected[field] = beforeValue;
    }
  });

  const vitalsUpdates = {};
  const vitalsExpected = {};
  CHARACTER_VITAL_AUTHORITY_FIELDS.forEach(field => {
    const beforeValue = normalizeVitalAuthorityField(beforeChar?.[field]);
    const afterValue = normalizeVitalAuthorityField(afterChar?.[field]);
    if (beforeValue !== afterValue) {
      vitalsUpdates[field] = afterValue;
      vitalsExpected[field] = beforeValue;
    }
  });

  if (Object.keys(systemUpdates).length === 0 && Object.keys(vitalsUpdates).length === 0) return null;
  return {
    ...(Object.keys(systemUpdates).length > 0 ? { systemUpdates, systemExpected } : {}),
    ...(Object.keys(vitalsUpdates).length > 0 ? { vitalsUpdates, vitalsExpected } : {}),
  };
};

const normalizeRuleResourceAuthorityField = (field, value) => {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return 0;
  const intValue = Math.trunc(n);
  if (field === 'exhaustionLevel') return Math.max(0, Math.min(6, intValue));
  return Math.max(0, intValue);
};

const buildSystemStateAndResourcesAuthorityPayload = (beforeChar = {}, afterChar = {}) => {
  const changedKeys = new Set([
    ...Object.keys(beforeChar || {}),
    ...Object.keys(afterChar || {}),
  ].filter(key => {
    if (SYSTEM_STATE_AUTHORITY_FIELD_SET.has(key)) {
      return !systemStateJsonEqual(
        normalizeSystemStateAuthorityField(key, beforeChar?.[key]),
        normalizeSystemStateAuthorityField(key, afterChar?.[key])
      );
    }
    if (CHARACTER_RULE_RESOURCE_AUTHORITY_FIELDS.includes(key)) {
      return normalizeRuleResourceAuthorityField(key, beforeChar?.[key]) !== normalizeRuleResourceAuthorityField(key, afterChar?.[key]);
    }
    return !systemStateJsonEqual(beforeChar?.[key], afterChar?.[key]);
  }));
  if ([...changedKeys].some(key => !SYSTEM_AND_RULE_RESOURCE_AUTHORITY_FIELD_SET.has(key))) return null;

  const systemUpdates = {};
  const systemExpected = {};
  SYSTEM_STATE_AUTHORITY_FIELDS.forEach(field => {
    const beforeValue = normalizeSystemStateAuthorityField(field, beforeChar?.[field]);
    const afterValue = normalizeSystemStateAuthorityField(field, afterChar?.[field]);
    if (!systemStateJsonEqual(beforeValue, afterValue)) {
      systemUpdates[field] = afterValue;
      systemExpected[field] = beforeValue;
    }
  });

  const resourceUpdates = {};
  const resourceExpected = {};
  CHARACTER_RULE_RESOURCE_AUTHORITY_FIELDS.forEach(field => {
    const beforeValue = normalizeRuleResourceAuthorityField(field, beforeChar?.[field]);
    const afterValue = normalizeRuleResourceAuthorityField(field, afterChar?.[field]);
    if (beforeValue !== afterValue) {
      resourceUpdates[field] = afterValue;
      resourceExpected[field] = beforeValue;
    }
  });

  if (Object.keys(systemUpdates).length === 0 && Object.keys(resourceUpdates).length === 0) return null;
  return {
    ...(Object.keys(systemUpdates).length > 0 ? { systemUpdates, systemExpected } : {}),
    ...(Object.keys(resourceUpdates).length > 0 ? { resourceUpdates, resourceExpected } : {}),
  };
};

const normalizeRuleEffectAuthorityField = (field, value) => {
  if (
    field === 'buffList'
    || field === 'debuffList'
    || field === 'permanentBuffs'
    || field === 'permanentDebuffs'
  ) {
    return Array.isArray(value) ? value : [];
  }
  if (field === 'buffSources') {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  }
  return value === undefined ? null : value;
};

const buildSystemStateAndEffectsAuthorityPayload = (beforeChar = {}, afterChar = {}) => {
  const changedKeys = new Set([
    ...Object.keys(beforeChar || {}),
    ...Object.keys(afterChar || {}),
  ].filter(key => {
    if (SYSTEM_STATE_AUTHORITY_FIELD_SET.has(key)) {
      return !systemStateJsonEqual(
        normalizeSystemStateAuthorityField(key, beforeChar?.[key]),
        normalizeSystemStateAuthorityField(key, afterChar?.[key])
      );
    }
    if (CHARACTER_RULE_EFFECT_AUTHORITY_FIELDS.includes(key)) {
      return !systemStateJsonEqual(
        normalizeRuleEffectAuthorityField(key, beforeChar?.[key]),
        normalizeRuleEffectAuthorityField(key, afterChar?.[key])
      );
    }
    return !systemStateJsonEqual(beforeChar?.[key], afterChar?.[key]);
  }));
  if ([...changedKeys].some(key => !SYSTEM_AND_RULE_EFFECT_AUTHORITY_FIELD_SET.has(key))) return null;

  const systemUpdates = {};
  const systemExpected = {};
  SYSTEM_STATE_AUTHORITY_FIELDS.forEach(field => {
    const beforeValue = normalizeSystemStateAuthorityField(field, beforeChar?.[field]);
    const afterValue = normalizeSystemStateAuthorityField(field, afterChar?.[field]);
    if (!systemStateJsonEqual(beforeValue, afterValue)) {
      systemUpdates[field] = afterValue;
      systemExpected[field] = beforeValue;
    }
  });

  const effectUpdates = {};
  const effectExpected = {};
  CHARACTER_RULE_EFFECT_AUTHORITY_FIELDS.forEach(field => {
    const beforeValue = normalizeRuleEffectAuthorityField(field, beforeChar?.[field]);
    const afterValue = normalizeRuleEffectAuthorityField(field, afterChar?.[field]);
    if (!systemStateJsonEqual(beforeValue, afterValue)) {
      effectUpdates[field] = afterValue;
      effectExpected[field] = beforeValue;
    }
  });

  if (Object.keys(systemUpdates).length === 0 && Object.keys(effectUpdates).length === 0) return null;
  return {
    ...(Object.keys(systemUpdates).length > 0 ? { systemUpdates, systemExpected } : {}),
    ...(Object.keys(effectUpdates).length > 0 ? { effectUpdates, effectExpected } : {}),
  };
};

export const removeSystemsWithLifecycle = ({
  char,
  systemKeys,
  systems = {},
  worldContext = null,
  removedAt = null,
  archiveDetails = 'System removed',
} = {}) => {
  const baseChar = char || {};
  const baseList = Array.isArray(baseChar.systemList) ? baseChar.systemList : [];
  const removal = collectSystemLifecycleRemovalTargets(baseList, systemKeys, systems);
  if (removal.targets.length === 0) {
    return {
      nextChar: baseChar,
      removedEntries: [],
      removedIds: [],
      notifications: [],
      statusMessages: [],
      logEntries: [],
      pendingSaves: [],
      pendingNpcUpdates: [],
    };
  }

  let nextChar = { ...baseChar, systemList: baseList };
  const sideEffects = createDispatchResultAccumulator();

  removal.targets.forEach(instance => {
    const template = getSystemTemplateForInstance(instance, systems);
    const instanceKey = getSystemInstanceKey(instance);
    if (!template || !instanceKey) return;
    const result = executeRulesForTrigger('on-remove', {
      char: nextChar,
      systemInstance: instance,
      systemTemplate: template,
      trackerValues: instance.trackerValues || {},
      worldContext,
    }, systems);
    nextChar = applyRuleDispatchToChar(nextChar, instanceKey, result, systems, worldContext);
    mergeDispatchResultAccumulator(sideEffects, result);
  });

  const finalList = (nextChar.systemList || []).filter(instance => {
    const key = getSystemInstanceKey(instance);
    return !key || !removal.removedKeys.has(key);
  });
  const timestamp = removedAt || new Date().toISOString();
  const removedEntries = removal.targets.map(instance => buildRemovedSystemEntry(instance, {
    ...(removal.targetMeta.get(getSystemInstanceKey(instance)) || {}),
    details: archiveDetails,
  }, timestamp));
  const finalChar = {
    ...nextChar,
    systemList: finalList,
    removedSystems: appendRemovedSystemHistory(nextChar.removedSystems, removedEntries),
  };

  return {
    nextChar: finalChar,
    removedEntries,
    removedIds: Array.from(removal.removedKeys),
    notifications: sideEffects.notifications || [],
    statusMessages: sideEffects.statusMessages || [],
    logEntries: sideEffects.logEntries || [],
    pendingSaves: sideEffects.pendingSaves || [],
    pendingNpcUpdates: sideEffects.pendingNpcUpdates || [],
    resourceEvents: sideEffects.resourceEvents || [],
  };
};

export const processSystemLifecycleExpiries = ({
  char,
  tickKind,
  systems = {},
  worldContext = null,
} = {}) => {
  const baseChar = char || {};
  const list = Array.isArray(baseChar.systemList) ? baseChar.systemList : [];
  if (!tickKind || list.length === 0) {
    return { nextChar: baseChar, removedIds: [], removedEntries: [], notifications: [], statusMessages: [] };
  }

  let changed = false;
  const decremented = list.map(instance => {
    const expiresIn = instance?.expiresIn;
    if (!expiresIn || typeof expiresIn !== 'object') return instance;
    const current = expiresIn[tickKind];
    if (typeof current !== 'number') return instance;
    changed = true;
    return {
      ...instance,
      expiresIn: {
        ...expiresIn,
        [tickKind]: current - 1,
      },
    };
  });

  const charAfterDecrement = changed ? { ...baseChar, systemList: decremented } : baseChar;
  const expiredIds = decremented
    .filter(instance => typeof instance?.expiresIn?.[tickKind] === 'number' && instance.expiresIn[tickKind] <= 0)
    .map(instance => getSystemInstanceKey(instance))
    .filter(Boolean);

  if (expiredIds.length === 0) {
    return {
      nextChar: charAfterDecrement,
      removedIds: [],
      removedEntries: [],
      notifications: [],
      statusMessages: [],
    };
  }

  const removal = removeSystemsWithLifecycle({
    char: charAfterDecrement,
    systemKeys: expiredIds,
    systems,
    worldContext,
    archiveDetails: `System expired after ${tickKind} tick`,
  });
  const expiryNotifications = removal.removedEntries.map(entry => {
    const template = getSystemTemplateForInstance(entry, systems);
    return `${template?.name || entry.systemId || 'System'} expired`;
  });

  return {
    ...removal,
    notifications: [...expiryNotifications, ...(removal.notifications || [])],
  };
};



// Tracker data field types

const TRACKER_FIELD_TYPES = [

  { id: 'number', label: 'Number', icon: '🔢', description: 'A numeric value with optional min/max' },

  { id: 'counter', label: 'Counter', icon: '📊', description: 'A value that counts up/down with buttons' },

  { id: 'progress', label: 'Progress Bar', icon: '📈', description: 'A visual progress bar with max value' },

  { id: 'timer', label: 'Timer', icon: '⏱️', description: 'Counts down days/hours/turns' },

  { id: 'checkbox', label: 'Checkbox', icon: '☑️', description: 'A simple on/off toggle' },

  { id: 'select', label: 'Dropdown', icon: '📋', description: 'Choose from predefined options' },

];



// Create a tracker data field

const createTrackerField = (type = 'number', label = 'Value') => ({

  id: `field-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,

  type,

  label,

  // Optional public-facing label shown to players instead of `label` when tracker is visible

  // (P3 — three-axis privacy applied at the tracker level). DM always sees `label`.

  // Falls back to `label` when empty. See docs/spell-resource-architecture.md §3.3.

  publicLabel: '',

  // Number/Counter/Progress fields

  value: 0,

  min: null,

  max: null,

  step: 1,

  // Thresholds for triggering effects

  thresholds: [], // [{ value: 5, effect: '-1 Str', notify: true, label: 'Stage 1' }]

  // Timer fields

  unit: 'days', // 'days', 'hours', 'turns'

  // Select fields

  options: [],

  selectedOption: null,

  // Display

  showToPlayer: false,

  color: '#c9a227',

});



// Check which thresholds are active for a tracker value

const getActiveThresholds = (field, currentValue) => {

  if (!field?.thresholds?.length) return [];

  return field.thresholds

    .filter(t => currentValue >= t.value)

    .sort((a, b) => b.value - a.value); // Highest first

};



// Get effects from all active thresholds

const getThresholdEffects = (field, currentValue) => {

  const active = getActiveThresholds(field, currentValue);

  return active.flatMap(t => t.effects || (t.effect ? [t.effect] : []));

};



// ============================================================================

// TECHNIQUE SHAPE (spell-resource binding) — Session 1 schema only.

// ============================================================================

// A "technique" is a binding between a system and a castable ability. It wraps a spell

// definition (from spellLibrary, or inlined here for simple cases) with system-specific

// metadata: costs, requirements, availability gates, per-technique automation rules, and

// a privacy flag grid controlling what the player sees.

//

// Sessions 2–5 extend this: rule engine triggers (on-cast, ...), editor UI, cast flow,

// reference implementation on Blood Magic. See docs/spell-resource-architecture.md.

//

// No editor UI writes this shape yet (Session 3). No caller reads `rules` or fires

// `on-cast` yet (Session 2). Schema is exposed here so rule-editor dropdowns and any

// future technique-aware code can import the factory from a single location.

const createTechnique = (overrides = {}) => ({

  id: `tech-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,

  name: 'New Technique',

  description: '',

  icon: '✨',

  order: 0,

  // Link to a library spell OR inline a minimal spell definition

  spellId: null,

  inlineSpell: null,

  // Cost array — each entry is { resource, amount?, percent?, level?, systemId?, trackerId?, ... }

  // See architecture doc §3.4 for the resource taxonomy.

  costs: [],

  forceCast: false,

  // Hard gates that must pass before the technique is available

  requirements: [],

  // Contextual gates (softer — can be shown greyed with a reason)

  availability: {

    combatOnly: false,

    restOnly: false,

    timeOfDay: null,

    environments: [],

    cooldown: null, // { scope: 'turn'|'round'|'short-rest'|'long-rest'|'day'|'encounter', count: 1 }

  },

  // Per-technique automation rules (fire in addition to system-wide rules)

  rules: [],

  // Privacy — what the player sees of this technique's metadata.

  // 2026-04-27: `name` defaults to true (hidden) per user feedback —

  // techniques are narrative reveals; the DM should opt IN to showing

  // the name. Existing techniques without a `hidden.name` field will

  // inherit the new default-hidden behavior at render time.

  hidden: {

    name: true,

    cost: false,

    // 2026-04-29: requirements default to true. Player sees "Unavailable"

    // with no reason text until DM opts in. Mirrors hidden.name —

    // requirements often gate on hidden trackers (addiction, corruption,

    // etc.) and the reason text leaks the gate's existence even when

    // publicLabel is set. DM toggles this off when the gate is

    // narratively visible (e.g. "HP must be ≤ 50%").

    requirements: true,

    rules: true,

    description: false,

    existence: false, // if true, player can't see the technique at all until unlocked

    // 2026-04-29 — BUG-REPORTS.md #10: hide the bound-spell mechanics

    // (dice / damage type / range / save DC / attack type) from player.

    // DM can show "Cost: 5 HP" but suppress "Spell: Fireball — 8d6 fire"

    // — useful when a technique is a re-skinned spell whose mechanical

    // shape is itself a narrative reveal.

    effects: false,

  },

  ...overrides,

});



// Option lists driving technique editor dropdowns. Order matches display order in the UI.

// Kept next to the factory so schema changes land in one place.

const RESOURCE_OPTIONS = [

  { id: 'hp',            label: 'HP',              icon: '❤️', description: 'Current hit points' },

  { id: 'hp-percent',    label: 'HP (%)',          icon: '❤️', description: 'Percentage of max HP' },

  { id: 'hp-all-but',    label: 'HP — all but N',  icon: '🖤', description: 'Drop HP to N (e.g. Exsanguinate)' },

  { id: 'temp-hp',       label: 'Temp HP',         icon: '🩵', description: 'Temporary HP pool' },

  { id: 'mana',          label: 'Mana',            icon: '🔷', description: 'Spell-point pool' },

  { id: 'tracker',       label: 'Tracker',         icon: '📊', description: 'A system tracker value' },

  { id: 'tracker-stack', label: 'Tracker (stack)', icon: '🔶', description: 'Stacks on a tracker (UI pills)' },

  { id: 'exhaustion',    label: 'Exhaustion',      icon: '😩', description: 'Adds exhaustion levels' },

  { id: 'hit-die',       label: 'Hit Die',         icon: '🎲', description: 'Spends a hit die' },

  { id: 'spell-slot',    label: 'Spell Slot',      icon: '✨', description: 'Standard 5e spell slot (by level)' },

];



const REQUIREMENT_OPTIONS = [

  { id: 'tracker-gte',      label: 'Tracker ≥ value', params: ['systemId', 'trackerId', 'value'] },

  { id: 'tracker-lte',      label: 'Tracker ≤ value', params: ['systemId', 'trackerId', 'value'] },

  { id: 'stage-gte',        label: 'Stage at least',  params: ['systemId', 'stageId'] },

  { id: 'has-condition',    label: 'Has condition',   params: ['name'] },

  { id: 'lacks-condition',  label: 'Lacks condition', params: ['name'] },

  { id: 'has-item',         label: 'Has item',        params: ['itemName', 'itemId', 'equipped'] },

  { id: 'hp-lte-percent',   label: 'HP ≤ % of max',   params: ['percent'] },

];



const COOLDOWN_SCOPES = [

  { id: '',            label: 'No cooldown' },

  { id: 'turn',        label: 'Once per turn' },

  { id: 'round',       label: 'Once per round' },

  { id: 'encounter',   label: 'Once per encounter' },

  { id: 'short-rest',  label: 'Once per short rest' },

  { id: 'long-rest',   label: 'Once per long rest' },

  { id: 'day',         label: 'Once per day' },

];



const TIME_OF_DAY_OPTIONS = [

  { id: '',         label: 'Any time' },

  { id: 'day',      label: 'Day' },

  { id: 'twilight', label: 'Twilight' },

  { id: 'night',    label: 'Night' },

];



// Factories keep defaults in one spot. Cost/requirement types are free-form objects shaped

// by the UI; these produce sensible starting points for a new "+ Add" click.

const createTechniqueCost = (resource = 'hp') => {

  const base = { resource };

  if (resource === 'hp' || resource === 'temp-hp' || resource === 'mana' || resource === 'exhaustion' || resource === 'hit-die') {

    base.amount = 1;

  } else if (resource === 'hp-percent') {

    base.percent = 10;

  } else if (resource === 'hp-all-but') {

    base.remaining = 1;

  } else if (resource === 'spell-slot') {

    base.level = 1;

  } else if (resource === 'tracker' || resource === 'tracker-stack') {

    base.amount = 1;

    base.systemId = '';

    base.trackerId = '';

  }

  return base;

};



const createTechniqueRequirement = (type = 'tracker-gte') => {

  const base = { type };

  switch (type) {

    case 'tracker-gte':

    case 'tracker-lte':

      base.systemId = '';

      base.trackerId = '';

      base.value = 1;

      break;

    case 'stage-gte':

      base.systemId = '';

      base.stageId = '';

      break;

    case 'has-condition':

    case 'lacks-condition':

      base.name = '';

      break;

    case 'has-item':

      base.itemName = '';

      base.itemId = '';

      base.equipped = false;

      break;

    case 'hp-lte-percent':

      base.percent = 50;

      break;

    default: break;

  }

  return base;

};



// Legacy compat: the original data shape had `system.spells[]` with simple

// { id, name, cost, costType, description, school } entries. When a system has no

// `techniques[]` but does have `spells[]`, synthesize a techniques view so callers can

// read through a single field name. Session 3's editor writes `techniques[]` explicitly

// and callers prefer reading through this helper over touching either field directly.

const getSystemTechniques = (sys) => {

  if (!sys) return [];

  if (Array.isArray(sys.techniques) && sys.techniques.length > 0) return sys.techniques;

  const legacy = Array.isArray(sys.spells) ? sys.spells : [];

  if (legacy.length === 0) return [];

  return legacy.map((sp, idx) => ({

    id: sp.id || `tech-legacy-${idx}`,

    name: sp.name || 'Technique',

    description: sp.description || '',

    icon: sp.icon || '✨',

    order: idx,

    spellId: null,

    inlineSpell: {

      name: sp.name,

      description: sp.description,

      school: sp.school || '',

      level: sp.level || 0,

    },

    costs: sp.cost != null ? [{

      resource: sp.costType === 'hp' ? 'hp'

        : sp.costType === 'hp-all' ? 'hp-all-but'

        : sp.costType === 'mana' ? 'mana'

        : 'tracker',

      amount: typeof sp.cost === 'number' ? sp.cost : undefined,

      remaining: sp.costType === 'hp-all' ? 1 : undefined,

    }] : [],

    forceCast: false,

    requirements: sp.tier ? [{ type: 'stage-gte', stageTier: sp.tier }] : [],

    availability: { combatOnly: false, restOnly: false, timeOfDay: null, environments: [], cooldown: null },

    rules: [],

    // 2026-04-29 — BUG-REPORTS.md #11: explicit name:true to match the v2

    // createTechnique convention (techniques are narrative reveals; DMs opt

    // IN to showing). Was implicit before — `name` undefined evaluated as

    // hidden via `!== false`, but emergent rather than intentional.

    // 2026-04-29 (later): requirements:true default too, same convention.

    hidden: { name: true, cost: false, requirements: true, rules: true, description: false, existence: false },

  }));

};



// Create a threshold entry

const createThreshold = (value = 0) => ({

  id: `threshold-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,

  value,

  label: '',

  effect: '',

  effects: [],

  notify: true,

  notifyMessage: '',

});



// ============================================================================

// PROGRESSION STAGES - Parse and manage disease/affliction stages

// ============================================================================



// Parse a stage line format: "Stage Name | Description | Effect1, Effect2"

const parseStage = (line) => {

  if (!line || typeof line !== 'string') return null;

  const parts = line.split('|').map(p => p.trim());

  return {

    name: parts[0] || 'Stage',

    description: parts[1] || '',

    effects: parts[2] ? parts[2].split(',').map(e => e.trim()).filter(Boolean) : [],

  };

};



// Get all stages from a stages section

const getStagesFromSection = (section) => {

  if (!section || section.kind !== 'stages') return [];

  return (section.items || []).map(parseStage).filter(Boolean);

};



// Get the current stage info from a system instance

const getCurrentStageInfo = (systemInstance, systemTemplate) => {

  if (!systemTemplate?.sections) return null;

  

  const stagesSection = systemTemplate.sections.find(s => s.kind === 'stages');

  if (!stagesSection) return null;

  

  const stages = getStagesFromSection(stagesSection);

  const currentStageIndex = systemInstance?.currentStage ?? stagesSection.currentStage ?? 0;

  

  if (currentStageIndex < 0 || currentStageIndex >= stages.length) return null;

  

  return {

    index: currentStageIndex,

    total: stages.length,

    stage: stages[currentStageIndex],

    isFirst: currentStageIndex === 0,

    isLast: currentStageIndex === stages.length - 1,

    allStages: stages,

  };

};



// Get effects for the current stage

const getStageEffects = (systemInstance, systemTemplate) => {

  const info = getCurrentStageInfo(systemInstance, systemTemplate);

  if (!info?.stage) return [];

  return info.stage.effects || [];

};



// ============================================================================

// ROLL TABLES - Dice rolling for roll table sections

// ============================================================================



// Parse dice notation (e.g., "d6", "d20", "2d6")

const parseDice = (notation) => {

  if (!notation || typeof notation !== 'string') return { count: 1, sides: 6 };

  const match = notation.match(/^(\d*)d(\d+)$/i);

  if (match) {

    return {

      count: parseInt(match[1]) || 1,

      sides: parseInt(match[2]) || 6,

    };

  }

  return { count: 1, sides: 6 };

};



// Roll on a roll table section and return the result

const rollOnTable = (section) => {

  if (!section || section.kind !== 'rolltable') return null;

  

  const items = section.items || [];

  if (items.length === 0) return null;

  

  const diceType = section.diceType || 'd6';

  const roll = rollDice(diceType);

  

  // Extract individual rolls from the rollDice result format

  // rollDice returns { total, rolls: [{ dice, rolls: [numbers], sign }], ... }

  const individualRolls = roll.rolls.flatMap(r => r.rolls || []);

  

  // Get the result based on roll (1-indexed, but array is 0-indexed)

  const resultIndex = Math.min(roll.total - 1, items.length - 1);

  const result = items[Math.max(0, resultIndex)] || items[items.length - 1];

  

  return {

    roll: roll.total,

    rolls: individualRolls,

    diceType,

    resultIndex: Math.max(0, resultIndex),

    result,

    section: section.label || 'Roll Table',

  };

};



// Get all roll table sections from a system

const getRollTableSections = (systemTemplate) => {

  if (!systemTemplate?.sections) return [];

  return systemTemplate.sections.filter(s => s.kind === 'rolltable');

};



// --- Instance slot resolution (Systems expansion Session 3) ---

// Substitutes `{{SLOT_KEY}}` placeholders in a string against an instance's `instanceValues`,

// falling back to the template's `instanceSlots[].default`, then to the raw placeholder

// (unresolved) so missing slots are visible rather than silently empty.

//

// Keys are matched case-insensitively on the left side ({{mentor}} and {{MENTOR}} both work)

// but stored case-sensitively; canonical form is whatever the slot definition declares.

// Whitespace inside braces is tolerated: {{ mentor }} → {{mentor}}.

//

// Non-string values pass through unchanged. Pure function — no side effects.

const resolveSlots = (str, instance, template) => {

  if (typeof str !== 'string' || !str.includes('{{')) return str;

  const values = instance?.instanceValues || {};

  const slots = template?.instanceSlots || [];

  const defaults = slots.reduce((acc, s) => {

    if (s?.key) acc[s.key.toLowerCase()] = s.default;

    return acc;

  }, {});

  const lookup = {};

  Object.entries(values).forEach(([k, v]) => { lookup[k.toLowerCase()] = v; });

  return str.replace(/\{\{\s*([A-Za-z0-9_.-]+)\s*\}\}/g, (match, key) => {

    const lower = key.toLowerCase();

    const val = lookup[lower];

    if (val !== undefined && val !== null && val !== '') return String(val);

    const def = defaults[lower];

    if (def !== undefined && def !== null && def !== '') return String(def);

    return match; // unresolved — leaves `{{KEY}}` visible so the gap is obvious.

  });

};



// Resolve any string fields in an action's params against an instance's slot values.

// Called from the rule engine just before an action executes so authored templates can

// carry `{{SLOT_KEY}}` placeholders in message/effect/amount strings.

const resolveActionParamSlots = (params, instance, template) => {

  if (!params || typeof params !== 'object') return params;

  const out = {};

  Object.entries(params).forEach(([k, v]) => {

    out[k] = typeof v === 'string' ? resolveSlots(v, instance, template) : v;

  });

  return out;

};



// Apply a bundle of pendingNpcUpdates from rule-engine results to an npcLibrary

// (Systems expansion Session 5). Pure: returns the next library plus a flag/notifications.

// Matches target NPC by npcId first, then case-insensitive npcName fallback. Unknown

// targets are skipped silently so a typo in an authored rule doesn't explode.

const applyPendingNpcUpdates = (npcLibrary, pendingNpcUpdates) => {

  if (!pendingNpcUpdates?.length) return { nextLibrary: npcLibrary, changed: false, notifications: [] };

  let next = { ...(npcLibrary || {}) };

  let changed = false;

  const notifications = [];

  pendingNpcUpdates.forEach(upd => {

    let target = null;

    let targetKey = null;

    if (upd.npcId && next[upd.npcId]) {

      target = next[upd.npcId];

      targetKey = upd.npcId;

    } else if (upd.npcName) {

      const nm = upd.npcName.toLowerCase();

      const entry = Object.entries(next).find(([, n]) => n?.name && n.name.toLowerCase() === nm);

      if (entry) { targetKey = entry[0]; target = entry[1]; }

    }

    if (!target || !targetKey) return;

    next[targetKey] = { ...target, ...upd.patch, updatedAt: new Date().toISOString() };

    changed = true;

    if (upd.patch?.disposition) {

      notifications.push(`${target.name || 'NPC'} → ${upd.patch.disposition}`);

    }

  });

  return { nextLibrary: next, changed, notifications };

};

export const collectRuleResultSideEffects = (results, { npcLibrary = null } = {}) => {
  if (!results) {
    return {
      notifications: [],
      statusMessages: [],
      logEntries: [],
      pendingSaves: [],
      pendingNpcUpdates: [],
      npcLibraryChanged: false,
      nextNpcLibrary: npcLibrary,
    };
  }

  const notifications = [...(Array.isArray(results.notifications) ? results.notifications : [])];
  const pendingNpcUpdates = Array.isArray(results.pendingNpcUpdates) ? results.pendingNpcUpdates : [];
  let nextNpcLibrary = npcLibrary;
  let npcLibraryChanged = false;

  if (pendingNpcUpdates.length > 0) {
    const npcResult = applyPendingNpcUpdates(npcLibrary, pendingNpcUpdates);
    nextNpcLibrary = npcResult.nextLibrary;
    npcLibraryChanged = !!npcResult.changed;
    notifications.push(...(npcResult.notifications || []));
  }

  return {
    notifications,
    statusMessages: Array.isArray(results.statusMessages) ? results.statusMessages : [],
    logEntries: Array.isArray(results.logEntries) ? results.logEntries : [],
    pendingSaves: Array.isArray(results.pendingSaves) ? results.pendingSaves : [],
    pendingNpcUpdates,
    npcLibraryChanged,
    nextNpcLibrary,
  };
};

const buildSystemRulePendingSaveNotice = (pendingSaves = [], label = 'System rule') => {
  const saves = Array.isArray(pendingSaves) ? pendingSaves : [];
  if (saves.length === 0) return null;
  const reasons = Array.from(new Set(saves
    .map(save => save?.reason)
    .filter(Boolean)))
    .slice(0, 3)
    .join('; ');
  return `${label} requested ${saves.length} saving throw(s)${reasons ? `: ${reasons}` : '.'}`;
};

const buildSystemRulePendingSaveQueueEntries = (pendingSaves = [], {
  char = null,
  systemInstance = null,
  systemTemplate = null,
  trackerValues = null,
  triggerContext = null,
  worldContext = null,
  label = 'System rule',
  source = 'systems',
} = {}) => {
  const saves = Array.isArray(pendingSaves) ? pendingSaves : [];
  return saves.map((pendingSave) => {
    const saveId = pendingSave?.id || generateId();
    return {
      id: saveId,
      pendingSave: {
        ...pendingSave,
        id: saveId,
        characterId: pendingSave?.characterId || char?.id || null,
        characterName: pendingSave?.characterName || char?.name || null,
        systemId: pendingSave?.systemId || systemTemplate?.id || systemInstance?.systemId || null,
        systemInstanceId: pendingSave?.systemInstanceId || systemInstance?.id || systemInstance?.systemId || null,
      },
      label,
      source,
      context: {
        char,
        systemInstance,
        systemTemplate,
        trackerValues: trackerValues || systemInstance?.trackerValues || {},
        triggerContext,
        worldContext,
      },
    };
  });
};

const buildSystemRuleTravelLogEntries = (logEntries = [], { char = null, action = 'system-rule-log', source = 'systems' } = {}) => {
  const entries = Array.isArray(logEntries) ? logEntries : [];
  return entries.map(entry => ({
    day: char?.currentDay || entry?.day || 1,
    character: char?.name || entry?.character || 'DM',
    action,
    source,
    systemId: entry?.systemId,
    message: entry?.message || entry?.text || 'System rule log',
    timestamp: entry?.timestamp || new Date().toISOString(),
  }));
};

const buildSystemRuleStatusMessages = (statusMessages = [], { char = null, source = 'systems' } = {}) => {
  const entries = Array.isArray(statusMessages) ? statusMessages : [];
  return entries
    .map(entry => {
      const content = entry?.content || entry?.text || entry?.message || '';
      if (!content) return null;
      const characterId = entry?.characterId || entry?.charId || char?.id || null;
      const characterName = entry?.characterName || char?.name || null;
      return {
        id: entry?.id || generateId(),
        type: entry?.type || 'system-rule',
        category: entry?.category || 'systems',
        timestamp: entry?.timestamp || new Date().toISOString(),
        content,
        text: entry?.text || content,
        urgency: entry?.urgency || 'normal',
        fromDM: entry?.fromDM !== false,
        visibility: entry?.visibility || (characterId ? 'private' : 'public'),
        recipients: Array.isArray(entry?.recipients)
          ? entry.recipients
          : (characterId ? [characterId] : []),
        characterId,
        characterName,
        systemId: entry?.systemId || null,
        systemName: entry?.systemName || null,
        source,
        ruleSource: entry?.source || null,
        dismissed: entry?.dismissed === true ? true : false,
      };
    })
    .filter(Boolean);
};

const persistSystemRuleStatusMessages = (
  statusMessages = [],
  { char = null, source = 'systems', messages = null, onSendStatusMessage = null, onSaveMessages = null, auditMeta = null } = {},
) => {
  const builtMessages = buildSystemRuleStatusMessages(statusMessages, { char, source });
  if (builtMessages.length === 0) return [];

  if (typeof onSendStatusMessage === 'function') {
    builtMessages.forEach(message => {
      const result = onSendStatusMessage({ message, auditMeta });
      if (result && typeof result.catch === 'function') {
        result.catch(err => console.error('Failed to send system rule status message:', err));
      }
    });
    return builtMessages;
  }

  if (typeof onSaveMessages === 'function') {
    onSaveMessages({
      ...(messages || {}),
      statusUpdates: [...builtMessages, ...((messages || {}).statusUpdates || [])],
    }, auditMeta || undefined);
    return builtMessages;
  }

  return [];
};

const persistSystemRuleTravelLogEntries = (
  logEntries = [],
  { char = null, action = 'system-rule-log', source = 'systems', groupTravelLog = [], onSaveGroupTravelLog = null } = {},
) => {
  const ruleLogEntries = buildSystemRuleTravelLogEntries(logEntries, { char, action, source });
  if (ruleLogEntries.length > 0 && typeof onSaveGroupTravelLog === 'function') {
    const keep = Math.max(0, 200 - ruleLogEntries.length);
    onSaveGroupTravelLog([...ruleLogEntries, ...((groupTravelLog || []).slice(0, keep))]);
  }
  return ruleLogEntries;
};

const resolveSystemRulePendingSaveSystemContext = (char = {}, pendingSave = {}, systems = {}) => {
  const systemInstanceId = pendingSave?.systemInstanceId || null;
  const systemId = pendingSave?.systemId || null;
  const systemList = Array.isArray(char?.systemList) ? char.systemList : [];
  const systemInstance = systemList.find(instance => (
    systemInstanceId && (instance?.id === systemInstanceId || instance?.systemId === systemInstanceId)
  )) || systemList.find(instance => (
    systemId && (instance?.systemId === systemId || instance?.id === systemId)
  )) || null;
  const templateId = systemInstance?.systemId || systemId || null;
  return {
    systemInstance,
    systemTemplate: templateId ? systems?.[templateId] || null : null,
    trackerValues: systemInstance?.trackerValues || {},
  };
};

const buildSystemRulePendingSaveQueueEntriesForCharacter = (pendingSaves = [], {
  char = null,
  systems = {},
  worldContext = null,
  triggerContext = null,
  label = 'System rule',
  source = 'systems',
} = {}) => {
  const saves = Array.isArray(pendingSaves) ? pendingSaves : [];
  return saves.flatMap((pendingSave) => {
    const context = resolveSystemRulePendingSaveSystemContext(char, pendingSave, systems);
    return buildSystemRulePendingSaveQueueEntries([pendingSave], {
      char,
      systemInstance: context.systemInstance,
      systemTemplate: context.systemTemplate,
      trackerValues: context.trackerValues,
      triggerContext,
      worldContext,
      label,
      source,
    });
  });
};

const resolveSystemRulePendingSaveQueueEntry = ({
  entry,
  outcome,
  systems = {},
  fallbackChar = null,
  fallbackWorldContext = null,
} = {}) => {
  if (!entry?.pendingSave) {
    return {
      queuedChar: fallbackChar,
      nextChar: fallbackChar,
      subResults: null,
      nestedSaveEntries: [],
    };
  }
  const context = entry.context || {};
  const queuedChar = context.char || fallbackChar;
  const worldContext = context.worldContext || fallbackWorldContext || null;
  if (!queuedChar?.id) {
    return {
      queuedChar,
      nextChar: queuedChar,
      subResults: null,
      nestedSaveEntries: [],
    };
  }
  const subResults = resolvePendingSave(entry.pendingSave, outcome, context);
  const instanceKey = context.systemInstance?.id || context.systemInstance?.systemId || entry.pendingSave?.systemInstanceId || null;
  const nextChar = instanceKey
    ? applyRuleDispatchToChar(queuedChar, instanceKey, subResults, systems, worldContext)
    : { ...queuedChar, ...applySystemRuleResults(queuedChar, subResults, systems, worldContext) };
  const nestedSaveEntries = buildSystemRulePendingSaveQueueEntriesForCharacter(subResults?.pendingSaves, {
    char: nextChar,
    systems,
    worldContext,
    triggerContext: context.triggerContext,
    label: `${entry.label || 'System rule'} follow-up`,
    source: `${entry.source || 'systems'}.save`,
  });
  return {
    queuedChar,
    nextChar,
    subResults,
    nestedSaveEntries,
  };
};



const getTemplateSlotKeys = (template) => (
  (Array.isArray(template?.instanceSlots) ? template.instanceSlots : [])
    .map(slot => slot?.key)
    .filter(Boolean)
);

export const getSystemInstanceSlotValidationErrors = (instanceSlots = []) => {
  const slots = Array.isArray(instanceSlots) ? instanceSlots : [];
  const errors = [];
  const seen = new Map();
  slots.forEach((slot, index) => {
    const key = String(slot?.key || '').trim();
    if (!key) {
      errors.push({ index, key, message: `Slot ${index + 1} needs a non-empty key.` });
      return;
    }
    if (seen.has(key)) {
      errors.push({
        index,
        key,
        message: `Slot key "${key}" is duplicated by slot ${seen.get(key) + 1}.`,
      });
      return;
    }
    seen.set(key, index);
  });
  return errors;
};

const seedSystemInstanceSlotValues = (template, explicitSlots = {}) => (
  (Array.isArray(template?.instanceSlots) ? template.instanceSlots : []).reduce((acc, slot) => {
    if (!slot?.key) return acc;
    const explicit = explicitSlots?.[slot.key];
    acc[slot.key] = (explicit !== undefined && explicit !== '') ? explicit : (slot.default ?? '');
    return acc;
  }, {})
);

const normalizeSlotIdentityValue = (value) => (
  value === null || value === undefined ? '' : String(value)
);

const getSystemSlotIdentityParts = (template, slotValues = {}) => (
  getTemplateSlotKeys(template)
    .sort()
    .map(key => [key, normalizeSlotIdentityValue(slotValues[key])])
);

const stableHashString = (value) => {
  const text = String(value || '');
  let hash = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

const buildStableSlottedSystemInstanceId = (template, { parentSystemId = null, slotValues = {} } = {}) => {
  const slotParts = getSystemSlotIdentityParts(template, slotValues);
  if (slotParts.length === 0) return null;
  const templateId = template?.id || 'system';
  const safeTemplateId = String(templateId).replace(/[^A-Za-z0-9_-]/g, '-').slice(0, 48) || 'system';
  const identityText = JSON.stringify({
    systemId: templateId,
    parentSystemId: parentSystemId || null,
    slots: slotParts,
  });
  return `sys-${safeTemplateId}-${stableHashString(identityText)}`;
};

const systemInstanceTargetsTemplate = (instance, targetSystemId) => (
  !!instance
  && !!targetSystemId
  && (instance.systemId === targetSystemId || instance.template?.id === targetSystemId)
);

const systemInstanceMatchesApplyTarget = (instance, targetSystemId, template, slotValues = {}, parentSystemId = null) => {
  if (!systemInstanceTargetsTemplate(instance, targetSystemId)) return false;
  const slotParts = getSystemSlotIdentityParts(template, slotValues);
  if (slotParts.length === 0) return true;
  if ((instance.parentSystemId || null) !== (parentSystemId || null)) return false;
  const instanceSlotValues = seedSystemInstanceSlotValues(template, instance.instanceValues || {});
  return slotParts.every(([key, value]) => normalizeSlotIdentityValue(instanceSlotValues[key]) === value);
};

// Build a fresh instance entry for a programmatic apply-system dispatch. Seeds slot

// values from the template's declared defaults merged with explicit slots. Carries

// parentSystemId so remove-cascade works.

const buildAppliedInstance = (template, { parentSystemId = null, slots = {}, expiresIn = null } = {}) => {

  if (!template) return null;

  const preset = SYSTEM_PRESETS[template.type] || SYSTEM_PRESETS.custom;

  const defaultVis = preset.defaultVisibility || {};

  const seededSlotValues = seedSystemInstanceSlotValues(template, slots);
  const stableId = buildStableSlottedSystemInstanceId(template, { parentSystemId, slotValues: seededSlotValues });

  return {

    id: stableId || `sys-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,

    systemId: template.id || null,

    template: null,

    parentSystemId,

    expiresIn,

    playerVisible: defaultVis.playerVisible !== false,

    active: true,

    showName: defaultVis.showName || false,

    showDescription: defaultVis.showDescription || false,

    visibleClues: [],

    showEffects: defaultVis.showEffects || false,

    showCure: defaultVis.showCure || false,

    applyEffects: true,

    applyHiddenEffects: template.type === 'secret' || template.type === 'curse',

    effectOverrides: {},

    visibleSections: {},

    showSections: {},

    trackerVisibility: {},

    trackerValues: {},

    instanceValues: seededSlotValues,

    unknownText: UNKNOWN_PLACEHOLDER,

    placeholderName: '',

    placeholderDescription: '',

    cluesLabel: 'Clues',

    showCluesLabel: true,

    dmNotes: '',

    playerNotes: '',

    showPlayerNotes: false,

    appliedAt: new Date().toISOString(),

    history: [{ action: 'applied', timestamp: new Date().toISOString(), details: parentSystemId ? `Applied by parent system ${parentSystemId}` : 'System applied' }],

  };

};



// Create a new system template from a preset

export const createSystemFromPreset = (presetId, name = 'New System') => {

  const preset = SYSTEM_PRESETS[presetId] || SYSTEM_PRESETS.custom;

  const typeOption = SYSTEM_TYPE_OPTIONS.find(t => t.value === preset.type);

  const trackerFields = (preset.defaultTrackerFields || []).map(f => ({ ...f, id: `${f.id}-${Date.now()}` }));

  const { resourceProfiles, directSpellBindings } = buildSystemPresetResourceModel(

    preset,

    name,

    trackerFields

  );

  

  return {

    id: `system-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,

    type: preset.type,

    name: name,

    publicName: '',

    description: '',

    publicDescription: '',

    icon: typeOption?.icon || '🔮',

    sections: preset.defaultSections.map(s => ({ ...s, id: `${s.id}-${Date.now()}` })),

    clues: [],

    effects: [],           // Visible effects: [{ displayText, mechanics: [...] }]

    hiddenEffects: [],     // Hidden effects (applied but source unknown to player)

    cures: [],

    locations: [],

    tags: [],

    // Tracker fields (for tracker type)

    trackerFields,

    resourceProfiles,

    directSpellBindings,

    // Custom data fields (for trackers)

    customData: preset.hasCustomData ? {} : null,

    // Automation rules (advanced)

    rules: [],

    // Instance slots — template parameterization (Systems expansion Session 3).

    // Each slot: { key, label, default, description }. Instance carries `instanceValues`

    // which fill `{{SLOT_KEY}}` placeholders in names/descriptions/rule messages at

    // render/eval time via resolveSlots(). Empty by default; DM adds slots in editor.

    instanceSlots: [],

    // Cascade removal (Systems expansion Session 4). When true, removing an instance of

    // this template also removes every child instance whose parentSystemId matches the

    // parent's instance id. Used for parent-spawns-children patterns (e.g. Bloodline

    // parent system applies a child "Blood Debt" system that should lift when Bloodline does).

    cascadeOnRemove: false,

    // Metadata

    createdAt: new Date().toISOString(),

    deletable: true,

  };

};



// Create a system instance (when applying to a character/item)

const createSystemInstance = (templateId, template = null) => {

  const preset = template ? SYSTEM_PRESETS[template.type] : null;

  const defaultVis = preset?.defaultVisibility || {};

  

  return {

    instanceId: `inst-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,

    systemId: templateId,

    template: template, // Can embed template for custom one-off systems

    active: true,

    // Visibility controls

    playerVisible: defaultVis.playerVisible !== false,

    showName: defaultVis.showName || false,

    showDescription: defaultVis.showDescription || false,

    showEffects: defaultVis.showEffects || false,

    showCure: defaultVis.showCure || false,

    visibleClues: [],

    showSections: {},      // { sectionId: true/false } for text sections

    visibleSections: {},   // { sectionId: [itemIndices] } for list sections

    // Placeholder text shown to players when real content is hidden

    unknownText: UNKNOWN_PLACEHOLDER,  // Options: '??', 'Unknown', ':::::::::::', '??'

    placeholderName: '',   // Custom name shown when showName is false

    placeholderDescription: '', // Custom description shown when showDescription is false

    // Clues label customization

    cluesLabel: 'Clues',  // Custom label for clues section (or '' to hide)

    showCluesLabel: true, // Whether to show the clues label at all

    // Badge appearance

    badgeColor: null,     // Custom badge color from BUFF_COLOR_OPTIONS (null = default)

    notifyGlow: false,    // Whether to add glow effect when notifying

    glowColorId: 'gold',  // Color for glow effect

    // Effect application

    applyEffects: true,

    applyHiddenEffects: true,

    // Per-effect controls: { 'visible-0': { apply: true, showSource: false }, 'hidden-0': { apply: true } }

    effectOverrides: {},

    // Per-tracker visibility: { 'trackerId': { showToPlayer: true, showBar: true, showValue: true, showSource: true } }

    trackerVisibility: {},

    trackerValues: {},

    // Notes

    dmNotes: '',

    playerNotes: '',

    showPlayerNotes: false,

    // Custom data values (for trackers)

    customDataValues: {},

    // Progression

    currentStage: null,

    // Timestamps

    appliedAt: new Date().toISOString(),

  };

};



const makeUniqueSystemId = (name, takenIds = new Set()) => {

  const base = (name || 'system').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'system';

  let id = base;

  let i = 2;

  while (takenIds.has(id)) {

    id = `${base}-${i++}`;

  }

  takenIds.add(id);

  return id;

};

const getSystemBundleOriginalId = (system = {}, fallbackId = null) => (
  system.originalId || system.exportedId || system.id || fallbackId || null
);

export const buildSystemExportBundle = (systemsMap = {}, systemIds = [], options = {}) => {

  const exportedAt = options.exportedAt || new Date().toISOString();

  const systems = (Array.isArray(systemIds) ? systemIds : [])

    .map(id => {

      const system = systemsMap?.[id];

      if (!system) return null;

      return {

        ...system,

        originalId: getSystemBundleOriginalId(system, id),

        id: undefined,

        deletable: true,

      };

    })

    .filter(Boolean);

  return {

    version: 1,

    exportedAt,

    systems,

  };

};

export const prepareImportedSystemsBundle = ({

  currentSystems = {},

  importedSystems = [],

  importedAt = null,

} = {}) => {

  const validSystems = (Array.isArray(importedSystems) ? importedSystems : [])

    .filter(system => system?.name);

  const takenIds = new Set(Object.keys(currentSystems || {}));

  const assignments = validSystems.map(system => {

    const originalId = getSystemBundleOriginalId(system);

    const newId = makeUniqueSystemId(system.name, takenIds);

    return { system, originalId, newId };

  });

  const systemIdMap = new Map();

  assignments.forEach(({ originalId, newId }) => {

    if (originalId) systemIdMap.set(originalId, newId);

  });

  const nextSystems = { ...(currentSystems || {}) };

  const timestamp = importedAt || new Date().toISOString();

  assignments.forEach(({ system, originalId, newId }) => {

    const {

      id: _discardedId,

      originalId: _discardedOriginalId,

      exportedId: _discardedExportedId,

      ...rest

    } = system;

    nextSystems[newId] = remapSystemReferenceGraph({

      ...rest,

      id: newId,

      deletable: true,

      importedAt: timestamp,

      importedFromSystemId: originalId || null,

    }, {

      systemIdMap,

    });

  });

  return {

    systems: normalizeSystemsMap(nextSystems),

    imported: assignments.length,

    importedIds: assignments.map(({ newId }) => newId),

    systemIdMap,

  };

};



// "Systems" are generalized hidden/revealed mechanics (curses, marks, omens, conditions, etc.)

const DEFAULT_SYSTEMS = {

  'watched-mark': {

    id: 'watched-mark',

    type: 'mark',

    name: 'Mark of the Watched',

    publicName: 'Uneasy Feeling',

    description: 'A subtle mark that suggests a presence is tracking the target. The true nature may be hidden until revealed.',

    publicDescription: 'You can’t shake the feeling that something is watching you.',

    clues: [

      'Shadows linger a beat too long.',

      'You occasionally hear a second set of footsteps.',

    ],

    effects: ['-1 Wis'],

    hiddenEffects: ['-1 to Perception checks'],

    sections: [],

    cures: [{ item: 'Consecrated Water', quantity: 1 }],

    locations: ['Forest', 'Cave'],

    tags: ['omen', 'horror'],

    deletable: false,

  },

  'black-vein-curse': {

    id: 'black-vein-curse',

    type: 'curse',

    name: 'Black Vein Curse',

    publicName: 'Faint Dark Veins',

    description: 'A slow corruption that spreads from a wound and dulls the body.',

    publicDescription: 'Dark veins spider across your skin from a painful wound.',

    clues: [

      'The veins throb when you lie.',

      'Cold iron tastes sweet on your tongue.',

    ],

    effects: ['-1 Con'],

    hiddenEffects: ['-2 to saving throws vs curses'],

    sections: [],

    cures: [{ item: 'Blessing Ritual', quantity: 1 }],

    locations: ['Swamp', 'Desert', 'Cave'],

    tags: ['curse', 'corruption'],

    deletable: false,

  },

  'corruption-tracker': {

    id: 'corruption-tracker',

    type: 'tracker',

    name: 'Corruption Level',

    publicName: 'Inner Darkness',

    description: 'Tracks how corrupted a character has become. At certain thresholds, dark manifestations occur.',

    publicDescription: 'A creeping darkness within you grows stronger.',

    clues: [],

    effects: [],

    hiddenEffects: [],

    sections: [

      { id: 'description', label: 'Description', kind: 'text', text: 'Each evil act or exposure to dark magic increases corruption.', dmOnly: false },

      { id: 'thresholds', label: 'Threshold Effects', kind: 'list', items: [

        '5+ Corruption: -1 to saves vs evil',

        '10+ Corruption: -1 Cha, Disadvantage on Persuasion',

        '15+ Corruption: Eyes turn black, NPCs are uneasy',

        '20+ Corruption: -2 Wis, Risk of losing control'

      ], dmOnly: true },

    ],

    cures: [],

    locations: [],

    tags: ['tracker', 'corruption'],

    trackerFields: [

      { 

        id: 'corruption-level', 

        type: 'progress', 

        label: 'Corruption', 

        value: 0, 

        min: 0, 

        max: 20, 

        step: 1, 

        showToPlayer: true, 

        color: '#8b0000',

        thresholds: [

          { id: 'thresh-5', value: 5, label: 'Stage 1', effect: '-1 to saves vs evil' },

          { id: 'thresh-10', value: 10, label: 'Stage 2', effect: '-1 Cha' },

          { id: 'thresh-15', value: 15, label: 'Stage 3', effect: '-1 Wis' },

          { id: 'thresh-20', value: 20, label: 'Stage 4', effect: '-2 Wis' },

        ]

      }

    ],

    deletable: false,

  },

  'fey-blessing': {

    id: 'fey-blessing',

    type: 'blessing',

    name: 'Blessing of the Summer Court',

    publicName: 'Fey Touched',

    description: 'A blessing bestowed by a powerful fey creature, granting subtle enhancements.',

    publicDescription: 'A warm, golden light seems to follow you, and flowers bloom in your presence.',

    clues: [

      'Animals are drawn to you.',

      'You find it easier to see in moonlight.',

    ],

    effects: ['+1 Cha', '+2 to Nature checks'],

    hiddenEffects: [],

    sections: [

      { id: 'source', label: 'Source', kind: 'text', text: 'Granted by the Archfey Titania during the summer solstice.', dmOnly: false },

    ],

    cures: [],

    locations: ['Forest'],

    tags: ['blessing', 'fey'],

    trackerFields: [],

    deletable: false,

  },

  'spreading-rot': {

    id: 'spreading-rot',

    type: 'affliction',

    name: 'Spreading Rot',

    publicName: 'The Blight',

    description: 'A magical disease that slowly spreads through the body. Requires daily Con saves to prevent progression. Can be cured with Lesser Restoration or 3 successful saves in a row.',

    publicDescription: 'Dark veins spread from the wound, bringing weakness and pain.',

    clues: [

      'The wound refuses to heal properly.',

      'Dark lines spread from the infection site.',

      'Fatigue sets in faster than normal.',

    ],

    effects: ['-1 Con'],

    hiddenEffects: [],

    sections: [

      { id: 'stages', label: 'Disease Stages', kind: 'stages', items: [

        'Stage 1: Infection | Dark veins appear around the wound | -1 Con',

        'Stage 2: Spreading | Veins spread to nearby limbs | -2 Con, -1 Str',

        'Stage 3: Systemic | Infection reaches vital organs | -3 Con, -2 Str, Exhaustion 1',

        'Stage 4: Terminal | Body begins to shut down | -4 Con, -3 Str, Exhaustion 2',

      ], currentStage: 0, dmOnly: false },

      { id: 'dm-notes', label: 'DM Notes', kind: 'text', text: 'Roll DC 12 Con save each day. Failure advances the stage. 3 successes in a row cures it. Lesser Restoration immediately cures.', dmOnly: true },

    ],

    cures: [{ item: 'Lesser Restoration', quantity: 1 }, { item: '3 successful Con saves', quantity: 1 }],

    locations: ['Swamp', 'Undead encounter'],

    tags: ['disease', 'progressive', 'con-save'],

    trackerFields: [

      { id: 'save-successes', type: 'counter', label: 'Con Save Successes', value: 0, min: 0, max: 3, step: 1, showToPlayer: true, color: '#27c95a', thresholds: [{ value: 3, label: 'Cured!', effect: 'Disease cured by saving throws' }] },

    ],

    automationRules: [

      {

        id: 'rule-daily-save',

        name: 'Daily Con Save',

        enabled: true,

        trigger: 'on-day-start',

        conditions: [{ type: 'always', params: {} }],

        conditionLogic: 'all',

        actions: [{ type: 'notify', params: { message: '🦠 Spreading Rot: Make a DC 12 Constitution saving throw!' } }],

      },

      {

        id: 'rule-failed-save',

        name: 'Failed Save - Advance',

        enabled: true,

        trigger: 'manual',

        conditions: [{ type: 'always', params: {} }],

        conditionLogic: 'all',

        actions: [

          { type: 'advance-stage', params: {} },

          { type: 'change-tracker', params: { trackerId: 'save-successes', operation: 'set', value: 0 } },

          { type: 'notify', params: { message: '💀 The disease worsens! Stage advanced.' } },

        ],

      },

      {

        id: 'rule-passed-save',

        name: 'Passed Save - Progress',

        enabled: true,

        trigger: 'manual',

        conditions: [{ type: 'always', params: {} }],

        conditionLogic: 'all',

        actions: [

          { type: 'change-tracker', params: { trackerId: 'save-successes', operation: 'add', value: 1 } },

          { type: 'notify', params: { message: '✅ Success! Keep fighting the infection.' } },

        ],

      },

    ],

    deletable: false,

  },

  'haunted-secret': {

    id: 'haunted-secret',

    type: 'secret',

    name: 'The Restless Spirit',

    publicName: '',

    description: 'A ghost has attached itself to this character and whispers dark secrets. The character is unaware but suffers nightmares.',

    publicDescription: '',

    clues: [

      'You wake exhausted, unable to remember your dreams.',

      'Sometimes you hear whispers at the edge of your hearing.',

      'Others say you mutter in an unknown language while sleeping.',

    ],

    effects: [],

    hiddenEffects: ['-1 to long rest recovery', 'Disadvantage on saves vs fear'],

    sections: [

      { id: 'dm-notes', label: 'DM Notes', kind: 'text', text: 'The spirit is the ghost of a murdered noble seeking revenge. Will reveal itself when the party reaches Ravenmoor.', dmOnly: true },

      { id: 'revelation', label: 'Revelation Text', kind: 'text', text: 'A translucent figure materializes beside you, its hollow eyes filled with sorrow and rage. You will help me... or share my fate.', dmOnly: false },

    ],

    cures: [{ item: 'Help the spirit find peace', quantity: 1 }],

    locations: [],

    tags: ['secret', 'ghost', 'horror'],

    trackerFields: [],

    deletable: false,

  },

  

  // ===== CREATIVE DEMO SYSTEMS =====

  // These demonstrate creative uses of the placeholder system

  

  'observed-presence': {

    id: 'observed-presence',

    type: 'secret',

    name: 'Divine Observation',

    publicName: '',

    description: 'A deity or powerful entity has taken notice of this character. They are being watched and judged. Use placeholder to create mystery.',

    publicDescription: '',

    clues: [

      'You feel a presence watching you during prayer.',

      'Your holy symbol glows faintly at dawn.',

      'A voice whispers "Interesting..." in moments of moral choice.',

    ],

    effects: [],

    hiddenEffects: ['+1 to Religion checks'],

    sections: [

      { id: 'dm-notes', label: 'DM Notes', kind: 'text', text: 'The god Torm is observing this character as a potential champion. Reveal clues based on their actions. If they act virtuously, offer them a divine boon.', dmOnly: true },

    ],

    cures: [],

    locations: [],

    tags: ['divine', 'mystery', 'demo-placeholder'],

    // DEMO: Set placeholderName to "Observed." and placeholderDescription to "You feel like you are being watched by something powerful."

    trackerFields: [],

    deletable: true,

  },

  

  'prophetic-dreams': {

    id: 'prophetic-dreams',

    type: 'omen',

    name: 'The Dreamwalker\'s Mark',

    publicName: 'Vivid Dreams',

    description: 'The character receives prophetic visions during sleep. Use to foreshadow events or give cryptic hints.',

    publicDescription: 'Your dreams have become unusually vivid and memorable.',

    clues: [

      'You dream of a tower wreathed in silver flames.',

      'In your dreams, you see a face you don\'t recognize - yet.',

      'A recurring dream shows you standing at a crossroads.',

      'You dream of your companions, but they have different faces.',

    ],

    effects: [],

    hiddenEffects: [],

    sections: [

      { id: 'dream-journal', label: 'Dream Journal', kind: 'list', items: [

        'Night 1: A dark tower rises from a frozen lake.',

        'Night 3: A woman with silver eyes speaks your name.',

        'Night 7: You walk through a city that hasn\'t been built yet.',

      ], dmOnly: false },

      { id: 'dm-key', label: 'Dream Key (DM Only)', kind: 'text', text: 'Tower = The villain\'s lair. Silver eyes = The oracle they\'ll meet in Chapter 3. Future city = Their destiny to become rulers.', dmOnly: true },

    ],

    cures: [],

    locations: [],

    tags: ['omen', 'prophecy', 'demo-clues'],

    trackerFields: [],

    deletable: true,

  },

  

  'debt-tracker': {

    id: 'debt-tracker',

    type: 'tracker',

    name: 'Thieves\' Guild Debt',

    publicName: 'Outstanding Debt',

    description: 'Tracks money owed to a dangerous organization. Interest accumulates. Collectors may visit.',

    publicDescription: 'You owe money to people who don\'t forget.',

    clues: [],

    effects: [],

    hiddenEffects: [],

    sections: [

      { id: 'terms', label: 'Terms', kind: 'text', text: 'Original loan: 500gp. Interest: 10% per week. Collectors visit at 1000gp owed.', dmOnly: false },

    ],

    cures: [{ item: 'Full repayment', quantity: 1 }],

    locations: [],

    tags: ['tracker', 'social', 'demo-tracker'],

    trackerFields: [

      { 

        id: 'debt-amount', 

        type: 'number', 

        label: 'Gold Owed', 

        value: 500, 

        min: 0, 

        max: 5000, 

        step: 50, 

        showToPlayer: true, 

        color: '#ff6961',

        thresholds: [

          { id: 'thresh-1000', value: 1000, label: 'Collectors Coming', effects: ['Disadvantage on Persuasion with guild contacts'] },

          { id: 'thresh-2000', value: 2000, label: 'Bounty Posted', effects: ['-2 to Hide checks in cities'] },

          { id: 'thresh-3000', value: 3000, label: 'Mark for Death', effects: ['Assassins are hunting you'] },

        ]

      }

    ],

    automationRules: [

      {

        id: 'weekly-interest',

        name: 'Weekly Interest',

        enabled: true,

        trigger: 'on-week-end',

        conditions: [{ type: 'tracker-above', params: { trackerId: 'debt-amount', value: 0 } }],

        conditionLogic: 'all',

        actions: [

          { type: 'change-tracker', params: { trackerId: 'debt-amount', operation: 'multiply', value: 1.1 } },

          { type: 'notify', params: { message: '💰 Interest accrued! Your debt grows...' } },

        ],

      },

    ],

    deletable: true,

  },

  

  'lycanthropy-progression': {

    id: 'lycanthropy-progression',

    type: 'affliction',

    name: 'Lycanthropy (Werewolf)',

    publicName: 'The Beast Within',

    description: 'Progressive werewolf curse with stages. Full moon triggers transformation.',

    publicDescription: 'Something is changing within you. Your senses sharpen, your temper shortens.',

    clues: [

      'Raw meat has become appetizing.',

      'Dogs growl at you; wolves watch you with strange recognition.',

      'Your wounds heal faster than they should.',

      'The full moon fills you with restless energy.',

    ],

    effects: ['+1 Con'],

    hiddenEffects: ['+2 to Perception at night'],

    sections: [

      { id: 'stages', label: 'Curse Stages', kind: 'stages', items: [

        'Stage 1: Infection | Heightened senses, appetite changes | +1 Con',

        'Stage 2: Bonding | Dreams of the hunt, silver burns | +1 Str, +1 Con, Silver vulnerability',

        'Stage 3: First Change | Involuntary transformation on full moon | +2 Str, +2 Con, Regeneration',

        'Stage 4: Mastered | Voluntary transformation possible | Full lycanthrope abilities',

      ], currentStage: 0, dmOnly: false },

    ],

    cures: [{ item: 'Remove Curse (before Stage 3)', quantity: 1 }, { item: 'Wolfsbane ritual under new moon', quantity: 1 }],

    locations: ['Forest', 'Mountains'],

    tags: ['curse', 'lycanthropy', 'progressive', 'demo-stages'],

    trackerFields: [

      { id: 'moon-nights', type: 'counter', label: 'Nights Until Full Moon', value: 14, min: 0, max: 28, step: 1, showToPlayer: true, color: '#c9c9c9' },

    ],

    deletable: true,

  },

  

  'qi-cultivation': {

    id: 'qi-cultivation',

    type: 'tracker',

    name: 'Qi Cultivation Progress',

    publicName: 'Inner Energy',

    description: 'Track cultivation progress in the Way of Qi. Progress is measured in years and months of dedicated practice. Reaching certain thresholds unlocks new abilities.',

    publicDescription: 'You have begun walking the path of cultivation, refining your inner energy.',

    clues: [

      'Your breathing becomes naturally rhythmic during meditation.',

      'You can sense the flow of energy in living things.',

      'Time seems to pass differently when you enter a meditative state.',

    ],

    effects: [],

    hiddenEffects: [],

    sections: [

      { id: 'cultivation-guide', label: 'Cultivation Guide', kind: 'text', text: 'Each month of dedicated practice (4 hours/day minimum) adds 1 month. Breakthrough items, sacred locations, or enlightenment events can add years instantly.', dmOnly: false },

      { id: 'realm-effects', label: 'Realm Effects (DM)', kind: 'list', items: [

        'Qi Gathering (1+ years): Can sense Qi, +1 to meditation checks',

        'Foundation (10+ years): Slow aging, +1 Con, can project Qi externally',

        'Core Formation (50+ years): Ageless, +2 Con, +1 Wis, Qi Shield (reaction)',

        'Nascent Soul (100+ years): Spiritual projection, +2 all mental stats',

        'Deity (500+ years): Immortal, can create pocket dimensions',

      ], dmOnly: true },

    ],

    cures: [],

    locations: [],

    tags: ['cultivation', 'progression', 'eastern', 'demo-resources'],

    trackerFields: [

      { 

        id: 'qi-years', 

        type: 'number', 

        label: 'Years of Cultivation', 

        value: 0, 

        min: 0, 

        max: 1000, 

        step: 1, 

        showToPlayer: true, 

        color: '#9b59b6',

        thresholds: [

          { id: 'realm-1', value: 1, label: 'Qi Gathering', effect: '+1 to meditation checks' },

          { id: 'realm-2', value: 10, label: 'Foundation', effect: '+1 Con, Slow Aging' },

          { id: 'realm-3', value: 50, label: 'Core Formation', effect: '+2 Con, +1 Wis, Ageless' },

          { id: 'realm-4', value: 100, label: 'Nascent Soul', effect: '+2 Int, +2 Wis, +2 Cha' },

          { id: 'realm-5', value: 500, label: 'Deity Realm', effect: 'Immortal, Legendary Power' },

        ]

      },

      { 

        id: 'qi-months', 

        type: 'counter', 

        label: 'Months', 

        value: 0, 

        min: 0, 

        max: 11, 

        step: 1, 

        showToPlayer: true, 

        color: '#8e44ad',

      },

      { 

        id: 'qi-pool', 

        type: 'progress', 

        label: 'Qi Reserve', 

        value: 10, 

        min: 0, 

        max: 100, 

        step: 5, 

        showToPlayer: true, 

        color: '#00bcd4',

      },

    ],

    progressionStages: [

      { id: 'mortal', name: 'Mortal', description: 'You have not yet begun to cultivate.', effects: [] },

      { id: 'qi-gathering', name: 'Qi Gathering', description: 'You can sense and gather ambient Qi.', effects: ['+1 to meditation'] },

      { id: 'foundation', name: 'Foundation Establishment', description: 'Your Qi foundation is stable.', effects: ['+1 Con', 'Slow Aging'] },

      { id: 'core-formation', name: 'Core Formation', description: 'A golden core forms within your dantian.', effects: ['+2 Con', '+1 Wis', 'Ageless'] },

      { id: 'nascent-soul', name: 'Nascent Soul', description: 'Your soul can exist independently of your body.', effects: ['+2 Int', '+2 Wis', '+2 Cha'] },

      { id: 'deity', name: 'Deity', description: 'You have transcended mortality.', effects: ['Immortal', '+3 All Stats'] },

    ],

    automationRules: [

      {

        id: 'month-rollover',

        name: 'Month to Year Rollover',

        description: 'When months reach 12, convert to 1 year',

        enabled: true,

        trigger: 'on-tracker-change',

        conditions: [

          { type: 'tracker-value', trackerId: 'qi-months', operator: 'gte', value: 12 }

        ],

        actions: [

          { type: 'change-tracker', trackerId: 'qi-months', operation: 'subtract', value: 12 },

          { type: 'change-tracker', trackerId: 'qi-years', operation: 'add', value: 1 },

          { type: 'notify', message: '🎉 A year of cultivation complete! Your Qi deepens.' }

        ],

        order: 1,

      }

    ],

    deletable: true,

  },

  

  // ========== OATH DEMO SYSTEMS ==========

  'oath-of-silence': {

    id: 'oath-of-silence',

    type: 'oath',

    name: 'Oath of Silence',

    publicName: 'Bound Tongue',

    description: 'A magical or sacred oath preventing the character from speaking about certain topics. Breaking the oath has severe consequences.',

    publicDescription: 'You are bound by a powerful oath. Certain words cannot leave your lips.',

    clues: [],

    effects: [],

    hiddenEffects: [],

    sections: [

      { id: 'terms', label: 'Oath Terms', kind: 'text', text: 'You cannot speak, write, or communicate in any way about [TOPIC]. Attempts to circumvent this through riddles, hints, or third parties also trigger the oath.', dmOnly: false },

      { id: 'restrictions', label: 'Restrictions', kind: 'list', items: [

        'Cannot speak directly about the forbidden topic',

        'Cannot write or draw information about it',

        'Cannot use Sending, Message, or telepathy to convey it',

        'Cannot nod, gesture, or otherwise confirm questions about it',

      ], dmOnly: false },

      { id: 'consequences', label: 'Breaking Consequences', kind: 'text', text: 'First violation: 3d10 psychic damage, cannot speak for 1 hour. Second violation: 6d10 psychic damage, mute for 24 hours. Third violation: Permanent curse - disadvantage on all Charisma checks.', dmOnly: true },

      { id: 'dm-notes', label: 'DM Notes', kind: 'text', text: 'The oath was sworn to [WHO] in exchange for [WHAT]. The player may not remember swearing it (memory modification). Consider allowing Investigation or Insight checks to notice their own compulsion.', dmOnly: true },

    ],

    cures: [

      { item: 'Greater Restoration from oath-giver', quantity: 1 },

      { item: 'Wish spell', quantity: 1 },

      { item: 'Death of the oath-giver', quantity: 1 },

    ],

    locations: [],

    tags: ['oath', 'restriction', 'social', 'demo-oath'],

    trackerFields: [],

    deletable: true,

  },

  

  'fey-bargain': {

    id: 'fey-bargain',

    type: 'oath',

    name: 'Fey Bargain',

    publicName: 'The Price',

    description: 'A debt owed to a fey creature. The terms may be vague but the consequences are very real.',

    publicDescription: 'You owe a debt to one of the Fair Folk. They will collect.',

    clues: [

      'Sometimes you feel watched by unseen eyes.',

      'Flowers wilt when you pass, or bloom unexpectedly.',

      'You occasionally hear distant, mocking laughter.',

    ],

    effects: [],

    hiddenEffects: ['-2 to saves vs Fey magic'],

    sections: [

      { id: 'terms', label: 'Bargain Terms', kind: 'text', text: 'In exchange for [BENEFIT RECEIVED], you agreed to [PRICE]. The fey may call upon this debt at any time.', dmOnly: false },

      { id: 'restrictions', label: 'Restrictions', kind: 'list', items: [

        'Cannot refuse a direct request from the fey creditor',

        'Cannot speak ill of the Fey Courts',

        'Must offer hospitality to any fey who asks',

        'Cannot break a promise made under moonlight',

      ], dmOnly: false },

      { id: 'consequences', label: 'Breaking Consequences', kind: 'text', text: 'Breaking any term: The fey may immediately invoke the full debt. Possible consequences: transformation, servitude, loss of a cherished memory, firstborn child, or 7 years in the Feywild.', dmOnly: true },

    ],

    cures: [

      { item: 'Fulfill the original bargain', quantity: 1 },

      { item: 'Trick the fey into releasing you (very hard)', quantity: 1 },

      { item: 'Find someone to take your debt', quantity: 1 },

    ],

    locations: [],

    tags: ['oath', 'fey', 'bargain', 'demo-oath'],

    trackerFields: [

      { id: 'debt-level', type: 'counter', label: 'Debt Severity', value: 1, min: 1, max: 5, step: 1, showToPlayer: false, color: '#27ae60' },

    ],

    deletable: true,

  },

  

  'geas-quest': {

    id: 'geas-quest',

    type: 'oath',

    name: 'Geas',

    publicName: 'Compelled Quest',

    description: 'A magical compulsion to complete a specific task. Standard 5e Geas mechanics with enhanced tracking.',

    publicDescription: 'You are magically compelled to complete a task. Resistance brings agony.',

    clues: [],

    effects: [],

    hiddenEffects: [],

    sections: [

      { id: 'terms', label: 'The Compulsion', kind: 'text', text: 'You must [TASK]. You cannot take any action that directly opposes this goal.', dmOnly: false },

      { id: 'restrictions', label: 'Restrictions', kind: 'list', items: [

        'Cannot take actions that oppose the goal',

        'Must make reasonable progress when possible',

        'Cannot allow others to prevent the goal if you can stop them',

      ], dmOnly: false },

      { id: 'consequences', label: 'Defiance Consequences', kind: 'text', text: '5d10 psychic damage each time you act against the geas (once per day max). At 0 HP from this damage, you are incapacitated until you resume the task.', dmOnly: false },

    ],

    cures: [

      { item: 'Complete the task', quantity: 1 },

      { item: 'Greater Restoration', quantity: 1 },

      { item: 'Remove Curse (if cast at 9th level)', quantity: 1 },

      { item: 'Wish', quantity: 1 },

    ],

    locations: [],

    tags: ['oath', 'geas', 'compulsion', 'demo-oath'],

    trackerFields: [

      { id: 'days-remaining', type: 'counter', label: 'Days Remaining', value: 30, min: 0, max: 365, step: 1, showToPlayer: true, color: '#c92727' },

    ],

    deletable: true,

  },

  

  // ========== SPELL RESOURCE DEMO SYSTEMS ==========

  'qi-spellcasting': {

    id: 'qi-spellcasting',

    type: 'spell-resource',

    name: 'Qi Arts',

    publicName: 'Cultivation Arts',

    description: 'A spell system powered by Qi cultivation. Spend Qi to cast techniques. Higher realm = more powerful techniques available.',

    publicDescription: 'You have learned to channel your Qi into mystical techniques.',

    clues: [],

    effects: [],

    hiddenEffects: [],

    sections: [

      { id: 'description', label: 'Description', kind: 'text', text: 'Qi Arts allow cultivators to manifest their inner energy as supernatural techniques. Each technique costs Qi points from your reserve. Qi regenerates through meditation (1 hour = 10 Qi).', dmOnly: false },

      { id: 'casting-rules', label: 'Casting Rules', kind: 'text', text: 'Spend Qi equal to technique cost. If Qi drops to 0, suffer 1 level of exhaustion. Cannot cast techniques above your realm tier.', dmOnly: false },

      { id: 'spell-list', label: 'Qi Techniques', kind: 'list', items: [

        '🌀 Qi Sense (5 Qi) - Detect living beings within 60ft for 10 min [Tier 1: Qi Gathering]',

        '💨 Wind Step (10 Qi) - Dash as bonus action, +10 movement for 1 min [Tier 1: Qi Gathering]',

        '🛡️ Iron Body (15 Qi) - Resistance to non-magical damage for 1 min [Tier 2: Foundation]',

        '⚡ Thunder Palm (20 Qi) - Melee attack, 4d8 lightning damage [Tier 2: Foundation]',

        '🌊 Qi Wave (30 Qi) - 30ft cone, 6d6 force damage, Dex save for half [Tier 3: Core Formation]',

        '✨ Spirit Walk (40 Qi) - Etherealness for 1 min [Tier 3: Core Formation]',

        '🔥 Heaven\'s Wrath (60 Qi) - 60ft radius, 10d6 radiant, Con save for half [Tier 4: Nascent Soul]',

        '🌟 Domain Expansion (100 Qi) - Create 100ft pocket dimension for 1 hour [Tier 5: Deity]',

      ], dmOnly: false },

      { id: 'dm-notes', label: 'DM Notes', kind: 'text', text: 'Tie technique availability to the qi-cultivation system\'s realm stages. Consider allowing players to create custom techniques at higher realms.', dmOnly: true },

    ],

    cures: [],

    locations: [],

    tags: ['spell-resource', 'qi', 'cultivation', 'demo-spellresource'],

    trackerFields: [

      { id: 'qi-current', type: 'progress', label: 'Qi Reserve', value: 50, min: 0, max: 100, step: 5, showToPlayer: true, color: '#00bcd4' },

      { id: 'realm-tier', type: 'counter', label: 'Realm Tier', value: 1, min: 1, max: 5, step: 1, showToPlayer: true, color: '#9b59b6' },

    ],

    resourceProfiles: [

      createResourceProfile('scalar', {

        id: 'profile-qi-reserve',

        name: 'Qi Reserve',

        visibleLabel: 'Qi',

        scalar: {

          trackerId: 'qi-current',

          min: 0,

          maxSource: 'tracker',

        },

        recovery: {

          shortRest: { mode: 'add', amount: 10 },

          longRest: 'full',

        },

      }),

      createResourceProfile('tiered', {

        id: 'profile-tiered-demo',

        name: 'Tiered Demo',

        visibleLabel: 'Tiered Demo',

        tiered: {

          tiers: [{ level: 1, trackerId: 'realm-tier' }],

          spendPolicy: 'exact-level',

        },

      }),

    ],

    directSpellBindings: [

      createDirectSpellBinding({

        id: 'binding-qi-default',

        assignmentMode: 'all-spells',

        resourceProfileId: 'profile-qi-reserve',

        castSurface: 'both',

      }),

      createDirectSpellBinding({

        id: 'binding-qi-firebolt',

        assignmentMode: 'specific-spells',

        spellIds: ['spell-firebolt'],

        resourceProfileId: 'profile-qi-reserve',

        castSurface: 'both',

        costMode: 'override',

        overrideCost: 5,

      }),

    ],

    // Spells that can be cast using this resource

    spells: [

      { id: 'qi-sense', name: 'Qi Sense', cost: 5, tier: 1, description: 'Detect living beings within 60ft for 10 minutes', school: 'divination' },

      { id: 'wind-step', name: 'Wind Step', cost: 10, tier: 1, description: 'Dash as bonus action, +10 movement for 1 minute', school: 'transmutation' },

      { id: 'iron-body', name: 'Iron Body', cost: 15, tier: 2, description: 'Resistance to non-magical damage for 1 minute', school: 'abjuration' },

      { id: 'thunder-palm', name: 'Thunder Palm', cost: 20, tier: 2, description: 'Melee attack dealing 4d8 lightning damage', school: 'evocation' },

      { id: 'qi-wave', name: 'Qi Wave', cost: 30, tier: 3, description: '30ft cone, 6d6 force damage, Dex save half', school: 'evocation' },

      { id: 'spirit-walk', name: 'Spirit Walk', cost: 40, tier: 3, description: 'Etherealness for 1 minute', school: 'transmutation' },

      { id: 'heavens-wrath', name: 'Heaven\'s Wrath', cost: 60, tier: 4, description: '60ft radius, 10d6 radiant, Con save half', school: 'evocation' },

      { id: 'domain-expansion', name: 'Domain Expansion', cost: 100, tier: 5, description: 'Create 100ft pocket dimension for 1 hour', school: 'conjuration' },

    ],

    deletable: true,

  },

  

  // Blood Magic — the reference implementation for the spell-resource architecture (Session 5).

  // Every architecture concept is exercised here: techniques[] with cost model + requirements +

  // cooldown + per-technique on-cast rules, tracker publicLabels (addiction hidden as "?"),

  // system-wide on-threshold save rule, and on-cast exhaustion rule for overcast penalties.

  // See docs/spell-resource-architecture.md §8 for the design rationale.

  'blood-magic': {

    id: 'blood-magic',

    type: 'spell-resource',

    name: 'Blood Magic',

    publicName: 'Crimson Arts',

    description: 'A forbidden magic system that uses the caster\'s life force (HP) as fuel. Powerful but dangerous — prolonged use leads to addiction.',

    publicDescription: CRIMSON_ARTS_PUBLIC_DESCRIPTION,

    clues: [

      'Your veins occasionally glow red when you concentrate.',

      'Healing magic feels slightly painful to you.',

      'You can smell blood from an unusual distance.',

    ],

    effects: ['-1 to saves vs Necrotic'],

    hiddenEffects: ['Blood magic damage cannot be healed by short rest'],

    sections: [

      { id: 'description', label: 'Description', kind: 'text', text: 'Blood magic converts your life force directly into magical power. No spell slots or mana required — only your blood. Each cast draws you deeper into the addiction.', dmOnly: false },

      { id: 'casting-rules', label: 'Casting Rules', kind: 'text', text: 'Pay HP equal to technique cost. This damage cannot be reduced or prevented. If casting would reduce you below 1 HP you gain 1 exhaustion per additional cast (auto-applied).', dmOnly: false },

      { id: 'dm-notes', label: 'DM Notes', kind: 'text', text: 'Addiction escalates automatically via the gain-stacks action on each technique. Thresholds at 5/10/15/20 engage progressively harder effects. The system-wide addiction-save rule fires on every threshold crossing once addiction ≥ 10, prompting a Wis save to resist. The player sees addiction as "?" and does not see the save rule.', dmOnly: true },

    ],

    cures: [

      { item: 'Greater Restoration (removes addiction)', quantity: 1 },

    ],

    locations: [],

    tags: ['spell-resource', 'blood', 'forbidden', 'demo-spellresource'],

    trackerFields: [

      {

        id: 'addiction-level', type: 'counter',

        label: 'Addiction Level', publicLabel: HIDDEN_LABEL_PLACEHOLDER, // player sees the counter's progress but not its name

        value: 0, min: 0, max: 20, step: 1,

        showToPlayer: true, color: '#c92727',

        thresholds: [

          { id: 'addiction-5',  value: 5,  label: 'Craving',    effect: 'Unlocks Exsanguinate; +1 damage on Blood Techniques (DM applies narratively)' },

          { id: 'addiction-10', value: 10, label: 'Dependency', effect: 'DC 12 Wis save required on each threshold crossing' },

          { id: 'addiction-15', value: 15, label: 'Bound',      effect: 'Cannot use non-blood magic' },

          { id: 'addiction-20', value: 20, label: 'Consumed',   effect: 'Permanent -2 Wis; no abstention possible' },

        ],

      },

      {

        id: 'total-casts', type: 'counter',

        label: 'Total Blood Casts', publicLabel: 'Blood Casts',

        value: 0, min: 0, max: 999, step: 1,

        showToPlayer: true, color: '#8b0000',

      },

    ],

    techniques: [

      {

        id: 'blood-bolt', name: 'Blood Bolt', description: 'Ranged attack. 2d10 necrotic damage.', icon: '', order: 0,

        spellId: null,

        inlineSpell: { castingTime: '1A', range: '60 ft', attackType: 'ranged', dice: '2d10', damageType: 'necrotic', targetType: 'single' },

        costs: [{ resource: 'hp', amount: 5 }],

        forceCast: true, requirements: [],

        availability: { combatOnly: false, restOnly: false, timeOfDay: null, environments: [], cooldown: null },

        rules: [

          {

            id: 'bb-on-cast', name: 'Blood Bolt consequences', enabled: true,

            trigger: 'on-cast', triggerParams: {},

            conditions: [{ type: 'always', params: {} }], conditionLogic: 'all',

            actions: [

              { type: 'gain-stacks', params: { trackerId: 'addiction-level', amount: 1 } },

              { type: 'change-tracker', params: { trackerId: 'total-casts', operation: 'add', value: 1 } },

            ],

            order: 0,

          },

        ],

        hidden: { cost: false, requirements: false, rules: true, description: false, existence: false },

      },

      {

        // SYSTEMS.md §7.1 test case: H11 (on-cast rules can't see roll outcome).
        // DM-intent: heal half the rolled damage. Engine-today: heal a fixed
        // 5 HP (3d6 averages 10.5; half ≈ 5). Swap to roll-aware amount when
        // H11 closes. See SYSTEMS.md §7.1 for the full table.

        id: 'life-leech', name: 'Life Leech', description: 'Touch attack. 3d6 necrotic damage. Heal 5 HP on cast (DM applies target damage). *Fixed heal until roll-aware actions ship — SYSTEMS.md H11.*', icon: '', order: 1,

        spellId: null,

        inlineSpell: { castingTime: '1A', range: 'Touch', attackType: 'melee', dice: '3d6', damageType: 'necrotic', targetType: 'touch' },

        costs: [{ resource: 'hp', amount: 10 }],

        forceCast: true, requirements: [],

        availability: { combatOnly: false, restOnly: false, timeOfDay: null, environments: [], cooldown: null },

        rules: [

          {

            id: 'll-on-cast', name: 'Life Leech consequences', enabled: true,

            trigger: 'on-cast', triggerParams: {},

            conditions: [{ type: 'always', params: {} }], conditionLogic: 'all',

            actions: [

              { type: 'gain-stacks', params: { trackerId: 'addiction-level', amount: 1 } },

              { type: 'change-tracker', params: { trackerId: 'total-casts', operation: 'add', value: 1 } },

              // Fixed-average rebate. When H11 closes, replace with:
              //   { type: 'heal', params: { amount: { source: 'diceResult.total', divide: 2, floor: true } } }
              { type: 'heal', params: { amount: 5 } },

            ],

            order: 0,

          },

        ],

        hidden: { cost: false, requirements: false, rules: true, description: false, existence: false },

      },

      {

        // SYSTEMS.md §7.1 test case: H12 (`add-effect` has no scoped duration).
        // DM-intent: +2 AC until the start of caster's next turn. Engine-today:
        // permanent +2 AC buff that the DM clears manually. Swap to scoped buff
        // when H12 closes (see SYSTEMS.md §7.1 + companion CLAUDE-NOTES-GENERAL
        // entry "[2026-04-27] No engine support for attack/turn/round-scoped buffs").

        id: 'crimson-shield', name: 'Crimson Shield', description: '+2 AC until the start of your next turn. *DM removes manually until scoped buffs ship — SYSTEMS.md H12.*', icon: '', order: 2,

        spellId: null,

        inlineSpell: { castingTime: '1BA', range: 'Self', attackType: 'none', dice: '', damageType: '', targetType: 'self', effectType: 'buff' },

        costs: [{ resource: 'hp', amount: 8 }],

        forceCast: true, requirements: [],

        availability: { combatOnly: false, restOnly: false, timeOfDay: null, environments: [], cooldown: { scope: 'turn', count: 1 } },

        rules: [

          {

            id: 'cs-on-cast', name: 'Crimson Shield consequences', enabled: true,

            trigger: 'on-cast', triggerParams: {},

            conditions: [{ type: 'always', params: {} }], conditionLogic: 'all',

            actions: [

              { type: 'gain-stacks', params: { trackerId: 'addiction-level', amount: 1 } },

              { type: 'change-tracker', params: { trackerId: 'total-casts', operation: 'add', value: 1 } },

              // Apply the shield. Persists until manually removed (DM action). When
              // H12 ships, add a scope param: { effect: '+2 AC', kind: 'buff', scope: 'until-next-turn' }
              { type: 'add-effect', params: { effect: '+2 AC', kind: 'buff' } },

            ],

            order: 0,

          },

        ],

        hidden: { cost: false, requirements: false, rules: true, description: false, existence: false },

      },

      {

        id: 'blood-clone', name: 'Blood Clone', description: 'Create an illusion of yourself for 1 minute.', icon: '', order: 3,

        spellId: null,

        inlineSpell: { castingTime: '1A', range: 'Self', attackType: 'none', dice: '', damageType: '', targetType: 'self', effectType: 'summon' },

        costs: [{ resource: 'hp', amount: 15 }],

        forceCast: true, requirements: [],

        availability: { combatOnly: false, restOnly: false, timeOfDay: null, environments: [], cooldown: null },

        rules: [

          {

            id: 'bc-on-cast', name: 'Blood Clone consequences', enabled: true,

            trigger: 'on-cast', triggerParams: {},

            conditions: [{ type: 'always', params: {} }], conditionLogic: 'all',

            actions: [

              { type: 'gain-stacks', params: { trackerId: 'addiction-level', amount: 1 } },

              { type: 'change-tracker', params: { trackerId: 'total-casts', operation: 'add', value: 1 } },

            ],

            order: 0,

          },

        ],

        hidden: { cost: false, requirements: false, rules: true, description: false, existence: false },

      },

      {

        id: 'hemorrhage', name: 'Hemorrhage', description: '4d8 necrotic damage, ongoing 1d8/round (DM tracks).', icon: '', order: 4,

        spellId: null,

        inlineSpell: { castingTime: '1A', range: '30 ft', attackType: 'save', saveAbility: 'con', saveEffect: 'half', dice: '4d8', damageType: 'necrotic', targetType: 'single', dotDice: '1d8', dotDamageType: 'necrotic', effectRounds: 3 },

        costs: [{ resource: 'hp', amount: 20 }],

        forceCast: true, requirements: [],

        availability: { combatOnly: false, restOnly: false, timeOfDay: null, environments: [], cooldown: null },

        rules: [

          {

            id: 'hem-on-cast', name: 'Hemorrhage consequences', enabled: true,

            trigger: 'on-cast', triggerParams: {},

            conditions: [{ type: 'always', params: {} }], conditionLogic: 'all',

            actions: [

              { type: 'gain-stacks', params: { trackerId: 'addiction-level', amount: 2 } },

              { type: 'change-tracker', params: { trackerId: 'total-casts', operation: 'add', value: 1 } },

            ],

            order: 0,

          },

        ],

        hidden: { cost: false, requirements: false, rules: true, description: false, existence: false },

      },

      {

        id: 'exsanguinate', name: 'Exsanguinate', description: 'Sacrifice all but 1 HP. Deal the sacrificed amount as necrotic damage to one target (Con save for half).', icon: '', order: 5,

        spellId: null,

        inlineSpell: { castingTime: '1A', range: '30 ft', attackType: 'save', saveAbility: 'con', saveEffect: 'half', dice: 'variable', damageType: 'necrotic', targetType: 'single' },

        costs: [{ resource: 'hp-all-but', remaining: 1 }],

        forceCast: false,

        requirements: [

          { type: 'tracker-gte', trackerId: 'addiction-level', value: 5 },

        ],

        availability: { combatOnly: false, restOnly: false, timeOfDay: null, environments: [], cooldown: { scope: 'long-rest', count: 1 } },

        rules: [

          {

            id: 'exsang-on-cast', name: 'Exsanguinate consequences', enabled: true,

            trigger: 'on-cast', triggerParams: {},

            conditions: [{ type: 'always', params: {} }], conditionLogic: 'all',

            actions: [

              { type: 'gain-stacks', params: { trackerId: 'addiction-level', amount: 5 } },

              { type: 'change-tracker', params: { trackerId: 'total-casts', operation: 'add', value: 1 } },

              { type: 'set-cooldown', params: { techniqueId: 'exsanguinate', scope: 'long-rest', count: 1 } },

            ],

            order: 0,

          },

        ],

        // 2026-04-28: requirements hidden so the addiction-level gate doesn't leak

        // the tracker name to players ("Requires Addiction Level ≥ 5"). The

        // addiction-level tracker has publicLabel: '???', so revealing the

        // requirement reason text would defeat that. P3 — three-axis privacy.

        hidden: { cost: false, requirements: true, rules: true, description: false, existence: false },

      },

    ],

    automationRules: [

      // Addiction save — fires on every threshold crossing of addiction-level. The condition

      // guards so the save only prompts when current addiction is ≥ 10 (i.e., crossings at 5

      // are ignored; crossings at 10/15/20 each prompt their own save). Hidden from player.

      {

        id: 'addiction-threshold-save', name: 'Addiction Save Required', enabled: true,

        trigger: 'on-threshold', triggerParams: {},

        conditions: [

          { type: 'tracker-value', params: { trackerId: 'addiction-level', operator: 'gte', value: 10 } },

        ],

        conditionLogic: 'all',

        actions: [

          {

            type: 'require-save',

            params: {

              ability: 'wis', dc: 12,

              reason: 'The blood calls to you. Resist the urge to cast again?',

              onFail: [

                { type: 'notify', params: { message: '💀 You cannot resist the Crimson Arts. The addiction tightens its grip.', toPlayer: true } },

              ],

              onPass: [

                { type: 'notify', params: { message: '✊ You resist the pull. For now.', toPlayer: true } },

              ],

            },

          },

        ],

        order: 0,

      },

      // Overcast penalty — if a cast dropped the caster below 1 HP, they gain exhaustion

      // AND heal back to 1 HP (per Blood Magic rules: "continue at the cost of exhaustion

      // per additional cast"). Fires on-cast AFTER the cost has applied, so has-resource

      // sees the post-cost HP.

      {

        id: 'overcast-exhaustion', name: 'Bleeding Out (overcast)', enabled: true,

        trigger: 'on-cast', triggerParams: {},

        conditions: [

          { type: 'has-resource', params: { resource: 'hp', operator: 'lt', amount: 1 } },

        ],

        conditionLogic: 'all',

        actions: [

          { type: 'add-exhaustion', params: { levels: 1 } },

          { type: 'heal', params: { amount: 1 } }, // keep caster standing at 1 HP

          { type: 'notify', params: { message: '💀 Overcast! Gained 1 exhaustion as blood magic drained beyond your limit. You stand on willpower alone.', toPlayer: true } },

        ],

        order: 1,

      },

    ],

    deletable: true,

  },

};



const DEFAULT_SETTINGS = {

  starvationThreshold: 7, hungerThreshold: 3, showDaysToStarvation: true, showDaysWithoutFood: true, hideWhenWellFed: false,

  wellFedBuffs: ['+3 Max HP', '+2 Con'], hungryDebuffs: ['-1 Con', '-1 Str'], starvationDebuffs: ['-2 Str', '-1 Con'],

  manaEnabled: true, maxMana: 10,

  // Spell/Mana System Settings

  spellSystem: 'mana', // 'mana' or 'slots' (traditional 5e spell slots)

  spellcastingAbility: 'wis', // The ability used for spellcasting modifier

  freeCastsPerDay: 4, // Max free casts for spells 3+ levels below max

  overcastEnabled: true, // Allow casting up to 2 levels above max

  manaSicknessEnabled: true, // Apply mana sickness on overcast

  manaDroughtEnabled: true, // Apply exhaustion for negative mana

  showSpellPanel: true, // Show spell panel on character sheet

  // Armor settings

  armorTypes: { ...DEFAULT_ARMOR_TYPES },

  armorDurability: { ...DEFAULT_ARMOR_DURABILITY },

  durabilityEnabled: true, // Can disable entire durability system

  // Buff/Debuff settings

  allowBuffClickOff: true, // Allow players to click off buffs/debuffs

  // Roll settings

  instantAdvantageRoll: false, // true = roll both dice at once, false = two-click (roll again) mode

  // Currency settings

  currencyEnabled: true,

  currencyTiers: [

    { id: 'copper', name: 'Bronze', symbol: 'b', multiplier: 1, baseId: null, icon: 'coinBronze' },

    { id: 'silver', name: 'Silver', symbol: 's', multiplier: 1000, baseId: 'copper', icon: 'coinSilver' },

    { id: 'gold', name: 'Gold', symbol: 'g', multiplier: 1000, baseId: 'silver', icon: 'coinGold' },

    { id: 'platinum', name: 'Platinum', symbol: 'p', multiplier: 1000, baseId: 'gold', icon: 'coinLightBlue' },

  ],

  currencyUsdConversion: { enabled: false, amount: 1, currencyId: 'gold', usdCents: 100 }, // 1 gold = $1.00



  // Audit / Session Mode logging (DM timeline)

  auditLog: {

    enabled: true,

    verbosity: 'standard', // 'minimal' | 'standard' | 'detailed'

    includePlayerEdits: true, // if false, only DM actions are logged

    categories: {

      combat: true,

      inventory: true,

      buffs: true,

      diseases: true,

      systems: true,

      messages: true,

      session: true,

      other: true,

    },

  },

  // 2026-04-27 (later): Profession System Phase 1 settings.

  // enabled: master toggle. When false, the Crafting page hides the

  //   Professions section and recipes ignore profReq gating.

  // maxMajor: how many "major" professions a character may have at once.

  // discoveryXpMult / firstCraftBonusMult: XP multipliers used by

  //   computeCraftXp. Players see them itemized in the level-up toast.

  profSettings: { enabled: true, maxMajor: 1, discoveryXpMult: 5, firstCraftBonusMult: 2 },

};



// Currency icon options - SVG-based coins and gems in consistent styles

// Colors: gold (#ffd700), silver (#c0c0c0), bronze (#cd7f32), dark blue (#1e3a5f), light blue (#5ba3d9)

const CURRENCY_ICONS = {

  // Single Coins (flat style with $ symbol)

  coinGold: { emoji: '🪙', color: '#ffd700', name: 'Gold Coin', svg: 'coin' },

  coinSilver: { emoji: '🥈', color: '#c0c0c0', name: 'Silver Coin', svg: 'coin' },

  coinBronze: { emoji: '🥉', color: '#cd7f32', name: 'Bronze Coin', svg: 'coin' },

  coinDarkBlue: { emoji: '🔵', color: '#1e3a5f', name: 'Dark Blue Coin', svg: 'coin' },

  coinLightBlue: { emoji: '💠', color: '#5ba3d9', name: 'Light Blue Coin', svg: 'coin' },

  // Coin Stacks (pile of coins)

  stackGold: { emoji: '💰', color: '#ffd700', name: 'Gold Stack', svg: 'stack' },

  stackSilver: { emoji: '🥈', color: '#c0c0c0', name: 'Silver Stack', svg: 'stack' },

  stackBronze: { emoji: '🥉', color: '#cd7f32', name: 'Bronze Stack', svg: 'stack' },

  stackDarkBlue: { emoji: '🔵', color: '#1e3a5f', name: 'Dark Blue Stack', svg: 'stack' },

  stackLightBlue: { emoji: '💠', color: '#5ba3d9', name: 'Light Blue Stack', svg: 'stack' },

  // Gems (cut diamond shape)

  gemGold: { emoji: '💎', color: '#ffd700', name: 'Gold Gem', svg: 'gem' },

  gemSilver: { emoji: '💎', color: '#c0c0c0', name: 'Silver Gem', svg: 'gem' },

  gemBronze: { emoji: '💎', color: '#cd7f32', name: 'Bronze Gem', svg: 'gem' },

  gemDarkBlue: { emoji: '💎', color: '#1e3a5f', name: 'Dark Blue Gem', svg: 'gem' },

  gemLightBlue: { emoji: '💎', color: '#5ba3d9', name: 'Light Blue Gem', svg: 'gem' },

  // Circles (simple)

  circleGold: { emoji: '🟡', color: '#ffd700', name: 'Gold Circle', svg: 'circle' },

  circleSilver: { emoji: '⚪', color: '#c0c0c0', name: 'Silver Circle', svg: 'circle' },

  circleBronze: { emoji: '🟠', color: '#cd7f32', name: 'Bronze Circle', svg: 'circle' },

  circleDarkBlue: { emoji: '🔵', color: '#1e3a5f', name: 'Dark Blue Circle', svg: 'circle' },

  circleLightBlue: { emoji: '🔵', color: '#5ba3d9', name: 'Light Blue Circle', svg: 'circle' },

  // Squares (simple)

  squareGold: { emoji: '🟨', color: '#ffd700', name: 'Gold Square', svg: 'square' },

  squareSilver: { emoji: '⬜', color: '#c0c0c0', name: 'Silver Square', svg: 'square' },

  squareBronze: { emoji: '🟧', color: '#cd7f32', name: 'Bronze Square', svg: 'square' },

  squareDarkBlue: { emoji: '🟦', color: '#1e3a5f', name: 'Dark Blue Square', svg: 'square' },

  squareLightBlue: { emoji: '🟦', color: '#5ba3d9', name: 'Light Blue Square', svg: 'square' },

  // Medals

  medalGold: { emoji: '🥇', color: '#ffd700', name: 'Gold Medal', svg: 'medal' },

  medalSilver: { emoji: '🥈', color: '#c0c0c0', name: 'Silver Medal', svg: 'medal' },

  medalBronze: { emoji: '🥉', color: '#cd7f32', name: 'Bronze Medal', svg: 'medal' },

};



// SVG Currency Icon Component

const CurrencyIcon = ({ type, size = 18 }) => {

  const iconDef = CURRENCY_ICONS[type] || CURRENCY_ICONS.coinGold;

  const color = iconDef.color;

  const svgType = iconDef.svg || 'coin';

  

  // Lighten color for highlights

  const lighten = (hex, percent) => {

    const num = parseInt(hex.replace('#', ''), 16);

    const r = Math.min(255, (num >> 16) + Math.round(255 * percent));

    const g = Math.min(255, ((num >> 8) & 0x00FF) + Math.round(255 * percent));

    const b = Math.min(255, (num & 0x0000FF) + Math.round(255 * percent));

    return `rgb(${r},${g},${b})`;

  };

  

  // Darken color for shadows

  const darken = (hex, percent) => {

    const num = parseInt(hex.replace('#', ''), 16);

    const r = Math.max(0, (num >> 16) - Math.round(255 * percent));

    const g = Math.max(0, ((num >> 8) & 0x00FF) - Math.round(255 * percent));

    const b = Math.max(0, (num & 0x0000FF) - Math.round(255 * percent));

    return `rgb(${r},${g},${b})`;

  };

  

  const highlight = lighten(color, 0.3);

  const shadow = darken(color, 0.25);

  const darkShadow = darken(color, 0.4);

  

  if (svgType === 'coin') {

    return (

      <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: 'inline-block', verticalAlign: 'middle' }}>

        <circle cx="12" cy="12" r="10" fill={color} stroke={shadow} strokeWidth="1"/>

        <circle cx="12" cy="12" r="7" fill="none" stroke={highlight} strokeWidth="1" opacity="0.5"/>

        <text x="12" y="16" textAnchor="middle" fontSize="10" fontWeight="bold" fill={darkShadow}>$</text>

      </svg>

    );

  }

  

  if (svgType === 'stack') {

    return (

      <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: 'inline-block', verticalAlign: 'middle' }}>

        {/* Bottom coins */}

        <ellipse cx="8" cy="18" rx="6" ry="2.5" fill={shadow}/>

        <ellipse cx="8" cy="17" rx="6" ry="2.5" fill={color}/>

        <ellipse cx="16" cy="18" rx="6" ry="2.5" fill={shadow}/>

        <ellipse cx="16" cy="17" rx="6" ry="2.5" fill={color}/>

        {/* Middle coins */}

        <ellipse cx="12" cy="14" rx="6" ry="2.5" fill={shadow}/>

        <ellipse cx="12" cy="13" rx="6" ry="2.5" fill={color}/>

        {/* Top coins */}

        <ellipse cx="12" cy="10" rx="5" ry="2" fill={shadow}/>

        <ellipse cx="12" cy="9" rx="5" ry="2" fill={color}/>

        <ellipse cx="12" cy="9" rx="3" ry="1" fill={highlight} opacity="0.4"/>

        {/* Sparkles */}

        <path d="M4 6 L5 4 L6 6 L5 8 Z" fill={highlight} opacity="0.7"/>

        <path d="M18 4 L19 2 L20 4 L19 6 Z" fill={highlight} opacity="0.7"/>

      </svg>

    );

  }

  

  if (svgType === 'gem') {

    return (

      <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: 'inline-block', verticalAlign: 'middle' }}>

        {/* Diamond shape */}

        <polygon points="12,2 22,9 12,22 2,9" fill={color} stroke={shadow} strokeWidth="0.5"/>

        {/* Facets */}

        <polygon points="12,2 17,9 12,14 7,9" fill={highlight} opacity="0.4"/>

        <polygon points="7,9 12,14 2,9" fill={shadow} opacity="0.3"/>

        <polygon points="17,9 22,9 12,14" fill={shadow} opacity="0.5"/>

        <polygon points="12,14 17,9 12,22" fill={darkShadow} opacity="0.3"/>

        {/* Top highlight */}

        <polygon points="12,2 14,6 12,8 10,6" fill={highlight} opacity="0.6"/>

      </svg>

    );

  }

  

  if (svgType === 'circle') {

    return (

      <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: 'inline-block', verticalAlign: 'middle' }}>

        <circle cx="12" cy="12" r="10" fill={color} stroke={shadow} strokeWidth="1"/>

        <circle cx="9" cy="9" r="3" fill={highlight} opacity="0.4"/>

      </svg>

    );

  }

  

  if (svgType === 'square') {

    return (

      <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: 'inline-block', verticalAlign: 'middle' }}>

        <rect x="3" y="3" width="18" height="18" rx="2" fill={color} stroke={shadow} strokeWidth="1"/>

        <rect x="5" y="5" width="6" height="6" rx="1" fill={highlight} opacity="0.3"/>

      </svg>

    );

  }

  

  if (svgType === 'medal') {

    return (

      <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: 'inline-block', verticalAlign: 'middle' }}>

        {/* Ribbon */}

        <polygon points="8,2 10,10 12,2 14,10 16,2 14,12 10,12" fill="#c92727"/>

        {/* Medal circle */}

        <circle cx="12" cy="16" r="6" fill={color} stroke={shadow} strokeWidth="1"/>

        <circle cx="12" cy="16" r="4" fill="none" stroke={highlight} strokeWidth="0.5" opacity="0.6"/>

        <text x="12" y="18" textAnchor="middle" fontSize="6" fontWeight="bold" fill={darkShadow}>★</text>

      </svg>

    );

  }

  

  // Fallback to emoji

  return <span style={{ fontSize: size * 0.8 }}>{iconDef.emoji}</span>;

};



// Helper: Convert any currency amount to base units

const currencyToBase = (amount, currencyId, tiers) => {

  const tier = tiers.find(t => t.id === currencyId);

  if (!tier) return amount;

  

  let multiplier = tier.multiplier;

  let currentTier = tier;

  

  // Walk up the chain to get total multiplier to base

  while (currentTier.baseId) {

    const parentTier = tiers.find(t => t.id === currentTier.baseId);

    if (!parentTier) break;

    multiplier *= parentTier.multiplier;

    currentTier = parentTier;

  }

  

  // The base tier has multiplier 1, so we need to adjust

  const baseTier = tiers.find(t => !t.baseId);

  if (baseTier && tier.id !== baseTier.id) {

    return amount * (multiplier / baseTier.multiplier);

  }

  return amount;

};



// Helper: Convert base units to a wallet object with optimal denominations

const baseToWallet = (baseAmount, tiers) => {

  const wallet = {};

  // Sort tiers by value (highest first) - need to calculate actual value

  const tiersWithValue = tiers.map(tier => ({

    ...tier,

    totalValue: currencyToBase(1, tier.id, tiers)

  })).sort((a, b) => b.totalValue - a.totalValue);

  

  let remaining = Math.floor(baseAmount);

  

  for (const tier of tiersWithValue) {

    if (tier.totalValue <= 0) continue;

    const count = Math.floor(remaining / tier.totalValue);

    wallet[tier.id] = count;

    remaining -= count * tier.totalValue;

  }

  

  return wallet;

};



// Helper: Calculate total wallet value in base units

const walletToBase = (wallet, tiers) => {

  let total = 0;

  for (const [currencyId, amount] of Object.entries(wallet)) {

    total += currencyToBase(amount || 0, currencyId, tiers);

  }

  return total;

};

// 2026-05-11 F1 fix: Multi-tier price formatting hoisted to module scope.
// Was previously a closure inside ShopPage at ~line 97992; the Session 11
// shop-header TweenedCurrency wiring referenced it from App scope and
// crashed with ReferenceError → blank screen on every player session.
// Now accessible globally; pass the active tiers array in.
const formatPriceFromBaseUnits = (baseAmount, tiers) => {
  if (!Array.isArray(tiers) || tiers.length === 0) return String(baseAmount ?? '');
  if (baseAmount <= 0) return `0${tiers[0]?.symbol || 'cp'}`;
  const w = baseToWallet(Math.abs(baseAmount), tiers);
  const tiersHighFirst = [...tiers].reverse();
  const parts = tiersHighFirst
    .filter((t) => (w[t.id] || 0) > 0)
    .map((t) => `${w[t.id]}${t.symbol}`);
  return parts.length > 0 ? parts.join(' ') : `0${tiers[0]?.symbol || 'cp'}`;
};



// Helper: Convert wallet to USD cents

const walletToUsd = (wallet, tiers, conversion) => {

  if (!conversion?.enabled) return null;

  const totalBase = walletToBase(wallet, tiers);

  const conversionBase = currencyToBase(conversion.amount, conversion.currencyId, tiers);

  if (conversionBase <= 0) return 0;

  return (totalBase / conversionBase) * conversion.usdCents;

};



// Helper: Format USD cents to string

const formatUsd = (cents) => {

  if (cents === null || cents === undefined) return '';

  const dollars = cents / 100;

  return dollars.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

};



const DEFAULT_STATS = { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 };



// XP thresholds (D&D 5e defaults) and helper

const XP_DEFAULTS = { 2: 300, 3: 900, 4: 2700, 5: 6500, 6: 14000, 7: 23000, 8: 34000, 9: 48000, 10: 64000, 11: 85000, 12: 100000, 13: 120000, 14: 140000, 15: 165000, 16: 195000, 17: 225000, 18: 265000, 19: 305000, 20: 355000 };

const getXpProgress = (char, globalSettings) => {

  const thresholds = globalSettings?.xpThresholds || XP_DEFAULTS;

  const currentXp = char.xp || 0;

  const currentLevel = char.level || 1;

  const xpForNext = thresholds[currentLevel + 1] || 0;

  const xpForCurrent = thresholds[currentLevel] || 0;

  const isMax = currentLevel >= 20;

  // Use absolute progress: currentXp / xpForNext when XP is below current level threshold

  // (happens when characters were leveled manually before XP tracking)

  let pct;

  if (isMax) { pct = 100; }

  else if (xpForNext <= 0) { pct = 0; }

  else { pct = Math.min(100, (currentXp / xpForNext) * 100); }

  return { currentXp, currentLevel, xpForNext, xpForCurrent, pct, isMax, thresholds };

};



// Granular disease visibility - each aspect can be toggled independently

// New structure: { diseaseId, showName: false, visibleSymptoms: [], showEffects: false, showCure: false, contractedDate }

// By default all hidden - player sees "? Unknown Affliction"



const SKILL_MAP = {

  'Athletics': 'str',

  'Acrobatics': 'dex', 'Sleight of Hand': 'dex', 'Stealth': 'dex',

  'Arcana': 'int', 'History': 'int', 'Investigation': 'int', 'Nature': 'int', 'Religion': 'int',

  'Animal Handling': 'wis', 'Insight': 'wis', 'Medicine': 'wis', 'Perception': 'wis', 'Survival': 'wis',

  'Deception': 'cha', 'Intimidation': 'cha', 'Performance': 'cha', 'Persuasion': 'cha'

};



const SKILLS_BY_STAT = {

  str: ['Athletics'],

  dex: ['Acrobatics', 'Sleight of Hand', 'Stealth'],

  con: [],

  int: ['Arcana', 'History', 'Investigation', 'Nature', 'Religion'],

  wis: ['Animal Handling', 'Insight', 'Medicine', 'Perception', 'Survival'],

  cha: ['Deception', 'Intimidation', 'Performance', 'Persuasion']

};



const ALL_SKILLS = Object.keys(SKILL_MAP);



const getProficiencyBonus = (level) => {

  if (level <= 4) return 2;

  if (level <= 8) return 3;

  if (level <= 12) return 4;

  if (level <= 16) return 5;

  return 6;

};



const getAbilityMod = (score) => Math.floor((score - 10) / 2);



// Calculate effective max HP including buffs

const getEffectiveMaxHp = (char) => {

  const activeLegacyEffects = getActiveLegacyEffectEntries(char);

  return getResolvedMaxHpContext({ char, activeLegacyEffects }).effectiveMaxHp;

};



const clampCurrentHpToEffectiveMax = (char) => {

  if (!char) return char;

  const currentHp = Number.isFinite(char.currentHp) ? char.currentHp : 0;

  const effectiveMaxHp = getEffectiveMaxHp(char);

  const clampedCurrentHp = Math.max(0, Math.min(effectiveMaxHp, currentHp));

  return clampedCurrentHp === currentHp ? char : { ...char, currentHp: clampedCurrentHp };

};



// Get armor status based on durability

const getArmorStatus = (durability, settings) => {

  const durSettings = settings?.armorDurability || DEFAULT_ARMOR_DURABILITY;

  if (durability <= durSettings.brokenThreshold) return 'broken';

  if (durability <= durSettings.damagedThreshold) return 'damaged';

  return 'good';

};



// Get armor-related buffs and debuffs for a character

const getArmorEffects = (char, settings) => {

  const armorTypes = settings?.armorTypes || DEFAULT_ARMOR_TYPES;

  const durSettings = settings?.armorDurability || DEFAULT_ARMOR_DURABILITY;

  const armorType = armorTypes[char.armorType] || armorTypes.none;

  const status = getArmorStatus(char.armorDurability ?? durSettings.maxDurability, settings);

  

  const buffs = [...(armorType.buffs || [])];

  const debuffs = [...(armorType.debuffs || [])];

  

  // Add durability-based debuffs only if durability is enabled

  if (settings?.durabilityEnabled !== false) {

    if (status === 'damaged') {

      debuffs.push(...(durSettings.damagedDebuffs || []));

    } else if (status === 'broken') {

      debuffs.push(...(durSettings.brokenDebuffs || []));

    }

  }

  

  return { buffs, debuffs, status, armorType };

};



// Exported (2026-07-14): the dashboard's create/import modal builds the same
// canonical character shell (via index.js, which already imports App).
export const createDefaultCharacter = (name, race, maxHp, password) => ({

  id: generateId(), name, race, charClass: 'Adventurer', level: 1, password, maxHp: parseInt(maxHp), currentHp: parseInt(maxHp),

  currentMana: 10, maxMana: 10,

  buffList: [], debuffList: [], permanentBuffs: [], permanentDebuffs: [], buffSources: {}, buffNotes: {}, image: null, starving: false, 

  diseaseList: [],

  diseaseImmunityList: [],

  // Legacy fields (kept for compatibility)

  daysWithoutFood: 0, daysToStarvation: 7,

  // New weekly hunger system

  weeklyHungerScore: 0, // Accumulates daily: Full +0.5, None 0, Light -1, Medium -2

  currentWeekNumber: 1, // Tracks which week we're in

  consecutiveNegativeWeeks: 0, // For 3-4 week death spiral

  negativeWeekStreak: 0, // How many times the 3-week penalty has triggered (increases penalty)

  weeklyStarvationBonus: 0, // From Mixed diet completion

  skillCheckBonus: 0, // From Blended diet completion

  diseaseCheckConPenalty: 0, // Monthly penalty from mild hunger (-2 per occurrence)

  // Diet tracking

  mealLog: [], currentDay: 1, mealsToday: 0,

  activeDiet: null, 

  dietWeekProgress: 0, // Weeks completed toward current diet

  dietDayStreak: 0, // Days in current week meeting diet requirements

  dietCompletionCounts: {}, // { 'Carnivore': 2, 'Vegan': 1, ... } tracks completions per diet

  completedDiets: [], actionHistory: [],

  abilities: [], feats: [],

  skillProficiencies: [], 

  skillExpertise: [],

  skillMiscMods: {},

  savingThrowProficiencies: [],

  armorClass: 10, initiative: 0, speed: 30,

  // Legacy Armor system (kept for compatibility)

  armorType: 'none',

  armorDurability: 25,

  armorName: '', // Custom name for the armor piece

  // ===== NEW EQUIPMENT DURABILITY SYSTEM =====

  equipment: [], // Array of equipment objects (weapons, armor, shields with durability)

  equippedWeapon: null, // ID of equipped weapon

  equippedArmor: null, // ID of equipped armor  

  equippedShield: null, // ID of equipped shield

  isMonkOrBarbarian: false, // Exempt from no-armor penalty

  currentMonth: 1, // For maintenance tracking

  // ===== END EQUIPMENT SYSTEM =====

  // Inventory system

  inventory: [],

  // Currency/Wallet

  wallet: {},

  walletVisible: true,

  currentLocation: 'Forest',

  // ===== SPELL/MANA SYSTEM =====

  spells: [], // Array of spell objects

  spellSlots: { 1: 2 }, // { level: count } - e.g., { 1: 4, 2: 3, 3: 2 }

  usedSpellSlots: {}, // { level: count } - tracks used slots for traditional system

  freeCastsUsedToday: 0, // Tracks free casts (spells 3+ below max)

  manaSickness: false, // Currently suffering mana sickness

  manaSicknessRounds: 0, // Rounds remaining of mana sickness

  manaDrought: false, // Currently in mana drought (negative mana)

  exhaustionLevel: 0, // OneDnD exhaustion from mana drought

  spellcastingModifier: 0, // Calculated from ability score

  concentratingOn: null, // Currently concentrating on spell (spell id or null)

  concentrationEffectGroupId: null, // Runtime effect group for active concentration, when linked

  spellHistory: [], // Log of spells cast

  // ===== END SPELL SYSTEM =====

  statusHistory: [{ timestamp: new Date().toISOString(), day: 1, event: 'created', description: 'Day 1: Character created' }],

  settings: { ...DEFAULT_SETTINGS, scalarSpellProgression: { maxCastLevel: 1 } }, immuneToday: false, baseStats: { ...DEFAULT_STATS },

  // 2026-04-27 (later): Profession System Phase 1 — per-character XP/level

  // map keyed by profession id. Empty by default; the player picks their

  // first profession on the Crafting page. Shape per entry:

  //   { level: number (1-based), xp: number (current level XP), totalXp: number (lifetime) }

  // Crafting grants XP via applyProfXp; recipes gate via checkProfReq.

  // See DEFAULT_PROFESSIONS for the catalog.

  playerProfs: {},

  // 2026-04-28 (Phase 4A): per-character recipe-shop discoveries. The

  // global `recipe.disc` flag stays the DM-controlled "this recipe is

  // canonically known to the party" toggle; learnedRecipes layers on top

  // so a player who buys a scroll from the shop unlocks the recipe just

  // for themselves. Resolution: isRecipeKnown = recipe.disc OR the id

  // appears here.

  learnedRecipes: [],

});



// ============================================================================

// DEMO DATA - Shows off all features for new users

// ============================================================================

const createDemoData = () => {

  const demoIdLika = 'demo-lika';

  const demoIdEve = 'demo-eve';

  const demoIdVentyl = 'demo-ventyl';

  const demoIdKhan = 'demo-khan';

  

  // Demo character 1: Li'ka - The main demo with ALL features

  const lika = {

    ...createDefaultCharacter("Li'ka", 'Half-Elf', 42, 'lika'),

    id: demoIdLika,

    charClass: 'Cleric',

    level: 5,

    currentHp: 38,

    maxHp: 42,

    currentMana: 8,

    maxMana: 12,

    armorClass: 15,

    speed: 35,

    initiative: 3,

    armorType: 'light',

    armorDurability: 22,

    wallet: { copper: 5, silver: 10, gold: 100, platinum: 0 },

    walletVisible: true,

    currentDay: 8,

    mealsToday: 1,

    

    // Stats

    baseStats: { str: 14, dex: 16, con: 14, int: 12, wis: 15, cha: 10 },

    skillProficiencies: ['Athletics', 'Perception', 'Stealth', 'Survival', 'Nature'],

    skillExpertise: ['Survival'],

    savingThrowProficiencies: ['str', 'dex'],

    

    // Buffs with full data - including the special demo buffs

    buffList: ['+2 Survival', 'Bless (self)'],

    permanentBuffs: ['Darkvision', 'Fey Ancestry', 'Divine Domain: Life'],

    debuffList: ['⚠️ Observed', 'Exhaustion 1', '-1 Str'],

    permanentDebuffs: ['🔗 Chained'],

    buffData: {

      '+2 Survival': { playerTooltip: 'From completing the Carnivore diet', source: 'Diet Completion', color: 'teal', noteVisible: true },

      'Bless (self)': { playerTooltip: '+1d4 to attacks and saves', source: 'Self-cast spell', color: 'gold' },

      'Darkvision': { playerTooltip: '60 ft darkvision from Half-Elf heritage' },

      'Fey Ancestry': { playerTooltip: 'Advantage vs charm, immune to magical sleep' },

      'Divine Domain: Life': { playerTooltip: 'Bonus healing on cure spells' },

      '⚠️ Observed': { playerTooltip: 'You are being observed', source: 'Something is watching...', color: 'gray' },

      'Exhaustion 1': { playerTooltip: 'Disadvantage on ability checks', source: 'Long journey without rest', color: 'default-debuff' },

      '-1 Str': { playerTooltip: 'Weakened from illness', source: 'Disease effect', color: 'default-debuff' },

      '🔗 Chained': { playerTooltip: 'You feel bound by an unseen force', source: 'Ancient curse from the ruins', color: 'void', glowColor: '#ff0000' },

    },

    

    // Diet tracking - show progress

    activeDiet: 'carnivore',

    dietWeekProgress: 2,

    dietDayStreak: 4,

    dietCompletionCounts: { 'carnivore': 1 },

    completedDiets: ['carnivore'],

    mealLog: [

      { day: 1, meal: 1, food: 'Dried Meat', notes: 'Found in abandoned camp', type: 'meat' },

      { day: 1, meal: 2, food: 'Venison Steak', notes: 'Hunted deer', type: 'meat' },

      { day: 2, meal: 1, food: 'Rabbit Jerky', notes: '', type: 'meat' },

      { day: 2, meal: 2, food: 'Boar Ribs', notes: 'Delicious!', type: 'meat' },

      { day: 3, meal: 1, food: 'Fish Fillet', notes: 'Caught at river', type: 'meat' },

      { day: 3, meal: 2, food: 'Salted Pork', notes: '', type: 'meat' },

      { day: 4, meal: 1, food: 'Chicken Leg', notes: '', type: 'meat' },

      { day: 5, meal: 1, food: 'Beef Stew', notes: 'From tavern', type: 'meat' },

      { day: 5, meal: 2, food: 'Mutton Chop', notes: '', type: 'meat' },

      { day: 6, meal: 1, food: 'Trail Rations', notes: 'Running low', type: 'ration' },

      { day: 7, meal: 1, food: 'Venison', notes: 'Fresh kill', type: 'meat' },

      { day: 7, meal: 2, food: 'Smoked Fish', notes: '', type: 'meat' },

      { day: 8, meal: 1, food: 'Bacon', notes: 'Breakfast', type: 'meat' },

    ],

    

    // Disease - with partial visibility (NOT swamp rot)

    diseaseList: [

      {

        diseaseId: 'mind-plague',

        showName: false,

        showEffects: true,

        visibleSymptoms: [0],

        showCure: false,

        contractedDay: 5,

      }

    ],

    

    // Systems applied - ALL demo systems on Li'ka

    systemList: [

      {

        instanceId: 'lika-corruption',

        systemId: 'corruption-tracker',

        active: true,

        playerVisible: true,

        showName: true,

        showDescription: true,

        showEffects: false,

        showCure: false,

        visibleClues: [],

        unknownText: 'Corrupted',

        placeholderName: 'Corrupted',

        placeholderDescription: 'A dark energy pulses within you...',

        trackerValues: { 'corruption-level': 7 },

        trackerVisibility: { 'corruption-level': { showToPlayer: true, showBar: true, showValue: true } },

        cluesLabel: 'Symptoms',

        appliedDay: 3,

      },

      {

        instanceId: 'lika-dreams',

        systemId: 'prophetic-dreams',

        active: true,

        playerVisible: true,

        showName: true,

        showDescription: true,

        showEffects: false,

        showCure: false,

        visibleClues: [0, 1],

        visibleSections: { 'dream-journal': [0, 1] },

        showSections: {},

        cluesLabel: 'Visions',

        appliedDay: 2,

      },

      {

        instanceId: 'lika-watched',

        systemId: 'watched-mark',

        active: true,

        playerVisible: true,

        showName: false,

        showDescription: false,

        showEffects: false,

        showCure: false,

        visibleClues: [0],

        placeholderName: 'Uneasy Feeling',

        placeholderDescription: "You can't shake the feeling that something is watching you.",

        cluesLabel: 'Signs',

        appliedDay: 1,

      }

    ],

    

    // Equipment

    inventory: [

      {

        id: 'lika-health-pot',

        name: 'Health Potion',

        category: 'consumable',

        quantity: 3,

        equipped: false,

        favorited: true,

        buffs: [],

        debuffs: [],

        buffsApplyOnUse: false,

        notes: 'Standard healing potion from the village apothecary',

        uses: { current: 1, max: 1, resetType: 'none' },

        abilities: [],

        questDetails: '',

        questComplete: false,

        onUseEffects: ['+10 HP'],

        combatBonuses: [],

        addedDate: new Date().toISOString(),

      },

      {

        id: 'lika-elixir',

        name: 'Elixir of Focus',

        category: 'consumable',

        quantity: 5,

        equipped: false,

        favorited: true,

        buffs: ['+2 Int', '+1 Wis'],

        debuffs: [],

        buffsApplyOnUse: true,

        notes: 'Lasts until next long rest',

        uses: { current: 1, max: 1, resetType: 'none' },

        abilities: [],

        questDetails: '',

        questComplete: false,

        onUseEffects: ['+5 Mana'],

        combatBonuses: [],

        addedDate: new Date().toISOString(),

      },

      {

        id: 'lika-rations',

        name: 'Trail Rations',

        category: 'consumable',

        quantity: 5,

        equipped: false,

        favorited: false,

        buffs: [],

        debuffs: [],

        buffsApplyOnUse: false,

        notes: 'Dried meat and hardtack',

        uses: { current: 1, max: 1, resetType: 'none' },

        abilities: [],

        questDetails: '',

        questComplete: false,

        onUseEffects: [],

        combatBonuses: [],

        addedDate: new Date().toISOString(),

      },

      {

        id: 'lika-greatsword',

        name: 'Phantom Greatsword',

        category: 'combat',

        quantity: 1,

        equipped: true,

        favorited: true,

        buffs: ['+1 Str', '+2 Damage'],

        debuffs: ['-1 Dex'],

        buffsApplyOnUse: false,

        notes: 'An ethereal blade that phases through armor',

        uses: null,

        abilities: [

          {

            id: 'lika-phantom-strike',

            name: 'Phantom Strike',

            description: 'Phase through enemy armor, ignoring AC bonus from armor',

            effects: ['+5 Damage'],

            uses: { current: 2, max: 2, resetType: 'shortRest' },

          },

          {

            id: 'lika-soul-drain',

            name: 'Soul Drain',

            description: 'Drain life force from a wounded enemy',

            effects: ['+3 HP'],

            uses: { current: 1, max: 1, resetType: 'longRest' },

          },

        ],

        questDetails: '',

        questComplete: false,

        onUseEffects: [],

        combatBonuses: [

          { type: 'advantage', scope: 'str_checks', label: 'Adv on Str checks (while attuned)' },

        ],

        addedDate: new Date().toISOString(),

      },

      {

        id: 'lika-longbow',

        name: 'Yew Longbow',

        category: 'combat',

        quantity: 1,

        equipped: false,

        favorited: false,

        buffs: ['+1 Dex'],

        debuffs: [],

        buffsApplyOnUse: false,

        notes: 'Finely crafted elven bow',

        uses: null,

        abilities: [],

        questDetails: '',

        questComplete: false,

        onUseEffects: [],

        combatBonuses: [],

        addedDate: new Date().toISOString(),

      },

      {

        id: 'lika-shield',

        name: 'Bulwark Shield',

        category: 'combat',

        quantity: 1,

        equipped: false,

        favorited: false,

        buffs: ['+2 AC'],

        debuffs: ['-5 Speed'],

        buffsApplyOnUse: false,

        notes: 'Heavy tower shield',

        uses: null,

        abilities: [

          {

            id: 'lika-shield-wall',

            name: 'Shield Wall',

            description: 'Brace for impact, reducing all damage taken',

            effects: ['+4 AC', '-10 Speed'],

            uses: { current: 3, max: 3, resetType: 'longRest' },

          },

        ],

        questDetails: '',

        questComplete: false,

        onUseEffects: [],

        combatBonuses: [

          { type: 'save_bonus', scope: 'dex_saves', value: 2, label: '+2 DEX Saves' },

        ],

        addedDate: new Date().toISOString(),

      },

      {

        id: 'lika-amulet',

        name: 'Amulet of the Lost King',

        category: 'quest',

        quantity: 1,

        equipped: false,

        favorited: false,

        buffs: [],

        debuffs: [],

        buffsApplyOnUse: false,

        notes: 'Glows faintly when near undead',

        uses: null,

        abilities: [],

        questDetails: 'Find the tomb of King Aldric in the Shadowed Vale. The amulet is the key to opening the inner sanctum. Speak with the ghost of Sir Brennan at the ruined watchtower for directions.',

        questComplete: false,

        onUseEffects: [],

        combatBonuses: [

          { type: 'passive', scope: 'passive_perception', value: 3, label: '+3 Passive Perception' },

        ],

        addedDate: new Date().toISOString(),

      },

      {

        id: 'lika-letter',

        name: 'Sealed Letter',

        category: 'quest',

        quantity: 1,

        equipped: false,

        favorited: false,

        buffs: [],

        debuffs: [],

        buffsApplyOnUse: false,

        notes: 'Delivered to Commander Vex',

        uses: null,

        abilities: [],

        questDetails: 'Delivered the urgent message from the frontier outpost.',

        questComplete: true,

        onUseEffects: [],

        combatBonuses: [],

        addedDate: new Date().toISOString(),

      },

      {

        id: 'lika-beast-core',

        name: 'Beast Core',

        category: 'component',

        quantity: 3,

        equipped: false,

        favorited: false,

        buffs: [],

        debuffs: [],

        buffsApplyOnUse: false,

        notes: 'Harvested from magical beasts',

        uses: null,

        abilities: [],

        questDetails: '',

        questComplete: false,

        onUseEffects: [],

        combatBonuses: [],

        addedDate: new Date().toISOString(),

      },

      {

        id: 'lika-cloak',

        name: 'Cloak of Protection',

        category: 'combat',

        quantity: 1,

        equipped: true,

        favorited: true,

        buffs: ['+1 AC'],

        debuffs: [],

        buffsApplyOnUse: false,

        notes: 'Shimmers faintly in moonlight. Requires attunement. +1 to saving throws.',

        uses: null,

        abilities: [],

        questDetails: '',

        questComplete: false,

        onUseEffects: [],

        combatBonuses: [

          { type: 'save_bonus', scope: 'all_saves', value: 1, label: '+1 to All Saves' },

        ],

        addedDate: new Date().toISOString(),

      },

      {

        id: 'lika-rope',

        name: 'Rope (50 ft)',

        category: 'misc',

        quantity: 1,

        equipped: false,

        favorited: false,

        buffs: [],

        debuffs: [],

        buffsApplyOnUse: false,

        notes: 'Hempen rope',

        uses: null,

        abilities: [],

        questDetails: '',

        questComplete: false,

        onUseEffects: [],

        combatBonuses: [],

        addedDate: new Date().toISOString(),

      },

      {

        id: 'lika-quiver',

        name: 'Quiver of Returning',

        category: 'combat',

        quantity: 1,

        equipped: true,

        favorited: false,

        buffs: [],

        debuffs: [],

        buffsApplyOnUse: false,

        notes: 'Arrows magically return after being fired (20 arrows)',

        uses: null,

        abilities: [],

        questDetails: '',

        questComplete: false,

        onUseEffects: [],

        combatBonuses: [

          { type: 'advantage', scope: 'ranged_attacks', label: 'Adv on ranged attacks (magical arrows)' },

        ],

        addedDate: new Date().toISOString(),

      },

    ],

    

    // Spells - Cleric spell list (8 spells)

    spells: [

      { ...SAMPLE_SPELLS[1], id: 'lika-sacred-flame', prepared: true },   // Sacred Flame (cantrip)

      { ...SAMPLE_SPELLS[2], id: 'lika-cure', prepared: true },           // Cure Wounds

      { ...SAMPLE_SPELLS[3], id: 'lika-healword', prepared: true },       // Healing Word

      { ...SAMPLE_SPELLS[4], id: 'lika-guiding', prepared: true },        // Guiding Bolt

      { ...SAMPLE_SPELLS[5], id: 'lika-bless', prepared: true },          // Bless

      { ...SAMPLE_SPELLS[6], id: 'lika-shield-faith', prepared: true },   // Shield of Faith

      { ...SAMPLE_SPELLS[10], id: 'lika-mass-heal', prepared: true },     // Mass Healing Word

      createSpell({

        id: 'lika-spirit-guardians',

        name: "Spirit Guardians",

        level: 3,

        school: 'Conjuration',

        castingTime: '1A',

        range: 'Self (15 ft radius)',

        components: 'V, S, M (holy symbol)',

        duration: 'Concentration, 10 minutes',

        concentration: true,

        description: 'Spirits swirl around you in a 15-foot radius. Each creature of your choice that enters or starts its turn in the area must make a WIS save or take 3d8 radiant damage (half on save).',

        effect: '3d8 radiant (AoE)',

        dice: '3d8',

        damageType: 'radiant',

        spellType: 'damage',

        targetType: 'multi',

        hitDC: 'save:WIS',

        halfOnSave: true,

        higherLevels: '+1d8 per level above 3rd',

        upcastDice: '1d8',

        tags: ['Damage', 'Radiant', 'AoE', 'Concentration'],

        source: 'PHB p.278',

        prepared: true

      }),

    ],

    spellSlots: { 1: 4, 2: 3, 3: 2 },

    usedSpellSlots: { 1: 0, 2: 0, 3: 0 },

    spellcastingStat: 'wis',

    concentratingOn: 'Bless',

    

    // Active combat effects (buffs on this character)

    activeEffects: [

      {

        name: 'Bless',

        modifier: '+1d4',

        duration: 'concentration',

        roundApplied: 1,

        appliesTo: 'attacks_and_saves',

        autoApply: false, // Don't auto-add to damage rolls

        casterId: demoIdLika,

        casterName: "Li'ka",

      },

      {

        name: 'Healing Spirit',

        modifier: null,

        duration: 5,

        roundApplied: 1,

        appliesTo: 'any',

        autoApply: false,

        casterId: demoIdEve,

        casterName: 'Eve',

        hotHeal: '1d6',

        tickTiming: 'start_of_target_turn',

      },

    ],

    

    abilities: ['Spellcasting', 'Channel Divinity', 'Disciple of Life', 'Destroy Undead'],

    feats: ['War Caster'],

    

    statusHistory: [

      { timestamp: new Date(Date.now() - 7*24*60*60*1000).toISOString(), day: 1, event: 'created', description: 'Day 1: Journey begins' },

      { timestamp: new Date(Date.now() - 6*24*60*60*1000).toISOString(), day: 2, event: 'system', description: 'Day 2: Strange dreams begin' },

      { timestamp: new Date(Date.now() - 5*24*60*60*1000).toISOString(), day: 3, event: 'system', description: 'Day 3: Touched corrupted artifact' },

      { timestamp: new Date(Date.now() - 3*24*60*60*1000).toISOString(), day: 5, event: 'disease', description: 'Day 5: Contracted unknown illness' },

      { timestamp: new Date(Date.now() - 1*24*60*60*1000).toISOString(), day: 7, event: 'diet', description: 'Day 7: Completed Carnivore diet!' },

    ],

  };

  

  // Demo character 2: Eve - Cleric with divine systems

  const eve = {

    ...createDefaultCharacter('Eve', 'Human', 38, 'eve'),

    id: demoIdEve,

    charClass: 'Ranger',

    level: 5,

    currentHp: 32,

    maxHp: 38,

    currentMana: 16,

    maxMana: 20,

    armorClass: 18,

    speed: 30,

    initiative: 1,

    currentDay: 8,

    mealsToday: 2,

    

    baseStats: { str: 14, dex: 10, con: 14, int: 12, wis: 17, cha: 13 },

    skillProficiencies: ['Insight', 'Medicine', 'Persuasion', 'Religion'],

    savingThrowProficiencies: ['wis', 'cha'],

    

    buffList: ["Hunter's Mark"],

    permanentBuffs: ['Fighting Style: Archery', 'Natural Explorer'],

    debuffList: [],

    permanentDebuffs: [],

    buffData: {

      "Hunter's Mark": { playerTooltip: 'Extra 1d6 damage to marked target', source: 'Ranger Spell' },

      'Fighting Style: Archery': { playerTooltip: '+2 to ranged attack rolls' },

      'Natural Explorer': { playerTooltip: 'Difficult terrain doesn\'t slow you' },

    },

    

    diseaseList: [],

    

    // Eve has the Fey Blessing - divine connection

    systemList: [

      {

        instanceId: 'eve-blessing',

        systemId: 'fey-blessing',

        active: true,

        playerVisible: true,

        showName: true,

        showDescription: true,

        showEffects: true,

        showCure: false,

        visibleClues: [0, 1],

        visibleSections: { 'source': true },

        showSections: { 'source': true },

        appliedDay: 1,

      }

    ],

    

    inventory: [

      { id: 'eve-inv-1', name: 'Mace', type: 'weapon', quantity: 1, equipped: true, description: 'Simple weapon' },

      { id: 'eve-inv-2', name: 'Shield', type: 'armor', quantity: 1, equipped: true, buffs: ['+2 AC'], description: 'Wooden shield' },

      { id: 'eve-inv-3', name: 'Chain Mail', type: 'armor', quantity: 1, equipped: true, description: 'Heavy armor' },

      { id: 'eve-inv-4', name: 'Holy Symbol', type: 'gear', quantity: 1, equipped: true, description: 'Silver sun emblem' },

      { id: 'eve-inv-5', name: 'Healer\'s Kit', type: 'gear', quantity: 1, description: '10 uses remaining' },

    ],

    

    spells: [

      { ...SAMPLE_SPELLS[0], id: 'eve-firebolt', prepared: true },        // Fire Bolt (cantrip)

      { ...SAMPLE_SPELLS[2], id: 'eve-cure', prepared: true },            // Cure Wounds

      { ...SAMPLE_SPELLS[3], id: 'eve-healword', prepared: true },        // Healing Word

      { ...SAMPLE_SPELLS[4], id: 'eve-guiding', prepared: false },        // Guiding Bolt (unprepared)

      { ...SAMPLE_SPELLS[7], id: 'eve-burning', prepared: true },         // Burning Hands

      createSpell({

        id: 'eve-hunters-mark',

        name: "Hunter's Mark",

        level: 1,

        school: 'Divination',

        castingTime: '1BA',

        range: '90 ft',

        components: 'V',

        duration: 'Concentration, 1 hour',

        concentration: true,

        description: 'You mark a creature as your quarry. Until the spell ends, you deal an extra 1d6 damage to the target whenever you hit it with a weapon attack.',

        effect: '+1d6 damage to marked target',

        dice: '1d6',

        spellType: 'mark',

        targetType: 'single',

        bonusDice: '1d6',

        casterOnly: true,

        higherLevels: 'Duration increases to 8 hours at 3rd, 24 hours at 5th',

        tags: ['Mark', 'Damage'],

        source: 'PHB p.251',

        prepared: true

      }),

      createSpell({

        id: 'eve-faerie-fire',

        name: "Faerie Fire",

        level: 1,

        school: 'Evocation',

        castingTime: '1A',

        range: '60 ft',

        components: 'V',

        duration: 'Concentration, 1 minute',

        concentration: true,

        description: 'Each object in a 20-foot cube is outlined in light. Any creature in the area is also outlined if it fails a DEX save. Attacks against affected creatures have advantage.',

        effect: 'Outlined, attacks have advantage',

        spellType: 'debuff',

        targetType: 'single',

        percentIncrease: 15,

        casterOnly: false,

        debuffDuration: null,

        hitDC: 'save:DEX',

        tags: ['Debuff', 'Light'],

        source: 'PHB p.239',

        prepared: true

      }),

    ],

    spellSlots: { 1: 4, 2: 3 },

    usedSpellSlots: { 1: 1, 2: 0 },

    spellcastingStat: 'wis',

    concentratingOn: "Hunter's Mark",

    

    // Active combat effects (buffs on this character)

    activeEffects: [

      {

        name: 'Shield of Faith',

        modifier: '+2 AC',

        duration: 'concentration',

        roundApplied: 1,

        appliesTo: 'ac',

        autoApply: false,

        casterId: demoIdLika,

        casterName: "Li'ka",

      },

      {

        name: 'Regenerate',

        modifier: null,

        duration: 8,

        roundApplied: 1,

        appliesTo: 'any',

        autoApply: false,

        casterId: demoIdLika,

        casterName: "Li'ka",

        hotHeal: '1d4',

        hotTempHp: 2,

        tickTiming: 'start_of_target_turn',

      },

    ],

    markedTargets: [{ targetId: 'enemy-1', targetName: 'Goblin Boss', spellName: "Hunter's Mark", bonusDice: '1d6', casterId: demoIdEve, casterOnly: true }],

    

    abilities: ['Favored Enemy (Undead)', 'Natural Explorer (Forest)', 'Fighting Style: Archery', 'Extra Attack'],

    feats: ['Sharpshooter'],

  };

  

  // Demo character 3: Khan - Rogue with secrets and debt

  const khan = {

    ...createDefaultCharacter('Khan', 'Tiefling', 32, 'khan'),

    id: demoIdKhan,

    charClass: 'Rogue',

    level: 5,

    currentHp: 28,

    maxHp: 32,

    currentMana: 0,

    maxMana: 0,

    armorClass: 15,

    speed: 30,

    initiative: 5,

    currentDay: 8,

    mealsToday: 1,

    

    baseStats: { str: 10, dex: 18, con: 12, int: 14, wis: 12, cha: 14 },

    skillProficiencies: ['Acrobatics', 'Deception', 'Sleight of Hand', 'Stealth', 'Thieves\' Tools', 'Perception'],

    skillExpertise: ['Stealth', 'Thieves\' Tools'],

    savingThrowProficiencies: ['dex', 'int'],

    

    buffList: [],

    permanentBuffs: ['Darkvision', 'Hellish Resistance', 'Sneak Attack (3d6)'],

    debuffList: ['-1 to Persuasion (guild contacts)'],

    permanentDebuffs: [],

    buffData: {

      'Darkvision': { playerTooltip: '60 ft darkvision from Tiefling heritage' },

      'Hellish Resistance': { playerTooltip: 'Resistance to fire damage' },

      'Sneak Attack (3d6)': { playerTooltip: 'Extra damage when you have advantage' },

      '-1 to Persuasion (guild contacts)': { playerTooltip: 'Your debt has damaged your reputation', source: 'Thieves\' Guild Debt', color: 'orange', noteVisible: true },

    },

    

    diseaseList: [],

    

    // Khan has the debt tracker and a secret

    systemList: [

      {

        instanceId: 'khan-debt',

        systemId: 'debt-tracker',

        active: true,

        playerVisible: true,

        showName: true,

        showDescription: true,

        showEffects: false,

        showCure: true,

        visibleClues: [],

        visibleSections: { 'terms': true },

        showSections: { 'terms': true },

        trackerValues: { 'debt-amount': 1200 },

        trackerVisibility: { 'debt-amount': { showToPlayer: true, showBar: false, showValue: true } },

        appliedDay: 1,

      },

      {

        instanceId: 'khan-secret',

        systemId: 'haunted-secret',

        active: true,

        playerVisible: false,

        showName: false,

        showDescription: false,

        showEffects: false,

        showCure: false,

        visibleClues: [0, 1],

        visibleSections: {},

        showSections: {},

        appliedDay: 4,

        dmNotes: 'Khan doesn\'t know about the ghost yet. Reveal when party reaches Ravenmoor.',

      }

    ],

    

    inventory: [

      { id: 'khan-inv-1', name: 'Rapier', type: 'weapon', quantity: 1, equipped: true, description: 'Finesse weapon' },

      { id: 'khan-inv-2', name: 'Shortbow', type: 'weapon', quantity: 1, equipped: false, description: 'Backup ranged' },

      { id: 'khan-inv-3', name: 'Leather Armor', type: 'armor', quantity: 1, equipped: true },

      { id: 'khan-inv-4', name: 'Thieves\' Tools', type: 'gear', quantity: 1, equipped: true },

      { id: 'khan-inv-5', name: 'Caltrops', type: 'gear', quantity: 2, description: 'Bag of 20' },

      { id: 'khan-inv-6', name: 'Smoke Bomb', type: 'consumable', quantity: 3, description: 'Creates heavily obscured area' },

    ],

    

    spells: [

      createSpell({

        id: 'khan-thaumaturgy',

        name: 'Thaumaturgy',

        level: 0,

        school: 'Transmutation',

        castingTime: '1A',

        range: '30 ft',

        components: 'V',

        duration: 'Up to 1 minute',

        description: 'You manifest a minor wonder within range. You create one of several harmless sensory effects: booming voice, flickering flames, tremors, sounds, doors slam open or shut, or alter your eye appearance.',

        effect: 'Sensory trick',

        spellType: 'buff',

        targetType: 'self',

        tags: ['Utility', 'Racial'],

        source: 'PHB p.282',

        prepared: true

      }),

      createSpell({

        id: 'khan-hellish-rebuke',

        name: 'Hellish Rebuke',

        level: 1,

        school: 'Evocation',

        castingTime: '1R',

        range: '60 ft',

        components: 'V, S',

        duration: 'Instantaneous',

        description: 'In response to being damaged, you point your finger and the creature that damaged you is momentarily surrounded by hellish flames. It must make a DEX save or take 2d10 fire damage (half on save).',

        effect: '2d10 fire',

        dice: '2d10',

        damageType: 'fire',

        spellType: 'damage',

        targetType: 'single',

        hitDC: 'save:DEX',

        halfOnSave: true,

        higherLevels: '+1d10 per level above 1st',

        upcastDice: '1d10',

        tags: ['Damage', 'Fire', 'Reaction', 'Racial'],

        source: 'PHB p.250',

        prepared: true

      }),

      createSpell({

        id: 'khan-darkness',

        name: 'Darkness',

        level: 2,

        school: 'Evocation',

        castingTime: '1A',

        range: '60 ft',

        components: 'V, M (bat fur)',

        duration: 'Concentration, 10 minutes',

        concentration: true,

        description: 'Magical darkness spreads from a point within range to fill a 15-foot-radius sphere. Creatures with darkvision can\'t see through it. Non-magical light can\'t illuminate it.',

        effect: '15ft sphere of darkness',

        spellType: 'debuff',

        targetType: 'aoe',

        percentIncrease: 0,

        casterOnly: false,

        applyBuff: 'Shrouded in Darkness (blinded)',

        tags: ['Control', 'Racial'],

        source: 'PHB p.230',

        prepared: true

      }),

    ],

    spellSlots: { 1: 1, 2: 1 },

    usedSpellSlots: { 1: 0, 2: 0 },

    spellcastingStat: 'cha',

    

    abilities: ['Sneak Attack', 'Cunning Action', 'Uncanny Dodge', 'Thieves\' Cant'],

    feats: ['Alert'],

  };

  const ventyl = {

    ...createTestCharacter(),

    id: demoIdVentyl,

    name: 'Ventyl',

    password: 'ventyl',

    currentDay: 8,

    mealsToday: 1,

  };

  

  // Demo food pile

  const demoFood = {

    totalUnits: 24,

    foodLog: [

      { id: 'food-1', name: 'Dried Meat', type: 'meat', quantity: 8, notes: 'Purchased in town', addedDay: 1, location: 'Town' },

      { id: 'food-2', name: 'Hardtack', type: 'ration', quantity: 6, notes: 'Emergency supplies', addedDay: 1, location: 'Town' },

      { id: 'food-3', name: 'Fresh Apples', type: 'fruit', quantity: 4, notes: 'From orchard - will spoil!', addedDay: 5, location: 'Farm' },

      { id: 'food-4', name: 'Cheese Wheel', type: 'dairy', quantity: 2, notes: 'Gift from grateful farmer', addedDay: 5, location: 'Farm' },

      { id: 'food-5', name: 'Smoked Fish', type: 'meat', quantity: 4, notes: 'Caught and preserved', addedDay: 7, location: 'River' },

    ],

  };

  

  // Demo travel log

  const demoTravelLog = [

    { id: 'travel-1', day: 1, from: 'Millbrook Village', to: 'Forest Road', distance: '12 miles', notes: 'Set out at dawn. Weather clear.', encounters: 'None', campType: 'roadside' },

    { id: 'travel-2', day: 2, from: 'Forest Road', to: 'Darkwood Forest', distance: '8 miles', notes: "Entered the forest. Li'ka had strange dreams.", encounters: 'Wolf pack (avoided)', campType: 'hidden' },

    { id: 'travel-3', day: 3, from: 'Darkwood Forest', to: 'Ancient Ruins', distance: '5 miles', notes: "Found mysterious ruins. Li'ka touched corrupted altar.", encounters: 'Skeleton patrol (defeated)', campType: 'ruins' },

    { id: 'travel-4', day: 4, from: 'Ancient Ruins', to: 'River Crossing', distance: '10 miles', notes: 'Fled the ruins. Khan started having nightmares.', encounters: 'None', campType: 'riverside' },

    { id: 'travel-5', day: 5, from: 'River Crossing', to: 'Abandoned Farm', distance: '6 miles', notes: "Li'ka showing symptoms of illness. Found supplies at farm.", encounters: 'Bandits (negotiated)', campType: 'building' },

    { id: 'travel-6', day: 6, from: 'Abandoned Farm', to: 'Crossroads', distance: '7 miles', notes: 'Met a debt collector looking for Khan.', encounters: 'Guild contact (tense)', campType: 'elevated' },

    { id: 'travel-7', day: 7, from: 'Crossroads', to: 'Stone Bridge', distance: '9 miles', notes: "Good progress. Li'ka completed diet! Eve received a vision.", encounters: 'Merchant caravan (traded)', campType: 'roadside' },

    { id: 'travel-8', day: 8, from: 'Stone Bridge', to: 'Current Camp', distance: '0 miles', notes: 'Resting. Preparing for battle.', encounters: 'Goblin scouts spotted', campType: 'defensive' },

  ];

  

  // Demo combat state

  const demoCombat = {

    active: true,

    round: 2,

    turnIndex: 0,

    combatants: [

      { id: demoIdLika, name: "Li'ka", type: 'player', initiative: 18, initiativeRoll: 15, initiativeMod: 3, currentHp: 38, maxHp: 42, conditions: ['Bless (self)'], isPlayer: true,

        debuffs: [

          {

            name: 'Bestow Curse',

            casterId: 'enemy-2',

            casterName: 'Goblin Shaman',

            casterOnly: false,

            bonusDice: null,

            percentIncrease: null,

            flatBonus: null,

            damageTypes: ['necrotic'],

            duration: 'concentration',

            autoApply: false,

            roundApplied: 1,

            dotDamage: '1d8',

            dotDamageType: 'necrotic',

            tickTiming: 'start_of_target_turn',

            saveType: 'wis',

            saveDC: 12,

            saveTiming: 'end_of_target_turn',

            saveRemovesEffect: true,

          },

          {

            name: 'Poisoned',

            casterId: 'enemy-1',

            casterName: 'Goblin Boss',

            casterOnly: false,

            bonusDice: null,

            percentIncrease: null,

            flatBonus: null,

            damageTypes: [],

            duration: 3,

            turnsRemaining: 3,

            autoApply: false,

            roundApplied: 2,

            saveType: 'con',

            saveDC: 13,

            saveTiming: 'end_of_target_turn',

            saveRemovesEffect: true,

          },

        ],

      },

      { id: demoIdEve, name: 'Eve', type: 'player', initiative: 12, initiativeRoll: 11, initiativeMod: 1, currentHp: 32, maxHp: 38, conditions: ['Hexed'], isPlayer: true,

        debuffs: [

          {

            name: 'Hex',

            casterId: 'enemy-2',

            casterName: 'Goblin Shaman',

            casterOnly: false,

            bonusDice: null,

            percentIncrease: null,

            flatBonus: null,

            damageTypes: ['necrotic'],

            duration: 'concentration',

            autoApply: false,

            roundApplied: 1,

            dotDamage: '1d6',

            dotDamageType: 'necrotic',

            tickTiming: 'start_of_target_turn',

          },

          {

            name: 'Slow',

            casterId: 'enemy-2',

            casterName: 'Goblin Shaman',

            casterOnly: false,

            bonusDice: null,

            percentIncrease: null,

            flatBonus: -2,

            damageTypes: ['all'],

            duration: 4,

            turnsRemaining: 4,

            autoApply: true,

            roundApplied: 1,

            saveType: 'wis',

            saveDC: 12,

            saveTiming: 'end_of_target_turn',

            saveRemovesEffect: true,

          },

        ],

      },

      { id: demoIdVentyl, name: 'Ventyl', type: 'player', initiative: 15, initiativeRoll: 12, initiativeMod: 3, currentHp: 38, maxHp: 42, conditions: [], isPlayer: true },

      { id: demoIdKhan, name: 'Khan', type: 'player', initiative: 22, initiativeRoll: 17, initiativeMod: 5, currentHp: 28, maxHp: 32, conditions: [], isPlayer: true },

      { 

        id: 'enemy-1', 

        name: 'Goblin Boss', 

        type: 'enemy', 

        initiative: 14, 

        initiativeRoll: 13, 

        initiativeMod: 1, 

        currentHp: 21, 

        maxHp: 21, 

        armorClass: 17, 

        conditions: ["Marked by Eve (Hunter's Mark)"], 

        notes: 'Leader of the raiding party',

        attackRoutines: [{

          id: 'gb-multi', name: 'Attack Routine', attacks: [

            { id: 'gb-scim', name: 'Scimitar', count: 2, toHitBonus: 4, damageDice: '1d6+2', damageType: 'slashing' },

            { id: 'gb-jav', name: 'Javelin', count: 1, toHitBonus: 4, damageDice: '1d6+2', damageType: 'piercing' },

          ],

        }],

        resistances: ['poison'],

        immunities: [],

        vulnerabilities: [],

        savingThrows: { str: 1, dex: 2, con: 1, int: 0, wis: -1, cha: -1 },

        debuffs: [{

          name: "Hunter's Mark",

          casterId: demoIdEve,

          casterName: 'Eve',

          casterOnly: true,

          bonusDice: '1d6',

          percentIncrease: null,

          flatBonus: null,

          damageTypes: ['all'],

          duration: null,

          turnsRemaining: null,

          autoApply: true,

          roundApplied: 1,

        },

        {

          name: "Spirit Guardians",

          casterId: demoIdLika,

          casterName: "Li'ka",

          casterOnly: false,

          bonusDice: null,

          percentIncrease: null,

          flatBonus: null,

          damageTypes: ['radiant'],

          duration: 'concentration',

          autoApply: false,

          roundApplied: 1,

          dotDamage: '3d8',

          dotDamageType: 'radiant',

          tickTiming: 'start_of_target_turn',

          saveType: 'wis',

          saveDC: 15,

          saveTiming: 'start_of_target_turn',

          saveRemovesEffect: false,

        }],

      },

      { 

        id: 'enemy-2', 

        name: 'Goblin Shaman', 

        type: 'enemy', 

        initiative: 11, 

        initiativeRoll: 9, 

        initiativeMod: 2, 

        currentHp: 15, 

        maxHp: 15, 

        armorClass: 13, 

        conditions: ['Faerie Fire'], 

        notes: 'Spellcaster - prioritize!',

        resistances: [],

        immunities: [],

        vulnerabilities: ['radiant'],

        savingThrows: { str: -1, dex: 1, con: 0, int: 2, wis: 3, cha: 1 },

        debuffs: [{

          name: 'Faerie Fire',

          casterId: demoIdLika,

          casterName: "Li'ka",

          casterOnly: false,

          bonusDice: null,

          percentIncrease: 15,

          flatBonus: null,

          damageTypes: ['all'],

          duration: 3,

          turnsRemaining: 3,

          autoApply: true,

          roundApplied: 1,

          saveType: 'dex',

          saveDC: 14,

          saveTiming: 'end_of_target_turn',

          saveRemovesEffect: true,

        },

        {

          name: 'Hail of Thorns',

          casterId: demoIdEve,

          casterName: 'Eve',

          casterOnly: false,

          bonusDice: null,

          percentIncrease: null,

          flatBonus: null,

          damageTypes: ['piercing'],

          duration: 3,

          turnsRemaining: 3,

          autoApply: false,

          roundApplied: 1,

          dotDamage: '1d10',

          dotDamageType: 'piercing',

          tickTiming: 'start_of_target_turn',

        }],

      },

      { 

        id: 'enemy-3', 

        name: 'Hobgoblin Warrior', 

        type: 'enemy', 

        initiative: 10, 

        initiativeRoll: 9, 

        initiativeMod: 1, 

        currentHp: 18, 

        maxHp: 18, 

        armorClass: 18, 

        conditions: ['Hold Person', 'Restrained'], 

        notes: 'Heavy armor, martial discipline',

        attackRoutines: [{

          id: 'hob-multi', name: 'Attack Routine', attacks: [

            { id: 'hob-ls', name: 'Longsword', count: 2, toHitBonus: 5, damageDice: '1d10+3', damageType: 'slashing' },

          ],

        }],

        resistances: [],

        immunities: [],

        vulnerabilities: [],

        savingThrows: { str: 3, dex: 0, con: 2, int: -1, wis: 0, cha: -1 },

        debuffs: [{

          name: 'Hold Person',

          casterId: demoIdLika,

          casterName: "Li'ka",

          casterOnly: false,

          bonusDice: null,

          percentIncrease: null,

          flatBonus: null,

          damageTypes: [],

          duration: 'concentration',

          autoApply: false,

          roundApplied: 1,

          saveType: 'wis',

          saveDC: 15,

          saveTiming: 'end_of_target_turn',

          saveRemovesEffect: true,

        },

        {

          name: 'Ensnaring Strike',

          casterId: demoIdEve,

          casterName: 'Eve',

          casterOnly: false,

          bonusDice: null,

          percentIncrease: null,

          flatBonus: null,

          damageTypes: ['piercing'],

          duration: 'concentration',

          autoApply: false,

          roundApplied: 2,

          dotDamage: '1d6',

          dotDamageType: 'piercing',

          tickTiming: 'start_of_target_turn',

          saveType: 'str',

          saveDC: 14,

          saveTiming: 'end_of_target_turn',

          saveRemovesEffect: true,

        }],

      },

      { 

        id: 'enemy-4', 

        name: 'Goblin Minion', 

        type: 'enemy', 

        initiative: 8, 

        initiativeRoll: 7, 

        initiativeMod: 1, 

        currentHp: 7, 

        maxHp: 7, 

        armorClass: 13, 

        conditions: ['Frightened'], 

        notes: '',

        resistances: [],

        immunities: [],

        vulnerabilities: [],

      },

      { 

        id: 'enemy-5', 

        name: 'Goblin Minion', 

        type: 'enemy', 

        initiative: 6, 

        initiativeRoll: 5, 

        initiativeMod: 1, 

        currentHp: 5, 

        maxHp: 7, 

        armorClass: 13, 

        conditions: [], 

        notes: 'Wounded',

        resistances: [],

        immunities: [],

        vulnerabilities: [],

      },

    ],

    damageLog: [

      { round: 1, attacker: 'Khan', target: 'Goblin Minion', damage: 18, type: 'piercing', notes: 'Sneak Attack!' },

      { round: 1, attacker: "Li'ka", target: 'Goblin Minion', damage: 12, type: 'piercing', notes: 'Longbow + Hunter\'s Mark' },

      { round: 1, attacker: 'Eve', target: 'Goblin Shaman', damage: 8, type: 'radiant', notes: 'Guiding Bolt' },

    ],

  };

  

  return {

    chars: {

      [demoIdLika]: lika,

      [demoIdEve]: eve,

      [demoIdVentyl]: ventyl,

      [demoIdKhan]: khan,

    },

    food: demoFood,

    groupTravelLog: demoTravelLog,

    combat: demoCombat,

  };

};



// Session 9 (2026-04-20): extract Li'ka from createDemoData for on-demand

// creation on prod. Previously Li'ka only existed via demoData seed (local

// mode only); prod users never had a way to materialize her as a persisted

// Convex row. The "Create Li'ka" button in the select-screen Dev Testing

// card now calls this to actually save Li'ka with id 'demo-lika' so the

// subsequent "Play as Li'ka" / "DM View" buttons have something to navigate

// to. Password stays the default ("lika") from createDefaultCharacter.

const createLikaCharacter = () => {

  const demo = createDemoData();

  return demo.chars['demo-lika'];

};





// Audit / Session mode defaults

const DEFAULT_AUDIT = {

  activeSessionId: null,

  sessions: {},

  events: {},

  snapshots: {},

};





const calcFoodInventory = log => { const inv = {}; (log || []).forEach(l => { if (l.unitsAdded && l.foodType) inv[l.foodType] = (inv[l.foodType] || 0) + l.unitsAdded; if (l.unitsConsumed && l.foodType) inv[l.foodType] = (inv[l.foodType] || 0) - l.unitsConsumed; }); Object.keys(inv).forEach(k => { if (inv[k] <= 0) delete inv[k]; }); return inv; };



const calcFoodWithNotes = log => {

  const inv = {};

  (log || []).forEach(l => {

    if (l.unitsAdded && l.foodType) {

      if (!inv[l.foodType]) inv[l.foodType] = { total: 0, notes: {} };

      inv[l.foodType].total += l.unitsAdded;

      const noteKey = l.notes || '_none_';

      inv[l.foodType].notes[noteKey] = (inv[l.foodType].notes[noteKey] || 0) + l.unitsAdded;

    }

    if (l.unitsConsumed && l.foodType) {

      if (!inv[l.foodType]) inv[l.foodType] = { total: 0, notes: {} };

      inv[l.foodType].total -= l.unitsConsumed;

      let remaining = l.unitsConsumed;

      if (inv[l.foodType].notes['_none_']) { const take = Math.min(remaining, inv[l.foodType].notes['_none_']); inv[l.foodType].notes['_none_'] -= take; remaining -= take; }

      for (const nk of Object.keys(inv[l.foodType].notes)) { if (nk !== '_none_' && remaining > 0) { const take = Math.min(remaining, inv[l.foodType].notes[nk]); inv[l.foodType].notes[nk] -= take; remaining -= take; } }

    }

  });

  Object.keys(inv).forEach(k => { if (inv[k].total <= 0) delete inv[k]; else { Object.keys(inv[k].notes).forEach(nk => { if (inv[k].notes[nk] <= 0) delete inv[k].notes[nk]; }); } });

  return inv;

};



const getFoodTooltip = (foodType, invWithNotes) => {

  if (!invWithNotes[foodType]) return '';

  const notes = invWithNotes[foodType].notes;

  const lines = [];

  Object.entries(notes).forEach(([note, count]) => { if (count > 0 && note !== '_none_') lines.push(`${count}x ${note}`); });

  if (notes['_none_'] > 0) lines.push(`${notes['_none_']}x (untagged)`);

  return lines.join('\n') || `${invWithNotes[foodType].total}x ${foodType}`;

};



const getRandomFoodType = inv => { const types = Object.keys(inv).filter(k => (typeof inv[k] === 'number' ? inv[k] : inv[k]?.total) > 0); return types.length ? types[Math.floor(Math.random() * types.length)] : null; };

const normalizeFood = (f) => f?.toLowerCase().replace(/s$/, '').trim();

const foodMatches = (foodType, requiredTypes) => requiredTypes.some(r => normalizeFood(foodType) === normalizeFood(r) || normalizeFood(foodType) + 's' === normalizeFood(r) || normalizeFood(foodType) === normalizeFood(r) + 's');

const getDietFoodType = (inv, diet, diets) => { if (!diet || !diets[diet]) return getRandomFoodType(inv); const required = diets[diet].requiredMealTypes || []; const available = Object.keys(inv).filter(t => { const count = typeof inv[t] === 'number' ? inv[t] : inv[t]?.total; return count > 0 && foodMatches(t, required); }); return available.length ? available[Math.floor(Math.random() * available.length)] : null; };

const hasDietFood = (inv, diet, diets) => { if (!diet || !diets[diet]) return true; const required = diets[diet].requiredMealTypes || []; return Object.keys(inv).some(t => { const count = typeof inv[t] === 'number' ? inv[t] : inv[t]?.total; return count > 0 && foodMatches(t, required); }); };



// Calculate stat modifiers from buffs/debuffs with source tracking

// Check buffs/debuffs for advantage/disadvantage on a given scope

// scope: 'str_checks', 'wis_saves', 'attack', 'stealth_checks', 'all_checks', 'all_saves', etc.

// Detect if a buff/debuff text has mechanical effects that the system auto-applies

// Get the buffData key for a specific buff instance, accounting for duplicates across all lists

// When adding: counts existing instances across ALL lists to determine key suffix  

// When looking up: pass the combined ordered list and the global index

const getNextBuffDataKey = (text, char) => {

  const allLists = [

    ...(char.permanentBuffs || []), ...(char.buffList || []),

    ...(char.permanentDebuffs || []), ...(char.debuffList || []),

  ];

  const count = allLists.filter(b => b === text).length;

  return count === 0 ? text : `${text}::${count}`;

};



// Lookup buffData for a buff by its global occurrence number

// globalOccurrence = 0 for first instance, 1 for second, etc.

const lookupBuffData = (text, globalOccurrence, buffData) => {

  if (!buffData) return undefined;

  const key = globalOccurrence === 0 ? text : `${text}::${globalOccurrence}`;

  return buffData[key] || (globalOccurrence === 0 ? undefined : buffData[text]);

};



// Count how many times `text` appears across multiple arrays, returning the count

const countInArrays = (text, ...arrays) => {

  let count = 0;

  for (const arr of arrays) { if (arr) for (const item of arr) { if (item === text) count++; } }

  return count;

};



// Get the global occurrence index for item at position `idx` in `list`,

// given arrays that were "counted before" this list in the canonical order

const getGlobalOccurrence = (text, idx, list, ...priorArrays) => {

  let count = countInArrays(text, ...priorArrays);

  for (let i = 0; i < idx; i++) { if (list[i] === text) count++; }

  return count;

};



// 2026-04-29: per-instance key for buff state that tracks specific

// instances rather than names — glow, disable, hide. Two same-named

// buffs on a char (e.g. "Observed" applied twice) need distinct entries

// so acting on one doesn't act on the other. Matches lookupBuffData's

// key shape: bare name for the first occurrence (preserves existing

// data keyed on bare name), `name::N` for subsequent occurrences.

const buffInstanceKey = (name, occurrence) => (

  !occurrence || occurrence <= 0 ? name : `${name}::${occurrence}`

);



// Inverse of buffInstanceKey — splits "name::N" back into { name, occurrence }.

// Used by graveyard render to display the bare name even though the

// stored key may include the occurrence suffix.

const parseBuffInstanceKey = (key) => {

  if (!key) return { name: '', occurrence: 0 };

  const sep = key.lastIndexOf('::');

  if (sep < 0) return { name: key, occurrence: 0 };

  const occ = parseInt(key.slice(sep + 2), 10);

  if (!Number.isFinite(occ)) return { name: key, occurrence: 0 };

  return { name: key.slice(0, sep), occurrence: occ };

};



// 2026-04-29: count instances of `name` across every buff/debuff list on

// a char. Subtracting 1 gives the occurrence index of the *latest*

// instance — which is what addBuffGlowPatch wants to glow when the

// caller has just appended a buff to one of the lists.

const latestBuffOccurrence = (charObj, name) => {

  const total = countInArrays(name,

    charObj?.buffList || [],

    charObj?.permanentBuffs || [],

    charObj?.debuffList || [],

    charObj?.permanentDebuffs || [],

  );

  return Math.max(0, total - 1);

};



const isBuffAutoApplied = (text) => {

  if (!text) return null;

  const t = text.trim();

  // Stat modifier: +2 Str, -1 Wis, etc

  const statMatch = t.match(/^[+-]\s*\d+\s*(str|dex|con|int|wis|cha|strength|dexterity|constitution|intelligence|wisdom|charisma)$/i);

  if (statMatch) return 'stat';

  // Skill modifier: +2 Survival, -1 Stealth

  const skillMatch = t.match(/^[+-]\s*\d+\s+(\w+(?:\s+\w+)?)$/i);

  if (skillMatch) {

    const skillName = skillMatch[1].trim();

    const knownSkills = Object.keys(SKILL_MAP || {});

    if (knownSkills.find(s => s.toLowerCase() === skillName.toLowerCase())) return 'skill';

  }

  // AC modifier: +1 AC, -2 AC

  if (/^[+-]\s*\d+\s*AC$/i.test(t)) return 'ac';

  // Advantage/disadvantage: adv STR, dis stealth, adv all checks

  if (/^(adv|dis)\s+.+$/i.test(t)) return 'adv';

  // 2026-05-10: Speed modifier — case-insensitive, accepts ms/MS, speed,
  // movespeed, "move speed". Examples: "+1 speed", "-5 Speed", "+10 ms",
  // "+1 movespeed", "+1 move speed". User reported the Bulwark Shield's
  // "-5 Speed" wasn't auto-applied because Speed wasn't in this list.
  if (/^[+-]\s*\d+\s*(ms|speed|movespeed|move\s+speed)$/i.test(t)) return 'speed';

  return null;

};



const getBuffAdvantage = (char, scope) => {

  if (!char) return { mode: 'normal', sources: [] };

  // 2026-04-29: pull through getEffectiveBuffs so disabled instances

  // don't contribute "adv X / dis X" — disable applies to advantage

  // sources just like to additive modifiers.

  const allEffects = getActiveLegacyEffectEntries(char);

  let result = 'normal';

  const sources = [];

  const scopeLower = (scope || '').toLowerCase();

  const parts = scopeLower.split('_'); // e.g. 'str_checks' -> ['str', 'checks']

  const scopeStat = parts[0]; // 'str', 'stealth', etc.

  const scopeType = parts.slice(1).join('_'); // 'checks', 'saves', ''

  

  for (const effect of allEffects) {

    const b = (effect.text || '').toLowerCase().trim();

    // Match patterns: "adv STR checks", "dis WIS saves", "adv attacks", "adv stealth", "adv all checks"

    const advMatch = b.match(/^(adv|dis)\s+(.+)$/);

    if (!advMatch) continue;

    const mode = advMatch[1] === 'adv' ? 'advantage' : 'disadvantage';

    const target = advMatch[2].trim();

    

    // Check if this buff applies to the given scope

    let matches = false;

    if (target === 'all checks' && scopeType === 'checks') matches = true;

    if (target === 'all saves' && scopeType === 'saves') matches = true;

    if (target === 'attacks' && scopeLower === 'attack') matches = true;

    if (target === 'attack rolls' && scopeLower === 'attack') matches = true;

    // Stat-specific: "adv STR" matches str_checks, "adv STR checks" matches str_checks, "adv STR saves" matches str_saves

    const targetParts = target.split(/\s+/);

    if (targetParts.length === 1) {

      if (targetParts[0] === scopeStat) matches = true;

    } else if (targetParts.length >= 2) {

      const tStat = targetParts[0];

      const tType = targetParts.slice(1).join(' ');

      if (tStat === scopeStat && (tType === scopeType || tType === scopeType.replace('_', ' '))) matches = true;

      if (tStat === scopeStat && tType === 'checks' && scopeType === 'checks') matches = true;

    }

    if (matches) { result = mode; sources.push(effect.text); }

  }

  return { mode: result, sources };

};



// Ensure equipped item buffs are always reflected in effective buff lists

// This handles items equipped before the auto-buff feature or data imported without buffList sync

const getEffectiveBuffs = (char) => {

  const permBuffs = char.permanentBuffs || [];

  const permDebuffs = char.permanentDebuffs || [];

  const buffList = [...(char.buffList || [])];

  const debuffList = [...(char.debuffList || [])];

  const buffSources = { ...(char.buffSources || {}) };

  const disabledBuffs = char.disabledBuffs || [];

  const disabledDebuffs = char.disabledDebuffs || [];

  // 2026-04-29: disabledBuffs entries are per-instance keys; collapse to

  // a name-set for the "skip re-add if any instance of this name is

  // disabled" check below.

  const disabledBuffNames = new Set(disabledBuffs.map(k => parseBuffInstanceKey(k).name));

  const disabledDebuffNames = new Set(disabledDebuffs.map(k => parseBuffInstanceKey(k).name));

  const removedItemBuffs = char.removedItemBuffs || {};



  (char.inventory || []).forEach(item => {

    if (!item.equipped || item.buffsApplyOnUse) return;

    const itemRemoved = removedItemBuffs[item.id] || [];



    // 2026-04-27: Always refresh buffSources from current item.name even if the

    // buff is already in buffList (otherwise renaming an item leaves stale

    // attribution from the original equip — the user has to unequip+rename+

    // re-equip to get the source updated). buffData per-instance source is

    // updated at equip; this pass keeps the legacy buffSources map fresh.

    (item.buffs || []).forEach(b => {

      if (itemRemoved.includes(b) || disabledBuffNames.has(b)) return;

      if (!buffList.includes(b)) buffList.push(b);

      buffSources[b] = item.name;

    });

    (item.debuffs || []).forEach(d => {

      if (itemRemoved.includes(d) || disabledDebuffNames.has(d)) return;

      if (!debuffList.includes(d)) debuffList.push(d);

      buffSources[d] = item.name;

    });

  });



  // 2026-04-29: filter out disabled instances before the math layer

  // sees them. Per-instance key uses the same getGlobalOccurrence

  // priority order the render uses, so a buff visually disabled in

  // StatusWindow is also skipped by calculateStats / skill / AC /

  // wardsave parsers — disable is no longer cosmetic-only.

  // Render still iterates raw char.buffList directly so disabled badges

  // remain visible (with line-through); only the math consumers read

  // the filtered output.

  return buildLegacyEffectCollections({

    buffList,

    debuffList,

    permBuffs,

    permDebuffs,

    buffData: char.buffData,

    buffSources,

    buffNotes: char.buffNotes,

    disabledBuffs,

    disabledDebuffs,

  });

};



const getActiveLegacyEffectEntries = (char, resolvedEffects = null) => {

  if (!char) return [];

  const eff = resolvedEffects || getEffectiveBuffs(char);

  return getCanonicalEffects({

    char,

    legacyState: {

      buffList: eff.buffList,

      debuffList: eff.debuffList,

      permBuffs: eff.permBuffs,

      permDebuffs: eff.permDebuffs,

      buffData: char.buffData,

      buffSources: eff.buffSources,

      buffNotes: char.buffNotes,

    },

    includeRuntime: false,

  }).filter((effect) => effect.storageSourceType === 'legacy' || effect.sourceType === 'legacy' || effect.sourceType === 'item');

};



const resolveSystemTemplateForViewer = (systemInstance, systems = {}, isDM = false) => {
  if (!isDM && systemInstance?.template) return systemInstance.template;
  return (systems && systemInstance?.systemId && systems[systemInstance.systemId])
    ? systems[systemInstance.systemId]
    : (systemInstance?.template || null);
};

const calculateStats = (base, buffs, debuffs, permBuffs, permDebuffs, diseaseList, diseases, buffSources = {}, buffNotes = {}, buffData = {}, systemList = [], systems = {}, isDM = false) => { 

  const stats = { ...(base || DEFAULT_STATS) }; 

  const modifiers = {}; // { stat: [{ effect, source, note }] }

  const legacyEffects = buildLegacyEffectEntries({

    buffList: buffs,

    debuffList: debuffs,

    permBuffs,

    permDebuffs,

    buffData,

    buffSources,

    buffNotes,

  });

  

  // Collect disease effects (optionally applied even when hidden)

  // Rules:

  // - active === false => never apply

  // - applyEffects === false => never apply

  // - if showEffects === true => apply

  // - else applyHiddenEffects === true => apply while hidden (default false for backwards compat)

  const diseaseEffects = [];

  (diseaseList || []).forEach(d => {

    const dis = (diseases && diseases[d.diseaseId]) ? diseases[d.diseaseId] : null;

    if (!dis) return;



    const activeFlag = d.active !== false;

    const applyEffectsFlag = d.applyEffects !== false; // default true

    const applyHidden = d.applyHiddenEffects === true; // default false

    const shouldApply = activeFlag && applyEffectsFlag && (d.showEffects || applyHidden);



    if (!shouldApply) return;



    const src = isDM ? dis.name : (d.showName ? dis.name : 'Unknown Disease');

    const note = (!d.showEffects && applyHidden) ? 'Disease (hidden)' : 'Disease';



    (dis.effects || []).forEach(eff => diseaseEffects.push({ effect: eff, source: src, note }));

  });



  // Collect system effects (curses/marks/omens/etc.) with the same apply rules

  const systemEffects = [];

  (systemList || []).forEach(sy => {

    const sys = resolveSystemTemplateForViewer(sy, systems, isDM);

    if (!sys) return;

    

    // Skip Note type systems - they have no mechanical effects

    if (sys.type === 'note') return;



    const activeFlag = sy.active !== false;

    const applyEffectsFlag = sy.applyEffects !== false; // default true

    const applyHidden = sy.applyHiddenEffects === true; // default false

    const effectOverrides = sy.effectOverrides || {}; // Per-effect settings

    const unknownText = sy.unknownText || UNKNOWN_PLACEHOLDER;



    if (!activeFlag || !applyEffectsFlag) return;



    const srcName = isDM

      ? (sys.name || sys.publicName || 'System')

      : (sy.showName ? (sys.publicName || sys.name || 'System') : (sy.placeholderName || unknownText));

    

    // Visible effects - check per-effect overrides

    if (sy.showEffects || applyHidden) {

      (sys.effects || []).forEach((eff, effIdx) => {

        const override = effectOverrides[`visible-${effIdx}`] || {};

        // Skip if explicitly disabled

        if (override.apply === false) return;

        

        const effText = typeof eff === 'string' ? eff : (eff.displayText || '');

        if (!effText) return;

        

        // Determine source display based on override

        let effectSource = srcName;

        let effectNote = sy.showEffects ? 'System' : 'System (hidden)';

        

        if (override.showSource === false) {

          // Mystery source - player doesn't know where it's from

          effectSource = isDM ? `${srcName} (hidden source)` : HIDDEN_LABEL_PLACEHOLDER;

          effectNote = 'Mystery effect';

        }

        

        systemEffects.push({ 

          effect: effText, 

          source: effectSource, 

          note: effectNote,

          isMystery: override.showSource === false && !isDM,

        });

      });

    }

    

    // Projected player payloads do not receive raw hiddenEffects or
    // applyHiddenEffects, but Convex can attach already-applied hidden effect
    // text for stat math with a mystery source.
    const projectedHiddenEffects = Array.isArray(sy._appliedHiddenEffects) ? sy._appliedHiddenEffects : null;

    if (projectedHiddenEffects) {

      projectedHiddenEffects.forEach(eff => {

        const effText = typeof eff === 'string' ? eff : (eff.displayText || '');

        if (!effText) return;

        systemEffects.push({

          effect: effText,

          source: HIDDEN_LABEL_PLACEHOLDER,

          note: 'Hidden effect',

          isMystery: !isDM,

        });

      });

    // Hidden effects - check per-effect overrides

    } else if (applyHidden && (sys.hiddenEffects || []).length > 0) {

      (sys.hiddenEffects || []).forEach((eff, effIdx) => {

        const override = effectOverrides[`hidden-${effIdx}`] || {};

        // Skip if explicitly disabled

        if (override.apply === false) return;

        

        const effText = typeof eff === 'string' ? eff : (eff.displayText || '');

        if (!effText) return;

        

        const hiddenSrc = isDM ? `${srcName} (hidden)` : HIDDEN_LABEL_PLACEHOLDER;

        systemEffects.push({ 

          effect: effText, 

          source: hiddenSrc, 

          note: 'Hidden effect',

          isMystery: !isDM,

        });

      });

    }

    

    // Tracker threshold effects - apply effects from active thresholds

    if ((sys.trackerFields || []).length > 0) {

      const trackerValues = sy.trackerValues || {};

      const trackerVisibility = sy.trackerVisibility || {};

      

      (sys.trackerFields || []).forEach(field => {

        const fieldVis = trackerVisibility[field.id] || {};

        const currentValue = trackerValues[field.id] ?? field.value ?? 0;

        const activeThresholds = (field.thresholds || [])

          .filter(t => currentValue >= t.value)

          .sort((a, b) => b.value - a.value);

        

        // Apply effects from all active thresholds

        activeThresholds.forEach((threshold, threshIdx) => {

          const override = effectOverrides[`threshold-${field.id}-${threshIdx}`] || {};

          // Skip if explicitly disabled

          if (override.apply === false) return;

          

          const thresholdEffects = threshold.effects || (threshold.effect ? [threshold.effect] : []);

          

          // Determine source based on visibility settings

          let thresholdSrc;

          if (isDM) {

            thresholdSrc = `${srcName} (${field.label}: ${threshold.label || threshold.value})`;

          } else if (override.showSource === false || fieldVis.showSource === false) {

            thresholdSrc = HIDDEN_LABEL_PLACEHOLDER;

          } else if (sy.showEffects) {

            thresholdSrc = `${srcName} (${field.label})`;

          } else {

            thresholdSrc = HIDDEN_LABEL_PLACEHOLDER;

          }

          

          thresholdEffects.forEach(eff => {

            const effText = typeof eff === 'string' ? eff : (eff.displayText || '');

            if (effText) {

              systemEffects.push({ 

                effect: effText, 

                source: thresholdSrc, 

                note: `Threshold: ${threshold.label || threshold.value}`,

                isMystery: thresholdSrc === HIDDEN_LABEL_PLACEHOLDER && !isDM,

              });

            }

          });

        });

      });

    }

    

    // Progression stage effects - apply effects from current stage

    const stageInfo = getCurrentStageInfo(sy, sys);

    if (stageInfo?.stage?.effects?.length > 0) {

      const stageOverride = effectOverrides[`stage-${stageInfo.index}`] || {};

      

      // Skip if stage effects are explicitly disabled

      if (stageOverride.apply !== false) {

        let stageSrc;

        if (isDM) {

          stageSrc = `${srcName} (${stageInfo.stage.name})`;

        } else if (stageOverride.showSource === false) {

          stageSrc = HIDDEN_LABEL_PLACEHOLDER;

        } else if (sy.showEffects) {

          stageSrc = `${srcName} (${stageInfo.stage.name})`;

        } else {

          stageSrc = HIDDEN_LABEL_PLACEHOLDER;

        }

      

        stageInfo.stage.effects.forEach(eff => {

          const effText = typeof eff === 'string' ? eff : (eff.displayText || '');

          if (effText) {

            systemEffects.push({ 

              effect: effText, 

              source: stageSrc, 

              note: `Stage ${stageInfo.index + 1}/${stageInfo.total}`,

              isMystery: stageSrc === HIDDEN_LABEL_PLACEHOLDER && !isDM,

            });

          }

        });

      }

    }

  });



  // Process buffs with sources

  const processEffect = (effect, source = 'Manual', note = 'Temporary') => {

    // Skip save-bonus phrasings — those are parsed by getSaveBonus, not here. Without this guard

    // strings like "+1 STR Saves" wrongly boost the Str ability score (and thus every Str-based

    // roll) because the regex below matches "+1 STR" at the start and ignores the "Saves" tail.

    if (/\bsaves?\b/i.test(effect)) return;

    const m = effect.match(/([+-])\s*(\d+)\s*(str|dex|con|int|wis|cha|strength|dexterity|constitution|intelligence|wisdom|charisma)/i);

    if (m) {

      const val = (m[1] === '+' ? 1 : -1) * parseInt(m[2]); 

      const map = { strength: 'str', dexterity: 'dex', constitution: 'con', intelligence: 'int', wisdom: 'wis', charisma: 'cha', str: 'str', dex: 'dex', con: 'con', int: 'int', wis: 'wis', cha: 'cha' }; 

      const stat = map[m[3].toLowerCase()]; 

      if (stat) { 

        stats[stat] += val; 

        if (!modifiers[stat]) modifiers[stat] = []; 

        let effectiveNote = note;

        if (/^Diet:/i.test(source) || /diet/i.test(source)) effectiveNote = 'Diet Reward';

        modifiers[stat].push({ 

          effect, 

          source,

          note: effectiveNote

        }); 

      }

    } 

  };

  

  legacyEffects.forEach(({ text, source, note }) => processEffect(text, source, note));

  diseaseEffects.forEach(({ effect, source, note }) => {

    if (/\bsaves?\b/i.test(effect)) return; // Same save-phrasing guard as processEffect above

    const m = effect.match(/([+-])\s*(\d+)\s*(str|dex|con|int|wis|cha)/i);

    if (m) {

      const val = (m[1] === '+' ? 1 : -1) * parseInt(m[2]);

      const map = { str: 'str', dex: 'dex', con: 'con', int: 'int', wis: 'wis', cha: 'cha' };

      const stat = map[m[3].toLowerCase()];

      if (stat) {

        stats[stat] += val;

        if (!modifiers[stat]) modifiers[stat] = [];

        modifiers[stat].push({ effect, source, note });

      }

    }

  });

  



  systemEffects.forEach(({ effect, source, note }) => {

    if (/\bsaves?\b/i.test(effect)) return; // Same save-phrasing guard as processEffect above

    const m = effect.match(/([+-])\s*(\d+)\s*(str|dex|con|int|wis|cha)/i);

    if (m) {

      const val = (m[1] === '+' ? 1 : -1) * parseInt(m[2]);

      const map = { str: 'str', dex: 'dex', con: 'con', int: 'int', wis: 'wis', cha: 'cha' };

      const stat = map[m[3].toLowerCase()];

      if (stat) {

        stats[stat] += val;

        if (!modifiers[stat]) modifiers[stat] = [];

        modifiers[stat].push({ effect, source, note });

      }

    }

  });



  // 2026-04-27: SET-stat overrides (e.g. "Set STR to 25" from belts/items). Highest wins,

  // matching 5e item rules where Belt of Storm Giant Strength doesn't stack with Hill Giant.

  // Runs after all additive modifiers so SET takes precedence and replaces the running total.

  const STAT_MAP = { strength: 'str', dexterity: 'dex', constitution: 'con', intelligence: 'int', wisdom: 'wis', charisma: 'cha', str: 'str', dex: 'dex', con: 'con', int: 'int', wis: 'wis', cha: 'cha' };

  const SET_RX = /\bset\s+(str|dex|con|int|wis|cha|strength|dexterity|constitution|intelligence|wisdom|charisma)\s+(?:to\s+)?(\d+)/i;

  const setWinners = {}; // { stat: { val, effect, source, note } }

  const considerSet = (effect, source, note) => {

    const m = effect.match(SET_RX);

    if (!m) return;

    const stat = STAT_MAP[m[1].toLowerCase()];

    const val = parseInt(m[2]);

    if (!stat || !Number.isFinite(val)) return;

    if (!setWinners[stat] || val > setWinners[stat].val) {

      setWinners[stat] = { val, effect, source, note };

    }

  };

  legacyEffects.forEach(({ text, source, note }) => considerSet(text, source, note));

  diseaseEffects.forEach(({ effect, source, note }) => considerSet(effect, source, note));

  systemEffects.forEach(({ effect, source, note }) => considerSet(effect, source, note));

  Object.entries(setWinners).forEach(([stat, { val, effect, source, note }]) => {

    stats[stat] = val;

    if (!modifiers[stat]) modifiers[stat] = [];

    modifiers[stat].push({ effect, source, note: `${note} (sets to ${val})` });

  });



  return { stats, modifiers };

};



// Calculate skill-specific buffs with sources (from buffList only - items add to buffList when equipped)
//
// 2026-05-11: Per-instance source attribution invariant. Each entry in
// `activeEffects` carries `entry.source` derived from the per-instance
// `buffData[text::N]` lookup in effectModel.buildLegacyEffectEntries.
// When two equipped items grant the same buff text (e.g., Phantom and
// Flamebrand both giving "+1 Str"), each occurrence has its own source.
// DO NOT replace `entry.source` with a text-keyed lookup like
// `char.buffSources[text]` — that would conflate duplicates and break
// the receipt for one of them. See CLAUDE-NOTES-GENERAL "Per-instance
// buff-source attribution" + statusViewModel test "attributes
// duplicate-text buffs to the right item".

const calculateSkillBuffs = (char, inventory) => {

  const skillBuffs = {}; // { skillName: [{ value, source, note }] }

  // 2026-04-29: eff.permBuffs/permDebuffs are the active (non-disabled)

  // perm lists from getEffectiveBuffs — using them here means disable

  // suppresses skill modifiers, not just the visual line-through.

  const eff = getEffectiveBuffs(char);

  const activeEffects = getActiveLegacyEffectEntries(char, eff);

  

  // Get list of equipped item names to identify "Equipped" vs "Temporary"

  const equippedItemNames = new Set((inventory || []).filter(i => i.equipped).map(i => i.name));

  

  // Process character buffs for skill-specific modifiers

  activeEffects.forEach((effectEntry) => {

    const buff = effectEntry.text;

    // Match patterns like "+2 Survival", "+1 Perception", "-1 Stealth"

    const skillMatch = buff.match(/([+-])\s*(\d+)\s+(\w+(?:\s+\w+)?)/i);

    if (skillMatch) {

      const value = (skillMatch[1] === '+' ? 1 : -1) * parseInt(skillMatch[2]);

      const skillName = skillMatch[3].trim();

      

      // Check if this matches a known skill (case insensitive)

      const knownSkills = Object.keys(SKILL_MAP);

      const matchedSkill = knownSkills.find(s => s.toLowerCase() === skillName.toLowerCase());

      

      if (matchedSkill) {

        if (!skillBuffs[matchedSkill]) skillBuffs[matchedSkill] = [];

        const isPerm = effectEntry.permanence === 'permanent';

        const source = effectEntry.source || 'Manual';

        // Determine note based on source and permanence

        let note = effectEntry.note || (isPerm ? 'Permanent' : 'Temporary');

        if (equippedItemNames.has(source)) {

          note = 'Equipped';

        } else if (/^Diet:/i.test(source) || /diet/i.test(source)) {

          note = 'Diet Reward';

        }

        skillBuffs[matchedSkill].push({ value, source, note, isPermanent: isPerm });

      }

    }

  });

  

  return skillBuffs;

};



// Calculate AC bonuses with sources (from buffList only - items add to buffList when equipped)

// 2026-05-10: Speed buffs/debuffs from active effects. Recognizes any
// of: speed, ms, MS, movespeed, "move speed" (case-insensitive). Used by
// the SPEED display to (a) compute the effective speed total and (b)
// surface a per-source breakdown in the hover tooltip.
const calculateSpeedBuffs = (char, inventory) => {
  const speedBuffs = []; // [{ value, source, note }]
  const eff = getEffectiveBuffs(char);
  // Speed can come from BOTH buffs (movement boots) and debuffs (heavy
  // shields, exhaustion). Walk both arrays and treat their signs as-is.
  const buffEntries = getActiveLegacyEffectEntries(char, eff).filter((e) => e.category === 'buff');
  const debuffEntries = getActiveLegacyEffectEntries(char, eff).filter((e) => e.category === 'debuff');
  const equippedItemNames = new Set((inventory || []).filter((i) => i.equipped).map((i) => i.name));
  const SPEED_RE = /([+-])\s*(\d+)\s*(ms|speed|movespeed|move\s+speed)\b/i;
  const harvest = (entries) => entries.forEach((entry) => {
    const text = entry.text || '';
    const m = text.match(SPEED_RE);
    if (!m) return;
    const value = (m[1] === '+' ? 1 : -1) * parseInt(m[2], 10);
    if (!Number.isFinite(value) || value === 0) return;
    const source = entry.source || 'Manual';
    let note = entry.note || (entry.permanence === 'permanent' ? 'Permanent' : 'Temporary');
    if (equippedItemNames.has(source)) note = 'Equipped';
    speedBuffs.push({ value, source, note });
  });
  harvest(buffEntries);
  harvest(debuffEntries);
  return speedBuffs;
};

// 2026-05-11: See per-instance source-attribution invariant comment
// above calculateSkillBuffs. Same rule applies: `entry.source` here
// is per-instance via buildLegacyEffectEntries; do not replace with
// text-keyed lookups.
const calculateACBuffs = (char, inventory) => {

  const acBuffs = []; // [{ value, source, note }]

  const eff = getEffectiveBuffs(char);

  const activeEffects = getActiveLegacyEffectEntries(char, eff).filter((effect) => effect.category === 'buff');

  

  // Get list of equipped item names to identify "Equipped" vs "Temporary"

  const equippedItemNames = new Set((inventory || []).filter(i => i.equipped).map(i => i.name));

  

  // Process character buffs for AC

  activeEffects.forEach((effectEntry) => {

    const buff = effectEntry.text;

    const acMatch = buff.match(/([+-])\s*(\d+)\s*AC/i);

    if (acMatch) {

      const value = (acMatch[1] === '+' ? 1 : -1) * parseInt(acMatch[2]);

      const source = effectEntry.source || 'Manual';

      let note = effectEntry.note || (effectEntry.permanence === 'permanent' ? 'Permanent' : 'Temporary');

      if (equippedItemNames.has(source)) {

        note = 'Equipped';

      }

      acBuffs.push({ value, source, note });

    }

  });



  return acBuffs;

};



// 2026-04-27: Effective wardsave from char buffs/debuffs. Mirrors the parsing in

// calculateFinalDamage so the char sheet shows the same number that combat applies:

// highest +N% wins (Warhammer-style; ward saves don't stack); -N% subtracts.

const calculateWardsave = (char) => {

  const activeEffects = getActiveLegacyEffectEntries(char);

  return getResolvedWardsaveContext({ activeLegacyEffects: activeEffects });

};



const getResolvedCharacterEffectContext = (char, diseases = {}, systems = {}, isDM = false) => {

  if (!char) return null;

  const effectiveBuffs = getEffectiveBuffs(char);

  const activeLegacyEffects = getActiveLegacyEffectEntries(char, effectiveBuffs);

  const statContext = calculateStats(

    char.baseStats,

    effectiveBuffs.buffList,

    effectiveBuffs.debuffList,

    effectiveBuffs.permBuffs,

    effectiveBuffs.permDebuffs,

    char.diseaseList,

    diseases,

    effectiveBuffs.buffSources,

    char.buffNotes,

    char.buffData,

    char.systemList,

    systems,

    isDM,

  );

  return {

    effectiveBuffs,

    activeLegacyEffects,

    effectiveStats: statContext.stats,

    modifiers: statContext.modifiers,

    equippedCombatBonuses: getEquippedCombatBonusesFromInventory(char.inventory),

  };

};



const getResolvedCharacterArmorContext = (char, diseases = {}, systems = {}, isDM = false, armorTypes = DEFAULT_ARMOR_TYPES) => {

  if (!char) return null;

  const effectContext = getResolvedCharacterEffectContext(char, diseases, systems, isDM);

  return getResolvedArmorClassContext({

    char,

    effectiveStats: effectContext?.effectiveStats,

    equippedCombatBonuses: effectContext?.equippedCombatBonuses,

    activeLegacyEffects: effectContext?.activeLegacyEffects,

    activeRuntimeEffects: Array.isArray(char.activeEffects) ? char.activeEffects : [],

    armorTypes,

    customArmorTypes: ARMOR_TYPES_DURA,

  });

};



const getResolvedCharacterAdvantageContext = (

  char,

  {

    manualMode = 'normal',

    itemScopes = [],
    supremeItemScopes = [],

    legacyScopes = [],

    runtimeScopes = [],

  } = {},

  diseases = {},

  systems = {},

  isDM = false,

) => {

  if (!char) return { mode: manualMode || 'normal', sources: [] };

  const effectContext = getResolvedCharacterEffectContext(char, diseases, systems, isDM);

  return getResolvedAdvantageContext({

    manualMode,

    equippedCombatBonuses: effectContext?.equippedCombatBonuses,

    activeLegacyEffects: effectContext?.activeLegacyEffects,

    activeRuntimeEffects: Array.isArray(char.activeEffects) ? char.activeEffects : [],

    itemScopes,
    supremeItemScopes,

    legacyScopes,

    runtimeScopes: runtimeScopes.length > 0 ? runtimeScopes : legacyScopes,

  });

};



const getResolvedSaveBreakdownParts = (stat, context) => {

  if (!context || context.isEnemy) return [];

  const parts = [

    {

      type: 'stat',

      value: context.abilityMod,

      source: `${stat.toUpperCase()} mod`,

      details: `Score: ${context.statScore ?? 10}`,

    },

  ];

  if (context.proficiencyBonus) parts.push({ type: 'prof', value: context.proficiencyBonus, source: 'Proficiency' });

  context.itemSources.forEach((entry) => {

    parts.push({

      type: 'equip',

      value: entry.value,

      source: entry.source,

      details: entry.label || 'Save bonus',

    });

  });

  context.textSources.forEach((entry) => {

    parts.push({

      type: 'buff',

      value: entry.value,

      source: entry.source,

      details: entry.label || 'Save bonus',

    });

  });

  context.runtimeFlatSources.forEach((entry) => {

    parts.push({

      type: 'effect',

      value: entry.value,

      source: entry.source,

      details: entry.label || 'Runtime effect',

    });

  });

  return parts;

};



const getResolvedSaveTooltipLines = (context) => {

  if (!context) return [];

  if (context.isEnemy) return [`Total: ${context.totalBonus >= 0 ? '+' : ''}${context.totalBonus}`];

  const lines = getResolvedSaveBreakdownParts(context.stat, context)

    .map((part) => `${part.value >= 0 ? '+' : ''}${part.value} ${part.source}${part.details ? ` (${part.details})` : ''}`);

  context.diceBonuses.forEach((entry) => lines.push(`${entry.dice} ${entry.source}`));

  lines.push(`Total: ${context.totalBonus >= 0 ? '+' : ''}${context.totalBonus}`);

  return lines;

};



const resolveSaveBonusDiceRoll = (saveContext) => {

  const bonusDiceRolls = (saveContext?.diceBonuses || []).map((bonus) => {

    const roll = rollDice(bonus.dice);

    return { ...bonus, total: roll.total };

  });

  const bonusDiceTotal = bonusDiceRolls.reduce((sum, bonus) => sum + bonus.total, 0);

  const breakdownParts = bonusDiceRolls.map((bonus) => ({

    type: 'effect',

    value: bonus.total,

    source: bonus.source,

    details: bonus.label || bonus.dice,

  }));

  return {

    bonusDiceRolls,

    bonusDiceTotal,

    breakdownParts,

    bonusDiceLabel: bonusDiceRolls.map((bonus) => `${bonus.total}(${bonus.source})`).join('+'),

  };

};



const getHpColor = (c, m) => (c / m) > 0.5 ? 'linear-gradient(90deg, #148b3d, #27c95a)' : (c / m) > 0.25 ? 'linear-gradient(90deg, #c9a227, #e8c547)' : 'linear-gradient(90deg, #8b1414, #c92727)';

const getManaColor = () => 'linear-gradient(90deg, #1a4a8b, #4a90d9)';

const getStarvColor = (d, t) => d >= t - 2 ? '#27c95a' : d >= 1 ? '#c9a227' : '#c92727';



const Tooltip = ({ text, children, maxWidth = 600, special = false, borderColor = null }) => {

  const [show, setShow] = useState(false);

  const wrapRef = useRef(null);

  const tipRef = useRef(null);

  const [flipped, setFlipped] = useState(false);



  // Support: string | string[] | React node

  const normalizedText =

    typeof text === 'string'

      ? text

      : Array.isArray(text)

        ? text.filter(t => t !== undefined && t !== null).map(t => String(t)).join('\n')

        : null;



  const content = normalizedText !== null ? normalizedText : text;

  const isReactNode = normalizedText === null && text != null;



  let calculatedWidth = 'auto';

  if (normalizedText !== null) {

    const lines = normalizedText.split('\n');

    const longestLine = Math.max(0, ...lines.map(l => (l || '').length));

    const charWidth = 8;

    const calcWidth = Math.min(Math.max(longestLine * charWidth + 40, 100), maxWidth);

    calculatedWidth = calcWidth;

  }



  // Check if tooltip would go above viewport - if so, flip below

  useEffect(() => {

    if (show && wrapRef.current && tipRef.current) {

      const wrapRect = wrapRef.current.getBoundingClientRect();

      const tipRect = tipRef.current.getBoundingClientRect();

      setFlipped(wrapRect.top - tipRect.height - 4 < 8);

    }

  }, [show]);



  return (

    <span

      ref={wrapRef}

      style={{ position: 'relative', display: 'inline-block' }}

      onMouseEnter={() => setShow(true)}

      onMouseLeave={() => { setShow(false); setFlipped(false); }}

    >

      {children}

      {show && content ? (

        <div

          ref={tipRef}

          style={{

            position: 'absolute',

            [flipped ? 'top' : 'bottom']: '100%',

            left: '50%',

            transform: 'translateX(-50%)',

            [flipped ? 'marginTop' : 'marginBottom']: '4px',

            background: 'rgba(20,15,10,0.98)',

            border: `1px solid ${borderColor || '#c9a227'}`,

            borderRadius: '6px',

            padding: '8px 12px',

            fontSize: S.tooltip?.fontSize || '0.75rem',

            whiteSpace: normalizedText !== null ? 'pre-wrap' : 'normal',

            zIndex: 100000,

            textAlign: 'left',

            boxShadow: `0 4px 12px rgba(0,0,0,0.4)${special && borderColor ? `, 0 0 6px ${borderColor}40` : ''}`,

            width: isReactNode ? 'auto' : calculatedWidth,

            minWidth: isReactNode ? '160px' : undefined,

            maxWidth: Math.min(maxWidth, window.innerWidth - 16),

            pointerEvents: 'none',

          }}

        >

          {content}

        </div>

      ) : null}

    </span>

  );

};



// Input that stores comma-separated arrays — edits raw string, commits array on blur

const CommaSeparatedInput = CommaListField;



// Privacy-aware text editor — replaces raw textarea for fields with PRIVATE markers

export const PrivacyEditor = ({ value, onChange, placeholder, minHeight = '60px', charId, chars, isDM }) => {

  const lastAssembled = useRef('');

  const buildSegId = (type, ownerId, ordinal) => `${type}:${ownerId || 'public'}:${ordinal}`;



  const parseSegs = (text) => {
    if (!text) return [{ type: 'public', text: '', ownerId: null, id: buildSegId('public', null, 0) }];

    const segs = [];

    let lastIdx = 0;

    const re = /\{\{PRIVATE:([^}]+)\}\}([\s\S]*?)\{\{\/PRIVATE\}\}/g;

    let m;

    while ((m = re.exec(text)) !== null) {

      if (m.index > lastIdx) segs.push({ type: 'public', text: text.substring(lastIdx, m.index), ownerId: null });

      segs.push({ type: 'private', text: m[2], ownerId: m[1] });

      lastIdx = m.index + m[0].length;

    }

    if (lastIdx < text.length) segs.push({ type: 'public', text: text.substring(lastIdx), ownerId: null });

    if (segs.length === 0) segs.push({ type: 'public', text: '', ownerId: null });

    return segs.map((seg, ordinal) => ({ ...seg, id: buildSegId(seg.type, seg.ownerId, ordinal) }));

  };

  

  const assemble = (segs) => segs.map(s => 

    s.type === 'private' && s.ownerId ? `{{PRIVATE:${s.ownerId}}}${s.text}{{/PRIVATE}}` : s.text

  ).join('');

  

  const [segs, setSegs] = useState(() => { const s = parseSegs(value); lastAssembled.current = assemble(s); return s; });

  

  // Re-parse only if external value changed (not from our own onChange)

  useEffect(() => {

    if (value !== lastAssembled.current) {

      const s = parseSegs(value);

      lastAssembled.current = assemble(s);

      setSegs(s);

    }

  }, [value]);

  

  const push = (newSegs) => {

    const merged = [];

    for (const s of newSegs) {

      const p = merged[merged.length - 1];

      if (p && p.type === s.type && p.ownerId === s.ownerId) {

        merged[merged.length - 1] = { ...p, text: p.text + s.text };

      } else {

        merged.push(s);

      }

    }

    if (merged.length === 0) merged.push({ type: 'public', text: '', ownerId: null, id: buildSegId('public', null, 0) });

    setSegs(merged);

    const assembled = assemble(merged);

    lastAssembled.current = assembled;

    onChange(assembled);

  };

  

  const updateText = (i, text) => {

    const n = [...segs]; n[i] = { ...n[i], text };

    setSegs(n);

    const assembled = assemble(n);

    lastAssembled.current = assembled;

    onChange(assembled);

  };

  

  const togglePrivacy = (i) => {

    const s = segs[i];

    const n = [...segs];

    n[i] = s.type === 'public'

      ? { ...s, type: 'private', ownerId: charId }

      : { ...s, type: 'public', ownerId: null };

    push(n);

  };

  

  const removeBlock = (i) => {

    if (segs.length <= 1) return;

    push(segs.filter((_, j) => j !== i));

  };

  

  return (

    <div>

      {segs.map((seg, i) => {

        const isPriv = seg.type === 'private';

        const noDM = seg.ownerId?.includes('!noDM');

        const clean = (seg.ownerId || '').replace('!noDM', '').replace(/,\s*$/, '');

        const shared = clean ? clean.split(',').map(s => s.trim()).filter(id => id !== charId) : [];

        const sharedNames = shared.map(id => (chars || {})[id]?.name || id);

        

        let label;

        if (!isPriv) label = '👁 Everyone';

        else if (sharedNames.length > 0) label = `You${!noDM && !isDM ? ', DM' : ''}${sharedNames.length > 0 ? ', ' + sharedNames.join(', ') : ''}`;

        else label = noDM ? 'Only you' : (isDM ? 'Only you' : 'You + DM');

        

        return (

          <React.Fragment key={seg.id}>

            <div style={{ display: 'flex' }}>

              <div

                onClick={() => togglePrivacy(i)}

                style={{

                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',

                  gap: '1px', padding: '4px 6px', cursor: 'pointer', userSelect: 'none',

                  background: isPriv ? 'rgba(138,79,255,0.15)' : 'rgba(201,162,39,0.08)',

                  borderLeft: `2px solid ${isPriv ? '#8a4fff' : 'rgba(201,162,39,0.3)'}`,

                  borderTop: i === 0 ? `1px solid ${isPriv ? 'rgba(138,79,255,0.3)' : 'rgba(201,162,39,0.2)'}` : 'none',

                  borderBottom: `1px solid ${isPriv ? 'rgba(138,79,255,0.3)' : 'rgba(201,162,39,0.2)'}`,

                  borderRadius: i === 0 ? '6px 0 0 0' : '0',

                  borderBottomLeftRadius: i === segs.length - 1 ? '6px' : '0',

                  minWidth: '46px', transition: 'all 0.15s',

                }}

                title={`Click to make ${isPriv ? 'public' : 'private'}`}

              >

                <span style={{ fontSize: '0.85rem' }}>{isPriv ? '🔒' : '🔓'}</span>

                <span style={{ fontSize: '0.4rem', color: isPriv ? '#c4a8ff' : '#999', textAlign: 'center', lineHeight: 1.1 }}>{label}</span>

              </div>

              <textarea

                value={seg.text}

                onChange={e => updateText(i, e.target.value)}

                onKeyDown={e => {

                  if (e.key === 'Backspace' && seg.text === '' && segs.length > 1) {

                    e.preventDefault();

                    removeBlock(i);

                  }

                }}

                placeholder={isPriv ? 'Private text...' : (i === 0 ? (placeholder || 'Text...') : 'Continue...')}

                style={{

                  ...S.input, flex: 1, minHeight, marginBottom: 0, resize: 'vertical',

                  borderRadius: i === 0 ? '0 6px 0 0' : '0',

                  borderBottomRightRadius: i === segs.length - 1 ? '6px' : '0',

                  borderTop: i > 0 ? 'none' : (isPriv ? '1px solid rgba(138,79,255,0.3)' : '1px solid rgba(201,162,39,0.2)'),

                  borderRight: isPriv ? '1px solid rgba(138,79,255,0.3)' : '1px solid rgba(201,162,39,0.2)',

                  borderBottom: isPriv ? '1px solid rgba(138,79,255,0.3)' : '1px solid rgba(201,162,39,0.2)',

                  borderLeft: 'none',

                  background: isPriv ? 'rgba(138,79,255,0.06)' : 'rgba(18,14,10,0.85)',

                  color: isPriv ? '#c4a8ff' : '#e8d5b7',

                }}

              />

            </div>

          </React.Fragment>

        );

      })}

    </div>

  );

};



const isAutomaticStatusSource = (sourceType, sourceMode) => {
  if (sourceType === 'item') return true;
  const mode = String(sourceMode || '').toLowerCase();
  return mode.startsWith('item-')
    || mode === 'equip'
    || mode === 'consume'
    || mode === 'diet'
    || mode === 'spell'
    || mode === 'disease';
};

const BuffBadge = ({ text, source, sourceType, sourceMode, sourceLabel, playerTooltip, note, isPermanent, isDebuff, onClick, onXClick, showActions, onRemove, onHide, isDisabled, linkedItemName, colorId, glowColor, onClearGlow, isPlayer, noteVisible, isHidden, isPending }) => {

  const baseStyle = isDebuff ? (isPermanent ? S.badgeDebuffPerm : S.badgeDebuff) : (isPermanent ? S.badgeBuffPerm : S.badgeBuff); 

  const disabledStyle = isDisabled ? { opacity: 0.4, textDecoration: 'line-through' } : {};

  const hiddenStyle = isHidden ? { opacity: 0.5, fontStyle: 'italic' } : {};

  const autoType = isBuffAutoApplied(text);
  const showManualSource = !!source && !isAutomaticStatusSource(sourceType, sourceMode);
  const showPlayerSource = showManualSource && noteVisible === true;
  const showAutomaticSource = !!sourceLabel && sourceLabel !== linkedItemName;

  const autoStyle = autoType ? { boxShadow: `inset 0 0 0 1px ${isDebuff ? 'rgba(201,39,39,0.5)' : 'rgba(39,201,90,0.5)'}` } : {};

  // 2026-04-27: detect non-hookable, time-limited buffs (e.g.,

  // "Invisible (1 minute)", "Hasted for 1 round"). The engine doesn't

  // mechanically apply these; they're player/DM-tracked statuses. Render

  // with a violet treatment + clock icon so it's visually obvious they

  // expire on their own and aren't being computed into rolls. Skip when

  // a custom colorId is set (DM intent overrides default styling).

  const timedDurationMatch = !autoType && /\b\d+\s*(minute|min|mins|round|turn|hour|hr|hrs|second|sec|secs)s?\b/i.test(text || '');

  

  // Detect advantage/disadvantage buffs

  const textLower = (text || '').toLowerCase().trim();

  const isAdvBuff = textLower.startsWith('adv ');

  const isDisBuff = textLower.startsWith('dis ');

  

  // Apply custom color if provided - use textColor for readable text, color for border

  const colorOpt = colorId ? BUFF_COLOR_OPTIONS.find(c => c.id === colorId) : null;

  const colorStyle = colorOpt ? {

    background: colorOpt.bgColor,

    borderColor: colorOpt.color,

    color: colorOpt.textColor || colorOpt.color,

    fontWeight: '500',

  } : (isAdvBuff ? {

    background: 'rgba(39,201,90,0.15)',

    borderColor: '#27c95a',

    color: '#6cff8a',

  } : isDisBuff ? {

    background: 'rgba(231,76,60,0.15)',

    borderColor: '#e74c3c',

    color: '#ff6b6b',

  } : timedDurationMatch ? {

    background: 'linear-gradient(120deg, rgba(138,79,255,0.2), rgba(74,144,217,0.16))',

    borderColor: '#a370ff',

    color: '#d4baff',

    fontStyle: 'italic',

    fontWeight: '500',

  } : {});

  

  // Determine tooltip border color: glow color > custom color > default debuff/buff color

  const tooltipBorderColor = glowColor || (colorOpt ? colorOpt.color : (isDebuff ? '#c92727' : '#27c95a'));

  

  // Build tooltip content with color coding

  const buildTooltip = () => {

    // For players, show linkedItemName too

    const playerHasContent = playerTooltip || showPlayerSource || showAutomaticSource || linkedItemName || autoType;

    // For DM, show everything

    const dmHasContent = playerTooltip || showManualSource || showAutomaticSource || isDisabled || linkedItemName || autoType;

    

    const hasContent = isPlayer ? playerHasContent : dmHasContent;

    

    if (!hasContent) {

      return text; // Simple string fallback

    }

    

    // Build React node with colors

    return (

      <div style={{ fontSize: '0.7rem', minWidth: '200px' }}>

        {isPlayer ? (

          // Player view

          <>

            {autoType && (

              <div style={{ color: '#8b8bff', fontSize: '0.65rem', marginBottom: '4px', whiteSpace: 'nowrap' }}>

                ⚙ {autoType === 'stat' ? 'Auto-applied to stats' : autoType === 'skill' ? 'Auto-applied to skill checks' : autoType === 'ac' ? 'Auto-applied to AC' : 'Auto-applied adv/dis'}

              </div>

            )}

            {playerTooltip && (

              <div style={{ color: '#e8d5b7' }}>{playerTooltip}</div>

            )}

            {showPlayerSource && (

              <div style={{ color: '#fbbf24', marginTop: playerTooltip ? '4px' : 0 }}>

                📝 {source}

              </div>

            )}

            {showAutomaticSource && (

              <div style={{ color: '#fbbf24', marginTop: playerTooltip || showPlayerSource ? '4px' : 0, whiteSpace: 'nowrap' }}>

                From {sourceLabel}

              </div>

            )}

            {linkedItemName && (

              <div style={{ color: '#f59e0b', marginTop: '4px', whiteSpace: 'nowrap' }}>⚠️ {linkedItemName}</div>

            )}

          </>

        ) : (

          // DM view

          <>

            {autoType && (

              <div style={{ color: '#8b8bff', fontSize: '0.65rem', marginBottom: '4px', whiteSpace: 'nowrap' }}>

                ⚙ {autoType === 'stat' ? 'Auto-applied to stats' : autoType === 'skill' ? 'Auto-applied to skill checks' : autoType === 'ac' ? 'Auto-applied to AC' : 'Auto-applied adv/dis'}

              </div>

            )}

            {playerTooltip && (

              <div style={{ color: '#4ade80', marginBottom: source ? '4px' : 0 }}>

                👁 {playerTooltip}

              </div>

            )}

            {showManualSource && (

              <div style={{ color: noteVisible ? '#fbbf24' : '#888' }}>

                {noteVisible ? '📝' : '🔒'} {source}

                <span style={{ fontSize: '0.6rem', display: 'block', opacity: 0.7, marginTop: '2px' }}>

                  {noteVisible ? '(visible to player)' : '(hidden from player)'}

                </span>

              </div>

            )}

            {isDisabled && <div style={{ color: '#f59e0b', marginTop: '4px', whiteSpace: 'nowrap' }}>⚠️ Disabled</div>}

            {showAutomaticSource && (

              <div style={{ color: '#fbbf24', marginTop: showManualSource ? '4px' : 0, whiteSpace: 'nowrap' }}>

                From {sourceLabel}

              </div>

            )}

            {linkedItemName && (

              <div style={{ color: '#f59e0b', marginTop: '4px', whiteSpace: 'nowrap' }}>⚠️ {linkedItemName}</div>

            )}

          </>

        )}

      </div>

    );

  };

  

  const tipContent = buildTooltip();

  

  // Determine if tooltip should have special styling (based on visible content)

  const hasSpecialContent = isPlayer 

    ? !!(playerTooltip || showPlayerSource || showAutomaticSource || linkedItemName)

    : !!(playerTooltip || showManualSource || showAutomaticSource || linkedItemName || isDisabled);

  

  // Glow effect - more prominent with larger spread and brighter colors

  const glowStyle = glowColor ? {

    boxShadow: `0 0 8px ${glowColor}, 0 0 16px ${glowColor}, 0 0 24px ${glowColor}88, inset 0 0 4px ${glowColor}44`,

    animation: 'buffBadgeGlow 1.2s ease-in-out infinite',

    borderColor: glowColor,

  } : {};

  

  const handleMouseEnter = glowColor && onClearGlow ? onClearGlow : undefined;

  const canAct = !isPending && (onXClick || onClick);

  

  // Merge autoStyle and glowStyle boxShadows

  const combinedShadows = [autoStyle.boxShadow, glowStyle.boxShadow].filter(Boolean).join(', ');

  const mergedStyle = { ...autoStyle, ...glowStyle, ...(combinedShadows ? { boxShadow: combinedShadows } : {}) };

  

  // Auto-applied label for tooltip

  const autoLabel = autoType === 'stat' ? '⚙ Auto-applied to stats' : autoType === 'skill' ? '⚙ Auto-applied to skill checks' : autoType === 'ac' ? '⚙ Auto-applied to AC' : autoType === 'adv' ? '⚙ Auto-applied advantage/disadvantage' : null;

  

  return <>

    {glowColor && (

      <style>{`

        @keyframes buffBadgeGlow {

          0%, 100% { 

            box-shadow: 0 0 6px ${glowColor}, 0 0 12px ${glowColor}, 0 0 20px ${glowColor}66; 

            transform: scale(1);

          }

          50% { 

            box-shadow: 0 0 12px ${glowColor}, 0 0 24px ${glowColor}, 0 0 36px ${glowColor}aa; 

            transform: scale(1.03);

          }

        }

      `}</style>

    )}

    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>

      <Tooltip text={tipContent} special={hasSpecialContent} borderColor={tooltipBorderColor}>

        <span 

          style={{ ...S.badge, ...baseStyle, ...colorStyle, ...disabledStyle, ...hiddenStyle, ...mergedStyle, cursor: 'default', display: 'inline-flex', alignItems: 'center', gap: '3px' }} 

          onMouseEnter={handleMouseEnter}

        >

          {autoType && <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#2d1b69', display: 'inline-block', flexShrink: 0 }} title={autoLabel} />}

          {timedDurationMatch && !autoType && <span style={{ fontSize: '0.7em', flexShrink: 0, opacity: 0.85 }} title="Player/DM-tracked status — not auto-applied">⏳</span>}

          {text}

          {canAct && !showActions && (

            <span 

              onClick={e => { e.stopPropagation(); if (!isPending) (onXClick || onClick)?.(); }}

              style={{ cursor: isPending ? 'wait' : 'pointer', opacity: isPending ? 0.35 : 0.6, fontSize: '0.7em', marginLeft: '1px', lineHeight: 1 }}

              title="Options"

            >✕</span>

          )}

        </span>

      </Tooltip>

      {showActions && (

        <span style={{ 

          position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', 

          zIndex: 30, display: 'flex', gap: '2px', marginTop: '2px', whiteSpace: 'nowrap',

          background: 'rgba(30,22,15,0.98)', border: '1px solid rgba(255,255,255,0.15)', 

          borderRadius: '4px', padding: '3px 4px', boxShadow: '0 3px 10px rgba(0,0,0,0.5)'

        }}>

          <button disabled={isPending} onClick={e => { e.stopPropagation(); if (!isPending) onRemove?.(); }} style={{ background: 'rgba(201,39,39,0.2)', border: '1px solid rgba(201,39,39,0.4)', color: '#ff7d7d', borderRadius: '3px', padding: '2px 6px', fontSize: '0.58rem', cursor: isPending ? 'wait' : 'pointer', whiteSpace: 'nowrap', opacity: isPending ? 0.55 : 1 }}>🗑️ Remove</button>

          <button disabled={isPending} onClick={e => { e.stopPropagation(); if (!isPending) onHide?.(); }} style={{ background: 'rgba(100,100,100,0.2)', border: '1px solid rgba(100,100,100,0.4)', color: '#aaa', borderRadius: '3px', padding: '2px 6px', fontSize: '0.58rem', cursor: isPending ? 'wait' : 'pointer', whiteSpace: 'nowrap', opacity: isPending ? 0.55 : 1 }}>👁 Hide</button>

        </span>

      )}

    </span>

  </>; 

};

// Disease badge with granular visibility

export const DiseaseBadge = ({ diseaseData, disease, isPlayer, onClick }) => {

  if (!diseaseData || !disease) return null;

  

  // Check if anything is visible to player

  const hasAnyVisible = diseaseData.showName || (diseaseData.visibleSymptoms || []).length > 0 || diseaseData.showEffects || diseaseData.showCure;

  

  // If nothing visible and player, show unknown affliction

  if (isPlayer && !hasAnyVisible) {

    return <Tooltip text="You feel unwell...">

      <span style={{ ...S.badge, ...S.badgeUnknown, cursor: 'help' }}>? Unknown Affliction</span>

    </Tooltip>;

  }

  

  let displayText = diseaseData.showName ? disease.name : HIDDEN_LABEL_PLACEHOLDER;

  let tooltipLines = [];

  

  if (isPlayer) {

    if (diseaseData.showName) tooltipLines.push(disease.name);

    else tooltipLines.push('Unknown Affliction');

    

    const visibleSymps = (diseaseData.visibleSymptoms || []).map(idx => disease.symptoms?.[idx]).filter(Boolean);

    if (visibleSymps.length > 0) tooltipLines.push(`Symptoms: ${visibleSymps.join(', ')}`);

    

    if (diseaseData.showEffects && disease.effects?.length > 0) tooltipLines.push(`Effects: ${disease.effects.join(', ')}`);

    if (diseaseData.showCure && disease.cures?.length > 0) tooltipLines.push(`Cure: ${disease.cures.map(c => `${c.quantity}x ${c.item}`).join(', ')}`);

  } else {

    // DM sees everything plus current visibility state

    tooltipLines.push(disease.name);

    tooltipLines.push(`Symptoms: ${disease.symptoms?.join(', ') || 'None'}`);

    tooltipLines.push(`Effects: ${disease.effects?.join(', ') || 'None'}`);

    tooltipLines.push(`Cure: ${disease.cures?.map(c => `${c.quantity}x ${c.item}`).join(', ') || 'Unknown'}`);

    tooltipLines.push('');

    tooltipLines.push('[Player sees:]');

    tooltipLines.push(`Name: ${diseaseData.showName ? '✓' : '✗'}`);

    const vs = (diseaseData.visibleSymptoms || []).map(i => disease.symptoms?.[i]).filter(Boolean);

    tooltipLines.push(`Symptoms: ${vs.length > 0 ? vs.join(', ') : 'hidden'}`);

    tooltipLines.push(`Effects: ${diseaseData.showEffects ? '✓' : '✗'}`);

    tooltipLines.push(`Cure: ${diseaseData.showCure ? '✓' : '✗'}`);

  }

  

  const badgeStyle = (!diseaseData.showName && isPlayer) ? S.badgeUnknown : S.badgeDisease;

  

  return <Tooltip text={tooltipLines.join('\n')}>

    <span style={{ ...S.badge, ...badgeStyle, cursor: onClick ? 'pointer' : 'help' }} onClick={onClick}>

      {displayText}{onClick && ' ✕'}

    </span>

  </Tooltip>;

};





const SystemBadge = ({ systemData, system, isPlayer, onClick, glowColor = null, onClearGlow = null, char = null }) => {

  const sys = isPlayer ? (systemData?.template || system || null) : (system || systemData?.template || null);



  const typeMeta = SYSTEM_TYPE_OPTIONS.find(t => t.value === ((sys && sys.type) || 'custom'));

  const icon = typeMeta?.icon || '🔒';



  const showName = !!systemData?.showName;

  const showDescription = !!systemData?.showDescription;

  const showEffects = !!systemData?.showEffects;

  const showCure = !!systemData?.showCure;

  const visibleClues = systemData?.visibleClues || [];



  const playerVisible = systemData?.playerVisible !== false; // default true

  const isActive = systemData?.active !== false; // default true



  const applyEffects = systemData?.applyEffects !== false; // default true

  const applyHiddenEffects = systemData?.applyHiddenEffects === true; // default false



  const showPlayerNotes = !!systemData?.showPlayerNotes;

  const playerNotes = (systemData?.playerNotes || '').trim();

  const playerSafeDescription = getPlayerSafeSystemPublicDescription(sys);
  const dmPublicDescription = isCrimsonArtsSystem(sys) ? playerSafeDescription : (sys?.publicDescription || '');



  const sections = (sys && sys.sections) ? sys.sections : [];

  const visibleSections = systemData?.visibleSections || {};

  const showSections = systemData?.showSections || {};



  const hasVisibleSections = sections.some(sec => {

    if (!sec || sec.dmOnly) return false;

    const sid = sec.id;

    if (!sid) return false;

    if (sec.kind === 'text') return !!showSections[sid] && !!(sec.text || '').trim();

    // default list

    return (visibleSections[sid] || []).length > 0;

  });



  const hasAnyVisible =

    showName ||

    showDescription ||

    visibleClues.length > 0 ||

    showEffects ||

    showCure ||

    (showPlayerNotes && playerNotes.length > 0) ||

    hasVisibleSections;



  // If the DM wants this entry to be entirely unknown to players until revealed,

  // set playerVisible=false. In that case, render nothing unless something is explicitly revealed.

  if (isPlayer && !playerVisible && !hasAnyVisible) return null;



  // Customizable unknown text (default "??")

  const unknownText = systemData?.unknownText || UNKNOWN_PLACEHOLDER;

  

  // Placeholder text for name/description when hidden

  const placeholderName = systemData?.placeholderName || '';

  const placeholderDescription = systemData?.placeholderDescription || '';

  

  // Custom badge color (from BUFF_COLOR_OPTIONS). When `useGlowColorForBadge` is set on

  // the instance, the badge derives its color from the glow color instead of badgeColor.

  const effectiveBadgeColorId = systemData?.useGlowColorForBadge

    ? (systemData?.glowColorId || null)

    : (systemData?.badgeColor || null);

  const customBadgeColor = effectiveBadgeColorId;

  const colorOpt = customBadgeColor ? BUFF_COLOR_OPTIONS.find(c => c.id === customBadgeColor) : null;



  const displayName = (() => {

    if (!sys) return isPlayer ? (showName ? 'System' : unknownText) : (systemData?.systemId || 'System');

    if (!isPlayer) return sys.name || sys.publicName || systemData?.systemId || 'System';

    // Show placeholder if set, otherwise show public name if revealed, otherwise show unknown text

    if (showName) return sys.publicName || sys.name || 'System';

    return placeholderName || unknownText;

  })();



  const baseBadgeStyle = !hasAnyVisible && isPlayer ? S.badgeUnknown : S.badgeSystem;

  

  // Special badge styles for oath and spell-resource types

  const getSpecialBadgeStyle = () => {

    if (!sys?.type) return null;

    if (sys.type === 'oath') {

      return {

        background: 'linear-gradient(180deg, rgba(139,31,31,0.4) 0%, rgba(80,20,20,0.6) 100%)',

        borderColor: '#c92727',

        color: '#ff8888',

      };

    }

    if (sys.type === 'spell-resource') {

      return {

        background: 'linear-gradient(180deg, rgba(155,89,182,0.3) 0%, rgba(100,50,120,0.5) 100%)',

        borderColor: '#9b59b6',

        color: '#d9a8f0',

      };

    }

    return null;

  };

  

  const specialStyle = getSpecialBadgeStyle();

  

  // Apply custom color if set, then special style, then default

  const badgeStyle = colorOpt ? {

    ...baseBadgeStyle,

    background: colorOpt.bgColor,

    borderColor: colorOpt.color,

    color: colorOpt.color,

  } : specialStyle ? {

    ...baseBadgeStyle,

    ...specialStyle,

  } : baseBadgeStyle;

  

  const clickable = !!onClick;



  const playerSees = [];

  if (playerVisible) playerSees.push('present');

  else playerSees.push('hidden-until-revealed');

  if (showName) playerSees.push('name');

  if (showDescription) playerSees.push('details');

  if (visibleClues.length > 0) playerSees.push(`${visibleClues.length} clue(s)`);

  if (showEffects) playerSees.push('effects');

  if (showCure) playerSees.push('cure');

  if (showPlayerNotes && playerNotes) playerSees.push('notes');



  // sections summary for DM tooltip

  const sectionSummary = sections

    .filter(sec => sec && !sec.dmOnly)

    .map(sec => {

      const sid = sec.id;

      if (!sid) return null;

      if (sec.kind === 'text') return `${sec.label || 'Text'}: ${showSections[sid] ? 'shown' : 'hidden'}`;

      const count = (visibleSections[sid] || []).length;

      return `${sec.label || 'List'}: ${count} shown`;

    })

    .filter(Boolean);



  // Clues label customization (default "Clues", can be renamed or hidden)

  const cluesLabel = systemData?.cluesLabel ?? 'Clues';

  const showCluesLabel = systemData?.showCluesLabel !== false;



  // Build tooltip content based on what player should see

  const title = (() => {

    // For players with only placeholder info, show clean minimal tooltip

    if (isPlayer && !showName && !showDescription && placeholderDescription) {

      return (

        <div>

          <div style={{ fontStyle: 'italic', color: '#c9a227' }}>{placeholderDescription}</div>

        </div>

      );

    }

    

    // For players with no info at all, show minimal message

    if (isPlayer && !hasAnyVisible && !placeholderDescription) {

      return (

        <div>

          <div style={{ color: '#888', fontStyle: 'italic' }}>Something is affecting you...</div>

        </div>

      );

    }

    

    return (

      <div>

        {/* Title - hide icon for players unless name is revealed; hide entire title if nothing revealed */}

        {(!isPlayer || showName) ? (

          <div style={{ fontWeight: 'bold' }}>

            {icon} {displayName} {!isActive && <span style={{ opacity: 0.75 }}>(inactive)</span>}

          </div>

        ) : placeholderName ? (

          <div style={{ fontWeight: 'bold', color: '#c9a227' }}>

            {placeholderName} {!isActive && <span style={{ opacity: 0.75 }}>(inactive)</span>}

          </div>

        ) : null}



        {/* Player content */}

        {isPlayer && (

          <div style={{ marginTop: '4px' }}>

            {/* Show placeholder description if real description not revealed */}

            {!showDescription && placeholderDescription && (

              <div style={{ fontStyle: 'italic', color: '#c9a227' }}>{placeholderDescription}</div>

            )}

            

            {/* Show real description if revealed */}

            {showDescription && playerSafeDescription && (

              <div style={{ marginTop: '4px' }}>{playerSafeDescription}</div>

            )}



            {/* Clues - with customizable label */}

            {visibleClues.length > 0 && sys?.clues?.length > 0 && (

              <div style={{ marginTop: '6px' }}>

                {showCluesLabel && cluesLabel && <div style={{ color: '#888' }}>{cluesLabel}:</div>}

                <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>

                  {visibleClues.map(i => <li key={i}>{sys.clues[i]}</li>)}

                </ul>

              </div>

            )}



            {sections.length > 0 && (

              <div style={{ marginTop: '6px' }}>

                {sections.filter(sec => sec && !sec.dmOnly).map(sec => {

                  const sid = sec.id;

                  if (!sid) return null;



                  if (sec.kind === 'text') {

                    if (!showSections[sid]) return null;

                    if (!(sec.text || '').trim()) return null;

                    return (

                      <div key={sid} style={{ marginTop: '6px' }}>

                        <div style={{ color: '#888' }}>{sec.label || 'Details'}:</div>

                        <div style={{ marginTop: '2px' }}>{sec.text}</div>

                      </div>

                    );

                  }



                  // list

                  const vis = visibleSections[sid] || [];

                  if (!vis.length) return null;

                  return (

                    <div key={sid} style={{ marginTop: '6px' }}>

                      <div style={{ color: '#888' }}>{sec.label || 'Details'}:</div>

                      <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>

                        {vis.map(i => <li key={i}>{(sec.items || [])[i]}</li>)}

                      </ul>

                    </div>

                  );

                })}

              </div>

            )}



            {/* OATH SPECIAL DISPLAY - Show restrictions prominently */}

            {sys?.type === 'oath' && showDescription && (

              <div style={{ marginTop: '8px', padding: '8px', background: 'rgba(139,31,31,0.15)', borderRadius: '6px', border: '1px solid rgba(201,39,39,0.3)' }}>

                <div style={{ color: '#c92727', fontWeight: 'bold', marginBottom: '4px' }}>⛓️ You Are Bound</div>

                {(() => {

                  const restrictionsSec = (sys.sections || []).find(s => s.id === 'restrictions');

                  const termsSection = (sys.sections || []).find(s => s.id === 'terms');

                  

                  return (

                    <>

                      {termsSection && termsSection.text && showSections?.['terms'] && (

                        <div style={{ fontSize: '0.85rem', marginBottom: '6px', fontStyle: 'italic' }}>{termsSection.text}</div>

                      )}

                      {restrictionsSec && (restrictionsSec.items || []).length > 0 && (visibleSections?.['restrictions'] || []).length > 0 && (

                        <ul style={{ margin: '4px 0 0 12px', padding: 0, fontSize: '0.85rem' }}>

                          {(visibleSections['restrictions'] || []).map(i => (

                            <li key={i} style={{ marginBottom: '2px', color: '#ff8888' }}>

                              {(restrictionsSec.items || [])[i]}

                            </li>

                          ))}

                        </ul>

                      )}

                    </>

                  );

                })()}

              </div>

            )}



            {/* SPELL RESOURCE SPECIAL DISPLAY — reads through getSystemTechniques so both the

                new `techniques[]` shape and the legacy `spells[]` shape render uniformly.

                Cost labels use formatCostEntry (shared with the cast modal). Greys out any

                technique the character can't currently afford; respects hidden.cost and

                hidden.existence privacy flags. Actual cast happens from TechniquesPanel on

                the character page — this tooltip is informational only. */}

            {sys?.type === 'spell-resource' && showDescription && (() => {

              const techniques = getSystemTechniques(sys);

              const visible = isPlayer

                ? techniques.filter(t => !t.hidden?.existence)

                : techniques;

              if (visible.length === 0) return null;

              // `systemData` IS the per-character system instance (that's the prop the

              // component receives); `char` is an optional prop passed by the caller when

              // available. If no char is in scope (older call sites, DM quick-view), we

              // still render the technique list but skip the availability-reason line —

              // the real availability check happens in TechniquesPanel at cast time.

              const systemInstance = systemData;

              return (

                <div style={{ marginTop: '8px', padding: '8px', background: 'rgba(155,89,182,0.15)', borderRadius: '6px', border: '1px solid rgba(155,89,182,0.3)' }}>

                  <div style={{ color: '#9b59b6', fontWeight: 'bold', marginBottom: '6px' }}>🔮 Available Techniques</div>

                  <div style={{ maxHeight: '180px', overflowY: 'auto' }}>

                    {visible.map((tech, i) => {

                      // Availability check uses the canonical helper when char is provided.

                      // When char is missing, default to "available" so the badge still

                      // renders informationally. Unavailable rows grey out with a reason

                      // tooltip (except when hidden.requirements is set for a player).

                      const avail = char

                        ? isTechniqueAvailable(tech, char, systemInstance, {

                            // worldContext is thin here — the full time/location/combat context

                            // lives in TechniquesPanel. Costs + requirements + cooldown still

                            // evaluate correctly.

                          }, null, isPlayer)

                        // 2026-04-28: dropped the 5th `systems` arg — SystemBadge

                        // doesn't accept the global systems map as a prop, so the

                        // identifier was undeclared and threw `ReferenceError:

                        // systems is not defined` whenever a player viewed a

                        // character with a system that exposes techniques.

                        // isTechniqueAvailable defaults `systems` to null, which

                        // means the cross-system require-system-applied check

                        // degrades to "skip" — fine for a badge tooltip; the

                        // canonical TechniquesPanel still does the full check at

                        // cast time. 2026-04-29: 6th arg `isPlayer` lets the

                        // requirement formatter swap to publicLabel for hidden

                        // trackers (BUG-REPORTS.md #6).

                        : { available: true, reason: null };

                      const hideCost = tech.hidden?.cost;

                      // 2026-04-29: requirements default-hidden too, mirroring

                      // hidden.name convention. DM opts IN to showing reasons

                      // by setting `requirements: false` on the technique. This

                      // also fixes already-applied instances whose cached

                      // template predated the per-technique privacy convention

                      // — without an explicit value, `requirements` is

                      // undefined → undefined !== false → hidden by default.

                      const hideReq = shouldHideTechniqueRequirementReason(tech, sys);

                      // 2026-04-28: respect hidden.name in this badge surface

                      // too. TechniquesPanel already does (App.js:~16081);

                      // SystemBadge was leaking the real name to players.

                      // Default-hidden is the convention for techniques —

                      // when `hidden.name !== false`, treat as hidden. P3.

                      const hideName = isPlayer && tech.hidden?.name !== false;

                      const displayName = hideName ? HIDDEN_LABEL_PLACEHOLDER : tech.name;

                      const costLabel = hideCost

                        ? HIDDEN_LABEL_PLACEHOLDER

                        : ((tech.costs || []).map(formatCostEntry).join(' + ') || 'Free');

                      const reasonLabel = avail.available

                        ? null

                        : (isPlayer && hideReq ? 'Unavailable' : avail.reason);
                      const displayIcon = getTechniqueDisplayIcon(tech, sys, '✨');

                      return (

                        <div key={tech.id || i} style={{

                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',

                          padding: '3px 6px', marginBottom: '2px',

                          background: avail.available ? 'rgba(155,89,182,0.1)' : 'rgba(0,0,0,0.2)',

                          borderRadius: '4px',

                          opacity: avail.available ? 1 : 0.55,

                        }}>

                          <span style={{ fontSize: '0.8rem' }}>

                            {displayIcon && <>{displayIcon} </>}{displayName}

                            {reasonLabel && (

                              <span style={{ fontSize: '0.7rem', opacity: 0.7, fontStyle: 'italic', marginLeft: '4px' }}>— {reasonLabel}</span>

                            )}

                          </span>

                          <span style={{ fontSize: '0.7rem', color: avail.available ? '#9b59b6' : '#888' }}>{costLabel}</span>

                        </div>

                      );

                    })}

                  </div>

                </div>

              );

            })()}



            {showEffects && sys?.effects?.length > 0 && (

              <div style={{ marginTop: '6px' }}>

                <div style={{ color: '#888' }}>Effects:</div>

                <div>{(sys.effects || []).map(e => typeof e === 'string' ? e : (e.displayText || '')).join(', ')}</div>

              </div>

            )}

            

            {/* Show hidden effects indicator to player if they're being applied */}

            {!showEffects && applyEffects && applyHiddenEffects && ((sys?.effects || []).length > 0 || (sys?.hiddenEffects || []).length > 0) && (

              <div style={{ marginTop: '6px' }}>

                <div style={{ color: '#c9a227' }}>⚠️ Unknown effects are affecting you...</div>

              </div>

            )}



            {showCure && sys?.cures?.length > 0 && (

              <div style={{ marginTop: '6px' }}>

                <div style={{ color: '#888' }}>Cure:</div>

                <div>{sys.cures.map(c => `${c.quantity}x ${c.item}`).join(', ')}</div>

              </div>

            )}



            {showPlayerNotes && playerNotes && (

              <div style={{ marginTop: '6px' }}>

                <div style={{ color: '#888' }}>Notes:</div>

                <div>{playerNotes}</div>

              </div>

            )}

            

            {/* Stage display for players (if revealed) */}

            {(() => {

              const showStage = systemData?.showStage;

              if (!showStage) return null;

              

              const stageInfo = getCurrentStageInfo(systemData, sys);

              if (!stageInfo?.stage) return null;

              

              return (

                <div style={{ marginTop: '6px' }}>

                  <div style={{ color: '#ff6b6b' }}>Stage: {stageInfo.stage.name}</div>

                  {stageInfo.stage.description && (

                    <div style={{ fontSize: '0.85rem', opacity: 0.9, marginTop: '2px' }}>

                      {stageInfo.stage.description}

                    </div>

                  )}

                </div>

              );

            })()}

            

            {/* Tracker display for players (if any are visible) */}

            {(() => {

              const trackerFields = sys?.trackerFields || [];

              const trackerValues = systemData?.trackerValues || {};

              const trackerVisibility = systemData?.trackerVisibility || {};

              

              const visibleTrackers = trackerFields.filter(field => {

                const vis = trackerVisibility[field.id] || {};

                // Check both instance visibility AND field-level showToPlayer

                const instanceVis = vis.showToPlayer !== false;

                const fieldVis = field.showToPlayer !== false;

                return instanceVis && fieldVis;

              });

              

              if (visibleTrackers.length === 0) return null;

              

              return (

                <div style={{ marginTop: '8px' }}>

                  <div style={{ color: '#c9a227', marginBottom: '4px' }}>📊 Status:</div>

                  {visibleTrackers.map((field, i) => {

                    const vis = trackerVisibility[field.id] || {};

                    // Default showBar to true for progress type, or if field has max

                    const showBar = vis.showBar !== false && (field.type === 'progress' || field.max);

                    const showValue = vis.showValue !== false;

                    const currentValue = trackerValues[field.id] ?? field.value ?? 0;

                    const max = field.max;

                    const percent = max ? Math.min(100, Math.max(0, (currentValue / max) * 100)) : 0;

                    

                    // Player sees publicLabel if set; DM always sees the real `label`.

                    const displayLabel = isPlayer ? (field.publicLabel || field.label) : field.label;

                    return (

                      <div key={field.id || i} style={{ marginBottom: '6px' }}>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showBar && max ? '4px' : '0' }}>

                          <span style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{displayLabel}</span>

                          {showValue ? (

                            <span style={{ fontSize: '0.8rem' }}>{currentValue}{max ? `/${max}` : ''}</span>

                          ) : (

                            <span style={{ fontSize: '0.75rem', opacity: 0.6 }}>?</span>

                          )}

                        </div>

                        {showBar && max && (

                          <div style={{ 

                            height: '10px', 

                            background: 'rgba(255,255,255,0.1)', 

                            borderRadius: '5px', 

                            overflow: 'hidden',

                            border: '1px solid rgba(255,255,255,0.2)',

                          }}>

                            <div style={{

                              height: '100%',

                              width: `${percent}%`,

                              background: `linear-gradient(90deg, ${field.color || '#c9a227'}, ${field.color || '#c9a227'}dd)`,

                              borderRadius: '4px',

                              transition: 'width 0.3s ease',

                              minWidth: percent > 0 ? '4px' : '0',

                            }} />

                          </div>

                        )}

                      </div>

                    );

                  })}

                </div>

              );

            })()}

          </div>

        )}



      {/* DM tooltip */}

      {!isPlayer && (

        <div style={{ marginTop: '6px' }}>

          {sys?.description && <div style={{ marginTop: '4px' }}><span style={{ color: '#888' }}>DM:</span> {sys.description}</div>}

          {dmPublicDescription && <div style={{ marginTop: '4px' }}><span style={{ color: '#888' }}>Public:</span> {dmPublicDescription}</div>}



          {sys?.clues?.length > 0 && (

            <div style={{ marginTop: '6px' }}>

              <div style={{ color: '#888' }}>Clues:</div>

              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>

                {sys.clues.map((c, i) => {

                  const revealed = (visibleClues || []).includes(i);

                  return <li key={i} style={{ opacity: revealed ? 1 : 0.5 }}>{c} {!revealed && <span style={{ color: '#c9a227' }}>(hidden)</span>}</li>;

                })}

              </ul>

            </div>

          )}



          {sections.length > 0 && (

            <div style={{ marginTop: '6px' }}>

              <div style={{ color: '#888' }}>Sections:</div>

              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>

                {sections.map(sec => {

                  if (!sec) return null;

                  const sid = sec.id;

                  if (!sid) return null;

                  const dmOnly = !!sec.dmOnly;

                  const shown = sec.kind === 'text' ? (showSections[sid] ? 1 : 0) : (visibleSections[sid] || []).length;

                  return (

                    <li key={sid} style={{ opacity: 0.95 }}>

                      <span style={{ fontWeight: 'bold' }}>{sec.label || sid}</span>{' '}

                      <span style={{ opacity: 0.7 }}>({sec.kind || 'list'})</span>{' '}

                      {dmOnly ? <span style={{ color: '#c9a227' }}>(DM only)</span> : <span style={{ opacity: 0.75 }}>shown: {shown}</span>}

                    </li>

                  );

                })}

              </ul>

            </div>

          )}



          {sys?.effects?.length > 0 && (

            <div style={{ marginTop: '6px' }}>

              <div style={{ color: '#888' }}>Visible Effects:</div>

              <div>{(sys.effects || []).map(e => typeof e === 'string' ? e : (e.displayText || '')).join(', ')}</div>

              <div style={{ marginTop: '2px', fontSize: '0.75rem', opacity: 0.75 }}>

                Apply: {applyEffects ? 'ON' : 'OFF'}{applyEffects && !showEffects && <span> • Hidden apply: {applyHiddenEffects ? 'ON' : 'OFF'}</span>}

              </div>

            </div>

          )}

          

          {/* Session 7B: DM-only content. The `!isPlayer` guard is defense-in-

              depth — for player viewers the server-side projection strips

              `sys.hiddenEffects` entirely (see src/convex/privacy/systems.ts).

              This guard keeps the leak closed even if projection regresses. */}

          {!isPlayer && sys?.hiddenEffects?.length > 0 && (

            <div style={{ marginTop: '6px' }}>

              <div style={{ color: '#c9a227' }}>🔒 Hidden Effects (source shows as ?):</div>

              <div>{(sys.hiddenEffects || []).map(e => typeof e === 'string' ? e : (e.displayText || '')).join(', ')}</div>

              <div style={{ marginTop: '2px', fontSize: '0.75rem', opacity: 0.75 }}>

                Apply hidden: {applyHiddenEffects ? 'ON' : 'OFF'}

              </div>

            </div>

          )}



          {/* Session 7B: P7 hidden-fact indicator for players. Shows the

              existence of hidden effects without leaking their content.

              `_hiddenEffectsCount` is populated by `projectSystemForViewer`

              on the projected systemList instance — see chars:listForViewer. */}

          {isPlayer && (systemData?._hiddenEffectsCount || 0) > 0 && (

            <div style={{ marginTop: '6px' }}>

              <div style={{ color: '#c9a227', fontSize: '0.75rem', opacity: 0.8 }}>

                🔒 {systemData._hiddenEffectsCount} hidden {systemData._hiddenEffectsCount === 1 ? 'effect' : 'effects'}

              </div>

            </div>

          )}



          {sys?.cures?.length > 0 && (

            <div style={{ marginTop: '6px' }}>

              <div style={{ color: '#888' }}>Cures:</div>

              <div>{sys.cures.map(c => `${c.quantity}x ${c.item}`).join(', ')}</div>

            </div>

          )}



          {/* Session 7B: DM-only field. Server projection strips dmNotes for

              players; guard here is defense-in-depth. */}

          {!isPlayer && (systemData?.dmNotes || '').trim() && (

            <div style={{ marginTop: '6px' }}>

              <div style={{ color: '#888' }}>DM Notes:</div>

              <div>{systemData.dmNotes}</div>

            </div>

          )}



          <div style={{ marginTop: '6px', fontSize: '0.75rem', opacity: 0.75 }}>

            Player sees: {playerSees.concat(sectionSummary).filter(Boolean).join(', ') || 'nothing'}

          </div>

        </div>

      )}

    </div>

  );

  })();



  return (

    <>

      {glowColor && (

        <style>{`

          @keyframes systemBadgeGlow {

            0%, 100% { box-shadow: 0 0 8px ${glowColor}, 0 0 16px ${glowColor}44; }

            50% { box-shadow: 0 0 16px ${glowColor}, 0 0 24px ${glowColor}88; }

          }

        `}</style>

      )}

      <Tooltip text={title}>

        <span

          style={{

            ...S.badge,

            ...badgeStyle,

            cursor: clickable ? 'pointer' : 'default',

            opacity: isActive ? 1 : 0.8,

            ...(glowColor ? {

              animation: 'systemBadgeGlow 1.5s ease-in-out infinite',

              boxShadow: `0 0 12px ${glowColor}, 0 0 20px ${glowColor}55`,

            } : {}),

          }}

          onClick={(e) => {

            if (glowColor && onClearGlow) onClearGlow();

            if (onClick) onClick(e);

          }}

          onMouseEnter={() => {

            if (glowColor && onClearGlow) onClearGlow();

          }}

        >

          {/* Only show icon for DM or if name is revealed */}

          {(!isPlayer || showName) && <span>{icon} </span>}

          {displayName}

          {!isActive && <span style={{ marginLeft: '6px', opacity: 0.8 }}>⏸</span>}

        </span>

      </Tooltip>

    </>

  );

};





// ============================================================================

// EFFECT BUILDER COMPONENT - For creating mechanical effects

// ============================================================================



const EffectBuilder = ({ onAdd, onCancel, existingEffect = null }) => {

  const [mode, setMode] = useState('quick'); // 'quick', 'text', 'syntax'

  const [category, setCategory] = useState(existingEffect?.type || null);

  const [displayText, setDisplayText] = useState(existingEffect?.displayText || '');

  const [syntaxInput, setSyntaxInput] = useState('');

  const [parsedEffects, setParsedEffects] = useState([]);

  

  // Quick builder state

  const [statTarget, setStatTarget] = useState('str');

  const [statOperation, setStatOperation] = useState('bonus');

  const [statValue, setStatValue] = useState(0);

  

  const [skillTarget, setSkillTarget] = useState('Perception');

  const [skillOperation, setSkillOperation] = useState('bonus');

  const [skillValue, setSkillValue] = useState(0);

  const [skillScope, setSkillScope] = useState('single'); // 'single', 'ability', 'all'

  const [skillAbility, setSkillAbility] = useState('wis');

  

  const [saveTarget, setSaveTarget] = useState('con');

  const [saveOperation, setSaveOperation] = useState('bonus');

  const [saveValue, setSaveValue] = useState(0);

  

  const [attackTarget, setAttackTarget] = useState('all');

  const [attackOperation, setAttackOperation] = useState('bonus');

  const [attackValue, setAttackValue] = useState(0);

  

  const [acValue, setAcValue] = useState(0);

  const [speedValue, setSpeedValue] = useState(0);

  const [hpTarget, setHpTarget] = useState('max');

  const [hpValue, setHpValue] = useState(0);

  

  const [damageType, setDamageType] = useState('Fire');

  const [damageOperation, setDamageOperation] = useState('resistance');

  

  const [conditionId, setConditionId] = useState('poisoned');

  const [exhaustionLevel, setExhaustionLevel] = useState(1);

  

  // Parse syntax input

  useEffect(() => {

    if (mode === 'syntax' && syntaxInput.trim()) {

      const effects = parseEffectSyntax(syntaxInput);

      setParsedEffects(effects);

    } else {

      setParsedEffects([]);

    }

  }, [syntaxInput, mode]);

  

  // Build the current effect based on category and settings

  const buildCurrentEffect = () => {

    if (mode === 'syntax') {

      return {

        displayText: displayText || parsedEffects.map(formatEffectDisplay).join(', '),

        mechanics: parsedEffects,

        category: 'custom',

      };

    }

    

    if (category === 'custom' || !category) {

      return {

        displayText: displayText,

        mechanics: [],

        category: 'custom',

      };

    }

    

    let mechanics = [];

    let autoDisplayText = '';

    

    switch (category) {

      case 'stat':

        mechanics = [createMechanicalEffect('stat', statTarget, statOperation, statValue)];

        autoDisplayText = formatEffectDisplay(mechanics[0]);

        break;

      

      case 'skill':

        if (skillScope === 'single') {

          mechanics = [createMechanicalEffect('skill', skillTarget, skillOperation, skillOperation === 'bonus' ? skillValue : null)];

        } else if (skillScope === 'ability') {

          // All skills using a specific ability

          const skillsForAbility = Object.entries(SKILL_ABILITY_MAP).filter(([, ab]) => ab === skillAbility).map(([sk]) => sk);

          mechanics = skillsForAbility.map(sk => createMechanicalEffect('skill', sk, skillOperation, skillOperation === 'bonus' ? skillValue : null));

        } else {

          mechanics = [createMechanicalEffect('skill', 'all', skillOperation, skillOperation === 'bonus' ? skillValue : null)];

        }

        autoDisplayText = formatEffectDisplay(mechanics[0]);

        if (skillScope === 'ability') autoDisplayText = `${skillOperation === 'advantage' ? 'Advantage' : skillOperation === 'disadvantage' ? 'Disadvantage' : `${skillValue >= 0 ? '+' : ''}${skillValue}`} on ${ABILITY_LABELS[skillAbility]} skills`;

        if (skillScope === 'all') autoDisplayText = `${skillOperation === 'advantage' ? 'Advantage' : skillOperation === 'disadvantage' ? 'Disadvantage' : `${skillValue >= 0 ? '+' : ''}${skillValue}`} on all skills`;

        break;

      

      case 'save':

        mechanics = [createMechanicalEffect('save', saveTarget, saveOperation, saveOperation === 'bonus' ? saveValue : null)];

        autoDisplayText = formatEffectDisplay(mechanics[0]);

        break;

      

      case 'attack':

        mechanics = [createMechanicalEffect('attack', attackTarget, attackOperation, attackOperation === 'bonus' ? attackValue : null)];

        autoDisplayText = formatEffectDisplay(mechanics[0]);

        break;

      

      case 'ac':

        mechanics = [createMechanicalEffect('ac', 'base', 'bonus', acValue)];

        autoDisplayText = formatEffectDisplay(mechanics[0]);

        break;

      

      case 'hp':

        mechanics = [createMechanicalEffect('hp', hpTarget, 'bonus', hpValue)];

        autoDisplayText = formatEffectDisplay(mechanics[0]);

        break;

      

      case 'speed':

        mechanics = [createMechanicalEffect('speed', 'walk', 'bonus', speedValue)];

        autoDisplayText = formatEffectDisplay(mechanics[0]);

        break;

      

      case 'damage':

        mechanics = [createMechanicalEffect('damage', damageType, damageOperation)];

        autoDisplayText = formatEffectDisplay(mechanics[0]);

        break;

      

      case 'condition':

        if (conditionId === 'exhaustion') {

          mechanics = [createMechanicalEffect('condition', 'exhaustion', 'set', exhaustionLevel)];

        } else {

          mechanics = [createMechanicalEffect('condition', conditionId, 'apply')];

        }

        autoDisplayText = formatEffectDisplay(mechanics[0]);

        break;

    }

    

    return {

      displayText: displayText || autoDisplayText,

      mechanics,

      category,

    };

  };

  

  const handleAdd = () => {

    const effect = buildCurrentEffect();

    if (!effect.displayText.trim() && effect.mechanics.length === 0) return;

    onAdd(effect);

  };

  

  const c = S._colors || {};

  const inputStyle = { ...S.input, marginBottom: '8px' };

  const selectStyle = { ...S.select, marginBottom: '8px' };

  const labelStyle = { ...S.label, marginBottom: '4px', fontSize: '0.85rem' };

  

  return (

    <div style={{ ...S.subCard, padding: '16px' }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>

        <h4 style={{ margin: 0, color: c.accent || '#c9a227' }}>⚡ Add Effect</h4>

        <div style={{ display: 'flex', gap: '4px' }}>

          {['quick', 'text', 'syntax'].map(m => (

            <button

              key={m}

              onClick={() => setMode(m)}

              style={{

                padding: '4px 10px',

                background: mode === m ? (c.accent || '#c9a227') : 'rgba(255,255,255,0.1)',

                border: 'none',

                borderRadius: '4px',

                color: mode === m ? '#000' : (c.textPrimary || '#fff'),

                cursor: 'pointer',

                fontSize: '0.75rem',

              }}

            >

              {m === 'quick' ? '🔧 Quick' : m === 'text' ? '📝 Text' : '⌨️ Syntax'}

            </button>

          ))}

        </div>

      </div>

      

      {/* Mode: Quick Builder */}

      {mode === 'quick' && (

        <>

          {/* Category Selection */}

          {!category && (

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '8px', marginBottom: '12px' }}>

              {EFFECT_CATEGORIES.map(cat => (

                <button

                  key={cat.id}

                  onClick={() => setCategory(cat.id)}

                  style={{

                    padding: '12px 8px',

                    background: 'rgba(255,255,255,0.05)',

                    border: `1px solid ${c.borderLight || '#444'}`,

                    borderRadius: '6px',

                    color: c.textPrimary || '#fff',

                    cursor: 'pointer',

                    textAlign: 'center',

                    transition: 'all 0.2s',

                  }}

                  onMouseEnter={e => e.target.style.background = 'rgba(255,255,255,0.1)'}

                  onMouseLeave={e => e.target.style.background = 'rgba(255,255,255,0.05)'}

                >

                  <div style={{ fontSize: '1.5rem', marginBottom: '4px' }}>{cat.icon}</div>

                  <div style={{ fontSize: '0.8rem' }}>{cat.label}</div>

                </button>

              ))}

            </div>

          )}

          

          {/* Category-specific UI */}

          {category && (

            <div style={{ marginBottom: '12px' }}>

              <button onClick={() => setCategory(null)} style={{ ...S.btn, ...S.btnXs, marginBottom: '12px' }}>

                ← Back to categories

              </button>

              

              {/* Stat Modifier */}

              {category === 'stat' && (

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>

                  <div>

                    <label style={labelStyle}>Ability</label>

                    <select value={statTarget} onChange={e => setStatTarget(e.target.value)} style={selectStyle}>

                      {ABILITY_SCORES.map(ab => <option key={ab} value={ab}>{ABILITY_LABELS[ab]}</option>)}

                    </select>

                  </div>

                  <div>

                    <label style={labelStyle}>Type</label>

                    <select value={statOperation} onChange={e => setStatOperation(e.target.value)} style={selectStyle}>

                      <option value="bonus">Modifier (+/-)</option>

                      <option value="set">Set to value</option>

                    </select>

                  </div>

                  <div>

                    <label style={labelStyle}>Value</label>

                    <input type="number" value={statValue} onChange={e => setStatValue(parseInt(e.target.value) || 0)} style={inputStyle} />

                  </div>

                </div>

              )}

              

              {/* Skill Modifier */}

              {category === 'skill' && (

                <>

                  <div style={{ marginBottom: '8px' }}>

                    <label style={labelStyle}>Affect</label>

                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>

                      {[

                        { id: 'single', label: 'Specific skill' },

                        { id: 'ability', label: 'All skills using...' },

                        { id: 'all', label: 'All skills' },

                      ].map(opt => (

                        <label key={opt.id} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>

                          <input type="radio" checked={skillScope === opt.id} onChange={() => setSkillScope(opt.id)} />

                          {opt.label}

                        </label>

                      ))}

                    </div>

                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: skillScope === 'all' ? '1fr 1fr' : '1fr 1fr 1fr', gap: '8px' }}>

                    {skillScope === 'single' && (

                      <div>

                        <label style={labelStyle}>Skill</label>

                        <select value={skillTarget} onChange={e => setSkillTarget(e.target.value)} style={selectStyle}>

                          {Object.keys(SKILL_ABILITY_MAP).sort().map(sk => <option key={sk} value={sk}>{sk}</option>)}

                        </select>

                      </div>

                    )}

                    {skillScope === 'ability' && (

                      <div>

                        <label style={labelStyle}>Ability</label>

                        <select value={skillAbility} onChange={e => setSkillAbility(e.target.value)} style={selectStyle}>

                          {ABILITY_SCORES.map(ab => <option key={ab} value={ab}>{ABILITY_LABELS[ab]}</option>)}

                        </select>

                      </div>

                    )}

                    <div>

                      <label style={labelStyle}>Modifier</label>

                      <select value={skillOperation} onChange={e => setSkillOperation(e.target.value)} style={selectStyle}>

                        <option value="bonus">Bonus (+/-)</option>

                        <option value="advantage">Advantage</option>

                        <option value="disadvantage">Disadvantage</option>

                      </select>

                    </div>

                    {skillOperation === 'bonus' && (

                      <div>

                        <label style={labelStyle}>Value</label>

                        <input type="number" value={skillValue} onChange={e => setSkillValue(parseInt(e.target.value) || 0)} style={inputStyle} />

                      </div>

                    )}

                  </div>

                </>

              )}

              

              {/* Saving Throw */}

              {category === 'save' && (

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>

                  <div>

                    <label style={labelStyle}>Save Type</label>

                    <select value={saveTarget} onChange={e => setSaveTarget(e.target.value)} style={selectStyle}>

                      <option value="all">All saves</option>

                      {ABILITY_SCORES.map(ab => <option key={ab} value={ab}>{ABILITY_LABELS[ab]}</option>)}

                      <option value="death">Death saves</option>

                      <option value="concentration">Concentration</option>

                    </select>

                  </div>

                  <div>

                    <label style={labelStyle}>Modifier</label>

                    <select value={saveOperation} onChange={e => setSaveOperation(e.target.value)} style={selectStyle}>

                      <option value="bonus">Bonus (+/-)</option>

                      <option value="advantage">Advantage</option>

                      <option value="disadvantage">Disadvantage</option>

                    </select>

                  </div>

                  {saveOperation === 'bonus' && (

                    <div>

                      <label style={labelStyle}>Value</label>

                      <input type="number" value={saveValue} onChange={e => setSaveValue(parseInt(e.target.value) || 0)} style={inputStyle} />

                    </div>

                  )}

                </div>

              )}

              

              {/* Attack Roll */}

              {category === 'attack' && (

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>

                  <div>

                    <label style={labelStyle}>Attack Type</label>

                    <select value={attackTarget} onChange={e => setAttackTarget(e.target.value)} style={selectStyle}>

                      <option value="all">All attacks</option>

                      <option value="melee">Melee</option>

                      <option value="ranged">Ranged</option>

                      <option value="spell">Spell attacks</option>

                    </select>

                  </div>

                  <div>

                    <label style={labelStyle}>Modifier</label>

                    <select value={attackOperation} onChange={e => setAttackOperation(e.target.value)} style={selectStyle}>

                      <option value="bonus">Bonus (+/-)</option>

                      <option value="advantage">Advantage</option>

                      <option value="disadvantage">Disadvantage</option>

                    </select>

                  </div>

                  {attackOperation === 'bonus' && (

                    <div>

                      <label style={labelStyle}>Value</label>

                      <input type="number" value={attackValue} onChange={e => setAttackValue(parseInt(e.target.value) || 0)} style={inputStyle} />

                    </div>

                  )}

                </div>

              )}

              

              {/* AC */}

              {category === 'ac' && (

                <div style={{ maxWidth: '200px' }}>

                  <label style={labelStyle}>AC Modifier</label>

                  <input type="number" value={acValue} onChange={e => setAcValue(parseInt(e.target.value) || 0)} style={inputStyle} placeholder="+/- value" />

                </div>

              )}

              

              {/* HP */}

              {category === 'hp' && (

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>

                  <div>

                    <label style={labelStyle}>HP Type</label>

                    <select value={hpTarget} onChange={e => setHpTarget(e.target.value)} style={selectStyle}>

                      <option value="max">Max HP</option>

                      <option value="temp">Temp HP</option>

                    </select>

                  </div>

                  <div>

                    <label style={labelStyle}>Value</label>

                    <input type="number" value={hpValue} onChange={e => setHpValue(parseInt(e.target.value) || 0)} style={inputStyle} />

                  </div>

                </div>

              )}

              

              {/* Speed */}

              {category === 'speed' && (

                <div style={{ maxWidth: '200px' }}>

                  <label style={labelStyle}>Speed Modifier (ft)</label>

                  <input type="number" value={speedValue} onChange={e => setSpeedValue(parseInt(e.target.value) || 0)} style={inputStyle} placeholder="+/- feet" />

                </div>

              )}

              

              {/* Damage */}

              {category === 'damage' && (

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>

                  <div>

                    <label style={labelStyle}>Damage Type</label>

                    <select value={damageType} onChange={e => setDamageType(e.target.value)} style={selectStyle}>

                      {EFFECT_DAMAGE_TYPES.map(dt => <option key={dt} value={dt}>{dt}</option>)}

                    </select>

                  </div>

                  <div>

                    <label style={labelStyle}>Modifier</label>

                    <select value={damageOperation} onChange={e => setDamageOperation(e.target.value)} style={selectStyle}>

                      <option value="resistance">Resistance (half)</option>

                      <option value="vulnerability">Vulnerability (double)</option>

                      <option value="immunity">Immunity</option>

                    </select>

                  </div>

                </div>

              )}

              

              {/* Condition */}

              {category === 'condition' && (

                <div style={{ display: 'grid', gridTemplateColumns: conditionId === 'exhaustion' ? '1fr 1fr' : '1fr', gap: '8px' }}>

                  <div>

                    <label style={labelStyle}>Condition</label>

                    <select value={conditionId} onChange={e => setConditionId(e.target.value)} style={selectStyle}>

                      {DND_CONDITIONS.map(cond => <option key={cond.id} value={cond.id}>{cond.name}</option>)}

                      <option value="exhaustion">Exhaustion</option>

                    </select>

                    {conditionId !== 'exhaustion' && (

                      <div style={{ fontSize: '0.75rem', color: c.textMuted || '#888', marginTop: '4px' }}>

                        {DND_CONDITIONS.find(c => c.id === conditionId)?.description}

                      </div>

                    )}

                  </div>

                  {conditionId === 'exhaustion' && (

                    <div>

                      <label style={labelStyle}>Level (1-6)</label>

                      <input type="number" min="1" max="6" value={exhaustionLevel} onChange={e => setExhaustionLevel(Math.min(6, Math.max(1, parseInt(e.target.value) || 1)))} style={inputStyle} />

                    </div>

                  )}

                </div>

              )}

              

              {/* Text Only */}

              {category === 'custom' && (

                <div style={{ fontSize: '0.85rem', color: c.textMuted || '#888', marginBottom: '8px' }}>

                  Text-only effect: displays as a reminder but has no mechanical automation.

                </div>

              )}

            </div>

          )}

        </>

      )}

      

      {/* Mode: Text Entry */}

      {mode === 'text' && (

        <div style={{ marginBottom: '12px' }}>

          <label style={labelStyle}>Describe the effect</label>

          <input

            type="text"

            value={displayText}

            onChange={e => setDisplayText(e.target.value)}

            style={inputStyle}

            placeholder="e.g., -2 to all saving throws"

          />

          <div style={{ fontSize: '0.75rem', color: c.textMuted || '#888' }}>

            This will display as text only. Use Quick Builder or Syntax mode for mechanical effects.

          </div>

        </div>

      )}

      

      {/* Mode: Syntax */}

      {mode === 'syntax' && (

        <div style={{ marginBottom: '12px' }}>

          <label style={labelStyle}>Effect Syntax</label>

          <input

            type="text"

            value={syntaxInput}

            onChange={e => setSyntaxInput(e.target.value)}

            style={inputStyle}

            placeholder="e.g., str-2, save.all.dis, dmg.fire.resist"

          />

          {parsedEffects.length > 0 && (

            <div style={{ marginTop: '8px', padding: '8px', background: 'rgba(39,201,90,0.1)', borderRadius: '4px', border: '1px solid rgba(39,201,90,0.3)' }}>

              <div style={{ fontSize: '0.75rem', color: '#6bff6b', marginBottom: '4px' }}>✓ Parsed {parsedEffects.length} effect(s):</div>

              {parsedEffects.map((eff, i) => (

                <div key={i} style={{ fontSize: '0.85rem' }}>• {formatEffectDisplay(eff)}</div>

              ))}

            </div>

          )}

          {syntaxInput.trim() && parsedEffects.length === 0 && (

            <div style={{ marginTop: '8px', padding: '8px', background: 'rgba(201,39,39,0.1)', borderRadius: '4px', border: '1px solid rgba(201,39,39,0.3)' }}>

              <div style={{ fontSize: '0.75rem', color: '#ff6b6b' }}>Could not parse syntax. Try: str-2, perception.dis, save.con+2</div>

            </div>

          )}

          <details style={{ marginTop: '8px' }}>

            <summary style={{ fontSize: '0.75rem', color: c.accent || '#c9a227', cursor: 'pointer' }}>Syntax Reference</summary>

            <div style={{ fontSize: '0.7rem', color: c.textMuted || '#888', marginTop: '4px', fontFamily: 'monospace' }}>

              <div>Stats: str+2, dex-1, con=10</div>

              <div>Skills: perception+5, stealth.dis, athletics.adv</div>

              <div>Saves: save.con+2, save.all-1, save.wis.dis</div>

              <div>Attacks: atk.all-1, atk.melee.adv, atk.spell+2</div>

              <div>AC: ac+2, ac-1</div>

              <div>Speed: speed-10, speed=0</div>

              <div>Damage: dmg.fire.resist, dmg.cold.vuln, dmg.poison.immune</div>

              <div>Conditions: cond.poisoned, cond.prone, exhaust=2</div>

              <div>Multiple: str-2, save.all-1, cond.poisoned</div>

            </div>

          </details>

        </div>

      )}

      

      {/* Display Text Override */}

      {(mode === 'quick' && category) && (

        <div style={{ marginBottom: '12px' }}>

          <label style={labelStyle}>Display Text (optional override)</label>

          <input

            type="text"

            value={displayText}

            onChange={e => setDisplayText(e.target.value)}

            style={inputStyle}

            placeholder={buildCurrentEffect().displayText || 'Auto-generated from effect'}

          />

        </div>

      )}

      

      {/* Preview */}

      {(category || mode !== 'quick') && (

        <div style={{ padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px', marginBottom: '12px' }}>

          <div style={{ fontSize: '0.75rem', color: c.textMuted || '#888', marginBottom: '4px' }}>Preview:</div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

            <span style={{ ...S.badge, ...S.badgeDebuff }}>

              {buildCurrentEffect().displayText || 'No effect configured'}

            </span>

          </div>

        </div>

      )}

      

      {/* Actions */}

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>

        <button onClick={onCancel} style={{ ...S.btn, ...S.btnSm }}>Cancel</button>

        <button 

          onClick={handleAdd} 

          style={{ ...S.btn, ...S.btnSm, ...S.btnSuccess }}

          disabled={!buildCurrentEffect().displayText.trim() && buildCurrentEffect().mechanics.length === 0}

        >

          Add Effect

        </button>

      </div>

    </div>

  );

};



// ============================================================================

// TRACKER WIDGET - For editing tracker values in System Manager

// ============================================================================



export const TrackerWidget = ({ field, value, onChange, isPlayer = false, visibility = {} }) => {

  const fieldType = field?.type || 'number';

  const currentValue = value ?? field?.value ?? 0;

  const min = field?.min;

  const max = field?.max;

  const step = field?.step || 1;

  const allowDecimalInput = [currentValue, min, max, step].some((numericValue) =>
    Number.isFinite(numericValue) && !Number.isInteger(numericValue)
  );

  

  // Visibility settings (default to showing everything to DM, respecting config for players)

  const showToPlayer = visibility.showToPlayer !== false;

  const showBar = visibility.showBar !== false;

  const showValue = visibility.showValue !== false;

  const showSource = visibility.showSource !== false;

  

  // Don't show to player if not enabled

  if (isPlayer && !showToPlayer) return null;

  

  const clamp = (v) => {

    let result = v;

    if (min !== null && min !== undefined) result = Math.max(min, result);

    if (max !== null && max !== undefined) result = Math.min(max, result);

    return result;

  };

  

  const handleChange = (newVal) => {

    onChange(clamp(newVal));

  };

  

  const progressPercent = max ? Math.min(100, (currentValue / max) * 100) : 0;

  

  // Get all active thresholds (sorted by value descending)

  const activeThresholds = (field?.thresholds || [])

    .filter(t => currentValue >= t.value)

    .sort((a, b) => b.value - a.value);

  const highestThreshold = activeThresholds[0];

  

  // Get all active effects

  const activeEffects = activeThresholds.flatMap(t => t.effects || (t.effect ? [t.effect] : []));

  

  const baseStyle = {

    padding: '8px',

    background: 'rgba(0,0,0,0.15)',

    borderRadius: '6px',

    border: `1px solid ${activeThresholds.length > 0 ? '#ff696140' : (field?.color || '#c9a227') + '40'}`,

  };

  

  // Player view with limited visibility

  if (isPlayer) {

    // Player sees publicLabel if set; DM-facing `label` is the fallback. P3 applied to trackers.

    const displayLabel = field?.publicLabel || field?.label || 'Value';

    return (

      <div style={baseStyle}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showBar ? '6px' : 0 }}>

          <span style={{ fontWeight: 'bold', color: field?.color || '#c9a227' }}>

            {displayLabel}

          </span>

          {showValue && (

            <span style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>

              {currentValue}{max ? `/${max}` : ''}

            </span>

          )}

          {!showValue && max && (

            <span style={{ fontSize: '0.75rem', opacity: 0.6 }}>?</span>

          )}

        </div>

        

        {/* Progress Bar (if enabled) */}

        {showBar && max && (

          <div style={{ 

            height: '12px', 

            background: 'rgba(0,0,0,0.3)', 

            borderRadius: '6px', 

            overflow: 'hidden',

            border: '1px solid rgba(255,255,255,0.1)',

          }}>

            <div style={{

              height: '100%',

              width: `${progressPercent}%`,

              background: highestThreshold?.color || field?.color || 'linear-gradient(90deg, #c9a227, #e6c64a)',

              transition: 'width 0.3s ease',

            }} />

          </div>

        )}

        

        {/* Threshold warning (but maybe hide source) */}

        {highestThreshold && (

          <div style={{ marginTop: '4px', fontSize: '0.75rem', color: '#ff6961' }}>

            ⚠️ {showSource ? (highestThreshold.label || `Level ${highestThreshold.value}`) : 'Effect Active'}

          </div>

        )}

      </div>

    );

  }

  

  // DM view (full controls)

  return (

    <div style={baseStyle}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>

        <span style={{ fontWeight: 'bold', color: field?.color || '#c9a227' }}>

          {field?.label || 'Value'}

        </span>

        {highestThreshold && (

          <span style={{ fontSize: '0.75rem', color: '#ff6961' }}>

            ⚠️ {highestThreshold.label || `Level ${highestThreshold.value}`}

          </span>

        )}

      </div>

      

      {/* Active Effects Display */}

      {activeEffects.length > 0 && !isPlayer && (

        <div style={{ marginBottom: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>

          {activeEffects.map((eff, i) => (

            <span key={i} style={{ ...S.badge, ...S.badgeDebuff, fontSize: '0.7rem' }}>

              {eff}

            </span>

          ))}

        </div>

      )}

      

      {/* Number / Counter */}

      {(fieldType === 'number' || fieldType === 'counter') && (

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

          {false && fieldType === 'counter' && !isPlayer && (

            <button

              onClick={() => handleChange(currentValue - step)}

              style={{ ...S.btn, ...S.btnXs, width: '32px', height: '32px', padding: 0, marginBottom: 0 }}

              disabled={min !== null && currentValue <= min}

            >

              −

            </button>

          )}

          {fieldType === 'counter' && !isPlayer && (

            <QuantityStepper
              value={currentValue}
              onCommit={handleChange}
              min={min ?? undefined}
              max={max ?? undefined}
              step={step}
              allowDecimal={allowDecimalInput}
              emptyValue={0}
              inputLabel={`${field?.label || 'Tracker'} value`}
              decrementAriaLabel={`Decrease ${field?.label || 'tracker'} value`}
              incrementAriaLabel={`Increase ${field?.label || 'tracker'} value`}
              buttonStyle={{ ...S.btn, ...S.btnXs }}
              inputStyle={{ width: '80px', minWidth: '80px', fontSize: '1.1rem', fontWeight: 'bold' }}
            />

          )}

          {!(fieldType === 'counter' && !isPlayer) && (

          <DebouncedInput

            as="input"

            type="number"

            value={currentValue}

            onCommit={v => handleChange(parseFloat(v) || 0)}

            min={min ?? undefined}

            max={max ?? undefined}

            step={step}

            disabled={isPlayer}

            style={{

              ...S.input,

              width: '80px',

              textAlign: 'center',

              marginBottom: 0,

              fontSize: '1.1rem',

              fontWeight: 'bold',

            }}

          />

          )}

          {false && fieldType === 'counter' && !isPlayer && (

            <button

              onClick={() => handleChange(currentValue + step)}

              style={{ ...S.btn, ...S.btnXs, width: '32px', height: '32px', padding: 0, marginBottom: 0 }}

              disabled={max !== null && currentValue >= max}

            >

              +

            </button>

          )}

          {max !== null && (

            <span style={{ opacity: 0.6, fontSize: '0.85rem' }}>/ {max}</span>

          )}

        </div>

      )}

      

      {/* Progress Bar */}

      {fieldType === 'progress' && (

        <div>

          <div style={{ 

            height: '24px', 

            background: 'rgba(0,0,0,0.3)', 

            borderRadius: '4px', 

            overflow: 'hidden',

            position: 'relative',

          }}>

            {/* Threshold markers */}

            {max && (field?.thresholds || []).map((t, i) => {

              const pos = (t.value / max) * 100;

              const isActive = currentValue >= t.value;

              return (

                <div

                  key={t.id || i}

                  style={{

                    position: 'absolute',

                    left: `${pos}%`,

                    top: 0,

                    bottom: 0,

                    width: '2px',

                    background: isActive ? '#ff6961' : 'rgba(255,255,255,0.3)',

                    zIndex: 2,

                  }}

                  title={`${t.label || t.value}: ${t.effect || 'threshold'}`}

                />

              );

            })}

            {/* Progress fill */}

            <div style={{ 

              height: '100%', 

              width: `${progressPercent}%`,

              background: activeThresholds.length > 0 

                ? `linear-gradient(90deg, ${field?.color || '#c9a227'}, #ff6961)`

                : `linear-gradient(90deg, ${field?.color || '#c9a227'}, ${field?.color || '#c9a227'}dd)`,

              transition: 'width 0.3s ease',

            }} />

            {/* Value text */}

            <div style={{ 

              position: 'absolute', 

              top: '50%', 

              left: '50%', 

              transform: 'translate(-50%, -50%)',

              fontWeight: 'bold',

              fontSize: '0.85rem',

              textShadow: '0 1px 2px rgba(0,0,0,0.5)',

              zIndex: 3,

            }}>

              {currentValue} / {max || '?'}

            </div>

          </div>

          {!isPlayer && (
            <>

            <div style={{ display: 'flex', gap: '8px', marginTop: '6px', alignItems: 'center', flexWrap: 'wrap' }}>

              <QuantityStepper
                value={currentValue}
                onCommit={handleChange}
                min={min ?? undefined}
                max={max ?? undefined}
                step={step}
                allowDecimal={allowDecimalInput}
                emptyValue={0}
                inputLabel={`${field?.label || 'Tracker'} progress value`}
                decrementLabel={`-${step}`}
                incrementLabel={`+${step}`}
                decrementAriaLabel={`Decrease ${field?.label || 'tracker'} progress value`}
                incrementAriaLabel={`Increase ${field?.label || 'tracker'} progress value`}
                style={{ gap: '8px', flex: 1 }}
                buttonStyle={{ ...S.btn, ...S.btnXs, marginBottom: 0 }}
                inputStyle={{ width: '72px', minWidth: '72px', fontSize: '0.95rem', fontWeight: 'bold' }}
              />

              <button onClick={() => handleChange(0)} style={{ ...S.btn, ...S.btnXs, marginBottom: 0 }}>Reset</button>

              {max && <button onClick={() => handleChange(max)} style={{ ...S.btn, ...S.btnXs, marginBottom: 0 }}>Max</button>}

            </div>

            {false && (

            <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>

              <button onClick={() => handleChange(currentValue - step)} style={{ ...S.btn, ...S.btnXs, flex: 1, marginBottom: 0 }}>−{step}</button>

              <button onClick={() => handleChange(currentValue + step)} style={{ ...S.btn, ...S.btnXs, flex: 1, marginBottom: 0 }}>+{step}</button>

              <button onClick={() => handleChange(0)} style={{ ...S.btn, ...S.btnXs, marginBottom: 0 }}>Reset</button>

              {max && <button onClick={() => handleChange(max)} style={{ ...S.btn, ...S.btnXs, marginBottom: 0 }}>Max</button>}

            </div>

            )}

            </>

          )}

        </div>

      )}

      

      {/* Timer */}

      {fieldType === 'timer' && (

        <div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

            <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: currentValue <= 1 ? '#ff6961' : (field?.color || '#c9a227') }}>

              {currentValue}

            </span>

            <span style={{ opacity: 0.7 }}>{field?.unit || 'days'} remaining</span>

          </div>

          {!isPlayer && (

            <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>

              <button onClick={() => handleChange(currentValue - 1)} style={{ ...S.btn, ...S.btnXs, marginBottom: 0 }}>−1</button>

              <button onClick={() => handleChange(currentValue + 1)} style={{ ...S.btn, ...S.btnXs, marginBottom: 0 }}>+1</button>

              <input

                type="number"

                value={currentValue}

                onChange={e => handleChange(parseInt(e.target.value) || 0)}

                style={{ ...S.input, width: '60px', marginBottom: 0, textAlign: 'center' }}

              />

            </div>

          )}

        </div>

      )}

      

      {/* Checkbox */}

      {fieldType === 'checkbox' && (

        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: isPlayer ? 'default' : 'pointer' }}>

          <input

            type="checkbox"

            checked={!!currentValue}

            onChange={e => handleChange(e.target.checked ? 1 : 0)}

            disabled={isPlayer}

            style={{ width: '20px', height: '20px' }}

          />

          <span style={{ opacity: currentValue ? 1 : 0.6 }}>

            {currentValue ? '✓ Yes' : '✗ No'}

          </span>

        </label>

      )}

      

      {/* Select / Dropdown */}

      {fieldType === 'select' && (

        <select

          value={currentValue || ''}

          onChange={e => handleChange(e.target.value)}

          disabled={isPlayer}

          style={{ ...S.select, marginBottom: 0 }}

        >

          <option value="">Select...</option>

          {(field?.options || []).map((opt, i) => (

            <option key={i} value={opt.value || opt}>{opt.label || opt}</option>

          ))}

        </select>

      )}

      

      {/* Thresholds indicator */}

      {(field?.thresholds || []).length > 0 && !isPlayer && (

        <div style={{ marginTop: '6px', fontSize: '0.7rem', opacity: 0.6 }}>

          Thresholds: {field.thresholds.map(t => `${t.value}→${t.effect}`).join(', ')}

        </div>

      )}

    </div>

  );

};



// ============================================================================

// AUTOMATION RULES EDITOR (Simplified)

// ============================================================================



// Condition Editor Sub-component

const ConditionEditor = ({ condition, index, trackerFields, onUpdate, onRemove }) => {

  const conditionInfo = RULE_CONDITIONS.find(c => c.id === condition.type);

  const params = condition.params || {};

  

  return (

    <div style={{ 

      padding: '8px', 

      background: 'rgba(74,144,217,0.1)', 

      borderRadius: '4px', 

      border: '1px solid rgba(74,144,217,0.2)',

      marginBottom: '6px',

    }}>

      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>

        <select

          style={{ ...S.select, width: '140px', marginBottom: 0 }}

          value={condition.type}

          onChange={e => onUpdate({ ...condition, type: e.target.value, params: {} })}

        >

          {RULE_CONDITIONS.map(c => (

            <option key={c.id} value={c.id}>{c.label}</option>

          ))}

        </select>

        

        {/* Stat Check Parameters */}

        {condition.type === 'stat-check' && (

          <>

            <select

              style={{ ...S.select, width: '80px', marginBottom: 0 }}

              value={params.stat || 'con'}

              onChange={e => onUpdate({ ...condition, params: { ...params, stat: e.target.value }})}

            >

              {['str', 'dex', 'con', 'int', 'wis', 'cha'].map(s => (

                <option key={s} value={s}>{s.toUpperCase()}</option>

              ))}

            </select>

            <select

              style={{ ...S.select, width: '60px', marginBottom: 0 }}

              value={params.operator || 'gte'}

              onChange={e => onUpdate({ ...condition, params: { ...params, operator: e.target.value }})}

            >

              <option value="gte">≥</option>

              <option value="gt">&gt;</option>

              <option value="lte">≤</option>

              <option value="lt">&lt;</option>

              <option value="eq">=</option>

            </select>

            <input

              type="number"

              style={{ ...S.input, width: '60px', marginBottom: 0 }}

              value={params.value || 10}

              onChange={e => onUpdate({ ...condition, params: { ...params, value: e.target.value }})}

            />

          </>

        )}

        

        {/* HP Percent Parameters */}

        {condition.type === 'hp-percent' && (

          <>

            <span style={{ fontSize: '0.85rem' }}>HP</span>

            <select

              style={{ ...S.select, width: '60px', marginBottom: 0 }}

              value={params.operator || 'lte'}

              onChange={e => onUpdate({ ...condition, params: { ...params, operator: e.target.value }})}

            >

              <option value="gte">≥</option>

              <option value="gt">&gt;</option>

              <option value="lte">≤</option>

              <option value="lt">&lt;</option>

            </select>

            <input

              type="number"

              style={{ ...S.input, width: '60px', marginBottom: 0 }}

              value={params.value || 50}

              onChange={e => onUpdate({ ...condition, params: { ...params, value: e.target.value }})}

            />

            <span style={{ fontSize: '0.85rem' }}>%</span>

          </>

        )}

        

        {/* Tracker Value Parameters */}

        {condition.type === 'tracker-value' && (

          <>

            <select

              style={{ ...S.select, width: '120px', marginBottom: 0 }}

              value={params.trackerId || ''}

              onChange={e => onUpdate({ ...condition, params: { ...params, trackerId: e.target.value }})}

            >

              <option value="">Select tracker...</option>

              {(trackerFields || []).map(f => (

                <option key={f.id} value={f.id}>{f.label}</option>

              ))}

            </select>

            <select

              style={{ ...S.select, width: '60px', marginBottom: 0 }}

              value={params.operator || 'gte'}

              onChange={e => onUpdate({ ...condition, params: { ...params, operator: e.target.value }})}

            >

              <option value="gte">≥</option>

              <option value="gt">&gt;</option>

              <option value="lte">≤</option>

              <option value="lt">&lt;</option>

              <option value="eq">=</option>

            </select>

            <input

              type="number"

              style={{ ...S.input, width: '60px', marginBottom: 0 }}

              value={params.value || 0}

              onChange={e => onUpdate({ ...condition, params: { ...params, value: e.target.value }})}

            />

          </>

        )}

        

        {/* Roll Check Parameters */}

        {condition.type === 'roll-check' && (

          <>

            <select

              style={{ ...S.select, width: '80px', marginBottom: 0 }}

              value={params.stat || 'con'}

              onChange={e => onUpdate({ ...condition, params: { ...params, stat: e.target.value }})}

            >

              {['str', 'dex', 'con', 'int', 'wis', 'cha'].map(s => (

                <option key={s} value={s}>{s.toUpperCase()}</option>

              ))}

            </select>

            <span style={{ fontSize: '0.85rem' }}>save DC</span>

            <input

              type="number"

              style={{ ...S.input, width: '60px', marginBottom: 0 }}

              value={params.dc || 10}

              onChange={e => onUpdate({ ...condition, params: { ...params, dc: e.target.value }})}

            />

          </>

        )}

        

        {/* Random Chance Parameters */}

        {condition.type === 'random-chance' && (

          <>

            <input

              type="number"

              style={{ ...S.input, width: '60px', marginBottom: 0 }}

              value={params.percent || 50}

              onChange={e => onUpdate({ ...condition, params: { ...params, percent: e.target.value }})}

            />

            <span style={{ fontSize: '0.85rem' }}>% chance</span>

          </>

        )}

        

        <button

          onClick={onRemove}

          style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, marginBottom: 0, marginLeft: 'auto' }}

        >✕</button>

      </div>

      {conditionInfo?.description && (

        <div style={{ fontSize: '0.7rem', opacity: 0.6, marginTop: '4px' }}>

          {conditionInfo.description}

        </div>

      )}

    </div>

  );

};



// Action Editor Sub-component

const ActionEditor = ({ action, index, trackerFields, onUpdate, onRemove }) => {

  const actionInfo = RULE_ACTIONS.find(a => a.id === action.type);

  const params = action.params || {};

  

  return (

    <div style={{ 

      padding: '8px', 

      background: 'rgba(201,162,39,0.1)', 

      borderRadius: '4px', 

      border: '1px solid rgba(201,162,39,0.2)',

      marginBottom: '6px',

    }}>

      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>

        <select

          style={{ ...S.select, width: '150px', marginBottom: 0 }}

          value={action.type}

          onChange={e => onUpdate({ ...action, type: e.target.value, params: {} })}

        >

          {RULE_ACTIONS.map(a => (

            <option key={a.id} value={a.id}>{a.label}</option>

          ))}

        </select>

        

        {/* Add/Remove Effect Parameters */}

        {(action.type === 'add-effect' || action.type === 'remove-effect') && (

          <input

            style={{ ...S.input, flex: 1, minWidth: '150px', marginBottom: 0 }}

            value={params.effect || ''}

            onChange={e => onUpdate({ ...action, params: { ...params, effect: e.target.value }})}

            placeholder="e.g., -2 Str, Disadvantage on Perception"

          />

        )}

        

        {/* Deal Damage / Heal Parameters */}

        {(action.type === 'deal-damage' || action.type === 'heal') && (

          <>

            <input

              type="number"

              style={{ ...S.input, width: '60px', marginBottom: 0 }}

              value={params.amount || 1}

              onChange={e => onUpdate({ ...action, params: { ...params, amount: e.target.value }})}

            />

            {action.type === 'deal-damage' && (

              <select

                style={{ ...S.select, width: '100px', marginBottom: 0 }}

                value={params.type || 'necrotic'}

                onChange={e => onUpdate({ ...action, params: { ...params, type: e.target.value }})}

              >

                {['slashing', 'piercing', 'bludgeoning', 'fire', 'cold', 'lightning', 'acid', 'poison', 'necrotic', 'radiant', 'psychic', 'force'].map(t => (

                  <option key={t} value={t}>{t}</option>

                ))}

              </select>

            )}

            <span style={{ fontSize: '0.85rem' }}>{action.type === 'deal-damage' ? 'damage' : 'HP'}</span>

          </>

        )}

        

        {/* Exhaustion Parameters */}

        {(action.type === 'add-exhaustion' || action.type === 'remove-exhaustion') && (

          <>

            <input

              type="number"

              style={{ ...S.input, width: '60px', marginBottom: 0 }}

              value={params.levels || 1}

              min={1}

              max={6}

              onChange={e => onUpdate({ ...action, params: { ...params, levels: e.target.value }})}

            />

            <span style={{ fontSize: '0.85rem' }}>level(s)</span>

          </>

        )}

        

        {/* Change Tracker Parameters */}

        {action.type === 'change-tracker' && (

          <>

            <select

              style={{ ...S.select, width: '120px', marginBottom: 0 }}

              value={params.trackerId || ''}

              onChange={e => onUpdate({ ...action, params: { ...params, trackerId: e.target.value }})}

            >

              <option value="">Select tracker...</option>

              {(trackerFields || []).map(f => (

                <option key={f.id} value={f.id}>{f.label}</option>

              ))}

            </select>

            <select

              style={{ ...S.select, width: '90px', marginBottom: 0 }}

              value={params.operation || 'add'}

              onChange={e => onUpdate({ ...action, params: { ...params, operation: e.target.value }})}

            >

              <option value="add">Add</option>

              <option value="subtract">Subtract</option>

              <option value="set">Set to</option>

              <option value="multiply">Multiply</option>

            </select>

            <input

              type="number"

              style={{ ...S.input, width: '60px', marginBottom: 0 }}

              value={params.value || 1}

              onChange={e => onUpdate({ ...action, params: { ...params, value: e.target.value }})}

            />

          </>

        )}

        

        {/* Notify Parameters */}

        {action.type === 'notify' && (

          <input

            style={{ ...S.input, flex: 1, minWidth: '200px', marginBottom: 0 }}

            value={params.message || ''}

            onChange={e => onUpdate({ ...action, params: { ...params, message: e.target.value }})}

            placeholder="Notification message..."

          />

        )}

        

        {/* Log Parameters */}

        {action.type === 'log' && (

          <input

            style={{ ...S.input, flex: 1, minWidth: '200px', marginBottom: 0 }}

            value={params.message || ''}

            onChange={e => onUpdate({ ...action, params: { ...params, message: e.target.value }})}

            placeholder="Log message..."

          />

        )}

        

        <button

          onClick={onRemove}

          style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, marginBottom: 0, marginLeft: 'auto' }}

        >✕</button>

      </div>

      {actionInfo?.description && (

        <div style={{ fontSize: '0.7rem', opacity: 0.6, marginTop: '4px' }}>

          {actionInfo.description}

        </div>

      )}

    </div>

  );

};



const RuleEditor = ({ rule, trackerFields, onUpdate, onDelete }) => {

  const [expanded, setExpanded] = useState(false);

  

  const triggerInfo = RULE_TRIGGERS.find(t => t.id === rule.trigger);

  const conditions = rule.conditions || [];

  const actions = rule.actions || [];

  

  const addCondition = () => {

    onUpdate({ conditions: [...conditions, { type: 'always', params: {} }] });

  };

  

  const updateCondition = (index, updated) => {

    const newConditions = [...conditions];

    newConditions[index] = updated;

    onUpdate({ conditions: newConditions });

  };

  

  const removeCondition = (index) => {

    onUpdate({ conditions: conditions.filter((_, i) => i !== index) });

  };

  

  const addAction = () => {

    onUpdate({ actions: [...actions, { type: 'notify', params: { message: 'Rule triggered!' } }] });

  };

  

  const updateAction = (index, updated) => {

    const newActions = [...actions];

    newActions[index] = updated;

    onUpdate({ actions: newActions });

  };

  

  const removeAction = (index) => {

    onUpdate({ actions: actions.filter((_, i) => i !== index) });

  };

  

  // Summary text for collapsed view

  const getSummary = () => {

    const parts = [];

    if (conditions.length > 0 && conditions[0].type !== 'always') {

      parts.push(`${conditions.length} condition${conditions.length > 1 ? 's' : ''}`);

    }

    parts.push(`${actions.length} action${actions.length !== 1 ? 's' : ''}`);

    return parts.join(', ');

  };

  

  return (

    <div style={{ 

      padding: '10px', 

      background: 'rgba(0,0,0,0.15)', 

      borderRadius: '6px', 

      border: `1px solid ${rule.enabled ? 'rgba(139,226,139,0.3)' : 'rgba(255,255,255,0.1)'}`,

      marginBottom: '8px',

    }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>

          <input 

            type="checkbox" 

            checked={rule.enabled} 

            onChange={e => { e.stopPropagation(); onUpdate({ enabled: e.target.checked }); }}

            onClick={e => e.stopPropagation()}

          />

          <span style={{ fontWeight: 'bold', color: rule.enabled ? '#8be28b' : '#888' }}>

            {rule.name || 'Unnamed Rule'}

          </span>

          <span style={{ fontSize: '0.75rem', opacity: 0.6 }}>

            ({triggerInfo?.label || rule.trigger})

          </span>

          {!expanded && (

            <span style={{ fontSize: '0.75rem', opacity: 0.5, marginLeft: '8px' }}>

              — {getSummary()}

            </span>

          )}

          <span style={{ marginLeft: 'auto', opacity: 0.5 }}>{expanded ? '▲' : '▼'}</span>

        </div>

        <button onClick={onDelete} style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, marginBottom: 0, marginLeft: '8px' }}>🗑️</button>

      </div>

      

      {expanded && (

        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>

          {/* Basic Info */}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>

            <div>

              <label style={S.label}>Rule Name</label>

              <input

                style={S.input}

                value={rule.name || ''}

                onChange={e => onUpdate({ name: e.target.value })}

                placeholder="e.g., Daily Con Save"

              />

            </div>

            <div>

              <label style={S.label}>Trigger</label>

              <select

                style={S.select}

                value={rule.trigger || 'manual'}

                onChange={e => onUpdate({ trigger: e.target.value })}

              >

                {RULE_TRIGGERS.map(t => (

                  <option key={t.id} value={t.id}>{t.label}</option>

                ))}

              </select>

            </div>

          </div>

          

          {triggerInfo?.description && (

            <div style={{ fontSize: '0.75rem', opacity: 0.6, marginBottom: '12px', padding: '6px 8px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px' }}>

              ℹ️ {triggerInfo.description}

            </div>

          )}

          

          {/* Conditions Section */}

          <div style={{ marginBottom: '12px' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>

              <label style={{ ...S.label, marginBottom: 0, color: '#4a90d9' }}>

                🔍 Conditions {conditions.length > 1 && (

                  <select

                    style={{ ...S.select, width: '70px', marginLeft: '8px', marginBottom: 0, padding: '2px 4px', fontSize: '0.75rem' }}

                    value={rule.conditionLogic || 'all'}

                    onChange={e => onUpdate({ conditionLogic: e.target.value })}

                  >

                    <option value="all">ALL</option>

                    <option value="any">ANY</option>

                  </select>

                )}

              </label>

              <button

                onClick={addCondition}

                style={{ ...S.btn, ...S.btnSuccess, ...S.btnXs, marginBottom: 0 }}

              >

                + Condition

              </button>

            </div>

            

            {conditions.length === 0 ? (

              <div style={{ fontSize: '0.85rem', opacity: 0.6, padding: '8px', background: 'rgba(0,0,0,0.1)', borderRadius: '4px' }}>

                No conditions — rule will always fire when triggered

              </div>

            ) : (

              conditions.map((cond, i) => (

                <ConditionEditor

                  key={i}

                  condition={cond}

                  index={i}

                  trackerFields={trackerFields}

                  onUpdate={(updated) => updateCondition(i, updated)}

                  onRemove={() => removeCondition(i)}

                />

              ))

            )}

          </div>

          

          {/* Actions Section */}

          <div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>

              <label style={{ ...S.label, marginBottom: 0, color: '#c9a227' }}>⚡ Actions</label>

              <button

                onClick={addAction}

                style={{ ...S.btn, ...S.btnSuccess, ...S.btnXs, marginBottom: 0 }}

              >

                + Action

              </button>

            </div>

            

            {actions.length === 0 ? (

              <div style={{ fontSize: '0.85rem', opacity: 0.6, padding: '8px', background: 'rgba(0,0,0,0.1)', borderRadius: '4px' }}>

                No actions configured — add at least one action

              </div>

            ) : (

              actions.map((act, i) => (

                <ActionEditor

                  key={i}

                  action={act}

                  index={i}

                  trackerFields={trackerFields}

                  onUpdate={(updated) => updateAction(i, updated)}

                  onRemove={() => removeAction(i)}

                />

              ))

            )}

          </div>

        </div>

      )}

    </div>

  );

};



// ============================================================================

// TECHNIQUE EDITOR (Session 3 — spell-resource architecture)

// ============================================================================

// Self-contained editor for one Technique. Split into collapsible sub-panels so a DM who

// just wants to tweak a cost doesn't scroll through availability + privacy + rules.

//

// Sub-panels: Binding (library or inline spell), Costs, Requirements, Availability, Rules,

// Privacy. Each section is independently collapsible; the header is always visible.

//

// Props:

//   technique          — the technique object (createTechnique shape)

//   currentSystemId    — id of the system this technique belongs to (default for refs)

//   currentSystem      — the system template (for tracker dropdowns in same system)

//   allSystems         — { systemId → template } for cross-system references

//   spellLibrary       — array of full spells for library binding

//   locations          — string[] for environment multi-select in availability

//   onUpdate(patch)    — merge patch into technique

//   onDelete()         — remove this technique

// Quick Spell Creator — a focused modal for creating a library spell from within the

// technique editor flow. Covers the essential fields (core identity, casting, effect,

// damage, target, description). Produces a valid spell via `createSpell()` and hands it

// back via onCreate — caller is responsible for appending to spellLibrary and binding.

// For advanced fields (upcast patterns, DoT/HoT, condition application, per-level overrides)

// DMs edit the created spell in the Spell Library. This modal is intentionally NOT the full

// spell editor — it's the path of least resistance for creating a new spell to bind to a

// technique without leaving the system template editor.

const QuickSpellCreatorModal = ({ initialName = '', onCreate, onCancel }) => {

  const [spell, setSpell] = useState(() => createSpell({

    name: initialName || 'New Spell',

    effectType: 'damage',

    attackType: 'ranged',

    targetType: 'single',

  }));

  const [comp, setCompState] = useState(() => migrateComponents(spell.components));

  const setComp = (patch) => {

    const next = { ...comp, ...patch };

    setCompState(next);

    setSpell(s => ({ ...s, components: formatComponents(next) }));

  };

  const patch = (p) => setSpell(s => ({ ...s, ...p }));

  const nameTrimmed = (spell.name || '').trim();

  const isSaveable = nameTrimmed.length > 0;



  const handleCreate = () => {

    if (!isSaveable) return;

    // Normalize: trim name, strip tags array

    const tagsArr = typeof spell.tags === 'string'

      ? spell.tags.split(',').map(t => t.trim()).filter(Boolean)

      : (spell.tags || []);

    onCreate({ ...spell, name: nameTrimmed, tags: tagsArr });

  };



  const label = (text) => <label style={{ ...S.label, fontSize: '0.68rem' }}>{text}</label>;

  const sectionHeader = (text) => (

    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#c9a227', marginTop: '10px', marginBottom: '6px', borderBottom: '1px solid rgba(201,162,39,0.2)', paddingBottom: '3px' }}>{text}</div>

  );



  const needsDamage = ['damage', 'healing', 'temp_hp'].includes(spell.effectType);

  const isSave = spell.attackType === 'save';



  return (

    <div style={{ ...S.modal, zIndex: 20000 }} onClick={onCancel}>

      <div

        style={{ ...S.modalContent, maxWidth: '640px', maxHeight: '85vh', overflowY: 'auto' }}

        onClick={e => e.stopPropagation()}

      >

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>

          <h3 style={{ color: '#9b59b6', margin: 0 }}>✨ Create New Spell</h3>

          <button style={{ ...S.btn, ...S.btnXs }} onClick={onCancel}>✕</button>

        </div>

        <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '10px' }}>

          This creates a library spell and binds it to the technique. For advanced fields

          (upcast, DoT, conditions, per-level overrides) open the spell in the Spell Library after saving.

        </div>



        {/* Core identity */}

        {sectionHeader('Core')}

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px' }}>

          <div>

            {label('Name *')}

            <input

              style={S.input}

              value={spell.name}

              onChange={e => patch({ name: e.target.value })}

              autoFocus

            />

          </div>

          <div>

            {label('Level')}

            <select style={S.select} value={spell.level} onChange={e => patch({ level: parseInt(e.target.value) })}>

              <option value={0}>Cantrip</option>

              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(l => <option key={l} value={l}>{l}</option>)}

            </select>

          </div>

          <div>

            {label('School')}

            <select style={S.select} value={spell.school} onChange={e => patch({ school: e.target.value })}>

              {SPELL_SCHOOLS.map(s => <option key={s.id} value={s.id}>{s.icon} {s.name}</option>)}

            </select>

          </div>

        </div>

        <div style={{ display: 'flex', gap: '14px', marginTop: '6px', flexWrap: 'wrap', fontSize: '0.75rem' }}>

          <label style={{ cursor: 'pointer' }}><input type="checkbox" checked={spell.ritual} onChange={e => patch({ ritual: e.target.checked })} /> Ritual</label>

          <label style={{ cursor: 'pointer' }}><input type="checkbox" checked={spell.concentration} onChange={e => patch({ concentration: e.target.checked })} /> Concentration</label>

          <label style={{ cursor: 'pointer' }}><input type="checkbox" checked={!!spell.addMod} onChange={e => patch({ addMod: e.target.checked })} /> + Spell Mod</label>

        </div>



        {/* Casting */}

        {sectionHeader('Casting')}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>

          <div>

            {label('Casting Time')}

            <select style={S.select} value={spell.castingTime} onChange={e => patch({ castingTime: e.target.value })}>

              {Object.entries(CASTING_TIMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}

            </select>

          </div>

          <div>

            {label('Range')}

            <input style={S.input} value={spell.range || ''} onChange={e => patch({ range: e.target.value })} placeholder="30 ft, Touch, Self..." />

          </div>

          <div>

            {label('Duration')}

            <input style={S.input} value={spell.duration || ''} onChange={e => patch({ duration: e.target.value })} placeholder="Instantaneous, 1 min..." />

          </div>

        </div>

        <div style={{ marginTop: '6px' }}>

          {label('Components')}

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', fontSize: '0.72rem' }}>

            <label style={{ cursor: 'pointer' }}><input type="checkbox" checked={comp.v} onChange={e => setComp({ v: e.target.checked })} /> V</label>

            <label style={{ cursor: 'pointer' }}><input type="checkbox" checked={comp.s} onChange={e => setComp({ s: e.target.checked })} /> S</label>

            <label style={{ cursor: 'pointer' }}><input type="checkbox" checked={!!comp.m} onChange={e => setComp({ m: e.target.checked ? 'material' : '' })} /> M</label>

            {comp.m && (

              <input

                style={{ ...S.input, flex: 1, marginBottom: 0, minWidth: '140px' }}

                value={comp.m}

                onChange={e => setComp({ m: e.target.value })}

                placeholder="Material description..."

              />

            )}

          </div>

        </div>



        {/* Effect & Targeting */}

        {sectionHeader('Effect & Targeting')}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>

          <div>

            {label('Effect Type')}

            <select style={S.select} value={spell.effectType || ''} onChange={e => patch({ effectType: e.target.value })}>

              <option value="">— none / utility —</option>

              <option value="damage">Damage</option>

              <option value="healing">Healing</option>

              <option value="temp_hp">Temp HP</option>

              <option value="buff">Buff</option>

              <option value="debuff">Debuff</option>

              <option value="control">Control</option>

              <option value="utility">Utility</option>

              <option value="summon">Summon</option>

            </select>

          </div>

          <div>

            {label('Attack Type')}

            <select style={S.select} value={spell.attackType || 'none'} onChange={e => patch({ attackType: e.target.value })}>

              <option value="none">None / auto-hit</option>

              <option value="melee">Melee Attack</option>

              <option value="ranged">Ranged Attack</option>

              <option value="save">Saving Throw</option>

            </select>

          </div>

          {isSave && (

            <>

              <div>

                {label('Save Ability')}

                <select style={S.select} value={spell.saveAbility || ''} onChange={e => patch({ saveAbility: e.target.value })}>

                  <option value="">— select —</option>

                  {['str','dex','con','int','wis','cha'].map(a => <option key={a} value={a}>{a.toUpperCase()}</option>)}

                </select>

              </div>

              <div>

                {label('Save DC')}

                <NumberField
                  style={S.input}
                  value={spell.saveDC}
                  onCommit={(value) => patch({ saveDC: value })}
                  format={formatZeroAsBlank}
                  emptyValue={0}
                  min={0}
                  placeholder="auto (8 + prof + spell mod)"
                  title="Leave empty to auto-calculate from the caster's proficiency bonus and spellcasting ability mod (5e standard)."
                />

              </div>

              <div style={{ gridColumn: '1 / span 2' }}>

                {label('On Save')}

                <select style={S.select} value={spell.saveEffect || 'negates'} onChange={e => patch({ saveEffect: e.target.value })}>

                  <option value="negates">Negates (no effect on save)</option>

                  <option value="half">Half damage</option>

                  <option value="special">Special (describe)</option>

                  <option value="none">No save possible</option>

                </select>

              </div>

            </>

          )}

          <div>

            {label('Target Type')}

            <select style={S.select} value={spell.targetType || 'single'} onChange={e => patch({ targetType: e.target.value })}>

              <option value="self">Self</option>

              <option value="touch">Touch</option>

              <option value="single">Single target</option>

              <option value="multi">Multi-target</option>

              <option value="aoe">AoE</option>

              <option value="special">Special</option>

            </select>

          </div>

          <div>

            {label('Max Targets')}

            <NumberField
              style={S.input}
              value={spell.maxTargets ?? 1}
              onCommit={(value) => patch({ maxTargets: value })}
              emptyValue={1}
              min={0}

            />

          </div>

        </div>



        {/* Damage */}

        {needsDamage && (

          <>

            {sectionHeader('Damage / Healing')}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>

              <div>

                {label('Dice')}

                <input

                  style={S.input}

                  value={spell.dice || ''}

                  onChange={e => patch({ dice: e.target.value })}

                  placeholder="e.g. 2d10, 3d6+2"

                />

              </div>

              <div>

                {label(spell.effectType === 'damage' ? 'Damage Type' : 'Type')}

                <input

                  style={S.input}

                  value={spell.damageType || ''}

                  onChange={e => patch({ damageType: e.target.value })}

                  placeholder="fire, necrotic, force..."

                />

              </div>

            </div>

          </>

        )}



        {/* Description & meta */}

        {sectionHeader('Description')}

        <textarea

          style={{ ...S.input, width: '100%', minHeight: '70px', resize: 'vertical' }}

          value={spell.description || ''}

          onChange={e => patch({ description: e.target.value })}

          placeholder="Narrative description of what the spell does..."

        />

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '8px', marginTop: '6px' }}>

          <div>

            {label('Tags (comma-separated)')}

            <input

              style={S.input}

              value={typeof spell.tags === 'string' ? spell.tags : (spell.tags || []).join(', ')}

              onChange={e => patch({ tags: e.target.value })}

              placeholder="blood, necrotic, signature..."

            />

          </div>

          <div>

            {label('Source')}

            <input

              style={S.input}

              value={spell.source || ''}

              onChange={e => patch({ source: e.target.value })}

              placeholder="Homebrew, PHB p.250..."

            />

          </div>

        </div>



        {/* Footer */}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>

          <button style={{ ...S.btn }} onClick={onCancel}>Cancel</button>

          <button

            style={{ ...S.btn, ...S.btnSuccess, ...(isSaveable ? {} : { opacity: 0.5, cursor: 'not-allowed' }) }}

            onClick={handleCreate}

            disabled={!isSaveable}

          >

            Create & Bind

          </button>

        </div>

      </div>

    </div>

  );

};



const DirectSpellBindingEditor = ({

  binding,

  currentSystem,

  spellLibrary,

  onSaveSpellLibrary,

  onUpdate,

  onDelete,

  lockedResourceProfileId = null,

  hideResourceProfile = false,

  lockedCastSurface = null,

  hideCastSurface = false,

  titleOverride = null,

  disableAllSpellsDefault = false,

}) => {

  const b = normalizeDirectSpellBinding(binding);

  const [expanded, setExpanded] = useState(false);

  const [showSpellCreator, setShowSpellCreator] = useState(false);

  const profileOptions = getBindableResourceProfiles(currentSystem, { scalarOnly: true });

  const effectiveProfileId = lockedResourceProfileId || b.resourceProfileId || null;

  const effectiveCastSurface = lockedCastSurface || b.castSurface;

  const selectedProfile = profileOptions.find((profile) => profile.id === effectiveProfileId) || null;

  const boundSpell = b.spellId ? (spellLibrary || []).find((spell) => spell.id === b.spellId) : null;

  const hasSpellLibrary = (spellLibrary || []).length > 0;

  const emitUpdate = (patch = {}) => {

    onUpdate({

      ...patch,

      ...(lockedResourceProfileId ? { resourceProfileId: lockedResourceProfileId } : {}),

      ...(lockedCastSurface ? { castSurface: lockedCastSurface } : {}),

    });

  };



  const handleCreateSpell = (newSpell) => {

    const nextLibrary = [...(spellLibrary || []), newSpell];

    if (onSaveSpellLibrary) {

      onSaveSpellLibrary(nextLibrary);

    }

    emitUpdate({

      assignmentMode: 'specific-spells',

      spellId: newSpell.id,

      spellIds: [newSpell.id],

    });

    setShowSpellCreator(false);

  };



  const summary = (() => {

    const bits = [];

    if (b.assignmentMode === 'all-spells') bits.push('All spells');

    else bits.push(boundSpell?.name || 'Specific spell');

    if (selectedProfile?.label) bits.push(selectedProfile.label);

    if (b.costMode === 'override') bits.push(`Cost ${Number.isFinite(b.overrideCost) ? b.overrideCost : '?'}`);

    else if (b.costMode === 'free') bits.push('Free');

    else bits.push('Derived cost');

    return bits.join(' • ');

  })();



  return (

    <div style={{

      padding: '10px',

      background: 'rgba(0,0,0,0.2)',

      borderRadius: '6px',

      border: '1px solid rgba(74,144,217,0.25)',

      marginBottom: '8px',

    }}>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

        <span

          style={{ cursor: 'pointer', fontSize: '1.1rem', flex: '0 0 auto' }}

          onClick={() => setExpanded(!expanded)}

          title={expanded ? 'Collapse' : 'Expand'}

        >{expanded ? '▼' : '▶'}</span>

        <div style={{ flex: 1 }}>

          <div style={{ fontWeight: 'bold', color: '#4a90d9' }}>

            {titleOverride || (b.assignmentMode === 'all-spells' ? '🌐 Default spell binding' : '📚 Direct spell binding')}

          </div>

          <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>{summary}</div>

        </div>

        <button style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, marginBottom: 0 }} onClick={onDelete}>🗑️</button>

      </div>



      {expanded && (

        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>

            <div>

              <label style={{ ...S.label, fontSize: '0.7rem' }}>Assignment scope</label>

              <select

                style={S.select}

                value={b.assignmentMode}

                onChange={(e) => {

                  const nextMode = e.target.value;

                  if (disableAllSpellsDefault && nextMode === 'all-spells' && b.assignmentMode !== 'all-spells') {

                    return;

                  }

                  emitUpdate({

                    assignmentMode: nextMode,

                    spellId: nextMode === 'all-spells' ? null : b.spellId,

                    spellIds: nextMode === 'all-spells' ? [] : (b.spellId ? [b.spellId] : []),

                  });

                }}

              >

                <option value="specific-spells">Specific spell</option>

                <option value="all-spells" disabled={disableAllSpellsDefault && b.assignmentMode !== 'all-spells'}>All spells (default)</option>

              </select>

            </div>

            {!hideResourceProfile && (

              <div>

                <label style={{ ...S.label, fontSize: '0.7rem' }}>Resource profile</label>

                <select

                  style={S.select}

                  value={effectiveProfileId || ''}

                  onChange={(e) => emitUpdate({ resourceProfileId: e.target.value || null })}

                  disabled={profileOptions.length === 0 || !!lockedResourceProfileId}

                >

                  <option value="">{profileOptions.length === 0 ? 'No scalar profiles available' : 'Select profile'}</option>

                  {profileOptions.map((profile) => (

                    <option key={profile.id} value={profile.id}>

                      {profile.label}{profile.trackerLabel ? ` (${profile.trackerLabel})` : ''}

                    </option>

                  ))}

                </select>

              </div>

            )}

            {!hideCastSurface && (

              <div>

                <label style={{ ...S.label, fontSize: '0.7rem' }}>Cast surface</label>

                <select

                  style={S.select}

                  value={effectiveCastSurface}

                  onChange={(e) => emitUpdate({ castSurface: e.target.value })}

                  disabled={!!lockedCastSurface}

                >

                  <option value="both">Both</option>

                  <option value="spell-panel">Spell panel only</option>

                  <option value="combat">Combat only</option>

                </select>

              </div>

            )}

            <div>

              <label style={{ ...S.label, fontSize: '0.7rem' }}>Cost mode</label>

              <select

                style={S.select}

                value={b.costMode}

                onChange={(e) => emitUpdate({ costMode: e.target.value })}

              >

                <option value="derived">Derived from spell</option>

                <option value="override">Override cost</option>

                <option value="free">Free</option>

              </select>

            </div>

          </div>



          {b.costMode === 'override' && (

            <div style={{ marginTop: '8px', maxWidth: '180px' }}>

              <label style={{ ...S.label, fontSize: '0.7rem' }}>Override cost</label>

              <DebouncedInput

                as="input"

                style={{ ...S.input, marginBottom: 0 }}

                value={Number.isFinite(b.overrideCost) ? String(b.overrideCost) : ''}

                onCommit={(value) => emitUpdate({ overrideCost: value === '' ? null : (parseInt(value, 10) || 0) })}

                placeholder="0"

                inputMode="numeric"

              />

            </div>

          )}



          {b.assignmentMode === 'specific-spells' && (

            <div style={{ marginTop: '10px' }}>

              <label style={{ ...S.label, fontSize: '0.7rem' }}>Library spell</label>

              <div style={{ display: 'flex', gap: '6px', alignItems: 'stretch' }}>

                <select

                  style={{ ...S.select, flex: 1, marginBottom: 0 }}

                  value={b.spellId || ''}

                  onChange={(e) => {

                    const spellId = e.target.value || null;

                    emitUpdate({

                      spellId,

                      spellIds: spellId ? [spellId] : [],

                    });

                  }}

                >

                  <option value="">{hasSpellLibrary ? '— select a spell —' : 'No library spells yet'}</option>

                  {(spellLibrary || []).map((spell) => (

                    <option key={spell.id} value={spell.id}>

                      {spell.name} {spell.level > 0 ? `(L${spell.level}` : '(Cantrip'}{spell.school ? `, ${spell.school})` : ')'}

                    </option>

                  ))}

                </select>

                <button

                  style={{ ...S.btn, ...S.btnEdit, marginBottom: 0, whiteSpace: 'nowrap', padding: '4px 10px', fontSize: '0.78rem' }}

                  onClick={() => setShowSpellCreator(true)}

                  title="Create a new library spell and bind it here"

                >

                  ✨ + New Spell

                </button>

              </div>

              <div style={{ fontSize: '0.7rem', opacity: 0.65, marginTop: '4px' }}>

                Specific bindings override the default all-spells profile when both exist.

              </div>

            </div>

          )}



          <div style={{ marginTop: '10px' }}>

            <div style={{ fontSize: '0.72rem', fontWeight: 'bold', color: '#c9a227', marginBottom: '4px' }}>Visibility</div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '0.75rem' }}>

              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>

                <input

                  type="checkbox"

                  checked={!!b.hidden?.existence}

                  onChange={(e) => emitUpdate({ hidden: { ...(b.hidden || {}), existence: e.target.checked } })}

                />

                Hide existence

              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>

                <input

                  type="checkbox"

                  checked={!!b.hidden?.cost}

                  onChange={(e) => emitUpdate({ hidden: { ...(b.hidden || {}), cost: e.target.checked } })}

                />

                Hide cost

              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>

                <input

                  type="checkbox"

                  checked={!!b.hidden?.effects}

                  onChange={(e) => emitUpdate({ hidden: { ...(b.hidden || {}), effects: e.target.checked } })}

                />

                Hide effects

              </label>

            </div>

          </div>



          {showSpellCreator && (

            <QuickSpellCreatorModal

              initialName={boundSpell?.name || ''}

              onCreate={handleCreateSpell}

              onCancel={() => setShowSpellCreator(false)}

            />

          )}

        </div>

      )}

    </div>

  );

};



const TechniqueEditor = ({ technique, currentSystemId, currentSystem, allSystems, spellLibrary, onSaveSpellLibrary, locations, onUpdate, onDelete }) => {

  const t = technique || {};

  const [expanded, setExpanded] = useState(false);

  const [sectionOpen, setSectionOpen] = useState({

    binding: true, costs: true, reqs: false, availability: false, rules: false, privacy: false,

  });

  const toggleSection = (key) => setSectionOpen(s => ({ ...s, [key]: !s[key] }));

  const [showSpellCreator, setShowSpellCreator] = useState(false);



  // Handle spell creation from the QuickSpellCreatorModal — appends the new spell to the

  // spell library, persists via onSaveSpellLibrary, and binds this technique to the new id.

  // Guards against missing onSaveSpellLibrary (older caller sites) by falling back to a

  // local-only bind: the spell binding still works for the current session even if the

  // library isn't persisted to Firebase. Logged so we notice if this path ever fires.

  const handleCreateSpell = (newSpell) => {

    const nextLib = [...(spellLibrary || []), newSpell];

    if (onSaveSpellLibrary) {

      onSaveSpellLibrary(nextLib);

    } else {

      console.warn('[TechniqueEditor] onSaveSpellLibrary not provided — spell bound but not persisted.');

    }

    onUpdate({ spellId: newSpell.id, inlineSpell: null });

    setShowSpellCreator(false);

  };



  // Mode is determined by inlineSpell presence, not spellId — this way "library selected but

  // unpicked" (spellId === '') stays in library mode rather than flipping to inline.

  const bindingMode = t.inlineSpell ? 'inline' : 'library';

  const setBindingMode = (mode) => {

    if (mode === 'library') onUpdate({ inlineSpell: null });

    else onUpdate({ spellId: null, inlineSpell: t.inlineSpell || { name: t.name || '', description: t.description || '', school: '', damageType: '', dice: '', range: '30 ft', targetType: 'single', attackType: 'none' } });

  };



  const resolveBoundSpell = () => {

    if (t.spellId && spellLibrary) return spellLibrary.find(s => s.id === t.spellId);

    return null;

  };

  const boundSpell = resolveBoundSpell();



  // Helpers for the mini-editors nested inside

  const updateInlineSpell = (patch) => onUpdate({ inlineSpell: { ...(t.inlineSpell || {}), ...patch } });

  const updateAvailability = (patch) => onUpdate({ availability: { ...(t.availability || {}), ...patch } });

  const updateHidden = (patch) => onUpdate({ hidden: { ...(t.hidden || {}), ...patch } });

  const updateCost = (idx, patch) => onUpdate({ costs: (t.costs || []).map((c, i) => i === idx ? { ...c, ...patch } : c) });

  const removeCost = (idx) => onUpdate({ costs: (t.costs || []).filter((_, i) => i !== idx) });

  const addCost = () => onUpdate({ costs: [...(t.costs || []), createTechniqueCost('hp')] });

  const updateReq = (idx, patch) => onUpdate({ requirements: (t.requirements || []).map((r, i) => i === idx ? { ...r, ...patch } : r) });

  const removeReq = (idx) => onUpdate({ requirements: (t.requirements || []).filter((_, i) => i !== idx) });

  const addReq = () => onUpdate({ requirements: [...(t.requirements || []), createTechniqueRequirement('tracker-gte')] });

  const updateRule = (idx, patch) => onUpdate({ rules: (t.rules || []).map((r, i) => i === idx ? { ...r, ...patch } : r) });

  const removeRule = (idx) => onUpdate({ rules: (t.rules || []).filter((_, i) => i !== idx) });

  const addRule = () => onUpdate({ rules: [...(t.rules || []), createAutomationRule('on-cast')] });



  // Tracker lookup for dropdowns. Prefers the supplied systemId, falls back to currentSystem.

  const trackersForSystem = (systemId) => {

    const target = systemId ? allSystems?.[systemId] : currentSystem;

    return target?.trackerFields || [];

  };

  const systemOptions = Object.values(allSystems || {}).map(s => ({ id: s.id, name: s.name || s.id }));

  const hasSystemOptions = systemOptions.length > 0;



  const summary = (() => {

    const bits = [];

    const nCost = (t.costs || []).length;

    if (nCost) bits.push(`${nCost} cost${nCost > 1 ? 's' : ''}`);

    const nReq = (t.requirements || []).length;

    if (nReq) bits.push(`${nReq} req${nReq > 1 ? 's' : ''}`);

    const nRule = (t.rules || []).length;

    if (nRule) bits.push(`${nRule} rule${nRule > 1 ? 's' : ''}`);

    if (t.availability?.cooldown?.scope) bits.push(`cd: ${t.availability.cooldown.scope}`);

    return bits.join(' • ');

  })();



  const sectionHeaderStyle = { display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', padding: '4px 0', fontSize: '0.8rem', fontWeight: 'bold', color: '#c9a227' };

  const sectionBodyStyle = { paddingLeft: '12px', paddingTop: '4px', paddingBottom: '8px', borderLeft: '2px solid rgba(201,162,39,0.2)', marginLeft: '4px' };



  return (

    <div style={{

      padding: '10px',

      background: 'rgba(0,0,0,0.2)',

      borderRadius: '6px',

      border: '1px solid rgba(155,89,182,0.3)',

      marginBottom: '8px',

    }}>

      {/* Header — always visible */}

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

        <span

          style={{ cursor: 'pointer', fontSize: '1.1rem', flex: '0 0 auto' }}

          onClick={() => setExpanded(!expanded)}

          title={expanded ? 'Collapse' : 'Expand'}

        >{expanded ? '▼' : '▶'}</span>

        <DebouncedInput

          as="input"

          style={{ ...S.input, width: '40px', marginBottom: 0, textAlign: 'center', padding: '4px' }}

          value={t.icon || '✨'}

          onCommit={v => onUpdate({ icon: v })}

          title="Icon (emoji)"

        />

        <DebouncedInput

          as="input"

          style={{ ...S.input, flex: 1, marginBottom: 0, fontWeight: 'bold' }}

          value={t.name || ''}

          onCommit={v => onUpdate({ name: v })}

          placeholder="Technique name"

        />

        <button style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, marginBottom: 0 }} onClick={onDelete}>🗑️</button>

      </div>

      {!expanded && summary && (

        <div style={{ fontSize: '0.75rem', opacity: 0.65, marginTop: '4px', paddingLeft: '50px' }}>{summary}</div>

      )}



      {expanded && (

        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>

          {/* Description */}

          <DebouncedInput

            as="textarea"

            style={{ ...S.input, width: '100%', minHeight: '40px', marginBottom: '10px', fontSize: '0.85rem' }}

            value={t.description || ''}

            onCommit={v => onUpdate({ description: v })}

            placeholder="What does this technique do? (shown to player unless hidden)"

          />



          {/* === BINDING === */}

          <div style={sectionHeaderStyle} onClick={() => toggleSection('binding')}>

            <span>{sectionOpen.binding ? '▼' : '▶'}</span>

            <span>🔗 Spell Binding</span>

            <span style={{ fontSize: '0.7rem', opacity: 0.6, fontWeight: 'normal' }}>

              {bindingMode === 'library' ? (boundSpell ? `→ ${boundSpell.name}` : '(library, unselected)') : '(inline)'}

            </span>

          </div>

          {sectionOpen.binding && (

            <div style={sectionBodyStyle}>

              <div style={{ display: 'flex', gap: '12px', marginBottom: '8px', fontSize: '0.8rem' }}>

                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>

                  <input type="radio" checked={bindingMode === 'library'} onChange={() => setBindingMode('library')} />

                  Link library spell

                </label>

                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>

                  <input type="radio" checked={bindingMode === 'inline'} onChange={() => setBindingMode('inline')} />

                  Inline (minimal)

                </label>

              </div>



              {bindingMode === 'library' && (

                <div>

                  <div style={{ display: 'flex', gap: '6px', alignItems: 'stretch' }}>

                    <select

                      style={{ ...S.select, flex: 1, marginBottom: 0 }}

                      value={t.spellId || ''}

                      onChange={e => onUpdate({ spellId: e.target.value || null })}

                    >

                      <option value="">— select a spell —</option>

                      {(spellLibrary || []).map(sp => (

                        <option key={sp.id} value={sp.id}>

                          {sp.name} {sp.level > 0 ? `(L${sp.level}` : '(Cantrip'}{sp.school ? `, ${sp.school})` : ')'}

                        </option>

                      ))}

                    </select>

                    <button

                      style={{ ...S.btn, ...S.btnEdit, marginBottom: 0, whiteSpace: 'nowrap', padding: '4px 10px', fontSize: '0.78rem' }}

                      onClick={() => setShowSpellCreator(true)}

                      title="Create a new library spell and bind it to this technique"

                    >

                      ✨ + New Spell

                    </button>

                  </div>

                  <div style={{ fontSize: '0.7rem', opacity: 0.6, marginTop: '4px' }}>

                    {(spellLibrary || []).length === 0

                      ? 'No spells in the library yet. Click "+ New Spell" to create one — it\'ll be added to the library AND bound to this technique.'

                      : 'The bound spell provides the roll mechanics (damage dice, save DC, range, upcast). Technique wraps it with system-specific cost/requirements/rules. Click "+ New Spell" to create a new library spell inline.'}

                  </div>

                  {showSpellCreator && (

                    <QuickSpellCreatorModal

                      initialName={t.name || ''}

                      onCreate={handleCreateSpell}

                      onCancel={() => setShowSpellCreator(false)}

                    />

                  )}

                </div>

              )}



              {bindingMode === 'inline' && (

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>

                  <div style={{ gridColumn: '1 / span 2', fontSize: '0.7rem', opacity: 0.6 }}>

                    Minimal spell definition. For full mechanics (upcast, DoT, saves, conditions), create a library spell and link it above.

                  </div>

                  <div>

                    <label style={{ fontSize: '0.7rem', opacity: 0.7 }}>Damage dice</label>

                    <DebouncedInput

                      as="input"

                      style={{ ...S.input, width: '100%', marginBottom: 0 }}

                      value={t.inlineSpell?.dice || ''}

                      onCommit={v => updateInlineSpell({ dice: v })}

                      placeholder="e.g. 2d10"

                    />

                  </div>

                  <div>

                    <label style={{ fontSize: '0.7rem', opacity: 0.7 }}>Damage type</label>

                    <DebouncedInput

                      as="input"

                      style={{ ...S.input, width: '100%', marginBottom: 0 }}

                      value={t.inlineSpell?.damageType || ''}

                      onCommit={v => updateInlineSpell({ damageType: v })}

                      placeholder="e.g. necrotic"

                    />

                  </div>

                  <div>

                    <label style={{ fontSize: '0.7rem', opacity: 0.7 }}>Range</label>

                    <DebouncedInput

                      as="input"

                      style={{ ...S.input, width: '100%', marginBottom: 0 }}

                      value={t.inlineSpell?.range || ''}

                      onCommit={v => updateInlineSpell({ range: v })}

                      placeholder="e.g. 60 ft"

                    />

                  </div>

                  <div>

                    <label style={{ fontSize: '0.7rem', opacity: 0.7 }}>Attack type</label>

                    <select

                      style={{ ...S.select, width: '100%', marginBottom: 0 }}

                      value={t.inlineSpell?.attackType || 'none'}

                      onChange={e => updateInlineSpell({ attackType: e.target.value })}

                    >

                      <option value="none">None / auto-hit</option>

                      <option value="melee">Melee attack</option>

                      <option value="ranged">Ranged attack</option>

                      <option value="save">Saving throw</option>

                    </select>

                  </div>

                  <div>

                    <label style={{ fontSize: '0.7rem', opacity: 0.7 }}>Target</label>

                    <select

                      style={{ ...S.select, width: '100%', marginBottom: 0 }}

                      value={t.inlineSpell?.targetType || 'single'}

                      onChange={e => updateInlineSpell({ targetType: e.target.value })}

                    >

                      <option value="self">Self</option>

                      <option value="touch">Touch</option>

                      <option value="single">Single target</option>

                      <option value="multi">Multi-target</option>

                      <option value="aoe">AoE</option>

                    </select>

                  </div>

                </div>

              )}

            </div>

          )}



          {/* === COSTS === */}

          <div style={sectionHeaderStyle} onClick={() => toggleSection('costs')}>

            <span>{sectionOpen.costs ? '▼' : '▶'}</span>

            <span>💰 Costs</span>

            <span style={{ fontSize: '0.7rem', opacity: 0.6, fontWeight: 'normal' }}>

              ({(t.costs || []).length})

            </span>

          </div>

          {sectionOpen.costs && (

            <div style={sectionBodyStyle}>

              {(t.costs || []).length === 0 && (

                <div style={{ fontSize: '0.75rem', opacity: 0.5, marginBottom: '6px' }}>Free to cast. Add a cost to require resource payment.</div>

              )}

              {(t.costs || []).map((cost, i) => {

                const needsAmount = !['hp-percent', 'hp-all-but', 'spell-slot'].includes(cost.resource);

                const needsPercent = cost.resource === 'hp-percent';

                const needsRemaining = cost.resource === 'hp-all-but';

                const needsLevel = cost.resource === 'spell-slot';

                const needsTracker = cost.resource === 'tracker' || cost.resource === 'tracker-stack';

                const trackers = needsTracker ? trackersForSystem(cost.systemId) : [];

                return (

                  <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap' }}>

                    <select

                      style={{ ...S.select, width: '170px', marginBottom: 0, fontSize: '0.8rem' }}

                      value={cost.resource || 'hp'}

                      onChange={e => {

                        const resource = e.target.value;

                        const fresh = createTechniqueCost(resource);

                        updateCost(i, fresh);

                      }}

                    >

                      {RESOURCE_OPTIONS.map(r => (<option key={r.id} value={r.id}>{r.icon} {r.label}</option>))}

                    </select>

                    {needsAmount && (

                      <input type="number" style={{ ...S.input, width: '70px', marginBottom: 0 }}

                        value={cost.amount ?? 1}

                        onChange={e => updateCost(i, { amount: parseFloat(e.target.value) || 0 })}

                        placeholder="amount" />

                    )}

                    {needsPercent && (

                      <input type="number" style={{ ...S.input, width: '70px', marginBottom: 0 }}

                        value={cost.percent ?? 10}

                        onChange={e => updateCost(i, { percent: parseFloat(e.target.value) || 0 })}

                        placeholder="%" />

                    )}

                    {needsRemaining && (

                      <input type="number" style={{ ...S.input, width: '70px', marginBottom: 0 }}

                        value={cost.remaining ?? 1}

                        onChange={e => updateCost(i, { remaining: parseFloat(e.target.value) || 0 })}

                        placeholder="keep" title="HP remaining after cost" />

                    )}

                    {needsLevel && (

                      <input type="number" style={{ ...S.input, width: '70px', marginBottom: 0 }}

                        value={cost.level ?? 1}

                        onChange={e => updateCost(i, { level: parseInt(e.target.value) || 1 })}

                        placeholder="slot lvl" min="1" max="9" />

                    )}

                    {needsTracker && (

                      <>

                        <select

                          style={{ ...S.select, width: '140px', marginBottom: 0, fontSize: '0.8rem' }}

                          value={cost.systemId || ''}

                          onChange={e => updateCost(i, { systemId: e.target.value, trackerId: '' })}

                        >

                          <option value="">{currentSystemId ? `(this system)` : '— select system —'}</option>

                          {systemOptions.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}

                        </select>

                        <select

                          style={{ ...S.select, width: '140px', marginBottom: 0, fontSize: '0.8rem' }}

                          value={cost.trackerId || ''}

                          onChange={e => updateCost(i, { trackerId: e.target.value })}

                          disabled={trackers.length === 0}

                        >

                          <option value="">{trackers.length === 0 ? '(no trackers)' : '— select tracker —'}</option>

                          {trackers.map(tr => (<option key={tr.id} value={tr.id}>{tr.label}</option>))}

                        </select>

                      </>

                    )}

                    <button style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, marginBottom: 0 }} onClick={() => removeCost(i)}>✕</button>

                  </div>

                );

              })}

              <button style={{ ...S.btn, ...S.btnEdit, ...S.btnXs, marginTop: '4px' }} onClick={addCost}>➕ Add cost</button>

            </div>

          )}



          {/* === REQUIREMENTS === */}

          <div style={sectionHeaderStyle} onClick={() => toggleSection('reqs')}>

            <span>{sectionOpen.reqs ? '▼' : '▶'}</span>

            <span>🚧 Requirements</span>

            <span style={{ fontSize: '0.7rem', opacity: 0.6, fontWeight: 'normal' }}>

              ({(t.requirements || []).length})

            </span>

          </div>

          {sectionOpen.reqs && (

            <div style={sectionBodyStyle}>

              {(t.requirements || []).length === 0 && (

                <div style={{ fontSize: '0.75rem', opacity: 0.5, marginBottom: '6px' }}>No gates. Always available (subject to cost).</div>

              )}

              {(t.requirements || []).map((req, i) => {

                const isTrackerCompare = req.type === 'tracker-gte' || req.type === 'tracker-lte';

                const trackers = isTrackerCompare ? trackersForSystem(req.systemId) : [];

                return (

                  <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap' }}>

                    <select

                      style={{ ...S.select, width: '180px', marginBottom: 0, fontSize: '0.8rem' }}

                      value={req.type || 'tracker-gte'}

                      onChange={e => updateReq(i, createTechniqueRequirement(e.target.value))}

                    >

                      {REQUIREMENT_OPTIONS.map(r => (<option key={r.id} value={r.id}>{r.label}</option>))}

                    </select>

                    {isTrackerCompare && (

                      <>

                        <select

                          style={{ ...S.select, width: '140px', marginBottom: 0, fontSize: '0.8rem' }}

                          value={req.systemId || ''}

                          onChange={e => updateReq(i, { systemId: e.target.value, trackerId: '' })}

                        >

                          <option value="">(this system)</option>

                          {systemOptions.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}

                        </select>

                        <select

                          style={{ ...S.select, width: '140px', marginBottom: 0, fontSize: '0.8rem' }}

                          value={req.trackerId || ''}

                          onChange={e => updateReq(i, { trackerId: e.target.value })}

                          disabled={trackers.length === 0}

                        >

                          <option value="">{trackers.length === 0 ? '(no trackers)' : '— tracker —'}</option>

                          {trackers.map(tr => (<option key={tr.id} value={tr.id}>{tr.label}</option>))}

                        </select>

                        <input type="number" style={{ ...S.input, width: '60px', marginBottom: 0 }}

                          value={req.value ?? 0}

                          onChange={e => updateReq(i, { value: parseFloat(e.target.value) || 0 })}

                          placeholder="value" />

                      </>

                    )}

                    {(req.type === 'has-condition' || req.type === 'lacks-condition') && (

                      <DebouncedInput

                        as="input"

                        style={{ ...S.input, flex: 1, minWidth: '140px', marginBottom: 0 }}

                        value={req.name || ''}

                        onCommit={v => updateReq(i, { name: v })}

                        placeholder="e.g. Bloodied"

                      />

                    )}

                    {req.type === 'has-item' && (

                      <>

                        <DebouncedInput

                          as="input"

                          style={{ ...S.input, flex: 1, minWidth: '140px', marginBottom: 0 }}

                          value={req.itemName || ''}

                          onCommit={v => updateReq(i, { itemName: v })}

                          placeholder="item name"

                        />

                        <DebouncedInput

                          as="input"

                          style={{ ...S.input, flex: 1, minWidth: '160px', marginBottom: 0 }}

                          value={req.itemId || ''}

                          onCommit={v => updateReq(i, { itemId: v })}

                          placeholder="definition id"

                        />

                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.78rem' }}>

                          <input

                            type="checkbox"

                            checked={req.equipped === true}

                            onChange={e => updateReq(i, { equipped: e.target.checked })}

                          />

                          equipped

                        </label>

                      </>

                    )}

                    {req.type === 'hp-lte-percent' && (

                      <input type="number" style={{ ...S.input, width: '70px', marginBottom: 0 }}

                        value={req.percent ?? 50}

                        onChange={e => updateReq(i, { percent: parseFloat(e.target.value) || 0 })}

                        placeholder="% of max" />

                    )}

                    {req.type === 'stage-gte' && (

                      <>

                        <select

                          style={{ ...S.select, width: '140px', marginBottom: 0, fontSize: '0.8rem' }}

                          value={req.systemId || ''}

                          onChange={e => updateReq(i, { systemId: e.target.value, stageId: '' })}

                        >

                          <option value="">(this system)</option>

                          {systemOptions.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}

                        </select>

                        <DebouncedInput

                          as="input"

                          style={{ ...S.input, flex: 1, minWidth: '140px', marginBottom: 0 }}

                          value={req.stageId || ''}

                          onCommit={v => updateReq(i, { stageId: v })}

                          placeholder="stage id"

                        />

                      </>

                    )}

                    <button style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, marginBottom: 0 }} onClick={() => removeReq(i)}>✕</button>

                  </div>

                );

              })}

              <button style={{ ...S.btn, ...S.btnEdit, ...S.btnXs, marginTop: '4px' }} onClick={addReq}>➕ Add requirement</button>

            </div>

          )}



          {/* === AVAILABILITY === */}

          <div style={sectionHeaderStyle} onClick={() => toggleSection('availability')}>

            <span>{sectionOpen.availability ? '▼' : '▶'}</span>

            <span>🕰️ Availability</span>

            {t.availability?.cooldown?.scope && (

              <span style={{ fontSize: '0.7rem', opacity: 0.6, fontWeight: 'normal' }}>• {t.availability.cooldown.scope}</span>

            )}

          </div>

          {sectionOpen.availability && (

            <div style={sectionBodyStyle}>

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '0.8rem', marginBottom: '6px' }}>

                <label style={{ display: 'flex', gap: '4px', cursor: 'pointer' }}>

                  <input type="checkbox" checked={!!t.availability?.combatOnly} onChange={e => updateAvailability({ combatOnly: e.target.checked })} />

                  Combat only

                </label>

                <label style={{ display: 'flex', gap: '4px', cursor: 'pointer' }}>

                  <input type="checkbox" checked={!!t.availability?.restOnly} onChange={e => updateAvailability({ restOnly: e.target.checked })} />

                  Out of combat only

                </label>

              </div>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', fontSize: '0.8rem', alignItems: 'center' }}>

                <div>

                  <label style={{ fontSize: '0.7rem', opacity: 0.7, display: 'block' }}>Time of day</label>

                  <select

                    style={{ ...S.select, marginBottom: 0 }}

                    value={t.availability?.timeOfDay || ''}

                    onChange={e => updateAvailability({ timeOfDay: e.target.value || null })}

                  >

                    {TIME_OF_DAY_OPTIONS.map(o => (<option key={o.id} value={o.id}>{o.label}</option>))}

                  </select>

                </div>

                <div>

                  <label style={{ fontSize: '0.7rem', opacity: 0.7, display: 'block' }}>Cooldown</label>

                  <select

                    style={{ ...S.select, marginBottom: 0 }}

                    value={t.availability?.cooldown?.scope || ''}

                    onChange={e => updateAvailability({ cooldown: e.target.value ? { scope: e.target.value, count: t.availability?.cooldown?.count || 1 } : null })}

                  >

                    {COOLDOWN_SCOPES.map(o => (<option key={o.id} value={o.id}>{o.label}</option>))}

                  </select>

                </div>

                {t.availability?.cooldown?.scope && (

                  <div>

                    <label style={{ fontSize: '0.7rem', opacity: 0.7, display: 'block' }}>Count</label>

                    <input type="number" min="1" style={{ ...S.input, width: '60px', marginBottom: 0 }}

                      value={t.availability.cooldown.count || 1}

                      onChange={e => updateAvailability({ cooldown: { ...t.availability.cooldown, count: parseInt(e.target.value) || 1 } })} />

                  </div>

                )}

              </div>

              <div style={{ marginTop: '6px' }}>

                <label style={{ fontSize: '0.7rem', opacity: 0.7, display: 'block' }}>Environments (comma-separated, matches currentLocation)</label>

                <DebouncedInput

                  as="input"

                  style={{ ...S.input, width: '100%', marginBottom: 0, fontSize: '0.85rem' }}

                  value={(t.availability?.environments || []).join(', ')}

                  onCommit={v => updateAvailability({ environments: v.split(',').map(s => s.trim()).filter(Boolean) })}

                  placeholder={(locations || []).slice(0, 3).join(', ') || 'e.g. Forest, Swamp'}

                />

              </div>

            </div>

          )}



          {/* === PER-TECHNIQUE RULES === */}

          <div style={sectionHeaderStyle} onClick={() => toggleSection('rules')}>

            <span>{sectionOpen.rules ? '▼' : '▶'}</span>

            <span>⚙️ On-Cast Rules</span>

            <span style={{ fontSize: '0.7rem', opacity: 0.6, fontWeight: 'normal' }}>({(t.rules || []).length})</span>

          </div>

          {sectionOpen.rules && (

            <div style={sectionBodyStyle}>

              <div style={{ fontSize: '0.75rem', opacity: 0.6, marginBottom: '6px' }}>

                Rules on this technique fire alongside system-wide rules. Use <code>on-cast</code> for consequences (gain stacks, increment counters, require saves) and <code>on-cast-success</code>/<code>on-cast-fail</code> for branching on the roll.

              </div>

              {(t.rules || []).map((rule, i) => (

                <RuleEditor

                  key={rule.id || i}

                  rule={rule}

                  trackerFields={currentSystem?.trackerFields || []}

                  onUpdate={patch => updateRule(i, patch)}

                  onDelete={() => removeRule(i)}

                />

              ))}

              <button style={{ ...S.btn, ...S.btnEdit, ...S.btnXs, marginTop: '4px' }} onClick={addRule}>➕ Add rule</button>

            </div>

          )}



          {/* === PRIVACY === */}

          <div style={sectionHeaderStyle} onClick={() => toggleSection('privacy')}>

            <span>{sectionOpen.privacy ? '▼' : '▶'}</span>

            <span>👁 Privacy</span>

            <span style={{ fontSize: '0.7rem', opacity: 0.6, fontWeight: 'normal' }}>

              {Object.values(t.hidden || {}).filter(Boolean).length || 0} hidden

            </span>

          </div>

          {sectionOpen.privacy && (

            <div style={sectionBodyStyle}>

              <div style={{ fontSize: '0.75rem', opacity: 0.6, marginBottom: '8px' }}>

                Controls what the player sees for this technique. DM always sees everything.

              </div>

              {[

                // 2026-04-27: name privacy row added. Default ON so DMs

                // opt IN to revealing technique names — they're narrative

                // beats, not stat-block fields.

                { key: 'name',        label: 'Hide name',        hint: "Default ON — player sees '???' until you reveal it." },

                { key: 'description', label: 'Hide description', hint: 'Player sees just the name.' },

                { key: 'cost',        label: 'Hide cost',        hint: "Default OFF — knowing the cost is part of player agency." },

                { key: 'requirements',label: 'Hide requirements',hint: "Default ON — player sees 'Unavailable' with no reason. Turn OFF when the gate is narratively visible (e.g. 'HP must be ≤ 50%')." },

                { key: 'effects',     label: 'Hide effects',     hint: "Player sees 'Spell: ?' instead of dice / damage type / range." },

                { key: 'rules',       label: 'Hide rules',       hint: 'Default ON — consequences are surprises.' },

                { key: 'existence',   label: 'Hide existence',   hint: 'Technique invisible until revealed (narrative unlock).' },

              ].map(row => (

                <label key={row.key} style={{ display: 'flex', gap: '6px', alignItems: 'flex-start', cursor: 'pointer', fontSize: '0.8rem', marginBottom: '4px' }}>

                  <input

                    type="checkbox"

                    checked={!!t.hidden?.[row.key]}

                    onChange={e => updateHidden({ [row.key]: e.target.checked })}

                  />

                  <div>

                    <div>{row.label}</div>

                    <div style={{ fontSize: '0.7rem', opacity: 0.55 }}>{row.hint}</div>

                  </div>

                </label>

              ))}

            </div>

          )}

        </div>

      )}

    </div>

  );

};



// ============================================================================

// CAST FLOW UI (Session 4 — spell-resource architecture §6)

// ============================================================================



// Shared helpers for rendering cost entries as human-readable strings. Technique's `hidden.cost`

// can suppress details entirely. Both the panel row and the cast modal reuse these.

const formatCostEntry = (cost) => {

  if (!cost) return '';

  const r = cost.resource;

  const a = cost.amount;

  if (r === 'hp')        return `${a} HP`;

  if (r === 'hp-percent') return `${cost.percent}% HP`;

  if (r === 'hp-all-but') return `HP → ${cost.remaining}`;

  if (r === 'temp-hp')   return `${a} Temp HP`;

  if (r === 'mana')      return `${a} Mana`;

  if (r === 'exhaustion') return `+${a} Exhaustion`;

  if (r === 'hit-die')   return `${a} Hit Die`;

  if (r === 'spell-slot') return `Lv${cost.level || 1} slot`;

  if (r === 'tracker' || r === 'tracker-stack') return `${a} ${cost.trackerId || 'tracker'}`;

  return r || '';

};



// Confirmation modal for a technique cast. Shows description, costs, post-cost HP preview.

// Respects `technique.hidden.cost` to suppress cost disclosure when the DM wants mystery

// (e.g., a cursed technique where the player feels weaker after but doesn't know why).

export const TechniqueCastModal = ({ technique, systemTemplate, boundSpell, char, warning, onConfirm, onCancel, isPlayer = false }) => {

  if (!technique) return null;

  const hideCost = !!technique.hidden?.cost;

  const costs = technique.costs || [];

  const hpCostEntry = costs.find(c => ['hp', 'hp-percent', 'hp-all-but'].includes(c.resource));

  let hpPreview = null;

  if (hpCostEntry && char) {

    let loss = 0;

    if (hpCostEntry.resource === 'hp') loss = parseFloat(hpCostEntry.amount) || 0;

    else if (hpCostEntry.resource === 'hp-percent') loss = Math.floor((char.maxHp || 0) * (parseFloat(hpCostEntry.percent) || 0) / 100);

    else if (hpCostEntry.resource === 'hp-all-but') loss = Math.max(0, (char.currentHp || 0) - (parseFloat(hpCostEntry.remaining) || 0));

    hpPreview = { before: char.currentHp || 0, after: Math.max(0, (char.currentHp || 0) - loss) };

  }

  // 2026-04-28: respect hidden.name in the cast modal title too. The

  // TechniquesPanel button shows '???' for hidden-name techniques, so the

  // confirmation modal must not leak the real name on click. Default-hidden

  // is the convention — `hidden.name !== false` reads as hidden. P3.

  const hideName = isPlayer && technique.hidden?.name !== false;

  const displayName = hideName ? HIDDEN_LABEL_PLACEHOLDER : (technique.name || 'Technique');
  const displayIcon = getTechniqueDisplayIcon(technique, systemTemplate, '🔮');

  return (

    <div style={S.modal} onClick={onCancel}>

      <div style={{ ...S.modalContent, maxWidth: '480px' }} onClick={e => e.stopPropagation()}>

        <h3 style={{ marginTop: 0, color: '#9b59b6' }}>{displayIcon && <>{displayIcon} </>}Cast {displayName}?</h3>

        {systemTemplate && (

          <div style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '-8px', marginBottom: '12px' }}>

            From {systemTemplate.publicName || systemTemplate.name}

          </div>

        )}

        {!technique.hidden?.description && technique.description && (

          <div style={{ fontStyle: 'italic', opacity: 0.85, marginBottom: '12px' }}>{technique.description}</div>

        )}

        {boundSpell && (

          // 2026-04-29 — BUG-REPORTS.md #10: gate on hidden.effects for the

          // player viewer. DM always sees the bound spell's mechanical

          // breakdown; player sees a "Spell: ?" placeholder when the DM

          // has flagged effects as hidden.

          isPlayer && technique.hidden?.effects ? (

            <div style={{ fontSize: '0.85rem', marginBottom: '8px', padding: '6px 8px', background: 'rgba(155,89,182,0.1)', borderRadius: '4px', fontStyle: 'italic', opacity: 0.7 }}>

              📜 Spell: ?

            </div>

          ) : (

            <div style={{ fontSize: '0.85rem', marginBottom: '8px', padding: '6px 8px', background: 'rgba(155,89,182,0.1)', borderRadius: '4px' }}>

              📜 Spell: {boundSpell.name} — {boundSpell.dice || boundSpell.effect || 'see spell for details'}

            </div>

          )

        )}

        {!hideCost && costs.length > 0 && (

          <div style={{ marginBottom: '12px' }}>

            <div style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '4px' }}>Cost:</div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>

              {costs.map((c, i) => (<div key={i} style={{ fontSize: '0.9rem' }}>• {formatCostEntry(c)}</div>))}

            </div>

            {hpPreview && (

              <div style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '6px' }}>

                HP: {hpPreview.before} → {hpPreview.after}

              </div>

            )}

          </div>

        )}

        {hideCost && costs.length > 0 && (

          <div style={{ fontSize: '0.85rem', opacity: 0.6, fontStyle: 'italic', marginBottom: '12px' }}>

            There will be a cost…

          </div>

        )}

        {warning && (

          <div style={{ padding: '8px 10px', background: 'rgba(201,39,39,0.15)', border: '1px solid rgba(201,39,39,0.4)', borderRadius: '4px', marginBottom: '12px', fontSize: '0.85rem', color: '#ff8888' }}>

            ⚠️ Overcast: {warning}. Casting anyway will trigger a penalty (exhaustion).

          </div>

        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>

          <button style={{ ...S.btn }} onClick={onCancel}>Cancel</button>

          <button style={{ ...S.btn, ...S.btnSuccess }} onClick={onConfirm}>Cast</button>

        </div>

      </div>

    </div>

  );

};



// Save-prompt modal for `require-save` outcomes enqueued by on-cast rules. DM resolves

// pass/fail (via roll or manual pick); the panel then dispatches the matching sub-actions

// via `resolvePendingSave`. The modal now consumes the shared resolved save context so

// proficiency, item bonuses, runtime flat modifiers, bonus dice, and save advantage stay

// aligned with the other save readers on the branch.

export const RequireSaveModal = ({ pendingSave, char, saveContext = null, saveAdvantage = null, onResolve, onSkip }) => {

  if (!pendingSave) return null;

  const abilityKey = (pendingSave.ability || 'wis').toLowerCase();

  const ability = abilityKey.toUpperCase();

  const fallbackMod = (() => {

    const stat = char?.baseStats?.[abilityKey] ?? char?.[abilityKey] ?? 10;

    return Math.floor((stat - 10) / 2);

  })();

  const modifier = typeof saveContext?.totalBonus === 'number' ? saveContext.totalBonus : fallbackMod;

  const advantageMode = saveAdvantage?.mode || 'normal';

  const isSupremeAdvantage = advantageMode === 'supreme_advantage';

  const effectiveAdvantageMode = isSupremeAdvantage ? 'advantage' : advantageMode;

  const advantageSources = effectiveAdvantageMode !== 'normal' ? (saveAdvantage?.sources || []) : [];

  const advantageLabel = isSupremeAdvantage
    ? 'Supreme advantage'
    : effectiveAdvantageMode === 'advantage'
      ? 'Advantage'
      : effectiveAdvantageMode === 'disadvantage'
        ? 'Disadvantage'
        : 'Normal';

  const hasBonusDice = Array.isArray(saveContext?.diceBonuses) && saveContext.diceBonuses.length > 0;

  const [roll, setRoll] = useState(null); // { value, total, outcome }



  const handleRoll = () => {

    const diceCount = effectiveAdvantageMode !== 'normal' ? (isSupremeAdvantage ? 3 : 2) : 1;

    const allDice = Array.from({ length: diceCount }, () => Math.floor(Math.random() * 20) + 1);

    const chosenRoll = effectiveAdvantageMode === 'advantage'

      ? Math.max(...allDice)

      : effectiveAdvantageMode === 'disadvantage'

        ? Math.min(...allDice)

        : allDice[0];

    const saveDice = saveContext ? resolveSaveBonusDiceRoll(saveContext) : null;

    const total = chosenRoll + modifier + (saveDice?.bonusDiceTotal || 0);

    const outcome = chosenRoll === 20

      ? 'pass'

      : chosenRoll === 1

        ? 'fail'

        : (total >= (pendingSave.dc || 10) ? 'pass' : 'fail');

    setRoll({

      value: chosenRoll,

      total,

      outcome,

      allDice: allDice.length > 1 ? allDice : null,

      advMode: effectiveAdvantageMode !== 'normal' ? effectiveAdvantageMode : null,

      isSupreme: isSupremeAdvantage,

      advSources: advantageSources,

      bonusDiceRolls: saveDice?.bonusDiceRolls || [],

      bonusDiceTotal: saveDice?.bonusDiceTotal || 0,

    });

  };



  return (

    <div style={S.modal}>

      <div style={{ ...S.modalContent, maxWidth: '440px' }} onClick={e => e.stopPropagation()}>

        <h3 style={{ marginTop: 0, color: '#c92727' }}>⚠️ {ability} Save DC {pendingSave.dc}</h3>

        {pendingSave.reason && (

          <div style={{ fontStyle: 'italic', marginBottom: '12px', opacity: 0.85 }}>"{pendingSave.reason}"</div>

        )}

        <div style={{ fontSize: '0.8rem', opacity: 0.7, marginBottom: '12px' }}>

          {pendingSave.source ? `From ${pendingSave.source}. ` : ''}Your {ability} save: {modifier >= 0 ? '+' : ''}{modifier}

          {hasBonusDice ? ' + bonus dice' : ''}

          {effectiveAdvantageMode !== 'normal' ? ` (${advantageLabel})` : ''}

        </div>

        {effectiveAdvantageMode !== 'normal' && advantageSources.length > 0 && (

          <div style={{ fontSize: '0.75rem', opacity: 0.65, marginBottom: '10px' }}>

            Sources: {advantageSources.join(', ')}

          </div>

        )}

        {roll && (

          <div style={{ padding: '10px', background: roll.outcome === 'pass' ? 'rgba(39,201,90,0.15)' : 'rgba(201,39,39,0.15)', border: `1px solid ${roll.outcome === 'pass' ? '#27c95a' : '#c92727'}`, borderRadius: '6px', marginBottom: '12px' }}>

            <div>

              🎲 Rolled {roll.allDice ? `${roll.allDice.join(', ')} → ` : ''}{roll.value} + {modifier}

              {roll.bonusDiceTotal ? ` + ${roll.bonusDiceTotal}` : ''} = <strong>{roll.total}</strong> — <strong>{roll.outcome === 'pass' ? 'PASS' : 'FAIL'}</strong>

            </div>

            {roll.bonusDiceRolls?.length > 0 && (

              <div style={{ fontSize: '0.78rem', opacity: 0.78, marginTop: '4px' }}>

                Bonus dice: {roll.bonusDiceRolls.map((bonus) => `${bonus.dice} ${bonus.source} (${bonus.total})`).join(', ')}

              </div>

            )}

          </div>

        )}

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>

          {!roll && (

            <button style={{ ...S.btn, ...S.btnEdit }} onClick={handleRoll}>🎲 Roll d20</button>

          )}

          <button style={{ ...S.btn, ...S.btnSuccess, ...(roll?.outcome !== 'pass' ? { opacity: 0.6 } : {}) }} onClick={() => onResolve('pass')}>Pass</button>

          <button style={{ ...S.btn, ...S.btnDanger, ...(roll?.outcome !== 'fail' ? { opacity: 0.6 } : {}) }} onClick={() => onResolve('fail')}>Fail</button>

          {onSkip && <button style={{ ...S.btn, marginLeft: 'auto' }} onClick={onSkip}>Skip</button>}

        </div>

      </div>

    </div>

  );

};



// Character-facing panel listing all castable techniques across all applied systems. Each

// row shows name, cost summary, availability state. Unavailable techniques are greyed with

// tooltip showing the blocker. Cast button fires performCast directly (no
// confirm modal) → orchestrateTechniqueCast → apply via onUpdateChar → push
// audit via onPushAuditEvent → surface player-visible deltas inline + any
// pending saves via RequireSaveModal (resolved sequentially).

// 2026-04-27: `embedded` mode renders the techniques list as an inline

// section without the outer card wrapper — used inside SpellPanel so

// techniques live alongside spells (per user request). The standalone

// (non-embedded) form is kept for the eventual dedicated Techniques

// card; see CLAUDE-NOTES-GENERAL for the longer-term direction.

const TechniquesPanel = ({ char, systems, spellLibrary, worldContext, onUpdateChar, onPushAuditEvent, onNotify, isPlayer = true, embedded = false, groupTravelLog = [], onSaveGroupTravelLog = null, messages = null, onSaveMessages = null, onSendStatusMessage = null, npcLibrary = {}, onSaveNpcLibrary = null }) => {

  const [showTechniques, setShowTechniques] = useState(true);
  const [expandedTechniques, setExpandedTechniques] = useState({});
  // Inline cast result strip per technique row. Mirrors the spell row's
  // post-cast roll panel — keyed by getTechniqueRowKey(entry). Each entry
  // captures only the player-visible deltas (HP, revealed trackers, dice)
  // so secret consequences (e.g. addiction crossing a threshold) stay
  // hidden until the DM reveals them. P3 + P5 (every number is a receipt).
  const [lastCastResults, setLastCastResults] = useState({});

  const [saveQueue, setSaveQueue] = useState([]); // [{ pendingSave, context }, ...]

  const [pendingAudit, setPendingAudit] = useState(null); // audit event to push after all saves resolve

  const activeQueuedSaveEntry = saveQueue[0] || null;

  const activeQueuedChar = activeQueuedSaveEntry?.context?.char || char;

  const activeQueuedPendingSave = activeQueuedSaveEntry?.pendingSave || null;

  const queuedSaveEffectContext = activeQueuedPendingSave

    ? getResolvedCharacterEffectContext(activeQueuedChar, {}, systems, !isPlayer)

    : null;

  const queuedSaveAbility = (activeQueuedPendingSave?.ability || 'wis').toLowerCase();

  const queuedSaveContext = activeQueuedPendingSave

    ? getResolvedSaveContext({

        stat: queuedSaveAbility,

        char: activeQueuedChar,

        effectiveStats: queuedSaveEffectContext?.effectiveStats,

        equippedCombatBonuses: queuedSaveEffectContext?.equippedCombatBonuses,

        activeLegacyEffects: queuedSaveEffectContext?.activeLegacyEffects,

        activeRuntimeEffects: Array.isArray(activeQueuedChar?.activeEffects) ? activeQueuedChar.activeEffects : [],

      })

    : null;

  const queuedSaveAdvantage = activeQueuedPendingSave

    ? getResolvedCharacterAdvantageContext(

        activeQueuedChar,

        {

          ...getSaveAdvantageConfig(queuedSaveAbility),

        },

        {},

        systems,

        !isPlayer,

      )

    : null;



  // Collect techniques across every active applied system on the character. Each entry

  // carries enough context (system instance + template + resolved spell) to cast.

  // 2026-04-27: also respect inst.playerVisible — when a DM marks a

  // system as hidden from the player on the char's instance, its

  // techniques should be invisible too. Without this gate, the player

  // could see technique buttons for a system whose pill was suppressed.

  const castable = useMemo(() => {

    const out = [];

    (char?.systemList || []).forEach(inst => {

      if (inst.active === false) return;

      if (isPlayer && inst.playerVisible === false) return;

      const template = resolveSystemTemplateForViewer(inst, systems, !isPlayer);

      if (!template) return;

      const techniques = getSystemTechniques(template);

      techniques.forEach(t => {

        // Respect hidden.existence for players (DM always sees all)

        if (isPlayer && t.hidden?.existence) return;

        const boundSpell = t.spellId ? (spellLibrary || []).find(s => s.id === t.spellId) : null;

        out.push({

          technique: t,

          systemInstance: inst,

          systemTemplate: template,

          instanceKey: inst.id || inst.systemId,

          boundSpell,

          availability: isTechniqueAvailable(t, char, inst, worldContext || {}, systems, isPlayer),

        });

      });

    });

    return out;

  }, [char, systems, spellLibrary, worldContext, isPlayer]);



  if (castable.length === 0) return null;



  // Group by system for display

  const bySystem = {};

  castable.forEach(entry => {

    const key = entry.systemTemplate.id;

    if (!bySystem[key]) bySystem[key] = { template: entry.systemTemplate, entries: [] };

    bySystem[key].entries.push(entry);

  });



  const getTechniqueRowKey = (entry) => `${entry.instanceKey || 'system'}:${entry.technique?.id || entry.technique?.name || 'technique'}`;

  const toggleTechniqueExpanded = (entry) => {
    const key = getTechniqueRowKey(entry);
    setExpandedTechniques(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // P3 — resolve the player-facing presentation of a tracker change.
  // Returns null when the tracker (or its name) is hidden from the
  // player. Conventions:
  //   * field.showToPlayer === false           → bar hidden entirely
  //   * vis.showToPlayer === false             → instance-level override
  //   * field.publicLabel === HIDDEN_PLACEHOLDER ("???")
  //                                            → player sees a redacted
  //     name. In the regular Status panel this is fine ("a counter is
  //     here, you can see it ticking but not what it is"), but in a
  //     fly-by cast result strip it's just noise — the player has no
  //     time to puzzle out what '???' rose by 1. So in this surface we
  //     suppress the chip entirely when the publicLabel is fully
  //     redacted. Blood Magic addiction is the canonical example.
  //   * vis.showValue === false                → show "?" instead of
  //     the actual numbers (caller substitutes).
  const resolveTrackerPresentation = (field, instance) => {
    if (!field) return null;
    if (field.showToPlayer === false) return null;
    const instVis = instance?.trackerVisibility?.[field.id] || {};
    if (instVis.showToPlayer === false) return null;
    if (!isPlayer) {
      return { label: field.label || field.id, showValue: true };
    }
    const playerLabel = field.publicLabel || field.label || field.id;
    if (playerLabel === HIDDEN_LABEL_PLACEHOLDER) return null;
    const showValue = instVis.showValue !== false && field.showValue !== false;
    return { label: playerLabel, showValue };
  };

  const performCast = (entry) => {

    if (!entry?.availability?.available) return;

    const { technique, systemInstance, systemTemplate, instanceKey, boundSpell } = entry;

    // Snapshot pre-cast values so we can diff after the orchestrator runs.
    const liveSysInstance = (char.systemList || []).find(s =>
      (s.id || s.systemId) === (systemInstance?.id || systemInstance?.systemId)
    ) || systemInstance;
    const hpBefore = char.currentHp;
    const trackerValuesBefore = { ...(liveSysInstance?.trackerValues || {}) };

    const result = orchestrateTechniqueCast({

      technique,

      char,

      instanceKey,

      systemInstance,

      systemTemplate,

      systems,

      worldContext: worldContext || {},

    });



    // The orchestrator now returns charFinal with cost + on-cast + threshold-dispatch all

    // applied. We build a patch by diffing against the starting char so onUpdateChar gets

    // only changed keys (preserves the merge-patch semantics of updateChar).

    const charFinal = result.charFinal || result.charPostCost;

    const patch = {};

    Object.keys(charFinal).forEach(k => { if (charFinal[k] !== char[k]) patch[k] = charFinal[k]; });

    if (Object.keys(patch).length > 0) onUpdateChar?.(patch, { category: 'combat', action: 'technique.cast', summary: result.auditEvent.summary });



    // Show notifications

    const notes = result.auditEvent.details?.notifications || [];

    if (notes.length > 0 && onNotify) notes.forEach(n => onNotify(n));



    // Post-cast damage roll. If the technique has dice notation on its bound or inline spell

    // that's a fixed formula (not 'variable' e.g. Exsanguinate), roll and surface the result

    // via onNotify so the DM/player sees the number. Target application is Session 6 work

    // (combat integration) — for now the DM reads the rolled damage and applies manually.

    // 2026-04-28: notifications go to whoever is using the panel (player or

    // DM). Use the privacy-aware name so a player whose technique has

    // hidden.name doesn't see the real name in their post-cast notification.

    // The audit event's summary still uses the real name (DM-facing). P3.

    const techNotifyName = (isPlayer && technique.hidden?.name !== false) ? HIDDEN_LABEL_PLACEHOLDER : technique.name;

    const resolvedSpell = boundSpell || technique.inlineSpell;

    const dice = resolvedSpell?.dice;

    let diceResult = null;

    if (dice && dice !== 'variable' && /\d+d\d+/.test(dice)) {

      const roll = rollDice(dice);

      if (roll && typeof roll.total === 'number') {

        const dmgType = resolvedSpell.damageType ? ` ${resolvedSpell.damageType}` : '';

        if (onNotify) onNotify(`🎲 ${techNotifyName}: rolled ${dice} = ${roll.total}${dmgType}`);

        // Stamp the rolled damage onto the audit event for history

        result.auditEvent.details = { ...(result.auditEvent.details || {}), rolledDamage: { dice, total: roll.total, type: resolvedSpell.damageType || null } };

        diceResult = { dice, total: roll.total, type: resolvedSpell.damageType || null };

      }

    } else if (dice === 'variable') {

      // Exsanguinate / similar — cost-derived damage. Extract from costs: hp-all-but → sacrificed = pre.currentHp - remaining.

      const hpCost = (technique.costs || []).find(c => c.resource === 'hp-all-but');

      if (hpCost) {

        const sacrificed = Math.max(0, (char.currentHp || 0) - (parseFloat(hpCost.remaining) || 0));

        const dmgType = resolvedSpell?.damageType ? ` ${resolvedSpell.damageType}` : '';

        if (onNotify) onNotify(`🩸 ${techNotifyName}: ${sacrificed}${dmgType} damage dealt (sacrificed HP)`);

        result.auditEvent.details = { ...(result.auditEvent.details || {}), rolledDamage: { formula: 'variable (sacrificed HP)', total: sacrificed, type: resolvedSpell?.damageType || null } };

        diceResult = { formula: 'variable (sacrificed HP)', total: sacrificed, type: resolvedSpell?.damageType || null };

      }

    }

    // Capture player-visible deltas for the inline result strip.
    const finalSysInstance = (charFinal.systemList || []).find(s =>
      (s.id || s.systemId) === (systemInstance?.id || systemInstance?.systemId)
    ) || liveSysInstance;
    const trackerValuesAfter = { ...(finalSysInstance?.trackerValues || {}) };
    const trackerFields = systemTemplate?.trackerFields || [];
    const trackerChanges = trackerFields.reduce((acc, field) => {
      const before = trackerValuesBefore[field.id] ?? field.value ?? 0;
      const after = trackerValuesAfter[field.id] ?? field.value ?? 0;
      if (before === after) return acc;
      const presentation = resolveTrackerPresentation(field, finalSysInstance);
      if (!presentation) return acc;
      acc.push({
        id: field.id,
        label: presentation.label,
        before: presentation.showValue ? before : '?',
        after: presentation.showValue ? after : '?',
      });
      return acc;
    }, []);
    const hpAfter = charFinal.currentHp;
    const tempHpBefore = char.tempHp || 0;
    const tempHpAfter = charFinal.tempHp || 0;
    // Capture target-type so the result strip can correctly attribute the
    // dice roll to "you" vs "target". Self/none-targeted rolls go in the
    // self group; everything else is a roll the DM applies to a target.
    const castTargetType = (boundSpell?.targetType || technique?.inlineSpell?.targetType || 'none');
    setLastCastResults(prev => ({
      ...prev,
      [getTechniqueRowKey(entry)]: {
        hpBefore,
        hpAfter,
        tempHpBefore,
        tempHpAfter,
        trackerChanges,
        diceResult,
        targetType: castTargetType,
        ts: Date.now(),
      },
    }));



    // Queue any pending saves for sequential resolution. Include threshold-dispatch pending

    // saves — Blood Magic's addiction-save rule fires here when addiction crosses 10/15/20.

    const saves = [

      ...(result.costResults.pendingSaves || []),

      ...(result.castResults.pendingSaves || []),

      ...(result.thresholdResults?.pendingSaves || []),

    ];

    if (saves.length > 0) {

      // Each save carries its own context (char, system, template) for sub-action dispatch

      setSaveQueue(saves.map(ps => ({

        pendingSave: ps,

        context: {

          char: charFinal,

          systemInstance,

          systemTemplate,

          trackerValues: systemInstance?.trackerValues || {},

          triggerContext: result.triggerContext,

          worldContext: worldContext || {},

        },

      })));

      setPendingAudit(result.auditEvent);

    } else {

      // No saves — push audit immediately

      onPushAuditEvent?.(result.auditEvent);

    }

  };

  const handleCastClick = (entry) => performCast(entry);

  const dismissCastResult = (rowKey) => {
    setLastCastResults(prev => {
      if (!prev[rowKey]) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  };



  const handleSaveResolve = (outcome) => {

    if (saveQueue.length === 0) return;

    const [head, ...rest] = saveQueue;

    const subResults = resolvePendingSave(head.pendingSave, outcome, head.context);

    const sysId = head.context.systemInstance?.id || head.context.systemInstance?.systemId || null;

    const queuedChar = head.context.char || char;

    const nextChar = sysId

      ? applyRuleDispatchToChar(queuedChar, sysId, subResults, systems, head.context.worldContext || null)

      : { ...queuedChar, ...applySystemRuleResults(queuedChar, subResults, systems, head.context.worldContext || null) };

    const nextSystemInstance = sysId

      ? (nextChar.systemList || []).find((entry) => (entry.id || entry.systemId) === sysId) || head.context.systemInstance

      : head.context.systemInstance || null;

    const queuedSaves = (subResults.pendingSaves || []).map((pendingSave) => ({

      pendingSave,

      context: {

        ...head.context,

        char: nextChar,

        systemInstance: nextSystemInstance,

        trackerValues: nextSystemInstance?.trackerValues || head.context.trackerValues || {},

      },

    }));

    // Apply sub-results to char via onUpdateChar — these chain directly off the char as-it-is

    const patch = {};

    if (subResults.charUpdates && Object.keys(subResults.charUpdates).length > 0) {

      Object.assign(patch, subResults.charUpdates);

    }

    // Sub-results may include systemUpdates keyed to the triggering instance

    if (subResults.systemUpdates && Object.keys(subResults.systemUpdates).length > 0 && head.context.systemInstance) {

      const sysId = head.context.systemInstance.id || head.context.systemInstance.systemId;

      patch.systemList = (queuedChar.systemList || []).map(s =>

        (s.id || s.systemId) === sysId ? applySystemInstanceUpdatePatch(s, subResults.systemUpdates) : s

      );

    }

    Object.keys(subResults.charUpdates || {}).forEach((key) => {

      patch[key] = nextChar[key];

    });

    if (nextChar.systemList !== queuedChar.systemList) {

      patch.systemList = nextChar.systemList;

    }

    if (Object.keys(patch).length > 0) onUpdateChar?.(patch);

    const sideEffects = collectRuleResultSideEffects(subResults, { npcLibrary });
    if (sideEffects.npcLibraryChanged && typeof onSaveNpcLibrary === 'function') {
      onSaveNpcLibrary(sideEffects.nextNpcLibrary);
    }
    const sentStatusMessages = persistSystemRuleStatusMessages(sideEffects.statusMessages, {
      char: nextChar,
      source: 'technique.save',
      messages,
      onSendStatusMessage,
      onSaveMessages,
      auditMeta: { category: 'systems', action: 'technique.save-status' },
    });
    const ruleLogEntries = persistSystemRuleTravelLogEntries(sideEffects.logEntries, {
      char: nextChar,
      action: 'system-rule-log',
      source: 'technique.save',
      groupTravelLog,
      onSaveGroupTravelLog,
    });
    if (onNotify) {
      const notices = [...(sideEffects.notifications || [])];
      if (queuedSaves.length > 0) {
        const pendingSaveNotice = buildSystemRulePendingSaveNotice(sideEffects.pendingSaves, 'Technique save follow-up');
        notices.push(`${pendingSaveNotice || `Technique save follow-up requested ${queuedSaves.length} saving throw${queuedSaves.length === 1 ? '' : 's'}.`} Queued for resolution.`);
      } else if (sideEffects.pendingSaves.length > 0) {
        const pendingSaveNotice = buildSystemRulePendingSaveNotice(sideEffects.pendingSaves, 'Technique save follow-up');
        if (pendingSaveNotice) notices.push(pendingSaveNotice);
      }
      if (ruleLogEntries.length > 0) {
        notices.push(`${ruleLogEntries.length} system rule log entr${ruleLogEntries.length === 1 ? 'y' : 'ies'} recorded.`);
      }
      if (sentStatusMessages.length > 0) {
        notices.push(`${sentStatusMessages.length} system status message${sentStatusMessages.length === 1 ? '' : 's'} sent.`);
      }
      if (notices.length > 0) onNotify(notices.join(' | '));
    }

    // Continue queue

    if (rest.length === 0 && queuedSaves.length === 0 && pendingAudit) {

      onPushAuditEvent?.(pendingAudit);

      setPendingAudit(null);

    }

    setSaveQueue([...queuedSaves, ...rest]);

  };



  // 2026-04-27: outer styling differs between standalone and embedded.

  // Standalone keeps the original purple card so the future dedicated

  // Techniques view can use it as-is. Embedded drops the card chrome

  // and uses a thin top border to mark the section boundary inside

  // SpellPanel.

  const outerStyle = embedded

    ? { marginTop: '12px', paddingTop: '10px', borderTop: '1px solid rgba(155,89,182,0.25)' }

    : { maxWidth: '700px', margin: '16px auto', padding: '12px', background: 'rgba(155,89,182,0.08)', border: '1px solid rgba(155,89,182,0.3)', borderRadius: '8px' };

  return (

    <div style={outerStyle}>

      <div
        style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: showTechniques ? '10px' : 0, cursor: 'pointer' }}
        onClick={() => setShowTechniques(!showTechniques)}
      >

        <span style={{ fontWeight: 'bold', color: '#9b59b6' }}>🔮 Techniques</span>

        <span style={{ fontSize: '0.75rem', opacity: 0.65 }}>({castable.length})</span>

        <span style={{ marginLeft: 'auto', color: '#ffd700' }}>{showTechniques ? '▼' : '▶'}</span>

      </div>

      {showTechniques && Object.values(bySystem).map(group => (

        <div key={group.template.id} style={{ marginBottom: '12px' }}>

          <div style={{ fontSize: '0.78rem', color: '#c9a227', marginBottom: '4px', paddingBottom: '2px', borderBottom: '1px solid rgba(201,162,39,0.2)' }}>

            {isPlayer ? (group.template.publicName || group.template.name) : group.template.name} ({group.entries.length})

          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>

            {group.entries.map(entry => {

              const { technique: t, availability, boundSpell } = entry;

              const hideCost = !!t.hidden?.cost;

              // 2026-04-29: default-hidden, mirrors hidden.name convention.

              // DM opts in to showing requirement reasons via explicit

              // `requirements: false`. See SystemBadge spell-resource block

              // (App.js:~13507) for the same change.

              const hideReq = shouldHideTechniqueRequirementReason(t, group.template);

              const costLabel = hideCost ? HIDDEN_LABEL_PLACEHOLDER : (t.costs || []).map(formatCostEntry).join(' + ');

              const reasonLabel = availability.available ? null : (hideReq && isPlayer ? 'Unavailable' : availability.reason);
              const rowKey = getTechniqueRowKey(entry);
              const isExpanded = !!expandedTechniques[rowKey];
              const displayIcon = getTechniqueDisplayIcon(t, group.template, '');
              const displayName = (isPlayer && t.hidden?.name !== false) ? HIDDEN_LABEL_PLACEHOLDER : t.name;
              const resolvedSpell = boundSpell || t.inlineSpell || {};
              const previewText = resolvedSpell.dice || resolvedSpell.effect || '';
              const detailBits = [
                resolvedSpell.castingTime,
                resolvedSpell.range,
                resolvedSpell.attackType && resolvedSpell.attackType !== 'none' ? resolvedSpell.attackType : null,
                resolvedSpell.targetType && resolvedSpell.targetType !== 'none' ? resolvedSpell.targetType : null,
              ].filter(Boolean);

              const accentColor = availability.available ? '#9b59b6' : '#555';

              return (

                <div
                  key={rowKey}
                  title={reasonLabel || (boundSpell ? `Spell: ${boundSpell.name}` : '')}
                  style={{
                    background: 'rgba(0,0,0,0.2)',
                    borderRadius: '6px',
                    borderLeft: `3px solid ${accentColor}`,
                    opacity: availability.available ? 1 : 0.6,
                  }}
                >

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 8px', flexWrap: 'wrap' }}>

                    {/* Expand */}
                    <button
                      type="button"
                      style={{ ...S.btn, padding: '2px 4px', fontSize: '0.65rem', opacity: 0.7 }}
                      onClick={(e) => { e.stopPropagation(); toggleTechniqueExpanded(entry); }}
                      aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${displayName}`}
                    >
                      {isExpanded ? '▼' : '▶'}
                    </button>

                    {/* Technique badge (purple, parallels spell level badge) */}
                    <span style={{ fontSize: '0.65rem', fontWeight: 'bold', color: accentColor, letterSpacing: '0.5px' }}>T</span>

                    {/* Type icon */}
                    {displayIcon && <span style={{ fontSize: '0.75rem' }}>{displayIcon}</span>}

                    {/* 2026-04-27: respect hidden.name — when truthy (or
                        undefined for legacy techniques), player sees
                        '???' until the DM unchecks the privacy toggle.
                        DM still sees the real name (the gate is `isPlayer
                        && hideName`, not just `hideName`). */}
                    <span
                      style={{ fontWeight: 'bold', fontSize: '0.8rem', cursor: 'pointer', color: availability.available ? '#e8d5f5' : '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      onClick={() => toggleTechniqueExpanded(entry)}
                    >
                      {displayName}
                    </span>

                    {/* Dice / preview — sits adjacent to the name. The unavailable
                        reason takes the spacer slot below so it doesn't shove the
                        dice away from short '???' names. */}
                    {previewText && (
                      <span style={{ fontSize: '0.7rem', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {previewText}
                      </span>
                    )}

                    {/* Spacer + (optional) inline unavailable reason */}
                    <span style={{ fontSize: '0.7rem', color: '#c9a227', fontStyle: 'italic', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {!availability.available && reasonLabel ? reasonLabel : ''}
                    </span>

                    {/* Cost pill — purple */}
                    <span style={{ fontSize: '0.65rem', padding: '1px 6px', borderRadius: '3px', color: '#d9a8f0', background: 'rgba(155,89,182,0.18)', border: '1px solid rgba(155,89,182,0.3)', whiteSpace: 'nowrap' }}>
                      {costLabel || 'Free'}
                    </span>

                    {/* Cast — purple-tinted to distinguish from spell's blue Cast */}
                    <button
                      type="button"
                      style={{ ...S.btn, padding: '2px 10px', fontSize: '0.7rem', fontWeight: 'bold', background: 'linear-gradient(180deg, #9b59b6 0%, #6c3a82 100%)', border: '1px solid #b07ac9', color: '#fff', ...(!availability.available ? { opacity: 0.4 } : {}) }}
                      disabled={!availability.available}
                      onClick={() => handleCastClick(entry)}
                    >
                      Cast
                    </button>

                  </div>

                  {isExpanded && (
                    <div style={{ padding: '8px 10px 10px 28px', borderTop: '1px solid rgba(255,255,255,0.06)', fontSize: '0.72rem', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      {detailBits.length > 0 && (
                        <div style={{ color: '#888' }}>{detailBits.join(' • ')}</div>
                      )}
                      {!t.hidden?.description && t.description && (
                        <div style={{ color: '#aaa' }}>{t.description}</div>
                      )}
                      {boundSpell && (
                        <div style={{ color: '#7db5ff', fontSize: '0.68rem' }}>
                          Bound spell: {isPlayer && t.hidden?.effects ? '?' : `${boundSpell.name}${boundSpell.dice ? ` • ${boundSpell.dice}` : ''}`}
                        </div>
                      )}
                      {!availability.available && reasonLabel && (
                        <div style={{ color: '#c9a227', padding: '4px 8px', background: 'rgba(201,162,39,0.08)', borderRadius: '3px', borderLeft: '2px solid rgba(201,162,39,0.4)' }}>
                          {reasonLabel}
                        </div>
                      )}
                    </div>
                  )}

                  {(() => {
                    const castResult = lastCastResults[rowKey];
                    if (!castResult) return null;
                    const hpDelta = castResult.hpAfter - castResult.hpBefore;
                    const tempHpDelta = castResult.tempHpAfter - castResult.tempHpBefore;
                    // Cost + on-cast tracker writes always land on the caster, so
                    // those chips go in the "you" group. The dice roll routes to
                    // "target" unless the spell is explicitly self/none-targeted.
                    const selfChips = [];
                    const targetChips = [];
                    if (hpDelta !== 0) {
                      selfChips.push({
                        key: 'hp',
                        icon: '❤️',
                        label: `HP ${castResult.hpBefore} → ${castResult.hpAfter}`,
                        color: hpDelta < 0 ? '#ff8888' : '#7dff7d',
                        bg: hpDelta < 0 ? 'rgba(231,76,60,0.12)' : 'rgba(39,201,90,0.12)',
                        border: hpDelta < 0 ? 'rgba(231,76,60,0.35)' : 'rgba(39,201,90,0.35)',
                      });
                    }
                    if (tempHpDelta !== 0) {
                      selfChips.push({
                        key: 'temp',
                        icon: '🛡️',
                        label: `Temp ${castResult.tempHpBefore} → ${castResult.tempHpAfter}`,
                        color: '#87ceeb',
                        bg: 'rgba(135,206,235,0.12)',
                        border: 'rgba(135,206,235,0.35)',
                      });
                    }
                    castResult.trackerChanges.forEach((tc) => {
                      selfChips.push({
                        key: `tr:${tc.id}`,
                        icon: '📊',
                        label: `${tc.label} ${tc.before} → ${tc.after}`,
                        color: '#ffd27d',
                        bg: 'rgba(201,162,39,0.12)',
                        border: 'rgba(201,162,39,0.35)',
                      });
                    });
                    if (castResult.diceResult) {
                      const dr = castResult.diceResult;
                      const tgt = castResult.targetType;
                      const diceIsSelf = !tgt || tgt === 'self' || tgt === 'none';
                      const diceChip = {
                        key: 'dice',
                        icon: '🎲',
                        label: `${dr.dice || dr.formula || 'roll'}: ${dr.total}${dr.type ? ` ${dr.type}` : ''}`,
                        color: '#7db5ff',
                        bg: 'rgba(74,144,217,0.12)',
                        border: 'rgba(74,144,217,0.35)',
                      };
                      (diceIsSelf ? selfChips : targetChips).push(diceChip);
                    }
                    if (selfChips.length === 0 && targetChips.length === 0) return null;
                    const renderGroup = (label, color, chips) => (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: '1px', color, fontWeight: 'bold', opacity: 0.85 }}>
                          {label}
                        </span>
                        {chips.map((chip) => (
                          <span key={chip.key} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.7rem', padding: '2px 7px', borderRadius: '10px', color: chip.color, background: chip.bg, border: `1px solid ${chip.border}`, whiteSpace: 'nowrap' }}>
                            <span aria-hidden="true">{chip.icon}</span>
                            {chip.label}
                          </span>
                        ))}
                      </div>
                    );
                    return (
                      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '6px', padding: '6px 24px 6px 28px', borderTop: '1px solid rgba(155,89,182,0.18)', background: 'rgba(155,89,182,0.06)', borderRadius: '0 0 5px 0' }}>
                        {selfChips.length > 0 && renderGroup('You', '#9b59b6', selfChips)}
                        {targetChips.length > 0 && renderGroup('Target', '#e67e22', targetChips)}
                        <button
                          type="button"
                          onClick={() => dismissCastResult(rowKey)}
                          aria-label="Dismiss cast result"
                          style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '50%', color: '#888', width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.55rem', cursor: 'pointer', padding: 0, lineHeight: 1 }}
                        >✕</button>
                      </div>
                    );
                  })()}

                </div>

              );

            })}

          </div>

        </div>

      ))}



      {saveQueue.length > 0 && (

        <RequireSaveModal

          key={`${activeQueuedPendingSave?.source || 'save'}:${queuedSaveAbility}:${activeQueuedPendingSave?.dc || 0}:${activeQueuedPendingSave?.reason || ''}:${saveQueue.length}`}

          pendingSave={saveQueue[0].pendingSave}

          char={activeQueuedChar}

          saveContext={queuedSaveContext}

          saveAdvantage={queuedSaveAdvantage}

          onResolve={handleSaveResolve}

          onSkip={() => {

            // Skip the current save (treat as neither pass nor fail — no sub-actions fire)

            const rest = saveQueue.slice(1);

            if (rest.length === 0 && pendingAudit) {

              onPushAuditEvent?.(pendingAudit);

              setPendingAudit(null);

            }

            setSaveQueue(rest);

          }}

        />

      )}

    </div>

  );

};



// Quick Add Buff/Debuff Dropdown

const QuickAddDropdown = ({ onAddBuff, onAddDebuff, onClose, onAddWithData }) => {

  const [value, setValue] = useState('');

  const [isDebuff, setIsDebuff] = useState(false);

  const [isPermanent, setIsPermanent] = useState(false);

  const [showAdvanced, setShowAdvanced] = useState(false);

  const [notes, setNotes] = useState('');

  const [playerTooltip, setPlayerTooltip] = useState('');

  const [colorId, setColorId] = useState('default-buff');

  const [hasManualColor, setHasManualColor] = useState(false); // Track if user manually selected color

  const [addGlow, setAddGlow] = useState(true); // Default to glow on

  const [noteVisible, setNoteVisible] = useState(false);

  

  const handleAdd = () => {

    if (!value.trim()) return;

    

    // Capture current state values explicitly

    const currentIsPermanent = isPermanent === true;

    const currentIsDebuff = isDebuff === true;

    

    // If we have the enhanced callback, use it

    if (onAddWithData) {

      onAddWithData({

        text: value.trim(),

        isDebuff: currentIsDebuff,

        isPermanent: currentIsPermanent,

        notes: notes.trim() || null,

        playerTooltip: playerTooltip.trim() || null,

        colorId: colorId || (currentIsDebuff ? 'default-debuff' : 'default-buff'),

        addGlow,

        noteVisible,

      });

    } else {

      // Fallback to simple add

      if (currentIsDebuff) onAddDebuff(value.trim());

      else onAddBuff(value.trim());

    }

    setValue('');

    setNotes('');

    setPlayerTooltip('');

    setAddGlow(false);

    onClose();

  };

  

  const selectPreset = (preset) => {

    // Only set icon if value is empty, otherwise prepend

    if (!value.trim()) {

      setValue(preset.icon + ' ');

    } else if (!value.startsWith(preset.icon)) {

      // Remove any existing preset icon and add new one

      const cleaned = value.replace(/^[^\w\s]+\s*/, '').trim();

      setValue(preset.icon + ' ' + cleaned);

    }

    setColorId(preset.color);

    setHasManualColor(true);

    setIsDebuff(preset.id === 'debuff' || preset.id === 'curse' || preset.id === 'geas');

    setShowAdvanced(true);

  };

  

  // Toggle buff/debuff without resetting color if manually set

  const toggleDebuff = (newIsDebuff) => {

    setIsDebuff(newIsDebuff);

    if (!hasManualColor) {

      setColorId(newIsDebuff ? 'default-debuff' : 'default-buff');

    }

  };

  

  return <div style={{ position: 'absolute', top: '100%', right: 0, background: 'rgba(30,20,15,0.98)', border: '1px solid #c9a227', borderRadius: '6px', padding: '10px', zIndex: 2000, minWidth: '280px', boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }}>

    {/* Quick Effect Type Buttons */}

    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>

      {EFFECT_TYPE_PRESETS.slice(0, 9).map(preset => {

        const colorOpt = BUFF_COLOR_OPTIONS.find(c => c.id === preset.color) || BUFF_COLOR_OPTIONS[0];

        return (

          <Tooltip key={preset.id} text={preset.description}>

            <button

              style={{

                ...S.btn,

                ...S.btnXs,

                padding: '3px 6px',

                fontSize: '0.7rem',

                background: colorOpt.bgColor,

                borderColor: colorOpt.color,

                color: colorOpt.textColor || colorOpt.color,

                marginBottom: 0,

              }}

              onClick={() => selectPreset(preset)}

            >

              {preset.svgIcon === 'biohazard' ? <BiohazardIcon size={14} color={colorOpt.textColor || colorOpt.color} /> : preset.svgIcon === 'plague-doctor' ? <PlagueDoctorIcon size={14} color={colorOpt.textColor || colorOpt.color} /> : preset.icon}

            </button>

          </Tooltip>

        );

      })}

    </div>

    

    <input style={{ ...S.input, marginBottom: '6px', padding: '8px', fontSize: '0.85rem' }} placeholder="+2 Str, ⛓️ Oath of Silence..." value={value} onChange={e => setValue(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleAdd()} autoFocus />

    

    <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>

      <button style={{ ...S.btn, ...S.btnXs, flex: 1, ...(isDebuff ? {} : { background: 'rgba(39,201,90,0.3)' }) }} onClick={() => toggleDebuff(false)}>Buff</button>

      <button style={{ ...S.btn, ...S.btnXs, flex: 1, ...(isDebuff ? { background: 'rgba(201,39,39,0.3)' } : {}) }} onClick={() => toggleDebuff(true)}>Debuff</button>

      <label style={{ display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer', fontSize: '0.85rem', color: isPermanent ? '#ffd700' : '#888', padding: '0 4px' }}>

        <input type="checkbox" checked={isPermanent} onChange={e => setIsPermanent(e.target.checked)} />

        ∞

      </label>

    </div>

    

    {/* Advanced Options Toggle */}

    <details open={showAdvanced} style={{ marginBottom: '6px' }}>

      <summary style={{ cursor: 'pointer', fontSize: '0.75rem', color: '#888' }} onClick={(e) => { e.preventDefault(); setShowAdvanced(!showAdvanced); }}>⚙️ Options</summary>

      {showAdvanced && (

        <div style={{ padding: '6px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px', marginTop: '4px' }}>

          {/* Player Tooltip - always visible to player */}

          <div style={{ marginBottom: '6px' }}>

            <label style={{ display: 'block', fontSize: '0.7rem', color: '#4ade80', marginBottom: '2px' }}>👁 Player Tooltip (always visible):</label>

            <input 

              style={{ ...S.input, marginBottom: 0, padding: '6px', fontSize: '0.75rem' }} 

              placeholder="Granted by the Forest Spirit" 

              value={playerTooltip} 

              onChange={e => setPlayerTooltip(e.target.value)} 

            />

          </div>

          {/* DM Note - optionally visible */}

          <div style={{ marginBottom: '4px' }}>

            <label style={{ display: 'block', fontSize: '0.7rem', color: '#c9a227', marginBottom: '2px' }}>🔒 DM Note:</label>

            <textarea 

              style={{ ...S.input, marginBottom: '4px', padding: '6px', fontSize: '0.75rem', minHeight: '36px', resize: 'vertical' }} 

              placeholder="Secret: Will betray them at the bridge..." 

              value={notes} 

              onChange={e => setNotes(e.target.value)} 

            />

          </div>

          {notes.trim() && (

            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.7rem', color: '#888', marginBottom: '6px' }}>

              <input type="checkbox" checked={noteVisible} onChange={e => setNoteVisible(e.target.checked)} />

              Also show DM note to player

            </label>

          )}

          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '6px' }}>

            <label style={{ fontSize: '0.7rem', color: '#888' }}>Color:</label>

            <select 

              style={{ ...S.select, flex: 1, marginBottom: 0, padding: '3px', fontSize: '0.7rem' }}

              value={colorId}

              onChange={e => { setColorId(e.target.value); setHasManualColor(true); }}

            >

              {BUFF_COLOR_OPTIONS.map(opt => (

                <option key={opt.id} value={opt.id}>{opt.name}</option>

              ))}

            </select>

            {(() => {

              const colorOpt = BUFF_COLOR_OPTIONS.find(c => c.id === colorId);

              return colorOpt && (

                <div style={{ width: '18px', height: '18px', borderRadius: '3px', background: colorOpt.bgColor, border: `2px solid ${colorOpt.color}`, flexShrink: 0 }} />

              );

            })()}

          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>

            <Tooltip text="Permanent effects are highlighted and persist across rests">

              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.7rem', color: isPermanent ? '#ffd700' : '#888' }}>

                <input type="checkbox" checked={isPermanent} onChange={e => setIsPermanent(e.target.checked)} />

                ∞ Permanent

              </label>

            </Tooltip>

          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.7rem', color: '#ffd700' }}>

            <input type="checkbox" checked={addGlow} onChange={e => setAddGlow(e.target.checked)} />

            ✨ Add glow effect (notify player)

          </label>

        </div>

      )}

    </details>

    

    <button style={{ ...S.btn, ...S.btnSuccess, ...S.btnXs, width: '100%' }} onClick={handleAdd}>Add</button>

  </div>;

};



// ==================== MESSAGING SYSTEM ====================



// Helper to format relative time

const formatRelativeTime = (timestamp) => {

  const now = new Date();

  const then = new Date(timestamp);

  const diffMs = now - then;

  const diffMins = Math.floor(diffMs / 60000);

  const diffHours = Math.floor(diffMs / 3600000);

  const diffDays = Math.floor(diffMs / 86400000);

  

  if (diffMins < 1) return 'Just now';

  if (diffMins < 60) return `${diffMins}m ago`;

  if (diffHours < 24) return `${diffHours}h ago`;

  return `${diffDays}d ago`;

};



// Helper to format full timestamp

const formatFullTime = (timestamp) => {

  return new Date(timestamp).toLocaleString();

};


const messageSortTime = (message) => {
  if (typeof message?.ts === 'number') return message.ts;
  const parsed = Date.parse(message?.timestamp || '');
  return Number.isFinite(parsed) ? parsed : 0;
};

const mergeMessageArraysById = (baseMessages = [], tailMessages = []) => {
  const byId = new Map();
  let syntheticIndex = 0;
  const add = (message) => {
    if (!message) return;
    const key = message.id || `__message_${syntheticIndex++}`;
    byId.set(key, message);
  };
  (baseMessages || []).forEach(add);
  (tailMessages || []).forEach(add);
  return [...byId.values()].sort((a, b) => messageSortTime(a) - messageSortTime(b));
};

const buildMessageReadView = (messages, messageLiveTail, charId) => {
  const base = messages || { statusUpdates: [], dmChats: {} };
  const tail = messageLiveTail || { statusUpdates: [], dmChats: {} };
  const dmChats = { ...(base.dmChats || {}) };
  if (charId && tail.dmChats?.[charId]) {
    dmChats[charId] = mergeMessageArraysById(base.dmChats?.[charId] || [], tail.dmChats[charId] || []);
  }
  return {
    ...base,
    statusUpdates: mergeMessageArraysById(base.statusUpdates || [], tail.statusUpdates || []),
    dmChats,
  };
};

const countUnreadMessagesForChar = (messages, charId, { excludeUrgent = false } = {}) => {
  const statusUnread = (messages?.statusUpdates || []).filter(m =>
    m.fromDM
    && !m.seen
    && !m.dismissed
    && (!excludeUrgent || m.urgency !== 'urgent')
    && (m.visibility === 'public' || (m.recipients || []).includes(charId))
  ).length;

  const dmUnread = (messages?.dmChats?.[charId] || []).filter(m =>
    !m.read && m.from === 'DM'
  ).length;

  return statusUnread + dmUnread;
};


// Notification Badge Component (appears in bottom right)

const NotificationBadge = ({ messages, charId, onClick }) => {

  const totalUnread = countUnreadMessagesForChar(messages, charId);

  const hasUrgent = (messages.statusUpdates || []).some(m => 

    m.fromDM && !m.dismissed && m.urgency === 'urgent' && (m.visibility === 'public' || (m.recipients || []).includes(charId))

  ) || (messages.dmChats?.[charId] || []).some(m => !m.read && m.from === 'DM' && m.urgency === 'urgent');

  

  if (totalUnread === 0) return null;

  

  return (

    <div 

      onClick={onClick}

      style={{

        position: 'fixed',

        bottom: '80px',

        right: '20px',

        width: '50px',

        height: '50px',

        borderRadius: '50%',

        background: hasUrgent ? 'linear-gradient(135deg, #c92727, #ff4444)' : 'linear-gradient(135deg, #c9a227, #ffd700)',

        display: 'flex',

        alignItems: 'center',

        justifyContent: 'center',

        cursor: 'pointer',

        boxShadow: '0 4px 15px rgba(0,0,0,0.4)',

        border: '2px solid rgba(255,255,255,0.3)',

        zIndex: 1000,

        animation: hasUrgent ? 'pulse 1s infinite' : 'none',

      }}

    >

      <span style={{ color: 'white', fontWeight: 'bold', fontSize: '1.2rem' }}>

        {hasUrgent ? '❗' : '📬'}{totalUnread}

      </span>

      <style>{`@keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.1); } }`}</style>

    </div>

  );

};



// Message Item Component

const MessageItem = ({ message, onDismiss, onDelete, isDMChat }) => {

  const [expanded, setExpanded] = useState(false);

  

  const displayTime = message.gameTime 

    ? `Day ${message.gameTime.day}${message.gameTime.time ? `, ${message.gameTime.time}` : ''}`

    : formatRelativeTime(message.timestamp);

  

  // Determine styling based on message type

  const isWhisper = message.type === 'whisper' || message.category === 'dm-whisper';

  const isUrgent = message.urgency === 'urgent';

  const isAmbient = message.type === 'ambient';

  

  // Get message type style from MESSAGE_TYPE_STYLES if available

  const typeStyle = MESSAGE_TYPE_STYLES[message.type] || MESSAGE_TYPE_STYLES[message.category] || {};

  

  const borderColor = isUrgent ? '#ff6961' : 

    isWhisper ? 'rgba(155,89,182,0.5)' :

    message.category === 'disease' ? '#d47dd4' :

    message.category === 'hunger' ? '#c9a227' : 

    message.fromDM ? '#4a90d9' : 'rgba(255,255,255,0.2)';

  

  const bgColor = isUrgent ? 'rgba(255,105,97,0.1)' :

    isWhisper ? 'rgba(155,89,182,0.15)' :

    isAmbient ? 'transparent' :

    'rgba(0,0,0,0.3)';

  

  const icon = isUrgent ? '🔴' :

    isWhisper ? '🤫' :

    isAmbient ? '🌫️' :

    message.category === 'disease' ? '☣' :

    message.category === 'hunger' ? '🍽️' :

    message.category === 'day' ? '📅' :

    message.category === 'diet' ? '🥗' :

    isDMChat ? '💬' : '📋';

  

  // Whisper-specific rendering

  if (isWhisper) {

    const senderConfig = WHISPER_SENDERS.find(s => s.id === message.senderStyle) || WHISPER_SENDERS[0];

    return (

      <div 

        onClick={() => setExpanded(!expanded)}

        style={{

          padding: '14px 16px',

          marginBottom: '10px',

          background: bgColor,

          borderRadius: '8px',

          border: `1px solid ${borderColor}`,

          cursor: 'pointer',

          animation: isUrgent ? 'pulse 2s infinite' : 'none',

        }}

      >

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>

          <div style={{ flex: 1 }}>

            <div style={{ 

              display: 'flex', 

              alignItems: 'center', 

              gap: '8px', 

              marginBottom: '6px',

              color: '#9b59b6',

              fontSize: '0.75rem',

            }}>

              <span>{senderConfig.icon || '🤫'}</span>

              <span style={{ fontStyle: 'italic' }}>

                {message.senderPrefix || senderConfig.prefix || 'A voice whispers...'}

              </span>

            </div>

            <div style={{ 

              color: '#e0d0c0', 

              fontStyle: 'italic',

              paddingLeft: '4px',

            }}>

              "{String(message.content || '')}"

            </div>

          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>

            <span style={{ fontSize: '0.7rem', opacity: 0.5 }}>{displayTime}</span>

            {onDismiss && (

              <button 

                onClick={(e) => { e.stopPropagation(); onDismiss(message.id); }}

                style={{ ...S.btn, ...S.btnXs, padding: '2px 6px', fontSize: '0.65rem' }}

              >✕</button>

            )}

          </div>

        </div>

        {expanded && (

          <div style={{ marginTop: '10px', paddingTop: '8px', borderTop: '1px solid rgba(155,89,182,0.2)', fontSize: '0.75rem', opacity: 0.6 }}>

            Received: {formatFullTime(message.timestamp)}

            {message.displayStyle && <span> • Style: {message.displayStyle}</span>}

          </div>

        )}

      </div>

    );

  }

  

  // Standard message rendering

  return (

    <div 

      onClick={() => setExpanded(!expanded)}

      style={{

        padding: '12px',

        marginBottom: '8px',

        background: bgColor,

        borderRadius: '6px',

        borderLeft: `4px solid ${borderColor}`,

        cursor: 'pointer',

        animation: isUrgent ? 'pulse 2s infinite' : 'none',

      }}

    >

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>

        <div style={{ flex: 1 }}>

          <span style={{ marginRight: '8px' }}>{icon}</span>

          <span style={{ color: isAmbient ? '#888' : '#e8d5b7', fontStyle: isAmbient ? 'italic' : 'normal' }}>

            {String(message.content || '')}

          </span>

        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

          <span style={{ fontSize: '0.75rem', opacity: 0.6 }}>{displayTime}</span>

          {onDismiss && !isDMChat && (

            <button 

              onClick={(e) => { e.stopPropagation(); onDismiss(message.id); }}

              style={{ ...S.btn, ...S.btnXs, padding: '2px 6px', fontSize: '0.7rem' }}

            >✕</button>

          )}

          {onDelete && isDMChat && (

            <button 

              onClick={(e) => { e.stopPropagation(); onDelete(message.id); }}

              style={{ ...S.btn, ...S.btnXs, padding: '2px 6px', fontSize: '0.7rem' }}

            >🗑️</button>

          )}

        </div>

      </div>

      {expanded && (

        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.1)', fontSize: '0.8rem', opacity: 0.7 }}>

          Sent: {formatFullTime(message.timestamp)}

          {message.from && message.from !== 'DM' && <span> • From: You</span>}

          {message.from === 'DM' && <span> • From: DM</span>}

        </div>

      )}

    </div>

  );

};



// Message Panel (Slide-out)

// Font options for dramatic sequences

const FONT_OPTIONS = {

  default: { name: 'Default', family: 'inherit' },

  serif: { name: 'Serif (Classic)', family: 'Georgia, "Times New Roman", serif' },

  gothic: { name: 'Gothic', family: '"Palatino Linotype", "Book Antiqua", Palatino, serif' },

  mono: { name: 'Monospace', family: '"Courier New", Courier, monospace' },

  fantasy: { name: 'Fantasy', family: 'Luminari, fantasy' },

  cursive: { name: 'Cursive', family: '"Brush Script MT", cursive' },

  elegant: { name: 'Elegant', family: '"Didot", "Bodoni MT", "Noto Serif Display", serif' },

  oldEnglish: { name: 'Old English', family: '"MedievalSharp", "UnifrakturCook", fantasy' },

  handwritten: { name: 'Handwritten', family: '"Segoe Script", "Bradley Hand", cursive' },

  dramatic: { name: 'Dramatic', family: '"Copperplate", "Papyrus", fantasy' },

  modern: { name: 'Modern', family: '"Futura", "Century Gothic", "Avenir", sans-serif' },

  storybook: { name: 'Storybook', family: '"Cochin", "Baskerville", "Libre Baskerville", serif' },

  ancient: { name: 'Ancient', family: '"Trajan Pro", "Cinzel", serif' },

  whisper: { name: 'Whisper', family: '"Apple Chancery", "Lucida Calligraphy", cursive' },

};



// Text effect definitions

const TEXT_EFFECTS = {

  none: { name: 'None', description: 'Clean text' },

  glow: { name: 'Glow', description: 'Ethereal glow effect' },

  flicker: { name: 'Flicker', description: 'Unstable, flickering light' },

  glitch: { name: 'Glitch', description: 'Digital corruption' },

  shake: { name: 'Shake', description: 'Trembling, unstable' },

  pulse: { name: 'Pulse', description: 'Breathing, pulsing' },

  blur: { name: 'Blur Focus', description: 'Shifts in and out of focus' },

  wave: { name: 'Wave', description: 'Gentle undulation' },

  static: { name: 'Static', description: 'Radio interference' },

};



// Transition style definitions - modular system

const TRANSITION_STYLES = {

  // Base styles (how lines appear/stack)

  stack: { name: 'Stack', description: 'Lines stack below, previous fade', category: 'base' },

  stackUp: { name: 'Stack Up', description: 'Lines stack above, pushing down', category: 'base' },

  fadeReplace: { name: 'Replace', description: 'Each line fades out before next', category: 'base' },

  centerFocus: { name: 'Center', description: 'New line centered, old shrink to sides', category: 'base' },

  typewriterStack: { name: 'Scroll', description: 'Like reading a scroll, continuous', category: 'base' },

  // Text reveal modifiers (how individual text is revealed) - can combine multiple

  typewriter: { name: 'Typewriter', description: 'Words appear one at a time', category: 'reveal' },

  letterByLetter: { name: 'Letter', description: 'Characters typed individually', category: 'reveal' },

  fragmented: { name: 'Fragmented', description: 'Broken, scattered speech', category: 'reveal' },

  radioStatic: { name: 'Radio', description: 'Tuning through frequencies', category: 'reveal' },

  obscured: { name: 'Obscured', description: 'Parts hidden, slowly revealed', category: 'reveal' },

};



// Separate into base and modifiers for UI

const BASE_TRANSITIONS = ['stack', 'stackUp', 'fadeReplace', 'centerFocus', 'typewriterStack'];

const REVEAL_MODIFIERS = ['typewriter', 'letterByLetter', 'fragmented', 'radioStatic', 'obscured'];



// Example text for each theme

const THEME_EXAMPLES = {

  foreboding: [

    { text: "A voice whispers from beyond", delay: 3 },

    { text: "...... Why? Why have you abandoned me?", delay: 4 },

    { text: "I dwell in endless agony", delay: 3 },

    { text: "while you?", delay: 2 },

    { text: "....", delay: 2 },

    { text: "You deserve to rot.", delay: 3 },

    { text: "When it comes for you, I will laugh. I will laugh for the first time in a thousand years.", delay: 5 },

  ],

  narration: [

    { text: "Three days pass in relative silence.", delay: 3 },

    { text: "The forest thins, giving way to rocky highlands.", delay: 3 },

    { text: "In the distance, the spires of an ancient city pierce the clouds.", delay: 4 },

  ],

  revelation: [

    { text: "The darkness lifts.", delay: 3 },

    { text: "For the first time in weeks, sunlight touches your face.", delay: 4 },

    { text: "You have survived.", delay: 3 },

  ],

  divine: [

    { text: "M O R T A L", delay: 3 },

    { text: "Your prayers have reached us across the infinite void", delay: 4 },

    { text: "We have watched. We have waited.", delay: 3 },

    { text: "The time of reckoning approaches.", delay: 4 },

  ],

  corruption: [

    { text: "y̷o̶u̸ ̵c̶a̷n̵n̶o̸t̷ ̵e̶s̶c̸a̶p̷e̶", delay: 3 },

    { text: "w̸̛͓̄ḙ̵̛ ̵̱̑a̴̬͝r̵̬͒e̷̜͝ ̵̣̈́a̷̰͐l̴̰̐r̶̨̛e̷̙̅a̸͉͆d̵͙̊y̴̧͊ ̶̣́i̷̬̇n̶̳̔s̷͖̓i̶̹͑d̷̰̈e̶͇͐", delay: 4 },

    { text: "there is no salvation", delay: 3 },

  ],

  radioFrequency: [

    { text: "---kzzzt--- hello? Can anyone ---", delay: 2 },

    { text: "---static--- they're coming from the ---kzzzt---", delay: 3 },

    { text: "---interference--- don't trust the ---", delay: 2 },

    { text: "---kzzzt--- repeat: DO NOT ---static---", delay: 3 },

    { text: "---signal lost---", delay: 2 },

  ],

  whisper: [

    { text: "can you hear me?", delay: 3 },

    { text: "i've been trying to reach you for so long", delay: 4 },

    { text: "the walls between worlds grow thin here", delay: 3 },

    { text: "remember what i told you. remember.", delay: 4 },

  ],

};



// Auto-calculate delay for a line based on word count, theme pacing, and reveal time

const getAutoDelay = (text, themePacing = 'normal', revealMods = [], revealSettings = {}) => {

  const words = (text || '').split(/\s+/).filter(w => w.length > 0).length;

  const chars = (text || '').length;

  const pacingMultiplier = { slow: 1.4, normal: 1.0, fast: 0.7 }[themePacing] || 1.0;

  // Base reading time: 2.5s minimum, ~0.4s per word, capped at 8s

  const readingTime = Math.min(8, Math.max(2.5, words * 0.4 + 1.5)) * pacingMultiplier;

  

  // Add estimated reveal animation time

  let revealTime = 0;

  if (revealMods.includes('letterByLetter')) {

    revealTime = chars * ((revealSettings.letterSpeed || 50) / 1000);

  } else if (revealMods.includes('typewriter')) {

    revealTime = words * ((revealSettings.wordSpeed || 200) / 1000);

  } else if (revealMods.includes('fragmented') || revealMods.includes('radioStatic')) {

    revealTime = words * 0.3 + 1.0; // Approximate staggered reveal

  } else if (revealMods.includes('obscured')) {

    revealTime = chars * 0.15 * 0.7; // Approximate obscured reveal

  }

  

  return readingTime + revealTime;

};



// Theme definitions for dramatic sequences

const SEQUENCE_THEMES = {

  foreboding: {

    name: '🌑 Foreboding',

    description: 'Dark and ominous - danger, dread, horror',

    background: 'rgba(0, 0, 0, 0.95)',

    textColor: '#c92727',

    textColorFaded: 'rgba(201, 39, 39, 0.35)',

    textShadow: '0 0 20px rgba(201, 39, 39, 0.5)',

    fontStyle: 'italic',

    timeColor: 'rgba(255,255,255,0.3)',

    isFullscreen: true,

    defaultEffect: 'glow',

    // Enhanced defaults

    pacing: 'slow',

    introDelay: 1.8,

    recommendedTransition: 'stack',

    recommendedReveal: [],

    recommendedEffects: ['glow'],

    vignette: null,

    particles: { type: 'embers', count: 15, color: 'rgba(201, 39, 39, 0.6)', speed: 'slow', sizeMin: 3, sizeMax: 6 },

  },

  narration: {

    name: '📜 Narration',

    description: 'Neutral storytelling - scene setting',

    background: 'linear-gradient(180deg, rgba(45, 35, 25, 0.98) 0%, rgba(30, 22, 15, 0.99) 100%)',

    textColor: '#e8d5b7',

    textColorFaded: 'rgba(232, 213, 183, 0.5)',

    textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)',

    fontStyle: 'normal',

    timeColor: 'rgba(201, 162, 39, 0.6)',

    fontSize: '1.3rem',

    fontSizeFaded: '1.1rem',

    isFullscreen: false,

    borderColor: '#c9a227',

    defaultEffect: 'none',

    // Enhanced defaults

    pacing: 'normal',

    introDelay: 1.0,

    recommendedTransition: 'stack',

    recommendedReveal: [],

    recommendedEffects: [],

    vignette: { color: '0, 0, 0', intensity: 0.2, spread: 55 },

    particles: null,

  },

  revelation: {

    name: '✨ Revelation',

    description: 'Bright and hopeful - discoveries',

    background: 'linear-gradient(180deg, rgba(10, 20, 40, 0.96) 0%, rgba(20, 35, 55, 0.94) 100%)',

    textColor: '#ffd700',

    textColorFaded: 'rgba(255, 215, 0, 0.4)',

    textShadow: '0 0 30px rgba(255, 215, 0, 0.4), 0 0 60px rgba(255, 200, 100, 0.2)',

    fontStyle: 'normal',

    timeColor: 'rgba(255, 215, 0, 0.4)',

    isFullscreen: true,

    defaultEffect: 'pulse',

    // Enhanced defaults

    pacing: 'normal',

    introDelay: 1.2,

    recommendedTransition: 'stack',

    recommendedReveal: [],

    recommendedEffects: ['pulse'],

    vignette: { color: '0, 0, 0', intensity: 0.3, spread: 50 },

    particles: { type: 'sparkle', count: 22, color: 'rgba(255, 215, 0, 0.7)', speed: 'medium', sizeMin: 2, sizeMax: 5 },

  },

  divine: {

    name: '👁 Divine',

    description: 'Ethereal - gods, cosmic entities',

    background: 'radial-gradient(ellipse at center, rgba(20, 12, 40, 0.96) 0%, rgba(5, 2, 15, 0.98) 100%)',

    textColor: '#d4a5ff',

    textColorFaded: 'rgba(212, 165, 255, 0.35)',

    textShadow: '0 0 25px rgba(180, 100, 255, 0.6), 0 0 50px rgba(120, 50, 200, 0.3)',

    fontStyle: 'italic',

    timeColor: 'rgba(180, 140, 255, 0.4)',

    letterSpacing: '3px',

    isFullscreen: true,

    defaultEffect: 'wave',

    // Enhanced defaults

    pacing: 'slow',

    introDelay: 2.0,

    recommendedTransition: 'centerFocus',

    recommendedReveal: ['letterByLetter'],

    recommendedEffects: ['wave', 'pulse'],

    vignette: { color: '0, 0, 0', intensity: 0.35, spread: 50 },

    particles: { type: 'ascend', count: 15, color: 'rgba(180, 140, 255, 0.35)', speed: 'slow' },

  },

  corruption: {

    name: '🩸 Corruption',

    description: 'Twisted - dark magic, madness',

    background: 'rgba(10, 2, 5, 0.96)',

    textColor: '#8b0000',

    textColorFaded: 'rgba(139, 0, 0, 0.4)',

    textShadow: '0 0 15px rgba(139, 0, 0, 0.8), 0 0 30px rgba(80, 0, 0, 0.5)',

    fontStyle: 'italic',

    timeColor: 'rgba(139, 0, 0, 0.4)',

    isFullscreen: true,

    defaultEffect: 'glitch',

    // Enhanced defaults

    pacing: 'fast',

    introDelay: 1.5,

    recommendedTransition: 'stack',

    recommendedReveal: [],

    recommendedEffects: ['glitch'],

    vignette: null,

    particles: { type: 'drip', count: 14, color: 'rgba(139, 0, 0, 0.7)', speed: 'medium', sizeMin: 2, sizeMax: 4 },

  },

  radioFrequency: {

    name: '📻 Radio Frequency',

    description: 'Tuning between stations - fragmented',

    background: 'rgba(2, 8, 12, 0.96)',

    textColor: '#00ff88',

    textColorFaded: 'rgba(0, 255, 136, 0.3)',

    textShadow: '0 0 10px rgba(0, 255, 136, 0.8), 0 0 20px rgba(0, 200, 100, 0.4)',

    fontStyle: 'normal',

    timeColor: 'rgba(0, 255, 136, 0.4)',

    isFullscreen: true,

    defaultEffect: 'static',

    fontFamily: '"Courier New", monospace',

    // Enhanced defaults

    pacing: 'fast',

    introDelay: 0.8,

    recommendedTransition: 'fadeReplace',

    recommendedReveal: ['radioStatic'],

    recommendedEffects: ['static'],

    vignette: { color: '0, 0, 0', intensity: 0.35, spread: 50 },

    particles: { type: 'static', count: 20, color: 'rgba(0, 255, 136, 0.3)', speed: 'fast' },

  },

  whisper: {

    name: '👻 Whisper',

    description: 'Barely audible - secrets, spirits',

    background: 'rgba(5, 5, 10, 0.92)',

    textColor: 'rgba(200, 200, 220, 0.7)',

    textColorFaded: 'rgba(200, 200, 220, 0.25)',

    textShadow: '0 0 30px rgba(255, 255, 255, 0.2)',

    fontStyle: 'italic',

    timeColor: 'rgba(255,255,255,0.2)',

    fontSize: '1.5rem',

    isFullscreen: true,

    defaultEffect: 'blur',

    // Enhanced defaults

    pacing: 'slow',

    introDelay: 2.2,

    recommendedTransition: 'fadeReplace',

    recommendedReveal: ['obscured'],

    recommendedEffects: ['blur'],

    vignette: { color: '0, 0, 0', intensity: 0.3, spread: 45 },

    particles: { type: 'motes', count: 12, color: 'rgba(200, 200, 220, 0.3)', speed: 'slow', sizeMin: 2, sizeMax: 5 },

  },

};



// Dramatic Overlay Component - modular transitions with combinable effects

const DramaticOverlay = ({ messages: allMessages, charId, onSaveMessages, onMarkStatusSeen, isPreview, previewData, onClosePreview }) => {

  const [visibleMessages, setVisibleMessages] = useState([]);

  const [currentSequence, setCurrentSequence] = useState(null);

  const [fadingOut, setFadingOut] = useState(false);

  const [revealedText, setRevealedText] = useState('');

  const [revealIndex, setRevealIndex] = useState(0);

  const [fragmentedParts, setFragmentedParts] = useState([]);

  const [obscuredChars, setObscuredChars] = useState(new Set());

  const [introPhase, setIntroPhase] = useState(true);

  const [dismissing, setDismissing] = useState(false);

  const timeoutsRef = React.useRef([]);

  

  useEffect(() => {

    return () => {

      timeoutsRef.current.forEach(t => clearTimeout(t));

    };

  }, []);

  

  const urgentMsgs = isPreview ? [] : (allMessages?.statusUpdates || []).filter(m => 

    m.urgency === 'urgent' && m.fromDM && !m.dismissed && !m.seen &&

    (m.visibility === 'public' || (m.recipients || []).includes(charId))

  ).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  

  const activeMsg = isPreview ? previewData : (urgentMsgs.length > 0 ? urgentMsgs[0] : null);

  const settings = activeMsg?.customStyle || {};

  

  // Parse modular transitions - baseTransition + revealModifiers (now an array)

  const baseTransition = activeMsg?.baseTransition || 'stack';

  const revealModifiers = activeMsg?.revealModifiers || [];

  const textEffects = activeMsg?.textEffects || [];

  

  // Check which reveal modifiers are active

  const hasModifier = (mod) => revealModifiers.includes(mod);

  const hasEffect = (eff) => textEffects.includes(eff);

  

  // Timing settings

  const baseSpeed = settings.transitionSpeed || 1;

  const sentencePause = (settings.sentencePause || 1.5) * 1000;

  const letterSpeed = settings.letterSpeed || 50;

  const wordSpeed = settings.wordSpeed || 200;

  const colonSpeed = settings.colonSpeed || 80;

  const revealDuration = settings.revealDuration || 0; // 0 = auto

  

  // Fade settings

  const fadeInDur = settings.fadeInDuration || 1.5;

  const fadeOutDur = settings.fadeOutDuration || 1.2;

  const enableFadeIn = settings.enableFadeIn !== false;

  const enableFadeOut = settings.enableFadeOut !== false;

  

  // Check if text has multiple sentences (for pause logic)

  const hasMultipleSentences = (text) => {

    const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);

    return sentences.length > 1;

  };

  

  // Calculate delay based on revealDuration setting

  const getRevealDelay = (fullText, unitCount, baseDelay) => {

    if (revealDuration > 0) {

      // Fixed duration mode - calculate delay per unit to complete in revealDuration seconds

      return (revealDuration * 1000) / unitCount / baseSpeed;

    }

    return baseDelay / baseSpeed;

  };

  

  // Letter by letter reveal - fixed punctuation timing

  useEffect(() => {

    if (!hasModifier('letterByLetter') || visibleMessages.length === 0) return;

    

    const currentMsg = visibleMessages[visibleMessages.length - 1];

    if (!currentMsg) return;

    

    const fullText = currentMsg.content || '';

    if (revealIndex >= fullText.length) return;

    

    const char = fullText[revealIndex];

    const nextChar = fullText[revealIndex + 1] || '';

    const prevChar = fullText[revealIndex - 1] || '';

    

    // Check for colon sequences (::::)

    const isColon = char === ':';

    const inColonSequence = isColon && (prevChar === ':' || nextChar === ':');

    

    // Pause AFTER punctuation if there's more text and multiple sentences

    const isPunctuation = ['.', '!', '?'].includes(char);

    const shouldPause = isPunctuation && nextChar && nextChar !== ' ' ? false : 

                        isPunctuation && hasMultipleSentences(fullText) && revealIndex < fullText.length - 1;

    

    let delay = getRevealDelay(fullText, fullText.length, letterSpeed);

    if (inColonSequence && revealDuration === 0) delay = colonSpeed / baseSpeed;

    else if (shouldPause && revealDuration === 0) delay = sentencePause;

    

    const timeout = setTimeout(() => {

      setRevealedText(fullText.slice(0, revealIndex + 1));

      setRevealIndex(prev => prev + 1);

    }, delay);

    

    timeoutsRef.current.push(timeout);

    return () => clearTimeout(timeout);

  }, [revealIndex, visibleMessages, revealModifiers, letterSpeed, baseSpeed, sentencePause, colonSpeed, revealDuration]);

  

  // Word by word reveal (typewriter) - fixed punctuation timing

  useEffect(() => {

    if (!hasModifier('typewriter') || hasModifier('letterByLetter') || visibleMessages.length === 0) return;

    

    const currentMsg = visibleMessages[visibleMessages.length - 1];

    if (!currentMsg) return;

    

    const fullText = currentMsg.content || '';

    const words = fullText.split(' ');

    

    if (revealIndex >= words.length) return;

    

    const currentWord = words[revealIndex] || '';

    // Pause AFTER the word with punctuation, only if multiple sentences

    const hasPunctuation = /[.!?]$/.test(currentWord);

    const shouldPause = hasPunctuation && hasMultipleSentences(fullText) && revealIndex < words.length - 1 && revealDuration === 0;

    

    const delay = shouldPause ? sentencePause : getRevealDelay(fullText, words.length, wordSpeed);

    

    const timeout = setTimeout(() => {

      setRevealedText(words.slice(0, revealIndex + 1).join(' '));

      setRevealIndex(prev => prev + 1);

    }, delay);

    

    timeoutsRef.current.push(timeout);

    return () => clearTimeout(timeout);

  }, [revealIndex, visibleMessages, revealModifiers, wordSpeed, baseSpeed, sentencePause]);

  

  // Fragmented/Radio static reveal

  useEffect(() => {

    if ((!hasModifier('fragmented') && !hasModifier('radioStatic')) || visibleMessages.length === 0) return;

    

    const currentMsg = visibleMessages[visibleMessages.length - 1];

    if (!currentMsg) return;

    

    const words = (currentMsg.content || '').split(' ');

    const fragments = words.map((word, i) => ({

      word, visible: false,

      glitching: hasModifier('radioStatic'),

      delay: Math.random() * 2000 + i * 300,

    }));

    

    setFragmentedParts(fragments);

    

    fragments.forEach((frag, i) => {

      const timeout = setTimeout(() => {

        setFragmentedParts(prev => prev.map((f, idx) => 

          idx === i ? { ...f, visible: true, glitching: false } : f

        ));

      }, frag.delay / baseSpeed);

      timeoutsRef.current.push(timeout);

    });

  }, [visibleMessages.length, revealModifiers, baseSpeed]);

  

  // Obscured text reveal

  useEffect(() => {

    if (!hasModifier('obscured') || visibleMessages.length === 0) return;

    

    const currentMsg = visibleMessages[visibleMessages.length - 1];

    if (!currentMsg) return;

    

    const fullText = currentMsg.content || '';

    const initialObscured = new Set();

    for (let i = 0; i < fullText.length; i++) {

      if (fullText[i] !== ' ' && Math.random() > 0.3) initialObscured.add(i);

    }

    setObscuredChars(initialObscured);

    

    Array.from(initialObscured).forEach((charIdx, i) => {

      const timeout = setTimeout(() => {

        setObscuredChars(prev => { const next = new Set(prev); next.delete(charIdx); return next; });

      }, (i * 150 + Math.random() * 500) / baseSpeed);

      timeoutsRef.current.push(timeout);

    });

  }, [visibleMessages.length, revealModifiers, baseSpeed]);

  

  // Handle sequence messages - supports all base transitions with intro phase

  useEffect(() => {

    timeoutsRef.current.forEach(t => clearTimeout(t));

    timeoutsRef.current = [];

    setFadingOut(false);

    setRevealedText('');

    setRevealIndex(0);

    setFragmentedParts([]);

    setObscuredChars(new Set());

    setDismissing(false);

    

    if (!activeMsg) {

      setVisibleMessages([]);

      setCurrentSequence(null);

      setIntroPhase(false);

      return;

    }

    

    // Get theme intro delay

    const themeData = SEQUENCE_THEMES[activeMsg.theme] || SEQUENCE_THEMES.foreboding;

    const introMs = ((activeMsg.customStyle?.introDelay ?? themeData.introDelay) || 1.2) * 1000 / baseSpeed;

    

    setIntroPhase(true);

    setVisibleMessages([]);

    

    // After intro darkness, show first message

    const introTimeout = setTimeout(() => {

      setIntroPhase(false);

      

      if (activeMsg.sequence && activeMsg.sequence.length > 0) {

        setCurrentSequence(activeMsg);

        setVisibleMessages([{ content: activeMsg.sequence[0].text, id: 0 }]);

        

        let totalDelay = 0;

        activeMsg.sequence.forEach((item, idx) => {

          if (idx > 0) {

            // Use auto-delay if delay is 0 or use manual delay

            const itemDelay = item.delay > 0 ? item.delay : getAutoDelay(activeMsg.sequence[idx - 1].text, themeData.pacing, revealModifiers, settings);

            totalDelay += itemDelay * 1000 / baseSpeed;

            

            // Fade out for replace-style transitions

            if (baseTransition === 'fadeReplace' && enableFadeOut) {

              const fadeOutTimeout = setTimeout(() => setFadingOut(true), totalDelay - (fadeOutDur * 1000 / baseSpeed));

              timeoutsRef.current.push(fadeOutTimeout);

            }

            

            const timeout = setTimeout(() => {

              setFadingOut(false);

              setRevealedText('');

              setRevealIndex(0);

              setFragmentedParts([]);

              setObscuredChars(new Set());

              

              if (baseTransition === 'fadeReplace') {

                setVisibleMessages([{ content: item.text, id: idx }]);

              } else if (baseTransition === 'centerFocus') {

                setVisibleMessages(prev => [...prev.slice(-2), { content: item.text, id: idx }]);

              } else if (baseTransition === 'typewriterStack') {

                setVisibleMessages(prev => [...prev, { content: item.text, id: idx }]);

              } else {

                setVisibleMessages(prev => [...prev, { content: item.text, id: idx }]);

              }

            }, totalDelay);

            timeoutsRef.current.push(timeout);

          }

        });

      } else {

        setCurrentSequence(null);

        setVisibleMessages([{ content: activeMsg.content, id: activeMsg.id || 'single' }]);

      }

    }, introMs);

    timeoutsRef.current.push(introTimeout);

    

  }, [activeMsg?.id, isPreview ? JSON.stringify(previewData?.sequence) : null, baseTransition, baseSpeed, fadeOutDur, enableFadeOut]);

  

  const handleDismiss = () => {

    if (dismissing) return;

    

    if (isPreview) { 

      setDismissing(true);

      setTimeout(() => { setDismissing(false); onClosePreview?.(); }, 600);

      return; 

    }

    

    setDismissing(true);

    

    // After fade animation, actually dismiss

    setTimeout(() => {

      if (activeMsg && typeof onMarkStatusSeen === 'function') {

        onMarkStatusSeen(activeMsg.id);

      } else if (activeMsg && allMessages?.statusUpdates) {

        const newUpdates = (allMessages.statusUpdates || []).map(m => 

          m.id === activeMsg.id ? { ...m, seen: true } : m

        );

        onSaveMessages({ ...allMessages, statusUpdates: newUpdates });

      }

      

      setVisibleMessages([]);

      setCurrentSequence(null);

      setRevealedText('');

      setRevealIndex(0);

      setFragmentedParts([]);

      setObscuredChars(new Set());

      setFadingOut(false);

      setDismissing(false);

      setIntroPhase(false);

      timeoutsRef.current.forEach(t => clearTimeout(t));

      timeoutsRef.current = [];

    }, 800);

  };

  

  // FLIP animation refs (must be before any early returns for hooks rules)

  const contentRef = React.useRef(null);

  const prevHeightRef = React.useRef(0);

  

  React.useLayoutEffect(() => {

    const el = contentRef.current;

    if (!el) return;

    

    const newHeight = el.scrollHeight;

    const prevHeight = prevHeightRef.current;

    

    if (prevHeight > 0 && newHeight !== prevHeight) {

      const delta = (newHeight - prevHeight) / 2;

      el.style.transition = 'none';

      el.style.transform = `translateY(${delta}px)`;

      el.getBoundingClientRect();

      el.style.transition = `transform ${3.0 / baseSpeed}s cubic-bezier(0.25, 0.1, 0.25, 1)`;

      el.style.transform = 'translateY(0)';

    }

    

    prevHeightRef.current = newHeight;

  }, [visibleMessages.length, baseSpeed]);



  // Memoize particle configs (must be before any early returns for hooks rules)

  const particleConfigs = React.useMemo(() => {

    if (!activeMsg) return null;

    const bt = SEQUENCE_THEMES[activeMsg.theme] || SEQUENCE_THEMES.foreboding;

    const p = bt.particles;

    if (!p) return null;

    const count = p.count || 10;

    const speedMult = { slow: 1.5, medium: 1.0, fast: 0.6 }[p.speed] || 1.0;

    const sizeMin = p.sizeMin || 2;

    const sizeMax = p.sizeMax || 4;

    

    return Array.from({ length: count }, (_, i) => {

      const baseDuration = (8 + Math.random() * 12) * speedMult;

      const delay = Math.random() * baseDuration;

      const left = Math.random() * 100;

      const top = Math.random() * 100;

      const size = sizeMin + Math.random() * (sizeMax - sizeMin);

      const dx = `${(Math.random() - 0.5) * 150}px`;

      const dy = `${-50 - Math.random() * 150}px`;

      const sparkleRate = 2 + Math.random() * 3;

      const dripRate = 4 + Math.random() * 4;

      const flickerRate = 0.3 + Math.random() * 0.5;

      const staticW = 1 + Math.random() * 2;

      const staticH = 15 + Math.random() * 30;

      return { i, baseDuration, delay, left, top, size, dx, dy, sparkleRate, dripRate, flickerRate, staticW, staticH };

    });

  }, [activeMsg?.theme]);



  if (!activeMsg || (!introPhase && visibleMessages.length === 0 && !dismissing)) return null;

  

  const baseTheme = SEQUENCE_THEMES[activeMsg.theme] || SEQUENCE_THEMES.foreboding;

  const theme = {

    ...baseTheme,

    textColor: settings.textColor || baseTheme.textColor,

    background: settings.bgColor ? `rgba(${hexToRgb(settings.bgColor)}, ${settings.bgOpacity || 0.92})` : baseTheme.background,

  };

  

  const fontFamily = settings.fontFamily || theme.fontFamily || FONT_OPTIONS[settings.font]?.family || 'inherit';

  const baseFontSize = settings.fontSize || theme.fontSize || '1.8rem';

  const fadedFontSize = theme.fontSizeFaded || '1.4rem';

  const textOpacity = settings.textOpacity || 1;

  const isBoxMode = settings.boxMode || !baseTheme.isFullscreen;

  const bgImage = settings.bgImage;

  const bgImageOpacity = settings.bgImageOpacity || 0.3;

  

  // Past text customization settings

  const pastText = settings.pastText || {};

  const showPastText = pastText.show !== false;

  const pastTextOpacity = pastText.opacity ?? 0.4;

  const pastTextScale = pastText.scale ?? 0.85;

  const pastTextColor = pastText.color || null;

  const pastTextBlur = pastText.blur ?? 0;

  const pastTextOffset = pastText.offset ?? 0;

  

  // Use fade settings for animation durations

  const actualFadeIn = enableFadeIn ? `${fadeInDur / baseSpeed}s` : '0s';

  const actualFadeOut = enableFadeOut ? `${fadeOutDur / baseSpeed}s` : '0s';

  const slideInDuration = `${1.8 / baseSpeed}s`;

  

  // Get combined text effect styles - now supports multiple effects

  const getTextEffectStyle = (isLatest) => {

    if (!isLatest || textEffects.length === 0) return {};

    

    const effectDefs = {

      glow: { animation: `glowPulse ${3/baseSpeed}s ease-in-out infinite` },

      flicker: { animation: `flicker ${0.1}s infinite` },

      glitch: { animation: `glitchText ${0.5}s infinite` },

      shake: { animation: `shake ${0.3}s infinite` },

      pulse: { animation: `breathe ${4/baseSpeed}s ease-in-out infinite` },

      blur: { animation: `blurFocus ${5/baseSpeed}s ease-in-out infinite` },

      wave: { animation: `wave ${6/baseSpeed}s ease-in-out infinite` },

      static: { animation: `staticNoise ${0.1}s steps(10) infinite` },

    };

    

    // Combine animations if multiple effects

    const animations = textEffects.map(e => effectDefs[e]?.animation).filter(Boolean);

    if (animations.length === 0) return {};

    

    return { animation: animations.join(', ') };

  };

  

  const renderObscuredText = (text) => text.split('').map((char, i) => {

    if (char === ' ') return <span key={i}>&nbsp;</span>;

    if (obscuredChars.has(i)) {

      const glyphs = '█▓▒░╳╲╱◼◾▪';

      return <span key={i} style={{ opacity: 0.4 }}>{glyphs[Math.floor(Math.random() * glyphs.length)]}</span>;

    }

    return <span key={i}>{char}</span>;

  });

  

  const renderFragmentedText = () => fragmentedParts.map((frag, i) => (

    <span key={i} style={{ 

      opacity: frag.visible ? 1 : 0,

      transition: `opacity ${0.5/baseSpeed}s ease`,

      animation: frag.glitching ? `staticNoise 0.1s steps(5) infinite` : 'none',

      marginRight: '0.3em',

    }}>

      {frag.visible ? frag.word : '░'.repeat(frag.word.length)}

    </span>

  ));

  

  const animationStyles = `

    @keyframes overlayFadeIn { from { opacity: 0; } to { opacity: 1; } }

    @keyframes overlayFadeOut { from { opacity: 1; } to { opacity: 0; } }

    @keyframes cinematicEnter { 

      0% { opacity: 0; transform: translateY(18px) scale(0.97); filter: blur(3px); } 

      50% { opacity: 0.7; transform: translateY(5px) scale(0.99); filter: blur(1px); }

      100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0px); } 

    }

    @keyframes cinematicEnterUp { 

      0% { opacity: 0; transform: translateY(-18px) scale(0.97); filter: blur(3px); } 

      50% { opacity: 0.7; transform: translateY(-5px) scale(0.99); filter: blur(1px); }

      100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0px); } 

    }

    @keyframes messageFadeIn { from { opacity: 0; } to { opacity: 1; } }

    @keyframes messageFadeOut { from { opacity: 1; } to { opacity: 0; } }

    @keyframes messageSlideIn { from { transform: translateY(15px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

    @keyframes messageSlideUp { from { transform: translateY(-15px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

    @keyframes glowPulse { 0%, 100% { filter: brightness(1); text-shadow: 0 0 10px currentColor; } 50% { filter: brightness(1.3); text-shadow: 0 0 20px currentColor, 0 0 30px currentColor; } }

    @keyframes flicker { 0%, 100% { opacity: 1; } 33% { opacity: 0.85; } 66% { opacity: 0.92; } }

    @keyframes glitchText { 0%, 100% { transform: translateX(0); } 10% { transform: translateX(-3px) skewX(-1deg); } 20% { transform: translateX(3px) skewX(1deg); } 30% { transform: translateX(-2px); } 40% { transform: translateX(2px); } 50% { transform: translateX(0); } }

    @keyframes shake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-2px); } 75% { transform: translateX(2px); } }

    @keyframes breathe { 0%, 100% { transform: scale(1); opacity: ${textOpacity}; } 50% { transform: scale(1.015); opacity: ${textOpacity * 0.9}; } }

    @keyframes blurFocus { 0%, 100% { filter: blur(0px); } 50% { filter: blur(1.5px); } }

    @keyframes wave { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }

    @keyframes staticNoise { 0% { transform: translateX(0); } 20% { transform: translateX(-1px); } 40% { transform: translateX(2px); } 60% { transform: translateX(-2px); } 80% { transform: translateX(1px); } }

    @keyframes centerFocusIn { from { transform: scale(0.85); opacity: 0; filter: blur(3px); } to { transform: scale(1); opacity: 1; filter: blur(0px); } }

    @keyframes shrinkToSide { from { transform: scale(1); opacity: 1; } to { transform: scale(0.7); opacity: 0.4; } }

    @keyframes particleFloat { 

      0% { transform: translate(0, 0) scale(1); opacity: 0; } 

      10% { opacity: 0.8; }

      50% { opacity: 0.6; }

      90% { opacity: 0.2; }

      100% { transform: translate(var(--dx), var(--dy)) scale(0.4); opacity: 0; } 

    }

    @keyframes particleAscend { 

      0% { transform: translateY(0) scale(1); opacity: 0; } 

      8% { opacity: 0.7; }

      85% { opacity: 0.3; }

      100% { transform: translateY(-100vh) scale(0.5); opacity: 0; } 

    }

    @keyframes particleDrip { 

      0% { transform: translateY(0); opacity: 0; } 

      5% { opacity: 0.8; }

      85% { opacity: 0.4; }

      100% { transform: translateY(105vh); opacity: 0; } 

    }

    @keyframes particleSparkle { 

      0%, 100% { opacity: 0; transform: scale(0.3); } 

      50% { opacity: 0.9; transform: scale(1.5); } 

    }

    @keyframes particleMote { 

      0% { transform: translate(0, 0); opacity: 0; } 

      20% { opacity: 0.4; }

      50% { transform: translate(var(--dx), var(--dy)); opacity: 0.5; }

      80% { opacity: 0.3; }

      100% { transform: translate(calc(var(--dx) * 1.5), calc(var(--dy) * 0.5)); opacity: 0; } 

    }

    @keyframes staticFlicker {

      0%, 100% { opacity: 0.1; }

      10% { opacity: 0.8; }

      20% { opacity: 0.05; }

      30% { opacity: 0.6; }

      40% { opacity: 0; }

      50% { opacity: 0.3; }

    }

    @keyframes vignetteBreath {

      0%, 100% { opacity: 1; }

      50% { opacity: 0.85; }

    }

  `;

  

  const getDisplayText = (msg, idx) => {

    const isLatest = idx === visibleMessages.length - 1;

    if (!isLatest || revealModifiers.length === 0) return msg.content;

    if (hasModifier('letterByLetter') || hasModifier('typewriter')) return revealedText || '';

    if (hasModifier('fragmented') || hasModifier('radioStatic') || hasModifier('obscured')) return null;

    return msg.content;

  };

  

  // Get animation based on base transition type - cinematic easing

  const getMessageAnimation = (idx) => {

    const isLatest = idx === visibleMessages.length - 1;

    const easing = 'cubic-bezier(0.25, 0.1, 0.25, 1)';

    const enterDur = `${2.0 / baseSpeed}s`;

    if (baseTransition === 'stackUp') return enableFadeIn ? `cinematicEnterUp ${enterDur} ${easing}` : 'none';

    if (baseTransition === 'centerFocus') {

      return isLatest ? `centerFocusIn ${enterDur} ${easing}` : `shrinkToSide ${actualFadeOut} ${easing}`;

    }

    return enableFadeIn ? `cinematicEnter ${enterDur} ${easing}` : 'none';

  };

  

  const renderMessage = (msg, idx) => {

    const isLatest = idx === visibleMessages.length - 1;

    const displayText = getDisplayText(msg, idx);

    const showQuotes = activeMsg.theme !== 'narration';

    

    // Hide past text if showPastText is false

    if (!isLatest && !showPastText) return null;

    

    // Calculate font size - for past text, scale from baseFontSize

    let computedFontSize = baseFontSize;

    if (!isLatest) {

      // Parse baseFontSize (e.g., "1.8rem") and apply scale

      const match = baseFontSize.match(/^([\d.]+)(.*)$/);

      if (match) {

        const value = parseFloat(match[1]) * pastTextScale;

        const unit = match[2] || 'rem';

        computedFontSize = `${value.toFixed(3)}${unit}`;

      }

    }

    

    // Calculate opacity

    let computedOpacity = textOpacity;

    if (isLatest && fadingOut) {

      computedOpacity = 0;

    } else if (!isLatest) {

      // For centerFocus, apply additional stacking effect

      if (baseTransition === 'centerFocus') {

        const pos = visibleMessages.length - 1 - idx;

        const stackMultiplier = Math.max(0.6, 1 - pos * 0.1);

        computedOpacity = pastTextOpacity * stackMultiplier * textOpacity;

      } else {

        computedOpacity = pastTextOpacity * textOpacity;

      }

    }

    

    // Calculate transform for past text

    let computedTransform = 'none';

    if (!isLatest) {

      if (baseTransition === 'centerFocus') {

        const pos = visibleMessages.length - 1 - idx;

        const stackMultiplier = Math.max(0.6, 1 - pos * 0.1);

        computedTransform = `translateY(${pastTextOffset}px) scale(${stackMultiplier})`;

      } else if (pastTextOffset !== 0) {

        computedTransform = `translateY(${pastTextOffset}px)`;

      }

    }

    

    // Calculate filter for past text

    const computedFilter = (!isLatest && pastTextBlur > 0) ? `blur(${pastTextBlur}px)` : 'none';

    

    return (

      <div key={msg.id} style={{

        fontSize: computedFontSize,

        color: isLatest ? theme.textColor : (pastTextColor || theme.textColorFaded),

        marginBottom: '16px',

        fontStyle: theme.fontStyle,

        fontFamily,

        lineHeight: 1.6,

        textShadow: isLatest ? theme.textShadow : 'none',

        letterSpacing: theme.letterSpacing || 'normal',

        animation: getMessageAnimation(idx),

        opacity: computedOpacity,

        transform: computedTransform,

        filter: computedFilter,

        transition: `opacity ${actualFadeOut} ease, color ${2.5 / baseSpeed}s ease, text-shadow ${2.5 / baseSpeed}s ease, transform 0.5s ease, font-size 0.3s ease`,

        transformOrigin: 'center center',

        ...getTextEffectStyle(isLatest),

      }}>

        {displayText !== null ? (

          showQuotes ? `"${String(displayText || '')}"` : String(displayText || '')

        ) : (

          hasModifier('obscured') ? renderObscuredText(msg.content) : renderFragmentedText()

        )}

      </div>

    );

  };

  

  // Render messages in correct order based on transition

  const orderedMessages = baseTransition === 'stackUp' ? [...visibleMessages].reverse() : visibleMessages;

  

  // Render encounter overlay (character, scenery, item)

  if (activeMsg.encounterType && activeMsg.encounter) {

    const enc = activeMsg.encounter;

    const encType = activeMsg.encounterType;

    const style = enc.style || 'portrait';

    const cust = enc.customization || {};

    

    // Default colors per type

    const defaultColors = {

      character: { primary: '#9b59b6', bg: '#1a1525', text: '#e8d5b7' },

      scenery: { primary: '#27ae60', bg: '#141914', text: '#e8d5b7' },

      item: { primary: '#e67e22', bg: '#1e140a', text: '#e8d5b7' },

      quest: { primary: '#e6c229', bg: '#1e1a0a', text: '#e8d5b7' },

      lore: { primary: '#8b7355', bg: '#1a1610', text: '#e8d5b7' },

    };

    const defaults = defaultColors[encType] || defaultColors.character;

    

    const primaryColor = cust.primaryColor || defaults.primary;

    // Auto-derive dark bg from primaryColor if no explicit bgColor

    const deriveBg = (hex) => {

      const r = parseInt(hex.slice(1,3), 16), g = parseInt(hex.slice(3,5), 16), b = parseInt(hex.slice(5,7), 16);

      return `rgb(${Math.round(r*0.08)}, ${Math.round(g*0.08)}, ${Math.round(b*0.08)})`;

    };

    const bgColor = cust.bgColor || (cust.primaryColor ? deriveBg(cust.primaryColor) : defaults.bg);

    const textColor = cust.textColor || defaults.text;

    const imageSize = cust.imageSize || 120;

    const boxSize = cust.boxSize || 500;

    const textSize = cust.textSize || 1.1;

    const nameSize = cust.nameSize || 1.5;

    const imageOpacity = cust.imageOpacity || 1;

    const borderColor = cust.borderColor || primaryColor;

    // When a custom border color is chosen, use it as the overall accent

    const accentColor = cust.borderColor || primaryColor;

    

    // Jumpscare style — true fullscreen, image covers entire screen

    if (style === 'jumpscare') {

      return (

        <div onClick={!isPreview ? handleDismiss : undefined} style={{

          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,

          background: '#000', zIndex: 2000, cursor: !isPreview ? 'pointer' : 'default',

        }}>

          <style>{animationStyles}{`

            @keyframes jumpscareIn { 0% { opacity: 0; transform: scale(1.05); } 100% { opacity: 1; transform: scale(1); } }

            @keyframes jumpscarePulse { 0%,100% { filter: brightness(1); } 50% { filter: brightness(1.05); } }

          `}</style>

          {enc.image && (

            <div style={{

              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,

              backgroundImage: `url(${enc.image})`,

              backgroundSize: 'contain',

              backgroundPosition: 'center',

              backgroundRepeat: 'no-repeat',

              opacity: imageOpacity,

              animation: 'jumpscareIn 1.5s cubic-bezier(0.25, 0.1, 0.25, 1), jumpscarePulse 6s ease-in-out 1.5s infinite',

            }} />

          )}

          {/* Subtle vignette */}

          <div style={{ position:'absolute', inset:0, background:'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.6) 100%)', pointerEvents:'none' }} />

          {/* Name overlay at bottom - only if provided */}

          {enc.name && (

            <div style={{

              position: 'absolute', bottom: 0, left: 0, right: 0,

              background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',

              padding: '40px 30px 30px', textAlign: 'center',

              animation: 'jumpscareIn 2s cubic-bezier(0.25, 0.1, 0.25, 1)',

            }}>

              <div style={{ fontSize: `${nameSize * 1.3}rem`, fontWeight: 'bold', color: accentColor, fontFamily: 'Georgia, serif', textShadow: `0 0 30px ${accentColor}44, 0 2px 8px rgba(0,0,0,0.8)`, letterSpacing: '1px' }}>{enc.name}</div>

              {enc.subtitle && <div style={{ fontSize: '0.85rem', color: textColor, opacity: 0.5, marginTop: '6px', letterSpacing: '2px', textTransform: 'uppercase' }}>{enc.subtitle}</div>}

            </div>

          )}

          {!isPreview && <div style={{ position:'absolute', bottom:'12px', left:'50%', transform:'translateX(-50%)', fontSize:'0.6rem', color:'rgba(255,255,255,0.15)', fontStyle:'italic' }}>Click anywhere to continue</div>}

          {isPreview && <button onClick={handleDismiss} style={{ position:'absolute', top:'16px', right:'16px', padding:'6px 16px', background:'rgba(0,0,0,0.6)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:'4px', color:'rgba(255,255,255,0.5)', cursor:'pointer', fontSize:'0.72rem', zIndex:10 }}>✕ Close</button>}

        </div>

      );

    }



    // Fullscreen style — cinematic immersive

    if (style === 'fullscreen') {

      return (

        <div onClick={!isPreview ? handleDismiss : undefined} style={{

          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,

          background: '#000', zIndex: 2000, cursor: !isPreview ? 'pointer' : 'default',

          animation: 'overlayFadeIn 0.5s ease-out',

        }}>

          <style>{animationStyles}{`

            @keyframes fsTextGlow { 0%,100%{text-shadow:0 0 20px rgba(0,0,0,0.8), 0 0 40px ${accentColor}15} 50%{text-shadow:0 0 20px rgba(0,0,0,0.8), 0 0 60px ${accentColor}25} }

          `}</style>

          {enc.image && (

            <div style={{

              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,

              backgroundImage: `url(${enc.image})`,

              backgroundSize: 'cover', backgroundPosition: 'center',

              opacity: imageOpacity, animation: 'overlayFadeIn 1.5s ease-out',

            }} />

          )}

          <div style={{ position:'absolute', top:0, left:0, right:0, height:'15%', background:'linear-gradient(rgba(0,0,0,0.6), transparent)', pointerEvents:'none' }} />

          <div style={{

            position: 'absolute', bottom: 0, left: 0, right: 0,

            background: `linear-gradient(transparent 0%, ${bgColor}bb 40%, ${bgColor}ee 100%)`,

            padding: '80px 40px 50px', textAlign: 'center',

          }}>

            {enc.name && <div style={{ fontSize: `${nameSize * 1.2}rem`, fontWeight: 'bold', color: accentColor, animation: 'fsTextGlow 4s ease-in-out infinite', marginBottom: '6px', fontFamily: 'Georgia, serif', letterSpacing: '1px' }}>{enc.name}</div>}

            {enc.subtitle && <div style={{ fontSize: '0.85rem', color: textColor, opacity: 0.6, marginBottom: '18px', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 500 }}>{enc.subtitle}</div>}

            {(enc.dialogue || enc.description) && <div style={{ fontSize: `${textSize}rem`, color: textColor, lineHeight: 1.65, maxWidth: '700px', margin: '0 auto', fontFamily: 'Georgia, serif', opacity: 0.92 }}>{enc.dialogue ? `"${enc.dialogue}"` : enc.description}</div>}

          </div>

          {!isPreview && <div style={{ position: 'absolute', bottom: '18px', left: '50%', transform: 'translateX(-50%)', fontSize: '0.68rem', color: accentColor, opacity: 0.3, fontStyle: 'italic' }}>Click anywhere to continue</div>}

          {isPreview && <button onClick={handleDismiss} style={{ position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)', padding: '7px 22px', background: 'rgba(0,0,0,0.5)', border: `1px solid ${accentColor}55`, borderRadius: '4px', color: accentColor, cursor: 'pointer', fontSize: '0.78rem' }}>Close Preview</button>}

        </div>

      );

    }

    

    

    // Card style (horizontal layout — ornate design)

    if (style === 'card') {

      const isQuest = encType === 'quest';

      const isLore = encType === 'lore';

      return (

        <div onClick={!isPreview ? handleDismiss : undefined} style={{

          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,

          background: 'rgba(0,0,0,0.88)', zIndex: 2000,

          display: 'flex', alignItems: 'center', justifyContent: 'center',

          cursor: !isPreview ? 'pointer' : 'default',

          animation: 'overlayFadeIn 0.5s ease-out',

        }}>

          <style>{animationStyles}{`

            @keyframes cardShimmer { 0%,100%{box-shadow:0 0 15px ${accentColor}22,0 20px 60px rgba(0,0,0,0.7)} 50%{box-shadow:0 0 25px ${accentColor}33,0 20px 60px rgba(0,0,0,0.7)} }

            @keyframes cardImgGlow { 0%,100%{box-shadow:0 4px 20px rgba(0,0,0,0.6),0 0 12px ${accentColor}33} 50%{box-shadow:0 4px 24px rgba(0,0,0,0.6),0 0 20px ${accentColor}55} }

          `}</style>

          <div onClick={e => e.stopPropagation()} style={{

            width: `${Math.max(boxSize, 520)}px`, maxWidth: '95%',

            background: isLore

              ? `linear-gradient(160deg, ${bgColor} 0%, rgba(40,34,24,0.98) 60%, ${accentColor}06 100%)`

              : `linear-gradient(160deg, ${bgColor} 0%, ${bgColor} 60%, ${accentColor}08 100%)`,

            border: `1px solid ${borderColor}30`, borderRadius: '6px', padding: '4px',

            animation: `messageSlideIn 0.6s ease-out, cardShimmer 4s ease-in-out infinite`,

            cursor: 'default', position: 'relative',

          }}>

            {isQuest && <div style={{ textAlign:'center', padding:'3px 0', fontSize:'0.58rem', fontWeight:700, letterSpacing:'3px', textTransform:'uppercase', color:accentColor, opacity:0.7 }}>✦ NEW QUEST ✦</div>}

            {isLore && <div style={{ textAlign:'center', padding:'3px 0', fontSize:'0.58rem', fontWeight:700, letterSpacing:'3px', textTransform:'uppercase', color:accentColor, opacity:0.6 }}>— LORE —</div>}

            <div style={{ border: `2px solid ${borderColor}50`, borderRadius: '4px', display: 'flex', flexDirection: 'row', overflow: 'hidden', position: 'relative', padding: '20px', gap: '20px', alignItems: 'center', minHeight: `${Math.max(imageSize + 40, 160)}px` }}>

              {[{t:'0',l:'0',r:'none'},{t:'0',r:'0',l:'none',tr:'scaleX(-1)'},{b:'0',l:'0',t:'auto',tr:'scaleY(-1)'},{b:'0',r:'0',t:'auto',l:'auto',tr:'scale(-1)'}].map((pos,i) => (

                <div key={i} style={{ position:'absolute', width:'20px', height:'20px', top:pos.t, bottom:pos.b, left:pos.l, right:pos.r, transform:pos.tr||'none', borderTop: i<2 ? `2px solid ${borderColor}` : 'none', borderLeft: i%2===0 ? `2px solid ${borderColor}` : 'none', borderBottom: i>=2 ? `2px solid ${borderColor}` : 'none', borderRight: i%2===1 ? `2px solid ${borderColor}` : 'none', pointerEvents:'none', zIndex:2 }} />

              ))}

              {enc.image && (

                <div style={{ width: `${imageSize}px`, height: `${imageSize}px`, flexShrink: 0, borderRadius: encType === 'item' ? '6px' : '8px', border: `3px solid ${borderColor}`, overflow: 'hidden', animation: 'cardImgGlow 3s ease-in-out infinite', position: 'relative' }}>

                  <img src={enc.image} alt={enc.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />

                  <div style={{ position:'absolute', inset:0, boxShadow:'inset 0 0 20px rgba(0,0,0,0.4)', pointerEvents:'none' }} />

                </div>

              )}

              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>

                <div style={{ fontSize: `${nameSize}rem`, fontWeight: 'bold', color: accentColor, marginBottom: '2px', fontFamily: isLore ? '"Palatino Linotype", Georgia, serif' : 'Georgia, serif', textShadow: `0 1px 3px rgba(0,0,0,0.5), 0 0 12px ${accentColor}22`, lineHeight: 1.2 }}>{enc.name}</div>

                {(enc.title || enc.subtitle) && <div style={{ fontSize: '0.72rem', color: accentColor, opacity: 0.8, fontStyle: 'italic', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '2px', fontWeight: 600 }}>{enc.title || enc.subtitle}</div>}

                {(enc.quote || enc.dialogue || enc.description) && <div style={{ fontSize: `${textSize * 0.95}rem`, color: textColor, lineHeight: 1.65, fontFamily: 'Georgia, serif', opacity: 0.92, fontStyle: isLore ? 'italic' : 'normal' }}>{enc.quote ? enc.quote : enc.dialogue ? `"${enc.dialogue}"` : enc.description}</div>}

              </div>

            </div>

            <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>

              {!isPreview && <div style={{ fontSize: '0.65rem', color: accentColor, opacity: 0.4, fontStyle: 'italic' }}>Click outside to continue</div>}

              {isPreview && <button onClick={handleDismiss} style={{ padding: '5px 18px', background: `${accentColor}18`, border: `1px solid ${accentColor}55`, borderRadius: '4px', color: accentColor, cursor: 'pointer', fontSize: '0.75rem' }}>Close Preview</button>}

            </div>

          </div>

        </div>

      );

    }



    // Portrait style (default) — type-specific treatments

    

    // Item portrait — compact horizontal layout (distinct from ornate card)

    if (encType === 'item' && style === 'portrait') {

      return (

        <div onClick={!isPreview ? handleDismiss : undefined} style={{

          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,

          background: 'rgba(0,0,0,0.88)', zIndex: 2000,

          display: 'flex', alignItems: 'center', justifyContent: 'center',

          cursor: !isPreview ? 'pointer' : 'default',

          animation: 'overlayFadeIn 0.5s ease-out',

        }}>

          <style>{animationStyles}{`

            @keyframes itemGlow { 0%,100%{box-shadow:0 4px 20px rgba(0,0,0,0.6), 0 0 12px ${accentColor}22} 50%{box-shadow:0 4px 24px rgba(0,0,0,0.6), 0 0 20px ${accentColor}44} }

          `}</style>

          <div onClick={e => e.stopPropagation()} style={{

            width: `${Math.max(boxSize, 480)}px`, maxWidth: '95%',

            background: `linear-gradient(160deg, rgba(28,22,16,0.98) 0%, rgba(22,18,12,0.99) 100%)`,

            border: `1px solid ${borderColor}25`,

            borderRadius: '10px',

            display: 'flex', flexDirection: 'row',

            overflow: 'hidden', padding: '18px',

            gap: '18px', alignItems: 'center',

            boxShadow: `0 16px 50px rgba(0,0,0,0.7), 0 0 1px ${accentColor}40`,

            animation: 'messageSlideIn 0.6s ease-out',

            cursor: 'default',

          }}>

            {enc.image && (

              <div style={{

                width: `${imageSize}px`, height: `${imageSize}px`, flexShrink: 0,

                borderRadius: '6px',

                border: `2.5px solid ${borderColor}88`,

                overflow: 'hidden',

                animation: 'itemGlow 3.5s ease-in-out infinite',

                position: 'relative',

              }}>

                <img src={enc.image} alt={enc.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />

                <div style={{ position:'absolute', inset:0, boxShadow:'inset 0 0 15px rgba(0,0,0,0.5)', pointerEvents:'none' }} />

              </div>

            )}

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>

              <div style={{ fontSize: `${nameSize}rem`, fontWeight: 'bold', color: accentColor, marginBottom: '3px', fontFamily: 'Georgia, "Times New Roman", serif', lineHeight: 1.2 }}>{enc.name}</div>

              {(enc.title || enc.subtitle) && <div style={{ fontSize: '0.7rem', color: accentColor, opacity: 0.75, fontStyle: 'italic', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: 600 }}>{enc.title || enc.subtitle}</div>}

              {(enc.quote || enc.dialogue || enc.description) && <div style={{ fontSize: `${textSize * 0.95}rem`, color: textColor, lineHeight: 1.6, fontFamily: 'Georgia, serif', opacity: 0.9 }}>{enc.quote ? enc.quote : enc.dialogue ? `"${enc.dialogue}"` : enc.description}</div>}

              {!isPreview && <div style={{ fontSize: '0.6rem', color: accentColor, opacity: 0.35, marginTop: '12px', fontStyle: 'italic' }}>Click outside to continue</div>}

              {isPreview && <button onClick={handleDismiss} style={{ marginTop: '10px', padding: '5px 18px', background: `${accentColor}15`, border: `1px solid ${accentColor}44`, borderRadius: '4px', color: accentColor, cursor: 'pointer', fontSize: '0.72rem', alignSelf: 'flex-start' }}>Close Preview</button>}

            </div>

          </div>

        </div>

      );

    }

    

    const isCharType = encType === 'character';

    const isLoreType = encType === 'lore';

    const isQuestType = encType === 'quest';

    const portraitRound = isCharType;

    const imageOverlap = isCharType;

    

    return (

      <div onClick={!isPreview ? handleDismiss : undefined} style={{

        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,

        background: 'rgba(0,0,0,0.88)', zIndex: 2000,

        display: 'flex', alignItems: 'center', justifyContent: 'center',

        cursor: !isPreview ? 'pointer' : 'default',

        animation: 'overlayFadeIn 0.5s ease-out',

      }}>

        <style>{animationStyles}{`

          @keyframes portraitPulse { 0%,100%{box-shadow:0 0 20px ${borderColor}44, 0 4px 24px rgba(0,0,0,0.5)} 50%{box-shadow:0 0 32px ${borderColor}66, 0 4px 24px rgba(0,0,0,0.5)} }

          @keyframes cardGlow { 0%,100%{box-shadow:0 20px 60px rgba(0,0,0,0.7), 0 0 30px ${accentColor}15} 50%{box-shadow:0 20px 60px rgba(0,0,0,0.7), 0 0 45px ${accentColor}25} }

        `}</style>

        <div onClick={e => e.stopPropagation()} style={{

          width: `${boxSize}px`, maxWidth: '95%',

          background: isLoreType ? `linear-gradient(180deg, ${bgColor} 0%, ${bgColor}f8 60%, rgba(60,50,35,0.15) 100%)` : `linear-gradient(180deg, ${bgColor} 0%, ${bgColor}f0 100%)`,

          border: `2px solid ${borderColor}35`, borderRadius: '12px',

          padding: '18px 20px 14px',

          paddingTop: enc.image && imageOverlap ? `${imageSize / 2 + 16}px` : '18px',

          animation: `messageSlideIn 0.6s ease-out, cardGlow 5s ease-in-out infinite`,

          cursor: 'default', textAlign: 'center', position: 'relative',

          marginTop: enc.image && imageOverlap ? `${imageSize / 2}px` : 0,

        }}>

          {isQuestType && <div style={{ position:'absolute', top:'-1px', left:'50%', transform:'translateX(-50%)', padding:'2px 20px', background:`linear-gradient(90deg, transparent, ${accentColor}30, ${accentColor}50, ${accentColor}30, transparent)`, borderRadius:'0 0 8px 8px', fontSize:'0.6rem', fontWeight:700, letterSpacing:'3px', textTransform:'uppercase', color:accentColor }}>NEW QUEST</div>}

          {isLoreType && <div style={{ position:'absolute', top:0, left:0, right:0, bottom:0, borderRadius:'12px', pointerEvents:'none', opacity:0.04, backgroundImage:`repeating-linear-gradient(0deg, transparent, transparent 30px, ${accentColor} 30px, ${accentColor} 31px)` }} />}

          {enc.image && imageOverlap && <div style={{ position:'absolute', top:0, left:'50%', transform:'translateX(-50%)', width:'60%', height:'2px', background:`linear-gradient(90deg, transparent, ${accentColor}60, transparent)` }} />}

          {enc.image && (

            <div style={{

              width: `${imageSize}px`, height: `${imageSize}px`,

              borderRadius: portraitRound ? '50%' : '10px',

              border: `3px solid ${borderColor}`, overflow: 'hidden',

              margin: imageOverlap ? '0 auto' : '0 auto 16px',

              animation: 'portraitPulse 3.5s ease-in-out infinite',

              position: imageOverlap ? 'absolute' : 'relative',

              top: imageOverlap ? `-${imageSize / 2}px` : 'auto',

              left: imageOverlap ? '50%' : 'auto',

              transform: imageOverlap ? 'translateX(-50%)' : 'none',

              background: bgColor,

            }}>

              <img src={enc.image} alt={enc.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />

              <div style={{ position:'absolute', inset:0, boxShadow:'inset 0 0 15px rgba(0,0,0,0.3)', borderRadius:'inherit', pointerEvents:'none' }} />

            </div>

          )}

          {enc.image && imageOverlap && <div style={{ width:'40px', height:'1px', background:`${accentColor}30`, margin:'0 auto 8px' }} />}

          <div style={{ fontSize: `${nameSize}rem`, fontWeight: 'bold', color: accentColor, marginBottom: '2px', fontFamily: isLoreType ? '"Palatino Linotype", Georgia, serif' : 'Georgia, serif', textShadow: `0 0 15px ${accentColor}33`, letterSpacing: isLoreType ? '1.5px' : '0.5px' }}>{enc.name}</div>

          {enc.subtitle && <div style={{ fontSize: '0.75rem', color: textColor, opacity: 0.45, fontStyle: 'italic', marginBottom: '10px' }}>{enc.subtitle}</div>}

          {enc.description && <div style={{ fontSize: `${textSize * 0.88}rem`, color: textColor, opacity: 0.8, lineHeight: 1.55, marginBottom: enc.dialogue ? '12px' : 0, fontStyle: isLoreType ? 'italic' : 'normal', fontFamily: 'Georgia, serif' }}>{enc.description}</div>}

          {enc.dialogue && (

            <div style={{ background: 'rgba(0,0,0,0.35)', borderRadius: '10px', padding: '14px 18px', border: `1px solid ${accentColor}20`, textAlign: 'left', marginTop: '8px' }}>

              <div style={{ fontSize: `${textSize}rem`, color: textColor, fontStyle: 'italic', lineHeight: 1.6, fontFamily: 'Georgia, serif', opacity: 0.9 }}>{(() => { const d = enc.dialogue.replace(/^"+|"+$/g, ''); return d ? `"${d}"` : ''; })()}</div>

            </div>

          )}

          {activeMsg.gameTime && <div style={{ fontSize: '0.72rem', color: `${accentColor}66`, marginTop: '16px', fontStyle: 'italic' }}>Day {activeMsg.gameTime.day}{activeMsg.gameTime.time ? `, ${activeMsg.gameTime.time}` : ''}</div>}

          <div style={{ marginTop: '14px' }}>

            {!isPreview && <div style={{ fontSize: '0.65rem', color: accentColor, opacity: 0.35, fontStyle: 'italic' }}>Click outside to continue</div>}

            {isPreview && <button onClick={handleDismiss} style={{ padding: '7px 22px', background: `${accentColor}18`, border: `1px solid ${accentColor}55`, borderRadius: '6px', color: accentColor, cursor: 'pointer', fontSize: '0.78rem' }}>Close Preview</button>}

          </div>

        </div>

      </div>

    );

  }

  

  // Vignette overlay renderer - subtle edge darkening

  const renderVignette = () => {

    const v = baseTheme.vignette;

    if (!v) return null;

    // If DM explicitly disabled vignette, skip

    if (settings.disableVignette) return null;

    // When a bg image is present, always use black vignette to avoid color bleed during fade-in

    const vignetteColor = bgImage ? '0, 0, 0' : (v.color || '0, 0, 0');

    const vignetteIntensity = v.intensity || 0.3;

    return (

      <div style={{

        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,

        background: `radial-gradient(ellipse at center, transparent ${(v.spread || 50) + 10}%, rgba(${vignetteColor}, ${vignetteIntensity}) 100%)`,

        pointerEvents: 'none', zIndex: 1,

        animation: `vignetteBreath ${8 / baseSpeed}s ease-in-out infinite`,

      }} />

    );

  };

  

  // CSS particle renderer - uses stable memoized configs

  const renderParticles = () => {

    const p = baseTheme.particles;

    if (!p || !particleConfigs) return null;

    if (settings.disableParticles) return null;

    

    return (

      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 1 }}>

        {particleConfigs.map((cfg, i) => {

          let anim = '';

          let extraStyle = {};

          if (p.type === 'embers') {

            anim = `particleFloat ${cfg.baseDuration}s ${cfg.delay}s ease-in-out infinite`;

          } else if (p.type === 'ascend') {

            anim = `particleAscend ${cfg.baseDuration}s ${cfg.delay}s linear infinite`;

          } else if (p.type === 'sparkle') {

            anim = `particleSparkle ${cfg.sparkleRate}s ${cfg.delay}s ease-in-out infinite`;

          } else if (p.type === 'drip') {

            anim = `particleDrip ${cfg.dripRate}s ${cfg.delay}s linear infinite`;

            extraStyle = { borderRadius: '40% 40% 50% 50%', top: '-5%' };

          } else if (p.type === 'motes') {

            anim = `particleMote ${cfg.baseDuration * 1.2}s ${cfg.delay}s ease-in-out infinite`;

          } else if (p.type === 'static') {

            anim = `staticFlicker ${cfg.flickerRate}s ${cfg.delay}s steps(6) infinite`;

            extraStyle = { width: `${cfg.staticW}px`, height: `${cfg.staticH}px`, borderRadius: '1px' };

          }

          

          return (

            <div key={i} style={{

              position: 'absolute',

              left: `${cfg.left}%`,

              top: p.type === 'drip' ? '-5%' : `${cfg.top}%`,

              width: `${cfg.size}px`,

              height: `${cfg.size}px`,

              borderRadius: '50%',

              background: p.color,

              animation: anim,

              '--dx': cfg.dx,

              '--dy': cfg.dy,

              opacity: 0,

              ...extraStyle,

            }} />

          );

        })}

      </div>

    );

  };

  

  // Dismiss animation style

  const dismissStyle = dismissing ? {

    animation: `overlayFadeOut 0.8s cubic-bezier(0.25, 0.1, 0.25, 1) forwards`,

  } : {};

  

  // Intro phase style

  const introStyle = introPhase ? { cursor: 'default' } : {};

  

  // Box mode

  if (isBoxMode) {

    return (

      <div 

        onClick={!isPreview && !introPhase ? handleDismiss : undefined}

        style={{

          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,

          background: 'rgba(0,0,0,0.6)', zIndex: 2000,

          display: 'flex', alignItems: 'center', justifyContent: 'center',

          animation: enableFadeIn ? `overlayFadeIn ${actualFadeIn} cubic-bezier(0.25, 0.1, 0.25, 1)` : 'none',

          cursor: !isPreview ? 'pointer' : 'default',

          ...dismissStyle,

          ...introStyle,

        }}>

        <style>{animationStyles}</style>

        <div onClick={e => e.stopPropagation()} style={{

          maxWidth: '600px', width: '90%',

          background: theme.background,

          border: `3px solid ${theme.borderColor || '#c9a227'}`,

          borderRadius: '12px', padding: '30px 40px',

          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',

          animation: `cinematicEnter ${2.0 / baseSpeed}s cubic-bezier(0.25, 0.1, 0.25, 1)`,

          position: 'relative', overflow: 'hidden',

          cursor: 'default',

        }}>

          {['top-left', 'top-right', 'bottom-left', 'bottom-right'].map(pos => (

            <div key={pos} style={{

              position: 'absolute',

              [pos.includes('top') ? 'top' : 'bottom']: '10px',

              [pos.includes('left') ? 'left' : 'right']: '10px',

              width: '20px', height: '20px',

              [pos.includes('top') ? 'borderTop' : 'borderBottom']: `2px solid ${theme.borderColor || '#c9a227'}`,

              [pos.includes('left') ? 'borderLeft' : 'borderRight']: `2px solid ${theme.borderColor || '#c9a227'}`,

            }} />

          ))}

          {bgImage && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: bgImageOpacity, zIndex: 0 }} />}

          {renderVignette()}

          <div ref={contentRef} style={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>

            {!introPhase && orderedMessages.map((msg, idx) => renderMessage(msg, baseTransition === 'stackUp' ? orderedMessages.length - 1 - idx : idx))}

            {activeMsg.gameTime && !introPhase && <div style={{ fontSize: '0.85rem', color: theme.timeColor, marginTop: '20px' }}>Day {activeMsg.gameTime.day}{activeMsg.gameTime.time ? `, ${activeMsg.gameTime.time}` : ''}</div>}

            {!isPreview && !introPhase && <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)', marginTop: '16px' }}>Click outside to continue</div>}

            {isPreview && !introPhase && <button onClick={handleDismiss} style={{ marginTop: '20px', padding: '8px 24px', background: 'rgba(201,162,39,0.2)', border: '1px solid #c9a227', borderRadius: '4px', color: '#c9a227', cursor: 'pointer', fontSize: '0.85rem' }}>Close Preview</button>}

          </div>

        </div>

      </div>

    );

  }

  

  // Fullscreen mode

  return (

    <div 

      onClick={!isPreview && !introPhase ? handleDismiss : undefined}

      style={{

        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,

        background: introPhase ? '#000' : theme.background, zIndex: 2000,

        display: 'flex', alignItems: 'center', justifyContent: 'center',

        animation: `overlayFadeIn ${Math.max(0.8, (baseTheme.introDelay || 1.2) / baseSpeed)}s cubic-bezier(0.25, 0.1, 0.25, 1)`,

        cursor: !isPreview && !introPhase ? 'pointer' : 'default',

        transition: `background ${1.0 / baseSpeed}s ease`,

        ...dismissStyle,

      }}>

      <style>{animationStyles}</style>

      {bgImage && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: introPhase ? 0 : bgImageOpacity, zIndex: 0, transition: `opacity ${1.5 / baseSpeed}s ease` }} />}

      {renderVignette()}

      {renderParticles()}

      <div ref={contentRef} style={{ maxWidth: '700px', padding: '50px 60px', textAlign: 'center', position: 'relative', zIndex: 2 }}>

        {!introPhase && orderedMessages.map((msg, idx) => renderMessage(msg, baseTransition === 'stackUp' ? orderedMessages.length - 1 - idx : idx))}

        {activeMsg.gameTime && !introPhase && <div style={{ fontSize: '0.9rem', color: theme.timeColor, marginTop: '40px' }}>Day {activeMsg.gameTime.day}{activeMsg.gameTime.time ? `, ${activeMsg.gameTime.time}` : ''}</div>}

        {!isPreview && !introPhase && visibleMessages.length > 0 && <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.2)', marginTop: '40px', animation: `messageFadeIn 2s ease` }}>Click anywhere to continue</div>}

        {isPreview && !introPhase && <button onClick={handleDismiss} style={{ marginTop: '50px', padding: '12px 35px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '4px', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: '0.9rem' }}>Close Preview</button>}

      </div>

    </div>

  );

};



// Helper function to convert hex to rgb

const hexToRgb = (hex) => {

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '0, 0, 0';

};



// Backwards compatibility wrapper

const UrgentMessageOverlay = (props) => <DramaticOverlay {...props} />;



const MessagePanel = ({
  isOpen,
  onClose,
  displayMessages = null,
  messages,
  charId,
  charName,
  onSaveMessages,
  onDismissStatusMessage,
  onMarkDmChatRead,
  onSendDmChatMessage,
  onDeleteDmChatMessage,
  isDM,
}) => {

  const [activeTab, setActiveTab] = useState('status');

  const [newMessage, setNewMessage] = useState('');

  const panelMessages = displayMessages || messages || { statusUpdates: [], dmChats: {} };

  const writeMessages = messages || panelMessages;

  const canFallbackWrite = !!messages && typeof onSaveMessages === 'function';



  // Mark DM messages as read when viewing (must be above the conditional return to keep hook order stable)

  useEffect(() => {

    if (!isOpen) return;

    if (activeTab !== 'dm' || isDM) return;



    const unreadDMMessages = (panelMessages.dmChats?.[charId] || []).filter(

      (m) => !m.deleted && !m.read && m.from === 'DM'

    );

    if (unreadDMMessages.length === 0) return;

    if (typeof onMarkDmChatRead === 'function') {
      onMarkDmChatRead({
        charId,
        messageIds: unreadDMMessages.map(m => m.id).filter(Boolean).slice(0, 100),
      });
      return;
    }



    const newChats = { ...(messages.dmChats || {}) };

    newChats[charId] = (newChats[charId] || []).map((m) =>

      (m.from === 'DM' && !m.read) ? { ...m, read: true } : m

    );



    onSaveMessages({ ...writeMessages, dmChats: newChats });

  }, [activeTab, isOpen, isDM, charId, panelMessages, writeMessages, onSaveMessages, onMarkDmChatRead, canFallbackWrite, messages]);



  if (!isOpen) return null;

  

  const statusUpdates = (panelMessages.statusUpdates || []).filter(m =>

    !m.dismissed && (m.visibility === 'public' || (m.recipients || []).includes(charId))

  ).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  

  const dmChat = (panelMessages.dmChats?.[charId] || []).filter(m => !m.deleted)

    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  

  const handleDismiss = (msgId) => {

    if (typeof onDismissStatusMessage === 'function') {
      onDismissStatusMessage(msgId);
      return;
    }

    if (!canFallbackWrite) return;

    const newUpdates = (writeMessages.statusUpdates || []).map(m =>

      m.id === msgId ? { ...m, dismissed: true } : m

    );

    onSaveMessages({ ...writeMessages, statusUpdates: newUpdates });

  };

  

  const handleDeleteDM = (msgId) => {

    if (typeof onDeleteDmChatMessage === 'function') {
      onDeleteDmChatMessage({ charId, messageId: msgId });
      return;
    }

    if (!canFallbackWrite) return;

    const newChats = { ...(writeMessages.dmChats || {}) };

    newChats[charId] = (newChats[charId] || []).map(m => 

      m.id === msgId ? { ...m, deleted: true } : m

    );

    onSaveMessages({ ...writeMessages, dmChats: newChats });

  };

  

  const handleSendDM = () => {

    if (!newMessage.trim()) return;

    const message = {

      id: generateId(),

      type: 'dm-message',

      timestamp: new Date().toISOString(),

      from: isDM ? 'DM' : charId,

      content: newMessage.trim(),

      urgency: 'normal',

      characterId: charId,

      read: isDM, // If DM sends, player hasn't read. If player sends, DM sees immediately

    };

    if (typeof onSendDmChatMessage === 'function') {
      onSendDmChatMessage({ charId, message });
      setNewMessage('');
      return;
    }

    if (!canFallbackWrite) return;

    const newChats = { ...(writeMessages.dmChats || {}) };

    if (!newChats[charId]) newChats[charId] = [];

    newChats[charId] = [...newChats[charId], message];

    onSaveMessages({ ...writeMessages, dmChats: newChats });

    setNewMessage('');

  };



  return (

    <div style={{

      position: 'fixed',

      top: 0,

      left: 0,

      right: 0,

      bottom: 0,

      background: 'rgba(0,0,0,0.6)',

      zIndex: 10000,

      display: 'flex',

      alignItems: 'center',

      justifyContent: 'center',

    }}>

      <div style={{

        width: '400px',

        maxWidth: '95vw',

        maxHeight: '70vh',

        background: 'linear-gradient(180deg, rgba(40,30,20,0.99) 0%, rgba(30,20,15,1) 100%)',

        border: '2px solid #c9a227',

        borderRadius: '8px',

        display: 'flex',

        flexDirection: 'column',

        boxShadow: '0 10px 40px rgba(0,0,0,0.5)',

      }}>

        {/* Header */}

        <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(201,162,39,0.3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

          <h2 style={{ margin: 0, color: '#c9a227', fontSize: '1.1rem' }}>📬 Messages</h2>

          <button onClick={onClose} style={{ ...S.btn, ...S.btnXs }}>✕</button>

        </div>

        

        {/* Tabs */}

        <div style={{ display: 'flex', borderBottom: '1px solid rgba(201,162,39,0.3)' }}>

          <button 

            onClick={() => setActiveTab('status')}

            style={{ 

              flex: 1, 

              padding: '10px', 

              background: activeTab === 'status' ? 'rgba(201,162,39,0.2)' : 'transparent',

              border: 'none',

              borderBottom: activeTab === 'status' ? '2px solid #c9a227' : '2px solid transparent',

              color: activeTab === 'status' ? '#c9a227' : '#e8d5b7',

              cursor: 'pointer',

              fontSize: '0.85rem',

            }}

          >

            📋 Status

          </button>

          <button 

            onClick={() => setActiveTab('dm')}

            style={{ 

              flex: 1, 

              padding: '10px', 

              background: activeTab === 'dm' ? 'rgba(201,162,39,0.2)' : 'transparent',

              border: 'none',

              borderBottom: activeTab === 'dm' ? '2px solid #c9a227' : '2px solid transparent',

              color: activeTab === 'dm' ? '#c9a227' : '#e8d5b7',

              cursor: 'pointer',

              fontSize: '0.85rem',

            }}

          >

            💬 DM Chat

            {dmChat.filter(m => !m.read && m.from === 'DM').length > 0 && !isDM && (

              <span style={{ marginLeft: '6px', background: '#c92727', color: 'white', borderRadius: '10px', padding: '2px 6px', fontSize: '0.7rem' }}>

                {dmChat.filter(m => !m.read && m.from === 'DM').length}

              </span>

            )}

          </button>

        </div>

        

        {/* Content */}

        <div style={{ flex: 1, overflowY: 'auto', padding: '12px', minHeight: '150px', maxHeight: '300px' }}>

          {activeTab === 'status' && (

            statusUpdates.length === 0 

              ? <p style={{ opacity: 0.6, textAlign: 'center', fontSize: '0.9rem' }}>No status updates</p>

              : statusUpdates.map(m => (

                  <MessageItem key={m.id} message={m} onDismiss={handleDismiss} />

                ))

          )}

          

          {activeTab === 'dm' && (

            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

              <div style={{ flex: 1, overflowY: 'auto' }}>

                {dmChat.length === 0 

                  ? <p style={{ opacity: 0.6, textAlign: 'center', fontSize: '0.9rem' }}>No messages yet</p>

                  : dmChat.map(m => (

                      <div 

                        key={m.id}

                        style={{

                          marginBottom: '10px',

                          textAlign: m.from === 'DM' ? 'left' : 'right',

                        }}

                      >

                        <div style={{

                          display: 'inline-block',

                          maxWidth: '85%',

                          padding: '8px 12px',

                          borderRadius: m.from === 'DM' ? '4px 12px 12px 12px' : '12px 4px 12px 12px',

                          background: m.from === 'DM' ? 'rgba(74,144,217,0.3)' : 'rgba(39,201,90,0.3)',

                          border: `1px solid ${m.from === 'DM' ? 'rgba(74,144,217,0.5)' : 'rgba(39,201,90,0.5)'}`,

                        }}>

                          <div style={{ fontSize: '0.85rem', color: '#e8d5b7' }}>{String(m.content || '')}</div>

                          <div style={{ fontSize: '0.65rem', opacity: 0.6, marginTop: '3px' }}>

                            {m.gameTime ? `Day ${m.gameTime.day}${m.gameTime.time ? `, ${m.gameTime.time}` : ''}` : formatRelativeTime(m.timestamp)}

                          </div>

                        </div>

                      </div>

                    ))

                }

              </div>

            </div>

          )}

        </div>

        

        {/* Chat Input (for DM tab) */}

        {activeTab === 'dm' && (

          <div style={{ padding: '10px 12px', borderTop: '1px solid rgba(201,162,39,0.3)', display: 'flex', gap: '8px' }}>

            <input 

              style={{ ...S.input, flex: 1, marginBottom: 0, padding: '8px', fontSize: '0.85rem' }}

              placeholder="Type a message..."

              value={newMessage}

              onChange={e => setNewMessage(e.target.value)}

              onKeyPress={e => e.key === 'Enter' && handleSendDM()}

            />

            <button style={{ ...S.btn, ...S.btnSuccess, ...S.btnSm }} onClick={handleSendDM}>Send</button>

          </div>

        )}

      </div>

    </div>

  );

};



// Encounter Preview Overlay Component - for character/scenery/item encounters

const EncounterPreviewOverlay = ({ encounterType, encounter, onClose }) => {

  const [revealedText, setRevealedText] = useState('');

  const [revealIndex, setRevealIndex] = useState(0);

  

  const enc = encounter;

  const encType = encounterType;

  const style = enc.style || 'portrait';

  const cust = enc.customization || {};

  

  // Default colors per type

  const defaultColors = {

    character: { primary: '#9b59b6', bg: '#1a1525', text: '#e8d5b7' },

    scenery: { primary: '#27ae60', bg: '#141914', text: '#e8d5b7' },

    item: { primary: '#e67e22', bg: '#1e140a', text: '#e8d5b7' },

    quest: { primary: '#e6c229', bg: '#1e1a0a', text: '#e8d5b7' },

    lore: { primary: '#8b7355', bg: '#1a1610', text: '#e8d5b7' },

  };

  const defaults = defaultColors[encounterType] || defaultColors.character;

  

  const primaryColor = cust.primaryColor || defaults.primary;

  const deriveBg2 = (hex) => {

    const r = parseInt(hex.slice(1,3), 16), g = parseInt(hex.slice(3,5), 16), b = parseInt(hex.slice(5,7), 16);

    return `rgb(${Math.round(r*0.08)}, ${Math.round(g*0.08)}, ${Math.round(b*0.08)})`;

  };

  const bgColor = cust.bgColor || (cust.primaryColor ? deriveBg2(cust.primaryColor) : defaults.bg);

  const textColor = cust.textColor || defaults.text;

  const imageSize = cust.imageSize || 120;

  const boxSize = cust.boxSize || 500;

  const textSize = cust.textSize || 1.1;

  const nameSize = cust.nameSize || 1.5;

  const imageOpacity = cust.imageOpacity || 1;

  const borderColor = cust.borderColor || primaryColor;

  // When a custom border color is chosen, use it as the overall accent

  const accentColor = cust.borderColor || primaryColor;

  const textReveal = cust.textReveal || 'instant';

  

  // Text reveal effect

  const textToReveal = enc.dialogue || enc.description || '';

  useEffect(() => {

    if (textReveal === 'instant' || !textToReveal) {

      setRevealedText(textToReveal);

      return;

    }

    

    setRevealedText('');

    setRevealIndex(0);

    

    const units = textReveal === 'letterByLetter' ? textToReveal.split('') : textToReveal.split(' ');

    const speed = textReveal === 'letterByLetter' ? 50 : 150;

    

    let idx = 0;

    const interval = setInterval(() => {

      idx++;

      if (textReveal === 'letterByLetter') {

        setRevealedText(textToReveal.slice(0, idx));

      } else {

        setRevealedText(units.slice(0, idx).join(' '));

      }

      if (idx >= units.length) clearInterval(interval);

    }, speed);

    

    return () => clearInterval(interval);

  }, [textToReveal, textReveal]);

  

  const animationStyles = `

    @keyframes encounterFadeIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }

    @keyframes encounterSlideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

  `;

  

  // Jumpscare style — true fullscreen, image covers entire screen

  if (style === 'jumpscare') {

    return (

      <div onClick={onClose} style={{

        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,

        background: '#000', zIndex: 2000, cursor: 'pointer',

      }}>

        <style>{animationStyles}{`

          @keyframes jumpscareIn { 0% { opacity: 0; transform: scale(1.05); } 100% { opacity: 1; transform: scale(1); } }

          @keyframes jumpscarePulse { 0%,100% { filter: brightness(1); } 50% { filter: brightness(1.05); } }

        `}</style>

        {enc.image && (

          <div style={{

            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,

            backgroundImage: `url(${enc.image})`,

            backgroundSize: 'contain',

            backgroundPosition: 'center',

            backgroundRepeat: 'no-repeat',

            opacity: imageOpacity,

            animation: 'jumpscareIn 1.5s cubic-bezier(0.25, 0.1, 0.25, 1), jumpscarePulse 6s ease-in-out 1.5s infinite',

          }} />

        )}

        <div style={{ position:'absolute', inset:0, background:'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.6) 100%)', pointerEvents:'none' }} />

        {enc.name && (

          <div style={{

            position: 'absolute', bottom: 0, left: 0, right: 0,

            background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',

            padding: '40px 30px 30px', textAlign: 'center',

            animation: 'jumpscareIn 2s cubic-bezier(0.25, 0.1, 0.25, 1)',

          }}>

            <div style={{ fontSize: `${nameSize * 1.3}rem`, fontWeight: 'bold', color: accentColor, fontFamily: 'Georgia, serif', textShadow: `0 0 30px ${accentColor}44, 0 2px 8px rgba(0,0,0,0.8)`, letterSpacing: '1px' }}>{enc.name}</div>

            {enc.subtitle && <div style={{ fontSize: '0.85rem', color: textColor, opacity: 0.5, marginTop: '6px', letterSpacing: '2px', textTransform: 'uppercase' }}>{enc.subtitle}</div>}

          </div>

        )}

        <button onClick={onClose} style={{ position:'absolute', top:'16px', right:'16px', padding:'6px 16px', background:'rgba(0,0,0,0.6)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:'4px', color:'rgba(255,255,255,0.5)', cursor:'pointer', fontSize:'0.72rem', zIndex:10 }}>✕ Close</button>

      </div>

    );

  }



  // Fullscreen style - image takes up the whole background

  if (style === 'fullscreen') {

    return (

      <div onClick={onClose} style={{

        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,

        background: '#000', zIndex: 2000, cursor: 'pointer',

        animation: 'encounterFadeIn 0.5s ease-out',

      }}>

        <style>{animationStyles}</style>

        {enc.image && (

          <div style={{

            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,

            backgroundImage: `url(${enc.image})`,

            backgroundSize: 'cover',

            backgroundPosition: 'center',

            opacity: imageOpacity,

          }} />

        )}

        <div style={{

          position: 'absolute', bottom: 0, left: 0, right: 0,

          background: `linear-gradient(transparent, ${bgColor}ee)`,

          padding: '60px 40px 40px',

          textAlign: 'center',

        }}>

          {enc.name && (

            <div style={{ fontSize: `${nameSize}rem`, fontWeight: 'bold', color: accentColor, textShadow: '0 0 20px rgba(0,0,0,0.8)', marginBottom: '8px' }}>

              {enc.name}

            </div>

          )}

          {enc.subtitle && (

            <div style={{ fontSize: '0.9rem', color: textColor, opacity: 0.7, marginBottom: '16px' }}>

              {enc.subtitle}

            </div>

          )}

          {(enc.dialogue || enc.description) && (

            <div style={{ fontSize: `${textSize}rem`, color: textColor, lineHeight: 1.6, maxWidth: '700px', margin: '0 auto' }}>

              {enc.dialogue ? `"${revealedText}"` : revealedText}

            </div>

          )}

        </div>

        <div style={{ position: 'absolute', bottom: '15px', left: '50%', transform: 'translateX(-50%)', fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)' }}>

          Click to close preview

        </div>

      </div>

    );

  }

  

  // Card style - ornate horizontal

  if (style === 'card') {

    const isQuest = encType === 'quest';

    const isLore = encType === 'lore';

    return (

      <div onClick={onClose} style={{

        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,

        background: 'rgba(0,0,0,0.88)', zIndex: 2000,

        display: 'flex', alignItems: 'center', justifyContent: 'center',

        cursor: 'pointer',

      }}>

        <style>{animationStyles}</style>

        <div onClick={e => e.stopPropagation()} style={{

          width: `${Math.max(boxSize, 520)}px`, maxWidth: '95%',

          background: isLore ? `linear-gradient(160deg, ${bgColor} 0%, rgba(40,34,24,0.98) 60%, ${accentColor}06 100%)` : `linear-gradient(160deg, ${bgColor} 0%, ${bgColor} 60%, ${accentColor}08 100%)`,

          border: `1px solid ${borderColor}30`, borderRadius: '6px', padding: '4px',

          boxShadow: `0 20px 60px rgba(0,0,0,0.7), 0 0 30px ${accentColor}22`,

          animation: 'encounterFadeIn 0.5s ease-out', cursor: 'default',

        }}>

          {isQuest && <div style={{ textAlign:'center', padding:'3px 0', fontSize:'0.58rem', fontWeight:700, letterSpacing:'3px', textTransform:'uppercase', color:accentColor, opacity:0.7 }}>✦ NEW QUEST ✦</div>}

          {isLore && <div style={{ textAlign:'center', padding:'3px 0', fontSize:'0.58rem', fontWeight:700, letterSpacing:'3px', textTransform:'uppercase', color:accentColor, opacity:0.6 }}>— LORE —</div>}

          <div style={{ border: `2px solid ${borderColor}50`, borderRadius: '4px', display: 'flex', padding: '20px', gap: '20px', alignItems: 'center', minHeight: `${Math.max(imageSize + 40, 160)}px`, position: 'relative' }}>

            {[{t:'0',l:'0'},{t:'0',r:'0',l:'auto'},{b:'0',l:'0',t:'auto'},{b:'0',r:'0',t:'auto',l:'auto'}].map((pos,i) => (

              <div key={i} style={{ position:'absolute', width:'20px', height:'20px', top:pos.t, bottom:pos.b, left:pos.l, right:pos.r, zIndex:2, pointerEvents:'none', borderTop:i<2?`2px solid ${borderColor}`:'none', borderLeft:i%2===0?`2px solid ${borderColor}`:'none', borderBottom:i>=2?`2px solid ${borderColor}`:'none', borderRight:i%2===1?`2px solid ${borderColor}`:'none' }} />

            ))}

            {enc.image && (

              <div style={{ width:`${imageSize}px`, height:`${imageSize}px`, flexShrink:0, borderRadius:encType==='item'?'6px':'8px', border:`3px solid ${borderColor}`, overflow:'hidden', boxShadow:`0 4px 20px rgba(0,0,0,0.5), 0 0 15px ${accentColor}22`, position:'relative' }}>

                <img src={enc.image} alt={enc.name} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />

                <div style={{ position:'absolute', inset:0, boxShadow:'inset 0 0 20px rgba(0,0,0,0.4)', pointerEvents:'none' }} />

              </div>

            )}

            <div style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'center', minWidth:0 }}>

              <div style={{ fontSize:`${nameSize}rem`, fontWeight:'bold', color:accentColor, marginBottom:'2px', fontFamily: isLore ? '"Palatino Linotype", Georgia, serif' : 'Georgia, serif', textShadow:`0 1px 3px rgba(0,0,0,0.5), 0 0 12px ${accentColor}22`, lineHeight:1.2 }}>{enc.name}</div>

              {(enc.title || enc.subtitle) && <div style={{ fontSize:'0.72rem', color:accentColor, opacity:0.8, fontStyle:'italic', marginBottom:'10px', textTransform:'uppercase', letterSpacing:'2px', fontWeight:600 }}>{enc.title || enc.subtitle}</div>}

              {(enc.quote || enc.dialogue || enc.description) && <div style={{ fontSize:`${textSize*0.95}rem`, color:textColor, lineHeight:1.65, fontFamily:'Georgia, serif', opacity:0.92, fontStyle: isLore ? 'italic' : 'normal' }}>{enc.quote ? enc.quote : enc.dialogue ? `"${revealedText}"` : revealedText}</div>}

            </div>

          </div>

          <div style={{ textAlign:'center', padding:'8px 0 4px' }}>

            <div style={{ fontSize:'0.65rem', color:accentColor, opacity:0.35, fontStyle:'italic' }}>Click outside to close preview</div>

          </div>

        </div>

      </div>

    );

  }

  

  // Portrait style (default)

  

  // Item portrait — compact horizontal layout

  if (encType === 'item' && style === 'portrait') {

    return (

      <div onClick={onClose} style={{

        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,

        background: 'rgba(0,0,0,0.88)', zIndex: 2000,

        display: 'flex', alignItems: 'center', justifyContent: 'center',

        cursor: 'pointer',

      }}>

        <style>{animationStyles}</style>

        <div onClick={e => e.stopPropagation()} style={{

          width: `${Math.max(boxSize, 480)}px`, maxWidth: '95%',

          background: 'linear-gradient(160deg, rgba(28,22,16,0.98) 0%, rgba(22,18,12,0.99) 100%)',

          border: `1px solid ${borderColor}25`,

          borderRadius: '10px',

          display: 'flex', flexDirection: 'row',

          overflow: 'hidden', padding: '18px',

          gap: '18px', alignItems: 'center',

          boxShadow: `0 16px 50px rgba(0,0,0,0.7), 0 0 1px ${accentColor}40`,

          animation: 'encounterFadeIn 0.5s ease-out',

          cursor: 'default',

        }}>

          {enc.image && (

            <div style={{

              width: `${imageSize}px`, height: `${imageSize}px`, flexShrink: 0,

              borderRadius: '6px', border: `2.5px solid ${borderColor}88`,

              overflow: 'hidden', position: 'relative',

              boxShadow: `0 4px 20px rgba(0,0,0,0.6), 0 0 12px ${accentColor}22`,

            }}>

              <img src={enc.image} alt={enc.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />

              <div style={{ position:'absolute', inset:0, boxShadow:'inset 0 0 15px rgba(0,0,0,0.5)', pointerEvents:'none' }} />

            </div>

          )}

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>

            <div style={{ fontSize: `${nameSize}rem`, fontWeight: 'bold', color: accentColor, marginBottom: '3px', fontFamily: 'Georgia, serif', lineHeight: 1.2 }}>{enc.name}</div>

            {(enc.title || enc.subtitle) && <div style={{ fontSize: '0.7rem', color: accentColor, opacity: 0.75, fontStyle: 'italic', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: 600 }}>{enc.title || enc.subtitle}</div>}

            {(enc.quote || enc.dialogue || enc.description) && <div style={{ fontSize: `${textSize * 0.95}rem`, color: textColor, lineHeight: 1.6, fontFamily: 'Georgia, serif', opacity: 0.9 }}>{enc.quote ? enc.quote : enc.dialogue ? `"${revealedText}"` : revealedText}</div>}

            <div style={{ fontSize: '0.6rem', color: accentColor, opacity: 0.35, marginTop: '12px', fontStyle: 'italic' }}>Click outside to close preview</div>

          </div>

        </div>

      </div>

    );

  }

  

  const isCharType = encType === 'character';

  const isLoreType = encType === 'lore';

  const isQuestType = encType === 'quest';

  const portraitRound = isCharType;

  const imageOverlap = isCharType;

  

  return (

    <div onClick={onClose} style={{

      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,

      background: 'rgba(0,0,0,0.85)', zIndex: 2000,

      display: 'flex', alignItems: 'center', justifyContent: 'center',

      cursor: 'pointer',

    }}>

      <style>{animationStyles}</style>

      <div onClick={e => e.stopPropagation()} style={{

        width: `${boxSize}px`, maxWidth: '95%',

        background: isLoreType ? `linear-gradient(180deg, ${bgColor} 0%, ${bgColor}f8 60%, rgba(60,50,35,0.15) 100%)` : `linear-gradient(180deg, ${bgColor} 0%, ${bgColor}f0 100%)`,

        border: `2px solid ${borderColor}35`,

        borderRadius: '12px', padding: '18px 20px 14px',

        paddingTop: enc.image && imageOverlap ? `${imageSize / 2 + 16}px` : '18px',

        boxShadow: `0 20px 60px rgba(0,0,0,0.8), 0 0 30px ${accentColor}15`,

        animation: 'encounterFadeIn 0.5s ease-out',

        cursor: 'default', textAlign: 'center', position: 'relative',

        marginTop: enc.image && imageOverlap ? `${imageSize / 2}px` : 0,

      }}>

        {isQuestType && <div style={{ position:'absolute', top:'-1px', left:'50%', transform:'translateX(-50%)', padding:'2px 20px', background:`linear-gradient(90deg, transparent, ${accentColor}30, ${accentColor}50, ${accentColor}30, transparent)`, borderRadius:'0 0 8px 8px', fontSize:'0.6rem', fontWeight:700, letterSpacing:'3px', textTransform:'uppercase', color:accentColor }}>NEW QUEST</div>}

        {isLoreType && <div style={{ position:'absolute', top:0, left:0, right:0, bottom:0, borderRadius:'12px', pointerEvents:'none', opacity:0.04, backgroundImage:`repeating-linear-gradient(0deg, transparent, transparent 30px, ${accentColor} 30px, ${accentColor} 31px)` }} />}

        {enc.image && (

          <div style={{

            width: `${imageSize}px`, height: `${imageSize}px`,

            borderRadius: portraitRound ? '50%' : '10px',

            border: `3px solid ${borderColor}`, overflow: 'hidden',

            margin: imageOverlap ? '0 auto' : '0 auto 12px',

            boxShadow: `0 4px 20px rgba(0,0,0,0.5), 0 0 20px ${accentColor}25`,

            position: imageOverlap ? 'absolute' : 'relative',

            top: imageOverlap ? `-${imageSize / 2}px` : 'auto',

            left: imageOverlap ? '50%' : 'auto',

            transform: imageOverlap ? 'translateX(-50%)' : 'none',

          }}>

            <img src={enc.image} alt={enc.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />

          </div>

        )}

        <div style={{ fontSize: `${nameSize}rem`, fontWeight: 'bold', color: accentColor, marginBottom: '2px', fontFamily: isLoreType ? '"Palatino Linotype", Georgia, serif' : 'Georgia, serif', textShadow: `0 0 15px ${accentColor}33`, letterSpacing: isLoreType ? '1.5px' : '0.5px' }}>{enc.name}</div>

        {enc.subtitle && <div style={{ fontSize: '0.75rem', color: textColor, opacity: 0.45, fontStyle: 'italic', marginBottom: '10px' }}>{enc.subtitle}</div>}

        {enc.description && !enc.dialogue && (

          <div style={{ fontSize: `${textSize * 0.88}rem`, color: textColor, lineHeight: 1.55, fontStyle: isLoreType ? 'italic' : 'normal', fontFamily: 'Georgia, serif', opacity: 0.85 }}>{revealedText}</div>

        )}

        {enc.dialogue && (

          <div style={{ background: 'rgba(0,0,0,0.35)', borderRadius: '10px', padding: '14px 18px', border: `1px solid ${accentColor}20`, textAlign: 'left', marginTop: '8px' }}>

            <div style={{ fontSize: `${textSize}rem`, color: textColor, fontStyle: 'italic', lineHeight: 1.6, fontFamily: 'Georgia, serif', opacity: 0.9 }}>{enc.dialogue.replace(/^"+|"+$/g, '') ? `"${enc.dialogue.replace(/^"+|"+$/g, '')}"` : ''}</div>

          </div>

        )}

        <div style={{ fontSize: '0.62rem', color: accentColor, opacity: 0.3, marginTop: '12px', fontStyle: 'italic' }}>Click outside to close preview</div>

      </div>

    </div>

  );

};



// DM Message Composer Card with Sequence Builder, Themes, and Preview

const DMMessageComposer = ({ chars, messages, onSaveMessages, onSendStatusMessage, groupTravelLog, onSaveGroupTravelLog }) => {

  const [content, setContent] = useState('');

  const [visibility, setVisibility] = useState('public');

  const [selectedChars, setSelectedChars] = useState([]);

  const [urgency, setUrgency] = useState('normal');

  const [addToHistory, setAddToHistory] = useState(false);

  const [gameDay, setGameDay] = useState('');

  const [gameTime, setGameTime] = useState('');

  const [collapsed, setCollapsed] = useState(false);

  

  // Message type: 'sequence' (dramatic text), 'character' (NPC encounter), 'scenery' (location), 'item' (object/item)

  const [messageType, setMessageType] = useState('sequence');

  

  // For character/scenery/item types

  const [encounterImage, setEncounterImage] = useState('');

  const [encounterName, setEncounterName] = useState('');

  const [encounterDescription, setEncounterDescription] = useState('');

  const [encounterDialogue, setEncounterDialogue] = useState(''); // For character type

  const [encounterSubtitle, setEncounterSubtitle] = useState(''); // e.g., "Mysterious Stranger" or "Ancient Ruins"

  

  // Encounter style customization

  const [encounterStyle, setEncounterStyle] = useState('portrait'); // 'portrait', 'card', 'fullscreen'

  const [encounterPrimaryColor, setEncounterPrimaryColor] = useState('');

  const [encounterBgColor, setEncounterBgColor] = useState('');

  const [encounterTextColor, setEncounterTextColor] = useState('');

  const [encounterImageSize, setEncounterImageSize] = useState(120); // px for portrait, % for fullscreen

  const [encounterBoxSize, setEncounterBoxSize] = useState(500); // max-width in px

  const [encounterTextSize, setEncounterTextSize] = useState(1.1); // rem

  const [encounterNameSize, setEncounterNameSize] = useState(1.5); // rem

  const [encounterTextReveal, setEncounterTextReveal] = useState('instant'); // 'instant', 'typewriter', 'letterByLetter'

  const [encounterShowAdvanced, setEncounterShowAdvanced] = useState(false);

  const [showEncounterPreview, setShowEncounterPreview] = useState(false);

  const [encounterImageOpacity, setEncounterImageOpacity] = useState(1); // For fullscreen mode

  const [encounterBorderColor, setEncounterBorderColor] = useState(''); // Empty = use default accent

  

  // Border color presets

  const BORDER_PRESETS = [

    { id: '', label: 'Default', color: null },

    { id: 'gold', label: 'Gold', color: '#c9a227' },

    { id: 'silver', label: 'Silver', color: '#a8b4c0' },

    { id: 'crimson', label: 'Crimson', color: '#c92727' },

    { id: 'emerald', label: 'Emerald', color: '#27ae60' },

    { id: 'royal', label: 'Royal', color: '#6b4fa6' },

    { id: 'frost', label: 'Frost', color: '#70a1ff' },

    { id: 'shadow', label: 'Shadow', color: '#4a4a5a' },

    { id: 'amber', label: 'Amber', color: '#e67e22' },

  ];

  

  // Sequence builder state

  const [showSequenceBuilder, setShowSequenceBuilder] = useState(false);

  const [sequence, setSequence] = useState([{ text: '', delay: 0 }]);

  const [sequenceTheme, setSequenceTheme] = useState('foreboding');

  const [showPreview, setShowPreview] = useState(false);

  

  // Advanced customization - modular transitions

  const [showAdvanced, setShowAdvanced] = useState(false);

  const [baseTransition, setBaseTransition] = useState('stack');

  const [revealModifiers, setRevealModifiers] = useState([]); // Array for multi-select

  const [textEffects, setTextEffects] = useState([]); // Now an array for multi-select

  const [selectedFont, setSelectedFont] = useState('default');

  const [customTextColor, setCustomTextColor] = useState('');

  const [customBgColor, setCustomBgColor] = useState('');

  const [customBgOpacity, setCustomBgOpacity] = useState(0.92);

  const [textOpacity, setTextOpacity] = useState(1);

  const [bgImageUrl, setBgImageUrl] = useState('');

  const [bgImageOpacity, setBgImageOpacity] = useState(0.3);

  const [boxMode, setBoxMode] = useState(false);

  const [disableVignette, setDisableVignette] = useState(false);

  const [disableParticles, setDisableParticles] = useState(false);

  const [textSize, setTextSize] = useState(1.8); // Base text size in rem

  

  // Past text customization (text that has faded to make way for new text)

  const [pastTextOpacity, setPastTextOpacity] = useState(0.4);

  const [pastTextScale, setPastTextScale] = useState(0.85);

  const [pastTextColor, setPastTextColor] = useState(''); // Empty = same as main text but dimmed

  const [pastTextBlur, setPastTextBlur] = useState(0);

  const [pastTextOffset, setPastTextOffset] = useState(0); // Vertical offset in px

  const [showPastText, setShowPastText] = useState(true); // Toggle to hide past text entirely

  

  // Timing options

  const [transitionSpeed, setTransitionSpeed] = useState(1);

  const [sentencePause, setSentencePause] = useState(1.5);

  const [letterSpeed, setLetterSpeed] = useState(50);

  const [wordSpeed, setWordSpeed] = useState(200);

  const [revealDuration, setRevealDuration] = useState(0); // 0 = auto based on text length

  

  // Fade in/out settings

  const [fadeInDuration, setFadeInDuration] = useState(1.5);

  const [fadeOutDuration, setFadeOutDuration] = useState(1.2);

  const [enableFadeIn, setEnableFadeIn] = useState(true);

  const [enableFadeOut, setEnableFadeOut] = useState(true);

  

  const charList = Object.values(chars);
  const canWriteTravelLog = typeof onSaveGroupTravelLog === 'function';

  const persistStatusMessage = (message) => {
    if (typeof onSendStatusMessage === 'function') {
      onSendStatusMessage({ message });
      return;
    }
    if (typeof onSaveMessages !== 'function') return;
    onSaveMessages({
      ...(messages || {}),
      statusUpdates: [...((messages || {}).statusUpdates || []), message],
    });
  };

  

  // Auto-set style and clear fields when switching message types

  useEffect(() => {

    if (messageType === 'character') { setEncounterStyle('portrait'); }

    else if (messageType === 'scenery') { setEncounterStyle('fullscreen'); }

    else if (messageType === 'item') { setEncounterStyle('portrait'); }

    else if (messageType === 'quest') { setEncounterStyle('card'); }

    else if (messageType === 'lore') { setEncounterStyle('portrait'); }

    // Clear fields on type switch

    setEncounterName(''); setEncounterSubtitle(''); setEncounterDescription('');

    setEncounterDialogue(''); setEncounterImage('');

    setEncounterPrimaryColor(''); setEncounterBgColor(''); setEncounterTextColor('');

    setEncounterShowAdvanced(false); setEncounterBorderColor('');

  }, [messageType]);

  

  // Toggle reveal modifier (multi-select)

  const toggleRevealModifier = (modifier) => {

    setRevealModifiers(prev => 

      prev.includes(modifier) ? prev.filter(m => m !== modifier) : [...prev, modifier]

    );

  };

  

  // Toggle text effect (multi-select)

  const toggleTextEffect = (effect) => {

    if (effect === 'none') {

      setTextEffects([]);

    } else {

      setTextEffects(prev => 

        prev.includes(effect) ? prev.filter(e => e !== effect) : [...prev, effect]

      );

    }

  };

  

  // Load example text when theme changes

  const loadExampleText = () => {

    const examples = THEME_EXAMPLES[sequenceTheme];

    if (examples) {

      setSequence(examples.map(e => ({ text: e.text, delay: e.delay })));

    }

  };

  

  const toggleChar = (cid) => {

    setSelectedChars(prev => 

      prev.includes(cid) ? prev.filter(c => c !== cid) : [...prev, cid]

    );

  };

  

  const addSequenceItem = () => {

    setSequence(prev => [...prev, { text: '', delay: 0 }]);

  };

  

  const removeSequenceItem = (idx) => {

    if (sequence.length > 1) {

      setSequence(prev => prev.filter((_, i) => i !== idx));

    }

  };

  

  const updateSequenceItem = (idx, field, value) => {

    setSequence(prev => prev.map((item, i) => 

      i === idx ? { ...item, [field]: value } : item

    ));

  };

  

  const handleSend = () => {

    if (!content.trim()) return;

    if (visibility === 'selected' && selectedChars.length === 0) return;

    

    const newMsg = {

      id: generateId(),

      type: 'status',

      category: 'dm-status',

      timestamp: new Date().toISOString(),

      content: content.trim(),

      urgency,

      fromDM: true,

      visibility,

      recipients: visibility === 'public' ? charList.map(c => c.id) : selectedChars,

      addToHistory: canWriteTravelLog && addToHistory,

      gameTime: gameDay ? { day: parseInt(gameDay), time: gameTime || null } : null,

      dismissed: false,

      theme: urgency === 'urgent' ? sequenceTheme : undefined,

    };

    

    persistStatusMessage(newMsg);

    

    // Add to group travel log if public and addToHistory

    if (canWriteTravelLog && addToHistory && visibility === 'public') {

      onSaveGroupTravelLog([

        ...groupTravelLog || [],

        { day: gameDay ? parseInt(gameDay) : '?', character: 'DM', action: 'message', content: content.trim(), timestamp: new Date().toISOString() },

      ].slice(-200));

    }

    

    setContent('');

    setSelectedChars([]);

    setGameDay('');

    setGameTime('');

  };

  

  const handleSendSequence = () => {

    const validSequence = sequence.filter(s => s.text.trim());

    if (validSequence.length === 0) return;

    if (visibility === 'selected' && selectedChars.length === 0) return;

    

    // Build custom style object with all customizations

    const customStyle = {};

    if (customTextColor) customStyle.textColor = customTextColor;

    if (customBgColor) {

      customStyle.bgColor = customBgColor;

      customStyle.bgOpacity = customBgOpacity;

    }

    if (bgImageUrl) {

      customStyle.bgImage = bgImageUrl;

      customStyle.bgImageOpacity = bgImageOpacity;

    }

    if (textOpacity !== 1) customStyle.textOpacity = textOpacity;

    if (boxMode) customStyle.boxMode = true;

    if (disableVignette) customStyle.disableVignette = true;

    if (disableParticles) customStyle.disableParticles = true;

    if (selectedFont !== 'default') customStyle.font = selectedFont;

    if (textSize !== 1.8) customStyle.fontSize = `${textSize}rem`;

    

    // Past text customization

    customStyle.pastText = {

      show: showPastText,

      opacity: pastTextOpacity,

      scale: pastTextScale,

      color: pastTextColor || null,

      blur: pastTextBlur,

      offset: pastTextOffset,

    };

    

    // Timing options

    if (transitionSpeed !== 1) customStyle.transitionSpeed = transitionSpeed;

    if (sentencePause !== 1.5) customStyle.sentencePause = sentencePause;

    if (letterSpeed !== 50) customStyle.letterSpeed = letterSpeed;

    if (wordSpeed !== 200) customStyle.wordSpeed = wordSpeed;

    if (revealDuration > 0) customStyle.revealDuration = revealDuration;

    

    // Fade settings

    customStyle.fadeInDuration = fadeInDuration;

    customStyle.fadeOutDuration = fadeOutDuration;

    customStyle.enableFadeIn = enableFadeIn;

    customStyle.enableFadeOut = enableFadeOut;

    

    const newMsg = {

      id: generateId(),

      type: 'status',

      category: 'dm-status',

      timestamp: new Date().toISOString(),

      content: validSequence.map(s => s.text).join(' '),

      urgency: 'urgent',

      fromDM: true,

      visibility,

      recipients: visibility === 'public' ? charList.map(c => c.id) : selectedChars,

      addToHistory: canWriteTravelLog && addToHistory,

      gameTime: gameDay ? { day: parseInt(gameDay), time: gameTime || null } : null,

      dismissed: false,

      sequence: validSequence,

      theme: sequenceTheme,

      baseTransition: baseTransition,

      revealModifiers: revealModifiers.length > 0 ? revealModifiers : undefined,

      textEffects: textEffects.length > 0 ? textEffects : undefined,

      customStyle: Object.keys(customStyle).length > 0 ? customStyle : undefined,

    };

    

    persistStatusMessage(newMsg);

    

    // Reset

    setSequence([{ text: '', delay: 0 }]);

    setShowSequenceBuilder(false);

    setSelectedChars([]);

    setGameDay('');

    setGameTime('');

    setShowAdvanced(false);

    setCustomTextColor('');

    setCustomBgColor('');

    setBgImageUrl('');

    setTextEffects([]);

    setBoxMode(false);

    setRevealModifiers([]);

  };

  

  const getPreviewData = () => {

    const validSequence = sequence.filter(s => s.text.trim());

    if (validSequence.length === 0) return null;

    

    const customStyle = {};

    if (customTextColor) customStyle.textColor = customTextColor;

    if (customBgColor) {

      customStyle.bgColor = customBgColor;

      customStyle.bgOpacity = customBgOpacity;

    }

    if (bgImageUrl) {

      customStyle.bgImage = bgImageUrl;

      customStyle.bgImageOpacity = bgImageOpacity;

    }

    if (textOpacity !== 1) customStyle.textOpacity = textOpacity;

    if (boxMode) customStyle.boxMode = true;

    if (disableVignette) customStyle.disableVignette = true;

    if (disableParticles) customStyle.disableParticles = true;

    if (selectedFont !== 'default') customStyle.font = selectedFont;

    if (textSize !== 1.8) customStyle.fontSize = `${textSize}rem`;

    if (transitionSpeed !== 1) customStyle.transitionSpeed = transitionSpeed;

    if (sentencePause !== 1.5) customStyle.sentencePause = sentencePause;

    if (letterSpeed !== 50) customStyle.letterSpeed = letterSpeed;

    if (wordSpeed !== 200) customStyle.wordSpeed = wordSpeed;

    if (revealDuration > 0) customStyle.revealDuration = revealDuration;

    customStyle.fadeInDuration = fadeInDuration;

    customStyle.fadeOutDuration = fadeOutDuration;

    customStyle.enableFadeIn = enableFadeIn;

    customStyle.enableFadeOut = enableFadeOut;

    

    // Past text customization

    customStyle.pastText = {

      show: showPastText,

      opacity: pastTextOpacity,

      scale: pastTextScale,

      color: pastTextColor || null,

      blur: pastTextBlur,

      offset: pastTextOffset,

    };

    

    return {

      id: 'preview',

      sequence: validSequence,

      theme: sequenceTheme,

      baseTransition: baseTransition,

      revealModifiers: revealModifiers.length > 0 ? revealModifiers : undefined,

      textEffects: textEffects.length > 0 ? textEffects : undefined,

      gameTime: gameDay ? { day: parseInt(gameDay), time: gameTime || null } : null,

      customStyle: Object.keys(customStyle).length > 0 ? customStyle : undefined,

    };

  };

  

  const themeInfo = SEQUENCE_THEMES[sequenceTheme];

  

  return (

    <HoverCard>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: collapsed ? 0 : '12px' }}>

        <h2 style={{ ...S.subtitle, margin: 0 }}>📬 Send Message</h2>

        <button style={{ ...S.btn, ...S.btnXs }} onClick={() => setCollapsed(!collapsed)}>{collapsed ? '▼' : '▲'}</button>

      </div>

      

      {!collapsed && <>

        {/* Recipients */}

        <div style={{ marginBottom: '12px' }}>

          <label style={{ ...S.label, fontSize: '0.85rem' }}>To:</label>

          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>

            <button 

              style={{ ...S.btn, ...S.btnXs, ...(visibility === 'public' ? { background: 'rgba(39,201,90,0.3)' } : {}) }}

              onClick={() => setVisibility('public')}

            >All (Public)</button>

            <button 

              style={{ ...S.btn, ...S.btnXs, ...(visibility === 'selected' ? { background: 'rgba(74,144,217,0.3)' } : {}) }}

              onClick={() => setVisibility('selected')}

            >Select Players</button>

          </div>

          {visibility === 'selected' && (

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>

              {charList.map(c => (

                <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.85rem' }}>

                  <input type="checkbox" checked={selectedChars.includes(c.id)} onChange={() => toggleChar(c.id)} />

                  {c.name}

                </label>

              ))}

            </div>

          )}

        </div>

        

        {/* Message Type Selector */}

        <div style={{ marginBottom: '12px' }}>

          <label style={{ ...S.label, fontSize: '0.85rem' }}>Message Type:</label>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>

            <button 

              style={{ ...S.btn, ...S.btnXs, ...(messageType === 'simple' ? { background: 'rgba(201,162,39,0.3)' } : {}) }}

              onClick={() => { setMessageType('simple'); setShowSequenceBuilder(false); }}

            >📝 Simple</button>

            <button 

              style={{ ...S.btn, ...S.btnXs, ...(messageType === 'sequence' ? { background: 'rgba(201,39,39,0.3)' } : {}) }}

              onClick={() => { setMessageType('sequence'); setShowSequenceBuilder(true); }}

            >🎭 Dramatic</button>

            <button 

              style={{ ...S.btn, ...S.btnXs, ...(messageType === 'character' ? { background: 'rgba(155,89,182,0.3)' } : {}) }}

              onClick={() => setMessageType('character')}

            >👤 Character</button>

            <button 

              style={{ ...S.btn, ...S.btnXs, ...(messageType === 'scenery' ? { background: 'rgba(39,174,96,0.3)' } : {}) }}

              onClick={() => setMessageType('scenery')}

            >🏰 Scenery</button>

            <button 

              style={{ ...S.btn, ...S.btnXs, ...(messageType === 'item' ? { background: 'rgba(230,126,34,0.3)' } : {}) }}

              onClick={() => setMessageType('item')}

            >💎 Item</button>

            <button 

              style={{ ...S.btn, ...S.btnXs, ...(messageType === 'quest' ? { background: 'rgba(230,194,41,0.3)' } : {}) }}

              onClick={() => setMessageType('quest')}

            >📜 Quest</button>

            <button 

              style={{ ...S.btn, ...S.btnXs, ...(messageType === 'lore' ? { background: 'rgba(139,115,85,0.3)' } : {}) }}

              onClick={() => setMessageType('lore')}

            >📖 Lore</button>

          </div>

        </div>

        

        {/* Character Encounter */}

        {messageType === 'character' && (

          <div style={{ padding: '12px', background: 'rgba(155,89,182,0.1)', borderRadius: '8px', border: '1px solid rgba(155,89,182,0.3)', marginBottom: '12px' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>

              <div style={{ fontSize: '0.75rem', color: '#9b59b6', fontWeight: 'bold' }}>👤 Character Encounter</div>

              <button 

                style={{ ...S.btn, ...S.btnXs, fontSize: '0.65rem', padding: '3px 8px' }}

                onClick={() => setShowEncounterPreview(true)}

              >👁 Preview</button>

            </div>

            

            {/* Style selector */}

            <div style={{ marginBottom: '10px' }}>

              <label style={{ ...S.label, fontSize: '0.7rem' }}>Style</label>

              <div style={{ display: 'flex', gap: '6px' }}>

                <button 

                  style={{ ...S.btn, ...S.btnXs, flex: 1, ...(encounterStyle === 'portrait' ? { background: 'rgba(155,89,182,0.4)', border: '1px solid #9b59b6' } : {}) }}

                  onClick={() => setEncounterStyle('portrait')}

                >🖼️ Portrait</button>

                <button 

                  style={{ ...S.btn, ...S.btnXs, flex: 1, ...(encounterStyle === 'card' ? { background: 'rgba(155,89,182,0.4)', border: '1px solid #9b59b6' } : {}) }}

                  onClick={() => setEncounterStyle('card')}

                >📇 Card</button>

                <button 

                  style={{ ...S.btn, ...S.btnXs, flex: 1, ...(encounterStyle === 'fullscreen' ? { background: 'rgba(155,89,182,0.4)', border: '1px solid #9b59b6' } : {}) }}

                  onClick={() => setEncounterStyle('fullscreen')}

                >📺 Large</button>

                <button 

                  style={{ ...S.btn, ...S.btnXs, flex: 1, ...(encounterStyle === 'jumpscare' ? { background: 'rgba(155,89,182,0.4)', border: '1px solid #9b59b6' } : {}) }}

                  onClick={() => setEncounterStyle('jumpscare')}

                >💀 Fullscreen</button>

              </div>

            </div>

            

            {/* Border Color */}

            {(encounterStyle === 'portrait' || encounterStyle === 'card') && (

              <div style={{ marginBottom: '10px' }}>

                <label style={{ ...S.label, fontSize: '0.7rem' }}>Border Color</label>

                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>

                  {BORDER_PRESETS.map(p => (

                    <button key={p.id} onClick={() => setEncounterBorderColor(p.color || '')} style={{

                      width: '28px', height: '28px', borderRadius: '6px', cursor: 'pointer',

                      background: p.color ? `${p.color}30` : 'rgba(255,255,255,0.05)',

                      border: (encounterBorderColor || '') === (p.color || '') ? `2px solid ${p.color || '#888'}` : '2px solid transparent',

                      display: 'flex', alignItems: 'center', justifyContent: 'center',

                      boxShadow: (encounterBorderColor || '') === (p.color || '') ? `0 0 8px ${p.color || '#888'}44` : 'none',

                    }} title={p.label}>

                      <div style={{ width: '14px', height: '14px', borderRadius: '3px', background: p.color || 'linear-gradient(135deg, #9b59b6 50%, #e67e22 50%)', border: p.color ? `2px solid ${p.color}` : '2px solid #666' }} />

                    </button>

                  ))}

                </div>

              </div>

            )}

            

            {/* Image upload/URL */}

            <div style={{ marginBottom: '10px' }}>

              <label style={{ ...S.label, fontSize: '0.7rem' }}>Character Image</label>

              <div style={{ display: 'flex', gap: '6px' }}>

                <input 

                  style={{ ...S.input, flex: 1, marginBottom: 0, padding: '6px', fontSize: '0.8rem' }} 

                  placeholder="https://... or upload" 

                  value={encounterImage} 

                  onChange={e => setEncounterImage(e.target.value)} 

                />

                <label style={{ ...S.btn, ...S.btnXs, padding: '6px 10px', cursor: 'pointer' }}>

                  🖼️

                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => {

                    const file = e.target.files?.[0];

                    if (file) {

                      const reader = new FileReader();

                      reader.onload = (ev) => setEncounterImage(ev.target.result);

                      reader.readAsDataURL(file);

                    }

                  }} />

                </label>

              </div>

              {encounterImage && <img src={encounterImage} alt="Preview" style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: encounterStyle === 'portrait' ? '50%' : '8px', marginTop: '8px', border: `2px solid ${encounterPrimaryColor || '#9b59b6'}` }} />}

            </div>

            

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>

              <div>

                <label style={{ ...S.label, fontSize: '0.7rem' }}>Name</label>

                <input style={{ ...S.input, marginBottom: 0, padding: '6px', fontSize: '0.85rem' }} placeholder="Mysterious Stranger" value={encounterName} onChange={e => setEncounterName(e.target.value)} />

              </div>

              <div>

                <label style={{ ...S.label, fontSize: '0.7rem' }}>Title/Role (optional)</label>

                <input style={{ ...S.input, marginBottom: 0, padding: '6px', fontSize: '0.85rem' }} placeholder="Village Elder" value={encounterSubtitle} onChange={e => setEncounterSubtitle(e.target.value)} />

              </div>

            </div>

            

            <div style={{ marginBottom: '10px' }}>

              <label style={{ ...S.label, fontSize: '0.7rem' }}>What they say</label>

              <textarea style={{ ...S.input, marginBottom: 0, minHeight: '50px', resize: 'vertical', fontStyle: 'italic' }} placeholder='"Greetings, traveler..."' value={encounterDialogue} onChange={e => setEncounterDialogue(e.target.value)} />

            </div>

            

            <div style={{ marginBottom: '10px' }}>

              <label style={{ ...S.label, fontSize: '0.7rem' }}>Description (optional)</label>

              <textarea style={{ ...S.input, marginBottom: 0, minHeight: '40px', resize: 'vertical', fontSize: '0.8rem' }} placeholder="A cloaked figure emerges from the shadows..." value={encounterDescription} onChange={e => setEncounterDescription(e.target.value)} />

            </div>

            

            {/* Advanced Settings Toggle */}

            <button 

              style={{ ...S.btn, ...S.btnXs, width: '100%', marginBottom: encounterShowAdvanced ? '10px' : 0, fontSize: '0.7rem' }}

              onClick={() => setEncounterShowAdvanced(!encounterShowAdvanced)}

            >

              {encounterShowAdvanced ? '▲ Hide' : '▼ Show'} Customization

            </button>

            

            {encounterShowAdvanced && (

              <div style={{ background: 'rgba(0,0,0,0.2)', padding: '10px', borderRadius: '6px', marginTop: '8px' }}>

                {/* Text Reveal */}

                <div style={{ marginBottom: '8px' }}>

                  <label style={{ ...S.label, fontSize: '0.65rem' }}>Text Reveal</label>

                  <div style={{ display: 'flex', gap: '4px' }}>

                    {['instant', 'typewriter', 'letterByLetter'].map(r => (

                      <button key={r} style={{ ...S.btn, ...S.btnXs, flex: 1, fontSize: '0.6rem', ...(encounterTextReveal === r ? { background: 'rgba(155,89,182,0.4)' } : {}) }}

                        onClick={() => setEncounterTextReveal(r)}>

                        {r === 'instant' ? 'Instant' : r === 'typewriter' ? 'Typewriter' : 'Letter'}

                      </button>

                    ))}

                  </div>

                </div>

                

                {/* Colors */}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px', marginBottom: '8px' }}>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.6rem' }}>Accent</label>

                    <input type="color" value={encounterPrimaryColor || '#9b59b6'} onChange={e => setEncounterPrimaryColor(e.target.value)} style={{ width: '100%', height: '24px', cursor: 'pointer' }} />

                  </div>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.6rem' }}>Background</label>

                    <input type="color" value={encounterBgColor || '#1a1525'} onChange={e => setEncounterBgColor(e.target.value)} style={{ width: '100%', height: '24px', cursor: 'pointer' }} />

                  </div>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.6rem' }}>Text</label>

                    <input type="color" value={encounterTextColor || '#e8d5b7'} onChange={e => setEncounterTextColor(e.target.value)} style={{ width: '100%', height: '24px', cursor: 'pointer' }} />

                  </div>

                </div>

                

                {/* Sizes */}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '8px' }}>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.6rem' }}>Name Size: {encounterNameSize}rem</label>

                    <input type="range" min="1" max="3" step="0.1" value={encounterNameSize} onChange={e => setEncounterNameSize(parseFloat(e.target.value))} style={{ width: '100%' }} />

                  </div>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.6rem' }}>Text Size: {encounterTextSize}rem</label>

                    <input type="range" min="0.8" max="2" step="0.1" value={encounterTextSize} onChange={e => setEncounterTextSize(parseFloat(e.target.value))} style={{ width: '100%' }} />

                  </div>

                </div>

                

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.6rem' }}>Image Size: {encounterImageSize}px</label>

                    <input type="range" min="80" max="300" step="10" value={encounterImageSize} onChange={e => setEncounterImageSize(parseInt(e.target.value))} style={{ width: '100%' }} />

                  </div>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.6rem' }}>Box Width: {encounterBoxSize}px</label>

                    <input type="range" min="300" max="800" step="25" value={encounterBoxSize} onChange={e => setEncounterBoxSize(parseInt(e.target.value))} style={{ width: '100%' }} />

                  </div>

                </div>

                

                {encounterStyle === 'fullscreen' && (

                  <div style={{ marginTop: '8px' }}>

                    <label style={{ ...S.label, fontSize: '0.6rem' }}>Image Opacity: {Math.round(encounterImageOpacity * 100)}%</label>

                    <input type="range" min="0.3" max="1" step="0.05" value={encounterImageOpacity} onChange={e => setEncounterImageOpacity(parseFloat(e.target.value))} style={{ width: '100%' }} />

                  </div>

                )}

              </div>

            )}

          </div>

        )}

        

        {/* Scenery/Location */}

        {messageType === 'scenery' && (

          <div style={{ padding: '12px', background: 'rgba(39,174,96,0.1)', borderRadius: '8px', border: '1px solid rgba(39,174,96,0.3)', marginBottom: '12px' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>

              <div style={{ fontSize: '0.75rem', color: '#27ae60', fontWeight: 'bold' }}>🏰 Scenery / Location</div>

              <button 

                style={{ ...S.btn, ...S.btnXs, fontSize: '0.65rem', padding: '3px 8px' }}

                onClick={() => setShowEncounterPreview(true)}

              >👁 Preview</button>

            </div>

            

            {/* Style selector */}

            <div style={{ marginBottom: '10px' }}>

              <label style={{ ...S.label, fontSize: '0.7rem' }}>Style</label>

              <div style={{ display: 'flex', gap: '6px' }}>

                <button 

                  style={{ ...S.btn, ...S.btnXs, flex: 1, ...(encounterStyle === 'portrait' ? { background: 'rgba(39,174,96,0.4)', border: '1px solid #27ae60' } : {}) }}

                  onClick={() => setEncounterStyle('portrait')}

                >🖼️ Banner</button>

                <button 

                  style={{ ...S.btn, ...S.btnXs, flex: 1, ...(encounterStyle === 'card' ? { background: 'rgba(39,174,96,0.4)', border: '1px solid #27ae60' } : {}) }}

                  onClick={() => setEncounterStyle('card')}

                >📇 Card</button>

                <button 

                  style={{ ...S.btn, ...S.btnXs, flex: 1, ...(encounterStyle === 'fullscreen' ? { background: 'rgba(39,174,96,0.4)', border: '1px solid #27ae60' } : {}) }}

                  onClick={() => setEncounterStyle('fullscreen')}

                >📺 Large</button>

                <button 

                  style={{ ...S.btn, ...S.btnXs, flex: 1, ...(encounterStyle === 'jumpscare' ? { background: 'rgba(39,174,96,0.4)', border: '1px solid #27ae60' } : {}) }}

                  onClick={() => setEncounterStyle('jumpscare')}

                >💀 Fullscreen</button>

              </div>

            </div>

            

            {/* Image upload/URL */}

            <div style={{ marginBottom: '10px' }}>

              <label style={{ ...S.label, fontSize: '0.7rem' }}>Location Image</label>

              <div style={{ display: 'flex', gap: '6px' }}>

                <input 

                  style={{ ...S.input, flex: 1, marginBottom: 0, padding: '6px', fontSize: '0.8rem' }} 

                  placeholder="https://... or upload" 

                  value={encounterImage} 

                  onChange={e => setEncounterImage(e.target.value)} 

                />

                <label style={{ ...S.btn, ...S.btnXs, padding: '6px 10px', cursor: 'pointer' }}>

                  🖼️

                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => {

                    const file = e.target.files?.[0];

                    if (file) {

                      const reader = new FileReader();

                      reader.onload = (ev) => setEncounterImage(ev.target.result);

                      reader.readAsDataURL(file);

                    }

                  }} />

                </label>

              </div>

              {encounterImage && <img src={encounterImage} alt="Preview" style={{ width: '100%', maxHeight: '100px', objectFit: 'cover', borderRadius: '6px', marginTop: '8px', border: `2px solid ${encounterPrimaryColor || '#27ae60'}` }} />}

            </div>

            

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>

              <div>

                <label style={{ ...S.label, fontSize: '0.7rem' }}>Location Name</label>

                <input style={{ ...S.input, marginBottom: 0, padding: '6px', fontSize: '0.85rem' }} placeholder="The Whispering Woods" value={encounterName} onChange={e => setEncounterName(e.target.value)} />

              </div>

              <div>

                <label style={{ ...S.label, fontSize: '0.7rem' }}>Subtitle (optional)</label>

                <input style={{ ...S.input, marginBottom: 0, padding: '6px', fontSize: '0.85rem' }} placeholder="Northern Forest Region" value={encounterSubtitle} onChange={e => setEncounterSubtitle(e.target.value)} />

              </div>

            </div>

            

            <div style={{ marginBottom: '10px' }}>

              <label style={{ ...S.label, fontSize: '0.7rem' }}>Description</label>

              <textarea style={{ ...S.input, marginBottom: 0, minHeight: '60px', resize: 'vertical' }} placeholder="Before you stretches an ancient forest..." value={encounterDescription} onChange={e => setEncounterDescription(e.target.value)} />

            </div>

            

            {/* Advanced Settings Toggle */}

            <button 

              style={{ ...S.btn, ...S.btnXs, width: '100%', marginBottom: encounterShowAdvanced ? '10px' : 0, fontSize: '0.7rem' }}

              onClick={() => setEncounterShowAdvanced(!encounterShowAdvanced)}

            >

              {encounterShowAdvanced ? '▲ Hide' : '▼ Show'} Customization

            </button>

            

            {encounterShowAdvanced && (

              <div style={{ background: 'rgba(0,0,0,0.2)', padding: '10px', borderRadius: '6px', marginTop: '8px' }}>

                {/* Text Reveal */}

                <div style={{ marginBottom: '8px' }}>

                  <label style={{ ...S.label, fontSize: '0.65rem' }}>Text Reveal</label>

                  <div style={{ display: 'flex', gap: '4px' }}>

                    {['instant', 'typewriter', 'letterByLetter'].map(r => (

                      <button key={r} style={{ ...S.btn, ...S.btnXs, flex: 1, fontSize: '0.6rem', ...(encounterTextReveal === r ? { background: 'rgba(39,174,96,0.4)' } : {}) }}

                        onClick={() => setEncounterTextReveal(r)}>

                        {r === 'instant' ? 'Instant' : r === 'typewriter' ? 'Typewriter' : 'Letter'}

                      </button>

                    ))}

                  </div>

                </div>

                

                {/* Colors */}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px', marginBottom: '8px' }}>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.6rem' }}>Accent</label>

                    <input type="color" value={encounterPrimaryColor || '#27ae60'} onChange={e => setEncounterPrimaryColor(e.target.value)} style={{ width: '100%', height: '24px', cursor: 'pointer' }} />

                  </div>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.6rem' }}>Background</label>

                    <input type="color" value={encounterBgColor || '#141914'} onChange={e => setEncounterBgColor(e.target.value)} style={{ width: '100%', height: '24px', cursor: 'pointer' }} />

                  </div>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.6rem' }}>Text</label>

                    <input type="color" value={encounterTextColor || '#e8d5b7'} onChange={e => setEncounterTextColor(e.target.value)} style={{ width: '100%', height: '24px', cursor: 'pointer' }} />

                  </div>

                </div>

                

                {/* Sizes */}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.6rem' }}>Name Size: {encounterNameSize}rem</label>

                    <input type="range" min="1" max="3" step="0.1" value={encounterNameSize} onChange={e => setEncounterNameSize(parseFloat(e.target.value))} style={{ width: '100%' }} />

                  </div>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.6rem' }}>Text Size: {encounterTextSize}rem</label>

                    <input type="range" min="0.8" max="2" step="0.1" value={encounterTextSize} onChange={e => setEncounterTextSize(parseFloat(e.target.value))} style={{ width: '100%' }} />

                  </div>

                </div>

                

                {encounterStyle === 'fullscreen' && (

                  <div style={{ marginTop: '8px' }}>

                    <label style={{ ...S.label, fontSize: '0.6rem' }}>Image Opacity: {Math.round(encounterImageOpacity * 100)}%</label>

                    <input type="range" min="0.3" max="1" step="0.05" value={encounterImageOpacity} onChange={e => setEncounterImageOpacity(parseFloat(e.target.value))} style={{ width: '100%' }} />

                  </div>

                )}

              </div>

            )}

          </div>

        )}

        

        {/* Item/Object */}

        {messageType === 'item' && (

          <div style={{ padding: '12px', background: 'rgba(230,126,34,0.1)', borderRadius: '8px', border: '1px solid rgba(230,126,34,0.3)', marginBottom: '12px' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>

              <div style={{ fontSize: '0.75rem', color: '#e67e22', fontWeight: 'bold' }}>💎 Item / Object Discovery</div>

              <button 

                style={{ ...S.btn, ...S.btnXs, fontSize: '0.65rem', padding: '3px 8px' }}

                onClick={() => setShowEncounterPreview(true)}

              >👁 Preview</button>

            </div>

            

            {/* Style selector */}

            <div style={{ marginBottom: '10px' }}>

              <label style={{ ...S.label, fontSize: '0.7rem' }}>Style</label>

              <div style={{ display: 'flex', gap: '6px' }}>

                <button 

                  style={{ ...S.btn, ...S.btnXs, flex: 1, ...(encounterStyle === 'portrait' ? { background: 'rgba(230,126,34,0.4)', border: '1px solid #e67e22' } : {}) }}

                  onClick={() => setEncounterStyle('portrait')}

                >🖼️ Standard</button>

                <button 

                  style={{ ...S.btn, ...S.btnXs, flex: 1, ...(encounterStyle === 'card' ? { background: 'rgba(230,126,34,0.4)', border: '1px solid #e67e22' } : {}) }}

                  onClick={() => setEncounterStyle('card')}

                >📇 Card</button>

                <button 

                  style={{ ...S.btn, ...S.btnXs, flex: 1, ...(encounterStyle === 'fullscreen' ? { background: 'rgba(230,126,34,0.4)', border: '1px solid #e67e22' } : {}) }}

                  onClick={() => setEncounterStyle('fullscreen')}

                >📺 Large</button>

                <button 

                  style={{ ...S.btn, ...S.btnXs, flex: 1, ...(encounterStyle === 'jumpscare' ? { background: 'rgba(230,126,34,0.4)', border: '1px solid #e67e22' } : {}) }}

                  onClick={() => setEncounterStyle('jumpscare')}

                >💀 Fullscreen</button>

              </div>

            </div>

            

            {/* Border Color */}

            {(encounterStyle === 'portrait' || encounterStyle === 'card') && (

              <div style={{ marginBottom: '10px' }}>

                <label style={{ ...S.label, fontSize: '0.7rem' }}>Border Color</label>

                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>

                  {BORDER_PRESETS.map(p => (

                    <button key={p.id} onClick={() => setEncounterBorderColor(p.color || '')} style={{

                      width: '28px', height: '28px', borderRadius: '6px', cursor: 'pointer',

                      background: p.color ? `${p.color}30` : 'rgba(255,255,255,0.05)',

                      border: (encounterBorderColor || '') === (p.color || '') ? `2px solid ${p.color || '#888'}` : '2px solid transparent',

                      display: 'flex', alignItems: 'center', justifyContent: 'center',

                      boxShadow: (encounterBorderColor || '') === (p.color || '') ? `0 0 8px ${p.color || '#888'}44` : 'none',

                    }} title={p.label}>

                      <div style={{ width: '14px', height: '14px', borderRadius: '3px', background: p.color || 'linear-gradient(135deg, #e67e22 50%, #c9a227 50%)', border: p.color ? `2px solid ${p.color}` : '2px solid #666' }} />

                    </button>

                  ))}

                </div>

              </div>

            )}

            

            {/* Image upload/URL */}

            <div style={{ marginBottom: '10px' }}>

              <label style={{ ...S.label, fontSize: '0.7rem' }}>Item Image</label>

              <div style={{ display: 'flex', gap: '6px' }}>

                <input 

                  style={{ ...S.input, flex: 1, marginBottom: 0, padding: '6px', fontSize: '0.8rem' }} 

                  placeholder="https://... or upload" 

                  value={encounterImage} 

                  onChange={e => setEncounterImage(e.target.value)} 

                />

                <label style={{ ...S.btn, ...S.btnXs, padding: '6px 10px', cursor: 'pointer' }}>

                  🖼️

                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => {

                    const file = e.target.files?.[0];

                    if (file) {

                      const reader = new FileReader();

                      reader.onload = (ev) => setEncounterImage(ev.target.result);

                      reader.readAsDataURL(file);

                    }

                  }} />

                </label>

              </div>

              {encounterImage && (

                <div style={{ textAlign: 'center', marginTop: '8px' }}>

                  <img src={encounterImage} alt="Preview" style={{ maxWidth: '80px', maxHeight: '80px', objectFit: 'contain', borderRadius: '6px', border: `2px solid ${encounterPrimaryColor || '#e67e22'}`, background: 'rgba(0,0,0,0.3)', padding: '6px' }} />

                </div>

              )}

            </div>

            

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>

              <div>

                <label style={{ ...S.label, fontSize: '0.7rem' }}>Item Name</label>

                <input style={{ ...S.input, marginBottom: 0, padding: '6px', fontSize: '0.85rem' }} placeholder="Amulet of the Lost King" value={encounterName} onChange={e => setEncounterName(e.target.value)} />

              </div>

              <div>

                <label style={{ ...S.label, fontSize: '0.7rem' }}>Type/Rarity (optional)</label>

                <input style={{ ...S.input, marginBottom: 0, padding: '6px', fontSize: '0.85rem' }} placeholder="Legendary Artifact" value={encounterSubtitle} onChange={e => setEncounterSubtitle(e.target.value)} />

              </div>

            </div>

            

            <div style={{ marginBottom: '10px' }}>

              <label style={{ ...S.label, fontSize: '0.7rem' }}>Description</label>

              <textarea style={{ ...S.input, marginBottom: 0, minHeight: '60px', resize: 'vertical' }} placeholder="A golden amulet inlaid with a deep crimson ruby..." value={encounterDescription} onChange={e => setEncounterDescription(e.target.value)} />

            </div>

            

            {/* Advanced Settings Toggle */}

            <button 

              style={{ ...S.btn, ...S.btnXs, width: '100%', marginBottom: encounterShowAdvanced ? '10px' : 0, fontSize: '0.7rem' }}

              onClick={() => setEncounterShowAdvanced(!encounterShowAdvanced)}

            >

              {encounterShowAdvanced ? '▲ Hide' : '▼ Show'} Customization

            </button>

            

            {encounterShowAdvanced && (

              <div style={{ background: 'rgba(0,0,0,0.2)', padding: '10px', borderRadius: '6px', marginTop: '8px' }}>

                {/* Colors */}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px', marginBottom: '8px' }}>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.6rem' }}>Accent</label>

                    <input type="color" value={encounterPrimaryColor || '#e67e22'} onChange={e => setEncounterPrimaryColor(e.target.value)} style={{ width: '100%', height: '24px', cursor: 'pointer' }} />

                  </div>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.6rem' }}>Background</label>

                    <input type="color" value={encounterBgColor || '#1e140a'} onChange={e => setEncounterBgColor(e.target.value)} style={{ width: '100%', height: '24px', cursor: 'pointer' }} />

                  </div>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.6rem' }}>Text</label>

                    <input type="color" value={encounterTextColor || '#e8d5b7'} onChange={e => setEncounterTextColor(e.target.value)} style={{ width: '100%', height: '24px', cursor: 'pointer' }} />

                  </div>

                </div>

                

                {/* Sizes */}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.6rem' }}>Name Size: {encounterNameSize}rem</label>

                    <input type="range" min="1" max="3" step="0.1" value={encounterNameSize} onChange={e => setEncounterNameSize(parseFloat(e.target.value))} style={{ width: '100%' }} />

                  </div>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.6rem' }}>Text Size: {encounterTextSize}rem</label>

                    <input type="range" min="0.8" max="2" step="0.1" value={encounterTextSize} onChange={e => setEncounterTextSize(parseFloat(e.target.value))} style={{ width: '100%' }} />

                  </div>

                </div>

                

                {encounterStyle === 'fullscreen' && (

                  <div style={{ marginTop: '8px' }}>

                    <label style={{ ...S.label, fontSize: '0.6rem' }}>Image Opacity: {Math.round(encounterImageOpacity * 100)}%</label>

                    <input type="range" min="0.3" max="1" step="0.05" value={encounterImageOpacity} onChange={e => setEncounterImageOpacity(parseFloat(e.target.value))} style={{ width: '100%' }} />

                  </div>

                )}

              </div>

            )}

          </div>

        )}

        

        {/* Quest */}

        {messageType === 'quest' && (

          <div style={{ padding: '12px', background: 'rgba(230,194,41,0.1)', borderRadius: '8px', border: '1px solid rgba(230,194,41,0.3)', marginBottom: '12px' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>

              <div style={{ fontSize: '0.75rem', color: '#e6c229', fontWeight: 'bold' }}>📜 Quest / Mission</div>

              <button style={{ ...S.btn, ...S.btnXs, fontSize: '0.65rem', padding: '3px 8px' }} onClick={() => setShowEncounterPreview(true)}>👁 Preview</button>

            </div>

            <div style={{ marginBottom: '10px' }}>

              <label style={{ ...S.label, fontSize: '0.7rem' }}>Style</label>

              <div style={{ display: 'flex', gap: '6px' }}>

                {['portrait','card','fullscreen','jumpscare'].map(s => (

                  <button key={s} style={{ ...S.btn, ...S.btnXs, flex: 1, ...(encounterStyle === s ? { background: 'rgba(230,194,41,0.4)', border: '1px solid #e6c229' } : {}) }} onClick={() => setEncounterStyle(s)}>

                    {s === 'portrait' ? '🖼️ Standard' : s === 'card' ? '📇 Card' : s === 'fullscreen' ? '📺 Large' : '💀 Fullscreen'}

                    {e.qty > 1 ? ` x${transferQty}` : ""}

                  </button>

                ))}

              </div>

            </div>

            {(encounterStyle === 'portrait' || encounterStyle === 'card') && (

              <div style={{ marginBottom: '10px' }}>

                <label style={{ ...S.label, fontSize: '0.7rem' }}>Border Color</label>

                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>

                  {BORDER_PRESETS.map(p => (

                    <button key={p.id} onClick={() => setEncounterBorderColor(p.color || '')} style={{

                      width: '28px', height: '28px', borderRadius: '6px', cursor: 'pointer',

                      background: p.color ? `${p.color}30` : 'rgba(255,255,255,0.05)',

                      border: (encounterBorderColor || '') === (p.color || '') ? `2px solid ${p.color || '#888'}` : '2px solid transparent',

                      display: 'flex', alignItems: 'center', justifyContent: 'center',

                      boxShadow: (encounterBorderColor || '') === (p.color || '') ? `0 0 8px ${p.color || '#888'}44` : 'none',

                    }} title={p.label}>

                      <div style={{ width: '14px', height: '14px', borderRadius: '3px', background: p.color || 'linear-gradient(135deg, #e6c229 50%, #c9a227 50%)', border: p.color ? `2px solid ${p.color}` : '2px solid #666' }} />

                    </button>

                  ))}

                </div>

              </div>

            )}

            <div style={{ marginBottom: '10px' }}>

              <label style={{ ...S.label, fontSize: '0.7rem' }}>Quest Image (optional)</label>

              <div style={{ display: 'flex', gap: '6px' }}>

                <input style={{ ...S.input, flex: 1, marginBottom: 0, padding: '6px', fontSize: '0.8rem' }} placeholder="https://... or upload" value={encounterImage} onChange={e => setEncounterImage(e.target.value)} />

                <label style={{ ...S.btn, ...S.btnXs, padding: '6px 10px', cursor: 'pointer' }}>📁<input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files && e.target.files[0]; if (f) { const r = new FileReader(); r.onload = function(ev) { setEncounterImage(ev.target.result); }; r.readAsDataURL(f); } }} /></label>

              </div>

              {encounterImage && <img src={encounterImage} alt="Preview" style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px', marginTop: '8px', border: '2px solid #e6c229' }} />}

            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>

              <div>

                <label style={{ ...S.label, fontSize: '0.7rem' }}>Quest Name</label>

                <input style={{ ...S.input, marginBottom: 0, padding: '6px', fontSize: '0.85rem' }} placeholder="The Lost Heir" value={encounterName} onChange={e => setEncounterName(e.target.value)} />

              </div>

              <div>

                <label style={{ ...S.label, fontSize: '0.7rem' }}>Type (optional)</label>

                <input style={{ ...S.input, marginBottom: 0, padding: '6px', fontSize: '0.85rem' }} placeholder="Main Quest / Side Quest" value={encounterSubtitle} onChange={e => setEncounterSubtitle(e.target.value)} />

              </div>

            </div>

            <div style={{ marginBottom: '10px' }}>

              <label style={{ ...S.label, fontSize: '0.7rem' }}>Objective / Description</label>

              <textarea style={{ ...S.input, marginBottom: 0, minHeight: '60px', resize: 'vertical' }} placeholder="Find the lost heir to the throne before the dark forces claim them..." value={encounterDescription} onChange={e => setEncounterDescription(e.target.value)} />

            </div>

            <button style={{ ...S.btn, ...S.btnXs, width: '100%', marginBottom: encounterShowAdvanced ? '10px' : 0, fontSize: '0.7rem' }} onClick={() => setEncounterShowAdvanced(!encounterShowAdvanced)}>

              {encounterShowAdvanced ? '▲ Hide' : '▼ Show'} Customization

            </button>

            {encounterShowAdvanced && (

              <div style={{ background: 'rgba(0,0,0,0.2)', padding: '10px', borderRadius: '6px', marginTop: '8px' }}>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px', marginBottom: '8px' }}>

                  <div><label style={{ ...S.label, fontSize: '0.6rem' }}>Accent</label><input type="color" value={encounterPrimaryColor || '#e6c229'} onChange={e => setEncounterPrimaryColor(e.target.value)} style={{ width: '100%', height: '24px', cursor: 'pointer' }} /></div>

                  <div><label style={{ ...S.label, fontSize: '0.6rem' }}>Background</label><input type="color" value={encounterBgColor || '#1e1a0a'} onChange={e => setEncounterBgColor(e.target.value)} style={{ width: '100%', height: '24px', cursor: 'pointer' }} /></div>

                  <div><label style={{ ...S.label, fontSize: '0.6rem' }}>Text</label><input type="color" value={encounterTextColor || '#e8d5b7'} onChange={e => setEncounterTextColor(e.target.value)} style={{ width: '100%', height: '24px', cursor: 'pointer' }} /></div>

                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>

                  <div><label style={{ ...S.label, fontSize: '0.6rem' }}>Name Size: {encounterNameSize}rem</label><input type="range" min="1" max="3" step="0.1" value={encounterNameSize} onChange={e => setEncounterNameSize(parseFloat(e.target.value))} style={{ width: '100%' }} /></div>

                  <div><label style={{ ...S.label, fontSize: '0.6rem' }}>Text Size: {encounterTextSize}rem</label><input type="range" min="0.8" max="2" step="0.1" value={encounterTextSize} onChange={e => setEncounterTextSize(parseFloat(e.target.value))} style={{ width: '100%' }} /></div>

                </div>

              </div>

            )}

          </div>

        )}

        

        {/* Lore */}

        {messageType === 'lore' && (

          <div style={{ padding: '12px', background: 'rgba(139,115,85,0.1)', borderRadius: '8px', border: '1px solid rgba(139,115,85,0.3)', marginBottom: '12px' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>

              <div style={{ fontSize: '0.75rem', color: '#8b7355', fontWeight: 'bold' }}>📖 Lore / History</div>

              <button style={{ ...S.btn, ...S.btnXs, fontSize: '0.65rem', padding: '3px 8px' }} onClick={() => setShowEncounterPreview(true)}>👁 Preview</button>

            </div>

            <div style={{ marginBottom: '10px' }}>

              <label style={{ ...S.label, fontSize: '0.7rem' }}>Style</label>

              <div style={{ display: 'flex', gap: '6px' }}>

                {['portrait','card','fullscreen','jumpscare'].map(s => (

                  <button key={s} style={{ ...S.btn, ...S.btnXs, flex: 1, ...(encounterStyle === s ? { background: 'rgba(139,115,85,0.4)', border: '1px solid #8b7355' } : {}) }} onClick={() => setEncounterStyle(s)}>

                    {s === 'portrait' ? '🖼️ Scroll' : s === 'card' ? '📇 Tome' : s === 'fullscreen' ? '📺 Large' : '💀 Fullscreen'}

                  </button>

                ))}

              </div>

            </div>

            {(encounterStyle === 'portrait' || encounterStyle === 'card') && (

              <div style={{ marginBottom: '10px' }}>

                <label style={{ ...S.label, fontSize: '0.7rem' }}>Border Color</label>

                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>

                  {BORDER_PRESETS.map(p => (

                    <button key={p.id} onClick={() => setEncounterBorderColor(p.color || '')} style={{

                      width: '28px', height: '28px', borderRadius: '6px', cursor: 'pointer',

                      background: p.color ? `${p.color}30` : 'rgba(255,255,255,0.05)',

                      border: (encounterBorderColor || '') === (p.color || '') ? `2px solid ${p.color || '#888'}` : '2px solid transparent',

                      display: 'flex', alignItems: 'center', justifyContent: 'center',

                      boxShadow: (encounterBorderColor || '') === (p.color || '') ? `0 0 8px ${p.color || '#888'}44` : 'none',

                    }} title={p.label}>

                      <div style={{ width: '14px', height: '14px', borderRadius: '3px', background: p.color || 'linear-gradient(135deg, #8b7355 50%, #c9a227 50%)', border: p.color ? `2px solid ${p.color}` : '2px solid #666' }} />

                    </button>

                  ))}

                </div>

              </div>

            )}

            <div style={{ marginBottom: '10px' }}>

              <label style={{ ...S.label, fontSize: '0.7rem' }}>Image (optional)</label>

              <div style={{ display: 'flex', gap: '6px' }}>

                <input style={{ ...S.input, flex: 1, marginBottom: 0, padding: '6px', fontSize: '0.8rem' }} placeholder="https://... or upload" value={encounterImage} onChange={e => setEncounterImage(e.target.value)} />

                <label style={{ ...S.btn, ...S.btnXs, padding: '6px 10px', cursor: 'pointer' }}>📁<input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files && e.target.files[0]; if (f) { const r = new FileReader(); r.onload = function(ev) { setEncounterImage(ev.target.result); }; r.readAsDataURL(f); } }} /></label>

              </div>

              {encounterImage && <img src={encounterImage} alt="Preview" style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px', marginTop: '8px', border: '2px solid #8b7355' }} />}

            </div>

            <div style={{ marginBottom: '10px' }}>

              <label style={{ ...S.label, fontSize: '0.7rem' }}>Title</label>

              <input style={{ ...S.input, marginBottom: 0, padding: '6px', fontSize: '0.85rem' }} placeholder="The Fall of the Silver Kingdom" value={encounterName} onChange={e => setEncounterName(e.target.value)} />

            </div>

            <div style={{ marginBottom: '10px' }}>

              <label style={{ ...S.label, fontSize: '0.7rem' }}>Source (optional)</label>

              <input style={{ ...S.input, marginBottom: 0, padding: '6px', fontSize: '0.85rem' }} placeholder="Ancient Tome / Inscription / Legend" value={encounterSubtitle} onChange={e => setEncounterSubtitle(e.target.value)} />

            </div>

            <div style={{ marginBottom: '10px' }}>

              <label style={{ ...S.label, fontSize: '0.7rem' }}>Lore Text</label>

              <textarea style={{ ...S.input, marginBottom: 0, minHeight: '80px', resize: 'vertical', fontStyle: 'italic' }} placeholder="In the age before the Sundering, when the Silver Kingdom still stood..." value={encounterDescription} onChange={e => setEncounterDescription(e.target.value)} />

            </div>

            <button style={{ ...S.btn, ...S.btnXs, width: '100%', marginBottom: encounterShowAdvanced ? '10px' : 0, fontSize: '0.7rem' }} onClick={() => setEncounterShowAdvanced(!encounterShowAdvanced)}>

              {encounterShowAdvanced ? '▲ Hide' : '▼ Show'} Customization

            </button>

            {encounterShowAdvanced && (

              <div style={{ background: 'rgba(0,0,0,0.2)', padding: '10px', borderRadius: '6px', marginTop: '8px' }}>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px', marginBottom: '8px' }}>

                  <div><label style={{ ...S.label, fontSize: '0.6rem' }}>Accent</label><input type="color" value={encounterPrimaryColor || '#8b7355'} onChange={e => setEncounterPrimaryColor(e.target.value)} style={{ width: '100%', height: '24px', cursor: 'pointer' }} /></div>

                  <div><label style={{ ...S.label, fontSize: '0.6rem' }}>Background</label><input type="color" value={encounterBgColor || '#1a1610'} onChange={e => setEncounterBgColor(e.target.value)} style={{ width: '100%', height: '24px', cursor: 'pointer' }} /></div>

                  <div><label style={{ ...S.label, fontSize: '0.6rem' }}>Text</label><input type="color" value={encounterTextColor || '#e8d5b7'} onChange={e => setEncounterTextColor(e.target.value)} style={{ width: '100%', height: '24px', cursor: 'pointer' }} /></div>

                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>

                  <div><label style={{ ...S.label, fontSize: '0.6rem' }}>Name Size: {encounterNameSize}rem</label><input type="range" min="1" max="3" step="0.1" value={encounterNameSize} onChange={e => setEncounterNameSize(parseFloat(e.target.value))} style={{ width: '100%' }} /></div>

                  <div><label style={{ ...S.label, fontSize: '0.6rem' }}>Text Size: {encounterTextSize}rem</label><input type="range" min="0.8" max="2" step="0.1" value={encounterTextSize} onChange={e => setEncounterTextSize(parseFloat(e.target.value))} style={{ width: '100%' }} /></div>

                </div>

              </div>

            )}

          </div>

        )}

        

        {messageType === 'simple' && (

          <>

            {/* Single Message */}

            <textarea 

              style={{ ...S.input, minHeight: '60px', resize: 'vertical' }}

              placeholder="You begin to feel uneasy..."

              value={content}

              onChange={e => setContent(e.target.value)}

            />

            

            {/* Options row */}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

                <label style={{ fontSize: '0.85rem' }}>Urgency:</label>

                <select style={{ ...S.select, width: 'auto', marginBottom: 0, padding: '4px 8px', fontSize: '0.85rem' }} value={urgency} onChange={e => setUrgency(e.target.value)}>

                  <option value="normal">Normal</option>

                  <option value="urgent">⚠️ Urgent (Overlay)</option>

                </select>

              </div>

              {canWriteTravelLog && <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.85rem' }}>

                <input type="checkbox" checked={addToHistory} onChange={e => setAddToHistory(e.target.checked)} />

                Add to logs

              </label>}

            </div>

            

            {/* Theme selector for urgent single messages */}

            {urgency === 'urgent' && (

              <div style={{ marginBottom: '12px' }}>

                <label style={{ ...S.label, fontSize: '0.8rem' }}>Theme:</label>

                <select 

                  style={{ ...S.select, marginBottom: 0, padding: '6px 8px', fontSize: '0.85rem' }} 

                  value={sequenceTheme} 

                  onChange={e => setSequenceTheme(e.target.value)}

                >

                  {Object.entries(SEQUENCE_THEMES).map(([key, t]) => (

                    <option key={key} value={key}>{t.name}</option>

                  ))}

                </select>

                <div style={{ fontSize: '0.75rem', opacity: 0.6, marginTop: '4px' }}>{themeInfo?.description}</div>

              </div>

            )}

            

            {/* Game time (optional) */}

            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>

              <div>

                <label style={{ ...S.label, fontSize: '0.8rem' }}>Game Day (opt)</label>

                <input style={{ ...S.input, width: '70px', padding: '4px 8px', marginBottom: 0 }} type="number" placeholder="Day" value={gameDay} onChange={e => setGameDay(e.target.value)} />

              </div>

              <div>

                <label style={{ ...S.label, fontSize: '0.8rem' }}>Time (opt)</label>

                <input style={{ ...S.input, width: '100px', padding: '4px 8px', marginBottom: 0 }} placeholder="Dusk, 3AM..." value={gameTime} onChange={e => setGameTime(e.target.value)} />

              </div>

            </div>

            

            <button 

              style={{ ...S.btn, ...S.btnSuccess, width: '100%' }}

              onClick={handleSend}

              disabled={!content.trim() || (visibility === 'selected' && selectedChars.length === 0)}

            >

              📤 Send Message

            </button>

          </>

        )}

        

        {/* Send button for encounter types */}

        {(['character','scenery','item','quest','lore'].includes(messageType)) && (

          <button 

            style={{ ...S.btn, ...S.btnSuccess, width: '100%', marginBottom: '12px' }}

            onClick={() => {

              if (!encounterName.trim() && !encounterImage) return;

              if (visibility === 'selected' && selectedChars.length === 0) return;

              

              const newMsg = {

                id: generateId(),

                type: 'status',

                category: 'dm-status',

                timestamp: new Date().toISOString(),

                content: encounterDescription || encounterDialogue || encounterName,

                urgency: 'urgent',

                fromDM: true,

                visibility,

                recipients: visibility === 'public' ? charList.map(c => c.id) : selectedChars,

                addToHistory: canWriteTravelLog && addToHistory,

                gameTime: gameDay ? { day: parseInt(gameDay), time: gameTime || null } : null,

                dismissed: false,

                encounterType: messageType,

                encounter: {

                  image: encounterImage,

                  name: encounterName,

                  subtitle: encounterSubtitle,

                  description: encounterDescription,

                  dialogue: encounterDialogue,

                  style: encounterStyle,

                  customization: {

                    primaryColor: encounterPrimaryColor || null,

                    bgColor: encounterBgColor || null,

                    textColor: encounterTextColor || null,

                    borderColor: encounterBorderColor || null,

                    imageSize: encounterImageSize,

                    boxSize: encounterBoxSize,

                    textSize: encounterTextSize,

                    nameSize: encounterNameSize,

                    textReveal: encounterTextReveal,

                    imageOpacity: encounterImageOpacity,

                  },

                },

              };

              

              persistStatusMessage(newMsg);

              

              if (canWriteTravelLog && addToHistory && gameDay) {

                const logEntry = {

                  day: parseInt(gameDay),

                  character: 'DM',

                  action: `${messageType}: ${encounterName}`,

                  timestamp: new Date().toISOString(),

                };

                onSaveGroupTravelLog([logEntry, ...(groupTravelLog || []).slice(0, 199)]);

              }

              

              // Reset

              setEncounterImage('');

              setEncounterName('');

              setEncounterSubtitle('');

              setEncounterDescription('');

              setEncounterDialogue('');

              setSelectedChars([]);

              setGameDay('');

              setGameTime('');

            }}

            disabled={(!encounterName.trim() && !encounterImage) || (visibility === 'selected' && selectedChars.length === 0)}

          >

            📤 Send {messageType === 'character' ? 'Character' : messageType === 'scenery' ? 'Scenery' : messageType === 'quest' ? 'Quest' : messageType === 'lore' ? 'Lore' : 'Item'}

          </button>

        )}

        

        {/* Encounter Preview Overlay */}

        {showEncounterPreview && (['character','scenery','item','quest','lore'].includes(messageType)) && (

          <EncounterPreviewOverlay 

            encounterType={messageType}

            encounter={{

              image: encounterImage,

              name: encounterName || '',

              subtitle: encounterSubtitle,

              description: encounterDescription || '',

              dialogue: encounterDialogue,

              style: encounterStyle,

              customization: {

                primaryColor: encounterPrimaryColor,

                bgColor: encounterBgColor,

                textColor: encounterTextColor,

                borderColor: encounterBorderColor,

                imageSize: encounterImageSize,

                boxSize: encounterBoxSize,

                textSize: encounterTextSize,

                nameSize: encounterNameSize,

                textReveal: encounterTextReveal,

                imageOpacity: encounterImageOpacity,

              },

            }}

            onClose={() => setShowEncounterPreview(false)}

          />

        )}

        

        {messageType === 'sequence' && (

          <>

            {/* Theme Selector */}

            <div style={{ marginBottom: '12px' }}>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>

                <label style={{ ...S.label, fontSize: '0.85rem', marginBottom: 0 }}>Theme:</label>

                <button 

                  style={{ ...S.btn, ...S.btnXs, fontSize: '0.65rem', padding: '3px 8px' }}

                  onClick={loadExampleText}

                  title="Load example text for this theme"

                >

                  📝 Load Example

                </button>

              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '5px' }}>

                {Object.entries(SEQUENCE_THEMES).map(([key, t]) => (

                  <button

                    key={key}

                    onClick={() => {

                      setSequenceTheme(key);

                      // Auto-apply theme's recommended settings

                      if (t.recommendedTransition) setBaseTransition(t.recommendedTransition);

                      if (t.recommendedReveal) setRevealModifiers(t.recommendedReveal);

                      if (t.recommendedEffects) setTextEffects(t.recommendedEffects);

                    }}

                    style={{

                      padding: '6px 8px',

                      background: sequenceTheme === key ? 'rgba(201,162,39,0.3)' : 'rgba(0,0,0,0.3)',

                      border: sequenceTheme === key ? '1px solid #c9a227' : '1px solid rgba(255,255,255,0.1)',

                      borderRadius: '4px',

                      color: sequenceTheme === key ? '#c9a227' : '#e8d5b7',

                      cursor: 'pointer',

                      fontSize: '0.7rem',

                      textAlign: 'left',

                    }}

                  >

                    {t.name}

                  </button>

                ))}

              </div>

              <div style={{ fontSize: '0.7rem', opacity: 0.6, marginTop: '5px', fontStyle: 'italic' }}>

                {themeInfo?.description}

              </div>

            </div>

            

            {/* Sequence Builder */}

            <div style={{ 

              background: `rgba(${sequenceTheme === 'foreboding' ? '201,39,39' : sequenceTheme === 'revelation' ? '255,215,0' : sequenceTheme === 'divine' ? '180,140,255' : sequenceTheme === 'corruption' ? '139,0,0' : '232,213,183'},0.1)`, 

              border: `1px solid rgba(${sequenceTheme === 'foreboding' ? '201,39,39' : sequenceTheme === 'revelation' ? '255,215,0' : sequenceTheme === 'divine' ? '180,140,255' : sequenceTheme === 'corruption' ? '139,0,0' : '232,213,183'},0.3)`, 

              borderRadius: '6px', 

              padding: '12px', 

              marginBottom: '12px' 

            }}>

              <div style={{ fontSize: '0.8rem', color: themeInfo?.textColor || '#c9a227', marginBottom: '10px', fontStyle: 'italic' }}>

                🎭 Build your sequence — timing is auto-calculated or set manually

              </div>

              

              {sequence.map((item, idx) => (

                <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>

                  <span style={{ color: themeInfo?.textColorFaded || 'rgba(201,39,39,0.6)', fontSize: '0.8rem', width: '20px' }}>{idx + 1}.</span>

                  <input 

                    style={{ ...S.input, flex: 1, marginBottom: 0, padding: '6px 8px', fontSize: '0.85rem' }}

                    placeholder={idx === 0 ? "The first line..." : "Then this appears..."}

                    value={item.text}

                    onChange={e => updateSequenceItem(idx, 'text', e.target.value)}

                  />

                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>

                    <input 

                      style={{ ...S.input, width: '45px', marginBottom: 0, padding: '4px', fontSize: '0.8rem', textAlign: 'center', color: item.delay === 0 ? '#888' : undefined }}

                      type="number"

                      min="0"

                      max="30"

                      step="0.5"

                      value={item.delay || ''}

                      placeholder={Math.round(getAutoDelay(item.text, themeInfo?.pacing, revealModifiers, { letterSpeed, wordSpeed }) * 10) / 10}

                      onChange={e => updateSequenceItem(idx, 'delay', parseFloat(e.target.value) || 0)}

                      title={item.delay === 0 ? `Auto: ${Math.round(getAutoDelay(item.text, themeInfo?.pacing, revealModifiers, { letterSpeed, wordSpeed }) * 10) / 10}s` : 'Seconds before next line (0 = auto)'}

                    />

                    <span style={{ fontSize: '0.65rem', opacity: 0.5, minWidth: '18px' }}>{item.delay === 0 ? 'auto' : 'sec'}</span>

                  </div>

                  {sequence.length > 1 && (

                    <button 

                      style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, padding: '4px 8px' }}

                      onClick={() => removeSequenceItem(idx)}

                    >✕</button>

                  )}

                </div>

              ))}

              

              <button 

                style={{ ...S.btn, ...S.btnXs, width: '100%', marginTop: '4px' }}

                onClick={addSequenceItem}

              >+ Add Line</button>

            </div>

            

            {/* Base Transition (how lines appear) */}

            <div style={{ marginBottom: '10px' }}>

              <label style={{ ...S.label, fontSize: '0.8rem' }}>Line Transition:</label>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '3px' }}>

                {BASE_TRANSITIONS.map(key => (

                  <button key={key} onClick={() => setBaseTransition(key)} style={{

                    padding: '5px 3px', fontSize: '0.55rem', borderRadius: '3px', cursor: 'pointer',

                    background: baseTransition === key ? 'rgba(201,162,39,0.3)' : 'rgba(0,0,0,0.2)',

                    border: baseTransition === key ? '1px solid #c9a227' : '1px solid rgba(255,255,255,0.1)',

                    color: baseTransition === key ? '#c9a227' : '#e8d5b7',

                  }}>{TRANSITION_STYLES[key].name}</button>

                ))}

              </div>

              <div style={{ fontSize: '0.6rem', opacity: 0.5, marginTop: '3px' }}>{TRANSITION_STYLES[baseTransition]?.description}</div>

            </div>

            

            {/* Reveal Modifiers - MULTI-SELECT */}

            <div style={{ marginBottom: '10px' }}>

              <label style={{ ...S.label, fontSize: '0.8rem' }}>Text Reveal (select multiple):</label>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px' }}>

                {REVEAL_MODIFIERS.map(key => (

                  <button key={key} onClick={() => toggleRevealModifier(key)} style={{

                    padding: '5px 4px', fontSize: '0.6rem', borderRadius: '3px', cursor: 'pointer',

                    background: revealModifiers.includes(key) ? 'rgba(201,162,39,0.3)' : 'rgba(0,0,0,0.2)',

                    border: revealModifiers.includes(key) ? '1px solid #c9a227' : '1px solid rgba(255,255,255,0.1)',

                    color: revealModifiers.includes(key) ? '#c9a227' : '#e8d5b7',

                  }}>{TRANSITION_STYLES[key].name}</button>

                ))}

              </div>

              {revealModifiers.length > 0 && (

                <div style={{ fontSize: '0.6rem', opacity: 0.5, marginTop: '3px' }}>

                  Active: {revealModifiers.map(m => TRANSITION_STYLES[m]?.name).join(' + ')}

                </div>

              )}

              {revealModifiers.length === 0 && (

                <div style={{ fontSize: '0.6rem', opacity: 0.4, marginTop: '3px' }}>None selected = instant reveal</div>

              )}

            </div>

            

            {/* Fade Controls */}

            <div style={{ marginBottom: '10px', padding: '8px', background: 'rgba(0,0,0,0.15)', borderRadius: '4px' }}>

              <div style={{ fontSize: '0.75rem', color: '#c9a227', marginBottom: '6px' }}>Fade In/Out</div>

              <div style={{ display: 'flex', gap: '12px' }}>

                <div style={{ flex: 1 }}>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.65rem', cursor: 'pointer' }}>

                    <input type="checkbox" checked={enableFadeIn} onChange={e => setEnableFadeIn(e.target.checked)} />

                    Fade In: {fadeInDuration}s

                  </label>

                  {enableFadeIn && (

                    <input type="range" min="0.3" max="4" step="0.1" value={fadeInDuration} 

                      onChange={e => setFadeInDuration(parseFloat(e.target.value))} style={{ width: '100%' }} />

                  )}

                </div>

                <div style={{ flex: 1 }}>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.65rem', cursor: 'pointer' }}>

                    <input type="checkbox" checked={enableFadeOut} onChange={e => setEnableFadeOut(e.target.checked)} />

                    Fade Out: {fadeOutDuration}s

                  </label>

                  {enableFadeOut && (

                    <input type="range" min="0.3" max="4" step="0.1" value={fadeOutDuration} 

                      onChange={e => setFadeOutDuration(parseFloat(e.target.value))} style={{ width: '100%' }} />

                  )}

                </div>

              </div>

            </div>

            

            {/* Text Effects - MULTI-SELECT */}

            <div style={{ marginBottom: '10px' }}>

              <label style={{ ...S.label, fontSize: '0.8rem' }}>Text Effects (select multiple):</label>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '3px' }}>

                <button onClick={() => setTextEffects([])} style={{

                  padding: '4px 2px', fontSize: '0.55rem', borderRadius: '3px', cursor: 'pointer',

                  background: textEffects.length === 0 ? 'rgba(201,162,39,0.3)' : 'rgba(0,0,0,0.2)',

                  border: textEffects.length === 0 ? '1px solid #c9a227' : '1px solid rgba(255,255,255,0.1)',

                  color: textEffects.length === 0 ? '#c9a227' : '#e8d5b7',

                }}>None</button>

                {Object.entries(TEXT_EFFECTS).filter(([k]) => k !== 'none').map(([key, t]) => (

                  <button key={key} onClick={() => toggleTextEffect(key)} style={{

                    padding: '4px 2px', fontSize: '0.55rem', borderRadius: '3px', cursor: 'pointer',

                    background: textEffects.includes(key) ? 'rgba(201,162,39,0.3)' : 'rgba(0,0,0,0.2)',

                    border: textEffects.includes(key) ? '1px solid #c9a227' : '1px solid rgba(255,255,255,0.1)',

                    color: textEffects.includes(key) ? '#c9a227' : '#e8d5b7',

                  }}>{t.name}</button>

                ))}

              </div>

              {textEffects.length > 0 && (

                <div style={{ fontSize: '0.6rem', opacity: 0.5, marginTop: '3px' }}>

                  Active: {textEffects.map(e => TEXT_EFFECTS[e]?.name).join(' + ')}

                </div>

              )}

            </div>

            

            {/* Reveal Duration Control */}

            {revealModifiers.length > 0 && (

              <div style={{ marginBottom: '10px', padding: '8px', background: 'rgba(0,0,0,0.15)', borderRadius: '4px' }}>

                <label style={{ ...S.label, fontSize: '0.7rem' }}>

                  Reveal Duration: {revealDuration === 0 ? 'Auto' : `${revealDuration}s`}

                </label>

                <input type="range" min="0" max="15" step="0.5" value={revealDuration} 

                  onChange={e => setRevealDuration(parseFloat(e.target.value))} style={{ width: '100%' }} />

                <div style={{ fontSize: '0.55rem', opacity: 0.4 }}>0 = auto based on text length. Set a value to complete reveal in that time.</div>

              </div>

            )}

            

            {/* Advanced Customization Toggle */}

            <button style={{ ...S.btn, ...S.btnXs, width: '100%', marginBottom: '10px', opacity: 0.8 }} onClick={() => setShowAdvanced(!showAdvanced)}>

              {showAdvanced ? '▲ Hide' : '▼ Show'} Advanced Options

            </button>

            

            {showAdvanced && (

              <div style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', padding: '10px', marginBottom: '10px' }}>

                {/* Display Mode & Font */}

                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>

                  <div style={{ flex: 1 }}>

                    <label style={{ ...S.label, fontSize: '0.7rem' }}>Display:</label>

                    <div style={{ display: 'flex', gap: '4px' }}>

                      <button onClick={() => setBoxMode(false)} style={{ ...S.btn, ...S.btnXs, flex: 1, fontSize: '0.6rem', padding: '3px', background: !boxMode ? 'rgba(201,162,39,0.3)' : 'transparent' }}>Full</button>

                      <button onClick={() => setBoxMode(true)} style={{ ...S.btn, ...S.btnXs, flex: 1, fontSize: '0.6rem', padding: '3px', background: boxMode ? 'rgba(201,162,39,0.3)' : 'transparent' }}>Box</button>

                    </div>

                  </div>

                  <div style={{ flex: 1 }}>

                    <label style={{ ...S.label, fontSize: '0.7rem' }}>Font:</label>

                    <select style={{ ...S.select, marginBottom: 0, padding: '3px', fontSize: '0.7rem' }} value={selectedFont} onChange={e => setSelectedFont(e.target.value)}>

                      {Object.entries(FONT_OPTIONS).map(([key, f]) => <option key={key} value={key}>{f.name}</option>)}

                    </select>

                  </div>

                </div>

                

                {/* Atmosphere Toggles */}

                <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.65rem', cursor: 'pointer', color: '#e8d5b7' }}>

                    <input type="checkbox" checked={!disableVignette} onChange={e => setDisableVignette(!e.target.checked)} />

                    Vignette

                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.65rem', cursor: 'pointer', color: '#e8d5b7' }}>

                    <input type="checkbox" checked={!disableParticles} onChange={e => setDisableParticles(!e.target.checked)} />

                    Particles

                  </label>

                </div>

                

                {/* Colors */}

                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>

                  <div style={{ flex: 1 }}>

                    <label style={{ ...S.label, fontSize: '0.65rem' }}>Text Color</label>

                    <input type="color" value={customTextColor || themeInfo?.textColor || '#c92727'} onChange={e => setCustomTextColor(e.target.value)} style={{ width: '100%', height: '24px', cursor: 'pointer' }} />

                  </div>

                  <div style={{ flex: 1 }}>

                    <label style={{ ...S.label, fontSize: '0.65rem' }}>BG Color</label>

                    <input type="color" value={customBgColor || '#000000'} onChange={e => setCustomBgColor(e.target.value)} style={{ width: '100%', height: '24px', cursor: 'pointer' }} />

                  </div>

                </div>

                

                {/* Text Size */}

                <div style={{ marginBottom: '8px' }}>

                  <label style={{ ...S.label, fontSize: '0.6rem' }}>Text Size: {textSize}rem</label>

                  <input type="range" min="1" max="3.5" step="0.1" value={textSize} onChange={e => setTextSize(parseFloat(e.target.value))} style={{ width: '100%' }} />

                </div>

                

                {/* Opacities */}

                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>

                  <div style={{ flex: 1 }}>

                    <label style={{ ...S.label, fontSize: '0.6rem' }}>Text: {Math.round(textOpacity * 100)}%</label>

                    <input type="range" min="0.3" max="1" step="0.05" value={textOpacity} onChange={e => setTextOpacity(parseFloat(e.target.value))} style={{ width: '100%' }} />

                  </div>

                  <div style={{ flex: 1 }}>

                    <label style={{ ...S.label, fontSize: '0.6rem' }}>BG: {Math.round(customBgOpacity * 100)}%</label>

                    <input type="range" min="0.5" max="1" step="0.05" value={customBgOpacity} onChange={e => setCustomBgOpacity(parseFloat(e.target.value))} style={{ width: '100%' }} />

                  </div>

                </div>

                

                {/* Background Image */}

                <div style={{ marginBottom: '8px' }}>

                  <label style={{ ...S.label, fontSize: '0.65rem' }}>Background Image</label>

                  <div style={{ display: 'flex', gap: '6px', marginBottom: '4px' }}>

                    <input 

                      style={{ ...S.input, flex: 1, marginBottom: 0, padding: '4px 6px', fontSize: '0.7rem' }} 

                      placeholder="https://example.com/image.jpg" 

                      value={bgImageUrl} 

                      onChange={e => setBgImageUrl(e.target.value)} 

                    />

                    <label style={{ 

                      ...S.btn, ...S.btnXs, 

                      marginBottom: 0, 

                      padding: '4px 8px', 

                      fontSize: '0.65rem',

                      cursor: 'pointer',

                      display: 'flex',

                      alignItems: 'center',

                      gap: '4px'

                    }}>

                      📁 Upload

                      <input 

                        type="file" 

                        accept="image/*" 

                        style={{ display: 'none' }}

                        onChange={e => {

                          const file = e.target.files?.[0];

                          if (file) {

                            const reader = new FileReader();

                            reader.onload = (ev) => {

                              setBgImageUrl(ev.target.result);

                            };

                            reader.readAsDataURL(file);

                          }

                        }}

                      />

                    </label>

                  </div>

                  {bgImageUrl && (

                    <div style={{ marginTop: '4px' }}>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>

                        <img 

                          src={bgImageUrl} 

                          alt="Preview" 

                          style={{ 

                            width: '40px', 

                            height: '40px', 

                            objectFit: 'cover', 

                            borderRadius: '4px',

                            border: '1px solid rgba(201,162,39,0.3)'

                          }} 

                          onError={(e) => { e.target.style.display = 'none'; }}

                        />

                        <button 

                          style={{ ...S.btn, ...S.btnXs, ...S.btnDanger, padding: '2px 6px', fontSize: '0.6rem' }}

                          onClick={() => setBgImageUrl('')}

                        >

                          ✕ Clear

                        </button>

                      </div>

                      <label style={{ ...S.label, fontSize: '0.6rem' }}>Img Opacity: {Math.round(bgImageOpacity * 100)}%</label>

                      <input type="range" min="0.1" max="0.8" step="0.05" value={bgImageOpacity} onChange={e => setBgImageOpacity(parseFloat(e.target.value))} style={{ width: '100%' }} />

                    </div>

                  )}

                </div>

                

                {/* Past Text Customization */}

                <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px', marginBottom: '8px' }}>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>

                    <div style={{ fontSize: '0.7rem', color: '#9b59b6' }}>👻 Past Text (faded text)</div>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.6rem', cursor: 'pointer' }}>

                      <input type="checkbox" checked={showPastText} onChange={e => setShowPastText(e.target.checked)} />

                      Show

                    </label>

                  </div>

                  {showPastText && (

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>

                      <div>

                        <label style={{ ...S.label, fontSize: '0.6rem' }}>Opacity: {Math.round(pastTextOpacity * 100)}%</label>

                        <input type="range" min="0.05" max="1" step="0.05" value={pastTextOpacity} onChange={e => setPastTextOpacity(parseFloat(e.target.value))} style={{ width: '100%' }} />

                      </div>

                      <div>

                        <label style={{ ...S.label, fontSize: '0.6rem' }}>Size: {Math.round(pastTextScale * 100)}%</label>

                        <input type="range" min="0.5" max="1.2" step="0.05" value={pastTextScale} onChange={e => setPastTextScale(parseFloat(e.target.value))} style={{ width: '100%' }} />

                      </div>

                      <div>

                        <label style={{ ...S.label, fontSize: '0.6rem' }}>Blur: {pastTextBlur}px</label>

                        <input type="range" min="0" max="8" step="0.5" value={pastTextBlur} onChange={e => setPastTextBlur(parseFloat(e.target.value))} style={{ width: '100%' }} />

                      </div>

                      <div>

                        <label style={{ ...S.label, fontSize: '0.6rem' }}>Offset: {pastTextOffset}px</label>

                        <input type="range" min="-20" max="20" step="2" value={pastTextOffset} onChange={e => setPastTextOffset(parseInt(e.target.value))} style={{ width: '100%' }} />

                      </div>

                      <div style={{ gridColumn: 'span 2' }}>

                        <label style={{ ...S.label, fontSize: '0.6rem' }}>Color Override</label>

                        <div style={{ display: 'flex', gap: '4px' }}>

                          <input 

                            style={{ ...S.input, flex: 1, marginBottom: 0, padding: '4px', fontSize: '0.7rem' }} 

                            placeholder="e.g., #666 or rgba(100,100,100,0.5)" 

                            value={pastTextColor} 

                            onChange={e => setPastTextColor(e.target.value)} 

                          />

                          <input 

                            type="color" 

                            value={pastTextColor || '#666666'} 

                            onChange={e => setPastTextColor(e.target.value)}

                            style={{ width: '30px', height: '26px', padding: 0, border: 'none', cursor: 'pointer' }}

                          />

                          {pastTextColor && (

                            <button 

                              style={{ ...S.btn, ...S.btnXs, padding: '2px 6px', fontSize: '0.6rem' }}

                              onClick={() => setPastTextColor('')}

                            >

                              ✕

                            </button>

                          )}

                        </div>

                        <div style={{ fontSize: '0.55rem', opacity: 0.5, marginTop: '2px' }}>Leave empty to auto-dim main text color</div>

                      </div>

                    </div>

                  )}

                </div>

                

                {/* Timing */}

                <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px' }}>

                  <div style={{ fontSize: '0.7rem', color: '#c9a227', marginBottom: '6px' }}>⏱️ Timing</div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>

                    <div><label style={{ ...S.label, fontSize: '0.6rem' }}>Speed: {transitionSpeed}x</label><input type="range" min="0.25" max="2" step="0.25" value={transitionSpeed} onChange={e => setTransitionSpeed(parseFloat(e.target.value))} style={{ width: '100%' }} /></div>

                    <div><label style={{ ...S.label, fontSize: '0.6rem' }}>Sentence Pause: {sentencePause}s</label><input type="range" min="0.5" max="5" step="0.5" value={sentencePause} onChange={e => setSentencePause(parseFloat(e.target.value))} style={{ width: '100%' }} /></div>

                    {revealModifiers.includes('letterByLetter') && <div><label style={{ ...S.label, fontSize: '0.6rem' }}>Letter: {letterSpeed}ms</label><input type="range" min="20" max="200" step="10" value={letterSpeed} onChange={e => setLetterSpeed(parseInt(e.target.value))} style={{ width: '100%' }} /></div>}

                    {revealModifiers.includes('typewriter') && <div><label style={{ ...S.label, fontSize: '0.6rem' }}>Word: {wordSpeed}ms</label><input type="range" min="50" max="500" step="25" value={wordSpeed} onChange={e => setWordSpeed(parseInt(e.target.value))} style={{ width: '100%' }} /></div>}

                  </div>

                </div>

              </div>

            )}

            

            {/* Game time for sequence */}

            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>

              <div>

                <label style={{ ...S.label, fontSize: '0.8rem' }}>Game Day (opt)</label>

                <input style={{ ...S.input, width: '70px', padding: '4px 8px', marginBottom: 0 }} type="number" placeholder="Day" value={gameDay} onChange={e => setGameDay(e.target.value)} />

              </div>

              <div>

                <label style={{ ...S.label, fontSize: '0.8rem' }}>Time (opt)</label>

                <input style={{ ...S.input, width: '100px', padding: '4px 8px', marginBottom: 0 }} placeholder="Midnight..." value={gameTime} onChange={e => setGameTime(e.target.value)} />

              </div>

            </div>

            

            {/* Preview and Send buttons */}

            <div style={{ display: 'flex', gap: '8px' }}>

              <button 

                style={{ ...S.btn, flex: 1 }}

                onClick={() => setShowPreview(true)}

                disabled={!sequence.some(s => s.text.trim())}

              >

                👁 Preview

              </button>

              <button 

                style={{ ...S.btn, flex: 2, background: 'linear-gradient(180deg, #c92727 0%, #8b1a1a 100%)', border: '1px solid #ff4444' }}

                onClick={handleSendSequence}

                disabled={!sequence.some(s => s.text.trim()) || (visibility === 'selected' && selectedChars.length === 0)}

              >

                🎭 Send Sequence

              </button>

            </div>

          </>

        )}

      </>}

      

      {/* Preview Overlay */}

      {showPreview && (

        <DramaticOverlay 

          isPreview={true}

          previewData={getPreviewData()}

          onClosePreview={() => setShowPreview(false)}

        />

      )}

    </HoverCard>

  );

};



// Full Whisper Composer Modal - Advanced whisper creation for DM

const WhisperComposerModal = ({ isOpen, onClose, chars, messages, onSaveMessages, onSendStatusMessage }) => {

  const [content, setContent] = useState('');

  const [selectedChars, setSelectedChars] = useState([]);

  const [displayStyle, setDisplayStyle] = useState('toast');

  const [senderStyle, setSenderStyle] = useState('anonymous');

  const [customSender, setCustomSender] = useState('');

  const [urgency, setUrgency] = useState('normal');

  const [addToHistory, setAddToHistory] = useState(true);

  const [delivery, setDelivery] = useState('gentle');

  

  if (!isOpen) return null;

  

  const handleSend = () => {

    if (!content.trim() || selectedChars.length === 0) return;

    

    const senderConfig = WHISPER_SENDERS.find(s => s.id === senderStyle) || WHISPER_SENDERS[0];

    const prefix = senderStyle === 'custom' 

      ? (customSender ? `${customSender} whispers...` : 'Someone whispers...') 

      : senderConfig.prefix;

    

    const newMsg = {

      id: generateId(),

      type: 'whisper',

      category: 'dm-whisper',

      timestamp: new Date().toISOString(),

      content: content.trim(),

      urgency,

      fromDM: true,

      visibility: 'private',

      recipients: selectedChars,

      addToHistory,

      dismissed: false,

      displayStyle,

      senderStyle,

      senderPrefix: prefix,

      customSender: senderStyle === 'custom' ? customSender : null,

      delivery,

    };

    

    if (typeof onSendStatusMessage === 'function') {
      onSendStatusMessage({ message: newMsg });
    } else if (typeof onSaveMessages === 'function') {
      onSaveMessages({ ...(messages || {}), statusUpdates: [newMsg, ...((messages || {}).statusUpdates || [])] });
    }

    

    // Reset and close

    setContent('');

    setSelectedChars([]);

    onClose();

  };

  

  const toggleChar = (charId) => {

    setSelectedChars(prev => 

      prev.includes(charId) ? prev.filter(id => id !== charId) : [...prev, charId]

    );

  };

  

  const selectAll = () => setSelectedChars(Object.keys(chars));

  const selectNone = () => setSelectedChars([]);

  

  const senderConfig = WHISPER_SENDERS.find(s => s.id === senderStyle) || WHISPER_SENDERS[0];

  const previewPrefix = senderStyle === 'custom' 

    ? (customSender ? `${customSender} whispers...` : 'Someone whispers...') 

    : senderConfig.prefix;

  

  return (

    <>

      {/* Backdrop */}

      <div 

        onClick={onClose}

        style={{

          position: 'fixed',

          top: 0,

          left: 0,

          right: 0,

          bottom: 0,

          background: 'rgba(0,0,0,0.7)',

          zIndex: 99998,

        }}

      />

      

      {/* Modal */}

      <div style={{

        position: 'fixed',

        top: '50%',

        left: '50%',

        transform: 'translate(-50%, -50%)',

        width: '500px',

        maxWidth: '95vw',

        maxHeight: '90vh',

        overflowY: 'auto',

        background: 'linear-gradient(135deg, #2a2118 0%, #1a1510 100%)',

        border: '2px solid #5c4a2a',

        borderRadius: '12px',

        boxShadow: '0 16px 64px rgba(0,0,0,0.8)',

        zIndex: 99999,

        padding: '20px',

      }}>

        {/* Header */}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid rgba(155,89,182,0.3)' }}>

          <h3 style={{ color: '#c9a0dc', margin: 0, fontSize: '1.1rem' }}>🤫 Compose Whisper</h3>

          <button style={{ ...S.btn, ...S.btnXs, padding: '4px 8px' }} onClick={onClose}>✕</button>

        </div>

        

        {/* Recipients */}

        <div style={{ marginBottom: '16px' }}>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>

            <label style={{ fontSize: '0.85rem', color: '#888' }}>Recipients:</label>

            <div style={{ display: 'flex', gap: '4px' }}>

              <button style={{ ...S.btn, ...S.btnXs, fontSize: '0.7rem', padding: '2px 8px' }} onClick={selectAll}>All</button>

              <button style={{ ...S.btn, ...S.btnXs, fontSize: '0.7rem', padding: '2px 8px' }} onClick={selectNone}>None</button>

            </div>

          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>

            {Object.values(chars).map(c => (

              <button

                key={c.id}

                onClick={() => toggleChar(c.id)}

                style={{

                  ...S.btn,

                  ...S.btnXs,

                  padding: '6px 12px',

                  fontSize: '0.8rem',

                  background: selectedChars.includes(c.id) ? 'rgba(155,89,182,0.3)' : 'transparent',

                  borderColor: selectedChars.includes(c.id) ? '#9b59b6' : '#5c4a2a',

                  color: selectedChars.includes(c.id) ? '#c9a0dc' : '#888',

                }}

              >

                {selectedChars.includes(c.id) ? '✓ ' : ''}{c.name}

              </button>

            ))}

          </div>

          {selectedChars.length === 0 && (

            <div style={{ fontSize: '0.75rem', color: '#ff6961', marginTop: '4px' }}>Select at least one recipient</div>

          )}

        </div>

        

        {/* Display Style */}

        <div style={{ marginBottom: '16px' }}>

          <label style={{ fontSize: '0.85rem', color: '#888', display: 'block', marginBottom: '8px' }}>Display Style:</label>

          <div style={{ display: 'flex', gap: '8px' }}>

            {WHISPER_STYLES.map(style => (

              <Tooltip key={style.id} text={style.description}>

                <button 

                  style={{ 

                    ...S.btn, 

                    ...S.btnSm, 

                    flex: 1,

                    padding: '10px 8px',

                    background: displayStyle === style.id ? 'rgba(155,89,182,0.3)' : 'transparent',

                    borderColor: displayStyle === style.id ? '#9b59b6' : '#5c4a2a',

                    flexDirection: 'column',

                    display: 'flex',

                    alignItems: 'center',

                    gap: '4px',

                  }}

                  onClick={() => setDisplayStyle(style.id)}

                >

                  <span style={{ fontSize: '1.2rem' }}>{style.icon}</span>

                  <span style={{ fontSize: '0.7rem' }}>{style.name}</span>

                </button>

              </Tooltip>

            ))}

          </div>

        </div>

        

        {/* Sender Style */}

        <div style={{ marginBottom: '16px' }}>

          <label style={{ fontSize: '0.85rem', color: '#888', display: 'block', marginBottom: '8px' }}>Sender Style:</label>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: senderStyle === 'custom' ? '8px' : '0' }}>

            {WHISPER_SENDERS.map(sender => (

              <button

                key={sender.id}

                onClick={() => setSenderStyle(sender.id)}

                style={{

                  ...S.btn,

                  ...S.btnXs,

                  padding: '6px 10px',

                  fontSize: '0.75rem',

                  background: senderStyle === sender.id ? 'rgba(155,89,182,0.2)' : 'transparent',

                  borderColor: senderStyle === sender.id ? '#9b59b6' : '#5c4a2a',

                }}

              >

                {sender.icon} {sender.name}

              </button>

            ))}

          </div>

          {senderStyle === 'custom' && (

            <input 

              style={{ ...S.input, marginBottom: 0, padding: '8px' }}

              placeholder="NPC name (e.g., Mysterious Stranger)"

              value={customSender}

              onChange={e => setCustomSender(e.target.value)}

            />

          )}

        </div>

        

        {/* Urgency & Delivery */}

        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>

          <div style={{ flex: 1 }}>

            <label style={{ fontSize: '0.85rem', color: '#888', display: 'block', marginBottom: '8px' }}>Urgency:</label>

            <div style={{ display: 'flex', gap: '6px' }}>

              <button

                onClick={() => setUrgency('normal')}

                style={{

                  ...S.btn, ...S.btnXs, flex: 1, padding: '8px',

                  background: urgency === 'normal' ? 'rgba(155,89,182,0.2)' : 'transparent',

                  borderColor: urgency === 'normal' ? '#9b59b6' : '#5c4a2a',

                }}

              >Normal</button>

              <button

                onClick={() => setUrgency('urgent')}

                style={{

                  ...S.btn, ...S.btnXs, flex: 1, padding: '8px',

                  background: urgency === 'urgent' ? 'rgba(255,105,97,0.2)' : 'transparent',

                  borderColor: urgency === 'urgent' ? '#ff6961' : '#5c4a2a',

                  color: urgency === 'urgent' ? '#ff6961' : undefined,

                }}

              >🔴 Urgent</button>

            </div>

          </div>

          <div style={{ flex: 1 }}>

            <label style={{ fontSize: '0.85rem', color: '#888', display: 'block', marginBottom: '8px' }}>Delivery:</label>

            <select 

              style={{ ...S.select, marginBottom: 0, padding: '8px' }}

              value={delivery}

              onChange={e => setDelivery(e.target.value)}

            >

              {WHISPER_DELIVERY.map(d => (

                <option key={d.id} value={d.id}>{d.name} - {d.description}</option>

              ))}

            </select>

          </div>

        </div>

        

        {/* Message Content */}

        <div style={{ marginBottom: '16px' }}>

          <label style={{ fontSize: '0.85rem', color: '#888', display: 'block', marginBottom: '8px' }}>Message:</label>

          <textarea 

            style={{ ...S.input, padding: '12px', minHeight: '80px', resize: 'vertical', marginBottom: 0 }}

            placeholder="You alone notice the assassin's blade glinting in the shadows..."

            value={content}

            onChange={e => setContent(e.target.value)}

          />

        </div>

        

        {/* Preview */}

        <div style={{ 

          marginBottom: '16px',

          padding: '16px',

          background: 'rgba(0,0,0,0.4)',

          borderRadius: '8px',

          border: '1px solid rgba(155,89,182,0.3)',

        }}>

          <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '8px' }}>Preview:</div>

          <div style={{ 

            display: 'flex',

            alignItems: 'flex-start',

            gap: '10px',

          }}>

            <span style={{ fontSize: '1.5rem' }}>{senderConfig.icon}</span>

            <div>

              <div style={{ color: '#9b59b6', fontSize: '0.8rem', fontStyle: 'italic', marginBottom: '4px' }}>

                {previewPrefix}

              </div>

              <div style={{ color: '#e8e0d5', fontStyle: 'italic', fontSize: '0.95rem' }}>

                "{content || '...'}"

              </div>

            </div>

          </div>

        </div>

        

        {/* Options */}

        <div style={{ marginBottom: '16px' }}>

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.85rem' }}>

            <input type="checkbox" checked={addToHistory} onChange={e => setAddToHistory(e.target.checked)} />

            <span style={{ color: addToHistory ? '#c9a0dc' : '#888' }}>Save to message history</span>

          </label>

        </div>

        

        {/* Actions */}

        <div style={{ display: 'flex', gap: '12px' }}>

          <button style={{ ...S.btn, flex: 1, padding: '12px' }} onClick={onClose}>

            Cancel

          </button>

          <button 

            style={{ 

              ...S.btn, 

              flex: 2, 

              padding: '12px',

              background: 'linear-gradient(180deg, rgba(155,89,182,0.4) 0%, rgba(155,89,182,0.2) 100%)',

              borderColor: '#9b59b6',

              color: '#c9a0dc',

            }}

            onClick={handleSend}

            disabled={!content.trim() || selectedChars.length === 0}

          >

            🤫 Send Whisper

          </button>

        </div>

      </div>

    </>

  );

};



// Quick Message in DMCard (collapsed by default)

const QuickDMMessage = ({ char, messages, onSaveMessages, onSendStatusMessage, compact = false }) => {

  const [expanded, setExpanded] = useState(false);

  const [content, setContent] = useState('');

  const [urgency, setUrgency] = useState('normal');

  const [addToHistory, setAddToHistory] = useState(false);

  const [displayStyle, setDisplayStyle] = useState('chatwindow'); // Default to new chat window

  const [senderStyle, setSenderStyle] = useState('custom');

  const [customSender, setCustomSender] = useState('');

  const [messageFormat, setMessageFormat] = useState('whisper'); // whisper, says, direct, emote

  const [textColor, setTextColor] = useState('#c9a0dc'); // Default purple whisper color

  const [showAdvanced, setShowAdvanced] = useState(false);

  

  // Preset colors for quick selection

  const colorPresets = [

    { id: 'purple', color: '#c9a0dc', name: 'Purple' },

    { id: 'gold', color: '#ffd700', name: 'Gold' },

    { id: 'red', color: '#ff6961', name: 'Red' },

    { id: 'green', color: '#7dce82', name: 'Green' },

    { id: 'blue', color: '#7db5ff', name: 'Blue' },

    { id: 'orange', color: '#f5a623', name: 'Orange' },

    { id: 'white', color: '#e8e0d5', name: 'White' },

    { id: 'gray', color: '#888888', name: 'Gray' },

  ];

  

  const handleSend = (shouldCollapse = true) => {

    if (!content.trim()) return;

    

    const senderConfig = WHISPER_SENDERS.find(s => s.id === senderStyle) || WHISPER_SENDERS[1];

    

    const newMsg = {

      id: generateId(),

      type: 'whisper',

      category: 'dm-whisper',

      timestamp: new Date().toISOString(),

      content: content.trim(),

      urgency,

      fromDM: true,

      visibility: 'private',

      recipients: [char.id],

      addToHistory,

      dismissed: false,

      // Whisper-specific fields

      displayStyle,

      senderStyle,

      messageFormat,

      textColor, // Custom text color

      customSender: senderStyle === 'custom' ? customSender : senderConfig.prefix,

    };

    

    if (typeof onSendStatusMessage === 'function') {
      onSendStatusMessage({ message: newMsg });
    } else if (typeof onSaveMessages === 'function') {
      onSaveMessages({ ...(messages || {}), statusUpdates: [newMsg, ...((messages || {}).statusUpdates || [])] });
    }

    

    setContent('');

    // Only collapse if not in compact mode

    if (shouldCollapse) {

      setExpanded(false);

    }

  };

  

  // Generate preview text

  const getPreviewText = () => {

    const name = senderStyle === 'custom' ? (customSender || 'Someone') : (WHISPER_SENDERS.find(s => s.id === senderStyle)?.prefix || 'DM');

    const msg = content || '...';

    switch (messageFormat) {

      case 'whisper': return `${name} whispers: ${msg}`;

      case 'says': return `${name} says: ${msg}`;

      case 'direct': return `${name}: ${msg}`;

      case 'emote': return `*${name}* ${msg}`;

      default: return `${name} whispers: ${msg}`;

    }

  };

  

  // Compact mode - simpler inline display, stays expanded after sending

  if (compact) {

    return (

      <div style={{ marginTop: '4px' }}>

        <div 

          style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.7rem', opacity: 0.8 }}

          onClick={() => setExpanded(!expanded)}

        >

          <span>📬 Message</span>

          <span>{expanded ? '▲' : '▼'}</span>

        </div>

        {expanded && (

          <div style={{ marginTop: '4px' }}>

            {/* Sender Name Row */}

            <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>

              <input 

                style={{ ...S.input, flex: 1, marginBottom: 0, padding: '4px 6px', fontSize: '0.7rem' }}

                placeholder="Sender name (e.g. Nzoth, DM, ?)"

                value={customSender}

                onChange={e => setCustomSender(e.target.value)}

              />

              <select 

                style={{ ...S.select, padding: '2px 4px', fontSize: '0.65rem', marginBottom: 0, width: '75px' }} 

                value={messageFormat} 

                onChange={e => setMessageFormat(e.target.value)}

              >

                {MESSAGE_FORMATS.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}

              </select>

            </div>

            {/* Message Content Row */}

            <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>

              <input 

                style={{ ...S.input, flex: 1, marginBottom: 0, padding: '4px 6px', fontSize: '0.7rem' }}

                placeholder="Message content..."

                value={content}

                onChange={e => setContent(e.target.value)}

                onKeyPress={e => e.key === 'Enter' && handleSend(false)}

              />

              <button style={{ ...S.btn, ...S.btnSuccess, ...S.btnXs, padding: '4px 8px', fontSize: '0.65rem' }} onClick={() => handleSend(false)}>➤</button>

            </div>

            {/* Preview */}

            <div style={{ 

              fontSize: '0.65rem', 

              color: textColor, 

              padding: '3px 6px', 

              background: 'rgba(0,0,0,0.3)', 

              borderRadius: '3px',

              marginBottom: '4px',

            }}>

              {getPreviewText()}

            </div>

            {/* Color Selection Row - below preview */}

            <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>

              {colorPresets.map(c => (

                <button

                  key={c.id}

                  onClick={() => setTextColor(c.color)}

                  title={c.name}

                  style={{

                    width: '12px',

                    height: '12px',

                    borderRadius: '2px',

                    background: c.color,

                    border: textColor === c.color ? '1px solid #fff' : '1px solid rgba(0,0,0,0.3)',

                    cursor: 'pointer',

                    padding: 0,

                  }}

                />

              ))}

              <input 

                type="color" 

                value={textColor} 

                onChange={e => setTextColor(e.target.value)}

                style={{ width: '14px', height: '12px', padding: 0, border: 'none', cursor: 'pointer', marginLeft: '2px' }}

                title="Custom color"

              />

            </div>

          </div>

        )}

      </div>

    );

  }

  

  return (

    <div style={{ ...S.subCard, marginTop: '8px' }}>

      <div 

        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}

        onClick={() => setExpanded(!expanded)}

      >

        <h4 style={{ color: '#4a90d9', margin: 0, fontSize: '0.85rem' }}>📬 Message to {char.name}</h4>

        <span style={{ opacity: 0.6 }}>{expanded ? '▲' : '▼'}</span>

      </div>

      

      {expanded && (

        <div style={{ marginTop: '8px' }}>

          {/* Sender Name & Format Row */}

          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>

            <div style={{ flex: 2 }}>

              <label style={{ fontSize: '0.7rem', color: '#888', display: 'block', marginBottom: '2px' }}>Sender Name</label>

              <input 

                style={{ ...S.input, marginBottom: 0, padding: '6px', fontSize: '0.85rem' }}

                placeholder="Nzoth, DM, ?, Mysterious Voice..."

                value={customSender}

                onChange={e => setCustomSender(e.target.value)}

              />

            </div>

            <div style={{ flex: 1 }}>

              <label style={{ fontSize: '0.7rem', color: '#888', display: 'block', marginBottom: '2px' }}>Format</label>

              <select 

                style={{ ...S.select, marginBottom: 0, padding: '6px', fontSize: '0.85rem' }} 

                value={messageFormat} 

                onChange={e => setMessageFormat(e.target.value)}

              >

                {MESSAGE_FORMATS.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}

              </select>

            </div>

          </div>

          

          {/* Message Content */}

          <textarea 

            style={{ ...S.input, padding: '8px', fontSize: '0.85rem', minHeight: '60px', resize: 'vertical' }}

            placeholder="Message content..."

            value={content}

            onChange={e => setContent(e.target.value)}

          />

          

          {/* Preview */}

          <div style={{ 

            padding: '8px 12px', 

            background: 'rgba(0,0,0,0.3)', 

            borderRadius: '6px', 

            marginBottom: '8px',

            borderLeft: `3px solid ${textColor}`,

            fontSize: '0.85rem',

          }}>

            <div style={{ color: '#888', fontSize: '0.7rem', marginBottom: '4px' }}>Preview:</div>

            <div style={{ color: textColor }}>

              {getPreviewText()}

            </div>

          </div>

          

          {/* Text Color Selection - below preview */}

          <div style={{ marginBottom: '8px' }}>

            <label style={{ fontSize: '0.7rem', color: '#888', display: 'block', marginBottom: '4px' }}>Text Color</label>

            <div style={{ display: 'flex', gap: '3px', alignItems: 'center', flexWrap: 'wrap' }}>

              {colorPresets.map(c => (

                <button

                  key={c.id}

                  onClick={() => setTextColor(c.color)}

                  title={c.name}

                  style={{

                    width: '18px',

                    height: '18px',

                    borderRadius: '3px',

                    background: c.color,

                    border: textColor === c.color ? '2px solid #fff' : '1px solid rgba(0,0,0,0.3)',

                    cursor: 'pointer',

                    padding: 0,

                    boxShadow: textColor === c.color ? '0 0 4px rgba(255,255,255,0.3)' : 'none',

                  }}

                />

              ))}

              <input 

                type="color" 

                value={textColor} 

                onChange={e => setTextColor(e.target.value)}

                style={{ width: '20px', height: '18px', padding: 0, border: 'none', cursor: 'pointer', marginLeft: '4px', borderRadius: '3px' }}

                title="Custom color"

              />

            </div>

          </div>

          

          {/* Display Style */}

          <div style={{ marginBottom: '8px' }}>

            <label style={{ fontSize: '0.7rem', color: '#888', display: 'block', marginBottom: '4px' }}>Display Style</label>

            <div style={{ display: 'flex', gap: '4px' }}>

              <Tooltip text="Message appears in collapsible chat window">

                <button 

                  style={{ 

                    ...S.btn, ...S.btnXs, flex: 1, padding: '6px', marginBottom: 0,

                    background: displayStyle === 'chatwindow' ? 'rgba(74,144,217,0.3)' : 'transparent',

                    borderColor: displayStyle === 'chatwindow' ? '#4a90d9' : '#5c4a2a',

                  }}

                  onClick={() => setDisplayStyle('chatwindow')}

                >💬 Chat Window</button>

              </Tooltip>

              <Tooltip text="Dramatic centered overlay with blur">

                <button 

                  style={{ 

                    ...S.btn, ...S.btnXs, flex: 1, padding: '6px', marginBottom: 0,

                    background: displayStyle === 'overlay' ? 'rgba(155,89,182,0.3)' : 'transparent',

                    borderColor: displayStyle === 'overlay' ? '#9b59b6' : '#5c4a2a',

                  }}

                  onClick={() => setDisplayStyle('overlay')}

                >🎭 Overlay</button>

              </Tooltip>

            </div>

          </div>

          

          {/* Options Row */}

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>

            <select style={{ ...S.select, width: 'auto', marginBottom: 0, padding: '4px 8px', fontSize: '0.8rem' }} value={urgency} onChange={e => setUrgency(e.target.value)}>

              <option value="normal">Normal</option>

              <option value="urgent">🔴 Urgent</option>

            </select>

            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>

              <input type="checkbox" checked={addToHistory} onChange={e => setAddToHistory(e.target.checked)} />

              Log to history

            </label>

            <button style={{ ...S.btn, ...S.btnSuccess, ...S.btnSm, marginLeft: 'auto' }} onClick={() => handleSend(true)}>

              Send Whisper

            </button>

          </div>

        </div>

      )}

    </div>

  );

};



// Chat window style presets

const CHAT_WINDOW_STYLES = [

  { 

    id: 'transparent', 

    name: 'Transparent', 

    icon: '👻',

    description: 'Minimal transparent background',

    background: 'rgba(0,0,0,0.4)',

    headerBg: 'transparent',

    border: 'none',

    showHeader: false,

  },

  { 

    id: 'panel', 

    name: 'Panel', 

    icon: '📋',

    description: 'Dark panel with header',

    background: 'rgba(10,8,6,0.9)',

    headerBg: 'rgba(0,0,0,0.5)',

    border: '1px solid rgba(201,162,39,0.4)',

    showHeader: true,

  },

  { 

    id: 'classic', 

    name: 'Classic', 

    icon: '🎮',

    description: 'WoW-style chat frame',

    background: 'linear-gradient(180deg, rgba(20,15,10,0.85) 0%, rgba(10,8,6,0.9) 100%)',

    headerBg: 'linear-gradient(180deg, rgba(40,30,20,0.9) 0%, rgba(20,15,10,0.9) 100%)',

    border: '1px solid rgba(100,80,50,0.6)',

    showHeader: true,

  },

  { 

    id: 'minimal', 

    name: 'Minimal', 

    icon: '✨',

    description: 'Clean minimal look',

    background: 'rgba(0,0,0,0.25)',

    headerBg: 'transparent',

    border: 'none',

    showHeader: false,

  },

  { 

    id: 'parchment', 

    name: 'Parchment', 

    icon: '📜',

    description: 'Fantasy parchment style',

    background: 'linear-gradient(135deg, rgba(45,35,25,0.92) 0%, rgba(35,28,20,0.95) 100%)',

    headerBg: 'linear-gradient(90deg, rgba(60,45,30,0.9) 0%, rgba(45,35,25,0.9) 100%)',

    border: '2px solid rgba(139,90,43,0.6)',

    showHeader: true,

  },

  { 

    id: 'dark', 

    name: 'Dark', 

    icon: '🌑',

    description: 'Solid dark background',

    background: 'rgba(15,12,10,0.95)',

    headerBg: 'rgba(25,20,15,0.95)',

    border: '1px solid rgba(60,50,40,0.5)',

    showHeader: true,

  },

];



// WhisperChatWindow - WoW-style collapsible chat window in bottom right

const WhisperChatWindow = ({ whispers, onDismiss, charId, autoExpand = true, windowStyle = 'transparent' }) => {

  const [isExpanded, setIsExpanded] = useState(false);

  const [hasNewMessage, setHasNewMessage] = useState(false);

  const [lastSeenCount, setLastSeenCount] = useState(0);

  const [currentStyle, setCurrentStyle] = useState(windowStyle);

  const [showStylePicker, setShowStylePicker] = useState(false);

  const scrollRef = useRef(null);

  

  const styleConfig = CHAT_WINDOW_STYLES.find(s => s.id === currentStyle) || CHAT_WINDOW_STYLES[0];

  

  // Filter whispers for this character

  const myWhispers = (whispers || []).filter(w => 

    (w.type === 'whisper' || w.category === 'dm-whisper' || w.category === 'dm-status') && 

    (w.recipients?.includes(charId) || w.visibility === 'public') &&

    !w.dismissed

  ).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  

  // Auto-expand when new messages arrive

  useEffect(() => {

    if (myWhispers.length > lastSeenCount && autoExpand) {

      setIsExpanded(true);

      setHasNewMessage(true);

      // Scroll to bottom

      setTimeout(() => {

        if (scrollRef.current) {

          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;

        }

      }, 100);

    }

    if (myWhispers.length !== lastSeenCount) {

      setLastSeenCount(myWhispers.length);

    }

  }, [myWhispers.length, lastSeenCount, autoExpand]);

  

  // Clear new message indicator when expanded

  useEffect(() => {

    if (isExpanded) {

      setHasNewMessage(false);

    }

  }, [isExpanded]);

  

  // Format message based on sender style and format

  const formatMessage = (msg) => {

    const senderConfig = WHISPER_SENDERS.find(s => s.id === msg.senderStyle) || WHISPER_SENDERS[0];

    const senderName = msg.customSender || senderConfig.prefix || 'Unknown';

    const format = msg.messageFormat || 'whisper';

    // Use custom textColor if provided, otherwise fall back to sender config color

    const color = msg.textColor || senderConfig.color || '#c9a0dc';

    

    let prefix = '';

    switch (format) {

      case 'whisper':

        prefix = `[${senderName}] whispers:`;

        break;

      case 'says':

        prefix = `[${senderName}] says:`;

        break;

      case 'direct':

        prefix = `[${senderName}]:`;

        break;

      case 'emote':

        return { prefix: `*${senderName}*`, content: msg.content, color, isEmote: true };

      default:

        prefix = `[${senderName}] whispers:`;

    }

    

    return { prefix, content: msg.content, color, isEmote: false };

  };

  

  if (myWhispers.length === 0 && !isExpanded) return null;

  

  return (

    <div style={{

      position: 'fixed',

      bottom: '12px',

      right: '12px',

      zIndex: 9000,

      fontFamily: 'system-ui, -apple-system, sans-serif',

    }}>

      <style>{`

        @keyframes slideIn {

          from { opacity: 0; transform: translateX(20px); }

          to { opacity: 1; transform: translateX(0); }

        }

        @keyframes pulse {

          0%, 100% { opacity: 1; }

          50% { opacity: 0.6; }

        }

        @keyframes newMsg {

          from { background: rgba(201,162,39,0.2); }

          to { background: transparent; }

        }

        .whisper-scroll::-webkit-scrollbar { width: 4px; }

        .whisper-scroll::-webkit-scrollbar-track { background: transparent; }

        .whisper-scroll::-webkit-scrollbar-thumb { background: rgba(201,162,39,0.3); border-radius: 2px; }

      `}</style>

      

      {/* Collapsed state - vertical bar */}

      {!isExpanded && (

        <div 

          onClick={() => setIsExpanded(true)}

          style={{

            display: 'flex',

            flexDirection: 'column',

            alignItems: 'center',

            gap: '4px',

            padding: '8px 4px',

            background: 'rgba(0,0,0,0.6)',

            border: '1px solid rgba(201,162,39,0.3)',

            borderRadius: '3px',

            cursor: 'pointer',

            writingMode: 'vertical-rl',

            textOrientation: 'mixed',

          }}

        >

          <span style={{ color: '#ffd700', fontSize: '0.9rem', writingMode: 'horizontal-tb' }}>💬</span>

          {myWhispers.length > 0 && (

            <span style={{ 

              background: hasNewMessage ? '#ff6961' : 'rgba(201,162,39,0.6)', 

              color: '#fff', 

              padding: '2px 4px', 

              borderRadius: '6px', 

              fontSize: '0.55rem',

              fontWeight: 'bold',

              animation: hasNewMessage ? 'pulse 1s infinite' : 'none',

              writingMode: 'horizontal-tb',

            }}>

              {myWhispers.length}

            </span>

          )}

          <span style={{ color: '#888', fontSize: '0.6rem', writingMode: 'horizontal-tb' }}>▲</span>

        </div>

      )}

      

      {/* Expanded state - chat window */}

      {isExpanded && (

        <div style={{

          width: '340px',

          maxHeight: '250px',

          background: styleConfig.background,

          backdropFilter: 'blur(4px)',

          border: styleConfig.border,

          borderRadius: '4px',

          overflow: 'hidden',

          boxShadow: styleConfig.id === 'transparent' ? 'none' : '0 2px 12px rgba(0,0,0,0.4)',

          animation: 'slideIn 0.15s ease',

        }}>

          

          {/* Header (optional based on style) */}

          {styleConfig.showHeader && (

            <div style={{

              display: 'flex',

              justifyContent: 'space-between',

              alignItems: 'center',

              padding: '4px 8px',

              background: styleConfig.headerBg,

              borderBottom: '1px solid rgba(100,80,50,0.4)',

            }}>

              <span style={{ color: '#c9a227', fontSize: '0.7rem', fontWeight: 'bold' }}>Messages</span>

              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>

                <button

                  onClick={() => setShowStylePicker(!showStylePicker)}

                  style={{

                    background: 'transparent',

                    border: 'none',

                    color: '#888',

                    cursor: 'pointer',

                    fontSize: '0.6rem',

                    padding: '2px 4px',

                  }}

                  title="Change style"

                >⚙️</button>

                {myWhispers.length > 0 && (

                  <button

                    onClick={() => myWhispers.forEach(w => onDismiss(w.id))}

                    style={{

                      background: 'transparent',

                      border: 'none',

                      color: '#888',

                      cursor: 'pointer',

                      fontSize: '0.6rem',

                      padding: '2px 4px',

                    }}

                  >Clear</button>

                )}

                <button

                  onClick={() => setIsExpanded(false)}

                  style={{

                    background: 'transparent',

                    border: 'none',

                    color: '#c9a227',

                    cursor: 'pointer',

                    fontSize: '0.8rem',

                    padding: '0 2px',

                  }}

                >▶</button>

              </div>

            </div>

          )}

          

          {/* Style Picker Dropdown */}

          {showStylePicker && (

            <div style={{

              position: 'absolute',

              top: styleConfig.showHeader ? '28px' : '4px',

              right: '4px',

              background: 'rgba(20,15,10,0.98)',

              border: '1px solid rgba(201,162,39,0.5)',

              borderRadius: '4px',

              padding: '4px',

              zIndex: 10,

              display: 'flex',

              flexDirection: 'column',

              gap: '2px',

            }}>

              {CHAT_WINDOW_STYLES.map(style => (

                <button

                  key={style.id}

                  onClick={() => { setCurrentStyle(style.id); setShowStylePicker(false); }}

                  style={{

                    background: currentStyle === style.id ? 'rgba(201,162,39,0.3)' : 'transparent',

                    border: 'none',

                    color: currentStyle === style.id ? '#ffd700' : '#ccc',

                    cursor: 'pointer',

                    fontSize: '0.65rem',

                    padding: '4px 8px',

                    textAlign: 'left',

                    borderRadius: '2px',

                    display: 'flex',

                    alignItems: 'center',

                    gap: '6px',

                  }}

                  title={style.description}

                >

                  <span>{style.icon}</span>

                  <span>{style.name}</span>

                </button>

              ))}

            </div>

          )}

          

          {/* Collapse button for transparent style (no header) */}

          {!styleConfig.showHeader && (

            <div style={{ 

              position: 'absolute', 

              top: '4px', 

              right: '6px', 

              zIndex: 1,

              display: 'flex',

              gap: '4px',

            }}>

              <button

                onClick={() => setShowStylePicker(!showStylePicker)}

                style={{

                  background: 'rgba(0,0,0,0.5)',

                  border: 'none',

                  color: '#888',

                  cursor: 'pointer',

                  fontSize: '0.55rem',

                  padding: '2px 4px',

                  borderRadius: '2px',

                }}

                title="Change style"

              >⚙️</button>

              {myWhispers.length > 0 && (

                <button

                  onClick={() => myWhispers.forEach(w => onDismiss(w.id))}

                  style={{

                    background: 'rgba(0,0,0,0.5)',

                    border: 'none',

                    color: '#888',

                    cursor: 'pointer',

                    fontSize: '0.55rem',

                    padding: '2px 4px',

                    borderRadius: '2px',

                  }}

                >Clear</button>

              )}

              <button

                onClick={() => setIsExpanded(false)}

                style={{

                  background: 'rgba(0,0,0,0.5)',

                  border: 'none',

                  color: '#c9a227',

                  cursor: 'pointer',

                  fontSize: '0.7rem',

                  padding: '0 4px',

                  borderRadius: '2px',

                }}

              >▶</button>

            </div>

          )}

          

          {/* Messages area */}

          <div 

            ref={scrollRef}

            className="whisper-scroll"

            style={{

              maxHeight: styleConfig.showHeader ? '210px' : '230px',

              overflowY: 'auto',

              padding: '6px 8px',

              position: 'relative',

            }}

          >

            {myWhispers.length === 0 ? (

              <div style={{ 

                textAlign: 'center', 

                color: '#666', 

                fontSize: '0.7rem', 

                padding: '16px',

                fontStyle: 'italic',

              }}>

                No messages...

              </div>

            ) : (

              myWhispers.map((msg, idx) => {

                const formatted = formatMessage(msg);

                const isUrgent = msg.urgency === 'urgent';

                const isNewest = idx === myWhispers.length - 1;

                

                return (

                  <div 

                    key={msg.id}

                    style={{

                      marginBottom: '2px',

                      padding: '1px 0',

                      animation: isNewest && hasNewMessage ? 'newMsg 1s ease' : 'none',

                    }}

                  >

                    {formatted.isEmote ? (

                      <div style={{ fontSize: '0.8rem', lineHeight: 1.3 }}>

                        <span style={{ color: formatted.color, fontStyle: 'italic' }}>{formatted.prefix}</span>

                        <span style={{ color: formatted.color, fontStyle: 'italic' }}> {formatted.content}</span>

                      </div>

                    ) : (

                      <div style={{ fontSize: '0.8rem', lineHeight: 1.3 }}>

                        <span style={{ color: formatted.color }}>{formatted.prefix}</span>

                        <span style={{ color: formatted.color }}> {formatted.content}</span>

                      </div>

                    )}

                  </div>

                );

              })

            )}

          </div>

        </div>

      )}

    </div>

  );

};



// WhisperDisplay - Main component that renders the chat window (keeping old name for compatibility)

const WhisperDisplay = ({ whispers, onDismiss, charId }) => {

  // Filter whispers for this character that haven't been dismissed

  const activeWhispers = (whispers || []).filter(w => 

    w.type === 'whisper' && 

    w.recipients?.includes(charId) && 

    !w.dismissed

  );

  

  // Check for overlay-style whispers (these still use the old centered modal)

  const overlayWhispers = activeWhispers.filter(w => w.displayStyle === 'overlay');

  

  return (

    <>

      {/* Main Chat Window */}

      <WhisperChatWindow 

        whispers={whispers} 

        onDismiss={onDismiss} 

        charId={charId}

        autoExpand={true}

      />

      

      {/* Overlay-style whispers - Centered modal (for dramatic moments) */}

      {overlayWhispers.length > 0 && (

        <WhisperOverlay whisper={overlayWhispers[0]} onDismiss={onDismiss} />

      )}

    </>

  );

};



// Overlay-style whisper - Centered semi-transparent modal (kept for dramatic moments)

const WhisperOverlay = ({ whisper, onDismiss }) => {

  const [isVisible, setIsVisible] = useState(false);

  

  useEffect(() => {

    setTimeout(() => setIsVisible(true), 50);

  }, []);

  

  const handleDismiss = () => {

    setIsVisible(false);

    setTimeout(() => onDismiss(whisper.id), 300);

  };

  

  const senderConfig = WHISPER_SENDERS.find(s => s.id === whisper.senderStyle) || WHISPER_SENDERS[0];

  const senderName = whisper.customSender || senderConfig.prefix || 'Unknown';

  const format = whisper.messageFormat || 'whisper';

  

  let displayPrefix = '';

  switch (format) {

    case 'whisper': displayPrefix = `${senderName} whispers...`; break;

    case 'says': displayPrefix = `${senderName} says...`; break;

    case 'direct': displayPrefix = `${senderName}:`; break;

    case 'emote': displayPrefix = `*${senderName}*`; break;

    default: displayPrefix = `${senderName} whispers...`;

  }

  

  return (

    <>

      {/* Backdrop */}

      <div 

        onClick={handleDismiss}

        style={{

          position: 'fixed',

          top: 0,

          left: 0,

          right: 0,

          bottom: 0,

          background: 'rgba(0,0,0,0.6)',

          backdropFilter: 'blur(4px)',

          zIndex: 9998,

          opacity: isVisible ? 1 : 0,

          transition: 'opacity 0.3s ease',

        }}

      />

      

      {/* Whisper Card */}

      <div style={{

        position: 'fixed',

        top: '50%',

        left: '50%',

        transform: isVisible ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0.9)',

        zIndex: 9999,

        background: 'linear-gradient(135deg, rgba(30,25,20,0.98) 0%, rgba(20,15,10,0.98) 100%)',

        border: whisper.urgency === 'urgent' ? '2px solid #ff6961' : '2px solid rgba(155,89,182,0.6)',

        borderRadius: '12px',

        padding: '24px 32px',

        maxWidth: '450px',

        minWidth: '300px',

        boxShadow: '0 8px 40px rgba(0,0,0,0.6)',

        opacity: isVisible ? 1 : 0,

        transition: 'all 0.3s ease',

        textAlign: 'center',

      }}>

        {/* Icon */}

        <div style={{ fontSize: '2rem', marginBottom: '12px', opacity: 0.8 }}>

          {senderConfig.icon || '🤫'}

        </div>

        

        {/* Sender */}

        <div style={{ 

          color: senderConfig.color || '#9b59b6', 

          fontSize: '0.85rem', 

          marginBottom: '16px',

          fontStyle: format === 'emote' ? 'italic' : 'normal',

        }}>

          {displayPrefix}

        </div>

        

        {/* Content */}

        <div style={{ 

          color: '#e8e0d5', 

          fontSize: '1.1rem',

          fontStyle: 'italic',

          lineHeight: 1.5,

          marginBottom: '20px',

          padding: '0 8px',

        }}>

          "{whisper.content}"

        </div>

        

        {/* Acknowledge Button */}

        <button 

          onClick={handleDismiss}

          style={{

            ...S.btn,

            padding: '10px 24px',

            fontSize: '0.9rem',

            background: 'linear-gradient(180deg, rgba(155,89,182,0.3) 0%, rgba(155,89,182,0.1) 100%)',

            borderColor: 'rgba(155,89,182,0.5)',

            color: '#c9a0dc',

          }}

        >

          Acknowledge

        </button>

      </div>

    </>

  );

};



// ==================== END MESSAGING SYSTEM ====================



// 2026-05-10: Step-outline group used by the 'step' grouped layout in
// the Status panel. Renders an SVG path that traces the chip cluster's
// silhouette so the outline "steps down" where rows end mid-line —
// hugging the chip shape rather than wrapping them in a rectangular
// box. Measures chip positions via a ResizeObserver + getBoundingClientRect
// so the path always matches actual layout. Pure CSS borders are
// rectangular and can't express this shape; SVG is the practical option.
//
// 2026-05-10b: When `puzzleFloat=true`, ALSO computes a `shape-outside`
// polygon matching the SVG path and applies `float:left` to the
// container. This makes subsequent puzzle-floated groups flow INTO the
// negative space carved by this group's step (e.g. Innate group fits
// to the right of Equipment's lone "Invisible" chip on row 2 instead
// of wrapping below the whole Equipment block). True masonry-style
// packing without a custom layout algorithm — CSS does the wrap-around.
function StatusStepOutlineGroup({ category, items, kind, renderBadge, onContainerClick, puzzleFloat = false }) {
  const containerRef = useRef(null);
  const [pathState, setPathState] = useState({ d: '', width: 0, height: 0, polygon: '' });

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || items.length === 0) {
      setPathState({ d: '', width: 0, height: 0 });
      return;
    }
    const compute = () => {
      // Query chips. Wrappers below carry `data-step-chip` so we can
      // exclude the SVG and the floating label from this measurement.
      const chips = el.querySelectorAll('[data-step-chip]');
      if (chips.length === 0) {
        setPathState({ d: '', width: 0, height: 0 });
        return;
      }
      const containerRect = el.getBoundingClientRect();

      // Group chips into rows by `top` coordinate (with a 3px tolerance
      // to absorb sub-pixel rounding). Each row tracks the leftmost and
      // rightmost extents since chips can vary in width within a row.
      const rows = [];
      const ROW_TOLERANCE = 3;
      chips.forEach((chipEl) => {
        const r = chipEl.getBoundingClientRect();
        const top = r.top - containerRect.top;
        const bottom = r.bottom - containerRect.top;
        const left = r.left - containerRect.left;
        const right = r.right - containerRect.left;
        const existing = rows.find(row => Math.abs(row.top - top) < ROW_TOLERANCE);
        if (existing) {
          existing.left = Math.min(existing.left, left);
          existing.right = Math.max(existing.right, right);
          existing.top = Math.min(existing.top, top);
          existing.bottom = Math.max(existing.bottom, bottom);
        } else {
          rows.push({ top, bottom, left, right });
        }
      });
      rows.sort((a, b) => a.top - b.top);
      if (rows.length === 0) return;

      // Padding around the chip cluster. Top padding extra so the
      // floating category label has room.
      const PAD_X = 5;
      const PAD_TOP = 10;
      const PAD_BOTTOM = 5;

      // Trace the outline clockwise starting from top-left of row 0.
      // Right side: top of row 0 → bottom of row 0 → horizontal jog to
      // row 1's right → bottom of row 1 → ... → bottom of last row.
      // Left side: bottom-left of last row → top-left of row 0.
      // Each "jog" between rows is a horizontal segment at the boundary
      // y-coordinate, creating the step shape.
      const pts = [];
      pts.push([rows[0].left - PAD_X, rows[0].top - PAD_TOP]);            // top-left
      pts.push([rows[0].right + PAD_X, rows[0].top - PAD_TOP]);           // top-right
      pts.push([rows[0].right + PAD_X, rows[0].bottom + PAD_BOTTOM]);     // down to bottom of row 0
      for (let i = 1; i < rows.length; i++) {
        const yJog = rows[i - 1].bottom + PAD_BOTTOM;
        pts.push([rows[i].right + PAD_X, yJog]);                          // horizontal jog at boundary
        pts.push([rows[i].right + PAD_X, rows[i].bottom + PAD_BOTTOM]);   // down to bottom of row i
      }
      const last = rows[rows.length - 1];
      pts.push([last.left - PAD_X, last.bottom + PAD_BOTTOM]);            // bottom-left of last row
      // Left side back up. For chips that all align at the same left
      // edge (typical case), this collapses to a single vertical line.
      // For staggered left edges it would step the same way.
      for (let i = rows.length - 1; i >= 1; i--) {
        pts.push([rows[i].left - PAD_X, rows[i].top - PAD_TOP]);
        pts.push([rows[i - 1].left - PAD_X, rows[i].top - PAD_TOP]);
      }
      // Path closes back to start automatically with Z.

      const d = `M ${pts.map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' L ')} Z`;
      // CSS shape-outside polygon: same vertices, but coordinates clamped
      // to non-negative (CSS spec) and expressed as `Xpx Ypx`. Used only
      // when puzzleFloat is true. Matches the SVG path so the visual
      // outline and the CSS layout boundary align exactly.
      const polygon = `polygon(${pts.map(p => `${Math.max(0, p[0]).toFixed(0)}px ${Math.max(0, p[1]).toFixed(0)}px`).join(', ')})`;
      setPathState({ d, width: containerRect.width, height: containerRect.height, polygon });
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    // Also recompute on font load / late layout — observe document body
    // so any reflow propagates. Cheap enough for this small panel.
    const bodyRo = new ResizeObserver(compute);
    if (typeof document !== 'undefined' && document.body) bodyRo.observe(document.body);
    return () => { ro.disconnect(); bodyRo.disconnect(); };
  }, [items.length, category.id, kind]);

  return (
    <div
      ref={containerRef}
      onClick={onContainerClick}
      style={{
        position: 'relative',
        display: puzzleFloat ? 'block' : 'inline-flex',
        flexWrap: puzzleFloat ? undefined : 'wrap',
        alignItems: puzzleFloat ? undefined : 'flex-start',
        gap: 3,
        padding: '12px 6px 6px 6px',
        // Negative margin so adjacent step outlines OVERLAP by 1px at
        // the seam — strokes merge into a single shared edge instead of
        // doubling. Chips have 6px padding so they never overlap; only
        // SVG strokes share the boundary.
        marginRight: -1,
        marginBottom: -1,
        maxWidth: '100%',
        // 2026-05-10b: Puzzle-piece packing. Float left so subsequent
        // siblings flow alongside; shape-outside makes them flow around
        // the actual chip silhouette (not the rectangular bounding box).
        // shape-margin adds a small breathing-buffer in the carved area.
        ...(puzzleFloat ? {
          float: 'left',
          shapeOutside: pathState.polygon || undefined,
          shapeMargin: 2,
        } : {}),
      }}
    >
      <svg
        aria-hidden="true"
        width={pathState.width || 1}
        height={pathState.height || 1}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}
      >
        {pathState.d && (
          <path d={pathState.d} fill={`${category.color}08`} stroke={`${category.color}66`} strokeWidth={1} />
        )}
      </svg>
      <span
        style={{
          position: 'absolute',
          top: -1,
          left: 9,
          padding: '0 5px',
          background: 'var(--bg, rgba(14,10,15,1))',
          fontSize: '0.55rem',
          color: category.color,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
          lineHeight: 1,
          whiteSpace: 'nowrap',
          zIndex: 2,
        }}
      >
        <span aria-hidden="true">{category.icon}</span> {category.label}
      </span>
      {items.map((effect) => (
        <span key={effect.effectId} data-step-chip style={{ display: 'inline-flex', flexShrink: 0 }}>
          {renderBadge(effect, kind)}
        </span>
      ))}
    </div>
  );
}

// 2026-05-10c: Puzzle-flow renderer for the 'step' Status view.
// Rather than render each category as its own bounded container that
// tries to pack against neighbors, this puts ALL chips in a single
// flex-wrap container (sorted by category, with thin spacers between
// categories for visible separation), measures each chip's actual
// position post-layout, then draws an SVG outline per category that
// traces around its chips wherever they happened to fall. Chips that
// wrap mid-category get an L-shaped outline; chips that share rows
// with the next category just get a tighter step outline. Outlines are
// drawn from the same getBoundingClientRect data as the existing Step
// component so the path/style stays consistent.
function StatusPuzzleFlowView({ chips, kind, renderBadge, categoriesById, getCategoryFor, onContainerClick }) {
  const containerRef = useRef(null);
  const [overlay, setOverlay] = useState({ width: 0, height: 0, items: [] });

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || chips.length === 0) {
      setOverlay({ width: 0, height: 0, items: [] });
      return;
    }
    const compute = () => {
      const containerRect = el.getBoundingClientRect();
      const chipEls = el.querySelectorAll('[data-puzzle-chip]');
      // Build per-category row data. Each row tracks left/right/top/bottom
      // bounds of that category's chips on that row. Row equality uses a
      // 3px tolerance for sub-pixel rounding.
      const rowsByCat = new Map();
      const ROW_TOL = 3;
      chipEls.forEach((chipEl) => {
        const catId = chipEl.getAttribute('data-puzzle-cat');
        if (!catId) return;
        const r = chipEl.getBoundingClientRect();
        const top = r.top - containerRect.top;
        const bottom = r.bottom - containerRect.top;
        const left = r.left - containerRect.left;
        const right = r.right - containerRect.left;
        if (!rowsByCat.has(catId)) rowsByCat.set(catId, []);
        const rows = rowsByCat.get(catId);
        let row = rows.find((rw) => Math.abs(rw.top - top) < ROW_TOL);
        if (row) {
          row.left = Math.min(row.left, left);
          row.right = Math.max(row.right, right);
          row.bottom = Math.max(row.bottom, bottom);
        } else {
          rows.push({ top, bottom, left, right });
        }
      });

      // Per-category, sort rows top→bottom. Then split rows into
      // SEGMENTS based on left-edge continuity: when row N+1's left
      // is significantly different from row N's left (>30px), they
      // belong to different "chunks" and can't be connected by a
      // single step polygon without drawing horizontal line segments
      // through empty space (the wonky behavior the user reported
      // when adding chips that wrapped Innate to a new row starting
      // at x=0 while its first row started at x≈150 after a spacer).
      // Each segment becomes its own step polygon. Same color, same
      // category — only the topmost segment carries the label.
      const PAD_X = 7;
      const PAD_TOP = 10;
      const PAD_BOTTOM = 5;
      const LEFT_TOLERANCE = 30; // px difference that breaks continuity
      const items = [];
      rowsByCat.forEach((rows, catId) => {
        if (rows.length === 0) return;
        rows.sort((a, b) => a.top - b.top);

        // Walk rows, breaking into segments when continuity breaks.
        const segments = [];
        rows.forEach((row) => {
          if (segments.length === 0) {
            segments.push([row]);
            return;
          }
          const lastSeg = segments[segments.length - 1];
          const prevRow = lastSeg[lastSeg.length - 1];
          if (Math.abs(row.left - prevRow.left) > LEFT_TOLERANCE) {
            segments.push([row]);
          } else {
            lastSeg.push(row);
          }
        });

        segments.forEach((segRows, segIdx) => {
          const pts = [];
          pts.push([segRows[0].left - PAD_X, segRows[0].top - PAD_TOP]);
          pts.push([segRows[0].right + PAD_X, segRows[0].top - PAD_TOP]);
          pts.push([segRows[0].right + PAD_X, segRows[0].bottom + PAD_BOTTOM]);
          for (let i = 1; i < segRows.length; i++) {
            const yJog = segRows[i - 1].bottom + PAD_BOTTOM;
            pts.push([segRows[i].right + PAD_X, yJog]);
            pts.push([segRows[i].right + PAD_X, segRows[i].bottom + PAD_BOTTOM]);
          }
          const last = segRows[segRows.length - 1];
          pts.push([last.left - PAD_X, last.bottom + PAD_BOTTOM]);
          for (let i = segRows.length - 1; i >= 1; i--) {
            pts.push([segRows[i].left - PAD_X, segRows[i].top - PAD_TOP]);
            pts.push([segRows[i - 1].left - PAD_X, segRows[i].top - PAD_TOP]);
          }
          const d = `M ${pts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' L ')} Z`;
          items.push({
            id: `${catId}-${segIdx}`,
            catId,
            d,
            // Label only on the first (topmost) segment of each
            // category. Subsequent segments share the color but no
            // duplicate label — visual continuity through color, not
            // repeated text.
            labelTop: segIdx === 0 ? segRows[0].top - PAD_TOP - 5 : null,
            labelLeft: segIdx === 0 ? segRows[0].left - PAD_X + 4 : null,
          });
        });
      });

      setOverlay({ width: containerRect.width, height: containerRect.height, items });
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    const bodyRo = new ResizeObserver(compute);
    if (typeof document !== 'undefined' && document.body) bodyRo.observe(document.body);
    return () => { ro.disconnect(); bodyRo.disconnect(); };
  }, [chips.length, chips.map((c) => c.effectId).join('|')]);

  // Sort chips by the category-order array so the flow runs Innate →
  // Equipment → Diet → Combat → Systems (or whatever the user has
  // configured). Stable within a category — preserves the order from
  // the upstream visibleBuffEntries / visibleDebuffEntries.
  const orderedChips = chips.slice().sort((a, b) => {
    const ai = categoriesById.findIndex((c) => c.id === getCategoryFor(a));
    const bi = categoriesById.findIndex((c) => c.id === getCategoryFor(b));
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  // Walk the ordered list inserting an inter-category INLINE spacer
  // whenever category changes. The spacer is a wide invisible
  // inline-block that sits between the previous category's last chip
  // and the next category's first chip. When both fit on the same
  // wrap-row this pushes them apart horizontally so the outlines have
  // visible separation; when they end up on different rows the spacer
  // just adds a chunk of width to whichever row it lands on (harmless
  // since outlines on different rows don't compete for space).
  // flexShrink:0 prevents the spacer from collapsing on tight panels.
  // This preserves the puzzle-piece structure where smaller categories
  // (Diet, Combat, etc.) naturally fit alongside the bottom-right of
  // larger ones (Equipment) instead of always wrapping below.
  const renderedItems = [];
  let prevCat = null;
  orderedChips.forEach((chip, idx) => {
    const catId = getCategoryFor(chip);
    if (prevCat !== null && catId !== prevCat) {
      renderedItems.push(
        <span
          key={`sp-${idx}`}
          aria-hidden="true"
          style={{ display: 'inline-block', width: 32, height: 1, flexShrink: 0 }}
        />,
      );
    }
    renderedItems.push(
      <span
        key={chip.effectId}
        data-puzzle-chip
        data-puzzle-cat={catId}
        style={{ display: 'inline-flex', flexShrink: 0 }}
      >
        {renderBadge(chip, kind)}
      </span>,
    );
    prevCat = catId;
  });

  return (
    <div
      ref={containerRef}
      onClick={onContainerClick}
      style={{
        position: 'relative',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        // Separate row/column gaps. Row-gap is generous (28px) so when
        // a small category's first chip shares a wrap-row with a
        // larger category's last chip (e.g. Innate next to Equipment's
        // "Invisible" on row 2), the outline of the lower row has
        // visible clearance from the outline above it. Math: PAD_TOP=10
        // + PAD_BOTTOM=5 = 15; rowGap=28 leaves 13px breathable space
        // between top-of-lower-outline and bottom-of-upper-outline.
        // Trade-off: a multi-row category (Equipment) shows ~28px of
        // empty band between its own rows inside its outline. That's
        // the cost of preserving same-row puzzle-piece packing.
        columnGap: 3,
        rowGap: 28,
        padding: '14px 6px 8px 6px',
      }}
    >
      <svg
        aria-hidden="true"
        width={overlay.width || 1}
        height={overlay.height || 1}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}
      >
        {overlay.items.map((it) => {
          const cat = categoriesById.find((c) => c.id === it.catId);
          if (!cat) return null;
          return (
            <path
              key={it.id}
              d={it.d}
              fill={`${cat.color}08`}
              stroke={`${cat.color}66`}
              strokeWidth={1}
            />
          );
        })}
      </svg>
      {overlay.items.map((it) => {
        const cat = categoriesById.find((c) => c.id === it.catId);
        if (!cat) return null;
        // Skip labels for non-first segments (where labelTop is null);
        // they share the category color so the label on the topmost
        // segment is enough to identify the category.
        if (it.labelTop == null) return null;
        return (
          <span
            key={`label-${it.id}`}
            style={{
              position: 'absolute',
              top: it.labelTop,
              left: it.labelLeft,
              padding: '0 5px',
              background: 'var(--bg, rgba(14,10,15,1))',
              fontSize: '0.55rem',
              color: cat.color,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              lineHeight: 1,
              whiteSpace: 'nowrap',
              zIndex: 2,
              pointerEvents: 'none',
            }}
          >
            <span aria-hidden="true">{cat.icon}</span> {cat.label}
          </span>
        );
      })}
      {renderedItems}
    </div>
  );
}

// Status Window Component

// 2026-05-10: Source-category taxonomy for the Status panel filter +
// grouped views. Five categories (Combat now subsumes spells/concentration,
// since from a player's mental model Bless lives in "what's on me right
// now in this fight" not in a separate Spells bucket). The `id` is used
// in state + persistence; the categorizer maps a buff/debuff entry's
// sourceType (and falls back to source-text matching for legacy data
// without sourceType — Bless's seed has source:'Self-cast spell' but no
// sourceType, hence the text-match path).
// 2026-05-10: Pill / fallback display order — Innate first (your
// baseline), then Equipment (gear-applied, biggest cluster typically),
// Diet (well-fed / starvation), Combat (active spells / concentration
// / conditions), Systems (mark / disease — rarest, often hidden). The
// grouped views (stacked / columns / outline) still sort categories by
// buff count for visual prominence; this order only governs the filter
// pills and any stable fallback iteration.
const STATUS_CATEGORIES = [
  { id: 'innate',    label: 'Innate',    icon: '👤', color: '#5cb85c' },
  { id: 'equipment', label: 'Equipment', icon: '⚒',  color: '#4a90d9' },
  { id: 'diet',      label: 'Diet',      icon: '🍖', color: '#d4883a' },
  { id: 'combat',    label: 'Combat',    icon: '⚔',  color: '#e74c3c' },
  { id: 'systems',   label: 'Systems',   icon: '🔮', color: '#b388e8' },
];

const categorizeStatusEffect = (effect) => {
  // 2026-05-10: Read provenance.sourceType FIRST. effectModel.js
  // flattens any non-item provenance to effect.sourceType='legacy'
  // (line 166 — only 'item' is preserved as the surfaced type). So a
  // diet-applied buff arrives as { sourceType: 'legacy', provenance:
  // { sourceType: 'diet', ... } }. Reading effect.sourceType first
  // would short-circuit to 'legacy' (bug user reported: Phantom
  // Greatsword "+1 Str" appearing in Innate even though Lika has a
  // duplicate diet-completion "+1 Str" with provenance.sourceType =
  // 'diet'). The richer provenance lives in the provenance object —
  // prefer it when present.
  const t = effect?.provenance?.sourceType || effect?.sourceType;
  // Strong signals: explicit sourceType from the effect pipeline.
  if (t === 'item' || t === 'item-equip' || t === 'item-ability' || t === 'crafted' || t === 'loot' || t === 'shop-purchase') return 'equipment';
  if (t === 'combat-debuff' || t === 'combat-condition' || t === 'concentration' || t === 'active-effect') return 'combat';
  if (t === 'mark' || t === 'system' || t === 'disease') return 'systems';
  if (t === 'diet') return 'diet';

  // Secondary: linked active-effect on the char (combat-cast buffs land
  // in buffList with sourceType 'legacy' but the matching activeEffects
  // entry tells us this came from a spell). Detected upstream and surfaced
  // via effect.sourceMode === 'spell-cast' (set by writeAdapters when
  // the cast flow attaches buffData with that mode).
  if (effect?.sourceMode === 'spell-cast' || effect?.sourceMode === 'spell') return 'combat';

  // Source-text fallback for legacy data lacking sourceType (most of
  // the demo seeds — buffData entries have `source: 'Self-cast spell'`
  // / `'Diet Completion'` / item names but no sourceType field). Kept
  // tight: only match unambiguous keywords. Generic words like "mark",
  // "watching", "observed" used to pull random debuffs into Systems
  // even when the source was a free-text DM note like
  // "Something is watching..." — those now fall through to Innate.
  const src = String(effect?.source || '').toLowerCase();
  if (src.includes('spell') || src.includes('cast') || src.includes('concentration')) return 'combat';
  if (src.includes('weapon') || src.includes('armor') || src.includes('ring') || src.includes('amulet') || src.includes('attuned') || src.includes('attunement')) return 'equipment';
  if (src.includes('diet') || src.includes('meal') || src.includes('fed') || src.includes('hunger') || src.includes('food')) return 'diet';
  // Systems text-fallback: only the unambiguous words. "disease",
  // "ailment", "infection" reliably indicate a Systems-applied
  // effect in this codebase. Loose words like "mark"/"watching" are
  // intentionally NOT here — they false-positive on free-text DM
  // notes (e.g., "Something is watching..." is just a flavor source,
  // not a system reference).
  if (src.includes('disease') || src.includes('ailment') || src.includes('infection')) return 'systems';

  // ColorId fallback — gold is the convention for self-cast / concentration
  // chips in this codebase, so when source is missing or generic, color
  // tells us this is a combat effect.
  if (effect?.colorId === 'gold') return 'combat';

  // Default: Innate (race, class, permanent traits, manual quick-add,
  // anything else without explicit provenance).
  return 'innate';
};

function StatusWindow(props) {

  const char = props.char;

  const diseases = props.diseases;

  const systems = props.systems;

  const isPlayer = props.isPlayer;

  const onAddBuff = props.onAddBuff;

  const onAddDebuff = props.onAddDebuff;

  const onUpdate = props.onUpdate;

  const onApplyEffectInstanceLifecycle = props.onApplyEffectInstanceLifecycle;
  const onReconcileItemAbilityEffectInstances = props.onReconcileItemAbilityEffectInstances;

  const globalSettings = props.globalSettings;
  const diseasesEnabled = props.diseasesEnabled !== false;
  const hungerEnabled = props.hungerEnabled !== false;

  const glowingSystems = props.glowingSystems || {};  // { systemInstanceId: { color, timestamp } }

  const onClearSystemGlow = props.onClearSystemGlow;

  const glowingBuffs = props.glowingBuffs || {};  // { buffName: { color, timestamp } }

  const onClearBuffGlow = props.onClearBuffGlow;

  const onAddBuffGlow = props.onAddBuffGlow;  // For adding glow effect (separate write, kept for back-compat)

  // 2026-04-27: patch-shape variant — use this to fold the glow into the

  // same onUpdate call as the buff itself, avoiding the two-write race

  // that meant cross-browser glows often got clobbered.

  const onApplyBuffGlowPatch = props.onApplyBuffGlowPatch;

  

  const [showAdd, setShowAdd] = useState(false);

  const [showGraveyard, setShowGraveyard] = useState(false);

  const [graveyardMode, setGraveyardMode] = useState('restore');

  const [confirmRemove, setConfirmRemove] = useState(null);

  const [actionBuff, setActionBuff] = useState(null); // { type: 'buff'|'debuff', index, text, isPermanent }

  const [pendingEffectLifecycleId, setPendingEffectLifecycleId] = useState(null);
  const pendingEffectLifecycleRef = useRef(null);

  const [effectLifecycleError, setEffectLifecycleError] = useState('');
  const [effectReconcilePending, setEffectReconcilePending] = useState(false);
  const [effectReconcileResult, setEffectReconcileResult] = useState(null);
  const [effectReconcileError, setEffectReconcileError] = useState('');

  const [showHiddenBuffs, setShowHiddenBuffs] = useState(false);

  const [showHiddenDebuffs, setShowHiddenDebuffs] = useState(false);

  // 2026-05-10: Status panel filter + group-style picker.
  //
  // Filter is a Set of EXCLUDED category ids (default empty → show all).
  // Multi-select via prof-pill-style toggles: click a pill to drop its
  // category from the view, click again to bring it back. "All" button
  // resets the set. Auto-shows above 6 effects.
  //
  // Group style picks one of {flat, stacked, columns}. Flat = current
  // chip cloud; stacked = category-labeled rows under each Buffs/Debuffs
  // section; columns = side-by-side category columns under each section
  // (the layout the user specifically asked to see). Picker only matters
  // when there's enough chips to group meaningfully.
  const [excludedStatusCategories, setExcludedStatusCategories] = useState(() => new Set());
  // 2026-05-11: Show/hide toggle for the filter pill row. When visible
  // (default), pills render INLINE with the Status title at smaller
  // size, instead of consuming their own row below the header. Style
  // picker stays visible regardless of this toggle.
  const [showStatusFilters, setShowStatusFilters] = useState(true);
  // 'flat' | 'stacked' | 'columns' | 'outline'. 'outline' is the
  // fieldset-style view: chips flow naturally on the page (sorted by
  // group), each group wrapped in a thin outline that hugs the chip
  // cluster — no fixed cards, the outline conforms to the chip shape.
  const [statusGroupStyle, setStatusGroupStyle] = useState('flat');
  // 2026-05-10: Permanence toggles. Same multi-select exclusion pattern
  // as the category filter. Two pills next to the categories: Temp /
  // Permanent. Click to hide that permanence class. Default: both
  // visible.
  const [excludedStatusPermanence, setExcludedStatusPermanence] = useState(() => new Set());

  const set = syncSpellSettings(char.settings || DEFAULT_SETTINGS);

  const gSettings = globalSettings || DEFAULT_SETTINGS;

  const allowClickOff = gSettings.allowBuffClickOff !== false;

  

  // Handler for adding buff/debuff with full data (color, notes, glow, etc)

  const handleAddWithData = (data) => {

    if (!onUpdate) return;



    // Explicitly check isPermanent as boolean

    const makePermanent = data.isPermanent === true;

    let updatedChar = addLegacyEffect(char, {

      text: data.text,

      kind: data.isDebuff ? 'debuff' : 'buff',

      permanent: makePermanent,

      metadata: buildLegacyEffectMetadata({

        source: data.notes || null,

        playerTooltip: data.playerTooltip || null,

        colorId: data.colorId,

        noteVisible: data.noteVisible === true,

      }),

    });



    // 2026-04-27: fold glow into the same onUpdate when the patch helper

    // is wired (default for player view from App). Falls back to the

    // separate onAddBuffGlow call for any caller that still passes the

    // older callback only.

    if (data.addGlow) {

      const colorOpt = BUFF_COLOR_OPTIONS.find(c => c.id === data.colorId);

      const glowColor = colorOpt?.glowColor || colorOpt?.color || (data.isDebuff ? '#ff6961' : '#27c95a');

      if (onApplyBuffGlowPatch) {

        updatedChar = onApplyBuffGlowPatch(updatedChar, data.text, glowColor);

      } else if (onAddBuffGlow) {

        onAddBuffGlow(data.text, glowColor);

      }

    }



    onUpdate(updatedChar);

  };

  

  // Gather equipped item combat bonuses to display as virtual badges in status window

  const equippedCombatBonuses = getEquippedCombatBonusesFromInventory(char.inventory).map(b => ({

    ...b,

    text: b.label || `${b.type} ${b.scope}`,

    itemName: b.itemName || b.source || 'Item',

    isBuff: b.type === 'advantage' || b.type === 'save_bonus' || b.type === 'passive' || b.type === 'resistance',

  }));



  const legacyStatusView = buildLegacyStatusEffectView({

    char,

    glowingBuffsForChar: glowingBuffs || {},

  });

  // 2026-05-10: Well Fed / Hungry / Starving surface as synthesized
  // CHIPS (one per effect string in the DM-configured arrays) instead
  // of a banner. So "+3 Max HP" + "+2 Con" each appear as a Diet buff
  // chip when wellFed, with "Well Fed" in the tooltip telling you
  // where the bonus comes from. Same pattern for hungry/starving
  // debuffs. Synthetic entries carry isSynthetic so the renderer
  // skips remove/hide handlers — they're auto-derived from char
  // state, not manually-added.
  const _wellFed = hungerEnabled && char?.mealsToday >= 2 && char?.daysWithoutFood === 0;
  const _set = char?.settings || DEFAULT_SETTINGS;
  const _hungry = hungerEnabled && char?.daysWithoutFood >= (_set?.hungerThreshold || 3) && !char?.starving;
  const _starving = hungerEnabled && char?.daysToStarvation === 0;
  const _wellFedEffects = Array.isArray(_set?.wellFedBuffs) ? _set.wellFedBuffs : [];
  const _hungryEffects = Array.isArray(_set?.hungryDebuffs) ? _set.hungryDebuffs : [];
  const _starvationEffects = Array.isArray(_set?.starvationDebuffs) ? _set.starvationDebuffs : [];
  const buildSyntheticEffect = (text, kind, opts = {}) => ({
    effectId: `synthetic-${kind}-${opts.tag || text}-${text}`,
    text,
    source: opts.source || null,
    sourceType: opts.sourceType || 'diet',
    provenance: opts.provenance || { sourceType: opts.sourceType || 'diet' },
    playerTooltip: opts.playerTooltip || null,
    colorId: opts.colorId || null,
    isPermanent: false,
    isDisabled: false,
    isHidden: false,
    isSynthetic: true,
    category: kind,
    legacyKey: `synthetic:${opts.tag || text}:${text}`,
    listIndex: -1,
  });
  const syntheticBuffs = _wellFed
    ? _wellFedEffects.map((effectText) => buildSyntheticEffect(effectText, 'buff', {
        tag: 'well-fed',
        source: 'Well Fed',
        sourceType: 'diet',
        playerTooltip: 'Well Fed — granted by completing two meals today without going hungry',
        colorId: 'teal',
      }))
    : [];
  const syntheticDebuffs = [
    ...(_hungry
      ? _hungryEffects.map((effectText) => buildSyntheticEffect(effectText, 'debuff', {
          tag: 'hungry',
          source: 'Hungry',
          sourceType: 'diet',
          playerTooltip: `Hungry — ${char?.daysWithoutFood || 0} day(s) without food`,
          colorId: 'amber',
        }))
      : []),
    ...(_starving
      ? _starvationEffects.map((effectText) => buildSyntheticEffect(effectText, 'debuff', {
          tag: 'starving',
          source: 'Starving',
          sourceType: 'diet',
          playerTooltip: 'Starving — body is collapsing, take damage on long rest',
          colorId: 'crimson',
        }))
      : []),
  ];

  const visibleBuffEntries = [...syntheticBuffs, ...legacyStatusView.visibleBuffs];

  const visibleDebuffEntries = [...syntheticDebuffs, ...legacyStatusView.visibleDebuffs];

  const hiddenBuffEntries = legacyStatusView.hiddenBuffs;

  const hiddenDebuffEntries = legacyStatusView.hiddenDebuffs;

  const disabledBuffEntries = legacyStatusView.disabledBuffs;

  const disabledDebuffEntries = legacyStatusView.disabledDebuffs;

  // 2026-05-10: per-category counts (split by buff vs debuff so the
  // column-layout grouping can sort categories by buff count for stable
  // left-to-right placement) + filtered entries given the multi-select
  // filter state.
  const statusBuffCountsByCategory = (() => {
    const out = {};
    visibleBuffEntries.forEach(e => { const c = categorizeStatusEffect(e); out[c] = (out[c] || 0) + 1; });
    return out;
  })();
  const statusDebuffCountsByCategory = (() => {
    const out = {};
    visibleDebuffEntries.forEach(e => { const c = categorizeStatusEffect(e); out[c] = (out[c] || 0) + 1; });
    return out;
  })();
  const statusCategoryCounts = (() => {
    const out = { ...statusBuffCountsByCategory };
    Object.entries(statusDebuffCountsByCategory).forEach(([k, v]) => { out[k] = (out[k] || 0) + v; });
    return out;
  })();
  const totalStatusEffectCount = visibleBuffEntries.length + visibleDebuffEntries.length;
  const passesStatusFilters = (e) => {
    if (excludedStatusCategories.has(categorizeStatusEffect(e))) return false;
    const permKey = e?.isPermanent ? 'permanent' : 'temporary';
    if (excludedStatusPermanence.has(permKey)) return false;
    return true;
  };
  const filteredBuffEntries = visibleBuffEntries.filter(passesStatusFilters);
  const filteredDebuffEntries = visibleDebuffEntries.filter(passesStatusFilters);
  const permanenceCounts = (() => {
    const out = { permanent: 0, temporary: 0 };
    [...visibleBuffEntries, ...visibleDebuffEntries].forEach(e => {
      out[e?.isPermanent ? 'permanent' : 'temporary'] += 1;
    });
    return out;
  })();

  // Tabs + group picker auto-show at 6+ effects.
  const showStatusFilterTabs = totalStatusEffectCount >= 6;
  const isStatusFiltered = excludedStatusCategories.size > 0 || excludedStatusPermanence.size > 0;
  const useGroupedStatusLayout = statusGroupStyle !== 'flat';

  // For grouped layouts: order categories left-to-right by BUFF count
  // (descending). Same order is used for Debuffs so the user develops
  // muscle memory ("first column = my biggest buff source").
  const orderedGroupedCategories = STATUS_CATEGORIES
    .filter(c => !excludedStatusCategories.has(c.id))
    .filter(c => (statusBuffCountsByCategory[c.id] || 0) + (statusDebuffCountsByCategory[c.id] || 0) > 0)
    .sort((a, b) => (statusBuffCountsByCategory[b.id] || 0) - (statusBuffCountsByCategory[a.id] || 0));

  const toggleStatusCategoryFilter = (id) => {
    setExcludedStatusCategories(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleStatusPermanenceFilter = (id) => {
    setExcludedStatusPermanence(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  

  const hasBuffs = visibleBuffEntries.length > 0 || hiddenBuffEntries.length > 0 || equippedCombatBonuses.filter(b => b.isBuff).length > 0;

  const hasDebuffs = visibleDebuffEntries.length > 0 || hiddenDebuffEntries.length > 0 || equippedCombatBonuses.filter(b => !b.isBuff).length > 0;

  const hasDiseases = diseasesEnabled && (char.diseaseList || []).length > 0;

  const visibleSystemList = isPlayer

    ? (char.systemList || []).filter(s => {

        const playerVisible = s.playerVisible !== false;

        const hasAnyVisible =

          !!s.showName ||

          !!s.showDescription ||

          !!s.showEffects ||

          !!s.showCure ||

          (s.visibleClues || []).length > 0 ||

          (!!s.showPlayerNotes && (s.playerNotes || '').trim()) ||

          Object.values(s.showSections || {}).some(Boolean) ||

          Object.values(s.visibleSections || {}).some(arr => (arr || []).length > 0);



        return playerVisible || hasAnyVisible;

      })

    : (char.systemList || []);



  const hasSystems = visibleSystemList.length > 0;
  const canReconcileItemAbilityEffects = !isPlayer && typeof onReconcileItemAbilityEffectInstances === 'function';



  // Get disabled buffs/debuffs (manually clicked off but item still equipped)

  const disabledBuffKeys = char.disabledBuffs || [];

  const disabledDebuffKeys = char.disabledDebuffs || [];

  

  // Get recently removed buffs/debuffs (for graveyard - max 2 each)

  const recentlyRemovedBuffs = char.recentlyRemovedBuffs || [];

  const recentlyRemovedDebuffs = char.recentlyRemovedDebuffs || [];

  

  // Check if a buff/debuff is linked to an equipped item

  // 2026-05-11: When an effect object is supplied, prefer its
  // per-instance linkedItemName (computed by statusViewModel using
  // provenance.itemEntryId). The text-scan fallback is kept for the
  // few callers that only have the buff TEXT (e.g., `isGraveyardWorthy`
  // which asks "is there ANY equipped item with this buff text?" —
  // a question text-scan can answer correctly). Most call sites
  // (handleRemove, etc.) already pass the effect object and receive
  // per-instance attribution.
  const isLinkedToEquippedItem = (effectText, effect = null) => {

    if (effect && effect.linkedItemName) return effect.linkedItemName;

    const inventory = char.inventory || [];

    for (const item of inventory) {

      if (!item.equipped) continue;

      if ((item.buffs || []).includes(effectText)) return item.name;

      if ((item.debuffs || []).includes(effectText)) return item.name;

    }

    return null;

  };

  

  // Get the item that has this buff/debuff (for tracking removed item buffs)

  const getItemWithEffect = (effectText) => {

    const inventory = char.inventory || [];

    for (const item of inventory) {

      if ((item.buffs || []).includes(effectText)) return item;

      if ((item.debuffs || []).includes(effectText)) return item;

    }

    return null;

  };

  

  // Check if effect should be in graveyard (permanent, item-linked, or recently removed)

  const isGraveyardWorthy = (effectText, isPermanent) => {

    if (isPermanent) return true;

    if (isLinkedToEquippedItem(effectText)) return true;

    return false;

  };

  const isEffectLifecyclePending = (effect) => (
    !!effect?.effectInstanceId
    && (pendingEffectLifecycleId === effect.effectInstanceId
      || pendingEffectLifecycleRef.current === effect.effectInstanceId)
  );

  const summarizeEffectReconcileResult = (result) => {
    if (!result) return '';
    const pieces = [
      `backfill ${Number(result.backfilled || 0)}`,
      `patch ${Number(result.patched || 0)}`,
      `tombstone ${Number(result.tombstoned || 0)}`,
    ];
    const hiddenKeysRepaired = Number(result.hiddenKeysRepaired || 0);
    if (hiddenKeysRepaired > 0) pieces.push(`repair hidden ${hiddenKeysRepaired}`);
    const lifecycleStateKeysPruned = Number(result.lifecycleStateKeysPruned || 0);
    if (lifecycleStateKeysPruned > 0) pieces.push(`prune stale state ${lifecycleStateKeysPruned}`);
    const itemAbilityBuffDataKeysPruned = Number(result.itemAbilityBuffDataKeysPruned || 0);
    if (itemAbilityBuffDataKeysPruned > 0) pieces.push(`prune stale metadata ${itemAbilityBuffDataKeysPruned}`);
    const terminalRowsRetained = Number(result.terminalRowsRetained || 0);
    if (terminalRowsRetained > 0) pieces.push(`retain terminal ${terminalRowsRetained}`);
    const terminalBlockers = Array.isArray(result.terminalCollisionEffectInstanceIds)
      ? result.terminalCollisionEffectInstanceIds.length
      : 0;
    if (terminalBlockers > 0) pieces.push(`blocked terminal ${terminalBlockers}`);
    const status = result.blocked ? 'blocked' : (result.changed ? 'found changes' : 'clean');
    return `${result.dryRun ? 'Dry run' : 'Applied'}: ${status}. ${pieces.join(', ')}.`;
  };

  const runItemAbilityEffectReconcile = async (dryRun) => {
    if (!canReconcileItemAbilityEffects || effectReconcilePending) return;
    if (!dryRun && !(effectReconcileResult?.dryRun === true && effectReconcileResult?.changed === true && effectReconcileResult?.blocked !== true)) return;

    const targetCharId = String(char?.id || char?.charId || '').trim();
    if (!targetCharId) {
      setEffectReconcileError('Character id is missing.');
      return;
    }

    setEffectReconcilePending(true);
    setEffectReconcileError('');
    try {
      const result = await onReconcileItemAbilityEffectInstances({
        charId: targetCharId,
        dryRun,
        confirm: dryRun ? undefined : 'RECONCILE_ITEM_ABILITY_EFFECTS_FOR_CHAR',
      });
      setEffectReconcileResult(result || { dryRun, changed: false });
    } catch (error) {
      console.error('Item ability effect reconcile failed:', error);
      setEffectReconcileError('Effect reconcile failed. Refresh and try again.');
    } finally {
      setEffectReconcilePending(false);
    }
  };

  const applyDurableEffectLifecycle = async (effect, actionOverride = null) => {

    const args = buildEffectInstanceLifecycleArgs(effect, actionOverride);

    if (!args) return false;

    if (pendingEffectLifecycleRef.current === args.effectInstanceId) return true;

    if (typeof onApplyEffectInstanceLifecycle !== 'function') {

      setEffectLifecycleError('This status effect is server-owned. Refresh and try again before changing it.');

      return true;

    }

    try {

      setEffectLifecycleError('');

      pendingEffectLifecycleRef.current = effect.effectInstanceId;

      setPendingEffectLifecycleId(effect.effectInstanceId);

      const result = await onApplyEffectInstanceLifecycle(args);

      if (!result) throw new Error('effect instance lifecycle authority unavailable');

    } catch (error) {

      console.error('[status] authoritative effect lifecycle update failed', error);

      const message = String(error?.message || error || '');

      setEffectLifecycleError(/stale/i.test(message)

        ? 'This status effect changed elsewhere. Refresh and try again.'

        : 'Could not update this server-owned status effect. Refresh and try again.');

    } finally {

      if (pendingEffectLifecycleRef.current === effect.effectInstanceId) {

        pendingEffectLifecycleRef.current = null;

        setPendingEffectLifecycleId(null);

      }

    }

    return true;

  };


  // Handle removing a buff/debuff

  const handleRemove = (type, index, text, isPermanent, effect = null) => {

    const itemName = effect
      ? effect.linkedItemName
      : isLinkedToEquippedItem(text);

    

    if (itemName) {

      // Show confirmation for item-linked buff/debuff

      setConfirmRemove({ type, index, text, isPermanent, itemName, effect });

    } else {

      // Direct remove for non-item buffs

      performRemove(type, index, text, isPermanent, false, effect);

    }

  };

  

  const performRemove = async (type, index, text, isPermanent, disableInsteadOfRemove, effect = null) => {

    if (!onUpdate) return;

    if (await applyDurableEffectLifecycle(effect, effect?.isDisabled ? 'enable' : 'disable')) {

      setConfirmRemove(null);

      setActionBuff(null);

      return;

    }



    // 2026-04-29: per-instance disable. Compute the same occurrence the

    // render layer used so the stored key (`name` or `name::N`) matches

    // what `disabledBuffs.includes(instanceKey)` will check during the

    // next render. Without this, two duplicate-name buffs share disable

    // state because the bare name is in disabledBuffs.

    const occ = type === 'buff'

      ? (isPermanent

          ? getGlobalOccurrence(text, index, char.permanentBuffs || [])

          : getGlobalOccurrence(text, index, char.buffList || [], char.permanentBuffs || []))

      : (isPermanent

          ? getGlobalOccurrence(text, index, char.permanentDebuffs || [], char.permanentBuffs || [], char.buffList || [])

          : getGlobalOccurrence(text, index, char.debuffList || [], char.permanentBuffs || [], char.buffList || [], char.permanentDebuffs || []));

    const instanceKey = buffInstanceKey(text, occ);



    const isDisabled = type === 'buff'

      ? disabledBuffKeys.includes(instanceKey)

      : disabledDebuffKeys.includes(instanceKey);



    if (disableInsteadOfRemove || isDisabled) {

      // Toggle disabled state — filter/add the per-instance key, not the bare name.

      if (type === 'buff') {

        const newDisabled = isDisabled

          ? disabledBuffKeys.filter(b => b !== instanceKey)

          : [...disabledBuffKeys, instanceKey];

        onUpdate({ ...char, disabledBuffs: newDisabled });

      } else {

        const newDisabled = isDisabled

          ? disabledDebuffKeys.filter(d => d !== instanceKey)

          : [...disabledDebuffKeys, instanceKey];

        onUpdate({ ...char, disabledDebuffs: newDisabled });

      }

    } else {

      onUpdate(removeLegacyEffectToRecent(char, {

        kind: type,

        permanent: isPermanent,

        index,

        text,

      }));

    }

    setConfirmRemove(null);

  };

  

  // Re-enable a disabled buff/debuff from graveyard

  // 2026-04-29: takes a per-instance key (output of buffInstanceKey),

  // not a bare name — the graveyard stores keys verbatim, so the filter

  // matches the stored entry exactly.

  const reEnableFromGraveyard = async (type, instanceKey, effect = null) => {

    if (!onUpdate) return;

    if (await applyDurableEffectLifecycle(effect, 'enable')) {

      return;

    }



    if (type === 'buff') {

      const newDisabled = disabledBuffKeys.filter(b => b !== instanceKey);

      onUpdate({ ...char, disabledBuffs: newDisabled });

    } else {

      const newDisabled = disabledDebuffKeys.filter(d => d !== instanceKey);

      onUpdate({ ...char, disabledDebuffs: newDisabled });

    }

  };

  

  // Restore a recently removed buff/debuff from graveyard

  const restoreFromGraveyard = (type, item, index) => {

    if (!onUpdate) return;



    onUpdate(restoreRecentlyRemovedLegacyEffect(char, {

      kind: type,

      item,

      index,

    }));

  };

  

  // Permanently remove a disabled buff/debuff from graveyard

  // 2026-04-29: takes a per-instance key. removedItemBuffs is still

  // keyed by bare name (item buffs are name-strings on the item), so

  // we parse the key to find the source item.

  const permanentlyRemoveDisabled = async (type, instanceKey, effect = null) => {

    if (!onUpdate) return;

    if (isEffectInstanceLifecycleAuthorityCandidate(effect)) {

      await applyDurableEffectLifecycle(effect, 'archive');

      return;

    }



    const updates = {};

    if (type === 'buff') {

      updates.disabledBuffs = disabledBuffKeys.filter(b => b !== instanceKey);

    } else {

      updates.disabledDebuffs = disabledDebuffKeys.filter(d => d !== instanceKey);

    }



    // Track as a removed item buff so the item card shows it as removed.

    const { name } = parseBuffInstanceKey(instanceKey);

    const item = getItemWithEffect(name);

    if (item) {

      const removedItemBuffs = char.removedItemBuffs || {};

      const itemRemoved = removedItemBuffs[item.id] || [];

      if (!itemRemoved.includes(name)) {

        updates.removedItemBuffs = { ...removedItemBuffs, [item.id]: [...itemRemoved, name] };

      }

    }



    onUpdate({ ...char, ...updates });

  };

  

  // Permanently remove a recently removed buff/debuff from graveyard

  const permanentlyRemoveRecent = (type, index) => {

    if (!onUpdate) return;

    

    if (type === 'buff') {

      onUpdate({ ...char, recentlyRemovedBuffs: recentlyRemovedBuffs.filter((_, i) => i !== index) });

    } else {

      onUpdate({ ...char, recentlyRemovedDebuffs: recentlyRemovedDebuffs.filter((_, i) => i !== index) });

    }

  };

  

  // Handle graveyard item click based on mode

  // Hide/unhide a buff/debuff (still active, just not displayed prominently)

  // 2026-04-29: hide/unhide are now per-instance — caller passes a

  // buffInstanceKey so duplicate-name buffs hide independently.

  const hideEffect = async (type, instanceKey, effect = null) => {

    if (!onUpdate) return;

    if (await applyDurableEffectLifecycle(effect, 'hide')) {

      setActionBuff(null);

      return;

    }

    const field = type === 'buff' ? 'hiddenBuffs' : 'hiddenDebuffs';

    const current = char[field] || [];

    if (!current.includes(instanceKey)) {

      onUpdate({ [field]: [...current, instanceKey] });

    }

    setActionBuff(null);

  };

  const unhideEffect = async (type, instanceKey, effect = null) => {

    if (!onUpdate) return;

    if (await applyDurableEffectLifecycle(effect, 'unhide')) {

      return;

    }

    const field = type === 'buff' ? 'hiddenBuffs' : 'hiddenDebuffs';

    onUpdate({ [field]: (char[field] || []).filter(t => t !== instanceKey) });

  };

  

  // Handle ✕ click on buff badge — show action menu instead of immediate remove

  const handleXClick = (type, index, text, isPermanent, effect = null) => {

    setActionBuff({ type, index, text, isPermanent, effectId: effect?.effectId || null });

  };

  // 2026-05-10: Shared BuffBadge renderer used by both the flat
  // buff/debuff sections and the new grouped-by-category view. Kept as
  // a closure so it inherits actionBuff / handleX / etc from scope
  // without prop-drilling. Producing IDENTICAL JSX to the original
  // inline calls is intentional — no behavior change, just letting two
  // call sites (flat + grouped) share one definition.
  //
  // 2026-05-10b: Two display transforms layered on top:
  //  1. Synthetic entries (Well Fed / Hungry / Starving) are auto-derived
  //     from char state; no remove/hide/X handlers (those would no-op
  //     anyway since there's no underlying buffList entry to remove).
  //  2. Spell-cast buffs whose text carries a parenthetical descriptor
  //     ("Bless (+1d4 attack rolls, saving throws)") are split into chip
  //     name + tooltip so the chip stays scannable while the descriptor
  //     stays accessible on hover.
  const renderStatusBuffBadge = (effect, kind) => {
    const isAction = !effect.isSynthetic && (actionBuff?.effectId
      ? actionBuff.effectId === effect.effectId
      : actionBuff?.type === kind
        && actionBuff?.text === effect.text
        && actionBuff?.index === effect.listIndex
        && actionBuff?.isPermanent === effect.isPermanent);

    // Spell-text transform — only applied to spell-like sources so we
    // don't accidentally strip parentheticals from buffs where the
    // parenthetical IS the meaningful chip name (e.g. "Invisible (1
    // minute)" — that's not a spell-cast trace, the duration matters).
    let displayText = effect.text;
    let displayTooltip = effect.playerTooltip;
    const isSpellLike = effect.sourceMode === 'spell-cast'
      || effect.colorId === 'gold'
      || /\b(spell|cast|concentration)\b/i.test(effect.source || '');
    if (isSpellLike) {
      const m = (effect.text || '').match(/^([^(]+?)\s*\((.+)\)\s*$/);
      // Only transform when the parenthetical clearly describes the
      // spell's effect — starts with +/- or contains "rolls"/"save"
      // keywords. Skips noise like "Bless (self)" where the suffix is
      // an identity tag, not a descriptor.
      if (m && /^[+\-]/.test(m[2].trim()) || (m && /\b(rolls?|saves?|attack)\b/i.test(m[2]))) {
        displayText = m[1].trim();
        const desc = m[2].trim();
        displayTooltip = displayTooltip ? `${desc} — ${displayTooltip}` : desc;
      }
    }

    const isSynth = effect.isSynthetic;
    return (
      <BuffBadge
        key={effect.effectId}
        text={displayText}
        source={effect.source}
        sourceType={effect.sourceType}
        sourceMode={effect.sourceMode}
        sourceLabel={effect.sourceLabel}
        playerTooltip={displayTooltip}
        colorId={effect.colorId}
        isPermanent={effect.isPermanent}
        isDebuff={kind === 'debuff'}
        isDisabled={effect.isDisabled}
        linkedItemName={effect.linkedItemName}
        glowColor={effect.glowColor}
        onClearGlow={effect.glowColor && onClearBuffGlow ? () => onClearBuffGlow(effect.legacyKey) : undefined}
        onXClick={!isSynth && allowClickOff && onUpdate ? () => handleXClick(kind, effect.listIndex, effect.text, effect.isPermanent, effect) : undefined}
        showActions={isAction}
        onRemove={isSynth ? undefined : (() => { setActionBuff(null); handleRemove(kind, effect.listIndex, effect.text, effect.isPermanent, effect); })}
        onHide={isSynth ? undefined : (() => hideEffect(kind, effect.legacyKey, effect))}
        isPlayer={isPlayer}
        noteVisible={effect.noteVisible}
        isPending={isEffectLifecyclePending(effect)}
      />
    );
  };

  const hiddenBuffKeys = char.hiddenBuffs || [];

  const hiddenDebuffKeys = char.hiddenDebuffs || [];

  const disabledBuffs = disabledBuffKeys;

  const disabledDebuffs = disabledDebuffKeys;

  const hiddenBuffs = hiddenBuffKeys;

  const hiddenDebuffs = hiddenDebuffKeys;

  

  const handleGraveyardClick = async (type, text, isDisabled, item, index) => {

    if (graveyardMode === 'remove') {

      if (isDisabled) {

        await permanentlyRemoveDisabled(type, text, item);

      } else {

        permanentlyRemoveRecent(type, index);

      }

    } else {

      // restore mode

      if (isDisabled) {

        reEnableFromGraveyard(type, text, item);

      } else {

        restoreFromGraveyard(type, item, index);

      }

    }

  };

  

  // Collect visible disease effects

  const visibleDiseaseEffects = [];

  if (diseasesEnabled) (char.diseaseList || []).forEach(d => {

    if (d.showEffects && diseases && diseases[d.diseaseId]) {

      const disease = diseases[d.diseaseId];

      (disease.effects || []).forEach(eff => {

        visibleDiseaseEffects.push({ effect: eff, diseaseName: d.showName ? disease.name : HIDDEN_LABEL_PLACEHOLDER });

      });

    }

  });

  

  

  // Collect visible system effects

  const visibleSystemEffects = [];

  (char.systemList || []).forEach(sy => {

    const sys = resolveSystemTemplateForViewer(sy, systems, !isPlayer);

    if (!sys) return;

    if (sy.active === false) return;

    if (sy.applyEffects === false) return;

    

    // Skip Note types - no mechanical effects

    if (sys.type === 'note') return;

    

    const unknownText = sy.unknownText || UNKNOWN_PLACEHOLDER;

    const systemName = sy.showName ? (sys.publicName || sys.name) : (sy.placeholderName || unknownText);

    const effectOverrides = sy.effectOverrides || {};

    

    // Visible effects (when showEffects is true OR applyHiddenEffects is true)

    if (sy.showEffects || sy.applyHiddenEffects) {

      (sys.effects || []).forEach((eff, effIdx) => {

        const override = effectOverrides[`visible-${effIdx}`] || {};

        // Skip if explicitly disabled

        if (override.apply === false) return;

        

        const effText = typeof eff === 'string' ? eff : (eff.displayText || '');

        if (!effText) return;

        

        // Determine source visibility

        let displayName = systemName;

        let isMystery = false;

        

        if (override.showSource === false) {

          displayName = unknownText;

          isMystery = true;

        } else if (!sy.showEffects) {

          displayName = unknownText;

          isMystery = true;

        }

        

        visibleSystemEffects.push({ 

          effect: effText, 

          systemName: displayName, 

          isHidden: !sy.showEffects,

          isMystery,

        });

      });

    }

    

    // Hidden effects (when applyHiddenEffects is true)

    if (sy.applyHiddenEffects) {

      (sys.hiddenEffects || []).forEach((eff, effIdx) => {

        const override = effectOverrides[`hidden-${effIdx}`] || {};

        // Skip if explicitly disabled

        if (override.apply === false) return;

        

        const effText = typeof eff === 'string' ? eff : (eff.displayText || '');

        if (effText) {

          visibleSystemEffects.push({ 

            effect: effText, 

            systemName: HIDDEN_LABEL_PLACEHOLDER, 

            isHidden: true,

            isMystery: true,

          });

        }

      });

    }

    

    // Stage effects (from progression systems)

    const stageInfo = getCurrentStageInfo(sy, sys);

    if (stageInfo?.stage?.effects?.length > 0) {

      const stageOverride = effectOverrides[`stage-${stageInfo.index}`] || {};

      

      // Skip if stage effects are explicitly disabled

      if (stageOverride.apply === false) return;

      

      let stageSourceName;

      let isStageMystery = false;

      

      if (stageOverride.showSource === false) {

        stageSourceName = HIDDEN_LABEL_PLACEHOLDER;

        isStageMystery = true;

      } else if (sy.showEffects) {

        stageSourceName = sy.showStage ? `${systemName} (${stageInfo.stage.name})` : systemName;

      } else {

        stageSourceName = HIDDEN_LABEL_PLACEHOLDER;

        isStageMystery = true;

      }

      

      stageInfo.stage.effects.forEach(eff => {

        const effText = typeof eff === 'string' ? eff : (eff.displayText || '');

        if (effText) {

          visibleSystemEffects.push({ 

            effect: effText, 

            systemName: stageSourceName, 

            isHidden: !sy.showEffects,

            isMystery: isStageMystery,

          });

        }

      });

    }

    

    // Tracker threshold effects

    if ((sys.trackerFields || []).length > 0) {

      const trackerValues = sy.trackerValues || {};

      const trackerVisibility = sy.trackerVisibility || {};

      

      (sys.trackerFields || []).forEach(field => {

        const fieldVis = trackerVisibility[field.id] || {};

        const currentValue = trackerValues[field.id] ?? field.value ?? 0;

        const activeThresholds = (field.thresholds || [])

          .filter(t => currentValue >= t.value)

          .sort((a, b) => b.value - a.value);

        

        activeThresholds.forEach((threshold, threshIdx) => {

          const override = effectOverrides[`threshold-${field.id}-${threshIdx}`] || {};

          if (override.apply === false) return;

          

          const thresholdEffects = threshold.effects || (threshold.effect ? [threshold.effect] : []);

          

          let thresholdSrc;

          let isThreshMystery = false;

          

          if (override.showSource === false || fieldVis.showSource === false) {

            thresholdSrc = HIDDEN_LABEL_PLACEHOLDER;

            isThreshMystery = true;

          } else if (sy.showEffects) {

            thresholdSrc = `${systemName} (${field.label})`;

          } else {

            thresholdSrc = HIDDEN_LABEL_PLACEHOLDER;

            isThreshMystery = true;

          }

          

          thresholdEffects.forEach(eff => {

            const effText = typeof eff === 'string' ? eff : (eff.displayText || '');

            if (effText) {

              visibleSystemEffects.push({ 

                effect: effText, 

                systemName: thresholdSrc, 

                isHidden: !sy.showEffects,

                isMystery: isThreshMystery,

              });

            }

          });

        });

      });

    }

  });



  // Determine hunger/starvation status

  const wellFed = char.mealsToday >= 2 && char.daysWithoutFood === 0;

  const hungry = char.daysWithoutFood >= set.hungerThreshold && !char.starving;

  const starving = char.daysToStarvation === 0;

  

  const hasAnyDebuffs = hasDebuffs || visibleDiseaseEffects.length > 0 || visibleSystemEffects.length > 0;

  

  // Count graveyard items

  const graveyardBuffCount = disabledBuffEntries.length + recentlyRemovedBuffs.length;

  const graveyardDebuffCount = disabledDebuffEntries.length + recentlyRemovedDebuffs.length;

  const totalGraveyardCount = graveyardBuffCount + graveyardDebuffCount;

  const effectLifecycleErrorBanner = effectLifecycleError ? (

    <div style={{ padding: '6px 8px', marginBottom: '8px', border: '1px solid rgba(201,39,39,0.45)', borderRadius: '4px', background: 'rgba(201,39,39,0.12)', color: '#ff9a9a', fontSize: '0.72rem' }}>

      {effectLifecycleError}

    </div>

  ) : null;
  const effectReconcileCanApply = effectReconcileResult?.dryRun === true && effectReconcileResult?.changed === true && effectReconcileResult?.blocked !== true;
  const effectReconcilePanel = canReconcileItemAbilityEffects ? (
    <div style={{ padding: '8px', marginBottom: '8px', border: '1px solid rgba(74,144,217,0.35)', borderRadius: '6px', background: 'rgba(20,30,50,0.28)', fontSize: '0.72rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
        <strong style={{ color: '#87ceeb' }}>Item effect reconcile</strong>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <button
            type="button"
            aria-label="Dry run item effect reconcile"
            disabled={effectReconcilePending}
            style={{ ...S.btn, ...S.btnXs, opacity: effectReconcilePending ? 0.55 : 1, cursor: effectReconcilePending ? 'wait' : 'pointer' }}
            onClick={() => runItemAbilityEffectReconcile(true)}
          >
            Dry run
          </button>
          <button
            type="button"
            aria-label="Apply item effect reconcile"
            disabled={effectReconcilePending || !effectReconcileCanApply}
            style={{ ...S.btn, ...S.btnSuccess, ...S.btnXs, opacity: effectReconcilePending || !effectReconcileCanApply ? 0.45 : 1, cursor: effectReconcilePending ? 'wait' : (effectReconcileCanApply ? 'pointer' : 'not-allowed') }}
            onClick={() => runItemAbilityEffectReconcile(false)}
          >
            Apply
          </button>
        </div>
      </div>
      {(effectReconcileResult || effectReconcileError) && (
        <div style={{ marginTop: '6px', color: effectReconcileError ? '#ff9a9a' : '#c9d8ff' }}>
          {effectReconcileError || summarizeEffectReconcileResult(effectReconcileResult)}
        </div>
      )}
    </div>
  ) : null;

  const confirmRemovePending = isEffectLifecyclePending(confirmRemove?.effect);



  // Graveyard View

  if (showGraveyard) {

    return (

      <HoverCard>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>

        <h3 style={{ color: '#666', margin: 0, fontSize: '1rem' }}>🪦 Buff Graveyard</h3>

        <Tooltip text="Exit the Buff Graveyard">

          <button 

            style={{ ...S.btn, ...S.btnXs, background: 'rgba(39,201,90,0.3)', border: '1px solid rgba(39,201,90,0.5)' }} 

            onClick={() => { setShowGraveyard(false); setGraveyardMode('restore'); }}

          >✓ Done</button>

        </Tooltip>

      </div>

      {effectLifecycleErrorBanner}

      

      {/* Mode Toggle */}

      <div style={{ 

        display: 'flex', 

        gap: '4px', 

        marginBottom: '12px',

        padding: '6px',

        background: 'rgba(0,0,0,0.2)',

        borderRadius: '4px',

      }}>

        <span style={{ fontSize: '0.75rem', color: '#888', marginRight: '4px' }}>Mode:</span>

        <button 

          onClick={() => setGraveyardMode('restore')}

          style={{ 

            padding: '3px 10px',

            background: graveyardMode === 'restore' ? 'rgba(39,201,90,0.4)' : 'rgba(0,0,0,0.3)',

            border: `1px solid ${graveyardMode === 'restore' ? 'rgba(39,201,90,0.6)' : 'rgba(255,255,255,0.1)'}`,

            borderRadius: '3px',

            color: graveyardMode === 'restore' ? '#6bff6b' : '#888',

            cursor: 'pointer',

            fontSize: '0.7rem',

          }}

        >↩️ Restore</button>

        <button 

          onClick={() => setGraveyardMode('remove')}

          style={{ 

            padding: '3px 10px',

            background: graveyardMode === 'remove' ? 'rgba(201,39,39,0.4)' : 'rgba(0,0,0,0.3)',

            border: `1px solid ${graveyardMode === 'remove' ? 'rgba(201,39,39,0.6)' : 'rgba(255,255,255,0.1)'}`,

            borderRadius: '3px',

            color: graveyardMode === 'remove' ? '#ff6b6b' : '#888',

            cursor: 'pointer',

            fontSize: '0.7rem',

          }}

        >🗑️ Remove</button>

      </div>

      

      <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '12px' }}>

        {graveyardMode === 'restore' 

          ? 'Click items to restore them to your active buffs/debuffs' 

          : 'Click items to permanently delete them from the graveyard'}

      </div>

      

      {/* Disabled Buffs */}

      {disabledBuffEntries.length > 0 && (

        <div style={{ marginBottom: '12px' }}>

          <div style={{ fontSize: '0.75rem', color: '#27c95a', marginBottom: '4px', opacity: 0.6 }}>💀 Disabled Buffs</div>

          {disabledBuffEntries.map((effect, i) => {

            const actionIcon = graveyardMode === 'remove' ? '🗑️' : '↩️';

            const tooltipText = graveyardMode === 'remove'

              ? `Click to permanently remove${effect.linkedItemName ? `\n(From: ${effect.linkedItemName})` : ''}`

              : `Click to re-enable${effect.linkedItemName ? `\n(From: ${effect.linkedItemName})` : ''}`;

            const pending = isEffectLifecyclePending(effect);

            return (

              <Tooltip key={i} text={tooltipText}>

                <span

                  style={{

                    ...S.badge,

                    ...S.badgeBuff,

                    opacity: pending ? 0.3 : 0.5,

                    textDecoration: 'line-through',

                    cursor: pending ? 'wait' : 'pointer',

                    marginRight: '4px',

                    marginBottom: '4px',

                    border: graveyardMode === 'remove' ? '1px solid rgba(201,39,39,0.5)' : undefined,

                  }}

                  aria-disabled={pending ? 'true' : undefined}

                  onClick={() => { if (!pending) handleGraveyardClick('buff', effect.legacyKey, true, effect, i); }}

                >

                  {effect.text} {actionIcon}

                </span>

              </Tooltip>

            );

          })}

        </div>

      )}

      

      {/* Recently Removed Buffs */}

      {recentlyRemovedBuffs.length > 0 && (

        <div style={{ marginBottom: '12px' }}>

          <div style={{ fontSize: '0.75rem', color: '#27c95a', marginBottom: '4px', opacity: 0.6 }}>🕐 Recently Removed Buffs</div>

          {recentlyRemovedBuffs.map((item, i) => {

            const actionIcon = graveyardMode === 'remove' ? '🗑️' : '↩️';

            const tooltipText = graveyardMode === 'remove' 

              ? 'Click to permanently remove'

              : `Click to restore${item.source ? `\n(Source: ${item.source})` : ''}`;

            return (

              <Tooltip key={i} text={tooltipText}>

                <span 

                  style={{ 

                    ...S.badge, 

                    ...S.badgeBuff, 

                    opacity: 0.5, 

                    cursor: 'pointer',

                    marginRight: '4px',

                    marginBottom: '4px',

                    border: graveyardMode === 'remove' ? '1px solid rgba(201,39,39,0.5)' : undefined,

                  }} 

                  onClick={() => handleGraveyardClick('buff', item.text, false, item, i)}

                >

                  {item.text} {actionIcon}

                </span>

              </Tooltip>

            );

          })}

        </div>

      )}

      

      {/* Disabled Debuffs */}

      {disabledDebuffEntries.length > 0 && (

        <div style={{ marginBottom: '12px' }}>

          <div style={{ fontSize: '0.75rem', color: '#c92727', marginBottom: '4px', opacity: 0.6 }}>💀 Disabled Debuffs</div>

          {disabledDebuffEntries.map((effect, i) => {

            const actionIcon = graveyardMode === 'remove' ? '🗑️' : '↩️';

            const tooltipText = graveyardMode === 'remove'

              ? `Click to permanently remove${effect.linkedItemName ? `\n(From: ${effect.linkedItemName})` : ''}`

              : `Click to re-enable${effect.linkedItemName ? `\n(From: ${effect.linkedItemName})` : ''}`;

            const pending = isEffectLifecyclePending(effect);

            return (

              <Tooltip key={i} text={tooltipText}>

                <span

                  style={{

                    ...S.badge,

                    ...S.badgeDebuff,

                    opacity: pending ? 0.3 : 0.5,

                    textDecoration: 'line-through',

                    cursor: pending ? 'wait' : 'pointer',

                    marginRight: '4px',

                    marginBottom: '4px',

                    border: graveyardMode === 'remove' ? '1px solid rgba(201,39,39,0.5)' : undefined,

                  }}

                  aria-disabled={pending ? 'true' : undefined}

                  onClick={() => { if (!pending) handleGraveyardClick('debuff', effect.legacyKey, true, effect, i); }}

                >

                  {effect.text} {actionIcon}

                </span>

              </Tooltip>

            );

          })}

        </div>

      )}

      

      {/* Recently Removed Debuffs */}

      {recentlyRemovedDebuffs.length > 0 && (

        <div style={{ marginBottom: '12px' }}>

          <div style={{ fontSize: '0.75rem', color: '#c92727', marginBottom: '4px', opacity: 0.6 }}>🕐 Recently Removed Debuffs</div>

          {recentlyRemovedDebuffs.map((item, i) => {

            const actionIcon = graveyardMode === 'remove' ? '🗑️' : '↩️';

            const tooltipText = graveyardMode === 'remove' 

              ? 'Click to permanently remove'

              : `Click to restore${item.source ? `\n(Source: ${item.source})` : ''}`;

            return (

              <Tooltip key={i} text={tooltipText}>

                <span 

                  style={{ 

                    ...S.badge, 

                    ...S.badgeDebuff, 

                    opacity: 0.5, 

                    cursor: 'pointer',

                    marginRight: '4px',

                    marginBottom: '4px',

                    border: graveyardMode === 'remove' ? '1px solid rgba(201,39,39,0.5)' : undefined,

                  }} 

                  onClick={() => handleGraveyardClick('debuff', item.text, false, item, i)}

                >

                  {item.text} {actionIcon}

                </span>

              </Tooltip>

            );

          })}

        </div>

      )}

      

      {totalGraveyardCount === 0 && (

        <p style={{ opacity: 0.5, fontSize: '0.85rem', textAlign: 'center', margin: '20px 0' }}>

          The graveyard is empty. Disabled or recently removed buffs/debuffs will appear here.

        </p>

      )}

      </HoverCard>

    );

  }

  

  // Normal View

  return (

    <HoverCard style={{ overflow: 'visible' }}>

    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', position: 'relative', overflow: 'visible', gap: 8 }}>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
        <h3 style={{ color: '#c9a227', margin: 0, fontSize: '1rem', whiteSpace: 'nowrap' }}>📊 Status</h3>

        {/* 2026-05-11: Inline filter pills — smaller variant of the
            previous below-header strip. Sits between the title and the
            right-side controls so the panel saves a vertical row when
            the user wants filters visible. Visibility is toggled by
            the ▾ button on the right (renders only at 6+ effects). */}
        {showStatusFilterTabs && showStatusFilters && (
          <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center', flex: 1, minWidth: 0 }}>
            <button
              type="button"
              onClick={() => { setExcludedStatusCategories(new Set()); setExcludedStatusPermanence(new Set()); }}
              title="Show everything (clear all filters)"
              style={{
                ...S.btn, ...S.btnXs,
                padding: '1px 7px', fontSize: '0.6rem', marginRight: 0, marginBottom: 0,
                background: !isStatusFiltered ? 'rgba(201,162,39,0.22)' : 'transparent',
                borderColor: !isStatusFiltered ? 'rgba(201,162,39,0.6)' : 'rgba(255,255,255,0.1)',
                color: !isStatusFiltered ? '#f0c240' : '#9a8b7a',
              }}
            >
              All <span style={{ opacity: 0.7, marginLeft: 2 }}>{totalStatusEffectCount}</span>
            </button>
            {STATUS_CATEGORIES.map(cat => {
              const count = statusCategoryCounts[cat.id] || 0;
              if (!count) return null;
              const off = excludedStatusCategories.has(cat.id);
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => toggleStatusCategoryFilter(cat.id)}
                  title={`${cat.label} — click to ${off ? 'show' : 'hide'}`}
                  aria-pressed={!off}
                  style={{
                    ...S.btn, ...S.btnXs,
                    padding: '1px 7px', fontSize: '0.6rem', marginRight: 0, marginBottom: 0,
                    background: off ? 'transparent' : `${cat.color}22`,
                    borderColor: off ? 'rgba(255,255,255,0.08)' : `${cat.color}99`,
                    color: off ? '#5e5266' : cat.color,
                    opacity: off ? 0.6 : 1,
                    textDecoration: off ? 'line-through' : 'none',
                    textDecorationColor: off ? 'rgba(94,82,102,0.5)' : 'transparent',
                  }}
                >
                  <span aria-hidden="true">{cat.icon}</span> {cat.label} <span style={{ opacity: 0.7, marginLeft: 2 }}>{count}</span>
                </button>
              );
            })}
            {permanenceCounts.permanent > 0 && permanenceCounts.temporary > 0 && (
              <>
                <span aria-hidden="true" style={{ alignSelf: 'center', color: '#3a3142', fontSize: '0.65rem' }}>·</span>
                {[
                  { id: 'temporary', label: 'Temp', icon: '⏳', count: permanenceCounts.temporary },
                  { id: 'permanent', label: 'Perm', icon: '⛓', count: permanenceCounts.permanent },
                ].map(p => {
                  const off = excludedStatusPermanence.has(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => toggleStatusPermanenceFilter(p.id)}
                      title={`${p.id === 'temporary' ? 'Temporary' : 'Permanent'} effects — click to ${off ? 'show' : 'hide'}`}
                      aria-pressed={!off}
                      style={{
                        ...S.btn, ...S.btnXs,
                        padding: '1px 7px', fontSize: '0.6rem', marginRight: 0, marginBottom: 0,
                        background: off ? 'transparent' : 'rgba(155,89,182,0.18)',
                        borderColor: off ? 'rgba(255,255,255,0.08)' : 'rgba(155,89,182,0.6)',
                        color: off ? '#5e5266' : '#b388e8',
                        opacity: off ? 0.6 : 1,
                        textDecoration: off ? 'line-through' : 'none',
                        textDecorationColor: off ? 'rgba(94,82,102,0.5)' : 'transparent',
                      }}
                    >
                      <span aria-hidden="true">{p.icon}</span> {p.label} <span style={{ opacity: 0.7, marginLeft: 2 }}>{p.count}</span>
                    </button>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '4px', position: 'relative', flexShrink: 0 }}>

        {allowClickOff && totalGraveyardCount > 0 && (

          <Tooltip text="Show the Buff Graveyard">

            <button 

              style={{ ...S.btn, ...S.btnXs, background: 'rgba(100,100,100,0.3)', position: 'relative' }} 

              onClick={() => setShowGraveyard(true)}

              aria-label="Show Buff Graveyard"

            >

              🪦

              <span style={{ 

                position: 'absolute', 

                top: '-4px', 

                right: '-4px', 

                background: '#666', 

                color: 'white', 

                borderRadius: '50%', 

                width: '14px', 

                height: '14px', 

                fontSize: '0.6rem', 

                display: 'flex', 

                alignItems: 'center', 

                justifyContent: 'center' 

              }}>{totalGraveyardCount}</span>

            </button>

          </Tooltip>

        )}

        {/* 2026-05-11: Filter visibility toggle. Shows/hides the inline
            filter pills (placed next to the Status title). The style
            picker below stays visible regardless — toggling only
            affects the category/permanence filter UI. */}
        {totalStatusEffectCount >= 6 && (
          <Tooltip text={showStatusFilters ? 'Hide filter pills' : 'Show filter pills'}>
            <button
              style={{
                ...S.btn, ...S.btnXs,
                background: showStatusFilters ? 'rgba(201,162,39,0.22)' : 'rgba(100,100,100,0.18)',
                borderColor: showStatusFilters ? 'rgba(201,162,39,0.55)' : undefined,
                color: showStatusFilters ? '#f0c240' : undefined,
              }}
              onClick={() => setShowStatusFilters(s => !s)}
              aria-pressed={showStatusFilters}
              aria-label={showStatusFilters ? 'Hide filter pills' : 'Show filter pills'}
            >🎚</button>
          </Tooltip>
        )}

        {/* 2026-05-10: Group-style picker. Three modes: flat (current
            chip cloud), stacked (category-labeled rows beneath each
            Buffs/Debuffs section), columns (categories side-by-side).
            Active mode highlighted; only renders at 6+ effects. Filter
            still applies on top of any group style. */}
        {totalStatusEffectCount >= 6 && (
          <div style={{ display: 'flex', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 4, padding: 1 }}>
            {[
              { id: 'flat',    icon: '☰', label: 'Flat list (no grouping)' },
              { id: 'stacked', icon: '▤', label: 'Stacked groups under Buffs / Debuffs' },
              { id: 'columns', icon: '▦', label: 'Side-by-side columns under Buffs / Debuffs' },
              { id: 'outline', icon: '◫', label: 'Outline groups (chips flow naturally, rectangular outline per category)' },
              { id: 'step',    icon: '⌐', label: 'Step outline (Tetris-shape that hugs chip silhouette tightly)' },
            ].map(mode => {
              const active = statusGroupStyle === mode.id;
              return (
                <Tooltip key={mode.id} text={mode.label}>
                  <button
                    style={{
                      ...S.btn, ...S.btnXs,
                      padding: '2px 7px', fontSize: '0.78rem', lineHeight: 1,
                      background: active ? 'rgba(212,168,50,0.22)' : 'transparent',
                      borderColor: active ? 'rgba(212,168,50,0.55)' : 'transparent',
                      color: active ? '#f0c240' : '#9a8b7a',
                      marginRight: 0,
                    }}
                    onClick={() => setStatusGroupStyle(mode.id)}
                    aria-label={mode.label}
                    aria-pressed={active}
                  >{mode.icon}</button>
                </Tooltip>
              );
            })}
          </div>
        )}

        {onAddBuff && <button style={{ ...S.btn, ...S.btnXs }} onClick={() => setShowAdd(!showAdd)}>+ Add</button>}

      </div>

      {showAdd && <QuickAddDropdown onAddBuff={onAddBuff} onAddDebuff={onAddDebuff} onAddWithData={handleAddWithData} onClose={() => setShowAdd(false)} />}

    </div>

    {effectLifecycleErrorBanner}
    {effectReconcilePanel}

    {/* 2026-05-11: The filter pill row used to live here below the
        Status header. It now renders INLINE with the title (see the
        showStatusFilters block in the header above). Toggle button (🎚)
        in the header controls visibility. */}

    {/* Confirmation Dialog */}

    {confirmRemove && (

      <div style={{ 

        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 

        background: 'rgba(0,0,0,0.7)', zIndex: 3000,

        display: 'flex', alignItems: 'center', justifyContent: 'center',

      }} onClick={() => setConfirmRemove(null)}>

        <div style={{ 

          background: 'rgba(40,30,20,0.98)', 

          border: '2px solid #c9a227', 

          borderRadius: '8px', 

          padding: '20px',

          maxWidth: '380px',

          textAlign: 'center',

        }} onClick={e => e.stopPropagation()}>

          <div style={{ fontSize: '1.5rem', marginBottom: '12px' }}>⚠️</div>

          <div style={{ marginBottom: '12px', color: '#e8d5b7' }}>

            <strong>{confirmRemove.text}</strong> is linked to equipped item <strong style={{ color: '#c9a227' }}>{confirmRemove.itemName}</strong>

          </div>

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>

            <button 

              disabled={confirmRemovePending}

              style={{ ...S.btn, ...S.btnSm, background: 'rgba(100,100,100,0.4)', opacity: confirmRemovePending ? 0.55 : 1, cursor: confirmRemovePending ? 'wait' : 'pointer' }}

              onClick={() => { if (!confirmRemovePending) performRemove(confirmRemove.type, confirmRemove.index, confirmRemove.text, confirmRemove.isPermanent, true, confirmRemove.effect); }}

            >🪦 Send to Graveyard</button>

            <button 

              disabled={confirmRemovePending}

              style={{ ...S.btn, ...S.btnDanger, ...S.btnSm, opacity: confirmRemovePending ? 0.55 : 1, cursor: confirmRemovePending ? 'wait' : 'pointer' }}

              onClick={() => { if (!confirmRemovePending) performRemove(confirmRemove.type, confirmRemove.index, confirmRemove.text, confirmRemove.isPermanent, false, confirmRemove.effect); }}

            >🗑️ Remove</button>

          </div>

          <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '12px', lineHeight: '1.4' }}>

            <strong>Graveyard:</strong> Disable but keep in graveyard for easy restore.<br/>

            <strong>Remove:</strong> Fully remove. Re-equipping item will restore it.

          </div>

          <button 

            style={{ ...S.btn, ...S.btnSm }}

            onClick={() => setConfirmRemove(null)}

          >Cancel</button>

        </div>

      </div>

    )}

    

    {/* 2026-05-10: Hunger/Fed banners removed in favor of synthesized
        chip entries (Well Fed → Diet buff chip; Hungry / Starving →
        Diet debuff chips). See `syntheticBuffs` / `syntheticDebuffs`
        injection at the visibleBuffEntries computation above. Kept
        the underlying wellFed/hungry/starving variables since later
        view-conditional logic still references them. */}

    {!hasBuffs && !hasAnyDebuffs && !hasDiseases && !hasSystems && !wellFed && !hungry && !starving && <p style={{ opacity: 0.6, fontSize: '0.9rem', margin: 0 }}>No active conditions</p>}

    

    {/* 2026-05-10: Filter-aware empty state. When the user has excluded
        every category that contains chips, both filtered lists are
        empty. Surface that distinct from a true zero-state. */}
    {isStatusFiltered && filteredBuffEntries.length === 0 && filteredDebuffEntries.length === 0 && (hasBuffs || hasAnyDebuffs) && (
      <p style={{ opacity: 0.5, fontSize: '0.85rem', margin: '4px 0' }}>
        All categories filtered out. Click <strong>All</strong> to reset.
      </p>
    )}

    {/* 2026-05-10: STACKED grouping. Buffs and Debuffs each keep their
        own labeled section header (matching the flat view's structure);
        within each, chips are organized into sub-cards by source
        category. Buff/debuff stay visually distinct (separate sections)
        but within each, you can scan by category. */}
    {statusGroupStyle === 'stacked' && (hasBuffs || hasAnyDebuffs) && (
      <>
        {hasBuffs && filteredBuffEntries.length > 0 && (
          <div style={{ marginBottom: 8 }} onClick={() => actionBuff?.type === 'buff' && setActionBuff(null)}>
            <div style={{ fontSize: '0.8rem', color: '#27c95a', marginBottom: 6 }}>✨ Buffs</div>
            {orderedGroupedCategories.map(cat => {
              const catBuffs = filteredBuffEntries.filter(e => categorizeStatusEffect(e) === cat.id);
              if (catBuffs.length === 0) return null;
              return (
                <div key={`b-${cat.id}`} style={{
                  border: `1px solid ${cat.color}33`,
                  background: `${cat.color}08`,
                  borderRadius: 5,
                  padding: '5px 8px 6px',
                  marginBottom: 5,
                }}>
                  <div style={{
                    fontSize: '0.6rem', color: cat.color, marginBottom: 4,
                    fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase',
                    display: 'flex', alignItems: 'center', gap: 5,
                  }}>
                    <span aria-hidden="true">{cat.icon}</span>
                    <span>{cat.label}</span>
                    <span style={{ marginLeft: 'auto', opacity: 0.5, fontWeight: 400, letterSpacing: 0 }}>{catBuffs.length}</span>
                  </div>
                  {catBuffs.map(e => renderStatusBuffBadge(e, 'buff'))}
                </div>
              );
            })}
          </div>
        )}
        {hasAnyDebuffs && filteredDebuffEntries.length > 0 && (
          <div style={{ marginBottom: 8 }} onClick={() => actionBuff?.type === 'debuff' && setActionBuff(null)}>
            <div style={{ fontSize: '0.8rem', color: '#c92727', marginBottom: 6 }}>⚠️ Debuffs</div>
            {orderedGroupedCategories.map(cat => {
              const catDebuffs = filteredDebuffEntries.filter(e => categorizeStatusEffect(e) === cat.id);
              if (catDebuffs.length === 0) return null;
              return (
                <div key={`d-${cat.id}`} style={{
                  border: `1px solid ${cat.color}33`,
                  background: `${cat.color}08`,
                  borderRadius: 5,
                  padding: '5px 8px 6px',
                  marginBottom: 5,
                }}>
                  <div style={{
                    fontSize: '0.6rem', color: cat.color, marginBottom: 4,
                    fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase',
                    display: 'flex', alignItems: 'center', gap: 5,
                  }}>
                    <span aria-hidden="true">{cat.icon}</span>
                    <span>{cat.label}</span>
                    <span style={{ marginLeft: 'auto', opacity: 0.5, fontWeight: 400, letterSpacing: 0 }}>{catDebuffs.length}</span>
                  </div>
                  {catDebuffs.map(e => renderStatusBuffBadge(e, 'debuff'))}
                </div>
              );
            })}
          </div>
        )}
      </>
    )}

    {/* 2026-05-10: COLUMNS grouping. Same Buffs/Debuffs separation as
        stacked, but categories sit side-by-side as auto-fitting grid
        columns. Categories ordered by buff-count descending, applied
        consistently to BOTH Buffs and Debuffs sections so left-to-right
        position is stable (the leftmost column is always your biggest
        buff source — easy muscle memory). Auto-fit minmax(140px) lets
        narrow panels collapse columns onto a second row gracefully. */}
    {statusGroupStyle === 'columns' && (hasBuffs || hasAnyDebuffs) && (
      <>
        {hasBuffs && filteredBuffEntries.length > 0 && (
          <div style={{ marginBottom: 10 }} onClick={() => actionBuff?.type === 'buff' && setActionBuff(null)}>
            <div style={{ fontSize: '0.8rem', color: '#27c95a', marginBottom: 6 }}>✨ Buffs</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 6 }}>
              {orderedGroupedCategories.map(cat => {
                const catBuffs = filteredBuffEntries.filter(e => categorizeStatusEffect(e) === cat.id);
                if (catBuffs.length === 0) return null;
                return (
                  <div key={`bc-${cat.id}`} style={{
                    border: `1px solid ${cat.color}33`,
                    background: `${cat.color}08`,
                    borderRadius: 5,
                    padding: '5px 7px 7px',
                    minWidth: 0,
                  }}>
                    <div style={{
                      fontSize: '0.58rem', color: cat.color, marginBottom: 5,
                      fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase',
                      display: 'flex', alignItems: 'center', gap: 4,
                      borderBottom: `1px solid ${cat.color}22`, paddingBottom: 3,
                    }}>
                      <span aria-hidden="true">{cat.icon}</span>
                      <span>{cat.label}</span>
                      <span style={{ marginLeft: 'auto', opacity: 0.5, fontWeight: 400, letterSpacing: 0 }}>{catBuffs.length}</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start' }}>
                      {catBuffs.map(e => renderStatusBuffBadge(e, 'buff'))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {hasAnyDebuffs && filteredDebuffEntries.length > 0 && (
          <div style={{ marginBottom: 10 }} onClick={() => actionBuff?.type === 'debuff' && setActionBuff(null)}>
            <div style={{ fontSize: '0.8rem', color: '#c92727', marginBottom: 6 }}>⚠️ Debuffs</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 6 }}>
              {orderedGroupedCategories.map(cat => {
                const catDebuffs = filteredDebuffEntries.filter(e => categorizeStatusEffect(e) === cat.id);
                if (catDebuffs.length === 0) return null;
                return (
                  <div key={`dc-${cat.id}`} style={{
                    border: `1px solid ${cat.color}33`,
                    background: `${cat.color}08`,
                    borderRadius: 5,
                    padding: '5px 7px 7px',
                    minWidth: 0,
                  }}>
                    <div style={{
                      fontSize: '0.58rem', color: cat.color, marginBottom: 5,
                      fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase',
                      display: 'flex', alignItems: 'center', gap: 4,
                      borderBottom: `1px solid ${cat.color}22`, paddingBottom: 3,
                    }}>
                      <span aria-hidden="true">{cat.icon}</span>
                      <span>{cat.label}</span>
                      <span style={{ marginLeft: 'auto', opacity: 0.5, fontWeight: 400, letterSpacing: 0 }}>{catDebuffs.length}</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start' }}>
                      {catDebuffs.map(e => renderStatusBuffBadge(e, 'debuff'))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </>
    )}

    {/* 2026-05-10: OUTLINE grouping. Chips flow naturally on the page,
        sorted by group; each group is wrapped in a thin "fieldset" outline
        that hugs the chip cluster (not a fixed-width card). The category
        label sits on the top border like a fieldset legend. Multiple
        groups can fit on the same row when their chips are short, or
        stack vertically when long. Buffs and Debuffs keep their existing
        section headers. */}
    {statusGroupStyle === 'outline' && (hasBuffs || hasAnyDebuffs) && (
      <>
        {hasBuffs && filteredBuffEntries.length > 0 && (
          <div style={{ marginBottom: 10 }} onClick={() => actionBuff?.type === 'buff' && setActionBuff(null)}>
            <div style={{ fontSize: '0.8rem', color: '#27c95a', marginBottom: 8 }}>✨ Buffs</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-start' }}>
              {orderedGroupedCategories.map(cat => {
                const catBuffs = filteredBuffEntries.filter(e => categorizeStatusEffect(e) === cat.id);
                if (catBuffs.length === 0) return null;
                return (
                  <div key={`bo-${cat.id}`} style={{
                    display: 'inline-flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 3,
                    padding: '10px 8px 5px 8px',
                    border: `1px solid ${cat.color}55`,
                    background: `${cat.color}06`,
                    borderRadius: 4,
                    position: 'relative',
                    maxWidth: '100%',
                  }}>
                    <span style={{
                      position: 'absolute',
                      top: -7,
                      left: 7,
                      padding: '0 5px',
                      background: 'var(--bg, rgba(14,10,15,1))',
                      fontSize: '0.55rem',
                      color: cat.color,
                      fontWeight: 700,
                      letterSpacing: 0.6,
                      textTransform: 'uppercase',
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                    }}>
                      <span aria-hidden="true">{cat.icon}</span> {cat.label}
                    </span>
                    {catBuffs.map(e => renderStatusBuffBadge(e, 'buff'))}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {hasAnyDebuffs && filteredDebuffEntries.length > 0 && (
          <div style={{ marginBottom: 10 }} onClick={() => actionBuff?.type === 'debuff' && setActionBuff(null)}>
            <div style={{ fontSize: '0.8rem', color: '#c92727', marginBottom: 8 }}>⚠️ Debuffs</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-start' }}>
              {orderedGroupedCategories.map(cat => {
                const catDebuffs = filteredDebuffEntries.filter(e => categorizeStatusEffect(e) === cat.id);
                if (catDebuffs.length === 0) return null;
                return (
                  <div key={`do-${cat.id}`} style={{
                    display: 'inline-flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 3,
                    padding: '10px 8px 5px 8px',
                    border: `1px solid ${cat.color}55`,
                    background: `${cat.color}06`,
                    borderRadius: 4,
                    position: 'relative',
                    maxWidth: '100%',
                  }}>
                    <span style={{
                      position: 'absolute',
                      top: -7,
                      left: 7,
                      padding: '0 5px',
                      background: 'var(--bg, rgba(14,10,15,1))',
                      fontSize: '0.55rem',
                      color: cat.color,
                      fontWeight: 700,
                      letterSpacing: 0.6,
                      textTransform: 'uppercase',
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                    }}>
                      <span aria-hidden="true">{cat.icon}</span> {cat.label}
                    </span>
                    {catDebuffs.map(e => renderStatusBuffBadge(e, 'debuff'))}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </>
    )}

    {/* 2026-05-10c: STEP grouping (puzzle-flow rewrite). All chips
        sorted by category in a single flex-wrap container, with thin
        spacers between categories for visible separation. After
        layout, each category's outline is drawn around wherever its
        chips landed — chips that wrap mid-category get an L-shaped
        outline; categories sharing a row get tight side-by-side
        outlines. No CSS float / shape-outside trickery; just measure
        actual positions and draw to match. */}
    {statusGroupStyle === 'step' && (hasBuffs || hasAnyDebuffs) && (
      <>
        {hasBuffs && filteredBuffEntries.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: '0.8rem', color: '#27c95a', marginBottom: 8 }}>✨ Buffs</div>
            <StatusPuzzleFlowView
              chips={filteredBuffEntries}
              kind="buff"
              renderBadge={renderStatusBuffBadge}
              categoriesById={orderedGroupedCategories}
              getCategoryFor={categorizeStatusEffect}
              onContainerClick={() => actionBuff?.type === 'buff' && setActionBuff(null)}
            />
          </div>
        )}
        {hasAnyDebuffs && filteredDebuffEntries.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: '0.8rem', color: '#c92727', marginBottom: 8 }}>⚠️ Debuffs</div>
            <StatusPuzzleFlowView
              chips={filteredDebuffEntries}
              kind="debuff"
              renderBadge={renderStatusBuffBadge}
              categoriesById={orderedGroupedCategories}
              getCategoryFor={categorizeStatusEffect}
              onContainerClick={() => actionBuff?.type === 'debuff' && setActionBuff(null)}
            />
          </div>
        )}
      </>
    )}

    {/* Buff section gate: when "All", render whenever hasBuffs is true
        (so the hidden-buffs / disabled-buffs / graveyard sub-toggles
        still mount even if visibleBuffEntries is empty). When a
        category filter is active, only render if there's at least one
        matching visible buff — otherwise the section is just an empty
        header. */}
    {!useGroupedStatusLayout && hasBuffs && (!isStatusFiltered || filteredBuffEntries.length > 0) && <div style={{ marginBottom: '8px', position: 'relative' }} onClick={() => actionBuff?.type === 'buff' && setActionBuff(null)}>

      <div style={{ fontSize: '0.8rem', color: '#27c95a', marginBottom: '4px' }}>✨ Buffs</div>

      {filteredBuffEntries.map((effect) => {

        const isAction = actionBuff?.effectId
          ? actionBuff.effectId === effect.effectId
          : actionBuff?.type === 'buff'

          && actionBuff?.text === effect.text

          && actionBuff?.index === effect.listIndex

          && actionBuff?.isPermanent === effect.isPermanent;

        return <BuffBadge

          key={effect.effectId}

          text={effect.text}

          source={effect.source}
          sourceType={effect.sourceType}
          sourceMode={effect.sourceMode}
          sourceLabel={effect.sourceLabel}

          playerTooltip={effect.playerTooltip}

          colorId={effect.colorId}

          isPermanent={effect.isPermanent}

          isDisabled={effect.isDisabled}

          linkedItemName={effect.linkedItemName}

          glowColor={effect.glowColor}

          onClearGlow={effect.glowColor && onClearBuffGlow ? () => onClearBuffGlow(effect.legacyKey) : undefined}

          onXClick={allowClickOff && onUpdate ? () => handleXClick('buff', effect.listIndex, effect.text, effect.isPermanent, effect) : undefined}

          showActions={isAction}

          onRemove={() => { setActionBuff(null); handleRemove('buff', effect.listIndex, effect.text, effect.isPermanent, effect); }}

          onHide={() => hideEffect('buff', effect.legacyKey, effect)}

          isPlayer={isPlayer}

          noteVisible={effect.noteVisible}

          isPending={isEffectLifecyclePending(effect)}

        />;

      })}

      {/* 2026-04-29: iterate over the unfiltered list and skip hidden by

          returning null inside the map. This way `origIdx` is the actual

          position in the array, not `indexOf(b)` which always returns the

          FIRST match — that bug conflated duplicate-name buffs (e.g. two

          "Observed" debuffs would both show the action menu and remove

          together because both got origIdx=0). The action-match also

          checks index now so the menu only opens on the actual clicked

          instance. */}

      {false && (char.permanentBuffs || []).map((b, origIdx) => {

        // 2026-04-29: per-instance key drives every state lookup

        // (hidden, disabled, glow, buffData) so duplicate-name buffs

        // act independently. Computed first so the hidden filter uses

        // the same per-instance shape as everything below.

        const occ = getGlobalOccurrence(b, origIdx, char.permanentBuffs || []);

        const instanceKey = buffInstanceKey(b, occ);

        if (hiddenBuffs.includes(instanceKey)) return null;

        const itemLinked = isLinkedToEquippedItem(b);

        const isDisabled = disabledBuffs.includes(instanceKey);

        const buffInfo = lookupBuffData(b, occ, char.buffData) || {};

        const glowData = glowingBuffs[instanceKey];

        const isAction = actionBuff?.type === 'buff' && actionBuff?.text === b && actionBuff?.index === origIdx && actionBuff?.isPermanent;

        return <BuffBadge

          key={'pb'+origIdx}

          text={b}

          source={buffInfo.source || char.buffSources?.[b]}

          playerTooltip={buffInfo.playerTooltip}

          note={char.buffNotes?.[b]}

          colorId={buffInfo.color}

          isPermanent

          isDisabled={isDisabled}

          linkedItemName={itemLinked}

          glowColor={glowData?.color}

          onClearGlow={glowData && onClearBuffGlow ? () => onClearBuffGlow(instanceKey) : undefined}

          onXClick={allowClickOff && onUpdate ? () => handleXClick('buff', origIdx, b, true) : undefined}

          showActions={isAction}

          onRemove={() => { setActionBuff(null); handleRemove('buff', origIdx, b, true); }}

          onHide={() => hideEffect('buff', instanceKey)}

          isPlayer={isPlayer}

          noteVisible={buffInfo.noteVisible}

        />;

      })}

      {false && (char.buffList || []).map((b, origIdx) => {

        const occ = getGlobalOccurrence(b, origIdx, char.buffList || [], char.permanentBuffs || []);

        const instanceKey = buffInstanceKey(b, occ);

        if (hiddenBuffs.includes(instanceKey)) return null;

        const itemLinked = isLinkedToEquippedItem(b);

        const isDisabled = disabledBuffs.includes(instanceKey);

        const buffInfo = lookupBuffData(b, occ, char.buffData) || {};

        const glowData = glowingBuffs[instanceKey];

        const isAction = actionBuff?.type === 'buff' && actionBuff?.text === b && actionBuff?.index === origIdx && !actionBuff?.isPermanent;

        return <BuffBadge

          key={'b'+origIdx}

          text={b}

          source={buffInfo.source || char.buffSources?.[b]}

          playerTooltip={buffInfo.playerTooltip}

          note={char.buffNotes?.[b]}

          colorId={buffInfo.color}

          isDisabled={isDisabled}

          linkedItemName={itemLinked}

          glowColor={glowData?.color}

          onClearGlow={glowData && onClearBuffGlow ? () => onClearBuffGlow(instanceKey) : undefined}

          onXClick={allowClickOff && onUpdate ? () => handleXClick('buff', origIdx, b, false) : undefined}

          showActions={isAction}

          onRemove={() => { setActionBuff(null); handleRemove('buff', origIdx, b, false); }}

          onHide={() => hideEffect('buff', instanceKey)}

          isPlayer={isPlayer}

          noteVisible={buffInfo.noteVisible}

        />;

      })}

      {/* Equipped item combat bonuses (buff-type) */}

      {equippedCombatBonuses.filter(b => b.isBuff).map((b, i) => (

        <Tooltip key={'ecb-buff-'+i} text={<div style={{ fontSize: '0.7rem', minWidth: '180px' }}>

          <div style={{ color: '#8b8bff', fontSize: '0.65rem', marginBottom: '4px' }}>⚙ Auto-applied {b.type === 'advantage' ? 'adv/dis' : b.type === 'resistance' ? 'resistance' : 'bonus'}</div>

          <div style={{ color: '#f59e0b', whiteSpace: 'nowrap' }}>⚠️ {b.itemName}</div>

        </div>} special borderColor="#27c95a">

          <span style={{ ...S.badge, ...S.badgeBuff, display: 'inline-flex', alignItems: 'center', gap: '3px', boxShadow: 'inset 0 0 0 1px rgba(39,201,90,0.5)', cursor: 'help' }}>

            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#2d1b69', display: 'inline-block', flexShrink: 0 }} />

            {b.text}

          </span>

        </Tooltip>

      ))}

      {/* Hidden buffs toggle - always at end */}

      {hiddenBuffEntries.length > 0 && <>

        <span

          onClick={(e) => { e.stopPropagation(); setShowHiddenBuffs(!showHiddenBuffs); }}

          style={{ ...S.badge, background: 'rgba(80,80,80,0.12)', border: '1px solid rgba(80,80,80,0.25)', color: '#555', cursor: 'pointer', fontSize: '0.55rem', padding: '1px 5px' }}

        >

          {hiddenBuffEntries.length} hidden {showHiddenBuffs ? 'v' : '>'}

        </span>

        {showHiddenBuffs && hiddenBuffEntries.map((effect, i) => (

          <span key={effect.effectId || ('hb'+i)} style={{ ...S.badge, ...S.badgeBuff, opacity: isEffectLifecyclePending(effect) ? 0.25 : 0.4, fontStyle: 'italic', cursor: isEffectLifecyclePending(effect) ? 'wait' : 'pointer', fontSize: '0.65rem' }} onClick={(ev) => { ev.stopPropagation(); if (!isEffectLifecyclePending(effect)) unhideEffect('buff', effect.legacyKey, effect); }} title="Click to unhide">

            {effect.text}

          </span>

        ))}

      </>}

      {false && (() => {

        // 2026-04-29: build per-instance entries so the hidden filter

        // matches the per-instance keys stored in hiddenBuffs (each

        // duplicate of a name has its own occurrence-suffixed entry).

        const allBuffEntries = [

          ...(char.permanentBuffs || []).map((b, idx) => ({

            b, occ: getGlobalOccurrence(b, idx, char.permanentBuffs || []),

          })),

          ...(char.buffList || []).map((b, idx) => ({

            b, occ: getGlobalOccurrence(b, idx, char.buffList || [], char.permanentBuffs || []),

          })),

        ];

        const hiddenEntries = allBuffEntries.filter(e => hiddenBuffs.includes(buffInstanceKey(e.b, e.occ)));

        const hiddenCount = hiddenEntries.length;

        if (hiddenCount === 0) return null;

        return <>

          <span

            onClick={(e) => { e.stopPropagation(); setShowHiddenBuffs(!showHiddenBuffs); }}

            style={{ ...S.badge, background: 'rgba(80,80,80,0.12)', border: '1px solid rgba(80,80,80,0.25)', color: '#555', cursor: 'pointer', fontSize: '0.55rem', padding: '1px 5px' }}

          >

            {hiddenCount} hidden {showHiddenBuffs ? '▾' : '▸'}

          </span>

          {showHiddenBuffs && hiddenEntries.map((e, i) => {

            const k = buffInstanceKey(e.b, e.occ);

            return (

              <span key={'hb'+i} style={{ ...S.badge, ...S.badgeBuff, opacity: 0.4, fontStyle: 'italic', cursor: 'pointer', fontSize: '0.65rem' }} onClick={(ev) => { ev.stopPropagation(); unhideEffect('buff', k); }} title="Click to unhide">

                {e.b}

              </span>

            );

          })}

        </>;

      })()}

    </div>}

    

    {/* Debuff section gate — same logic as buff section above. */}
    {!useGroupedStatusLayout && hasAnyDebuffs && (!isStatusFiltered || filteredDebuffEntries.length > 0) && <div style={{ marginBottom: '8px', position: 'relative' }} onClick={() => actionBuff?.type === 'debuff' && setActionBuff(null)}>

      <div style={{ fontSize: '0.8rem', color: '#c92727', marginBottom: '4px' }}>⚠️ Debuffs</div>

      {/* 2026-04-29: same fix as buff iteration above — iterate unfiltered

          so origIdx is correct for duplicate-name debuffs. */}

      {filteredDebuffEntries.map((effect) => {

        const isAction = actionBuff?.effectId
          ? actionBuff.effectId === effect.effectId
          : actionBuff?.type === 'debuff'

          && actionBuff?.text === effect.text

          && actionBuff?.index === effect.listIndex

          && actionBuff?.isPermanent === effect.isPermanent;

        return <BuffBadge

          key={effect.effectId}

          text={effect.text}

          source={effect.source}
          sourceType={effect.sourceType}
          sourceMode={effect.sourceMode}
          sourceLabel={effect.sourceLabel}

          playerTooltip={effect.playerTooltip}

          colorId={effect.colorId}

          isPermanent={effect.isPermanent}

          isDebuff

          isDisabled={effect.isDisabled}

          linkedItemName={effect.linkedItemName}

          glowColor={effect.glowColor}

          onClearGlow={effect.glowColor && onClearBuffGlow ? () => onClearBuffGlow(effect.legacyKey) : undefined}

          onXClick={allowClickOff && onUpdate ? () => handleXClick('debuff', effect.listIndex, effect.text, effect.isPermanent, effect) : undefined}

          showActions={isAction}

          onRemove={() => { setActionBuff(null); handleRemove('debuff', effect.listIndex, effect.text, effect.isPermanent, effect); }}

          onHide={() => hideEffect('debuff', effect.legacyKey, effect)}

          isPlayer={isPlayer}

          noteVisible={effect.noteVisible}

          isPending={isEffectLifecyclePending(effect)}

        />;

      })}

      {false && (char.permanentDebuffs || []).map((d, origIdx) => {

        const occ = getGlobalOccurrence(d, origIdx, char.permanentDebuffs || [], char.permanentBuffs || [], char.buffList || []);

        const instanceKey = buffInstanceKey(d, occ);

        if (hiddenDebuffs.includes(instanceKey)) return null;

        const itemLinked = isLinkedToEquippedItem(d);

        const isDisabled = disabledDebuffs.includes(instanceKey);

        const debuffInfo = lookupBuffData(d, occ, char.buffData) || {};

        const glowData = glowingBuffs[instanceKey];

        const isAction = actionBuff?.type === 'debuff' && actionBuff?.text === d && actionBuff?.index === origIdx && actionBuff?.isPermanent;

        return <BuffBadge

          key={'pd'+origIdx}

          text={d}

          source={debuffInfo.source || char.buffSources?.[d]}

          playerTooltip={debuffInfo.playerTooltip}

          note={char.buffNotes?.[d]}

          colorId={debuffInfo.color}

          isPermanent

          isDebuff

          isDisabled={isDisabled}

          linkedItemName={itemLinked}

          glowColor={glowData?.color}

          onClearGlow={glowData && onClearBuffGlow ? () => onClearBuffGlow(instanceKey) : undefined}

          onXClick={allowClickOff && onUpdate ? () => handleXClick('debuff', origIdx, d, true) : undefined}

          showActions={isAction}

          onRemove={() => { setActionBuff(null); handleRemove('debuff', origIdx, d, true); }}

          onHide={() => hideEffect('debuff', instanceKey)}

          isPlayer={isPlayer}

          noteVisible={debuffInfo.noteVisible}

        />;

      })}

      {false && (char.debuffList || []).map((d, origIdx) => {

        const occ = getGlobalOccurrence(d, origIdx, char.debuffList || [], char.permanentBuffs || [], char.buffList || [], char.permanentDebuffs || []);

        const instanceKey = buffInstanceKey(d, occ);

        if (hiddenDebuffs.includes(instanceKey)) return null;

        const itemLinked = isLinkedToEquippedItem(d);

        const isDisabled = disabledDebuffs.includes(instanceKey);

        const debuffInfo = lookupBuffData(d, occ, char.buffData) || {};

        const glowData = glowingBuffs[instanceKey];

        const isAction = actionBuff?.type === 'debuff' && actionBuff?.text === d && actionBuff?.index === origIdx && !actionBuff?.isPermanent;

        return <BuffBadge

          key={'d'+origIdx}

          text={d}

          source={debuffInfo.source || char.buffSources?.[d]}

          playerTooltip={debuffInfo.playerTooltip}

          note={char.buffNotes?.[d]}

          colorId={debuffInfo.color}

          isDebuff

          isDisabled={isDisabled}

          linkedItemName={itemLinked}

          glowColor={glowData?.color}

          onClearGlow={glowData && onClearBuffGlow ? () => onClearBuffGlow(instanceKey) : undefined}

          onXClick={allowClickOff && onUpdate ? () => handleXClick('debuff', origIdx, d, false) : undefined}

          showActions={isAction}

          onRemove={() => { setActionBuff(null); handleRemove('debuff', origIdx, d, false); }}

          onHide={() => hideEffect('debuff', instanceKey)}

          isPlayer={isPlayer}

          noteVisible={debuffInfo.noteVisible}

        />;

      })}

      {/* Equipped item combat bonuses (debuff-type) */}

      {equippedCombatBonuses.filter(b => !b.isBuff).map((b, i) => (

        <Tooltip key={'ecb-debuff-'+i} text={<div style={{ fontSize: '0.7rem', minWidth: '180px' }}>

          <div style={{ color: '#8b8bff', fontSize: '0.65rem', marginBottom: '4px' }}>⚙ Auto-applied {b.type === 'disadvantage' ? 'adv/dis' : 'effect'}</div>

          <div style={{ color: '#f59e0b', whiteSpace: 'nowrap' }}>⚠️ {b.itemName}</div>

        </div>} special borderColor="#c92727">

          <span style={{ ...S.badge, ...S.badgeDebuff, display: 'inline-flex', alignItems: 'center', gap: '3px', boxShadow: 'inset 0 0 0 1px rgba(201,39,39,0.5)', cursor: 'help' }}>

            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#2d1b69', display: 'inline-block', flexShrink: 0 }} />

            {b.text}

          </span>

        </Tooltip>

      ))}

      {/* Disease effects shown in purple */}

      {visibleDiseaseEffects.map((de, i) => (

        <Tooltip key={'de'+i} text={`From: ${de.diseaseName}`}>

          <span style={{ ...S.badge, ...S.badgeDisease, marginRight: '4px', marginBottom: '4px', display: 'inline-flex', alignItems: 'center', gap: 3 }}><BiohazardIcon size={12} color="#d47dd4" /> {de.effect}</span>

        </Tooltip>

      ))}

      {/* Hidden debuffs toggle - always at very end */}

      {hiddenDebuffEntries.length > 0 && <>

        <span

          onClick={(e) => { e.stopPropagation(); setShowHiddenDebuffs(!showHiddenDebuffs); }}

          style={{ ...S.badge, background: 'rgba(80,80,80,0.12)', border: '1px solid rgba(80,80,80,0.25)', color: '#555', cursor: 'pointer', fontSize: '0.55rem', padding: '1px 5px' }}

        >

          {hiddenDebuffEntries.length} hidden {showHiddenDebuffs ? 'v' : '>'}

        </span>

        {showHiddenDebuffs && hiddenDebuffEntries.map((effect, i) => (

          <span key={effect.effectId || ('hd'+i)} style={{ ...S.badge, ...S.badgeDebuff, opacity: isEffectLifecyclePending(effect) ? 0.25 : 0.4, fontStyle: 'italic', cursor: isEffectLifecyclePending(effect) ? 'wait' : 'pointer', fontSize: '0.65rem' }} onClick={(ev) => { ev.stopPropagation(); if (!isEffectLifecyclePending(effect)) unhideEffect('debuff', effect.legacyKey, effect); }} title="Click to unhide">

            {effect.text}

          </span>

        ))}

      </>}

      {false && (() => {

        const allDebuffEntries = [

          ...(char.permanentDebuffs || []).map((d, idx) => ({

            d, occ: getGlobalOccurrence(d, idx, char.permanentDebuffs || [], char.permanentBuffs || [], char.buffList || []),

          })),

          ...(char.debuffList || []).map((d, idx) => ({

            d, occ: getGlobalOccurrence(d, idx, char.debuffList || [], char.permanentBuffs || [], char.buffList || [], char.permanentDebuffs || []),

          })),

        ];

        const hiddenEntries = allDebuffEntries.filter(e => hiddenDebuffs.includes(buffInstanceKey(e.d, e.occ)));

        const hiddenCount = hiddenEntries.length;

        if (hiddenCount === 0) return null;

        return <>

          <span

            onClick={(e) => { e.stopPropagation(); setShowHiddenDebuffs(!showHiddenDebuffs); }}

            style={{ ...S.badge, background: 'rgba(80,80,80,0.12)', border: '1px solid rgba(80,80,80,0.25)', color: '#555', cursor: 'pointer', fontSize: '0.55rem', padding: '1px 5px' }}

          >

            {hiddenCount} hidden {showHiddenDebuffs ? '▾' : '▸'}

          </span>

          {showHiddenDebuffs && hiddenEntries.map((e, i) => {

            const k = buffInstanceKey(e.d, e.occ);

            return (

              <span key={'hd'+i} style={{ ...S.badge, ...S.badgeDebuff, opacity: 0.4, fontStyle: 'italic', cursor: 'pointer', fontSize: '0.65rem' }} onClick={(ev) => { ev.stopPropagation(); unhideEffect('debuff', k); }} title="Click to unhide">

                {e.d}

              </span>

            );

          })}

        </>;

      })()}

    </div>}

      {/* System effects shown in blue/purple - mystery effects have special styling */}

      {visibleSystemEffects.map((se, i) => {

        // Determine if this is a buff or debuff based on effect text

        const isBuff = se.effect.match(/^\+\d+/);

        const isDebuff = se.effect.match(/^-\d+/);

        

        // Choose badge style based on mystery status and buff/debuff type

        let badgeStyle;

        if (se.isMystery) {

          if (isBuff) badgeStyle = S.badgeMysteryBuff;

          else if (isDebuff) badgeStyle = S.badgeMysteryDebuff;

          else badgeStyle = S.badgeMystery;

        } else if (se.isHidden) {

          badgeStyle = S.badgeUnknown;

        } else {

          badgeStyle = S.badgeSystem;

        }

        

        return (

          <Tooltip key={'se'+i} text={se.isMystery ? 'Source: ? (Mystery effect)' : (se.isHidden ? 'Source: Unknown' : `From: ${se.systemName}`)}>

            <span style={{ 

              ...S.badge, 

              ...badgeStyle, 

              marginRight: '4px', 

              marginBottom: '4px' 

            }}>

              {se.isMystery ? '✨' : (se.isHidden ? '❓' : '🔒')} {se.effect}

            </span>

          </Tooltip>

        );

      })}



    

    {hasDiseases && <div>

      <div style={{ fontSize: '0.8rem', color: '#d47dd4', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: 4 }}><BiohazardIcon size={14} color="#d47dd4" /> Conditions</div>

      {(char.diseaseList || []).map((d, i) => (

        <DiseaseBadge key={i} diseaseData={d} disease={diseases[d.diseaseId]} isPlayer={isPlayer} />

      ))}

    </div>}



    {hasSystems && <div style={{ marginTop: '8px' }}>

      <div style={{ fontSize: '0.8rem', color: '#b0b0ff', marginBottom: '4px' }}>🔒 Systems</div>

      {visibleSystemList.map((s, i) => {

        const instanceId = s.instanceId || s.id || s.systemId;

        const glowInfo = glowingSystems[instanceId];

        return (

          <SystemBadge

            key={s.id || s.systemId || i}

            systemData={s}

            system={(systems && s.systemId && systems[s.systemId]) || s.template}

            isPlayer={isPlayer}

            glowColor={glowInfo?.color || null}

            onClearGlow={glowInfo && onClearSystemGlow ? () => onClearSystemGlow(instanceId) : null}

            char={char}

          />

        );

      })}

    </div>}

    </HoverCard>

  );

}



// ============== EQUIPMENT DURABILITY PANEL ==============

const EquipmentDuraPanel = ({ char, onUpdate, isDM }) => {

  const [showPanel, setShowPanel] = useState(false);

  const [editingEquipment, setEditingEquipment] = useState(null);

  const [showAddNew, setShowAddNew] = useState(false);

  const [newEquipType, setNewEquipType] = useState('weapon');

  const [newEquipSubtype, setNewEquipSubtype] = useState('slashing');

  const [newEquipName, setNewEquipName] = useState('');

  const [showDamageModal, setShowDamageModal] = useState(null);

  const [damageType, setDamageType] = useState('slashing');

  const [isBlocking, setIsBlocking] = useState(false);

  const [customDamage, setCustomDamage] = useState('');

  

  const equipment = char.equipment || [];

  const equippedWeapon = equipment.find(e => e.id === char.equippedWeapon);

  const equippedArmor = equipment.find(e => e.id === char.equippedArmor);

  const equippedShield = equipment.find(e => e.id === char.equippedShield);

  

  // Get subtype options based on equipment type

  const getSubtypeOptions = (type) => {

    if (type === 'weapon') return Object.entries(WEAPON_TYPES).map(([k, v]) => ({ value: k, label: v.name, icon: v.icon }));

    if (type === 'armor') return Object.entries(ARMOR_TYPES_DURA).filter(([k]) => k !== 'none').map(([k, v]) => ({ value: k, label: v.name, icon: v.icon }));

    if (type === 'shield') return Object.entries(SHIELD_TYPES).filter(([k]) => k !== 'none').map(([k, v]) => ({ value: k, label: v.name, icon: v.icon }));

    return [];

  };

  

  // Add new equipment

  const addEquipment = () => {

    if (!newEquipName.trim()) {

      window.alert('Please enter a name for the equipment');

      return;

    }

    const newEquip = createEquipmentDura(newEquipName.trim(), newEquipType, newEquipSubtype);

    if (newEquip) {

      onUpdate({ equipment: [...equipment, newEquip] });

      setNewEquipName('');

      setShowAddNew(false);

    }

  };

  

  // Update equipment

  const updateEquipment = (equipId, updates) => {

    onUpdate({

      equipment: equipment.map(e => e.id === equipId ? { ...e, ...updates } : e)

    });

  };

  

  // Delete equipment

  const deleteEquipment = (equipId) => {

    if (window.confirm('Delete this equipment?')) {

      const updates = { equipment: equipment.filter(e => e.id !== equipId) };

      if (char.equippedWeapon === equipId) updates.equippedWeapon = null;

      if (char.equippedArmor === equipId) updates.equippedArmor = null;

      if (char.equippedShield === equipId) updates.equippedShield = null;

      onUpdate(updates);

      setEditingEquipment(null);

    }

  };

  

  // Equip/unequip

  const toggleEquip = (equip) => {

    const field = equip.type === 'weapon' ? 'equippedWeapon' : equip.type === 'armor' ? 'equippedArmor' : 'equippedShield';

    const currentEquipped = char[field];

    onUpdate({ [field]: currentEquipped === equip.id ? null : equip.id });

  };

  

  // Apply durability damage

  const applyDamage = (equip, damage) => {

    const result = applyDuraDamage(equip, damage);

    updateEquipment(equip.id, result);

    if (result.events.length > 0) {

      alert(result.events.join('\n'));

    }

    setShowDamageModal(null);

  };

  

  // Calculate damage from type

  const calcDamageFromType = (equip) => {

    if (customDamage) return parseInt(customDamage) || 0;

    return calculateDuraDamage(equip, damageType, isBlocking);

  };

  

  // Repair equipment

  const repairEquipment = (equip, amount) => {

    const newDura = Math.min(equip.maxDura, equip.currentDura + amount);

    updateEquipment(equip.id, { currentDura: newDura, broken: newDura > 0 ? false : equip.broken });

  };

  

  // Full repair

  const fullRepair = (equip) => {

    updateEquipment(equip.id, { currentDura: equip.maxDura, broken: false });

  };

  

  // Get type data

  const getTypeData = (equip) => {

    if (equip.type === 'weapon') return WEAPON_TYPES[equip.subtype];

    if (equip.type === 'armor') return ARMOR_TYPES_DURA[equip.subtype];

    if (equip.type === 'shield') return SHIELD_TYPES[equip.subtype];

    return null;

  };

  

  // Render durability bar

  const renderDuraBar = (equip, compact = false) => {

    const percent = getDuraPercent(equip.currentDura, equip.maxDura);

    const color = getDuraColor(percent);

    const effects = getActiveDuraEffects(equip);

    

    return (

      <div style={{ marginTop: compact ? '4px' : '8px' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: compact ? '0.75rem' : '0.85rem', marginBottom: '2px' }}>

          <span style={{ color }}>{equip.currentDura}/{equip.maxDura} ({percent}%)</span>

          {!compact && effects.buffs.length > 0 && <span style={{ color: '#27c95a', fontSize: '0.7rem' }}>{effects.buffs.length} buff{effects.buffs.length !== 1 ? 's' : ''}</span>}

        </div>

        <div style={{ height: compact ? '6px' : '10px', background: 'rgba(0,0,0,0.4)', borderRadius: '4px', overflow: 'hidden' }}>

          <div style={{ height: '100%', width: `${percent}%`, background: color, transition: 'width 0.3s, background 0.3s' }} />

        </div>

      </div>

    );

  };

  

  // Render equipment card

  const renderEquipmentCard = (equip, isEquipped = false) => {

    const typeData = getTypeData(equip);

    const effects = getActiveDuraEffects(equip);

    const percent = getDuraPercent(equip.currentDura, equip.maxDura);

    

    return (

      <div key={equip.id} style={{

        padding: '12px',

        background: isEquipped ? 'rgba(74,144,217,0.2)' : 'rgba(0,0,0,0.2)',

        borderRadius: '6px',

        border: `1px solid ${isEquipped ? '#4a90d9' : equip.broken ? '#c92727' : 'rgba(255,255,255,0.1)'}`,

        marginBottom: '8px',

        opacity: equip.broken ? 0.6 : 1,

      }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>

          <div style={{ flex: 1 }}>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>

              <span style={{ fontSize: '1.2rem' }}>{typeData?.icon || '⚔️'}</span>

              <strong style={{ color: equip.broken ? '#c92727' : '#e8d5b7' }}>{equip.name}</strong>

              {equip.broken && <span style={{ fontSize: '0.7rem', color: '#c92727', fontWeight: 'bold' }}>BROKEN</span>}

              {equip.unrepairable && <span style={{ fontSize: '0.7rem', color: '#ff6b6b' }}>UNREPAIRABLE</span>}

              {isEquipped && <span style={{ fontSize: '0.7rem', color: '#4a90d9' }}>EQUIPPED</span>}

            </div>

            <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '2px' }}>

              {typeData?.name} • Base: {equip.baseMaxDura} {equip.bonusDura > 0 && <span style={{ color: '#27c95a' }}>+{equip.bonusDura}</span>}

              {equip.type === 'shield' && typeData?.msPenalty > 0 && <span style={{ color: '#c9a227' }}> • -{typeData.msPenalty}ft MS</span>}

            </div>

            {renderDuraBar(equip)}

          </div>

          

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginLeft: '8px' }}>

            <button 

              style={{ ...S.btn, ...S.btnXs, ...(isEquipped ? S.btnMana : {}), padding: '4px 8px' }}

              onClick={() => toggleEquip(equip)}

              disabled={equip.broken}

            >

              {isEquipped ? '✓' : '◯'}

            </button>

            <button 

              style={{ ...S.btn, ...S.btnXs, ...S.btnDanger, padding: '4px 8px' }}

              onClick={() => setShowDamageModal(equip)}

            >

              💥

            </button>

            <button 

              style={{ ...S.btn, ...S.btnXs, ...S.btnEdit, padding: '4px 8px' }}

              onClick={() => setEditingEquipment(equip)}

            >

              ✏️

            </button>

          </div>

        </div>

        

        {/* Active Effects */}

        {(effects.buffs.length > 0 || effects.debuffs.length > 0) && (

          <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>

            {effects.buffs.map((b, i) => (

              <span key={`b-${i}`} style={{ fontSize: '0.7rem', padding: '2px 6px', background: 'rgba(39,201,90,0.3)', border: '1px solid #27c95a', borderRadius: '4px', color: '#7dff7d' }}>{b}</span>

            ))}

            {effects.debuffs.map((d, i) => (

              <span key={`d-${i}`} style={{ fontSize: '0.7rem', padding: '2px 6px', background: 'rgba(201,39,39,0.3)', border: '1px solid #c92727', borderRadius: '4px', color: '#ff7d7d' }}>{d}</span>

            ))}

          </div>

        )}

      </div>

    );

  };

  

  // Group equipment by type

  const weapons = equipment.filter(e => e.type === 'weapon');

  const armors = equipment.filter(e => e.type === 'armor');

  const shields = equipment.filter(e => e.type === 'shield');

  

  return (

    <HoverCard>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setShowPanel(!showPanel)}>

        <h2 style={{ ...S.subtitle, marginBottom: 0 }}>⚔️ Equipment & Durability ({equipment.length})</h2>

        <span style={{ color: '#ffd700' }}>{showPanel ? '▼' : '▶'}</span>

      </div>

      

      {/* Quick status for equipped items */}

      {!showPanel && (equippedWeapon || equippedArmor || equippedShield) && (

        <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>

          {equippedWeapon && (

            <div style={{ padding: '4px 8px', background: 'rgba(74,144,217,0.2)', borderRadius: '4px', fontSize: '0.8rem' }}>

              {WEAPON_TYPES[equippedWeapon.subtype]?.icon} {equippedWeapon.name}: {equippedWeapon.currentDura}/{equippedWeapon.maxDura}

            </div>

          )}

          {equippedArmor && (

            <div style={{ padding: '4px 8px', background: 'rgba(74,144,217,0.2)', borderRadius: '4px', fontSize: '0.8rem' }}>

              {ARMOR_TYPES_DURA[equippedArmor.subtype]?.icon} {equippedArmor.name}: {equippedArmor.currentDura}/{equippedArmor.maxDura}

            </div>

          )}

          {equippedShield && (

            <div style={{ padding: '4px 8px', background: 'rgba(74,144,217,0.2)', borderRadius: '4px', fontSize: '0.8rem' }}>

              {SHIELD_TYPES[equippedShield.subtype]?.icon} {equippedShield.name}: {equippedShield.currentDura}/{equippedShield.maxDura}

            </div>

          )}

        </div>

      )}

      

      {/* No armor warning */}

      {!showPanel && !equippedArmor && !char.isMonkOrBarbarian && (

        <div style={{ marginTop: '8px', padding: '6px', background: 'rgba(201,39,39,0.2)', border: '1px solid #c92727', borderRadius: '4px', fontSize: '0.8rem', color: '#ff7d7d' }}>

          ⚠️ No armor equipped: x3 Damage Taken

        </div>

      )}

      

      {showPanel && (

        <div style={{ marginTop: '16px' }}>

          {/* Controls */}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>

            <button style={{ ...S.btn, ...S.btnSm }} onClick={() => setShowAddNew(true)}>+ Add Equipment</button>

            {isDM && (

              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem', cursor: 'pointer' }}>

                <input type="checkbox" checked={char.isMonkOrBarbarian || false} onChange={e => onUpdate({ isMonkOrBarbarian: e.target.checked })} />

                Monk/Barbarian (no armor penalty)

              </label>

            )}

          </div>

          

          {/* No armor warning - expanded */}

          {!equippedArmor && !char.isMonkOrBarbarian && (

            <div style={{ marginBottom: '16px', padding: '10px', background: 'rgba(201,39,39,0.2)', border: '1px solid #c92727', borderRadius: '6px' }}>

              <strong style={{ color: '#ff7d7d' }}>⚠️ No Armor Equipped!</strong>

              <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#e8d5b7' }}>Take x3 damage from all sources (unless Monk or Barbarian)</p>

            </div>

          )}

          

          {/* Weapons */}

          {weapons.length > 0 && (

            <div style={{ marginBottom: '16px' }}>

              <h4 style={{ color: '#c9a227', fontSize: '0.9rem', marginBottom: '8px', borderBottom: '1px solid rgba(201,162,39,0.3)', paddingBottom: '4px' }}>

                ⚔️ Weapons ({weapons.length})

              </h4>

              {weapons.map(w => renderEquipmentCard(w, w.id === char.equippedWeapon))}

            </div>

          )}

          

          {/* Armor */}

          {armors.length > 0 && (

            <div style={{ marginBottom: '16px' }}>

              <h4 style={{ color: '#c9a227', fontSize: '0.9rem', marginBottom: '8px', borderBottom: '1px solid rgba(201,162,39,0.3)', paddingBottom: '4px' }}>

                🛡️ Armor ({armors.length})

              </h4>

              {armors.map(a => renderEquipmentCard(a, a.id === char.equippedArmor))}

            </div>

          )}

          

          {/* Shields */}

          {shields.length > 0 && (

            <div style={{ marginBottom: '16px' }}>

              <h4 style={{ color: '#c9a227', fontSize: '0.9rem', marginBottom: '8px', borderBottom: '1px solid rgba(201,162,39,0.3)', paddingBottom: '4px' }}>

                🛡️ Shields ({shields.length})

              </h4>

              {shields.map(s => renderEquipmentCard(s, s.id === char.equippedShield))}

            </div>

          )}

          

          {equipment.length === 0 && (

            <p style={{ textAlign: 'center', opacity: 0.5 }}>No equipment. Add some weapons, armor, or shields!</p>

          )}

        </div>

      )}

      

      {/* Add New Modal */}

      {showAddNew && (

        <div style={S.modal} onClick={() => setShowAddNew(false)}>

          <div style={{ ...S.modalContent, maxWidth: '450px' }} onClick={e => e.stopPropagation()}>

            <h3 style={{ color: '#ffd700', marginBottom: '16px' }}>➕ Add Equipment</h3>

            

            <div style={{ marginBottom: '12px' }}>

              <label style={S.label}>Type</label>

              <select style={S.select} value={newEquipType} onChange={e => { setNewEquipType(e.target.value); setNewEquipSubtype(getSubtypeOptions(e.target.value)[0]?.value || ''); }}>

                <option value="weapon">Weapon</option>

                <option value="armor">Armor</option>

                <option value="shield">Shield</option>

              </select>

            </div>

            

            <div style={{ marginBottom: '12px' }}>

              <label style={S.label}>Subtype</label>

              <select style={S.select} value={newEquipSubtype} onChange={e => setNewEquipSubtype(e.target.value)}>

                {getSubtypeOptions(newEquipType).map(opt => (

                  <option key={opt.value} value={opt.value}>{opt.icon} {opt.label}</option>

                ))}

              </select>

            </div>

            

            {/* Show stats preview */}

            {newEquipSubtype && (

              <div style={{ marginBottom: '12px', padding: '10px', background: 'rgba(0,0,0,0.3)', borderRadius: '6px', fontSize: '0.85rem' }}>

                {(() => {

                  const data = newEquipType === 'weapon' ? WEAPON_TYPES[newEquipSubtype] : 

                               newEquipType === 'armor' ? ARMOR_TYPES_DURA[newEquipSubtype] : 

                               SHIELD_TYPES[newEquipSubtype];

                  if (!data) return null;

                  return (

                    <>

                      <div><strong>Base Dura:</strong> {data.baseDura}</div>

                      <div><strong>Potential Bonus:</strong> +{data.potentialExtraDura[0]} to +{data.potentialExtraDura[1]}</div>

                      {data.msPenalty > 0 && <div style={{ color: '#c9a227' }}><strong>MS Penalty:</strong> -{data.msPenalty}ft</div>}

                      {data.restriction && <div style={{ color: '#c9a227' }}><strong>Restriction:</strong> {data.restriction}</div>}

                    </>

                  );

                })()}

              </div>

            )}

            

            <div style={{ marginBottom: '12px' }}>

              <label style={S.label}>Name</label>

              <input style={S.input} value={newEquipName} onChange={e => setNewEquipName(e.target.value)} placeholder="e.g., Longsword of Flames" />

            </div>

            

            <div style={{ display: 'flex', gap: '8px' }}>

              <button style={{ ...S.btn, ...S.btnSuccess, flex: 1 }} onClick={addEquipment}>Add</button>

              <button style={{ ...S.btn, flex: 1 }} onClick={() => setShowAddNew(false)}>Cancel</button>

            </div>

          </div>

        </div>

      )}

      

      {/* Damage Modal */}

      {showDamageModal && (

        <div style={S.modal} onClick={() => setShowDamageModal(null)}>

          <div style={{ ...S.modalContent, maxWidth: '400px' }} onClick={e => e.stopPropagation()}>

            <h3 style={{ color: '#ffd700', marginBottom: '16px' }}>💥 Apply Durability Damage</h3>

            <p style={{ marginBottom: '12px' }}><strong>{showDamageModal.name}</strong> ({showDamageModal.currentDura}/{showDamageModal.maxDura})</p>

            

            <div style={{ marginBottom: '12px' }}>

              <label style={S.label}>Damage Type</label>

              <select style={S.select} value={damageType} onChange={e => setDamageType(e.target.value)}>

                {DURA_DAMAGE_TYPES.map(dt => <option key={dt} value={dt}>{dt.charAt(0).toUpperCase() + dt.slice(1)}</option>)}

                {showDamageModal.type === 'weapon' && <option value="blocking">Enemy Blocking</option>}

                {showDamageModal.type === 'armor' && <option value="heavy">Heavy Armor (target)</option>}

                {showDamageModal.type === 'armor' && <option value="medium">Medium Armor (target)</option>}

              </select>

            </div>

            

            {showDamageModal.type === 'shield' && (

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', cursor: 'pointer' }}>

                <input type="checkbox" checked={isBlocking} onChange={e => setIsBlocking(e.target.checked)} />

                Shield blocked an attack (x2 damage)

              </label>

            )}

            

            <div style={{ marginBottom: '12px', padding: '10px', background: 'rgba(201,39,39,0.2)', borderRadius: '6px' }}>

              <strong>Calculated Damage:</strong> {calcDamageFromType(showDamageModal)}

              <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '4px' }}>

                Based on {showDamageModal.type} type vs {damageType} damage

              </div>

            </div>

            

            <div style={{ marginBottom: '12px' }}>

              <label style={S.label}>Or Custom Amount</label>

              <input style={S.input} type="number" value={customDamage} onChange={e => setCustomDamage(e.target.value)} placeholder="Override calculated damage" />

            </div>

            

            <div style={{ display: 'flex', gap: '8px' }}>

              <button style={{ ...S.btn, ...S.btnDanger, flex: 1 }} onClick={() => applyDamage(showDamageModal, calcDamageFromType(showDamageModal))}>

                Apply {calcDamageFromType(showDamageModal)} Damage

              </button>

              <button style={{ ...S.btn, flex: 1 }} onClick={() => setShowDamageModal(null)}>Cancel</button>

            </div>

          </div>

        </div>

      )}

      

      {/* Edit Modal */}

      {editingEquipment && (

        <div style={S.modal} onClick={() => setEditingEquipment(null)}>

          <div style={{ ...S.modalContent, maxWidth: '500px' }} onClick={e => e.stopPropagation()}>

            <h3 style={{ color: '#ffd700', marginBottom: '16px' }}>✏️ Edit {editingEquipment.name}</h3>

            

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>

              <div>

                <label style={S.label}>Name</label>

                <input style={S.input} value={editingEquipment.name} onChange={e => setEditingEquipment({ ...editingEquipment, name: e.target.value })} />

              </div>

              <div>

                <label style={S.label}>Maintenance</label>

                <select style={S.select} value={editingEquipment.maintenance} onChange={e => setEditingEquipment({ ...editingEquipment, maintenance: e.target.value })}>

                  {Object.entries(MAINTENANCE_LEVELS).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}

                </select>

              </div>

              <div>

                <label style={S.label}>Current Dura</label>

                <input style={S.input} type="number" min="0" max={editingEquipment.maxDura} value={editingEquipment.currentDura} onChange={e => setEditingEquipment({ ...editingEquipment, currentDura: Math.min(editingEquipment.maxDura, parseInt(e.target.value) || 0) })} />

              </div>

              <div>

                <label style={S.label}>Max Dura</label>

                <input style={S.input} type="number" min="1" value={editingEquipment.maxDura} onChange={e => setEditingEquipment({ ...editingEquipment, maxDura: parseInt(e.target.value) || 1 })} />

              </div>

            </div>

            

            {/* Dura effects reference */}

            <div style={{ marginTop: '16px', padding: '10px', background: 'rgba(0,0,0,0.3)', borderRadius: '6px', maxHeight: '200px', overflowY: 'auto' }}>

              <strong style={{ color: '#c9a227' }}>Durability Effects:</strong>

              {(() => {

                const data = getTypeData(editingEquipment);

                if (!data?.effects) return <p style={{ color: '#888' }}>No effects defined</p>;

                return (

                  <div style={{ marginTop: '8px', fontSize: '0.8rem' }}>

                    {data.effects.map((eff, i) => (

                      <div key={i} style={{ marginBottom: '4px', color: eff.buff ? '#7dff7d' : eff.debuff ? '#ff7d7d' : '#c9a227' }}>

                        {eff.type === 'above' ? `>${eff.threshold}%` : eff.type === 'below' ? `<${eff.threshold}%` : `=${eff.threshold}%`}: {eff.effect}

                      </div>

                    ))}

                  </div>

                );

              })()}

            </div>

            

            <div style={{ marginTop: '16px' }}>

              <label style={S.label}>Notes</label>

              <textarea style={{ ...S.input, minHeight: '60px' }} value={editingEquipment.notes || ''} onChange={e => setEditingEquipment({ ...editingEquipment, notes: e.target.value })} />

            </div>

            

            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>

              <button style={{ ...S.btn, ...S.btnSuccess, flex: 1 }} onClick={() => { updateEquipment(editingEquipment.id, editingEquipment); setEditingEquipment(null); }}>Save</button>

              <button style={{ ...S.btn, ...S.btnMana }} onClick={() => { fullRepair(editingEquipment); setEditingEquipment({ ...editingEquipment, currentDura: editingEquipment.maxDura, broken: false }); }}>🔧 Full Repair</button>

              <button style={{ ...S.btn, ...S.btnDanger }} onClick={() => deleteEquipment(editingEquipment.id)}>Delete</button>

              <button style={{ ...S.btn }} onClick={() => setEditingEquipment(null)}>Cancel</button>

            </div>

          </div>

        </div>

      )}

    </HoverCard>

  );

};



// Damage types for combat (moved before SpellPanel so it's available in spell editor)

const DAMAGE_TYPES = {

  slashing: { name: 'Slashing', icon: '🗡️', color: '#c92727' },

  piercing: { name: 'Piercing', icon: '🔪', color: '#27c9c9' },

  bludgeoning: { name: 'Bludgeoning', icon: '🔨', color: '#8b4513' },

  fire: { name: 'Fire', icon: '🔥', color: '#ff6600' },

  cold: { name: 'Cold', icon: '❄️', color: '#00bfff' },

  lightning: { name: 'Lightning', icon: '⚡', color: '#ffff00' },

  thunder: { name: 'Thunder', icon: '💥', color: '#9370db' },

  poison: { name: 'Poison', icon: '☠️', color: '#32cd32' },

  acid: { name: 'Acid', icon: '🧪', color: '#7fff00' },

  necrotic: { name: 'Necrotic', icon: '💀', color: '#4b0082' },

  radiant: { name: 'Radiant', icon: '✨', color: '#ffd700' },

  force: { name: 'Force', icon: '💫', color: '#ee82ee' },

  psychic: { name: 'Psychic', icon: '🧠', color: '#ff69b4' },

  raidwide: { name: 'Raid-Wide', icon: '🌀', color: '#ff00ff' },

};



export const getCombatItemAbilityEffectTextsForAuthority = (effects) => {
  if (Array.isArray(effects)) {
    return effects.map((effect) => String(effect || '').trim()).filter(Boolean);
  }

  if (typeof effects === 'string' && effects.trim()) {
    return effects.split(',').map((effect) => effect.trim()).filter(Boolean);
  }

  return [];
};

export const parseCombatItemAbilityDamageTextForAuthority = (text) => {
  const raw = String(text || '').trim();
  const match = raw.match(/^([+-]?)\s*(\d*d\d+(?:\s*[+-]\s*\d+)?)\s*(.*)$/i);
  if (!match) return null;

  const label = match[3].trim();
  const lower = label.toLowerCase();
  const damageType = Object.keys(DAMAGE_TYPES).find((type) => (
    type !== 'raidwide'
    && (lower.includes(type) || lower.includes(String(DAMAGE_TYPES[type]?.name || '').toLowerCase()))
  )) || 'force';

  return {
    raw,
    sign: match[1] === '-' ? -1 : 1,
    expr: match[2].replace(/\s+/g, ''),
    label,
    damageType,
  };
};

const combatItemAbilityLabelHasExplicitDamageType = (label = '') => {
  const lower = String(label || '').toLowerCase();
  return Object.keys(DAMAGE_TYPES).some((type) => (
    type !== 'raidwide'
    && (lower.includes(type) || lower.includes(String(DAMAGE_TYPES[type]?.name || '').toLowerCase()))
  ));
};

const COMBAT_ITEM_ABILITY_SAVE_REQUIRED_RE = /\b(dc\s*\d+|saving throw|save for half|half on save|half damage|on a successful save|must make [^.]* save|(?:str|dex|con|int|wis|cha)\s+save)\b/i;
const COMBAT_ITEM_ABILITY_HEALING_RE = /\b(hp|hit points?|heals?|healing|restore|restores|regain|regains)\b/i;
const COMBAT_ITEM_ABILITY_TEMP_HP_RE = /\b(temp(?:orary)?\s*(?:hp|hit points?)|temporary hit points?)\b/i;
const COMBAT_ITEM_ABILITY_SAVE_EFFECT_HALF_RE = /\b(save for half|half on save|half damage|takes half|success(?:ful)? save[^.]*half)\b/i;
const COMBAT_ITEM_ABILITY_SAVE_EFFECT_NEGATES_RE = /\b(negates?|no damage|takes no damage|avoids? all|success(?:ful)? save[^.]*(?:negates?|no damage))\b/i;
const COMBAT_ITEM_ABILITY_SAVE_ABILITY_ALIASES = {
  str: 'str',
  strength: 'str',
  dex: 'dex',
  dexterity: 'dex',
  con: 'con',
  constitution: 'con',
  int: 'int',
  intelligence: 'int',
  wis: 'wis',
  wisdom: 'wis',
  cha: 'cha',
  charisma: 'cha',
};

export const parseCombatItemAbilitySaveDamageTextForCombat = (text = '') => {
  const raw = String(text || '').trim();
  if (!raw) return null;
  if (COMBAT_ITEM_ABILITY_HEALING_RE.test(raw) || COMBAT_ITEM_ABILITY_TEMP_HP_RE.test(raw)) return null;

  const parsedDamage = parseCombatItemAbilityDamageTextForAuthority(raw);
  if (!parsedDamage || parsedDamage.sign <= 0) return null;

  const dcMatch = raw.match(/\bdc\s*(\d{1,2})\b/i);
  const saveAbilityMatch = raw.match(/\b(str(?:ength)?|dex(?:terity)?|con(?:stitution)?|int(?:elligence)?|wis(?:dom)?|cha(?:risma)?)\s+(?:saving\s+throw|save)\b/i);
  const saveEffect = COMBAT_ITEM_ABILITY_SAVE_EFFECT_HALF_RE.test(raw)
    ? 'half'
    : (COMBAT_ITEM_ABILITY_SAVE_EFFECT_NEGATES_RE.test(raw) ? 'negates' : null);
  const saveDC = dcMatch ? Number(dcMatch[1]) : null;
  const saveAbility = saveAbilityMatch
    ? COMBAT_ITEM_ABILITY_SAVE_ABILITY_ALIASES[String(saveAbilityMatch[1] || '').toLowerCase()]
    : null;

  if (!Number.isFinite(saveDC) || !saveAbility || !saveEffect) return null;

  return {
    ...parsedDamage,
    saveDC,
    saveAbility,
    saveEffect,
    halfOnSave: saveEffect === 'half',
  };
};

export const classifyCombatItemAbilityEffectTextForCombat = (text = '') => {
  const raw = String(text || '').trim();
  if (!raw) return { kind: 'empty', raw };

  const parsedDamage = parseCombatItemAbilityDamageTextForAuthority(raw);
  const hasSaveRequirement = COMBAT_ITEM_ABILITY_SAVE_REQUIRED_RE.test(raw);
  const hasHealingIntent = COMBAT_ITEM_ABILITY_HEALING_RE.test(raw);
  const hasTempHpIntent = COMBAT_ITEM_ABILITY_TEMP_HP_RE.test(raw);
  const flatHealingMatch = raw.match(/^\+?\s*(\d+)\s*(?:hp|hit points?|healing)\b/i);
  const flatTempHpMatch = raw.match(/^\+?\s*(\d+)\s*(?:temp(?:orary)?\s*(?:hp|hit points?)|temporary hit points?)\b/i);
  const hasExplicitDamageType = parsedDamage
    && parsedDamage.sign > 0
    && combatItemAbilityLabelHasExplicitDamageType(parsedDamage.label);

  if (hasSaveRequirement) {
    return {
      kind: 'save-required',
      raw,
      parsedDamage,
      saveDamage: parseCombatItemAbilitySaveDamageTextForCombat(raw),
    };
  }

  if (hasExplicitDamageType && hasTempHpIntent) {
    return { kind: 'unsupported-mixed-damage-temp-hp', raw, parsedDamage };
  }

  if (hasExplicitDamageType && hasHealingIntent) {
    return { kind: 'unsupported-mixed-damage-healing', raw, parsedDamage };
  }

  if (hasTempHpIntent) {
    if (parsedDamage && parsedDamage.sign > 0) {
      return { kind: 'temporary-hp', raw, parsedDamage };
    }
    if (flatTempHpMatch) {
      return {
        kind: 'temporary-hp',
        raw,
        flatAmount: Number(flatTempHpMatch[1]) || 0,
        label: raw.replace(flatTempHpMatch[0], '').trim(),
      };
    }
    return { kind: 'temporary-hp', raw };
  }

  if (parsedDamage && (parsedDamage.sign < 0 || hasHealingIntent)) {
    return { kind: 'healing', raw, parsedDamage };
  }

  if (!parsedDamage && flatHealingMatch) {
    return {
      kind: 'healing',
      raw,
      flatAmount: Number(flatHealingMatch[1]) || 0,
      label: raw.replace(flatHealingMatch[0], '').trim(),
    };
  }

  if (parsedDamage) {
    return { kind: 'auto-damage', raw, parsedDamage };
  }

  return { kind: 'status', raw };
};

const getDiceExpressionMaxTotal = (notation = '') => {
  const cleanNotation = String(notation || '').toLowerCase().replace(/\s/g, '');
  if (!cleanNotation) return 0;

  const parts = cleanNotation.split(/([+-])/);
  let total = 0;
  let currentSign = 1;

  parts.forEach((part) => {
    if (part === '+') {
      currentSign = 1;
      return;
    }
    if (part === '-') {
      currentSign = -1;
      return;
    }
    if (!part) return;

    const diceMatch = part.match(/^(\d+)?d(\d+)$/);
    if (diceMatch) {
      const count = parseInt(diceMatch[1], 10) || 1;
      const sides = parseInt(diceMatch[2], 10) || 0;
      total += count * sides * currentSign;
      return;
    }

    const flat = parseInt(part, 10);
    if (!Number.isNaN(flat)) total += flat * currentSign;
  });

  return Math.max(0, total);
};

const getDiceExpressionMinTotal = (notation = '') => {
  const cleanNotation = String(notation || '').toLowerCase().replace(/\s/g, '');
  if (!cleanNotation) return 0;

  const parts = cleanNotation.split(/([+-])/);
  let total = 0;
  let currentSign = 1;

  parts.forEach((part) => {
    if (part === '+') {
      currentSign = 1;
      return;
    }
    if (part === '-') {
      currentSign = -1;
      return;
    }
    if (!part) return;

    const diceMatch = part.match(/^(\d+)?d(\d+)$/);
    if (diceMatch) {
      const count = parseInt(diceMatch[1], 10) || 1;
      const sides = parseInt(diceMatch[2], 10) || 0;
      total += count * (currentSign > 0 ? 1 : sides) * currentSign;
      return;
    }

    const flat = parseInt(part, 10);
    if (!Number.isNaN(flat)) total += flat * currentSign;
  });

  return Math.max(0, total);
};

const normalizeCombatPercentEntries = (items = []) => (
  (Array.isArray(items) ? items : [])
    .map((item) => (typeof item === 'string' ? { type: item, percent: 50 } : item))
    .map((item) => (item?.type ? { ...item, type: String(item.type).toLowerCase() } : item))
    .filter((item) => item?.type)
);

const hasDamageAmplifierForType = (target = {}, damageType = '') => {
  const normalizedDamageType = String(damageType || '').toLowerCase();
  const vulnerabilities = normalizeCombatPercentEntries(target.vulnerabilities);
  if (vulnerabilities.some((entry) => entry.type === normalizedDamageType)) return true;

  return (Array.isArray(target.debuffs) ? target.debuffs : []).some((debuff) => {
    if (!debuff || debuff.casterOnly || !(Number(debuff.percentIncrease) > 0)) return false;
    const damageTypes = Array.isArray(debuff.damageTypes)
      ? debuff.damageTypes.map((type) => String(type || '').toLowerCase())
      : null;
    return !damageTypes || damageTypes.includes(normalizedDamageType) || damageTypes.includes('all');
  });
};

const hasDamageMitigationForType = (target = {}, damageType = '') => {
  const normalizedDamageType = String(damageType || '').toLowerCase();
  const resistances = normalizeCombatPercentEntries(target.resistances);
  if (resistances.some((entry) => entry.type === normalizedDamageType)) return true;

  const immunities = Array.isArray(target.immunities)
    ? target.immunities.map((entry) => String(entry || '').toLowerCase())
    : [];
  return immunities.includes(normalizedDamageType);
};

const isCombatItemAbilityDamageAuthorityTextSupported = (text = '') => {
  return classifyCombatItemAbilityEffectTextForCombat(text).kind === 'auto-damage';
};

const isCombatItemAbilityHealingAuthorityTextSupported = (text = '') => {
  return classifyCombatItemAbilityEffectTextForCombat(text).kind === 'healing'
    && !COMBAT_ITEM_ABILITY_TEMP_HP_RE.test(String(text || ''));
};

const isCombatItemAbilityTempHpAuthorityTextSupported = (text = '') => {
  const classification = classifyCombatItemAbilityEffectTextForCombat(text);
  if (classification.kind !== 'temporary-hp') return false;
  if (classification.parsedDamage) return classification.parsedDamage.sign > 0;
  return Number(classification.flatAmount) > 0;
};

export const isEnemyCombatItemAbilityNonlethalAuthoritySafe = ({
  effectTexts = [],
  target = null,
  hasTargetedDamage = false,
  isCrit = false,
} = {}) => {
  if (!target || target.type !== 'enemy') return false;
  if (hasTargetedDamage !== true) return false;
  if (!Array.isArray(effectTexts) || effectTexts.length !== 1) return false;

  const parsed = parseCombatItemAbilityDamageTextForAuthority(effectTexts[0]);
  if (!parsed || parsed.sign < 0) return false;
  if (hasDamageAmplifierForType(target, parsed.damageType)) return false;

  const currentHp = Number(target.currentHp ?? 0);
  if (!Number.isFinite(currentHp) || currentHp <= 0) return false;
  const tempHp = Number(target.tempHp ?? 0);
  if (!Number.isFinite(tempHp) || tempHp < 0) return false;

  const maxDamage = getDiceExpressionMaxTotal(parsed.expr) * (isCrit === true ? 2 : 1);
  if (maxDamage <= 0) return false;

  return (currentHp + tempHp) - maxDamage > 0;
};

export const isEnemyCombatItemAbilitySaveNonlethalAuthoritySafe = ({
  effectTexts = [],
  target = null,
  hasTargetedSaveDamage = false,
  isCrit = false,
} = {}) => {
  if (!target || target.type !== 'enemy') return false;
  if (hasTargetedSaveDamage !== true) return false;
  if (!Array.isArray(effectTexts) || effectTexts.length !== 1) return false;

  const parsed = parseCombatItemAbilitySaveDamageTextForCombat(effectTexts[0]);
  if (!parsed || parsed.sign < 0) return false;
  if (hasDamageAmplifierForType(target, parsed.damageType)) return false;

  const currentHp = Number(target.currentHp ?? 0);
  if (!Number.isFinite(currentHp) || currentHp <= 0) return false;
  const tempHp = Number(target.tempHp ?? 0);
  if (!Number.isFinite(tempHp) || tempHp < 0) return false;

  const maxFailedSaveDamage = getDiceExpressionMaxTotal(parsed.expr) * (isCrit === true ? 2 : 1);
  if (maxFailedSaveDamage <= 0) return false;

  return (currentHp + tempHp) - maxFailedSaveDamage > 0;
};

export const isEnemyCombatItemAbilitySaveLethalAuthoritySafe = ({
  effectTexts = [],
  target = null,
  hasTargetedSaveDamage = false,
  isCrit = false,
} = {}) => {
  if (!target || target.type !== 'enemy') return false;
  if (hasTargetedSaveDamage !== true) return false;
  if (!Array.isArray(effectTexts) || effectTexts.length !== 1) return false;

  const parsed = parseCombatItemAbilitySaveDamageTextForCombat(effectTexts[0]);
  if (!parsed || parsed.sign < 0 || parsed.saveEffect !== 'half') return false;
  if (hasDamageAmplifierForType(target, parsed.damageType)) return false;
  if (hasDamageMitigationForType(target, parsed.damageType)) return false;

  const currentHp = Number(target.currentHp ?? 0);
  if (!Number.isFinite(currentHp) || currentHp <= 0) return false;
  const tempHp = Number(target.tempHp ?? 0);
  if (!Number.isFinite(tempHp) || tempHp < 0) return false;

  const minFailedSaveDamage = getDiceExpressionMinTotal(parsed.expr) * (isCrit === true ? 2 : 1);
  const minPassedSaveDamage = Math.floor(minFailedSaveDamage / 2);
  if (minPassedSaveDamage <= 0) return false;

  return minPassedSaveDamage >= currentHp + tempHp;
};

export const isEnemyCombatItemAbilityFailedSaveLethalAuthorityCandidate = ({
  effectTexts = [],
  target = null,
  hasTargetedSaveDamage = false,
  isCrit = false,
} = {}) => {
  if (!target || target.type !== 'enemy') return false;
  if (hasTargetedSaveDamage !== true) return false;
  if (!Array.isArray(effectTexts) || effectTexts.length !== 1) return false;

  const parsed = parseCombatItemAbilitySaveDamageTextForCombat(effectTexts[0]);
  if (!parsed || parsed.sign < 0) return false;
  if (parsed.saveEffect !== 'half' && parsed.saveEffect !== 'negates') return false;
  if (hasDamageAmplifierForType(target, parsed.damageType)) return false;
  if (hasDamageMitigationForType(target, parsed.damageType)) return false;

  const currentHp = Number(target.currentHp ?? 0);
  if (!Number.isFinite(currentHp) || currentHp <= 0) return false;
  const tempHp = Number(target.tempHp ?? 0);
  if (!Number.isFinite(tempHp) || tempHp < 0) return false;

  const totalHp = currentHp + tempHp;
  const critMultiplier = isCrit === true ? 2 : 1;
  const minFailedSaveDamage = getDiceExpressionMinTotal(parsed.expr) * critMultiplier;
  const maxFailedSaveDamage = getDiceExpressionMaxTotal(parsed.expr) * critMultiplier;
  if (minFailedSaveDamage <= 0 || maxFailedSaveDamage <= 0) return false;

  const maxPassedSaveDamage = parsed.saveEffect === 'half'
    ? Math.floor(maxFailedSaveDamage / 2)
    : 0;
  return minFailedSaveDamage >= totalHp && maxPassedSaveDamage < totalHp;
};

export const isEnemyCombatItemAbilityLethalAuthoritySafe = ({
  effectTexts = [],
  target = null,
  hasTargetedDamage = false,
  isCrit = false,
} = {}) => {
  if (!target || target.type !== 'enemy') return false;
  if (hasTargetedDamage !== true) return false;
  if (!Array.isArray(effectTexts) || effectTexts.length !== 1) return false;

  const parsed = parseCombatItemAbilityDamageTextForAuthority(effectTexts[0]);
  if (!parsed || parsed.sign < 0) return false;
  if (hasDamageAmplifierForType(target, parsed.damageType)) return false;
  if (hasDamageMitigationForType(target, parsed.damageType)) return false;

  const currentHp = Number(target.currentHp ?? 0);
  if (!Number.isFinite(currentHp) || currentHp <= 0) return false;
  const tempHp = Number(target.tempHp ?? 0);
  if (!Number.isFinite(tempHp) || tempHp < 0) return false;

  const minDamage = getDiceExpressionMinTotal(parsed.expr) * (isCrit === true ? 2 : 1);
  if (minDamage <= 0) return false;

  return minDamage >= currentHp + tempHp;
};

export const isCombatItemAbilityAuthorityShapeSupported = ({
  authorityEnabled = false,
  shape = {},
} = {}) => {
  if (!authorityEnabled) return false;
  const effectTexts = Array.isArray(shape.effectTexts) ? shape.effectTexts : [];
  const effectKinds = effectTexts.map((text) => classifyCombatItemAbilityEffectTextForCombat(text).kind);

  if (!shape.targetId) {
    return shape.hasTargetedDamage !== true
      && shape.hasTargetedHealing !== true
      && shape.hasTargetedTempHp !== true
      && effectTexts.length > 0
      && effectKinds.every((kind) => kind === 'status');
  }

  const isTempHpOnly = effectTexts.length === 1
    && isCombatItemAbilityTempHpAuthorityTextSupported(effectTexts[0]);
  if (shape.hasTargetedTempHp === true) {
    if (shape.hasTargetedDamage === true || shape.hasTargetedHealing === true || shape.hasTargetedSaveDamage === true || !isTempHpOnly) return false;
    if (shape.targetType !== 'player' && shape.targetType !== 'npc') return false;

    const target = shape.target || {};
    const currentHp = Number(target.currentHp ?? 0);
    const tempHp = Number(target.tempHp ?? 0);
    if (!Number.isFinite(currentHp) || currentHp <= 0) return false;
    if (!Number.isFinite(tempHp) || tempHp < 0) return false;
    if (target.deathSaves) return false;
    const conditions = Array.isArray(target.conditions) ? target.conditions : [];
    if (conditions.some((condition) => /^(unconscious|dead)$/i.test(String(condition || '').trim()))) return false;
    return true;
  }

  const isHealingOnly = effectTexts.length === 1
    && isCombatItemAbilityHealingAuthorityTextSupported(effectTexts[0]);
  if (shape.hasTargetedHealing === true) {
    if (shape.hasTargetedDamage === true || !isHealingOnly) return false;
    if (shape.targetType !== 'player' && shape.targetType !== 'npc') return false;

    const target = shape.target || {};
    const currentHp = Number(target.currentHp ?? 0);
    const maxHp = Number(target.maxHp ?? 0);
    const tempHp = Number(target.tempHp ?? 0);
    if (!Number.isFinite(currentHp) || currentHp <= 0) return false;
    if (!Number.isFinite(maxHp) || maxHp <= 0) return false;
    if (!Number.isFinite(tempHp) || tempHp < 0) return false;
    if (target.deathSaves) return false;
    const conditions = Array.isArray(target.conditions) ? target.conditions : [];
    if (conditions.some((condition) => /^(unconscious|dead)$/i.test(String(condition || '').trim()))) return false;
    return true;
  }

  const isSaveDamageOnly = effectTexts.length === 1
    && effectKinds[0] === 'save-required'
    && !!parseCombatItemAbilitySaveDamageTextForCombat(effectTexts[0]);
  if (shape.hasTargetedSaveDamage === true) {
    if (!isSaveDamageOnly) return false;
    const target = shape.target || {};
    const currentHp = Number(target.currentHp ?? 0);
    const tempHp = Number(target.tempHp ?? 0);
    if (!Number.isFinite(currentHp)) return false;
    if (!Number.isFinite(tempHp) || tempHp < 0) return false;
    if (shape.targetType === 'enemy') {
      const enemySaveShape = {
        effectTexts,
        target,
        hasTargetedSaveDamage: true,
        isCrit: shape.isCrit === true,
      };
      return isEnemyCombatItemAbilitySaveNonlethalAuthoritySafe(enemySaveShape)
        || isEnemyCombatItemAbilitySaveLethalAuthoritySafe(enemySaveShape)
        || isEnemyCombatItemAbilityFailedSaveLethalAuthorityCandidate(enemySaveShape);
    }
    if (shape.targetType !== 'player' && shape.targetType !== 'npc') return false;
    return true;
  }

  const isDiceOnly = effectTexts.length === 1
    && isCombatItemAbilityDamageAuthorityTextSupported(effectTexts[0]);
  if (shape.hasTargetedDamage !== true || !isDiceOnly) return false;
  if (shape.targetType === 'player' || shape.targetType === 'npc') return true;
  if (shape.targetType === 'enemy') {
    const enemyShape = {
      effectTexts,
      target: shape.target,
      hasTargetedDamage: shape.hasTargetedDamage,
      isCrit: shape.isCrit === true,
    };
    return isEnemyCombatItemAbilityNonlethalAuthoritySafe(enemyShape)
      || isEnemyCombatItemAbilityLethalAuthoritySafe(enemyShape);
  }

  return false;
};

// ============== SPELL PANEL COMPONENT ==============

// `systems`, `chars`, `journal` threaded through for rule-engine worldContext and

// on-spell-cast emission (Systems expansion Session 2). Default-safe if omitted.



// 2026-04-27: Override-aware field wrapper. Hoisted to module scope (and given a

// stable identity) because previously this was a function defined inside SpellPanel's

// render IIFE; each parent re-render produced a new component reference, so React

// unmounted/remounted the wrapped <input> on every keystroke and focus was lost

// after each character. As a stable top-level component, focus is preserved.

const SpellEditorFieldWrap = ({ field, label, lc, children, span, auto, isOverridden, clearOverride }) => (

  <div style={span ? { gridColumn: `span ${span}` } : {}}>

    <label style={{ ...S.label, fontSize: '0.68rem', color: lc || '#e8d5b7', display: 'flex', alignItems: 'center', gap: '3px' }}>

      {auto && <span style={{ color: '#27c95a', fontSize: '0.45rem', marginRight: '2px', verticalAlign: 'middle' }}>⬤</span>}

      {label}

      {isOverridden && isOverridden(field) && <button onClick={() => clearOverride(field)} title="Revert to base" style={{ background: 'none', border: 'none', color: '#c9a227', cursor: 'pointer', fontSize: '0.55rem', padding: 0 }}>↩</button>}

    </label>

    {children}

  </div>

);



export const SpellPanel = ({ char, onUpdate, isDM, spellLibrary, onSaveLibrary, globalSettings, onSaveGlobalSettings, systems = {}, manaEnabled = true, chars = {}, journal = [], npcLibrary = {}, onSaveNpcLibrary = null, onNotify = null, onDropConcentration = null, groupTravelLog = [], onSaveGroupTravelLog = null, messages = null, onSaveMessages = null, onSendStatusMessage = null }) => {

  const isMobileDefault = useIsMobile();

  // 2026-04-29: spells expanded by default on mobile so the user lands

  // on a usable list, not a collapsed header. Desktop keeps the

  // existing default-collapsed for compactness within a wider page.

  const [showSpells, setShowSpells] = useState(isMobileDefault);

  const [editingSpell, setEditingSpell] = useState(null);

  const [editLevelTab, setEditLevelTab] = useState('base');

  const [showLibrary, setShowLibrary] = useState(false);

  const [editorSections, setEditorSections] = useState({ casting: true, effect: true, scaling: false, overtime: false, conditions: false, description: false });

  const [showTemplates, setShowTemplates] = useState(false);

  const [templateName, setTemplateName] = useState('');

  const [showTemplateSave, setShowTemplateSave] = useState(false);

  const [spellListTab, setSpellListTab] = useState('prepared'); // 'prepared' | 'unprepared'

  const [editingSpellList, setEditingSpellList] = useState(false); // show add/duplicate controls

  const [expandedListSpells, setExpandedListSpells] = useState({}); // expanded spell details

  const [spellListRolls, setSpellListRolls] = useState({}); // per-spell roll results { spellId: rollResult }

  const [showImport, setShowImport] = useState(false);

  const [importText, setImportText] = useState('');

  const [filterLevel, setFilterLevel] = useState('all');

  const [filterTag, setFilterTag] = useState('all');

  const [showCastConfirm, setShowCastConfirm] = useState(null);

  const [castLevel, setCastLevel] = useState(null);

  const [lastRoll, setLastRoll] = useState(null);

  // 2026-05-11 F2: auto-dismiss the spell roll-result banner after 8s.
  // Cleared on every new roll so the timer resets, not stacks.
  useEffect(() => {
    if (!lastRoll) return undefined;
    const t = setTimeout(() => setLastRoll(null), 8000);
    return () => clearTimeout(t);
  }, [lastRoll]);

  const [showSettings, setShowSettings] = useState(false);

  // 2026-05-11 — Session 6: long-press a spell name to open a receipt
  // sheet decomposing the save DC / attack bonus from the resolved
  // spell context. Delegated handler pattern (one hook + ref-tracked
  // target) so we don't violate hooks-in-loops inside the spell .map.
  const [spellReceiptOpen, setSpellReceiptOpen] = useState(false);
  const [spellReceiptKey, setSpellReceiptKey] = useState(null);
  const pressingSpellRef = useRef(null);
  const spellLongPress = useLongPress(() => {
    if (!pressingSpellRef.current) return;
    setSpellReceiptKey(pressingSpellRef.current);
    setSpellReceiptOpen(true);
    // Clear after consumption (same rationale as SkillsDisplay).
    pressingSpellRef.current = null;
  });

  // 2026-05-11 — Session 6 §4h: long-form reading degrades on mobile
  // (Delgado 2018 screen-inferiority meta-analysis). Spell descriptions
  // chunk to ~180 char teaser + tap-to-expand. State per spell.id so
  // distinct spells track independently.
  const [descExpanded, setDescExpanded] = useState({});

  const [spellCastLevels, setSpellCastLevels] = useState({}); // Track selected cast level per spell

  const [autoRoll, setAutoRoll] = useState(true); // Auto-roll dice when casting



  const rawSpellSlots = char.spellSlots || { 1: 2 };
  const legacySlotMaxSpellLevel = getMaxSpellLevel(rawSpellSlots);
  const settings = syncSpellSettings(
    char.settings || DEFAULT_SETTINGS,
    {
      ...(getScalarSpellProgressionMaxCastLevel(char.settings || {}, null) === null && legacySlotMaxSpellLevel > 0
        ? { scalarSpellProgression: { maxCastLevel: legacySlotMaxSpellLevel } }
        : {}),
      ...(!manaEnabled ? { manaEnabled: false } : {}),
    },
  );

  const spells = char.spells || [];

  const rawUsedSlots = char.usedSpellSlots || {};

  // 2026-04-27: Prefer char.maxMana when set so the "Max" input on the char-edit panel

  // is honored. Fall back to slot-derived calc only when char.maxMana is missing/zero.

  const rawMaxMana = (Number.isFinite(char.maxMana) && char.maxMana > 0) ? char.maxMana : calculateMaxMana(rawSpellSlots);

  const rawCurrentMana = char.currentMana || 0;

  const maxSpellLevel = legacySlotMaxSpellLevel;
  const scalarSpellMaxCastLevel = getScalarSpellProgressionMaxCastLevel(settings, maxSpellLevel) ?? 0;

  const spellResourceUi = getSpellResourceUiModel({

    char,

    systems,

    settings,

    surface: SPELL_BINDING_SURFACE_SHEET,

  });

  const sheetSettingsSpellResourceUi = getSpellResourceUiModel({

    char,

    systems,

    settings,

    surface: SPELL_RESOURCE_SURFACE_ANY,

  });

  const isUsingLegacyManaSpellResource = spellResourceUi.usesLegacyManaAsPrimary;

  const showSpellFreeCastCounter = spellResourceUi.summary.displayKind === 'meter' && (settings.freeCastsPerDay || 4) > 0;

  const spellSlots = spellResourceUi.mode === 'system-tiered'

    ? Object.fromEntries((spellResourceUi.summary.tiers || []).map((tier) => [tier.level, tier.max ?? tier.current]))

    : rawSpellSlots;

  const usedSlots = spellResourceUi.mode === 'system-tiered'

    ? Object.fromEntries((spellResourceUi.summary.tiers || []).map((tier) => [tier.level, Math.max(0, (tier.max ?? tier.current) - tier.current)]))

    : rawUsedSlots;

  const spellResourceMeterText = formatSpellResourceReadout(spellResourceUi);

  const spellPanelEffectContext = getResolvedCharacterEffectContext(char, {}, systems, isDM);

  const spellPanelSpellContext = getResolvedSpellContext({

    char,

    effectiveStats: spellPanelEffectContext?.effectiveStats,

    equippedCombatBonuses: spellPanelEffectContext?.equippedCombatBonuses,

  });

  const buildSpellWorldContext = () => ({ chars, journal, npcLibrary });
  const [spellResourceSaveQueue, setSpellResourceSaveQueue] = useState([]);
  const activeSpellResourceSaveEntry = spellResourceSaveQueue[0] || null;
  const activeSpellResourcePendingSave = activeSpellResourceSaveEntry?.pendingSave || null;
  const activeSpellResourceSaveChar = activeSpellResourceSaveEntry?.context?.char || char;
  const spellResourceSaveAbility = (activeSpellResourcePendingSave?.ability || 'wis').toLowerCase();
  const activeSpellResourceEffectContext = activeSpellResourcePendingSave
    ? getResolvedCharacterEffectContext(activeSpellResourceSaveChar, {}, systems, isDM)
    : null;
  const activeSpellResourceSaveContext = activeSpellResourcePendingSave
    ? getResolvedSaveContext({
        stat: spellResourceSaveAbility,
        char: activeSpellResourceSaveChar,
        effectiveStats: activeSpellResourceEffectContext?.effectiveStats,
        equippedCombatBonuses: activeSpellResourceEffectContext?.equippedCombatBonuses,
        activeLegacyEffects: activeSpellResourceEffectContext?.activeLegacyEffects,
        activeRuntimeEffects: Array.isArray(activeSpellResourceSaveChar?.activeEffects) ? activeSpellResourceSaveChar.activeEffects : [],
      })
    : null;
  const activeSpellResourceSaveAdvantage = activeSpellResourcePendingSave
    ? getResolvedCharacterAdvantageContext(
        activeSpellResourceSaveChar,
        { ...getSaveAdvantageConfig(spellResourceSaveAbility) },
        {},
        systems,
        isDM,
      )
    : null;

  const consumeResourceFollowUpResults = (results, {
    charForEffects = char,
    label = 'Spell resource follow-up',
    source = 'spell.resource-follow-up',
    queuePendingSaves = true,
    triggerContext = null,
    worldContext = null,
    statusAuditAction = 'spell.resource-follow-up-status',
  } = {}) => {
    if (!results) return;
    const sideEffects = collectRuleResultSideEffects(results, { npcLibrary });
    if (sideEffects.npcLibraryChanged && typeof onSaveNpcLibrary === 'function') {
      onSaveNpcLibrary(sideEffects.nextNpcLibrary);
    }
    const sentStatusMessages = persistSystemRuleStatusMessages(sideEffects.statusMessages, {
      char: charForEffects,
      source,
      messages,
      onSendStatusMessage,
      onSaveMessages,
      auditMeta: { category: 'systems', action: statusAuditAction },
    });
    const ruleLogEntries = persistSystemRuleTravelLogEntries(sideEffects.logEntries, {
      char: charForEffects,
      action: 'system-rule-log',
      source,
      groupTravelLog,
      onSaveGroupTravelLog,
    });
    const queuedPendingSaves = queuePendingSaves
      ? buildSystemRulePendingSaveQueueEntriesForCharacter(sideEffects.pendingSaves, {
          char: charForEffects,
          systems,
          worldContext: worldContext || buildSpellWorldContext(),
          triggerContext,
          label,
          source,
        })
      : [];
    if (queuedPendingSaves.length > 0) {
      setSpellResourceSaveQueue(prev => [...(prev || []), ...queuedPendingSaves]);
    }
    if (typeof onNotify === 'function') {
      sideEffects.notifications.forEach(message => onNotify(message));
      if (queuedPendingSaves.length > 0) {
        const pendingSaveNotice = buildSystemRulePendingSaveNotice(sideEffects.pendingSaves, label);
        onNotify(`${pendingSaveNotice || `${label} requested ${queuedPendingSaves.length} saving throw${queuedPendingSaves.length === 1 ? '' : 's'}.`} Queued for resolution.`);
      } else if (sideEffects.pendingSaves.length > 0) {
        const pendingSaveNotice = buildSystemRulePendingSaveNotice(sideEffects.pendingSaves, label);
        onNotify(pendingSaveNotice);
      }
      if (ruleLogEntries.length > 0) {
        onNotify(`${ruleLogEntries.length} system rule log entr${ruleLogEntries.length === 1 ? 'y' : 'ies'} recorded.`);
      }
      if (sentStatusMessages.length > 0) {
        onNotify(`${sentStatusMessages.length} system status message${sentStatusMessages.length === 1 ? '' : 's'} sent.`);
      }
    }
  };

  const handleSpellResourceSaveResolve = (outcome) => {
    if (!activeSpellResourceSaveEntry) return;
    const [, ...rest] = spellResourceSaveQueue;
    const resolution = resolveSystemRulePendingSaveQueueEntry({
      entry: activeSpellResourceSaveEntry,
      outcome,
      systems,
      fallbackChar: activeSpellResourceSaveChar,
      fallbackWorldContext: buildSpellWorldContext(),
    });
    if (resolution.subResults) {
      const patch = buildChangedCharacterFields(resolution.queuedChar, resolution.nextChar);
      if (Object.keys(patch).length > 0) {
        onUpdate(patch, {
          summary: `${activeSpellResourceSaveEntry.label || 'Spell resource follow-up'} save ${outcome}: ${activeSpellResourcePendingSave?.reason || activeSpellResourceSaveChar?.name || 'system rule'}`,
          category: 'systems',
          action: `${activeSpellResourceSaveEntry.source || 'spell.resource-follow-up'}.save.${outcome}`,
        });
      }
      consumeResourceFollowUpResults(resolution.subResults, {
        charForEffects: resolution.nextChar,
        label: `${activeSpellResourceSaveEntry.label || 'Spell resource follow-up'} save`,
        source: `${activeSpellResourceSaveEntry.source || 'spell.resource-follow-up'}.save`,
        queuePendingSaves: false,
      });
    }
    setSpellResourceSaveQueue([...(resolution.nestedSaveEntries || []), ...rest]);
  };

  const handleSpellResourceSaveSkip = () => {
    setSpellResourceSaveQueue(([, ...rest]) => rest);
  };

  

  // Collect all unique tags from spells

  const allTags = [...new Set(spells.flatMap(s => s.tags || []))].sort();

  

  // Sort spells: favorites first, then by level, then by name

  const sortedSpells = [...spells].sort((a, b) => {

    // Favorites first

    if (a.favorited && !b.favorited) return -1;

    if (!a.favorited && b.favorited) return 1;

    // Then by level

    if (a.level !== b.level) return a.level - b.level;

    // Then by name

    return a.name.localeCompare(b.name);

  });

  

  // Filter spells (prepared/unprepared is handled by tabs)

  const filteredSpells = sortedSpells.filter(s => {

    if (filterLevel !== 'all' && s.level !== parseInt(filterLevel)) return false;

    if (filterTag !== 'all' && !(s.tags || []).map(t => t.toLowerCase()).includes(filterTag.toLowerCase())) return false;

    return true;

  });

  

  // (Grouping is done inline in the render for prepared/unprepared tabs)

  

  // Toggle favorite

  const toggleFavorite = (spellId) => {

    const spell = spells.find(s => s.id === spellId);

    if (spell) {

      onUpdate({

        spells: spells.map(s => s.id === spellId ? { ...s, favorited: !s.favorited } : s)

      });

    }

  };

  

  // Calculate casting cost

  const getCastingInfo = (spell, atLevel) => {

    return getSurfaceSpellCastInfo({

      char,

      spell,

      systems,

      settingsOverride: settings,

      surface: SPELL_BINDING_SURFACE_SHEET,

      castLevel: Number.isFinite(atLevel) ? atLevel : spell.level,

    });

  };



  const getCastLevelOptions = (spell) => {

    return getSpellCastLevelOptions({

      spell,

      getCastInfo: getCastingInfo,

    });

  };



  const formatCastInfoLabel = (info, spell) => {

    if (!info) return '';

    if (info.displayMode === 'system-tiered') {

      return spell.level === 0 ? '∞' : `${info.slotsRemaining} slot${info.cost > 1 ? ` (${info.cost})` : ''}`;

    }

    if (info.displayMode === 'legacy-slots') {

      return spell.level === 0 ? '∞' : `${info.slotsRemaining} slots`;

    }

    if (spell.level === 0) return '∞';

    if (info.freeAvailable) return 'FREE';

    const suffix = info.displayMode === 'system-scalar'

      ? (info.resourceLabel || 'Resource')

      : 'MP';

    return info.overcast ? `${info.cost}⚡` : `${info.cost} ${suffix}`;

  };

  

  const renderSpellResourceHeader = () => {

    if (spellResourceUi.summary.displayKind === 'meter') {

      const currentValue = spellResourceUi.summary.current ?? 0;

      const maxValue = Number.isFinite(spellResourceUi.summary.max) ? spellResourceUi.summary.max : '—';

      const isNegativeLegacyMana = spellResourceUi.mode === 'legacy-mana' && currentValue < 0;



      return (

        <span style={{

          padding: '4px 12px',

          borderRadius: '4px',

          background: isNegativeLegacyMana ? 'rgba(201,39,39,0.3)' : 'rgba(74,144,217,0.3)',

          border: `1px solid ${isNegativeLegacyMana ? '#c92727' : '#4a90d9'}`,

          color: isNegativeLegacyMana ? '#ff7d7d' : '#7db5ff',

          fontWeight: 'bold',

          fontSize: '0.9rem',

        }}>

          ✨ {spellResourceUi.summary.label}: {currentValue}/{maxValue}

        </span>

      );

    }



    return (

      <span style={{ fontSize: '0.8rem', color: '#888' }}>

        {spellResourceUi.summary.label}: {spellResourceUi.summary.detailText || 'Systems-managed'}

      </span>

    );

  };



  const resolveSelectedCastLevel = (spell, preferredLevel = null) => {

    return resolveSelectedSpellCastLevel({

      spell,

      preferredLevel,

      getCastInfo: getCastingInfo,

    });

  };



  const getShiftedCastLevel = (spell, currentLevel, direction) => {

    return shiftSelectedSpellCastLevel({

      spell,

      currentLevel,

      direction,

      getCastInfo: getCastingInfo,

    });

  };



  const buildSpellPatch = (baseChar, nextChar) => {

    const patch = {};

    Object.keys(nextChar || {}).forEach((key) => {

      if (nextChar[key] !== baseChar[key]) patch[key] = nextChar[key];

    });

    return patch;

  };



  // Cast a spell

  const castSpell = (spell, atLevel = null) => {

    // 2026-04-29: capture scroll position before the cast so we can

    // restore it after the re-render. iOS Safari and the spell-list's

    // inline roll-result rendering both nudge the page; the user

    // wants to see the result *where they tapped*, not after a jump.

    const preCastScrollY = typeof window !== 'undefined' ? window.scrollY : 0;

    const castLevel = Number.isFinite(atLevel) ? atLevel : (spell.level || 0);

    const info = getCastingInfo(spell, castLevel);

    const resolvedSpell = info?.resolvedSpell || spell;

    if (!info.canCast && !isDM) {

      window.alert('Not enough spell resources!');

      return;

    }

    if (info.tooHigh) {

      window.alert(`Cannot cast at level ${castLevel} - too far above your max spell level.`);

      return;

    }

    if (spell.concentration && char.concentratingOn && char.concentratingOn !== spell.name) {

      if (!window.confirm(`${char.name} is concentrating on "${char.concentratingOn}". Cast "${spell.name}"? This will end "${char.concentratingOn}".`)) {

        return;

      }

    }

    let nextChar = char;
    const spellCastRuleSideEffectBundles = [];

    if (spell.concentration) {

      nextChar = { ...nextChar, concentratingOn: spell.name, concentrationEffectGroupId: null };

    }

    const commitResult = finalizeSpellCastCommit({

      char: nextChar,

      spell,

      systems,

      castQuote: info,

      applyResourceFollowUps: (startingChar, resourceEvents) => applySpellResourceFollowUpsWithResults({

        startingChar,

        resourceEvents,

        systems,

        worldContext: buildSpellWorldContext(),

      }),

      runSpellCastTrigger: (updatedChar) => {

        if (!systems || !(updatedChar.systemList || []).length) {

          return updatedChar;

        }

        const triggerContext = { spellId: spell.id, spellName: spell.name, castLevel };

        const worldContext = buildSpellWorldContext();

        const ruleResults = processAllSystemRulesForTrigger('on-spell-cast', updatedChar, systems, triggerContext, worldContext);
        spellCastRuleSideEffectBundles.push({ results: ruleResults, triggerContext, worldContext });

        const ruleUpdates = applySystemRuleResults(updatedChar, ruleResults, systems, worldContext);

        return { ...updatedChar, ...ruleUpdates };

      },

    });

    nextChar = commitResult.nextChar;
    consumeResourceFollowUpResults(commitResult.resourceFollowUpResults, {
      charForEffects: nextChar,
      label: 'Spell cast resource follow-up',
      source: 'spell.resource-follow-up',
    });
    spellCastRuleSideEffectBundles.forEach((bundle) => {
      const spellCastWorldContext = {
        ...(bundle.worldContext || {}),
        chars: {
          ...((bundle.worldContext || {}).chars || chars || {}),
          ...(nextChar?.id ? { [nextChar.id]: nextChar } : {}),
        },
      };
      consumeResourceFollowUpResults(bundle.results, {
        charForEffects: nextChar,
        label: 'Spell cast rule',
        source: 'spell.on-cast',
        triggerContext: bundle.triggerContext,
        worldContext: spellCastWorldContext,
        statusAuditAction: 'spell.on-cast-status',
      });
    });

    if (resolvedSpell?.dice) {

      const diceToRoll = resolvedSpell.dice;

      if (diceToRoll) {

        const result = rollDice(diceToRoll);

        const spellMod = resolvedSpell.addMod ? spellPanelSpellContext.spellModifier : 0;

        const breakdown = [];

        const allRolls = result.rolls?.[0]?.rolls || [];

        breakdown.push({ type: 'base', source: spell.name, dice: diceToRoll, rolls: allRolls, total: result.total });

        if (spellMod > 0) {

          breakdown.push({ type: 'modifier', source: 'Spell Mod', value: spellMod });

        }

        const grandTotal = result.total + spellMod;

        const rollResult = { ...result, spellMod, breakdown, grandTotal, total: grandTotal, formula: diceToRoll };

        setSpellListRolls(prev => ({ ...prev, [spell.id]: rollResult }));

        setLastRoll({ spell: spell.name, level: castLevel, formula: diceToRoll, ...result, spellMod, total: grandTotal });

      }

    }

    const patch = buildSpellPatch(char, nextChar);

    if (Object.keys(patch).length > 0) {

      onUpdate(patch);

    }

    setShowCastConfirm(null);

    if (typeof window !== 'undefined') {

      requestAnimationFrame(() => {

        requestAnimationFrame(() => {

          if (Math.abs(window.scrollY - preCastScrollY) > 4) {

            window.scrollTo({ top: preCastScrollY, behavior: 'auto' });

          }

        });

      });

    }

  };

  // Import spells from text

  const handleImport = () => {

    const parsed = parseSpellImport(importText);

    if (parsed.length === 0) {

      window.alert('No valid spells found in import. Check format.');

      return;

    }

    

    // Merge with existing, avoiding duplicates by name

    const existingNames = new Set(spells.map(s => s.name.toLowerCase()));

    const newSpells = parsed.filter(s => !existingNames.has(s.name.toLowerCase()));

    

    if (newSpells.length === 0) {

      window.alert('All spells already exist in your spell list.');

      return;

    }

    

    onUpdate({ spells: [...spells, ...newSpells] });

    setImportText('');

    setShowImport(false);

    alert(`Imported ${newSpells.length} spell(s)!`);

  };

  

  // Export spells

  const handleExport = (format) => {

    const text = exportSpellsToText(spells, format);

    navigator.clipboard.writeText(text).then(() => {

      alert(`Spells copied to clipboard (${format.toUpperCase()} format)`);

    }).catch(() => {

      // Fallback: show in textarea

      setImportText(text);

      setShowImport(true);

    });

  };

  

  // Add new spell

  const addNewSpell = () => {

    const newSpell = createSpell({ name: 'New Spell', prepared: false });

    onUpdate({ spells: [...spells, newSpell] });

    setEditingSpell(newSpell);

    setEditLevelTab('base');

  };

  

  // Duplicate an existing spell (or create from template)

  const duplicateSpell = (sourceSpell, namePrefix = 'Copy of ') => {

    const { id, _libraryId, prepared, ...rest } = sourceSpell;

    const newSpell = createSpell({ ...rest, name: `${namePrefix}${sourceSpell.name}`, prepared: false });

    onUpdate({ spells: [...spells, newSpell] });

    setEditingSpell(newSpell);

    setEditLevelTab('base');

  };

  

  // Create from library spell

  const createFromLibrarySpell = (libSpell) => {

    const { _libraryId, approved, addedBy, addedByName, submittedAt, ...rest } = libSpell;

    const newSpell = createSpell({ ...rest, id: generateId(), _libraryId: libSpell._libraryId, prepared: false });

    onUpdate({ spells: [...spells, newSpell] });

    setEditingSpell(newSpell);

    setEditLevelTab('base');

  };

  

  // Spell templates — stored in globalSettings.spellTemplates

  const spellTemplates = globalSettings?.spellTemplates || [];

  

  const saveAsTemplate = (spell, templateName) => {

    const { id, _libraryId, prepared, ...rest } = spell;

    const template = { ...rest, _templateId: generateId(), _templateName: templateName, _createdAt: Date.now() };

    const updated = [...spellTemplates, template];

    if (onSaveGlobalSettings) onSaveGlobalSettings({ ...globalSettings, spellTemplates: updated });

  };

  

  const deleteTemplate = (templateId) => {

    if (onSaveGlobalSettings) {

      onSaveGlobalSettings({ ...globalSettings, spellTemplates: spellTemplates.filter(t => t._templateId !== templateId) });

    }

  };

  

  const createFromTemplate = (template) => {

    const { _templateId, _templateName, _createdAt, ...rest } = template;

    const newSpell = createSpell({ ...rest, name: `${template.name || _templateName}`, prepared: false });

    onUpdate({ spells: [...spells, newSpell] });

    setEditingSpell(newSpell);

    setEditLevelTab('base');

  };

  

  // Update spell

  const updateSpell = (spellId, updates) => {

    onUpdate({

      spells: spells.map(s => s.id === spellId ? { ...s, ...updates } : s)

    });

  };

  

  // Delete spell

  const deleteSpell = (spellId) => {

    if (window.confirm('Delete this spell?')) {

      onUpdate({ spells: spells.filter(s => s.id !== spellId) });

      setEditingSpell(null);

    }

  };

  

  // Toggle prepared

  const togglePrepared = (spellId) => {

    const spell = spells.find(s => s.id === spellId);

    if (spell) {

      updateSpell(spellId, { prepared: !spell.prepared });

    }

  };

  

  // Short/Long rest

  const handleRest = (type) => {

    let nextChar = char;



    const recoveryType = type === 'long' ? 'longRest' : 'shortRest';

    const restResult = resetSpellResourcesForRest({

      char: nextChar,

      systems,

      restType: recoveryType,

      settings,

      surface: SPELL_BINDING_SURFACE_ANY,

      applyResourceFollowUps: (startingChar, resourceEvents) => applySpellResourceFollowUpsWithResults({

        startingChar,

        resourceEvents,

        systems,

        worldContext: buildSpellWorldContext(),

      }),

    });

    nextChar = restResult.nextChar;
    consumeResourceFollowUpResults(restResult.resourceFollowUpResults, {
      charForEffects: nextChar,
      label: 'Spell rest resource follow-up',
      source: 'spell.resource-follow-up',
    });



    const patch = buildSpellPatch(char, nextChar);

    if (Object.keys(patch).length > 0) {

      onUpdate(patch);

    }

  };

  

  // Render spell level indicator

  const renderLevelBadge = (level) => {

    const colors = ['#888', '#4a90d9', '#27c95a', '#c9a227', '#c95a27', '#9b59b6', '#3498db', '#1abc9c', '#e74c3c', '#f39c12'];

    return (

      <span style={{

        display: 'inline-block',

        padding: '2px 6px',

        borderRadius: '4px',

        fontSize: '0.7rem',

        fontWeight: 'bold',

        background: colors[level] || '#888',

        color: 'white',

        marginRight: '6px',

      }}>

        {level === 0 ? 'C' : level}

      </span>

    );

  };

  

  // Casting time display

  const formatCastingTime = (time) => CASTING_TIMES[time] || time;

  

  if (!settings.showSpellPanel && !isDM) return null;

  

  // 2026-05-11 — Session 6: receipt modifiers for the long-pressed spell.
  // Decomposes spell save DC into base (8) + proficiency + casting-stat
  // mod + item bonus when applicable. The full context object lives in
  // spellPanelSpellContext; we pull the contributor fields and build
  // an ad-hoc list (no breakdown helper exists for spell DC yet —
  // unify with a real helper in A.3).
  const spellReceiptSelected = spellReceiptKey
    ? spells.find((s) => s.id === spellReceiptKey)
    : null;
  const spellReceiptModifiers = (() => {
    if (!spellReceiptSelected) return [];
    const ctx = spellPanelSpellContext || {};
    const stat = (ctx.spellStat || '').toUpperCase() || 'CHA';
    const statMod = Number.isFinite(ctx.statMod) ? ctx.statMod : 0;
    const profBonus = Number.isFinite(ctx.proficiencyBonus) ? ctx.proficiencyBonus : 0;
    const itemBonus = Number.isFinite(ctx.spellSaveDcItemBonus) ? ctx.spellSaveDcItemBonus : 0;
    const rows = [
      { effect: 8, source: 'Base spell save DC', sourceType: 'base', note: 'Spellcasting rules' },
      { effect: profBonus, source: 'Proficiency bonus', sourceType: 'system', note: 'Character level' },
      { effect: statMod, source: `${stat} modifier`, sourceType: 'base', note: 'Spellcasting stat' },
    ];
    if (itemBonus) {
      rows.push({ effect: itemBonus, source: 'Item bonus', sourceType: 'item', note: 'Equipped gear' });
    }
    return rows;
  })();

  return (

    <>

    {/* 2026-05-11 — Session 6: receipt sheet for long-pressed spell.
        Single instance; content keyed off spellReceiptKey. Title shows
        the spell name, value the computed save DC. */}
    <ReceiptSheet
      open={spellReceiptOpen}
      onClose={() => setSpellReceiptOpen(false)}
      title={spellReceiptSelected ? `${spellReceiptSelected.name} — Save DC` : ''}
      value={spellReceiptSelected ? (spellPanelSpellContext?.spellSaveDC ?? null) : null}
      modifiers={spellReceiptModifiers}
    />

    <HoverCard>

      {/* 2026-05-11 F2: sticky-top roll-result banner for spells. Same
          rationale as Skills — when the user taps Cast on a cantrip at
          the top of the list, the existing rendered result lands below
          the description, off-screen on mobile. This compact banner
          gives immediate confirmation at the top. 8s auto-dismiss
          (timer wired below); tap to dismiss early. */}
      {lastRoll && (
        <div
          onClick={() => setLastRoll(null)}
          role="button"
          aria-label={`Roll result: ${lastRoll.spell || 'Spell'} ${lastRoll.total}. Tap to dismiss.`}
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 5,
            marginBottom: '8px',
            padding: '10px 14px',
            background: 'linear-gradient(135deg, rgba(155,89,182,0.25), rgba(155,89,182,0.12))',
            border: '1px solid #9b59b6',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
            boxShadow: '0 2px 10px rgba(0,0,0,0.35)',
            backdropFilter: 'blur(4px)',
          }}
        >
          <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', flexShrink: 0 }}>🎲 {lastRoll.spell || 'Spell'}</span>
          <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffd700', flexShrink: 0, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{lastRoll.total}</span>
          {lastRoll.formula && (
            <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.55)', flexShrink: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {lastRoll.formula}{lastRoll.spellMod ? ` ${lastRoll.spellMod >= 0 ? '+' : ''}${lastRoll.spellMod}` : ''}
            </span>
          )}
          <span style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.45)', marginLeft: 'auto', flexShrink: 0 }} aria-hidden="true">✕</span>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setShowSpells(!showSpells)}>

        <h2 style={{ ...S.subtitle, marginBottom: 0 }}>✨ Spells ({spells.filter(s => s.prepared).length}/{spells.length})</h2>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

          {/* Mana/Slots display */}

          {renderSpellResourceHeader()}

          <span style={{ color: '#ffd700' }}>{showSpells ? '▼' : '▶'}</span>

        </div>

      </div>

      

      {/* Status warnings */}

      {isUsingLegacyManaSpellResource && char.manaSickness && (

        <div style={{ marginTop: '8px', padding: '8px', background: 'rgba(155,89,182,0.2)', border: '1px solid #9b59b6', borderRadius: '4px', fontSize: '0.85rem' }}>

          ⚠️ <strong>Mana Sickness:</strong> Losing 10% mana per round ({char.manaSicknessRounds} rounds remaining)

        </div>

      )}

      {isUsingLegacyManaSpellResource && char.manaDrought && (

        <div style={{ marginTop: '8px', padding: '8px', background: 'rgba(201,39,39,0.2)', border: '1px solid #c92727', borderRadius: '4px', fontSize: '0.85rem' }}>

          🔥 <strong>Mana Drought:</strong> Can only cast spells costing ≤3 mana. Exhaustion level: {char.exhaustionLevel || 0}

        </div>

      )}

      {char.concentratingOn && (

        <div style={{ marginTop: '8px', padding: '8px', background: 'rgba(201,162,39,0.2)', border: '1px solid #c9a227', borderRadius: '4px', fontSize: '0.85rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

          <span>🎯 <strong>Concentrating on:</strong> {spells.find(s => s.id === char.concentratingOn)?.name || char.concentratingOn || 'Unknown'}</span>

          <button style={{ ...S.btn, ...S.btnXs, ...S.btnDanger }} onClick={() => {

            if (window.confirm(`Drop concentration on ${spells.find(s => s.id === char.concentratingOn)?.name || char.concentratingOn}?`)) {

              if (onDropConcentration) {

                onDropConcentration(char.id);

              } else {

                onUpdate({ concentratingOn: null, concentrationEffectGroupId: null });

              }

            }

          }}>Drop</button>

        </div>

      )}

      

      {showSpells && (() => {

        const preparedSpells = filteredSpells.filter(s => s.prepared !== false);

        const unpreparedSpells = filteredSpells.filter(s => s.prepared === false);

        const displaySpells = spellListTab === 'prepared' ? preparedSpells : unpreparedSpells;

        

        // Group by level

        const grouped = {};

        displaySpells.forEach(spell => {

          const key = spell.level === 0 ? 'Cantrips' : `Level ${spell.level}`;

          if (!grouped[key]) grouped[key] = [];

          grouped[key].push(spell);

        });

        

        // Type colors/icons (same as combat)

        const typeColors = { healing: '#27c95a', buff: '#4a90d9', mark: '#9b59b6', debuff: '#e74c3c', damage: '#c92727', control: '#c9a227', utility: '#888', temp_hp: '#87ceeb', summon: '#27c9c9' };

        const typeIcons = { healing: '➕', buff: '✨', mark: '🎯', debuff: '💀', damage: '🔥', control: '🎭', utility: '🔧', temp_hp: '🩵', summon: '👻' };

        

        const inferType = (spell) => {

          const et = spell.effectType || spell.spellType;

          if (et && et !== 'utility') return et === 'temp_hp' ? 'buff' : et;

          const tags = (spell.tags || []).map(t => t.toLowerCase());

          const effect = (spell.effect || '').toLowerCase();

          if (tags.includes('healing') || effect.includes('heal') || effect.includes('hp')) return 'healing';

          if (spell.damageType || spell.attackType === 'melee' || spell.attackType === 'ranged') return 'damage';

          if (tags.includes('buff')) return 'buff';

          if (tags.includes('debuff')) return 'debuff';

          return 'utility';

        };

        

        const getUpcastSummary = (spell) => {

          if (!spell.upcastPattern || spell.upcastPattern === 'none') {

            return spell.higherLevels || '';

          }

          const labels = { dice_per_level: `+${spell.upcastDice || spell.dice || '?'} per level above ${spell.level}`, targets_per_level: `+${spell.upcastTargets || 1} target per level above ${spell.level}`, flat_per_level: `+${spell.upcastFlat || '?'} ${spell.upcastFlatField || ''} per level`, cantrip: 'Scales with character level', custom: spell.higherLevels || 'See level overrides' };

          return labels[spell.upcastPattern] || spell.higherLevels || '';

        };

        

        return (

        <div style={{ marginTop: '16px' }}>

          {/* Tab bar + controls */}

          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px', flexWrap: 'wrap' }}>

            {/* Prepared / Unprepared tabs */}

            <button style={{ ...S.btn, padding: '5px 14px', fontSize: '0.78rem', background: spellListTab === 'prepared' ? 'rgba(74,144,217,0.2)' : 'transparent', border: `1px solid ${spellListTab === 'prepared' ? '#4a90d9' : 'rgba(255,255,255,0.1)'}`, color: spellListTab === 'prepared' ? '#7db5ff' : '#888', borderRadius: '4px 4px 0 0' }} onClick={() => setSpellListTab('prepared')}>

              Prepared ({preparedSpells.length})

            </button>

            <button style={{ ...S.btn, padding: '5px 14px', fontSize: '0.78rem', background: spellListTab === 'unprepared' ? 'rgba(155,89,182,0.15)' : 'transparent', border: `1px solid ${spellListTab === 'unprepared' ? '#9b59b6' : 'rgba(255,255,255,0.1)'}`, color: spellListTab === 'unprepared' ? '#bb8fce' : '#888', borderRadius: '4px 4px 0 0' }} onClick={() => setSpellListTab('unprepared')}>

              Unprepared ({unpreparedSpells.length})

            </button>

            

            <div style={{ flex: 1 }} />

            

            {/* Level filter */}

            <select style={{ ...S.select, width: 'auto', marginBottom: 0, padding: '3px 6px', fontSize: '0.7rem' }} value={filterLevel} onChange={e => setFilterLevel(e.target.value)}>

              <option value="all">All Lvl</option>

              <option value="0">Cantrips</option>

              {[1,2,3,4,5,6,7,8,9].map(l => <option key={l} value={l}>Lv {l}</option>)}

            </select>

            

            {/* Edit List toggle */}

            <button style={{ ...S.btn, padding: '4px 10px', fontSize: '0.7rem', background: editingSpellList ? 'rgba(201,162,39,0.2)' : 'transparent', border: `1px solid ${editingSpellList ? '#c9a227' : 'rgba(255,255,255,0.1)'}`, color: editingSpellList ? '#c9a227' : '#888' }} onClick={() => setEditingSpellList(!editingSpellList)}>

              {editingSpellList ? '✓ Done' : '✏️ Edit List'}

            </button>

          </div>

          

          {/* Edit mode action bar */}

          {editingSpellList && (

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px', padding: '6px 8px', background: 'rgba(201,162,39,0.05)', borderRadius: '4px', border: '1px solid rgba(201,162,39,0.15)' }}>

              <button style={{ ...S.btn, ...S.btnSm, fontSize: '0.72rem' }} onClick={addNewSpell}>+ Add Spell</button>

              {spells.length > 0 && (

                <select value="" onChange={e => { const src = spells.find(s => s.id === e.target.value); if (src) duplicateSpell(src); }} style={{ ...S.select, width: 'auto', marginBottom: 0, padding: '3px 6px', fontSize: '0.72rem' }}>

                  <option value="">📋 Duplicate...</option>

                  {spells.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}

                </select>

              )}

              {spellTemplates.length > 0 && (

                <select value="" onChange={e => { const tpl = spellTemplates.find(t => t._templateId === e.target.value); if (tpl) createFromTemplate(tpl); }} style={{ ...S.select, width: 'auto', marginBottom: 0, padding: '3px 6px', fontSize: '0.72rem', background: 'rgba(155,89,182,0.1)', border: '1px solid rgba(155,89,182,0.3)', color: '#bb8fce' }}>

                  <option value="">📐 Template...</option>

                  {spellTemplates.map(t => <option key={t._templateId} value={t._templateId}>{t._templateName || t.name}</option>)}

                </select>

              )}

              <button style={{ ...S.btn, ...S.btnSm, fontSize: '0.72rem', background: 'rgba(74,144,217,0.15)', border: '1px solid rgba(74,144,217,0.4)', color: '#7db5ff' }} onClick={() => setShowLibrary(true)}>📚 Library</button>

              <button style={{ ...S.btn, ...S.btnSm, fontSize: '0.72rem' }} onClick={() => setShowImport(true)}>📥 Import</button>

              <button style={{ ...S.btn, ...S.btnSm, fontSize: '0.72rem' }} onClick={() => handleExport('csv')}>📤 Export</button>

            </div>

          )}

          

          {/* Mana / Rest bar */}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px', alignItems: 'center' }}>

            {spellResourceUi.summary.displayKind === 'meter' ? (

              <span style={{ fontSize: '0.72rem', color: '#7db5ff', padding: '2px 8px', background: 'rgba(74,144,217,0.1)', borderRadius: '4px' }}>

                ✨ {spellResourceUi.summary.label}: {spellResourceMeterText}

              </span>

            ) : (

              <span style={{ fontSize: '0.72rem', color: '#888', padding: '2px 8px', background: 'rgba(255,255,255,0.05)', borderRadius: '4px' }}>

                {spellResourceUi.summary.label}: {spellResourceUi.summary.detailText || 'Systems-managed'}

              </span>

            )}

            {showSpellFreeCastCounter && (

              <span style={{ fontSize: '0.65rem', color: '#888' }}>

                Free: {(char.freeCastsUsedToday || 0)}/{settings.freeCastsPerDay || 4}

              </span>

            )}

            <div style={{ flex: 1 }} />

            <button style={{ ...S.btn, padding: '3px 10px', fontSize: '0.7rem', background: 'rgba(255,165,0,0.1)', border: '1px solid rgba(255,165,0,0.3)', color: '#ffa500' }} onClick={() => handleRest('short')}>☀️ Short</button>

            <button style={{ ...S.btn, padding: '3px 10px', fontSize: '0.7rem', background: 'rgba(39,201,90,0.1)', border: '1px solid rgba(39,201,90,0.3)', color: '#27c95a' }} onClick={() => handleRest('long')}>🌙 Long</button>

            {isDM && <button style={{ ...S.btn, padding: '3px 8px', fontSize: '0.7rem' }} onClick={() => setShowSettings(true)}>⚙️</button>}

          </div>

          

          {/* Available to Learn from Library */}

          {editingSpellList && (spellLibrary || []).length > 0 && (() => {

            const charLevel = char.level || 1;

            const charClass = (char.class || '').toLowerCase();

            const knownLibIds = new Set(spells.map(s => s._libraryId).filter(Boolean));

            const available = (spellLibrary || []).filter(s => 

              s.approved && 

              !knownLibIds.has(s._libraryId) && 

              s.learnAtLevel > 0 && 

              s.learnAtLevel <= charLevel &&

              (!s.spellClass || s.spellClass.toLowerCase() === charClass || !charClass)

            );

            if (available.length === 0) return null;

            return (

              <div style={{ marginBottom: '10px', padding: '6px 8px', background: 'rgba(74,144,217,0.08)', borderRadius: '6px', border: '1px solid rgba(74,144,217,0.2)' }}>

                <div style={{ fontSize: '0.72rem', color: '#7db5ff', marginBottom: '4px', fontWeight: 'bold' }}>📚 Available to Learn ({available.length})</div>

                {available.slice(0, 5).map(libSpell => (

                  <div key={libSpell._libraryId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 6px', marginBottom: '1px', borderRadius: '3px', background: 'rgba(0,0,0,0.1)' }}>

                    <span style={{ fontSize: '0.72rem' }}>

                      {libSpell.name} <span style={{ color: '#888', fontSize: '0.6rem' }}>Lvl {libSpell.level} {libSpell.spellClass}</span>

                    </span>

                    <button style={{ ...S.btn, ...S.btnXs, background: 'rgba(39,201,90,0.15)', border: '1px solid rgba(39,201,90,0.4)', color: '#27c95a', fontSize: '0.6rem' }} onClick={() => {

                      const newSpell = createSpell({ ...libSpell, id: generateId(), _libraryId: libSpell._libraryId, prepared: false });

                      onUpdate({ spells: [...spells, newSpell] });

                    }}>+ Learn</button>

                  </div>

                ))}

                {available.length > 5 && <div style={{ fontSize: '0.6rem', color: '#888', textAlign: 'center' }}>+{available.length - 5} more</div>}

              </div>

            );

          })()}

          

          {/* Spell rows — compact combat-style */}

          {Object.keys(grouped).length === 0 ? (

            <p style={{ opacity: 0.5, textAlign: 'center', fontSize: '0.8rem' }}>

              {spellListTab === 'prepared' ? 'No prepared spells. Switch to Unprepared tab to prepare some!' : 'No unprepared spells.'}

            </p>

          ) : (

            Object.entries(grouped).map(([levelName, lvlSpells]) => (

              <div key={levelName} style={{ marginBottom: '12px' }}>

                <div style={{ fontSize: '0.78rem', color: '#c9a227', marginBottom: '4px', paddingBottom: '2px', borderBottom: '1px solid rgba(201,162,39,0.2)' }}>

                  {levelName} ({lvlSpells.length})

                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>

                  {lvlSpells.map(spell => {

                    const sType = inferType(spell);

                    const color = typeColors[sType] || '#888';

                    const icon = typeIcons[sType] || '🔮';

                    const isExpanded = expandedListSpells[spell.id];

                    const selectedLevel = resolveSelectedCastLevel(spell, spellCastLevels[spell.id]);

                    const levelOptions = getCastLevelOptions(spell);

                    const canUpcast = levelOptions.length > 1;

                    const castInfo = getCastingInfo(spell, selectedLevel);

                    const resolvedSpellData = castInfo?.resolvedSpell || spell;

                    const upcastText = getUpcastSummary(spell);

                    const resolvedDice = resolvedSpellData?.dice || '';

                    const resolvedMaxTargets = Number.isFinite(resolvedSpellData?.maxTargets)

                      ? resolvedSpellData.maxTargets

                      : (spell.maxTargets || 1);

                    

                    return (

                      <div key={spell.id} style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '6px', borderLeft: `3px solid ${color}` }}>

                        {/* 2026-04-29 — mobile reads each spell as a deliberate

                            two-tier card: identity on top, controls on a single

                            right-aligned action row below. Desktop keeps the

                            single-row flex layout. The previous flex-wrap was

                            letting the browser pick where to break, which made

                            Bless's controls look fused with its dice (controls

                            sat directly under the dropdown) while Cure Wounds's

                            upcast cluster ended up alone on row 2 looking like

                            an overflow accident. */}

                        <div style={isMobileDefault ? { display: 'flex', flexDirection: 'column', gap: '4px', padding: '6px 8px' } : { display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 8px', flexWrap: 'wrap' }}>

                          {/* — IDENTITY TIER — */}

                          <div style={isMobileDefault ? { display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 } : { display: 'contents' }}>

                            {/* Expand */}

                            <button style={{ ...S.btn, padding: '2px 4px', fontSize: '0.65rem', opacity: 0.7 }} onClick={() => setExpandedListSpells(prev => ({ ...prev, [spell.id]: !prev[spell.id] }))}>

                              {isExpanded ? '▼' : '▶'}

                            </button>



                            {/* Level badge */}

                            <span style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#4a90d9' }}>{spell.level === 0 ? 'C' : spell.level}</span>



                            {/* Type icon */}

                            <span style={{ fontSize: '0.75rem' }}>{icon}</span>



                            {/* Name — long-press opens receipt sheet (Session 6 pilot) */}

                            <span
                              style={{ fontWeight: 'bold', fontSize: '0.8rem', minWidth: '80px', cursor: 'pointer', ...spellLongPress.bind.style, minHeight: isMobileDefault ? '44px' : undefined, display: 'inline-flex', alignItems: 'center' }}
                              onClick={(e) => {
                                spellLongPress.bind.onClick(e);
                                if (e.defaultPrevented) return;
                                setExpandedListSpells(prev => ({ ...prev, [spell.id]: !prev[spell.id] }));
                              }}
                              onTouchStart={(e) => { pressingSpellRef.current = spell.id; spellLongPress.bind.onTouchStart(e); }}
                              onTouchMove={spellLongPress.bind.onTouchMove}
                              onTouchEnd={(e) => { spellLongPress.bind.onTouchEnd(e); pressingSpellRef.current = null; }}
                              onTouchCancel={(e) => { spellLongPress.bind.onTouchCancel(e); pressingSpellRef.current = null; }}
                              onMouseDown={(e) => { pressingSpellRef.current = spell.id; spellLongPress.bind.onMouseDown(e); }}
                              onMouseUp={(e) => { spellLongPress.bind.onMouseUp(e); pressingSpellRef.current = null; }}
                              onMouseLeave={(e) => { spellLongPress.bind.onMouseLeave(e); }}
                            >

                              {spell.name}

                            </span>



                            {/* Concentration */}

                            {spell.concentration && <span style={{ fontSize: '0.6rem', color: '#c9a227' }} title="Concentration">🎯</span>}



                            {/* Favorite star */}

                            {spell.favorited && <span style={{ fontSize: '0.55rem', color: '#ffd700' }}>⭐</span>}



                            {/* Dice / Effect */}

                            <span style={{ fontSize: '0.7rem', color: '#888', flex: 1, minWidth: '40px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>

                              {resolvedDice || spell.effect?.substring(0, 20) || ''}

                            </span>

                          </div>



                          {/* — ACTION TIER — */}

                          <div style={isMobileDefault ? { display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end', flexWrap: 'wrap' } : { display: 'contents' }}>

                            {/* Upcast level stepper */}

                            {canUpcast && (

                              <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>

                                <button style={{ ...S.btn, padding: '1px 4px', fontSize: '0.65rem' }} onClick={() => setSpellCastLevels(prev => ({ ...prev, [spell.id]: getShiftedCastLevel(spell, selectedLevel, -1) }))}>-</button>

                                <span style={{ fontSize: '0.65rem', color: selectedLevel > spell.level ? '#c9a227' : '#888', minWidth: '18px', textAlign: 'center' }}>L{selectedLevel}</span>

                                <button style={{ ...S.btn, padding: '1px 4px', fontSize: '0.65rem' }} onClick={() => setSpellCastLevels(prev => ({ ...prev, [spell.id]: getShiftedCastLevel(spell, selectedLevel, 1) }))}>+</button>

                              </div>

                            )}



                            {/* Cost / availability */}

                            <span style={{ fontSize: '0.65rem', padding: '1px 6px', borderRadius: '3px', color: castInfo.freeAvailable ? '#7dff7d' : castInfo.overcast ? '#d47dd4' : castInfo.displayMode === 'legacy-slots' || castInfo.displayMode === 'system-tiered' ? '#ffd27d' : '#7db5ff', background: castInfo.freeAvailable ? 'rgba(39,201,90,0.15)' : castInfo.overcast ? 'rgba(155,89,182,0.15)' : castInfo.displayMode === 'legacy-slots' || castInfo.displayMode === 'system-tiered' ? 'rgba(201,162,39,0.14)' : 'rgba(74,144,217,0.1)' }}>

                              {formatSpellCastInfoLabel(castInfo, spell)}

                            </span>

                            <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>

                              {editingSpellList && (

                                <>

                                  <button style={{ ...S.btn, padding: '2px 5px', fontSize: '0.6rem' }} onClick={() => duplicateSpell(spell)} title="Duplicate">📋</button>

                                  <button style={{ ...S.btn, ...S.btnDanger, padding: '2px 5px', fontSize: '0.6rem' }} onClick={() => { if (window.confirm(`Delete ${spell.name}?`)) onUpdate({ spells: spells.filter(s => s.id !== spell.id) }); }} title="Delete">🗑️</button>

                                </>

                              )}

                              <button style={{ ...S.btn, padding: '2px 4px', fontSize: '0.6rem', background: spell.favorited ? 'rgba(255,215,0,0.2)' : 'transparent', borderColor: spell.favorited ? 'rgba(255,215,0,0.4)' : undefined }} onClick={() => toggleFavorite(spell.id)} title={spell.favorited ? 'Unfavorite' : 'Favorite'}>

                                {spell.favorited ? '⭐' : '☆'}

                              </button>

                              <button style={{ ...S.btn, ...S.btnMana, padding: '2px 10px', fontSize: '0.7rem', fontWeight: 'bold', ...(!castInfo.canCast && !isDM ? { opacity: 0.4 } : {}) }} disabled={!castInfo.canCast && !isDM} onClick={() => castSpell(spell, canUpcast ? selectedLevel : null)} title={`Cast ${spell.name}${selectedLevel > spell.level ? ` at level ${selectedLevel}` : ''}`}>

                                Cast

                              </button>

                            </div>

                          </div>

                          

                          {/* Inline roll result (from casting) — far right */}

                          {resolvedDice && (() => {

                            const spellRoll = spellListRolls[spell.id];

                            if (!spellRoll || !spellRoll.breakdown) return null;

                            return (

                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '3px', fontSize: '0.75rem', background: 'rgba(0,0,0,0.3)', padding: '3px 6px', borderRadius: '4px', flexWrap: 'wrap', position: 'relative' }}>

                                {spellRoll.breakdown.map((part, idx) => {

                                  const colors = { base: '#e8d5b7', modifier: '#4a90d9', mark: '#9b59b6', debuff: '#e74c3c', buff: '#27c95a' };

                                  const color = colors[part.type] || '#888';

                                  let displayValue = '';

                                  if (part.rolls && part.rolls.length > 0) displayValue = `[${part.rolls.join('+')}]`;

                                  else if (part.value !== undefined) displayValue = `+${part.value}`;

                                  let sourceLabel = part.dice || (part.type === 'modifier' ? 'mod' : part.source || '');

                                  return (

                                    <React.Fragment key={idx}>

                                      {idx > 0 && <span style={{ color: '#666', alignSelf: 'center', marginTop: '-6px' }}>+</span>}

                                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

                                        <span style={{ padding: '1px 5px', background: `${color}25`, border: `1px solid ${color}60`, borderRadius: '3px', color, fontWeight: 'bold', fontFamily: 'monospace', fontSize: '0.78rem' }}>

                                          {displayValue}

                                        </span>

                                        <span style={{ fontSize: '0.5rem', color, opacity: 0.9, marginTop: '1px' }}>{sourceLabel}</span>

                                      </div>

                                    </React.Fragment>

                                  );

                                })}

                                <span style={{ color: '#666', alignSelf: 'center', marginTop: '-6px', marginLeft: '2px' }}>=</span>

                                <span style={{ color: '#ffd700', fontWeight: 'bold', fontSize: '1rem', alignSelf: 'center', marginTop: '-6px', marginLeft: '2px' }}>

                                  {spellRoll.grandTotal || spellRoll.total}

                                </span>

                                <button

                                  onClick={(e) => { e.stopPropagation(); setSpellListRolls(prev => { const n = { ...prev }; delete n[spell.id]; return n; }); }}

                                  style={{ position: 'absolute', top: '-4px', right: '-4px', background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '50%', color: '#888', fontSize: '0.5rem', width: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, lineHeight: 1 }}

                                  title="Dismiss"

                                >✕</button>

                              </div>

                            );

                          })()}

                        </div>

                        

                        {/* Expanded details */}

                        {isExpanded && (

                          <div style={{ padding: '8px 10px 10px 28px', borderTop: '1px solid rgba(255,255,255,0.06)', fontSize: '0.72rem', display: 'flex', flexDirection: 'column', gap: '5px' }}>

                            <div style={{ color: '#888' }}>

                              {spell.school} • {CASTING_TIMES[spell.castingTime] || spell.castingTime || '1 action'} • {spell.range || '—'} • {spell.duration || '—'}

                              {spell.components && <span> • {typeof spell.components === 'object' ? formatComponents(spell.components) : spell.components}</span>}

                            </div>

                            {spell.description && (() => {
                              // Session 6 §4h: chunk long descriptions on
                              // mobile to a teaser + "Read more" toggle.
                              // 180 chars is roughly 3 lines on a 6.1" phone;
                              // short descriptions render in full.
                              const CHUNK_THRESHOLD = 180;
                              const isLong = spell.description.length > CHUNK_THRESHOLD;
                              const expanded = !!descExpanded[spell.id];
                              const shouldChunk = isMobileDefault && isLong && !expanded;
                              let body = spell.description;
                              if (shouldChunk) {
                                // Truncate at the last word boundary at or before the threshold.
                                const cut = spell.description.lastIndexOf(' ', CHUNK_THRESHOLD);
                                body = spell.description.slice(0, cut > 60 ? cut : CHUNK_THRESHOLD) + '…';
                              }
                              return (
                                <div style={{ color: '#aaa' }}>
                                  {body}
                                  {isMobileDefault && isLong ? (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); setDescExpanded(prev => ({ ...prev, [spell.id]: !prev[spell.id] })); }}
                                      style={{ marginLeft: '6px', background: 'transparent', border: 'none', color: '#7db5ff', cursor: 'pointer', padding: 0, fontSize: 'inherit', textDecoration: 'underline', minHeight: '32px' }}
                                    >
                                      {expanded ? 'Show less' : 'Read more'}
                                    </button>
                                  ) : null}
                                </div>
                              );
                            })()}

                            {upcastText && (

                              <div style={{ color: '#c9a227', padding: '4px 8px', background: 'rgba(201,162,39,0.08)', borderRadius: '3px', borderLeft: '2px solid rgba(201,162,39,0.4)' }}>

                                <strong>↑ Upcast:</strong> {upcastText}

                              </div>

                            )}

                            {selectedLevel > spell.level && (

                              <div style={{ color: '#7db5ff', fontSize: '0.68rem' }}>

                                At level {selectedLevel}: {resolvedDice || '—'} dice{spell.upcastPattern === 'targets_per_level' ? `, ${resolvedMaxTargets} targets` : ''}

                              </div>

                            )}

                            {castInfo.overcast && castInfo.displayMode === 'legacy-mana' && (

                              <div style={{ color: '#d47dd4', fontSize: '0.68rem' }}>

                                ⚠️ Overcasting: Double mana + Mana Sickness

                              </div>

                            )}

                            {spell.buffText && <div style={{ color: '#4a90d9', fontSize: '0.68rem' }}>Status: {spell.buffText}</div>}

                            <div style={{ marginTop: '2px', display: 'flex', gap: '4px' }}>

                              <button style={{ ...S.btn, ...S.btnEdit, padding: '2px 6px', fontSize: '0.58rem' }} onClick={() => { setEditingSpell(spell); setEditLevelTab('base'); }}>

                                ✏️ Edit

                              </button>

                              <button style={{ ...S.btn, padding: '2px 6px', fontSize: '0.58rem' }} onClick={() => togglePrepared(spell.id)}>

                                {spell.prepared !== false ? '📕 Unprepare' : '📖 Prepare'}

                              </button>

                            </div>

                          </div>

                        )}

                      </div>

                    );

                  })}

                </div>

              </div>

            ))

          )}

        </div>

        );

      })()}



      {/* 2026-04-27: techniques embedded as a subsection inside the

          spells card. They live here for now; eventually they'll get

          their own dedicated card (see CLAUDE-NOTES-GENERAL). The

          embedded form drops the outer purple card and uses a top

          border to separate from the spells list. The sub-panel

          self-hides if the character has no castable techniques. */}

      <TechniquesPanel

        char={char}

        systems={systems}

        spellLibrary={spellLibrary}

        worldContext={{ currentLocation: char.currentLocation, combat: null, timeOfDay: null, chars, journal, npcLibrary }}

        onUpdateChar={onUpdate}

        onPushAuditEvent={null}

        onNotify={onNotify}

        isPlayer={!isDM}

        embedded={true}

        groupTravelLog={groupTravelLog}

        onSaveGroupTravelLog={onSaveGroupTravelLog}

        messages={messages}

        onSaveMessages={onSaveMessages}

        onSendStatusMessage={onSendStatusMessage}

        npcLibrary={npcLibrary}

        onSaveNpcLibrary={onSaveNpcLibrary}

      />

    </HoverCard>



      {/* Cast confirmation modal (for upcasting) */}

      {showCastConfirm && (

        <div style={S.modal} onClick={() => setShowCastConfirm(null)}>

          <div style={{ ...S.modalContent, maxWidth: '400px' }} onClick={e => e.stopPropagation()}>

            <h3 style={{ color: '#ffd700', marginBottom: '16px' }}>Cast {showCastConfirm.name}</h3>

            <div style={{ marginBottom: '16px' }}>

              <label style={S.label}>Cast at Level:</label>

              <select style={S.select} value={castLevel} onChange={e => setCastLevel(parseInt(e.target.value, 10))}>

                {getCastLevelOptions(showCastConfirm).map(lvl => {

                  const info = getCastingInfo(showCastConfirm, lvl);

                  return (

                    <option key={lvl} value={lvl} disabled={!info.canCast && !isDM}>

                      Level {lvl} - {formatSpellCastInfoLabel(info, showCastConfirm)}

                    </option>

                  );

                })}

              </select>

            </div>

            {showCastConfirm.higherLevels && castLevel > showCastConfirm.level && (

              <div style={{ marginBottom: '16px', padding: '8px', background: 'rgba(74,144,217,0.2)', borderRadius: '4px', fontSize: '0.85rem' }}>

                <strong>At Higher Levels:</strong> {showCastConfirm.higherLevels}

              </div>

            )}

            {getCastingInfo(showCastConfirm, castLevel).overcast && getCastingInfo(showCastConfirm, castLevel).displayMode === 'legacy-mana' && (

              <div style={{ marginBottom: '16px', padding: '8px', background: 'rgba(155,89,182,0.2)', border: '1px solid #9b59b6', borderRadius: '4px', fontSize: '0.85rem' }}>

                ⚠️ <strong>Overcasting!</strong> Costs double mana and causes Mana Sickness (1d4 rounds).

              </div>

            )}

            <div style={{ display: 'flex', gap: '8px' }}>

              <button style={{ ...S.btn, ...S.btnMana, flex: 1 }} onClick={() => castSpell(showCastConfirm, castLevel)}>

                ✨ Cast

              </button>

              <button style={{ ...S.btn, flex: 1 }} onClick={() => setShowCastConfirm(null)}>Cancel</button>

            </div>

          </div>

        </div>

      )}

      

      {/* Import modal */}

      {showImport && (

        <div style={S.modal} onClick={() => setShowImport(false)}>

          <div style={{ ...S.modalContent, maxWidth: '600px' }} onClick={e => e.stopPropagation()}>

            <h3 style={{ color: '#ffd700', marginBottom: '16px' }}>📥 Import Spells</h3>

            

            <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(0,0,0,0.3)', borderRadius: '4px', fontSize: '0.85rem' }}>

              <strong>Supported formats:</strong>

              <ul style={{ margin: '8px 0 0 16px' }}>

                <li><strong>CSV/TSV:</strong> Headers: Name, Level, School, Time, Range, Components, Duration, Effect, Dice, Hit/DC, Description, Higher Levels, Tags, Source, Concentration</li>

                <li><strong>Block format:</strong> Each spell starts with === SPELL === followed by Key: Value pairs</li>

              </ul>

            </div>

            

            <textarea 

              style={{ ...S.input, minHeight: '200px', fontFamily: 'monospace', fontSize: '0.8rem' }}

              placeholder={`Example CSV:\nName,Level,School,Time,Range,Components,Duration,Effect,Description\nFireball,3,Evocation,1A,150ft,V S M,Instant,8d6 fire,A bright streak...\n\nOr Block format:\n=== SPELL ===\nName: Fireball\nLevel: 3\nSchool: Evocation\n...`}

              value={importText}

              onChange={e => setImportText(e.target.value)}

            />

            

            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>

              <button style={{ ...S.btn, ...S.btnSuccess, flex: 1 }} onClick={handleImport}>Import</button>

              <button style={{ ...S.btn, flex: 1 }} onClick={() => { setShowImport(false); setImportText(''); }}>Cancel</button>

            </div>

            

            <div style={{ marginTop: '16px' }}>

              <button style={{ ...S.btn, ...S.btnSm }} onClick={() => {

                // Load sample spells

                const newSpells = SAMPLE_SPELLS.map(s => ({ ...s, id: generateId() }));

                onUpdate({ spells: [...spells, ...newSpells] });

                setShowImport(false);

                window.alert('Loaded 5 sample spells!');

              }}>📚 Load Sample Spells</button>

            </div>

          </div>

        </div>

      )}

      

      {/* Edit spell modal */}

      {editingSpell && (() => {

        const baseLvl = editingSpell.level || 0;

        const configuredLevels = Object.keys(editingSpell.levelData || {}).map(Number).sort((a, b) => a - b);

        const isBaseTab = editLevelTab === 'base';

        const activeLevelData = isBaseTab ? null : (editingSpell.levelData?.[editLevelTab] || {});

        const isCantrip = baseLvl === 0;

        const upPat = editingSpell.upcastPattern || 'none';

        

        const getVal = (field) => {

          if (isBaseTab) return editingSpell[field];

          return activeLevelData?.[field] !== undefined ? activeLevelData[field] : editingSpell[field];

        };

        const setField = (field, value) => {

          if (isBaseTab) {

            setEditingSpell({ ...editingSpell, [field]: value });

          } else {

            const newLD = { ...(editingSpell.levelData || {}) };

            newLD[editLevelTab] = { ...(newLD[editLevelTab] || {}), [field]: value };

            setEditingSpell({ ...editingSpell, levelData: newLD });

          }

        };

        const clearOverride = (field) => {

          if (isBaseTab) return;

          const newLD = { ...(editingSpell.levelData || {}) };

          const d = { ...(newLD[editLevelTab] || {}) };

          delete d[field];

          if (Object.keys(d).length === 0) delete newLD[editLevelTab]; else newLD[editLevelTab] = d;

          setEditingSpell({ ...editingSpell, levelData: newLD });

        };

        const isOverridden = (field) => !isBaseTab && activeLevelData?.[field] !== undefined;

        const addLevel = (lvl) => {

          const prev = configuredLevels.filter(l => l < lvl);

          const prevData = prev.length > 0 ? { ...(editingSpell.levelData?.[prev[prev.length - 1]] || {}) } : {};

          setEditingSpell({ ...editingSpell, levelData: { ...(editingSpell.levelData || {}), [lvl]: { ...prevData } } });

          setEditLevelTab(lvl);

        };

        const removeLevel = (lvl) => {

          const newLD = { ...(editingSpell.levelData || {}) }; delete newLD[lvl];

          setEditingSpell({ ...editingSpell, levelData: newLD });

          setEditLevelTab('base');

        };

        

        // Parse components (migrate string → structured on the fly)

        const comp = migrateComponents(editingSpell.components);

        const setComp = (updates) => {

          const newComp = { ...comp, ...updates };

          setEditingSpell({ ...editingSpell, components: formatComponents(newComp), materialConsumed: editingSpell.materialConsumed, materialGoldCost: editingSpell.materialGoldCost });

        };

        

        // Collapsible section header

        const Sec = ({ id, label, icon, color }) => (

          <div 

            onClick={() => setEditorSections(s => ({ ...s, [id]: !s[id] }))}

            style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', padding: '6px 0', borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: '4px', userSelect: 'none' }}

          >

            <span style={{ fontSize: '0.6rem', color: '#666', transition: 'transform 0.15s', transform: editorSections[id] ? 'rotate(90deg)' : 'none' }}>▶</span>

            <span style={{ fontSize: '0.78rem', color: color || '#c9a227', fontWeight: 'bold' }}>{icon} {label}</span>

          </div>

        );

        

        

        const inp = (props) => ({ ...S.input, ...props });

        

        // Automation indicator dot — shows green dot for fields that affect combat

        const A = () => <span style={{ color: '#27c95a', fontSize: '0.45rem', marginRight: '2px', verticalAlign: 'middle' }}>⬤</span>;

        

        return (

        <div style={S.modal} onClick={() => setEditingSpell(null)}>

          <div style={{ ...S.modalContent, maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>

              <h3 style={{ color: '#ffd700', margin: 0, fontSize: '1rem' }}>✏️ Edit Spell</h3>

              <button style={{ ...S.btn, ...S.btnXs }} onClick={() => setEditingSpell(null)}>✕</button>

            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', padding: '3px 8px', background: 'rgba(255,255,255,0.03)', borderRadius: '4px', fontSize: '0.6rem', color: '#888' }}>

              <span><span style={{ color: '#27c95a', fontSize: '0.5rem' }}>⬤</span> = Affects combat automation</span>

              <span style={{ color: '#555' }}>No dot = Display / reference only</span>

            </div>

            

            {/* ═══ CORE IDENTITY (always visible) ═══ */}

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px', marginBottom: '8px' }}>

              <div>

                <label style={{ ...S.label, fontSize: '0.68rem' }}>Name *</label>

                <input style={S.input} value={editingSpell.name} onChange={e => setEditingSpell({ ...editingSpell, name: e.target.value })} />

              </div>

              <div>

                <label style={{ ...S.label, fontSize: '0.68rem' }}>Level</label>

                <select style={S.select} value={editingSpell.level} onChange={e => setEditingSpell({ ...editingSpell, level: parseInt(e.target.value) })}>

                  <option value={0}>Cantrip</option>

                  {[1,2,3,4,5,6,7,8,9].map(l => <option key={l} value={l}>{l}</option>)}

                </select>

              </div>

              <div>

                <label style={{ ...S.label, fontSize: '0.68rem' }}>School</label>

                <select style={S.select} value={editingSpell.school} onChange={e => setEditingSpell({ ...editingSpell, school: e.target.value })}>

                  {SPELL_SCHOOLS.map(s => <option key={s.id} value={s.id}>{s.icon} {s.name}</option>)}

                </select>

              </div>

            </div>

            <div style={{ display: 'flex', gap: '14px', marginBottom: '6px', flexWrap: 'wrap' }}>

              <label style={{ cursor: 'pointer', fontSize: '0.75rem' }}>

                <input type="checkbox" checked={editingSpell.ritual} onChange={e => setEditingSpell({ ...editingSpell, ritual: e.target.checked })} /> Ritual

              </label>

              <label style={{ cursor: 'pointer', fontSize: '0.75rem' }}>

                <input type="checkbox" checked={editingSpell.concentration} onChange={e => setEditingSpell({ ...editingSpell, concentration: e.target.checked })} /> <A/>Concentration

              </label>

              <label style={{ cursor: 'pointer', fontSize: '0.75rem' }}>

                <input type="checkbox" checked={editingSpell.prepared} onChange={e => setEditingSpell({ ...editingSpell, prepared: e.target.checked })} /> <A/>Prepared

              </label>

              <label style={{ cursor: 'pointer', fontSize: '0.75rem' }}>

                <input type="checkbox" checked={editingSpell.addMod} onChange={e => setEditingSpell({ ...editingSpell, addMod: e.target.checked })} /> <A/>+Spell Mod

              </label>

            </div>

            

            {/* ═══ CASTING ═══ */}

            <Sec id="casting" label="Casting" icon="🎯" />

            {editorSections.casting && (

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '6px', paddingLeft: '12px' }}>

                <div>

                  <label style={{ ...S.label, fontSize: '0.68rem' }}>Casting Time</label>

                  <select style={S.select} value={editingSpell.castingTime} onChange={e => setEditingSpell({ ...editingSpell, castingTime: e.target.value })}>

                    {Object.entries(CASTING_TIMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}

                  </select>

                </div>

                <div>

                  <label style={{ ...S.label, fontSize: '0.68rem' }}>Range</label>

                  <input style={S.input} value={editingSpell.range || ''} onChange={e => setEditingSpell({ ...editingSpell, range: e.target.value })} placeholder="30 ft, Touch, Self..." />

                </div>

                <div>

                  <label style={{ ...S.label, fontSize: '0.68rem' }}>Duration</label>

                  <input style={S.input} value={editingSpell.duration || ''} onChange={e => setEditingSpell({ ...editingSpell, duration: e.target.value })} placeholder="Instantaneous, 1 minute..." />

                </div>

                {/* Components */}

                <div style={{ gridColumn: 'span 3' }}>

                  <label style={{ ...S.label, fontSize: '0.68rem' }}>Components</label>

                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>

                    <label style={{ cursor: 'pointer', fontSize: '0.72rem' }}><input type="checkbox" checked={comp.v} onChange={e => setComp({ v: e.target.checked })} /> V</label>

                    <label style={{ cursor: 'pointer', fontSize: '0.72rem' }}><input type="checkbox" checked={comp.s} onChange={e => setComp({ s: e.target.checked })} /> S</label>

                    <label style={{ cursor: 'pointer', fontSize: '0.72rem' }}><input type="checkbox" checked={!!comp.m} onChange={e => setComp({ m: e.target.checked ? 'material' : '' })} /> M</label>

                    {comp.m && (

                      <>

                        <input style={{ ...S.input, flex: 1, marginBottom: 0, minWidth: '120px' }} value={comp.m} onChange={e => setComp({ m: e.target.value })} placeholder="Material description..." />

                        <label style={{ cursor: 'pointer', fontSize: '0.65rem', color: '#888' }}>

                          <input type="checkbox" checked={editingSpell.materialConsumed || false} onChange={e => setEditingSpell({ ...editingSpell, materialConsumed: e.target.checked })} /> Consumed

                        </label>

                        <NumberField
                          style={{ ...S.input, width: '60px', marginBottom: 0 }}
                          value={editingSpell.materialGoldCost}
                          onCommit={(value) => setEditingSpell({ ...editingSpell, materialGoldCost: value })}
                          format={formatZeroAsBlank}
                          emptyValue={0}
                          min={0}
                          placeholder="GP"
                          title="Gold cost"
                        />

                      </>

                    )}

                  </div>

                </div>

                {editingSpell.castingTime === '1R' && (

                  <div style={{ gridColumn: 'span 3' }}>

                    <label style={{ ...S.label, fontSize: '0.68rem' }}>Reaction Trigger</label>

                    <input style={S.input} value={editingSpell.reactionTrigger || ''} onChange={e => setEditingSpell({ ...editingSpell, reactionTrigger: e.target.value })} placeholder="which you take when..." />

                  </div>

                )}

              </div>

            )}

            

            {/* ═══ EFFECT & TARGETING ═══ */}

            <Sec id="effect" label="Effect & Targeting" icon="⚡" />

            {editorSections.effect && (() => {

              const et = editingSpell.effectType || editingSpell.spellType || '';

              const showAppliesTo = ['buff','debuff','temp_hp','mark'].includes(et);

              

              // Applies-to categories for multi-select

              const APPLIES_TO_OPTIONS = [

                { id: 'attack_rolls', icon: '⚔️', label: 'Attack Rolls' },

                { id: 'saving_throws', icon: '🛡️', label: 'Saving Throws' },

                { id: 'damage', icon: '💥', label: 'Damage Dealt' },

                { id: 'healing_done', icon: '💚', label: 'Healing Done' },

                { id: 'healing_received', icon: '➕', label: 'Healing Received' },

                { id: 'ac', icon: '🛡️', label: 'Armor Class' },

                { id: 'ability_checks', icon: '📊', label: 'Ability Checks' },

                { id: 'speed', icon: '🏃', label: 'Speed' },

                { id: 'hp_max', icon: '❤️', label: 'Max HP' },

                { id: 'custom', icon: '📝', label: 'Custom' },

              ];

              

              // Normalize buffAppliesTo to array

              const appliesTo = Array.isArray(editingSpell.buffAppliesTo) 

                ? editingSpell.buffAppliesTo 

                : (editingSpell.buffAppliesTo ? [editingSpell.buffAppliesTo] : []);

              

              const toggleAppliesTo = (id) => {

                const updated = appliesTo.includes(id) ? appliesTo.filter(x => x !== id) : [...appliesTo, id];

                // Auto-generate label

                const dice = editingSpell.dice || editingSpell.bonusDice || '';

                const name = editingSpell.name || 'Effect';

                const selectedLabels = updated.filter(x => x !== 'custom').map(x => APPLIES_TO_OPTIONS.find(o => o.id === x)?.label?.toLowerCase()).filter(Boolean);

                const prefix = dice ? `+${dice} ` : '';

                const autoLabel = selectedLabels.length > 0 ? `${name} (${prefix}${selectedLabels.join(', ')})` : name;

                setEditingSpell({ ...editingSpell, buffAppliesTo: updated, buffText: autoLabel, applyBuff: autoLabel });

              };

              

              return (

              <div style={{ paddingLeft: '12px', marginBottom: '6px' }}>

                {/* Row 1: Effect Type + Dice + Damage Type/Effect Text */}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '6px' }}>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.68rem' }}><A/>Effect Type</label>

                    <select style={S.select} value={et} onChange={e => {

                      const newEt = e.target.value;

                      const updates = { effectType: newEt, spellType: newEt };

                      if (newEt === 'healing') updates.addMod = true;

                      setEditingSpell({ ...editingSpell, ...updates });

                    }}>

                      <option value="">None/Utility</option>

                      <option value="damage">⚔️ Damage</option>

                      <option value="healing">➕ Healing</option>

                      <option value="buff">✨ Buff</option>

                      <option value="debuff">💀 Debuff</option>

                      <option value="temp_hp">🩵 Temp HP</option>

                      <option value="control">🎭 Control</option>

                      <option value="utility">🔧 Utility</option>

                      <option value="summon">👻 Summon</option>

                      <option value="mark">🎯 Mark</option>

                    </select>

                  </div>

                  <SpellEditorFieldWrap isOverridden={isOverridden} clearOverride={clearOverride} auto field="dice" label="Dice">

                    <input style={{ ...S.input, borderColor: isOverridden('dice') ? '#4a90d9' : undefined }} value={getVal('dice') || ''} onChange={e => setField('dice', e.target.value)} placeholder="1d8, 4d6..." />

                  </SpellEditorFieldWrap>

                  {(et === 'damage') ? (

                    <div>

                      <label style={{ ...S.label, fontSize: '0.68rem' }}><A/>Damage Type</label>

                      <select style={S.select} value={editingSpell.damageType || ''} onChange={e => setEditingSpell({ ...editingSpell, damageType: e.target.value })}>

                        <option value="">—</option>

                        {Object.entries(DAMAGE_TYPES).filter(([k]) => k !== 'raidwide').map(([key, dt]) => (

                          <option key={key} value={key}>{dt.icon} {dt.name}</option>

                        ))}

                      </select>

                    </div>

                  ) : (

                    <div>

                      <label style={{ ...S.label, fontSize: '0.68rem' }}>Effect Text</label>

                      <input style={S.input} value={editingSpell.effect || ''} onChange={e => setEditingSpell({ ...editingSpell, effect: e.target.value })} placeholder="1d8+mod HP, Buff..." />

                    </div>

                  )}

                </div>

                

                {/* Row 2: Applies To (multi-select pills) — right under effect type */}

                {showAppliesTo && (

                  <div style={{ marginBottom: '8px', padding: '8px', background: 'rgba(74,144,217,0.04)', borderRadius: '6px', border: '1px solid rgba(74,144,217,0.1)' }}>

                    <label style={{ ...S.label, fontSize: '0.68rem', marginBottom: '4px' }}><A/>Applies To <span style={{ color: '#666', fontWeight: 'normal' }}>(select all that apply)</span></label>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>

                      {APPLIES_TO_OPTIONS.map(opt => {

                        const active = appliesTo.includes(opt.id);

                        return (

                          <button key={opt.id}

                            style={{ ...S.btn, padding: '3px 10px', fontSize: '0.68rem', borderRadius: '12px', background: active ? 'rgba(74,144,217,0.2)' : 'transparent', border: `1px solid ${active ? '#4a90d9' : 'rgba(255,255,255,0.1)'}`, color: active ? '#7db5ff' : '#888' }}

                            onClick={() => toggleAppliesTo(opt.id)}

                          >{opt.icon} {opt.label}</button>

                        );

                      })}

                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '4px' }}>

                      <div>

                        <label style={{ ...S.label, fontSize: '0.68rem' }}><A/>Status Label</label>

                        <input style={S.input} value={editingSpell.buffText || editingSpell.applyBuff || ''} onChange={e => setEditingSpell({ ...editingSpell, buffText: e.target.value, applyBuff: e.target.value })} placeholder="Bless (+1d4 attack rolls, saving throws)" />

                      </div>

                    </div>

                    {appliesTo.length > 0 && !appliesTo.includes('custom') && (

                      <div style={{ fontSize: '0.6rem', color: '#888', padding: '3px 6px', marginTop: '4px', background: 'rgba(0,0,0,0.15)', borderRadius: '3px' }}>

                        <A/> <strong style={{ color: '#aaa' }}>When cast:</strong> Status pill applied to target. {

                          appliesTo.includes('damage') ? 'Bonus dice auto-roll on damage to marked targets. ' : ''

                        }{

                          appliesTo.includes('attack_rolls') ? 'Buff dice auto-roll on attack rolls. ' : ''

                        }{

                          (appliesTo.includes('ac') || appliesTo.includes('speed') || appliesTo.includes('hp_max')) ? 'Stat changes are tracked as reminders — DM adjusts values. ' : ''

                        }{

                          (appliesTo.includes('saving_throws') || appliesTo.includes('ability_checks')) ? 'Reminder shown on saves/checks. ' : ''

                        }

                      </div>

                    )}

                  </div>

                )}

                

                {/* Row 3: Targeting */}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '6px' }}>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.68rem' }}><A/>Target Type</label>

                    <select style={S.select} value={editingSpell.targetType || 'single'} onChange={e => setEditingSpell({ ...editingSpell, targetType: e.target.value })}>

                      <option value="self">Self</option>

                      <option value="touch">Touch</option>

                      <option value="single">Single Target</option>

                      <option value="multi">Multi-Target</option>

                      <option value="aoe">Area of Effect</option>

                      <option value="special">Special</option>

                    </select>

                  </div>

                  {(editingSpell.targetType === 'multi' || editingSpell.targetType === 'single') && (

                    <SpellEditorFieldWrap isOverridden={isOverridden} clearOverride={clearOverride} auto field="maxTargets" label="Max Targets">

                      <NumberField
                        style={{ ...S.input, borderColor: isOverridden('maxTargets') ? '#4a90d9' : undefined }}
                        value={getVal('maxTargets') ?? 1}
                        onCommit={(value) => setField('maxTargets', value)}
                        emptyValue={1}
                        min={0}
                        placeholder="0=∞"
                      />

                    </SpellEditorFieldWrap>

                  )}

                  <div>

                    <label style={{ ...S.label, fontSize: '0.68rem' }}>Target Filter</label>

                    <select style={S.select} value={editingSpell.targetFilter || 'any'} onChange={e => setEditingSpell({ ...editingSpell, targetFilter: e.target.value })}>

                      <option value="any">Any</option>

                      <option value="willing">Willing creatures</option>

                      <option value="hostile">Hostile creatures</option>

                      <option value="creature">Creatures only</option>

                      <option value="object">Objects only</option>

                    </select>

                  </div>

                </div>

                

                {/* Row 4: Mark/Debuff bonus fields */}

                {(et === 'mark' || et === 'debuff') && (

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '6px' }}>

                    <SpellEditorFieldWrap isOverridden={isOverridden} clearOverride={clearOverride} auto field="bonusDice" label="Bonus Dice">

                      <input style={{ ...S.input, borderColor: isOverridden('bonusDice') ? '#4a90d9' : undefined }} value={getVal('bonusDice') || ''} onChange={e => setField('bonusDice', e.target.value)} placeholder="1d6, 2d8..." />

                    </SpellEditorFieldWrap>

                    <div>

                      <label style={{ ...S.label, fontSize: '0.68rem' }}><A/>% Increase</label>

                      <NumberField
                        style={S.input}
                        value={editingSpell.percentIncrease}
                        onCommit={(value) => setEditingSpell({ ...editingSpell, percentIncrease: value })}
                        format={formatZeroAsBlank}
                        emptyValue={0}
                        min={0}
                      />

                    </div>

                    <div>

                      <label style={{ ...S.label, fontSize: '0.68rem' }}><A/>Duration (turns)</label>

                      <NumberField
                        style={S.input}
                        value={editingSpell.debuffDuration}
                        onCommit={(value) => setEditingSpell({ ...editingSpell, debuffDuration: value })}
                        format={formatZeroAsBlank}
                        emptyValue={0}
                        min={0}
                      />

                    </div>

                  </div>

                )}

                

                {/* Row 5: Attack / Save — at the bottom */}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.68rem' }}><A/>Attack Type</label>

                    <select style={S.select} value={editingSpell.attackType || 'none'} onChange={e => setEditingSpell({ ...editingSpell, attackType: e.target.value })}>

                      <option value="none">None</option>

                      <option value="melee">Melee Spell Attack</option>

                      <option value="ranged">Ranged Spell Attack</option>

                      <option value="save">Saving Throw</option>

                    </select>

                  </div>

                  {editingSpell.attackType === 'save' && (

                    <>

                      <div>

                        <label style={{ ...S.label, fontSize: '0.68rem' }}><A/>Save Ability</label>

                        <select style={S.select} value={editingSpell.saveAbility || ''} onChange={e => setEditingSpell({ ...editingSpell, saveAbility: e.target.value })}>

                          <option value="">—</option>

                          {['str','dex','con','int','wis','cha'].map(a => <option key={a} value={a}>{a.toUpperCase()}</option>)}

                        </select>

                      </div>

                      <div>

                        <label style={{ ...S.label, fontSize: '0.68rem' }}><A/>On Save</label>

                        <select style={S.select} value={editingSpell.saveEffect || 'negates'} onChange={e => setEditingSpell({ ...editingSpell, saveEffect: e.target.value })}>

                          <option value="negates">Negates (no effect)</option>

                          <option value="half">Half damage</option>

                          <option value="special">Special (see desc)</option>

                          <option value="none">None (auto-hit)</option>

                        </select>

                      </div>

                    </>

                  )}

                </div>

              </div>

              );

            })()}

            

            {/* ═══ SCALING ═══ */}

            <Sec id="scaling" label="Scaling / Higher Levels" icon="📈" color="#4a90d9" />

            {editorSections.scaling && (

              <div style={{ paddingLeft: '12px', marginBottom: '6px' }}>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '6px' }}>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.68rem' }}><A/>Upcast Pattern</label>

                    <select style={S.select} value={editingSpell.upcastPattern || 'none'} onChange={e => setEditingSpell({ ...editingSpell, upcastPattern: e.target.value })}>

                      <option value="none">No scaling</option>

                      <option value="dice_per_level">+Dice per level above base</option>

                      <option value="targets_per_level">+Targets per level above base</option>

                      <option value="flat_per_level">+Flat bonus per level above base</option>

                      <option value="cantrip">Cantrip (character level scaling)</option>

                      <option value="custom">Custom (per-level overrides only)</option>

                    </select>

                  </div>

                  {upPat === 'dice_per_level' && (

                    <div>

                      <label style={{ ...S.label, fontSize: '0.68rem' }}><A/>+Dice Per Level</label>

                      <input style={S.input} value={editingSpell.upcastDice || ''} onChange={e => setEditingSpell({ ...editingSpell, upcastDice: e.target.value })} placeholder="1d8, 1d6..." />

                    </div>

                  )}

                  {upPat === 'targets_per_level' && (

                    <div>

                      <label style={{ ...S.label, fontSize: '0.68rem' }}><A/>+Targets Per Level</label>

                      <NumberField
                        style={S.input}
                        value={editingSpell.upcastTargets}
                        onCommit={(value) => setEditingSpell({ ...editingSpell, upcastTargets: value })}
                        format={formatZeroAsBlank}
                        emptyValue={0}
                        min={1}
                        placeholder="1"
                      />

                    </div>

                  )}

                  {upPat === 'flat_per_level' && (

                    <div>

                      <label style={{ ...S.label, fontSize: '0.68rem' }}><A/>+Flat Per Level</label>

                      <input style={S.input} type="number" value={editingSpell.upcastFlat || ''} onChange={e => setEditingSpell({ ...editingSpell, upcastFlat: parseInt(e.target.value) || 0 })} placeholder="5" />

                    </div>

                  )}

                </div>

                

                <div>

                  <label style={{ ...S.label, fontSize: '0.68rem' }}>Higher Levels (text)</label>

                  <input style={S.input} value={editingSpell.higherLevels || ''} onChange={e => setEditingSpell({ ...editingSpell, higherLevels: e.target.value })} placeholder="+1d8 per level above 1st" />

                </div>

                

                {/* Per-Level Override Tabs (for custom/cantrip or any spell) */}

                {(baseLvl > 0 || upPat === 'cantrip' || upPat === 'custom' || configuredLevels.length > 0) && (

                  <div style={{ marginTop: '8px', padding: '8px', background: 'rgba(74,144,217,0.05)', borderRadius: '6px', border: '1px solid rgba(74,144,217,0.15)' }}>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', marginBottom: '6px' }}>

                      <span style={{ fontSize: '0.65rem', color: '#888' }}>{upPat === 'cantrip' ? 'Char Lv:' : 'Cast Lv:'}</span>

                      <button

                        style={{ ...S.btn, padding: '1px 8px', fontSize: '0.65rem', borderRadius: '10px', background: isBaseTab ? 'rgba(201,162,39,0.3)' : 'transparent', border: `1px solid ${isBaseTab ? '#c9a227' : 'rgba(255,255,255,0.08)'}`, color: isBaseTab ? '#ffd700' : '#888' }}

                        onClick={() => setEditLevelTab('base')}

                      >Base{baseLvl > 0 ? ` (${baseLvl})` : ''}</button>

                      {configuredLevels.map(lvl => (

                        <button key={lvl} style={{ ...S.btn, padding: '1px 8px', fontSize: '0.65rem', borderRadius: '10px', background: editLevelTab === lvl ? 'rgba(74,144,217,0.3)' : 'transparent', border: `1px solid ${editLevelTab === lvl ? '#4a90d9' : 'rgba(255,255,255,0.08)'}`, color: editLevelTab === lvl ? '#7db5ff' : '#aaa' }}

                          onClick={() => setEditLevelTab(lvl)}

                        >{upPat === 'cantrip' ? `Lv${lvl}` : `L${lvl}`}</button>

                      ))}

                      <select value="" onChange={e => { if (e.target.value) addLevel(parseInt(e.target.value)); }}

                        style={{ ...S.select, width: 'auto', marginBottom: 0, padding: '1px 4px', fontSize: '0.62rem', borderRadius: '10px', background: 'rgba(39,201,90,0.08)', border: '1px solid rgba(39,201,90,0.25)', color: '#27c95a' }}>

                        <option value="">+ Level</option>

                        {upPat === 'cantrip'

                          ? [5,11,17].filter(l => !configuredLevels.includes(l)).map(l => <option key={l} value={l}>Char Lv {l}</option>)

                          : Array.from({ length: 9 - (baseLvl || 1) }, (_, i) => (baseLvl || 1) + 1 + i).filter(l => !configuredLevels.includes(l)).map(l => <option key={l} value={l}>Level {l}</option>)

                        }

                      </select>

                      {!isBaseTab && <button style={{ ...S.btn, padding: '1px 4px', fontSize: '0.55rem', color: '#c92727', border: 'none' }} onClick={() => removeLevel(editLevelTab)}>🗑️</button>}

                    </div>

                    {!isBaseTab && (

                      <>

                        <div style={{ fontSize: '0.6rem', color: '#888', marginBottom: '4px' }}>

                          Override fields for {upPat === 'cantrip' ? `character level ${editLevelTab}` : `cast level ${editLevelTab}`}. ↩ reverts to base.

                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px' }}>

                          <SpellEditorFieldWrap isOverridden={isOverridden} clearOverride={clearOverride} auto field="dice" label="Dice">

                            <input style={{ ...S.input, fontSize: '0.75rem', borderColor: isOverridden('dice') ? '#4a90d9' : undefined }} value={getVal('dice') || ''} onChange={e => setField('dice', e.target.value)} placeholder="2d10..." />

                          </SpellEditorFieldWrap>

                          <SpellEditorFieldWrap isOverridden={isOverridden} clearOverride={clearOverride} auto field="effect" label="Effect">

                            <input style={{ ...S.input, fontSize: '0.75rem', borderColor: isOverridden('effect') ? '#4a90d9' : undefined }} value={getVal('effect') || ''} onChange={e => setField('effect', e.target.value)} />

                          </SpellEditorFieldWrap>

                          <SpellEditorFieldWrap isOverridden={isOverridden} clearOverride={clearOverride} auto field="maxTargets" label="Max Targets">

                            <NumberField
                              style={{ ...S.input, fontSize: '0.75rem', borderColor: isOverridden('maxTargets') ? '#4a90d9' : undefined }}
                              value={getVal('maxTargets') ?? 1}
                              onCommit={(value) => setField('maxTargets', value)}
                              emptyValue={1}
                              min={0}
                            />

                          </SpellEditorFieldWrap>

                          <SpellEditorFieldWrap isOverridden={isOverridden} clearOverride={clearOverride} auto field="dotDice" label="DoT Dice">

                            <input style={{ ...S.input, fontSize: '0.75rem', borderColor: isOverridden('dotDice') ? '#4a90d9' : undefined }} value={getVal('dotDice') || ''} onChange={e => setField('dotDice', e.target.value)} />

                          </SpellEditorFieldWrap>

                          <SpellEditorFieldWrap isOverridden={isOverridden} clearOverride={clearOverride} auto field="hotDice" label="HoT Dice">

                            <input style={{ ...S.input, fontSize: '0.75rem', borderColor: isOverridden('hotDice') ? '#4a90d9' : undefined }} value={getVal('hotDice') || ''} onChange={e => setField('hotDice', e.target.value)} />

                          </SpellEditorFieldWrap>

                          <SpellEditorFieldWrap isOverridden={isOverridden} clearOverride={clearOverride} auto field="bonusDice" label="Bonus Dice">

                            <input style={{ ...S.input, fontSize: '0.75rem', borderColor: isOverridden('bonusDice') ? '#4a90d9' : undefined }} value={getVal('bonusDice') || ''} onChange={e => setField('bonusDice', e.target.value)} />

                          </SpellEditorFieldWrap>

                        </div>

                      </>

                    )}

                  </div>

                )}

              </div>

            )}

            

            {/* ═══ OVER-TIME EFFECTS ═══ */}

            <Sec id="overtime" label="Over-Time Effects & Recurring Saves" icon="⏱️" color="#ff9800" />

            {editorSections.overtime && (

              <div style={{ paddingLeft: '12px', marginBottom: '6px' }}>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '6px' }}>

                  <SpellEditorFieldWrap isOverridden={isOverridden} clearOverride={clearOverride} auto field="dotDice" label="🔥 DoT Dice" lc="#ff6b6b">

                    <input style={{ ...S.input, borderColor: isOverridden('dotDice') ? '#4a90d9' : undefined }} value={getVal('dotDice') || ''} onChange={e => setField('dotDice', e.target.value)} placeholder="1d6, 2d4+3..." />

                  </SpellEditorFieldWrap>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.68rem', color: '#ff6b6b' }}><A/>DoT Type</label>

                    <select style={S.select} value={editingSpell.dotDamageType || 'fire'} onChange={e => setEditingSpell({ ...editingSpell, dotDamageType: e.target.value })}>

                      {Object.entries(DAMAGE_TYPES).filter(([k]) => k !== 'raidwide').map(([key, dt]) => (

                        <option key={key} value={key}>{dt.icon} {dt.name}</option>

                      ))}

                    </select>

                  </div>

                  <SpellEditorFieldWrap isOverridden={isOverridden} clearOverride={clearOverride} auto field="hotDice" label="➕ HoT Dice" lc="#27c95a">

                    <input style={{ ...S.input, borderColor: isOverridden('hotDice') ? '#4a90d9' : undefined }} value={getVal('hotDice') || ''} onChange={e => setField('hotDice', e.target.value)} placeholder="1d4+3..." />

                  </SpellEditorFieldWrap>

                  <SpellEditorFieldWrap isOverridden={isOverridden} clearOverride={clearOverride} auto field="hotTempHpAmount" label="🩵 Temp HP/tick" lc="#87ceeb">

                    <NumberField
                      style={{ ...S.input, borderColor: isOverridden('hotTempHpAmount') ? '#4a90d9' : undefined }}
                      value={getVal('hotTempHpAmount')}
                      onCommit={(value) => setField('hotTempHpAmount', value)}
                      format={formatZeroAsBlank}
                      emptyValue={0}
                      min={0}
                      placeholder="0"
                    />

                  </SpellEditorFieldWrap>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.68rem' }}><A/>Duration (rounds)</label>

                    <NumberField
                      style={S.input}
                      value={editingSpell.effectRounds}
                      onCommit={(value) => setEditingSpell({ ...editingSpell, effectRounds: value })}
                      format={formatZeroAsBlank}
                      emptyValue={0}
                      min={0}
                    />

                  </div>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.68rem' }}><A/>Tick Timing</label>

                    <select style={S.select} value={editingSpell.tickTiming || 'start_of_target_turn'} onChange={e => setEditingSpell({ ...editingSpell, tickTiming: e.target.value })}>

                      <option value="start_of_target_turn">Target's turn (start)</option>

                      <option value="end_of_target_turn">Target's turn (end)</option>

                      <option value="start_of_caster_turn">Caster's turn (start)</option>

                      <option value="end_of_caster_turn">Caster's turn (end)</option>

                    </select>

                  </div>

                </div>

                {/* Recurring Save */}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.68rem', color: '#c9a227' }}>🎲 <A/>Recurring Save</label>

                    <select style={S.select} value={editingSpell.saveType || ''} onChange={e => setEditingSpell({ ...editingSpell, saveType: e.target.value })}>

                      <option value="">None</option>

                      {['str','dex','con','int','wis','cha'].map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}

                    </select>

                  </div>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.68rem', color: '#c9a227' }}><A/>Save DC</label>

                    {(() => {

                      // Auto-DC: 8 + prof + spell-mod, recomputed on every render so the

                      // placeholder tracks level-ups/stat changes without storing a static value.

                      const spellStat = spellPanelSpellContext.spellStat;

                      const statMod = spellPanelSpellContext.statMod;

                      const profBonus = spellPanelSpellContext.proficiencyBonus;

                      const autoDC = spellPanelSpellContext.spellSaveDC;

                      return (

                        <NumberField
                          style={S.input}
                          value={editingSpell.saveDC}
                          onCommit={(value) => setEditingSpell({ ...editingSpell, saveDC: value })}
                          format={formatZeroAsBlank}
                          emptyValue={0}
                          min={0}
                          placeholder={`auto: ${autoDC}`}
                          title={`Leave empty to auto-calculate from caster: 8 + proficiency (${profBonus}) + ${spellStat.toUpperCase()} mod (${statMod >= 0 ? '+' : ''}${statMod})${spellPanelSpellContext.spellSaveDcItemBonus ? ` + item bonus (${spellPanelSpellContext.spellSaveDcItemBonus >= 0 ? '+' : ''}${spellPanelSpellContext.spellSaveDcItemBonus})` : ''} = ${autoDC}`}
                        />

                      );

                    })()}

                  </div>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.68rem', color: '#c9a227' }}><A/>Save Timing</label>

                    <select style={S.select} value={editingSpell.saveTiming || 'end_of_target_turn'} onChange={e => setEditingSpell({ ...editingSpell, saveTiming: e.target.value })}>

                      <option value="start_of_target_turn">Target's turn (start)</option>

                      <option value="end_of_target_turn">Target's turn (end)</option>

                      <option value="start_of_caster_turn">Caster's turn (start)</option>

                      <option value="end_of_caster_turn">Caster's turn (end)</option>

                    </select>

                  </div>

                </div>

                {editingSpell.saveType && (

                  <label style={{ cursor: 'pointer', fontSize: '0.7rem', marginTop: '4px', display: 'block' }}>

                    <input type="checkbox" checked={editingSpell.saveRemovesEffect !== false} onChange={e => setEditingSpell({ ...editingSpell, saveRemovesEffect: e.target.checked })} />

                    {' '}<A/>Successful save removes effect

                  </label>

                )}

              </div>

            )}

            

            {/* ═══ CONDITIONS ═══ */}

            <Sec id="conditions" label="Conditions Applied" icon="⚠️" color="#e74c3c" />

            {editorSections.conditions && (

              <div style={{ paddingLeft: '12px', marginBottom: '6px' }}>

                <div style={{ fontSize: '0.6rem', color: '#888', marginBottom: '6px', padding: '4px 8px', background: 'rgba(231,76,60,0.05)', borderRadius: '4px', borderLeft: '2px solid rgba(231,76,60,0.3)' }}>

                  <A/> <strong style={{ color: '#aaa' }}>Casting this spell will apply a status pill</strong> showing the condition name &amp; tooltip on the target. Mechanical effects (disadvantage, speed 0, etc.) are not auto-enforced — the DM resolves those.

                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>

                  {DND_CONDITIONS.map(cond => {

                    const active = (editingSpell.conditionsApplied || []).includes(cond.id);

                    return (

                      <button key={cond.id}

                        style={{ ...S.btn, padding: '2px 8px', fontSize: '0.65rem', borderRadius: '10px', background: active ? 'rgba(231,76,60,0.2)' : 'transparent', border: `1px solid ${active ? '#e74c3c' : 'rgba(255,255,255,0.1)'}`, color: active ? '#ff6b6b' : '#888' }}

                        onClick={() => {

                          const current = editingSpell.conditionsApplied || [];

                          setEditingSpell({ ...editingSpell, conditionsApplied: active ? current.filter(c => c !== cond.id) : [...current, cond.id] });

                        }}

                        title={cond.description}

                      >{cond.name}</button>

                    );

                  })}

                </div>

                {(editingSpell.conditionsApplied || []).length > 0 && (

                  <label style={{ cursor: 'pointer', fontSize: '0.7rem' }}>

                    <input type="checkbox" checked={editingSpell.conditionSaveEnds || false} onChange={e => setEditingSpell({ ...editingSpell, conditionSaveEnds: e.target.checked })} />

                    {' '}<A/>Target can save each turn to end condition

                  </label>

                )}

              </div>

            )}

            

            {/* ═══ DESCRIPTION & META ═══ */}

            <Sec id="description" label="Description, Tags & Source" icon="📝" color="#888" />

            {editorSections.description && (

              <div style={{ paddingLeft: '12px', marginBottom: '6px' }}>

                <div style={{ marginBottom: '6px' }}>

                  <label style={{ ...S.label, fontSize: '0.68rem' }}>Description</label>

                  <textarea style={{ ...S.input, minHeight: '60px' }} value={editingSpell.description || ''} onChange={e => setEditingSpell({ ...editingSpell, description: e.target.value })} />

                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.68rem' }}>Tags</label>

                    <CommaSeparatedInput style={S.input} value={editingSpell.tags || []} onChange={v => setEditingSpell({ ...editingSpell, tags: v })} placeholder="Healing, Damage, Fire..." />

                  </div>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.68rem' }}>Source</label>

                    <input style={S.input} value={editingSpell.source || ''} onChange={e => setEditingSpell({ ...editingSpell, source: e.target.value })} placeholder="PHB p.250" />

                  </div>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.68rem' }}>Class</label>

                    <input style={S.input} value={editingSpell.spellClass || ''} onChange={e => setEditingSpell({ ...editingSpell, spellClass: e.target.value })} placeholder="Cleric, Wizard..." />

                  </div>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.68rem' }}>Learned at Char Level</label>

                    <input style={S.input} type="number" min="0" max="20" value={editingSpell.learnAtLevel || ''} onChange={e => setEditingSpell({ ...editingSpell, learnAtLevel: parseInt(e.target.value) || 0 })} />

                  </div>

                </div>

              </div>

            )}

            

            {/* ═══ ACTIONS ═══ */}

            <div style={{ display: 'flex', gap: '6px', marginTop: '12px', flexWrap: 'wrap', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '10px' }}>

              <button style={{ ...S.btn, ...S.btnSuccess, flex: 1, minWidth: '80px' }} onClick={() => {

                updateSpell(editingSpell.id, editingSpell);

                setEditingSpell(null);

              }}>💾 Save</button>

              

              {/* Duplicate */}

              <button style={{ ...S.btn, padding: '6px 10px', fontSize: '0.72rem' }} onClick={() => duplicateSpell(editingSpell)} title="Duplicate this spell">📋 Duplicate</button>

              

              {/* Save as Template */}

              {!showTemplateSave ? (

                <button style={{ ...S.btn, padding: '6px 10px', fontSize: '0.72rem', background: 'rgba(155,89,182,0.15)', border: '1px solid rgba(155,89,182,0.4)', color: '#bb8fce' }} onClick={() => setShowTemplateSave(true)} title="Save as reusable template">📐 Template</button>

              ) : (

                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>

                  <input style={{ ...S.input, marginBottom: 0, width: '110px', fontSize: '0.7rem' }} value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="Template name..." autoFocus />

                  <button style={{ ...S.btn, ...S.btnXs, ...S.btnSuccess }} onClick={() => {

                    if (templateName.trim()) { saveAsTemplate(editingSpell, templateName.trim()); setShowTemplateSave(false); setTemplateName(''); }

                  }}>✓</button>

                  <button style={{ ...S.btn, ...S.btnXs }} onClick={() => setShowTemplateSave(false)}>✕</button>

                </div>

              )}

              

              {/* Save to Library */}

              {onSaveLibrary && (

                <button style={{ ...S.btn, padding: '6px 10px', fontSize: '0.72rem', background: 'rgba(74,144,217,0.15)', border: '1px solid rgba(74,144,217,0.4)', color: '#7db5ff' }} onClick={() => {

                  const libSpell = { ...editingSpell, _libraryId: editingSpell._libraryId || generateId(), addedBy: char.id, addedByName: char.name, approved: isDM ? true : !(globalSettings?.requireSpellApproval !== false), submittedAt: Date.now() };

                  const existing = (spellLibrary || []).findIndex(s => s._libraryId === libSpell._libraryId);

                  if (existing >= 0) { const u = [...(spellLibrary || [])]; u[existing] = libSpell; onSaveLibrary(u); }

                  else { onSaveLibrary([...(spellLibrary || []), libSpell]); }

                  updateSpell(editingSpell.id, { ...editingSpell, _libraryId: libSpell._libraryId });

                  setEditingSpell(null);

                }}>📚 Library</button>

              )}

              

              <button style={{ ...S.btn, ...S.btnDanger, padding: '6px 10px' }} onClick={() => deleteSpell(editingSpell.id)}>🗑️</button>

              <button style={{ ...S.btn, padding: '6px 10px' }} onClick={() => setEditingSpell(null)}>Cancel</button>

            </div>

          </div>

        </div>

        );

      })()}

      

      {/* Spell Library Modal */}

      {showLibrary && (

        <div style={S.modal} onClick={() => setShowLibrary(false)}>

          <div style={{ ...S.modalContent, maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>

              <h3 style={{ color: '#7db5ff', margin: 0 }}>📚 Spell Library</h3>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>

                {isDM && onSaveGlobalSettings && (

                  <label style={{ fontSize: '0.72rem', color: '#888', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>

                    <input type="checkbox" checked={globalSettings?.requireSpellApproval !== false} onChange={(e) => {

                      onSaveGlobalSettings({ ...(globalSettings || {}), requireSpellApproval: e.target.checked });

                    }} />

                    Require approval

                  </label>

                )}

                <button style={{ ...S.btn, ...S.btnXs }} onClick={() => setShowLibrary(false)}>✕</button>

              </div>

            </div>

            

            {/* Pending Approval Section (DM only) */}

            {isDM && (spellLibrary || []).filter(s => !s.approved).length > 0 && (

              <div style={{ marginBottom: '16px', padding: '10px', background: 'rgba(201,162,39,0.08)', borderRadius: '6px', border: '1px solid rgba(201,162,39,0.2)' }}>

                <h4 style={{ color: '#c9a227', margin: '0 0 8px 0', fontSize: '0.85rem' }}>⏳ Pending Approval</h4>

                {(spellLibrary || []).filter(s => !s.approved).map(libSpell => (

                  <div key={libSpell._libraryId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', background: 'rgba(0,0,0,0.15)', borderRadius: '4px', marginBottom: '4px' }}>

                    <div>

                      <span style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{libSpell.name}</span>

                      <span style={{ color: '#888', fontSize: '0.7rem', marginLeft: '8px' }}>Lvl {libSpell.level} {libSpell.school}</span>

                      <span style={{ color: '#666', fontSize: '0.65rem', marginLeft: '8px' }}>by {libSpell.addedByName}</span>

                      {libSpell.spellClass && <span style={{ color: '#9b59b6', fontSize: '0.65rem', marginLeft: '6px' }}>({libSpell.spellClass})</span>}

                    </div>

                    <div style={{ display: 'flex', gap: '4px' }}>

                      <button style={{ ...S.btn, ...S.btnSuccess, ...S.btnXs }} onClick={() => {

                        const updated = (spellLibrary || []).map(s => s._libraryId === libSpell._libraryId ? { ...s, approved: true } : s);

                        onSaveLibrary(updated);

                      }}>✓ Approve</button>

                      <button style={{ ...S.btn, ...S.btnDanger, ...S.btnXs }} onClick={() => {

                        onSaveLibrary((spellLibrary || []).filter(s => s._libraryId !== libSpell._libraryId));

                      }}>✕ Reject</button>

                    </div>

                  </div>

                ))}

              </div>

            )}

            

            {/* Library Spells */}

            {(() => {

              const approved = (spellLibrary || []).filter(s => s.approved);

              const grouped = {};

              approved.forEach(s => {

                const lvl = s.level || 0;

                if (!grouped[lvl]) grouped[lvl] = [];

                grouped[lvl].push(s);

              });

              const mySpellIds = new Set(spells.map(s => s._libraryId).filter(Boolean));

              

              if (approved.length === 0) return (

                <div style={{ color: '#888', textAlign: 'center', padding: '20px', fontSize: '0.85rem' }}>

                  No spells in library yet. Save spells to library from the spell editor.

                </div>

              );

              

              return Object.keys(grouped).sort((a, b) => a - b).map(lvl => (

                <div key={lvl} style={{ marginBottom: '12px' }}>

                  <h4 style={{ color: '#c9a227', margin: '0 0 6px 0', fontSize: '0.8rem', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '4px' }}>

                    {lvl == 0 ? 'Cantrips' : `Level ${lvl}`} ({grouped[lvl].length})

                  </h4>

                  {grouped[lvl].map(libSpell => {

                    const alreadyKnown = mySpellIds.has(libSpell._libraryId);

                    return (

                      <div key={libSpell._libraryId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 8px', borderRadius: '4px', background: alreadyKnown ? 'rgba(39,201,90,0.06)' : 'rgba(0,0,0,0.1)', marginBottom: '3px' }}>

                        <div style={{ flex: 1 }}>

                          <span style={{ fontWeight: 'bold', fontSize: '0.82rem' }}>{libSpell.name}</span>

                          <span style={{ color: '#888', fontSize: '0.68rem', marginLeft: '8px' }}>{libSpell.school}</span>

                          {libSpell.spellClass && <span style={{ color: '#9b59b6', fontSize: '0.65rem', marginLeft: '6px' }}>{libSpell.spellClass}</span>}

                          {libSpell.learnAtLevel > 0 && <span style={{ color: '#c9a227', fontSize: '0.6rem', marginLeft: '6px' }}>@Lv{libSpell.learnAtLevel}</span>}

                          {libSpell.effect && <span style={{ color: '#666', fontSize: '0.65rem', marginLeft: '6px' }}>{libSpell.effect}</span>}

                          {Object.keys(libSpell.levelData || {}).length > 0 && (

                            <span style={{ color: '#4a90d9', fontSize: '0.6rem', marginLeft: '6px' }}>({Object.keys(libSpell.levelData).length} level{Object.keys(libSpell.levelData).length > 1 ? 's' : ''} configured)</span>

                          )}

                        </div>

                        <div style={{ display: 'flex', gap: '4px' }}>

                          {alreadyKnown ? (

                            <span style={{ color: '#27c95a', fontSize: '0.7rem' }}>✓ Known</span>

                          ) : (

                            <button style={{ ...S.btn, ...S.btnXs, background: 'rgba(39,201,90,0.15)', border: '1px solid rgba(39,201,90,0.4)', color: '#27c95a' }} onClick={() => {

                              const newSpell = createSpell({ ...libSpell, id: generateId(), _libraryId: libSpell._libraryId, prepared: false });

                              onUpdate({ spells: [...spells, newSpell] });

                            }}>+ Learn</button>

                          )}

                          {isDM && (

                            <button style={{ ...S.btn, ...S.btnXs, ...S.btnDanger }} onClick={() => {

                              if (window.confirm(`Remove "${libSpell.name}" from library?`)) {

                                onSaveLibrary((spellLibrary || []).filter(s => s._libraryId !== libSpell._libraryId));

                              }

                            }}>🗑️</button>

                          )}

                        </div>

                      </div>

                    );

                  })}

                </div>

              ));

            })()}

            

            {/* Templates Section */}

            {spellTemplates.length > 0 && (

              <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid rgba(155,89,182,0.2)' }}>

                <h4 style={{ color: '#bb8fce', margin: '0 0 8px 0', fontSize: '0.85rem' }}>📐 Spell Templates ({spellTemplates.length})</h4>

                {spellTemplates.map(tpl => (

                  <div key={tpl._templateId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px', marginBottom: '3px', borderRadius: '4px', background: 'rgba(155,89,182,0.06)' }}>

                    <span style={{ fontSize: '0.78rem' }}>

                      {tpl._templateName || tpl.name} <span style={{ color: '#888', fontSize: '0.65rem' }}>Lvl {tpl.level || 0} {tpl.school || ''}</span>

                    </span>

                    <div style={{ display: 'flex', gap: '4px' }}>

                      <button style={{ ...S.btn, ...S.btnXs, background: 'rgba(155,89,182,0.15)', border: '1px solid rgba(155,89,182,0.3)', color: '#bb8fce' }} onClick={() => { createFromTemplate(tpl); setShowLibrary(false); }}>+ Use</button>

                      <button style={{ ...S.btn, ...S.btnXs, ...S.btnDanger }} onClick={() => deleteTemplate(tpl._templateId)}>✕</button>

                    </div>

                  </div>

                ))}

              </div>

            )}

          </div>

        </div>

      )}

      

      {/* Settings modal (DM only) */}

      {showSettings && isDM && (

        <div style={S.modal} onClick={() => setShowSettings(false)}>

          <div style={{ ...S.modalContent, maxWidth: '500px' }} onClick={e => e.stopPropagation()}>

            <h3 style={{ color: '#ffd700', marginBottom: '16px' }}>⚙️ Spell System Settings</h3>

            

            <div style={{ marginBottom: '16px' }}>

              <label style={S.label}>Casting System</label>

              <select style={S.select} value={getCanonicalSpellSystem(settings)} onChange={e => onUpdate({ settings: syncSpellSettings(settings, { spellSystem: e.target.value }) })}>

                <option value="mana">Mana System (Custom)</option>

                <option value="slots">Traditional Spell Slots</option>

              </select>

              {sheetSettingsSpellResourceUi.usesSystemsAsPrimary && (

                <div style={{ fontSize: '0.78rem', color: '#888', marginTop: '4px' }}>

                  Systems-managed spell resources are primary on at least one spell surface. These legacy spell-system fields remain compatibility-only during migration.

                </div>

              )}

            </div>

            

            <div style={{ marginBottom: '16px' }}>

              <label style={S.label}>{sheetSettingsSpellResourceUi.usesSystemsAsPrimary ? 'Legacy Spell Slots (format: level:count, ...)' : 'Spell Slots (format: level:count, ...)'}</label>

              <input 

                style={S.input} 

                value={Object.entries(rawSpellSlots).map(([l, c]) => `${l}:${c}`).join(', ')}

                onChange={e => {

                  const newSlots = {};

                  e.target.value.split(',').forEach(pair => {

                    const [l, c] = pair.split(':').map(x => parseInt(x.trim()));

                    if (l && c) newSlots[l] = c;

                  });

                  onUpdate({ spellSlots: newSlots });

                }}

                placeholder="1:4, 2:3, 3:2"

              />

              <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '4px' }}>

                Max Mana: {calculateMaxMana(rawSpellSlots)} | Legacy Slot Max Spell Level: {getMaxSpellLevel(rawSpellSlots)}

              </div>

            </div>

            {(isManaSpellSystem(settings) || spellResourceUi.mode === 'system-scalar' || sheetSettingsSpellResourceUi.mode === 'system-scalar') && (

              <div style={{ marginBottom: '16px' }}>

                <label style={S.label}>Scalar Spell Max Cast Level</label>

                <input

                  style={{ ...S.input, width: '80px' }}

                  type="number"

                  min="0"

                  max="9"

                  value={scalarSpellMaxCastLevel}

                  onChange={e => onUpdate({

                    settings: syncSpellSettings(settings, {

                      scalarSpellProgression: {

                        maxCastLevel: Math.max(0, Math.min(9, parseInt(e.target.value, 10) || 0)),

                      },

                    }),

                  })}

                />

                <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '4px' }}>

                  Used for mana and systems-scalar spell pricing, free-cast eligibility, and overcast limits. Legacy slot counts are compatibility-only.

                </div>

              </div>

            )}

            

            <div style={{ marginBottom: '16px' }}>

              <label style={S.label}>Free Casts Per Day</label>

              <input 

                style={{ ...S.input, width: '80px' }} 

                type="number" 

                min="0" 

                value={settings.freeCastsPerDay || 4}

                onChange={e => onUpdate({ settings: { ...settings, freeCastsPerDay: parseInt(e.target.value) || 4 } })}

              />

            </div>

            

            <div style={{ marginBottom: '16px' }}>

              <label style={{ cursor: 'pointer', display: 'block', marginBottom: '8px' }}>

                <input type="checkbox" checked={settings.overcastEnabled !== false} onChange={e => onUpdate({ settings: { ...settings, overcastEnabled: e.target.checked } })} />

                {' '}Allow Overcasting (up to 2 levels above max)

              </label>

              <label style={{ cursor: 'pointer', display: 'block', marginBottom: '8px' }}>

                <input type="checkbox" checked={settings.manaSicknessEnabled !== false} onChange={e => onUpdate({ settings: { ...settings, manaSicknessEnabled: e.target.checked } })} />

                {' '}Mana Sickness on Overcast

              </label>

              <label style={{ cursor: 'pointer', display: 'block' }}>

                <input type="checkbox" checked={settings.manaDroughtEnabled !== false} onChange={e => onUpdate({ settings: { ...settings, manaDroughtEnabled: e.target.checked } })} />

                {' '}Mana Drought (exhaustion for negative mana)

              </label>

            </div>

            

            <div style={{ marginBottom: '16px' }}>

              <label style={S.label}>{sheetSettingsSpellResourceUi.usesSystemsAsPrimary ? 'Legacy Current Mana' : 'Current Mana'}</label>

              <input 

                style={{ ...S.input, width: '80px' }} 

                type="number" 

                value={rawCurrentMana}

                onChange={e => onUpdate({ currentMana: parseInt(e.target.value) || 0 })}

              />

            </div>

            

            <button style={{ ...S.btn, width: '100%' }} onClick={() => setShowSettings(false)}>Close</button>

          </div>

        </div>

      )}

      {activeSpellResourcePendingSave && (
        <RequireSaveModal
          key={`${activeSpellResourceSaveEntry?.id || 'spell-resource-save'}:${spellResourceSaveQueue.length}`}
          pendingSave={activeSpellResourcePendingSave}
          char={activeSpellResourceSaveChar}
          saveContext={activeSpellResourceSaveContext}
          saveAdvantage={activeSpellResourceSaveAdvantage}
          onResolve={handleSpellResourceSaveResolve}
          onSkip={handleSpellResourceSaveSkip}
        />
      )}

    </>

  );

};



// Inventory Panel Component - matches DM disease dropdown style

export const InventoryPanel = ({ char, onUpdate, isDM, allChars = null, onUpdateOtherChar = null, onTransferToOtherChar = null, onToggleInventoryItemEquipped = null, onConsumeInventoryItem = null, onAddInventoryItem = null, onUpdateInventoryItem = null, onRemoveInventoryItem = null, durabilityEnabled = true, manaEnabled = true }) => {

  const [showInventory, setShowInventory] = useState(false);

  const [editingItem, setEditingItem] = useState(null);
  const [legacyItemQuickAddDrafts, setLegacyItemQuickAddDrafts] = useState({});
  const [pendingEquipItemId, setPendingEquipItemId] = useState(null);
  const [pendingConsumeItemId, setPendingConsumeItemId] = useState(null);
  const [pendingCrudActionKey, setPendingCrudActionKey] = useState(null);

  const [newItemName, setNewItemName] = useState('');

  const [newItemCat, setNewItemCat] = useState('misc');

  const [newItemQty, setNewItemQty] = useState(1);

  const [categoryFilter, setCategoryFilter] = useState('all');

  

  // On-use effect builder state

  const [effectType, setEffectType] = useState('restore');

  const [effectValue, setEffectValue] = useState('');

  

  const inventory = char.inventory || [];

  const totalItems = inventory.reduce((sum, item) => sum + (item.quantity || 1), 0);

  

  // Filter items

  const filteredItems = categoryFilter === 'all' 

    ? inventory 

    : inventory.filter(i => i.category === categoryFilter);

  

  // Group for display

  const itemsByCategory = {};

  Object.keys(ITEM_CATEGORIES).forEach(cat => { itemsByCategory[cat] = []; });

  filteredItems.forEach(item => {

    const cat = item.category || 'misc';

    if (!itemsByCategory[cat]) itemsByCategory[cat] = [];

    itemsByCategory[cat].push(item);

  });

  

  const addItem = async () => {

    if (!newItemName.trim()) return;

    const item = createItem(newItemName.trim(), newItemCat);

    item.quantity = parseInt(newItemQty) || 1;

    if (onAddInventoryItem) {
      const actionKey = 'add';
      if (pendingCrudActionKey === actionKey) return false;
      setPendingCrudActionKey(actionKey);
      try {
        const result = await onAddInventoryItem({
          charId: char.id,
          item,
        });
        if (result !== null && result !== undefined) {
          setNewItemName('');
          setNewItemQty(1);
          return true;
        }
        console.error('[InventoryPanel] authoritative item add returned no result; local fallback blocked');
        return false;
      } catch (error) {
        console.error('[InventoryPanel] authoritative item add failed', error);
        return false;
      } finally {
        setPendingCrudActionKey((current) => (current === actionKey ? null : current));
      }
    }

    const result = applyCharInventoryItemAdd(char, { item });
    if (!result) return;

    onUpdate({ inventory: result.nextCharData.inventory }, { summary: `🎁 ${char.name} received: ${item.name} ×${item.quantity}`, category: 'inventory', action: 'inventory.add' });

    setNewItemName('');

    setNewItemQty(1);

  };

  

  const updateItem = async (itemId, updates) => {

    if (onUpdateInventoryItem) {
      const actionKey = `update:${itemId}`;
      if (pendingCrudActionKey === actionKey) return false;
      setPendingCrudActionKey(actionKey);
      try {
        const result = await onUpdateInventoryItem({
          charId: char.id,
          itemEntryId: itemId,
          updates,
        });
        if (result !== null && result !== undefined) return true;
        console.error('[InventoryPanel] authoritative item update returned no result; local fallback blocked');
        return false;
      } catch (error) {
        console.error('[InventoryPanel] authoritative item update failed', error);
        return false;
      } finally {
        setPendingCrudActionKey((current) => (current === actionKey ? null : current));
      }
    }

    const result = applyCharInventoryItemUpdate(char, { itemEntryId: itemId, updates });
    if (!result) return;

    onUpdate({ inventory: result.nextCharData.inventory });

  };

  

  const deleteItem = async (itemId) => {

    if (onRemoveInventoryItem) {
      const actionKey = `remove:${itemId}`;
      if (pendingCrudActionKey === actionKey) return false;
      setPendingCrudActionKey(actionKey);
      try {
        const result = await onRemoveInventoryItem({
          charId: char.id,
          itemEntryId: itemId,
        });
        if (result !== null && result !== undefined) {
          if (editingItem === itemId) setEditingItem(null);
          return true;
        }
        console.error('[InventoryPanel] authoritative item delete returned no result; local fallback blocked');
        return false;
      } catch (error) {
        console.error('[InventoryPanel] authoritative item delete failed', error);
        return false;
      } finally {
        setPendingCrudActionKey((current) => (current === actionKey ? null : current));
      }
    }

    const result = applyCharInventoryItemRemove(char, { itemEntryId: itemId });
    if (!result) return;

    onUpdate({ inventory: result.nextCharData.inventory });

    if (editingItem === itemId) setEditingItem(null);

  };



  // 2026-04-22 crafting ↔ inventory bridge. Moves an inventory item's entire

  // stack onto the crafting table (single mutation updates both slots).

  // Round-trip fidelity: the item's full record rides along in _invSnapshot

  // so if the player moves it back to inventory later, equipped state,

  // abilities, uses, etc. are preserved.

  const transferToCraftingTable = (item) => {

    if (!item) return;
    if (item.equipped) return;

    const nextCraftingTable = applyLegacyInventoryTransferToCraftingTable(char.craftingTable, item);

    if (!nextCraftingTable) return;

    onUpdate({

      inventory: inventory.filter(i => i.id !== item.id),

      craftingTable: nextCraftingTable,

    }, { summary: `🛠 Moved to crafting table: ${item.name}`, category: 'inventory', action: 'inventory.transfer-to-table' });

  };



  // Player-to-player transfer. Source side: decrement or remove. Target side: stack by itemId

  // (Tier A) if both have one, else by name if both lack one, else append as a new entry.

  // Abilities that have unique per-instance ids are re-cloned on the target side so two

  // characters don't silently share the same ability uses-tracker.

  const transferItem = async (invId, targetCharId, qty) => {

    if (!targetCharId || !allChars || !onUpdateOtherChar) return;

    const item = inventory.find(i => i.id === invId);

    const targetChar = allChars[targetCharId];

    if (!item || !targetChar || targetCharId === char.id) return;

    const maxQty = item.quantity || 1;

    const moveQty = Math.max(1, Math.min(qty || 1, maxQty));

    if (onTransferToOtherChar) {

      if (pendingTransferItemId === invId) return;

      setPendingTransferItemId(invId);

      try {

        const result = await onTransferToOtherChar({

          sourceCharId: char.id,

          targetCharId,

          itemEntryId: invId,

          quantity: moveQty,

        });

        if (result != null) {

          setTransferState(null);

          return;

        }

        console.error('[inventory.transfer.char] authoritative transfer returned no result; local fallback blocked');

        return;

      } catch (error) {

        console.error('[inventory.transfer.char] authoritative transfer failed', error);

        return;

      } finally {

        setPendingTransferItemId((current) => (current === invId ? null : current));

      }

    }

    // Source update

    const newSourceInventory = maxQty > moveQty

      ? inventory.map(i => i.id === invId ? { ...i, quantity: maxQty - moveQty } : i)

      : inventory.filter(i => i.id !== invId);

    onUpdate(

      { inventory: newSourceInventory },

      { summary: `📤 ${char.name} gave ${moveQty}× ${item.name} → ${targetChar.name}`, category: 'inventory', action: 'inventory.transfer.out' }

    );

    // Target update — keep the current transfer-specific itemId/name merge rule,
    // but route recipient-side ingress through one shared normalization helper.

    const newTargetInventory = applyLegacyInventoryTransferToTargetInventory(
      targetChar.inventory || [],
      item,
      {
        quantity: moveQty,
        sourceName: char.name,
      },
    );

    onUpdateOtherChar(

      targetCharId,

      { inventory: newTargetInventory },

      { summary: `📥 ${targetChar.name} received ${moveQty}× ${item.name} ← ${char.name}`, category: 'inventory', action: 'inventory.transfer.in' }

    );

    setTransferState(null);

  };



  const [transferState, setTransferState] = useState(null); // { invId, qty, targetCharId } | null
  const [pendingTransferItemId, setPendingTransferItemId] = useState(null);

  const updateLegacyItemQuickAddDraft = (itemId, field, value) => {
    setLegacyItemQuickAddDrafts(prev => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] || {}),
        [field]: value,
      },
    }));
  };

  const getLegacyItemQuickAddDraft = (itemId, field) => legacyItemQuickAddDrafts[itemId]?.[field] || '';

  const clearLegacyItemQuickAddDraft = (itemId, field) => {
    setLegacyItemQuickAddDrafts(prev => {
      const nextItemDrafts = { ...(prev[itemId] || {}) };
      delete nextItemDrafts[field];

      if (Object.keys(nextItemDrafts).length === 0) {
        const next = { ...prev };
        delete next[itemId];
        return next;
      }

      return {
        ...prev,
        [itemId]: nextItemDrafts,
      };
    });
  };

  const submitLegacyItemQuickAdd = (itemId, field, onSubmitDraft) => {
    const draft = getLegacyItemQuickAddDraft(itemId, field).trim();
    if (!draft) return;
    onSubmitDraft(itemId, draft);
    clearLegacyItemQuickAddDraft(itemId, field);
  };

  

  const addOnUseEffect = (itemId, effect) => {

    if (!effect.trim()) return;

    const item = inventory.find(i => i.id === itemId);

    if (!item) return;

    updateItem(itemId, { onUseEffects: [...(item.onUseEffects || []), effect.trim()] });

  };

  

  const removeOnUseEffect = (itemId, idx) => {

    const item = inventory.find(i => i.id === itemId);

    if (!item) return;

    updateItem(itemId, { onUseEffects: (item.onUseEffects || []).filter((_, i) => i !== idx) });

  };

  

  const addBuffToItem = (itemId, buff) => {

    if (!buff.trim()) return;

    const item = inventory.find(i => i.id === itemId);

    if (!item) return;

    updateItem(itemId, { buffs: [...(item.buffs || []), buff.trim()] });

  };

  

  const removeBuffFromItem = (itemId, idx) => {

    const item = inventory.find(i => i.id === itemId);

    if (!item) return;

    updateItem(itemId, { buffs: (item.buffs || []).filter((_, i) => i !== idx) });

  };

  

  const addDebuffToItem = (itemId, debuff) => {

    if (!debuff.trim()) return;

    const item = inventory.find(i => i.id === itemId);

    if (!item) return;

    updateItem(itemId, { debuffs: [...(item.debuffs || []), debuff.trim()] });

  };

  

  const removeDebuffFromItem = (itemId, idx) => {

    const item = inventory.find(i => i.id === itemId);

    if (!item) return;

    updateItem(itemId, { debuffs: (item.debuffs || []).filter((_, i) => i !== idx) });

  };

  

  const addAbilityToItem = (itemId, ability) => {

    if (!ability.trim()) return;

    const item = inventory.find(i => i.id === itemId);

    if (!item) return;

    updateItem(itemId, { abilities: [...(item.abilities || []), ability.trim()] });

  };

  

  const removeAbilityFromItem = (itemId, idx) => {

    const item = inventory.find(i => i.id === itemId);

    if (!item) return;

    updateItem(itemId, { abilities: (item.abilities || []).filter((_, i) => i !== idx) });

  };

  

  const consumeItem = async (item) => {
    if (!item) return;

    if (onConsumeInventoryItem) {
      if (pendingConsumeItemId === item.id) return;
      setPendingConsumeItemId(item.id);
      try {
        const result = await onConsumeInventoryItem({
          charId: char.id,
          itemEntryId: item.id,
          expectedQuantity: item.quantity || 1,
          expectedUsesCurrent: item.uses ? Number(item.uses.current ?? item.uses.cur ?? 0) : null,
        });
        if (result !== null && result !== undefined) return;
        console.error('[InventoryPanel] authoritative consume returned no result; local fallback blocked');
        return;
      } catch (error) {
        console.error('[InventoryPanel] authoritative consume failed', error);
        return;
      } finally {
        setPendingConsumeItemId((current) => (current === item.id ? null : current));
      }
    }

    const result = consumeLegacyInventoryItemRuntime({
      char,
      inventory,
      item,
      parseEffect: parseHealEffect,
      getEffectiveMaxHp,
      durabilityEnabled,
      manaEnabled,
    });
    if (!result) return;

    const { updates } = result;

    onUpdate(updates, { summary: `ðŸ§ª ${char.name} used: ${item.name}`, category: 'inventory', action: 'inventory.consume' });
    if (false) {
      /*



    // 2026-04-27: dice-aware via parseHealEffect — same change as V2.

    effects.forEach(effect => {

      const parsed = parseHealEffect(effect);

      if (parsed) {

        if (parsed.resource === 'hp') hpRestore += parsed.amount;

        else if (parsed.resource === 'mana') manaRestore += parsed.amount;

        else if (parsed.resource === 'dura') duraRestore += parsed.amount;

        return;

      }

      const lower = effect.toLowerCase();

      if (lower.includes('-')) tempDebuffs.push(effect);

      else if (lower.includes('+')) tempBuffs.push(effect);

    });



    const updates = {};

    if (hpRestore !== 0) updates.currentHp = Math.max(0, Math.min(effectiveMaxHp, (char.currentHp || 0) + hpRestore));

    if (manaRestore !== 0) updates.currentMana = Math.max(0, Math.min(char.maxMana || 10, (char.currentMana || 0) + manaRestore));

    if (duraRestore !== 0) {

      const maxDura = char.settings?.armorDurability?.maxDurability || 25;

      updates.armorDurability = Math.max(0, Math.min(maxDura, (char.armorDurability || maxDura) + duraRestore));

    }

    // 2026-04-29: write per-instance buffData with noteVisible:true so the

    // status-card tooltip shows "📝 {item.name}" to the player. Mirrors V2's

    // earlier fix; V1 (DM-side panel) was missed the first pass, which is

    // why elixirs consumed via the DM character view weren't surfacing

    // their source on hover.

    const consumedBuffs = [...tempBuffs];

    const consumedDebuffs = [...tempDebuffs];

    if (item.buffsApplyOnUse) {

      if (item.buffs?.length > 0) consumedBuffs.push(...item.buffs);

      if (item.debuffs?.length > 0) consumedDebuffs.push(...item.debuffs);

    }

    if (consumedBuffs.length > 0 || consumedDebuffs.length > 0) {

      const nextBuffList = [...(char.buffList || []), ...consumedBuffs];

      const nextDebuffList = [...(char.debuffList || []), ...consumedDebuffs];

      const nextBuffData = { ...(char.buffData || {}) };

      const nextBuffSources = { ...(char.buffSources || {}) };

      const permBuffs = char.permanentBuffs || [];

      const permDebuffs = char.permanentDebuffs || [];

      consumedBuffs.forEach(b => {

        const occ = (char.buffList || []).filter(x => x === b).length + permBuffs.filter(x => x === b).length;

        const key = occ === 0 ? b : `${b}::${occ}`;

        nextBuffData[key] = {

          source: item.name,

          sourceItemId: item.id,

          addedAt: new Date().toISOString(),

          addedBy: 'item-consume',

          noteVisible: true,

        };

        if (nextBuffSources[b] === undefined) nextBuffSources[b] = item.name;

      });

      consumedDebuffs.forEach(d => {

        const occ = (char.debuffList || []).filter(x => x === d).length + permDebuffs.filter(x => x === d).length;

        const key = occ === 0 ? d : `${d}::${occ}`;

        nextBuffData[key] = {

          source: item.name,

          sourceItemId: item.id,

          addedAt: new Date().toISOString(),

          addedBy: 'item-consume',

          noteVisible: true,

        };

        if (nextBuffSources[d] === undefined) nextBuffSources[d] = item.name;

      });

      if (consumedBuffs.length > 0) updates.buffList = nextBuffList;

      if (consumedDebuffs.length > 0) updates.debuffList = nextDebuffList;

      updates.buffData = nextBuffData;

      updates.buffSources = nextBuffSources;

    }



    // Handle quantity/uses

    // 2026-04-29: items configured with a rest reset (uses.resetType ===

    // 'shortRest' | 'longRest' | 'longShortRest' | etc.) should NOT be

    // destroyed when their charges hit 0 — they wait in inventory for the

    // next matching rest to restore. Only items with resetType 'none' (or

    // missing) are consumed-on-empty. Without this guard, e.g. a wand of

    // fireball with shortRest reset and quantity 1 was deleted instead of

    // sticking around at 0 charges.

    const isResettable = !!(item.uses?.resetType && item.uses.resetType !== 'none');

    if (item.uses && item.uses.current > 0) {

      const newUses = item.uses.current - 1;

      if (newUses <= 0 && item.quantity <= 1) {

        if (isResettable) {

          updates.inventory = inventory.map(i => i.id === item.id ? { ...i, uses: { ...i.uses, current: 0 } } : i);

        } else {

          updates.inventory = inventory.filter(i => i.id !== item.id);

        }

      }

      else if (newUses <= 0) updates.inventory = inventory.map(i => i.id === item.id ? { ...i, quantity: i.quantity - 1, uses: { ...i.uses, current: i.uses.max } } : i);

      else updates.inventory = inventory.map(i => i.id === item.id ? { ...i, uses: { ...i.uses, current: newUses } } : i);

    } else if (item.quantity > 1) {

      updates.inventory = inventory.map(i => i.id === item.id ? { ...i, quantity: i.quantity - 1 } : i);

    } else if (!isResettable) {

      updates.inventory = inventory.filter(i => i.id !== item.id);

    }

    onUpdate(updates, { summary: `🧪 ${char.name} used: ${item.name}`, category: 'inventory', action: 'inventory.consume' });

      */
    }
  };

  const toggleEquipped = async (itemId) => {
    const item = inventory.find(i => i.id === itemId);
    if (!item) return;

    if (onToggleInventoryItemEquipped) {
      if (pendingEquipItemId === itemId) return;
      setPendingEquipItemId(itemId);
      try {
        const result = await onToggleInventoryItemEquipped({
          charId: char.id,
          itemEntryId: itemId,
          expectedEquipped: !item.equipped,
        });
        if (result !== null && result !== undefined) return;
        console.error('[InventoryPanel] authoritative equip toggle returned no result; local fallback blocked');
        return;
      } catch (error) {
        console.error('[InventoryPanel] authoritative equip toggle failed', error);
        return;
      } finally {
        setPendingEquipItemId((current) => (current === itemId ? null : current));
      }
    }

    const result = toggleLegacyInventoryItemEquipRuntime({
      char,
      inventory,
      itemId,
    });
    if (!result) return;
    onUpdate(result.updates);

  };



  // Toggle restore on equip for an item

  const toggleRestoreOnEquip = (itemId) => {

    const item = inventory.find(i => i.id === itemId);

    if (!item) return;

    updateItem(itemId, { restoreRemovedOnEquip: item.restoreRemovedOnEquip === false ? true : false });

  };

  

  // Check if item has any removed buffs

  const hasRemovedBuffs = (itemId) => {

    const removedItemBuffs = char.removedItemBuffs || {};

    const disabledBuffs = char.disabledBuffs || [];

    const disabledDebuffs = char.disabledDebuffs || [];

    const item = inventory.find(i => i.id === itemId);

    if (!item) return false;



    const itemRemoved = removedItemBuffs[itemId] || [];

    const hasRemoved = itemRemoved.length > 0;

    // 2026-04-29: disabledBuffs entries are per-instance keys; the

    // inventory card answers "any instance of this buff name disabled?"

    // — collapse keys to names to compare.

    const disabledNames = new Set(disabledBuffs.map(k => parseBuffInstanceKey(k).name));

    const disabledDebuffNames = new Set(disabledDebuffs.map(k => parseBuffInstanceKey(k).name));

    const hasDisabled = (item.buffs || []).some(b => disabledNames.has(b)) ||

                        (item.debuffs || []).some(d => disabledDebuffNames.has(d));

    return hasRemoved || hasDisabled;

  };



  // Check if a specific buff is removed/disabled for display

  const isEffectRemovedOrDisabled = (effectText, itemId) => {

    const removedItemBuffs = char.removedItemBuffs || {};

    const disabledBuffs = char.disabledBuffs || [];

    const disabledDebuffs = char.disabledDebuffs || [];

    const itemRemoved = removedItemBuffs[itemId] || [];



    // 2026-04-29: name-level match against per-instance keys.

    return itemRemoved.includes(effectText) ||

           disabledBuffs.some(k => parseBuffInstanceKey(k).name === effectText) ||

           disabledDebuffs.some(k => parseBuffInstanceKey(k).name === effectText);

  };



  return (

    <HoverCard>

      <button

        style={{ ...S.btn, ...S.btnEdit, ...S.btnXs, width: '100%' }}

        onClick={() => setShowInventory(!showInventory)}

      >

        🎒 Inventory ({totalItems}) {showInventory ? '▼' : '▶'}

      </button>



      {showInventory && (

        <div style={{ marginTop: '8px', padding: '10px', background: 'rgba(0,0,0,0.3)', borderRadius: '6px', fontSize: '0.85rem' }}>

          {/* Category Filter */}

          <div style={{ marginBottom: '8px' }}>

            <label style={{ ...S.label, fontSize: '0.75rem' }}>Filter:</label>

            <select style={{ ...S.select, marginBottom: 0, padding: '4px', fontSize: '0.8rem' }} value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>

              <option value="all">All Items ({inventory.length})</option>

              {Object.entries(ITEM_CATEGORIES).map(([key, cat]) => {

                const count = inventory.filter(i => i.category === key).length;

                return count > 0 ? <option key={key} value={key}>{cat.icon} {cat.name} ({count})</option> : null;

              })}

            </select>

          </div>

          

          {/* Add Item */}

          <div style={S.flex}>

            <input style={{ ...S.input, flex: 2, marginBottom: 0, padding: '6px', fontSize: '0.8rem' }} 

              placeholder="Item name..." value={newItemName} onChange={e => setNewItemName(e.target.value)} 

              onKeyPress={e => e.key === 'Enter' && addItem()} />

            <select style={{ ...S.select, flex: 1.5, marginBottom: 0, padding: '6px', fontSize: '0.8rem' }} value={newItemCat} onChange={e => setNewItemCat(e.target.value)}>

              {Object.entries(ITEM_CATEGORIES).map(([key, cat]) => <option key={key} value={key}>{cat.icon} {cat.name}</option>)}

            </select>

            <input style={{ ...S.input, width: '40px', marginBottom: 0, padding: '6px', fontSize: '0.8rem' }} type="number" min="1" value={newItemQty} onChange={e => setNewItemQty(parseInt(e.target.value) || 1)} />

            <button
              disabled={pendingCrudActionKey === 'add'}
              style={{ ...S.btn, ...S.btnSuccess, ...S.btnXs, opacity: pendingCrudActionKey === 'add' ? 0.55 : 1 }}
              onClick={addItem}
            >+</button>

          </div>

          

          {/* Items by Category */}

          {Object.entries(ITEM_CATEGORIES).map(([catKey, cat]) => {

            const items = itemsByCategory[catKey] || [];

            if (items.length === 0) return null;

            

            return (

              <div key={catKey} style={{ marginTop: '10px' }}>

                <div style={{ fontSize: '0.75rem', color: cat.color, marginBottom: '4px', fontWeight: 'bold' }}>

                  {cat.icon} {cat.name}

                </div>

                {items.map(item => {

                  const isEditing = editingItem === item.id;

                  return (

                    <div key={item.id} style={{ 

                      marginBottom: '6px', padding: '8px', 

                      background: item.category === 'quest' ? 'rgba(155,89,182,0.15)' : 'rgba(0,0,0,0.2)', 

                      borderRadius: '4px', borderLeft: `3px solid ${cat.color}` 

                    }}>

                      {/* DISPLAY VIEW (default) */}

                      {!isEditing && (

                        <>

                          {/* Item Header Row */}

                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

                            <span style={{ flex: 1, fontWeight: 'bold', color: cat.color }}>

                              {item.name} {item.quantity > 1 && <span style={{ fontWeight: 'normal', opacity: 0.7 }}>x{item.quantity}</span>}

                              {item.equipped && <span style={{ color: '#27c95a', fontSize: '0.7rem' }}> ✓EQ</span>}

                              {item.uses && <span style={{ color: '#c9a227', fontSize: '0.7rem' }}> [{item.uses.current}/{item.uses.max}]</span>}

                              {item.questComplete && <span style={{ color: '#9b59b6', fontSize: '0.7rem' }}> ✓Done</span>}

                            </span>

                            {item.category === 'consumable' && (() => {

                              // 2026-04-27: hover tooltip parity with V2 — short summary of effects.

                              const effects = (item.onUseEffects || []).filter(Boolean);

                              const tipText = effects.length > 0 ? `Use: ${effects.join(', ')}` : 'Use this item';

                              return (

                                <button aria-label={`Use ${item.name}`} disabled={pendingConsumeItemId === item.id} style={{ ...S.btn, ...S.btnSuccess, ...S.btnXs, marginBottom: 0, padding: '2px 6px', fontSize: '0.7rem', opacity: pendingConsumeItemId === item.id ? 0.55 : 1 }} onClick={() => consumeItem(item)} title={tipText}>Use</button>

                              );

                            })()}

                            {(item.buffs?.length > 0 || item.debuffs?.length > 0 || item.category === 'combat') && (

                              <button aria-label={`${item.equipped ? 'Unequip' : 'Equip'} ${item.name}`} disabled={pendingEquipItemId === item.id} style={{ ...S.btn, ...S.btnXs, marginBottom: 0, padding: '2px 6px', fontSize: '0.7rem', background: item.equipped ? 'rgba(39,201,90,0.4)' : 'rgba(0,0,0,0.3)', opacity: pendingEquipItemId === item.id ? 0.55 : 1 }} onClick={() => toggleEquipped(item.id)}>

                                {item.equipped ? '⊖' : '⊕'}

                              </button>

                            )}

                            <button
                              aria-label={`Edit ${item.name}`}
                              style={{ ...S.btn, ...S.btnEdit, ...S.btnXs, marginBottom: 0, padding: '2px 6px', fontSize: '0.7rem' }}
                              onClick={() => setEditingItem(item.id)}
                            >✏️</button>

                          </div>

                          

                          {/* Restore on equip checkbox (only if item has removed buffs) */}

                          {hasRemovedBuffs(item.id) && !item.equipped && (

                            <label style={{ 

                              display: 'flex', 

                              alignItems: 'center', 

                              gap: '4px', 

                              fontSize: '0.65rem', 

                              color: '#c9a227',

                              cursor: 'pointer',

                              marginTop: '4px',

                              padding: '3px 6px',

                              background: 'rgba(201,162,39,0.1)',

                              borderRadius: '3px',

                              border: '1px solid rgba(201,162,39,0.3)',

                            }}>

                              <input 

                                type="checkbox" 

                                checked={item.restoreRemovedOnEquip !== false} 

                                onChange={() => toggleRestoreOnEquip(item.id)}

                                style={{ margin: 0 }}

                              />

                              Restore removed buffs on equip

                            </label>

                          )}

                          

                          {/* Buffs/Debuffs Display */}

                          {(item.buffs?.length > 0 || item.debuffs?.length > 0) && (

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '4px' }}>

                              {item.buffs?.map((b, i) => {

                                const isRemoved = isEffectRemovedOrDisabled(b, item.id);

                                return (

                                  <span 

                                    key={`b${i}`} 

                                    style={{ 

                                      ...S.badge, 

                                      ...S.badgeBuff, 

                                      padding: '1px 5px', 

                                      fontSize: '0.65rem',

                                      opacity: isRemoved ? 0.4 : 1,

                                      textDecoration: isRemoved ? 'line-through' : 'none',

                                    }}

                                    title={isRemoved ? 'Removed/Disabled - will restore on equip if checkbox is checked' : ''}

                                  >

                                    {b}{isRemoved && ' 🪦'}

                                  </span>

                                );

                              })}

                              {item.debuffs?.map((d, i) => {

                                const isRemoved = isEffectRemovedOrDisabled(d, item.id);

                                return (

                                  <span 

                                    key={`d${i}`} 

                                    style={{ 

                                      ...S.badge, 

                                      ...S.badgeDebuff, 

                                      padding: '1px 5px', 

                                      fontSize: '0.65rem',

                                      opacity: isRemoved ? 0.4 : 1,

                                      textDecoration: isRemoved ? 'line-through' : 'none',

                                    }}

                                    title={isRemoved ? 'Removed/Disabled - will restore on equip if checkbox is checked' : ''}

                                  >

                                    {d}{isRemoved && ' 🪦'}

                                  </span>

                                );

                              })}

                            </div>

                          )}

                          

                          {/* On-Use Effects (consumable) */}

                          {item.category === 'consumable' && item.onUseEffects?.length > 0 && (

                            <div style={{ fontSize: '0.7rem', color: '#27c95a', marginTop: '3px' }}>→ {item.onUseEffects.join(', ')}</div>

                          )}

                          

                          {/* Abilities (combat) */}

                          {item.category === 'combat' && item.abilities?.length > 0 && (

                            <div style={{ fontSize: '0.7rem', color: '#5a9fd4', marginTop: '3px' }}>
                              ⚔ {item.abilities.map(getLegacyInventoryAbilityName).filter(Boolean).join(' • ')}
                            </div>

                          )}

                          

                          {/* Quest Details */}

                          {item.category === 'quest' && item.questDetails && (

                            <div style={{ fontSize: '0.7rem', color: '#d4a5e8', marginTop: '3px', fontStyle: 'italic' }}>{item.questDetails}</div>

                          )}

                          

                          {/* Notes */}

                          {item.notes && <div style={{ fontSize: '0.65rem', opacity: 0.6, marginTop: '2px', fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>{item.notes}</div>}

                        </>

                      )}

                      

                      {/* EDIT VIEW */}

                      {isEditing && (

                        <div style={{ fontSize: '0.8rem' }}>

                          {/* Edit Header */}

                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>

                            <span style={{ fontWeight: 'bold', color: cat.color }}>{cat.icon} Editing: {item.name}</span>

                            <div style={{ display: 'flex', gap: '4px' }}>

                              <button style={{ ...S.btn, ...S.btnSuccess, ...S.btnXs, marginBottom: 0, padding: '2px 8px', fontSize: '0.7rem' }} onClick={() => setEditingItem(null)}>✓ Done</button>

                              {allChars && onUpdateOtherChar && Object.values(allChars).some(c => c.id !== char.id) && (

                                <button

                                  style={{ ...S.btn, ...S.btnXs, marginBottom: 0, padding: '2px 6px', fontSize: '0.7rem' }}

                                  onClick={() => setTransferState({ invId: item.id, qty: 1, targetCharId: '' })}

                                  title="Give this item to another player"

                                >

                                  📤 Give

                                </button>

                              )}

                              <button
                                aria-label={`Delete ${item.name}`}
                                disabled={pendingCrudActionKey === `remove:${item.id}`}
                                style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, marginBottom: 0, padding: '2px 6px', fontSize: '0.7rem', opacity: pendingCrudActionKey === `remove:${item.id}` ? 0.55 : 1 }}
                                onClick={() => deleteItem(item.id)}
                              >🗑️</button>

                            </div>

                          </div>

                          

                          {/* Basic Info */}

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px', marginBottom: '8px' }}>

                            <div>

                              <label style={{ ...S.label, fontSize: '0.65rem' }}>Name</label>

                              <DebouncedInput as="input" style={{ ...S.input, marginBottom: 0, padding: '4px', fontSize: '0.8rem' }} value={item.name} onCommit={v => updateItem(item.id, { name: v })} />

                            </div>

                            <div>

                              <label style={{ ...S.label, fontSize: '0.65rem' }}>Category</label>

                              <select style={{ ...S.select, marginBottom: 0, padding: '4px', fontSize: '0.8rem' }} value={item.category} onChange={e => updateItem(item.id, { category: e.target.value })}>

                                {Object.entries(ITEM_CATEGORIES).map(([key, c]) => <option key={key} value={key}>{c.icon} {c.name}</option>)}

                              </select>

                            </div>

                            <div>

                              <label style={{ ...S.label, fontSize: '0.65rem' }}>Qty</label>

                              <input style={{ ...S.input, marginBottom: 0, padding: '4px', fontSize: '0.8rem' }} type="number" min="1" value={item.quantity} onChange={e => updateItem(item.id, { quantity: parseInt(e.target.value) || 1 })} />

                            </div>

                          </div>

                          

                          {/* Uses (for combat/consumable) */}

                          {(item.category === 'combat' || item.category === 'consumable') && (

                            <div style={{ marginBottom: '8px' }}>

                              <label style={{ ...S.label, fontSize: '0.65rem' }}>Uses (empty = unlimited)</label>

                              <input style={{ ...S.input, marginBottom: 0, padding: '4px', fontSize: '0.8rem', width: '80px' }} type="number" min="0" placeholder="∞"

                                value={item.maxUses ?? ''} onChange={e => { const val = e.target.value ? parseInt(e.target.value) : null; updateItem(item.id, { maxUses: val, uses: val }); }} />

                            </div>

                          )}

                          

                          {/* Buffs */}

                          <div style={{ marginBottom: '8px' }}>

                            <label style={{ ...S.label, fontSize: '0.65rem' }}>Buffs (applied when equipped)</label>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginBottom: '4px' }}>

                              {(item.buffs || []).map((b, i) => (

                                <span key={i} style={{ ...S.badge, ...S.badgeBuff, padding: '2px 6px', fontSize: '0.7rem', cursor: 'pointer' }} onClick={() => removeBuffFromItem(item.id, i)}>{b} ✕</span>

                              ))}

                            </div>

                            <ControlledMiniForm
                              onSubmit={() => submitLegacyItemQuickAdd(item.id, 'buffs', addBuffToItem)}
                              submitLabel="Add"
                              submitDisabled={!getLegacyItemQuickAddDraft(item.id, 'buffs').trim()}
                              style={{ ...S.flex, alignItems: 'center' }}
                              actionsStyle={{ gap: '4px' }}
                              submitButtonStyle={{ ...S.btn, ...S.btnSuccess, ...S.btnXs }}
                            >
                              <input
                                aria-label={`${item.name} buff draft`}
                                style={{ ...S.input, flex: 1, marginBottom: 0, padding: '4px', fontSize: '0.8rem' }}
                                placeholder="+2 Str"
                                value={getLegacyItemQuickAddDraft(item.id, 'buffs')}
                                onChange={e => updateLegacyItemQuickAddDraft(item.id, 'buffs', e.target.value)}
                              />
                            </ControlledMiniForm>

                          </div>

                          

                          {/* Debuffs */}

                          <div style={{ marginBottom: '8px' }}>

                            <label style={{ ...S.label, fontSize: '0.65rem' }}>Debuffs</label>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginBottom: '4px' }}>

                              {(item.debuffs || []).map((d, i) => (

                                <span key={i} style={{ ...S.badge, ...S.badgeDebuff, padding: '2px 6px', fontSize: '0.7rem', cursor: 'pointer' }} onClick={() => removeDebuffFromItem(item.id, i)}>{d} ✕</span>

                              ))}

                            </div>

                            <ControlledMiniForm
                              onSubmit={() => submitLegacyItemQuickAdd(item.id, 'debuffs', addDebuffToItem)}
                              submitLabel="Add"
                              submitDisabled={!getLegacyItemQuickAddDraft(item.id, 'debuffs').trim()}
                              style={{ ...S.flex, alignItems: 'center' }}
                              actionsStyle={{ gap: '4px' }}
                              submitButtonStyle={{ ...S.btn, ...S.btnDanger, ...S.btnXs }}
                            >
                              <input
                                aria-label={`${item.name} debuff draft`}
                                style={{ ...S.input, flex: 1, marginBottom: 0, padding: '4px', fontSize: '0.8rem' }}
                                placeholder="-1 Dex"
                                value={getLegacyItemQuickAddDraft(item.id, 'debuffs')}
                                onChange={e => updateLegacyItemQuickAddDraft(item.id, 'debuffs', e.target.value)}
                              />
                            </ControlledMiniForm>

                            {/* Consumable: choose when buffs apply */}

                            {item.category === 'consumable' && (item.buffs?.length > 0 || item.debuffs?.length > 0) && (

                              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '6px', fontSize: '0.7rem', cursor: 'pointer', color: '#c9a227' }}>

                                <input type="checkbox" checked={item.buffsApplyOnUse || false} onChange={e => updateItem(item.id, { buffsApplyOnUse: e.target.checked })} />

                                Apply buffs/debuffs when consumed (otherwise when equipped)

                              </label>

                            )}

                          </div>

                          

                          {/* Consumable On-Use Effects */}

                          {item.category === 'consumable' && (

                            <div style={{ marginBottom: '8px', padding: '8px', background: 'rgba(39,201,90,0.1)', borderRadius: '4px', border: '1px solid rgba(39,201,90,0.3)' }}>

                              <label style={{ ...S.label, fontSize: '0.65rem', color: '#27c95a' }}>On Use Effects</label>

                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginBottom: '6px' }}>

                                {(item.onUseEffects || []).map((eff, i) => (

                                  <span key={i} style={{ ...S.badge, background: 'rgba(39,201,90,0.3)', border: '1px solid #27c95a', color: '#7dff7d', padding: '2px 6px', fontSize: '0.7rem', cursor: 'pointer' }} onClick={() => removeOnUseEffect(item.id, i)}>{eff} ✕</span>

                                ))}

                              </div>

                              <div style={{ fontSize: '0.65rem', opacity: 0.7, marginBottom: '4px' }}>

                                Examples: "+10 HP", "+5 Mana", "+3 Dura", "+2 Str"

                              </div>

                              <ControlledMiniForm
                                onSubmit={() => submitLegacyItemQuickAdd(item.id, 'onUseEffects', addOnUseEffect)}
                                submitLabel="Add"
                                submitDisabled={!getLegacyItemQuickAddDraft(item.id, 'onUseEffects').trim()}
                                style={{ ...S.flex, alignItems: 'center' }}
                                actionsStyle={{ gap: '4px' }}
                                submitButtonStyle={{ ...S.btn, ...S.btnSuccess, ...S.btnXs }}
                              >
                                <input
                                  aria-label={`${item.name} on use effect draft`}
                                  style={{ ...S.input, flex: 1, marginBottom: 0, padding: '4px', fontSize: '0.8rem' }}
                                  placeholder="+10 HP"
                                  value={getLegacyItemQuickAddDraft(item.id, 'onUseEffects')}
                                  onChange={e => updateLegacyItemQuickAddDraft(item.id, 'onUseEffects', e.target.value)}
                                />
                              </ControlledMiniForm>

                            </div>

                          )}

                          

                          {/* Combat Abilities */}

                          {item.category === 'combat' && (

                            <div style={{ marginBottom: '8px', padding: '8px', background: 'rgba(90,159,212,0.1)', borderRadius: '4px', border: '1px solid rgba(90,159,212,0.3)' }}>

                              <label style={{ ...S.label, fontSize: '0.65rem', color: '#5a9fd4' }}>Special Abilities</label>

                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginBottom: '4px' }}>

                                {(item.abilities || []).map((ab, i) => (

                                  <span key={i} style={{ ...S.badge, background: 'rgba(90,159,212,0.3)', border: '1px solid #5a9fd4', color: '#8ec5f0', padding: '2px 6px', fontSize: '0.7rem', cursor: 'pointer' }} onClick={() => removeAbilityFromItem(item.id, i)}>{getLegacyInventoryAbilityName(ab)} ✕</span>

                                ))}

                              </div>

                              <ControlledMiniForm
                                onSubmit={() => submitLegacyItemQuickAdd(item.id, 'abilities', addAbilityToItem)}
                                submitLabel="Add"
                                submitDisabled={!getLegacyItemQuickAddDraft(item.id, 'abilities').trim()}
                                style={{ ...S.flex, alignItems: 'center' }}
                                actionsStyle={{ gap: '4px' }}
                                submitButtonStyle={{ ...S.btn, ...S.btnEdit, ...S.btnXs }}
                              >
                                <input
                                  aria-label={`${item.name} ability draft`}
                                  style={{ ...S.input, flex: 1, marginBottom: 0, padding: '4px', fontSize: '0.8rem' }}
                                  placeholder="Flame Strike"
                                  value={getLegacyItemQuickAddDraft(item.id, 'abilities')}
                                  onChange={e => updateLegacyItemQuickAddDraft(item.id, 'abilities', e.target.value)}
                                />
                              </ControlledMiniForm>

                            </div>

                          )}

                          

                          {/* Quest Details */}

                          {item.category === 'quest' && (

                            <div style={{ marginBottom: '8px', padding: '8px', background: 'rgba(155,89,182,0.1)', borderRadius: '4px', border: '1px solid rgba(155,89,182,0.3)' }}>

                              <label style={{ ...S.label, fontSize: '0.65rem', color: '#9b59b6' }}>Quest Details</label>

                              <DebouncedInput as="textarea" style={{ ...S.input, marginBottom: 0, padding: '4px', fontSize: '0.8rem', minHeight: '50px', resize: 'vertical' }}

                                placeholder="Quest objectives, progress..." value={item.questDetails || ''} onCommit={v => updateItem(item.id, { questDetails: v })} />

                              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px', fontSize: '0.75rem', cursor: 'pointer' }}>

                                <input type="checkbox" checked={item.questComplete || false} onChange={e => updateItem(item.id, { questComplete: e.target.checked })} />

                                Quest Complete

                              </label>

                            </div>

                          )}

                          

                          {/* Notes */}

                          <div>

                            <label style={{ ...S.label, fontSize: '0.65rem' }}>Notes</label>

                            <DebouncedInput as="textarea" style={{ ...S.input, marginBottom: 0, padding: '4px', fontSize: '0.8rem', minHeight: '30px', resize: 'vertical' }}

                              placeholder="Notes..." value={item.notes || ''} onCommit={v => updateItem(item.id, { notes: v })} />

                          </div>

                        </div>

                      )}

                    </div>

                  );

                })}

              </div>

            );

          })}

          

          {inventory.length === 0 && (

            <div style={{ textAlign: 'center', padding: '12px', opacity: 0.5, fontSize: '0.8rem' }}>

              No items. Add items above.

            </div>

          )}

        </div>

      )}



      {/* Player-to-player transfer modal. Opens via the 📤 Give button in an item's edit view.

          Stacks on the recipient side by itemId when present, name when legacy (Tier A rule). */}

      {transferState && (() => {

        const transferItemEntry = inventory.find(i => i.id === transferState.invId);

        if (!transferItemEntry) return null;

        const otherPlayers = Object.values(allChars || {}).filter(c => c && c.id !== char.id);

        const maxQty = transferItemEntry.quantity || 1;

        return (

          <div style={S.modal} onClick={() => setTransferState(null)}>

            <div style={{ ...S.modalContent, width: '360px', maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>

              <h3 style={{ marginTop: 0 }}>📤 Give "{transferItemEntry.name}"</h3>

              {otherPlayers.length === 0 ? (

                <div style={{ padding: '8px 0', fontSize: '0.85rem', opacity: 0.7 }}>

                  No other characters to give this to.

                </div>

              ) : (

                <>

                  <label style={{ ...S.label, fontSize: '0.75rem' }}>Recipient</label>

                  <select

                    value={transferState.targetCharId}

                    onChange={e => setTransferState({ ...transferState, targetCharId: e.target.value })}

                    style={{ ...S.select, width: '100%', marginBottom: '10px' }}

                    autoFocus

                  >

                    <option value="">Select character…</option>

                    {otherPlayers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}

                  </select>

                  <label style={{ ...S.label, fontSize: '0.75rem' }}>Quantity (max {maxQty})</label>

                  <input

                    type="number"

                    min={1}

                    max={maxQty}

                    value={transferState.qty}

                    onChange={e => setTransferState({ ...transferState, qty: Math.max(1, Math.min(maxQty, parseInt(e.target.value) || 1)) })}

                    style={{ ...S.input, width: '100%', marginBottom: '10px' }}

                  />

                </>

              )}

              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>

                <button style={{ ...S.btn, ...S.btnSm }} onClick={() => setTransferState(null)}>Cancel</button>

                {otherPlayers.length > 0 && (

                  <button

                    style={{ ...S.btn, ...S.btnSuccess, ...S.btnSm, opacity: transferState.targetCharId ? 1 : 0.5 }}

                    disabled={!transferState.targetCharId || pendingTransferItemId === transferState.invId}

                    onClick={() => transferItem(transferState.invId, transferState.targetCharId, transferState.qty)}

                  >

                    {pendingTransferItemId === transferState.invId ? 'Giving…' : 'Give →'}

                  </button>

                )}

              </div>

            </div>

          </div>

        );

      })()}

    </HoverCard>

  );

};



// ============================================================================

// INVENTORY PANEL V2 - Quick Access Design

// ============================================================================

// Design Philosophy:

// - Quick Bar: Always-visible strip for favorited/frequently used items

// - Active Loadout: Shows equipped items and their effects at a glance

// - Tabbed Categories: Clean separation without overwhelming

// - Inline actions: Use/Equip without opening edit mode

// - Progressive disclosure: Details only when needed

// ============================================================================



export const InventoryPanelV2 = ({ char, onUpdate, isDM, partyInventory = null, onUpdateParty = null, onDepositToParty = null, onTakeFromParty = null, onTransferToCraftingTable = null, onAddInventoryItem = null, onUpdateInventoryItem = null, onRemoveInventoryItem = null, onSetInventoryItemFavorite = null, onToggleInventoryItemEquipped = null, onConsumeInventoryItem = null, onActivateInventoryItemAbility = null, durabilityEnabled = true, manaEnabled = true }) => {

  const [activeTab, setActiveTab] = useState('quick'); // quick, combat, consumable, quest, storage, party

  const [expandedItem, setExpandedItem] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);

  const [editingItem, setEditingItem] = useState(null); // Item being edited

  const [usesUnlocked, setUsesUnlocked] = useState(false); // Unlock editing uses

  const [showInventory, setShowInventory] = useState(true); // 2026-04-27: top-level collapse, parallels Equip/Spells

  const [customCombatBonusDraft, setCustomCombatBonusDraft] = useState('');
  const [pendingPartyDepositItemId, setPendingPartyDepositItemId] = useState(null);
  const [pendingPartyTakeItemId, setPendingPartyTakeItemId] = useState(null);
  const [pendingCraftingTableItemId, setPendingCraftingTableItemId] = useState(null);
  const [pendingCrudActionKey, setPendingCrudActionKey] = useState(null);
  const [pendingEquipItemId, setPendingEquipItemId] = useState(null);
  const [pendingConsumeItemId, setPendingConsumeItemId] = useState(null);
  const [pendingAbilityActivationKey, setPendingAbilityActivationKey] = useState(null);
  const [movementActionError, setMovementActionError] = useState(null);

  // 2026-05-11 — Session 7: shared partial-quantity sheet, replacing the
  // three window.prompt() sites for transferToCraftingTable / depositToParty
  // / takeFromParty. One state object, one sheet instance — each caller
  // sets the contextual title/action/onConfirm.
  const [pendingQtySheet, setPendingQtySheet] = useState({
    open: false,
    title: '',
    actionLabel: '',
    max: 1,
    onConfirm: null,
  });

  // 2026-04-27: Equipped / Stowed group collapse state — the ▼ chevrons next

  // to those labels were decorative only; users expected them to be clickable.

  const [equippedCollapsed, setEquippedCollapsed] = useState(false);

  const [stowedCollapsed, setStowedCollapsed] = useState(false);

  const [newItem, setNewItem] = useState({ name: '', category: 'misc', quantity: 1, maxUses: 1, resetType: 'none' });

  // 2026-04-27: receipt for the most recent consumeItem call (P5/P7).

  // { itemName, lines: [{resource, amount, hadDice, breakdown}], hpDelta, manaDelta, duraDelta }

  const [consumeReceipt, setConsumeReceipt] = useState(null);
  const [consumeFeedback, setConsumeFeedback] = useState(null);

  useEffect(() => {

    if (!consumeReceipt) return;

    const t = setTimeout(() => setConsumeReceipt(null), 6000);

    return () => clearTimeout(t);

  }, [consumeReceipt]);

  useEffect(() => {

    if (!consumeFeedback) return;

    const t = setTimeout(() => setConsumeFeedback(null), 2200);

    return () => clearTimeout(t);

  }, [consumeFeedback]);

  useEffect(() => {

    if (!movementActionError) return;

    const t = setTimeout(() => setMovementActionError(null), 6000);

    return () => clearTimeout(t);

  }, [movementActionError]);

  const reportMovementActionError = (error, fallbackMessage) => {
    setMovementActionError(getMovementAuthorityFailureMessage(error, fallbackMessage));
  };



  const inventory = char.inventory || [];

  

  // Categorize items

  const quickBar = inventory.filter(i => i.favorited);

  const equipped = inventory.filter(i => i.equipped);

  const combat = inventory.filter(i => i.category === 'combat');

  const consumables = inventory.filter(i => i.category === 'consumable');

  const quests = inventory.filter(i => i.category === 'quest');

  const storage = inventory.filter(i => i.category === 'misc' || i.category === 'component');

  

  const activeQuests = quests.filter(i => !i.questComplete);

  const totalItems = inventory.reduce((sum, i) => sum + (i.quantity || 1), 0);
  const consumeFeedbackHasVisibleItem = consumeFeedback
    ? inventory.some((item) => item.id === consumeFeedback.itemId)
    : false;

  

  // Helper functions

  const commitInventoryPatch = (result, auditHint = null) => {
    if (!result?.nextCharData || !Array.isArray(result.nextCharData.inventory)) return false;
    onUpdate({ inventory: result.nextCharData.inventory }, auditHint || undefined);
    return true;
  };

  const toggleFavorite = async (itemId) => {

    const item = inventory.find(i => i.id === itemId);
    if (!item) return false;
    const currentFavorited = item.favorited === true;
    const nextFavorited = !currentFavorited;

    if (onSetInventoryItemFavorite) {
      const actionKey = `favorite:${itemId}`;
      if (pendingCrudActionKey === actionKey) return false;
      setPendingCrudActionKey(actionKey);
      try {
        const result = await onSetInventoryItemFavorite({
          charId: char.id,
          itemEntryId: itemId,
          favorite: nextFavorited,
          expectedFavorited: currentFavorited,
        });
        if (result !== null && result !== undefined) return true;
        console.error('[InventoryPanelV2] authoritative favorite update returned no result; local fallback blocked');
        return false;
      } catch (e) {
        console.error('[InventoryPanelV2] authoritative favorite update failed', e);
        return false;
      } finally {
        setPendingCrudActionKey((current) => (current === actionKey ? null : current));
      }
    }

    const result = applyCharInventoryItemFavoriteToggle(char, { itemEntryId: itemId });
    if (!result) return false;

    return commitInventoryPatch(result, {
      summary: `${result.item?.favorited ? '⭐' : '☆'} ${char.name} ${result.item?.favorited ? 'favorited' : 'unfavorited'} item: ${result.item?.name || 'Item'}`,
      category: 'inventory',
      action: result.item?.favorited ? 'inventory.favorite' : 'inventory.unfavorite',
    });

  };

  

  const updateItem = async (itemId, updates) => {

    if (onUpdateInventoryItem) {
      const actionKey = `update:${itemId}`;
      if (pendingCrudActionKey === actionKey) return false;
      setPendingCrudActionKey(actionKey);
      try {
        const result = await onUpdateInventoryItem({
          charId: char.id,
          itemEntryId: itemId,
          updates,
        });
        if (result !== null && result !== undefined) return true;
        console.error('[InventoryPanelV2] authoritative item update returned no result; local fallback blocked');
        return false;
      } catch (e) {
        console.error('[InventoryPanelV2] authoritative item update failed', e);
        return false;
      } finally {
        setPendingCrudActionKey((current) => (current === actionKey ? null : current));
      }
    }

    const result = applyCharInventoryItemUpdate(char, { itemEntryId: itemId, updates });
    if (!result) return false;

    return commitInventoryPatch(result, {
      summary: `✏️ ${char.name} edited item: ${result.item?.name || result.previousItem?.name || 'Item'}`,
      category: 'inventory',
      action: 'inventory.update',
    });

  };

  

  const deleteItem = async (itemId) => {

    if (onRemoveInventoryItem) {
      const actionKey = `remove:${itemId}`;
      if (pendingCrudActionKey === actionKey) return false;
      setPendingCrudActionKey(actionKey);
      try {
        const result = await onRemoveInventoryItem({
          charId: char.id,
          itemEntryId: itemId,
        });
        if (result !== null && result !== undefined) {
          if (expandedItem === itemId) setExpandedItem(null);
          if (editingItem?.id === itemId) setEditingItem(null);
          return true;
        }
        console.error('[InventoryPanelV2] authoritative item delete returned no result; local fallback blocked');
        return false;
      } catch (e) {
        console.error('[InventoryPanelV2] authoritative item delete failed', e);
        return false;
      } finally {
        setPendingCrudActionKey((current) => (current === actionKey ? null : current));
      }
    }

    const result = applyCharInventoryItemRemove(char, { itemEntryId: itemId });
    if (!result) return false;
    commitInventoryPatch(result, { summary: `🗑️ ${char.name} removed item: ${result.removedItem?.name || 'Item'}`, category: 'inventory', action: 'inventory.remove' });

    if (expandedItem === itemId) setExpandedItem(null);

    if (editingItem?.id === itemId) setEditingItem(null);

    return true;

  };



  // 2026-04-22 crafting ↔ inventory bridge. Moves an inventory item's entire

  // stack to the character's crafting table in a single updateChar patch.

  // The item's full record rides along in _invSnapshot on the table entry so

  // if the player moves it back to inventory later, equipped state, abilities,

  // uses, etc. are preserved. (V2 variant of the same function also in

  // InventoryPanel for DM-dashboard access.)

  // 2026-04-27: optional qty arg for partial transfers. When omitted, behaves

  // as a full-stack move (preserves existing call sites).

  const transferToCraftingTable = async (item, qty = null) => {

    if (!item) return;

    const haveQty = item.quantity || 1;

    const moveQty = qty == null ? haveQty : Math.max(1, Math.min(haveQty, parseInt(qty) || 1));
    if (item.equipped && moveQty >= haveQty) return;

    if (onTransferToCraftingTable) {
      setPendingCraftingTableItemId(item.id);
      try {
        const result = await onTransferToCraftingTable({
          charId: char.id,
          itemEntryId: item.id,
          quantity: moveQty,
        });
        if (result !== null && result !== undefined) {
          if (expandedItem === item.id && moveQty >= haveQty) setExpandedItem(null);
          setMovementActionError(null);
          return;
        }
        console.error('[InventoryPanelV2] authoritative crafting-table transfer returned no result; local fallback blocked');
        reportMovementActionError(null, 'Transfer failed. The item stayed in inventory.');
        return;
      } catch (e) {
        console.error('[InventoryPanelV2] authoritative crafting-table transfer failed', e);
        reportMovementActionError(e, 'Transfer failed. The item stayed in inventory.');
        return;
      } finally {
        setPendingCraftingTableItemId(null);
      }
    }

    const nextCraftingTable = applyLegacyInventoryTransferToCraftingTable(char.craftingTable, item, { quantity: moveQty });

    if (!nextCraftingTable) return;

    const nextInventory = moveQty >= haveQty

      ? inventory.filter(i => i.id !== item.id)

      : inventory.map(i => i.id === item.id ? { ...i, quantity: haveQty - moveQty } : i);

    onUpdate({

      inventory: nextInventory,

      craftingTable: nextCraftingTable,

    }, { summary: `🛠 Moved to crafting table: ${item.name}${moveQty > 1 ? ` ×${moveQty}` : ''}`, category: 'inventory', action: 'inventory.transfer-to-table' });

    if (expandedItem === item.id && moveQty >= haveQty) setExpandedItem(null);

  };



  // 2026-04-23: party shared inventory transfers.

  // Deposit: remove from personal inventory, stack into the shared pool by

  // name (unequipped). Take: reverse direction — stack into personal

  // inventory, remove from pool. Both operations share the stackIntoInventory

  // helper for name-based merging, which matches player expectation

  // ("I'm putting 3 potions in" → stack with existing 2 = 5, not 2 separate).

  // 2026-04-27: optional qty arg for partial deposits.

  const depositToParty = async (item, qty = null) => {

    if (!item || (!onUpdateParty && !onDepositToParty)) return;

    const haveQty = item.quantity || 1;

    const moveQty = qty == null ? haveQty : Math.max(1, Math.min(haveQty, parseInt(qty) || 1));
    if (item.equipped && moveQty >= haveQty) return;

    if (onDepositToParty) {

      if (pendingPartyDepositItemId === item.id) return;

      setPendingPartyDepositItemId(item.id);

      try {

        const result = await onDepositToParty({

          charId: char.id,

          itemEntryId: item.id,

          quantity: moveQty,

        });

        if (result != null) {

          if (expandedItem === item.id && moveQty >= haveQty) setExpandedItem(null);

          setMovementActionError(null);

          return;

        }
        console.error('[party.deposit] authoritative deposit returned no result; local fallback blocked');
        reportMovementActionError(null, 'Deposit failed. The item stayed in inventory.');
        return;

      } catch (error) {

        console.error('[party.deposit] authoritative deposit failed', error);
        reportMovementActionError(error, 'Deposit failed. The item stayed in inventory.');
        return;

      } finally {

        setPendingPartyDepositItemId((current) => (current === item.id ? null : current));

      }

    }

    const currentPool = partyInventory?.items || [];

    const nextPool = applyLegacyInventoryTransferToPartyPool(currentPool, item, { quantity: moveQty });

    // 2026-04-28 diagnostic: user reports items deposited to party "disappear"

    // — inventory side removes the item but the pool doesn't show it. Logging

    // the before/after sizes lets us confirm whether the save fires with the

    // expected payload (rules out a stale-closure where currentPool is stale)

    // and whether the subscription round-trip is what's lagging.

    console.log('[party.deposit] inventory→pool', {

      itemName: item.name,

      itemId: item.id,

      moveQty,

      poolBefore: currentPool.length,

      poolAfter: nextPool.length,

      partyInventoryDefined: partyInventory !== undefined,

    });

    onUpdateParty({ ...(partyInventory || {}), items: nextPool });

    const nextInventory = moveQty >= haveQty

      ? inventory.filter(i => i.id !== item.id)

      : inventory.map(i => i.id === item.id ? { ...i, quantity: haveQty - moveQty } : i);

    onUpdate({ inventory: nextInventory },

      { summary: `📤 Deposited to party pool: ${item.name}${moveQty > 1 ? ` ×${moveQty}` : ''}`, category: 'inventory', action: 'party.deposit' });

    if (expandedItem === item.id && moveQty >= haveQty) setExpandedItem(null);

  };



  // 2026-04-27: optional qty arg for partial takes from the party pool.

  const takeFromParty = async (poolItem, qty = null) => {

    if (!poolItem || (!onUpdateParty && !onTakeFromParty)) return;

    const haveQty = poolItem.quantity || 1;

    const moveQty = qty == null ? haveQty : Math.max(1, Math.min(haveQty, parseInt(qty) || 1));

    if (onTakeFromParty) {

      if (pendingPartyTakeItemId === poolItem.id) return;

      setPendingPartyTakeItemId(poolItem.id);

      try {

        const result = await onTakeFromParty({

          charId: char.id,

          itemEntryId: poolItem.id,

          quantity: moveQty,

        });

        if (result != null) {

          setMovementActionError(null);

          return;

        }
        console.error('[party.take] authoritative take returned no result; local fallback blocked');
        reportMovementActionError(null, 'Take failed. The item stayed in the party pool.');
        return;

      } catch (error) {

        console.error('[party.take] authoritative take failed', error);
        reportMovementActionError(error, 'Take failed. The item stayed in the party pool.');
        return;

      } finally {

        setPendingPartyTakeItemId((current) => (current === poolItem.id ? null : current));

      }

    }

    const currentPool = partyInventory?.items || [];
    const nextState = applyPartyTakeToCharAndPool(char, { ...(partyInventory || {}), items: currentPool }, {

      itemEntryId: poolItem.id,

      quantity: moveQty,

    });

    if (!nextState) return;

    onUpdate({ inventory: nextState.nextCharData.inventory },

      { summary: `📥 Took from party pool: ${poolItem.name}${moveQty > 1 ? ` ×${moveQty}` : ''}`, category: 'inventory', action: 'party.take' });

    onUpdateParty(nextState.nextPartyInventory);

  };



  // 2026-04-27: prompt for quantity when item has more than 1; transfer immediately

  // for single items. Buttons use these prompt-wrappers so the user can split stacks.

  // 2026-05-11 — Session 7: prompt wrappers now route through
  // PartialQtySheet instead of window.prompt(). Single-quantity items
  // still skip the sheet entirely (immediate transfer) since there's
  // nothing to choose. Confirm action labels are scoped per caller
  // ("Transfer" / "Deposit" / "Take") so the button text reads
  // "Transfer 5" rather than generic "Confirm".

  const promptTransferToTable = (item) => {
    const haveQty = item.quantity || 1;
    if (haveQty <= 1) { transferToCraftingTable(item); return; }
    setPendingQtySheet({
      open: true,
      title: `Move "${item.name}" to crafting table`,
      actionLabel: 'Transfer',
      max: haveQty,
      onConfirm: (qty) => transferToCraftingTable(item, qty),
    });
  };

  const promptDepositToParty = (item) => {
    const haveQty = item.quantity || 1;
    if (haveQty <= 1) { depositToParty(item); return; }
    setPendingQtySheet({
      open: true,
      title: `Deposit "${item.name}" to party pool`,
      actionLabel: 'Deposit',
      max: haveQty,
      onConfirm: (qty) => depositToParty(item, qty),
    });
  };

  const promptTakeFromParty = (poolItem) => {
    const haveQty = poolItem.quantity || 1;
    if (haveQty <= 1) { takeFromParty(poolItem); return; }
    setPendingQtySheet({
      open: true,
      title: `Take "${poolItem.name}" from party pool`,
      actionLabel: 'Take',
      max: haveQty,
      onConfirm: (qty) => takeFromParty(poolItem, qty),
    });
  };



  const openEditModal = (item) => {

    setEditingItem({ ...item }); // Clone item for editing

    setUsesUnlocked(false);
    setCustomCombatBonusDraft('');

  };

  

  const saveEditedItem = async () => {

    if (!editingItem) return;

    const saved = await updateItem(editingItem.id, editingItem);
    if (!saved) return;

    setEditingItem(null);

    setUsesUnlocked(false);
    setCustomCombatBonusDraft('');

  };

  

  const updateEditingItem = (updates) => {

    setEditingItem(prev => ({ ...prev, ...updates }));

  };

  const submitCustomCombatBonusDraft = () => {

    const nextLabel = customCombatBonusDraft.trim();

    if (!nextLabel || !editingItem) return;

    updateEditingItem({
      combatBonuses: [
        ...(editingItem.combatBonuses || []),
        { type: 'custom', scope: 'custom', label: nextLabel },
      ],
    });
    setCustomCombatBonusDraft('');

  };

  

  // Ability management for editing item

  const addAbilityToEditingItem = () => {

    const newAbility = createAbility('New Ability', '', [], null, 'shortRest');

    updateEditingItem({ abilities: [...(editingItem.abilities || []), newAbility] });

  };

  

  const updateAbilityInEditingItem = (abilityId, updates) => {

    updateEditingItem({

      abilities: (editingItem.abilities || []).map(a => 

        a.id === abilityId ? { ...a, ...updates } : a

      )

    });

  };

  

  const removeAbilityFromEditingItem = (abilityId) => {

    updateEditingItem({

      abilities: (editingItem.abilities || []).filter(a => a.id !== abilityId)

    });

  };

  

  // Use ability (decrease uses + surface effect receipt).

  // 2026-04-27: previously this just decremented uses silently. Now it

  // rolls any dice-bearing effects (e.g., "+2d6 Fire" on Flame Burst)

  // and surfaces a roll receipt so the player sees what happened. The

  // app doesn't auto-apply damage to a target — that's DM judgment

  // (P14) — but the rolls are made transparent and named (P5/P7).

  const activateAbility = async (itemId, abilityId) => {
    const item = inventory.find(i => i.id === itemId);
    if (!item) return;

    const ability = item.abilities?.find(a => a.id === abilityId);
    if (!ability) return;

    const expectedUsesCurrent = ability.uses
      ? Number(ability.uses.current ?? ability.uses.cur ?? ability.uses.max ?? 0)
      : null;
    const expectedUsesMax = ability.uses
      ? Number(ability.uses.max ?? expectedUsesCurrent ?? 0)
      : null;
    if (ability.uses && Number.isFinite(expectedUsesCurrent) && expectedUsesCurrent <= 0) return;

    if (onActivateInventoryItemAbility) {
      const activationKey = `${itemId}:${abilityId}`;
      if (pendingAbilityActivationKey === activationKey) return;
      setPendingAbilityActivationKey(activationKey);
      try {
        const result = await onActivateInventoryItemAbility({
          charId: char.id,
          itemEntryId: itemId,
          itemDefinitionId: item.itemId || item.definitionId || null,
          abilityId,
          expectedUsesCurrent: Number.isFinite(expectedUsesCurrent) ? expectedUsesCurrent : null,
          expectedUsesMax: Number.isFinite(expectedUsesMax) ? expectedUsesMax : null,
        });
        if (result !== null && result !== undefined) {
          const receiptLines = Array.isArray(result.receiptLines) ? result.receiptLines : [];
          if (receiptLines.length > 0) {
            setConsumeReceipt({
              itemName: `${result.itemName || item.name} -> ${result.abilityName || ability.name}`,
              icon: item._raw?.icon || item.icon || '',
              lines: receiptLines,
              hpDelta: 0, manaDelta: 0, duraDelta: 0,
            });
          }
          return;
        }
        console.error('[InventoryPanelV2] authoritative item ability activation returned no result; local fallback blocked');
        return;
      } catch (error) {
        console.error('[InventoryPanelV2] authoritative item ability activation failed', error);
        return;
      } finally {
        setPendingAbilityActivationKey((current) => (current === activationKey ? null : current));
      }
    }

    const runtimeResult = activateLegacyInventoryAbilityRuntime({

      char,

      inventory,

      itemId,

      abilityId,

      resolveEffectLine: (text) => {
        const parsed = parseLegacyAbilityEffectLine(text, { random: Math.random });
        return parsed;

      },

    });

    if (runtimeResult) {

      const { ability, item, updates, receiptLines, statusBuffsToAdd } = runtimeResult;

      const rolledLine = receiptLines.filter((line) => line.hadDice).map((line) => line.breakdown).join('; ');

      const summary = `âš¡ ${char.name} used ability: ${item.name} â†’ ${ability.name}${rolledLine ? ` â€” ${rolledLine}` : ''}${statusBuffsToAdd.length ? ` (${statusBuffsToAdd.join(', ')})` : ''}`;

      onUpdate(updates, { summary, category: 'inventory', action: 'inventory.ability' });



      if (receiptLines.length > 0) {

        setConsumeReceipt({

          itemName: `${item.name} â†’ ${ability.name}`,

          icon: 'âš¡',

          lines: receiptLines,

          hpDelta: 0, manaDelta: 0, duraDelta: 0,

        });

      }

      return;

    }

    return;















  };


  const addItem = async () => {

    if (!newItem.name.trim()) return;

    const item = createItem(newItem.name.trim(), newItem.category);

    item.quantity = parseInt(newItem.quantity) || 1;

    // Add uses for consumables

    if (newItem.category === 'consumable' && newItem.maxUses > 0) {

      item.uses = createUsesTracker(parseInt(newItem.maxUses) || 1, newItem.resetType || 'none');

    }

    if (onAddInventoryItem) {
      const actionKey = 'add';
      if (pendingCrudActionKey === actionKey) return false;
      setPendingCrudActionKey(actionKey);
      try {
        const result = await onAddInventoryItem({
          charId: char.id,
          item,
        });
        if (result !== null && result !== undefined) {
          setNewItem({ name: '', category: 'misc', quantity: 1, maxUses: 1, resetType: 'none' });
          setShowAddModal(false);
          return true;
        }
        console.error('[InventoryPanelV2] authoritative item add returned no result; local fallback blocked');
        return false;
      } catch (e) {
        console.error('[InventoryPanelV2] authoritative item add failed', e);
        return false;
      } finally {
        setPendingCrudActionKey((current) => (current === actionKey ? null : current));
      }
    }

    const result = applyCharInventoryItemAdd(char, { item });
    if (!result) return false;

    commitInventoryPatch(result, {
      summary: `Added item: ${item.name}${item.quantity > 1 ? ` x${item.quantity}` : ''}`,
      category: 'inventory',
      action: 'inventory.add',
    });

    setNewItem({ name: '', category: 'misc', quantity: 1, maxUses: 1, resetType: 'none' });

    setShowAddModal(false);

    return true;

  };



  const buildConsumeFeedbackText = (item, receiptLines = [], deltas = {}) => {
    const summaryBits = [];
    const hpDelta = deltas.hpDelta || 0;
    const manaDelta = deltas.manaDelta || 0;
    const duraDelta = deltas.duraDelta || 0;

    if (hpDelta !== 0) summaryBits.push(`${hpDelta >= 0 ? '+' : ''}${hpDelta} HP`);
    if (manaDelta !== 0) summaryBits.push(`${manaDelta >= 0 ? '+' : ''}${manaDelta} Mana`);
    if (duraDelta !== 0) summaryBits.push(`${duraDelta >= 0 ? '+' : ''}${duraDelta} Dura`);
    if (summaryBits.length > 0) return summaryBits.join(', ');

    const statusEffects = [];
    if (item?.buffsApplyOnUse) {
      statusEffects.push(...(item.buffs || []), ...(item.debuffs || []));
    }
    (item?.onUseEffects || []).forEach((effect) => {
      const text = String(effect || '').trim();
      if (!text) return;
      const wasReceipted = receiptLines.some((line) => line.raw === text || line.breakdown === text);
      if (!wasReceipted && /[+-]/.test(text)) statusEffects.push(text);
    });

    return statusEffects.slice(0, 2).join(', ') || 'Used';
  };

  const consumeItem = async (item) => {
    if (!item) return;

    if (onConsumeInventoryItem) {
      if (pendingConsumeItemId === item.id) return;
      setPendingConsumeItemId(item.id);
      try {
        const result = await onConsumeInventoryItem({
          charId: char.id,
          itemEntryId: item.id,
          expectedQuantity: item.quantity || 1,
          expectedUsesCurrent: item.uses ? Number(item.uses.current ?? item.uses.cur ?? 0) : null,
        });
        if (result !== null && result !== undefined) {
          const receiptLines = Array.isArray(result.receiptLines) ? result.receiptLines : [];
          const deltas = result.deltas && typeof result.deltas === 'object' ? result.deltas : {};
          const hpDelta = deltas.hpDelta || 0;
          const manaDelta = deltas.manaDelta || 0;
          const duraDelta = deltas.duraDelta || 0;
          if (receiptLines.length > 0 || hpDelta !== 0 || manaDelta !== 0 || duraDelta !== 0) {
            setConsumeFeedback({
              itemId: item.id,
              itemName: result.itemName || item.name,
              text: buildConsumeFeedbackText(item, receiptLines, { hpDelta, manaDelta, duraDelta }),
            });
          }
          if (receiptLines.length > 0) {
            setConsumeReceipt({
              itemName: result.itemName || item.name,
              icon: item._raw?.icon || item.icon || '',
              lines: receiptLines,
              hpDelta,
              manaDelta,
              duraDelta,
            });
          }
          return;
        }
        console.error('[InventoryPanelV2] authoritative consume returned no result; local fallback blocked');
        return;
      } catch (error) {
        console.error('[InventoryPanelV2] authoritative consume failed', error);
        return;
      } finally {
        setPendingConsumeItemId((current) => (current === item.id ? null : current));
      }
    }

    const result = consumeLegacyInventoryItemRuntime({
      char,
      inventory,
      item,
      parseEffect: parseHealEffect,
      getEffectiveMaxHp,
      durabilityEnabled,
      manaEnabled,
    });
    if (!result) {
      setConsumeFeedback({ itemId: item.id, itemName: item.name, text: 'No uses' });
      return;
    }

    const { updates, receiptLines, deltas: { hpDelta, manaDelta, duraDelta } } = result;
    setConsumeFeedback({
      itemId: item.id,
      itemName: item.name,
      text: buildConsumeFeedbackText(item, receiptLines, { hpDelta, manaDelta, duraDelta }),
    });

    const summaryBits = [];

    if (hpDelta !== 0) summaryBits.push(`${hpDelta >= 0 ? '+' : ''}${hpDelta} HP`);
    if (manaDelta !== 0) summaryBits.push(`${manaDelta >= 0 ? '+' : ''}${manaDelta} Mana`);
    if (duraDelta !== 0) summaryBits.push(`${duraDelta >= 0 ? '+' : ''}${duraDelta} Dura`);

    const rolledLine = receiptLines.filter(l => l.hadDice).map(l => l.breakdown).join('; ');
    const summary = `ðŸ§ª ${char.name} used: ${item.name}${summaryBits.length ? ` (${summaryBits.join(', ')})` : ''}${rolledLine ? ` â€” ${rolledLine}` : ''}`;

    onUpdate(updates, { summary, category: 'inventory', action: 'inventory.consume' });

    if (receiptLines.length > 0) {
      setConsumeReceipt({
        itemName: item.name,
        icon: item._raw?.icon || 'ðŸ§ª',
        lines: receiptLines,
        hpDelta,
        manaDelta,
        duraDelta,
      });
    }

    if (false) {



    effects.forEach(effect => {

      // 2026-04-27: dice-aware parse via parseHealEffect. Handles flat

      // ("+10 HP") and rolled ("+2d4+2 HP") effects uniformly. Anything

      // that isn't a recognized resource line falls through to the

      // buff/debuff tagging below — preserves prior behavior.

      const parsed = parseHealEffect(effect);

      if (parsed) {

        if (parsed.resource === 'hp') hpRestore += parsed.amount;

        else if (parsed.resource === 'mana') manaRestore += parsed.amount;

        else if (parsed.resource === 'dura') duraRestore += parsed.amount;

        receiptLines.push(parsed);

        return;

      }

      const lower = effect.toLowerCase();

      if (lower.includes('-')) tempDebuffs.push(effect);

      else if (lower.includes('+')) tempBuffs.push(effect);

    });



    const updates = {};

    let hpDelta = 0, manaDelta = 0, duraDelta = 0;

    if (hpRestore !== 0) {

      const newHp = Math.max(0, Math.min(effectiveMaxHp, (char.currentHp || 0) + hpRestore));

      hpDelta = newHp - (char.currentHp || 0);

      updates.currentHp = newHp;

    }

    if (manaRestore !== 0) {

      const newMana = Math.max(0, Math.min(char.maxMana || 10, (char.currentMana || 0) + manaRestore));

      manaDelta = newMana - (char.currentMana || 0);

      updates.currentMana = newMana;

    }

    if (duraRestore !== 0) {

      const maxDura = char.settings?.armorDurability?.maxDurability || 25;

      const newDura = Math.max(0, Math.min(maxDura, (char.armorDurability || maxDura) + duraRestore));

      duraDelta = newDura - (char.armorDurability || maxDura);

      updates.armorDurability = newDura;

    }

    // 2026-04-27: also write per-instance buffData so the buff tooltip on the

    // status card shows "from {item.name}" to the player. Without buffData the

    // source comes only from char.buffSources, which BuffBadge hides from

    // players unless noteVisible is explicitly set. Consumable-applied buffs

    // come from items the player chose to use, so noteVisible:true is correct.

    const consumedBuffs = [...tempBuffs];

    const consumedDebuffs = [...tempDebuffs];

    if (item.buffsApplyOnUse) {

      if (item.buffs?.length > 0) consumedBuffs.push(...item.buffs);

      if (item.debuffs?.length > 0) consumedDebuffs.push(...item.debuffs);

    }

    if (consumedBuffs.length > 0 || consumedDebuffs.length > 0) {

      const nextBuffList = [...(char.buffList || []), ...consumedBuffs];

      const nextDebuffList = [...(char.debuffList || []), ...consumedDebuffs];

      const nextBuffData = { ...(char.buffData || {}) };

      const nextBuffSources = { ...(char.buffSources || {}) };

      const permBuffs = char.permanentBuffs || [];

      const permDebuffs = char.permanentDebuffs || [];

      consumedBuffs.forEach(b => {

        const occ = (char.buffList || []).filter(x => x === b).length + permBuffs.filter(x => x === b).length;

        const key = occ === 0 ? b : `${b}::${occ}`;

        nextBuffData[key] = {

          source: item.name,

          sourceItemId: item.id,

          addedAt: new Date().toISOString(),

          addedBy: 'item-consume',

          noteVisible: true,

        };

        if (nextBuffSources[b] === undefined) nextBuffSources[b] = item.name;

      });

      consumedDebuffs.forEach(d => {

        const occ = (char.debuffList || []).filter(x => x === d).length + permDebuffs.filter(x => x === d).length;

        const key = occ === 0 ? d : `${d}::${occ}`;

        nextBuffData[key] = {

          source: item.name,

          sourceItemId: item.id,

          addedAt: new Date().toISOString(),

          addedBy: 'item-consume',

          noteVisible: true,

        };

        if (nextBuffSources[d] === undefined) nextBuffSources[d] = item.name;

      });

      if (consumedBuffs.length > 0) updates.buffList = nextBuffList;

      if (consumedDebuffs.length > 0) updates.debuffList = nextDebuffList;

      updates.buffData = nextBuffData;

      updates.buffSources = nextBuffSources;

    }



    // Handle quantity/uses

    // 2026-04-29: items configured with a rest reset (uses.resetType ===

    // 'shortRest' | 'longRest' | 'longShortRest' | etc.) should NOT be

    // destroyed when their charges hit 0 — they wait in inventory for the

    // next matching rest to restore. Only items with resetType 'none' (or

    // missing) are consumed-on-empty. Without this guard, e.g. a wand of

    // fireball with shortRest reset and quantity 1 was deleted instead of

    // sticking around at 0 charges.

    const isResettable = !!(item.uses?.resetType && item.uses.resetType !== 'none');

    if (item.uses && item.uses.current > 0) {

      const newUses = item.uses.current - 1;

      if (newUses <= 0 && item.quantity <= 1) {

        if (isResettable) {

          updates.inventory = inventory.map(i => i.id === item.id ? { ...i, uses: { ...i.uses, current: 0 } } : i);

        } else {

          updates.inventory = inventory.filter(i => i.id !== item.id);

        }

      }

      else if (newUses <= 0) updates.inventory = inventory.map(i => i.id === item.id ? { ...i, quantity: i.quantity - 1, uses: { ...i.uses, current: i.uses.max } } : i);

      else updates.inventory = inventory.map(i => i.id === item.id ? { ...i, uses: { ...i.uses, current: newUses } } : i);

    } else if (item.quantity > 1) {

      updates.inventory = inventory.map(i => i.id === item.id ? { ...i, quantity: i.quantity - 1 } : i);

    } else if (!isResettable) {

      updates.inventory = inventory.filter(i => i.id !== item.id);

    }

    // Build a player-visible audit summary that names the rolls when dice were involved.

    const summaryBits = [];

    if (hpDelta !== 0) summaryBits.push(`${hpDelta >= 0 ? '+' : ''}${hpDelta} HP`);

    if (manaDelta !== 0) summaryBits.push(`${manaDelta >= 0 ? '+' : ''}${manaDelta} Mana`);

    if (duraDelta !== 0) summaryBits.push(`${duraDelta >= 0 ? '+' : ''}${duraDelta} Dura`);

    const rolledLine = receiptLines.filter(l => l.hadDice).map(l => l.breakdown).join('; ');

    const summary = `🧪 ${char.name} used: ${item.name}${summaryBits.length ? ` (${summaryBits.join(', ')})` : ''}${rolledLine ? ` — ${rolledLine}` : ''}`;

    onUpdate(updates, { summary, category: 'inventory', action: 'inventory.consume' });



    if (receiptLines.length > 0) {

      setConsumeReceipt({

        itemName: item.name,

        icon: item._raw?.icon || '🧪',

        lines: receiptLines,

        hpDelta, manaDelta, duraDelta,

      });

    }

    }
  };

  const toggleEquipped = async (itemId) => {
    const item = inventory.find(i => i.id === itemId);
    if (!item) return;

    if (onToggleInventoryItemEquipped) {
      if (pendingEquipItemId === itemId) return;
      setPendingEquipItemId(itemId);
      try {
        const result = await onToggleInventoryItemEquipped({
          charId: char.id,
          itemEntryId: itemId,
          expectedEquipped: !item.equipped,
        });
        if (result !== null && result !== undefined) return;
        console.error('[InventoryPanelV2] authoritative equip toggle returned no result; local fallback blocked');
        return;
      } catch (error) {
        console.error('[InventoryPanelV2] authoritative equip toggle failed', error);
        return;
      } finally {
        setPendingEquipItemId((current) => (current === itemId ? null : current));
      }
    }

    const result = toggleLegacyInventoryItemEquipRuntime({
      char,
      inventory,
      itemId,
    });
    if (!result) return;

    onUpdate(result.updates, {
      summary: `🧰 ${char.name} ${result.newEquipped ? 'equipped' : 'unequipped'}: ${result.item.name}`,
      category: 'inventory',
      action: 'inventory.equipToggle',
    });

  };

  

  // Toggle restore on equip for an item

  const toggleRestoreOnEquip = (itemId) => {

    const item = inventory.find(i => i.id === itemId);

    if (!item) return;

    onUpdate({ inventory: inventory.map(i => i.id === itemId ? { ...i, restoreRemovedOnEquip: item.restoreRemovedOnEquip === false ? true : false } : i) });

  };

  

  // Check if item has any removed buffs

  const hasRemovedBuffs = (itemId) => {

    const removedItemBuffs = char.removedItemBuffs || {};

    const disabledBuffs = char.disabledBuffs || [];

    const disabledDebuffs = char.disabledDebuffs || [];

    const item = inventory.find(i => i.id === itemId);

    if (!item) return false;



    const itemRemoved = removedItemBuffs[itemId] || [];

    const hasRemoved = itemRemoved.length > 0;

    // 2026-04-29: name-level match against per-instance disable keys.

    const disabledNames = new Set(disabledBuffs.map(k => parseBuffInstanceKey(k).name));

    const disabledDebuffNames = new Set(disabledDebuffs.map(k => parseBuffInstanceKey(k).name));

    const hasDisabled = (item.buffs || []).some(b => disabledNames.has(b)) ||

                        (item.debuffs || []).some(d => disabledDebuffNames.has(d));

    return hasRemoved || hasDisabled;

  };



  // Check if a specific buff is removed/disabled for display

  const isEffectRemovedOrDisabled = (effectText, itemId) => {

    const removedItemBuffs = char.removedItemBuffs || {};

    const disabledBuffs = char.disabledBuffs || [];

    const disabledDebuffs = char.disabledDebuffs || [];

    const itemRemoved = removedItemBuffs[itemId] || [];



    return itemRemoved.includes(effectText) ||

           disabledBuffs.some(k => parseBuffInstanceKey(k).name === effectText) ||

           disabledDebuffs.some(k => parseBuffInstanceKey(k).name === effectText);

  };



  // Styles

  const tabStyle = (isActive) => ({

    padding: '6px 12px',

    background: isActive ? 'rgba(201,162,39,0.3)' : 'transparent',

    border: 'none',

    borderBottom: isActive ? '2px solid #c9a227' : '2px solid transparent',

    color: isActive ? '#ffd700' : '#888',

    cursor: 'pointer',

    fontSize: '0.75rem',

    fontFamily: 'inherit',

    transition: 'all 0.2s ease',

  });

  

  const quickItemStyle = {

    display: 'flex',

    flexDirection: 'column',

    alignItems: 'center',

    padding: '8px',

    background: 'rgba(0,0,0,0.4)',

    borderRadius: '8px',

    minWidth: '60px',

    cursor: 'pointer',

    border: '1px solid rgba(201,162,39,0.3)',

    transition: 'all 0.2s ease',

  };



  // Render quick bar item

  const renderQuickItem = (item) => {

    const cat = ITEM_CATEGORIES[item.category] || ITEM_CATEGORIES.misc;
    const feedbackText = consumeFeedback?.itemId === item.id ? consumeFeedback.text : null;

    return (

      <div 

        key={item.id} 

        style={quickItemStyle}

        onClick={() => item.category === 'consumable' ? consumeItem(item) : toggleEquipped(item.id)}

        title={`${item.name}${item.category === 'consumable' ? ' (click to use)' : ' (click to equip/unequip)'}`}

      >

        <span style={{ fontSize: '1.2rem' }}>{cat.icon}</span>

        <span style={{ fontSize: '0.65rem', color: cat.color, textAlign: 'center', marginTop: '2px', maxWidth: '55px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>

          {item.name}

        </span>

        {item.quantity > 1 && <span style={{ fontSize: '0.6rem', opacity: 0.7 }}>x{item.quantity}</span>}

        {item.equipped && <span style={{ fontSize: '0.5rem', color: '#27c95a' }}>●</span>}

        {item.uses && <span style={{ fontSize: '0.5rem', color: '#c9a227' }}>{item.uses.current}/{item.uses.max}{item.quantity > 1 && <span style={{ opacity: 0.7 }}> x{item.quantity}</span>}</span>}
        {feedbackText && <span style={{ fontSize: '0.55rem', color: '#7dff7d', marginTop: '2px', fontWeight: 700 }}>{feedbackText}</span>}

      </div>

    );

  };



  // Render compact item row

  const renderItemRow = (item, showCategory = false) => {

    const cat = ITEM_CATEGORIES[item.category] || ITEM_CATEGORIES.misc;

    const isExpanded = expandedItem === item.id;
    const feedbackText = consumeFeedback?.itemId === item.id ? consumeFeedback.text : null;

    

    return (

      <div key={item.id} style={{ marginBottom: '4px' }}>

        {/* Compact Row */}

        <div style={{

          display: 'flex',

          alignItems: 'center',

          padding: '8px 10px',

          background: isExpanded ? 'rgba(201,162,39,0.15)' : 'rgba(0,0,0,0.2)',

          borderRadius: '6px',

          borderLeft: `3px solid ${cat.color}`,

          gap: '8px',

        }}>

          {/* Favorite Star */}

          <span 

            style={{ cursor: 'pointer', fontSize: '0.9rem', opacity: item.favorited ? 1 : 0.3 }}

            onClick={() => toggleFavorite(item.id)}

            title={item.favorited ? 'Remove from Quick Bar' : 'Add to Quick Bar'}

          >

            {item.favorited ? '★' : '☆'}

          </span>

          

          {/* Icon */}

          <span style={{ fontSize: '1rem' }}>{cat.icon}</span>

          

          {/* Name & Info */}

          <div style={{ flex: 1, minWidth: 0 }}>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

              <span style={{ fontWeight: 'bold', color: '#e8d5b7', fontSize: '0.85rem' }}>{item.name}</span>

              {item.quantity > 1 && <span style={{ fontSize: '0.7rem', opacity: 0.6 }}>×{item.quantity}</span>}

              {item.equipped && <span style={{ background: '#27c95a', color: '#000', padding: '1px 4px', borderRadius: '3px', fontSize: '0.55rem', fontWeight: 'bold' }}>EQ</span>}

              {item.questComplete && <span style={{ background: '#9b59b6', color: '#fff', padding: '1px 4px', borderRadius: '3px', fontSize: '0.55rem' }}>✓</span>}

            </div>

            {/* Mini effect preview */}

            {(item.buffs?.length > 0 || item.onUseEffects?.length > 0) && !isExpanded && (

              <div style={{ fontSize: '0.65rem', color: '#27c95a', opacity: 0.8, marginTop: '1px' }}>

                {item.buffs?.length > 0 && item.buffs.slice(0, 2).join(', ')}

                {item.buffs?.length > 2 && '...'}

                {item.onUseEffects?.length > 0 && `→ ${item.onUseEffects.slice(0, 2).join(', ')}`}

              </div>

            )}

          </div>

          

          {/* Quick Actions */}

          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>

            {item.uses && (

              <span style={{ fontSize: '0.65rem', color: '#c9a227', background: 'rgba(201,162,39,0.2)', padding: '2px 5px', borderRadius: '3px' }}>

                {item.uses.current}/{item.uses.max}{item.quantity > 1 && <span style={{ opacity: 0.7 }}> x{item.quantity}</span>}

              </span>

            )}

            {item.category === 'consumable' && (() => {

              // 2026-04-27: simple hover tooltip listing the on-use

              // effects (e.g. "+5 Mana"). Stays brief — full receipt

              // lands after the click.

              const effects = (item.onUseEffects || []).filter(Boolean);

              const tipText = effects.length > 0 ? `Use: ${effects.join(', ')}` : 'Use this item';

              return (

                <button

                  style={{
                    background: feedbackText ? '#7dff7d' : '#27c95a',
                    border: feedbackText ? '1px solid #d4ffd4' : 'none',
                    color: '#000',
                    padding: '3px 8px',
                    borderRadius: '4px',
                    fontSize: '0.7rem',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    boxShadow: feedbackText ? '0 0 0 2px rgba(125,255,125,0.18)' : 'none',
                  }}

                  onClick={(e) => { e.stopPropagation(); consumeItem(item); }}

                  title={tipText}

                >

                  {feedbackText ? 'USED' : 'USE'}

                </button>

              );

            })()}

            {feedbackText && (

              <span style={{ fontSize: '0.65rem', color: '#7dff7d', background: 'rgba(39,201,90,0.16)', border: '1px solid rgba(125,255,125,0.35)', padding: '2px 5px', borderRadius: '4px', fontWeight: 700 }}>

                {feedbackText}

              </span>

            )}

            {(item.category === 'combat' || (item.buffs?.length > 0 && !item.buffsApplyOnUse)) && (

              <button 

                style={{ 

                  background: item.equipped ? '#27c95a' : 'rgba(255,255,255,0.1)', 

                  border: item.equipped ? 'none' : '1px solid rgba(255,255,255,0.2)',

                  color: item.equipped ? '#000' : '#888', 

                  padding: '3px 8px', 

                  borderRadius: '4px', 

                  fontSize: '0.65rem', 

                  cursor: 'pointer' 

                }}

                onClick={(e) => { e.stopPropagation(); toggleEquipped(item.id); }}

              >

                {item.equipped ? 'UNEQUIP' : 'EQUIP'}

              </button>

            )}

            <button 

              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${item.name} details`}
              style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', padding: '2px 4px', fontSize: '0.8rem' }}

              onClick={() => setExpandedItem(isExpanded ? null : item.id)}

            >

              {isExpanded ? '▲' : '▼'}

            </button>

          </div>

        </div>

        

        {/* Expanded Details */}

        {isExpanded && (

          <div style={{ 

            padding: '12px', 

            background: 'rgba(0,0,0,0.3)', 

            borderRadius: '0 0 6px 6px',

            marginTop: '-4px',

            borderLeft: `3px solid ${cat.color}`,

          }}>

            {/* Effects */}

            {(item.buffs?.length > 0 || item.debuffs?.length > 0) && (

              <div style={{ marginBottom: '8px' }}>

                <div style={{ fontSize: '0.65rem', color: '#888', marginBottom: '4px' }}>EFFECTS {item.buffsApplyOnUse && item.category === 'consumable' ? '(on use)' : '(when equipped)'}</div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>

                  {item.buffs?.map((b, i) => {

                    const isRemoved = isEffectRemovedOrDisabled(b, item.id);

                    return (

                      <span 

                        key={i} 

                        style={{ 

                          ...S.badge, 

                          ...S.badgeBuff, 

                          fontSize: '0.7rem', 

                          padding: '2px 6px',

                          opacity: isRemoved ? 0.4 : 1,

                          textDecoration: isRemoved ? 'line-through' : 'none',

                        }}

                      >

                        {b}{isRemoved && ' 🪦'}

                      </span>

                    );

                  })}

                  {item.debuffs?.map((d, i) => {

                    const isRemoved = isEffectRemovedOrDisabled(d, item.id);

                    return (

                      <span 

                        key={i} 

                        style={{ 

                          ...S.badge, 

                          ...S.badgeDebuff, 

                          fontSize: '0.7rem', 

                          padding: '2px 6px',

                          opacity: isRemoved ? 0.4 : 1,

                          textDecoration: isRemoved ? 'line-through' : 'none',

                        }}

                      >

                        {d}{isRemoved && ' 🪦'}

                      </span>

                    );

                  })}

                </div>

              </div>

            )}

            

            {/* On-Use Effects */}

            {item.category === 'consumable' && item.onUseEffects?.length > 0 && (

              <div style={{ marginBottom: '8px' }}>

                <div style={{ fontSize: '0.65rem', color: '#888', marginBottom: '4px' }}>ON USE</div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>

                  {item.onUseEffects.map((e, i) => <span key={i} style={{ background: 'rgba(39,201,90,0.2)', border: '1px solid #27c95a', color: '#7dff7d', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7rem' }}>{e}</span>)}

                </div>

              </div>

            )}

            

            {/* Abilities - now with proper display and use buttons */}

            {item.abilities?.length > 0 && (

              <div style={{ marginBottom: '8px' }}>

                <div style={{ fontSize: '0.65rem', color: '#888', marginBottom: '4px' }}>ABILITIES</div>

                {item.abilities.map(ability => (

                  <div key={ability.id} style={{ 

                    background: 'rgba(90,159,212,0.1)', 

                    border: '1px solid rgba(90,159,212,0.3)',

                    borderRadius: '4px',

                    padding: '8px',

                    marginBottom: '6px',

                  }}>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                      <span style={{ fontWeight: 'bold', color: '#5a9fd4', fontSize: '0.85rem' }}>{ability.name}</span>

                      {ability.uses && (

                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

                          <span style={{ 

                            fontSize: '0.7rem', 

                            padding: '2px 6px', 

                            borderRadius: '3px',

                            background: ability.uses.current > 0 ? 'rgba(39,201,90,0.2)' : 'rgba(201,39,39,0.2)',

                            color: ability.uses.current > 0 ? '#27c95a' : '#c92727',

                          }}>

                            {ability.uses.current}/{ability.uses.max}

                            <span

                              style={{ opacity: 0.7, marginLeft: '4px', fontSize: '0.6rem' }}

                              title={USES_RESET_TYPES[ability.uses.resetType]?.description || "Uses don't refresh on rest."}

                            >

                              ({USES_RESET_TYPES[ability.uses.resetType]?.name || "Doesn't reset"})

                            </span>

                          </span>

                          <button 

                            style={{ 

                              ...S.btn, ...S.btnXs, 

                              padding: '2px 8px', 

                              fontSize: '0.65rem',

                              opacity: (ability.uses.current <= 0 || pendingAbilityActivationKey === `${item.id}:${ability.id}`) ? 0.5 : 1,

                            }}

                            disabled={ability.uses.current <= 0 || pendingAbilityActivationKey === `${item.id}:${ability.id}`}

                            onClick={() => activateAbility(item.id, ability.id)}

                          >

                            USE

                          </button>

                        </div>

                      )}

                    </div>

                    {ability.description && (

                      <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '4px' }}>{ability.description}</div>

                    )}

                    {/* 2026-04-27: defensive coercion. Legacy items in player

                        inventories may have ability.effects as a string (recipe-

                        side shape leaked through pre-bridge-fix). Rather than

                        crash, coerce here so the UI renders for any shape. */}

                    {(() => {

                      const effs = Array.isArray(ability.effects)

                        ? ability.effects

                        : (typeof ability.effects === 'string' && ability.effects.trim())

                          ? ability.effects.split(',').map(s => s.trim()).filter(Boolean)

                          : [];

                      if (effs.length === 0) return null;

                      return (

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '4px' }}>

                          {effs.map((eff, i) => (

                            <span key={i} style={{ background: 'rgba(90,159,212,0.2)', color: '#8ec5f0', padding: '1px 5px', borderRadius: '3px', fontSize: '0.65rem' }}>{eff}</span>

                          ))}

                        </div>

                      );

                    })()}

                  </div>

                ))}

              </div>

            )}

            

            {/* Combat Bonuses */}

            {Array.isArray(item.combatBonuses) && item.combatBonuses.length > 0 && (

              <div style={{ marginBottom: '8px' }}>

                <div style={{ fontSize: '0.65rem', color: '#888', marginBottom: '4px' }}>COMBAT BONUSES</div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>

                  {item.combatBonuses.map((bonus, bIdx) => (

                    <span key={bIdx} style={{ 

                      padding: '2px 7px', borderRadius: '10px', fontSize: '0.65rem',

                      background: bonus.type === 'advantage' ? 'rgba(39,201,90,0.15)' : bonus.type === 'disadvantage' ? 'rgba(231,76,60,0.15)' : 'rgba(201,162,39,0.15)',

                      color: bonus.type === 'advantage' ? '#6cff8a' : bonus.type === 'disadvantage' ? '#ff6b6b' : '#ffd700',

                    }}>

                      {bonus.label}

                    </span>

                  ))}

                </div>

              </div>

            )}



            {/* Quest Details */}

            {item.category === 'quest' && (

              <div style={{ marginBottom: '8px' }}>

                <div style={{ fontSize: '0.65rem', color: '#888', marginBottom: '4px' }}>QUEST DETAILS</div>

                <DebouncedInput as="textarea"

                  style={{ ...S.input, marginBottom: 0, padding: '6px', fontSize: '0.8rem', minHeight: '50px', resize: 'vertical', background: 'rgba(155,89,182,0.1)', border: '1px solid rgba(155,89,182,0.3)' }}

                  value={item.questDetails || ''}

                  onCommit={(v) => updateItem(item.id, { questDetails: v })}

                  placeholder="Quest objectives, notes..."

                />

                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '6px', fontSize: '0.75rem', cursor: 'pointer' }}>

                  <input type="checkbox" checked={item.questComplete || false} onChange={(e) => updateItem(item.id, { questComplete: e.target.checked })} />

                  Mark as Complete

                </label>

              </div>

            )}

            

            {/* Notes */}

            {item.notes && (

              <div style={{ marginBottom: '8px' }}>

                <div style={{ fontSize: '0.65rem', color: '#888', marginBottom: '4px' }}>NOTES</div>

                <div style={{ fontSize: '0.8rem', fontStyle: 'italic', opacity: 0.8, whiteSpace: 'pre-wrap' }}>{item.notes}</div>

              </div>

            )}



            {isDM && item.dmNotes && (

              <div style={{ marginTop: '6px', fontSize: '0.85rem', opacity: 0.9 }}>

                <div style={{ fontSize: '0.75rem', opacity: 0.75, color: '#c9a227' }}>DM Notes</div>

                <div style={{ marginTop: '2px', fontStyle: 'italic', opacity: 0.85, color: '#c9a227' }}>{item.dmNotes}</div>

              </div>

            )}



            {/* Actions */}

            <div style={{ display: 'flex', gap: '6px', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.1)', flexWrap: 'wrap' }}>

              <button style={{ ...S.btn, ...S.btnEdit, ...S.btnXs, fontSize: '0.7rem' }} onClick={() => openEditModal(item)}>✏️ Edit</button>

              {/* 2026-04-22: transfer to crafting table. 2026-04-27: stacks > 1

                  prompt for partial qty so users can split. Single items move

                  immediately. */}

              <button

                style={{ ...S.btn, ...S.btnXs, fontSize: '0.7rem', background: 'rgba(201,162,39,0.15)', border: '1px solid rgba(201,162,39,0.4)', color: '#c9a227', opacity: pendingCraftingTableItemId === item.id ? 0.7 : 1 }}

                disabled={pendingCraftingTableItemId === item.id}
                onClick={() => promptTransferToTable(item)}

                title={pendingCraftingTableItemId === item.id ? 'Moving to crafting table...' : (item.quantity > 1 ? `Move some or all (×${item.quantity}) to your crafting table` : 'Move this item to your crafting table')}

              >

                🛠 To Table

              </button>

              {/* 2026-04-23: deposit to the shared party pool. 2026-04-27: stacks > 1

                  prompt for partial qty. */}

              {(onUpdateParty || onDepositToParty) && (

                <button

                  style={{ ...S.btn, ...S.btnXs, fontSize: '0.7rem', background: 'rgba(58,107,154,0.15)', border: '1px solid rgba(58,107,154,0.4)', color: '#4a9ad4', opacity: pendingPartyDepositItemId === item.id ? 0.7 : 1 }}

                  disabled={pendingPartyDepositItemId === item.id}
                  onClick={() => promptDepositToParty(item)}

                  title={pendingPartyDepositItemId === item.id ? 'Depositing to the party pool...' : (item.quantity > 1 ? `Deposit some or all (×${item.quantity}) to the party pool` : 'Share this item with the party')}

                >

                  {pendingPartyDepositItemId === item.id ? '⏳ Depositing…' : '📤 To Party'}

                </button>

              )}

              <button style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, fontSize: '0.7rem' }} onClick={() => deleteItem(item.id)}>🗑️ Delete</button>

            </div>

          </div>

        )}

      </div>

    );

  };



  return (

    <HoverCard>

      {/* 2026-05-11 — Session 7: shared partial-quantity sheet for
          transferToCraftingTable / depositToParty / takeFromParty.
          Replaces three window.prompt() call sites. Single instance;
          content keyed off pendingQtySheet state. */}
      <PartialQtySheet
        open={pendingQtySheet.open}
        onClose={() => setPendingQtySheet((s) => ({ ...s, open: false }))}
        onConfirm={(qty) => {
          if (typeof pendingQtySheet.onConfirm === 'function') {
            pendingQtySheet.onConfirm(qty);
          }
        }}
        title={pendingQtySheet.title}
        max={pendingQtySheet.max}
        min={1}
        actionLabel={pendingQtySheet.actionLabel}
      />

      {/* 2026-04-27: consume receipt overlay (P5/P7). Shows the rolled

          breakdown for dice-based heals like Healing Salve's "+2d4+2 HP"

          so the player sees what was rolled, not just the final delta. */}

      {consumeReceipt && (

        <div

          onClick={() => setConsumeReceipt(null)}

          style={{

            position: 'fixed', bottom: 24, right: 24, zIndex: 1100,

            background: 'rgba(14,10,15,0.96)', border: '1px solid var(--ag)',

            borderRadius: 'var(--rl)', padding: '12px 16px', maxWidth: 320,

            boxShadow: 'var(--gn)', cursor: 'pointer', animation: 'fi .25s ease',

          }}

        >

          {/* 2026-04-27: dropped the "Used: {itemName}" header line per

              user feedback — felt redundant with the icon and made the

              receipt feel cluttered. Effects + delta carry the meaning. */}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>

            <span style={{ fontSize: 22 }}>{consumeReceipt.icon}</span>

          </div>

          {consumeReceipt.lines.map((l, i) => (

            <div key={i} style={{ fontSize: 12, color: 'var(--tgrb)', fontFamily: 'Alegreya,serif', lineHeight: 1.5 }}>

              {l.hadDice ? `🎲 ${l.breakdown}` : `↳ ${l.breakdown}`}

            </div>

          ))}

          {(consumeReceipt.hpDelta !== 0 || consumeReceipt.manaDelta !== 0 || consumeReceipt.duraDelta !== 0) && (

            <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--bs)', fontSize: 11, color: 'var(--ts)' }}>

              {consumeReceipt.hpDelta !== 0 && <span style={{ marginRight: 8, color: consumeReceipt.hpDelta > 0 ? '#27c95a' : '#c92727', fontWeight: 600 }}>{consumeReceipt.hpDelta > 0 ? '+' : ''}{consumeReceipt.hpDelta} HP</span>}

              {consumeReceipt.manaDelta !== 0 && <span style={{ marginRight: 8, color: '#5a9fd4', fontWeight: 600 }}>{consumeReceipt.manaDelta > 0 ? '+' : ''}{consumeReceipt.manaDelta} Mana</span>}

              {consumeReceipt.duraDelta !== 0 && <span style={{ marginRight: 8, color: '#c9a227', fontWeight: 600 }}>{consumeReceipt.duraDelta > 0 ? '+' : ''}{consumeReceipt.duraDelta} Dura</span>}

            </div>

          )}

          <div style={{ fontSize: 9, color: 'var(--tm)', marginTop: 4, opacity: 0.6 }}>click to dismiss</div>

        </div>

      )}

      {consumeFeedback && !consumeReceipt && !consumeFeedbackHasVisibleItem && (

        <div

          onClick={() => setConsumeFeedback(null)}

          style={{

            position: 'fixed', bottom: 24, right: 24, zIndex: 1100,

            background: 'rgba(14,10,15,0.96)', border: '1px solid rgba(39,201,90,0.55)',

            borderRadius: '8px', padding: '8px 12px', maxWidth: 280,

            boxShadow: '0 0 16px rgba(39,201,90,0.22)', cursor: 'pointer',

          }}

        >

          <div style={{ fontSize: 11, color: '#b9f6ca', marginBottom: 2 }}>{consumeFeedback.itemName || 'Item used'}</div>

          <div style={{ fontSize: 13, color: '#7dff7d', fontWeight: 700 }}>{consumeFeedback.text}</div>

        </div>

      )}

      {/* Header */}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>

        <h2 style={{ ...S.subtitle, margin: 0, border: 'none', paddingBottom: 0, fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }} onClick={() => setShowInventory(s => !s)}>

          <span style={{ color: '#ffd700', fontSize: '0.85rem' }}>{showInventory ? '▼' : '▶'}</span>

          🎒 Inventory

          <span style={{ fontSize: '0.7rem', opacity: 0.6, fontWeight: 'normal', marginLeft: '8px' }}>{totalItems} items</span>

        </h2>

        <button

          style={{ ...S.btn, ...S.btnSuccess, ...S.btnXs, fontSize: '0.75rem' }}

          onClick={() => setShowAddModal(true)}

        >

          + Add Item

        </button>

      </div>

      {/* 2026-04-27: top-level collapse — parallels Spells/Equip panels. */}

      {movementActionError && (

        <div
          role="alert"
          onClick={() => setMovementActionError(null)}
          style={{
            marginBottom: '12px',
            padding: '10px 12px',
            borderRadius: '6px',
            border: '1px solid rgba(212,64,64,0.65)',
            background: 'rgba(92,20,20,0.45)',
            color: '#ff6961',
            fontSize: '0.82rem',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {movementActionError}
        </div>

      )}

      {showInventory && <>

      {/* Quick Access Bar */}

      {quickBar.length > 0 && (

        <div style={{ marginBottom: '12px' }}>

          <div style={{ fontSize: '0.65rem', color: '#c9a227', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1px' }}>Quick Access</div>

          <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>

            {quickBar.map(renderQuickItem)}

          </div>

        </div>

      )}

      

      {/* Active Loadout Summary */}

      {equipped.length > 0 && (

        <div style={{ 

          marginBottom: '12px', 

          padding: '10px', 

          background: 'linear-gradient(90deg, rgba(39,201,90,0.1), transparent)',

          borderRadius: '6px',

          borderLeft: '3px solid #27c95a',

        }}>

          <div style={{ fontSize: '0.65rem', color: '#27c95a', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1px' }}>Active Loadout</div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>

            {equipped.map(item => (

              <span 

                key={item.id} 

                style={{ 

                  background: 'rgba(39,201,90,0.2)', 

                  border: '1px solid rgba(39,201,90,0.4)',

                  padding: '4px 8px', 

                  borderRadius: '4px', 

                  fontSize: '0.75rem',

                  cursor: 'pointer',

                }}

                onClick={() => toggleEquipped(item.id)}

                title="Click to unequip"

              >

                {ITEM_CATEGORIES[item.category]?.icon} {item.name}

                {item.buffs?.length > 0 && <span style={{ color: '#27c95a', marginLeft: '4px' }}>({item.buffs.join(', ')})</span>}

              </span>

            ))}

          </div>

        </div>

      )}

      

      {/* Active Quests Mini-Tracker */}

      {activeQuests.length > 0 && (

        <div style={{ 

          marginBottom: '12px', 

          padding: '10px', 

          background: 'linear-gradient(90deg, rgba(155,89,182,0.1), transparent)',

          borderRadius: '6px',

          borderLeft: '3px solid #9b59b6',

        }}>

          <div style={{ fontSize: '0.65rem', color: '#9b59b6', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1px' }}>Active Quests ({activeQuests.length})</div>

          {activeQuests.slice(0, 3).map(item => (

            <div key={item.id} style={{ fontSize: '0.8rem', marginBottom: '2px' }}>

              📜 {item.name}

              {item.questDetails && <span style={{ fontSize: '0.7rem', opacity: 0.6, marginLeft: '6px' }}>— {item.questDetails.slice(0, 40)}{item.questDetails.length > 40 ? '...' : ''}</span>}

            </div>

          ))}

          {activeQuests.length > 3 && <div style={{ fontSize: '0.7rem', opacity: 0.6 }}>+{activeQuests.length - 3} more</div>}

        </div>

      )}

      

      {/* Tab Navigation */}

      <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: '12px', flexWrap: 'wrap' }}>

        <button style={tabStyle(activeTab === 'quick')} onClick={() => setActiveTab('quick')}>

          ⚡ All ({inventory.length})

        </button>

        <button style={tabStyle(activeTab === 'combat')} onClick={() => setActiveTab('combat')}>

          ⚔️ Combat ({combat.length})

        </button>

        <button style={tabStyle(activeTab === 'consumable')} onClick={() => setActiveTab('consumable')}>

          🧪 Consumable ({consumables.length})

        </button>

        <button style={tabStyle(activeTab === 'quest')} onClick={() => setActiveTab('quest')}>

          📜 Quest ({quests.length})

        </button>

        <button style={tabStyle(activeTab === 'storage')} onClick={() => setActiveTab('storage')}>

          📦 Storage ({storage.length})

        </button>

        {/* 2026-04-23: party tab — only visible when the parent wires

            partyInventory + onUpdateParty props (i.e., in player mode). */}

        {(onUpdateParty || onTakeFromParty) && (

          <button style={tabStyle(activeTab === 'party')} onClick={() => setActiveTab('party')}>

            🎒 Party ({(partyInventory?.items || []).length})

          </button>

        )}

      </div>



      {/* Item List */}

      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>

        {/* 2026-04-27: equipped items sort to the top of each tab so the

            player's active loadout is always at-a-glance. The compact

            "Active Loadout" pill row above stays as a quick-toggle

            shortcut; this groups the full rows so abilities, buffs, and

            usage trackers are visible without scrolling past unequipped

            stuff. Stable sort: equipped → favorited → original order. */}

        {(() => {

          const sortForDisplay = (items) => {

            const eq = [], fav = [], rest = [];

            items.forEach(i => {

              if (i.equipped) eq.push(i);

              else if (i.favorited) fav.push(i);

              else rest.push(i);

            });

            return [...eq, ...fav, ...rest];

          };

          const renderGrouped = (items) => {

            const sorted = sortForDisplay(items);

            const equippedCount = sorted.filter(i => i.equipped).length;

            return sorted.map((item, idx) => {

              const isEquippedItem = idx < equippedCount;

              const isStowedItem = idx >= equippedCount;

              const hideThisItem = (isEquippedItem && equippedCollapsed) || (isStowedItem && stowedCollapsed);

              return (

                <React.Fragment key={item.id}>

                  {idx === 0 && equippedCount > 0 && (

                    <div

                      onClick={() => setEquippedCollapsed(c => !c)}

                      style={{ fontSize: '0.6rem', color: '#27c95a', textTransform: 'uppercase', letterSpacing: '1px', padding: '2px 4px', marginBottom: '2px', fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}

                    >

                      {equippedCollapsed ? '▶' : '▼'} Equipped ({equippedCount})

                    </div>

                  )}

                  {idx === equippedCount && equippedCount > 0 && idx < sorted.length && (

                    <div

                      onClick={() => setStowedCollapsed(c => !c)}

                      style={{ fontSize: '0.6rem', color: '#888', textTransform: 'uppercase', letterSpacing: '1px', padding: '6px 4px 2px', marginBottom: '2px', cursor: 'pointer', userSelect: 'none' }}

                    >

                      {stowedCollapsed ? '▶' : '▼'} Stowed ({sorted.length - equippedCount})

                    </div>

                  )}

                  {!hideThisItem && renderItemRow(item, activeTab === 'quick')}

                </React.Fragment>

              );

            });

          };

          if (activeTab === 'quick') return renderGrouped(inventory);

          if (activeTab === 'combat') return renderGrouped(combat);

          if (activeTab === 'consumable') return renderGrouped(consumables);

          if (activeTab === 'quest') return renderGrouped(quests);

          if (activeTab === 'storage') return renderGrouped(storage);

          return null;

        })()}



        {/* 2026-04-23: party pool view. Simpler item rows than the main

            inventory — just name, quantity, and a "📥 Take" action per

            stack. No edit/delete affordance (players can only take from

            the pool; editing the pool is the DM's job). */}

        {activeTab === 'party' && (

          <>

            <div style={{ padding: '8px 12px', fontSize: '0.7rem', opacity: 0.7, fontStyle: 'italic', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: '6px' }}>

              Shared with all party members. Take an item to add it to your personal inventory.

            </div>

            {(partyInventory?.items || []).map(poolItem => (

              <div key={poolItem.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: 'rgba(58,107,154,0.06)', border: '1px solid rgba(58,107,154,0.25)', borderRadius: '4px', marginBottom: '4px' }}>

                <span style={{ fontSize: '1rem' }}>{(ITEM_CATEGORIES[poolItem.category] || ITEM_CATEGORIES.misc).icon}</span>

                <div style={{ flex: 1, minWidth: 0 }}>

                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#e8d5b7' }}>

                    {poolItem.name}{poolItem.quantity > 1 ? ` ×${poolItem.quantity}` : ''}

                  </div>

                  <div style={{ fontSize: '0.65rem', opacity: 0.6 }}>

                    {poolItem.category}

                    {poolItem.buffs?.length > 0 && ` · ${poolItem.buffs.join(', ')}`}

                  </div>

                </div>

                <button

                  onClick={() => promptTakeFromParty(poolItem)}

                  disabled={pendingPartyTakeItemId === poolItem.id}

                  title={
                    pendingPartyTakeItemId === poolItem.id
                      ? 'Taking from the party pool...'
                      : (poolItem.quantity > 1 ? `Take some or all (×${poolItem.quantity}) from the party pool` : 'Take this stack from the party pool')
                  }

                  style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid rgba(39,201,90,0.4)', background: 'rgba(39,201,90,0.15)', color: '#27c95a', fontSize: '0.7rem', fontWeight: 700, cursor: pendingPartyTakeItemId === poolItem.id ? 'wait' : 'pointer', whiteSpace: 'nowrap', opacity: pendingPartyTakeItemId === poolItem.id ? 0.7 : 1 }}>

                  {pendingPartyTakeItemId === poolItem.id ? '⏳ Taking…' : '📥 Take'}

                </button>

              </div>

            ))}

            {(partyInventory?.items || []).length === 0 && (

              <div style={{ textAlign: 'center', padding: '30px', opacity: 0.5 }}>

                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🎒</div>

                <div>Party pool is empty</div>

                <div style={{ fontSize: '0.8rem', marginTop: '4px' }}>Deposit items from your inventory or crafting table to share with the party.</div>

              </div>

            )}

          </>

        )}



        {inventory.length === 0 && activeTab !== 'party' && (

          <div style={{ textAlign: 'center', padding: '30px', opacity: 0.5 }}>

            <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🎒</div>

            <div>Your inventory is empty</div>

            <div style={{ fontSize: '0.8rem', marginTop: '4px' }}>Click "+ Add Item" to get started</div>

          </div>

        )}

      </div>

      </>}

      {/* 2026-04-27: closes the showInventory fragment opened above. Modals

          (Add Item, Edit Item) stay mounted so an in-flight edit isn't torn

          down if the user toggles the panel closed. */}



      {/* Add Item Modal */}

      {showAddModal && (

        <div style={{ 

          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 

          background: 'rgba(0,0,0,0.8)', 

          display: 'flex', alignItems: 'center', justifyContent: 'center',

          zIndex: 10000,

        }} onClick={() => setShowAddModal(false)}>

          <div style={{ 

            background: 'linear-gradient(180deg, rgba(40,30,20,0.98) 0%, rgba(30,20,15,0.99) 100%)',

            border: '2px solid #c9a227',

            borderRadius: '8px',

            padding: '20px',

            width: '90%',

            maxWidth: '400px',

          }} onClick={e => e.stopPropagation()}>

            <h3 style={{ color: '#ffd700', marginBottom: '16px' }}>Add New Item</h3>

            

            <div style={{ marginBottom: '12px' }}>

              <label style={S.label}>Item Name</label>

              <input 

                style={S.input} 

                value={newItem.name} 

                onChange={e => setNewItem({ ...newItem, name: e.target.value })}

                onKeyPress={e => e.key === 'Enter' && addItem()}

                placeholder="Enter item name..."

                autoFocus

              />

            </div>

            

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginBottom: '12px' }}>

              <div>

                <label style={S.label}>Category</label>

                <select style={S.select} value={newItem.category} onChange={e => setNewItem({ ...newItem, category: e.target.value })}>

                  {Object.entries(ITEM_CATEGORIES).map(([key, cat]) => (

                    <option key={key} value={key}>{cat.icon} {cat.name}</option>

                  ))}

                </select>

              </div>

              <div>

                <label style={S.label}>Quantity</label>

                <input 

                  style={S.input} 

                  type="number" 

                  min="1" 

                  value={newItem.quantity}

                  onChange={e => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}

                />

              </div>

            </div>

            

            {/* Consumable Uses Configuration */}

            {newItem.category === 'consumable' && (

              <div style={{ padding: '12px', background: 'rgba(39,201,90,0.1)', borderRadius: '6px', border: '1px solid rgba(39,201,90,0.3)', marginBottom: '12px' }}>

                <div style={{ fontSize: '0.8rem', color: '#27c95a', marginBottom: '8px' }}>Consumable Uses</div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.7rem' }}>Uses per Item</label>

                    <input 

                      style={{ ...S.input, marginBottom: 0 }} 

                      type="number" 

                      min="1" 

                      value={newItem.maxUses}

                      onChange={e => setNewItem({ ...newItem, maxUses: parseInt(e.target.value) || 1 })}

                    />

                  </div>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.7rem' }}>Resets</label>

                    <select style={{ ...S.select, marginBottom: 0 }} value={newItem.resetType} onChange={e => setNewItem({ ...newItem, resetType: e.target.value })}>

                      {Object.entries(USES_RESET_TYPES).map(([key, rt]) => (

                        <option key={key} value={key}>{rt.name}</option>

                      ))}

                    </select>

                  </div>

                </div>

              </div>

            )}

            

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>

              <button style={S.btn} onClick={() => setShowAddModal(false)}>Cancel</button>

              <button style={{ ...S.btn, ...S.btnSuccess }} onClick={addItem}>Add Item</button>

            </div>

          </div>

        </div>

      )}

      

      {/* Edit Item Modal */}

      {editingItem && (

        // 2026-04-27 — two fixes:

        //   (a) THEME: dropped hardcoded brown gradient + gold border in

        //       favor of S.modal / theme card style, so the panel reads

        //       the active theme (Dark Arcana etc.) instead of clashing.

        //   (b) CLOSE BEHAVIOR: outside-click no longer dismisses with

        //       unsaved changes. The user has to confirm or use the

        //       explicit Cancel/Save buttons. This stops accidental

        //       wipe-outs when the user clicks slightly outside the

        //       form to switch focus.

        <div

          style={{

            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,

            background: 'rgba(0,0,0,0.85)',

            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',

            zIndex: 1000,

            overflowY: 'auto',

            padding: '20px',

          }}

          onClick={() => {

            const ok = window.confirm('Close without saving? Your edits will be lost.');

            if (ok) setEditingItem(null);

          }}

        >

          <div

            style={{

              ...(S.cardElevated || {}),

              background: 'var(--bg2, #1a1a2e)',

              border: '1px solid var(--ag, #c9a227)',

              borderRadius: '10px',

              padding: '20px',

              width: '100%',

              maxWidth: '500px',

              marginTop: '20px',

              marginBottom: '20px',

              boxShadow: '0 12px 40px rgba(0,0,0,0.6)',

            }}

            onClick={e => e.stopPropagation()}

          >

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>

              <h3 style={{ color: 'var(--tg, #ffd700)', margin: 0 }}>✏️ Edit Item</h3>

              <button style={{ ...S.btn, ...S.btnXs }} onClick={() => setEditingItem(null)} title="Close without saving">✕</button>

            </div>

            

            {/* Basic Info */}

            <div style={{ marginBottom: '16px' }}>

              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px', marginBottom: '8px' }}>

                <div>

                  <label style={{ ...S.label, fontSize: '0.75rem' }}>Name</label>

                  <input style={{ ...S.input, marginBottom: 0 }} value={editingItem.name} onChange={e => updateEditingItem({ name: e.target.value })} />

                </div>

                <div>

                  <label style={{ ...S.label, fontSize: '0.75rem' }}>Category</label>

                  <select style={{ ...S.select, marginBottom: 0 }} value={editingItem.category} onChange={e => updateEditingItem({ category: e.target.value })}>

                    {Object.entries(ITEM_CATEGORIES).map(([key, cat]) => (

                      <option key={key} value={key}>{cat.icon} {cat.name}</option>

                    ))}

                  </select>

                </div>

                <div>

                  <label style={{ ...S.label, fontSize: '0.75rem' }}>Qty</label>

                  <input style={{ ...S.input, marginBottom: 0 }} type="number" min="1" value={editingItem.quantity} onChange={e => updateEditingItem({ quantity: parseInt(e.target.value) || 1 })} />

                </div>

              </div>

            </div>

            

            {/* Uses Section (for consumables or items with uses) */}

            {(editingItem.category === 'consumable' || editingItem.uses) && (

              <div style={{ padding: '12px', background: 'rgba(201,162,39,0.1)', borderRadius: '6px', border: '1px solid rgba(201,162,39,0.3)', marginBottom: '12px' }}>

                <div style={{ fontSize: '0.8rem', color: '#c9a227', marginBottom: '8px' }}>Uses Configuration</div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.7rem' }}>Current</label>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>

                      <input 

                        style={{ ...S.input, marginBottom: 0, opacity: usesUnlocked ? 1 : 0.5 }} 

                        type="number" 

                        min="0" 

                        value={editingItem.uses?.current || 0}

                        disabled={!usesUnlocked}

                        onChange={e => updateEditingItem({ uses: { ...editingItem.uses, current: parseInt(e.target.value) || 0 } })}

                      />

                    </div>

                  </div>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.7rem' }}>Max Uses</label>

                    <input 

                      style={{ ...S.input, marginBottom: 0 }} 

                      type="number" 

                      min="1" 

                      value={editingItem.uses?.max || 1}

                      onChange={e => {

                        const max = parseInt(e.target.value) || 1;

                        updateEditingItem({ uses: { ...(editingItem.uses || {}), max, current: Math.min(editingItem.uses?.current || max, max) } });

                      }}

                    />

                  </div>

                  <div>

                    <label style={{ ...S.label, fontSize: '0.7rem' }}>Resets</label>

                    <select 

                      style={{ ...S.select, marginBottom: 0 }} 

                      value={editingItem.uses?.resetType || 'none'}

                      onChange={e => updateEditingItem({ uses: { ...(editingItem.uses || { current: 1, max: 1 }), resetType: e.target.value } })}

                    >

                      {Object.entries(USES_RESET_TYPES).map(([key, rt]) => (

                        <option key={key} value={key}>{rt.name}</option>

                      ))}

                    </select>

                  </div>

                </div>

                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', fontSize: '0.7rem', cursor: 'pointer', color: usesUnlocked ? '#c9a227' : '#888' }}>

                  <input type="checkbox" checked={usesUnlocked} onChange={e => setUsesUnlocked(e.target.checked)} />

                  🔓 Unlock current uses editing (to correct mistakes)

                </label>

                {!editingItem.uses && (

                  <button 

                    style={{ ...S.btn, ...S.btnXs, marginTop: '8px', fontSize: '0.7rem' }}

                    onClick={() => updateEditingItem({ uses: createUsesTracker(1, 'none') })}

                  >

                    + Enable Uses Tracking

                  </button>

                )}

                {editingItem.uses && (

                  <button 

                    style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, marginTop: '8px', fontSize: '0.7rem' }}

                    onClick={() => updateEditingItem({ uses: null })}

                  >

                    Remove Uses Tracking

                  </button>

                )}

              </div>

            )}

            

            {/* Buffs & Debuffs */}

            <div style={{ marginBottom: '12px' }}>

              <label style={{ ...S.label, fontSize: '0.75rem' }}>Buffs (comma separated, e.g., +2 Str, +1 AC)</label>

              <CommaSeparatedInput 

                style={{ ...S.input, marginBottom: '8px' }} 

                value={editingItem.buffs || []}

                onChange={v => updateEditingItem({ buffs: v })}

                placeholder="+2 Str, +1 AC..."

              />

              <label style={{ ...S.label, fontSize: '0.75rem' }}>Debuffs</label>

              <CommaSeparatedInput 

                style={{ ...S.input, marginBottom: '8px' }} 

                value={editingItem.debuffs || []}

                onChange={v => updateEditingItem({ debuffs: v })}

                placeholder="-1 Dex, -5 Speed..."

              />

              {editingItem.category === 'consumable' && (editingItem.buffs?.length > 0 || editingItem.debuffs?.length > 0) && (

                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', cursor: 'pointer' }}>

                  <input type="checkbox" checked={editingItem.buffsApplyOnUse || false} onChange={e => updateEditingItem({ buffsApplyOnUse: e.target.checked })} />

                  Apply buffs/debuffs when used (otherwise when equipped)

                </label>

              )}

            </div>

            

            {/* On Use Effects (Consumables) */}

            {editingItem.category === 'consumable' && (

              <div style={{ padding: '12px', background: 'rgba(39,201,90,0.1)', borderRadius: '6px', border: '1px solid rgba(39,201,90,0.3)', marginBottom: '12px' }}>

                <label style={{ ...S.label, fontSize: '0.75rem', color: '#27c95a' }}>On Use Effects</label>

                <CommaSeparatedInput 

                  style={{ ...S.input, marginBottom: 0 }} 

                  value={editingItem.onUseEffects || []}

                  onChange={v => updateEditingItem({ onUseEffects: v })}

                  placeholder="+10 HP, +5 Mana, Cure Poison..."

                />

                <div style={{ fontSize: '0.65rem', opacity: 0.7, marginTop: '4px' }}>

                  Examples: "+10 HP", "+5 Mana", "+3 Dura", "Cure Poison"

                </div>

              </div>

            )}

            

            {/* Abilities Section */}

            <div style={{ marginBottom: '12px' }}>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>

                <label style={{ ...S.label, fontSize: '0.75rem', marginBottom: 0 }}>Abilities</label>

                <button style={{ ...S.btn, ...S.btnXs, fontSize: '0.7rem' }} onClick={addAbilityToEditingItem}>+ Add Ability</button>

              </div>

              

              {(editingItem.abilities || []).map((ability, idx) => (

                <div key={ability.id} style={{ 

                  padding: '10px', 

                  background: 'rgba(90,159,212,0.1)', 

                  borderRadius: '6px', 

                  border: '1px solid rgba(90,159,212,0.3)',

                  marginBottom: '8px',

                }}>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>

                    <span style={{ fontSize: '0.75rem', color: '#5a9fd4' }}>Ability #{idx + 1}</span>

                    <button style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, fontSize: '0.6rem' }} onClick={() => removeAbilityFromEditingItem(ability.id)}>✕</button>

                  </div>

                  

                  <div style={{ marginBottom: '6px' }}>

                    <input 

                      style={{ ...S.input, marginBottom: '6px', fontWeight: 'bold' }} 

                      value={ability.name}

                      onChange={e => updateAbilityInEditingItem(ability.id, { name: e.target.value })}

                      placeholder="Ability name..."

                    />

                    <textarea 

                      style={{ ...S.input, marginBottom: '6px', minHeight: '40px', resize: 'vertical' }} 

                      value={ability.description || ''}

                      onChange={e => updateAbilityInEditingItem(ability.id, { description: e.target.value })}

                      placeholder="Description..."

                    />

                    <CommaSeparatedInput 

                      style={{ ...S.input, marginBottom: '6px' }} 

                      value={ability.effects || []}

                      onChange={v => updateAbilityInEditingItem(ability.id, { effects: v })}

                      placeholder="Effects: +1 HP, +2 Damage..."

                    />

                  </div>

                  

                  {/* Ability Uses */}

                  <div style={{ padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px' }}>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.7rem', cursor: 'pointer', marginBottom: ability.uses ? '8px' : 0 }}>

                      <input 

                        type="checkbox" 

                        checked={!!ability.uses}

                        onChange={e => {

                          if (e.target.checked) {

                            updateAbilityInEditingItem(ability.id, { uses: createUsesTracker(1, 'shortRest') });

                          } else {

                            updateAbilityInEditingItem(ability.id, { uses: null });

                          }

                        }}

                      />

                      Has limited uses

                    </label>

                    

                    {ability.uses && (

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px' }}>

                        <div>

                          <label style={{ fontSize: '0.6rem', color: '#888' }}>Current</label>

                          <input 

                            style={{ ...S.input, marginBottom: 0, padding: '4px', fontSize: '0.8rem', opacity: usesUnlocked ? 1 : 0.5 }} 

                            type="number" 

                            min="0"

                            disabled={!usesUnlocked}

                            value={ability.uses.current}

                            onChange={e => updateAbilityInEditingItem(ability.id, { uses: { ...ability.uses, current: parseInt(e.target.value) || 0 } })}

                          />

                        </div>

                        <div>

                          <label style={{ fontSize: '0.6rem', color: '#888' }}>Max</label>

                          <input 

                            style={{ ...S.input, marginBottom: 0, padding: '4px', fontSize: '0.8rem' }} 

                            type="number" 

                            min="1"

                            value={ability.uses.max}

                            onChange={e => {

                              const max = parseInt(e.target.value) || 1;

                              updateAbilityInEditingItem(ability.id, { uses: { ...ability.uses, max, current: Math.min(ability.uses.current, max) } });

                            }}

                          />

                        </div>

                        <div>

                          <label style={{ fontSize: '0.6rem', color: '#888' }}>Reset</label>

                          <select 

                            style={{ ...S.select, marginBottom: 0, padding: '4px', fontSize: '0.75rem' }} 

                            value={ability.uses.resetType}

                            onChange={e => updateAbilityInEditingItem(ability.id, { uses: { ...ability.uses, resetType: e.target.value } })}

                          >

                            {Object.entries(USES_RESET_TYPES).map(([key, rt]) => (

                              <option key={key} value={key}>{rt.name}</option>

                            ))}

                          </select>

                        </div>

                      </div>

                    )}

                  </div>

                </div>

              ))}

              

              {(editingItem.abilities || []).length === 0 && (

                <div style={{ fontSize: '0.75rem', opacity: 0.5, textAlign: 'center', padding: '12px' }}>

                  No abilities. Click "+ Add Ability" to add one.

                </div>

              )}

            </div>

            

            {/* Quest Details (Quest items) */}

            {editingItem.category === 'quest' && (

              <div style={{ padding: '12px', background: 'rgba(155,89,182,0.1)', borderRadius: '6px', border: '1px solid rgba(155,89,182,0.3)', marginBottom: '12px' }}>

                <label style={{ ...S.label, fontSize: '0.75rem', color: '#9b59b6' }}>Quest Details</label>

                <textarea 

                  style={{ ...S.input, marginBottom: '8px', minHeight: '60px', resize: 'vertical' }} 

                  value={editingItem.questDetails || ''}

                  onChange={e => updateEditingItem({ questDetails: e.target.value })}

                  placeholder="Quest objectives, notes..."

                />

                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', cursor: 'pointer' }}>

                  <input type="checkbox" checked={editingItem.questComplete || false} onChange={e => updateEditingItem({ questComplete: e.target.checked })} />

                  Quest Complete

                </label>

              </div>

            )}

            

            {/* Notes */}

            <div style={{ marginBottom: '16px' }}>

              <label style={{ ...S.label, fontSize: '0.75rem' }}>Notes</label>

              <textarea 

                style={{ ...S.input, marginBottom: 0, minHeight: '50px', resize: 'vertical' }} 

                value={editingItem.notes || ''}

                onChange={e => updateEditingItem({ notes: e.target.value })}

                placeholder="Additional notes..."

              />

            </div>



            {isDM && (

              <div style={{ marginTop: '10px' }}>

                <div style={{ fontSize: '0.75rem', opacity: 0.75, color: '#c9a227' }}>DM Notes (private)</div>

                <textarea

                  style={{ ...S.input, minHeight: '60px', resize: 'vertical' }}

                  value={editingItem.dmNotes || ''}

                  onChange={e => updateEditingItem({ dmNotes: e.target.value })}

                  placeholder="DM-only notes (secret lore, curses, conditions, etc.)"

                />

              </div>

            )}



            {/* Advanced Combat Bonuses */}

            <div style={{ marginBottom: '16px' }}>

              <button 

                style={{ ...S.btn, ...S.btnXs, fontSize: '0.7rem', marginBottom: '6px', color: '#c9a227', border: '1px solid rgba(201,162,39,0.3)' }}

                onClick={() => updateEditingItem({ _showAdvanced: !editingItem._showAdvanced })}

              >

                {editingItem._showAdvanced ? 'v' : '>'} Advanced Combat Integration

              </button>

              {editingItem._showAdvanced && (

                <div style={{ padding: '10px', background: 'rgba(201,162,39,0.08)', border: '1px solid rgba(201,162,39,0.2)', borderRadius: '6px' }}>

                  <div style={{ fontSize: '0.7rem', color: '#c9a227', marginBottom: '8px' }}>

                    Active bonuses apply when item is equipped (or when consumed for consumables)

                  </div>

                  {Array.isArray(editingItem.combatBonuses) && editingItem.combatBonuses.length > 0 && (

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>

                      {editingItem.combatBonuses.map((bonus, bIdx) => (

                        <span key={bIdx} style={{ 

                          padding: '2px 8px', borderRadius: '10px', fontSize: '0.65rem',

                          background: bonus.type === 'advantage' ? 'rgba(39,201,90,0.15)' : bonus.type === 'disadvantage' ? 'rgba(231,76,60,0.15)' : 'rgba(201,162,39,0.15)',

                          color: bonus.type === 'advantage' ? '#6cff8a' : bonus.type === 'disadvantage' ? '#ff6b6b' : '#ffd700',

                          display: 'inline-flex', alignItems: 'center', gap: '4px',

                        }}>

                          {bonus.label}

                          <span 

                            style={{ cursor: 'pointer', opacity: 0.6, marginLeft: '2px' }}

                            onClick={() => updateEditingItem({ combatBonuses: editingItem.combatBonuses.filter((_, j) => j !== bIdx) })}

                          >x</span>

                        </span>

                      ))}

                    </div>

                  )}

                  <select

                    aria-label="Add inventory combat bonus"

                    style={{ ...S.select, fontSize: '0.7rem', marginBottom: 0 }}

                    value=""

                    onChange={e => {

                      if (!e.target.value) return;

                      var sel = COMBAT_BONUS_TYPES.find(b => b.label === e.target.value);

                      if (sel) {

                        updateEditingItem({ combatBonuses: [...(editingItem.combatBonuses || []), { type: sel.type, scope: sel.scope, value: sel.value, label: sel.label }] });

                      }

                      e.target.value = '';

                    }}

                  >

                    <option value="">+ Add combat bonus...</option>

                    <optgroup label="Advantage">

                      {COMBAT_BONUS_TYPES.filter(b => b.type === 'advantage').map(b => (

                        <option key={b.label} value={b.label}>{b.label}</option>

                      ))}

                    </optgroup>

                    <optgroup label="Supreme Advantage">

                      {COMBAT_BONUS_TYPES.filter(b => b.type === 'supreme_advantage').map(b => (

                        <option key={b.label} value={b.label}>{b.label}</option>

                      ))}

                    </optgroup>

                    <optgroup label="Disadvantage">

                      {COMBAT_BONUS_TYPES.filter(b => b.type === 'disadvantage').map(b => (

                        <option key={b.label} value={b.label}>{b.label}</option>

                      ))}

                    </optgroup>

                    <optgroup label="Save Bonuses">

                      {COMBAT_BONUS_TYPES.filter(b => b.type === 'save_bonus').map(b => (

                        <option key={b.label} value={b.label}>{b.label}</option>

                      ))}

                    </optgroup>

                    <optgroup label="Passive Bonuses">

                      {COMBAT_BONUS_TYPES.filter(b => b.type === 'passive').map(b => (

                        <option key={b.label} value={b.label}>{b.label}</option>

                      ))}

                    </optgroup>

                    <optgroup label="Resistances and Immunities">

                      {COMBAT_BONUS_TYPES.filter(b => b.type === 'resistance' || b.type === 'immunity').map(b => (

                        <option key={b.label} value={b.label}>{b.label}</option>

                      ))}

                    </optgroup>

                  </select>

                  <ControlledMiniForm
                    onSubmit={submitCustomCombatBonusDraft}
                    submitLabel="Add"
                    submitDisabled={!customCombatBonusDraft.trim()}
                    style={{ display: 'flex', gap: '4px', marginTop: '6px', alignItems: 'center' }}
                    actionsStyle={{ gap: '4px' }}
                    submitButtonStyle={{ ...S.btn, ...S.btnXs, fontSize: '0.65rem' }}
                  >
                    <input
                      aria-label="Custom combat bonus draft"
                      style={{ ...S.input, fontSize: '0.7rem', marginBottom: 0, flex: 1 }}
                      placeholder="Custom bonus (e.g., +2 WIS Saves)"
                      value={customCombatBonusDraft}
                      onChange={e => setCustomCombatBonusDraft(e.target.value)}
                    />
                  </ControlledMiniForm>

                  {hasPartiallySupportedItemCombatBonuses(editingItem.combatBonuses) && (

                    <div data-testid="inventory-authored-mechanic-warning" role="note" style={{ marginTop: 6, padding: '6px 8px', borderRadius: 4, background: 'rgba(201,162,39,0.12)', border: '1px solid rgba(201,162,39,0.3)', color: '#ffd700', fontSize: '0.68rem' }}>
                      {AUTHORED_ITEM_MECHANIC_WARNING_TEXT}
                    </div>

                  )}

                </div>

              )}

            </div>



            {/* Action Buttons */}

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between' }}>

              <button style={{ ...S.btn, ...S.btnDanger }} onClick={() => deleteItem(editingItem.id)}>🗑️ Delete</button>

              <div style={{ display: 'flex', gap: '8px' }}>

                <button style={S.btn} onClick={() => setEditingItem(null)}>Cancel</button>

                <button style={{ ...S.btn, ...S.btnSuccess }} onClick={saveEditedItem}>💾 Save</button>

              </div>

            </div>

          </div>

        </div>

      )}

    </HoverCard>

  );

};



// Skills Display Component for Homepage - allows all users to edit

const SkillsDisplay = ({ char, isDM, onUpdate, diseases, systems, globalSettings }) => {

  // 2026-05-11 — Session 6: detect mobile internally, matching the pattern
  // SpellPanel uses (no prop drilling from caller). Drives grid collapse
  // to 1fr at <=768 px and gates the long-press-receipt wiring.
  const mob = useIsMobile();

  const [editingSkill, setEditingSkill] = useState(null);

  const [collapsed, setCollapsed] = useState(false);

  const [lastRoll, setLastRoll] = useState(null); // { skill, roll, modifier, total }

  // 2026-05-11 F2: auto-dismiss the roll-result banner after 8s.
  // Cleared on every new roll so the timer resets, not stacks.
  useEffect(() => {
    if (!lastRoll) return undefined;
    const t = setTimeout(() => setLastRoll(null), 8000);
    return () => clearTimeout(t);
  }, [lastRoll]);

  const [skillAdvantage, setSkillAdvantage] = useState('normal');

  const [skillSupreme, setSkillSupreme] = useState(false);

  // 2026-05-11 — Session 6: long-press a skill name to open a receipt
  // sheet decomposing the bonus by source. Delegated-handler pattern:
  // one useLongPress instance + a ref tracking which skill is being
  // pressed, rather than per-row hook (which would be hooks-in-loop).
  const [skillReceiptOpen, setSkillReceiptOpen] = useState(false);
  const [skillReceiptKey, setSkillReceiptKey] = useState(null);
  const pressingSkillRef = useRef(null);
  const skillLongPress = useLongPress(() => {
    if (!pressingSkillRef.current) return;
    setSkillReceiptKey(pressingSkillRef.current);
    setSkillReceiptOpen(true);
    // Clear after consumption so a stale row identity can't leak into
    // a later long-press fired by a different element. Reviewer-flagged
    // bug: without this, a drag-across-rows that exits the move
    // tolerance and re-enters could open the wrong row's receipt.
    pressingSkillRef.current = null;
  });

  const [pendingSkillRoll, setPendingSkillRoll] = useState(null); // { skill, firstDie, modifier, advMode }

  const resolvedSkillEffectContext = getResolvedCharacterEffectContext(char, diseases, systems, isDM);

  const effStats = resolvedSkillEffectContext?.effectiveStats || DEFAULT_STATS;

  const modifiers = resolvedSkillEffectContext?.modifiers || {};

  const profBonus = getProficiencyBonus(char.level || 1);



  const getResolvedSkillDetails = (skill) => getResolvedSkillContext({

    skill,

    stat: SKILL_MAP[skill],

    char,

    effectiveStats: effStats,

    statModifiers: modifiers[SKILL_MAP[skill]] || [],

    activeLegacyEffects: resolvedSkillEffectContext?.activeLegacyEffects,

  });

  

  const getSkillBonus = (skill) => getResolvedSkillDetails(skill).totalBonus;

  

  const getSkillTooltip = (skill) => {

    const lines = getResolvedSkillTooltipLines(getResolvedSkillDetails(skill));

    lines.push('\n[Click to edit]');

    return lines.join('\n');

  };



  const rollSkillCheck = (skill, e) => {

    e.stopPropagation();

    const skillContext = getResolvedSkillDetails(skill);

    const stat = skillContext.stat;

    const skillNameLower = skill.toLowerCase();

    const resolvedAdvantage = getResolvedCharacterAdvantageContext(char, {

      manualMode: skillAdvantage,

      ...getSkillAdvantageConfig(skillNameLower, stat),

    });

    const effectiveAdv = resolvedAdvantage.mode;
    const rollMode = effectiveAdv === 'supreme_advantage' ? 'advantage' : effectiveAdv;
    const isItemSupremeAdvantage = effectiveAdv === 'supreme_advantage';
    const isSupremeAdvantage = isItemSupremeAdvantage || (rollMode !== 'normal' && skillSupreme);

    const advSources = effectiveAdv !== 'normal' ? resolvedAdvantage.sources : [];

    

    const modifier = skillContext.totalBonus;

    

    const buildBreakdown = (rollVal) => {

      return [{ type: 'roll', value: rollVal, source: 'd20', isRoll: true }, ...getResolvedSkillBreakdownParts(skillContext)];

    };

    

    const isInstant = (char.settings || {}).instantAdvantageRoll !== false;

    

    if (rollMode !== 'normal' && !isInstant && !pendingSkillRoll) {

      // Two-click mode: first roll

      const firstDie = Math.floor(Math.random() * 20) + 1;

      setPendingSkillRoll({
        skill,
        firstDie,
        dice: [firstDie],
        requiredDice: isSupremeAdvantage ? 3 : 2,
        modifier,
        advMode: rollMode,
        isSupreme: isSupremeAdvantage,
        advSources,
        buildBreakdown,
      });

      setLastRoll(null);

      return;

    }

    

    if (rollMode !== 'normal' && !isInstant && pendingSkillRoll && pendingSkillRoll.skill === skill) {

      // Two-click mode: continue until enough dice have been rolled.

      const nextDie = Math.floor(Math.random() * 20) + 1;

      const previousDice = Array.isArray(pendingSkillRoll.dice) && pendingSkillRoll.dice.length > 0
        ? pendingSkillRoll.dice
        : [pendingSkillRoll.firstDie];

      const allDice = [...previousDice, nextDie];

      const requiredDice = pendingSkillRoll.requiredDice || 2;

      if (allDice.length < requiredDice) {
        setPendingSkillRoll({
          ...pendingSkillRoll,
          dice: allDice,
          firstDie: allDice[0],
          advSources: pendingSkillRoll.advSources || advSources,
        });
        setLastRoll(null);
        return;
      }

      const pendingMode = pendingSkillRoll.advMode || rollMode;

      const roll = pendingMode === 'advantage' ? Math.max(...allDice) : Math.min(...allDice);

      const total = roll + modifier;

      const breakdown = buildBreakdown(roll);

      setPendingSkillRoll(null);

      setLastRoll({
        skill,
        roll,
        modifier,
        total,
        breakdown,
        allDice,
        advMode: pendingMode,
        isSupreme: Boolean(pendingSkillRoll.isSupreme),
        advSources: pendingSkillRoll.advSources || advSources,
      });

      return;

    }

    

    // Instant mode or normal roll

    const numDice = rollMode !== 'normal' ? (isSupremeAdvantage ? 3 : 2) : 1;

    const allDice = [];

    for (let dd = 0; dd < numDice; dd++) allDice.push(Math.floor(Math.random() * 20) + 1);

    const roll = rollMode === 'advantage' ? Math.max(...allDice) : rollMode === 'disadvantage' ? Math.min(...allDice) : allDice[0];

    const total = roll + modifier;

    const breakdown = buildBreakdown(roll);

    

    setPendingSkillRoll(null);

    setLastRoll({
      skill,
      roll,
      modifier,
      total,
      breakdown,
      allDice: allDice.length > 1 ? allDice : null,
      advMode: rollMode !== 'normal' ? rollMode : null,
      isSupreme: isSupremeAdvantage,
      advSources: effectiveAdv !== 'normal' ? advSources : null,
    });

  };



  const handleSkillClick = (skill) => {

    setEditingSkill(editingSkill === skill ? null : skill);

  };



  const toggleProf = (skill) => {

    const profs = char.skillProficiencies || [];

    const expertise = char.skillExpertise || [];

    if (profs.includes(skill)) {

      onUpdate({ skillProficiencies: profs.filter(s => s !== skill), skillExpertise: expertise.filter(s => s !== skill) });

    } else {

      onUpdate({ skillProficiencies: [...profs, skill] });

    }

  };



  const toggleExpertise = (skill) => {

    const profs = char.skillProficiencies || [];

    const expertise = char.skillExpertise || [];

    if (!profs.includes(skill)) return;

    if (expertise.includes(skill)) {

      onUpdate({ skillExpertise: expertise.filter(s => s !== skill) });

    } else {

      onUpdate({ skillExpertise: [...expertise, skill] });

    }

  };



  const setMiscMod = (skill, val) => {

    const mods = { ...(char.skillMiscMods || {}) };

    if (val === 0 || val === '' || isNaN(val)) delete mods[skill];

    else mods[skill] = parseInt(val);

    onUpdate({ skillMiscMods: mods });

  };



  // Click outside to close

  const handleContainerClick = (e) => {

    if (editingSkill && !e.target.closest('.skill-edit-area')) {

      setEditingSkill(null);

    }

  };

  

  // 2026-05-11 — Session 6: build the receipt modifiers for the currently
  // long-pressed skill. Adapter from getResolvedSkillBreakdownParts's
  // {type,value,source,details} into ReceiptSheet's {effect,source,
  // sourceType,note}. Type → pill-category mapping: stat→base (ability
  // mod is a base contributor), prof→system, misc→base, equip→item,
  // buff→buff. The mapping aligns with ReceiptSheet's pillColors set.
  const skillReceiptType = (t) => {
    if (t === 'prof') return 'system';
    if (t === 'equip') return 'item';
    if (t === 'buff') return 'buff';
    return 'base'; // stat, misc, anything else
  };
  const skillReceiptModifiers = skillReceiptKey
    ? getResolvedSkillBreakdownParts(getResolvedSkillDetails(skillReceiptKey)).map((part) => ({
        effect: part.value,
        source: part.source,
        sourceType: skillReceiptType(part.type),
        note: part.details || null,
      }))
    : [];

  return <HoverCard style={{ marginTop: '16px' }} onClick={handleContainerClick}>

    {/* 2026-05-11 — Session 6: receipt sheet for long-pressed skill.
        Single instance for the whole panel; content swaps based on
        which skill the user held down on. */}
    <ReceiptSheet
      open={skillReceiptOpen}
      onClose={() => setSkillReceiptOpen(false)}
      title={skillReceiptKey || ''}
      value={skillReceiptKey ? (() => { const b = getSkillBonus(skillReceiptKey); return b >= 0 ? `+${b}` : `${b}`; })() : null}
      modifiers={skillReceiptModifiers}
    />

    {/* 2026-05-11 F2: sticky-top roll-result banner. Real-device feedback
        revealed that rolling a skill at the top of the list rendered the
        result at the bottom (after 12+ skills), feeling like "tap did
        nothing" — universal trust violation per MOBILE-REDESIGN §4c
        (100ms feedback rule). Now a compact banner pins at the top of
        the panel showing the most recent roll. The full breakdown card
        still renders below for users who want detail. Auto-dismiss
        timer is wired inside the component (8s, cleared on new roll).
        Tap the banner to dismiss early; tap the X to dismiss. */}
    {lastRoll && !collapsed && (
      <div
        onClick={() => setLastRoll(null)}
        role="button"
        aria-label={`Roll result: ${lastRoll.skill} ${lastRoll.total}. Tap to dismiss.`}
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          marginBottom: '8px',
          padding: '10px 14px',
          background: lastRoll.roll === 20 ? 'linear-gradient(135deg, rgba(39,201,90,0.25), rgba(39,201,90,0.12))'
                    : lastRoll.roll === 1  ? 'linear-gradient(135deg, rgba(201,39,39,0.25), rgba(201,39,39,0.12))'
                                            : 'linear-gradient(135deg, rgba(155,89,182,0.25), rgba(155,89,182,0.12))',
          border: `1px solid ${lastRoll.roll === 20 ? '#00ff00' : lastRoll.roll === 1 ? '#ff0000' : '#9b59b6'}`,
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          cursor: 'pointer',
          boxShadow: '0 2px 10px rgba(0,0,0,0.35)',
          backdropFilter: 'blur(4px)',
        }}
      >
        <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', flexShrink: 0 }}>🎲 {lastRoll.skill}</span>
        <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffd700', flexShrink: 0, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{lastRoll.total}</span>
        {Array.isArray(lastRoll.breakdown) && (
          <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.55)', flexShrink: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {lastRoll.breakdown.filter((p) => p.value !== undefined && p.value !== null).map((p, i) => (
              <span key={i} style={{ marginRight: '8px' }}>
                {p.value >= 0 ? '+' : ''}{p.value} {p.source}
              </span>
            ))}
          </span>
        )}
        <span style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.45)', marginLeft: 'auto', flexShrink: 0 }} aria-hidden="true">✕</span>
      </div>
    )}

    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: collapsed ? 0 : '8px' }}>

      <h2 style={{ ...S.subtitle, fontSize: mob ? '1.125rem' : '1rem', margin: 0, border: 'none', paddingBottom: 0 }}>🎯 Skills <span style={{ fontSize: mob ? '0.8125rem' : '0.75rem', color: '#4a90d9' }}>{mob ? '(tap to edit · hold for sources)' : '(click to edit · hover for sources)'}</span></h2>

      <div style={{ display: 'flex', alignItems: 'center', gap: mob ? '8px' : '4px' }}>

        {!collapsed && (

          <div style={{ display: 'inline-flex', gap: mob ? '8px' : '4px' }}>

            <button

              style={{ padding: mob ? '10px 14px' : '2px 8px', fontSize: mob ? '0.8125rem' : '0.62rem', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', background: skillAdvantage === 'advantage' ? 'rgba(39,201,90,0.35)' : 'rgba(255,255,255,0.05)', border: skillAdvantage === 'advantage' ? '1px solid #27c95a' : '1px solid rgba(255,255,255,0.12)', color: skillAdvantage === 'advantage' ? '#6cff8a' : '#666', minHeight: mob ? '40px' : undefined }}

              onClick={() => setSkillAdvantage(skillAdvantage === 'advantage' ? 'normal' : 'advantage')}

            >Adv</button>

            <button

              style={{ padding: mob ? '10px 14px' : '2px 8px', fontSize: mob ? '0.8125rem' : '0.62rem', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', background: skillAdvantage === 'disadvantage' ? 'rgba(231,76,60,0.35)' : 'rgba(255,255,255,0.05)', border: skillAdvantage === 'disadvantage' ? '1px solid #e74c3c' : '1px solid rgba(255,255,255,0.12)', color: skillAdvantage === 'disadvantage' ? '#ff6b6b' : '#666', minHeight: mob ? '40px' : undefined }}

              onClick={() => setSkillAdvantage(skillAdvantage === 'disadvantage' ? 'normal' : 'disadvantage')}

            >Dis</button>

            {skillAdvantage !== 'normal' && (

              <label style={{ fontSize: mob ? '0.75rem' : '0.55rem', color: skillSupreme ? '#ffd700' : '#555', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>

                <input type="checkbox" checked={skillSupreme} onChange={() => setSkillSupreme(!skillSupreme)} style={{ width: mob ? '18px' : '10px', height: mob ? '18px' : '10px' }} />

                Sup

              </label>

            )}

          </div>

        )}

        <button style={{ ...S.btn, ...S.btnXs }} onClick={(e) => { e.stopPropagation(); setCollapsed(!collapsed); }}>{collapsed ? '▶' : '▼'}</button>

      </div>

    </div>

    

    {!collapsed && <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : 'repeat(auto-fit, minmax(170px, 1fr))', gap: mob ? '6px' : '4px', fontSize: '0.85rem' }}>

      {ALL_SKILLS.map(skill => {

        const bonus = getSkillBonus(skill);

        const skillContext = getResolvedSkillDetails(skill);

        const isProficient = skillContext.isProficient;

        const isExpertise = skillContext.isExpertise;

        const miscMod = (char.skillMiscMods || {})[skill] || 0;

        return <div key={skill} className="skill-edit-area">

          <div style={{ ...S.skillRow, background: isExpertise ? 'rgba(201,162,39,0.2)' : isProficient ? 'rgba(39,201,90,0.15)' : 'rgba(0,0,0,0.2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

            <span

              style={{ display: 'flex', alignItems: 'center', position: 'relative', ...skillLongPress.bind.style, minHeight: mob ? '44px' : undefined }}

              onClick={(e) => {
                // Suppress click if a long-press just fired (delegated
                // handler swallows the post-press click).
                skillLongPress.bind.onClick(e);
                if (e.defaultPrevented) return;
                handleSkillClick(skill);
              }}

              onTouchStart={(e) => { pressingSkillRef.current = skill; skillLongPress.bind.onTouchStart(e); }}
              onTouchMove={skillLongPress.bind.onTouchMove}
              onTouchEnd={(e) => { skillLongPress.bind.onTouchEnd(e); pressingSkillRef.current = null; }}
              onTouchCancel={(e) => { skillLongPress.bind.onTouchCancel(e); pressingSkillRef.current = null; }}
              onMouseDown={(e) => { pressingSkillRef.current = skill; skillLongPress.bind.onMouseDown(e); }}
              onMouseUp={(e) => { skillLongPress.bind.onMouseUp(e); pressingSkillRef.current = null; }}

              onMouseEnter={(e) => {

                const tip = e.currentTarget.querySelector('.skill-tooltip');

                if (tip) tip.style.display = 'block';

              }}

              onMouseLeave={(e) => {

                const tip = e.currentTarget.querySelector('.skill-tooltip');

                if (tip) tip.style.display = 'none';
                skillLongPress.bind.onMouseLeave();

              }}

            >

              <span style={{ width: mob ? '18px' : '14px', marginRight: mob ? '8px' : '4px', color: isExpertise ? '#ffd700' : isProficient ? '#27c95a' : '#555', fontSize: mob ? '0.9rem' : '0.7rem' }}>{isExpertise ? '◆' : isProficient ? '●' : '○'}</span>

              <span style={{ fontSize: mob ? '0.9375rem' : '0.8rem' }}>{skill}</span>

              <span style={{ fontWeight: 'bold', color: bonus >= 0 ? '#7dff7d' : '#ff7d7d', fontSize: mob ? '1rem' : '0.85rem', marginLeft: mob ? '8px' : '4px' }}>{bonus >= 0 ? '+' : ''}{bonus}</span>

              {/* Tooltip */}

              <div 

                className="skill-tooltip"

                style={{

                  display: 'none',

                  position: 'absolute',

                  bottom: '100%',

                  left: '50%',

                  transform: 'translateX(-50%)',

                  background: 'rgba(20,15,10,0.98)',

                  border: '1px solid #c9a227',

                  borderRadius: '6px',

                  padding: '8px 12px',

                  fontSize: '0.75rem',

                  whiteSpace: 'pre-wrap',

                  zIndex: 10000,

                  marginBottom: '4px',

                  minWidth: '180px',

                  maxWidth: '280px',

                  textAlign: 'left',

                  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',

                  color: '#e8d5b7',

                }}

              >

                {getSkillTooltip(skill)}

              </div>

            </span>

            <button

              style={{

                background: 'rgba(155,89,182,0.3)',

                border: '1px solid #9b59b6',

                borderRadius: mob ? '8px' : '3px',

                color: '#d4a5ff',

                cursor: 'pointer',

                padding: mob ? '0' : '1px 3px',

                fontSize: mob ? '1.25rem' : '0.55rem',

                flexShrink: 0,

                width: mob ? '48px' : '18px',

                height: mob ? '48px' : '18px',

                marginLeft: mob ? '8px' : '0',

                display: 'flex',

                alignItems: 'center',

                justifyContent: 'center',

              }}

              onClick={(e) => rollSkillCheck(skill, e)}

              aria-label={`Roll ${skill}`}

              title="Roll d20 + modifier"

            >

              🎲

            </button>

          </div>

          {editingSkill === skill && <div style={{ padding: '6px', background: 'rgba(0,0,0,0.3)', borderRadius: '4px', marginTop: '2px', fontSize: '0.75rem', position: 'relative' }}>

            <button style={{ position: 'absolute', top: '2px', right: '2px', background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '0.7rem', padding: '2px' }} onClick={() => setEditingSkill(null)}>✕</button>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>

              <label><input type="checkbox" checked={isProficient} onChange={() => toggleProf(skill)} /> Prof</label>

              <label><input type="checkbox" checked={isExpertise} disabled={!isProficient} onChange={() => toggleExpertise(skill)} /> Expert</label>

            </div>

            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>

              <span>Misc:</span>

              <input style={{ ...S.input, width: '50px', padding: '4px', marginBottom: 0, fontSize: '0.75rem' }} type="number" value={miscMod || ''} onChange={e => setMiscMod(skill, e.target.value)} />

            </div>

          </div>}

        </div>;

      })}

    </div>}

    

    {/* Pending Roll — Two-click Advantage Mode */}

    {pendingSkillRoll && !collapsed && (

      <div style={{ 

        marginTop: '8px', padding: '6px 12px',

        background: pendingSkillRoll.advMode === 'advantage' ? 'rgba(39,201,90,0.12)' : 'rgba(231,76,60,0.12)',

        border: '1px solid ' + (pendingSkillRoll.advMode === 'advantage' ? 'rgba(39,201,90,0.4)' : 'rgba(231,76,60,0.4)'),

        borderRadius: '6px',

      }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

          <div>

            <span style={{ color: '#c9a227', fontWeight: 'bold', fontSize: '0.85rem' }}>{pendingSkillRoll.skill}</span>

            <Tooltip text={(pendingSkillRoll.advSources || []).length > 0 ? `From: ${pendingSkillRoll.advSources.join(', ')}` : (pendingSkillRoll.advMode === 'advantage' ? 'Advantage' : 'Disadvantage')}>

              <span style={{ fontSize: '0.6rem', marginLeft: '6px', padding: '1px 5px', borderRadius: '3px', background: pendingSkillRoll.advMode === 'advantage' ? 'rgba(39,201,90,0.25)' : 'rgba(231,76,60,0.25)', color: pendingSkillRoll.advMode === 'advantage' ? '#6cff8a' : '#ff6b6b', cursor: 'help' }}>{pendingSkillRoll.advMode === 'advantage' ? 'ADV' : 'DIS'}</span>

            </Tooltip>

          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

            <span style={{ fontSize: '0.8rem', color: '#aaa' }}>{pendingSkillRoll.firstDie}</span>

            <span style={{ fontSize: '0.7rem', color: '#888' }}>{pendingSkillRoll.modifier >= 0 ? '+' : ''}{pendingSkillRoll.modifier}</span>

            <span style={{ fontSize: '0.7rem', color: '#666' }}>=</span>

            <span style={{ padding: '1px 8px', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.9rem', background: 'rgba(201,162,39,0.2)', border: '1px solid #c9a227', color: '#ffd700' }}>{pendingSkillRoll.firstDie + pendingSkillRoll.modifier}</span>

            <button

              style={{ padding: '3px 10px', fontSize: '0.72rem', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', background: 'rgba(155,89,182,0.3)', border: '1px solid #9b59b6', color: '#d4a5ff', animation: 'pulse 1.5s infinite' }}

              onClick={(e) => rollSkillCheck(pendingSkillRoll.skill, e)}

            >Roll Again</button>

            <button style={{ ...S.btn, ...S.btnXs, padding: '2px 6px', fontSize: '0.65rem', marginBottom: 0 }} onClick={() => setPendingSkillRoll(null)}>x</button>

          </div>

        </div>

      </div>

    )}

    

    {/* Roll Result Display - At Bottom */}

    {lastRoll && !collapsed && (

      <div style={{ 

        marginTop: '8px', 

        padding: '8px 12px', 

        background: lastRoll.roll === 20 ? 'rgba(0,255,0,0.15)' : lastRoll.roll === 1 ? 'rgba(255,0,0,0.15)' : 'rgba(155,89,182,0.15)', 

        border: `1px solid ${lastRoll.roll === 20 ? '#00ff00' : lastRoll.roll === 1 ? '#ff0000' : '#9b59b6'}`,

        borderRadius: '6px',

      }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: lastRoll.breakdown ? '6px' : 0 }}>

          <div>

            <span style={{ color: '#ffd700', fontWeight: 'bold', fontSize: '0.9rem' }}>{lastRoll.skill}</span>

            {lastRoll.roll === 20 && <span style={{ color: '#00ff00', fontWeight: 'bold', marginLeft: '8px' }}>✨ NAT 20!</span>}

            {lastRoll.roll === 1 && <span style={{ color: '#ff0000', fontWeight: 'bold', marginLeft: '8px' }}>💀 NAT 1!</span>}

          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

            <span style={{ 

              fontSize: '1.4rem', 

              fontWeight: 'bold', 

              color: lastRoll.roll === 20 ? '#00ff00' : lastRoll.roll === 1 ? '#ff0000' : '#ffd700',

              textShadow: lastRoll.roll === 20 ? '0 0 8px rgba(0,255,0,0.5)' : lastRoll.roll === 1 ? '0 0 8px rgba(255,0,0,0.5)' : '0 0 4px rgba(255,215,0,0.3)',

            }}>

              {lastRoll.total}

            {lastRoll.advMode && lastRoll.advMode !== 'normal' && (

              <Tooltip text={(lastRoll.advSources || []).length > 0 ? `From: ${lastRoll.advSources.join(', ')}` : (lastRoll.advMode === 'advantage' ? 'Advantage' : 'Disadvantage')}>

                <span style={{ fontSize: '0.6rem', marginLeft: '6px', padding: '1px 5px', borderRadius: '3px', background: lastRoll.advMode === 'advantage' ? 'rgba(39,201,90,0.2)' : 'rgba(231,76,60,0.2)', color: lastRoll.advMode === 'advantage' ? '#6cff8a' : '#ff6b6b', cursor: 'help' }}>{lastRoll.advMode === 'advantage' ? 'ADV' : 'DIS'}</span>

              </Tooltip>

            )}

            </span>

            <button 

              style={{ ...S.btn, ...S.btnXs, padding: '2px 6px', fontSize: '0.7rem', marginBottom: 0 }} 

              onClick={() => setLastRoll(null)}

            >✕</button>

          </div>

        </div>

        {/* Breakdown */}

        {lastRoll.breakdown && (

          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', flexWrap: 'wrap', fontSize: '0.75rem' }}>

            {lastRoll.breakdown.map((part, idx) => {

              const colors = {

                roll: lastRoll.roll === 20 ? '#00ff00' : lastRoll.roll === 1 ? '#ff0000' : '#e8d5b7',

                stat: '#4a90d9',

                prof: '#c9a227',

                misc: '#9b59b6',

                buff: '#27c95a',

              };

              const color = colors[part.type] || '#888';

              const tooltipText = part.details ? `${part.source}\n${part.details}` : part.source;

              

              return (

                <React.Fragment key={idx}>

                  {idx > 0 && <span style={{ color: '#666', alignSelf: 'center', marginBottom: '8px' }}>+</span>}

                  <div 

                    style={{ 

                      display: 'flex',

                      flexDirection: 'column',

                      alignItems: 'center',

                      cursor: part.details ? 'help' : 'default',

                    }}

                    title={tooltipText}

                  >

                    <span 

                      style={{ 

                        padding: '3px 8px',

                        background: `${color}20`,

                        border: `1px solid ${color}50`,

                        borderRadius: '4px',

                        color,

                        fontWeight: 'bold',

                        fontSize: '0.9rem',

                      }}

                    >

                      {part.isRoll ? part.value : (part.value >= 0 ? `+${part.value}` : part.value)}

                    </span>

                    <span style={{ fontSize: '0.55rem', color: color, opacity: 0.9, marginTop: '2px' }}>{part.source}</span>

                  </div>

                </React.Fragment>

              );

            })}

            <span style={{ color: '#666', alignSelf: 'center', marginBottom: '8px' }}>=</span>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

              <span style={{ padding: '3px 10px', background: 'rgba(201,162,39,0.25)', border: '2px solid #c9a227', borderRadius: '4px', color: '#ffd700', fontWeight: 'bold', fontSize: '1rem' }}>{lastRoll.total}</span>

              <span style={{ fontSize: '0.55rem', color: '#c9a227', opacity: 0.9, marginTop: '2px' }}>Total</span>

            </div>

          </div>

        )}

        {/* Side-by-side advantage/disadvantage calculations */}

        {lastRoll.allDice && lastRoll.allDice.length > 1 && (

          <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.78rem' }}>

            {lastRoll.allDice.map((d, di) => {

              const total = d + lastRoll.modifier;

              const isChosen = d === lastRoll.roll;

              return (

                <span key={di} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', opacity: isChosen ? 1 : 0.65 }}>

                  <span style={{ color: isChosen ? '#e8d5b7' : '#999' }}>{d}</span>

                  <span style={{ color: isChosen ? '#888' : '#777', fontSize: '0.7rem' }}>{lastRoll.modifier >= 0 ? '+' : ''}{lastRoll.modifier}</span>

                  <span style={{ color: isChosen ? '#888' : '#777', fontSize: '0.7rem' }}>=</span>

                  <span style={{

                    padding: '1px 6px', borderRadius: '3px', fontWeight: 'bold',

                    background: isChosen ? 'rgba(201,162,39,0.2)' : 'transparent',

                    border: isChosen ? '1px solid #c9a227' : '1px solid transparent',

                    color: isChosen ? '#ffd700' : '#888',

                  }}>{total}</span>

                </span>

              );

            })}

          </div>

        )}

      </div>

    )}

  </HoverCard>;

};



// Compact Wallet Display Component

const WalletPanel = ({ char, onUpdate, globalSettings }) => {

  const [editMode, setEditMode] = useState(null); // null, 'add', 'spend'

  const [amounts, setAmounts] = useState({});

  

  const settings = globalSettings || DEFAULT_SETTINGS;

  const tiers = settings.currencyTiers || DEFAULT_SETTINGS.currencyTiers;

  const wallet = char.wallet || {};

  

  if (!settings.currencyEnabled) return null;

  

  // Sort tiers by value (highest first for display)

  const sortedTiers = [...tiers].map(tier => ({

    ...tier,

    totalValue: currencyToBase(1, tier.id, tiers)

  })).sort((a, b) => b.totalValue - a.totalValue);

  

  // Tiers with amounts (always shown)

  const tiersWithAmount = sortedTiers.filter(t => (wallet[t.id] || 0) > 0);

  // Additional tiers (only shown in edit mode)

  const additionalTiers = sortedTiers.filter(t => (wallet[t.id] || 0) === 0);

  

  const handleConfirm = () => {

    const currentBase = walletToBase(wallet, tiers);

    let delta = 0;

    

    // Calculate total change in base units

    for (const tier of tiers) {

      const amt = parseFloat(amounts[tier.id]) || 0;

      if (amt > 0) {

        delta += currencyToBase(amt, tier.id, tiers);

      }

    }

    

    if (delta === 0) {

      setEditMode(null);

      setAmounts({});

      return;

    }

    

    let newBase;

    if (editMode === 'spend') {

      if (currentBase < delta) {

        window.alert('Insufficient funds!');

        return;

      }

      newBase = currentBase - delta;

    } else {

      newBase = currentBase + delta;

    }

    

    const newWallet = baseToWallet(newBase, tiers);

    onUpdate({ ...char, wallet: newWallet });

    setEditMode(null);

    setAmounts({});

  };

  

  const handleCancel = () => {

    setEditMode(null);

    setAmounts({});

  };

  

  // If nothing to show and not editing

  if (tiersWithAmount.length === 0 && !editMode) {

    return (

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

        <span style={{ fontSize: '0.85rem', color: '#888' }}>💰 Empty</span>

        <button 

          onClick={() => setEditMode('add')}

          style={{ 

            background: 'rgba(39,201,90,0.2)', 

            border: '1px solid rgba(39,201,90,0.4)', 

            borderRadius: '4px', 

            color: '#6bff6b', 

            cursor: 'pointer',

            padding: '2px 8px',

            fontSize: '0.7rem',

          }}

        >+ Add</button>

      </div>

    );

  }

  

  const inputBorderColor = editMode === 'spend' ? 'rgba(201,39,39,0.5)' : 'rgba(39,201,90,0.5)';

  

  return (

    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', flexWrap: 'wrap' }}>

      {/* Currency display - tiers with amounts */}

      {tiersWithAmount.map(tier => {

        const iconDef = CURRENCY_ICONS[tier.icon] || CURRENCY_ICONS.coinGold;

        const amount = wallet[tier.id] || 0;

        

        return (

          <div key={tier.id} style={{ 

            display: 'flex', 

            flexDirection: 'column',

            alignItems: 'center',

            background: 'rgba(0,0,0,0.3)',

            padding: '4px 6px',

            borderRadius: '4px',

            border: `1px solid ${iconDef.color}33`,

            minWidth: '44px',

          }}>

            {/* Icon and current amount */}

            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>

              <span title={tier.name}><CurrencyIcon type={tier.icon || 'coinGold'} size={16} /></span>

              <span style={{ 

                fontWeight: 'bold', 

                color: '#ffd700',

                fontSize: '0.9rem',

                minWidth: '16px',

                textAlign: 'center',

              }}>

                {amount}

              </span>

            </div>

            {/* Input below (only in edit mode) */}

            {editMode && (

              <input

                type="number"

                min="0"

                placeholder="0"

                value={amounts[tier.id] || ''}

                onChange={e => setAmounts({ ...amounts, [tier.id]: e.target.value })}

                style={{

                  width: '100%',

                  marginTop: '4px',

                  padding: '2px 4px',

                  background: 'rgba(0,0,0,0.4)',

                  border: `1px solid ${inputBorderColor}`,

                  borderRadius: '3px',

                  color: editMode === 'spend' ? '#ff6b6b' : '#6bff6b',

                  fontSize: '0.75rem',

                  textAlign: 'center',

                  boxSizing: 'border-box',

                }}

              />

            )}

          </div>

        );

      })}

      

      {/* Additional tiers (only in edit mode, shown to the right) */}

      {editMode && additionalTiers.map(tier => {

        const iconDef = CURRENCY_ICONS[tier.icon] || CURRENCY_ICONS.coinGold;

        

        return (

          <div key={tier.id} style={{ 

            display: 'flex', 

            flexDirection: 'column',

            alignItems: 'center',

            background: 'rgba(0,0,0,0.2)',

            padding: '4px 6px',

            borderRadius: '4px',

            border: `1px dashed ${iconDef.color}33`,

            minWidth: '44px',

            opacity: 0.7,

          }}>

            {/* Icon and zero amount */}

            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>

              <span title={tier.name}><CurrencyIcon type={tier.icon || 'coinGold'} size={16} /></span>

              <span style={{ 

                fontWeight: 'bold', 

                color: '#888',

                fontSize: '0.9rem',

                minWidth: '16px',

                textAlign: 'center',

              }}>

                0

              </span>

            </div>

            {/* Input below */}

            <input

              type="number"

              min="0"

              placeholder="0"

              value={amounts[tier.id] || ''}

              onChange={e => setAmounts({ ...amounts, [tier.id]: e.target.value })}

              style={{

                width: '100%',

                marginTop: '4px',

                padding: '2px 4px',

                background: 'rgba(0,0,0,0.4)',

                border: `1px solid ${inputBorderColor}`,

                borderRadius: '3px',

                color: editMode === 'spend' ? '#ff6b6b' : '#6bff6b',

                fontSize: '0.75rem',

                textAlign: 'center',

                boxSizing: 'border-box',

              }}

            />

          </div>

        );

      })}

      

      {/* Action buttons */}

      {!editMode ? (

        <div style={{ display: 'flex', gap: '4px', marginLeft: '4px', alignSelf: 'center' }}>

          <button 

            onClick={() => setEditMode('add')}

            style={{ 

              background: 'rgba(39,201,90,0.2)', 

              border: '1px solid rgba(39,201,90,0.4)', 

              borderRadius: '4px', 

              color: '#6bff6b', 

              cursor: 'pointer',

              padding: '2px 6px',

              fontSize: '0.7rem',

            }}

            title="Add currency"

          >+</button>

          <button 

            onClick={() => setEditMode('spend')}

            style={{ 

              background: 'rgba(201,39,39,0.2)', 

              border: '1px solid rgba(201,39,39,0.4)', 

              borderRadius: '4px', 

              color: '#ff6b6b', 

              cursor: 'pointer',

              padding: '2px 6px',

              fontSize: '0.7rem',

            }}

            title="Spend currency"

          >−</button>

        </div>

      ) : (

        <div style={{ display: 'flex', gap: '4px', marginLeft: '4px', alignSelf: 'center' }}>

          <button 

            onClick={handleConfirm}

            style={{ 

              background: editMode === 'spend' ? 'rgba(201,39,39,0.3)' : 'rgba(39,201,90,0.3)', 

              border: `1px solid ${editMode === 'spend' ? 'rgba(201,39,39,0.5)' : 'rgba(39,201,90,0.5)'}`, 

              borderRadius: '4px', 

              color: editMode === 'spend' ? '#ff6b6b' : '#6bff6b', 

              cursor: 'pointer',

              padding: '2px 8px',

              fontSize: '0.7rem',

            }}

          >{editMode === 'spend' ? '💸' : '✓'}</button>

          <button 

            onClick={handleCancel}

            style={{ 

              background: 'rgba(100,100,100,0.2)', 

              border: '1px solid rgba(100,100,100,0.4)', 

              borderRadius: '4px', 

              color: '#888', 

              cursor: 'pointer',

              padding: '2px 6px',

              fontSize: '0.7rem',

            }}

          >✕</button>

        </div>

      )}

    </div>

  );

};



// Character Sheet Panel Component

export const CharacterSheetPanel = ({ char, showSheet, setShowSheet, diseases, systems, isDM, globalSettings }) => {

  const [tab, setTab] = useState('stats');

  const [lastRoll, setLastRoll] = useState(null); // { type, name, roll, modifier, total, allDice, advMode }

  // 2026-05-11 F2: auto-dismiss the stat roll-result banner after 8s.
  // The character sheet panel already renders its result at the top
  // (banner pattern); this just adds the auto-dismiss timer that
  // skills/spells got.
  useEffect(() => {
    if (!lastRoll) return undefined;
    const t = setTimeout(() => setLastRoll(null), 8000);
    return () => clearTimeout(t);
  }, [lastRoll]);

  const [sheetAdvantage, setSheetAdvantage] = useState('normal');

  const [pendingSheetRoll, setPendingSheetRoll] = useState(null); // { type, name, firstDie, modifier }

  // 2026-04-29: stat panel position rules — see BUG-REPORT-REFERENCE.md

  // §8 for the unified floating-icon layout.

  //   Closed button: right:20 bottom:20 (its bottom-right home / slot A

  //                  in the bottom row when other panels are open).

  //   Popup open, drawer closed: right:16 bottom:70 — the "center" anchor.

  //                  Vacates bottom:20 corner so journal + bug icons can

  //                  fill the bottom row below the popup.

  //   Popup open, drawer open: right:472 bottom:70 — clears drawer's

  //                  right column (drawer occupies right:12-452).

  const bugReportDrawerOpen = useBugReportDrawerOpen();

  if (!char) return null;

  

  const _eff = getEffectiveBuffs(char);

  const { stats: effStats, modifiers } = calculateStats(char.baseStats, _eff.buffList, _eff.debuffList, _eff.permBuffs, _eff.permDebuffs, char.diseaseList, diseases, _eff.buffSources, char.buffNotes, char.buffData, char.systemList, systems, isDM);

  const activeLegacyEffects = getActiveLegacyEffectEntries(char, _eff);

  const activeRuntimeEffects = char.activeEffects || [];

  const base = char.baseStats || DEFAULT_STATS;

  const profBonus = getProficiencyBonus(char.level || 1);

  const resolvedPopupArmorContext = getResolvedArmorClassContext({

    char,

    effectiveStats: effStats,

    equippedCombatBonuses: getEquippedCombatBonusesFromInventory(char.inventory),

    activeLegacyEffects,

    activeRuntimeEffects,

    armorTypes: globalSettings?.armorTypes || DEFAULT_ARMOR_TYPES,

    customArmorTypes: ARMOR_TYPES_DURA,

  });

  const equippedCombatBonuses = getEquippedCombatBonusesFromInventory(char.inventory);

  const getResolvedPopupSkillContext = (skill) => getResolvedSkillContext({

    skill,

    stat: SKILL_MAP[skill],

    char,

    effectiveStats: effStats,

    statModifiers: modifiers[SKILL_MAP[skill]] || [],

    activeLegacyEffects,

  });

  const getResolvedPopupPassiveContext = (skill) => getResolvedPassiveSkillContext({

    skillContext: getResolvedPopupSkillContext(skill),

    equippedCombatBonuses,

  });

  

  const getSkillBonus = (skill) => getResolvedPopupSkillContext(skill).totalBonus;

  

  const getSkillTooltip = (skill) => getResolvedSkillTooltipLines(getResolvedPopupSkillContext(skill)).join('\n');

  

  const getResolvedSheetSaveContext = (stat) => (

    getResolvedSaveContext({

      stat,

      char,

      effectiveStats: effStats,

      equippedCombatBonuses,

      activeLegacyEffects,

      activeRuntimeEffects,

    })

  );



  const getSaveBonus = (stat) => getResolvedSheetSaveContext(stat).totalBonus;

  const getEquippedPassiveBonusParts = (scope) => equippedCombatBonuses
    .filter((bonus) => bonus?.type === 'passive' && bonus?.scope === scope && bonus?.value)
    .map((bonus) => ({
      type: 'equip',
      value: Number(bonus.value) || 0,
      source: bonus.source || bonus.itemName || bonus.label || 'Equipped item',
      details: bonus.label || null,
    }))
    .filter((part) => part.value !== 0);

  const getSheetInitiativeContext = () => {
    const dexMod = getAbilityMod(effStats.dex);
    const itemBonusParts = getEquippedPassiveBonusParts('initiative');
    const itemBonus = itemBonusParts.reduce((sum, part) => sum + part.value, 0);
    return {
      modifier: dexMod + itemBonus,
      breakdownParts: [
        { type: 'stat', value: dexMod, source: 'DEX mod' },
        ...itemBonusParts,
      ],
    };
  };



  const rollCheck = (type, name, modifier, breakdownParts = null, scopeConfig = null, rollOptions = null) => {

    const isInstant = (char.settings || {}).instantAdvantageRoll !== false;

    const resolvedAdvantage = scopeConfig

      ? getResolvedCharacterAdvantageContext(

          char,

          {

            manualMode: sheetAdvantage,

            itemScopes: scopeConfig.itemScopes || [],
            supremeItemScopes: scopeConfig.supremeItemScopes || [],

            legacyScopes: scopeConfig.legacyScopes || [],

            runtimeScopes: scopeConfig.runtimeScopes || [],

          },

          diseases,

          systems,

          isDM,

        )

      : { mode: sheetAdvantage, sources: sheetAdvantage !== 'normal' ? ['Manual toggle'] : [] };

    const effectiveAdv = resolvedAdvantage.mode;
    const isSupremeAdvantage = effectiveAdv === 'supreme_advantage';
    const rollMode = isSupremeAdvantage ? 'advantage' : effectiveAdv;

    const advSources = effectiveAdv !== 'normal' ? resolvedAdvantage.sources : [];

    

    const buildBD = (rollVal, extraParts = []) => {

      if (!breakdownParts && extraParts.length === 0) return null;

      return [{ type: 'roll', value: rollVal, source: 'd20', isRoll: true }, ...(breakdownParts || []), ...extraParts];

    };



    const finalizeRoll = (rollVal, allDice = null, advMode = null, advSourceList = null, isSupreme = false) => {

      const saveDice = rollOptions?.saveContext ? resolveSaveBonusDiceRoll(rollOptions.saveContext) : null;

      const resolvedModifier = modifier + (saveDice?.bonusDiceTotal || 0);

      setLastRoll({

        type,

        name,

        roll: rollVal,

        modifier: resolvedModifier,

        total: rollVal + resolvedModifier,

        allDice: allDice && allDice.length > 1 ? allDice : null,

        advMode,
        isSupreme,

        advSources: advSourceList,

        breakdown: buildBD(rollVal, saveDice?.breakdownParts || []),

      });

    };

    

    if (rollMode !== 'normal' && !isInstant && !pendingSheetRoll) {

      const firstDie = Math.floor(Math.random() * 20) + 1;

      setPendingSheetRoll({
        type,
        name,
        firstDie,
        dice: [firstDie],
        requiredDice: isSupremeAdvantage ? 3 : 2,
        modifier,
        advMode: rollMode,
        isSupreme: isSupremeAdvantage,
        advSources,
        breakdownParts,
        scopeConfig,
        rollOptions,
        hasBonusDice: Boolean(rollOptions?.saveContext?.diceBonuses?.length),
      });

      setLastRoll(null);

      return;

    }

    

    if (rollMode !== 'normal' && !isInstant && pendingSheetRoll) {

      const nextDie = Math.floor(Math.random() * 20) + 1;

      const previousDice = Array.isArray(pendingSheetRoll.dice) && pendingSheetRoll.dice.length > 0
        ? pendingSheetRoll.dice
        : [pendingSheetRoll.firstDie];

      const allDice = [...previousDice, nextDie];

      const requiredDice = pendingSheetRoll.requiredDice || 2;

      if (allDice.length < requiredDice) {
        setPendingSheetRoll({
          ...pendingSheetRoll,
          dice: allDice,
          firstDie: allDice[0],
          advSources: pendingSheetRoll.advSources || advSources,
        });
        setLastRoll(null);
        return;
      }

      const pendingMode = pendingSheetRoll.advMode || rollMode;

      const roll = pendingMode === 'advantage' ? Math.max(...allDice) : Math.min(...allDice);

      setPendingSheetRoll(null);

      finalizeRoll(roll, allDice, pendingMode, pendingSheetRoll.advSources || advSources, Boolean(pendingSheetRoll.isSupreme));

      return;

    }

    

    const numDice = rollMode !== 'normal' ? (isSupremeAdvantage ? 3 : 2) : 1;

    const allDice = [];

    for (let d = 0; d < numDice; d++) allDice.push(Math.floor(Math.random() * 20) + 1);

    const roll = rollMode === 'advantage' ? Math.max(...allDice) : rollMode === 'disadvantage' ? Math.min(...allDice) : allDice[0];

    finalizeRoll(roll, allDice, rollMode !== 'normal' ? rollMode : null, effectiveAdv !== 'normal' ? advSources : null, isSupremeAdvantage);

  };

  

  const statsPanelStyle = (() => {

    const base = { ...S.statsPanel, transition: 'right 200ms ease-out, bottom 200ms ease-out' };

    if (showSheet && bugReportDrawerOpen) return { ...base, right: '472px', bottom: '70px' };

    if (showSheet) return { ...base, right: '16px', bottom: '70px' };

    return base; // closed button stays at S.statsPanel's default right:20 bottom:20

  })();

  return <div style={statsPanelStyle}>

    <button style={{ ...S.btn, ...S.btnSm }} onClick={() => setShowSheet(!showSheet)}>📜</button>

    {showSheet && <div style={S.statsPopup}>

      <div style={{ ...S.tabs, justifyContent: 'space-between' }}>

        <div style={{ display: 'flex', gap: '2px' }}>

          {['stats', 'skills', 'saves'].map(t => <button key={t} style={{ ...S.tab, ...(tab === t ? S.tabActive : {}), padding: '6px 12px', fontSize: '0.8rem' }} onClick={() => setTab(t)}>{t === 'stats' ? '📊 Stats' : t === 'skills' ? '🎯 Skills' : '🛡️ Saves'}</button>)}

        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>

          <button

            style={{ padding: '2px 7px', fontSize: '0.6rem', borderRadius: '3px', cursor: 'pointer', fontWeight: 'bold', background: sheetAdvantage === 'advantage' ? 'rgba(39,201,90,0.35)' : 'rgba(255,255,255,0.05)', border: sheetAdvantage === 'advantage' ? '1px solid #27c95a' : '1px solid rgba(255,255,255,0.1)', color: sheetAdvantage === 'advantage' ? '#6cff8a' : '#555' }}

            onClick={() => { setSheetAdvantage(sheetAdvantage === 'advantage' ? 'normal' : 'advantage'); setPendingSheetRoll(null); }}

          >Adv</button>

          <button

            style={{ padding: '2px 7px', fontSize: '0.6rem', borderRadius: '3px', cursor: 'pointer', fontWeight: 'bold', background: sheetAdvantage === 'disadvantage' ? 'rgba(231,76,60,0.35)' : 'rgba(255,255,255,0.05)', border: sheetAdvantage === 'disadvantage' ? '1px solid #e74c3c' : '1px solid rgba(255,255,255,0.1)', color: sheetAdvantage === 'disadvantage' ? '#ff6b6b' : '#555' }}

            onClick={() => { setSheetAdvantage(sheetAdvantage === 'disadvantage' ? 'normal' : 'disadvantage'); setPendingSheetRoll(null); }}

          >Dis</button>

        </div>

      </div>

      

      {/* Pending Roll — Two-click Mode */}

      {pendingSheetRoll && (

        <div style={{ 

          padding: '5px 10px', marginBottom: '6px',

          background: pendingSheetRoll.advMode === 'advantage' ? 'rgba(39,201,90,0.12)' : 'rgba(231,76,60,0.12)',

          border: '1px solid ' + (pendingSheetRoll.advMode === 'advantage' ? 'rgba(39,201,90,0.4)' : 'rgba(231,76,60,0.4)'),

          borderRadius: '4px', fontSize: '0.8rem',

        }}>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

            <span>

              <span style={{ color: '#c9a227', fontWeight: 'bold' }}>{pendingSheetRoll.name}</span>

              <Tooltip text={(pendingSheetRoll.advSources || []).length > 0 ? `From: ${pendingSheetRoll.advSources.join(', ')}` : (pendingSheetRoll.advMode === 'advantage' ? 'Advantage' : 'Disadvantage')}>

                <span style={{ fontSize: '0.6rem', marginLeft: '6px', padding: '1px 5px', borderRadius: '3px', background: pendingSheetRoll.advMode === 'advantage' ? 'rgba(39,201,90,0.25)' : 'rgba(231,76,60,0.25)', color: pendingSheetRoll.advMode === 'advantage' ? '#6cff8a' : '#ff6b6b', cursor: 'help' }}>{pendingSheetRoll.advMode === 'advantage' ? 'ADV' : 'DIS'}</span>

              </Tooltip>

            </span>

            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

              <span style={{ fontSize: '0.8rem', color: '#aaa' }}>{pendingSheetRoll.firstDie}</span>

              <span style={{ fontSize: '0.7rem', color: '#888' }}>

                {pendingSheetRoll.modifier >= 0 ? '+' : ''}{pendingSheetRoll.modifier}

                {pendingSheetRoll.hasBonusDice ? ' + dice' : ''}

              </span>

              <span style={{ fontSize: '0.7rem', color: '#666' }}>=</span>

              <span style={{ padding: '1px 8px', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.9rem', background: 'rgba(201,162,39,0.2)', border: '1px solid #c9a227', color: '#ffd700' }}>

                {pendingSheetRoll.firstDie + pendingSheetRoll.modifier}{pendingSheetRoll.hasBonusDice ? ' + dice' : ''}

              </span>

              <button

                style={{ padding: '3px 10px', fontSize: '0.7rem', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', background: 'rgba(155,89,182,0.3)', border: '1px solid #9b59b6', color: '#d4a5ff', animation: 'pulse 1.5s infinite' }}

                onClick={() => rollCheck(pendingSheetRoll.type, pendingSheetRoll.name, pendingSheetRoll.modifier, pendingSheetRoll.breakdownParts, pendingSheetRoll.scopeConfig, pendingSheetRoll.rollOptions)}

              >Roll Again</button>

              <button style={{ ...S.btn, ...S.btnXs, padding: '2px 6px', fontSize: '0.65rem', marginBottom: 0 }} onClick={() => setPendingSheetRoll(null)}>x</button>

            </span>

          </div>

        </div>

      )}

      

      {/* Roll Result Banner */}

      {lastRoll && (

        <div style={{ 

          padding: '6px 10px', 

          marginBottom: '8px',

          background: lastRoll.roll === 20 ? 'rgba(0,255,0,0.15)' : lastRoll.roll === 1 ? 'rgba(255,0,0,0.15)' : 'rgba(155,89,182,0.15)', 

          border: '1px solid ' + (lastRoll.roll === 20 ? '#00ff00' : lastRoll.roll === 1 ? '#ff0000' : '#9b59b6'),

          borderRadius: '4px',

          fontSize: '0.8rem',

        }}>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

            <span>

              <span style={{ color: '#c9a227', fontWeight: 'bold' }}>{lastRoll.name}</span>

              {lastRoll.advMode && <Tooltip text={(lastRoll.advSources || []).length > 0 ? `From: ${lastRoll.advSources.join(', ')}` : (lastRoll.advMode === 'advantage' ? 'Advantage' : 'Disadvantage')}><span style={{ fontSize: '0.6rem', marginLeft: '6px', padding: '1px 5px', borderRadius: '3px', background: lastRoll.advMode === 'advantage' ? 'rgba(39,201,90,0.25)' : 'rgba(231,76,60,0.25)', color: lastRoll.advMode === 'advantage' ? '#6cff8a' : '#ff6b6b', cursor: 'help' }}>{lastRoll.advMode === 'advantage' ? 'ADV' : 'DIS'}</span></Tooltip>}

              {lastRoll.roll === 20 && <span style={{ color: '#00ff00' }}> NAT 20!</span>}

              {lastRoll.roll === 1 && <span style={{ color: '#ff0000' }}> NAT 1!</span>}

            </span>

            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

              <span>

                <span style={{ color: lastRoll.roll === 20 ? '#00ff00' : lastRoll.roll === 1 ? '#ff0000' : '#e8d5b7' }}>{lastRoll.roll}</span>

                <span style={{ color: '#888' }}>{lastRoll.modifier >= 0 ? '+' : ''}{lastRoll.modifier} = </span>

                <span style={{ fontWeight: 'bold', color: lastRoll.roll === 20 ? '#00ff00' : lastRoll.roll === 1 ? '#ff0000' : '#ffd700', fontSize: '1rem' }}>{lastRoll.total}</span>

              </span>

              <button 

                style={{ ...S.btn, ...S.btnXs, padding: '2px 6px', fontSize: '0.65rem', marginBottom: 0 }} 

                onClick={() => setLastRoll(null)}

              >x</button>

            </span>

          </div>

          {lastRoll.allDice && lastRoll.allDice.length > 1 && (

            <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.78rem' }}>

              {lastRoll.allDice.map((d, di) => {

                const total = d + lastRoll.modifier;

                const isChosen = d === lastRoll.roll;

                return (

                  <span key={di} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', opacity: isChosen ? 1 : 0.65 }}>

                    <span style={{ color: isChosen ? '#e8d5b7' : '#999' }}>{d}</span>

                    <span style={{ color: '#666', fontSize: '0.7rem' }}>{lastRoll.modifier >= 0 ? '+' : ''}{lastRoll.modifier}</span>

                    <span style={{ color: '#666', fontSize: '0.7rem' }}>=</span>

                    <span style={{

                      padding: '1px 6px', borderRadius: '3px', fontWeight: 'bold',

                      background: isChosen ? 'rgba(201,162,39,0.2)' : 'transparent',

                      border: isChosen ? '1px solid #c9a227' : '1px solid transparent',

                      color: isChosen ? '#ffd700' : '#888',

                    }}>{total}</span>

                  </span>

                );

              })}

            </div>

          )}

          {/* Breakdown */}

          {lastRoll.breakdown && (

            <div style={{ marginTop: '6px', display: 'flex', alignItems: 'flex-end', gap: '4px', flexWrap: 'wrap', fontSize: '0.7rem' }}>

              {lastRoll.breakdown.map((part, idx) => {

                const colors = { roll: lastRoll.roll === 20 ? '#00ff00' : lastRoll.roll === 1 ? '#ff0000' : '#e8d5b7', stat: '#4a90d9', prof: '#ffd700', buff: '#27c95a', equip: '#d97429' };

                const color = colors[part.type] || '#888';

                return (

                  <React.Fragment key={idx}>

                    {idx > 0 && <span style={{ color: '#666', alignSelf: 'center', marginBottom: '6px' }}>+</span>}

                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }} title={part.details || part.source}>

                      <span style={{ padding: '2px 6px', background: `${color}20`, border: `1px solid ${color}50`, borderRadius: '3px', color, fontWeight: 'bold', fontSize: '0.8rem' }}>

                        {part.isRoll ? part.value : (part.value >= 0 ? `+${part.value}` : part.value)}

                      </span>

                      <span style={{ fontSize: '0.5rem', color, opacity: 0.9, marginTop: '1px', maxWidth: '60px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>{part.source}</span>

                    </div>

                  </React.Fragment>

                );

              })}

              <span style={{ color: '#666', alignSelf: 'center', marginBottom: '6px' }}>=</span>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

                <span style={{ padding: '2px 8px', background: 'rgba(201,162,39,0.25)', border: '2px solid #c9a227', borderRadius: '3px', color: '#ffd700', fontWeight: 'bold', fontSize: '0.9rem' }}>{lastRoll.total}</span>

                <span style={{ fontSize: '0.5rem', color: '#c9a227', opacity: 0.9, marginTop: '1px' }}>Total</span>

              </div>

            </div>

          )}

        </div>

      )}

      

      {tab === 'stats' && <div>

        <div style={{ textAlign: 'center', marginBottom: '8px' }}>

          <div style={{ fontSize: '0.8rem', color: '#c9a227' }}>Proficiency: +{profBonus}</div>

        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>

          {['str', 'dex', 'con', 'int', 'wis', 'cha'].map(stat => {

            const diff = effStats[stat] - base[stat];

            const mod = getAbilityMod(effStats[stat]);

            return <Tooltip key={stat} text={modifiers[stat] ? `Effects:\n${modifiers[stat].map(m => `${m.effect} (${m.source})`).join('\n')}` : 'No modifiers'}>

              <div style={{ ...S.statBox, position: 'relative' }}>

                <div style={{ fontSize: '0.7rem', color: '#c9a227', textTransform: 'uppercase' }}>{stat}</div>

                <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{mod >= 0 ? '+' : ''}{mod}</div>

                <div style={{ fontSize: '0.8rem', color: diff !== 0 ? (diff > 0 ? '#27c95a' : '#c92727') : '#e8d5b7' }}>{effStats[stat]}</div>

                <button 

                  style={{ 

                    position: 'absolute', top: '2px', right: '2px',

                    background: 'rgba(155,89,182,0.4)', border: '1px solid #9b59b6', 

                    borderRadius: '3px', color: '#d4a5ff', cursor: 'pointer',

                    padding: '1px 3px', fontSize: '0.5rem', lineHeight: 1,

                  }}

                  onClick={() => {

                    const bd = [{ type: 'stat', value: getAbilityMod(base[stat]), source: `Base ${stat.toUpperCase()}`, details: `Score: ${base[stat]}` }];

                    (modifiers[stat] || []).forEach(m => { const v = m.effect.match(/([+-]?\d+)/); if (v) bd.push({ type: 'buff', value: parseInt(v[1]), source: m.source, details: m.note || m.effect }); });

                    rollCheck('stat', stat.toUpperCase(), mod, bd, getStatCheckAdvantageConfig(stat));

                  }}

                  title={`Roll ${stat.toUpperCase()} check`}

                >🎲</button>

              </div>

            </Tooltip>;

          })}

        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', fontSize: '0.85rem' }}>

          <div style={{ textAlign: 'center' }}><div style={{ color: '#c9a227' }}>AC</div><div style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>{resolvedPopupArmorContext.totalArmorClass}</div></div>

          <div style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => {
            const initiativeContext = getSheetInitiativeContext();
            rollCheck('init', 'Initiative', initiativeContext.modifier, initiativeContext.breakdownParts, getInitiativeAdvantageConfig());
          }}>

            <div style={{ color: '#c9a227' }}>Initiative</div>

            <div style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>{getSheetInitiativeContext().modifier >= 0 ? '+' : ''}{getSheetInitiativeContext().modifier} <span style={{ fontSize: '0.6rem', color: '#9b59b6' }}>🎲</span></div>

          </div>

          <div style={{ textAlign: 'center' }}><div style={{ color: '#c9a227' }}>Speed</div><div style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>{char.speed || 30}ft</div></div>

        </div>

        <div style={{ marginTop: '12px', fontSize: '0.85rem' }}>

          <div style={{ color: '#c9a227', marginBottom: '4px' }}>Passive Perception: {getResolvedPopupPassiveContext('Perception').totalScore}</div>

          <div style={{ color: '#c9a227', marginBottom: '4px' }}>Passive Investigation: {getResolvedPopupPassiveContext('Investigation').totalScore}</div>

          <div style={{ color: '#c9a227' }}>Passive Insight: {getResolvedPopupPassiveContext('Insight').totalScore}</div>

        </div>

        

        {/* Resistances/Vulnerabilities/Immunities Display */}

        {((char.resistances || []).length > 0 || (char.vulnerabilities || []).length > 0 || (char.immunities || []).length > 0 || char.raidwideResist > 0) && (

          <div style={{ marginTop: '12px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.1)', fontSize: '0.75rem' }}>

            {(char.resistances || []).length > 0 && (

              <div style={{ marginBottom: '4px' }}>

                <span style={{ color: '#4a90d9' }}>🛡️ Res: </span>

                <span style={{ color: '#888' }}>

                  {(char.resistances || []).map(r => typeof r === 'string' ? r : `${r.type}(${r.percent}%)`).join(', ')}

                </span>

              </div>

            )}

            {(char.vulnerabilities || []).length > 0 && (

              <div style={{ marginBottom: '4px' }}>

                <span style={{ color: '#ff6666' }}>⚡ Vuln: </span>

                <span style={{ color: '#888' }}>

                  {(char.vulnerabilities || []).map(v => typeof v === 'string' ? v : `${v.type}(${v.percent}%)`).join(', ')}

                </span>

              </div>

            )}

            {(char.immunities || []).length > 0 && (

              <div style={{ marginBottom: '4px' }}>

                <span style={{ color: '#888' }}>🚫 Immune: </span>

                <span style={{ color: '#666' }}>{(char.immunities || []).join(', ')}</span>

              </div>

            )}

            {char.raidwideResist > 0 && (

              <div>

                <span style={{ color: '#ff00ff' }}>🌀 Raid: </span>

                <span style={{ color: '#888' }}>{char.raidwideResist}%</span>

              </div>

            )}

          </div>

        )}

      </div>}

      

      {tab === 'skills' && <div style={{ maxHeight: '400px', overflowY: 'auto' }}>

        {Object.entries(SKILLS_BY_STAT).filter(([_, skills]) => skills.length > 0).map(([stat, skills]) => (

            <div key={stat} style={{ marginBottom: '12px' }}>

              <div style={{ fontSize: '0.75rem', color: '#c9a227', textTransform: 'uppercase', marginBottom: '4px' }}>{stat}</div>

              {skills.map(skill => {

              const skillContext = getResolvedPopupSkillContext(skill);

              const bonus = skillContext.totalBonus;

              const isProficient = skillContext.isProficient;

              const isExpertise = skillContext.isExpertise;

              return <div key={skill} style={{ ...S.skillRow, background: isExpertise ? 'rgba(201,162,39,0.2)' : isProficient ? 'rgba(39,201,90,0.15)' : 'rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

                <span 

                  style={{ display: 'flex', alignItems: 'center', position: 'relative' }}

                  onMouseEnter={(e) => {

                    const tip = e.currentTarget.querySelector('.skill-tip');

                    if (tip) tip.style.display = 'block';

                  }}

                  onMouseLeave={(e) => {

                    const tip = e.currentTarget.querySelector('.skill-tip');

                    if (tip) tip.style.display = 'none';

                  }}

                >

                  <span style={{ width: '16px', marginRight: '6px', color: isExpertise ? '#ffd700' : isProficient ? '#27c95a' : '#555' }}>{isExpertise ? '◆' : isProficient ? '●' : '○'}</span>

                  <span style={{ fontSize: '0.85rem' }}>{skill}</span>

                  <span style={{ fontWeight: 'bold', color: bonus >= 0 ? '#7dff7d' : '#ff7d7d', marginLeft: '4px' }}>{bonus >= 0 ? '+' : ''}{bonus}</span>

                  <div 

                    className="skill-tip"

                    style={{

                      display: 'none',

                      position: 'absolute',

                      bottom: '100%',

                      left: '50%',

                      transform: 'translateX(-50%)',

                      background: 'rgba(20,15,10,0.98)',

                      border: '1px solid #c9a227',

                      borderRadius: '6px',

                      padding: '8px 12px',

                      fontSize: '0.75rem',

                      whiteSpace: 'pre-wrap',

                      zIndex: 10000,

                      marginBottom: '4px',

                      minWidth: '160px',

                      maxWidth: '250px',

                      textAlign: 'left',

                      boxShadow: '0 4px 12px rgba(0,0,0,0.4)',

                      color: '#e8d5b7',

                    }}

                  >

                    {getSkillTooltip(skill)}

                  </div>

                </span>

                <button 

                  style={{ 

                    background: 'rgba(155,89,182,0.3)', border: '1px solid #9b59b6', 

                    borderRadius: '3px', color: '#d4a5ff', cursor: 'pointer',

                    padding: '1px 3px', fontSize: '0.5rem',

                    width: '18px', height: '18px', flexShrink: 0,

                    display: 'flex', alignItems: 'center', justifyContent: 'center',

                  }}

                  onClick={() => {

                    const bd = getResolvedSkillBreakdownParts(skillContext);

                    rollCheck('skill', skill, bonus, bd, getSkillAdvantageConfig(skill, SKILL_MAP[skill]));

                  }}

                  title={`Roll ${skill}`}

                >🎲</button>

              </div>;

            })}

          </div>

        ))}

      </div>}

      

      {tab === 'saves' && <div>

        <div style={{ fontSize: '0.85rem', color: '#888', marginBottom: '12px' }}>Saving Throw Modifiers</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>

          {['str', 'dex', 'con', 'int', 'wis', 'cha'].map(stat => {

            const saveContext = getResolvedSheetSaveContext(stat);

            const bonus = saveContext.totalBonus;

            const isProficient = (char.savingThrowProficiencies || []).includes(stat);

            const bd = getResolvedSaveBreakdownParts(stat, saveContext);

            const tipLines = getResolvedSaveTooltipLines(saveContext);

            return <Tooltip key={stat} text={tipLines.join('\n')}>

              <div style={{ ...S.skillRow, background: isProficient ? 'rgba(39,201,90,0.15)' : 'rgba(0,0,0,0.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

              <span style={{ display: 'flex', alignItems: 'center' }}>

                <span style={{ width: '16px', marginRight: '6px', color: isProficient ? '#27c95a' : '#555' }}>{isProficient ? '●' : '○'}</span>

                <span style={{ fontSize: '0.85rem', textTransform: 'uppercase' }}>{stat}</span>

                <span style={{ fontWeight: 'bold', color: bonus >= 0 ? '#7dff7d' : '#ff7d7d', marginLeft: '4px' }}>{bonus >= 0 ? '+' : ''}{bonus}</span>

              </span>

              <button 

                style={{ 

                  background: 'rgba(155,89,182,0.3)', border: '1px solid #9b59b6', 

                  borderRadius: '3px', color: '#d4a5ff', cursor: 'pointer',

                  padding: '1px 3px', fontSize: '0.5rem',

                  width: '18px', height: '18px', flexShrink: 0,

                  display: 'flex', alignItems: 'center', justifyContent: 'center',

                }}

                onClick={() => rollCheck('save', `${stat.toUpperCase()} Save`, bonus, bd, getSaveAdvantageConfig(stat), { saveContext })}

                title={`Roll ${stat.toUpperCase()} save`}

              >🎲</button>

            </div>

            </Tooltip>;

          })}

        </div>

      </div>}

    </div>}

  </div>;

};



// Home Character Sheet Display with click-to-edit for everyone

export const HomeCharacterSheet = ({ char, isDM, onUpdate, diseases, systems, globalSettings }) => {

  // 2026-05-11 F2: mobile branching for touch-target + typography fixes.
  // Per real-device feedback, stat-box internals (label, score, dice
  // button) were rendering at sub-spec sizes (10-12px text, 16px dice
  // button). All mob ternaries below.
  const mob = useIsMobile();

  const [editingStat, setEditingStat] = useState(null);

  const [showArmorEdit, setShowArmorEdit] = useState(false);

  const [collapsed, setCollapsed] = useState(false);

  const [lastStatRoll, setLastStatRoll] = useState(null);

  const [statAdvantage, setStatAdvantage] = useState('normal');

  const [pendingStatRoll, setPendingStatRoll] = useState(null); // For two-click mode: { stat, firstDie, modifier, breakdown }

  const _eff = getEffectiveBuffs(char);

  const { stats: effStats, modifiers } = calculateStats(char.baseStats, _eff.buffList, _eff.debuffList, _eff.permBuffs, _eff.permDebuffs, char.diseaseList, diseases, _eff.buffSources, char.buffNotes, char.buffData, char.systemList, systems, isDM);

  const activeLegacyEffects = getActiveLegacyEffectEntries(char, _eff);

  const activeRuntimeEffects = char.activeEffects || [];

  const base = char.baseStats || DEFAULT_STATS;

  const profBonus = getProficiencyBonus(char.level || 1);

  const skillBuffs = calculateSkillBuffs(char, char.inventory);

  const acBuffs = calculateACBuffs(char, char.inventory);

  const equippedCombatBonuses = getEquippedCombatBonusesFromInventory(char.inventory);
  const homeInitiativeModifier = getAbilityMod(effStats.dex) + equippedCombatBonuses.reduce((sum, bonus) => (
    bonus?.type === 'passive' && bonus?.scope === 'initiative' ? sum + (Number(bonus.value) || 0) : sum
  ), 0);

  const getResolvedHomeSaveContext = (stat) => getResolvedSaveContext({

    stat,

    char,

    effectiveStats: effStats,

    equippedCombatBonuses,

    activeLegacyEffects,

    activeRuntimeEffects,

  });

  

  // Armor calculations

  const armorTypes = globalSettings?.armorTypes || DEFAULT_ARMOR_TYPES;

  const durSettings = globalSettings?.armorDurability || DEFAULT_ARMOR_DURABILITY;

  // 2026-04-27: Prefer custom equipment-array armor when set so the char sheet

  // reflects what the user equipped via Equipment & Durability. Falls back to the

  // legacy `armorType` keyed lookup when no custom armor is equipped.

  const equippedCustomArmor = (char.equipment || []).find(e => e?.id === char.equippedArmor && e?.type === 'armor') || null;

  const customArmorTypeData = equippedCustomArmor ? ARMOR_TYPES_DURA[equippedCustomArmor.subtype] : null;

  const currentArmor = equippedCustomArmor

    ? {

        name: equippedCustomArmor.name || customArmorTypeData?.name || 'Custom Armor',

        description: customArmorTypeData?.description || '',

        baseAC: customArmorTypeData?.baseAC ?? equippedCustomArmor.baseAC ?? 10,

        icon: customArmorTypeData?.icon || '🛡️',

      }

    : (armorTypes[char.armorType] || armorTypes.none);

  const armorDura = equippedCustomArmor

    ? (equippedCustomArmor.currentDura ?? equippedCustomArmor.maxDura ?? durSettings.maxDurability)

    : (char.armorDurability ?? durSettings.maxDurability);

  const armorStatus = getArmorStatus(armorDura, globalSettings);

  const armorEffects = getArmorEffects(char, globalSettings);

  const duraPercent = equippedCustomArmor

    ? ((equippedCustomArmor.currentDura ?? 0) / (equippedCustomArmor.maxDura || 1)) * 100

    : (armorDura / durSettings.maxDurability) * 100;

  const resolvedHomeArmorContext = getResolvedArmorClassContext({

    char,

    effectiveStats: effStats,

    equippedCombatBonuses,

    activeLegacyEffects,

    activeRuntimeEffects,

    armorTypes,

    customArmorTypes: ARMOR_TYPES_DURA,

  });



  const handleStatClick = (stat) => {

    setEditingStat(editingStat === stat ? null : stat);

  };



  const updateBaseStat = (stat, val) => {

    onUpdate({ baseStats: { ...(char.baseStats || DEFAULT_STATS), [stat]: parseInt(val) || 10 } });

  };

  

  const updateArmorType = (type) => {

    onUpdate({ armorType: type, armorDurability: durSettings.maxDurability });

  };

  

  const updateArmorDurability = (delta) => {

    const newDura = Math.max(0, Math.min(durSettings.maxDurability, armorDura + delta));

    onUpdate({ armorDurability: newDura });

  };

  

  const repairArmor = () => {

    onUpdate({ armorDurability: durSettings.maxDurability });

  };



  const rollStatCheck = (stat, e) => {

    e.stopPropagation();

    const baseMod = getAbilityMod(base[stat]);

    const effMod = getAbilityMod(effStats[stat]);

    const statModList = modifiers[stat] || [];

    

    const resolvedAdvantage = getResolvedCharacterAdvantageContext(char, {

      manualMode: statAdvantage,

      ...getStatCheckAdvantageConfig(stat),

    });

    const effectiveAdv = resolvedAdvantage.mode;

    const advSources = effectiveAdv !== 'normal' ? resolvedAdvantage.sources : [];

    

    // Build breakdown parts

    const buildBreakdown = (rollVal) => {

      const bd = [];

      bd.push({ type: 'roll', value: rollVal, source: 'd20', isRoll: true });

      bd.push({ type: 'stat', value: baseMod, source: 'Base ' + stat.toUpperCase(), details: 'Score: ' + base[stat] });

      statModList.forEach(m => {

        const buffMod = m.effect.match(/([+-]?\d+)/);

        if (buffMod) bd.push({ type: 'buff', value: parseInt(buffMod[1]), source: m.source, details: m.note || m.effect });

      });

      return bd;

    };

    

    const isInstant = (char.settings || {}).instantAdvantageRoll !== false;

    

    if (effectiveAdv !== 'normal' && !isInstant && !pendingStatRoll) {

      // Two-click mode: first roll

      const firstDie = Math.floor(Math.random() * 20) + 1;

      setPendingStatRoll({ stat: stat.toUpperCase(), firstDie, modifier: effMod, advMode: effectiveAdv, advSources, buildBreakdown });

      setLastStatRoll(null);

      return;

    }

    

    if (effectiveAdv !== 'normal' && !isInstant && pendingStatRoll) {

      // Two-click mode: second roll — resolve both

      const secondDie = Math.floor(Math.random() * 20) + 1;

      const allDice = [pendingStatRoll.firstDie, secondDie];

      const roll = effectiveAdv === 'advantage' ? Math.max(...allDice) : Math.min(...allDice);

      const total = roll + effMod;

      const breakdown = buildBreakdown(roll);

      setPendingStatRoll(null);

      setLastStatRoll({ stat: stat.toUpperCase(), roll, modifier: effMod, total, breakdown, allDice, advMode: effectiveAdv, advSources: pendingStatRoll.advSources || advSources });

      return;

    }

    

    // Instant mode or normal roll

    const numDice = effectiveAdv !== 'normal' ? 2 : 1;

    const allDice = [];

    for (let dd = 0; dd < numDice; dd++) allDice.push(Math.floor(Math.random() * 20) + 1);

    const roll = effectiveAdv === 'advantage' ? Math.max(...allDice) : effectiveAdv === 'disadvantage' ? Math.min(...allDice) : allDice[0];

    const total = roll + effMod;

    const breakdown = buildBreakdown(roll);

    

    setLastStatRoll({ stat: stat.toUpperCase(), roll, modifier: effMod, total, breakdown, allDice: allDice.length > 1 ? allDice : null, advMode: effectiveAdv !== 'normal' ? effectiveAdv : null, advSources: effectiveAdv !== 'normal' ? advSources : null });

  };



  const rollSaveCheck = (stat, e) => {

    e.stopPropagation();

    const saveContext = getResolvedHomeSaveContext(stat);

    const totalMod = saveContext.totalBonus;

    

    const buildBreakdown = (rollVal, extraParts = []) => {

      const bd = [{ type: 'roll', value: rollVal, source: 'd20', isRoll: true }];

      bd.push(...getResolvedSaveBreakdownParts(stat, saveContext));

      bd.push(...extraParts);

      return bd;

    };



    const finalizeSaveRoll = (rollVal, allDice = null, advMode = null, advSourceList = null, isSupreme = false) => {

      const saveDice = resolveSaveBonusDiceRoll(saveContext);

      const resolvedModifier = totalMod + saveDice.bonusDiceTotal;

      const breakdown = buildBreakdown(rollVal, saveDice.breakdownParts);

      setLastStatRoll({

        stat: `${stat.toUpperCase()} Save`,

        roll: rollVal,

        modifier: resolvedModifier,

        total: rollVal + resolvedModifier,

        breakdown,

        allDice: allDice && allDice.length > 1 ? allDice : null,

        advMode,

        isSupreme,

        advSources: advSourceList,

      });

    };

    

    const resolvedAdvantage = getResolvedCharacterAdvantageContext(char, {

      manualMode: statAdvantage,

      ...getSaveAdvantageConfig(stat),

    });

    const effectiveAdv = resolvedAdvantage.mode;

    const advSources = effectiveAdv !== 'normal' ? resolvedAdvantage.sources : [];

    const isSupremeAdvantage = effectiveAdv === 'supreme_advantage';

    const rollMode = isSupremeAdvantage ? 'advantage' : effectiveAdv;

    

    const isInstant = (char.settings || {}).instantAdvantageRoll !== false;

    

    if (rollMode !== 'normal' && !isInstant && !pendingStatRoll) {

      const firstDie = Math.floor(Math.random() * 20) + 1;

      setPendingStatRoll({

        stat: `${stat.toUpperCase()} Save`,

        saveStat: stat,

        rollKind: 'save',

        firstDie,

        dice: [firstDie],

        requiredDice: isSupremeAdvantage ? 3 : 2,

        modifier: totalMod,

        advMode: rollMode,

        advSources,

        isSupreme: isSupremeAdvantage,

        buildBreakdown,

        hasBonusDice: saveContext.diceBonuses.length > 0,

      });

      setLastStatRoll(null);

      return;

    }

    if (rollMode !== 'normal' && !isInstant && pendingStatRoll) {

      const nextDie = Math.floor(Math.random() * 20) + 1;

      const previousDice = Array.isArray(pendingStatRoll.dice) && pendingStatRoll.dice.length > 0

        ? pendingStatRoll.dice

        : [pendingStatRoll.firstDie];

      const allDice = [...previousDice, nextDie];

      const requiredDice = pendingStatRoll.requiredDice || 2;

      if (allDice.length < requiredDice) {

        setPendingStatRoll({

          ...pendingStatRoll,

          firstDie: allDice[0],

          dice: allDice,

        });

        return;

      }

      const pendingMode = pendingStatRoll.advMode === 'advantage' ? 'advantage' : 'disadvantage';

      const roll = pendingMode === 'advantage' ? Math.max(...allDice) : Math.min(...allDice);

      setPendingStatRoll(null);

      finalizeSaveRoll(roll, allDice, pendingMode, pendingStatRoll.advSources || advSources, Boolean(pendingStatRoll.isSupreme));

      return;

    }

    

    const numDice = rollMode !== 'normal' ? (isSupremeAdvantage ? 3 : 2) : 1;

    const allDice = [];

    for (let dd = 0; dd < numDice; dd++) allDice.push(Math.floor(Math.random() * 20) + 1);

    const roll = rollMode === 'advantage' ? Math.max(...allDice) : rollMode === 'disadvantage' ? Math.min(...allDice) : allDice[0];

    finalizeSaveRoll(roll, allDice, rollMode !== 'normal' ? rollMode : null, effectiveAdv !== 'normal' ? advSources : null, isSupremeAdvantage);

  };



  // Click outside to close editing

  const handleContainerClick = (e) => {

    if (editingStat && !e.target.closest('.stat-edit-area')) {

      setEditingStat(null);

    }

  };

  

  return <HoverCard style={{ maxWidth: '700px', margin: '20px auto' }} onClick={handleContainerClick}>

    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: collapsed ? 0 : '10px' }}>

      <h2 style={{ ...S.subtitle, margin: 0, border: 'none', paddingBottom: 0, fontSize: mob ? '1.125rem' : '1rem' }}>📋 Character Sheet <span style={{ fontSize: mob ? '0.8125rem' : '0.65rem', color: '#4a90d9' }}>{mob ? '(tap a stat to edit · hold for sources)' : '(click stat to edit · hover for sources)'}</span></h2>

      <div style={{ display: 'flex', alignItems: 'center', gap: mob ? '8px' : '4px' }}>

        {!collapsed && (

          <div style={{ display: 'inline-flex', gap: mob ? '8px' : '3px' }}>

            <button

              style={{ padding: mob ? '10px 14px' : '1px 6px', fontSize: mob ? '0.8125rem' : '0.58rem', borderRadius: '3px', cursor: 'pointer', fontWeight: 'bold', background: statAdvantage === 'advantage' ? 'rgba(39,201,90,0.35)' : 'rgba(255,255,255,0.05)', border: statAdvantage === 'advantage' ? '1px solid #27c95a' : '1px solid rgba(255,255,255,0.1)', color: statAdvantage === 'advantage' ? '#6cff8a' : '#555', minHeight: mob ? '40px' : undefined }}

              onClick={() => { setStatAdvantage(statAdvantage === 'advantage' ? 'normal' : 'advantage'); setPendingStatRoll(null); }}

            >Adv</button>

            <button

              style={{ padding: mob ? '10px 14px' : '1px 6px', fontSize: mob ? '0.8125rem' : '0.58rem', borderRadius: '3px', cursor: 'pointer', fontWeight: 'bold', background: statAdvantage === 'disadvantage' ? 'rgba(231,76,60,0.35)' : 'rgba(255,255,255,0.05)', border: statAdvantage === 'disadvantage' ? '1px solid #e74c3c' : '1px solid rgba(255,255,255,0.1)', color: statAdvantage === 'disadvantage' ? '#ff6b6b' : '#555', minHeight: mob ? '40px' : undefined }}

              onClick={() => { setStatAdvantage(statAdvantage === 'disadvantage' ? 'normal' : 'disadvantage'); setPendingStatRoll(null); }}

            >Dis</button>

          </div>

        )}

        <button style={{ ...S.btn, ...S.btnXs }} onClick={() => setCollapsed(!collapsed)}>{collapsed ? '▶' : '▼'}</button>

      </div>

    </div>

    

    {!collapsed && <>

      {/* Pending Roll — Two-click Advantage Mode */}

      {pendingStatRoll && (

        <div style={{ 

          marginBottom: '10px', padding: '6px 12px',

          background: pendingStatRoll.advMode === 'advantage' ? 'rgba(39,201,90,0.12)' : 'rgba(231,76,60,0.12)',

          border: '1px solid ' + (pendingStatRoll.advMode === 'advantage' ? 'rgba(39,201,90,0.4)' : 'rgba(231,76,60,0.4)'),

          borderRadius: '6px',

        }}>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

            <div>

              <span style={{ color: '#c9a227', fontWeight: 'bold', fontSize: '0.85rem' }}>{pendingStatRoll.stat} Check</span>

              <Tooltip text={(pendingStatRoll.advSources || []).length > 0 ? `From: ${pendingStatRoll.advSources.join(', ')}` : (pendingStatRoll.advMode === 'advantage' ? 'Advantage' : 'Disadvantage')}>

                <span style={{ fontSize: '0.6rem', marginLeft: '6px', padding: '1px 5px', borderRadius: '3px', background: pendingStatRoll.advMode === 'advantage' ? 'rgba(39,201,90,0.25)' : 'rgba(231,76,60,0.25)', color: pendingStatRoll.advMode === 'advantage' ? '#6cff8a' : '#ff6b6b', cursor: 'help' }}>{pendingStatRoll.advMode === 'advantage' ? 'ADV' : 'DIS'}</span>

              </Tooltip>

            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

              <span style={{ fontSize: '0.8rem', color: '#aaa' }}>{pendingStatRoll.firstDie}</span>

              <span style={{ fontSize: '0.7rem', color: '#888' }}>

                {pendingStatRoll.modifier >= 0 ? '+' : ''}{pendingStatRoll.modifier}

                {pendingStatRoll.hasBonusDice ? ' + dice' : ''}

              </span>

              <span style={{ fontSize: '0.7rem', color: '#666' }}>=</span>

              <span style={{ padding: '1px 8px', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.9rem', background: 'rgba(201,162,39,0.2)', border: '1px solid #c9a227', color: '#ffd700' }}>

                {pendingStatRoll.firstDie + pendingStatRoll.modifier}{pendingStatRoll.hasBonusDice ? ' + dice' : ''}

              </span>

              <button

                style={{ padding: '3px 10px', fontSize: '0.72rem', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', background: 'rgba(155,89,182,0.3)', border: '1px solid #9b59b6', color: '#d4a5ff', animation: 'pulse 1.5s infinite' }}

                onClick={(e) => {

                  if (pendingStatRoll.rollKind === 'save') {

                    rollSaveCheck(pendingStatRoll.saveStat, e);

                  } else {

                    rollStatCheck(pendingStatRoll.stat.toLowerCase(), e);

                  }

                }}

              >Roll Again</button>

              <button style={{ ...S.btn, ...S.btnXs, padding: '2px 6px', fontSize: '0.65rem', marginBottom: 0 }} onClick={() => setPendingStatRoll(null)}>x</button>

            </div>

          </div>

        </div>

      )}

      {/* Stat Roll Result Display */}

      {lastStatRoll && (

        <div style={{ 

          marginBottom: '10px', 

          padding: '8px 12px', 

          background: lastStatRoll.roll === 20 ? 'rgba(0,255,0,0.15)' : lastStatRoll.roll === 1 ? 'rgba(255,0,0,0.15)' : 'rgba(155,89,182,0.15)', 

          border: `1px solid ${lastStatRoll.roll === 20 ? '#00ff00' : lastStatRoll.roll === 1 ? '#ff0000' : '#9b59b6'}`,

          borderRadius: '6px',

        }}>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: lastStatRoll.breakdown ? '6px' : 0 }}>

            <div>

              <span style={{ color: '#c9a227', fontWeight: 'bold', fontSize: '0.85rem' }}>{lastStatRoll.stat} Check</span>

              {lastStatRoll.advMode && <Tooltip text={(lastStatRoll.advSources || []).length > 0 ? `From: ${lastStatRoll.advSources.join(', ')}` : (lastStatRoll.advMode === 'advantage' ? 'Advantage' : 'Disadvantage')}><span style={{ fontSize: '0.6rem', marginLeft: '6px', padding: '1px 5px', borderRadius: '3px', background: lastStatRoll.advMode === 'advantage' ? 'rgba(39,201,90,0.25)' : 'rgba(231,76,60,0.25)', color: lastStatRoll.advMode === 'advantage' ? '#6cff8a' : '#ff6b6b', cursor: 'help' }}>{lastStatRoll.advMode === 'advantage' ? 'ADV' : 'DIS'}</span></Tooltip>}

              {lastStatRoll.roll === 20 && <span style={{ color: '#00ff00', fontWeight: 'bold', marginLeft: '8px' }}>NAT 20!</span>}

              {lastStatRoll.roll === 1 && <span style={{ color: '#ff0000', fontWeight: 'bold', marginLeft: '8px' }}>NAT 1!</span>}

            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

              <span style={{ 

                fontSize: '1.3rem', 

                fontWeight: 'bold', 

                color: lastStatRoll.roll === 20 ? '#00ff00' : lastStatRoll.roll === 1 ? '#ff0000' : '#ffd700',

                textShadow: lastStatRoll.roll === 20 ? '0 0 8px rgba(0,255,0,0.5)' : lastStatRoll.roll === 1 ? '0 0 8px rgba(255,0,0,0.5)' : 'none',

              }}>

                {lastStatRoll.total}

              </span>

              <button 

                style={{ ...S.btn, ...S.btnXs, padding: '2px 6px', fontSize: '0.7rem', marginBottom: 0 }} 

                onClick={() => setLastStatRoll(null)}

              >✕</button>

            </div>

          </div>

          {/* All Dice Display for Advantage/Disadvantage */}

          {lastStatRoll.allDice && lastStatRoll.allDice.length > 1 && (

            <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.78rem' }}>

              {lastStatRoll.allDice.map((d, di) => {

                const total = d + lastStatRoll.modifier;

                const isChosen = d === lastStatRoll.roll;

                return (

                  <span key={di} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', opacity: isChosen ? 1 : 0.65 }}>

                    <span style={{ color: isChosen ? '#e8d5b7' : '#999' }}>{d}</span>

                    <span style={{ color: '#666', fontSize: '0.7rem' }}>{lastStatRoll.modifier >= 0 ? '+' : ''}{lastStatRoll.modifier}</span>

                    <span style={{ color: '#666', fontSize: '0.7rem' }}>=</span>

                    <span style={{

                      padding: '1px 6px', borderRadius: '3px', fontWeight: 'bold',

                      background: isChosen ? 'rgba(201,162,39,0.2)' : 'transparent',

                      border: isChosen ? '1px solid #c9a227' : '1px solid transparent',

                      color: isChosen ? '#ffd700' : '#888',

                    }}>{total}</span>

                  </span>

                );

              })}

            </div>

          )}

          {/* Breakdown */}

          {lastStatRoll.breakdown && (

            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', flexWrap: 'wrap', fontSize: '0.75rem' }}>

              {lastStatRoll.breakdown.map((part, idx) => {

                const colors = {

                  roll: lastStatRoll.roll === 20 ? '#00ff00' : lastStatRoll.roll === 1 ? '#ff0000' : '#e8d5b7',

                  stat: '#4a90d9',

                  prof: '#c9a227',

                  buff: '#27c95a',

                  equip: '#d97429',

                };

                const color = colors[part.type] || '#888';

                const tooltipText = part.details ? `${part.source}\n${part.details}` : part.source;

                

                return (

                  <React.Fragment key={idx}>

                    {idx > 0 && <span style={{ color: '#666', alignSelf: 'center', marginBottom: '8px' }}>+</span>}

                    <div 

                      style={{ 

                        display: 'flex',

                        flexDirection: 'column',

                        alignItems: 'center',

                        cursor: part.details ? 'help' : 'default',

                      }}

                      title={tooltipText}

                    >

                      <span 

                        style={{ 

                          padding: '3px 8px',

                          background: `${color}20`,

                          border: `1px solid ${color}50`,

                          borderRadius: '4px',

                          color,

                          fontWeight: 'bold',

                          fontSize: '0.9rem',

                        }}

                      >

                        {part.isRoll ? part.value : (part.value >= 0 ? `+${part.value}` : part.value)}

                      </span>

                      <span style={{ fontSize: '0.55rem', color: color, opacity: 0.9, marginTop: '2px' }}>{part.source}</span>

                    </div>

                  </React.Fragment>

                );

              })}

              <span style={{ color: '#666', alignSelf: 'center', marginBottom: '8px' }}>=</span>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

                <span style={{ padding: '3px 10px', background: 'rgba(201,162,39,0.25)', border: '2px solid #c9a227', borderRadius: '4px', color: '#ffd700', fontWeight: 'bold', fontSize: '1rem' }}>{lastStatRoll.total}</span>

                <span style={{ fontSize: '0.55rem', color: '#c9a227', opacity: 0.9, marginTop: '2px' }}>Total</span>

              </div>

            </div>

          )}

        </div>

      )}

    

      {/* Stats Row - Compact */}

      <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>

        {['str', 'dex', 'con', 'int', 'wis', 'cha'].map(stat => {

          const diff = effStats[stat] - base[stat];

          const mod = getAbilityMod(effStats[stat]);

          // Build detailed tooltip with sources

          const statModList = modifiers[stat] || [];

          // Get equipped item names to label sources correctly

          const equippedItemNames = new Set((char.inventory || []).filter(i => i.equipped).map(i => i.name));

          let tooltipText = `${stat.toUpperCase()} - Base ${base[stat]}`;

          if (statModList.length > 0) {

            tooltipText += '\n\nEffects:';

            statModList.forEach(m => {

              const note = equippedItemNames.has(m.source) ? 'Equipped' : m.note;

              tooltipText += `\n  ${m.effect} (${m.source}) - ${note}`;

            });

            tooltipText += `\n\nTotal: ${effStats[stat]}`;

          }

          tooltipText += '\n\n[Click to edit]';

          return <div key={stat} className="stat-edit-area" style={{ position: 'relative', zIndex: 1 }}

            onMouseEnter={(e) => e.currentTarget.style.zIndex = 10001}

            onMouseLeave={(e) => e.currentTarget.style.zIndex = 1}

          >

            <div

              style={{ ...S.statBox, minWidth: mob ? '92px' : '70px', padding: mob ? '8px 10px' : '6px 8px', cursor: 'pointer', position: 'relative' }}

              onClick={() => { handleStatClick(stat); if (!char.hasEditedStats) onUpdate({ hasEditedStats: true }); }}

              onMouseEnter={(e) => {

                const tip = e.currentTarget.querySelector('.stat-tooltip');

                if (tip) tip.style.display = 'block';

              }}

              onMouseLeave={(e) => {

                const tip = e.currentTarget.querySelector('.stat-tooltip');

                if (tip) tip.style.display = 'none';

              }}

            >

              <div style={{ fontSize: mob ? '0.8125rem' : '0.6rem', color: '#c9a227', textTransform: 'uppercase' }}>{stat.toUpperCase()}</div>

              <div style={{ fontSize: mob ? '1.5rem' : '1.2rem', fontWeight: 'bold', color: diff !== 0 ? (diff > 0 ? '#27c95a' : '#c92727') : '#ffd700' }}>{mod >= 0 ? '+' : ''}{mod}</div>

              <div style={{ fontSize: mob ? '0.8125rem' : '0.75rem', padding: '1px 6px', background: 'rgba(0,0,0,0.3)', borderRadius: '8px', display: 'inline-block' }}>{effStats[stat]}</div>

              <button

                style={{

                  position: 'absolute',

                  top: mob ? '4px' : '2px',

                  right: mob ? '4px' : '2px',

                  background: 'rgba(155,89,182,0.4)',

                  border: '1px solid #9b59b6',

                  borderRadius: mob ? '8px' : '3px',

                  color: '#d4a5ff',

                  cursor: 'pointer',

                  padding: mob ? '0' : '1px 4px',

                  fontSize: mob ? '1.25rem' : '0.55rem',

                  lineHeight: 1,

                  width: mob ? '40px' : undefined,

                  height: mob ? '40px' : undefined,

                  display: mob ? 'flex' : undefined,

                  alignItems: mob ? 'center' : undefined,

                  justifyContent: mob ? 'center' : undefined,

                }}

                onClick={(e) => rollStatCheck(stat, e)}

                aria-label={`Roll ${stat.toUpperCase()} check`}

                title={`Roll ${stat.toUpperCase()} check`}

              >

                🎲

              </button>

              {/* Stat Tooltip */}

              <div 

                className="stat-tooltip"

                style={{

                  display: 'none',

                  position: 'absolute',

                  bottom: '100%',

                  left: '50%',

                  transform: 'translateX(-50%)',

                  background: 'rgba(20,15,10,0.98)',

                  border: '1px solid #c9a227',

                  borderRadius: '6px',

                  padding: '8px 12px',

                  fontSize: '0.75rem',

                  whiteSpace: 'pre-wrap',

                  zIndex: 10000,

                  marginBottom: '4px',

                  minWidth: '150px',

                  maxWidth: '250px',

                  textAlign: 'left',

                  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',

                  color: '#e8d5b7',

                }}

              >

                {tooltipText}

              </div>

            </div>

            {editingStat === stat && <div style={{ marginTop: '4px', position: 'relative' }}>

              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>

                <input style={{ ...S.input, width: '50px', padding: '3px', marginBottom: 0, textAlign: 'center', fontSize: '0.8rem' }} type="number" value={base[stat]} onChange={e => updateBaseStat(stat, e.target.value)} autoFocus />

                <button style={{ ...S.btn, ...S.btnXs, marginBottom: 0, padding: '2px 5px', fontSize: '0.7rem' }} onClick={() => setEditingStat(null)}>✕</button>

              </div>

            </div>}

          </div>;

        })}

      </div>

      

      {/* Combat Stats Row - Compact */}

      <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginBottom: '12px', flexWrap: 'wrap' }}>

        <div style={{ textAlign: 'center' }}><div style={{ fontSize: '0.65rem', color: '#c9a227' }}>PROF</div><div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#ffd700' }}>+{profBonus}</div></div>

        <Tooltip text={(() => {

          const acContext = resolvedHomeArmorContext;

          let tip = `AC Breakdown\n\nBase (${acContext.currentArmor?.name || currentArmor.name}): ${acContext.baseArmorClass}`;

          if (acContext.hasOverride) {

            tip += `\nOverride: ${acContext.overrideArmorClass}`;

          } else if (acContext.dexModifier) {

            tip += `\nDEX: ${acContext.dexModifier >= 0 ? '+' : ''}${acContext.dexModifier}`;

          }

          const acSources = [...acContext.legacySources, ...acContext.runtimeSources];

          if (acSources.length > 0) {

            tip += '\n\nBonuses:';

            acSources.forEach(source => {

              tip += `\n${source.value >= 0 ? '+' : ''}${source.value} (${source.source})`;

            });

          }

          tip += `\n\nTotal: ${acContext.totalArmorClass}`;

          tip += '\n\n[Click to edit armor]';

          return tip;

        })()}>

          <div style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => setShowArmorEdit(!showArmorEdit)}>

            <div style={{ fontSize: '0.65rem', color: '#c9a227' }}>AC{(typeof char.armorClass === 'number' && char.armorClass !== 10) ? <span style={{ fontSize: '0.5rem', color: '#9b59b6' }}> ⊕</span> : null}</div>

            <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#ffd700' }}>{resolvedHomeArmorContext.totalArmorClass}</div>

          </div>

        </Tooltip>

        {(() => {

          // 2026-04-27: Wardsave display — only shown when char actually has WS active.

          const ws = calculateWardsave(char);

          if (ws.total <= 0 && ws.bestPos <= 0 && ws.neg <= 0) return null;

          const tipLines = ['Wardsave Breakdown'];

          if (ws.bestPos > 0) tipLines.push(`Best buff: +${ws.bestPos}% (${ws.bestPosSource || 'Manual'})`);

          if (ws.neg > 0) tipLines.push(`Debuffs: -${ws.neg}%`);

          tipLines.push(`Net: ${ws.total}% damage reduction`);

          return (

            <Tooltip text={tipLines.join('\n')}>

              <div style={{ textAlign: 'center' }}>

                <div style={{ fontSize: '0.65rem', color: '#c9a227' }}>WS</div>

                <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#ffd700' }}>{ws.total}%</div>

              </div>

            </Tooltip>

          );

        })()}

        <div style={{ textAlign: 'center' }}><div style={{ fontSize: '0.65rem', color: '#c9a227' }}>INIT</div><div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#ffd700' }}>{homeInitiativeModifier >= 0 ? '+' : ''}{homeInitiativeModifier}</div></div>

        {(() => {
          // 2026-05-10: Speed now shows the effective total (base +
          // buffs/debuffs from any chip matching +N speed/ms/movespeed)
          // and exposes a hover tooltip with per-source breakdown. Same
          // pattern as the AC tooltip below.
          const baseSpeed = char.speed || 30;
          const speedBuffs = calculateSpeedBuffs(char, char.inventory);
          const speedDelta = speedBuffs.reduce((sum, b) => sum + b.value, 0);
          const effSpeed = Math.max(0, baseSpeed + speedDelta);
          const tip = (
            <div style={{ minWidth: 180 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 4, color: '#ffd700' }}>Speed: {effSpeed}ft</div>
              <div style={{ fontSize: '0.7rem', color: '#aaa', marginBottom: 6 }}>Base {baseSpeed}ft{speedDelta !== 0 ? ` ${speedDelta >= 0 ? '+' : ''}${speedDelta}ft from sources` : ''}</div>
              {speedBuffs.length > 0 ? speedBuffs.map((b, i) => (
                <div key={i} style={{ fontSize: '0.72rem', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ color: b.value < 0 ? '#ff8a8a' : '#7dd87d' }}>{b.value >= 0 ? '+' : ''}{b.value}ft</span>
                  <span style={{ color: '#bbb' }}>{b.source}{b.note ? ` · ${b.note}` : ''}</span>
                </div>
              )) : <div style={{ fontSize: '0.7rem', color: '#888', fontStyle: 'italic' }}>No active speed modifiers</div>}
            </div>
          );
          return (
            <Tooltip text={tip}>
              <div style={{ textAlign: 'center', cursor: 'help' }}>
                <div style={{ fontSize: '0.65rem', color: '#c9a227' }}>SPEED</div>
                <div style={{ fontSize: '1rem', fontWeight: 'bold', color: speedDelta < 0 ? '#ff8a8a' : (speedDelta > 0 ? '#7dd87d' : '#ffd700') }}>{effSpeed}ft</div>
              </div>
            </Tooltip>
          );
        })()}

        <div style={{ textAlign: 'center' }}><span style={{ ...S.badge, ...S.badgeNeutral, fontSize: '0.7rem', padding: '3px 8px' }}>Lvl {char.level || 1} {char.charClass || 'Adventurer'}</span>

          {globalSettings?.xpTrackingEnabled !== false && globalSettings?.xpVisibleToPlayers !== false && (char.xp !== undefined && char.xp !== null) && (() => {

            const xp = getXpProgress(char, globalSettings);

            return (

              <div style={{ margin: '3px auto 0', maxWidth: '120px' }}>

                <div style={{ height: '4px', background: 'rgba(0,0,0,0.3)', borderRadius: '2px', overflow: 'hidden' }}>

                  <div style={{ height: '100%', width: `${xp.pct}%`, background: xp.isMax ? '#ffd700' : 'linear-gradient(90deg, #5b3a8c, #8b5fcf)', borderRadius: '2px', transition: 'width 0.3s' }} />

                </div>

                <div style={{ fontSize: '0.5rem', color: '#666', marginTop: '1px' }}>

                  {xp.currentXp.toLocaleString()} / {xp.isMax ? 'MAX' : xp.xpForNext.toLocaleString()} XP

                </div>

              </div>

            );

          })()}

        </div>

      </div>

      

      {/* Saving Throws — 2026-05-11 F2: mobile bumps. Save chips are
          combat-critical (DEX save under pressure), so 48px floor.
          Label font bumped from 0.55/0.5rem to 0.75rem on mobile. */}

      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: mob ? '8px' : '4px', marginBottom: '12px', flexWrap: 'wrap' }}>

        <span style={{ fontSize: mob ? '0.75rem' : '0.55rem', color: '#9b59b6', textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: '4px' }}>Saves</span>

        {['str', 'dex', 'con', 'int', 'wis', 'cha'].map(stat => {

          const saveContext = getResolvedHomeSaveContext(stat);

          const isProficient = (char.savingThrowProficiencies || []).includes(stat);

          const bonus = saveContext.totalBonus;

          const tipParts = getResolvedSaveTooltipLines(saveContext);

          tipParts.push('Click to roll');

          return (

            <Tooltip key={stat} text={tipParts.join('\n')}>

              <div

                onClick={(e) => rollSaveCheck(stat, e)}

                role="button"

                aria-label={`Roll ${stat.toUpperCase()} save`}

                style={{

                  textAlign: 'center', cursor: 'pointer',

                  padding: mob ? '8px 10px' : '2px 6px',

                  borderRadius: mob ? '6px' : '4px',

                  background: isProficient ? 'rgba(39,201,90,0.08)' : 'transparent',

                  border: `1px solid ${isProficient ? 'rgba(39,201,90,0.2)' : 'rgba(255,255,255,0.04)'}`,

                  minWidth: mob ? '52px' : '32px',

                  minHeight: mob ? '48px' : undefined,

                  display: mob ? 'flex' : undefined,

                  flexDirection: mob ? 'column' : undefined,

                  alignItems: mob ? 'center' : undefined,

                  justifyContent: mob ? 'center' : undefined,

                }}

              >

                <div style={{ fontSize: mob ? '0.75rem' : '0.5rem', color: isProficient ? '#27c95a' : '#666', textTransform: 'uppercase' }}>{stat}</div>

                <div style={{ fontSize: mob ? '0.9375rem' : '0.8rem', fontWeight: 'bold', color: isProficient ? '#7dff7d' : '#888' }}>

                  {bonus >= 0 ? '+' : ''}{bonus}

                </div>

              </div>

            </Tooltip>

          );

        })}

      </div>

    

      {/* Armor Section - Compact */}

      <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '6px', padding: '10px', marginBottom: '10px' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }} onClick={() => setShowArmorEdit(!showArmorEdit)}>

            <span style={{ fontSize: '1rem' }}>🛡️</span>

            <div>

              <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#c9a227' }}>

                {char.armorName || currentArmor.name}

              </div>

              <div style={{ fontSize: '0.6rem', opacity: 0.6 }}>{currentArmor.description}</div>

            </div>

          </div>

          {(globalSettings?.durabilityEnabled !== false) && (char.armorType !== 'none' || equippedCustomArmor) && (

            <div style={{ textAlign: 'right' }}>

              <div style={{ 

                fontSize: '0.7rem', 

                fontWeight: 'bold',

                color: armorStatus === 'good' ? '#27c95a' : armorStatus === 'damaged' ? '#c9a227' : '#c92727'

              }}>

                {armorStatus === 'good' ? 'Good' : armorStatus === 'damaged' ? '⚠️ Damaged' : '💔 Broken'}

              </div>

            </div>

          )}

        </div>

      

      {/* Durability Bar - only show if durability is enabled */}

      {(globalSettings?.durabilityEnabled !== false) && (char.armorType !== 'none' || equippedCustomArmor) && (

        <div style={{ marginBottom: '6px' }}>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>

            <span style={{ fontSize: '0.65rem', color: '#c9a227' }}>DURA</span>

            <span style={{ fontSize: '0.65rem' }}>{armorDura}/{equippedCustomArmor?.maxDura || durSettings.maxDurability}</span>

          </div>

          <div style={{ height: '8px', background: 'rgba(20,15,10,0.9)', borderRadius: '4px', overflow: 'hidden', border: '1px solid #5c4a1f' }}>

            <div style={{ 

              height: '100%', 

              width: `${duraPercent}%`, 

              background: armorStatus === 'good' ? 'linear-gradient(90deg, #27c95a, #1a8a3e)' : 

                         armorStatus === 'damaged' ? 'linear-gradient(90deg, #c9a227, #8b6914)' : 

                         'linear-gradient(90deg, #c92727, #8b1414)',

              transition: 'width 0.3s ease'

            }} />

          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', marginTop: '6px' }}>

            <button style={{ ...S.btn, ...S.btnXs, ...S.btnDanger, padding: '2px 6px', fontSize: '0.65rem' }} onClick={() => updateArmorDurability(-1)}>-1</button>

            <button style={{ ...S.btn, ...S.btnXs, ...S.btnDanger, padding: '2px 6px', fontSize: '0.65rem' }} onClick={() => updateArmorDurability(-5)}>-5</button>

            <button style={{ ...S.btn, ...S.btnXs, ...S.btnSuccess, padding: '2px 6px', fontSize: '0.65rem' }} onClick={() => updateArmorDurability(1)}>+1</button>

            <button style={{ ...S.btn, ...S.btnXs, ...S.btnSuccess, padding: '2px 6px', fontSize: '0.65rem' }} onClick={repairArmor}>🔧</button>

          </div>

        </div>

      )}

      

      {/* Armor Effects */}

      {(armorEffects.buffs.length > 0 || armorEffects.debuffs.length > 0) && (

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '6px' }}>

          {armorEffects.buffs.map((buff, i) => (

            <span key={`b-${i}`} style={{ ...S.badge, ...S.badgeBuff, fontSize: '0.6rem', padding: '1px 6px' }}>{buff}</span>

          ))}

          {armorEffects.debuffs.map((debuff, i) => (

            <span key={`d-${i}`} style={{ ...S.badge, ...S.badgeDebuff, fontSize: '0.6rem', padding: '1px 6px' }}>{debuff}</span>

          ))}

        </div>

      )}

      

      {/* Armor Type Selector */}

      {showArmorEdit && (

        <div style={{ marginTop: '8px', padding: '8px', background: 'rgba(0,0,0,0.3)', borderRadius: '4px', border: '1px solid rgba(201,162,39,0.3)' }}>

          <div style={{ fontSize: '0.7rem', color: '#c9a227', marginBottom: '6px' }}>Armor Type:</div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '4px' }}>

            {Object.entries(armorTypes).map(([key, armor]) => (

              <button 

                key={key}

                onClick={() => { onUpdate({ armorType: key }); }}

                style={{

                  ...S.btn, ...S.btnXs,

                  background: char.armorType === key ? 'rgba(201,162,39,0.4)' : 'rgba(0,0,0,0.3)',

                  border: char.armorType === key ? '2px solid #c9a227' : '1px solid rgba(255,255,255,0.1)',

                  textAlign: 'left',

                  padding: '6px',

                  fontSize: '0.75rem',

                }}

              >

                <div style={{ fontWeight: 'bold' }}>{armor.name}</div>

                <div style={{ fontSize: '0.55rem', opacity: 0.7 }}>Base AC: {armor.baseAC}</div>

              </button>

            ))}

          </div>

          <div style={{ marginTop: '8px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>

            <div>

              <label style={{ ...S.label, fontSize: '0.65rem' }}>Armor Name:</label>

              <input 

                style={{ ...S.input, marginBottom: 0, padding: '4px', fontSize: '0.8rem' }}

                placeholder="e.g., Mithral Chain Mail"

                value={char.armorName || ''}

                onChange={e => onUpdate({ armorName: e.target.value })}

              />

            </div>

            <div>

              <label style={{ ...S.label, fontSize: '0.65rem' }}>AC Override:</label>

              <input 

                style={{ ...S.input, marginBottom: 0, padding: '4px', fontSize: '0.8rem' }}

                type="number"

                placeholder={currentArmor.baseAC}

                value={char.armorClass || ''}

                onChange={e => onUpdate({ armorClass: e.target.value ? parseInt(e.target.value) : null })}

              />

            </div>

          </div>

          <div style={{ fontSize: '0.6rem', opacity: 0.5, marginTop: '4px' }}>Leave AC empty to use base armor AC ({currentArmor.baseAC})</div>

          <button style={{ ...S.btn, ...S.btnXs, marginTop: '8px' }} onClick={() => setShowArmorEdit(false)}>Done</button>

        </div>

      )}

    </div>

    </>}

  </HoverCard>;

};



// Inline HP/Mana bar with controls - HP centered, Long Rest far left

// `chars` and `journal` are threaded through for rule-engine worldContext

// (Systems expansion Session 1 — cross-cutting conditions like has-disease,

// quest-status, party-has-system need collection visibility at dispatch).

export const ResourceBar = ({ char, onUpdate, systems, manaEnabled = true, durabilityEnabled = true, chars = {}, journal = [], npcLibrary = {}, onSaveNpcLibrary = null, groupTravelLog = [], onSaveGroupTravelLog = null, messages = null, onSaveMessages = null, onSendStatusMessage = null }) => {

  // 2026-04-29: mobile redesign A.0.4 pilot. `mob` gates the 44×44 hit

  // area on the +/- adjusters; the desktop layout is pixel-identical

  // when mob is false.

  const mob = useIsMobile();

  const [hpChange, setHpChange] = useState('');
  const [hpChangeValue, setHpChangeValue] = useState(null);
  const [hpChangeInputKey, setHpChangeInputKey] = useState(0);

  const [manaChange, setManaChange] = useState('');
  const [manaChangeValue, setManaChangeValue] = useState(null);
  const [manaChangeInputKey, setManaChangeInputKey] = useState(0);

  const [isTempHp, setIsTempHp] = useState(false);

  // 2026-05-11 — Session 5 pilot: long-press the HP bar to open a
  // receipt sheet showing the value's decomposition. Pattern source
  // is MOBILE-REDESIGN.md §3a domain addendum: "every computed value
  // and every applied effect has a long-press = full receipt
  // affordance." This is the first wired site; ability scores,
  // saves, AC, and skills follow in A.2/A.3.
  const [hpReceiptOpen, setHpReceiptOpen] = useState(false);

  const [restNotifications, setRestNotifications] = useState([]);
  const [restResourceSaveQueue, setRestResourceSaveQueue] = useState([]);
  const activeRestResourceSaveEntry = restResourceSaveQueue[0] || null;
  const activeRestResourcePendingSave = activeRestResourceSaveEntry?.pendingSave || null;
  const activeRestResourceSaveChar = activeRestResourceSaveEntry?.context?.char || char;
  const restResourceSaveAbility = (activeRestResourcePendingSave?.ability || 'wis').toLowerCase();
  const activeRestResourceEffectContext = activeRestResourcePendingSave
    ? getResolvedCharacterEffectContext(activeRestResourceSaveChar, {}, systems, true)
    : null;
  const activeRestResourceSaveContext = activeRestResourcePendingSave
    ? getResolvedSaveContext({
        stat: restResourceSaveAbility,
        char: activeRestResourceSaveChar,
        effectiveStats: activeRestResourceEffectContext?.effectiveStats,
        equippedCombatBonuses: activeRestResourceEffectContext?.equippedCombatBonuses,
        activeLegacyEffects: activeRestResourceEffectContext?.activeLegacyEffects,
        activeRuntimeEffects: Array.isArray(activeRestResourceSaveChar?.activeEffects) ? activeRestResourceSaveChar.activeEffects : [],
      })
    : null;
  const activeRestResourceSaveAdvantage = activeRestResourcePendingSave
    ? getResolvedCharacterAdvantageContext(
        activeRestResourceSaveChar,
        { ...getSaveAdvantageConfig(restResourceSaveAbility) },
        {},
        systems,
        true,
      )
    : null;
  const buildRestWorldContext = () => ({ chars, journal, npcLibrary });

  const set = char.settings || DEFAULT_SETTINGS;

  const effectiveMaxHp = getEffectiveMaxHp(char);

  

  // Calculate max mana from spell slots if using mana system

  // 2026-04-27: Prefer explicit char.maxMana override even in mana mode so the "Max" input

  // on the char-edit panel is honored. Fall back to slot-derived calc when unset.

  const spellSlots = char.spellSlots || { 1: 2 };

  const calculatedMaxMana = calculateMaxMana(spellSlots);

  const explicitMax = (Number.isFinite(char.maxMana) && char.maxMana > 0) ? char.maxMana : null;

  const maxMana = isManaSpellSystem(set) ? (explicitMax || calculatedMaxMana) : (explicitMax || set.maxMana || 10);

  const spellResourceUi = getSpellResourceUiModel({

    char,

    systems,

    settings: set,

    surface: SPELL_BINDING_SURFACE_SHEET,

  });

  const showLegacyManaBar = manaEnabled && spellResourceUi.usesLegacyManaAsPrimary;

  const showSpellResourceSummaryRow = manaEnabled && spellResourceUi.usesSystemsAsPrimary && spellResourceUi.summary.displayKind !== 'meter';

  const effectiveLegacyMaxMana = spellResourceUi.summary.displayKind === 'meter'

    ? (spellResourceUi.summary.max ?? maxMana)

    : maxMana;

  const systemResourceRows = getDisplayableSystemResourceRows(char, systems);

  const customResourceTrackers = getCharacterCustomResourceTrackers(char);

  const legacyOnlyCustomResourceTrackers = getLegacyOnlyCharacterCustomResourceTrackers(char);

  const commitLegacyCustomResourceTrackerValue = (trackerId, nextValue) => {
    const newTrackers = [...customResourceTrackers];
    const trackerIdx = newTrackers.findIndex((tracker) => tracker.id === trackerId);
    if (trackerIdx < 0) return;

    newTrackers[trackerIdx] = { ...newTrackers[trackerIdx], value: nextValue };

    const syncedChar = syncCharacterCustomResources(
      { ...char, resourceTrackers: customResourceTrackers },
      newTrackers
    );

    onUpdate({ resourceTrackers: syncedChar.resourceTrackers, systemList: syncedChar.systemList });
  };

  

  const tempHp = char.tempHp || 0;

  const parsePositiveDeltaMagnitude = (draftValue) => {
    const parsedValue = parseInt(String(draftValue ?? '').trim(), 10);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
  };

  const clearHpChangeInput = () => {
    setHpChange('');
    setHpChangeValue(null);
    setHpChangeInputKey((currentKey) => currentKey + 1);
  };

  const clearManaChangeInput = () => {
    setManaChange('');
    setManaChangeValue(null);
    setManaChangeInputKey((currentKey) => currentKey + 1);
  };

  

  const modifyHp = (delta) => {

    // Default to 1 if no value entered

    let actualDelta = delta || (delta === 0 ? 0 : 1);

    

    // 2026-04-27: no-armor x3 multiplier now also recognizes custom equipped

    // armor (the new char.equippedArmor + equipment[] system) — previously it

    // only checked the legacy char.armorType, so a player who set up armor via

    // Equipment & Durability still ate triple damage from manual HP clicks.

    // Also: skip the multiplier for Temp HP edits — manual temp HP adjustments

    // are not "taking damage", and the user reported the x3 was hitting Temp HP

    // shield too. The multiplier still applies to manual HP damage edits and to

    // calculateFinalDamage (CombatPage path) which has its own no-armor handling.

    const hasCustomArmor = !!(char.equippedArmor && (char.equipment || []).some(e => e?.id === char.equippedArmor && e?.type === 'armor'));

    const hasNoArmor = !hasCustomArmor && (!char.armorType || char.armorType === 'none');

    if (durabilityEnabled !== false && actualDelta < 0 && hasNoArmor && !isTempHp) {

      const charClass = (char.charClass || '').toLowerCase();

      const isUnarmoredClass = charClass.includes('monk') || charClass.includes('barbarian');

      if (!isUnarmoredClass) {

        actualDelta = actualDelta * 3;

      }

    }

    

    let updates = {};

    

    if (isTempHp) {

      if (actualDelta > 0) {

        updates.tempHp = tempHp + actualDelta;

      } else {

        updates.tempHp = Math.max(0, tempHp + actualDelta);

      }

    } else {

      const newHp = Math.max(0, Math.min(effectiveMaxHp, char.currentHp + actualDelta));

      updates.currentHp = newHp;

      

      // Execute automation rules for damage/heal

      if (systems && (char.systemList || []).length > 0) {

        const trigger = actualDelta < 0 ? 'on-damage' : 'on-heal';
        const triggerContext = {
          amount: Math.abs(actualDelta),
          delta: actualDelta,
          previousHp: char.currentHp,
          currentHp: newHp,
        };

        const worldContext = { chars, journal, npcLibrary };

        const ruleResults = processAllSystemRulesForTrigger(trigger, { ...char, currentHp: newHp }, systems, triggerContext, worldContext);

        const ruleUpdates = applySystemRuleResults(char, ruleResults, systems, worldContext);

        updates = { ...updates, ...ruleUpdates };

        const charAfterHpRule = { ...char, ...updates };
        consumeRestResourceFollowUpResults(ruleResults, {
          charForEffects: charAfterHpRule,
          label: actualDelta < 0 ? 'HP damage rule' : 'HP heal rule',
          source: actualDelta < 0 ? 'resource-bar.hp-damage' : 'resource-bar.hp-heal',
          triggerContext,
          worldContext: {
            ...worldContext,
            chars: {
              ...(worldContext.chars || {}),
              ...(charAfterHpRule?.id ? { [charAfterHpRule.id]: charAfterHpRule } : {}),
            },
          },
          statusAuditAction: actualDelta < 0 ? 'resource-bar.hp-damage-status' : 'resource-bar.hp-heal-status',
        });

      }

    }

    

    onUpdate(updates);

    clearHpChangeInput();

  };

  

  const modifyMana = (delta) => {

    const actualDelta = delta || (delta === 0 ? 0 : 1);

    // Allow negative mana for mana drought mechanic

    const minMana = set.manaDroughtEnabled ? -effectiveLegacyMaxMana : 0;

    const newMana = Math.max(minMana, Math.min(effectiveLegacyMaxMana, (char.currentMana || 0) + actualDelta));

    

    const updates = { currentMana: newMana };

    

    // Check for mana drought

    if (newMana < 0 && set.manaDroughtEnabled) {

      updates.manaDrought = true;

    } else if (newMana >= 0) {

      updates.manaDrought = false;

    }

    

    onUpdate(updates);

    clearManaChangeInput();

  };

  

  // Reset uses for items and abilities based on rest type

  const resetUsesForRest = (inventory, restType) => {
    return resetLegacyInventoryUsesForRest(inventory, restType);

  };

  const consumeRestResourceFollowUpResults = (results, {
    charForEffects = char,
    label = 'Rest resource follow-up',
    source = 'rest.resource-follow-up',
    queuePendingSaves = true,
    triggerContext = null,
    worldContext = null,
    statusAuditAction = 'rest.resource-follow-up-status',
  } = {}) => {
    if (!results) return;
    const sideEffects = collectRuleResultSideEffects(results, { npcLibrary });
    if (sideEffects.npcLibraryChanged && typeof onSaveNpcLibrary === 'function') {
      onSaveNpcLibrary(sideEffects.nextNpcLibrary);
    }
    const notifications = [...sideEffects.notifications];
    const sentStatusMessages = persistSystemRuleStatusMessages(sideEffects.statusMessages, {
      char: charForEffects,
      source,
      messages,
      onSendStatusMessage,
      onSaveMessages,
      auditMeta: { category: 'systems', action: statusAuditAction },
    });
    const ruleLogEntries = persistSystemRuleTravelLogEntries(sideEffects.logEntries, {
      char: charForEffects,
      action: 'system-rule-log',
      source,
      groupTravelLog,
      onSaveGroupTravelLog,
    });
    const queuedPendingSaves = queuePendingSaves
      ? buildSystemRulePendingSaveQueueEntriesForCharacter(sideEffects.pendingSaves, {
          char: charForEffects,
          systems,
          worldContext: worldContext || buildRestWorldContext(),
          triggerContext,
          label,
          source,
        })
      : [];
    if (queuedPendingSaves.length > 0) {
      setRestResourceSaveQueue(prev => [...(prev || []), ...queuedPendingSaves]);
      const pendingSaveNotice = buildSystemRulePendingSaveNotice(sideEffects.pendingSaves, label);
      notifications.push(`${pendingSaveNotice || `${label} requested ${queuedPendingSaves.length} saving throw${queuedPendingSaves.length === 1 ? '' : 's'}.`} Queued for resolution.`);
    } else if (sideEffects.pendingSaves.length > 0) {
      notifications.push(buildSystemRulePendingSaveNotice(sideEffects.pendingSaves, label));
    }
    if (ruleLogEntries.length > 0) {
      notifications.push(`${ruleLogEntries.length} system rule log entr${ruleLogEntries.length === 1 ? 'y' : 'ies'} recorded.`);
    }
    if (sentStatusMessages.length > 0) {
      notifications.push(`${sentStatusMessages.length} system status message${sentStatusMessages.length === 1 ? '' : 's'} sent.`);
    }
    if (notifications.length > 0) {
      setRestNotifications(prev => [...(prev || []), ...notifications]);
      setTimeout(() => setRestNotifications([]), 5000);
    }
  };

  const handleRestResourceSaveResolve = (outcome) => {
    if (!activeRestResourceSaveEntry) return;
    const [, ...rest] = restResourceSaveQueue;
    const resolution = resolveSystemRulePendingSaveQueueEntry({
      entry: activeRestResourceSaveEntry,
      outcome,
      systems,
      fallbackChar: activeRestResourceSaveChar,
      fallbackWorldContext: buildRestWorldContext(),
    });
    if (resolution.subResults) {
      const patch = buildChangedCharacterFields(resolution.queuedChar, resolution.nextChar);
      if (Object.keys(patch).length > 0) {
        onUpdate(patch, {
          summary: `${activeRestResourceSaveEntry.label || 'Rest resource follow-up'} save ${outcome}: ${activeRestResourcePendingSave?.reason || activeRestResourceSaveChar?.name || 'system rule'}`,
          category: 'systems',
          action: `${activeRestResourceSaveEntry.source || 'rest.resource-follow-up'}.save.${outcome}`,
        });
      }
      consumeRestResourceFollowUpResults(resolution.subResults, {
        charForEffects: resolution.nextChar,
        label: `${activeRestResourceSaveEntry.label || 'Rest resource follow-up'} save`,
        source: `${activeRestResourceSaveEntry.source || 'rest.resource-follow-up'}.save`,
        queuePendingSaves: false,
      });
    }
    setRestResourceSaveQueue([...(resolution.nestedSaveEntries || []), ...rest]);
  };

  const handleRestResourceSaveSkip = () => {
    setRestResourceSaveQueue(([, ...rest]) => rest);
  };



  const applyDirectSpellResourceRecovery = (updates, restType) => {

    const nextChar = { ...char, ...updates };

    const recovery = resetSpellResourcesForRest({

      char: nextChar,

      systems,

      restType,

      settings: char.settings,

      surface: SPELL_BINDING_SURFACE_ANY,

      applyResourceFollowUps: (startingChar, resourceEvents) => applySpellResourceFollowUpsWithResults({

        startingChar,

        resourceEvents,

        systems,

        worldContext: buildRestWorldContext(),

      }),

    });

    if (recovery.nextChar.currentMana !== nextChar.currentMana) {

      updates.currentMana = recovery.nextChar.currentMana;

    }

    if (recovery.nextChar.usedSpellSlots !== nextChar.usedSpellSlots) {

      updates.usedSpellSlots = recovery.nextChar.usedSpellSlots;

    }

    if (recovery.nextChar.freeCastsUsedToday !== nextChar.freeCastsUsedToday) {

      updates.freeCastsUsedToday = recovery.nextChar.freeCastsUsedToday;

    }

    if (recovery.nextChar.manaSickness !== nextChar.manaSickness) {

      updates.manaSickness = recovery.nextChar.manaSickness;

    }

    if (recovery.nextChar.manaSicknessRounds !== nextChar.manaSicknessRounds) {

      updates.manaSicknessRounds = recovery.nextChar.manaSicknessRounds;

    }

    if (recovery.nextChar.manaDrought !== nextChar.manaDrought) {

      updates.manaDrought = recovery.nextChar.manaDrought;

    }

    if (recovery.nextChar.concentratingOn !== nextChar.concentratingOn) {

      updates.concentratingOn = recovery.nextChar.concentratingOn;

    }

    if (recovery.nextChar.concentrationEffectGroupId !== nextChar.concentrationEffectGroupId) {

      updates.concentrationEffectGroupId = recovery.nextChar.concentrationEffectGroupId;

    }

    if (recovery.nextChar.exhaustionLevel !== nextChar.exhaustionLevel) {

      updates.exhaustionLevel = recovery.nextChar.exhaustionLevel;

    }

    if (recovery.nextChar.systemList !== nextChar.systemList) {

      updates.systemList = recovery.nextChar.systemList;

    }

    if (recovery.nextChar.resourceTrackers !== nextChar.resourceTrackers) {

      updates.resourceTrackers = recovery.nextChar.resourceTrackers;

    }

    consumeRestResourceFollowUpResults(recovery.resourceFollowUpResults, {
      charForEffects: recovery.nextChar,
      label: 'Rest resource follow-up',
      source: 'rest.resource-follow-up',
    });

  };

  

  const shortRest = () => {

    const updates = {};
    const restWorldContext = { chars, journal, npcLibrary };
    const restRuleSideEffectBundles = [];

    // Reset inventory uses for short rest

    if (char.inventory?.length > 0) {

      updates.inventory = resetUsesForRest(char.inventory, 'short');

    }



    // Execute on-rest-short automation rules

    if (systems && (char.systemList || []).length > 0) {

      const ruleResults = processAllSystemRulesForTrigger('on-rest-short', char, systems, null, restWorldContext);

      const ruleUpdates = applySystemRuleResults(char, ruleResults, systems, restWorldContext);

      Object.assign(updates, ruleUpdates);
      restRuleSideEffectBundles.push({
        results: ruleResults,
        label: 'Short rest rule',
        source: 'rest.short',
        statusAuditAction: 'rest.short-status',
        triggerContext: { restType: 'shortRest' },
      });

    }



    // Clear technique cooldowns scoped to turn/round/encounter/short-rest.

    // Work from whatever systemList the rule-engine updates produced, not the raw one.

    const postRuleSystemList = updates.systemList || char.systemList;

    const clearedList = clearCooldownsAtScopes(postRuleSystemList, scopesClearedBy('short-rest'));

    if (clearedList !== postRuleSystemList) updates.systemList = clearedList;



    // Tick 'rests' expiries (Systems expansion Session 4). Both short and long rest

    // decrement the rests counter so expiresIn: { rests: 1 } expires on the next rest

    // of any kind.

    const listForExpiry = updates.systemList || char.systemList;

    const charBeforeExpiry = { ...char, ...updates, systemList: listForExpiry };

    const expiryResult = processSystemLifecycleExpiries({
      char: charBeforeExpiry,
      tickKind: 'rests',
      systems,
      worldContext: restWorldContext,
    });

    if (expiryResult.removedIds.length > 0) {

      Object.assign(updates, buildChangedCharacterFields(charBeforeExpiry, expiryResult.nextChar));

      setRestNotifications(prev => [...(prev || []), ...expiryResult.notifications]);

      setTimeout(() => setRestNotifications([]), 5000);

    } else if (expiryResult.nextChar.systemList !== listForExpiry) {

      updates.systemList = expiryResult.nextChar.systemList;

    }



    applyDirectSpellResourceRecovery(updates, 'shortRest');
    const charAfterShortRestRules = { ...char, ...updates };
    restRuleSideEffectBundles.forEach((bundle) => {
      consumeRestResourceFollowUpResults(bundle.results, {
        charForEffects: charAfterShortRestRules,
        label: bundle.label,
        source: bundle.source,
        triggerContext: bundle.triggerContext,
        worldContext: {
          ...restWorldContext,
          chars: {
            ...(restWorldContext.chars || {}),
            ...(charAfterShortRestRules?.id ? { [charAfterShortRestRules.id]: charAfterShortRestRules } : {}),
          },
        },
        statusAuditAction: bundle.statusAuditAction,
      });
    });



    onUpdate(updates);

  };

  

  const longRest = () => {

    const updates = { currentHp: effectiveMaxHp, tempHp: 0 };
    const restWorldContext = { chars, journal, npcLibrary };
    const restRuleSideEffectBundles = [];

    

    // Reset inventory uses for long rest (includes short rest resets)

    if (char.inventory?.length > 0) {

      updates.inventory = resetUsesForRest(char.inventory, 'long');

    }

    

    // Execute on-rest-long automation rules (and on-rest-short since long rest includes short rest benefits)

    if (systems && (char.systemList || []).length > 0) {

      // First process short rest rules

      const shortRuleResults = processAllSystemRulesForTrigger('on-rest-short', char, systems, null, restWorldContext);

      const shortUpdates = applySystemRuleResults(char, shortRuleResults, systems, restWorldContext);

      Object.assign(updates, shortUpdates);
      restRuleSideEffectBundles.push({
        results: shortRuleResults,
        label: 'Long rest short-rule',
        source: 'rest.long.short',
        statusAuditAction: 'rest.long-short-status',
        triggerContext: { restType: 'longRest', includedRestType: 'shortRest' },
      });



      // Then process long rest rules

      const longRuleResults = processAllSystemRulesForTrigger('on-rest-long', { ...char, ...updates }, systems, null, restWorldContext);

      const longUpdates = applySystemRuleResults({ ...char, ...updates }, longRuleResults, systems, restWorldContext);

      Object.assign(updates, longUpdates);
      restRuleSideEffectBundles.push({
        results: longRuleResults,
        label: 'Long rest rule',
        source: 'rest.long',
        statusAuditAction: 'rest.long-status',
        triggerContext: { restType: 'longRest' },
      });

    }



    // Clear technique cooldowns at every scope (long rest resets everything).

    const postRuleSystemList = updates.systemList || char.systemList;

    const clearedList = clearCooldownsAtScopes(postRuleSystemList, scopesClearedBy('long-rest'));

    if (clearedList !== postRuleSystemList) updates.systemList = clearedList;



    // Tick 'rests' expiries (Systems expansion Session 4). Long rest counts as a rest tick.

    const listForExpiry = updates.systemList || char.systemList;

    const charBeforeExpiry = { ...char, ...updates, systemList: listForExpiry };

    const expiryResult = processSystemLifecycleExpiries({
      char: charBeforeExpiry,
      tickKind: 'rests',
      systems,
      worldContext: restWorldContext,
    });

    if (expiryResult.removedIds.length > 0) {

      Object.assign(updates, buildChangedCharacterFields(charBeforeExpiry, expiryResult.nextChar));

      setRestNotifications(prev => [...(prev || []), ...expiryResult.notifications]);

      setTimeout(() => setRestNotifications([]), 5000);

    } else if (expiryResult.nextChar.systemList !== listForExpiry) {

      updates.systemList = expiryResult.nextChar.systemList;

    }



    applyDirectSpellResourceRecovery(updates, 'longRest');
    const charAfterLongRestRules = { ...char, ...updates };
    restRuleSideEffectBundles.forEach((bundle) => {
      consumeRestResourceFollowUpResults(bundle.results, {
        charForEffects: charAfterLongRestRules,
        label: bundle.label,
        source: bundle.source,
        triggerContext: bundle.triggerContext,
        worldContext: {
          ...restWorldContext,
          chars: {
            ...(restWorldContext.chars || {}),
            ...(charAfterLongRestRules?.id ? { [charAfterLongRestRules.id]: charAfterLongRestRules } : {}),
          },
        },
        statusAuditAction: bundle.statusAuditAction,
      });
    });



    onUpdate(updates);

  };



  // Calculate bar widths - extend bar if temp HP goes beyond max

  const totalHpWithTemp = char.currentHp + tempHp;

  const barMax = Math.max(effectiveMaxHp, totalHpWithTemp);

  const hpPercent = (char.currentHp / barMax) * 100;

  const tempHpPercent = tempHp > 0 ? (tempHp / barMax) * 100 : 0;

  const manaPercent = showLegacyManaBar && effectiveLegacyMaxMana > 0

    ? (((char.currentMana || 0) / effectiveLegacyMaxMana) * 100)

    : 0;

  

  // Hash pattern for temp HP using CSS

  const tempHpStyle = {

    height: '100%',

    width: `${tempHpPercent}%`,

    background: `repeating-linear-gradient(

      45deg,

      rgba(91, 192, 222, 0.7),

      rgba(91, 192, 222, 0.7) 4px,

      rgba(135, 206, 235, 0.9) 4px,

      rgba(135, 206, 235, 0.9) 8px

    )`,

    position: 'absolute',

    left: `${hpPercent}%`,

    boxShadow: 'inset 0 0 8px rgba(255,255,255,0.4)',

    border: '1px solid rgba(91, 192, 222, 0.8)',

    borderLeft: 'none'

  };

  const deltaInputStyle = {
    width: mob ? '44px' : '34px',
    minWidth: mob ? '44px' : '34px',
    height: mob ? '34px' : '28px',
    marginBottom: 0,
    padding: 0,
    borderRadius: '8px',
    border: '1px solid rgba(155,89,182,0.65)',
    background: 'rgba(12,6,18,0.88)',
    color: '#e8d5b7',
    fontSize: mob ? '0.9rem' : '0.78rem',
    fontWeight: 700,
    textAlign: 'center',
    boxShadow: 'inset 0 0 8px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.04)',
  };

  const deltaButtonBaseStyle = mob ? {
    ...S.btn,
    ...S.btnXs,
    width: '38px',
    minWidth: '38px',
    height: '34px',
    padding: 0,
    marginRight: 0,
    marginBottom: 0,
    lineHeight: '32px',
    fontWeight: 800,
  } : {
    ...S.btn,
    ...S.btnXs,
    marginRight: 0,
    marginBottom: 0,
  };



  // 2026-05-11 — Session 5: HP receipt modifiers. Pilot decomposition
  // shows base/effective/temp HP as separate rows. Deeper modifier
  // sources (item, buff, system contributions to max HP) wire in
  // A.2c when char.buffList parsing for HP-affecting modifiers
  // lands as a shared helper.
  const hpDelta = effectiveMaxHp - (Number.isFinite(char.maxHp) ? char.maxHp : 0);
  const hpReceiptModifiers = [
    { sourceType: 'base', source: 'Base Max HP', effect: Number.isFinite(char.maxHp) ? char.maxHp : effectiveMaxHp, note: 'Character sheet' },
    ...(hpDelta !== 0 ? [{ sourceType: 'buff', source: 'Active bonuses', effect: hpDelta, note: 'level / class / item modifiers' }] : []),
    ...(tempHp > 0 ? [{ sourceType: 'buff', source: 'Temporary HP', effect: tempHp, note: 'shield buff' }] : []),
  ];

  // Long-press the HP bar to open the receipt. 400 ms hold; haptic
  // confirms on Android the moment the threshold is hit.
  const hpLongPress = useLongPress(() => setHpReceiptOpen(true));

  return <div style={{ maxWidth: '580px', margin: '16px auto', width: '100%' }}>

    {activeRestResourcePendingSave && (
      <RequireSaveModal
        key={`${activeRestResourceSaveEntry?.id || 'rest-resource-save'}:${restResourceSaveQueue.length}`}
        pendingSave={activeRestResourcePendingSave}
        char={activeRestResourceSaveChar}
        saveContext={activeRestResourceSaveContext}
        saveAdvantage={activeRestResourceSaveAdvantage}
        onResolve={handleRestResourceSaveResolve}
        onSkip={handleRestResourceSaveSkip}
      />
    )}

    {/* Long-press receipt for HP (Session 5 pilot). Overlay; renders
        as bottom sheet on mobile, centered modal on desktop. */}
    <ReceiptSheet
      open={hpReceiptOpen}
      onClose={() => setHpReceiptOpen(false)}
      title="Max HP"
      value={effectiveMaxHp}
      unit="hp"
      modifiers={hpReceiptModifiers}
    />

    {/* Rest Notifications */}

    {restNotifications.length > 0 && (

      <div style={{ 

        marginBottom: '8px', 

        padding: '8px', 

        background: 'rgba(201,162,39,0.2)', 

        border: '1px solid rgba(201,162,39,0.4)', 

        borderRadius: '6px',

        fontSize: '0.85rem',

      }}>

        <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#c9a227' }}>⚙️ System Notifications:</div>

        {restNotifications.map((msg, i) => (

          <div key={i} style={{ marginLeft: '8px' }}>• {msg}</div>

        ))}

      </div>

    )}

    

    {/* HP Bar Row - Rest buttons far left, bar centered, controls right with Temp on far right */}

    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>

      <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>

        <Tooltip text="Reset ability uses (short rest resets)">

          <button style={{ ...S.btn, ...S.btnXs, marginRight: 0, marginBottom: 0, whiteSpace: 'nowrap', fontSize: '0.65rem' }} onClick={shortRest}>☀️ Short</button>

        </Tooltip>

        <Tooltip text="Restore HP/Mana to full, reset all ability uses">

          <button style={{ ...S.btn, ...S.btnSuccess, ...S.btnXs, marginRight: 0, marginBottom: 0, whiteSpace: 'nowrap', fontSize: '0.65rem' }} onClick={longRest}>🛏️ Long</button>

        </Tooltip>

      </div>

      <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>

        <div
          style={{ ...S.hpBar, width: '280px', position: 'relative', overflow: 'visible', ...hpLongPress.bind.style }}
          onTouchStart={hpLongPress.bind.onTouchStart}
          onTouchMove={hpLongPress.bind.onTouchMove}
          onTouchEnd={hpLongPress.bind.onTouchEnd}
          onTouchCancel={hpLongPress.bind.onTouchCancel}
          onMouseDown={hpLongPress.bind.onMouseDown}
          onMouseMove={hpLongPress.bind.onMouseMove}
          onMouseUp={hpLongPress.bind.onMouseUp}
          onMouseLeave={hpLongPress.bind.onMouseLeave}
          onClick={hpLongPress.bind.onClick}
          role="button"
          tabIndex={0}
          aria-label="HP — long-press for source breakdown"
        >

          {/* Regular HP */}

          <div style={{ height: '100%', width: `${hpPercent}%`, background: getHpColor(char.currentHp, effectiveMaxHp), position: 'absolute', left: 0, borderRadius: tempHp > 0 ? '14px 0 0 14px' : '14px' }} />

          {/* Temp HP - light blue with hash marks, extends beyond bar if needed */}

          {tempHp > 0 && <div style={tempHpStyle} />}

          <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontWeight: 'bold', textShadow: '1px 1px 2px #000', zIndex: 1 }}>

            ❤️ <TweenedNumber value={char.currentHp} />{tempHp > 0 && <span style={{ color: '#87ceeb' }}>+<TweenedNumber value={tempHp} /></span>}/{effectiveMaxHp}

          </span>

        </div>

      </div>

      {/* 2026-04-29 — A.0.4 pilot: ±HP buttons via AdjustButton. Long-press

          ramps 1→1→1→5→5→10; haptic on Android, visual press state on iOS.

          Typed value in hpChange overrides the ramp (every tick fires that

          magnitude). Mobile gets 44×44 hit area; desktop unchanged. */}

      <div style={{ display: 'flex', gap: mob ? '8px' : '6px', alignItems: 'center', flexShrink: 0 }}>

        <CompactNumberField
          key={hpChangeInputKey}
          aria-label="HP change amount"
          placeholder="1"
          value={hpChangeValue}
          onCommit={value => {
            setHpChangeValue(value);
            setHpChange(value === null ? '' : String(value));
          }}
          onChange={e => setHpChange(e.target.value)}
          emptyValue={null}
          min={0}
          style={deltaInputStyle}
        />

        <AdjustButton

          isMobile={mob}

          mobileSize={48}

          sign={1}

          baseAmount={parsePositiveDeltaMagnitude(hpChange)}

          feedbackType={isTempHp ? 'tap' : 'heal'}

          ariaLabel={isTempHp ? 'Add temp HP' : 'Heal HP'}

          onAdjust={(delta) => modifyHp(delta)}

          style={{ ...deltaButtonBaseStyle, ...(isTempHp ? { background: 'linear-gradient(180deg, #5bc0de 0%, #31b0d5 100%)', border: '1px solid #87ceeb' } : S.btnSuccess), borderRadius: '8px' }}

        >+</AdjustButton>

        <AdjustButton

          isMobile={mob}

          mobileSize={48}

          sign={-1}

          baseAmount={parsePositiveDeltaMagnitude(hpChange)}

          feedbackType="damage"

          ariaLabel={isTempHp ? 'Reduce temp HP' : 'Take damage'}

          onAdjust={(delta) => modifyHp(delta)}

          style={{ ...deltaButtonBaseStyle, ...S.btnDanger, borderRadius: '8px' }}

        >−</AdjustButton>

        <Tooltip text="Toggle to add/remove temporary HP">

          <label style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '0.75rem', color: isTempHp ? '#dff6ff' : '#87ceeb', marginLeft: '2px', width: mob ? '34px' : '28px', height: mob ? '34px' : '28px', borderRadius: '999px', border: `1px solid ${isTempHp ? '#87ceeb' : 'rgba(135,206,235,0.35)'}`, background: isTempHp ? 'rgba(91,192,222,0.28)' : 'rgba(12,6,18,0.72)', boxShadow: isTempHp ? '0 0 10px rgba(135,206,235,0.22)' : 'none', overflow: 'hidden' }}>

            <input type="checkbox" checked={isTempHp} onChange={e => setIsTempHp(e.target.checked)} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, margin: 0, padding: 0, cursor: 'pointer' }} />

            🛡️

          </label>

        </Tooltip>

      </div>

    </div>

    

    {/* Mana Bar Row - same layout but no Rest buttons */}

    {showLegacyManaBar && <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

      <div style={{ width: '100px' }}></div>

      <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>

        <div style={{ ...S.manaBar, width: '200px' }}>

          <div style={{ height: '100%', width: `${manaPercent}%`, background: getManaColor() }} />

          <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontWeight: 'bold', textShadow: '1px 1px 2px #000', fontSize: '0.8rem' }}>

            ✨ <TweenedNumber value={char.currentMana || 0} />/{effectiveLegacyMaxMana}

          </span>

        </div>

      </div>

      {/* 2026-04-29 — A.4 polish: mana ± buttons promoted to AdjustButton

          for parity with HP. Same long-press ramp, press state, haptic. */}

      <div style={{ display: 'flex', gap: mob ? '8px' : '4px', alignItems: 'center', flexShrink: 0 }}>

        <CompactNumberField
          key={manaChangeInputKey}
          aria-label="Mana change amount"
          placeholder="1"
          value={manaChangeValue}
          onCommit={value => {
            setManaChangeValue(value);
            setManaChange(value === null ? '' : String(value));
          }}
          onChange={e => setManaChange(e.target.value)}
          emptyValue={null}
          min={0}
          style={deltaInputStyle}
        />

        <AdjustButton

          isMobile={mob}

          mobileSize={48}

          sign={1}

          baseAmount={parsePositiveDeltaMagnitude(manaChange)}

          feedbackType="tap"

          ariaLabel="Add mana"

          onAdjust={(delta) => modifyMana(delta)}

          style={{ ...deltaButtonBaseStyle, ...S.btnMana, borderRadius: '8px' }}

        >+</AdjustButton>

        <AdjustButton

          isMobile={mob}

          mobileSize={48}

          sign={-1}

          baseAmount={parsePositiveDeltaMagnitude(manaChange)}

          feedbackType="tap"

          ariaLabel="Spend mana"

          onAdjust={(delta) => modifyMana(delta)}

          style={{ ...deltaButtonBaseStyle, ...S.btnDanger, borderRadius: '8px' }}

        >−</AdjustButton>

        <div style={{ width: '28px' }}></div>

      </div>

    </div>}



    {showSpellResourceSummaryRow && <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: showLegacyManaBar ? '8px' : 0 }}>

      <div style={{ width: '100px' }}></div>

      <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>

        <span style={{ fontSize: '0.78rem', color: '#888', padding: '4px 10px', background: 'rgba(255,255,255,0.05)', borderRadius: '6px' }}>

          {spellResourceUi.summary.label}: {spellResourceUi.summary.detailText || 'Systems-managed'}

        </span>

      </div>

      <div style={{ width: mob ? '140px' : '116px' }}></div>

    </div>}



    {systemResourceRows.map((row) => {

      const current = Number.isFinite(row.current) ? row.current : 0;

      const max = Number.isFinite(row.max) ? row.max : null;

      const isCounter = row.trackerType === 'counter' || row.trackerType === 'number';

      const percent = max ? Math.min(100, (current / max) * 100) : 0;



      return (

        <div key={row.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>

          <div style={{ width: '100px' }}></div>

          <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>

            {isCounter ? (

              <div style={{

                padding: '4px 16px',

                background: 'rgba(0,0,0,0.4)',

                border: `1px solid ${row.color}50`,

                borderRadius: '12px',

                minWidth: '140px',

                textAlign: 'center',

              }}>

                <span style={{ fontWeight: 'bold', fontSize: '0.85rem', color: row.color }}>

                  {row.icon} {row.label}: {current}{max ? `/${max}` : ''}

                </span>

                <div style={{ fontSize: '0.65rem', opacity: 0.65, marginTop: '2px' }}>

                  {row.systemName}

                </div>

              </div>

            ) : (

              <div style={{

                ...S.manaBar,

                width: '180px',

                background: 'rgba(0,0,0,0.4)',

                border: `1px solid ${row.color}50`,

              }}>

                <div style={{

                  height: '100%',

                  width: `${percent}%`,

                  background: `linear-gradient(180deg, ${row.color} 0%, ${row.color}88 100%)`,

                  borderRadius: '12px',

                }} />

                <span style={{ position: 'absolute', top: '42%', left: '50%', transform: 'translate(-50%,-50%)', fontWeight: 'bold', textShadow: '1px 1px 2px #000', fontSize: '0.75rem' }}>

                  {row.icon} {row.label}: {current}{max ? `/${max}` : ''}

                </span>

                <span style={{ position: 'absolute', top: '72%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: '0.62rem', opacity: 0.72, whiteSpace: 'nowrap' }}>

                  {row.systemName}

                </span>

              </div>

            )}

          </div>

          <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexShrink: 0, fontSize: '0.68rem', opacity: 0.72 }}>

            System

            <div style={{ width: '28px' }}></div>

          </div>

        </div>

      );

    })}

    

    {/* Custom Resource Trackers - show resources that have displayOnScreen enabled */}

    {legacyOnlyCustomResourceTrackers.filter(rt => rt.displayOnScreen).map((tracker, idx) => {

      const preset = RESOURCE_TRACKER_PRESETS.find(p => p.id === tracker.presetId);

      const icon = preset?.icon || '📊';

      const color = tracker.color || '#c9a227';

      const current = tracker.value || 0;

      const max = tracker.max;

      const min = Number.isFinite(tracker.min) ? tracker.min : 0;

      const step = Number.isFinite(tracker.step) ? tracker.step : 1;

      const isCounter = tracker.type === 'counter' || tracker.type === 'number';

      const allowDecimalInput = [current, min, max, step].some((numericValue) =>
        Number.isFinite(numericValue) && !Number.isInteger(numericValue)
      );

      const percent = max ? Math.min(100, (current / max) * 100) : 0;

      

      return (

        <div key={tracker.id || idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>

          <div style={{ width: '100px' }}></div>

          <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>

            {isCounter ? (

              // Counter display (just numbers)

              <div style={{ 

                padding: '4px 16px',

                background: 'rgba(0,0,0,0.4)',

                border: `1px solid ${color}50`,

                borderRadius: '12px',

                minWidth: '120px',

                textAlign: 'center',

              }}>

                <span style={{ fontWeight: 'bold', fontSize: '0.85rem', color }}>

                  {icon} {tracker.label}: {current}{max ? `/${max}` : ''}

                </span>

              </div>

            ) : (

              // Progress bar display

              <div style={{ 

                ...S.manaBar, 

                width: '180px',

                background: 'rgba(0,0,0,0.4)',

                border: `1px solid ${color}50`,

              }}>

                <div style={{ 

                  height: '100%', 

                  width: `${percent}%`, 

                  background: `linear-gradient(180deg, ${color} 0%, ${color}88 100%)`,

                  borderRadius: '12px',

                }} />

                <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontWeight: 'bold', textShadow: '1px 1px 2px #000', fontSize: '0.75rem' }}>

                  {icon} {tracker.label}: {current}{max ? `/${max}` : ''}

                </span>

              </div>

            )}

          </div>

          <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexShrink: 0 }}>

            <QuantityStepper
              value={current}
              onCommit={(nextValue) => commitLegacyCustomResourceTrackerValue(tracker.id, nextValue)}
              min={min}
              max={Number.isFinite(max) ? max : undefined}
              step={step}
              allowDecimal={allowDecimalInput}
              emptyValue={min}
              inputLabel={`${tracker.label} tracker value`}
              decrementLabel="-"
              incrementLabel="+"
              decrementAriaLabel={`Decrease ${tracker.label} tracker value`}
              incrementAriaLabel={`Increase ${tracker.label} tracker value`}
              style={{ gap: '4px' }}
              buttonStyle={{ ...S.btn, ...S.btnXs, marginRight: 0, marginBottom: 0 }}
              decrementButtonStyle={{ ...S.btnDanger }}
              incrementButtonStyle={{ background: `linear-gradient(180deg, ${color} 0%, ${color}88 100%)` }}
              inputStyle={{ width: '56px', minWidth: '56px', padding: '4px', fontSize: '0.75rem' }}
            />

            {false && (<button 

              style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, marginRight: 0, marginBottom: 0 }} 

              onClick={() => {

                const newTrackers = [...customResourceTrackers];

                const trackerIdx = newTrackers.findIndex(t => t.id === tracker.id);

                if (trackerIdx >= 0) {

                  newTrackers[trackerIdx] = { ...newTrackers[trackerIdx], value: Math.max(tracker.min || 0, (current - (tracker.step || 1))) };

                  const syncedChar = syncCharacterCustomResources({ ...char, resourceTrackers: customResourceTrackers }, newTrackers);

                  onUpdate({ resourceTrackers: syncedChar.resourceTrackers, systemList: syncedChar.systemList });

                }

              }}

            >−</button>

            )}

            <div style={{ width: '28px' }}></div>

          </div>

        </div>

      );

    })}

  </div>;

};



// ============== COMBAT SYSTEM ==============



// Create an enemy for combat

const createEnemy = (overrides = {}) => ({

  id: generateId(),

  name: 'Enemy',

  type: 'enemy',

  maxHp: 20,

  currentHp: 20,

  tempHp: 0,

  ac: 12,

  initiative: 20, // Default to 20 for enemies

  resistances: [], // { type: 'fire', percent: 50 }

  vulnerabilities: [], // { type: 'radiant', percent: 50 }

  immunities: [], // ['poison', 'necrotic']

  debuffs: [], // Damage-modifying debuffs { name, bonusDice, percentIncrease, flatBonus, etc }

  raidwideResist: 0, // % resistance to raid-wide damage

  isCurrentTurn: false,

  conditions: [],

  notes: '',

  // Attack Routines — portable for future enemy library

  // Each routine: { id, name, attacks: [{ id, name, count, attackType, toHitBonus, damageDice, damageType, description }] }

  attackRoutines: [],

  // Visibility settings (DM can toggle what players see)

  showHp: false,

  showAc: false,

  showResistances: false,

  showVulnerabilities: false,

  savingThrows: { str: 0, dex: 0, con: 0, int: 0, wis: 0, cha: 0 }, // For enemy saves

  ...overrides,

});



// Create a test enemy for testing combat

const createTestEnemy = () => createEnemy({

  name: 'Goblin Skirmisher',

  maxHp: 15,

  currentHp: 15,

  ac: 13,

  initiative: 14,

  resistances: [],

  vulnerabilities: [{ type: 'radiant', percent: 50 }],

  immunities: [],

  savingThrows: { str: -1, dex: 2, con: 0, int: 0, wis: -1, cha: -1 },

  notes: 'Test enemy - nimble but weak to radiant damage',

  showHp: true,

  showAc: true,

  attackRoutines: [{

    id: generateId(), name: 'Attack Routine', attacks: [

      { id: generateId(), name: 'Scimitar', count: 2, toHitBonus: 4, damageDice: '1d6+2', damageType: 'slashing' },

    ],

  }],

});



// Create a tougher test enemy

const createTestBoss = () => createEnemy({

  name: 'Orc Warchief',

  maxHp: 65,

  currentHp: 65,

  ac: 16,

  initiative: 12,

  resistances: [{ type: 'slashing', percent: 25 }],

  vulnerabilities: [],

  immunities: ['poison'],

  raidwideResist: 20,

  savingThrows: { str: 4, dex: 0, con: 3, int: -1, wis: 1, cha: 2 },

  notes: 'Boss enemy - high HP, resistant to physical damage',

  showHp: true,

  showAc: true,

  attackRoutines: [{

    id: generateId(), name: 'Attack Routine', attacks: [

      { id: generateId(), name: 'Greataxe', count: 2, toHitBonus: 6, damageDice: '1d12+4', damageType: 'slashing' },

      { id: generateId(), name: 'Headbutt', count: 1, toHitBonus: 6, damageDice: '1d4+4', damageType: 'bludgeoning' },

    ],

  }],

});



// Calculate damage after resistances/vulnerabilities (percentage-based)

// Calculate final damage with full breakdown of all modifiers

const calculateFinalDamage = (
  baseDamage,
  damageType,
  target,
  isRaidwide = false,
  attackerId = null,
  combat = null,
  effectSource = null,
  options = {},
) => {

  const debuffs = target.debuffs || []; // Debuffs applied to target that increase damage taken
  const skipTargetDebuffPercentReplay = options?.skipTargetDebuffPercentReplay === true;

  

  // Build breakdown array: { source, type, value, resultingDamage }

  const breakdown = [];

  let currentDamage = baseDamage;

  

  breakdown.push({ source: 'Base', type: 'base', value: baseDamage, resultingDamage: currentDamage });

  // Apply debuffs from spells (damage increase %)

  debuffs.forEach(debuff => {

    // Check if debuff applies to this damage type (or all types)

    if (debuff.damageTypes?.includes(damageType) || debuff.damageTypes?.includes('all') || !debuff.damageTypes) {

      // Check if debuff only applies from specific caster

      if (debuff.casterOnly && debuff.casterId !== attackerId) return;

      

      if (!skipTargetDebuffPercentReplay && debuff.percentIncrease) {

        const increase = Math.floor(currentDamage * (debuff.percentIncrease / 100));

        currentDamage += increase;

        breakdown.push({ 

          source: debuff.name || debuff.spellName, 

          type: 'debuff_percent', 

          value: `+${debuff.percentIncrease}%`, 

          rawValue: increase,

          resultingDamage: currentDamage 

        });

      }

    }

  });



  const mitigation = applyResolvedDamageMitigation({

    currentDamage,

    damageType,

    target,

    isRaidwide,

    activeLegacyEffects: getActiveLegacyEffectEntries(effectSource || target),

    immuneSourceLabel: `Immune to ${damageType}`,

    immuneValueFormatter: (damage) => -damage,

  });



  return {

    damage: mitigation.damage,

    modifier: mitigation.modifier,

    percent: mitigation.percent,

    breakdown: [...breakdown, ...mitigation.breakdown],

  };

  // 2026-04-27: Wardsave (WS) — flat % damage mitigator independent of damage type.

  // Parsed from buff/debuff strings like "+10% WS" or "+10% Wardsave". Highest WS

  // wins (Warhammer-style: ward saves don't stack), and net WS = (sum of buffs) -

  // (sum of debuffs). Applied last so it reduces post-resistance damage.

};



// Calculate full damage with mark bonuses and breakdown

const calculateFullDamageWithBreakdown = (baseDamage, damageType, target, attacker, combat = null, effectSource = null) => {

  const breakdown = [];

  let currentDamage = baseDamage;

  

  breakdown.push({ source: 'Spell/Attack', type: 'base', value: baseDamage, resultingDamage: currentDamage });

  

  // Check for marks from attacker on this target (Hunter's Mark, Hex, etc.)

  if (attacker?.markedTargets) {

    const mark = attacker.markedTargets.find(m => m.targetId === target.id);

    if (mark && mark.casterId === attacker.id) {

      const markRoll = rollDice(mark.bonusDice || '1d6');

      currentDamage += markRoll.total;

      breakdown.push({ 

        source: mark.spellName, 

        type: 'mark_dice', 

        value: `+${mark.bonusDice}`, 

        roll: markRoll,

        rawValue: markRoll.total,

        resultingDamage: currentDamage 

      });

    }

  }

  

  // Check for debuffs on target that apply from any attacker

  const debuffs = target.debuffs || [];

  debuffs.forEach(debuff => {

    if (debuff.damageTypes?.includes(damageType) || debuff.damageTypes?.includes('all') || !debuff.damageTypes) {

      // Skip if casterOnly and not from this caster

      if (debuff.casterOnly && debuff.casterId !== attacker?.id) return;

      

      if (debuff.bonusDice) {

        const debuffRoll = rollDice(debuff.bonusDice);

        currentDamage += debuffRoll.total;

        breakdown.push({ 

          source: debuff.name, 

          type: 'debuff_dice', 

          value: `+${debuff.bonusDice}`, 

          roll: debuffRoll,

          rawValue: debuffRoll.total,

          resultingDamage: currentDamage 

        });

      }

      if (debuff.percentIncrease) {

        const increase = Math.floor(currentDamage * (debuff.percentIncrease / 100));

        currentDamage += increase;

        breakdown.push({ 

          source: debuff.name, 

          type: 'debuff_percent', 

          value: `+${debuff.percentIncrease}%`, 

          rawValue: increase,

          resultingDamage: currentDamage 

        });

      }

    }

  });

  

  const mitigation = applyResolvedDamageMitigation({

    currentDamage,

    damageType,

    target,

    activeLegacyEffects: getActiveLegacyEffectEntries(effectSource || target),

  });

  

  return {

    finalDamage: mitigation.damage,

    breakdown: [...breakdown, ...mitigation.breakdown],

    modifier: mitigation.modifier,

  };

};



// Percentage Resistance/Vulnerability Editor Component

const ResistanceEditor = ({ label, icon, items, onChange, availableTypes }) => {

  const [inputValue, setInputValue] = useState('');

  

  const handleKeyDown = (e) => {

    if (e.key === 'Enter') {

      e.preventDefault();

      const typeName = inputValue.trim().toLowerCase();

      if (typeName && DAMAGE_TYPES[typeName] && !items.find(i => i.type === typeName)) {

        onChange([...items, { type: typeName, percent: 10 }]);

        setInputValue('');

      } else if (typeName && !DAMAGE_TYPES[typeName]) {

        // Allow custom types too

        if (!items.find(i => i.type === typeName)) {

          onChange([...items, { type: typeName, percent: 10 }]);

          setInputValue('');

        }

      }

    }

  };

  

  const updatePercent = (type, percent) => {

    onChange(items.map(i => i.type === type ? { ...i, percent: Math.max(0, Math.min(100, parseInt(percent) || 0)) } : i));

  };

  

  const removeItem = (type) => {

    onChange(items.filter(i => i.type !== type));

  };

  

  return (

    <div style={{ marginBottom: '12px' }}>

      <label style={{ ...S.label, fontSize: '0.8rem' }}>{icon} {label}</label>

      <input 

        style={{ ...S.input, marginBottom: '4px', fontSize: '0.85rem' }}

        value={inputValue}

        onChange={(e) => setInputValue(e.target.value)}

        onKeyDown={handleKeyDown}

        placeholder="Type name then press Enter"

      />

      {items.length > 0 && (

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>

          {items.map(item => (

            <div key={item.type} style={{ 

              display: 'flex', 

              alignItems: 'center', 

              gap: '4px', 

              padding: '2px 6px', 

              background: 'rgba(0,0,0,0.3)', 

              borderRadius: '4px',

              fontSize: '0.75rem',

              border: `1px solid ${DAMAGE_TYPES[item.type]?.color || '#888'}40`

            }}>

              <span>{DAMAGE_TYPES[item.type]?.icon || '⚡'} {item.type}</span>

              <input 

                type="number"

                value={item.percent}

                onChange={(e) => updatePercent(item.type, e.target.value)}

                style={{ width: '35px', padding: '1px 2px', background: 'rgba(0,0,0,0.3)', border: '1px solid #555', borderRadius: '2px', color: 'white', fontSize: '0.7rem', textAlign: 'center' }}

                min="0"

                max="100"

              />

              <span style={{ fontSize: '0.65rem' }}>%</span>

              <button onClick={() => removeItem(item.type)} style={{ background: 'none', border: 'none', color: '#c92727', cursor: 'pointer', fontSize: '0.7rem', padding: '0 2px' }}>✕</button>

            </div>

          ))}

        </div>

      )}

    </div>

  );

};



// Combat Page Component

export const CombatPage = ({ chars, currentCharId, isDM, combat, combatSnapshots: combatSnapshotsProp, onUpdateCombat: onUpdateCombatBase, onUpdateCombatSnapshots, onUpdateChars, onActivateCombatItemAbility = null, isCombatItemAbilityAuthoritySupported = null, onBack, diseases = {}, systems = {}, journal = [], npcLibrary = {}, onUpdateNpcLibrary = () => {}, globalSettings = {}, groupTravelLog = [], onSaveGroupTravelLog = null, messages = null, onSaveMessages = null, onSendStatusMessage = null }) => {

  // Snapshots are stored on their own top-level path (`gameData/combatSnapshots`) so that the

  // ~600KB array doesn't ride every combat write. Read from the prop; if a legacy snapshots

  // array exists on `combat` (older clients before the split) treat it as the source until the

  // migration effect below moves it. After migration, `combat.snapshots` is always undefined.

  const combatSnapshots = (Array.isArray(combatSnapshotsProp) && combatSnapshotsProp.length > 0)

    ? combatSnapshotsProp

    : (Array.isArray(combat?.snapshots) ? combat.snapshots : []);

  // One-shot migration: if combat carries a legacy snapshots array, lift it to the new path

  // and strip from combat. Idempotent (only fires when both conditions hold).

  useEffect(() => {

    if (Array.isArray(combat?.snapshots) && combat.snapshots.length > 0

        && (!Array.isArray(combatSnapshotsProp) || combatSnapshotsProp.length === 0)) {

      const lifted = combat.snapshots;

      const { snapshots: _drop, ...combatWithoutSnapshots } = combat;

      if (onUpdateCombatSnapshots) onUpdateCombatSnapshots(lifted);

      onUpdateCombatBase(combatWithoutSnapshots);

    }

    // We intentionally don't depend on combatSnapshotsProp/onUpdate* here — this should run

    // only when `combat` arrives with a legacy field; subsequent writes drop the field.

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [combat?.snapshots]);



  const [damageAmount, setDamageAmount] = useState('');

  const [damageType, setDamageType] = useState('slashing');

  const [selectedTargets, setSelectedTargets] = useState([]); // Array for multi-select

  const [showAddEnemy, setShowAddEnemy] = useState(false);

  const [newEnemy, setNewEnemy] = useState(createEnemy());

  const [isCrit, setIsCrit] = useState(false);

  const [showDamageLog, setShowDamageLog] = useState(false);

  const [attackSource, setAttackSource] = useState('dm'); // 'dm' or enemy id

  const [bypassReaction, setBypassReaction] = useState(false);

  const [expandedCombatant, setExpandedCombatant] = useState(null); // For showing resistance editor

  

  // DM "View As Player" — renders entire combat page from a player's perspective

  const [dmViewAsPlayerId, setDmViewAsPlayerId] = useState('');

  const [dmViewMode, setDmViewMode] = useState('dm'); // 'dm' | 'ally' | 'enemy'

  const [lastSelectedAllyId, setLastSelectedAllyId] = useState('');

  const [lastSelectedEnemyId, setLastSelectedEnemyId] = useState('');

  

  // Computed view context: when DM is viewing as a player, the page renders as that player sees it

  // isDM is KEPT for control permissions (advance turns, add enemies, edit combatants)

  // viewIsDM/viewCharId control what the page LOOKS like

  const viewIsDM = isDM && !dmViewAsPlayerId;

  const viewCharId = dmViewAsPlayerId || currentCharId;

  const viewIsPlayer = !!viewCharId && !viewIsDM;

  

  // Saving throw state

  const [showSaveRoll, setShowSaveRoll] = useState(false);

  const [saveType, setSaveType] = useState('dex');

  const [saveDC, setSaveDC] = useState(15);

  const [saveResults, setSaveResults] = useState([]);

  const [pendingSpellSaveContext, setPendingSpellSaveContext] = useState(null);

  const [combatResourceSaveQueue, setCombatResourceSaveQueue] = useState([]);
  const activeCombatResourceSaveEntry = combatResourceSaveQueue[0] || null;
  const activeCombatResourcePendingSave = activeCombatResourceSaveEntry?.pendingSave || null;
  const activeCombatResourceSaveChar = activeCombatResourceSaveEntry?.context?.char || (activeCombatResourceSaveEntry?.pendingSave?.characterId ? chars?.[activeCombatResourceSaveEntry.pendingSave.characterId] : null);
  const combatResourceSaveAbility = (activeCombatResourcePendingSave?.ability || 'wis').toLowerCase();
  const activeCombatResourceEffectContext = activeCombatResourcePendingSave && activeCombatResourceSaveChar
    ? getResolvedCharacterEffectContext(activeCombatResourceSaveChar, {}, systems, viewIsDM)
    : null;
  const activeCombatResourceSaveContext = activeCombatResourcePendingSave && activeCombatResourceSaveChar
    ? getResolvedSaveContext({
        stat: combatResourceSaveAbility,
        char: activeCombatResourceSaveChar,
        effectiveStats: activeCombatResourceEffectContext?.effectiveStats,
        equippedCombatBonuses: activeCombatResourceEffectContext?.equippedCombatBonuses,
        activeLegacyEffects: activeCombatResourceEffectContext?.activeLegacyEffects,
        activeRuntimeEffects: Array.isArray(activeCombatResourceSaveChar?.activeEffects) ? activeCombatResourceSaveChar.activeEffects : [],
      })
    : null;
  const activeCombatResourceSaveAdvantage = activeCombatResourcePendingSave && activeCombatResourceSaveChar
    ? getResolvedCharacterAdvantageContext(
        activeCombatResourceSaveChar,
        { ...getSaveAdvantageConfig(combatResourceSaveAbility) },
        {},
        systems,
        viewIsDM,
      )
    : null;

  

  // Combat spell UI state (for inline casting)

  const [combatSpellLevels, setCombatSpellLevels] = useState({}); // Track cast level per spell

  const [combatSpellTargets, setCombatSpellTargets] = useState({}); // Track selected target per spell

  const [combatSpellMultiTargets, setCombatSpellMultiTargets] = useState({}); // Track multi-select targets per spell { spellId: [targetId, ...] }

  const [combatSpellShowTargetPicker, setCombatSpellShowTargetPicker] = useState({}); // Which spell has target picker open

  const [combatSpellRolls, setCombatSpellRolls] = useState({}); // Track roll results per spell

  const [combatSpellManaCosts, setCombatSpellManaCosts] = useState({}); // Track custom mana cost per spell

  const [expandedSpells, setExpandedSpells] = useState({}); // Track which spells have details expanded

  

  // Player quick actions state

  const [playerDamageAmount, setPlayerDamageAmount] = useState('');

  const [playerDamageTarget, setPlayerDamageTarget] = useState('');

  const [playerDamageType, setPlayerDamageType] = useState('slashing');

  const [combatItemAbilityTargets, setCombatItemAbilityTargets] = useState({});
  const combatItemAbilityActivationInFlightRef = useRef(new Set());

  const [playerNotes, setPlayerNotes] = useState(''); // Active combat notes

  const [showQuickEffect, setShowQuickEffect] = useState(false); // Quick buff/debuff modal

  

  // Player Focus View — highlights effects relevant to the viewing player on combat cards

  const [playerFocusView, setPlayerFocusView] = useState(true); // Default ON for players

  const [cardViewFilter, setCardViewFilter] = useState('all'); // 'all'|'allies'|'enemies'|'players'|'pinned'|'my_effects'|'my_actions'

  const [collapsedSections, setCollapsedSections] = useState({ allies: false, enemies: false }); // independently collapse sections

  const [pinnedCombatantIds, setPinnedCombatantIds] = useState(new Set()); // persistent pinned IDs for 'pinned' filter

  const [collapsedCardIds, setCollapsedCardIds] = useState(new Set()); // individually minimized cards

  

  // Advantage/Disadvantage roll modifiers — per-combatant, DM or player can set

  const [combatantAdvantage, setCombatantAdvantage] = useState({}); // { [combatantId]: 'advantage'|'disadvantage' }

  const [supremeRoll, setSupremeRoll] = useState(false); // Roll 3 dice instead of 2

  

  // Comprehensive effect state

  const [effectName, setEffectName] = useState('');

  const [effectModifier, setEffectModifier] = useState(''); // +1d4, +10%, -5%, etc

  const [effectType, setEffectType] = useState('buff'); // 'buff' or 'debuff'

  const [effectTargets, setEffectTargets] = useState([]); // multiple targets

  const [effectAppliesTo, setEffectAppliesTo] = useState('any'); // 'any' or specific combatant id

  

  // Multi-Attack state

  const [attackEditCombatant, setAttackEditCombatant] = useState(null); // combatant id whose routines are being edited

  const [attackResults, setAttackResults] = useState(null); // { combatantId, routineName, results: [{ attackName, hitRoll, hitTotal, targetAC, isHit, isCrit, isMiss, damageRoll, damageTotal, damageType }], targetId }

  const [attackTargetId, setAttackTargetId] = useState(''); // selected target for attack execution

  const [effectDuration, setEffectDuration] = useState(''); // number of rounds or empty

  const [effectConcentration, setEffectConcentration] = useState(false);

  const [effectAutoApply, setEffectAutoApply] = useState(true);

  

  // DoT/HoT state

  const [effectDotDamage, setEffectDotDamage] = useState('');

  const [effectDotDamageType, setEffectDotDamageType] = useState('fire');

  const [effectHotHeal, setEffectHotHeal] = useState('');

  const [effectHotTempHp, setEffectHotTempHp] = useState('');

  const [effectTickTiming, setEffectTickTiming] = useState('start_of_target_turn');

  // Recurring save state

  const [effectSaveType, setEffectSaveType] = useState('');

  const [effectSaveDC, setEffectSaveDC] = useState('');

  const [effectSaveTiming, setEffectSaveTiming] = useState('end_of_target_turn');

  const [effectSaveRemoves, setEffectSaveRemoves] = useState(true);

  

  // Turn lifecycle state - queue-based confirmations

  const [turnNotifications, setTurnNotifications] = useState([]); // [{id, type, message, color}] - transient info

  const [lifecycleQueue, setLifecycleQueue] = useState([]); // [{id, type, title, message, actions:[{label,color,onClick}]}] - pending confirmations

  const [showConcentrationCheck, setShowConcentrationCheck] = useState(null);

  

  // Confirmation dialog state

  const [pendingConfirm, setPendingConfirm] = useState(null); // { title, message, details, actions: [{label,color,onClick}] }

  const [reactingToPendingId, setReactingToPendingId] = useState(null); // which pending damage is being reacted to

  const [reactionCustomAmount, setReactionCustomAmount] = useState(''); // custom reduction amount

  const [reactionMode, setReactionMode] = useState('points'); // 'points' or 'percent'

  

  // Effect management state  

  const [editingEffect, setEditingEffect] = useState(null); // { type:'debuff'|'buff'|'condition', combatantId, index, effect }

  const [moveEffectTarget, setMoveEffectTarget] = useState(''); // target id for moving effects

  

  // Death Save System

  const [deathSaveConfig, setDeathSaveConfig] = useState({

    successesNeeded: 3,

    failuresNeeded: 3,

    nat20Behavior: 'revive', // 'revive' | 'two_successes' | 'one_success'

    nat20ReviveHp: 1,

    nat1Behavior: 'two_failures', // 'two_failures' | 'one_failure'

    damageAtZeroFailures: 1,

    critAtZeroFailures: 2,

    reviveHpMode: 'heal_amount', // 'heal_amount' | 'fixed'

    fixedReviveHp: 15,

    autoApplyConditions: true,

    concentrationCheckOnDamage: true,

    hotTickTiming: 'per_spell',

    dotTickTiming: 'per_spell',

    healingCheck: {

      enabled: true,

      shieldName: "Death's Barrier",

      shieldHp: 10,

      maxChecks: 1,   // -1 = unlimited

      optional: false,

      checkReplacesDeathSave: true,

    },

  });

  const [showDeathSaveConfig, setShowDeathSaveConfig] = useState(false);

  const [deathSaveRollResult, setDeathSaveRollResult] = useState(null); // { combatantId, roll, resultType, message }

  const [healShieldAnim, setHealShieldAnim] = useState(null); // { combatantId, type: 'blocked'|'broken', amount }



  // --- Combat reversibility (history viewer + revert) ---

  // UI state for the history modal. Snapshots themselves live on the separate top-level

  // `combatSnapshots` path (see prop/wrapper above) so they don't bloat every combat write.

  // Cleared on End Combat / Start Combat (encounter-scoped, not campaign-scoped).

  const [showHistory, setShowHistory] = useState(false);

  const [historyTab, setHistoryTab] = useState('event'); // 'event' | 'turn' | 'round'

  const [selectedSnapshotId, setSelectedSnapshotId] = useState(null);

  const [revertTarget, setRevertTarget] = useState(null); // snapshot object pending confirmation
  const [revertError, setRevertError] = useState(null);
  const [revertingSnapshotId, setRevertingSnapshotId] = useState(null);

  

  // Combat notes

  const [combatNotes, setCombatNotes] = useState([]); // [{ id, text, timestamp }]

  const [combatNoteInput, setCombatNoteInput] = useState('');

  

  // DoT/HoT manual tick tracking - resets each turn

  const [tickedThisTurn, setTickedThisTurn] = useState({}); // { [runtimeTickKey]: true }

  

  // Active Effects Tracker panel

  const [showEffectsTracker, setShowEffectsTracker] = useState(false);

  const [effectsTrackerFilter, setEffectsTrackerFilter] = useState('all'); // 'all'|'buffs'|'debuffs'|'dots'|'concentration'

  const [dmViewAsId, setDmViewAsId] = useState(''); // DM can view effects from a player's perspective

  

  // Roll d20 with advantage/disadvantage support

  // Returns { selected, all[], mode, isSupreme }

  const rollD20WithAdvantage = (overrideMode = null, combatantId = null, scope = null) => {

    // Resolve mode: explicit override > per-combatant (with scope) > active turn > normal

    const scopedMode = combatantId ? getAdvantageForCombatant(combatantId, scope) : null;
    let mode = overrideMode || scopedMode || rollAdvantage;
    if (overrideMode === 'advantage' && scopedMode === 'supreme_advantage') {
      mode = 'supreme_advantage';
    }

    if (!mode || mode === 'normal') mode = 'normal';

    const isItemSupremeAdvantage = mode === 'supreme_advantage';
    const effectiveMode = isItemSupremeAdvantage ? 'advantage' : mode;
    const isSupreme = isItemSupremeAdvantage || supremeRoll;

    const numDice = (effectiveMode !== 'normal') ? (isSupreme ? 3 : 2) : 1;

    const all = [];

    for (let i = 0; i < numDice; i++) {

      all.push(Math.floor(Math.random() * 20) + 1);

    }

    let selected;

    if (effectiveMode === 'advantage') {

      selected = Math.max(...all);

    } else if (effectiveMode === 'disadvantage') {

      selected = Math.min(...all);

    } else {

      selected = all[0];

    }

    return { selected, all, mode: effectiveMode, isSupreme };

  };

  

  // Format a d20 advantage roll for display

  const formatAdvantageRoll = (roll) => {

    if (!roll || roll.mode === 'normal' || roll.all.length <= 1) return `${roll.selected}`;

    return roll.all.map(d => d === roll.selected ? `**${d}**` : `~~${d}~~`).join(', ');

  };

  

  // Parse effect modifier format (+1d4, +10%, -5%, -1d3, or "+1d4 attacks/saves")

  const parseEffectModifier = (mod) => {

    if (!mod) return null;

    const clean = mod.trim();

    

    // Check for percentage: +10%, -5%

    const percentMatch = clean.match(/^([+-]?)(\d+)%/);

    if (percentMatch) {

      const isNegative = percentMatch[1] === '-';

      const value = parseInt(percentMatch[2]);

      return {

        type: 'percent',

        value: isNegative ? -value : value,

        display: clean,

      };

    }

    

    // Check for dice: +1d4, -2d6, 1d8, or "+1d4 to attacks" (allow trailing text)

    const diceMatch = clean.match(/^([+-]?)(\d*)d(\d+)/i);

    if (diceMatch) {

      const isNegative = diceMatch[1] === '-';

      const count = parseInt(diceMatch[2]) || 1;

      const sides = parseInt(diceMatch[3]);

      return {

        type: 'dice',

        dice: `${count}d${sides}`,

        isNegative,

        display: clean,

      };

    }

    

    // Check for flat number: +5, -3, +2 AC (allow trailing text)

    const flatMatch = clean.match(/^([+-]?)(\d+)(?:\s|$)/);

    if (flatMatch) {

      const isNegative = flatMatch[1] === '-';

      const value = parseInt(flatMatch[2]);

      return {

        type: 'flat',

        value: isNegative ? -value : value,

        display: clean,

      };

    }

    

    return null; // Unparseable - treat as condition only

  };

  

  const currentChar = currentCharId ? chars[currentCharId] : null;

  const viewChar = viewCharId ? chars[viewCharId] : null; // Character whose perspective we're viewing

  

  // DM can select any player character for spell casting

  const [dmCombatCharId, setDmCombatCharId] = useState(null);

  // When DM is viewing as a player, auto-use that player for casting

  const effectiveDmCharId = dmViewAsPlayerId || dmCombatCharId;

  const dmCombatChar = isDM ? (effectiveDmCharId ? chars[effectiveDmCharId] : (Object.values(chars).length > 0 ? Object.values(chars)[0] : null)) : null;

  const activeSpellChar = isDM ? dmCombatChar : currentChar;

  // The character used for spell casting in combat (DM can pick any char)

  const castChar = activeSpellChar || currentChar;

  const viewCharSpellResourceUi = viewChar ? getSpellResourceUiModel({

    char: viewChar,

    systems,

    settings: syncSpellSettings(viewChar.settings || DEFAULT_SETTINGS),

    surface: SPELL_BINDING_SURFACE_COMBAT,

  }) : null;

  const castCharSpellResourceUi = castChar ? getSpellResourceUiModel({

    char: castChar,

    systems,

    settings: syncSpellSettings(castChar.settings || DEFAULT_SETTINGS),

    surface: SPELL_BINDING_SURFACE_COMBAT,

  }) : null;

  const castCharLegacyMaxMana = castCharSpellResourceUi?.summary.max ?? castChar?.maxMana ?? 10;



  const formatCombatSpellResourceReadout = (resourceUi) => formatSpellResourceReadout(resourceUi);



  const formatCombatSpellCostLabel = (info, spell) => {

    if (!info) return '';

    if (spell?.level === 0) return '∞';

    if (info.displayMode === 'system-tiered') {

      return `${info.slotsRemaining} ${info.resourceLabel || 'Slots'}`;

    }

    if (info.displayMode === 'legacy-slots') {

      return `${info.slotsRemaining} Slots`;

    }

    if (info.freeAvailable) return 'FREE';

    const suffix = info.displayMode === 'system-scalar'

      ? (info.resourceLabel || 'Resource')

      : 'MP';

    return info.overcast ? `${info.cost}⚡` : `${info.cost} ${suffix}`;

  };



  const isCombatSpellResourceBlocked = (info, spell) => {

    if (!info) return true;

    if (info.tooHigh) return true;

    if (info.kind !== 'legacy-mana') return !info.canCast && !isDM;

    if (spell?.level === 0 || info.freeAvailable || isDM) return false;

    if (info.cost <= (castChar?.currentMana || 0)) return false;

    if ((castChar?.manaDrought ?? false) && info.castLevel <= 3) return false;

    return true;

  };

  

  // Calculate spellcasting modifier for current character with breakdown

  const getSpellMod = (char) => {

    return getResolvedCombatSpellContext(char).spellModifier;

  };

  

  // Get detailed breakdown of spell modifier

  const getSpellModBreakdown = (char) => {

    const context = getResolvedCombatSpellContext(char);

    return {

      total: context.spellModifier,

      parts: [

        { name: context.spellStat.toUpperCase(), value: context.statMod },

        { name: 'Proficiency', value: context.proficiencyBonus },

      ],

    };

  };

  

  const getSpellAttackAdvantageScope = (spell = {}) => {

    const attackType = String(spell?.attackType || '').trim().toLowerCase();

    if (attackType === 'melee') return 'melee_attacks';

    if (attackType === 'ranged') return 'ranged_attacks';

    return 'attack';

  };

  const getAttackBonusContextForCombatant = (combatantId, scope = 'attack') => {

    const charData = chars?.[combatantId];

    if (!charData) return getResolvedAttackBonusContext({ scope });

    const effectContext = getResolvedCharacterEffectContext(charData, diseases, systems, isDM);

    return getResolvedAttackBonusContext({

      equippedCombatBonuses: effectContext?.equippedCombatBonuses,

      ...getCombatAdvantageScopeConfig(scope),

      scope,

    });

  };

  const getSpellSaveDC = (char) => getResolvedCombatSpellContext(char).spellSaveDC;

  const getSpellAttackBonus = (char, spell = null) => {

    const baseAttackBonus = getResolvedCombatSpellContext(char).spellAttackBonus;

    const scope = spell ? getSpellAttackAdvantageScope(spell) : 'attack';

    return baseAttackBonus + getAttackBonusContextForCombatant(char?.id, scope).totalBonus;

  };



  // Get all combatants (players + enemies) - normalize types

  const rawCombatants = combat?.combatants || [];

  // Also merge in legacy enemies array if present

  const legacyEnemies = combat?.enemies || [];

  const mergedCombatants = legacyEnemies.length > 0

    ? [...rawCombatants, ...legacyEnemies.filter(e => !rawCombatants.some(r => r.id === e.id))]

    : rawCombatants;

  // Normalize types: 'character' -> 'player', handle NPC

  const combatants = mergedCombatants.map(c => {

    let type = c.type;

    if (type === 'character') {

      // Check if this character is an NPC

      const char = chars[c.id];

      type = char?.isNPC ? 'npc' : 'player';

    }

    return type !== c.type ? { ...c, type } : c;

  });

  const damageLog = combat?.damageLog || [];

  const pendingDamage = combat?.pendingDamage || []; // { id, targetId, amount, type, source, timestamp, isRaidwide }

  

  // Death save configuration (campaign-level, stored on combat object)

  

  // Sort combatants by initiative (null initiatives at the end)

  // Initiative sort. D&D-5e: ties break on Dex mod, then DM decision. We approximate: higher

  // initiative first; on a tie, higher Dex mod; final fallback to stable id ordering so the

  // turn order doesn't flicker between renders. Without the id fallback, JS Array.sort is

  // unstable and the DM could see a ties-shuffled order after any state write.

  const getCombatantDexMod = (c) => {

    const score = chars?.[c.id]?.baseStats?.dex;

    if (typeof score === 'number') return Math.floor((score - 10) / 2);

    // Enemy combatants carry savingThrows.dex (a total mod incl. proficiency). It's the

    // best available proxy; we accept it even though it overstates the ability mod slightly.

    return c.savingThrows?.dex ?? 0;

  };

  const sortedCombatants = [...combatants].sort((a, b) => {

    const aNull = a.initiative === null;

    const bNull = b.initiative === null;

    if (aNull && bNull) return (a.id || '').localeCompare(b.id || '');

    if (aNull) return 1;

    if (bNull) return -1;

    const initDiff = (b.initiative || 0) - (a.initiative || 0);

    if (initDiff !== 0) return initDiff;

    const dexDiff = getCombatantDexMod(b) - getCombatantDexMod(a);

    if (dexDiff !== 0) return dexDiff;

    return (a.id || '').localeCompare(b.id || '');

  });

  

  const playerCombatants = combatants.filter(c => c.type === 'player' || c.type === 'npc');

  const enemyCombatants = combatants.filter(c => c.type === 'enemy');

  

  // Check if all initiatives are set

  const allInitiativesSet = combatants.every(c => c.initiative !== null);



  // --- Combat reversibility engine ---

  // Snapshot strategy: on each call to onUpdateCombat, diff current vs incoming state; if the

  // change is significant (round/turn advance, HP delta, damage log entry, combatant add/remove,

  // death-save change), capture the PRE-change state as a snapshot. Cap at 30 to bound the

  // combat object size — each snapshot is ~10-30KB depending on combatant count. Reverting

  // restores that snapshot's state and drops all later snapshots.

  // Tactical cap reduction (2026-04-19): Convex has a hard 1 MiB per-document

  // limit. At 30 snapshots × ~30KB each we were routinely exceeding it for

  // mid-combat saves. 10 snapshots keeps blob size well under the limit while

  // preserving enough history for meaningful revert UX. Proper fix is the

  // row-per-snapshot refactor (noted in CLAUDE-NOTES-CRITICAL.md §7).

  const SNAPSHOT_CAP = 10;



  const describeCombatChange = (before, after) => {

    if (!before || !after) return null;

    if ((before.active || false) !== (after.active || false)) {

      return after.active ? { action: 'combat_start', summary: 'Combat started' } : { action: 'combat_end', summary: 'Combat ended' };

    }

    if (before.round !== after.round) {

      return { action: 'round_advance', summary: `Round advanced: ${before.round || 0} → ${after.round || 0}` };

    }

    if (before.turnIndex !== after.turnIndex) {

      const sortedAfter = [...(after.combatants || [])].sort((a, b) => (b.initiative || 0) - (a.initiative || 0));

      const actor = sortedAfter[after.turnIndex]?.name || 'next combatant';

      return { action: 'turn_advance', summary: `Turn → ${actor}` };

    }

    const beforeLog = before.damageLog || [];

    const afterLog = after.damageLog || [];

    if (afterLog.length > beforeLog.length) {

      const entry = afterLog[0];

      const kind = (entry.amount || 0) < 0 ? 'Healing' : 'Damage';

      const tempBit = entry.tempHpAbsorbed ? ` (${entry.tempHpAbsorbed} temp HP absorbed)` : '';

      return { action: (entry.amount || 0) < 0 ? 'heal' : 'damage', summary: `${entry.source || kind} → ${entry.target}: ${Math.abs(entry.amount || 0)}${entry.type ? ' ' + entry.type : ''}${tempBit}` };

    }

    const beforeCombatants = before.combatants || [];

    const afterCombatants = after.combatants || [];

    if (afterCombatants.length > beforeCombatants.length) {

      const added = afterCombatants.find(c => !beforeCombatants.some(b => b.id === c.id));

      return { action: 'combatant_added', summary: `Combatant added: ${added?.name || 'unknown'}` };

    }

    if (afterCombatants.length < beforeCombatants.length) {

      const removed = beforeCombatants.find(b => !afterCombatants.some(a => a.id === b.id));

      return { action: 'combatant_removed', summary: `Combatant removed: ${removed?.name || 'unknown'}` };

    }

    for (const a of afterCombatants) {

      const b = beforeCombatants.find(x => x.id === a.id);

      if (!b) continue;

      if ((b.currentHp ?? 0) !== (a.currentHp ?? 0) || (b.tempHp || 0) !== (a.tempHp || 0)) {

        const delta = (a.currentHp || 0) - (b.currentHp || 0);

        const tempBit = (b.tempHp || 0) !== (a.tempHp || 0) ? ` tempHp ${b.tempHp || 0}→${a.tempHp || 0}` : '';

        return { action: delta > 0 ? 'heal' : 'damage', summary: `${a.name}: HP ${b.currentHp} → ${a.currentHp}${delta !== 0 ? ` (${delta > 0 ? '+' : ''}${delta})` : ''}${tempBit}` };

      }

      if (JSON.stringify(b.deathSaves || {}) !== JSON.stringify(a.deathSaves || {})) {

        const ads = a.deathSaves || {};

        const stateBit = ads.dead ? ' — dead' : ads.stabilized ? ' — stabilized' : '';

        return { action: 'death_save', summary: `${a.name} death save → ${ads.successes || 0} succ / ${ads.failures || 0} fail${stateBit}` };

      }

    }

    return null; // nothing worth snapshotting

  };



  const captureCombatSnapshot = (action, summary) => {

    if (!combat) return null;

    // Defensive strip in case a legacy `snapshots` field is still on combat mid-migration.

    const { snapshots: _discard, ...combatWithoutSnapshots } = combat;

    const sortedNow = [...(combat.combatants || [])].sort((a, b) => (b.initiative || 0) - (a.initiative || 0));

    return {

      id: generateId(),

      timestamp: new Date().toISOString(),

      round: combat.round || 1,

      turnIndex: combat.turnIndex || 0,

      actorName: sortedNow[combat.turnIndex || 0]?.name || '—',

      action,

      summary,

      // Shallow snapshot — React's functional updates produce new references on change, so

      // later mutations don't back-propagate into this captured state. chars is a shared ref

      // but individual char entries follow the same functional-update discipline.

      state: { combat: combatWithoutSnapshots, chars },

    };

  };



  // Wrapper for onUpdateCombat that auto-captures snapshots on significant changes and writes

  // them to the SEPARATE `combatSnapshots` path. Combat itself never carries snapshots after

  // this point — saves a ~600KB payload off every combat write. The original prop is aliased

  // as onUpdateCombatBase; every call site in this component continues calling

  // `onUpdateCombat(...)` unchanged.

  const getCombatSaveFailureMessage = (error) => {
    if (isCombatItemAbilityStaleAuthorityError(error)) {
      return 'Combat changed before this combat update could be saved. Review the current combat state and try again.';
    }
    return `Combat update could not be saved: ${error?.message || 'storage unavailable'}`;
  };

  const getCombatSnapshotSaveFailureMessage = (error) => (
    `Combat updated, but history snapshot could not be saved: ${error?.message || 'storage unavailable'}`
  );

  const onUpdateCombat = async (newCombat) => {

    const change = describeCombatChange(combat, newCombat);
    let snapshotToWrite = null;
    let nextSnapshots = null;

    if (change && combat && onUpdateCombatSnapshots) {

      const snap = captureCombatSnapshot(change.action, change.summary);

      if (snap) {

        snapshotToWrite = snap;
        nextSnapshots = [...combatSnapshots, snap].slice(-SNAPSHOT_CAP);

      }

    }

    // Strip any legacy snapshots field a caller might have spread in via `...combat`.

    const { snapshots: _drop, ...newCombatNoSnaps } = newCombat || {};

    try {

      await Promise.resolve(onUpdateCombatBase(newCombatNoSnaps));

    } catch (error) {

      console.error('[combat] combat update failed', error);

      notifyCombatAuthorityFailure(getCombatSaveFailureMessage(error), 'combat-save-error');

      return null;

    }

    if (snapshotToWrite && onUpdateCombatSnapshots && nextSnapshots) {

      try {

        await Promise.resolve(onUpdateCombatSnapshots(nextSnapshots));

      } catch (error) {

        console.error('[combat] combat snapshot save failed', error);

        notifyCombatAuthorityFailure(getCombatSnapshotSaveFailureMessage(error), 'combat-snapshot-error');

      }

    }

    return newCombatNoSnaps;

  };

  const resolveCombatItemAbilityDamageType = (label = '') => {

    const lower = String(label || '').toLowerCase();

    return Object.keys(DAMAGE_TYPES).find((type) => (

      type !== 'raidwide'

      && (lower.includes(type) || lower.includes(String(DAMAGE_TYPES[type]?.name || '').toLowerCase()))

    )) || 'force';

  };

  const parseCombatItemAbilityDamageText = (text) => {

    const raw = String(text || '').trim();

    const m = raw.match(/^([+-]?)\s*(\d*d\d+(?:\s*[+-]\s*\d+)?)\s*(.*)$/i);

    if (!m) return null;

    const label = m[3].trim();

    return {

      raw,

      sign: m[1] === '-' ? -1 : 1,

      expr: m[2].replace(/\s+/g, ''),

      label,

      damageType: resolveCombatItemAbilityDamageType(label),

    };

  };

  const getCombatItemAbilityEffectTexts = (effects) => {

    if (Array.isArray(effects)) {

      return effects.map((effect) => String(effect || '').trim()).filter(Boolean);

    }

    if (typeof effects === 'string' && effects.trim()) {

      return effects.split(',').map((effect) => effect.trim()).filter(Boolean);

    }

    return [];

  };

  const resolveCombatItemAbilityEffectLine = (text) => {

    const classification = classifyCombatItemAbilityEffectTextForCombat(text);

    if (String(classification.kind || '').startsWith('unsupported-mixed-damage')) {
      return {
        hadDice: false,
        suppressesStatus: true,
        kind: classification.kind,
        breakdown: `${classification.raw} is not supported as a mixed item ability effect`,
      };
    }

    if (classification.kind === 'save-required') {

      if (classification.saveDamage) {

        const parsed = classification.saveDamage;

        const r = rollDice(parsed.expr);

        const total = Math.max(0, r.total * parsed.sign);

        const dieParts = r.rolls.map((entry) => `[${entry.rolls.join(', ')}]`);

        const flat = parsed.expr.match(/[+-]\d+$/);

        return {

          hadDice: true,

          suppressesStatus: true,

          hadSaveRequired: true,

          kind: 'save-damage',

          breakdown: `${parsed.expr}${parsed.label ? ' ' + parsed.label : ''}: ${dieParts.join(' + ')}${flat ? ' ' + flat[0] : ''} = ${total} before ${parsed.saveAbility.toUpperCase()} save DC ${parsed.saveDC}`,

          expr: parsed.expr,

          label: parsed.label,

          total,

          damageType: parsed.damageType,

          roll: r,

          saveType: parsed.saveAbility,

          saveDC: parsed.saveDC,

          saveEffect: parsed.saveEffect,

          halfOnSave: parsed.halfOnSave,

        };

      }

      return {

        hadDice: false,

        suppressesStatus: true,

        hadSaveRequired: true,

        kind: 'save-required',

        breakdown: `${classification.raw} requires save resolution before damage can be applied`,

      };

    }

    if (classification.kind === 'healing') {

      const parsed = classification.parsedDamage;

      if (parsed) {

        const r = rollDice(parsed.expr);

        const total = Math.abs(r.total * parsed.sign);

        const dieParts = r.rolls.map((entry) => `[${entry.rolls.join(', ')}]`);

        const flat = parsed.expr.match(/[+-]\d+$/);

        return {

          hadDice: true,

          suppressesStatus: true,

          hadHealing: true,

          kind: 'healing',

          breakdown: `${parsed.expr}${parsed.label ? ' ' + parsed.label : ''}: ${dieParts.join(' + ')}${flat ? ' ' + flat[0] : ''} = +${total} healing`,

          expr: parsed.expr,

          label: parsed.label,

          total,

          roll: r,

        };

      }

      const flatTotal = Math.max(0, Number(classification.flatAmount) || 0);

      return {

        hadDice: false,

        suppressesStatus: true,

        hadHealing: true,

        kind: 'healing',

        breakdown: `${classification.raw}: +${flatTotal} healing`,

        total: flatTotal,

      };

    }

    if (classification.kind !== 'auto-damage') return null;

    const parsed = parseCombatItemAbilityDamageText(text);

    if (!parsed) return null;

    const r = rollDice(parsed.expr);

    const total = r.total * parsed.sign;

    const dieParts = r.rolls.map((entry) => `[${entry.rolls.join(', ')}]`);

    const flat = parsed.expr.match(/[+-]\d+$/);

    return {

      hadDice: true,

      kind: 'auto-damage',

      breakdown: `${parsed.expr}${parsed.label ? ' ' + parsed.label : ''}: ${dieParts.join(' + ')}${flat ? ' ' + flat[0] : ''} = ${total >= 0 ? '+' : ''}${total}${parsed.label ? ' ' + parsed.label : ''}`,

      expr: parsed.expr,

      label: parsed.label,

      total,

      damageType: parsed.damageType,

      roll: r,

    };

  };

  const getCombatItemAbilityRows = (char) => (

    (char?.inventory || []).flatMap((item) => (

      (Array.isArray(item?.abilities) ? item.abilities : []).map((ability, index) => {

        if (!ability || typeof ability !== 'object' || !ability.id) return null;

        const abilityName = getLegacyInventoryAbilityName(ability) || `Ability ${index + 1}`;

        const uses = ability.uses && typeof ability.uses === 'object' ? ability.uses : null;

        const currentUses = uses ? Number(uses.current ?? uses.cur ?? uses.max ?? 0) : null;

        const maxUses = uses ? Number(uses.max ?? currentUses ?? 0) : null;

        const depleted = uses && Number.isFinite(currentUses) && currentUses <= 0;

        const effectTexts = getCombatItemAbilityEffectTexts(ability.effects);

        const effectClassifications = effectTexts.map(classifyCombatItemAbilityEffectTextForCombat);

        const damageEffect = effectClassifications.find((effect) => effect.kind === 'auto-damage')?.parsedDamage || null;

        const healingEffect = effectClassifications.find((effect) => effect.kind === 'healing') || null;

        const tempHpEffect = effectClassifications.find((effect) => effect.kind === 'temporary-hp') || null;

        const hasSaveRequired = effectClassifications.some((effect) => effect.kind === 'save-required');

        const saveDamageEffect = effectTexts.length === 1

          ? effectClassifications.find((effect) => effect.kind === 'save-required' && effect.saveDamage)?.saveDamage || null

          : null;

        const hasTargetedHealing = !!healingEffect;

        const hasTargetedTempHp = !!tempHpEffect;

        const hasUnsupportedMixedDamageEffect = effectClassifications.some((effect) => (
          String(effect.kind || '').startsWith('unsupported-mixed-damage')
        ));

        const hasSplitMixedDamageAndHealing = !!damageEffect && hasTargetedHealing;

        const hasSplitMixedDamageAndTempHp = !!damageEffect && hasTargetedTempHp;

        const effectPreview = effectTexts.length > 0

          ? effectTexts.join('; ')

          : String(ability.description || '').trim();

        return {

          itemId: item.id,

          itemName: item.name || 'Item',

          abilityId: ability.id,

          abilityName,

          effectPreview,

          hasTargetedDamage: !!damageEffect,

          hasTargetedHealing,

          hasTargetedTempHp,

          hasSaveRequired,

          hasResolvableSaveDamage: !!saveDamageEffect,

          requiresTarget: !!damageEffect || hasTargetedHealing || hasTargetedTempHp || !!saveDamageEffect,

          unsupportedReason: hasUnsupportedMixedDamageEffect || hasSplitMixedDamageAndHealing || hasSplitMixedDamageAndTempHp
            ? 'Mixed damage and healing or temporary HP item abilities are not supported yet.'
            : (hasSaveRequired && !saveDamageEffect ? 'Save-required item abilities need a save resolution flow before they can be applied.' : ''),

          damageEffectText: damageEffect?.raw || '',

          damageType: damageEffect?.damageType || 'force',

          saveType: saveDamageEffect?.saveAbility || null,

          saveDC: saveDamageEffect?.saveDC || null,

          saveEffect: saveDamageEffect?.saveEffect || null,

          currentUses,

          maxUses,

          depleted,

          hasLimitedUses: !!uses,

        };

      }).filter(Boolean)

    ))

  );

  const buildCombatItemAbilityIdempotencyKey = ({ charId, itemId, abilityId, targetId }) => (

    `combat-item-ability:${charId || 'char'}:${itemId || 'item'}:${abilityId || 'ability'}:${targetId || 'none'}:${combat?.round ?? 'no-round'}:${Date.now().toString(36)}:${generateId()}`

  );

  const notifyCombatAuthorityFailure = (message, type = 'item-ability-error') => {

    setTurnNotifications(prev => [...prev, {

      id: generateId(),

      type,

      message,

      color: '#f87171',

    }]);

    setTimeout(() => setTurnNotifications([]), 6000);

  };

  const isCombatItemAbilityStaleAuthorityError = (error) => {
    const message = String(error?.message || error || '');
    return /stale item ability|stale combat|stale combat singleton/i.test(message);
  };

  const getCombatItemAbilityAuthorityFailureMessage = ({
    error,
    charName,
    itemName,
    abilityName,
  }) => {
    if (isCombatItemAbilityStaleAuthorityError(error)) {
      return 'Combat changed before the item ability resolved. Review the updated state and try again.';
    }
    return `${charName} could not use ${itemName || 'Item'} -> ${abilityName}: ${error?.message || 'authority unavailable'}`;
  };

  const activateCombatItemAbility = async (charId, itemId, abilityId, options = {}) => {

    const liveChar = chars?.[charId];

    if (!liveChar) return;

    if (pendingSpellSaveContext) {
      const pendingName = pendingSpellSaveContext.sourceName || pendingSpellSaveContext.spell?.name || 'the pending effect';
      notifyCombatAuthorityFailure(
        `Resolve ${pendingName} before using another item ability.`,
        'item-ability-pending-save',
      );
      setShowSaveRoll(true);
      return null;
    }

    const targetId = options?.targetId || null;

    const target = targetId ? combatants.find((combatant) => combatant.id === targetId) : null;

    const authorityItem = (liveChar.inventory || []).find((entry) => entry?.id === itemId) || null;

    const authorityAbility = authorityItem?.abilities?.find((entry) => entry?.id === abilityId) || null;

    const authorityEffectTexts = authorityAbility ? getCombatItemAbilityEffectTexts(authorityAbility.effects) : [];

    const authorityEffectClassifications = authorityEffectTexts.map(classifyCombatItemAbilityEffectTextForCombat);

    const authorityHasTargetedDamage = authorityEffectClassifications.some((effect) => effect.kind === 'auto-damage');

    const authorityHasTargetedHealing = authorityEffectClassifications.some((effect) => effect.kind === 'healing');

    const authorityHasTargetedTempHp = authorityEffectClassifications.some((effect) => effect.kind === 'temporary-hp');

    const authorityHasSaveRequired = authorityEffectClassifications.some((effect) => effect.kind === 'save-required');

    const authorityHasUnsupportedMixedDamage = authorityEffectClassifications.some((effect) => (
      String(effect.kind || '').startsWith('unsupported-mixed-damage')
    )) || (authorityHasTargetedDamage && (authorityHasTargetedHealing || authorityHasTargetedTempHp));

    const authoritySaveDamage = authorityEffectTexts.length === 1

      ? authorityEffectClassifications.find((effect) => effect.kind === 'save-required' && effect.saveDamage)?.saveDamage || null

      : null;

    if (authorityHasSaveRequired && !authoritySaveDamage) {

      notifyCombatAuthorityFailure(

        'This item ability requires a saving throw. Resolve the save manually for now; it was not auto-applied.',

        'item-ability-unsupported',

      );

      return null;

    }

    if (authorityHasUnsupportedMixedDamage) {

      notifyCombatAuthorityFailure(

        'Mixed damage and healing or temporary HP item abilities are not supported yet.',

        'item-ability-unsupported',

      );

      return null;

    }

    if (authoritySaveDamage && !target) {

      notifyCombatAuthorityFailure('Choose a target before using this save-based item ability.', 'item-ability-target-required');

      return null;

    }

    if (authorityHasTargetedHealing && !target) {

      notifyCombatAuthorityFailure('Choose a target before using this healing item ability.', 'item-ability-target-required');

      return null;

    }

    if (authorityHasTargetedTempHp && !target) {

      notifyCombatAuthorityFailure('Choose a target before using this temporary HP item ability.', 'item-ability-target-required');

      return null;

    }

    const targetSupportsItemHealingAuthority = target && (target.type === 'player' || target.type === 'npc');

    if (authorityHasTargetedHealing && target && !targetSupportsItemHealingAuthority) {

      notifyCombatAuthorityFailure(

        'Item ability healing only supports conscious player or NPC targets. Enemy target healing is not supported yet.',

        'item-ability-unsupported',

      );

      return null;

    }

    if (authorityHasTargetedTempHp && target && !targetSupportsItemHealingAuthority) {

      notifyCombatAuthorityFailure(

        'Temporary HP item abilities only support conscious player or NPC targets. Enemy target temporary HP is not supported yet.',

        'item-ability-unsupported',

      );

      return null;

    }

    const targetHasDownedHealingState = target && (
      Number(target.currentHp ?? 0) <= 0
      || target.deathSaves
      || (Array.isArray(target.conditions) && target.conditions.some((condition) => /^(unconscious|dead)$/i.test(String(condition || '').trim())))
    );

    if (authorityHasTargetedHealing && targetHasDownedHealingState) {

      notifyCombatAuthorityFailure(

        'Item ability healing for downed targets is not supported yet. Use the manual heal flow for death-save handling.',

        'item-ability-unsupported',

      );

      return null;

    }

    if (authorityHasTargetedTempHp && targetHasDownedHealingState) {

      notifyCombatAuthorityFailure(

        'Temporary HP item abilities for downed targets are not supported yet. Use the manual flow for death-save handling.',

        'item-ability-unsupported',

      );

      return null;

    }

    if (authoritySaveDamage && (target?.type === 'player' || target?.type === 'npc' || target?.type === 'enemy')) {

      const authoritySaveSupported = typeof onActivateCombatItemAbility === 'function'

        && typeof isCombatItemAbilityAuthoritySupported === 'function'

        && isCombatItemAbilityAuthoritySupported({

          charId,

          itemEntryId: itemId,

          abilityId,

          targetId,

          target,

          targetType: target?.type || null,

          targetName: target?.name || null,

          item: authorityItem,

          ability: authorityAbility,

          effectTexts: authorityEffectTexts,

          hasTargetedDamage: false,

          hasTargetedHealing: false,

          hasTargetedSaveDamage: true,

          isCrit: isCrit === true,

        }) === true;

      if (authoritySaveSupported) {

        if (!authorityItem || !authorityAbility) return null;

        const enemySaveShape = {
          effectTexts: authorityEffectTexts,
          target,
          hasTargetedSaveDamage: true,
          isCrit: isCrit === true,
        };

        const enemySaveLethalAuthoritySafe = target?.type === 'enemy'
          && isEnemyCombatItemAbilitySaveLethalAuthoritySafe(enemySaveShape);

        const enemySaveFailedSaveLethalAuthorityCandidate = target?.type === 'enemy'
          && !enemySaveLethalAuthoritySafe
          && isEnemyCombatItemAbilityFailedSaveLethalAuthorityCandidate(enemySaveShape);

        const enemySaveAuthorityMode = target?.type === 'enemy'
          ? (enemySaveLethalAuthoritySafe
            ? 'guaranteed-lethal'
            : (enemySaveFailedSaveLethalAuthorityCandidate ? 'failed-save-lethal' : 'guaranteed-nonlethal'))
          : null;

        const abilityName = getLegacyInventoryAbilityName(authorityAbility) || 'Ability';

        const abilityUses = authorityAbility.uses && typeof authorityAbility.uses === 'object'

          ? authorityAbility.uses

          : null;

        const expectedAbilityUsesCurrent = abilityUses

          ? Number(abilityUses.current ?? abilityUses.cur ?? abilityUses.max ?? 0)

          : null;

        const expectedAbilityUsesMax = abilityUses

          ? Number(abilityUses.max ?? expectedAbilityUsesCurrent ?? 0)

          : null;

        const sourceName = `${authorityItem.name || 'Item'} -> ${abilityName}`;

        const sourceLabel = `${liveChar.name}: ${sourceName}`;

        const expectedTargetCurrentHp = Number(target.currentHp ?? 0);

        const expectedTargetTempHp = Number(target.tempHp ?? 0);

        setPendingSpellSaveContext(createPendingSpellSaveContext({

          spell: {

            name: sourceName,

            saveEffect: authoritySaveDamage.saveEffect,

            halfOnSave: authoritySaveDamage.halfOnSave === true,

          },

          sourceKind: 'item-ability-authority',

          sourceName,

          entries: [{

            targetId: target.id,

            targetName: target.name,

            amount: null,

            type: authoritySaveDamage.damageType || 'force',

            source: sourceLabel,

            isRaidwide: false,

            breakdown: [],

          }],

          saveType: authoritySaveDamage.saveAbility || 'dex',

          saveDC: Number(authoritySaveDamage.saveDC) || 10,

          bypassReaction: false,

          combatState: combat,

          charsState: chars,

          itemAbilityAuthority: {

            charId,

            combatId: combat?.id || null,

            itemEntryId: itemId,

            itemName: authorityItem.name || 'Item',

            abilityId,

            abilityName,

            targetId: target.id,

            expectedTargetType: target.type || null,

            expectedTargetCurrentHp: Number.isFinite(expectedTargetCurrentHp) ? expectedTargetCurrentHp : null,

            expectedTargetTempHp: Number.isFinite(expectedTargetTempHp) ? expectedTargetTempHp : null,

            nonlethalOnly: target.type === 'enemy' ? !enemySaveLethalAuthoritySafe : false,

            enemySaveAuthorityMode,

            isCrit: isCrit === true,

            expectedAbilityUsesCurrent: Number.isFinite(expectedAbilityUsesCurrent) ? expectedAbilityUsesCurrent : null,

            expectedAbilityUsesMax: Number.isFinite(expectedAbilityUsesMax) ? expectedAbilityUsesMax : null,

            expectedCombatRound: combat?.round ?? null,

            expectedCombatTurnIndex: combat?.turnIndex ?? null,

            saveAbility: authoritySaveDamage.saveAbility,

            saveDC: Number(authoritySaveDamage.saveDC) || 10,

            saveEffect: authoritySaveDamage.saveEffect,

            activationSurface: 'combat',

            idempotencyKey: buildCombatItemAbilityIdempotencyKey({ charId, itemId, abilityId, targetId: target.id }),

          },

        }));

        setSaveType(authoritySaveDamage.saveAbility || 'dex');

        setSaveDC(Number(authoritySaveDamage.saveDC) || 10);

        setSaveResults([]);

        setShowSaveRoll(true);

        setTurnNotifications(prev => [...prev, {

          id: generateId(),

          type: 'item-ability',

          message: `${liveChar.name} prepared ${sourceName}; resolve ${authoritySaveDamage.saveAbility?.toUpperCase() || 'DEX'} save DC ${authoritySaveDamage.saveDC}.`,

          color: '#c9a227',

        }]);

        setTimeout(() => setTurnNotifications([]), 6000);

        return null;

      }

    }

    const authoritySupported = !authorityHasSaveRequired

      && typeof onActivateCombatItemAbility === 'function'

      && (

        typeof isCombatItemAbilityAuthoritySupported !== 'function'

        ? authorityHasTargetedHealing !== true

        : isCombatItemAbilityAuthoritySupported({

          charId,

          itemEntryId: itemId,

          abilityId,

          targetId,

          target,

          targetType: target?.type || null,

          targetName: target?.name || null,

          item: authorityItem,

          ability: authorityAbility,

          effectTexts: authorityEffectTexts,

          hasTargetedDamage: authorityHasTargetedDamage,

          hasTargetedHealing: authorityHasTargetedHealing,

          hasTargetedTempHp: authorityHasTargetedTempHp,

          isCrit: isCrit === true,

        }) === true

      );

    if (authoritySupported) {

      if (!authorityItem || !authorityAbility) return;

      const abilityName = getLegacyInventoryAbilityName(authorityAbility) || 'Ability';

      const abilityUses = authorityAbility.uses && typeof authorityAbility.uses === 'object'

        ? authorityAbility.uses

        : null;

      const expectedAbilityUsesCurrent = abilityUses

        ? Number(abilityUses.current ?? abilityUses.cur ?? abilityUses.max ?? 0)

        : null;

      const expectedAbilityUsesMax = abilityUses

        ? Number(abilityUses.max ?? expectedAbilityUsesCurrent ?? 0)

        : null;

      const activationKey = `${charId}:${itemId}:${abilityId}:${targetId || 'none'}`;
      const shouldSendTargetFreshness = !!target && (
        target.type === 'enemy'
        || (authorityHasTargetedHealing && (target.type === 'player' || target.type === 'npc'))
        || (authorityHasTargetedTempHp && (target.type === 'player' || target.type === 'npc'))
      );
      const expectedAuthorityTargetCurrentHp = shouldSendTargetFreshness
        ? Number(target.currentHp ?? 0)
        : null;
      const expectedAuthorityTargetTempHp = shouldSendTargetFreshness
        ? Number(target.tempHp ?? 0)
        : null;
      const enemyLethalAuthoritySafe = target?.type === 'enemy'
        && isEnemyCombatItemAbilityLethalAuthoritySafe({
          effectTexts: authorityEffectTexts,
          target,
          hasTargetedDamage: authorityHasTargetedDamage,
          isCrit: isCrit === true,
        });

      if (combatItemAbilityActivationInFlightRef.current.has(activationKey)) return;

      combatItemAbilityActivationInFlightRef.current.add(activationKey);

      try {

        const authorityResult = await onActivateCombatItemAbility({

          charId,

          combatId: combat?.id || null,

          itemEntryId: itemId,

          itemName: authorityItem.name || 'Item',

          abilityId,

          abilityName,

          targetId: targetId || null,

          expectedTargetType: target?.type || null,

          expectedTargetCurrentHp: Number.isFinite(expectedAuthorityTargetCurrentHp)
            ? expectedAuthorityTargetCurrentHp
            : null,

          expectedTargetTempHp: Number.isFinite(expectedAuthorityTargetTempHp)
            ? expectedAuthorityTargetTempHp
            : null,

          nonlethalOnly: target?.type === 'enemy' ? !enemyLethalAuthoritySafe : false,

          isCrit: isCrit === true,

          expectedAbilityUsesCurrent: Number.isFinite(expectedAbilityUsesCurrent) ? expectedAbilityUsesCurrent : null,

          expectedAbilityUsesMax: Number.isFinite(expectedAbilityUsesMax) ? expectedAbilityUsesMax : null,

          expectedCombatRound: combat?.round ?? null,

          expectedCombatTurnIndex: combat?.turnIndex ?? null,

          activationSurface: 'combat',

          idempotencyKey: buildCombatItemAbilityIdempotencyKey({ charId, itemId, abilityId, targetId }),

        });

        if (authorityResult === null || authorityResult === undefined) {

          throw new Error('combat item ability authority unavailable');

        }

        setTurnNotifications(prev => [...prev, {

          id: generateId(),

          type: 'item-ability',

          message: authorityResult.message || authorityResult.summary || `${liveChar.name} used ${authorityItem.name || 'Item'} -> ${abilityName}${target ? ` on ${target.name}` : ''}`,

          color: '#c9a227',

        }]);

        setTimeout(() => setTurnNotifications([]), 6000);

        return authorityResult;

      } catch (error) {

        console.error('[combat] authoritative item ability activation failed', error);

        notifyCombatAuthorityFailure(getCombatItemAbilityAuthorityFailureMessage({
          error,
          charName: liveChar.name,
          itemName: authorityItem.name,
          abilityName,
        }));

        return null;

      } finally {

        combatItemAbilityActivationInFlightRef.current.delete(activationKey);

      }

    }

    if (authorityHasTargetedTempHp) {

      notifyCombatAuthorityFailure(

        'Temporary HP item abilities need connected authority before they can be applied from combat.',

        'item-ability-unsupported',

      );

      return null;

    }

    const runtimeResult = activateLegacyInventoryAbilityRuntime({

      char: liveChar,

      inventory: liveChar.inventory || [],

      itemId,

      abilityId,

      resolveEffectLine: resolveCombatItemAbilityEffectLine,

    });

    if (!runtimeResult) return;

    const { ability, item, updates, receiptLines, statusBuffsToAdd } = runtimeResult;

    const updatedChar = { ...liveChar, ...updates };
    let charsAfterItemAbility = { ...chars, [liveChar.id]: updatedChar };
    let currentCombatState = combat;
    let combatStateChangedByAbility = false;

    const damageLine = target

      ? receiptLines.find((line) => line.kind === 'auto-damage' && line.hadDice && Number.isFinite(Number(line.total)))

      : null;

    const saveDamageLine = target

      ? receiptLines.find((line) => line.kind === 'save-damage' && line.hadDice && Number.isFinite(Number(line.total)))

      : null;

    const healingLines = target

      ? receiptLines.filter((line) => line.hadHealing && Number(line.total) > 0)

      : [];

    const healingAmount = healingLines.reduce((total, line) => total + (Number(line.total) || 0), 0);

    const rolledLine = receiptLines

      .filter((line) => line.hadDice && !line.hadHealing)

      .map((line) => line.breakdown)

      .join('; ');

    const healingReceiptLine = healingLines.map((line) => line.breakdown).join('; ');

    const effectReceiptLine = [rolledLine, healingReceiptLine].filter(Boolean).join('; ');

    const statusLine = statusBuffsToAdd.length ? ` (${statusBuffsToAdd.join(', ')})` : '';

    if (target && saveDamageLine) {

      const damageAmount = Math.max(0, Number(saveDamageLine.total) || 0);

      const sourceLabel = `${liveChar.name}: ${item.name} -> ${ability.name}`;

      const summary = `${liveChar.name} used item ability: ${item.name} -> ${ability.name} - ${saveDamageLine.breakdown}`;

      onUpdateChars(

        charsAfterItemAbility,

        { perCharMeta: { [liveChar.id]: { summary, category: 'inventory', action: 'inventory.ability.combat' } } },

      );

      setPendingSpellSaveContext(createPendingSpellSaveContext({

        spell: {

          name: `${item.name} -> ${ability.name}`,

          saveEffect: saveDamageLine.saveEffect,

          halfOnSave: saveDamageLine.halfOnSave === true,

        },

        sourceKind: 'item-ability',

        sourceName: `${item.name} -> ${ability.name}`,

        entries: [{

          targetId: target.id,

          targetName: target.name,

          amount: damageAmount,

          type: saveDamageLine.damageType || 'force',

          source: sourceLabel,

          isRaidwide: false,

          breakdown: [{

            source: sourceLabel,

            type: 'base_dice',

            value: saveDamageLine.expr || saveDamageLine.raw,

            roll: saveDamageLine.roll,

            rawValue: damageAmount,

            resultingDamage: damageAmount,

            itemName: item.name,

            itemEntryId: item.id,

            itemId: item.itemId || item.id || null,

            itemDefinitionId: item.definitionId || item.itemId || null,

            itemProvenance: item.provenance || null,

            abilityId: ability.id,

            abilityName: ability.name,

            sourceType: 'item-ability',

          }],

        }],

        saveType: saveDamageLine.saveType || 'dex',

        saveDC: Number(saveDamageLine.saveDC) || 10,

        bypassReaction: false,

        combatState: currentCombatState,

        charsState: charsAfterItemAbility,

      }));

      setSaveType(saveDamageLine.saveType || 'dex');

      setSaveDC(Number(saveDamageLine.saveDC) || 10);

      setSaveResults([]);

      setShowSaveRoll(true);

      setTurnNotifications(prev => [...prev, {

        id: generateId(),

        type: 'item-ability',

        message: `${liveChar.name} used ${item.name} -> ${ability.name}; resolve ${saveDamageLine.saveType?.toUpperCase() || 'DEX'} save DC ${saveDamageLine.saveDC}.`,

        color: '#c9a227',

      }]);

      setTimeout(() => setTurnNotifications([]), 6000);

      return;

    }

    if (statusBuffsToAdd.length > 0) {

      const updatedCombatants = (currentCombatState.combatants || combatants).map((combatant) => {

        if (combatant.id !== liveChar.id) return combatant;

        const nextConditions = [...(combatant.conditions || [])];

        statusBuffsToAdd.forEach((status) => {

          if (!nextConditions.includes(status)) nextConditions.push(status);

        });

        return { ...combatant, conditions: nextConditions };

      });

      currentCombatState = { ...currentCombatState, combatants: updatedCombatants };

      combatStateChangedByAbility = true;

    }

    if (target && healingAmount > 0) {

      const liveTarget = (currentCombatState.combatants || combatants).find((combatant) => combatant.id === target.id) || target;

      const healingOutcome = resolveImmediateHealingOutcome({

        target: liveTarget,

        amount: healingAmount,

        deathSaveConfig,

      });

      if (healingOutcome.kind === 'heal') {

        const sourceLabel = `${liveChar.name}: ${item.name} -> ${ability.name}`;

        const updatedCombatants = (currentCombatState.combatants || combatants).map((combatant) => (

          combatant.id === target.id ? { ...combatant, currentHp: healingOutcome.finalHp } : combatant

        ));

        const healingLogEntry = {

          timestamp: new Date().toISOString(),

          source: sourceLabel,

          target: liveTarget.name,

          targetId: target.id,

          amount: healingOutcome.actualHeal,

          type: 'healing',

          isHealing: true,

          itemName: item.name,

          itemEntryId: item.id,

          itemDefinitionId: item.definitionId || item.itemId || null,

          abilityName: ability.name,

          abilityId: ability.id,

          sourceType: 'item-ability',

          itemProvenance: item.provenance || null,

        };

        currentCombatState = {

          ...currentCombatState,

          combatants: updatedCombatants,

          damageLog: [healingLogEntry, ...(currentCombatState.damageLog || [])].slice(0, 50),

        };

        if (charsAfterItemAbility[target.id]) {

          charsAfterItemAbility = {

            ...charsAfterItemAbility,

            [target.id]: { ...charsAfterItemAbility[target.id], currentHp: healingOutcome.finalHp },

          };

        }

        combatStateChangedByAbility = true;

      }

    }

    const summary = `${liveChar.name} used item ability: ${item.name} -> ${ability.name}${effectReceiptLine ? ` - ${effectReceiptLine}` : ''}${statusLine}`;

    onUpdateChars(

      charsAfterItemAbility,

      { perCharMeta: { [liveChar.id]: { summary, category: 'inventory', action: 'inventory.ability.combat' } } },

    );

    if (combatStateChangedByAbility && !damageLine) {

      onUpdateCombat(currentCombatState);

    }

    if (target && damageLine) {

      const damageAmount = Math.max(0, Number(damageLine.total) || 0);

      if (damageAmount > 0) {

        const sourceLabel = `${liveChar.name}: ${item.name} -> ${ability.name}`;

        const breakdown = [{

          source: sourceLabel,

          type: 'base_dice',

          value: damageLine.expr || damageLine.raw,

          roll: damageLine.roll,

          rawValue: damageAmount,

          resultingDamage: damageAmount,

        }];

        queueDamageWithBreakdown(

          target.id,

          damageAmount,

          damageLine.damageType || 'force',

          sourceLabel,

          false,

          false,

          breakdown,

          currentCombatState,

          charsAfterItemAbility,

        );

      }

    }

    setTurnNotifications(prev => [...prev, {

      id: generateId(),

      type: 'item-ability',

      message: `${liveChar.name} used ${item.name} -> ${ability.name}${target ? ` on ${target.name}` : ''}${effectReceiptLine ? ` - ${effectReceiptLine}` : ''}${statusLine}`,

      color: '#c9a227',

    }]);

    setTimeout(() => setTurnNotifications([]), 6000);

  };



  // Revert: restore snapshot's captured state, truncate snapshots to before this event,

  // write combat + chars + truncated snapshots independently.

  const getCombatRevertFailureMessage = (error) => {
    if (isCombatItemAbilityStaleAuthorityError(error)) {
      return 'Combat changed before the revert could be saved. Review the current combat state and try again.';
    }
    return `Combat revert could not be saved: ${error?.message || 'storage unavailable'}`;
  };

  const revertToSnapshot = async (snapshotId) => {

    const idx = combatSnapshots.findIndex(s => s.id === snapshotId);

    if (idx < 0) return;

    const target = combatSnapshots[idx];

    if (!target?.state) return;

    const truncated = combatSnapshots.slice(0, idx);

    const { snapshots: _drop, ...restoredCombat } = target.state.combat || {};

    const restoredChars = target.state.chars;

    // Bypass the wrapper — we're setting snapshots explicitly to the truncated list.

    setRevertError(null);
    setRevertingSnapshotId(snapshotId);

    try {

      await Promise.resolve(onUpdateCombatBase(restoredCombat));

      if (onUpdateCombatSnapshots) await Promise.resolve(onUpdateCombatSnapshots(truncated));

      if (onUpdateChars && restoredChars) await Promise.resolve(onUpdateChars(restoredChars));

      setShowHistory(false);

      setSelectedSnapshotId(null);

      setRevertTarget(null);

    } catch (error) {

      console.error('[combat] snapshot revert failed', error);

      const message = getCombatRevertFailureMessage(error);

      setRevertError(message);

      notifyCombatAuthorityFailure(message, 'combat-revert-error');

    } finally {

      setRevertingSnapshotId(null);

    }

  };



  // Per-combatant advantage convenience getters

  // Get all combat bonuses from equipped items for a character

  const getEquippedCombatBonuses = (combatantId) => {

    const charData = chars[combatantId];

    if (!charData?.inventory) return [];

    const bonuses = [];

    charData.inventory.forEach(item => {

      if (!item.equipped || !item.combatBonuses?.length) return;

      item.combatBonuses.forEach(b => bonuses.push({ ...b, source: item.name }));

    });

    return bonuses;

  };



  const getLinkedArmorContext = (linkedChar) => {

    if (!linkedChar) return null;

    const effectContext = getResolvedCharacterEffectContext(linkedChar, diseases, systems, isDM);

    return getResolvedArmorClassContext({

      char: linkedChar,

      effectiveStats: effectContext?.effectiveStats,

      equippedCombatBonuses: effectContext?.equippedCombatBonuses,

      activeLegacyEffects: effectContext?.activeLegacyEffects,

      activeRuntimeEffects: Array.isArray(linkedChar.activeEffects) ? linkedChar.activeEffects : [],

      armorTypes: linkedChar.settings?.armorTypes || globalSettings?.armorTypes || DEFAULT_ARMOR_TYPES,

      customArmorTypes: ARMOR_TYPES_DURA,

    });

  };



  const getCombatantDisplayArmorClass = (combatant) => {

    const linkedChar = chars[combatant?.id];

    const resolvedArmorContext = getLinkedArmorContext(linkedChar);

    return resolvedArmorContext?.totalArmorClass ?? combatant?.ac ?? combatant?.armorClass ?? 10;

  };


  // Get effective advantage for a combatant considering: manual toggle + equipped item bonuses

  // scope: 'attack_rolls', 'str_checks', 'dex_checks', 'con_saves', 'initiative', etc.

  const getAdvantageForCombatant = (combatantId, scope = null) => {

    // Manual toggle always takes priority

    const manual = combatantAdvantage[combatantId];

    if (manual && manual !== 'normal') return manual;

    if (scope && chars[combatantId]) {

      const scopeConfig = getCombatAdvantageScopeConfig(scope);

      return getResolvedCharacterAdvantageContext(

        chars[combatantId],

        { manualMode: 'normal', ...scopeConfig },

        diseases,

        systems,

        isDM,

      ).mode;

    }

    return 'normal';

  };

  // Simple version for backward compat (no scope)

  const getAdvantageMode = (combatantId) => combatantAdvantage[combatantId] || 'normal';

  const setAdvantageForCombatant = (combatantId, mode) => {

    setCombatantAdvantage(prev => {

      const next = { ...prev };

      if (mode === 'normal') delete next[combatantId];

      else next[combatantId] = mode;

      return next;

    });

  };

  // Convenience: current turn combatant's advantage (used by rollD20WithAdvantage)

  const activeTurnId = allInitiativesSet && sortedCombatants.length > 0 ? sortedCombatants[combat?.turnIndex || 0]?.id : null;

  const rollAdvantage = activeTurnId ? (combatantAdvantage[activeTurnId] || 'normal') : 'normal';

  const setRollAdvantage = (val) => activeTurnId && setAdvantageForCombatant(activeTurnId, val);


  // Get passive bonuses from equipped items (e.g., +3 Passive Perception)

  const getPassiveBonusFromItems = (combatantId, scope) => {

    const bonuses = getEquippedCombatBonuses(combatantId);

    let total = 0;

    bonuses.forEach(b => {

      if (b.type === 'passive' && b.scope === scope) total += (b.value || 0);

    });

    return total;

  };


  const getResolvedCombatCharEffectContext = (char) => (

    getResolvedCharacterEffectContext(char, diseases, systems, isDM)

  );



  const getResolvedCombatSpellContext = (char) => {

    if (!char) return getResolvedSpellContext({});

    const effectContext = getResolvedCombatCharEffectContext(char);

    return getResolvedSpellContext({

      char,

      effectiveStats: effectContext?.effectiveStats,

      equippedCombatBonuses: effectContext?.equippedCombatBonuses,

    });

  };



  const getResolvedCombatSaveContext = (combatantId, stat) => {

    const combatant = combatants.find(c => c.id === combatantId);

    if (!combatant) return null;

    const char = chars[combatantId];

    if (char) {

      const effectContext = getResolvedCombatCharEffectContext(char);

      return getResolvedSaveContext({

        stat,

        char,

        effectiveStats: effectContext?.effectiveStats,

        equippedCombatBonuses: effectContext?.equippedCombatBonuses,

        activeLegacyEffects: effectContext?.activeLegacyEffects,

        activeRuntimeEffects: char.activeEffects || [],

      });

    }

    if (combatant.savingThrows) {

      return getResolvedSaveContext({

        stat,

        enemySaveBonus: combatant.savingThrows[stat] || 0,

      });

    }

    return getResolvedSaveContext({ stat, enemySaveBonus: 0 });

  };



  const rollResolvedCombatSave = (combatantId, stat, overrideMode = null, advantageScope = null) => {

    const saveContext = getResolvedCombatSaveContext(combatantId, stat);

    if (!saveContext) return null;

    const d20Roll = rollD20WithAdvantage(overrideMode, combatantId, advantageScope || `${stat}_saves`);

    const bonusDiceRolls = saveContext.diceBonuses.map((bonus) => {

      const roll = rollDice(bonus.dice);

      return { ...bonus, total: roll.total };

    });

    const bonusDiceTotal = bonusDiceRolls.reduce((sum, bonus) => sum + bonus.total, 0);

    return {

      saveContext,

      d20Roll,

      d20: d20Roll.selected,

      modifier: saveContext.totalBonus,

      total: d20Roll.selected + saveContext.totalBonus + bonusDiceTotal,

      bonusDiceTotal,

      bonusDiceLabel: bonusDiceRolls.map((bonus) => `${bonus.total}(${bonus.source})`).join('+'),

      bonusDiceRolls,

      breakdown: formatResolvedSaveBreakdown(saveContext),

    };

  };


  // Start combat (without auto-rolling initiative)

  const startCombat = () => {

    const playerCombatants = Object.values(chars).map(c => {

      const armorContext = getLinkedArmorContext(c);

      const computedAC = armorContext?.totalArmorClass ?? c.armorClass ?? 10;

      return {

      id: c.id,

      name: c.name,

      type: c.isNPC ? 'npc' : 'player',

      maxHp: c.maxHp,

      currentHp: c.currentHp,

      tempHp: c.tempHp || 0,

      ac: computedAC,

      initiative: null, // Players will input their own

      resistances: (c.resistances || []).map(r => typeof r === 'string' ? { type: r, percent: 50 } : r),

      vulnerabilities: (c.vulnerabilities || []).map(v => typeof v === 'string' ? { type: v, percent: 50 } : v),

      immunities: c.immunities || [],

      debuffs: [],

      raidwideResist: c.raidwideResist || 0,

      isCurrentTurn: false,

      conditions: [],

      };

    });


    // In test mode (test-char exists), auto-add a test enemy

    const isTestMode = !activeViewerCampaignId && !isCampaignSandboxSession && !!chars['test-char'];

    const initialCombatants = isTestMode

      ? [...playerCombatants, createTestEnemy()]

      : playerCombatants;


    onUpdateCombat({

      ...combat,

      active: true,

      combatants: initialCombatants,

      round: 1,

      turnIndex: 0,

      damageLog: [],

      pendingDamage: [],

    });

    // Defensive clear in case End Combat was skipped (browser closed mid-combat, etc.).

    if (onUpdateCombatSnapshots && combatSnapshots.length > 0) onUpdateCombatSnapshots([]);

  };


  // End combat

  const endCombat = () => {

    const updatedChars = { ...chars };

    combatants.filter(c => c.type === 'player' || c.type === 'npc').forEach(c => {

      if (updatedChars[c.id]) {

        updatedChars[c.id] = { ...updatedChars[c.id], currentHp: c.currentHp, tempHp: c.tempHp || 0 };

      }

    });



    // Emit on-combat-end across every party char (Session 2). Outcome payload:

    //   killedEnemies — names of enemies that dropped to 0 during this encounter.

    //   won — heuristic: true when no enemies remain above 0 HP at combat end.

    //   surrendered — reserved. Currently always false (no surrender UI yet). A DM-

    //     authored rule can still match via `always` + downstream state checks.

    // Rule updates are merged into updatedChars BEFORE onUpdateChars fires so the

    // single write covers combat-HP sync + rule side effects.

    if (systems && Object.keys(updatedChars).length > 0) {

      const killedEnemies = combatants

        .filter(c => c.type === 'enemy' && (c.currentHp ?? 0) <= 0)

        .map(c => c.name);

      const enemiesAlive = combatants.some(c => c.type === 'enemy' && (c.currentHp ?? 0) > 0);

      const triggerContext = { killedEnemies, won: !enemiesAlive, surrendered: false };

      const worldContext = { chars: updatedChars, journal, npcLibrary };

      const combatEndSideEffectBundles = [];

      combatants.filter(c => c.type === 'player' || c.type === 'npc').forEach(party => {

        const partyChar = updatedChars[party.id];

        if (!partyChar || !(partyChar.systemList || []).length) return;

        const ruleResults = processAllSystemRulesForTrigger('on-combat-end', partyChar, systems, triggerContext, worldContext);

        const ruleUpdates = applySystemRuleResults(partyChar, ruleResults, systems, worldContext);

        let nextChar = partyChar;

        if (Object.keys(ruleUpdates).length > 0) {

          nextChar = { ...partyChar, ...ruleUpdates };

        }

        // 2026-04-29 — BUG-REPORTS.md #7: clear encounter-scoped cooldowns

        // at combat end. Pairs with on-turn-end / on-round-end clears in

        // applyRuleAndExpiry above.

        const clearedList = clearCooldownsAtScopes(nextChar.systemList, scopesClearedBy('encounter'));

        if (clearedList !== nextChar.systemList) nextChar = { ...nextChar, systemList: clearedList };

        if (nextChar !== partyChar) updatedChars[party.id] = nextChar;

        combatEndSideEffectBundles.push({
          results: ruleResults,
          charForEffects: nextChar,
          label: `${party.name || nextChar.name || 'Combatant'} combat-end rule`,
          source: 'combat.end',
          statusAuditAction: 'combat.end-status',
          triggerContext,
          prefix: `${party.name || nextChar.name || 'Combatant'}: `,
        });

      });

      // Session 5 — apply on-combat-end side effects.

      const allNotifications = [];
      let nextNpcLibraryForCombatEnd = npcLibrary;
      combatEndSideEffectBundles.forEach((bundle) => {
        const worldContextForBundle = {
          ...worldContext,
          chars: updatedChars,
          npcLibrary: nextNpcLibraryForCombatEnd,
        };
        const followUp = consumeCombatResourceFollowUpSideEffects(bundle.results, {
          charForEffects: bundle.charForEffects,
          label: bundle.label,
          source: bundle.source,
          triggerContext: bundle.triggerContext,
          worldContext: worldContextForBundle,
          statusAuditAction: bundle.statusAuditAction,
          npcLibraryForEffects: nextNpcLibraryForCombatEnd,
        });
        if (followUp.sideEffects?.npcLibraryChanged) {
          nextNpcLibraryForCombatEnd = followUp.sideEffects.nextNpcLibrary;
        }
        appendCombatRuleSideEffectNotices(allNotifications, followUp, {
          label: bundle.label,
          prefix: bundle.prefix,
        });
      });

      if (allNotifications.length > 0) {

        setTurnNotifications(prev => [...prev, ...allNotifications.map(msg => ({ id: generateId(), type: 'system', message: `⚙️ ${msg}`, color: '#9d4edd' }))]);

        setTimeout(() => setTurnNotifications([]), 8000);

      }

    }



    onUpdateChars(updatedChars);



    onUpdateCombat({

      ...combat,

      active: false,

      combatants: [],

      round: 1,

      turnIndex: 0,

      pendingDamage: [],

    });

    // Clear reversibility history — snapshots are scoped to one combat encounter, not the

    // campaign. Previously they leaked across combats because endCombat/startCombat both

    // spread `...combat` and the wrapper preserved `existing` snapshots through trivial

    // updates. Explicit clear here makes the lifecycle obvious.

    if (onUpdateCombatSnapshots) onUpdateCombatSnapshots([]);

  };


  // Set initiative for a combatant

  const setInitiative = (id, value) => {

    const updatedCombatants = combatants.map(c =>

      c.id === id ? { ...c, initiative: value === '' ? null : (parseFloat(value) || 0) } : c

    );

    onUpdateCombat({ ...combat, combatants: updatedCombatants });

  };


  const getInitiativeRollParts = (combatant) => {

    const dexScore = chars?.[combatant.id]?.baseStats?.dex;

    const dexMod = typeof dexScore === 'number'

      ? Math.floor((dexScore - 10) / 2)

      : (combatant.initiativeMod ?? combatant.savingThrows?.dex ?? 0);

    const itemBonus = getPassiveBonusFromItems(combatant.id, 'initiative');

    const initiativeMod = dexMod + itemBonus;

    const tiebreak = typeof dexScore === 'number' ? dexScore / 100 : (dexMod + 10) / 100;

    return { dexMod, itemBonus, initiativeMod, tiebreak };

  };



  // Roll initiative for every combatant whose initiative is unset.

  // initiative = d20 + initiative-mod + (dex_score / 100). Initiative-mod is Dex mod plus

  // supported equipped item bonuses. The dex/100 decimal is a house-rule tiebreaker

  // that resolves most ties without forcing DM adjudication while keeping the integer roll obvious.

  const rollAllInitiative = () => {

    const updatedCombatants = combatants.map(c => {

      if (c.initiative !== null && c.initiative !== undefined) return c;

      const initiativeRoll = rollD20WithAdvantage(null, c.id, 'initiative');

      const d20 = initiativeRoll.selected;

      const { initiativeMod, tiebreak } = getInitiativeRollParts(c);

      const total = Math.round((d20 + initiativeMod + tiebreak) * 100) / 100;

      return { ...c, initiative: total, initiativeRoll: d20, initiativeMod };

    });

    onUpdateCombat({ ...combat, combatants: updatedCombatants });

  };


  // Update combatant resistances/vulnerabilities

  const updateCombatantResists = (id, field, value) => {

    const updatedCombatants = combatants.map(c =>

      c.id === id ? { ...c, [field]: value } : c

    );

    onUpdateCombat({ ...combat, combatants: updatedCombatants });

  };



  // Bulk-toggle enemy AC + HP visibility. New enemies default to hidden, but the DM may

  // want to flip everyone at once (mid-fight reveal of a boss's HP for tension, or hide

  // everything at the start of an encounter). Player combatants are unaffected — they

  // always see their own stats.

  const toggleAllEnemyStatsVisible = (visible) => {

    const updated = combatants.map(c =>

      c.type === 'enemy' ? { ...c, showHp: visible, showAc: visible } : c

    );

    onUpdateCombat({ ...combat, combatants: updated });

  };

  const anyEnemyVisible = combatants.some(c => c.type === 'enemy' && (c.showHp || c.showAc));

  // Add enemy to combat

  const addEnemy = () => {

    const enemy = {

      ...newEnemy,

      id: generateId(),

      currentHp: newEnemy.maxHp,

      initiative: newEnemy.initiative ?? 20, // Use entered value or default to 20

    };

    onUpdateCombat({

      ...combat,

      combatants: [...combatants, enemy],

    });

    setNewEnemy(createEnemy());

    setShowAddEnemy(false);

  };

  // Remove combatant. Confirms on DM click (fat-finger safety — there's no undo).

  // Also cleans up debuffs whose casterId points at the removed combatant, so we don't

  // leave orphaned effects (e.g. Hex cast by a wizard who's about to be removed still

  // queueing saves on its target after the wizard is gone).

  const removeCombatant = (id) => {

    const removed = combatants.find(c => c.id === id);

    if (!removed) return;

    if (!window.confirm(`Remove "${removed.name}" from combat? This can't be undone.`)) return;

    const filteredCombatants = combatants.filter(c => c.id !== id);

    const newCombatants = filteredCombatants.map(c => {

      if (!c.debuffs || c.debuffs.length === 0) return c;

      const cleaned = c.debuffs.filter(d => !d.casterId || d.casterId !== id);

      if (cleaned.length === c.debuffs.length) return c;

      return { ...c, debuffs: cleaned };

    });

    // Clamp turnIndex so sortedCombatants[turnIndex] can never be undefined on the next render.

    // If we removed someone earlier in initiative than the current turn, the clamp shifts us

    // back by one (preserving who's "on deck"). If we removed the current-turn combatant and

    // they were last in order, we wrap to the new final index; nextTurn() rolls into a new

    // round correctly on the next advance.

    const currentTurnIndex = combat.turnIndex || 0;

    const clampedTurnIndex = newCombatants.length === 0

      ? 0

      : Math.min(currentTurnIndex, newCombatants.length - 1);

    onUpdateCombat({

      ...combat,

      combatants: newCombatants,

      pendingDamage: pendingDamage.filter(p => p.targetId !== id),

      turnIndex: clampedTurnIndex,

    });

    setSelectedTargets(selectedTargets.filter(t => t !== id));

  };

  

  // --- Multi-Attack Routines ---

  const updateCombatantRoutines = (combatantId, routines) => {

    onUpdateCombat({

      ...combat,

      combatants: combatants.map(c => c.id === combatantId ? { ...c, attackRoutines: routines } : c),

    });

  };

  

  const executeAttackRoutine = (combatantId, routine, targetId) => {

    const target = combatants.find(c => c.id === targetId);

    if (!target || !routine) return;

    // Recompute from the live char data when one is linked so combat outcome, card display,

    // and sheet AC all read from the same resolver path.

    const targetAC = getCombatantDisplayArmorClass(target);

    const attacker = combatants.find(c => c.id === combatantId);

    

    const results = [];

    (routine.attacks || []).forEach(atk => {

      for (let i = 0; i < (atk.count || 1); i++) {

        // Roll to hit with advantage/disadvantage

        const attackScope = getAttackRoutineAdvantageScope(atk.attackType);

        const d20Roll = rollD20WithAdvantage(null, combatantId, attackScope);

        const itemAttackBonusContext = getAttackBonusContextForCombatant(combatantId, attackScope);

        const itemAttackBonus = itemAttackBonusContext.totalBonus || 0;

        const hitTotal = d20Roll.selected + (atk.toHitBonus || 0) + itemAttackBonus;

        const isCrit = d20Roll.selected === 20;

        const isMiss = d20Roll.selected === 1 || (!isCrit && hitTotal < targetAC);

        const isHit = isCrit || hitTotal >= targetAC;

        

        // Roll damage (only if hit)

        let damageRoll = null;

        let damageTotal = 0;

        if (isHit && atk.damageDice) {

          damageRoll = rollDice(atk.damageDice);

          damageTotal = damageRoll.total;

          if (isCrit) {

            // 5e RAW: on a crit, double the DICE rolled — NOT the flat modifier. Previously

            // this line did Math.floor(damageTotal * 2), which doubled the +N modifier too

            // (a 1d6+3 attack crit for (6+3)*2=18 instead of the correct 2d6+3). We re-roll

            // the dice expression and add ONLY its dice portion (static bonuses from rollDice

            // are folded into `total`, not `modifier`, so we compute the dice sum directly

            // from the `rolls` groups to avoid double-counting the +N).

            const critReroll = rollDice(atk.damageDice);

            const critDiceSum = (critReroll.rolls || []).reduce((sum, group) => {

              const groupSum = (group.rolls || []).reduce((a, b) => a + b, 0);

              return sum + groupSum * (group.sign || 1);

            }, 0);

            damageTotal = damageRoll.total + critDiceSum;

          }

        }

        

        results.push({

          attackName: atk.name + (atk.count > 1 ? ` #${i + 1}` : ''),
          attackType: normalizeAttackRoutineAttackType(atk.attackType),

          d20Roll,

          toHitBonus: atk.toHitBonus || 0,
          itemAttackBonus,
          itemAttackBonusSources: itemAttackBonusContext.itemSources || [],

          hitTotal,

          targetAC,

          isHit,

          isCrit,

          isMiss: d20Roll.selected === 1,

          isNatMiss: d20Roll.selected === 1,

          damageRoll,

          damageTotal,

          damageType: atk.damageType || 'slashing',

          enabled: isHit, // Can toggle off individual hits before applying

        });

      }

    });

    

    setAttackResults({ combatantId, routineName: routine.name, results, targetId, targetName: target.name, targetAC, attackerName: attacker?.name || 'Enemy' });

  };

  

  const applyAttackResults = () => {

    if (!attackResults) return;

    const { results, targetId, attackerName } = attackResults;

    results.forEach(r => {

      if (r.enabled && r.isHit && r.damageTotal > 0) {

        queueDamage(targetId, r.damageTotal, r.damageType, `${attackerName}: ${r.attackName}${r.isCrit ? ' (CRIT)' : ''}`, false, true);

      }

    });

    setAttackResults(null);

  };

  

  const toggleAttackResult = (index) => {

    if (!attackResults) return;

    const updated = { ...attackResults, results: attackResults.results.map((r, i) => i === index ? { ...r, enabled: !r.enabled } : r) };

    setAttackResults(updated);

  };

  

  // Toggle target selection (for multi-select)

  const toggleTarget = (id) => {

    if (selectedTargets.includes(id)) {

      setSelectedTargets(selectedTargets.filter(t => t !== id));

    } else {

      setSelectedTargets([...selectedTargets, id]);

    }

  };

  

  // Quick select functions

  const selectAllPlayers = () => setSelectedTargets(playerCombatants.map(c => c.id));

  const selectAllEnemies = () => setSelectedTargets(enemyCombatants.map(c => c.id));

  const selectAll = () => setSelectedTargets(combatants.map(c => c.id));

  const clearSelection = () => setSelectedTargets([]);

  

  // Apply damage to all selected targets

  const applyDamageToSelected = () => {

    if (selectedTargets.length === 0 || !damageAmount) return;

    const sourceName = attackSource === 'dm' 

      ? (isDM ? 'DM' : (currentChar?.name || 'Unknown'))

      : (combatants.find(c => c.id === attackSource)?.name || 'Unknown');

    const isRaidwide = selectedTargets.length > 1;

    

    selectedTargets.forEach(targetId => {

      queueDamage(targetId, damageAmount, damageType, isRaidwide ? `${sourceName} (AoE)` : sourceName, isRaidwide, bypassReaction);

    });

    

    setDamageAmount('');

    setIsCrit(false);

  };

  

  // Heal all selected targets

  const healSelected = () => {

    if (selectedTargets.length === 0 || !damageAmount) return;

    selectedTargets.forEach(targetId => healTarget(targetId, damageAmount));

    setDamageAmount('');

  };

  

  // Grant temp HP to selected targets (takes higher value, doesn't stack)

  const grantTempHpToSelected = () => {

    if (selectedTargets.length === 0 || !damageAmount) return;

    const amount = parseInt(damageAmount) || 0;

    if (amount <= 0) return;

    

    const updatedCombatants = combatants.map(c => {

      if (!selectedTargets.includes(c.id)) return c;

      const outcome = resolveImmediateTempHpOutcome({ target: c, amount });

      return outcome.changed ? { ...c, tempHp: outcome.finalTempHp } : c;

    });

    

    onUpdateCombat({ ...combat, combatants: updatedCombatants });

    

    // Sync to chars

    const updatedChars = { ...chars };

    selectedTargets.forEach(targetId => {

      if (updatedChars[targetId]) {

        const outcome = resolveImmediateTempHpOutcome({ target: updatedChars[targetId], amount });

        if (outcome.changed) {

          updatedChars[targetId] = { ...updatedChars[targetId], tempHp: outcome.finalTempHp };

        }

      }

    });

    onUpdateChars(updatedChars);

    

    setTurnNotifications(prev => [...prev, { 

      id: generateId(), type: 'heal', 

      message: `🩵 Granted ${amount} temp HP to ${selectedTargets.length} target${selectedTargets.length > 1 ? 's' : ''}`,

      color: '#87ceeb' 

    }]);

    setTimeout(() => setTurnNotifications([]), 6000);

    setDamageAmount('');

  };

  

  // Toggle enemy visibility setting

  const toggleEnemyVisibility = (id, field) => {

    const updatedCombatants = combatants.map(c => 

      c.id === id ? { ...c, [field]: !c[field] } : c

    );

    onUpdateCombat({ ...combat, combatants: updatedCombatants });

  };

  

  // Add test enemies

  const addTestEnemy = () => {

    const enemy = createTestEnemy();

    onUpdateCombat({

      ...combat,

      combatants: [...combatants, enemy],

    });

  };

  

  const addTestBoss = () => {

    const boss = createTestBoss();

    onUpdateCombat({

      ...combat,

      combatants: [...combatants, boss],

    });

  };

  

  const buildCombatSpellWorldContext = () => ({ chars, journal, npcLibrary });

  const consumeCombatResourceFollowUpSideEffects = (results, {
    charForEffects,
    label = 'Combat spell resource follow-up',
    source = 'combat.spell-resource-follow-up',
    queuePendingSaves = true,
    triggerContext = null,
    worldContext = null,
    statusAuditAction = 'combat.spell-resource-follow-up-status',
    npcLibraryForEffects = npcLibrary,
  } = {}) => {
    const sideEffects = collectRuleResultSideEffects(results, { npcLibrary: npcLibraryForEffects });
    if (sideEffects.npcLibraryChanged && typeof onUpdateNpcLibrary === 'function') {
      onUpdateNpcLibrary(sideEffects.nextNpcLibrary);
    }
    const sentStatusMessages = persistSystemRuleStatusMessages(sideEffects.statusMessages, {
      char: charForEffects,
      source,
      messages,
      onSendStatusMessage,
      onSaveMessages,
      auditMeta: { category: 'systems', action: statusAuditAction },
    });
    const ruleLogEntries = persistSystemRuleTravelLogEntries(sideEffects.logEntries, {
      char: charForEffects,
      action: 'system-rule-log',
      source,
      groupTravelLog,
      onSaveGroupTravelLog,
    });
    const queuedPendingSaves = queuePendingSaves
      ? buildSystemRulePendingSaveQueueEntriesForCharacter(sideEffects.pendingSaves, {
          char: charForEffects,
          systems,
          worldContext: worldContext || buildCombatSpellWorldContext(),
          triggerContext,
          label,
          source,
        })
      : [];
    if (queuedPendingSaves.length > 0) {
      setCombatResourceSaveQueue(prev => [...(prev || []), ...queuedPendingSaves]);
    }
    return {
      sideEffects,
      sentStatusMessages,
      ruleLogEntries,
      queuedPendingSaves,
    };
  };

  const appendCombatRuleSideEffectNotices = (notices, followUp, {
    label = 'Combat system rule',
    prefix = '',
  } = {}) => {
    if (!followUp) return;
    const sideEffects = followUp.sideEffects || {};
    (sideEffects.notifications || []).forEach(message => {
      if (message) notices.push(`${prefix}${message}`);
    });
    if ((followUp.queuedPendingSaves || []).length > 0) {
      const pendingSaveNotice = buildSystemRulePendingSaveNotice(sideEffects.pendingSaves, label);
      notices.push(`${prefix}${pendingSaveNotice || `${label} requested ${followUp.queuedPendingSaves.length} saving throw${followUp.queuedPendingSaves.length === 1 ? '' : 's'}.`} Queued for resolution.`);
    } else if ((sideEffects.pendingSaves || []).length > 0) {
      const pendingSaveNotice = buildSystemRulePendingSaveNotice(sideEffects.pendingSaves, label);
      if (pendingSaveNotice) notices.push(`${prefix}${pendingSaveNotice}`);
    }
    if ((followUp.ruleLogEntries || []).length > 0) {
      notices.push(`${prefix}${followUp.ruleLogEntries.length} system rule log entr${followUp.ruleLogEntries.length === 1 ? 'y' : 'ies'} recorded.`);
    }
    if ((followUp.sentStatusMessages || []).length > 0) {
      notices.push(`${prefix}${followUp.sentStatusMessages.length} system status message${followUp.sentStatusMessages.length === 1 ? '' : 's'} sent.`);
    }
  };

  const handleCombatResourceSaveResolve = (outcome) => {
    if (!activeCombatResourceSaveEntry) return;
    const [, ...rest] = combatResourceSaveQueue;
    const resolution = resolveSystemRulePendingSaveQueueEntry({
      entry: activeCombatResourceSaveEntry,
      outcome,
      systems,
      fallbackChar: activeCombatResourceSaveChar,
      fallbackWorldContext: buildCombatSpellWorldContext(),
    });
    if (resolution.subResults && resolution.nextChar?.id) {
      const previousChar = chars?.[resolution.nextChar.id] || resolution.queuedChar || resolution.nextChar;
      const nextChars = {
        ...chars,
        [resolution.nextChar.id]: {
          ...previousChar,
          ...buildChangedCharacterFields(previousChar, resolution.nextChar),
        },
      };
      onUpdateChars(nextChars, {
        perCharMeta: {
          [resolution.nextChar.id]: {
            summary: `${activeCombatResourceSaveEntry.label || 'Combat spell resource follow-up'} save ${outcome}: ${activeCombatResourcePendingSave?.reason || resolution.nextChar.name || 'system rule'}`,
            category: 'systems',
            action: `${activeCombatResourceSaveEntry.source || 'combat.spell-resource-follow-up'}.save.${outcome}`,
          },
        },
      });
      const followUp = consumeCombatResourceFollowUpSideEffects(resolution.subResults, {
        charForEffects: resolution.nextChar,
        label: `${activeCombatResourceSaveEntry.label || 'Combat spell resource follow-up'} save`,
        source: `${activeCombatResourceSaveEntry.source || 'combat.spell-resource-follow-up'}.save`,
        queuePendingSaves: false,
      });
      const notices = [...(followUp.sideEffects.notifications || [])];
      if (followUp.ruleLogEntries.length > 0) {
        notices.push(`${followUp.ruleLogEntries.length} system rule log entr${followUp.ruleLogEntries.length === 1 ? 'y' : 'ies'} recorded.`);
      }
      if (followUp.sentStatusMessages.length > 0) {
        notices.push(`${followUp.sentStatusMessages.length} system status message${followUp.sentStatusMessages.length === 1 ? '' : 's'} sent.`);
      }
      if (notices.length > 0) {
        setTurnNotifications((prev) => [
          ...(prev || []),
          ...notices.map(message => ({
            id: generateId(),
            type: 'system',
            message,
            color: '#9d4edd',
          })),
        ]);
      }
    }
    setCombatResourceSaveQueue([...(resolution.nestedSaveEntries || []), ...rest]);
  };

  const handleCombatResourceSaveSkip = () => {
    setCombatResourceSaveQueue(([, ...rest]) => rest);
  };



  const applyCombatSpellResourceFollowUps = (startingChar, resourceEvents) => {

    return applySpellResourceFollowUpsWithResults({

      startingChar,

      resourceEvents,

      systems,

      worldContext: buildCombatSpellWorldContext(),

    });

  };





  const getCombatSpellCastInfo = (spell, char, castLevel = null, customManaCost = null) => {

    if (!spell || !char) return null;

    const liveChar = chars?.[char.id] || char;

    return getSurfaceSpellCastInfo({

      char: liveChar,

      spell,

      systems,

      surface: SPELL_BINDING_SURFACE_COMBAT,

      castLevel,

      manualCostOverride: isDM && Number.isFinite(customManaCost) ? customManaCost : null,

    });

  };



  const getCombatCastLevelOptions = (spell, char) => {

    return getSpellCastLevelOptions({

      spell,

      getCastInfo: (targetSpell, castLevel) => getCombatSpellCastInfo(targetSpell, char, castLevel),

    });

  };



  const resolveCombatSelectedCastLevel = (spell, char, preferredLevel = null) => {

    return resolveSelectedSpellCastLevel({

      spell,

      preferredLevel,

      getCastInfo: (targetSpell, castLevel) => getCombatSpellCastInfo(targetSpell, char, castLevel),

    });

  };



  const getShiftedCombatCastLevel = (spell, char, currentLevel, direction) => {

    return shiftSelectedSpellCastLevel({

      spell,

      currentLevel,

      direction,

      getCastInfo: (targetSpell, castLevel) => getCombatSpellCastInfo(targetSpell, char, castLevel),

    });

  };



  const formatCombatCastInfoLabel = (info, spell) => {

    if (!info) return '';

    if (info.displayMode === 'system-tiered') {

      return spell.level === 0 ? '∞' : `${info.slotsRemaining} slot${info.cost > 1 ? ` (${info.cost})` : ''}`;

    }

    if (info.displayMode === 'legacy-slots') {

      return spell.level === 0 ? '∞' : `${info.slotsRemaining} slots`;

    }

    if (spell.level === 0) return '∞';

    if (info.freeAvailable) return 'FREE';

    const suffix = info.displayMode === 'system-scalar'

      ? (info.resourceLabel || 'Resource')

      : 'MP';

    return info.overcast ? `${info.cost}⚡` : `${info.cost} ${suffix}`;

  };



  // Cast spell (rollInfo is optional - contains dice roll details for breakdown, customManaCost overrides default)

  const castSpell = (spell, char, targets, amount, rollInfo = null, customManaCost = null, spellCastLevel = null, castQuoteOverride = null) => {

    if (!spell || !char || targets.length === 0) return;
    if (pendingSpellSaveContext) {
      setShowSaveRoll(true);
      setTurnNotifications((prev) => [...prev, {
        id: generateId(),
        type: 'info',
        message: `Resolve ${pendingSpellSaveContext.sourceName || pendingSpellSaveContext.spell?.name || 'the pending effect'} before casting another spell.`,
        color: '#c9a227',
      }]);
      return;
    }

    const preCastConcentrationRef = spell.concentration && char.concentratingOn

      ? {

          spellName: (typeof char.concentratingOn === 'string'

            ? char.concentratingOn

            : (char.concentratingOn?.name || null)),

          effectGroupId: char.concentrationEffectGroupId || null,

        }

      : null;

    const shouldReplaceConcentration = !!preCastConcentrationRef?.spellName;



    // 2026-04-28: spell.conditionsApplied was stored in the editor (line ~23981) but never read

    // by castSpell. Building the set here so each spellType branch below can fold them into its

    // updatedCombatants map; for the 'damage' branch (which doesn't otherwise touch combatants)

    // we issue a separate condition-application pass after queueing damage. Conditions are not

    // mechanically auto-enforced — same caveat as the spell editor (P14).

    const conditionsToApply = Array.isArray(spell.conditionsApplied) ? spell.conditionsApplied : [];

    const applyConditionsToCombatantIds = (c, targetIds = targets) => {

      if (conditionsToApply.length === 0) return c;

      if (!targetIds.includes(c.id)) return c;

      const existing = c.conditions || [];

      const newConds = conditionsToApply.filter(cond => !existing.some(e => e === cond || (typeof e === 'string' && e.includes(cond))));

      if (newConds.length === 0) return c;

      return { ...c, conditions: [...existing, ...newConds.map(cond => `${cond} (${spell.name})`)] };

    };

    const applyConditionsToCombatant = (c) => applyConditionsToCombatantIds(c, targets);



    // Concentration swap prompt — same pattern as the spell-panel castSpell. Must run before

    // slot/mana deduction so declining doesn't burn a resource.

    if (shouldReplaceConcentration) {

      const existingLabel = preCastConcentrationRef.spellName;

      const promptMessage = existingLabel === spell.name

        ? `${char.name} is already concentrating on "${existingLabel}". Recast "${spell.name}"? This will end the current "${existingLabel}".`

        : `${char.name} is concentrating on "${existingLabel}". Cast "${spell.name}"? This will end "${existingLabel}".`;

      if (!window.confirm(promptMessage)) {

        return;

      }

    }



    const spellMod = getSpellMod(char);

    const spellSaveDC = getSpellSaveDC(char);

    const effectiveCastLevel = Number.isFinite(spellCastLevel)

      ? spellCastLevel

      : (Number.isFinite(spell?.level) ? spell.level : 0);

    const effectiveManualCost = isDM && Number.isFinite(customManaCost) ? customManaCost : null;

    const spellRuntimeGroupId = ['buff', 'mark', 'debuff'].includes(spell.spellType)

      ? `runtime-group:spell:${generateId()}`

      : null;



    // Deduct mana or spell slot

    let updatedChar = { ...chars[char.id] };

    let nextChars = { ...chars, [char.id]: updatedChar };

    const settings = syncSpellSettings(char.settings || { spellSystem: 'mana' });

    const resourceInfo = castQuoteOverride || getCombatSpellCastInfo(spell, updatedChar, effectiveCastLevel, effectiveManualCost);



    if (!resourceInfo?.canCast && !isDM) {

      window.alert(resourceInfo?.kind === 'legacy-mana' ? 'Not enough mana!' : 'Not enough spell resources!');

      return;

    }

    if (resourceInfo.tooHigh) {

      window.alert(`Cannot cast at level ${effectiveCastLevel} with the current spell resource.`);

      return;

    }

    let runtimeSpell = { ...(resourceInfo?.resolvedSpell || spell) };

    if (!runtimeSpell.damageType && runtimeSpell.spellType === 'damage') {

      const tags = (runtimeSpell.tags || []).map(t => t.toLowerCase());

      const damageTypes = ['fire', 'cold', 'lightning', 'thunder', 'acid', 'poison', 'necrotic', 'radiant', 'force', 'psychic'];

      runtimeSpell.damageType = damageTypes.find(dt => tags.includes(dt)) || 'force';

    }

    if ((runtimeSpell.spellType === 'mark' || runtimeSpell.spellType === 'debuff') && !runtimeSpell.bonusDice) {

      runtimeSpell.bonusDice = runtimeSpell.dice || '1d6';

    }

    let currentCombatState = combat;

    let replacementNotifications = [];

    let spellOutcomeNotifications = [];
    const spellCastRuleSideEffectBundles = [];

    const commitResult = finalizeSpellCastCommit({

      char: updatedChar,

      spell,

      systems,

      castQuote: resourceInfo,

      applyResourceFollowUps: (startingChar, resourceEvents) =>

        applyCombatSpellResourceFollowUps(startingChar, resourceEvents),

      beforeFinalize: (postPaymentChar) => {

        let nextCommittedChar = postPaymentChar;

        nextChars[char.id] = nextCommittedChar;

        if (shouldReplaceConcentration) {

          const replacementResult = breakConcentration(

            char.id,

            preCastConcentrationRef.spellName === spell.name ? `recast ${spell.name}` : `replaced by ${spell.name}`,

            currentCombatState.combatants || combatants,

            nextChars,

            preCastConcentrationRef,

          );

          currentCombatState = {

            ...currentCombatState,

            combatants: replacementResult.combatants,

          };

          nextChars = replacementResult.chars;

          nextCommittedChar = nextChars[char.id] || nextCommittedChar;

          replacementNotifications = replacementResult.notifications;

        }

        nextChars[char.id] = nextCommittedChar;

        return nextCommittedChar;

      },

      runSpellCastTrigger: (postCommitChar) => {

        nextChars[char.id] = postCommitChar;

        if (!systems || !(postCommitChar.systemList || []).length) {

          return postCommitChar;

        }

        const triggerContext = { spellId: spell.id, spellName: spell.name, castLevel: effectiveCastLevel };

        const worldContext = { chars: nextChars, journal, npcLibrary, combat: currentCombatState };

        const ruleResults = processAllSystemRulesForTrigger('on-spell-cast', postCommitChar, systems, triggerContext, worldContext);
        spellCastRuleSideEffectBundles.push({ results: ruleResults, triggerContext, worldContext });

        const ruleUpdates = applySystemRuleResults(postCommitChar, ruleResults, systems, worldContext);

        return { ...postCommitChar, ...ruleUpdates };

      },

    });

    updatedChar = commitResult.nextChar;
    const {
      sideEffects: resourceFollowUpSideEffects,
      sentStatusMessages: sentResourceFollowUpStatusMessages,
      ruleLogEntries: resourceFollowUpLogEntries,
      queuedPendingSaves: resourceFollowUpQueuedSaves,
    } = consumeCombatResourceFollowUpSideEffects(commitResult.resourceFollowUpResults, {
      charForEffects: updatedChar,
      label: 'Combat spell resource follow-up',
      source: 'combat.spell-resource-follow-up',
    });
    if (resourceFollowUpSideEffects.notifications.length > 0) {
      spellOutcomeNotifications = [
        ...spellOutcomeNotifications,
        ...resourceFollowUpSideEffects.notifications.map(message => ({
          id: generateId(),
          type: 'system',
          message: `System: ${message}`,
          color: '#9d4edd',
        })),
      ];
    }
    if (resourceFollowUpQueuedSaves.length > 0) {
      spellOutcomeNotifications = [
        ...spellOutcomeNotifications,
        {
          id: generateId(),
          type: 'info',
          message: `${buildSystemRulePendingSaveNotice(resourceFollowUpSideEffects.pendingSaves, 'Combat spell resource follow-up') || `Combat spell resource follow-up requested ${resourceFollowUpQueuedSaves.length} saving throw${resourceFollowUpQueuedSaves.length === 1 ? '' : 's'}.`} Queued for resolution.`,
          color: '#c9a227',
        },
      ];
    } else if (resourceFollowUpSideEffects.pendingSaves.length > 0) {
      spellOutcomeNotifications = [
        ...spellOutcomeNotifications,
        {
          id: generateId(),
          type: 'info',
          message: buildSystemRulePendingSaveNotice(resourceFollowUpSideEffects.pendingSaves, 'Combat spell resource follow-up'),
          color: '#c9a227',
        },
      ];
    }
    if (resourceFollowUpLogEntries.length > 0) {
      spellOutcomeNotifications = [
        ...spellOutcomeNotifications,
        {
          id: generateId(),
          type: 'system',
          message: `${resourceFollowUpLogEntries.length} system rule log entr${resourceFollowUpLogEntries.length === 1 ? 'y' : 'ies'} recorded.`,
          color: '#9d4edd',
        },
      ];
    }
    if (sentResourceFollowUpStatusMessages.length > 0) {
      spellOutcomeNotifications = [
        ...spellOutcomeNotifications,
        {
          id: generateId(),
          type: 'system',
          message: `${sentResourceFollowUpStatusMessages.length} system status message${sentResourceFollowUpStatusMessages.length === 1 ? '' : 's'} sent.`,
          color: '#9d4edd',
        },
      ];
    }
    spellCastRuleSideEffectBundles.forEach((bundle) => {
      const combatSpellCastWorldContext = {
        ...(bundle.worldContext || {}),
        chars: {
          ...((bundle.worldContext || {}).chars || nextChars || {}),
          ...(updatedChar?.id ? { [updatedChar.id]: updatedChar } : {}),
        },
        combat: currentCombatState,
      };
      const {
        sideEffects,
        sentStatusMessages,
        ruleLogEntries,
        queuedPendingSaves,
      } = consumeCombatResourceFollowUpSideEffects(bundle.results, {
        charForEffects: updatedChar,
        label: 'Combat spell cast rule',
        source: 'combat.spell-on-cast',
        triggerContext: bundle.triggerContext,
        worldContext: combatSpellCastWorldContext,
        statusAuditAction: 'combat.spell-on-cast-status',
      });
      if (sideEffects.notifications.length > 0) {
        spellOutcomeNotifications = [
          ...spellOutcomeNotifications,
          ...sideEffects.notifications.map(message => ({
            id: generateId(),
            type: 'system',
            message: `System: ${message}`,
            color: '#9d4edd',
          })),
        ];
      }
      if (queuedPendingSaves.length > 0) {
        spellOutcomeNotifications = [
          ...spellOutcomeNotifications,
          {
            id: generateId(),
            type: 'info',
            message: `${buildSystemRulePendingSaveNotice(sideEffects.pendingSaves, 'Combat spell cast rule') || `Combat spell cast rule requested ${queuedPendingSaves.length} saving throw${queuedPendingSaves.length === 1 ? '' : 's'}.`} Queued for resolution.`,
            color: '#c9a227',
          },
        ];
      } else if (sideEffects.pendingSaves.length > 0) {
        spellOutcomeNotifications = [
          ...spellOutcomeNotifications,
          {
            id: generateId(),
            type: 'info',
            message: buildSystemRulePendingSaveNotice(sideEffects.pendingSaves, 'Combat spell cast rule'),
            color: '#c9a227',
          },
        ];
      }
      if (ruleLogEntries.length > 0) {
        spellOutcomeNotifications = [
          ...spellOutcomeNotifications,
          {
            id: generateId(),
            type: 'system',
            message: `${ruleLogEntries.length} system rule log entr${ruleLogEntries.length === 1 ? 'y' : 'ies'} recorded.`,
            color: '#9d4edd',
          },
        ];
      }
      if (sentStatusMessages.length > 0) {
        spellOutcomeNotifications = [
          ...spellOutcomeNotifications,
          {
            id: generateId(),
            type: 'system',
            message: `${sentStatusMessages.length} system status message${sentStatusMessages.length === 1 ? '' : 's'} sent.`,
            color: '#9d4edd',
          },
        ];
      }
    });

    nextChars[char.id] = updatedChar;

    spell = runtimeSpell;

    

    // Apply spell effect based on type

    if (spell.spellType === 'healing') {

      const finalAmount = parseInt(amount) || 0;

      let updatedCombatants = [...(currentCombatState.combatants || combatants)];

      const successfulConditionTargetIds = [];

      const revivedTargetNames = [];



      targets.forEach(targetId => {

        const targetIndex = updatedCombatants.findIndex(c => c.id === targetId);

        if (targetIndex !== -1) {

          const target = updatedCombatants[targetIndex];

          const outcome = resolveImmediateHealingOutcome({

            target,

            amount: finalAmount,

            deathSaveConfig,

          });

          if (outcome.kind === 'none') return;

          if (outcome.kind === 'heal-check') {

            attemptHealCheck(targetId, finalAmount);

            return;

          }



          successfulConditionTargetIds.push(targetId);

          if (outcome.kind === 'revive') {

            revivedTargetNames.push(target.name);

          }



          updatedCombatants[targetIndex] = {

            ...target,

            currentHp: outcome.finalHp,

            ...(outcome.kind === 'revive'

              ? { deathSaves: null, conditions: outcome.conditions }

              : {}),

          };



          if (chars[targetId] || nextChars[targetId]) {

            nextChars[targetId] = {

              ...(nextChars[targetId] || chars[targetId] || {}),

              currentHp: outcome.finalHp,

            };

          }

        }

      });

      if (successfulConditionTargetIds.length > 0) {

        updatedCombatants = updatedCombatants.map((combatant) =>

          applyConditionsToCombatantIds(combatant, successfulConditionTargetIds)

        );

      }



      // Log the healing

      const logEntry = {

        timestamp: new Date().toISOString(),

        source: char.name,

        target: targets.map(id => (currentCombatState.combatants || combatants).find(c => c.id === id)?.name).filter(Boolean).join(', '),

        amount: finalAmount,

        type: 'healing',

        spellName: spell.name,

        isHealing: true,

        isRevive: revivedTargetNames.length > 0,

      };

      

      // Update combat with all changes at once

      onUpdateCombat({

        ...currentCombatState,

        combatants: updatedCombatants,

        damageLog: [logEntry, ...((currentCombatState.damageLog || combat.damageLog) || [])].slice(0, 50),

      });

    } else if (spell.spellType === 'temp_hp') {

      const finalAmount = parseInt(amount) || 0;

      let updatedCombatants = [...(currentCombatState.combatants || combatants)];

      const successfulConditionTargetIds = [];



      targets.forEach(targetId => {

        const targetIndex = updatedCombatants.findIndex(c => c.id === targetId);

        if (targetIndex === -1) return;

        const target = updatedCombatants[targetIndex];

        const outcome = resolveImmediateTempHpOutcome({

          target,

          amount: finalAmount,

        });

        if (outcome.kind === 'none' || !outcome.changed) return;



        successfulConditionTargetIds.push(targetId);

        updatedCombatants[targetIndex] = {

          ...target,

          tempHp: outcome.finalTempHp,

        };

        if (chars[targetId] || nextChars[targetId]) {

          nextChars[targetId] = {

            ...(nextChars[targetId] || chars[targetId] || {}),

            tempHp: outcome.finalTempHp,

          };

        }

      });



      if (successfulConditionTargetIds.length > 0) {

        updatedCombatants = updatedCombatants.map((combatant) =>

          applyConditionsToCombatantIds(combatant, successfulConditionTargetIds)

        );

      }



      const logEntry = {

        timestamp: new Date().toISOString(),

        source: char.name,

        target: targets.map(id => (currentCombatState.combatants || combatants).find(c => c.id === id)?.name).filter(Boolean).join(', '),

        amount: finalAmount,

        type: 'temp_hp',

        spellName: spell.name,

      };



      onUpdateCombat({

        ...currentCombatState,

        combatants: updatedCombatants,

        damageLog: [logEntry, ...((currentCombatState.damageLog || combat.damageLog) || [])].slice(0, 50),

      });

    } else if (spell.spellType === 'damage') {

      const baseAmount = parseInt(amount) || 0;

      const initialCheck = resolveInitialTargetChecks(spell, spell.saveDC || spellSaveDC);

      const pendingSaveEntries = [];

      const queuedDamageEntries = [];

      const successfulConditionTargetIds = [];

      const attackOutcomeNotifications = [];



      // Damage branch doesn't update combatants directly — queueDamageWithBreakdown writes to

      // pendingDamage. Apply conditions BEFORE queueing so the combat update lands first; if

      // we did it after the loop, the queue's pendingDamage write would either see a stale

      // combat or be overwritten by ours.

      if (initialCheck.mode === 'direct' && conditionsToApply.length > 0) {

        const updatedCombatants = (currentCombatState.combatants || combatants).map(applyConditionsToCombatant);

        currentCombatState = { ...currentCombatState, combatants: updatedCombatants };

        onUpdateCombat(currentCombatState);

      }



      targets.forEach(targetId => {

        const target = (currentCombatState.combatants || combatants).find(c => c.id === targetId);

        if (!target) return;

        

        // Use pre-rolled breakdown if available (from inline combat roll)

        let breakdown = [];

        let preRolledTotal = baseAmount;

        

        if (rollInfo && rollInfo.breakdown) {

          // Convert new breakdown format to damage log format

          rollInfo.breakdown.forEach(part => {

            if (part.type === 'base') {

              breakdown.push({

                source: part.source,

                type: 'base_dice',

                value: part.dice || part.rolls?.join('+'),

                roll: { rolls: [{ rolls: part.rolls || [] }], total: part.total },

                rawValue: part.total,

                resultingDamage: part.total,

              });

            } else if (part.type === 'modifier') {

              breakdown.push({

                source: part.source,

                type: 'modifier',

                value: `+${part.value}`,

                rawValue: part.value,

                resultingDamage: preRolledTotal,

              });

            } else if (part.type === 'mark') {

              breakdown.push({

                source: part.source,

                type: 'mark_dice',

                value: `+${part.dice}`,

                roll: { rolls: [{ rolls: part.rolls || [] }], total: part.total },

                rawValue: part.total,

                resultingDamage: preRolledTotal,

              });

            } else if (part.type === 'debuff') {

              breakdown.push({

                source: part.source,

                type: 'debuff_dice',

                value: `+${part.dice}`,

                roll: { rolls: [{ rolls: part.rolls || [] }], total: part.total },

                rawValue: part.total,

                resultingDamage: preRolledTotal,

              });

            } else if (part.type === 'percent') {

              breakdown.push({

                source: part.source,

                type: 'debuff_percent',

                value: `+${part.percent}%`,

                rawValue: part.value,

                resultingDamage: preRolledTotal,

              });

            }

          });

          preRolledTotal = rollInfo.grandTotal || rollInfo.total;

        } else if (rollInfo && rollInfo.rolls) {

          // Old format - basic roll info

          const diceTotal = rollInfo.rolls.reduce((sum, r) => sum + r.rolls.reduce((a, b) => a + b, 0), 0);

          breakdown.push({ 

            source: spell.name + ' (dice)', 

            type: 'base_dice', 

            value: rollInfo.rolls.map(r => `${r.count}d${r.sides}`).join('+'),

            roll: rollInfo,

            rawValue: diceTotal,

            resultingDamage: diceTotal

          });

          if (rollInfo.spellMod) {

            breakdown.push({

              source: 'Spellcasting Modifier',

              type: 'modifier',

              value: `+${rollInfo.spellMod}`,

              rawValue: rollInfo.spellMod,

              resultingDamage: diceTotal + rollInfo.spellMod

            });

          }

          

          // For old format, still check marks/debuffs

          if (updatedChar.markedTargets) {

            const mark = updatedChar.markedTargets.find(m => m.targetId === targetId);

            if (mark && mark.casterId === char.id) {

              const markRoll = rollDice(mark.bonusDice || '1d6');

              preRolledTotal += markRoll.total;

              breakdown.push({ 

                source: mark.spellName, 

                type: 'mark_dice', 

                value: `+${mark.bonusDice}`, 

                roll: markRoll,

                rawValue: markRoll.total,

                resultingDamage: preRolledTotal

              });

            }

          }

          

          const debuffs = target.debuffs || [];

          debuffs.forEach(debuff => {

            if (debuff.damageTypes?.includes(spell.damageType) || debuff.damageTypes?.includes('all') || !debuff.damageTypes) {

              if (debuff.casterOnly && debuff.casterId !== char.id) return;

              

              if (debuff.bonusDice) {

                const debuffRoll = rollDice(debuff.bonusDice);

                preRolledTotal += debuffRoll.total;

                breakdown.push({ 

                  source: debuff.name, 

                  type: 'debuff_dice', 

                  value: `+${debuff.bonusDice}`, 

                  roll: debuffRoll,

                  rawValue: debuffRoll.total,

                  resultingDamage: preRolledTotal

                });

              }

              if (debuff.percentIncrease) {

                const increase = Math.floor(preRolledTotal * (debuff.percentIncrease / 100));

                preRolledTotal += increase;

                breakdown.push({ 

                  source: debuff.name, 

                  type: 'debuff_percent', 

                  value: `+${debuff.percentIncrease}%`, 

                  rawValue: increase,

                  resultingDamage: preRolledTotal

                });

              }

            }

          });

        } else {

          // No roll info - just use the base amount

          breakdown.push({ source: spell.name, type: 'base', value: baseAmount, resultingDamage: baseAmount });

          preRolledTotal = baseAmount;

        }

        

        // Apply attacker's active buff effects (e.g., Bless +1d4, etc.)

        const attackerEffects = updatedChar.activeEffects || char.activeEffects || [];

        attackerEffects.forEach(eff => {

          if (!eff.autoApply) return;

          // Parse the modifier for bonus dice or flat bonus

          const mod = eff.modifier || '';

          const diceMatch = mod.match(/[+-]?\s*(\d*d\d+(?:[+-]\d+)?)/i);

          if (diceMatch) {

            const diceExpr = diceMatch[1];

            const buffRoll = rollDice(diceExpr);

            preRolledTotal += buffRoll.total;

            breakdown.push({

              source: eff.name || 'Buff',

              type: 'buff_dice',

              value: `+${diceExpr}`,

              roll: buffRoll,

              rawValue: buffRoll.total,

              resultingDamage: preRolledTotal,

            });

          } else {

            const flatMatch = mod.match(/[+-]?\s*(\d+)/);

            if (flatMatch) {

              const flatVal = parseInt(flatMatch[1]) || 0;

              if (flatVal > 0) {

                preRolledTotal += flatVal;

                breakdown.push({

                  source: eff.name || 'Buff',

                  type: 'buff_flat',

                  value: `+${flatVal}`,

                  rawValue: flatVal,

                  resultingDamage: preRolledTotal,

                });

              }

            }

          }

        });

        

        // Queue raw damage with the pre-mitigation breakdown. The shared damage

        // resolver applies immunity/resistance/vulnerability/wardsave exactly once

        // when the event is accepted or bypassed.

        const damageEntry = {

          targetId,

          targetName: target.name,

          amount: preRolledTotal,

          type: spell.damageType || 'force',

          source: `${char.name} (${spell.name})`,

          isRaidwide: targets.length > 1,

          breakdown,

        };

        if (initialCheck.mode === SPELL_INITIAL_CHECK_SAVE) {

          pendingSaveEntries.push(damageEntry);

        } else if (initialCheck.mode === SPELL_INITIAL_CHECK_ATTACK) {

          const attackRoll = rollD20WithAdvantage(getAdvantageForCombatant(char.id, getSpellAttackAdvantageScope(spell)));

          const attackOutcome = resolveSpellAttackOutcome({

            attackRoll,

            attackBonus: getSpellAttackBonus(updatedChar || char, spell),

            targetAC: getCombatantDisplayArmorClass(target),

          });

          damageEntry.breakdown = [{

            source: `${spell.name} attack`,

            type: 'spell_attack_roll',

            value: `${attackOutcome.total} vs AC ${attackOutcome.targetAC}`,

            roll: attackRoll,

            rawValue: attackOutcome.total,

            targetArmorClass: attackOutcome.targetAC,

            attackBonus: attackOutcome.attackBonus,

            hit: attackOutcome.isHit,

            crit: attackOutcome.isCrit,

            resultingDamage: attackOutcome.isHit ? damageEntry.amount : 0,

          }, ...damageEntry.breakdown];

          if (attackOutcome.isHit) {

            queuedDamageEntries.push(damageEntry);

            successfulConditionTargetIds.push(targetId);

          } else {

            attackOutcomeNotifications.push({

              id: generateId(),

              type: 'save_pass',

              message: `${char.name}'s ${spell.name} misses ${target.name} (${attackOutcome.total} vs AC ${attackOutcome.targetAC})`,

              color: '#888',

            });

          }

        } else {

          queuedDamageEntries.push(damageEntry);

          successfulConditionTargetIds.push(targetId);

        }

      });

      if (initialCheck.mode === SPELL_INITIAL_CHECK_SAVE && pendingSaveEntries.length > 0) {

        setPendingSpellSaveContext(createPendingSpellSaveContext({
          spell,
          entries: pendingSaveEntries,
          saveType: initialCheck.saveType,
          saveDC: initialCheck.saveDC,
          conditionsToApply,
          bypassReaction,
          combatState: currentCombatState,
        }));

        setSaveType(initialCheck.saveType || 'dex');

        setSaveDC(initialCheck.saveDC || 10);

        setSaveResults([]);

        setShowSaveRoll(true);

      } else {

        if (initialCheck.mode === SPELL_INITIAL_CHECK_ATTACK && conditionsToApply.length > 0 && successfulConditionTargetIds.length > 0) {

          const updatedCombatants = (currentCombatState.combatants || combatants).map((combatant) => {

            if (!successfulConditionTargetIds.includes(combatant.id)) return combatant;

            return applyConditionsToCombatant(combatant);

          });

          currentCombatState = { ...currentCombatState, combatants: updatedCombatants };

          onUpdateCombat(currentCombatState);

        }

        queuedDamageEntries.forEach((damageEntry) => {

          queueDamageWithBreakdown(

            damageEntry.targetId,

            damageEntry.amount,

            damageEntry.type,

            damageEntry.source,

            damageEntry.isRaidwide,

            bypassReaction,

            damageEntry.breakdown,

            currentCombatState,

          );

        });

        if (attackOutcomeNotifications.length > 0) {

          spellOutcomeNotifications = [...spellOutcomeNotifications, ...attackOutcomeNotifications];

        }

      }

    } else if (spell.spellType === 'buff') {

      // Apply buff to targets

      let updatedCombatants = [...(currentCombatState.combatants || combatants)];

      

      targets.forEach(targetId => {

        const targetIndex = updatedCombatants.findIndex(c => c.id === targetId);

        if (targetIndex !== -1) {

          const target = updatedCombatants[targetIndex];

          // 2026-05-10: Use the bare spell name as the chip identifier
          // ("Bless") and stash the descriptive text ("Bless (+1d4 attack
          // rolls, saving throws)") in the tooltip below. Previously the
          // buffText/applyBuff field was used directly as the chip name,
          // leaving long descriptive strings cluttering the chip wall.
          // Falls back to applyBuff/buffText if spell.name is missing for
          // some odd reason (legacy data).
          const buffName = spell.name || spell.applyBuff || spell.buffText;

          const effectId = `runtime:active-effect:${generateId()}`;

          updatedCombatants[targetIndex] = { 

            ...target, 

            conditions: [...(target.conditions || []), buffName] 

          };

          

          const runtimeOwner = resolveSpellBeneficialRuntimeOwner({
            targetId,
            chars,
            pendingChars: nextChars,
          });

          // Also apply to linked character storage when this combatant has a backing char.
          if (runtimeOwner) {

            const targetChar = runtimeOwner.targetChar;

            targetChar.buffList = [...(targetChar.buffList || []), buffName];

            targetChar.buffSources = { ...(targetChar.buffSources || {}), [buffName]: spell.name };

            // 2026-05-10: stamp buffData with addedBy='spell-cast' so the
            // Status panel categorizer can place this buff in the Combat
            // category. Previously the cast path only wrote buffList +
            // buffSources, leaving the chip with no provenance — it
            // defaulted to Innate, which felt wrong for combat-relevant
            // chips like Bless. effectModel reads `addedBy` and surfaces
            // it as `sourceMode` on the rendered effect, which the
            // categorizer keys off.
            targetChar.buffData = {
              ...(targetChar.buffData || {}),
              [buffName]: {
                ...((targetChar.buffData || {})[buffName] || {}),
                source: spell.name,
                addedBy: 'spell-cast',
                color: spell.concentration ? 'gold' : ((targetChar.buffData || {})[buffName]?.color || null),
                // Prefer buffText (the rich descriptor like "Bless (+1d4
                // attack rolls, saving throws)") in the tooltip — that's
                // where the descriptive details belong now that the chip
                // text shows just the spell name.
                playerTooltip: ((targetChar.buffData || {})[buffName]?.playerTooltip)
                  || spell.buffText
                  || spell.effect
                  || (spell.concentration ? `Concentration: ${spell.name}` : `Cast: ${spell.name}`),
              },
            };

            // Track as active effect with duration

            targetChar.activeEffects = [...(targetChar.activeEffects || []), {

              id: effectId,

              effectId,

              effectGroupId: spellRuntimeGroupId,

              name: spell.name,

              spellName: spell.name,

              concentrationSpellName: spell.name,

              legacyBuffName: buffName,

              legacyConditionName: buffName,

              modifier: spell.effect || spell.buffText || null,

              buffAppliesTo: spell.buffAppliesTo || null,

              duration: spell.concentration ? 'concentration' : (spell.debuffDuration ? parseInt(spell.debuffDuration) : (spell.effectRounds || null)),

              roundApplied: combat?.round || 1,

              appliesTo: Array.isArray(spell.buffAppliesTo) ? spell.buffAppliesTo.join(',') : (spell.buffAppliesTo || 'any'),

              autoApply: spell.spellType === 'buff' && (spell.bonusDice || spell.dice) ? true : false,

              casterId: char.id,

              casterName: char.name,

              // Over-time fields from spell

              dotDamage: spell.dotDice ? spell.dotDice : null,

              dotDamageType: spell.dotDice ? (spell.dotDamageType || 'fire') : null,

              hotHeal: spell.hotDice ? spell.hotDice : null,

              hotTempHp: spell.hotTempHpAmount || null,

              tickTiming: spell.tickTiming || 'start_of_target_turn',

              // Recurring save fields

              saveType: spell.saveType || null,

              saveDC: spell.saveDC || (spell.saveType ? spellSaveDC : null),

              saveTiming: spell.saveTiming || 'end_of_target_turn',

              saveRemovesEffect: spell.saveRemovesEffect !== false,

            }];

            nextChars[targetId] = targetChar;

            if (targetId === char.id) {

              updatedChar = targetChar;

            }

          }

        }

      });

      

      // Log the buff

      const logEntry = {

        timestamp: new Date().toISOString(),

        source: char.name,

        target: targets.map(id => (currentCombatState.combatants || combatants).find(c => c.id === id)?.name).join(', '),

        type: 'buff',

        spellName: spell.name,

        effect: spell.applyBuff,

      };



      onUpdateCombat({

        ...currentCombatState,

        combatants: updatedCombatants.map(applyConditionsToCombatant),

        damageLog: [logEntry, ...((currentCombatState.damageLog || combat.damageLog) || [])].slice(0, 50),

      });

    } else if (spell.spellType === 'mark') {

      // Handle marking spells (Hunter's Mark, Hex, etc.)

      // Marks can be caster-only (Hunter's Mark) or apply as debuffs to everyone (Faerie Fire)

      const targetId = targets[0]; // Marks typically target one enemy

      const target = (currentCombatState.combatants || combatants).find(c => c.id === targetId);

      

      if (target) {

        // Determine if this is a caster-only mark or a debuff that applies to everyone

        const isCasterOnly = spell.casterOnly !== false; // Default to caster-only

        const conditionText = isCasterOnly 

          ? `Marked by ${char.name} (${spell.name})`

          : `${spell.name}`;

        

        // Add mark to caster's tracked marks (for caster-only marks)

        if (isCasterOnly) {

          const markId = `runtime:mark:${generateId()}`;

          updatedChar.markedTargets = [

            ...(updatedChar.markedTargets || []).filter(m => m.spellName !== spell.name),

            { 

              id: markId,

              effectId: markId,

              effectGroupId: spellRuntimeGroupId,

              targetId, 

              targetName: target.name, 

              spellName: spell.name, 

              conditionName: conditionText,

              bonusDice: spell.bonusDice || '1d6',

              casterId: char.id,

              casterOnly: true,

            }

          ];

        }

        

        // Add debuff to target (for damage calculations)

        const debuffId = `runtime:combat-debuff:${generateId()}`;

        const debuff = {

          id: debuffId,

          effectId: debuffId,

          effectGroupId: spellRuntimeGroupId,

          name: spell.name,

          spellName: spell.name,

          concentrationSpellName: spell.name,

          legacyConditionName: conditionText,

          casterId: char.id,

          casterName: char.name,

          casterOnly: isCasterOnly,

          bonusDice: spell.bonusDice || null,

          percentIncrease: spell.percentIncrease || null,

          flatBonus: spell.flatBonus || null,

          damageTypes: spell.damageTypes || ['all'],

          duration: spell.debuffDuration || null, // null = until concentration ends

          turnsRemaining: spell.debuffDuration || null,

          autoApply: true, // Auto-apply when rolling damage

          roundApplied: combat?.round || 1,

          // Over-time fields

          dotDamage: spell.dotDice || null,

          dotDamageType: spell.dotDice ? (spell.dotDamageType || 'fire') : null,

          hotHeal: spell.hotDice || null,

          hotTempHp: spell.hotTempHpAmount || null,

          tickTiming: spell.tickTiming || 'start_of_target_turn',

          // Recurring save fields

          saveType: spell.saveType || null,

          saveDC: spell.saveDC || (spell.saveType ? spellSaveDC : null),

          saveTiming: spell.saveTiming || 'end_of_target_turn',

          saveRemovesEffect: spell.saveRemovesEffect !== false,

        };

        

        // Update combatant with debuff and condition

        let updatedCombatants = (currentCombatState.combatants || combatants).map(c => {

          if (c.id === targetId) {

            const existingDebuffs = (c.debuffs || []).filter(d => d.name !== spell.name);

            return { 

              ...c, 

              debuffs: [...existingDebuffs, debuff],

              conditions: [...(c.conditions || []).filter(cond => !cond.includes(spell.name)), conditionText]

            };

          }

          return c;

        });

        

        // Build effect description

        const effectParts = [];

        if (spell.bonusDice) effectParts.push(`+${spell.bonusDice} damage`);

        if (spell.percentIncrease) effectParts.push(`+${spell.percentIncrease}% damage taken`);

        if (isCasterOnly) effectParts.push(`(${char.name} only)`);

        

        // Log the mark

        const logEntry = {

          timestamp: new Date().toISOString(),

          source: char.name,

          target: target.name,

          type: 'mark',

          spellName: spell.name,

          effect: effectParts.join(', ') || 'Marked',

          casterOnly: isCasterOnly,

        };



        onUpdateCombat({

          ...currentCombatState,

          combatants: updatedCombatants.map(applyConditionsToCombatant),

          damageLog: [logEntry, ...((currentCombatState.damageLog || combat.damageLog) || [])].slice(0, 50),

        });

      }

    } else if (spell.spellType === 'debuff') {

      // Debuff spells that make enemies take more damage from everyone

      const targetId = targets[0];

      const target = (currentCombatState.combatants || combatants).find(c => c.id === targetId);

      

      if (target) {

        const debuffId = `runtime:combat-debuff:${generateId()}`;

        const debuff = {

          id: debuffId,

          effectId: debuffId,

          effectGroupId: spellRuntimeGroupId,

          name: spell.name,

          spellName: spell.name,

          concentrationSpellName: spell.name,

          legacyConditionName: spell.name,

          casterId: char.id,

          casterName: char.name,

          casterOnly: false, // Debuffs apply to damage from everyone

          bonusDice: spell.bonusDice || null,

          percentIncrease: spell.percentIncrease || null,

          flatBonus: spell.flatBonus || null,

          damageTypes: spell.damageTypes || ['all'],

          duration: spell.debuffDuration || null,

          turnsRemaining: spell.debuffDuration || null,

          autoApply: true, // Auto-apply when rolling damage

          roundApplied: combat?.round || 1,

          // Over-time fields

          dotDamage: spell.dotDice || null,

          dotDamageType: spell.dotDice ? (spell.dotDamageType || 'fire') : null,

          hotHeal: spell.hotDice || null,

          hotTempHp: spell.hotTempHpAmount || null,

          tickTiming: spell.tickTiming || 'start_of_target_turn',

          // Recurring save fields

          saveType: spell.saveType || null,

          saveDC: spell.saveDC || (spell.saveType ? spellSaveDC : null),

          saveTiming: spell.saveTiming || 'end_of_target_turn',

          saveRemovesEffect: spell.saveRemovesEffect !== false,

        };

        

        let updatedCombatants = (currentCombatState.combatants || combatants).map(c => {

          if (c.id === targetId) {

            const existingDebuffs = (c.debuffs || []).filter(d => d.name !== spell.name);

            return { 

              ...c, 

              debuffs: [...existingDebuffs, debuff],

              conditions: [...(c.conditions || []).filter(cond => !cond.includes(spell.name)), spell.name]

            };

          }

          return c;

        });

        

        const effectParts = [];

        if (spell.bonusDice) effectParts.push(`+${spell.bonusDice} damage`);

        if (spell.percentIncrease) effectParts.push(`+${spell.percentIncrease}% damage taken`);

        

        const logEntry = {

          timestamp: new Date().toISOString(),

          source: char.name,

          target: target.name,

          type: 'debuff',

          spellName: spell.name,

          effect: effectParts.join(', ') || 'Debuffed',

        };



        onUpdateCombat({

          ...currentCombatState,

          combatants: updatedCombatants.map(applyConditionsToCombatant),

          damageLog: [logEntry, ...((currentCombatState.damageLog || combat.damageLog) || [])].slice(0, 50),

        });

      }

    }

    

    // If concentration, track it

    if (spell.concentration) {

      updatedChar = {

        ...updatedChar,

        concentratingOn: spell.name,

        concentrationEffectGroupId: spellRuntimeGroupId || null,

      };

    }

    nextChars[char.id] = updatedChar;

    

    // Update character

    onUpdateChars(nextChars);

    const finalSpellNotifications = [...replacementNotifications, ...spellOutcomeNotifications];

    if (finalSpellNotifications.length > 0) {

      setTurnNotifications(prev => [...prev, ...finalSpellNotifications]);

      setTimeout(() => setTurnNotifications([]), 6000);

    }

    

  };

  

  // Roll saving throw for a combatant

  const rollSavingThrow = (combatantId, stat) => {

    const combatant = combatants.find(c => c.id === combatantId);

    if (!combatant) return null;

    const saveRoll = rollResolvedCombatSave(combatantId, stat);

    if (!saveRoll) return null;

    return {

      d20: saveRoll.d20,

      modifier: saveRoll.modifier,

      total: saveRoll.total,

      stat,

      combatantName: combatant.name,

      combatantId,

      bonusDiceTotal: saveRoll.bonusDiceTotal,

      bonusDiceLabel: saveRoll.bonusDiceLabel,

    };

  };

  

  // Roll saves for selected targets

  const rollSavesForTargets = () => {

    const targetIds = pendingSpellSaveContext?.entries?.map((entry) => entry.targetId) || selectedTargets;

    const effectiveSaveType = pendingSpellSaveContext?.saveType || saveType;

    const results = targetIds.map(id => rollSavingThrow(id, effectiveSaveType)).filter(Boolean);

    setSaveResults(results);

    setShowSaveRoll(true);

  };

  

  // Apply save results (half damage on success, etc.)

  const applySaveResults = (spell, baseAmount) => {

    saveResults.forEach(result => {

      const passed = result.total >= saveDC;

      let finalAmount = parseInt(baseAmount) || 0;

      

      if (passed && spell?.halfOnSave) {

        finalAmount = Math.floor(finalAmount / 2);

      } else if (passed) {

        finalAmount = 0;

      }

      

      if (finalAmount > 0 && spell?.spellType === 'damage') {

        queueDamage(result.combatantId, finalAmount, spell.damageType || 'force', `Save Effect`, false, bypassReaction);

      }

    });

    

    setShowSaveRoll(false);

    setSaveResults([]);

  };



  const closeSaveRollModal = () => {

    setShowSaveRoll(false);
    if (!pendingSpellSaveContext) {
      setSaveResults([]);
      setPendingSpellSaveContext(null);
    }

  };



  const openManualSaveRollModal = () => {
    if (pendingSpellSaveContext) {
      setShowSaveRoll(true);
      return;
    }
    setPendingSpellSaveContext(null);
    setSaveResults([]);

    setShowSaveRoll(true);

  };



  const applyPendingSpellSaveResults = async () => {

    if (!pendingSpellSaveContext) return;

    const pendingSaveSourceName = pendingSpellSaveContext.sourceName

      || pendingSpellSaveContext.spell?.name

      || 'the pending effect';

    const pendingItemAbilityAuthority = pendingSpellSaveContext.sourceKind === 'item-ability-authority'

      ? pendingSpellSaveContext.itemAbilityAuthority || null

      : null;

    if (pendingItemAbilityAuthority) {

      const pendingEntry = pendingSpellSaveContext.entries?.[0] || null;

      const pendingSaveDC = Number(pendingSpellSaveContext.saveDC) || saveDC;

      const saveResult = saveResults.find((result) => result.combatantId === pendingEntry?.targetId) || null;

      if (!saveResult) {

        notifyCombatAuthorityFailure(`Roll saves before resolving ${pendingSaveSourceName}.`, 'item-ability-pending-save');

        setShowSaveRoll(true);

        return null;

      }

      if (typeof onActivateCombatItemAbility !== 'function') {

        notifyCombatAuthorityFailure(`${pendingSaveSourceName} cannot resolve through item ability authority right now.`, 'item-ability-unsupported');

        setShowSaveRoll(true);

        return null;

      }

      const saveTotal = Number(saveResult.total);

      const savePassed = Number.isFinite(saveTotal) && saveTotal >= pendingSaveDC;

      const activationKey = `${pendingItemAbilityAuthority.charId}:${pendingItemAbilityAuthority.itemEntryId}:${pendingItemAbilityAuthority.abilityId}:${pendingItemAbilityAuthority.targetId || 'none'}:save`;

      if (combatItemAbilityActivationInFlightRef.current.has(activationKey)) return null;

      combatItemAbilityActivationInFlightRef.current.add(activationKey);

      try {

        const {
          enemySaveAuthorityMode,
          ...pendingItemAbilityAuthorityPayload
        } = pendingItemAbilityAuthority;

        const expectedTargetType = String(pendingItemAbilityAuthority.expectedTargetType || '').toLowerCase();
        let resolvedNonlethalOnly = pendingItemAbilityAuthority.nonlethalOnly === true;
        if (expectedTargetType === 'enemy') {
          if (enemySaveAuthorityMode === 'guaranteed-lethal') {
            resolvedNonlethalOnly = false;
          } else if (enemySaveAuthorityMode === 'failed-save-lethal') {
            resolvedNonlethalOnly = savePassed !== false;
          } else if (enemySaveAuthorityMode === 'guaranteed-nonlethal') {
            resolvedNonlethalOnly = true;
          }
        }

        const authorityResult = await onActivateCombatItemAbility({

          ...pendingItemAbilityAuthorityPayload,

          nonlethalOnly: resolvedNonlethalOnly,

          saveTotal,

          savePassed,

        });

        if (authorityResult === null || authorityResult === undefined) {

          throw new Error('combat item ability authority unavailable');

        }

        setTurnNotifications(prev => [...prev, {

          id: generateId(),

          type: 'item-ability',

          message: authorityResult.message || authorityResult.summary || `${pendingItemAbilityAuthority.itemName || 'Item'} -> ${pendingItemAbilityAuthority.abilityName || 'Ability'} resolved by authority.`,

          color: '#c9a227',

        }]);

        setTimeout(() => setTurnNotifications([]), 6000);

        setPendingSpellSaveContext(null);

        setShowSaveRoll(false);

        setSaveResults([]);

        return authorityResult;

      } catch (error) {

        console.error('[combat] authoritative item ability save resolution failed', error);

        notifyCombatAuthorityFailure(getCombatItemAbilityAuthorityFailureMessage({

          error,

          charName: chars?.[pendingItemAbilityAuthority.charId]?.name || pendingItemAbilityAuthority.charId || 'Actor',

          itemName: pendingItemAbilityAuthority.itemName,

          abilityName: pendingItemAbilityAuthority.abilityName,

        }));

        setShowSaveRoll(true);

        return null;

      } finally {

        combatItemAbilityActivationInFlightRef.current.delete(activationKey);

      }

    }

    const outcomes = resolveInitialSpellOutcomes({

      spell: pendingSpellSaveContext.spell,

      targetEntries: pendingSpellSaveContext.entries,

      saveResults,

      saveDC: Number(pendingSpellSaveContext.saveDC) || saveDC,

    });



    let currentCombatState = getPendingSpellSaveCombatState(pendingSpellSaveContext, combat);

    const currentCharsState = getPendingSpellSaveCharsState(pendingSpellSaveContext, chars);

    const pendingSaveType = pendingSpellSaveContext.saveType || saveType;

    const pendingSaveDC = Number(pendingSpellSaveContext.saveDC) || saveDC;

    if ((pendingSpellSaveContext.conditionsToApply || []).length > 0) {

      const affectedTargetIds = outcomes

        .filter((outcome) => outcome.appliedAmount > 0)

        .map((outcome) => outcome.targetId);

      if (affectedTargetIds.length > 0) {

        const updatedCombatants = (currentCombatState.combatants || combatants).map((combatant) => {

          if (!affectedTargetIds.includes(combatant.id)) return combatant;

          const existing = combatant.conditions || [];

          const newConds = pendingSpellSaveContext.conditionsToApply.filter((cond) => !existing.some((entry) => entry === cond || (typeof entry === 'string' && entry.includes(cond))));

          if (newConds.length === 0) return combatant;

          return {

            ...combatant,

            conditions: [...existing, ...newConds.map((cond) => `${cond} (${pendingSaveSourceName})`)],

          };

        });

        currentCombatState = {

          ...currentCombatState,

          combatants: updatedCombatants,

        };

        onUpdateCombat(currentCombatState);

      }

    }



    outcomes.forEach((outcome) => {

      const saveSummary = outcome.saveResult

        ? `${pendingSaveType.toUpperCase()} save ${outcome.passedSave ? 'passed' : 'failed'} (${outcome.saveResult.total} vs DC ${pendingSaveDC})`

        : `${pendingSaveType.toUpperCase()} save unresolved`;

      const breakdown = [

        ...(outcome.breakdown || []),

        {

          source: saveSummary,

          type: outcome.passedSave ? 'save_pass' : 'save_fail',

          value: outcome.outcome,

          rawValue: outcome.appliedAmount,

          resultingDamage: outcome.appliedAmount,

        },

      ];

      if (outcome.appliedAmount > 0) {

        queueDamageWithBreakdown(

          outcome.targetId,

          outcome.appliedAmount,

          outcome.type,

          outcome.source,

          outcome.isRaidwide,

          pendingSpellSaveContext.bypassReaction,

          breakdown,

          currentCombatState,

          currentCharsState,

        );

      } else {

        setTurnNotifications((prev) => [...prev, {

          id: generateId(),

          type: 'info',

          message: `${outcome.targetName || 'Target'} avoids ${pendingSaveSourceName} on a successful save.`,

          color: '#c9a227',

        }]);

      }

    });



    setPendingSpellSaveContext(null);

    setShowSaveRoll(false);

    setSaveResults([]);

  };

  

  // Queue damage (for player reaction system)

  const queueDamage = (targetId, amount, type, sourceName, isRaidwide = false, bypass = false) => {

    const target = combatants.find(c => c.id === targetId);

    if (!target || !amount) return;

    

    let finalAmount = parseInt(amount) || 0;

    if (isCrit) finalAmount *= 2;

    

    // If bypass or target is enemy, apply immediately

    if (bypass || target.type === 'enemy') {

      applyDamageDirectly(targetId, finalAmount, type, sourceName, isRaidwide);

      return;

    }

    

    // Queue for player reaction

    const pendingEntry = {

      id: generateId(),

      targetId,

      targetName: target.name,

      amount: finalAmount,

      type,

      source: sourceName,

      timestamp: new Date().toISOString(),

      isRaidwide,

      wasCrit: isCrit,

    };

    

    onUpdateCombat({

      ...combat,

      pendingDamage: [...pendingDamage, pendingEntry],

    });

    

    setDamageAmount('');

    setIsCrit(false);

  };

  

  // Queue damage with full breakdown (for spells)

  const queueDamageWithBreakdown = (targetId, amount, type, sourceName, isRaidwide = false, bypass = false, breakdown = [], currentCombatStateIn = null, currentCharsStateIn = null) => {

    const currentCombatState = currentCombatStateIn || combat;

    const currentCombatants = currentCombatState.combatants || combatants;

    const currentPendingDamage = currentCombatState.pendingDamage || pendingDamage;

    const target = currentCombatants.find(c => c.id === targetId);

    if (!target || !amount) return;

    

    let finalAmount = parseInt(amount) || 0;

    if (isCrit) finalAmount *= 2;

    

    // If bypass or target is enemy, apply immediately

    if (bypass || target.type === 'enemy') {

      applyDamageDirectly(targetId, finalAmount, type, sourceName, isRaidwide, null, breakdown, currentCombatState, currentCharsStateIn);

      return;

    }

    

    // Queue for player reaction with breakdown

    const pendingEntry = {

      id: generateId(),

      targetId,

      targetName: target.name,

      amount: finalAmount,

      type,

      source: sourceName,

      timestamp: new Date().toISOString(),

      isRaidwide,

      wasCrit: isCrit,

      breakdown,

    };

    

    onUpdateCombat({

      ...currentCombatState,

      pendingDamage: [...currentPendingDamage, pendingEntry],

    });

    

    setDamageAmount('');

    setIsCrit(false);

  };

  

  // Emit on-enemy-killed across every party char currently in combat (Session 2).

  // Called from both applyDamageWithBreakdown and applyDamageDirectly on the

  // wasAlive → nowAtZero transition for enemy-type targets.

  //

  // Implementation notes:

  //  - Combat HP is authoritative during an encounter; char.currentHp only syncs on

  //    endCombat. Rule actions that touch char state (gain-stacks, change-tracker,

  //    apply-system, add-effect) flow through onUpdateChars and persist correctly.

  //  - Rule actions that modify char.currentHp mid-combat will NOT reflect in the

  //    combat view until endCombat; edge case, noted for Session 4.

  //  - killedEnemies is included in the triggerContext so a rule can match

  //    `enemy-name-matches` generically across either trigger emitter.

  //  - party targets = every combatant with type player/npc that has a char record

  //    with systems; absent systems means no rule fires, which is the correct no-op.

  const emitEnemyKilled = (triggerCtx, currentCombatants, currentCharsState = chars) => {

    const sideEffects = resolveEnemyKilledSideEffects({
      triggerCtx,
      combatants: currentCombatants || combatants,
      chars: currentCharsState,
      systems,
      journal,
      npcLibrary,
      processRules: processAllSystemRulesForTrigger,
      applyRuleResults: applySystemRuleResults,
      applyNpcUpdates: applyPendingNpcUpdates,
    });

    if (sideEffects.charsChanged) onUpdateChars(sideEffects.nextChars);

    if (sideEffects.npcLibraryChanged) onUpdateNpcLibrary(sideEffects.nextNpcLibrary);

    if (sideEffects.notifications.length > 0) {

      setTurnNotifications(prev => [...prev, ...sideEffects.notifications.map(msg => ({ id: generateId(), type: 'system', message: `System: ${msg}`, color: '#9d4edd' }))]);

      setTimeout(() => setTurnNotifications([]), 8000);

    }

  };

  // Apply damage with breakdown (shows full calculation trail)

  const applyDamageWithBreakdown = (targetId, amount, type, sourceName, isRaidwide = false, breakdown = []) => {

    const target = combatants.find(c => c.id === targetId);

    if (!target) return;

    

    // If target is already at 0 HP with death saves, add failures instead

    if (target.currentHp <= 0 && target.deathSaves && !target.deathSaves.dead) {

      const updatedDs = handleDamageAtZero(targetId, isCrit);

      const logEntry = {

        timestamp: new Date().toISOString(),

        source: sourceName, target: target.name, targetId, amount,

        originalAmount: breakdown[0]?.value || amount, type,

        isRaidwide, wasCrit: isCrit, breakdown,

        note: 'Damage at 0 HP — death save failure',

      };

      const updatedCombatants = updatedDs ? combatants.map(c =>

        c.id === targetId ? { ...c, deathSaves: { ...c.deathSaves, ...updatedDs } } : c

      ) : combatants;

      onUpdateCombat({ ...combat, combatants: updatedCombatants, damageLog: [logEntry, ...(combat.damageLog || [])].slice(0, 50) });

      // Check for death after state update

      if (updatedDs && updatedDs.failures >= deathSaveConfig.failuresNeeded) {

        setTimeout(() => checkDeathSaveResult(targetId, updatedDs), 150);

      }

      setDamageAmount(''); setIsCrit(false);

      return;

    }

    

    // Temp HP absorbs damage first (5e rules). Before this fix, applyDamageWithBreakdown

    // (the spell-damage path) silently bypassed temp HP — a player with 8 temp HP hit by

    // Fireball would lose 8 extra real HP while the temp HP field sat untouched. Now the

    // absorption step is appended to the breakdown so the receipt is complete here too.

    const currentTempHp = target.tempHp || 0;

    let tempHpLost = 0;

    let hpDamage = amount;

    let newTempHp = currentTempHp;

    const workingBreakdown = breakdown ? [...breakdown] : [];



    if (currentTempHp > 0 && amount > 0) {

      tempHpLost = Math.min(currentTempHp, amount);

      newTempHp = currentTempHp - tempHpLost;

      hpDamage = amount - tempHpLost;

      workingBreakdown.push({

        source: `Temp HP absorbed (${currentTempHp} → ${newTempHp})`,

        type: 'tempHp',

        value: `-${tempHpLost}`,

        rawValue: -tempHpLost,

        resultingDamage: hpDamage,

      });

    }



    const newHp = Math.max(0, target.currentHp - hpDamage);

    const wasAlive = target.currentHp > 0;

    const nowAtZero = newHp <= 0;



    let updatedCombatants = combatants.map(c =>

      c.id === targetId ? { ...c, currentHp: newHp, tempHp: newTempHp } : c

    );



    // Temp HP absorption notification (mirrors the applyDamageDirectly UX so spell damage

    // and direct damage feel the same at the table).

    if (tempHpLost > 0) {

      const absorbMsg = hpDamage > 0

        ? `🩵 ${target.name}'s temp HP absorbs ${tempHpLost} damage (${hpDamage} gets through)`

        : `🩵 ${target.name}'s temp HP absorbs all ${tempHpLost} damage!`;

      setTurnNotifications(prev => [...prev, { id: generateId(), type: 'damage', message: absorbMsg, color: '#87ceeb' }]);

      setTimeout(() => setTurnNotifications([]), 6000);

    }



    // If dropped to 0, initialize death saves

    if (wasAlive && nowAtZero && (target.type === 'player' || target.type === 'npc')) {

      updatedCombatants = initDeathSaves(targetId, updatedCombatants);

      setTurnNotifications(prev => [...prev, { id: generateId(), type: 'damage', message: `💀 ${target.name} drops to 0 HP — making death saves!`, color: '#c92727' }]);

      setTimeout(() => setTurnNotifications([]), 8000);

    }



    // Emit on-enemy-killed to every party char in the encounter (Session 2). Fires once per

    // wasAlive→nowAtZero transition so repeated damage on a downed enemy doesn't re-fire.

    // Rule updates are merged into the canonical chars record via onUpdateChars; combat-view

    // HP remains authoritative for the encounter itself.

    if (wasAlive && nowAtZero && target.type === 'enemy' && systems && chars) {

      emitEnemyKilled({ name: target.name, type: 'enemy', enemyId: targetId, source: sourceName }, updatedCombatants);

    }



    const lastStep = workingBreakdown[workingBreakdown.length - 1] || {};

    const modifier = lastStep.type === 'immune' ? 'immune'

      : lastStep.type === 'resistant' ? 'resistant'

      : lastStep.type === 'vulnerable' ? 'vulnerable'

      : 'normal';



    const logEntry = {

      timestamp: new Date().toISOString(),

      source: sourceName, target: target.name, targetId, amount,

      originalAmount: (breakdown && breakdown[0]?.value) || amount, type, modifier,

      isRaidwide, wasCrit: isCrit,

      tempHpAbsorbed: tempHpLost > 0 ? tempHpLost : undefined,

      breakdown: workingBreakdown,

    };



    onUpdateCombat({

      ...combat,

      combatants: updatedCombatants,

      damageLog: [logEntry, ...(combat.damageLog || [])].slice(0, 50),

    });



    // Durability damage for players

    // 2026-04-27: Two bugs fixed here. (1) toggleEquip stores equipped state on

    // char.equippedArmor (an id), not on equipment[].equipped — the old `e.equipped`

    // check never matched, so dura never decreased from combat damage. (2) applyDuraDamage

    // returns the updated equipment fields spread inline, not under an `equipment` key,

    // so destructuring `{ equipment: updatedEquip }` was always undefined.

    if (globalSettings?.durabilityEnabled !== false && target.type === 'player' && chars[targetId]) {

      const char = chars[targetId];

      if (char.equipment) {

        const equippedArmor = char.equipment.find(e => e.id === char.equippedArmor && e.type === 'armor');

        if (equippedArmor) {

          const duraDamage = calculateDuraDamage(equippedArmor, type);

          if (duraDamage > 0) {

            const updatedEquip = applyDuraDamage(equippedArmor, duraDamage);

            const updatedEquipment = char.equipment.map(e => e.id === equippedArmor.id ? updatedEquip : e);

            onUpdateChars({ ...chars, [targetId]: { ...char, equipment: updatedEquipment, currentHp: newHp, tempHp: newTempHp } });

          }

        }

      }

    }

    

    // Queue concentration check if target is concentrating

    if (amount > 0 && deathSaveConfig.concentrationCheckOnDamage) {

      setTimeout(() => queueConcentrationCheck(targetId, amount), 100);

    }


    setDamageAmount('');

    setIsCrit(false);

  };



  // Apply damage directly (bypass reaction or for enemies)

  // prefixBreakdown: optional pre-resistance breakdown steps from queued damage

  // (e.g. pre-rolled dice, caster stat bonus, bless dice). Prepended to the resistance

  // chain calculateFinalDamage produces so the log has the full receipt from dice → HP.

  const applyDamageDirectly = (targetId, amount, type, sourceName, isRaidwide = false, pendingIdToRemove = null, prefixBreakdown = [], currentCombatStateIn = null, currentCharsStateIn = null) => {

    const currentCombatState = currentCombatStateIn || combat;

    const currentCombatants = currentCombatState.combatants || combatants;

    const currentDamageLog = currentCombatState.damageLog || combat.damageLog || [];

    const currentPendingDamage = currentCombatState.pendingDamage || pendingDamage;
    const currentCharsState = currentCharsStateIn || chars;

    const target = currentCombatants.find(c => c.id === targetId);

    if (!target) return;



    // If target is already at 0 HP with death saves, add failures instead

    if (target.currentHp <= 0 && target.deathSaves && !target.deathSaves.dead) {

      const updatedDs = handleDamageAtZero(targetId, isCrit);

      const logEntry = {

        timestamp: new Date().toISOString(),

        source: sourceName, target: target.name, targetId,

        amount: 0, originalAmount: amount, type,

        isRaidwide, wasCrit: isCrit,

        note: 'Damage at 0 HP — death save failure',

        ...(prefixBreakdown.length > 0 ? { breakdown: prefixBreakdown } : {}),

      };

      const updatedCombatants = updatedDs ? currentCombatants.map(c =>

        c.id === targetId ? { ...c, deathSaves: { ...c.deathSaves, ...updatedDs } } : c

      ) : currentCombatants;

      onUpdateCombat({

        ...currentCombatState,

        combatants: updatedCombatants,

        damageLog: [logEntry, ...currentDamageLog].slice(0, 50),

        ...(pendingIdToRemove ? { pendingDamage: currentPendingDamage.filter(p => p.id !== pendingIdToRemove) } : {}),

      });

      // Check for death after state update

      if (updatedDs && updatedDs.failures >= deathSaveConfig.failuresNeeded) {

        setTimeout(() => checkDeathSaveResult(targetId, updatedDs), 150);

      }

      setDamageAmount(''); setIsCrit(false);

      return;

    }



    const { damage, modifier, percent, breakdown: resistanceBreakdown } = calculateFinalDamage(

      amount,

      type,

      target,

      isRaidwide,

      null,

      null,

      currentCharsState[targetId] || null,

      { skipTargetDebuffPercentReplay: hasPreAppliedTargetDebuffPercent(prefixBreakdown) },

    );

    // Prepend any queued pre-resistance steps (dice rolls, stat bonuses) so the full chain

    // from dice to HP shows in the receipt, not just the resistance/temp-HP portion.

    const breakdown = prefixBreakdown.length > 0 ? [...prefixBreakdown, ...resistanceBreakdown] : resistanceBreakdown;



    // Temp HP absorbs damage first (5e rules)

    const currentTempHp = target.tempHp || 0;

    let tempHpLost = 0;

    let hpDamage = damage;

    let newTempHp = currentTempHp;



    if (currentTempHp > 0 && damage > 0) {

      tempHpLost = Math.min(currentTempHp, damage);

      newTempHp = currentTempHp - tempHpLost;

      hpDamage = damage - tempHpLost;

      // Append temp-HP absorption as its own step so the receipt is complete (P5).

      // Before this, the breakdown stopped at resistance/vulnerability and the absorbed amount

      // only showed up as the top-level `tempHpAbsorbed` field — not a named step in the chain.

      breakdown.push({

        source: `Temp HP absorbed (${currentTempHp} → ${newTempHp})`,

        type: 'tempHp',

        value: `-${tempHpLost}`,

        rawValue: -tempHpLost,

        resultingDamage: hpDamage,

      });

    }



    const newHp = Math.max(0, target.currentHp - hpDamage);

    const wasAlive = target.currentHp > 0;

    const nowAtZero = newHp <= 0;



    let updatedCombatants = currentCombatants.map(c =>

      c.id === targetId ? { ...c, currentHp: newHp, tempHp: newTempHp } : c

    );



    // Temp HP absorption notification

    if (tempHpLost > 0) {

      const absorbMsg = hpDamage > 0

        ? `🩵 ${target.name}'s temp HP absorbs ${tempHpLost} damage (${hpDamage} gets through)`

        : `🩵 ${target.name}'s temp HP absorbs all ${tempHpLost} damage!`;

      setTurnNotifications(prev => [...prev, { id: generateId(), type: 'damage', message: absorbMsg, color: '#87ceeb' }]);

      setTimeout(() => setTurnNotifications([]), 6000);

    }



    // If dropped to 0, initialize death saves

    if (wasAlive && nowAtZero && (target.type === 'player' || target.type === 'npc')) {

      updatedCombatants = initDeathSaves(targetId, updatedCombatants);

      setTurnNotifications(prev => [...prev, { id: generateId(), type: 'damage', message: `💀 ${target.name} drops to 0 HP — making death saves!`, color: '#c92727' }]);

      setTimeout(() => setTurnNotifications([]), 8000);

    }



    // Emit on-enemy-killed across the party (Session 2). Mirrors the applyDamage path above.

    if (wasAlive && nowAtZero && target.type === 'enemy' && systems && chars) {

      emitEnemyKilled({ name: target.name, type: 'enemy', enemyId: targetId, source: sourceName }, updatedCombatants, currentCharsState);

    }



    const logEntry = {

      timestamp: new Date().toISOString(),

      source: sourceName, target: target.name, targetId,

      amount: damage, originalAmount: amount, type, modifier, percent,

      isRaidwide, wasCrit: isCrit,

      tempHpAbsorbed: tempHpLost > 0 ? tempHpLost : undefined,

      breakdown,

    };



    onUpdateCombat({

      ...currentCombatState,

      combatants: updatedCombatants,

      damageLog: [logEntry, ...currentDamageLog].slice(0, 50),

      ...(pendingIdToRemove ? { pendingDamage: currentPendingDamage.filter(p => p.id !== pendingIdToRemove) } : {}),

    });



    // Durability damage for players

    // 2026-04-27: Two bugs fixed here. (1) toggleEquip stores equipped state on

    // char.equippedArmor (an id), not on equipment[].equipped — the old `e.equipped`

    // check never matched, so dura never decreased from combat damage. (2) applyDuraDamage

    // returns the updated equipment fields spread inline, not under an `equipment` key,

    // so destructuring `{ equipment: updatedEquip }` was always undefined.

    if (globalSettings?.durabilityEnabled !== false && target.type === 'player' && currentCharsState[targetId]) {

      const char = currentCharsState[targetId];

      if (char.equipment) {

        const equippedArmor = char.equipment.find(e => e.id === char.equippedArmor && e.type === 'armor');

        if (equippedArmor) {

          const duraDamage = calculateDuraDamage(equippedArmor, type);

          if (duraDamage > 0) {

            const updatedEquip = applyDuraDamage(equippedArmor, duraDamage);

            const updatedEquipment = char.equipment.map(e => e.id === equippedArmor.id ? updatedEquip : e);

            onUpdateChars({ ...currentCharsState, [targetId]: { ...char, equipment: updatedEquipment, currentHp: newHp, tempHp: newTempHp } });

          }

        }

      }

    }



    // Queue concentration check if target is concentrating

    // Note: Full damage triggers conc check even if temp HP absorbed some (5e rules)

    if (damage > 0 && deathSaveConfig.concentrationCheckOnDamage) {

      setTimeout(() => queueConcentrationCheck(targetId, damage), 100);

    }

    

    setDamageAmount('');

    setIsCrit(false);

  };

  const acceptDamage = (pendingId) => {

    const pending = pendingDamage.find(p => p.id === pendingId);

    if (!pending) return;

    // Pass pendingId so applyDamageDirectly removes it in the same onUpdateCombat call.

    // Forward any queued breakdown (pre-resistance dice chain) so the log has the full receipt.

    applyDamageDirectly(pending.targetId, pending.amount, pending.type, pending.source, pending.isRaidwide, pendingId, pending.breakdown || []);

  };



  // Player reacts to pending damage (DM resolves)

  const reactToDamage = (pendingId, newAmount) => {

    const pending = pendingDamage.find(p => p.id === pendingId);

    if (!pending) return;



    // Apply modified damage

    const finalAmount = parseInt(newAmount) || 0;

    if (finalAmount > 0) {

      // Note: we keep the queued breakdown even when amount is modified on react. The breakdown

      // documents the original roll; the reacted amount just reflects mitigation/re-adjudication.

      applyDamageDirectly(pending.targetId, finalAmount, pending.type, `${pending.source} (reacted)`, pending.isRaidwide, pendingId, pending.breakdown || []);

    } else {

      // Zero damage — just remove the pending entry

      onUpdateCombat({ ...combat, pendingDamage: pendingDamage.filter(p => p.id !== pendingId) });

    }

  };

  

  // Cancel pending damage (DM only)

  const cancelPendingDamage = (pendingId) => {

    onUpdateCombat({

      ...combat,

      pendingDamage: pendingDamage.filter(p => p.id !== pendingId),

    });

  };

  

  // Heal target

  const healTarget = (targetId, amount, sourceName = 'DM', spellName = null) => {

    const target = combatants.find(c => c.id === targetId);

    if (!target || !amount) return;

    

    const healAmount = parseInt(amount) || 0;

    const outcome = resolveImmediateHealingOutcome({

      target,

      amount: healAmount,

      deathSaveConfig,

    });

    

    if (outcome.kind === 'heal-check') {

      attemptHealCheck(targetId, healAmount);

      setDamageAmount('');

      return;

    }



    if (outcome.kind === 'revive') {

      const logEntry = {

        timestamp: new Date().toISOString(),

        source: sourceName, target: target.name, targetId,

        amount: outcome.actualHeal, type: 'healing', spellName,

        isHealing: true, isRevive: true,

      };

      

      // Show brief stabilized flash

      setRevivingIds(prev => new Set(prev).add(targetId));

      setTimeout(() => { setRevivingIds(prev => { const n = new Set(prev); n.delete(targetId); return n; }); }, 2000);

      

      // Single update: clear death saves + restore HP + add log

      const updatedCombatants = combatants.map(c =>

        c.id === targetId ? { ...c, currentHp: outcome.finalHp, deathSaves: null, conditions: outcome.conditions } : c

      );

      onUpdateCombat({ ...combat, combatants: updatedCombatants, damageLog: [logEntry, ...(combat.damageLog || [])].slice(0, 50) });

      if (chars[targetId]) {

        onUpdateChars({ ...chars, [targetId]: { ...chars[targetId], currentHp: outcome.finalHp } });

      }

      setTurnNotifications(prev => [...prev, { id: generateId(), type: 'heal', message: `?? ${target.name} stabilized with ${outcome.finalHp} HP!`, color: '#27c95a' }]);

      setTimeout(() => setTurnNotifications([]), 6000);

      setDamageAmount('');

      return;

    }



    if (outcome.kind !== 'heal') {

      setDamageAmount('');

      return;

    }

    

    const updatedCombatants = combatants.map(c => 

      c.id === targetId ? { ...c, currentHp: outcome.finalHp } : c

    );

    

    // Log the healing

    const logEntry = {

      timestamp: new Date().toISOString(),

      source: sourceName,

      target: target.name,

      targetId,

      amount: outcome.actualHeal,

      type: 'healing',

      spellName,

      isHealing: true,

    };



    const updatedChars = chars[targetId]

      ? { ...chars, [targetId]: { ...chars[targetId], currentHp: outcome.finalHp } }

      : chars;

    

    onUpdateCombat({ 

      ...combat, 

      combatants: updatedCombatants,

      damageLog: [logEntry, ...(combat.damageLog || [])].slice(0, 50),

    });

    if (updatedChars !== chars) {

      onUpdateChars(updatedChars);

    }

    setDamageAmount('');

  };

  

  // ============== DEATH SAVE SYSTEM ==============

  

  // Initialize death save state on a combatant

  const initDeathSaves = (combatantId, updatedCombatantsIn = null) => {

    const src = updatedCombatantsIn || combatants;

    const target = src.find(c => c.id === combatantId);

    if (!target) return src;

    

    const conditions = [...(target.conditions || [])];

    if (deathSaveConfig.autoApplyConditions) {

      if (!conditions.includes('Unconscious')) conditions.push('Unconscious');

      if (!conditions.includes('Prone')) conditions.push('Prone');

    }

    

    return src.map(c => c.id === combatantId ? {

      ...c, currentHp: 0, conditions,

      deathSaves: {

        successes: 0, failures: 0, stabilized: false, dead: false,

        shieldActive: false, // Never auto-start — player initiates

        shieldHp: deathSaveConfig.healingCheck.shieldHp,

        shieldBroken: false, healChecksUsed: 0,

      },

    } : c);

  };



  // Update death save data on a combatant

  const updateDeathSaves = (combatantId, updates) => {

    const updatedCombatants = combatants.map(c => {

      if (c.id !== combatantId) return c;

      return { ...c, deathSaves: { ...c.deathSaves, ...updates } };

    });

    onUpdateCombat({ ...combat, combatants: updatedCombatants });

  };



  // Check if thresholds met → revive (with stabilize flash) or death

  const checkDeathSaveResult = (combatantId, ds) => {

    if (ds.successes >= deathSaveConfig.successesNeeded) {

      // Update the saves first, then revive

      const updatedCombatants = combatants.map(c =>

        c.id === combatantId ? { ...c, deathSaves: { ...c.deathSaves, ...ds } } : c

      );

      onUpdateCombat({ ...combat, combatants: updatedCombatants });

      // Revive with brief stabilized flash (delay slightly so save pips show first)

      setTimeout(() => reviveCombatant(combatantId, 1), 600);

      return 'stabilized';

    }

    if (ds.failures >= deathSaveConfig.failuresNeeded && !ds.dead) {

      const target = combatants.find(c => c.id === combatantId);

      setPendingConfirm({

        title: '☠️ Character Death',

        message: `${target?.name} has failed ${deathSaveConfig.failuresNeeded} death saves.`,

        details: 'Confirm death or override.',

        actions: [

          { label: 'Confirm Death', color: '#c92727', onClick: () => {

            const updated = combatants.map(c => {

              if (c.id !== combatantId) return c;

              const conds = [...(c.conditions || [])].filter(cond => cond !== 'Unconscious');

              if (!conds.includes('Dead')) conds.push('Dead');

              return { ...c, deathSaves: { ...c.deathSaves, ...ds, dead: true }, conditions: conds };

            });

            onUpdateCombat({ ...combat, combatants: updated });

            setTurnNotifications(prev => [...prev, { id: generateId(), type: 'damage', message: `☠️ ${target?.name} has died.`, color: '#c92727' }]);

            setTimeout(() => setTurnNotifications([]), 8000);

            setPendingConfirm(null);

          }},

          { label: 'Override — Keep Alive', color: '#666', onClick: () => setPendingConfirm(null) },

        ]

      });

      return 'dead';

    }

    // Just update the saves

    const updatedCombatants = combatants.map(c =>

      c.id === combatantId ? { ...c, deathSaves: { ...c.deathSaves, ...ds } } : c

    );

    onUpdateCombat({ ...combat, combatants: updatedCombatants });

    return 'ongoing';

  };



  // Roll a death save

  const rollDeathSave = (combatantId) => {

    const target = combatants.find(c => c.id === combatantId);

    if (!target?.deathSaves || target.deathSaves.dead) return;

    

    const d20Roll = rollD20WithAdvantage(null, combatantId, 'death_saves');

    const roll = d20Roll.selected;

    const deathSaveModeSuffix = d20Roll.mode !== 'normal' ? `, ${d20Roll.mode}${d20Roll.isSupreme ? '/supreme' : ''}` : '';

    setDeathSaveRollResult({
      combatantId,
      roll,
      allDice: d20Roll.mode !== 'normal' && d20Roll.all.length > 1 ? d20Roll.all : null,
      advMode: d20Roll.mode !== 'normal' ? d20Roll.mode : null,
      timestamp: Date.now(),
    });

    setTimeout(() => setDeathSaveRollResult(null), 3000);

    

    const ds = { ...target.deathSaves };

    

    if (roll === 20) {

      if (deathSaveConfig.nat20Behavior === 'revive') {

        reviveCombatant(combatantId, deathSaveConfig.nat20ReviveHp);

        setTurnNotifications(prev => [...prev, { id: generateId(), type: 'save_pass', message: `🎉 NAT 20! ${target.name} revives with ${deathSaveConfig.nat20ReviveHp} HP!`, color: '#ffd700' }]);

        setTimeout(() => setTurnNotifications([]), 6000);

        // Log it

        const logEntry = { timestamp: new Date().toISOString(), source: target.name, target: target.name, type: 'death_save', amount: roll, spellName: `Death Save (NAT 20 Revive!${deathSaveModeSuffix})`, isDeathSave: true };

        onUpdateCombat({ ...combat, damageLog: [logEntry, ...(combat.damageLog || [])].slice(0, 50) });

        return;

      } else if (deathSaveConfig.nat20Behavior === 'two_successes') {

        ds.successes = Math.min(ds.successes + 2, deathSaveConfig.successesNeeded);

      } else {

        ds.successes = Math.min(ds.successes + 1, deathSaveConfig.successesNeeded);

      }

    } else if (roll === 1) {

      if (deathSaveConfig.nat1Behavior === 'two_failures') {

        ds.failures = Math.min(ds.failures + 2, deathSaveConfig.failuresNeeded);

      } else {

        ds.failures = Math.min(ds.failures + 1, deathSaveConfig.failuresNeeded);

      }

    } else if (roll >= 10) {

      ds.successes = Math.min(ds.successes + 1, deathSaveConfig.successesNeeded);

    } else {

      ds.failures = Math.min(ds.failures + 1, deathSaveConfig.failuresNeeded);

    }

    

    const logEntry = {

      timestamp: new Date().toISOString(), source: target.name, target: target.name,

      type: 'death_save', amount: roll,

      spellName: `Death Save (${roll >= 10 ? 'Success' : 'Failure'}${deathSaveModeSuffix})`, isDeathSave: true,

    };

    const updatedCombatants = combatants.map(c =>

      c.id === combatantId ? { ...c, deathSaves: { ...c.deathSaves, ...ds } } : c

    );

    onUpdateCombat({ ...combat, combatants: updatedCombatants, damageLog: [logEntry, ...(combat.damageLog || [])].slice(0, 50) });

    setTimeout(() => checkDeathSaveResult(combatantId, ds), 100);

  };



  // Manual +1 success / failure

  const addDeathSuccess = (combatantId) => {

    const t = combatants.find(c => c.id === combatantId);

    if (!t?.deathSaves || t.deathSaves.dead) return;

    const ds = { ...t.deathSaves, successes: Math.min(t.deathSaves.successes + 1, deathSaveConfig.successesNeeded) };

    checkDeathSaveResult(combatantId, ds);

  };

  const addDeathFailure = (combatantId) => {

    const t = combatants.find(c => c.id === combatantId);

    if (!t?.deathSaves || t.deathSaves.dead) return;

    const ds = { ...t.deathSaves, failures: Math.min(t.deathSaves.failures + 1, deathSaveConfig.failuresNeeded) };

    checkDeathSaveResult(combatantId, ds);

  };



  // Reviving flash tracker (brief "Stabilized!" overlay)

  const [revivingIds, setRevivingIds] = useState(new Set());

  

  // Revive from unconscious — shows brief "Stabilized!" flash then fully clears death saves

  const reviveCombatant = (combatantId, hp = null) => {

    const target = combatants.find(c => c.id === combatantId);

    if (!target) return;

    const reviveHp = hp !== null ? hp :

      deathSaveConfig.reviveHpMode === 'fixed' ? deathSaveConfig.fixedReviveHp : 1;

    const finalHp = Math.min(Math.max(1, reviveHp), target.maxHp);

    const conditions = (target.conditions || []).filter(c => c !== 'Unconscious' && c !== 'Prone' && c !== 'Dead');

    

    // Show brief stabilized flash

    setRevivingIds(prev => new Set(prev).add(combatantId));

    

    // Immediately clear death saves and restore HP

    const updatedCombatants = combatants.map(c =>

      c.id === combatantId ? { ...c, currentHp: finalHp, deathSaves: null, conditions } : c

    );

    onUpdateCombat({ ...combat, combatants: updatedCombatants });

    if (chars[combatantId]) {

      onUpdateChars({ ...chars, [combatantId]: { ...chars[combatantId], currentHp: finalHp } });

    }

    setTurnNotifications(prev => [...prev, { id: generateId(), type: 'heal', message: `🎉 ${target.name} stabilized with ${finalHp} HP!`, color: '#27c95a' }]);

    setTimeout(() => setTurnNotifications([]), 6000);

    // Clear the reviving flash after 2 seconds

    setTimeout(() => {

      setRevivingIds(prev => { const n = new Set(prev); n.delete(combatantId); return n; });

    }, 2000);

  };



  // Kill

  const killCombatant = (combatantId) => {

    const target = combatants.find(c => c.id === combatantId);

    if (!target) return;

    const conditions = (target.conditions || []).filter(c => c !== 'Unconscious');

    if (!conditions.includes('Dead')) conditions.push('Dead');

    const updatedCombatants = combatants.map(c =>

      c.id === combatantId ? { ...c, currentHp: 0, deathSaves: { ...(c.deathSaves || {}), dead: true, failures: deathSaveConfig.failuresNeeded }, conditions } : c

    );

    onUpdateCombat({ ...combat, combatants: updatedCombatants });

  };



  // Initiate heal check — player activates the shield barrier

  const initiateHealCheck = (combatantId) => {

    const target = combatants.find(c => c.id === combatantId);

    if (!target?.deathSaves || target.deathSaves.dead || target.deathSaves.shieldBroken) return;

    const ds = { ...target.deathSaves, shieldActive: true, shieldHp: deathSaveConfig.healingCheck.shieldHp };

    const updatedCombatants = combatants.map(c => c.id === combatantId ? { ...c, deathSaves: ds } : c);

    onUpdateCombat({ ...combat, combatants: updatedCombatants });

    setTurnNotifications(prev => [...prev, { id: generateId(), type: 'damage', message: `🔒 ${target.name} initiates heal check — ${deathSaveConfig.healingCheck.shieldName} (${deathSaveConfig.healingCheck.shieldHp} HP) is now active!`, color: '#7db5ff' }]);

    setTimeout(() => setTurnNotifications([]), 6000);

  };



  // Attempt healing check — one-shot pass/fail against the barrier threshold

  // heal >= threshold → PASS (1 save success), heal < threshold → FAIL (1 death failure)

  const attemptHealCheck = (combatantId, healAmount) => {

    const target = combatants.find(c => c.id === combatantId);

    if (!target?.deathSaves || target.deathSaves.shieldBroken || target.deathSaves.dead) return;

    

    const ds = { ...target.deathSaves };

    const threshold = ds.shieldHp ?? deathSaveConfig.healingCheck.shieldHp;

    const passed = healAmount >= threshold;

    

    // Always resolve — shield is done after one attempt

    ds.shieldBroken = true;

    ds.shieldActive = false;

    ds.healChecksUsed = (ds.healChecksUsed || 0) + 1;

    

    if (passed) {

      ds.successes = Math.min(ds.successes + 1, deathSaveConfig.successesNeeded);

    } else {

      ds.failures = Math.min(ds.failures + 1, deathSaveConfig.failuresNeeded);

    }

    

    const logEntry = {

      timestamp: new Date().toISOString(),

      source: target.name, target: target.name, targetId: combatantId,

      amount: healAmount, type: 'healing_check',

      spellName: `Heal Check (${passed ? 'PASS' : 'FAIL'} — needed ${threshold})`,

      isHealingCheck: true,

    };

    const updatedCombatants = combatants.map(c => c.id === combatantId ? { ...c, deathSaves: ds } : c);

    onUpdateCombat({ ...combat, combatants: updatedCombatants, damageLog: [logEntry, ...(combat.damageLog || [])].slice(0, 50) });

    

    if (passed) {

      setTurnNotifications(prev => [...prev,

        { id: generateId(), type: 'save_pass', message: `💥 ${target.name} healed for ${healAmount} — barrier broken! Death save success!`, color: '#ffd700' },

      ]);

    } else {

      setTurnNotifications(prev => [...prev,

        { id: generateId(), type: 'damage', message: `🔒 ${target.name} healed for ${healAmount} — not enough! Needed ${threshold}. Death save failure!`, color: '#c92727' },

      ]);

    }

    setTimeout(() => setTurnNotifications([]), 6000);

    setTimeout(() => checkDeathSaveResult(combatantId, ds), 200);

  };



  // Handle damage at 0 HP → returns updated death saves (caller does the update)

  const handleDamageAtZero = (combatantId, wasCrit = false) => {

    const target = combatants.find(c => c.id === combatantId);

    if (!target?.deathSaves || target.deathSaves.dead) return null;

    const failuresToAdd = wasCrit ? deathSaveConfig.critAtZeroFailures : deathSaveConfig.damageAtZeroFailures;

    const ds = { ...target.deathSaves, failures: Math.min(target.deathSaves.failures + failuresToAdd, deathSaveConfig.failuresNeeded) };

    setTurnNotifications(prev => [...prev, {

      id: generateId(), type: 'damage',

      message: `💀 ${target.name} takes damage at 0 HP — ${failuresToAdd} death save failure${failuresToAdd > 1 ? 's' : ''}!`,

      color: '#c92727'

    }]);

    setTimeout(() => setTurnNotifications([]), 6000);

    return ds;

  };



  // ============== TURN LIFECYCLE SYSTEM ==============

  

  // Break concentration for a caster - removes all linked effects everywhere

  const breakConcentration = (

    casterId,

    reason = 'Concentration broken',

    updatedCombatantsIn = null,

    updatedCharsIn = null,

    queuedRef = null,

  ) => {

    const casterChar = (updatedCharsIn || chars)[casterId];

    const spellName = queuedRef?.spellName || casterChar?.concentratingOn?.name || casterChar?.concentratingOn || null;

    const effectGroupId = queuedRef?.effectGroupId || casterChar?.concentrationEffectGroupId || null;

    if (!casterChar || (!spellName && !effectGroupId)) {

      return { combatants: updatedCombatantsIn || combatants, chars: updatedCharsIn || chars, notifications: [] };

    }



    const removalResult = removeLinkedConcentrationState({

      casterId,

      spellName,

      effectGroupId,

      combatants: updatedCombatantsIn || combatants,

      chars: updatedCharsIn || chars,

    });



    setLifecycleQueue(prev => removeQueuedConcentrationItems(prev, {

      casterId,

      spellName,

      effectGroupId,

    }));



    const notifications = [{

      id: generateId(),

      type: 'concentration',

      message: `⚡ ${casterChar.name}'s ${spellName || 'concentration'} — ${reason}`,

      color: '#ff9800',

    }];



    removalResult.removedTargetIds

      .filter((targetId) => targetId !== casterId)

      .forEach((targetId) => {

        const targetName = removalResult.combatants.find((combatant) => combatant.id === targetId)?.name

          || removalResult.chars[targetId]?.name

          || targetId;

        notifications.push({

          id: generateId(),

          type: 'expire',

          message: `${spellName || 'Effect'} ended on ${targetName}`,

          color: '#e74c3c',

        });

      });



    return {

      combatants: removalResult.combatants,

      chars: removalResult.chars,

      notifications,

    };

  };



  const createCombatRuntimeEffectRef = ({

    effectId = null,

    effectGroupId = null,

    sourceCollection = null,

    effectIndex = null,

    effectName = null,

    casterId = null,

  } = {}) => createRuntimeEffectRef({

    effectId,

    effectGroupId,

    sourceCollection,

    effectIndex,

    effectName,

    casterId,

  });



  const getCombatTickKey = (combatantId, tickType, effectRef = null, fallbackName = '') => (

    effectRef

      ? getRuntimeTickKey(combatantId, tickType, effectRef)

      : `${combatantId}-${tickType}-${fallbackName}`

  );



  const removeRecurringRuntimeEffect = (combatantId, effectRef = null, effectName = '') => (

    removeRuntimeEffectInstance({

      combatants,

      chars,

      combatantId,

      ref: effectRef || createCombatRuntimeEffectRef({ effectName }),

    })

  );



  const getQueueItemEffectRef = (item = {}) => createCombatRuntimeEffectRef({

    effectId: item.effectId || null,

    effectGroupId: item.effectGroupId || null,

    sourceCollection: item.sourceCollection || null,

    effectIndex: Number.isInteger(item.effectIndex) ? item.effectIndex : null,

    effectName: item.effectName || null,

    casterId: item.casterId || null,

  });



  const getQueueItemTickType = (item = {}) => {

    if (item.type === 'dot_tick') return 'dot';

    if (item.type === 'hot_tick') return 'hot';

    if (item.type === 'temp_hp_tick') return 'tempHp';

    if (item.type === 'recurring_save') return 'save_only';

    return null;

  };



  const getQueueItemTickKey = (item = {}, tickTypeOverride = null) => {

    const tickType = tickTypeOverride || getQueueItemTickType(item);

    if (!tickType) return null;

    return getCombatTickKey(item.combatantId, tickType, getQueueItemEffectRef(item), item.effectName || '');

  };

  

  // Queue concentration save check after damage (user confirms the drop)

  const queueConcentrationCheck = (targetId, damageAmount) => {

    const targetChar = chars[targetId];

    if (!targetChar || !targetChar.concentratingOn) return;

    

    const dc = Math.max(10, Math.floor(damageAmount / 2));

    // Check for War Caster advantage on concentration saves

    const hasWarCaster = (targetChar.feats || []).some(f => f.toLowerCase().includes('war caster'));

    const effectiveMode = hasWarCaster ? 'advantage' : null;

    const saveRoll = rollResolvedCombatSave(targetId, 'con', effectiveMode, 'concentration');

    if (!saveRoll) return;

    const { d20Roll, d20, modifier, total, bonusDiceRolls } = saveRoll;

    const passed = total >= dc;

    const spellName = targetChar.concentratingOn;

    const concentrationEffectGroupId = targetChar.concentrationEffectGroupId || null;

    

    // Build roll text with advantage display

    let rollDisplay = '';

    if (d20Roll.mode !== 'normal' && d20Roll.all.length > 1) {

      rollDisplay = d20Roll.all.map(v => v === d20 ? `**${v}**` : `~~${v}~~`).join('/');

    } else {

      rollDisplay = `${d20}`;

    }

    const buffStr = bonusDiceRolls.map((bonus) => `+${bonus.total}(${bonus.source})`).join('');

    const rollText = `${rollDisplay}${modifier >= 0 ? '+' : ''}${modifier}${buffStr} = ${total} vs DC ${dc}`;

    const modeTag = d20Roll.mode !== 'normal' ? ` (${d20Roll.mode}${d20Roll.isSupreme ? '/supreme' : ''})` : '';

    

    if (passed) {

      setTurnNotifications(prev => [...prev, {

        id: generateId(), type: 'save_pass', color: '#27c95a',

        message: `✅ ${targetChar.name} maintains ${spellName} (CON: ${rollText})${modeTag}`

      }]);

      // 2026-04-27: 6s was too short for users to read the save result.

      // Bumped to 15s; the ✕ dismiss button on each notification still works.

      setTimeout(() => setTurnNotifications([]), 15000);

    } else {

      setLifecycleQueue(prev => [...prev, {

        id: generateId(),

        type: 'concentration_fail',

        title: `⚡ Drop Concentration?`,

        message: `${targetChar.name} failed CON save for ${spellName} (${rollText})${modeTag}`,

        color: '#ff9800',

        casterId: targetId,

        spellName,

        effectGroupId: concentrationEffectGroupId,

        onConfirm: () => {

          const result = breakConcentration(targetId, `failed CON save (${rollText})`, null, null, {

            spellName,

            effectGroupId: concentrationEffectGroupId,

          });

          onUpdateCombat({ ...combat, combatants: result.combatants });

          onUpdateChars(result.chars);

          setTurnNotifications(prev => [...prev, ...result.notifications]);

          setTimeout(() => setTurnNotifications([]), 6000);

        },

        confirmLabel: 'Drop',

        cancelLabel: 'Keep (Override)',

      }]);

    }

  };

  

  // Process effect expiration for a given round - queues confirmations for each expired effect

  const processEffectExpiration = (currentRound, updatedCombatantsIn = null, updatedCharsIn = null) => {

    let updatedCombatants = updatedCombatantsIn || [...combatants];

    let updatedChars = { ...(updatedCharsIn || chars) };

    const notifications = [];

    const queueItems = [];

    

    // 1. Find timed debuffs on combatants that are due to expire

    updatedCombatants.forEach(c => {

      (c.debuffs || []).forEach((d, i) => {

        if (typeof d.duration === 'number' && d.roundApplied != null) {

          const elapsed = currentRound - d.roundApplied;

          if (elapsed >= d.duration) {

            const runtimeIdentity = buildSpellRuntimeEffectIdentity(d, 'combatant.debuffs', i);

            queueItems.push({

              id: generateId(),

              type: 'effect_expire',

              title: `⏱️ Effect Expired`,

              message: `${d.name} on ${c.name} has expired (${d.duration} rounds). Remove?`,

              color: '#888',

              combatantId: c.id,

              effectName: d.name,

              effectId: runtimeIdentity.effectId,

              effectGroupId: runtimeIdentity.effectGroupId,

              sourceCollection: runtimeIdentity.sourceCollection,

              effectIndex: runtimeIdentity.effectIndex,

              effectType: 'debuff',

              confirmLabel: 'Remove',

              cancelLabel: 'Extend +1',

            });

          }

        }

      });

    });

    

    // 2. Find timed activeEffects on characters due to expire

    Object.keys(updatedChars).forEach(charId => {

      const char = updatedChars[charId];

      if (!char.activeEffects || char.activeEffects.length === 0) return;

      

      char.activeEffects.forEach((e, i) => {

        if (typeof e.duration === 'number' && e.roundApplied != null) {

          const elapsed = currentRound - e.roundApplied;

          if (elapsed >= e.duration) {

            const runtimeIdentity = buildSpellRuntimeEffectIdentity(e, 'char.activeEffects', i);

            queueItems.push({

              id: generateId(),

              type: 'buff_expire',

              title: `⏱️ Buff Expired`,

              message: `${e.name} on ${char.name} has expired (${e.duration} rounds). Remove?`,

              color: '#4a90d9',

              charId: charId,

              effectName: e.name,

              effectId: runtimeIdentity.effectId,

              effectGroupId: runtimeIdentity.effectGroupId,

              sourceCollection: runtimeIdentity.sourceCollection,

              effectIndex: runtimeIdentity.effectIndex,

              legacyConditionName: e.legacyConditionName || e.legacyBuffName || e.name,

              effectType: 'activeEffect',

              confirmLabel: 'Remove',

              cancelLabel: 'Extend +1',

            });

          }

        }

      });

    });

    

    return { combatants: updatedCombatants, chars: updatedChars, notifications, queueItems };

  };

  

  // Next turn - enhanced with lifecycle hooks

  const nextTurn = () => {

    if (!allInitiativesSet || sortedCombatants.length === 0) return;

    

    let newIndex = (combat.turnIndex + 1) % sortedCombatants.length;

    let newRound = combat.round;

    const isNewRound = newIndex === 0;

    if (isNewRound) newRound += 1;

    

    const allNotifications = [];

    const allQueueItems = [];

    

    // --- PROCESS EFFECT EXPIRATION ---

    // At the start of each new round, queue expired effects for DM confirmation

    let processedCombatants = combatants;

    let processedChars = chars;

    

    if (isNewRound) {

      const expResult = processEffectExpiration(newRound, processedCombatants, processedChars);

      processedCombatants = expResult.combatants;

      processedChars = expResult.chars;

      allNotifications.push(...expResult.notifications);

      allQueueItems.push(...expResult.queueItems);

    }

    

    // --- CHECK UNCONSCIOUS CONCENTRATION ---

    // If the departing combatant is at 0 HP and concentrating, queue confirmation

    const departingCombatant = sortedCombatants[combat.turnIndex];

    if (departingCombatant) {

      const dcId = departingCombatant.id;

      const departingChar = processedChars[dcId];

      if (departingChar?.concentratingOn && departingCombatant.currentHp <= 0) {

        allQueueItems.push({

          id: generateId(),

          type: 'concentration_unconscious',

          title: `⚡ Unconscious — Drop Concentration?`,

          message: `${departingChar.name} is unconscious. Drop ${departingChar.concentratingOn}?`,

          color: '#ff9800',

          casterId: dcId,

          spellName: departingChar.concentratingOn,

          effectGroupId: departingChar.concentrationEffectGroupId || null,

          confirmLabel: 'Drop',

          cancelLabel: 'Keep',

        });

      }

    }

    

    // --- UNIFIED LIFECYCLE: QUEUE TICKS AND SAVES ---

    // Both ticks (DoT/HoT/TempHP) and saves are queued based on their respective timings.

    // Global tick timing rules from combat config can override per-spell settings.

    // Order: ticks first, then saves (damage applies before save opportunity).

    

    const queuedKeys = new Set(); // Prevent duplicate prompts per effect+combatant+type

    

    // Resolve effective tick timing: global override > per-spell setting

    const resolveTickTiming = (eff) => {

      const isHealType = eff.type === 'hot' || eff.type === 'tempHp';

      const globalSetting = isHealType ? deathSaveConfig.hotTickTiming : (eff.type === 'dot' ? deathSaveConfig.dotTickTiming : 'per_spell');

      if (!globalSetting || globalSetting === 'per_spell') return eff.tickTiming || 'start_of_target_turn';

      return globalSetting;

    };

    

    // Helper: queue all matching ticks for a combatant at a given timing

    const queueTicksForTiming = (comb, effects, timing, context) => {

      effects.forEach(eff => {

        const effTickTiming = resolveTickTiming(eff);

        if (effTickTiming !== timing) return;

        if (eff.type === 'save_only') return; // Save-only effects have no tick

        

        const key = `${comb.id}-tick-${getRuntimeEffectRefKey(eff.effectRef || createCombatRuntimeEffectRef({ effectName: eff.name }))}`;

        if (queuedKeys.has(key)) return;

        queuedKeys.add(key);

        

        if (eff.type === 'dot') {

          const isDice = typeof eff.amount === 'string' && String(eff.amount).match(/d/i);

          allQueueItems.push({

            id: generateId(),

            type: 'dot_tick',

            title: `🔥 ${eff.name} — ${eff.amount} ${eff.damageType || ''} damage`,

            message: `${comb.name}${context}: ${eff.name} deals ${eff.amount} ${eff.damageType || ''} damage${isDice ? ' (roll)' : ''}.`,

            color: '#e74c3c',

            combatantId: comb.id,

            effectName: eff.name,

            effectId: eff.effectId,

            effectGroupId: eff.effectGroupId,

            sourceCollection: eff.sourceCollection,

            effectIndex: eff.effectIndex,

            amount: eff.amount,

            damageType: eff.damageType,

            confirmLabel: `🔥 Apply ${eff.amount}`,

            cancelLabel: 'Skip',

          });

        } else if (eff.type === 'hot') {

          const isDice = typeof eff.amount === 'string' && String(eff.amount).match(/d/i);

          allQueueItems.push({

            id: generateId(),

            type: 'hot_tick',

            title: `➕ ${eff.name} — ${eff.amount} healing`,

            message: `${comb.name}${context}: ${eff.name} heals ${eff.amount} HP${isDice ? ' (roll)' : ''}.`,

            color: '#27c95a',

            combatantId: comb.id,

            effectName: eff.name,

            effectId: eff.effectId,

            effectGroupId: eff.effectGroupId,

            sourceCollection: eff.sourceCollection,

            effectIndex: eff.effectIndex,

            amount: eff.amount,

            confirmLabel: `➕ Heal ${eff.amount}`,

            cancelLabel: 'Skip',

          });

        } else if (eff.type === 'tempHp') {

          allQueueItems.push({

            id: generateId(),

            type: 'temp_hp_tick',

            title: `🩵 ${eff.name} — ${eff.amount} temp HP`,

            message: `${comb.name}${context}: ${eff.name} grants ${eff.amount} temporary HP.`,

            color: '#87ceeb',

            combatantId: comb.id,

            effectName: eff.name,

            effectId: eff.effectId,

            effectGroupId: eff.effectGroupId,

            sourceCollection: eff.sourceCollection,

            effectIndex: eff.effectIndex,

            amount: eff.amount,

            confirmLabel: `🩵 Grant ${eff.amount} temp HP`,

            cancelLabel: 'Skip',

          });

        }

      });

    };

    

    // Helper: queue all matching saves for a combatant at a given timing

    const queueSavesForTiming = (comb, effects, timing, context) => {

      effects.forEach(eff => {

        if (!eff.saveType || !eff.saveDC) return;

        const effSaveTiming = eff.saveTiming || 'end_of_target_turn';

        if (effSaveTiming !== timing) return;

        

        const key = `${comb.id}-save-${getRuntimeEffectRefKey(eff.effectRef || createCombatRuntimeEffectRef({ effectName: eff.name }))}`;

        if (queuedKeys.has(key)) return;

        queuedKeys.add(key);

        

        allQueueItems.push({

          id: generateId(),

          type: 'recurring_save',

          title: `🎲 ${(eff.saveType || 'DEX').toUpperCase()} Save DC ${eff.saveDC} — ${eff.name}`,

          message: `${comb.name}${context}: ${(eff.saveType || 'DEX').toUpperCase()} save DC ${eff.saveDC} to end ${eff.name}?`,

          color: '#c9a227',

          combatantId: comb.id,

          effectName: eff.name,

          effectId: eff.effectId,

          effectGroupId: eff.effectGroupId,

          sourceCollection: eff.sourceCollection,

          effectIndex: eff.effectIndex,

          saveType: eff.saveType,

          saveDC: eff.saveDC,

          saveRemovesEffect: eff.saveRemovesEffect,

          confirmLabel: '✓ Passed',

          cancelLabel: '✕ Failed',

        });

      });

    };

    

    // === ROUND-LEVEL TICKS: start_of_round / end_of_round ===

    // When global timing is round-based, queue ALL ticks for ALL combatants at round transitions

    if (isNewRound) {

      // End of round (last turn just ended) — queue end_of_round ticks

      processedCombatants.forEach(c => {

        const cEffects = getTickEffects(c);

        queueTicksForTiming(c, cEffects, 'end_of_round', ` (end of round ${combat.round})`);

        queueSavesForTiming(c, cEffects, 'end_of_round', ` (end of round ${combat.round})`);

      });

      // Start of new round — queue start_of_round ticks

      processedCombatants.forEach(c => {

        const cEffects = getTickEffects(c);

        queueTicksForTiming(c, cEffects, 'start_of_round', ` (start of round ${newRound})`);

        queueSavesForTiming(c, cEffects, 'start_of_round', ` (start of round ${newRound})`);

      });

    }

    

    // === DEPARTING COMBATANT (end of their turn) ===

    if (departingCombatant) {

      const departingEffects = getTickEffects(departingCombatant);

      

      // End-of-target-turn: ticks first, then saves

      queueTicksForTiming(departingCombatant, departingEffects, 'end_of_target_turn', "'s turn ending");

      queueSavesForTiming(departingCombatant, departingEffects, 'end_of_target_turn', "'s turn ending");

      

      // End-of-CASTER-turn: effects on OTHER combatants where departing is the caster

      processedCombatants.forEach(c => {

        if (c.id === departingCombatant.id) return;

        const cEffects = getTickEffects(c).filter(eff => eff.casterId === departingCombatant.id);

        queueTicksForTiming(c, cEffects, 'end_of_caster_turn', ` (${departingCombatant.name}'s turn ending)`);

        queueSavesForTiming(c, cEffects, 'end_of_caster_turn', ` (${departingCombatant.name}'s turn ending)`);

      });

    }

    

    // === ARRIVING COMBATANT (start of their turn) ===

    const nextCombatantForEffects = sortedCombatants[newIndex];

    if (nextCombatantForEffects) {

      const arrEffects = getTickEffects(nextCombatantForEffects);

      

      // Start-of-target-turn: ticks first, then saves

      queueTicksForTiming(nextCombatantForEffects, arrEffects, 'start_of_target_turn', "'s turn starting");

      queueSavesForTiming(nextCombatantForEffects, arrEffects, 'start_of_target_turn', "'s turn starting");

      

      // Start-of-CASTER-turn: effects on OTHER combatants where arriving is the caster

      processedCombatants.forEach(c => {

        if (c.id === nextCombatantForEffects.id) return;

        const cEffects = getTickEffects(c).filter(eff => eff.casterId === nextCombatantForEffects.id);

        queueTicksForTiming(c, cEffects, 'start_of_caster_turn', ` (${nextCombatantForEffects.name}'s turn starting)`);

        queueSavesForTiming(c, cEffects, 'start_of_caster_turn', ` (${nextCombatantForEffects.name}'s turn starting)`);

      });

    }

    

    // --- UPDATE TURN MARKERS ---

    const nextCombatant = sortedCombatants[newIndex];

    const updatedCombatants = processedCombatants.map((c) => ({

      ...c,

      isCurrentTurn: nextCombatant?.id === c.id

    }));

    

    // --- RESET TICK TRACKING for the new turn ---

    setTickedThisTurn({});

    

    // --- QUEUE DEATH SAVE PROMPT for dying combatants ---

    if (nextCombatant && nextCombatant.currentHp <= 0 && nextCombatant.deathSaves && 

        !nextCombatant.deathSaves.dead) {

      // Check if healing check is available for player to initiate

      const canHealCheck = deathSaveConfig.healingCheck.enabled && 

        !nextCombatant.deathSaves.shieldActive &&

        !nextCombatant.deathSaves.shieldBroken;

      

      allQueueItems.push({

        id: generateId(),

        type: 'death_save',

        title: `💀 Death Save — ${nextCombatant.name}`,

        message: canHealCheck 

          ? `${nextCombatant.name} is dying. Roll death save or initiate a heal check.`

          : nextCombatant.deathSaves.shieldActive

          ? `${nextCombatant.name} is dying. Shield active — awaiting healing. Roll death save or wait.`

          : `${nextCombatant.name} is dying. Roll a death save.`,

        color: '#c92727',

        combatantId: nextCombatant.id,

        confirmLabel: '🎲 Roll Death Save',

        cancelLabel: 'Skip',

        canHealCheck,

      });

    }

    

    // --- ADD TURN NOTIFICATION ---

    if (nextCombatant) {

      const turnColor = nextCombatant.type === 'enemy' ? '#c92727' : 

                        nextCombatant.type === 'npc' ? '#27c95a' : '#4a90d9';

      const icon = nextCombatant.type === 'enemy' ? '👹' : nextCombatant.type === 'npc' ? '🤝' : '🧝';

      allNotifications.unshift({ 

        id: generateId(), 

        type: 'turn', 

        message: `${icon} ${nextCombatant.name}'s turn${isNewRound ? ` — Round ${newRound}` : ''}`, 

        color: turnColor 

      });

    }

    

    // Set notifications (auto-clear after 8 seconds, longer if queue items)

    setTurnNotifications(allNotifications);

    const clearDelay = allQueueItems.length > 0 ? 15000 : 6000;

    if (allNotifications.length > 0) {

      setTimeout(() => setTurnNotifications([]), clearDelay);

    }

    

    // Set lifecycle queue items for DM confirmation

    if (allQueueItems.length > 0) {

      setLifecycleQueue(prev => [...prev, ...allQueueItems]);

    }

    

    // --- Systems expansion Session 4: on-turn-end / on-round-end + turn/round expiry ---

    // Fire on-turn-end for the departing combatant (turn just ended). Fire on-round-end

    // for every party char if a round boundary was crossed. Tick expiresIn.turns on the

    // departing char, and expiresIn.rounds on every party char at round end. All merged

    // into processedChars before commit so a single onUpdateChars covers everything.

    const systemNotifications = [];

    let systemChars = processedChars;



    const combatLifecycleSideEffectBundles = [];

    const applyRuleAndExpiry = (partyId, trigger, tickKind) => {

      const partyChar = systemChars[partyId];

      if (!partyChar || !(partyChar.systemList || []).length) return;

      if (!systems) return;

      const worldContext = { chars: systemChars, journal, currentLocation: combat?.currentLocation, npcLibrary };

      const triggerContext = { round: newRound };

      const ruleResults = processAllSystemRulesForTrigger(trigger, partyChar, systems, triggerContext, worldContext);

      const ruleUpdates = applySystemRuleResults(partyChar, ruleResults, systems, worldContext);

      let nextChar = Object.keys(ruleUpdates).length > 0 ? { ...partyChar, ...ruleUpdates } : partyChar;

      const ruleLabel = trigger === 'on-turn-end'
        ? `${partyChar.name || 'Combatant'} turn-end rule`
        : `${partyChar.name || 'Combatant'} round-end rule`;
      const ruleSource = trigger === 'on-turn-end' ? 'combat.turn-end' : 'combat.round-end';
      combatLifecycleSideEffectBundles.push({
        results: ruleResults,
        charForEffects: nextChar,
        label: ruleLabel,
        source: ruleSource,
        statusAuditAction: `${ruleSource}-status`,
        triggerContext,
        worldContext,
        prefix: `${partyChar.name || 'Combatant'}: `,
      });

      // Tick matching expiries AFTER on-X-end rules run so a rule with `expiresIn: rounds: 1`

      // that fires in its last round still gets to execute before removal.

      if (tickKind) {

        const expiry = processSystemLifecycleExpiries({
          char: nextChar,
          tickKind,
          systems,
          worldContext,
        });

        if (expiry.nextChar !== nextChar) {

          nextChar = expiry.nextChar;
          combatLifecycleSideEffectBundles.push({
            results: expiry,
            charForEffects: nextChar,
            label: `${partyChar.name || 'Combatant'} ${tickKind === 'turns' ? 'turn' : 'round'} expiry`,
            source: tickKind === 'turns' ? 'combat.turn-expiry' : 'combat.round-expiry',
            statusAuditAction: tickKind === 'turns' ? 'combat.turn-expiry-status' : 'combat.round-expiry-status',
            triggerContext,
            worldContext,
            prefix: `${partyChar.name || 'Combatant'}: `,
          });

        }

      }

      // 2026-04-29 — BUG-REPORTS.md #7: clear cooldowns at the matching scope.

      // turn → on turn end of the owning combatant; round → on round boundary

      // for every party char. Without this, the editor's "Cooldown: turn / round"

      // dropdowns expired only on short rest.

      const cooldownScope = trigger === 'on-turn-end' ? 'turn' : trigger === 'on-round-end' ? 'round' : null;

      if (cooldownScope) {

        const clearedList = clearCooldownsAtScopes(nextChar.systemList, scopesClearedBy(cooldownScope));

        if (clearedList !== nextChar.systemList) nextChar = { ...nextChar, systemList: clearedList };

      }

      if (nextChar !== partyChar) {

        systemChars = { ...systemChars, [partyId]: nextChar };

      }

    };



    // on-turn-end for the departing combatant only

    if (departingCombatant && (departingCombatant.type === 'player' || departingCombatant.type === 'npc')) {

      applyRuleAndExpiry(departingCombatant.id, 'on-turn-end', 'turns');

    }



    // on-round-end for every party char at round boundary

    if (isNewRound) {

      sortedCombatants

        .filter(cb => cb.type === 'player' || cb.type === 'npc')

        .forEach(cb => applyRuleAndExpiry(cb.id, 'on-round-end', 'rounds'));

    }



    // Session 5 — apply any side effects emitted during on-turn-end / on-round-end.

    let nextNpcLibraryForLifecycle = npcLibrary;
    combatLifecycleSideEffectBundles.forEach((bundle) => {
      const worldContextForBundle = {
        ...(bundle.worldContext || {}),
        chars: systemChars,
        npcLibrary: nextNpcLibraryForLifecycle,
      };
      const followUp = consumeCombatResourceFollowUpSideEffects(bundle.results, {
        charForEffects: bundle.charForEffects,
        label: bundle.label,
        source: bundle.source,
        triggerContext: bundle.triggerContext,
        worldContext: worldContextForBundle,
        statusAuditAction: bundle.statusAuditAction,
        npcLibraryForEffects: nextNpcLibraryForLifecycle,
      });
      if (followUp.sideEffects?.npcLibraryChanged) {
        nextNpcLibraryForLifecycle = followUp.sideEffects.nextNpcLibrary;
      }
      appendCombatRuleSideEffectNotices(systemNotifications, followUp, {
        label: bundle.label,
        prefix: bundle.prefix,
      });
    });



    if (systemNotifications.length > 0) {

      setTurnNotifications(prev => [...prev, ...systemNotifications.map(msg => ({

        id: generateId(),

        type: 'system',

        message: `⚙️ ${msg}`,

        color: '#9d4edd',

      }))]);

      setTimeout(() => setTurnNotifications([]), 8000);

    }



    // Commit state updates

    if (systemChars !== chars) {

      onUpdateChars(systemChars);

    }



    onUpdateCombat({

      ...combat,

      combatants: updatedCombatants,

      turnIndex: newIndex,

      round: newRound,

    });

  };



  // === LIFECYCLE QUEUE HANDLERS ===

  const handleLifecycleConfirm = (item) => {

    if (item.type === 'effect_expire') {

      const updatedCombatants = removeRuntimeEffectInstance({
        combatants,
        chars,
        combatantId: item.combatantId,
        ref: getQueueItemEffectRef(item),
      }).combatants;

      onUpdateCombat({ ...combat, combatants: updatedCombatants });

      setTurnNotifications(prev => [...prev, { id: generateId(), type: 'expire', message: `${item.effectName} removed from ${combatants.find(c => c.id === item.combatantId)?.name}`, color: '#888' }]);

    } else if (item.type === 'buff_expire') {

      const removalResult = removeRuntimeEffectInstance({
        combatants,
        chars,
        combatantId: item.charId,
        ref: getQueueItemEffectRef(item),
      });

      const combatantIndex = removalResult.combatants.findIndex((combatant) => combatant.id === item.charId);
      let updatedCombatants = removalResult.combatants;
      if (combatantIndex >= 0) {
        const targetCombatant = updatedCombatants[combatantIndex];
        const { nextConditions, removed } = removeSingleSpellRuntimeCondition(
          targetCombatant.conditions,
          item.legacyConditionName || item.effectName,
        );
        if (removed) {
          updatedCombatants = updatedCombatants.map((combatant, index) => (
            index === combatantIndex
              ? { ...combatant, conditions: nextConditions }
              : combatant
          ));
        }
      }

      onUpdateCombat({ ...combat, combatants: updatedCombatants });
      onUpdateChars(removalResult.chars);

      setTurnNotifications(prev => [...prev, { id: generateId(), type: 'expire', message: `${item.effectName} removed from ${chars[item.charId]?.name}`, color: '#4a90d9' }]);

    } else if (item.type === 'concentration_fail' || item.type === 'concentration_unconscious') {

      // Break concentration

      const result = breakConcentration(item.casterId, item.type === 'concentration_unconscious' ? 'unconscious' : 'failed save', null, null, {

        spellName: item.spellName || null,

        effectGroupId: item.effectGroupId || null,

      });

      onUpdateCombat({ ...combat, combatants: result.combatants });

      onUpdateChars(result.chars);

      setTurnNotifications(prev => [...prev, ...result.notifications]);

    } else if (item.type === 'dot_tick') {

      // Apply damage over time (supports dice strings)

      const target = combatants.find(c => c.id === item.combatantId);

      if (target) {

        const tickKey = getQueueItemTickKey(item, 'dot');

        const isDice = typeof item.amount === 'string' && item.amount.match(/d/i);

        const rolled = isDice ? rollDice(item.amount) : null;

        const resolvedAmount = rolled ? rolled.total : (typeof item.amount === 'number' ? item.amount : parseInt(item.amount) || 0);

        

        // Absorb from temp HP

        const currentTempHp = target.tempHp || 0;

        let tempHpLost = 0;

        let newTempHp = currentTempHp;

        let hpDamage = resolvedAmount;

        if (currentTempHp > 0 && resolvedAmount > 0) {

          tempHpLost = Math.min(currentTempHp, resolvedAmount);

          newTempHp = currentTempHp - tempHpLost;

          hpDamage = resolvedAmount - tempHpLost;

        }

        const newHp = Math.max(0, target.currentHp - hpDamage);

        const updatedCombatants = combatants.map(c => 

          c.id === item.combatantId ? { ...c, currentHp: newHp, tempHp: newTempHp } : c

        );

        const logEntry = {

          timestamp: new Date().toISOString(),

          source: item.effectName,

          target: target.name,

          amount: resolvedAmount,

          type: item.damageType || 'untyped',

          spellName: `${item.effectName} (DoT${isDice ? ` ${item.amount}` : ''})`,

          isDot: true,

          tempHpAbsorbed: tempHpLost > 0 ? tempHpLost : undefined,

        };

        onUpdateCombat({

          ...combat,

          combatants: updatedCombatants,

          damageLog: [logEntry, ...(combat.damageLog || [])].slice(0, 50),

        });

        if (chars[item.combatantId]) {

          onUpdateChars({ ...chars, [item.combatantId]: { ...chars[item.combatantId], currentHp: newHp, tempHp: newTempHp } });

        }

        const absorbNote = tempHpLost > 0 ? ` (🩵${tempHpLost} absorbed)` : '';

        setTurnNotifications(prev => [...prev, { id: generateId(), type: 'damage', message: `🔥 ${target.name} takes ${resolvedAmount} ${item.damageType || ''} from ${item.effectName}${absorbNote}`, color: '#e74c3c' }]);

        setTickedThisTurn(prev => ({ ...prev, [tickKey]: true }));

      }

    } else if (item.type === 'hot_tick') {

      // Apply healing over time (supports dice strings)

      const target = combatants.find(c => c.id === item.combatantId);

      if (target) {

        const tickKey = getQueueItemTickKey(item, 'hot');

        const isDice = typeof item.amount === 'string' && item.amount.match(/d/i);

        const rolled = isDice ? rollDice(item.amount) : null;

        const resolvedAmount = rolled ? rolled.total : (typeof item.amount === 'number' ? item.amount : parseInt(item.amount) || 0);

        

        const newHp = Math.min(target.maxHp, target.currentHp + resolvedAmount);

        const updatedCombatants = combatants.map(c => 

          c.id === item.combatantId ? { ...c, currentHp: newHp } : c

        );

        const logEntry = {

          timestamp: new Date().toISOString(),

          source: item.effectName,

          target: target.name,

          amount: resolvedAmount,

          type: 'healing',

          spellName: `${item.effectName} (HoT${isDice ? ` ${item.amount}` : ''})`,

          isHealing: true,

          isHot: true,

        };

        onUpdateCombat({

          ...combat,

          combatants: updatedCombatants,

          damageLog: [logEntry, ...(combat.damageLog || [])].slice(0, 50),

        });

        if (chars[item.combatantId]) {

          onUpdateChars({ ...chars, [item.combatantId]: { ...chars[item.combatantId], currentHp: newHp } });

        }

        setTurnNotifications(prev => [...prev, { id: generateId(), type: 'heal', message: `➕ ${target.name} heals ${resolvedAmount} HP from ${item.effectName}`, color: '#27c95a' }]);

        setTickedThisTurn(prev => ({ ...prev, [tickKey]: true }));

      }

    } else if (item.type === 'temp_hp_tick') {

      // Apply temp HP over time (e.g., Heroism)

      const target = combatants.find(c => c.id === item.combatantId);

      if (target) {

        const tickKey = getQueueItemTickKey(item, 'tempHp');

        const currentTemp = target.tempHp || 0;

        const newTemp = Math.max(currentTemp, item.amount);

        const updatedCombatants = combatants.map(c => 

          c.id === item.combatantId ? { ...c, tempHp: newTemp } : c

        );

        const logEntry = {

          timestamp: new Date().toISOString(),

          source: item.effectName,

          target: target.name,

          amount: item.amount,

          type: 'temp_hp',

          spellName: `${item.effectName} (Temp HP)`,

          isTempHp: true,

        };

        onUpdateCombat({

          ...combat,

          combatants: updatedCombatants,

          damageLog: [logEntry, ...(combat.damageLog || [])].slice(0, 50),

        });

        if (chars[item.combatantId]) {

          onUpdateChars({ ...chars, [item.combatantId]: { ...chars[item.combatantId], tempHp: newTemp } });

        }

        setTurnNotifications(prev => [...prev, { id: generateId(), type: 'heal', message: `🩵 ${target.name} gains ${item.amount} temp HP from ${item.effectName}`, color: '#87ceeb' }]);

        setTickedThisTurn(prev => ({ ...prev, [tickKey]: true }));

      }

    } else if (item.type === 'death_save') {

      // Roll death save for the combatant

      rollDeathSave(item.combatantId);

    } else if (item.type === 'recurring_save') {

      // Passed save — remove the effect

      const target = combatants.find(c => c.id === item.combatantId);

      const effectRef = createCombatRuntimeEffectRef({

        effectId: item.effectId || null,

        effectGroupId: item.effectGroupId || null,

        sourceCollection: item.sourceCollection || null,

        effectIndex: Number.isInteger(item.effectIndex) ? item.effectIndex : null,

        effectName: item.effectName,

      });

      if (target && item.saveRemovesEffect) {

        const removalResult = removeRecurringRuntimeEffect(item.combatantId, effectRef, item.effectName);

        const updatedCombatants = removalResult.combatants;

        const updatedChars = removalResult.chars;

        onUpdateChars(updatedChars);

        const logEntry = {

          timestamp: new Date().toISOString(),

          source: target.name,

          target: item.effectName,

          type: 'buff',

          effect: `Saved! ${(item.saveType || 'DEX').toUpperCase()} DC ${item.saveDC} — ${item.effectName} removed`,

        };

        onUpdateCombat({

          ...combat,

          combatants: updatedCombatants,

          damageLog: [logEntry, ...(combat.damageLog || [])].slice(0, 50),

        });

        setTurnNotifications(prev => [...prev, { id: generateId(), type: 'heal', message: `🎲 ${target.name} saves vs ${item.effectName}! Effect removed.`, color: '#27c95a' }]);

        setTickedThisTurn(prev => ({ ...prev, [getCombatTickKey(item.combatantId, 'save_only', effectRef, item.effectName)]: true }));

      }

    }

    setLifecycleQueue(prev => prev.filter(q => q.id !== item.id));

    setTimeout(() => setTurnNotifications([]), 6000);

  };

  

  const handleLifecycleCancel = (item) => {

    if (item.type === 'effect_expire') {

      const effectRef = getQueueItemEffectRef(item);
      const updatedCombatants = combatants.map(c => {

        if (c.id !== item.combatantId) return c;

        const extendResult = extendSpellRuntimeDuration(c.debuffs || [], effectRef);

        return extendResult.changed ? { ...c, debuffs: extendResult.nextEntries } : c;

      });

      onUpdateCombat({ ...combat, combatants: updatedCombatants });

      setTurnNotifications(prev => [...prev, { id: generateId(), type: 'save_pass', message: `${item.effectName} extended +1 round`, color: '#27c95a' }]);

    } else if (item.type === 'buff_expire') {

      const char = chars[item.charId];

      if (char) {

        const extendResult = extendSpellRuntimeDuration(
          char.activeEffects || [],
          getQueueItemEffectRef(item),
        );

        if (extendResult.changed) {
          onUpdateChars({ ...chars, [item.charId]: { ...char, activeEffects: extendResult.nextEntries } });
        }

      }

      setTurnNotifications(prev => [...prev, { id: generateId(), type: 'save_pass', message: `${item.effectName} extended +1 round`, color: '#27c95a' }]);

    } else if (item.type === 'recurring_save') {

      // Failed save — effect stays

      const target = combatants.find(c => c.id === item.combatantId);

      const effectRef = createCombatRuntimeEffectRef({

        effectId: item.effectId || null,

        effectGroupId: item.effectGroupId || null,

        sourceCollection: item.sourceCollection || null,

        effectIndex: Number.isInteger(item.effectIndex) ? item.effectIndex : null,

        effectName: item.effectName,

      });

      const logEntry = {

        timestamp: new Date().toISOString(),

        source: target?.name || 'Unknown',

        target: item.effectName,

        type: 'debuff',

        effect: `Failed ${(item.saveType || 'DEX').toUpperCase()} DC ${item.saveDC} — ${item.effectName} persists`,

      };

      onUpdateCombat({

        ...combat,

        damageLog: [logEntry, ...(combat.damageLog || [])].slice(0, 50),

      });

      setTurnNotifications(prev => [...prev, { id: generateId(), type: 'damage', message: `🎲 ${target?.name || 'Target'} fails save vs ${item.effectName}`, color: '#c92727' }]);

      setTickedThisTurn(prev => ({ ...prev, [getCombatTickKey(item.combatantId, 'save_only', effectRef, item.effectName)]: true }));

    } else if (item.type === 'dot_tick') {

      // Skipped DoT — mark as ticked without applying

      setTickedThisTurn(prev => ({ ...prev, [getQueueItemTickKey(item, 'dot')]: true }));

      setTurnNotifications(prev => [...prev, { id: generateId(), type: 'info', message: `⏭️ Skipped ${item.effectName} tick on ${combatants.find(c => c.id === item.combatantId)?.name || 'target'}`, color: '#888' }]);

    } else if (item.type === 'hot_tick') {

      // Skipped HoT — mark as ticked without applying

      setTickedThisTurn(prev => ({ ...prev, [getQueueItemTickKey(item, 'hot')]: true }));

      setTurnNotifications(prev => [...prev, { id: generateId(), type: 'info', message: `⏭️ Skipped ${item.effectName} healing on ${combatants.find(c => c.id === item.combatantId)?.name || 'target'}`, color: '#888' }]);

    } else if (item.type === 'temp_hp_tick') {

      // Skipped Temp HP — mark as ticked without applying

      setTickedThisTurn(prev => ({ ...prev, [getQueueItemTickKey(item, 'tempHp')]: true }));

      setTurnNotifications(prev => [...prev, { id: generateId(), type: 'info', message: `⏭️ Skipped ${item.effectName} temp HP on ${combatants.find(c => c.id === item.combatantId)?.name || 'target'}`, color: '#888' }]);

    }

    // For concentration checks, cancel = keep concentration (no action needed)

    setLifecycleQueue(prev => prev.filter(q => q.id !== item.id));

    setTimeout(() => setTurnNotifications([]), 6000);

  };

  

  // === MANUAL DoT/HoT TICK HANDLERS ===

  const tickDot = (combatantId, effectName, amount, damageType, effectRefIn = null) => {

    const target = combatants.find(c => c.id === combatantId);

    if (!target) return;

    const effectRef = effectRefIn || createCombatRuntimeEffectRef({ effectName });

    const tickKey = getCombatTickKey(combatantId, 'dot', effectRef, effectName);

    

    // Roll dice if amount is a string expression

    const isDice = typeof amount === 'string' && amount.match(/d/i);

    const rolled = isDice ? rollDice(amount) : null;

    const resolvedAmount = rolled ? rolled.total : (typeof amount === 'number' ? amount : parseInt(amount) || 0);

    const displayAmount = isDice ? `${amount} → ${resolvedAmount}` : resolvedAmount;

    

    setPendingConfirm({

      title: `🔥 Tick ${effectName}`,

      message: `Apply ${displayAmount} ${damageType || 'untyped'} damage to ${target.name}?`,

      actions: [

        { label: `Apply ${resolvedAmount} Damage`, color: '#e74c3c', onClick: () => {

          // Absorb from temp HP first

          const currentTempHp = target.tempHp || 0;

          let tempHpLost = 0;

          let hpDamage = resolvedAmount;

          let newTempHp = currentTempHp;

          if (currentTempHp > 0 && resolvedAmount > 0) {

            tempHpLost = Math.min(currentTempHp, resolvedAmount);

            newTempHp = currentTempHp - tempHpLost;

            hpDamage = resolvedAmount - tempHpLost;

          }

          const newHp = Math.max(0, target.currentHp - hpDamage);

          const updatedCombatants = combatants.map(c => 

            c.id === combatantId ? { ...c, currentHp: newHp, tempHp: newTempHp } : c

          );

          const logEntry = {

            timestamp: new Date().toISOString(),

            source: effectName,

            target: target.name,

            amount: resolvedAmount,

            type: damageType || 'untyped',

            spellName: `${effectName} (DoT${isDice ? ` ${amount}` : ''})`,

            isDot: true,

            tempHpAbsorbed: tempHpLost > 0 ? tempHpLost : undefined,

          };

          onUpdateCombat({

            ...combat,

            combatants: updatedCombatants,

            damageLog: [logEntry, ...(combat.damageLog || [])].slice(0, 50),

          });

          if (chars[combatantId]) {

            onUpdateChars({ ...chars, [combatantId]: { ...chars[combatantId], currentHp: newHp, tempHp: newTempHp } });

          }

          setTickedThisTurn(prev => ({ ...prev, [tickKey]: true }));

          const absorbNote = tempHpLost > 0 ? ` (🩵${tempHpLost} absorbed)` : '';

          setTurnNotifications(prev => [...prev, { id: generateId(), type: 'damage', message: `🔥 ${target.name} takes ${resolvedAmount} ${damageType || ''} from ${effectName}${absorbNote}`, color: '#e74c3c' }]);

          setTimeout(() => setTurnNotifications([]), 6000);

          setPendingConfirm(null);

        }},

        { label: 'Cancel', color: '#666', onClick: () => setPendingConfirm(null) },

      ]

    });

  };



  const tickHot = (combatantId, effectName, amount, effectRefIn = null) => {

    const target = combatants.find(c => c.id === combatantId);

    if (!target) return;

    const effectRef = effectRefIn || createCombatRuntimeEffectRef({ effectName });

    const tickKey = getCombatTickKey(combatantId, 'hot', effectRef, effectName);

    

    const isDice = typeof amount === 'string' && amount.match(/d/i);

    const rolled = isDice ? rollDice(amount) : null;

    const resolvedAmount = rolled ? rolled.total : (typeof amount === 'number' ? amount : parseInt(amount) || 0);

    const displayAmount = isDice ? `${amount} → ${resolvedAmount}` : resolvedAmount;

    

    setPendingConfirm({

      title: `➕ Tick ${effectName}`,

      message: `Heal ${target.name} for ${displayAmount} HP?`,

      actions: [

        { label: `Heal ${resolvedAmount} HP`, color: '#27c95a', onClick: () => {

          const newHp = Math.min(target.maxHp, target.currentHp + resolvedAmount);

          const updatedCombatants = combatants.map(c => 

            c.id === combatantId ? { ...c, currentHp: newHp } : c

          );

          const logEntry = {

            timestamp: new Date().toISOString(),

            source: effectName,

            target: target.name,

            amount: resolvedAmount,

            type: 'healing',

            spellName: `${effectName} (HoT${isDice ? ` ${amount}` : ''})`,

            isHealing: true,

            isHot: true,

          };

          onUpdateCombat({

            ...combat,

            combatants: updatedCombatants,

            damageLog: [logEntry, ...(combat.damageLog || [])].slice(0, 50),

          });

          if (chars[combatantId]) {

            onUpdateChars({ ...chars, [combatantId]: { ...chars[combatantId], currentHp: newHp } });

          }

          setTickedThisTurn(prev => ({ ...prev, [tickKey]: true }));

          setTurnNotifications(prev => [...prev, { id: generateId(), type: 'heal', message: `➕ ${target.name} heals ${resolvedAmount} HP from ${effectName}`, color: '#27c95a' }]);

          setTimeout(() => setTurnNotifications([]), 6000);

          setPendingConfirm(null);

        }},

        { label: 'Cancel', color: '#666', onClick: () => setPendingConfirm(null) },

      ]

    });

  };



  const tickTempHp = (combatantId, effectName, amount, effectRefIn = null) => {

    const target = combatants.find(c => c.id === combatantId);

    if (!target) return;

    const effectRef = effectRefIn || createCombatRuntimeEffectRef({ effectName });

    const tickKey = getCombatTickKey(combatantId, 'tempHp', effectRef, effectName);

    

    setPendingConfirm({

      title: `🩵 Tick ${effectName}`,

      message: `Grant ${target.name} ${amount} temp HP? (takes higher of current ${target.tempHp || 0} or ${amount})`,

      actions: [

        { label: `Grant ${amount} Temp HP`, color: '#87ceeb', onClick: () => {

          const currentTemp = target.tempHp || 0;

          const newTemp = Math.max(currentTemp, amount);

          const updatedCombatants = combatants.map(c => 

            c.id === combatantId ? { ...c, tempHp: newTemp } : c

          );

          const logEntry = {

            timestamp: new Date().toISOString(),

            source: effectName,

            target: target.name,

            amount,

            type: 'temp_hp',

            spellName: `${effectName} (Temp HP)`,

            isTempHp: true,

          };

          onUpdateCombat({

            ...combat,

            combatants: updatedCombatants,

            damageLog: [logEntry, ...(combat.damageLog || [])].slice(0, 50),

          });

          if (chars[combatantId]) {

            onUpdateChars({ ...chars, [combatantId]: { ...chars[combatantId], tempHp: newTemp } });

          }

          setTickedThisTurn(prev => ({ ...prev, [tickKey]: true }));

          setTurnNotifications(prev => [...prev, { id: generateId(), type: 'heal', message: `🩵 ${target.name} gains ${amount} temp HP from ${effectName}`, color: '#87ceeb' }]);

          setTimeout(() => setTurnNotifications([]), 6000);

          setPendingConfirm(null);

        }},

        { label: 'Cancel', color: '#666', onClick: () => setPendingConfirm(null) },

      ]

    });

  };



  // Recurring save tick handler

  const tickSave = (combatantId, effectName, saveType, saveDC, saveRemovesEffect = true, effectRefIn = null) => {

    const target = combatants.find(c => c.id === combatantId);

    if (!target) return;

    const effectRef = effectRefIn || createCombatRuntimeEffectRef({ effectName });

    

    const saveLabel = (saveType || 'DEX').toUpperCase();

    const st = (saveType || 'dex').toLowerCase();

    const saveContext = getResolvedCombatSaveContext(combatantId, st);

    if (!saveContext) return;

    const mod = saveContext.totalBonus;

    const breakdownStr = formatResolvedSaveBreakdown(saveContext);

    const combatAdvMode = getAdvantageForCombatant(combatantId, `${st}_saves`);

    const advTag = combatAdvMode !== 'normal' ? ` [${combatAdvMode}${supremeRoll ? '/supreme' : ''}]` : '';

    const saveTickKey = getCombatTickKey(combatantId, 'save_only', effectRef, effectName);



    if (effectRef?.effectId || effectRef?.sourceCollection) {

      const applyRuntimeRemoval = (effectText) => {

        const removalResult = removeRecurringRuntimeEffect(combatantId, effectRef, effectName);

        onUpdateChars(removalResult.chars);

        onUpdateCombat({

          ...combat,

          combatants: removalResult.combatants,

          damageLog: [{

            timestamp: new Date().toISOString(),

            source: target.name,

            target: effectName,

            type: 'buff',

            effect: effectText,

          }, ...(combat.damageLog || [])].slice(0, 50),

        });

      };



      setPendingConfirm({

        title: `🎲 ${saveLabel} Save DC ${saveDC || '?'} — ${effectName}`,

        message: `${target.name} must make a ${saveLabel} saving throw (DC ${saveDC || '?'}) to end ${effectName}.${advTag}`,

        details: breakdownStr ? `Modifier: ${breakdownStr} = ${mod >= 0 ? '+' : ''}${mod}${saveContext.diceBonuses.length ? ' + dice' : ''}` : null,

        actions: [
          { label: `🎲 Roll (${mod >= 0 ? '+' : ''}${mod}${saveContext.diceBonuses.length ? '+dice' : ''})`, color: '#c9a227', onClick: () => {

            const saveRoll = rollResolvedCombatSave(combatantId, st);

            if (!saveRoll) return;

            const { d20Roll, d20, total, bonusDiceRolls } = saveRoll;

            let diceDisplay = '';

            if (d20Roll.mode !== 'normal' && d20Roll.all.length > 1) {
              diceDisplay = `[${d20Roll.all.map(v => v === d20 ? v : v).join('/')}]→${d20}`;

            } else {

              diceDisplay = `${d20}`;

            }

            let rollStr = `${diceDisplay}${mod >= 0 ? '+' : ''}${mod}`;

            bonusDiceRolls.forEach((bonus) => {

              rollStr += `+${bonus.total}(${bonus.source})`;

            });

            const passed = total >= (saveDC || 10);

            setPendingConfirm({

              title: `🎲 ${saveLabel} Save DC ${saveDC || '?'} — ${effectName}`,

              message: `${target.name} rolled: ${rollStr} = ${total} vs DC ${saveDC}`,

              details: passed ? `✓ PASSED! ${total} ≥ ${saveDC}` : `✕ FAILED! ${total} < ${saveDC}`,

              actions: [

                ...(passed && saveRemovesEffect ? [{

                  label: `✓ Remove ${effectName}`,

                  color: '#27c95a',

                  onClick: () => {

                    applyRuntimeRemoval(`Saved! ${saveLabel} ${rollStr}=${total} vs DC ${saveDC} — ${effectName} removed`);

                    setTurnNotifications(prev => [...prev, { id: generateId(), type: 'heal', message: `🎲 ${target.name} saves vs ${effectName}! (${total} vs DC ${saveDC})`, color: '#27c95a' }]);

                    setTickedThisTurn(prev => ({ ...prev, [saveTickKey]: true }));

                    setTimeout(() => setTurnNotifications([]), 6000);

                    setPendingConfirm(null);

                  },

                }] : []),

                ...(!passed ? [{

                  label: `✕ Effect Stays`,

                  color: '#c92727',

                  onClick: () => {

                    setTickedThisTurn(prev => ({ ...prev, [saveTickKey]: true }));

                    onUpdateCombat({

                      ...combat,

                      damageLog: [{

                        timestamp: new Date().toISOString(),

                        source: target.name,

                        target: effectName,

                        type: 'debuff',

                        effect: `Failed ${saveLabel} ${rollStr}=${total} vs DC ${saveDC} — ${effectName} persists`,

                      }, ...(combat.damageLog || [])].slice(0, 50),

                    });

                    setTurnNotifications(prev => [...prev, { id: generateId(), type: 'save_fail', message: `🎲 ${target.name} fails vs ${effectName} (${total} vs DC ${saveDC})`, color: '#e74c3c' }]);

                    setTimeout(() => setTurnNotifications([]), 6000);

                    setPendingConfirm(null);

                  },

                }] : []),

                { label: 'Override', color: '#888', onClick: () => setPendingConfirm(null) },

              ],

            });

          }},

          { label: `✓ Passed — Remove ${effectName}`, color: '#27c95a', onClick: () => {

            if (saveRemovesEffect) {

              applyRuntimeRemoval(`Saved! ${saveLabel} DC ${saveDC} — ${effectName} removed`);

              setTurnNotifications(prev => [...prev, { id: generateId(), type: 'heal', message: `🎲 ${target.name} saves vs ${effectName}! Effect removed.`, color: '#27c95a' }]);

            }

            setTickedThisTurn(prev => ({ ...prev, [saveTickKey]: true }));

            setTimeout(() => setTurnNotifications([]), 6000);

            setPendingConfirm(null);

          }},

          { label: `✕ Failed — ${effectName} Stays`, color: '#c92727', onClick: () => {

            setTickedThisTurn(prev => ({ ...prev, [saveTickKey]: true }));

            onUpdateCombat({

              ...combat,

              damageLog: [{

                timestamp: new Date().toISOString(),

                source: target.name,

                target: effectName,

                type: 'debuff',

                effect: `Failed ${saveLabel} DC ${saveDC} ? ${effectName} persists`,

              }, ...(combat.damageLog || [])].slice(0, 50),

            });

            setTurnNotifications(prev => [...prev, { id: generateId(), type: 'damage', message: `🎲 ${target.name} fails save vs ${effectName}`, color: '#c92727' }]);

            setTimeout(() => setTurnNotifications([]), 6000);

            setPendingConfirm(null);

          }},

          { label: 'Skip', color: '#666', onClick: () => setPendingConfirm(null) },

        ],

      });

      return;

    }

    

    setPendingConfirm({

      title: `🎲 ${saveLabel} Save DC ${saveDC || '?'} — ${effectName}`,

      message: `${target.name} must make a ${saveLabel} saving throw (DC ${saveDC || '?'}) to end ${effectName}.${advTag}`,

      details: breakdownStr ? `Modifier: ${breakdownStr} = ${mod >= 0 ? '+' : ''}${mod}${saveContext.diceBonuses.length ? ' + dice' : ''}` : null,

      actions: [
          { label: `🎲 Roll (${mod >= 0 ? '+' : ''}${mod}${saveContext.diceBonuses.length ? '+dice' : ''})`, color: '#c9a227', onClick: () => {

          const saveRoll = rollResolvedCombatSave(combatantId, st);

          if (!saveRoll) return;

          const { d20Roll, d20, total, bonusDiceRolls } = saveRoll;

          // Build roll display showing all dice

          let diceDisplay = '';

          if (d20Roll.mode !== 'normal' && d20Roll.all.length > 1) {
              diceDisplay = `[${d20Roll.all.map(v => v === d20 ? v : v).join('/')}]→${d20}`;

          } else {

            diceDisplay = `${d20}`;

          }

          let rollStr = `${diceDisplay}${mod >= 0 ? '+' : ''}${mod}`;

          bonusDiceRolls.forEach((bonus) => {

            rollStr += `+${bonus.total}(${bonus.source})`;

          });

          const passed = total >= (saveDC || 10);

          const modeLabel = d20Roll.mode !== 'normal' ? ` (${d20Roll.mode}${d20Roll.isSupreme ? '/supreme' : ''})` : '';

          // Replace dialog with result

          setPendingConfirm({

            title: `🎲 ${saveLabel} Save DC ${saveDC || '?'} — ${effectName}`,

            message: `${target.name} rolled: ${rollStr} = ${total} vs DC ${saveDC}`,

            details: passed ? `✓ PASSED! ${total} ≥ ${saveDC}` : `✕ FAILED! ${total} < ${saveDC}`,

            actions: [

              ...(passed && saveRemovesEffect ? [{ label: `✓ Remove ${effectName}`, color: '#27c95a', onClick: () => {

                const updatedCombatants = combatants.map(c => {

                  if (c.id !== combatantId) return c;

                  return { ...c, debuffs: (c.debuffs || []).filter(d => d.name !== effectName), conditions: (c.conditions || []).filter(cond => cond !== effectName && !cond.includes(effectName)) };

                });

                const updatedChars = { ...chars };

                if (updatedChars[combatantId]) {

                  updatedChars[combatantId] = { ...updatedChars[combatantId], activeEffects: (updatedChars[combatantId].activeEffects || []).filter(e => e.name !== effectName) };

                  onUpdateChars(updatedChars);

                }

                onUpdateCombat({ ...combat, combatants: updatedCombatants, damageLog: [{ timestamp: new Date().toISOString(), source: target.name, target: effectName, type: 'buff', effect: `Saved! ${saveLabel} ${rollStr}=${total} vs DC ${saveDC} — ${effectName} removed` }, ...(combat.damageLog || [])].slice(0, 50) });

                setTurnNotifications(prev => [...prev, { id: generateId(), type: 'heal', message: `🎲 ${target.name} saves vs ${effectName}! (${total} vs DC ${saveDC})`, color: '#27c95a' }]);

                setTickedThisTurn(prev => ({ ...prev, [`${combatantId}-save_only-${effectName}`]: true }));

                setTimeout(() => setTurnNotifications([]), 6000);

                setPendingConfirm(null);

              }}] : []),

              ...(!passed ? [{ label: `✕ Effect Stays`, color: '#c92727', onClick: () => {

                setTickedThisTurn(prev => ({ ...prev, [`${combatantId}-save_only-${effectName}`]: true }));

                onUpdateCombat({ ...combat, damageLog: [{ timestamp: new Date().toISOString(), source: target.name, target: effectName, type: 'debuff', effect: `Failed ${saveLabel} ${rollStr}=${total} vs DC ${saveDC} — ${effectName} persists` }, ...(combat.damageLog || [])].slice(0, 50) });

                setTurnNotifications(prev => [...prev, { id: generateId(), type: 'save_fail', message: `🎲 ${target.name} fails vs ${effectName} (${total} vs DC ${saveDC})`, color: '#e74c3c' }]);

                setTimeout(() => setTurnNotifications([]), 6000);

                setPendingConfirm(null);

              }}] : []),

              { label: 'Override', color: '#888', onClick: () => setPendingConfirm(null) },

            ]

          });

        }},

        { label: `✓ Passed — Remove ${effectName}`, color: '#27c95a', onClick: () => {

          if (saveRemovesEffect) {

            // Remove the effect from debuffs and conditions

            const updatedCombatants = combatants.map(c => {

              if (c.id !== combatantId) return c;

              return {

                ...c,

                debuffs: (c.debuffs || []).filter(d => d.name !== effectName),

                conditions: (c.conditions || []).filter(cond => cond !== effectName && !cond.includes(effectName)),

              };

            });

            // Also remove from activeEffects if applicable

            const updatedChars = { ...chars };

            if (updatedChars[combatantId]) {

              updatedChars[combatantId] = {

                ...updatedChars[combatantId],

                activeEffects: (updatedChars[combatantId].activeEffects || []).filter(e => e.name !== effectName),

              };

              onUpdateChars(updatedChars);

            }

            const logEntry = {

              timestamp: new Date().toISOString(),

              source: target.name,

              target: effectName,

              type: 'buff',

              effect: `Saved! ${saveLabel} DC ${saveDC} — ${effectName} removed`,

            };

            onUpdateCombat({

              ...combat,

              combatants: updatedCombatants,

              damageLog: [logEntry, ...(combat.damageLog || [])].slice(0, 50),

            });

            setTurnNotifications(prev => [...prev, { id: generateId(), type: 'heal', message: `🎲 ${target.name} saves vs ${effectName}! Effect removed.`, color: '#27c95a' }]);

          }

          setTickedThisTurn(prev => ({ ...prev, [`${combatantId}-save_only-${effectName}`]: true }));

          setTimeout(() => setTurnNotifications([]), 6000);

          setPendingConfirm(null);

        }},

        { label: `✕ Failed — ${effectName} Stays`, color: '#c92727', onClick: () => {

          setTickedThisTurn(prev => ({ ...prev, [`${combatantId}-save_only-${effectName}`]: true }));

          const logEntry = {

            timestamp: new Date().toISOString(),

            source: target.name,

            target: effectName,

            type: 'debuff',

            effect: `Failed ${saveLabel} DC ${saveDC} — ${effectName} persists`,

          };

          onUpdateCombat({

            ...combat,

            damageLog: [logEntry, ...(combat.damageLog || [])].slice(0, 50),

          });

          setTurnNotifications(prev => [...prev, { id: generateId(), type: 'damage', message: `🎲 ${target.name} fails save vs ${effectName}`, color: '#c92727' }]);

          setTimeout(() => setTurnNotifications([]), 6000);

          setPendingConfirm(null);

        }},

        { label: 'Skip', color: '#666', onClick: () => setPendingConfirm(null) },

      ]

    });

  };



  // Helper: check if a tick effect should glow (it's the right timing to tick it)

  const shouldTickGlow = (eff, combatant) => {

    // Apply global timing override

    const isHealType = eff.type === 'hot' || eff.type === 'tempHp';

    const globalSetting = isHealType ? deathSaveConfig.hotTickTiming : (eff.type === 'dot' ? deathSaveConfig.dotTickTiming : 'per_spell');

    const timing = (!globalSetting || globalSetting === 'per_spell') ? (eff.tickTiming || 'start_of_target_turn') : globalSetting;

    

    const currentTurnCombatant = sortedCombatants[combat.turnIndex];

    if (!currentTurnCombatant) return false;

    

    // Round-based timings always glow (they'll be queued at round transition)

    if (timing === 'start_of_round' || timing === 'end_of_round') return true;

    

    if (timing === 'start_of_target_turn' || timing === 'end_of_target_turn') {

      return currentTurnCombatant.id === combatant.id;

    }

    if (timing === 'start_of_caster_turn' || timing === 'end_of_caster_turn') {

      return currentTurnCombatant.id === eff.casterId;

    }

    return false;

  };



  // Collect all ticking effects (DoT/HoT/TempHP) for a given combatant

  const getTickEffects = (combatant) => {

    const effects = [];

    if (!combatant || (combatant.currentHp ?? 0) <= 0) return effects;

    (combatant.debuffs || []).forEach((debuff, index) => {

      effects.push(...buildSpellRuntimeLifecycleEntries(debuff, 'combatant.debuffs', index));

    });

    const char = chars[combatant.id];

    if (char?.activeEffects) {

      char.activeEffects.forEach((effect, index) => {

        effects.push(...buildSpellRuntimeLifecycleEntries(effect, 'char.activeEffects', index));

      });

    }

    return effects;

  };



  const getLegacyTickEffects = (combatant) => {

    const effects = [];

    // Skip DoT/HoT/save ticks for dead or unconscious combatants. Death-save prompts are

    // handled separately (see nextTurn around the nextCombatantForEffects block + the

    // death-save queue push). Without this guard, a downed player with an active DoT kept

    // getting damage queued every turn, and dead enemies kept queuing their recurring saves.

    if (!combatant || (combatant.currentHp ?? 0) <= 0) return effects;

    // Helper to resolve amount — supports dice notation "1d6", "2d4+3" or flat numbers

    const resolveAmount = (val) => {

      if (!val) return 0;

      if (typeof val === 'number') return val;

      const str = String(val);

      // Check if it's dice notation

      if (str.match(/\d*d\d+/i)) return str; // Return as string for dice rolling

      return parseInt(str) || 0;

    };

    

    // DoTs from debuffs on the combatant

    (combatant.debuffs || []).forEach((d, index) => {

      const effectRef = createCombatRuntimeEffectRef({

        effectId: d.id || d.effectId || null,

        effectGroupId: d.effectGroupId || null,

        sourceCollection: 'combatant.debuffs',

        effectIndex: index,

        effectName: d.name,

        casterId: d.casterId || null,

      });

      const dotVal = resolveAmount(d.dotDamage);

      if (dotVal) {

        effects.push({ type: 'dot', name: d.name, amount: dotVal, damageType: d.dotDamageType || 'fire', tickTiming: d.tickTiming || 'start_of_target_turn', casterId: d.casterId, saveType: d.saveType, saveDC: d.saveDC, saveTiming: d.saveTiming, saveRemovesEffect: d.saveRemovesEffect, isDice: typeof dotVal === 'string', effectId: effectRef.effectId, effectGroupId: effectRef.effectGroupId, sourceCollection: effectRef.sourceCollection, effectIndex: effectRef.effectIndex, effectRef });

      }

    });

    // HoTs from debuffs (unusual but supported)

    (combatant.debuffs || []).forEach((d, index) => {

      const effectRef = createCombatRuntimeEffectRef({

        effectId: d.id || d.effectId || null,

        effectGroupId: d.effectGroupId || null,

        sourceCollection: 'combatant.debuffs',

        effectIndex: index,

        effectName: d.name,

        casterId: d.casterId || null,

      });

      const hotVal = resolveAmount(d.hotHeal);

      if (hotVal) {

        effects.push({ type: 'hot', name: d.name, amount: hotVal, tickTiming: d.tickTiming || 'start_of_target_turn', casterId: d.casterId, saveType: d.saveType, saveDC: d.saveDC, saveTiming: d.saveTiming, saveRemovesEffect: d.saveRemovesEffect, isDice: typeof hotVal === 'string', effectId: effectRef.effectId, effectGroupId: effectRef.effectGroupId, sourceCollection: effectRef.sourceCollection, effectIndex: effectRef.effectIndex, effectRef });

      }

      if (d.hotTempHp > 0) {

        effects.push({ type: 'tempHp', name: d.name, amount: d.hotTempHp, tickTiming: d.tickTiming || 'start_of_target_turn', casterId: d.casterId, saveType: d.saveType, saveDC: d.saveDC, saveTiming: d.saveTiming, saveRemovesEffect: d.saveRemovesEffect, effectId: effectRef.effectId, effectGroupId: effectRef.effectGroupId, sourceCollection: effectRef.sourceCollection, effectIndex: effectRef.effectIndex, effectRef });

      }

    });

    // Save-only debuffs (no tick damage, but has recurring save)

    (combatant.debuffs || []).forEach((d, index) => {

      if (d.saveType && !resolveAmount(d.dotDamage) && !resolveAmount(d.hotHeal) && !d.hotTempHp) {

        const effectRef = createCombatRuntimeEffectRef({

          effectId: d.id || d.effectId || null,

          effectGroupId: d.effectGroupId || null,

          sourceCollection: 'combatant.debuffs',

          effectIndex: index,

          effectName: d.name,

          casterId: d.casterId || null,

        });

        effects.push({ type: 'save_only', name: d.name, saveType: d.saveType, saveDC: d.saveDC, saveTiming: d.saveTiming || 'end_of_target_turn', saveRemovesEffect: d.saveRemovesEffect, casterId: d.casterId, tickTiming: d.saveTiming || 'end_of_target_turn', effectId: effectRef.effectId, effectGroupId: effectRef.effectGroupId, sourceCollection: effectRef.sourceCollection, effectIndex: effectRef.effectIndex, effectRef });

      }

    });

    // DoTs, HoTs, TempHP ticks, and saves from character activeEffects

    const char = chars[combatant.id];

    if (char?.activeEffects) {

      char.activeEffects.forEach((e, index) => {

        const effectRef = createCombatRuntimeEffectRef({

          effectId: e.id || e.effectId || null,

          effectGroupId: e.effectGroupId || null,

          sourceCollection: 'char.activeEffects',

          effectIndex: index,

          effectName: e.name,

          casterId: e.casterId || null,

        });

        const dotVal = resolveAmount(e.dotDamage);

        if (dotVal) {

          effects.push({ type: 'dot', name: e.name, amount: dotVal, damageType: e.dotDamageType || 'fire', tickTiming: e.tickTiming || 'start_of_target_turn', casterId: e.casterId, saveType: e.saveType, saveDC: e.saveDC, saveTiming: e.saveTiming, saveRemovesEffect: e.saveRemovesEffect, isDice: typeof dotVal === 'string', effectId: effectRef.effectId, effectGroupId: effectRef.effectGroupId, sourceCollection: effectRef.sourceCollection, effectIndex: effectRef.effectIndex, effectRef });

        }

        const hotVal = resolveAmount(e.hotHeal);

        if (hotVal) {

          effects.push({ type: 'hot', name: e.name, amount: hotVal, tickTiming: e.tickTiming || 'start_of_target_turn', casterId: e.casterId, saveType: e.saveType, saveDC: e.saveDC, saveTiming: e.saveTiming, saveRemovesEffect: e.saveRemovesEffect, isDice: typeof hotVal === 'string', effectId: effectRef.effectId, effectGroupId: effectRef.effectGroupId, sourceCollection: effectRef.sourceCollection, effectIndex: effectRef.effectIndex, effectRef });

        }

        if (e.hotTempHp > 0) {

          effects.push({ type: 'tempHp', name: e.name, amount: e.hotTempHp, tickTiming: e.tickTiming || 'start_of_target_turn', casterId: e.casterId, saveType: e.saveType, saveDC: e.saveDC, saveTiming: e.saveTiming, saveRemovesEffect: e.saveRemovesEffect, effectId: effectRef.effectId, effectGroupId: effectRef.effectGroupId, sourceCollection: effectRef.sourceCollection, effectIndex: effectRef.effectIndex, effectRef });

        }

        if (e.saveType && !dotVal && !hotVal && !e.hotTempHp) {

          effects.push({ type: 'save_only', name: e.name, saveType: e.saveType, saveDC: e.saveDC, saveTiming: e.saveTiming || 'end_of_target_turn', saveRemovesEffect: e.saveRemovesEffect, casterId: e.casterId, tickTiming: e.saveTiming || 'end_of_target_turn', effectId: effectRef.effectId, effectGroupId: effectRef.effectGroupId, sourceCollection: effectRef.sourceCollection, effectIndex: effectRef.effectIndex, effectRef });

        }

      });

    }

    return effects;

  };



  // Collect ALL active effects across ALL combatants for the effects tracker

  const getAllActiveEffects = () => {

    const allEffects = [];



    combatants.forEach(c => {

      const char = chars[c.id] || null;

      allEffects.push(...getRenderableEffects({

        char,

        combatant: c,

        combatants,

        combatRound: combat.round,

      }));

    });



    return allEffects;

  };



  // === EFFECT MANAGEMENT ===

  // Remove a debuff from a combatant

  const removeDebuff = (combatantId, debuffIndex) => {

    const c = combatants.find(x => x.id === combatantId);

    if (!c) return;

    const debuff = (c.debuffs || [])[debuffIndex];

    if (!debuff) return;

    

    setPendingConfirm({

      title: '💀 Remove Effect?',

      message: `Remove "${debuff.name}" from ${c.name}?`,

      details: debuff.casterName ? `Cast by ${debuff.casterName}` : null,

      actions: [

        { label: 'Remove', color: '#c92727', onClick: () => {

          const updated = combatants.map(x => {

            if (x.id !== combatantId) return x;

            const newDebuffs = (x.debuffs || []).filter((_, i) => i !== debuffIndex);

            const newConditions = (x.conditions || []).filter(cond => !cond.includes(debuff.name));

            return { ...x, debuffs: newDebuffs, conditions: newConditions };

          });

          onUpdateCombat({ ...combat, combatants: updated });

          setPendingConfirm(null);

        }},

        { label: 'Cancel', color: '#666', onClick: () => setPendingConfirm(null) },

      ]

    });

  };

  

  // Move a debuff to a different target

  const moveDebuff = (fromCombatantId, debuffIndex, toCombatantId) => {

    const from = combatants.find(x => x.id === fromCombatantId);

    const to = combatants.find(x => x.id === toCombatantId);

    if (!from || !to) return;

    const debuff = (from.debuffs || [])[debuffIndex];

    if (!debuff) return;

    

    const updated = combatants.map(x => {

      if (x.id === fromCombatantId) {

        const newDebuffs = (x.debuffs || []).filter((_, i) => i !== debuffIndex);

        const newConditions = (x.conditions || []).filter(cond => !cond.includes(debuff.name));

        return { ...x, debuffs: newDebuffs, conditions: newConditions };

      }

      if (x.id === toCombatantId) {

        const condText = debuff.casterOnly ? `Marked by ${debuff.casterName} (${debuff.name})` : debuff.name;

        return { 

          ...x, 

          debuffs: [...(x.debuffs || []), { ...debuff }],

          conditions: [...(x.conditions || []), condText]

        };

      }

      return x;

    });

    // Update marked targets if applicable

    if (debuff.casterOnly && debuff.casterId) {

      const casterChar = chars[debuff.casterId];

      if (casterChar?.markedTargets) {

        const updatedMarks = casterChar.markedTargets.map(m => 

          m.targetId === fromCombatantId && m.spellName === debuff.name 

            ? { ...m, targetId: toCombatantId, targetName: to.name }

            : m

        );

        onUpdateChars({ ...chars, [debuff.casterId]: { ...casterChar, markedTargets: updatedMarks } });

      }

    }

    onUpdateCombat({ ...combat, combatants: updated });

    setEditingEffect(null);

  };

  

  // Change debuff remaining duration

  const changeDebuffDuration = (combatantId, debuffIndex, delta) => {

    const updated = combatants.map(x => {

      if (x.id !== combatantId) return x;

      const newDebuffs = (x.debuffs || []).map((d, i) => {

        if (i !== debuffIndex) return d;

        const newDuration = typeof d.duration === 'number' ? Math.max(1, d.duration + delta) : (delta > 0 ? 1 : null);

        return { ...d, duration: newDuration };

      });

      return { ...x, debuffs: newDebuffs };

    });

    onUpdateCombat({ ...combat, combatants: updated });

  };

  

  // === ADVANTAGE / DISADVANTAGE SYSTEM ===

  // Get advantage/disadvantage sources for an attacker attacking a target

  const getAdvDisadv = (attackerId, targetId) => {

    const attacker = combatants.find(c => c.id === attackerId);

    const target = combatants.find(c => c.id === targetId);

    const advSources = [];

    const disSources = [];

    

    // Check target conditions

    const targetConditions = (target?.conditions || []).map(c => c.toLowerCase());

    const attackerConditions = (attacker?.conditions || []).map(c => c.toLowerCase());

    

    // Faerie Fire on target → advantage

    if (targetConditions.some(c => c.includes('faerie fire'))) {

      advSources.push('Faerie Fire');

    }

    // Restrained target → advantage on attacks against

    if (targetConditions.some(c => c.includes('restrained'))) {

      advSources.push('Restrained (target)');

    }

    // Paralyzed target → advantage

    if (targetConditions.some(c => c.includes('paralyzed'))) {

      advSources.push('Paralyzed (target)');

    }

    // Stunned target → advantage

    if (targetConditions.some(c => c.includes('stunned'))) {

      advSources.push('Stunned (target)');

    }

    // Prone target → advantage in melee (we don't know range, so just note it)

    if (targetConditions.some(c => c.includes('prone'))) {

      advSources.push('Prone (melee only)');

    }

    // Invisible attacker → advantage

    if (attackerConditions.some(c => c.includes('invisible'))) {

      advSources.push('Invisible');

    }

    

    // Blinded attacker → disadvantage

    if (attackerConditions.some(c => c.includes('blinded'))) {

      disSources.push('Blinded');

    }

    // Frightened attacker when target is in sight → disadvantage

    if (attackerConditions.some(c => c.includes('frightened'))) {

      disSources.push('Frightened');

    }

    // Poisoned attacker → disadvantage

    if (attackerConditions.some(c => c.includes('poisoned'))) {

      disSources.push('Poisoned');

    }

    // Restrained attacker → disadvantage

    if (attackerConditions.some(c => c.includes('restrained'))) {

      disSources.push('Restrained');

    }

    // Prone target → disadvantage at range

    if (targetConditions.some(c => c.includes('prone'))) {

      disSources.push('Prone (ranged only)');

    }

    // Invisible target → disadvantage

    if (targetConditions.some(c => c.includes('invisible'))) {

      disSources.push('Invisible (target)');

    }

    // Exhaustion level 3+ → disadvantage on attacks  

    if (attackerConditions.some(c => c.includes('exhaustion') && /[3-6]/.test(c))) {

      disSources.push('Exhaustion 3+');

    }

    

    // Check target debuffs for custom adv/disadv flags

    (target?.debuffs || []).forEach(d => {

      if (d.grantsAdvantage) advSources.push(d.name);

      if (d.grantsDisadvantage) disSources.push(d.name);

    });

    

    // Check attacker active effects for adv/disadv

    const attackerChar = chars[attackerId];

    (attackerChar?.activeEffects || []).forEach(e => {

      if (e.grantsAdvantage) advSources.push(e.name);

      if (e.grantsDisadvantage) disSources.push(e.name);

    });

    

    // Net result: if both advantage and disadvantage, they cancel out (D&D rules)

    const hasAdv = advSources.length > 0;

    const hasDis = disSources.length > 0;

    let net = null;

    if (hasAdv && !hasDis) net = 'advantage';

    else if (hasDis && !hasAdv) net = 'disadvantage';

    // else they cancel out

    

    return { net, advSources, disSources };

  };

  

  // Roll with advantage or disadvantage

  const rollWithAdvDisadv = (sides, advDisadv) => {

    const roll1 = Math.floor(Math.random() * sides) + 1;

    const roll2 = Math.floor(Math.random() * sides) + 1;

    if (advDisadv === 'advantage') {

      return { result: Math.max(roll1, roll2), rolls: [roll1, roll2], type: 'advantage', used: roll1 >= roll2 ? 0 : 1 };

    } else if (advDisadv === 'disadvantage') {

      return { result: Math.min(roll1, roll2), rolls: [roll1, roll2], type: 'disadvantage', used: roll1 <= roll2 ? 0 : 1 };

    }

    return { result: roll1, rolls: [roll1], type: 'normal', used: 0 };

  };



  // Get pending damage for a target

  const getPendingForTarget = (targetId) => pendingDamage.filter(p => p.targetId === targetId);

  

  // Render combatant card

  const renderCombatant = (combatant) => {

    const tempHp = combatant.tempHp || 0;

    const totalHpWithTemp = combatant.currentHp + tempHp;

    const barMax = Math.max(combatant.maxHp, totalHpWithTemp);

    const hpPercent = (combatant.currentHp / barMax) * 100;

    const tempHpPercent = tempHp > 0 ? (tempHp / barMax) * 100 : 0;

    const hpColor = hpPercent > 50 ? '#27c95a' : hpPercent > 25 ? '#c9a227' : '#c92727';

    const isPlayer = combatant.type === 'player' || combatant.type === 'npc';

    const isNPC = combatant.type === 'npc';

    const isSelected = selectedTargets.includes(combatant.id);

    const isExpanded = expandedCombatant === combatant.id;

    const currentTurnCombatant = allInitiativesSet ? sortedCombatants[combat.turnIndex] : null;

    const isCurrentTurn = currentTurnCombatant?.id === combatant.id;

    const targetPending = getPendingForTarget(combatant.id);

    const canEdit = isDM || (isPlayer && combatant.id === currentCharId);

    const isOwnCharacter = combatant.id === viewCharId;

    const isDying = combatant.deathSaves && !combatant.deathSaves.dead && combatant.currentHp <= 0;

    const isDead = combatant.deathSaves?.dead;

    const isReviving = revivingIds.has(combatant.id);

    const isCardCollapsed = collapsedCardIds.has(combatant.id);

    const isPinned = pinnedCombatantIds.has(combatant.id);

    

    // Collapsed compact view

    if (isCardCollapsed) {

      const conditions = combatant.conditions || [];

      return (

        <div 

          key={combatant.id}

          style={{

            padding: '6px 12px',

            background: isDead ? 'rgba(60,60,60,0.3)' : isCurrentTurn ? 'rgba(201,162,39,0.15)' : isSelected ? 'rgba(74,144,217,0.15)' : isOwnCharacter ? 'rgba(39,201,90,0.1)' : 'rgba(0,0,0,0.2)',

            borderRadius: '6px',

            border: `1px solid ${isDead ? '#555' : isCurrentTurn ? '#ffd700' : isSelected ? '#4a90d9' : isOwnCharacter ? '#27c95a40' : isPlayer ? '#27c95a20' : '#c9272720'}`,

            cursor: 'pointer',

            opacity: isDead ? 0.5 : 1,

            display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',

          }}

          onClick={() => toggleTarget(combatant.id)}

        >

          <span 

            style={{ cursor: 'pointer', fontSize: '0.7rem', color: '#888', userSelect: 'none' }}

            onClick={(e) => { e.stopPropagation(); setCollapsedCardIds(prev => { const n = new Set(prev); n.delete(combatant.id); return n; }); }}

          >▶</span>

          {isCurrentTurn && <span style={{ color: '#ffd700', fontSize: '0.8rem' }}>⚔️</span>}

          <strong style={{ color: isPlayer ? '#27c95a' : '#c92727', fontSize: '0.85rem' }}>{combatant.name}</strong>

          <span style={{ fontSize: '0.75rem', color: hpColor, fontFamily: 'monospace' }}>

            {combatant.currentHp}{tempHp > 0 && <span style={{ color: '#87ceeb' }}>+{tempHp}</span>}/{combatant.maxHp}

          </span>

          {/* Mini HP bar */}

          <div style={{ width: '60px', height: '4px', background: 'rgba(255,255,255,0.1)', borderRadius: '2px', overflow: 'hidden' }}>

            <div style={{ width: `${hpPercent}%`, height: '100%', background: hpColor, borderRadius: '2px' }} />

          </div>

          {conditions.length > 0 && conditions.map((cond, i) => (

            <span key={i} style={{ fontSize: '0.55rem', padding: '1px 4px', background: 'rgba(155,89,182,0.15)', border: '1px solid rgba(155,89,182,0.3)', borderRadius: '8px', color: '#c39bdb' }}>{cond}</span>

          ))}

          {targetPending.length > 0 && <span style={{ fontSize: '0.65rem', color: '#ff6666' }}>⚠️ {targetPending.length} pending</span>}

          <span 

            style={{ fontSize: '0.6rem', color: isPinned ? '#ffd700' : '#444', opacity: isPinned ? 1 : 0.4, cursor: 'pointer', userSelect: 'none' }}

            onClick={(e) => { e.stopPropagation(); setPinnedCombatantIds(prev => { const n = new Set(prev); isPinned ? n.delete(combatant.id) : n.add(combatant.id); return n; }); }}

            title={isPinned ? "Unpin from view" : "Pin to view"}

          >📌</span>

        </div>

      );

    }

    

    return (

      <div 

        key={combatant.id}

        style={{

          padding: '12px',

          background: isReviving ? 'rgba(74,144,217,0.25)' : isDead ? 'rgba(60,60,60,0.3)' : isDying ? 'rgba(139,0,0,0.15)' : isCurrentTurn ? 'rgba(201,162,39,0.2)' : isSelected ? 'rgba(74,144,217,0.2)' : targetPending.length > 0 ? 'rgba(201,39,39,0.15)' : isOwnCharacter ? 'rgba(39,201,90,0.15)' : 'rgba(0,0,0,0.3)',

          borderRadius: '8px',

          border: `2px solid ${isReviving ? '#4a90d9' : isDead ? '#555' : isCurrentTurn ? '#ffd700' : isSelected ? '#4a90d9' : targetPending.length > 0 ? '#c92727' : isOwnCharacter ? '#27c95a' : isPlayer ? '#27c95a40' : '#c9272740'}`,

          cursor: 'pointer',

          position: 'relative',

          opacity: isDead ? 0.55 : 1,

          transition: 'all 0.5s',

          animation: isReviving ? 'stabilizedPulse 0.8s ease-out 3' : isDying && !isDead ? 'deathPulse 2s ease-in-out infinite' : 'none',

        }}

        onClick={() => toggleTarget(combatant.id)}

      >

        {/* Stabilized flash overlay */}

        {isReviving && (

          <div style={{

            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,

            borderRadius: '6px',

            background: 'rgba(74,144,217,0.12)',

            display: 'flex', alignItems: 'center', justifyContent: 'center',

            zIndex: 5, pointerEvents: 'none',

          }}>

            <div style={{

              background: 'rgba(74,144,217,0.9)', color: 'white', padding: '4px 16px',

              borderRadius: '20px', fontWeight: 'bold', fontSize: '0.85rem',

              animation: 'turnFadeIn 0.4s ease-out',

              boxShadow: '0 0 20px rgba(74,144,217,0.4)',

            }}>

              🛡️ Stabilized!

            </div>

          </div>

        )}

        {/* "You" indicator for player's own character */}

        {isOwnCharacter && (

          <div style={{

            position: 'absolute',

            top: '-10px',

            right: '10px',

            background: '#27c95a',

            color: 'white',

            padding: '2px 8px',

            borderRadius: '10px',

            fontSize: '0.7rem',

            fontWeight: 'bold',

          }}>

            YOU

          </div>

        )}

        

        {/* Selection Checkbox */}

        <div 

            style={{ 

              position: 'absolute', 

              top: '8px', 

              left: '8px', 

              width: '20px', 

              height: '20px', 

              borderRadius: '4px',

              border: `2px solid ${isSelected ? '#4a90d9' : '#666'}`,

              background: isSelected ? '#4a90d9' : 'transparent',

              display: 'flex',

              alignItems: 'center',

              justifyContent: 'center',

              fontSize: '0.75rem',

              color: 'white',

            }}

            onClick={(e) => { e.stopPropagation(); toggleTarget(combatant.id); }}

          >

            {isSelected && '✓'}

          </div>

        

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', marginLeft: '28px' }}>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

            <span 

              style={{ cursor: 'pointer', fontSize: '0.7rem', color: '#888', userSelect: 'none' }}

              onClick={(e) => { e.stopPropagation(); setCollapsedCardIds(prev => { const n = new Set(prev); n.add(combatant.id); return n; }); }}

              title="Collapse card"

            >▼</span>

            {isCurrentTurn && <span style={{ color: '#ffd700' }}>⚔️</span>}

            <span style={{ fontSize: '1.1rem' }}>{isNPC ? '🤝' : isPlayer ? '👤' : '👹'}</span>

            <strong style={{ color: isPlayer ? '#27c95a' : '#c92727' }}>{combatant.name}</strong>

            {/* Pin icon - always visible, clickable */}

            <span 

              style={{ 

                cursor: 'pointer', fontSize: '0.7rem', 

                color: isPinned ? '#ffd700' : '#444',

                opacity: isPinned ? 1 : 0.5,

                transition: 'all 0.15s',

                userSelect: 'none',

              }}

              onClick={(e) => { e.stopPropagation(); setPinnedCombatantIds(prev => { const n = new Set(prev); isPinned ? n.delete(combatant.id) : n.add(combatant.id); return n; }); }}

              title={isPinned ? "Unpin from view" : "Pin to view"}

            >📌</span>

          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

            <span style={{ fontSize: '0.8rem', color: '#888' }}>Init:</span>

            {canEdit ? (

              <>

                <input

                  type="number"

                  step="any"

                  value={combatant.initiative ?? ''}

                  onChange={(e) => setInitiative(combatant.id, e.target.value)}

                  onClick={(e) => e.stopPropagation()}

                  placeholder="?"

                  style={{ ...S.input, width: '60px', padding: '4px', marginBottom: 0, textAlign: 'center', background: combatant.initiative === null ? 'rgba(201,162,39,0.3)' : undefined }}

                />

                <button

                  style={{ ...S.btn, ...S.btnXs, padding: '2px 6px', marginBottom: 0, fontSize: '0.75rem' }}

                  onClick={(e) => {

                    e.stopPropagation();

                    const initiativeRoll = rollD20WithAdvantage(null, combatant.id, 'initiative');

                    const d20 = initiativeRoll.selected;

                    const { initiativeMod, tiebreak } = getInitiativeRollParts(combatant);

                    const total = Math.round((d20 + initiativeMod + tiebreak) * 100) / 100;

                    const updated = combatants.map(c => c.id === combatant.id ? { ...c, initiative: total, initiativeRoll: d20, initiativeMod } : c);

                    onUpdateCombat({ ...combat, combatants: updated });

                  }}

                  title="Roll 1d20 + initiative mod (Dex score / 100 added as tiebreaker)"

                >🎲</button>

              </>

            ) : (

              <span style={{ fontWeight: 'bold', color: combatant.initiative === null ? '#c9a227' : 'white' }}>

                {combatant.initiative ?? '?'}

              </span>

            )}

          </div>

        </div>

        

        {/* Attack Routines (enemies, DM only) */}

        {isDM && !isPlayer && (combatant.attackRoutines || []).length > 0 && (

          <div style={{ marginTop: '6px', marginBottom: '4px' }}>

            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>

              {(combatant.attackRoutines || []).map(routine => (

                <button

                  key={routine.id}

                  style={{

                    padding: '3px 10px', fontSize: '0.72rem', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold',

                    background: 'rgba(201,39,39,0.2)', border: '1px solid rgba(201,39,39,0.5)', color: '#ff8a8a',

                    display: 'flex', alignItems: 'center', gap: '4px',

                  }}

                  onClick={(e) => { e.stopPropagation(); setAttackTargetId(''); setAttackResults(null); setAttackEditCombatant(attackEditCombatant === combatant.id + ':' + routine.id ? null : combatant.id + ':' + routine.id); }}

                  title={routine.attacks.map(a => `${a.count > 1 ? a.count + '× ' : ''}${a.name} [${getAttackRoutineTypeLabel(a.attackType)}]: +${a.toHitBonus} to hit, ${a.damageDice} ${a.damageType}`).join('\n')}

                >⚔️ {routine.name}</button>

              ))}

              <button

                style={{ padding: '2px 6px', fontSize: '0.6rem', borderRadius: '3px', cursor: 'pointer', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#777' }}

                onClick={(e) => { e.stopPropagation(); setAttackEditCombatant(attackEditCombatant === combatant.id + ':edit' ? null : combatant.id + ':edit'); }}

              >✏️</button>

            </div>

            

            {/* Attack Execution Panel */}

            {attackEditCombatant && attackEditCombatant.startsWith(combatant.id + ':') && !attackEditCombatant.endsWith(':edit') && (() => {

              const routineId = attackEditCombatant.split(':')[1];

              const routine = (combatant.attackRoutines || []).find(r => r.id === routineId);

              if (!routine) return null;

              return (

                <div style={{ marginTop: '6px', padding: '8px', background: 'rgba(201,39,39,0.08)', borderRadius: '6px', border: '1px solid rgba(201,39,39,0.25)' }} onClick={e => e.stopPropagation()}>

                  <div style={{ fontSize: '0.75rem', color: '#c9a227', fontWeight: 'bold', marginBottom: '6px' }}>

                    ⚔️ {routine.name}: {routine.attacks.map(a => `${a.count > 1 ? a.count + '× ' : ''}${a.name} [${getAttackRoutineTypeLabel(a.attackType)}] (+${a.toHitBonus}, ${a.damageDice} ${a.damageType})`).join(', ')}

                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>

                    <select

                      style={{ ...S.input, width: 'auto', flex: 1, marginBottom: 0, fontSize: '0.75rem', padding: '4px 6px' }}

                      value={attackTargetId}

                      onChange={e => setAttackTargetId(e.target.value)}

                    >

                      <option value="">Select target...</option>

                      {combatants.filter(c => c.id !== combatant.id && c.currentHp > 0).map(c => (
                        <option key={c.id} value={c.id}>{c.type === 'enemy' ? 'ð¹' : 'ð§'} {c.name} (AC {getCombatantDisplayArmorClass(c)})</option>

                      ))}

                    </select>

                    <button

                      style={{ ...S.btn, ...S.btnSm, background: 'rgba(201,39,39,0.3)', borderColor: '#c92727', color: '#ff6b6b', opacity: attackTargetId ? 1 : 0.4, marginBottom: 0 }}

                      disabled={!attackTargetId}

                      onClick={() => { executeAttackRoutine(combatant.id, routine, attackTargetId); }}

                    >🎯 Execute</button>

                  </div>

                  

                  {/* Attack Results Display */}

                  {attackResults && attackResults.combatantId === combatant.id && (

                    <div style={{ marginTop: '8px' }}>

                      <div style={{ fontSize: '0.7rem', color: '#aaa', marginBottom: '4px' }}>vs <strong style={{ color: '#e8d5b7' }}>{attackResults.targetName}</strong> (AC {attackResults.targetAC ?? getCombatantDisplayArmorClass(combatants.find(c => c.id === attackResults.targetId) || {})})</div>

                      {attackResults.results.map((r, ri) => {

                        const hitColor = r.isCrit ? '#00ff00' : r.isHit ? '#6cff8a' : '#ff6b6b';

                        const hitLabel = r.isCrit ? 'CRIT!' : r.isNatMiss ? 'NAT 1!' : r.isHit ? 'Hit' : 'Miss';

                        return (

                          <div key={ri} style={{

                            display: 'flex', alignItems: 'center', gap: '6px', padding: '3px 6px', marginBottom: '2px',

                            background: r.enabled ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)', borderRadius: '4px',

                            opacity: r.enabled ? 1 : 0.4,

                            fontSize: '0.75rem',

                          }}>

                            <input type="checkbox" checked={r.enabled} onChange={() => toggleAttackResult(ri)} style={{ width: '12px', height: '12px' }} />

                            <span style={{ color: '#e8d5b7', fontWeight: 'bold', minWidth: '70px' }}>{r.attackName}</span>

                            {r.attackType && r.attackType !== 'generic' && (

                              <span style={{ color: '#8ab4ff', fontSize: '0.62rem', minWidth: '38px' }}>{getAttackRoutineTypeLabel(r.attackType)}</span>

                            )}

                            <span style={{ color: '#aaa' }}>

                              {r.d20Roll.all.length > 1 ? (

                                r.d20Roll.all.map((d, di) => (

                                  <span key={di} style={{ color: d === r.d20Roll.selected ? '#e8d5b7' : '#555', marginRight: '2px' }}>{d}</span>

                                ))

                              ) : (

                                <span>{r.d20Roll.selected}</span>

                              )}

                              <span style={{ color: '#888' }}>

                                {r.toHitBonus >= 0 ? `+${r.toHitBonus}` : r.toHitBonus}

                                {r.itemAttackBonus ? (

                                  <span title={(r.itemAttackBonusSources || []).map(source => `${source.source}: ${source.value >= 0 ? '+' : ''}${source.value}`).join(', ') || 'Equipped item attack bonus'}>

                                    {r.itemAttackBonus >= 0 ? `+${r.itemAttackBonus}` : r.itemAttackBonus}

                                  </span>

                                ) : null}

                                =

                              </span>

                              <span style={{ fontWeight: 'bold', color: hitColor }}>{r.hitTotal}</span>

                            </span>

                            <span style={{ padding: '1px 5px', borderRadius: '3px', fontSize: '0.62rem', fontWeight: 'bold', background: r.isCrit ? 'rgba(0,255,0,0.2)' : r.isHit ? 'rgba(39,201,90,0.15)' : 'rgba(231,76,60,0.15)', color: hitColor }}>{hitLabel}</span>

                            {r.isHit && r.damageTotal > 0 && (

                              <span style={{ marginLeft: 'auto', fontWeight: 'bold', color: r.isCrit ? '#00ff00' : '#ffd700' }}>

                                {r.damageTotal} <span style={{ fontSize: '0.6rem', color: '#888', fontWeight: 'normal' }}>{r.damageType}{r.isCrit ? ' (2×)' : ''}</span>

                              </span>

                            )}

                          </div>

                        );

                      })}

                      {/* Totals and Apply */}

                      {(() => {

                        const hits = attackResults.results.filter(r => r.enabled && r.isHit);

                        const totalDmg = hits.reduce((sum, r) => sum + r.damageTotal, 0);

                        const crits = hits.filter(r => r.isCrit).length;

                        return (

                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px', paddingTop: '6px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>

                            <span style={{ fontSize: '0.75rem', color: '#aaa' }}>

                              {hits.length}/{attackResults.results.length} hits{crits > 0 ? ` (${crits} crit${crits > 1 ? 's' : ''})` : ''} — <strong style={{ color: '#ffd700' }}>{totalDmg} total</strong>

                            </span>

                            <div style={{ display: 'flex', gap: '4px' }}>

                              <button style={{ ...S.btn, ...S.btnXs, background: 'rgba(39,201,90,0.3)', borderColor: '#27c95a', color: '#6cff8a', marginBottom: 0, fontWeight: 'bold' }} onClick={applyAttackResults}>✅ Apply All</button>

                              <button style={{ ...S.btn, ...S.btnXs, marginBottom: 0 }} onClick={() => setAttackResults(null)}>✕</button>

                            </div>

                          </div>

                        );

                      })()}

                    </div>

                  )}

                </div>

              );

            })()}

            

            {/* Attack Routine Editor */}

            {attackEditCombatant === combatant.id + ':edit' && (

              <div style={{ marginTop: '6px', padding: '8px', background: 'rgba(155,89,182,0.08)', borderRadius: '6px', border: '1px solid rgba(155,89,182,0.25)' }} onClick={e => e.stopPropagation()}>

                <div style={{ fontSize: '0.75rem', color: '#9b59b6', fontWeight: 'bold', marginBottom: '6px' }}>✏️ Edit Attack Routines</div>

                {(combatant.attackRoutines || []).map((routine, rIdx) => (

                  <div key={routine.id} style={{ marginBottom: '8px', padding: '6px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px' }}>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>

                      <input

                        style={{ ...S.input, flex: 1, marginBottom: 0, fontSize: '0.75rem', padding: '3px 6px' }}

                        value={routine.name}

                        onChange={e => {

                          const updated = [...(combatant.attackRoutines || [])];

                          updated[rIdx] = { ...routine, name: e.target.value };

                          updateCombatantRoutines(combatant.id, updated);

                        }}

                        placeholder="Routine name..."

                      />

                      <button style={{ ...S.btn, ...S.btnXs, color: '#ff6b6b', marginBottom: 0, fontSize: '0.6rem' }} onClick={() => {

                        updateCombatantRoutines(combatant.id, (combatant.attackRoutines || []).filter((_, i) => i !== rIdx));

                      }}>🗑</button>

                    </div>

                    {(routine.attacks || []).map((atk, aIdx) => (

                      <div key={atk.id} style={{ display: 'flex', gap: '3px', alignItems: 'center', marginBottom: '3px', fontSize: '0.7rem' }}>

                        <input style={{ ...S.input, width: '70px', marginBottom: 0, fontSize: '0.7rem', padding: '2px 4px' }} value={atk.name} placeholder="Name" onChange={e => {

                          const updated = [...(combatant.attackRoutines || [])];

                          const attacks = [...routine.attacks]; attacks[aIdx] = { ...atk, name: e.target.value }; updated[rIdx] = { ...routine, attacks };

                          updateCombatantRoutines(combatant.id, updated);

                        }} />

                        <input style={{ ...S.input, width: '28px', marginBottom: 0, fontSize: '0.7rem', padding: '2px 3px', textAlign: 'center' }} type="number" value={atk.count || 1} min={1} max={10} onChange={e => {

                          const updated = [...(combatant.attackRoutines || [])]; const attacks = [...routine.attacks]; attacks[aIdx] = { ...atk, count: parseInt(e.target.value) || 1 }; updated[rIdx] = { ...routine, attacks };

                          updateCombatantRoutines(combatant.id, updated);

                        }} title="Count" />

                        <select

                          aria-label={`Attack type for ${atk.name || 'attack'}`}

                          style={{ ...S.input, width: '62px', marginBottom: 0, fontSize: '0.65rem', padding: '2px 3px' }}

                          value={normalizeAttackRoutineAttackType(atk.attackType)}

                          onChange={e => {

                            const updated = [...(combatant.attackRoutines || [])]; const attacks = [...routine.attacks]; attacks[aIdx] = { ...atk, attackType: e.target.value }; updated[rIdx] = { ...routine, attacks };

                            updateCombatantRoutines(combatant.id, updated);

                          }}

                          title="Attack type for item melee/ranged bonuses"

                        >

                          {ATTACK_ROUTINE_ATTACK_TYPES.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}

                        </select>

                        <span style={{ color: '#888', fontSize: '0.6rem' }}>×</span>

                        <span style={{ color: '#888', fontSize: '0.6rem' }}>+</span>

                        <input style={{ ...S.input, width: '30px', marginBottom: 0, fontSize: '0.7rem', padding: '2px 3px', textAlign: 'center' }} type="number" value={atk.toHitBonus || 0} onChange={e => {

                          const updated = [...(combatant.attackRoutines || [])]; const attacks = [...routine.attacks]; attacks[aIdx] = { ...atk, toHitBonus: parseInt(e.target.value) || 0 }; updated[rIdx] = { ...routine, attacks };

                          updateCombatantRoutines(combatant.id, updated);

                        }} title="To Hit Bonus" />

                        <input

                          style={{

                            ...S.input,

                            width: '65px', marginBottom: 0, fontSize: '0.7rem', padding: '2px 4px',

                            // Red tint when empty — flag silent-damage misconfigurations before execute.

                            ...((!atk.damageDice || !atk.damageDice.trim()) ? { border: '1px solid #c92727', background: 'rgba(201,39,39,0.08)' } : {}),

                          }}

                          value={atk.damageDice || ''}

                          placeholder="1d8+3 or 5"

                          onChange={e => {

                            const updated = [...(combatant.attackRoutines || [])]; const attacks = [...routine.attacks]; attacks[aIdx] = { ...atk, damageDice: e.target.value }; updated[rIdx] = { ...routine, attacks };

                            updateCombatantRoutines(combatant.id, updated);

                          }}

                          title="Damage dice (e.g. '2d6+3') or flat damage (e.g. '5'). Empty = attack deals no damage."

                        />

                        <select style={{ ...S.input, width: '70px', marginBottom: 0, fontSize: '0.65rem', padding: '2px 3px' }} value={atk.damageType || 'slashing'} onChange={e => {

                          const updated = [...(combatant.attackRoutines || [])]; const attacks = [...routine.attacks]; attacks[aIdx] = { ...atk, damageType: e.target.value }; updated[rIdx] = { ...routine, attacks };

                          updateCombatantRoutines(combatant.id, updated);

                        }}>

                          {Object.keys(DAMAGE_TYPES).map(dt => <option key={dt} value={dt}>{dt}</option>)}

                        </select>

                        <button style={{ background: 'none', border: 'none', color: '#ff6b6b', cursor: 'pointer', fontSize: '0.7rem', padding: '0 2px' }} onClick={() => {

                          const updated = [...(combatant.attackRoutines || [])]; const attacks = routine.attacks.filter((_, i) => i !== aIdx); updated[rIdx] = { ...routine, attacks };

                          updateCombatantRoutines(combatant.id, updated);

                        }}>✕</button>

                      </div>

                    ))}

                    <button style={{ ...S.btn, ...S.btnXs, fontSize: '0.6rem', marginBottom: 0, marginTop: '2px' }} onClick={() => {

                      const updated = [...(combatant.attackRoutines || [])];

                      updated[rIdx] = { ...routine, attacks: [...routine.attacks, { id: generateId(), name: 'Attack', count: 1, attackType: 'generic', toHitBonus: 4, damageDice: '1d8+2', damageType: 'slashing' }] };

                      updateCombatantRoutines(combatant.id, updated);

                    }}>+ Attack</button>

                  </div>

                ))}

                <button style={{ ...S.btn, ...S.btnXs, fontSize: '0.65rem', marginBottom: 0, color: '#9b59b6' }} onClick={() => {

                  updateCombatantRoutines(combatant.id, [...(combatant.attackRoutines || []), {

                    id: generateId(), name: 'Multiattack', attacks: [{ id: generateId(), name: 'Attack', count: 1, attackType: 'generic', toHitBonus: 4, damageDice: '1d8+2', damageType: 'slashing' }],

                  }]);

                }}>+ New Routine</button>

              </div>

            )}

          </div>

        )}

        

        {/* No routines — show add button for DM */}

        {isDM && !isPlayer && (combatant.attackRoutines || []).length === 0 && (

          <div style={{ marginTop: '4px' }}>

            <button

              style={{ padding: '2px 8px', fontSize: '0.62rem', borderRadius: '3px', cursor: 'pointer', background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.1)', color: '#555' }}

              onClick={(e) => { e.stopPropagation(); setAttackEditCombatant(attackEditCombatant === combatant.id + ':edit' ? null : combatant.id + ':edit'); }}

            >+ ⚔️ Add Attacks</button>

            {attackEditCombatant === combatant.id + ':edit' && (

              <div style={{ marginTop: '6px', padding: '8px', background: 'rgba(155,89,182,0.08)', borderRadius: '6px', border: '1px solid rgba(155,89,182,0.25)' }} onClick={e => e.stopPropagation()}>

                <button style={{ ...S.btn, ...S.btnXs, fontSize: '0.65rem', marginBottom: 0, color: '#9b59b6' }} onClick={() => {

                  updateCombatantRoutines(combatant.id, [{

                    id: generateId(), name: 'Multiattack', attacks: [{ id: generateId(), name: 'Attack', count: 1, attackType: 'generic', toHitBonus: 4, damageDice: '1d8+2', damageType: 'slashing' }],

                  }]);

                }}>+ New Routine</button>

              </div>

            )}

          </div>

        )}

        

        {/* HP Bar + Death Save System */}

        {(isPlayer || viewIsDM || combatant.showHp) ? (

          <div style={{ marginBottom: '8px' }}>

            {/* Dead state */}

            {isDead ? (

              <div style={{ textAlign: 'center', padding: '8px', background: 'rgba(80,0,0,0.3)', borderRadius: '6px', border: '1px solid #880000' }}>

                <div style={{ fontSize: '1.3rem', marginBottom: '2px' }}>☠️</div>

                <div style={{ color: '#880000', fontWeight: 'bold', fontSize: '0.85rem' }}>Dead</div>

                {isDM && (

                  <button

                    style={{ ...S.btn, ...S.btnXs, marginTop: '6px', background: 'rgba(39,201,90,0.2)', border: '1px solid #27c95a', color: '#27c95a' }}

                    onClick={(e) => { e.stopPropagation(); reviveCombatant(combatant.id); }}

                  >🔄 Revive</button>

                )}

              </div>

            ) : combatant.deathSaves && combatant.currentHp <= 0 ? (

              /* Unconscious / Death Save state */

              <div style={{ background: 'rgba(100,0,0,0.15)', borderRadius: '6px', border: '1px solid rgba(201,39,39,0.3)', padding: '8px' }} onClick={e => e.stopPropagation()}>

                {/* Status label */}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>

                  <span style={{ color: '#c92727', fontWeight: 'bold', fontSize: '0.8rem' }}>

                    💀 Unconscious

                  </span>

                  <span style={{ fontSize: '0.7rem', color: '#888' }}>0 / {combatant.maxHp} HP</span>

                </div>

                

                {/* HP Bar — empty red with light-blue hatched shield overlay when player-initiated */}

                <div style={{ height: '10px', background: 'rgba(0,0,0,0.4)', borderRadius: '5px', overflow: 'hidden', marginBottom: '4px', position: 'relative' }}>

                  <div style={{ width: '100%', height: '100%', background: 'rgba(201,39,39,0.15)', position: 'absolute' }} />

                  {combatant.deathSaves.shieldActive && !combatant.deathSaves.shieldBroken && (() => {

                    const threshold = combatant.deathSaves.shieldHp ?? deathSaveConfig.healingCheck.shieldHp;

                    const shieldPct = Math.min(100, (threshold / combatant.maxHp) * 100);

                    return (

                      <div style={{ 

                        width: `${shieldPct}%`, height: '100%', position: 'absolute', left: 0,

                        background: 'repeating-linear-gradient(45deg, rgba(125,181,255,0.35), rgba(125,181,255,0.35) 2px, rgba(170,210,255,0.15) 2px, rgba(170,210,255,0.15) 5px)',

                        borderRadius: shieldPct >= 100 ? '5px' : '5px 0 0 5px',

                        borderRight: shieldPct < 100 ? '2px solid rgba(125,181,255,0.6)' : 'none',

                      }} />

                    );

                  })()}

                </div>

                

                {/* Shield threshold label — only when player has activated */}

                {combatant.deathSaves.shieldActive && !combatant.deathSaves.shieldBroken && (

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>

                    <span style={{ fontSize: '0.68rem', color: '#7db5ff' }}>

                      🛡️ {deathSaveConfig.healingCheck.shieldName}: heal ≥ {combatant.deathSaves.shieldHp ?? deathSaveConfig.healingCheck.shieldHp} to pass

                    </span>

                    {isDM && (

                      <input

                        type="number"

                        value={combatant.deathSaves.shieldHp ?? deathSaveConfig.healingCheck.shieldHp}

                        onChange={(e) => {

                          const val = parseInt(e.target.value) || 0;

                          const ds = { ...combatant.deathSaves, shieldHp: Math.max(1, val) };

                          const uc = combatants.map(c => c.id === combatant.id ? { ...c, deathSaves: ds } : c);

                          onUpdateCombat({ ...combat, combatants: uc });

                        }}

                        style={{ ...S.input, width: '45px', padding: '2px 4px', fontSize: '0.65rem', marginBottom: 0, textAlign: 'center' }}

                        onClick={e => e.stopPropagation()}

                      />

                    )}

                  </div>

                )}

                

                {/* Death Save Roll Result Animation */}

                {deathSaveRollResult?.combatantId === combatant.id && (

                  <div style={{ 

                    textAlign: 'center', marginBottom: '6px', padding: '4px',

                    background: deathSaveRollResult.roll >= 10 ? 'rgba(39,201,90,0.15)' : 'rgba(201,39,39,0.15)',

                    borderRadius: '6px',

                    border: `1px solid ${deathSaveRollResult.roll === 20 ? '#ffd700' : deathSaveRollResult.roll === 1 ? '#ff0000' : deathSaveRollResult.roll >= 10 ? '#27c95a' : '#c92727'}`,

                  }}>

                    <span style={{ 

                      fontSize: '1.4rem', fontWeight: 'bold', fontFamily: 'monospace',

                      color: deathSaveRollResult.roll === 20 ? '#ffd700' : deathSaveRollResult.roll === 1 ? '#ff0000' : deathSaveRollResult.roll >= 10 ? '#27c95a' : '#c92727',

                      animation: 'deathSaveRoll 0.6s ease-out',

                      display: 'inline-block',

                    }}>

                      🎲 {deathSaveRollResult.roll}

                    </span>

                    <span style={{ fontSize: '0.7rem', marginLeft: '8px', color: deathSaveRollResult.roll >= 10 ? '#27c95a' : '#c92727' }}>

                      {deathSaveRollResult.roll === 20 ? (deathSaveConfig.nat20Behavior === 'revive' ? 'NAT 20 — REVIVE!' : 'NAT 20!') :

                       deathSaveRollResult.roll === 1 ? 'NAT 1 — Critical Fail!' :

                       deathSaveRollResult.roll >= 10 ? 'Success' : 'Failure'}

                    </span>

                    {deathSaveRollResult.advMode && deathSaveRollResult.allDice && (

                      <span style={{ fontSize: '0.62rem', marginLeft: '8px', color: '#aaa' }}>

                        {deathSaveRollResult.advMode === 'advantage' ? 'ADV' : 'DIS'} {deathSaveRollResult.allDice.join('/')}

                      </span>

                    )}

                  </div>

                )}

                

                {/* Death Save Pips — Deaths LEFT, Saves RIGHT */}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '4px', marginBottom: '6px' }}>

                  {/* Failures (left) */}

                  <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>

                    <span style={{ fontSize: '0.6rem', color: '#c92727', fontWeight: 'bold', minWidth: '14px' }}>☠️</span>

                    {Array.from({ length: deathSaveConfig.failuresNeeded }).map((_, i) => (

                      <div

                        key={`f-${i}`}

                        title={`Death ${i + 1}`}

                        style={{

                          width: '14px', height: '14px', borderRadius: '50%',

                          border: `2px solid ${i < (combatant.deathSaves.failures || 0) ? '#c92727' : '#555'}`,

                          background: i < (combatant.deathSaves.failures || 0) ? '#c92727' : 'transparent',

                          transition: 'all 0.2s',

                        }}

                      />

                    ))}

                  </div>

                  

                  {/* Center: Roll button */}

                  {canEdit && (

                    <button

                      style={{

                        padding: '3px 10px', fontSize: '0.72rem', fontWeight: 'bold',

                        borderRadius: '4px', cursor: 'pointer',

                        border: '1px solid #c9a227', background: 'rgba(201,162,39,0.2)',

                        color: '#ffd700',

                        animation: isCurrentTurn ? 'tickGlowDot 1.8s ease-in-out infinite' : 'none',

                      }}

                      onClick={(e) => { e.stopPropagation(); rollDeathSave(combatant.id); }}

                    >🎲 Roll</button>

                  )}

                  

                  {/* Successes (right) */}

                  <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>

                    {Array.from({ length: deathSaveConfig.successesNeeded }).map((_, i) => (

                      <div

                        key={`s-${i}`}

                        title={`Save ${i + 1}`}

                        style={{

                          width: '14px', height: '14px', borderRadius: '50%',

                          border: `2px solid ${i < (combatant.deathSaves.successes || 0) ? '#27c95a' : '#555'}`,

                          background: i < (combatant.deathSaves.successes || 0) ? '#27c95a' : 'transparent',

                          transition: 'all 0.2s',

                        }}

                      />

                    ))}

                    <span style={{ fontSize: '0.6rem', color: '#27c95a', fontWeight: 'bold', minWidth: '10px' }}>✓</span>

                  </div>

                </div>

                

                {/* Fail / Save buttons */}

                {canEdit && (

                  <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>

                    <button

                      style={{

                        flex: 1, padding: '4px 0', fontSize: '0.7rem', fontWeight: 'bold',

                        borderRadius: '4px', cursor: 'pointer',

                        border: '1px solid #c92727', background: 'rgba(201,39,39,0.12)',

                        color: '#ff6666',

                      }}

                      onClick={(e) => { e.stopPropagation(); addDeathFailure(combatant.id); }}

                    >☠️ Fail Save</button>

                    <button

                      style={{

                        flex: 1, padding: '4px 0', fontSize: '0.7rem', fontWeight: 'bold',

                        borderRadius: '4px', cursor: 'pointer',

                        border: '1px solid #27c95a', background: 'rgba(39,201,90,0.12)',

                        color: '#27c95a',

                      }}

                      onClick={(e) => { e.stopPropagation(); addDeathSuccess(combatant.id); }}

                    >✓ Pass Save</button>

                  </div>

                )}

                

                {/* Initiate Heal Check — small text link, only before shield activated */}

                {canEdit && deathSaveConfig.healingCheck.enabled && 

                 !combatant.deathSaves.shieldActive && !combatant.deathSaves.shieldBroken && (

                  <div style={{ textAlign: 'center', marginBottom: '4px' }}>

                    <span

                      style={{

                        fontSize: '0.68rem', color: '#7db5ff', cursor: 'pointer',

                        textDecoration: 'underline', textDecorationStyle: 'dotted',

                      }}

                      onClick={(e) => { e.stopPropagation(); initiateHealCheck(combatant.id); }}

                    >🛡️ Initiate Heal Check</span>

                    <div style={{ fontSize: '0.55rem', color: '#666', marginTop: '1px' }}>

                      Next heal must meet {deathSaveConfig.healingCheck.shieldHp} HP — pass = save, fail = death

                    </div>

                  </div>

                )}

                

                {/* Active shield status — shown after player initiates, heals auto-resolve */}

                {combatant.deathSaves.shieldActive && !combatant.deathSaves.shieldBroken && (

                  <div style={{ textAlign: 'center', marginBottom: '4px', fontSize: '0.62rem', color: '#7db5ff', fontStyle: 'italic' }}>

                    Awaiting healing — heal ≥ {combatant.deathSaves.shieldHp ?? deathSaveConfig.healingCheck.shieldHp} = pass, below = fail

                  </div>

                )}

                

                {/* DM Quick Actions — Revive and Kill */}

                {isDM && (

                  <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>

                    <button style={{ ...S.btn, ...S.btnXs, background: 'rgba(39,201,90,0.15)', border: '1px solid #27c95a', color: '#27c95a' }}

                      onClick={(e) => { e.stopPropagation(); reviveCombatant(combatant.id); }}>🔄 Revive</button>

                    <button style={{ ...S.btn, ...S.btnXs, background: 'rgba(136,0,0,0.15)', border: '1px solid #880000', color: '#cc4444' }}

                      onClick={(e) => { e.stopPropagation(); killCombatant(combatant.id); }}>☠️ Kill</button>

                  </div>

                )}

              </div>

            ) : (

              /* Normal HP display */

              <>

                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '4px' }}>

                  <span>HP</span>

                  <span style={{ color: hpColor }}>

                    {combatant.currentHp}{tempHp > 0 && <span style={{ color: '#87ceeb' }}>+{tempHp}</span>} / {combatant.maxHp}

                  </span>

                </div>

                <div style={{ height: '8px', background: 'rgba(0,0,0,0.3)', borderRadius: '4px', overflow: 'hidden', position: 'relative' }}>

                  <div style={{ width: `${hpPercent}%`, height: '100%', background: hpColor, transition: 'width 0.3s', position: 'absolute', left: 0, borderRadius: tempHp > 0 ? '4px 0 0 4px' : '4px' }} />

                  {tempHp > 0 && (

                    <div style={{ 

                      width: `${tempHpPercent}%`, height: '100%', position: 'absolute',

                      left: `${hpPercent}%`,

                      background: 'repeating-linear-gradient(45deg, rgba(125,181,255,0.45), rgba(125,181,255,0.45) 2px, rgba(170,210,255,0.25) 2px, rgba(170,210,255,0.25) 5px)',

                      borderRight: '2px solid rgba(125,181,255,0.6)',

                      transition: 'width 0.3s',

                    }} />

                  )}

                </div>

              </>

            )}

          </div>

        ) : (

          <div style={{ marginBottom: '8px', fontSize: '0.8rem', color: '#666', fontStyle: 'italic' }}>HP: ?</div>

        )}

        

        {/* DM Visibility Controls for Enemies */}

        {viewIsDM && !isPlayer && (

          <div style={{ marginBottom: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px' }} onClick={e => e.stopPropagation()}>

            <label style={{ fontSize: '0.7rem', color: '#888', display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer' }}>

              <input type="checkbox" checked={combatant.showHp || false} onChange={() => toggleEnemyVisibility(combatant.id, 'showHp')} />

              HP

            </label>

            <label style={{ fontSize: '0.7rem', color: '#888', display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer' }}>

              <input type="checkbox" checked={combatant.showAc || false} onChange={() => toggleEnemyVisibility(combatant.id, 'showAc')} />

              AC

            </label>

            <label style={{ fontSize: '0.7rem', color: '#888', display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer' }}>

              <input type="checkbox" checked={combatant.showResistances || false} onChange={() => toggleEnemyVisibility(combatant.id, 'showResistances')} />

              Res

            </label>

            <label style={{ fontSize: '0.7rem', color: '#888', display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer' }}>

              <input type="checkbox" checked={combatant.showVulnerabilities || false} onChange={() => toggleEnemyVisibility(combatant.id, 'showVulnerabilities')} />

              Vuln

            </label>

          </div>

        )}

        

        {/* Pending Damage Alert */}

        {targetPending.length > 0 && (

          <div style={{ marginBottom: '8px', padding: '8px', background: 'rgba(201,39,39,0.2)', borderRadius: '6px', border: '1px solid #c92727' }}>

            <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#ff6666', marginBottom: '6px' }}>

              ⚠️ Incoming Damage!

            </div>

            {targetPending.map(pending => {

              const isReacting = reactingToPendingId === pending.id;

              return (

                <div key={pending.id} style={{ marginBottom: '6px', padding: '6px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px' }}>

                  <div style={{ fontSize: '0.8rem', marginBottom: '4px' }}>

                    <strong>{pending.source}</strong>: {pending.amount} {DAMAGE_TYPES[pending.type]?.icon} {pending.type}

                    {pending.wasCrit && <span style={{ color: '#ffd700' }}> (CRIT!)</span>}

                    {pending.isRaidwide && <span style={{ color: '#ff00ff' }}> (Raid-Wide)</span>}

                  </div>

                  {(canEdit || isDM) && !isReacting && (

                    <div style={{ display: 'flex', gap: '4px' }}>

                      <button 

                        style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, flex: 1 }}

                        onClick={(e) => { e.stopPropagation(); acceptDamage(pending.id); }}

                      >

                        Accept ({pending.amount})

                      </button>

                      <button 

                        style={{ ...S.btn, ...S.btnEdit, ...S.btnXs, flex: 1 }}

                        onClick={(e) => { 

                          e.stopPropagation(); 

                          setReactingToPendingId(pending.id);

                          setReactionCustomAmount('');

                          setReactionMode('points');

                        }}

                      >

                        ⚡ React

                      </button>

                      {isDM && (

                        <button 

                          style={{ ...S.btn, ...S.btnXs }}

                          onClick={(e) => { e.stopPropagation(); cancelPendingDamage(pending.id); }}

                        >

                          ✕

                        </button>

                      )}

                    </div>

                  )}

                  {/* Inline Reaction Panel */}

                  {isReacting && (canEdit || isDM) && (() => {

                    const reductionVal = parseInt(reactionCustomAmount) || 0;

                    const finalDmg = reactionMode === 'percent' 

                      ? Math.max(0, Math.floor(pending.amount * (1 - reductionVal / 100)))

                      : Math.max(0, pending.amount - reductionVal);

                    return (

                    <div style={{ marginTop: '4px', padding: '6px', background: 'rgba(74,144,217,0.1)', borderRadius: '4px', border: '1px solid rgba(74,144,217,0.3)' }}>

                      <div style={{ fontSize: '0.7rem', color: '#7db5ff', fontWeight: 'bold', marginBottom: '6px' }}>

                        ⚡ React to {pending.amount} damage

                      </div>

                      {/* Reduce by input with mode toggle */}

                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap' }}>

                        <span style={{ fontSize: '0.65rem', color: '#aaa', whiteSpace: 'nowrap' }}>Reduce by</span>

                        <input

                          type="number"

                          value={reactionCustomAmount}

                          onChange={(e) => setReactionCustomAmount(e.target.value)}

                          placeholder="0"

                          min="0"

                          style={{ ...S.input, width: '50px', fontSize: '0.7rem', padding: '2px 4px', marginBottom: 0, textAlign: 'center' }}

                          onClick={(e) => e.stopPropagation()}

                        />

                        {/* Points / Percent toggle */}

                        <div style={{ display: 'flex', borderRadius: '4px', overflow: 'hidden', border: '1px solid rgba(74,144,217,0.3)' }}>

                          <button

                            style={{ ...S.btn, padding: '2px 8px', fontSize: '0.65rem', borderRadius: 0, border: 'none',

                              background: reactionMode === 'points' ? 'rgba(74,144,217,0.4)' : 'transparent',

                              color: reactionMode === 'points' ? '#7db5ff' : '#666' }}

                            onClick={(e) => { e.stopPropagation(); setReactionMode('points'); }}

                          >pts</button>

                          <button

                            style={{ ...S.btn, padding: '2px 8px', fontSize: '0.65rem', borderRadius: 0, border: 'none', borderLeft: '1px solid rgba(74,144,217,0.2)',

                              background: reactionMode === 'percent' ? 'rgba(74,144,217,0.4)' : 'transparent',

                              color: reactionMode === 'percent' ? '#7db5ff' : '#666' }}

                            onClick={(e) => { e.stopPropagation(); setReactionMode('percent'); }}

                          >%</button>

                        </div>

                        <button

                          style={{ ...S.btn, ...S.btnXs, background: 'rgba(74,144,217,0.3)', border: '1px solid #4a90d9', color: '#7db5ff', whiteSpace: 'nowrap' }}

                          onClick={(e) => {

                            e.stopPropagation();

                            reactToDamage(pending.id, finalDmg);

                            setReactingToPendingId(null);

                          }}

                        >

                          Apply ({finalDmg})

                        </button>

                      </div>

                      {/* Negate + Cancel */}

                      <div style={{ display: 'flex', gap: '4px' }}>

                        <button

                          style={{ ...S.btn, ...S.btnXs, background: 'rgba(39,201,90,0.15)', border: '1px solid rgba(39,201,90,0.4)', color: '#6cff8a', flex: 1 }}

                          onClick={(e) => { e.stopPropagation(); reactToDamage(pending.id, 0); setReactingToPendingId(null); }}

                        >

                          Negate All

                        </button>

                        <button

                          style={{ ...S.btn, ...S.btnXs, color: '#888', flex: 1 }}

                          onClick={(e) => { e.stopPropagation(); setReactingToPendingId(null); }}

                        >

                          Cancel

                        </button>

                      </div>

                    </div>

                    );

                  })()}

                </div>

              );

            })}

          </div>

        )}

        

        {/* Stats - Hidden for enemies unless DM or show flags enabled */}

        <div style={{ display: 'flex', gap: '12px', fontSize: '0.8rem', color: '#888', flexWrap: 'wrap', alignItems: 'center' }}>

          {(isPlayer || viewIsDM || combatant.showAc) ? (() => {

            // 2026-04-28: char.armorClass=10 is the createChar legacy default; treat it as

            // "no override" and fall through to the armor type's baseAC. Without this guard

            // the in-combat AC label always shows 10 even when the char has Light/Med/Heavy

            // armor selected (matches the home-sheet AC display).

            const charForAC = chars[combatant.id];

            const acContext = getLinkedArmorContext(charForAC);

            const effectiveAC = acContext?.totalArmorClass ?? combatant.ac ?? combatant.armorClass ?? 10;

            const baseLabel = acContext?.hasOverride

              ? `Override: ${acContext.overrideArmorClass}`

              : `Base (${acContext?.currentArmor?.name || 'Armor'}): ${acContext?.baseArmorClass ?? (combatant.armorClass || 10)}`;

            const dexLabel = !acContext?.hasOverride && acContext?.dexModifier

              ? `\nDEX: ${acContext.dexModifier >= 0 ? '+' : ''}${acContext.dexModifier}`

              : '';

            const bonusSources = acContext ? [...acContext.legacySources, ...acContext.runtimeSources] : [];

            const totalBonus = acContext?.totalBonus || 0;

            const acBonus = totalBonus;

            return (

              <span title={bonusSources.length > 0 ? `${baseLabel}${dexLabel}\n${bonusSources.map((entry) => `${entry.source}: ${entry.value >= 0 ? '+' : ''}${entry.value}`).join('\n')}` : `${baseLabel}${dexLabel}`}>

                🛡️ AC: {effectiveAC}{acBonus !== 0 && <span style={{ color: acBonus > 0 ? '#27c95a' : '#e74c3c', fontSize: '0.7rem' }}> ({acBonus > 0 ? '+' : ''}{acBonus})</span>}

              </span>

            );

          })() : (

            <span>🛡️ AC: ?</span>

          )}

          {/* Temp HP display/editor */}

          {(isPlayer || viewIsDM) && (

            tempHp > 0 || viewIsDM ? (

              <span style={{ color: '#87ceeb', display: 'flex', alignItems: 'center', gap: '3px' }}>

                🩵 Temp: {viewIsDM ? (

                  <input

                    type="number"

                    value={tempHp}

                    min="0"

                    onChange={(e) => {

                      const val = Math.max(0, parseInt(e.target.value) || 0);

                      const uc = combatants.map(c => c.id === combatant.id ? { ...c, tempHp: val } : c);

                      onUpdateCombat({ ...combat, combatants: uc });

                      if (chars[combatant.id]) {

                        onUpdateChars({ ...chars, [combatant.id]: { ...chars[combatant.id], tempHp: val } });

                      }

                    }}

                    style={{ ...S.input, width: '38px', padding: '1px 3px', fontSize: '0.75rem', marginBottom: 0, textAlign: 'center', color: '#87ceeb', background: 'rgba(125,181,255,0.1)', border: '1px solid rgba(125,181,255,0.3)' }}

                    onClick={e => e.stopPropagation()}

                  />

                ) : tempHp}

              </span>

            ) : null

          )}

          {combatant.raidwideResist > 0 && (viewIsDM || isPlayer) && <span style={{ color: '#ff00ff' }}>🌀 Raid: {combatant.raidwideResist}%</span>}

        </div>

        

        {/* Resistances/Vulnerabilities Summary - Hidden for enemies unless DM or show flags enabled */}

        {(isPlayer || viewIsDM) ? (

          (combatant.resistances?.length > 0 || combatant.vulnerabilities?.length > 0 || combatant.immunities?.length > 0) && (

            <div style={{ marginTop: '6px', fontSize: '0.75rem' }}>

              {combatant.resistances?.length > 0 && (

                <div style={{ color: '#4a90d9' }}>

                  Res: {combatant.resistances.map(r => typeof r === 'string' ? `${r}(50%)` : r?.type ? `${r.type}(${r.percent ?? 50}%)` : null).filter(Boolean).join(', ')}

                </div>

              )}

              {combatant.vulnerabilities?.length > 0 && (

                <div style={{ color: '#ff6666' }}>

                  Vuln: {combatant.vulnerabilities.map(v => typeof v === 'string' ? `${v}(50%)` : v?.type ? `${v.type}(${v.percent ?? 50}%)` : null).filter(Boolean).join(', ')}

                </div>

              )}

              {combatant.immunities?.length > 0 && (

                <div style={{ color: '#888' }}>

                  Immune: {combatant.immunities.join(', ')}

                </div>

              )}

            </div>

          )

        ) : (

          <>

            {combatant.showResistances && combatant.resistances?.length > 0 && (

              <div style={{ marginTop: '6px', fontSize: '0.75rem', color: '#4a90d9' }}>

                Res: {combatant.resistances.map(r => typeof r === 'string' ? `${r}(50%)` : r?.type ? `${r.type}(${r.percent ?? 50}%)` : null).filter(Boolean).join(', ')}

              </div>

            )}

            {combatant.showVulnerabilities && combatant.vulnerabilities?.length > 0 && (

              <div style={{ marginTop: '6px', fontSize: '0.75rem', color: '#ff6666' }}>

                Vuln: {combatant.vulnerabilities.map(v => typeof v === 'string' ? `${v}(50%)` : v?.type ? `${v.type}(${v.percent ?? 50}%)` : null).filter(Boolean).join(', ')}

              </div>

            )}

          </>

        )}

        

        {/* Expandable Resistance Editor */}

        {canEdit && (

          <button 

            style={{ ...S.btn, ...S.btnXs, marginTop: '6px', fontSize: '0.7rem' }}

            onClick={(e) => { e.stopPropagation(); setExpandedCombatant(isExpanded ? null : combatant.id); }}

          >

            {isExpanded ? '▲ Hide Resistances' : '▼ Edit Resistances'}

          </button>

        )}

        

        {isExpanded && canEdit && (

          <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.1)' }} onClick={e => e.stopPropagation()}>

            <ResistanceEditor 

              label="Resistances" 

              icon="🛡️" 

              items={(combatant.resistances || []).map(r => typeof r === 'string' ? { type: r, percent: 50 } : r).filter(r => r?.type)} 

              onChange={(val) => updateCombatantResists(combatant.id, 'resistances', val)}

            />

            <ResistanceEditor 

              label="Vulnerabilities" 

              icon="⚡" 

              items={(combatant.vulnerabilities || []).map(v => typeof v === 'string' ? { type: v, percent: 50 } : v).filter(v => v?.type)} 

              onChange={(val) => updateCombatantResists(combatant.id, 'vulnerabilities', val)}

            />

            <div style={{ marginBottom: '8px' }}>

              <label style={{ ...S.label, fontSize: '0.8rem' }}>🌀 Raid-Wide Resistance %</label>

              <input 

                type="number" 

                value={combatant.raidwideResist || 0}

                onChange={(e) => updateCombatantResists(combatant.id, 'raidwideResist', parseInt(e.target.value) || 0)}

                style={{ ...S.input, width: '80px', marginBottom: 0 }}

                min="0"

                max="100"

              />

            </div>

          </div>

        )}

        

        {/* Concentration Indicator */}

        {chars[combatant.id]?.concentratingOn && (

          <div style={{ 

            marginTop: '8px', 

            padding: '5px 8px', 

            background: 'rgba(255,152,0,0.12)', 

            border: '1px solid rgba(255,152,0,0.4)',

            borderRadius: '5px',

            display: 'flex',

            alignItems: 'center',

            justifyContent: 'space-between',

            gap: '6px',

          }}>

            <span style={{ fontSize: '0.75rem', color: '#ff9800' }}>

              ⚡ Concentrating: <strong>{chars[combatant.id].concentratingOn}</strong>

            </span>

            {(isDM || (isPlayer && combatant.id === currentCharId)) && (

              <button

                style={{ 

                  background: 'rgba(201,39,39,0.3)', 

                  border: '1px solid #c92727', 

                  color: '#ff6666', 

                  fontSize: '0.65rem', 

                  padding: '2px 6px', 

                  borderRadius: '3px', 

                  cursor: 'pointer',

                  whiteSpace: 'nowrap',

                }}

                onClick={(e) => {

                  e.stopPropagation();

                  const spellName = chars[combatant.id].concentratingOn;

                  setPendingConfirm({

                    title: '⚡ Drop Concentration?',

                    message: `Drop ${combatant.name}'s concentration on ${spellName}?`,

                    details: 'This will remove all linked effects from all targets.',

                    actions: [

                      { label: 'Drop Concentration', color: '#c92727', onClick: () => {

                        const result = breakConcentration(combatant.id, isDM ? 'manually broken by DM' : 'voluntarily dropped');

                        onUpdateCombat({ ...combat, combatants: result.combatants });

                        onUpdateChars(result.chars);

                        setTurnNotifications(result.notifications);

                        setTimeout(() => setTurnNotifications([]), 6000);

                        setPendingConfirm(null);

                      }},

                      { label: 'Keep Concentrating', color: '#666', onClick: () => setPendingConfirm(null) },

                    ]

                  });

                }}

              >⚡ Drop</button>

            )}

          </div>

        )}

        

        {/* === COMBINED EFFECTS DISPLAY === */}

        {/* Conditions (only standalone ones not covered by buffs/debuffs) */}

        {/* Effects (buffs + debuffs) with integrated tick actions */}

        {(() => {

          const cardChar = chars[combatant.id];

          const buffs = (cardChar?.activeEffects || []).map((e, i) => ({ ...e, _kind: 'buff', _idx: i }));

          const debuffs = (combatant.debuffs || []).map((d, i) => ({ ...d, _kind: 'debuff', _idx: i }));

          const allEffects = [...buffs, ...debuffs];

          

          // Deduplicate: filter conditions that match a buff or debuff name

          const effectNames = new Set(allEffects.map(e => e.name));

          const standaloneConditions = (combatant.conditions || [])

            .map((cond, i) => ({ name: cond, _idx: i }))

            .filter(c => !effectNames.has(c.name) && !allEffects.some(e => c.name.includes(e.name) || e.name.includes(c.name)));

          

          // Tick effects for inline action buttons

          const tickEffects = getTickEffects(combatant);

          const tickLifecycleMap = buildSpellRuntimeLifecycleMap(tickEffects);

          const queuedLifecycleTickKeys = new Set(

            (lifecycleQueue || []).map(item => getQueueItemTickKey(item)).filter(Boolean),

          );

          

          // Focus mode

          const useFocus = playerFocusView && viewIsPlayer && viewCharId;

          

          // Nothing to show?

          if (standaloneConditions.length === 0 && allEffects.length === 0) return null;

          

          // Relevance scoring

          const withRelevance = allEffects.map(eff => {

            const isMyCast = viewCharId && eff.casterId === viewCharId;

            const isOnMe = viewCharId && combatant.id === viewCharId;

            const casterC = eff.casterId ? combatants.find(c => c.id === eff.casterId) : null;

            const isAllyCast = casterC && (casterC.type === 'player' || casterC.type === 'npc') && !isMyCast;

            const isEnemyCast = casterC && casterC.type === 'enemy';

            let relevance = isOnMe ? 3 : isMyCast ? 2 : isAllyCast ? 1 : 0;

            return { ...eff, isMyCast, isOnMe, isAllyCast, isEnemyCast, relevance };

          });

          

          if (useFocus) withRelevance.sort((a, b) => b.relevance - a.relevance);

          

          const durText = (e) => {

            if (e.duration === 'concentration') return '⚡';

            if (typeof e.duration === 'number') {

              const left = Math.max(0, e.duration - (combat.round - (e.roundApplied || combat.round)));

              return `${left}r`;

            }

            return '';

          };

          

          // Render a single effect pill — compact, with integrated tick

          const renderEffect = (eff) => {

            const storedAsBuff = eff._kind === 'buff';

            // Visual classification: if a "buff" has harmful properties, treat it visually as a debuff

            const isVisuallyHarmful = eff.dotDamage || (eff.saveType && eff.saveRemovesEffect !== false) || (eff._kind === 'debuff');

            const isBuff = storedAsBuff && !isVisuallyHarmful;

            // On my card: everything visible. On other cards: only my casts are prominent

            const isSmall = useFocus && (eff.relevance === 0 || (!eff.isOnMe && eff.relevance <= 1));

            const isEditing = !storedAsBuff && editingEffect?.type === 'debuff' && editingEffect?.combatantId === combatant.id && editingEffect?.index === eff._idx;

            

            // Tick state

            const renderedEffectRef = createCombatRuntimeEffectRef({

              effectId: eff.id || eff.effectId || null,

              effectGroupId: eff.effectGroupId || null,

              sourceCollection: storedAsBuff ? 'char.activeEffects' : 'combatant.debuffs',

              effectIndex: eff._idx,

              effectName: eff.name,

              casterId: eff.casterId || null,

            });

            const lifecycle = tickLifecycleMap.byRef.get(getRuntimeEffectRefKey(renderedEffectRef))

              || tickLifecycleMap.byName.get(`name:${eff.name}`)

              || null;

            const periodicTick = lifecycle?.periodic || null;

            const saveTick = lifecycle?.save || null;

            const periodicTickRef = periodicTick?.effectRef || renderedEffectRef;

            const saveTickRef = saveTick?.effectRef || renderedEffectRef;

            const periodicTickKey = periodicTick

              ? getCombatTickKey(combatant.id, periodicTick.type, periodicTickRef, periodicTick.name)

              : null;

            const saveTickKey = saveTick

              ? getCombatTickKey(combatant.id, 'save_only', saveTickRef, saveTick.name)

              : null;

            const periodicAlreadyTicked = periodicTickKey ? tickedThisTurn[periodicTickKey] : false;

            const saveAlreadyTicked = saveTickKey ? tickedThisTurn[saveTickKey] : false;

            const periodicQueued = periodicTickKey ? queuedLifecycleTickKeys.has(periodicTickKey) : false;

            const saveQueued = saveTickKey ? queuedLifecycleTickKeys.has(saveTickKey) : false;

            const tick = (!periodicAlreadyTicked && periodicTick)

              || (!saveAlreadyTicked && saveTick)

              || periodicTick

              || saveTick

              || null;

            const tickRef = tick?.type === 'save_only' ? saveTickRef : periodicTickRef;

            const alreadyTicked = tick

              ? (tick.type === 'save_only' ? saveAlreadyTicked : periodicAlreadyTicked)

              : false;

            

            // Tick interactivity: DM, caster, or target can click ▶ to tick

            const isEffectOnMe = viewCharId && combatant.id === viewCharId;

            const canManagePeriodicTick = periodicTick && !periodicQueued && (viewIsDM || isDM || periodicTick.casterId === viewCharId || isEffectOnMe);

            const canManageSaveTick = saveTick && !saveQueued && (viewIsDM || isDM || saveTick.casterId === viewCharId || isEffectOnMe);

            const canManageTick = tick && (tick.type === 'save_only' ? canManageSaveTick : canManagePeriodicTick);

            const periodicGlowActive = canManagePeriodicTick && shouldTickGlow(periodicTick, combatant) && !periodicAlreadyTicked;

            const saveGlowActive = canManageSaveTick && shouldTickGlow(saveTick, combatant) && !saveAlreadyTicked;

            const glowActive = periodicGlowActive || saveGlowActive;

            

            // Awareness: other players see a softer highlight on ticking effects they can't manage

            const tickIsReady = (!!periodicTick && !periodicQueued && shouldTickGlow(periodicTick, combatant) && !periodicAlreadyTicked)

              || (!!saveTick && !saveQueued && shouldTickGlow(saveTick, combatant) && !saveAlreadyTicked);

            const isAwarenessOnly = tickIsReady && !canManageTick;

            

            // Colors based on visual classification

            const rgb = isBuff ? '39,201,90' : '231,76,60';

            const accentColor = isBuff ? '#27c95a' : '#e74c3c';

            

            // Sizing: larger when glowing (time to tick), smaller when irrelevant, faded when ticked

            // isGlowing = actionable pulsing glow (DM/caster/target)

            // isAwarenessOnly = softer static highlight for observers

            const isGlowing = glowActive;

            const pillPad = isSmall ? '1px 5px' : isGlowing ? '3px 9px' : '2px 7px';

            const pillFont = isSmall ? '0.58rem' : isGlowing ? '0.74rem' : '0.67rem';

            

            const pillBg = alreadyTicked ? 'rgba(80,80,80,0.1)' 

              : isEditing ? `rgba(${rgb},0.35)` 

              : isAwarenessOnly ? `rgba(${rgb},0.08)`

              : `rgba(${rgb},${isSmall ? '0.04' : isGlowing ? '0.18' : '0.1'})`;

            const textColor = alreadyTicked ? '#666' : isSmall ? '#888' : isAwarenessOnly ? (isBuff ? '#6abf78' : '#d49090') : (isBuff ? '#8eff9e' : '#ffb0a0');

            const dimColor = isSmall ? '#666' : '#999';

            

            // Glow animation: full pulse for actionable, none for awareness-only

            const glowSource = periodicGlowActive ? periodicTick : saveGlowActive ? saveTick : null;

            const glowAnim = isGlowing ? (glowSource.type === 'dot' ? 'tickGlowDot' : glowSource.type === 'save_only' ? 'tickGlowSave' : 'tickGlowHot') : null;

            

            const dur = durText(eff);

            

            // Build rich tooltip

            const tooltipLines = [eff.name];

            if (eff.modifier) tooltipLines.push(`Effect: ${eff.modifier}`);

            if (eff.bonusDice) tooltipLines.push(`Bonus Dice: ${eff.bonusDice}`);

            if (eff.percentIncrease) tooltipLines.push(`Damage: ${eff.percentIncrease > 0 ? '+' : ''}${eff.percentIncrease}%`);

            if (eff.flatBonus) tooltipLines.push(`Flat Bonus: ${eff.flatBonus > 0 ? '+' : ''}${eff.flatBonus}`);

            if (eff.dotDamage) tooltipLines.push(`DoT: ${eff.dotDamage} ${eff.dotDamageType || ''} per tick`);

            if (eff.hotHeal) tooltipLines.push(`HoT: ${eff.hotHeal} healing per tick`);

            if (eff.hotTempHp > 0) tooltipLines.push(`Temp HP: ${eff.hotTempHp} per tick`);

            if (eff.saveType) tooltipLines.push(`Save: ${(eff.saveType).toUpperCase()} DC ${eff.saveDC}${eff.saveRemovesEffect !== false ? ' (removes on pass)' : ''}`);

            if (eff.tickTiming) {

              const timingMap = { start_of_target_turn: "Start of target's turn", end_of_target_turn: "End of target's turn", start_of_caster_turn: "Start of caster's turn", end_of_caster_turn: "End of caster's turn" };

              tooltipLines.push(`Timing: ${timingMap[eff.tickTiming] || eff.tickTiming}`);

            }

            tooltipLines.push(`Cast by: ${eff.casterName || 'Unknown'}`);

            tooltipLines.push(`Duration: ${eff.duration === 'concentration' ? 'Concentration' : typeof eff.duration === 'number' ? `${eff.duration} rounds` : 'Permanent'}`);

            if (alreadyTicked) tooltipLines.push('✓ Already ticked this turn');

            

            return (

              <div key={`${eff._kind}-${eff._idx}`} style={{ position: 'relative', display: 'inline-flex' }}>

                <Tooltip text={tooltipLines.join('\n')}>

                  <span 

                    style={{ 

                      padding: pillPad, 

                      background: pillBg,

                      border: alreadyTicked ? '1px solid rgba(100,100,100,0.2)' 

                        : isEditing ? `1px solid ${accentColor}` 

                        : isAwarenessOnly ? `1px dashed rgba(${rgb},0.25)`

                        : `1px solid rgba(${rgb},${isSmall ? '0.1' : isGlowing ? '0.4' : '0.2'})`,

                      borderRadius: '10px', 

                      fontSize: pillFont,

                      color: textColor,

                      display: 'inline-flex',

                      alignItems: 'center',

                      gap: '3px',

                      cursor: !storedAsBuff && canEdit ? 'pointer' : 'default',

                      opacity: alreadyTicked ? 0.4 : isSmall ? 0.55 : isAwarenessOnly ? 0.7 : 1,

                      transition: 'all 0.15s',

                      animation: glowAnim ? `${glowAnim} 1.8s ease-in-out infinite` : 'none',

                      lineHeight: 1.2,

                      textDecoration: alreadyTicked ? 'line-through' : 'none',

                    }}

                    onClick={(e) => {

                      e.stopPropagation();

                    }}

                    onDoubleClick={(e) => {

                      e.stopPropagation();

                      if (!storedAsBuff && canEdit) setEditingEffect(isEditing ? null : { type: 'debuff', combatantId: combatant.id, index: eff._idx, effect: eff });

                    }}

                  >

                    {/* Name */}

                    {eff.name}

                    {/* Key modifier (compact) */}

                    {!isSmall && !alreadyTicked && isBuff && eff.modifier && <span style={{ fontFamily: 'monospace', fontSize: '0.55rem', color: '#27c95a', opacity: 0.8 }}>{eff.modifier}</span>}

                    {!isSmall && !alreadyTicked && !isBuff && eff.bonusDice && <span style={{ fontFamily: 'monospace', fontSize: '0.55rem', color: '#ff6b6b' }}>{eff.bonusDice.startsWith('+') || eff.bonusDice.startsWith('-') ? eff.bonusDice : `+${eff.bonusDice}`}</span>}

                    {!isSmall && !alreadyTicked && !isBuff && eff.percentIncrease && <span style={{ fontFamily: 'monospace', fontSize: '0.55rem', color: '#ff9800' }}>{eff.percentIncrease > 0 ? '+' : ''}{eff.percentIncrease}%</span>}

                    {!isSmall && !alreadyTicked && !isBuff && eff.flatBonus && <span style={{ fontFamily: 'monospace', fontSize: '0.55rem', color: '#ffd700' }}>{eff.flatBonus > 0 ? '+' : ''}{eff.flatBonus}</span>}

                    {/* Inline tick/save indicators */}

                    {!isSmall && !alreadyTicked && eff.dotDamage && <span style={{ fontSize: '0.52rem', color: '#ff6b6b' }}>🔥{eff.dotDamage}</span>}

                    {!isSmall && !alreadyTicked && eff.hotHeal && <span style={{ fontSize: '0.52rem', color: '#6cff8a' }}>➕{eff.hotHeal}</span>}

                    {!isSmall && !alreadyTicked && eff.hotTempHp > 0 && <span style={{ fontSize: '0.52rem', color: '#87ceeb' }}>🩵{eff.hotTempHp}</span>}

                    {!isSmall && !alreadyTicked && eff.saveType && <span style={{ fontSize: '0.52rem', color: '#c9a227' }}>🎲{(eff.saveType).toUpperCase()}{eff.saveDC}</span>}

                    {/* Duration */}

                    {dur && !alreadyTicked && <span style={{ fontSize: isSmall ? '0.46rem' : '0.52rem', color: dimColor }}>{dur}</span>}

                    {/* Inline tick action button — DM, caster, or target */}

                    {!isSmall && canManageTick && !alreadyTicked && (

                      <span

                        style={{ 

                          cursor: 'pointer', 

                          fontSize: isGlowing ? '0.7rem' : '0.6rem', 

                          padding: '0 2px',

                          borderLeft: '1px solid rgba(255,255,255,0.1)',

                          marginLeft: '1px',

                          opacity: 0.9,

                        }}

                        title={tick.type === 'save_only' ? `Roll ${(tick.saveType||'').toUpperCase()} save DC${tick.saveDC}` : `Tick: ${tick.type === 'dot' ? '-' : '+'}${tick.amount}`}

                        onClick={(e) => {

                          e.stopPropagation();

                          if (tick.type === 'save_only') tickSave(combatant.id, tick.name, tick.saveType, tick.saveDC, tick.saveRemovesEffect, tickRef);

                          else if (tick.type === 'dot') tickDot(combatant.id, tick.name, tick.amount, tick.damageType, tickRef);

                          else if (tick.type === 'tempHp') tickTempHp(combatant.id, tick.name, tick.amount, tickRef);

                          else tickHot(combatant.id, tick.name, tick.amount, tickRef);

                        }}

                      >▶</span>

                    )}

                    {/* Awareness indicator — observers see this instead of ▶ */}

                    {!isSmall && tick !== saveTick && canManageSaveTick && !saveAlreadyTicked && (

                      <span

                        style={{

                          cursor: 'pointer',

                          fontSize: isGlowing ? '0.66rem' : '0.58rem',

                          padding: '0 2px',

                          borderLeft: '1px solid rgba(255,255,255,0.1)',

                          marginLeft: '1px',

                          opacity: 0.9,

                          color: '#ffd700',

                        }}

                        title={`Roll ${(saveTick?.saveType || '').toUpperCase()} save DC${saveTick?.saveDC}`}

                        onClick={(e) => {

                          e.stopPropagation();

                          tickSave(combatant.id, saveTick.name, saveTick.saveType, saveTick.saveDC, saveTick.saveRemovesEffect, saveTickRef);

                        }}

                      >ðŸŽ²</span>

                    )}

                    {!isSmall && isAwarenessOnly && (

                      <span style={{ fontSize: '0.5rem', color: isBuff ? 'rgba(39,201,90,0.4)' : 'rgba(231,76,60,0.4)', marginLeft: '1px' }} title={`${tick?.type === 'dot' ? 'Damage' : tick?.type === 'hot' ? 'Healing' : tick?.type === 'save_only' ? 'Save' : 'Effect'} pending`}>●</span>

                    )}

                    {alreadyTicked && <span style={{ fontSize: '0.5rem', color: '#555', textDecoration: 'none' }}>✓</span>}

                  </span>

                </Tooltip>

                {/* Inline Edit Controls for debuffs */}

                {!isBuff && isEditing && canEdit && (

                  <div style={{

                    position: 'absolute', top: '100%', left: 0, zIndex: 20,

                    background: 'rgba(30,25,20,0.98)', border: '1px solid #e74c3c', borderRadius: '6px',

                    padding: '8px', marginTop: '4px', minWidth: '180px',

                    boxShadow: '0 4px 12px rgba(0,0,0,0.5)',

                  }} onClick={e => e.stopPropagation()}>

                    <div style={{ fontSize: '0.7rem', color: '#e74c3c', fontWeight: 'bold', marginBottom: '6px' }}>{eff.name}</div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '6px' }}>

                      <span style={{ fontSize: '0.65rem', color: '#888', minWidth: '45px' }}>Duration:</span>

                      <button style={{ ...S.btn, padding: '1px 6px', fontSize: '0.65rem' }} onClick={() => changeDebuffDuration(combatant.id, eff._idx, -1)}>−1</button>

                      <span style={{ fontSize: '0.7rem', color: '#aaa', minWidth: '30px', textAlign: 'center' }}>

                        {eff.duration === 'concentration' ? '⚡' : typeof eff.duration === 'number' ? `${eff.duration}r` : '∞'}

                      </span>

                      <button style={{ ...S.btn, padding: '1px 6px', fontSize: '0.65rem' }} onClick={() => changeDebuffDuration(combatant.id, eff._idx, 1)}>+1</button>

                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '6px' }}>

                      <span style={{ fontSize: '0.65rem', color: '#888', minWidth: '45px' }}>Move to:</span>

                      <select 

                        value={moveEffectTarget} 

                        onChange={(e) => setMoveEffectTarget(e.target.value)}

                        style={{ ...S.select, marginBottom: 0, padding: '2px', fontSize: '0.65rem', flex: 1 }}

                      >

                        <option value="">Pick target…</option>

                        {combatants.filter(c => c.id !== combatant.id).map(c => (

                          <option key={c.id} value={c.id}>{c.type === 'player' || c.type === 'npc' ? '👤' : '👹'} {c.name}</option>

                        ))}

                      </select>

                      {moveEffectTarget && (

                        <button style={{ ...S.btn, padding: '1px 6px', fontSize: '0.65rem', background: 'rgba(74,144,217,0.3)', border: '1px solid #4a90d9', color: '#7db5ff' }} onClick={() => {

                          moveDebuff(combatant.id, eff._idx, moveEffectTarget);

                          setMoveEffectTarget('');

                        }}>Go</button>

                      )}

                    </div>

                    <button 

                      style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, width: '100%' }}

                      onClick={() => removeDebuff(combatant.id, eff._idx)}

                    >🗑️ Remove Effect</button>

                  </div>

                )}

              </div>

            );

          };

          

          return (

            <div style={{ marginTop: '6px' }}>

              {/* Standalone conditions (not duplicated by effects) — with focus dimming */}

              {standaloneConditions.length > 0 && (

                <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', marginBottom: allEffects.length > 0 ? '4px' : 0 }}>

                  {standaloneConditions.map((c, i) => {

                    // Try to determine relevance from condition text

                    const viewCharName = viewCharId ? (chars[viewCharId]?.name || combatants.find(cb => cb.id === viewCharId)?.name || '') : '';

                    const isOnMyCard = viewCharId && combatant.id === viewCharId;

                    const mentionsMyCaster = viewCharName && c.name.toLowerCase().includes(viewCharName.toLowerCase());

                    const isRelevantCond = isOnMyCard || mentionsMyCaster;

                    const isDimmed = useFocus && !isRelevantCond;

                    return (

                      <span key={`cond-${c._idx}`} style={{ 

                        padding: '2px 7px', 

                        background: isDimmed ? 'rgba(155,89,182,0.08)' : 'rgba(155,89,182,0.2)', 

                        border: `1px solid rgba(155,89,182,${isDimmed ? '0.1' : '0.25'})`,

                        borderRadius: '10px', 

                        fontSize: isDimmed ? '0.56rem' : '0.65rem',

                        color: isDimmed ? '#886a9e' : '#c39bdb',

                        opacity: isDimmed ? 0.5 : 1,

                        display: 'inline-flex', alignItems: 'center', gap: '3px',

                        transition: 'all 0.15s',

                      }}>

                        {c.name}

                        {canEdit && !isDimmed && (

                          <button

                            style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '0.55rem', padding: 0, lineHeight: 1 }}

                            onClick={(e) => {

                              e.stopPropagation();

                              const updated = combatants.map(cb => 

                                cb.id === combatant.id ? { ...cb, conditions: (cb.conditions || []).filter((_, idx) => idx !== c._idx) } : cb

                              );

                              onUpdateCombat({ ...combat, combatants: updated });

                            }}

                          >✕</button>

                        )}

                      </span>

                    );

                  })}

                </div>

              )}

              {/* Effects — unified buffs + debuffs with inline ticks */}

              {allEffects.length > 0 && (

                <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>

                  {withRelevance.map(renderEffect)}

                </div>

              )}

            </div>

          );

        })()}

        

        {/* Quick Actions - Delete for enemies (DM), or heal self for players */}

        {isSelected && (

          <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>

            {isDM && !isPlayer && (

              <button 

                style={{ ...S.btn, ...S.btnDanger, ...S.btnXs }}

                onClick={(e) => { e.stopPropagation(); removeCombatant(combatant.id); }}

              >

                🗑️ Remove

              </button>

            )}

            {!isDM && isPlayer && combatant.id === currentCharId && (

              <span style={{ fontSize: '0.75rem', color: '#888' }}>Use DM controls above for damage/healing</span>

            )}

          </div>

        )}

      </div>

    );

  };

  

  return (

    <div style={S.container}>

      {/* Turn lifecycle animations */}

      <style>{`

        @keyframes turnFadeIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }

        @keyframes turnFlashIn { 0% { opacity: 0; background: rgba(255,255,255,0.15); } 30% { opacity: 1; background: rgba(255,255,255,0.08); } 100% { opacity: 1; } }

        @keyframes concFlash { 0% { box-shadow: 0 0 0 0 rgba(255,152,0,0.6); } 50% { box-shadow: 0 0 12px 4px rgba(255,152,0,0.3); } 100% { box-shadow: 0 0 0 0 rgba(255,152,0,0); } }

        @keyframes deathPulse { 0% { border-color: rgba(139,0,0,0.6); } 50% { border-color: rgba(200,0,0,0.9); } 100% { border-color: rgba(139,0,0,0.6); } }

        @keyframes shieldShimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }

        @keyframes shieldBlock { 0% { transform: translateX(0); opacity: 1; } 30% { transform: translateX(20%); opacity: 1; } 60% { transform: translateX(20%); opacity: 0.6; } 100% { transform: translateX(0); opacity: 0; } }

        @keyframes shieldBreak { 0% { transform: scale(1); opacity: 1; } 40% { transform: scale(1.15); opacity: 1; } 100% { transform: scale(0); opacity: 0; } }

        @keyframes shieldBreakFlash { 0% { opacity: 1; transform: scale(1); } 50% { opacity: 0.6; transform: scale(1.1); } 100% { opacity: 0; transform: scale(1.3); } }

        @keyframes deathRollIn { 0% { transform: scale(0) rotate(-180deg); opacity: 0; } 50% { transform: scale(1.3) rotate(10deg); opacity: 1; } 100% { transform: scale(1) rotate(0); opacity: 1; } }

        @keyframes deathSaveRoll { 0% { transform: rotateX(0deg) scale(1); } 25% { transform: rotateX(180deg) scale(1.2); } 50% { transform: rotateX(360deg) scale(1); } 75% { transform: rotateX(540deg) scale(1.1); } 100% { transform: rotateX(720deg) scale(1); } }

        @keyframes stabilizedPulse { 0% { box-shadow: 0 0 0 0 rgba(39,201,90,0.5); } 50% { box-shadow: 0 0 12px 6px rgba(39,201,90,0.3); } 100% { box-shadow: 0 0 0 0 rgba(39,201,90,0); } }

        @keyframes tickGlowDot { 0% { box-shadow: 0 0 4px 1px rgba(231,76,60,0.4); } 50% { box-shadow: 0 0 12px 4px rgba(231,76,60,0.8); } 100% { box-shadow: 0 0 4px 1px rgba(231,76,60,0.4); } }

        @keyframes tickGlowHot { 0% { box-shadow: 0 0 4px 1px rgba(39,201,90,0.4); } 50% { box-shadow: 0 0 12px 4px rgba(39,201,90,0.8); } 100% { box-shadow: 0 0 4px 1px rgba(39,201,90,0.4); } }

        @keyframes tickGlowSave { 0% { box-shadow: 0 0 4px 1px rgba(201,162,39,0.4); } 50% { box-shadow: 0 0 12px 4px rgba(201,162,39,0.8); } 100% { box-shadow: 0 0 4px 1px rgba(201,162,39,0.4); } }

      `}</style>

      {activeCombatResourcePendingSave && activeCombatResourceSaveChar && (
        <RequireSaveModal
          key={`${activeCombatResourceSaveEntry?.id || 'combat-resource-save'}:${combatResourceSaveQueue.length}`}
          pendingSave={activeCombatResourcePendingSave}
          char={activeCombatResourceSaveChar}
          saveContext={activeCombatResourceSaveContext}
          saveAdvantage={activeCombatResourceSaveAdvantage}
          onResolve={handleCombatResourceSaveResolve}
          onSkip={handleCombatResourceSaveSkip}
        />
      )}

      {/* DM View-As-Player Banner */}

      {isDM && dmViewAsPlayerId && (() => {

        const vc = chars[dmViewAsPlayerId] || {};

        const combatantMatch = combatants.find(c => c.id === dmViewAsPlayerId);

        const viewName = vc.name || combatantMatch?.name || HIDDEN_LABEL_PLACEHOLDER;

        const isEnemy = combatantMatch?.type === 'enemy';

        return (

          <div style={{

            position: 'sticky', top: 0, zIndex: 50,

            background: isEnemy ? 'rgba(201,39,39,0.12)' : 'rgba(74,144,217,0.12)',

            border: `1px solid ${isEnemy ? 'rgba(201,39,39,0.3)' : 'rgba(74,144,217,0.3)'}`, borderRadius: '4px',

            padding: '3px 14px', marginBottom: '8px',

            display: 'flex', justifyContent: 'space-between', alignItems: 'center',

          }}>

            <span style={{ fontSize: '0.72rem', color: isEnemy ? '#ff6b6b' : '#7db5ff' }}>

              ◉ Viewing as <strong>{viewName}</strong>

            </span>

            <button

              style={{ ...S.btn, padding: '1px 8px', fontSize: '0.65rem', color: '#888', border: 'none' }}

              onClick={() => { setDmViewAsPlayerId(''); setDmViewMode('dm'); }}

            >✕</button>

          </div>

        );

      })()}

      {/* Header */}

      <div style={{ ...S.card, marginBottom: '16px' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>

            <button style={{ ...S.btn, ...S.btnSm }} onClick={onBack}>← Back</button>

            <h1 style={{ ...S.title, marginBottom: 0, fontSize: '1.5rem' }}>⚔️ Combat</h1>

          </div>

          {combat?.active ? (

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>

              <span style={{ fontSize: '1.1rem', color: '#ffd700' }}>Round {combat.round}</span>

              {!allInitiativesSet && <span style={{ fontSize: '0.85rem', color: '#c9a227' }}>⚠️ Waiting for initiatives...</span>}

              {isDM && !allInitiativesSet && <button style={{ ...S.btn, ...S.btnSm }} onClick={rollAllInitiative} title="Roll 1d20 + initiative mod for every combatant whose initiative is unset. Dex score / 100 is added as a tiebreaker decimal.">🎲 Roll All Initiative</button>}

              {isDM && combatants.some(c => c.type === 'enemy') && <button style={{ ...S.btn, ...S.btnSm }} onClick={() => toggleAllEnemyStatsVisible(!anyEnemyVisible)} title={anyEnemyVisible ? 'Hide enemy AC/HP from players' : 'Reveal enemy AC/HP to players'}>{anyEnemyVisible ? '🙈 Hide Enemy Stats' : '👁 Reveal Enemy Stats'}</button>}

              {isDM && <button style={{ ...S.btn, ...S.btnDanger }} onClick={() => { if (window.confirm('End combat now? Combatants, pending damage, and lifecycle queue will be cleared. HP is synced back to character sheets. This cannot be undone.')) endCombat(); }}>End Combat</button>}

              {isDM && <button style={{ ...S.btn, ...S.btnSm }} onClick={() => setShowDeathSaveConfig(!showDeathSaveConfig)} title="Death Save Rules">⚙️ Rules</button>}

              {isDM && <button style={{ ...S.btn, ...S.btnSm }} onClick={() => { setShowHistory(true); setSelectedSnapshotId(null); }} title="Combat history & revert to any prior state">📜 History{combatSnapshots.length > 0 ? ` (${combatSnapshots.length})` : ''}</button>}

            </div>

          ) : (

            isDM && <button style={{ ...S.btn, ...S.btnSuccess }} onClick={startCombat}>Start Combat</button>

          )}

        </div>

      </div>

      

      {!combat?.active ? (

        <div style={{ ...S.card, textAlign: 'center', padding: '40px' }}>

          <div style={{ fontSize: '4rem', marginBottom: '16px' }}>⚔️</div>

          <h2 style={{ color: '#c9a227', marginBottom: '16px' }}>No Active Combat</h2>

          <p style={{ color: '#888', marginBottom: '24px' }}>

            {isDM ? 'Start a combat encounter to track initiative, HP, and damage.' : 'Waiting for the DM to start combat...'}

          </p>

        </div>

      ) : (

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>

          {/* Turn order */}

          <div style={{ ...S.card, gridColumn: '1 / -1' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>

              <h3 style={{ color: '#c9a227', margin: 0 }}>Turn Order</h3>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>

                <button style={{ ...S.btn, ...S.btnSm }} onClick={() => setShowDamageLog(!showDamageLog)}>

                  📜 Log ({damageLog.length})

                </button>

                <button style={{ 

                  ...S.btn, ...S.btnSm, 

                  ...(showEffectsTracker ? { background: 'rgba(155,89,182,0.3)', border: '1px solid #9b59b6', color: '#c39bdb' } : {})

                }} onClick={() => { setDmViewAsId(''); setShowEffectsTracker(!showEffectsTracker); }}>

                  ✨ Effects

                </button>

                {/* Focus Mode Toggle — players/DM-view-as can toggle relevance-based effect hierarchy */}

                {(viewIsPlayer || (isDM && dmViewAsPlayerId)) && (

                  <button 

                    style={{ 

                      ...S.btn, ...S.btnSm,

                      ...(playerFocusView ? { background: 'rgba(74,144,217,0.25)', border: '1px solid #4a90d9', color: '#7db5ff' } : {}),

                    }}

                    onClick={() => setPlayerFocusView(!playerFocusView)}

                    title={playerFocusView ? 'Focus View ON — effects sized by relevance to you' : 'Focus View OFF — all effects shown equally'}

                  >

                    🎯 {playerFocusView ? 'Focus' : 'All'}

                  </button>

                )}

                {/* DM View-As-Player Buttons (full page perspective switch) */}

                {/* Advantage/Disadvantage — active turn combatant controls */}

                {activeTurnId && (

                  <div style={{ 

                    display: 'flex', gap: '3px', alignItems: 'center', 

                    borderLeft: '1px solid rgba(255,255,255,0.15)', paddingLeft: '8px', marginLeft: '4px',

                    background: rollAdvantage !== 'normal' ? (rollAdvantage === 'advantage' ? 'rgba(39,201,90,0.1)' : 'rgba(231,76,60,0.1)') : 'transparent',

                    padding: '3px 8px', borderRadius: '4px',

                  }}>

                    <span style={{ fontSize: '0.6rem', color: '#888', marginRight: '2px' }}>🎲</span>

                    <button

                      style={{ 

                        ...S.btn, padding: '3px 8px', fontSize: '0.68rem', fontWeight: 'bold',

                        ...(rollAdvantage === 'advantage' ? { background: 'rgba(39,201,90,0.35)', border: '1px solid #27c95a', color: '#6cff8a' } : {}),

                      }}

                      onClick={() => setRollAdvantage(rollAdvantage === 'advantage' ? 'normal' : 'advantage')}

                      title={`Advantage for active turn combatant`}

                    >⬆ Adv</button>

                    <button

                      style={{ 

                        ...S.btn, padding: '3px 8px', fontSize: '0.68rem', fontWeight: 'bold',

                        ...(rollAdvantage === 'disadvantage' ? { background: 'rgba(231,76,60,0.35)', border: '1px solid #e74c3c', color: '#ff6b6b' } : {}),

                      }}

                      onClick={() => setRollAdvantage(rollAdvantage === 'disadvantage' ? 'normal' : 'disadvantage')}

                      title={`Disadvantage for active turn combatant`}

                    >⬇ Dis</button>

                    <label style={{ 

                      fontSize: '0.58rem', color: supremeRoll ? '#ffd700' : '#666', 

                      display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer',

                      opacity: rollAdvantage === 'normal' ? 0.35 : 1,

                    }}>

                      <input type="checkbox" checked={supremeRoll} onChange={() => setSupremeRoll(!supremeRoll)} disabled={rollAdvantage === 'normal'} style={{ width: '10px', height: '10px' }} />

                      Sup

                    </label>

                    {/* Show count of combatants with active adv/dis */}

                    {Object.keys(combatantAdvantage).length > 0 && (

                      <span style={{ fontSize: '0.55rem', color: '#c9a227', marginLeft: '4px' }} title={Object.entries(combatantAdvantage).map(([id, mode]) => `${combatants.find(c => c.id === id)?.name || id}: ${mode}`).join('\n')}>

                        ({Object.keys(combatantAdvantage).length} active)

                      </span>

                    )}

                  </div>

                )}

                {isDM && (

                  <>

                    <button 

                      style={{ ...S.btn, ...S.btnSm }} 

                      onClick={nextTurn}

                      disabled={!allInitiativesSet}

                    >

                      Next Turn →

                    </button>

                    <button style={{ ...S.btn, ...S.btnSm, ...S.btnSuccess }} onClick={() => setShowAddEnemy(true)}>+ Add Enemy</button>

                  </>

                )}

                {/* Player End Turn Button - shown for actual players or DM viewing as player */}

                {viewIsPlayer && viewCharId && (() => {

                  const myTurnCombatant = sortedCombatants[combat.turnIndex];

                  const isMyTurn = myTurnCombatant?.id === viewCharId;

                  return isMyTurn ? (

                    <button 

                      style={{ 

                        ...S.btn, ...S.btnSm,

                        background: 'rgba(39,201,90,0.3)',

                        border: '2px solid #27c95a',

                        color: '#6cff8a',

                        fontWeight: 'bold',

                        animation: 'tickGlowHot 2s ease-in-out infinite',

                      }} 

                      onClick={nextTurn}

                    >

                      ✓ End My Turn →

                    </button>

                  ) : (

                    <span style={{ fontSize: '0.75rem', color: '#888', padding: '4px 8px' }}>

                      ⏳ Waiting — {myTurnCombatant?.name || HIDDEN_LABEL_PLACEHOLDER}'s turn

                    </span>

                  );

                })()}

              </div>

            </div>

            

            {/* Combat Notes */}

            <div style={{ marginBottom: '8px' }}>

              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>

                <input

                  type="text"

                  value={combatNoteInput}

                  onChange={(e) => setCombatNoteInput(e.target.value)}

                  onKeyDown={(e) => {

                    if (e.key === 'Enter' && combatNoteInput.trim()) {

                      setCombatNotes(prev => [...prev, { id: generateId(), text: combatNoteInput.trim(), timestamp: Date.now() }]);

                      setCombatNoteInput('');

                    }

                  }}

                  placeholder="Combat note... (Enter to log)"

                  style={{ ...S.input, flex: 1, marginBottom: 0, padding: '4px 8px', fontSize: '0.75rem' }}

                />

              </div>

              {combatNotes.length > 0 && (

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>

                  {combatNotes.map(note => (

                    <span key={note.id} style={{

                      display: 'inline-flex', alignItems: 'center', gap: '4px',

                      padding: '3px 8px', fontSize: '0.7rem',

                      background: 'rgba(201,162,39,0.1)', border: '1px solid rgba(201,162,39,0.25)',

                      borderRadius: '4px', color: '#e8d5b7',

                    }}>

                      {note.text}

                      <button

                        onClick={() => setCombatNotes(prev => prev.filter(n => n.id !== note.id))}

                        style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '0.65rem', padding: 0, lineHeight: 1 }}

                      >✕</button>

                    </span>

                  ))}

                </div>

              )}

            </div>

            

            {pendingDamage.length > 0 && (

              <div style={{ padding: '8px', background: 'rgba(201,39,39,0.2)', borderRadius: '6px', marginBottom: '12px', border: '1px solid #c92727' }}>

                <span style={{ color: '#ff6666', fontSize: '0.85rem' }}>⚠️ {pendingDamage.length} pending damage event{pendingDamage.length > 1 ? 's' : ''} awaiting response</span>

              </div>

            )}

            

            <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px' }}>

              {sortedCombatants.map((c, i) => {

                const isCurrent = allInitiativesSet && i === combat.turnIndex;

                const hasPending = getPendingForTarget(c.id).length > 0;

                return (

                  <div 

                    key={c.id}

                    style={{

                      padding: '8px 12px',

                      background: isCurrent ? 'rgba(201,162,39,0.3)' : hasPending ? 'rgba(201,39,39,0.2)' : 'rgba(0,0,0,0.3)',

                      borderRadius: '6px',

                      border: `2px solid ${isCurrent ? '#ffd700' : hasPending ? '#c92727' : 'transparent'}`,

                      textAlign: 'center',

                      minWidth: '80px',

                      flexShrink: 0,

                    }}

                  >

                    <div style={{ fontSize: '0.7rem', color: c.initiative === null ? '#c9a227' : '#888' }}>

                      {c.initiative ?? '?'}

                    </div>

                    <div style={{ fontWeight: 'bold', color: c.type === 'player' ? '#27c95a' : c.type === 'npc' ? '#27c95a' : '#c92727', fontSize: '0.85rem' }}>{c.name}</div>

                    {/* 2026-05-11 — Session 8: intent glyphs above the token
                        (Slay the Spire pattern). Telegraphs next-turn
                        intent: concentrating / incoming damage / ongoing
                        effect. Replaces the previous one-line concentration
                        text + pending-damage warning icon — both are
                        subsumed by the unified intent system. */}
                    {(() => {
                      const intents = computeIntents(c, combat, chars[c.id]);
                      return intents.length > 0 ? (
                        <div style={{ display: 'flex', gap: '3px', justifyContent: 'center', marginTop: '3px', flexWrap: 'wrap' }}>
                          {intents.map((intent) => (
                            <IntentGlyph
                              key={intent.key}
                              kind={intent.kind}
                              value={intent.value}
                              title={intent.title}
                            />
                          ))}
                        </div>
                      ) : null;
                    })()}

                    <div style={{ fontSize: '0.75rem', color: c.currentHp <= 0 ? '#c92727' : '#888' }}>

                      {c.currentHp}{(c.tempHp || 0) > 0 && <span style={{ color: '#87ceeb' }}>+{c.tempHp}</span>}/{c.maxHp}

                    </div>

                  </div>

                );

              })}

            </div>

          </div>

          

          {/* Turn Lifecycle Notifications */}

          {turnNotifications.length > 0 && (

            <div style={{ 

              gridColumn: '1 / -1',

              display: 'flex',

              flexDirection: 'column',

              gap: '4px',

              animation: 'turnFadeIn 0.3s ease-out',

            }}>

              {turnNotifications.map((notif) => (

                <div 

                  key={notif.id}

                  style={{

                    padding: '8px 14px',

                    background: `${notif.color}18`,

                    border: `1px solid ${notif.color}50`,

                    borderRadius: '6px',

                    color: notif.color,

                    fontSize: '0.85rem',

                    display: 'flex',

                    alignItems: 'center',

                    justifyContent: 'space-between',

                    gap: '8px',

                    animation: notif.type === 'turn' ? 'none' : 'turnFlashIn 0.5s ease-out',

                  }}

                >

                  <span>{notif.message}</span>

                  <button 

                    style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '0.75rem', padding: '0 4px' }}

                    onClick={() => setTurnNotifications(prev => prev.filter(n => n.id !== notif.id))}

                  >✕</button>

                </div>

              ))}

            </div>

          )}

          

          {/* Lifecycle Queue - pending confirmations (visible to DM and affected players) */}

          {lifecycleQueue.length > 0 && (

            <div style={{ ...S.card, gridColumn: '1 / -1' }}>

              <div style={{ padding: '12px', background: 'rgba(255,152,0,0.08)', borderRadius: '8px', border: '1px solid rgba(255,152,0,0.3)' }}>

                <div style={{ fontSize: '0.85rem', color: '#ff9800', fontWeight: 'bold', marginBottom: '8px' }}>

                  ⚠️ Pending Actions ({lifecycleQueue.length})

                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>

                  {lifecycleQueue.map(item => {

                    const isSave = item.type === 'recurring_save';

                    

                    // Compute save modifier for roll button

                    let saveMod = null;

                    let saveBreakdown = '';

                    if (isSave && item.combatantId) {

                      const st = item.saveType || 'dex';

                      const saveContext = getResolvedCombatSaveContext(item.combatantId, st);

                      if (saveContext) {

                        saveMod = saveContext.totalBonus;

                        saveBreakdown = formatResolvedSaveBreakdown(saveContext);

                      }

                    }

                    

                    return (

                      <div key={item.id} style={{ 

                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',

                        padding: '8px 10px', background: `${item.color}15`, border: `1px solid ${item.color}40`,

                        borderRadius: '6px', flexWrap: 'wrap',

                      }}>

                        <div style={{ flex: 1, minWidth: '150px' }}>

                          <div style={{ fontSize: '0.8rem', color: item.color, fontWeight: 'bold' }}>{item.title}</div>

                          <div style={{ fontSize: '0.75rem', color: '#aaa' }}>{item.message}</div>

                          {isSave && saveBreakdown && (

                            <div style={{ fontSize: '0.65rem', color: '#888', marginTop: '2px', fontFamily: 'monospace' }}>

                              {saveBreakdown} = {saveMod !== null ? `${saveMod >= 0 ? '+' : ''}${saveMod} total` : '?'}

                            </div>

                          )}

                          {/* Show roll result if attached */}

                          {item._rollResult && (

                            <div style={{ 

                              fontSize: '0.78rem', fontWeight: 'bold', marginTop: '4px',

                              color: item._rollResult.passed ? '#27c95a' : '#e74c3c',

                            }}>

                              🎲 Rolled: {item._rollResult.allDice && item._rollResult.allDice.length > 1 ? (

                                <span>

                                  {item._rollResult.allDice.map((d, i) => (

                                    <span key={i} style={{ 

                                      color: d === item._rollResult.d20 ? (item._rollResult.passed ? '#6cff8a' : '#ff6b6b') : '#555',

                                      fontWeight: d === item._rollResult.d20 ? 'bold' : 'normal',

                                      textDecoration: d !== item._rollResult.d20 ? 'line-through' : 'none',

                                      fontSize: d !== item._rollResult.d20 ? '0.65rem' : '0.78rem',

                                    }}>{d}{i < item._rollResult.allDice.length - 1 ? '/' : ''}</span>

                                  ))}

                                  <span style={{ fontSize: '0.6rem', color: '#888' }}> ({item._rollResult.advMode}{item._rollResult.isSupreme ? '/sup' : ''})</span>

                                </span>

                              ) : item._rollResult.d20}

                              {item._rollResult.mod >= 0 ? '+' : ''}{item._rollResult.mod}

                              {item._rollResult.bonusDiceLabel ? ` +${item._rollResult.bonusDiceLabel}` : ''}

                              {' = '}{item._rollResult.total} vs DC {item.saveDC} — {item._rollResult.passed ? '✓ PASS' : '✕ FAIL'}

                            </div>

                          )}

                        </div>

                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>

                          {/* Roll button for saves */}

                          {isSave && !item._rollResult && (

                            <button

                              style={{ ...S.btn, padding: '4px 10px', fontSize: '0.7rem', background: 'rgba(201,162,39,0.3)', border: '1px solid #c9a227', color: '#ffd700' }}

                              onClick={() => {

                                const st = item.saveType || 'dex';

                                const saveRoll = rollResolvedCombatSave(item.combatantId, st);

                                if (!saveRoll) return;

                                const { d20Roll, d20, modifier, bonusDiceTotal, bonusDiceLabel, total } = saveRoll;

                                const passed = total >= (item.saveDC || 10);

                                

                                setLifecycleQueue(prev => prev.map(q => 

                                  q.id === item.id ? { ...q, _rollResult: { d20, allDice: d20Roll.all, advMode: d20Roll.mode, isSupreme: d20Roll.isSupreme, mod: modifier, bonusDiceTotal, bonusDiceLabel, total, passed } } : q

                                ));

                              }}

                            >🎲 Roll Save{saveMod !== null ? ` (${saveMod >= 0 ? '+' : ''}${saveMod})` : ''}</button>

                          )}

                          <button

                            style={{ ...S.btn, padding: '4px 10px', fontSize: '0.7rem', background: 'rgba(39,201,90,0.25)', border: '1px solid #27c95a', color: '#6cff8a' }}

                            onClick={() => handleLifecycleConfirm(item)}

                          >{item.confirmLabel || 'Confirm'}</button>

                          <button

                            style={{ ...S.btn, padding: '4px 10px', fontSize: '0.7rem', background: 'rgba(201,39,39,0.2)', border: '1px solid #c92727', color: '#ff6b6b' }}

                            onClick={() => handleLifecycleCancel(item)}

                          >{item.cancelLabel || 'Skip'}</button>

                        </div>

                      </div>

                    );

                  })}

                  {lifecycleQueue.length > 1 && (

                    <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>

                      <button style={{ ...S.btn, ...S.btnXs, ...S.btnDanger }} onClick={() => {

                        lifecycleQueue.forEach(item => handleLifecycleConfirm(item));

                      }}>Confirm All</button>

                      <button style={{ ...S.btn, ...S.btnXs }} onClick={() => { if (window.confirm(`Dismiss all ${lifecycleQueue.length} pending actions without applying them? Effects, ticks, and saves will be skipped.`)) setLifecycleQueue([]); }}>Dismiss All</button>

                    </div>

                  )}

                </div>

              </div>

            </div>

          )}

          

          {/* DM Targeting Panel */}

          {isDM && (

            <div style={{ ...S.card, gridColumn: '1 / -1' }}>

              {/* DM / Ally / Enemy View Toggle */}

              {isDM && (() => {

                const allAllies = combatants.filter(c => c.type === 'player' || c.type === 'npc');

                const allEnemies = combatants.filter(c => c.type === 'enemy');

                const currentTurnC = allInitiativesSet ? sortedCombatants[combat.turnIndex] : null;

                const currentTurnIsAlly = currentTurnC && (currentTurnC.type === 'player' || currentTurnC.type === 'npc');

                const currentTurnIsEnemy = currentTurnC && currentTurnC.type === 'enemy';

                

                // Helper to find next ally/enemy turn

                const findNextOfType = (isAllyType) => {

                  if (!allInitiativesSet) return null;

                  for (let i = 0; i < sortedCombatants.length; i++) {

                    const idx = (combat.turnIndex + i) % sortedCombatants.length;

                    const c = sortedCombatants[idx];

                    const matches = isAllyType ? (c.type === 'player' || c.type === 'npc') : c.type === 'enemy';

                    if (matches) return c;

                  }

                  return null;

                };

                

                const selectViewAs = (id) => {

                  setDmViewAsPlayerId(id);

                  if (id) {

                    setPlayerFocusView(true);

                    setDmCombatCharId(id);

                    const c = combatants.find(x => x.id === id);

                    if (c && (c.type === 'player' || c.type === 'npc')) setLastSelectedAllyId(id);

                    if (c && c.type === 'enemy') setLastSelectedEnemyId(id);

                  }

                };

                

                const handleModeSwitch = (mode) => {

                  setDmViewMode(mode);

                  if (mode === 'dm') {

                    setDmViewAsPlayerId('');

                  } else if (mode === 'ally') {

                    // Auto-select: current turn ally, or last selected, or first ally

                    const autoId = currentTurnIsAlly ? currentTurnC.id 

                      : (lastSelectedAllyId && allAllies.find(c => c.id === lastSelectedAllyId) ? lastSelectedAllyId 

                      : (findNextOfType(true)?.id || allAllies[0]?.id || ''));

                    selectViewAs(autoId);

                  } else if (mode === 'enemy') {

                    const autoId = currentTurnIsEnemy ? currentTurnC.id 

                      : (lastSelectedEnemyId && allEnemies.find(c => c.id === lastSelectedEnemyId) ? lastSelectedEnemyId 

                      : (findNextOfType(false)?.id || allEnemies[0]?.id || ''));

                    selectViewAs(autoId);

                  }

                };

                

                // Build sub-pills for ally or enemy mode

                const renderSubPills = (pool, isAllyPool) => {

                  if (pool.length === 0) return null;

                  const MAX_PILLS = 4;

                  const nextTurn = findNextOfType(isAllyPool);

                  const lastId = isAllyPool ? lastSelectedAllyId : lastSelectedEnemyId;

                  

                  if (pool.length <= MAX_PILLS) {

                    // Show all as pills

                    return pool.map(c => {

                      const isActive = dmViewAsPlayerId === c.id;

                      const isTheirTurn = currentTurnC?.id === c.id;

                      return (

                        <button key={c.id} style={{

                          ...S.btn, padding: '3px 10px', fontSize: '0.7rem', borderRadius: '12px',

                          background: isActive ? 'rgba(74,144,217,0.3)' : 'transparent',

                          border: `1px solid ${isActive ? '#4a90d9' : isTheirTurn ? 'rgba(255,215,0,0.3)' : 'rgba(255,255,255,0.1)'}`,

                          color: isActive ? '#7db5ff' : isTheirTurn ? '#ffd700' : '#888',

                        }} onClick={() => selectViewAs(c.id)}>

                          {c.type === 'npc' ? '🤝' : isAllyPool ? '👤' : '👹'} {c.name}{isTheirTurn && !isActive ? ' ⚔️' : ''}

                        </button>

                      );

                    });

                  }

                  

                  // >4 options: show current turn pill + most recent pill + dropdown for rest

                  const pinnedIds = new Set();

                  const pills = [];

                  

                  // Pin: next turn of this type

                  if (nextTurn) { pinnedIds.add(nextTurn.id); }

                  // Pin: most recently selected (if different)

                  if (lastId && lastId !== nextTurn?.id && pool.find(c => c.id === lastId)) { pinnedIds.add(lastId); }

                  // Pin: currently active view if in pool

                  if (dmViewAsPlayerId && pool.find(c => c.id === dmViewAsPlayerId)) { pinnedIds.add(dmViewAsPlayerId); }

                  

                  pool.filter(c => pinnedIds.has(c.id)).forEach(c => {

                    const isActive = dmViewAsPlayerId === c.id;

                    const isTheirTurn = currentTurnC?.id === c.id;

                    pills.push(

                      <button key={c.id} style={{

                        ...S.btn, padding: '3px 10px', fontSize: '0.7rem', borderRadius: '12px',

                        background: isActive ? 'rgba(74,144,217,0.3)' : 'transparent',

                        border: `1px solid ${isActive ? '#4a90d9' : isTheirTurn ? 'rgba(255,215,0,0.3)' : 'rgba(255,255,255,0.1)'}`,

                        color: isActive ? '#7db5ff' : isTheirTurn ? '#ffd700' : '#888',

                      }} onClick={() => selectViewAs(c.id)}>

                        {c.type === 'npc' ? '🤝' : isAllyPool ? '👤' : '👹'} {c.name}{isTheirTurn && !isActive ? ' ⚔️' : ''}

                      </button>

                    );

                  });

                  

                  const remaining = pool.filter(c => !pinnedIds.has(c.id));

                  if (remaining.length > 0) {

                    pills.push(

                      <select key="overflow" 

                        value="" 

                        onChange={(e) => { if (e.target.value) selectViewAs(e.target.value); }}

                        style={{ ...S.select, marginBottom: 0, padding: '3px 6px', fontSize: '0.7rem', width: 'auto', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#888' }}

                      >

                        <option value="">+{remaining.length} more...</option>

                        {remaining.map(c => (

                          <option key={c.id} value={c.id}>{c.name}</option>

                        ))}

                      </select>

                    );

                  }

                  

                  return pills;

                };

                

                const modeBtnStyle = (active, color, activeColor) => ({

                  ...S.btn, padding: '3px 12px', fontSize: '0.72rem', borderRadius: '12px',

                  background: active ? `rgba(${color},0.3)` : 'transparent',

                  border: `1px solid ${active ? activeColor : 'rgba(255,255,255,0.1)'}`,

                  color: active ? activeColor : '#666',

                  fontWeight: active ? 'bold' : 'normal',

                });

                

                return (

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexWrap: 'wrap' }}>

                    <span style={{ fontSize: '0.75rem', color: '#888' }}>◉</span>

                    <button style={modeBtnStyle(dmViewMode === 'dm', '201,162,39', '#ffd700')} onClick={() => handleModeSwitch('dm')}>DM</button>

                    <button style={modeBtnStyle(dmViewMode === 'ally', '39,201,90', '#27c95a')} onClick={() => handleModeSwitch('ally')}>

                      Ally{allAllies.length > 0 ? ` (${allAllies.length})` : ''}

                    </button>

                    <button style={modeBtnStyle(dmViewMode === 'enemy', '201,39,39', '#c92727')} onClick={() => handleModeSwitch('enemy')}>

                      Enemy{allEnemies.length > 0 ? ` (${allEnemies.length})` : ''}

                    </button>

                    {dmViewMode === 'ally' && allAllies.length > 0 && (

                      <>

                        <span style={{ color: 'rgba(255,255,255,0.15)', fontSize: '1rem' }}>│</span>

                        {renderSubPills(allAllies, true)}

                      </>

                    )}

                    {dmViewMode === 'enemy' && allEnemies.length > 0 && (

                      <>

                        <span style={{ color: 'rgba(255,255,255,0.15)', fontSize: '1rem' }}>│</span>

                        {renderSubPills(allEnemies, false)}

                      </>

                    )}

                  </div>

                );

              })()}

              {/* Combatant grid continued */}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>

                <h3 style={{ color: '#c92727', margin: 0 }}>🎯 Target Selection ({selectedTargets.length} selected)</h3>

                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>

                  <button style={{ ...S.btn, ...S.btnXs }} onClick={selectAllPlayers}>👤 Players</button>

                  <button style={{ ...S.btn, ...S.btnXs }} onClick={selectAllEnemies}>👹 Enemies</button>

                  <button style={{ ...S.btn, ...S.btnXs }} onClick={selectAll}>All</button>

                  <button style={{ ...S.btn, ...S.btnXs }} onClick={clearSelection}>Clear</button>

                </div>

              </div>

              

              {/* Selected Targets Display */}

              {selectedTargets.length > 0 && (

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '12px' }}>

                  {selectedTargets.map(id => {

                    const c = combatants.find(x => x.id === id);

                    if (!c) return null;

                    return (

                      <span 

                        key={id} 

                        style={{ 

                          padding: '4px 8px', 

                          background: c.type === 'player' ? 'rgba(39,201,90,0.2)' : 'rgba(201,39,39,0.2)',

                          border: `1px solid ${c.type === 'player' ? '#27c95a' : '#c92727'}`,

                          borderRadius: '4px', 

                          fontSize: '0.8rem',

                          display: 'flex',

                          alignItems: 'center',

                          gap: '6px',

                        }}

                      >

                        {c.type === 'player' ? '👤' : '👹'} {c.name}

                        <button 

                          onClick={() => toggleTarget(id)}

                          style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '0.75rem', padding: 0 }}

                        >✕</button>

                      </span>

                    );

                  })}

                </div>

              )}

              

              {/* Damage/Heal Controls */}

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>

                <input

                  type="number"

                  placeholder="Amount"

                  value={damageAmount}

                  onChange={(e) => setDamageAmount(e.target.value)}

                  style={{ ...S.input, width: '80px', padding: '8px', marginBottom: 0 }}

                />

                <select 

                  value={damageType} 

                  onChange={(e) => setDamageType(e.target.value)}

                  style={{ ...S.select, width: 'auto', marginBottom: 0, padding: '8px' }}

                >

                  {Object.entries(DAMAGE_TYPES).filter(([k]) => k !== 'raidwide').map(([key, dt]) => (

                    <option key={key} value={key}>{dt.icon} {dt.name}</option>

                  ))}

                </select>

                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem', cursor: 'pointer' }}>

                  <input type="checkbox" checked={isCrit} onChange={(e) => setIsCrit(e.target.checked)} />

                  CRIT

                </label>

                <select 

                  value={attackSource}

                  onChange={(e) => setAttackSource(e.target.value)}

                  style={{ ...S.select, width: 'auto', marginBottom: 0, padding: '8px', fontSize: '0.85rem' }}

                >

                  <option value="dm">From: DM</option>

                  {enemyCombatants.map(e => <option key={e.id} value={e.id}>From: 👹 {e.name}</option>)}

                </select>

              </div>

              

              <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap', alignItems: 'center' }}>

                <button 

                  style={{ ...S.btn, ...S.btnDanger, padding: '10px 20px' }}

                  onClick={applyDamageToSelected}

                  disabled={selectedTargets.length === 0 || !damageAmount}

                >

                  💥 Deal Damage {selectedTargets.length > 1 ? `(${selectedTargets.length} targets)` : ''}

                </button>

                <button 

                  style={{ ...S.btn, ...S.btnSuccess, padding: '10px 20px' }}

                  onClick={healSelected}

                  disabled={selectedTargets.length === 0 || !damageAmount}

                >

                  ➕ Heal {selectedTargets.length > 1 ? `(${selectedTargets.length} targets)` : ''}

                </button>

                <button

                  style={{ ...S.btn, padding: '10px 20px', background: 'rgba(125,181,255,0.15)', border: '1px solid rgba(125,181,255,0.4)', color: '#87ceeb' }}

                  onClick={grantTempHpToSelected}

                  disabled={selectedTargets.length === 0 || !damageAmount}

                >

                  🩵 Temp HP

                </button>

                <button 

                  style={{ ...S.btn, padding: '10px 20px' }}

                  onClick={openManualSaveRollModal}

                  disabled={!pendingSpellSaveContext && selectedTargets.length === 0}

                >

                  🎲 Roll Saves

                </button>

                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem', cursor: 'pointer', color: '#c9a227', marginLeft: '8px' }}>

                  <input type="checkbox" checked={bypassReaction} onChange={(e) => setBypassReaction(e.target.checked)} />

                  Bypass Reactions

                </label>

              </div>

              

              {selectedTargets.length === 0 && (

                <p style={{ fontSize: '0.8rem', color: '#888', marginTop: '8px', marginBottom: 0 }}>

                  Click combatants below to select targets, or use quick select buttons above.

                </p>

              )}

            </div>

          )}

          

          {/* Player Action Bar - for non-DM players */}

          {!isDM && viewCharId && (

            <div style={{ ...S.card, gridColumn: '1 / -1' }}>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>

                <h3 style={{ color: '#27c95a', margin: 0, fontSize: '0.9rem' }}>⚔️ Actions ({selectedTargets.length} selected)</h3>

                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>

                  <button style={{ ...S.btn, ...S.btnXs }} onClick={selectAllPlayers}>👤 Players</button>

                  <button style={{ ...S.btn, ...S.btnXs }} onClick={selectAllEnemies}>👹 Enemies</button>

                  <button style={{ ...S.btn, ...S.btnXs }} onClick={clearSelection}>Clear</button>

                </div>

              </div>

              

              {/* Selected Targets Display */}

              {selectedTargets.length > 0 && (

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '10px' }}>

                  {selectedTargets.map(id => {

                    const c = combatants.find(x => x.id === id);

                    if (!c) return null;

                    return (

                      <span 

                        key={id} 

                        style={{ 

                          padding: '3px 8px', 

                          background: c.type === 'player' || c.type === 'npc' ? 'rgba(39,201,90,0.15)' : 'rgba(201,39,39,0.15)',

                          border: `1px solid ${c.type === 'player' || c.type === 'npc' ? '#27c95a40' : '#c9272740'}`,

                          borderRadius: '4px', 

                          fontSize: '0.75rem',

                          display: 'flex', alignItems: 'center', gap: '4px',

                        }}

                      >

                        {c.type === 'player' ? '👤' : c.type === 'npc' ? '🤝' : '👹'} {c.name}

                        <button 

                          onClick={() => toggleTarget(id)}

                          style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '0.7rem', padding: 0 }}

                        >✕</button>

                      </span>

                    );

                  })}

                </div>

              )}

              

              {/* Controls */}

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>

                <input

                  type="number"

                  placeholder="Amount"

                  value={damageAmount}

                  onChange={(e) => setDamageAmount(e.target.value)}

                  style={{ ...S.input, width: '75px', padding: '6px 8px', marginBottom: 0, fontSize: '0.85rem' }}

                />

                <select 

                  value={damageType} 

                  onChange={(e) => setDamageType(e.target.value)}

                  style={{ ...S.select, width: 'auto', marginBottom: 0, padding: '6px 8px', fontSize: '0.8rem' }}

                >

                  {Object.entries(DAMAGE_TYPES).filter(([k]) => k !== 'raidwide').map(([key, dt]) => (

                    <option key={key} value={key}>{dt.icon} {dt.name}</option>

                  ))}

                </select>

                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', cursor: 'pointer' }}>

                  <input type="checkbox" checked={isCrit} onChange={(e) => setIsCrit(e.target.checked)} />

                  CRIT

                </label>

              </div>

              

              <div style={{ display: 'flex', gap: '6px', marginTop: '10px', flexWrap: 'wrap' }}>

                <button 

                  style={{ ...S.btn, ...S.btnDanger, padding: '8px 16px', fontSize: '0.8rem' }}

                  onClick={applyDamageToSelected}

                  disabled={selectedTargets.length === 0 || !damageAmount}

                >

                  💥 Deal Damage

                </button>

                <button 

                  style={{ ...S.btn, ...S.btnSuccess, padding: '8px 16px', fontSize: '0.8rem' }}

                  onClick={healSelected}

                  disabled={selectedTargets.length === 0 || !damageAmount}

                >

                  ➕ Heal

                </button>

                <button

                  style={{ ...S.btn, padding: '8px 16px', fontSize: '0.8rem', background: 'rgba(125,181,255,0.15)', border: '1px solid rgba(125,181,255,0.4)', color: '#87ceeb' }}

                  onClick={grantTempHpToSelected}

                  disabled={selectedTargets.length === 0 || !damageAmount}

                >

                  🩵 Temp HP

                </button>

                <button 

                  style={{ ...S.btn, padding: '8px 16px', fontSize: '0.8rem' }}

                  onClick={openManualSaveRollModal}

                  disabled={!pendingSpellSaveContext && selectedTargets.length === 0}

                >

                  🎲 Roll Saves

                </button>

              </div>

              

              {selectedTargets.length === 0 && (

                <p style={{ fontSize: '0.75rem', color: '#666', marginTop: '6px', marginBottom: 0 }}>

                  Click combatants to select targets.

                </p>

              )}

            </div>

          )}

          

          {/* Card View Filter Toolbar */}

          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center', padding: '4px 0' }}>

            <span style={{ fontSize: '0.7rem', color: '#888', marginRight: '4px' }}>View:</span>

            {[

              { key: 'all', label: 'All' },

              { key: 'allies', label: 'Allies' },

              { key: 'enemies', label: 'Enemies' },

              { key: 'players', label: 'Players' },

              { key: 'pinned', label: '📌 Pinned' },

              { key: 'my_effects', label: '✨ My Effects' },

              { key: 'my_actions', label: '⚡ My Turn' },

            ].map(f => (

              <button

                key={f.key}

                style={{

                  ...S.btn, padding: '2px 8px', fontSize: '0.65rem', borderRadius: '10px',

                  background: cardViewFilter === f.key ? 'rgba(201,162,39,0.3)' : 'rgba(255,255,255,0.03)',

                  border: `1px solid ${cardViewFilter === f.key ? '#c9a227' : 'rgba(255,255,255,0.1)'}`,

                  color: cardViewFilter === f.key ? '#ffd700' : '#888',

                }}

                onClick={() => setCardViewFilter(f.key)}

              >{f.label}</button>

            ))}

          </div>

          

          {/* Split Combatants into Allies and Enemies with filter */}

          {(() => {

            // Determine which combatants pass the filter

            const passesFilter = (c) => {

              const isMe = c.id === viewCharId;

              const isAlly = c.type === 'player' || c.type === 'npc';

              const isEnemy = c.type === 'enemy';

              

              switch (cardViewFilter) {

                case 'allies': return isMe || isAlly;

                case 'enemies': return isMe || isEnemy;

                case 'players': return c.type === 'player';

                case 'pinned': return isMe || pinnedCombatantIds.has(c.id);

                case 'my_effects': {

                  if (isMe) return true;

                  // Check if I have buffs/debuffs on this combatant

                  const hasMyDebuff = (c.debuffs || []).some(d => d.casterId === viewCharId);

                  const charEffects = chars[c.id]?.activeEffects || [];

                  const hasMyBuff = charEffects.some(e => e.casterId === viewCharId);

                  // Check if this combatant has effects on me

                  const myDebuffs = (combatants.find(mc => mc.id === viewCharId)?.debuffs || []);

                  const hasTheirDebuffOnMe = myDebuffs.some(d => d.casterId === c.id);

                  return hasMyDebuff || hasMyBuff || hasTheirDebuffOnMe;

                }

                case 'my_actions': {

                  if (isMe) return true;

                  // Check if I have ticks to manage on this combatant

                  const tickEffects = getTickEffects(c);

                  const hasMyTick = tickEffects.some(eff => {

                    const isCaster = eff.casterId === viewCharId;

                    const isTarget = c.id === viewCharId;

                    if (!isCaster && !isTarget) return false;

                    const tickRef = eff.effectRef || createCombatRuntimeEffectRef({

                      effectId: eff.effectId || null,

                      effectGroupId: eff.effectGroupId || null,

                      sourceCollection: eff.sourceCollection || null,

                      effectIndex: Number.isInteger(eff.effectIndex) ? eff.effectIndex : null,

                      effectName: eff.name,

                      casterId: eff.casterId || null,

                    });

                    const tickKey = getCombatTickKey(c.id, eff.type, tickRef, eff.name);

                    return !tickedThisTurn[tickKey] && shouldTickGlow(eff, c);

                  });

                  return hasMyTick;

                }

                default: return true; // 'all'

              }

            };

            

            const allies = sortedCombatants.filter(c => (c.type === 'player' || c.type === 'npc') && passesFilter(c));

            const enemies = sortedCombatants.filter(c => c.type === 'enemy' && passesFilter(c));

            

            const toggleSection = (section) => setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));

            

            const sectionHeader = (label, icon, count, section, color) => (

              <div 

                style={{ 

                  gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '8px', 

                  padding: '4px 8px', cursor: 'pointer', userSelect: 'none',

                  borderBottom: `1px solid ${color}30`,

                }}

                onClick={() => toggleSection(section)}

              >

                <span style={{ fontSize: '0.7rem', color: '#888' }}>{collapsedSections[section] ? '▶' : '▼'}</span>

                <span style={{ fontSize: '0.8rem', color }}>{icon} {label}</span>

                <span style={{ fontSize: '0.65rem', color: '#666' }}>({count})</span>

                {collapsedSections[section] && count > 0 && (

                  <span style={{ fontSize: '0.6rem', color: '#888', marginLeft: 'auto' }}>

                    {sortedCombatants.filter(c => (section === 'allies' ? (c.type === 'player' || c.type === 'npc') : c.type === 'enemy') && passesFilter(c))

                      .map(c => c.name.slice(0, 6)).join(', ')}

                  </span>

                )}

              </div>

            );

            

            return (

              <>

                {sectionHeader('Allies', '🛡️', allies.length, 'allies', '#27c95a')}

                {!collapsedSections.allies && allies.map(c => renderCombatant(c))}

                {collapsedSections.allies && allies.length > 0 && (

                  <div style={{ gridColumn: '1 / -1', fontSize: '0.7rem', color: '#666', padding: '2px 20px', fontStyle: 'italic' }}>

                    {allies.length} hidden — click to expand

                  </div>

                )}

                

                {sectionHeader('Enemies', '⚔️', enemies.length, 'enemies', '#c92727')}

                {!collapsedSections.enemies && enemies.map(c => renderCombatant(c))}

                {collapsedSections.enemies && enemies.length > 0 && (

                  <div style={{ gridColumn: '1 / -1', fontSize: '0.7rem', color: '#666', padding: '2px 20px', fontStyle: 'italic' }}>

                    {enemies.length} hidden — click to expand

                  </div>

                )}

              </>

            );

          })()}

          

          {/* Player's Own Character Card (if not found in combatants) */}

          {!viewIsDM && viewChar && !combatants.find(c => c.id === viewCharId) && (

            <div style={{ 

              ...S.card, 

              gridColumn: '1 / -1',

              border: '2px solid #27c95a',

              background: 'rgba(39,201,90,0.1)',

            }}>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>

                <span style={{ fontSize: '2rem' }}>👤</span>

                <div>

                  <h3 style={{ color: '#27c95a', margin: 0 }}>{viewChar.name}</h3>

                  <span style={{ fontSize: '0.8rem', color: '#888' }}>(Not in active combat)</span>

                </div>

              </div>

              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>

                <div>

                  <span style={{ fontSize: '0.8rem', color: '#888' }}>HP:</span>

                  <span style={{ fontWeight: 'bold', marginLeft: '8px', color: viewChar.currentHp > getEffectiveMaxHp(viewChar) * 0.5 ? '#27c95a' : viewChar.currentHp > getEffectiveMaxHp(viewChar) * 0.25 ? '#c9a227' : '#c92727' }}>

                    {viewChar.currentHp}{(viewChar.tempHp || 0) > 0 && <span style={{ color: '#87ceeb' }}>+{viewChar.tempHp}</span>}/{getEffectiveMaxHp(viewChar)}

                  </span>

                </div>

                <div>

                  <span style={{ fontSize: '0.8rem', color: '#888' }}>AC:</span>

                  <span style={{ fontWeight: 'bold', marginLeft: '8px' }}>{(getLinkedArmorContext(viewChar)?.totalArmorClass ?? viewChar.armorClass) || 10}</span>

                </div>

                {viewCharSpellResourceUi && (

                  <div>

                    <span style={{ fontSize: '0.8rem', color: '#888' }}>{viewCharSpellResourceUi.summary.label}:</span>

                    <span style={{ fontWeight: 'bold', marginLeft: '8px', color: '#7db5ff' }}>

                      {formatCombatSpellResourceReadout(viewCharSpellResourceUi)}

                    </span>

                  </div>

                )}

              </div>

            </div>

          )}

          

          {/* Player/DM Actions Panel - At Bottom */}

          {(viewIsDM || viewChar || currentChar) && (

            <div style={{ ...S.card, gridColumn: '1 / -1' }}>

              {viewIsDM && (

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>

                  <span style={{ fontSize: '0.8rem', color: '#c9a227' }}>🎭 Casting as:</span>

                  <select

                    value={effectiveDmCharId || (Object.values(chars)[0]?.id || '')}

                    onChange={(e) => setDmCombatCharId(e.target.value)}

                    style={{ ...S.select, marginBottom: 0, padding: '4px 8px', fontSize: '0.8rem', width: 'auto' }}

                  >

                    {Object.values(chars).map(c => (

                      <option key={c.id} value={c.id}>{c.isNPC ? '🤝' : '👤'} {c.name}</option>

                    ))}

                  </select>

                </div>

              )}

              {!viewIsDM && dmViewAsPlayerId && (

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid rgba(74,144,217,0.15)' }}>

                  <span style={{ fontSize: '0.8rem', color: dmViewMode === 'enemy' ? '#ff6b6b' : '#7db5ff' }}>🎭 Acting as <strong>{chars[dmViewAsPlayerId]?.name || combatants.find(c => c.id === dmViewAsPlayerId)?.name || HIDDEN_LABEL_PLACEHOLDER}</strong></span>

                </div>

              )}

              {castChar ? (<>

              {/* === TURN STATUS BANNER === */}

              {(() => {

                const myTurnCombatant = sortedCombatants[combat.turnIndex];

                const isMyTurn = myTurnCombatant?.id === castChar.id;

                return isMyTurn ? (

                  <div style={{ 

                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',

                    padding: '10px 14px', marginBottom: '12px', borderRadius: '8px',

                    background: 'rgba(39,201,90,0.15)', border: '2px solid #27c95a',

                    animation: 'tickGlowHot 2.5s ease-in-out infinite',

                  }}>

                    <span style={{ color: '#6cff8a', fontWeight: 'bold', fontSize: '0.9rem' }}>⚔️ It's Your Turn!</span>

                    <button 

                      style={{ 

                        ...S.btn, 

                        padding: '6px 20px', 

                        fontSize: '0.85rem',

                        fontWeight: 'bold',

                        background: 'rgba(39,201,90,0.35)',

                        border: '2px solid #27c95a',

                        color: '#6cff8a',

                      }} 

                      onClick={nextTurn}

                    >

                      ✓ End My Turn →

                    </button>

                  </div>

                ) : (

                  <div style={{ 

                    display: 'flex', alignItems: 'center', gap: '8px',

                    padding: '6px 12px', marginBottom: '12px', borderRadius: '6px',

                    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)',

                  }}>

                    <span style={{ fontSize: '0.8rem', color: '#888' }}>

                      ⏳ {myTurnCombatant?.name || '...'}'s turn — Round {combat.round}

                    </span>

                  </div>

                );

              })()}

              {/* === SECTION 1: Quick Stats & Adjustments === */}

              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>

                {/* HP Quick Adjust */}

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

                  <span style={{ color: '#c92727', fontWeight: 'bold', fontSize: '0.85rem' }}>❤️ HP:</span>

                  <button 

                    style={{ ...S.btn, padding: '2px 8px', fontSize: '0.8rem' }}

                    onClick={() => {

                      const combatant = combatants.find(c => c.id === castChar.id);

                      if (combatant) {

                        const newHp = Math.max(0, combatant.currentHp - 1);

                        const updated = combatants.map(c => c.id === castChar.id ? { ...c, currentHp: newHp } : c);

                        onUpdateCombat({ ...combat, combatants: updated });

                      }

                    }}

                  >−</button>

                  <span style={{ fontWeight: 'bold', minWidth: '50px', textAlign: 'center' }}>

                    {(() => { const cb = combatants.find(c => c.id === castChar.id); const hp = cb?.currentHp || castChar.currentHp; const tmp = cb?.tempHp || 0; return <>{hp}{tmp > 0 && <span style={{ color: '#87ceeb' }}>+{tmp}</span>}/{castChar.maxHp}</>; })()}

                  </span>

                  <button 

                    style={{ ...S.btn, padding: '2px 8px', fontSize: '0.8rem' }}

                    onClick={() => {

                      const combatant = combatants.find(c => c.id === castChar.id);

                      if (combatant) {

                        const newHp = Math.min(combatant.maxHp, combatant.currentHp + 1);

                        const updated = combatants.map(c => c.id === castChar.id ? { ...c, currentHp: newHp } : c);

                        onUpdateCombat({ ...combat, combatants: updated });

                      }

                    }}

                  >+</button>

                </div>

                

                {/* Mana Quick Adjust */}

                {castCharSpellResourceUi?.usesLegacyManaAsPrimary && (

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

                    <span style={{ color: '#4a90d9', fontWeight: 'bold', fontSize: '0.85rem' }}>✨ MP:</span>

                    <button 

                      style={{ ...S.btn, padding: '2px 8px', fontSize: '0.8rem' }}

                      onClick={() => {

                        const newMana = Math.max(0, (castChar.currentMana || 0) - 1);

                        onUpdateChars({ ...chars, [castChar.id]: { ...castChar, currentMana: newMana } });

                      }}

                    >−</button>

                    <span style={{ fontWeight: 'bold', minWidth: '50px', textAlign: 'center', color: '#7db5ff' }}>

                      {castChar.currentMana || 0}/{castCharLegacyMaxMana}

                    </span>

                    <button 

                      style={{ ...S.btn, padding: '2px 8px', fontSize: '0.8rem' }}

                      onClick={() => {

                        const newMana = Math.min(castCharLegacyMaxMana, (castChar.currentMana || 0) + 1);

                        onUpdateChars({ ...chars, [castChar.id]: { ...castChar, currentMana: newMana } });

                      }}

                    >+</button>

                  </div>

                )}

                {castCharSpellResourceUi && !castCharSpellResourceUi.usesLegacyManaAsPrimary && (

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

                    <span style={{ color: '#4a90d9', fontWeight: 'bold', fontSize: '0.85rem' }}>{castCharSpellResourceUi.summary.label}:</span>

                    <span style={{ fontWeight: 'bold', minWidth: '50px', textAlign: 'center', color: '#7db5ff' }}>

                      {formatCombatSpellResourceReadout(castCharSpellResourceUi)}

                    </span>

                  </div>

                )}

                

                {/* Spell Stats */}

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>

                  <span style={{ fontSize: '0.75rem', color: '#888' }}>DC {getSpellSaveDC(castChar)} | +{getSpellAttackBonus(castChar)} atk</span>

                </div>

              </div>

              

              {/* === SECTION 2: Quick Damage/Heal === */}

              <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>

                <div style={{ fontSize: '0.8rem', color: '#c9a227', marginBottom: '6px', fontWeight: 'bold' }}>⚔️ Quick Attack/Heal</div>

                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>

                  <input

                    type="number"

                    placeholder="Amount"

                    value={playerDamageAmount}

                    onChange={(e) => setPlayerDamageAmount(e.target.value)}

                    style={{ ...S.input, width: '70px', padding: '4px 8px', marginBottom: 0, fontSize: '0.85rem' }}

                  />

                  <select

                    value={playerDamageType}

                    onChange={(e) => setPlayerDamageType(e.target.value)}

                    style={{ ...S.select, marginBottom: 0, padding: '4px 6px', fontSize: '0.75rem', width: 'auto' }}

                  >

                    {Object.entries(DAMAGE_TYPES).filter(([k]) => k !== 'raidwide').map(([key, dt]) => (

                      <option key={key} value={key}>{dt.icon} {dt.name}</option>

                    ))}

                  </select>

                  <span style={{ color: '#888', fontSize: '0.8rem' }}>→</span>

                  <select

                    value={playerDamageTarget}

                    onChange={(e) => setPlayerDamageTarget(e.target.value)}

                    style={{ ...S.select, marginBottom: 0, padding: '4px 6px', fontSize: '0.75rem', minWidth: '100px' }}

                  >

                    <option value="">Select target...</option>

                    {combatants.map(c => (

                      <option key={c.id} value={c.id}>{c.type === 'player' || c.type === 'npc' ? '👤' : '👹'} {c.name}</option>

                    ))}

                  </select>

                  <button

                    style={{ ...S.btn, ...S.btnDanger, padding: '4px 12px', fontSize: '0.8rem' }}

                    disabled={!playerDamageAmount || !playerDamageTarget}

                    onClick={() => {

                      if (!playerDamageAmount || !playerDamageTarget) return;

                      const target = combatants.find(c => c.id === playerDamageTarget);

                      if (target) {

                        // Build simple breakdown

                        const breakdown = [{ source: `${castChar.name} (manual)`, type: 'base', value: parseInt(playerDamageAmount), resultingDamage: parseInt(playerDamageAmount) }];

                        queueDamageWithBreakdown(playerDamageTarget, parseInt(playerDamageAmount), playerDamageType, `${castChar.name}`, false, false, breakdown);

                        setPlayerDamageAmount('');

                      }

                    }}

                  >

                    💥 Damage

                  </button>

                  <button

                    style={{ ...S.btn, ...S.btnSuccess, padding: '4px 12px', fontSize: '0.8rem' }}

                    disabled={!playerDamageAmount || !playerDamageTarget}

                    onClick={() => {

                      if (!playerDamageAmount || !playerDamageTarget) return;

                      healTarget(playerDamageTarget, parseInt(playerDamageAmount), castChar.name);

                      setPlayerDamageAmount('');

                    }}

                  >

                    ➕ Heal

                  </button>

                  <button

                    style={{ ...S.btn, padding: '4px 10px', fontSize: '0.8rem' }}

                    onClick={() => setShowQuickEffect(true)}

                    title="Add buff/debuff to target"

                  >

                    ✨ Effect

                  </button>

                </div>

              </div>

              {(() => {

                const itemAbilityRows = getCombatItemAbilityRows(castChar);

                if (itemAbilityRows.length === 0) return null;

                return (

                  <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>

                    <div style={{ fontSize: '0.8rem', color: '#c9a227', marginBottom: '6px', fontWeight: 'bold' }}>Item Abilities</div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>

                      {itemAbilityRows.map((row) => {

                        const abilityTargetKey = `${castChar.id}:${row.itemId}:${row.abilityId}`;

                        const selectedItemAbilityTarget = combatItemAbilityTargets[abilityTargetKey] || '';

                        return (

                        <div key={`${row.itemId}:${row.abilityId}`} style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', padding: '5px 8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', borderLeft: '3px solid #c9a227' }}>

                          <span style={{ fontWeight: 'bold', fontSize: '0.8rem', color: '#eee' }}>{row.abilityName}</span>

                          <span style={{ fontSize: '0.7rem', color: '#aaa' }}>{row.itemName}</span>

                          {row.effectPreview && (

                            <span style={{ fontSize: '0.7rem', color: '#888', flex: 1, minWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.effectPreview}</span>

                          )}

                          {row.hasLimitedUses && (

                            <span style={{ fontSize: '0.65rem', color: row.depleted ? '#777' : '#7db5ff', fontFamily: 'monospace' }}>

                              {row.currentUses}/{row.maxUses}

                            </span>

                          )}

                          {row.requiresTarget && (

                            <select

                              aria-label={`Target for ${row.itemName} ${row.abilityName}`}

                              value={selectedItemAbilityTarget}

                              disabled={row.depleted || !!row.unsupportedReason}

                              onChange={(event) => setCombatItemAbilityTargets((prev) => ({ ...prev, [abilityTargetKey]: event.target.value }))}

                              style={{ ...S.select, marginBottom: 0, padding: '2px 6px', fontSize: '0.7rem', minWidth: '110px', width: 'auto', opacity: row.depleted ? 0.45 : 1 }}

                            >

                              <option value="">Target...</option>

                              {combatants.map((combatant) => (

                                <option key={combatant.id} value={combatant.id}>

                                  {combatant.type === 'enemy' ? 'Enemy' : 'Ally'}: {combatant.name}

                                </option>

                              ))}

                            </select>

                          )}

                          <button

                            style={{ ...S.btn, ...S.btnMana, padding: '2px 8px', fontSize: '0.7rem', opacity: row.depleted ? 0.45 : 1 }}

                            disabled={row.depleted || !!row.unsupportedReason || (row.requiresTarget && !selectedItemAbilityTarget)}

                            aria-label={row.hasSaveRequired ? `${row.itemName} ${row.abilityName} requires save resolution` : row.hasTargetedTempHp ? `Grant temp HP with ${row.itemName} ${row.abilityName}` : row.hasTargetedHealing ? `Heal with ${row.itemName} ${row.abilityName}` : row.hasTargetedDamage ? `Damage with ${row.itemName} ${row.abilityName}` : `Use ${row.itemName} ${row.abilityName} in combat`}

                            title={row.unsupportedReason || (row.depleted ? 'No uses remaining' : `Use ${row.itemName} -> ${row.abilityName}`)}

                            onClick={() => activateCombatItemAbility(castChar.id, row.itemId, row.abilityId, { targetId: selectedItemAbilityTarget })}

                          >

                            {row.hasResolvableSaveDamage ? 'Roll Save' : row.hasSaveRequired ? 'Needs Save' : row.hasTargetedTempHp ? 'Temp HP' : row.hasTargetedHealing ? 'Heal' : row.hasTargetedDamage ? 'Damage' : 'Use'}

                          </button>

                        </div>

                        );

                      })}

                    </div>

                  </div>

                );

              })()}

              

              {/* === SECTION 3: Active Notes === */}

              <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>

                <div style={{ fontSize: '0.8rem', color: '#c9a227', marginBottom: '6px', fontWeight: 'bold' }}>📝 Combat Notes</div>

                <textarea

                  value={playerNotes}

                  onChange={(e) => setPlayerNotes(e.target.value)}

                  placeholder="Track active effects, reminders... (e.g., 'Target takes 2x damage for 2 rounds', 'Concentration on Bless')"

                  style={{ 

                    ...S.input, 

                    width: '100%', 

                    minHeight: '50px', 

                    marginBottom: 0, 

                    fontSize: '0.8rem',

                    resize: 'vertical',

                    fontFamily: 'inherit',

                  }}

                />

              </div>

              

              {/* === SECTION 4: Effects Dashboard === */}

              {(() => {

                const myId = castChar.id;

                const myCombatant = combatants.find(c => c.id === myId);

                

                // ── CATEGORY 1: DIRECTLY ON ME ──

                // Buffs on me (split by self vs other caster)

                const allMyBuffs = castChar.activeEffects || [];

                const mySelfBuffs = allMyBuffs.filter(e => e.casterId === myId);

                const othersBuffsOnMe = allMyBuffs.filter(e => e.casterId && e.casterId !== myId);

                // Debuffs on me 

                const debuffsOnMe = myCombatant?.debuffs || [];

                // Conditions on me

                const conditionsOnMe = myCombatant?.conditions || [];

                

                // ── CATEGORY 2: MY CASTS ON OTHERS ──

                const myMarks = castChar.markedTargets || [];

                const myDebuffsOnOthers = [];

                const myBuffsOnOthers = [];

                combatants.forEach(c => {

                  if (c.id === myId) return;

                  (c.debuffs || []).forEach(d => {

                    if (d.casterId === myId) myDebuffsOnOthers.push({ ...d, targetId: c.id, targetName: c.name });

                  });

                });

                Object.values(chars).forEach(ch => {

                  if (ch.id === myId) return;

                  (ch.activeEffects || []).forEach(e => {

                    if (e.casterId === myId) myBuffsOnOthers.push({ ...e, targetId: ch.id, targetName: ch.name });

                  });

                });

                

                // ── CATEGORY 3: PARTY EFFECTS ON ENEMIES (not mine) ──

                const partyEffectsOnEnemies = [];

                combatants.forEach(c => {

                  if (c.type !== 'enemy') return;

                  (c.debuffs || []).forEach(d => {

                    if (d.casterId && d.casterId !== myId) {

                      const casterCombatant = combatants.find(x => x.id === d.casterId);

                      if (casterCombatant?.type === 'player' || casterCombatant?.type === 'npc') {

                        partyEffectsOnEnemies.push({ ...d, targetId: c.id, targetName: c.name });

                      }

                    }

                  });

                });

                

                const hasOnMe = mySelfBuffs.length > 0 || othersBuffsOnMe.length > 0 || debuffsOnMe.length > 0 || conditionsOnMe.length > 0;

                const hasMyCasts = myMarks.length > 0 || myDebuffsOnOthers.length > 0 || myBuffsOnOthers.length > 0 || castChar.concentratingOn;

                const hasPartyOnEnemies = partyEffectsOnEnemies.length > 0;

                

                if (!hasOnMe && !hasMyCasts && !hasPartyOnEnemies) return null;

                

                // Duration helper

                const dur = (d, roundApplied) => {

                  if (d === 'concentration') return '⚡';

                  if (typeof d === 'number') return `${Math.max(0, d - (combat.round - (roundApplied || combat.round)))}r`;

                  return '∞';

                };

                

                return (

                  <div style={{ marginBottom: '12px' }}>

                    {/* ── ON ME ── */}

                    {hasOnMe && (

                      <div style={{ 

                        marginBottom: '8px', padding: '8px', borderRadius: '6px',

                        background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.08)',

                      }}>

                        <div style={{ fontSize: '0.75rem', color: '#aaa', marginBottom: '6px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>

                          📍 On Me

                          <span style={{ fontSize: '0.6rem', color: '#555', fontWeight: 'normal' }}>

                            ({mySelfBuffs.length + othersBuffsOnMe.length + debuffsOnMe.length + conditionsOnMe.length})

                          </span>

                        </div>

                        

                        {/* Buffs I cast on myself */}

                        {mySelfBuffs.length > 0 && (

                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: (othersBuffsOnMe.length > 0 || debuffsOnMe.length > 0) ? '6px' : 0 }}>

                            {mySelfBuffs.map((eff, i) => {

                              const origIdx = allMyBuffs.indexOf(eff);

                              return (

                                <span key={`sb-${i}`} style={{ 

                                  padding: '3px 8px', borderRadius: '4px', fontSize: '0.7rem',

                                  background: 'rgba(74,144,217,0.12)', 

                                  borderLeft: '3px solid #4a90d9',

                                  border: '1px solid rgba(74,144,217,0.25)', borderLeftWidth: '3px', borderLeftColor: '#4a90d9',

                                  color: '#7db5ff', display: 'flex', alignItems: 'center', gap: '3px',

                                  cursor: 'pointer',

                                }} onClick={() => setEditingEffect(editingEffect?.charId === myId && editingEffect?.index === origIdx ? null : { type: 'buff', charId: myId, index: origIdx, effect: eff })}>

                                  ✨ {eff.name}

                                  {eff.modifier && <span style={{ color: '#27c95a', fontFamily: 'monospace', fontSize: '0.6rem' }}>{eff.modifier}</span>}

                                  <span style={{ color: '#555', fontSize: '0.55rem' }}>{dur(eff.duration, eff.roundApplied)}</span>

                                </span>

                              );

                            })}

                          </div>

                        )}

                        

                        {/* Buffs from others */}

                        {othersBuffsOnMe.length > 0 && (

                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: debuffsOnMe.length > 0 ? '6px' : 0 }}>

                            {othersBuffsOnMe.map((eff, i) => (

                              <span key={`ob-${i}`} style={{ 

                                padding: '3px 8px', borderRadius: '4px', fontSize: '0.7rem',

                                background: 'rgba(39,201,90,0.1)', 

                                border: '1px solid rgba(39,201,90,0.2)', borderLeftWidth: '3px', borderLeftColor: '#27c95a',

                                color: '#6cff8a', display: 'flex', alignItems: 'center', gap: '3px',

                              }}>

                                ✨ {eff.name}

                                {eff.modifier && <span style={{ fontFamily: 'monospace', fontSize: '0.6rem' }}>{eff.modifier}</span>}

                                <span style={{ color: '#555', fontSize: '0.55rem' }}>{dur(eff.duration, eff.roundApplied)} · {eff.casterName || '?'}</span>

                              </span>

                            ))}

                          </div>

                        )}

                        

                        {/* Conditions */}

                        {conditionsOnMe.length > 0 && (

                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: debuffsOnMe.length > 0 ? '6px' : 0 }}>

                            {conditionsOnMe.map((cond, i) => (

                              <span key={`co-${i}`} style={{ 

                                padding: '2px 6px', borderRadius: '4px', fontSize: '0.68rem',

                                background: 'rgba(155,89,182,0.15)', border: '1px solid rgba(155,89,182,0.3)',

                                color: '#c39bdb',

                              }}>🔮 {cond}</span>

                            ))}

                          </div>

                        )}

                        

                        {/* Debuffs on me — with SAVE BUTTONS */}

                        {debuffsOnMe.length > 0 && (

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>

                            {debuffsOnMe.map((d, i) => {

                              const hasSave = d.saveType && d.saveDC;

                              const saveEffectRef = createCombatRuntimeEffectRef({

                                effectId: d.id || d.effectId || null,

                                effectGroupId: d.effectGroupId || null,

                                sourceCollection: 'combatant.debuffs',

                                effectIndex: i,

                                effectName: d.name,

                                casterId: d.casterId || null,

                              });

                              const saveTickKey = getCombatTickKey(myId, 'save_only', saveEffectRef, d.name);

                              const saveAlreadyTicked = tickedThisTurn[saveTickKey];

                              const saveQueued = (lifecycleQueue || []).some(item => getQueueItemTickKey(item) === saveTickKey);

                              const isMyCastDebuff = d.casterId === myId;

                              const leftColor = isMyCastDebuff ? '#4a90d9' : '#e74c3c';

                              

                              return (

                                <div key={`dm-${i}`} style={{ 

                                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px',

                                  padding: '4px 8px', borderRadius: '4px',

                                  background: 'rgba(231,76,60,0.08)',

                                  border: '1px solid rgba(231,76,60,0.2)', borderLeftWidth: '3px', borderLeftColor: leftColor,

                                }}>

                                  <span style={{ fontSize: '0.7rem', color: '#e0bbbb', display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', flex: 1 }}>

                                    💀 <strong style={{ color: '#ff6b6b' }}>{d.name}</strong>

                                    {d.dotDamage && <span style={{ color: '#ff4444', fontFamily: 'monospace', fontSize: '0.6rem' }}>🔥{d.dotDamage}/t</span>}

                                    {d.hotHeal && <span style={{ color: '#27c95a', fontFamily: 'monospace', fontSize: '0.6rem' }}>➕{d.hotHeal}/t</span>}

                                    {d.bonusDice && <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: '#ff6b6b' }}>{d.bonusDice}</span>}

                                    <span style={{ color: '#555', fontSize: '0.55rem' }}>

                                      {dur(d.duration, d.roundApplied)} · {d.casterName || '?'}

                                    </span>

                                  </span>

                                  {hasSave && (

                                    <button

                                      disabled={saveAlreadyTicked || saveQueued}

                                      style={{

                                        padding: '2px 8px', fontSize: '0.65rem', fontWeight: 'bold', borderRadius: '4px',

                                        cursor: saveAlreadyTicked || saveQueued ? 'default' : 'pointer', whiteSpace: 'nowrap', flexShrink: 0,

                                        border: `1px solid ${saveAlreadyTicked || saveQueued ? '#555' : '#c9a227'}`,

                                        background: saveAlreadyTicked || saveQueued ? 'rgba(80,80,80,0.15)' : 'rgba(201,162,39,0.15)',

                                        color: saveAlreadyTicked || saveQueued ? '#666' : '#ffd700',

                                        opacity: saveAlreadyTicked || saveQueued ? 0.5 : 1,

                                      }}

                                      onClick={() => {

                                        if (saveQueued) return;

                                        tickSave(myId, d.name, d.saveType, d.saveDC, d.saveRemovesEffect !== false, saveEffectRef);

                                      }}

                                    >

                                      🎲 {(d.saveType || 'DEX').toUpperCase()} DC {d.saveDC}

                                    </button>

                                  )}

                                </div>

                              );

                            })}

                          </div>

                        )}

                      </div>

                    )}

                    

                    {/* ── MY CASTS ── */}

                    {hasMyCasts && (

                      <div style={{ 

                        marginBottom: '8px', padding: '8px', borderRadius: '6px',

                        background: 'rgba(155,89,182,0.05)', border: '1px solid rgba(155,89,182,0.15)',

                      }}>

                        <div style={{ fontSize: '0.75rem', color: '#9b59b6', marginBottom: '6px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>

                          🔮 My Casts

                          <span style={{ fontSize: '0.6rem', color: '#555', fontWeight: 'normal' }}>

                            ({myMarks.length + myDebuffsOnOthers.length + myBuffsOnOthers.length})

                          </span>

                        </div>

                        

                        {castChar.concentratingOn && (

                          <div style={{ 

                            display: 'inline-flex', alignItems: 'center', gap: '4px', marginBottom: '6px',

                            padding: '2px 8px', background: 'rgba(255,152,0,0.12)', border: '1px solid rgba(255,152,0,0.4)', borderRadius: '4px',

                            fontSize: '0.7rem', color: '#ff9800',

                          }}>

                            ⚡ Concentrating: <strong>{castChar.concentratingOn}</strong>

                          </div>

                        )}

                        

                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>

                          {myMarks.map((mark, i) => (

                            <span key={`mk-${i}`} style={{ 

                              padding: '2px 8px', borderRadius: '4px', fontSize: '0.7rem',

                              background: 'rgba(201,162,39,0.12)', border: '1px solid rgba(201,162,39,0.3)', borderLeftWidth: '3px', borderLeftColor: '#c9a227',

                              color: '#ffd700', display: 'flex', alignItems: 'center', gap: '3px',

                            }}>

                              🎯 {mark.spellName || 'Mark'} → <span style={{ color: '#aaa' }}>{mark.targetName}</span>

                              <span style={{ fontFamily: 'monospace', fontSize: '0.6rem' }}>+{mark.bonusDice}</span>

                              <button

                                style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontSize: '0.6rem', padding: '0 2px' }}

                                onClick={() => {

                                  setPendingConfirm({

                                    title: '🎯 Remove Mark?',

                                    message: `Remove ${mark.spellName || 'mark'} on ${mark.targetName}?`,

                                    actions: [

                                      { label: 'Remove', color: '#9b59b6', onClick: () => {

                                        const updated = { ...castChar, markedTargets: castChar.markedTargets.filter((_, idx) => idx !== i) };

                                        const updatedCombatants = combatants.map(c => {

                                          if (c.id === mark.targetId) {

                                            return { ...c,

                                              debuffs: (c.debuffs || []).filter(d => !(d.name === mark.spellName && d.casterId === myId)),

                                              conditions: (c.conditions || []).filter(cond => !cond.includes(mark.spellName)),

                                            };

                                          }

                                          return c;

                                        });

                                        onUpdateChars({ ...chars, [myId]: updated });

                                        onUpdateCombat({ ...combat, combatants: updatedCombatants });

                                        setPendingConfirm(null);

                                      }},

                                      { label: 'Cancel', color: '#666', onClick: () => setPendingConfirm(null) },

                                    ]

                                  });

                                }}

                              >✕</button>

                            </span>

                          ))}

                          {myBuffsOnOthers.map((eff, i) => (

                            <span key={`bo-${i}`} style={{ 

                              padding: '2px 8px', borderRadius: '4px', fontSize: '0.7rem',

                              background: 'rgba(39,201,90,0.1)', border: '1px solid rgba(39,201,90,0.25)', borderLeftWidth: '3px', borderLeftColor: '#4a90d9',

                              color: '#6cff8a', display: 'flex', alignItems: 'center', gap: '3px',

                            }}>

                              ✨ {eff.name} → <span style={{ color: '#aaa' }}>{eff.targetName}</span>

                              {eff.modifier && <span style={{ fontFamily: 'monospace', fontSize: '0.6rem' }}>{eff.modifier}</span>}

                              <span style={{ color: '#555', fontSize: '0.55rem' }}>{dur(eff.duration, eff.roundApplied)}</span>

                            </span>

                          ))}

                          {myDebuffsOnOthers.map((d, i) => (

                            <span key={`do-${i}`} style={{ 

                              padding: '2px 8px', borderRadius: '4px', fontSize: '0.7rem',

                              background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.25)', borderLeftWidth: '3px', borderLeftColor: '#4a90d9',

                              color: '#ff6b6b', display: 'flex', alignItems: 'center', gap: '3px',

                            }}>

                              💀 {d.name} → <span style={{ color: '#aaa' }}>{d.targetName}</span>

                              {d.dotDamage && <span style={{ fontFamily: 'monospace', fontSize: '0.6rem' }}>🔥{d.dotDamage}/t</span>}

                              {d.saveType && <span style={{ color: '#c9a227', fontSize: '0.55rem' }}>🎲{(d.saveType).toUpperCase()} DC{d.saveDC}</span>}

                              <span style={{ color: '#555', fontSize: '0.55rem' }}>{dur(d.duration, d.roundApplied)}</span>

                            </span>

                          ))}

                        </div>

                      </div>

                    )}

                    

                    {/* ── PARTY EFFECTS ON ENEMIES ── (smaller) */}

                    {hasPartyOnEnemies && (

                      <div style={{ 

                        marginBottom: '8px', padding: '6px 8px', borderRadius: '6px',

                        background: 'rgba(0,0,0,0.12)', border: '1px solid rgba(255,255,255,0.05)',

                      }}>

                        <div style={{ fontSize: '0.68rem', color: '#666', marginBottom: '4px', fontWeight: 'bold' }}>

                          🎯 Party Effects on Enemies

                          <span style={{ fontWeight: 'normal' }}> ({partyEffectsOnEnemies.length})</span>

                        </div>

                        <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>

                          {partyEffectsOnEnemies.map((d, i) => (

                            <span key={`pe-${i}`} style={{ 

                              padding: '1px 6px', borderRadius: '3px', fontSize: '0.62rem',

                              background: 'rgba(39,201,90,0.06)', border: '1px solid rgba(39,201,90,0.15)', borderLeftWidth: '2px', borderLeftColor: '#27c95a',

                              color: '#888', display: 'flex', alignItems: 'center', gap: '3px',

                            }}>

                              {d.name} → <span style={{ color: '#666' }}>{d.targetName}</span>

                              {d.bonusDice && <span style={{ fontFamily: 'monospace' }}>{d.bonusDice}</span>}

                              {d.dotDamage && <span style={{ fontFamily: 'monospace' }}>🔥{d.dotDamage}</span>}

                              <span style={{ color: '#444' }}>{d.casterName}</span>

                            </span>

                          ))}

                        </div>

                      </div>

                    )}

                  </div>

                );

              })()}

              

              {/* === SECTION 5: Spells === */}

              <div style={{ fontSize: '0.8rem', color: '#4a90d9', marginBottom: '6px', fontWeight: 'bold' }}>🔮 Spells</div>

              {(() => {

                const spells = (castChar.spells || []).filter(s => s.prepared !== false).map(spell => {

                  // Use effectType or spellType, with inference fallback

                  const existingType = spell.spellType || spell.effectType;

                  if (existingType && existingType !== 'utility' && existingType !== 'control' && existingType !== 'summon') return { ...spell, spellType: existingType };

                  const tags = (spell.tags || []).map(t => t.toLowerCase());

                  const effect = (spell.effect || '').toLowerCase();

                  const name = (spell.name || '').toLowerCase();

                  let inferredType = existingType || 'utility';

                  if (tags.includes('healing') || effect.includes('hp') || effect.includes('heal')) inferredType = 'healing';

                  else if (tags.includes('damage') || spell.damageType || spell.attackType === 'melee' || spell.attackType === 'ranged') inferredType = 'damage';

                  else if (tags.includes('mark') || name.includes('mark') || name.includes('hex')) inferredType = 'mark';

                  else if (tags.includes('debuff') || tags.includes('curse')) inferredType = 'debuff';

                  else if (tags.includes('buff') || existingType === 'buff') inferredType = 'buff';

                  return { ...spell, spellType: inferredType };

                });

                

                return spells.length > 0 ? (

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>

                    {spells.map(spell => {

                      const preferredSpellLevel = combatSpellLevels[spell.id] ?? spell.level ?? 1;

                      const resolvedSpellLevel = resolveCombatSelectedCastLevel(spell, castChar, preferredSpellLevel);

                      const customManaCost = combatSpellManaCosts[spell.id];

                      const castInfo = getCombatSpellCastInfo(spell, castChar, resolvedSpellLevel, customManaCost);

                      const defaultCastInfo = customManaCost !== undefined

                        ? getCombatSpellCastInfo(spell, castChar, resolvedSpellLevel)

                        : castInfo;

                      const spellCastLevel = castInfo?.castLevel ?? resolvedSpellLevel;

                      const resolvedSpellData = castInfo?.resolvedSpell || spell;

                      const resolvedSpellDice = resolvedSpellData?.dice || '';

                      const resolvedMaxTargets = Number.isFinite(resolvedSpellData?.maxTargets)

                        ? resolvedSpellData.maxTargets

                        : (spell.maxTargets || 1);

                      const manaCost = castInfo?.cost;

                      const canUpcast = (castInfo?.availableCastLevels?.length || 0) > 1;

                      const spellTarget = combatSpellTargets[spell.id];

                      const spellRoll = combatSpellRolls[spell.id];

                      const isExpanded = expandedSpells[spell.id];

                      const manualAmount = combatSpellRolls[spell.id + '_manual'] || '';

                      const resourceBlocked = isSpellCastInfoBlocked(castInfo, { isDM });

                      const costLabel = formatSpellCastInfoLabel(castInfo, spell);

                      

                      const typeColors = {

                        healing: '#27c95a', buff: '#4a90d9', mark: '#9b59b6', debuff: '#e74c3c', damage: '#c92727'

                      };

                      const typeIcons = {

                        healing: '➕', buff: '✨', mark: '🎯', debuff: '💀', damage: '🔥'

                      };

                      const color = typeColors[spell.spellType] || '#c92727';

                      const icon = typeIcons[spell.spellType] || '🔥';

                      

                      return (

                        <div key={spell.id} style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '6px', borderLeft: `3px solid ${color}` }}>

                          {/* Main Row */}

                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 8px', flexWrap: 'wrap' }}>

                            {/* Expand Button */}

                            <button

                              style={{ ...S.btn, padding: '2px 4px', fontSize: '0.65rem', opacity: 0.7 }}

                              onClick={() => setExpandedSpells(prev => ({ ...prev, [spell.id]: !prev[spell.id] }))}

                              title="Show details"

                            >

                              {isExpanded ? '▼' : '▶'}

                            </button>

                            

                            {/* Level + Icon */}

                            <span style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#4a90d9' }}>{spell.level === 0 ? 'C' : spell.level}</span>

                            <span style={{ fontSize: '0.75rem' }}>{icon}</span>

                            

                            {/* Name */}

                            <span style={{ fontWeight: 'bold', fontSize: '0.8rem', minWidth: '80px', cursor: 'pointer' }} onClick={() => setExpandedSpells(prev => ({ ...prev, [spell.id]: !prev[spell.id] }))}>{spell.name}</span>

                            

                            {/* Effect - shorter */}

                            <span style={{ fontSize: '0.7rem', color: '#888', flex: 1, minWidth: '50px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{resolvedSpellDice || spell.effect?.substring(0, 15)}</span>

                            

                            {/* Upcast */}

                            {canUpcast && (

                              <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>

                                <button style={{ ...S.btn, padding: '1px 4px', fontSize: '0.65rem' }} onClick={() => {

                                  setCombatSpellLevels(prev => ({ ...prev, [spell.id]: getShiftedCombatCastLevel(spell, castChar, spellCastLevel, -1) }));

                                  setCombatSpellManaCosts(prev => ({ ...prev, [spell.id]: undefined })); // Reset custom cost

                                }}>−</button>

                                <span style={{ fontSize: '0.65rem', color: spellCastLevel > spell.level ? '#c9a227' : '#888', minWidth: '18px', textAlign: 'center' }}>L{spellCastLevel}</span>

                                <button style={{ ...S.btn, padding: '1px 4px', fontSize: '0.65rem' }} onClick={() => {

                                  setCombatSpellLevels(prev => ({ ...prev, [spell.id]: getShiftedCombatCastLevel(spell, castChar, spellCastLevel, 1) }));

                                  setCombatSpellManaCosts(prev => ({ ...prev, [spell.id]: undefined })); // Reset custom cost

                                }}>+</button>

                              </div>

                            )}

                            

                            {/* Resource cost - editable only for DM legacy combat mana */}

                            {isDM && castInfo?.kind === 'legacy-mana' && spell.level > 0 ? (

                              <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>

                                <input

                                  type="number"

                                  value={manaCost}

                                  onChange={(e) => setCombatSpellManaCosts(prev => ({ ...prev, [spell.id]: parseInt(e.target.value) || 0 }))}

                                  style={{ 

                                    ...S.input, 

                                    width: '32px', 

                                    padding: '1px 2px', 

                                    marginBottom: 0, 

                                    fontSize: '0.65rem', 

                                    textAlign: 'center',

                                    color: customManaCost !== undefined && customManaCost !== defaultCastInfo?.cost ? '#c9a227' : '#7db5ff',

                                  }}

                                  title={`Mana cost (default: ${defaultCastInfo?.cost ?? castInfo?.cost ?? 0})`}

                                  min="0"

                                />

                                <span style={{ fontSize: '0.55rem', color: '#7db5ff' }}>MP</span>

                              </div>

                            ) : (

                              <span style={{ fontSize: '0.65rem', color: '#7db5ff', minWidth: '20px' }}>{costLabel}</span>

                            )}

                            

                            {/* Target */}

                            {(() => {

                              const isMulti = resolvedMaxTargets > 1 || resolvedMaxTargets === 0;

                              const multiTargets = combatSpellMultiTargets[spell.id] || [];

                              const showPicker = combatSpellShowTargetPicker[spell.id];

                              

                              if (isMulti) {

                                // Multi-target: show count button that opens checkbox picker

                                return (

                                  <div style={{ position: 'relative' }}>

                                    <button

                                      style={{ ...S.btn, padding: '2px 8px', fontSize: '0.7rem', minWidth: '80px', background: multiTargets.length > 0 ? 'rgba(74,144,217,0.15)' : 'transparent', border: `1px solid ${multiTargets.length > 0 ? '#4a90d9' : '#444'}`, color: multiTargets.length > 0 ? '#7db5ff' : '#888' }}

                                      onClick={() => setCombatSpellShowTargetPicker(prev => ({ ...prev, [spell.id]: !prev[spell.id] }))}

                                    >

                                      🎯 {multiTargets.length}/{resolvedMaxTargets || '∞'}

                                    </button>

                                    {showPicker && (

                                      <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 100, background: '#1a1a2e', border: '1px solid #4a90d9', borderRadius: '6px', padding: '6px', minWidth: '160px', maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>

                                        <div style={{ fontSize: '0.6rem', color: '#888', marginBottom: '4px' }}>

                                          Select up to {resolvedMaxTargets || '∞'} targets:

                                        </div>

                                        {combatants.map(c => {

                                          const isChecked = multiTargets.includes(c.id);

                                          const atMax = resolvedMaxTargets > 0 && multiTargets.length >= resolvedMaxTargets && !isChecked;

                                          return (

                                            <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 4px', cursor: atMax ? 'not-allowed' : 'pointer', opacity: atMax ? 0.4 : 1, fontSize: '0.72rem', borderRadius: '3px', background: isChecked ? 'rgba(74,144,217,0.1)' : 'transparent' }}>

                                              <input type="checkbox" checked={isChecked} disabled={atMax} onChange={() => {

                                                setCombatSpellMultiTargets(prev => {

                                                  const current = prev[spell.id] || [];

                                                  const updated = isChecked ? current.filter(id => id !== c.id) : [...current, c.id];

                                                  return { ...prev, [spell.id]: updated };

                                                });

                                              }} />

                                              <span>{c.type === 'player' || c.type === 'npc' ? '👤' : '👹'} {c.name}</span>

                                              <span style={{ fontSize: '0.55rem', color: '#666', marginLeft: 'auto' }}>{c.currentHp}/{c.maxHp}</span>

                                            </label>

                                          );

                                        })}

                                        <button style={{ ...S.btn, ...S.btnXs, width: '100%', marginTop: '4px', fontSize: '0.6rem' }} onClick={() => setCombatSpellShowTargetPicker(prev => ({ ...prev, [spell.id]: false }))}>Done</button>

                                      </div>

                                    )}

                                  </div>

                                );

                              }

                              

                              // Single target: keep dropdown

                              return (

                                <select

                                  value={spellTarget || ''}

                                  onChange={(e) => setCombatSpellTargets(prev => ({ ...prev, [spell.id]: e.target.value }))}

                                  style={{ ...S.select, marginBottom: 0, padding: '2px 4px', fontSize: '0.7rem', minWidth: '70px', maxWidth: '90px' }}

                                >

                                  <option value="">Target...</option>

                                  {combatants.map(c => (

                                    <option key={c.id} value={c.id}>{c.type === 'player' || c.type === 'npc' ? '👤' : '👹'} {c.name}</option>

                                  ))}

                                </select>

                              );

                            })()}

                            

                            {/* Bonus Indicator - shows what modifiers will apply */}

                            {spellTarget && spell.spellType === 'damage' && (() => {

                              const target = combatants.find(c => c.id === spellTarget);

                              const bonuses = [];

                              

                              // Check marks

                              if (castChar.markedTargets) {

                                const mark = castChar.markedTargets.find(m => m.targetId === spellTarget && m.casterId === castChar.id);

                                if (mark) bonuses.push({ name: mark.spellName || 'Mark', dice: mark.bonusDice, color: '#9b59b6' });

                              }

                              

                              // Check debuffs

                              if (target?.debuffs) {

                                target.debuffs.forEach(debuff => {

                                  if (debuff.casterOnly && debuff.casterId !== castChar.id) return;

                                  if (debuff.autoApply === false) return;

                                  if (debuff.bonusDice) bonuses.push({ name: debuff.name, dice: debuff.bonusDice, color: '#e74c3c' });

                                  if (debuff.percentIncrease) bonuses.push({ name: debuff.name, percent: debuff.percentIncrease, color: '#ff9800' });

                                  if (debuff.flatBonus) bonuses.push({ name: debuff.name, flat: debuff.flatBonus, color: '#ffd700' });

                                });

                              }

                              

                              // Check active effects on caster (buffs)

                              if (castChar.activeEffects) {

                                castChar.activeEffects.forEach(effect => {

                                  if (!effect.autoApply) return;

                                  if (effect.appliesTo !== 'any' && effect.appliesTo !== spellTarget) return;

                                  

                                  const parsed = effect.parsed || parseEffectModifier(effect.modifier);

                                  if (!parsed) return;

                                  

                                  if (parsed.type === 'dice') {

                                    bonuses.push({ name: effect.name, dice: effect.modifier, color: '#27c95a' });

                                  } else if (parsed.type === 'percent') {

                                    bonuses.push({ name: effect.name, percent: parsed.value, color: '#7FFFD4' });

                                  } else if (parsed.type === 'flat') {

                                    bonuses.push({ name: effect.name, flat: parsed.value, color: '#27c95a' });

                                  }

                                });

                              }

                              

                              return bonuses.length > 0 ? (

                                <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>

                                  {bonuses.map((b, i) => (

                                    <span 

                                      key={i}

                                      style={{ 

                                        fontSize: '0.6rem', 

                                        padding: '1px 3px', 

                                        background: `${b.color}30`, 

                                        border: `1px solid ${b.color}`,

                                        borderRadius: '3px',

                                        color: b.color,

                                        fontFamily: 'monospace',

                                      }}

                                      title={b.name}

                                    >

                                      {b.dice ? (b.dice.startsWith('+') || b.dice.startsWith('-') ? b.dice : `+${b.dice}`) : 

                                       b.percent !== undefined ? `${b.percent >= 0 ? '+' : ''}${b.percent}%` : 

                                       b.flat !== undefined ? `${b.flat >= 0 ? '+' : ''}${b.flat}` : '?'}

                                    </span>

                                  ))}

                                </div>

                              ) : null;

                            })()}

                            

                            {/* Roll OR Manual for damage/healing/temp HP spells */}

                            {(spell.spellType === 'damage' || spell.spellType === 'healing' || spell.spellType === 'temp_hp') && resolvedSpellDice && (

                              <>

                                <button

                                  style={{ ...S.btn, padding: '2px 6px', fontSize: '0.7rem', background: 'rgba(201,162,39,0.2)', borderColor: '#c9a227' }}

                                  onClick={() => {

                                    const dice = resolvedSpellDice;

                                    const baseResult = rollDice(dice);

                                    const spellMod = resolvedSpellData.addMod ? getSpellMod(castChar) : 0;

                                    

                                    // Build comprehensive breakdown

                                    const breakdown = [];

                                    

                                    // Base dice

                                    const baseRolls = baseResult.rolls?.flatMap(r => r.rolls) || [];

                                    breakdown.push({

                                      source: spell.name,

                                      type: 'base',

                                      dice: dice, // Include the dice notation

                                      rolls: baseRolls,

                                      total: baseResult.total,

                                    });

                                    

                                    // Spell modifier with detailed breakdown

                                    if (spellMod) {

                                      const modBreakdown = getSpellModBreakdown(castChar);

                                      const modSource = modBreakdown.parts.map(p => `${p.name} ${p.value >= 0 ? '+' : ''}${p.value}`).join(', ');

                                      breakdown.push({

                                        source: modSource,

                                        type: 'modifier',

                                        value: spellMod,

                                      });

                                    }

                                    

                                    let grandTotal = baseResult.total + spellMod;

                                    

                                    // Track what we've already applied to avoid duplicates

                                    const appliedEffects = new Set();

                                    

                                    // Check for marks/debuffs on selected target

                                    if (spellTarget && spell.spellType === 'damage') {

                                      const target = combatants.find(c => c.id === spellTarget);

                                      

                                      // Check for marks from this caster

                                      if (castChar.markedTargets) {

                                        const mark = castChar.markedTargets.find(m => m.targetId === spellTarget && m.casterId === castChar.id);

                                        if (mark && mark.bonusDice && !appliedEffects.has(`mark-${mark.spellName}`)) {

                                          appliedEffects.add(`mark-${mark.spellName}`);

                                          const markResult = rollDice(mark.bonusDice);

                                          const markRolls = markResult.rolls?.flatMap(r => r.rolls) || [];

                                          breakdown.push({

                                            source: mark.spellName || 'Mark',

                                            type: 'mark',

                                            dice: mark.bonusDice,

                                            rolls: markRolls,

                                            total: markResult.total,

                                          });

                                          grandTotal += markResult.total;

                                        }

                                      }

                                      

                                      // Check for debuffs on target (skip if already applied as mark)

                                      if (target?.debuffs) {

                                        target.debuffs.forEach(debuff => {

                                          if (debuff.casterOnly && debuff.casterId !== castChar.id) return;

                                          if (debuff.autoApply === false) return;

                                          // Skip if already applied (e.g., same effect as a mark)

                                          if (appliedEffects.has(`debuff-${debuff.name}-${debuff.id || debuff.casterId}`)) return;

                                          appliedEffects.add(`debuff-${debuff.name}-${debuff.id || debuff.casterId}`);

                                          

                                          if (debuff.bonusDice) {

                                            const debuffResult = rollDice(debuff.bonusDice.replace(/^[+-]/, ''));

                                            const debuffRolls = debuffResult.rolls?.flatMap(r => r.rolls) || [];

                                            const isNegative = debuff.bonusDice.startsWith('-');

                                            const total = isNegative ? -debuffResult.total : debuffResult.total;

                                            breakdown.push({

                                              source: debuff.name || 'Debuff',

                                              type: 'debuff',

                                              dice: debuff.bonusDice,

                                              rolls: debuffRolls,

                                              total: total,

                                            });

                                            grandTotal += total;

                                          }

                                          if (debuff.percentIncrease) {

                                            const increase = Math.floor(grandTotal * (debuff.percentIncrease / 100));

                                            breakdown.push({

                                              source: debuff.name || 'Debuff',

                                              type: 'percent',

                                              percent: debuff.percentIncrease,

                                              value: increase,

                                            });

                                            grandTotal += increase;

                                          }

                                          if (debuff.flatBonus) {

                                            breakdown.push({

                                              source: debuff.name || 'Debuff',

                                              type: 'debuff',

                                              value: debuff.flatBonus,

                                            });

                                            grandTotal += debuff.flatBonus;

                                          }

                                        });

                                      }

                                      

                                      // Check for active effects (buffs) on caster

                                      if (castChar.activeEffects) {

                                        castChar.activeEffects.forEach(effect => {

                                          // Check if effect applies to this target

                                          if (!effect.autoApply) return;

                                          if (effect.appliesTo !== 'any' && effect.appliesTo !== spellTarget) return;

                                          // Skip duplicates

                                          if (appliedEffects.has(`buff-${effect.name}-${effect.id}`)) return;

                                          appliedEffects.add(`buff-${effect.name}-${effect.id}`);

                                          

                                          const parsed = effect.parsed || parseEffectModifier(effect.modifier);

                                          if (!parsed) return;

                                          

                                          if (parsed.type === 'dice') {

                                            const effectResult = rollDice(parsed.dice);

                                            const effectRolls = effectResult.rolls?.flatMap(r => r.rolls) || [];

                                            let effectTotal = effectResult.total;

                                            if (parsed.isNegative) effectTotal = -effectTotal;

                                            breakdown.push({

                                              source: effect.name,

                                              type: 'buff',

                                              dice: effect.modifier,

                                              rolls: effectRolls,

                                              total: effectTotal,

                                            });

                                            grandTotal += effectTotal;

                                          } else if (parsed.type === 'percent') {

                                            const increase = Math.floor(grandTotal * (parsed.value / 100));

                                            breakdown.push({

                                              source: effect.name,

                                              type: 'buff_percent',

                                              percent: parsed.value,

                                              value: increase,

                                            });

                                            grandTotal += increase;

                                          } else if (parsed.type === 'flat') {

                                            breakdown.push({

                                              source: effect.name,

                                              type: 'buff',

                                              value: parsed.value,

                                            });

                                            grandTotal += parsed.value;

                                          }

                                        });

                                      }

                                    }

                                    

                                    setCombatSpellRolls(prev => ({ 

                                      ...prev, 

                                      [spell.id]: { 

                                        ...baseResult, 

                                        total: grandTotal,

                                        spellMod, 

                                        breakdown,

                                        grandTotal,

                                      }, 

                                      [spell.id + '_manual']: '' 

                                    }));

                                  }}

                                  title="Roll dice (includes marks/debuffs on target)"

                                >

                                  🎲

                                </button>

                                {/* Advantage indicator near roll button */}

                                {getAdvantageForCombatant(castChar?.id) !== 'normal' && (

                                  <span style={{ 

                                    fontSize: '0.55rem', padding: '1px 4px', borderRadius: '3px',

                                    background: getAdvantageForCombatant(castChar?.id) === 'advantage' ? 'rgba(39,201,90,0.25)' : 'rgba(231,76,60,0.25)',

                                    color: getAdvantageForCombatant(castChar?.id) === 'advantage' ? '#6cff8a' : '#ff6b6b',

                                    border: `1px solid ${getAdvantageForCombatant(castChar?.id) === 'advantage' ? '#27c95a40' : '#e74c3c40'}`,

                                  }} title={`${getAdvantageForCombatant(castChar?.id)} active — applies to d20 rolls`}>

                                    {getAdvantageForCombatant(castChar?.id) === 'advantage' ? '⬆' : '⬇'}

                                  </span>

                                )}

                                <input

                                  type="number"

                                  placeholder="#"

                                  value={manualAmount}

                                  onChange={(e) => setCombatSpellRolls(prev => ({ ...prev, [spell.id + '_manual']: e.target.value, [spell.id]: null }))}

                                  style={{ ...S.input, width: '40px', padding: '2px 4px', marginBottom: 0, fontSize: '0.7rem', textAlign: 'center' }}

                                  title="Manual amount"

                                />

                              </>

                            )}

                            

                            {/* Roll Result - Full Breakdown */}

                            {spellRoll && spellRoll.breakdown && (

                              <div style={{ 

                                display: 'flex', 

                                alignItems: 'flex-start', 

                                gap: '3px', 

                                fontSize: '0.75rem',

                                background: 'rgba(0,0,0,0.3)',

                                padding: '4px 8px',

                                borderRadius: '4px',

                                flexWrap: 'wrap',

                              }}>

                                {spellRoll.breakdown.map((part, idx) => {

                                  const colors = {

                                    base: '#e8d5b7',

                                    modifier: '#4a90d9',

                                    mark: '#9b59b6',

                                    debuff: '#e74c3c',

                                    percent: '#ff9800',

                                    buff: '#27c95a',

                                    buff_percent: '#7FFFD4',

                                  };

                                  const color = colors[part.type] || '#888';

                                  

                                  // What to show in the badge

                                  let displayValue = '';

                                  if (part.rolls && part.rolls.length > 0) {

                                    displayValue = `[${part.rolls.join('+')}]`;

                                  } else if (part.percent !== undefined) {

                                    // For percentage effects, show the calculated value with proper sign

                                    const val = part.value || 0;

                                    displayValue = val >= 0 ? `+${val}` : `${val}`;

                                  } else if (part.value !== undefined) {

                                    displayValue = part.value >= 0 ? `+${part.value}` : `${part.value}`;

                                  } else if (part.total !== undefined) {

                                    displayValue = part.total >= 0 ? `+${part.total}` : `${part.total}`;

                                  }

                                  

                                  // Label to show underneath

                                  let sourceLabel = part.source || '';

                                  if (part.dice) sourceLabel = part.dice;

                                  if (part.type === 'modifier') sourceLabel = 'mod';

                                  if (part.percent !== undefined) sourceLabel = `${part.percent >= 0 ? '+' : ''}${part.percent}%`;

                                  

                                  // Build tooltip text

                                  let tooltipText = part.source || '';

                                  if (part.dice) tooltipText += ` (${part.dice})`;

                                  if (part.rolls && part.rolls.length > 0) tooltipText += `\nRolled: [${part.rolls.join(', ')}] = ${part.total}`;

                                  else if (part.value !== undefined) tooltipText += `\nValue: ${part.value}`;

                                  if (part.percent !== undefined) tooltipText += `\n${part.percent >= 0 ? '+' : ''}${part.percent}% of damage`;

                                  

                                  return (

                                    <React.Fragment key={idx}>

                                      {idx > 0 && <span style={{ color: '#666', alignSelf: 'center', marginTop: '-8px' }}>+</span>}

                                      <Tooltip text={tooltipText}>

                                        <div 

                                          style={{ 

                                            display: 'flex', 

                                            flexDirection: 'column', 

                                            alignItems: 'center',

                                            cursor: 'help',

                                          }}

                                        >

                                          <span

                                            style={{

                                              padding: '2px 6px',

                                              background: `${color}25`,

                                              border: `1px solid ${color}60`,

                                              borderRadius: '3px',

                                              color,

                                              fontWeight: 'bold',

                                              fontFamily: 'monospace',

                                              fontSize: '0.8rem',

                                            }}

                                          >

                                            {displayValue}

                                          </span>

                                          <span style={{ 

                                            fontSize: '0.55rem', 

                                            color: color, 

                                            opacity: 0.9, 

                                            marginTop: '2px', 

                                            maxWidth: '65px', 

                                            overflow: 'hidden', 

                                            textOverflow: 'ellipsis', 

                                            whiteSpace: 'nowrap',

                                            textAlign: 'center',

                                          }}>

                                            {sourceLabel}

                                          </span>

                                        </div>

                                      </Tooltip>

                                    </React.Fragment>

                                  );

                                })}

                                <span style={{ color: '#666', alignSelf: 'center', marginTop: '-8px', marginLeft: '2px' }}>=</span>

                                <span style={{ 

                                  color: '#ffd700', 

                                  fontWeight: 'bold', 

                                  fontSize: '1rem',

                                  alignSelf: 'center',

                                  marginTop: '-8px',

                                  marginLeft: '2px',

                                }}>

                                  {spellRoll.grandTotal || spellRoll.total}

                                </span>

                              </div>

                            )}

                            

                            {/* Fallback for old-style rolls without breakdown */}

                            {spellRoll && !spellRoll.breakdown && (

                              <span 

                                style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#ffd700', cursor: 'help' }}

                                title={`Rolls: ${spellRoll.rolls?.map(r => `[${r.rolls.join('+')}]`).join(' + ')}${spellRoll.spellMod ? ` + ${spellRoll.spellMod} (mod)` : ''}`}

                              >

                                ={spellRoll.total}

                              </span>

                            )}

                            

                            {/* Cast Button */}

                            {(() => {

                              const isMulti = resolvedMaxTargets > 1 || resolvedMaxTargets === 0;

                              const multiTargets = combatSpellMultiTargets[spell.id] || [];

                              const hasTarget = isMulti ? multiTargets.length > 0 : !!spellTarget;

                              const targetList = isMulti ? multiTargets.slice(0, resolvedMaxTargets || 999) : [spellTarget];

                              const castDisabled = !hasTarget

                                || resourceBlocked

                                || ((spell.spellType === 'damage' || spell.spellType === 'healing' || spell.spellType === 'temp_hp') && resolvedSpellDice && !spellRoll && !manualAmount);

                              const blockedTitle = resourceBlocked

                                ? `Not enough ${castInfo?.resourceLabel || (castInfo?.kind === 'legacy-slots' ? 'spell slots' : 'spell resources')}`

                                : undefined;



                              return (

                                <button

                                  style={{ ...S.btn, ...S.btnMana, padding: '2px 8px', fontSize: '0.7rem', opacity: castDisabled ? 0.5 : 1 }}

                                  disabled={castDisabled}

                                  title={blockedTitle}

                                  onClick={() => {

                                    if (!hasTarget) return;

                                    if (resourceBlocked) return;

                                    // Use roll result OR manual amount

                                    const amount = spellRoll ? spellRoll.total.toString() : (manualAmount || '0');

                                    const actualManaCost = castInfo?.kind === 'legacy-mana' && combatSpellManaCosts[spell.id] !== undefined

                                      ? combatSpellManaCosts[spell.id]

                                      : null;

                                    castSpell(spell, castChar, targetList, amount, spellRoll, actualManaCost, spellCastLevel, castInfo);

                                    setCombatSpellTargets(prev => ({ ...prev, [spell.id]: '' }));

                                    setCombatSpellMultiTargets(prev => ({ ...prev, [spell.id]: [] }));

                                    setCombatSpellShowTargetPicker(prev => ({ ...prev, [spell.id]: false }));

                                    setCombatSpellRolls(prev => ({ ...prev, [spell.id]: null, [spell.id + '_manual']: '' }));

                                    setCombatSpellManaCosts(prev => ({ ...prev, [spell.id]: undefined }));

                                  }}

                                >

                                  Cast{isMulti && multiTargets.length > 0 ? ` (${multiTargets.length})` : ''}

                                </button>

                              );

                            })()}

                          </div>

                          

                          {/* Expanded Details */}

                          {isExpanded && (

                            <div style={{ padding: '6px 8px 8px 28px', borderTop: '1px solid rgba(255,255,255,0.1)', fontSize: '0.75rem' }}>

                              <div style={{ color: '#888', marginBottom: '4px' }}>

                                {spell.school} • {CASTING_TIMES[spell.castingTime] || spell.castingTime} • {spell.range} • {spell.duration}

                              </div>

                              <div style={{ color: '#aaa', marginBottom: '4px' }}>{spell.description}</div>

                              {spell.higherLevels && (

                                <div style={{ color: '#c9a227', marginBottom: '4px' }}>

                                  <strong>At Higher Levels:</strong> {spell.higherLevels}

                                </div>

                              )}

                              {(spell.spellType === 'mark' || spell.spellType === 'debuff') && (

                                <div style={{ color: '#9b59b6', marginBottom: '4px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>

                                  {resolvedSpellData.bonusDice && <span>+{resolvedSpellData.bonusDice} damage</span>}

                                  {resolvedSpellData.percentIncrease && <span>+{resolvedSpellData.percentIncrease}% damage taken</span>}

                                  {resolvedSpellData.casterOnly !== false && spell.spellType === 'mark' && <span>(caster only)</span>}

                                  {resolvedSpellData.casterOnly === false && <span>(applies to all attackers)</span>}

                                  {resolvedSpellData.debuffDuration && <span>Duration: {resolvedSpellData.debuffDuration} turns</span>}

                                </div>

                              )}

                              {spell.concentration && (

                                <div style={{ color: '#ff9800' }}>⚡ Concentration</div>

                              )}

                            </div>

                          )}

                        </div>

                      );

                    })}

                  </div>

                ) : (

                  <p style={{ fontSize: '0.8rem', color: '#888', margin: 0 }}>No prepared spells.</p>

                );

              })()}

              </>) : (

                <p style={{ fontSize: '0.8rem', color: '#888' }}>Select a character above to see spell casting options.</p>

              )}

            </div>

          )}

          

          {/* Quick Effect Modal (Buff/Debuff) */}

          {showQuickEffect && (

            <div style={S.modal} onClick={() => setShowQuickEffect(false)}>

              <div style={{ ...S.modalContent, maxWidth: '500px' }} onClick={e => e.stopPropagation()}>

                <h3 style={{ color: '#9b59b6', marginBottom: '16px' }}>✨ Add Effect</h3>

                

                {/* Effect Type */}

                <div style={{ marginBottom: '12px' }}>

                  <label style={S.label}>Effect Type</label>

                  <div style={{ display: 'flex', gap: '8px' }}>

                    <button

                      style={{ ...S.btn, flex: 1, background: effectType === 'buff' ? 'rgba(74,144,217,0.3)' : 'transparent', border: effectType === 'buff' ? '2px solid #4a90d9' : '1px solid #444' }}

                      onClick={() => setEffectType('buff')}

                    >

                      ✨ Buff

                    </button>

                    <button

                      style={{ ...S.btn, flex: 1, background: effectType === 'debuff' ? 'rgba(201,39,39,0.3)' : 'transparent', border: effectType === 'debuff' ? '2px solid #c92727' : '1px solid #444' }}

                      onClick={() => setEffectType('debuff')}

                    >

                      💀 Debuff

                    </button>

                  </div>

                </div>

                

                {/* Target Selection - Multi-select for both buffs and debuffs */}

                <div style={{ marginBottom: '12px' }}>

                  <label style={S.label}>Apply To (select targets){effectType === 'buff' && effectTargets.length === 0 ? ' — defaults to caster' : ''}</label>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px', maxHeight: '120px', overflowY: 'auto' }}>

                    {combatants.map(c => {

                      const isSelected = effectTargets.includes(c.id);

                      return (

                        <button

                          key={c.id}

                          style={{

                            ...S.btn,

                            padding: '4px 8px',

                            fontSize: '0.75rem',

                            background: isSelected ? (c.type === 'player' ? 'rgba(39,201,90,0.3)' : 'rgba(201,39,39,0.3)') : 'transparent',

                            border: `1px solid ${isSelected ? (c.type === 'player' ? '#27c95a' : '#c92727') : '#444'}`,

                          }}

                          onClick={() => {

                            if (isSelected) {

                              setEffectTargets(effectTargets.filter(id => id !== c.id));

                            } else {

                              setEffectTargets([...effectTargets, c.id]);

                            }

                          }}

                        >

                          {c.type === 'player' ? '👤' : '👹'} {c.name} {isSelected && '✓'}

                        </button>

                      );

                    })}

                  </div>

                  <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>

                    <button style={{ ...S.btn, ...S.btnXs }} onClick={() => setEffectTargets(combatants.filter(c => c.type === 'enemy').map(c => c.id))}>All Enemies</button>

                    <button style={{ ...S.btn, ...S.btnXs }} onClick={() => setEffectTargets(combatants.filter(c => c.type === 'player').map(c => c.id))}>All Players</button>

                    <button style={{ ...S.btn, ...S.btnXs }} onClick={() => setEffectTargets([])}>Clear</button>

                  </div>

                </div>

                

                {/* Effect Name */}

                <div style={{ marginBottom: '12px' }}>

                  <label style={S.label}>Effect Name</label>

                  <input

                    value={effectName}

                    onChange={(e) => setEffectName(e.target.value)}

                    style={S.input}

                    placeholder="e.g., Bless, Hex, Faerie Fire..."

                  />

                </div>

                

                {/* Effect Modifier */}

                <div style={{ marginBottom: '12px' }}>

                  <label style={S.label}>Damage Modifier (optional)</label>

                  <input

                    value={effectModifier}

                    onChange={(e) => setEffectModifier(e.target.value)}

                    style={{ ...S.input, fontFamily: 'monospace' }}

                    placeholder="+1d4, +10%, -5%, -1d3"

                  />

                  {effectModifier && (

                    <div style={{ fontSize: '0.75rem', marginTop: '4px' }}>

                      {(() => {

                        const parsed = parseEffectModifier(effectModifier);

                        if (parsed) {

                          return <span style={{ color: '#27c95a' }}>✓ Valid: {parsed.type === 'dice' ? `${parsed.dice} dice` : parsed.type === 'percent' ? `${parsed.value}%` : `${parsed.value} flat`}</span>;

                        }

                        return <span style={{ color: '#888' }}>No damage modifier (condition only)</span>;

                      })()}

                    </div>

                  )}

                </div>

                

                {/* When to apply - for buffs */}

                {effectType === 'buff' && (

                  <div style={{ marginBottom: '12px' }}>

                    <label style={S.label}>Triggers When Attacking</label>

                    <select

                      value={effectAppliesTo}

                      onChange={(e) => setEffectAppliesTo(e.target.value)}

                      style={S.select}

                    >

                      <option value="any">Any target (always applies)</option>

                      {combatants.filter(c => c.type === 'enemy').map(c => (

                        <option key={c.id} value={c.id}>👹 {c.name} only</option>

                      ))}

                    </select>

                  </div>

                )}

                

                {/* Duration */}

                <div style={{ marginBottom: '12px' }}>

                  <label style={S.label}>Duration</label>

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>

                    <input

                      type="number"

                      value={effectDuration}

                      onChange={(e) => setEffectDuration(e.target.value)}

                      style={{ ...S.input, width: '80px', marginBottom: 0 }}

                      placeholder="∞"

                      min="1"

                    />

                    <span style={{ fontSize: '0.85rem', color: '#888' }}>rounds</span>

                    <span style={{ color: '#666', margin: '0 8px' }}>or</span>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.85rem' }}>

                      <input

                        type="checkbox"

                        checked={effectConcentration}

                        onChange={(e) => {

                          setEffectConcentration(e.target.checked);

                          if (e.target.checked) setEffectDuration('');

                        }}

                      />

                      ⚡ Concentration

                    </label>

                  </div>

                </div>

                

                {/* Auto-apply */}

                <div style={{ marginBottom: '12px', padding: '8px', background: 'rgba(201,162,39,0.1)', borderRadius: '4px' }}>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>

                    <input

                      type="checkbox"

                      checked={effectAutoApply}

                      onChange={(e) => setEffectAutoApply(e.target.checked)}

                    />

                    <span>🎲 Auto-apply when rolling damage</span>

                  </label>

                  <p style={{ fontSize: '0.7rem', color: '#888', margin: '4px 0 0 24px' }}>

                    {effectType === 'buff' 

                      ? 'Automatically add this bonus when you roll damage against the specified target(s)'

                      : 'Automatically add this modifier when anyone attacks these targets'}

                  </p>

                </div>

                

                {/* DoT / HoT */}

                <div style={{ marginBottom: '16px', padding: '10px', background: 'rgba(255,255,255,0.03)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.08)' }}>

                  <div style={{ fontSize: '0.8rem', color: '#c9a227', marginBottom: '8px', fontWeight: 'bold' }}>⏱️ Per-Turn Effect (optional)</div>

                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>

                    <div>

                      <label style={{ ...S.label, fontSize: '0.75rem' }}>🔥 DoT Dice</label>

                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>

                        <input

                          value={effectDotDamage}

                          onChange={(e) => setEffectDotDamage(e.target.value)}

                          style={{ ...S.input, width: '80px', marginBottom: 0, padding: '4px' }}

                          placeholder="1d6, 2d4+3"

                        />

                        <select

                          value={effectDotDamageType}

                          onChange={(e) => setEffectDotDamageType(e.target.value)}

                          style={{ ...S.select, marginBottom: 0, padding: '4px', fontSize: '0.75rem', width: 'auto' }}

                        >

                          {Object.entries(DAMAGE_TYPES).filter(([k]) => k !== 'raidwide').map(([key, dt]) => (

                            <option key={key} value={key}>{dt.icon} {dt.name}</option>

                          ))}

                        </select>

                      </div>

                    </div>

                    <div>

                      <label style={{ ...S.label, fontSize: '0.75rem' }}>➕ HoT Dice</label>

                      <input

                        value={effectHotHeal}

                        onChange={(e) => setEffectHotHeal(e.target.value)}

                        style={{ ...S.input, width: '80px', marginBottom: 0, padding: '4px' }}

                        placeholder="1d4+3"

                      />

                    </div>

                    <div>

                      <label style={{ ...S.label, fontSize: '0.75rem', color: '#87ceeb' }}>🩵 Temp HP/t</label>

                      <input

                        type="number"

                        value={effectHotTempHp || ''}

                        onChange={(e) => setEffectHotTempHp(e.target.value)}

                        style={{ ...S.input, width: '60px', marginBottom: 0, padding: '4px' }}

                        placeholder="0"

                        min="0"

                      />

                    </div>

                    <div>

                      <label style={{ ...S.label, fontSize: '0.75rem' }}>Tick Timing</label>

                      <select

                        value={effectTickTiming}

                        onChange={(e) => setEffectTickTiming(e.target.value)}

                        style={{ ...S.select, marginBottom: 0, padding: '4px', fontSize: '0.75rem', width: 'auto' }}

                      >

                        <option value="start_of_target_turn">Start of target's turn</option>

                        <option value="end_of_target_turn">End of target's turn</option>

                        <option value="start_of_caster_turn">Start of caster's turn</option>

                        <option value="end_of_caster_turn">End of caster's turn</option>

                      </select>

                    </div>

                  </div>

                  

                  {/* Recurring Save */}

                  <div style={{ marginTop: '10px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>

                    <div style={{ fontSize: '0.75rem', color: '#c9a227', marginBottom: '6px', fontWeight: 'bold' }}>🎲 Recurring Save (optional)</div>

                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>

                      <div>

                        <label style={{ ...S.label, fontSize: '0.7rem' }}>Save Type</label>

                        <select

                          value={effectSaveType}

                          onChange={(e) => setEffectSaveType(e.target.value)}

                          style={{ ...S.select, marginBottom: 0, padding: '4px', fontSize: '0.75rem', width: 'auto' }}

                        >

                          <option value="">None</option>

                          <option value="str">STR</option>

                          <option value="dex">DEX</option>

                          <option value="con">CON</option>

                          <option value="int">INT</option>

                          <option value="wis">WIS</option>

                          <option value="cha">CHA</option>

                        </select>

                      </div>

                      {effectSaveType && (

                        <>

                          <div>

                            <label style={{ ...S.label, fontSize: '0.7rem' }}>DC</label>

                            <input

                              type="number"

                              value={effectSaveDC}

                              onChange={(e) => setEffectSaveDC(e.target.value)}

                              style={{ ...S.input, width: '50px', marginBottom: 0, padding: '4px' }}

                              placeholder="14"

                              min="1"

                            />

                          </div>

                          <div>

                            <label style={{ ...S.label, fontSize: '0.7rem' }}>Save Timing</label>

                            <select

                              value={effectSaveTiming}

                              onChange={(e) => setEffectSaveTiming(e.target.value)}

                              style={{ ...S.select, marginBottom: 0, padding: '4px', fontSize: '0.75rem', width: 'auto' }}

                            >

                              <option value="start_of_target_turn">Start of target's turn</option>

                              <option value="end_of_target_turn">End of target's turn</option>

                              <option value="start_of_caster_turn">Start of caster's turn</option>

                              <option value="end_of_caster_turn">End of caster's turn</option>

                            </select>

                          </div>

                          <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.7rem', cursor: 'pointer' }}>

                            <input type="checkbox" checked={effectSaveRemoves} onChange={(e) => setEffectSaveRemoves(e.target.checked)} />

                            Removes on success

                          </label>

                        </>

                      )}

                    </div>

                  </div>

                </div>

                

                {/* Apply Button */}

                <div style={{ display: 'flex', gap: '8px' }}>

                  <button

                    style={{ ...S.btn, ...S.btnSuccess, flex: 1 }}

                    disabled={!effectName || (effectType === 'debuff' && effectTargets.length === 0)}

                    onClick={() => {

                      const parsed = parseEffectModifier(effectModifier);

                      const effectId = generateId();

                      

                      if (effectType === 'buff') {

                        // Apply buff to selected targets (or self if none selected)

                        const effectCaster = castChar || currentChar;

                        const targets = effectTargets.length > 0 ? effectTargets : (effectCaster ? [effectCaster.id] : []);

                        if (targets.length === 0) return;

                        

                        const newEffect = {

                          id: effectId,

                          name: effectName,

                          modifier: effectModifier,

                          parsed,

                          appliesTo: effectAppliesTo,

                          duration: effectConcentration ? 'concentration' : (effectDuration ? parseInt(effectDuration) : null),

                          autoApply: effectAutoApply,

                          casterId: effectCaster?.id,

                          casterName: effectCaster?.name,

                          roundApplied: combat.round,

                          dotDamage: effectDotDamage || null,

                          dotDamageType: effectDotDamage ? effectDotDamageType : null,

                          hotHeal: effectHotHeal || null,

                          hotTempHp: effectHotTempHp ? parseInt(effectHotTempHp) : null,

                          tickTiming: effectTickTiming,

                          saveType: effectSaveType || null,

                          saveDC: effectSaveDC ? parseInt(effectSaveDC) : null,

                          saveTiming: effectSaveTiming,

                          saveRemovesEffect: effectSaveRemoves,

                        };

                        

                        // Apply to each target's character data and add condition to combatant

                        const updatedChars = { ...chars };

                        let updatedCombatants = [...combatants];

                        

                        targets.forEach(targetId => {

                          if (updatedChars[targetId]) {

                            updatedChars[targetId] = {

                              ...updatedChars[targetId],

                              activeEffects: [...(updatedChars[targetId].activeEffects || []), { ...newEffect }],

                            };

                          }

                          // Also add as condition on combatant for visibility

                          updatedCombatants = updatedCombatants.map(c => {

                            if (c.id === targetId) {

                              const existing = c.conditions || [];

                              if (!existing.includes(effectName)) {

                                return { ...c, conditions: [...existing, effectName] };

                              }

                            }

                            return c;

                          });

                        });

                        

                        onUpdateChars(updatedChars);

                        

                        // Log it

                        const targetNames = targets.map(id => combatants.find(c => c.id === id)?.name || chars[id]?.name).filter(Boolean).join(', ');

                        const logEntry = {

                          timestamp: new Date().toISOString(),

                          source: currentChar?.name || 'Effect',

                          target: targetNames,

                          type: 'buff',

                          effect: `${effectName}${effectModifier ? ` (${effectModifier})` : ''}`,

                        };

                        onUpdateCombat({

                          ...combat,

                          combatants: updatedCombatants,

                          damageLog: [logEntry, ...(combat.damageLog || [])].slice(0, 50),

                        });

                      } else {

                        // Add debuff to selected targets

                        const effectCaster = castChar || currentChar;

                        const newDebuff = {

                          id: effectId,

                          name: effectName,

                          bonusDice: parsed?.type === 'dice' ? (parsed.isNegative ? `-${parsed.dice}` : parsed.dice) : null,

                          percentIncrease: parsed?.type === 'percent' ? parsed.value : null,

                          flatBonus: parsed?.type === 'flat' ? parsed.value : null,

                          duration: effectConcentration ? 'concentration' : (effectDuration ? parseInt(effectDuration) : null),

                          autoApply: effectAutoApply,

                          casterId: effectCaster?.id,

                          casterName: effectCaster?.name,

                          casterOnly: false, // Debuffs apply from any attacker

                          roundApplied: combat.round,

                          dotDamage: effectDotDamage || null,

                          dotDamageType: effectDotDamage ? effectDotDamageType : null,

                          hotHeal: effectHotHeal || null,

                          hotTempHp: effectHotTempHp ? parseInt(effectHotTempHp) : null,

                          tickTiming: effectTickTiming,

                          saveType: effectSaveType || null,

                          saveDC: effectSaveDC ? parseInt(effectSaveDC) : null,

                          saveTiming: effectSaveTiming,

                          saveRemovesEffect: effectSaveRemoves,

                        };

                        

                        const updatedCombatants = combatants.map(c => {

                          if (effectTargets.includes(c.id)) {

                            return {

                              ...c,

                              debuffs: [...(c.debuffs || []), newDebuff],

                              conditions: [...(c.conditions || []), effectName],

                            };

                          }

                          return c;

                        });

                        

                        // Log it

                        const targetNames = effectTargets.map(id => combatants.find(c => c.id === id)?.name).filter(Boolean).join(', ');

                        const logEntry = {

                          timestamp: new Date().toISOString(),

                          source: currentChar?.name || 'Effect',

                          target: targetNames,

                          type: 'debuff',

                          effect: `${effectName}${effectModifier ? ` (${effectModifier})` : ''}`,

                        };

                        

                        onUpdateCombat({

                          ...combat,

                          combatants: updatedCombatants,

                          damageLog: [logEntry, ...(combat.damageLog || [])].slice(0, 50),

                        });

                      }

                      

                      // Reset and close

                      setShowQuickEffect(false);

                      setEffectName('');

                      setEffectModifier('');

                      setEffectTargets([]);

                      setEffectAppliesTo('any');

                      setEffectDuration('');

                      setEffectConcentration(false);

                      setEffectDotDamage('');

                      setEffectDotDamageType('fire');

                      setEffectHotHeal('');

                      setEffectHotTempHp('');

                      setEffectTickTiming('start_of_target_turn');

                      setEffectSaveType('');

                      setEffectSaveDC('');

                      setEffectSaveTiming('end_of_target_turn');

                      setEffectSaveRemoves(true);

                    }}

                  >

                    Apply Effect

                  </button>

                  <button style={S.btn} onClick={() => setShowQuickEffect(false)}>Cancel</button>

                </div>

              </div>

            </div>

          )}

        </div>

      )}

      

      {/* Add Enemy Modal */}

      {showAddEnemy && (

        <div style={S.modal} onClick={() => setShowAddEnemy(false)}>

          <div style={{ ...S.modalContent, maxWidth: '500px', maxHeight: '85vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>

            <h3 style={{ color: '#ffd700', marginBottom: '16px' }}>👹 Add Enemy</h3>

            

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>

              <div style={{ gridColumn: 'span 2' }}>

                <label style={S.label}>Name</label>

                <input style={S.input} value={newEnemy.name} onChange={e => setNewEnemy({ ...newEnemy, name: e.target.value })} placeholder="Enemy name..." />

              </div>

              <div>

                <label style={S.label}>Max HP</label>

                <input style={S.input} type="number" value={newEnemy.maxHp} onChange={e => setNewEnemy({ ...newEnemy, maxHp: parseInt(e.target.value) || 1 })} />

              </div>

              <div>

                <label style={S.label}>AC</label>

                <input style={S.input} type="number" value={newEnemy.ac} onChange={e => setNewEnemy({ ...newEnemy, ac: parseInt(e.target.value) || 10 })} />

              </div>

              <div>

                <label style={S.label}>Initiative (default: 20)</label>

                <input style={S.input} type="number" value={newEnemy.initiative} onChange={e => setNewEnemy({ ...newEnemy, initiative: parseInt(e.target.value) || 20 })} />

              </div>

              <div>

                <label style={S.label}>🌀 Raid Resist %</label>

                <input style={S.input} type="number" value={newEnemy.raidwideResist || 0} onChange={e => setNewEnemy({ ...newEnemy, raidwideResist: parseInt(e.target.value) || 0 })} min="0" max="100" />

              </div>

            </div>

            

            <div style={{ marginTop: '16px' }}>

              <ResistanceEditor 

                label="Resistances" 

                icon="🛡️" 

                items={newEnemy.resistances || []} 

                onChange={(val) => setNewEnemy({ ...newEnemy, resistances: val })}

              />

              <ResistanceEditor 

                label="Vulnerabilities" 

                icon="⚡" 

                items={newEnemy.vulnerabilities || []} 

                onChange={(val) => setNewEnemy({ ...newEnemy, vulnerabilities: val })}

              />

              <div>

                <label style={{ ...S.label, fontSize: '0.8rem' }}>🚫 Immunities (comma-separated)</label>

                <input 

                  style={S.input} 

                  value={(newEnemy.immunities || []).join(', ')} 

                  onChange={e => setNewEnemy({ ...newEnemy, immunities: e.target.value.split(',').map(s => s.trim().toLowerCase()).filter(Boolean) })} 

                  placeholder="poison, necrotic..." 

                />

              </div>

            </div>

            

            {/* Attack Routines */}

            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>

              <label style={{ ...S.label, color: '#c92727', fontSize: '0.8rem' }}>⚔️ Attack Routines</label>

              {(newEnemy.attackRoutines || []).map((routine, rIdx) => (

                <div key={rIdx} style={{ marginBottom: '8px', padding: '6px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px' }}>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>

                    <input style={{ ...S.input, flex: 1, marginBottom: 0, fontSize: '0.75rem', padding: '3px 6px' }} value={routine.name} placeholder="Routine name"

                      onChange={e => { const r = [...newEnemy.attackRoutines]; r[rIdx] = { ...routine, name: e.target.value }; setNewEnemy({ ...newEnemy, attackRoutines: r }); }} />

                    <button style={{ ...S.btn, ...S.btnXs, color: '#ff6b6b', marginBottom: 0, fontSize: '0.6rem' }} onClick={() => setNewEnemy({ ...newEnemy, attackRoutines: newEnemy.attackRoutines.filter((_, i) => i !== rIdx) })}>🗑</button>

                  </div>

                  {(routine.attacks || []).map((atk, aIdx) => (

                    <div key={aIdx} style={{ display: 'flex', gap: '3px', alignItems: 'center', marginBottom: '3px', fontSize: '0.7rem' }}>

                      <input style={{ ...S.input, width: '70px', marginBottom: 0, fontSize: '0.7rem', padding: '2px 4px' }} value={atk.name} placeholder="Name" onChange={e => {

                        const r = [...newEnemy.attackRoutines]; const a = [...routine.attacks]; a[aIdx] = { ...atk, name: e.target.value }; r[rIdx] = { ...routine, attacks: a }; setNewEnemy({ ...newEnemy, attackRoutines: r });

                      }} />

                      <input style={{ ...S.input, width: '28px', marginBottom: 0, fontSize: '0.7rem', padding: '2px 3px', textAlign: 'center' }} type="number" value={atk.count || 1} min={1} onChange={e => {

                        const r = [...newEnemy.attackRoutines]; const a = [...routine.attacks]; a[aIdx] = { ...atk, count: parseInt(e.target.value) || 1 }; r[rIdx] = { ...routine, attacks: a }; setNewEnemy({ ...newEnemy, attackRoutines: r });

                      }} title="Count" />

                      <select

                        aria-label={`Attack type for ${atk.name || 'attack'}`}

                        style={{ ...S.input, width: '62px', marginBottom: 0, fontSize: '0.65rem', padding: '2px 3px' }}

                        value={normalizeAttackRoutineAttackType(atk.attackType)}

                        onChange={e => {

                          const r = [...newEnemy.attackRoutines]; const a = [...routine.attacks]; a[aIdx] = { ...atk, attackType: e.target.value }; r[rIdx] = { ...routine, attacks: a }; setNewEnemy({ ...newEnemy, attackRoutines: r });

                        }}

                        title="Attack type for item melee/ranged bonuses"

                      >

                        {ATTACK_ROUTINE_ATTACK_TYPES.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}

                      </select>

                      <span style={{ color: '#888', fontSize: '0.6rem' }}>× +</span>

                      <input style={{ ...S.input, width: '30px', marginBottom: 0, fontSize: '0.7rem', padding: '2px 3px', textAlign: 'center' }} type="number" value={atk.toHitBonus || 0} onChange={e => {

                        const r = [...newEnemy.attackRoutines]; const a = [...routine.attacks]; a[aIdx] = { ...atk, toHitBonus: parseInt(e.target.value) || 0 }; r[rIdx] = { ...routine, attacks: a }; setNewEnemy({ ...newEnemy, attackRoutines: r });

                      }} title="Hit bonus" />

                      <input style={{ ...S.input, width: '65px', marginBottom: 0, fontSize: '0.7rem', padding: '2px 4px' }} value={atk.damageDice || ''} placeholder="1d8+3" onChange={e => {

                        const r = [...newEnemy.attackRoutines]; const a = [...routine.attacks]; a[aIdx] = { ...atk, damageDice: e.target.value }; r[rIdx] = { ...routine, attacks: a }; setNewEnemy({ ...newEnemy, attackRoutines: r });

                      }} title="Damage" />

                      <select style={{ ...S.input, width: '65px', marginBottom: 0, fontSize: '0.65rem', padding: '2px' }} value={atk.damageType || 'slashing'} onChange={e => {

                        const r = [...newEnemy.attackRoutines]; const a = [...routine.attacks]; a[aIdx] = { ...atk, damageType: e.target.value }; r[rIdx] = { ...routine, attacks: a }; setNewEnemy({ ...newEnemy, attackRoutines: r });

                      }}>

                        {Object.keys(DAMAGE_TYPES).map(dt => <option key={dt} value={dt}>{dt}</option>)}

                      </select>

                      <button style={{ background: 'none', border: 'none', color: '#ff6b6b', cursor: 'pointer', fontSize: '0.7rem', padding: '0 2px' }} onClick={() => {

                        const r = [...newEnemy.attackRoutines]; const a = routine.attacks.filter((_, i) => i !== aIdx); r[rIdx] = { ...routine, attacks: a }; setNewEnemy({ ...newEnemy, attackRoutines: r });

                      }}>✕</button>

                    </div>

                  ))}

                  <button style={{ ...S.btn, ...S.btnXs, fontSize: '0.6rem', marginBottom: 0, marginTop: '2px' }} onClick={() => {

                    const r = [...newEnemy.attackRoutines]; r[rIdx] = { ...routine, attacks: [...routine.attacks, { id: generateId(), name: 'Attack', count: 1, attackType: 'generic', toHitBonus: 4, damageDice: '1d8+2', damageType: 'slashing' }] }; setNewEnemy({ ...newEnemy, attackRoutines: r });

                  }}>+ Attack</button>

                </div>

              ))}

              <button style={{ ...S.btn, ...S.btnXs, fontSize: '0.65rem' }} onClick={() => setNewEnemy({ ...newEnemy, attackRoutines: [...(newEnemy.attackRoutines || []), { id: generateId(), name: 'Multiattack', attacks: [{ id: generateId(), name: 'Attack', count: 1, attackType: 'generic', toHitBonus: 4, damageDice: '1d8+2', damageType: 'slashing' }] }] })}>+ Add Routine</button>

            </div>

            

            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>

              <button style={{ ...S.btn, ...S.btnSuccess, flex: 1 }} onClick={addEnemy}>Add Enemy</button>

              <button style={S.btn} onClick={() => setShowAddEnemy(false)}>Cancel</button>

            </div>

            

            {/* Quick Add Test Enemies */}

            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>

              <label style={{ ...S.label, color: '#c9a227' }}>🧪 Quick Add (Test Mode)</label>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>

                <button 

                  style={{ ...S.btn, ...S.btnSm }}

                  onClick={() => { addTestEnemy(); setShowAddEnemy(false); }}

                >

                  👹 Goblin (CR 1/4)

                </button>

                <button 

                  style={{ ...S.btn, ...S.btnSm }}

                  onClick={() => { addTestBoss(); setShowAddEnemy(false); }}

                >

                  💀 Orc Warchief (CR 4)

                </button>

              </div>

            </div>

          </div>

        </div>

      )}

      

      {/* Confirmation Dialog Modal */}

      {pendingConfirm && (

        <div style={S.modal} onClick={() => setPendingConfirm(null)}>

          <div style={{ ...S.modalContent, maxWidth: '400px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>

            <h3 style={{ color: '#ffd700', marginBottom: '12px' }}>{pendingConfirm.title}</h3>

            <p style={{ color: '#ddd', marginBottom: '8px', fontSize: '0.9rem' }}>{pendingConfirm.message}</p>

            {pendingConfirm.details && <p style={{ color: '#888', fontSize: '0.8rem', marginBottom: '16px' }}>{pendingConfirm.details}</p>}

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>

              {pendingConfirm.actions.map((action, i) => (

                <button 

                  key={i} 

                  style={{ 

                    ...S.btn, 

                    padding: '8px 20px', 

                    background: `${action.color}30`, 

                    border: `1px solid ${action.color}`, 

                    color: action.color === '#666' ? '#aaa' : action.color,

                    fontWeight: 'bold',

                  }}

                  onClick={action.onClick}

                >{action.label}</button>

              ))}

            </div>

          </div>

        </div>

      )}

      

      {/* Damage Log Modal */}

      {showDamageLog && (

        <div style={S.modal} onClick={() => setShowDamageLog(false)}>

          <div style={{ ...S.modalContent, maxWidth: '500px', maxHeight: '70vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>

            <h3 style={{ color: '#ffd700', marginBottom: '16px', flexShrink: 0 }}>📜 Damage Log</h3>

            

            <div style={{ flex: 1, overflowY: 'auto' }}>

              {damageLog.length === 0 ? (

                <p style={{ color: '#888', textAlign: 'center' }}>No combat log entries yet.</p>

              ) : (

                damageLog.map((entry, i) => (

                  <div 

                    key={i} 

                    style={{ 

                      padding: '8px', 

                      background: 'rgba(0,0,0,0.2)', 

                      borderRadius: '4px', 

                      marginBottom: '8px',

                      borderLeft: `3px solid ${entry.isDeathSave ? '#c9a227' : entry.isHealCheck ? '#ff9800' : entry.isTempHp ? '#87ceeb' : entry.isHealing ? '#27c95a' : entry.type === 'buff' ? '#4a90d9' : entry.type === 'mark' || entry.type === 'debuff' ? '#9b59b6' : entry.type === 'concentration' ? '#ff9800' : DAMAGE_TYPES[entry.type]?.color || '#888'}`

                    }}

                  >

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                      <span>

                        <strong>{entry.source}</strong> → <strong>{entry.target}</strong>

                        {entry.spellName && <span style={{ color: '#4a90d9', fontSize: '0.8rem' }}> ({entry.spellName})</span>}

                        {entry.isRaidwide && <span style={{ color: '#ff00ff', fontSize: '0.8rem' }}> (Raid)</span>}

                      </span>

                      {entry.isDeathSave ? (

                        <span style={{ 

                          color: entry.deathSaveResult === 'dead' ? '#880000' : entry.deathSaveResult === 'revived' || entry.deathSaveResult === 'stabilized' ? '#27c95a' : entry.deathSaveResult === 'success' ? '#c9a227' : '#c92727',

                          fontWeight: 'bold' 

                        }}>

                          🎲 {entry.amount} — {entry.deathSaveResult === 'dead' ? '☠️ Dead' : entry.deathSaveResult === 'revived' ? '🎉 Revived!' : entry.deathSaveResult === 'stabilized' ? '🛡️ Stabilized' : entry.deathSaveResult === 'success' ? '✓ Success' : '✕ Failure'}

                        </span>

                      ) : entry.isHealCheck ? (

                        <span style={{ color: entry.shieldBroken ? '#27c95a' : '#ff9800', fontWeight: 'bold' }}>

                          {entry.shieldBroken ? '🔓 Shield Broken!' : `🔒 ${entry.amount} vs Shield`}

                        </span>

                      ) : entry.isHealing ? (

                        <span style={{ color: '#27c95a', fontWeight: 'bold' }}>

                          ➕ +{entry.amount} HP

                        </span>

                      ) : entry.isTempHp ? (

                        <span style={{ color: '#87ceeb', fontWeight: 'bold' }}>

                          🩵 +{entry.amount} Temp HP

                        </span>

                      ) : entry.type === 'concentration' ? (

                        <span style={{ color: '#ff9800', fontWeight: 'bold', fontSize: '0.85rem' }}>

                          ⚡ {entry.effect}

                        </span>

                      ) : entry.type === 'buff' ? (

                        <span style={{ color: '#4a90d9', fontWeight: 'bold' }}>

                          ✨ {entry.effect}

                        </span>

                      ) : entry.type === 'mark' || entry.type === 'debuff' ? (

                        <span style={{ color: '#9b59b6', fontWeight: 'bold' }}>

                          {entry.type === 'debuff' ? '💀' : '🎯'} {entry.effect}

                          {entry.casterOnly && <span style={{ fontSize: '0.7rem', opacity: 0.7 }}> (caster only)</span>}

                        </span>

                      ) : (

                        <span style={{ color: '#c92727', fontWeight: 'bold', fontSize: entry.wasCrit ? '1.1rem' : '1rem' }}>

                          {entry.wasCrit && '💥'} -{entry.amount} {DAMAGE_TYPES[entry.type]?.icon}

                          {entry.tempHpAbsorbed > 0 && <span style={{ color: '#87ceeb', fontSize: '0.75rem', fontWeight: 'normal' }}> (🩵{entry.tempHpAbsorbed} absorbed)</span>}

                        </span>

                      )}

                    </div>

                    

                    {/* Damage Breakdown with Tooltips */}

                    {entry.breakdown && entry.breakdown.length > 0 && (

                      <div style={{ fontSize: '0.75rem', marginTop: '6px', padding: '6px 8px', background: 'rgba(0,0,0,0.3)', borderRadius: '4px' }}>

                        <div style={{ color: '#c9a227', marginBottom: '4px', fontWeight: 'bold' }}>Breakdown:</div>

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>

                          {entry.breakdown.map((step, idx) => {

                            const isBaseDice = step.type === 'base_dice';

                            const isBase = step.type === 'base';

                            const isMod = step.type === 'modifier';

                            const isMarkDice = step.type === 'mark_dice';

                            const isDebuffDice = step.type === 'debuff_dice';

                            const isDebuffPercent = step.type === 'debuff_percent';

                            const isBuff = step.type === 'buff';

                            const isBuffPercent = step.type === 'buff_percent';

                            const isResistant = step.type === 'resistant';

                            const isVulnerable = step.type === 'vulnerable';

                            const isImmune = step.type === 'immune';

                            

                            // Color coding

                            let color = '#e8d5b7'; // default/base

                            if (isMarkDice) color = '#9b59b6'; // purple for marks

                            else if (isDebuffDice || isDebuffPercent) color = '#e74c3c'; // red-orange for debuffs

                            else if (isBuff || isBuffPercent) color = '#27c95a'; // green for buffs

                            else if (isMod) color = '#4a90d9'; // blue for modifier

                            else if (isResistant) color = '#3498db'; // blue for resistance

                            else if (isVulnerable) color = '#e74c3c'; // red for vulnerable

                            else if (isImmune) color = '#888'; // gray for immune

                            else if (step.rawValue > 0) color = '#27c95a'; // green for positive

                            else if (step.rawValue < 0) color = '#c92727'; // red for negative

                            

                            // Build display text and tooltip

                            let displayText = '';

                            let rollDetail = '';

                            

                            if (isBaseDice && step.roll) {

                              const diceRolls = step.roll.rolls?.flatMap(r => r.rolls) || [];

                              displayText = `[${diceRolls.join('+')}]`;

                              rollDetail = `${step.source}\n${step.value}\nRolled: [${diceRolls.join(', ')}] = ${step.rawValue || diceRolls.reduce((a,b)=>a+b,0)}`;

                            } else if (isMod) {

                              displayText = `[+${step.rawValue}]`;

                              rollDetail = `Spellcasting Modifier\n+${step.rawValue}`;

                            } else if (isMarkDice && step.roll) {

                              const diceRolls = step.roll.rolls?.flatMap(r => r.rolls) || [];

                              displayText = `[${diceRolls.join('+')}]`;

                              rollDetail = `${step.source}\n${step.value}\nRolled: [${diceRolls.join(', ')}] = ${step.rawValue}`;

                            } else if (isDebuffDice && step.roll) {

                              const diceRolls = step.roll.rolls?.flatMap(r => r.rolls) || [];

                              displayText = `[${diceRolls.join('+')}]`;

                              rollDetail = `${step.source}\n${step.value}\nRolled: [${diceRolls.join(', ')}] = ${step.rawValue}`;

                            } else if (isBuff && step.roll) {

                              const diceRolls = step.roll.rolls?.flatMap(r => r.rolls) || [];

                              displayText = `[${diceRolls.join('+')}]`;

                              rollDetail = `${step.source} (Buff)\n${step.value}\nRolled: [${diceRolls.join(', ')}] = ${step.rawValue}`;

                            } else if (isBuff) {

                              displayText = `[${step.rawValue >= 0 ? '+' : ''}${step.rawValue}]`;

                              rollDetail = `${step.source} (Buff)\n+${step.rawValue}`;

                            } else if (isBuffPercent) {

                              displayText = `[${step.rawValue >= 0 ? '+' : ''}${step.rawValue}]`;

                              rollDetail = `${step.source} (Buff)\n${step.value} damage increase`;

                            } else if (isDebuffPercent) {

                              displayText = `[${step.rawValue >= 0 ? '+' : ''}${step.rawValue}]`;

                              rollDetail = `${step.source}\n${step.value} damage increase`;

                            } else if (isResistant) {

                              displayText = `[${step.rawValue}]`;

                              rollDetail = `${step.source}\nReduced by resistance`;

                            } else if (isVulnerable) {

                              displayText = `[+${Math.abs(step.rawValue)}]`;

                              rollDetail = `${step.source}\nIncreased by vulnerability`;

                            } else if (isImmune) {

                              displayText = '[IMMUNE]';

                              rollDetail = `${step.source}\nImmune to this damage type`;

                            } else if (isBase) {

                              displayText = `[${step.value}]`;

                              rollDetail = step.source || 'Base damage';

                            } else {

                              displayText = typeof step.value === 'number' ? `[${step.value}]` : `[${step.value}]`;

                              rollDetail = step.source || 'Unknown';

                            }

                            

                            return (

                              <React.Fragment key={idx}>

                                {idx > 0 && <span style={{ color: '#666', fontSize: '0.9rem' }}>+</span>}

                                <Tooltip text={rollDetail}>

                                  <span 

                                    style={{ 

                                      padding: '2px 6px', 

                                      background: `${color}20`, 

                                      border: `1px solid ${color}50`,

                                      borderRadius: '4px',

                                      color,

                                      cursor: 'help',

                                      fontFamily: 'monospace',

                                      fontWeight: 'bold',

                                    }}

                                  >

                                    {displayText}

                                  </span>

                                </Tooltip>

                              </React.Fragment>

                            );

                          })}

                          <span style={{ color: '#666', fontSize: '0.9rem' }}>=</span>

                          <span style={{ 

                            color: '#ffd700', 

                            fontWeight: 'bold', 

                            fontSize: '0.9rem',

                            padding: '2px 6px',

                            background: 'rgba(255,215,0,0.15)',

                            borderRadius: '4px',

                          }}>

                            {entry.amount}

                          </span>

                        </div>

                        {/* Legend for colors */}

                        <div style={{ marginTop: '6px', display: 'flex', gap: '8px', flexWrap: 'wrap', fontSize: '0.65rem', opacity: 0.7 }}>

                          <span style={{ color: '#e8d5b7' }}>■ Base</span>

                          <span style={{ color: '#4a90d9' }}>■ Mod</span>

                          <span style={{ color: '#27c95a' }}>■ Buff</span>

                          <span style={{ color: '#9b59b6' }}>■ Mark</span>

                          <span style={{ color: '#e74c3c' }}>■ Debuff</span>

                          <span style={{ color: '#3498db' }}>■ Resist</span>

                        </div>

                      </div>

                    )}

                    

                    {/* Simple modifier display for non-breakdown entries */}

                    {(!entry.breakdown || entry.breakdown.length === 0) && entry.modifier && entry.modifier !== 'normal' && (

                      <div style={{ fontSize: '0.75rem', color: entry.modifier === 'resistant' ? '#4a90d9' : entry.modifier === 'vulnerable' ? '#c92727' : entry.modifier === 'raidResist' ? '#ff00ff' : '#888', marginTop: '4px' }}>

                        {entry.modifier === 'resistant' && `🛡️ Resistant ${entry.percent}% (${entry.originalAmount} → ${entry.amount})`}

                        {entry.modifier === 'vulnerable' && `⚡ Vulnerable +${entry.percent}% (${entry.originalAmount} → ${entry.amount})`}

                        {entry.modifier === 'raidResist' && `🌀 Raid Resist ${entry.percent}% (${entry.originalAmount} → ${entry.amount})`}

                        {entry.modifier === 'immune' && '🚫 Immune'}

                      </div>

                    )}

                    <div style={{ fontSize: '0.7rem', color: '#666', marginTop: '4px' }}>

                      {new Date(entry.timestamp).toLocaleTimeString()}

                    </div>

                  </div>

                ))

              )}

            </div>

            

            <button style={{ ...S.btn, marginTop: '16px', flexShrink: 0 }} onClick={() => setShowDamageLog(false)}>Close</button>

          </div>

        </div>

      )}

      

      {/* Active Effects Tracker Modal — Perspective-Based */}

      {showEffectsTracker && (

        <div style={S.modal} onClick={() => setShowEffectsTracker(false)}>

          <div style={{ ...S.modalContent, maxWidth: '720px', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>

            {(() => {

              // Determine whose perspective we're viewing from

              const viewerId = isDM ? (dmViewAsPlayerId || dmViewAsId || '') : currentCharId;

              const viewerChar = viewerId ? chars[viewerId] : null;

              const viewerCombatant = viewerId ? combatants.find(c => c.id === viewerId) : null;

              const viewerName = viewerChar?.name || viewerCombatant?.name || 'Overview';

              const allEffects = getAllActiveEffects();

              

              // Categorize effects by relevance to viewer

              const onMe = [];        // Effects directly on the viewer's character

              const myCasts = [];     // Effects the viewer maintains on others

              const onMyTargets = []; // Effects from OTHER casters on enemies (useful to know for damage)

              const other = [];       // Everything else

              

              if (viewerId) {

                allEffects.forEach(eff => {

                  const isOnMe = eff.targetId === viewerId;

                  const isMyCast = eff.casterId === viewerId;

                  const isOnEnemy = eff.targetType === 'enemy';

                  

                  if (isOnMe) {

                    onMe.push({ ...eff, _relation: isMyCast ? 'self' : 'from_other' });

                  } else if (isMyCast) {

                    myCasts.push(eff);

                  } else if (isOnEnemy && (eff.category === 'debuff' || eff.category === 'mark')) {

                    onMyTargets.push(eff);

                  } else {

                    other.push(eff);

                  }

                });

              } else {

                // No viewer — show all grouped by target

                other.push(...allEffects);

              }

              

              // Sort onMe: buffs first, then debuffs

              onMe.sort((a, b) => {

                const order = { buff: 0, concentration: 1, condition: 2, debuff: 3, mark: 4 };

                return (order[a.category] || 5) - (order[b.category] || 5);

              });

              

              // Helper: render a single effect row

              const renderEffectRow = (eff, size = 'normal', showTarget = false, showCaster = true) => {

                const catColors = {

                  buff: { bg: 'rgba(74,144,217,0.1)', border: '#4a90d9', icon: '✨', text: '#7db5ff' },

                  debuff: { bg: 'rgba(231,76,60,0.1)', border: '#e74c3c', icon: '💀', text: '#ff6b6b' },

                  condition: { bg: 'rgba(155,89,182,0.1)', border: '#9b59b6', icon: '🔮', text: '#c39bdb' },

                  concentration: { bg: 'rgba(255,152,0,0.1)', border: '#ff9800', icon: '⚡', text: '#ffb74d' },

                  mark: { bg: 'rgba(201,162,39,0.1)', border: '#c9a227', icon: '🎯', text: '#ffd700' },

                };

                const cat = catColors[eff.category] || catColors.debuff;

                

                // Border color based on relationship to viewer

                let borderColor = cat.border;

                if (viewerId) {

                  if (eff._relation === 'self' || eff.casterId === viewerId) borderColor = '#4a90d9'; // my cast

                  else if (eff._relation === 'from_other') {

                    const casterCombatant = combatants.find(c => c.id === eff.casterId);

                    borderColor = casterCombatant?.type === 'enemy' ? '#e74c3c' : '#27c95a'; // enemy vs ally

                  }

                }

                

                const durationText = eff.concentration ? '⚡' :

                  eff.remaining !== undefined && eff.remaining !== null ? `${eff.remaining}r` :

                  eff.duration ? `${eff.duration}r` : '∞';

                const isExpiringSoon = typeof eff.remaining === 'number' && eff.remaining <= 1 && eff.remaining >= 0;

                const isSmall = size === 'small';

                const hasSave = eff.saveType && eff.saveDC && eff.targetId === viewerId;

                const saveEffectRef = hasSave ? createCombatRuntimeEffectRef({

                  effectId: eff.effectId || eff.id || null,

                  effectGroupId: eff.effectGroupId || null,

                  sourceCollection: eff.sourceType === 'combat-debuff'

                    ? 'combatant.debuffs'

                    : eff.sourceType === 'active-effect'

                      ? 'char.activeEffects'

                      : null,

                  effectName: eff.name,

                  casterId: eff.casterId || null,

                }) : null;

                const saveTickKey = hasSave

                  ? getCombatTickKey(eff.targetId, 'save_only', saveEffectRef, eff.name)

                  : null;

                const saveQueued = saveTickKey

                  ? (lifecycleQueue || []).some(item => getQueueItemTickKey(item) === saveTickKey)

                  : false;

                

                return (

                  <div key={eff.id} style={{ 

                    padding: isSmall ? '3px 6px' : '5px 8px', 

                    marginBottom: '3px',

                    background: cat.bg, 

                    borderLeft: `3px solid ${borderColor}`,

                    borderRadius: '4px',

                    display: 'flex',

                    justifyContent: 'space-between',

                    alignItems: 'center',

                    gap: '6px',

                    opacity: isSmall ? 0.7 : 1,

                    fontSize: isSmall ? '0.68rem' : '0.78rem',

                  }}>

                    <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>

                      <span style={{ fontSize: isSmall ? '0.65rem' : '0.75rem' }}>{cat.icon}</span>

                      <strong style={{ color: cat.text, fontSize: isSmall ? '0.68rem' : '0.78rem' }}>{eff.name}</strong>

                      {showTarget && <span style={{ color: '#888', fontSize: isSmall ? '0.58rem' : '0.65rem' }}>→ {eff.targetName}</span>}

                      {showCaster && eff.casterName && eff.casterId !== viewerId && (

                        <span style={{ color: '#666', fontSize: isSmall ? '0.55rem' : '0.62rem' }}>({eff.casterName})</span>

                      )}

                      {eff.modifier && <span style={{ color: '#27c95a', fontFamily: 'monospace', fontSize: isSmall ? '0.58rem' : '0.65rem' }}>{eff.modifier}</span>}

                      {eff.bonusDice && <span style={{ color: '#ff9800', fontFamily: 'monospace', fontSize: isSmall ? '0.58rem' : '0.65rem' }}>{eff.bonusDice}</span>}

                      {eff.percentIncrease && <span style={{ color: '#ff9800', fontFamily: 'monospace', fontSize: isSmall ? '0.58rem' : '0.65rem' }}>{eff.percentIncrease > 0 ? '+' : ''}{eff.percentIncrease}%</span>}

                      {eff.dotDamage && <span style={{ color: '#ff4444', fontFamily: 'monospace', fontSize: isSmall ? '0.55rem' : '0.62rem' }}>🔥{eff.dotDamage}/t</span>}

                      {eff.hotHeal && <span style={{ color: '#27c95a', fontFamily: 'monospace', fontSize: isSmall ? '0.55rem' : '0.62rem' }}>➕{eff.hotHeal}/t</span>}

                      {eff.hotTempHp > 0 && <span style={{ color: '#87ceeb', fontFamily: 'monospace', fontSize: isSmall ? '0.55rem' : '0.62rem' }}>🩵{eff.hotTempHp}/t</span>}

                      {eff.saveType && <span style={{ color: '#c9a227', fontFamily: 'monospace', fontSize: isSmall ? '0.55rem' : '0.62rem' }}>🎲{(eff.saveType).toUpperCase()} DC{eff.saveDC}</span>}

                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>

                      {hasSave && (

                        <button

                          disabled={saveQueued}
                          style={{

                            padding: '2px 8px', fontSize: '0.65rem', fontWeight: 'bold', borderRadius: '3px',

                            border: `1px solid ${saveQueued ? '#555' : '#c9a227'}`, background: saveQueued ? 'rgba(80,80,80,0.15)' : 'rgba(201,162,39,0.2)', color: saveQueued ? '#666' : '#ffd700',

                            cursor: saveQueued ? 'default' : 'pointer', whiteSpace: 'nowrap',

                            opacity: saveQueued ? 0.5 : 1,

                          }}

                          onClick={() => {
                            if (saveQueued) return;
                            tickSave(eff.targetId, eff.name, eff.saveType, eff.saveDC, eff.saveRemovesEffect !== false, saveEffectRef);

                          }}

                        >

                          🎲 Save

                        </button>

                      )}

                      <span style={{ 

                        fontSize: isSmall ? '0.62rem' : '0.7rem', fontWeight: 'bold', whiteSpace: 'nowrap',

                        color: isExpiringSoon ? '#e74c3c' : eff.concentration ? '#ff9800' : '#777',

                        ...(isExpiringSoon ? { animation: 'concFlash 2s ease-in-out infinite' } : {}),

                      }}>

                        {durationText}

                      </span>

                    </div>

                  </div>

                );

              };

              

              // Section header component

              const SectionHeader = ({ icon, title, count, color, collapsed, onToggle }) => (

                <div 

                  style={{ 

                    display: 'flex', alignItems: 'center', gap: '8px', 

                    padding: '6px 0', cursor: 'pointer', userSelect: 'none',

                  }}

                  onClick={onToggle}

                >

                  <span style={{ color, fontSize: '0.85rem' }}>{icon}</span>

                  <span style={{ color, fontWeight: 'bold', fontSize: '0.82rem' }}>{title}</span>

                  <span style={{ 

                    padding: '1px 7px', borderRadius: '10px', fontSize: '0.65rem', fontWeight: 'bold',

                    background: `${color}25`, color, border: `1px solid ${color}40`,

                  }}>{count}</span>

                  <span style={{ color: '#555', fontSize: '0.7rem', marginLeft: 'auto' }}>{collapsed ? '▸' : '▾'}</span>

                </div>

              );

              

              return (

                <>

                  {/* Header */}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexShrink: 0 }}>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>

                      <h3 style={{ color: '#9b59b6', margin: 0 }}>✨ Effects</h3>

                      <span style={{ fontSize: '0.75rem', color: '#888' }}>Round {combat.round}</span>

                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

                      {/* View-As Selector */}

                      <select

                        value={viewerId || ''}

                        onChange={(e) => {

                          if (isDM) setDmViewAsId(e.target.value);

                        }}

                        disabled={!isDM}

                        style={{ ...S.select, marginBottom: 0, padding: '3px 8px', fontSize: '0.72rem', width: 'auto', maxWidth: '160px' }}

                      >

                        <option value="">🌐 All Effects</option>

                        {combatants.filter(c => c.type === 'player').map(c => (

                          <option key={c.id} value={c.id}>◉ {c.name}'s View</option>

                        ))}

                      </select>

                      <button style={{ ...S.btn, padding: '3px 8px', fontSize: '0.72rem' }} onClick={() => setShowEffectsTracker(false)}>✕</button>

                    </div>

                  </div>

                  

                  {/* Border color legend */}

                  {viewerId && (

                    <div style={{ display: 'flex', gap: '12px', marginBottom: '8px', fontSize: '0.62rem', color: '#666', flexShrink: 0 }}>

                      <span><span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#4a90d9', borderRadius: '2px', marginRight: '3px', verticalAlign: 'middle' }}></span>My cast</span>

                      <span><span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#27c95a', borderRadius: '2px', marginRight: '3px', verticalAlign: 'middle' }}></span>Ally</span>

                      <span><span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#e74c3c', borderRadius: '2px', marginRight: '3px', verticalAlign: 'middle' }}></span>Enemy</span>

                    </div>

                  )}

                  

                  {/* Main content */}

                  <div style={{ flex: 1, overflowY: 'auto' }}>

                    {viewerId ? (

                      <>

                        {/* SECTION 1: ON ME */}

                        {onMe.length > 0 && (

                          <div style={{ marginBottom: '12px' }}>

                            <SectionHeader icon="📍" title="On Me" count={onMe.length} color="#4a90d9" />

                            <div style={{ paddingLeft: '4px' }}>

                              {onMe.filter(e => e.category === 'buff' || e.category === 'concentration').length > 0 && (

                                <div style={{ marginBottom: '4px' }}>

                                  {onMe.filter(e => e.category === 'buff' || e.category === 'concentration').map(eff => renderEffectRow(eff, 'normal', false, true))}

                                </div>

                              )}

                              {onMe.filter(e => e.category === 'condition').length > 0 && (

                                <div style={{ marginBottom: '4px' }}>

                                  {onMe.filter(e => e.category === 'condition').map(eff => renderEffectRow(eff, 'normal', false, false))}

                                </div>

                              )}

                              {onMe.filter(e => e.category === 'debuff' || e.category === 'mark').length > 0 && (

                                <div>

                                  {onMe.filter(e => e.category === 'debuff' || e.category === 'mark').map(eff => renderEffectRow(eff, 'normal', false, true))}

                                </div>

                              )}

                            </div>

                          </div>

                        )}

                        

                        {/* SECTION 2: MY CASTS */}

                        {myCasts.length > 0 && (

                          <div style={{ marginBottom: '12px' }}>

                            <SectionHeader icon="🔮" title="My Casts" count={myCasts.length} color="#9b59b6" />

                            <div style={{ paddingLeft: '4px' }}>

                              {myCasts.map(eff => renderEffectRow(eff, 'normal', true, false))}

                            </div>

                          </div>

                        )}

                        

                        {/* SECTION 3: ON MY TARGETS (from other party members) */}

                        {onMyTargets.length > 0 && (

                          <div style={{ marginBottom: '12px' }}>

                            <SectionHeader icon="🎯" title="Party Effects on Enemies" count={onMyTargets.length} color="#c9a227" />

                            <div style={{ paddingLeft: '4px' }}>

                              {onMyTargets.map(eff => renderEffectRow(eff, 'normal', true, true))}

                            </div>

                          </div>

                        )}

                        

                        {/* SECTION 4: OTHER (not relevant to me) */}

                        {other.length > 0 && (

                          <div style={{ marginBottom: '8px' }}>

                            <SectionHeader icon="📋" title="Other Effects" count={other.length} color="#555" />

                            <div style={{ paddingLeft: '4px' }}>

                              {other.map(eff => renderEffectRow(eff, 'small', true, true))}

                            </div>

                          </div>

                        )}

                        

                        {allEffects.length === 0 && (

                          <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>No active effects.</p>

                        )}

                      </>

                    ) : (

                      /* OVERVIEW MODE (no viewer selected) — group by target */

                      (() => {

                        if (other.length === 0) {

                          return <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>No active effects.</p>;

                        }

                        const grouped = {};

                        other.forEach(eff => {

                          if (!grouped[eff.targetId]) grouped[eff.targetId] = { name: eff.targetName, type: eff.targetType, effects: [] };

                          grouped[eff.targetId].effects.push(eff);

                        });

                        

                        return Object.entries(grouped).map(([targetId, group]) => {

                          const isPlayerType = group.type === 'player' || group.type === 'npc';

                          const combatant2 = combatants.find(c => c.id === targetId);

                          const hpPercent = combatant2 ? (combatant2.currentHp / combatant2.maxHp) * 100 : 100;

                          

                          return (

                            <div key={targetId} style={{ 

                              marginBottom: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px',

                              border: `1px solid ${isPlayerType ? 'rgba(39,201,90,0.2)' : 'rgba(201,39,39,0.2)'}`,

                              overflow: 'hidden',

                            }}>

                              <div style={{ 

                                padding: '6px 10px', 

                                background: isPlayerType ? 'rgba(39,201,90,0.08)' : 'rgba(201,39,39,0.08)',

                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',

                                borderBottom: '1px solid rgba(255,255,255,0.05)',

                              }}>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

                                  <span>{group.type === 'npc' ? '🤝' : isPlayerType ? '👤' : '👹'}</span>

                                  <strong style={{ color: isPlayerType ? '#27c95a' : '#c92727', fontSize: '0.8rem' }}>{group.name}</strong>

                                  <span style={{ fontSize: '0.65rem', color: '#888' }}>({group.effects.length})</span>

                                </div>

                                {combatant2 && (

                                  <span style={{ fontSize: '0.7rem', fontWeight: 'bold', fontFamily: 'monospace',

                                    color: hpPercent > 50 ? '#27c95a' : hpPercent > 25 ? '#c9a227' : '#c92727',

                                  }}>

                                    {combatant2.currentHp}{(combatant2.tempHp || 0) > 0 && <span style={{ color: '#87ceeb' }}>+{combatant2.tempHp}</span>}/{combatant2.maxHp}

                                  </span>

                                )}

                                {isDM && (

                                  <button 

                                    style={{ ...S.btn, padding: '1px 6px', fontSize: '0.6rem' }}

                                    onClick={() => { if (isPlayerType) setDmViewAsId(targetId); }}

                                    title={`View from ${group.name}'s perspective`}

                                  >◉</button>

                                )}

                              </div>

                              <div style={{ padding: '4px 6px' }}>

                                {group.effects.map(eff => renderEffectRow(eff, 'normal', false, true))}

                              </div>

                            </div>

                          );

                        });

                      })()

                    )}

                  </div>

                  

                  {/* Footer summary */}

                  <div style={{ 

                    paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: '6px', flexShrink: 0,

                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px',

                  }}>

                    <div style={{ display: 'flex', gap: '10px', fontSize: '0.68rem', color: '#888' }}>

                      {viewerId ? (

                        <>

                          {onMe.length > 0 && <span style={{ color: '#4a90d9' }}>📍{onMe.length} on me</span>}

                          {myCasts.length > 0 && <span style={{ color: '#9b59b6' }}>🔮{myCasts.length} my casts</span>}

                          {onMyTargets.length > 0 && <span style={{ color: '#c9a227' }}>🎯{onMyTargets.length} on targets</span>}

                          {other.length > 0 && <span style={{ color: '#555' }}>📋{other.length} other</span>}

                        </>

                      ) : (

                        (() => {

                          const counts = { buff: 0, debuff: 0, condition: 0, concentration: 0, mark: 0 };

                          allEffects.forEach(e => { if (counts[e.category] !== undefined) counts[e.category]++; });

                          return (

                            <>

                              {counts.buff > 0 && <span style={{ color: '#4a90d9' }}>✨{counts.buff}</span>}

                              {counts.debuff > 0 && <span style={{ color: '#e74c3c' }}>💀{counts.debuff}</span>}

                              {counts.mark > 0 && <span style={{ color: '#c9a227' }}>🎯{counts.mark}</span>}

                              {counts.concentration > 0 && <span style={{ color: '#ff9800' }}>⚡{counts.concentration}</span>}

                              {counts.condition > 0 && <span style={{ color: '#9b59b6' }}>🔮{counts.condition}</span>}

                            </>

                          );

                        })()

                      )}

                    </div>

                    <button style={{ ...S.btn, flexShrink: 0, padding: '4px 12px' }} onClick={() => setShowEffectsTracker(false)}>Close</button>

                  </div>

                </>

              );

            })()}

          </div>

        </div>

      )}

      

      

      {/* Death Save Config Modal */}

      {showDeathSaveConfig && (

        <div style={S.modal} onClick={() => setShowDeathSaveConfig(false)}>

          <div style={{ ...S.modalContent, maxWidth: '550px', maxHeight: '80vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>

            <h3 style={{ color: '#c9a227', marginBottom: '16px' }}>⚙️ Death Save Rules</h3>

            

            {/* Core Settings */}

            <div style={{ marginBottom: '16px' }}>

              <h4 style={{ color: '#e8d5b7', fontSize: '0.85rem', marginBottom: '8px' }}>Core Rules</h4>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>

                <div>

                  <label style={{ ...S.label, fontSize: '0.72rem' }}>Successes Needed</label>

                  <input type="number" min="1" max="10" value={deathSaveConfig.successesNeeded}

                    onChange={e => setDeathSaveConfig(prev => ({ ...prev, successesNeeded: Math.max(1, parseInt(e.target.value) || 3) }))}

                    style={{ ...S.input, padding: '4px 8px', fontSize: '0.8rem' }} />

                </div>

                <div>

                  <label style={{ ...S.label, fontSize: '0.72rem' }}>Failures Needed</label>

                  <input type="number" min="1" max="10" value={deathSaveConfig.failuresNeeded}

                    onChange={e => setDeathSaveConfig(prev => ({ ...prev, failuresNeeded: Math.max(1, parseInt(e.target.value) || 3) }))}

                    style={{ ...S.input, padding: '4px 8px', fontSize: '0.8rem' }} />

                </div>

              </div>

              <label style={{ fontSize: '0.72rem', color: '#888', display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', cursor: 'pointer' }}>

                <input type="checkbox" checked={deathSaveConfig.autoApplyConditions}

                  onChange={e => setDeathSaveConfig(prev => ({ ...prev, autoApplyConditions: e.target.checked }))} />

                Auto-apply Unconscious + Prone at 0 HP

              </label>

              <label style={{ fontSize: '0.72rem', color: '#888', display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', cursor: 'pointer' }}>

                <input type="checkbox" checked={deathSaveConfig.concentrationCheckOnDamage !== false}

                  onChange={e => setDeathSaveConfig(prev => ({ ...prev, concentrationCheckOnDamage: e.target.checked }))} />

                Auto-roll concentration save on damage

              </label>

            </div>

            

            {/* Nat 20 / Nat 1 */}

            <div style={{ marginBottom: '16px' }}>

              <h4 style={{ color: '#e8d5b7', fontSize: '0.85rem', marginBottom: '8px' }}>Critical Rolls</h4>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>

                <div>

                  <label style={{ ...S.label, fontSize: '0.72rem' }}>Natural 20</label>

                  <select value={deathSaveConfig.nat20Behavior}

                    onChange={e => setDeathSaveConfig(prev => ({ ...prev, nat20Behavior: e.target.value }))}

                    style={{ ...S.select, padding: '4px', fontSize: '0.8rem' }}>

                    <option value="revive">Revive with HP</option>

                    <option value="two_successes">2 Successes</option>

                    <option value="one_success">1 Success</option>

                  </select>

                </div>

                <div>

                  <label style={{ ...S.label, fontSize: '0.72rem' }}>Natural 1</label>

                  <select value={deathSaveConfig.nat1Behavior}

                    onChange={e => setDeathSaveConfig(prev => ({ ...prev, nat1Behavior: e.target.value }))}

                    style={{ ...S.select, padding: '4px', fontSize: '0.8rem' }}>

                    <option value="two_failures">2 Failures</option>

                    <option value="one_failure">1 Failure</option>

                  </select>

                </div>

              </div>

              {deathSaveConfig.nat20Behavior === 'revive' && (

                <div style={{ marginTop: '8px' }}>

                  <label style={{ ...S.label, fontSize: '0.72rem' }}>Nat 20 Revive HP</label>

                  <input type="number" min="1" value={deathSaveConfig.nat20ReviveHp}

                    onChange={e => setDeathSaveConfig(prev => ({ ...prev, nat20ReviveHp: Math.max(1, parseInt(e.target.value) || 1) }))}

                    style={{ ...S.input, padding: '4px 8px', fontSize: '0.8rem', width: '80px' }} />

                </div>

              )}

            </div>

            

            {/* Damage at 0 HP */}

            <div style={{ marginBottom: '16px' }}>

              <h4 style={{ color: '#e8d5b7', fontSize: '0.85rem', marginBottom: '8px' }}>Damage at 0 HP</h4>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>

                <div>

                  <label style={{ ...S.label, fontSize: '0.72rem' }}>Failures per Hit</label>

                  <input type="number" min="1" max="5" value={deathSaveConfig.damageAtZeroFailures}

                    onChange={e => setDeathSaveConfig(prev => ({ ...prev, damageAtZeroFailures: Math.max(1, parseInt(e.target.value) || 1) }))}

                    style={{ ...S.input, padding: '4px 8px', fontSize: '0.8rem' }} />

                </div>

                <div>

                  <label style={{ ...S.label, fontSize: '0.72rem' }}>Failures per Crit</label>

                  <input type="number" min="1" max="5" value={deathSaveConfig.critAtZeroFailures}

                    onChange={e => setDeathSaveConfig(prev => ({ ...prev, critAtZeroFailures: Math.max(1, parseInt(e.target.value) || 2) }))}

                    style={{ ...S.input, padding: '4px 8px', fontSize: '0.8rem' }} />

                </div>

              </div>

            </div>

            

            {/* Revival Rules */}

            <div style={{ marginBottom: '16px' }}>

              <h4 style={{ color: '#e8d5b7', fontSize: '0.85rem', marginBottom: '8px' }}>Revival</h4>

              <div>

                <label style={{ ...S.label, fontSize: '0.72rem' }}>Revive HP Mode</label>

                <select value={deathSaveConfig.reviveHpMode}

                  onChange={e => setDeathSaveConfig(prev => ({ ...prev, reviveHpMode: e.target.value }))}

                  style={{ ...S.select, padding: '4px', fontSize: '0.8rem' }}>

                  <option value="heal_amount">Use Heal Amount</option>

                  <option value="fixed">Fixed HP</option>

                </select>

              </div>

              {deathSaveConfig.reviveHpMode === 'fixed' && (

                <div style={{ marginTop: '8px' }}>

                  <label style={{ ...S.label, fontSize: '0.72rem' }}>Fixed Revive HP</label>

                  <input type="number" min="1" value={deathSaveConfig.fixedReviveHp}

                    onChange={e => setDeathSaveConfig(prev => ({ ...prev, fixedReviveHp: Math.max(1, parseInt(e.target.value) || 15) }))}

                    style={{ ...S.input, padding: '4px 8px', fontSize: '0.8rem', width: '80px' }} />

                </div>

              )}

            </div>

            

            {/* Healing Check / Shield System */}

            <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(125,181,255,0.06)', borderRadius: '6px', border: '1px solid rgba(125,181,255,0.2)' }}>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>

                <h4 style={{ color: '#7db5ff', fontSize: '0.85rem', margin: 0 }}>🛡️ Heal Check</h4>

                <label style={{ fontSize: '0.72rem', color: '#888', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>

                  <input type="checkbox" checked={deathSaveConfig.healingCheck.enabled}

                    onChange={e => setDeathSaveConfig(prev => ({ ...prev, healingCheck: { ...prev.healingCheck, enabled: e.target.checked } }))} />

                  Enabled

                </label>

              </div>

              {deathSaveConfig.healingCheck.enabled && (

                <>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>

                    <div>

                      <label style={{ ...S.label, fontSize: '0.72rem' }}>Barrier Name</label>

                      <input type="text" value={deathSaveConfig.healingCheck.shieldName}

                        onChange={e => setDeathSaveConfig(prev => ({ ...prev, healingCheck: { ...prev.healingCheck, shieldName: e.target.value } }))}

                        style={{ ...S.input, padding: '4px 8px', fontSize: '0.8rem' }} />

                    </div>

                    <div>

                      <label style={{ ...S.label, fontSize: '0.72rem' }}>HP Threshold</label>

                      <input type="number" min="1" value={deathSaveConfig.healingCheck.shieldHp}

                        onChange={e => setDeathSaveConfig(prev => ({ ...prev, healingCheck: { ...prev.healingCheck, shieldHp: Math.max(1, parseInt(e.target.value) || 10) } }))}

                        style={{ ...S.input, padding: '4px 8px', fontSize: '0.8rem' }} />

                    </div>

                  </div>

                  <div style={{ fontSize: '0.68rem', color: '#999', lineHeight: '1.4', padding: '6px', background: 'rgba(0,0,0,0.15)', borderRadius: '4px' }}>

                    🛡️ Player-initiated. After going unconscious, player can activate the barrier. The next heal attempt is a one-shot check: heal ≥ threshold = death save <span style={{ color: '#27c95a' }}>success</span>, below = death save <span style={{ color: '#c92727' }}>failure</span>. Barrier is consumed either way.

                  </div>

                </>

              )}

            </div>

            

            {/* Tick Timing Rules */}

            <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(201,162,39,0.06)', borderRadius: '6px', border: '1px solid rgba(201,162,39,0.2)' }}>

              <h4 style={{ color: '#c9a227', fontSize: '0.85rem', marginBottom: '10px' }}>⏱️ Tick Timing Rules</h4>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>

                {/* HoT Timing */}

                <div>

                  <label style={{ ...S.label, fontSize: '0.72rem', color: '#27c95a' }}>➕ When HoTs Tick</label>

                  <select value={deathSaveConfig.hotTickTiming || 'per_spell'}

                    onChange={e => setDeathSaveConfig(prev => ({ ...prev, hotTickTiming: e.target.value }))}

                    style={{ ...S.select, padding: '4px', fontSize: '0.78rem' }}>

                    <option value="per_spell">Per spell setting</option>

                    <option value="start_of_round">Start of round</option>

                    <option value="end_of_round">End of round</option>

                    <option value="start_of_caster_turn">Caster's turn (start)</option>

                    <option value="end_of_caster_turn">Caster's turn (end)</option>

                    <option value="start_of_target_turn">Receiver's turn (start)</option>

                    <option value="end_of_target_turn">Receiver's turn (end)</option>

                  </select>

                </div>

                {/* DoT Timing */}

                <div>

                  <label style={{ ...S.label, fontSize: '0.72rem', color: '#e74c3c' }}>🔥 When DoTs Tick</label>

                  <select value={deathSaveConfig.dotTickTiming || 'per_spell'}

                    onChange={e => setDeathSaveConfig(prev => ({ ...prev, dotTickTiming: e.target.value }))}

                    style={{ ...S.select, padding: '4px', fontSize: '0.78rem' }}>

                    <option value="per_spell">Per spell setting</option>

                    <option value="start_of_round">Start of round</option>

                    <option value="end_of_round">End of round</option>

                    <option value="start_of_caster_turn">Caster's turn (start)</option>

                    <option value="end_of_caster_turn">Caster's turn (end)</option>

                    <option value="start_of_target_turn">Receiver's turn (start)</option>

                    <option value="end_of_target_turn">Receiver's turn (end)</option>

                  </select>

                </div>

              </div>

              <div style={{ fontSize: '0.65rem', color: '#888', marginTop: '6px' }}>

                "Per spell setting" uses each spell's individual timing. Global overrides apply to all HoTs/DoTs.

              </div>

            </div>

            

            {/* Preset defaults */}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>

              <div style={{ display: 'flex', gap: '8px' }}>

                <button style={{ ...S.btn, fontSize: '0.72rem', flex: 1, background: 'rgba(125,181,255,0.1)', border: '1px solid rgba(125,181,255,0.3)', color: '#7db5ff' }} onClick={() => setDeathSaveConfig({

                  successesNeeded: 3, failuresNeeded: 3, nat20Behavior: 'revive', nat20ReviveHp: 1,

                  nat1Behavior: 'two_failures', damageAtZeroFailures: 1, critAtZeroFailures: 2,

                  reviveHpMode: 'heal_amount', fixedReviveHp: 15, autoApplyConditions: true,

                  concentrationCheckOnDamage: true, hotTickTiming: 'per_spell', dotTickTiming: 'per_spell',

                  healingCheck: { enabled: true, shieldName: "Death's Barrier", shieldHp: 10, maxChecks: 1, optional: false, checkReplacesDeathSave: true },

                })}>🛡️ BMG Defaults</button>

                <button style={{ ...S.btn, fontSize: '0.72rem', flex: 1, background: 'rgba(201,162,39,0.1)', border: '1px solid rgba(201,162,39,0.3)', color: '#c9a227' }} onClick={() => setDeathSaveConfig({

                  successesNeeded: 3, failuresNeeded: 3, nat20Behavior: 'revive', nat20ReviveHp: 1,

                  nat1Behavior: 'two_failures', damageAtZeroFailures: 1, critAtZeroFailures: 2,

                  reviveHpMode: 'heal_amount', fixedReviveHp: 1, autoApplyConditions: true,

                  concentrationCheckOnDamage: true, hotTickTiming: 'per_spell', dotTickTiming: 'start_of_target_turn',

                  healingCheck: { enabled: false, shieldName: "Death's Barrier", shieldHp: 10, maxChecks: 1, optional: false, checkReplacesDeathSave: true },

                })}>📜 5e Defaults</button>

              </div>

              <button style={{ ...S.btn }} onClick={() => setShowDeathSaveConfig(false)}>Close</button>

            </div>

          </div>

        </div>

      )}

      

      {/* Saving Throw Modal */}

      {showSaveRoll && (

        <div style={S.modal} onClick={closeSaveRollModal}>

          <div style={{ ...S.modalContent, maxWidth: '500px' }} onClick={e => e.stopPropagation()}>

            <h3 style={{ color: '#c9a227', marginBottom: '16px' }}>

              🎲 Saving Throws{(pendingSpellSaveContext?.sourceName || pendingSpellSaveContext?.spell?.name) ? ` — ${pendingSpellSaveContext.sourceName || pendingSpellSaveContext.spell.name}` : ''}

            </h3>

            

            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>

              <div style={{ flex: 1 }}>

                <label style={S.label}>Save Type</label>

                <select
                  value={pendingSpellSaveContext?.saveType || saveType}
                  onChange={(e) => setSaveType(e.target.value)}
                  disabled={!!pendingSpellSaveContext}
                  style={S.select}
                >

                  {['str', 'dex', 'con', 'int', 'wis', 'cha'].map(stat => (

                    <option key={stat} value={stat}>{stat.toUpperCase()}</option>

                  ))}

                </select>

              </div>

              <div style={{ flex: 1 }}>

                <label style={S.label}>DC</label>

                <input 

                  type="number" 

                  value={pendingSpellSaveContext?.saveDC || saveDC}

                  onChange={(e) => setSaveDC(parseInt(e.target.value) || 10)}

                  disabled={!!pendingSpellSaveContext}

                  style={S.input}

                />

              </div>

            </div>

            

            {saveResults.length === 0 ? (

              <>

                <div style={{ textAlign: 'center', padding: '20px', color: '#888' }}>

                  {pendingSpellSaveContext

                    ? `Roll ${(pendingSpellSaveContext.saveType || saveType).toUpperCase()} saves for ${pendingSpellSaveContext.entries.length} target${pendingSpellSaveContext.entries.length === 1 ? '' : 's'} to resolve ${pendingSpellSaveContext.sourceName || pendingSpellSaveContext.spell?.name || 'the pending effect'}.`

                    : 'Select targets and click "Roll Saves" to see results'}

                </div>

                {pendingSpellSaveContext && (

                  <div style={{ textAlign: 'center', paddingBottom: '16px', color: '#888', fontSize: '0.8rem' }}>

                    Closing this modal hides the pending save. Use the combat bar "Roll Saves" button to resume it.

                  </div>

                )}

              </>

            ) : (

              <div style={{ marginBottom: '16px' }}>

                {saveResults.map((result, i) => {

                  const effectiveResultSaveDC = Number(pendingSpellSaveContext?.saveDC) || saveDC;

                  const passed = result.total >= effectiveResultSaveDC;

                  return (

                    <div 

                      key={i}

                      style={{

                        padding: '8px 12px',

                        background: passed ? 'rgba(39,201,90,0.2)' : 'rgba(201,39,39,0.2)',

                        borderRadius: '6px',

                        marginBottom: '6px',

                        border: `1px solid ${passed ? '#27c95a' : '#c92727'}`,

                        display: 'flex',

                        justifyContent: 'space-between',

                        alignItems: 'center',

                      }}

                    >

                      <span style={{ fontWeight: 'bold' }}>{result.combatantName}</span>

                      <span>

                        <span style={{ color: '#888' }}>({result.d20}</span>

                        <span style={{ color: result.modifier >= 0 ? '#27c95a' : '#c92727' }}>

                          {result.modifier >= 0 ? '+' : ''}{result.modifier}

                        </span>

                        {result.bonusDiceTotal ? (

                          <span style={{ color: '#7db5ff' }}>

                            +{result.bonusDiceTotal}{result.bonusDiceLabel ? `(${result.bonusDiceLabel})` : ''}

                          </span>

                        ) : null}

                        <span style={{ color: '#888' }}>) = </span>

                        <span style={{ fontWeight: 'bold', color: passed ? '#27c95a' : '#c92727' }}>

                          {result.total} {passed ? '✓' : '✗'}

                        </span>

                      </span>

                    </div>

                  );

                })}

              </div>

            )}

            

            <div style={{ display: 'flex', gap: '8px' }}>

              <button style={{ ...S.btn, ...S.btnSuccess }} onClick={rollSavesForTargets}>

                🎲 Roll Saves ({pendingSpellSaveContext ? pendingSpellSaveContext.entries.length : selectedTargets.length} selected)

              </button>

              {pendingSpellSaveContext && saveResults.length > 0 && (

                <button style={{ ...S.btn, ...S.btnDanger }} onClick={applyPendingSpellSaveResults}>

                  Resolve {pendingSpellSaveContext.sourceName || pendingSpellSaveContext.spell?.name || 'Pending Save'}

                </button>

              )}

              <button style={S.btn} onClick={closeSaveRollModal}>{pendingSpellSaveContext ? 'Hide' : 'Close'}</button>

            </div>

          </div>

        </div>

      )}



      {/* ========== COMBAT HISTORY MODAL ========== */}

      {/* Three views (Event / Turn / Round) over the same snapshot list. Each row is single-click

          to select, double-click to open the revert confirmation. Revert restores the snapshot's

          pre-event state and drops all later snapshots. */}

      {showHistory && isDM && (() => {

        const snapshots = combatSnapshots;

        // Build reverse-chronological list once; Turn/Round views group but keep the same sort.

        const reversed = snapshots.slice().reverse();

        const groupByTurn = () => {

          const groups = [];

          reversed.forEach(s => {

            const key = `R${s.round}-T${s.turnIndex}`;

            let g = groups.find(x => x.key === key);

            if (!g) { g = { key, round: s.round, turnIndex: s.turnIndex, actorName: s.actorName, events: [] }; groups.push(g); }

            g.events.push(s);

          });

          return groups;

        };

        const groupByRound = () => {

          const groups = [];

          reversed.forEach(s => {

            let g = groups.find(x => x.round === s.round);

            if (!g) { g = { round: s.round, events: [] }; groups.push(g); }

            g.events.push(s);

          });

          return groups;

        };

        const renderRow = (s) => {

          const isSelected = selectedSnapshotId === s.id;

          const actionColor = s.action === 'damage' ? '#c92727' : s.action === 'heal' ? '#27c95a' : s.action === 'turn_advance' || s.action === 'round_advance' ? '#c9a227' : s.action === 'death_save' ? '#9b59b6' : '#888';

          return (

            <div

              key={s.id}

              onClick={() => setSelectedSnapshotId(s.id)}

              onDoubleClick={() => setRevertTarget(s)}

              title="Click to select · Double-click to revert"

              style={{

                padding: '6px 10px', cursor: 'pointer',

                background: isSelected ? 'rgba(201,162,39,0.25)' : 'transparent',

                borderLeft: isSelected ? '3px solid #c9a227' : `3px solid ${actionColor}33`,

                fontSize: '0.8rem', display: 'flex', gap: '8px', alignItems: 'center',

                userSelect: 'none',

              }}

            >

              <span style={{ opacity: 0.55, fontFamily: 'monospace', fontSize: '0.7rem', minWidth: '54px', color: actionColor }}>

                R{s.round}·T{(s.turnIndex || 0) + 1}

              </span>

              <span style={{ opacity: 0.5, fontFamily: 'monospace', fontSize: '0.7rem', minWidth: '58px' }}>

                {new Date(s.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}

              </span>

              <span style={{ flex: 1 }}>{s.summary}</span>

            </div>

          );

        };

        return (

          <div style={S.modal} onClick={() => setShowHistory(false)}>

            <div style={{ ...S.modalContent, width: '760px', maxWidth: '95vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>

                <h3 style={{ margin: 0, color: '#c9a227' }}>📜 Combat History</h3>

                <button style={{ ...S.btn, ...S.btnSm }} onClick={() => setShowHistory(false)}>✕ Close</button>

              </div>



              <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>

                {[

                  { id: 'event', label: 'By Event' },

                  { id: 'turn', label: 'By Turn' },

                  { id: 'round', label: 'By Round' },

                ].map(tab => (

                  <button

                    key={tab.id}

                    style={{ ...S.btn, ...S.btnSm, marginBottom: 0, background: historyTab === tab.id ? 'rgba(201,162,39,0.35)' : 'rgba(0,0,0,0.3)' }}

                    onClick={() => setHistoryTab(tab.id)}

                  >

                    {tab.label}

                  </button>

                ))}

                <div style={{ flex: 1 }} />

                <span style={{ fontSize: '0.7rem', opacity: 0.6, alignSelf: 'center' }}>

                  {snapshots.length} / {SNAPSHOT_CAP} snapshots

                </span>

              </div>



              <div style={{ fontSize: '0.72rem', opacity: 0.7, marginBottom: '8px', padding: '6px 8px', background: 'rgba(74,144,217,0.08)', borderRadius: '4px', border: '1px solid rgba(74,144,217,0.15)' }}>

                💡 Single-click a row to select it. <strong>Double-click</strong> to revert combat to the state just before that event (with confirmation).

              </div>



              <div style={{ flex: 1, overflowY: 'auto', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px', background: 'rgba(0,0,0,0.15)' }}>

                {snapshots.length === 0 ? (

                  <div style={{ padding: '20px', textAlign: 'center', opacity: 0.55, fontSize: '0.85rem' }}>

                    No history yet. Snapshots appear here as combat actions occur.

                  </div>

                ) : historyTab === 'event' ? (

                  reversed.map(renderRow)

                ) : historyTab === 'turn' ? (

                  groupByTurn().map(g => (

                    <div key={g.key}>

                      <div style={{ padding: '5px 10px', background: 'rgba(201,162,39,0.15)', fontWeight: 'bold', fontSize: '0.78rem', borderTop: '1px solid rgba(201,162,39,0.2)', borderBottom: '1px solid rgba(201,162,39,0.1)' }}>

                        Round {g.round}, Turn {(g.turnIndex || 0) + 1} — {g.actorName}

                        <span style={{ opacity: 0.6, fontWeight: 'normal', marginLeft: '8px' }}>({g.events.length} event{g.events.length !== 1 ? 's' : ''})</span>

                      </div>

                      {g.events.map(renderRow)}

                    </div>

                  ))

                ) : (

                  groupByRound().map(g => (

                    <div key={g.round}>

                      <div style={{ padding: '5px 10px', background: 'rgba(74,144,217,0.15)', fontWeight: 'bold', fontSize: '0.78rem', borderTop: '1px solid rgba(74,144,217,0.2)', borderBottom: '1px solid rgba(74,144,217,0.1)' }}>

                        Round {g.round}

                        <span style={{ opacity: 0.6, fontWeight: 'normal', marginLeft: '8px' }}>({g.events.length} event{g.events.length !== 1 ? 's' : ''})</span>

                      </div>

                      {g.events.map(renderRow)}

                    </div>

                  ))

                )}

              </div>



              {selectedSnapshotId && (() => {

                const sel = snapshots.find(s => s.id === selectedSnapshotId);

                if (!sel) return null;

                return (

                  <div style={{ marginTop: '10px', display: 'flex', gap: '8px', alignItems: 'center', padding: '8px 10px', background: 'rgba(201,162,39,0.1)', borderRadius: '4px' }}>

                    <span style={{ flex: 1, fontSize: '0.8rem' }}>

                      <strong>Selected:</strong> {sel.summary}

                    </span>

                    <button style={{ ...S.btn, ...S.btnDanger, ...S.btnSm }} onClick={() => setRevertTarget(sel)}>↩ Revert to here</button>

                  </div>

                );

              })()}

            </div>

          </div>

        );

      })()}



      {/* ========== REVERT CONFIRMATION MODAL ========== */}

      {revertTarget && (() => {

        const snapshots = combatSnapshots;

        const idx = snapshots.findIndex(s => s.id === revertTarget.id);

        const subsequent = idx >= 0 ? snapshots.length - idx : 0;

        return (

          <div style={{ ...S.modal, zIndex: 10001 }} onClick={() => setRevertTarget(null)}>

            <div style={{ ...S.modalContent, width: '500px', maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>

              <h3 style={{ marginTop: 0, color: '#ff6b6b' }}>↩ Revert Combat?</h3>

              <div style={{ padding: '10px', background: 'rgba(201,162,39,0.1)', borderRadius: '4px', marginBottom: '10px' }}>

                <div style={{ fontSize: '0.85rem' }}><strong>Target event:</strong> {revertTarget.summary}</div>

                <div style={{ opacity: 0.7, fontSize: '0.75rem', marginTop: '4px' }}>

                  Round {revertTarget.round}, Turn {(revertTarget.turnIndex || 0) + 1} — {revertTarget.actorName}

                </div>

                <div style={{ opacity: 0.7, fontSize: '0.75rem' }}>{new Date(revertTarget.timestamp).toLocaleString()}</div>

              </div>

              <p style={{ fontSize: '0.85rem', lineHeight: 1.4 }}>

                Combat (and affected character HP/tempHP/equipment) will be restored to the state <strong>before</strong> this event.

                <br />

                <strong>{subsequent} subsequent event{subsequent !== 1 ? 's' : ''}</strong> will be discarded. This cannot be undone.

              </p>

              {revertError && (
                <div style={{ padding: '8px 10px', background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.45)', borderRadius: '4px', color: '#f87171', fontSize: '0.82rem', marginTop: '8px' }}>
                  {revertError}
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' }}>

                <button style={{ ...S.btn }} onClick={() => { setRevertError(null); setRevertTarget(null); }} disabled={revertingSnapshotId === revertTarget.id}>Cancel</button>

                <button style={{ ...S.btn, ...S.btnDanger }} onClick={() => revertToSnapshot(revertTarget.id)} disabled={revertingSnapshotId === revertTarget.id}>
                  {revertingSnapshotId === revertTarget.id ? 'Reverting...' : '↩ Revert'}
                </button>

              </div>

            </div>

          </div>

        );

      })()}

    </div>

  );

};



// ============== END COMBAT SYSTEM ==============



// ============== JOURNAL PAGE COMPONENT ==============

export const JournalPage = ({ chars, currentCharId, isDM, journal, onUpdateJournal, upsertJournalEntry, deleteJournalEntry, onSetEntryCampaignScope, activeCampaignId, activeCampaignName, onBack, journalSettings, onUpdateJournalSettings, initialEntry, onClearInitEntry }) => {

  const [activeCategory, setActiveCategory] = useState('session');

  const [searchQuery, setSearchQuery] = useState('');

  const [filterTags, setFilterTags] = useState([]);

  const [filterLocation, setFilterLocation] = useState('');

  const [filterVisibility, setFilterVisibility] = useState('all');

  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  const [selectedEntry, setSelectedEntry] = useState(null);

  const [editingEntry, setEditingEntry] = useState(null);

  const [showNewEntry, setShowNewEntry] = useState(false);

  const [categoryOrder, setCategoryOrder] = useState([...DEFAULT_JOURNAL_ORDER]);

  const [showOrderSettings, setShowOrderSettings] = useState(false);

  const [unlinkedNames, setUnlinkedNames] = useState([]); // Names explicitly unlinked

  const [navigationHistory, setNavigationHistory] = useState([]); // Stack of previously viewed entries

  const [hoveredEntry, setHoveredEntry] = useState(null); // For tooltip

  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const [showJournalSettings, setShowJournalSettings] = useState(false);

  const [quickAddName, setQuickAddName] = useState('');

  const [quickAddCategory, setQuickAddCategory] = useState(null); // null = use activeCategory

  const [quickAddVisible, setQuickAddVisible] = useState(true);

  const [showPlayerAddBlock, setShowPlayerAddBlock] = useState(false);

  const [playerAddBlockField, setPlayerAddBlockField] = useState(null);

  const [playerAddBlockText, setPlayerAddBlockText] = useState('');

  // 2026-04-27: per-entry-id persistent collapse state. Was a flat

  // {fieldId: collapsed} map shared across all entries — switching cards

  // bled state, and closing/reopening reset everything. Now keyed by

  // entry id and persisted to localStorage so each card remembers what

  // the user had open the last time they viewed it. Defaults to all-

  // expanded; user toggles persist.

  const [playerCardSections, setPlayerCardSections] = useState(() => {

    try { return JSON.parse(localStorage.getItem('bmg-player-card-sections') || '{}'); }

    catch { return {}; }

  });

  useEffect(() => {

    try { localStorage.setItem('bmg-player-card-sections', JSON.stringify(playerCardSections)); }

    catch {}

  }, [playerCardSections]);

  

  // Auto-select entry when navigating from quick journal

  useEffect(() => {

    if (initialEntry && journal) {

      const entry = journal.find(e => e.id === initialEntry.id) || journal.find(e => e.name?.toLowerCase() === initialEntry.name?.toLowerCase() && e.category === initialEntry.category);

      if (entry) {

        setActiveCategory(entry.category);

        setSelectedEntry(entry);

      }

      if (onClearInitEntry) onClearInitEntry();

    }

  }, [initialEntry]);

  

  // Journal settings with defaults

  const settings = journalSettings || { showLinkTooltips: true, tooltipDelay: 300 };

  

  const entries = journal || [];

  const currentChar = currentCharId ? chars[currentCharId] : null;

  const runJournalRowCommand = (label, command, fallback) => {
    if (typeof command !== 'function') {
      if (typeof fallback === 'function') fallback();
      return false;
    }
    try {
      const result = command();
      if (result && typeof result.catch === 'function') {
        result.catch((error) => {
          console.error(`${label} failed:`, error);
          if (typeof fallback === 'function') fallback();
        });
      }
      return true;
    } catch (error) {
      console.error(`${label} failed:`, error);
      if (typeof fallback === 'function') fallback();
      return false;
    }
  };

  const buildJournalWithEntry = (entry) => {
    const current = Array.isArray(entries) ? entries : [];
    const existingIndex = current.findIndex(e => e.id === entry.id);
    if (existingIndex >= 0) {
      const updated = [...current];
      updated[existingIndex] = entry;
      return updated;
    }
    return [...current, entry];
  };

  const upsertJournalEntryFromPage = (entry, updatedJournal = null) => {
    const entryId = entry?.id;
    const nextJournal = updatedJournal || buildJournalWithEntry(entry);
    runJournalRowCommand(
      'Journal page row update',
      typeof upsertJournalEntry === 'function' && entryId
        ? () => upsertJournalEntry({ entryId, entry })
        : null,
      () => onUpdateJournal(nextJournal),
    );
  };

  const deleteJournalEntryFromPage = (entryId) => {
    if (!entryId) return;
    runJournalRowCommand(
      'Journal page row delete',
      typeof deleteJournalEntry === 'function'
        ? () => deleteJournalEntry({ entryId })
        : null,
      () => onUpdateJournal(entries.filter(e => e.id !== entryId)),
    );
  };

  

  // Get ordered categories

  const orderedCategories = categoryOrder.map(key => [key, JOURNAL_CATEGORIES[key]]).filter(([k, v]) => v);

  

  // Get all unique tags and locations from entries

  const allTags = [...new Set(entries.flatMap(e => e.tags || []))].sort();

  const allLocations = [...new Set(entries.flatMap(e => e.locationTags || []))].sort();

  

  // Filter entries

  const filteredEntries = entries.filter(entry => {

    // Category filter

    if (entry.category !== activeCategory) return false;

    

    // Visibility filter

    if (!isDM) {

      if (entry.visibility === 'private' && entry.createdBy !== currentCharId) {

        console.log('[Journal Filter] Hidden private entry:', entry.name, 'createdBy:', entry.createdBy, 'currentCharId:', currentCharId);

        return false;

      }

      if (entry.visibility === 'dm') return false;

      if (entry.visibility === 'select' && !entry.sharedWith?.includes(currentCharId) && entry.createdBy !== currentCharId) return false;

    }

    if (filterVisibility !== 'all' && entry.visibility !== filterVisibility) return false;

    

    // Search filter

    if (searchQuery) {

      const query = searchQuery.toLowerCase();

      const searchable = [

        entry.name, entry.description, 

        ...(entry.tags || []), 

        ...(entry.locationTags || []),

        entry.habitat, entry.effects, entry.personality, entry.summary

      ].filter(Boolean).join(' ').toLowerCase();

      if (!searchable.includes(query)) return false;

    }

    

    // Tag filter

    if (filterTags.length > 0) {

      const entryTagsLower = (entry.tags || []).map(t => t.toLowerCase());

      if (!filterTags.some(t => entryTagsLower.includes(t.toLowerCase()))) return false;

    }

    

    // Location filter

    if (filterLocation && !(entry.locationTags || []).some(t => t.toLowerCase() === filterLocation.toLowerCase())) return false;

    

    return true;

  });

  

  // Sort entries (pinned first, then by updated date)

  const sortedEntries = [...filteredEntries].sort((a, b) => {

    if (a.pinned && !b.pinned) return -1;

    if (!a.pinned && b.pinned) return 1;

    return new Date(b.updatedAt) - new Date(a.updatedAt);

  });

  

  // Move category in order

  const moveCategoryOrder = (index, direction) => {

    const newOrder = [...categoryOrder];

    const newIndex = index + direction;

    if (newIndex < 0 || newIndex >= newOrder.length) return;

    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];

    setCategoryOrder(newOrder);

  };

  

  // Handle clicking on a linked entry - with navigation history

  const handleEntryClick = (entry, fromEntry = null) => {

    if (fromEntry || selectedEntry) {

      // Add current entry to history before navigating

      setNavigationHistory(prev => [...prev, { entry: fromEntry || selectedEntry, category: activeCategory }]);

    }

    setActiveCategory(entry.category);

    setSelectedEntry(entry);

    setHoveredEntry(null); // Clear tooltip on navigation

  };

  

  // Go back to previous entry

  const goBack = () => {

    if (navigationHistory.length === 0) return;

    const prev = navigationHistory[navigationHistory.length - 1];

    setNavigationHistory(navigationHistory.slice(0, -1));

    setActiveCategory(prev.category);

    setSelectedEntry(prev.entry);

  };

  

  // Clear history when closing entry view

  const closeEntry = () => {

    setSelectedEntry(null);

    setNavigationHistory([]);

  };

  

  // Handle tooltip hover

  const handleLinkHover = (entry, event) => {

    if (!settings.showLinkTooltips) return;

    const rect = event.target.getBoundingClientRect();

    setTooltipPos({ x: rect.left, y: rect.bottom + 5 });

    setHoveredEntry(entry);

  };

  

  const handleLinkLeave = () => {

    setHoveredEntry(null);

  };

  

  // Toggle unlink for a name

  const toggleUnlink = (name) => {

    if (unlinkedNames.includes(name)) {

      setUnlinkedNames(unlinkedNames.filter(n => n !== name));

    } else {

      setUnlinkedNames([...unlinkedNames, name]);

    }

  };

  

  // Create new entry (full editor)

  const createEntry = () => {

    const newEntry = createJournalEntry(activeCategory, {

      createdBy: currentCharId,

      visibility: 'private',

    });

    setEditingEntry(newEntry);

    setShowNewEntry(true);

  };

  

  // Quick-add: create a stub entry with just a name, or parse tags if present

  const quickAddEntry = (text, category) => {

    if (!text.trim()) return;

    

    // Check if text contains tag syntax (@ref, #ref, #X@Name, or #todo)

    if (/[#@][A-Z]{1,2}[#@]/.test(text) || /@\S/.test(text) || /#todo\b/i.test(text) || /#[a-zA-Z]/.test(text)) {

      // Use the full parser

      const qaSettings = { ...DEFAULT_QUICKADD_SETTINGS, ...(journalSettings?.quickAdd || {}) };

      const parsed = parseQuickAddInput(text, qaSettings);

      // Find active session

      const sessions = entries.filter(e => e.category === 'session').sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

      const activeSession = sessions[0] || null;

      const { updatedJournal, summary } = applyQuickAddResults(parsed, entries, activeSession?.id, currentCharId);

      onUpdateJournal(updatedJournal);

      setQuickAddName('');

      setQuickAddCategory(null);

      // Switch to the first created entry's category

      if (summary.created.length > 0) setActiveCategory(summary.created[0].category);

      else if (summary.updated.length > 0) setActiveCategory(summary.updated[0].category);

      return;

    }

    

    // Simple stub creation (no tags)

    const cat = category || activeCategory;

    const newEntry = createJournalEntry(cat, {

      name: text.trim(),

      createdBy: currentCharId,

      visibility: 'shared',

      isStub: true,

    });

    upsertJournalEntryFromPage(newEntry, [...entries, newEntry]);

    setQuickAddName('');

    setQuickAddCategory(null);

    // Switch to the category we just added to

    if (cat !== activeCategory) setActiveCategory(cat);

  };

  

  // Save entry

  const saveEntry = (entry) => {

    let nextEntry = { ...entry, updatedAt: new Date().toISOString() };

    // Clear stub flag when saved through the full editor

    if (nextEntry.isStub) nextEntry = { ...nextEntry, isStub: false };

    // Process tag syntax in description field (clean markup like quick add does)

    if (nextEntry.description && /[#@][A-Z]{1,2}[#@]/.test(nextEntry.description)) {

      const qaSettings = { ...DEFAULT_QUICKADD_SETTINGS, ...(journalSettings?.quickAdd || {}) };

      const tags = findAllTags(nextEntry.description, qaSettings.delimiter || ' - ');

      const entryTags = tags.filter(t => t.type === 'tag');

      if (entryTags.length > 0) {

        nextEntry = { ...nextEntry, description: cleanRefMarkup(cleanTagMarkup(nextEntry.description, entryTags)) };

      }

    }

    const existingIndex = entries.findIndex(e => e.id === nextEntry.id);

    if (existingIndex >= 0) {

      // Campaign tag (2026-07-13): scope moves go through the dedicated
      // mutation (which tombstones the source row) — the upsert itself stays
      // in the entry's original scope so we never leave a stale copy behind.
      const originalCampaignId = entries[existingIndex]?.campaignId || null;

      const chosenCampaignId = nextEntry.campaignId || null;

      const tagChanged = originalCampaignId !== chosenCampaignId && typeof onSetEntryCampaignScope === 'function';

      const upsertPayload = tagChanged
        ? { ...nextEntry, campaignId: originalCampaignId || undefined }
        : nextEntry;

      const updated = [...entries];

      updated[existingIndex] = nextEntry;

      upsertJournalEntryFromPage(upsertPayload, updated);

      if (tagChanged) {

        Promise.resolve(onSetEntryCampaignScope({ entryId: nextEntry.id, campaignId: chosenCampaignId }))
          .catch(err => console.error('Journal campaign tag change failed:', err));

      }

    } else {

      upsertJournalEntryFromPage(nextEntry, [...entries, nextEntry]);

    }

    setEditingEntry(null);

    setShowNewEntry(false);

    setSelectedEntry(prev => prev?.id === nextEntry.id ? nextEntry : prev);

  };

  

  // Delete entry

  const deleteEntry = (entryId) => {

    if (window.confirm('Delete this journal entry?')) {

      deleteJournalEntryFromPage(entryId);

      setSelectedEntry(null);

      setEditingEntry(null);

    }

  };

  

  // Toggle pin

  const togglePin = (entryId) => {

    const target = entries.find(e => e.id === entryId);

    if (!target) return;

    const nextEntry = { ...target, pinned: !target.pinned, updatedAt: new Date().toISOString() };

    upsertJournalEntryFromPage(nextEntry, entries.map(e => e.id === entryId ? nextEntry : e));

    setSelectedEntry(prev => prev?.id === entryId ? nextEntry : prev);

  };

  

  // Get category color

  const getCatColor = (cat) => JOURNAL_CATEGORIES[cat]?.color || '#888';

  

  // Render visibility badge

  const renderVisibilityBadge = (visibility) => {

    const opt = VISIBILITY_OPTIONS.find(o => o.id === visibility);

    return (

      <span style={{ fontSize: '0.7rem', padding: '2px 6px', borderRadius: '4px', background: 'rgba(0,0,0,0.3)', marginLeft: '6px' }}>

        {opt?.icon} {opt?.name}

      </span>

    );

  };

  // Campaign tag chip (2026-07-13): entries default to universal "Lore";
  // campaign-tagged entries are scoped to that campaign. Only rendered when a
  // campaign context exists, so standalone play stays uncluttered.
  const renderScopeBadge = (entry) => {
    if (!activeCampaignId && !entry?.campaignId) return null;
    const isCampaign = !!entry?.campaignId;
    const label = isCampaign
      ? (entry.campaignId === activeCampaignId && activeCampaignName ? activeCampaignName : 'Campaign')
      : 'Lore';
    return (
      <span style={{
        fontSize: '0.7rem',
        padding: '2px 6px',
        borderRadius: '4px',
        marginLeft: '6px',
        background: isCampaign ? 'rgba(147,112,219,0.15)' : 'rgba(255,215,0,0.10)',
        border: `1px solid ${isCampaign ? 'rgba(147,112,219,0.4)' : 'rgba(255,215,0,0.3)'}`,
        color: isCampaign ? '#b48aff' : '#d4b76a',
      }}>
        {isCampaign ? '🏰' : '📖'} {label}
      </span>
    );
  };

  

  // Render entry card

  const renderEntryCard = (entry) => {

    const catData = JOURNAL_CATEGORIES[entry.category];

    

    return (

      <div 

        key={entry.id}

        style={{

          background: 'linear-gradient(135deg, rgba(40,30,20,0.95) 0%, rgba(30,25,20,0.98) 100%)',

          border: `2px ${entry.isStub ? 'dashed' : 'solid'} ${entry.pinned ? '#ffd700' : getCatColor(entry.category)}40`,

          borderRadius: '8px',

          padding: viewMode === 'grid' ? '12px' : '10px',

          cursor: 'pointer',

          transition: 'all 0.2s',

          display: viewMode === 'list' ? 'flex' : 'block',

          gap: viewMode === 'list' ? '12px' : '0',

          alignItems: viewMode === 'list' ? 'center' : 'stretch',

        }}

        onClick={() => setSelectedEntry(entry)}

        onMouseEnter={e => { e.currentTarget.style.borderColor = getCatColor(entry.category); e.currentTarget.style.borderStyle = 'solid'; }}

        onMouseLeave={e => { e.currentTarget.style.borderColor = entry.pinned ? '#ffd700' : `${getCatColor(entry.category)}40`; e.currentTarget.style.borderStyle = entry.isStub ? 'dashed' : 'solid'; }}

      >

        {/* Image */}

        {entry.image && viewMode === 'grid' && (

          <div style={{

            width: '100%',

            height: '100px',

            marginBottom: '8px',

            borderRadius: '4px',

            overflow: 'hidden',

            background: 'rgba(0,0,0,0.3)',

          }}>

            <img src={entry.image} alt={entry.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />

          </div>

        )}

        

        {entry.image && viewMode === 'list' && (

          <div style={{

            width: '50px',

            height: '50px',

            borderRadius: '4px',

            overflow: 'hidden',

            background: 'rgba(0,0,0,0.3)',

            flexShrink: 0,

          }}>

            <img src={entry.image} alt={entry.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />

          </div>

        )}

        

        <div style={{ flex: 1, minWidth: 0 }}>

          {/* Header */}

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>

            {entry.pinned && <span style={{ color: '#ffd700' }}>📌</span>}

            <span style={{ fontSize: '1.1rem' }}>{catData?.icon}</span>

            <strong style={{ color: '#e8d5b7', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.name || 'Untitled'}</strong>

            {entry.isStub && <span style={{ fontSize: '0.55rem', padding: '1px 5px', borderRadius: '3px', background: 'rgba(201,162,39,0.25)', border: '1px dashed rgba(201,162,39,0.5)', color: '#c9a227', whiteSpace: 'nowrap' }}>stub</span>}

            {(entry.tags || []).includes('dm-revealed') && <span style={{ fontSize: '0.55rem', padding: '1px 5px', borderRadius: '3px', background: 'rgba(74,144,217,0.15)', border: '1px solid rgba(74,144,217,0.3)', color: '#4a90d9', whiteSpace: 'nowrap' }}>📓 DM</span>}

            {isDM && entry.createdBy && chars[entry.createdBy] && <span style={{ fontSize: '0.55rem', padding: '1px 6px', borderRadius: '3px', background: `${chars[entry.createdBy].color || '#4a90d9'}15`, border: `1px solid ${chars[entry.createdBy].color || '#4a90d9'}30`, color: chars[entry.createdBy].color || '#4a90d9', whiteSpace: 'nowrap', marginLeft: 'auto' }}>{chars[entry.createdBy].name}</span>}

          </div>

          

          {/* Category-specific info */}

          {entry.category === 'creatures' && (

            <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '4px' }}>

              {entry.size} {entry.creatureType} • CR {entry.challengeRating}

              {entry.isBoss && <span style={{ color: '#c92727', marginLeft: '6px' }}>👑 BOSS</span>}

            </div>

          )}

          {entry.category === 'plants' && (

            <div style={{ fontSize: '0.75rem', color: RARITY_LEVELS.find(r => r.id === entry.rarity)?.color || '#888', marginBottom: '4px' }}>

              {entry.plantType} • {RARITY_LEVELS.find(r => r.id === entry.rarity)?.name}

            </div>

          )}

          {entry.category === 'items' && (

            <div style={{ fontSize: '0.75rem', color: RARITY_LEVELS.find(r => r.id === entry.rarity)?.color || '#888', marginBottom: '4px' }}>

              {entry.itemType} • {RARITY_LEVELS.find(r => r.id === entry.rarity)?.name}

              {entry.isQuestItem && <span style={{ color: '#9b59b6', marginLeft: '6px' }}>📜 Quest</span>}

            </div>

          )}

          {entry.category === 'recipes' && (

            <div style={{ fontSize: '0.75rem', color: '#17a589', marginBottom: '4px' }}>

              {entry.recipeType} • {(entry.ingredients || []).length} ingredients

              {(entry.curesDiseases || []).length > 0 && <span style={{ color: '#8e44ad', marginLeft: '6px', display: 'inline-flex', alignItems: 'center', gap: 3 }}><BiohazardIcon size={12} color='#8e44ad' /> Cures</span>}

            </div>

          )}

          {entry.category === 'diseases' && (

            <div style={{ fontSize: '0.75rem', color: DISEASE_SEVERITIES.find(s => s.id === entry.severity)?.color || '#888', marginBottom: '4px' }}>

              {DISEASE_SEVERITIES.find(s => s.id === entry.severity)?.name || 'Unknown'} severity

              {(entry.curedBy || []).length > 0 && <span style={{ color: '#27c95a', marginLeft: '6px' }}>✓ Cure known</span>}

            </div>

          )}

          {entry.category === 'npcs' && (entry.title || (entry.disposition && entry.disposition !== 'unknown')) && (

            <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '4px' }}>

              {entry.title && `${entry.title}`}

              {entry.title && entry.disposition && entry.disposition !== 'unknown' && ' • '}

              {entry.disposition && entry.disposition !== 'unknown' && <span style={{ color: NPC_DISPOSITIONS.find(d => d.id === entry.disposition)?.color }}>

                {NPC_DISPOSITIONS.find(d => d.id === entry.disposition)?.icon} {NPC_DISPOSITIONS.find(d => d.id === entry.disposition)?.name}

              </span>}

            </div>

          )}

          {entry.category === 'quests' && (

            <div style={{ fontSize: '0.75rem', marginBottom: '4px' }}>

              <span style={{ color: QUEST_STATUSES.find(s => s.id === entry.status)?.color }}>

                {QUEST_STATUSES.find(s => s.id === entry.status)?.icon} {QUEST_STATUSES.find(s => s.id === entry.status)?.name}

              </span>

              {(() => {

                const p = QUEST_PRIORITIES.find(p => p.id === entry.priority);

                if (!p) return null;

                return (

                  <span style={{ color: p.color, marginLeft: '8px' }}>

                    {p.icon} {p.name}

                  </span>

                );

              })()}

            </div>

          )}

          {entry.category === 'session' && (

            <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '4px' }}>

              Session {entry.sessionNumber} • {entry.sessionDate}

            </div>

          )}

          

          {/* Description preview */}

          {viewMode === 'grid' && entry.description && (

            <p style={{ 

              fontSize: '0.8rem', 

              color: '#999', 

              margin: '4px 0', 

              overflow: 'hidden', 

              textOverflow: 'ellipsis',

              display: '-webkit-box',

              WebkitLineClamp: 2,

              WebkitBoxOrient: 'vertical',

              lineHeight: '1.3',

            }}>

              {stripPrivateForViewer(entry.description, currentCharId, isDM).replace(/\{\{PRIVATE:[^}]+\}\}|\{\{\/PRIVATE\}\}/g, '')}

            </p>

          )}

          

          {/* Tags */}

          {(entry.tags?.length > 0 || entry.locationTags?.length > 0) && (

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>

              {(entry.locationTags || []).slice(0, 2).map((tag, i) => (

                <span key={`loc-${i}`} style={{ fontSize: '0.65rem', padding: '1px 4px', background: 'rgba(74,144,217,0.3)', border: '1px solid #4a90d9', borderRadius: '3px', color: '#7db5ff' }}>

                  📍{tag}

                </span>

              ))}

              {(entry.tags || []).slice(0, 3).map((tag, i) => (

                <span key={`tag-${i}`} style={{ fontSize: '0.65rem', padding: '1px 4px', background: 'rgba(201,162,39,0.2)', border: '1px solid #c9a22780', borderRadius: '3px', color: '#c9a227' }}>

                  {tag}

                </span>

              ))}

            </div>

          )}

        </div>

      </div>

    );

  };

  

  // Render entry detail view

  const renderEntryDetail = (entry) => {

    const catData = JOURNAL_CATEGORIES[entry.category];

    const canEdit = isDM || entry.createdBy === currentCharId || entry.visibility === 'shared';

    

    // Helper to render linked text with tooltips and privacy filtering

    const renderWithLinks = (text) => {

      if (!text) return null;

      return renderTextWithPrivacy(text, currentCharId, isDM, entries, (e) => handleEntryClick(e, entry), {

        onHover: settings.showLinkTooltips ? handleLinkHover : undefined,

        onLeave: settings.showLinkTooltips ? handleLinkLeave : undefined,

        firstOccurrenceOnly: true

      }, chars);

    };

    

    return (

      <div style={S.modal} onClick={closeEntry}>

        <div style={{ ...S.modalContent, maxWidth: '700px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>

          {/* Header */}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexShrink: 0 }}>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>

              {/* Back button */}

              {navigationHistory.length > 0 && (

                <button 

                  style={{ ...S.btn, ...S.btnXs, padding: '6px 10px' }} 

                  onClick={(e) => { e.stopPropagation(); goBack(); }}

                  title="Go back to previous entry"

                >

                  ← Back

                </button>

              )}

              <div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

                  <span style={{ fontSize: '1.5rem' }}>{catData?.icon}</span>

                  <div style={{ flex: 1 }}>

                    <h2 style={{ color: '#ffd700', margin: 0 }}>{entry.name || 'Untitled'}</h2>

                    {entry.category === 'npcs' && entry.title && <div style={{ fontSize: '0.78rem', color: '#888' }}>{entry.title}</div>}

                  </div>

                  {entry.pinned && <span style={{ color: '#ffd700' }}>📌</span>}

                  {/* NPC badges in header like DM Library */}

                  {entry.category === 'npcs' && (() => {

                    const d = NPC_DISPOSITIONS.find(dd => dd.id === entry.disposition);

                    return <>

                      {d && d.id !== 'unknown' && <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '0.72rem', background: `${d.color}18`, border: `1px solid ${d.color}40`, color: d.color, fontWeight: 600 }}>{d.name}</span>}

                      <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '0.72rem', background: entry.isAlive === false ? 'rgba(201,39,39,0.18)' : 'rgba(39,201,90,0.12)', border: `1px solid ${entry.isAlive === false ? 'rgba(201,39,39,0.4)' : 'rgba(39,201,90,0.3)'}`, color: entry.isAlive === false ? '#c92727' : '#27c95a', fontWeight: 600 }}>{entry.isAlive === false ? 'Deceased' : 'Alive'}</span>

                    </>;

                  })()}

                </div>

                <div style={{ fontSize: '0.85rem', color: '#888', marginTop: '4px' }}>

                  {catData?.name} {renderVisibilityBadge(entry.visibility)}{renderScopeBadge(entry)}

                  {isDM && entry.createdBy && chars[entry.createdBy] && <span style={{ marginLeft: 8, fontSize: '0.72rem', padding: '1px 8px', borderRadius: 10, background: `${chars[entry.createdBy].color || '#4a90d9'}15`, border: `1px solid ${chars[entry.createdBy].color || '#4a90d9'}30`, color: chars[entry.createdBy].color || '#4a90d9' }}>by {chars[entry.createdBy].name}</span>}

                  {navigationHistory.length > 0 && <span style={{ marginLeft: '8px', opacity: 0.6 }}>({navigationHistory.length} in history)</span>}

                </div>

              </div>

            </div>

            <div style={{ display: 'flex', gap: '4px' }}>

              {canEdit && <button style={{ ...S.btn, ...S.btnXs }} onClick={() => togglePin(entry.id)}>{entry.pinned ? '📌' : '📍'}</button>}

              {canEdit && <button style={{ ...S.btn, ...S.btnXs, ...S.btnEdit }} onClick={() => { setEditingEntry(entry); setSelectedEntry(null); }}>✏️</button>}

              <button style={{ ...S.btn, ...S.btnXs }} onClick={closeEntry}>✕</button>

            </div>

          </div>

          

          {/* Content - scrollable */}

          <div style={{ flex: 1, overflowY: 'auto', paddingRight: '8px', overflowWrap: 'break-word', wordBreak: 'break-word' }}>

            {/* Stub banner */}

            {entry.isStub && canEdit && (

              <div style={{ 

                marginBottom: '16px', padding: '10px 14px', 

                background: 'rgba(201,162,39,0.1)', 

                border: '1px dashed rgba(201,162,39,0.4)', 

                borderRadius: '6px',

                display: 'flex', justifyContent: 'space-between', alignItems: 'center',

              }}>

                <span style={{ fontSize: '0.85rem', color: '#c9a227' }}>⚡ Quick-add stub — tap edit to fill in details</span>

                <button 

                  style={{ ...S.btn, ...S.btnXs, ...S.btnEdit }} 

                  onClick={() => { setEditingEntry(entry); setSelectedEntry(null); }}

                >✏️ Fill Details</button>

              </div>

            )}

            {/* Image */}

            {entry.image && (

              <div style={{ marginBottom: '16px', textAlign: 'center' }}>

                <img src={entry.image} alt={entry.name} style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', border: '2px solid #5c4a1f' }} />

              </div>

            )}

            

            {/* Category-specific content */}

            {entry.category === 'creatures' && (

              <div style={{ marginBottom: '16px' }}>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '16px' }}>

                  <div style={{ padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', textAlign: 'center' }}>

                    <div style={{ fontSize: '0.7rem', color: '#888' }}>Type</div>

                    <div style={{ fontWeight: 'bold', color: '#c9a227' }}>{entry.creatureType}</div>

                  </div>

                  <div style={{ padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', textAlign: 'center' }}>

                    <div style={{ fontSize: '0.7rem', color: '#888' }}>Size</div>

                    <div style={{ fontWeight: 'bold', color: '#e8d5b7' }}>{entry.size}</div>

                  </div>

                  <div style={{ padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', textAlign: 'center' }}>

                    <div style={{ fontSize: '0.7rem', color: '#888' }}>CR</div>

                    <div style={{ fontWeight: 'bold', color: '#c92727' }}>{entry.challengeRating}</div>

                  </div>

                </div>

                {entry.isBoss && <div style={{ padding: '8px', background: 'rgba(201,39,39,0.2)', border: '1px solid #c92727', borderRadius: '6px', marginBottom: '12px', textAlign: 'center' }}>👑 <strong>BOSS CREATURE</strong></div>}

                {entry.habitat && <div style={{ marginBottom: '8px' }}><strong style={{ color: '#c9a227' }}>Habitat:</strong> {entry.habitat}</div>}

                {entry.weaknesses?.length > 0 && <div style={{ marginBottom: '8px' }}><strong style={{ color: '#27c95a' }}>Weaknesses:</strong> {entry.weaknesses.join(', ')}</div>}

                {entry.resistances?.length > 0 && <div style={{ marginBottom: '8px' }}><strong style={{ color: '#c9a227' }}>Resistances:</strong> {entry.resistances.join(', ')}</div>}

                {entry.immunities?.length > 0 && <div style={{ marginBottom: '8px' }}><strong style={{ color: '#c92727' }}>Immunities:</strong> {entry.immunities.join(', ')}</div>}

                {entry.abilities && <div style={{ marginBottom: '8px' }}><strong style={{ color: '#4a90d9' }}>Abilities:</strong> {renderWithLinks(entry.abilities)}</div>}

                {entry.loot && <div style={{ marginBottom: '8px' }}><strong style={{ color: '#ffd700' }}>Loot:</strong> {entry.loot}</div>}

                {entry.encounters?.length > 0 && (

                  <div style={{ marginTop: '12px' }}>

                    <strong style={{ color: '#9b59b6' }}>Encounter History:</strong>

                    {entry.encounters.map((enc, i) => (

                      <div key={i} style={{ padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px', marginTop: '6px', fontSize: '0.85rem' }}>

                        <div><strong>{enc.date}</strong> - {enc.outcome}</div>

                        {enc.notes && <div style={{ color: '#888', marginTop: '4px' }}>{enc.notes}</div>}

                      </div>

                    ))}

                  </div>

                )}

              </div>

            )}

            

            {entry.category === 'plants' && (

              <div style={{ marginBottom: '16px' }}>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '16px' }}>

                  <div style={{ padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', textAlign: 'center' }}>

                    <div style={{ fontSize: '0.7rem', color: '#888' }}>Type</div>

                    <div style={{ fontWeight: 'bold', color: '#27c95a' }}>{entry.plantType}</div>

                  </div>

                  <div style={{ padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', textAlign: 'center' }}>

                    <div style={{ fontSize: '0.7rem', color: '#888' }}>Rarity</div>

                    <div style={{ fontWeight: 'bold', color: RARITY_LEVELS.find(r => r.id === entry.rarity)?.color }}>{RARITY_LEVELS.find(r => r.id === entry.rarity)?.name}</div>

                  </div>

                </div>

                {entry.habitat && <div style={{ marginBottom: '8px' }}><strong style={{ color: '#c9a227' }}>Habitat:</strong> {entry.habitat}</div>}

                {entry.effects && <div style={{ marginBottom: '8px' }}><strong style={{ color: '#27c95a' }}>Effects:</strong> {entry.effects}</div>}

                {entry.harvesting && <div style={{ marginBottom: '8px' }}><strong style={{ color: '#4a90d9' }}>Harvesting:</strong> {entry.harvesting}</div>}

                {entry.recipes?.length > 0 && (

                  <div style={{ marginTop: '12px' }}>

                    <strong style={{ color: '#9b59b6' }}>Recipes:</strong>

                    {entry.recipes.map((rec, i) => (

                      <div key={i} style={{ padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px', marginTop: '6px', fontSize: '0.85rem' }}>

                        <div><strong>{rec.name}</strong></div>

                        <div style={{ color: '#888' }}>Ingredients: {rec.ingredients?.join(', ')}</div>

                        <div style={{ color: '#27c95a' }}>Result: {rec.result}</div>

                      </div>

                    ))}

                  </div>

                )}

              </div>

            )}

            

            {entry.category === 'npcs' && (

              <div style={{ marginBottom: '16px' }}>

                {/* Inline strong-label fields — matches the creature / item / quest detail

                    pattern (colored <strong> label + content). This replaces the earlier

                    card-style boxes (🎭 Personality & RP, ⚜️ Affiliations, 📋 Known Info)

                    so NPC entries read like the rest of the journal. Blocks + connections

                    remain as their own collapsible sections below since they serve a

                    different purpose (multi-entry accumulated notes / cross-links). */}

                {/* 2026-04-27: bumped marginBottom from 8px to 14px and

                    added line-height for breathing room — the NPC card

                    was reading as a wall of text. Each strong-label

                    field is now a clearly separated row. Pills get a

                    flex-wrap gap so multi-row tag lists also breathe. */}

                {/* 2026-04-27 (later): location demoted to small metadata

                    text — it's a single bit of context, not body content,

                    so it shouldn't compete visually with Personality /

                    Session Notes / etc. */}

                {entry.currentLocation && (

                  <div style={{ marginBottom: '12px', fontSize: '0.72rem', color: '#888', lineHeight: 1.4 }}>

                    <span style={{ opacity: 0.8 }}>📍</span>{' '}

                    <span

                      style={{ color: '#b87a3a', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textDecorationColor: 'rgba(184,122,58,0.35)', textUnderlineOffset: '2px' }}

                      onClick={() => { const loc = entries.find(e => e.category === 'locations' && e.name?.toLowerCase() === entry.currentLocation?.toLowerCase()); if (loc) handleEntryClick(loc, entry); }}

                    >{entry.currentLocation}</span>

                  </div>

                )}

                {(entry.tags || []).length > 0 && (

                  <div style={{ marginBottom: '14px', lineHeight: 1.6, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>

                    <strong style={{ color: '#c9a227', marginRight: '4px' }}>Tags:</strong>

                    {(entry.tags || []).map((t, i) => (

                      <span key={t} style={{ padding: '2px 10px', borderRadius: '4px', fontSize: '0.7rem', background: 'rgba(201,162,39,0.12)', border: '1px solid rgba(201,162,39,0.3)', color: '#c9a227' }}>{t}</span>

                    ))}

                  </div>

                )}

                {entry.personality && (

                  <div style={{ marginBottom: '14px', lineHeight: 1.6 }}>

                    <strong style={{ color: '#c9a227' }}>Personality:</strong> {renderWithLinks(entry.personality)}

                  </div>

                )}

                {entry.appearance && (

                  <div style={{ marginBottom: '14px', lineHeight: 1.6 }}>

                    <strong style={{ color: '#c9a227' }}>Appearance:</strong> {renderWithLinks(entry.appearance)}

                  </div>

                )}

                {entry.faction && (

                  <div style={{ marginBottom: '14px', lineHeight: 1.6 }}>

                    <strong style={{ color: '#c9a227' }}>Affiliations:</strong> {entry.faction}

                  </div>

                )}

                {entry.knownInfo && (

                  <div style={{ marginBottom: '14px', lineHeight: 1.6 }}>

                    <strong style={{ color: '#c9a227' }}>Known:</strong>{' '}

                    <span style={{ whiteSpace: 'pre-wrap' }}>{renderWithLinks(entry.knownInfo)}</span>

                  </div>

                )}

                {/* 2026-04-27: parse the DM-revealed description into

                    structured sections instead of dumping everything

                    into one Session Notes block.

                    The DM reveal flow (App.js around 41549) joins each

                    revealed REVEAL_FIELDS section into the description

                    text using "{icon} {Label}:" headers. The old single-

                    block render meant "🎒 Inventory:" appeared inline

                    after "Session Notes:", visually attached to it.

                    Now: split on the canonical headers and render each

                    section as its own strong-label field — Inventory

                    sits alongside Story, Profile, etc., matching the

                    quest card hierarchy.

                    Falls back to the single-block render when the

                    description isn't structured (player-typed text or

                    legacy entries without the canonical headers). */}

                {/* 2026-04-27 (later): Session Notes gets a darker

                    inset backdrop so it stands out from the surrounding

                    strong-label fields — it's the most important content.

                    The other parsed sections (Profile, Inventory, etc.)

                    keep the normal inline-row treatment. Spacing below

                    the description block bumped to 22px so the next

                    section (user-created blocks) reads as a distinct

                    region rather than crowding the notes. */}

                {entry.description && (() => {

                  const parsed = parseRevealedDescription(entry.description);

                  const sessionPanelStyle = {

                    marginBottom: '14px',

                    padding: '10px 12px',

                    background: 'rgba(0,0,0,0.28)',

                    borderLeft: '3px solid rgba(201,162,39,0.5)',

                    borderRadius: '4px',

                    lineHeight: 1.6,

                  };

                  if (parsed && Object.keys(parsed.sections).length > 0) {

                    return (

                      <div style={{ marginBottom: '22px' }}>

                        {parsed.leading && (

                          <div style={{ marginBottom: '14px', fontStyle: 'italic', color: '#888', fontSize: '0.85rem', lineHeight: 1.6 }}>{renderWithLinks(parsed.leading)}</div>

                        )}

                        {REVEAL_FIELDS.map(f => {

                          const s = parsed.sections[f.id];

                          if (!s) return null;

                          if (f.id === 'session') {

                            return (

                              <div key={f.id} style={sessionPanelStyle}>

                                <strong style={{ color: '#c9a227' }}>{f.icon} Session Notes:</strong>{' '}

                                <span style={{ whiteSpace: 'pre-wrap' }}>{renderWithLinks(s.content)}</span>

                              </div>

                            );

                          }

                          return (

                            <div key={f.id} style={{ marginBottom: '14px', lineHeight: 1.6 }}>

                              <strong style={{ color: '#c9a227' }}>{f.icon} {f.label}:</strong>{' '}

                              <span style={{ whiteSpace: 'pre-wrap' }}>{renderWithLinks(s.content)}</span>

                            </div>

                          );

                        })}

                      </div>

                    );

                  }

                  return (

                    <div style={{ marginBottom: '22px' }}>

                      <div style={sessionPanelStyle}>

                        <strong style={{ color: '#c9a227' }}>Session Notes:</strong>{' '}

                        <span style={{ whiteSpace: 'pre-wrap' }}>{renderWithLinks(entry.description)}</span>

                      </div>

                    </div>

                  );

                })()}



                {/* 2026-04-27: NPC card body restructured.

                    Order is now:

                      1. Strong-label inline fields above (matches quest cards)

                      2. Categorized note blocks (kept — user explicitly

                         wants section organization preserved)

                      3. Connections (link-list, collapsible)

                      4. + Add Block button at the bottom

                    Was: blocks → Add Block (middle) → Connections, which

                    made the card feel busy and pushed Connections below

                    a non-data control. All sections default expanded;

                    collapse/expand state is per-entry-id and persisted

                    in localStorage (playerCardSections), so reopening a

                    card restores what the user had visible. */}

                {(() => {

                  const allNotes = entry.playerNotes || [];

                  const sectionState = playerCardSections[entry.id] || {};

                  const isOpen = (sectionId) => !(sectionState[sectionId] === true); // default expanded

                  const toggle = (sectionId) => {

                    setPlayerCardSections(prev => {

                      const cur = prev[entry.id] || {};

                      const next = { ...cur, [sectionId]: !(cur[sectionId] === true) ? true : false };

                      // Drop the entry if all defaults — keeps storage tidy.

                      const allDefault = Object.values(next).every(v => v === false);

                      if (allDefault) {

                        const { [entry.id]: _drop, ...rest } = prev;

                        return rest;

                      }

                      return { ...prev, [entry.id]: next };

                    });

                  };



                  const renderNote = (note) => (

                    <div key={note.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '4px 6px', marginBottom: 3, borderLeft: '2px solid rgba(201,162,39,0.3)', borderRadius: 2, background: 'rgba(201,162,39,0.03)' }}>

                      <div style={{ flex: 1, fontSize: '0.75rem', color: '#b0a48a', lineHeight: 1.5 }}>{renderWithLinks(note.text)}</div>

                      <button onClick={() => {

                        const notes = allNotes.filter(n => n.id !== note.id);

                        const nextEntry = { ...entry, playerNotes: notes, updatedAt: new Date().toISOString() };

                        const updated = entries.map(e => e.id === entry.id ? nextEntry : e);

                        upsertJournalEntryFromPage(nextEntry, updated);

                        setSelectedEntry(nextEntry);

                      }} style={{ background: 'none', border: 'none', color: '#ff696140', cursor: 'pointer', fontSize: '0.5rem', flexShrink: 0, padding: '1px 3px' }}>🗑</button>

                    </div>

                  );



                  const renderBlock = (fieldId, icon, label) => {

                    const items = allNotes.filter(n => n.category === fieldId);

                    if (items.length === 0) return null;

                    const open = isOpen(fieldId);

                    return <div key={fieldId} style={{ ...S.card, marginBottom: 8, overflow: 'hidden' }}>

                      <button onClick={() => toggle(fieldId)} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#e8d5b7' }}>

                        <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#c9a227' }}>{icon} {label} <span style={{ fontSize: '0.55rem', color: '#888', fontWeight: 400 }}>({items.length})</span></span>

                        <span style={{ fontSize: '0.6rem', color: '#888' }}>{open ? '▾' : '▸'}</span>

                      </button>

                      {open && <div style={{ padding: '0 10px 10px' }}>{items.map(renderNote)}</div>}

                    </div>;

                  };



                  const addNoteToBlock = (fieldId, text) => {

                    const newNote = { id: generateId(), category: fieldId, text, createdAt: new Date().toISOString() };

                    const notes = [...allNotes, newNote];

                    const nextEntry = { ...entry, playerNotes: notes, updatedAt: new Date().toISOString() };

                    const updated = entries.map(e => e.id === entry.id ? nextEntry : e);

                    upsertJournalEntryFromPage(nextEntry, updated);

                    setSelectedEntry(nextEntry);

                    // Force-expand the block we just added to so the user

                    // sees their note land. Override any saved-collapsed state.

                    setPlayerCardSections(prev => {

                      const cur = prev[entry.id] || {};

                      return { ...prev, [entry.id]: { ...cur, [fieldId]: false } };

                    });

                  };



                  const knownIds = PLAYER_BLOCK_FIELDS.map(f => f.id);

                  const customFields = [...new Set(allNotes.filter(n => !knownIds.includes(n.category)).map(n => n.category))];



                  return <>

                    {PLAYER_BLOCK_FIELDS.map(f => renderBlock(f.id, f.icon, f.label))}

                    {customFields.map(cf => renderBlock(cf, '📝', cf))}



                    {/* 2026-04-27 (later): Connections moved out of this

                        IIFE to a discrete small-text footer below — see

                        sibling render after this block. Keeping it as a

                        boxed card alongside user-created blocks like

                        Profile / Quest was misleading because it implied

                        Connections was user-curated. It's auto-derived

                        cross-link metadata, so it now reads as such. */}



                    {/* + Add Block — moved to bottom so it's not visually

                        between the data sections. Keeps the discoverable

                        "add a section" affordance without splitting the

                        readable areas. */}

                    <div style={{ marginTop: 8 }}>

                      {!showPlayerAddBlock ? (

                        <button onClick={() => { setShowPlayerAddBlock(true); setPlayerAddBlockField('profile'); setPlayerAddBlockText(''); }} style={{ ...S.btn, ...S.btnXs, width: '100%', textAlign: 'center', opacity: 0.6 }}>+ Add Block</button>

                      ) : (

                        <div style={{ ...S.card, padding: 10, width: '100%' }}>

                          <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginBottom: 8 }}>

                            {PLAYER_BLOCK_FIELDS.map(f => {

                              const exists = allNotes.some(n => n.category === f.id);

                              const isSel = playerAddBlockField === f.id;

                              return <button key={f.id} onClick={() => setPlayerAddBlockField(f.id)} style={{ padding: '3px 8px', fontSize: '0.58rem', borderRadius: 5, background: isSel ? 'rgba(201,162,39,0.2)' : exists ? 'rgba(201,162,39,0.06)' : 'rgba(255,255,255,0.02)', border: `1px solid ${isSel ? '#c9a22780' : exists ? '#c9a22725' : '#3a302030'}`, color: isSel ? '#c9a227' : exists ? '#c9a227' : '#888', cursor: 'pointer', fontWeight: isSel ? 600 : 400 }}>{f.icon} {f.label}{exists ? ' +' : ''}</button>;

                            })}

                          </div>

                          {playerAddBlockField && <div style={{ marginBottom: 8 }}>

                            <textarea value={playerAddBlockText} onChange={e => setPlayerAddBlockText(e.target.value)} rows={2} autoFocus placeholder={`Add note to ${(PLAYER_BLOCK_FIELDS.find(f => f.id === playerAddBlockField) || {}).label || playerAddBlockField}...`} style={{ ...S.input, width: '100%', resize: 'vertical', fontSize: '0.75rem' }} onKeyDown={e => {

                              if (e.key === 'Enter' && !e.shiftKey && playerAddBlockText.trim()) {

                                e.preventDefault();

                                addNoteToBlock(playerAddBlockField, playerAddBlockText.trim());

                                setPlayerAddBlockText('');

                                setShowPlayerAddBlock(false); setPlayerAddBlockField(null);

                              }

                            }} />

                          </div>}

                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>

                            <button onClick={() => { setShowPlayerAddBlock(false); setPlayerAddBlockField(null); setPlayerAddBlockText(''); }} style={{ ...S.btn, ...S.btnXs, fontSize: '0.58rem' }}>Cancel</button>

                            <button onClick={() => {

                              if (!playerAddBlockField || !playerAddBlockText.trim()) return;

                              addNoteToBlock(playerAddBlockField, playerAddBlockText.trim());

                              setPlayerAddBlockText(''); setShowPlayerAddBlock(false); setPlayerAddBlockField(null);

                            }} disabled={!playerAddBlockField || !playerAddBlockText.trim()} style={{ ...S.btn, ...S.btnXs, ...S.btnSuccess, fontSize: '0.58rem' }}>Add Note</button>

                          </div>

                        </div>

                      )}

                    </div>

                  </>;

                })()}



                {/* 2026-04-27 (later): Connections rendered here as a

                    discrete footer — small text, dim color, sits at the

                    very bottom of the NPC card. No card chrome (S.card),

                    no collapse toggle. Cross-references to quests, items,

                    NPCs, locations, sells. The user explicitly wanted

                    this to NOT look like a user-created block.

                    `linkedQuests` etc. computed inline since this is

                    outside the user-blocks IIFE that previously held

                    these locals. */}

                {(() => {

                  const npcName = entry.name?.toLowerCase() || '';

                  if (!npcName) return null;

                  const linkedQuests = entries.filter(j => j.category === 'quests' && ((j.description || '').toLowerCase().includes(npcName) || (j.questGiver || '').toLowerCase() === npcName));

                  const linkedItems = entries.filter(j => j.category === 'items' && (j.description || '').toLowerCase().includes(npcName));

                  const linkedNpcs = entries.filter(j => j.category === 'npcs' && j.id !== entry.id && (j.description || '').toLowerCase().includes(npcName));

                  const questDirect = (entry.questConnections || []).map(q => ({ name: q, source: 'direct' }));

                  const questMentioned = linkedQuests

                    .filter(q => !(entry.questConnections || []).some(qc => qc.toLowerCase() === q.name?.toLowerCase()))

                    .map(q => ({ name: q.name, id: q.id, source: 'mentioned' }));

                  const sellsItems = (entry.sells || []);

                  const hasAny = questDirect.length || questMentioned.length || linkedItems.length || linkedNpcs.length || sellsItems.length;

                  if (!hasAny) return null;

                  const link = (color, icon, label, onClick, suffix) => (

                    <span

                      key={`${color}-${label}`}

                      onClick={onClick}

                      style={{ cursor: onClick ? 'pointer' : 'default', color, textDecoration: 'underline', textDecorationStyle: 'dotted', textDecorationColor: color + '55', textUnderlineOffset: '2px', whiteSpace: 'nowrap' }}

                    >

                      <span style={{ opacity: 0.7, marginRight: 2 }}>{icon}</span>{label}{suffix && <span style={{ opacity: 0.55, marginLeft: 3, fontSize: '0.85em' }}>{suffix}</span>}

                    </span>

                  );

                  return (

                    <div style={{ marginTop: '20px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.05)', fontSize: '0.65rem', color: '#666', lineHeight: 1.5 }}>

                      <span style={{ marginRight: 6, opacity: 0.7 }}>🧭 Connections:</span>

                      <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '4px 12px', verticalAlign: 'middle' }}>

                        {questDirect.map(q => link('#9b59b6', '📜', q.name, () => { const quest = entries.find(j => j.category === 'quests' && j.name?.toLowerCase() === q.name?.toLowerCase()); if (quest) handleEntryClick(quest, entry); }))}

                        {questMentioned.map(q => link('#9b59b6', '📜', q.name, () => { const quest = entries.find(j => j.category === 'quests' && j.name?.toLowerCase() === q.name?.toLowerCase()); if (quest) handleEntryClick(quest, entry); }, 'mentioned'))}

                        {sellsItems.map(it => link('#d97429', '🎒', it, () => { const e = entries.find(en => (en.category === 'items' || en.category === 'plants') && en.name?.toLowerCase() === it.toLowerCase()); if (e) handleEntryClick(e, entry); }))}

                        {linkedItems.map(it => link('#d97429', '🎒', it.name, () => handleEntryClick(it, entry)))}

                        {linkedNpcs.map(np => link('#4a90d9', '👤', np.name, () => handleEntryClick(np, entry)))}

                      </span>

                    </div>

                  );

                })()}

              </div>

            )}



            {entry.category === 'quests' && (

              <div style={{ marginBottom: '16px' }}>

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>

                  <span style={{ padding: '6px 12px', background: `${QUEST_STATUSES.find(s => s.id === entry.status)?.color}30`, borderRadius: '6px', color: QUEST_STATUSES.find(s => s.id === entry.status)?.color, fontWeight: 'bold' }}>

                    {QUEST_STATUSES.find(s => s.id === entry.status)?.icon} {QUEST_STATUSES.find(s => s.id === entry.status)?.name}

                  </span>

                  {(() => {

                    const p = QUEST_PRIORITIES.find(p => p.id === entry.priority);

                    if (!p) return null;

                    return (

                      <span style={{ padding: '6px 12px', background: `${p.color}30`, borderRadius: '6px', color: p.color }}>

                        {p.icon} {p.name}

                      </span>

                    );

                  })()}

                </div>

                {entry.questGiver && (

                  <div style={{ marginBottom: '8px' }}>

                    <strong style={{ color: '#4a90d9' }}>Quest Giver:</strong>{' '}

                    <span 

                      style={{ cursor: 'pointer', color: '#4a90d9', textDecoration: 'underline', textDecorationStyle: 'dotted', textDecorationColor: 'rgba(74,144,217,0.35)', textUnderlineOffset: '3px', textDecorationThickness: '1px' }}

                      onClick={() => {

                        const npcEntry = entries.find(e => e.category === 'npcs' && e.name.toLowerCase() === entry.questGiver.toLowerCase());

                        if (npcEntry) handleEntryClick(npcEntry, entry);

                      }}

                      onMouseEnter={(e) => {

                        const npcEntry = entries.find(en => en.category === 'npcs' && en.name.toLowerCase() === entry.questGiver.toLowerCase());

                        if (npcEntry && settings.showLinkTooltips) handleLinkHover(npcEntry, e);

                      }}

                      onMouseLeave={handleLinkLeave}

                    >{entry.questGiver}</span>

                  </div>

                )}

                {entry.objectives?.length > 0 && (

                  <div style={{ marginBottom: '12px' }}>

                    <strong style={{ color: '#c9a227' }}>Objectives:</strong>

                    {entry.objectives.map((obj, i) => (

                      <div 

                        key={i} 

                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px', marginTop: '4px', cursor: canEdit ? 'pointer' : 'default' }}

                        onClick={() => {

                          if (!canEdit) return;

                          const updated = [...(entry.objectives || [])];

                          updated[i] = { ...updated[i], completed: !updated[i].completed };

                          const nextEntry = { ...entry, objectives: updated, updatedAt: new Date().toISOString() };

                          const updatedEntries = entries.map(e => e.id === entry.id ? nextEntry : e);

                          upsertJournalEntryFromPage(nextEntry, updatedEntries);

                          setSelectedEntry(prev => prev && prev.id === entry.id ? nextEntry : prev);

                        }}

                      >

                        <span style={{ color: obj.completed ? '#27c95a' : '#888', fontSize: '1.1rem', userSelect: 'none' }}>{obj.completed ? '☑' : '☐'}</span>

                        <span style={{ textDecoration: obj.completed ? 'line-through' : 'none', color: obj.completed ? '#666' : '#e8d5b7', flex: 1 }}>{renderWithLinks(obj.text)}</span>

                      </div>

                    ))}

                  </div>

                )}

                

                {/* Related NPCs */}

                {(entry.relatedNpcs || []).length > 0 && (

                  <div style={{ marginBottom: '8px' }}>

                    <strong style={{ color: '#4a90d9' }}>👤 Related NPCs:</strong>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>

                      {entry.relatedNpcs.map((npc, i) => (

                        <span 

                          key={i} 

                          style={{ padding: '2px 8px', background: 'rgba(74,144,217,0.2)', borderRadius: '4px', fontSize: '0.85rem', cursor: 'pointer' }}

                          onClick={() => {

                            const npcEntry = entries.find(e => e.category === 'npcs' && e.name.toLowerCase() === npc.toLowerCase());

                            if (npcEntry) handleEntryClick(npcEntry, entry);

                          }}

                          onMouseEnter={(e) => {

                            const npcEntry = entries.find(en => en.category === 'npcs' && en.name.toLowerCase() === npc.toLowerCase());

                            if (npcEntry && settings.showLinkTooltips) handleLinkHover(npcEntry, e);

                          }}

                          onMouseLeave={handleLinkLeave}

                        >{npc}</span>

                      ))}

                    </div>

                  </div>

                )}

                

                {/* Related Items */}

                {(entry.relatedItems || []).length > 0 && (

                  <div style={{ marginBottom: '8px' }}>

                    <strong style={{ color: '#d97429' }}>🎒 Related Items:</strong>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>

                      {entry.relatedItems.map((item, i) => (

                        <span 

                          key={i} 

                          style={{ padding: '2px 8px', background: 'rgba(217,116,41,0.2)', borderRadius: '4px', fontSize: '0.85rem', cursor: 'pointer' }}

                          onClick={() => {

                            const itemEntry = entries.find(e => e.category === 'items' && e.name.toLowerCase() === item.toLowerCase());

                            if (itemEntry) handleEntryClick(itemEntry, entry);

                          }}

                          onMouseEnter={(e) => {

                            const itemEntry = entries.find(en => en.category === 'items' && en.name.toLowerCase() === item.toLowerCase());

                            if (itemEntry && settings.showLinkTooltips) handleLinkHover(itemEntry, e);

                          }}

                          onMouseLeave={handleLinkLeave}

                        >{item}</span>

                      ))}

                    </div>

                  </div>

                )}

                

                {entry.rewards && <div style={{ marginBottom: '8px', padding: '8px', background: 'rgba(255,215,0,0.1)', borderRadius: '4px', borderLeft: '3px solid #ffd700' }}><strong style={{ color: '#ffd700' }}>🎁 Rewards:</strong> {entry.rewards}</div>}

              </div>

            )}

            

            {entry.category === 'session' && (

              <div style={{ marginBottom: '16px' }}>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '16px' }}>

                  <div style={{ padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', textAlign: 'center' }}>

                    <div style={{ fontSize: '0.7rem', color: '#888' }}>Session</div>

                    <div style={{ fontWeight: 'bold', color: '#c9a227', fontSize: '1.2rem' }}>#{entry.sessionNumber}</div>

                  </div>

                  <div style={{ padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', textAlign: 'center' }}>

                    <div style={{ fontSize: '0.7rem', color: '#888' }}>Date</div>

                    <div style={{ fontWeight: 'bold', color: '#e8d5b7' }}>{entry.sessionDate}</div>

                  </div>

                </div>

                {entry.summary && <div style={{ marginBottom: '12px', padding: '12px', background: 'rgba(201,162,39,0.1)', borderRadius: '6px', borderLeft: '3px solid #c9a227', lineHeight: '1.5', overflowWrap: 'break-word', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{renderWithLinks(entry.summary.replace(/\n{3,}/g, '\n\n'))}</div>}

                {/* Session Public Notes */}

                {(entry.publicNotes || []).length > 0 && (

                  <div style={{ marginBottom: '12px', padding: '10px', borderLeft: '3px solid rgba(74,144,217,0.4)', borderRadius: '0 6px 6px 0' }}>

                    <strong style={{ color: '#4a90d9', fontSize: '0.85rem' }}>📝 Aside</strong>

                    {(entry.publicNotes || []).map((note, i) => (

                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '4px', marginTop: '4px', lineHeight: '1.5' }}>

                        <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>

                          {note.text}

                          <span style={{ fontSize: '0.65rem', color: '#888', marginLeft: '6px' }}>

                            — {(chars || {})[note.charId]?.name || 'player'}

                          </span>

                        </span>

                      </div>

                    ))}

                  </div>

                )}

                {/* Session Private Aside */}

                {(() => {

                  const myNotes = (entry.privateNotes || []).filter(n => n.charId === currentCharId || (isDM && !n.noDM) || (n.sharedWith || []).includes(currentCharId));

                  if (myNotes.length === 0) return null;

                  return (

                    <div style={{

                      marginBottom: '12px', padding: '10px',

                      borderLeft: '3px solid rgba(138, 79, 255, 0.4)',

                      borderRadius: '0 6px 6px 0',

                    }}>

                      <strong style={{ color: '#8a4fff', fontSize: '0.85rem' }}>🔒 Private Aside</strong>

                      {myNotes.map((note, i) => {

                        const isShared = (note.sharedWith || []).length > 0;

                        const noteColor = isShared ? '#8fd4d4' : '#c4a8ff';

                        const sharedNames = isShared ? note.sharedWith.map(sid => (chars || {})[sid]?.name || sid) : [];

                        const tooltipText = isShared ? `Shared with: ${sharedNames.join(', ')}${note.noDM ? '' : (isDM ? '' : ' and DM')}` : (note.noDM ? 'Private — only visible to you (DM excluded)' : 'Private — only visible to you and DM');

                        const emoji = isShared ? '👥' : null;

                        const borderCol = isShared ? '#6bbfbf' : '#8a4fff';

                        return (

                          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '4px', marginTop: '4px', color: noteColor, lineHeight: '1.5' }}>

                            {emoji ? (

                              <Tooltip text={tooltipText} special borderColor={borderCol}>

                                <span style={{ cursor: 'help', opacity: 0.85, flexShrink: 0, fontSize: '0.65em' }}>{emoji}</span>

                              </Tooltip>

                            ) : null}

                            <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>

                              {note.text}

                              {isDM && note.charId !== currentCharId && (

                                <span style={{ fontSize: '0.65rem', color: '#888', marginLeft: '6px' }}>

                                  — {(chars || {})[note.charId]?.name || 'player'}

                                </span>

                              )}

                            </span>

                          </div>

                        );

                      })}

                    </div>

                  );

                })()}

                {entry.keyEvents?.length > 0 && (

                  <div style={{ marginBottom: '12px' }}>

                    <strong style={{ color: '#c9a227' }}>Key Events:</strong>

                    {entry.keyEvents.map((ev, i) => (

                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px', background: ev.important ? 'rgba(201,162,39,0.15)' : 'rgba(0,0,0,0.2)', borderRadius: '4px', marginTop: '4px', borderLeft: ev.important ? '3px solid #ffd700' : 'none' }}>

                        {ev.important && <span>⭐</span>}

                        <span>{renderWithLinks(ev.event)}</span>

                      </div>

                    ))}

                  </div>

                )}

                {/* NPCs Encountered - with links */}

                {entry.npcsEncountered?.length > 0 && (

                  <div style={{ marginBottom: '8px' }}>

                    <strong style={{ color: '#4a90d9' }}>👤 NPCs:</strong>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>

                      {entry.npcsEncountered.map((npc, i) => {

                        const npcEntry = entries.find(e => e.category === 'npcs' && e.name.toLowerCase() === npc.toLowerCase());

                        return (

                          <span 

                            key={i} 

                            style={{ padding: '2px 8px', background: npcEntry ? 'rgba(74,144,217,0.2)' : 'rgba(0,0,0,0.2)', borderRadius: '4px', fontSize: '0.85rem', cursor: npcEntry ? 'pointer' : 'default', color: npcEntry ? '#4a90d9' : '#888' }}

                            onClick={() => npcEntry && handleEntryClick(npcEntry, entry)}

                            onMouseEnter={(e) => npcEntry && settings.showLinkTooltips && handleLinkHover(npcEntry, e)}

                            onMouseLeave={handleLinkLeave}

                          >{npc}</span>

                        );

                      })}

                    </div>

                  </div>

                )}

                {/* Creatures Encountered - with links */}

                {entry.creaturesEncountered?.length > 0 && (

                  <div style={{ marginBottom: '8px' }}>

                    <strong style={{ color: '#c92727' }}>🐾 Creatures:</strong>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>

                      {entry.creaturesEncountered.map((creature, i) => {

                        const creatureEntry = entries.find(e => e.category === 'creatures' && e.name.toLowerCase() === creature.toLowerCase());

                        return (

                          <span 

                            key={i} 

                            style={{ padding: '2px 8px', background: creatureEntry ? 'rgba(201,39,39,0.2)' : 'rgba(0,0,0,0.2)', borderRadius: '4px', fontSize: '0.85rem', cursor: creatureEntry ? 'pointer' : 'default', color: creatureEntry ? '#c92727' : '#888' }}

                            onClick={() => creatureEntry && handleEntryClick(creatureEntry, entry)}

                            onMouseEnter={(e) => creatureEntry && settings.showLinkTooltips && handleLinkHover(creatureEntry, e)}

                            onMouseLeave={handleLinkLeave}

                          >{creature}</span>

                        );

                      })}

                    </div>

                  </div>

                )}

                {/* Items Found - with links */}

                {entry.itemsFound?.length > 0 && (

                  <div style={{ marginBottom: '8px' }}>

                    <strong style={{ color: '#ffd700' }}>✨ Items:</strong>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>

                      {entry.itemsFound.map((item, i) => {

                        const itemEntry = entries.find(e => e.category === 'items' && e.name.toLowerCase() === item.toLowerCase());

                        return (

                          <span 

                            key={i} 

                            style={{ padding: '2px 8px', background: itemEntry ? 'rgba(217,116,41,0.2)' : 'rgba(255,215,0,0.1)', borderRadius: '4px', fontSize: '0.85rem', cursor: itemEntry ? 'pointer' : 'default', color: itemEntry ? '#d97429' : '#ffd700' }}

                            onClick={() => itemEntry && handleEntryClick(itemEntry, entry)}

                            onMouseEnter={(e) => itemEntry && settings.showLinkTooltips && handleLinkHover(itemEntry, e)}

                            onMouseLeave={handleLinkLeave}

                          >{item}</span>

                        );

                      })}

                    </div>

                  </div>

                )}

                {entry.playerNotes && <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(74,144,217,0.1)', borderRadius: '6px', borderLeft: '3px solid #4a90d9' }}><strong style={{ color: '#4a90d9' }}>📝 Notes:</strong><br/>{entry.playerNotes}</div>}

              </div>

            )}

            

            {/* Items Detail */}

            {entry.category === 'items' && (

              <div style={{ marginBottom: '16px' }}>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '16px' }}>

                  <div style={{ padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', textAlign: 'center' }}>

                    <div style={{ fontSize: '0.7rem', color: '#888' }}>Type</div>

                    <div style={{ fontWeight: 'bold', color: '#d97429' }}>{entry.itemType}</div>

                  </div>

                  <div style={{ padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', textAlign: 'center' }}>

                    <div style={{ fontSize: '0.7rem', color: '#888' }}>Rarity</div>

                    <div style={{ fontWeight: 'bold', color: RARITY_LEVELS.find(r => r.id === entry.rarity)?.color || '#888' }}>{RARITY_LEVELS.find(r => r.id === entry.rarity)?.name}</div>

                  </div>

                  <div style={{ padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', textAlign: 'center' }}>

                    <div style={{ fontSize: '0.7rem', color: '#888' }}>Value</div>

                    <div style={{ fontWeight: 'bold', color: '#ffd700' }}>{entry.value || '—'}</div>

                  </div>

                </div>

                {entry.isQuestItem && <div style={{ padding: '8px', background: 'rgba(155,89,182,0.2)', border: '1px solid #9b59b6', borderRadius: '6px', marginBottom: '12px', textAlign: 'center' }}>📜 <strong>QUEST ITEM</strong></div>}

                {entry.effects && <div style={{ marginBottom: '8px', padding: '8px', background: 'rgba(201,162,39,0.1)', borderRadius: '4px', borderLeft: '3px solid #c9a227' }}><strong style={{ color: '#c9a227' }}>Effects:</strong> {entry.effects}</div>}

                {(entry.locations || []).length > 0 && (

                  <div style={{ marginBottom: '8px' }}>

                    <strong style={{ color: '#4a90d9' }}>📍 Found at:</strong>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>

                      {entry.locations.map((loc, i) => (

                        <span key={i} style={{ padding: '2px 8px', background: 'rgba(74,144,217,0.2)', borderRadius: '4px', fontSize: '0.85rem' }}>{renderWithLinks(loc)}</span>

                      ))}

                    </div>

                  </div>

                )}

                {(entry.soldAt || []).length > 0 && (

                  <div style={{ marginBottom: '8px' }}>

                    <strong style={{ color: '#27c95a' }}>🛒 Sold by:</strong>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>

                      {entry.soldAt.map((npc, i) => (

                        <span key={i} style={{ padding: '2px 8px', background: 'rgba(39,201,90,0.2)', borderRadius: '4px', fontSize: '0.85rem' }}>{renderWithLinks(npc)}</span>

                      ))}

                    </div>

                  </div>

                )}

                {(entry.usedInRecipes || []).length > 0 && (

                  <div style={{ marginBottom: '8px' }}>

                    <strong style={{ color: '#17a589' }}>📋 Used in recipes:</strong>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>

                      {entry.usedInRecipes.map((recipe, i) => (

                        <span key={i} style={{ padding: '2px 8px', background: 'rgba(23,165,137,0.2)', borderRadius: '4px', fontSize: '0.85rem', cursor: 'pointer' }} onClick={() => {

                          const recipeEntry = entries.find(e => e.category === 'recipes' && e.name.toLowerCase() === recipe.toLowerCase());

                          if (recipeEntry) handleEntryClick(recipeEntry, entry);

                        }}>{recipe}</span>

                      ))}

                    </div>

                  </div>

                )}

                {(entry.relatedQuests || []).length > 0 && (

                  <div style={{ marginBottom: '8px' }}>

                    <strong style={{ color: '#9b59b6' }}>📜 Related quests:</strong>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>

                      {entry.relatedQuests.map((quest, i) => (

                        <span key={i} style={{ padding: '2px 8px', background: 'rgba(155,89,182,0.2)', borderRadius: '4px', fontSize: '0.85rem', cursor: 'pointer' }} onClick={() => {

                          const questEntry = entries.find(e => e.category === 'quests' && e.name.toLowerCase() === quest.toLowerCase());

                          if (questEntry) handleEntryClick(questEntry, entry);

                        }}>{quest}</span>

                      ))}

                    </div>

                  </div>

                )}

              </div>

            )}

            

            {/* Recipes Detail */}

            {entry.category === 'recipes' && (

              <div style={{ marginBottom: '16px' }}>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '16px' }}>

                  <div style={{ padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', textAlign: 'center' }}>

                    <div style={{ fontSize: '0.7rem', color: '#888' }}>Type</div>

                    <div style={{ fontWeight: 'bold', color: '#17a589' }}>{entry.recipeType}</div>

                  </div>

                  <div style={{ padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', textAlign: 'center' }}>

                    <div style={{ fontSize: '0.7rem', color: '#888' }}>Crafting DC</div>

                    <div style={{ fontWeight: 'bold', color: '#c9a227' }}>{entry.craftingDC || '—'}</div>

                  </div>

                  <div style={{ padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', textAlign: 'center' }}>

                    <div style={{ fontSize: '0.7rem', color: '#888' }}>Time</div>

                    <div style={{ fontWeight: 'bold', color: '#e8d5b7' }}>{entry.craftingTime || '—'}</div>

                  </div>

                </div>

                

                {/* Ingredients */}

                {(entry.ingredients || []).length > 0 && (

                  <div style={{ marginBottom: '12px', padding: '12px', background: 'rgba(23,165,137,0.1)', borderRadius: '6px', border: '1px solid rgba(23,165,137,0.3)' }}>

                    <strong style={{ color: '#17a589' }}>🧪 Ingredients:</strong>

                    <div style={{ marginTop: '8px' }}>

                      {entry.ingredients.map((ing, i) => (

                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px', marginTop: '4px' }}>

                          <span style={{ color: '#ffd700', fontWeight: 'bold', minWidth: '30px' }}>{ing.quantity}x</span>

                          <span 

                            style={{ cursor: 'pointer', color: '#d97429', textDecoration: 'underline', textDecorationStyle: 'dotted', textDecorationColor: 'rgba(217,116,41,0.35)', textUnderlineOffset: '3px', textDecorationThickness: '1px' }}

                            onClick={() => {

                              const itemEntry = entries.find(e => (e.category === 'items' || e.category === 'plants') && e.name.toLowerCase() === ing.name.toLowerCase());

                              if (itemEntry) handleEntryClick(itemEntry, entry);

                            }}

                            onMouseEnter={(e) => {

                              const itemEntry = entries.find(en => (en.category === 'items' || en.category === 'plants') && en.name.toLowerCase() === ing.name.toLowerCase());

                              if (itemEntry && settings.showLinkTooltips) handleLinkHover(itemEntry, e);

                            }}

                            onMouseLeave={handleLinkLeave}

                          >

                            {ing.name}

                          </span>

                          {ing.optional && <span style={{ fontSize: '0.75rem', color: '#888', fontStyle: 'italic' }}>(optional)</span>}

                        </div>

                      ))}

                    </div>

                  </div>

                )}

                

                {entry.result && <div style={{ marginBottom: '8px', padding: '8px', background: 'rgba(255,215,0,0.1)', borderRadius: '4px', borderLeft: '3px solid #ffd700' }}><strong style={{ color: '#ffd700' }}>✨ Creates:</strong> {entry.result} {entry.resultQuantity > 1 && `(x${entry.resultQuantity})`}</div>}

                {entry.effects && <div style={{ marginBottom: '8px', padding: '8px', background: 'rgba(201,162,39,0.1)', borderRadius: '4px', borderLeft: '3px solid #c9a227' }}><strong style={{ color: '#c9a227' }}>Effects:</strong> {entry.effects}</div>}

                {entry.duration && <div style={{ marginBottom: '8px' }}><strong style={{ color: '#888' }}>Duration:</strong> {entry.duration}</div>}

                

                {(entry.curesDiseases || []).length > 0 && (

                  <div style={{ marginBottom: '8px', padding: '8px', background: 'rgba(142,68,173,0.15)', borderRadius: '4px', border: '1px solid #8e44ad' }}>

                    <strong style={{ color: '#8e44ad', display: 'inline-flex', alignItems: 'center', gap: 3 }}><BiohazardIcon size={13} color='#8e44ad' /> Cures diseases:</strong>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>

                      {entry.curesDiseases.map((disease, i) => (

                        <span key={i} style={{ padding: '2px 8px', background: 'rgba(142,68,173,0.2)', borderRadius: '4px', fontSize: '0.85rem', cursor: 'pointer' }} onClick={() => {

                          const diseaseEntry = entries.find(e => e.category === 'diseases' && e.name.toLowerCase() === disease.toLowerCase());

                          if (diseaseEntry) handleEntryClick(diseaseEntry, entry);

                        }}>{disease}</span>

                      ))}

                    </div>

                  </div>

                )}

                

                {entry.craftingTools && <div style={{ marginBottom: '8px' }}><strong style={{ color: '#888' }}>🔧 Tools needed:</strong> {entry.craftingTools}</div>}

                {entry.learnedFrom && <div style={{ marginBottom: '8px' }}><strong style={{ color: '#4a90d9' }}>📚 Learned from:</strong> {renderWithLinks(entry.learnedFrom)}</div>}

              </div>

            )}

            

            {/* Diseases Detail */}

            {entry.category === 'diseases' && (

              <div style={{ marginBottom: '16px' }}>

                <div style={{ padding: '10px', background: `${DISEASE_SEVERITIES.find(s => s.id === entry.severity)?.color}20`, border: `2px solid ${DISEASE_SEVERITIES.find(s => s.id === entry.severity)?.color}`, borderRadius: '6px', marginBottom: '16px', textAlign: 'center' }}>

                  <div style={{ fontSize: '0.7rem', color: '#888' }}>Severity</div>

                  <div style={{ fontWeight: 'bold', color: DISEASE_SEVERITIES.find(s => s.id === entry.severity)?.color, fontSize: '1.1rem' }}>

                    {DISEASE_SEVERITIES.find(s => s.id === entry.severity)?.name || 'Unknown'}

                  </div>

                  <div style={{ fontSize: '0.75rem', color: '#888' }}>{DISEASE_SEVERITIES.find(s => s.id === entry.severity)?.description}</div>

                </div>

                

                {/* Symptoms */}

                {(entry.symptoms || []).length > 0 && (

                  <div style={{ marginBottom: '12px', padding: '12px', background: 'rgba(142,68,173,0.1)', borderRadius: '6px', border: '1px solid rgba(142,68,173,0.3)' }}>

                    <strong style={{ color: '#8e44ad' }}>🤒 Symptoms:</strong>

                    <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>

                      {entry.symptoms.map((symptom, i) => (

                        <span key={i} style={{ padding: '4px 8px', background: 'rgba(142,68,173,0.2)', borderRadius: '4px', fontSize: '0.85rem' }}>• {symptom}</span>

                      ))}

                    </div>

                  </div>

                )}

                

                {entry.transmission && <div style={{ marginBottom: '8px' }}><strong style={{ color: '#c92727' }}>🔄 Transmission:</strong> {entry.transmission}</div>}

                {entry.incubation && <div style={{ marginBottom: '8px' }}><strong style={{ color: '#c9a227' }}>⏱️ Incubation:</strong> {entry.incubation}</div>}

                {entry.progression && <div style={{ marginBottom: '8px', padding: '8px', background: 'rgba(201,39,39,0.1)', borderRadius: '4px', borderLeft: '3px solid #c92727' }}><strong style={{ color: '#c92727' }}>📈 Progression:</strong> {entry.progression}</div>}

                

                {/* Effects/Debuffs */}

                {(entry.effects || []).length > 0 && (

                  <div style={{ marginBottom: '12px' }}>

                    <strong style={{ color: '#c92727' }}>⚠️ Effects:</strong>

                    <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>

                      {entry.effects.map((eff, i) => (

                        <span key={i} style={{ padding: '2px 8px', background: 'rgba(201,39,39,0.2)', borderRadius: '4px', fontSize: '0.85rem', color: '#ff6666' }}>{eff}</span>

                      ))}

                    </div>

                  </div>

                )}

                

                {/* Cures */}

                {(entry.curedBy || []).length > 0 && (

                  <div style={{ marginBottom: '12px', padding: '12px', background: 'rgba(39,201,90,0.1)', borderRadius: '6px', border: '1px solid rgba(39,201,90,0.3)' }}>

                    <strong style={{ color: '#27c95a' }}>💚 Cured by:</strong>

                    <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>

                      {entry.curedBy.map((cure, i) => (

                        <span 

                          key={i} 

                          style={{ padding: '4px 10px', background: 'rgba(39,201,90,0.2)', borderRadius: '4px', fontSize: '0.85rem', cursor: 'pointer', border: '1px solid #27c95a' }}

                          onClick={() => {

                            const cureEntry = entries.find(e => (e.category === 'recipes' || e.category === 'items') && e.name.toLowerCase() === cure.toLowerCase());

                            if (cureEntry) handleEntryClick(cureEntry, entry);

                          }}

                          onMouseEnter={(e) => {

                            const cureEntry = entries.find(en => (en.category === 'recipes' || en.category === 'items') && en.name.toLowerCase() === cure.toLowerCase());

                            if (cureEntry && settings.showLinkTooltips) handleLinkHover(cureEntry, e);

                          }}

                          onMouseLeave={handleLinkLeave}

                        >

                          {cure}

                        </span>

                      ))}

                    </div>

                  </div>

                )}

                

                {(entry.locations || []).length > 0 && (

                  <div style={{ marginBottom: '8px' }}>

                    <strong style={{ color: '#4a90d9' }}>📍 Common in:</strong>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>

                      {entry.locations.map((loc, i) => (

                        <span key={i} style={{ padding: '2px 8px', background: 'rgba(74,144,217,0.2)', borderRadius: '4px', fontSize: '0.85rem' }}>{loc}</span>

                      ))}

                    </div>

                  </div>

                )}

                

                {(entry.creatures || []).length > 0 && (

                  <div style={{ marginBottom: '8px' }}>

                    <strong style={{ color: '#c92727' }}>🐾 Spread by:</strong>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>

                      {entry.creatures.map((creature, i) => (

                        <span key={i} style={{ padding: '2px 8px', background: 'rgba(201,39,39,0.2)', borderRadius: '4px', fontSize: '0.85rem', cursor: 'pointer' }} onClick={() => {

                          const creatureEntry = entries.find(e => e.category === 'creatures' && e.name.toLowerCase() === creature.toLowerCase());

                          if (creatureEntry) handleEntryClick(creatureEntry, entry);

                        }}>{creature}</span>

                      ))}

                    </div>

                  </div>

                )}

                

                {entry.notes && <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(74,144,217,0.1)', borderRadius: '6px', borderLeft: '3px solid #4a90d9' }}><strong style={{ color: '#4a90d9' }}>📝 Notes:</strong><br/>{entry.notes}</div>}

              </div>

            )}

            

            {/* Description — skip for NPCs (shown in Session Notes card) */}

            {entry.description && entry.category !== 'npcs' && (

              <div style={{ marginBottom: '16px' }}>

                <strong style={{ color: '#c9a227' }}>Description:</strong>

                <div style={{ marginTop: '4px', lineHeight: '1.6', whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'break-word' }}>

                  {renderWithLinks(entry.description)}

                </div>

              </div>

            )}

            

            {/* Public Notes */}

            {(entry.publicNotes || []).length > 0 && (

              <div style={{ marginBottom: '16px', padding: '10px', borderLeft: '3px solid rgba(74,144,217,0.4)', borderRadius: '0 6px 6px 0' }}>

                <strong style={{ color: '#4a90d9', fontSize: '0.85rem' }}>📝 Aside</strong>

                {(entry.publicNotes || []).map((note, i) => (

                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '4px', marginTop: '4px', lineHeight: '1.5' }}>

                    <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>

                      {renderWithLinks(note.text)}

                      <span style={{ fontSize: '0.65rem', color: '#888', marginLeft: '6px' }}>

                        — {(chars || {})[note.charId]?.name || 'player'}

                      </span>

                    </span>

                  </div>

                ))}

              </div>

            )}



            {/* Private Aside */}

            {(() => {

              const myNotes = (entry.privateNotes || []).filter(n => n.charId === currentCharId || (isDM && !n.noDM) || (n.sharedWith || []).includes(currentCharId));

              if (myNotes.length === 0) return null;

              return (

                <div style={{

                  marginBottom: '16px', padding: '10px',

                  borderLeft: '3px solid rgba(138, 79, 255, 0.4)',

                  borderRadius: '0 6px 6px 0',

                }}>

                  <strong style={{ color: '#8a4fff', fontSize: '0.85rem' }}>🔒 Private Aside</strong>

                  {myNotes.map((note, i) => {

                    const isShared = (note.sharedWith || []).length > 0;

                    const noteColor = isShared ? '#8fd4d4' : '#c4a8ff';

                    const sharedNames = isShared ? note.sharedWith.map(sid => (chars || {})[sid]?.name || sid) : [];

                    const tooltipText = isShared ? `Shared with: ${sharedNames.join(', ')}${note.noDM ? '' : (isDM ? '' : ' and DM')}` : (note.noDM ? 'Private — only visible to you (DM excluded)' : 'Private — only visible to you and DM');

                    const emoji = isShared ? '👥' : null;

                    const borderCol = isShared ? '#6bbfbf' : '#8a4fff';

                    return (

                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '4px', marginTop: '4px', color: noteColor, lineHeight: '1.5' }}>

                        {emoji ? (

                          <Tooltip text={tooltipText} special borderColor={borderCol}>

                            <span style={{ cursor: 'help', opacity: 0.85, flexShrink: 0, fontSize: '0.65em' }}>{emoji}</span>

                          </Tooltip>

                        ) : null}

                        <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>

                          {renderWithLinks(note.text)}

                          {isDM && note.charId !== currentCharId && (

                            <span style={{ fontSize: '0.65rem', color: '#888', marginLeft: '6px' }}>

                              — {(chars || {})[note.charId]?.name || 'player'}

                            </span>

                          )}

                        </span>

                      </div>

                    );

                  })}

                </div>

              );

            })()}

            

            {/* Detected Links */}

            {(() => {

              const textFields = [entry.description, entry.summary, entry.abilities, entry.knownInfo, entry.personality].filter(Boolean).join(' ');

              const detectedLinks = findMatchingEntries(textFields, entries, entry.id);

              if (detectedLinks.length === 0) return null;

              

              return (

                <div style={{ marginBottom: '12px', padding: '6px 8px', background: 'rgba(74,144,217,0.04)', borderRadius: '4px', border: '1px solid rgba(74,144,217,0.1)' }}>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>

                    <span style={{ color: '#4a90d9', fontSize: '0.6rem', opacity: 0.6 }}>🔗 {detectedLinks.length} linked</span>

                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>

                    {detectedLinks.map((link, i) => {

                      const isUnlinked = (entry.unlinkedNames || []).includes(link.entryName);

                      return (

                        <div 

                          key={i}

                          style={{

                            display: 'flex',

                            alignItems: 'center',

                            gap: '3px',

                            padding: '2px 6px',

                            background: isUnlinked ? 'rgba(0,0,0,0.2)' : `${JOURNAL_CATEGORIES[link.category]?.color}10`,

                            border: `1px solid ${isUnlinked ? 'rgba(255,255,255,0.06)' : `${JOURNAL_CATEGORIES[link.category]?.color}25`}`,

                            borderRadius: '3px',

                            fontSize: '0.65rem',

                            opacity: isUnlinked ? 0.4 : 0.7,

                          }}

                        >

                          <span style={{ cursor: 'pointer' }} onClick={() => handleEntryClick(entries.find(e => e.id === link.entryId))}>

                            {JOURNAL_CATEGORIES[link.category]?.icon} {link.entryName}

                          </span>

                          <button

                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 1px', fontSize: '0.55rem', color: '#888', opacity: 0.6 }}

                            onClick={() => {

                              const newUnlinked = isUnlinked 

                                ? (entry.unlinkedNames || []).filter(n => n !== link.entryName)

                                : [...(entry.unlinkedNames || []), link.entryName];

                              const nextEntry = { ...entry, unlinkedNames: newUnlinked, updatedAt: new Date().toISOString() };

                              upsertJournalEntryFromPage(nextEntry, entries.map(e => e.id === entry.id ? nextEntry : e));

                              setSelectedEntry(nextEntry);

                            }}

                            title={isUnlinked ? 'Re-enable link' : 'Disable link'}

                          >

                            {isUnlinked ? '🔗' : '🚫'}

                          </button>

                        </div>

                      );

                    })}

                  </div>

                </div>

              );

            })()}

            

            {/* Tags */}

            {(entry.locationTags?.length > 0 || entry.tags?.length > 0) && (

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>

                {(entry.locationTags || []).map((tag, i) => (

                  <span key={`loc-${i}`} style={{ fontSize: '0.8rem', padding: '4px 8px', background: 'rgba(74,144,217,0.3)', border: '1px solid #4a90d9', borderRadius: '4px', color: '#7db5ff' }}>

                    📍 {tag}

                  </span>

                ))}

                {(entry.tags || []).map((tag, i) => (

                  <span key={`tag-${i}`} style={{ fontSize: '0.8rem', padding: '4px 8px', background: 'rgba(201,162,39,0.2)', border: '1px solid #c9a22780', borderRadius: '4px', color: '#c9a227' }}>

                    {tag}

                  </span>

                ))}

              </div>

            )}

          </div>

          

          {/* Quick Note - append without opening editor */}

          <div style={{ marginTop: '12px', padding: '10px', background: 'rgba(0,0,0,0.15)', borderRadius: '6px', border: '1px solid rgba(201,162,39,0.15)' }}>

            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>

              <input

                style={{ ...S.input, flex: 1, marginBottom: 0, padding: '7px 10px', fontSize: '0.82rem', background: 'rgba(0,0,0,0.2)' }}

                placeholder={`Quick note about ${entry.name}...`}

                onKeyDown={e => {

                  if (e.key === 'Enter' && e.target.value.trim()) {

                    const sessions = entries.filter(en => en.category === 'session').sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

                    const sessionNum = sessions[0]?.sessionNumber;

                    let separator = '\n';

                    if (sessionNum && entry.description) {

                      const lastMatch = entry.description.match(/── Session #(\d+) ──/g);

                      const lastNum = lastMatch ? parseInt(lastMatch[lastMatch.length - 1].match(/\d+/)[0]) : null;

                      if (lastNum !== sessionNum) separator = `\n\n── Session #${sessionNum} ──\n`;

                    } else if (sessionNum && !entry.description) {

                      separator = `── Session #${sessionNum} ──\n`;

                    }

                    const newDesc = (entry.description || '') + separator + e.target.value.trim();

                    const nextEntry = { ...entry, description: newDesc, updatedAt: new Date().toISOString() };

                    const updated = entries.map(en => en.id === entry.id ? nextEntry : en);

                    upsertJournalEntryFromPage(nextEntry, updated);

                    setSelectedEntry(nextEntry);

                    e.target.value = '';

                  }

                }}

              />

              <span style={{ fontSize: '0.6rem', color: '#666' }}>↵</span>

            </div>

          </div>

          

          {/* Footer */}

          <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', color: '#666', flexShrink: 0 }}>

            <span>Created: {new Date(entry.createdAt).toLocaleDateString()}</span>

            <span>Updated: {new Date(entry.updatedAt).toLocaleDateString()}</span>

          </div>

        </div>

      </div>

    );

  };

  

  // Render entry edit form (simplified - full form would be much longer)

  const renderEditForm = () => {

    const entry = editingEntry;

    if (!entry) return null;

    

    const catData = JOURNAL_CATEGORIES[entry.category];

    

    const updateField = (field, value) => setEditingEntry({ ...entry, [field]: value });

    

    const handleImageUpload = (e) => {

      const file = e.target.files[0];

      if (file) {

        const reader = new FileReader();

        reader.onloadend = () => updateField('image', reader.result);

        reader.readAsDataURL(file);

      }

    };

    

    return (

      <div style={S.modal} onClick={() => { setEditingEntry(null); setShowNewEntry(false); }}>

        <div style={{ ...S.modalContent, maxWidth: '650px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>

          <h3 style={{ color: '#ffd700', marginBottom: '16px', flexShrink: 0 }}>

            {catData?.icon} {showNewEntry ? `New ${catData?.name} Entry` : `Edit ${entry.name || 'Entry'}`}

          </h3>

          

          <div style={{ flex: 1, overflowY: 'auto', paddingRight: '8px', overflowWrap: 'break-word', wordBreak: 'break-word' }}>

            {/* Common fields */}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>

              <div style={{ gridColumn: 'span 2' }}>

                <label style={S.label}>Name *</label>

                <input style={S.input} value={entry.name} onChange={e => updateField('name', e.target.value)} placeholder="Entry name..." />

              </div>

              

              <div>

                <label style={S.label}>Visibility</label>

                <select style={S.select} value={entry.visibility} onChange={e => updateField('visibility', e.target.value)}>

                  {VISIBILITY_OPTIONS.map(opt => (

                    <option key={opt.id} value={opt.id}>{opt.icon} {opt.name}</option>

                  ))}

                </select>

              </div>



              {/* Campaign tag (2026-07-13): default is universal "Lore"; tagging
                  scopes the entry to the campaign. Hidden outside campaign
                  contexts so standalone play sees no extra field. */}

              {(activeCampaignId || entry.campaignId) && (

                <div>

                  <label style={S.label}>Campaign Tag</label>

                  <select style={S.select} value={entry.campaignId || ''} onChange={e => updateField('campaignId', e.target.value || null)}>

                    <option value="">📖 Lore (Universal)</option>

                    {activeCampaignId && (

                      <option value={activeCampaignId}>🏰 {activeCampaignName || 'Current Campaign'}</option>

                    )}

                    {entry.campaignId && entry.campaignId !== activeCampaignId && (

                      <option value={entry.campaignId}>🏰 Campaign ({String(entry.campaignId).slice(0, 8)}…)</option>

                    )}

                  </select>

                </div>

              )}



              <div>

                <label style={S.label}>Image</label>

                <input type="file" accept="image/*" onChange={handleImageUpload} style={{ fontSize: '0.8rem' }} />

              </div>

            </div>

            

            {entry.image && (

              <div style={{ marginBottom: '16px', textAlign: 'center' }}>

                <img src={entry.image} alt="Preview" style={{ maxWidth: '200px', maxHeight: '100px', borderRadius: '4px' }} />

                <button style={{ ...S.btn, ...S.btnXs, ...S.btnDanger, marginLeft: '8px' }} onClick={() => updateField('image', null)}>Remove</button>

              </div>

            )}

            

            {/* Category-specific fields */}

            {entry.category === 'creatures' && (

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>

                <div>

                  <label style={S.label}>Creature Type</label>

                  <select style={S.select} value={entry.creatureType} onChange={e => updateField('creatureType', e.target.value)}>

                    {CREATURE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}

                  </select>

                </div>

                <div>

                  <label style={S.label}>Size</label>

                  <select style={S.select} value={entry.size} onChange={e => updateField('size', e.target.value)}>

                    {CREATURE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}

                  </select>

                </div>

                <div>

                  <label style={S.label}>CR</label>

                  <input style={S.input} value={entry.challengeRating} onChange={e => updateField('challengeRating', e.target.value)} placeholder="1/4, 1, 5..." />

                </div>

                <div style={{ gridColumn: 'span 3' }}>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>

                    <input type="checkbox" checked={entry.isBoss} onChange={e => updateField('isBoss', e.target.checked)} />

                    👑 Boss Creature

                  </label>

                </div>

                <div style={{ gridColumn: 'span 3' }}>

                  <label style={S.label}>Habitat</label>

                  <input style={S.input} value={entry.habitat} onChange={e => updateField('habitat', e.target.value)} placeholder="Where this creature is found..." />

                </div>

                <div style={{ gridColumn: 'span 3' }}>

                  <label style={S.label}>Weaknesses (comma-separated)</label>

                  <CommaSeparatedInput style={S.input} value={entry.weaknesses || []} onChange={v => updateField('weaknesses', v)} />

                </div>

                <div style={{ gridColumn: 'span 3' }}>

                  <TextAreaField
                    label={<span style={S.label}>Abilities</span>}
                    style={{ ...S.input, minHeight: '60px' }}
                    value={entry.abilities}
                    onChange={e => updateField('abilities', e.target.value)}
                    placeholder="Special abilities and attacks..."
                  />

                </div>

                <div style={{ gridColumn: 'span 3' }}>

                  <label style={S.label}>Loot</label>

                  <input style={S.input} value={entry.loot} onChange={e => updateField('loot', e.target.value)} placeholder="What it drops..." />

                </div>

              </div>

            )}

            

            {entry.category === 'plants' && (

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>

                <div>

                  <label style={S.label}>Plant Type</label>

                  <select style={S.select} value={entry.plantType} onChange={e => updateField('plantType', e.target.value)}>

                    {PLANT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}

                  </select>

                </div>

                <div>

                  <label style={S.label}>Rarity</label>

                  <select style={S.select} value={entry.rarity} onChange={e => updateField('rarity', e.target.value)}>

                    {RARITY_LEVELS.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}

                  </select>

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <label style={S.label}>Habitat</label>

                  <input style={S.input} value={entry.habitat} onChange={e => updateField('habitat', e.target.value)} placeholder="Where to find it..." />

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <TextAreaField
                    label={<span style={S.label}>Effects / Uses</span>}
                    style={{ ...S.input, minHeight: '60px' }}
                    value={entry.effects}
                    onChange={e => updateField('effects', e.target.value)}
                    placeholder="What it does when used..."
                  />

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <label style={S.label}>Harvesting Notes</label>

                  <input style={S.input} value={entry.harvesting} onChange={e => updateField('harvesting', e.target.value)} placeholder="How to harvest, DC checks..." />

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <label style={S.label}>Used in Recipes (comma-separated)</label>

                  <CommaSeparatedInput style={S.input} value={entry.usedInRecipes || []} onChange={v => updateField('usedInRecipes', v)} placeholder="Antidote Potion, Healing Salve..." />

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <label style={S.label}>Sold At (comma-separated NPCs/shops)</label>

                  <CommaSeparatedInput style={S.input} value={entry.soldAt || []} onChange={v => updateField('soldAt', v)} placeholder="Herbalist Mira, Alchemy Shop..." />

                </div>

              </div>

            )}

            

            {/* Items Edit Form */}

            {entry.category === 'items' && (

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>

                <div>

                  <label style={S.label}>Item Type</label>

                  <select style={S.select} value={entry.itemType} onChange={e => updateField('itemType', e.target.value)}>

                    {ITEM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}

                  </select>

                </div>

                <div>

                  <label style={S.label}>Rarity</label>

                  <select style={S.select} value={entry.rarity} onChange={e => updateField('rarity', e.target.value)}>

                    {RARITY_LEVELS.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}

                  </select>

                </div>

                <div>

                  <label style={S.label}>Value</label>

                  <input style={S.input} value={entry.value || ''} onChange={e => updateField('value', e.target.value)} placeholder="10 gp, 5 sp..." />

                </div>

                <div>

                  <label style={S.label}>Weight</label>

                  <input style={S.input} value={entry.weight || ''} onChange={e => updateField('weight', e.target.value)} placeholder="1 lb, 0.5 lb..." />

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '12px' }}>

                    <input type="checkbox" checked={entry.isQuestItem || false} onChange={e => updateField('isQuestItem', e.target.checked)} />

                    <span style={{ color: '#9b59b6' }}>📜 Quest Item</span>

                  </label>

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <TextAreaField
                    label={<span style={S.label}>Effects (if consumable)</span>}
                    style={{ ...S.input, minHeight: '50px' }}
                    value={entry.effects || ''}
                    onChange={e => updateField('effects', e.target.value)}
                    placeholder="Heals 2d4+2 HP, grants +2 to Stealth..."
                  />

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <label style={S.label}>Found at (comma-separated locations)</label>

                  <CommaSeparatedInput style={S.input} value={entry.locations || []} onChange={v => updateField('locations', v)} placeholder="Darkwood Forest, Goblin Caves..." />

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <label style={S.label}>Sold by (comma-separated NPCs/shops)</label>

                  <CommaSeparatedInput style={S.input} value={entry.soldAt || []} onChange={v => updateField('soldAt', v)} placeholder="Aldric the Smith, Market Vendor..." />

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <label style={S.label}>Used in Recipes (comma-separated)</label>

                  <CommaSeparatedInput style={S.input} value={entry.usedInRecipes || []} onChange={v => updateField('usedInRecipes', v)} placeholder="Antidote Potion, Cure Disease Salve..." />

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <label style={S.label}>Related Quests (comma-separated)</label>

                  <CommaSeparatedInput style={S.input} value={entry.relatedQuests || []} onChange={v => updateField('relatedQuests', v)} placeholder="Clear the Goblin Caves, Find the Lost Artifact..." />

                </div>

              </div>

            )}

            

            {/* Recipes Edit Form */}

            {entry.category === 'recipes' && (

              <div style={{ marginBottom: '16px' }}>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>

                  <div>

                    <label style={S.label}>Recipe Type</label>

                    <select style={S.select} value={entry.recipeType} onChange={e => updateField('recipeType', e.target.value)}>

                      {RECIPE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}

                    </select>

                  </div>

                  <div>

                    <label style={S.label}>Crafting DC</label>

                    <input style={S.input} value={entry.craftingDC || ''} onChange={e => updateField('craftingDC', e.target.value)} placeholder="12, 15..." />

                  </div>

                  <div>

                    <label style={S.label}>Crafting Time</label>

                    <input style={S.input} value={entry.craftingTime || ''} onChange={e => updateField('craftingTime', e.target.value)} placeholder="1 hour, 30 minutes..." />

                  </div>

                  <div>

                    <label style={S.label}>Tools Needed</label>

                    <input style={S.input} value={entry.craftingTools || ''} onChange={e => updateField('craftingTools', e.target.value)} placeholder="Herbalism Kit, Alchemist's Supplies..." />

                  </div>

                </div>

                

                {/* Ingredients editor */}

                <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(23,165,137,0.1)', borderRadius: '6px', border: '1px solid rgba(23,165,137,0.3)' }}>

                  <label style={{ ...S.label, color: '#17a589' }}>🧪 Ingredients</label>

                  {(entry.ingredients || []).map((ing, i) => (

                    <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>

                      <input 

                        style={{ ...S.input, width: '60px', marginBottom: 0 }} 

                        type="number" 

                        min="1" 

                        value={ing.quantity || 1} 

                        onChange={e => {

                          const newIngs = [...(entry.ingredients || [])];

                          newIngs[i] = { ...newIngs[i], quantity: parseInt(e.target.value) || 1 };

                          updateField('ingredients', newIngs);

                        }} 

                      />

                      <input 

                        style={{ ...S.input, flex: 1, marginBottom: 0 }} 

                        value={ing.name || ''} 

                        onChange={e => {

                          const newIngs = [...(entry.ingredients || [])];

                          newIngs[i] = { ...newIngs[i], name: e.target.value };

                          updateField('ingredients', newIngs);

                        }} 

                        placeholder="Ingredient name..."

                      />

                      <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>

                        <input 

                          type="checkbox" 

                          checked={ing.optional || false} 

                          onChange={e => {

                            const newIngs = [...(entry.ingredients || [])];

                            newIngs[i] = { ...newIngs[i], optional: e.target.checked };

                            updateField('ingredients', newIngs);

                          }}

                        />

                        Optional

                      </label>

                      <button 

                        style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, padding: '4px 8px' }} 

                        onClick={() => updateField('ingredients', (entry.ingredients || []).filter((_, idx) => idx !== i))}

                      >✕</button>

                    </div>

                  ))}

                  <button 

                    style={{ ...S.btn, ...S.btnSm }} 

                    onClick={() => updateField('ingredients', [...(entry.ingredients || []), { name: '', quantity: 1, optional: false }])}

                  >+ Add Ingredient</button>

                </div>

                

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>

                  <div style={{ gridColumn: 'span 2' }}>

                    <label style={S.label}>Creates (Result)</label>

                    <input style={S.input} value={entry.result || ''} onChange={e => updateField('result', e.target.value)} placeholder="Antidote Potion, Healing Salve..." />

                  </div>

                  <div>

                    <label style={S.label}>Result Quantity</label>

                    <input style={S.input} type="number" min="1" value={entry.resultQuantity || 1} onChange={e => updateField('resultQuantity', parseInt(e.target.value) || 1)} />

                  </div>

                  <div>

                    <label style={S.label}>Effect Duration</label>

                    <input style={S.input} value={entry.duration || ''} onChange={e => updateField('duration', e.target.value)} placeholder="1 hour, Instant..." />

                  </div>

                  <div style={{ gridColumn: 'span 2' }}>

                    <TextAreaField
                      label={<span style={S.label}>Effects</span>}
                      style={{ ...S.input, minHeight: '50px' }}
                      value={entry.effects || ''}
                      onChange={e => updateField('effects', e.target.value)}
                      placeholder="Cures poison, +2 Str for 1 hour..."
                    />

                  </div>

                  <div style={{ gridColumn: 'span 2' }}>

                    <label style={S.label}>Cures Diseases (comma-separated)</label>

                    <CommaSeparatedInput style={S.input} value={entry.curesDiseases || []} onChange={v => updateField('curesDiseases', v)} placeholder="Swamp Fever, Goblin Pox..." />

                  </div>

                  <div style={{ gridColumn: 'span 2' }}>

                    <label style={S.label}>Learned From</label>

                    <input style={S.input} value={entry.learnedFrom || ''} onChange={e => updateField('learnedFrom', e.target.value)} placeholder="Herbalist Mira, Ancient Tome..." />

                  </div>

                </div>

              </div>

            )}

            

            {/* Diseases Edit Form */}

            {entry.category === 'diseases' && (

              <div style={{ marginBottom: '16px' }}>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>

                  <div style={{ gridColumn: 'span 2' }}>

                    <label style={S.label}>Severity</label>

                    <select style={S.select} value={entry.severity || 'mild'} onChange={e => updateField('severity', e.target.value)}>

                      {DISEASE_SEVERITIES.map(s => <option key={s.id} value={s.id}>{s.name} - {s.description}</option>)}

                    </select>

                  </div>

                </div>

                

                {/* Symptoms editor */}

                <div style={{ marginBottom: '16px' }}>

                  <label style={S.label}>🤒 Symptoms</label>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>

                    {(entry.symptoms || []).map((symptom, i) => (

                      <span key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', background: 'rgba(142,68,173,0.2)', borderRadius: '4px', fontSize: '0.85rem' }}>

                        {symptom}

                        <button style={{ background: 'none', border: 'none', color: '#c92727', cursor: 'pointer', fontSize: '0.8rem' }} onClick={() => updateField('symptoms', (entry.symptoms || []).filter((_, idx) => idx !== i))}>✕</button>

                      </span>

                    ))}

                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>

                    <input id="new-symptom" style={{ ...S.input, flex: 1, marginBottom: 0 }} placeholder="Add symptom..." onKeyDown={e => {

                      if (e.key === 'Enter' && e.target.value.trim()) {

                        updateField('symptoms', [...(entry.symptoms || []), e.target.value.trim()]);

                        e.target.value = '';

                      }

                    }} />

                    <button style={{ ...S.btn, ...S.btnSm }} onClick={() => {

                      const input = document.getElementById('new-symptom');

                      if (input.value.trim()) {

                        updateField('symptoms', [...(entry.symptoms || []), input.value.trim()]);

                        input.value = '';

                      }

                    }}>Add</button>

                  </div>

                </div>

                

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>

                  <div style={{ gridColumn: 'span 2' }}>

                    <label style={S.label}>Transmission (how you catch it)</label>

                    <input style={S.input} value={entry.transmission || ''} onChange={e => updateField('transmission', e.target.value)} placeholder="Contact with swamp water, mosquito bites..." />

                  </div>

                  <div>

                    <label style={S.label}>Incubation Period</label>

                    <input style={S.input} value={entry.incubation || ''} onChange={e => updateField('incubation', e.target.value)} placeholder="1-3 days..." />

                  </div>

                  <div>

                    <label style={S.label}>Common Locations</label>

                    <CommaSeparatedInput style={S.input} value={entry.locations || []} onChange={v => updateField('locations', v)} placeholder="Blackmire Swamp..." />

                  </div>

                  <div style={{ gridColumn: 'span 2' }}>

                    <label style={S.label}>Progression (how it worsens)</label>

                    <textarea style={{ ...S.input, minHeight: '50px' }} value={entry.progression || ''} onChange={e => updateField('progression', e.target.value)} placeholder="Gets worse every 2 days without treatment..." />

                  </div>

                </div>

                

                {/* Effects/Debuffs editor */}

                <div style={{ marginBottom: '16px', marginTop: '12px' }}>

                  <label style={S.label}>⚠️ Effects/Debuffs</label>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>

                    {(entry.effects || []).map((eff, i) => (

                      <span key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', background: 'rgba(201,39,39,0.2)', borderRadius: '4px', fontSize: '0.85rem', color: '#ff6666' }}>

                        {eff}

                        <button style={{ background: 'none', border: 'none', color: '#c92727', cursor: 'pointer', fontSize: '0.8rem' }} onClick={() => updateField('effects', (entry.effects || []).filter((_, idx) => idx !== i))}>✕</button>

                      </span>

                    ))}

                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>

                    <input id="new-effect" style={{ ...S.input, flex: 1, marginBottom: 0 }} placeholder="Add effect (-2 Con, Disadvantage on saves...)" onKeyDown={e => {

                      if (e.key === 'Enter' && e.target.value.trim()) {

                        updateField('effects', [...(entry.effects || []), e.target.value.trim()]);

                        e.target.value = '';

                      }

                    }} />

                    <button style={{ ...S.btn, ...S.btnSm }} onClick={() => {

                      const input = document.getElementById('new-effect');

                      if (input.value.trim()) {

                        updateField('effects', [...(entry.effects || []), input.value.trim()]);

                        input.value = '';

                      }

                    }}>Add</button>

                  </div>

                </div>

                

                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>

                  <div>

                    <label style={S.label}>💚 Cured By (comma-separated recipes/items/spells)</label>

                    <CommaSeparatedInput style={S.input} value={entry.curedBy || []} onChange={v => updateField('curedBy', v)} placeholder="Cure Disease Salve, Lesser Restoration spell..." />

                  </div>

                  <div>

                    <label style={S.label}>🐾 Spread by Creatures (comma-separated)</label>

                    <CommaSeparatedInput style={S.input} value={entry.creatures || []} onChange={v => updateField('creatures', v)} placeholder="Giant Mosquito, Zombie..." />

                  </div>

                  <div>

                    <label style={S.label}>📝 Additional Notes</label>

                    <TextAreaField style={{ ...S.input, minHeight: '50px' }} value={entry.notes || ''} onChange={e => updateField('notes', e.target.value)} placeholder="Any other important information..." />

                  </div>

                </div>

              </div>

            )}

            

            {entry.category === 'npcs' && (

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>

                <div>

                  <label style={S.label}>Title / Role</label>

                  <input style={S.input} value={entry.title} onChange={e => updateField('title', e.target.value)} placeholder="Blacksmith, Mayor, Guard..." />

                </div>

                <div>

                  <label style={S.label}>Disposition</label>

                  <select style={S.select} value={entry.disposition} onChange={e => updateField('disposition', e.target.value)}>

                    {NPC_DISPOSITIONS.map(d => <option key={d.id} value={d.id}>{d.icon} {d.name}</option>)}

                  </select>

                </div>

                <div>

                  <label style={S.label}>Location</label>

                  <input style={S.input} value={entry.currentLocation} onChange={e => updateField('currentLocation', e.target.value)} placeholder="Where they can be found..." />

                </div>

                <div>

                  <label style={S.label}>Faction</label>

                  <input style={S.input} value={entry.faction} onChange={e => updateField('faction', e.target.value)} placeholder="Guild, kingdom, group..." />

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <label style={S.label}>Appearance</label>

                  <input style={S.input} value={entry.appearance} onChange={e => updateField('appearance', e.target.value)} placeholder="Physical description..." />

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <label style={S.label}>Personality</label>

                  <TextAreaField style={{ ...S.input, minHeight: '60px' }} value={entry.personality} onChange={e => updateField('personality', e.target.value)} placeholder="How they act, their quirks..." />

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <label style={S.label}>Known Information</label>

                  <TextAreaField style={{ ...S.input, minHeight: '60px' }} value={entry.knownInfo} onChange={e => updateField('knownInfo', e.target.value)} placeholder="What you know about them..." />

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <label style={S.label}>🛒 Sells (comma-separated items)</label>

                  <CommaSeparatedInput style={S.input} value={entry.sells || []} onChange={v => updateField('sells', v)} placeholder="Mandrake Root, Healing Potions, Weapons..." />

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <label style={S.label}>📜 Quest Connections (comma-separated quest names)</label>

                  <CommaSeparatedInput style={S.input} value={entry.questConnections || []} onChange={v => updateField('questConnections', v)} placeholder="Clear the Goblin Caves, Find the Lost Artifact..." />

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>

                    <input type="checkbox" checked={entry.isAlive !== false} onChange={e => updateField('isAlive', e.target.checked)} />

                    Character is alive

                  </label>

                </div>

              </div>

            )}

            

            {entry.category === 'quests' && (

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>

                <div>

                  <label style={S.label}>Status</label>

                  <select style={S.select} value={entry.status} onChange={e => updateField('status', e.target.value)}>

                    {QUEST_STATUSES.map(s => <option key={s.id} value={s.id}>{s.icon} {s.name}</option>)}

                  </select>

                </div>

                <div>

                  <label style={S.label}>Priority</label>

                  <select style={S.select} value={entry.priority || ''} onChange={e => updateField('priority', e.target.value)}>

                    <option value="">— Set priority —</option>

                    {QUEST_PRIORITIES.map(p => <option key={p.id} value={p.id}>{p.icon} {p.name}</option>)}

                  </select>

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <label style={S.label}>Quest Giver</label>

                  <input style={S.input} value={entry.questGiver} onChange={e => updateField('questGiver', e.target.value)} placeholder="NPC name..." />

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <label style={S.label}>Objectives</label>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>

                    {(entry.objectives || []).map((obj, i) => (

                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 8px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px' }}>

                        <span 

                          style={{ cursor: 'pointer', fontSize: '1rem', userSelect: 'none', color: obj.completed ? '#27c95a' : '#888' }}

                          onClick={() => {

                            const updated = [...(entry.objectives || [])];

                            updated[i] = { ...updated[i], completed: !updated[i].completed };

                            updateField('objectives', updated);

                          }}

                        >{obj.completed ? '☑' : '☐'}</span>

                        <span style={{ flex: 1, textDecoration: obj.completed ? 'line-through' : 'none', color: obj.completed ? '#666' : '#e8d5b7', fontSize: '0.85rem' }}>{obj.text}</span>

                        <button 

                          style={{ background: 'none', border: 'none', color: '#c92727', cursor: 'pointer', fontSize: '0.8rem', padding: '2px 4px', opacity: 0.5 }}

                          onClick={() => updateField('objectives', (entry.objectives || []).filter((_, j) => j !== i))}

                          title="Remove objective"

                        >✕</button>

                      </div>

                    ))}

                    <div style={{ display: 'flex', gap: '6px', marginTop: '2px' }}>

                      <input 

                        style={{ ...S.input, marginBottom: 0, flex: 1 }} 

                        placeholder="New objective..."

                        onKeyDown={e => {

                          if (e.key === 'Enter' && e.target.value.trim()) {

                            updateField('objectives', [...(entry.objectives || []), { text: e.target.value.trim(), completed: false }]);

                            e.target.value = '';

                          }

                        }}

                      />

                      <button 

                        style={{ ...S.btn, ...S.btnXs, padding: '6px 12px', whiteSpace: 'nowrap' }}

                        onClick={e => {

                          const input = e.target.previousElementSibling;

                          if (input && input.value.trim()) {

                            updateField('objectives', [...(entry.objectives || []), { text: input.value.trim(), completed: false }]);

                            input.value = '';

                            input.focus();

                          }

                        }}

                      >+ Add</button>

                    </div>

                  </div>

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <label style={S.label}>Rewards</label>

                  <input style={S.input} value={entry.rewards} onChange={e => updateField('rewards', e.target.value)} placeholder="Gold, items, reputation..." />

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <label style={S.label}>👤 Related NPCs (comma-separated)</label>

                  <CommaSeparatedInput style={S.input} value={entry.relatedNpcs || []} onChange={v => updateField('relatedNpcs', v)} placeholder="Elder Thornwood, Aldric the Smith..." />

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <label style={S.label}>🎒 Related Items (comma-separated)</label>

                  <CommaSeparatedInput style={S.input} value={entry.relatedItems || []} onChange={v => updateField('relatedItems', v)} placeholder="Goblin Chieftain Totem, Ancient Map..." />

                </div>

              </div>

            )}

            

            {entry.category === 'session' && (

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>

                <div>

                  <label style={S.label}>Session Number</label>

                  <input style={S.input} type="number" min="1" value={entry.sessionNumber} onChange={e => updateField('sessionNumber', parseInt(e.target.value) || 1)} />

                </div>

                <div>

                  <label style={S.label}>Session Date</label>

                  <input style={S.input} type="date" value={entry.sessionDate} onChange={e => updateField('sessionDate', e.target.value)} />

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <label style={S.label}>Summary</label>

                  <PrivacyEditor value={entry.summary || ''} onChange={v => updateField('summary', v)} placeholder="What happened this session..." minHeight="80px" charId={currentCharId} chars={chars} isDM={isDM} />

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <label style={S.label}>Key Events (one per line, prefix with * for important)</label>

                  <textarea 

                    style={{ ...S.input, minHeight: '80px' }} 

                    value={(entry.keyEvents || []).map(e => (e.important ? '* ' : '') + e.event).join('\n')} 

                    onChange={e => updateField('keyEvents', e.target.value.split('\n').filter(Boolean).map(line => ({

                      event: line.replace(/^\*\s*/, ''),

                      important: line.startsWith('*')

                    })))}

                    placeholder="Event 1&#10;* Important event&#10;Event 3"

                  />

                </div>

                <div>

                  <label style={S.label}>NPCs Encountered (comma-separated)</label>

                  <CommaSeparatedInput style={S.input} value={entry.npcsEncountered || []} onChange={v => updateField('npcsEncountered', v)} />

                </div>

                <div>

                  <label style={S.label}>Creatures Encountered (comma-separated)</label>

                  <CommaSeparatedInput style={S.input} value={entry.creaturesEncountered || []} onChange={v => updateField('creaturesEncountered', v)} />

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <label style={S.label}>Items Found (comma-separated)</label>

                  <CommaSeparatedInput style={S.input} value={entry.itemsFound || []} onChange={v => updateField('itemsFound', v)} />

                </div>

                <div style={{ gridColumn: 'span 2' }}>

                  <label style={S.label}>Player Notes</label>

                  <TextAreaField style={{ ...S.input, minHeight: '60px' }} value={entry.playerNotes} onChange={e => updateField('playerNotes', e.target.value)} placeholder="Personal notes, reminders..." />

                </div>

              </div>

            )}

            

            {/* Description (common) */}

            <div style={{ marginBottom: '16px' }}>

              <label style={S.label}>Description</label>

              <PrivacyEditor value={entry.description || ''} onChange={v => updateField('description', v)} placeholder="General notes and description..." minHeight="60px" charId={currentCharId} chars={chars} isDM={isDM} />

            </div>

            

            {/* Tags */}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>

              <div>

                <label style={S.label}>Location Tags (comma-separated)</label>

                <CommaSeparatedInput style={S.input} value={entry.locationTags || []} onChange={v => updateField('locationTags', v)} placeholder="Forest, Dungeon, Town..." />

              </div>

              <div>

                <label style={S.label}>Custom Tags (comma-separated)</label>

                <CommaSeparatedInput style={S.input} value={entry.tags || []} onChange={v => updateField('tags', v)} placeholder="Combat, Ally, Important..." />

              </div>

            </div>

          </div>

          

          {/* Actions */}

          <div style={{ display: 'flex', gap: '8px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}>

            <button style={{ ...S.btn, ...S.btnSuccess, flex: 1 }} onClick={() => saveEntry(entry)} disabled={!entry.name}>💾 Save</button>

            {!showNewEntry && <button style={{ ...S.btn, ...S.btnDanger }} onClick={() => deleteEntry(entry.id)}>🗑️ Delete</button>}

            <button style={S.btn} onClick={() => { setEditingEntry(null); setShowNewEntry(false); }}>Cancel</button>

          </div>

        </div>

      </div>

    );

  };

  

  return (

    <div style={S.container}>

      {/* Header */}

      <div style={{ ...S.card, marginBottom: '16px' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>

            <button style={{ ...S.btn, ...S.btnSm }} onClick={onBack}>← Back</button>

            <h1 style={{ ...S.title, marginBottom: 0, fontSize: '1.5rem' }}>📚 Party Journal</h1>

          </div>

          <div style={{ display: 'flex', gap: '8px' }}>

            <button style={{ ...S.btn }} onClick={() => setShowJournalSettings(true)} title="Journal Settings">⚙️</button>

            <Tooltip text="Quick jot — name only, fill details later">

              <button 

                style={{ ...S.btn, background: quickAddVisible ? 'rgba(201,162,39,0.3)' : undefined, borderColor: quickAddVisible ? '#c9a227' : undefined }} 

                onClick={() => setQuickAddVisible(!quickAddVisible)}

                title="Quick Add"

              >⚡</button>

            </Tooltip>

            <button style={{ ...S.btn, ...S.btnSuccess }} onClick={createEntry}>+ New Entry</button>

          </div>

        </div>

        

        {/* Quick-Add Bar */}

        {quickAddVisible && (

          <div style={{ 

            marginTop: '12px', 

            padding: '10px 12px', 

            background: 'rgba(0,0,0,0.25)', 

            borderRadius: '6px',

            border: '1px solid rgba(201,162,39,0.25)',

          }}>

            <QuickAddInput

              value={quickAddName}

              onChange={setQuickAddName}

              onSubmit={(text) => { if (text.trim()) quickAddEntry(text, quickAddCategory); }}

              onCancel={() => { setQuickAddVisible(false); setQuickAddName(''); }}

              settings={journalSettings?.quickAdd}

              placeholder={`Quick add to ${JOURNAL_CATEGORIES[quickAddCategory || activeCategory]?.name || 'journal'}... (use #N@ #Q@ @name tags)`}

              autoFocus

              entries={entries}

            />

            {/* Category quick-pick */}

            <div style={{ display: 'flex', gap: '4px', marginTop: '8px', flexWrap: 'wrap', alignItems: 'center' }}>

              <span style={{ fontSize: '0.7rem', color: '#888', marginRight: '4px' }}>Category:</span>

              {orderedCategories.map(([key, cat]) => (

                <button

                  key={key}

                  onClick={() => setQuickAddCategory(key === (quickAddCategory || activeCategory) ? null : key)}

                  style={{

                    padding: '2px 8px',

                    background: (quickAddCategory || activeCategory) === key ? `${cat.color}30` : 'rgba(0,0,0,0.2)',

                    border: `1px solid ${(quickAddCategory || activeCategory) === key ? cat.color : 'rgba(255,255,255,0.08)'}`,

                    borderRadius: '4px',

                    color: (quickAddCategory || activeCategory) === key ? cat.color : '#999',

                    cursor: 'pointer',

                    fontSize: '0.7rem',

                    display: 'flex', alignItems: 'center', gap: '3px',

                  }}

                  title={cat.name}

                >

                  <span>{cat.icon}</span>

                  <span>{cat.name}</span>

                </button>

              ))}

            </div>

          </div>

        )}

      </div>

      

      {/* Category tabs */}

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', overflowX: 'auto', paddingBottom: '8px', alignItems: 'center' }}>

        {orderedCategories.map(([key, cat]) => (

          <button

            key={key}

            style={{

              ...S.btn,

              background: activeCategory === key ? cat.color : 'rgba(0,0,0,0.3)',

              borderColor: cat.color,

              opacity: activeCategory === key ? 1 : 0.7,

              whiteSpace: 'nowrap',

              display: 'flex',

              alignItems: 'center',

              gap: '6px',

            }}

            onClick={() => setActiveCategory(key)}

          >

            <span style={{ fontSize: '1.2rem' }}>{cat.icon}</span>

            <span>{cat.name}</span>

            <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>({entries.filter(e => {

              if (e.category !== key) return false;

              if (!isDM) {

                if (e.visibility === 'private' && e.createdBy !== currentCharId) return false;

                if (e.visibility === 'dm') return false;

                if (e.visibility === 'select' && !e.sharedWith?.includes(currentCharId) && e.createdBy !== currentCharId) return false;

              }

              return true;

            }).length})</span>

          </button>

        ))}

        <button

          style={{ ...S.btn, ...S.btnXs, marginLeft: 'auto', padding: '6px 10px' }}

          onClick={() => setShowOrderSettings(true)}

          title="Customize tab order"

        >

          ⚙️

        </button>

      </div>

      

      {/* Tab Order Settings Modal */}

      {showOrderSettings && (

        <div style={S.modal} onClick={() => setShowOrderSettings(false)}>

          <div style={{ ...S.modalContent, maxWidth: '400px' }} onClick={e => e.stopPropagation()}>

            <h3 style={{ color: '#ffd700', marginBottom: '16px' }}>📋 Customize Tab Order</h3>

            <p style={{ fontSize: '0.85rem', color: '#888', marginBottom: '16px' }}>Drag or use arrows to reorder journal tabs</p>

            

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>

              {categoryOrder.map((key, index) => {

                const cat = JOURNAL_CATEGORIES[key];

                if (!cat) return null;

                return (

                  <div 

                    key={key}

                    style={{

                      display: 'flex',

                      alignItems: 'center',

                      gap: '12px',

                      padding: '10px 12px',

                      background: 'rgba(0,0,0,0.3)',

                      borderRadius: '6px',

                      border: `1px solid ${cat.color}40`,

                    }}

                  >

                    <span style={{ fontSize: '1.2rem' }}>{cat.icon}</span>

                    <span style={{ flex: 1, color: '#e8d5b7' }}>{cat.name}</span>

                    <div style={{ display: 'flex', gap: '4px' }}>

                      <button 

                        style={{ ...S.btn, ...S.btnXs, padding: '4px 8px', opacity: index === 0 ? 0.3 : 1 }}

                        disabled={index === 0}

                        onClick={() => moveCategoryOrder(index, -1)}

                      >▲</button>

                      <button 

                        style={{ ...S.btn, ...S.btnXs, padding: '4px 8px', opacity: index === categoryOrder.length - 1 ? 0.3 : 1 }}

                        disabled={index === categoryOrder.length - 1}

                        onClick={() => moveCategoryOrder(index, 1)}

                      >▼</button>

                    </div>

                  </div>

                );

              })}

            </div>

            

            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>

              <button style={{ ...S.btn, flex: 1 }} onClick={() => setCategoryOrder([...DEFAULT_JOURNAL_ORDER])}>Reset to Default</button>

              <button style={{ ...S.btn, ...S.btnSuccess, flex: 1 }} onClick={() => setShowOrderSettings(false)}>Done</button>

            </div>

          </div>

        </div>

      )}

      

      {/* Filters */}

      <div style={{ ...S.card, marginBottom: '16px' }}>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>

          {/* Search */}

          <div style={{ flex: '1 1 200px' }}>

            <input 

              style={{ ...S.input, marginBottom: 0 }} 

              placeholder="🔍 Search entries..." 

              value={searchQuery} 

              onChange={e => setSearchQuery(e.target.value)} 

            />

          </div>

          

          {/* Location filter */}

          {allLocations.length > 0 && (

            <select style={{ ...S.select, width: 'auto', marginBottom: 0 }} value={filterLocation} onChange={e => setFilterLocation(e.target.value)}>

              <option value="">📍 All Locations</option>

              {allLocations.map(loc => <option key={loc} value={loc}>{loc}</option>)}

            </select>

          )}

          

          {/* Visibility filter */}

          <select style={{ ...S.select, width: 'auto', marginBottom: 0 }} value={filterVisibility} onChange={e => setFilterVisibility(e.target.value)}>

            <option value="all">All Visibility</option>

            {VISIBILITY_OPTIONS.map(opt => <option key={opt.id} value={opt.id}>{opt.icon} {opt.name}</option>)}

          </select>

          

          {/* View mode toggle */}

          <div style={{ display: 'flex', gap: '4px' }}>

            <button 

              style={{ ...S.btn, ...S.btnXs, ...(viewMode === 'grid' ? S.btnMana : {}) }} 

              onClick={() => setViewMode('grid')}

              title="Grid view"

            >⊞</button>

            <button 

              style={{ ...S.btn, ...S.btnXs, ...(viewMode === 'list' ? S.btnMana : {}) }} 

              onClick={() => setViewMode('list')}

              title="List view"

            >☰</button>

          </div>

        </div>

        

        {/* Tag filters */}

        {allTags.length > 0 && (

          <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>

            {allTags.slice(0, 15).map(tag => (

              <button

                key={tag}

                style={{

                  padding: '4px 8px',

                  fontSize: '0.75rem',

                  background: filterTags.includes(tag) ? 'rgba(201,162,39,0.4)' : 'rgba(0,0,0,0.2)',

                  border: `1px solid ${filterTags.includes(tag) ? '#c9a227' : 'rgba(255,255,255,0.1)'}`,

                  borderRadius: '4px',

                  color: filterTags.includes(tag) ? '#ffd700' : '#888',

                  cursor: 'pointer',

                }}

                onClick={() => setFilterTags(filterTags.includes(tag) ? filterTags.filter(t => t !== tag) : [...filterTags, tag])}

              >

                {tag}

              </button>

            ))}

            {filterTags.length > 0 && (

              <button style={{ ...S.btn, ...S.btnXs, ...S.btnDanger }} onClick={() => setFilterTags([])}>Clear filters</button>

            )}

          </div>

        )}

      </div>

      

      {/* Entries */}

      <div style={{

        display: viewMode === 'grid' ? 'grid' : 'flex',

        gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(280px, 1fr))' : 'none',

        flexDirection: viewMode === 'list' ? 'column' : 'row',

        gap: '12px',

      }}>

        {sortedEntries.length === 0 ? (

          <div style={{ ...S.card, textAlign: 'center', gridColumn: '1 / -1' }}>

            <p style={{ opacity: 0.5, fontSize: '1.1rem' }}>

              {JOURNAL_CATEGORIES[activeCategory]?.icon} No {JOURNAL_CATEGORIES[activeCategory]?.name.toLowerCase()} entries yet

            </p>

            <button style={{ ...S.btn, ...S.btnSuccess, marginTop: '12px' }} onClick={createEntry}>

              + Create First Entry

            </button>

          </div>

        ) : (

          sortedEntries.map(entry => renderEntryCard(entry))

        )}

      </div>

      

      {/* Modals */}

      {selectedEntry && renderEntryDetail(selectedEntry)}

      {editingEntry && renderEditForm()}

      

      {/* Hover Tooltip */}

      {hoveredEntry && settings.showLinkTooltips && (

        <div 

          style={{

            position: 'fixed',

            left: Math.min(tooltipPos.x, window.innerWidth - 320),

            top: tooltipPos.y,

            width: '300px',

            maxHeight: '280px',

            overflow: 'hidden',

            background: 'linear-gradient(135deg, rgba(40,30,20,0.98) 0%, rgba(30,25,20,0.99) 100%)',

            border: `2px solid ${JOURNAL_CATEGORIES[hoveredEntry.category]?.color || '#5c4a1f'}`,

            borderRadius: '8px',

            padding: '12px',

            zIndex: 10001,

            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',

            pointerEvents: 'none',

          }}

        >

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>

            <span style={{ fontSize: '1.2rem' }}>{JOURNAL_CATEGORIES[hoveredEntry.category]?.icon}</span>

            <strong style={{ color: '#ffd700' }}>{hoveredEntry.name}</strong>

          </div>

          <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '6px' }}>

            {JOURNAL_CATEGORIES[hoveredEntry.category]?.name}

            {hoveredEntry.category === 'creatures' && ` • ${hoveredEntry.size} ${hoveredEntry.creatureType} • CR ${hoveredEntry.challengeRating}`}

            {hoveredEntry.category === 'npcs' && hoveredEntry.title && ` • ${hoveredEntry.title}`}

            {hoveredEntry.category === 'plants' && ` • ${RARITY_LEVELS.find(r => r.id === hoveredEntry.rarity)?.name || 'Common'}`}

            {hoveredEntry.category === 'quests' && ` • ${QUEST_STATUSES.find(s => s.id === hoveredEntry.status)?.name}`}

            {hoveredEntry.category === 'session' && ` • Session ${hoveredEntry.sessionNumber}`}

            {hoveredEntry.category === 'items' && ` • ${hoveredEntry.itemType} • ${RARITY_LEVELS.find(r => r.id === hoveredEntry.rarity)?.name || 'Common'}`}

            {hoveredEntry.category === 'recipes' && ` • ${hoveredEntry.recipeType}${hoveredEntry.craftingDC ? ` • DC ${hoveredEntry.craftingDC}` : ''}`}

            {hoveredEntry.category === 'diseases' && ` • ${DISEASE_SEVERITIES.find(s => s.id === hoveredEntry.severity)?.name || 'Unknown'} severity`}

          </div>

          

          {/* Category-specific quick info */}

          {hoveredEntry.category === 'items' && (

            <div style={{ fontSize: '0.75rem', marginBottom: '6px' }}>

              {(hoveredEntry.locations || []).length > 0 && (

                <div style={{ color: '#4a90d9' }}>📍 Found: {hoveredEntry.locations.slice(0, 2).join(', ')}{hoveredEntry.locations.length > 2 ? '...' : ''}</div>

              )}

              {(hoveredEntry.soldAt || []).length > 0 && (

                <div style={{ color: '#27c95a' }}>🛒 Sold by: {hoveredEntry.soldAt.slice(0, 2).join(', ')}{hoveredEntry.soldAt.length > 2 ? '...' : ''}</div>

              )}

              {(hoveredEntry.usedInRecipes || []).length > 0 && (

                <div style={{ color: '#17a589' }}>📋 Used in: {hoveredEntry.usedInRecipes.slice(0, 2).join(', ')}{hoveredEntry.usedInRecipes.length > 2 ? '...' : ''}</div>

              )}

            </div>

          )}

          

          {hoveredEntry.category === 'plants' && (

            <div style={{ fontSize: '0.75rem', marginBottom: '6px' }}>

              {hoveredEntry.habitat && <div style={{ color: '#4a90d9' }}>📍 Habitat: {hoveredEntry.habitat}</div>}

              {(hoveredEntry.soldAt || []).length > 0 && (

                <div style={{ color: '#27c95a' }}>🛒 Sold by: {hoveredEntry.soldAt.join(', ')}</div>

              )}

              {(hoveredEntry.usedInRecipes || []).length > 0 && (

                <div style={{ color: '#17a589' }}>📋 Used in: {hoveredEntry.usedInRecipes.slice(0, 2).join(', ')}{hoveredEntry.usedInRecipes.length > 2 ? '...' : ''}</div>

              )}

            </div>

          )}

          

          {hoveredEntry.category === 'recipes' && (

            <div style={{ fontSize: '0.75rem', marginBottom: '6px' }}>

              {hoveredEntry.result && <div style={{ color: '#ffd700' }}>✨ Creates: {hoveredEntry.result}</div>}

              {(hoveredEntry.ingredients || []).length > 0 && (

                <div style={{ color: '#d97429' }}>🧪 Needs: {hoveredEntry.ingredients.map(i => i.name).slice(0, 3).join(', ')}{hoveredEntry.ingredients.length > 3 ? '...' : ''}</div>

              )}

              {(hoveredEntry.curesDiseases || []).length > 0 && (

                <div style={{ color: '#27c95a' }}>💚 Cures: {hoveredEntry.curesDiseases.join(', ')}</div>

              )}

            </div>

          )}

          

          {hoveredEntry.category === 'diseases' && (

            <div style={{ fontSize: '0.75rem', marginBottom: '6px' }}>

              {(hoveredEntry.symptoms || []).length > 0 && (

                <div style={{ color: '#8e44ad' }}>🤒 Symptoms: {hoveredEntry.symptoms.slice(0, 3).join(', ')}{hoveredEntry.symptoms.length > 3 ? '...' : ''}</div>

              )}

              {(hoveredEntry.curedBy || []).length > 0 && (

                <div style={{ color: '#27c95a' }}>💚 Cured by: {hoveredEntry.curedBy.slice(0, 2).join(', ')}{hoveredEntry.curedBy.length > 2 ? '...' : ''}</div>

              )}

              {(hoveredEntry.locations || []).length > 0 && (

                <div style={{ color: '#4a90d9' }}>📍 Found in: {hoveredEntry.locations.join(', ')}</div>

              )}

            </div>

          )}

          

          {hoveredEntry.category === 'npcs' && (

            <div style={{ fontSize: '0.75rem', marginBottom: '6px' }}>

              {hoveredEntry.currentLocation && <div style={{ color: '#4a90d9' }}>📍 Location: {hoveredEntry.currentLocation}</div>}

              {(hoveredEntry.sells || []).length > 0 && (

                <div style={{ color: '#d97429' }}>🛒 Sells: {hoveredEntry.sells.slice(0, 3).join(', ')}{hoveredEntry.sells.length > 3 ? '...' : ''}</div>

              )}

            </div>

          )}

          

          {hoveredEntry.description && (

            <p style={{ 

              fontSize: '0.8rem', 

              color: '#ccc', 

              margin: 0, 

              overflow: 'hidden',

              textOverflow: 'ellipsis',

              display: '-webkit-box',

              WebkitLineClamp: 3,

              WebkitBoxOrient: 'vertical',

              lineHeight: '1.4'

            }}>

              {stripPrivateForViewer(hoveredEntry.description, currentCharId, isDM).replace(/\{\{PRIVATE:[^}]+\}\}|\{\{\/PRIVATE\}\}/g, '')}

            </p>

          )}

          <div style={{ fontSize: '0.7rem', color: '#666', marginTop: '8px', fontStyle: 'italic' }}>

            Click to view full entry

          </div>

        </div>

      )}

      

      {/* Journal Settings Modal */}

      {showJournalSettings && (

        <div style={S.modal} onClick={() => setShowJournalSettings(false)}>

          <div style={{ ...S.modalContent, maxWidth: '400px' }} onClick={e => e.stopPropagation()}>

            <h3 style={{ color: '#ffd700', marginBottom: '16px' }}>⚙️ Journal Settings</h3>

            

            <div style={{ marginBottom: '16px' }}>

              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>

                <input 

                  type="checkbox" 

                  checked={settings.showLinkTooltips} 

                  onChange={e => onUpdateJournalSettings && onUpdateJournalSettings({ ...settings, showLinkTooltips: e.target.checked })}

                />

                <span>Show tooltips on hover</span>

              </label>

              <p style={{ fontSize: '0.8rem', color: '#888', marginTop: '4px', marginLeft: '26px' }}>

                Display a preview when hovering over linked entry names

              </p>

            </div>

            

            {/* Quick-Add Tag Settings */}

            <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '16px', marginBottom: '16px' }}>

              <h4 style={{ color: '#c9a227', marginBottom: '12px', fontSize: '0.9rem' }}>⚡ Quick-Add Settings</h4>

              

              <div style={{ marginBottom: '12px' }}>

                <label style={S.label}>Default scope mode</label>

                <select 

                  style={{ ...S.select, marginBottom: '4px' }}

                  value={(settings.quickAdd || {}).scopeMode || 'after'}

                  onChange={e => onUpdateJournalSettings && onUpdateJournalSettings({ 

                    ...settings, 

                    quickAdd: { ...(settings.quickAdd || {}), scopeMode: e.target.value } 

                  })}

                >

                  <option value="after">Text after tag → entry description</option>

                  <option value="before">Text before tag → entry description</option>

                  <option value="all">All text → entry description</option>

                </select>

                <p style={{ fontSize: '0.75rem', color: '#666', margin: '2px 0 0' }}>

                  Controls which text gets added to an entry when using #X@ tags

                </p>

              </div>

              

              <div style={{ marginBottom: '12px' }}>

                <label style={S.label}>Name delimiter</label>

                <input 

                  style={{ ...S.input, maxWidth: '120px' }}

                  value={(settings.quickAdd || {}).delimiter || ' - '}

                  onChange={e => onUpdateJournalSettings && onUpdateJournalSettings({ 

                    ...settings, 

                    quickAdd: { ...(settings.quickAdd || {}), delimiter: e.target.value } 

                  })}

                  placeholder=" - "

                />

                <p style={{ fontSize: '0.75rem', color: '#666', margin: '2px 0 0' }}>

                  Separates the entry name from its description (e.g. #N@Viego - tall stranger)

                </p>

              </div>

              

              {/* Per-category scope overrides */}

              <div>

                <label style={{ ...S.label, marginBottom: '8px' }}>Per-category overrides</label>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>

                  {Object.entries(JOURNAL_TAG_CODES).filter(([_, info]) => info.category).map(([code, info]) => {

                    const catScopeModes = (settings.quickAdd || {}).categoryScopeModes || {};

                    const currentMode = catScopeModes[info.category] || '';

                    return (

                      <div key={code} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

                        <span style={{ color: info.color, fontWeight: 'bold', fontSize: '0.8rem', minWidth: '45px' }}>#{code}@</span>

                        <span style={{ fontSize: '0.8rem', color: '#999', minWidth: '80px' }}>{info.label}</span>

                        <select

                          style={{ ...S.select, marginBottom: 0, flex: 1, fontSize: '0.75rem', padding: '3px 6px' }}

                          value={currentMode}

                          onChange={e => {

                            const newModes = { ...catScopeModes };

                            if (e.target.value === '') {

                              delete newModes[info.category];

                            } else {

                              newModes[info.category] = e.target.value;

                            }

                            onUpdateJournalSettings && onUpdateJournalSettings({ 

                              ...settings, 

                              quickAdd: { ...(settings.quickAdd || {}), categoryScopeModes: newModes } 

                            });

                          }}

                        >

                          <option value="">Use default</option>

                          <option value="after">Text after</option>

                          <option value="before">Text before</option>

                          <option value="all">All text</option>

                        </select>

                      </div>

                    );

                  })}

                </div>

              </div>

            </div>

            

            <button style={{ ...S.btn, width: '100%' }} onClick={() => setShowJournalSettings(false)}>Done</button>

          </div>

        </div>

      )}

    </div>

  );

};



// ============== END JOURNAL PAGE ==============



// ============== LOOT SYSTEM ==============



const LOOT_RARITIES = {

  common:    { name: 'Common',    color: '#9a9a8a', glow: 'rgba(154,154,138,0.15)', border: '#3a3a35' },

  uncommon:  { name: 'Uncommon',  color: '#4a9e4a', glow: 'rgba(74,158,74,0.18)',   border: '#2a5a2a' },

  rare:      { name: 'Rare',      color: '#4a7eb8', glow: 'rgba(74,126,184,0.2)',   border: '#2a4a6a' },

  epic:      { name: 'Epic',      color: '#9a4ab8', glow: 'rgba(154,74,184,0.22)',  border: '#5a2a6a' },

  legendary: { name: 'Legendary', color: '#d4a020', glow: 'rgba(212,160,32,0.25)',  border: '#6a5020' },

};

const LOOT_CATEGORIES = {

  weapon: { name: 'Weapons', icon: '⚔️' }, armor: { name: 'Armor', icon: '🛡️' },

  potion: { name: 'Potions', icon: '🧪' }, scroll: { name: 'Scrolls', icon: '📜' },

  ingredient: { name: 'Materials', icon: '🌿' }, gear: { name: 'Gear', icon: '🎒' },

  food: { name: 'Food', icon: '🍖' }, misc: { name: 'Misc', icon: '📦' }, currency: { name: 'Currency', icon: '💰' },

};

const getLootCatIcon = (cat) => LOOT_CATEGORIES[cat]?.icon || '📦';

const getLootRarity = (id) => LOOT_RARITIES[id] || LOOT_RARITIES.common;



const LOOT_DROP_MODES = {

  selectN: { label: 'Pick N', desc: 'Roll N random items, equal chance' },

  individualPercent: { label: 'Per-Item %', desc: 'Each item rolls independently' },

  combinedPercent: { label: 'Weighted %', desc: 'Chances must total 100%' },

};



const injectLootStyles = () => {

  if (document.getElementById('loot-v2-styles')) return;

  const s = document.createElement('style'); s.id = 'loot-v2-styles';

  s.textContent = `

    @keyframes lootFadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}

    @keyframes lootSlideIn{from{opacity:0;transform:translateX(-12px)}to{opacity:1;transform:translateX(0)}}

    @keyframes legendaryBorder{0%{border-color:#8a6a20}33%{border-color:#d4a020}66%{border-color:#f0c040}100%{border-color:#8a6a20}}

    @keyframes contestGlow{0%,100%{box-shadow:0 0 8px rgba(255,165,2,0.3)}50%{box-shadow:0 0 18px rgba(255,165,2,0.6)}}

    @keyframes rollResult{0%{transform:scale(0);opacity:0}60%{transform:scale(1.3)}100%{transform:scale(1);opacity:1}}

    @keyframes lootSpin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}

    @keyframes lockPop{0%{transform:scale(1)}50%{transform:scale(1.3)}100%{transform:scale(1)}}

    .loot-sys *{box-sizing:border-box}

    .loot-sys ::-webkit-scrollbar{width:6px}

    .loot-sys ::-webkit-scrollbar-track{background:rgba(0,0,0,0.3)}

    .loot-sys ::-webkit-scrollbar-thumb{background:rgba(155,89,182,0.3);border-radius:3px}

    .loot-sys .lb{cursor:pointer;user-select:none;transition:all 0.2s ease;border:none}

    .loot-sys .lb:hover:not(:disabled){filter:brightness(1.15);transform:translateY(-1px)}

    .loot-sys .lb:active:not(:disabled){transform:scale(0.97)}

    .loot-sys .lb:disabled{opacity:0.35;cursor:not-allowed}

    .loot-sys .lc{transition:all 0.25s ease}

    .loot-sys .lc:hover{transform:translateY(-2px)}

    .loot-sys .li{transition:border-color 0.2s ease;outline:none;color-scheme:dark}

    .loot-sys .li:focus{border-color:rgba(155,89,182,0.6)!important}

  `;

  document.head.appendChild(s);

};



const deriveLootColors = (theme) => {

  if (!theme?.colors) {

    const c = THEMES['fantasy-classic'].colors;

    return { bg: c.bgSecondary, bgCard: c.bgCardSolid, bgCardHover: c.bgCardHover, bgInput: c.bgInput, border: c.borderLight, borderMed: c.borderPrimary, borderAccent: c.borderAccent, text: c.textPrimary, textHead: c.accentLight, textSub: c.textSecondary, textMuted: c.textMuted, accent: c.accent, accentLight: c.accentLight, accentGlow: c.accentGlow, gold: c.warning || c.accent, green: c.success, greenBg: c.successBg, red: c.danger, redBg: c.dangerBg, blue: c.info, blueBg: c.infoBg, warning: c.warning, warningBg: c.warningBg, buffBg: c.buffBg, buffBorder: c.buffBorder, buffText: c.buffText, debuffBg: c.debuffBg, debuffBorder: c.debuffBorder, debuffText: c.debuffText, hpHigh: c.hpHigh, hpMid: c.hpMid, hpLow: c.hpLow };

  }

  const c = theme.colors;

  return { bg: c.bgSecondary, bgCard: c.bgCardSolid, bgCardHover: c.bgCardHover, bgInput: c.bgInput, border: c.borderLight, borderMed: c.borderPrimary, borderAccent: c.borderAccent, text: c.textPrimary, textHead: c.accentLight, textSub: c.textSecondary, textMuted: c.textMuted, accent: c.accent, accentLight: c.accentLight, accentGlow: c.accentGlow, gold: c.warning || c.accent, green: c.success, greenBg: c.successBg, red: c.danger, redBg: c.dangerBg, blue: c.info, blueBg: c.infoBg, warning: c.warning, warningBg: c.warningBg, buffBg: c.buffBg, buffBorder: c.buffBorder, buffText: c.buffText, debuffBg: c.debuffBg, debuffBorder: c.debuffBorder, debuffText: c.debuffText, hpHigh: c.hpHigh, hpMid: c.hpMid, hpLow: c.hpLow };

};



// CC BY 3.0 — game-icons.net/1x1/skoll/open-treasure-chest — by Skoll

const TreasureChestIcon = ({ size = 18, color = 'currentColor' }) => (

  <svg width={size} height={size} viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}>

    <path fill={color} d="M410.365 101.005c8.21-22.26 16.21-31.12 20.33-34.45 3.06-2.48 5.73-3.42 7.92-2.81 4 1.13 8.49 7.45 11.88 16.89 10.89 30.34 10 84.28-.93 129.51zm-286 72.92c7.52-31 10.28-66.13 7.77-94.92l-43.6-4.86zm289.46-113l-301.2-33.53c-2.5-.28-5.24 1.46-7.11 3-3.67 3-10.42 10.32-17.66 27.64l308.68 34.34c5.16-13.25 11.02-23.89 17.31-31.43zm-228.78 298.71v-70.72l10.76 1.19 42.24 5.18v70.51zm16-40.34a13 13 0 0 0 5.34 10.29l-2.34 24.42 17 1.74-4-25a9.54 9.54 0 0 0 5-9.15 13.64 13.64 0 0 0-11.06-12.59s.17.1.13.1c-5.95-.68-11.07 3.9-10.07 10.1zm53 64.45l-85-9.84v-86.72l-1.05-.09a8.14 8.14 0 0 1-7.27 6.71 8 8 0 0 1 5.23 8.9 8 8 0 0 1-8 6.66c8.453 4.004 4.341 16.778-4.86 15.1a8 8 0 0 1-8 13.8 8.01 8.01 0 0 1-12.28 10.29v.09a8 8 0 0 1-3.86 8.37l9.13 5.35v14.25l-12 7.13-12-7.12v-14.26l8.15-4.82a8.21 8.21 0 0 1-5.07-5.92.418.418 0 0 1 0-.1 8 8 0 0 1-15.18-5c-6.851 7.214-18.094-2.065-12.31-10.16-8.346 4.519-16.217-6.676-9.14-13-9.17 2.661-14.453-10.083-6.09-14.69a8 8 0 0 1-3.21-15.67c-9.294-1.047-9.548-14.463-.3-15.86-.669-.164-1.264-.473-1.83-.76l-17.24-1.86.6 167.11 309.18 34.49-.6-165.83-107-13.05zm140.06-164l4.72 1.91.91.58 38.72 4.31-23.26-64.77-12.82 37c-.16.46-3.41 9.8-8.27 20.99zm-208.54-39.74l5 5.49 12.75-11.15 21.45-2.28 16.61 15.35 10.51 8.73 18.54-9.29 3.44.5c.12-.67.25-1.34.38-2 3.08-16.1 7.35-30.16 7.53-30.75l13.39-43.91 16.88 42.71 8.42 21.42 10.66-12.39 22.14-25.73 5.78 33.45 3.29 19.1 17.1-9.64 35.09-19.79-18.48-51.4-247.86-27.61c2.51 34.94-1.85 77.32-12.39 112h2.32l7-12.86h40.46zm-111.29 97.39c7.6 2.1 7.9 12.766.43 15.29 7.737.867 9.802 11.153 3 14.94 7.653-.548 11.614 8.947 5.84 14 7.313-2.115 13.168 6.216 8.7 12.38 6.288-3.518 13.657 2.417 11.56 9.31 4.53-4.723 12.506-2.304 13.65 4.14 2.057-5.713 9.48-7.141 13.51-2.6-1.285-6.404 5.23-11.566 11.17-8.85-4.564-5.77.425-14.123 7.67-12.84-6.419-4.541-3.122-14.648 4.74-14.53-7.316-3.503-5.375-14.415 2.7-15.18a8 8 0 0 1-5.38-8l-76.43-8.26c-.41.19-.746.15-1.16.2zm367.54 139.08l-.59-163.86-8.67 7-55.51 46.79.58 162zm-26.23-165.2l-24.11-15.27-4.18-1.69c-5.91 11.52-13.39 23-22.66 27.88-5.44 2.88-12.22 4.34-20.16 4.34-11.13 0-24.75-2.91-37.35-8-10-4-23.3-11-30.26-21.34-4.9-7.29-6.64-17.77-5.31-32.92l-21.78 10.93-19-15.8-11.42-10.53-9.16 1-20.45 17.83-11-11.7h-24.21l-17.61 32-5.7-7.2-4.42 4.85-10.76 16.35-12.29 4.91L97.611 256h-12.2l-2.776 6.005 76.9 8.21a8.15 8.15 0 0 1 2-2.9 8 8 0 0 1 10.31-.46 1.657 1.657 0 0 1-.14-.24c-4.955-8.368 6.459-16.62 12.87-9.375 6.412 7.245-3.167 17.571-10.87 11.635a8 8 0 0 1 1.12 2.89l22.62 2.44 168.54 20.57 51.49-43.38zm-28.34-57.73l-36.88 20.79-7.14-41.47-28 32.51-18.13-46.11s-16.65 54.58-7 69c7.69 11.45 35.42 22.25 54.33 22.25 5 0 9.43-.76 12.67-2.48 13.8-7.31 30.15-54.49 30.15-54.49zm-317.08 270.8v-.2c0-3.77-8.21-6.83-18.33-6.83-10.12 0-18.33 3.06-18.33 6.83 0 3.21 6 5.9 14 6.63v.2c0 3.77 8.21 6.83 18.33 6.83 10.12 0 18.33-3.06 18.33-6.83-.01-3.21-5.98-5.9-14-6.63zm350 6.63c-10.13 0-18.33 3.06-18.33 6.83s8.21 6.83 18.33 6.83c10.12 0 18.33-3.06 18.33-6.83s-8.25-6.8-18.38-6.8zm40-16.28c-10.13 0-18.33 3.06-18.33 6.83s8.21 6.83 18.33 6.83c10.12 0 18.33-3.06 18.33-6.83s-8.22-6.83-18.34-6.83z"/>

  </svg>

);



// CC BY 3.0 — game-icons.net/1x1/lorc/biohazard — by Lorc

const BiohazardIcon = ({ size = 18, color = 'currentColor' }) => (

  <svg width={size} height={size} viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}>

    <path fill={color} d="M158.03 41.656c-19.755 22.934-31.78 52.76-31.78 85.406 0 23.544 6.228 45.667 17.125 64.782-22.02.108-44.312 5.78-64.72 17.562-28.266 16.32-48.113 41.64-58.093 70.22 9.484-15.492 23.012-28.562 39.844-38.282 23.238-13.417 49.246-17.604 73.813-13.72-2.547 9.988-3.907 20.45-3.907 31.22 0 57.436 38.516 106.03 91.062 121.375-9.005 22.822-25.49 42.892-48.406 56.124-16.838 9.722-35.253 14.313-53.407 14.78 29.763 5.674 61.89 1.723 90.187-14.624 20.317-11.732 36.307-28.117 47.406-47.03 11.1 18.922 27.08 35.325 47.406 47.06 28.296 16.35 60.422 20.268 90.188 14.595-18.163-.466-36.574-5.03-53.406-14.75-22.99-13.275-39.514-33.43-48.5-56.344 52.175-15.572 90.312-64.01 90.312-121.186 0-10.714-1.356-21.122-3.875-31.063 24.812-4.113 51.127.025 74.626 13.595 16.832 9.725 30.362 22.788 39.844 38.28-9.978-28.58-29.826-53.896-58.094-70.217-20.29-11.718-42.444-17.393-64.344-17.563 10.906-19.12 17.157-41.258 17.157-64.813 0-32.68-12.678-62.462-32.47-85.406 8.668 15.962 13.906 34.184 13.906 53.625 0 27.412-9.775 52.516-26.03 72.032-22.69-21.61-53.39-34.906-87.126-34.906-33.412 0-63.84 13.036-86.47 34.28-15.94-19.424-25.5-44.29-25.5-71.405 0-19.44 4.58-37.662 13.25-53.624zm98.72 109.438c28.748 0 54.833 11.19 74.125 29.47-17.373 14.994-39.375 24.76-63.594 26.874v19.812c12.912 3.6 22.283 15.35 22.283 29.47 0 2.39-.272 4.708-.782 6.936l17.44 9.5c14.018-19.577 33.415-33.477 54.967-40.844 2.14 8.485 3.282 17.372 3.282 26.532 0 48.918-32.41 90.075-76.97 103.28-5.058-23.923-2.37-49.563 9.125-72.905l-17.438-9.5c-5.385 4.726-12.45 7.592-20.25 7.592-8.056 0-15.342-3.05-20.78-8.062l-20.032 10.78c11.2 23.207 13.728 48.628 8.656 72.345-44.973-12.95-77.78-54.324-77.78-103.53 0-9.25 1.163-18.223 3.344-26.783 22.14 7.39 42.055 21.663 56.28 41.875l20.345-10.968c-.414-2.02-.626-4.103-.626-6.25 0-13.38 8.396-24.635 20.25-28.845V207.53c-24.962-1.917-47.63-11.958-65.375-27.5 19.238-17.95 45.074-28.936 73.53-28.936z"/>

  </svg>

);



// CC BY 3.0 — game-icons.net/1x1/delapouite/plague-doctor-profile — by Delapouite

const PlagueDoctorIcon = ({ size = 18, color = 'currentColor' }) => (

  <svg width={size} height={size} viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}>

    <path fill={color} d="M259.646 33.586c-44.698 10.116-96.576 26.075-141.98 42.926-42.563 15.796-79.721 32.796-97.816 44.22l28.634 40.006C85.45 141.836 163.58 106.833 258.87 78.725c.115-12.307.426-26.651.777-45.14zm-.755 63.922c-89.553 26.838-163.813 59.876-199.809 78.035l11.363 15.877c64.955-26.867 129.99-52.813 189.244-73.865-.436-6.558-.686-13.057-.798-20.047zm10.861 35.584c-63.467 22.407-134.187 50.745-204.494 79.949l-.063-.148c-22.345 11.147-36.325 23.22-47.084 35.552 3.174.194 6.684.262 10.803-.04 11.524-.847 26.29-3.532 42.982-7.69 33.387-8.316 74.56-22.461 115.413-38.873 40.852-16.412 81.444-35.104 113.841-52.438 20.416-10.922 37.096-21.52 48.778-30.168-27.24-.104-57.425 6.306-80.176 13.856zm18.371 43.3a1084.226 1084.226 0 0 1-16.488 8.057c6.635 4.076 11.482 10.968 14.367 18.592 3.944 10.42 4.892 22.894 2.566 35.8-2.325 12.907-7.527 24.049-14.789 32.046s-17.434 13.233-28.275 10.888c-10.842-2.344-18.433-11.423-22.377-21.843-3.944-10.42-4.894-22.894-2.568-35.801 1.264-7.02 3.387-13.512 6.222-19.266a1358.468 1358.468 0 0 1-32.761 13.678 1248.826 1248.826 0 0 1-43.65 16.613c11.354 20.924 27.32 38.077 51.366 50.307l9.17 4.664-5.842 8.469c-8.506 12.333-6.783 30.624 1.875 43.974 8.659 13.35 21.871 20.897 38.64 14.395 59.434-23.047 104.228-28.81 145.775-21.113 34.839 6.454 67.127 22.398 103.834 43.81-12.425-28.256-33.968-57.384-60.729-82.209-33.985-31.525-76.04-56.09-116.33-64.65l-5.635-1.198-1.271-5.617c-.802-3.538-8.001-29.713-23.1-49.595zm-29.527 22.372c-2.798.11-6.202 1.744-9.908 5.826-4.563 5.024-8.733 13.374-10.53 23.348-1.796 9.973-.835 19.436 1.643 25.982 2.477 6.546 5.817 9.469 8.896 10.135 3.08.666 7.182-.646 11.744-5.67 4.563-5.024 8.735-13.377 10.532-23.35 1.796-9.973.833-19.436-1.645-25.982-2.478-6.547-5.817-9.469-8.896-10.135a7.293 7.293 0 0 0-1.836-.154zm-125.414 42.4c-18.266 6.199-35.82 11.553-51.98 15.748-4.18 24.009 9.737 55.444 19.53 71.184 36.172 26.058 68.19 52.072 126.23 70.021l-4.89-22.824c-12.567-3.576-23.285-12.214-30.234-22.93-10.383-16.01-14.055-37.249-6.25-55.41-23.92-14.166-40.703-33.554-52.406-55.789zm-56.55 92.08c-9.031 13.247-23.97 18.894-37.823 21.772 15.285 25.397 49.541 49.906 93.216 72.103 41.305 20.993 90.376 40.17 138.215 58.6-11.892-19.381-20.046-40.812-22.304-63.33-86.163-20.547-125.096-57.285-171.303-89.145zm-27.696 61.57C36.052 424.224 26.8 455.703 25.279 487h198.588c-34.824-13.775-69.286-28.225-99.998-43.834-29.2-14.84-55.252-30.589-74.933-48.352z"/>

  </svg>

);



// Game-icons.net SVG icon registry — renders SVG icons stored as "gi:name" strings

const GAME_ICON_REGISTRY = {

  'gi:biohazard': BiohazardIcon,

  'gi:plague-doctor': PlagueDoctorIcon,

  'gi:treasure-chest': TreasureChestIcon,

};

const renderGameIcon = (icon, size = 20, color = 'currentColor') => {

  if (typeof icon === 'string' && icon.startsWith('gi:')) {

    const Comp = GAME_ICON_REGISTRY[icon];

    return Comp ? <Comp size={size} color={color} /> : icon;

  }

  if (typeof icon !== 'string') return icon; // Already a React element

  return icon; // Regular emoji string

};

const isGameIcon = (icon) => typeof icon === 'string' && icon.startsWith('gi:');



const LootSectionLabel = ({ icon, children, sc, count, right }) => (

  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>

    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

      {icon && <span style={{ fontSize: '0.85rem' }}>{icon}</span>}

      <span style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: sc.textSub, fontVariant: 'small-caps' }}>{children}</span>

      {count !== undefined && <span style={{ fontSize: '0.65rem', color: sc.textMuted }}>({count})</span>}

    </div>

    {right}

  </div>

);



const LootTinyInput = ({ value, onChange, sc, width = 50, type, ...rest }) => (

  <input className="li" value={value}

    type={type === 'number' ? 'text' : (type || 'text')}

    inputMode={type === 'number' ? 'numeric' : undefined}

    pattern={type === 'number' ? '[0-9]*' : undefined}

    onChange={e => {

      if (type === 'number') {

        const v = e.target.value.replace(/[^0-9]/g, '');

        onChange({ target: { value: v === '' ? '0' : v } });

      } else { onChange(e); }

    }}

    style={{ width, backgroundColor: '#1a1028', border: `1px solid ${sc.border}`, borderRadius: 4, padding: '2px 4px', color: sc.text, fontSize: '0.72rem', textAlign: 'center', outline: 'none', ...rest.style }} {...rest} />

);



const LootStepperInput = ({ value, onChange, min = 1, max = 999, sc, label, color, isGreen }) => {

  const [editing, setEditing] = useState(false);

  const [draft, setDraft] = useState(String(value));

  const commitDraft = () => { setEditing(false); const v = parseInt(draft); if (isNaN(v) || v < min) onChange(min); else if (v > max) onChange(max); else onChange(v); };

  const step = (d) => onChange(Math.max(min, Math.min(max, value + d)));

  const inputBg = isGreen ? 'rgba(10,20,15,0.7)' : 'rgba(20,12,35,0.8)';

  const borderC = isGreen ? sc.green + '50' : sc.border;

  return (

    <div style={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0 }} onClick={e => e.stopPropagation()}>

      {label && <span style={{ fontSize: '0.5rem', color: sc.textMuted, marginRight: 3 }}>{label}</span>}

      <button className="lb" onClick={() => step(-1)} style={{ width: 14, height: 18, borderRadius: '3px 0 0 3px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: inputBg, border: `1px solid ${borderC}`, borderRight: 'none', color: sc.textMuted, fontSize: '0.6rem', padding: 0 }}>−</button>

      {editing ? (

        <input type="text" inputMode="numeric" autoFocus value={draft} onChange={e => setDraft(e.target.value.replace(/[^0-9]/g, ''))} onBlur={commitDraft} onKeyDown={e => e.key === 'Enter' && commitDraft()} style={{ width: 24, height: 18, backgroundColor: inputBg, border: `1px solid ${borderC}`, borderRadius: 0, padding: 0, color: color || sc.text, fontSize: '0.6rem', textAlign: 'center', outline: 'none' }} />

      ) : (

        <div onClick={() => { setDraft(String(value)); setEditing(true); }} style={{ width: 24, height: 18, backgroundColor: inputBg, border: `1px solid ${borderC}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: color || sc.text, fontSize: '0.6rem', fontWeight: 600, cursor: 'text' }}>{value}</div>

      )}

      <button className="lb" onClick={() => step(1)} style={{ width: 14, height: 18, borderRadius: '0 3px 3px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', background: inputBg, border: `1px solid ${borderC}`, borderLeft: 'none', color: sc.textMuted, fontSize: '0.6rem', padding: 0 }}>+</button>

    </div>

  );

};



const LootPill = ({ li, item, sc, onToggleGuaranteed, onChangeQty, onChangeChance, onRemove, interactive = true, dropMode }) => {

  const [confirmRemove, setConfirmRemove] = useState(false);

  if (!item) return null;

  const r = getLootRarity(item.rarity);

  const isG = li.guaranteed;

  const showPercent = !isG && (dropMode === 'combinedPercent' || dropMode === 'individualPercent');

  return (

    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 6, padding: '5px 5px 5px 10px', background: isG ? sc.greenBg : 'rgba(255,255,255,0.03)', border: `1.5px solid ${isG ? sc.green + '45' : sc.border}`, borderRadius: 20, userSelect: 'none', transition: 'all 0.2s ease' }}>

      {interactive && !confirmRemove && <button className="lb" onClick={e => { e.stopPropagation(); setConfirmRemove(true); }} title="Remove item" style={{ position: 'absolute', top: -5, right: -5, zIndex: 10, width: 16, height: 16, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: sc.redBg, border: `1px solid ${sc.red}40`, color: sc.red, fontSize: '0.55rem', fontWeight: 700, padding: 0 }}>✕</button>}

      {interactive && confirmRemove && <div style={{ position: 'absolute', top: -8, right: -4, zIndex: 10, display: 'flex', gap: 2 }} onClick={e => e.stopPropagation()}>

        <button className="lb" onClick={() => onRemove()} style={{ padding: '2px 7px', borderRadius: 5, fontSize: '0.55rem', fontWeight: 700, background: sc.redBg, border: `1px solid ${sc.red}50`, color: sc.red }}>Remove?</button>

        <button className="lb" onClick={() => setConfirmRemove(false)} style={{ padding: '2px 5px', borderRadius: 5, fontSize: '0.55rem', background: 'rgba(255,255,255,0.04)', border: `1px solid ${sc.border}`, color: sc.textMuted }}>No</button>

      </div>}

      <div onClick={interactive ? onToggleGuaranteed : undefined} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: interactive ? 'pointer' : 'default', flex: 1, minWidth: 0 }}>

        <span style={{ fontSize: '0.85rem', flexShrink: 0 }}>{item.iconRef && getIconByKey(item.iconRef) ? <RpgIcon iconKey={item.iconRef} size={18} /> : getLootCatIcon(item.category)}</span>

        <div style={{ minWidth: 0 }}>

          <div style={{ fontSize: '0.76rem', color: r.color, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 130 }}>{item.name}</div>

          <div style={{ fontSize: '0.55rem', color: isG ? sc.green : sc.textMuted }}>{isG ? '✓ Guaranteed' : '🎲 Random'}{!interactive && li.qty > 1 && <span> · ×{li.qty}</span>}{!interactive && showPercent && <span style={{ color: sc.warning }}> · {li.dropChance}%</span>}</div>

        </div>

      </div>

      {interactive && <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>

        {showPercent && <LootStepperInput value={li.dropChance} onChange={v => onChangeChance(v)} min={1} max={100} sc={sc} label="%" color={sc.warning} isGreen={isG} />}

        <LootStepperInput value={li.qty} onChange={v => onChangeQty(v)} min={1} max={99} sc={sc} label="×" color={sc.text} isGreen={isG} />

      </div>}

    </div>

  );

};



const LootUndoToast = ({ item, sc, onUndo, onDismiss }) => {

  useEffect(() => { const t = setTimeout(onDismiss, 5000); return () => clearTimeout(t); }, []);

  if (!item) return null;

  const r = getLootRarity(item.rarity);

  return (

    <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 9500, display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', background: sc.bgCard, border: `2px solid ${sc.borderMed}`, borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.7)', animation: 'lootFadeIn 0.2s ease' }}>

      <span style={{ fontSize: '0.8rem' }}>{item.iconRef && getIconByKey(item.iconRef) ? <RpgIcon iconKey={item.iconRef} size={18} /> : getLootCatIcon(item.category)}</span>

      <span style={{ fontSize: '0.78rem', color: r.color, fontWeight: 600 }}>{item.name}</span>

      <span style={{ fontSize: '0.72rem', color: sc.textMuted }}>removed</span>

      <button className="lb" onClick={onUndo} style={{ padding: '4px 12px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700, background: `${sc.accent}15`, border: `1px solid ${sc.accent}50`, color: sc.accentLight }}>↩ Undo</button>

      <button className="lb" onClick={onDismiss} style={{ background: 'none', border: 'none', color: sc.textMuted, fontSize: '0.65rem', padding: '2px' }}>✕</button>

    </div>

  );

};



const LootDropModeSelector = ({ mode, onChange, sc }) => (

  <div style={{ display: 'flex', gap: 2 }}>

    {Object.entries(LOOT_DROP_MODES).map(([k, v]) => (

      <button key={k} className="lb" onClick={() => onChange(k)} title={v.desc} style={{ padding: '2px 7px', fontSize: '0.58rem', borderRadius: 4, fontWeight: mode === k ? 700 : 500, background: mode === k ? `${sc.accent}18` : 'transparent', border: `1px solid ${mode === k ? sc.accent + '50' : sc.border}`, color: mode === k ? sc.accentLight : sc.textMuted }}>{v.label}</button>

    ))}

  </div>

);



const LootEnemyEditorModal = ({ enemy, allItems, sc, onSave, onCancel }) => {

  const [data, setData] = useState(() => enemy || { id: generateId(), name: '', type: 'Humanoid', cr: '1', hp: { current: 10, max: 10 }, ac: 10, notes: '', mechanics: [], lootTable: { items: [], xp: 100, rollCount: 1, dropMode: 'selectN' } });

  const [newMechanic, setNewMechanic] = useState('');

  const addMech = () => { if (!newMechanic.trim()) return; setData(p => ({ ...p, mechanics: [...(p.mechanics || []), newMechanic.trim()] })); setNewMechanic(''); };

  return (

    <div style={{ position: 'fixed', inset: 0, zIndex: 8000, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 30, overflowY: 'auto' }} onClick={onCancel}>

      <div onClick={e => e.stopPropagation()} style={{ background: sc.bgCard, border: `2px solid ${sc.borderMed}`, borderRadius: 12, padding: 20, maxWidth: 540, width: '92%', marginBottom: 30, animation: 'lootFadeIn 0.2s ease' }}>

        <LootSectionLabel icon={data.name ? '✏️' : '➕'} sc={sc}>{data.name ? 'Edit Enemy' : 'New Enemy'}</LootSectionLabel>

        <div style={{ display: 'grid', gap: 10 }}>

          <input className="li" value={data.name} onChange={e => setData({ ...data, name: e.target.value })} placeholder="Enemy name..." style={{ padding: '8px 12px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.text, fontSize: '0.88rem' }} autoFocus />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>

            <div><label style={{ fontSize: '0.6rem', color: sc.textMuted }}>Type</label><input className="li" value={data.type} onChange={e => setData({ ...data, type: e.target.value })} style={{ width: '100%', padding: '4px 8px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 4, color: sc.text, fontSize: '0.78rem' }} /></div>

            <div><label style={{ fontSize: '0.6rem', color: sc.textMuted }}>CR</label><input className="li" value={data.cr} onChange={e => setData({ ...data, cr: e.target.value })} style={{ width: '100%', padding: '4px 8px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 4, color: sc.text, fontSize: '0.78rem' }} /></div>

            <div><label style={{ fontSize: '0.6rem', color: sc.textMuted }}>HP</label><input className="li" type="number" value={data.hp?.max || 10} onChange={e => setData({ ...data, hp: { current: parseInt(e.target.value) || 10, max: parseInt(e.target.value) || 10 } })} style={{ width: '100%', padding: '4px 8px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 4, color: sc.text, fontSize: '0.78rem' }} /></div>

            <div><label style={{ fontSize: '0.6rem', color: sc.textMuted }}>AC</label><input className="li" type="number" value={data.ac} onChange={e => setData({ ...data, ac: parseInt(e.target.value) || 10 })} style={{ width: '100%', padding: '4px 8px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 4, color: sc.text, fontSize: '0.78rem' }} /></div>

          </div>

          <textarea className="li" value={data.notes || ''} onChange={e => setData({ ...data, notes: e.target.value })} placeholder="Notes, lore, tactics..." rows={2} style={{ padding: '8px 12px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.text, fontSize: '0.78rem', resize: 'vertical' }} />

          <div><label style={{ fontSize: '0.6rem', color: sc.textMuted }}>Mechanics / Abilities</label>

            <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}><input className="li" value={newMechanic} onChange={e => setNewMechanic(e.target.value)} onKeyDown={e => e.key === 'Enter' && addMech()} placeholder="e.g. Multiattack: Two claw attacks" style={{ flex: 1, padding: '4px 8px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 4, color: sc.text, fontSize: '0.72rem' }} /><button className="lb" onClick={addMech} style={{ padding: '4px 10px', background: `${sc.accent}12`, border: `1px solid ${sc.accent}40`, borderRadius: 4, color: sc.accentLight, fontSize: '0.72rem' }}>+</button></div>

            {(data.mechanics || []).map((m, i) => <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', marginBottom: 2, background: sc.blueBg, border: `1px solid ${sc.blue}18`, borderRadius: 5, fontSize: '0.66rem', color: sc.textSub }}><span style={{ color: sc.blue, fontWeight: 600 }}>⚙</span><span style={{ flex: 1 }}>{m}</span><button className="lb" onClick={() => setData(p => ({ ...p, mechanics: p.mechanics.filter((_, j) => j !== i) }))} style={{ background: 'none', border: 'none', color: sc.red, fontSize: '0.6rem', padding: '0 2px' }}>✕</button></div>)}

          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>

            <div><label style={{ fontSize: '0.6rem', color: sc.textMuted }}>XP Reward</label><input className="li" type="number" value={data.lootTable.xp} onChange={e => setData({ ...data, lootTable: { ...data.lootTable, xp: parseInt(e.target.value) || 0 } })} style={{ width: 80, padding: '4px 8px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 4, color: sc.text, fontSize: '0.78rem' }} /></div>

            <div style={{ flex: 1 }}><label style={{ fontSize: '0.6rem', color: sc.textMuted }}>Add Item to Loot Table</label>

              <select className="li" onChange={e => { if (!e.target.value) return; setData(p => ({ ...p, lootTable: { ...p.lootTable, items: [...p.lootTable.items, { itemId: e.target.value, qty: 1, guaranteed: false, dropChance: 50 }] } })); e.target.value = ''; }} style={{ width: '100%', padding: '4px 8px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 4, color: sc.text, fontSize: '0.72rem' }}>

                <option value="">Select item...</option>

                {Object.values(allItems).map(item => <option key={item.id} value={item.id}>{getLootCatIcon(item.category)} {item.name} ({getLootRarity(item.rarity).name})</option>)}

              </select>

            </div>

          </div>

          {data.lootTable.items.length > 0 && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>

            {data.lootTable.items.map((li, idx) => <LootPill key={idx} li={li} item={allItems[li.itemId]} sc={sc} interactive={true} dropMode={data.lootTable.dropMode || 'selectN'}

              onToggleGuaranteed={() => { const items = [...data.lootTable.items]; items[idx] = { ...items[idx], guaranteed: !items[idx].guaranteed, dropChance: !items[idx].guaranteed ? 100 : 50 }; setData({ ...data, lootTable: { ...data.lootTable, items } }); }}

              onChangeQty={v => { const items = [...data.lootTable.items]; items[idx] = { ...items[idx], qty: v }; setData({ ...data, lootTable: { ...data.lootTable, items } }); }}

              onChangeChance={v => { const items = [...data.lootTable.items]; items[idx] = { ...items[idx], dropChance: v }; setData({ ...data, lootTable: { ...data.lootTable, items } }); }}

              onRemove={() => setData({ ...data, lootTable: { ...data.lootTable, items: data.lootTable.items.filter((_, i) => i !== idx) } })}

            />)}

          </div>}

        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>

          <button className="lb" onClick={onCancel} style={{ padding: '8px 20px', background: 'rgba(255,255,255,0.04)', border: `1px solid ${sc.border}`, borderRadius: 8, color: sc.textSub, fontSize: '0.82rem' }}>Cancel</button>

          <button className="lb" onClick={() => { if (data.name.trim()) onSave(data); }} disabled={!data.name.trim()} style={{ padding: '8px 24px', background: `${sc.green}18`, border: `2px solid ${sc.green}50`, borderRadius: 8, color: sc.green, fontSize: '0.82rem', fontWeight: 700 }}>💾 Save Enemy</button>

        </div>

      </div>

    </div>

  );

};



const LootRollOverlay = ({ enemy, allItems, sc, onComplete, onCancel }) => {

  const [lootItems, setLootItems] = useState(() => JSON.parse(JSON.stringify(enemy.lootTable.items)));

  const [phase, setPhase] = useState('configure');

  const [selected, setSelected] = useState([]);

  const [currentRoll, setCurrentRoll] = useState(0);

  const dropMode = enemy.lootTable.dropMode || 'selectN';

  const lootRows = lootItems.map((item, index) => ({ ...item, _lootTableRowIndex: index }));

  const guaranteed = lootRows.filter(i => i.guaranteed);

  const nonGuaranteed = lootRows.filter(i => !i.guaranteed);

  const rollCount = Math.min(enemy.lootTable.rollCount || 1, nonGuaranteed.length);

  const toggleG = (idx) => setLootItems(p => { const n = [...p]; n[idx] = { ...n[idx], guaranteed: !n[idx].guaranteed, dropChance: !n[idx].guaranteed ? 100 : 50 }; return n; });

  const doRoll = () => {

    setPhase('rolling');

    let pickedDrops = [];

    const toDrop = (row) => ({ definitionId: row.itemId, quantity: row.qty, lootTableRowIndex: row._lootTableRowIndex });

    if (dropMode === 'selectN') { const shuffled = [...nonGuaranteed].sort(() => Math.random() - 0.5); pickedDrops = shuffled.slice(0, rollCount).map(toDrop); }

    else if (dropMode === 'combinedPercent') { const remaining = [...nonGuaranteed]; for (let i = 0; i < rollCount && remaining.length > 0; i++) { const totalW = remaining.reduce((s, it) => s + (it.dropChance || 1), 0); let roll = Math.random() * totalW; let chosen = remaining[0]; for (const it of remaining) { roll -= (it.dropChance || 1); if (roll <= 0) { chosen = it; break; } } pickedDrops.push(toDrop(chosen)); remaining.splice(remaining.indexOf(chosen), 1); } }

    else { for (const it of nonGuaranteed) { if (Math.random() * 100 < (it.dropChance || 50)) pickedDrops.push(toDrop(it)); } }

    if (pickedDrops.length === 0 && nonGuaranteed.length > 0 && dropMode === 'individualPercent') { pickedDrops = [toDrop(nonGuaranteed[Math.floor(Math.random() * nonGuaranteed.length)])]; }

    let idx = 0;

    const interval = setInterval(() => { if (idx < pickedDrops.length) { setSelected(p => [...p, pickedDrops[idx]]); setCurrentRoll(idx + 1); idx++; } else { clearInterval(interval); setPhase('done'); } }, 600);

  };

  const finalItems = [...guaranteed.map(i => ({ definitionId: i.itemId, quantity: i.qty, lootTableRowIndex: i._lootTableRowIndex })), ...selected];

  const qtys = {}; lootItems.forEach(li => { if (li?.itemId) qtys[li.itemId] = li.qty; });

  const modeLabel = dropMode === 'selectN' ? `Pick ${rollCount} of ${nonGuaranteed.length}` : dropMode === 'combinedPercent' ? `Weighted pick ${rollCount}` : 'Per-item % chance';

  return (

    <div style={{ position: 'fixed', inset: 0, zIndex: 8000, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 40, overflowY: 'auto' }} onClick={onCancel}>

      <div onClick={e => e.stopPropagation()} style={{ background: sc.bgCard, border: `2px solid ${sc.borderMed}`, borderRadius: 14, padding: 24, maxWidth: 560, width: '92%', marginBottom: 40, boxShadow: `0 20px 60px rgba(0,0,0,0.8), 0 0 40px ${sc.accentGlow}`, animation: 'lootFadeIn 0.25s ease' }}>

        <LootSectionLabel icon="🎲" sc={sc}>Loot Roll</LootSectionLabel>

        {guaranteed.length > 0 && <div style={{ marginBottom: 14 }}>

          <div style={{ fontSize: '0.66rem', color: sc.green, fontWeight: 700, marginBottom: 6, letterSpacing: 1 }}>✓ GUARANTEED DROPS</div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>

            {lootItems.map((li, idx) => { if (!li.guaranteed) return null; const item = allItems[li.itemId]; if (!item) return null; const r = getLootRarity(item.rarity); return (<div key={idx} onClick={() => phase === 'configure' && toggleG(idx)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: sc.greenBg, border: `1.5px solid ${sc.green}45`, borderRadius: 20, cursor: phase === 'configure' ? 'pointer' : 'default' }} title="Click to make random"><span style={{ fontSize: '0.85rem' }}>{item.iconRef && getIconByKey(item.iconRef) ? <RpgIcon iconKey={item.iconRef} size={20} /> : getLootCatIcon(item.category)}</span><span style={{ fontSize: '0.76rem', color: r.color, fontWeight: 600 }}>{item.name}</span>{li.qty > 1 && <span style={{ fontSize: '0.6rem', color: sc.textMuted }}>×{li.qty}</span>}</div>); })}

          </div>

        </div>}

        {nonGuaranteed.length > 0 && <div style={{ marginBottom: 14 }}>

          <div style={{ fontSize: '0.66rem', color: sc.warning, fontWeight: 700, marginBottom: 6, letterSpacing: 1 }}>🎲 RANDOM DROPS — {modeLabel}</div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 6 }}>

            {lootItems.map((li, idx) => { if (li.guaranteed) return null; const item = allItems[li.itemId]; if (!item) return null; const r = getLootRarity(item.rarity); const isSel = selected.some(drop => drop.lootTableRowIndex === idx); return (<div key={idx} onClick={() => phase === 'configure' && toggleG(idx)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: isSel ? sc.greenBg : 'rgba(255,255,255,0.02)', border: `1px solid ${isSel ? sc.green + '60' : sc.border}`, borderRadius: 8, cursor: phase === 'configure' ? 'pointer' : 'default', opacity: phase === 'done' && !isSel ? 0.3 : 1, transition: 'all 0.3s', animation: isSel ? 'lootSlideIn 0.4s ease' : undefined }} title="Click to guarantee"><span style={{ fontSize: '1rem' }}>{item.iconRef && getIconByKey(item.iconRef) ? <RpgIcon iconKey={item.iconRef} size={24} /> : getLootCatIcon(item.category)}</span><div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: '0.78rem', color: r.color, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div><div style={{ fontSize: '0.6rem', color: sc.textMuted }}>{item.shortDesc}</div></div>{dropMode !== 'selectN' && <span style={{ fontSize: '0.6rem', color: sc.warning, fontWeight: 700, flexShrink: 0 }}>{li.dropChance}%</span>}{isSel && <span style={{ color: sc.green, fontSize: '0.85rem' }}>✓</span>}</div>); })}

          </div>

        </div>}

        {phase === 'rolling' && <div style={{ textAlign: 'center', margin: '10px 0', color: sc.accent, fontSize: '0.85rem' }}><span style={{ display: 'inline-block', animation: 'lootSpin 1s linear infinite', marginRight: 8 }}>🎲</span>Rolling... {currentRoll}/{dropMode === 'individualPercent' ? nonGuaranteed.length : rollCount}</div>}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 16 }}>

          {phase === 'configure' && <><button className="lb" onClick={onCancel} style={{ padding: '8px 20px', background: 'rgba(255,255,255,0.04)', border: `1px solid ${sc.border}`, borderRadius: 8, color: sc.textSub, fontSize: '0.82rem' }}>Cancel</button><button className="lb" onClick={doRoll} style={{ padding: '8px 24px', background: `${sc.accent}20`, border: `2px solid ${sc.accent}60`, borderRadius: 8, color: sc.accentLight, fontSize: '0.88rem', fontWeight: 700, boxShadow: `0 0 20px ${sc.accentGlow}` }}>🎲 Roll for Loot!</button></>}

          {phase === 'done' && <button className="lb" onClick={() => onComplete(finalItems, qtys)} style={{ padding: '10px 30px', background: `${sc.green}18`, border: `2px solid ${sc.green}60`, borderRadius: 8, color: sc.green, fontSize: '0.9rem', fontWeight: 700 }}>✓ Confirm & Begin Distribution</button>}

        </div>

      </div>

    </div>

  );

};



const LootItemCard = ({ item, qty, sc, delay = 0, isVisible, claimedBy, contested, playerClaims, isPlayer, isDM, onClaim, onUnclaim, onToggleReveal, currentPlayerId, players }) => {

  const [hovered, setHovered] = useState(false);

  if (!item) return null;

  const r = getLootRarity(item.rarity);

  const isClaimed = !!claimedBy;

  const isClaimedByMe = claimedBy === currentPlayerId;

  const myClaimExists = playerClaims?.includes(currentPlayerId);

  const contestCount = playerClaims?.length || 0;

  if (isPlayer && !isVisible) return (

    <div style={{ background: sc.bgCard, border: `2px solid ${sc.border}`, borderRadius: 10, padding: 20, textAlign: 'center', opacity: 0.5, animation: `lootFadeIn 0.3s ease ${delay}ms both` }}>

      <span style={{ fontSize: '1.6rem' }}>🔒</span><div style={{ fontSize: '0.72rem', color: sc.textMuted, marginTop: 6 }}>Hidden</div>

    </div>

  );

  return (

    <div className="lc" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} style={{

      position: 'relative', background: hovered ? sc.bgCardHover : sc.bgCard,

      border: `2px solid ${isClaimed ? (isClaimedByMe ? sc.green + '80' : sc.textMuted + '40') : contested ? sc.warning + '80' : r.border + '60'}`,

      borderRadius: 10, overflow: 'hidden', animation: `lootFadeIn 0.3s ease ${delay}ms both`,

      boxShadow: hovered ? `0 12px 40px rgba(0,0,0,0.6), 0 0 30px ${r.glow}` : '0 4px 16px rgba(0,0,0,0.4)',

      opacity: isDM && !isVisible ? 0.55 : (isClaimed && !isClaimedByMe ? 0.45 : 1),

    }}>

      <div style={{ height: 2, background: `linear-gradient(90deg, transparent, ${r.color}80, transparent)` }} />

      {isDM && <button className="lb" onClick={e => { e.stopPropagation(); onToggleReveal?.(); }} title={isVisible ? 'Hide from players' : 'Reveal to players'} style={{ position: 'absolute', top: 6, left: 6, zIndex: 5, width: 26, height: 26, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isVisible ? sc.greenBg : 'rgba(0,0,0,0.5)', border: `1.5px solid ${isVisible ? sc.green + '60' : sc.textMuted + '40'}`, fontSize: '0.75rem' }}>{isVisible ? '🔓' : '🔒'}</button>}

      {qty > 1 && <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 5, background: sc.bgCard, border: `1px solid ${sc.borderMed}`, borderRadius: 8, padding: '1px 7px', fontSize: '0.68rem', fontWeight: 700, color: sc.textSub }}>×{qty}</div>}

      {isClaimed && <div style={{ position: 'absolute', top: 8, left: isDM ? 38 : 8, zIndex: 5, background: isClaimedByMe ? sc.greenBg : 'rgba(100,100,100,0.3)', border: `1px solid ${isClaimedByMe ? sc.green + '60' : '#555'}`, borderRadius: 8, padding: '1px 7px', fontSize: '0.6rem', fontWeight: 600, color: isClaimedByMe ? sc.green : sc.textMuted }}>✓ {claimedBy === currentPlayerId ? 'Yours' : (players || []).find(p => p.id === claimedBy)?.name || 'Claimed'}</div>}

      {contested && !isClaimed && contestCount > 1 && <div style={{ position: 'absolute', top: 8, left: isDM ? 38 : 8, zIndex: 5, background: sc.warningBg, border: `1px solid ${sc.warning}60`, borderRadius: 8, padding: '1px 7px', fontSize: '0.6rem', fontWeight: 600, color: sc.warning }}>⚔️ {contestCount} want</div>}

      <div style={{ height: 65, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `radial-gradient(ellipse at center, ${r.glow}, transparent 70%)`, fontSize: 30 }}>{item.iconRef && getIconByKey(item.iconRef) ? <RpgIcon iconKey={item.iconRef} size={48} /> : getLootCatIcon(item.category)}</div>

      <div style={{ padding: '4px 10px 10px' }}>

        <div style={{ fontSize: '0.8rem', fontWeight: 600, color: r.color !== '#9a9a8a' ? r.color : sc.textHead, marginBottom: 2, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div>

        <div style={{ fontSize: '0.65rem', color: sc.textMuted, marginBottom: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.shortDesc}</div>

        {(item.buffs?.length > 0 || item.debuffs?.length > 0) && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 5 }}>

          {(item.buffs || []).map((b, i) => <span key={i} style={{ fontSize: '0.55rem', padding: '1px 5px', background: sc.buffBg, border: `1px solid ${sc.buffBorder}40`, borderRadius: 6, color: sc.buffText }}>{b}</span>)}

          {(item.debuffs || []).map((d, i) => <span key={i} style={{ fontSize: '0.55rem', padding: '1px 5px', background: sc.debuffBg, border: `1px solid ${sc.debuffBorder}40`, borderRadius: 6, color: sc.debuffText }}>{d}</span>)}

        </div>}

        {isPlayer && !isClaimed && isVisible && <button className="lb" onClick={e => { e.stopPropagation(); myClaimExists ? onUnclaim?.() : onClaim?.(); }} style={{ width: '100%', padding: '5px 0', background: myClaimExists ? sc.warningBg : `${sc.green}08`, border: `1px solid ${myClaimExists ? sc.warning + '50' : sc.green + '40'}`, borderRadius: 6, fontSize: '0.72rem', fontWeight: 600, color: myClaimExists ? sc.warning : sc.green }}>{myClaimExists ? '★ Withdraw' : '🤚 Claim'}</button>}

      </div>

      {hovered && item.longDesc && <div style={{ position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: 6, width: 230, background: '#111118', border: `1px solid ${r.border}`, borderRadius: 8, padding: 10, boxShadow: `0 8px 32px rgba(0,0,0,0.8), 0 0 12px ${r.glow}`, zIndex: 3000, pointerEvents: 'none', animation: 'lootFadeIn 0.15s ease' }}><div style={{ fontSize: '0.85rem', fontWeight: 600, color: r.color, marginBottom: 2 }}>{item.name}</div><div style={{ fontSize: '0.68rem', color: sc.textMuted, marginBottom: 4 }}>{item.shortDesc}</div><div style={{ fontSize: '0.68rem', color: sc.textSub, lineHeight: 1.5, borderTop: `1px solid ${sc.border}`, paddingTop: 4 }}>{item.longDesc}</div></div>}

    </div>

  );

};



const LootContestPanel = ({ itemId, dropId, itemOverride = null, playerClaims, allItems, sc, players, onResolve, isDM, currentPlayerId, onLogRoll }) => {

  const [rolls, setRolls] = useState({});

  const [winner, setWinner] = useState(null);

  const item = itemOverride || allItems[itemId];

  const r = item ? getLootRarity(item.rarity) : null;

  const allRolled = item ? playerClaims.every(pid => rolls[pid] !== undefined) : false;

  useEffect(() => { if (!allRolled || winner) return; const maxRoll = Math.max(...Object.values(rolls)); const winners = Object.entries(rolls).filter(([, v]) => v === maxRoll); setWinner(winners[0][0]); }, [allRolled, rolls, winner]);

  if (!item) return null;

  const doMyRoll = () => { const roll = Math.floor(Math.random() * 20) + 1; setRolls(p => ({ ...p, [currentPlayerId]: roll })); onLogRoll?.(dropId || itemId, currentPlayerId, roll); };

  const doRollFor = (pid) => { const roll = Math.floor(Math.random() * 20) + 1; setRolls(p => ({ ...p, [pid]: roll })); onLogRoll?.(dropId || itemId, pid, roll); };

  return (

    <div style={{ background: sc.bgCard, border: `2px solid ${sc.warning}40`, borderRadius: 10, padding: 14, animation: 'contestGlow 2s ease-in-out infinite' }}>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>

        <span style={{ fontSize: '1.1rem' }}>⚔️</span>

        <div style={{ flex: 1 }}><div style={{ fontSize: '0.78rem', fontWeight: 700, color: sc.warning }}>Contested</div><div style={{ fontSize: '0.72rem', color: r.color, fontWeight: 600 }}>{item.iconRef && getIconByKey(item.iconRef) ? <><RpgIcon iconKey={item.iconRef} size={16} /> </> : getLootCatIcon(item.category)} {item.name}</div></div>

        {!isDM && !rolls[currentPlayerId] && playerClaims.includes(currentPlayerId) && <button className="lb" onClick={doMyRoll} style={{ padding: '6px 14px', background: `${sc.accent}20`, border: `2px solid ${sc.accent}60`, borderRadius: 8, color: sc.accentLight, fontSize: '0.82rem', fontWeight: 700, boxShadow: `0 0 15px ${sc.accentGlow}` }}>🎲 Roll d20!</button>}

      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>

        {playerClaims.map(pid => { const player = players.find(p => p.id === pid); const roll = rolls[pid]; const isWinner = winner === pid; const isMe = pid === currentPlayerId; return (

          <div key={pid} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px', background: isWinner ? sc.greenBg : 'rgba(255,255,255,0.02)', border: `1px solid ${isWinner ? sc.green + '60' : sc.border}`, borderRadius: 8 }}>

            <div style={{ width: 8, height: 8, borderRadius: '50%', background: player?.color || sc.textMuted, boxShadow: `0 0 6px ${player?.color || sc.textMuted}60` }} />

            <span style={{ fontSize: '0.82rem', color: sc.text, fontWeight: 600, flex: 1 }}>{player?.name}{isMe && !isDM ? ' (you)' : ''}</span>

            {roll !== undefined ? (<div style={{ display: 'flex', alignItems: 'center', gap: 5, animation: 'rollResult 0.4s ease' }}><span style={{ fontSize: '0.85rem' }}>🎲</span><span style={{ fontSize: '1.1rem', fontWeight: 900, color: isWinner ? sc.green : sc.textSub }}>{roll}</span>{isWinner && <span style={{ fontSize: '0.65rem', color: sc.green, fontWeight: 700 }}>WINNER</span>}</div>)

            : (<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ fontSize: '0.65rem', color: sc.textMuted }}>Waiting...</span>{isDM && <button className="lb" onClick={() => doRollFor(pid)} style={{ padding: '2px 8px', fontSize: '0.62rem', background: `${sc.accent}12`, border: `1px solid ${sc.accent}30`, borderRadius: 4, color: sc.accent }}>Roll for</button>}</div>)}

          </div>

        ); })}

      </div>

      {isDM && winner && <button className="lb" onClick={() => onResolve(dropId || itemId, winner)} style={{ width: '100%', marginTop: 10, padding: '7px 0', background: `${sc.green}18`, border: `2px solid ${sc.green}50`, borderRadius: 8, color: sc.green, fontSize: '0.82rem', fontWeight: 700 }}>✓ Award to {players.find(p => p.id === winner)?.name}</button>}

    </div>

  );

};



export const LootPage = ({
  chars,
  currentCharId,
  isDM,
  lootData,
  onSaveLootData,
  onUpdateChar,
  onBack,
  appTheme,
  globalSettings,
  shopTemplates,
  isServerOwnedLootRuntime = false,
  onStartLootEncounter = null,
  onClaimLootDrop = null,
  onWithdrawLootClaim = null,
  onSetLootDropVisibility = null,
  onRevealAllLootDrops = null,
  onFinishLootEncounter = null,
  onDistributeLootXp = null,
  onAwardLootDrop = null,
  onAwardCurrencyLoot = null,
}) => {

  useEffect(injectLootStyles, []);

  const sc = deriveLootColors(appTheme);

  

  const enemies = lootData?.enemies || [];

  const lootItems = lootData?.items || {};

  // Raw source data stays separate from the canonical read-side display catalog.
  const allItemSources = useMemo(() => {

    const merged = {};

    // Shop templates first (lower priority)

    Object.values(shopTemplates || {}).forEach(item => { merged[item.id] = { ...item, _source: 'shop' }; });

    // Loot items override (higher priority)

    Object.values(lootItems).forEach(item => { merged[item.id] = { ...item, _source: 'loot' }; });

    return merged;

  }, [lootItems, shopTemplates]);

  const allItems = useMemo(
    () => buildUnifiedItemCatalogDisplayMap({ lootItems, shopTemplates }),
    [lootItems, shopTemplates],
  );

  const players = Object.values(chars).filter(c => !c.isNPC && c.id !== 'dm').map(c => ({ id: c.id, name: c.name, class: c.charClass || 'Adventurer', level: c.level || 1, color: ['#00d9a0', '#70a1ff', '#ffa502', '#ff6b81', '#e056fd', '#7bed9f'][Object.keys(chars).indexOf(c.id) % 6] }));

  const lootCurrencyTiers = (
    Array.isArray(globalSettings?.currencyTiers) && globalSettings.currencyTiers.length > 0
      ? globalSettings.currencyTiers
      : DEFAULT_SETTINGS.currencyTiers
  );

  const defaultLootCurrencyId = lootCurrencyTiers.some((tier) => tier.id === 'gold')
    ? 'gold'
    : (lootCurrencyTiers[lootCurrencyTiers.length - 1]?.id || 'gold');



  const [selectedEnemyId, setSelectedEnemyId] = useState(null);

  const [activeEncounter, setActiveEncounter] = useState(lootData?.activeEncounter || null);

  // 2026-05-11 — Session 9: batch-reveal stagger flag. Drops fade in
  // one-at-a-time (~80 ms apart) ONLY when the DM batch-reveals (e.g.,
  // tap "Reveal All"), preserving the DM's authorial pacing for
  // single-item reveals. Auto-clears after the stagger window.
  // Single-toggle reveals continue to render instantly (delay=0).
  const [batchRevealActive, setBatchRevealActive] = useState(false);
  const batchRevealTimerRef = useRef(null);
  const triggerBatchRevealStagger = (count) => {
    if (!count || count <= 1) return;
    setBatchRevealActive(true);
    if (batchRevealTimerRef.current) clearTimeout(batchRevealTimerRef.current);
    // Hold the stagger window for the full reveal duration + small buffer.
    batchRevealTimerRef.current = setTimeout(() => setBatchRevealActive(false), count * 80 + 600);
  };
  useEffect(() => () => {
    if (batchRevealTimerRef.current) clearTimeout(batchRevealTimerRef.current);
  }, []);

  const [showRollOverlay, setShowRollOverlay] = useState(false);

  const [editingEnemy, setEditingEnemy] = useState(null);

  const [tab, setTab] = useState(activeEncounter ? 'loot' : (isDM ? 'encounters' : 'loot'));

  const [lootLog, setLootLog] = useState(lootData?.log || []);

  const [logMode, setLogMode] = useState('short');

  const [undoItem, setUndoItem] = useState(null);

  const [newItemName, setNewItemName] = useState('');

  const [newItemCat, setNewItemCat] = useState('misc');

  const [newItemRarity, setNewItemRarity] = useState('common');

  const [newItemDesc, setNewItemDesc] = useState('');

  const [newItemLongDesc, setNewItemLongDesc] = useState('');

  const [newItemBuffs, setNewItemBuffs] = useState('');

  const [newItemDebuffs, setNewItemDebuffs] = useState('');

  const [newItemOnUseEffects, setNewItemOnUseEffects] = useState('');

  const [newItemTags, setNewItemTags] = useState('');

  const [newItemNotes, setNewItemNotes] = useState('');

  const [newItemDmNotes, setNewItemDmNotes] = useState('');

  const [newItemBuffsApplyOnUse, setNewItemBuffsApplyOnUse] = useState(false);

  const [newItemTradeOnly, setNewItemTradeOnly] = useState(false);

  const [newItemUsesEnabled, setNewItemUsesEnabled] = useState(false);

  const [newItemUsesMax, setNewItemUsesMax] = useState(1);

  const [newItemUsesResetType, setNewItemUsesResetType] = useState('none');

  const [newItemAbilities, setNewItemAbilities] = useState([]);

  const [newItemCombatBonuses, setNewItemCombatBonuses] = useState([]);

  const [newItemPrice, setNewItemPrice] = useState(0);

  const [newItemCurrencyId, setNewItemCurrencyId] = useState(defaultLootCurrencyId);

  const [newItemCurrencyAmount, setNewItemCurrencyAmount] = useState(0);

  const [showNewItemForm, setShowNewItemForm] = useState(false);

  const [newItemIcon, setNewItemIcon] = useState(null);

  const [editingCatalogItemId, setEditingCatalogItemId] = useState(null);

  const [catalogSearch, setCatalogSearch] = useState('');

  const [catalogFilter, setCatalogFilter] = useState('all'); // 'all', 'loot', 'shop', or a category

  const [catalogAutocomplete, setCatalogAutocomplete] = useState([]);

  const parseLootListDraft = (value) => String(value || '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);

  const serializeLootListDraft = (value) => (
    Array.isArray(value) ? value.filter(Boolean).join(', ') : ''
  );

  const cloneLootValue = (value) => {
    if (Array.isArray(value)) return value.map(cloneLootValue);
    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value).map(([key, innerValue]) => [key, cloneLootValue(innerValue)]),
      );
    }
    return value;
  };

  const normalizeLootResetType = (value) => (
    Object.prototype.hasOwnProperty.call(USES_RESET_TYPES, value) ? value : 'none'
  );

  const normalizeLootCurrencyAmount = (value) => {
    const amount = Math.trunc(Number(value));
    return Number.isFinite(amount) ? Math.max(0, amount) : 0;
  };

  const resolveLootFormCurrencyId = (item = {}) => {
    const candidate = (
      item.currencyId
      || item.currencyTierId
      || item.walletCurrencyId
      || item._raw?.currencyId
      || defaultLootCurrencyId
    );
    return lootCurrencyTiers.some((tier) => tier.id === candidate)
      ? candidate
      : defaultLootCurrencyId;
  };

  const buildLootAbilityDraft = (ability = {}) => {
    if (typeof ability === 'string') {
      return {
        id: generateId(),
        name: ability.trim(),
        description: '',
        _effectsRaw: '',
        uses: null,
      };
    }

    const effects = Array.isArray(ability?.effects)
      ? ability.effects
      : (typeof ability?.effects === 'string' ? parseLootListDraft(ability.effects) : []);

    return {
      id: ability?.id || generateId(),
      name: ability?.name || 'Ability',
      description: ability?.description || ability?.desc || '',
      _effectsRaw: effects.join(', '),
      uses: ability?.uses
        ? {
          current: ability.uses.current ?? ability.uses.cur ?? ability.uses.max ?? 1,
          max: ability.uses.max ?? 1,
          resetType: normalizeLootResetType(ability.uses.resetType || ability.uses.reset),
        }
        : null,
    };
  };

  const buildLootAbilityPayload = (ability = {}) => {
    const name = String(ability.name || '').trim();
    if (!name) return null;
    const maxUses = Math.max(1, Math.trunc(Number(ability.uses?.max) || 1));
    const currentUses = Math.min(
      maxUses,
      Math.max(0, Math.trunc(Number(ability.uses?.current ?? ability.uses?.max) || 0)),
    );
    return {
      id: ability.id || generateId(),
      name,
      description: ability.description || '',
      effects: parseLootListDraft(ability._effectsRaw),
      uses: ability.uses
        ? {
          current: currentUses,
          max: maxUses,
          resetType: normalizeLootResetType(ability.uses.resetType),
        }
        : null,
    };
  };

  const resetLootItemForm = () => {
    setNewItemName('');
    setNewItemCat('misc');
    setNewItemRarity('common');
    setNewItemDesc('');
    setNewItemLongDesc('');
    setNewItemBuffs('');
    setNewItemDebuffs('');
    setNewItemOnUseEffects('');
    setNewItemTags('');
    setNewItemNotes('');
    setNewItemDmNotes('');
    setNewItemBuffsApplyOnUse(false);
    setNewItemTradeOnly(false);
    setNewItemUsesEnabled(false);
    setNewItemUsesMax(1);
    setNewItemUsesResetType('none');
    setNewItemAbilities([]);
    setNewItemCombatBonuses([]);
    setNewItemPrice(0);
    setNewItemCurrencyId(defaultLootCurrencyId);
    setNewItemCurrencyAmount(0);
    setNewItemIcon(null);
    setEditingCatalogItemId(null);
  };

  useEffect(() => {
    if (!lootCurrencyTiers.some((tier) => tier.id === newItemCurrencyId)) {
      setNewItemCurrencyId(defaultLootCurrencyId);
    }
  }, [defaultLootCurrencyId, lootCurrencyTiers, newItemCurrencyId]);

  const updateLootAbilityDraft = (abilityId, updates) => {
    setNewItemAbilities((abilities) => abilities.map((ability) => (
      ability.id === abilityId ? { ...ability, ...updates } : ability
    )));
  };

  const updateLootAbilityUsesDraft = (abilityId, updates) => {
    setNewItemAbilities((abilities) => abilities.map((ability) => (
      ability.id === abilityId
        ? { ...ability, uses: { ...(ability.uses || createUsesTracker(1, 'shortRest')), ...updates } }
        : ability
    )));
  };

  useEffect(() => {
    if (!isServerOwnedLootRuntime) return;
    const nextEncounter = lootData?.activeEncounter || null;
    setActiveEncounter(nextEncounter);
    setLootLog(Array.isArray(lootData?.log) ? lootData.log : []);
    if (nextEncounter) setTab('loot');
    else setTab(isDM ? 'encounters' : 'loot');
  }, [
    isServerOwnedLootRuntime,
    isDM,
    lootData?.activeEncounter,
    lootData?.log,
    lootData?._stateUpdatedAt,
  ]);



  // Populate form from existing item (edit or duplicate)

  const populateFormFromItem = (item, isDuplicate) => {

    setNewItemName(isDuplicate ? `${item.name} (copy)` : item.name);

    setNewItemCat(item.category || 'misc');

    setNewItemRarity(item.rarity || 'common');

    setNewItemDesc(item.shortDesc || '');

    setNewItemLongDesc(item.longDesc || '');

    setNewItemBuffs(serializeLootListDraft(item.buffs));

    setNewItemDebuffs(serializeLootListDraft(item.debuffs));

    setNewItemOnUseEffects(serializeLootListDraft(item.onUseEffects));

    setNewItemTags(serializeLootListDraft(item.tags));

    setNewItemNotes(item.notes || '');

    setNewItemDmNotes(item.dmNotes || '');

    setNewItemBuffsApplyOnUse(item.buffsApplyOnUse === true);

    setNewItemTradeOnly(item.tradeOnly === true);

    setNewItemUsesEnabled(Boolean(item.uses));

    setNewItemUsesMax(Math.max(1, Number(item.uses?.max) || 1));

    setNewItemUsesResetType(normalizeLootResetType(item.uses?.resetType || item.uses?.reset));

    setNewItemAbilities(Array.isArray(item.abilities)
      ? item.abilities.map(buildLootAbilityDraft).filter((ability) => ability.name)
      : []);

    setNewItemCombatBonuses(Array.isArray(item.combatBonuses)
      ? item.combatBonuses.map(cloneLootValue)
      : []);

    setNewItemPrice(item.basePrice || 0);

    setNewItemCurrencyId(resolveLootFormCurrencyId(item));

    setNewItemCurrencyAmount(normalizeLootCurrencyAmount(
      item.currencyAmount
        ?? item.amount
        ?? item.basePrice
        ?? 0,
    ));

    setNewItemIcon(item.iconRef || null);

    setEditingCatalogItemId(isDuplicate ? null : item.id);

    setShowNewItemForm(true);

  };  

  const selectedEnemy = enemies.find(e => e.id === selectedEnemyId);

  const activeDrops = useMemo(() => normalizeLootEncounterDrops(activeEncounter), [activeEncounter]);

  const serverLootAuthorityActive = isServerOwnedLootRuntime && Boolean(activeEncounter?.encounterId);

  const runServerLootCommand = async (label, command, args) => {
    if (!serverLootAuthorityActive || typeof command !== 'function') {
      throw new Error(`${label} authority unavailable`);
    }
    const result = await command(args);
    if (result === null || result === undefined) {
      throw new Error(`${label} authority unavailable`);
    }
    return result;
  };

  const buildLootAuthorityIdempotencyKey = (action, dropId, targetCharId) => [
    action,
    activeEncounter?.encounterId || 'encounter',
    dropId || 'drop',
    targetCharId || currentCharId || 'target',
    Date.now(),
    Math.random().toString(36).slice(2),
  ].join(':');

  const getLootDropItem = (drop) => (
    drop?.snapshot || allItemSources[drop?.definitionId] || allItems[drop?.definitionId]
  );

  const getLootDropDisplayItem = (drop) => (
    drop?.snapshot || allItems[drop?.definitionId] || null
  );

  const addLog = (entry) => { const newLog = [...lootLog, { id: generateId(), ts: Date.now(), ...entry }]; setLootLog(newLog); return newLog; };

  

  const saveLoot = (overrides = {}) => { onSaveLootData({ enemies, items: lootItems, activeEncounter, log: lootLog, ...overrides }); };

  const saveEnemies = (ne) => { onSaveLootData({ ...lootData, enemies: ne }); };

  const saveItems = (ni) => { onSaveLootData({ ...lootData, items: ni }); };

  

  const saveEnemy = (data) => { const ne = enemies.some(e => e.id === data.id) ? enemies.map(e => e.id === data.id ? data : e) : [...enemies, data]; saveEnemies(ne); setEditingEnemy(null); };

  const startEncounter = (enemy) => { setSelectedEnemyId(enemy.id); setShowRollOverlay(true); };



  const onRollComplete = async (selectedDrops, qtys) => {

    const enc = createLootEncounterRuntime({
      enemyId: selectedEnemyId,
      selectedDrops,
      qtys,
      itemSources: allItemSources,
      encounterIdFactory: generateId,
    });

    const enemy = enemies.find(e => e.id === selectedEnemyId);

    if (isServerOwnedLootRuntime && typeof onStartLootEncounter === 'function') {
      try {
        const publishResult = await onStartLootEncounter({
          encounterId: enc.encounterId,
          enemyId: enc.enemyId,
          enemyName: enemy?.name,
          xpTotal: Number(enemy?.lootTable?.xp || 0),
          drops: normalizeLootEncounterDrops(enc).map((drop, index) => ({
            dropId: drop.dropId,
            definitionId: drop.definitionId,
            quantity: drop.quantity || 1,
            visibleToPlayers: drop.visibleToPlayers === true,
            snapshot: drop.snapshot || allItemSources[drop.definitionId] || allItems[drop.definitionId] || null,
            lootTableRowIndex: drop.lootTableRowIndex ?? index,
          })),
        });
        setActiveEncounter(reconcileLootEncounterDropIdsFromPublishResult(enc, publishResult));
        setShowRollOverlay(false);
        setTab('loot');
      } catch (error) {
        console.error('[loot] authoritative encounter publish failed', error);
      }
      return;
    }

    setActiveEncounter(enc); setShowRollOverlay(false); setTab('loot');

    const newLog = addLog({ type: 'encounter_start', enemyName: enemy?.name, itemCount: enc.drops.length });

    onSaveLootData({ ...lootData, activeEncounter: enc, log: newLog });

  };



  const toggleVis = async (dropId) => {
    const drop = activeDrops.find(candidate => candidate.dropId === dropId);
    if (serverLootAuthorityActive) {
      if (!drop) return;
      try {
        await runServerLootCommand('set loot drop visibility', onSetLootDropVisibility, {
          encounterId: activeEncounter.encounterId,
          dropId,
          visible: drop.visibleToPlayers !== true,
          expectedDropVersion: drop.version,
        });
      } catch (error) {
        console.error('[loot] authoritative visibility update failed', error);
      }
      return;
    }
    const result = setLootDropVisibilityInEncounter(activeEncounter, { dropId }); if (!result) return; const enc = result.encounter; setActiveEncounter(enc); onSaveLootData({ ...lootData, activeEncounter: enc });
  };

  const revealAll = async () => {
    // 2026-05-11 — Session 9: trigger the batch-reveal stagger. Count
    // the *currently-unrevealed* drops so we stagger only the newly-
    // appearing cards, not already-visible ones. Single-item reveals
    // (count <= 1) are passed through unstaggerered.
    const hiddenCount = (activeEncounter?.drops || []).filter((d) => !d.visibleToPlayers).length;
    triggerBatchRevealStagger(hiddenCount);
    if (serverLootAuthorityActive) {
      try {
        await runServerLootCommand('reveal all loot drops', onRevealAllLootDrops, {
          encounterId: activeEncounter.encounterId,
          expectedEncounterVersion: activeEncounter.version,
        });
      } catch (error) {
        console.error('[loot] authoritative reveal-all failed', error);
      }
      return;
    }
    const result = revealAllLootDropsInEncounter(activeEncounter); if (!result) return; const enc = result.encounter; setActiveEncounter(enc); onSaveLootData({ ...lootData, activeEncounter: enc });
  };



  const claimItem = async (dropId) => {

    if (serverLootAuthorityActive) {
      const drop = activeDrops.find(candidate => candidate.dropId === dropId);
      if (!drop) return;
      try {
        await runServerLootCommand('claim loot drop', onClaimLootDrop, {
          encounterId: activeEncounter.encounterId,
          dropId,
          expectedDropVersion: drop.version,
        });
      } catch (error) {
        console.error('[loot] authoritative claim failed', error);
      }
      return;
    }

    const result = claimLootDropInEncounter(activeEncounter, { dropId, playerId: currentCharId }); if (!result) return;

    const enc = result.encounter; setActiveEncounter(enc); onSaveLootData({ ...lootData, activeEncounter: enc });

    if (result.changed) addLog({ type: 'claim', playerName: chars[currentCharId]?.name, itemName: getLootDropItem(result.drop)?.name, playerId: currentCharId, dropId });

  };

  const unclaimItem = async (dropId) => {

    if (serverLootAuthorityActive) {
      const drop = activeDrops.find(candidate => candidate.dropId === dropId);
      if (!drop) return;
      try {
        await runServerLootCommand('withdraw loot claim', onWithdrawLootClaim, {
          encounterId: activeEncounter.encounterId,
          dropId,
          expectedDropVersion: drop.version,
        });
      } catch (error) {
        console.error('[loot] authoritative claim withdrawal failed', error);
      }
      return;
    }

    const result = withdrawLootClaimInEncounter(activeEncounter, { dropId, playerId: currentCharId }); if (!result) return;

    const enc = result.encounter; setActiveEncounter(enc); onSaveLootData({ ...lootData, activeEncounter: enc });

  };

  

  const awardItem = (itemId, playerId) => {

    const enc = { ...activeEncounter, awardedItems: { ...activeEncounter.awardedItems, [itemId]: playerId } };

    setActiveEncounter(enc); onSaveLootData({ ...lootData, activeEncounter: enc });

    const item = allItemSources[itemId]; const playerName = chars[playerId]?.name;

    addLog({ type: 'award', playerName, itemName: item?.name, playerId });

    if (item && chars[playerId]) {

      const qty = activeEncounter.revealedQtys?.[itemId] || 1;

      if (item.category === 'currency') {

        const goldAmount = item.basePrice || 0;

        onUpdateChar(playerId, { wallet: { ...(chars[playerId].wallet || {}), gold: ((chars[playerId].wallet || {}).gold || 0) + goldAmount } }, { meta: { summary: `🎲 Looted ${item.name} (${goldAmount}gp)`, category: 'loot' } });

      } else {

        const newInvItem = materializeLegacyInventoryItemFromLootCatalogItem(item, {
          quantity: qty,
          entryIdFactory: generateId,
          acquiredAt: new Date().toISOString(),
          lootSource: activeEncounter.enemyId,
        });

        onUpdateChar(playerId, { inventory: stackLegacyInventoryItem(chars[playerId].inventory || [], newInvItem) }, { meta: { summary: `🎲 Looted ${item.name}${qty > 1 ? ` ×${qty}` : ''} from encounter`, category: 'loot' } });

      }

    }

  };



  const awardDrop = async (dropId, playerId) => {
    if (!chars[playerId]) return;

    if (serverLootAuthorityActive) {
      const drop = activeDrops.find(candidate => candidate.dropId === dropId);
      const item = getLootDropItem(drop);
      if (!drop || !item) return;
      const isCurrencyDrop = item.category === 'currency';
      const command = isCurrencyDrop ? onAwardCurrencyLoot : onAwardLootDrop;
      try {
        await runServerLootCommand(isCurrencyDrop ? 'award currency loot' : 'award loot drop', command, {
          encounterId: activeEncounter.encounterId,
          dropId,
          targetCharId: playerId,
          expectedDropVersion: drop.version,
          idempotencyKey: buildLootAuthorityIdempotencyKey(isCurrencyDrop ? 'award-currency' : 'award-item', dropId, playerId),
        });
      } catch (error) {
        console.error('[loot] authoritative award failed', error);
      }
      return;
    }

    const settlement = applyLootDropAwardToChar(chars[playerId], activeEncounter, allItemSources, {
      dropId,
      currencyTiers: globalSettings?.currencyTiers || DEFAULT_SETTINGS.currencyTiers,
      entryIdFactory: generateId,
      acquiredAt: new Date().toISOString(),
    });
    if (!settlement) return;

    const awardResult = awardLootDropInEncounter(activeEncounter, { dropId, playerId });
    if (!awardResult || !awardResult.changed) return;

    const enc = awardResult.encounter;
    setActiveEncounter(enc); onSaveLootData({ ...lootData, activeEncounter: enc });

    const item = settlement.item;
    const playerName = chars[playerId]?.name;
    addLog({ type: 'award', playerName, itemName: item?.name, playerId, dropId });

    if (settlement.currencyAward) {
      const award = settlement.currencyAward;
      onUpdateChar(playerId, { wallet: settlement.nextCharData.wallet }, { meta: { summary: `Looted ${item.name} (${award.amount * award.quantity} ${award.currencyId})`, category: 'loot' } });
      return;
    }

    const qty = settlement.drop.quantity || 1;
    onUpdateChar(playerId, { inventory: settlement.nextCharData.inventory }, { meta: { summary: `Looted ${item.name}${qty > 1 ? ` x${qty}` : ''} from encounter`, category: 'loot' } });
  };

  const logContestRoll = (dropId, playerId, roll) => { const drop = activeDrops.find(candidate => candidate.dropId === dropId); addLog({ type: 'roll', playerName: players.find(p => p.id === playerId)?.name || chars[playerId]?.name, itemName: getLootDropItem(drop)?.name, playerId, roll, dropId }); };



  const distributeXP = () => {

    if (serverLootAuthorityActive) {
      runServerLootCommand('distribute loot XP', onDistributeLootXp, {
        encounterId: activeEncounter.encounterId,
        expectedEncounterVersion: activeEncounter.version,
      }).catch((error) => {
        console.error('[loot] authoritative XP distribution failed', error);
      });
      return;
    }

    const enemy = enemies.find(e => e.id === activeEncounter?.enemyId); if (!enemy) return;

    const xpTotal = enemy.lootTable.xp || 0; const perPlayer = Math.floor(xpTotal / Math.max(1, players.length));

    const enc = { ...activeEncounter, xpDistributed: true }; setActiveEncounter(enc); onSaveLootData({ ...lootData, activeEncounter: enc });

    players.forEach(p => {

      const char = chars[p.id]; if (!char) return;

      const oldXp = char.xp || 0; const newXp = oldXp + perPlayer;

      const xpThresholds = globalSettings?.xpThresholds || XP_DEFAULTS;

      let newLevel = char.level || 1;

      while (newLevel < 20 && xpThresholds[newLevel + 1] && newXp >= xpThresholds[newLevel + 1]) newLevel++;

      const updates = { xp: newXp, xpLog: { ...(char.xpLog || {}), [Date.now()]: { amount: perPlayer, source: `${enemy.name} (Loot encounter)`, total: newXp } } };

      if (newLevel > (char.level || 1)) { updates.level = newLevel; onUpdateChar(p.id, updates, { meta: { summary: `⭐ +${perPlayer} XP from ${enemy.name} — LEVEL UP to ${newLevel}!`, category: 'xp' } }); }

      else onUpdateChar(p.id, updates, { meta: { summary: `⭐ +${perPlayer} XP from ${enemy.name} (${newXp} total)`, category: 'xp' } });

    });

    addLog({ type: 'xp', amount: xpTotal, perPlayer, enemyName: enemy.name });

  };



  const finishEncounter = async () => {
    if (serverLootAuthorityActive) {
      try {
        await runServerLootCommand('finish loot encounter', onFinishLootEncounter, {
          encounterId: activeEncounter.encounterId,
          expectedEncounterVersion: activeEncounter.version,
        });
      } catch (error) {
        console.error('[loot] authoritative finish encounter failed', error);
        return;
      }
      setActiveEncounter(null);
      setSelectedEnemyId(null);
      setTab(isDM ? 'encounters' : 'loot');
      return;
    }
    addLog({ type: 'encounter_end', enemyName: enemies.find(e => e.id === activeEncounter?.enemyId)?.name }); setActiveEncounter(null); setSelectedEnemyId(null); setTab(isDM ? 'encounters' : 'loot'); onSaveLootData({ ...lootData, activeEncounter: null });
  };



  const contestedItems = useMemo(() => activeDrops.filter(drop => drop.playerClaims.length > 1 && !drop.awardedTo).map(drop => ({ dropId: drop.dropId, itemId: drop.definitionId, item: getLootDropDisplayItem(drop), pids: drop.playerClaims })), [activeDrops, allItems]);

  const singleClaimItems = useMemo(() => activeDrops.filter(drop => drop.playerClaims.length === 1 && !drop.awardedTo).map(drop => ({ dropId: drop.dropId, itemId: drop.definitionId, item: getLootDropDisplayItem(drop), playerId: drop.playerClaims[0] })), [activeDrops, allItems]);

  const unclaimedItems = useMemo(() => activeDrops.filter(drop => !drop.awardedTo && drop.playerClaims.length === 0).map(drop => ({ ...drop, item: getLootDropDisplayItem(drop) })), [activeDrops, allItems]);

  const revealedCount = activeDrops.filter(drop => drop.visibleToPlayers).length;



  const handleUndo = () => { if (!undoItem) return; const ne = JSON.parse(JSON.stringify(enemies)); const e = ne.find(x => x.id === undoItem.enemyId); if (e) { e.lootTable.items.splice(Math.min(undoItem.index, e.lootTable.items.length), 0, undoItem.lootItem); } saveEnemies(ne); setUndoItem(null); };



  return (

    <div className="loot-sys" style={{ minHeight: '100vh', background: sc.bg, fontFamily: '"Palatino Linotype", "Book Antiqua", Palatino, serif', color: sc.text }}>

      {showRollOverlay && selectedEnemy && <LootRollOverlay enemy={selectedEnemy} allItems={allItems} sc={sc} onComplete={onRollComplete} onCancel={() => setShowRollOverlay(false)} />}

      {editingEnemy !== null && <LootEnemyEditorModal enemy={editingEnemy === 'new' ? null : editingEnemy} allItems={allItems} sc={sc} onSave={saveEnemy} onCancel={() => setEditingEnemy(null)} />}

      {undoItem && <LootUndoToast item={undoItem.item} sc={sc} onUndo={handleUndo} onDismiss={() => setUndoItem(null)} />}



      {/* Header */}

      <div style={{ padding: '12px 20px', borderBottom: `2px solid ${sc.borderMed}`, background: sc.bgCard, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>

          <button className="lb" onClick={onBack} style={{ padding: '4px 12px', background: 'none', border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.textSub, fontSize: '0.78rem' }}>← Back</button>

          <TreasureChestIcon size={22} color={sc.accentLight} />

          <span style={{ fontSize: '1.05rem', fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', color: sc.accentLight }}>Loot</span>

          {activeEncounter && <span style={{ fontSize: '0.65rem', padding: '2px 8px', background: sc.greenBg, border: `1px solid ${sc.green}40`, borderRadius: 8, color: sc.green, fontWeight: 600 }}>● Active</span>}

        </div>

        {isDM ? <span style={{ padding: '3px 10px', background: 'rgba(212,170,64,0.12)', border: '1px solid rgba(212,170,64,0.3)', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700, color: '#d4aa40' }}>👑 DM</span>

          : <span style={{ padding: '3px 10px', background: sc.greenBg, border: `1px solid ${sc.green}40`, borderRadius: 6, fontSize: '0.72rem', fontWeight: 700, color: sc.green }}>🧙 {chars[currentCharId]?.name}</span>}

      </div>



      {/* Tabs */}

      <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${sc.border}`, background: sc.bgCard }}>

        {[...(isDM ? [{ id: 'encounters', label: 'Encounters', icon: '💀' }] : []), { id: 'loot', label: 'Active Loot', icon: <TreasureChestIcon size={14} color="currentColor" />, badge: contestedItems.length }, ...(isDM ? [{ id: 'items', label: 'Item Catalog', icon: '📦' }] : []), { id: 'log', label: 'Log', icon: '📜', badge: lootLog.filter(l => l.type === 'award').length }].map(t => (

          <button key={t.id} className="lb" onClick={() => setTab(t.id)} style={{ padding: '10px 18px', background: 'none', borderBottom: tab === t.id ? `2px solid ${sc.accent}` : '2px solid transparent', color: tab === t.id ? sc.accentLight : sc.textMuted, fontSize: '0.8rem', fontWeight: tab === t.id ? 700 : 500, display: 'flex', alignItems: 'center', gap: 5, borderRadius: 0 }}>

            <span>{t.icon}</span>{t.label}

            {t.badge > 0 && <span style={{ fontSize: '0.58rem', padding: '1px 5px', borderRadius: 8, background: t.id === 'loot' ? sc.warningBg : sc.accent + '20', color: t.id === 'loot' ? sc.warning : sc.accent, fontWeight: 700 }}>{t.badge}</span>}

          </button>

        ))}

      </div>



      <div style={{ padding: 20, maxWidth: 1100, margin: '0 auto' }}>

        {/* ENCOUNTERS — DM only */}

        {tab === 'encounters' && isDM && <div style={{ animation: 'lootFadeIn 0.3s ease' }}>

          <LootSectionLabel icon="💀" sc={sc} count={enemies.length} right={isDM && <button className="lb" onClick={() => setEditingEnemy('new')} style={{ padding: '5px 14px', fontSize: '0.75rem', fontWeight: 600, background: `${sc.accent}12`, border: `1px solid ${sc.accent}50`, borderRadius: 8, color: sc.accentLight }}>+ New Enemy</button>}>Enemies & Loot Tables</LootSectionLabel>

          {enemies.length === 0 && <div style={{ textAlign: 'center', padding: '40px 0', color: sc.textMuted }}>No enemies yet. {isDM ? 'Create one to set up loot tables.' : 'Waiting for DM...'}</div>}

          <div style={{ display: 'grid', gap: 10 }}>

            {enemies.map((enemy, i) => {

              const isExp = selectedEnemyId === enemy.id;

              const rndC = enemy.lootTable.items.filter(x => !x.guaranteed).length;

              return (<div key={enemy.id} style={{ animation: `lootFadeIn 0.3s ease ${i * 60}ms both` }}>

                <div className="lc" onClick={() => setSelectedEnemyId(isExp ? null : enemy.id)} style={{ background: isExp ? sc.bgCardHover : sc.bgCard, border: `2px solid ${isExp ? sc.accent + '60' : sc.border}`, borderRadius: isExp ? '10px 10px 0 0' : 10, padding: '12px 16px', cursor: 'pointer', boxShadow: isExp ? `0 0 20px ${sc.accentGlow}` : '0 4px 16px rgba(0,0,0,0.3)' }}>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>

                    <span style={{ fontSize: '1rem' }}>💀</span>

                    <div style={{ flex: 1 }}><div style={{ fontSize: '0.95rem', fontWeight: 700, color: sc.textHead }}>{enemy.name}</div><div style={{ fontSize: '0.65rem', color: sc.textMuted }}>{enemy.type} · CR {enemy.cr}</div></div>

                    <div style={{ textAlign: 'right', fontSize: '0.72rem' }}><span style={{ color: sc.hpHigh }}>HP {enemy.hp?.max}</span> · <span style={{ color: sc.blue }}>AC {enemy.ac}</span></div>

                  </div>

                  {enemy.notes && <div style={{ fontSize: '0.7rem', color: sc.textMuted, fontStyle: 'italic', marginBottom: 6 }}>{enemy.notes}</div>}

                  {(enemy.mechanics || []).map((m, j) => <div key={j} style={{ fontSize: '0.66rem', color: sc.textSub, padding: '3px 8px', marginBottom: 2, background: sc.blueBg, border: `1px solid ${sc.blue}18`, borderRadius: 5 }}><span style={{ color: sc.blue, fontWeight: 600 }}>⚙</span> {m}</div>)}

                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: '0.68rem', marginTop: 4 }}><span style={{ color: sc.gold, display: 'inline-flex', alignItems: 'center', gap: 3 }}><TreasureChestIcon size={13} color={sc.gold} /> {enemy.lootTable.items.length} items</span><span style={{ color: sc.green }}>{enemy.lootTable.items.filter(x => x.guaranteed).length} guaranteed</span><span style={{ color: sc.accent }}>+{enemy.lootTable.xp} XP</span></div>

                </div>

                {isExp && <div style={{ padding: 14, background: sc.bgCard, border: `2px solid ${sc.borderMed}`, borderTop: 'none', borderRadius: '0 0 10px 10px', animation: 'lootFadeIn 0.2s ease' }}>

                  <LootSectionLabel icon={<TreasureChestIcon size={15} color={sc.accentLight} />} sc={sc} count={enemy.lootTable.items.length} right={<div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.7rem' }}><span style={{ color: sc.accent }}>XP: {enemy.lootTable.xp}</span>{(enemy.lootTable.dropMode || 'selectN') === 'selectN' && rndC > 0 && <>{isDM ? <><span style={{ color: sc.textMuted }}>| Roll</span><LootTinyInput type="number" value={enemy.lootTable.rollCount} onChange={e => { const ne = JSON.parse(JSON.stringify(enemies)); ne.find(x => x.id === enemy.id).lootTable.rollCount = Math.max(1, Math.min(rndC, parseInt(e.target.value) || 1)); saveEnemies(ne); }} sc={sc} width={30} style={{ fontSize: '0.68rem', padding: '1px 3px' }} /></> : <span style={{ color: sc.textMuted }}>|</span>}<span style={{ color: sc.warning }}>of {rndC} random</span></>}</div>}>Loot Table</LootSectionLabel>

                  {isDM && rndC > 0 && <div style={{ marginBottom: 8 }}><LootDropModeSelector mode={enemy.lootTable.dropMode || 'selectN'} onChange={m => { const ne = JSON.parse(JSON.stringify(enemies)); ne.find(x => x.id === enemy.id).lootTable.dropMode = m; saveEnemies(ne); }} sc={sc} /></div>}

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>

                    {enemy.lootTable.items.map((li, idx) => <LootPill key={idx} li={li} item={allItems[li.itemId]} sc={sc} interactive={isDM} dropMode={enemy.lootTable.dropMode || 'selectN'}

                      onToggleGuaranteed={() => { const ne = JSON.parse(JSON.stringify(enemies)); const e = ne.find(x => x.id === enemy.id); e.lootTable.items[idx].guaranteed = !e.lootTable.items[idx].guaranteed; if (e.lootTable.items[idx].guaranteed) e.lootTable.items[idx].dropChance = 100; saveEnemies(ne); }}

                      onChangeQty={v => { const ne = JSON.parse(JSON.stringify(enemies)); ne.find(x => x.id === enemy.id).lootTable.items[idx].qty = v; saveEnemies(ne); }}

                      onChangeChance={v => { const ne = JSON.parse(JSON.stringify(enemies)); ne.find(x => x.id === enemy.id).lootTable.items[idx].dropChance = v; saveEnemies(ne); }}

                      onRemove={() => { const removedLi = JSON.parse(JSON.stringify(li)); setUndoItem({ enemyId: enemy.id, lootItem: removedLi, index: idx, item: allItems[li.itemId] }); const ne = JSON.parse(JSON.stringify(enemies)); ne.find(x => x.id === enemy.id).lootTable.items.splice(idx, 1); saveEnemies(ne); }}

                    />)}

                  </div>

                  {isDM && <div style={{ display: 'flex', gap: 8 }}>

                    <button className="lb" onClick={() => startEncounter(enemy)} disabled={enemy.lootTable.items.length === 0} style={{ padding: '8px 18px', background: `${sc.accent}20`, border: `2px solid ${sc.accent}60`, borderRadius: 8, color: sc.accentLight, fontSize: '0.82rem', fontWeight: 700, boxShadow: `0 0 15px ${sc.accentGlow}` }}>🎲 Roll & Distribute Loot</button>

                    <button className="lb" onClick={() => setEditingEnemy(enemy)} style={{ padding: '8px 14px', background: 'rgba(255,255,255,0.03)', border: `1px solid ${sc.border}`, borderRadius: 8, color: sc.textSub, fontSize: '0.78rem' }}>✏️ Edit</button>

                    <button className="lb" onClick={() => { if (window.confirm(`Delete ${enemy.name}?`)) { saveEnemies(enemies.filter(e => e.id !== enemy.id)); setSelectedEnemyId(null); } }} style={{ padding: '8px 14px', background: sc.redBg, border: `1px solid ${sc.red}40`, borderRadius: 8, color: sc.red, fontSize: '0.78rem' }}>🗑️</button>

                  </div>}

                </div>}

              </div>);

            })}

          </div>

        </div>}



        {/* ACTIVE LOOT */}

        {tab === 'loot' && <div style={{ animation: 'lootFadeIn 0.3s ease' }}>

          {!activeEncounter ? <div style={{ textAlign: 'center', padding: '60px 0', color: sc.textMuted }}><TreasureChestIcon size={48} color={sc.textMuted} /><div style={{ marginTop: 10, fontSize: '0.88rem' }}>No active loot encounter</div><div style={{ fontSize: '0.7rem', marginTop: 4 }}>{isDM ? 'Select an enemy and roll' : 'Waiting for DM...'}</div></div>

          : (() => {

            const enemy = enemies.find(e => e.id === activeEncounter.enemyId); if (!enemy) return null;
            const encounterXpTotal = Number(enemy?.lootTable?.xp ?? activeEncounter?.xpTotal ?? 0) || 0;
            const canDistributeXp = isDM && !activeEncounter.xpDistributed && (!serverLootAuthorityActive || typeof onDistributeLootXp === 'function');

            return (<div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 18, padding: '10px 14px', background: sc.bgCard, border: `2px solid ${sc.borderMed}`, borderRadius: 10 }}>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><span style={{ fontSize: '1rem' }}>💀</span><div><div style={{ fontSize: '0.95rem', fontWeight: 700, color: sc.textHead }}>{enemy.name} — Loot</div><div style={{ fontSize: '0.65rem', color: sc.textMuted }}>{activeDrops.length} items · {revealedCount} visible · {activeDrops.filter(drop => drop.awardedTo).length} awarded</div></div></div>

                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>

                  {isDM && revealedCount < activeDrops.length && <button className="lb" onClick={revealAll} style={{ padding: '5px 12px', fontSize: '0.72rem', fontWeight: 600, background: `${sc.accent}12`, border: `1px solid ${sc.accent}40`, borderRadius: 6, color: sc.accentLight }}>🔓 Reveal All</button>}

                  {canDistributeXp && <button className="lb" onClick={distributeXP} style={{ padding: '5px 12px', fontSize: '0.72rem', fontWeight: 600, background: `${sc.accent}12`, border: `1px solid ${sc.accent}40`, borderRadius: 6, color: sc.accentLight }}>⭐ {encounterXpTotal} XP</button>}

                  {activeEncounter.xpDistributed && <span style={{ fontSize: '0.7rem', color: sc.green, fontWeight: 600, padding: '4px 8px', background: sc.greenBg, border: `1px solid ${sc.green}40`, borderRadius: 6 }}>✓ {Math.floor(encounterXpTotal / Math.max(1, players.length))} XP each</span>}

                  {isDM && <button className="lb" onClick={finishEncounter} style={{ padding: '5px 12px', fontSize: '0.72rem', background: sc.redBg, border: `1px solid ${sc.red}40`, borderRadius: 6, color: sc.red, fontWeight: 600 }}>✕ End</button>}

                </div>

              </div>

              {contestedItems.length > 0 && <div style={{ marginBottom: 18 }}><LootSectionLabel icon="⚔️" sc={sc} count={contestedItems.length}>{isDM ? 'Contested — Players Rolling' : 'Contested — Roll to Win!'}</LootSectionLabel><div style={{ display: 'grid', gap: 8 }}>{contestedItems.map(({ dropId, itemId, item, pids }) => <LootContestPanel key={dropId} dropId={dropId} itemId={itemId} itemOverride={item} playerClaims={pids} allItems={allItems} sc={sc} players={players} onResolve={awardDrop} isDM={isDM} currentPlayerId={currentCharId} onLogRoll={logContestRoll} />)}</div></div>}

              {isDM && singleClaimItems.length > 0 && <div style={{ marginBottom: 18 }}><LootSectionLabel icon="🤚" sc={sc} count={singleClaimItems.length}>Pending Claims</LootSectionLabel><div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>{singleClaimItems.map(({ dropId, item, playerId }) => { const player = players.find(p => p.id === playerId); if (!item || !player) return null; const r = getLootRarity(item.rarity); return (<div key={dropId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px', background: sc.bgCard, border: `1px solid ${sc.border}`, borderRadius: 8 }}><span style={{ fontSize: '0.9rem' }}>{item.iconRef && getIconByKey(item.iconRef) ? <RpgIcon iconKey={item.iconRef} size={20} /> : getLootCatIcon(item.category)}</span><span style={{ fontSize: '0.8rem', color: r.color, fontWeight: 600, flex: 1 }}>{item.name}</span><div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><div style={{ width: 8, height: 8, borderRadius: '50%', background: player.color }} /><span style={{ fontSize: '0.75rem', color: sc.text }}>{player.name}</span></div><button className="lb" onClick={() => awardDrop(dropId, playerId)} style={{ padding: '4px 10px', fontSize: '0.7rem', fontWeight: 600, background: sc.greenBg, border: `1px solid ${sc.green}40`, borderRadius: 6, color: sc.green }}>✓ Award</button></div>); })}</div></div>}

              <LootSectionLabel icon={<TreasureChestIcon size={15} color={sc.accentLight} />} sc={sc} count={activeDrops.length}>{isDM ? 'All Loot (🔒/🔓 to reveal)' : 'Available Loot'}</LootSectionLabel>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(165px, 1fr))', gap: 8 }}>

                {activeDrops.map((drop, i) => <LootItemCard key={drop.dropId} item={getLootDropDisplayItem(drop)} qty={drop.quantity || 1} sc={sc} delay={batchRevealActive ? i * 80 : 0} isVisible={drop.visibleToPlayers} claimedBy={drop.awardedTo || null} contested={contestedItems.some(c => c.dropId === drop.dropId)} playerClaims={drop.playerClaims || []} isPlayer={!isDM} isDM={isDM} currentPlayerId={currentCharId} onClaim={() => claimItem(drop.dropId)} onUnclaim={() => unclaimItem(drop.dropId)} onToggleReveal={() => toggleVis(drop.dropId)} players={players} />)}

              </div>

              {isDM && unclaimedItems.length > 0 && <div style={{ marginTop: 18 }}><LootSectionLabel icon="🎯" sc={sc}>Quick Award</LootSectionLabel><div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>{unclaimedItems.map(drop => { const item = drop.item; if (!item) return null; return (<div key={drop.dropId} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 8px', background: sc.bgCard, border: `1px solid ${sc.border}`, borderRadius: 8 }}><span>{item.iconRef && getIconByKey(item.iconRef) ? <RpgIcon iconKey={item.iconRef} size={18} /> : getLootCatIcon(item.category)}</span><span style={{ fontSize: '0.75rem', color: sc.text, fontWeight: 600 }}>{item.name}</span><span style={{ color: sc.textMuted, fontSize: '0.6rem' }}>→</span>{players.map(p => <button key={p.id} className="lb" onClick={() => awardDrop(drop.dropId, p.id)} title={p.name} style={{ width: 20, height: 20, borderRadius: '50%', background: p.color + '22', border: `2px solid ${p.color}55`, fontSize: '0.5rem', color: p.color, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{p.name[0]}</button>)}</div>); })}</div></div>}

            </div>);

          })()}

        </div>}



        {/* ITEM CATALOG — Universal Library */}

        {tab === 'items' && isDM && (() => {

          const allItemsList = Object.values(allItems);

          const query = catalogSearch.toLowerCase();

          const filtered = allItemsList.filter(item => {

            if (catalogFilter === 'loot' && item._source !== 'loot') return false;

            if (catalogFilter === 'shop' && item._source !== 'shop') return false;

            if (catalogFilter !== 'all' && catalogFilter !== 'loot' && catalogFilter !== 'shop' && item.category !== catalogFilter) return false;

            if (query && !item.name.toLowerCase().includes(query) && !(item.shortDesc || '').toLowerCase().includes(query) && !(item.category || '').toLowerCase().includes(query)) return false;

            return true;

          }).sort((a, b) => {

            const rarOrder = { legendary: 0, epic: 1, rare: 2, uncommon: 3, common: 4 };

            return (rarOrder[a.rarity] || 5) - (rarOrder[b.rarity] || 5) || a.name.localeCompare(b.name);

          });

          return <div style={{ animation: 'lootFadeIn 0.3s ease' }}>

            <LootSectionLabel icon="📦" sc={sc} count={allItemsList.length} right={

              <button className="lb" onClick={() => { resetLootItemForm(); setShowNewItemForm(!showNewItemForm); }} style={{ padding: '5px 14px', fontSize: '0.75rem', fontWeight: 600, background: showNewItemForm ? sc.warningBg : `${sc.accent}12`, border: `1px solid ${showNewItemForm ? sc.warning + '50' : sc.accent + '50'}`, borderRadius: 8, color: showNewItemForm ? sc.warning : sc.accentLight }}>{showNewItemForm ? '✕ Cancel' : '+ New Item'}</button>

            }>Universal Item Library</LootSectionLabel>



            {/* New Item Form */}

            {showNewItemForm && <div style={{ padding: 14, background: sc.bgCard, border: `2px solid ${editingCatalogItemId ? sc.accent + '40' : sc.accent + '40'}`, borderRadius: 10, marginBottom: 16, animation: 'lootFadeIn 0.2s ease' }}>

              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: editingCatalogItemId ? sc.accent : sc.accentLight, marginBottom: 8 }}>{editingCatalogItemId ? '✏️ Edit Item' : '✨ Create New Item'}</div>

              <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>

                  <label style={{ fontSize: '0.6rem', color: sc.textMuted }}>Icon</label>

                  <IconSelectButton value={newItemIcon} onChange={setNewItemIcon} sc={sc} size={36} />

                </div>

                <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>

                  <div><label style={{ fontSize: '0.6rem', color: sc.textMuted }}>Name</label><input className="li" aria-label="Loot item name" value={newItemName} onChange={e => setNewItemName(e.target.value)} placeholder="Item name..." style={{ width: '100%', padding: '6px 10px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.text, fontSize: '0.82rem' }} autoFocus /></div>

                  <div><label style={{ fontSize: '0.6rem', color: sc.textMuted }}>Category</label><select className="li" aria-label="Loot item category" value={newItemCat} onChange={e => setNewItemCat(e.target.value)} style={{ width: '100%', padding: '6px 8px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.text, fontSize: '0.75rem' }}>{Object.entries(LOOT_CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.name}</option>)}</select></div>

                  <div><label style={{ fontSize: '0.6rem', color: sc.textMuted }}>Rarity</label><select className="li" aria-label="Loot item rarity" value={newItemRarity} onChange={e => setNewItemRarity(e.target.value)} style={{ width: '100%', padding: '6px 8px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.text, fontSize: '0.75rem' }}>{Object.entries(LOOT_RARITIES).map(([k, v]) => <option key={k} value={k} style={{ color: v.color }}>{v.name}</option>)}</select></div>

                </div>

              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>

                <div><label style={{ fontSize: '0.6rem', color: sc.textMuted }}>Short Description</label><input className="li" aria-label="Loot item short description" value={newItemDesc} onChange={e => setNewItemDesc(e.target.value)} placeholder="e.g. +1 Longsword, 1d6 fire damage" style={{ width: '100%', padding: '6px 10px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.text, fontSize: '0.78rem' }} /></div>

                <div><label style={{ fontSize: '0.6rem', color: sc.textMuted }}>Base Price (gp)</label><input className="li" aria-label="Loot item base price" type="number" value={newItemPrice} onChange={e => setNewItemPrice(parseInt(e.target.value) || 0)} style={{ width: '100%', padding: '6px 10px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.text, fontSize: '0.78rem' }} /></div>

              </div>

              {newItemCat === 'currency' && <div data-testid="loot-item-currency-fields" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>

                <div><label style={{ fontSize: '0.6rem', color: sc.textMuted }}>Currency Amount</label><input className="li" aria-label="Loot item currency amount" type="number" min="0" value={newItemCurrencyAmount} onChange={e => setNewItemCurrencyAmount(normalizeLootCurrencyAmount(e.target.value))} style={{ width: '100%', padding: '6px 10px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.text, fontSize: '0.78rem' }} /></div>

                <div><label style={{ fontSize: '0.6rem', color: sc.textMuted }}>Currency Type</label><select className="li" aria-label="Loot item currency type" value={newItemCurrencyId} onChange={e => setNewItemCurrencyId(e.target.value || defaultLootCurrencyId)} style={{ width: '100%', padding: '6px 8px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.text, fontSize: '0.75rem' }}>{lootCurrencyTiers.map((tier) => <option key={tier.id} value={tier.id}>{tier.name || tier.id} {tier.symbol ? `(${tier.symbol})` : ''}</option>)}</select></div>

              </div>}

              <div style={{ marginBottom: 8 }}><label style={{ fontSize: '0.6rem', color: sc.textMuted }}>Long Description</label><textarea className="li" aria-label="Loot item long description" value={newItemLongDesc} onChange={e => setNewItemLongDesc(e.target.value)} placeholder="Detailed lore, appearance, mechanics..." rows={2} style={{ width: '100%', padding: '6px 10px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.text, fontSize: '0.78rem', resize: 'vertical' }} /></div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>

                <div><label style={{ fontSize: '0.6rem', color: sc.green }}>Buffs <span style={{ color: sc.textMuted }}>(comma-separated)</span></label><input className="li" aria-label="Loot item buffs" value={newItemBuffs} onChange={e => setNewItemBuffs(e.target.value)} placeholder="e.g. +1 Attack, +1d6 Fire" style={{ width: '100%', padding: '6px 10px', background: sc.bgInput, border: `1px solid ${sc.green}30`, borderRadius: 6, color: sc.text, fontSize: '0.78rem' }} /></div>

                <div><label style={{ fontSize: '0.6rem', color: sc.red }}>Debuffs <span style={{ color: sc.textMuted }}>(comma-separated)</span></label><input className="li" aria-label="Loot item debuffs" value={newItemDebuffs} onChange={e => setNewItemDebuffs(e.target.value)} placeholder="e.g. -1 Persuasion" style={{ width: '100%', padding: '6px 10px', background: sc.bgInput, border: `1px solid ${sc.red}30`, borderRadius: 6, color: sc.text, fontSize: '0.78rem' }} /></div>

              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 8, marginBottom: 10 }}>

                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.68rem', color: sc.textSub }}><input aria-label="Loot item buffs apply on use" type="checkbox" checked={newItemBuffsApplyOnUse} onChange={e => setNewItemBuffsApplyOnUse(e.target.checked)} /> Buffs apply on use</label>

                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.68rem', color: sc.textSub }}><input aria-label="Loot item trade only" type="checkbox" checked={newItemTradeOnly} onChange={e => setNewItemTradeOnly(e.target.checked)} /> Trade only</label>

                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.68rem', color: sc.textSub }}><input aria-label="Enable loot item uses" type="checkbox" checked={newItemUsesEnabled} onChange={e => setNewItemUsesEnabled(e.target.checked)} /> Limited uses</label>

              </div>

              {newItemUsesEnabled && <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>

                <div><label style={{ fontSize: '0.6rem', color: sc.textMuted }}>Uses</label><input className="li" aria-label="Loot item uses max" type="number" min="1" value={newItemUsesMax} onChange={e => setNewItemUsesMax(Math.max(1, parseInt(e.target.value, 10) || 1))} style={{ width: '100%', padding: '6px 10px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.text, fontSize: '0.78rem' }} /></div>

                <div><label style={{ fontSize: '0.6rem', color: sc.textMuted }}>Reset</label><select className="li" aria-label="Loot item uses reset" value={newItemUsesResetType} onChange={e => setNewItemUsesResetType(normalizeLootResetType(e.target.value))} style={{ width: '100%', padding: '6px 8px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.text, fontSize: '0.75rem' }}>{Object.entries(USES_RESET_TYPES).map(([key, rt]) => <option key={key} value={key}>{rt.name}</option>)}</select></div>

              </div>}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>

                <div><label style={{ fontSize: '0.6rem', color: sc.textMuted }}>On-Use Effects <span style={{ color: sc.textMuted }}>(comma-separated)</span></label><input className="li" aria-label="Loot item on-use effects" value={newItemOnUseEffects} onChange={e => setNewItemOnUseEffects(e.target.value)} placeholder="e.g. Gain fire resistance" style={{ width: '100%', padding: '6px 10px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.text, fontSize: '0.78rem' }} /></div>

                <div><label style={{ fontSize: '0.6rem', color: sc.textMuted }}>Tags <span style={{ color: sc.textMuted }}>(comma-separated)</span></label><input className="li" aria-label="Loot item tags" value={newItemTags} onChange={e => setNewItemTags(e.target.value)} placeholder="e.g. alchemy, fire" style={{ width: '100%', padding: '6px 10px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.text, fontSize: '0.78rem' }} /></div>

              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>

                <div><label style={{ fontSize: '0.6rem', color: sc.textMuted }}>Player Notes</label><textarea className="li" aria-label="Loot item notes" value={newItemNotes} onChange={e => setNewItemNotes(e.target.value)} rows={2} style={{ width: '100%', padding: '6px 10px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.text, fontSize: '0.78rem', resize: 'vertical' }} /></div>

                <div><label style={{ fontSize: '0.6rem', color: sc.textMuted }}>DM Notes</label><textarea className="li" aria-label="Loot item DM notes" value={newItemDmNotes} onChange={e => setNewItemDmNotes(e.target.value)} rows={2} style={{ width: '100%', padding: '6px 10px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.text, fontSize: '0.78rem', resize: 'vertical' }} /></div>

              </div>

              <div style={{ display: 'grid', gap: 8, marginBottom: 10 }}>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ fontSize: '0.66rem', color: sc.textMuted, fontWeight: 600 }}>Abilities</div>
                  <button className="lb" aria-label="Add loot ability" onClick={() => setNewItemAbilities(abilities => [...abilities, buildLootAbilityDraft(createAbility('New Ability', '', [], null, 'shortRest'))])} style={{ padding: '3px 8px', background: `${sc.accent}12`, border: `1px solid ${sc.accent}40`, borderRadius: 6, color: sc.accentLight, fontSize: '0.62rem' }}>Add Ability</button>
                </div>

                {newItemAbilities.map((ability, index) => <div key={ability.id} style={{ display: 'grid', gap: 6, padding: 8, background: 'rgba(255,255,255,0.025)', border: `1px solid ${sc.border}`, borderRadius: 8 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 6, alignItems: 'end' }}>
                    <div><label style={{ fontSize: '0.56rem', color: sc.textMuted }}>Name</label><input className="li" aria-label={`Loot ability name ${index + 1}`} value={ability.name} onChange={e => updateLootAbilityDraft(ability.id, { name: e.target.value })} style={{ width: '100%', padding: '5px 8px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.text, fontSize: '0.74rem' }} /></div>
                    <div><label style={{ fontSize: '0.56rem', color: sc.textMuted }}>Description</label><input className="li" aria-label={`Loot ability description ${index + 1}`} value={ability.description} onChange={e => updateLootAbilityDraft(ability.id, { description: e.target.value })} style={{ width: '100%', padding: '5px 8px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.text, fontSize: '0.74rem' }} /></div>
                    <button className="lb" aria-label={`Remove loot ability ${index + 1}`} onClick={() => setNewItemAbilities(abilities => abilities.filter(candidate => candidate.id !== ability.id))} style={{ padding: '5px 8px', background: sc.redBg, border: `1px solid ${sc.red}40`, borderRadius: 6, color: sc.red, fontSize: '0.62rem' }}>Remove</button>
                  </div>
                  <div><label style={{ fontSize: '0.56rem', color: sc.textMuted }}>Effects <span style={{ color: sc.textMuted }}>(comma-separated)</span></label><input className="li" aria-label={`Loot ability effects ${index + 1}`} value={ability._effectsRaw} onChange={e => updateLootAbilityDraft(ability.id, { _effectsRaw: e.target.value })} style={{ width: '100%', padding: '5px 8px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.text, fontSize: '0.74rem' }} /></div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.62rem', color: sc.textSub }}><input aria-label={`Loot ability uses enabled ${index + 1}`} type="checkbox" checked={Boolean(ability.uses)} onChange={e => updateLootAbilityDraft(ability.id, { uses: e.target.checked ? createUsesTracker(1, 'shortRest') : null })} /> Limited uses</label>
                  {ability.uses && <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    <input className="li" aria-label={`Loot ability uses max ${index + 1}`} type="number" min="1" value={ability.uses.max} onChange={e => { const max = Math.max(1, parseInt(e.target.value, 10) || 1); updateLootAbilityUsesDraft(ability.id, { current: max, max }); }} style={{ width: '100%', padding: '5px 8px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.text, fontSize: '0.74rem' }} />
                    <select className="li" aria-label={`Loot ability uses reset ${index + 1}`} value={normalizeLootResetType(ability.uses.resetType)} onChange={e => updateLootAbilityUsesDraft(ability.id, { resetType: normalizeLootResetType(e.target.value) })} style={{ width: '100%', padding: '5px 8px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.text, fontSize: '0.74rem' }}>{Object.entries(USES_RESET_TYPES).map(([key, rt]) => <option key={key} value={key}>{rt.name}</option>)}</select>
                  </div>}
                </div>)}

              </div>

              <div style={{ display: 'grid', gap: 6, marginBottom: 10 }}>
                <div style={{ fontSize: '0.66rem', color: sc.textMuted, fontWeight: 600 }}>Combat Bonuses</div>
                {newItemCombatBonuses.length > 0 && <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>{newItemCombatBonuses.map((bonus, index) => <span key={`${bonus.type}-${bonus.scope}-${bonus.value || 0}-${index}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 6px', background: `${sc.accent}12`, border: `1px solid ${sc.accent}30`, borderRadius: 5, color: sc.accentLight, fontSize: '0.56rem' }}>{bonus.label || bonus.scope || bonus.type}<button className="lb" aria-label={`Remove loot combat bonus ${index + 1}`} onClick={() => setNewItemCombatBonuses(bonuses => bonuses.filter((_, i) => i !== index))} style={{ border: 0, background: 'transparent', color: sc.textMuted, padding: 0, fontSize: '0.62rem' }}>x</button></span>)}</div>}
                <select className="li" aria-label="Add loot combat bonus" value="" onChange={e => { const selected = COMBAT_BONUS_TYPES.find(bonus => bonus.label === e.target.value); if (selected) setNewItemCombatBonuses(bonuses => [...bonuses, cloneLootValue(selected)]); }} style={{ width: '100%', padding: '6px 8px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.text, fontSize: '0.75rem' }}>
                  <option value="">Add combat bonus...</option>
                  {COMBAT_BONUS_TYPES.map((bonus, index) => <option key={`${bonus.type}-${bonus.scope}-${bonus.value || 0}-${index}`} value={bonus.label}>{bonus.label}</option>)}
                </select>
                {hasPartiallySupportedItemCombatBonuses(newItemCombatBonuses) && (
                  <div data-testid="loot-authored-mechanic-warning" role="note" style={{ padding: '7px 8px', borderRadius: 4, background: 'rgba(201,162,39,0.12)', border: '1px solid rgba(201,162,39,0.3)', color: sc.warning || sc.accentLight, fontSize: '0.68rem' }}>
                    {AUTHORED_ITEM_MECHANIC_WARNING_TEXT}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8 }}>

                <button className="lb" disabled={!newItemName.trim()} onClick={() => {

                  const isEdit = !!editingCatalogItemId;

                  const id = isEdit ? editingCatalogItemId : newItemName.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') + '_' + generateId().slice(0, 4);

                  const usesMax = Math.max(1, parseInt(newItemUsesMax, 10) || 1);

                  const itemUses = newItemUsesEnabled ? createUsesTracker(usesMax, normalizeLootResetType(newItemUsesResetType)) : null;

                  const existingLootItem = isEdit && lootItems[id] ? lootItems[id] : null;

                  const existingItemHadCurrencyAmountAlias = Boolean(existingLootItem && (
                    existingLootItem.category === 'currency'
                    || existingLootItem.currencyId
                    || existingLootItem.currencyTierId
                    || existingLootItem.walletCurrencyId
                    || Object.prototype.hasOwnProperty.call(existingLootItem, 'currencyAmount')
                    || Object.prototype.hasOwnProperty.call(existingLootItem, 'amount')
                  ));

                  const currencyId = lootCurrencyTiers.some((tier) => tier.id === newItemCurrencyId)
                    ? newItemCurrencyId
                    : defaultLootCurrencyId;

                  const newItem = {
                    ...(existingLootItem || {}),
                    id,
                    name: newItemName.trim(),
                    category: newItemCat,
                    rarity: newItemRarity,
                    shortDesc: newItemDesc,
                    longDesc: newItemLongDesc,
                    buffs: parseLootListDraft(newItemBuffs),
                    debuffs: parseLootListDraft(newItemDebuffs),
                    onUseEffects: parseLootListDraft(newItemOnUseEffects),
                    buffsApplyOnUse: newItemBuffsApplyOnUse,
                    uses: itemUses,
                    abilities: newItemAbilities.map(buildLootAbilityPayload).filter(Boolean),
                    combatBonuses: newItemCombatBonuses.map(cloneLootValue),
                    tags: parseLootListDraft(newItemTags),
                    notes: newItemNotes,
                    dmNotes: newItemDmNotes,
                    basePrice: newItemPrice,
                    tradeOnly: newItemTradeOnly,
                    ...(newItemCat === 'currency'
                      ? { currencyId, currencyAmount: normalizeLootCurrencyAmount(newItemCurrencyAmount) }
                      : {}),
                    ...(newItemIcon ? { iconRef: newItemIcon } : {}),
                  };

                  if (newItem.category === 'currency') {
                    delete newItem.currencyTierId;
                    delete newItem.walletCurrencyId;
                    delete newItem.amount;
                  } else {
                    delete newItem.currencyId;
                    delete newItem.currencyTierId;
                    delete newItem.walletCurrencyId;
                    delete newItem.currencyAmount;
                    if (existingItemHadCurrencyAmountAlias) delete newItem.amount;
                  }

                  saveItems({ ...lootItems, [id]: newItem });

                  resetLootItemForm(); setShowNewItemForm(false);

                }} style={{ padding: '8px 20px', background: `${sc.green}18`, border: `2px solid ${sc.green}50`, borderRadius: 8, color: sc.green, fontSize: '0.82rem', fontWeight: 700 }}>{editingCatalogItemId ? '💾 Save Changes' : '💾 Add to Library'}</button>

                <button className="lb" onClick={() => { resetLootItemForm(); setShowNewItemForm(false); }} style={{ padding: '8px 14px', background: 'rgba(255,255,255,0.03)', border: `1px solid ${sc.border}`, borderRadius: 8, color: sc.textMuted, fontSize: '0.78rem' }}>Cancel</button>

              </div>

            </div>}



            {/* Search + Filter Bar */}

            <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>

              <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>

                <input className="li" value={catalogSearch} onChange={e => {

                  setCatalogSearch(e.target.value);

                  const q = e.target.value.toLowerCase();

                  if (q.length >= 1) {

                    setCatalogAutocomplete(allItemsList.filter(i => i.name.toLowerCase().includes(q)).slice(0, 8));

                  } else setCatalogAutocomplete([]);

                }} placeholder="🔍 Search items..." style={{ width: '100%', padding: '6px 10px', background: sc.bgInput, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.text, fontSize: '0.78rem' }} />

                {catalogAutocomplete.length > 0 && catalogSearch && <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: sc.bgCard, border: `1px solid ${sc.borderMed}`, borderRadius: '0 0 8px 8px', boxShadow: '0 8px 24px rgba(0,0,0,0.5)', maxHeight: 200, overflowY: 'auto' }}>

                  {catalogAutocomplete.map(item => { const r = getLootRarity(item.rarity); return (

                    <div key={item.id} onClick={() => { setCatalogSearch(item.name); setCatalogAutocomplete([]); }} style={{ padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, borderBottom: `1px solid ${sc.border}` }} onMouseEnter={e => e.currentTarget.style.background = sc.bgCardHover} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                      <span style={{ fontSize: '0.8rem' }}>{item.iconRef && getIconByKey(item.iconRef) ? <RpgIcon iconKey={item.iconRef} size={18} /> : getLootCatIcon(item.category)}</span>

                      <span style={{ fontSize: '0.75rem', color: r.color, fontWeight: 600, flex: 1 }}>{item.name}</span>

                      <span style={{ fontSize: '0.55rem', color: sc.textMuted }}>{item._source === 'shop' ? '🏪' : '🎲'}</span>

                      <span style={{ fontSize: '0.55rem', color: r.color, opacity: 0.7 }}>{r.name}</span>

                    </div>

                  ); })}

                </div>}

              </div>

              <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>

                {[{ id: 'all', label: 'All' }, { id: 'loot', label: <><TreasureChestIcon size={12} color="currentColor" /> Loot</> }, { id: 'shop', label: '🏪 Shop' }].map(f => (

                  <button key={f.id} className="lb" onClick={() => setCatalogFilter(f.id)} style={{ padding: '3px 8px', fontSize: '0.62rem', borderRadius: 4, background: catalogFilter === f.id ? `${sc.accent}18` : 'transparent', border: `1px solid ${catalogFilter === f.id ? sc.accent + '50' : sc.border}`, color: catalogFilter === f.id ? sc.accentLight : sc.textMuted, display: 'inline-flex', alignItems: 'center', gap: 3 }}>{f.label}</button>

                ))}

                <span style={{ width: 1, height: 16, background: sc.border, alignSelf: 'center', margin: '0 2px' }} />

                {Object.entries(LOOT_CATEGORIES).map(([k, v]) => (

                  <button key={k} className="lb" onClick={() => setCatalogFilter(catalogFilter === k ? 'all' : k)} style={{ padding: '3px 6px', fontSize: '0.62rem', borderRadius: 4, background: catalogFilter === k ? `${sc.accent}18` : 'transparent', border: `1px solid ${catalogFilter === k ? sc.accent + '50' : 'transparent'}`, color: catalogFilter === k ? sc.accentLight : sc.textMuted }} title={v.name}>{v.icon}</button>

                ))}

              </div>

            </div>



            {/* Item Grid */}

            <div style={{ fontSize: '0.65rem', color: sc.textMuted, marginBottom: 6 }}>{filtered.length} of {allItemsList.length} items</div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>

              {filtered.map(item => { const r = getLootRarity(item.rarity); return (

                <div key={item.id} style={{ padding: '10px 12px', background: sc.bgCard, border: `1px solid ${r.color}30`, borderRadius: 8, position: 'relative' }}>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>

                    <span style={{ fontSize: '0.9rem' }}>{item.iconRef && getIconByKey(item.iconRef) ? <RpgIcon iconKey={item.iconRef} size={24} /> : getLootCatIcon(item.category)}</span>

                    <span style={{ fontSize: '0.8rem', fontWeight: 600, color: r.color, flex: 1 }}>{item.name}</span>

                    <span style={{ fontSize: '0.5rem', padding: '1px 5px', borderRadius: 4, background: `${r.color}15`, border: `1px solid ${r.color}30`, color: r.color }}>{r.name}</span>

                  </div>

                  {item.shortDesc && <div style={{ fontSize: '0.65rem', color: sc.textMuted, marginBottom: 3 }}>{item.shortDesc}</div>}

                  {(item.buffs?.length > 0 || item.debuffs?.length > 0) && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 3 }}>

                    {(item.buffs || []).map((b, i) => <span key={i} style={{ fontSize: '0.5rem', padding: '1px 4px', background: sc.buffBg, border: `1px solid ${sc.buffBorder}30`, borderRadius: 4, color: sc.buffText }}>{b}</span>)}

                    {(item.debuffs || []).map((d, i) => <span key={i} style={{ fontSize: '0.5rem', padding: '1px 4px', background: sc.debuffBg, border: `1px solid ${sc.debuffBorder}30`, borderRadius: 4, color: sc.debuffText }}>{d}</span>)}

                  </div>}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.6rem' }}>

                    <span style={{ color: sc.gold }}>{item.basePrice > 0 ? `${item.basePrice}gp` : ''}</span>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>

                      {isDM && item._source !== 'shop' && <button className="lb" onClick={() => populateFormFromItem(allItemSources[item.id] || item, false)} title="Edit" style={{ padding: '2px 5px', fontSize: '0.55rem', background: 'rgba(201,162,39,0.1)', border: `1px solid ${sc.accent}30`, borderRadius: 4, color: sc.accent }}>✏️</button>}

                      {isDM && <button className="lb" onClick={() => populateFormFromItem(allItemSources[item.id] || item, true)} title="Duplicate as new" style={{ padding: '2px 5px', fontSize: '0.55rem', background: 'rgba(120,140,200,0.1)', border: '1px solid rgba(120,140,200,0.3)', borderRadius: 4, color: '#9a9ac8' }}>📋</button>}

                      {isDM && item._source !== 'shop' && <button className="lb" onClick={() => { if (window.confirm(`Delete "${item.name}"?`)) { const copy = { ...lootItems }; delete copy[item.id]; saveItems(copy); } }} title="Delete" style={{ padding: '2px 5px', fontSize: '0.55rem', background: 'rgba(201,39,39,0.1)', border: '1px solid rgba(201,39,39,0.3)', borderRadius: 4, color: '#ff6961' }}>🗑</button>}

                      <span style={{ color: sc.textMuted, display: 'inline-flex', alignItems: 'center', gap: 3 }}>{item._source === 'shop' ? '🏪 Shop' : <><TreasureChestIcon size={12} color={sc.textMuted} /> Loot</>}</span>

                    </div>

                  </div>

                </div>

              ); })}

            </div>

          </div>;

        })()}



        {/* LOG */}

        {tab === 'log' && <div style={{ animation: 'lootFadeIn 0.3s ease' }}>

          <LootSectionLabel icon="📜" sc={sc} count={lootLog.length} right={<div style={{ display: 'flex', gap: 3 }}>{['short', 'long'].map(m => <button key={m} className="lb" onClick={() => setLogMode(m)} style={{ padding: '3px 10px', fontSize: '0.68rem', borderRadius: 6, background: logMode === m ? `${sc.accent}18` : 'transparent', border: `1px solid ${logMode === m ? sc.accent + '60' : sc.border}`, color: logMode === m ? sc.accentLight : sc.textMuted }}>{m === 'short' ? 'Summary' : 'Full Log'}</button>)}</div>}>Transaction Log</LootSectionLabel>

          {lootLog.length === 0 ? <div style={{ textAlign: 'center', padding: '40px 0', color: sc.textMuted, fontSize: '0.8rem' }}>No log entries yet.</div>

          : (() => {

            const rev = [...lootLog].reverse();

            const entries = logMode === 'short' ? rev.filter(e => e.type === 'award' || e.type === 'xp') : rev;

            if (entries.length === 0) return <div style={{ textAlign: 'center', padding: '30px 0', color: sc.textMuted, fontSize: '0.8rem' }}>No awards yet.</div>;

            return <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>{entries.map((entry, i) => {

              const player = players.find(p => p.id === entry.playerId);

              const isAward = entry.type === 'award';

              const dot = player ? <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: player.color, marginRight: 6 }} /> : null;

              return <div key={entry.id} style={{ padding: '7px 12px', background: isAward ? sc.greenBg : sc.bgCard, border: `1px solid ${isAward ? sc.green + '30' : sc.border}`, borderRadius: 7, animation: `lootSlideIn 0.25s ease ${i * 20}ms both`, fontSize: '0.78rem' }}>

                {entry.type === 'encounter_start' && <div style={{ color: sc.accent }}>💀 <strong>Encounter started</strong> — {entry.enemyName} ({entry.itemCount} items)</div>}

                {entry.type === 'encounter_end' && <div style={{ color: sc.textMuted }}>🏁 <strong>Encounter ended</strong> — {entry.enemyName}</div>}

                {entry.type === 'claim' && <div style={{ color: sc.textSub }}>{dot}🤚 <strong>{entry.playerName}</strong> claimed <strong style={{ color: sc.textHead }}>{entry.itemName}</strong></div>}

                {entry.type === 'unclaim' && <div style={{ color: sc.textMuted }}>{dot}↩ <strong>{entry.playerName}</strong> withdrew claim on <strong>{entry.itemName}</strong></div>}

                {entry.type === 'roll' && <div style={{ color: sc.textSub }}>{dot}🎲 <strong>{entry.playerName}</strong> rolled <strong style={{ color: sc.accentLight, fontSize: '0.9rem' }}>{entry.roll}</strong> for <strong style={{ color: sc.textHead }}>{entry.itemName}</strong></div>}

                {isAward && <div style={{ color: sc.green, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>{dot}<TreasureChestIcon size={14} color={sc.green} /> <strong>{entry.playerName}</strong> was awarded <strong style={{ color: sc.textHead }}>{entry.itemName}</strong></div>}

                {entry.type === 'xp' && <div style={{ color: sc.accent }}>⭐ <strong>{entry.amount} XP</strong> from <strong style={{ color: sc.red }}>{entry.enemyName}</strong> — {entry.perPlayer} each</div>}

              </div>;

            })}</div>;

          })()}

        </div>}

      </div>

    </div>

  );

};



// ============== END LOOT SYSTEM ==============



// Floating Combat Indicator Component

const FloatingCombatIndicator = ({ combat, onClick }) => {

  if (!combat?.active) return null;

  

  return (

    <div

      style={{

        position: 'fixed',
        // 2026-05-11 F1 fix: indicator was at top:12, z:9999 — landed
        // on top of the mobile sticky header's bell (right side, z:100)
        // and made it untappable during combat. Reposition to top:56
        // (below 50px header) and bring z below the header so the
        // header chrome can always reach.

        top: '56px',

        right: '12px',

        background: 'linear-gradient(135deg, rgba(201,39,39,0.9) 0%, rgba(139,20,20,0.9) 100%)',

        border: '1px solid #ff4444',

        borderRadius: '6px',

        padding: '6px 10px',

        boxShadow: '0 2px 10px rgba(201,39,39,0.4)',

        zIndex: 95,

        cursor: 'pointer',

      }}

      onClick={onClick}

    >

      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

        <span style={{ fontSize: '1rem' }}>⚔️</span>

        <div>

          <div style={{ fontWeight: 'bold', color: 'white', fontSize: '0.75rem' }}>Combat</div>

          <div style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.8)' }}>

            R{combat.round} • {combat.combatants?.length || 0}

          </div>

        </div>

      </div>

    </div>

  );

};



// Floating Dice Roller Button

// Compact Dice Roller Side Panel

const DiceRollerPanel = ({ isOpen, onToggle, diceInput, setDiceInput, diceResult, setDiceResult, parseAndRollDice }) => {

  const quickRolls = ['1d20', '1d10', '1d8', '1d6', '1d4', '1d100'];

  

  const doRoll = (notation) => {

    setDiceInput(notation);

    setDiceResult(parseAndRollDice(notation));

  };

  

  // Check for nat 20 or nat 1 on a d20

  const getNatStatus = () => {

    if (!diceResult) return null;

    // Look for a single d20 roll

    const d20Part = diceResult.parts.find(p => p.type === 'dice' && p.notation === '1d20');

    if (d20Part && d20Part.rolls && d20Part.rolls.length === 1) {

      const roll = d20Part.rolls[0];

      if (roll === 20) return 'nat20';

      if (roll === 1) return 'nat1';

    }

    return null;

  };

  

  const natStatus = getNatStatus();

  

  // Colors based on nat status

  const getResultColor = () => {

    if (natStatus === 'nat20') return '#00ff00'; // Bright green for crit

    if (natStatus === 'nat1') return '#ff0000'; // Bright red for crit fail

    return '#ffd700'; // Gold for normal

  };

  

  const getResultGlow = () => {

    if (natStatus === 'nat20') return '0 0 15px rgba(0,255,0,0.6), 0 0 30px rgba(0,255,0,0.3)';

    if (natStatus === 'nat1') return '0 0 15px rgba(255,0,0,0.6), 0 0 30px rgba(255,0,0,0.3)';

    return '0 0 10px rgba(255,215,0,0.4)';

  };

  

  const getResultLabel = () => {

    if (natStatus === 'nat20') return '✨ NAT 20! ✨';

    if (natStatus === 'nat1') return '💀 NAT 1 💀';

    return null;

  };

  

  return (

    <div style={{

      position: 'fixed',

      bottom: '20px',

      left: '20px',

      zIndex: 999,

      display: 'flex',

      flexDirection: 'column',

      alignItems: 'flex-start',

      gap: '8px',

    }}>

      {/* Expanded Panel */}

      {isOpen && (

        <div style={{

          background: 'linear-gradient(135deg, rgba(30,20,15,0.98), rgba(40,30,25,0.98))',

          border: `2px solid ${natStatus === 'nat20' ? '#00ff00' : natStatus === 'nat1' ? '#ff0000' : '#9b59b6'}`,

          borderRadius: '10px',

          padding: '10px',

          width: '180px',

          boxShadow: '0 4px 20px rgba(0,0,0,0.5)',

        }}>

          {/* Input Row */}

          <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>

            <input

              value={diceInput}

              onChange={(e) => setDiceInput(e.target.value)}

              onKeyDown={(e) => e.key === 'Enter' && doRoll(diceInput)}

              style={{ 

                ...S.input, 

                flex: 1, 

                marginBottom: 0, 

                padding: '6px 8px', 

                fontSize: '0.9rem', 

                fontFamily: 'monospace',

                textAlign: 'center',

              }}

              placeholder="2d6+3"

            />

            <button

              style={{ ...S.btn, ...S.btnSuccess, ...S.btnXs, padding: '6px 10px' }}

              onClick={() => doRoll(diceInput)}

            >

              🎲

            </button>

          </div>

          

          {/* Quick Roll Grid */}

          <div style={{ 

            display: 'grid', 

            gridTemplateColumns: 'repeat(3, 1fr)', 

            gap: '4px',

            marginBottom: diceResult ? '8px' : 0,

          }}>

            {quickRolls.map(dice => (

              <button

                key={dice}

                style={{ 

                  ...S.btn, 

                  ...S.btnXs, 

                  padding: '4px 2px',

                  fontSize: '0.7rem',

                  fontFamily: 'monospace',

                  background: 'rgba(155,89,182,0.2)',

                  borderColor: '#9b59b6',

                }}

                onClick={() => doRoll(dice)}

              >

                {dice}

              </button>

            ))}

          </div>

          

          {/* Result Display */}

          {diceResult && (

            <div style={{ 

              background: natStatus === 'nat20' ? 'rgba(0,255,0,0.1)' : natStatus === 'nat1' ? 'rgba(255,0,0,0.1)' : 'rgba(0,0,0,0.3)', 

              borderRadius: '6px',

              padding: '8px',

              textAlign: 'center',

              border: natStatus ? `1px solid ${getResultColor()}` : 'none',

            }}>

              {/* Nat 20/1 Label */}

              {getResultLabel() && (

                <div style={{ 

                  fontSize: '0.75rem', 

                  fontWeight: 'bold',

                  color: getResultColor(),

                  marginBottom: '4px',

                  animation: 'pulse 0.5s ease-in-out',

                }}>

                  {getResultLabel()}

                </div>

              )}

              

              {/* Total - big and prominent */}

              <div style={{ 

                fontSize: '2rem', 

                fontWeight: 'bold', 

                color: getResultColor(),

                textShadow: getResultGlow(),

                lineHeight: 1,

              }}>

                {diceResult.total}

              </div>

              

              {/* Breakdown */}

              <div style={{ fontSize: '0.7rem', color: '#888', marginTop: '4px' }}>

                {diceResult.notation}

                {diceResult.parts.filter(p => p.type === 'dice').map((p, i) => (

                  <span key={i} style={{ display: 'block', color: '#9b59b6' }}>

                    [{p.detail}]

                  </span>

                ))}

              </div>

            </div>

          )}

        </div>

      )}

      

      {/* Toggle Button */}

      <div

        onClick={onToggle}

        style={{

          width: '44px',

          height: '44px',

          borderRadius: '50%',

          background: isOpen 

            ? 'linear-gradient(135deg, #8e44ad, #7b3a9e)' 

            : 'linear-gradient(135deg, #9b59b6, #8e44ad)',

          display: 'flex',

          alignItems: 'center',

          justifyContent: 'center',

          cursor: 'pointer',

          boxShadow: '0 4px 15px rgba(0,0,0,0.4)',

          border: `2px solid ${isOpen ? '#ffd700' : 'rgba(255,255,255,0.3)'}`,

          transition: 'all 0.2s ease',

        }}

        title={isOpen ? "Close dice roller" : "Open dice roller"}

      >

        <span style={{ fontSize: '1.3rem' }}>🎲</span>

      </div>

    </div>

  );

};



// Keep old modal for backward compatibility but redirect to panel

const FloatingDiceButton = ({ onClick }) => null; // Deprecated - use DiceRollerPanel instead



const DiceRollerModal = ({ isOpen, onClose, diceInput, setDiceInput, diceResult, setDiceResult, parseAndRollDice }) => null; // Deprecated



// Legacy D&D Beyond PDF import retired. Use src/features/character-import and the sanitized app-payload review/apply path.

// THEME CONFIGURATOR PAGE - Full preview with sample character

// ============================================================================





// ============================================================================

// THEME CONFIGURATOR PAGE - Matches Demo v9 Layout

// ============================================================================



const SAMPLE_CHARACTER = {

  name: "Li'ka",

  race: 'Half-Elf',

  charClass: 'Ranger',

  level: 5,

  currentHp: 38,

  maxHp: 42,

  currentMana: 8,

  maxMana: 12,

  armorClass: 15,

  speed: 35,

  currentDay: 12,

  mealsToday: 2,

  buffList: ['+2 Dex (Cat\'s Grace)', 'Hunter\'s Mark'],

  debuffList: ['Exhaustion 1', '-1 Str'],

  permanentBuffs: ['Darkvision', 'Fey Ancestry'],

  conditions: [],

};



const ThemeConfiguratorPage = ({ currentThemeId, currentVariationId, currentDensity, currentCardStyle, currentHoverEffects, onSave, onCancel }) => {

  const [selectedTheme, setSelectedTheme] = useState(currentThemeId || 'fantasy-classic');

  const [selectedVariation, setSelectedVariation] = useState(currentVariationId || 'default');

  const [selectedDensity, setSelectedDensity] = useState(currentDensity || 'comfortable');

  const [selectedCardStyle, setSelectedCardStyle] = useState(currentCardStyle || 'elevated');

  const [enableHoverEffects, setEnableHoverEffects] = useState(currentHoverEffects !== false);

  const [category, setCategory] = useState('all');

  

  const baseTheme = THEMES[selectedTheme] || THEMES['fantasy-classic'];

  const theme = applyVariation(baseTheme, selectedVariation);

  const variations = THEME_VARIATIONS[selectedTheme] || [{ id: 'default', name: 'Default' }];

  const density = DENSITY_MODES[selectedDensity];

  const c = theme.colors;

  const e = theme.effects || {};

  const t = theme.typography || { fontFamily: 'Georgia, serif', headingFamily: 'Georgia, serif' };



  // Filter themes by category (excluding high-contrast)

  const filteredThemes = THEME_ORDER

    .map(id => THEMES[id])

    .filter(th => th && th.id !== 'high-contrast')

    .filter(th => category === 'all' || th.category === category);



  // Card component with hover effects

  const ThemedCard = ({ children, style = {} }) => {

    const [hovered, setHovered] = useState(false);

    const cardStyleObj = CARD_STYLES[selectedCardStyle].getStyle(theme, enableHoverEffects ? hovered : false);

    return (

      <div 

        onMouseEnter={() => setHovered(true)} 

        onMouseLeave={() => setHovered(false)}

        style={{

          ...cardStyleObj,

          padding: density.spacing.cardPadding,

          marginBottom: density.spacing.cardGap,

          transition: e.transition || 'all 0.2s ease',

          ...style

        }}

      >

        {children}

      </div>

    );

  };



  // Button component with hover

  const ThemedButton = ({ children, variant = 'default', active = false, size = 'md', style = {}, ...props }) => {

    const [hovered, setHovered] = useState(false);

    const variants = {

      default: { 

        background: `linear-gradient(180deg, ${c.accent} 0%, ${c.accentDark} 100%)`, 

        border: `1px solid ${c.accentLight}`, 

        color: c.textOnAccent 

      },

      ghost: { 

        background: active ? c.bgHover : 'transparent', 

        border: `1px solid ${active ? c.accent : c.borderPrimary}`, 

        color: active ? c.accent : c.textPrimary 

      },

    };

    const sizes = {

      sm: { padding: density.layout.buttonPadding, fontSize: density.typography.labelSize },

      md: { padding: density.layout.buttonPadding, fontSize: density.typography.baseFontSize },

    };

    return (

      <button 

        onMouseEnter={() => setHovered(true)} 

        onMouseLeave={() => setHovered(false)}

        style={{ 

          ...variants[variant], 

          ...sizes[size],

          borderRadius: e.borderRadiusSm || '4px', 

          cursor: 'pointer', 

          fontFamily: t.fontFamily,

          fontWeight: 600, 

          transition: e.transition || 'all 0.2s ease',

          opacity: hovered ? 0.85 : 1,

          transform: hovered ? 'translateY(-1px)' : 'none',

          ...style 

        }} {...props}>

        {children}

      </button>

    );

  };



  // Badge component

  const ThemedBadge = ({ children, variant = 'buff', permanent = false }) => {

    const baseVariants = {

      buff: { background: c.buffBg, border: `1px solid ${c.buffBorder}`, color: c.buffText },

      debuff: { background: c.debuffBg, border: `1px solid ${c.debuffBorder}`, color: c.debuffText },

      condition: { background: c.warningBg, border: `1px solid ${c.warning}`, color: c.warning },

    };

    const permanentStyle = permanent ? {

      background: variant === 'buff' ? 'rgba(20,80,40,0.6)' : 'rgba(80,20,20,0.6)',

      border: `1px solid ${variant === 'buff' ? c.buffText : c.debuffText}`,

      boxShadow: `0 0 6px ${variant === 'buff' ? c.buffBorder : c.debuffBorder}50`,

    } : {};

    return (

      <span style={{ 

        display: 'inline-flex', 

        alignItems: 'center',

        gap: '4px',

        padding: density.layout.badgePadding, 

        borderRadius: '20px', 

        fontSize: density.typography.badgeSize, 

        fontWeight: 600, 

        ...baseVariants[variant], 

        ...permanentStyle,

      }}>

        {permanent && <span style={{ fontSize: '0.85em' }}>◆</span>}

        {children}

      </span>

    );

  };



  // HP Bar

  const HPBar = ({ current, max }) => {

    const percent = (current / max) * 100;

    const color = percent > 50 ? c.hpHigh : percent > 25 ? c.hpMid : c.hpLow;

    return (

      <div style={{ height: '26px', background: c.bgInput, borderRadius: e.borderRadiusSm || '4px', overflow: 'hidden', border: e.inputBorder || `1px solid ${c.borderLight}`, position: 'relative' }}>

        <div style={{ height: '100%', width: `${percent}%`, background: `linear-gradient(90deg, ${color}99 0%, ${color} 50%, ${color}cc 100%)`, boxShadow: `0 0 10px ${color}66` }} />

        <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: density.typography.labelSize, fontWeight: 'bold', color: c.textPrimary, textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>{current}/{max}</span>

      </div>

    );

  };



  // Mana Bar

  const ManaBar = ({ current, max }) => {

    const percent = (current / max) * 100;

    return (

      <div style={{ height: '18px', background: c.bgInput, borderRadius: e.borderRadiusSm || '4px', overflow: 'hidden', border: e.inputBorder || `1px solid ${c.borderLight}`, position: 'relative' }}>

        <div style={{ height: '100%', width: `${percent}%`, background: `linear-gradient(90deg, ${c.mana}99 0%, ${c.mana} 100%)`, boxShadow: `0 0 8px ${c.mana}66` }} />

        <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '0.7rem', fontWeight: 'bold', color: c.textPrimary, textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>✨ {current}/{max}</span>

      </div>

    );

  };



  // Sample character

  const character = {

    name: "Li'ka",

    race: 'Half-Elf',

    charClass: 'Ranger',

    level: 5,

    currentHp: 38,

    maxHp: 42,

    currentMana: 8,

    maxMana: 12,

    armorClass: 15,

    speed: 35,

    currentDay: 12,

    mealsToday: 2,

    permanentBuffs: ['Darkvision', 'Fey Ancestry'],

    buffList: ['+2 Dex (Cat\'s Grace)', 'Hunter\'s Mark'],

    debuffList: ['Exhaustion 1', '-1 Str'],

    conditions: [],

  };



  return (

    <div style={{ 

      minHeight: '100vh', 

      background: c.bgPrimary, 

      fontFamily: t.fontFamily,

      fontSize: density.typography.baseFontSize,

      color: c.textPrimary, 

      padding: '24px', 

      boxSizing: 'border-box',

      transition: e.transition || 'all 0.2s ease',

    }}>

      {/* Header */}

      <div style={{ 

        textAlign: 'center', 

        marginBottom: '32px', 

        padding: '24px', 

        background: c.bgCardSolid, 

        borderRadius: e.borderRadiusLg || '12px', 

        border: e.cardBorder || `2px solid ${c.borderPrimary}`, 

        boxShadow: e.cardShadow 

      }}>

        <h1 style={{ 

          color: c.accentLight, 

          margin: '0 0 8px 0', 

          fontFamily: t.headingFamily, 

          fontSize: '1.8rem', 

          textShadow: e.headingTextShadow || '0 2px 4px rgba(0,0,0,0.6)' 

        }}>

          🎲 Survival Tracker

        </h1>

        <p style={{ color: c.textSecondary, margin: 0, fontSize: '0.9rem' }}>

          <strong style={{ color: c.accent }}>{theme.name}</strong>

          {selectedVariation !== 'default' && (

            <span style={{ color: c.textMuted }}> — {variations.find(v => v.id === selectedVariation)?.name}</span>

          )}

        </p>

      </div>

      

      {/* Main Grid - Two Column Layout */}

      <div style={{ 

        display: 'grid', 

        gridTemplateColumns: `repeat(auto-fit, minmax(${density.layout.gridMinWidth}, 1fr))`, 

        gap: density.spacing.cardGap, 

        maxWidth: '1400px', 

        margin: '0 auto' 

      }}>

        {/* Left Column - Selectors */}

        <div>

          {/* Theme Selector */}

          <ThemedCard>

            <h3 style={{ 

              color: c.accentLight, 

              margin: `0 0 ${density.spacing.sectionGap} 0`, 

              fontFamily: t.headingFamily, 

              fontSize: density.typography.headingSize, 

              textShadow: e.headingTextShadow 

            }}>

              🎨 Color Theme

            </h3>

            

            {/* Variation Selector - At Top */}

            {variations.length > 1 && (

              <div style={{ 

                background: c.bgInput, 

                borderRadius: e.borderRadius || '8px', 

                padding: '14px', 

                border: `1px solid ${c.borderPrimary}`, 

                marginBottom: density.spacing.sectionGap 

              }}>

                <div style={{ 

                  color: c.accent, 

                  fontSize: density.typography.labelSize, 

                  marginBottom: '10px', 

                  fontWeight: 700, 

                  display: 'flex', 

                  alignItems: 'center', 

                  gap: '8px' 

                }}>

                  <span style={{ fontSize: '1.1rem' }}>{baseTheme.preview}</span>

                  {baseTheme.name} Variations

                </div>

                <div style={{ display: 'flex', gap: density.spacing.itemGap, flexWrap: 'wrap' }}>

                  {variations.map(v => (

                    <ThemedButton 

                      key={v.id} 

                      variant="ghost" 

                      size="sm"

                      active={selectedVariation === v.id} 

                      onClick={() => setSelectedVariation(v.id)}

                    >

                      {v.name}

                    </ThemedButton>

                  ))}

                </div>

              </div>

            )}

            

            {/* Category Filter */}

            <div style={{ display: 'flex', gap: density.spacing.itemGap, marginBottom: density.spacing.sectionGap, flexWrap: 'wrap' }}>

              {['all', 'fantasy', 'multicolor', 'professional'].map(cat => (

                <ThemedButton 

                  key={cat} 

                  variant={category === cat ? 'default' : 'ghost'} 

                  size="sm"

                  onClick={() => setCategory(cat)}

                >

                  {cat === 'all' ? '🌈 All' : cat === 'fantasy' ? '🏰 Fantasy' : cat === 'multicolor' ? '🎨 Multi' : '💼 Pro'}

                </ThemedButton>

              ))}

            </div>

            

            {/* Theme Grid */}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '10px' }}>

              {filteredThemes.map(th => (

                <div 

                  key={th.id} 

                  onClick={() => { setSelectedTheme(th.id); setSelectedVariation('default'); }}

                  style={{ 

                    padding: '12px 8px', 

                    borderRadius: e.borderRadius || '8px', 

                    background: selectedTheme === th.id ? c.bgHover : c.bgInput, 

                    border: selectedTheme === th.id ? `2px solid ${c.accent}` : e.inputBorder || `1px solid ${c.borderLight}`, 

                    cursor: 'pointer', 

                    transition: 'all 0.2s', 

                    textAlign: 'center' 

                  }}

                >

                  <div style={{ fontSize: '1.4rem', marginBottom: '4px' }}>{th.preview}</div>

                  <div style={{ 

                    color: selectedTheme === th.id ? c.accent : c.textPrimary, 

                    fontWeight: selectedTheme === th.id ? 'bold' : 'normal', 

                    fontSize: '0.65rem' 

                  }}>

                    {th.name}

                  </div>

                </div>

              ))}

            </div>

          </ThemedCard>

          

          {/* Layout Density Selector */}

          <ThemedCard>

            <h3 style={{ 

              color: c.accentLight, 

              margin: `0 0 ${density.spacing.sectionGap} 0`, 

              fontFamily: t.headingFamily, 

              fontSize: density.typography.headingSize, 

              textShadow: e.headingTextShadow 

            }}>

              📐 Layout Density

            </h3>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>

              {Object.values(DENSITY_MODES).map(d => (

                <div 

                  key={d.id} 

                  onClick={() => setSelectedDensity(d.id)} 

                  style={{ 

                    flex: '1 1 80px', 

                    padding: '14px', 

                    borderRadius: e.borderRadius || '8px', 

                    background: selectedDensity === d.id ? c.bgHover : c.bgInput, 

                    border: selectedDensity === d.id ? `2px solid ${c.accent}` : e.inputBorder || `1px solid ${c.borderLight}`, 

                    cursor: 'pointer', 

                    textAlign: 'center',

                    transition: 'all 0.2s',

                  }}

                >

                  <div style={{ fontSize: '1.3rem', marginBottom: '4px' }}>{d.icon}</div>

                  <div style={{ color: selectedDensity === d.id ? c.accent : c.textPrimary, fontWeight: selectedDensity === d.id ? 'bold' : 'normal', fontSize: '0.75rem' }}>{d.name}</div>

                </div>

              ))}

            </div>

          </ThemedCard>

          

          {/* Card Style Selector */}

          <ThemedCard>

            <h3 style={{ 

              color: c.accentLight, 

              margin: `0 0 ${density.spacing.sectionGap} 0`, 

              fontFamily: t.headingFamily, 

              fontSize: density.typography.headingSize, 

              textShadow: e.headingTextShadow 

            }}>

              🃏 Card Style

            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '10px' }}>

              {Object.values(CARD_STYLES).map(s => (

                <div 

                  key={s.id} 

                  onClick={() => setSelectedCardStyle(s.id)} 

                  style={{ 

                    padding: '12px 8px', 

                    borderRadius: e.borderRadius || '8px', 

                    background: selectedCardStyle === s.id ? c.bgHover : c.bgInput, 

                    border: selectedCardStyle === s.id ? `2px solid ${c.accent}` : e.inputBorder || `1px solid ${c.borderLight}`, 

                    cursor: 'pointer', 

                    textAlign: 'center',

                    transition: 'all 0.2s',

                  }}

                >

                  <div style={{ fontSize: '1.3rem', marginBottom: '4px' }}>{s.icon}</div>

                  <div style={{ color: selectedCardStyle === s.id ? c.accent : c.textPrimary, fontWeight: selectedCardStyle === s.id ? 'bold' : 'normal', fontSize: '0.7rem' }}>{s.name}</div>

                </div>

              ))}

            </div>

          </ThemedCard>

          

          {/* Hover Effects Toggle */}

          <ThemedCard>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>

              <label style={{ 

                display: 'flex', 

                alignItems: 'center', 

                gap: '10px',

                cursor: 'pointer',

                color: c.textPrimary,

                fontSize: density.typography.baseFontSize,

              }}>

                <input 

                  type="checkbox" 

                  checked={enableHoverEffects} 

                  onChange={(ev) => setEnableHoverEffects(ev.target.checked)}

                  style={{

                    width: '20px',

                    height: '20px',

                    accentColor: c.accent,

                    cursor: 'pointer',

                  }}

                />

                <span>✨ Enable Hover Effects</span>

              </label>

            </div>

            <p style={{ 

              color: c.textMuted, 

              fontSize: density.typography.labelSize, 

              margin: '10px 0 0 0',

            }}>

              Cards will glow and lift on hover when enabled

            </p>

          </ThemedCard>

        </div>

        

        {/* Right Column - Character Preview */}

        <div>

          <ThemedCard>

            {/* Character Header */}

            <div style={{ display: 'flex', alignItems: 'center', gap: density.spacing.sectionGap, marginBottom: density.spacing.sectionGap }}>

              <div style={{ 

                width: '64px', 

                height: '64px', 

                borderRadius: '50%', 

                background: c.bgInput, 

                border: `3px solid ${c.accent}`, 

                display: 'flex', 

                alignItems: 'center', 

                justifyContent: 'center', 

                fontSize: '1.8rem', 

                boxShadow: `0 0 15px ${c.accentGlow}` 

              }}>

                🧝

              </div>

              <div style={{ flex: 1 }}>

                <h2 style={{ 

                  margin: 0, 

                  color: c.accentLight, 

                  fontSize: density.typography.headingSize, 

                  fontFamily: t.headingFamily, 

                  textShadow: e.headingTextShadow 

                }}>

                  {character.name}

                </h2>

                <div style={{ color: c.textSecondary, fontSize: density.typography.labelSize }}>

                  {character.race} • {character.charClass} • Level {character.level}

                </div>

              </div>

            </div>

            

            {/* HP and Mana */}

            <div style={{ marginBottom: density.spacing.sectionGap }}>

              <HPBar current={character.currentHp} max={character.maxHp} />

              <div style={{ marginTop: '10px' }}>

                <ManaBar current={character.currentMana} max={character.maxMana} />

              </div>

            </div>

            

            {/* Stats Grid */}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: density.spacing.itemGap, marginBottom: density.spacing.sectionGap }}>

              {[

                { label: 'AC', value: character.armorClass, icon: '🛡️' },

                { label: 'SPEED', value: character.speed, icon: '👟' },

                { label: 'DAY', value: character.currentDay, icon: '📅' },

                { label: 'MEALS', value: `${character.mealsToday}/2`, icon: '🍖' }

              ].map(stat => (

                <div key={stat.label} style={{ 

                  background: c.bgInput, 

                  padding: density.layout.inputPadding, 

                  borderRadius: e.borderRadiusSm || '4px', 

                  textAlign: 'center', 

                  border: e.inputBorder || `1px solid ${c.borderLight}` 

                }}>

                  <div style={{ fontSize: '1.1rem', marginBottom: '2px' }}>{stat.icon}</div>

                  <div style={{ color: c.textPrimary, fontWeight: 'bold', fontSize: density.typography.baseFontSize }}>{stat.value}</div>

                  <div style={{ color: c.textMuted, fontSize: '0.65rem', textTransform: 'uppercase' }}>{stat.label}</div>

                </div>

              ))}

            </div>

            

            {/* Active Effects */}

            <div style={{ marginBottom: density.spacing.sectionGap }}>

              <div style={{ color: c.textSecondary, fontSize: density.typography.labelSize, marginBottom: density.spacing.itemGap, fontWeight: 600 }}>Active Effects</div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: density.spacing.itemGap }}>

                {character.permanentBuffs.map((b, i) => (

                  <ThemedBadge key={`pb-${i}`} variant="buff" permanent>{b}</ThemedBadge>

                ))}

                {character.buffList.map((b, i) => (

                  <ThemedBadge key={`b-${i}`} variant="buff">{b}</ThemedBadge>

                ))}

                {character.debuffList.map((d, i) => (

                  <ThemedBadge key={`d-${i}`} variant="debuff">{d}</ThemedBadge>

                ))}

                {character.conditions?.map((cn, i) => (

                  <ThemedBadge key={`c-${i}`} variant="condition">{cn}</ThemedBadge>

                ))}

              </div>

            </div>

            

            {/* Sample Buttons */}

            <div style={{ display: 'flex', gap: density.spacing.itemGap, flexWrap: 'wrap' }}>

              <ThemedButton size="sm">🎒 Inventory</ThemedButton>

              <ThemedButton size="sm" variant="ghost">✨ Spells</ThemedButton>

              <ThemedButton size="sm" variant="ghost">📜 Journal</ThemedButton>

            </div>

          </ThemedCard>

        </div>

      </div>

      

      {/* Save/Cancel Buttons */}

      <div style={{ 

        display: 'flex', 

        justifyContent: 'center', 

        gap: '16px', 

        marginTop: '24px',

      }}>

        <button

          onClick={() => onSave(selectedTheme, selectedVariation, selectedDensity, selectedCardStyle, enableHoverEffects)}

          style={{

            padding: '14px 32px',

            background: `linear-gradient(180deg, ${c.success} 0%, #1a6b35 100%)`,

            border: `2px solid ${c.success}`,

            borderRadius: '8px',

            color: '#fff',

            cursor: 'pointer',

            fontSize: '1rem',

            fontWeight: 'bold',

          }}

        >

          ✓ Save & Apply

        </button>

        <button

          onClick={onCancel}

          style={{

            padding: '14px 32px',

            background: 'rgba(0,0,0,0.3)',

            border: `2px solid ${c.borderLight}`,

            borderRadius: '8px',

            color: c.textSecondary,

            cursor: 'pointer',

            fontSize: '1rem',

          }}

        >

          ✕ Cancel

        </button>

      </div>

      

      {/* Footer */}

      <div style={{ textAlign: 'center', marginTop: '32px', padding: '16px', color: c.textMuted, fontSize: '0.8rem' }}>

        💡 Select a main theme, then choose a variation to customize the background & text

      </div>

    </div>

  );

};





// --- Inject shop-specific CSS animations ---

const injectShopStyles = () => {

  let style = document.getElementById('shop-integrated-styles');

  if (!style) {

    style = document.createElement('style');

    style.id = 'shop-integrated-styles';

    document.head.appendChild(style);

  }

  style.textContent = `

    @keyframes shopFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }

    @keyframes shopPulse { 0%,100% { opacity: 0.6; } 50% { opacity: 1; } }

    @keyframes shopSlideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

    @keyframes shopGlow { 0%,100% { box-shadow: 0 0 8px var(--glow-color); } 50% { box-shadow: 0 0 16px var(--glow-color); } }

    @keyframes cartBounce { 0%,100% { transform: scale(1); } 50% { transform: scale(1.15); } }

    @keyframes modalIn { from { opacity:0; transform: scale(0.95) translateY(10px); } to { opacity:1; transform: scale(1) translateY(0); } }

    @keyframes slideRight { from { transform: translateX(100%); } to { transform: translateX(0); } }

    @keyframes fadeOverlay { from { opacity:0; } to { opacity:1; } }

    @keyframes legendaryShimmer { 0% { border-color: #8a6a20; } 33% { border-color: #d4a020; } 66% { border-color: #f0c040; } 100% { border-color: #8a6a20; } }

    .shop-sys * { box-sizing: border-box; }

    .shop-sys ::-webkit-scrollbar { width: 6px; }

    .shop-sys ::-webkit-scrollbar-track { background: #0a0a0c; }

    .shop-sys ::-webkit-scrollbar-thumb { background: #2a2a2a; border-radius: 3px; }

    .shop-sys ::-webkit-scrollbar-thumb:hover { background: #3a3a3a; }

    .shop-grid-card { transition: all 0.2s ease; cursor: pointer; }

    .shop-grid-card:hover { }

    .shop-grid-card:active { transform: scale(0.99); }

    .shop-list-row { transition: background 0.15s ease; }

    .shop-list-row:hover { background: #1a1a1e !important; }

    .shop-btn { transition: all 0.15s ease; cursor: pointer; user-select: none; }

    .shop-btn:hover { filter: brightness(1.2); }

    .shop-btn:active { transform: scale(0.97); }

    .shop-buy-btn { transition: all 0.2s ease; cursor: pointer; user-select: none; background: #1a1a28; border: 1px solid #3a3a58; border-radius: 4px; color: #9a9ac8; font-weight: 600; }

    .shop-buy-btn:hover:not(:disabled) { background: #22223a; box-shadow: 0 0 10px rgba(154,154,200,0.3), 0 0 4px rgba(154,154,200,0.15); border-color: #5a5a8a; color: #b8b8e0; transform: translateY(-1px); }

    .shop-buy-btn:active:not(:disabled) { transform: scale(0.96); box-shadow: 0 0 6px rgba(154,154,200,0.15); }

    .shop-buy-btn:disabled { opacity: 0.3; cursor: not-allowed; }

    .shop-cart-add-btn { transition: all 0.2s ease; cursor: pointer; user-select: none; background: #1a1a28; border: 1px solid #3a3a58; border-radius: 4px; color: #9a9ac8; }

    .shop-cart-add-btn:hover:not(:disabled) { background: #22223a; box-shadow: 0 0 10px rgba(154,154,200,0.3), 0 0 4px rgba(154,154,200,0.15); border-color: #5a5a8a; color: #b8b8e0; transform: translateY(-1px); }

    .shop-cart-add-btn:active:not(:disabled) { transform: scale(0.96); box-shadow: 0 0 6px rgba(154,154,200,0.15); }

    .shop-cart-add-btn:disabled { opacity: 0.3; cursor: not-allowed; }

    .shop-cat-btn { transition: all 0.2s ease; }

    .shop-cat-btn:hover { background: #1e1e22 !important; }

    .shop-input { transition: border-color 0.2s ease; }

    .shop-input:focus { border-color: #4a4030 !important; outline: none; }

    .shop-tab { transition: all 0.2s ease; position: relative; }

    .shop-tab::after { content: ''; position: absolute; bottom: -2px; left: 0; right: 0; height: 2px; background: transparent; transition: background 0.2s ease; }

    .shop-tab:hover { color: #c4b998 !important; }

    .shop-tab-active::after { background: #d4aa40 !important; }

    .shop-tab-active { color: #d4c4a0 !important; }

    .offer-pulse { animation: shopPulse 2s ease-in-out infinite; }

    .legendary-card { animation: legendaryShimmer 3s ease-in-out infinite; }

  `;

};



// --- Shop theme colors (hardcoded dark theme matching prototype) ---

const SC = {

  bg: '#0a0a0e', bgBar: '#0d0d0f', bgCard: '#111116', bgCardHover: '#18181e',

  bgInput: '#0d0d0f', bgDark: '#0e0e12', bgDarker: '#0a0a0e',

  border: '#1e1e22', borderLight: '#1a1a1e', borderMed: '#2a2a2a', borderActive: '#3a3a40',

  text: '#c4b998', textHead: '#d4c4a0', textMuted: '#5a5a4a', textDim: '#3a3a35', textSub: '#7a7a6a', textSubLight: '#8a8a7a',

  gold: '#d4aa40', goldDark: '#2a2518', goldBorder: '#4a4030',

  green: '#8cb87a', greenBg: '#1a2518', greenBorder: '#2a4030',

  red: '#b88a7a', redBg: '#251a18', redBorder: '#40302a',

  blue: '#8a8ab8', blueBg: '#1a1a2a',

};



// Derive shop colors from app theme for theme toggle

const deriveShopColors = (theme) => {

  if (!theme?.colors) return SC;

  const c = theme.colors;

  return {

    bg: c.bgSecondary || SC.bg,

    bgBar: c.bgCardSolid || SC.bgBar,

    bgCard: c.bgCardSolid || SC.bgCard,

    bgCardHover: c.bgCardHover || SC.bgCardHover,

    bgInput: c.bgInput || SC.bgInput,

    bgDark: c.bgSecondary || SC.bgDark,

    bgDarker: c.bgSecondary || SC.bgDarker,

    border: c.borderLight || SC.border,

    borderLight: c.borderLight || SC.borderLight,

    borderMed: c.borderPrimary || SC.borderMed,

    borderActive: c.borderAccent || SC.borderActive,

    text: c.textPrimary || SC.text,

    textHead: c.accentLight || SC.textHead,

    textMuted: c.textMuted || SC.textMuted,

    textDim: c.textMuted || SC.textDim,

    textSub: c.textSecondary || SC.textSub,

    textSubLight: c.textSecondary || SC.textSubLight,

    gold: c.accent || SC.gold,

    goldDark: c.warningBg || SC.goldDark,

    goldBorder: c.borderAccent || SC.goldBorder,

    green: c.success || SC.green,

    greenBg: c.successBg || SC.greenBg,

    greenBorder: c.success || SC.greenBorder,

    red: c.danger || SC.red,

    redBg: c.dangerBg || SC.redBg,

    redBorder: c.danger || SC.redBorder,

    blue: c.info || SC.blue,

    blueBg: c.infoBg || SC.blueBg,

    accentGlow: c.accentGlow || 'rgba(201,162,39,0.4)',

  };

};



const SHOP_CATEGORIES = [

  { id: 'all', name: 'All', icon: '⚡' },

  { id: 'potion', name: 'Potions', icon: '🧪' },

  { id: 'scroll', name: 'Scrolls', icon: '📜' },

  { id: 'weapon', name: 'Weapons', icon: '⚔️' },

  { id: 'armor', name: 'Armor', icon: '🛡️' },

  { id: 'gear', name: 'Gear', icon: '🎒' },

  { id: 'food', name: 'Food', icon: '🍖' },

  { id: 'ingredient', name: 'Ingredients', icon: '🌿' },

  { id: 'misc', name: 'Misc', icon: '📦' },

];



const SHOP_RARITIES = {

  common:    { name: 'Common',    color: '#9a9a8a', glow: 'rgba(154,154,138,0.15)', border: '#3a3a35' },

  uncommon:  { name: 'Uncommon',  color: '#4a9e4a', glow: 'rgba(74,158,74,0.18)',   border: '#2a5a2a' },

  rare:      { name: 'Rare',      color: '#4a7eb8', glow: 'rgba(74,126,184,0.2)',   border: '#2a4a6a' },

  epic:      { name: 'Epic',      color: '#9a4ab8', glow: 'rgba(154,74,184,0.22)',  border: '#5a2a6a' },

  legendary: { name: 'Legendary', color: '#d4a020', glow: 'rgba(212,160,32,0.25)',  border: '#6a5020' },

};



const SHOP_SECRECY_LEVELS = [

  { id: 'visible',  name: 'Visible',    icon: '', locks: 0, color: '#8cb87a', desc: 'Shown to all players' },

  { id: 'hidden',   name: 'Hidden',     icon: '', locks: 0, color: '#8a8a7a', desc: 'Not shown until revealed' },

  { id: 'secret-1', name: 'Secret ★',   icon: '🔒', locks: 1, color: '#d4aa40', desc: 'Tier 1 secret — ask the keeper' },

  { id: 'secret-2', name: 'Secret ★★',  icon: '🔒🔒', locks: 2, color: '#d97429', desc: 'Tier 2 secret — requires trust' },

  { id: 'secret-3', name: 'Secret ★★★', icon: '🔒🔒🔒', locks: 3, color: '#c92727', desc: 'Tier 3 secret — black market only' },

];



const SHOP_CAT_ICONS = { potion: '🧪', scroll: '📜', weapon: '⚔️', armor: '🛡️', gear: '🎒', food: '🍖', ingredient: '🌿', misc: '📦' };

const getShopRarity = (id) => SHOP_RARITIES[id] || SHOP_RARITIES.common;

const getShopCatIcon = (id) => SHOP_CAT_ICONS[id] || '📦';

const getSecrecy = (id) => SHOP_SECRECY_LEVELS.find(s => s.id === id) || SHOP_SECRECY_LEVELS[0];



const SHOP_TYPE_OPTIONS = ['General', 'Weapons & Armor', 'Potions & Alchemy', 'Magic Items', 'Food & Supplies', 'Black Market', 'Tavern', 'Jeweler', 'Enchanter', 'Herbalist'];



const SHOP_SINGLE_USE = { current: 1, max: 1, resetType: 'none' };
const SHOP_SHORT_REST_USE = { current: 1, max: 1, resetType: 'shortRest' };

const DEFAULT_ITEM_TEMPLATES = {

  health_potion:    { id: 'health_potion',    name: 'Health Potion',           category: 'potion',     rarity: 'common',    shortDesc: 'Restores 2d4+2 HP', iconRef: 'bottle_long_red', longDesc: 'A ruby-red liquid that shimmers when shaken. Restores 2d4+2 hit points.', basePrice: 50, uses: { ...SHOP_SINGLE_USE }, onUseEffects: ['Heal 2d4+2 HP'], buffsApplyOnUse: true },

  greater_health:   { id: 'greater_health',   name: 'Greater Health Potion',   category: 'potion',     rarity: 'uncommon',  shortDesc: 'Restores 4d4+4 HP', iconRef: 'bottle_magic_red', longDesc: 'A more potent crimson elixir that glows faintly. Restores 4d4+4 HP.', basePrice: 150, uses: { ...SHOP_SINGLE_USE }, onUseEffects: ['Heal 4d4+4 HP'], buffsApplyOnUse: true },

  mana_elixir:      { id: 'mana_elixir',      name: 'Mana Elixir',            category: 'potion',     rarity: 'uncommon',  shortDesc: 'Restores 1d6+2 mana', iconRef: 'bottle_long_blue', longDesc: 'Shimmering blue-violet draught. Restores 1d6+2 mana.', basePrice: 75, uses: { ...SHOP_SINGLE_USE }, onUseEffects: ['Restore 1d6+2 mana'], buffsApplyOnUse: true },

  antidote:         { id: 'antidote',         name: 'Antidote',               category: 'potion',     rarity: 'common',    shortDesc: 'Cures poison', iconRef: 'bottle_long_green', longDesc: 'Pale green liquid. Grants poison resistance for 1hr.', basePrice: 40, uses: { ...SHOP_SINGLE_USE }, buffs: ['Poison resistance (1 hour)'], onUseEffects: ['Cure poison', 'Poison resistance (1 hour)'], buffsApplyOnUse: true },

  scroll_fireball:  { id: 'scroll_fireball',  name: 'Scroll of Fireball',     category: 'scroll',     rarity: 'rare',      shortDesc: 'Cast Fireball (8d6)', iconRef: 'scroll_emblem', longDesc: 'Aged parchment with orange-glowing arcane symbols. Single use.', basePrice: 200, uses: { ...SHOP_SINGLE_USE }, abilities: [{ id: 'cast-fireball', name: 'Cast Fireball', effects: ['+8d6 Fire DC 15 Dex save for half'], uses: { ...SHOP_SINGLE_USE } }], onUseEffects: ['Cast Fireball'], buffsApplyOnUse: true },

  scroll_heal:      { id: 'scroll_heal',      name: 'Scroll of Healing Word', category: 'scroll',     rarity: 'uncommon',  shortDesc: 'Cast Healing Word (2nd)', iconRef: 'scroll_note', basePrice: 100, uses: { ...SHOP_SINGLE_USE }, onUseEffects: ['Cast Healing Word at 2nd level'], buffsApplyOnUse: true },

  healing_herb:     { id: 'healing_herb',     name: 'Healing Herb',           category: 'ingredient', rarity: 'common',    shortDesc: 'Potion ingredient', iconRef: 'twig_yellow', basePrice: 2 },

  moonpetal:        { id: 'moonpetal',        name: 'Moonpetal Flower',       category: 'ingredient', rarity: 'rare',      shortDesc: 'Rare magical flower', iconRef: 'twig_purple', longDesc: 'Silver-white flower that blooms under a full moon.', basePrice: 25 },

  rations:          { id: 'rations',          name: 'Trail Rations',          category: 'food',       rarity: 'common',    shortDesc: '1 day of food', iconRef: 'meat_grilled', basePrice: 5, uses: { current: 1, max: 1, resetType: 'longRest' }, onUseEffects: ['Eat one day of food'], buffsApplyOnUse: true },

  iron_sword:       { id: 'iron_sword',       name: 'Iron Longsword',         category: 'weapon',     rarity: 'common',    shortDesc: '1d8 slashing, versatile', longDesc: 'Well-forged iron longsword. Versatile (1d10 two-handed).', basePrice: 15, combatBonuses: [{ type: 'weapon_damage', label: '1d8 slashing', value: 0 }], abilities: [{ id: 'guarded-strike', name: 'Guarded Strike', effects: ['+1 AC until next turn'], uses: { ...SHOP_SHORT_REST_USE } }] },

  cloak_protection: { id: 'cloak_protection', name: 'Cloak of Protection',    category: 'gear',       rarity: 'rare',      shortDesc: '+1 AC and saving throws', iconRef: 'medallion_gold', longDesc: 'Midnight blue cloak. +1 AC and saving throws. Attunement.', basePrice: 500, combatBonuses: [{ type: 'ac_bonus', value: 1, label: '+1 AC' }, { type: 'save_bonus', value: 1, label: '+1 saving throws' }] },

  phoenix_feather:  { id: 'phoenix_feather',  name: 'Phoenix Feather',        category: 'ingredient', rarity: 'legendary', shortDesc: 'Extremely rare reagent', iconRef: 'twig_red', longDesc: 'Radiates warmth. Used in powerful resurrection magic.', basePrice: 1000 },

  rope:             { id: 'rope',             name: 'Hempen Rope (50ft)',     category: 'gear',       rarity: 'common',    shortDesc: 'Standard adventuring rope', basePrice: 1, onUseEffects: ['Secure a climb, tie gear, or bind a target'] },

  chain_mail:       { id: 'chain_mail',       name: 'Chain Mail',             category: 'armor',      rarity: 'common',    shortDesc: 'AC 16, Str 13 req', basePrice: 75, combatBonuses: [{ type: 'armor_class', value: 16, label: 'AC 16' }] },

  shadow_dagger:    { id: 'shadow_dagger',    name: 'Shadow Dagger',          category: 'weapon',     rarity: 'epic',      shortDesc: '1d4+2 piercing, +2d6 sneak', iconRef: 'bottle_skull_violet', longDesc: 'Dagger wreathed in shadow. Bonus damage from stealth.', basePrice: 800, combatBonuses: [{ type: 'weapon_damage', label: '1d4+2 piercing', value: 0 }, { type: 'conditional_damage', label: '+2d6 from stealth', value: 0 }], abilities: [{ id: 'shadow-feint', name: 'Shadow Feint', effects: ['Advantage on next stealth attack'], uses: { ...SHOP_SHORT_REST_USE } }] },

  // Specialty and combat shop items

  hellfire_elixir:  { id: 'hellfire_elixir',  name: 'Hellfire Elixir',        category: 'potion',     rarity: 'rare',      shortDesc: '+3d6 fire damage next attack', iconRef: 'bottle_skull_red', longDesc: 'A bubbling crimson liquid that radiates heat. Coats a weapon in hellfire for one devastating strike.', buffs: ['+3d6 Fire (1 attack)'], basePrice: 350, uses: { ...SHOP_SINGLE_USE }, onUseEffects: ['Apply +3d6 fire damage to the next weapon hit'], buffsApplyOnUse: true },

  voidwater:        { id: 'voidwater',        name: 'Voidwater',              category: 'potion',     rarity: 'epic',      shortDesc: 'Phase through walls for 1 min', iconRef: 'bottle_long_violet', longDesc: 'Shimmering purple-black liquid from the plane of shadow. Grants ethereal movement briefly.', buffs: ['Ethereal (1 min)'], basePrice: 900, uses: { ...SHOP_SINGLE_USE }, onUseEffects: ['Ethereal movement (1 minute)'], buffsApplyOnUse: true },

  frozen_tears:     { id: 'frozen_tears',     name: 'Frozen Tears',           category: 'potion',     rarity: 'uncommon',  shortDesc: 'Cold resistance 1hr, heal 2d6', iconRef: 'bottle_ice', longDesc: 'Ice crystals suspended in pale blue liquid. Said to be the tears of a frost giant queen.', buffs: ['Cold Resist', '+2d6 HP'], basePrice: 200, uses: { ...SHOP_SINGLE_USE }, onUseEffects: ['Heal 2d6 HP', 'Cold resistance (1 hour)'], buffsApplyOnUse: true },

  shadow_essence:   { id: 'shadow_essence',   name: 'Essence of Shadow',      category: 'potion',     rarity: 'rare',      shortDesc: 'Invisibility 1 min, +2 Stealth', iconRef: 'bottle_magic_violet', longDesc: 'Distilled darkness from the Shadowfell. Grants brief invisibility and enhanced stealth.', buffs: ['Invisible (1 min)', '+2 Stealth'], basePrice: 500, uses: { ...SHOP_SINGLE_USE }, onUseEffects: ['Invisible (1 minute)', '+2 Stealth'], buffsApplyOnUse: true },

  scroll_damnation: { id: 'scroll_damnation', name: 'Scroll of Damnation',    category: 'scroll',     rarity: 'epic',      shortDesc: 'Banish one fiend permanently', iconRef: 'scroll_map1', longDesc: 'Ancient parchment inscribed in celestial script. Burns with holy fire when read aloud.', basePrice: 1200, uses: { ...SHOP_SINGLE_USE }, abilities: [{ id: 'banish-fiend', name: 'Banish Fiend', effects: ['Banish one fiend'], uses: { ...SHOP_SINGLE_USE } }], onUseEffects: ['Banish one fiend'], buffsApplyOnUse: true },

  infernal_shroom:  { id: 'infernal_shroom',  name: 'Infernal Mushroom',      category: 'ingredient', rarity: 'rare',      shortDesc: 'Alchemy: fire resistance potions', iconRef: 'mushroom_big_red', longDesc: 'Glowing red mushroom that grows near lava vents. Key ingredient in fire resistance brews.', basePrice: 75 },

  soul_ember:       { id: 'soul_ember',       name: 'Soul Ember Twig',        category: 'ingredient', rarity: 'uncommon',  shortDesc: 'Alchemy: resurrection components', iconRef: 'twig_red', longDesc: 'A twig that still smolders with spiritual energy. Used in powerful restoration magic.', basePrice: 40 },

  demon_bile:       { id: 'demon_bile',       name: "Demon's Bile",           category: 'potion',     rarity: 'uncommon',  shortDesc: 'Poison: 4d6 to undead/fiends', iconRef: 'bottle_skull_green', longDesc: 'Caustic green liquid harvested from demon corpses. Devastating against evil creatures.', buffs: ['+4d6 vs Undead/Fiends'], debuffs: ['-1 Con for 1hr'], basePrice: 180, uses: { ...SHOP_SINGLE_USE }, onUseEffects: ['Apply poison: +4d6 vs undead or fiends', '-1 Con for 1 hour'], buffsApplyOnUse: true },

  cursed_medallion: { id: 'cursed_medallion', name: 'Cursed Medallion',       category: 'gear',       rarity: 'rare',      shortDesc: '+2 AC, -1 Wis saves', iconRef: 'medallion_gold', longDesc: 'A gold medallion that whispers dark secrets. Protective but gradually erodes willpower.', buffs: ['+2 AC'], debuffs: ['-1 Wis saves'], basePrice: 600, combatBonuses: [{ type: 'ac_bonus', value: 2, label: '+2 AC' }, { type: 'save_bonus', value: -1, label: '-1 Wisdom saves', ability: 'wisdom' }] },

  hellhound_jerky:  { id: 'hellhound_jerky',  name: 'Hellhound Jerky',        category: 'food',       rarity: 'uncommon',  shortDesc: 'Fire resist 1hr, tastes awful', iconRef: 'meat_piece', longDesc: 'Dried and smoked meat from a hellhound. Grants temporary fire resistance but may cause indigestion.', buffs: ['Fire Resist (1hr)'], debuffs: ['Nausea (10 min)'], basePrice: 30, uses: { ...SHOP_SINGLE_USE }, onUseEffects: ['Fire resistance (1 hour)', 'Nausea (10 minutes)'], buffsApplyOnUse: true },

  witch_hat:        { id: 'witch_hat',        name: 'Hat of Dark Foresight',  category: 'gear',       rarity: 'rare',      shortDesc: 'Adv on Perception, darkvision 60ft', iconRef: 'magic_hat_green', longDesc: 'A pointed hat woven with enchanted thread. The wearer can see in darkness and sense danger.', buffs: ['Adv: Perception', 'Darkvision 60ft'], basePrice: 750, combatBonuses: [{ type: 'skill_advantage', skill: 'perception', label: 'Advantage on Perception' }] },

  abyssal_spore:    { id: 'abyssal_spore',    name: 'Abyssal Spore',          category: 'ingredient', rarity: 'uncommon',  shortDesc: 'Alchemy: poison & necrotic brews', iconRef: 'mushroom_small_violet', longDesc: 'Tiny violet mushroom from the Underdark. Potent in poisons and necrotic concoctions.', basePrice: 25 },

  firebrand_oil:    { id: 'firebrand_oil',    name: 'Firebrand Oil',          category: 'potion',     rarity: 'common',    shortDesc: 'Coat weapon: +1d4 fire, 3 hits', iconRef: 'bottle_round_red', longDesc: 'A flask of volatile orange oil. Applied to a weapon to add fire damage for three strikes.', buffs: ['+1d4 Fire (3 hits)'], basePrice: 50, uses: { ...SHOP_SINGLE_USE }, onUseEffects: ['Coat weapon: +1d4 fire for 3 hits'], buffsApplyOnUse: true },

  mana_draught:     { id: 'mana_draught',     name: 'Deep Mana Draught',      category: 'potion',     rarity: 'rare',      shortDesc: 'Restore one 3rd-level spell slot', iconRef: 'bottle_cylinder_blue', longDesc: 'A tall cylinder of luminous blue liquid. Restores arcane energy to a depleted spellcaster.', basePrice: 400, uses: { ...SHOP_SINGLE_USE }, onUseEffects: ['Restore one 3rd-level spell slot'], buffsApplyOnUse: true },

  neutralizer:      { id: 'neutralizer',      name: 'Universal Neutralizer',  category: 'potion',     rarity: 'uncommon',  shortDesc: 'Cures poison, disease, curse', iconRef: 'bottle_standard_yellow', longDesc: 'A bright yellow tonic brewed by master alchemists. Neutralizes almost any affliction.', buffs: ['Cure: Poison', 'Cure: Disease', 'Cure: Curse'], basePrice: 250, uses: { ...SHOP_SINGLE_USE }, onUseEffects: ['Cure poison', 'Cure disease', 'Cure curse'], buffsApplyOnUse: true },

};



const DEFAULT_SHOP_DATA = {

  shops: [

    {

      id: 'shop_example_provisioner', name: "Wayfarer's Provisioner", keeperName: 'Mira Calder',

      type: 'Food & Supplies', description: 'Reliable field supplies, basic healing, and travel gear for everyday adventuring.',

      isOpen: true, starred: true, mode: 'both', campaigns: ['general'], tags: ['Starter', 'Road'],

      inventory: [

        { itemId: 'health_potion', price: 50, stock: 8, secrecy: 'visible', tradeOnly: false },

        { itemId: 'greater_health', price: 150, stock: 3, secrecy: 'visible', tradeOnly: false },

        { itemId: 'antidote', price: 40, stock: 6, secrecy: 'visible', tradeOnly: false },

        { itemId: 'rations', price: 5, stock: 20, secrecy: 'visible', tradeOnly: false },

        { itemId: 'rope', price: 1, stock: 12, secrecy: 'visible', tradeOnly: false },

        { itemId: 'firebrand_oil', price: 50, stock: 5, secrecy: 'visible', tradeOnly: false },

      ],

      createdAt: new Date().toISOString(),

    },

    {

      id: 'shop_example_arcane', name: 'Ember & Ink', keeperName: 'Salven Quill',

      type: 'Magic Items', description: 'Scrolls, spell fuel, and protective trinkets that can be used immediately after purchase.',

      isOpen: true, starred: true, mode: 'both', campaigns: ['general'], tags: ['Arcane', 'Scrolls'],

      iconMode: 'emoji',

      inventory: [

        { itemId: 'mana_elixir', price: 75, stock: 6, secrecy: 'visible', tradeOnly: false },

        { itemId: 'scroll_heal', price: 100, stock: 4, secrecy: 'visible', tradeOnly: false },

        { itemId: 'scroll_fireball', price: 200, stock: 2, secrecy: 'visible', tradeOnly: false },

        { itemId: 'neutralizer', price: 250, stock: 3, secrecy: 'visible', tradeOnly: false },

        { itemId: 'cloak_protection', price: 500, stock: 1, secrecy: 'secret-1', tradeOnly: false },

      ],

      createdAt: new Date().toISOString(),

    },

    {

      id: 'shop_example_armory', name: 'Hearthforge Armory', keeperName: 'Borin Hearthforge',

      type: 'Weapons & Armor', description: 'Starter weapons, armor, and a few dangerous specialty consumables for harder fights.',

      isOpen: true, starred: true, mode: 'both', campaigns: ['general'], tags: ['Armory', 'Combat'],

      iconMode: 'custom',

      inventory: [

        { itemId: 'iron_sword', price: 15, stock: 5, secrecy: 'visible', tradeOnly: false },

        { itemId: 'chain_mail', price: 75, stock: 3, secrecy: 'visible', tradeOnly: false },

        { itemId: 'hellfire_elixir', price: 350, stock: 3, secrecy: 'visible', tradeOnly: false },

        { itemId: 'frozen_tears', price: 200, stock: 5, secrecy: 'visible', tradeOnly: false },

        { itemId: 'shadow_essence', price: 500, stock: 2, secrecy: 'secret-1', tradeOnly: false },

        { itemId: 'hellhound_jerky', price: 30, stock: 10, secrecy: 'visible', tradeOnly: false },

        { itemId: 'cursed_medallion', price: 600, stock: 1, secrecy: 'secret-2', tradeOnly: false },

        { itemId: 'shadow_dagger', price: 800, stock: 1, secrecy: 'secret-2', tradeOnly: false },

        { itemId: 'demon_bile', price: 180, stock: 4, secrecy: 'visible', tradeOnly: false },

        { itemId: 'witch_hat', price: 750, stock: 1, secrecy: 'secret-1', tradeOnly: false },

        { itemId: 'mana_draught', price: 400, stock: 2, secrecy: 'visible', tradeOnly: false },

        { itemId: 'voidwater', price: 900, stock: 1, secrecy: 'secret-2', tradeOnly: false },

        { itemId: 'scroll_damnation', price: 1200, stock: 1, secrecy: 'secret-3', tradeOnly: false },

        { itemId: 'infernal_shroom', price: 75, stock: 6, secrecy: 'visible', tradeOnly: false },

        { itemId: 'abyssal_spore', price: 25, stock: 8, secrecy: 'visible', tradeOnly: false },

        { itemId: 'soul_ember', price: 40, stock: 4, secrecy: 'visible', tradeOnly: false },

      ],

      createdAt: new Date().toISOString(),

    }

  ],

  itemTemplates: DEFAULT_ITEM_TEMPLATES,

  activeShopId: 'shop_example_provisioner',

  offers: [],

  transactionLog: [],

  campaigns: ['Eye Long Lost', 'Lockhood'],

  activeCampaign: 'all',

  customRarities: {},

  customCategories: [],

};



// Shop Coin component

const ShopCoin = ({ type = 'gold', size = 16 }) => {

  const colors = { gold: '#d4aa40', silver: '#a8b0b8', copper: '#b87a50' };

  return (

    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size, borderRadius: '50%', fontSize: size * 0.55, background: `radial-gradient(circle at 35% 35%, ${colors[type]}, ${colors[type]}88)`, color: '#0d0d0f', fontWeight: 700, lineHeight: 1, flexShrink: 0, boxShadow: `0 1px 3px ${colors[type]}44` }}>$</span>

  );

};



// Secrecy badge

const SecrecyBadge = ({ secrecy, small, onClick }) => {

  const s = getSecrecy(secrecy);

  if (!s.locks || s.locks === 0) return null; // visible/hidden don't show locks

  return (

    <span onClick={onClick} title={s.desc}

      style={{ display: 'inline-flex', alignItems: 'center', gap: 1, cursor: onClick ? 'pointer' : 'default', whiteSpace: 'nowrap', flexShrink: 0 }}>

      {Array.from({ length: s.locks }).map((_, i) => (

        <span key={i} style={{ fontSize: small ? '0.65rem' : '0.8rem', opacity: 0.85 }}>🔒</span>

      ))}

    </span>

  );

};



// --- GRID CARD ---

const ShopGridCard = ({ invItem, item, isDM, charGold, onBuy, onAddToCart, canAddToCart = true, onPriceChange, onStockChange, onEdit, onToggleHidden, onDelete, secrecyBadge, formatPrice, renderPrice, priceLabel = 'gp', delay = 0, cardSize = 160, sc = SC, getRarity, getCatIcon, iconMode = 'mixed' }) => {

  const [hovered, setHovered] = useState(false);

  const r = (getRarity || getShopRarity)(item.rarity);

  const inStock = invItem.stock === null || invItem.stock > 0;

  const canAfford = invItem.price <= charGold;

  const isLegendary = item.rarity === 'legendary';



  return (

    <div className={`shop-grid-card ${isLegendary ? 'legendary-card' : ''}`}

      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}

      onClick={() => isDM ? onEdit() : null}

      style={{ 

        position: 'relative', 

        background: hovered ? sc.bgCardHover : sc.bgCard, 

        border: `1px solid ${hovered ? (sc.accentGlow ? r.color + '80' : r.color + '60') : r.border + '50'}`, 

        borderRadius: 8, 

        opacity: inStock ? 1 : 0.45, 

        cursor: isDM ? 'pointer' : 'default',

        animation: `shopFadeIn 0.3s ease ${delay}ms both`, 

        '--glow-color': r.glow,

        boxShadow: hovered 

          ? `0 16px 48px rgba(0,0,0,0.6), 0 0 30px ${sc.accentGlow || 'rgba(201,162,39,0.4)'}, 0 0 60px ${sc.accentGlow || 'rgba(201,162,39,0.2)'}` 

          : '0 4px 16px rgba(0,0,0,0.4)',

        transform: hovered ? 'translateY(-4px) scale(1.02)' : 'translateY(0) scale(1)',

        transition: 'all 0.25s ease',

      }}>

      {/* Rarity glow top bar */}

      <div style={{ height: 2, background: `linear-gradient(90deg, transparent, ${r.color}80, transparent)` }} />

      {/* DM hide/show toggle — top left */}

      {isDM && <button className="shop-btn" onClick={e => { e.stopPropagation(); onToggleHidden(); }}

        style={{ position: 'absolute', top: 5, left: 6, zIndex: 5, padding: '1px 5px', background: 'rgba(0,0,0,0.5)', border: `1px solid ${(invItem.secrecy || 'visible') === 'hidden' ? '#5a3a3a' : '#3a5a3a'}`, borderRadius: 4, fontSize: '0.6rem', color: (invItem.secrecy || 'visible') === 'hidden' ? '#b86a6a' : '#6ab86a', letterSpacing: 0.3 }}

        title={(invItem.secrecy || 'visible') === 'hidden' ? 'Click to show to players' : 'Click to hide from players'}>

        {(invItem.secrecy || 'visible') === 'hidden' ? '🔒' : '✓'}

      </button>}

      {/* DM remove from shop — top right */}

      {isDM && <button className="shop-btn" onClick={e => { e.stopPropagation(); if (window.confirm(`Remove "${item.name}" from this shop?`)) onDelete(); }}

        style={{ position: 'absolute', top: 5, right: 6, zIndex: 5, padding: '1px 5px', background: 'rgba(0,0,0,0.6)', border: '1px solid #5a2a2a', borderRadius: 4, fontSize: '0.6rem', color: '#b86a6a', opacity: 0.7 }}

        title="Remove from shop">✕</button>}

      {/* Player add-to-cart — top left */}

      {!isDM && <button className="shop-cart-add-btn" onClick={e => { e.stopPropagation(); inStock && canAddToCart && onAddToCart(); }} disabled={!inStock || !canAddToCart}

        style={{ position: 'absolute', top: 5, left: 6, zIndex: 5, padding: '2px 7px', fontSize: '0.7rem' }}

        title="Add to cart">+</button>}

      {/* Icon area */}

      <div style={{ height: cardSize <= 140 ? 55 : cardSize <= 180 ? 80 : 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `radial-gradient(ellipse at center, ${r.glow}, transparent 70%)`, fontSize: cardSize <= 140 ? 26 : cardSize <= 180 ? 36 : 44, position: 'relative' }}>

        {iconMode !== 'emoji' && item.iconRef && getIconByKey(item.iconRef) ? <RpgIcon iconKey={item.iconRef} size={cardSize <= 140 ? 56 : cardSize <= 180 ? 72 : 90} /> : iconMode === 'custom' && DEFAULT_CATEGORY_ICONS[item.category] && getIconByKey(DEFAULT_CATEGORY_ICONS[item.category]) ? <RpgIcon iconKey={DEFAULT_CATEGORY_ICONS[item.category]} size={cardSize <= 140 ? 56 : cardSize <= 180 ? 72 : 90} /> : (getCatIcon || getShopCatIcon)(item.category)}

        {/* Stock count — player view only */}

        {!isDM && <div style={{ position: 'absolute', top: 6, right: 8, fontSize: '0.65rem', color: invItem.stock !== null && invItem.stock <= 2 ? '#c87a5a' : sc.textSub, background: sc.bgBar, padding: '1px 6px', borderRadius: 10, border: `1px solid ${invItem.stock !== null && invItem.stock <= 2 ? '#5a3020' : sc.borderMed}` }}>

          {invItem.stock === null ? '∞' : invItem.stock === 0 ? 'Sold Out' : `×${invItem.stock}`}

        </div>}

      </div>

      {/* Info */}

      <div style={{ padding: '8px 10px 10px' }}>

        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: r.color !== '#9a9a8a' ? r.color : sc.textHead, marginBottom: 2, lineHeight: 1.2, display: 'flex', alignItems: 'center', gap: 6 }}>

          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{item.name}</span>

          {secrecyBadge}

        </div>

        <div style={{ fontSize: '0.72rem', color: '#6a6a5e', marginBottom: 8, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.shortDesc}</div>

        {/* Price + action row */}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

          {invItem.tradeOnly ? (

            <div style={{ fontSize: '0.72rem', color: '#b8a060', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 4 }}>⚖️ Trade Only</div>

          ) : isDM ? (

            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', flex: 1, minWidth: 0 }} onClick={e => e.stopPropagation()}>

              {renderPrice ? renderPrice(invItem.price) : <span style={{ color: sc.gold, fontSize: '0.78rem' }}>{formatPrice ? formatPrice(invItem.price) : invItem.price}</span>}

            </div>

          ) : (

            <div style={{ display: 'flex', alignItems: 'center', gap: 3, opacity: canAfford ? 1 : 0.5 }}>

              {renderPrice ? renderPrice(invItem.price) : <><ShopCoin size={13} /><span style={{ fontSize: '0.9rem', fontWeight: 600, color: canAfford ? sc.gold : '#8a5a4a' }}>{formatPrice ? formatPrice(invItem.price) : invItem.price}</span></>}

            </div>

          )}

          {isDM && !invItem.tradeOnly && (

            <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }} onClick={e => e.stopPropagation()}>

              <span style={{ fontSize: '0.55rem', color: sc.textDim }}>×</span>

              <input type="number" value={invItem.stock === null ? '' : invItem.stock} onChange={e => { e.stopPropagation(); onStockChange(e.target.value === '' ? null : e.target.value); }} onClick={e => e.stopPropagation()} className="shop-input"

                placeholder="∞" style={{ width: 36, background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '3px 4px', color: sc.textSub, fontSize: '0.75rem', textAlign: 'center' }} title="Stock (empty=∞)" />

            </div>

          )}

          {!isDM && (

            <div style={{ display: 'flex', gap: 3 }} onClick={e => e.stopPropagation()}>

              {invItem.tradeOnly ? (

                <button className="shop-btn" onClick={() => onBuy()} disabled={!inStock}

                  style={{ padding: '3px 10px', fontSize: '0.72rem', background: 'rgba(201,162,39,0.08)', border: '1px solid rgba(201,162,39,0.2)', borderRadius: 4, color: '#b8a060', fontWeight: 500 }}>⚖️ Offer</button>

              ) : (

                <button className="shop-buy-btn" onClick={() => onBuy()} disabled={!inStock || !canAfford}

                  style={{ padding: '3px 10px', fontSize: '0.72rem' }}>Buy</button>

              )}

            </div>

          )}

        </div>

      </div>

      {/* Hover tooltip */}

      {hovered && (item.longDesc || item.buffs?.length > 0 || item.debuffs?.length > 0 || item.abilities?.length > 0 || item.onUseEffects?.length > 0) && (

        <div style={{ position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: 6, width: 260, background: '#111115', border: `1px solid ${r.border}`, borderRadius: 6, padding: 14, boxShadow: `0 8px 32px rgba(0,0,0,0.7), 0 0 12px ${r.glow}`, zIndex: 2000, pointerEvents: 'none', animation: 'shopFadeIn 0.15s ease' }}>

          <div style={{ fontSize: '1rem', fontWeight: 600, color: r.color, marginBottom: 4 }}>{item.name}</div>

          <div style={{ fontSize: '0.8rem', color: '#8a8a7a', marginBottom: 6 }}>{item.shortDesc}</div>

          {item.longDesc && <div style={{ fontSize: '0.8rem', color: '#6a6a5e', lineHeight: 1.5, borderTop: '1px solid #2a2a2e', paddingTop: 6, marginBottom: 6 }}>{item.longDesc}</div>}

          {(item.buffs?.length > 0 || item.debuffs?.length > 0) && (

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>

              {(item.buffs || []).map((b, i) => <span key={`b${i}`} style={{ fontSize: '0.65rem', padding: '1px 6px', background: 'rgba(80,180,80,0.15)', border: '1px solid rgba(80,180,80,0.3)', borderRadius: 8, color: '#6cc06c' }}>{b}</span>)}

              {(item.debuffs || []).map((d, i) => <span key={`d${i}`} style={{ fontSize: '0.65rem', padding: '1px 6px', background: 'rgba(180,80,80,0.15)', border: '1px solid rgba(180,80,80,0.3)', borderRadius: 8, color: '#c06c6c' }}>{d}</span>)}

            </div>

          )}

          {item.onUseEffects?.length > 0 && (

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>

              {item.onUseEffects.map((e, i) => <span key={i} style={{ fontSize: '0.65rem', padding: '1px 6px', background: 'rgba(39,201,90,0.12)', border: '1px solid rgba(39,201,90,0.3)', borderRadius: 8, color: '#27c95a' }}>{e}</span>)}

            </div>

          )}

          {item.abilities?.length > 0 && (

            <div style={{ borderTop: '1px solid #2a2a2e', paddingTop: 6 }}>

              {item.abilities.map(a => (

                <div key={a.id} style={{ marginBottom: 4 }}>

                  <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#5a9fd4' }}>{a.name}{a.uses ? ` (${a.uses.current}/${a.uses.max})` : ''}</div>

                  {a.description && <div style={{ fontSize: '0.68rem', color: '#6a6a5e' }}>{a.description}</div>}

                  {a.effects?.length > 0 && <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginTop: 2 }}>{a.effects.map((e, i) => <span key={i} style={{ fontSize: '0.6rem', padding: '0 4px', background: 'rgba(90,159,212,0.12)', border: '1px solid rgba(90,159,212,0.3)', borderRadius: 6, color: '#5a9fd4' }}>{e}</span>)}</div>}

                </div>

              ))}

            </div>

          )}

        </div>

      )}

    </div>

  );

};



// --- LIST ROW ---

const ShopListRow = ({ invItem, item, isDM, charGold, onBuy, onAddToCart, canAddToCart = true, onPriceChange, onStockChange, onEdit, onToggleHidden, onDelete, secrecyBadge, formatPrice, renderPrice, priceLabel = 'gp', sc = SC, getRarity, getCatIcon, iconMode = 'mixed' }) => {

  const [expanded, setExpanded] = useState(false);

  const r = (getRarity || getShopRarity)(item.rarity);

  const inStock = invItem.stock === null || invItem.stock > 0;

  const canAfford = invItem.price <= charGold;



  return (

    <>

      <div className="shop-list-row" onClick={() => isDM ? onEdit() : (item.longDesc && setExpanded(!expanded))}

        style={{ display: 'grid', gridTemplateColumns: isDM ? '1fr auto auto auto auto' : '1fr auto auto auto', gap: 16, alignItems: 'center', padding: '10px 14px', background: sc.bgDark, borderLeft: `3px solid ${r.color}40`, cursor: isDM ? 'pointer' : (item.longDesc ? 'pointer' : 'default'), opacity: inStock ? 1 : 0.45 }}>

        <div>

          <div style={{ fontWeight: 500, color: r.color !== '#9a9a8a' ? r.color : sc.textHead, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 6 }}>

            {iconMode !== 'emoji' && item.iconRef && getIconByKey(item.iconRef) ? <RpgIcon iconKey={item.iconRef} size={20} /> : (getCatIcon || getShopCatIcon)(item.category)} <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{item.name}</span>

            {secrecyBadge}

            {!isDM && item.longDesc && <span style={{ fontSize: '0.65rem', color: sc.textDim, flexShrink: 0 }}>{expanded ? '▾' : '▸'}</span>}

          </div>

          <div style={{ fontSize: '0.8rem', color: '#6a6a5a', marginTop: 1 }}>{item.shortDesc}</div>

        </div>

        <div style={{ fontSize: '0.8rem', color: '#6a6a5a', minWidth: 50, textAlign: 'center' }}>

          {isDM ? (

            <input type="number" value={invItem.stock === null ? '' : invItem.stock} 

              onChange={e => { e.stopPropagation(); onStockChange(e.target.value === '' ? null : e.target.value); }}

              onClick={e => e.stopPropagation()} className="shop-input" placeholder="∞"

              style={{ width: 40, background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 3, padding: '3px 4px', color: sc.textSub, fontSize: '0.8rem', textAlign: 'center' }} title="Stock (empty=∞)" />

          ) : (

            invItem.stock === null ? '∞' : invItem.stock

          )}

        </div>

        <div style={{ minWidth: 60, textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3 }}>

          {invItem.tradeOnly ? (

            <span style={{ fontSize: '0.75rem', color: '#b8a060', fontStyle: 'italic' }}>⚖️ Trade Only</span>

          ) : isDM ? (

            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>

              {renderPrice ? renderPrice(invItem.price) : <span style={{ color: sc.gold, fontSize: '0.85rem' }}>{formatPrice ? formatPrice(invItem.price) : invItem.price}</span>}

            </div>

          ) : (

            <>{renderPrice ? renderPrice(invItem.price) : <><ShopCoin size={12} /><span style={{ fontSize: '0.95rem', fontWeight: 500, color: canAfford ? sc.gold : '#8a5a4a' }}>{formatPrice ? formatPrice(invItem.price) : invItem.price}</span></>}</>

          )}

        </div>

        {isDM && (

          <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>

            <button className="shop-btn" onClick={onToggleHidden}

              style={{ padding: '3px 6px', background: 'none', border: `1px solid ${sc.borderMed}`, borderRadius: 3, fontSize: '0.65rem', color: (invItem.secrecy || 'visible') === 'hidden' ? '#b86a6a' : '#6ab86a' }}

              title={(invItem.secrecy || 'visible') === 'hidden' ? 'Click to show to players' : 'Click to hide from players'}>

              {(invItem.secrecy || 'visible') === 'hidden' ? '🔒 Hidden' : '✓ Shown'}

            </button>

            <button className="shop-btn" onClick={() => { if (window.confirm(`Remove "${item.name}" from this shop?`)) onDelete(); }}

              style={{ padding: '3px 6px', background: 'none', border: `1px solid #4a2a2a`, borderRadius: 3, fontSize: '0.65rem', color: '#b86a6a' }}

              title="Remove from shop">✕</button>

          </div>

        )}

        {!isDM && (

          <div style={{ display: 'flex', gap: 6, minWidth: 80, justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>

            <button className="shop-cart-add-btn" onClick={() => inStock && canAddToCart && onAddToCart()} disabled={!inStock || !canAddToCart}

              style={{ padding: '4px 8px', fontSize: '0.78rem' }}>+</button>

            {invItem.tradeOnly ? (

              <button className="shop-btn" onClick={() => onBuy()} disabled={!inStock}

                style={{ padding: '4px 10px', fontSize: '0.78rem', background: 'rgba(201,162,39,0.08)', border: '1px solid rgba(201,162,39,0.2)', borderRadius: 4, color: '#b8a060', fontWeight: 500 }}>⚖️ Offer</button>

            ) : (

              <button className="shop-buy-btn" onClick={() => onBuy()} disabled={!inStock || !canAfford}

                style={{ padding: '4px 10px', fontSize: '0.78rem' }}>Buy</button>

            )}

          </div>

        )}

      </div>

      {expanded && item.longDesc && (

        <div style={{ padding: '10px 14px 12px 20px', background: sc.bgDark, borderLeft: `3px solid ${r.color}25`, fontSize: '0.85rem', color: sc.textSubLight, lineHeight: 1.6, animation: 'shopFadeIn 0.15s ease' }}>{item.longDesc}</div>

      )}

    </>

  );

};



// --- MAIN SHOP PAGE ---

export const ShopPage = ({
  char,
  isDM,
  shopData,
  onSaveShopData,
  onUpdateChar,
  onPurchaseShopListing = null,
  onCheckoutShopCart = null,
  onSellInventoryItemToShopForDm = null,
  onSubmitTradeOffer = null,
  onCancelTradeOffer = null,
  onDenyTradeOfferForDm = null,
  onSettleTradeOfferForDm = null,
  onPromoteTradeReceiptLineToShopListingForDm = null,
  onAdjustShopWalletForDm = null,
  globalSettings,
  allChars = {},
  currentCharId,
  onBack,
  appTheme,
}) => {

  // Shop theme toggle

  const [shopUseAppTheme, setShopUseAppTheme] = useState(() => {

    return true;

  });

  const toggleShopTheme = () => {

    const next = !shopUseAppTheme;

    setShopUseAppTheme(next);

    

  };

  // sc (shop colors) is computed below, after active shop is derived — see "Per-shop theme resolution"

  useEffect(injectShopStyles, []);



  // Core state

  const [dmTab, setDmTab] = useState('shop'); // 'shop' | 'manager'

  const [displayMode, setDisplayMode] = useState('grid');

  const [filter, setFilter] = useState('all');

  const [search, setSearch] = useState('');

  const [previewAsPlayer, setPreviewAsPlayer] = useState(false);

  const [cardSize, setCardSize] = useState(() => {

    return 160;

  });

  const cycleCardSize = () => {

    const sizes = [120, 140, 160, 200, 240];

    const idx = sizes.indexOf(cardSize);

    const next = sizes[(idx + 1) % sizes.length];

    setCardSize(next);

    

  };



  // Shop manager state

  const [shopTagFilter, setShopTagFilter] = useState('');

  const [shopSearch, setShopSearch] = useState('');

  const [shopTagSearch, setShopTagSearch] = useState('');

  const [shopTagSearchFocused, setShopTagSearchFocused] = useState(false);

  const [editingTagsShopId, setEditingTagsShopId] = useState(null);

  const [addingTagShopId, setAddingTagShopId] = useState(null);

  const [newTagInput, setNewTagInput] = useState('');

  const [renamingTag, setRenamingTag] = useState(null); // { shopId, oldTag, newTag }

  const [showAllShopsInView, setShowAllShopsInView] = useState(false);

  const [showShopNotes, setShowShopNotes] = useState(false);

  const [shopNotesText, setShopNotesText] = useState('');

  const [dragShopId, setDragShopId] = useState(null);

  const [dragOverShopId, setDragOverShopId] = useState(null);



  // Cart & purchase

  const [cart, setCart] = useState([]);
  const purchaseAuthorityInFlightRef = useRef(false);
  const checkoutAuthorityInFlightRef = useRef(false);
  const tradeOfferSubmitInFlightRef = useRef(false);
  const tradeOfferCancelInFlightRef = useRef(false);
  const tradeOfferDenyInFlightRef = useRef(false);
  const tradeOfferSettleInFlightRef = useRef(false);
  const tradeReceiptPromotionInFlightRef = useRef(false);
  const shopWalletAdjustmentInFlightRef = useRef(false);
  const shopSellBackInFlightRef = useRef(false);

  const [showCart, setShowCart] = useState(false);

  const [cartQtyDrafts, setCartQtyDrafts] = useState({});

  const [purchaseModal, setPurchaseModal] = useState(null);

  const [purchaseQty, setPurchaseQty] = useState(1);

  const [purchaseQtyDraft, setPurchaseQtyDraft] = useState('1');

  // Trade

  const [showOfferModal, setShowOfferModal] = useState(false);

  const [tradeGiving, setTradeGiving] = useState({ items: [], gold: 0 });

  const [tradeWanting, setTradeWanting] = useState({ items: [], gold: 0 });

  const [tradeNote, setTradeNote] = useState('');
  const [tradeReceiptPromotionDrafts, setTradeReceiptPromotionDrafts] = useState({});
  const [shopWalletAdjustmentDrafts, setShopWalletAdjustmentDrafts] = useState({});
  const [shopWalletAdjustmentReasonDrafts, setShopWalletAdjustmentReasonDrafts] = useState({});
  const [shopSellBackDrafts, setShopSellBackDrafts] = useState({});



  // Library & Create shop

  const [showLibrary, setShowLibrary] = useState(false);

  const [libFilter, setLibFilter] = useState('all');

  const [libSearch, setLibSearch] = useState('');

  const [libStockValues, setLibStockValues] = useState({});

  const [showCreateShop, setShowCreateShop] = useState(false);

  const [newShopName, setNewShopName] = useState('');

  const [newShopKeeper, setNewShopKeeper] = useState('');

  const [newShopType, setNewShopType] = useState('General');

  const [newShopDesc, setNewShopDesc] = useState('');

  const [newShopTags, setNewShopTags] = useState('');

  const [newShopMode, setNewShopMode] = useState('coin'); // coin | trade | both

  const [newShopCampaigns, setNewShopCampaigns] = useState([]);

  const [newShopTheme, setNewShopTheme] = useState(''); // '' = use global default



  // Edit item - stores { templateDraft, invDraft, isNew } for full editing

  const [editingItem, setEditingItem] = useState(null);

  const [editUsesUnlocked, setEditUsesUnlocked] = useState(false);



  const saveTemplate = (templateId, updates) => {

    const newTemplates = { ...templates, [templateId]: { ...templates[templateId], ...updates } };

    onSaveShopData({ ...shopData, itemTemplates: newTemplates });

  };



  // Remember last-used price/tradeOnly for new item creation

  const [lastItemDefaults, setLastItemDefaults] = useState({ price: 10, tradeOnly: false });



  const openItemEditor = (lineKey) => {

    const inv = findShopInventoryLineByKey(lineKey) || shop?.inventory.find(i => i.itemId === lineKey);

    const templateKey = getShopTemplateKeyForInventoryLine(inv);

    const tmpl = getRawShopTemplateForInventoryLine(inv);

    if (!tmpl || !inv) return;

    // Store raw strings so user can type commas/spaces freely

    const draft = { ...tmpl,

      _buffsRaw: (tmpl.buffs || []).join(', '),

      _debuffsRaw: (tmpl.debuffs || []).join(', '),

      _onUseEffectsRaw: (tmpl.onUseEffects || []).join(', '),

      _tagsRaw: (tmpl.tags || []).join(', '),

      combatBonuses: normalizeShopCombatBonuses(tmpl.combatBonuses),

      dmNotes: tmpl.dmNotes || '',

      abilities: (tmpl.abilities || []).map(a => ({ ...a, _effectsRaw: (a.effects || []).join(', '), uses: a.uses ? { ...a.uses } : null })),

    };

    setEditingItem({
      templateDraft: draft,
      invDraft: { ...inv },
      isNew: false,
      originalId: inv.itemId,
      originalLineKey: getShopInventoryLineKey(inv, shop?.inventory?.indexOf(inv)),
      originalTemplateKey: templateKey || inv.itemId,
    });

    setEditUsesUnlocked(false);

  };



  const openNewItemEditor = (fromTemplateId) => {

    const id = `custom_${Date.now()}`;

    let templateDraft, invDraft;

    const sourceLine = fromTemplateId
      ? (findShopInventoryLineByKey(fromTemplateId) || shop?.inventory.find(i => i.itemId === fromTemplateId))
      : null;
    const sourceTemplateKey = sourceLine ? getShopTemplateKeyForInventoryLine(sourceLine) : fromTemplateId;

    if (sourceTemplateKey && templates[sourceTemplateKey]) {

      // Clone from existing template

      const src = templates[sourceTemplateKey];

      const srcInv = sourceLine || shop?.inventory.find(i => i.itemId === fromTemplateId);

      templateDraft = { ...src, id, name: `${src.name} (copy)`,

        _buffsRaw: (src.buffs || []).join(', '),

        _debuffsRaw: (src.debuffs || []).join(', '),

        _onUseEffectsRaw: (src.onUseEffects || []).join(', '),

        _tagsRaw: (src.tags || []).join(', '),

        combatBonuses: normalizeShopCombatBonuses(src.combatBonuses),

        dmNotes: src.dmNotes || '',

        abilities: (src.abilities || []).map(a => ({ ...a, id: `${a.id}_${Date.now()}`, _effectsRaw: (a.effects || []).join(', '), uses: a.uses ? { ...a.uses } : null })),

        uses: src.uses ? { ...src.uses } : null,

      };

      invDraft = { itemId: id, price: srcInv?.price ?? lastItemDefaults.price, stock: srcInv?.stock ?? 5, secrecy: srcInv?.secrecy || 'visible', tradeOnly: srcInv?.tradeOnly ?? lastItemDefaults.tradeOnly };

    } else {

      templateDraft = { id, name: '', category: 'misc', rarity: 'common', shortDesc: '', longDesc: '', basePrice: 10,

        buffs: [], debuffs: [], _buffsRaw: '', _debuffsRaw: '',

        abilities: [], onUseEffects: [], _onUseEffectsRaw: '',

        uses: null, buffsApplyOnUse: false, notes: '', dmNotes: '',

        tags: [], _tagsRaw: '', combatBonuses: [] };

      const shopMode = shop?.mode || 'coin';

      const defaultTradeOnly = shopMode === 'trade' ? true : (shopMode === 'both' ? lastItemDefaults.tradeOnly : lastItemDefaults.tradeOnly);

      invDraft = { itemId: id, price: lastItemDefaults.price, stock: 5, secrecy: 'visible', tradeOnly: defaultTradeOnly };

    }

    setEditingItem({ templateDraft, invDraft, isNew: true, originalId: id });

    setEditUsesUnlocked(false);

  };



  const duplicateItem = (lineKey) => {

    openNewItemEditor(lineKey);

    showToast('Duplicated — edit and save');

  };



  const parseCommaSep = (raw) => (raw || '').split(',').map(s => s.trim()).filter(Boolean);

  const cloneShopDraftValue = (value) => {
    if (Array.isArray(value)) return value.map(cloneShopDraftValue);
    if (value && typeof value === 'object') {
      return Object.fromEntries(Object.entries(value).map(([key, nested]) => [key, cloneShopDraftValue(nested)]));
    }
    return value;
  };

  const normalizeShopCombatBonuses = (bonuses) => (
    Array.isArray(bonuses) ? bonuses.map(cloneShopDraftValue) : []
  );

  const hasPartiallySupportedShopCombatBonuses = (bonuses) => (
    hasPartiallySupportedItemCombatBonuses(normalizeShopCombatBonuses(bonuses))
  );



  const saveItemEdit = () => {

    if (!editingItem) return;

    const { templateDraft: td, invDraft, isNew, originalId, originalLineKey, originalTemplateKey } = editingItem;

    if (!td.name?.trim()) { showToast('Item needs a name', 'error'); return; }

    // Parse raw strings back to arrays

    const cleaned = { ...td,

      buffs: parseCommaSep(td._buffsRaw),

      debuffs: parseCommaSep(td._debuffsRaw),

      onUseEffects: parseCommaSep(td._onUseEffectsRaw),

      tags: parseCommaSep(td._tagsRaw),

      combatBonuses: normalizeShopCombatBonuses(td.combatBonuses),

      basePrice: invDraft.price,

      tradeOnly: invDraft.tradeOnly === true,

      abilities: (td.abilities || []).map(a => {

        const { _effectsRaw, ...rest } = a;

        return { ...rest, effects: parseCommaSep(_effectsRaw) };

      }),

    };

    // Remove raw fields before saving

    delete cleaned._buffsRaw; delete cleaned._debuffsRaw; delete cleaned._onUseEffectsRaw; delete cleaned._tagsRaw;

    // Save template

    const templateSaveKey = originalTemplateKey || originalId;

    const newTemplates = { ...templates, [templateSaveKey]: cleaned };

    // Save inventory

    let newShops;

    if (isNew) {

      const newInv = [...(shop?.inventory || []), invDraft];

      newShops = shops.map(s => s.id === shop.id ? { ...s, inventory: newInv } : s);

    } else {

      const newInv = shop.inventory.map((i, index) => (
        getShopInventoryLineKey(i, index) === originalLineKey ? invDraft : i
      ));

      newShops = shops.map(s => s.id === shop.id ? { ...s, inventory: newInv } : s);

    }

    onSaveShopData({ ...shopData, itemTemplates: newTemplates, shops: newShops });

    // Remember defaults for next creation

    setLastItemDefaults({ price: invDraft.price, tradeOnly: invDraft.tradeOnly || false });

    showToast(isNew ? `Created "${td.name}"` : `Saved "${td.name}"`);

    setEditingItem(null);

  };



  const updateTemplateDraft = (updates) => setEditingItem(prev => prev ? { ...prev, templateDraft: { ...prev.templateDraft, ...updates } } : null);

  const updateInvDraft = (updates) => setEditingItem(prev => prev ? { ...prev, invDraft: { ...prev.invDraft, ...updates } } : null);

  const addCombatBonusToShopItem = (label) => {
    const bonus = COMBAT_BONUS_TYPES.find(option => option.label === label);
    if (!bonus || !editingItem) return;
    updateTemplateDraft({
      combatBonuses: [...normalizeShopCombatBonuses(editingItem.templateDraft.combatBonuses), cloneShopDraftValue(bonus)],
    });
  };

  const removeCombatBonusFromShopItem = (indexToRemove) => {
    if (!editingItem) return;
    updateTemplateDraft({
      combatBonuses: normalizeShopCombatBonuses(editingItem.templateDraft.combatBonuses)
        .filter((_, index) => index !== indexToRemove),
    });
  };



  // Ability management for shop item editing

  const addAbilityToShopItem = () => {

    const newAbility = { ...createAbility('New Ability', '', [], null, 'shortRest'), _effectsRaw: '' };

    updateTemplateDraft({ abilities: [...(editingItem.templateDraft.abilities || []), newAbility] });

  };

  const updateAbilityInShopItem = (abilityId, updates) => {

    updateTemplateDraft({ abilities: (editingItem.templateDraft.abilities || []).map(a => a.id === abilityId ? { ...a, ...updates } : a) });

  };

  const removeAbilityFromShopItem = (abilityId) => {

    updateTemplateDraft({ abilities: (editingItem.templateDraft.abilities || []).filter(a => a.id !== abilityId) });

  };



  // Toast

  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => setToast({ message, type, key: Date.now() });

  useEffect(() => { if (toast) { const t = setTimeout(() => setToast(null), 2500); return () => clearTimeout(t); } }, [toast]);



  const shops = shopData?.shops || [];

  const templates = shopData?.itemTemplates || DEFAULT_ITEM_TEMPLATES;
  const templateDisplayCatalog = useMemo(
    () => buildShopTemplateDisplayMap(templates),
    [templates],
  );

  const activeShopId = shopData?.activeShopId;

  const offers = shopData?.offers || [];

  const log = shopData?.transactionLog || [];



  // Campaign system

  const campaigns = shopData?.campaigns || [];

  const activeCampaign = shopData?.activeCampaign || 'all';

  const setActiveCampaign = (c) => onSaveShopData({ ...shopData, activeCampaign: c });

  const addCampaign = (name) => { if (!name.trim() || campaigns.includes(name.trim())) return; onSaveShopData({ ...shopData, campaigns: [...campaigns, name.trim()] }); };

  const removeCampaign = (name) => { onSaveShopData({ ...shopData, campaigns: campaigns.filter(c => c !== name), activeCampaign: activeCampaign === name ? 'all' : activeCampaign }); };

  const renameCampaign = (old, next) => { if (!next.trim() || campaigns.includes(next.trim())) return; onSaveShopData({ ...shopData, campaigns: campaigns.map(c => c === old ? next.trim() : c), activeCampaign: activeCampaign === old ? next.trim() : activeCampaign, shops: shops.map(s => ({ ...s, campaigns: (s.campaigns || []).map(c => c === old ? next.trim() : c) })) }); };



  // Merged rarities & categories (customizable)

  const customRarities = shopData?.customRarities || {};

  const customCategories = shopData?.customCategories || [];

  const mergedRarities = { ...SHOP_RARITIES };

  Object.entries(customRarities).forEach(([k, v]) => { if (mergedRarities[k]) mergedRarities[k] = { ...mergedRarities[k], ...v }; else mergedRarities[k] = v; });

  const mergedCategories = [...SHOP_CATEGORIES, ...customCategories.filter(cc => !SHOP_CATEGORIES.find(c => c.id === cc.id))];

  const mergedCatIcons = { ...SHOP_CAT_ICONS };

  customCategories.forEach(cc => { mergedCatIcons[cc.id] = cc.icon; });

  const getRarity = (id) => mergedRarities[id] || mergedRarities.common || SHOP_RARITIES.common;

  const getCatIcon = (id) => mergedCatIcons[id] || '📦';



  // Shop settings state

  const [showShopSettings, setShowShopSettings] = useState(false);

  const [settingsTab, setSettingsTab] = useState('general'); // 'general' | 'rarities' | 'categories' | 'campaigns'

  const [newCampaignName, setNewCampaignName] = useState('');

  const [newCategoryDraft, setNewCategoryDraft] = useState({ id: '', name: '', icon: '📦' });

  const [editingCampaign, setEditingCampaign] = useState(null);



  const isShopVisibleToPlayer = (s) => {

    if (!s.isOpen || !s.starred) return false;

    if (!s.visibleTo || s.visibleTo.length === 0) return true;

    return s.visibleTo.includes(currentCharId);

  };

  const shopInCampaign = (s) => {

    if (activeCampaign === 'all') return true;

    const sc2 = s.campaigns || [];

    if (sc2.length === 0) return false; // unassigned shops only show in 'all'

    return sc2.includes('general') || sc2.includes(activeCampaign);

  };

  const shop = shops.find(s => s.id === activeShopId) || (isDM ? shops.find(s => s.starred && shopInCampaign(s)) || shops.find(s => shopInCampaign(s)) || shops[0] : shops.find(s => isShopVisibleToPlayer(s) && shopInCampaign(s)) || null);



  // --- Per-shop theme resolution ---

  // Priority: shop.themeId override > global shopUseAppTheme toggle > shop default dark theme

  const sc = useMemo(() => {

    if (shop?.themeId && THEMES[shop.themeId]) {

      return deriveShopColors(THEMES[shop.themeId]);

    }

    return shopUseAppTheme && appTheme ? deriveShopColors(appTheme) : SC;

  }, [shop?.themeId, shopUseAppTheme, appTheme]);



  const shopVisible = isDM || (shop && isShopVisibleToPlayer(shop));

  const effectiveIsDM = isDM && !previewAsPlayer;

  const getShopInventoryLineKey = useCallback((inventoryItem, index = null) => String(
    inventoryItem?.listingId
    || inventoryItem?.id
    || inventoryItem?.itemId
    || (index !== null && index !== undefined ? `row:${index}` : '')
  ), []);

  const getCartLineKey = useCallback((cartItem) => String(
    cartItem?.lineKey
    || cartItem?.listingId
    || cartItem?.itemId
    || ''
  ), []);

  const findShopInventoryLineByKey = useCallback((lineKey) => {
    const normalizedKey = String(lineKey || '');
    if (!normalizedKey) return null;
    return (shop?.inventory || [])
      .map((inventoryItem, index) => ({
        inventoryItem,
        lineKey: getShopInventoryLineKey(inventoryItem, index),
      }))
      .find((entry) => entry.lineKey === normalizedKey)?.inventoryItem || null;
  }, [getShopInventoryLineKey, shop?.inventory]);

  const getShopTemplateKeyForInventoryLine = useCallback((inventoryItem) => String(
    inventoryItem?.templateKey
    || inventoryItem?.listingId
    || inventoryItem?.itemId
    || ''
  ), []);

  const getRawShopTemplateForInventoryLine = useCallback((inventoryItem) => (
    templates[getShopTemplateKeyForInventoryLine(inventoryItem)]
    || templates[inventoryItem?.itemId]
    || null
  ), [getShopTemplateKeyForInventoryLine, templates]);

  const getShopTemplateForInventoryLine = useCallback((inventoryItem) => (
    templateDisplayCatalog[getShopTemplateKeyForInventoryLine(inventoryItem)]
    || templateDisplayCatalog[inventoryItem?.itemId]
    || null
  ), [getShopTemplateKeyForInventoryLine, templateDisplayCatalog]);



  const wallet = char?.wallet || {};

  const tiers = (globalSettings?.currencyTiers || DEFAULT_SETTINGS.currencyTiers);

  const charGoldBase = walletToBase(wallet, tiers);

  const goldMultiplier = (() => { try { return currencyToBase(1, 'gold', tiers); } catch { return 100; } })();

  const charInventory = char?.inventory || [];

  const clampPurchaseQuantity = (nextValue, maxStock, fallbackValue = 1) => {

    const parsedValue = parseInt(String(nextValue ?? '').trim(), 10);

    const normalizedValue = Number.isFinite(parsedValue) ? parsedValue : fallbackValue;

    return Math.max(1, Math.min(maxStock, normalizedValue));

  };

  const commitPurchaseQuantity = (nextQuantity) => {

    setPurchaseQty(nextQuantity);

    setPurchaseQtyDraft(String(nextQuantity));

  };

  const openPurchaseModalForLine = (lineKey) => {

    const inventoryItem = findShopInventoryLineByKey(lineKey);
    if (inventoryItem?.tradeOnly) {
      setShowOfferModal(true);
      return;
    }

    setPurchaseModal(lineKey);

    setPurchaseQty(1);

    setPurchaseQtyDraft('1');

  };

  const closePurchaseModal = () => {

    setPurchaseModal(null);

    setPurchaseQty(1);

    setPurchaseQtyDraft('1');

  };

  const getCartStockLimit = useCallback((lineKey) => {

    const inventoryItem = findShopInventoryLineByKey(lineKey);

    if (!inventoryItem) return 0;

    if (inventoryItem.tradeOnly) return 0;

    return inventoryItem.stock === null ? null : Math.max(0, inventoryItem.stock);

  }, [findShopInventoryLineByKey]);

  const resolveCartQuantity = useCallback((itemId, nextValue, fallbackValue = 1, minValue = 1) => {

    const maxStock = getCartStockLimit(itemId);

    const parsedValue = parseInt(String(nextValue ?? '').trim(), 10);

    const normalizedValue = Number.isFinite(parsedValue) ? parsedValue : fallbackValue;

    if (maxStock === 0) return 0;

    const lowerBoundedValue = Math.max(minValue, normalizedValue);

    if (maxStock === null) {

      return lowerBoundedValue;

    }

    return Math.min(maxStock, lowerBoundedValue);

  }, [getCartStockLimit]);

  const removeCartItem = useCallback((lineKey) => {

    setCart(c => c.filter(i => getCartLineKey(i) !== lineKey));

    setCartQtyDrafts((drafts) => {

      if (!Object.prototype.hasOwnProperty.call(drafts, lineKey)) return drafts;

      const nextDrafts = { ...drafts };

      delete nextDrafts[lineKey];

      return nextDrafts;

    });

  }, [getCartLineKey]);

  const commitCartQuantity = useCallback((lineKey, nextValue) => {

    const resolvedQuantity = resolveCartQuantity(lineKey, nextValue, 1, 0);

    if (resolvedQuantity <= 0) {

      removeCartItem(lineKey);

      return;

    }

    setCart((currentCart) => {

      const inventoryItem = findShopInventoryLineByKey(lineKey);

      if (!inventoryItem) return currentCart;

      const hasItem = currentCart.some(i => getCartLineKey(i) === lineKey);

      if (!hasItem) {

        return [...currentCart, {
          lineKey,
          listingId: inventoryItem.listingId || inventoryItem.id || null,
          itemId: inventoryItem.itemId,
          qty: resolvedQuantity,
        }];

      }

      return currentCart.map(i => getCartLineKey(i) === lineKey ? { ...i, qty: resolvedQuantity } : i);

    });

    setCartQtyDrafts((drafts) => ({ ...drafts, [lineKey]: String(resolvedQuantity) }));

  }, [findShopInventoryLineByKey, getCartLineKey, removeCartItem, resolveCartQuantity]);



  // Coin icon colors for each tier

  const COIN_COLORS = { copper: '#b87a50', silver: '#a8b0b8', gold: '#d4aa40', platinum: '#88c8e8', electrum: '#a8d0a0' };

  const getCoinColor = (tierId) => COIN_COLORS[tierId] || '#d4aa40';

  

  // CoinIcon: renders a small circular coin with the tier's color

  const CoinIcon = ({ tierId, size = 12 }) => {

    const c = getCoinColor(tierId);

    return <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size, borderRadius: '50%', fontSize: size * 0.5, background: `radial-gradient(circle at 35% 35%, ${c}, ${c}88)`, color: '#0d0d0f', fontWeight: 700, lineHeight: 1, flexShrink: 0, boxShadow: `0 1px 2px ${c}44`, verticalAlign: 'middle' }}>$</span>;

  };

  

  // Multi-tier price formatting: base units → compact text display

  // Prices are ALWAYS stored in base units (smallest currency, e.g. copper)

  const formatPriceFromBase = (baseAmount) => {

    if (baseAmount <= 0) return `0${tiers[0]?.symbol || 'cp'}`;

    const w = baseToWallet(Math.abs(baseAmount), tiers);

    const tiersHighFirst = [...tiers].reverse();

    const parts = tiersHighFirst

      .filter(t => (w[t.id] || 0) > 0)

      .map(t => `${w[t.id]}${t.symbol}`);

    return parts.length > 0 ? parts.join(' ') : `0${tiers[0]?.symbol || 'cp'}`;

  };

  

  // formatPrice = formatPriceFromBase — prices are stored in base units

  const formatPrice = formatPriceFromBase;

  

  // JSX price display with coin icons

  const PriceWithIcons = ({ baseAmount, size = 12, fontSize = '0.82rem' }) => {

    if (baseAmount <= 0) return <span style={{ fontSize, color: sc.textDim }}>0</span>;

    const w = baseToWallet(Math.abs(baseAmount), tiers);

    const tiersHighFirst = [...tiers].reverse();

    const parts = tiersHighFirst.filter(t => (w[t.id] || 0) > 0);

    if (parts.length === 0) return <span style={{ fontSize, color: sc.textDim }}>0</span>;

    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>

      {parts.map((t, i) => <span key={t.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><CoinIcon tierId={t.id} size={size} /><span style={{ fontSize, color: sc.gold, fontWeight: 500 }}>{w[t.id]}</span></span>)}

    </span>;

  };



  // Format character's total wealth compactly

  const charWealthDisplay = formatPriceFromBase(charGoldBase);



  // Convert a shop item price (stored as "gold equivalent") to the display currency

  // Shop prices are always stored as the primary currency amount



  // Filter inventory for display

  const filteredInventory = useMemo(() => {

    if (!shop) return [];

    return shop.inventory.filter(inv => {

      const item = getShopTemplateForInventoryLine(inv);

      if (!item) return false;

      if (!effectiveIsDM && (inv.secrecy || 'visible') !== 'visible') return false;

      const matchSearch = !search || item.name.toLowerCase().includes(search.toLowerCase());

      const matchFilter = filter === 'all' || item.category === filter;

      return matchSearch && matchFilter;

    });

  }, [shop?.inventory, search, filter, getShopTemplateForInventoryLine, effectiveIsDM]);



  const normalizedCart = useMemo(() => cart

    .map((cartItem) => ({

      ...cartItem,

      lineKey: getCartLineKey(cartItem),

      qty: resolveCartQuantity(getCartLineKey(cartItem), cartQtyDrafts[getCartLineKey(cartItem)], cartItem.qty, 0),

    }))

    .filter((cartItem) => {
      if (cartItem.qty <= 0) return false;
      const inventoryItem = findShopInventoryLineByKey(cartItem.lineKey);
      return Boolean(inventoryItem) && inventoryItem.tradeOnly !== true;
    }), [cart, cartQtyDrafts, findShopInventoryLineByKey, getCartLineKey, resolveCartQuantity]);

  const cartQtyByLineKey = useMemo(() => Object.fromEntries(normalizedCart.map((cartItem) => [cartItem.lineKey, cartItem.qty])), [normalizedCart]);

  const cartCount = normalizedCart.reduce((s, c) => s + c.qty, 0);

  const cartTotal = normalizedCart.reduce((sum, ci) => {

    const inv = findShopInventoryLineByKey(ci.lineKey);

    return sum + (inv ? inv.price * ci.qty : 0);

  }, 0);



  const categoryCounts = useMemo(() => {

    if (!shop) return { all: 0 };

    const counts = { all: 0 };

    shop.inventory.forEach(inv => {

      const item = getShopTemplateForInventoryLine(inv);

      if (!item) return;

      if (!effectiveIsDM && (inv.secrecy || 'visible') !== 'visible') return;

      counts.all++;

      counts[item.category] = (counts[item.category] || 0) + 1;

    });

    return counts;

  }, [shop?.inventory, getShopTemplateForInventoryLine, effectiveIsDM]);



  const pendingOffers = offers.filter(o => o.status === 'pending');
  const visiblePendingOffers = effectiveIsDM
    ? pendingOffers
    : pendingOffers.filter(o => o?.source === 'shop-trade-offer' && (!o.charId || o.charId === (currentCharId || char?.id)));
  const terminalOffers = offers.filter(o => o.status === 'denied' || o.status === 'cancelled');
  const visibleTerminalOffers = effectiveIsDM
    ? terminalOffers
    : terminalOffers.filter(o => o?.source === 'shop-trade-offer' && (!o.charId || o.charId === (currentCharId || char?.id)));

  const summarizeTradeSide = (side = {}) => {
    const parts = [];
    if (Array.isArray(side.items)) {
      parts.push(...side.items.map(i => `${i.name} x${i.qty}`).filter(Boolean));
    }
    if (side.gold > 0) parts.push(formatPrice(side.gold));
    return parts.join(', ') || 'Nothing';
  };



  // --- Helpers ---

  const saveShop = (updatedShop) => {

    const newShops = shops.map(s => s.id === updatedShop.id ? updatedShop : s);

    onSaveShopData({ ...shopData, shops: newShops });

  };

  const buildShopAuthorityLine = (inventoryItem, quantity) => ({
    listingId: inventoryItem?.listingId || inventoryItem?.id || (shop?.id && inventoryItem?.itemId ? `${shop.id}:${inventoryItem.itemId}` : null),
    itemId: inventoryItem?.itemId || null,
    quantity,
    expectedPrice: inventoryItem?.price,
    expectedListingVersion: inventoryItem?.version,
  });

  const buildShopAuthorityIdempotencyKey = (kind, targetShopId = shop?.id) => (
    `${kind}:${currentCharId || char?.id || 'unknown'}:${targetShopId || 'unknown'}:${Date.now().toString(36)}:${Math.random().toString(36).slice(2, 10)}`
  );

  const serverShopRuntimeDisplay = shopData?._source === 'convex-shop-runtime';
  const tradeOfferSubmitAuthorityEnabled = typeof onSubmitTradeOffer === 'function';
  const tradeOfferCancelAuthorityEnabled = typeof onCancelTradeOffer === 'function';
  const tradeOfferDenyAuthorityEnabled = effectiveIsDM && typeof onDenyTradeOfferForDm === 'function';
  const tradeOfferSettleAuthorityEnabled = effectiveIsDM && typeof onSettleTradeOfferForDm === 'function';
  const tradeReceiptPromotionAuthorityEnabled = effectiveIsDM && typeof onPromoteTradeReceiptLineToShopListingForDm === 'function';
  const shopWalletAdjustmentAuthorityEnabled = effectiveIsDM && typeof onAdjustShopWalletForDm === 'function';
  const shopSellBackAuthorityEnabled = effectiveIsDM && typeof onSellInventoryItemToShopForDm === 'function';
  const resetTradeOfferDraft = () => {
    setTradeGiving({ items: [], gold: 0 });
    setTradeWanting({ items: [], gold: 0 });
    setTradeNote('');
  };
  const getTradeInventoryOptionKey = (item, index) => String(item?.id || item?.entryId || item?.itemEntryId || `${item?.name || 'item'}:${index}`);
  const getTradeListingOptionKey = (inventoryItem) => String(inventoryItem?.listingId || inventoryItem?.id || inventoryItem?.itemId || '');
  const addTradeGivingItem = (optionKey) => {
    const selected = charInventory
      .map((item, index) => ({ item, index, optionKey: getTradeInventoryOptionKey(item, index) }))
      .find((entry) => entry.optionKey === optionKey);
    if (!selected) return;
    const { item } = selected;
    const authorityEntryId = String(item?.id || '').trim();
    if (tradeOfferSubmitAuthorityEnabled && !authorityEntryId) {
      showToast('That inventory item cannot be offered yet', 'error');
      return;
    }
    const maxQty = Math.max(1, Math.trunc(Number(item?.quantity) || 1));
    const itemEntryId = authorityEntryId || selected.optionKey;
    const existing = tradeGiving.items.find(i => i.itemEntryId === itemEntryId);
    if (existing) {
      setTradeGiving({
        ...tradeGiving,
        items: tradeGiving.items.map(i => i.itemEntryId === itemEntryId ? { ...i, qty: Math.min(maxQty, i.qty + 1), maxQty } : i),
      });
      return;
    }
    setTradeGiving({
      ...tradeGiving,
      items: [
        ...tradeGiving.items,
        {
          itemEntryId,
          name: item?.name || item?.itemName || item?.id || 'Inventory Item',
          qty: 1,
          maxQty,
        },
      ],
    });
  };
  const addTradeWantedListing = (optionKey) => {
    if (!shop) return;
    const inv = (shop.inventory || []).find((candidate) => getTradeListingOptionKey(candidate) === optionKey);
    if (!inv) return;
    const item = getShopTemplateForInventoryLine(inv);
    if (!item) return;
    const listingId = String(inv.listingId || inv.id || '').trim();
    if (tradeOfferSubmitAuthorityEnabled && !listingId) {
      showToast('That shop listing cannot be requested yet', 'error');
      return;
    }
    const maxQty = inv.stock === null ? 999 : Math.max(0, Math.trunc(Number(inv.stock) || 0));
    if (maxQty <= 0) {
      showToast('That listing is out of stock', 'error');
      return;
    }
    const lineKey = listingId || getTradeListingOptionKey(inv);
    const existing = tradeWanting.items.find(i => (i.listingId || i.itemId) === lineKey);
    if (existing) {
      setTradeWanting({
        ...tradeWanting,
        items: tradeWanting.items.map(i => (i.listingId || i.itemId) === lineKey ? { ...i, qty: Math.min(maxQty, i.qty + 1), maxQty } : i),
      });
      return;
    }
    setTradeWanting({
      ...tradeWanting,
      items: [
        ...tradeWanting.items,
        {
          listingId,
          itemId: inv.itemId,
          name: item.name,
          qty: 1,
          maxQty,
          expectedPrice: inv.price,
          expectedListingVersion: inv.version,
        },
      ],
    });
  };



  const updateInvItem = (lineKey, updates) => {

    if (!shop) return;

    const newInv = shop.inventory.map((i, index) => (
      getShopInventoryLineKey(i, index) === lineKey ? { ...i, ...updates } : i
    ));

    saveShop({ ...shop, inventory: newInv });

  };



  const removeInvItem = (lineKey) => {

    if (!shop) return;

    saveShop({
      ...shop,
      inventory: shop.inventory.filter((i, index) => getShopInventoryLineKey(i, index) !== lineKey),
    });

    showToast('Item removed');

  };



  const handlePurchase = async () => {

    if (!purchaseModal || !shop) return;

    const inv = findShopInventoryLineByKey(purchaseModal);

    const item = getShopTemplateForInventoryLine(inv);

    if (!inv || !item) return;

    if (inv.tradeOnly) {
      closePurchaseModal();
      setShowOfferModal(true);
      return;
    }

    const maxStock = inv.stock === null ? 999 : inv.stock;

    const resolvedPurchaseQty = clampPurchaseQuantity(purchaseQtyDraft, maxStock, purchaseQty);

    if (typeof onPurchaseShopListing === 'function') {
      if (purchaseAuthorityInFlightRef.current) return;
      purchaseAuthorityInFlightRef.current = true;
      try {
        const authorityResult = await onPurchaseShopListing({
          charId: currentCharId || char?.id,
          shopId: shop.id,
          idempotencyKey: buildShopAuthorityIdempotencyKey('shop-purchase'),
          ...buildShopAuthorityLine(inv, resolvedPurchaseQty),
        });
        if (authorityResult !== null && authorityResult !== undefined) {
          closePurchaseModal();
          showToast(`Purchased ${resolvedPurchaseQty}x ${item.name}`);
          return;
        }
      } catch (e) {
        console.error('[shop] authoritative purchase failed', e);
        showToast('Purchase could not be completed', 'error');
        return;
      } finally {
        purchaseAuthorityInFlightRef.current = false;
      }
    }

    const settlement = applyShopCheckoutToCharAndShop(char, shopData, {
      shopId: shop.id,
      purchases: [buildShopAuthorityLine(inv, resolvedPurchaseQty)],
      templates,
      currencyTiers: tiers,
      isDM: effectiveIsDM,
      currentCharId,
    });

    if (!settlement) return;

    const { nextCharData, nextShopData, totalCost, itemCount } = settlement;

    onUpdateChar({ wallet: nextCharData.wallet, inventory: nextCharData.inventory }, { summary: `${char.name} purchased ${itemCount}x ${item.name} for ${formatPrice(totalCost)}`, category: 'inventory', action: 'inventory.shop' });

    onSaveShopData(nextShopData);

    closePurchaseModal();

    showToast(`Purchased ${itemCount}× ${item.name} for ${formatPrice(totalCost)}`);

  };



  const canAddMoreToCart = useCallback((lineKey) => {

    const maxStock = getCartStockLimit(lineKey);

    if (maxStock === 0) return false;

    if (maxStock === null) return true;

    return (cartQtyByLineKey[lineKey] || 0) < maxStock;

  }, [cartQtyByLineKey, getCartStockLimit]);

  const handleAddToCart = (lineKey) => {

    const inventoryItem = findShopInventoryLineByKey(lineKey);
    if (inventoryItem?.tradeOnly) return;

    if (!canAddMoreToCart(lineKey)) return;

    const currentQty = cartQtyByLineKey[lineKey] || 0;

    commitCartQuantity(lineKey, currentQty + 1);

  };



  const handleCheckout = async () => {

    if (cartTotal > charGoldBase) return;

    const purchaseLines = normalizedCart.map((cartItem) => {
      const inv = findShopInventoryLineByKey(cartItem.lineKey);
      return buildShopAuthorityLine(inv, cartItem.qty);
    });

    if (typeof onCheckoutShopCart === 'function') {
      if (checkoutAuthorityInFlightRef.current) return;
      checkoutAuthorityInFlightRef.current = true;
      try {
        const authorityResult = await onCheckoutShopCart({
          charId: currentCharId || char?.id,
          shopId: shop?.id,
          lines: purchaseLines,
          idempotencyKey: buildShopAuthorityIdempotencyKey('shop-checkout'),
        });
        if (authorityResult !== null && authorityResult !== undefined) {
          showToast('Checkout complete');
          setCart([]); setCartQtyDrafts({}); setShowCart(false);
          return;
        }
      } catch (e) {
        console.error('[shop] authoritative checkout failed', e);
        showToast('Checkout could not be completed', 'error');
        return;
      } finally {
        checkoutAuthorityInFlightRef.current = false;
      }
    }

    const settlement = applyShopCheckoutToCharAndShop(char, shopData, {
      shopId: shop.id,
      purchases: purchaseLines,
      templates,
      currencyTiers: tiers,
      isDM: effectiveIsDM,
      currentCharId,
    });

    if (!settlement) return;

    const { nextCharData, nextShopData, totalCost, itemCount } = settlement;

    onUpdateChar({ wallet: nextCharData.wallet, inventory: nextCharData.inventory }, { summary: `${char.name} bought ${itemCount} items for ${formatPrice(totalCost)}`, category: 'inventory', action: 'inventory.shop' });

    onSaveShopData(nextShopData);

    showToast(`Checkout: ${formatPrice(totalCost)} for ${itemCount} items`); setCart([]); setCartQtyDrafts({}); setShowCart(false);

  };



  const handleCreateShop = () => {

    if (!newShopName.trim()) return;

    const tags = (newShopTags || '').split(',').map(s => s.trim()).filter(Boolean);

    const newShop = { id: `shop_${Date.now()}`, name: newShopName, keeperName: newShopKeeper || 'Unknown Keeper', type: newShopType, description: newShopDesc, isOpen: false, starred: true, inventory: [], tags, mode: newShopMode, campaigns: newShopCampaigns.length > 0 ? newShopCampaigns : (campaigns.length > 0 ? ['general'] : []), themeId: newShopTheme || null, createdAt: new Date().toISOString() };

    onSaveShopData({ ...shopData, shops: [...shops, newShop], activeShopId: newShop.id });

    setNewShopName(''); setNewShopKeeper(''); setNewShopDesc(''); setNewShopTags(''); setNewShopMode('coin'); setNewShopCampaigns([]); setNewShopTheme(''); setShowCreateShop(false);

    showToast(`Created "${newShopName}"`);

  };



  const handleDeleteShop = (shopId) => { if (!window.confirm('Delete this shop?')) return; const newShops = shops.filter(s => s.id !== shopId); onSaveShopData({ ...shopData, shops: newShops, activeShopId: activeShopId === shopId ? (newShops[0]?.id || null) : activeShopId }); showToast('Shop deleted'); };

  const handleToggleShopOpen = (shopId) => { onSaveShopData({ ...shopData, shops: shops.map(s => s.id === shopId ? { ...s, isOpen: !s.isOpen } : s) }); };

  const handleToggleHidden = (lineKey) => {

    const inv = findShopInventoryLineByKey(lineKey);

    if (!inv) return;

    const cur = inv.secrecy || 'visible';

    updateInvItem(lineKey, { secrecy: cur === 'hidden' ? 'visible' : 'hidden' });

  };

  const handleRemoveItemFromShop = (lineKey) => {

    if (!shop) return;

    const newInv = shop.inventory.filter((i, index) => getShopInventoryLineKey(i, index) !== lineKey);

    onSaveShopData({ ...shopData, shops: shops.map(s => s.id === shop.id ? { ...s, inventory: newInv } : s) });

    showToast('Item removed from shop');

  };

  const handleToggleStarred = (shopId) => { onSaveShopData({ ...shopData, shops: shops.map(s => s.id === shopId ? { ...s, starred: !s.starred } : s) }); };

  const [managerFilter, setManagerFilter] = useState('all');

  const handleMoveShop = (shopId, direction) => {

    const idx = shops.findIndex(s => s.id === shopId);

    if (idx < 0) return;

    const newIdx = idx + direction;

    if (newIdx < 0 || newIdx >= shops.length) return;

    const newShops = [...shops];

    [newShops[idx], newShops[newIdx]] = [newShops[newIdx], newShops[idx]];

    onSaveShopData({ ...shopData, shops: newShops });

  };

  const handleDragDrop = (fromId, toId) => {

    if (!fromId || !toId || fromId === toId) return;

    const fromIdx = shops.findIndex(s => s.id === fromId);

    const toIdx = shops.findIndex(s => s.id === toId);

    if (fromIdx < 0 || toIdx < 0) return;

    const newShops = [...shops];

    const [moved] = newShops.splice(fromIdx, 1);

    newShops.splice(toIdx, 0, moved);

    onSaveShopData({ ...shopData, shops: newShops });

  };

  const handleSortShops = (mode) => {

    const sorted = [...shops];

    if (mode === 'alpha') sorted.sort((a, b) => a.name.localeCompare(b.name));

    else if (mode === 'date') sorted.sort((a, b) => (b.createdAt || b.id).localeCompare(a.createdAt || a.id));

    // Always put starred first

    sorted.sort((a, b) => (b.starred ? 1 : 0) - (a.starred ? 1 : 0));

    onSaveShopData({ ...shopData, shops: sorted });

  };

  const handleUpdateShopTags = (shopId, tags) => {

    onSaveShopData({ ...shopData, shops: shops.map(s => s.id === shopId ? { ...s, tags } : s) });

  };

  const handleAddTagToShop = (shopId, tag) => {

    if (!tag.trim()) return;

    const s = shops.find(sh => sh.id === shopId);

    if (!s) return;

    const existing = (s.tags || []);

    if (existing.some(t => t.toLowerCase() === tag.trim().toLowerCase())) return; // no dupes

    handleUpdateShopTags(shopId, [...existing, tag.trim()]);

  };

  const handleRemoveTagFromShop = (shopId, tag) => {

    const s = shops.find(sh => sh.id === shopId);

    if (!s) return;

    handleUpdateShopTags(shopId, (s.tags || []).filter(t => t !== tag));

  };

  const handleRenameTagInShop = (shopId, oldTag, newTag) => {

    if (!newTag.trim()) return handleRemoveTagFromShop(shopId, oldTag);

    const s = shops.find(sh => sh.id === shopId);

    if (!s) return;

    handleUpdateShopTags(shopId, (s.tags || []).map(t => t === oldTag ? newTag.trim() : t));

  };

  const getShopWalletBalanceBase = (targetShop) => (
    Math.max(0, Math.trunc(Number(targetShop?.shopWallet?.balanceBase ?? targetShop?.shopWalletBalanceBase ?? 0) || 0))
  );

  const getShopWalletVersion = (targetShop) => (
    Math.max(0, Math.trunc(Number(targetShop?.shopWallet?.version ?? targetShop?.shopWalletVersion ?? 0) || 0))
  );

  const getTradeReceiptPromotionDraftKey = (entry, line) => (
    `${entry?.transactionId || 'unknown'}:${line?.lineIndex ?? 0}`
  );

  const getTradeReceiptPromotionDraft = (entry, line) => {
    const key = getTradeReceiptPromotionDraftKey(entry, line);
    const fallbackPriceBase = Math.max(0, Math.trunc(Number(line?.suggestedPriceBase ?? line?.basePrice ?? 0) || 0));
    const draft = tradeReceiptPromotionDrafts[key] || {};
    return {
      priceBase: draft.priceBase ?? String(fallbackPriceBase),
      secrecy: draft.secrecy || 'visible',
      tradeOnly: draft.tradeOnly === true,
    };
  };

  const updateTradeReceiptPromotionDraft = (entry, line, updates) => {
    const key = getTradeReceiptPromotionDraftKey(entry, line);
    setTradeReceiptPromotionDrafts((drafts) => ({
      ...drafts,
      [key]: {
        ...getTradeReceiptPromotionDraft(entry, line),
        ...(drafts[key] || {}),
        ...updates,
      },
    }));
  };

  const handlePromoteTradeReceiptLine = async (entry, line) => {
    if (!entry || !line) return;
    if (!tradeReceiptPromotionAuthorityEnabled) {
      showToast('Trade resale promotion is not available yet', 'error');
      return;
    }
    if (line.promoted || line.alreadyPromoted) {
      showToast('This receipt line is already listed for resale', 'error');
      return;
    }
    if (!entry.transactionId || line.lineIndex === undefined || line.lineIndex === null || line.canPromote === false) {
      showToast('Receipt line cannot be promoted yet', 'error');
      return;
    }
    const draft = getTradeReceiptPromotionDraft(entry, line);
    const priceBase = Math.trunc(Number(draft.priceBase));
    if (!Number.isFinite(priceBase) || priceBase < 0) {
      showToast('Enter a valid resale price', 'error');
      return;
    }
    if (tradeReceiptPromotionInFlightRef.current) return;
    tradeReceiptPromotionInFlightRef.current = true;
    try {
      const authorityResult = await onPromoteTradeReceiptLineToShopListingForDm({
        transactionId: entry.transactionId,
        lineIndex: line.lineIndex,
        priceBase,
        secrecy: draft.secrecy || 'visible',
        tradeOnly: draft.tradeOnly === true,
        idempotencyKey: buildShopAuthorityIdempotencyKey('shop-trade-promote', entry.shopId || shop?.id),
      });
      if (authorityResult === null || authorityResult === undefined) {
        throw new Error('shop trade resale promotion authority unavailable');
      }
      showToast(`${line.name || line.itemName || 'Item'} listed for resale`, 'info');
    } catch (e) {
      console.error('[shop] authoritative trade receipt promotion failed', e);
      showToast('Receipt line could not be promoted', 'error');
    } finally {
      tradeReceiptPromotionInFlightRef.current = false;
    }
  };

  const handleShopWalletAdjustmentDraft = (shopId, value) => {
    setShopWalletAdjustmentDrafts((drafts) => ({ ...drafts, [shopId]: value }));
  };

  const handleShopWalletReasonDraft = (shopId, value) => {
    setShopWalletAdjustmentReasonDrafts((drafts) => ({ ...drafts, [shopId]: value }));
  };

  const getShopSellBackDraft = (shopId) => shopSellBackDrafts[shopId] || {
    charId: '',
    itemEntryId: '',
    quantity: '1',
    payoutBase: '',
  };

  const getSellBackEligibleItems = (charId) => {
    const selectedChar = allChars?.[charId];
    return (selectedChar?.inventory || [])
      .map((item) => {
        const totalQuantity = Math.max(1, Math.trunc(Number(item?.quantity) || 1));
        const availableQuantity = item?.equipped === true
          ? Math.max(0, totalQuantity - 1)
          : totalQuantity;
        return { item, availableQuantity };
      })
      .filter(({ item, availableQuantity }) => (
        item?.id
        && item.tradeOnly !== true
        && availableQuantity > 0
      ));
  };

  const handleShopSellBackDraft = (shopId, updates) => {
    setShopSellBackDrafts((drafts) => ({
      ...drafts,
      [shopId]: {
        ...getShopSellBackDraft(shopId),
        ...updates,
      },
    }));
  };

  const handleAdjustShopWallet = async (targetShop) => {
    if (!targetShop) return;
    const rawDelta = shopWalletAdjustmentDrafts[targetShop.id];
    const deltaBase = Math.trunc(Number(rawDelta));
    if (!Number.isFinite(deltaBase) || deltaBase === 0) {
      showToast('Enter a non-zero wallet adjustment', 'error');
      return;
    }
    if (!shopWalletAdjustmentAuthorityEnabled) {
      showToast('Shop wallet authority unavailable', 'error');
      return;
    }
    if (shopWalletAdjustmentInFlightRef.current) return;
    shopWalletAdjustmentInFlightRef.current = true;
    try {
      const authorityResult = await onAdjustShopWalletForDm({
        shopId: targetShop.id,
        deltaBase,
        reason: shopWalletAdjustmentReasonDrafts[targetShop.id] || 'DM wallet adjustment',
        expectedWalletVersion: getShopWalletVersion(targetShop),
        idempotencyKey: buildShopAuthorityIdempotencyKey('shop-wallet-adjust', targetShop.id),
      });
      if (authorityResult === null || authorityResult === undefined) {
        throw new Error('shop wallet authority unavailable');
      }
      setShopWalletAdjustmentDrafts((drafts) => ({ ...drafts, [targetShop.id]: '' }));
      setShopWalletAdjustmentReasonDrafts((drafts) => ({ ...drafts, [targetShop.id]: '' }));
      showToast(deltaBase > 0 ? 'Shop wallet credited' : 'Shop wallet debited', 'info');
    } catch (e) {
      console.error('[shop] authoritative wallet adjustment failed', e);
      showToast('Shop wallet adjustment failed', 'error');
    } finally {
      shopWalletAdjustmentInFlightRef.current = false;
    }
  };

  const handleSellInventoryItemToShopForDm = async (targetShop) => {
    if (!targetShop) return;
    if (!shopSellBackAuthorityEnabled) {
      showToast('Shop sell-back authority unavailable', 'error');
      return;
    }
    const draft = getShopSellBackDraft(targetShop.id);
    const selectedCharId = String(draft.charId || '').trim();
    const selectedItemEntryId = String(draft.itemEntryId || '').trim();
    const eligibleItems = getSellBackEligibleItems(selectedCharId);
    const selected = eligibleItems.find(({ item }) => item.id === selectedItemEntryId);
    if (!selected) {
      showToast('Choose an eligible inventory item', 'error');
      return;
    }
    const quantity = Math.max(1, Math.min(
      selected.availableQuantity,
      Math.trunc(Number(draft.quantity) || 1),
    ));
    const payoutBase = Math.trunc(Number(draft.payoutBase));
    if (!Number.isFinite(payoutBase) || payoutBase < 0) {
      showToast('Enter a valid sell-back payout', 'error');
      return;
    }
    if (shopSellBackInFlightRef.current) return;
    shopSellBackInFlightRef.current = true;
    try {
      const authorityResult = await onSellInventoryItemToShopForDm({
        charId: selectedCharId,
        shopId: targetShop.id,
        itemEntryId: selectedItemEntryId,
        quantity,
        payoutBase,
        expectedShopVersion: targetShop.version,
        idempotencyKey: buildShopAuthorityIdempotencyKey('shop-sell-back', targetShop.id),
      });
      if (authorityResult === null || authorityResult === undefined) {
        throw new Error('shop sell-back authority unavailable');
      }
      setShopSellBackDrafts((drafts) => ({
        ...drafts,
        [targetShop.id]: {
          charId: selectedCharId,
          itemEntryId: '',
          quantity: '1',
          payoutBase: '',
        },
      }));
      showToast('Item sold to shop', 'info');
    } catch (e) {
      console.error('[shop] authoritative sell-back failed', e);
      showToast('Shop sell-back failed', 'error');
    } finally {
      shopSellBackInFlightRef.current = false;
    }
  };

  const allShopTags = [...new Set(shops.flatMap(s => s.tags || []))].sort();

  const tagSuggestions = shopTagSearch.trim() ? allShopTags.filter(t => t.toLowerCase().includes(shopTagSearch.toLowerCase()) && t.toLowerCase() !== shopTagFilter.toLowerCase()) : [];

  const handleAddItemToShop = (itemId, secrecy = 'visible', stockQty = 5) => { const tmpl = templates[itemId]; if (!tmpl || !shop) return; if (shop.inventory.some(i => i.itemId === itemId)) return; saveShop({ ...shop, inventory: [...shop.inventory, { itemId, price: tmpl.basePrice * goldMultiplier, stock: stockQty, secrecy }] }); showToast(`Added ${tmpl.name}`); };

  const handleSubmitOffer = async () => {
    const canSubmit = (tradeGiving.items.length > 0 || tradeGiving.gold > 0) && (tradeWanting.items.length > 0 || tradeWanting.gold > 0);
    if (!canSubmit || !shop || !char) return;

    if (tradeOfferSubmitAuthorityEnabled) {
      if (tradeOfferSubmitInFlightRef.current) return;
      const offeredItems = tradeGiving.items
        .map((item) => ({ itemEntryId: String(item.itemEntryId || '').trim(), quantity: Math.trunc(Number(item.qty) || 0) }))
        .filter((item) => item.itemEntryId && item.quantity > 0);
      const requestedListings = tradeWanting.items
        .map((item) => {
          const expectedPrice = Number(item.expectedPrice);
          const line = {
            listingId: String(item.listingId || '').trim(),
            quantity: Math.trunc(Number(item.qty) || 0),
            expectedListingVersion: Number(item.expectedListingVersion),
          };
          if (Number.isFinite(expectedPrice)) line.expectedPrice = expectedPrice;
          return line;
        })
        .filter((item) => item.listingId && item.quantity > 0 && Number.isFinite(item.expectedListingVersion));

      if (offeredItems.length !== tradeGiving.items.length || requestedListings.length !== tradeWanting.items.length) {
        showToast('Offer needs current inventory and listing selections', 'error');
        return;
      }

      tradeOfferSubmitInFlightRef.current = true;
      try {
        const authorityResult = await onSubmitTradeOffer({
          charId: currentCharId || char.id,
          shopId: shop.id,
          offeredItems,
          offeredGoldBase: Math.max(0, Math.trunc(Number(tradeGiving.gold) || 0)),
          requestedListings,
          requestedGoldBase: Math.max(0, Math.trunc(Number(tradeWanting.gold) || 0)),
          note: tradeNote,
          expectedShopVersion: shop.version,
          idempotencyKey: buildShopAuthorityIdempotencyKey('shop-trade-submit'),
        });
        if (authorityResult === null || authorityResult === undefined) {
          throw new Error('shop trade-offer authority unavailable');
        }
        setShowOfferModal(false);
        resetTradeOfferDraft();
        showToast('Offer submitted!', 'info');
        return;
      } catch (e) {
        console.error('[shop] authoritative trade offer submit failed', e);
        showToast('Offer could not be submitted', 'error');
        return;
      } finally {
        tradeOfferSubmitInFlightRef.current = false;
      }
    }

    if (serverShopRuntimeDisplay) {
      showToast('Trade offer authority unavailable', 'error');
      return;
    }

    onSaveShopData({ ...shopData, offers: [...offers, { id: `offer_${Date.now()}`, charName: char.name, giving: tradeGiving, wanting: tradeWanting, note: tradeNote, status: 'pending' }] });
    setShowOfferModal(false);
    resetTradeOfferDraft();
    showToast('Offer submitted!', 'info');
  };

  const handleResolveOffer = async (offerId, status) => {

    const offer = offers.find(o => o.id === offerId);

    if (!offer) return;

    if (offer.source === 'shop-trade-offer') {
      if (status === 'accepted') {
        if (!tradeOfferSettleAuthorityEnabled) {
          showToast('Trade settlement is not available yet', 'error');
          return;
        }
        if (tradeOfferSettleInFlightRef.current) return;
        tradeOfferSettleInFlightRef.current = true;
        try {
          const authorityResult = await onSettleTradeOfferForDm({
            offerId: offer.offerId || offer.id,
            expectedOfferVersion: offer.version,
            idempotencyKey: buildShopAuthorityIdempotencyKey('shop-trade-settle'),
          });
          if (authorityResult === null || authorityResult === undefined) {
            throw new Error('shop trade-offer settlement authority unavailable');
          }
          showToast('Offer accepted!', 'info');
          return;
        } catch (e) {
          console.error('[shop] authoritative trade offer settlement failed', e);
          showToast('Offer could not be accepted', 'error');
          return;
        } finally {
          tradeOfferSettleInFlightRef.current = false;
        }
      }
      if (status === 'denied') {
        if (!tradeOfferDenyAuthorityEnabled) {
          showToast('Trade denial is not available yet', 'error');
          return;
        }
        if (tradeOfferDenyInFlightRef.current) return;
        tradeOfferDenyInFlightRef.current = true;
        try {
          const authorityResult = await onDenyTradeOfferForDm({
            offerId: offer.offerId || offer.id,
            expectedOfferVersion: offer.version,
            reason: 'dm-denied',
            idempotencyKey: buildShopAuthorityIdempotencyKey('shop-trade-deny'),
          });
          if (authorityResult === null || authorityResult === undefined) {
            throw new Error('shop trade-offer denial authority unavailable');
          }
          showToast('Offer denied.', 'info');
          return;
        } catch (e) {
          console.error('[shop] authoritative trade offer denial failed', e);
          showToast('Offer could not be denied', 'error');
          return;
        } finally {
          tradeOfferDenyInFlightRef.current = false;
        }
      }
      if (!tradeOfferCancelAuthorityEnabled) {
        showToast('Trade offer authority unavailable', 'error');
        return;
      }
      if (tradeOfferCancelInFlightRef.current) return;
      tradeOfferCancelInFlightRef.current = true;
      try {
        const authorityResult = await onCancelTradeOffer({
          offerId: offer.offerId || offer.id,
          expectedOfferVersion: offer.version,
          reason: 'cancelled',
          idempotencyKey: buildShopAuthorityIdempotencyKey('shop-trade-cancel'),
        });
        if (authorityResult === null || authorityResult === undefined) {
          throw new Error('shop trade-offer cancel authority unavailable');
        }
        showToast('Offer cancelled', 'info');
        return;
      } catch (e) {
        console.error('[shop] authoritative trade offer cancel failed', e);
        showToast('Offer could not be cancelled', 'error');
        return;
      } finally {
        tradeOfferCancelInFlightRef.current = false;
      }
    }

    if (serverShopRuntimeDisplay) {
      showToast('Trade offer authority unavailable', 'error');
      return;
    }

    let newLog = log;

    if (status === 'accepted' && offer) {

      const giveSummary = [...offer.giving.items.map(i => `${i.name} ×${i.qty}`), ...(offer.giving.gold > 0 ? [`${formatPrice(offer.giving.gold)}`] : [])].join(', ');

      const wantSummary = [...offer.wanting.items.map(i => `${i.name} ×${i.qty}`), ...(offer.wanting.gold > 0 ? [`${formatPrice(offer.wanting.gold)}`] : [])].join(', ');

      newLog = [{ type: 'trade', char: offer.charName, giving: giveSummary, wanting: wantSummary, note: offer.note, time: new Date().toISOString() }, ...log].slice(0, 50);

    }

    onSaveShopData({ ...shopData, offers: offers.map(o => o.id === offerId ? { ...o, status } : o), transactionLog: newLog });

    showToast(status === 'accepted' ? 'Offer accepted!' : 'Offer denied.');

  };

  const handlePriceChange = (lineKey, price) => updateInvItem(lineKey, { price });

  const handleStockChange = (lineKey, stock) => updateInvItem(lineKey, { stock: stock === '' || stock === null ? null : Math.max(0, parseInt(stock) || 0) });



  // --- RENDER ---

  // No shops exist for player

  if (shops.length === 0 && !isDM) {

    return (

      <div className="shop-sys" style={{ minHeight: '100vh', background: sc.bg, color: sc.text, padding: 20 }}>

        <button className="shop-btn" onClick={onBack} style={{ padding: '4px 10px', background: 'none', border: `1px solid ${sc.borderMed}`, borderRadius: 4, color: sc.textMuted, fontSize: '0.8rem' }}>← Back</button>

        <div style={{ textAlign: 'center', padding: '80px 20px' }}>

          <div style={{ fontSize: '2rem', marginBottom: 12 }}>🚪</div>

          <div style={{ fontSize: '1.1rem', color: sc.textMuted }}>No shops are open right now</div>

          <div style={{ fontSize: '0.85rem', color: sc.textDim, marginTop: 8 }}>The DM hasn't created any shops yet.</div>

        </div>

      </div>

    );

  }



  return (

    <div className="shop-sys" style={{ minHeight: '100vh', background: sc.bg, color: sc.text }}>

      {/* Top Bar */}

      <div style={{ background: sc.bgBar, borderBottom: `1px solid ${sc.border}`, padding: '10px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100 }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>

          <button className="shop-btn" onClick={onBack} style={{ padding: '4px 10px', background: 'none', border: `1px solid ${sc.borderMed}`, borderRadius: 4, color: sc.textMuted, fontSize: '0.8rem' }}>← Back</button>

          <span style={{ fontSize: '1rem', fontWeight: 600, color: sc.textHead }}>🏪 Shop</span>

        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>

          {/* Player wallet */}

          {!effectiveIsDM && char && (

            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>

              {Object.entries(wallet).filter(([_, v]) => v > 0).map(([k, v]) => {

                const tier = tiers.find(t => t.id === k);

                return tier ? <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 3 }}><ShopCoin type={k === 'gold' ? 'gold' : k === 'silver' ? 'silver' : 'copper'} size={k === 'gold' ? 18 : k === 'silver' ? 16 : 14} /><span style={{ color: k === 'gold' ? sc.gold : k === 'silver' ? '#a8b0b8' : '#b87a50', fontSize: k === 'gold' ? '0.95rem' : '0.82rem', fontWeight: k === 'gold' ? 600 : 400 }}>{v}</span></div> : null;

              })}

            </div>

          )}

          {/* DM preview toggle */}

          {isDM && dmTab === 'shop' && (

            <button className="shop-btn" onClick={() => setPreviewAsPlayer(!previewAsPlayer)}

              style={{ padding: '4px 12px', background: previewAsPlayer ? '#2a1a3a' : sc.bgCard, border: `1px solid ${previewAsPlayer ? '#5a3a7a' : sc.border}`, borderRadius: 4, color: previewAsPlayer ? '#b87ae0' : sc.textMuted, fontSize: '0.75rem' }}>

              {previewAsPlayer ? '🎭 Player View' : '🔧 DM View'}

            </button>

          )}

          {/* Shop Theme Toggle */}

          {shop?.themeId && THEMES[shop.themeId] ? (

            <span style={{ padding: '4px 12px', background: `${THEMES[shop.themeId].colors.accent}15`, border: `1px solid ${THEMES[shop.themeId].colors.accent}40`, borderRadius: 4, color: THEMES[shop.themeId].colors.accent, fontSize: '0.75rem' }}

              title={`This shop uses its own theme: ${THEMES[shop.themeId].name}. Change it in the Manager tab.`}>

              {THEMES[shop.themeId].preview} {THEMES[shop.themeId].name}

            </span>

          ) : (

            <button className="shop-btn" onClick={toggleShopTheme}

              style={{ padding: '4px 12px', background: shopUseAppTheme ? '#1a2a3a' : sc.bgCard, border: `1px solid ${shopUseAppTheme ? '#3a5a7a' : sc.border}`, borderRadius: 4, color: shopUseAppTheme ? '#7ab8e0' : sc.textMuted, fontSize: '0.75rem' }}>

              {shopUseAppTheme ? '🎨 App' : '🏪 Shop'}

            </button>

          )}

          {isDM && <span style={{ padding: '3px 10px', background: sc.goldDark, border: `1px solid ${sc.goldBorder}`, borderRadius: 12, color: sc.gold, fontSize: '0.7rem', fontWeight: 600 }}>👑 DM</span>}

        </div>

      </div>



      <div style={{ maxWidth: 960, margin: '0 auto', padding: '20px 16px' }}>

        {/* DM Tab Bar */}

        {isDM && (

          <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: `1px solid ${sc.border}` }}>

            {[{ id: 'shop', label: '🏪 Shop View' }, { id: 'manager', label: '⚙️ Manager' }].map(tab => (

              <button key={tab.id} className={`shop-tab ${dmTab === tab.id ? 'shop-tab-active' : ''}`}

                onClick={() => setDmTab(tab.id)}

                style={{ padding: '8px 18px', background: 'transparent', border: 'none', color: dmTab === tab.id ? sc.textHead : sc.textDim, fontSize: '0.85rem', cursor: 'pointer' }}>

                {tab.label}

              </button>

            ))}

            {pendingOffers.length > 0 && dmTab !== 'shop' && <span style={{ alignSelf: 'center', marginLeft: 12, fontSize: '0.72rem', color: sc.gold }}>📨 {pendingOffers.length} pending</span>}

          </div>

        )}



        {/* DM Manager Tab */}

        {isDM && dmTab === 'manager' && (

          <div style={{ background: sc.bgDark, border: `1px solid ${sc.border}`, borderRadius: 8, padding: 16, marginBottom: 20 }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>

              <div style={{ fontSize: '0.85rem', color: sc.textHead, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>🏪 Shop Manager</div>

              <button className="shop-btn" onClick={() => setShowCreateShop(!showCreateShop)} title={showCreateShop ? 'Cancel creating a new shop' : 'Create a new shop'}

                style={{ padding: '4px 12px', background: sc.greenBg, border: `1px solid ${sc.greenBorder}`, borderRadius: 4, color: sc.green, fontSize: '0.78rem' }}>

                {showCreateShop ? '✕ Cancel' : '+ New Shop'}

              </button>

            </div>

            {showCreateShop && (

              <div style={{ background: sc.bgDarker, border: `1px solid ${sc.borderLight}`, borderRadius: 6, padding: 12, marginBottom: 12, animation: 'shopFadeIn 0.2s ease' }}>

                <div style={{ fontSize: '0.82rem', fontWeight: 600, color: sc.gold, marginBottom: 10 }}>✨ Create a New Shop</div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>

                  <div><div style={{ fontSize: '0.7rem', color: sc.textMuted, marginBottom: 3 }}>Shop Name *</div><input value={newShopName} onChange={e => setNewShopName(e.target.value)} placeholder="The Gilded Flask" className="shop-input" style={{ width: '100%', background: sc.bgCard, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '6px 10px', color: sc.text, fontSize: '0.85rem' }} /></div>

                  <div><div style={{ fontSize: '0.7rem', color: sc.textMuted, marginBottom: 3 }}>Shopkeeper</div><input value={newShopKeeper} onChange={e => setNewShopKeeper(e.target.value)} placeholder="Mirella Thornwick" className="shop-input" style={{ width: '100%', background: sc.bgCard, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '6px 10px', color: sc.text, fontSize: '0.85rem' }} /></div>

                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>

                  <div><div style={{ fontSize: '0.7rem', color: sc.textMuted, marginBottom: 3 }}>Type</div><select value={newShopType} onChange={e => setNewShopType(e.target.value)} className="shop-input" style={{ width: '100%', background: sc.bgCard, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '6px 10px', color: sc.textSubLight, fontSize: '0.8rem' }}>{SHOP_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}</select></div>

                  <div><div style={{ fontSize: '0.7rem', color: sc.textMuted, marginBottom: 3 }}>Description</div><input value={newShopDesc} onChange={e => setNewShopDesc(e.target.value)} placeholder="A dusty apothecary..." className="shop-input" style={{ width: '100%', background: sc.bgCard, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '6px 10px', color: sc.text, fontSize: '0.85rem' }} /></div>

                </div>

                <div style={{ marginBottom: 10 }}>

                  <div style={{ fontSize: '0.7rem', color: sc.textMuted, marginBottom: 3 }}>Tags (comma separated: location, theme, etc.)</div>

                  <input value={newShopTags || ''} onChange={e => setNewShopTags(e.target.value)} placeholder="Waterdeep, Black Market, Rare Items..." className="shop-input"

                    style={{ width: '100%', background: sc.bgCard, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '6px 10px', color: sc.text, fontSize: '0.85rem' }} />

                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>

                  <div>

                    <div style={{ fontSize: '0.7rem', color: sc.textMuted, marginBottom: 3 }}>Shop Mode</div>

                    <div style={{ display: 'flex', gap: 4 }}>

                      {[{ id: 'coin', label: '🪙 Coin' }, { id: 'trade', label: '⚖️ Trade' }, { id: 'both', label: '🪙⚖️ Both' }].map(m => (

                        <button key={m.id} className="shop-btn" onClick={() => setNewShopMode(m.id)}

                          style={{ flex: 1, padding: '4px 6px', fontSize: '0.68rem', background: newShopMode === m.id ? 'rgba(201,162,39,0.12)' : 'transparent', border: `1px solid ${newShopMode === m.id ? 'rgba(201,162,39,0.3)' : sc.borderLight}`, borderRadius: 4, color: newShopMode === m.id ? sc.gold : sc.textDim }}>{m.label}</button>

                      ))}

                    </div>

                  </div>

                  {campaigns.length > 0 && (

                    <div>

                      <div style={{ fontSize: '0.7rem', color: sc.textMuted, marginBottom: 3 }}>Campaign</div>

                      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>

                        <button className="shop-btn" onClick={() => setNewShopCampaigns([])}

                          style={{ padding: '3px 6px', fontSize: '0.62rem', background: newShopCampaigns.length === 0 ? 'rgba(201,162,39,0.12)' : 'transparent', border: `1px solid ${newShopCampaigns.length === 0 ? 'rgba(201,162,39,0.3)' : sc.borderLight}`, borderRadius: 3, color: newShopCampaigns.length === 0 ? sc.gold : sc.textDim }}>General</button>

                        {campaigns.map(c => {

                          const sel = newShopCampaigns.includes(c);

                          return <button key={c} className="shop-btn" onClick={() => setNewShopCampaigns(sel ? newShopCampaigns.filter(x => x !== c) : [...newShopCampaigns, c])}

                            style={{ padding: '3px 6px', fontSize: '0.62rem', background: sel ? 'rgba(201,162,39,0.12)' : 'transparent', border: `1px solid ${sel ? 'rgba(201,162,39,0.3)' : sc.borderLight}`, borderRadius: 3, color: sel ? sc.gold : sc.textDim }}>{c}</button>;

                        })}

                      </div>

                    </div>

                  )}

                </div>

                <div style={{ marginBottom: 10 }}>

                  <div style={{ fontSize: '0.7rem', color: sc.textMuted, marginBottom: 3 }}>Shop Theme (override)</div>

                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>

                    <button className="shop-btn" onClick={() => setNewShopTheme('')}

                      style={{ padding: '4px 10px', fontSize: '0.68rem', background: !newShopTheme ? 'rgba(201,162,39,0.12)' : 'transparent', border: `1px solid ${!newShopTheme ? 'rgba(201,162,39,0.3)' : sc.borderLight}`, borderRadius: 4, color: !newShopTheme ? sc.gold : sc.textDim }}>

                      🌐 Default

                    </button>

                    {Object.values(THEMES).map(t => (

                      <button key={t.id} className="shop-btn" onClick={() => setNewShopTheme(t.id)}

                        style={{ padding: '4px 8px', fontSize: '0.68rem', background: newShopTheme === t.id ? `${t.colors.accent}20` : 'transparent', border: `1px solid ${newShopTheme === t.id ? t.colors.accent : sc.borderLight}`, borderRadius: 4, color: newShopTheme === t.id ? t.colors.accent : sc.textDim }}

                        title={t.description || t.name}>

                        {t.preview} {t.name}

                      </button>

                    ))}

                  </div>

                </div>

                <button className="shop-btn" onClick={handleCreateShop} style={{ width: '100%', padding: '8px 16px', background: sc.greenBg, border: `1px solid ${sc.greenBorder}`, borderRadius: 6, color: sc.green, fontSize: '0.88rem', fontWeight: 600, opacity: newShopName.trim() ? 1 : 0.4 }} disabled={!newShopName.trim()}>🏪 Create Shop</button>

              </div>

            )}

            {/* Search + Filter toolbar */}

            <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>

              {/* Shop name search */}

              <div style={{ position: 'relative', flex: '1 1 140px', minWidth: 120 }}>

                <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: sc.textDim, fontSize: '0.75rem' }}>🔍</span>

                <input value={shopSearch} onChange={e => setShopSearch(e.target.value)} placeholder="Search shops..." className="shop-input"

                  style={{ width: '100%', background: sc.bgCard, border: `1px solid ${sc.borderLight}`, borderRadius: 4, padding: '5px 8px 5px 26px', color: sc.text, fontSize: '0.78rem' }} />

              </div>

              {/* Filter tabs */}

              {[

                { id: 'all', label: `All (${activeCampaign === 'all' ? shops.length : shops.filter(s => shopInCampaign(s)).length})`, tip: 'Show all shops' },

                { id: 'starred', label: `⭐ (${shops.filter(s => s.starred && (activeCampaign === 'all' || shopInCampaign(s))).length})`, tip: 'Show only active shops' },

              ].map(tab => (

                <button key={tab.id} className="shop-btn" onClick={() => setManagerFilter(tab.id)} title={tab.tip}

                  style={{ padding: '5px 10px', background: managerFilter === tab.id ? '#1a1a22' : 'transparent', border: `1px solid ${managerFilter === tab.id ? sc.borderActive : sc.borderLight}`, borderRadius: 4, color: managerFilter === tab.id ? sc.gold : sc.textMuted, fontSize: '0.72rem' }}>

                  {tab.label}

                </button>

              ))}

              {/* Sort buttons (one-shot) */}

              <button className="shop-btn" onClick={() => handleSortShops('alpha')} title="Sort shops alphabetically (A→Z)"

                style={{ padding: '5px 8px', background: 'transparent', border: `1px solid ${sc.borderLight}`, borderRadius: 4, color: sc.textMuted, fontSize: '0.72rem' }}>A→Z</button>

              <button className="shop-btn" onClick={() => handleSortShops('date')} title="Sort by newest created first"

                style={{ padding: '5px 8px', background: 'transparent', border: `1px solid ${sc.borderLight}`, borderRadius: 4, color: sc.textMuted, fontSize: '0.72rem' }}>🕐</button>

              <button className="shop-btn" onClick={() => setShowShopSettings(true)} title="Shop settings (rarities, categories, campaigns)"

                style={{ padding: '5px 8px', background: 'transparent', border: `1px solid ${sc.borderLight}`, borderRadius: 4, color: sc.textMuted, fontSize: '0.72rem' }}>⚙️</button>

            </div>

            {/* Campaign filter */}

            {campaigns.length > 0 && (

              <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>

                <span style={{ fontSize: '0.65rem', color: sc.textDim, marginRight: 2 }}>Campaign:</span>

                <button className="shop-btn" onClick={() => setActiveCampaign('all')}

                  style={{ padding: '3px 8px', fontSize: '0.68rem', background: activeCampaign === 'all' ? 'rgba(201,162,39,0.12)' : 'transparent', border: `1px solid ${activeCampaign === 'all' ? 'rgba(201,162,39,0.3)' : sc.borderLight}`, borderRadius: 10, color: activeCampaign === 'all' ? sc.gold : sc.textMuted }}>All</button>

                {campaigns.map(c => (

                  <button key={c} className="shop-btn" onClick={() => setActiveCampaign(c)}

                    style={{ padding: '3px 8px', fontSize: '0.68rem', background: activeCampaign === c ? 'rgba(201,162,39,0.12)' : 'transparent', border: `1px solid ${activeCampaign === c ? 'rgba(201,162,39,0.3)' : sc.borderLight}`, borderRadius: 10, color: activeCampaign === c ? sc.gold : sc.textMuted }}>{c}</button>

                ))}

              </div>

            )}

            {/* Tag filter row */}

            <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>

              {/* Tag search with autocomplete */}

              <div style={{ position: 'relative', flex: '1 1 140px', minWidth: 120 }}>

                <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: sc.textDim, fontSize: '0.7rem' }}>🏷</span>

                <input value={shopTagSearch} onChange={e => { setShopTagSearch(e.target.value); setShopTagSearchFocused(true); }}

                  onFocus={() => setShopTagSearchFocused(true)} onBlur={() => setTimeout(() => setShopTagSearchFocused(false), 150)}

                  placeholder="Search by tag..." className="shop-input"

                  onKeyDown={e => { if (e.key === 'Enter' && shopTagSearch.trim()) { setShopTagFilter(shopTagSearch.trim()); setShopTagSearch(''); setShopTagSearchFocused(false); } }}

                  style={{ width: '100%', background: sc.bgCard, border: `1px solid ${sc.borderLight}`, borderRadius: 4, padding: '5px 8px 5px 26px', color: sc.text, fontSize: '0.75rem' }} />

                {/* Autocomplete suggestions */}

                {shopTagSearchFocused && tagSuggestions.length > 0 && (

                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: sc.bgDarker, border: `1px solid ${sc.borderMed}`, borderRadius: '0 0 4px 4px', zIndex: 100, maxHeight: 150, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>

                    {tagSuggestions.map(t => (

                      <div key={t} onMouseDown={() => { setShopTagFilter(t); setShopTagSearch(''); setShopTagSearchFocused(false); }}

                        style={{ padding: '6px 10px', cursor: 'pointer', fontSize: '0.75rem', color: sc.textSub, borderBottom: `1px solid ${sc.borderLight}` }}

                        onMouseEnter={e => e.currentTarget.style.background = sc.bgCard}

                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                        🏷 {t}

                      </div>

                    ))}

                  </div>

                )}

              </div>

              {/* Tag dropdown (quick select) */}

              {allShopTags.length > 0 && (

                <select value={shopTagFilter} onChange={e => setShopTagFilter(e.target.value)} className="shop-input"

                  style={{ padding: '5px 8px', background: sc.bgCard, border: `1px solid ${sc.borderLight}`, borderRadius: 4, color: shopTagFilter ? sc.gold : sc.textSub, fontSize: '0.72rem' }}>

                  <option value="">All tags</option>

                  {allShopTags.map(t => <option key={t} value={t}>{t}</option>)}

                </select>

              )}

              {/* Active tag filter pill */}

              {shopTagFilter && (

                <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', background: 'rgba(201,162,39,0.15)', border: '1px solid rgba(201,162,39,0.3)', borderRadius: 12 }}>

                  <span style={{ fontSize: '0.7rem', color: sc.gold }}>🏷 {shopTagFilter}</span>

                  <button className="shop-btn" onClick={() => setShopTagFilter('')} style={{ background: 'none', border: 'none', color: sc.textMuted, fontSize: '0.7rem', cursor: 'pointer', padding: '0 2px' }}>✕</button>

                </div>

              )}

            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>

              {(() => {

                let list = [...shops];

                // Campaign filter

                if (activeCampaign !== 'all') list = list.filter(s => shopInCampaign(s));

                // Filter only — do NOT re-sort, preserve manual order from drag/arrows

                if (managerFilter === 'starred') list = list.filter(s => s.starred);

                if (shopTagFilter) list = list.filter(s => (s.tags || []).some(t => t.toLowerCase() === shopTagFilter.toLowerCase()));

                if (shopSearch.trim()) {

                  const q = shopSearch.toLowerCase();

                  list = list.filter(s => s.name.toLowerCase().includes(q) || (s.keeperName || '').toLowerCase().includes(q) || (s.type || '').toLowerCase().includes(q) || (s.tags || []).some(t => t.toLowerCase().includes(q)));

                }

                if (list.length === 0) {

                  return (

                    <div style={{ textAlign: 'center', padding: '20px', color: sc.textMuted, fontSize: '0.82rem' }}>

                      No shops match filters. <button className="shop-btn" onClick={() => { setManagerFilter('all'); setShopTagFilter(''); setShopSearch(''); }} style={{ background: 'none', border: 'none', color: sc.gold, cursor: 'pointer', fontSize: '0.82rem', textDecoration: 'underline' }}>Clear filters</button>

                    </div>

                  );

                }

                return list.map((s, listIdx) => {

                const isSelected = s.id === activeShopId;

                const secretCount = s.inventory.filter(i => (i.secrecy || 'visible') !== 'visible').length;

                const shopIdx = shops.findIndex(sh => sh.id === s.id);

                const isEditingTags = editingTagsShopId === s.id;

                return (

                  <div key={s.id}

                    draggable

                    onDragStart={e => { setDragShopId(s.id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', s.id); }}

                    onDragEnd={() => { setDragShopId(null); setDragOverShopId(null); }}

                    onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverShopId(s.id); }}

                    onDragLeave={() => { if (dragOverShopId === s.id) setDragOverShopId(null); }}

                    onDrop={e => { e.preventDefault(); handleDragDrop(dragShopId, s.id); setDragShopId(null); setDragOverShopId(null); }}

                    style={{ opacity: dragShopId === s.id ? 0.4 : 1, transition: 'opacity 0.15s' }}>

                    {/* Drop indicator */}

                    {dragOverShopId === s.id && dragShopId !== s.id && <div style={{ height: 2, background: sc.gold, borderRadius: 1, margin: '2px 0' }} />}

                    <div onClick={() => onSaveShopData({ ...shopData, activeShopId: s.id })} className="shop-btn"

                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 6, background: isSelected ? '#1a1a22' : sc.bgCard, border: `1px solid ${isSelected ? sc.borderActive : sc.borderLight}`, cursor: 'pointer' }}>

                      {/* Star (Active) on far left */}

                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, minWidth: 28 }} onClick={e => e.stopPropagation()}>

                        <button className="shop-btn" onClick={() => handleToggleStarred(s.id)}

                          style={{ padding: '1px 3px', background: 'none', border: 'none', fontSize: '0.75rem', cursor: 'pointer', color: s.starred ? '#b0b8c8' : '#3a3a40', lineHeight: 1 }}

                          title={s.starred ? 'Deactivate shop' : 'Activate shop'}>

                          ★

                        </button>

                        <span style={{ fontSize: '0.5rem', fontWeight: 600, color: s.starred ? sc.green : sc.textDim, letterSpacing: 0.3 }}>

                          {s.starred ? 'Active' : 'Inactive'}

                        </span>

                      </div>

                      {/* Name + info */}

                      <div style={{ flex: 1, minWidth: 0 }}>

                        <div style={{ fontSize: '0.92rem', fontWeight: 500, color: isSelected ? sc.textHead : sc.textSubLight, display: 'flex', alignItems: 'center', gap: 6 }}>

                          {s.name}

                          <span style={{ fontSize: '0.65rem', color: sc.textDim }}>{s.inventory.length} items{secretCount > 0 && ` · ${secretCount} secret`}</span>

                          {s.themeId && THEMES[s.themeId] && <span style={{ fontSize: '0.58rem', padding: '0 4px', background: `${THEMES[s.themeId].colors.accent}15`, border: `1px solid ${THEMES[s.themeId].colors.accent}40`, borderRadius: 6, color: THEMES[s.themeId].colors.accent }} title={`Theme: ${THEMES[s.themeId].name}`}>{THEMES[s.themeId].preview} {THEMES[s.themeId].name}</span>}

                        </div>

                        <div style={{ fontSize: '0.72rem', color: sc.textMuted, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>

                          {s.keeperName} · {s.type}

                          {s.mode && s.mode !== 'coin' && <span style={{ fontSize: '0.58rem', padding: '0 4px', background: 'rgba(184,160,96,0.12)', border: '1px solid rgba(184,160,96,0.25)', borderRadius: 6, color: '#b8a060' }}>{s.mode === 'trade' ? '⚖️ Trade' : '🪙⚖️ Both'}</span>}

                          {(s.campaigns || []).length > 0 && s.campaigns.map(c => (

                            <span key={c} style={{ fontSize: '0.58rem', padding: '0 4px', background: 'rgba(120,140,200,0.12)', border: '1px solid rgba(120,140,200,0.25)', borderRadius: 6, color: '#8a9ac8' }}>{c}</span>

                          ))}

                          {(s.tags || []).length > 0 && (

                            <span style={{ display: 'inline-flex', gap: 3, flexWrap: 'wrap' }}>

                              {s.tags.map(t => (

                                <span key={t} onClick={e => { e.stopPropagation(); setShopTagFilter(t); }} style={{ fontSize: '0.6rem', padding: '0 5px', background: 'rgba(201,162,39,0.12)', border: '1px solid rgba(201,162,39,0.25)', borderRadius: 8, color: sc.gold, cursor: 'pointer' }} title={`Filter by "${t}"`}>{t}</span>

                              ))}

                            </span>

                          )}

                        </div>

                      </div>

                      {/* Open/Closed toggle - no eye icon */}

                      <button className="shop-btn" onClick={e => { e.stopPropagation(); handleToggleShopOpen(s.id); }}

                        style={{ padding: '3px 10px', background: s.isOpen ? sc.greenBg : sc.redBg, border: `1px solid ${s.isOpen ? sc.greenBorder : sc.redBorder}`, borderRadius: 12, color: s.isOpen ? sc.green : sc.red, fontSize: '0.7rem', flexShrink: 0 }}

                        title={s.isOpen ? 'Close shop (hide from players)' : 'Open shop (visible to players)'}>

                        {s.isOpen ? 'Open' : '🔒 Closed'}

                      </button>

                      {/* Drag handle + reorder arrows */}

                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>

                          <button className="shop-btn" onClick={() => handleMoveShop(s.id, -1)} disabled={shopIdx <= 0}

                            style={{ padding: '3px 10px', background: shopIdx > 0 ? 'rgba(255,255,255,0.05)' : 'transparent', border: `1px solid ${shopIdx > 0 ? sc.borderLight : 'transparent'}`, borderRadius: 3, fontSize: '0.75rem', color: shopIdx > 0 ? sc.textSub : sc.textDim, cursor: shopIdx > 0 ? 'pointer' : 'default', lineHeight: 1 }}

                            title="Move up">▲</button>

                          <button className="shop-btn" onClick={() => handleMoveShop(s.id, 1)} disabled={shopIdx >= shops.length - 1}

                            style={{ padding: '3px 10px', background: shopIdx < shops.length - 1 ? 'rgba(255,255,255,0.05)' : 'transparent', border: `1px solid ${shopIdx < shops.length - 1 ? sc.borderLight : 'transparent'}`, borderRadius: 3, fontSize: '0.75rem', color: shopIdx < shops.length - 1 ? sc.textSub : sc.textDim, cursor: shopIdx < shops.length - 1 ? 'pointer' : 'default', lineHeight: 1 }}

                            title="Move down">▼</button>

                        </div>

                        <div style={{ cursor: 'grab', padding: '6px 4px', color: sc.textDim, fontSize: '1rem', userSelect: 'none' }} title="Drag to reorder">⠿</div>

                      </div>

                    </div>

                    {isSelected && (

                      <div style={{ padding: '8px 14px' }}>

                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: isEditingTags ? 8 : 0 }}>

                          <button className="shop-btn" onClick={() => setDmTab('shop')} title="View shop inventory and edit items" style={{ padding: '4px 10px', background: '#1a1a2a', border: `1px solid #2a2a4a`, borderRadius: 4, color: sc.blue, fontSize: '0.72rem' }}>🏪 View & Edit</button>

                          <button className="shop-btn" onClick={() => { setDmTab('shop'); setTimeout(() => setShowLibrary(true), 100); }} title="Add items from the template library" style={{ padding: '4px 10px', background: sc.greenBg, border: `1px solid ${sc.greenBorder}`, borderRadius: 4, color: sc.green, fontSize: '0.72rem' }}>📚 Add Items</button>

                          <button className="shop-btn" onClick={() => handleDeleteShop(s.id)} title="Permanently delete this shop" style={{ padding: '4px 10px', background: sc.redBg, border: `1px solid ${sc.redBorder}`, borderRadius: 4, color: sc.red, fontSize: '0.72rem' }}>🗑 Delete</button>

                          <div style={{ width: 16 }} />

                          {/* Add tag input — standalone, does NOT open edit panel */}

                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} onClick={e => e.stopPropagation()}>

                            <input value={s.id === editingTagsShopId || s.id === addingTagShopId ? newTagInput : ''} 

                              onChange={e => { setAddingTagShopId(s.id); setNewTagInput(e.target.value); }}

                              onFocus={() => { if (addingTagShopId !== s.id) { setAddingTagShopId(s.id); setNewTagInput(''); } }}

                              onKeyDown={e => { if (e.key === 'Enter' && newTagInput.trim()) { handleAddTagToShop(s.id, newTagInput); setNewTagInput(''); } }}

                              placeholder="add tag ↵" className="shop-input"

                              style={{ width: 90, background: sc.bgBar, border: `1px solid ${sc.borderLight}`, borderRadius: 4, padding: '3px 8px', color: sc.textSub, fontSize: '0.68rem' }} />

                          </div>

                          {/* Edit Tags button */}

                          {(s.tags || []).length > 0 && (

                            <button className="shop-btn" onClick={e => { e.stopPropagation(); setEditingTagsShopId(isEditingTags ? null : s.id); setRenamingTag(null); }}

                              title={isEditingTags ? 'Close tag editor' : 'Edit, rename, or remove tags'}

                              style={{ padding: '3px 8px', background: isEditingTags ? 'rgba(201,162,39,0.15)' : 'transparent', border: `1px solid ${isEditingTags ? 'rgba(201,162,39,0.3)' : sc.borderLight}`, borderRadius: 4, color: isEditingTags ? sc.gold : sc.textDim, fontSize: '0.68rem', cursor: 'pointer' }}>

                              🏷 {isEditingTags ? 'Done' : 'Edit Tags'}

                            </button>

                          )}

                        </div>

                        {shopWalletAdjustmentAuthorityEnabled && (

                          <div onClick={e => e.stopPropagation()} style={{ marginTop: 8, padding: '8px 10px', background: 'rgba(0,0,0,0.18)', border: `1px solid ${sc.borderLight}`, borderRadius: 6, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 6, alignItems: 'center' }}>

                            <div style={{ minWidth: 0 }}>

                              <div style={{ fontSize: '0.62rem', color: sc.textDim, textTransform: 'uppercase', letterSpacing: 0.5 }}>Shop Wallet</div>

                              <div aria-label={`${s.name} shop wallet balance`} style={{ fontSize: '0.84rem', color: sc.gold, fontWeight: 600 }}>{formatPrice(getShopWalletBalanceBase(s))}</div>

                            </div>

                            <input aria-label={`Wallet adjustment for ${s.name}`} value={shopWalletAdjustmentDrafts[s.id] || ''} onChange={e => handleShopWalletAdjustmentDraft(s.id, e.target.value)} placeholder="+/- base" type="number" className="shop-input" style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '4px 8px', color: sc.textSub, fontSize: '0.72rem' }} />

                            <input aria-label={`Wallet adjustment reason for ${s.name}`} value={shopWalletAdjustmentReasonDrafts[s.id] || ''} onChange={e => handleShopWalletReasonDraft(s.id, e.target.value)} placeholder="Reason" className="shop-input" style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '4px 8px', color: sc.textSub, fontSize: '0.72rem' }} />

                            <button className="shop-btn" onClick={() => handleAdjustShopWallet(s)} title="Apply shop wallet adjustment" style={{ padding: '4px 10px', background: sc.blueBg, border: `1px solid ${sc.blueBorder}`, borderRadius: 4, color: sc.blue, fontSize: '0.72rem', whiteSpace: 'nowrap' }}>Apply</button>

                          </div>

                        )}

                        {shopSellBackAuthorityEnabled && (() => {
                          const draft = getShopSellBackDraft(s.id);
                          const sellBackChars = Object.values(allChars || {}).filter(c => c?.id && c.id !== 'dm');
                          const eligibleItems = getSellBackEligibleItems(draft.charId);
                          const selectedItem = eligibleItems.find(({ item }) => item.id === draft.itemEntryId);
                          const maxSellBackQuantity = selectedItem?.availableQuantity || 1;

                          return (
                            <div onClick={e => e.stopPropagation()} style={{ marginTop: 8, padding: '8px 10px', background: 'rgba(0,0,0,0.18)', border: `1px solid ${sc.borderLight}`, borderRadius: 6, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 6, alignItems: 'center' }}>

                              <div style={{ minWidth: 0 }}>

                                <div style={{ fontSize: '0.62rem', color: sc.textDim, textTransform: 'uppercase', letterSpacing: 0.5 }}>Direct Sell-Back</div>

                                <div style={{ fontSize: '0.72rem', color: sc.textMuted }}>Receipt-only, DM approved</div>

                              </div>

                              <select aria-label={`Sell-back character for ${s.name}`} value={draft.charId} onChange={e => handleShopSellBackDraft(s.id, { charId: e.target.value, itemEntryId: '', quantity: '1' })} className="shop-input" style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '4px 8px', color: sc.textSub, fontSize: '0.72rem' }}>

                                <option value="">Character</option>

                                {sellBackChars.map(c => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}

                              </select>

                              <select aria-label={`Sell-back item for ${s.name}`} value={draft.itemEntryId} onChange={e => handleShopSellBackDraft(s.id, { itemEntryId: e.target.value, quantity: '1' })} className="shop-input" style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '4px 8px', color: sc.textSub, fontSize: '0.72rem' }}>

                                <option value="">{draft.charId ? 'Eligible item' : 'Pick character'}</option>

                                {eligibleItems.map(({ item, availableQuantity }) => (
                                  <option key={item.id} value={item.id}>{item.name || item.id} x{availableQuantity}</option>
                                ))}

                              </select>

                              <input aria-label={`Sell-back quantity for ${s.name}`} value={draft.quantity} onChange={e => handleShopSellBackDraft(s.id, { quantity: e.target.value })} type="number" min="1" max={maxSellBackQuantity} className="shop-input" style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '4px 8px', color: sc.textSub, fontSize: '0.72rem' }} />

                              <input aria-label={`Sell-back payout for ${s.name}`} value={draft.payoutBase} onChange={e => handleShopSellBackDraft(s.id, { payoutBase: e.target.value })} placeholder="Payout base" type="number" min="0" className="shop-input" style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '4px 8px', color: sc.textSub, fontSize: '0.72rem' }} />

                              <button className="shop-btn" onClick={() => handleSellInventoryItemToShopForDm(s)} disabled={!draft.charId || !draft.itemEntryId} title="Approve direct sell-back" style={{ padding: '4px 10px', background: (!draft.charId || !draft.itemEntryId) ? '#1a1a1a' : sc.goldDark, border: `1px solid ${(!draft.charId || !draft.itemEntryId) ? sc.borderMed : sc.goldBorder}`, borderRadius: 4, color: (!draft.charId || !draft.itemEntryId) ? sc.textDim : sc.gold, fontSize: '0.72rem', whiteSpace: 'nowrap', opacity: (!draft.charId || !draft.itemEntryId) ? 0.55 : 1 }}>Sell</button>

                            </div>
                          );
                        })()}

                        {/* Tag editor panel — only visible when editing */}

                        {isEditingTags && (s.tags || []).length > 0 && (

                          <div onClick={e => e.stopPropagation()} style={{ background: 'rgba(0,0,0,0.2)', border: `1px solid ${sc.borderLight}`, borderRadius: 6, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>

                            <div style={{ fontSize: '0.62rem', color: sc.textDim, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>Tags — click to rename, ✕ to remove</div>

                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>

                              {(s.tags || []).map(t => (

                                <span key={t} style={{ fontSize: '0.7rem', padding: '3px 8px', background: 'rgba(201,162,39,0.12)', border: '1px solid rgba(201,162,39,0.25)', borderRadius: 10, color: sc.gold, display: 'inline-flex', alignItems: 'center', gap: 5 }}>

                                  {renamingTag?.shopId === s.id && renamingTag?.oldTag === t ? (

                                    <input value={renamingTag.newTag} onChange={e => setRenamingTag({ ...renamingTag, newTag: e.target.value })}

                                      onBlur={() => { handleRenameTagInShop(s.id, t, renamingTag.newTag); setRenamingTag(null); }}

                                      onKeyDown={e => { if (e.key === 'Enter') { handleRenameTagInShop(s.id, t, renamingTag.newTag); setRenamingTag(null); } if (e.key === 'Escape') setRenamingTag(null); }}

                                      autoFocus className="shop-input"

                                      style={{ width: Math.max(50, renamingTag.newTag.length * 7 + 10), background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '1px 4px', color: sc.gold, fontSize: '0.7rem' }} />

                                  ) : (

                                    <span onClick={() => setRenamingTag({ shopId: s.id, oldTag: t, newTag: t })} style={{ cursor: 'pointer' }} title="Click to rename">{t}</span>

                                  )}

                                  <button className="shop-btn" onClick={() => handleRemoveTagFromShop(s.id, t)}

                                    style={{ background: 'none', border: 'none', color: '#b86a6a', fontSize: '0.65rem', padding: 0, cursor: 'pointer', lineHeight: 1 }} title={`Remove "${t}"`}>✕</button>

                                </span>

                              ))}

                            </div>

                          </div>

                        )}

                        {/* Campaign assignment */}

                        {campaigns.length > 0 && (

                          <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 }}>

                            <span style={{ fontSize: '0.62rem', color: sc.textDim }}>🗺️</span>

                            <button className="shop-btn" onClick={() => onSaveShopData({ ...shopData, shops: shops.map(sh => sh.id === s.id ? { ...sh, campaigns: (sh.campaigns || []).includes('general') ? (sh.campaigns || []).filter(x => x !== 'general') : [...(sh.campaigns || []).filter(x => x !== 'general'), 'general'] } : sh) })}

                              style={{ padding: '2px 6px', fontSize: '0.6rem', background: (s.campaigns || []).includes('general') ? 'rgba(201,162,39,0.12)' : 'transparent', border: `1px solid ${(s.campaigns || []).includes('general') ? 'rgba(201,162,39,0.3)' : sc.borderLight}`, borderRadius: 4, color: (s.campaigns || []).includes('general') ? sc.gold : sc.textDim }}>🌐 All</button>

                            {campaigns.map(c => {

                              const sel = (s.campaigns || []).includes(c);

                              return <button key={c} className="shop-btn" onClick={() => {

                                const cur = s.campaigns || [];

                                const next = sel ? cur.filter(x => x !== c) : [...cur, c];

                                onSaveShopData({ ...shopData, shops: shops.map(sh => sh.id === s.id ? { ...sh, campaigns: next } : sh) });

                              }}

                                style={{ padding: '2px 6px', fontSize: '0.6rem', background: sel ? 'rgba(120,140,200,0.15)' : 'transparent', border: `1px solid ${sel ? 'rgba(120,140,200,0.35)' : sc.borderLight}`, borderRadius: 4, color: sel ? '#8a9ac8' : sc.textDim }}>{c}</button>;

                            })}

                          </div>

                        )}

                        {/* Per-shop theme override */}

                        <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 }}>

                          <span style={{ fontSize: '0.62rem', color: sc.textDim }}>🎨</span>

                          <button className="shop-btn" onClick={() => onSaveShopData({ ...shopData, shops: shops.map(sh => sh.id === s.id ? { ...sh, themeId: null } : sh) })}

                            style={{ padding: '2px 6px', fontSize: '0.6rem', background: !s.themeId ? 'rgba(201,162,39,0.12)' : 'transparent', border: `1px solid ${!s.themeId ? 'rgba(201,162,39,0.3)' : sc.borderLight}`, borderRadius: 4, color: !s.themeId ? sc.gold : sc.textDim }}>

                            🌐 Default

                          </button>

                          {Object.values(THEMES).map(t => (

                            <button key={t.id} className="shop-btn" onClick={() => onSaveShopData({ ...shopData, shops: shops.map(sh => sh.id === s.id ? { ...sh, themeId: t.id } : sh) })}

                              style={{ padding: '2px 6px', fontSize: '0.6rem', background: s.themeId === t.id ? `${t.colors.accent}20` : 'transparent', border: `1px solid ${s.themeId === t.id ? t.colors.accent : sc.borderLight}`, borderRadius: 4, color: s.themeId === t.id ? t.colors.accent : sc.textDim }}

                              title={t.description || t.name}>

                              {t.preview}

                            </button>

                          ))}

                        </div>

                      </div>

                    )}

                  </div>

                );

              });

              })()}

            </div>

          </div>

        )}



        {/* Shop selector (when in shop view with multiple shops) */}

        {isDM && dmTab === 'shop' && shops.length > 1 && (

          <div style={{ marginBottom: 16 }}>

            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>

              {(showAllShopsInView ? shops : shops.filter(s => s.starred)).filter(s => shopInCampaign(s)).map(s => (

                <button key={s.id} className="shop-cat-btn" onClick={() => onSaveShopData({ ...shopData, activeShopId: s.id })}

                  style={{ padding: '5px 12px', borderRadius: 6, background: s.id === activeShopId ? '#1a1a22' : 'transparent', border: `1px solid ${s.id === activeShopId ? sc.borderActive : sc.borderLight}`, color: s.id === activeShopId ? sc.gold : sc.textMuted, fontSize: '0.78rem', cursor: 'pointer', opacity: s.starred ? 1 : 0.5 }}>

                  {s.name} {!s.isOpen && '🔒'} {!s.starred && '(inactive)'}

                </button>

              ))}

              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.68rem', color: sc.textDim, cursor: 'pointer', marginLeft: 8 }}>

                <input type="checkbox" checked={showAllShopsInView} onChange={e => setShowAllShopsInView(e.target.checked)} />

                Show all

              </label>

            </div>

          </div>

        )}



        {/* Player shop selector (when multiple open shops exist) */}

        {!isDM && (() => {

          const openShops = shops.filter(s => isShopVisibleToPlayer(s) && shopInCampaign(s));

          return openShops.length > 1 ? (

            <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>

              {openShops.map(s => (

                <button key={s.id} className="shop-cat-btn" onClick={() => onSaveShopData({ ...shopData, activeShopId: s.id })}

                  style={{ padding: '5px 12px', borderRadius: 6, background: s.id === activeShopId ? '#1a1a22' : 'transparent', border: `1px solid ${s.id === activeShopId ? sc.borderActive : sc.borderLight}`, color: s.id === activeShopId ? sc.gold : sc.textMuted, fontSize: '0.78rem', cursor: 'pointer' }}>

                  🏪 {s.name}

                </button>

              ))}

            </div>

          ) : null;

        })()}



        {/* Shop not visible to player */}

        {(!isDM || dmTab === 'shop') && !shopVisible && shop && (

          <div style={{ textAlign: 'center', padding: '80px 20px' }}>

            <div style={{ fontSize: '2rem', marginBottom: 12 }}>🚪</div>

            <div style={{ fontSize: '1.1rem', color: sc.textMuted }}>{shop.isOpen ? 'This shop is not currently available' : 'This shop is currently closed'}</div>

            <div style={{ fontSize: '0.85rem', color: sc.textDim, marginTop: 8 }}>The DM hasn't made this shop available yet.</div>

          </div>

        )}

        {!isDM && !shop && shops.length > 0 && (

          <div style={{ textAlign: 'center', padding: '80px 20px' }}>

            <div style={{ fontSize: '2rem', marginBottom: 12 }}>🏪</div>

            <div style={{ fontSize: '1.1rem', color: sc.textMuted }}>No shops available</div>

            <div style={{ fontSize: '0.85rem', color: sc.textDim, marginTop: 8 }}>The DM hasn't opened any shops yet.</div>

          </div>

        )}



        {/* Shop Content */}

        {(!isDM || dmTab === 'shop') && (shopVisible || effectiveIsDM) && shop && (

          <>

            {/* Shop Header */}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20, paddingBottom: 14, borderBottom: `1px solid ${sc.borderLight}` }}>

              <div>

                <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: sc.textHead, margin: 0, lineHeight: 1.2 }}>{shop.name}</h2>

                <div style={{ fontSize: '0.82rem', color: sc.textMuted, marginTop: 3 }}>

                  {shop.keeperName && <>Keeper: <span style={{ color: sc.textSub }}>{shop.keeperName}</span> · </>}

                  {shop.type} · {categoryCounts.all || 0} items{effectiveIsDM && ` (${shop.inventory.length} total)`}

                </div>

                {shop.description && <div style={{ fontSize: '0.78rem', fontStyle: 'italic', color: sc.textDim, marginTop: 3 }}>{shop.description}</div>}

              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

                {effectiveIsDM && <button className="shop-btn" onClick={() => setShowLibrary(true)} style={{ padding: '6px 14px', background: sc.greenBg, border: `1px solid ${sc.greenBorder}`, borderRadius: 6, color: sc.green, fontSize: '0.82rem' }}>📚 Add Items</button>}

                {effectiveIsDM && <button className="shop-btn" onClick={() => openNewItemEditor()} style={{ padding: '6px 14px', background: '#2a1a2a', border: '1px solid #5a3a5a', borderRadius: 6, color: '#c87ae0', fontSize: '0.82rem' }}>✨ Create</button>}

                {effectiveIsDM && shop?.inventory.length > 0 && (

                  <select className="shop-input" onChange={e => { if (e.target.value) { openNewItemEditor(e.target.value); e.target.value = ''; } }} defaultValue=""

                    style={{ width: 'auto', padding: '6px 8px', background: '#1a1a2a', border: '1px solid #3a3a5a', borderRadius: 6, color: '#9a9ac8', fontSize: '0.75rem', cursor: 'pointer' }}>

                    <option value="">📋 From...</option>

                    {shop.inventory.map((inv, index) => { const key = getShopInventoryLineKey(inv, index); const t = getShopTemplateForInventoryLine(inv); return t ? <option key={key} value={key}>{t.name}</option> : null; })}

                  </select>

                )}

                {effectiveIsDM && <button className="shop-btn" onClick={() => { setShopNotesText(shop.dmNotes || ''); setShowShopNotes(!showShopNotes); }}

                  style={{ padding: '6px 10px', background: shop.dmNotes ? 'rgba(201,162,39,0.08)' : 'transparent', border: `1px solid ${shop.dmNotes ? 'rgba(201,162,39,0.25)' : sc.borderLight}`, borderRadius: 6, color: shop.dmNotes ? sc.gold : sc.textDim, fontSize: '0.82rem' }}

                  title="DM notes for this shop">📝</button>}

                {!effectiveIsDM && char && (

                  <>

                    <button className="shop-btn" onClick={() => setShowOfferModal(true)} style={{ padding: '6px 14px', background: sc.goldDark, border: `1px solid ${sc.goldBorder}`, borderRadius: 6, color: sc.textHead, fontSize: '0.82rem' }}>⚖️ Trade</button>

                    <button className="shop-btn" aria-label="Open cart" onClick={() => setShowCart(true)} style={{ padding: '6px 14px', background: '#1a1a20', border: '1px solid #2a2a30', borderRadius: 6, color: sc.text, fontSize: '0.82rem', position: 'relative' }}>

                      🎒 {cartCount > 0 && <span style={{ position: 'absolute', top: -6, right: -6, background: sc.gold, color: sc.bg, borderRadius: '50%', width: 18, height: 18, fontSize: '0.65rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'cartBounce 0.3s ease' }}>{cartCount}</span>}

                    </button>

                  </>

                )}

              </div>

            </div>



            {/* DM: Player Visibility Control */}

            {effectiveIsDM && (

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, padding: '8px 12px', background: 'rgba(0,0,0,0.15)', borderRadius: 6, border: `1px solid ${sc.borderLight}`, flexWrap: 'wrap' }}>

                <span style={{ fontSize: '0.72rem', color: sc.textMuted }}>Visible to:</span>

                <button className="shop-btn" onClick={() => {

                  const newShops = shops.map(s => s.id === shop.id ? { ...s, visibleTo: [] } : s);

                  onSaveShopData({ ...shopData, shops: newShops });

                }}

                  style={{ padding: '3px 8px', background: (!shop.visibleTo || shop.visibleTo.length === 0) ? 'rgba(80,180,80,0.15)' : 'transparent', border: `1px solid ${(!shop.visibleTo || shop.visibleTo.length === 0) ? 'rgba(80,180,80,0.3)' : sc.borderLight}`, borderRadius: 4, color: (!shop.visibleTo || shop.visibleTo.length === 0) ? sc.green : sc.textDim, fontSize: '0.68rem', cursor: 'pointer' }}

                  title="All players can see this shop when open">All Players</button>

                {Object.values(allChars).filter(c => c.id !== 'dm').map(c => {

                  const isVis = !shop.visibleTo || shop.visibleTo.length === 0 || shop.visibleTo.includes(c.id);

                  const isRestricted = shop.visibleTo && shop.visibleTo.length > 0;

                  return (

                    <button key={c.id} className="shop-btn" onClick={() => {

                      let vis = shop.visibleTo ? [...shop.visibleTo] : [];

                      if (vis.length === 0) {

                        // Switching from "all" to specific — start with just this player

                        vis = [c.id];

                      } else if (vis.includes(c.id)) {

                        vis = vis.filter(id => id !== c.id);

                        if (vis.length === 0) vis = []; // back to all

                      } else {

                        vis.push(c.id);

                      }

                      const newShops = shops.map(s => s.id === shop.id ? { ...s, visibleTo: vis } : s);

                      onSaveShopData({ ...shopData, shops: newShops });

                    }}

                      style={{ padding: '3px 8px', background: (isRestricted && isVis) ? 'rgba(80,180,80,0.15)' : (isRestricted && !isVis) ? 'rgba(180,80,80,0.1)' : 'transparent', border: `1px solid ${(isRestricted && isVis) ? 'rgba(80,180,80,0.3)' : sc.borderLight}`, borderRadius: 4, color: (isRestricted && isVis) ? sc.green : (isRestricted && !isVis) ? '#8a5a5a' : sc.textSub, fontSize: '0.68rem', cursor: 'pointer' }}

                      title={isRestricted ? (isVis ? `${c.name} can see this shop` : `${c.name} cannot see this shop`) : `Click to restrict visibility to specific players`}>

                      {c.name}

                    </button>

                  );

                })}

                {shop.visibleTo && shop.visibleTo.length > 0 && (

                  <span style={{ fontSize: '0.62rem', color: '#b87a5a', marginLeft: 4 }}>⚠ Restricted</span>

                )}

              </div>

            )}

            {/* DM Shop Notes */}

            {effectiveIsDM && showShopNotes && (

              <div style={{ marginBottom: 16, padding: 12, background: 'rgba(201,162,39,0.04)', borderRadius: 6, border: `1px solid rgba(201,162,39,0.15)`, animation: 'shopFadeIn 0.15s ease' }}>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>

                  <span style={{ fontSize: '0.75rem', color: sc.gold, fontWeight: 500 }}>📝 DM Notes</span>

                  <button className="shop-btn" onClick={() => { saveShop({ ...shop, dmNotes: shopNotesText }); setShowShopNotes(false); showToast('Notes saved'); }}

                    style={{ padding: '3px 10px', background: 'rgba(201,162,39,0.1)', border: '1px solid rgba(201,162,39,0.3)', borderRadius: 4, color: sc.gold, fontSize: '0.7rem' }}>Save & Close</button>

                </div>

                <textarea value={shopNotesText} onChange={e => setShopNotesText(e.target.value)}

                  placeholder="e.g. The passphrase for black market items is 'shadows guide the weary.' Torbin will only reveal secret-tier items if players mention the Merchant's Guild."

                  style={{ width: '100%', minHeight: 80, background: sc.bgDark, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '8px 10px', color: sc.text, fontSize: '0.82rem', resize: 'vertical', lineHeight: 1.5 }} />

                <div style={{ fontSize: '0.62rem', color: sc.textDim, marginTop: 4 }}>Only visible to the DM. Track passwords, secret item conditions, NPC behavior, etc.</div>

              </div>

            )}

            {(effectiveIsDM || visiblePendingOffers.length > 0) && visiblePendingOffers.length > 0 && (

              <div style={{ marginBottom: 20 }}>

                <div style={{ fontSize: '0.8rem', color: sc.gold, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>📨 Pending Offers ({visiblePendingOffers.length})</div>

                {visiblePendingOffers.map(offer => {
                  const isServerOffer = offer.source === 'shop-trade-offer';
                  const canAcceptServerOffer = isServerOffer && effectiveIsDM && tradeOfferSettleAuthorityEnabled;
                  const canDenyServerOffer = isServerOffer && effectiveIsDM && tradeOfferDenyAuthorityEnabled;
                  const givingItems = Array.isArray(offer.giving?.items) ? offer.giving.items : [];
                  const wantingItems = Array.isArray(offer.wanting?.items) ? offer.wanting.items : [];
                  return (

                  <div key={offer.id} style={{ background: sc.bgDark, border: `1px solid ${sc.goldDark}`, borderRadius: 6, padding: 12, marginBottom: 8, animation: 'shopFadeIn 0.3s ease' }}>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>

                      <span style={{ fontWeight: 600, color: sc.textHead, fontSize: '0.9rem' }}>{offer.charName}</span>

                      <span className="offer-pulse" style={{ fontSize: '0.65rem', color: sc.gold, background: sc.goldDark, padding: '2px 8px', borderRadius: 10 }}>Pending</span>

                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 8, marginBottom: 8, fontSize: '0.82rem' }}>

                      <div><div style={{ color: '#b87a6a', fontSize: '0.7rem', marginBottom: 2 }}>GIVES</div>{givingItems.map((i, idx) => <div key={idx} style={{ color: sc.textSubLight }}>{i.name} ×{i.qty}</div>)}{offer.giving?.gold > 0 && <div style={{ color: sc.gold }}>{formatPrice(offer.giving.gold)}</div>}</div>

                      <div style={{ color: sc.textDim, alignSelf: 'center', fontSize: '1.2rem' }}>⇄</div>

                      <div><div style={{ color: '#7ab87a', fontSize: '0.7rem', marginBottom: 2 }}>WANTS</div>{wantingItems.map((i, idx) => <div key={idx} style={{ color: sc.textSubLight }}>{i.name} ×{i.qty}</div>)}{offer.wanting?.gold > 0 && <div style={{ color: sc.gold }}>{formatPrice(offer.wanting.gold)}</div>}</div>

                    </div>

                    {offer.note && <div style={{ fontSize: '0.78rem', fontStyle: 'italic', color: sc.textMuted, marginBottom: 8, padding: '4px 8px', background: sc.bgCard, borderRadius: 4 }}>"{offer.note}"</div>}

                    <div style={{ display: 'flex', gap: 8 }}>

                      {isServerOffer && !effectiveIsDM ? (
                        <button className="shop-btn" onClick={() => handleResolveOffer(offer.id, 'cancelled')} style={{ flex: 1, padding: '6px 12px', background: sc.redBg, border: `1px solid ${sc.redBorder}`, borderRadius: 4, color: sc.red, fontSize: '0.82rem' }}>✕ Cancel</button>
                      ) : (
                        <>
                          <button className="shop-btn" onClick={() => handleResolveOffer(offer.id, 'accepted')} disabled={isServerOffer && !canAcceptServerOffer} title={isServerOffer && !canAcceptServerOffer ? 'Trade settlement is not available yet' : undefined} style={{ flex: 1, padding: '6px 12px', background: isServerOffer && !canAcceptServerOffer ? '#1a1a1a' : sc.greenBg, border: `1px solid ${isServerOffer && !canAcceptServerOffer ? sc.borderMed : sc.greenBorder}`, borderRadius: 4, color: isServerOffer && !canAcceptServerOffer ? sc.textDim : sc.green, fontSize: '0.82rem', fontWeight: 600, opacity: isServerOffer && !canAcceptServerOffer ? 0.55 : 1 }}>✓ Accept</button>

                          <button className="shop-btn" onClick={() => handleResolveOffer(offer.id, 'denied')} disabled={isServerOffer && !canDenyServerOffer} title={isServerOffer && !canDenyServerOffer ? 'Trade denial is not available yet' : undefined} style={{ flex: 1, padding: '6px 12px', background: sc.redBg, border: `1px solid ${sc.redBorder}`, borderRadius: 4, color: sc.red, fontSize: '0.82rem', opacity: isServerOffer && !canDenyServerOffer ? 0.55 : 1 }}>✕ Deny</button>
                        </>
                      )}

                    </div>

                  </div>

                )})}

              </div>

            )}

            {visibleTerminalOffers.length > 0 && (

              <div style={{ marginBottom: 18 }}>

                <div style={{ fontSize: '0.76rem', color: sc.textMuted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Offer History ({visibleTerminalOffers.length})</div>

                <div style={{ display: 'grid', gap: 6 }}>

                  {visibleTerminalOffers.slice(0, 8).map(offer => {
                    const givingItems = Array.isArray(offer.giving?.items) ? offer.giving.items : [];
                    const wantingItems = Array.isArray(offer.wanting?.items) ? offer.wanting.items : [];
                    const statusColor = offer.status === 'denied' ? sc.red : sc.textDim;
                    return (

                      <div key={offer.id} style={{ padding: '8px 10px', background: 'rgba(0,0,0,0.14)', border: `1px solid ${sc.borderLight}`, borderRadius: 6 }}>

                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', marginBottom: 4 }}>

                          <span style={{ fontSize: '0.78rem', color: sc.textSubLight }}>{offer.charName || offer.char || offer.charId || 'Character'}</span>

                          <span style={{ fontSize: '0.65rem', color: statusColor, textTransform: 'uppercase', letterSpacing: 0.5 }}>{offer.status}</span>

                        </div>

                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: '0.72rem', color: sc.textDim }}>

                          <span>Gave: {summarizeTradeSide({ ...offer.giving, items: givingItems })}</span>

                          <span>Wanted: {summarizeTradeSide({ ...offer.wanting, items: wantingItems })}</span>

                        </div>

                        {offer.note && <div style={{ fontSize: '0.68rem', fontStyle: 'italic', color: sc.textDim, marginTop: 4 }}>"{offer.note}"</div>}

                      </div>

                    );
                  })}

                </div>

              </div>

            )}



            {/* Toolbar */}

            <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>

              <div style={{ position: 'relative', flex: '1 1 180px' }}>

                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: sc.textDim, fontSize: '0.85rem' }}>🔍</span>

                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search items..." className="shop-input"

                  style={{ width: '100%', background: sc.bgCard, border: `1px solid ${sc.border}`, borderRadius: 6, padding: '8px 12px 8px 32px', color: sc.text, fontSize: '0.85rem' }} />

              </div>

              <div style={{ display: 'flex', background: sc.bgCard, borderRadius: 6, border: `1px solid ${sc.border}`, overflow: 'hidden' }}>

                {[{ id: 'grid', icon: '⊞' }, { id: 'list', icon: '☰' }].map(v => (

                  <button key={v.id} className="shop-btn" onClick={() => setDisplayMode(v.id)}

                    style={{ padding: '6px 12px', background: displayMode === v.id ? '#1a1a22' : 'transparent', border: 'none', color: displayMode === v.id ? sc.text : sc.textDim, fontSize: '0.9rem', borderRadius: 0 }}>{v.icon}</button>

                ))}

              </div>

              {displayMode === 'grid' && effectiveIsDM && (

                <button className="shop-btn" onClick={cycleCardSize} title={`Card size: ${cardSize}px`}

                  style={{ padding: '6px 10px', background: sc.bgCard, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.textMuted, fontSize: '0.72rem' }}>

                  {cardSize <= 140 ? '🔲 S' : cardSize <= 160 ? '🔲 M' : cardSize <= 200 ? '🔲 L' : '🔲 XL'}

                </button>

              )}

            </div>



            {/* Category filters */}

            <div style={{ display: 'flex', gap: 5, marginBottom: 18, flexWrap: 'wrap' }}>

              {mergedCategories.filter(c => c.id === 'all' || categoryCounts[c.id]).map(c => (

                <button key={c.id} className="shop-cat-btn" onClick={() => setFilter(c.id)}

                  style={{ padding: '5px 12px', borderRadius: 16, background: filter === c.id ? '#1e1e24' : 'transparent', border: `1px solid ${filter === c.id ? sc.borderActive : sc.borderLight}`, color: filter === c.id ? sc.text : sc.textMuted, fontSize: '0.78rem', cursor: 'pointer' }}>

                  {c.icon} {c.name} {categoryCounts[c.id] !== undefined && <span style={{ marginLeft: 4, fontSize: '0.65rem', color: filter === c.id ? sc.textSubLight : sc.textDim }}>{categoryCounts[c.id]}</span>}

                </button>

              ))}

            </div>



            {/* Items */}

            {filteredInventory.length === 0 ? (

              <div style={{ textAlign: 'center', padding: '50px 20px', color: sc.textDim, fontStyle: 'italic' }}>

                {shop.inventory.length === 0 ? 'No items in this shop yet. Use "📚 Add Items" to stock it.' : 'No items match your search.'}

              </div>

            ) : displayMode === 'grid' ? (

              <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${cardSize}px, 1fr))`, gap: 10 }}>

                {filteredInventory.map((invItem, idx) => {

                  const item = getShopTemplateForInventoryLine(invItem); if (!item) return null;
                  const inventoryRowKey = getShopInventoryLineKey(invItem, idx);

                  return <ShopGridCard key={inventoryRowKey} invItem={invItem} item={item} isDM={effectiveIsDM} charGold={charGoldBase}

                    onBuy={() => openPurchaseModalForLine(inventoryRowKey)}

                    onAddToCart={() => handleAddToCart(inventoryRowKey)}

                    canAddToCart={canAddMoreToCart(inventoryRowKey)}

                    onPriceChange={p => handlePriceChange(inventoryRowKey, p)}

                    onStockChange={s => handleStockChange(inventoryRowKey, s)}

                    onEdit={() => openItemEditor(inventoryRowKey)}

                    onToggleHidden={() => handleToggleHidden(inventoryRowKey)}

                    onDelete={() => handleRemoveItemFromShop(inventoryRowKey)}

                    secrecyBadge={<SecrecyBadge secrecy={invItem.secrecy} small />}

                    formatPrice={formatPrice}

                    renderPrice={(amt) => <PriceWithIcons baseAmount={amt} size={11} fontSize="0.78rem" />}

                    cardSize={cardSize}

                    iconMode={shop?.iconMode || 'mixed'}

                    sc={sc}

                    getRarity={getRarity}

                    getCatIcon={getCatIcon}

                    delay={idx * 30} />;

                })}

              </div>

            ) : (

              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                <div style={{ display: 'grid', gridTemplateColumns: effectiveIsDM ? '1fr auto auto auto auto' : '1fr auto auto auto', gap: 16, padding: '6px 14px', fontSize: '0.68rem', color: sc.textDim, textTransform: 'uppercase', letterSpacing: 0.8 }}>

                  <div>Item</div><div style={{ minWidth: 50, textAlign: 'center' }}>Stock</div><div style={{ minWidth: 60, textAlign: 'right' }}>Price</div>{effectiveIsDM && <div />}<div />

                </div>

                {filteredInventory.map((invItem, idx) => {

                  const item = getShopTemplateForInventoryLine(invItem); if (!item) return null;
                  const inventoryRowKey = getShopInventoryLineKey(invItem, idx);

                  return <ShopListRow key={inventoryRowKey} invItem={invItem} item={item} iconMode={shop?.iconMode || 'mixed'} isDM={effectiveIsDM} charGold={charGoldBase}

                    onBuy={() => openPurchaseModalForLine(inventoryRowKey)}

                    onAddToCart={() => handleAddToCart(inventoryRowKey)}

                    canAddToCart={canAddMoreToCart(inventoryRowKey)}

                    onPriceChange={p => handlePriceChange(inventoryRowKey, p)}

                    onStockChange={s => handleStockChange(inventoryRowKey, s)}

                    onEdit={() => openItemEditor(inventoryRowKey)}

                    onToggleHidden={() => handleToggleHidden(inventoryRowKey)}

                    onDelete={() => handleRemoveItemFromShop(inventoryRowKey)}

                    secrecyBadge={<SecrecyBadge secrecy={invItem.secrecy} small />}

                    sc={sc}

                    getRarity={getRarity}

                    getCatIcon={getCatIcon}

                    renderPrice={(amt) => <PriceWithIcons baseAmount={amt} size={11} fontSize="0.82rem" />}

                    formatPrice={formatPrice} />;

                })}

              </div>

            )}



            {/* Transaction Log */}

            {effectiveIsDM && log.length > 0 && (

              <div style={{ background: sc.bgDark, border: `1px solid ${sc.border}`, borderRadius: 8, padding: 16, marginTop: 24 }}>

                <div style={{ fontSize: '0.8rem', color: sc.textHead, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>📋 Transaction Log</div>

                {log.slice(0, 15).map((entry, idx) => (

                  <div key={idx} style={{ padding: '6px 0', borderBottom: `1px solid ${sc.borderLight}`, animation: idx === 0 ? 'shopFadeIn 0.3s ease' : undefined }}>

                    {entry.type === 'trade' ? (

                      <div style={{ fontSize: '0.82rem' }}>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>

                          <span style={{ fontSize: '0.65rem', padding: '1px 6px', background: '#2a2a18', border: '1px solid #4a4a30', borderRadius: 8, color: sc.gold }}>⚖️ Trade</span>

                          <span style={{ color: sc.blue }}>{entry.char}</span>

                        </div>

                        <div style={{ display: 'flex', gap: 8, fontSize: '0.78rem', marginLeft: 4 }}>

                          <span style={{ color: '#b87a6a' }}>Gave: <span style={{ color: sc.textSubLight }}>{entry.giving}</span></span>

                          <span style={{ color: sc.textDim }}>⇄</span>

                          <span style={{ color: '#7ab87a' }}>Got: <span style={{ color: sc.textSubLight }}>{entry.wanting}</span></span>

                        </div>

                        {entry.note && <div style={{ fontSize: '0.7rem', fontStyle: 'italic', color: sc.textDim, marginLeft: 4, marginTop: 2 }}>"{entry.note}"</div>}

                        {effectiveIsDM && Array.isArray(entry.resaleLines) && entry.resaleLines.length > 0 && (

                          <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>

                            {entry.resaleLines.map((line) => {
                              const draft = getTradeReceiptPromotionDraft(entry, line);
                              const lineName = line.name || line.itemName || 'Item';
                              const promotionDisabled = line.promoted || line.canPromote === false || !tradeReceiptPromotionAuthorityEnabled;
                              const promotionTitle = line.promoted
                                ? `${lineName} is already listed for resale`
                                : (line.canPromote === false ? `${lineName} cannot be promoted: ${line.promotionBlockedReason || 'missing receipt identity'}` : `Promote ${lineName} for resale`);
                              return (

                                <div key={`${entry.transactionId}:${line.lineIndex}`} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 6, alignItems: 'center', padding: '6px 8px', background: 'rgba(0,0,0,0.18)', border: `1px solid ${sc.borderLight}`, borderRadius: 6 }}>

                                  <div style={{ minWidth: 0 }}>

                                    <div style={{ fontSize: '0.72rem', color: sc.textSubLight, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lineName} ×{line.qty || line.quantity || 1}</div>

                                    <div style={{ fontSize: '0.62rem', color: line.promoted ? sc.green : sc.textDim }}>{line.promoted ? 'Listed for resale' : 'Receipt line'}</div>

                                  </div>

                                  <input aria-label={`Resale price for ${lineName}`} value={draft.priceBase} onChange={e => updateTradeReceiptPromotionDraft(entry, line, { priceBase: e.target.value })} disabled={promotionDisabled} type="number" className="shop-input" style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '4px 8px', color: sc.textSub, fontSize: '0.72rem' }} />

                                  <select aria-label={`Resale visibility for ${lineName}`} value={draft.secrecy} onChange={e => updateTradeReceiptPromotionDraft(entry, line, { secrecy: e.target.value })} disabled={promotionDisabled} className="shop-input" style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '4px 8px', color: sc.textSub, fontSize: '0.72rem' }}>

                                    <option value="visible">Visible</option>

                                    <option value="hidden">Hidden</option>

                                    <option value="secret">Secret</option>

                                  </select>

                                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: sc.textDim, fontSize: '0.68rem', whiteSpace: 'nowrap' }}>

                                    <input aria-label={`Trade only resale for ${lineName}`} type="checkbox" checked={draft.tradeOnly} onChange={e => updateTradeReceiptPromotionDraft(entry, line, { tradeOnly: e.target.checked })} disabled={promotionDisabled} />

                                    Trade

                                  </label>

                                  <button className="shop-btn" onClick={() => handlePromoteTradeReceiptLine(entry, line)} disabled={promotionDisabled} title={promotionTitle} style={{ padding: '4px 10px', background: line.promoted ? 'rgba(80,180,80,0.08)' : sc.greenBg, border: `1px solid ${line.promoted ? 'rgba(80,180,80,0.25)' : sc.greenBorder}`, borderRadius: 4, color: line.promoted ? sc.green : sc.green, fontSize: '0.72rem', opacity: promotionDisabled ? 0.6 : 1, whiteSpace: 'nowrap' }}>{line.promoted ? 'Listed' : 'Promote'}</button>

                                </div>

                              );
                            })}

                          </div>

                        )}

                      </div>

                    ) : entry.type === 'sell-back' ? (

                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem' }}>

                        <span style={{ color: sc.textSub }}><span style={{ color: sc.blue }}>{entry.char}</span> sold {entry.item} Ã—{entry.qty}</span>

                        <span style={{ color: '#7ab87a' }}>+{formatPrice(entry.total)}</span>

                      </div>

                    ) : (

                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem' }}>

                        <span style={{ color: sc.textSub }}><span style={{ color: sc.blue }}>{entry.char}</span> bought {entry.item} ×{entry.qty}</span>

                        <span style={{ color: sc.gold }}>−{formatPrice(entry.total)}</span>

                      </div>

                    )}

                  </div>

                ))}

              </div>

            )}

          </>

        )}

      </div>



      {/* ========== MODALS ========== */}



      {/* Purchase Modal */}

      {purchaseModal && (() => {

        const invItem = findShopInventoryLineByKey(purchaseModal);
        const item = getShopTemplateForInventoryLine(invItem);

        if (!item || !invItem) return null;

        const r = getRarity(item.rarity);

        const unitPrice = invItem.price;

        const maxStock = invItem.stock === null ? 999 : invItem.stock;

        const resolvedPurchaseQty = clampPurchaseQuantity(purchaseQtyDraft, maxStock, purchaseQty);

        const total = unitPrice * resolvedPurchaseQty; const canAfford = total <= charGoldBase;

        const quickAmounts = [1, 5, 10, 20].filter(n => n <= maxStock);

        return (

          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20, animation: 'fadeOverlay 0.2s ease' }} onClick={closePurchaseModal}>

            <div onClick={e => e.stopPropagation()} style={{ background: sc.bgCard, border: `1px solid ${r.border}`, borderRadius: 8, padding: 24, maxWidth: 420, width: '100%', animation: 'modalIn 0.25s ease', boxShadow: `0 20px 60px rgba(0,0,0,0.7), 0 0 20px ${r.glow}` }}>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${sc.border}` }}>

                <div><div style={{ fontSize: '1.15rem', fontWeight: 600, color: r.color !== '#9a9a8a' ? r.color : sc.textHead }}>{getCatIcon(item.category)} {item.name}</div><div style={{ fontSize: '0.8rem', color: '#6a6a5a', marginTop: 2 }}>{item.shortDesc}</div>{item.longDesc && <div style={{ fontSize: '0.78rem', color: sc.textDim, marginTop: 6, lineHeight: 1.5 }}>{item.longDesc}</div>}

                  {(item.buffs?.length > 0 || item.debuffs?.length > 0) && (

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>

                      {(item.buffs || []).map((b, i) => <span key={`b${i}`} style={{ fontSize: '0.68rem', padding: '1px 6px', background: 'rgba(80,180,80,0.12)', border: '1px solid rgba(80,180,80,0.25)', borderRadius: 8, color: '#6cc06c' }}>{b}</span>)}

                      {(item.debuffs || []).map((d, i) => <span key={`d${i}`} style={{ fontSize: '0.68rem', padding: '1px 6px', background: 'rgba(180,80,80,0.12)', border: '1px solid rgba(180,80,80,0.25)', borderRadius: 8, color: '#c06c6c' }}>{d}</span>)}

                    </div>

                  )}

                  {item.abilities?.length > 0 && (

                    <div style={{ marginTop: 6 }}>{item.abilities.map(a => <div key={a.id} style={{ fontSize: '0.72rem', color: '#5a9fd4', marginTop: 2 }}>⚡ {a.name}{a.uses ? ` (${a.uses.max} uses)` : ''}{a.effects?.length > 0 ? `: ${a.effects.join(', ')}` : ''}</div>)}</div>

                  )}

                </div>

                <button className="shop-btn" onClick={closePurchaseModal} style={{ background: 'none', border: 'none', color: sc.textMuted, fontSize: '1.1rem', padding: 4 }}>✕</button>

              </div>

              {/* Quantity */}

              <div style={{ marginBottom: 16 }}>

                <div style={{ fontSize: '0.8rem', color: sc.textSub, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Quantity</div>

                <QuantityStepper
                  value={purchaseQty}
                  onCommit={commitPurchaseQuantity}
                  onChange={e => setPurchaseQtyDraft(e.target.value)}
                  min={1}
                  max={maxStock}
                  inputLabel="Purchase quantity"
                  decrementAriaLabel="Decrease purchase quantity"
                  incrementAriaLabel="Increase purchase quantity"
                  style={{ gap: 8 }}
                  inputStyle={{ width: 60, minWidth: 60, background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '6px 8px', color: sc.text, fontSize: '1rem' }}
                  buttonStyle={{ padding: '6px 14px', background: '#1a1a20', border: '1px solid #2a2a30', borderRadius: 4, color: sc.text, fontSize: '1rem' }}
                />

                {maxStock > 1 && <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>

                  {quickAmounts.map(n => <button key={n} className="shop-btn" onClick={() => commitPurchaseQuantity(n)} style={{ padding: '3px 10px', background: resolvedPurchaseQty === n ? sc.goldDark : '#151518', border: `1px solid ${resolvedPurchaseQty === n ? sc.goldBorder : sc.borderMed}`, borderRadius: 12, color: resolvedPurchaseQty === n ? sc.gold : sc.textMuted, fontSize: '0.75rem' }}>×{n}</button>)}

                  {maxStock < 999 && !quickAmounts.includes(maxStock) && <button className="shop-btn" onClick={() => commitPurchaseQuantity(maxStock)} style={{ padding: '3px 10px', background: resolvedPurchaseQty === maxStock ? sc.goldDark : '#151518', border: `1px solid ${resolvedPurchaseQty === maxStock ? sc.goldBorder : sc.borderMed}`, borderRadius: 12, color: resolvedPurchaseQty === maxStock ? sc.gold : sc.textMuted, fontSize: '0.75rem' }}>All ({maxStock})</button>}

                </div>}

              </div>

              {/* Summary */}

              <div style={{ background: sc.bgDarker, padding: 14, borderRadius: 6, marginBottom: 16, border: `1px solid ${sc.borderLight}` }}>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: '0.85rem' }}><span style={{ color: sc.textMuted }}>Price each</span><span style={{ color: sc.text }}>{formatPrice(unitPrice)}</span></div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: '0.85rem' }}><span style={{ color: sc.textMuted }}>Quantity</span><span style={{ color: sc.text }}>×{resolvedPurchaseQty}</span></div>

                <div style={{ height: 1, background: sc.border, margin: '8px 0' }} />

                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.1rem', fontWeight: 600 }}><span style={{ color: sc.text }}>Total</span><div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><ShopCoin size={16} /><span style={{ color: canAfford ? sc.gold : '#b85a4a' }}>{formatPrice(total)}</span></div></div>

                <div style={{ fontSize: '0.72rem', color: sc.textDim, textAlign: 'right', marginTop: 4 }}>Your funds: {charWealthDisplay} → {canAfford ? `${formatPrice(charGoldBase - total)} remaining` : 'Not enough!'}</div>

              </div>

              <div style={{ display: 'flex', gap: 10 }}>

                <button className="shop-btn" onClick={closePurchaseModal} style={{ flex: 1, padding: '10px 16px', background: '#1a1a20', border: '1px solid #2a2a30', borderRadius: 6, color: sc.textSubLight, fontSize: '0.9rem' }}>Cancel</button>

                <button className="shop-btn" onClick={handlePurchase} disabled={!canAfford} style={{ flex: 1, padding: '10px 16px', background: canAfford ? sc.greenBg : '#1a1a1a', border: `1px solid ${canAfford ? sc.greenBorder : sc.borderMed}`, borderRadius: 6, color: canAfford ? sc.green : sc.textMuted, fontSize: '0.9rem', fontWeight: 600, opacity: canAfford ? 1 : 0.5 }}>✓ Confirm Purchase</button>

              </div>

            </div>

          </div>

        );

      })()}



      {/* Edit Item Modal (DM) - Full Editor */}

      {editingItem && (() => {

        const { templateDraft: td, invDraft: inv, isNew } = editingItem;

        const r = getRarity(td.rarity);

        const currentSecrecy = inv.secrecy || 'visible';

        return (

          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 2000, overflowY: 'auto', padding: 20, animation: 'fadeOverlay 0.2s ease' }} onClick={() => setEditingItem(null)}>

            <div onClick={e => e.stopPropagation()} style={{ background: `linear-gradient(180deg, ${sc.bgCard} 0%, ${sc.bgDarker} 100%)`, border: `2px solid ${r.color}60`, borderRadius: 8, padding: 24, maxWidth: 520, width: '100%', marginTop: 20, marginBottom: 20, animation: 'modalIn 0.25s ease', boxShadow: `0 20px 60px rgba(0,0,0,0.7), 0 0 20px ${r.glow}` }}>

              {/* Header */}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${sc.border}` }}>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

                  {!isNew && <button className="shop-btn" onClick={() => { if (window.confirm('Remove this item from the shop?')) { removeInvItem(editingItem.originalLineKey); setEditingItem(null); } }}

                    style={{ padding: '4px 8px', background: sc.redBg, border: `1px solid ${sc.redBorder}`, borderRadius: 4, color: sc.red, fontSize: '0.72rem' }} title="Remove from shop">🗑</button>}

                  {!isNew && <button className="shop-btn" onClick={() => { setEditingItem(null); duplicateItem(editingItem.originalLineKey); }}

                    style={{ padding: '4px 8px', background: '#1a1a2a', border: `1px solid #3a3a5a`, borderRadius: 4, color: '#9a9ac8', fontSize: '0.72rem' }} title="Duplicate this item">📋 Duplicate</button>}

                  <div style={{ fontSize: '1.15rem', fontWeight: 600, color: sc.gold }}>{isNew ? '✨ Create Item' : '✏️ Edit Item'}</div>

                </div>

                <button className="shop-btn" onClick={() => setEditingItem(null)} style={{ background: 'none', border: 'none', color: sc.textMuted, fontSize: '1.1rem', padding: 4 }}>✕</button>

              </div>



              {/* Name, Category, Rarity row */}

              <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>

                  <div style={{ fontSize: '0.72rem', color: sc.gold, marginBottom: 4 }}>Icon</div>

                  <IconSelectButton value={td.iconRef || null} onChange={(key) => updateTemplateDraft({ iconRef: key })} sc={sc} size={36} />

                </div>

                <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>

                <div>

                  <div style={{ fontSize: '0.72rem', color: sc.gold, marginBottom: 4 }}>Name</div>

                  <input className="shop-input" value={td.name} onChange={e => updateTemplateDraft({ name: e.target.value })} placeholder="Item name..."

                    style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '6px 8px', color: sc.textHead, fontSize: '0.9rem', fontWeight: 500 }} />

                </div>

                <div>

                  <div style={{ fontSize: '0.72rem', color: sc.gold, marginBottom: 4 }}>Category</div>

                  <select className="shop-input" value={td.category} onChange={e => updateTemplateDraft({ category: e.target.value })}

                    style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '6px 4px', color: sc.textSubLight, fontSize: '0.8rem' }}>

                    {mergedCategories.filter(c => c.id !== 'all').map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}

                  </select>

                </div>

                <div>

                  <div style={{ fontSize: '0.72rem', color: sc.gold, marginBottom: 4 }}>Rarity</div>

                  <select className="shop-input" value={td.rarity} onChange={e => updateTemplateDraft({ rarity: e.target.value })}

                    style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '6px 4px', color: getRarity(td.rarity).color, fontSize: '0.8rem' }}>

                    {Object.entries(mergedRarities).map(([k, v]) => <option key={k} value={k} style={{ color: v.color }}>{v.name}</option>)}

                  </select>

                </div>

                </div>

              </div>



              {/* Short Description */}

              <div style={{ marginBottom: 12 }}>

                <div style={{ fontSize: '0.72rem', color: sc.gold, marginBottom: 4 }}>Short Description</div>

                <input className="shop-input" value={td.shortDesc || ''} onChange={e => updateTemplateDraft({ shortDesc: e.target.value })} placeholder="Brief item description..."

                  style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '6px 8px', color: sc.text, fontSize: '0.85rem' }} />

              </div>



              {/* Long Description */}

              <div style={{ marginBottom: 12 }}>

                <div style={{ fontSize: '0.72rem', color: sc.gold, marginBottom: 4 }}>Full Description</div>

                <textarea className="shop-input" value={td.longDesc || ''} onChange={e => updateTemplateDraft({ longDesc: e.target.value })} placeholder="Detailed description, lore, flavor text..."

                  style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '6px 8px', color: sc.text, fontSize: '0.85rem', minHeight: 50, resize: 'vertical' }} />

              </div>



              {/* Price & Stock */}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, marginBottom: 6, padding: 12, background: sc.bgDark, borderRadius: 6, border: `1px solid ${sc.borderLight}` }}>

                <div>

                  <div style={{ fontSize: '0.72rem', color: sc.textMuted, marginBottom: 4 }}>Price</div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>

                    {(() => {

                      // Price is stored in base units — convert directly to wallet

                      const priceWallet = baseToWallet(inv.price || 0, tiers);

                      return [...tiers].reverse().map(tier => (

                        <div key={tier.id} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>

                          <CoinIcon tierId={tier.id} size={14} />

                          <input type="number" value={priceWallet[tier.id] || 0} min={0}

                            onChange={e => {

                              const newVal = Math.max(0, parseInt(e.target.value) || 0);

                              const newWallet = { ...priceWallet, [tier.id]: newVal };

                              updateInvDraft({ price: walletToBase(newWallet, tiers) });

                            }}

                            className="shop-input"

                            style={{ width: 44, background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '5px 4px', color: inv.tradeOnly ? sc.textDim : sc.gold, fontSize: '0.82rem', textAlign: 'right', opacity: inv.tradeOnly ? 0.4 : 1 }} />

                        </div>

                      ));

                    })()}

                  </div>

                  <div style={{ fontSize: '0.6rem', color: sc.textDim, marginTop: 4 }}>= {formatPrice(inv.price)}</div>

                </div>

                <div>

                  <div style={{ fontSize: '0.72rem', color: sc.textMuted, marginBottom: 4 }}>Stock (999 = ∞)</div>

                  <input type="number" value={inv.stock === null ? 999 : inv.stock} onChange={e => { const v = parseInt(e.target.value) || 0; updateInvDraft({ stock: v >= 999 ? null : Math.max(0, v) }); }} className="shop-input"

                    style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '6px 8px', color: sc.text, fontSize: '0.9rem' }} />

                </div>

              </div>

              {/* Trade Only toggle */}

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '6px 12px', background: inv.tradeOnly ? 'rgba(201,162,39,0.06)' : 'transparent', borderRadius: 6, border: `1px solid ${inv.tradeOnly ? 'rgba(201,162,39,0.2)' : sc.borderLight}`, cursor: 'pointer' }}

                onClick={() => updateInvDraft({ tradeOnly: !inv.tradeOnly })}>

                <span style={{ fontSize: '1rem' }}>⚖️</span>

                <div style={{ flex: 1 }}>

                  <div style={{ fontSize: '0.78rem', fontWeight: inv.tradeOnly ? 600 : 400, color: inv.tradeOnly ? sc.gold : sc.textSub }}>Trade Only</div>

                  <div style={{ fontSize: '0.62rem', color: sc.textDim }}>Item can only be obtained through barter, not purchased with coin</div>

                </div>

                <div style={{ width: 32, height: 18, borderRadius: 9, background: inv.tradeOnly ? 'rgba(201,162,39,0.4)' : sc.bgBar, border: `1px solid ${inv.tradeOnly ? 'rgba(201,162,39,0.5)' : sc.borderMed}`, position: 'relative', transition: 'all 0.2s ease' }}>

                  <div style={{ width: 14, height: 14, borderRadius: '50%', background: inv.tradeOnly ? sc.gold : sc.textDim, position: 'absolute', top: 1, left: inv.tradeOnly ? 15 : 1, transition: 'all 0.2s ease' }} />

                </div>

              </div>



              {/* Uses Configuration (item-level) */}

              <div style={{ padding: 12, background: 'rgba(201,162,39,0.08)', borderRadius: 6, border: '1px solid rgba(201,162,39,0.2)', marginBottom: 12 }}>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: td.uses ? 8 : 0 }}>

                  <div style={{ fontSize: '0.72rem', color: sc.gold }}>Uses Configuration</div>

                  {!td.uses ? (

                    <button className="shop-btn" onClick={() => updateTemplateDraft({ uses: createUsesTracker(1, 'none') })}

                      style={{ padding: '3px 10px', background: '#1a1a2a', border: '1px solid #3a3a20', borderRadius: 4, color: sc.gold, fontSize: '0.7rem' }}>+ Enable Uses Tracking</button>

                  ) : (

                    <button className="shop-btn" onClick={() => updateTemplateDraft({ uses: null })}

                      style={{ padding: '3px 10px', background: sc.redBg, border: `1px solid ${sc.redBorder}`, borderRadius: 4, color: sc.red, fontSize: '0.7rem' }}>Remove Uses Tracking</button>

                  )}

                </div>

                {td.uses && (

                  <div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>

                      <div>

                        <div style={{ fontSize: '0.6rem', color: sc.textDim }}>Current</div>

                        <input type="number" min="0" value={td.uses.current} disabled={!editUsesUnlocked}

                          onChange={e => updateTemplateDraft({ uses: { ...td.uses, current: parseInt(e.target.value) || 0 } })} className="shop-input"

                          style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 3, padding: '6px', color: sc.text, fontSize: '0.85rem', opacity: editUsesUnlocked ? 1 : 0.5 }} />

                      </div>

                      <div>

                        <div style={{ fontSize: '0.6rem', color: sc.textDim }}>Max Uses</div>

                        <input type="number" min="1" value={td.uses.max}

                          onChange={e => { const max = parseInt(e.target.value) || 1; updateTemplateDraft({ uses: { ...td.uses, max, current: max } }); }} className="shop-input"

                          style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 3, padding: '6px', color: sc.text, fontSize: '0.85rem' }} />

                      </div>

                      <div>

                        <div style={{ fontSize: '0.6rem', color: sc.textDim }}>Resets</div>

                        <select value={td.uses.resetType} onChange={e => updateTemplateDraft({ uses: { ...td.uses, resetType: e.target.value } })} className="shop-input"

                          style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 3, padding: '6px 4px', color: sc.textSubLight, fontSize: '0.8rem' }}>

                          {Object.entries(USES_RESET_TYPES).map(([key, rt]) => <option key={key} value={key}>{rt.name}</option>)}

                        </select>

                      </div>

                    </div>

                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: '0.7rem', cursor: 'pointer', color: editUsesUnlocked ? sc.gold : '#888' }}>

                      <input type="checkbox" checked={editUsesUnlocked} onChange={e => setEditUsesUnlocked(e.target.checked)} />

                      🔓 Unlock current uses editing (to correct mistakes)

                    </label>

                  </div>

                )}

                {!td.uses && <div style={{ fontSize: '0.65rem', color: sc.textDim, marginTop: 4 }}>Enable to track limited uses (e.g., consumables, scrolls, charges).</div>}

              </div>



              {/* Buffs & Debuffs */}

              <div style={{ marginBottom: 12 }}>

                <div style={{ fontSize: '0.72rem', color: '#8cb87a', marginBottom: 4 }}>Buffs (comma separated, e.g., +2 Str, +1 AC)</div>

                <input className="shop-input" value={td._buffsRaw ?? (td.buffs || []).join(', ')} onChange={e => updateTemplateDraft({ _buffsRaw: e.target.value })} placeholder="+2 Str, +1 AC..."

                  style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '6px 8px', color: sc.text, fontSize: '0.85rem', marginBottom: 8 }} />

                <div style={{ fontSize: '0.72rem', color: '#b88a7a', marginBottom: 4 }}>Debuffs</div>

                <input className="shop-input" value={td._debuffsRaw ?? (td.debuffs || []).join(', ')} onChange={e => updateTemplateDraft({ _debuffsRaw: e.target.value })} placeholder="-1 Dex, -5 Speed..."

                  style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '6px 8px', color: sc.text, fontSize: '0.85rem', marginBottom: 8 }} />

                {(parseCommaSep(td._buffsRaw).length > 0 || parseCommaSep(td._debuffsRaw).length > 0 || td.buffs?.length > 0 || td.debuffs?.length > 0) && (

                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', cursor: 'pointer', color: sc.text }}>

                    <input type="checkbox" checked={td.buffsApplyOnUse || false} onChange={e => updateTemplateDraft({ buffsApplyOnUse: e.target.checked })} />

                    Apply buffs/debuffs when used (otherwise when equipped)

                  </label>

                )}

              </div>



              {/* On Use Effects */}

              <div style={{ padding: 12, background: 'rgba(39,201,90,0.08)', borderRadius: 6, border: '1px solid rgba(39,201,90,0.25)', marginBottom: 12 }}>

                <div style={{ fontSize: '0.72rem', color: '#27c95a', marginBottom: 4 }}>On Use Effects</div>

                <input className="shop-input" value={td._onUseEffectsRaw ?? (td.onUseEffects || []).join(', ')} onChange={e => updateTemplateDraft({ _onUseEffectsRaw: e.target.value })} placeholder="+10 HP, +5 Mana, Cure Poison..."

                  style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '6px 8px', color: sc.text, fontSize: '0.85rem' }} />

                <div style={{ fontSize: '0.65rem', color: sc.textDim, marginTop: 4 }}>Separate from buffs/debuffs. These trigger each time the item is used. Examples: "+10 HP", "+5 Mana", "Cure Poison"</div>

              </div>



              {/* Abilities Section */}

              <div style={{ marginBottom: 12 }}>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>

                  <div style={{ fontSize: '0.72rem', color: '#5a9fd4' }}>Abilities</div>

                  <button className="shop-btn" onClick={addAbilityToShopItem}

                    style={{ padding: '3px 10px', background: '#1a1a2a', border: '1px solid #2a2a5a', borderRadius: 4, color: '#5a9fd4', fontSize: '0.72rem' }}>+ Add Ability</button>

                </div>

                <div style={{ fontSize: '0.65rem', color: sc.textDim, marginBottom: 8 }}>Unique actions the item grants. Each can have its own uses and reset schedule. Non-consumable items require being equipped.</div>

                {(td.abilities || []).map((ability, idx) => (

                  <div key={ability.id} style={{ padding: 10, background: 'rgba(90,159,212,0.08)', borderRadius: 6, border: '1px solid rgba(90,159,212,0.25)', marginBottom: 8 }}>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>

                      <span style={{ fontSize: '0.75rem', color: '#5a9fd4' }}>Ability #{idx + 1}</span>

                      <button className="shop-btn" onClick={() => removeAbilityFromShopItem(ability.id)}

                        style={{ padding: '2px 8px', background: sc.redBg, border: `1px solid ${sc.redBorder}`, borderRadius: 4, color: sc.red, fontSize: '0.6rem' }}>✕</button>

                    </div>

                    <input className="shop-input" value={ability.name} onChange={e => updateAbilityInShopItem(ability.id, { name: e.target.value })} placeholder="Ability name..."

                      style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '6px 8px', color: sc.textHead, fontSize: '0.85rem', fontWeight: 600, marginBottom: 6 }} />

                    <textarea className="shop-input" value={ability.description || ''} onChange={e => updateAbilityInShopItem(ability.id, { description: e.target.value })} placeholder="Description..."

                      style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '6px 8px', color: sc.text, fontSize: '0.85rem', minHeight: 36, resize: 'vertical', marginBottom: 6 }} />

                    <input className="shop-input" value={ability._effectsRaw ?? (ability.effects || []).join(', ')} onChange={e => updateAbilityInShopItem(ability.id, { _effectsRaw: e.target.value })} placeholder="Effects: +5 Damage, +1d4 Attack..."

                      style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '6px 8px', color: sc.text, fontSize: '0.85rem', marginBottom: 6 }} />

                    {/* Ability Uses */}

                    <div style={{ padding: 8, background: 'rgba(0,0,0,0.2)', borderRadius: 4 }}>

                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.7rem', cursor: 'pointer', color: sc.text, marginBottom: ability.uses ? 8 : 0 }}>

                        <input type="checkbox" checked={!!ability.uses}

                          onChange={e => { if (e.target.checked) { updateAbilityInShopItem(ability.id, { uses: createUsesTracker(1, 'shortRest') }); } else { updateAbilityInShopItem(ability.id, { uses: null }); } }} />

                        Has limited uses

                      </label>

                      {ability.uses && (

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>

                          <div>

                            <div style={{ fontSize: '0.6rem', color: sc.textDim }}>Current</div>

                            <input type="number" min="0" value={ability.uses.current} disabled={!editUsesUnlocked}

                              onChange={e => updateAbilityInShopItem(ability.id, { uses: { ...ability.uses, current: parseInt(e.target.value) || 0 } })} className="shop-input"

                              style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 3, padding: '4px 6px', color: sc.text, fontSize: '0.8rem', opacity: editUsesUnlocked ? 1 : 0.5 }} />

                          </div>

                          <div>

                            <div style={{ fontSize: '0.6rem', color: sc.textDim }}>Max</div>

                            <input type="number" min="1" value={ability.uses.max}

                              onChange={e => { const max = parseInt(e.target.value) || 1; updateAbilityInShopItem(ability.id, { uses: { ...ability.uses, max, current: max } }); }} className="shop-input"

                              style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 3, padding: '4px 6px', color: sc.text, fontSize: '0.8rem' }} />

                          </div>

                          <div>

                            <div style={{ fontSize: '0.6rem', color: sc.textDim }}>Reset</div>

                            <select value={ability.uses.resetType} onChange={e => updateAbilityInShopItem(ability.id, { uses: { ...ability.uses, resetType: e.target.value } })} className="shop-input"

                              style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 3, padding: '4px 4px', color: sc.textSubLight, fontSize: '0.75rem' }}>

                              {Object.entries(USES_RESET_TYPES).map(([key, rt]) => <option key={key} value={key}>{rt.name}</option>)}

                            </select>

                          </div>

                        </div>

                      )}

                    </div>

                  </div>

                ))}

                {(td.abilities || []).length === 0 && (

                  <div style={{ fontSize: '0.75rem', color: sc.textDim, textAlign: 'center', padding: 12 }}>No abilities. Click "+ Add Ability" to add one.</div>

                )}

              </div>



              {/* Advanced Combat Integration */}

              <div style={{ padding: 12, background: 'rgba(138,92,246,0.08)', borderRadius: 6, border: '1px solid rgba(138,92,246,0.25)', marginBottom: 12 }}>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 }}>

                  <div style={{ fontSize: '0.72rem', color: '#a78bfa' }}>Advanced Combat Integration</div>

                  <select
                    aria-label="Add shop combat bonus"
                    className="shop-input"
                    value=""
                    onChange={e => addCombatBonusToShopItem(e.target.value)}
                    style={{ maxWidth: 250, background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '4px 6px', color: sc.textSubLight, fontSize: '0.72rem' }}
                  >

                    <option value="">+ Add Combat Bonus</option>

                    {COMBAT_BONUS_TYPES.map(bonus => (
                      <option key={`${bonus.type}:${bonus.scope}:${bonus.value ?? ''}`} value={bonus.label}>{bonus.label}</option>
                    ))}

                  </select>

                </div>

                <div style={{ fontSize: '0.65rem', color: sc.textDim, marginBottom: 8 }}>Bonuses attach to the item definition and are preserved when the shop item is purchased or traded.</div>

                {normalizeShopCombatBonuses(td.combatBonuses).length > 0 ? (

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>

                    {normalizeShopCombatBonuses(td.combatBonuses).map((bonus, idx) => (

                      <div key={`${bonus.type}:${bonus.scope}:${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', background: 'rgba(0,0,0,0.22)', border: `1px solid ${sc.borderLight}`, borderRadius: 4 }}>

                        <span style={{ flex: 1, color: sc.text, fontSize: '0.78rem' }}>{bonus.label || `${bonus.type || 'bonus'}:${bonus.scope || 'unknown'}`}</span>

                        <button
                          aria-label={`Remove shop combat bonus ${idx + 1}`}
                          className="shop-btn"
                          onClick={() => removeCombatBonusFromShopItem(idx)}
                          style={{ padding: '2px 8px', background: sc.redBg, border: `1px solid ${sc.redBorder}`, borderRadius: 4, color: sc.red, fontSize: '0.65rem' }}
                        >
                          Remove
                        </button>

                      </div>

                    ))}

                  </div>

                ) : (

                  <div style={{ fontSize: '0.75rem', color: sc.textDim, textAlign: 'center', padding: 10 }}>No combat bonuses configured.</div>

                )}

                {hasPartiallySupportedShopCombatBonuses(td.combatBonuses) && (

                  <div data-testid="shop-authored-mechanic-warning" role="note" style={{ marginTop: 8, padding: '7px 8px', borderRadius: 4, background: 'rgba(201,162,39,0.12)', border: '1px solid rgba(201,162,39,0.3)', color: sc.gold, fontSize: '0.68rem' }}>
                    {AUTHORED_ITEM_MECHANIC_WARNING_TEXT}
                  </div>

                )}

              </div>



              {/* Tags */}

              <div style={{ marginBottom: 12 }}>

                <div style={{ fontSize: '0.72rem', color: sc.gold, marginBottom: 4 }}>Tags</div>

                <input
                  aria-label="Shop item tags"
                  className="shop-input"
                  value={td._tagsRaw ?? (td.tags || []).join(', ')}
                  onChange={e => updateTemplateDraft({ _tagsRaw: e.target.value })}
                  placeholder="arcane, consumable, rare..."
                  style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '6px 8px', color: sc.text, fontSize: '0.85rem' }}
                />

              </div>



              {/* Notes */}

              <div style={{ marginBottom: 16 }}>

                <div style={{ fontSize: '0.72rem', color: sc.gold, marginBottom: 4 }}>Notes</div>

                <textarea className="shop-input" value={td.notes || ''} onChange={e => updateTemplateDraft({ notes: e.target.value })} placeholder="Additional notes, lore, special rules..."

                  style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '6px 8px', color: sc.text, fontSize: '0.85rem', minHeight: 50, resize: 'vertical' }} />

              </div>



              {/* DM Notes */}

              <div style={{ marginBottom: 16 }}>

                <div style={{ fontSize: '0.72rem', color: sc.textMuted, marginBottom: 4 }}>DM Notes</div>

                <textarea
                  aria-label="Shop item DM notes"
                  className="shop-input"
                  value={td.dmNotes || ''}
                  onChange={e => updateTemplateDraft({ dmNotes: e.target.value })}
                  placeholder="Private source notes, reveal conditions, or handling details..."
                  style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '6px 8px', color: sc.text, fontSize: '0.85rem', minHeight: 44, resize: 'vertical' }}
                />

              </div>



              {/* Secrecy Level */}

              <div style={{ marginBottom: 16 }}>

                <div style={{ fontSize: '0.72rem', color: sc.textMuted, marginBottom: 4 }}>🤫 Secrecy Level</div>

                <div style={{ fontSize: '0.68rem', color: sc.textDim, marginBottom: 8 }}>Hidden items won't appear for players. Use tiers for items requiring code phrases or trust.</div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>

                  {SHOP_SECRECY_LEVELS.map(level => {

                    const displayIcon = level.locks > 0 ? '🔒'.repeat(level.locks) : (level.id === 'visible' ? '✓' : '⊘');

                    return (

                    <button key={level.id} className="shop-btn" onClick={() => updateInvDraft({ secrecy: level.id })}

                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: currentSecrecy === level.id ? `${level.color}12` : sc.bgDark, border: `1px solid ${currentSecrecy === level.id ? `${level.color}50` : sc.borderLight}`, borderRadius: 6, textAlign: 'left' }}>

                      <span style={{ fontSize: '0.85rem', minWidth: 28 }}>{displayIcon}</span>

                      <div style={{ flex: 1 }}><div style={{ fontSize: '0.82rem', fontWeight: currentSecrecy === level.id ? 600 : 400, color: currentSecrecy === level.id ? level.color : sc.text }}>{level.name}</div><div style={{ fontSize: '0.68rem', color: sc.textMuted }}>{level.desc}</div></div>

                      {currentSecrecy === level.id && <span style={{ color: level.color, fontSize: '0.85rem' }}>✓</span>}

                    </button>

                    );

                  })}

                </div>

              </div>



              {/* Action Buttons */}

              <div style={{ display: 'flex', gap: 8, borderTop: `1px solid ${sc.border}`, paddingTop: 12, justifyContent: 'flex-end' }}>

                <button className="shop-btn" onClick={() => setEditingItem(null)}

                  style={{ padding: '8px 16px', background: sc.bgDark, border: `1px solid ${sc.border}`, borderRadius: 6, color: sc.textSub, fontSize: '0.85rem' }}>Cancel</button>

                <button className="shop-btn" onClick={saveItemEdit}

                  style={{ padding: '8px 16px', background: sc.greenBg, border: `1px solid ${sc.greenBorder}`, borderRadius: 6, color: sc.green, fontSize: '0.85rem', fontWeight: 600 }}>💾 Save</button>

              </div>

            </div>

          </div>

        );

      })()}



      {/* Trade Offer Modal */}

      {showOfferModal && (

        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20, animation: 'fadeOverlay 0.2s ease' }} onClick={() => setShowOfferModal(false)}>

          <div onClick={e => e.stopPropagation()} style={{ background: sc.bgCard, border: `1px solid ${sc.borderMed}`, borderRadius: 8, padding: 24, maxWidth: 560, width: '100%', animation: 'modalIn 0.25s ease', boxShadow: '0 20px 60px rgba(0,0,0,0.7)' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${sc.border}` }}>

              <div style={{ fontSize: '1.1rem', fontWeight: 600, color: sc.textHead }}>⚖️ Make an Offer</div>

              <button className="shop-btn" onClick={() => setShowOfferModal(false)} style={{ background: 'none', border: 'none', color: sc.textMuted, fontSize: '1.1rem' }}>✕</button>

            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 12, marginBottom: 16, alignItems: 'start' }}>

              {/* Give side */}

              <div>

                <div style={{ fontSize: '0.8rem', color: '#b87a6a', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>You Give</div>

                <div style={{ background: sc.bgDarker, border: `1px solid ${sc.borderLight}`, borderRadius: 6, padding: 10, minHeight: 100 }}>

                  {tradeGiving.items.length === 0 && tradeGiving.gold === 0 && <div style={{ fontSize: '0.78rem', color: sc.textDim, fontStyle: 'italic', textAlign: 'center', padding: '20px 0' }}>No items added</div>}

                  {tradeGiving.items.map((item, idx) => (<div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.82rem', padding: '4px 6px', background: sc.bgCard, borderRadius: 4, marginBottom: 2 }}><span style={{ color: sc.text, flex: 1 }}>{item.name} ×{item.qty}</span><button className="shop-btn" onClick={() => setTradeGiving({ ...tradeGiving, items: tradeGiving.items.filter((_, i) => i !== idx) })} style={{ background: 'none', border: 'none', color: '#5a3a3a', fontSize: '0.7rem', padding: '0 2px' }}>✕</button></div>))}

                  {tradeGiving.gold > 0 && <div style={{ fontSize: '0.82rem', color: sc.gold, padding: '4px 6px', display: 'flex', alignItems: 'center', gap: 3 }}><ShopCoin size={12} />{formatPrice(tradeGiving.gold)}</div>}

                </div>

                <select aria-label="Offer inventory item" className="shop-input" style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '6px 8px', color: sc.textSubLight, fontSize: '0.8rem', marginTop: 6, cursor: 'pointer' }} onChange={e => { if (!e.target.value) return; addTradeGivingItem(e.target.value); e.target.value = ''; }} defaultValue=""><option value="">+ From inventory</option>{charInventory.map((item, idx) => { const key = getTradeInventoryOptionKey(item, idx); if (tradeOfferSubmitAuthorityEnabled && !item?.id) return null; return <option key={key} value={key}>{item.name} (×{item.quantity || 1})</option>; })}</select>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}><ShopCoin size={12} /><input aria-label="Offer gold" type="number" value={tradeGiving.gold} onChange={e => setTradeGiving({ ...tradeGiving, gold: Math.max(0, Math.min(charGoldBase, parseInt(e.target.value) || 0)) })} className="shop-input" style={{ width: 70, background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '4px 8px', color: sc.gold, fontSize: '0.8rem' }} min="0" /></div>

              </div>

              <div style={{ alignSelf: 'center', fontSize: '1.4rem', color: sc.textDim, paddingTop: 40 }}>⇄</div>

              {/* Want side */}

              <div>

                <div style={{ fontSize: '0.8rem', color: '#7ab87a', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>You Want</div>

                <div style={{ background: sc.bgDarker, border: `1px solid ${sc.borderLight}`, borderRadius: 6, padding: 10, minHeight: 100 }}>

                  {tradeWanting.items.length === 0 && tradeWanting.gold === 0 && <div style={{ fontSize: '0.78rem', color: sc.textDim, fontStyle: 'italic', textAlign: 'center', padding: '20px 0' }}>No items added</div>}

                  {tradeWanting.items.map((item, idx) => (<div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.82rem', padding: '4px 6px', background: sc.bgCard, borderRadius: 4, marginBottom: 2 }}><span style={{ color: sc.text, flex: 1 }}>{item.name} ×{item.qty}</span><button className="shop-btn" onClick={() => setTradeWanting({ ...tradeWanting, items: tradeWanting.items.filter((_, i) => i !== idx) })} style={{ background: 'none', border: 'none', color: '#5a3a3a', fontSize: '0.7rem', padding: '0 2px' }}>✕</button></div>))}

                  {tradeWanting.gold > 0 && <div style={{ fontSize: '0.82rem', color: sc.gold, padding: '4px 6px', display: 'flex', alignItems: 'center', gap: 3 }}><ShopCoin size={12} />{formatPrice(tradeWanting.gold)}</div>}

                </div>

                <select aria-label="Offer shop item" className="shop-input" style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '6px 8px', color: sc.textSubLight, fontSize: '0.8rem', marginTop: 6, cursor: 'pointer' }} onChange={e => { if (!e.target.value) return; addTradeWantedListing(e.target.value); e.target.value = ''; }} defaultValue=""><option value="">+ From shop</option>{shop?.inventory.map(inv => { const item = getShopTemplateForInventoryLine(inv); const key = getTradeListingOptionKey(inv); if (!item || (tradeOfferSubmitAuthorityEnabled && !inv.listingId)) return null; return <option key={key} value={key}>{item.name} ({formatPrice(inv.price)})</option>; })}</select>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}><ShopCoin size={12} /><input aria-label="Requested gold" type="number" value={tradeWanting.gold} onChange={e => setTradeWanting({ ...tradeWanting, gold: Math.max(0, parseInt(e.target.value) || 0) })} className="shop-input" style={{ width: 70, background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '4px 8px', color: sc.gold, fontSize: '0.8rem' }} min="0" /></div>

              </div>

            </div>

            <input type="text" value={tradeNote} onChange={e => setTradeNote(e.target.value)} placeholder="Note: As we discussed by the campfire..." className="shop-input" style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '8px 12px', color: sc.text, fontSize: '0.85rem', marginBottom: 16 }} />

            <div style={{ display: 'flex', gap: 10 }}>

              <button className="shop-btn" onClick={() => setShowOfferModal(false)} style={{ flex: 1, padding: '10px 16px', background: '#1a1a20', border: '1px solid #2a2a30', borderRadius: 6, color: sc.textSubLight, fontSize: '0.9rem' }}>Cancel</button>

              <button className="shop-btn" onClick={handleSubmitOffer} style={{ flex: 1, padding: '10px 16px', background: (tradeGiving.items.length > 0 || tradeGiving.gold > 0) && (tradeWanting.items.length > 0 || tradeWanting.gold > 0) ? sc.goldDark : '#1a1a1a', border: `1px solid ${(tradeGiving.items.length > 0 || tradeGiving.gold > 0) && (tradeWanting.items.length > 0 || tradeWanting.gold > 0) ? sc.goldBorder : sc.borderMed}`, borderRadius: 6, color: sc.textHead, fontSize: '0.9rem', fontWeight: 600, opacity: (tradeGiving.items.length > 0 || tradeGiving.gold > 0) && (tradeWanting.items.length > 0 || tradeWanting.gold > 0) ? 1 : 0.5 }}>📨 Submit Offer</button>

            </div>

          </div>

        </div>

      )}



      {/* Item Library Modal */}

      {showLibrary && (

        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20, animation: 'fadeOverlay 0.2s ease' }} onClick={() => { setShowLibrary(false); setLibSearch(''); setLibFilter('all'); }}>

          <div onClick={e => e.stopPropagation()} style={{ background: sc.bgCard, border: `1px solid ${sc.borderMed}`, borderRadius: 8, padding: 24, maxWidth: 600, width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', animation: 'modalIn 0.25s ease', boxShadow: '0 20px 60px rgba(0,0,0,0.7)' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${sc.border}` }}>

              <div style={{ fontSize: '1.1rem', fontWeight: 600, color: sc.textHead }}>📚 Item Library</div>

              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>

                <button className="shop-btn" onClick={() => { setShowLibrary(false); openNewItemEditor(); }}

                  style={{ padding: '4px 12px', background: '#2a1a2a', border: '1px solid #5a3a5a', borderRadius: 4, color: '#c87ae0', fontSize: '0.78rem' }}>✨ Create Custom</button>

                <button className="shop-btn" onClick={() => { setShowLibrary(false); setLibSearch(''); setLibFilter('all'); }} style={{ background: 'none', border: 'none', color: sc.textMuted, fontSize: '1.1rem' }}>✕</button>

              </div>

            </div>

            <input value={libSearch} onChange={e => setLibSearch(e.target.value)} placeholder="Search items..." className="shop-input" style={{ width: '100%', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '8px 12px', color: sc.text, fontSize: '0.85rem', marginBottom: 10 }} />

            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 12 }}>

              {mergedCategories.map(c => { const count = Object.values(templates).filter(t => c.id === 'all' || t.category === c.id).length; if (c.id !== 'all' && count === 0) return null; return (<button key={c.id} className="shop-cat-btn" onClick={() => setLibFilter(c.id)} style={{ padding: '3px 10px', background: libFilter === c.id ? '#1a1a22' : 'transparent', border: `1px solid ${libFilter === c.id ? sc.borderActive : sc.borderLight}`, borderRadius: 12, color: libFilter === c.id ? sc.text : sc.textMuted, fontSize: '0.72rem', cursor: 'pointer' }}>{c.icon} {c.name}</button>); })}

            </div>

            <div style={{ flex: 1, overflow: 'auto' }}>

              {Object.values(templates).filter(t => (libFilter === 'all' || t.category === libFilter) && (!libSearch || t.name.toLowerCase().includes(libSearch.toLowerCase()))).sort((a, b) => { const ro = { common: 0, uncommon: 1, rare: 2, epic: 3, legendary: 4 }; return (ro[a.rarity] || 0) - (ro[b.rarity] || 0) || a.name.localeCompare(b.name); }).map(t => {

                const r = getRarity(t.rarity); const alreadyAdded = shop?.inventory.some(i => i.itemId === t.id);

                return (

                  <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: sc.bgDark, borderRadius: 4, marginBottom: 3, borderLeft: `3px solid ${r.color}40`, opacity: alreadyAdded ? 0.4 : 1 }}>

                    <div style={{ flex: 1, minWidth: 0 }}>

                      <div style={{ fontSize: '0.88rem', fontWeight: 500, color: r.color !== '#9a9a8a' ? r.color : sc.text }}>{getCatIcon(t.category)} {t.name}</div>

                      <div style={{ fontSize: '0.75rem', color: sc.textMuted }}>{t.shortDesc} · {formatPrice(t.basePrice * goldMultiplier)} base · {r.name}</div>

                    </div>

                    <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0, marginLeft: 8 }}>

                      {!alreadyAdded && (

                        <>

                          <input type="number" value={libStockValues[t.id] ?? 5} min="1" max="999"

                            onChange={e => setLibStockValues(prev => ({ ...prev, [t.id]: parseInt(e.target.value) || 1 }))}

                            onClick={e => e.stopPropagation()} className="shop-input"

                            style={{ width: 36, background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '2px 4px', color: sc.textSub, fontSize: '0.72rem', textAlign: 'center' }} title="Stock qty" />

                          <select onClick={e => e.stopPropagation()} onChange={e => { if (e.target.value) { handleAddItemToShop(t.id, e.target.value, libStockValues[t.id] ?? 5); e.target.value = ''; } }} defaultValue="" className="shop-input"

                            style={{ width: 'auto', background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '2px 4px', color: sc.textSubLight, fontSize: '0.65rem' }}>

                            <option value="" disabled>Secrecy...</option>

                            {SHOP_SECRECY_LEVELS.map(s => <option key={s.id} value={s.id}>{s.locks > 0 ? '🔒'.repeat(s.locks) : (s.id === 'visible' ? '✓' : '⊘')} {s.name}</option>)}

                          </select>

                        </>

                      )}

                      <button className="shop-btn" onClick={() => !alreadyAdded && handleAddItemToShop(t.id, 'visible', libStockValues[t.id] ?? 5)} disabled={alreadyAdded}

                        style={{ padding: '4px 12px', background: alreadyAdded ? sc.bgCard : sc.greenBg, border: `1px solid ${alreadyAdded ? sc.borderLight : sc.greenBorder}`, borderRadius: 4, color: alreadyAdded ? sc.textDim : sc.green, fontSize: '0.78rem' }}>

                        {alreadyAdded ? 'Added' : '+ Add'}

                      </button>

                    </div>

                  </div>

                );

              })}

            </div>

          </div>

        </div>

      )}



      {/* Satchel (Cart) Panel */}

      {showCart && (

        <>

          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1999, animation: 'fadeOverlay 0.2s ease', backdropFilter: 'blur(2px)' }} onClick={() => setShowCart(false)} />

          <div style={{ position: 'fixed', top: '50%', right: 24, transform: 'translateY(-50%)', width: 360, maxWidth: '90vw', maxHeight: '80vh', background: `linear-gradient(170deg, #12121a 0%, ${sc.bgBar} 100%)`, border: `1px solid ${sc.borderActive}`, borderRadius: 16, display: 'flex', flexDirection: 'column', zIndex: 2000, animation: 'modalIn 0.25s ease', boxShadow: '0 20px 60px rgba(0,0,0,0.7), 0 0 30px rgba(0,0,0,0.4)', overflow: 'hidden' }}>

            {/* Decorative top accent */}

            <div style={{ height: 3, background: 'linear-gradient(90deg, transparent, #d4aa4060, #d4aa4040, transparent)' }} />

            <div style={{ padding: '14px 18px', borderBottom: `1px solid ${sc.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

                <span style={{ fontSize: '1.2rem' }}>🎒</span>

                <div>

                  <span style={{ fontSize: '1rem', fontWeight: 600, color: sc.textHead }}>Satchel</span>

                  <span style={{ fontSize: '0.72rem', color: sc.textMuted, marginLeft: 8 }}>{cartCount} {cartCount === 1 ? 'item' : 'items'}</span>

                </div>

              </div>

              <button className="shop-btn" onClick={() => setShowCart(false)} style={{ background: 'none', border: 'none', color: sc.textMuted, fontSize: '1.1rem', padding: 4 }}>✕</button>

            </div>

            <div style={{ flex: 1, overflow: 'auto', padding: 14 }}>

              {normalizedCart.length === 0 ? (

                <div style={{ textAlign: 'center', padding: '40px 16px' }}>

                  <div style={{ fontSize: '2rem', marginBottom: 8, opacity: 0.5 }}>🎒</div>

                  <div style={{ color: sc.textDim, fontStyle: 'italic', fontSize: '0.88rem' }}>Your satchel is empty</div>

                  <div style={{ color: sc.textDim, fontSize: '0.72rem', marginTop: 4 }}>Browse the wares and add items to purchase</div>

                </div>

              ) : normalizedCart.map(ci => {

                const inv = findShopInventoryLineByKey(ci.lineKey);
                const item = getShopTemplateForInventoryLine(inv);
                if (!item || !inv) return null; const r = getRarity(item.rarity);

                return (<div key={ci.lineKey} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: sc.bgCard, borderRadius: 8, marginBottom: 6, borderLeft: `3px solid ${r.color}40` }}>

                  <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: '0.88rem', fontWeight: 500, color: sc.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div><div style={{ fontSize: '0.75rem', color: sc.gold }}>{formatPrice(inv.price)} ea → {formatPrice(inv.price * ci.qty)}</div></div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>

                    <QuantityStepper
                      value={ci.qty}
                      onCommit={(nextQty) => commitCartQuantity(ci.lineKey, nextQty)}
                      onChange={(event) => setCartQtyDrafts((drafts) => ({ ...drafts, [ci.lineKey]: event.target.value }))}
                      min={0}
                      max={inv.stock === null ? undefined : inv.stock}
                      inputLabel={`${item.name} cart quantity`}
                      decrementAriaLabel={`Decrease ${item.name} cart quantity`}
                      incrementAriaLabel={`Increase ${item.name} cart quantity`}
                      style={{ gap: 4 }}
                      inputStyle={{ width: 40, minWidth: 40, background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '2px 4px', color: sc.text, fontSize: '0.8rem' }}
                      buttonStyle={{ padding: '2px 7px', background: '#1a1a20', border: '1px solid #2a2a30', borderRadius: 4, color: sc.textSubLight, fontSize: '0.8rem' }}
                    />

                    <button className="shop-btn" onClick={() => removeCartItem(ci.lineKey)} style={{ padding: '2px 5px', background: 'none', border: 'none', color: '#5a3a3a', fontSize: '0.75rem', marginLeft: 2 }}>✕</button>

                  </div>

                </div>);

              })}

            </div>

            {normalizedCart.length > 0 && (

              <div style={{ padding: '14px 18px', borderTop: `1px solid ${sc.border}`, background: 'rgba(0,0,0,0.15)' }}>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, fontSize: '1.05rem' }}>

                  <span style={{ color: sc.text }}>Total:</span>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><ShopCoin size={16} /><span style={{ color: cartTotal <= charGoldBase ? sc.gold : '#b85a4a', fontWeight: 600 }}>{formatPrice(cartTotal)}</span></div>

                </div>

                {cartTotal > charGoldBase && <div style={{ fontSize: '0.75rem', color: '#b87a6a', marginBottom: 8 }}>Not enough coin ({charWealthDisplay} available)</div>}

                <button className="shop-buy-btn" onClick={handleCheckout} disabled={cartTotal > charGoldBase}

                  style={{ width: '100%', padding: '10px 16px', borderRadius: 8, fontSize: '0.95rem' }}>✓ Complete Purchase ({formatPrice(cartTotal)})</button>

              </div>

            )}

          </div>

        </>

      )}



      {/* Shop Settings Modal */}

      {showShopSettings && (

        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 2000, overflowY: 'auto', padding: 20, animation: 'fadeOverlay 0.2s ease' }} onClick={() => setShowShopSettings(false)}>

          <div onClick={e => e.stopPropagation()} style={{ background: `linear-gradient(180deg, ${sc.bgCard} 0%, ${sc.bgDarker} 100%)`, border: `2px solid ${sc.borderActive}`, borderRadius: 8, padding: 24, maxWidth: 520, width: '100%', marginTop: 20, animation: 'modalIn 0.25s ease', boxShadow: '0 20px 60px rgba(0,0,0,0.7)' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${sc.border}` }}>

              <div style={{ fontSize: '1.1rem', fontWeight: 600, color: sc.gold }}>⚙️ Shop Settings</div>

              <button className="shop-btn" onClick={() => setShowShopSettings(false)} style={{ background: 'none', border: 'none', color: sc.textMuted, fontSize: '1.1rem', padding: 4 }}>✕</button>

            </div>

            {/* Tabs */}

            <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>

              {[{ id: 'general', label: '🏪 General' }, { id: 'rarities', label: '🎨 Rarities' }, { id: 'categories', label: '📦 Categories' }, { id: 'campaigns', label: '🗺️ Campaigns' }].map(t => (

                <button key={t.id} className="shop-btn" onClick={() => setSettingsTab(t.id)}

                  style={{ flex: 1, padding: '6px 8px', fontSize: '0.72rem', background: settingsTab === t.id ? 'rgba(201,162,39,0.12)' : 'transparent', border: `1px solid ${settingsTab === t.id ? 'rgba(201,162,39,0.3)' : sc.borderLight}`, borderRadius: 6, color: settingsTab === t.id ? sc.gold : sc.textMuted }}>{t.label}</button>

              ))}

            </div>



            {/* General Tab - per-shop mode and campaign assignment */}

            {settingsTab === 'general' && shop && (

              <div>

                <div style={{ fontSize: '0.82rem', fontWeight: 500, color: sc.textHead, marginBottom: 10 }}>"{shop.name}" Settings</div>

                <div style={{ marginBottom: 14 }}>

                  <div style={{ fontSize: '0.72rem', color: sc.textMuted, marginBottom: 6 }}>Shop Mode</div>

                  <div style={{ display: 'flex', gap: 6 }}>

                    {[{ id: 'coin', label: '🪙 Coin Only', desc: 'Items purchased with currency' }, { id: 'trade', label: '⚖️ Trade Only', desc: 'All items require barter' }, { id: 'both', label: '🪙⚖️ Both', desc: 'Mix of purchasable and trade items' }].map(m => (

                      <button key={m.id} className="shop-btn" onClick={() => { onSaveShopData({ ...shopData, shops: shops.map(s => s.id === shop.id ? { ...s, mode: m.id } : s) }); }}

                        style={{ flex: 1, padding: '8px 6px', background: (shop.mode || 'coin') === m.id ? 'rgba(201,162,39,0.12)' : sc.bgDark, border: `1px solid ${(shop.mode || 'coin') === m.id ? 'rgba(201,162,39,0.4)' : sc.borderLight}`, borderRadius: 6, textAlign: 'center' }}>

                        <div style={{ fontSize: '0.82rem', color: (shop.mode || 'coin') === m.id ? sc.gold : sc.textSub }}>{m.label}</div>

                        <div style={{ fontSize: '0.58rem', color: sc.textDim, marginTop: 2 }}>{m.desc}</div>

                      </button>

                    ))}

                  </div>

                </div>

                <div style={{ marginBottom: 14 }}>

                  <div style={{ fontSize: '0.72rem', color: sc.textMuted, marginBottom: 6 }}>Item Display Icons</div>

                  <div style={{ display: 'flex', gap: 6 }}>

                    {[{ id: 'emoji', label: '😀 Emoji', desc: 'Category emojis (default)' }, { id: 'custom', label: '🖼️ Custom Art', desc: 'RPG artwork when available' }, { id: 'mixed', label: '🔀 Mixed', desc: 'Art for items that have it, emoji for rest' }].map(m => (

                      <button key={m.id} className="shop-btn" onClick={() => { onSaveShopData({ ...shopData, shops: shops.map(s => s.id === shop.id ? { ...s, iconMode: m.id } : s) }); }}

                        style={{ flex: 1, padding: '8px 6px', background: (shop.iconMode || 'mixed') === m.id ? 'rgba(201,162,39,0.12)' : sc.bgDark, border: `1px solid ${(shop.iconMode || 'mixed') === m.id ? 'rgba(201,162,39,0.4)' : sc.borderLight}`, borderRadius: 6, textAlign: 'center' }}>

                        <div style={{ fontSize: '0.82rem', color: (shop.iconMode || 'mixed') === m.id ? sc.gold : sc.textSub }}>{m.label}</div>

                        <div style={{ fontSize: '0.58rem', color: sc.textDim, marginTop: 2 }}>{m.desc}</div>

                      </button>

                    ))}

                  </div>

                </div>

                {campaigns.length > 0 && (

                  <div>

                    <div style={{ fontSize: '0.72rem', color: sc.textMuted, marginBottom: 6 }}>Assign to Campaigns</div>

                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>

                      <button className="shop-btn" onClick={() => { onSaveShopData({ ...shopData, shops: shops.map(s => s.id === shop.id ? { ...s, campaigns: [] } : s) }); }}

                        style={{ padding: '5px 10px', fontSize: '0.72rem', background: (shop.campaigns || []).length === 0 ? 'rgba(201,162,39,0.12)' : 'transparent', border: `1px solid ${(shop.campaigns || []).length === 0 ? 'rgba(201,162,39,0.3)' : sc.borderLight}`, borderRadius: 6, color: (shop.campaigns || []).length === 0 ? sc.gold : sc.textDim }}>🌐 General (All)</button>

                      {campaigns.map(c => {

                        const sel = (shop.campaigns || []).includes(c);

                        return <button key={c} className="shop-btn" onClick={() => {

                          const cur = shop.campaigns || [];

                          const next = sel ? cur.filter(x => x !== c) : [...cur, c];

                          onSaveShopData({ ...shopData, shops: shops.map(s => s.id === shop.id ? { ...s, campaigns: next } : s) });

                        }}

                          style={{ padding: '5px 10px', fontSize: '0.72rem', background: sel ? 'rgba(120,140,200,0.15)' : 'transparent', border: `1px solid ${sel ? 'rgba(120,140,200,0.35)' : sc.borderLight}`, borderRadius: 6, color: sel ? '#8a9ac8' : sc.textDim }}>{c}</button>;

                      })}

                    </div>

                    <div style={{ fontSize: '0.6rem', color: sc.textDim, marginTop: 4 }}>General = visible in all campaigns. Select specific campaigns to restrict.</div>

                  </div>

                )}

              </div>

            )}

            {settingsTab === 'general' && !shop && (

              <div style={{ padding: 20, textAlign: 'center', color: sc.textMuted, fontSize: '0.85rem' }}>Select a shop first to configure its settings.</div>

            )}



            {/* Rarities Tab */}

            {settingsTab === 'rarities' && (

              <div>

                <div style={{ fontSize: '0.72rem', color: sc.textDim, marginBottom: 10 }}>Customize rarity tier colors. Changes apply to all shops.</div>

                {Object.entries(mergedRarities).map(([id, r]) => (

                  <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, padding: '6px 10px', background: sc.bgDark, borderRadius: 6, border: `1px solid ${sc.borderLight}` }}>

                    <input type="color" value={r.color} onChange={e => {

                      const hex = e.target.value;

                      const newCustom = { ...customRarities, [id]: { ...r, color: hex, glow: `${hex}30`, border: `${hex}50` } };

                      onSaveShopData({ ...shopData, customRarities: newCustom });

                    }} style={{ width: 28, height: 28, border: 'none', borderRadius: 4, cursor: 'pointer', background: 'transparent' }} />

                    <div style={{ flex: 1 }}>

                      <div style={{ fontSize: '0.85rem', fontWeight: 500, color: r.color }}>{r.name}</div>

                    </div>

                    <span style={{ fontSize: '0.68rem', color: sc.textDim }}>{r.color}</span>

                    {customRarities[id] && (

                      <button className="shop-btn" onClick={() => { const c = { ...customRarities }; delete c[id]; onSaveShopData({ ...shopData, customRarities: c }); }}

                        style={{ padding: '2px 6px', background: 'none', border: `1px solid ${sc.borderLight}`, borderRadius: 3, color: sc.textDim, fontSize: '0.62rem' }}>Reset</button>

                    )}

                  </div>

                ))}

              </div>

            )}



            {/* Categories Tab */}

            {settingsTab === 'categories' && (

              <div>

                <div style={{ fontSize: '0.72rem', color: sc.textDim, marginBottom: 10 }}>Default categories and any custom ones you've added.</div>

                {mergedCategories.filter(c => c.id !== 'all').map(c => {

                  const isCustom = customCategories.find(cc => cc.id === c.id);

                  return (

                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, padding: '5px 10px', background: sc.bgDark, borderRadius: 4, border: `1px solid ${sc.borderLight}` }}>

                      <span style={{ fontSize: '1rem' }}>{c.icon}</span>

                      <span style={{ flex: 1, fontSize: '0.82rem', color: sc.textSubLight }}>{c.name}</span>

                      <span style={{ fontSize: '0.62rem', color: sc.textDim }}>{c.id}</span>

                      {isCustom && <button className="shop-btn" onClick={() => onSaveShopData({ ...shopData, customCategories: customCategories.filter(cc => cc.id !== c.id) })}

                        style={{ padding: '2px 6px', background: 'none', border: `1px solid #4a2a2a`, borderRadius: 3, color: '#b86a6a', fontSize: '0.62rem' }}>✕</button>}

                    </div>

                  );

                })}

                <div style={{ marginTop: 10, padding: 10, background: sc.bgDark, borderRadius: 6, border: `1px dashed ${sc.borderLight}` }}>

                  <div style={{ fontSize: '0.72rem', color: sc.gold, marginBottom: 6 }}>Add Custom Category</div>

                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>

                    <input value={newCategoryDraft.icon} onChange={e => setNewCategoryDraft({ ...newCategoryDraft, icon: e.target.value })} placeholder="📦"

                      style={{ width: 36, background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '4px', color: sc.text, fontSize: '1rem', textAlign: 'center' }} />

                    <input value={newCategoryDraft.name} onChange={e => setNewCategoryDraft({ ...newCategoryDraft, name: e.target.value, id: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '_') })} placeholder="Category name..."

                      style={{ flex: 1, background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '5px 8px', color: sc.text, fontSize: '0.82rem' }} />

                    <button className="shop-btn" onClick={() => {

                      if (!newCategoryDraft.name.trim() || !newCategoryDraft.id) return;

                      if (mergedCategories.find(c => c.id === newCategoryDraft.id)) { showToast('Category ID already exists', 'error'); return; }

                      onSaveShopData({ ...shopData, customCategories: [...customCategories, { ...newCategoryDraft }] });

                      setNewCategoryDraft({ id: '', name: '', icon: '📦' });

                      showToast('Category added');

                    }}

                      style={{ padding: '5px 10px', background: sc.greenBg, border: `1px solid ${sc.greenBorder}`, borderRadius: 4, color: sc.green, fontSize: '0.72rem', opacity: newCategoryDraft.name.trim() ? 1 : 0.4 }}

                      disabled={!newCategoryDraft.name.trim()}>+ Add</button>

                  </div>

                </div>

              </div>

            )}



            {/* Campaigns Tab */}

            {settingsTab === 'campaigns' && (

              <div>

                <div style={{ fontSize: '0.72rem', color: sc.textDim, marginBottom: 10 }}>Organize shops by campaign. Shops set to "General" appear in all campaigns.</div>

                {campaigns.length === 0 && (

                  <div style={{ padding: '16px', textAlign: 'center', color: sc.textMuted, fontSize: '0.82rem', background: sc.bgDark, borderRadius: 6, marginBottom: 10 }}>

                    No campaigns yet. Add one below to start organizing your shops.

                  </div>

                )}

                {campaigns.map(c => (

                  <div key={c} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, padding: '8px 12px', background: activeCampaign === c ? 'rgba(201,162,39,0.08)' : sc.bgDark, borderRadius: 6, border: `1px solid ${activeCampaign === c ? 'rgba(201,162,39,0.25)' : sc.borderLight}` }}>

                    {editingCampaign === c ? (

                      <input autoFocus value={c} onChange={e => { renameCampaign(c, e.target.value); setEditingCampaign(e.target.value); }} onBlur={() => setEditingCampaign(null)} onKeyDown={e => e.key === 'Enter' && setEditingCampaign(null)}

                        style={{ flex: 1, background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '4px 8px', color: sc.text, fontSize: '0.85rem' }} />

                    ) : (

                      <span style={{ flex: 1, fontSize: '0.88rem', color: activeCampaign === c ? sc.gold : sc.textSubLight, fontWeight: activeCampaign === c ? 600 : 400, cursor: 'pointer' }} onClick={() => setActiveCampaign(c)}>{c}</span>

                    )}

                    <span style={{ fontSize: '0.62rem', color: sc.textDim }}>{shops.filter(s => (s.campaigns || []).includes(c)).length} shops</span>

                    <button className="shop-btn" onClick={() => setActiveCampaign(activeCampaign === c ? 'all' : c)}

                      style={{ padding: '2px 8px', background: activeCampaign === c ? 'rgba(201,162,39,0.15)' : 'transparent', border: `1px solid ${activeCampaign === c ? 'rgba(201,162,39,0.3)' : sc.borderLight}`, borderRadius: 4, color: activeCampaign === c ? sc.gold : sc.textDim, fontSize: '0.62rem' }}>{activeCampaign === c ? '✓ Active' : 'Set Active'}</button>

                    <button className="shop-btn" onClick={() => setEditingCampaign(c)}

                      style={{ padding: '2px 6px', background: 'none', border: `1px solid ${sc.borderLight}`, borderRadius: 3, color: sc.textDim, fontSize: '0.65rem' }}>✏️</button>

                    <button className="shop-btn" onClick={() => { if (window.confirm(`Delete campaign "${c}"? Shops assigned to it will become General.`)) removeCampaign(c); }}

                      style={{ padding: '2px 6px', background: 'none', border: `1px solid #4a2a2a`, borderRadius: 3, color: '#b86a6a', fontSize: '0.65rem' }}>✕</button>

                  </div>

                ))}

                <div style={{ marginTop: 10, display: 'flex', gap: 6, alignItems: 'center' }}>

                  <input value={newCampaignName} onChange={e => setNewCampaignName(e.target.value)} placeholder="New campaign name..."

                    onKeyDown={e => { if (e.key === 'Enter' && newCampaignName.trim()) { addCampaign(newCampaignName); setNewCampaignName(''); } }}

                    style={{ flex: 1, background: sc.bgBar, border: `1px solid ${sc.borderMed}`, borderRadius: 4, padding: '6px 10px', color: sc.text, fontSize: '0.82rem' }} />

                  <button className="shop-btn" onClick={() => { if (newCampaignName.trim()) { addCampaign(newCampaignName); setNewCampaignName(''); } }}

                    style={{ padding: '6px 12px', background: sc.greenBg, border: `1px solid ${sc.greenBorder}`, borderRadius: 4, color: sc.green, fontSize: '0.78rem', opacity: newCampaignName.trim() ? 1 : 0.4 }}

                    disabled={!newCampaignName.trim()}>+ Add Campaign</button>

                </div>

              </div>

            )}

          </div>

        </div>

      )}



      {/* Toast */}

      {toast && (

        <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 3000, background: toast.type === 'success' ? sc.greenBg : toast.type === 'error' ? sc.redBg : sc.blueBg, border: `1px solid ${toast.type === 'success' ? `${sc.green}40` : toast.type === 'error' ? `${sc.red}40` : `${sc.blue}40`}`, padding: '10px 20px', borderRadius: 8, color: toast.type === 'success' ? sc.green : toast.type === 'error' ? sc.red : sc.blue, fontSize: '0.88rem', fontWeight: 500, animation: 'shopSlideUp 0.3s ease', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>

          {toast.message}

        </div>

      )}

    </div>

  );

};







// ============================================================================

// CRAFTING SYSTEM — Integrated from crafting_system.jsx

// ============================================================================



const injectCraftingStyles = () => {

  let style = document.getElementById('crafting-system-styles');

  if (!style) {

    style = document.createElement('style');

    style.id = 'crafting-system-styles';

    document.head.appendChild(style);

  }

  style.textContent = `@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Alegreya:ital,wght@0,400;0,500;0,600;1,400&family=Alegreya+Sans:wght@400;500;700&display=swap');

.crafting-scope {

  --bg0:#0e0a0f;--bg1:#16101a;--bg2:#1e1624;--bg2h:#261d2e;--bgi:#130e17;--bs:#2a2033;

  --bg:#8b6914;--bgb:#c9951a;--bp:#6b3fa0;

  --tp:#e8ddd0;--ts:#9a8b7a;--tm:#5e5266;--tg:#d4a832;--tgb:#f0c240;

  --tgr:#5cb85c;--tgrb:#7dd87d;--tr:#d44040;--trb:#f06060;--tpu:#b388e8;--to:#d4883a;

  --ag:#2d7a2d;--agb:#1a3a1a;--ar:#7a2d2d;--arb:#3a1a1a;--ap:#6b3fa0;--apb:#2a1a3a;

  --gg:0 0 12px rgba(201,149,26,.3);--gn:0 0 12px rgba(93,184,93,.3);--gp:0 0 12px rgba(107,63,160,.3);--gu:0 0 16px rgba(212,168,50,.5);

  --rs:4px;--rm:8px;--rl:12px;

  --prof-gold:#d4a832;--prof-gold-glow:rgba(212,168,50,.4);

  --prof-silver:#a8a8b8;

  --quality-common:#9a8b7a;--quality-fine:#5cb85c;--quality-superior:#4a9ad4;--quality-master:#b388e8;--quality-legendary:#f0c240;

}

.crafting-scope *, .crafting-scope *::before, .crafting-scope *::after { box-sizing:border-box; }

.crafting-scope { background:var(--bg0); color:var(--tp); font-family:'Alegreya Sans',sans-serif; font-size:15px; min-height:100vh; }

.crafting-scope ::-webkit-scrollbar { width:6px; height:6px; }

.crafting-scope ::-webkit-scrollbar-track { background:var(--bg1); }

.crafting-scope ::-webkit-scrollbar-thumb { background:var(--bs); border-radius:3px; }

.crafting-scope input, .crafting-scope select, .crafting-scope textarea, .crafting-scope button { font-family: inherit; }

.crafting-scope input::placeholder { color: var(--tm); opacity: 1; }

@keyframes fi { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }

@keyframes shim { 0% { transform:translateX(-100%); } 100% { transform:translateX(100%); } }

@keyframes gatherPop { 0% { transform:scale(.97); } 50% { transform:scale(1.02); } 100% { transform:scale(1); } }

@keyframes popIn { from { transform:scale(.5); opacity:0; } to { transform:scale(1); opacity:1; } }

@keyframes failFlash { 0%,100% { background:var(--bgi); } 30% { background:rgba(212,64,64,.15); } }

@keyframes successFlash { 0%,100% { background:var(--bgi); } 30% { background:rgba(93,184,93,.12); } }

.crafting-scope .fade { animation: fi .3s ease; }

.crafting-scope .rar-common{color:#9a8b7a} .crafting-scope .rar-uncommon{color:#5cb85c} .crafting-scope .rar-rare{color:#4a9ad4}

.crafting-scope .rar-veryrare{color:#b388e8} .crafting-scope .rar-legendary{color:#f0c240} .crafting-scope .rar-artifact{color:#d44040}

/* 2026-05-09: Category badges are intentionally muted now — bright red
   on "weapons" was visually fighting the red-when-locked profession
   gates, making the level-gate signal feel like just another decoration.
   New scheme: a single neutral slate base for all categories with a tiny
   colored dot at the start to keep the per-category color cue. The
   profession-gate badges (.prof-req below) own the bold semantic colors
   (green = met, red = locked) so they read as serious gates, not decoration. */
.crafting-scope .catb{padding:1px 6px;border-radius:3px;border:1px solid var(--bs);background:rgba(255,255,255,.03);color:var(--ts);font-size:10px;display:inline-flex;align-items:center;gap:5px;font-family:'Alegreya Sans',sans-serif;letter-spacing:0.2px;text-transform:lowercase}
.crafting-scope .catb::before{content:"";width:6px;height:6px;border-radius:50%;background:var(--catb-dot,#9a8b7a);flex-shrink:0}

.crafting-scope .catb-weapons{--catb-dot:#c46060}
.crafting-scope .catb-armor{--catb-dot:#5a9ad4}
.crafting-scope .catb-potions{--catb-dot:#6db86d}
.crafting-scope .catb-tools{--catb-dot:#d4a832}
.crafting-scope .catb-materials{--catb-dot:#9a8b7a}
.crafting-scope .catb-food{--catb-dot:#d4883a}

/* 2026-05-09: Profession requirement badge. Sharp corners (3px) and
   bolder treatment than the muted catb badges so it reads as a real
   gate. .met = green border + green text (you can craft it). .unmet =
   red border + red bold text + 🔒 prefix in markup; pair with
   .recipe-row.locked on the parent for the full grayscale effect. */
.crafting-scope .prof-req{display:inline-flex;align-items:center;gap:5px;padding:2px 8px;border-radius:3px;border:1px solid;font-size:10px;font-weight:700;font-family:'Cinzel',serif;letter-spacing:0.4px;line-height:1.3;white-space:nowrap}
.crafting-scope .prof-req.met{border-color:var(--ag);background:rgba(93,184,93,.08);color:var(--tgr)}
.crafting-scope .prof-req.unmet{border-color:var(--ar);background:rgba(212,64,64,.08);color:var(--tr)}
.crafting-scope .prof-req .prof-icon{font-weight:400;font-size:11px;line-height:1}

/* 2026-05-09: Locked recipe row — stronger desaturation than the previous
   opacity:.45/grayscale:.5 so locked rows clearly read as out-of-reach
   without disappearing. Pairs with .prof-req.unmet on the same row. */
.crafting-scope .recipe-row.locked{opacity:.55;filter:grayscale(.7)}
.crafting-scope .recipe-row.locked:hover{opacity:.75}

/* 2026-05-09: Recipe section header — collapsible. Subtle but distinct
   from page chrome so the four-section split (Available / Need materials
   / Profession too low / Other professions) is immediately scannable. */
.crafting-scope .rsec{margin-bottom:8px}
.crafting-scope .rsec-hdr{display:flex;align-items:center;gap:6px;padding:7px 10px;background:var(--bgi);border:1px solid var(--bs);border-radius:var(--rm);cursor:pointer;user-select:none;transition:background .15s,border-color .15s;min-width:0}
.crafting-scope .rsec-hdr:hover{background:var(--bg2);border-color:var(--bg2h)}
.crafting-scope .rsec-hdr .rsec-caret{font-size:10px;color:var(--tm);transition:transform .15s;flex-shrink:0}
.crafting-scope .rsec.open .rsec-hdr .rsec-caret{transform:rotate(90deg)}
/* white-space:nowrap + flex-shrink:0 keeps "Available now" / "Profession
   too low" / etc. on a single line even when the panel is at its
   minimum width (280px) and the pill cluster + count are competing for
   space. Letter-spacing trimmed from .5 to .3 to reclaim a few pixels. */
.crafting-scope .rsec-hdr .rsec-title{font-family:'Cinzel',serif;font-size:12px;letter-spacing:0.3px;text-transform:uppercase;white-space:nowrap;flex-shrink:0}
.crafting-scope .rsec-hdr .rsec-count{font-size:11px;color:var(--tm);background:var(--bg1);padding:1px 7px;border-radius:10px;margin-left:auto;flex-shrink:0}
.crafting-scope .rsec-body{display:flex;flex-direction:column;gap:4px;padding:6px 0 4px 0}
.crafting-scope .rsec-empty{font-size:11px;color:var(--tm);font-style:italic;padding:4px 10px}
.crafting-scope .rsec.tone-available .rsec-title{color:var(--tgr)}
.crafting-scope .rsec.tone-materials .rsec-title{color:var(--tg)}
.crafting-scope .rsec.tone-locked .rsec-title{color:var(--tr)}
.crafting-scope .rsec.tone-other .rsec-title{color:var(--tm)}

/* 2026-05-09b: per-profession quick-filter pills, rendered inline with
   the "Available now" section header. They scope the entire list (not
   just the section they sit in) — toggling Blacksmithing off hides
   blacksmith recipes from every section. ON state = filled green;
   OFF state = outlined and dimmed. */
.crafting-scope .prof-pills{display:inline-flex;align-items:center;gap:4px;flex-wrap:wrap;margin-left:6px;min-width:0}
.crafting-scope .prof-pill{display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:11px;border:1px solid;font-size:10px;font-family:'Alegreya Sans',sans-serif;font-weight:600;cursor:pointer;transition:all .15s;user-select:none;line-height:1.4;white-space:nowrap;background:transparent}
.crafting-scope .prof-pill .prof-pill-icon{font-size:11px;line-height:1}
/* Icon-only profession pill: square, not pill-shape, so it reads as a
   compact toggle instead of an empty pill. The hover tooltip carries
   the profession name. */
.crafting-scope .prof-pill.icon-only{padding:2px 6px;border-radius:6px;min-width:24px;justify-content:center}
.crafting-scope .prof-pill.icon-only .prof-pill-icon{font-size:13px}
.crafting-scope .prof-pill.on{border-color:var(--ag);background:rgba(93,184,93,.12);color:var(--tgr)}
.crafting-scope .prof-pill.on:hover{background:rgba(93,184,93,.2);box-shadow:0 0 6px rgba(93,184,93,.25)}
.crafting-scope .prof-pill.off{border-color:var(--bs);background:transparent;color:var(--tm);opacity:.55}
.crafting-scope .prof-pill.off:hover{opacity:1;border-color:var(--ts);color:var(--ts)}
/* Strikethrough only on labeled (Misc) pills. On icon-only pills it
   would cut the icon in half. */
.crafting-scope .prof-pill.off:not(.icon-only){text-decoration:line-through;text-decoration-color:rgba(94,82,102,.5)}
.crafting-scope .prof-pill.icon-only.off{filter:grayscale(.8)}

@media(max-width:768px) {

  .crafting-scope { -webkit-text-size-adjust:100%; }

  .crafting-scope ::-webkit-scrollbar { width:3px; height:3px; }

}

`;

};



// 2026-04-27: Profession System styles, ported verbatim from

// src/professions.html (lines 41-1300). The user requested EXACT visual

// fidelity to the prototype so we inject the prototype's CSS as-is for

// the profession-specific class families (.prof-*, .pc-*, .pm-*, .ph-*,

// .pf-chip, .ps-*, .qp-*, .pcard, .pick-*, .pb-*, .pb-hint, .lvlup-*,

// .tt-*, .xp-*, .q-result-*, .cres-quality-badge, .feat-banner, .fb-*,

// .empty-slot, .es-*, .slot-hdr, .sh-count, .quality-preview,

// .locked-notice, .discovery-notice, .prof-grid, .prof-strip,

// .prof-talents-btn, .prof-modal-overlay, .picker-overlay, .prof-filter,

// .prof-pill, .ud-lvl, .unlock-*, .pts-badge). Layout-conflicting

// classes (.app/.pnl/.tabs/.tab/.toast/.dtabs/.dtab/.dms-*/.dm-*/.rhd/

// .rcard/.rico/.rinfo/.rname/.rmeta/.cview/.cctl/.cbtn/.isec/.slbl/

// .islots/.irow/.iico/.inm/.ict/.rprev/.brow/.btag/.fclay/.craft-table/

// .inv-*/.ct-*/.fc-*/.guide-*/.shop-*/.scroll-*/.sd-*/.pconfirm*/

// .learn-*/.cbadge/.cnt) are deliberately omitted — those layout zones

// stay rendered by their existing React markup. As Phase 4 lands, those

// React zones will be rewritten to use the prototype classes and the

// remaining CSS will be added then.

const injectProfessionStyles = () => {

  let style = document.getElementById('profession-system-styles');

  if (!style) {

    style = document.createElement('style');

    style.id = 'profession-system-styles';

    document.head.appendChild(style);

  }

  style.textContent = `

/* ========= PROFESSION HUD (top-bar emblem) ========= */

.prof-hud{display:flex;align-items:center;gap:8px;background:var(--bgi);border:1px solid var(--bs);border-radius:20px;padding:4px 4px 4px 12px;cursor:pointer;transition:all .2s;max-width:320px;position:relative;font-family:'Alegreya Sans',sans-serif}

.prof-hud:hover{border-color:var(--prof-gold);box-shadow:0 0 8px var(--prof-gold-glow)}

.prof-hud.empty{border-color:var(--ar);background:rgba(122,45,45,.08)}

.prof-hud.empty:hover{border-color:var(--tr)}

.prof-hud .ph-emblem{width:28px;height:28px;border-radius:50%;background:linear-gradient(135deg,var(--bg2),var(--bg1));border:1px solid var(--prof-gold);display:flex;align-items:center;justify-content:center;font-size:16px;flex-shrink:0;box-shadow:inset 0 0 4px rgba(212,168,50,.2)}

.prof-hud .ph-info{min-width:0;flex:1}

.prof-hud .ph-name{font-family:'Cinzel',serif;font-size:12px;color:var(--prof-gold);line-height:1.1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}

.prof-hud .ph-lvl{font-size:10px;color:var(--ts);line-height:1}

.prof-hud .ph-bar{height:3px;width:68px;background:var(--bg1);border-radius:2px;overflow:hidden;margin-top:3px;position:relative}

.prof-hud .ph-fill{height:100%;background:linear-gradient(90deg,var(--bg),var(--bgb),var(--tgb));border-radius:2px;transition:width .5s cubic-bezier(.4,.2,.2,1);box-shadow:0 0 4px var(--prof-gold-glow)}

.prof-hud .ph-chev{width:28px;height:28px;border-radius:50%;background:var(--bg2);display:flex;align-items:center;justify-content:center;font-size:11px;color:var(--ts);flex-shrink:0;transition:all .2s}

.prof-hud:hover .ph-chev{color:var(--tp);background:var(--bg2h)}

.prof-hud .ph-burst{position:absolute;top:-4px;right:-4px;background:var(--tgb);color:#000;font-size:9px;font-weight:900;padding:1px 5px;border-radius:8px;animation:profPulse 1.2s ease infinite;font-family:'Cinzel',serif;letter-spacing:.5px}

@keyframes profPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.08)}}



/* ========= FEATURE BANNER (no-profession onboarding) ========= */

.feat-banner{background:linear-gradient(135deg,rgba(107,63,160,.12),rgba(212,168,50,.06));border:1px solid var(--ap);border-radius:var(--rl);padding:20px;display:flex;align-items:center;gap:16px;margin-bottom:18px;font-family:'Alegreya Sans',sans-serif}

.feat-banner .fb-icon{font-size:40px}

.feat-banner .fb-body{flex:1}

.feat-banner .fb-title{font-family:'Cinzel',serif;font-size:18px;color:var(--tpu);margin-bottom:4px}

.feat-banner .fb-sub{font-size:13px;color:var(--ts);line-height:1.4}

.feat-banner button{padding:10px 24px;border-radius:var(--rm);border:none;background:linear-gradient(135deg,var(--ap),#8855cc);color:#fff;font-family:'Cinzel',serif;font-size:14px;font-weight:700;cursor:pointer;box-shadow:var(--gp);transition:transform .2s}

.feat-banner button:hover{transform:translateY(-1px)}



/* ========= PROFESSION FILTER CHIPS ========= */

.prof-filter{display:flex;flex-wrap:wrap;gap:5px;margin-bottom:12px}

.pf-chip{padding:4px 10px;border:1px solid var(--bs);border-radius:14px;background:0 0;color:var(--ts);font-size:11px;font-family:'Cinzel',serif;cursor:pointer;transition:all .2s;display:inline-flex;align-items:center;gap:4px;letter-spacing:.3px}

.pf-chip:hover{border-color:var(--prof-gold);color:var(--tp)}

.pf-chip.on{background:rgba(212,168,50,.12);border-color:var(--prof-gold);color:var(--prof-gold);box-shadow:inset 0 0 6px rgba(212,168,50,.15)}



/* ========= PROFESSION PILL (on recipe cards) ========= */

.prof-pill{display:inline-flex;align-items:center;gap:3px;padding:1px 7px;border-radius:10px;font-size:10px;font-weight:700;border:1px solid;font-family:'Cinzel',serif;letter-spacing:.3px}

.prof-pill.ok{background:rgba(93,184,93,.1);color:var(--tgr);border-color:var(--ag)}

.prof-pill.lck{background:rgba(212,64,64,.08);color:var(--tr);border-color:var(--ar)}

.prof-pill.mnr{background:rgba(168,168,184,.06);color:var(--prof-silver);border-color:rgba(168,168,184,.3)}



/* ========= PROFESSION CONTEXT STRIP (crafting station) ========= */

.prof-strip{display:flex;align-items:center;gap:10px;padding:10px 14px;background:linear-gradient(135deg,rgba(212,168,50,.06),rgba(107,63,160,.04));border:1px solid rgba(212,168,50,.25);border-radius:var(--rm);position:relative;overflow:hidden;font-family:'Alegreya Sans',sans-serif}

.prof-strip::before{content:'';position:absolute;left:0;top:0;bottom:0;width:3px;background:linear-gradient(180deg,var(--bg),var(--bgb),var(--bg))}

.prof-strip .ps-emb{font-size:22px;margin-left:4px}

.prof-strip .ps-body{flex:1;min-width:0}

.prof-strip .ps-lbl{font-family:'Cinzel',serif;font-size:10px;color:var(--ts);letter-spacing:1px;text-transform:uppercase}

.prof-strip .ps-prof{font-family:'Cinzel',serif;font-size:14px;color:var(--prof-gold);display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:2px}

.prof-strip .ps-lvl{background:var(--bg1);border:1px solid var(--bs);padding:1px 8px;border-radius:10px;font-size:11px;color:var(--tp)}

.prof-strip .ps-xp-wrap{display:flex;align-items:center;gap:6px;min-width:0}

.prof-strip .ps-xp-bar{height:5px;width:110px;background:var(--bg1);border-radius:3px;overflow:hidden;position:relative;border:1px solid var(--bs)}

.prof-strip .ps-xp-fill{height:100%;background:linear-gradient(90deg,var(--bg),var(--bgb));border-radius:3px;transition:width .5s cubic-bezier(.4,.2,.2,1)}

.prof-strip .ps-xp-txt{font-size:10px;color:var(--ts);font-family:'Cinzel',serif;white-space:nowrap}

.prof-strip .ps-quality{display:flex;align-items:center;gap:4px;font-size:11px;color:var(--tpu);white-space:nowrap}

.prof-strip.locked{background:rgba(122,45,45,.08);border-color:var(--ar)}

.prof-strip.locked::before{background:var(--tr)}

.prof-strip.locked .ps-prof{color:var(--tr)}



/* Quality preview bar (under prof-strip) */

.quality-preview{padding:10px 14px;background:var(--bgi);border:1px solid var(--bs);border-radius:var(--rm);margin-top:4px;font-family:'Alegreya Sans',sans-serif}

.qp-label{font-family:'Cinzel',serif;font-size:10px;color:var(--ts);letter-spacing:1px;margin-bottom:6px;display:flex;align-items:center;justify-content:space-between}

.qp-track{display:flex;gap:2px;height:8px;border-radius:3px;overflow:hidden}

.qp-seg{flex:1;background:var(--bg1);transition:all .3s;position:relative}

.qp-seg.on{background:linear-gradient(135deg,var(--quality-common),var(--quality-fine))}

.qp-seg.fine{background:var(--quality-fine)}

.qp-seg.superior{background:var(--quality-superior)}

.qp-seg.master{background:var(--quality-master)}

.qp-seg.legendary{background:var(--quality-legendary);box-shadow:inset 0 0 4px rgba(240,194,64,.8)}

.qp-legend{display:flex;justify-content:space-between;margin-top:5px;font-size:9px;color:var(--tm);font-family:'Cinzel',serif;letter-spacing:.5px}



/* ========= LOCKED / DISCOVERY NOTICES ========= */

.locked-notice{padding:16px 18px;background:rgba(122,45,45,.12);border:1px solid var(--ar);border-radius:var(--rm);color:var(--tr);font-size:13px;display:flex;align-items:center;gap:10px;font-family:'Alegreya Sans',sans-serif}

.locked-notice .ln-ico{font-size:24px;flex-shrink:0}

.locked-notice .ln-body{flex:1}

.locked-notice .ln-title{font-family:'Cinzel',serif;font-weight:700;color:var(--trb);margin-bottom:2px}

.locked-notice .ln-sub{color:var(--ts);font-size:12px}

.discovery-notice{padding:12px 14px;background:linear-gradient(135deg,rgba(107,63,160,.1),rgba(212,168,50,.05));border:1px solid var(--ap);border-radius:var(--rm);display:flex;align-items:center;gap:10px;margin-top:10px;font-family:'Alegreya Sans',sans-serif}

.discovery-notice .dn-ico{font-size:22px;animation:profSparkle 2s ease infinite}

@keyframes profSparkle{0%,100%{transform:rotate(0) scale(1)}25%{transform:rotate(5deg) scale(1.1)}75%{transform:rotate(-5deg) scale(1.05)}}

.discovery-notice .dn-body{flex:1;font-size:12px;color:var(--tp)}

.discovery-notice .dn-title{font-family:'Cinzel',serif;color:var(--tpu);font-weight:700;margin-bottom:2px}

.discovery-notice .dn-xp{color:var(--tgb);font-weight:700}



/* ========= XP FLOATER ========= */

.xp-floater{position:fixed;pointer-events:none;z-index:2000;font-family:'Cinzel',serif;font-weight:900;font-size:18px;color:var(--tgb);text-shadow:0 0 8px rgba(240,194,64,.9),0 2px 4px rgba(0,0,0,.8);animation:xpRise 1.5s cubic-bezier(.25,.9,.3,1) forwards;letter-spacing:1px;white-space:nowrap}

.xp-floater.bonus{color:#f0c240;font-size:22px}

.xp-floater.mega{color:#f8d060;font-size:26px;animation:xpRiseMega 2s cubic-bezier(.25,.9,.3,1) forwards}

.xp-floater .xp-label{font-size:11px;color:var(--tpu);display:block;text-align:center;margin-top:-2px;letter-spacing:2px}

@keyframes xpRise{0%{transform:translate(-50%,0) scale(.5);opacity:0}15%{transform:translate(-50%,-10px) scale(1.2);opacity:1}30%{transform:translate(-50%,-20px) scale(1);opacity:1}100%{transform:translate(-50%,-80px) scale(.9);opacity:0}}

@keyframes xpRiseMega{0%{transform:translate(-50%,0) scale(.3) rotate(-10deg);opacity:0}20%{transform:translate(-50%,-15px) scale(1.4) rotate(0);opacity:1}40%{transform:translate(-50%,-30px) scale(1.1) rotate(0);opacity:1}100%{transform:translate(-50%,-120px) scale(1) rotate(5deg);opacity:0}}



/* ========= LEVEL UP BANNER ========= */

.lvlup-banner{position:fixed;top:70px;left:50%;transform:translateX(-50%) translateY(-200px);z-index:1500;width:min(90vw,520px);background:linear-gradient(135deg,#2a1a3a 0%,#1e1624 50%,#3a2a1a 100%);border:2px solid var(--bgb);border-radius:var(--rl);padding:18px 24px;box-shadow:0 10px 40px rgba(212,168,50,.3),0 0 80px rgba(212,168,50,.15);transition:transform .6s cubic-bezier(.2,1.3,.5,1),opacity .4s;opacity:0;pointer-events:auto;font-family:'Alegreya Sans',sans-serif}

.lvlup-banner.show{transform:translateX(-50%) translateY(0);opacity:1}

.lvlup-banner::before{content:'';position:absolute;inset:-1px;border-radius:var(--rl);background:linear-gradient(135deg,transparent 30%,rgba(240,194,64,.3) 50%,transparent 70%);background-size:200% 200%;animation:shimmerBorder 2.5s ease infinite;z-index:-1}

@keyframes shimmerBorder{0%{background-position:200% 200%}100%{background-position:-200% -200%}}

.lvlup-inner{display:flex;align-items:center;gap:16px}

.lvlup-emb{width:56px;height:56px;border-radius:50%;background:radial-gradient(circle,rgba(240,194,64,.3),rgba(201,149,26,.1));border:2px solid var(--prof-gold);display:flex;align-items:center;justify-content:center;font-size:28px;flex-shrink:0;animation:embPulse 1.5s ease infinite;box-shadow:0 0 20px rgba(212,168,50,.6)}

@keyframes embPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.08)}}

.lvlup-body{flex:1;min-width:0}

.lvlup-title{font-family:'Cinzel',serif;font-size:11px;color:var(--tgb);letter-spacing:3px;font-weight:700}

.lvlup-name{font-family:'Cinzel',serif;font-size:20px;color:var(--tp);font-weight:700;margin-top:2px}

.lvlup-lvl{font-family:'Cinzel',serif;font-size:14px;color:var(--prof-gold);margin-top:2px}

.lvlup-unlock{font-size:12px;color:var(--tpu);font-style:italic;margin-top:4px}

.lvlup-close{width:28px;height:28px;border-radius:50%;border:1px solid var(--bs);background:var(--bg2);color:var(--ts);cursor:pointer;font-size:14px;display:flex;align-items:center;justify-content:center;flex-shrink:0}

.lvlup-close:hover{color:var(--tp);background:var(--bg2h)}



/* ========= QUALITY RESULT FLASH ========= */

.q-result-card{padding:14px 16px;background:var(--bgi);border:2px solid var(--bs);border-radius:var(--rm);text-align:center;font-family:'Cinzel',serif}

.q-result-card .qr-roll{font-size:11px;color:var(--ts);letter-spacing:2px;margin-bottom:6px}

.q-result-card .qr-name{font-size:18px;font-weight:700;margin-bottom:4px}

.q-result-card .qr-desc{font-family:'Alegreya',serif;font-size:12px;color:var(--ts);font-style:italic}

.q-result-card.common{border-color:var(--quality-common)}

.q-result-card.common .qr-name{color:var(--quality-common)}

.q-result-card.fine{border-color:var(--quality-fine);box-shadow:0 0 10px rgba(93,184,93,.2)}

.q-result-card.fine .qr-name{color:var(--quality-fine)}

.q-result-card.superior{border-color:var(--quality-superior);box-shadow:0 0 14px rgba(74,154,212,.3)}

.q-result-card.superior .qr-name{color:var(--quality-superior)}

.q-result-card.master{border-color:var(--quality-master);box-shadow:0 0 18px rgba(179,136,232,.4)}

.q-result-card.master .qr-name{color:var(--quality-master)}

.q-result-card.legendary{border-color:var(--quality-legendary);box-shadow:0 0 24px rgba(240,194,64,.5);animation:legendaryFlash 2s ease infinite}

.q-result-card.legendary .qr-name{color:var(--quality-legendary);text-shadow:0 0 8px rgba(240,194,64,.6)}

@keyframes legendaryFlash{0%,100%{box-shadow:0 0 24px rgba(240,194,64,.5)}50%{box-shadow:0 0 32px rgba(240,194,64,.8)}}



/* ========= PROFESSION PANEL (modal) ========= */

.prof-modal-overlay{position:fixed;inset:0;z-index:1200;background:rgba(0,0,0,.7);backdrop-filter:blur(3px);display:flex;align-items:flex-start;justify-content:center;padding:40px 20px;overflow-y:auto;animation:profFade .2s ease;font-family:'Alegreya Sans',sans-serif}

@keyframes profFade{from{opacity:0}to{opacity:1}}

.prof-panel{width:100%;max-width:920px;background:var(--bg1);border:1px solid var(--bs);border-radius:var(--rl);overflow:hidden;animation:profSlideDown .3s cubic-bezier(.2,.8,.4,1)}

@keyframes profSlideDown{from{transform:translateY(-20px);opacity:0}to{transform:translateY(0);opacity:1}}

.pm-hdr{padding:18px 24px;background:linear-gradient(135deg,rgba(212,168,50,.08),rgba(107,63,160,.05));border-bottom:1px solid var(--bs);display:flex;align-items:center;justify-content:space-between}

.pm-hdr-l{display:flex;align-items:center;gap:12px}

.pm-hdr-t{font-family:'Cinzel',serif;font-size:22px;color:var(--prof-gold);letter-spacing:1px}

.pm-hdr-sub{font-size:12px;color:var(--ts);margin-top:2px}

.pm-close{width:32px;height:32px;border-radius:50%;border:1px solid var(--bs);background:var(--bg2);color:var(--ts);cursor:pointer;font-size:16px;display:flex;align-items:center;justify-content:center}

.pm-close:hover{color:var(--tp);background:var(--bg2h)}

.pm-body{padding:20px 24px;max-height:calc(100vh - 180px);overflow-y:auto}

.slot-hdr{font-family:'Cinzel',serif;font-size:13px;color:var(--to);letter-spacing:1.5px;margin-bottom:10px;display:flex;align-items:center;gap:6px}

.slot-hdr .sh-count{background:var(--bg2);color:var(--tp);padding:2px 8px;border-radius:10px;font-size:11px;margin-left:auto;font-family:'Alegreya Sans',sans-serif;letter-spacing:0}

.prof-grid{display:grid;gap:12px;margin-bottom:24px}

.prof-grid.major{grid-template-columns:repeat(auto-fill,minmax(260px,1fr))}

.prof-grid.minor{grid-template-columns:repeat(auto-fill,minmax(220px,1fr))}

.crafting-scope hr.sep{border:none;border-top:1px solid var(--bs);margin:18px 0}



/* Profession card */

.pcard{background:linear-gradient(135deg,var(--bg2),var(--bgi));border:1px solid var(--bs);border-radius:var(--rm);padding:14px;position:relative;overflow:hidden;transition:all .25s;font-family:'Alegreya Sans',sans-serif}

.pcard.major{border-color:var(--prof-gold);background:linear-gradient(135deg,rgba(212,168,50,.06),var(--bgi))}

.pcard.major::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent,var(--prof-gold),transparent)}

.pcard.minor{border-color:rgba(168,168,184,.3)}

.pcard.maxed{border-color:var(--quality-legendary);box-shadow:0 0 20px rgba(240,194,64,.2)}

.pcard.maxed::after{content:'MAX';position:absolute;top:8px;right:8px;font-family:'Cinzel',serif;font-size:9px;color:var(--quality-legendary);background:var(--bg1);padding:2px 6px;border-radius:3px;border:1px solid var(--quality-legendary);letter-spacing:1px;font-weight:900}

.pc-hdr{display:flex;align-items:center;gap:10px;margin-bottom:10px}

.pc-emblem{width:44px;height:44px;border-radius:var(--rm);background:linear-gradient(135deg,var(--bg1),var(--bg2));border:1px solid var(--bs);display:flex;align-items:center;justify-content:center;font-size:24px;flex-shrink:0;position:relative}

.pcard.major .pc-emblem{border-color:var(--prof-gold);box-shadow:inset 0 0 6px rgba(212,168,50,.2)}

.pc-info{flex:1;min-width:0}

.pc-name{font-family:'Cinzel',serif;font-size:15px;color:var(--tp);font-weight:700;line-height:1.1}

.pcard.major .pc-name{color:var(--prof-gold)}

.pc-type{font-size:10px;color:var(--ts);letter-spacing:1px;font-family:'Cinzel',serif;text-transform:uppercase;margin-top:2px}

.pc-menu{width:24px;height:24px;border-radius:50%;border:1px solid var(--bs);background:var(--bg2);color:var(--ts);cursor:pointer;font-size:14px;display:flex;align-items:center;justify-content:center;flex-shrink:0}

.pc-menu:hover{color:var(--tp);background:var(--bg2h)}

.pc-lvl-row{display:flex;align-items:baseline;gap:8px;margin-bottom:8px}

.pc-lvl-big{font-family:'Cinzel',serif;font-size:28px;font-weight:700;color:var(--tp);line-height:1}

.pc-lvl-lbl{font-family:'Cinzel',serif;font-size:10px;color:var(--ts);letter-spacing:2px;text-transform:uppercase}

.pc-xp-text{margin-left:auto;font-size:11px;color:var(--ts);font-family:'Cinzel',serif}

.pc-xpbar{height:8px;background:var(--bg1);border-radius:4px;overflow:hidden;border:1px solid var(--bs);position:relative}

.pc-xpbar-fill{height:100%;background:linear-gradient(90deg,var(--bg),var(--bgb),var(--tgb));transition:width .6s cubic-bezier(.3,.8,.3,1);box-shadow:0 0 6px var(--prof-gold-glow);position:relative}

.pc-xpbar-fill::after{content:'';position:absolute;inset:0;background:linear-gradient(90deg,transparent,rgba(255,255,255,.25),transparent);animation:barShine 3s linear infinite}

@keyframes barShine{0%{transform:translateX(-100%)}100%{transform:translateX(200%)}}

.pc-stats{display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-top:12px;padding-top:12px;border-top:1px solid var(--bs)}

.pc-stat{font-size:11px;color:var(--ts)}

.pc-stat b{color:var(--tp);font-weight:700;font-family:'Cinzel',serif}



/* Unlock track on prof card */

.unlock-track{margin-top:12px;padding-top:12px;border-top:1px solid var(--bs)}

.unlock-track-lbl{font-family:'Cinzel',serif;font-size:10px;color:var(--to);letter-spacing:1.5px;margin-bottom:6px}

.unlock-row{display:flex;gap:4px;justify-content:space-between;position:relative}

.unlock-row::before{content:'';position:absolute;top:13px;left:10%;right:10%;height:2px;background:var(--bs);z-index:0}

.unlock-dot{width:28px;height:28px;border-radius:50%;background:var(--bg1);border:2px solid var(--bs);display:flex;align-items:center;justify-content:center;font-size:12px;position:relative;z-index:1;transition:all .3s;flex-shrink:0;cursor:help;color:var(--ts);font-family:'Cinzel',serif;font-weight:700}

.unlock-dot.reached{background:linear-gradient(135deg,var(--bg),var(--bgb));border-color:var(--prof-gold);color:#0e0a0f;box-shadow:0 0 8px var(--prof-gold-glow)}

.unlock-dot.next{border-color:var(--prof-gold);animation:profPulse 1.5s ease infinite}

.unlock-dot .ud-lvl{position:absolute;bottom:-16px;font-size:9px;color:var(--ts);font-family:'Cinzel',serif;white-space:nowrap}

.unlock-dot.reached .ud-lvl{color:var(--prof-gold)}



/* Empty profession slot */

.empty-slot{background:var(--bgi);border:1px dashed var(--bs);border-radius:var(--rm);padding:20px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;cursor:pointer;transition:all .2s;min-height:140px;text-align:center;font-family:'Alegreya Sans',sans-serif}

.empty-slot:hover{border-color:var(--prof-gold);background:rgba(212,168,50,.03)}

.empty-slot .es-ico{font-size:28px;color:var(--tm);margin-bottom:4px}

.empty-slot:hover .es-ico{color:var(--prof-gold)}

.empty-slot .es-title{font-family:'Cinzel',serif;font-size:13px;color:var(--tp);letter-spacing:1px}

.empty-slot .es-sub{font-size:11px;color:var(--ts)}



/* Profession picker */

.picker-overlay{position:fixed;inset:0;z-index:1400;background:rgba(0,0,0,.8);display:flex;align-items:center;justify-content:center;padding:20px;animation:profFade .2s ease;font-family:'Alegreya Sans',sans-serif}

.picker-box{width:100%;max-width:620px;background:var(--bg1);border:1px solid var(--bs);border-radius:var(--rl);overflow:hidden;animation:profSlideDown .3s cubic-bezier(.2,.8,.4,1)}

.pb-hdr{padding:16px 20px;border-bottom:1px solid var(--bs);display:flex;align-items:center;justify-content:space-between}

.pb-title{font-family:'Cinzel',serif;font-size:18px;color:var(--prof-gold)}

.pb-body{padding:16px 20px;max-height:60vh;overflow-y:auto}

.pb-hint{font-size:12px;color:var(--ts);margin-bottom:10px;font-style:italic}

.pick-list{display:grid;gap:8px}

.pick-item{display:flex;align-items:center;gap:12px;padding:12px 14px;background:var(--bg2);border:1px solid var(--bs);border-radius:var(--rm);cursor:pointer;transition:all .2s}

.pick-item:hover{border-color:var(--prof-gold);background:var(--bg2h)}

.pick-item.disabled{opacity:.4;cursor:not-allowed}

.pick-item.disabled:hover{border-color:var(--bs);background:var(--bg2)}

.pick-emb{width:40px;height:40px;border-radius:var(--rm);background:var(--bg1);border:1px solid var(--bs);display:flex;align-items:center;justify-content:center;font-size:22px;flex-shrink:0}

.pick-info{flex:1}

.pick-name{font-family:'Cinzel',serif;font-size:14px;color:var(--tp);font-weight:700}

.pick-desc{font-size:11px;color:var(--ts);margin-top:2px}

.pick-type{font-family:'Cinzel',serif;font-size:10px;letter-spacing:1.5px;padding:3px 9px;border-radius:10px;border:1px solid;flex-shrink:0}

.pick-type.major{color:var(--prof-gold);border-color:var(--prof-gold);background:rgba(212,168,50,.06)}

.pick-type.minor{color:var(--prof-silver);border-color:rgba(168,168,184,.4);background:rgba(168,168,184,.05)}



/* ========= TALENTS BUTTON ON PROF CARD ========= */

.prof-talents-btn{display:inline-flex;align-items:center;gap:6px;padding:6px 12px;background:rgba(232,196,120,.06);border:1px solid rgba(232,196,120,.3);color:#f0c260;border-radius:var(--rm);font-family:'Cinzel',serif;font-size:11px;font-weight:700;letter-spacing:.7px;cursor:pointer;transition:all .2s;margin-top:10px;width:100%;justify-content:center}

.prof-talents-btn:hover{background:rgba(232,196,120,.15);border-color:#f0c260;box-shadow:0 0 10px rgba(232,196,120,.25)}

.prof-talents-btn.has-points{animation:talentsBtnPulse 2s ease-in-out infinite;border-color:#f0c260;background:rgba(240,194,96,.1)}

@keyframes talentsBtnPulse{0%,100%{box-shadow:0 0 0 rgba(240,194,96,0)}50%{box-shadow:0 0 14px rgba(240,194,96,.5)}}

.prof-talents-btn .pts-badge{display:inline-flex;align-items:center;justify-content:center;min-width:18px;height:18px;padding:0 5px;background:#f0c260;color:#15101e;border-radius:9px;font-size:10px;font-weight:700}



/* ========= TALENT TREE (Last Epoch-style node graph) ========= */

.tt-overlay{position:fixed;inset:0;z-index:1300;background:rgba(0,0,0,.85);backdrop-filter:blur(4px);display:flex;align-items:flex-start;justify-content:center;padding:20px;overflow-y:auto;animation:profFade .2s ease;font-family:'Alegreya Sans',sans-serif}

.tt-panel{width:100%;max-width:1080px;background:radial-gradient(ellipse at 50% 0%, rgba(232,196,120,.08) 0%, transparent 40%),radial-gradient(ellipse at 50% 100%, rgba(179,136,232,.06) 0%, transparent 40%),linear-gradient(180deg, #14101e 0%, #0a0810 100%);border:1px solid rgba(232,196,120,.3);border-radius:16px;overflow:hidden;position:relative;animation:profSlideDown .35s cubic-bezier(.2,.8,.4,1);box-shadow:0 20px 60px rgba(0,0,0,.7), 0 0 60px rgba(232,196,120,.06)}

.tt-hdr{position:relative;padding:18px 26px;background:linear-gradient(135deg,rgba(232,196,120,.08),rgba(179,136,232,.05));border-bottom:1px solid rgba(232,196,120,.25);display:flex;align-items:center;justify-content:space-between;gap:20px;z-index:5}

.tt-hdr-l{display:flex;align-items:center;gap:14px;flex:1;min-width:0}

.tt-hdr-ico{width:54px;height:54px;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,rgba(232,196,120,.15),rgba(179,136,232,.08));border:1px solid rgba(232,196,120,.4);border-radius:14px;font-size:30px;flex-shrink:0;filter:drop-shadow(0 0 8px rgba(232,196,120,.3))}

.tt-hdr-info{min-width:0;flex:1}

.tt-title{font-family:'Cinzel',serif;font-size:20px;color:#e8dcc0;letter-spacing:.7px;line-height:1.1;margin-bottom:3px}

.tt-sub{font-size:12px;color:var(--ts);font-style:italic;opacity:.85}

.tt-points{display:flex;align-items:center;gap:10px;padding:8px 18px;background:rgba(15,10,21,.7);border:1px solid rgba(232,196,120,.4);border-radius:18px;position:relative;flex-shrink:0}

.tt-points.has-points{border-color:#f0c260;box-shadow:0 0 16px rgba(240,194,96,.35);animation:pointsGlow 2.4s ease-in-out infinite}

@keyframes pointsGlow{0%,100%{box-shadow:0 0 16px rgba(240,194,96,.25)}50%{box-shadow:0 0 24px rgba(240,194,96,.6)}}

.tt-points-num{font-family:'Cinzel',serif;font-size:24px;font-weight:700;color:#f0c260;line-height:1;text-shadow:0 0 10px rgba(240,194,96,.5)}

.tt-points.no-points .tt-points-num{color:var(--ts);text-shadow:none}

.tt-points-lbl{font-family:'Cinzel',serif;font-size:10px;letter-spacing:1.5px;color:var(--ts);text-transform:uppercase;line-height:1.2}

.tt-close{position:absolute;top:14px;right:14px;width:30px;height:30px;border-radius:50%;border:1px solid var(--bs);background:var(--bg2);color:var(--ts);cursor:pointer;font-size:14px;display:flex;align-items:center;justify-content:center;z-index:10}

.tt-close:hover{color:var(--tp);background:var(--bg2h)}

.tt-canvas-scroll{position:relative;max-height:calc(100vh - 240px);overflow:auto;background:repeating-linear-gradient(60deg, rgba(232,196,120,.018) 0 1px, transparent 1px 60px),repeating-linear-gradient(-60deg, rgba(232,196,120,.018) 0 1px, transparent 1px 60px),radial-gradient(ellipse at 50% 50%, rgba(107,63,160,.08) 0%, transparent 60%),#0a0810}

.tt-canvas{position:relative;margin:0 auto}

.tt-canvas::before{content:'';position:absolute;inset:0;pointer-events:none;background-image:radial-gradient(circle at 8% 12%, rgba(232,196,120,.4) 0.7px, transparent 1.4px),radial-gradient(circle at 88% 8%, rgba(179,136,232,.5) 0.6px, transparent 1.3px),radial-gradient(circle at 22% 38%, rgba(232,196,120,.3) 0.5px, transparent 1.2px),radial-gradient(circle at 78% 32%, rgba(232,196,120,.4) 0.6px, transparent 1.3px),radial-gradient(circle at 14% 62%, rgba(179,136,232,.35) 0.5px, transparent 1.2px),radial-gradient(circle at 92% 68%, rgba(232,196,120,.35) 0.6px, transparent 1.3px),radial-gradient(circle at 32% 88%, rgba(179,136,232,.35) 0.5px, transparent 1.2px),radial-gradient(circle at 68% 92%, rgba(232,196,120,.35) 0.6px, transparent 1.3px);background-size:480px 480px;opacity:.6}

.tt-svg{position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:1}

.tt-line{stroke-width:3;fill:none;stroke-linecap:round}

.tt-line.locked{stroke:rgba(120,120,140,.18);stroke-dasharray:4 6}

.tt-line.reachable{stroke:rgba(232,196,120,.25)}

.tt-line.active{stroke:#f0c260;filter:drop-shadow(0 0 4px rgba(240,194,96,.6));animation:lineFlow 3s linear infinite;stroke-dasharray:1}

@keyframes lineFlow{0%{stroke-dashoffset:0}100%{stroke-dashoffset:-30}}

.tt-node{position:absolute;transform:translate(-50%,-50%);z-index:2;cursor:pointer;display:flex;flex-direction:column;align-items:center;transition:transform .15s}

.tt-node:hover:not(.tt-node-locked):not(.tt-node-blocked){transform:translate(-50%,-50%) scale(1.08)}

.tt-node-frame{position:relative;display:flex;align-items:center;justify-content:center;background:linear-gradient(180deg,#1c1527,#0a0810);border:2px solid rgba(120,120,140,.35);font-size:24px;transition:all .25s;box-shadow:0 0 0 4px rgba(0,0,0,.6), inset 0 0 8px rgba(0,0,0,.6)}

.tt-node[data-kind="standard"] .tt-node-frame{width:54px;height:60px;clip-path:polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)}

.tt-node[data-kind="notable"] .tt-node-frame{width:74px;height:74px;clip-path:polygon(30% 0%,70% 0%,100% 30%,100% 70%,70% 100%,30% 100%,0% 70%,0% 30%);font-size:30px}

.tt-node[data-kind="keystone"] .tt-node-frame{width:96px;height:96px;border-radius:50%;font-size:38px;border-width:3px}

.tt-node[data-kind="keystone"] .tt-node-frame::before{content:'';position:absolute;inset:-8px;border:1px solid rgba(232,196,120,.25);border-radius:50%;pointer-events:none}

.tt-node-locked .tt-node-frame{opacity:.5;filter:grayscale(.85) brightness(.55);border-color:rgba(120,120,140,.3)}

.tt-node-locked{cursor:not-allowed}

.tt-node-blocked .tt-node-frame{opacity:.55;filter:grayscale(.6) brightness(.65);border-color:rgba(120,120,140,.4)}

.tt-node-blocked{cursor:not-allowed}

.tt-node-reachable .tt-node-frame{border-color:rgba(232,196,120,.55);background:linear-gradient(180deg,#1c1527,#15101e)}

.tt-node-available .tt-node-frame{border-color:#f0c260;background:linear-gradient(180deg,rgba(240,194,96,.14),#15101e);animation:availableNode 1.8s ease-in-out infinite}

@keyframes availableNode{0%,100%{box-shadow:0 0 0 4px rgba(0,0,0,.6),0 0 6px rgba(240,194,96,.35),inset 0 0 10px rgba(240,194,96,.08);filter:drop-shadow(0 -3px 0 #c9942e) drop-shadow(0 3px 0 #c9942e) drop-shadow(-3px 0 0 #c9942e) drop-shadow(3px 0 0 #c9942e) drop-shadow(0 0 4px rgba(240,194,96,.4))}50%{box-shadow:0 0 0 4px rgba(0,0,0,.6),0 0 22px rgba(240,194,96,.7),inset 0 0 14px rgba(240,194,96,.18);filter:drop-shadow(0 -3px 0 #f7d680) drop-shadow(0 3px 0 #f7d680) drop-shadow(-3px 0 0 #f7d680) drop-shadow(3px 0 0 #f7d680) drop-shadow(0 0 14px rgba(240,194,96,.85))}}

.tt-node-ranked-clickable .tt-node-frame{border-color:#f0c260;background:linear-gradient(180deg,rgba(107,63,160,.3),#15101e);animation:rankedClickableNode 1.8s ease-in-out infinite}

@keyframes rankedClickableNode{0%,100%{box-shadow:0 0 0 4px rgba(0,0,0,.6),0 0 10px rgba(179,136,232,.4),inset 0 0 10px rgba(179,136,232,.12);filter:drop-shadow(0 -3px 0 #c9942e) drop-shadow(0 3px 0 #c9942e) drop-shadow(-3px 0 0 #c9942e) drop-shadow(3px 0 0 #c9942e) drop-shadow(0 0 4px rgba(240,194,96,.4))}50%{box-shadow:0 0 0 4px rgba(0,0,0,.6),0 0 18px rgba(179,136,232,.5),0 0 24px rgba(240,194,96,.4),inset 0 0 14px rgba(179,136,232,.18);filter:drop-shadow(0 -3px 0 #f7d680) drop-shadow(0 3px 0 #f7d680) drop-shadow(-3px 0 0 #f7d680) drop-shadow(3px 0 0 #f7d680) drop-shadow(0 0 14px rgba(240,194,96,.85))}}

.tt-node-available:hover .tt-node-frame,.tt-node-ranked-clickable:hover .tt-node-frame{animation-play-state:paused}

.tt-node-ranked .tt-node-frame{border-color:rgba(179,136,232,.7);background:linear-gradient(180deg,rgba(107,63,160,.3),#15101e);box-shadow:0 0 0 4px rgba(0,0,0,.6), 0 0 14px rgba(179,136,232,.4), inset 0 0 10px rgba(179,136,232,.12)}

.tt-node-maxed .tt-node-frame{border-color:#f0c260;background:linear-gradient(180deg,rgba(240,194,96,.22),#1a1308);box-shadow:0 0 0 4px rgba(0,0,0,.6), 0 0 24px rgba(240,194,96,.55), inset 0 0 14px rgba(240,194,96,.18)}

.tt-node-maxed[data-kind="keystone"] .tt-node-frame::before{border-color:#f0c260}

.tt-node-lock-overlay{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);font-size:14px;color:var(--tm);text-shadow:0 0 6px rgba(0,0,0,.9);pointer-events:none}

.tt-node[data-kind="notable"] .tt-node-lock-overlay{font-size:16px}

.tt-node[data-kind="keystone"] .tt-node-lock-overlay{font-size:20px}

.tt-node-pip{margin-top:6px;display:inline-flex;align-items:center;padding:2px 8px;background:rgba(15,10,21,.85);border:1px solid rgba(232,196,120,.3);border-radius:10px;font-family:'Cinzel',serif;font-size:10px;font-weight:700;color:var(--ts);letter-spacing:.5px;white-space:nowrap}

.tt-node-available .tt-node-pip{color:#f0c260;border-color:rgba(240,194,96,.5)}

.tt-node-ranked .tt-node-pip{color:var(--tpu);border-color:rgba(179,136,232,.5)}

.tt-node-ranked-clickable .tt-node-pip{color:#f0c260;border-color:rgba(240,194,96,.6);background:rgba(240,194,96,.08)}

.tt-node-maxed .tt-node-pip{color:#f0c260;border-color:#f0c260;background:rgba(240,194,96,.1)}

.tt-node-locked .tt-node-pip,.tt-node-blocked .tt-node-pip{opacity:.5}

.tt-tooltip-shared{position:fixed;z-index:1500;pointer-events:none;background:linear-gradient(180deg,#1f1832,#15101e);border:1px solid rgba(232,196,120,.5);border-radius:10px;padding:12px 14px;width:260px;box-shadow:0 10px 30px rgba(0,0,0,.7), 0 0 14px rgba(232,196,120,.15);font-family:'Alegreya',serif;opacity:0;display:none;transition:opacity .12s}

.tt-tooltip-shared.show{opacity:1;display:block}

.tt-tooltip-name{font-family:'Cinzel',serif;font-size:13px;font-weight:700;color:#e8dcc0;letter-spacing:.4px;margin-bottom:4px}

.tt-tooltip-tier{font-family:'Cinzel',serif;font-size:9px;letter-spacing:1.5px;color:#f0c260;text-transform:uppercase;margin-bottom:6px}

.tt-tooltip-desc{font-size:12px;color:#c9b088;line-height:1.4;margin-bottom:6px}

.tt-tooltip-effect{font-size:11px;color:#a89681;font-style:italic;border-top:1px solid rgba(232,196,120,.15);padding-top:6px;margin-top:6px}

.tt-tooltip-effect b{color:#f0c260;font-style:normal;font-weight:700}

.tt-tooltip-block{font-size:11px;color:#e67373;margin-top:6px;padding-top:6px;border-top:1px solid rgba(230,115,115,.2);font-style:italic}

.tt-footer{padding:12px 26px;border-top:1px solid rgba(232,196,120,.2);display:flex;align-items:center;justify-content:space-between;gap:16px;background:rgba(15,10,21,.6);position:relative;flex-wrap:wrap;z-index:5}

.tt-legend{display:flex;gap:14px;align-items:center;flex-wrap:wrap}

.tt-legend-item{display:inline-flex;align-items:center;gap:5px;font-size:10px;color:var(--ts);font-family:'Cinzel',serif;letter-spacing:.5px}

.tt-legend-dot{width:9px;height:9px;border-radius:50%;border:1.5px solid}

.tt-legend-dot.available{border-color:#f0c260;background:rgba(240,194,96,.2)}

.tt-legend-dot.ranked{border-color:var(--tpu);background:rgba(179,136,232,.2)}

.tt-legend-dot.maxed{border-color:#f0c260;background:#f0c260}

.tt-legend-dot.locked{border-color:var(--bs);background:var(--bg1)}

.tt-reset-btn{padding:6px 14px;background:rgba(212,64,64,.08);border:1px solid rgba(212,64,64,.3);color:#e67373;border-radius:var(--rs);font-family:'Cinzel',serif;font-size:11px;letter-spacing:.5px;cursor:pointer;transition:all .2s}

.tt-reset-btn:hover{background:rgba(212,64,64,.15);border-color:#e67373}



/* ========= QUALITY BADGE inside cres modal ========= */

.cres-quality-badge{display:inline-flex;align-items:center;gap:4px;padding:3px 10px;border-radius:10px;font-family:'Cinzel',serif;font-size:11px;font-weight:700;letter-spacing:1px;margin-bottom:8px}

.cres-quality-badge.common{background:var(--bg1);color:var(--quality-common);border:1px solid var(--bs)}

.cres-quality-badge.fine{background:rgba(92,184,92,.1);color:var(--quality-fine);border:1px solid var(--quality-fine)}

.cres-quality-badge.superior{background:rgba(74,154,212,.1);color:var(--quality-superior);border:1px solid var(--quality-superior)}

.cres-quality-badge.master{background:rgba(179,136,232,.1);color:var(--quality-master);border:1px solid var(--quality-master)}

.cres-quality-badge.legendary{background:rgba(240,194,64,.12);color:var(--quality-legendary);border:1px solid var(--quality-legendary);animation:legendPulse 1.8s ease-in-out infinite}

@keyframes legendPulse{0%,100%{box-shadow:0 0 6px rgba(240,194,64,.2)}50%{box-shadow:0 0 18px rgba(240,194,64,.5)}}



/* ========= RECIPE SHOP (Phase 4A) ========= */

.shop-lay{display:grid;grid-template-columns:420px 1fr;gap:20px;min-height:580px;font-family:'Alegreya Sans',sans-serif}

@media(max-width:900px){.shop-lay{grid-template-columns:1fr}}

.shop-hdr{display:flex;align-items:center;gap:14px;padding:14px 18px;background:linear-gradient(135deg,var(--bg2),var(--bg1));border:1px solid var(--bs);border-top:2px solid var(--prof-gold);border-radius:var(--rm) var(--rm) 0 0;position:relative;overflow:hidden}

.shop-hdr::after{content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,var(--prof-gold),transparent);opacity:.5}

.shop-npc-ico{width:56px;height:56px;border-radius:50%;background:linear-gradient(135deg,var(--bgi),var(--bg2));border:2px solid var(--prof-gold);display:flex;align-items:center;justify-content:center;font-size:30px;flex-shrink:0;box-shadow:inset 0 0 12px rgba(212,168,50,.15),var(--gu)}

.shop-npc-info{flex:1;min-width:0}

.shop-npc-title{font-family:'Cinzel',serif;font-size:18px;color:var(--tgb);font-weight:700;letter-spacing:.5px}

.shop-npc-sub{font-size:12px;color:var(--ts);font-style:italic;margin-top:2px}

.shop-refresh-ind{font-size:11px;color:var(--tm);font-family:'Cinzel',serif;letter-spacing:1px}

.shop-wallet{display:flex;gap:12px;align-items:center;padding:10px 18px;background:var(--bgi);border:1px solid var(--bs);border-top:none;border-radius:0 0 var(--rm) var(--rm);margin-bottom:14px;flex-wrap:wrap}

.shop-wallet-item{display:flex;align-items:center;gap:6px;padding:4px 12px;background:var(--bg2);border:1px solid var(--bs);border-radius:14px;font-family:'Cinzel',serif;font-size:13px}

.shop-wallet-item.gp{color:var(--tgb);border-color:rgba(201,149,26,.35)}

.shop-wallet-item.mp{color:var(--tpu);border-color:rgba(179,136,232,.35)}

.shop-wallet-ico{font-size:14px}

.shop-wallet-amount{font-weight:700;font-size:14px}

.shop-list{background:var(--bg1);border:1px solid var(--bs);border-radius:var(--rm);padding:10px;max-height:620px;overflow-y:auto;scrollbar-width:thin}

.shop-list-empty{padding:40px 20px;text-align:center;color:var(--tm);font-size:13px}

.shop-list-empty .emb{font-size:36px;margin-bottom:10px;display:block;opacity:.4}

.shop-row{display:flex;align-items:center;gap:12px;padding:10px 12px;border-radius:var(--rm);cursor:pointer;transition:all .2s;border:1px solid transparent;margin-bottom:4px;background:var(--bgi);position:relative}

.shop-row:hover{background:var(--bg2)}

.shop-row.selected{background:linear-gradient(135deg,rgba(201,149,26,.15),rgba(201,149,26,.04));border-color:var(--prof-gold);box-shadow:inset 0 0 10px rgba(212,168,50,.15),0 0 6px rgba(212,168,50,.2)}

.shop-row.locked{opacity:.55;cursor:default}

.shop-row.locked:hover{background:var(--bgi)}

.shop-row.owned{opacity:.8}

.shop-row-ico{width:40px;height:40px;border-radius:var(--rs);background:var(--bg2);border:1px solid var(--bs);display:flex;align-items:center;justify-content:center;font-size:22px;flex-shrink:0}

.shop-row.selected .shop-row-ico{border-color:var(--prof-gold)}

.shop-row-body{flex:1;min-width:0}

.shop-row-name{font-family:'Cinzel',serif;font-size:14px;color:var(--tp);font-weight:700;line-height:1.1}

.shop-row.locked .shop-row-name{color:var(--ts)}

.shop-row.selected .shop-row-name{color:var(--tgb)}

.shop-row-req{font-size:11px;color:var(--ts);margin-top:3px;font-family:'Cinzel',serif;letter-spacing:.3px}

.shop-row-req .req-hi{color:var(--tgb)}

.shop-row-req .req-lo{color:var(--tr)}

.shop-row-price{display:flex;align-items:center;gap:4px;font-family:'Cinzel',serif;font-size:13px;color:var(--tgb);font-weight:700;flex-shrink:0}

.shop-row-price.cant{color:var(--tr)}

.shop-row-price.owned{color:var(--tgr)}

.shop-row-locked-ico{color:var(--tm);font-size:16px;margin-left:6px}

.shop-detail{background:var(--bg1);border:1px solid var(--bs);border-radius:var(--rm);padding:22px 24px;min-height:580px}

.shop-detail-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;color:var(--tm);text-align:center;padding:40px 20px}

.shop-detail-empty .big{font-size:54px;margin-bottom:16px;opacity:.3}

.shop-detail-empty .t{font-family:'Cinzel',serif;font-size:16px;color:var(--ts)}

.sd-hdr{display:flex;align-items:flex-start;gap:16px;margin-bottom:16px;padding-bottom:16px;border-bottom:1px solid var(--bs)}

.sd-ico{width:72px;height:72px;border-radius:var(--rm);background:linear-gradient(135deg,var(--bg2),var(--bgi));border:2px solid var(--prof-gold);display:flex;align-items:center;justify-content:center;font-size:42px;flex-shrink:0;box-shadow:inset 0 0 14px rgba(212,168,50,.2)}

.sd-info{flex:1;min-width:0}

.sd-name{font-family:'Cinzel',serif;font-size:22px;color:var(--tgb);font-weight:700;letter-spacing:.5px}

.sd-sub{font-size:13px;color:var(--ts);font-style:italic;margin-top:4px;line-height:1.4}

.sd-tags{display:flex;gap:6px;flex-wrap:wrap;margin-top:8px}

.sd-tag{font-size:10px;padding:2px 8px;border-radius:10px;background:var(--bg2);color:var(--ts);border:1px solid var(--bs);font-family:'Cinzel',serif;letter-spacing:1px}

.sd-section{margin-bottom:16px}

.sd-section-hdr{font-family:'Cinzel',serif;font-size:12px;color:var(--to);letter-spacing:1.5px;margin-bottom:8px}

.sd-req-row{display:flex;align-items:center;gap:10px;padding:8px 12px;background:var(--bgi);border:1px solid var(--bs);border-radius:var(--rm);margin-bottom:6px}

.sd-req-row.met{border-color:var(--ag);background:rgba(93,184,93,.05)}

.sd-req-row.unmet{border-color:var(--ar);background:rgba(212,64,64,.05)}

.sd-req-ico{font-size:16px}

.sd-req-text{flex:1;font-size:13px;color:var(--tp)}

.sd-req-status{font-family:'Cinzel',serif;font-size:11px;letter-spacing:1px}

.sd-req-status.met{color:var(--tgr)}

.sd-req-status.unmet{color:var(--tr)}

.sd-comp{display:flex;align-items:center;gap:8px;padding:6px 10px;background:var(--bgi);border:1px solid var(--bs);border-radius:var(--rm);margin-bottom:4px;font-size:12px}

.sd-comp.owned{border-color:var(--ag);background:rgba(93,184,93,.04)}

.sd-comp-ico{font-size:16px;opacity:.9}

.sd-comp-name{flex:1}

.sd-comp-count{font-family:'Cinzel',serif;font-size:12px;font-weight:700}

.sd-comp-count.have{color:var(--tgr)}

.sd-comp-count.need{color:var(--tr)}

.sd-buy-row{display:flex;gap:10px;align-items:center;justify-content:flex-end;padding-top:16px;border-top:1px solid var(--bs);flex-wrap:wrap}

.sd-price-chip{display:inline-flex;align-items:center;gap:4px;padding:6px 14px;border-radius:var(--rm);background:var(--bgi);border:1px solid var(--bs);font-family:'Cinzel',serif;font-weight:700}

.sd-price-chip.gp{color:var(--tgb);border-color:rgba(201,149,26,.3)}

.sd-price-chip.discount{color:var(--tgr);border-color:var(--ag);font-size:11px}

.sd-buy-btn{padding:10px 26px;border-radius:var(--rm);border:none;background:linear-gradient(135deg,var(--ag),#3d9a3d);color:#fff;font-family:'Cinzel',serif;font-size:14px;font-weight:700;letter-spacing:1px;cursor:pointer;transition:all .25s}

.sd-buy-btn:hover{background:linear-gradient(135deg,#3d9a3d,#55b855);transform:translateY(-1px);box-shadow:0 0 14px rgba(93,184,93,.4)}

.sd-buy-btn.dis{background:var(--bgi);color:var(--tm);cursor:not-allowed;border:1px solid var(--bs)}

.sd-buy-btn.dis:hover{transform:none;box-shadow:none}

.pconfirm-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.7);z-index:210;display:flex;align-items:center;justify-content:center;backdrop-filter:blur(3px);animation:profFade .25s ease;font-family:'Alegreya Sans',sans-serif}

.pconfirm{background:linear-gradient(145deg,var(--bg1),var(--bg2));border:1px solid var(--prof-gold);border-radius:var(--rl);padding:24px;max-width:420px;width:90%;text-align:center;box-shadow:0 0 30px rgba(212,168,50,.2),0 8px 32px rgba(0,0,0,.6)}

.pconfirm-title{font-family:'Cinzel',serif;color:var(--tgb);font-size:18px;margin-bottom:12px}

.pconfirm-recipe{padding:14px;background:var(--bgi);border-radius:var(--rm);margin-bottom:14px}

.pconfirm-recipe .pc-ico{font-size:38px;margin-bottom:6px;display:block}

.pconfirm-recipe .pc-name{font-family:'Cinzel',serif;font-size:15px;color:var(--tp);font-weight:700}

.pconfirm-body{font-size:13px;color:var(--ts);margin-bottom:16px;line-height:1.5}

.pconfirm-btns{display:flex;gap:10px;justify-content:center}

.pconfirm-btns button{padding:9px 22px;border-radius:var(--rm);border:1px solid var(--bs);font-family:'Cinzel',serif;font-weight:700;cursor:pointer;font-size:13px;letter-spacing:1px}

.pconfirm-cancel{background:var(--bg2);color:var(--ts)}

.pconfirm-cancel:hover{color:var(--tp);border-color:var(--tm)}

.pconfirm-confirm{background:linear-gradient(135deg,var(--ag),#3d9a3d);color:#fff;border-color:var(--ag)}

.pconfirm-confirm:hover{background:linear-gradient(135deg,#3d9a3d,#55b855)}

`;

};



// ─── MOBILE DETECTION ───────────────────────────────────

function useIsMobile(breakpoint = 768) {

  const [mobile, setMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= breakpoint);

  useEffect(() => {

    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);

    const handler = (e) => setMobile(e.matches);

    mq.addEventListener("change", handler);

    return () => mq.removeEventListener("change", handler);

  }, [breakpoint]);

  return mobile;

}

// ─── ICON DATA ───────────────────────────────────────────

const ICONS_BASE = {

  Weapons: ["🗡️","⚔️","🏹","🪓","🔪","🛡️","💣","🔱","🪃","🪄"],

  Nature: ["🌿","🌾","🪵","🌳","🍂","🌱","🪨","🌸","🍄","🌻"],

  Magic: ["💎","🔮","✨","⭐","🌟","💫","🔥","❄️","⚡","💧","🌊","🌙"],

  Creatures: ["🐺","🐻","🦎","🕷️","🐍","🦂","🐲","🦅","🐗","🦇"],

  Potions: ["⚗️","🧪","💚","💜","❤️","💙","🧫","🩸","🫧"],

  Food: ["🥩","🍖","🍞","🧀","🍗","🥘","🍲","🥗","🍎","🥕"],

  Misc: ["📦","🔩","🧵","🪡","🕸️","🪶","🦴","💀","🗝️","📜","🪙"],

  Armor: ["🧥","👕","🥾","🧤","👑","💍","📿","🎒","🪖","🛡️","⛑️"],

  "Game Icons": ["gi:biohazard", "gi:plague-doctor", "gi:treasure-chest"],

};

const ICONS_EXT = {

  Weapons:["⛏️","🪚","🗡️","🔫","💥","🧨","🎯","🏹","🪤","⚒️","🔧","🪛","🪝","⚓","🧲"],

  Nature:["🌺","🥀","🌵","🎋","🎍","🪻","🌴","🍀","🌲","🎄","🌰","🥥","🎑","🏔️","🌋","🏜️","🌅","🍁"],

  Magic:["🪬","🧿","📿","🔔","🕯️","🎆","🎇","🎐","🎴","🀄","💫","🌈","☄️","🔮","🧙","🪄","♾️","☯️","➕","✳️","❇️","🔆"],

  Creatures:["🦁","🐯","🦊","🐸","🦎","🦈","🐙","🦑","🐊","🦃","🦉","🦋","🐝","🐞","🕊️","🦌","🐐","🐘","🦏","🐢","🐠","🦞","🐚","🪸","🦟","🪳"],

  Potions:["🍵","🥤","🧋","☕","🍷","🍶","🥃","🧃","🫗","💊","💉","🩹","🩺","➕","✳️"],

  Food:["🧅","🌽","🫑","🥔","🥚","🧈","🍯","🌶️","🫐","🍇","🍋","🥭","🍌","🫒","🧄","🥝","🍓","🍑","🥑","🍕","🌮","🥧","🧁","🎂","🍩","🍪","🥖","🫓"],

  Misc:["🪤","🧰","🪣","🧲","🪞","🧱","🪜","🛒","🧺","💰","👛","🎪","🏺","🏮","🎏","🪘","🥁","🎺","📯","🔑","🗂️","📎","✏️","🖊️","📐","🏷️","🎁","🪦","⚰️","🚩","🏴","🗻","⛩️","🛕"],

  Armor:["🧣","🧢","👒","🎩","👞","👟","🥿","👠","👢","🥻","👔","🩴","🪬","🦺","🎽","👗","👘"],

};

const RARS = ["Common","Uncommon","Rare","Very Rare","Legendary","Artifact"];

const DEFAULT_CATS = [

  { id: "all", l: "⚡ All" },

  { id: "weapons", l: "⚔️ Weapons" },

  { id: "armor", l: "🛡️ Armor" },

  { id: "potions", l: "🧪 Potions" },

  { id: "tools", l: "🔧 Tools" },

  { id: "materials", l: "📦 Materials" },

  { id: "food", l: "🍖 Food" },

];



// ─── DEFAULT DATA ────────────────────────────────────────

const DEFAULT_ITEMS = [

  { id: "monster_core", name: "Monster Core", icon: "🟢", cat: "materials" },

  { id: "mandrake_root", name: "Mandrake Root", icon: "🌿", cat: "materials" },

  { id: "iron_ingot", name: "Iron Ingot", icon: "🔩", cat: "materials" },

  { id: "branch", name: "Branch", icon: "🪵", cat: "materials" },

  { id: "log", name: "Log", icon: "🪵", cat: "materials" },

  { id: "raw_meat", name: "Raw Meat", icon: "🥩", cat: "food" },

  { id: "herb_bundle", name: "Herb Bundle", icon: "🌾", cat: "materials" },

  { id: "spider_silk", name: "Spider Silk", icon: "🕸️", cat: "materials" },

  { id: "fire_essence", name: "Fire Essence", icon: "🔥", cat: "materials" },

  { id: "wolf_pelt", name: "Wolf Pelt", icon: "🐺", cat: "materials" },

  { id: "crystal_shard", name: "Crystal Shard", icon: "🔮", cat: "materials" },

  { id: "empty_vial", name: "Empty Vial", icon: "⚗️", cat: "tools" },

  { id: "leather_strip", name: "Leather Strip", icon: "🟤", cat: "materials" },

];



// 2026-04-27: Profession System — MVP (Phase 1 of 5).

// Source design: src/professions.html (full prototype with talents,

// quality, shop). This MVP ships gating + XP only; talents / quality /

// shop / DM editor land in subsequent sessions. See CLAUDE-NOTES-GENERAL

// for the phased plan.

//

// Each profession definition is a static template here; making them

// DM-editable is a later phase that needs a Convex `professions` table

// (singleton blob, like systems). For MVP these five cover the canonical

// crafting verbs.

//

// type: 'major' | 'minor'

//   - major: gated by profSettings.maxMajor (default 1) — character can

//     only have N of these active at once.

//   - minor: unlimited; cooking, fishing, etc.

// baseXp: XP required to reach level 2 from level 1. Subsequent levels

//   scale via xpForLevel below.

// milestones: flavor text + small mechanical hints surfaced at given

//   levels. MVP doesn't apply them mechanically — they're shown on the

//   profession card so the player has something to look forward to.

// 2026-04-27 (Phase 2): talent tier → minimum profession level required.

// Reflects the prototype's gating: only ranked-up talents from prior tiers

// satisfy `requires`, so tier-3 nodes need both Lv 5 and a tier-2 prereq

// with at least 1 rank.

const TALENT_TIER_LEVELS = { 1: 1, 2: 3, 3: 5, 4: 7, 5: 10 };



const DEFAULT_PROFESSIONS = [

  {

    id: 'blacksmith', name: 'Blacksmithing', icon: '⚒️',

    desc: 'Forge weapons and heavy armor from iron and steel',

    type: 'major', baseXp: 100, maxLevel: 10,

    milestones: [

      { level: 2, desc: 'Reliable Edge' },

      { level: 3, desc: 'Journeyman Smith — tier-2 weapon recipes' },

      { level: 5, desc: 'Master Smith — heavy armor expertise' },

      { level: 7, desc: 'Reforge — chance to salvage on failure' },

      { level: 10, desc: 'Grandmaster — +1 bonus stat on every craft' },

    ],

    // 2026-04-27 (Phase 2): talents ported from src/professions.html.

    // effect.kind drives runtime behavior:

    //   xpBonus      — % bonus to craft XP (live in MVP)

    //   componentSave — % chance to refund a component on craft fail (live)

    //   shopDiscount / shopWeight — inert until Phase 4 shop ships

    //   flag         — display-only; DM resolves

    // x/y from the prototype's SVG layout aren't used here — the MVP

    // tree renders as a tier-grouped list. Keeping the fields makes a

    // future graph view a drop-in.

    talents: [

      { id: 'bs_thrift',    name: "Forgemaster's Bargain", icon: '💰', tier: 1, maxRanks: 3, kind: 'standard', x: 200, y: 80,  requires: [],

        desc: 'Reduce GP cost of blacksmithing recipes in the shop. Stacks per rank.',

        effect: { kind: 'shopDiscount', value: 5 } },

      { id: 'bs_xp',        name: 'Hammer Practice',       icon: '📖', tier: 1, maxRanks: 2, kind: 'standard', x: 440, y: 80,  requires: [],

        desc: 'Gain bonus profession XP from successful crafts. Stacks per rank.',

        effect: { kind: 'xpBonus', value: 10 } },

      { id: 'bs_save',      name: 'Frugal Smithing',       icon: '⚒️', tier: 2, maxRanks: 3, kind: 'standard', x: 200, y: 220, requires: ['bs_thrift'],

        desc: 'Chance to not consume a component when a craft fails. Stacks per rank.',

        effect: { kind: 'componentSave', value: 10 } },

      { id: 'bs_supply',    name: "Trader's Eye",          icon: '👁',  tier: 2, maxRanks: 2, kind: 'standard', x: 440, y: 220, requires: ['bs_xp'],

        desc: 'Increase the chance blacksmithing recipes appear when shop rotation refreshes.',

        effect: { kind: 'shopWeight', value: 10 } },

      { id: 'bs_armor',     name: 'Master of Plates',      icon: '🛡️', tier: 3, maxRanks: 1, kind: 'notable',  x: 200, y: 380, requires: ['bs_save'],

        desc: 'No movespeed loss for wearing heavy armor you craft.',

        effect: { kind: 'flag', label: 'No movespeed loss in heavy armor' } },

      { id: 'bs_hint',      name: "Smith's Intuition",     icon: '💭', tier: 3, maxRanks: 1, kind: 'notable',  x: 440, y: 380, requires: ['bs_supply'],

        desc: '+1 hint shown for undiscovered weapon recipes.',

        effect: { kind: 'flag', label: '+1 free hint per discovery attempt' } },

      { id: 'bs_critforge', name: 'Critical Forge',        icon: '💥', tier: 4, maxRanks: 2, kind: 'notable',  x: 320, y: 540, requires: ['bs_armor', 'bs_hint'],

        desc: 'Crafted weapons gain +1 to crit damage rolls per rank.',

        effect: { kind: 'flag', label: '+1 crit damage per rank' } },

      { id: 'bs_legend',    name: 'Legendary Smith',       icon: '⚔️', tier: 5, maxRanks: 1, kind: 'keystone', x: 320, y: 700, requires: ['bs_critforge'],

        desc: 'Once per long rest, instantly succeed on a single difficult craft.',

        effect: { kind: 'flag', label: 'Auto-succeed 1 craft per long rest' } },

    ],

  },

  {

    id: 'alchemy', name: 'Alchemy', icon: '⚗️',

    desc: 'Brew potions, elixirs, and magical tinctures',

    type: 'major', baseXp: 100, maxLevel: 10,

    milestones: [

      { level: 2, desc: 'Steady Hand — reduced material loss' },

      { level: 4, desc: 'Elixir Brewing — tier-2 potions unlocked' },

      { level: 5, desc: 'Double Dose — chance for extra potion' },

      { level: 7, desc: 'Purity of Essence — quality bonus' },

      { level: 10, desc: 'Master Alchemist — +1 use on every potion' },

    ],

    talents: [

      { id: 'alc_thrift', name: "Apothecary's Thrift", icon: '💰', tier: 1, maxRanks: 3, kind: 'standard', x: 320, y: 80,  requires: [],

        desc: 'Reduce GP cost of potion recipes in the shop.',

        effect: { kind: 'shopDiscount', value: 5 } },

      { id: 'alc_save',   name: 'Steady Pour',          icon: '🧪', tier: 2, maxRanks: 3, kind: 'standard', x: 200, y: 220, requires: ['alc_thrift'],

        desc: 'Chance to not consume a component when a brew fails.',

        effect: { kind: 'componentSave', value: 10 } },

      { id: 'alc_xp',     name: 'Curious Mind',         icon: '📖', tier: 2, maxRanks: 2, kind: 'standard', x: 440, y: 220, requires: ['alc_thrift'],

        desc: 'Gain bonus profession XP from successful brews.',

        effect: { kind: 'xpBonus', value: 10 } },

      { id: 'alc_hint',   name: 'Whispered Recipes',    icon: '💭', tier: 3, maxRanks: 1, kind: 'notable',  x: 440, y: 380, requires: ['alc_xp'],

        desc: '+1 hint shown for undiscovered potion recipes.',

        effect: { kind: 'flag', label: '+1 free hint per discovery attempt' } },

      { id: 'alc_yield',  name: 'Bountiful Brewing',    icon: '✨', tier: 4, maxRanks: 2, kind: 'notable',  x: 200, y: 540, requires: ['alc_save'],

        desc: 'Chance to produce an extra dose when crafting potions.',

        effect: { kind: 'flag', label: '+15% extra yield per rank' } },

      { id: 'alc_master', name: "Alchemist's Mastery", icon: '⚗️', tier: 5, maxRanks: 1, kind: 'keystone', x: 320, y: 700, requires: ['alc_yield', 'alc_hint'],

        desc: 'Your potions cannot fail. Period.',

        effect: { kind: 'flag', label: 'Potions never fail' } },

    ],

  },

  {

    id: 'leatherworking', name: 'Leatherworking', icon: '🧥',

    desc: 'Craft light armor, cloaks, and leather goods',

    type: 'major', baseXp: 100, maxLevel: 10,

    milestones: [

      { level: 3, desc: 'Ranger Gear unlocked' },

      { level: 5, desc: 'Camoweave — stealth bonus on crafted items' },

      { level: 8, desc: "Enchanter's Stitch — magical affinity" },

    ],

    talents: [

      { id: 'lth_thrift', name: "Tanner's Bargain", icon: '💰', tier: 1, maxRanks: 3, kind: 'standard', x: 320, y: 80,  requires: [],

        desc: 'Reduce GP cost of leatherworking recipes in the shop.',

        effect: { kind: 'shopDiscount', value: 5 } },

      { id: 'lth_save',   name: 'Careful Cuts',     icon: '✂️', tier: 2, maxRanks: 3, kind: 'standard', x: 320, y: 220, requires: ['lth_thrift'],

        desc: 'Chance to not consume a component when a craft fails.',

        effect: { kind: 'componentSave', value: 10 } },

      { id: 'lth_supple', name: 'Supple Stitching', icon: '🪡', tier: 3, maxRanks: 1, kind: 'notable',  x: 200, y: 380, requires: ['lth_save'],

        desc: 'No movespeed loss for wearing your crafted heavy armor.',

        effect: { kind: 'flag', label: 'No movespeed loss in heavy armor' } },

      { id: 'lth_weight', name: 'Featherweave',     icon: '🪶', tier: 4, maxRanks: 2, kind: 'notable',  x: 440, y: 380, requires: ['lth_save'],

        desc: 'Crafted armor counts as one weight category lighter.',

        effect: { kind: 'flag', label: 'Crafted armor: lighter weight class' } },

      { id: 'lth_master', name: 'Master Tailor',    icon: '🧥', tier: 5, maxRanks: 1, kind: 'keystone', x: 320, y: 560, requires: ['lth_supple', 'lth_weight'],

        desc: 'Crafted leather gear gains +1 magical property at quality 80+.',

        effect: { kind: 'flag', label: 'High-quality leather gains +1 magical trait' } },

    ],

  },

  {

    id: 'cooking', name: 'Cooking', icon: '🍳',

    desc: 'Prepare meals and rations that bolster the party',

    type: 'minor', baseXp: 50, maxLevel: 10,

    milestones: [

      { level: 2, desc: 'Seasoning — better flavor, better buffs' },

      { level: 5, desc: 'Hearty Fare — food gives extra temp HP' },

      { level: 8, desc: 'Master Chef — all food becomes Fine quality' },

    ],

    talents: [

      { id: 'cook_thrift', name: 'Pantry Saver',     icon: '💰', tier: 1, maxRanks: 3, kind: 'standard', x: 200, y: 80,  requires: [],

        desc: 'Reduce GP cost of cooking recipes in the shop.',

        effect: { kind: 'shopDiscount', value: 5 } },

      { id: 'cook_xp',     name: 'Tasting Practice', icon: '👅', tier: 2, maxRanks: 2, kind: 'standard', x: 440, y: 80,  requires: [],

        desc: 'Gain bonus profession XP from successful meals.',

        effect: { kind: 'xpBonus', value: 10 } },

      { id: 'cook_hearty', name: 'Hearty Portions',  icon: '🍲', tier: 3, maxRanks: 2, kind: 'notable',  x: 200, y: 240, requires: ['cook_thrift'],

        desc: 'Your meals grant +2 temp HP per rank to allies who eat them.',

        effect: { kind: 'flag', label: '+2 temp HP from meals per rank' } },

      { id: 'cook_iron',   name: 'Iron Stomach',     icon: '🛡️', tier: 4, maxRanks: 1, kind: 'keystone', x: 440, y: 240, requires: ['cook_xp'],

        desc: 'Allies eating your meals gain advantage on saves vs poison for 1 hour.',

        effect: { kind: 'flag', label: 'Meals: advantage on poison saves (1h)' } },

    ],

  },

  {

    id: 'fishing', name: 'Fishing', icon: '🎣',

    desc: 'Catch fish and river creatures',

    type: 'minor', baseXp: 30, maxLevel: 10,

    milestones: [

      { level: 3, desc: 'Lucky Cast — chance for double catch' },

      { level: 7, desc: 'Rare Catch — chance for exotic fish' },

    ],

    talents: [

      { id: 'fish_patience', name: 'Patient Angler', icon: '🧘', tier: 1, maxRanks: 3, kind: 'standard', x: 320, y: 80,  requires: [],

        desc: 'Gain bonus XP per cast.',

        effect: { kind: 'xpBonus', value: 10 } },

      { id: 'fish_lucky',    name: 'Lucky Lure',      icon: '🍀', tier: 2, maxRanks: 2, kind: 'notable',  x: 320, y: 220, requires: ['fish_patience'],

        desc: 'Chance to catch a second fish per cast.',

        effect: { kind: 'flag', label: '+10% chance for double catch per rank' } },

      { id: 'fish_exotic',   name: 'Exotic Hooks',    icon: '🐠', tier: 3, maxRanks: 1, kind: 'keystone', x: 320, y: 380, requires: ['fish_lucky'],

        desc: 'Chance for rare/exotic catches doubled.',

        effect: { kind: 'flag', label: '2x rare catch chance' } },

    ],

  },

];



// XP curve: baseXp scaled mildly per level. Mirrors the prototype.

const xpForLevel = (prof, level) => {

  if (!prof || level <= 1) return 0;

  return Math.round((prof.baseXp || 100) * (1 + (level - 1) * 0.35));

};



const totalXpForLevel = (prof, level) => {

  let s = 0;

  for (let i = 1; i < level; i++) s += xpForLevel(prof, i + 1);

  return s;

};



// Compute XP awarded for a single craft.

//   recipe: the recipe being crafted (uses res.rar for base XP).

//   ctx: { wasUnknown, isFirstCraft, destructionRulesOn, profSettings, talentXpBonusPct }.

// Returns { base, multipliers, total } so callers can show a receipt.

//

// 2026-04-27 (Phase 2): talentXpBonusPct adds an additive multiplier

// after the rarity base, so a player with 30% xpBonus from talents on

// a Rare recipe gets `50 (base) × 1.30 = 65 XP` (before first-craft /

// discovery multipliers, which then multiply on top).

const computeCraftXp = (recipe, ctx) => {

  const rarityMult = { Common: 1, Uncommon: 1.5, Rare: 2.5, 'Very Rare': 4, Legendary: 8, Artifact: 15 };

  const base = Math.round(20 * (rarityMult[recipe?.res?.rar || 'Common'] || 1));

  const mults = [];

  let total = base;

  const talentPct = ctx?.talentXpBonusPct || 0;

  if (talentPct > 0) {

    const m = 1 + talentPct / 100;

    mults.push({ label: `Talents (+${talentPct}%)`, mult: m });

    total *= m;

  }

  const ps = ctx?.profSettings || {};

  if (ctx?.isFirstCraft) {

    const m = ps.firstCraftBonusMult ?? 2;

    mults.push({ label: 'First Craft', mult: m });

    total *= m;

  }

  if (ctx?.wasUnknown && ctx?.destructionRulesOn) {

    const m = ps.discoveryXpMult ?? 5;

    mults.push({ label: 'Discovery (Destruction Risk)', mult: m });

    total *= m;

  }

  return { base, multipliers: mults, total: Math.round(total) };

};



// Apply XP gain to a char.playerProfs entry. Pure; returns the new

// playerProfs map plus a `levelUps: [{ profId, oldLevel, newLevel }]`

// array so the caller can fire celebration UI.

//

// 2026-04-27 (Phase 2): each level-up grants +1 skillPoint that the

// player spends in the talent tree. skillPoints + talentRanks are

// preserved across XP grants and persist on char.playerProfs.

const applyProfXp = (playerProfs, profId, amount, professions = DEFAULT_PROFESSIONS) => {

  if (!profId || amount <= 0) return { playerProfs, levelUps: [] };

  const prof = professions.find(p => p.id === profId);

  if (!prof) return { playerProfs, levelUps: [] };

  const cur = playerProfs[profId] || { level: 1, xp: 0, totalXp: 0, skillPoints: 0, talentRanks: {} };

  let level = cur.level || 1;

  let xp = (cur.xp || 0) + amount;

  let totalXp = (cur.totalXp || 0) + amount;

  let skillPoints = cur.skillPoints || 0;

  const talentRanks = cur.talentRanks || {};

  const oldLevel = level;

  // Roll xp into levels until we hit the cap or run out.

  while (level < (prof.maxLevel || 10)) {

    const need = xpForLevel(prof, level + 1);

    if (xp < need) break;

    xp -= need;

    level += 1;

    skillPoints += 1; // Phase 2: 1 SP per level.

  }

  if (level >= (prof.maxLevel || 10)) xp = 0; // cap at max

  const next = { ...playerProfs, [profId]: { level, xp, totalXp, skillPoints, talentRanks } };

  const levelUps = level > oldLevel ? [{ profId, oldLevel, newLevel: level }] : [];

  return { playerProfs: next, levelUps };

};



// 2026-04-27 (Phase 2): can the player put another rank into this

// talent? Checks tier unlock (level), prereqs (require talents have

// at least 1 rank), maxRanks, and skillPoints budget. Returns

// { ok, reason } so the UI can show a tooltip.

const canSpendTalent = (prof, talent, playerProf) => {

  if (!prof || !talent || !playerProf) return { ok: false, reason: 'No data' };

  const ranks = playerProf.talentRanks || {};

  const cur = ranks[talent.id] || 0;

  if (cur >= (talent.maxRanks || 1)) return { ok: false, reason: 'Maxed out' };

  const tierLvl = TALENT_TIER_LEVELS[talent.tier] || 1;

  if ((playerProf.level || 1) < tierLvl) return { ok: false, reason: `Requires Lv ${tierLvl}` };

  for (const reqId of (talent.requires || [])) {

    if ((ranks[reqId] || 0) < 1) {

      const req = (prof.talents || []).find(t => t.id === reqId);

      return { ok: false, reason: `Requires ${req?.name || reqId}` };

    }

  }

  if ((playerProf.skillPoints || 0) < 1) return { ok: false, reason: 'No skill points' };

  return { ok: true, reason: null };

};



// 2026-04-27 (Phase 2): roll up the current ranked talents into a

// summary the craft pipeline (and future surfaces) can read. Returns

// { xpBonusPct, componentSavePct, shopDiscountPct, shopWeightPct, flags: [{label, ranks}] }.

// MVP: only xpBonusPct is wired into computeCraftXp; shop ones land

// alive when Phase 4 ships, componentSave when free-craft fail handling

// gets revisited.

const sumTalentEffects = (prof, playerProf) => {

  const out = { xpBonusPct: 0, componentSavePct: 0, shopDiscountPct: 0, shopWeightPct: 0, flags: [] };

  if (!prof || !playerProf) return out;

  const ranks = playerProf.talentRanks || {};

  (prof.talents || []).forEach(t => {

    const r = ranks[t.id] || 0;

    if (r <= 0) return;

    const eff = t.effect || {};

    if (eff.kind === 'xpBonus')        out.xpBonusPct       += (eff.value || 0) * r;

    else if (eff.kind === 'componentSave') out.componentSavePct += (eff.value || 0) * r;

    else if (eff.kind === 'shopDiscount')  out.shopDiscountPct  += (eff.value || 0) * r;

    else if (eff.kind === 'shopWeight')    out.shopWeightPct    += (eff.value || 0) * r;

    else if (eff.kind === 'flag')          out.flags.push({ label: eff.label || t.name, ranks: r });

  });

  return out;

};



// Check whether a character meets a recipe's profession requirement.

// profReq shape: { profId, minLevel } | null

// Returns { ok, reason } — reason is used for the disabled-button title.

const checkProfReq = (recipe, playerProfs, professions = DEFAULT_PROFESSIONS) => {

  const req = recipe?.profReq;

  if (!req || !req.profId) return { ok: true, reason: null };

  const prof = professions.find(p => p.id === req.profId);

  if (!prof) return { ok: true, reason: null }; // unknown profession — allow

  const player = playerProfs?.[req.profId];

  const level = player?.level || 0;

  const need = req.minLevel || 1;

  if (level >= need) return { ok: true, reason: null };

  return { ok: false, reason: `Requires ${prof.name} Lv ${need}` };

};



const DEFAULT_PROF_SETTINGS = {

  enabled: true,

  maxMajor: 1,

  discoveryXpMult: 5,

  firstCraftBonusMult: 2,

  // 2026-04-27 (Phase 3): when false, every craft yields 'common' quality.

  // True = roll quality based on level-over-min skill.

  qualityEnabled: true,

};



// 2026-04-27 (Phase 3): Quality Tiers — applied to crafted items as

// stamped metadata (item.quality) and surfaced on the success receipt.

// Thresholds match the prototype's rollCraftQuality. Color tokens use

// the --quality-* CSS variables defined in injectCraftingStyles below.

const QUALITY_TIERS = [

  { id: 'common',    label: 'Common',     min: 0,   color: '#9a8b7a', desc: 'Standard crafting quality' },

  { id: 'fine',      label: 'Fine',       min: 30,  color: '#5cb85c', desc: 'A well-made piece' },

  { id: 'superior',  label: 'Superior',   min: 60,  color: '#4a9ad4', desc: 'Crafted with skill' },

  { id: 'master',    label: 'Masterwork', min: 80,  color: '#b388e8', desc: 'Exceptional craftsmanship' },

  { id: 'legendary', label: 'Legendary',  min: 100, color: '#f0c240', desc: 'A creation worthy of legends!' },

];



const qualityMeta = (tierId) => QUALITY_TIERS.find(t => t.id === tierId) || QUALITY_TIERS[0];



// Roll a quality tier for a craft. Returns { tier, roll } where tier

// is one of the QUALITY_TIERS ids. Always 'common' when:

//   - profSettings.qualityEnabled is false (master toggle)

//   - the recipe has no profReq (no skill to compare against)

//   - the player hasn't picked the recipe's profession

// Otherwise: roll = (1-100) + (level - minLevel) * 8, tier picked by threshold.

// 2026-04-27 (Phase 3): no bonus_stat milestone path yet (our milestones

// are flavor-only at MVP). When that lands, this is the spot to extend.

const rollCraftQuality = (recipe, playerProfsState, settings = DEFAULT_PROF_SETTINGS) => {

  if (!settings?.qualityEnabled) return { tier: 'common', roll: 50 };

  const req = recipe?.profReq;

  if (!req?.profId) return { tier: 'common', roll: 50 };

  const player = playerProfsState?.[req.profId];

  if (!player) return { tier: 'common', roll: 50 };

  const lvl = player.level || 1;

  const over = Math.max(0, lvl - (req.minLevel || 1));

  const roll = Math.floor(Math.random() * 100) + 1 + over * 8;

  // Walk QUALITY_TIERS from high to low; first match wins.

  let tier = 'common';

  for (let i = QUALITY_TIERS.length - 1; i >= 0; i--) {

    if (roll >= QUALITY_TIERS[i].min) { tier = QUALITY_TIERS[i].id; break; }

  }

  return { tier, roll };

};



const DEFAULT_RECIPES = [

  {

    id: "r8", name: "Iron Dagger", icon: "🗡️", cat: "weapons",

    desc: "A simple blade for early smithing practice", disc: true, vis: true,

    tags: ["forge required", "starter", "blacksmithing"],

    profReq: { profId: 'blacksmith', minLevel: 1 },

    ings: [{ iid: "iron_ingot", qty: 1 },{ iid: "leather_strip", qty: 1 }],

    res: { name: "Iron Dagger", icon: "🗡️", desc: "Light practice blade. Finesse, light.", cat: "weapons", rar: "Common", price: 8, buffs: [], debuffs: [], onUse: [], notes: "A reliable starter weapon.", dmNotes: "Starter blacksmithing craft for profession XP.", tags: ["weapon", "crafted"], uses: null, abilities: [] },

  },

  {

    id: "r9", name: "Reinforced Buckler", icon: "🛡️", cat: "armor",

    desc: "A compact shield banded with iron", disc: true, vis: true,

    tags: ["forge required", "starter", "blacksmithing", "equippable"],

    profReq: { profId: 'blacksmith', minLevel: 1 },

    ings: [{ iid: "iron_ingot", qty: 2 },{ iid: "leather_strip", qty: 1 }],

    res: { name: "Reinforced Buckler", icon: "🛡️", desc: "+1 AC while equipped", cat: "armor", rar: "Uncommon", price: 35, buffs: ["+1 AC"], debuffs: [], onUse: [], notes: "", dmNotes: "Starter shield craft; the +1 AC is intentionally hookable.", tags: ["equippable", "shield", "crafted"], uses: null, abilities: [] },

  },

  {

    id: "r10", name: "Tempered Longsword", icon: "⚔️", cat: "weapons",

    desc: "A balanced blade quenched with monster-core heat", disc: true, vis: true,

    tags: ["forge required", "blacksmithing", "equippable"],

    profReq: { profId: 'blacksmith', minLevel: 2 },

    ings: [{ iid: "iron_ingot", qty: 2 },{ iid: "monster_core", qty: 1 }],

    res: { name: "Tempered Longsword", icon: "⚔️", desc: "+1 damage from a carefully tempered edge", cat: "weapons", rar: "Uncommon", price: 75, buffs: ["+1 Damage"], debuffs: [], onUse: [], notes: "", dmNotes: "Journeyman blacksmithing craft; +1 Damage is the current hookable proxy for a better edge.", tags: ["equippable", "weapon", "crafted"], uses: null, abilities: [] },

  },

  {

    id: "r1", name: "Flamebrand Sword", icon: "🗡️", cat: "weapons",

    desc: "A blade infused with elemental fire", disc: true, vis: true,

    tags: ["forge required", "enchanted"],

    // 2026-04-27 (later): profession requirement added (Phase 1 MVP).

    // profReq shape: { profId, minLevel } | null. Null = always craftable.

    profReq: { profId: 'blacksmith', minLevel: 3 },

    ings: [{ iid: "monster_core", qty: 3 },{ iid: "mandrake_root", qty: 2 },{ iid: "iron_ingot", qty: 2 },{ iid: "fire_essence", qty: 1 }],

    res: {

      name: "Flamebrand Sword", icon: "🗡️", desc: "+1 Longsword, 1d6 fire damage",

      cat: "weapons", rar: "Rare", price: 250,

      // 2026-04-27: buffs rewritten to use only auto-hookable patterns

      // (stat / skill / AC / adv-dis). The original "+1 Attack" / "+1d6 Fire"

      // strings are conceptually right for 5e but the engine has no

      // attack-roll or elemental-damage hook today, so they sat inert in

      // buffList. +1 Str is the closest auto-hookable proxy for a +1

      // magic weapon (boosts melee attack and damage). Fire-on-hit lives

      // on the active ability + the description, where the DM resolves it.

      buffs: ["+1 Str"], debuffs: [], onUse: [],

      notes: "", dmNotes: "Requires a forge. Base weapon is a +1 longsword; on a hit, deals an extra 1d6 fire damage (DM resolves until attack/elemental hooks ship).", tags: ["enchanted"],

      uses: null,

      // 2026-04-27: ability example tuned. The previous "+2d6 Fire"

      // effect rolled dice via the receipt UI but produced no persistent

      // status the player could see — and we don't yet have an

      // auto-hook for "next attack deals +Nd6 elemental" (see notes).

      // "Vanish in Flames" instead sets a clearly time-limited,

      // non-hookable status — the engine doesn't track invisibility

      // mechanically, but adding it to the buff list with the duration

      // in the text gives the player a visible reminder for the duration.

      abilities: [{ name: "Vanish in Flames", description: "Wreathe yourself in fire and disappear for 1 minute. Reappear with a burst of flame.", effects: ["Invisible (1 minute)"], uses: { current: 1, max: 1, resetType: "shortRest" } }],

    },

  },

  {

    id: "r2", name: "Log Bundle", icon: "🪵", cat: "materials",

    desc: "Combine branches into logs", disc: true, vis: true, tags: ["survival"],

    profReq: null, // 2026-04-27: utility craft, anyone.

    ings: [{ iid: "branch", qty: 3 }],

    res: { name: "Log", icon: "🪵", desc: "A sturdy log", cat: "materials", rar: "Common", price: 1, buffs: [], debuffs: [], onUse: [], notes: "", dmNotes: "", tags: [], uses: null, abilities: [] },

  },

  {

    id: "r3", name: "Firewood", icon: "🔥", cat: "materials",

    desc: "Split logs into firewood", disc: true, vis: true, tags: ["survival", "cooking"],

    profReq: null,

    ings: [{ iid: "log", qty: 3 }],

    res: { name: "Firewood", icon: "🔥", desc: "Enough for one long rest campfire", cat: "materials", rar: "Common", price: 0, buffs: [], debuffs: [], onUse: [], notes: "", dmNotes: "", tags: ["survival"], uses: null, abilities: [] },

  },

  {

    id: "r4", name: "Healing Salve", icon: "➕", cat: "potions",

    desc: "A restorative herbal paste", disc: true, vis: true, tags: ["alchemist", "consumable"],

    profReq: { profId: 'alchemy', minLevel: 1 },

    ings: [{ iid: "herb_bundle", qty: 2 },{ iid: "mandrake_root", qty: 1 },{ iid: "empty_vial", qty: 1 }],

    res: { name: "Healing Salve", icon: "➕", desc: "Heals 2d4+2 HP (consumable)", cat: "potions", rar: "Uncommon", price: 50, buffs: [], debuffs: [], onUse: ["+2d4+2 HP"], notes: "", dmNotes: "", tags: ["consumable"], uses: { cur: 1, max: 1, reset: "None" }, abilities: [] },

  },

  {

    id: "r5", name: "Ranger's Cloak", icon: "🧥", cat: "armor",

    desc: "Woven from spider silk and wolf pelt", disc: false, vis: true, tags: ["equippable"],

    profReq: { profId: 'leatherworking', minLevel: 3 },

    ings: [{ iid: "spider_silk", qty: 4 },{ iid: "wolf_pelt", qty: 2 },{ iid: "leather_strip", qty: 3 }],

    // 2026-04-27: dropped "(Forest)" qualifier from the Stealth buff —

    // isBuffAutoApplied's skill regex doesn't allow parenthetical

    // conditions, so the bonus wasn't auto-hooking. Kept the

    // forest-specific narrative in dmNotes for DM judgment (P14).

    res: { name: "Ranger's Cloak", icon: "🧥", desc: "+1 AC, +2 Stealth (advantage in forests, DM resolves)", cat: "armor", rar: "Rare", price: 300, buffs: ["+1 AC", "+2 Stealth"], debuffs: [], onUse: [], notes: "", dmNotes: "Grants forest camo — DM may upgrade the +2 Stealth to advantage when the wearer is concealed in foliage.", tags: ["equippable"], uses: null, abilities: [] },

  },

  {

    id: "r6", name: "Cooked Rations", icon: "🍖", cat: "food",

    desc: "Seasoned meat with herbs", disc: true, vis: true, tags: ["cooking", "consumable"],

    profReq: { profId: 'cooking', minLevel: 1 },

    ings: [{ iid: "raw_meat", qty: 1 },{ iid: "herb_bundle", qty: 1 }],

    // 2026-04-27: temp HP isn't auto-hookable. Moved the heal to onUse

    // so it goes through the consume flow (now with dice-aware roll

    // receipts) and uses the buff slot for Con — a hearty meal making

    // you sturdier reads as fair flavor and actually hooks.

    res: { name: "Cooked Rations", icon: "🍖", desc: "Hearty meal — heals 1d4 HP on consume; +1 Con while held", cat: "food", rar: "Common", price: 5, buffs: ["+1 Con"], debuffs: [], onUse: ["+1d4 HP"], notes: "Counts as 1 meal for travel", dmNotes: "", tags: ["consumable"], uses: { cur: 1, max: 1, reset: "None" }, abilities: [] },

  },

  {

    id: "r7", name: "Crystal Focus", icon: "🔮", cat: "tools",

    desc: "Arcane focus from refined crystal", disc: false, vis: false, tags: ["enchanted", "quest item"],

    profReq: { profId: 'alchemy', minLevel: 4 },

    ings: [{ iid: "crystal_shard", qty: 5 },{ iid: "monster_core", qty: 1 }],

    // 2026-04-27: spell attack isn't auto-hookable, so swapped in +1 Int

    // (typical arcane spellcasting modifier) as the closest hookable

    // proxy. DMs casting off Wis/Cha can swap via the editor; the

    // judgment is surfaced in dmNotes (P14).

    res: { name: "Crystal Focus", icon: "🔮", desc: "+1 Int while attuned (proxy for +1 spell attack)", cat: "tools", rar: "Very Rare", price: 500, buffs: ["+1 Int"], debuffs: [], onUse: [], notes: "Requires attunement", dmNotes: "Found in Crystalline Caves. +1 Int is a stand-in for the canonical +1-to-spell-attack-rolls effect — DM may instead grant +1 to spell save DC and spell attack rolls when the proper hook ships.", tags: ["enchanted", "equippable"], uses: null, abilities: [] },

  },

  {

    id: "r11", name: "Minor Healing Draught", icon: "🧪", cat: "potions",

    desc: "A quick field tonic for small wounds", disc: true, vis: true,

    tags: ["alchemy", "starter", "consumable"],

    profReq: { profId: 'alchemy', minLevel: 1 },

    ings: [{ iid: "herb_bundle", qty: 1 },{ iid: "empty_vial", qty: 1 }],

    res: { name: "Minor Healing Draught", icon: "🧪", desc: "Heals 1d4+1 HP (consumable)", cat: "potions", rar: "Common", price: 15, buffs: [], debuffs: [], onUse: ["+1d4+1 HP"], notes: "", dmNotes: "Starter alchemy craft for profession XP.", tags: ["consumable", "crafted"], uses: { cur: 1, max: 1, reset: "None" }, abilities: [] },

  },

  {

    id: "r12", name: "Antitoxin Tonic", icon: "⚗️", cat: "potions",

    desc: "A bitter tonic against venom and sickness", disc: true, vis: true,

    tags: ["alchemy", "consumable", "medicine"],

    profReq: { profId: 'alchemy', minLevel: 2 },

    ings: [{ iid: "herb_bundle", qty: 1 },{ iid: "mandrake_root", qty: 1 },{ iid: "empty_vial", qty: 1 }],

    res: { name: "Antitoxin Tonic", icon: "⚗️", desc: "Advantage on poison saves for 1 hour", cat: "potions", rar: "Uncommon", price: 60, buffs: [], debuffs: [], onUse: ["Adv on Poison saves (1 hour)"], notes: "", dmNotes: "Journeyman alchemy craft; DM resolves exact poison/disease coverage.", tags: ["consumable", "crafted"], uses: { cur: 1, max: 1, reset: "None" }, abilities: [] },

  },

  {

    id: "r13", name: "Elixir of Focus", icon: "🧪", cat: "potions",

    desc: "A clear draught that sharpens perception and recall", disc: true, vis: true,

    tags: ["alchemy", "consumable", "focus"],

    profReq: { profId: 'alchemy', minLevel: 3 },

    ings: [{ iid: "crystal_shard", qty: 1 },{ iid: "mandrake_root", qty: 1 },{ iid: "empty_vial", qty: 1 }],

    res: { name: "Elixir of Focus", icon: "🧪", desc: "+1 Wis and +2 Int after drinking", cat: "potions", rar: "Rare", price: 120, buffs: [], debuffs: [], onUse: ["+1 Wis", "+2 Int"], notes: "", dmNotes: "Consumable bonuses are intentionally on-use, not passive while carried.", tags: ["consumable", "crafted"], uses: { cur: 1, max: 1, reset: "None" }, abilities: [] },

  },

];



const DEFAULT_INV = { monster_core: 8, mandrake_root: 14, iron_ingot: 16, branch: 9, log: 3, raw_meat: 4, herb_bundle: 16, spider_silk: 2, fire_essence: 2, wolf_pelt: 1, crystal_shard: 6, empty_vial: 14, leather_strip: 10 };



export const getDefaultCraftingProgressionSummary = () => {

  const summarize = (profId) => {

    const recipes = DEFAULT_RECIPES.filter(r => r.profReq?.profId === profId);

    const discovered = recipes.filter(r => r.disc && r.vis !== false);

    const levels = [...new Set(recipes.map(r => r.profReq?.minLevel || 1))].sort((a, b) => a - b);

    const craftableWithDefaultMaterials = discovered

      .filter(r => (r.ings || []).every(ing => (DEFAULT_INV[ing.iid] || 0) >= ing.qty))

      .map(r => r.name);

    return {

      recipeCount: recipes.length,

      discoveredCount: discovered.length,

      levels,

      craftableWithDefaultMaterials,

    };

  };

  return {

    blacksmith: summarize('blacksmith'),

    alchemy: summarize('alchemy'),

  };

};



// 2026-04-28 (Phase 4A): Recipe Shop — curated entries the trainer NPC

// sells. recipeId pairs with a GP price; the recipe's existing profReq

// supplies the level gate (no per-entry override at MVP). When the DM

// editor lands (Phase 4B) this becomes a per-campaign blob on

// globalSettings.shopEntries; for now it's a static module-scope list so

// the shop has something to display without DM setup.

const SHOP_ENTRIES = [

  { recipeId: 'r8', gpPrice: 25 },  // Iron Dagger - blacksmith L1

  { recipeId: 'r9', gpPrice: 35 },  // Reinforced Buckler - blacksmith L1

  { recipeId: 'r10', gpPrice: 90 }, // Tempered Longsword - blacksmith L2

  { recipeId: 'r11', gpPrice: 20 }, // Minor Healing Draught - alchemy L1

  { recipeId: 'r12', gpPrice: 55 }, // Antitoxin Tonic - alchemy L2

  { recipeId: 'r13', gpPrice: 90 }, // Elixir of Focus - alchemy L3

  { recipeId: 'r1', gpPrice: 250 }, // Flamebrand Sword — blacksmith L3

  { recipeId: 'r4', gpPrice: 40 },  // Healing Salve — alchemy L1

  { recipeId: 'r5', gpPrice: 180 }, // Ranger's Cloak — leatherworking L3

  { recipeId: 'r6', gpPrice: 25 },  // Cooked Rations — cooking L1

  { recipeId: 'r7', gpPrice: 320 }, // Crystal Focus — alchemy L4

];



// 2026-04-28 (Phase 4A): per-recipe knowledge resolution. The global

// `recipe.disc` flag is the DM-controlled "the party knows this" toggle

// (carried over from the original crafting system). learnedRecipes is

// the per-character buy-from-shop layer on top — once a player buys a

// scroll, their character holds the recipe id in this list.

const isRecipeKnown = (recipe, char) => {

  if (!recipe) return false;

  if (recipe.disc) return true;

  if (!char) return false;

  return Array.isArray(char.learnedRecipes) && char.learnedRecipes.includes(recipe.id);

};

const DEFAULT_TAGS = ["crafting","rare drop","quest item","consumable","equippable","forge required","alchemist","cooking","survival","enchanted"];



// ─── CRAFTING ↔ INVENTORY BRIDGE (2026-04-22) ───────────────────────

// Characters have two separate storage spaces:

//  - char.inventory: rich items the character carries (weapons, armor, quest

//    items, consumables). Used during play (equipped, used, tracked via uses).

//  - char.craftingTable: materials + finished crafts that haven't been

//    moved to inventory yet. Per-character, persists across sessions. Feeds

//    the CraftingPage. Shape: { inv: {[id]: qty}, customItems: [{id,name,icon,cat}] }.

//    (DEFAULT_ITEMS supplies the standard item definitions; customItems holds

//    dynamically created ones like crafted swords.)

//

// The transforms below move items between the two shapes. Round-trip

// fidelity: unmapped fields are preserved in `_raw` so no data is lost when

// a player moves an item table → inventory → table.



// Category taxonomy maps between the two systems.

// Crafting cats (in recipes + items): "weapons" | "armor" | "potions" | "food" | "materials" | "tools"

// Inventory categories (in char.inventory): "weapon" | "armor" | "consumable" | "storage" | "quest" | "misc"

const CRAFT_CAT_TO_INVENTORY = {

  weapons: 'weapon',

  armor: 'armor',

  potions: 'consumable',

  food: 'consumable',

  materials: 'storage',

  tools: 'storage',

};

const INVENTORY_CAT_TO_CRAFT = {

  weapon: 'weapons',

  armor: 'armor',

  consumable: 'potions', // ambiguous — defaults to potions; food gets its own tag

  storage: 'materials',

  quest: 'tools',

  misc: 'materials',

};



// Convert a recipe result (or crafting-table item) into a char.inventory item.

// Safe to pass recipe.res directly; additional qty arg lets the caller specify

// stack size when crafting multiple at once. Field mapping per header comment.

const craftResultToInventoryItemLegacyCompat = (res, qty = 1) => {

  if (!res) return null;

  const category = CRAFT_CAT_TO_INVENTORY[res.cat] || 'misc';

  const usesIn = res.uses || null;

  // Uses: recipe shape uses {cur,max,reset}; inventory shape uses {current,max,resetType}.

  // Some recipes use 'cur' (e.g. DEFAULT_RECIPES r4), others omit uses entirely.

  // 2026-04-27: bug fix — previously did toLowerCase().replace(/\s+/g, ''),

  // which produced "longrest" / "shortrest". USES_RESET_TYPES keys are

  // camelCase ("longRest" / "shortRest"), so the lookup missed and the

  // display fell back to "Doesn't reset" — making short-/long-rest

  // abilities look like one-time-use. Normalize to canonical camelCase.

  const normalizeReset = (r) => {

    const s = String(r || 'none').toLowerCase().replace(/[\s_-]+/g, '');

    if (s === 'longrest' || s === 'long' || s === 'lr') return 'longRest';

    if (s === 'shortrest' || s === 'short' || s === 'sr') return 'shortRest';

    return 'none';

  };

  const uses = usesIn ? {

    current: (usesIn.cur != null ? usesIn.cur : usesIn.current) ?? usesIn.max ?? 1,

    max: usesIn.max ?? 1,

    resetType: normalizeReset(usesIn.reset || usesIn.resetType),

  } : { current: 1, max: 1, resetType: 'none' };

  return {

    id: 'crafted_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),

    name: res.name || 'Crafted Item',

    category,

    quantity: Math.max(1, qty || 1),

    equipped: false,

    favorited: false,

    buffs: [...(res.buffs || [])],

    debuffs: [...(res.debuffs || [])],

    buffsApplyOnUse: category === 'consumable',

    notes: res.notes || '',

    uses,

    // 2026-04-27: normalize recipe ability shape → inventory ability shape.

    // Recipe-side stores: { name, desc, effects: "string", limited, cur, max, reset }.

    // InventoryPanelV2 expects:  { id, name, description, effects: [...], uses: {current,max,resetType} }.

    // Mismatches were crashing on equip — `effects.map is not a function` when `effects` was a string.

    // 2026-04-27: normalize ability shape, idempotent for round-trips.

    // Inputs may arrive in either of two shapes:

    //   recipe-side    { name, desc, effects: "string", limited, cur, max, reset }

    //   inventory-side { id, name, description, effects: [...], uses: { current, max, resetType } }

    // The latter happens when an inventory item round-trips through the

    // crafting table (_invSnapshot path) and lands back here. Earlier code

    // only checked `a.limited`; on round-trip that field is gone, so uses

    // got dropped and the next render-or-edit broke. Now we accept either.

    abilities: (res.abilities || []).map((a, idx) => {

      const effectsArr = Array.isArray(a.effects)

        ? a.effects.filter(Boolean)

        : (typeof a.effects === 'string' && a.effects.trim())

          ? a.effects.split(',').map(s => s.trim()).filter(Boolean)

          : [];

      let usesOut = null;

      if (a.uses && typeof a.uses === 'object') {

        // Already inventory-shape — preserve as-is, normalizing keys.

        // Reset must land on camelCase USES_RESET_TYPES keys; see the

        // outer normalizeReset for the bug story.

        usesOut = {

          current: a.uses.current ?? a.uses.cur ?? a.uses.max ?? 1,

          max: a.uses.max ?? 1,

          resetType: normalizeReset(a.uses.resetType || a.uses.reset),

        };

      } else if (a.limited) {

        usesOut = {

          current: a.cur ?? a.max ?? 1,

          max: a.max ?? 1,

          resetType: normalizeReset(a.reset),

        };

      }

      return {

        id: a.id || `ab_${Date.now()}_${idx}_${Math.random().toString(36).slice(2, 6)}`,

        name: a.name || 'Ability',

        description: a.description || a.desc || '',

        effects: effectsArr,

        uses: usesOut,

      };

    }),

    questDetails: res.dmNotes || '',

    questComplete: false,

    onUseEffects: [...(res.onUse || [])],

    // 2026-04-27: combat bonuses (advantage/disadvantage/save/passive/

    // resistance) are now authorable in the recipe editor and pass

    // through to the inventory item where they're already rendered as

    // virtual badges and consulted by combat helpers.

    combatBonuses: Array.isArray(res.combatBonuses) ? deepClone(res.combatBonuses) : [],

    addedDate: new Date().toISOString(),

    // Preserve the crafting-side shape + icon for round-trip fidelity when

    // transferring back to the crafting table.

    _raw: { icon: res.icon || '📦', cat: res.cat || null, rar: res.rar || 'Common', tags: res.tags || [] },

  };

};

const craftResultToInventoryItem = (res, qty = 1) => (
  materializeLegacyInventoryItemFromCraftResult(res, { quantity: qty })
);



// Convert a char.inventory item into a crafting-table {item, qty} pair.

// Returns { item: {id,name,icon,cat,...}, qty } so the caller can merge into

// the table's customItems list + inv map.

const inventoryItemToTableItem = (invItem) => {

  if (!invItem) return null;

  const cat = invItem._raw?.cat || INVENTORY_CAT_TO_CRAFT[invItem.category] || 'materials';

  const icon = invItem._raw?.icon || '📦';

  return {

    item: {

      id: invItem.id, // reuse id so transfers round-trip predictably

      name: invItem.name || 'Item',

      icon,

      cat,

      // Preserve rich fields so re-crafting or re-transferring maintains data.

      _invSnapshot: { ...invItem, _raw: undefined },

    },

    qty: invItem.quantity || 1,

  };

};



// Stack-merge helper for char.inventory. Finds an existing unequipped entry

// with the same name and increments its quantity; else appends as new.

const stackIntoInventory = (inventory, newItem) => {

  return stackLegacyInventoryItem(inventory || [], newItem);

};

const buildConsumedIngredientsFromTableInv = (beforeInv = {}, afterInv = {}) => (
  Object.keys(beforeInv || {})
    .map((itemId) => ({
      itemId,
      quantity: Math.max(0, (Number(beforeInv[itemId]) || 0) - (Number(afterInv?.[itemId]) || 0)),
    }))
    .filter((ingredient) => ingredient.quantity > 0)
);

const withCraftingTableTransferMetadata = (res = {}, tableItemId = null) => ({
  ...(res || {}),
  __craftingTableItemId: tableItemId,
});

const stripCraftingTableTransferMetadata = (res = {}) => {
  if (!res || typeof res !== 'object') return res;
  const { __craftingTableItemId, ...clean } = res;
  return clean;
};

const serializeCraftingBridgePayload = (payload) => JSON.stringify(payload ?? {});

const awaitMaybePromise = async (value) => (
  value && typeof value.then === 'function'
    ? await value
    : value
);

const makeSingleValueFactory = (firstValue) => {
  let used = false;
  return () => {
    if (!used && firstValue) {
      used = true;
      return firstValue;
    }
    used = true;
    return generateId();
  };
};

const makeAbilityIdFactoryForEntry = (entryId) => {
  let index = 0;
  return (abilityId = 'ability') => {
    if (entryId) {
      const safeAbilityId = String(abilityId || 'ability')
        .replace(/[^A-Za-z0-9_-]+/g, '_')
        .replace(/^_+|_+$/g, '') || 'ability';
      const next = `${entryId}:ability:${safeAbilityId}:${index}`;
      index += 1;
      return next;
    }
    index += 1;
    return generateId();
  };
};

const syncAuthorityCharSnapshot = (syncAuthorityChar, charId, nextState) => {
  if (typeof syncAuthorityChar !== 'function' || !nextState?.nextCharData) return;
  syncAuthorityChar(charId, nextState.nextCharData);
};

const syncAuthorityPartySnapshot = (syncAuthorityPartyInventory, nextState) => {
  if (typeof syncAuthorityPartyInventory !== 'function' || !nextState?.nextPartyInventory) return;
  syncAuthorityPartyInventory(nextState.nextPartyInventory);
};

export const addCraftResultToInventoryBridge = async ({
  charId,
  char,
  recipeRes,
  qty,
  updatedTableInv,
  tableRowToChar = null,
  craftOutputToChar = null,
  syncAuthorityChar = null,
  updateChar,
  logger = console,
}) => {
  if (!charId || !char || typeof updateChar !== 'function') return null;

  const tableItemId = recipeRes?.__craftingTableItemId || null;
  const cleanRecipeRes = stripCraftingTableTransferMetadata(recipeRes);

  if (tableItemId && tableRowToChar) {
    try {
      const result = await tableRowToChar({
        charId,
        tableItemId,
        quantity: qty,
        fallbackResultJson: serializeCraftingBridgePayload(cleanRecipeRes),
      });
      if (result !== null && result !== undefined) {
        const nextState = applyCraftingTableRowToCharInventory(char, {
          tableItemId,
          quantity: qty,
          fallbackResult: cleanRecipeRes,
          entryIdFactory: makeSingleValueFactory(result?.itemEntryId),
          abilityIdFactory: makeAbilityIdFactoryForEntry(result?.itemEntryId),
        });
        syncAuthorityCharSnapshot(syncAuthorityChar, charId, nextState);
        return { mode: 'authority', result, nextState };
      }
      throw new Error('Authoritative table-to-inventory route returned no result');
    } catch (e) {
      logger?.error?.('[crafting bridge] authoritative table-to-inventory failed; local fallback blocked', e);
      throw e;
    }
  } else if (!tableItemId && craftOutputToChar && updatedTableInv !== undefined && char.craftingTable) {
    try {
      const consumedIngredients = buildConsumedIngredientsFromTableInv(char.craftingTable?.inv || {}, updatedTableInv);
      const result = await craftOutputToChar({
        charId,
        recipeResultJson: serializeCraftingBridgePayload(cleanRecipeRes),
        quantity: qty,
        consumedIngredients,
      });
      if (result !== null && result !== undefined) {
        const nextState = applyCraftOutputToCharInventory(char, {
          result: cleanRecipeRes,
          quantity: qty,
          consumedIngredients,
          entryIdFactory: makeSingleValueFactory(result?.itemEntryId),
          abilityIdFactory: makeAbilityIdFactoryForEntry(result?.itemEntryId),
        });
        syncAuthorityCharSnapshot(syncAuthorityChar, charId, nextState);
        return { mode: 'authority', result, nextState };
      }
      throw new Error('Authoritative craft-output-to-inventory route returned no result');
    } catch (e) {
      logger?.error?.('[crafting bridge] authoritative craft-to-inventory failed; local fallback blocked', e);
      throw e;
    }
  }

  const item = craftResultToInventoryItem(cleanRecipeRes, qty);

  if (!item) {
    logger?.warn?.('[crafting bridge] craftResultToInventoryItem returned null for', cleanRecipeRes);
    return null;
  }

  const newInventory = stackIntoInventory(char.inventory || [], item);
  const patch = { inventory: newInventory };

  if (updatedTableInv !== undefined) {
    patch.craftingTable = {
      inv: updatedTableInv,
      customItems: (char.craftingTable?.customItems) || [],
    };
  }

  logger?.log?.('[crafting bridge] adding to inventory', {
    charId,
    addedItem: item.name,
    qty: item.quantity,
    invBefore: (char.inventory || []).length,
    invAfter: newInventory.length,
    patchKeys: Object.keys(patch),
  });

  await awaitMaybePromise(updateChar(charId, patch));
  return { mode: 'fallback', item, patch };
};

export const addCraftResultToPartyBridge = async ({
  charId,
  char,
  recipeRes,
  qty,
  updatedTableInv,
  partyInventory,
  tableRowToParty = null,
  craftOutputToParty = null,
  syncAuthorityChar = null,
  syncAuthorityPartyInventory = null,
  savePartyInventory,
  updateChar,
  logger = console,
}) => {
  const hasPartyFallback = typeof savePartyInventory === 'function';
  const hasPartyAuthority = typeof tableRowToParty === 'function' || typeof craftOutputToParty === 'function';
  if (!charId || !char || (!hasPartyFallback && !hasPartyAuthority)) return null;

  const tableItemId = recipeRes?.__craftingTableItemId || null;
  const cleanRecipeRes = stripCraftingTableTransferMetadata(recipeRes);

  if (tableItemId && tableRowToParty) {
    try {
      const result = await tableRowToParty({
        charId,
        tableItemId,
        quantity: qty,
        fallbackResultJson: serializeCraftingBridgePayload(cleanRecipeRes),
      });
      if (result !== null && result !== undefined) {
        const nextState = applyCraftingTableRowToPartyPool(char, partyInventory || { items: [] }, {
          tableItemId,
          quantity: qty,
          fallbackResult: cleanRecipeRes,
          partyEntryIdFactory: makeSingleValueFactory(result?.partyItemEntryId),
          partyAbilityIdFactory: makeAbilityIdFactoryForEntry(result?.partyItemEntryId),
          craftedEntryIdFactory: makeSingleValueFactory(result?.partyItemEntryId ? `${result.partyItemEntryId}:crafted` : null),
          craftedAbilityIdFactory: makeAbilityIdFactoryForEntry(result?.partyItemEntryId ? `${result.partyItemEntryId}:crafted` : null),
        });
        syncAuthorityCharSnapshot(syncAuthorityChar, charId, nextState);
        syncAuthorityPartySnapshot(syncAuthorityPartyInventory, nextState);
        return { mode: 'authority', result, nextState };
      }
      throw new Error('Authoritative table-to-party route returned no result');
    } catch (e) {
      logger?.error?.('[crafting bridge] authoritative table-to-party failed; local fallback blocked', e);
      throw e;
    }
  } else if (!tableItemId && craftOutputToParty && updatedTableInv !== undefined && char.craftingTable) {
    try {
      const consumedIngredients = buildConsumedIngredientsFromTableInv(char.craftingTable?.inv || {}, updatedTableInv);
      const result = await craftOutputToParty({
        charId,
        recipeResultJson: serializeCraftingBridgePayload(cleanRecipeRes),
        quantity: qty,
        consumedIngredients,
      });
      if (result !== null && result !== undefined) {
        const nextState = applyCraftOutputToPartyPool(char, partyInventory || { items: [] }, {
          result: cleanRecipeRes,
          quantity: qty,
          consumedIngredients,
          craftedEntryIdFactory: makeSingleValueFactory(result?.partyItemEntryId ? `${result.partyItemEntryId}:crafted` : null),
          craftedAbilityIdFactory: makeAbilityIdFactoryForEntry(result?.partyItemEntryId ? `${result.partyItemEntryId}:crafted` : null),
          partyEntryIdFactory: makeSingleValueFactory(result?.partyItemEntryId),
          partyAbilityIdFactory: makeAbilityIdFactoryForEntry(result?.partyItemEntryId),
        });
        syncAuthorityCharSnapshot(syncAuthorityChar, charId, nextState);
        syncAuthorityPartySnapshot(syncAuthorityPartyInventory, nextState);
        return { mode: 'authority', result, nextState };
      }
      throw new Error('Authoritative craft-output-to-party route returned no result');
    } catch (e) {
      logger?.error?.('[crafting bridge] authoritative craft-to-party failed; local fallback blocked', e);
      throw e;
    }
  }

  const item = craftResultToInventoryItem(cleanRecipeRes, qty);

  if (!item) {
    logger?.warn?.('[crafting bridge] craftResultToInventoryItem returned null for party deposit', cleanRecipeRes);
    return null;
  }

  const currentPool = partyInventory?.items || [];
  const nextPool = applyCraftResultTransferToPartyPool(currentPool, cleanRecipeRes, {
    quantity: qty,
  });

  logger?.log?.('[crafting bridge] table-to-party', {
    itemName: item.name,
    itemId: item.id,
    qty: item.quantity,
    poolBefore: currentPool.length,
    poolAfter: nextPool.length,
    partyInventoryDefined: partyInventory !== undefined,
  });

  if (!hasPartyFallback) {
    logger?.error?.('[crafting bridge] party whole-blob fallback unavailable; local fallback blocked');
    throw new Error('party inventory fallback unavailable');
  }

  await awaitMaybePromise(savePartyInventory({ ...(partyInventory || {}), items: nextPool }));

  if (updatedTableInv !== undefined && typeof updateChar === 'function') {
    await awaitMaybePromise(updateChar(charId, {
      craftingTable: {
        inv: updatedTableInv,
        customItems: (char.craftingTable?.customItems) || [],
      },
    }));
  }

  return { mode: 'fallback', item, nextPool };
};



// ─── HELPERS ─────────────────────────────────────────────

const nm = (n) => n.trim().toLowerCase().replace(/\s+/g, " ");

const mkid = (n, items) => {

  let id = n.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/_+$/, "");

  let c = 1;

  while (items.find((i) => i.id === id)) { id = n.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_") + "_" + c; c++; }

  return id;

};

const autoCase = (s) => s.trim().replace(/([+-]?\d+\w*)\s+(.+)/, (m, num, rest) => num + " " + rest.split(/\s+/).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ")).replace(/^([a-z])/, (m) => m.toUpperCase());



const deepClone = (o) => JSON.parse(JSON.stringify(o));



// ─── CSS ─────────────────────────────────────────────────



// ─── TOAST COMPONENT ─────────────────────────────────────

export const getToastTone = (message) => {
  const text = String(message || '').toLowerCase();
  return /\b(failed|failure|error|invalid|required|unavailable|blocked|missing|cannot|can't)\b/.test(text)
    ? 'error'
    : 'success';
};

const getMovementAuthorityFailureMessage = (error, fallbackMessage) => {
  const rawMessage = String(error?.message || error || '').trim();
  if (!rawMessage) return fallbackMessage;

  if (/not authorized/i.test(rawMessage)) {
    return `${fallbackMessage} Your sign-in is not linked to this character yet.`;
  }

  return `${fallbackMessage} ${rawMessage}`;
};

function Toast({ message }) {

  if (!message) return null;

  const tone = getToastTone(message);
  const toneStyle = tone === 'error'
    ? {
        background: "var(--arb)",
        border: "1px solid var(--ar)",
        color: "var(--tr)",
        boxShadow: "0 0 18px rgba(212,64,64,.35)",
      }
    : {
        background: "var(--agb)",
        border: "1px solid var(--ag)",
        color: "var(--tgrb)",
        boxShadow: "var(--gn)",
      };

  return (

    <div style={{

      position:"fixed",bottom:30,left:"50%",transform:"translateX(-50%)",

      background:toneStyle.background,border:toneStyle.border,color:toneStyle.color,

      padding:"12px 24px",borderRadius:"var(--rm)",fontFamily:"'Alegreya',serif",

      fontSize:15,fontWeight:600,display:"flex",alignItems:"center",gap:8,

      boxShadow:toneStyle.boxShadow,zIndex:1000,animation:"fi .4s ease",

    }}>{message}</div>

  );

}



// ─── ICON PICKER ─────────────────────────────────────────

function IconPicker({ current, onSelect, onClose, favIcons, setFavIcons }) {

  const [search, setSearch] = useState("");

  const [showAll, setShowAll] = useState(false);

  const [custom, setCustom] = useState("");

  const mob = useIsMobile();



  const icons = showAll

    ? Object.fromEntries(Object.entries(ICONS_BASE).map(([k, v]) => [k, [...new Set([...v, ...(ICONS_EXT[k] || [])])]]))

    : ICONS_BASE;



  return (

    <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{

      position:"fixed",inset:0,zIndex:500,display:"flex",alignItems:"center",

      justifyContent:"center",background:"rgba(0,0,0,.7)",animation:"fi .2s",

    }}>

      <div style={{

        background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"var(--rl)",

        width:mob?"90%":380,maxWidth:380,maxHeight:mob?"70vh":460,display:"flex",flexDirection:"column",overflow:"hidden",

      }}>

        <div style={{padding:mob?"10px 14px":"14px 18px",borderBottom:"1px solid var(--bs)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>

          <span style={{fontFamily:"'Cinzel',serif",fontSize:mob?13:15,color:"var(--tg)"}}>🎨 Choose Icon</span>

          <button onClick={onClose} style={{width:28,height:28,borderRadius:"50%",border:"1px solid var(--ar)",background:"var(--arb)",color:"var(--tr)",fontSize:14,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>

        </div>

        <div style={{padding:mob?"8px 14px":"10px 18px",borderBottom:"1px solid var(--bs)"}}>

          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search category..."

            style={{width:"100%",background:"var(--bgi)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--ts)",padding:"8px 10px",fontSize:13,outline:0}} />

        </div>

        <div style={{display:"grid",gridTemplateColumns:mob?"repeat(6,1fr)":"repeat(8,1fr)",gap:4,padding:mob?"10px 14px":"14px 18px",overflowY:"auto",flex:1}}>

          {favIcons.length > 0 && !search && (

            <>

              <div style={{gridColumn:"1/-1",fontSize:11,color:"var(--tgb)",padding:"6px 0 2px",fontFamily:"Cinzel,serif"}}>⭐ Favorites</div>

              {favIcons.map((ic) => (

                <button key={"fav-"+ic} onClick={() => { onSelect(ic); onClose(); }}

                  onContextMenu={(e) => { e.preventDefault(); setFavIcons(favIcons.filter(f => f !== ic)); }}

                  style={{width:38,height:38,borderRadius:"var(--rs)",border:"1px solid var(--bgb)",background:"rgba(201,149,26,.08)",fontSize:22,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}

                  title="Right-click to unfavorite">{isGameIcon(ic) ? renderGameIcon(ic, 22, 'var(--tp)') : ic}</button>

              ))}

            </>

          )}

          {Object.entries(icons).filter(([c]) => !search || c.toLowerCase().includes(search.toLowerCase())).map(([cat, ics]) => (

            <React.Fragment key={cat}>

              <div style={{gridColumn:"1/-1",fontSize:11,color:"var(--ts)",padding:"6px 0 2px",fontFamily:"Cinzel,serif"}}>{cat}</div>

              {ics.map((ic, i) => {

                const isFav = favIcons.includes(ic);

                return (

                  <button key={cat+"-"+i} onClick={() => { onSelect(ic); onClose(); }}

                    onContextMenu={(e) => {

                      e.preventDefault();

                      if (isFav) setFavIcons(favIcons.filter(f => f !== ic));

                      else setFavIcons([...favIcons, ic]);

                    }}

                    style={{width:38,height:38,borderRadius:"var(--rs)",border:isFav?"1px solid var(--bgb)":"1px solid transparent",

                      background:isFav?"rgba(201,149,26,.08)":"none",fontSize:22,cursor:"pointer",

                      display:"flex",alignItems:"center",justifyContent:"center",position:"relative"}}

                    title={isFav?"Right-click to unfavorite":"Right-click to favorite"}>{isGameIcon(ic) ? renderGameIcon(ic, 22, 'var(--tp)') : ic}</button>

                );

              })}

            </React.Fragment>

          ))}

        </div>

        <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 18px"}}>

          <label style={{fontSize:12,color:"var(--ts)",display:"flex",alignItems:"center",gap:5,cursor:"pointer"}}>

            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} style={{accentColor:"var(--ap)"}} /> Show all icons

          </label>

        </div>

        <div style={{padding:"10px 18px",borderTop:"1px solid var(--bs)",display:"flex",alignItems:"center",gap:8}}>

          <input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Or paste emoji..." maxLength={4}

            style={{flex:1,background:"var(--bgi)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"6px 10px",fontSize:18,outline:0,textAlign:"center"}} />

          <button onClick={() => { if (custom.trim()) { onSelect(custom.trim()); onClose(); } }}

            style={{padding:"6px 14px",borderRadius:"var(--rs)",border:"none",background:"var(--ap)",color:"#fff",fontSize:13,cursor:"pointer",fontWeight:600}}>Use</button>

        </div>

      </div>

    </div>

  );

}



// ─── AUTOCOMPLETE COMPONENT ──────────────────────────────

function AutocompleteInput({ value, onChange, onSelect, onCreate, items, placeholder, style: extraStyle }) {

  const [show, setShow] = useState(false);

  const [q, setQ] = useState(value || "");

  const wrapRef = useRef(null);



  useEffect(() => { setQ(value || ""); }, [value]);



  const matches = useMemo(() => {

    if (!q.trim()) return [];

    const qn = nm(q);

    return items.filter((i) => nm(i.name).includes(qn)).slice(0, 10);

  }, [q, items]);



  const exact = matches.some((i) => nm(i.name) === nm(q));



  return (

    <div ref={wrapRef} style={{position:"relative",flex:1,...(extraStyle||{})}}>

      <input value={q} placeholder={placeholder}

        onChange={(e) => { setQ(e.target.value); onChange?.(e.target.value); setShow(true); }}

        onFocus={() => { if (q.trim()) setShow(true); }}

        onBlur={() => setTimeout(() => setShow(false), 200)}

        style={{width:"100%",background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"8px 12px",fontSize:14,outline:0}} />

      {show && (matches.length > 0 || (!exact && q.length > 0)) && (

        <div style={{position:"absolute",top:"100%",left:0,right:0,zIndex:50,background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"0 0 var(--rm) var(--rm)",maxHeight:180,overflowY:"auto",boxShadow:"0 8px 24px rgba(0,0,0,.5)"}}>

          {matches.map((it) => (

            <div key={it.id} onMouseDown={(e) => { e.preventDefault(); setQ(it.name); setShow(false); onSelect?.(it); }}

              style={{padding:"8px 12px",cursor:"pointer",display:"flex",alignItems:"center",gap:8,fontSize:13,transition:"background .1s"}}

              onMouseEnter={(e) => e.currentTarget.style.background = "var(--bg2h)"}

              onMouseLeave={(e) => e.currentTarget.style.background = ""}>

              <span>{isGameIcon(it.icon) ? renderGameIcon(it.icon, 16, 'var(--tp)') : it.icon}</span><span style={{flex:1}}>{it.name}</span><span style={{color:"var(--tm)",fontSize:11}}>{it.cat}</span>

            </div>

          ))}

          {!exact && q.length > 0 && (

            <div onMouseDown={(e) => {

              e.preventDefault();

              const cap = q.split(/\s+/).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");

              setQ(cap); setShow(false); onCreate?.(cap);

            }}

              style={{padding:"8px 12px",cursor:"pointer",display:"flex",alignItems:"center",gap:8,fontSize:13,color:"var(--tpu)",borderTop:"1px solid var(--bs)"}}

              onMouseEnter={(e) => e.currentTarget.style.background = "var(--bg2h)"}

              onMouseLeave={(e) => e.currentTarget.style.background = ""}>

              <span>✨</span><span>Create "<strong>{q.split(/\s+/).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ")}</strong>"</span>

            </div>

          )}

        </div>

      )}

    </div>

  );

}



// ─── TAG INPUT ───────────────────────────────────────────

function TagInput({ tags, allTags, onAdd, onRemove, label, color }) {

  const [val, setVal] = useState("");

  const [show, setShow] = useState(false);

  const matches = useMemo(() => {

    if (!val.trim()) return [];

    return allTags.filter((t) => t.includes(val.toLowerCase())).slice(0, 8);

  }, [val, allTags]);



  const doAdd = () => {

    const v = val.trim().toLowerCase();

    if (v && !tags.includes(v)) { onAdd(v); setVal(""); }

  };



  return (

    <div>

      {label && <label style={{fontFamily:"'Cinzel',serif",fontSize:13,color:color||"var(--tpu)",display:"block",marginBottom:4}}>🏷️ {label}</label>}

      <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:6}}>

        {tags.map((t, i) => (

          <span key={t} style={{padding:"2px 8px",borderRadius:12,fontSize:11,background:"rgba(179,136,232,.12)",border:"1px solid rgba(179,136,232,.3)",color:"var(--tpu)",display:"flex",alignItems:"center",gap:4}}>

            {t} <span onClick={() => onRemove(i)} style={{cursor:"pointer",opacity:.6,fontSize:9}}>✕</span>

          </span>

        ))}

      </div>

      <div style={{display:"flex",gap:6,alignItems:"center",position:"relative"}}>

        <input value={val} onChange={(e) => { setVal(e.target.value); setShow(true); }}

          onFocus={() => { if (val.trim()) setShow(true); }}

          onBlur={() => setTimeout(() => setShow(false), 200)}

          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); doAdd(); } }}

          placeholder="Add tag..." style={{flex:1,background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"4px 8px",fontSize:12,outline:0}} />

        <button onClick={doAdd} style={{padding:"5px 10px",borderRadius:"var(--rs)",border:"1px solid var(--ag)",background:"var(--agb)",color:"var(--tgr)",fontSize:12,cursor:"pointer",fontWeight:700,whiteSpace:"nowrap"}}>+ Tag</button>

        {show && matches.length > 0 && (

          <div style={{position:"absolute",top:"100%",left:0,right:60,zIndex:50,background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"0 0 var(--rm) var(--rm)",maxHeight:140,overflowY:"auto",boxShadow:"0 8px 24px rgba(0,0,0,.5)"}}>

            {matches.map((t) => (

              <div key={t} onMouseDown={(e) => { e.preventDefault(); if (!tags.includes(t)) { onAdd(t); } setVal(""); setShow(false); }}

                style={{padding:"6px 12px",cursor:"pointer",fontSize:12}}

                onMouseEnter={(e) => e.currentTarget.style.background = "var(--bg2h)"}

                onMouseLeave={(e) => e.currentTarget.style.background = ""}>{t}</div>

            ))}

          </div>

        )}

      </div>

    </div>

  );

}



// ─── EFFECT CHIPS ────────────────────────────────────────

function EffectChips({ items: effs, onRemove, type }) {

  const colors = { pos: { bg: "var(--agb)", color: "var(--tgr)", border: "var(--ag)" }, neg: { bg: "var(--arb)", color: "var(--tr)", border: "var(--ar)" }, use: { bg: "rgba(58,107,154,.12)", color: "#4a9ad4", border: "rgba(58,107,154,.4)" } };

  const c = colors[type] || colors.pos;

  return (

    <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:8}}>

      {effs.map((b, i) => (

        <span key={i} style={{display:"flex",alignItems:"center",gap:4,padding:"3px 8px",borderRadius:3,fontSize:11,fontWeight:600,border:`1px solid ${c.border}`,background:c.bg,color:c.color}}>

          {type === "use" && "⚡ "}{b} <span onClick={() => onRemove(i)} style={{cursor:"pointer",opacity:.6,fontSize:10,marginLeft:2}}>✕</span>

        </span>

      ))}

    </div>

  );

}



function EffectAdder({ onAdd, placeholder, type }) {

  const [val, setVal] = useState("");

  const doAdd = () => {

    if (val.trim()) {

      const effs = val.split(",").map((s) => autoCase(s.trim())).filter(Boolean);

      effs.forEach((e) => onAdd(e));

      setVal("");

    }

  };

  const colors = { pos: { border: "var(--ag)", bg: "var(--agb)", color: "var(--tgr)" }, neg: { border: "var(--ar)", bg: "var(--arb)", color: "var(--tr)" }, use: { border: "rgba(58,107,154,.4)", bg: "rgba(58,107,154,.12)", color: "#4a9ad4" } };

  const c = colors[type] || colors.pos;

  return (

    <div style={{display:"flex",gap:6,alignItems:"center"}}>

      <input value={val} onChange={(e) => setVal(e.target.value)} placeholder={placeholder}

        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); doAdd(); } }}

        style={{flex:1,background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"5px 8px",fontSize:12,outline:0}} />

      <button onClick={doAdd} style={{padding:"5px 10px",borderRadius:"var(--rs)",border:`1px solid ${c.border}`,background:c.bg,color:c.color,fontSize:12,cursor:"pointer",fontWeight:700,whiteSpace:"nowrap"}}>+ Add</button>

    </div>

  );

}



// ─── ABILITY EDITOR ──────────────────────────────────────

// 2026-04-27: ported to inventory ability shape so the recipe editor

// matches what the inventory item editor produces. Was {desc, effects:

// "string", limited, cur, max, reset}; now {description, effects: [...],

// uses: { current, max, resetType }}. The bridge (craftResultToInventoryItem)

// still accepts both shapes for back-compat.

//

// One remaining gap: recipe abilities don't yet have stable `id`s. The

// bridge generates one on first transfer to inventory, so it's harmless

// for crafted items, but DM-edited abilities don't get an id until then.

// Fine for now; logged in CLAUDE-NOTES-GENERAL as part of the shared-

// editor extraction (path 2).

function AbilityEditor({ ability, onChange, onRemove, index }) {

  const update = (k, v) => onChange({ ...ability, [k]: v });

  const mob = useIsMobile();

  // Effects: round-trip array <-> comma-separated string. Keeps the

  // recipe editor's input shape compatible with users who type effects

  // inline ("+1d6 Fire, +1 Atk") while storing as an array (what the

  // inventory renderer wants).

  const effectsArr = Array.isArray(ability.effects)

    ? ability.effects

    : (typeof ability.effects === 'string' && ability.effects.trim())

      ? ability.effects.split(',').map(s => s.trim()).filter(Boolean)

      : [];

  const hasUses = !!ability.uses;

  return (

    <div style={{background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",padding:mob?10:14,marginBottom:10,position:"relative"}}>

      <div style={{fontFamily:"'Cinzel',serif",fontSize:12,color:"var(--tpu)",marginBottom:8}}>Ability #{index + 1}</div>

      <button onClick={onRemove} style={{position:"absolute",top:10,right:10,width:24,height:24,borderRadius:"50%",border:"1px solid var(--ar)",background:"var(--arb)",color:"var(--tr)",fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>

      <div style={{marginBottom:10}}>

        <input value={ability.name} onChange={(e) => update("name", e.target.value)} placeholder="Ability name..."

          style={{width:"100%",background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"8px 12px",fontSize:mob?13:14,fontWeight:700,outline:0}} />

      </div>

      <div style={{marginBottom:10}}>

        <textarea

          value={ability.description ?? ability.desc ?? ''}

          onChange={(e) => update("description", e.target.value)}

          placeholder="Description..."

          style={{width:"100%",background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"8px 12px",fontSize:mob?13:14,outline:0,resize:"vertical",minHeight:50}} />

      </div>

      <div style={{marginBottom:10}}>

        <input

          value={effectsArr.join(', ')}

          onChange={(e) => {

            const next = e.target.value.split(',').map(s => s.trim()).filter(Boolean);

            update('effects', next);

          }}

          placeholder="Effects: +1d6 Fire, +2d6 Cold (comma-separated)"

          style={{width:"100%",background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"8px 12px",fontSize:mob?13:14,outline:0}} />

        <div style={{fontSize:10,color:"var(--tm)",marginTop:4,opacity:0.7}}>Dice notation (NdN+N) is rolled and surfaced via a roll receipt when the ability is used.</div>

      </div>

      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>

        <input

          type="checkbox"

          checked={hasUses}

          onChange={(e) => {

            if (e.target.checked) {

              update('uses', { current: 1, max: 1, resetType: 'shortRest' });

            } else {

              update('uses', null);

            }

          }}

          style={{accentColor:"var(--ap)"}} />

        <span style={{fontSize:13}}>Has limited uses</span>

      </div>

      {hasUses && (

        <div style={{display:"grid",gridTemplateColumns:mob?"1fr":"1fr 1fr 1fr",gap:8}}>

          <div><label style={{fontSize:11,color:"var(--ts)",display:"block",marginBottom:4}}>Current</label>

            <input type="number" value={ability.uses.current ?? 0}

              onChange={(e) => update("uses", { ...ability.uses, current: parseInt(e.target.value) || 0 })}

              min="0"

              style={{width:"100%",background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"8px 12px",fontSize:14,outline:0}} /></div>

          <div><label style={{fontSize:11,color:"var(--ts)",display:"block",marginBottom:4}}>Max</label>

            <input type="number" value={ability.uses.max ?? 1}

              onChange={(e) => {

                const max = parseInt(e.target.value) || 1;

                update("uses", { ...ability.uses, max, current: Math.min(ability.uses.current ?? max, max) });

              }}

              min="1"

              style={{width:"100%",background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"8px 12px",fontSize:14,outline:0}} /></div>

          <div><label style={{fontSize:11,color:"var(--ts)",display:"block",marginBottom:4}}>Reset</label>

            <select value={ability.uses.resetType || 'none'}

              onChange={(e) => update("uses", { ...ability.uses, resetType: e.target.value })}

              style={{width:"100%",background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"8px 12px",fontSize:14,outline:0,cursor:"pointer"}}>

              {Object.entries(USES_RESET_TYPES).map(([key, rt]) => (

                <option key={key} value={key}>{rt.name}</option>

              ))}

            </select></div>

        </div>

      )}

    </div>

  );

}



// ─── RECIPE EDITOR ───────────────────────────────────────

function RecipeEditor({ editState, onSave, onCancel, onDelete, items, allItems, categories, allTags, setAllTags, favIcons, setFavIcons, isNew, mob }) {

  const [ed, setEd] = useState(deepClone(editState));

  const [iconPicker, setIconPicker] = useState(null); // 'recipe' | 'result'



  const cats = categories.filter((c) => c.id !== "all");

  const updateEd = (path, val) => {

    const next = deepClone(ed);

    const keys = path.split(".");

    let obj = next;

    for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];

    obj[keys[keys.length - 1]] = val;

    setEd(next);

  };



  const updateIng = (idx, field, val) => {

    const next = deepClone(ed);

    next.ings[idx][field] = val;

    setEd(next);

  };



  return (

    <div className="fade" style={{background:"var(--bgi)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",padding:mob?12:18}}>

      <div style={{fontFamily:"'Cinzel',serif",fontSize:mob?13:15,color:"var(--tg)",marginBottom:mob?10:14,display:"flex",alignItems:"center",gap:8}}>

        {isNew ? "✨ New Recipe" : "✏️ Edit: " + ed.name}

      </div>



      {/* Name & Icon */}

      <div style={{display:"flex",gap:mob?8:12,alignItems:"flex-start",marginBottom:mob?10:14,flexWrap:mob?"wrap":"nowrap"}}>

        <div>

          <label style={{fontFamily:"'Cinzel',serif",fontSize:12,color:"var(--to)",display:"block",marginBottom:4}}>Icon</label>

          <button onClick={() => setIconPicker("recipe")} style={{width:mob?40:48,height:mob?40:48,borderRadius:"var(--rm)",border:"1px solid var(--bs)",background:"var(--bg1)",fontSize:mob?22:26,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",position:"relative"}}>

            {isGameIcon(ed.icon) ? renderGameIcon(ed.icon, 26, 'var(--tp)') : ed.icon}<span style={{position:"absolute",bottom:-2,right:-2,background:"var(--ap)",borderRadius:"50%",width:16,height:16,fontSize:9,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff"}}>✎</span>

          </button>

        </div>

        <div style={{flex:1,minWidth:mob?120:undefined}}>

          <label style={{fontFamily:"'Cinzel',serif",fontSize:12,color:"var(--to)",display:"block",marginBottom:4}}>Recipe Name</label>

          <input value={ed.name} onChange={(e) => updateEd("name", e.target.value)} placeholder="e.g., Flamebrand Sword"

            style={{width:"100%",background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"8px 12px",fontSize:mob?13:14,outline:0}} />

        </div>

        <div style={{flex:mob?"1 1 100%":"0 0 130px"}}>

          <label style={{fontFamily:"'Cinzel',serif",fontSize:12,color:"var(--to)",display:"block",marginBottom:4}}>Category</label>

          <select value={ed.cat} onChange={(e) => updateEd("cat", e.target.value)}

            style={{width:"100%",background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"8px 12px",fontSize:mob?13:14,outline:0,cursor:"pointer"}}>

            {cats.map((c) => <option key={c.id} value={c.id}>{c.l}</option>)}

          </select>

        </div>

      </div>



      {/* Description */}

      <div style={{marginBottom:14}}>

        <label style={{fontFamily:"'Cinzel',serif",fontSize:12,color:"var(--to)",display:"block",marginBottom:4}}>Description</label>

        <input value={ed.desc} onChange={(e) => updateEd("desc", e.target.value)} placeholder="A short description..."

          style={{width:"100%",background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"8px 12px",fontSize:14,outline:0}} />

      </div>



      {/* Recipe Tags */}

      <div style={{marginBottom:14}}>

        <TagInput tags={ed.tags} allTags={allTags} label="Recipe Tags"

          onAdd={(t) => { const next = deepClone(ed); next.tags.push(t); setEd(next); if (!allTags.includes(t)) setAllTags([...allTags, t]); }}

          onRemove={(i) => { const next = deepClone(ed); next.tags.splice(i, 1); setEd(next); }} />

      </div>



      {/* Ingredients */}

      <div style={{marginBottom:14}}>

        <label style={{fontFamily:"'Cinzel',serif",fontSize:12,color:"var(--to)",display:"block",marginBottom:8}}>🧾 Ingredients</label>

        {ed.ings.map((ing, i) => {

          const it = allItems.find((x) => x.id === ing.iid);

          return (

            <div key={i} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>

              <AutocompleteInput value={it ? it.name : ing._n || ""} items={allItems} placeholder="Type item name..."

                onChange={(v) => {

                  const next = deepClone(ed);

                  next.ings[i]._n = v;

                  const found = allItems.find((x) => nm(x.name) === nm(v));

                  next.ings[i].iid = found ? found.id : "";

                  setEd(next);

                }}

                onSelect={(it) => updateIng(i, "iid", it.id)}

                onCreate={(name) => {

                  const id = mkid(name, allItems);

                  const newIt = { id, name: name.trim(), icon: "📦", cat: "materials" };

                  allItems.push(newIt);

                  updateIng(i, "iid", id);

                }} />

              <input type="number" value={ing.qty} onChange={(e) => updateIng(i, "qty", parseInt(e.target.value) || 1)}

                min="1" max="99" style={{width:60,textAlign:"center",background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"8px 12px",fontSize:14,outline:0,flexShrink:0}} />

              <button onClick={() => { if (ed.ings.length <= 1) return; const next = deepClone(ed); next.ings.splice(i, 1); setEd(next); }}

                disabled={ed.ings.length <= 1}

                style={{width:28,height:28,borderRadius:"50%",border:"1px solid var(--ar)",background:"var(--arb)",color:"var(--tr)",fontSize:14,cursor:ed.ings.length <= 1?"not-allowed":"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,opacity:ed.ings.length<=1?.3:1}}>×</button>

            </div>

          );

        })}

        <button onClick={() => { const next = deepClone(ed); next.ings.push({ iid: "", _n: "", qty: 1 }); setEd(next); }}

          style={{padding:"6px 14px",borderRadius:"var(--rs)",border:"1px dashed var(--bs)",background:"none",color:"var(--ts)",fontSize:12,cursor:"pointer",marginTop:8}}>+ Add Ingredient</button>

      </div>



      {/* Result Item */}

      <div style={{borderTop:"1px solid var(--bs)",paddingTop:14,marginBottom:14}}>

        <label style={{fontFamily:"'Cinzel',serif",fontSize:12,color:"var(--to)",display:"block",marginBottom:8}}>🎁 Result Item</label>

        <div style={{display:"flex",gap:mob?8:12,alignItems:"flex-start",flexWrap:mob?"wrap":"nowrap"}}>

          <div>

            <label style={{fontSize:11,color:"var(--ts)",display:"block",marginBottom:4}}>Icon</label>

            <button onClick={() => setIconPicker("result")} style={{width:mob?36:40,height:mob?36:40,borderRadius:"var(--rm)",border:"1px solid var(--bs)",background:"var(--bg1)",fontSize:mob?18:22,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",position:"relative"}}>

              {isGameIcon(ed.res.icon) ? renderGameIcon(ed.res.icon, 20, 'var(--tp)') : ed.res.icon}<span style={{position:"absolute",bottom:-2,right:-2,background:"var(--ap)",borderRadius:"50%",width:16,height:16,fontSize:9,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff"}}>✎</span>

            </button>

          </div>

          <div style={{flex:1,minWidth:mob?100:undefined}}>

            <label style={{fontSize:11,color:"var(--ts)",display:"block",marginBottom:4}}>Name</label>

            <input value={ed.res.name} onChange={(e) => updateEd("res.name", e.target.value)} placeholder="Crafted item name"

              style={{width:"100%",background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"8px 12px",fontSize:mob?13:14,outline:0}} />

          </div>

          <div style={{flex:mob?"1 1 45%":"0 0 100px"}}>

            <label style={{fontSize:11,color:"var(--ts)",display:"block",marginBottom:4}}>Category</label>

            <select value={ed.res.cat} onChange={(e) => updateEd("res.cat", e.target.value)}

              style={{width:"100%",background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"8px 12px",fontSize:mob?13:14,outline:0,cursor:"pointer"}}>

              {cats.map((c) => <option key={c.id} value={c.id}>{c.l}</option>)}

            </select>

          </div>

          <div style={{flex:mob?"1 1 45%":"0 0 100px"}}>

            <label style={{fontSize:11,color:"var(--ts)",display:"block",marginBottom:4}}>Rarity</label>

            <select value={ed.res.rar} onChange={(e) => updateEd("res.rar", e.target.value)}

              style={{width:"100%",background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"8px 12px",fontSize:mob?13:14,outline:0,cursor:"pointer"}}>

              {RARS.map((r) => <option key={r}>{r}</option>)}

            </select>

          </div>

        </div>

        <div style={{marginTop:8,display:"flex",gap:mob?8:12,flexWrap:mob?"wrap":"nowrap"}}>

          <div style={{flex:1,minWidth:mob?"100%":undefined}}>

            <label style={{fontSize:11,color:"var(--ts)",display:"block",marginBottom:4}}>Description</label>

            <input value={ed.res.desc} onChange={(e) => updateEd("res.desc", e.target.value)} placeholder="Item properties..."

              style={{width:"100%",background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"8px 12px",fontSize:mob?13:14,outline:0}} />

          </div>

          <div style={{flex:mob?"1 1 100%":"0 0 80px"}}>

            <label style={{fontSize:11,color:"var(--ts)",display:"block",marginBottom:4}}>Price (pp)</label>

            <input type="number" value={ed.res.price} onChange={(e) => updateEd("res.price", parseInt(e.target.value) || 0)} min="0"

              style={{width:"100%",background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"8px 12px",fontSize:mob?13:14,outline:0}} />

          </div>

        </div>

      </div>



      {/* Uses */}

      <div style={{background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",padding:12,marginBottom:14}}>

        <div style={{fontFamily:"'Cinzel',serif",fontSize:12,color:"var(--tgr)",marginBottom:8,display:"flex",alignItems:"center",justifyContent:"space-between"}}>

          <span>Uses Configuration</span>

          {ed.res.uses ? (

            <button onClick={() => updateEd("res.uses", null)}

              style={{padding:"4px 10px",borderRadius:"var(--rs)",border:"1px dashed var(--ar)",background:"none",color:"var(--tr)",fontSize:11,cursor:"pointer"}}>Remove Uses</button>

          ) : (

            <button onClick={() => updateEd("res.uses", { cur: 1, max: 1, reset: "None" })}

              style={{padding:"4px 10px",borderRadius:"var(--rs)",border:"1px solid var(--ag)",background:"var(--agb)",color:"var(--tgr)",fontSize:11,cursor:"pointer"}}>+ Enable Uses</button>

          )}

        </div>

        {ed.res.uses ? (

          <div style={{display:"grid",gridTemplateColumns:mob?"1fr":"1fr 1fr 1fr",gap:8}}>

            <div><label style={{fontSize:11,color:"var(--ts)",display:"block",marginBottom:4}}>Current</label>

              <input type="number" value={ed.res.uses.cur} onChange={(e) => { const next = deepClone(ed); next.res.uses.cur = parseInt(e.target.value) || 0; setEd(next); }} min="0"

                style={{width:"100%",background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"8px 12px",fontSize:14,outline:0}} /></div>

            <div><label style={{fontSize:11,color:"var(--ts)",display:"block",marginBottom:4}}>Max Uses</label>

              <input type="number" value={ed.res.uses.max} onChange={(e) => { const next = deepClone(ed); next.res.uses.max = parseInt(e.target.value) || 1; setEd(next); }} min="1"

                style={{width:"100%",background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"8px 12px",fontSize:14,outline:0}} /></div>

            <div><label style={{fontSize:11,color:"var(--ts)",display:"block",marginBottom:4}}>Resets</label>

              <select value={ed.res.uses.reset} onChange={(e) => { const next = deepClone(ed); next.res.uses.reset = e.target.value; setEd(next); }}

                style={{width:"100%",background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"8px 12px",fontSize:14,outline:0,cursor:"pointer"}}>

                <option>None</option><option>Long Rest</option><option>Short Rest</option>

              </select></div>

          </div>

        ) : <div style={{fontSize:12,color:"var(--tm)"}}>Enable to track limited uses (e.g., consumables, scrolls).</div>}

      </div>



      {/* Buffs / Debuffs / On Use */}

      <div style={{marginBottom:14}}>

        <label style={{fontFamily:"'Cinzel',serif",fontSize:12,color:"var(--to)",display:"block",marginBottom:4}}>✨ Buffs</label>

        <EffectChips items={ed.res.buffs} type="pos" onRemove={(i) => { const next = deepClone(ed); next.res.buffs.splice(i, 1); setEd(next); }} />

        <EffectAdder type="pos" placeholder="+2 Str, +1 AC..." onAdd={(e) => { const next = deepClone(ed); next.res.buffs.push(e); setEd(next); }} />

      </div>

      <div style={{marginBottom:14}}>

        <label style={{fontFamily:"'Cinzel',serif",fontSize:12,color:"var(--ts)",display:"block",marginBottom:4}}>Debuffs</label>

        <EffectChips items={ed.res.debuffs} type="neg" onRemove={(i) => { const next = deepClone(ed); next.res.debuffs.splice(i, 1); setEd(next); }} />

        <EffectAdder type="neg" placeholder="-1 Dex, -5 Speed..." onAdd={(e) => { const next = deepClone(ed); next.res.debuffs.push(e); setEd(next); }} />

      </div>

      <div style={{marginBottom:14}}>

        <div style={{background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",padding:12}}>

          <label style={{fontFamily:"'Cinzel',serif",fontSize:12,color:"#4a9ad4",display:"block",marginBottom:4}}>⚡ On Use Effects</label>

          <EffectChips items={ed.res.onUse} type="use" onRemove={(i) => { const next = deepClone(ed); next.res.onUse.splice(i, 1); setEd(next); }} />

          <EffectAdder type="use" placeholder="+10 HP, +5 Mana, Cure Poison..." onAdd={(e) => { const next = deepClone(ed); next.res.onUse.push(e); setEd(next); }} />

          <div style={{fontSize:11,color:"var(--tm)",marginTop:6}}>Separate from buffs/debuffs. These trigger each time the item is used.</div>

        </div>

      </div>



      {/* Abilities */}

      <div style={{marginBottom:14}}>

        <label style={{fontFamily:"'Cinzel',serif",fontSize:12,color:"var(--tpu)",display:"block",marginBottom:4}}>⚡ Abilities</label>

        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>

          <span style={{fontSize:12,color:"var(--ts)"}}>Unique actions the item grants.</span>

          <button onClick={() => { const next = deepClone(ed); next.res.abilities.push({ name: "New Ability", description: "", effects: [], uses: null }); setEd(next); }}

            style={{padding:"6px 14px",borderRadius:"var(--rs)",border:"1px solid var(--bs)",background:"none",color:"var(--ts)",fontSize:12,cursor:"pointer"}}>+ Add Ability</button>

        </div>

        {ed.res.abilities.map((a, i) => (

          <AbilityEditor key={i} ability={a} index={i}

            onChange={(updated) => { const next = deepClone(ed); next.res.abilities[i] = updated; setEd(next); }}

            onRemove={() => { const next = deepClone(ed); next.res.abilities.splice(i, 1); setEd(next); }} />

        ))}

      </div>



      {/* Advanced Combat Integration — ported from inventory item editor.

          2026-04-27: gives DMs the same structured advantage/disadvantage/

          save-bonus/passive/resistance vocabulary at recipe-authoring time

          that they have when editing items by hand. Stored on

          ed.res.combatBonuses; the bridge already passes it through to the

          inventory item via the `_raw`/spread path. */}

      <div style={{marginBottom:14}}>

        <button

          onClick={() => { const next = deepClone(ed); next.res._showAdvanced = !next.res._showAdvanced; setEd(next); }}

          style={{padding:"6px 12px",borderRadius:"var(--rs)",border:"1px solid rgba(201,162,39,0.3)",background:"rgba(201,162,39,0.08)",color:"#c9a227",fontSize:12,cursor:"pointer",marginBottom:6}}>

          {ed.res._showAdvanced ? 'v' : '>'} Advanced Combat Integration

        </button>

        {ed.res._showAdvanced && (

          <div style={{padding:"10px",background:"rgba(201,162,39,0.08)",border:"1px solid rgba(201,162,39,0.2)",borderRadius:"var(--rm)"}}>

            <div style={{fontSize:11,color:"#c9a227",marginBottom:8}}>

              Active bonuses apply when item is equipped (or when consumed for consumables).

            </div>

            {Array.isArray(ed.res.combatBonuses) && ed.res.combatBonuses.length > 0 && (

              <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:8}}>

                {ed.res.combatBonuses.map((bonus, bIdx) => (

                  <span key={bIdx} style={{

                    padding:"2px 8px",borderRadius:10,fontSize:11,

                    background: bonus.type === 'advantage' ? 'rgba(39,201,90,0.15)' : bonus.type === 'disadvantage' ? 'rgba(231,76,60,0.15)' : 'rgba(201,162,39,0.15)',

                    color: bonus.type === 'advantage' ? '#6cff8a' : bonus.type === 'disadvantage' ? '#ff6b6b' : '#ffd700',

                    display:"inline-flex",alignItems:"center",gap:4,

                  }}>

                    {bonus.label}

                    <span

                      style={{cursor:"pointer",opacity:0.6,marginLeft:2}}

                      onClick={() => { const next = deepClone(ed); next.res.combatBonuses = next.res.combatBonuses.filter((_, j) => j !== bIdx); setEd(next); }}

                    >x</span>

                  </span>

                ))}

              </div>

            )}

            <select

              aria-label="Add craft combat bonus"

              style={{width:"100%",background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"6px 10px",fontSize:12,outline:0,marginBottom:6,cursor:"pointer"}}

              value=""

              onChange={(e) => {

                if (!e.target.value) return;

                const sel = COMBAT_BONUS_TYPES.find(b => b.label === e.target.value);

                if (sel) {

                  const next = deepClone(ed);

                  if (!Array.isArray(next.res.combatBonuses)) next.res.combatBonuses = [];

                  next.res.combatBonuses.push({ type: sel.type, scope: sel.scope, value: sel.value, label: sel.label });

                  setEd(next);

                }

                e.target.value = '';

              }}

            >

              <option value="">+ Add combat bonus...</option>

              <optgroup label="Advantage">

                {COMBAT_BONUS_TYPES.filter(b => b.type === 'advantage').map(b => (

                  <option key={b.label} value={b.label}>{b.label}</option>

                ))}

              </optgroup>

              <optgroup label="Supreme Advantage">

                {COMBAT_BONUS_TYPES.filter(b => b.type === 'supreme_advantage').map(b => (

                  <option key={b.label} value={b.label}>{b.label}</option>

                ))}

              </optgroup>

              <optgroup label="Disadvantage">

                {COMBAT_BONUS_TYPES.filter(b => b.type === 'disadvantage').map(b => (

                  <option key={b.label} value={b.label}>{b.label}</option>

                ))}

              </optgroup>

              <optgroup label="Save Bonuses">

                {COMBAT_BONUS_TYPES.filter(b => b.type === 'save_bonus').map(b => (

                  <option key={b.label} value={b.label}>{b.label}</option>

                ))}

              </optgroup>

              <optgroup label="Passive Bonuses">

                {COMBAT_BONUS_TYPES.filter(b => b.type === 'passive').map(b => (

                  <option key={b.label} value={b.label}>{b.label}</option>

                ))}

              </optgroup>

              <optgroup label="Resistances and Immunities">

                {COMBAT_BONUS_TYPES.filter(b => b.type === 'resistance' || b.type === 'immunity').map(b => (

                  <option key={b.label} value={b.label}>{b.label}</option>

                ))}

              </optgroup>

            </select>

            <div style={{display:"flex",gap:4}}>

              <input

                aria-label="Custom craft combat bonus draft"

                style={{flex:1,background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"6px 10px",fontSize:12,outline:0}}

                placeholder="Custom bonus (e.g., +2 WIS Saves)"

                onKeyDown={(e) => {

                  if (e.key === 'Enter' && e.target.value.trim()) {

                    const next = deepClone(ed);

                    if (!Array.isArray(next.res.combatBonuses)) next.res.combatBonuses = [];

                    next.res.combatBonuses.push({ type: 'custom', scope: 'custom', label: e.target.value.trim() });

                    setEd(next);

                    e.target.value = '';

                  }

                }}

              />

            </div>

            {hasPartiallySupportedItemCombatBonuses(ed.res.combatBonuses) && (

              <div data-testid="craft-authored-mechanic-warning" role="note" style={{marginTop:6,padding:"6px 8px",borderRadius:4,background:"rgba(201,162,39,0.12)",border:"1px solid rgba(201,162,39,0.3)",color:"#c9a227",fontSize:11}}>
                {AUTHORED_ITEM_MECHANIC_WARNING_TEXT}
              </div>

            )}

          </div>

        )}

      </div>



      {/* Notes */}

      <div style={{marginBottom:14}}>

        <label style={{fontFamily:"'Cinzel',serif",fontSize:12,color:"var(--ts)",display:"block",marginBottom:4}}>📝 Notes</label>

        <textarea value={ed.res.notes} onChange={(e) => updateEd("res.notes", e.target.value)} placeholder="Additional notes..."

          style={{width:"100%",background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"8px 12px",fontSize:14,outline:0,resize:"vertical",minHeight:40}} />

      </div>

      <div style={{marginBottom:14}}>

        <label style={{fontFamily:"'Cinzel',serif",fontSize:12,color:"var(--tr)",display:"block",marginBottom:4}}>🔒 DM Notes (private)</label>

        <textarea value={ed.res.dmNotes} onChange={(e) => updateEd("res.dmNotes", e.target.value)} placeholder="DM-only notes..."

          style={{width:"100%",background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"8px 12px",fontSize:14,outline:0,resize:"vertical",minHeight:40}} />

      </div>



      {/* Result Tags */}

      <div style={{marginBottom:14}}>

        <TagInput tags={ed.res.tags} allTags={allTags} label="Result Item Tags" color="var(--tpu)"

          onAdd={(t) => { const next = deepClone(ed); next.res.tags.push(t); setEd(next); if (!allTags.includes(t)) setAllTags([...allTags, t]); }}

          onRemove={(i) => { const next = deepClone(ed); next.res.tags.splice(i, 1); setEd(next); }} />

      </div>



      {/* Actions */}

      <div style={{display:"flex",gap:mob?6:10,justifyContent:"flex-end",paddingTop:12,borderTop:"1px solid var(--bs)",marginTop:8,flexWrap:mob?"wrap":"nowrap"}}>

        {!isNew && <button onClick={() => onDelete()} style={{padding:mob?"6px 12px":"8px 16px",borderRadius:"var(--rm)",border:"1px solid var(--ar)",background:"var(--arb)",color:"var(--tr)",fontSize:mob?12:13,cursor:"pointer",marginRight:"auto"}}>🗑️ Delete</button>}

        <button onClick={onCancel} style={{padding:mob?"6px 14px":"8px 18px",borderRadius:"var(--rm)",border:"1px solid var(--bs)",background:"none",color:"var(--ts)",fontSize:mob?12:13,cursor:"pointer"}}>Cancel</button>

        <button onClick={() => onSave(ed)} style={{padding:mob?"6px 16px":"8px 22px",borderRadius:"var(--rm)",border:"none",background:"linear-gradient(135deg,#6b3fa0,#8855cc)",color:"#fff",fontFamily:"'Cinzel',serif",fontSize:mob?12:13,fontWeight:600,cursor:"pointer"}}>💾 {isNew ? "Create" : "Save"}</button>

      </div>



      {/* Icon Picker Modals */}

      {iconPicker === "recipe" && <IconPicker current={ed.icon} onSelect={(ic) => updateEd("icon", ic)} onClose={() => setIconPicker(null)} favIcons={favIcons} setFavIcons={setFavIcons} />}

      {iconPicker === "result" && <IconPicker current={ed.res.icon} onSelect={(ic) => updateEd("res.icon", ic)} onClose={() => setIconPicker(null)} favIcons={favIcons} setFavIcons={setFavIcons} />}

    </div>

  );

}



// ─── ITEM EDITOR ─────────────────────────────────────────

function ItemEditor({ item, onSave, onCancel, onDelete, categories, items, recipes, isNew, favIcons, setFavIcons, onJumpToRecipe, mob }) {

  const [ed, setEd] = useState({ name: item?.name || "", icon: item?.icon || "📦", cat: item?.cat || "materials" });

  const [iconPicker, setIconPicker] = useState(false);

  const cats = categories.filter((c) => c.id !== "all");



  const usedIn = item ? recipes.filter((r) => r.ings.some((i) => i.iid === item.id)) : [];

  const producedBy = item ? recipes.filter((r) => nm(r.res.name) === nm(item.name)) : [];

  const canDelete = usedIn.length === 0;



  return (

    <div className="fade" style={{background:"var(--bgi)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",padding:mob?12:18}}>

      <div style={{fontFamily:"'Cinzel',serif",fontSize:mob?13:15,color:"var(--tg)",marginBottom:mob?10:14}}>

        {isNew ? "✨ New Base Item" : "✏️ Edit: " + ed.name}

      </div>

      <div style={{display:"flex",gap:mob?8:12,alignItems:"flex-start",marginBottom:mob?10:14,flexWrap:mob?"wrap":"nowrap"}}>

        <div>

          <label style={{fontFamily:"'Cinzel',serif",fontSize:12,color:"var(--to)",display:"block",marginBottom:4}}>Icon</label>

          <button onClick={() => setIconPicker(true)} style={{width:mob?40:48,height:mob?40:48,borderRadius:"var(--rm)",border:"1px solid var(--bs)",background:"var(--bg1)",fontSize:mob?22:26,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",position:"relative"}}>

            {isGameIcon(ed.icon) ? renderGameIcon(ed.icon, 26, 'var(--tp)') : ed.icon}<span style={{position:"absolute",bottom:-2,right:-2,background:"var(--ap)",borderRadius:"50%",width:16,height:16,fontSize:9,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff"}}>✎</span>

          </button>

        </div>

        <div style={{flex:1,minWidth:mob?120:undefined}}>

          <label style={{fontFamily:"'Cinzel',serif",fontSize:12,color:"var(--to)",display:"block",marginBottom:4}}>Item Name</label>

          <input value={ed.name} onChange={(e) => setEd({ ...ed, name: e.target.value })} placeholder="e.g., Mandrake Root"

            style={{width:"100%",background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"8px 12px",fontSize:mob?13:14,outline:0}} />

        </div>

        <div style={{flex:mob?"1 1 100%":"0 0 140px"}}>

          <label style={{fontFamily:"'Cinzel',serif",fontSize:12,color:"var(--to)",display:"block",marginBottom:4}}>Category</label>

          <select value={ed.cat} onChange={(e) => setEd({ ...ed, cat: e.target.value })}

            style={{width:"100%",background:"var(--bg1)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",color:"var(--tp)",padding:"8px 12px",fontSize:mob?13:14,outline:0,cursor:"pointer"}}>

            {cats.map((c) => <option key={c.id} value={c.id}>{c.l}</option>)}

          </select>

        </div>

      </div>

      {!isNew && (usedIn.length > 0 || producedBy.length > 0) && (

        <div style={{marginBottom:14}}>

          <label style={{fontFamily:"'Cinzel',serif",fontSize:12,color:"var(--to)",display:"block",marginBottom:4}}>⚙ Recipe Connections</label>

          {usedIn.map((r) => (

            <div key={r.id} style={{padding:"6px 10px",fontSize:12,color:"var(--ts)",borderLeft:"2px solid var(--bg)",marginBottom:4,marginLeft:6,display:"flex",alignItems:"center",gap:6}}>

              <span style={{fontSize:10,padding:"1px 6px",borderRadius:3,fontWeight:700,background:"var(--apb)",color:"var(--tpu)",border:"1px solid var(--ap)"}}>ingredient</span> {isGameIcon(r.icon) ? renderGameIcon(r.icon, 16, 'var(--tp)') : r.icon} {r.name}

              {onJumpToRecipe && <span onClick={() => onJumpToRecipe(r.id)} style={{cursor:"pointer",fontSize:11,color:"var(--tg)",marginLeft:"auto"}}>→ view</span>}

            </div>

          ))}

          {producedBy.map((r) => (

            <div key={r.id} style={{padding:"6px 10px",fontSize:12,color:"var(--ts)",borderLeft:"2px solid var(--bg)",marginBottom:4,marginLeft:6,display:"flex",alignItems:"center",gap:6}}>

              <span style={{fontSize:10,padding:"1px 6px",borderRadius:3,fontWeight:700,background:"var(--agb)",color:"var(--tgr)",border:"1px solid var(--ag)"}}>result</span> {isGameIcon(r.icon) ? renderGameIcon(r.icon, 16, 'var(--tp)') : r.icon} {r.name}

              {onJumpToRecipe && <span onClick={() => onJumpToRecipe(r.id)} style={{cursor:"pointer",fontSize:11,color:"var(--tg)",marginLeft:"auto"}}>→ view</span>}

            </div>

          ))}

        </div>

      )}

      <div style={{display:"flex",gap:mob?6:10,justifyContent:"flex-end",paddingTop:12,borderTop:"1px solid var(--bs)",marginTop:8,flexWrap:mob?"wrap":"nowrap"}}>

        {!isNew && <button onClick={() => { if (!canDelete) return; onDelete(); }} title={canDelete ? "Delete item" : "Can't delete — used in recipes"}

          style={{padding:mob?"6px 12px":"8px 16px",borderRadius:"var(--rm)",border:"1px solid var(--ar)",background:"var(--arb)",color:"var(--tr)",fontSize:mob?12:13,cursor:canDelete?"pointer":"not-allowed",marginRight:"auto",opacity:canDelete?1:.5}}>🗑️ Delete</button>}

        <button onClick={onCancel} style={{padding:mob?"6px 14px":"8px 18px",borderRadius:"var(--rm)",border:"1px solid var(--bs)",background:"none",color:"var(--ts)",fontSize:mob?12:13,cursor:"pointer"}}>Cancel</button>

        <button onClick={() => onSave(ed)} style={{padding:mob?"6px 16px":"8px 22px",borderRadius:"var(--rm)",border:"none",background:"linear-gradient(135deg,#6b3fa0,#8855cc)",color:"#fff",fontFamily:"'Cinzel',serif",fontSize:mob?12:13,fontWeight:600,cursor:"pointer"}}>💾 {isNew ? "Create" : "Save"}</button>

      </div>

      {iconPicker && <IconPicker current={ed.icon} onSelect={(ic) => setEd({ ...ed, icon: ic })} onClose={() => setIconPicker(false)} favIcons={favIcons} setFavIcons={setFavIcons} />}

    </div>

  );

}



// ─── MAIN APP ────────────────────────────────────────────

export const CraftingPage = ({

  isDM: propIsDM,

  onBack,

  isOpen = true,

  // 2026-04-22 crafting ↔ inventory bridge props:

  //  - craftingTable: char.craftingTable or null. Shape { inv, customItems }.

  //    When null (DM mode or unwired), falls back to DEFAULT_INV/DEFAULT_ITEMS.

  //  - onUpdateCraftingTable: ({inv, customItems}) => void. Called on changes

  //    so state persists on the character. Debounced internally.

  //  - onAddToInventory: (craftedItem) => void. Routes a crafted item into

  //    char.inventory when the player chooses that destination.

  //  - onAddToParty: (res, qty, updatedTableInv) => void. Deposits a table

  //    item into the shared partyInventory pool. Called from the Crafting

  //    Table view's "📤 To Party" button (2026-04-23).

  craftingTable = null,

  onUpdateCraftingTable = null,

  onAddToInventory = null,

  onAddToParty = null,

  // 2026-04-27 (later): Profession System Phase 1 props.

  //   char: the active character (player mode only). Holds playerProfs.

  //   onUpdateChar: (patch, auditHint) => void. Persists XP/level changes.

  //   profSettings: global toggles (enabled, maxMajor, ...).

  //   onUpdateProfSettings: 2026-04-29 — DM-only setter so the profession

  //     system can be toggled on/off from the crafting UI. Null/undefined

  //     in player mode (toggle UI is DM-only anyway).

  char = null,

  onUpdateChar = null,

  // 2026-05-09 (Recipe organization rework): party characters dict, used
  // by the "Party Skills" scope to show recipes any party member's
  // profession can hit. When omitted, scope toggle still renders but
  // "Party Skills" behaves identically to "My Skills".
  chars = null,

  profSettings = DEFAULT_PROF_SETTINGS,

  onUpdateProfSettings = null,

  professionsFeatureEnabled = true,

}) => {

  const mob = useIsMobile();

  useEffect(() => { injectCraftingStyles(); injectProfessionStyles(); }, []);

  // 2026-05-09: scroll-to-top on mount. Without this, navigating to
  // Crafting from a deep-scrolled view (combat log, journal, character
  // sheet) leaves the window scrolled to the previous page's offset and
  // the user opens to a half-rendered Recipe Book.
  useEffect(() => {
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);



  // Core data — player mode loads from the character's persisted craftingTable

  // when available; DM mode (no prop) still uses DEFAULT_ITEMS + DEFAULT_INV

  // as a local sandbox. DEFAULT_ITEMS always supplies the base catalog; any

  // customItems (e.g. crafted items like "ci_123") are merged on top.

  const buildItemsFromCraftingTable = useCallback((table) => {

    const base = deepClone(DEFAULT_ITEMS);

    const custom = table?.customItems || [];

    return [...base, ...custom.filter(c => !base.find(b => b.id === c.id))];

  }, []);

  const buildInvFromCraftingTable = useCallback((table) => {

    // Null craftingTable -> demo defaults. Empty-but-present table -> respect
    // the player's actual state instead of re-seeding.

    if (!table) return { ...DEFAULT_INV };

    return { ...(table.inv || {}) };

  }, []);

  const [items, setItems] = useState(() => buildItemsFromCraftingTable(craftingTable));

  // 2026-04-28 (Phase 4A): rename the underlying state to _rawRecipes and

  // expose `recipes` as a player-aware view. The DM-controlled `disc`

  // flag is preserved as the canonical "the party knows this" signal;

  // the view layer flips disc=true for any recipe id present in

  // char.learnedRecipes so the rest of the player-side rendering (Recipe

  // Crafting tab, Free Craft, Proximity, etc.) sees a learned recipe as

  // discovered without each call site needing isRecipeKnown checks.

  // setRecipes still mutates the underlying state, so the DM editor

  // continues to flip the canonical flag.

  const [_rawRecipes, setRecipes] = useState(deepClone(DEFAULT_RECIPES));

  const recipes = useMemo(() => {

    if (propIsDM) return _rawRecipes;

    const learned = (char && Array.isArray(char.learnedRecipes)) ? char.learnedRecipes : [];

    if (learned.length === 0) return _rawRecipes;

    const learnedSet = new Set(learned);

    return _rawRecipes.map(r => (!r.disc && learnedSet.has(r.id)) ? { ...r, disc: true } : r);

  }, [_rawRecipes, propIsDM, char]);

  const [inv, setInv] = useState(() => buildInvFromCraftingTable(craftingTable));

  const [categories, setCategories] = useState(deepClone(DEFAULT_CATS));

  const [allTags, setAllTags] = useState([...DEFAULT_TAGS]);

  const [favIcons, setFavIcons] = useState([]);



  // UI state

  // 2026-04-23 privacy fix: view starts 'player' when propIsDM is false, and

  // the only UI path to flip it (Player/DM toggle in the header) is gated on

  // propIsDM below. React state can't be mutated without DevTools, so a

  // player-mode mount stays in player view indefinitely without needing an

  // extra runtime guard.

  const [view, setView] = useState(propIsDM ? "dm" : "player"); // 'player' | 'dm'

  const [playerTab, setPlayerTab] = useState("recipe"); // 'recipe' | 'free' | 'crafted' | 'proximity'

  const [dmTab, setDmTab] = useState("recipes"); // 'recipes' | 'items' | 'freecraft' | 'proximity'

  const [selRecipe, setSelRecipe] = useState(null);

  const [searchQ, setSearchQ] = useState("");

  const [catFilter, setCatFilter] = useState("all");

  // 2026-05-09 (Recipe organization rework): four collapsible sections
  // (Available / Need materials / Profession too low / Other professions),
  // a scope toggle (My Skills / Party Skills), and a missing-materials
  // visibility toggle. Defaults: Available + Need materials open; the
  // gated and other-prof sections start closed since they're aspirational
  // not actionable.
  const [recipeScope, setRecipeScope] = useState('mine'); // 'mine' | 'party'
  const [showMissingMaterials, setShowMissingMaterials] = useState(true);
  const [expandedRecipeSections, setExpandedRecipeSections] = useState({
    available: true,
    needMaterials: true,
    profLocked: false,
    otherProf: false,
    undiscovered: false,
  });
  // 2026-05-09b: per-profession quick filters. Stored as a Set of EXCLUDED
  // profession ids (default empty = show all). The "Misc" sentinel covers
  // recipes with no profReq. Pills render inline with the "Available now"
  // header and toggle entries in/out of this set.
  const [excludedProfFilters, setExcludedProfFilters] = useState(() => new Set());

  const [craftQty, setCraftQty] = useState(1);

  const [gathered, setGathered] = useState({});

  const [craftSuccessMsg, setCraftSuccessMsg] = useState(null);



  // DM state

  const [dmSearchQ, setDmSearchQ] = useState("");

  const [dmItemSearchQ, setDmItemSearchQ] = useState("");

  const [showCatMgr, setShowCatMgr] = useState(false);

  const [newCatName, setNewCatName] = useState("");

  const [newCatIcon, setNewCatIcon] = useState("📁");

  const [editingRecipe, setEditingRecipe] = useState(null); // recipe id or 'new'

  const [editingItem, setEditingItem] = useState(null);

  const [dmItemFilter, setDmItemFilter] = useState(null);

  const [showImportModal, setShowImportModal] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [pinnedCampaigns, setPinnedCampaigns] = useState(["c1"]);



  // Free craft state

  const [fcTable, setFcTable] = useState([]);

  const [fcSalvageMsg, setFcSalvageMsg] = useState(null);

  const [fcShowRef, setFcShowRef] = useState(true);

  const [fcRefSel, setFcRefSel] = useState(null);

  const [fcSmartAdd, setFcSmartAdd] = useState(true);

  const [fcRefSearch, setFcRefSearch] = useState("");

  const [fcItemSearch, setFcItemSearch] = useState("");

  const [fcDragOver, setFcDragOver] = useState(false);

  const [mobRecipeListOpen, setMobRecipeListOpen] = useState(true);



  // Settings

  const [failMode, setFailMode] = useState("pct_one");

  const [failPctEach, setFailPctEach] = useState(10);

  const [failPctOne, setFailPctOne] = useState(30);

  const [hintCfg, setHintCfg] = useState({ showUnknown: true, defaultHint: "none" });



  // Campaign

  const [campaigns, setCampaigns] = useState([{ id: "c1", name: "Dragon's Keep" }]);

  const [activeCampaignId, setActiveCampaignId] = useState("c1");

  const [campaignStore, setCampaignStore] = useState({});

  const [renamingCampaign, setRenamingCampaign] = useState(false);

  const [renameVal, setRenameVal] = useState("");



  // Craft log

  const [craftLog, setCraftLog] = useState([]);

  const [undoLog, setUndoLog] = useState([]);

  const [proxOpen, setProxOpen] = useState({});



  // Toast

  const [toastMsg, setToastMsg] = useState(null);

  const toastTimer = useRef(null);

  const toast = useCallback((msg) => {

    setToastMsg(msg);

    if (toastTimer.current) clearTimeout(toastTimer.current);

    toastTimer.current = setTimeout(() => setToastMsg(null), 2200);

  }, []);



  // 2026-04-22: craft destination toggle. When player crafts, decide whether

  // the result lands in char.inventory (ready to equip/use) or stays on the

  // crafting table (available for further crafts/transfers). Default is

  // category-dependent — weapons/armor land in inventory; materials/potions

  // stay on the table since they're likely to be consumed by other recipes.

  // (Stored as string so future expansion can add other destinations.)

  const [craftDest, setCraftDest] = useState('inventory');



  // 2026-04-22: persist craftingTable state (inv + customItems) onto the

  // character via onUpdateCraftingTable, debounced so rapid edits don't spam

  // the storage layer. Only runs in the wired player path; DM sandbox mode

  // (no onUpdateCraftingTable) keeps local-only behavior.

  const saveTableTimer = useRef(null);

  const skipNextCraftingTablePersistRef = useRef(false);

  const buildPersistableCraftingTable = useCallback((nextItems, nextInv) => {

    const baseIds = new Set(DEFAULT_ITEMS.map(d => d.id));

    return {
      inv: nextInv,
      customItems: nextItems.filter(i => !baseIds.has(i.id)),
    };

  }, []);

  const craftingTableSourceSignature = useMemo(() => (
    craftingTable
      ? JSON.stringify({
          inv: craftingTable.inv || {},
          customItems: craftingTable.customItems || [],
        })
      : null
  ), [craftingTable]);

  useEffect(() => {

    if (!craftingTable) return;

    setItems(buildItemsFromCraftingTable(craftingTable));

    setInv(buildInvFromCraftingTable(craftingTable));

  }, [buildInvFromCraftingTable, buildItemsFromCraftingTable, craftingTable]);

  useEffect(() => {

    if (!onUpdateCraftingTable) return;

    if (saveTableTimer.current) clearTimeout(saveTableTimer.current);

    const nextTable = buildPersistableCraftingTable(items, inv);

    const nextSignature = JSON.stringify(nextTable);

    if (skipNextCraftingTablePersistRef.current) {

      skipNextCraftingTablePersistRef.current = false;

      return;

    }

    if (craftingTableSourceSignature !== null && nextSignature === craftingTableSourceSignature) return;

    saveTableTimer.current = setTimeout(() => {

      onUpdateCraftingTable(nextTable);

    }, 600);

    return () => { if (saveTableTimer.current) clearTimeout(saveTableTimer.current); };

  }, [buildPersistableCraftingTable, craftingTableSourceSignature, inv, items, onUpdateCraftingTable]);



  // Helpers using current state

  const gi = useCallback((id) => items.find((i) => i.id === id), [items]);

  const gr = useCallback((id) => recipes.find((r) => r.id === id), [recipes]);

  const ph = useCallback((id) => inv[id] || 0, [inv]);

  // 2026-04-27 (later): profession derivations hoisted up here so the

  // `cc` useCallback below can close over them without TDZ. The fuller

  // Profession-System block (picker UI, talent tree, grantCraftXp) is

  // declared further down where it's used; only the values that gate

  // the craftability check live up here.

  const playerProfsState = char?.playerProfs || {};

  // Profession UI, gates, XP, and recipe requirement pills are controlled by

  // the system-wide feature flag first, then the campaign-level setting.

  const profsFeatureEnabled = !!professionsFeatureEnabled;

  const profsEnabled = profsFeatureEnabled && !!profSettings?.enabled;

  // 2026-04-27 (later): canCraft now factors profession requirement.

  // Disc + ingredients + profession gate must all pass for the craft

  // button to enable. If profSettings.enabled is false, profession

  // gating is bypassed (legacy behavior).

  const cc = useCallback((r, q = 1) => {

    if (!r.disc) return false;

    if (!r.ings.every((i) => ph(i.iid) >= i.qty * q)) return false;

    if (profsEnabled) {

      const gate = checkProfReq(r, playerProfsState, DEFAULT_PROFESSIONS);

      if (!gate.ok) return false;

    }

    return true;

  }, [ph, profsEnabled, playerProfsState]);

  const mc = useCallback((r) => !r.disc ? 0 : Math.min(...r.ings.map((i) => Math.floor(ph(i.iid) / i.qty))), [ph]);

  const ru = useCallback((id) => recipes.filter((r) => r.ings.some((i) => i.iid === id)), [recipes]);

  const rp = useCallback((id) => { const it = items.find((i) => i.id === id); return it ? recipes.filter((r) => nm(r.res.name) === nm(it.name)) : []; }, [recipes, items]);



  // Save campaign state

  const saveCampaign = useCallback(() => {

    setCampaignStore((prev) => ({

      ...prev,

      [activeCampaignId]: deepClone({ items, recipes, inv, allTags, categories: categories.filter((c) => c.id !== "all"), hintCfg, failMode, failPctEach, failPctOne, undoLog, favIcons, proxOpen, craftLog }),

    }));

  }, [activeCampaignId, items, recipes, inv, allTags, categories, hintCfg, failMode, failPctEach, failPctOne, undoLog, favIcons, proxOpen, craftLog]);



  const switchCampaign = useCallback((id) => {

    if (id === activeCampaignId) return;

    saveCampaign();

    const d = campaignStore[id];

    if (d) {

      setItems(d.items); setRecipes(d.recipes); setInv(d.inv); setAllTags(d.allTags);

      setCategories([{ id: "all", l: "⚡ All" }, ...d.categories]);

      setHintCfg({ ...d.hintCfg }); setFailMode(d.failMode); setFailPctEach(d.failPctEach);

      setFailPctOne(d.failPctOne); setUndoLog(d.undoLog); setFavIcons(d.favIcons || []);

      setProxOpen(d.proxOpen || {}); setCraftLog(d.craftLog || []);

    }

    setActiveCampaignId(id);

    setSelRecipe(null); setEditingRecipe(null); setEditingItem(null);

    setFcTable([]); setFcSalvageMsg(null); setGathered({});

  }, [activeCampaignId, saveCampaign, campaignStore]);



  // Init campaign store

  useEffect(() => {

    saveCampaign();

  }, []); // eslint-disable-line



  // Recipe match for free craft

  const findRecipeMatch = useCallback((tbl) => {

    if (!tbl.length) return null;

    const tMap = {};

    tbl.forEach((t) => { tMap[t.iid] = (tMap[t.iid] || 0) + t.qty; });

    for (const r of recipes) {

      if (!r.vis && !r.disc) continue;

      const rMap = {};

      r.ings.forEach((i) => { rMap[i.iid] = (rMap[i.iid] || 0) + i.qty; });

      const tKeys = Object.keys(tMap), rKeys = Object.keys(rMap);

      if (tKeys.length !== rKeys.length) continue;

      let match = true;

      for (const k of rKeys) { if ((tMap[k] || 0) !== (rMap[k] || 0)) { match = false; break; } }

      if (match) { for (const k of tKeys) { if (!(k in rMap)) { match = false; break; } } }

      if (match) return r;

    }

    return null;

  }, [recipes]);



  const storeCraftResult = useCallback((res, qty, recipeName) => {

    setCraftLog((prev) => [...prev, { id: "cl" + Date.now(), name: res.name, icon: res.icon, cat: res.cat || "", qty, time: Date.now(), recipe: recipeName || "" }]);

    const incomingStackKey = getCraftResultTableStackKey(res);

    const existingItem = incomingStackKey
      ? items.find((i) => i._res && getCraftResultTableStackKey(i._res) === incomingStackKey)
      : null;

    if (existingItem) {

      setInv((prev) => ({ ...prev, [existingItem.id]: (prev[existingItem.id] || 0) + qty }));

    } else {

      const baseIds = new Set(DEFAULT_ITEMS.map(d => d.id));

      const currentTable = {
        inv: {},
        customItems: items.filter(i => !baseIds.has(i.id)),
      };

      const nextTable = applyCraftResultToCraftingTable(currentTable, res, {
        quantity: qty,
        tableItemIdFactory: () => "ci_" + Date.now(),
      });

      const createdItem = nextTable.customItems.find(i => nextTable.inv[i.id] === qty && i._res && i._res.name === res.name)
        || nextTable.customItems[nextTable.customItems.length - 1];

      // 2026-04-22 fix: preserve the full recipe result as `_res` so a later

      // "Move to inventory" transfer can reconstruct the item with stats,

      // abilities, uses, onUse effects, etc. Without this, the crafting table

      // only kept the minimal {id, name, icon, cat} definition and the

      // reverse transfer produced a bare-name item.

      setItems((prev) => [...prev, createdItem]);

      setInv((prev) => ({ ...prev, [createdItem.id]: qty }));

    }

  }, [items]);



  // ─── PLAYER: RECIPE VIEW ────────────────────────────────
  // 2026-05-09 (Recipe organization rework): replaced flat sorted list
  // with four collapsible sections (Available / Need materials /
  // Profession too low / Other professions) + a scope toggle (My Skills
  // / Party Skills) + missing-materials toggle. Old design dropped every
  // recipe into the same list and relied on a green "✦ CRAFTABLE" pill
  // to indicate readiness — fine for 12 recipes, hopeless once the
  // marketplace ships and a player has hundreds.

  const PlayerRecipeView = () => {

    // Per-recipe classification: which bucket and (if applicable) which
    // profession owner met the level. Pure of state; reads from the
    // closure so React doesn't need to memoize across the closure scope.
    const classifyRecipe = (r) => {
      if (!r.disc) return { bucket: 'undiscovered', prof: null, hasMaterials: false };
      const hasMaterials = r.ings.every(i => ph(i.iid) >= i.qty);
      if (!profsEnabled || !r.profReq) {
        return { bucket: hasMaterials ? 'available' : 'needMaterials', prof: null, hasMaterials };
      }
      const prof = DEFAULT_PROFESSIONS.find(p => p.id === r.profReq.profId);
      const ownLevel = playerProfsState[r.profReq.profId]?.level || 0;
      const meetsOwn = ownLevel >= r.profReq.minLevel;
      // Party scope: also count any other party member's profession.
      const partyMembers = (recipeScope === 'party' && chars && typeof chars === 'object')
        ? Object.entries(chars).filter(([id]) => id !== char?.id)
        : [];
      const partyMeets = partyMembers.some(([, c]) =>
        (c?.playerProfs?.[r.profReq.profId]?.level || 0) >= r.profReq.minLevel);
      const partyHas = partyMembers.some(([, c]) => !!c?.playerProfs?.[r.profReq.profId]);
      const someoneHasProf = !!playerProfsState[r.profReq.profId] || partyHas;
      const someoneMeets = meetsOwn || partyMeets;
      if (someoneMeets) return { bucket: hasMaterials ? 'available' : 'needMaterials', prof, hasMaterials };
      if (someoneHasProf) return { bucket: 'profLocked', prof, hasMaterials };
      return { bucket: 'otherProf', prof, hasMaterials };
    };

    // PlayerRecipeView is recreated on every parent render (inline component
    // closure pattern that predates this rework), so useMemo here would
    // never reuse a cached value. Just compute inline — recipe lists are
    // small enough that this is cheap.
    const profFilterKey = (r) => r.profReq?.profId || '__misc__';
    const buckets = (() => {
      const out = { available: [], needMaterials: [], profLocked: [], otherProf: [], undiscovered: [] };
      recipes.filter(r => r.vis || r.disc).forEach(r => {
        if (r.disc) {
          if (catFilter !== 'all' && r.cat !== catFilter) return;
          if (searchQ) {
            const q = searchQ.toLowerCase();
            if (!r.name.toLowerCase().includes(q) && !(r.tags || []).some(t => t.toLowerCase().includes(q))) return;
          }
          // Per-profession quick filter (only applied to discovered recipes;
          // undiscovered are always allowed through since the pill list
          // doesn't include them anyway).
          if (profsEnabled && excludedProfFilters.has(profFilterKey(r))) return;
        }
        const info = classifyRecipe(r);
        if (info.bucket === 'needMaterials' && !showMissingMaterials) return;
        out[info.bucket].push({ r, info });
      });
      return out;
    })();

    // Build the pill set: every profession the user has learned, plus
    // every profession a party member has learned (when in Party Skills
    // scope), plus "Misc" if any visible recipe has no profession
    // requirement. Each pill toggles its profession id in/out of
    // excludedProfFilters. Pills are then narrowed to ones with at
    // least one matching visible recipe under the current category +
    // search filters — so a player who's taken Fishing (no fishing
    // recipes exist) doesn't see a dead Fishing pill. Excluded pills
    // are kept visible regardless so the user can always toggle them
    // back on.
    const pillProfIds = (() => {
      const seen = new Set(Object.keys(playerProfsState));
      if (recipeScope === 'party' && chars && typeof chars === 'object') {
        Object.entries(chars).forEach(([id, c]) => {
          if (id === char?.id) return;
          Object.keys(c?.playerProfs || {}).forEach(pid => seen.add(pid));
        });
      }
      return Array.from(seen);
    })();
    const visibleRecipes = recipes.filter(r => r.vis || r.disc);
    const candidatePills = pillProfIds
      .map(id => DEFAULT_PROFESSIONS.find(p => p.id === id))
      .filter(Boolean)
      .map(p => ({ key: p.id, icon: p.icon, label: p.name }));
    if (visibleRecipes.some(r => !r.profReq)) {
      candidatePills.push({ key: '__misc__', icon: '✦', label: 'Misc' });
    }

    // Recipe-count check: same predicates as the bucketing loop minus
    // the prof-pill filter itself (otherwise an excluded pill would
    // count zero and immediately disappear).
    const recipeMatchesView = (r, profKey) => {
      if (!r.disc) return false;
      if (catFilter !== 'all' && r.cat !== catFilter) return false;
      if (searchQ) {
        const q = searchQ.toLowerCase();
        if (!r.name.toLowerCase().includes(q) && !(r.tags || []).some(t => t.toLowerCase().includes(q))) return false;
      }
      return profFilterKey(r) === profKey;
    };

    const profPills = candidatePills.filter(p => {
      if (excludedProfFilters.has(p.key)) return true; // keep so user can re-enable
      return visibleRecipes.some(r => recipeMatchesView(r, p.key));
    });

    const toggleProfFilter = (key) => {
      setExcludedProfFilters(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
      });
    };

    // Counts for the category filter pills — based on the visible-but-discovered
    // pool (matches the old behavior; doesn't double-count category filter).
    const ct = {};
    recipes.filter(r => r.vis || r.disc).forEach(r => { ct[r.cat] = (ct[r.cat] || 0) + 1; });

    const sel = selRecipe ? gr(selRecipe) : null;
    const totalShown = buckets.available.length + buckets.needMaterials.length + buckets.profLocked.length + buckets.otherProf.length;

    const sectionDef = [
      { id: 'available',     label: 'Available now',       icon: '✦', tone: 'available', emptyMsg: 'Nothing crafted-ready yet. Gather materials or level up a profession.' },
      { id: 'needMaterials', label: 'Need materials',      icon: '📦', tone: 'materials', emptyMsg: 'No recipes waiting on materials.' },
      { id: 'profLocked',    label: 'Profession too low',  icon: '🔒', tone: 'locked',    emptyMsg: 'Nothing currently above your profession level.' },
      { id: 'otherProf',     label: 'Other professions',   icon: '⚙️', tone: 'other',     emptyMsg: 'No recipes from professions you haven\'t learned.' },
    ];

    const renderRecipeRow = (r, info) => {
      const isLocked = info.bucket === 'profLocked' || info.bucket === 'otherProf';
      const isMissingMats = info.bucket === 'needMaterials';
      const profMet = info.bucket === 'available' || info.bucket === 'needMaterials';
      return (
        <div key={r.id}
          className={`recipe-row ${isLocked ? 'locked' : ''}`}
          onClick={() => { setSelRecipe(r.id); setCraftQty(1); setGathered({}); setCraftSuccessMsg(null); if (mob) setMobRecipeListOpen(false); }}
          style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:"var(--bgi)",
            border:"1px solid " + (selRecipe === r.id ? "var(--bgb)" : "var(--bs)"),
            borderRadius:"var(--rm)",cursor:"pointer",transition:"all .2s",
            boxShadow: selRecipe === r.id ? "var(--gg)" : "none"}}>
          <div style={{width:mob?32:40,height:mob?32:40,borderRadius:"var(--rs)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:mob?18:22,flexShrink:0,background:"var(--bg1)",border:"1px solid var(--bs)"}}>
            {r.disc ? r.icon : "❓"}
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontFamily:"'Alegreya',serif",fontSize:mob?13:14,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.disc ? r.name : "? Unknown Recipe"}</div>
            <div style={{fontSize:11,color:"var(--ts)",display:"flex",gap:6,alignItems:"center",marginTop:2,flexWrap:"wrap"}}>
              <span className={`catb catb-${r.cat}`}>{r.cat}</span>
              {r.disc && (
                <span style={{color: isMissingMats ? 'var(--tr)' : 'var(--ts)', fontWeight: isMissingMats ? 600 : 400}}>
                  {r.ings.length} ing.
                </span>
              )}
              {profsEnabled && r.disc && r.profReq && info.prof && (
                <span className={`prof-req ${profMet ? 'met' : 'unmet'}`}>
                  {!profMet && <span style={{fontSize:9}}>🔒</span>}
                  <span className="prof-icon">{info.prof.icon}</span>
                  <span>{info.prof.name} · Lv {r.profReq.minLevel}</span>
                </span>
              )}
            </div>
          </div>
          {!r.disc && <span style={{color:"var(--tm)",fontSize:14}}>🔒</span>}
          {info.bucket === 'available' && r.disc && <span title="Available now" style={{color:'var(--tgr)',fontSize:14,flexShrink:0}}>✦</span>}
        </div>
      );
    };

    const renderSection = (def, items, headerInline = null) => {
      const isOpen = !!expandedRecipeSections[def.id];
      const toggle = () => setExpandedRecipeSections(prev => ({ ...prev, [def.id]: !prev[def.id] }));
      // Hide entirely when no items AND it's a section that's optional info (locked/other).
      // Available + Need materials always render (with empty state) so the user knows where to look.
      const alwaysShow = def.id === 'available' || def.id === 'needMaterials';
      if (!alwaysShow && items.length === 0) return null;
      return (
        <div className={`rsec tone-${def.tone} ${isOpen ? 'open' : ''}`} key={def.id}>
          <div className="rsec-hdr" onClick={toggle}>
            <span className="rsec-caret">▶</span>
            <span className="rsec-title">{def.label}</span>
            {headerInline}
            <span className="rsec-count">{items.length}</span>
          </div>
          {isOpen && (
            <div className="rsec-body">
              {items.length === 0
                ? <div className="rsec-empty">{def.emptyMsg}</div>
                : items.map(({r, info}) => renderRecipeRow(r, info))}
            </div>
          )}
        </div>
      );
    };

    // Pills element rendered inline with "Available now" — they're filters
    // that span every section (toggling Blacksmithing off hides Iron
    // Dagger from Need-materials and Profession-too-low too), placed here
    // because Available is the most-scanned section. Click is stopped
    // from bubbling so it doesn't toggle the section.
    // Crowded-cluster fallback: at 4+ pills the labeled Misc would dominate
    // the row and force wrapping that the icon-only mode avoids. So once
    // we cross that threshold every pill (including Misc) goes icon-only.
    // 6+ pills will then naturally wrap to a second line within the
    // header via flex-wrap on .prof-pills — no JS needed for that case.
    const pillsCompact = profPills.length >= 4;
    const profPillsEl = profsEnabled && profPills.length > 0 ? (
      <span className="prof-pills" onClick={(e) => e.stopPropagation()}>
        {profPills.map(p => {
          const off = excludedProfFilters.has(p.key);
          // Misc keeps its label in the relaxed (≤3) layout since "✦"
          // alone has no obvious meaning. In compact (≥4) it joins the
          // icon-only treatment; the tooltip + aria-label still carry
          // the full "Misc" name.
          const iconOnly = pillsCompact || p.key !== '__misc__';
          return (
            <button
              key={p.key}
              type="button"
              className={`prof-pill ${off ? 'off' : 'on'} ${iconOnly ? 'icon-only' : ''}`}
              onClick={() => toggleProfFilter(p.key)}
              title={`${p.label} — click to ${off ? 'show' : 'hide'}`}
              aria-label={`${p.label} filter, currently ${off ? 'hidden' : 'visible'}`}
            >
              <span className="prof-pill-icon" aria-hidden="true">{p.icon}</span>
              {!iconOnly && <span className="prof-pill-label">{p.label}</span>}
            </button>
          );
        })}
      </span>
    ) : null;

    return (

      <div style={mob ? {display:"flex",flexDirection:"column",gap:12,minWidth:0} : {display:"grid",gridTemplateColumns:"minmax(280px,340px) 1fr",gap:20,minWidth:0}}>

        {/* Recipe List */}

        <div style={{background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"var(--rl)",overflow:"hidden",order:mob?1:undefined}}>

          {/* Header — on mobile, tapping toggles the list */}

          <div onClick={mob ? () => setMobRecipeListOpen(!mobRecipeListOpen) : undefined}

            style={{padding:mob?"10px 14px":"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid var(--bs)",cursor:mob?"pointer":"default"}}>

            <span style={{fontFamily:"'Cinzel',serif",fontSize:mob?14:16,color:"var(--tg)",display:"flex",alignItems:"center",gap:8}}>

              📖 {mob && sel ? sel.name : "Recipe Book"}

            </span>

            <span style={{fontSize:12,color:"var(--ts)",display:"flex",alignItems:"center",gap:6}}>

              {totalShown} recipes

              {mob && <span style={{fontSize:10,color:"var(--tpu)"}}>{mobRecipeListOpen ? "▲" : "▼"}</span>}

            </span>

          </div>

          {(!mob || mobRecipeListOpen) && (

          <div style={{padding:mob?"12px 14px":"16px 18px"}}>

            {/* Search */}
            <div style={{display:"flex",alignItems:"center",gap:8,background:"var(--bgi)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",padding:"8px 12px",marginBottom:10}}>

              <span style={{color:"var(--tm)"}}>🔍</span>

              <input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Search recipes or tags..."

                style={{background:"none",border:"none",color:"var(--ts)",fontSize:13,flex:1,outline:0}} />

            </div>

            {/* Scope + missing-materials toggles. Hidden when professions
                are disabled — at that point everything is "everyone can
                craft anything", which makes the toggles meaningless. */}
            {profsEnabled && (
              <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap",alignItems:"center"}}>
                <div style={{display:"flex",background:"var(--bgi)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",padding:2}}>
                  <button onClick={() => setRecipeScope('mine')}
                    title="Show recipes only your character can craft"
                    style={{padding:"4px 10px",fontSize:11,border:"none",background:recipeScope==='mine'?'var(--ap)':'none',color:recipeScope==='mine'?'#fff':'var(--ts)',borderRadius:'var(--rs)',cursor:"pointer",fontFamily:"'Alegreya Sans',sans-serif"}}>My Skills</button>
                  <button onClick={() => setRecipeScope('party')}
                    title="Show recipes any party member can craft"
                    style={{padding:"4px 10px",fontSize:11,border:"none",background:recipeScope==='party'?'var(--ap)':'none',color:recipeScope==='party'?'#fff':'var(--ts)',borderRadius:'var(--rs)',cursor:"pointer",fontFamily:"'Alegreya Sans',sans-serif"}}>Party Skills</button>
                </div>
                <button onClick={() => setShowMissingMaterials(!showMissingMaterials)}
                  title={showMissingMaterials ? 'Hide recipes missing materials' : 'Show recipes missing materials'}
                  style={{display:"flex",alignItems:"center",gap:5,padding:"4px 10px",fontSize:11,border:`1px solid ${showMissingMaterials ? 'var(--ap)' : 'var(--bs)'}`,background:showMissingMaterials?'var(--apb)':'var(--bgi)',color:showMissingMaterials?'var(--tpu)':'var(--ts)',borderRadius:'var(--rm)',cursor:"pointer",fontFamily:"'Alegreya Sans',sans-serif"}}>
                  {showMissingMaterials ? '☑' : '☐'} Show missing materials
                </button>
              </div>
            )}

            {/* Category filter — kept as a top-level filter that crosses
                section boundaries. Square corners (var(--rm), not the old
                pill 20px) so it visually reads as a filter, not a tag. */}
            <div style={{display:"flex",flexWrap:"wrap",gap:mob?4:6,marginBottom:mob?10:14}}>

              {categories.map((c) => {

                const n = c.id === "all" ? Object.values(buckets).reduce((s, arr) => s + arr.length, 0) : (ct[c.id] || 0);

                if (c.id !== "all" && !n) return null;

                return (

                  <button key={c.id} onClick={() => setCatFilter(c.id)}

                    style={{padding:mob?"4px 10px":"5px 12px",borderRadius:"var(--rm)",border:"1px solid " + (catFilter === c.id ? "var(--ap)" : "var(--bs)"),

                      background: catFilter === c.id ? "var(--apb)" : "none",

                      color: catFilter === c.id ? "var(--tpu)" : "var(--ts)",

                      fontSize:mob?11:12,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>

                    {c.l} <span style={{background:"var(--bgi)",padding:"1px 6px",borderRadius:10,fontSize:mob?10:11}}>{n}</span>

                  </button>

                );

              })}

            </div>

            {/* Sections */}
            <div style={{display:"flex",flexDirection:"column",gap:0,maxHeight:mob?420:600,overflowY:"auto",paddingRight:4}}>

              {totalShown === 0 && buckets.undiscovered.length === 0 && (
                <div style={{textAlign:"center",color:"var(--tm)",padding:20,fontSize:13}}>No recipes match your filters</div>
              )}

              {sectionDef.map(def => renderSection(
                def,
                buckets[def.id],
                def.id === 'available' ? profPillsEl : null,
              ))}

              {buckets.undiscovered.length > 0 && renderSection(
                { id: 'undiscovered', label: 'Undiscovered', icon: '❓', tone: 'other', emptyMsg: '' },
                buckets.undiscovered
              )}

            </div>

          </div>

          )}

        </div>



        {/* Crafting Station */}

        <div style={{background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"var(--rl)",overflow:"hidden",position:"relative",order:mob?0:undefined}}>

          <div style={{padding:mob?"10px 14px":"14px 18px",borderBottom:"1px solid var(--bs)"}}>

            <span style={{fontFamily:"'Cinzel',serif",fontSize:mob?14:16,color:"var(--tg)"}}>⚒️ Crafting Station</span>

          </div>

          <div style={{padding:mob?"12px 14px":"16px 18px"}}>

            {sel && sel.disc ? <CraftingStation recipe={sel} qty={craftQty} setQty={setCraftQty} gathered={gathered} setGathered={setGathered} gi={gi} ph={ph} mc={mc} inv={inv} setInv={setInv} storeCraftResult={storeCraftResult} toast={toast} onCraftSuccess={setCraftSuccessMsg} />

              : sel && !sel.disc ? (

                <div className="fade" style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:mob?"40px 16px":"60px 20px",textAlign:"center",color:"var(--tm)"}}>

                  <div style={{fontSize:mob?36:48,marginBottom:12,opacity:.5}}>🔒</div>

                  <p style={{color:"var(--ts)"}}>This recipe hasn't been discovered yet.</p>

                </div>

              ) : (

                <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:mob?"40px 16px":"60px 20px",textAlign:"center",color:"var(--tm)"}}>

                  <div style={{fontSize:mob?36:48,marginBottom:12,opacity:.5}}>⚒️</div>

                  <p>Select a recipe to begin crafting</p>

                </div>

              )}

          </div>



          {/* Recipe Craft Success Popup */}

          {craftSuccessMsg && (

            <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",background:"rgba(14,10,15,.92)",borderRadius:"var(--rl)",zIndex:10,animation:"fi .3s ease",padding:mob?14:20,textAlign:"center"}}>

              <div style={{fontSize:mob?36:48,marginBottom:mob?8:12}}>✨</div>

              <div style={{fontFamily:"'Cinzel',serif",fontSize:mob?15:18,marginBottom:mob?8:12,color:"var(--tgr)"}}>Craft Successful!</div>



              {/* Item Card */}

              <div style={{background:"var(--bg2)",border:"1px solid var(--ag)",borderRadius:"var(--rl)",padding:mob?"12px 14px":"16px 20px",maxWidth:320,width:"100%",textAlign:"center"}}>

                <div style={{fontSize:mob?32:40,marginBottom:mob?6:8}}>{isGameIcon(craftSuccessMsg.icon) ? renderGameIcon(craftSuccessMsg.icon, 36, "var(--tp)") : craftSuccessMsg.icon}</div>

                <div style={{fontFamily:"'Cinzel',serif",fontSize:mob?14:16,color:"var(--tgb)",marginBottom:2}}>

                  {craftSuccessMsg.name}{craftSuccessMsg.qty > 1 ? ` ×${craftSuccessMsg.qty}` : ""}

                </div>

                <div style={{display:"flex",gap:6,justifyContent:"center",alignItems:"center",marginBottom:6,flexWrap:'wrap'}}>

                  {craftSuccessMsg.rar && <span className={`rar-${craftSuccessMsg.rar.toLowerCase().replace(/\s/,"")}`} style={{fontSize:12,fontWeight:600}}>{craftSuccessMsg.rar}</span>}

                  {craftSuccessMsg.cat && <span className={`catb catb-${craftSuccessMsg.cat}`}>{craftSuccessMsg.cat}</span>}

                  {/* 2026-04-27 (Visual Port): quality badge uses the

                      prototype's .cres-quality-badge class — legendary

                      gets a pulsing glow animation via injected CSS. */}

                  {craftSuccessMsg.quality && craftSuccessMsg.quality.tier !== 'common' && (() => {

                    const m = qualityMeta(craftSuccessMsg.quality.tier);

                    return (

                      <span className={`cres-quality-badge ${craftSuccessMsg.quality.tier}`} title={`${m.desc} · roll ${craftSuccessMsg.quality.roll}`}>

                        ✦ {m.label}

                      </span>

                    );

                  })()}

                </div>

                {craftSuccessMsg.desc && <div style={{fontSize:12,color:"var(--ts)",lineHeight:1.5,marginBottom:8,fontStyle:"italic"}}>{craftSuccessMsg.desc}</div>}

                {(craftSuccessMsg.buffs.length > 0 || craftSuccessMsg.debuffs.length > 0 || craftSuccessMsg.onUse.length > 0) && (

                  <div style={{display:"flex",gap:4,flexWrap:"wrap",justifyContent:"center",marginBottom:6}}>

                    {craftSuccessMsg.buffs.map((b, i) => <span key={"b"+i} style={{padding:"2px 8px",borderRadius:3,fontSize:11,fontWeight:600,border:"1px solid var(--ag)",background:"var(--agb)",color:"var(--tgr)"}}>{b}</span>)}

                    {craftSuccessMsg.debuffs.map((b, i) => <span key={"d"+i} style={{padding:"2px 8px",borderRadius:3,fontSize:11,fontWeight:600,border:"1px solid var(--ar)",background:"var(--arb)",color:"var(--tr)"}}>{b}</span>)}

                    {craftSuccessMsg.onUse.map((b, i) => <span key={"u"+i} style={{padding:"2px 8px",borderRadius:3,fontSize:11,fontWeight:600,border:"1px solid rgba(58,107,154,.4)",background:"rgba(58,107,154,.12)",color:"#4a9ad4"}}>⚡ {b}</span>)}

                  </div>

                )}

                {craftSuccessMsg.abilities?.length > 0 && craftSuccessMsg.abilities.map((a, i) => (

                  <div key={i} style={{padding:"5px 8px",background:"var(--bgi)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",marginBottom:4,textAlign:"left"}}>

                    <div style={{fontFamily:"Cinzel,serif",fontSize:11,color:"var(--tpu)"}}>{a.name}</div>

                    {a.desc && <div style={{fontSize:10,color:"var(--ts)",marginTop:1}}>{a.desc}</div>}

                    {a.effects && <div style={{marginTop:2}}><span style={{padding:"1px 6px",borderRadius:3,fontSize:10,fontWeight:600,border:"1px solid var(--ap)",background:"var(--apb)",color:"var(--tpu)"}}>{a.effects}</span></div>}

                    {a.limited && <div style={{fontSize:9,color:"var(--tm)",marginTop:2}}>Uses: {a.cur}/{a.max}{a.reset && a.reset !== "None" ? " · Resets: " + a.reset : ""}</div>}

                  </div>

                ))}

                {craftSuccessMsg.uses && <div style={{fontSize:11,color:"var(--tm)",marginTop:4}}>Uses: {craftSuccessMsg.uses.cur}/{craftSuccessMsg.uses.max}{craftSuccessMsg.uses.reset && craftSuccessMsg.uses.reset !== "None" ? " · Resets: " + craftSuccessMsg.uses.reset : ""}</div>}

                {craftSuccessMsg.notes && <div style={{fontSize:11,color:"var(--ts)",marginTop:4,fontStyle:"italic"}}>📝 {craftSuccessMsg.notes}</div>}

                {craftSuccessMsg.price > 0 && <div style={{fontSize:11,color:"var(--tg)",marginTop:4}}>💰 {craftSuccessMsg.price} gp</div>}

                {/* 2026-04-22: destination indicator. The toggle already

                    routed the item at craft time; this line tells the player

                    where it went so they know whether to look on the table or

                    in their inventory. */}

                {craftSuccessMsg.sentTo === 'inventory' && <div style={{fontSize:11,color:"var(--tgr)",marginTop:6,fontWeight:600}}>📦 Added to your inventory</div>}

                {craftSuccessMsg.sentTo === 'party' && <div style={{fontSize:11,color:"#4a9ad4",marginTop:6,fontWeight:600}}>🎒 Deposited to the party pool</div>}

                {craftSuccessMsg.sentTo === 'table' && <div style={{fontSize:11,color:"var(--tgb)",marginTop:6,fontWeight:600}}>🛠 Kept on your crafting table</div>}

                {/* 2026-04-27 (later): XP receipt — base + multipliers (P5).

                    Hidden when xpInfo is null (no profession, feature off,

                    or recipe has no profReq). */}

                {craftSuccessMsg.xpInfo && (() => {

                  const xi = craftSuccessMsg.xpInfo;

                  const prof = DEFAULT_PROFESSIONS.find(p => p.id === xi.profId);

                  return (

                    <div style={{marginTop:8,paddingTop:8,borderTop:'1px solid var(--bs)',fontSize:11,color:'var(--tgb)',fontFamily:"'Cinzel',serif"}}>

                      ✨ +{xi.total} XP {prof ? `(${prof.icon} ${prof.name})` : ''}

                      {xi.multipliers && xi.multipliers.length > 0 && (

                        <div style={{fontSize:10,color:'var(--ts)',fontFamily:'inherit',marginTop:2,fontStyle:'italic'}}>

                          Base {xi.base}{xi.multipliers.map((m,i) => ` × ${m.mult} (${m.label})`).join('')}

                        </div>

                      )}

                    </div>

                  );

                })()}

              </div>



              <button onClick={() => setCraftSuccessMsg(null)} style={{padding:"8px 24px",borderRadius:"var(--rm)",border:"none",background:"var(--ap)",color:"#fff",fontFamily:"'Cinzel',serif",fontSize:13,cursor:"pointer",marginTop:14}}>

                {/* 2026-04-22: dynamic label reflects destination. The action

                    (route to destination) has already happened — this button

                    just dismisses. Label change makes intent readable without

                    scanning the card's status line. */}

                {craftSuccessMsg.sentTo === 'inventory' ? '📦 Done (Inventory)'

                  : craftSuccessMsg.sentTo === 'party' ? '🎒 Done (Party Pool)'

                  : craftSuccessMsg.sentTo === 'table' ? '🛠 Done (on Table)'

                  : 'OK'}

              </button>

            </div>

          )}

        </div>

      </div>

    );

  };



  // ─── CRAFTING STATION ───────────────────────────────────

  function CraftingStation({ recipe: r, qty: q, setQty, gathered, setGathered, gi, ph, mc: maxCraft, inv, setInv, storeCraftResult, toast, onCraftSuccess }) {

    const mx = maxCraft(r);

    const res = r.res;

    const allGathered = r.ings.every((ing, i) => {

      const hv = ph(ing.iid), nd = ing.qty * q;

      return hv >= nd && gathered[i];

    });



    const handleCraft = async () => {

      if (!allGathered) return;

      const newInv = { ...inv };

      r.ings.forEach((i) => {
        newInv[i.iid] -= i.qty * q;
        if ((Number(newInv[i.iid]) || 0) <= 0) delete newInv[i.iid];
      });

      // 2026-04-22: craft destination is chosen via the craftDest toggle.

      // "inventory" → route to char.inventory via onAddToInventory. Pass

      //              newInv so the parent can save inventory+craftingTable

      //              atomically (eliminates the stale-closure race where the

      //              debounced save effect could overwrite the inventory add).

      // "table" → routes to local storeCraftResult so it stacks on the

      //           crafting table for future crafts/transfers.

      // When onAddToInventory is null (DM sandbox), always route to table.

      // 2026-04-27 (Phase 3): roll quality BEFORE persisting so the

      // crafted item carries the tier metadata. One roll per stack

      // (matches the "crafted three Masterwork swords" mental model).

      // Resolves to 'common' when feature off / no profession bound.

      const quality = rollCraftQuality(r, playerProfsState, profSettings);

      // Stamp quality on the result that gets persisted, so both

      // inventory-bound items and table-bound stacks remember their tier.

      const stampedRes = quality && quality.tier !== 'common'

        ? { ...r.res, quality: quality.tier }

        : r.res;

      // 2026-04-28: route the crafted item by destination toggle.

      //   inventory → onAddToInventory (char inventory + atomic table save)

      //   party     → onAddToParty (shared party pool + atomic table save)

      //   table     → storeCraftResult (keeps it on the local crafting table)

      // The party branch was previously absent — only inventory and table

      // were wired, so picking "Party Pool" silently fell through to table.

      try {
        if (craftDest === 'inventory' && onAddToInventory) {
          const result = await awaitMaybePromise(onAddToInventory(stampedRes, q, newInv));
          if (result?.mode) skipNextCraftingTablePersistRef.current = true;
          setInv(newInv);
        } else if (craftDest === 'party' && onAddToParty) {
          const result = await awaitMaybePromise(onAddToParty(stampedRes, q, newInv));
          if (result?.mode) skipNextCraftingTablePersistRef.current = true;
          setInv(newInv);
        } else {
          setInv(newInv);
          storeCraftResult(stampedRes, q, r.name);
        }
      } catch (e) {
        console.error('craft destination transfer failed', e);
        if (typeof toast === 'function') {
          toast(getMovementAuthorityFailureMessage(e, 'Craft transfer failed. Materials were not consumed.'));
        }
        return;
      }

      // 2026-04-27 (later): grant profession XP on successful craft.

      // grantCraftXp is a closure over char + onUpdateChar + profSettings;

      // returns null if profession isn't picked / disabled / no profReq.

      // We call it q times so a "craft 3" press feels like 3 grants

      // (matches the spirit of "you crafted 3 swords"). Surfaces a

      // level-up toast via setLevelUpToast inside grantCraftXp.

      let xpInfo = null;

      if (typeof grantCraftXp === 'function') {

        for (let i = 0; i < q; i++) {

          const info = grantCraftXp(r, { isFirstCraft: false, wasUnknown: false, destructionRulesOn: false });

          if (info) xpInfo = info; // keep last for the success toast

        }

      }



      onCraftSuccess({

        name: r.res.name,

        icon: r.res.icon,

        qty: q,

        rar: r.res.rar || "Common",

        cat: r.res.cat || r.cat || "",

        desc: r.res.desc || "",

        recipeName: r.name,

        buffs: r.res.buffs || [],

        debuffs: r.res.debuffs || [],

        onUse: r.res.onUse || [],

        notes: r.res.notes || "",

        abilities: r.res.abilities || [],

        price: r.res.price || 0,

        uses: r.res.uses || null,

        // 2026-04-22: surface destination on the success toast so players

        // can see where their crafted item went. 2026-04-28: party branch

        // added so the modal label reflects deposit-to-party correctly.

        sentTo: (craftDest === 'inventory' && onAddToInventory) ? 'inventory'

          : (craftDest === 'party' && onAddToParty) ? 'party'

          : 'table',

        // 2026-04-27 (later): include XP info so the success modal can

        // show "+40 XP (Blacksmithing)". Null when profession path not

        // applicable (no profReq, no chosen prof, feature disabled).

        xpInfo: xpInfo ? { ...xpInfo, profId: r.profReq?.profId } : null,

        // 2026-04-27 (Phase 3): quality tier + raw roll so the receipt

        // can show "Masterwork (roll 87)" with the tier color.

        quality,

      });

      // 2026-04-27 (Visual Port — Phase 4): record the result for the

      // inline quality-flash card. Auto-clears after 8s so it doesn't

      // linger forever; the prototype uses the same window.

      const xpInfoForCard = xpInfo ? { ...xpInfo, profId: r.profReq?.profId } : null;

      setLastCraftResult({ recipeId: r.id, quality, xpInfo: xpInfoForCard, time: Date.now() });

      setTimeout(() => setLastCraftResult((prev) => (prev && prev.recipeId === r.id ? null : prev)), 8000);

      setQty(1);

      setGathered({});

    };



    // 2026-04-27 (Visual Port — Phase 4): profession context strip

    // mirrors prototype `.prof-strip` + `.quality-preview`. Renders

    // the locked variant when the player can't currently craft this

    // recipe (no prof / under-leveled), or the unlocked variant with

    // current level + XP bar + a 5-segment quality preview track that

    // fills based on how many levels above the recipe's minimum the

    // player has reached. Hidden when the recipe has no profReq or the

    // feature is off.

    let profStrip = null;

    if (profsEnabled && r.profReq) {

      const prof = DEFAULT_PROFESSIONS.find(p => p.id === r.profReq.profId);

      const pState = playerProfsState[r.profReq.profId];

      const profCheck = checkProfReq(r, playerProfsState, DEFAULT_PROFESSIONS);

      if (prof && !profCheck.ok) {

        const minLvl = r.profReq.minLevel || 1;

        const curLvl = pState?.level || 0;

        const reasonLine = !pState

          ? <>Requires <b>{prof.name}</b> · Level {minLvl}</>

          : <>Requires <b>{prof.name} Level {minLvl}</b> — currently Level {curLvl}</>;

        profStrip = (

          <div className="prof-strip locked">

            <div className="ps-emb">{prof.icon}</div>

            <div className="ps-body">

              <div className="ps-lbl">🔒 PROFESSION REQUIRED</div>

              <div className="ps-prof">{reasonLine}</div>

            </div>

            <div style={{ fontSize: 11, color: 'var(--tr)', textAlign: 'right', fontFamily: "'Cinzel',serif" }}>LOCKED</div>

          </div>

        );

      } else if (prof && pState) {

        const atMax = pState.level >= (prof.maxLevel || 10);

        const need = atMax ? 0 : xpForLevel(prof, pState.level + 1);

        const xpPct = atMax ? 100 : (need > 0 ? Math.round((pState.xp / need) * 100) : 0);

        const overLevel = pState.level - (r.profReq.minLevel || 1);

        const qualityStage = Math.min(4, Math.max(0, Math.floor(overLevel / 2)));

        const qualityLabels = ['Common', 'Fine', 'Superior', 'Masterwork', 'Legendary'];

        const qualityClasses = ['common', 'fine', 'superior', 'master', 'legendary'];

        profStrip = (

          <>

            <div className="prof-strip">

              <div className="ps-emb">{prof.icon}</div>

              <div className="ps-body">

                <div className="ps-lbl">{prof.type === 'major' ? '✦ MAJOR PROFESSION' : '◆ MINOR PROFESSION'}</div>

                <div className="ps-prof">

                  {prof.name}

                  <span className="ps-lvl">Lvl {pState.level}{atMax ? ' MAX' : ''}</span>

                  {!atMax && (

                    <span className="ps-xp-wrap">

                      <span className="ps-xp-bar"><span className="ps-xp-fill" style={{ width: `${xpPct}%` }} /></span>

                      <span className="ps-xp-txt">{pState.xp}/{need}</span>

                    </span>

                  )}

                </div>

              </div>

              <div className="ps-quality" title="Potential quality roll based on your skill">

                🎲 Up to <b style={{ color: `var(--quality-${qualityClasses[qualityStage]})` }}>{qualityLabels[qualityStage]}</b>

              </div>

            </div>

            <div className="quality-preview">

              <div className="qp-label">

                <span>QUALITY RANGE</span>

                <span>Higher skill = better rolls</span>

              </div>

              <div className="qp-track">

                {qualityClasses.map((c, i) => (

                  <div key={c} className={`qp-seg${i <= qualityStage ? ' on ' + c : ''}`} />

                ))}

              </div>

              <div className="qp-legend">

                {qualityLabels.map(l => <span key={l}>{l}</span>)}

              </div>

            </div>

          </>

        );

      }

    }



    return (

      <div className="fade" style={{display:"flex",flexDirection:"column",gap:mob?12:18,position:"relative"}}>

        {/* Header */}

        <div style={{display:"flex",alignItems:"center",gap:mob?10:14}}>

          <div style={{width:mob?44:56,height:mob?44:56,borderRadius:"var(--rs)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:mob?24:30,background:"var(--bg1)",border:"1px solid var(--bg)"}}>

            {isGameIcon(r.icon) ? renderGameIcon(r.icon, 22, "var(--tp)") : r.icon}

          </div>

          <div style={{flex:1,minWidth:0}}>

            <div style={{fontFamily:"'Cinzel',serif",fontSize:mob?16:20,color:"var(--tgb)"}}>{r.name}</div>

            <div style={{fontFamily:"'Alegreya',serif",fontStyle:"italic",color:"var(--ts)",fontSize:mob?12:13,marginTop:2}}>{r.desc}</div>

            <div style={{display:"flex",gap:6,marginTop:4,alignItems:"center"}}>

              <span className={`catb catb-${r.cat}`}>{r.cat}</span>

              {res.rar && res.rar !== "Common" && <span style={{fontSize:11}} className={`rar-${res.rar.toLowerCase().replace(/\s/,"")}`}>{res.rar}</span>}

            </div>

          </div>

        </div>



        {profStrip}



        {/* 2026-04-27 (Visual Port — Phase 4): inline quality flash —

            shown briefly after a successful craft on this same recipe.

            Tier-colored border + name; legendary gets the pulsing glow

            via injected CSS. */}

        {(() => {

          if (!lastCraftResult || lastCraftResult.recipeId !== r.id) return null;

          if (Date.now() - lastCraftResult.time >= 8000) return null;

          const q = lastCraftResult.quality;

          if (!q) return null;

          const qm = qualityMeta(q.tier);

          return (

            <div className={`q-result-card ${q.tier}`}>

              <div className="qr-roll">🎲 CRAFT RESULT</div>

              <div className="qr-name">{qm.label}{q.tier === 'legendary' ? ' ✨' : ''}</div>

              <div className="qr-desc">{qm.desc}</div>

              {lastCraftResult.xpInfo && (

                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--bs)', fontSize: 12, color: 'var(--tgb)', fontFamily: "'Cinzel',serif" }}>

                  +{lastCraftResult.xpInfo.total} XP

                  {lastCraftResult.xpInfo.multipliers && lastCraftResult.xpInfo.multipliers.length > 0 && (

                    <> · {lastCraftResult.xpInfo.multipliers.map(m => m.label).join(' + ')}</>

                  )}

                </div>

              )}

            </div>

          );

        })()}



        {/* Ingredients */}

        <div style={{background:"var(--bgi)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",padding:mob?12:16}}>

          <div style={{fontFamily:"'Cinzel',serif",fontSize:mob?12:13,color:"var(--to)",marginBottom:mob?8:12}}>{mob?"🧾 Tap to gather":"🧾 Click each ingredient to gather it"}</div>

          <div style={{display:"flex",flexDirection:"column",gap:mob?6:8}}>

            {r.ings.map((ing, i) => {

              const it = gi(ing.iid), hv = ph(ing.iid), nd = ing.qty * q, ok = hv >= nd, gath = gathered[i];

              return (

                <div key={i} onClick={() => {

                  if (!ok) return;

                  const next = { ...gathered };

                  if (next[i]) delete next[i]; else next[i] = true;

                  setGathered(next);

                }}

                  style={{display:"flex",alignItems:"center",gap:mob?8:10,padding:mob?"6px 10px":"8px 12px",

                    background: gath ? "var(--agb)" : ok ? "rgba(201,149,26,.04)" : "var(--arb)",

                    border: "1px solid " + (gath ? "var(--ag)" : ok ? "var(--bgb)" : "var(--ar)"),

                    borderRadius:"var(--rm)",cursor: ok ? "pointer" : "not-allowed",

                    transition:"all .3s",

                    animation: gath ? "gatherPop .3s ease" : "none"}}>

                  <div style={{width:mob?28:32,height:mob?28:32,borderRadius:"var(--rs)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:mob?15:18,background:gath?"var(--agb)":"var(--bg1)",flexShrink:0,border:"1px solid "+(gath?"var(--ag)":"var(--bs)")}}>

                    {it ? it.icon : "?"}

                  </div>

                  <div style={{flex:1,fontFamily:"'Alegreya',serif",fontSize:mob?13:14,fontWeight:500,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>

                    {it ? it.name : ing.iid}

                  </div>

                  <div style={{fontSize:mob?12:13,fontWeight:700,flexShrink:0}}>

                    <span style={{color: ok ? "var(--tgr)" : "var(--tr)"}}>{hv}</span>

                    <span style={{color:"var(--ts)"}}> / {nd}</span>

                  </div>

                  <span style={{fontSize:mob?12:14,minWidth:mob?40:50,textAlign:"right",flexShrink:0}}>

                    {gath ? <span style={{color:"var(--tgr)"}}>✓</span>

                      : ok ? <span style={{color:"var(--tgb)",fontSize:mob?10:12}}>{mob?"Tap":"Click to gather"}</span>

                      : <span style={{color:"var(--tr)"}}>❌</span>}

                  </span>

                </div>

              );

            })}

          </div>

        </div>



        {/* Arrow & Result Preview */}

        <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:"4px 0"}}>

          <div style={{flex:1,height:1,background:"linear-gradient(90deg,transparent,var(--bs))"}} />

          <div style={{padding:"0 12px",display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>

            <svg width={mob?20:24} height={mob?20:24} viewBox="0 0 24 24" fill="none" style={{opacity:.7}}>

              <path d="M7 8.5L12 13.5L17 8.5" stroke="var(--tpu)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>

              <path d="M7 13L12 18L17 13" stroke="var(--tg)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity=".5"/>

            </svg>

          </div>

          <div style={{flex:1,height:1,background:"linear-gradient(90deg,var(--bs),transparent)"}} />

        </div>



        <div style={{display:"flex",alignItems:mob?"center":"flex-start",gap:mob?10:14,padding:mob?"10px 12px":"14px 16px",background:"var(--bgi)",border:"1px dashed var(--bg)",borderRadius:"var(--rm)",flexWrap:mob?"wrap":"nowrap"}}>

          <div style={{width:mob?40:48,height:mob?40:48,borderRadius:"var(--rs)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:mob?22:26,border:"1px solid var(--bg)",background:"rgba(201,149,26,.08)",flexShrink:0}}>

            {isGameIcon(res.icon) ? renderGameIcon(res.icon, 22, "var(--tp)") : res.icon}

          </div>

          <div style={{flex:1,minWidth:0}}>

            <div style={{fontFamily:"'Alegreya',serif",fontWeight:600,fontSize:mob?14:16,color:"var(--tgb)"}}>{res.name}{q > 1 ? ` ×${q}` : ""}</div>

            <div style={{fontSize:mob?11:12,color:"var(--ts)",marginTop:2}}>{res.desc}</div>

            {((res.buffs?.length) || (res.debuffs?.length) || (res.onUse?.length)) && (

              <div style={{display:"flex",gap:6,marginTop:6,flexWrap:"wrap"}}>

                {(res.buffs || []).map((b, i) => <span key={"b"+i} style={{padding:"2px 8px",borderRadius:3,fontSize:11,fontWeight:600,border:"1px solid var(--ag)",background:"var(--agb)",color:"var(--tgr)"}}>{b}</span>)}

                {(res.debuffs || []).map((b, i) => <span key={"d"+i} style={{padding:"2px 8px",borderRadius:3,fontSize:11,fontWeight:600,border:"1px solid var(--ar)",background:"var(--arb)",color:"var(--tr)"}}>{b}</span>)}

                {(res.onUse || []).map((b, i) => <span key={"u"+i} style={{padding:"2px 8px",borderRadius:3,fontSize:11,fontWeight:600,border:"1px solid rgba(58,107,154,.4)",background:"rgba(58,107,154,.12)",color:"#4a9ad4"}}>⚡ {b}</span>)}

              </div>

            )}

            {res.uses && <div style={{fontSize:11,color:"var(--tm)",marginTop:4}}>Uses: {res.uses.cur}/{res.uses.max}{res.uses.reset && res.uses.reset !== "None" ? " · Resets: " + res.uses.reset : ""}</div>}

            {res.notes && <div style={{fontSize:11,color:"var(--ts)",marginTop:4,fontStyle:"italic"}}>📝 {res.notes}</div>}

            {res.abilities?.length > 0 && res.abilities.map((a, i) => (

              <div key={i} style={{padding:"6px 10px",background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",marginTop:4}}>

                <div style={{fontFamily:"Cinzel,serif",fontSize:12,color:"var(--tpu)"}}>{a.name}</div>

                {a.desc && <div style={{fontSize:11,color:"var(--ts)",marginTop:2}}>{a.desc}</div>}

                {a.effects && <div style={{marginTop:3}}><span style={{padding:"2px 8px",borderRadius:3,fontSize:11,fontWeight:600,border:"1px solid var(--ap)",background:"var(--apb)",color:"var(--tpu)"}}>{a.effects}</span></div>}

                {a.limited && <div style={{fontSize:10,color:"var(--tm)",marginTop:3}}>Uses: {a.cur}/{a.max}{a.reset && a.reset !== "None" ? " · Resets: " + a.reset : ""}</div>}

              </div>

            ))}

            {res.price > 0 && <div style={{fontSize:11,color:"var(--tg)",marginTop:4}}>💰 {res.price} pp</div>}

          </div>

        </div>



        {/* Qty & Craft button */}

        <div style={{display:"flex",alignItems:"center",gap:mob?8:14,justifyContent:"flex-end",paddingTop:4,flexWrap:mob?"wrap":"nowrap"}}>

          {mx > 0 && <span style={{fontSize:12,color:"var(--ts)"}}>Can craft: {mx}</span>}

          <div style={{display:"flex",alignItems:"center",border:"1px solid var(--bs)",borderRadius:"var(--rm)",overflow:"hidden"}}>

            <button onClick={() => { if (q > 1) { setQty(q - 1); setGathered({}); } }}

              style={{width:mob?28:32,height:mob?28:32,border:"none",background:"var(--bgi)",color:"var(--ts)",fontSize:mob?14:16,cursor:"pointer"}}>−</button>

            <div style={{width:mob?30:36,textAlign:"center",fontSize:mob?13:14,fontWeight:700,background:"var(--bg1)",height:mob?28:32,lineHeight:mob?"28px":"32px",borderLeft:"1px solid var(--bs)",borderRight:"1px solid var(--bs)"}}>{q}</div>

            <button onClick={() => { if (q < mx) { setQty(q + 1); setGathered({}); } }}

              style={{width:mob?28:32,height:mob?28:32,border:"none",background:"var(--bgi)",color:"var(--ts)",fontSize:mob?14:16,cursor:"pointer"}}>+</button>

          </div>

          <button onClick={handleCraft} disabled={!allGathered}

            style={{padding:mob?"8px 20px":"10px 28px",borderRadius:"var(--rm)",border:"none",fontFamily:"'Cinzel',serif",fontSize:mob?13:15,fontWeight:700,cursor:allGathered?"pointer":"not-allowed",

              background: allGathered ? "linear-gradient(135deg,#2d7a2d,#3a9a3a)" : "var(--bgi)",

              color: allGathered ? "#fff" : "var(--tm)",

              boxShadow: allGathered ? "var(--gn)" : "none"}}>

            ⚒️ Craft{q > 1 ? ` (×${q})` : ""}

          </button>

        </div>

        {/* 2026-04-22: destination toggle. Player mode only (DM sandbox has

            no char.inventory to route to). Row below the craft button so it's

            visible at decision time without cluttering the header.

            2026-04-28: added Party Pool option — only renders when

            onAddToParty is wired (player mode with party-storage support). */}

        {onAddToInventory && (

          <div style={{display:"flex",alignItems:"center",gap:8,padding:mob?"8px 4px":"10px 2px",fontSize:mob?11:12,color:"var(--tm)",flexWrap:'wrap'}}>

            <span style={{whiteSpace:"nowrap"}}>Send to:</span>

            <button onClick={() => setCraftDest('inventory')}

              style={{padding:"4px 10px",borderRadius:"var(--rs)",border:"1px solid "+(craftDest==='inventory'?"var(--ag)":"var(--bs)"),

                background:craftDest==='inventory'?"var(--agb)":"var(--bg1)",

                color:craftDest==='inventory'?"var(--tgr)":"var(--ts)",

                fontSize:mob?11:12,cursor:"pointer",fontWeight:craftDest==='inventory'?700:400}}>

              📦 Inventory

            </button>

            {onAddToParty && (

              <button onClick={() => setCraftDest('party')}

                style={{padding:"4px 10px",borderRadius:"var(--rs)",border:"1px solid "+(craftDest==='party'?"#4a9ad4":"var(--bs)"),

                  background:craftDest==='party'?"rgba(58,107,154,0.18)":"var(--bg1)",

                  color:craftDest==='party'?"#4a9ad4":"var(--ts)",

                  fontSize:mob?11:12,cursor:"pointer",fontWeight:craftDest==='party'?700:400}}>

                🎒 Party Pool

              </button>

            )}

            <button onClick={() => setCraftDest('table')}

              style={{padding:"4px 10px",borderRadius:"var(--rs)",border:"1px solid "+(craftDest==='table'?"var(--ag)":"var(--bs)"),

                background:craftDest==='table'?"var(--agb)":"var(--bg1)",

                color:craftDest==='table'?"var(--tgr)":"var(--ts)",

                fontSize:mob?11:12,cursor:"pointer",fontWeight:craftDest==='table'?700:400}}>

              🛠 Keep on table

            </button>

          </div>

        )}

      </div>

    );

  }



  // ─── PLAYER: FREE CRAFT ─────────────────────────────────

  const PlayerFreeCraft = () => {

    const tableUsed = {};

    fcTable.forEach((t) => { tableUsed[t.iid] = (tableUsed[t.iid] || 0) + t.qty; });

    const match = findRecipeMatch(fcTable);

    const invItems = items.filter((i) => {

      const avail = ph(i.id) - (tableUsed[i.id] || 0);

      return avail > 0;

    });



    const addToTable = (iid) => {

      const avail = ph(iid) - (tableUsed[iid] || 0);

      if (avail <= 0) return;

      if (fcSmartAdd && fcRefSel) {

        const rr = gr(fcRefSel);

        if (rr) {

          const ig = rr.ings.find((x) => x.iid === iid);

          if (ig && (tableUsed[iid] || 0) >= ig.qty) return;

        }

      }

      const existing = fcTable.find((t) => t.iid === iid);

      if (existing) setFcTable(fcTable.map((t) => t.iid === iid ? { ...t, qty: t.qty + 1 } : t));

      else setFcTable([...fcTable, { iid, qty: 1 }]);

      if (fcRefSel) setFcShowRef(false);

    };



    const removeFromTable = (idx) => {

      const entry = fcTable[idx];

      if (entry.qty > 1) setFcTable(fcTable.map((t, i) => i === idx ? { ...t, qty: t.qty - 1 } : t));

      else setFcTable(fcTable.filter((_, i) => i !== idx));

    };



    const attemptCraft = () => {

      if (!fcTable.length) return;

      const matchR = findRecipeMatch(fcTable);

      if (matchR) {

        const newInv = { ...inv };

        fcTable.forEach((t) => { newInv[t.iid] = Math.max(0, (newInv[t.iid] || 0) - t.qty); });

        setInv(newInv);

        const wasUnknown = !matchR.disc;

        if (wasUnknown) {

          // 2026-04-28 (Phase 4A): use functional setter so we map over

          // the underlying _rawRecipes, not the player-aware view (which

          // patches disc=true for learnedRecipes). Without the functional

          // form, a player whose `learnedRecipes` already has other

          // entries would silently flip those to disc=true globally on

          // any free-craft discovery write.

          setRecipes(prev => prev.map((r) => r.id === matchR.id ? { ...r, disc: true } : r));

          toast("📖 New recipe discovered: " + matchR.name + "!");

        }

        // 2026-04-27 (later): grant profession XP for the discovery

        // craft. wasUnknown + destruction-rules-on triggers the

        // discovery multiplier (default 5×). isFirstCraft true so the

        // first-craft bonus also stacks. Free Craft gates on profession

        // intentionally aren't enforced here — discovery via Free Craft

        // is the design path, regardless of profession level.

        if (typeof grantCraftXp === 'function') {

          grantCraftXp(matchR, {

            isFirstCraft: wasUnknown,

            wasUnknown,

            destructionRulesOn: failMode !== 'no_destroy',

          });

        }

        storeCraftResult(matchR.res, 1, matchR.name);

        setFcSalvageMsg({

          type: "success",

          resultIcon: matchR.res.icon,

          resultName: matchR.res.name,

          resultRar: matchR.res.rar || "Common",

          resultCat: matchR.res.cat || matchR.cat || "",

          resultDesc: matchR.res.desc || "",

          buffs: matchR.res.buffs || [],

          debuffs: matchR.res.debuffs || [],

          onUse: matchR.res.onUse || [],

          notes: matchR.res.notes || "",

          abilities: matchR.res.abilities || [],

          price: matchR.res.price || 0,

          uses: matchR.res.uses || null,

        });

        setFcTable([]);

      } else {

        const itemsCopy = fcTable.map((t) => ({ iid: t.iid, qty: t.qty }));

        let destroyed = [], salvaged = [];

        const newInv = { ...inv };



        if (failMode === "pct_each") {

          itemsCopy.forEach((t) => {

            let dQty = 0;

            for (let q = 0; q < t.qty; q++) { if (Math.random() * 100 < failPctEach) dQty++; }

            if (dQty > 0) { destroyed.push({ iid: t.iid, qty: dQty }); newInv[t.iid] = Math.max(0, (newInv[t.iid] || 0) - dQty); }

            const sQty = t.qty - dQty;

            if (sQty > 0) salvaged.push({ iid: t.iid, qty: sQty });

          });

        } else if (failMode === "pct_one") {

          const ri = Math.floor(Math.random() * itemsCopy.length);

          if (Math.random() * 100 < failPctOne) {

            destroyed = [{ iid: itemsCopy[ri].iid, qty: 1 }];

            newInv[itemsCopy[ri].iid] = Math.max(0, (newInv[itemsCopy[ri].iid] || 0) - 1);

            itemsCopy.forEach((t, i) => {

              if (i === ri) { if (t.qty > 1) salvaged.push({ iid: t.iid, qty: t.qty - 1 }); }

              else salvaged.push({ ...t });

            });

          } else { salvaged = itemsCopy.map((t) => ({ ...t })); }

        } else { salvaged = itemsCopy.map((t) => ({ ...t })); }



        setInv(newInv);

        if (destroyed.length) {

          setUndoLog((prev) => [...prev, { id: "u" + Date.now(), time: Date.now(), items: destroyed, msg: "Failed craft", undone: false }]);

        }

        setFcSalvageMsg({

          type: "fail",

          title: failMode === "no_destroy" ? "Craft Failed" : "Craft Failed!",

          body: "These items don't combine into anything known." + (destroyed.length ? " Some materials were lost." : failMode !== "no_destroy" ? " All materials salvaged!" : ""),

          destroyed: destroyed.length ? destroyed : null,

          salvaged: salvaged.length ? salvaged : null,

        });

        setFcTable([]);

      }

    };



    const disc = recipes.filter((r) => r.disc);



    return (

      <div style={mob ? {display:"flex",flexDirection:"column",gap:12} : {display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,minHeight:500}}>

        {/* Inventory */}

        <div style={{background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"var(--rl)",overflow:"hidden",order:mob?1:undefined}}>

          <div style={{padding:mob?"10px 14px":"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid var(--bs)"}}>

            <span style={{fontFamily:"'Cinzel',serif",fontSize:mob?14:16,color:"var(--tg)"}}>🎒 Inventory</span>

            <span style={{fontSize:12,color:"var(--ts)"}}>{mob?"Tap to add":"Click to add"}</span>

          </div>

          <div style={{padding:mob?"12px 14px":"16px 18px"}}>

            {/* Guide card — desktop only (mobile version is in crafting table panel) */}

            {!mob && fcRefSel && (() => {

              const rr = gr(fcRefSel);

              if (!rr || !rr.disc) return null;

              return (

                <div style={{background:"var(--bgi)",border:"1px solid var(--bgb)",borderRadius:"var(--rm)",padding:mob?10:12,marginBottom:12,position:"relative"}}>

                  <button onClick={() => setFcRefSel(null)} style={{position:"absolute",top:8,right:8,width:22,height:22,borderRadius:"50%",border:"1px solid var(--bs)",background:"var(--bg2)",color:"var(--ts)",fontSize:11,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>

                  <div style={{display:"flex",alignItems:"center",gap:mob?8:10,marginBottom:mob?8:10}}>

                    <div style={{fontSize:mob?20:24,width:mob?36:40,height:mob?36:40,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"var(--rs)",background:"var(--bg2)",border:"1px solid var(--bg)"}}>{isGameIcon(rr.icon) ? renderGameIcon(rr.icon, 20, "var(--tp)") : rr.icon}</div>

                    <div>

                      <div style={{fontFamily:"'Cinzel',serif",fontSize:mob?13:14,color:"var(--tgb)"}}>{rr.name}</div>

                      <div style={{fontSize:11,color:"var(--ts)",fontStyle:"italic"}}>{rr.desc}</div>

                    </div>

                  </div>

                  <div style={{display:"flex",flexDirection:"column",gap:4}}>

                    {rr.ings.map((ig) => {

                      const it = gi(ig.iid), onTable = tableUsed[ig.iid] || 0;

                      const filled = onTable >= ig.qty, totalHave = ph(ig.iid), hv = totalHave - onTable;

                      const clickable = hv > 0 && !(fcSmartAdd && filled);

                      return (

                        <div key={ig.iid} onClick={() => clickable && addToTable(ig.iid)}

                          style={{display:"flex",alignItems:"center",gap:8,padding:mob?"6px 10px":"8px 10px",borderRadius:"var(--rm)",fontSize:12,

                            border:"1px solid " + (filled ? "var(--ag)" : totalHave > 0 ? "var(--bgb)" : "var(--ar)"),

                            background: filled ? "var(--agb)" : totalHave > 0 ? "rgba(201,149,26,.04)" : "var(--arb)",

                            cursor: clickable ? "pointer" : "default",

                            animation: filled ? "gatherPop .3s ease" : "none"}}>

                          <span style={{fontSize:mob?14:16}}>{it ? it.icon : "?"}</span>

                          <span style={{flex:1,minWidth:0}}>{it ? it.name : ig.iid}</span>

                          <span style={{fontWeight:700,fontSize:11,color:filled?"var(--tgr)":onTable>0?"var(--tgb)":"var(--tr)"}}>{onTable}/{ig.qty}</span>

                          <span style={{fontSize:11,minWidth:mob?30:60,textAlign:"right"}}>

                            {filled ? <span style={{color:"var(--tgr)"}}>✓{!mob && " Ready"}</span> : clickable ? <span style={{color:"var(--tgb)"}}>{mob?"Tap":"Click to add"}</span> : <span style={{color:"var(--tr)"}}>{mob?"Need":"Need more"}</span>}

                          </span>

                        </div>

                      );

                    })}

                  </div>

                  <label style={{display:"flex",alignItems:"center",gap:5,padding:"6px 8px",marginTop:6,fontSize:11,color:"var(--ts)",cursor:"pointer",userSelect:"none"}}>

                    <input type="checkbox" checked={fcSmartAdd} onChange={(e) => setFcSmartAdd(e.target.checked)} style={{accentColor:"var(--ag)",width:12,height:12}} />

                    Cap Ingredients <span style={{opacity:.6}}>— limit to recipe amounts</span>

                  </label>

                </div>

              );

            })()}

            {/* Search items */}

            <div style={{display:"flex",alignItems:"center",gap:8,background:"var(--bgi)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",padding:"8px 12px",marginBottom:10}}>

              <span style={{color:"var(--tm)"}}>🔍</span>

              <input value={fcItemSearch} onChange={(e) => setFcItemSearch(e.target.value)} placeholder="Search items..."

                style={{background:"none",border:"none",color:"var(--ts)",fontSize:13,flex:1,outline:0}} />

            </div>

            {/* Inventory grid — split into needed/others when recipe selected */}

            {(() => {

              const neededIds = new Set();

              if (fcRefSel) { const rr = gr(fcRefSel); if (rr && rr.disc) rr.ings.forEach((ig) => neededIds.add(ig.iid)); }

              const sq = fcItemSearch.toLowerCase();

              const filtered = invItems.filter((it) => {

                const avail = ph(it.id) - (tableUsed[it.id] || 0);

                if (avail <= 0) return false;

                if (sq && !it.name.toLowerCase().includes(sq)) return false;

                return true;

              });

              // On mobile, needed items are shown in crafting table panel, so skip them here

              const needed = neededIds.size > 0 && !mob ? filtered.filter((it) => neededIds.has(it.id)) : [];

              const others = neededIds.size > 0 ? filtered.filter((it) => !neededIds.has(it.id)) : filtered;

              const showItems = mob && neededIds.size > 0 ? others : filtered;



              const renderSlot = (it, highlight) => {

                const avail = ph(it.id) - (tableUsed[it.id] || 0);

                return (

                  <div key={it.id} onClick={() => addToTable(it.id)}

                    draggable={!mob} onDragStart={mob?undefined:(e) => { e.dataTransfer.setData("text/plain", it.id); e.currentTarget.style.opacity = ".4"; }}

                    onDragEnd={mob?undefined:(e) => { e.currentTarget.style.opacity = "1"; }}

                    style={{width:mob?56:64,height:mob?56:64,borderRadius:"var(--rm)",border:"1px solid "+(highlight?"var(--bgb)":"var(--bs)"),background:"var(--bgi)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",cursor:mob?"pointer":"grab",transition:"all .2s",position:"relative",userSelect:"none"}}>

                    <span style={{fontSize:mob?20:24}}>{isGameIcon(it.icon) ? renderGameIcon(it.icon, 20, "var(--tp)") : it.icon}</span>

                    <span style={{fontSize:8,color:"var(--ts)",textAlign:"center",lineHeight:1.1,marginTop:2,maxWidth:mob?50:58,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{it.name}</span>

                    <span style={{position:"absolute",top:2,right:4,fontSize:10,fontWeight:700,color:"var(--tgb)",background:"var(--bg1)",padding:"0 4px",borderRadius:8,border:"1px solid var(--bs)"}}>{avail}</span>

                  </div>

                );

              };



              if (mob) {

                // Mobile: flat list (needed items already in crafting table panel)

                if (showItems.length === 0) return <div style={{color:"var(--tm)",fontSize:13,padding:20,textAlign:"center"}}>{neededIds.size > 0 ? "Other items will appear here" : "No items available"}</div>;

                return (

                  <div>

                    {neededIds.size > 0 && <div style={{fontSize:10,color:"var(--tm)",fontFamily:"Cinzel,serif",padding:"0 0 4px",letterSpacing:1}}>📦 OTHER ITEMS</div>}

                    <div style={{display:"flex",flexWrap:"wrap",gap:6,padding:"4px 0"}}>{showItems.map((it) => renderSlot(it, false))}</div>

                  </div>

                );

              }



              if (filtered.length === 0) return <div style={{color:"var(--tm)",fontSize:13,padding:20,textAlign:"center"}}>No items available</div>;



              return (

                <div>

                  {needed.length > 0 && (

                    <>

                      <div style={{width:"100%",fontSize:10,color:"var(--tgb)",fontFamily:"Cinzel,serif",padding:"2px 0",letterSpacing:1}}>📋 RECIPE INGREDIENTS</div>

                      <div style={{display:"flex",flexWrap:"wrap",gap:6,padding:"4px 0"}}>{needed.map((it) => renderSlot(it, true))}</div>

                    </>

                  )}

                  {others.length > 0 && (

                    <>

                      {needed.length > 0 && <div style={{width:"100%",fontSize:10,color:"var(--tm)",fontFamily:"Cinzel,serif",padding:"8px 0 2px",letterSpacing:1,borderTop:"1px solid var(--bs)",marginTop:6}}>📦 OTHER ITEMS</div>}

                      <div style={{display:"flex",flexWrap:"wrap",gap:6,padding:"4px 0"}}>{others.map((it) => renderSlot(it, false))}</div>

                    </>

                  )}

                </div>

              );

            })()}

          </div>

        </div>



        {/* Crafting Table */}

        <div style={{background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"var(--rl)",overflow:"hidden",position:"relative",order:mob?0:undefined}}>

          <div style={{padding:mob?"10px 14px":"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid var(--bs)"}}>

            <span style={{fontFamily:"'Cinzel',serif",fontSize:mob?14:16,color:"var(--tg)"}}>🧪 Crafting Table</span>

            <span style={{fontSize:12,color:"var(--ts)"}}>{fcTable.length} item{fcTable.length !== 1 ? "s" : ""} on table</span>

          </div>

          <div style={{padding:mob?"12px 14px":"16px 18px"}}>

            <div onDragOver={mob?undefined:(e) => { e.preventDefault(); setFcDragOver(true); }}

              onDragLeave={mob?undefined:() => setFcDragOver(false)}

              onDrop={mob?undefined:(e) => { e.preventDefault(); setFcDragOver(false); const iid = e.dataTransfer.getData("text/plain"); if (iid) addToTable(iid); }}

              style={{position:"relative",minHeight:mob?200:320,background:fcDragOver?"rgba(201,149,26,.04)":"var(--bgi)",border:"2px dashed "+(fcDragOver?"var(--bgb)":"var(--bs)"),borderRadius:"var(--rl)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",overflow:"hidden",transition:"all .2s",boxShadow:fcDragOver?"var(--gg)":"none"}}>

              {fcTable.length === 0 ? (

                <div style={{color:"var(--tm)",textAlign:"center",fontSize:13,padding:mob?20:30}}>

                  <div style={{fontSize:mob?32:40,marginBottom:8,opacity:.5}}>🧪</div>

                  {mob ? "Tap items from your inventory to add them here" : "Click or drag items from your inventory to add them to the table"}

                </div>

              ) : (

                <>

                  <div style={{display:"flex",flexWrap:"wrap",gap:mob?6:10,padding:mob?12:20,justifyContent:"center",alignItems:"center",width:"100%"}}>

                    {fcTable.map((t, i) => {

                      const it = gi(t.iid);

                      if (!it) return null;

                      return (

                        <React.Fragment key={i}>

                          {i > 0 && <span style={{fontSize:mob?18:22,color:"var(--tm)",fontWeight:700,padding:"0 4px"}}>+</span>}

                          <div style={{width:mob?56:72,height:mob?56:72,borderRadius:"var(--rm)",border:"1px solid var(--bs)",background:"var(--bg2)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",position:"relative",cursor:"pointer",animation:"popIn .25s ease"}}>

                            <span onClick={(e) => { e.stopPropagation(); removeFromTable(i); }}

                              style={{position:"absolute",top:-3,left:-3,width:16,height:16,borderRadius:"50%",background:"rgba(140,40,40,.7)",color:"rgba(255,180,180,.9)",fontSize:12,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",border:"1px solid rgba(180,60,60,.5)"}}>−</span>

                            <span style={{fontSize:mob?22:28}}>{isGameIcon(it.icon) ? renderGameIcon(it.icon, 20, "var(--tp)") : it.icon}</span>

                            <span style={{fontSize:mob?8:9,color:"var(--ts)",textAlign:"center",lineHeight:1.1,marginTop:2,maxWidth:mob?50:66,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{it.name}</span>

                            {t.qty > 1 && <span style={{position:"absolute",top:2,right:4,fontSize:10,fontWeight:700,color:"var(--tgb)",background:"var(--bg1)",padding:"0 4px",borderRadius:8,border:"1px solid var(--bs)"}}>×{t.qty}</span>}

                          </div>

                        </React.Fragment>

                      );

                    })}

                  </div>

                  <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:mob?8:12,width:"100%",padding:mob?"10px 14px":"16px 20px",borderTop:"1px solid var(--bs)"}}>

                    <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>

                      <svg width={mob?20:26} height={mob?20:26} viewBox="0 0 24 24" fill="none" style={{opacity:.7}}>

                        <path d="M7 8.5L12 13.5L17 8.5" stroke="var(--tpu)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>

                        <path d="M7 13L12 18L17 13" stroke="var(--tg)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity=".5"/>

                      </svg>

                    </div>

                    {match ? (

                      <div style={{padding:mob?"12px 14px":"16px 20px",borderRadius:"var(--rm)",textAlign:"center",fontFamily:"'Alegreya',serif",fontWeight:600,fontSize:mob?14:16,width:"100%",maxWidth:360,background:"rgba(93,184,93,.08)",border:"1px solid var(--ag)",color:"var(--tgr)",boxShadow:"var(--gn)"}}>

                        <div style={{fontSize:mob?28:36,marginBottom:6}}>{isGameIcon(match.res.icon) ? renderGameIcon(match.res.icon, 30, "var(--tp)") : match.res.icon}</div>

                        <div style={{fontSize:mob?15:18}}>{match.res.name}</div>

                        <div style={{fontSize:12,fontWeight:400,marginTop:4,opacity:.8}}>{match.disc ? "Recipe known!" : "✨ New recipe discovery!"}</div>

                      </div>

                    ) : (

                      <div style={{padding:mob?"12px 14px":"16px 20px",borderRadius:"var(--rm)",textAlign:"center",fontFamily:"'Alegreya',serif",fontWeight:600,fontSize:mob?14:16,width:"100%",maxWidth:360,background:"var(--bgi)",border:"1px dashed var(--bs)",color:"var(--tm)"}}>

                        <div style={{fontSize:mob?28:36,marginBottom:6}}>❓</div>

                        <div style={{fontSize:mob?15:18}}>Unknown Combination</div>

                        <div style={{fontSize:12,fontWeight:400,marginTop:4,opacity:.8}}>Try combining items to discover recipes</div>

                      </div>

                    )}

                  </div>

                </>

              )}

            </div>

            {/* Mobile: Guide card + needed inventory items above buttons */}

            {mob && fcRefSel && (() => {

              const rr = gr(fcRefSel);

              if (!rr || !rr.disc) return null;

              const neededIds = new Set();

              rr.ings.forEach((ig) => neededIds.add(ig.iid));

              const sq = fcItemSearch.toLowerCase();

              const neededItems = invItems.filter((it) => {

                if (!neededIds.has(it.id)) return false;

                const avail = ph(it.id) - (tableUsed[it.id] || 0);

                if (avail <= 0) return false;

                if (sq && !it.name.toLowerCase().includes(sq)) return false;

                return true;

              });

              return (

                <div style={{background:"var(--bgi)",border:"1px solid var(--bgb)",borderRadius:"var(--rm)",padding:10,marginTop:8,position:"relative"}}>

                  <button onClick={() => setFcRefSel(null)} style={{position:"absolute",top:6,right:6,width:20,height:20,borderRadius:"50%",border:"1px solid var(--bs)",background:"var(--bg2)",color:"var(--ts)",fontSize:10,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>

                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>

                    <div style={{fontSize:20,width:36,height:36,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"var(--rs)",background:"var(--bg2)",border:"1px solid var(--bg)"}}>{isGameIcon(rr.icon) ? renderGameIcon(rr.icon, 20, "var(--tp)") : rr.icon}</div>

                    <div>

                      <div style={{fontFamily:"'Cinzel',serif",fontSize:13,color:"var(--tgb)"}}>{rr.name}</div>

                      <div style={{fontSize:11,color:"var(--ts)",fontStyle:"italic"}}>{rr.desc}</div>

                    </div>

                  </div>

                  <div style={{display:"flex",flexDirection:"column",gap:4}}>

                    {rr.ings.map((ig) => {

                      const it = gi(ig.iid), onTable = tableUsed[ig.iid] || 0;

                      const filled = onTable >= ig.qty, totalHave = ph(ig.iid), hv = totalHave - onTable;

                      const clickable = hv > 0 && !(fcSmartAdd && filled);

                      return (

                        <div key={ig.iid} onClick={() => clickable && addToTable(ig.iid)}

                          style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",borderRadius:"var(--rm)",fontSize:12,

                            border:"1px solid " + (filled ? "var(--ag)" : totalHave > 0 ? "var(--bgb)" : "var(--ar)"),

                            background: filled ? "var(--agb)" : totalHave > 0 ? "rgba(201,149,26,.04)" : "var(--arb)",

                            cursor: clickable ? "pointer" : "default",

                            animation: filled ? "gatherPop .3s ease" : "none"}}>

                          <span style={{fontSize:14}}>{it ? it.icon : "?"}</span>

                          <span style={{flex:1,minWidth:0}}>{it ? it.name : ig.iid}</span>

                          <span style={{fontWeight:700,fontSize:11,color:filled?"var(--tgr)":onTable>0?"var(--tgb)":"var(--tr)"}}>{onTable}/{ig.qty}</span>

                          <span style={{fontSize:11}}>{filled ? <span style={{color:"var(--tgr)"}}>✓</span> : clickable ? <span style={{color:"var(--tgb)"}}>Tap</span> : <span style={{color:"var(--tr)"}}>Need</span>}</span>

                        </div>

                      );

                    })}

                  </div>

                  <label style={{display:"flex",alignItems:"center",gap:5,padding:"6px 8px",marginTop:6,fontSize:11,color:"var(--ts)",cursor:"pointer",userSelect:"none"}}>

                    <input type="checkbox" checked={fcSmartAdd} onChange={(e) => setFcSmartAdd(e.target.checked)} style={{accentColor:"var(--ag)",width:12,height:12}} />

                    Cap Ingredients <span style={{opacity:.6}}>— limit to recipe amounts</span>

                  </label>

                  {/* Needed inventory items inline */}

                  {neededItems.length > 0 && (

                    <div style={{marginTop:8,borderTop:"1px solid var(--bs)",paddingTop:8}}>

                      <div style={{fontSize:10,color:"var(--tgb)",fontFamily:"Cinzel,serif",padding:"0 0 4px",letterSpacing:1}}>📋 TAP TO ADD</div>

                      <div style={{display:"flex",flexWrap:"wrap",gap:6}}>

                        {neededItems.map((it) => {

                          const avail = ph(it.id) - (tableUsed[it.id] || 0);

                          return (

                            <div key={it.id} onClick={() => addToTable(it.id)}

                              style={{width:56,height:56,borderRadius:"var(--rm)",border:"1px solid var(--bgb)",background:"var(--bg2)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",cursor:"pointer",transition:"all .2s",position:"relative",userSelect:"none"}}>

                              <span style={{fontSize:20}}>{isGameIcon(it.icon) ? renderGameIcon(it.icon, 20, "var(--tp)") : it.icon}</span>

                              <span style={{fontSize:8,color:"var(--ts)",textAlign:"center",lineHeight:1.1,marginTop:2,maxWidth:50,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{it.name}</span>

                              <span style={{position:"absolute",top:2,right:4,fontSize:10,fontWeight:700,color:"var(--tgb)",background:"var(--bg1)",padding:"0 4px",borderRadius:8,border:"1px solid var(--bs)"}}>{avail}</span>

                            </div>

                          );

                        })}

                      </div>

                    </div>

                  )}

                </div>

              );

            })()}



            <div style={{display:"flex",gap:mob?8:10,justifyContent:mob?"flex-end":"center",padding:mob?"10px 0 4px":"10px 0",flexWrap:mob?"wrap":"nowrap"}}>

              <button onClick={() => { setFcTable([]); setFcSalvageMsg(null); }} disabled={fcTable.length === 0}

                style={{padding:mob?"6px 14px":"8px 18px",borderRadius:"var(--rm)",border:"1px solid var(--bs)",background:"none",color:"var(--ts)",fontSize:mob?12:13,cursor:fcTable.length?"pointer":"not-allowed",opacity:fcTable.length?1:.4}}>🗑️ Clear</button>

              <button onClick={attemptCraft} disabled={fcTable.length === 0}

                style={{padding:mob?"8px 22px":"10px 32px",borderRadius:"var(--rm)",border:"none",fontFamily:"'Cinzel',serif",fontSize:mob?13:15,fontWeight:700,cursor:fcTable.length?"pointer":"not-allowed",

                  background: fcTable.length ? "linear-gradient(135deg,#6b3fa0,#8855cc)" : "var(--bgi)",

                  color: fcTable.length ? "#fff" : "var(--tm)",

                  boxShadow: fcTable.length ? "var(--gp)" : "none"}}>🧪 Attempt Craft</button>

            </div>



            {/* Recipe reference toggle */}

            <div style={{borderTop:"1px solid var(--bs)",marginTop:mob?4:12,paddingTop:mob?4:12,paddingBottom:mob?2:undefined}}>

              <div onClick={() => setFcShowRef(!fcShowRef)} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:mob?"4px 0":"8px 0",color:"var(--ts)",fontSize:mob?12:13,userSelect:"none"}}>

                <span style={{transition:"transform .2s",fontSize:10,transform:fcShowRef?"rotate(90deg)":"none"}}>▶</span> 📖 Recipe Book ({disc.length} known)

              </div>

              {fcShowRef && (

                <div style={{paddingTop:8,maxHeight:320,overflowY:"auto"}}>

                  {/* Recipe book search */}

                  <div style={{display:"flex",alignItems:"center",gap:8,background:"var(--bgi)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",padding:"6px 10px",marginBottom:8}}>

                    <span style={{color:"var(--tm)",fontSize:12}}>🔍</span>

                    <input value={fcRefSearch} onChange={(e) => setFcRefSearch(e.target.value)} placeholder="Search known recipes..."

                      style={{background:"none",border:"none",color:"var(--ts)",fontSize:12,flex:1,outline:0}} />

                  </div>

                  {(() => {

                    const rq = fcRefSearch.toLowerCase();

                    const filteredDisc = rq ? disc.filter((r) => r.name.toLowerCase().includes(rq) || (r.tags || []).some((t) => t.includes(rq)) || r.ings.some((i) => { const it = gi(i.iid); return it && it.name.toLowerCase().includes(rq); })) : disc;

                    return filteredDisc.length ? filteredDisc.map((r) => {

                      const haveCount = r.ings.filter((i) => ph(i.iid) >= i.qty).length;

                      const canCraft = haveCount === r.ings.length;

                      const hasNone = haveCount === 0;

                      const availClr = canCraft ? "var(--tgr)" : hasNone ? "var(--tr)" : "var(--to)";

                      return (

                        <div key={r.id} onClick={() => setFcRefSel(fcRefSel === r.id ? null : r.id)}

                          style={{display:"flex",alignItems:"flex-start",gap:10,padding:"8px 12px",background:fcRefSel===r.id?"rgba(201,149,26,.06)":"var(--bgi)",border:"1px solid "+(fcRefSel===r.id?"var(--bgb)":"var(--bs)"),borderRadius:"var(--rm)",marginBottom:4,cursor:"pointer",boxShadow:fcRefSel===r.id?"var(--gg)":"none",transition:"all .2s"}}>

                          <span style={{fontSize:20}}>{r.icon}</span>

                          <div style={{flex:1,minWidth:0}}>

                            <div style={{display:"flex",alignItems:"center",gap:6}}>

                              <span style={{fontFamily:"Alegreya,serif",fontWeight:600,fontSize:13}}>{r.name}</span>

                              <span style={{fontSize:10,padding:"1px 6px",borderRadius:"var(--rs)",border:"1px solid "+availClr,color:availClr,background:canCraft?"var(--agb)":hasNone?"var(--arb)":"rgba(212,136,58,.1)"}}>{canCraft?"✓ Ready":haveCount+"/"+r.ings.length}</span>

                            </div>

                            <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:4}}>

                              {r.ings.map((i) => {

                                const it = gi(i.iid), have = ph(i.iid), enough = have >= i.qty;

                                return (

                                  <span key={i.iid} style={{fontSize:11,padding:"2px 6px",background:"var(--bg2)",border:"1px solid "+(enough?"var(--ag)":"var(--ar)"),borderRadius:"var(--rs)",display:"flex",alignItems:"center",gap:3}}>

                                    <span style={{opacity:enough?1:.6}}>{it ? it.icon : "?"}</span> {it ? it.name : i.iid} <span style={{color:enough?"var(--tgr)":"var(--tr)",fontWeight:600}}>{have}/{i.qty}</span>

                                  </span>

                                );

                              })}

                              <span style={{fontSize:11,color:"var(--tm)",marginLeft:4,display:"flex",alignItems:"center"}}>→</span>

                              <span style={{fontSize:11,padding:"2px 6px",border:"1px solid var(--bgb)",borderRadius:"var(--rs)",color:"var(--tgb)",display:"flex",alignItems:"center",gap:3}}>{isGameIcon(r.res.icon) ? renderGameIcon(r.res.icon, 11, "var(--tgb)") : r.res.icon} {r.res.name}</span>

                            </div>

                          </div>

                        </div>

                      );

                    }) : <div style={{textAlign:"center",color:"var(--tm)",padding:12,fontSize:12}}>No recipes match</div>;

                  })()}

                </div>

              )}

            </div>

          </div>



          {/* Salvage overlay */}

          {fcSalvageMsg && fcSalvageMsg.type === "success" && (

            <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",background:"rgba(14,10,15,.92)",borderRadius:"var(--rl)",zIndex:10,animation:"fi .3s ease",padding:mob?14:20,textAlign:"center"}}>

              <div style={{fontSize:mob?36:48,marginBottom:mob?8:12}}>✨</div>

              <div style={{fontFamily:"'Cinzel',serif",fontSize:mob?15:18,marginBottom:mob?8:12,color:"var(--tgr)"}}>Craft Successful!</div>



              <div style={{background:"var(--bg2)",border:"1px solid var(--ag)",borderRadius:"var(--rl)",padding:mob?"12px 14px":"16px 20px",maxWidth:320,width:"100%",textAlign:"center"}}>

                <div style={{fontSize:mob?32:40,marginBottom:mob?6:8}}>{fcSalvageMsg.resultIcon}</div>

                <div style={{fontFamily:"'Cinzel',serif",fontSize:mob?14:16,color:"var(--tgb)",marginBottom:2}}>{fcSalvageMsg.resultName}</div>

                <div style={{display:"flex",gap:6,justifyContent:"center",alignItems:"center",marginBottom:6}}>

                  {fcSalvageMsg.resultRar && <span className={`rar-${fcSalvageMsg.resultRar.toLowerCase().replace(/\s/,"")}`} style={{fontSize:12,fontWeight:600}}>{fcSalvageMsg.resultRar}</span>}

                  {fcSalvageMsg.resultCat && <span className={`catb catb-${fcSalvageMsg.resultCat}`}>{fcSalvageMsg.resultCat}</span>}

                </div>

                {fcSalvageMsg.resultDesc && <div style={{fontSize:mob?11:12,color:"var(--ts)",lineHeight:1.5,marginBottom:8,fontStyle:"italic"}}>{fcSalvageMsg.resultDesc}</div>}

                {(fcSalvageMsg.buffs?.length > 0 || fcSalvageMsg.debuffs?.length > 0 || fcSalvageMsg.onUse?.length > 0) && (

                  <div style={{display:"flex",gap:4,flexWrap:"wrap",justifyContent:"center",marginBottom:6}}>

                    {(fcSalvageMsg.buffs||[]).map((b, i) => <span key={"b"+i} style={{padding:"2px 8px",borderRadius:3,fontSize:11,fontWeight:600,border:"1px solid var(--ag)",background:"var(--agb)",color:"var(--tgr)"}}>{b}</span>)}

                    {(fcSalvageMsg.debuffs||[]).map((b, i) => <span key={"d"+i} style={{padding:"2px 8px",borderRadius:3,fontSize:11,fontWeight:600,border:"1px solid var(--ar)",background:"var(--arb)",color:"var(--tr)"}}>{b}</span>)}

                    {(fcSalvageMsg.onUse||[]).map((b, i) => <span key={"u"+i} style={{padding:"2px 8px",borderRadius:3,fontSize:11,fontWeight:600,border:"1px solid rgba(58,107,154,.4)",background:"rgba(58,107,154,.12)",color:"#4a9ad4"}}>⚡ {b}</span>)}

                  </div>

                )}

                {fcSalvageMsg.abilities?.length > 0 && fcSalvageMsg.abilities.map((a, i) => (

                  <div key={i} style={{padding:"5px 8px",background:"var(--bgi)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",marginBottom:4,textAlign:"left"}}>

                    <div style={{fontFamily:"Cinzel,serif",fontSize:11,color:"var(--tpu)"}}>{a.name}</div>

                    {a.desc && <div style={{fontSize:10,color:"var(--ts)",marginTop:1}}>{a.desc}</div>}

                    {a.effects && <div style={{marginTop:2}}><span style={{padding:"1px 6px",borderRadius:3,fontSize:10,fontWeight:600,border:"1px solid var(--ap)",background:"var(--apb)",color:"var(--tpu)"}}>{a.effects}</span></div>}

                    {a.limited && <div style={{fontSize:9,color:"var(--tm)",marginTop:2}}>Uses: {a.cur}/{a.max}{a.reset && a.reset !== "None" ? " · Resets: " + a.reset : ""}</div>}

                  </div>

                ))}

                {fcSalvageMsg.uses && <div style={{fontSize:11,color:"var(--tm)",marginTop:4}}>Uses: {fcSalvageMsg.uses.cur}/{fcSalvageMsg.uses.max}{fcSalvageMsg.uses.reset && fcSalvageMsg.uses.reset !== "None" ? " · Resets: " + fcSalvageMsg.uses.reset : ""}</div>}

                {fcSalvageMsg.notes && <div style={{fontSize:11,color:"var(--ts)",marginTop:4,fontStyle:"italic"}}>📝 {fcSalvageMsg.notes}</div>}

                {fcSalvageMsg.price > 0 && <div style={{fontSize:11,color:"var(--tg)",marginTop:4}}>💰 {fcSalvageMsg.price} gp</div>}

              </div>



              <button onClick={() => setFcSalvageMsg(null)} style={{padding:"8px 24px",borderRadius:"var(--rm)",border:"none",background:"var(--ap)",color:"#fff",fontFamily:"'Cinzel',serif",fontSize:13,cursor:"pointer",marginTop:mob?10:14}}>OK</button>

            </div>

          )}

          {fcSalvageMsg && fcSalvageMsg.type === "fail" && (

            <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",background:"rgba(14,10,15,.92)",borderRadius:"var(--rl)",zIndex:10,animation:"fi .3s ease",padding:mob?14:20,textAlign:"center"}}>

              <div style={{fontSize:mob?36:48,marginBottom:mob?8:12}}>💥</div>

              <div style={{fontFamily:"'Cinzel',serif",fontSize:mob?15:18,marginBottom:8,color:"var(--tr)"}}>{fcSalvageMsg.title}</div>

              <div style={{fontSize:mob?12:13,color:"var(--ts)",maxWidth:280,lineHeight:1.5}}>{fcSalvageMsg.body}</div>

              {fcSalvageMsg.destroyed?.length > 0 && (

                <div style={{marginTop:8}}>

                  <div style={{fontSize:11,color:"var(--tr)"}}>💀 Destroyed:</div>

                  <div style={{display:"flex",gap:mob?4:6,justifyContent:"center",margin:"6px 0",flexWrap:"wrap"}}>

                    {fcSalvageMsg.destroyed.map((d, i) => { const it = gi(d.iid); return it ? <span key={i} style={{padding:mob?"3px 8px":"4px 10px",background:"var(--bg2)",border:"1px solid var(--ar)",borderRadius:"var(--rm)",fontSize:mob?12:13}}>{it.icon} {it.name}{d.qty>1?" ×"+d.qty:""}</span> : null; })}

                  </div>

                </div>

              )}

              {fcSalvageMsg.salvaged?.length > 0 && (

                <div style={{marginTop:4}}>

                  <div style={{fontSize:11,color:"var(--tgr)"}}>♻️ Salvaged:</div>

                  <div style={{display:"flex",gap:mob?4:6,justifyContent:"center",margin:"6px 0",flexWrap:"wrap"}}>

                    {fcSalvageMsg.salvaged.map((d, i) => { const it = gi(d.iid); return it ? <span key={i} style={{padding:mob?"3px 8px":"4px 10px",background:"var(--bg2)",border:"1px solid var(--ag)",borderRadius:"var(--rm)",fontSize:mob?12:13}}>{it.icon} {it.name}{d.qty>1?" ×"+d.qty:""}</span> : null; })}

                  </div>

                </div>

              )}

              <button onClick={() => setFcSalvageMsg(null)} style={{padding:"8px 24px",borderRadius:"var(--rm)",border:"none",background:"var(--ap)",color:"#fff",fontFamily:"'Cinzel',serif",fontSize:13,cursor:"pointer",marginTop:12}}>OK</button>

            </div>

          )}

        </div>

      </div>

    );

  };



  // ─── PLAYER: CRAFTED LOG ────────────────────────────────

  // 2026-04-22: PlayerCraftLog reworked into "🛠 Crafting Table" — shows

  // the current full inv (items with qty>0) and provides the reverse

  // transfer (table → inventory) per entry. The original craft-history

  // log is preserved as a collapsible section at the bottom.

  const PlayerCraftLog = () => {

    const [tableTransferQtyById, setTableTransferQtyById] = useState({});

    // Join items + inv into a flat list of present entries.

    const tableEntries = items

      .map(it => ({ ...it, qty: inv[it.id] || 0 }))

      .filter(e => e.qty > 0)

      .sort((a, b) => {

        // Group by category with a stable order, then name.

        if (a.cat !== b.cat) return String(a.cat || '').localeCompare(String(b.cat || ''));

        return String(a.name || '').localeCompare(String(b.name || ''));

      });



    const clampTableTransferQty = (value, maxQty) => (
      Math.max(1, Math.min(Math.max(1, Number(maxQty) || 1), Math.floor(Number(value) || 1)))
    );

    const getTableTransferQty = (tableItem) => {
      const maxQty = Math.max(1, Number(tableItem?.qty) || 1);
      return clampTableTransferQty(tableTransferQtyById[tableItem?.id], maxQty);
    };

    const setTableTransferQty = (tableItem, value) => {
      if (!tableItem?.id) return;
      const maxQty = Math.max(1, Number(tableItem.qty) || 1);
      const nextQty = clampTableTransferQty(value, maxQty);
      setTableTransferQtyById(prev => ({ ...prev, [tableItem.id]: nextQty }));
    };

    const updateTableTransferQtyAfterMove = (tableItem, remainingQty) => {
      if (!tableItem?.id) return;
      setTableTransferQtyById(prev => {
        if (!Object.prototype.hasOwnProperty.call(prev, tableItem.id)) return prev;
        const next = { ...prev };
        if (remainingQty <= 0) delete next[tableItem.id];
        else next[tableItem.id] = clampTableTransferQty(next[tableItem.id], remainingQty);
        return next;
      });
    };

    const buildTableInvAfterTransfer = (tableItem, moveQty) => {
      const newInv = { ...inv };
      const currentQty = Math.max(0, Number(inv?.[tableItem.id]) || Number(tableItem.qty) || 0);
      const remainingQty = Math.max(0, currentQty - moveQty);
      if (remainingQty > 0) newInv[tableItem.id] = remainingQty;
      else delete newInv[tableItem.id];
      return { newInv, remainingQty };
    };

    const resolveTableItemTransferResult = (tableItem) => {

      // Build a res-shaped payload that craftResultToInventoryItem can

      // consume. Three preservation paths, in priority order:

      //  1. _res — full recipe result preserved at craft time (stats, abilities, uses)

      //  2. _invSnapshot — original inventory item preserved at inventory→table transfer

      //  3. fallback — minimal shape (just name/icon/cat); stats start fresh

      let res;

      if (tableItem._res) {

        // Crafted item — recipe.res has everything: buffs, debuffs, onUse,

        // uses, abilities, dmNotes, etc. Use as-is.

        res = tableItem._res;

      } else if (tableItem._invSnapshot) {

        // Round-trip item from inventory. Reconstruct res-shape from the

        // inventory snapshot (key-rename for uses).

        const snap = tableItem._invSnapshot;

        res = {

          name: snap.name,

          icon: tableItem.icon,

          cat: tableItem.cat,

          buffs: snap.buffs || [],

          debuffs: snap.debuffs || [],

          onUse: snap.onUseEffects || [],

          notes: snap.notes || '',

          abilities: snap.abilities || [],

          uses: snap.uses

            ? { cur: snap.uses.current, max: snap.uses.max, reset: snap.uses.resetType }

            : null,

        };

      } else {

        // Basic material — carry over only identity + category.

        res = {

          name: tableItem.name,

          icon: tableItem.icon,

          cat: tableItem.cat,

          buffs: [], debuffs: [], onUse: [], notes: '', abilities: [], uses: null,

        };

      }

      return res;

    };

    const transferTableItemToInventory = async (tableItem, qty = null) => {

      if (!onAddToInventory || !tableItem) return;

      const moveQty = clampTableTransferQty(qty ?? getTableTransferQty(tableItem), tableItem.qty);
      const res = resolveTableItemTransferResult(tableItem);

      // Compute the post-transfer inv so onAddToInventory can save both slots

      // atomically (eliminates race with the debounced craftingTable effect).

      const { newInv, remainingQty } = buildTableInvAfterTransfer(tableItem, moveQty);

      try {
        const result = await awaitMaybePromise(onAddToInventory(withCraftingTableTransferMetadata(res, tableItem.id), moveQty, newInv));
        if (result?.mode) skipNextCraftingTablePersistRef.current = true;
        setInv(newInv);
        updateTableTransferQtyAfterMove(tableItem, remainingQty);
      } catch (e) {
        console.error('table item transfer to inventory failed', e);
        toast(getMovementAuthorityFailureMessage(e, `Transfer failed: ${tableItem.name} stayed on the crafting table.`));
      }

    };

    const transferTableItemToParty = async (tableItem, qty = null) => {

      if (!onAddToParty || !tableItem) return;

      const moveQty = clampTableTransferQty(qty ?? getTableTransferQty(tableItem), tableItem.qty);
      const res = resolveTableItemTransferResult(tableItem);
      const { newInv, remainingQty } = buildTableInvAfterTransfer(tableItem, moveQty);

      try {
        const result = await awaitMaybePromise(onAddToParty(withCraftingTableTransferMetadata(res, tableItem.id), moveQty, newInv));
        if (result?.mode) skipNextCraftingTablePersistRef.current = true;
        setInv(newInv);
        updateTableTransferQtyAfterMove(tableItem, remainingQty);

        toast(`Deposited ${tableItem.name}${moveQty > 1 ? ` x${moveQty}` : ''} to party pool`);

      } catch (err) {
        console.error('table item transfer to party failed', err);
        toast(getMovementAuthorityFailureMessage(err, `Transfer failed: ${tableItem.name} stayed on the crafting table.`));
      }

    };



    // 2026-04-27 (later): targets reduced to "realistic" stack sizes.

    // Earlier targets were 4–12 per material; combined with the

    // previous additive +500 semantics, dev inventories ended up with

    // visibly absurd 400+ counts that didn't fit a survival-game vibe.

    // Now we ABSOLUTE-set each target (not top-up): clicking Restock

    // resets the materials to these values, which doubles as a "clean

    // up bloated stacks from earlier dev testing" affordance. The

    // gating property (Cloak / Crystal Focus stay uncraftable) holds.

    const RESTOCK_TARGETS = {

      monster_core: 8,    // Flamebrand needs 3; one-and-spare

      mandrake_root: 14,  // Flamebrand 2 + Salve 1 = 3

      iron_ingot: 16,     // Flamebrand needs 2

      fire_essence: 2,    // Flamebrand needs 1

      herb_bundle: 16,    // Salve 2 + Rations 1 each, two of each

      empty_vial: 14,     // Salve needs 1

      raw_meat: 4,        // Rations needs 1

      branch: 6,          // Log Bundle×2

      log: 3,             // Firewood×1

      // Below recipe thresholds — keep these gated

      spider_silk: 2,     // Cloak needs 4

      wolf_pelt: 1,       // Cloak needs 2

      leather_strip: 10,  // Cloak needs 3

      crystal_shard: 6,   // Crystal Focus needs 5

    };

    const restockMaterials = () => {

      // Absolute-set: clicking Restock resets the table to RESTOCK_TARGETS.

      // Keeps any non-default materials the user has on the table.

      setInv(prev => ({ ...prev, ...RESTOCK_TARGETS }));

      toast(`🧪 Materials reset to dev defaults`);

    };



    return (

      <div style={{background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"var(--rl)",overflow:"hidden"}}>

        <div style={{padding:mob?"10px 14px":"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid var(--bs)",gap:8,flexWrap:"wrap"}}>

          <span style={{fontFamily:"'Cinzel',serif",fontSize:mob?14:16,color:"var(--tg)"}}>🛠 Crafting Table</span>

          <div style={{display:"flex",alignItems:"center",gap:10}}>

            <span style={{fontSize:12,color:"var(--ts)"}}>{tableEntries.length} unique · {tableEntries.reduce((s, e) => s + e.qty, 0)} total</span>

            <button

              onClick={restockMaterials}

              title="Dev: refill the basic materials (monster cores, iron, herbs, etc.) so testing doesn't stall. Remove before production launch."

              style={{padding:"4px 10px",borderRadius:"var(--rs)",border:"1px solid rgba(201,162,39,0.4)",background:"rgba(201,162,39,0.12)",color:"#c9a227",fontSize:11,cursor:"pointer",fontWeight:600,whiteSpace:"nowrap"}}>

              🧪 Dev: Restock Materials

            </button>

          </div>

        </div>

        <div style={{padding:mob?"12px 14px":"16px 18px",display:"flex",flexDirection:"column",gap:6}}>

          {tableEntries.length === 0 && (

            <div style={{display:"flex",flexDirection:"column",alignItems:"center",padding:mob?"40px 16px":"60px 20px",color:"var(--tm)"}}>

              <div style={{fontSize:mob?36:48,marginBottom:12,opacity:.5}}>🛠</div>

              <p>Your crafting table is empty.</p>

              <p style={{fontSize:11,opacity:.7,marginTop:4}}>Craft items with "Keep on table" or move items here from your inventory.</p>

            </div>

          )}

          {tableEntries.map(e => {
            const transferQty = getTableTransferQty(e);
            return (

            <div key={e.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",flexWrap:"wrap"}}>

              <span style={{fontSize:22}}>{isGameIcon(e.icon) ? renderGameIcon(e.icon, 22, "var(--tp)") : e.icon}</span>

              <div style={{flex:1,minWidth:0}}>

                <div style={{fontFamily:"Alegreya,serif",fontWeight:600,fontSize:13}}>{e.name}{e.qty > 1 ? ` ×${e.qty}` : ""}</div>

                <div style={{fontSize:11,color:"var(--ts)"}}>{e.cat}</div>

              </div>

              <div style={{display:"flex",gap:6,flexWrap:"wrap",justifyContent:"flex-end",alignItems:"center"}}>

                {e.qty > 1 && (

                  <div
                    aria-label={`Transfer quantity for ${e.name}`}
                    style={{display:"flex",alignItems:"center",gap:3,padding:"2px",border:"1px solid var(--bs)",borderRadius:"var(--rs)",background:"rgba(0,0,0,.18)"}}
                  >

                    <button
                      type="button"
                      aria-label={`Decrease ${e.name} transfer quantity`}
                      onClick={() => setTableTransferQty(e, transferQty - 1)}
                      disabled={transferQty <= 1}
                      style={{width:24,height:24,border:"none",borderRight:"1px solid var(--bs)",background:"transparent",color:transferQty <= 1 ? "var(--tm)" : "var(--tp)",cursor:transferQty <= 1 ? "not-allowed" : "pointer",fontWeight:700}}
                    >
                      -
                    </button>

                    <input
                      aria-label={`Transfer quantity value for ${e.name}`}
                      type="number"
                      min="1"
                      max={e.qty}
                      value={transferQty}
                      onChange={(event) => setTableTransferQty(e, event.target.value)}
                      style={{width:Math.max(40, String(e.qty).length * 12),height:24,textAlign:"center",border:"none",background:"transparent",color:"var(--tp)",fontWeight:700,fontSize:12}}
                    />

                    <button
                      type="button"
                      aria-label={`Increase ${e.name} transfer quantity`}
                      onClick={() => setTableTransferQty(e, transferQty + 1)}
                      disabled={transferQty >= e.qty}
                      style={{width:24,height:24,border:"none",borderLeft:"1px solid var(--bs)",background:"transparent",color:transferQty >= e.qty ? "var(--tm)" : "var(--tp)",cursor:transferQty >= e.qty ? "not-allowed" : "pointer",fontWeight:700}}
                    >
                      +
                    </button>

                    <button
                      type="button"
                      aria-label={`Transfer all ${e.name}`}
                      onClick={() => setTableTransferQty(e, e.qty)}
                      style={{height:24,padding:"0 6px",border:"none",borderLeft:"1px solid var(--bs)",background:"transparent",color:"var(--tgb)",cursor:"pointer",fontSize:10,fontWeight:700}}
                    >
                      All
                    </button>

                  </div>

                )}

                {onAddToInventory && (

                  <button

                    aria-label={`Move ${e.name} to inventory`}

                    onClick={() => transferTableItemToInventory(e, transferQty)}

                    title={e.qty > 1 ? `Move ${transferQty} of ${e.qty} to your character's inventory` : "Move this stack to your character's inventory"}

                    style={{padding:"4px 10px",borderRadius:"var(--rs)",border:"1px solid var(--bgb)",background:"var(--bg1)",color:"var(--tgb)",fontSize:11,cursor:"pointer",fontWeight:600,whiteSpace:"nowrap"}}>

                    📦 To Inventory

                  </button>

                )}

                {/* 2026-04-23: deposit to shared party pool. Reuses the

                    reverse-transfer logic: rebuild a res-shape from _res /

                    _invSnapshot / fallback, then the parent's onAddToParty

                    stacks into partyInventory.items. */}

                {onAddToParty && (

                  <button

                    aria-label={`Move ${e.name} to party`}

                    onClick={() => transferTableItemToParty(e, transferQty)}

                    title={e.qty > 1 ? `Deposit ${transferQty} of ${e.qty} into the shared party inventory` : "Deposit this stack into the shared party inventory"}
                    style={{padding:"4px 10px",borderRadius:"var(--rs)",border:"1px solid rgba(58,107,154,.4)",background:"rgba(58,107,154,.12)",color:"#4a9ad4",fontSize:11,cursor:"pointer",fontWeight:600,whiteSpace:"nowrap"}}>

                    📤 To Party

                  </button>

                )}

              </div>

            </div>

            );
          })}

        </div>

        {craftLog.length > 0 && (

          <div style={{borderTop:"1px solid var(--bs)",padding:mob?"10px 14px":"14px 18px"}}>

            <div style={{fontFamily:"'Cinzel',serif",fontSize:mob?12:13,color:"var(--ts)",marginBottom:6}}>📜 Recent Crafts</div>

            <div style={{display:"flex",flexDirection:"column",gap:4}}>

              {[...craftLog].reverse().slice(0, 10).map((e) => {

                const ago = Math.round((Date.now() - e.time) / 1000);

                const ts = ago < 60 ? ago + "s ago" : ago < 3600 ? Math.round(ago / 60) + "m ago" : Math.round(ago / 3600) + "h ago";

                return (

                  <div key={e.id} style={{display:"flex",alignItems:"center",gap:8,fontSize:11,color:"var(--ts)"}}>

                    <span>{e.icon}</span>

                    <span style={{flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.name}{e.qty > 1 ? " ×" + e.qty : ""}</span>

                    <span style={{opacity:.7}}>{ts}</span>

                  </div>

                );

              })}

            </div>

          </div>

        )}

      </div>

    );

  };



  // ─── PLAYER: RECIPE SHOP (Phase 4A) ─────────────────────

  // 2026-04-28: trainer-NPC-style recipe shop. Shows SHOP_ENTRIES

  // (curated module-scope list), gates by profession level, and lets

  // the player buy unknown recipes for GP. Buying:

  //   - deducts char.wallet.gold (with talent shopDiscount applied)

  //   - pushes recipe.id into char.learnedRecipes

  //   - the recipe-discovery resolver (isRecipeKnown) makes the recipe

  //     appear in the player's Recipe Crafting tab from then on

  // Out of scope for MVP: scrolls strip, learn animation, MP currency,

  // shop rotation, DM editor — see Phase 4B.

  const PlayerRecipeShop = () => {

    const [shopSel, setShopSel] = useState(null); // selected entry's recipeId

    const [shopFilter, setShopFilter] = useState('all'); // 'all' | 'affordable' | profId

    const [pendingBuy, setPendingBuy] = useState(null); // recipeId in confirm modal



    // Talent shopDiscount aggregate across the recipe's profession.

    // Returns 0 when feature off / no profession bound / no ranks spent.

    const discountForRecipe = (r) => {

      if (!profsEnabled) return 0;

      const profId = r?.profReq?.profId;

      if (!profId) return 0;

      const prof = DEFAULT_PROFESSIONS.find(p => p.id === profId);

      const player = playerProfsState[profId];

      if (!prof || !player) return 0;

      const eff = sumTalentEffects(prof, player);

      return eff.shopDiscountPct || 0;

    };



    const finalPrice = (basePrice, r) => {

      const pct = discountForRecipe(r);

      if (pct <= 0) return basePrice;

      return Math.max(1, Math.round(basePrice * (1 - pct / 100)));

    };



    const wallet = char?.wallet || {};

    const gold = wallet.gold || 0;



    // Build the visible entry list — join SHOP_ENTRIES with the recipe

    // catalog. Skip entries whose recipe is missing.

    const entries = SHOP_ENTRIES

      .map(e => {

        const r = recipes.find(x => x.id === e.recipeId);

        if (!r) return null;

        const owned = isRecipeKnown(r, char);

        const profCheck = profsEnabled ? checkProfReq(r, playerProfsState, DEFAULT_PROFESSIONS) : { ok: true };

        const price = finalPrice(e.gpPrice, r);

        const baseDiscount = discountForRecipe(r);

        const canAfford = gold >= price;

        const buyable = !owned && profCheck.ok && canAfford;

        return { entry: e, recipe: r, owned, profCheck, price, baseDiscount, canAfford, buyable };

      })

      .filter(Boolean);



    // Apply filter chip.

    const filtered = entries.filter(it => {

      if (shopFilter === 'all') return true;

      if (shopFilter === 'affordable') return it.buyable;

      // profession id filter

      return it.recipe.profReq?.profId === shopFilter;

    });



    // Profession chips — only show professions the shop actually

    // contains entries for (clean affordance).

    const shopProfs = Array.from(new Set(entries

      .map(it => it.recipe.profReq?.profId)

      .filter(Boolean)))

      .map(id => DEFAULT_PROFESSIONS.find(p => p.id === id))

      .filter(Boolean);



    const selected = shopSel ? entries.find(it => it.recipe.id === shopSel) : null;



    const handleBuy = (recipeId) => {

      const it = entries.find(e => e.recipe.id === recipeId);

      if (!it || !it.buyable || !onUpdateChar) return;

      const nextLearned = Array.isArray(char.learnedRecipes) ? [...char.learnedRecipes] : [];

      if (!nextLearned.includes(recipeId)) nextLearned.push(recipeId);

      const nextWallet = { ...wallet, gold: Math.max(0, gold - it.price) };

      onUpdateChar({ wallet: nextWallet, learnedRecipes: nextLearned });

      setPendingBuy(null);

      // Land back on the same row so the user sees the "Owned" state

      // flip in place.

      toast(`📜 Learned ${it.recipe.name} (−${it.price} gp)`);

    };



    return (

      <div className="shop-lay">

        {/* LEFT: NPC card + wallet + filters + list */}

        <div>

          <div className="shop-hdr">

            <div className="shop-npc-ico">🧙</div>

            <div className="shop-npc-info">

              <div className="shop-npc-title">The Wandering Scribe</div>

              <div className="shop-npc-sub">Recipes, scrolls, and the secrets of the trade.</div>

            </div>

          </div>

          <div className="shop-wallet">

            <div className="shop-wallet-item gp">

              <span className="shop-wallet-ico">🪙</span>

              <span className="shop-wallet-amount">{gold.toLocaleString()}</span>

              <span style={{ fontSize: 10, opacity: 0.7, marginLeft: 2 }}>GP</span>

            </div>

            <span style={{ fontSize: 11, color: 'var(--tm)', fontStyle: 'italic' }}>

              {entries.length} recipe{entries.length === 1 ? '' : 's'} for sale

            </span>

          </div>



          {/* Filter chips (reuse .pf-chip from professions CSS) */}

          <div className="prof-filter">

            <span className={`pf-chip${shopFilter === 'all' ? ' on' : ''}`} onClick={() => setShopFilter('all')}>All</span>

            <span className={`pf-chip${shopFilter === 'affordable' ? ' on' : ''}`} onClick={() => setShopFilter('affordable')}>🪙 Affordable</span>

            {shopProfs.map(p => (

              <span key={p.id} className={`pf-chip${shopFilter === p.id ? ' on' : ''}`} onClick={() => setShopFilter(p.id)}>

                {p.icon} {p.name}

              </span>

            ))}

          </div>



          {/* List */}

          <div className="shop-list">

            {filtered.length === 0 ? (

              <div className="shop-list-empty">

                <span className="emb">🪶</span>

                <div>No recipes match this filter.</div>

              </div>

            ) : filtered.map(it => {

              const r = it.recipe;

              const isSel = shopSel === r.id;

              const profDef = r.profReq ? DEFAULT_PROFESSIONS.find(p => p.id === r.profReq.profId) : null;

              const reqText = profDef

                ? `${profDef.icon} ${profDef.name} Lv ${r.profReq.minLevel}`

                : 'No requirement';

              const reqClass = it.profCheck.ok ? 'req-hi' : 'req-lo';

              return (

                <div key={r.id}

                  className={`shop-row${isSel ? ' selected' : ''}${!it.profCheck.ok ? ' locked' : ''}${it.owned ? ' owned' : ''}`}

                  onClick={() => setShopSel(r.id)}>

                  <div className="shop-row-ico">{r.icon}</div>

                  <div className="shop-row-body">

                    <div className="shop-row-name">{r.name}</div>

                    <div className="shop-row-req"><span className={reqClass}>{reqText}</span></div>

                  </div>

                  {it.owned ? (

                    <div className="shop-row-price owned">✓ Known</div>

                  ) : !it.profCheck.ok ? (

                    <span className="shop-row-locked-ico">🔒</span>

                  ) : (

                    <div className={`shop-row-price${it.canAfford ? '' : ' cant'}`}>

                      <span>🪙</span>{it.price}

                    </div>

                  )}

                </div>

              );

            })}

          </div>

        </div>



        {/* RIGHT: detail panel */}

        <div className="shop-detail">

          {!selected ? (

            <div className="shop-detail-empty">

              <span className="big">📜</span>

              <div className="t">Select a recipe to inspect</div>

            </div>

          ) : (() => {

            const r = selected.recipe;

            const profDef = r.profReq ? DEFAULT_PROFESSIONS.find(p => p.id === r.profReq.profId) : null;

            return (

              <>

                <div className="sd-hdr">

                  <div className="sd-ico">{r.icon}</div>

                  <div className="sd-info">

                    <div className="sd-name">{r.name}</div>

                    <div className="sd-sub">{r.desc}</div>

                    <div className="sd-tags">

                      <span className="sd-tag">{r.cat}</span>

                      {(r.tags || []).map(t => <span key={t} className="sd-tag">{t}</span>)}

                    </div>

                  </div>

                </div>



                {/* Requirements */}

                {profDef && (

                  <div className="sd-section">

                    <div className="sd-section-hdr">REQUIREMENTS</div>

                    <div className={`sd-req-row${selected.profCheck.ok ? ' met' : ' unmet'}`}>

                      <span className="sd-req-ico">{profDef.icon}</span>

                      <span className="sd-req-text">{profDef.name} · Level {r.profReq.minLevel}</span>

                      <span className={`sd-req-status ${selected.profCheck.ok ? 'met' : 'unmet'}`}>

                        {selected.profCheck.ok ? '✓ MET' : '✗ ' + (playerProfsState[r.profReq.profId]?.level ? `Lv ${playerProfsState[r.profReq.profId].level}` : 'Not learned')}

                      </span>

                    </div>

                  </div>

                )}



                {/* Components (read-only display so the player can plan) */}

                <div className="sd-section">

                  <div className="sd-section-hdr">COMPONENTS</div>

                  {(r.ings || []).map(ig => {

                    const it = items.find(x => x.id === ig.iid);

                    const have = inv[ig.iid] || 0;

                    const enough = have >= ig.qty;

                    return (

                      <div key={ig.iid} className={`sd-comp${enough ? ' owned' : ''}`}>

                        <span className="sd-comp-ico">{it ? it.icon : '?'}</span>

                        <span className="sd-comp-name">{it ? it.name : ig.iid}</span>

                        <span className={`sd-comp-count ${enough ? 'have' : 'need'}`}>{have} / {ig.qty}</span>

                      </div>

                    );

                  })}

                </div>



                {/* Buy row */}

                <div className="sd-buy-row">

                  {selected.baseDiscount > 0 && !selected.owned && (

                    <span className="sd-price-chip discount" title="Talent discount applied">

                      −{selected.baseDiscount}% from talents

                    </span>

                  )}

                  <span className="sd-price-chip gp">

                    🪙 {selected.price} GP

                  </span>

                  {selected.owned ? (

                    <button className="sd-buy-btn dis" disabled>✓ Already Known</button>

                  ) : !selected.profCheck.ok ? (

                    <button className="sd-buy-btn dis" disabled>🔒 Level Required</button>

                  ) : !selected.canAfford ? (

                    <button className="sd-buy-btn dis" disabled>💸 Not Enough GP</button>

                  ) : (

                    <button className="sd-buy-btn" onClick={() => setPendingBuy(r.id)}>📜 Buy Recipe</button>

                  )}

                </div>

              </>

            );

          })()}

        </div>



        {/* Confirm modal */}

        {pendingBuy && (() => {

          const it = entries.find(e => e.recipe.id === pendingBuy);

          if (!it) return null;

          return (

            <div className="pconfirm-backdrop" onClick={() => setPendingBuy(null)}>

              <div className="pconfirm" onClick={e => e.stopPropagation()}>

                <div className="pconfirm-title">Confirm Purchase</div>

                <div className="pconfirm-recipe">

                  <span className="pc-ico">{it.recipe.icon}</span>

                  <div className="pc-name">{it.recipe.name}</div>

                </div>

                <div className="pconfirm-body">

                  Spend <b style={{ color: 'var(--tgb)' }}>{it.price} GP</b> to learn this recipe?

                  <div style={{ marginTop: 6, fontSize: 11, color: 'var(--tm)' }}>

                    Wallet after: {Math.max(0, gold - it.price).toLocaleString()} GP

                  </div>

                </div>

                <div className="pconfirm-btns">

                  <button className="pconfirm-cancel" onClick={() => setPendingBuy(null)}>Cancel</button>

                  <button className="pconfirm-confirm" onClick={() => handleBuy(pendingBuy)}>Confirm</button>

                </div>

              </div>

            </div>

          );

        })()}

      </div>

    );

  };



  // ─── PLAYER: PROXIMITY ──────────────────────────────────

  const PlayerProximity = () => {

    const prox = recipes.filter((r) => r.vis || r.disc).map((r) => {

      let completedTypes = 0;

      const ings = r.ings.map((ig) => {

        const hv = ph(ig.iid), nd = ig.qty;

        if (hv >= nd) completedTypes++;

        return { iid: ig.iid, qty: nd, have: hv, ok: hv >= nd, partial: hv > 0 && hv < nd };

      });

      const pct = r.ings.length > 0 ? Math.round((completedTypes / r.ings.length) * 100) : 0;

      return { r, ings, pct, completedTypes, totalTypes: r.ings.length, craftable: completedTypes === r.ings.length };

    }).sort((a, b) => b.pct - a.pct);



    const known = prox.filter((p) => p.r.disc);

    const unknown = prox.filter((p) => !p.r.disc);



    const ProxCard = ({ p, isUnknown }) => {

      const r = p.r;

      const barColor = p.pct === 100 ? "var(--tgr)" : p.pct >= 50 ? "var(--tgb)" : p.pct > 0 ? "var(--to)" : isUnknown ? "var(--tm)" : "var(--tr)";

      const hl = isUnknown ? (r.hintLevel || hintCfg.defaultHint) : "full";

      const showBar = !isUnknown || hl !== "none" || (r.revealedIngs || []).length > 0;

      const reveals = r.revealedIngs || [];



      const IngPill = ({ ig }) => {

        const it = gi(ig.iid);

        return (

          <span style={{fontSize:11,padding:"2px 6px",borderRadius:"var(--rs)",border:"1px solid "+(ig.ok?"var(--ag)":ig.partial?"var(--bgb)":"var(--ar)"),

            background:ig.ok?"var(--agb)":ig.partial?"rgba(201,149,26,.08)":"var(--arb)",

            color:ig.ok?"var(--tgr)":ig.partial?"var(--tgb)":"var(--tr)",

            display:"flex",alignItems:"center",gap:3}}>

            {it?it.icon:"?"} {it?it.name:ig.iid} <strong>{ig.have}/{ig.qty}</strong>

          </span>

        );

      };



      return (

        <div style={{padding:"12px 14px",background:"var(--bgi)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",marginBottom:8,borderStyle:isUnknown?"dashed":"solid"}}>

          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>

            <span style={{fontSize:22}}>{isUnknown ? "❓" : (isGameIcon(r.icon) ? renderGameIcon(r.icon, 22, "var(--tp)") : r.icon)}</span>

            <span style={{fontFamily:"'Alegreya',serif",fontWeight:600,fontSize:14,flex:1,...(isUnknown?{color:"var(--ts)",fontStyle:"italic"}:{})}}>{isUnknown ? "? Unknown Recipe" : r.name}</span>

            {showBar && <span style={{fontFamily:"'Cinzel',serif",fontSize:13,fontWeight:700,minWidth:48,textAlign:"right",color:barColor}}>{p.pct}%</span>}

          </div>

          {showBar && (

            <div style={{height:6,background:"var(--bg1)",borderRadius:3,overflow:"hidden",marginBottom:8}}>

              <div style={{height:"100%",borderRadius:3,width:p.pct+"%",background:barColor,transition:"width .3s"}} />

            </div>

          )}

          {!isUnknown && (

            <div style={{display:"flex",flexWrap:"wrap",gap:4}}>

              {p.ings.map((ig) => <IngPill key={ig.iid} ig={ig} />)}

            </div>

          )}

          {isUnknown && hl === "count" && (

            <div>

              <div style={{fontSize:12,color:"var(--ts)",marginTop:4}}>🧩 {p.completedTypes} of {p.totalTypes} ingredient types gathered</div>

              {reveals.length > 0 && (

                <div style={{display:"flex",flexWrap:"wrap",gap:4,marginTop:6}}>

                  {p.ings.filter((ig) => reveals.includes(ig.iid)).map((ig) => <IngPill key={ig.iid} ig={ig} />)}

                </div>

              )}

            </div>

          )}

          {isUnknown && hl === "none" && reveals.length > 0 && (

            <div style={{display:"flex",flexWrap:"wrap",gap:4,marginTop:4}}>

              {p.ings.filter((ig) => reveals.includes(ig.iid)).map((ig) => <IngPill key={ig.iid} ig={ig} />)}

              {p.ings.filter((ig) => !reveals.includes(ig.iid)).map((ig) => (

                <span key={ig.iid} style={{fontSize:11,padding:"2px 6px",borderRadius:"var(--rs)",border:"1px solid var(--bs)",background:"var(--bg2)",color:"var(--tm)",display:"flex",alignItems:"center",gap:3}}>❓ ?</span>

              ))}

            </div>

          )}

          {isUnknown && hl === "have" && (

            <div style={{display:"flex",flexWrap:"wrap",gap:4,marginTop:4}}>

              {p.ings.filter((ig) => ig.have > 0 || reveals.includes(ig.iid)).map((ig) => <IngPill key={ig.iid} ig={ig} />)}

            </div>

          )}

          {isUnknown && hl === "have_mystery" && (

            <div style={{display:"flex",flexWrap:"wrap",gap:4,marginTop:4}}>

              {p.ings.map((ig) => {

                if (ig.have > 0 || reveals.includes(ig.iid)) return <IngPill key={ig.iid} ig={ig} />;

                return <span key={ig.iid} style={{fontSize:11,padding:"2px 6px",borderRadius:"var(--rs)",border:"1px solid var(--ar)",background:"var(--arb)",color:"var(--tr)",display:"flex",alignItems:"center",gap:3}}>❓ ? <strong>?/?</strong></span>;

              })}

            </div>

          )}

        </div>

      );

    };



    return (

      <div style={{background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"var(--rl)",overflow:"hidden"}}>

        <div style={{padding:mob?"10px 14px":"14px 18px",borderBottom:"1px solid var(--bs)"}}><span style={{fontFamily:"'Cinzel',serif",fontSize:mob?14:16,color:"var(--tg)"}}>📊 Crafting Proximity</span></div>

        <div style={{padding:mob?"12px 14px":"16px 18px"}}>

          {known.length > 0 && <>

            <div style={{fontFamily:"Cinzel,serif",fontSize:13,color:"var(--tg)",marginBottom:8}}>📖 Known Recipes</div>

            {known.map((p) => <ProxCard key={p.r.id} p={p} isUnknown={false} />)}

          </>}

          {!known.length && <div style={{color:"var(--tm)",fontSize:13,padding:"12px 0"}}>No discovered recipes yet.</div>}

          {hintCfg.showUnknown && unknown.length > 0 && <>

            <div style={{fontFamily:"Cinzel,serif",fontSize:13,color:"var(--tm)",margin:"16px 0 8px",paddingTop:12,borderTop:"1px solid var(--bs)"}}>❓ Unknown Recipes</div>

            {unknown.map((p) => <ProxCard key={p.r.id} p={p} isUnknown={true} />)}

          </>}

        </div>

      </div>

    );

  };



  // ─── DM: RECIPE MANAGER ─────────────────────────────────

  const DMRecipes = () => {

    let d = [...recipes];

    if (dmItemFilter) {

      const fi = gi(dmItemFilter);

      d = d.filter((r) => r.ings.some((i) => i.iid === dmItemFilter) || (fi && nm(r.res.name) === nm(fi.name)));

    }

    if (dmSearchQ) {

      const q = dmSearchQ.toLowerCase();

      d = d.filter((r) => r.name.toLowerCase().includes(q) || r.cat.includes(q) || (r.tags || []).some((t) => t.includes(q)) || r.ings.some((i) => { const it = gi(i.iid); return it && it.name.toLowerCase().includes(q); }));

    }

    const filterItem = dmItemFilter ? gi(dmItemFilter) : null;

    return (

      <div style={{background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"var(--rl)",overflow:"hidden"}}>

        <div style={{padding:mob?"10px 14px":"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid var(--bs)",flexWrap:mob?"wrap":"nowrap",gap:mob?6:0}}>

          <span style={{fontFamily:"'Cinzel',serif",fontSize:mob?14:16,color:"var(--tg)"}}>📖 Recipe Manager</span>

          <div style={{display:"flex",gap:mob?4:6}}>

            <button onClick={() => setShowCatMgr(!showCatMgr)}

              style={{padding:mob?"5px 10px":"6px 16px",borderRadius:"var(--rs)",border:"1px solid "+(showCatMgr?"var(--ap)":"var(--bs)"),background:showCatMgr?"var(--apb)":"none",color:showCatMgr?"var(--tg)":"var(--ts)",fontSize:mob?11:12,cursor:"pointer"}}>{mob?"🏷️":"🏷️ Categories"}</button>

            <button aria-label="Create crafting recipe" onClick={() => { setEditingRecipe("new"); }}

              style={{padding:mob?"5px 10px":"6px 16px",borderRadius:"var(--rs)",border:"1px solid var(--bs)",background:"none",color:"var(--ts)",fontSize:mob?11:12,cursor:"pointer"}}>+ New</button>

          </div>

        </div>

        <div style={{padding:mob?"12px 14px":"16px 18px"}}>

          {showCatMgr && (

            <div style={{background:"var(--bgi)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",padding:14,marginBottom:14}}>

              <div style={{fontFamily:"'Cinzel',serif",fontSize:13,color:"var(--to)",marginBottom:10}}>🏷️ Category Manager</div>

              <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>

                {categories.filter((c) => c.id !== "all").map((c) => {

                  const count = recipes.filter((r) => r.cat === c.id).length;

                  return (

                    <div key={c.id} style={{display:"flex",alignItems:"center",gap:4,padding:"4px 10px",background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",fontSize:12}}>

                      <span>{c.l}</span>

                      <span style={{color:"var(--tm)",fontSize:10}}>({count})</span>

                      {count === 0 && (

                        <button onClick={() => {

                          setCategories(categories.filter((x) => x.id !== c.id));

                          toast("🗑️ Category removed");

                        }} style={{background:"none",border:"none",color:"var(--tr)",cursor:"pointer",fontSize:12,padding:"0 2px"}} title="Remove empty category">✕</button>

                      )}

                    </div>

                  );

                })}

              </div>

              <div style={{display:"flex",gap:6,alignItems:"center"}}>

                <button onClick={() => {

                  const icons = ["📁","⚔️","🛡️","🧪","🔧","📦","🍖","🎭","💀","🗺️","🎵","🔮","👑","🏰","⛏️"];

                  const cur = icons.indexOf(newCatIcon);

                  setNewCatIcon(icons[(cur + 1) % icons.length]);

                }} style={{padding:"4px 8px",border:"1px solid var(--bs)",borderRadius:"var(--rs)",background:"var(--bg2)",cursor:"pointer",fontSize:16}}>{newCatIcon}</button>

                <input value={newCatName} onChange={(e) => setNewCatName(e.target.value)} placeholder="New category name..."

                  onKeyDown={(e) => {

                    if (e.key === "Enter" && newCatName.trim()) {

                      const id = newCatName.trim().toLowerCase().replace(/\s+/g, "_");

                      if (categories.find((c) => c.id === id)) { toast("❌ Category already exists"); return; }

                      setCategories([...categories, { id, l: newCatIcon + " " + newCatName.trim() }]);

                      setNewCatName(""); setNewCatIcon("📁");

                      toast("✨ Category added!");

                    }

                  }}

                  style={{padding:"5px 10px",border:"1px solid var(--bs)",borderRadius:"var(--rs)",background:"var(--bg2)",color:"var(--tp)",fontSize:13,flex:1,outline:"none"}} />

                <button onClick={() => {

                  if (!newCatName.trim()) return;

                  const id = newCatName.trim().toLowerCase().replace(/\s+/g, "_");

                  if (categories.find((c) => c.id === id)) { toast("❌ Category already exists"); return; }

                  setCategories([...categories, { id, l: newCatIcon + " " + newCatName.trim() }]);

                  setNewCatName(""); setNewCatIcon("📁");

                  toast("✨ Category added!");

                }} style={{padding:"4px 14px",border:"1px solid var(--ag)",borderRadius:"var(--rs)",background:"var(--agb)",color:"var(--tgr)",fontSize:12,cursor:"pointer"}}>+ Add</button>

              </div>

            </div>

          )}

          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:14}}>

            <div style={{display:"flex",alignItems:"center",gap:8,background:"var(--bgi)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",padding:"8px 12px",flex:1,minWidth:200}}>

              <span style={{color:"var(--tm)"}}>🔍</span>

              <input value={dmSearchQ} onChange={(e) => setDmSearchQ(e.target.value)} placeholder="Search recipes, ingredients, or tags..."

                style={{background:"none",border:"none",color:"var(--ts)",fontSize:13,flex:1,outline:0}} />

            </div>

            {filterItem && (

              <div style={{display:"flex",alignItems:"center",gap:6,padding:"5px 10px",background:"var(--apb)",border:"1px solid var(--ap)",borderRadius:"var(--rm)",fontSize:12,color:"var(--tpu)"}}>

                Filter: {filterItem.icon} {filterItem.name}

                <span onClick={() => setDmItemFilter(null)} style={{cursor:"pointer",opacity:.7}}>✕</span>

              </div>

            )}

          </div>



          {editingRecipe !== null && (

            <div style={{marginBottom:18}}>

              <RecipeEditor

                editState={editingRecipe === "new" ? { name: "", icon: "⚒️", cat: "weapons", desc: "", ings: [{ iid: "", _n: "", qty: 1 }], tags: [], res: { name: "", icon: "⚒️", desc: "", cat: "weapons", rar: "Common", price: 0, buffs: [], debuffs: [], onUse: [], notes: "", dmNotes: "", tags: [], uses: null, abilities: [], combatBonuses: [] } }

                  : (() => { const r = gr(editingRecipe); return r ? { name: r.name, icon: r.icon, cat: r.cat, desc: r.desc, ings: r.ings.map((i) => { const it = gi(i.iid); return { iid: i.iid, _n: it ? it.name : "", qty: i.qty }; }), tags: [...(r.tags || [])], res: { name: r.res.name, icon: r.res.icon, desc: r.res.desc, cat: r.res.cat, rar: r.res.rar, price: r.res.price, buffs: [...(r.res.buffs || [])], debuffs: [...(r.res.debuffs || [])], onUse: [...(r.res.onUse || [])], notes: r.res.notes || "", dmNotes: r.res.dmNotes || "", tags: [...(r.res.tags || [])], uses: r.res.uses ? { ...r.res.uses } : null, abilities: (r.res.abilities || []).map((a) => ({ ...a })), combatBonuses: Array.isArray(r.res.combatBonuses) ? r.res.combatBonuses.map(b => ({ ...b })) : [] } } : null; })()}

                isNew={editingRecipe === "new"}

                allItems={items}

                items={items}

                categories={categories}

                allTags={allTags}

                setAllTags={setAllTags}

                favIcons={favIcons}

                setFavIcons={setFavIcons}

                mob={mob}

                onCancel={() => setEditingRecipe(null)}

                onDelete={() => { setRecipes(recipes.filter((r) => r.id !== editingRecipe)); setEditingRecipe(null); toast("🗑️ Recipe deleted"); }}

                onSave={(ed) => {

                  const validIngs = ed.ings.filter((i) => i.iid);

                  if (!validIngs.length) { toast("❌ Add at least one ingredient"); return; }

                  if (!ed.name.trim()) { toast("❌ Recipe name required"); return; }

                  const data = { name: ed.name.trim(), icon: ed.icon, cat: ed.cat, desc: ed.desc, tags: ed.tags, ings: validIngs.map((i) => ({ iid: i.iid, qty: i.qty })), res: { name: ed.res.name.trim() || ed.name.trim(), icon: ed.res.icon, desc: ed.res.desc, cat: ed.res.cat, rar: ed.res.rar, price: ed.res.price, buffs: ed.res.buffs, debuffs: ed.res.debuffs, onUse: ed.res.onUse, notes: ed.res.notes, dmNotes: ed.res.dmNotes, tags: ed.res.tags, uses: ed.res.uses, abilities: ed.res.abilities, combatBonuses: Array.isArray(ed.res.combatBonuses) ? ed.res.combatBonuses : [] } };

                  if (editingRecipe === "new") {

                    setRecipes([...recipes, { id: "r" + Date.now(), ...data, disc: false, vis: true }]);

                    toast("✨ Recipe created!");

                  } else {

                    setRecipes(recipes.map((r) => r.id === editingRecipe ? { ...r, ...data } : r));

                    toast("💾 Recipe saved!");

                  }

                  setEditingRecipe(null);

                }}

              />

            </div>

          )}



          <div style={{display:"flex",flexDirection:"column",gap:4}}>

            {d.map((r) => (

              <div key={r.id} style={{display:"flex",alignItems:"center",gap:mob?6:10,padding:mob?"6px 10px":"8px 12px",background:"var(--bgi)",border:"1px solid var(--bs)",borderRadius:"var(--rm)"}}>

                <span style={{fontSize:mob?16:20}}>{r.icon}</span>

                <div style={{flex:1,minWidth:0}}>

                  <div style={{fontFamily:"Alegreya,serif",fontWeight:600,fontSize:mob?13:14}}>{r.name}</div>

                  <div style={{fontSize:11,color:"var(--ts)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:mob?"nowrap":"normal"}}>{r.cat} · {r.ings.map((i) => { const it = gi(i.iid); return it ? it.name : i.iid; }).join(", ")}</div>

                  {!mob && r.tags?.length > 0 && <div style={{display:"flex",gap:3,marginTop:2}}>{r.tags.map((t) => <span key={t} style={{padding:"1px 5px",borderRadius:12,fontSize:9,background:"rgba(179,136,232,.12)",border:"1px solid rgba(179,136,232,.3)",color:"var(--tpu)"}}>{t}</span>)}</div>}

                </div>

                {!r.disc && <span onClick={() => { setRecipes(recipes.map((x) => x.id === r.id ? { ...x, vis: !x.vis } : x)); }}

                  style={{fontSize:mob?14:16,cursor:"pointer",padding:"2px 4px",borderRadius:"var(--rs)",userSelect:"none",opacity:r.vis?1:.3}}

                  title={r.vis ? "Visible to player — click to hide" : "Hidden from player — click to show"}>

                  👁

                </span>}

                <span onClick={() => { setRecipes(recipes.map((x) => x.id === r.id ? { ...x, disc: !x.disc } : x)); }}

                  style={{fontSize:mob?16:18,cursor:"pointer",padding:"2px 4px",borderRadius:"var(--rs)",userSelect:"none",opacity:r.disc?1:.3}}

                  title={r.disc ? "Discovered — click to lock" : "Undiscovered — click to discover"}>

                  {r.disc ? "🔓" : "🔒"}

                </span>

                <div style={{display:"flex",gap:mob?4:6,flexShrink:0}}>

                  <button onClick={() => setEditingRecipe(r.id)} style={{padding:mob?"4px 8px":"4px 10px",borderRadius:"var(--rs)",border:"1px solid var(--bs)",background:"var(--bg2)",color:"var(--ts)",fontSize:11,cursor:"pointer"}}>✏️</button>

                  <button onClick={() => { setRecipes(recipes.filter((x) => x.id !== r.id)); toast("🗑️ Recipe deleted"); }}

                    style={{padding:mob?"4px 8px":"4px 10px",borderRadius:"var(--rs)",border:"1px solid var(--bs)",background:"var(--bg2)",color:"var(--ts)",fontSize:11,cursor:"pointer"}}>🗑️</button>

                </div>

              </div>

            ))}

            {!d.length && <div style={{textAlign:"center",color:"var(--tm)",padding:20,fontSize:13}}>No recipes match</div>}

          </div>

        </div>

      </div>

    );

  };



  // ─── DM: ITEMS ──────────────────────────────────────────

  const DMItems = () => {

    const q = dmItemSearchQ.toLowerCase();

    const f = q ? items.filter((i) => i.name.toLowerCase().includes(q) || i.cat.includes(q)) : items;

    return (

      <div style={{background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"var(--rl)",overflow:"hidden"}}>

        <div style={{padding:mob?"10px 14px":"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid var(--bs)"}}>

          <span style={{fontFamily:"'Cinzel',serif",fontSize:mob?14:16,color:"var(--tg)"}}>📦 Base Items</span>

          <button onClick={() => setEditingItem("new")} style={{padding:mob?"5px 10px":"6px 16px",borderRadius:"var(--rs)",border:"1px solid var(--bs)",background:"none",color:"var(--ts)",fontSize:mob?11:12,cursor:"pointer"}}>+ New</button>

        </div>

        <div style={{padding:mob?"12px 14px":"16px 18px"}}>

          <div style={{display:"flex",alignItems:"center",gap:8,background:"var(--bgi)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",padding:"8px 12px",marginBottom:14}}>

            <span style={{color:"var(--tm)"}}>🔍</span>

            <input value={dmItemSearchQ} onChange={(e) => setDmItemSearchQ(e.target.value)} placeholder="Search items..."

              style={{background:"none",border:"none",color:"var(--ts)",fontSize:13,flex:1,outline:0}} />

          </div>

          {editingItem !== null && (

            <div style={{marginBottom:14}}>

              <ItemEditor

                item={editingItem === "new" ? null : items.find((i) => i.id === editingItem)}

                isNew={editingItem === "new"}

                categories={categories}

                items={items}

                recipes={recipes}

                favIcons={favIcons}

                setFavIcons={setFavIcons}

                mob={mob}

                onJumpToRecipe={(rid) => { setDmTab("recipes"); setEditingRecipe(rid); setEditingItem(null); }}

                onCancel={() => setEditingItem(null)}

                onDelete={() => {

                  const usedIn = recipes.filter((r) => r.ings.some((i) => i.iid === editingItem));

                  if (usedIn.length) { toast("❌ Can't delete — used in recipes"); return; }

                  setItems(items.filter((i) => i.id !== editingItem));

                  setEditingItem(null);

                  toast("🗑️ Item deleted");

                }}

                onSave={(ed) => {

                  if (!ed.name.trim()) { toast("❌ Item name required"); return; }

                  if (editingItem === "new") {

                    const id = mkid(ed.name, items);

                    setItems([...items, { id, name: ed.name.trim(), icon: ed.icon, cat: ed.cat }]);

                    toast("✨ Item created!");

                  } else {

                    setItems(items.map((i) => i.id === editingItem ? { ...i, name: ed.name.trim(), icon: ed.icon, cat: ed.cat } : i));

                    toast("💾 Item saved!");

                  }

                  setEditingItem(null);

                }}

              />

            </div>

          )}

          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>

            {f.map((it) => {

              const usage = ru(it.id).length + rp(it.id).length;

              return (

                <div key={it.id} onClick={() => setEditingItem(it.id)}

                  style={{display:"flex",alignItems:"center",gap:6,padding:"6px 10px",background:"var(--bgi)",border:"1px solid "+(editingItem===it.id?"var(--bgb)":"var(--bs)"),borderRadius:"var(--rs)",fontSize:13,cursor:"pointer",boxShadow:editingItem===it.id?"var(--gg)":"none"}}>

                  <span>{isGameIcon(it.icon) ? renderGameIcon(it.icon, 20, "var(--tp)") : it.icon}</span><span>{it.name}</span><span style={{fontSize:10,color:"var(--tm)"}}>{it.cat}</span>

                  {usage > 0 && <span onClick={(e) => { e.stopPropagation(); setDmTab("recipes"); setDmItemFilter(it.id); }}

                    style={{fontSize:10,color:"var(--tg)",cursor:"pointer"}} title="Filter recipes using this item">⚙{usage}</span>}

                </div>

              );

            })}

            {!f.length && <div style={{textAlign:"center",color:"var(--tm)",padding:20,fontSize:13,width:"100%"}}>No items match</div>}

          </div>

        </div>

      </div>

    );

  };



  // ─── DM: FREE CRAFT SETTINGS ────────────────────────────

  const DMFreeCraftSettings = () => {

    const modes = [

      { id: "pct_each", label: "🎲 % Chance Per Item", desc: `Each item has a ${failPctEach}% chance of being destroyed.` },

      { id: "pct_one", label: "🎯 Destroy 1 Random (% Chance)", desc: `One random item selected, ${failPctOne}% chance destroyed.` },

      { id: "no_destroy", label: "🛡️ No Destruction", desc: "All items returned on failure." },

    ];

    return (

      <div style={{background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"var(--rl)",overflow:"hidden"}}>

        <div style={{padding:mob?"10px 14px":"14px 18px",borderBottom:"1px solid var(--bs)"}}><span style={{fontFamily:"'Cinzel',serif",fontSize:mob?14:16,color:"var(--tg)"}}>🧪 Free Craft Settings</span></div>

        <div style={{padding:mob?"12px 14px":"16px 18px"}}>

          <div style={{background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",padding:14,marginBottom:14}}>

            <div style={{fontFamily:"'Cinzel',serif",fontSize:13,color:"var(--to)",marginBottom:10}}>⚙️ Failure Behavior</div>

            {modes.map((m) => (

              <label key={m.id} onClick={() => setFailMode(m.id)}

                style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderRadius:"var(--rm)",cursor:"pointer",marginBottom:4,

                  background:failMode===m.id?"var(--apb)":"none",border:failMode===m.id?"1px solid var(--ap)":"1px solid transparent"}}>

                <input type="radio" name="failMode" checked={failMode === m.id} onChange={() => setFailMode(m.id)} style={{accentColor:"var(--ap)"}} />

                <div><div style={{fontSize:13,fontWeight:600}}>{m.label}</div><div style={{fontSize:11,color:"var(--ts)"}}>{m.desc}</div></div>

              </label>

            ))}

            {failMode === "pct_each" && (

              <div style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",marginTop:4}}>

                <label style={{fontSize:12,color:"var(--ts)",whiteSpace:"nowrap"}}>Destroy chance per item:</label>

                <input type="range" min="0" max="100" value={failPctEach} onChange={(e) => setFailPctEach(parseInt(e.target.value))} style={{flex:1,accentColor:"var(--ap)"}} />

                <span style={{fontSize:13,fontWeight:700,color:"var(--tr)",minWidth:40}}>{failPctEach}%</span>

              </div>

            )}

            {failMode === "pct_one" && (

              <div style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",marginTop:4}}>

                <label style={{fontSize:12,color:"var(--ts)",whiteSpace:"nowrap"}}>Destroy chance:</label>

                <input type="range" min="0" max="100" value={failPctOne} onChange={(e) => setFailPctOne(parseInt(e.target.value))} style={{flex:1,accentColor:"var(--ap)"}} />

                <span style={{fontSize:13,fontWeight:700,color:"var(--tr)",minWidth:40}}>{failPctOne}%</span>

              </div>

            )}

          </div>



          <div style={{background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",padding:14}}>

            <div style={{fontFamily:"'Cinzel',serif",fontSize:13,color:"var(--to)",marginBottom:10}}>👁️ Player Proximity — Hints for Unknown Recipes</div>

            <label style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderRadius:"var(--rm)",cursor:"pointer",background:hintCfg.showUnknown?"var(--apb)":"none",border:hintCfg.showUnknown?"1px solid var(--ap)":"1px solid transparent"}}>

              <input type="checkbox" checked={hintCfg.showUnknown} onChange={(e) => setHintCfg({ ...hintCfg, showUnknown: e.target.checked })} style={{accentColor:"var(--ap)"}} />

              <div><div style={{fontSize:13,fontWeight:600}}>Show unknown recipes in player proximity</div><div style={{fontSize:11,color:"var(--ts)"}}>When enabled, undiscovered recipes appear in the player's proximity tab.</div></div>

            </label>

            {hintCfg.showUnknown && (

              <div style={{marginTop:8}}>

                <div style={{fontSize:12,color:"var(--ts)",marginBottom:6}}>Default hint level for unknown recipes:</div>

                {[

                  { id: "none", label: "🚫 No Hints", desc: "Unknown recipes show as '🗑️'" },

                  { id: "count", label: "🔢 Ingredient Count", desc: "Shows X/Y types gathered" },

                  { id: "have", label: "📋 Show Owned Items", desc: "Shows ingredients the party has" },

                  { id: "have_mystery", label: "🔮 Owned + Mystery", desc: "Owned by name, missing as ?" },

                ].map((h) => (

                  <label key={h.id} onClick={() => setHintCfg({ ...hintCfg, defaultHint: h.id })}

                    style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderRadius:"var(--rm)",cursor:"pointer",marginBottom:4,

                      background:hintCfg.defaultHint===h.id?"var(--apb)":"none",border:hintCfg.defaultHint===h.id?"1px solid var(--ap)":"1px solid transparent"}}>

                    <input type="radio" checked={hintCfg.defaultHint === h.id} onChange={() => {}} style={{accentColor:"var(--ap)"}} />

                    <div><div style={{fontSize:13,fontWeight:600}}>{h.label}</div><div style={{fontSize:11,color:"var(--ts)"}}>{h.desc}</div></div>

                  </label>

                ))}

              </div>

            )}

          </div>



          {/* Undo Log */}

          <div style={{background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"var(--rl)",overflow:"hidden",marginTop:16}}>

            <div style={{padding:mob?"10px 14px":"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid var(--bs)"}}>

              <span style={{fontFamily:"'Cinzel',serif",fontSize:mob?14:16,color:"var(--tg)"}}>↩️ Undo Log</span>

              <span style={{fontSize:12,color:"var(--ts)"}}>{undoLog.length} entries</span>

            </div>

            <div style={{padding:mob?"12px 14px":"16px 18px"}}>

              {undoLog.length === 0 ? <div style={{textAlign:"center",color:"var(--tm)",padding:20,fontSize:13}}>No craft attempts to undo yet.</div>

                : [...undoLog].reverse().map((u) => {

                  const ago = Math.round((Date.now() - u.time) / 1000);

                  const ts = ago < 60 ? ago + "s ago" : Math.round(ago / 60) + "m ago";

                  return (

                    <div key={u.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:"var(--bgi)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",marginBottom:6,fontSize:12}}>

                      <div style={{display:"flex",gap:3,flexWrap:"wrap",flex:1}}>

                        {u.items.map((i, idx) => { const it = gi(i.iid); return it ? <span key={idx} style={{fontSize:16}} title={it.name + " ×" + i.qty}>{isGameIcon(it.icon) ? renderGameIcon(it.icon, 20, "var(--tp)") : it.icon}</span> : null; })}

                      </div>

                      <div style={{color:"var(--ts)",flex:1,minWidth:0}}>{u.msg}</div>

                      <div style={{color:"var(--tm)",fontSize:10,whiteSpace:"nowrap"}}>{ts}</div>

                      <button onClick={() => {

                        if (u.undone) return;

                        const newUndoLog = undoLog.map((x) => x.id === u.id ? { ...x, undone: true } : x);

                        setUndoLog(newUndoLog);

                        const newInv = { ...inv };

                        u.items.forEach((i) => { newInv[i.iid] = (newInv[i.iid] || 0) + i.qty; });

                        setInv(newInv);

                        toast("↩️ Undo: restored items");

                      }} disabled={u.undone}

                        style={{padding:"4px 12px",borderRadius:"var(--rs)",border:"1px solid var(--ag)",background:"var(--agb)",color:"var(--tgr)",fontSize:11,cursor:u.undone?"not-allowed":"pointer",fontWeight:700,whiteSpace:"nowrap",flexShrink:0,opacity:u.undone?.4:1}}>

                        {u.undone ? "✓ Undone" : "↩️ Undo"}

                      </button>

                    </div>

                  );

                })}

            </div>

          </div>

        </div>

      </div>

    );

  };



  // ─── DM: PROXIMITY OVERRIDES ────────────────────────────

  const DMProximity = () => {

    const hintLevels = [

      { id: "none", label: "None" },

      { id: "count", label: "Count" },

      { id: "have", label: "Owned" },

      { id: "have_mystery", label: "Owned+?" },

    ];



    const setHintForRecipe = (rid, level) => {

      setRecipes(recipes.map((r) => r.id === rid ? { ...r, hintLevel: level === "default" ? undefined : level } : r));

    };



    const toggleRevealIngredient = (rid, iid) => {

      setRecipes(recipes.map((r) => {

        if (r.id !== rid) return r;

        const reveals = r.revealedIngs || [];

        return { ...r, revealedIngs: reveals.includes(iid) ? reveals.filter((x) => x !== iid) : [...reveals, iid] };

      }));

    };



    // Calculate proximity for all visible recipes

    const prox = recipes.filter((r) => r.vis || r.disc).map((r) => {

      let totalNeeded = 0, totalHave = 0;

      const ings = r.ings.map((ig) => {

        const hv = ph(ig.iid), nd = ig.qty;

        const pct = Math.min(hv / nd, 1);

        totalNeeded += nd; totalHave += Math.min(hv, nd);

        return { iid: ig.iid, qty: nd, have: hv, pct, status: hv >= nd ? "ok" : hv > 0 ? "partial" : "none" };

      });

      const overallPct = totalNeeded > 0 ? Math.round((totalHave / totalNeeded) * 100) : 0;

      return { r, ings, pct: overallPct, craftable: overallPct === 100 };

    }).sort((a, b) => b.pct - a.pct);



    const craftable = prox.filter((p) => p.craftable);

    const close = prox.filter((p) => !p.craftable && p.pct >= 50);

    const far = prox.filter((p) => !p.craftable && p.pct > 0 && p.pct < 50);

    const none = prox.filter((p) => p.pct === 0);



    const renderCard = (p) => {

      const r = p.r;

      const barColor = p.pct === 100 ? "var(--tgr)" : p.pct >= 50 ? "var(--tgb)" : p.pct > 0 ? "var(--to)" : "var(--tr)";

      const isOpen = proxOpen[r.id];

      const curHL = r.hintLevel || "default";

      const reveals = r.revealedIngs || [];

      const revCount = reveals.length;



      return (

        <div key={r.id} style={{background:"var(--bgi)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",marginBottom:6,overflow:"hidden"}}>

          {/* Header */}

          <div style={{display:"flex",alignItems:"center",gap:mob?6:8,padding:mob?"6px 10px":"8px 14px"}}>

            <span style={{fontFamily:"Alegreya,serif",fontWeight:600,fontSize:mob?13:15,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.name}</span>

            <span onClick={() => setRecipes(recipes.map((x) => x.id === r.id ? { ...x, disc: !x.disc } : x))}

              style={{cursor:"pointer",color:r.disc?"var(--tgr)":"var(--tr)",fontSize:mob?12:14}} title={r.disc?"Discovered — click to lock":"Undiscovered — click to discover"}>

              {r.disc ? "🔓" : "🔒"}

            </span>

            {!r.disc && <span onClick={() => setRecipes(recipes.map((x) => x.id === r.id ? { ...x, vis: !x.vis } : x))}

              style={{cursor:"pointer",color:r.vis?"var(--tpu)":"var(--tm)",fontSize:mob?12:14}} title={r.vis?"Visible — click to hide":"Hidden — click to show"}>

              👁

            </span>}

            {revCount > 0 && !r.disc && !mob && <span style={{fontSize:10,color:"var(--tpu)",whiteSpace:"nowrap"}}>{revCount} hint{revCount > 1 ? "s" : ""}</span>}

            <span style={{fontWeight:700,fontSize:mob?13:15,color:barColor,minWidth:mob?36:40,textAlign:"right"}}>{p.pct}%</span>

            <span onClick={() => setProxOpen((prev) => ({ ...prev, [r.id]: !prev[r.id] }))}

              style={{cursor:"pointer",fontSize:mob?10:12,color:isOpen?"#fff":"var(--tpu)",width:mob?22:26,height:mob?22:26,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"50%",

                background:isOpen?"rgba(107,63,160,.7)":"none",border:"1px solid "+(isOpen?"rgba(107,63,160,.8)":"rgba(179,136,232,.3)"),transition:"all .2s"}}>

              {isOpen ? "▼" : "▶"}

            </span>

          </div>

          {/* Progress bar */}

          <div style={{height:4,background:"var(--bg1)",margin:mob?"0 10px 4px":"0 14px 6px",borderRadius:2}}>

            <div style={{height:"100%",width:p.pct+"%",background:barColor,borderRadius:2,transition:"width .4s ease"}} />

          </div>

          {/* Ingredient pills */}

          <div style={{display:"flex",flexWrap:"wrap",gap:mob?4:5,padding:mob?"0 10px 6px":"0 14px 8px"}}>

            {p.ings.map((ig) => {

              const it = gi(ig.iid);

              const rev = !r.disc && reveals.includes(ig.iid);

              const bgMap = { ok: "var(--agb)", partial: "rgba(212,136,58,.12)", none: "var(--arb)" };

              const brMap = { ok: "var(--ag)", partial: "var(--to)", none: "var(--ar)" };

              const clrMap = { ok: "var(--tgr)", partial: "var(--to)", none: "var(--tr)" };

              return (

                <span key={ig.iid} style={{fontSize:11,padding:"3px 8px",borderRadius:"var(--rs)",display:"inline-flex",alignItems:"center",gap:3,

                  background:bgMap[ig.status],border:"1px solid "+brMap[ig.status],

                  boxShadow:rev?"0 0 0 2px var(--tpu)":"none"}}>

                  {it ? it.icon : "?"} {it ? it.name : ig.iid} <strong style={{color:clrMap[ig.status]}}>{ig.have}/{ig.qty}</strong>

                  {rev && <span style={{fontSize:10,marginLeft:2}}>👁</span>}

                </span>

              );

            })}

          </div>

          {/* Missing items callout */}

          {p.pct >= 50 && !p.craftable && (

            <div style={{fontSize:11,color:"var(--ts)",fontStyle:"italic",padding:"0 14px 8px"}}>

              Missing: {p.ings.filter((ig) => ig.status !== "ok").map((ig) => { const it = gi(ig.iid); return (it ? it.name : ig.iid) + " (need " + (ig.qty - ig.have) + " more)"; }).join(", ")}

            </div>

          )}

          {/* Expandable hint config */}

          {isOpen && !r.disc && (

            <div style={{padding:"10px 14px",borderTop:"1px solid var(--bs)",background:"rgba(0,0,0,.12)"}}>

              <div style={{background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",padding:"10px 12px"}}>

                <div style={{fontFamily:"Cinzel,serif",fontSize:12,color:"var(--tgb)",marginBottom:6}}>👁 Player Hint Level</div>

                <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:10}}>

                  {hintLevels.map((h) => {

                    const isDef = h.id === hintCfg.defaultHint;

                    const isActive = curHL === "default" ? isDef : curHL === h.id;

                    return (

                      <button key={h.id} onClick={() => setHintForRecipe(r.id, isDef ? "default" : h.id)}

                        style={{padding:"4px 10px",borderRadius:"var(--rs)",border:"1px solid "+(isActive?"var(--ap)":"var(--bs)"),

                          background:isActive?"var(--apb)":"var(--bg2)",color:isActive?"var(--tg)":"var(--ts)",

                          fontSize:11,cursor:"pointer"}}>

                        {h.label}{isDef ? " (Default)" : ""}

                      </button>

                    );

                  })}

                </div>

                <div style={{fontFamily:"Cinzel,serif",fontSize:12,color:"var(--tgb)",marginBottom:6}}>

                  Reveal Individual Ingredients<span style={{fontWeight:400,color:"var(--ts)",fontFamily:"Alegreya Sans,sans-serif"}}> — click to toggle visibility to players</span>

                </div>

                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>

                  {p.ings.map((ig) => {

                    const it = gi(ig.iid);

                    const rev = reveals.includes(ig.iid);

                    return (

                      <button key={ig.iid} onClick={() => toggleRevealIngredient(r.id, ig.iid)}

                        style={{padding:"4px 10px",borderRadius:"var(--rs)",fontSize:11,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:4,

                          border:"1px solid "+(rev?"var(--ag)":"rgba(94,82,102,.6)"),

                          background:rev?"var(--agb)":"rgba(18,14,22,.9)",

                          color:rev?"var(--tgr)":"var(--ts)"}}>

                        <span style={{fontSize:rev?11:9}}>{rev ? "👁" : "🔒"}</span> {it ? it.name : ig.iid} <strong>{ig.have}/{ig.qty}</strong>

                      </button>

                    );

                  })}

                </div>

              </div>

            </div>

          )}

          {isOpen && r.disc && (

            <div style={{padding:"10px 14px",borderTop:"1px solid var(--bs)",fontSize:12,color:"var(--ts)"}}>

              This recipe is already discovered — players see full details.

            </div>

          )}

        </div>

      );

    };



    const renderSection = (title, items, color, emptyMsg) => (

      <div style={{padding:"6px 0 2px"}}>

        <div style={{fontFamily:"Cinzel,serif",fontSize:13,color,letterSpacing:.5,marginBottom:4}}>{title}{items.length > 0 ? " (" + items.length + ")" : ""}</div>

        {items.length > 0 ? items.map(renderCard) : <div style={{fontSize:12,color:"var(--tm)",padding:"6px 0"}}>{emptyMsg}</div>}

      </div>

    );



    return (

      <div style={{background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"var(--rl)",overflow:"hidden"}}>

        <div style={{padding:mob?"10px 14px":"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid var(--bs)"}}>

          <span style={{fontFamily:"'Cinzel',serif",fontSize:mob?14:16,color:"var(--tg)"}}>📊 Crafting Proximity</span>

          {!mob && <span style={{fontSize:12,color:"var(--ts)"}}>Party's crafting potential</span>}

        </div>

        <div style={{padding:mob?"12px 14px":"16px 18px"}}>

          <div style={{padding:"8px 12px",background:"var(--bgi)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",marginBottom:12,fontSize:12,color:"var(--ts)"}}>

            Shows all recipes sorted by how close the party is to crafting them. Use this to plan loot drops, quest rewards, and hints. <strong style={{color:"var(--tpu)"}}>Includes undiscovered recipes.</strong> Click ▶ to configure player hints per recipe.

          </div>

          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12,flexWrap:"wrap"}}>

            <span style={{fontSize:12,color:"var(--ts)"}}>Default hint level:</span>

            {hintLevels.map((h) => (

              <button key={h.id} onClick={() => setHintCfg({ ...hintCfg, defaultHint: h.id })}

                style={{padding:"3px 9px",borderRadius:"var(--rs)",border:"1px solid "+(hintCfg.defaultHint===h.id?"var(--ap)":"var(--bs)"),

                  background:hintCfg.defaultHint===h.id?"var(--apb)":"var(--bg2)",color:hintCfg.defaultHint===h.id?"var(--tg)":"var(--ts)",

                  fontSize:11,cursor:"pointer"}}>

                {h.label}

              </button>

            ))}

          </div>

          {renderSection("Ready to Craft", craftable, "var(--tgr)", "No recipes fully craftable right now.")}

          {renderSection("Almost There (50%+)", close, "var(--tgb)", "No recipes close to completion.")}

          {renderSection("In Progress", far, "var(--to)", "No recipes partially available.")}

          {renderSection("No Materials", none, "var(--tm)", "All recipes have at least some materials!")}

        </div>

      </div>

    );

  };



  // ─── IMPORT MODAL ────────────────────────────────────────

  const ImportModal = () => {

    const others = campaigns.filter((c) => c.id !== activeCampaignId);

    const [impSrc, setImpSrc] = useState(others[0]?.id || "");

    const [impTab, setImpTab] = useState("content");

    const [impQ, setImpQ] = useState("");

    const [impFilter, setImpFilter] = useState("all");

    const [selected, setSelected] = useState(new Set());

    const [selSettings, setSelSettings] = useState(new Set(["fail", "hint", "cats", "tags"]));



    const src = campaignStore[impSrc];

    const srcItems = src?.items || [];

    const srcRecs = src?.recipes || [];



    const existingItemNames = new Set(items.map((i) => i.name.toLowerCase()));

    const existingRecNames = new Set(recipes.map((r) => r.name.toLowerCase()));



    const allSrc = [

      ...srcItems.filter((i) => !existingItemNames.has(i.name.toLowerCase())).map((i) => ({ type: "item", d: i })),

      ...srcRecs.filter((r) => !existingRecNames.has(r.name.toLowerCase())).map((r) => ({ type: "recipe", d: r })),

    ];



    let filtered = allSrc;

    if (impFilter === "items") filtered = filtered.filter((s) => s.type === "item");

    if (impFilter === "recipes") filtered = filtered.filter((s) => s.type === "recipe");

    if (impQ) {

      const q = impQ.toLowerCase();

      filtered = filtered.filter((s) => {

        const n = s.d.name.toLowerCase();

        const cat = (s.d.cat || "").toLowerCase();

        const tags = (s.d.tags || []).join(" ").toLowerCase();

        const ingNames = s.type === "recipe" ? (s.d.ings || []).map((ig) => { const it = srcItems.find((i) => i.id === ig.iid); return it ? it.name.toLowerCase() : ""; }).join(" ") : "";

        return n.includes(q) || cat.includes(q) || tags.includes(q) || ingNames.includes(q);

      });

    }



    const alreadyCount = (srcItems.length - allSrc.filter((s) => s.type === "item").length) + (srcRecs.length - allSrc.filter((s) => s.type === "recipe").length);



    const doImport = () => {

      if (impTab === "content") {

        if (!selected.size) { toast("Nothing selected"); return; }

        let ic = 0, rc = 0, ts = Date.now();

        selected.forEach((idx) => {

          const s = filtered[idx];

          if (!s) return;

          if (s.type === "item") {

            if (!items.find((i) => i.name.toLowerCase() === s.d.name.toLowerCase())) {

              setItems((prev) => [...prev, JSON.parse(JSON.stringify(s.d))]); ic++;

            }

          } else {

            const nr = JSON.parse(JSON.stringify(s.d));

            nr.id = "r" + (ts++) + "_" + Math.random().toString(36).slice(2, 6);

            nr.ings.forEach((ig) => {

              if (!items.find((i) => i.id === ig.iid)) {

                const srcItem = srcItems.find((i) => i.id === ig.iid);

                if (srcItem && !items.find((i) => i.name.toLowerCase() === srcItem.name.toLowerCase())) {

                  setItems((prev) => [...prev, JSON.parse(JSON.stringify(srcItem))]); ic++;

                }

              }

            });

            setRecipes((prev) => [...prev, nr]); rc++;

          }

        });

        setShowImportModal(false);

        toast("📥 Imported " + ic + " items, " + rc + " recipes");

      } else {

        if (!selSettings.size) { toast("Nothing selected"); return; }

        const applied = [];

        if (selSettings.has("fail") && src) { setFailMode(src.failMode); setFailPctEach(src.failPctEach); setFailPctOne(src.failPctOne); applied.push("Failure mode"); }

        if (selSettings.has("hint") && src) { setHintCfg({ ...src.hintCfg }); applied.push("Hint config"); }

        if (selSettings.has("cats") && src) { setCategories([{ id: "all", l: "⚡ All" }, ...(src.categories || []).map((x) => ({ ...x }))]); applied.push("Categories"); }

        if (selSettings.has("tags") && src) { setAllTags([...(src.allTags || [])]); applied.push("Tags"); }

        setShowImportModal(false);

        toast("⚙ Applied: " + applied.join(", "));

      }

    };



    const toggleSel = (idx) => {

      const next = new Set(selected);

      if (next.has(idx)) next.delete(idx); else next.add(idx);

      setSelected(next);

    };



    return (

      <div onClick={(e) => { if (e.target === e.currentTarget) setShowImportModal(false); }}

        style={{position:"fixed",inset:0,background:"rgba(0,0,0,.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>

        <div style={{background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"var(--rl)",maxWidth:520,width:mob?"95%":"90%",maxHeight:mob?"85vh":"80vh",display:"flex",flexDirection:"column"}}>

          {/* Header */}

          <div style={{padding:mob?"12px 14px":"16px 20px",borderBottom:"1px solid var(--bs)"}}>

            <div style={{fontFamily:"Cinzel,serif",fontSize:15,color:"var(--tpu)",marginBottom:8}}>📥 Import from Campaign</div>

            <select value={impSrc} onChange={(e) => { setImpSrc(e.target.value); setSelected(new Set()); }}

              style={{width:"100%",padding:"6px 10px",border:"1px solid var(--bs)",borderRadius:"var(--rs)",background:"var(--bg1)",color:"var(--tp)",fontSize:13,marginBottom:8}}>

              {others.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}

            </select>

            <div style={{display:"flex",gap:4}}>

              <button onClick={() => { setImpTab("content"); setSelected(new Set()); }}

                style={{padding:"4px 12px",border:"1px solid "+(impTab==="content"?"var(--ap)":"var(--bs)"),borderRadius:"var(--rs)",background:impTab==="content"?"var(--apb)":"none",color:impTab==="content"?"var(--tpu)":"var(--ts)",fontSize:11,cursor:"pointer"}}>📦 Content</button>

              <button onClick={() => setImpTab("settings")}

                style={{padding:"4px 12px",border:"1px solid "+(impTab==="settings"?"var(--ap)":"var(--bs)"),borderRadius:"var(--rs)",background:impTab==="settings"?"var(--apb)":"none",color:impTab==="settings"?"var(--tpu)":"var(--ts)",fontSize:11,cursor:"pointer"}}>⚙ Settings</button>

            </div>

            {impTab === "content" && (

              <>

                <div style={{display:"flex",alignItems:"center",gap:6,background:"var(--bgi)",border:"1px solid var(--bs)",borderRadius:"var(--rs)",padding:"5px 10px",marginTop:8}}>

                  <span style={{color:"var(--tm)",fontSize:12}}>🔍</span>

                  <input value={impQ} onChange={(e) => { setImpQ(e.target.value); setSelected(new Set()); }} placeholder="Search..."

                    style={{background:"none",border:"none",color:"var(--ts)",fontSize:13,flex:1,outline:0}} />

                </div>

                <div style={{display:"flex",gap:4,marginTop:6,alignItems:"center"}}>

                  {["all","items","recipes"].map((f) => (

                    <button key={f} onClick={() => { setImpFilter(f); setSelected(new Set()); }}

                      style={{padding:"3px 10px",border:"1px solid "+(impFilter===f?"var(--ap)":"var(--bs)"),borderRadius:"var(--rs)",background:impFilter===f?"var(--apb)":"none",color:impFilter===f?"var(--tpu)":"var(--ts)",fontSize:10,cursor:"pointer"}}>

                      {f === "all" ? "All" : f === "items" ? "📦 Items" : "📖 Recipes"}

                    </button>

                  ))}

                  <span style={{flex:1}} />

                  <button onClick={() => setSelected(new Set(filtered.map((_, i) => i)))}

                    style={{padding:"3px 10px",border:"1px solid var(--bs)",borderRadius:"var(--rs)",background:"none",color:"var(--ts)",fontSize:10,cursor:"pointer"}}>Select All</button>

                  <button onClick={() => setSelected(new Set())}

                    style={{padding:"3px 10px",border:"1px solid var(--bs)",borderRadius:"var(--rs)",background:"none",color:"var(--ts)",fontSize:10,cursor:"pointer"}}>Clear</button>

                </div>

              </>

            )}

          </div>



          {/* Body */}

          <div style={{flex:1,overflow:"auto",padding:"8px 20px"}}>

            {impTab === "content" ? (

              <>

                {filtered.length > 0 && <div style={{fontSize:11,color:"var(--tm)",padding:"4px 0"}}>{filtered.length} available{alreadyCount > 0 ? " · " + alreadyCount + " already imported" : ""}</div>}

                {filtered.map((s, fi) => (

                  <div key={fi} onClick={() => toggleSel(fi)}

                    style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",margin:"2px 0",borderRadius:"var(--rs)",cursor:"pointer",

                      background:selected.has(fi)?"var(--apb)":"var(--bgi)",border:"1px solid "+(selected.has(fi)?"var(--ap)":"var(--bs)")}}>

                    <span style={{fontSize:16}}>{s.d.icon || "📦"}</span>

                    <span style={{flex:1,fontSize:13,minWidth:0}}>

                      <span style={{display:"block"}}>{s.d.name}</span>

                      {s.type === "recipe" && <span style={{fontSize:10,color:"var(--tm)"}}>{(s.d.ings || []).map((ig) => { const it = srcItems.find((i) => i.id === ig.iid); return it ? it.name : ig.iid; }).join(", ")}</span>}

                    </span>

                    <span style={{fontSize:10,padding:"2px 6px",borderRadius:"var(--rs)",background:s.type==="recipe"?"var(--apb)":"var(--bgi)",color:s.type==="recipe"?"var(--tpu)":"var(--ts)"}}>{s.type === "recipe" ? "Recipe" : "Item"}</span>

                    <span style={{fontSize:10,color:"var(--tm)"}}>{s.d.cat || ""}</span>

                  </div>

                ))}

                {!filtered.length && <div style={{textAlign:"center",color:"var(--tm)",padding:20,fontSize:13}}>{alreadyCount ? "All content already imported!" : impQ || impFilter !== "all" ? "No matches" : "No content in this campaign"}</div>}

              </>

            ) : (

              <div style={{padding:"8px 0"}}>

                <div style={{fontSize:13,color:"var(--tp)",marginBottom:12}}>Import settings from <strong>{others.find((c) => c.id === impSrc)?.name || ""}</strong>:</div>

                {[

                  { k: "fail", icon: "🎲", label: `Failure Mode: ${src?.failMode || "?"} (${src?.failPctEach || 0}%/${src?.failPctOne || 0}%)` },

                  { k: "hint", icon: "👁", label: `Hint Config: ${src?.hintCfg?.defaultHint || "?"}, Unknown ${src?.hintCfg?.showUnknown ? "shown" : "hidden"}` },

                  { k: "cats", icon: "📋", label: `Categories (${(src?.categories || []).length})` },

                  { k: "tags", icon: "🏷️", label: `Tags (${(src?.allTags || []).length})` },

                ].map((s) => (

                  <div key={s.k} onClick={() => { const next = new Set(selSettings); if (next.has(s.k)) next.delete(s.k); else next.add(s.k); setSelSettings(next); }}

                    style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",margin:"2px 0",borderRadius:"var(--rs)",cursor:"pointer",

                      background:selSettings.has(s.k)?"var(--apb)":"var(--bgi)",border:"1px solid "+(selSettings.has(s.k)?"var(--ap)":"var(--bs)")}}>

                    <span style={{fontSize:16}}>{s.icon}</span>

                    <span style={{flex:1,fontSize:13}}>{s.label}</span>

                  </div>

                ))}

              </div>

            )}

          </div>



          {/* Footer */}

          <div style={{display:"flex",alignItems:"center",gap:10,padding:"12px 20px",borderTop:"1px solid var(--bs)"}}>

            <span style={{fontSize:12,color:"var(--tm)",flex:1}}>{impTab === "content" ? (selected.size ? selected.size + " selected" : "Click to select") : ""}</span>

            <button onClick={() => setShowImportModal(false)}

              style={{padding:"6px 16px",border:"1px solid var(--bs)",borderRadius:"var(--rm)",background:"none",color:"var(--ts)",fontSize:13,cursor:"pointer"}}>Cancel</button>

            <button onClick={doImport}

              style={{padding:"6px 16px",border:"none",borderRadius:"var(--rm)",background:"linear-gradient(135deg,#6b3fa0,#8855cc)",color:"#fff",fontFamily:"Cinzel,serif",fontSize:13,cursor:"pointer"}}>

              {impTab === "content" ? "Import Selected" : "Apply Settings"}

            </button>

          </div>

        </div>

      </div>

    );

  };



  // ─── PROFESSION SYSTEM (Phase 1 MVP) ─────────────────────

  // 2026-04-27 (later): per-character profession state lives on the

  // char doc. Picker UI + XP grant flow rendered inline below; helper

  // logic (XP curve, gating, level-up math) lives at module scope.

  // playerProfsState + profsEnabled are hoisted earlier (above `cc`)

  // so the craftability callback can read them without TDZ.

  // 2026-04-27 (Visual Port): picker now takes a slot type ('major' | 'minor')

  // so the prototype's "Choose a Major/Minor Profession" hint string is

  // accurate — empty-slot clicks in the panel route to the right type.

  // `profPanelOpen` shows the prototype's `.prof-modal-overlay` Your-

  // Professions panel; `profHudVisible` is implied by profsEnabled+anyProf.

  const [profPickerOpen, setProfPickerOpen] = useState(false);

  const [pickerSlotType, setPickerSlotType] = useState(null); // 'major' | 'minor' | null (any)

  const [profPanelOpen, setProfPanelOpen] = useState(false);

  const [levelUpToast, setLevelUpToast] = useState(null);

  const [talentTreeProfId, setTalentTreeProfId] = useState(null);

  // 2026-04-27 (Visual Port — Phase 4): inline quality flash card.

  // Mirrors the prototype's `UI.lastCraftResult` — after a successful

  // craft, the crafting station shows a `.q-result-card` with the

  // rolled tier color/glow for ~8 seconds. Distinct from the existing

  // success modal, which is a separate confirmation overlay.

  const [lastCraftResult, setLastCraftResult] = useState(null); // {recipeId, quality, xpInfo, time}

  const maxMajor = profSettings?.maxMajor ?? 1;



  // Persist a playerProfs update through the character doc.

  const persistPlayerProfs = (next) => {

    if (!onUpdateChar) return;

    onUpdateChar({ playerProfs: next });

  };



  // Add a profession slot (no XP, just chosen). Honors maxMajor.

  const pickProfession = (profId) => {

    if (!onUpdateChar) return;

    const closePicker = () => { setProfPickerOpen(false); setPickerSlotType(null); };

    if (playerProfsState[profId]) { closePicker(); return; }

    const prof = DEFAULT_PROFESSIONS.find(p => p.id === profId);

    if (!prof) return;

    if (prof.type === 'major') {

      const currentMajors = Object.keys(playerProfsState).filter(id => {

        const p = DEFAULT_PROFESSIONS.find(x => x.id === id);

        return p && p.type === 'major';

      }).length;

      if (currentMajors >= maxMajor) {

        toast(`Already at major-profession limit (${maxMajor})`);

        return;

      }

    }

    const next = { ...playerProfsState, [profId]: { level: 1, xp: 0, totalXp: 0, skillPoints: 0, talentRanks: {} } };

    persistPlayerProfs(next);

    closePicker();

    toast(`✨ ${prof.icon} ${prof.name} chosen`);

  };



  // Grant XP for a craft. Uses computeCraftXp + applyProfXp at module

  // scope so the math is pure and testable. Surfaces a level-up toast

  // when level changes.

  const grantCraftXp = (recipe, ctx = {}) => {

    if (!onUpdateChar || !profsEnabled) return null;

    const profId = recipe?.profReq?.profId;

    if (!profId) return null;                    // free recipe — no XP target

    if (!playerProfsState[profId]) return null;  // player hasn't picked it

    // 2026-04-27 (Phase 2): talent xpBonus folds into the receipt as a

    // labeled multiplier so the player sees where the boost came from.

    const prof = DEFAULT_PROFESSIONS.find(p => p.id === profId);

    const talentEffects = sumTalentEffects(prof, playerProfsState[profId]);

    const xpInfo = computeCraftXp(recipe, { ...ctx, profSettings, talentXpBonusPct: talentEffects.xpBonusPct });

    const { playerProfs: next, levelUps } = applyProfXp(playerProfsState, profId, xpInfo.total, DEFAULT_PROFESSIONS);

    persistPlayerProfs(next);

    if (levelUps.length > 0) {

      const lu = levelUps[0];

      const milestone = (prof?.milestones || []).find(m => m.level === lu.newLevel);

      // Phase 2: surface the +1 SP grant + any newly-unlocked talent tier

      // so the player knows where to spend.

      const newTier = Object.entries(TALENT_TIER_LEVELS).find(([t, lvl]) => lvl === lu.newLevel);

      const tierUnlock = newTier && (prof?.talents || []).some(t => t.tier === Number(newTier[0]))

        ? `Talent Tier ${newTier[0]} unlocked`

        : null;

      setLevelUpToast({ profId, newLevel: lu.newLevel, profName: prof?.name, profIcon: prof?.icon, milestone, tierUnlock });

      setTimeout(() => setLevelUpToast(null), 5000);

    }

    return xpInfo;

  };



  // 2026-04-27 (Visual Port): profession card uses the prototype's `.pcard`

  // markup (pc-hdr, pc-lvl-row, pc-xpbar, unlock-track, pc-stats, prof-

  // talents-btn). Class-based styling lives in injectProfessionStyles.

  const renderProfCard = (prof) => {

    const player = playerProfsState[prof.id] || { level: 1, xp: 0, totalXp: 0, skillPoints: 0, talentRanks: {} };

    const atMax = player.level >= (prof.maxLevel || 10);

    const need = atMax ? 0 : xpForLevel(prof, player.level + 1);

    const pct = atMax ? 100 : (need > 0 ? Math.min(100, Math.round((player.xp / need) * 100)) : 0);

    const sp = player.skillPoints || 0;

    const typeClass = prof.type === 'major' ? 'major' : 'minor';

    const milestoneLevels = (prof.milestones || []).map(m => m.level).slice(0, 6);

    const hasTalents = (prof.talents || []).length > 0;

    const recipeCount = recipes.filter(r => r.profReq && r.profReq.profId === prof.id).length;

    return (

      <div key={prof.id} className={`pcard ${typeClass}${atMax ? ' maxed' : ''}`}>

        <div className="pc-hdr">

          <div className="pc-emblem">{prof.icon}</div>

          <div className="pc-info">

            <div className="pc-name">{prof.name}</div>

            <div className="pc-type">{prof.type.toUpperCase()} · {prof.desc}</div>

          </div>

        </div>

        <div className="pc-lvl-row">

          <div className="pc-lvl-big">{player.level}</div>

          <div className="pc-lvl-lbl">/ {prof.maxLevel}</div>

          <div className="pc-xp-text">{atMax ? 'MAXED' : `${player.xp} / ${need} XP`}</div>

        </div>

        <div className="pc-xpbar"><div className="pc-xpbar-fill" style={{ width: `${pct}%` }} /></div>

        {milestoneLevels.length > 0 && (

          <div className="unlock-track">

            <div className="unlock-track-lbl">✦ UNLOCKS</div>

            <div className="unlock-row">

              {milestoneLevels.map(lvl => {

                const ms = (prof.milestones || []).find(m => m.level === lvl);

                const reached = player.level >= lvl;

                const isNext = !reached && lvl <= player.level + 1 && lvl > player.level;

                return (

                  <div key={lvl} className={`unlock-dot${reached ? ' reached' : ''}${isNext ? ' next' : ''}`} title={`Lvl ${lvl}: ${ms ? ms.desc : ''}`}>

                    {reached ? '✦' : lvl}

                    <span className="ud-lvl">Lvl {lvl}</span>

                  </div>

                );

              })}

            </div>

          </div>

        )}

        <div className="pc-stats">

          <div className="pc-stat"><b>{(player.totalXp || 0).toLocaleString()}</b> total XP</div>

          <div className="pc-stat"><b>{recipeCount}</b> recipes</div>

        </div>

        {hasTalents && (

          <button className={`prof-talents-btn${sp > 0 ? ' has-points' : ''}`} onClick={() => setTalentTreeProfId(prof.id)}>

            🌟 Talents{sp > 0 && <span className="pts-badge">{sp}</span>}

          </button>

        )}

      </div>

    );

  };



  // Spend one skill point on a talent. Persists via onUpdateChar.

  // Re-validates with canSpendTalent at click time so concurrent state

  // changes (multi-tab) don't let an invalid spend through.

  const spendTalentRank = (profId, talentId) => {

    if (!onUpdateChar) return;

    const prof = DEFAULT_PROFESSIONS.find(p => p.id === profId);

    if (!prof) return;

    const player = playerProfsState[profId];

    if (!player) return;

    const talent = (prof.talents || []).find(t => t.id === talentId);

    if (!talent) return;

    const check = canSpendTalent(prof, talent, player);

    if (!check.ok) { toast(`🚫 ${check.reason}`); return; }

    const nextRanks = { ...(player.talentRanks || {}), [talentId]: (player.talentRanks?.[talentId] || 0) + 1 };

    const nextPlayer = { ...player, skillPoints: (player.skillPoints || 0) - 1, talentRanks: nextRanks };

    persistPlayerProfs({ ...playerProfsState, [profId]: nextPlayer });

    toast(`✦ ${talent.name} → Rank ${nextRanks[talentId]}`);

  };



  // 2026-04-27 (Visual Port — Phase 3): talent tree as the prototype's

  // Last Epoch-style SVG node graph. Layout uses x/y from each talent

  // (added to DEFAULT_PROFESSIONS above). State classes (locked /

  // blocked / reachable / available / ranked / ranked-clickable /

  // maxed) drive all the visuals via injectProfessionStyles. The shared

  // tooltip is a single positioned div mutated via mouse events on each

  // node — escapes scroll clipping and avoids re-rendering 8+ tooltips.

  const renderTalentTree = () => {

    if (!profsEnabled) return null;

    if (!talentTreeProfId) return null;

    const prof = DEFAULT_PROFESSIONS.find(p => p.id === talentTreeProfId);

    if (!prof) return null;

    const player = playerProfsState[talentTreeProfId] || { level: 1, skillPoints: 0, talentRanks: {} };

    const ranks = player.talentRanks || {};

    const sp = player.skillPoints || 0;

    const talents = prof.talents || [];

    const byId = {};

    talents.forEach(t => { byId[t.id] = t; });



    const computeState = (t) => {

      const requiredLevel = TALENT_TIER_LEVELS[t.tier || 1];

      const cur = ranks[t.id] || 0;

      const max = t.maxRanks || 1;

      const tierOk = (player.level || 1) >= requiredLevel;

      const prereqOk = (t.requires || []).length === 0

        || (t.requires || []).some(rid => (ranks[rid] || 0) > 0);

      if (cur >= max) return 'maxed';

      if (!tierOk) return 'locked';

      if (!prereqOk) return 'blocked';

      if (cur > 0) return sp > 0 ? 'ranked-clickable' : 'ranked';

      if (sp > 0) return 'available';

      return 'reachable';

    };



    let maxX = 640, maxY = 200;

    talents.forEach(t => {

      if (typeof t.x === 'number') maxX = Math.max(maxX, t.x + 80);

      if (typeof t.y === 'number') maxY = Math.max(maxY, t.y + 100);

    });

    const canvasW = Math.max(maxX + 40, 640);

    const canvasH = maxY + 40;



    const lines = [];

    talents.forEach(t => {

      (t.requires || []).forEach(rid => {

        const src = byId[rid];

        if (!src || typeof src.x !== 'number' || typeof t.x !== 'number') return;

        const srcRank = ranks[src.id] || 0;

        const tgtRank = ranks[t.id] || 0;

        const tierOkTarget = (player.level || 1) >= TALENT_TIER_LEVELS[t.tier || 1];

        let cls;

        if (srcRank > 0 && tgtRank > 0) cls = 'active';

        else if (srcRank > 0 && tierOkTarget) cls = 'reachable';

        else cls = 'locked';

        lines.push(<line key={`${rid}-${t.id}`} className={`tt-line ${cls}`} x1={src.x} y1={src.y} x2={t.x} y2={t.y} />);

      });

    });



    const totalRanks = Object.values(ranks).reduce((sum, r) => sum + r, 0);

    const hasInvested = totalRanks > 0;



    // Tooltip: mutate the shared div imperatively for performance.

    const showTooltip = (e, t) => {

      const el = document.getElementById('ttTooltip');

      if (!el) return;

      const cur = ranks[t.id] || 0;

      const max = t.maxRanks || 1;

      const eff = t.effect || {};

      const state = computeState(t);

      const isLocked = state === 'locked';

      const isBlocked = state === 'blocked';

      let effectText = '';

      if (eff.kind === 'shopDiscount') effectText = `<b>−${eff.value}% per rank</b> · current −${eff.value * cur}% / max −${eff.value * max}%`;

      else if (eff.kind === 'componentSave') effectText = `<b>+${eff.value}% per rank</b> · current ${eff.value * cur}% / max ${eff.value * max}%`;

      else if (eff.kind === 'xpBonus') effectText = `<b>+${eff.value}% XP per rank</b> · current +${eff.value * cur}% / max +${eff.value * max}%`;

      else if (eff.kind === 'shopWeight') effectText = `<b>+${eff.value} weight per rank</b> · current +${eff.value * cur} / max +${eff.value * max}`;

      else if (eff.kind === 'flag') effectText = `<b>${eff.label || 'Special perk'}</b>`;

      let blockMsg = '';

      if (isLocked) blockMsg = `<div class="tt-tooltip-block">🔒 Requires Profession Level ${TALENT_TIER_LEVELS[t.tier || 1]}</div>`;

      else if (isBlocked) {

        const reqNames = (t.requires || []).map(rid => byId[rid]?.name).filter(Boolean).join(' or ');

        blockMsg = `<div class="tt-tooltip-block">🔒 Requires 1+ rank in: ${reqNames}</div>`;

      }

      const kindLabel = t.kind === 'keystone' ? 'Keystone' : t.kind === 'notable' ? 'Notable' : 'Talent';

      el.innerHTML = `

        <div class="tt-tooltip-tier">Tier ${t.tier || 1} · ${kindLabel}</div>

        <div class="tt-tooltip-name">${t.name}</div>

        <div class="tt-tooltip-desc">${t.desc || ''}</div>

        ${effectText ? `<div class="tt-tooltip-effect">${effectText}</div>` : ''}

        ${blockMsg}`;

      // Position tooltip near the node, but keep it on screen.

      el.classList.add('show');

      const ttRect = el.getBoundingClientRect();

      const nodeRect = e.currentTarget.getBoundingClientRect();

      const pad = 12;

      let left = nodeRect.right + pad;

      let top = nodeRect.top + nodeRect.height / 2 - ttRect.height / 2;

      if (left + ttRect.width > window.innerWidth - 8) left = nodeRect.left - ttRect.width - pad;

      if (top < 8) top = 8;

      if (top + ttRect.height > window.innerHeight - 8) top = window.innerHeight - ttRect.height - 8;

      el.style.left = `${left}px`;

      el.style.top = `${top}px`;

    };

    const hideTooltip = () => {

      const el = document.getElementById('ttTooltip');

      if (el) el.classList.remove('show');

    };



    const handleNodeClick = (t) => {

      const state = computeState(t);

      if (state === 'available' || state === 'ranked-clickable') {

        spendTalentRank(prof.id, t.id);

      }

    };



    return (

      <div className="tt-overlay" onClick={() => { setTalentTreeProfId(null); hideTooltip(); }}>

        <div className="tt-panel" onClick={e => e.stopPropagation()}>

          <button className="tt-close" onClick={() => { setTalentTreeProfId(null); hideTooltip(); }}>✕</button>

          <div className="tt-hdr">

            <div className="tt-hdr-l">

              <div className="tt-hdr-ico">{prof.icon}</div>

              <div className="tt-hdr-info">

                <div className="tt-title">{prof.name} Talents</div>

                <div className="tt-sub">Spend skill points to invest in passive bonuses. Earn 1 point per profession level.</div>

              </div>

            </div>

            <div className={`tt-points ${sp > 0 ? 'has-points' : 'no-points'}`}>

              <span className="tt-points-num">{sp}</span>

              <span className="tt-points-lbl">Skill<br />Points</span>

            </div>

          </div>

          <div className="tt-canvas-scroll">

            {talents.length === 0 ? (

              <div style={{ textAlign: 'center', padding: 60, color: 'var(--tm)', fontStyle: 'italic' }}>

                No talents configured for this profession yet.

              </div>

            ) : (

              <div className="tt-canvas" style={{ width: canvasW, height: canvasH }}>

                <svg className="tt-svg" viewBox={`0 0 ${canvasW} ${canvasH}`} preserveAspectRatio="none">

                  {lines}

                </svg>

                {talents.map(t => {

                  if (typeof t.x !== 'number' || typeof t.y !== 'number') return null;

                  const state = computeState(t);

                  const cur = ranks[t.id] || 0;

                  const max = t.maxRanks || 1;

                  const isMaxed = state === 'maxed';

                  const showLockIcon = state === 'locked' || state === 'blocked';

                  const kind = t.kind || 'standard';

                  return (

                    <div key={t.id}

                      className={`tt-node tt-node-${state}`}

                      data-kind={kind}

                      style={{ left: t.x, top: t.y }}

                      onMouseEnter={(e) => showTooltip(e, t)}

                      onMouseLeave={hideTooltip}

                      onClick={(e) => { e.stopPropagation(); handleNodeClick(t); }}>

                      <div className="tt-node-frame">

                        <span className="tt-node-ico-emoji">{t.icon || '✦'}</span>

                        {showLockIcon && <span className="tt-node-lock-overlay">🔒</span>}

                      </div>

                      <div className="tt-node-pip">{isMaxed ? 'MAX' : `${cur} / ${max}`}</div>

                    </div>

                  );

                })}

              </div>

            )}

          </div>

          <div className="tt-footer">

            <div className="tt-legend">

              <span className="tt-legend-item"><span className="tt-legend-dot available" />Available</span>

              <span className="tt-legend-item"><span className="tt-legend-dot ranked" />Ranked</span>

              <span className="tt-legend-item"><span className="tt-legend-dot maxed" />Maxed</span>

              <span className="tt-legend-item"><span className="tt-legend-dot locked" />Locked</span>

            </div>

            {hasInvested && (

              <button className="tt-reset-btn" title="Refund all points" onClick={() => {

                const refunded = totalRanks;

                const nextPlayer = { ...player, skillPoints: (player.skillPoints || 0) + refunded, talentRanks: {} };

                persistPlayerProfs({ ...playerProfsState, [prof.id]: nextPlayer });

                toast(`↻ ${refunded} skill point${refunded === 1 ? '' : 's'} refunded`);

              }}>↻ Reset Talents</button>

            )}

          </div>

          <div className="tt-tooltip-shared" id="ttTooltip" />

        </div>

      </div>

    );

  };



  // 2026-04-27 (Visual Port): top-bar HUD button. Mirrors the prototype's

  // .prof-hud rendered into the header. Empty state (no chosen profession)

  // shows a red ❓ pill that prompts the player to pick one. Active state

  // shows the primary profession's icon, name, level, and a thin XP bar.

  // The level-up burst pip pulses gold when a level-up just fired.

  const renderProfHud = () => {

    if (!profsEnabled || !char) return null;

    const activeProfs = Object.keys(playerProfsState)

      .map(id => {

        const def = DEFAULT_PROFESSIONS.find(p => p.id === id);

        if (!def) return null;

        return { ...def, ...playerProfsState[id] };

      })

      .filter(Boolean);

    const primary = activeProfs.find(p => p.type === 'major') || activeProfs[0];

    if (!primary) {

      return (

        <div className="prof-hud empty" onClick={() => setProfPanelOpen(true)} title="Choose a profession">

          <div className="ph-emblem" style={{ borderColor: 'var(--tr)', color: 'var(--tr)' }}>❓</div>

          <div className="ph-info">

            <div className="ph-name" style={{ color: 'var(--tr)' }}>No Profession</div>

            <div className="ph-lvl">Click to choose</div>

          </div>

          <div className="ph-chev">▸</div>

        </div>

      );

    }

    const atMax = primary.level >= primary.maxLevel;

    const need = atMax ? 0 : xpForLevel(primary, primary.level + 1);

    const pct = atMax ? 100 : (need > 0 ? Math.min(100, Math.round((primary.xp / need) * 100)) : 0);

    const justLeveled = !!(levelUpToast && levelUpToast.profId === primary.id);

    return (

      <div className="prof-hud" onClick={() => setProfPanelOpen(true)} title="Open Professions">

        <div className="ph-emblem">{primary.icon}</div>

        <div className="ph-info">

          <div className="ph-name">{primary.name}</div>

          <div className="ph-lvl">Lvl {primary.level}{atMax ? ' (MAX)' : ''}</div>

          <div className="ph-bar"><div className="ph-fill" style={{ width: `${pct}%` }} /></div>

        </div>

        <div className="ph-chev">▸</div>

        {justLeveled && <div className="ph-burst">LVL UP!</div>}

      </div>

    );

  };



  // 2026-04-27 (Visual Port): "Your Professions" modal panel. Mirrors the

  // prototype's `.prof-modal-overlay` + `.prof-panel`. Fills `maxMajor`

  // slots with cards/empty-slots, then a minor section with cards + a

  // single empty-slot CTA. Empty-slot click routes the picker to the

  // matching slot type.

  const renderProfPanel = () => {

    if (!profsEnabled || !char || !profPanelOpen) return null;

    const ownedIds = Object.keys(playerProfsState);

    const majorChosen = ownedIds.filter(id => {

      const p = DEFAULT_PROFESSIONS.find(x => x.id === id);

      return p && p.type === 'major';

    });

    const minorChosen = ownedIds.filter(id => {

      const p = DEFAULT_PROFESSIONS.find(x => x.id === id);

      return p && p.type === 'minor';

    });

    const majorSlots = [];

    for (let i = 0; i < maxMajor; i++) {

      if (i < majorChosen.length) {

        const prof = DEFAULT_PROFESSIONS.find(p => p.id === majorChosen[i]);

        if (prof) majorSlots.push(renderProfCard(prof));

      } else {

        majorSlots.push(

          <div key={`empty-major-${i}`} className="empty-slot" onClick={() => { setPickerSlotType('major'); setProfPickerOpen(true); }}>

            <div className="es-ico">✦</div>

            <div className="es-title">EMPTY SLOT</div>

            <div className="es-sub">Click to choose a major profession</div>

          </div>

        );

      }

    }

    return (

      <div className="prof-modal-overlay" onClick={() => setProfPanelOpen(false)}>

        <div className="prof-panel" onClick={e => e.stopPropagation()}>

          <div className="pm-hdr">

            <div className="pm-hdr-l">

              <div style={{ fontSize: 32 }}>⚒️</div>

              <div>

                <div className="pm-hdr-t">Your Professions</div>

                <div className="pm-hdr-sub">Level them through practice — each level unlocks recipes, improves quality rolls, and grants passive bonuses.</div>

              </div>

            </div>

            <button className="pm-close" onClick={() => setProfPanelOpen(false)}>✕</button>

          </div>

          <div className="pm-body">

            <div className="slot-hdr">

              ✦ MAJOR PROFESSIONS

              <span className="sh-count">{majorChosen.length} / {maxMajor}</span>

            </div>

            <div className="prof-grid major">{majorSlots}</div>

            <hr className="sep" />

            <div className="slot-hdr">

              ◆ MINOR PROFESSIONS

              <span className="sh-count">Unlimited · {minorChosen.length} learned</span>

            </div>

            <div className="prof-grid minor">

              {minorChosen.map(id => {

                const prof = DEFAULT_PROFESSIONS.find(p => p.id === id);

                return prof ? renderProfCard(prof) : null;

              })}

              <div className="empty-slot" onClick={() => { setPickerSlotType('minor'); setProfPickerOpen(true); }}>

                <div className="es-ico">◆</div>

                <div className="es-title">LEARN MINOR PROFESSION</div>

                <div className="es-sub">Cooking, fishing, and more</div>

              </div>

            </div>

          </div>

        </div>

      </div>

    );

  };



  // 2026-04-27 (Visual Port): no-profession onboarding banner. Shown

  // above the crafting tabs when the player has the system enabled but

  // hasn't chosen anything yet. Mirrors the prototype's `.feat-banner`.

  const renderOnboardingBanner = () => {

    if (!profsEnabled || !char) return null;

    if (Object.keys(playerProfsState).length > 0) return null;

    return (

      <div className="feat-banner">

        <div className="fb-icon">⚒️</div>

        <div className="fb-body">

          <div className="fb-title">Choose Your Path</div>

          <div className="fb-sub">This campaign has the profession system enabled. Pick a major profession to begin your craft — you can always add minor professions like cooking or fishing alongside it.</div>

        </div>

        <button onClick={() => { setPickerSlotType('major'); setProfPickerOpen(true); }}>Choose Profession</button>

      </div>

    );

  };



  // 2026-04-27 (Visual Port): picker modal. Mirrors the prototype's

  // `.picker-overlay` + `.picker-box`. When `pickerSlotType` is set,

  // filters to that type and uses the type-specific hint text. When

  // unset, shows all professions (legacy "+ Pick a Profession" entry).

  const renderProfPicker = () => {

    if (!profsEnabled) return null;

    if (!profPickerOpen) return null;

    const ownedIds = new Set(Object.keys(playerProfsState));

    const ownedMajorCount = Object.keys(playerProfsState).filter(id => {

      const p = DEFAULT_PROFESSIONS.find(x => x.id === id);

      return p && p.type === 'major';

    }).length;

    const wantType = pickerSlotType;

    const options = wantType ? DEFAULT_PROFESSIONS.filter(p => p.type === wantType) : DEFAULT_PROFESSIONS;

    const close = () => { setProfPickerOpen(false); setPickerSlotType(null); };

    const titleText = wantType === 'major' ? 'Choose a Major Profession'

      : wantType === 'minor' ? 'Choose a Minor Profession'

      : 'Pick a Profession';

    const hintText = wantType === 'major'

      ? <>Major professions are your primary craft. You can have <b>{maxMajor}</b> ({ownedMajorCount} chosen). They unlock the most powerful recipes.</>

      : wantType === 'minor'

      ? <>Minor professions like cooking and fishing are unlimited — pick as many as you like.</>

      : <>Major slots: {ownedMajorCount}/{maxMajor}. Minor professions are unlimited.</>;

    return (

      <div className="picker-overlay" onClick={close}>

        <div className="picker-box" onClick={e => e.stopPropagation()}>

          <div className="pb-hdr">

            <div className="pb-title">{titleText}</div>

            <button className="pm-close" onClick={close}>✕</button>

          </div>

          <div className="pb-body">

            <div className="pb-hint">{hintText}</div>

            <div className="pick-list">

              {options.map(p => {

                const already = ownedIds.has(p.id);

                const blockedMajor = p.type === 'major' && !already && ownedMajorCount >= maxMajor;

                const disabled = already || blockedMajor;

                return (

                  <div key={p.id} className={`pick-item${disabled ? ' disabled' : ''}`} onClick={() => !disabled && pickProfession(p.id)}>

                    <div className="pick-emb">{p.icon}</div>

                    <div className="pick-info">

                      <div className="pick-name">{p.name}</div>

                      <div className="pick-desc">{p.desc}</div>

                    </div>

                    <span className={`pick-type ${p.type}`}>{p.type.toUpperCase()}</span>

                    {already && <span style={{ fontSize: 11, color: 'var(--tgr)', marginLeft: 6 }}>✓ Learned</span>}

                    {blockedMajor && <span style={{ fontSize: 10, color: 'var(--tr)', fontStyle: 'italic', marginLeft: 6 }}>major slot full</span>}

                  </div>

                );

              })}

            </div>

          </div>

        </div>

      </div>

    );

  };



  // 2026-04-27 (Visual Port): centered top banner. Mirrors the

  // prototype's `.lvlup-banner.show`. The shimmerBorder + embPulse

  // animations fire from injectProfessionStyles. Auto-dismisses via the

  // same setTimeout already wired in grantCraftXp; the X also clears it.

  const renderLevelUpToast = () => {

    if (!levelUpToast) return null;

    const { profIcon, profName, newLevel, milestone, tierUnlock } = levelUpToast;

    const profDef = DEFAULT_PROFESSIONS.find(p => p.id === levelUpToast.profId);

    const isMastery = profDef && newLevel >= (profDef.maxLevel || 10);

    const unlockText = tierUnlock || (milestone ? milestone.desc : null);

    return (

      <div className="lvlup-banner show" onClick={() => setLevelUpToast(null)}>

        <div className="lvlup-inner">

          <div className="lvlup-emb">{profIcon}</div>

          <div className="lvlup-body">

            <div className="lvlup-title">✦ PROFESSION LEVEL UP ✦</div>

            <div className="lvlup-name">{profName}</div>

            <div className="lvlup-lvl">Level {newLevel}{isMastery ? ' — MASTERY!' : ''}</div>

            {unlockText && <div className="lvlup-unlock">✦ Unlocked: {unlockText}</div>}

          </div>

          <button className="lvlup-close" onClick={(e) => { e.stopPropagation(); setLevelUpToast(null); }}>✕</button>

        </div>

      </div>

    );

  };



  // ─── RENDER ─────────────────────────────────────────────

  return (

    <div className="crafting-scope">



      {/* Header — outer band is full-width so the sticky background spans
          the viewport; inner row is max-width-constrained so the back
          button on the left and prof HUD on the right line up with the
          1200px body content below. */}

      <div style={{background:"var(--bg1)",borderBottom:"1px solid var(--bs)",position:"sticky",top:0,zIndex:100}}>

      <div style={{maxWidth:1200,margin:"0 auto",display:"flex",alignItems:"center",justifyContent:"space-between",padding:mob?"8px 12px":"12px 20px"}}>

        <div style={{display:"flex",alignItems:"center",gap:mob?8:12}}>

          {!mob && onBack && <button onClick={onBack} style={{background:"var(--bg2)",border:"1px solid var(--bs)",color:"var(--ts)",padding:"6px 14px",borderRadius:"var(--rs)",cursor:"pointer",fontSize:13}}>← Back</button>}

          {mob && onBack && <button onClick={onBack} style={{background:"var(--bg2)",border:"1px solid var(--bs)",color:"var(--ts)",padding:"6px 10px",borderRadius:"var(--rs)",cursor:"pointer",fontSize:12}}>←</button>}

          <div style={{fontFamily:"'Cinzel',serif",fontSize:mob?18:22,color:"var(--tg)",display:"flex",alignItems:"center",gap:mob?6:10}}>

            <span>⚒️</span> Crafting

          </div>

        </div>

        <div style={{display:"flex",alignItems:"center",gap:mob?6:10}}>

          {/* 2026-04-27 (Visual Port): profession HUD lives in the

              header right zone, mirroring the prototype. Hidden in DM

              view so the DM toggle isn't cluttered. */}

          {view === "player" && renderProfHud()}

          {/* 2026-04-23: privacy fix — the Player/DM toggle is DM-only. Previously

              any player could click "👑 DM" and see all recipes including

              undiscovered/hidden ones, plus the item/recipe editor. Now the

              toggle only renders when propIsDM is true; the button set is simply

              absent for players (cleaner than disabling). */}

          {propIsDM && (

            <div style={{display:"flex",gap:4,background:"var(--bgi)",borderRadius:"var(--rm)",padding:3}}>

              <button onClick={() => { setView("player"); setEditingRecipe(null); setEditingItem(null); }}

                style={{padding:mob?"5px 12px":"6px 16px",border:"none",borderRadius:"var(--rs)",background:view==="player"?"var(--ap)":"none",color:view==="player"?"#fff":"var(--ts)",fontSize:mob?12:13,cursor:"pointer"}}>{mob?"🧙":"🧙 Player"}</button>

              <button onClick={() => { setView("dm"); setSelRecipe(null); }}

                style={{padding:mob?"5px 12px":"6px 16px",border:"none",borderRadius:"var(--rs)",background:view==="dm"?"var(--ap)":"none",color:view==="dm"?"#fff":"var(--ts)",fontSize:mob?12:13,cursor:"pointer"}}>{mob?"👑":"👑 DM"}</button>

            </div>

          )}

        </div>

      </div>

      </div>



      {/* Campaign Bar */}

      {(campaigns.length > 1 || view === "dm") && (

        <div style={{maxWidth:1200,margin:"0 auto",padding:mob?"12px 12px 0":"20px 20px 0"}}>

          <div style={{display:"flex",alignItems:"center",gap:mob?6:8,padding:mob?"6px 10px":"8px 12px",background:"var(--bgi)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",flexWrap:"wrap"}}>

            {renamingCampaign ? (

              <div style={{display:"flex",alignItems:"center",gap:6,width:mob?"100%":"auto"}}>

                <input autoFocus value={renameVal} onChange={(e) => setRenameVal(e.target.value)}

                  onKeyDown={(e) => {

                    if (e.key === "Enter" && renameVal.trim()) {

                      setCampaigns(campaigns.map((c) => c.id === activeCampaignId ? { ...c, name: renameVal.trim() } : c));

                      setRenamingCampaign(false);

                      toast("✏️ Campaign renamed!");

                    } else if (e.key === "Escape") setRenamingCampaign(false);

                  }}

                  style={{padding:"5px 12px",border:"1px solid var(--ap)",borderRadius:"var(--rs)",background:"var(--bg2)",color:"var(--tp)",fontSize:13,flex:mob?1:undefined,width:mob?"auto":180,outline:"none"}} />

                <button onClick={() => {

                  if (renameVal.trim()) {

                    setCampaigns(campaigns.map((c) => c.id === activeCampaignId ? { ...c, name: renameVal.trim() } : c));

                    toast("✏️ Campaign renamed!");

                  }

                  setRenamingCampaign(false);

                }} style={{padding:"4px 10px",border:"1px solid var(--ag)",borderRadius:"var(--rs)",background:"var(--agb)",color:"var(--tgr)",fontSize:11,cursor:"pointer"}}>✓</button>

                <button onClick={() => setRenamingCampaign(false)}

                  style={{padding:"4px 10px",border:"1px solid var(--bs)",borderRadius:"var(--rs)",background:"var(--bg2)",color:"var(--ts)",fontSize:11,cursor:"pointer"}}>✕</button>

              </div>

            ) : (

              <select value={activeCampaignId} onChange={(e) => switchCampaign(e.target.value)}

                style={{padding:"5px 12px",border:"1px solid var(--bs)",borderRadius:"var(--rs)",background:"var(--bg2)",color:"var(--tp)",fontSize:13,cursor:"pointer",maxWidth:mob?undefined:200,minWidth:mob?0:120,flex:mob?1:undefined}}>

                {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}

              </select>

            )}

            {view === "dm" && !renamingCampaign && (

              <div style={{display:"flex",gap:4,marginLeft:mob?0:"auto",flexWrap:"wrap"}}>

                <button onClick={() => {

                  const isPinned = pinnedCampaigns.includes(activeCampaignId);

                  if (isPinned) { setPinnedCampaigns(pinnedCampaigns.filter((id) => id !== activeCampaignId)); toast("📍 Unpinned"); }

                  else { setPinnedCampaigns([...pinnedCampaigns, activeCampaignId]); toast("📌 Pinned!"); }

                }} style={{padding:"4px 10px",border:"1px solid var(--bs)",borderRadius:"var(--rs)",background:"var(--bg2)",color:"var(--ts)",fontSize:11,cursor:"pointer"}}

                  title={pinnedCampaigns.includes(activeCampaignId) ? "Unpin" : "Pin for quick access"}>

                  {pinnedCampaigns.includes(activeCampaignId) ? "📌" : "📍"}

                </button>

                <button onClick={() => {

                  const cur = campaigns.find((c) => c.id === activeCampaignId);

                  setRenameVal(cur ? cur.name : "");

                  setRenamingCampaign(true);

                }} style={{padding:"4px 10px",border:"1px solid var(--bs)",borderRadius:"var(--rs)",background:"var(--bg2)",color:"var(--ts)",fontSize:11,cursor:"pointer"}} title="Rename campaign">✏️</button>

                <button onClick={() => {

                  saveCampaign();

                  const id = "c" + Date.now();

                  const newCampaigns = [...campaigns, { id, name: "New Campaign" }];

                  setCampaigns(newCampaigns);

                  setCampaignStore((prev) => ({

                    ...prev,

                    [id]: { items: [], recipes: [], inv: {}, allTags: ["crafting"], categories: [{ id: "weapons", l: "⚔️ Weapons" }, { id: "armor", l: "🛡️ Armor" }, { id: "potions", l: "🧪 Potions" }, { id: "tools", l: "🔧 Tools" }, { id: "materials", l: "📦 Materials" }, { id: "food", l: "🍖 Food" }], hintCfg: { showUnknown: true, defaultHint: "none" }, failMode: "pct_one", failPctEach: 10, failPctOne: 30, undoLog: [], favIcons: [], proxOpen: {}, craftLog: [] },

                  }));

                  switchCampaign(id);

                }} style={{padding:"4px 10px",border:"1px solid var(--bs)",borderRadius:"var(--rs)",background:"var(--bg2)",color:"var(--ts)",fontSize:11,cursor:"pointer"}}>+ New</button>

                <button onClick={() => {

                  saveCampaign();

                  const id = "c" + Date.now();

                  const cur = campaigns.find((c) => c.id === activeCampaignId);

                  const newCampaigns = [...campaigns, { id, name: (cur ? cur.name : "Campaign") + " (Copy)" }];

                  setCampaigns(newCampaigns);

                  const curData = deepClone({ items, recipes, inv, allTags, categories: categories.filter((c) => c.id !== "all"), hintCfg, failMode, failPctEach, failPctOne, undoLog, favIcons, proxOpen, craftLog });

                  setCampaignStore((prev) => ({ ...prev, [id]: curData }));

                  switchCampaign(id);

                  toast("📋 Campaign duplicated!");

                }} style={{padding:"4px 10px",border:"1px solid var(--bs)",borderRadius:"var(--rs)",background:"var(--bg2)",color:"var(--ts)",fontSize:11,cursor:"pointer"}} title="Duplicate campaign">📋</button>

                {campaigns.length > 1 && (

                  <button onClick={() => setShowImportModal(true)}

                    style={{padding:"4px 10px",border:"1px solid var(--bs)",borderRadius:"var(--rs)",background:"var(--bg2)",color:"var(--ts)",fontSize:11,cursor:"pointer"}} title="Import from another campaign">📥</button>

                )}

                {campaigns.length > 1 && (

                  <button onClick={() => setShowDeleteConfirm(true)}

                    style={{padding:"4px 10px",border:"1px solid var(--bs)",borderRadius:"var(--rs)",background:"var(--bg2)",color:"var(--tr)",fontSize:11,cursor:"pointer"}}>🗑️</button>

                )}

              </div>

            )}

            {/* Pinned campaigns quick access */}

            {pinnedCampaigns.filter((id) => campaigns.find((c) => c.id === id)).length > 0 && (

              <div style={{display:"flex",gap:3,flexWrap:"wrap",width:"100%",marginTop:4}}>

                {pinnedCampaigns.filter((id) => campaigns.find((c) => c.id === id)).map((id) => {

                  const c = campaigns.find((x) => x.id === id);

                  return c ? (

                    <button key={id} onClick={() => switchCampaign(id)}

                      style={{padding:"3px 10px",border:"1px solid "+(id===activeCampaignId?"var(--ap)":"var(--bs)"),borderRadius:"var(--rs)",

                        background:id===activeCampaignId?"var(--apb)":"var(--bg2)",color:id===activeCampaignId?"var(--tpu)":"var(--ts)",

                        fontSize:11,cursor:"pointer"}}>{c.name}</button>

                  ) : null;

                })}

              </div>

            )}

          </div>

        </div>

      )}



      {/* Main Content */}

      <div style={{maxWidth:1200,margin:"0 auto",padding:mob?12:20,display:"flex",flexDirection:"column",gap:mob?12:20}}>

        {view === "player" ? (

          <>

            {/* 2026-04-27 (Visual Port): the inline "Professions bar" was

                replaced by the prototype's HUD-button-into-modal flow. The

                HUD lives in the header (rendered below in the header zone);

                here we show only the no-profession onboarding banner so

                first-time players still see a clear CTA above the tabs. */}

            {renderOnboardingBanner()}

            <div style={{display:"flex",gap:2,background:"var(--bgi)",borderRadius:"var(--rm)",padding:3,width:mob?"100%":"fit-content",overflowX:mob?"auto":"visible",WebkitOverflowScrolling:"touch"}}>

              {[

                { id: "recipe", label: mob ? "📖 Recipes" : "📖 Recipe Crafting" },

                { id: "free", label: mob ? "🧪 Craft" : "🧪 Free Craft" },

                { id: "crafted", label: `📦 Crafted${craftLog.length ? ` (${craftLog.length})` : ""}` },

                { id: "shop", label: mob ? "🛒 Shop" : "🛒 Recipe Shop" },

                { id: "proximity", label: mob ? "📊 Prox." : "📊 Proximity" },

              ].map((t) => (

                <button key={t.id} onClick={() => setPlayerTab(t.id)}

                  style={{padding:mob?"6px 12px":"7px 18px",border:"none",borderRadius:6,background:playerTab===t.id?"var(--ap)":"none",color:playerTab===t.id?"#fff":"var(--ts)",fontFamily:"'Cinzel',serif",fontSize:mob?11:13,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>

                  {t.label}

                </button>

              ))}

            </div>

            {playerTab === "recipe" && <PlayerRecipeView />}

            {playerTab === "free" && <PlayerFreeCraft />}

            {playerTab === "crafted" && <PlayerCraftLog />}

            {playerTab === "shop" && <PlayerRecipeShop />}

            {playerTab === "proximity" && <PlayerProximity />}

            {renderProfPanel()}

            {renderProfPicker()}

            {renderTalentTree()}

            {renderLevelUpToast()}

          </>

        ) : (

          <>

            {/* 2026-04-29: DM-only profession-system toggle. Lets the DM run

                a campaign with crafting but no profession layer (no XP, no

                gating, no HUD). When OFF: all profession UI hides for both

                DM and players, recipe profReq fields are ignored, no XP

                grants fire. When ON: full profession ecosystem (current

                default behavior). Persists to globalSettings.profSettings.

                The toggle itself is hidden while the Professions feature flag

                is off. */}

            {profsFeatureEnabled && onUpdateProfSettings && (

              <div style={{display:"flex",alignItems:"center",gap:10,padding:mob?"8px 12px":"10px 14px",marginBottom:10,background:"var(--bgi)",border:"1px solid var(--bs)",borderRadius:"var(--rm)",fontSize:mob?12:13,flexWrap:"wrap"}}>

                <span style={{fontFamily:"'Cinzel',serif",color:"var(--tg)",letterSpacing:.5}}>⚒️ Profession System</span>

                <button

                  onClick={() => onUpdateProfSettings({ ...(profSettings || {}), enabled: !profsEnabled })}

                  style={{padding:"4px 12px",borderRadius:"var(--rs)",border:"1px solid "+(profsEnabled?"var(--ag)":"var(--bs)"),background:profsEnabled?"var(--agb)":"var(--bg1)",color:profsEnabled?"var(--tgr)":"var(--ts)",fontFamily:"'Cinzel',serif",fontSize:mob?11:12,cursor:"pointer",fontWeight:600,letterSpacing:.5}}>

                  {profsEnabled ? "✓ Enabled" : "○ Disabled"}

                </button>

                <span style={{fontSize:mob?10:11,color:"var(--tm)",fontStyle:"italic",flex:1,minWidth:160}}>

                  {profsEnabled

                    ? "Crafting requires professions; players earn XP and unlock recipes by leveling."

                    : "Pure crafting — no professions, no XP, no level gates. Recipes are available to anyone with the materials."}

                </span>

              </div>

            )}

            <div style={{display:"flex",gap:mob?2:4,marginBottom:0,overflowX:mob?"auto":"visible",WebkitOverflowScrolling:"touch"}}>

              {[

                { id: "recipes", label: mob ? `📖 (${recipes.length})` : `📖 Recipes (${recipes.length})` },

                { id: "items", label: mob ? `📦 (${items.length})` : `📦 Items (${items.length})` },

                { id: "freecraft", label: mob ? "🧪 Craft" : "🧪 Free Craft" },

                { id: "proximity", label: mob ? "📊 Prox." : "📊 Proximity" },

              ].map((t) => (

                <button key={t.id} onClick={() => { setDmTab(t.id); setEditingRecipe(null); setEditingItem(null); }}

                  style={{padding:mob?"6px 12px":"8px 18px",border:"1px solid "+(dmTab===t.id?"var(--bg)":"var(--bs)"),borderRadius:"var(--rm) var(--rm) 0 0",background:dmTab===t.id?"var(--bgi)":"none",color:dmTab===t.id?"var(--tg)":"var(--ts)",fontFamily:"'Cinzel',serif",fontSize:mob?11:13,cursor:"pointer",borderBottom:"none",whiteSpace:"nowrap",flexShrink:0}}>

                  {t.label}

                </button>

              ))}

            </div>

            {dmTab === "recipes" && <DMRecipes />}

            {dmTab === "items" && <DMItems />}

            {dmTab === "freecraft" && <DMFreeCraftSettings />}

            {dmTab === "proximity" && <DMProximity />}

          </>

        )}

      </div>



      <Toast message={toastMsg} />



      {/* Delete Campaign Confirmation */}

      {showDeleteConfirm && (() => {

        const camp = campaigns.find((c) => c.id === activeCampaignId);

        return (

          <div onClick={(e) => { if (e.target === e.currentTarget) setShowDeleteConfirm(false); }}

            style={{position:"fixed",inset:0,background:"rgba(0,0,0,.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>

            <div style={{background:"var(--bg2)",border:"1px solid var(--bs)",borderRadius:"var(--rl)",maxWidth:380,width:mob?"92%":"90%",textAlign:"center",padding:mob?"20px 16px":"24px 20px"}}>

              <div style={{fontSize:mob?28:36,marginBottom:8}}>🗑️</div>

              <div style={{fontFamily:"Cinzel,serif",fontSize:mob?14:16,color:"var(--tr)",marginBottom:12}}>Delete Campaign?</div>

              <div style={{fontSize:13,color:"var(--ts)"}}>Are you sure you want to delete <strong style={{color:"var(--tp)"}}>{camp?.name}</strong>?<br/>This cannot be undone.</div>

              <div style={{display:"flex",gap:10,justifyContent:"center",marginTop:16}}>

                <button onClick={() => setShowDeleteConfirm(false)}

                  style={{padding:"6px 20px",border:"1px solid var(--bs)",borderRadius:"var(--rm)",background:"none",color:"var(--ts)",fontSize:13,cursor:"pointer"}}>Cancel</button>

                <button onClick={() => {

                  setPinnedCampaigns(pinnedCampaigns.filter((id) => id !== activeCampaignId));

                  const newCampaigns = campaigns.filter((c) => c.id !== activeCampaignId);

                  setCampaigns(newCampaigns);

                  const newStore = { ...campaignStore };

                  delete newStore[activeCampaignId];

                  setCampaignStore(newStore);

                  setActiveCampaignId(newCampaigns[0].id);

                  const d = newStore[newCampaigns[0].id];

                  if (d) { setItems(d.items || []); setRecipes(d.recipes || []); setInv(d.inv || {}); setAllTags(d.allTags || ["crafting"]); setCategories([{id:"all",l:"⚡ All"},...(d.categories||[])]); setHintCfg(d.hintCfg || {showUnknown:true,defaultHint:"none"}); setFailMode(d.failMode || "pct_one"); setUndoLog(d.undoLog || []); setFavIcons(d.favIcons || []); setCraftLog(d.craftLog || []); }

                  setShowDeleteConfirm(false);

                  toast("🗑️ Campaign deleted");

                }} style={{padding:"6px 20px",border:"1px solid var(--ar)",borderRadius:"var(--rm)",background:"var(--arb)",color:"var(--tr)",fontSize:13,cursor:"pointer"}}>🗑️ Delete</button>

              </div>

            </div>

          </div>

        );

      })()}



      {/* Import from Campaign Modal */}

      {showImportModal && <ImportModal />}

    </div>

  );

};







// Session Block Renderer — cleanly renders text with optional session/date indicators

const SessionBlockRenderer = ({ text, showMode = 'clean', S }) => {

  if (!text) return null;

  // Strip REVEAL markers

  const cleaned = text.replace(/\{\{REVEAL\}\}|\{\{\/REVEAL\}\}/g, '');

  // Split on session delimiters

  const sessionRx = /──\s*Session\s*#(\d+)\s*──/g;

  const blocks = [];

  let lastIdx = 0;

  let m;

  while ((m = sessionRx.exec(cleaned)) !== null) {

    if (m.index > lastIdx) {

      const prevText = cleaned.substring(lastIdx, m.index).trim();

      if (prevText) blocks.push({ sessionNum: null, text: prevText });

    }

    lastIdx = m.index + m[0].length;

    // Collect text until next delimiter or end

    const nextMatch = sessionRx.exec(cleaned);

    const endIdx = nextMatch ? nextMatch.index : cleaned.length;

    const blockText = cleaned.substring(lastIdx, endIdx).trim();

    if (blockText) blocks.push({ sessionNum: parseInt(m[1]), text: blockText });

    if (nextMatch) {

      // Reset regex to re-process this match

      sessionRx.lastIndex = nextMatch.index;

    }

    lastIdx = endIdx;

  }

  if (lastIdx < cleaned.length) {

    const remaining = cleaned.substring(lastIdx).trim();

    if (remaining) blocks.push({ sessionNum: null, text: remaining });

  }

  if (blocks.length === 0) return <div style={{ fontSize: '0.78rem', color: '#b0a48a', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{cleaned}</div>;

  // If no session delimiters found, just render clean text

  if (blocks.every(b => b.sessionNum === null)) return <div style={{ fontSize: '0.78rem', color: '#b0a48a', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{cleaned}</div>;



  if (showMode === 'clean') {

    // Just concatenate without delimiters

    return <div style={{ fontSize: '0.78rem', color: '#b0a48a', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{blocks.map(b => b.text).join('\n')}</div>;

  }

  if (showMode === 'session') {

    return <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>

      {blocks.map((b, i) => <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>

        {b.sessionNum != null ? <span style={{ fontSize: '0.55rem', color: '#c9a227', background: 'rgba(201,162,39,0.1)', padding: '2px 6px', borderRadius: 4, whiteSpace: 'nowrap', marginTop: 2, flexShrink: 0, minWidth: 32, textAlign: 'center' }}>S{b.sessionNum}</span>

          : <span style={{ minWidth: 32, flexShrink: 0 }} />}

        <div style={{ fontSize: '0.78rem', color: '#b0a48a', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{b.text}</div>

      </div>)}

    </div>;

  }

  if (showMode === 'date') {

    return <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>

      {blocks.map((b, i) => <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>

        <span style={{ fontSize: '0.5rem', color: '#888', whiteSpace: 'nowrap', marginTop: 3, flexShrink: 0, minWidth: 32 }}></span>

        <div style={{ fontSize: '0.78rem', color: '#b0a48a', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{b.text}</div>

      </div>)}

    </div>;

  }

  return <div style={{ fontSize: '0.78rem', color: '#b0a48a', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{cleaned}</div>;

};



// Session display mode toggle

const SessionDisplayToggle = ({ mode, onChange, S }) => (

  <div style={{ display: 'flex', gap: 2, marginBottom: 6 }}>

    {[{ id: 'clean', label: 'Clean' }, { id: 'session', label: 'By Session' }].map(m => (

      <button key={m.id} onClick={() => onChange(m.id)} style={{ padding: '2px 8px', fontSize: '0.55rem', borderRadius: 4, background: mode === m.id ? 'rgba(201,162,39,0.12)' : 'transparent', border: `1px solid ${mode === m.id ? '#c9a22750' : 'transparent'}`, color: mode === m.id ? '#c9a227' : '#666', cursor: 'pointer' }}>{m.label}</button>

    ))}

  </div>

);



// NPC Library Page Component

export const LibraryPage = ({ npcLibrary, saveNpcLibrary, dmJournal, saveDmJournal, journal, saveJournal, upsertJournalEntry, upsertDmJournalEntry, deleteDmJournalEntry, messages, saveMessages, onSendStatusMessage, S, setView, chars, initialEntry, onClearInitEntry, defaultTab, onTabChange, systems = {}, saveChars = () => {}, groupTravelLog = [], saveGroupTravelLog = null, onNotify = null, onQueueSystemRulePendingSaves = null }) => {

  const [activeTab, setActiveTabRaw] = useState(defaultTab || 'session');

  const setActiveTab = (tab) => { setActiveTabRaw(tab); if (onTabChange) onTabChange(tab); };

  const [search, setSearch] = useState('');

  const [selectedEntry, setSelectedEntry] = useState(null);

  const sendStatusUpdate = (message) => {
    if (typeof onSendStatusMessage === 'function') {
      onSendStatusMessage({ message });
      return;
    }
    if (typeof saveMessages !== 'function') return;
    saveMessages({
      ...(messages || {}),
      statusUpdates: [...((messages || {}).statusUpdates || []), message],
    });
  };

  const stripLibraryEntryUiFields = (entry = {}) => {
    const { _type, _journalDesc, _journalId, ...rest } = entry || {};
    return rest;
  };

  const runLibraryRowCommand = (label, command, fallback) => {
    if (typeof command !== 'function') {
      if (typeof fallback === 'function') fallback();
      return false;
    }
    try {
      const result = command();
      if (result && typeof result.catch === 'function') {
        result.catch((error) => {
          console.error(`${label} failed:`, error);
          if (typeof fallback === 'function') fallback();
        });
      }
      return true;
    } catch (error) {
      console.error(`${label} failed:`, error);
      if (typeof fallback === 'function') fallback();
      return false;
    }
  };

  const updateDmJournalEntryFromLibrary = (entry) => {
    const entryId = entry?.id;
    if (!entryId) return;
    const nextEntry = stripLibraryEntryUiFields(entry);
    runLibraryRowCommand(
      'Library DM journal row update',
      typeof upsertDmJournalEntry === 'function'
        ? () => upsertDmJournalEntry({ entryId, entry: nextEntry })
        : null,
      () => {
        const current = Array.isArray(dmJournal) ? dmJournal : [];
        const replaced = current.some(e => e.id === entryId);
        saveDmJournal(replaced ? current.map(e => e.id === entryId ? nextEntry : e) : [...current, nextEntry]);
      },
    );
    setSelectedEntry(prev => prev?.id === entryId ? { ...nextEntry, _type: 'journal' } : prev);
  };

  const deleteDmJournalEntryFromLibrary = (entryId) => {
    if (!entryId) return;
    runLibraryRowCommand(
      'Library DM journal row delete',
      typeof deleteDmJournalEntry === 'function'
        ? () => deleteDmJournalEntry({ entryId })
        : null,
      () => saveDmJournal((dmJournal || []).filter(e => e.id !== entryId)),
    );
  };

  const upsertPlayerJournalEntryFromLibrary = (entry, updatedJournal) => {
    const entryId = entry?.id;
    runLibraryRowCommand(
      'Library player journal row update',
      typeof upsertJournalEntry === 'function' && entryId
        ? () => upsertJournalEntry({ entryId, entry })
        : null,
      () => saveJournal(updatedJournal),
    );
  };



  // Auto-select entry when navigating from DM quick journal

  useEffect(() => {

    if (initialEntry) {

      if (initialEntry._type === 'npc') {

        const npc = Object.values(npcLibrary || {}).find(n => n.id === initialEntry.id || n.name?.toLowerCase() === initialEntry.name?.toLowerCase());

        if (npc) { setActiveTab('npcs'); setSelectedEntry({ ...npc, _type: 'npc' }); }

      } else {

        const entry = (dmJournal || []).find(e => e.id === initialEntry.id) || (dmJournal || []).find(e => e.name?.toLowerCase() === initialEntry.name?.toLowerCase() && e.category === initialEntry.category);

        if (entry) { setActiveTab(entry.category); setSelectedEntry({ ...entry, _type: 'journal' }); }

      }

      if (onClearInitEntry) onClearInitEntry();

    }

  }, [initialEntry]);

  const [editingNpc, setEditingNpc] = useState(null);

  const [showStatBlock, setShowStatBlock] = useState(false);

  const [showAbilities, setShowAbilities] = useState(false);

  const [showPersonality, setShowPersonality] = useState(true);

  const [viewMode, setViewMode] = useState('grid');

  const [detailTab, setDetailTab] = useState('profile');

  const [sessionDisplayMode, setSessionDisplayMode] = useState('clean');

  const [showAddBlock, setShowAddBlock] = useState(false);

  const [editCineId, setEditCineId] = useState(null);

  const [cineType, setCineType] = useState('character');

  const [cineStyle, setCineStyle] = useState('portrait');

  const [cineName, setCineName] = useState('');

  const [cineTitle, setCineTitle] = useState('');

  const [cineQuote, setCineQuote] = useState('');

  const [cineDesc, setCineDesc] = useState('');

  const [cineImage, setCineImage] = useState('');

  const [cineTheme, setCineTheme] = useState('foreboding');

  const [cineFont, setCineFont] = useState('default');

  const [cineTransition, setCineTransition] = useState('stack');

  const [cineSequence, setCineSequence] = useState([{ text: '', delay: 0 }]);

  const [cineBgOpacity, setCineBgOpacity] = useState(0.3);

  const [showCineCustom, setShowCineCustom] = useState(false);

  const [cineTextReveal, setCineTextReveal] = useState('instant');

  const [cineTextEffects, setCineTextEffects] = useState([]);

  const [cineSendTo, setCineSendTo] = useState('all');

  const [cineRecipients, setCineRecipients] = useState([]);

  const [showCreateCine, setShowCreateCine] = useState(false);

  const [cineMood, setCineMood] = useState('nature');

  const [cineBorderColor, setCineBorderColor] = useState('');

  const [customBlockName, setCustomBlockName] = useState('');

  const [addBlockField, setAddBlockField] = useState(null);

  const [addBlockText, setAddBlockText] = useState('');

  const [showCinematicEditor, setShowCinematicEditor] = useState(false);

  const [cinematicPreview, setCinematicPreview] = useState(null); // null or cinematic data for preview

  // 2026-04-27: per-entry-id collapse state, persisted to localStorage.

  // Was a flat {fieldId: collapsed} map shared across every entry —

  // closing a card lost state, switching cards bled state. Now keyed by

  // entry id so each card remembers its own open/closed sections, and

  // defaults to all-expanded (the user explicitly asked for this so DMs

  // see everything by default and only collapse what they want to hide).

  const [dmCardSections, setDmCardSections] = useState(() => {

    try { return JSON.parse(localStorage.getItem('bmg-dm-card-sections') || '{}'); }

    catch { return {}; }

  });

  useEffect(() => {

    try { localStorage.setItem('bmg-dm-card-sections', JSON.stringify(dmCardSections)); }

    catch {}

  }, [dmCardSections]);

  // Active-entry-id derivation. When no entry is selected (e.g. mid-

  // route), `_default` is used as a benign namespace so getters/setters

  // don't NPE; the `_default` bucket is harmless and never persisted in

  // a way that affects real entries.

  const dmActiveEntryId = (selectedEntry && selectedEntry.id) ? selectedEntry.id : '_default';

  const collapsedBlocks = dmCardSections[dmActiveEntryId] || {};

  const setCollapsedBlocks = (updater) => {

    setDmCardSections(prev => {

      const cur = prev[dmActiveEntryId] || {};

      const nextSection = typeof updater === 'function' ? updater(cur) : updater;

      const allDefault = Object.values(nextSection).every(v => v === false);

      if (allDefault) {

        const { [dmActiveEntryId]: _drop, ...rest } = prev;

        return rest;

      }

      return { ...prev, [dmActiveEntryId]: nextSection };

    });

  };

  const [navigationHistory, setNavigationHistory] = useState([]);

  const [hoveredLinkEntry, setHoveredLinkEntry] = useState(null);

  const [linkTooltipPos, setLinkTooltipPos] = useState({ x: 0, y: 0 });



  const LIBRARY_TABS = [

    { id: 'session', label: 'Sessions', icon: '📖', color: '#c9a227' },

    { id: 'npcs', label: 'NPCs', icon: '👤', color: '#4a90d9' },

    { id: 'creatures', label: 'Creatures', icon: '🐾', color: '#c92727' },

    { id: 'items', label: 'Items', icon: '🎒', color: '#d97429' },

    { id: 'recipes', label: 'Recipes', icon: '📋', color: '#17a589' },

    { id: 'diseases', label: 'Diseases', icon: '☣', color: '#8e44ad' },

    { id: 'plants', label: 'Plants', icon: '🌿', color: '#27c95a' },

    { id: 'locations', label: 'Locations', icon: '📍', color: '#e67e22' },

    { id: 'quests', label: 'Quests', icon: '📜', color: '#9b59b6' },

  ];



  const tabInfo = LIBRARY_TABS.find(t => t.id === activeTab) || LIBRARY_TABS[0];



  // Get entries for current tab - merge NPC library with journal entries

  const getEntries = () => {

    const journalEntries = (dmJournal || []).filter(e => e.category === activeTab).map(e => ({ ...e, _type: 'journal' }));

    

    if (activeTab === 'npcs') {

      // Merge: NPC library entries enriched with matching journal descriptions

      const npcEntries = Object.values(npcLibrary || {}).map(n => {

        const journalMatch = (dmJournal || []).find(j => j.category === 'npcs' && j.name?.toLowerCase() === n.name?.toLowerCase());

        return { ...n, _type: 'npc', _journalDesc: journalMatch?.description || null, _journalId: journalMatch?.id || null };

      });

      // Also include any journal NPC entries that DON'T have a matching npcLibrary entry

      const npcNames = new Set(npcEntries.map(n => n.name?.toLowerCase()));

      const orphanJournal = journalEntries.filter(j => !npcNames.has(j.name?.toLowerCase()));

      return [...npcEntries, ...orphanJournal];

    }

    return journalEntries;

  };



  const allEntries = getEntries();

  const filtered = allEntries.filter(e => {

    if (!search) return true;

    const q = search.toLowerCase();

    return e.name?.toLowerCase().includes(q) || e.title?.toLowerCase().includes(q) || e.description?.toLowerCase().includes(q) || (e.tags || []).some(t => t.toLowerCase().includes(q));

  }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));



  const getCatColor = () => tabInfo.color;



  // Build all linkable entries across DM journal + NPC library for cross-category linking

  const allLinkableEntries = useMemo(() => {

    const entries = [];

    // All DM journal entries

    (dmJournal || []).forEach(e => {

      if (e.name) entries.push({ ...e, _type: 'journal' });

    });

    // All NPC library entries (avoid duplicates with journal)

    const journalNames = new Set((dmJournal || []).filter(e => e.category === 'npcs').map(e => e.name?.toLowerCase()));

    Object.values(npcLibrary || {}).forEach(n => {

      if (n.name && !journalNames.has(n.name.toLowerCase())) {

        entries.push({ ...n, _type: 'npc', category: 'npcs' });

      }

    });

    // Player journal entries too (for cross-linking)

    (journal || []).forEach(e => {

      if (e.name && !entries.some(x => x.name?.toLowerCase() === e.name?.toLowerCase())) {

        entries.push({ ...e, _type: 'player-journal' });

      }

    });

    return entries;

  }, [dmJournal, npcLibrary, journal]);



  // Navigate to an entry with history tracking

  const handleEntryNav = (entry, fromEntry = null) => {

    if (fromEntry || selectedEntry) {

      setNavigationHistory(prev => [...prev, { entry: fromEntry || selectedEntry, tab: activeTab, detailTab }]);

    }

    // Find the right tab for this entry

    if (entry._type === 'npc') {

      setActiveTab('npcs');

    } else if (entry.category) {

      setActiveTab(entry.category);

    }

    setSelectedEntry(entry);

    setDetailTab('profile');

    setHoveredLinkEntry(null);

  };



  // Go back to previous entry

  const navGoBack = () => {

    if (navigationHistory.length === 0) return;

    const prev = navigationHistory[navigationHistory.length - 1];

    setNavigationHistory(h => h.slice(0, -1));

    setActiveTab(prev.tab);

    setSelectedEntry(prev.entry);

    setDetailTab(prev.detailTab || 'profile');

  };



  // Close entry and clear history

  const closeEntryView = () => {

    setSelectedEntry(null);

    setNavigationHistory([]);

  };



  // Tooltip handlers for linked text

  const handleLinkHover = (entry, event) => {

    const rect = event.target.getBoundingClientRect();

    setLinkTooltipPos({ x: rect.left + rect.width / 2, y: rect.top - 8 });

    setHoveredLinkEntry(entry);

  };

  const handleLinkLeave = () => setHoveredLinkEntry(null);



  // Render text with cross-category linked names

  const renderLinked = (text, fromEntry = null) => {

    if (!text || allLinkableEntries.length === 0) return text;

    const result = renderLinkedText(text, allLinkableEntries, (e) => handleEntryNav(e, fromEntry), [], [], {

      onHover: handleLinkHover,

      onLeave: handleLinkLeave,

      firstOccurrenceOnly: false,

    });

    return result.elements || result;

  };

  

  const saveNpc = (npc) => {

    // Systems expansion Session 5: detect the alive→dead transition and fire

    // on-npc-death rules for every party character before committing the save. Rule

    // updates merge into chars via saveChars; any pending NPC-state updates (e.g.

    // chained set-npc-disposition actions) merge back into the npcLibrary write.

    const prevNpc = (npcLibrary || {})[npc.id];

    const prevStatus = prevNpc?.status || 'alive';

    const isDeath = npc.status === 'dead' && prevStatus !== 'dead';



    let finalLibrary = { ...(npcLibrary || {}), [npc.id]: { ...npc, updatedAt: new Date().toISOString() } };



    if (isDeath && chars && systems) {

      const triggerContext = { npcId: npc.id, npcName: npc.name, prevStatus };

      const worldContext = {

        chars,

        journal: [...(journal || []), ...(dmJournal || [])],

        npcLibrary: finalLibrary,

      };

      let accChars = { ...chars };
      let nextNpcDeathLibrary = finalLibrary;
      const npcDeathLogEntries = [];
      const npcDeathPendingSaveEntries = [];
      const npcDeathNotices = [];

      Object.entries(chars).forEach(([charId, partyChar]) => {

        if (!partyChar || !(partyChar.systemList || []).length) return;

        const ruleResults = processAllSystemRulesForTrigger('on-npc-death', partyChar, systems, triggerContext, worldContext);

        const ruleUpdates = applySystemRuleResults(partyChar, ruleResults, systems, worldContext);
        const nextPartyChar = Object.keys(ruleUpdates).length > 0
          ? { ...partyChar, ...ruleUpdates }
          : partyChar;

        if (Object.keys(ruleUpdates).length > 0) {

          accChars = { ...accChars, [charId]: nextPartyChar };

        }

        const sideEffects = collectRuleResultSideEffects(ruleResults, { npcLibrary: nextNpcDeathLibrary });
        if (sideEffects.npcLibraryChanged) {
          nextNpcDeathLibrary = sideEffects.nextNpcLibrary;
        }

        const sentStatusMessages = persistSystemRuleStatusMessages(sideEffects.statusMessages, {
          char: nextPartyChar,
          source: 'library.npc-death',
          messages,
          onSendStatusMessage,
          onSaveMessages: saveMessages,
          auditMeta: { category: 'systems', action: 'library.npc-death-status' },
        });

        const ruleLogEntries = buildSystemRuleTravelLogEntries(sideEffects.logEntries, {
          char: nextPartyChar,
          action: 'system-rule-log',
          source: 'library.npc-death',
        });
        npcDeathLogEntries.push(...ruleLogEntries);

        const pendingSaveEntries = buildSystemRulePendingSaveQueueEntriesForCharacter(sideEffects.pendingSaves, {
          char: nextPartyChar,
          systems,
          worldContext: {
            ...worldContext,
            chars: accChars,
            npcLibrary: nextNpcDeathLibrary,
          },
          triggerContext,
          label: `${npc.name || 'NPC'} death rule`,
          source: 'library.npc-death',
        });
        npcDeathPendingSaveEntries.push(...pendingSaveEntries);

        npcDeathNotices.push(...(sideEffects.notifications || []));
        if (pendingSaveEntries.length > 0) {
          const pendingSaveNotice = buildSystemRulePendingSaveNotice(sideEffects.pendingSaves, `${npc.name || 'NPC'} death rule`);
          npcDeathNotices.push(`${pendingSaveNotice || `${npc.name || 'NPC'} death rule requested ${pendingSaveEntries.length} saving throw${pendingSaveEntries.length === 1 ? '' : 's'}.`} Queued for resolution.`);
        } else if (sideEffects.pendingSaves.length > 0) {
          const pendingSaveNotice = buildSystemRulePendingSaveNotice(sideEffects.pendingSaves, `${npc.name || 'NPC'} death rule`);
          if (pendingSaveNotice) npcDeathNotices.push(pendingSaveNotice);
        }
        if (ruleLogEntries.length > 0) {
          npcDeathNotices.push(`${ruleLogEntries.length} system rule log entr${ruleLogEntries.length === 1 ? 'y' : 'ies'} recorded.`);
        }
        if (sentStatusMessages.length > 0) {
          npcDeathNotices.push(`${sentStatusMessages.length} system status message${sentStatusMessages.length === 1 ? '' : 's'} sent.`);

        }

      });

      if (accChars !== chars) saveChars(accChars);

      finalLibrary = nextNpcDeathLibrary;
      if (npcDeathLogEntries.length > 0 && typeof saveGroupTravelLog === 'function') {
        const keep = Math.max(0, 200 - npcDeathLogEntries.length);
        saveGroupTravelLog([...npcDeathLogEntries, ...((groupTravelLog || []).slice(0, keep))]);
      }
      if (npcDeathPendingSaveEntries.length > 0 && typeof onQueueSystemRulePendingSaves === 'function') {
        onQueueSystemRulePendingSaves(npcDeathPendingSaveEntries);
      }
      if (npcDeathNotices.length > 0 && typeof onNotify === 'function') {
        onNotify(npcDeathNotices.join(' | '));
      }

    }



    saveNpcLibrary(finalLibrary);

    if (selectedEntry?.id === npc.id) setSelectedEntry({ ...(finalLibrary[npc.id] || npc), _type: 'npc', updatedAt: new Date().toISOString() });

  };



  const deleteEntry = (entry) => {

    if (!window.confirm(`Delete "${entry.name}"?`)) return;

    if (entry._type === 'npc') {

      const updated = { ...(npcLibrary || {}) }; delete updated[entry.id]; saveNpcLibrary(updated);

    } else {

      deleteDmJournalEntryFromLibrary(entry.id);

    }

    setSelectedEntry(null);

  };



  // Reveal a single NPC reveal item

  const revealNpcItem = (npcId, itemId) => {

    const npc = (npcLibrary || {})[npcId];

    if (!npc) return;

    const item = (npc.revealItems || []).find(ri => ri.id === itemId);

    if (!item || item.visibility !== 'revealable') return;

    // Update item to revealed

    const updatedItems = (npc.revealItems || []).map(ri => ri.id === itemId ? { ...ri, visibility: 'revealed', revealedAt: new Date().toISOString() } : ri);

    const updatedNpc = { ...npc, revealItems: updatedItems, updatedAt: new Date().toISOString() };

    saveNpcLibrary({ ...(npcLibrary || {}), [npcId]: updatedNpc });

    setSelectedEntry({ ...updatedNpc, _type: 'npc' });

    // Push to player journal

    const fieldInfo = REVEAL_FIELDS.find(f => f.id === item.field) || { label: 'Info' };

    const existingEntry = (journal || []).find(j => j.name?.toLowerCase() === npc.name?.toLowerCase() && j.category === 'npcs' && (j.tags || []).includes('dm-revealed'));

    let updatedJournal;

    let nextJournalEntry;

    const revealedLine = `${fieldInfo.icon} ${item.text}`;

    if (existingEntry) {

      nextJournalEntry = { ...existingEntry, description: existingEntry.description ? `${existingEntry.description}\n${revealedLine}` : revealedLine, updatedAt: new Date().toISOString() };

      updatedJournal = (journal || []).map(j => j.id === existingEntry.id ? nextJournalEntry : j);

    } else {

      nextJournalEntry = createJournalEntry('npcs', { name: npc.name, description: revealedLine, visibility: 'shared', createdBy: 'DM', tags: ['dm-revealed'] });

      updatedJournal = [...(journal || []), nextJournalEntry];

    }

    upsertPlayerJournalEntryFromLibrary(nextJournalEntry, updatedJournal);

    // Notify players

    sendStatusUpdate({ id: generateId(), type: 'dm-reveal', fromDM: true, visibility: 'public', text: 'New info about ' + npc.name + ' revealed!', timestamp: new Date().toISOString(), dismissed: false });

  };



  // Reveal all revealable items as a character card

  const revealCharacterCard = (npcId) => {

    const npc = (npcLibrary || {})[npcId];

    if (!npc) return;

    const revealable = (npc.revealItems || []).filter(ri => ri.visibility === 'revealable');

    const alreadyRevealed = (npc.revealItems || []).filter(ri => ri.visibility === 'revealed');

    const toReveal = [...revealable]; // reveal all revealable items

    if (toReveal.length === 0 && alreadyRevealed.length === 0) return;

    // Update all revealable items to revealed

    const now = new Date().toISOString();

    const updatedItems = (npc.revealItems || []).map(ri => ri.visibility === 'revealable' ? { ...ri, visibility: 'revealed', revealedAt: now } : ri);

    const updatedNpc = { ...npc, revealItems: updatedItems, updatedAt: now };

    saveNpcLibrary({ ...(npcLibrary || {}), [npcId]: updatedNpc });

    setSelectedEntry({ ...updatedNpc, _type: 'npc' });

    // Build character card text from ALL revealed items (newly + previously revealed)

    const allRevealed = updatedItems.filter(ri => ri.visibility === 'revealed');

    const grouped = {};

    allRevealed.forEach(ri => {

      const f = ri.field || 'custom';

      if (!grouped[f]) grouped[f] = [];

      grouped[f].push(ri.text);

    });

    let cardText = '';

    if (npc.title) cardText += npc.title + '\n\n';

    REVEAL_FIELDS.forEach(f => {

      if (grouped[f.id]?.length) {

        cardText += `${f.icon} ${f.label}:\n${grouped[f.id].join('\n')}\n\n`;

      }

    });

    cardText = cardText.trim();

    if (!cardText) return;

    // Create or update player journal entry

    const existingEntry = (journal || []).find(j => j.name?.toLowerCase() === npc.name?.toLowerCase() && j.category === 'npcs' && (j.tags || []).includes('dm-revealed'));

    let updatedJournal;

    let nextJournalEntry;

    if (existingEntry) {

      nextJournalEntry = { ...existingEntry, description: cardText, updatedAt: now };

      updatedJournal = (journal || []).map(j => j.id === existingEntry.id ? nextJournalEntry : j);

    } else {

      nextJournalEntry = createJournalEntry('npcs', { name: npc.name, title: npc.title || '', description: cardText, visibility: 'shared', createdBy: 'DM', tags: ['dm-revealed'], currentLocation: npc.currentLocation || '' });

      updatedJournal = [...(journal || []), nextJournalEntry];

    }

    upsertPlayerJournalEntryFromLibrary(nextJournalEntry, updatedJournal);

    sendStatusUpdate({ id: generateId(), type: 'dm-reveal', fromDM: true, visibility: 'public', text: npc.name + ' — character card revealed!', timestamp: new Date().toISOString(), dismissed: false });

  };



  // Render session summary with DM-only/revealable color coding and cross-category links

  const renderSessionSummary = (text, fromEntry = null) => {

    if (!text) return null;

    const cleaned = text.replace(/──\s*Session\s*#\d+\s*──\n?/g, '');

    const segments = [];

    let lastIdx = 0;

    const rx = /\{\{REVEAL\}\}([\s\S]*?)\{\{\/REVEAL\}\}/g;

    let m;

    while ((m = rx.exec(cleaned)) !== null) {

      if (m.index > lastIdx) segments.push({ type: 'dm-only', text: cleaned.substring(lastIdx, m.index) });

      segments.push({ type: 'revealable', text: m[1] });

      lastIdx = m.index + m[0].length;

    }

    if (lastIdx < cleaned.length) segments.push({ type: 'dm-only', text: cleaned.substring(lastIdx) });

    if (segments.length === 0) return <div style={{ fontSize: '0.75rem', color: '#b0a48a', lineHeight: 1.5 }}>{renderLinked(cleaned, fromEntry)}</div>;

    return <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>

      {segments.map((seg, i) => {

        const trimmed = seg.text.trim();

        if (!trimmed) return null;

        return trimmed.split('\n').filter(line => line.trim()).map((line, li) => (

          <div key={`${i}-${li}`} style={{ fontSize: '0.75rem', lineHeight: 1.4, color: seg.type === 'revealable' ? '#8ab4d9' : '#c4a8ff', opacity: seg.type === 'dm-only' ? 0.7 : 1, paddingLeft: 0 }}>

            {renderLinked(line, fromEntry)}

          </div>

        ));

      })}

    </div>;

  };



  // Send NPC cinematic to players

  const sendNpcCinematic = (npcId) => {

    const npc = (npcLibrary || {})[npcId];

    const cine = (npc?.cinematics || [])[0];

    if (!cine?.sequence?.length) return;

    const customStyle = {};

    if (cine.imagePath) { customStyle.bgImage = cine.imagePath; customStyle.bgImageOpacity = cine.bgOpacity ?? 0.3; }

    if (cine.font && cine.font !== 'default') customStyle.font = cine.font;

    const newMsg = {

      id: generateId(), type: 'whisper', category: 'dm-cinematic', timestamp: new Date().toISOString(),

      content: cine.title || npc.name, urgency: 'urgent', fromDM: true, visibility: 'public',

      recipients: [], addToHistory: true, dismissed: false, seen: false, displayStyle: 'dramatic',

      sequence: cine.sequence.filter(s => s.text?.trim()),

      theme: cine.theme || 'foreboding', senderPrefix: '',

      baseTransition: cine.transition || 'stack', revealModifiers: [], textEffects: [],

      customStyle,

    };

    sendStatusUpdate(newMsg);

    // Also reveal the character card if there are revealable items

    if ((npc.revealItems || []).some(ri => ri.visibility === 'revealable')) {

      revealCharacterCard(npcId);

    }

  };



  const SCENERY_MOODS = [

    { id: 'nature', label: 'Nature', desc: 'Forests, rivers, open fields', overlay: 'rgba(39,174,96,0.15)', border: '#27ae60' },

    { id: 'dark', label: 'Dark', desc: 'Castles, dungeons, ruins', overlay: 'rgba(40,20,60,0.6)', border: '#6c3483' },

    { id: 'fire', label: 'Fire & War', desc: 'Battlefields, burning villages', overlay: 'rgba(201,39,39,0.25)', border: '#c92727' },

    { id: 'interior', label: 'Interior', desc: 'Taverns, throne rooms, halls', overlay: 'rgba(201,162,39,0.15)', border: '#c9a227' },

    { id: 'celestial', label: 'Celestial', desc: 'Sky castles, cosmic, divine', overlay: 'rgba(74,144,217,0.2)', border: '#4a90d9' },

    { id: 'urban', label: 'Urban', desc: 'Towns, markets, city streets', overlay: 'rgba(160,140,120,0.15)', border: '#a08c78' },

    { id: 'void', label: 'Void', desc: 'Abyss, emptiness, the unknown', overlay: 'rgba(0,0,0,0.7)', border: '#333' },

    { id: 'fey', label: 'Fey', desc: 'Enchanted, whimsical, otherworldly', overlay: 'rgba(180,100,255,0.15)', border: '#b464ff' },

  ];



  const abilityMod = (score) => { const m = Math.floor(((score || 10) - 10) / 2); return m >= 0 ? `+${m}` : `${m}`; };



  // ====== NPC EDIT VIEW ======

  if (editingNpc) {

    const e = editingNpc;

    const upd = (fields) => setEditingNpc({ ...e, ...fields });

    const saveEditing = () => { if (!e.name?.trim()) return; saveNpc(e); setSelectedEntry({ ...e, _type: 'npc' }); setEditingNpc(null); };

    return (

      <div style={S.container}>

      <div style={{ maxWidth: 640, margin: '0 auto' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>

          <button style={{ ...S.btn, ...S.btnSm }} onClick={() => setEditingNpc(null)}>← Back</button>

          <span style={{ fontSize: '1rem', fontWeight: 600, color: S.title?.color || '#c9a227' }}>{(npcLibrary || {})[e.id] ? '✏️ Edit NPC' : '✨ New NPC'}</span>

          <button style={{ ...S.btn, ...S.btnSm, ...S.btnSuccess }} onClick={saveEditing} disabled={!e.name?.trim()}>💾 Save</button>

        </div>

        <div style={{ ...S.card, padding: 16 }}>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', gap: 8, marginBottom: 12 }}>

            <div><label style={{ ...S.label, fontSize: '0.65rem' }}>Name *</label><input value={e.name} onChange={ev => upd({ name: ev.target.value })} style={{ ...S.input, width: '100%' }} /></div>

            <div><label style={{ ...S.label, fontSize: '0.65rem' }}>Title</label><input value={e.title || ''} onChange={ev => upd({ title: ev.target.value })} placeholder="Captain of the Guard..." style={{ ...S.input, width: '100%' }} /></div>

            <div><label style={{ ...S.label, fontSize: '0.65rem' }}>Status</label><select value={e.status} onChange={ev => upd({ status: ev.target.value })} style={{ ...S.select, width: '100%' }}>

              {NPC_STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}

            </select></div>

          </div>

          <div style={{ marginBottom: 12 }}>

            <label style={{ ...S.label, fontSize: '0.65rem' }}>Disposition</label>

            <div style={{ display: 'flex', gap: 6 }}>

              {NPC_DISPOSITIONS.map(d => (

                <button key={d.id} onClick={() => upd({ disposition: d.id })} style={{ ...S.btn, ...S.btnXs, flex: 1, background: e.disposition === d.id ? `${d.color}20` : 'transparent', borderColor: e.disposition === d.id ? d.color + '60' : undefined, color: e.disposition === d.id ? d.color : '#888' }}>{d.icon} {d.label}</button>

              ))}

            </div>

          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>

            <div><label style={{ ...S.label, fontSize: '0.65rem' }}>Location</label><input value={e.currentLocation || ''} onChange={ev => upd({ currentLocation: ev.target.value })} style={{ ...S.input, width: '100%' }} placeholder="Krin Hall..." /></div>

            <div><label style={{ ...S.label, fontSize: '0.65rem' }}>Tags (comma-separated)</label><input value={(e.tags || []).join(', ')} onChange={ev => upd({ tags: ev.target.value.split(',').map(t => t.trim()).filter(Boolean) })} style={{ ...S.input, width: '100%' }} /></div>

          </div>

          <button onClick={() => setShowPersonality(!showPersonality)} style={{ ...S.btn, ...S.btnXs, width: '100%', marginBottom: 6, textAlign: 'left' }}>🎭 Personality & RP {showPersonality ? '▲' : '▼'}</button>

          {showPersonality && <div style={{ ...S.card, padding: 10, marginBottom: 12 }}>

            <textarea value={e.personality || ''} onChange={ev => upd({ personality: ev.target.value })} rows={2} style={{ ...S.input, width: '100%', resize: 'vertical', marginBottom: 6 }} placeholder="Personality traits..." />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>

              <input value={e.ideals || ''} onChange={ev => upd({ ideals: ev.target.value })} style={{ ...S.input }} placeholder="Ideals..." />

              <input value={e.bonds || ''} onChange={ev => upd({ bonds: ev.target.value })} style={{ ...S.input }} placeholder="Bonds..." />

            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>

              <input value={e.flaws || ''} onChange={ev => upd({ flaws: ev.target.value })} style={{ ...S.input }} placeholder="Flaws..." />

              <input value={e.appearance || ''} onChange={ev => upd({ appearance: ev.target.value })} style={{ ...S.input }} placeholder="Appearance..." />

            </div>

            <textarea value={e.voiceNotes || ''} onChange={ev => upd({ voiceNotes: ev.target.value })} rows={2} style={{ ...S.input, width: '100%', resize: 'vertical' }} placeholder="Voice & RP notes..." />

          </div>}

          <button onClick={() => { if (!e.statBlock && !showStatBlock) upd({ statBlock: createStatBlock() }); setShowStatBlock(!showStatBlock); }} style={{ ...S.btn, ...S.btnXs, width: '100%', marginBottom: 6, textAlign: 'left' }}>📊 Stat Block {e.statBlock ? '✓' : '(optional)'} {showStatBlock ? '▲' : '▼'}</button>

          {showStatBlock && e.statBlock && (() => { const sb = e.statBlock; const updSB = (f) => upd({ statBlock: { ...sb, ...f } }); return <div style={{ ...S.card, padding: 10, marginBottom: 12 }}>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 8 }}>

              <div><label style={{ fontSize: '0.6rem', color: '#c92727' }}>HP</label><input type="number" value={sb.hp} onChange={ev => updSB({ hp: +ev.target.value, maxHp: Math.max(+ev.target.value, sb.maxHp || 0) })} style={{ ...S.input, width: '100%' }} /></div>

              <div><label style={{ fontSize: '0.6rem', color: '#4a90d9' }}>AC</label><input type="number" value={sb.ac} onChange={ev => updSB({ ac: +ev.target.value })} style={{ ...S.input, width: '100%' }} /></div>

              <div><label style={{ fontSize: '0.6rem', color: '#d97429' }}>CR</label><input value={sb.cr} onChange={ev => updSB({ cr: ev.target.value })} style={{ ...S.input, width: '100%' }} /></div>

            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 4, marginBottom: 8 }}>

              {['str','dex','con','int','wis','cha'].map(ab => <div key={ab} style={{ textAlign: 'center' }}>

                <label style={{ fontSize: '0.55rem', color: '#888', textTransform: 'uppercase' }}>{ab}</label>

                <input type="number" value={sb[ab]} onChange={ev => updSB({ [ab]: +ev.target.value })} style={{ ...S.input, width: '100%', textAlign: 'center', fontSize: '0.85rem', padding: '4px 2px' }} />

                <div style={{ fontSize: '0.6rem', color: S.title?.color || '#c9a227' }}>{abilityMod(sb[ab])}</div>

              </div>)}

            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>

              <input value={sb.speed || ''} onChange={ev => updSB({ speed: ev.target.value })} style={{ ...S.input }} placeholder="Speed: 30 ft." />

              <input value={sb.senses || ''} onChange={ev => updSB({ senses: ev.target.value })} style={{ ...S.input }} placeholder="Senses: Darkvision 60 ft." />

            </div>

            <button onClick={() => { upd({ statBlock: null }); setShowStatBlock(false); }} style={{ ...S.btn, ...S.btnXs, ...S.btnDanger, fontSize: '0.6rem', marginTop: 6 }}>Remove Stat Block</button>

          </div>; })()}

          <button onClick={() => setShowAbilities(!showAbilities)} style={{ ...S.btn, ...S.btnXs, width: '100%', marginBottom: 6, textAlign: 'left' }}>⚡ Abilities ({(e.abilities || []).length}) {showAbilities ? '▲' : '▼'}</button>

          {showAbilities && <div style={{ ...S.card, padding: 10, marginBottom: 12 }}>

            {(e.abilities || []).map((ab, i) => <div key={ab.id || i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>

              <div style={{ flex: 1 }}>

                <input value={ab.name} onChange={ev => upd({ abilities: e.abilities.map((a, j) => j === i ? { ...a, name: ev.target.value } : a) })} placeholder="Ability name" style={{ ...S.input, width: '100%', marginBottom: 4 }} />

                <textarea value={ab.description} onChange={ev => upd({ abilities: e.abilities.map((a, j) => j === i ? { ...a, description: ev.target.value } : a) })} rows={2} style={{ ...S.input, width: '100%', resize: 'vertical', fontSize: '0.75rem' }} />

              </div>

              <button onClick={() => upd({ abilities: e.abilities.filter((_, j) => j !== i) })} style={{ ...S.btn, ...S.btnXs, ...S.btnDanger, alignSelf: 'flex-start' }}>✕</button>

            </div>)}

            <button onClick={() => upd({ abilities: [...(e.abilities || []), { id: generateId(), name: '', description: '' }] })} style={{ ...S.btn, ...S.btnXs, ...S.btnSuccess }}>+ Add Ability</button>

          </div>}

          {/* Reveal Items Editor */}

          <div style={{ ...S.card, padding: 10, marginBottom: 12 }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>

              <div style={{ fontSize: '0.72rem', fontWeight: 600, color: S.title?.color || '#c9a227' }}>📋 Character Info (Reveal Items)</div>

              <button onClick={() => upd({ revealItems: [...(e.revealItems || []), { id: generateId(), text: '', field: 'personality', visibility: 'revealable' }] })} style={{ ...S.btn, ...S.btnXs, ...S.btnSuccess }}>+ Add</button>

            </div>

            <div style={{ fontSize: '0.6rem', color: '#666', marginBottom: 8 }}>Add discrete facts about this NPC. Each can be individually revealed to players or kept DM-only.</div>

            {(e.revealItems || []).map((ri, i) => (

              <div key={ri.id || i} style={{ display: 'flex', gap: 6, marginBottom: 6, padding: 6, background: ri.visibility === 'dm-only' ? 'rgba(156,120,255,0.04)' : ri.visibility === 'revealed' ? 'rgba(39,201,90,0.03)' : 'rgba(74,144,217,0.03)', borderRadius: 4, borderLeft: `2px solid ${ri.visibility === 'dm-only' ? '#c4a8ff30' : ri.visibility === 'revealed' ? '#27c95a40' : '#4a90d950'}` }}>

                <div style={{ flex: 1 }}>

                  <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>

                    <select value={ri.field} onChange={ev => upd({ revealItems: (e.revealItems || []).map((r, j) => j === i ? { ...r, field: ev.target.value } : r) })} style={{ ...S.select, width: 'auto', fontSize: '0.65rem', padding: '2px 4px', marginBottom: 0 }}>

                      {REVEAL_FIELDS.map(f => <option key={f.id} value={f.id}>{f.icon} {f.label}</option>)}

                    </select>

                    <select value={ri.visibility} onChange={ev => upd({ revealItems: (e.revealItems || []).map((r, j) => j === i ? { ...r, visibility: ev.target.value } : r) })} style={{ ...S.select, width: 'auto', fontSize: '0.65rem', padding: '2px 4px', marginBottom: 0, color: ri.visibility === 'dm-only' ? '#c4a8ff' : ri.visibility === 'revealed' ? '#27c95a' : '#4a90d9' }}>

                      <option value="dm-only">🔒 DM Only</option>

                      <option value="revealable">👁 Revealable</option>

                      <option value="revealed">✓ Revealed</option>

                    </select>

                  </div>

                  <textarea value={ri.text} onChange={ev => upd({ revealItems: (e.revealItems || []).map((r, j) => j === i ? { ...r, text: ev.target.value } : r) })} rows={2} style={{ ...S.input, width: '100%', resize: 'vertical', fontSize: '0.75rem' }} placeholder="Info about this NPC..." />

                </div>

                <button onClick={() => upd({ revealItems: (e.revealItems || []).filter((_, j) => j !== i) })} style={{ ...S.btn, ...S.btnXs, ...S.btnDanger, alignSelf: 'flex-start' }}>✕</button>

              </div>

            ))}

          </div>

          {e.disposition === 'hostile' && <div style={{ padding: 10, marginBottom: 12, background: 'rgba(201,39,39,0.06)', border: '1px solid rgba(201,39,39,0.2)', borderRadius: S.card?.borderRadius || 6 }}>

            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#c92727', marginBottom: 6 }}>⚔️ Hostile — Loot & XP</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>

              <div><label style={{ fontSize: '0.6rem', color: '#888' }}>XP Reward</label><input type="number" value={e.xpReward || 0} onChange={ev => upd({ xpReward: +ev.target.value })} style={{ ...S.input, width: '100%' }} /></div>

              <div><label style={{ fontSize: '0.6rem', color: '#888' }}>Loot Table ID</label><input value={e.lootTableId || ''} onChange={ev => upd({ lootTableId: ev.target.value || null })} style={{ ...S.input, width: '100%' }} placeholder="(link later)" /></div>

            </div>

          </div>}

          <div style={{ marginBottom: 8 }}><label style={{ ...S.label, fontSize: '0.65rem' }}>DM Notes</label><textarea value={e.notes || ''} onChange={ev => upd({ notes: ev.target.value })} rows={3} style={{ ...S.input, width: '100%', resize: 'vertical' }} placeholder="Notes..." /></div>

          <div><label style={{ fontSize: '0.65rem', color: '#888' }}>Secret Notes (never revealed)</label><textarea value={e.dmSecretNotes || ''} onChange={ev => upd({ dmSecretNotes: ev.target.value })} rows={2} style={{ ...S.input, width: '100%', resize: 'vertical', borderColor: '#5a202040' }} placeholder="Hidden plot info..." /></div>

        </div>

      </div>

      </div>

    );

  }



  // ====== DETAIL VIEW ======

  if (selectedEntry) {

    const isNpc = selectedEntry._type === 'npc';

    const n = isNpc ? { ...((npcLibrary || {})[selectedEntry.id] || selectedEntry), _type: 'npc' } : selectedEntry;

    const disp = NPC_DISPOSITIONS.find(d => d.id === n.disposition);

    const stat = NPC_STATUSES.find(s => s.id === n.status);

    return (

      <div style={S.container}>

      <div style={{ maxWidth: 640, margin: '0 auto' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>

          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>

            {navigationHistory.length > 0 && (

              <button style={{ ...S.btn, ...S.btnSm, background: 'rgba(201,162,39,0.1)', border: '1px solid rgba(201,162,39,0.3)', color: '#c9a227' }} onClick={navGoBack}>

                ← Back {navigationHistory.length > 1 ? `(${navigationHistory.length})` : ''}

              </button>

            )}

            <button style={{ ...S.btn, ...S.btnSm }} onClick={closeEntryView}>← Library</button>

          </div>

          <div style={{ display: 'flex', gap: 6 }}>

            {n._type === 'npc' && (n.cinematics || []).some(c => c.sequence?.some(s => s.text?.trim())) && <button style={{ ...S.btn, ...S.btnSm, background: 'rgba(201,162,39,0.15)', border: '1px solid rgba(201,162,39,0.4)', color: '#ffd700' }} onClick={() => setDetailTab('cinematic')}>🎬 Cinematic</button>}

            {(n.revealItems || []).some(ri => ri.visibility === 'revealable') && n.category !== 'session' && <button style={{ ...S.btn, ...S.btnSm, background: 'rgba(74,144,217,0.15)', border: '1px solid rgba(74,144,217,0.4)', color: '#7db5ff' }} onClick={() => { if (n._type === 'npc') revealCharacterCard(n.id); }}>👁 Reveal Card</button>}

            {n._type === 'npc' && <button style={{ ...S.btn, ...S.btnSm, ...S.btnEdit }} onClick={() => setEditingNpc({ ...n })}>✏️ Edit</button>}

            <button style={{ ...S.btn, ...S.btnSm, ...S.btnDanger }} onClick={() => deleteEntry(n)}>🗑</button>

          </div>

        </div>

        <div style={{ ...S.card, padding: 16, borderLeft: `4px solid ${disp?.color || getCatColor()}` }}>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>

            <span style={{ fontSize: '1.5rem' }}>{n._type === 'npc' ? '👤' : tabInfo.icon}</span>

            <div style={{ flex: 1 }}>

              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: S.title?.color || '#e8d5b7' }}>{n.name}</div>

              {n.title && <div style={{ fontSize: '0.78rem', color: '#888' }}>{n.title}</div>}

            </div>

            {disp && <span style={{ ...S.badge, background: `${disp.color}18`, borderColor: `${disp.color}40`, color: disp.color }}>{disp.label}</span>}

            {stat && <span style={{ ...S.badge, background: `${stat.color}18`, borderColor: `${stat.color}40`, color: stat.color }}>{stat.label}</span>}

          </div>

          {n.currentLocation && <div style={{ fontSize: '0.78rem', color: '#888', marginBottom: 6 }}>📍 {n.currentLocation}</div>}

          {(n.tags || []).length > 0 && <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>{n.tags.map(t => <span key={t} style={{ ...S.badge, ...S.badgeNeutral, fontSize: '0.6rem' }}>{t}</span>)}</div>}

          

          {/* Detail Tabs */}

          {n._type === 'npc' && <div style={{ display: 'flex', gap: 4, marginBottom: 12, borderBottom: `1px solid ${S.card?.borderColor || '#3a3020'}` }}>

            {[{ id: 'profile', label: '🎭 Profile' }, { id: 'combat', label: '⚔️ Combat' }, { id: 'cinematic', label: '🎬 Cinematic' }].map(tab => (

              <button key={tab.id} onClick={() => { setDetailTab(tab.id); if (tab.id === 'cinematic' && !cineName && cineType === 'character') { setCineName(n.name || ''); setCineTitle(n.title || ''); } }} style={{ padding: '6px 14px', fontSize: '0.75rem', fontWeight: detailTab === tab.id ? 600 : 400, background: detailTab === tab.id ? `${S.title?.color || '#c9a227'}12` : 'transparent', border: 'none', borderBottom: detailTab === tab.id ? `2px solid ${S.title?.color || '#c9a227'}` : '2px solid transparent', color: detailTab === tab.id ? (S.title?.color || '#c9a227') : '#888', cursor: 'pointer' }}>{tab.label}</button>

            ))}

          </div>}



          {/* Profile Tab (or all content for non-NPC entries) */}

          {(detailTab === 'profile' || n._type !== 'npc') && <>

            {/* Session-specific detail */}

            {n.category === 'session' && <div style={{ ...S.card, padding: 12, marginBottom: 10 }}>

              {n.sessionDate && <div style={{ fontSize: '0.78rem', color: '#c9a227', marginBottom: 8 }}>📅 {n.sessionDate}</div>}

              {n.summary && <div style={{ marginBottom: 8 }}>

                <div style={{ fontSize: '0.72rem', fontWeight: 600, color: S.title?.color || '#c9a227', marginBottom: 4 }}>Summary</div>

                {renderSessionSummary(n.summary, n)}

              </div>}

              {(n.npcsEncountered || []).length > 0 && <div style={{ marginBottom: 4 }}>

                <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#4a90d9' }}>👤 NPCs: </span>

                {n.npcsEncountered.map((name, ni) => <span key={ni}>{ni > 0 && ', '}<span onClick={() => { const npc = Object.values(npcLibrary || {}).find(np => np.name?.toLowerCase() === name.toLowerCase()); if (npc) handleEntryNav({ ...npc, _type: 'npc', category: 'npcs' }, n); }} style={{ fontSize: '0.72rem', color: '#4a90d9', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textDecorationColor: '#4a90d955', textUnderlineOffset: '3px' }}>{name}</span></span>)}

              </div>}

              {(n.creaturesEncountered || []).length > 0 && <div style={{ marginBottom: 4 }}>

                <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#c92727' }}>🐾 Creatures: </span>

                {n.creaturesEncountered.map((name, ni) => { const hasEntry = (dmJournal || []).some(j => j.category === 'creatures' && j.name?.toLowerCase() === name.toLowerCase()); return <span key={ni}>{ni > 0 && ', '}<span onClick={() => { const creature = (dmJournal || []).find(j => j.category === 'creatures' && j.name?.toLowerCase() === name.toLowerCase()); if (creature) handleEntryNav({ ...creature, _type: 'journal' }, n); }} style={{ fontSize: '0.72rem', color: hasEntry ? '#c92727' : '#b0a48a', cursor: hasEntry ? 'pointer' : 'default', textDecoration: hasEntry ? 'underline' : 'none', textDecorationStyle: 'dotted', textDecorationColor: '#c9272755', textUnderlineOffset: '3px' }}>{name}</span></span>; })}

              </div>}

              {(n.itemsFound || []).length > 0 && <div style={{ marginBottom: 4 }}>

                <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#d97429' }}>🎒 Items: </span>

                {n.itemsFound.map((name, ni) => { const hasEntry = (dmJournal || []).some(j => j.category === 'items' && j.name?.toLowerCase() === name.toLowerCase()); return <span key={ni}>{ni > 0 && ', '}<span onClick={() => { const item = (dmJournal || []).find(j => j.category === 'items' && j.name?.toLowerCase() === name.toLowerCase()); if (item) handleEntryNav({ ...item, _type: 'journal' }, n); }} style={{ fontSize: '0.72rem', color: hasEntry ? '#d97429' : '#b0a48a', cursor: hasEntry ? 'pointer' : 'default', textDecoration: hasEntry ? 'underline' : 'none', textDecorationStyle: 'dotted', textDecorationColor: '#d9742955', textUnderlineOffset: '3px' }}>{name}</span></span>; })}

              </div>}

              {n.description && <div style={{ marginTop: 6 }}>{renderSessionSummary(n.description, n)}</div>}

            </div>}

            {/* NPC personality - only for NPC entries */}

            {n.category !== 'session' && (n.personality || n.appearance || n.voiceNotes) && <div style={{ ...S.card, padding: 10, marginBottom: 10 }}>

              <div style={{ fontSize: '0.72rem', fontWeight: 600, color: S.title?.color || '#c9a227', marginBottom: 4 }}>🎭 Personality & RP</div>

              {n.personality && <div style={{ fontSize: '0.78rem', color: '#b0a48a', marginBottom: 4 }}>{renderLinked(n.personality, n)}</div>}

              {n.appearance && <div style={{ fontSize: '0.72rem', color: '#888', marginBottom: 2 }}>Appearance: {renderLinked(n.appearance, n)}</div>}

              {n.voiceNotes && <div style={{ fontSize: '0.72rem', color: '#888' }}>Voice/RP: {n.voiceNotes}</div>}

            </div>}

            {(n.affiliations || []).length > 0 && <div style={{ ...S.card, padding: 10, marginBottom: 10 }}>

              <div style={{ fontSize: '0.72rem', fontWeight: 600, color: S.title?.color || '#c9a227', marginBottom: 4 }}>⚜️ Affiliations</div>

              {n.affiliations.map((a, i) => <div key={i} style={{ fontSize: '0.72rem', color: '#b0a48a' }}>{a.name}{a.role ? ` — ${a.role}` : ''}</div>)}

            </div>}

            {/* Collapsible reveal item blocks by category */}

            {(() => {

              const allItems = (n.revealItems || []).filter(ri => ri.field !== 'session');

              if (allItems.length === 0 && n.category === 'session') return null;

              const toggleVis = (riId, newVis) => {

                const updItems = (n.revealItems || []).map(r => r.id === riId ? { ...r, visibility: newVis } : r);

                if (n._type === 'npc') { saveNpc({ ...n, revealItems: updItems }); } 

                else { updateDmJournalEntryFromLibrary({ ...n, revealItems: updItems, updatedAt: new Date().toISOString() }); }

              };

              const renderItem = (ri) => {

                const isDmOnly = ri.visibility === 'dm-only';

                const isRevealable = ri.visibility === 'revealable';

                const isRevealed = ri.visibility === 'revealed';

                return <div key={ri.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '4px 6px', marginBottom: 3, borderLeft: `2px solid ${isDmOnly ? '#c4a8ff30' : isRevealed ? '#27c95a40' : '#4a90d950'}`, borderRadius: 2, background: isDmOnly ? 'rgba(156,120,255,0.04)' : isRevealed ? 'rgba(39,201,90,0.03)' : 'rgba(74,144,217,0.03)' }}>

                  <div style={{ flex: 1, fontSize: '0.75rem', color: isDmOnly ? '#c4a8ff' : isRevealed ? '#8fbc8f' : '#8ab4d9', opacity: isDmOnly ? 0.7 : 1, lineHeight: 1.5 }}>{ri.text}</div>

                  {isDmOnly && <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>

                  <span style={{ fontSize: '0.5rem', color: '#c4a8ff' }}>🔒</span>

                  <button onClick={(ev) => { ev.stopPropagation(); toggleVis(ri.id, 'revealable'); }} style={{ padding: '1px 5px', fontSize: '0.45rem', background: 'rgba(74,144,217,0.08)', border: '1px solid #4a90d930', borderRadius: 3, color: '#4a90d9', cursor: 'pointer' }}>make visible</button>

                </div>}

                  {isRevealable && <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>

                    <button onClick={(ev) => { ev.stopPropagation(); revealNpcItem(n.id, ri.id); }} style={{ padding: '2px 8px', fontSize: '0.55rem', fontWeight: 600, background: 'rgba(74,144,217,0.12)', border: '1px solid #4a90d950', borderRadius: 4, color: '#4a90d9', cursor: 'pointer' }}>👁 Reveal</button>

                    <button onClick={(ev) => { ev.stopPropagation(); toggleVis(ri.id, 'dm-only'); }} style={{ padding: '1px 5px', fontSize: '0.5rem', background: 'rgba(156,120,255,0.06)', border: '1px solid #c4a8ff20', borderRadius: 3, color: '#c4a8ff', cursor: 'pointer', flexShrink: 0 }} title="Lock">🔒</button>

                  </div>}

                  {isRevealed && <span style={{ fontSize: '0.5rem', color: '#27c95a', flexShrink: 0, marginTop: 2 }}>✓</span>}

                </div>;

              };

              const renderBlock = (fieldId, icon, label, fallbackCollapsed) => {

                const items = allItems.filter(ri => ri.field === fieldId);

                if (items.length === 0) return null;

                const isCollapsed = collapsedBlocks[fieldId] ?? fallbackCollapsed;

                return <div key={fieldId} style={{ ...S.card, marginBottom: 8, overflow: 'hidden' }}>

                  <button onClick={() => setCollapsedBlocks(prev => ({ ...prev, [fieldId]: !isCollapsed }))} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#e8d5b7' }}>

                    <span style={{ fontSize: '0.72rem', fontWeight: 600, color: S.title?.color || '#c9a227' }}>{icon ? `${icon} ` : ''}{label} <span style={{ fontSize: '0.55rem', color: '#888', fontWeight: 400 }}>({items.length})</span></span>

                    <span style={{ fontSize: '0.6rem', color: '#888', transition: 'transform 0.15s' }}>{isCollapsed ? '▸' : '▾'}</span>

                  </button>

                  {!isCollapsed && <div style={{ padding: '0 10px 10px' }}>{items.map(renderItem)}</div>}

                </div>;

              };

              // Find custom block types (fields not in REVEAL_FIELDS)

              const knownFields = REVEAL_FIELDS.map(f => f.id);

              const customFields = [...new Set(allItems.filter(ri => !knownFields.includes(ri.field)).map(ri => ri.field))];

              

              const addItemToBlock = (fieldId, text) => {

                const newItem = { id: generateId(), text: text || '', field: fieldId, visibility: 'dm-only', revealedAt: null };

                const updItems = [...(n.revealItems || []), newItem];

                if (n._type === 'npc') { saveNpc({ ...n, revealItems: updItems }); }

                else { updateDmJournalEntryFromLibrary({ ...n, revealItems: updItems, updatedAt: new Date().toISOString() }); }

                setCollapsedBlocks(prev => ({ ...prev, [fieldId]: false }));

              };

              

              return <>

                {/* 2026-04-27: all blocks now default to expanded so the

                    DM sees full content immediately. Per-card collapse

                    state (dmCardSections, keyed by entry id) persists in

                    localStorage so the DM's preferences survive close /

                    reopen and don't bleed across cards. */}

                {renderBlock('profile', '', 'Profile', false)}

                {renderBlock('development', '', 'Story Progression', false)}

                {renderBlock('hooks', '', 'Plot Hooks', false)}

                {renderBlock('secrets', '', 'Secrets', false)}

                {renderBlock('relationships', '', 'Relationships', false)}

                {renderBlock('inventory', '', 'Inventory', false)}

                {customFields.map(cf => renderBlock(cf, '', cf, false))}

                {renderBlock('custom', '', 'Other', false)}

                {/* 2026-04-27: Add Block button moved to AFTER the

                    Associations IIFE below, so the data sections

                    (blocks → session notes → associations) read as a

                    contiguous group and the "+ Add Block" affordance is

                    unambiguously a footer action. */}

              </>;

            })()}

            {/* Session Notes — reveal items with field='session' */}

            {(() => {

              const sessionItems = (n.revealItems || []).filter(ri => ri.field === 'session');

              const hasManualNotes = n.notes && n.notes !== '(Auto-created from DM Journal)' && n.notes.trim();

              if (sessionItems.length === 0 && !hasManualNotes && !n.dmSecretNotes) return null;

              return <div style={{ ...S.card, padding: 10, marginBottom: 8 }}>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>

                  <div style={{ fontSize: '0.72rem', fontWeight: 600, color: S.title?.color || '#c9a227' }}>📝 Session Notes</div>

                  <SessionDisplayToggle mode={sessionDisplayMode} onChange={setSessionDisplayMode} S={S} />

                </div>

                {/* Manual notes from NPC editor */}

                {hasManualNotes && <div style={{ fontSize: '0.78rem', color: '#b0a48a', whiteSpace: 'pre-wrap', marginBottom: 8, paddingBottom: 8, borderBottom: `1px solid ${S.card?.borderColor || '#3a302030'}` }}>{n.notes}</div>}

                {/* Session items from reveal items */}

                {sessionItems.length > 0 && <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>

                  {sessionItems.map(ri => {

                    const isDmOnly = ri.visibility === 'dm-only';

                    const isRevealable = ri.visibility === 'revealable';

                    const isRevealed = ri.visibility === 'revealed';

                    return <div key={ri.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '4px 6px', borderLeft: `2px solid ${isDmOnly ? '#c4a8ff30' : isRevealed ? '#27c95a40' : '#4a90d950'}`, borderRadius: 2, background: isDmOnly ? 'rgba(156,120,255,0.04)' : isRevealed ? 'rgba(39,201,90,0.03)' : 'rgba(74,144,217,0.03)' }}>

                      <div style={{ flex: 1, fontSize: '0.75rem', color: isDmOnly ? '#c4a8ff' : isRevealed ? '#8fbc8f' : '#8ab4d9', opacity: isDmOnly ? 0.7 : 1, lineHeight: 1.5 }}>{ri.text}</div>

                      {isDmOnly && <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>

                        <span style={{ fontSize: '0.5rem', color: '#c4a8ff' }}>🔒</span>

                        <button onClick={(ev) => { ev.stopPropagation(); const updItems = (n.revealItems || []).map(r => r.id === ri.id ? { ...r, visibility: 'revealable' } : r); if (n._type === 'npc') { saveNpc({ ...n, revealItems: updItems }); } else { updateDmJournalEntryFromLibrary({ ...n, revealItems: updItems, updatedAt: new Date().toISOString() }); } }} style={{ padding: '1px 5px', fontSize: '0.45rem', background: 'rgba(74,144,217,0.08)', border: '1px solid #4a90d930', borderRadius: 3, color: '#4a90d9', cursor: 'pointer' }}>make visible</button>

                      </div>}

                      {isRevealable && <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>

                        <button onClick={(ev) => { ev.stopPropagation(); revealNpcItem(n.id, ri.id); }} style={{ padding: '2px 8px', fontSize: '0.55rem', fontWeight: 600, background: 'rgba(74,144,217,0.12)', border: '1px solid #4a90d950', borderRadius: 4, color: '#4a90d9', cursor: 'pointer' }}>👁 Reveal</button>

                        <button onClick={(ev) => { ev.stopPropagation(); const updItems = (n.revealItems || []).map(r => r.id === ri.id ? { ...r, visibility: 'dm-only' } : r); if (n._type === 'npc') { saveNpc({ ...n, revealItems: updItems }); } else { updateDmJournalEntryFromLibrary({ ...n, revealItems: updItems, updatedAt: new Date().toISOString() }); } }} style={{ padding: '1px 5px', fontSize: '0.5rem', background: 'rgba(156,120,255,0.06)', border: '1px solid #c4a8ff20', borderRadius: 3, color: '#c4a8ff', cursor: 'pointer', flexShrink: 0 }} title="Lock">🔒</button>

                      </div>}

                      {isRevealed && <span style={{ fontSize: '0.5rem', color: '#27c95a', flexShrink: 0, marginTop: 2 }}>✓</span>}

                    </div>;

                  })}

                </div>}

                {/* Secret notes from NPC editor */}

                {n.dmSecretNotes && <div style={{ marginTop: 8, padding: '6px 8px', background: 'rgba(156,120,255,0.04)', borderLeft: '2px solid #c4a8ff30', borderRadius: 2 }}>

                  <span style={{ fontSize: '0.5rem', color: '#c4a8ff', fontWeight: 600 }}>🔒 SECRET</span>

                  <div style={{ fontSize: '0.75rem', color: '#b0a48a', opacity: 0.8, whiteSpace: 'pre-wrap' }}>{n.dmSecretNotes}</div>

                </div>}

              </div>;

            })()}

            {/* Associations — linked entities */}

            {n._type === 'npc' && (() => {

              const npcName = n.name?.toLowerCase() || '';

              const linkedQuests = (dmJournal || []).filter(j => j.category === 'quests' && ((j.description || '').toLowerCase().includes(npcName) || (j.questGiver || '').toLowerCase() === npcName));

              const linkedItems = (dmJournal || []).filter(j => j.category === 'items' && (j.description || '').toLowerCase().includes(npcName));

              const linkedLocations = (dmJournal || []).filter(j => j.category === 'locations' && (j.description || '').toLowerCase().includes(npcName));

              const linkedNpcs = Object.values(npcLibrary || {}).filter(np => np.name?.toLowerCase() !== npcName && ((np.notes || '').toLowerCase().includes(npcName) || (np.revealItems || []).some(ri => ri.text.toLowerCase().includes(npcName))));

              const hasAny = linkedQuests.length || linkedItems.length || linkedLocations.length || linkedNpcs.length || (n.questConnections || []).length || n.currentLocation || n.xpReward > 0;

              if (!hasAny) return null;

              const isCollapsed = collapsedBlocks['associations'];

              const linkStyle = (color) => ({ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 6px', borderRadius: 3, background: `${color}08` });

              const nameStyle = (color) => ({ fontSize: '0.72rem', color, cursor: 'pointer', textDecoration: 'none' });

              return <div style={{ ...S.card, marginBottom: 8, overflow: 'hidden' }}>

                <button onClick={() => setCollapsedBlocks(prev => ({ ...prev, associations: !isCollapsed }))} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#e8d5b7' }}>

                  <span style={{ fontSize: '0.72rem', fontWeight: 600, color: S.title?.color || '#c9a227' }}>🧭 Connections</span>

                  <span style={{ fontSize: '0.6rem', color: '#888' }}>{isCollapsed ? '▸' : '▾'}</span>

                </button>

                {!isCollapsed && <div style={{ padding: '0 10px 10px', display: 'flex', flexDirection: 'column', gap: 3 }}>

                  {n.currentLocation && <div style={linkStyle('#e67e22')}>

                    <span style={{ fontSize: '0.55rem', color: '#e67e22' }}>📍</span>

                    <span style={nameStyle('#e67e22')} onClick={() => { const loc = (dmJournal || []).find(j => j.category === 'locations' && j.name?.toLowerCase() === n.currentLocation?.toLowerCase()); if (loc) handleEntryNav({ ...loc, _type: 'journal' }, n); }}>{n.currentLocation}</span>

                  </div>}

                  {[...(n.questConnections || []).map(q => ({ name: q, source: 'direct' })), ...linkedQuests.filter(q => !(n.questConnections || []).some(qc => qc.toLowerCase() === q.name?.toLowerCase())).map(q => ({ name: q.name, id: q.id, source: 'mentioned' }))].map((q, qi) => <div key={qi} style={linkStyle('#9b59b6')}>

                    <span style={{ fontSize: '0.55rem', color: '#9b59b6' }}>📜</span>

                    <span style={nameStyle('#9b59b6')} onClick={() => { const quest = (dmJournal || []).find(j => j.category === 'quests' && j.name?.toLowerCase() === q.name?.toLowerCase()); if (quest) handleEntryNav({ ...quest, _type: 'journal' }, n); }}>{q.name}</span>

                    {q.source === 'mentioned' && <span style={{ fontSize: '0.48rem', color: '#666' }}>mentioned</span>}

                  </div>)}

                  {linkedItems.map(item => <div key={item.id} style={linkStyle('#d97429')}>

                    <span style={{ fontSize: '0.55rem', color: '#d97429' }}>🎒</span>

                    <span style={nameStyle('#d97429')} onClick={() => handleEntryNav({ ...item, _type: 'journal' }, n)}>{item.name}</span>

                  </div>)}

                  {linkedNpcs.map(np => <div key={np.id} style={linkStyle('#4a90d9')}>

                    <span style={{ fontSize: '0.55rem', color: '#4a90d9' }}>👤</span>

                    <span style={nameStyle('#4a90d9')} onClick={() => handleEntryNav({ ...np, _type: 'npc', category: 'npcs' }, n)}>{np.name}</span>

                  </div>)}

                  {n.xpReward > 0 && <div style={linkStyle('#c9a227')}>

                    <span style={{ fontSize: '0.55rem', color: '#c9a227' }}>⭐</span>

                    <span style={{ fontSize: '0.72rem', color: '#c9a227' }}>XP: {n.xpReward}</span>

                  </div>}

                </div>}

              </div>;

            })()}

            {/* 2026-04-27: Add Block button — moved here from inside the

                blocks IIFE so it sits below ALL data sections (blocks,

                session notes, associations) rather than splitting them.

                Same UI as before; allItems / addItemToBlock recomputed

                inline since the block-IIFE locals are out of scope. */}

            {(() => {

              const allItems = (n.revealItems || []).filter(ri => ri.field !== 'session');

              const addItemToBlock = (fieldId, text) => {

                const newItem = { id: generateId(), text: text || '', field: fieldId, visibility: 'dm-only', revealedAt: null };

                const updItems = [...(n.revealItems || []), newItem];

                if (n._type === 'npc') { saveNpc({ ...n, revealItems: updItems }); }

                else { updateDmJournalEntryFromLibrary({ ...n, revealItems: updItems, updatedAt: new Date().toISOString() }); }

                setCollapsedBlocks(prev => ({ ...prev, [fieldId]: false }));

              };

              return (

                <div style={{ marginTop: 8 }}>

                  {!showAddBlock ? (

                    <button onClick={() => { setShowAddBlock(true); setAddBlockField(null); setAddBlockText(''); setCustomBlockName(''); }} style={{ ...S.btn, ...S.btnXs, width: '100%', textAlign: 'center', opacity: 0.6 }}>+ Add Block</button>

                  ) : (

                    <div style={{ ...S.card, padding: 10, width: '100%' }}>

                      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginBottom: 8 }}>

                        {REVEAL_FIELDS.filter(f => f.id !== 'session').map(f => {

                          const exists = allItems.some(ri => ri.field === f.id);

                          const isSel = addBlockField === f.id;

                          return <button key={f.id} onClick={() => { setAddBlockField(f.id); setCustomBlockName(''); }} style={{ padding: '3px 8px', fontSize: '0.58rem', borderRadius: 5, background: isSel ? 'rgba(201,162,39,0.2)' : exists ? 'rgba(201,162,39,0.06)' : 'rgba(255,255,255,0.02)', border: `1px solid ${isSel ? '#c9a22780' : exists ? '#c9a22725' : '#3a302030'}`, color: isSel ? '#c9a227' : exists ? '#c9a227' : '#888', cursor: 'pointer', fontWeight: isSel ? 600 : 400 }}>{f.icon} {f.label}{exists ? ' +' : ''}</button>;

                        })}

                        <button onClick={() => { setAddBlockField('__custom__'); }} style={{ padding: '3px 8px', fontSize: '0.58rem', borderRadius: 5, background: addBlockField === '__custom__' ? 'rgba(201,162,39,0.2)' : 'rgba(255,255,255,0.02)', border: `1px solid ${addBlockField === '__custom__' ? '#c9a22780' : '#3a302030'}`, color: addBlockField === '__custom__' ? '#c9a227' : '#888', cursor: 'pointer', fontWeight: addBlockField === '__custom__' ? 600 : 400 }}>📝 Custom</button>

                      </div>

                      {addBlockField === '__custom__' && <div style={{ marginBottom: 8 }}>

                        <input value={customBlockName} onChange={e => setCustomBlockName(e.target.value)} placeholder="Block name..." autoFocus style={{ ...S.input, width: '100%', fontSize: '0.7rem', marginBottom: 0 }} />

                      </div>}

                      {addBlockField && (addBlockField !== '__custom__' || customBlockName.trim()) && <div style={{ marginBottom: 8 }}>

                        <textarea value={addBlockText} onChange={e => setAddBlockText(e.target.value)} rows={2} autoFocus={addBlockField !== '__custom__'} placeholder={`Add note to ${addBlockField === '__custom__' ? customBlockName : (REVEAL_FIELDS.find(f => f.id === addBlockField)?.label || addBlockField)}...`} style={{ ...S.input, width: '100%', resize: 'vertical', fontSize: '0.75rem' }} onKeyDown={e => {

                          if (e.key === 'Enter' && !e.shiftKey && addBlockText.trim()) {

                            e.preventDefault();

                            const field = addBlockField === '__custom__' ? customBlockName.trim() : addBlockField;

                            addItemToBlock(field, addBlockText.trim());

                            setAddBlockText('');

                            setShowAddBlock(false); setAddBlockField(null); setCustomBlockName('');

                          }

                        }} />

                      </div>}

                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>

                        <button onClick={() => { setShowAddBlock(false); setAddBlockField(null); setAddBlockText(''); setCustomBlockName(''); }} style={{ ...S.btn, ...S.btnXs, fontSize: '0.58rem' }}>Cancel</button>

                        <button onClick={() => {

                          const field = addBlockField === '__custom__' ? customBlockName.trim() : addBlockField;

                          if (!field || !addBlockText.trim()) return;

                          addItemToBlock(field, addBlockText.trim());

                          setAddBlockText(''); setShowAddBlock(false); setAddBlockField(null); setCustomBlockName('');

                        }} disabled={!addBlockField || (addBlockField === '__custom__' && !customBlockName.trim()) || !addBlockText.trim()} style={{ ...S.btn, ...S.btnXs, ...S.btnSuccess, fontSize: '0.58rem' }}>Add Note</button>

                      </div>

                    </div>

                  )}

                </div>

              );

            })()}

          </>}

          {/* Cinematic Tab */}

          {detailTab === 'cinematic' && n._type === 'npc' && (() => {

            const cinematics = n.cinematics || [];

            // State is at component level to avoid hooks-in-IIFE error



            const loadCine = (cine) => {

              setEditCineId(cine.id);

              setCineType(cine.messageType || 'dramatic');

              setCineStyle(cine.style || 'portrait');

              setCineName(cine.encounterName || n.name);

              setCineTitle(cine.encounterTitle || n.title || '');

              setCineQuote(cine.encounterQuote || '');

              setCineDesc(cine.encounterDesc || '');

              setCineImage(cine.imagePath || '');

              setCineBorderColor(cine.borderColor || '');

              setCineTheme(cine.theme || 'foreboding');

              setCineFont(cine.font || 'default');

              setCineTransition(cine.transition || 'stack');

              setCineSequence(cine.sequence || [{ text: '', delay: 0 }]);

              setCineBgOpacity(cine.bgOpacity ?? 0.3);

              setCineTextEffects(cine.textEffects || []);

              setShowCreateCine(true);

            };



            const buildCineData = () => {

              // Session 10 post-migration fix (2026-04-22): Convex's v.any() arg

              // validator rejects `undefined` values anywhere in the payload.

              // `moodOverlay` / `moodBorder` are undefined when SCENERY_MOODS has

              // no entry for the current mood (or cineMood is unset). Without

              // scrubbing, the save silently failed on the server-side validator.

              // Coerce undefineds to null so the Convex writer accepts the blob.

              // Broader follow-up (see CLAUDE-NOTES-GENERAL.md): switch npcs

              // schema to JSON-stringified `data` like chars/systems, which

              // side-steps this whole class of bug.

              const moodMeta = SCENERY_MOODS.find(m => m.id === cineMood) || {};

              return {

                id: editCineId || generateId(),

                title: cineType === 'character' ? cineName : (cineSequence[0]?.text?.substring(0, 40) || 'Cinematic'),

                messageType: cineType, style: cineStyle,

                encounterName: cineName, encounterTitle: cineTitle, encounterQuote: cineQuote, encounterDesc: cineDesc,

                borderColor: cineBorderColor || null,

                imagePath: cineImage || null,

                theme: cineTheme, font: cineFont, transition: cineTransition,

                sequence: cineSequence, bgOpacity: cineBgOpacity, textEffects: cineTextEffects,

                mood: cineMood || null,

                moodOverlay: moodMeta.overlay || null,

                moodBorder: moodMeta.border || null,

                linkedEntries: [], savedAt: new Date().toISOString(),

              };

            };



            const saveCineToNpc = () => {

              const data = buildCineData();

              const existing = cinematics.find(c => c.id === data.id);

              const updated = existing ? cinematics.map(c => c.id === data.id ? data : c) : [...cinematics, data];

              saveNpc({ ...((npcLibrary || {})[n.id] || n), cinematics: updated });

              setSelectedEntry({ ...((npcLibrary || {})[n.id] || n), cinematics: updated, _type: 'npc' });

              setEditCineId(data.id);

            };



            const deleteCine = (cineId) => { if (!window.confirm('Delete?')) return; const updated = cinematics.filter(c => c.id !== cineId); saveNpc({ ...((npcLibrary || {})[n.id] || n), cinematics: updated }); setSelectedEntry({ ...((npcLibrary || {})[n.id] || n), cinematics: updated, _type: 'npc' }); if (editCineId === cineId) { setEditCineId(null); setShowCreateCine(false); } };



            const sendCine = (cineData) => {

              const customStyle = {};

              if (cineData.imagePath) { customStyle.bgImage = cineData.imagePath; customStyle.bgImageOpacity = cineData.bgOpacity ?? 0.3; }

              if (cineData.font && cineData.font !== 'default') customStyle.font = cineData.font;

              const isEncounter = ['character','scenery','item','quest','lore'].includes(cineData.messageType);

              const newMsg = {

                id: generateId(), type: 'whisper', category: 'dm-cinematic', timestamp: new Date().toISOString(),

                content: cineData.title || n.name, urgency: 'urgent', fromDM: true,

                visibility: cineSendTo === 'all' ? 'public' : 'private',

                recipients: cineSendTo === 'all' ? [] : cineRecipients,

                addToHistory: true, dismissed: false, seen: false,

                ...(isEncounter ? {

                  displayStyle: 'encounter',

                  encounterType: cineData.messageType,

                  encounter: { name: cineData.encounterName, title: cineData.encounterTitle, image: cineData.imagePath, style: cineData.style, quote: cineData.encounterQuote, description: cineData.encounterDesc, customization: { primaryColor: cineData.moodBorder || SEQUENCE_THEMES[cineData.theme]?.textColor || '#c9a227', borderColor: cineData.borderColor || null, bgColor: cineData.moodOverlay ? undefined : undefined } },

                } : {

                  displayStyle: 'dramatic',

                  sequence: (cineData.sequence || []).filter(s => s.text?.trim()),

                  theme: cineData.theme || 'foreboding',

                  baseTransition: cineData.transition || 'stack',

                  revealModifiers: [], textEffects: cineData.textEffects || [],

                }),

                customStyle,

              };

              sendStatusUpdate(newMsg);

            };



            const previewCine = () => {

              const data = buildCineData();

              const isEncounter = ['character','scenery','item','quest','lore'].includes(data.messageType);

              setCinematicPreview({

                ...data,

                ...(isEncounter ? {

                  displayStyle: 'encounter',

                  encounterType: data.messageType,

                  encounter: { name: data.encounterName, title: data.encounterTitle, image: data.imagePath, style: data.style, quote: data.encounterQuote, description: data.encounterDesc, customization: { primaryColor: data.moodBorder || SEQUENCE_THEMES[data.theme]?.textColor || '#c9a227', borderColor: data.borderColor || null, bgColor: data.moodOverlay ? undefined : undefined } },

                } : {

                  sequence: data.sequence,

                  theme: data.theme,

                  transition: data.transition,

                }),

              });

            };



            const browseImage = () => {

              const inp = document.createElement('input'); inp.type = 'file'; inp.accept = 'image/*';

              inp.onchange = (ev) => { const file = ev.target.files[0]; if (!file) return; const reader = new FileReader(); reader.onload = (re) => setCineImage(re.target.result); reader.readAsDataURL(file); };

              inp.click();

            };



            const MSG_TYPES = [

              { id: 'character', label: 'Character', icon: '👤', color: 'rgba(155,89,182,0.3)' },

              { id: 'scenery', label: 'Scenery', icon: '🏔', color: 'rgba(39,174,96,0.3)' },

              { id: 'item', label: 'Item', icon: '💎', color: 'rgba(230,126,34,0.3)' },

              { id: 'quest', label: 'Quest', icon: '📜', color: 'rgba(230,194,41,0.3)' },

              { id: 'lore', label: 'Lore', icon: '📖', color: 'rgba(139,115,85,0.3)' },

              { id: 'dramatic', label: 'Dramatic', icon: '🎬', color: 'rgba(201,39,39,0.3)' },

            ];



            return <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

              {/* Saved cinematics */}

              {cinematics.map(cine => (

                <div key={cine.id} style={{ ...S.card, padding: '8px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                  <div onClick={() => loadCine(cine)} style={{ cursor: 'pointer', flex: 1 }}>

                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#e8d5b7' }}>{cine.title || 'Untitled'}</div>

                    <div style={{ fontSize: '0.55rem', color: '#888' }}>{(MSG_TYPES.find(t => t.id === cine.messageType) || {}).label || 'Dramatic'} · {SEQUENCE_THEMES[cine.theme]?.name || cine.theme}</div>

                  </div>

                  <div style={{ display: 'flex', gap: 4 }}>

                    <button onClick={() => { sendCine(cine); }} style={{ padding: '3px 8px', fontSize: '0.55rem', fontWeight: 600, background: 'rgba(39,201,90,0.12)', border: '1px solid #27c95a40', borderRadius: 4, color: '#27c95a', cursor: 'pointer' }}>Send</button>

                    <button onClick={() => loadCine(cine)} style={{ ...S.btn, ...S.btnXs, fontSize: '0.55rem' }}>Edit</button>

                    <button onClick={() => deleteCine(cine.id)} style={{ ...S.btn, ...S.btnXs, ...S.btnDanger, fontSize: '0.55rem' }}>✕</button>

                  </div>

                </div>

              ))}



              {/* Create/Edit cinematic */}

              {!showCreateCine && cinematics.length > 0 && <button onClick={() => { setEditCineId(null); setCineName(''); setCineTitle(''); setCineType('character'); setCineStyle('portrait'); setCineSequence([{ text: '', delay: 0 }]); setCineImage(''); setCineQuote(''); setCineDesc(''); setShowCreateCine(true); }} style={{ ...S.btn, ...S.btnSuccess, width: '100%' }}>+ New Cinematic</button>}



              {(showCreateCine || cinematics.length === 0) && <div style={{ ...S.card, padding: 12 }}>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>

                  <span style={{ fontSize: '0.78rem', fontWeight: 600, color: S.title?.color || '#c9a227' }}>{editCineId ? 'Edit Cinematic' : 'New Cinematic'}</span>

                  <button onClick={() => setShowCreateCine(false)} style={{ ...S.btn, ...S.btnXs }}>✕</button>

                </div>



                {/* Send To */}

                <div style={{ marginBottom: 8 }}>

                  <label style={{ fontSize: '0.6rem', color: '#888' }}>To:</label>

                  <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>

                    <button onClick={() => setCineSendTo('all')} style={{ ...S.btn, ...S.btnXs, background: cineSendTo === 'all' ? 'rgba(39,201,90,0.2)' : 'transparent', borderColor: cineSendTo === 'all' ? '#27c95a50' : undefined, color: cineSendTo === 'all' ? '#27c95a' : '#888' }}>All (Public)</button>

                    <button onClick={() => setCineSendTo('select')} style={{ ...S.btn, ...S.btnXs, background: cineSendTo === 'select' ? 'rgba(74,144,217,0.2)' : 'transparent', borderColor: cineSendTo === 'select' ? '#4a90d950' : undefined, color: cineSendTo === 'select' ? '#4a90d9' : '#888' }}>Select Players</button>

                  </div>

                  {cineSendTo === 'select' && <div style={{ display: 'flex', gap: 3, marginTop: 4, flexWrap: 'wrap' }}>

                    {Object.values(chars || {}).map(c => <button key={c.id} onClick={() => setCineRecipients(prev => prev.includes(c.id) ? prev.filter(x => x !== c.id) : [...prev, c.id])} style={{ padding: '2px 8px', fontSize: '0.58rem', borderRadius: 4, background: cineRecipients.includes(c.id) ? `${c.color || '#4a90d9'}20` : 'transparent', border: `1px solid ${cineRecipients.includes(c.id) ? (c.color || '#4a90d9') + '50' : '#3a3020'}`, color: cineRecipients.includes(c.id) ? (c.color || '#4a90d9') : '#888', cursor: 'pointer' }}>{c.name}</button>)}

                  </div>}

                </div>



                {/* Message Type */}

                <div style={{ marginBottom: 8 }}>

                  <label style={{ fontSize: '0.6rem', color: '#888' }}>Message Type:</label>

                  <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>

                    {MSG_TYPES.map(t => <button key={t.id} onClick={() => { setCineType(t.id); setCineName(t.id === 'character' ? (n.name || '') : ''); setCineTitle(t.id === 'character' ? (n.title || '') : ''); setCineQuote(''); setCineDesc(''); setCineBorderColor(''); setCineStyle(t.id === 'character' ? 'portrait' : t.id === 'item' ? 'portrait' : t.id === 'scenery' ? 'fullscreen' : t.id === 'quest' ? 'card' : 'portrait'); }} style={{ ...S.btn, ...S.btnXs, flex: 1, background: cineType === t.id ? t.color : 'transparent', fontWeight: cineType === t.id ? 600 : 400 }}>{t.icon} {t.label}</button>)}

                  </div>

                </div>



                {/* Character/Scenery/Item encounter form */}

                {['character','scenery','item','quest','lore'].includes(cineType) && <div style={{ ...S.card, padding: 10, marginBottom: 8 }}>

                  <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#9b59b6', marginBottom: 6 }}>{cineType === 'character' ? '👤 Character Encounter' : cineType === 'scenery' ? '🏔 Scenery' : cineType === 'quest' ? '📜 Quest' : cineType === 'lore' ? '📖 Lore' : '💎 Item Reveal'}</div>

                  {/* Style */}

                  <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>

                    {(cineType === 'scenery' ? ['fullscreen', 'card', 'portrait', 'jumpscare'] : ['portrait', 'card', 'fullscreen', 'jumpscare']).map(s => <button key={s} onClick={() => setCineStyle(s)} style={{ ...S.btn, ...S.btnXs, flex: 1, background: cineStyle === s ? 'rgba(155,89,182,0.3)' : 'transparent', borderColor: cineStyle === s ? '#9b59b6' : undefined }}>{s === 'portrait' ? '🖼 Portrait' : s === 'card' ? '📇 Card' : s === 'fullscreen' ? '📺 Large' : '💀 Full'}</button>)}

                  </div>

                  {/* Border Color - for portrait/card styles */}

                  {['character','item','quest','lore'].includes(cineType) && (cineStyle === 'portrait' || cineStyle === 'card') && (

                    <div style={{ marginTop: 6, marginBottom: 4 }}>

                      <label style={{ fontSize: '0.55rem', color: '#888' }}>Border</label>

                      <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', marginTop: 2 }}>

                        {[{ id: '', label: 'Default', color: null }, { id: 'gold', label: 'Gold', color: '#c9a227' }, { id: 'silver', label: 'Silver', color: '#a8b4c0' }, { id: 'crimson', label: 'Crimson', color: '#c92727' }, { id: 'emerald', label: 'Emerald', color: '#27ae60' }, { id: 'royal', label: 'Royal', color: '#6b4fa6' }, { id: 'frost', label: 'Frost', color: '#70a1ff' }, { id: 'shadow', label: 'Shadow', color: '#4a4a5a' }, { id: 'amber', label: 'Amber', color: '#e67e22' }].map(p => (

                          <button key={p.id} onClick={() => setCineBorderColor(p.color || '')} title={p.label} style={{ width: 22, height: 22, borderRadius: 4, cursor: 'pointer', background: p.color ? `${p.color}30` : 'rgba(255,255,255,0.05)', border: (cineBorderColor || '') === (p.color || '') ? `2px solid ${p.color || '#888'}` : '2px solid transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>

                            <div style={{ width: 10, height: 10, borderRadius: 2, background: p.color || '#666', border: p.color ? `1.5px solid ${p.color}` : '1.5px solid #555' }} />

                          </button>

                        ))}

                      </div>

                    </div>

                  )}

                  {/* Scenery mood */}

                  {cineType === 'scenery' && <div style={{ marginBottom: 8 }}>

                    <label style={{ fontSize: '0.55rem', color: '#888' }}>Scene Mood</label>

                    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginTop: 2 }}>

                      {SCENERY_MOODS.map(m => <button key={m.id} onClick={() => setCineMood(m.id)} style={{ padding: '2px 8px', fontSize: '0.52rem', borderRadius: 4, background: cineMood === m.id ? `${m.border}20` : 'transparent', border: `1px solid ${cineMood === m.id ? m.border : '#3a302030'}`, color: cineMood === m.id ? m.border : '#888', cursor: 'pointer' }}>{m.label}</button>)}

                    </div>

                    {SCENERY_MOODS.find(m => m.id === cineMood)?.desc && <div style={{ fontSize: '0.55rem', color: '#888', fontStyle: 'italic', marginTop: 3 }}>{SCENERY_MOODS.find(m => m.id === cineMood).desc}</div>}

                  </div>}

                  {/* Image */}

                  <div style={{ marginBottom: 6 }}>

                    <label style={{ fontSize: '0.55rem', color: '#888' }}>{cineType === 'character' ? 'Character Image' : cineType === 'item' ? 'Item Image' : cineType === 'quest' ? 'Quest Image' : cineType === 'lore' ? 'Lore Image' : 'Scene Image'}</label>

                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>

                      <input value={cineImage} onChange={e => setCineImage(e.target.value)} placeholder="https://... or upload" style={{ ...S.input, flex: 1, fontSize: '0.65rem', marginBottom: 0 }} />

                      <button onClick={browseImage} style={{ ...S.btn, ...S.btnXs, flexShrink: 0 }}>📁</button>

                    </div>

                    {cineImage && <img src={cineImage} alt="" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: cineStyle === 'portrait' ? '50%' : 6, marginTop: 4, border: '2px solid #9b59b6' }} />}

                  </div>

                  {/* Name + Title */}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>

                    <div><label style={{ fontSize: '0.55rem', color: '#888' }}>{cineType === 'character' ? 'Name' : cineType === 'item' ? 'Item Name' : cineType === 'scenery' ? 'Location Name' : cineType === 'quest' ? 'Quest Name' : cineType === 'lore' ? 'Title' : 'Name'}</label><input value={cineName} onChange={e => setCineName(e.target.value)} placeholder={cineType === 'character' ? 'Mysterious Stranger' : cineType === 'item' ? 'Amulet of the Lost King' : cineType === 'scenery' ? 'The Whispering Woods' : cineType === 'quest' ? 'The Lost Heir' : cineType === 'lore' ? 'The Fall of the Silver Kingdom' : ''} style={{ ...S.input, width: '100%', marginBottom: 0 }} /></div>

                    <div><label style={{ fontSize: '0.55rem', color: '#888' }}>{cineType === 'character' ? 'Title/Role (optional)' : cineType === 'item' ? 'Type/Rarity' : cineType === 'scenery' ? 'Region' : cineType === 'quest' ? 'Quest Type' : cineType === 'lore' ? 'Source' : 'Subtitle'}</label><input value={cineTitle} onChange={e => setCineTitle(e.target.value)} placeholder={cineType === 'character' ? 'Village Elder' : cineType === 'item' ? 'Legendary Artifact' : cineType === 'scenery' ? 'Northern Forest' : cineType === 'quest' ? 'Main Quest' : cineType === 'lore' ? 'Ancient Tome' : ''} style={{ ...S.input, width: '100%', marginBottom: 0 }} /></div>

                  </div>

                  {/* Quote - only for character type */}

                  {cineType === 'character' && <div style={{ marginBottom: 6 }}><label style={{ fontSize: '0.55rem', color: '#888' }}>What they say</label><textarea value={cineQuote} onChange={e => setCineQuote(e.target.value)} rows={2} placeholder='"Greetings, traveler..."' style={{ ...S.input, width: '100%', resize: 'vertical', fontStyle: 'italic', marginBottom: 0 }} /></div>}

                  <div><label style={{ fontSize: '0.55rem', color: '#888' }}>{cineType === 'character' ? 'Description (optional)' : cineType === 'item' ? 'Item Description' : cineType === 'scenery' ? 'Scene Description' : cineType === 'quest' ? 'Objective' : cineType === 'lore' ? 'Lore Text' : 'Description'}</label><textarea value={cineDesc} onChange={e => setCineDesc(e.target.value)} rows={2} placeholder={cineType === 'character' ? 'A cloaked figure emerges from the shadows...' : cineType === 'item' ? 'A golden amulet inlaid with a deep crimson ruby...' : cineType === 'scenery' ? 'Before you stretches an ancient forest...' : cineType === 'quest' ? 'Find the lost heir before the dark forces claim them...' : cineType === 'lore' ? 'In the age before the Sundering...' : ''} style={{ ...S.input, width: '100%', resize: 'vertical', marginBottom: 0, fontStyle: cineType === 'lore' ? 'italic' : 'normal' }} /></div>

                  {/* Theme for encounter styling */}

                  <div style={{ marginTop: 8 }}>

                    <label style={{ fontSize: '0.55rem', color: '#888' }}>Color Theme</label>

                    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginTop: 2 }}>

                      {Object.entries(SEQUENCE_THEMES).slice(0, 8).map(([key, t]) => <button key={key} onClick={() => setCineTheme(key)} style={{ padding: '2px 6px', fontSize: '0.5rem', borderRadius: 4, background: cineTheme === key ? 'rgba(201,162,39,0.3)' : 'transparent', border: `1px solid ${cineTheme === key ? '#c9a227' : '#3a302030'}`, color: cineTheme === key ? '#c9a227' : '#666', cursor: 'pointer' }}>{(t.name || key).replace(/^[^\w\s]+\s*/, '')}</button>)}

                    </div>

                  </div>

                </div>}



                {/* Dramatic sequence form */}

                {cineType === 'dramatic' && <div style={{ ...S.card, padding: 10, marginBottom: 8 }}>

                  {/* Theme */}

                  <div style={{ marginBottom: 8 }}>

                    <label style={{ fontSize: '0.6rem', color: '#888' }}>Theme:</label>

                    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginTop: 2 }}>

                      {Object.entries(SEQUENCE_THEMES).map(([key, t]) => <button key={key} onClick={() => setCineTheme(key)} style={{ padding: '3px 8px', fontSize: '0.55rem', borderRadius: 5, background: cineTheme === key ? 'rgba(201,162,39,0.3)' : 'transparent', border: `1px solid ${cineTheme === key ? '#c9a227' : '#3a3020'}`, color: cineTheme === key ? '#c9a227' : '#888', cursor: 'pointer' }}>{(t.name || key).replace(/^[^\w\s]+\s*/, '')}</button>)}

                    </div>

                    {SEQUENCE_THEMES[cineTheme]?.description && <div style={{ fontSize: '0.55rem', color: '#888', fontStyle: 'italic', marginTop: 2 }}>{SEQUENCE_THEMES[cineTheme].description}</div>}

                  </div>

                  {/* Sequence builder */}

                  <div style={{ marginBottom: 8 }}>

                    <label style={{ fontSize: '0.6rem', color: '#888' }}>Build your sequence:</label>

                    {cineSequence.map((slide, i) => <div key={i} style={{ display: 'flex', gap: 4, marginTop: 4, alignItems: 'flex-start' }}>

                      <span style={{ fontSize: '0.55rem', color: '#c9a227', marginTop: 6, width: 16 }}>{i + 1}.</span>

                      <textarea value={slide.text} onChange={e => { const s = [...cineSequence]; s[i] = { ...s[i], text: e.target.value }; setCineSequence(s); }} rows={1} style={{ ...S.input, flex: 1, resize: 'vertical', fontSize: '0.72rem', marginBottom: 0 }} placeholder="The first line..." />

                      {cineSequence.length > 1 && <button onClick={() => setCineSequence(cineSequence.filter((_, j) => j !== i))} style={{ ...S.btn, ...S.btnXs, ...S.btnDanger, marginTop: 2 }}>✕</button>}

                    </div>)}

                    <button onClick={() => setCineSequence([...cineSequence, { text: '', delay: 0 }])} style={{ ...S.btn, ...S.btnXs, width: '100%', marginTop: 4, opacity: 0.7 }}>+ Add Line</button>

                  </div>

                  {/* Line Transition */}

                  <div style={{ marginBottom: 6 }}>

                    <label style={{ fontSize: '0.6rem', color: '#888' }}>Line Transition:</label>

                    <div style={{ display: 'flex', gap: 3, marginTop: 2 }}>

                      {['stack', 'stackUp', 'replace', 'center', 'scroll'].map(t => <button key={t} onClick={() => setCineTransition(t)} style={{ ...S.btn, ...S.btnXs, flex: 1, fontSize: '0.55rem', background: cineTransition === t ? 'rgba(201,162,39,0.2)' : 'transparent', color: cineTransition === t ? '#c9a227' : '#888' }}>{t === 'stackUp' ? 'Stack Up' : t.charAt(0).toUpperCase() + t.slice(1)}</button>)}

                    </div>

                  </div>

                  {/* Background Image */}

                  <div>

                    <label style={{ fontSize: '0.55rem', color: '#888' }}>Background Image</label>

                    <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 2 }}>

                      <button onClick={browseImage} style={{ ...S.btn, ...S.btnXs, flexShrink: 0 }}>📁 Browse</button>

                      {cineImage && <img src={cineImage} alt="" style={{ width: 30, height: 30, objectFit: 'cover', borderRadius: 4 }} />}

                      {cineImage && <button onClick={() => setCineImage('')} style={{ ...S.btn, ...S.btnXs, ...S.btnDanger, fontSize: '0.5rem' }}>✕</button>}

                      <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginLeft: 'auto' }}>

                        <input type="range" min="0" max="1" step="0.05" value={cineBgOpacity} onChange={e => setCineBgOpacity(parseFloat(e.target.value))} style={{ width: 50 }} />

                        <span style={{ fontSize: '0.5rem', color: '#888' }}>{Math.round(cineBgOpacity * 100)}%</span>

                      </div>

                    </div>

                  </div>

                </div>}



                {/* Customization (collapsible) */}

                <button onClick={() => setShowCineCustom(!showCineCustom)} style={{ ...S.btn, ...S.btnXs, width: '100%', marginBottom: showCineCustom ? 6 : 0 }}>{showCineCustom ? '▴ Hide' : '▾ Show'} Customization</button>

                {showCineCustom && <div style={{ ...S.card, padding: 10, marginBottom: 8 }}>

                  {/* Font */}

                  <div style={{ marginBottom: 6 }}>

                    <label style={{ fontSize: '0.55rem', color: '#888' }}>Font</label>

                    <select value={cineFont} onChange={e => setCineFont(e.target.value)} style={{ ...S.select, width: '100%', fontSize: '0.65rem' }}>

                      {Object.entries(FONT_OPTIONS).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}

                    </select>

                  </div>

                  {/* Text Effects */}

                  <div>

                    <label style={{ fontSize: '0.55rem', color: '#888' }}>Text Effects (select multiple):</label>

                    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginTop: 2 }}>

                      {Object.entries(TEXT_EFFECTS).map(([k, v]) => <button key={k} onClick={() => setCineTextEffects(prev => k === 'none' ? [] : prev.includes(k) ? prev.filter(x => x !== k) : [...prev.filter(x => x !== 'none'), k])} style={{ padding: '2px 8px', fontSize: '0.55rem', borderRadius: 4, background: (k === 'none' ? cineTextEffects.length === 0 : cineTextEffects.includes(k)) ? 'rgba(201,162,39,0.2)' : 'transparent', border: `1px solid ${(k === 'none' ? cineTextEffects.length === 0 : cineTextEffects.includes(k)) ? '#c9a22750' : '#3a3020'}`, color: (k === 'none' ? cineTextEffects.length === 0 : cineTextEffects.includes(k)) ? '#c9a227' : '#888', cursor: 'pointer' }}>{v.name}</button>)}

                    </div>

                  </div>

                </div>}



                {/* Actions */}

                <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>

                  <button onClick={previewCine} style={{ flex: 1, padding: '6px 10px', fontSize: '0.7rem', background: 'rgba(155,89,182,0.12)', border: '1px solid rgba(155,89,182,0.35)', borderRadius: 6, color: '#c4a8ff', cursor: 'pointer' }}>▶ Preview</button>

                  <button onClick={saveCineToNpc} style={{ flex: 1, padding: '6px 10px', fontSize: '0.7rem', background: 'rgba(201,162,39,0.12)', border: '1px solid rgba(201,162,39,0.35)', borderRadius: 6, color: '#c9a227', cursor: 'pointer' }}>{editCineId ? '💾 Update' : '💾 Save'}</button>

                  <button onClick={() => { const data = buildCineData(); sendCine(data); }} disabled={cineType === 'dramatic' ? !cineSequence.some(s => s.text?.trim()) : (!cineName.trim() && !cineImage)} style={{ flex: 1, padding: '6px 10px', fontSize: '0.7rem', fontWeight: 600, background: 'rgba(201,162,39,0.2)', border: '1px solid rgba(201,162,39,0.5)', borderRadius: 6, color: '#ffd700', cursor: 'pointer' }}>🎬 Send</button>

                </div>

              </div>}

            </div>;

          })()}



          {detailTab === 'combat' && n._type === 'npc' && <>

            {n.statBlock ? <div style={{ ...S.card, padding: 10, marginBottom: 10 }}>

              <div style={{ fontSize: '0.72rem', fontWeight: 600, color: S.title?.color || '#c9a227', marginBottom: 6 }}>📊 Stat Block</div>

              <div style={{ display: 'flex', gap: 10, marginBottom: 6, fontSize: '0.78rem', flexWrap: 'wrap' }}>

                <span><b style={{ color: '#c92727' }}>HP:</b> {n.statBlock.hp}/{n.statBlock.maxHp}</span>

                <span><b style={{ color: '#4a90d9' }}>AC:</b> {n.statBlock.ac}</span>

                <span><b style={{ color: '#d97429' }}>CR:</b> {n.statBlock.cr}</span>

                {n.statBlock.speed && <span><b style={{ color: '#888' }}>Speed:</b> {n.statBlock.speed}</span>}

                {n.statBlock.senses && <span><b style={{ color: '#888' }}>Senses:</b> {n.statBlock.senses}</span>}

              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 4, textAlign: 'center' }}>

                {['str','dex','con','int','wis','cha'].map(ab => <div key={ab} style={{ ...S.card, padding: 4 }}>

                  <div style={{ fontSize: '0.55rem', color: '#888', textTransform: 'uppercase' }}>{ab}</div>

                  <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>{n.statBlock[ab]}</div>

                  <div style={{ fontSize: '0.6rem', color: S.title?.color || '#c9a227' }}>{abilityMod(n.statBlock[ab])}</div>

                </div>)}

              </div>

            </div> : <div style={{ textAlign: 'center', padding: 20, color: '#666', fontSize: '0.78rem' }}>No stat block configured. Edit this NPC to add one.</div>}

            

            {(n.abilities || []).length > 0 && <div style={{ ...S.card, padding: 10, marginBottom: 10 }}>

              <div style={{ fontSize: '0.72rem', fontWeight: 600, color: S.title?.color || '#c9a227', marginBottom: 6 }}>⚡ Abilities</div>

              {n.abilities.map((ab, i) => <div key={i} style={{ marginBottom: 6, padding: '6px 8px', background: 'rgba(255,255,255,0.02)', borderRadius: 4, borderLeft: `2px solid #d9742940` }}>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>

                  <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#d97429' }}>{ab.name}</span>

                  {ab.recharge && <span style={{ fontSize: '0.55rem', padding: '1px 5px', borderRadius: 4, background: 'rgba(217,116,41,0.1)', color: '#d97429' }}>Recharge {ab.recharge}</span>}

                  {ab.damage && <span style={{ fontSize: '0.55rem', padding: '1px 5px', borderRadius: 4, background: 'rgba(201,39,39,0.1)', color: '#c92727' }}>{ab.damage}</span>}

                </div>

                <div style={{ fontSize: '0.72rem', color: '#b0a48a', marginTop: 2 }}>{ab.description}</div>

              </div>)}

            </div>}

            

            {n.disposition === 'hostile' && <div style={{ ...S.card, padding: 10, marginBottom: 10, borderLeft: `3px solid #c9272740` }}>

              <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#c92727', marginBottom: 6 }}>⚔️ Encounter Info</div>

              <div style={{ display: 'flex', gap: 16, fontSize: '0.78rem' }}>

                <span><b style={{ color: '#c9a227' }}>XP Reward:</b> {n.xpReward || 0}</span>

                {n.lootTableId && <span><b style={{ color: '#c9a227' }}>Loot Table:</b> {n.lootTableId}</span>}

              </div>

            </div>}



            {!n.statBlock && (n.abilities || []).length === 0 && n.disposition !== 'hostile' && <div style={{ textAlign: 'center', padding: 20, color: '#666', fontSize: '0.78rem' }}>No combat data. Edit this NPC to add stat block, abilities, or encounter info.</div>}

          </>}

        </div>

        {/* Cinematic Preview Overlay */}

        {cinematicPreview && <DramaticOverlay 

          isPreview 

          previewData={{

            id: 'preview',

            content: cinematicPreview.title || cinematicPreview.encounterName || 'Preview',

            urgency: 'urgent',

            ...(cinematicPreview.displayStyle === 'encounter' || ['character','scenery','item','quest','lore'].includes(cinematicPreview.messageType) ? {

              displayStyle: 'encounter',

              encounterType: cinematicPreview.messageType || cinematicPreview.encounterType || 'character',

              encounter: cinematicPreview.encounter || {

                name: cinematicPreview.encounterName,

                title: cinematicPreview.encounterTitle,

                image: cinematicPreview.imagePath,

                style: cinematicPreview.style || 'portrait',

                quote: cinematicPreview.encounterQuote,

                description: cinematicPreview.encounterDesc,

                customization: { primaryColor: cinematicPreview.moodBorder || SEQUENCE_THEMES[cinematicPreview.theme]?.textColor || '#c9a227', borderColor: cinematicPreview.borderColor || null },

              },

            } : {

              displayStyle: 'dramatic',

              sequence: (cinematicPreview.sequence || []).filter(s => s.text?.trim()),

              theme: cinematicPreview.theme || 'foreboding',

              baseTransition: cinematicPreview.transition || 'stack',

              revealModifiers: [],

              textEffects: cinematicPreview.textEffects || [],

            }),

            customStyle: {

              ...(cinematicPreview.imagePath && !['character','scenery','item','quest','lore'].includes(cinematicPreview.messageType) ? { bgImage: cinematicPreview.imagePath, bgImageOpacity: cinematicPreview.bgOpacity ?? 0.3 } : {}),

              ...(cinematicPreview.font && cinematicPreview.font !== 'default' ? { font: cinematicPreview.font } : {}),

            },

          }}

          onClosePreview={() => setCinematicPreview(null)}

        />}

        {/* Link hover tooltip */}

        {hoveredLinkEntry && (

          <div style={{

            position: 'fixed', left: Math.min(linkTooltipPos.x, window.innerWidth - 260), top: linkTooltipPos.y,

            transform: 'translate(-50%, -100%)',

            background: 'rgba(20,18,14,0.97)', border: `1px solid ${(LIBRARY_TABS.find(t => t.id === (hoveredLinkEntry.category || 'npcs'))?.color || '#c9a227')}50`,

            borderRadius: '8px', padding: '10px 14px', maxWidth: '250px',

            boxShadow: '0 8px 24px rgba(0,0,0,0.6)', zIndex: 3000, pointerEvents: 'none',

          }}>

            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: LIBRARY_TABS.find(t => t.id === (hoveredLinkEntry.category || 'npcs'))?.color || '#c9a227', marginBottom: '3px' }}>

              {LIBRARY_TABS.find(t => t.id === (hoveredLinkEntry.category || 'npcs'))?.icon} {hoveredLinkEntry.name}

            </div>

            {hoveredLinkEntry.title && <div style={{ fontSize: '0.65rem', color: '#888', marginBottom: '4px', fontStyle: 'italic' }}>{hoveredLinkEntry.title}</div>}

            {(hoveredLinkEntry.description || hoveredLinkEntry.personality || hoveredLinkEntry.summary) && (

              <div style={{ fontSize: '0.68rem', color: '#b0a48a', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>

                {(hoveredLinkEntry.description || hoveredLinkEntry.personality || hoveredLinkEntry.summary || '').replace(/\{\{REVEAL\}\}|\{\{\/REVEAL\}\}/g, '').substring(0, 150)}

              </div>

            )}

          </div>

        )}

      </div>

      </div>

    );

  }



  // ====== LIST VIEW ======

  return (

    <div style={S.container}>

    <div style={{ maxWidth: 700, margin: '0 auto' }}>

      {/* Header */}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>

        <button style={{ ...S.btn, ...S.btnSm }} onClick={() => setView('dm')}>← Back</button>

        <h2 style={{ ...S.title, fontSize: '1.2rem', margin: 0 }}>📚 Library</h2>

        <div style={{ display: 'flex', gap: 4 }}>

          <button style={{ ...S.btn, ...S.btnXs, background: viewMode === 'grid' ? 'rgba(201,162,39,0.15)' : 'transparent' }} onClick={() => setViewMode('grid')} title="Grid">▦</button>

          <button style={{ ...S.btn, ...S.btnXs, background: viewMode === 'list' ? 'rgba(201,162,39,0.15)' : 'transparent' }} onClick={() => setViewMode('list')} title="List">☰</button>

          {activeTab === 'npcs' && <button style={{ ...S.btn, ...S.btnSm, ...S.btnSuccess }} onClick={() => setEditingNpc(createNPC({ name: 'New NPC' }))}>+ New NPC</button>}

        </div>

      </div>



      {/* Category Tabs */}

      <div style={{ display: 'flex', gap: 2, marginBottom: 12, overflowX: 'auto', paddingBottom: 4 }}>

        {LIBRARY_TABS.map(tab => {

          const count = tab.id === 'npcs' ? (Object.keys(npcLibrary || {}).length + (dmJournal || []).filter(e => e.category === 'npcs' && !Object.values(npcLibrary || {}).some(n => n.name?.toLowerCase() === e.name?.toLowerCase())).length) : (dmJournal || []).filter(e => e.category === tab.id).length;

          return <button key={tab.id} onClick={() => { setActiveTab(tab.id); setSelectedEntry(null); setSearch(''); }} style={{

            padding: '6px 12px', fontSize: '0.72rem', fontWeight: activeTab === tab.id ? 600 : 400,

            background: activeTab === tab.id ? `${tab.color}15` : 'transparent',

            border: `1px solid ${activeTab === tab.id ? tab.color + '50' : 'transparent'}`,

            borderBottom: activeTab === tab.id ? `2px solid ${tab.color}` : '2px solid transparent',

            borderRadius: '6px 6px 0 0', color: activeTab === tab.id ? tab.color : '#888',

            cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4,

          }}>

            <span>{tab.icon}</span> {tab.label}

            {count > 0 && <span style={{ fontSize: '0.55rem', padding: '0 4px', borderRadius: 8, background: `${tab.color}15`, color: tab.color }}>{count}</span>}

          </button>;

        })}

      </div>



      {/* Search */}

      <div style={{ marginBottom: 12 }}>

        <input value={search} onChange={e => setSearch(e.target.value)} placeholder={`Search ${tabInfo.label.toLowerCase()}...`} style={{ ...S.input, width: '100%', marginBottom: 0 }} />

      </div>



      {/* Entry Grid/List */}

      {filtered.length === 0 ? (

        <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>

          <div style={{ fontSize: '2rem', marginBottom: 8 }}>{tabInfo.icon}</div>

          <div style={{ fontSize: '0.85rem' }}>{allEntries.length === 0 ? `No ${tabInfo.label.toLowerCase()} yet.${activeTab === 'npcs' ? ' Create one or use #N@ in the DM Journal!' : ' Use the DM Journal to add entries.'}` : 'No matches.'}</div>

        </div>

      ) : (

        <div style={{ display: viewMode === 'grid' ? 'grid' : 'flex', gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(200px, 1fr))' : undefined, flexDirection: viewMode === 'list' ? 'column' : undefined, gap: 8 }}>

          {filtered.map(entry => {

            const disp = entry._type === 'npc' ? NPC_DISPOSITIONS.find(d => d.id === entry.disposition) : null;

            const stat = entry._type === 'npc' ? NPC_STATUSES.find(s => s.id === entry.status) : null;

            const borderColor = disp?.color || getCatColor();

            return (

              <div key={entry.id} onClick={() => { setSelectedEntry(entry); setDetailTab('profile'); }} style={{

                ...S.card, padding: viewMode === 'grid' ? 12 : '10px 14px', cursor: 'pointer',

                borderLeft: `3px solid ${borderColor}50`,

                display: viewMode === 'list' ? 'flex' : 'block',

                gap: viewMode === 'list' ? 12 : 0,

                alignItems: viewMode === 'list' ? 'center' : undefined,

              }}

              onMouseEnter={ev => { ev.currentTarget.style.borderLeftColor = borderColor; }}

              onMouseLeave={ev => { ev.currentTarget.style.borderLeftColor = borderColor + '50'; }}>

                <div style={{ flex: 1, minWidth: 0 }}>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>

                    <span style={{ fontSize: '0.85rem' }}>{entry._type !== 'npc' ? tabInfo.icon : ''}</span>

                    <strong style={{ color: '#e8d5b7', fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.name || 'Untitled'}</strong>

                    {entry.isStub && <span style={{ ...S.badge, fontSize: '0.5rem', background: 'rgba(201,162,39,0.2)', color: '#c9a227', borderStyle: 'dashed' }}>stub</span>}

                    {entry.disposition === 'hostile' && <span style={{ fontSize: '0.5rem', padding: '1px 5px', borderRadius: 4, background: 'rgba(201,39,39,0.15)', color: '#c92727' }}>Hostile</span>}

                    {stat && <span style={{ fontSize: '0.5rem', padding: '1px 5px', borderRadius: 4, background: `${stat.color}15`, color: stat.color, marginLeft: 'auto' }}>{stat.label}</span>}

                  </div>

                  {entry.title && <div style={{ fontSize: '0.68rem', color: '#888', marginBottom: 2 }}>{entry.title}</div>}

                  {entry.category === 'session' && entry.sessionDate && <div style={{ fontSize: '0.62rem', color: '#c9a227', marginBottom: 2 }}>📅 {entry.sessionDate}</div>}

                  {entry.currentLocation && <div style={{ fontSize: '0.62rem', color: '#666' }}>📍 {entry.currentLocation}</div>}

                  {entry.statBlock && <div style={{ fontSize: '0.6rem', color: '#888', marginTop: 2 }}>HP: {entry.statBlock.hp} · AC: {entry.statBlock.ac} · CR: {entry.statBlock.cr}</div>}

                  {entry.category === 'session' && entry.summary && <div style={{ fontSize: '0.65rem', color: '#777', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.summary.substring(0, 120)}</div>}

                  {entry.category === 'session' && (entry.npcsEncountered || []).length > 0 && <div style={{ fontSize: '0.55rem', color: '#4a90d9', marginTop: 2 }}>👤 {entry.npcsEncountered.join(', ')}</div>}

                  {entry.category !== 'session' && (entry._journalDesc || entry.description) && !entry.statBlock && <div style={{ fontSize: '0.65rem', color: '#777', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(entry._journalDesc || entry.description || '').replace(/\{\{REVEAL\}\}|\{\{\/REVEAL\}\}/g, '').substring(0, 100)}</div>}

                  {(entry.tags || []).length > 0 && <div style={{ display: 'flex', gap: 3, marginTop: 4, flexWrap: 'wrap' }}>{entry.tags.slice(0, 4).map(t => <span key={t} style={{ ...S.badge, ...S.badgeNeutral, fontSize: '0.5rem', padding: '0 4px' }}>{t}</span>)}</div>}

                  {entry._type === 'npc' && (entry.revealItems || []).some(ri => ri.visibility === 'revealable') && <div style={{ marginTop: 4 }}>

                    <button onClick={(ev) => { ev.stopPropagation(); revealCharacterCard(entry.id); }} style={{ padding: '2px 8px', fontSize: '0.55rem', fontWeight: 600, background: 'rgba(74,144,217,0.1)', border: '1px solid #4a90d940', borderRadius: 4, color: '#4a90d9', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3 }}>👁 Reveal ({(entry.revealItems || []).filter(ri => ri.visibility === 'revealable').length})</button>

                  </div>}

                </div>

              </div>

            );

          })}

        </div>

      )}

    </div>

    </div>

  );

};

  

// Session C (2026-04-29): cap audit event payloads at ~4 KB. Beta showed

// audit.appendEvent at 430 KB per call (76 MB across 177 events) — driven

// mostly by `revert.patch` carrying deepClone(before[k]) for inventory /

// equipment / buffSources / etc. Realistic event payloads should be ~500

// bytes ("DM healed Lika for 5 HP at session 3, 14:32"); the bloat comes

// from undo support stuffing whole pre-edit subtrees into every event.

//

// Strategy: progressive trim until under cap. Drop the largest non-summary

// fields first; preserve identity / category / actor / summary always so

// the audit log remains useful even when events were trimmed. Mark trimmed

// events with `_trimmed: true` so the log UI can show a hint.

//

// Why a soft trim instead of full structural redesign: the revert system

// is real — DMs use "undo my last action." Better to trim large reverts

// (rare actions like "DM bulk-edited 5 chars") than to drop reverts

// entirely. Small reverts (typical single-field changes) fit fine.

const AUDIT_EVENT_MAX_BYTES = 4096;



const capAuditEvent = (event) => {

  if (!event || typeof event !== 'object') return event;

  // Fast path: stringify once, return as-is if already under cap.

  let serialized;

  try { serialized = JSON.stringify(event); }

  catch { return event; /* circular or non-serializable — let pushAuditEvent handle */ }

  if (serialized.length <= AUDIT_EVENT_MAX_BYTES) return event;



  // Slow path: progressively trim, re-measuring after each step.

  // Clone shallowly so we don't mutate the caller's event.

  let trimmed = { ...event, _trimmed: true };



  // Step 1: trim the revert payload. revert.patch holds full pre-edit

  // subtrees — by far the biggest typical bloater. Keep revert metadata

  // (kind, charId, deleteKeys) so the audit log can still show "this had

  // a revert" but accept that the actual undo data is gone.

  if (trimmed.revert && trimmed.revert.patch) {

    trimmed.revert = {

      ...trimmed.revert,

      patch: undefined,

      _patchTrimmed: true,

    };

    delete trimmed.revert.patch;

    serialized = JSON.stringify(trimmed);

    if (serialized.length <= AUDIT_EVENT_MAX_BYTES) return trimmed;

  }



  // Step 2: drop full buffSources snapshot from details. The summary already

  // names the changed buff + its source; the full map adds nothing.

  if (trimmed.details && trimmed.details.buffSources) {

    trimmed.details = { ...trimmed.details };

    delete trimmed.details.buffSources;

    serialized = JSON.stringify(trimmed);

    if (serialized.length <= AUDIT_EVENT_MAX_BYTES) return trimmed;

  }



  // Step 3: truncate large delta arrays in details. Keep first 5 of each;

  // record the original count so the UI can show "+N more".

  const truncateDeltaList = (delta) => {

    if (!delta || typeof delta !== 'object') return delta;

    const out = { ...delta };

    for (const key of ['added', 'removed', 'changed']) {

      const arr = delta[key];

      if (Array.isArray(arr) && arr.length > 5) {

        out[key] = arr.slice(0, 5);

        out[`_${key}TruncatedFrom`] = arr.length;

      }

    }

    return out;

  };

  if (trimmed.details) {

    trimmed.details = { ...trimmed.details };

    if (trimmed.details.inventory) trimmed.details.inventory = truncateDeltaList(trimmed.details.inventory);

    if (trimmed.details.equipment) trimmed.details.equipment = truncateDeltaList(trimmed.details.equipment);

    if (trimmed.details.diseases) trimmed.details.diseases = truncateDeltaList(trimmed.details.diseases);

    if (trimmed.details.buffsDelta) {

      trimmed.details.buffsDelta = {

        buffs: truncateDeltaList(trimmed.details.buffsDelta.buffs),

        debuffs: truncateDeltaList(trimmed.details.buffsDelta.debuffs),

      };

    }

    serialized = JSON.stringify(trimmed);

    if (serialized.length <= AUDIT_EVENT_MAX_BYTES) return trimmed;

  }



  // Step 4: last-resort fallback. Keep only the identity-and-summary fields.

  // Detail is gone but the event is still useful for "what happened when."

  return {

    ts: event.ts,

    category: event.category,

    action: event.action,

    actor: event.actor,

    sessionId: event.sessionId,

    target: event.target,

    summary: event.summary,

    importance: event.importance,

    isDemo: event.isDemo,

    _trimmed: true,

    _trimmedReason: 'oversize',

    _originalBytes: serialized.length,

  };

};



// Session 7B: App accepts an optional `viewerContext` prop supplied by

// src/index.js's AuthGate when Convex Auth is on. When set, useStorage routes

// chars + systems subscriptions through projected queries so player clients

// never receive DM-only fields over the wire. When null/undefined (demo-auth,

// ?local=1, unauthenticated), behavior matches pre-7B exactly.

const isNonEmptyArrayValueEqual = (left, right) => (
  JSON.stringify(Array.isArray(left) ? left : []) === JSON.stringify(Array.isArray(right) ? right : [])
);

const isPlainObjectValueEqual = (left, right) => (
  JSON.stringify(left && typeof left === 'object' && !Array.isArray(left) ? left : {})
    === JSON.stringify(right && typeof right === 'object' && !Array.isArray(right) ? right : {})
);

const buildShopManagementPatchFromShop = (shop, sortOrder) => ({
  name: String(shop?.name || '').trim() || 'New Shop',
  keeperName: String(shop?.keeperName || '').trim() || 'Unknown Keeper',
  type: String(shop?.type || '').trim() || 'General',
  description: String(shop?.description || ''),
  mode: String(shop?.mode || 'coin'),
  themeId: shop?.themeId || null,
  tags: Array.isArray(shop?.tags) ? shop.tags : [],
  campaigns: Array.isArray(shop?.campaigns) ? shop.campaigns : [],
  sortOrder,
  isOpen: shop?.isOpen === true,
  starred: shop?.starred === true,
  visibleTo: Array.isArray(shop?.visibleTo) ? shop.visibleTo : [],
});

const buildListingManagementPatchFromLine = (line) => ({
  price: Number.isFinite(Number(line?.price)) ? Number(line.price) : 0,
  stock: line?.stock === null || line?.stock === undefined ? null : Number(line.stock),
  secrecy: String(line?.secrecy || 'visible'),
  tradeOnly: line?.tradeOnly === true,
  isActive: line?.isActive !== false,
});

const getRuntimeListingIdFromLine = (line) => line?.listingId || line?.id || null;

const getClientListingIdFromLine = (line) => (
  line?.sourceListingId
  || line?.legacyListingId
  || line?.listingId
  || line?.id
  || line?.itemId
);

const buildTemplateSnapshotForLine = (templates, line) => {
  const template = templates?.[line?.templateKey || line?.listingId] || templates?.[line?.itemId] || {};
  const definitionId = line?.definitionId || line?.itemId || template.definitionId || template.sourceDefinitionId || template.id;
  return {
    ...template,
    id: definitionId,
    definitionId,
    name: template.name || line?.name || line?.itemId,
    category: template.category || line?.category || 'misc',
  };
};

const hasShopManagementPatchChanged = (previousPatch, nextPatch) => (
  previousPatch.name !== nextPatch.name
  || previousPatch.keeperName !== nextPatch.keeperName
  || previousPatch.type !== nextPatch.type
  || previousPatch.description !== nextPatch.description
  || previousPatch.mode !== nextPatch.mode
  || previousPatch.themeId !== nextPatch.themeId
  || previousPatch.sortOrder !== nextPatch.sortOrder
  || previousPatch.isOpen !== nextPatch.isOpen
  || previousPatch.starred !== nextPatch.starred
  || !isNonEmptyArrayValueEqual(previousPatch.tags, nextPatch.tags)
  || !isNonEmptyArrayValueEqual(previousPatch.campaigns, nextPatch.campaigns)
  || !isNonEmptyArrayValueEqual(previousPatch.visibleTo, nextPatch.visibleTo)
);

const hasListingManagementPatchChanged = (previousPatch, nextPatch) => (
  previousPatch.price !== nextPatch.price
  || previousPatch.stock !== nextPatch.stock
  || previousPatch.secrecy !== nextPatch.secrecy
  || previousPatch.tradeOnly !== nextPatch.tradeOnly
  || previousPatch.isActive !== nextPatch.isActive
);

const addExpectedVersionIfPresent = (args, version) => (
  Number.isFinite(Number(version)) ? { ...args, expectedVersion: Number(version) } : args
);

const callRequiredShopManagementCommand = async (label, command, args) => {
  if (typeof command !== 'function') throw new Error(`${label} authority unavailable`);
  const result = await command(args);
  if (result === null || result === undefined) throw new Error(`${label} authority unavailable`);
  return result;
};

const syncServerShopManagementChanges = async ({
  previousData,
  nextData,
  createDmShop,
  deleteDmShop,
  updateDmShop,
  createDmShopListing,
  deleteDmShopListing,
  updateDmShopListing,
  updateDmShopRuntimeState,
}) => {
  const previousShops = Array.isArray(previousData?.shops) ? previousData.shops : [];
  const nextShops = Array.isArray(nextData?.shops) ? nextData.shops : [];
  const previousTemplates = previousData?.itemTemplates || {};
  const previousById = new Map(previousShops.map((shop) => [shop.id, shop]));
  const nextById = new Map(nextShops.map((shop) => [shop.id, shop]));
  const nextTemplates = nextData?.itemTemplates || {};
  let activeShopChangeHandledByDelete = false;

  for (const previousShop of previousShops) {
    if (!nextById.has(previousShop.id)) {
      const deletingActiveShop = previousShop.id === previousData?.activeShopId;
      const replacementActiveShopId = deletingActiveShop && nextData?.activeShopId && nextData.activeShopId !== previousShop.id
        ? nextData.activeShopId
        : undefined;
      await callRequiredShopManagementCommand(
        'delete shop',
        deleteDmShop,
        addExpectedVersionIfPresent({
          shopId: previousShop.id,
          replacementActiveShopId,
        }, previousShop.version),
      );
      if (deletingActiveShop) activeShopChangeHandledByDelete = true;
    }
  }

  for (const nextShop of nextShops) {
    if (!nextShop?.id || previousById.has(nextShop.id)) continue;
    await callRequiredShopManagementCommand('create shop', createDmShop, {
      clientShopId: nextShop.sourceShopId || nextShop.legacyShopId || nextShop.id,
      activate: nextData?.activeShopId === nextShop.id,
      patch: buildShopManagementPatchFromShop(nextShop, nextShops.indexOf(nextShop)),
    });
  }

  for (const nextShop of nextShops) {
    const previousShop = previousById.get(nextShop.id);
    if (!previousShop) continue;
    const nextShopPatch = buildShopManagementPatchFromShop(nextShop, nextShops.indexOf(nextShop));
    const previousShopPatch = buildShopManagementPatchFromShop(previousShop, previousShops.indexOf(previousShop));
    if (hasShopManagementPatchChanged(previousShopPatch, nextShopPatch)) {
      await callRequiredShopManagementCommand(
        'update shop',
        updateDmShop,
        addExpectedVersionIfPresent({
          shopId: nextShop.id,
          patch: nextShopPatch,
        }, previousShop.version),
      );
    }

    const previousLines = Array.isArray(previousShop.inventory) ? previousShop.inventory : [];
    const nextLines = Array.isArray(nextShop.inventory) ? nextShop.inventory : [];
    const previousLinesByRuntimeId = new Map(
      previousLines
        .map((line) => [getRuntimeListingIdFromLine(line), line])
        .filter(([listingId]) => Boolean(listingId)),
    );
    const nextLinesByRuntimeId = new Map(
      nextLines
        .map((line) => [getRuntimeListingIdFromLine(line), line])
        .filter(([listingId]) => Boolean(listingId)),
    );

    for (const previousLine of previousLines) {
      const listingId = getRuntimeListingIdFromLine(previousLine);
      if (!listingId || nextLinesByRuntimeId.has(listingId)) continue;
      await callRequiredShopManagementCommand(
        'delete shop listing',
        deleteDmShopListing,
        addExpectedVersionIfPresent({ listingId }, previousLine.version),
      );
    }

    for (const nextLine of nextLines) {
      const listingId = getRuntimeListingIdFromLine(nextLine);
      const previousLine = listingId ? previousLinesByRuntimeId.get(listingId) : null;
      if (!previousLine) {
        const clientListingId = getClientListingIdFromLine(nextLine);
        const definitionId = nextLine?.definitionId || nextLine?.itemId;
        if (!clientListingId || !definitionId) continue;
        await callRequiredShopManagementCommand('create shop listing', createDmShopListing, {
          shopId: nextShop.id,
          clientListingId,
          definitionId,
          templateSnapshot: buildTemplateSnapshotForLine(nextTemplates, nextLine),
          patch: buildListingManagementPatchFromLine(nextLine),
        });
        continue;
      }

      const previousListingPatch = buildListingManagementPatchFromLine(previousLine);
      const nextListingPatch = buildListingManagementPatchFromLine(nextLine);
      const previousTemplateSnapshot = buildTemplateSnapshotForLine(previousTemplates, previousLine);
      const nextTemplateSnapshot = buildTemplateSnapshotForLine(nextTemplates, nextLine);
      const templateSnapshotChanged = !isPlainObjectValueEqual(previousTemplateSnapshot, nextTemplateSnapshot);
      if (hasListingManagementPatchChanged(previousListingPatch, nextListingPatch) || templateSnapshotChanged) {
        const listingArgs = {
          listingId,
          patch: nextListingPatch,
        };
        if (templateSnapshotChanged) {
          listingArgs.templateSnapshot = nextTemplateSnapshot;
        }
        await callRequiredShopManagementCommand(
          'update shop listing',
          updateDmShopListing,
          addExpectedVersionIfPresent(listingArgs, previousLine.version),
        );
      }
    }
  }

  const runtimePatch = {};
  if (
    nextData?.activeShopId !== previousData?.activeShopId
    && (!nextData?.activeShopId || previousById.has(nextData.activeShopId))
    && !activeShopChangeHandledByDelete
  ) {
    runtimePatch.activeShopId = nextData.activeShopId || null;
  }
  if (nextData?.activeCampaign !== previousData?.activeCampaign) {
    runtimePatch.activeCampaign = nextData?.activeCampaign || 'all';
  }
  if (!isNonEmptyArrayValueEqual(previousData?.campaigns, nextData?.campaigns)) {
    runtimePatch.campaigns = Array.isArray(nextData?.campaigns) ? nextData.campaigns : [];
  }
  if (!isPlainObjectValueEqual(previousData?.customRarities, nextData?.customRarities)) {
    runtimePatch.customRarities = nextData?.customRarities || {};
  }
  if (!isNonEmptyArrayValueEqual(previousData?.customCategories, nextData?.customCategories)) {
    runtimePatch.customCategories = Array.isArray(nextData?.customCategories) ? nextData.customCategories : [];
  }
  if (Object.keys(runtimePatch).length > 0) {
    const runtimeArgs = {
      patch: runtimePatch,
    };
    if (Number.isFinite(Number(previousData?._stateUpdatedAt))) {
      runtimeArgs.expectedUpdatedAt = Number(previousData._stateUpdatedAt);
    }
    await callRequiredShopManagementCommand('update shop runtime state', updateDmShopRuntimeState, runtimeArgs);
  }
};

export default function App({

  viewerContext = null,

  viewerAuthToken = null,

  authMovementAuthorityEnabled = false,

  claimViewedCharAsOwner = null,

  useServerDerivedViewerQueries = false,

  forceLocalStorage = false,

  characterImportUploadClient = null,

  createStandaloneCharacterFromImport = null,

  onCharacterImportCreateApplied = null,

  initialLaunch = null,

  sandboxStorageSeed = null,

  onReturnToCampaignLanding = null,

  campaignRuntimeSettings = null,

} = {}) {

  const initialLaunchRole = initialLaunch?.role;
  const initialLaunchCharId = initialLaunchRole === 'player' ? initialLaunch?.charId || null : null;
  // Phase II polish (2026-05-17) — caller may request a specific starting
  // view via launchTarget.initialView (e.g. 'journal' from the dashboard's
  // Party Journal button, 'dev-dashboard' from the Dev access affordance).
  const initialLaunchView = typeof initialLaunch?.initialView === 'string' && initialLaunch.initialView
    ? initialLaunch.initialView
    : null;
  const initialLaunchRequestsCharacterImport = initialLaunchView === 'character-import';
  const initialRouteView = initialLaunchRequestsCharacterImport ? 'select' : initialLaunchView;
  const sandboxStorageContext = useMemo(() => {
    const isSandboxLaunch = initialLaunch
      && (
        initialLaunch.runtimeScope === RUNTIME_SCOPE_CAMPAIGN_SANDBOX
        || initialLaunch.storageMode === STORAGE_MODE_LOCAL_SANDBOX
      );
    if (!isSandboxLaunch) return {};
    return {
      runtimeScope: RUNTIME_SCOPE_CAMPAIGN_SANDBOX,
      storageMode: STORAGE_MODE_LOCAL_SANDBOX,
      sandboxId: initialLaunch.sandboxId || 'default',
      sourceCampaignId: initialLaunch.sourceCampaignId || null,
      sourceMode: initialLaunch.sourceMode || null,
    };
  }, [initialLaunch]);
  const isCampaignSandboxSession = sandboxStorageContext.runtimeScope === RUNTIME_SCOPE_CAMPAIGN_SANDBOX;
  const sandboxStoredInitialState = useMemo(() => (
    isCampaignSandboxSession
      ? loadCampaignSandboxState(sandboxStorageContext, null)
      : null
  ), [isCampaignSandboxSession, sandboxStorageContext]);
  const sandboxStoredGlobalSettings = sandboxStoredInitialState?.globalSettings
    && typeof sandboxStoredInitialState.globalSettings === 'object'
    ? sandboxStoredInitialState.globalSettings
    : null;
  const sandboxInitialRuntimeSettings = sandboxStoredGlobalSettings
    ? {
        enabledFeatures: sandboxStoredGlobalSettings.enabledFeatures || null,
        homebrew: sandboxStoredGlobalSettings.homebrew || null,
      }
    : null;
  const [sandboxRuntimeSettings, setSandboxRuntimeSettings] = useState(() => sandboxInitialRuntimeSettings);
  const initialCampaignEnabledFeatures = initialLaunch
    && Object.prototype.hasOwnProperty.call(initialLaunch, 'enabledFeatures')
    ? initialLaunch.enabledFeatures || null
    : null;
  const initialCampaignHomebrew = initialLaunch
    && Object.prototype.hasOwnProperty.call(initialLaunch, 'homebrew')
    ? initialLaunch.homebrew || null
    : null;
  const activeLaunchCampaignId = initialLaunch?.campaignId || viewerContext?.activeCampaignId || null;
  const hasMatchingCampaignRuntimeSettings = campaignRuntimeSettings
    && (!campaignRuntimeSettings.campaignId || campaignRuntimeSettings.campaignId === activeLaunchCampaignId);
  const runtimeCampaignEnabledFeatures = hasMatchingCampaignRuntimeSettings
    && Object.prototype.hasOwnProperty.call(campaignRuntimeSettings, 'enabledFeatures')
    ? campaignRuntimeSettings.enabledFeatures || null
    : (isCampaignSandboxSession && sandboxRuntimeSettings
      ? sandboxRuntimeSettings.enabledFeatures || null
      : initialCampaignEnabledFeatures);
  const runtimeCampaignHomebrew = hasMatchingCampaignRuntimeSettings
    && Object.prototype.hasOwnProperty.call(campaignRuntimeSettings, 'homebrew')
    ? campaignRuntimeSettings.homebrew || null
    : (isCampaignSandboxSession && sandboxRuntimeSettings
      ? sandboxRuntimeSettings.homebrew || null
      : initialCampaignHomebrew);

  const [view, setView] = useState(() => {
    if (initialRouteView) return initialRouteView;
    if (initialLaunchRole === 'dm') return 'dm';
    if (initialLaunchRole === 'player' && initialLaunchCharId) return 'home';
    return 'select';
  });

  const [timelineInitRange, setTimelineInitRange] = useState(null);

  const [journalInitEntry, setJournalInitEntry] = useState(null); // Auto-select entry when opening journal

  const [libraryInitEntry, setLibraryInitEntry] = useState(null); // Auto-select entry when opening library

  const [lastLibraryTab, setLastLibraryTab] = useState('session'); // Remember last DM library tab

  const [charId, setCharId] = useState(initialLaunchCharId);

  const [isDM, setIsDM] = useState(initialLaunchRole === 'dm');

  const [showMessagePanel, setShowMessagePanel] = useState(false);

  const [showQuickAddJournal, setShowQuickAddJournal] = useState(false);

  const [showDmJournal, setShowDmJournal] = useState(false);

  // Feature-flag map (system-wide kill switches set on the dev dashboard)
  // combined with the active campaign's enabledFeatures contract.
  const featureFlagsMap = useFeatureFlags({ forceLocal: forceLocalStorage || isCampaignSandboxSession });

  const flagEnabled = (key) => {

    if (featureFlagsMap === undefined) return true;

    const f = featureFlagsMap[key];

    return f ? f.enabled : true;

  };

  const globalCombatEnabled = flagEnabled('feature.combat');

  const globalShopsEnabled = flagEnabled('feature.shops');

  const globalCraftingEnabled = flagEnabled('feature.crafting');

  const globalProfessionsFeatureEnabled = globalCraftingEnabled && flagEnabled('feature.professions');

  const effectiveCampaignFeatures = useMemo(

    () => getEffectiveCampaignFeatures(runtimeCampaignEnabledFeatures, featureFlagsMap),

    [runtimeCampaignEnabledFeatures, featureFlagsMap]

  );

  const effectiveCampaignHomebrew = useMemo(

    () => getEffectiveCampaignHomebrew(runtimeCampaignHomebrew),

    [runtimeCampaignHomebrew]

  );

  const combatEnabled = globalCombatEnabled && !!effectiveCampaignFeatures.combat;

  const shopsEnabled = globalShopsEnabled && !!effectiveCampaignFeatures.shops;

  const lootEnabled = !!effectiveCampaignFeatures.loot;

  const systemsEnabled = !!effectiveCampaignFeatures.systems;

  const craftingEnabled = globalCraftingEnabled && !!effectiveCampaignFeatures.crafting;

  const professionsFeatureEnabled = globalProfessionsFeatureEnabled && craftingEnabled && !!effectiveCampaignFeatures.professions;

  const travelLogEnabled = !!effectiveCampaignFeatures.travelLog;

  const campaignManaEnabled = !!effectiveCampaignHomebrew.mana;

  const campaignDurabilityEnabled = !!effectiveCampaignHomebrew.durability;

  const campaignHungerEnabled = !!effectiveCampaignHomebrew.hungerStarvation;

  const campaignDietsEnabled = !!effectiveCampaignHomebrew.diets;

  const campaignDiseasesEnabled = !!effectiveCampaignHomebrew.diseases;

  const campaignFoodSettingsEnabled = !!effectiveCampaignHomebrew.foodSettings;

  const isViewAllowedByCampaignFeatures = useCallback((nextView) => {

    if (nextView === 'combat') return combatEnabled;

    if (nextView === 'shop') return shopsEnabled;

    if (nextView === 'loot') return lootEnabled;

    if (nextView === 'crafting') return craftingEnabled;

    if (nextView === 'travel') return travelLogEnabled;

    return true;

  }, [combatEnabled, shopsEnabled, lootEnabled, craftingEnabled, travelLogEnabled]);

  const featureGuardedView = isViewAllowedByCampaignFeatures(view)

    ? view

    : (isDM ? 'dm' : (charId ? 'home' : 'select'));

  const storageSubscriptionPaths = useMemo(
    () => buildStorageSubscriptionPaths({
      view: featureGuardedView,
      isDM,
      isMessageSurfaceOpen: showMessagePanel,
      isPlayerJournalSurfaceOpen: featureGuardedView === 'journal',
      isPlayerQuickJournalSurfaceOpen: showQuickAddJournal,
      isDmJournalSurfaceOpen: isDM && featureGuardedView === 'npcs',
      isDmQuickJournalSurfaceOpen: isDM && showDmJournal,
      featureAccess: effectiveCampaignFeatures,
      homebrewAccess: effectiveCampaignHomebrew,
    }),
    [featureGuardedView, isDM, showMessagePanel, showQuickAddJournal, showDmJournal, effectiveCampaignFeatures, effectiveCampaignHomebrew]
  );
  const hasPlayerFullJournalDiscovery = storageSubscriptionPaths.includes('journal');
  const hasDmFullJournalDiscovery = storageSubscriptionPaths.includes('dmJournal');

  const messageLiveTailCharId = !isDM
    ? (viewerContext?.viewerKind === 'player' && viewerContext?.viewerCharId ? viewerContext.viewerCharId : charId)
    : null;
  const messagePanelHistoryCharId = isDM ? charId : messageLiveTailCharId;

  const storageSubscriptionArgs = useMemo(() => ({
    messageLiveTail: {
      charId: messageLiveTailCharId || null,
      statusLimit: 50,
      dmLimit: 50,
    },
    messagePanelHistory: {
      charId: messagePanelHistoryCharId || null,
      statusLimit: 200,
      dmLimit: 200,
    },
    messageUnreadCounts: {
      charId: messagePanelHistoryCharId || null,
      statusLimit: 100,
      dmLimit: 100,
    },
    journalRecent: {
      limit: 50,
    },
    journalPage: {
      category: showQuickAddJournal && view !== 'journal' ? 'session' : null,
      sessionId: null,
      paginationOpts: { cursor: null, numItems: 200 },
    },
    dmJournalRecent: {
      limit: 50,
    },
    dmJournalPage: {
      category: isDM && showDmJournal && view !== 'npcs' ? 'session' : null,
      sessionId: null,
      paginationOpts: { cursor: null, numItems: 200 },
    },
  }), [messageLiveTailCharId, messagePanelHistoryCharId, showQuickAddJournal, showDmJournal, view, isDM]);

  const storageDefaults = useMemo(() => {
    const builtInDefaults = {
      diets: DEFAULT_DIETS,
      diseases: DEFAULT_DISEASES,
      systems: DEFAULT_SYSTEMS,
      foodTypes: DEFAULT_FOOD_TYPES,
      locations: DEFAULT_LOCATIONS,
      globalSettings: { ...DEFAULT_SETTINGS },
      journal: [...SAMPLE_JOURNAL_ENTRIES],
      npcLibrary: { ...DEMO_NPCS },
      audit: { ...DEFAULT_AUDIT },
      journalSettings: { showLinkTooltips: true },
      currentLocation: 'Stone Bridge',
    };
    if (!sandboxStorageSeed?.defaults) return builtInDefaults;
    return {
      ...builtInDefaults,
      ...sandboxStorageSeed.defaults,
      globalSettings: {
        ...builtInDefaults.globalSettings,
        ...(sandboxStorageSeed.defaults.globalSettings || {}),
      },
      audit: sandboxStorageSeed.defaults.audit || builtInDefaults.audit,
    };
  }, [sandboxStorageSeed]);

  const storageDemoData = useMemo(() => (
    sandboxStorageSeed?.demoData || createDemoData()
  ), [sandboxStorageSeed]);

  const {

    chars, food, diets, diseases, systems, foodTypes, locations, globalSettings, spellLibrary, groupTravelLog, currentLocation,

    messages, messageLiveTail, messagePanelHistory, journal, journalRecent, journalPage, dmJournal, dmJournalRecent, dmJournalPage, npcLibrary, journalSettings, combat, combatSnapshots,
    audit: legacyAudit, auditSessionMeta, auditMeta, auditRecent,

    partyInventory,
    shopRuntime,
    lootRuntime,

    loading, connectionStatus,

    saveChars: saveCharsRaw,

    patchCharacterVitals,

    patchCharacterSystemInstance,

    patchCharacterSystemState,

    patchCharacterSystemStateAndVitals,

    patchCharacterSystemStateAndResources,

    patchCharacterSystemStateAndEffects,

    saveFood: saveFoodRaw,

    saveDiets: saveDietsRaw,

    saveDiseases: saveDiseasesRaw,

    saveSystems: saveSystemsRaw,

    saveFoodTypes: saveFoodTypesRaw,

    saveLocations: saveLocationsRaw,

    saveGlobalSettings: saveGlobalSettingsRaw,

    saveSpellLibrary: saveSpellLibraryRaw,

    saveGroupTravelLog: saveGroupTravelLogRaw,

    saveCurrentLocation: saveCurrentLocationRaw,

    saveMessages: saveMessagesRaw,

    saveJournal: saveJournalRaw,

    saveDmJournal: saveDmJournalRaw,

    upsertJournalEntry,

    deleteJournalEntry,

    setJournalEntryCampaignScope,

    upsertDmJournalEntry,

    deleteDmJournalEntry,

    lookupJournalEntriesByName,

    lookupDmJournalEntriesByName,

    discoverJournalEntries,

    discoverDmJournalEntries,

    saveNpcLibrary: saveNpcLibraryRaw,

    saveJournalSettings: saveJournalSettingsRaw,

    saveCombat: saveCombatRaw,

    saveCombatSnapshots,

    savePartyInventory,

    depositPartyInventoryFromChar,

    takePartyInventoryToChar,

    transferInventoryItemBetweenChars,

    moveCharItemToCraftingTable,

    addInventoryItem,

    updateInventoryItem,

    removeInventoryItem,

    setInventoryItemFavorite,

    toggleInventoryItemEquipped,

    consumeInventoryItem,

    activateInventoryItemAbility,

    craftOutputToChar,

    craftOutputToParty,

    tableRowToChar,

    tableRowToParty,

    activateCombatItemAbility: activateCombatItemAbilityAuthority,

    applyEffectInstanceLifecycle: applyEffectInstanceLifecycleAuthority,

    reconcileItemAbilityEffectInstancesForChar,

    importShopDataForDm,
    importLootDataForDm,
    purchaseShopListing,
    checkoutShopCart,
    sellInventoryItemToShopForDm,
    submitTradeOffer,
    cancelTradeOffer,
    denyTradeOfferForDm,
    settleTradeOfferForDm,
    promoteTradeReceiptLineToShopListingForDm,
    adjustShopWalletForDm,
    createDmShop,
    deleteDmShop,
    updateDmShop,
    createDmShopListing,
    deleteDmShopListing,
    updateDmShopListing,
    updateDmShopRuntimeState,
    publishLootEncounterForDm,
    claimLootDrop,
    withdrawLootClaim,
    setLootDropVisibilityForDm,
    revealAllLootDropsForDm,
    finishLootEncounterForDm,
    distributeLootXpForDm,
    awardLootDrop,
    awardCurrencyLoot,

    updateGameData,

    pushAuditEvent: pushAuditEventRaw,

    pushAuditSession,

    markStatusMessageSeen,

    dismissStatusMessage,

    markDmChatRead,

    sendStatusMessage,

    sendDmChatMessage,

    deleteDmChatMessage

  } = useStorage({

    // Defaults the driver falls back to when a collection is missing or empty.

    // Spread the objects the driver may touch via optimistic cache updates so the

    // module-level constants aren't aliased into the driver's state.

    defaults: storageDefaults,

    demoData: storageDemoData,

    // Session 7B: plumbed through from AuthGate when Convex Auth is on.

    // Null means: demo-auth path or unauthenticated — driver uses non-projected

    // `chars:list` / `systems:list` subscriptions (legacy behavior).

    viewerContext,

    viewerAuthToken,

    useServerDerivedViewerQueries,

    forceLocalMode: forceLocalStorage,

    ...sandboxStorageContext,

    subscriptionPaths: storageSubscriptionPaths,

    subscriptionArgs: storageSubscriptionArgs,

  });

  const runtimeDiseases = campaignDiseasesEnabled ? diseases : {};

  const runtimeSystems = systemsEnabled ? systems : {};

  const runtimeGlobalSettings = useMemo(() => ({
    ...(globalSettings || {}),
    durabilityEnabled: (globalSettings?.durabilityEnabled !== false) && campaignDurabilityEnabled,
  }), [globalSettings, campaignDurabilityEnabled]);


  const audit = useMemo(() => {
    const sessionMeta = auditSessionMeta || {};
    const meta = auditMeta || {};
    const recent = auditRecent || {};
    const legacy = legacyAudit || {};
    return {
      ...DEFAULT_AUDIT,
      ...sessionMeta,
      ...meta,
      ...legacy,
      activeSessionId: legacy.activeSessionId ?? meta.activeSessionId ?? sessionMeta.activeSessionId ?? null,
      sessions: {
        ...(DEFAULT_AUDIT.sessions || {}),
        ...(sessionMeta.sessions || {}),
        ...(meta.sessions || {}),
        ...(legacy.sessions || {}),
      },
      snapshots: {
        ...(DEFAULT_AUDIT.snapshots || {}),
        ...(meta.snapshots || {}),
        ...(legacy.snapshots || {}),
      },
      events: {
        ...(recent.events || {}),
        ...(legacy.events || {}),
      },
    };
  }, [legacyAudit, auditSessionMeta, auditMeta, auditRecent]);


  const messageReadView = useMemo(() => {
    const baseMessages = showMessagePanel ? messagePanelHistory : null;
    return buildMessageReadView(baseMessages, messageLiveTail, charId);
  }, [showMessagePanel, messagePanelHistory, messageLiveTail, charId]);



  // Session C (2026-04-29): wrap pushAuditEvent so every event passes through

  // capAuditEvent (defined at module scope above). Original is renamed to

  // pushAuditEventRaw above; the wrapper here is what the rest of App.js

  // calls. ~68 call sites continue to use the unrenamed name; capping is

  // transparent. See capAuditEvent for the trim strategy.

  const pushAuditEvent = useCallback(

    (event) => pushAuditEventRaw(capAuditEvent(event)),

    [pushAuditEventRaw]

  );



  // Session 7B: bridge auth-derived viewerContext into the app's isDM/charId

  // state. Without this, a user signed in via Google OAuth would see the

  // character-select screen + password prompts from the demo-auth flow even

  // though the server already knows their role. This effect runs once when

  // viewerContext resolves (AuthedApp passes it after the myViewerContext

  // query returns) and auto-advances past select → DM dashboard or home.

  //

  // The demo-auth login path remains operational when viewerContext is null

  // (flag off, or `?local=1`) — clicking "Logout" returns to `view: 'select'`

  // and password prompts. Sign-out from Convex Auth unmounts AuthedApp,

  // which clears viewerContext and this effect fires again (no-op for null).

  useEffect(() => {

    if (!viewerContext) return;

    if (viewerContext.viewerKind === 'dm') {

      setIsDM(true);

      setCharId(null);

      if (!initialLaunchView) setView('dm');

    } else if (viewerContext.viewerKind === 'player' && viewerContext.viewerCharId) {

      setIsDM(false);

      setCharId(viewerContext.viewerCharId);

      if (!initialLaunchView) setView('home');

    }

  // view is set below; intentional single-fire on viewerContext change.

  // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [viewerContext]);



  const [showSettings, setShowSettings] = useState(false);

  const [showDMSettings, setShowDMSettings] = useState(false);

  const [showSandboxSettings, setShowSandboxSettings] = useState(false);

  const [showSandboxParty, setShowSandboxParty] = useState(false);

  const [editSystemFromDash, setEditSystemFromDash] = useState(null); // System ID to edit when opening DMSettings

  const [showSheet, setShowSheet] = useState(false);

  // 2026-04-29 — mobile redesign A.1.a: chrome state. `mobileTab` drives

  // the bottom-nav active state; A.1.a renders chrome only (body wiring

  // lands in A.2). `mobileMenuOpen` is the hamburger overflow — for

  // A.1.a it's an inline-positioned panel; A.1.b migrates to bottom sheet.

  const isMobileViewport = useIsMobile();

  // 2026-04-29: chrome offset for iOS Safari bottom toolbar / Android

  // URL bar reveals. Body wrappers add this to their paddingBottom so

  // content isn't clipped behind the bottom-nav-now-floating-higher.

  const chromeOffset = useChromeOffset();

  const mobileBodyStyle = useMemo(() => (isMobileViewport ? {

    paddingTop: '50px',

    paddingBottom: `${70 + chromeOffset}px`,

    minHeight: '100vh',

  } : undefined), [isMobileViewport, chromeOffset]);

  const [mobileTab, setMobileTab] = useState('stats');

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // 2026-04-29: when the user taps a different bottom-nav tab, scroll

  // to top of the new panel. Without this, the body keeps its previous

  // scroll position so a long Spells page leaves Skills opened halfway

  // down. Only fires on mobile and only on actual change (not first

  // mount). Use 'auto' over 'smooth' so the jump is instant — a

  // mid-animation scroll while the page is being rerendered with new

  // content is more disorienting than a snap.

  const mobileTabPrev = React.useRef(mobileTab);

  React.useEffect(() => {

    if (!isMobileViewport) return;

    if (mobileTabPrev.current === mobileTab) return;

    mobileTabPrev.current = mobileTab;

    window.scrollTo({ top: 0, behavior: 'auto' });

  }, [mobileTab, isMobileViewport]);



  // 2026-04-29: bug-report breadcrumb instrumentation. Pushes a 'view'

  // breadcrumb whenever the top-level view changes so the bug-report

  // form's breadcrumb panel can show "what was the user just doing?"

  // beyond what audit captures (audit tracks state changes; this tracks

  // navigation). Mobile tab changes likewise. Cheap (one push per change).

  React.useEffect(() => { pushBreadcrumb('view', view); }, [view]);

  React.useEffect(() => { if (isMobileViewport) pushBreadcrumb('tab', mobileTab); }, [mobileTab, isMobileViewport]);



  // 2026-04-29: drawer state for the floating-icon coordination.

  // The bug-report drawer anchors above the journal icon (bottom:130);

  // none of the other floating icons need to shift VERTICALLY when only

  // the drawer is open. The remaining shifts happen when the journal

  // panel or stat popup OPENS while the drawer is also open — see the

  // per-cluster `right` logic below. The derived `floatingJournalOpen`

  // / `floatingSheetOpen` flags + the closeOtherFloatingSurfaces callback

  // now see those booleans. The heavier journal draft/search state still

  // lives below with the rest of the journal implementation details.

  const bugReportDrawerOpen = useBugReportDrawerOpen();

  const [showDdbImport, setShowDdbImport] = useState(initialLaunchRequestsCharacterImport);

  const [showDdbUpdate, setShowDdbUpdate] = useState(false);

  const [showCongrats, setShowCongrats] = useState(null);

  const [loginPw, setLoginPw] = useState('');

  const [selChar, setSelChar] = useState(null);

  const [mealType, setMealType] = useState('auto');

  const [dmNotification, setDmNotification] = useState(null);
  const [dmSystemRuleSaveQueue, setDmSystemRuleSaveQueue] = useState([]);
  const activeDmSystemRuleSaveEntry = dmSystemRuleSaveQueue[0] || null;
  const activeDmSystemRulePendingSave = activeDmSystemRuleSaveEntry?.pendingSave || null;
  const activeDmSystemRuleSaveChar = activeDmSystemRuleSaveEntry?.context?.char || (activeDmSystemRulePendingSave?.characterId ? chars?.[activeDmSystemRulePendingSave.characterId] : null);
  const dmSystemRuleSaveAbility = (activeDmSystemRulePendingSave?.ability || 'wis').toLowerCase();
  const activeDmSystemRuleEffectContext = activeDmSystemRulePendingSave && activeDmSystemRuleSaveChar
    ? getResolvedCharacterEffectContext(activeDmSystemRuleSaveChar, {}, systems, true)
    : null;
  const activeDmSystemRuleSaveContext = activeDmSystemRulePendingSave && activeDmSystemRuleSaveChar
    ? getResolvedSaveContext({
        stat: dmSystemRuleSaveAbility,
        char: activeDmSystemRuleSaveChar,
        effectiveStats: activeDmSystemRuleEffectContext?.effectiveStats,
        equippedCombatBonuses: activeDmSystemRuleEffectContext?.equippedCombatBonuses,
        activeLegacyEffects: activeDmSystemRuleEffectContext?.activeLegacyEffects,
        activeRuntimeEffects: Array.isArray(activeDmSystemRuleSaveChar?.activeEffects) ? activeDmSystemRuleSaveChar.activeEffects : [],
      })
    : null;
  const activeDmSystemRuleSaveAdvantage = activeDmSystemRulePendingSave && activeDmSystemRuleSaveChar
    ? getResolvedCharacterAdvantageContext(
        activeDmSystemRuleSaveChar,
        { ...getSaveAdvantageConfig(dmSystemRuleSaveAbility) },
        {},
        systems,
        true,
      )
    : null;
  const [showDiseaseCheckModal, setShowDiseaseCheckModal] = useState(false);

  const [showDiceRoller, setShowDiceRoller] = useState(false);

  const [quickAddJournalText, setQuickAddJournalText] = useState('');

  const [quickAddConfirmation, setQuickAddConfirmation] = useState(null); // { summary } after submission

  const [showQuickAddCheatSheet, setShowQuickAddCheatSheet] = useState(false);

  const [showQuickAddRecent, setShowQuickAddRecent] = useState(false);

  const [quickAddSessionOverride, setQuickAddSessionOverride] = useState(null); // Manual session selection

  const [showSessionPicker, setShowSessionPicker] = useState(false);

  const [quickAddViewEntry, setQuickAddViewEntryRaw] = useState(null); // Entry to show in popup

  const [quickAddViewHistory, setQuickAddViewHistory] = useState([]); // Back navigation stack (max 15)

  const [quickAddSearch, setQuickAddSearch] = useState(''); // Title search query

  const [quickAddTextSearch, setQuickAddTextSearch] = useState(''); // Text/mention search query

  const [quickAddTitleDiscoveryResults, setQuickAddTitleDiscoveryResults] = useState([]);

  const quickAddTitleDiscoveryResultsRef = useRef([]);

  const quickAddTitleDiscoverySeqRef = useRef(0);

  const [quickAddNpcDiscovery, setQuickAddNpcDiscovery] = useState({ key: '', entries: [] });

  const quickAddNpcDiscoveryRef = useRef({ key: '', entries: [] });

  const quickAddNpcDiscoverySeqRef = useRef(0);

  const [quickAddSearchMode, setQuickAddSearchMode] = useState('title'); // 'title' or 'text'

  const [quickAddTextSearchMode, setQuickAddTextSearchMode] = useState('phrase'); // 'phrase' or 'words'

  const [quickAddEntryMode, setQuickAddEntryMode] = useState('session'); // 'session' or 'standalone'

  const [quickAddStandaloneName, setQuickAddStandaloneName] = useState('');

  const [quickAddStandaloneCategory, setQuickAddStandaloneCategory] = useState('creatures');

  const [editingSessionNumber, setEditingSessionNumber] = useState(false);

  const [editSessionNumValue, setEditSessionNumValue] = useState('');

  const [quickAddPrivate, setQuickAddPrivate] = useState(false); // Persistent private text toggle

  const [quickAddPrivateNotes, setQuickAddPrivateNotes] = useState(''); // Separate private notes input

  const [quickAddSharedWith, setQuickAddSharedWith] = useState([]); // charIds to share private text with

  const [quickAddNotesOpen, setQuickAddNotesOpen] = useState(false); // Notes panel persistence

  const quickAddNotesRef = useRef(null); // Notes textarea ref

  const quickAddEditorRef = useRef(null); // Main editor focus ref

  const quickAddLastFocusRef = useRef('editor'); // Track last focused panel: 'editor' | 'notes'

  const [quickAddNewlineOnPrivChange, setQuickAddNewlineOnPrivChange] = useState(true); // Newline between different privacy types

  const quickAddLastPrivateRef = useRef(null); // Track last submission's privacy: true/false/null

  const [quickAddShareWithDM, setQuickAddShareWithDM] = useState(true); // Share private text with DM

  // null = don't file this as a categorized note block on NPC entries (general note only).

  // When user explicitly picks 'profile' / 'quests' / etc., the submitted text is also

  // appended to that NPC's corresponding playerNotes category. See addPlayerNoteBlocks.

  const [playerNoteCategories, setPlayerNoteCategories] = useState(null);

  

  // ============ DM QUICK JOURNAL STATE ============

  const closeQuickAddJournal = useCallback(() => {
    setShowQuickAddJournal(false);
    setQuickAddJournalText('');
    setShowQuickAddCheatSheet(false);
    setShowQuickAddRecent(false);
  }, []);



  // 2026-04-29: floating-state coordination for the bug-report system.

  // Placed AFTER showDmJournal / showQuickAddJournal / showSheet have all

  // been declared above. `closeOtherFloatingSurfaces` is invoked by the

  // bug-report form when starting recording or element-selection so the

  // recording bar / picker overlay isn't covered by another open surface.

  const floatingJournalOpen = !!(showDmJournal || showQuickAddJournal);

  const floatingSheetOpen = !!showSheet;

  const closeOtherFloatingSurfaces = useCallback(() => {

    setShowDmJournal(false);

    setShowQuickAddJournal(false);

    setShowSheet(false);

  }, []);



  // Unified journal-cluster position. See BUG-REPORT-REFERENCE.md §8.

  //

  // PANEL OPEN — sits at the "center" anchor (right:16 bottom:70) by

  // default; shifts LEFT when other panels are also open to avoid

  // overlap (panel widths: bug drawer 440 → starts at right:12, ends at

  // right:452; stat popup 320 → starts at right:16, ends at right:336).

  //

  // PANEL CLOSED (button only) — stays at right:16 bottom:70 in default

  // state. When some OTHER panel is open, the closed button drops into

  // the bottom row at bottom:20. It claims slot A (right:20) when stat

  // is open (stat popup vacated the corner by moving to "center"), and

  // slot B (right:80) when only the drawer is open (stat sits at slot A).

  const computeJournalPos = (panelOpen) => {

    if (panelOpen) {

      // All-three-open: stat popup at right:472 (width ~320 → ends near

      // right:792). Journal anchored at right:820 sits ~28px to the

      // popup's left — tight visual cluster, no overlap. (Was 880; the

      // 88px gap read as wasted space.)

      if (bugReportDrawerOpen && floatingSheetOpen) return { right: '820px', bottom: '70px' };

      if (bugReportDrawerOpen) return { right: '472px', bottom: '70px' };

      if (floatingSheetOpen) return { right: '400px', bottom: '70px' };

      return { right: '16px', bottom: '70px' };

    }

    // Closed button:

    if (floatingSheetOpen) return { right: '20px', bottom: '20px' }; // slot A — stat vacated corner

    if (bugReportDrawerOpen) return { right: '80px', bottom: '20px' }; // slot B — stat at A

    return { right: '16px', bottom: '70px' }; // default vertical stack

  };



  const [dmJournalText, setDmJournalText] = useState('');

  const [dmJournalConfirm, setDmJournalConfirm] = useState(null);

  const [dmJournalFilter, setDmJournalFilter] = useState('all');

  const [dmJournalSearch, setDmJournalSearch] = useState('');

  const [dmJournalTitleDiscoveryResults, setDmJournalTitleDiscoveryResults] = useState([]);

  const dmJournalTitleDiscoveryResultsRef = useRef([]);

  const dmJournalTitleDiscoverySeqRef = useRef(0);

  const [dmQuestDiscovery, setDmQuestDiscovery] = useState({ key: '', entries: [] });

  const dmQuestDiscoveryRef = useRef({ key: '', entries: [] });

  const dmQuestDiscoverySeqRef = useRef(0);

  const [dmJournalViewEntry, setDmJournalViewEntry] = useState(null);

  const dmJournalEditorRef = useRef(null);

  const [dmJournalRevealable, setDmJournalRevealable] = useState(false);

  const [dmLogToSession, setDmLogToSession] = useState(true);

  const [dmNpcNoteCategories, setDmNpcNoteCategories] = useState(['session']);

  const [dmSelectedQuest, setDmSelectedQuest] = useState(null); // Active quest chip selection
  const [dmSelectedQuestExplicit, setDmSelectedQuestExplicit] = useState(false);

  const [dmActiveSessionId, setDmActiveSessionId] = useState(null);

  const [showDmSessionPicker, setShowDmSessionPicker] = useState(false);

  

  const getDmActiveSession = (entriesOverride = null) => (
    getActiveJournalSession(
      Array.isArray(entriesOverride) ? entriesOverride : getDmSessionCandidates(),
      { manualSessionId: dmActiveSessionId },
    )
  );

  const extractJournalSessionNumber = (entry) => {
    if (Number.isFinite(entry?.sessionNumber)) return Math.trunc(entry.sessionNumber);
    const match = typeof entry?.name === 'string' ? entry.name.match(/\bSession\s+(\d+)\b/i) : null;
    if (!match) return null;
    const parsed = Number.parseInt(match[1], 10);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const resolveNextJournalSessionNumber = (entries, sessionMeta = null) => {
    const localMax = listJournalSessions(entries)
      .map(extractJournalSessionNumber)
      .filter(Number.isFinite)
      .reduce((max, value) => Math.max(max, value), 0);
    const metaMax = Number.isFinite(sessionMeta?.maxSessionNumber)
      ? Math.trunc(sessionMeta.maxSessionNumber)
      : 0;
    return Math.max(localMax, metaMax) + 1;
  };

  const fetchJournalSessionMeta = async (discoverEntries, label) => {
    if (typeof discoverEntries !== 'function') return null;
    try {
      const result = await discoverEntries({
        includeSessionMeta: true,
        sessionLimit: 200,
      });
      return result?.sessionMeta || null;
    } catch (error) {
      console.error(`${label} session metadata lookup failed:`, error);
      return null;
    }
  };

  const fetchJournalAutocompleteEntries = async (discoverEntries, query, label) => {
    if (typeof discoverEntries !== 'function') return [];
    const normalizedQuery = typeof query === 'string' ? query.trim() : '';
    if (normalizedQuery.length < 3) return [];
    try {
      const result = await discoverEntries({
        query: normalizedQuery,
        limit: 8,
        categoryLimit: 50,
      });
      return Array.isArray(result?.entries) ? result.entries : [];
    } catch (error) {
      console.error(`${label} autocomplete discovery failed:`, error);
      return [];
    }
  };

  const collectPlayerNpcReferenceNames = (text, { includeFormal = true, includeBare = true } = {}) => {
    if (!text) return [];
    const qaSettings = { ...DEFAULT_QUICKADD_SETTINGS, ...(journalSettings?.quickAdd || {}) };
    const tags = findAllTags(text, qaSettings.delimiter || ' - ');
    const seen = new Set();
    const names = [];
    tags.forEach(tag => {
      const isFormalNpc = tag.type === 'tag' && (tag.code === 'N' || tag.code === 'S');
      const isBareRef = tag.type === 'ref';
      if ((!includeFormal && isFormalNpc) || (!includeBare && isBareRef)) return;
      if (!isFormalNpc && !isBareRef) return;
      const cleaned = String(tag.name || '').replace(/[.,;:!?]+$/, '').trim();
      const key = normalizeJournalNameKey(cleaned);
      if (!key || seen.has(key)) return;
      seen.add(key);
      names.push(cleaned);
    });
    return names;
  };

  const buildPlayerNpcDiscoveryKey = (text) => (
    collectPlayerNpcReferenceNames(text, { includeFormal: false, includeBare: true })
      .map(name => normalizeJournalNameKey(name))
      .filter(key => key.length >= 3)
      .sort()
      .join('|')
  );

  const entryUpdatedTime = (entry) => {
    const parsed = Date.parse(entry?.updatedAt || entry?.createdAt || '');
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const resolveClearNpcDiscoveryMatches = (npcMatches) => {
    const byId = new Map();
    (Array.isArray(npcMatches) ? npcMatches : []).forEach(match => {
      const refName = match?.refName || '';
      const refKey = normalizeJournalNameKey(refName);
      const exactMatches = (Array.isArray(match?.entries) ? match.entries : [])
        .map(entry => entry?.entry || entry)
        .filter(entry => entry?.id && entry?.name && entry?.category === 'npcs')
        .filter(entry => refKey && normalizeJournalNameKey(entry.name) === refKey)
        .sort((a, b) => entryUpdatedTime(b) - entryUpdatedTime(a));
      if (exactMatches.length === 1 && !byId.has(exactMatches[0].id)) {
        byId.set(exactMatches[0].id, exactMatches[0]);
      }
    });
    return Array.from(byId.values());
  };

  const clearQuickAddNpcDiscovery = () => {
    if (!quickAddNpcDiscoveryRef.current.key && quickAddNpcDiscoveryRef.current.entries.length === 0) return;
    const empty = { key: '', entries: [] };
    quickAddNpcDiscoveryRef.current = empty;
    setQuickAddNpcDiscovery(empty);
  };

  const replaceQuickAddNpcDiscovery = (key, entries) => {
    const next = { key, entries: Array.isArray(entries) ? entries : [] };
    quickAddNpcDiscoveryRef.current = next;
    setQuickAddNpcDiscovery(next);
  };

  const getClearDiscoveredNpcEntriesForText = (text) => {
    const key = buildPlayerNpcDiscoveryKey(text);
    if (!key || quickAddNpcDiscovery.key !== key) return [];
    return Array.isArray(quickAddNpcDiscovery.entries) ? quickAddNpcDiscovery.entries : [];
  };

  const mergeDiscoveredNpcRefsForText = (baseEntries, text) => {
    const discoveredNpcs = getClearDiscoveredNpcEntriesForText(text);
    return discoveredNpcs.length > 0
      ? mergeJournalEntrySources(baseEntries, discoveredNpcs)
      : baseEntries;
  };

  const discoverClearPlayerNpcRefs = async (text) => {
    if (hasPlayerFullJournalDiscovery || typeof discoverJournalEntries !== 'function') return [];
    const refNames = collectPlayerNpcReferenceNames(text, { includeFormal: false, includeBare: true })
      .filter(name => normalizeJournalNameKey(name).length >= 3);
    if (refNames.length === 0) return [];
    try {
      const result = await discoverJournalEntries({
        npcRefs: refNames,
        limit: 5,
        categoryLimit: 50,
      });
      return resolveClearNpcDiscoveryMatches(result?.npcMatches);
    } catch (error) {
      console.error('Quick Journal NPC reference discovery failed:', error);
      return [];
    }
  };

  useEffect(() => {
    const key = buildPlayerNpcDiscoveryKey(quickAddJournalText);
    const requestId = quickAddNpcDiscoverySeqRef.current + 1;
    quickAddNpcDiscoverySeqRef.current = requestId;
    if (
      !showQuickAddJournal
      || hasPlayerFullJournalDiscovery
      || !key
      || typeof discoverJournalEntries !== 'function'
    ) {
      clearQuickAddNpcDiscovery();
      return undefined;
    }
    const timer = setTimeout(async () => {
      const entries = await discoverClearPlayerNpcRefs(quickAddJournalText);
      if (quickAddNpcDiscoverySeqRef.current !== requestId) return;
      if (buildPlayerNpcDiscoveryKey(quickAddJournalText) !== key) return;
      replaceQuickAddNpcDiscovery(key, entries);
    }, 220);
    return () => clearTimeout(timer);
  }, [
    discoverJournalEntries,
    hasPlayerFullJournalDiscovery,
    journalSettings?.quickAdd,
    quickAddJournalText,
    showQuickAddJournal,
  ]);

  const clearQuickAddTitleDiscoveryResults = () => {
    if (quickAddTitleDiscoveryResultsRef.current.length === 0) return;
    quickAddTitleDiscoveryResultsRef.current = [];
    setQuickAddTitleDiscoveryResults([]);
  };

  const replaceQuickAddTitleDiscoveryResults = (entries) => {
    const next = Array.isArray(entries) ? entries : [];
    quickAddTitleDiscoveryResultsRef.current = next;
    setQuickAddTitleDiscoveryResults(next);
  };

  const clearDmJournalTitleDiscoveryResults = () => {
    if (dmJournalTitleDiscoveryResultsRef.current.length === 0) return;
    dmJournalTitleDiscoveryResultsRef.current = [];
    setDmJournalTitleDiscoveryResults([]);
  };

  const replaceDmJournalTitleDiscoveryResults = (entries) => {
    const next = Array.isArray(entries) ? entries : [];
    dmJournalTitleDiscoveryResultsRef.current = next;
    setDmJournalTitleDiscoveryResults(next);
  };

  const collectDmQuestNpcNames = (text) => (
    collectPlayerNpcReferenceNames(text, { includeFormal: true, includeBare: false })
      .filter(name => normalizeJournalNameKey(name).length >= 3)
  );

  const buildDmQuestDiscoveryKey = (text) => (
    collectDmQuestNpcNames(text)
      .map(name => normalizeJournalNameKey(name))
      .sort()
      .join('|')
  );

  const isActiveJournalQuest = (entry) => {
    const status = normalizeJournalNameKey(entry?.status || '');
    return entry?.category === 'quests'
      && status !== 'completed'
      && status !== 'complete'
      && status !== 'done'
      && status !== 'closed';
  };

  const resolveClearDmQuestDiscoveryMatches = (questMatches) => {
    const byId = new Map();
    (Array.isArray(questMatches) ? questMatches : []).forEach(match => {
      (Array.isArray(match?.entries) ? match.entries : [])
        .map(entry => entry?.entry || entry)
        .filter(entry => entry?.id && isActiveJournalQuest(entry))
        .forEach(entry => {
          if (!byId.has(entry.id)) byId.set(entry.id, entry);
        });
    });
    return byId.size === 1 ? Array.from(byId.values()) : [];
  };

  const clearDmQuestDiscovery = () => {
    if (!dmQuestDiscoveryRef.current.key && dmQuestDiscoveryRef.current.entries.length === 0) return;
    const empty = { key: '', entries: [] };
    dmQuestDiscoveryRef.current = empty;
    setDmQuestDiscovery(empty);
  };

  const replaceDmQuestDiscovery = (key, entries) => {
    const next = { key, entries: Array.isArray(entries) ? entries : [] };
    dmQuestDiscoveryRef.current = next;
    setDmQuestDiscovery(next);
  };

  const getClearDiscoveredDmQuestEntriesForText = (text) => {
    const key = buildDmQuestDiscoveryKey(text);
    const currentDiscovery = dmQuestDiscoveryRef.current;
    if (!key || currentDiscovery.key !== key) return [];
    return Array.isArray(currentDiscovery.entries) ? currentDiscovery.entries : [];
  };

  const getSelectedDmQuestForText = (text) => {
    if (!dmSelectedQuest || !dmSelectedQuestExplicit) return null;
    if (!hasDmFullJournalDiscovery) {
      return getClearDiscoveredDmQuestEntriesForText(text)
        .find(entry => entry?.id === dmSelectedQuest) || null;
    }
    return dmJournalReadCandidates
      .filter(entry => isActiveJournalQuest(entry))
      .find(entry => entry?.id === dmSelectedQuest) || null;
  };

  const mergeNpcQuestConnections = (questConnections, questName) => {
    const trimmedQuestName = typeof questName === 'string' ? questName.trim() : '';
    const existingConnections = Array.isArray(questConnections) ? questConnections : [];
    if (!trimmedQuestName) return existingConnections;
    const questKey = normalizeJournalNameKey(trimmedQuestName);
    return existingConnections.some(q => normalizeJournalNameKey(q) === questKey)
      ? existingConnections
      : [...existingConnections, trimmedQuestName];
  };

  const clearDmQuestSelection = () => {
    setDmSelectedQuest(null);
    setDmSelectedQuestExplicit(false);
  };

  const selectDmQuestExplicitly = (questId) => {
    if (dmSelectedQuest === questId && dmSelectedQuestExplicit) {
      clearDmQuestSelection();
      return;
    }
    setDmSelectedQuest(questId);
    setDmSelectedQuestExplicit(true);
  };

  const autoSelectDmQuest = (questId) => {
    setDmSelectedQuest(questId);
    setDmSelectedQuestExplicit(false);
  };

  const discoverClearDmQuestMatches = async (text) => {
    if (hasDmFullJournalDiscovery || typeof discoverDmJournalEntries !== 'function') return [];
    const questNpcNames = collectDmQuestNpcNames(text);
    if (questNpcNames.length === 0) return [];
    try {
      const result = await discoverDmJournalEntries({
        questNpcNames,
        limit: 6,
        questLimit: 50,
      });
      return resolveClearDmQuestDiscoveryMatches(result?.questMatches);
    } catch (error) {
      console.error('DM Quick Journal quest discovery failed:', error);
      return [];
    }
  };

  useEffect(() => {
    const query = quickAddSearch.trim();
    const requestId = quickAddTitleDiscoverySeqRef.current + 1;
    quickAddTitleDiscoverySeqRef.current = requestId;
    if (
      !showQuickAddJournal
      || quickAddSearchMode !== 'title'
      || hasPlayerFullJournalDiscovery
      || query.length < 3
      || typeof discoverJournalEntries !== 'function'
    ) {
      clearQuickAddTitleDiscoveryResults();
      return undefined;
    }
    const timer = setTimeout(async () => {
      const entries = await fetchJournalAutocompleteEntries(discoverJournalEntries, query, 'Quick Journal title search');
      if (quickAddTitleDiscoverySeqRef.current !== requestId) return;
      replaceQuickAddTitleDiscoveryResults(entries);
    }, 200);
    return () => clearTimeout(timer);
  }, [
    discoverJournalEntries,
    hasPlayerFullJournalDiscovery,
    quickAddSearch,
    quickAddSearchMode,
    showQuickAddJournal,
  ]);

  useEffect(() => {
    const query = dmJournalSearch.trim();
    const requestId = dmJournalTitleDiscoverySeqRef.current + 1;
    dmJournalTitleDiscoverySeqRef.current = requestId;
    if (
      !showDmJournal
      || hasDmFullJournalDiscovery
      || query.length < 3
      || typeof discoverDmJournalEntries !== 'function'
    ) {
      clearDmJournalTitleDiscoveryResults();
      return undefined;
    }
    const timer = setTimeout(async () => {
      const entries = await fetchJournalAutocompleteEntries(discoverDmJournalEntries, query, 'DM Quick Journal title search');
      if (dmJournalTitleDiscoverySeqRef.current !== requestId) return;
      replaceDmJournalTitleDiscoveryResults(entries);
    }, 200);
    return () => clearTimeout(timer);
  }, [
    discoverDmJournalEntries,
    dmJournalSearch,
    hasDmFullJournalDiscovery,
    showDmJournal,
  ]);

  useEffect(() => {
    const key = buildDmQuestDiscoveryKey(dmJournalText);
    const requestId = dmQuestDiscoverySeqRef.current + 1;
    dmQuestDiscoverySeqRef.current = requestId;
    if (
      !showDmJournal
      || hasDmFullJournalDiscovery
      || !key
      || typeof discoverDmJournalEntries !== 'function'
    ) {
      clearDmQuestDiscovery();
      return undefined;
    }
    const timer = setTimeout(async () => {
      const entries = await discoverClearDmQuestMatches(dmJournalText);
      if (dmQuestDiscoverySeqRef.current !== requestId) return;
      if (buildDmQuestDiscoveryKey(dmJournalText) !== key) return;
      replaceDmQuestDiscovery(key, entries);
    }, 220);
    return () => clearTimeout(timer);
  }, [
    discoverDmJournalEntries,
    dmJournalText,
    hasDmFullJournalDiscovery,
    journalSettings?.quickAdd,
    showDmJournal,
  ]);

  

  const handleDmJournalSubmit = async (text) => {

    if (!text.trim()) return;

    try {

      const qaSettings = { ...DEFAULT_QUICKADD_SETTINGS, ...(journalSettings?.quickAdd || {}) };

      // Use DM tag codes for parsing (supports #P# and #C#)

      const parsed = parseQuickAddInput(text.trim(), qaSettings);

      const initialDmJournal = getDmSessionCandidates();
      const currentDmJournal = await enrichDmQuickAddParserBase(initialDmJournal, parsed);

      // Find or create active DM session

      let activeSessionId = null;

      let targetDmJournal = cloneJournalEntriesForDiff(currentDmJournal);

      if (dmLogToSession) {

        let activeSession = getDmActiveSession(currentDmJournal);

        if (!activeSession) {

          // Auto-create a new session

          const sessionMeta = await fetchJournalSessionMeta(discoverDmJournalEntries, 'DM Quick Journal');

          const sessionNum = resolveNextJournalSessionNumber(currentDmJournal, sessionMeta);

          activeSession = createJournalEntry('session', {

            name: `Session ${sessionNum}`,

            sessionNumber: sessionNum,

            sessionDate: new Date().toISOString().split('T')[0],

            summary: '',

            visibility: 'shared',

            createdBy: 'DM',

          });

          targetDmJournal = [...targetDmJournal, activeSession];

        }

        activeSessionId = activeSession.id;

      }

      const beforeDmJournal = cloneJournalEntriesForDiff(targetDmJournal);

      const { updatedJournal, summary } = applyQuickAddResults(parsed, targetDmJournal, activeSessionId, 'DM');

      // Wrap revealable content in markers, tag entries

      const taggedJournal = updatedJournal.map(e => {

        const wasCreated = summary.created.some(c => c.name.toLowerCase() === e.name?.toLowerCase() && c.category === e.category);

        const wasUpdated = summary.updated.some(c => c.name.toLowerCase() === e.name?.toLowerCase() && c.category === e.category);

        if (wasCreated) {

          // New entry: wrap entire description if revealable

          const desc = e.description || '';

          return { ...e, _dmRevealable: dmJournalRevealable, _dmRevealed: false, description: dmJournalRevealable && desc ? `{{REVEAL}}${desc}{{/REVEAL}}` : desc };

        }

        if (wasUpdated && dmJournalRevealable) {

          // Existing entry updated: wrap only the NEW text in REVEAL markers

          const oldEntry = beforeDmJournal.find(o => o.id === e.id);

          const oldDesc = oldEntry?.description || '';

          if (e.description && e.description.length > oldDesc.length) {

            const newText = e.description.substring(oldDesc.length);

            return { ...e, _dmRevealable: true, description: oldDesc + '{{REVEAL}}' + newText + '{{/REVEAL}}' };

          }

        }

        return e;

      });

      if (!persistDmQuickAddJournalDiff(beforeDmJournal, taggedJournal)) return;

      const selectedDmQuest = getSelectedDmQuestForText(text);
      const selectedDmQuestName = typeof selectedDmQuest?.name === 'string'
        ? selectedDmQuest.name.trim()
        : '';

      setDmJournalText('');
      clearDmQuestSelection();

      // Auto-create/update NPC Library entries for #N@ tags  

      const npcEntries = [...summary.created, ...summary.updated].filter(e => e.category === 'npcs');

      if (npcEntries.length > 0) {

        let updatedNpcLib = { ...(npcLibrary || {}) };

        let npcChanged = false;

        npcEntries.forEach(e => {

          // Get only the NEW text from the parsed entry description

          const journalEntry = taggedJournal.find(j => j.name?.toLowerCase() === e.name?.toLowerCase() && j.category === 'npcs');

          const oldEntry = currentDmJournal.find(j => j.name?.toLowerCase() === e.name?.toLowerCase() && j.category === 'npcs');

          const fullDesc = (journalEntry?.description || '');

          const oldDesc = (oldEntry?.description || '');

          // Extract only the newly added portion

          const newPortion = fullDesc.length > oldDesc.length ? fullDesc.substring(oldDesc.length) : fullDesc;

          const cleanText = newPortion.replace(/\{\{REVEAL\}\}|\{\{\/REVEAL\}\}/g, '').replace(/──\s*Session\s*#\d+\s*──\n?/g, '').replace(/^[\n\s]+/, '').trim();

          if (!cleanText) return;

          const visibility = dmJournalRevealable ? 'revealable' : 'dm-only';

          const noteField = (dmNpcNoteCategories && dmNpcNoteCategories[0]) || 'session';

          // Create a reveal item for each selected category

          const categories = dmNpcNoteCategories || ['session'];

          const newItems = categories.map(cat => ({

            id: generateId(), text: cleanText, field: cat,

            visibility: cat === 'secrets' ? 'dm-only' : visibility, revealedAt: null

          }));

          const existing = Object.values(updatedNpcLib).find(n => n.name?.toLowerCase() === e.name?.toLowerCase());

          if (existing) {

            const existingItems = existing.revealItems || [];

            const itemsToAdd = newItems.filter(ni => !existingItems.some(ri => ri.text === ni.text && ri.field === ni.field));

            const nextQuestConnections = selectedDmQuestName
              ? mergeNpcQuestConnections(existing.questConnections, selectedDmQuestName)
              : existing.questConnections;

            const questConnectionChanged = nextQuestConnections !== existing.questConnections;

            if (itemsToAdd.length > 0 || questConnectionChanged) {

              updatedNpcLib[existing.id] = {
                ...existing,
                revealItems: itemsToAdd.length > 0 ? [...existingItems, ...itemsToAdd] : existingItems,
                ...(selectedDmQuestName ? { questConnections: nextQuestConnections } : {}),
                updatedAt: new Date().toISOString(),
              };

              npcChanged = true;

            }

          } else {

            const npc = createNPC({
              name: e.name,
              notes: '',
              revealItems: newItems,
              ...(selectedDmQuestName ? { questConnections: [selectedDmQuestName] } : {}),
            });

            updatedNpcLib[npc.id] = npc;

            npcChanged = true;

          }

        });

        if (npcChanged) saveNpcLibrary(updatedNpcLib);

      }

      if (summary.created.length || summary.updated.length) {

        const allAffected = [...summary.created, ...summary.updated].map(e => {

          const match = taggedJournal.find(j => j.name?.toLowerCase() === e.name?.toLowerCase() && j.category === e.category);

          return { ...e, id: match?.id };

        });

        setDmJournalConfirm({ entries: allAffected, created: summary.created.length, updated: summary.updated.length });

        setTimeout(() => setDmJournalConfirm(null), 6000);

      }

    } catch (err) { console.error('DM Journal submit error:', err); }

  };

  const findPlayerRevealTarget = async (entry) => {
    const fullJournalEntries = hasPlayerFullJournalDiscovery && Array.isArray(journal) ? journal : [];
    const fullMatch = findJournalEntryByCategoryName(fullJournalEntries, {
      category: entry?.category,
      name: entry?.name,
    });
    const rowFirstMatch = findJournalEntryByCategoryName(
      mergeJournalEntrySources(journalRecentEntries, journalPageEntries, fullJournalEntries),
      {
        category: entry?.category,
        name: entry?.name,
      },
    );
    if (rowFirstMatch) {
      return {
        entry: rowFirstMatch,
        source: fullMatch?.id === rowFirstMatch.id ? 'full' : 'row',
      };
    }
    if (typeof lookupJournalEntriesByName !== 'function') return { entry: null, source: null };
    try {
      const result = await lookupJournalEntriesByName({
        lookups: [{ category: entry.category, name: entry.name }],
        limit: 5,
      });
      const lookupMatch = findJournalEntryByCategoryName(entriesFromNameLookupResult(result), {
        category: entry.category,
        name: entry.name,
      });
      return lookupMatch ? { entry: lookupMatch, source: 'lookup' } : { entry: null, source: null };
    } catch (error) {
      console.error('DM journal reveal target lookup failed:', error);
      return { entry: null, source: null };
    }
  };

  const revealDmEntry = async (entryId) => {

    const fullDmEntry = (dmJournal || []).find(e => e.id === entryId);
    const entry = dmJournalReadCandidates.find(e => e.id === entryId) || fullDmEntry;

    if (!entry) return;

    // Extract only REVEAL-wrapped content (DM-only content stays hidden)

    const revealRegex = /\{\{REVEAL\}\}([\s\S]*?)\{\{\/REVEAL\}\}/g;

    const revealParts = [];

    let match;

    while ((match = revealRegex.exec(entry.description || '')) !== null) {

      revealParts.push(match[1].trim());

    }

    // If no REVEAL markers, fall back to full description (legacy entries)

    const revealedText = (revealParts.length > 0 ? revealParts.join('\n') : (entry.description || '')).replace(/──\s*Session\s*#\d+\s*──\n?/g, '').trim();

    if (!revealedText.trim()) { setDmJournalConfirm('Nothing revealable in this entry'); setTimeout(() => setDmJournalConfirm(null), 3000); return; }

    // Check if entry already exists in player journal (update vs create)

    const { entry: existingPlayerEntry, source: playerTargetSource } = await findPlayerRevealTarget(entry);

    let playerRevealEntry;

    if (existingPlayerEntry) {

      // Append to existing player journal entry

      playerRevealEntry = {
        ...existingPlayerEntry,
        description: (existingPlayerEntry.description ? existingPlayerEntry.description + '\n' : '') + revealedText,
        updatedAt: new Date().toISOString(),
      };

    } else {

      // Create new shared journal entry

      playerRevealEntry = createJournalEntry(entry.category, {

        name: entry.name,

        description: revealedText,

        visibility: 'shared',

        createdBy: 'DM',

        tags: [...(entry.tags || []), 'dm-revealed'],

        locationTags: entry.locationTags || [],

      });

    }

    if (typeof upsertJournalEntry === 'function') {
      try {
        const result = upsertJournalEntry({ entryId: playerRevealEntry.id, entry: playerRevealEntry });
        if (result && typeof result.catch === 'function') {
          result.catch(error => console.error('DM journal reveal player row upsert failed:', error));
        }
      } catch (error) {
        console.error('DM journal reveal player row upsert failed:', error);
      }
    } else {
      if (!existingPlayerEntry || playerTargetSource === 'full') {
        const updatedPlayerJournal = existingPlayerEntry
          ? (journal || []).map(j => j.id === existingPlayerEntry.id ? playerRevealEntry : j)
          : [...(journal || []), playerRevealEntry];
        saveJournal(updatedPlayerJournal);
      } else {
        console.warn('DM journal reveal skipped full player journal fallback for row-only target');
      }
    }

    // Mark as revealed in DM journal

    const revealedDmEntry = { ...entry, _dmRevealed: true, _dmRevealedAt: new Date().toISOString(), updatedAt: new Date().toISOString() };

    if (typeof upsertDmJournalEntry === 'function') {
      try {
        const result = upsertDmJournalEntry({ entryId: revealedDmEntry.id, entry: revealedDmEntry });
        if (result && typeof result.catch === 'function') {
          result.catch(error => console.error('DM journal reveal source row upsert failed:', error));
        }
      } catch (error) {
        console.error('DM journal reveal source row upsert failed:', error);
      }
    } else {
      if (fullDmEntry) {
        const updatedDmJournal = (dmJournal || []).map(e =>

          e.id === entryId ? revealedDmEntry : e

        );

        saveDmJournal(updatedDmJournal);
      } else {
        console.warn('DM journal reveal skipped full DM journal fallback for row-only source');
      }
    }

    setDmJournalViewEntry(prev => prev?.id === entryId ? revealedDmEntry : prev);

    // Push notification to players

    const newMessages = { ...messages, statusUpdates: [...(messages?.statusUpdates || []), { id: generateId(), type: 'dm-reveal', fromDM: true, visibility: 'public', text: '📓 New journal entry revealed: ' + entry.name, category: entry.category, timestamp: new Date().toISOString(), dismissed: false }] };

    handleSendStatusMessage({ message: newMessages.statusUpdates[newMessages.statusUpdates.length - 1] });

    setDmJournalConfirm('Revealed to players: ' + entry.name);

    setTimeout(() => setDmJournalConfirm(null), 4000);

  };

  // Render DM journal description with REVEAL sections highlighted

  const renderDmDescription = (text) => {

    if (!text) return null;

    const parts = [];

    let lastIdx = 0;

    const regex = /\{\{REVEAL\}\}([\s\S]*?)\{\{\/REVEAL\}\}/g;

    let m;

    while ((m = regex.exec(text)) !== null) {

      if (m.index > lastIdx) parts.push({ type: 'hidden', text: text.substring(lastIdx, m.index) });

      parts.push({ type: 'reveal', text: m[1] });

      lastIdx = m.index + m[0].length;

    }

    if (lastIdx < text.length) parts.push({ type: 'hidden', text: text.substring(lastIdx) });

    if (parts.length === 0) return <span style={{ whiteSpace: 'pre-wrap' }}>{text.replace(/──\s*Session\s*#\d+\s*──\n?/g, '')}</span>;

    if (parts.length === 1 && parts[0].type === 'hidden') return <span style={{ whiteSpace: 'pre-wrap' }}>{parts[0].text.replace(/──\s*Session\s*#\d+\s*──\n?/g, '')}</span>;

    return parts.map((p, i) => p.type === 'reveal' 

      ? <div key={i} style={{ padding: '3px 6px', margin: '2px 0', background: 'rgba(74,144,217,0.08)', borderLeft: '2px solid #4a90d960', borderRadius: 2 }}><span style={{ fontSize: '0.48rem', color: '#4a90d9', fontWeight: 600, letterSpacing: 0.5 }}>👁 PLAYERS WILL SEE</span><div style={{ whiteSpace: 'pre-wrap', color: '#8ab4d9' }}>{p.text.replace(/──\s*Session\s*#\d+\s*──\n?/g, '').trim()}</div></div>

      : <div key={i} style={{ padding: '3px 6px', margin: '2px 0', background: 'rgba(156,120,255,0.04)', borderLeft: '2px solid #c4a8ff25', borderRadius: 2 }}><span style={{ fontSize: '0.48rem', color: '#c4a8ff', fontWeight: 600, letterSpacing: 0.5 }}>🔒 DM ONLY</span><div style={{ whiteSpace: 'pre-wrap', color: '#c4a8ff', opacity: 0.6 }}>{p.text.replace(/──\s*Session\s*#\d+\s*──\n?/g, '').trim()}</div></div>

    );

  };

  // ============ END DM JOURNAL STATE ============

  

  // Wrapper: navigating to an entry pushes current to history

  const setQuickAddViewEntry = (entry) => {

    setQuickAddViewEntryRaw(prev => {

      if (prev && entry && prev.id !== entry?.id) {

        setQuickAddViewHistory(h => [prev, ...h].slice(0, 15));

      }

      return entry;

    });

  };

  const quickAddGoBack = () => {

    if (quickAddViewHistory.length === 0) return;

    const [prev, ...rest] = quickAddViewHistory;

    setQuickAddViewHistory(rest);

    setQuickAddViewEntryRaw(prev);

  };

  const [diceInput, setDiceInput] = useState('');

  const [diceResult, setDiceResult] = useState(null);

  const [showBatchApply, setShowBatchApply] = useState(false);

  const [batchApplySystem, setBatchApplySystem] = useState('');

  const [batchApplyTargetMode, setBatchApplyTargetMode] = useState(CAMPAIGN_CHARACTER_TARGET_MODE_PLAYERS);

  const [batchApplyTargets, setBatchApplyTargets] = useState(new Set());

  const [batchApplySlotValuesByChar, setBatchApplySlotValuesByChar] = useState({});

  const [showBatchRemove, setShowBatchRemove] = useState(false);

  const [batchRemoveSystem, setBatchRemoveSystem] = useState('');

  const [batchRemoveTargetMode, setBatchRemoveTargetMode] = useState(CAMPAIGN_CHARACTER_TARGET_MODE_PLAYERS);

  const [batchRemoveTargets, setBatchRemoveTargets] = useState(new Set());

  

  // Batch buff/debuff state

  const [showBatchBuff, setShowBatchBuff] = useState(false);

  const [batchBuffText, setBatchBuffText] = useState('');

  const [batchBuffIsDebuff, setBatchBuffIsDebuff] = useState(false);

  const [batchBuffIsPermanent, setBatchBuffIsPermanent] = useState(false);

  const [batchBuffTargetMode, setBatchBuffTargetMode] = useState(CAMPAIGN_CHARACTER_TARGET_MODE_PLAYERS);

  const [batchBuffTargets, setBatchBuffTargets] = useState(new Set());

  const [batchBuffColor, setBatchBuffColor] = useState('default-buff');

  const [batchBuffPlayerTooltip, setBatchBuffPlayerTooltip] = useState('');

  const [batchBuffSource, setBatchBuffSource] = useState('');

  const [batchBuffNoteVisible, setBatchBuffNoteVisible] = useState(false);

  const [batchBuffGlow, setBatchBuffGlow] = useState(true);

  

  // Whisper composer modal

  const [showWhisperComposer, setShowWhisperComposer] = useState(false);

  

  // Clear stale localStorage keys that caused permanent buff bug

  React.useEffect(() => {

    try {

      localStorage.removeItem('bmg-last-buff-permanent');

      localStorage.removeItem('bmg-dm-buff-permanent');

      localStorage.removeItem('bmg-dm-debuff-permanent');

    } catch {}

  }, []);

  

  // Shop system state

  // 2026-05-11 F1 fix: craftingOpen migrated from per-device localStorage
  // to globalSettings (cloud-backed). The original was a localStorage-
  // only flag, so the DM's "open to players" toggle never reached
  // player mobile devices — they saw crafting as unreachable even
  // though the DM had it open on desktop. Now derived from cloud
  // globalSettings; falls back to local for backwards compat on the
  // first load before the migration useEffect below has run.
  const craftingOpen = (() => {
    if (globalSettings && globalSettings.craftingOpen !== undefined) {
      return !!globalSettings.craftingOpen;
    }
    if (isCampaignSandboxSession) return false;
    try { return localStorage.getItem('bmg-crafting-open') === 'true'; } catch { return false; }
  })();

  const saveCraftingOpen = (val) => {
    // Write to cloud — this is the canonical source after the fix.
    // Local mirror kept for fast first-paint reads before cloud loads.
    if (typeof saveGlobalSettings === 'function') {
      saveGlobalSettings({ ...(globalSettings || {}), craftingOpen: !!val });
    }
    if (isCampaignSandboxSession) return;
    try { localStorage.setItem('bmg-crafting-open', String(!!val)); } catch {}
  };



  const SHOP_DATA_VERSION = 6;

  const [shopData, setShopData] = useState(() => {

    const defaultShopData = { ...DEFAULT_SHOP_DATA, _version: SHOP_DATA_VERSION };

    if (isCampaignSandboxSession) {

      const sandboxShopRuntime = sandboxStoredInitialState?.shopRuntime;

      if (sandboxShopRuntime && typeof sandboxShopRuntime === 'object') {

        return {
          ...sandboxShopRuntime,
          _source: 'local-sandbox-shop-runtime',
          _version: SHOP_DATA_VERSION,
        };

      }

      return {
        ...defaultShopData,
        _source: 'local-sandbox-shop-runtime',
      };

    }

    try { 

      const s = localStorage.getItem('bmg-shop-data'); 

      if (s) { const parsed = JSON.parse(s); if (parsed._version === SHOP_DATA_VERSION) return parsed; }

      return defaultShopData;

    } catch { return defaultShopData; }

  });

  const saveShopData = useCallback((data) => {

    const withVersion = {
      ...data,
      _version: SHOP_DATA_VERSION,
      ...(isCampaignSandboxSession ? { _source: 'local-sandbox-shop-runtime' } : {}),
    };

    setShopData(withVersion);

    if (isCampaignSandboxSession) {
      if (typeof updateGameData === 'function') {
        updateGameData({ shopRuntime: withVersion });
      }
      return;
    }

    try { localStorage.setItem('bmg-shop-data', JSON.stringify(withVersion)); } catch {}

  }, [isCampaignSandboxSession, updateGameData]);

  const sandboxShopRuntimeSeededRef = useRef(false);

  React.useEffect(() => {
    if (!isCampaignSandboxSession || sandboxShopRuntimeSeededRef.current) return;
    if (sandboxStoredInitialState?.shopRuntime) {
      sandboxShopRuntimeSeededRef.current = true;
      return;
    }
    sandboxShopRuntimeSeededRef.current = true;
    if (typeof updateGameData === 'function') {
      updateGameData({ shopRuntime: shopData });
    }
  }, [isCampaignSandboxSession, sandboxStoredInitialState, shopData, updateGameData]);

  const [serverShopUiState, setServerShopUiState] = useState({
    activeShopId: null,
    activeCampaign: null,
  });

  const hasConnectedAuthedRuntimeViewer = useMemo(() => (
    connectionStatus === 'connected'
    && useServerDerivedViewerQueries
    && (
      viewerContext?.viewerKind === 'dm'
      || (viewerContext?.viewerKind === 'player' && Boolean(viewerContext?.viewerCharId))
    )
  ), [connectionStatus, useServerDerivedViewerQueries, viewerContext]);

  const shopRuntimeAvailable = useMemo(() => (
    shopRuntime?._source === 'convex-shop-runtime'
    && Array.isArray(shopRuntime?.shops)
  ), [shopRuntime]);

  const shopRuntimePurchaseAuthorityEnabled = useMemo(() => (
    !isDM
    && shopRuntimeAvailable
    && shopRuntime.shops.length > 0
    && shopRuntime.shops.every((shop) => (
      (shop?.inventory || []).every((line) => line?.tradeOnly || Boolean(line?.listingId))
    ))
    && typeof purchaseShopListing === 'function'
    && typeof checkoutShopCart === 'function'
  ), [checkoutShopCart, isDM, purchaseShopListing, shopRuntime, shopRuntimeAvailable]);

  const shopRuntimeOfferAuthorityEnabled = useMemo(() => (
    shopRuntimeAvailable
    && shopRuntime.shops.length > 0
    && typeof submitTradeOffer === 'function'
    && typeof cancelTradeOffer === 'function'
  ), [cancelTradeOffer, shopRuntime, shopRuntimeAvailable, submitTradeOffer]);

  const shopRuntimeOfferSettleAuthorityEnabled = useMemo(() => (
    isDM
    && shopRuntimeOfferAuthorityEnabled
    && typeof settleTradeOfferForDm === 'function'
  ), [isDM, settleTradeOfferForDm, shopRuntimeOfferAuthorityEnabled]);

  const shopRuntimeOfferDenyAuthorityEnabled = useMemo(() => (
    isDM
    && shopRuntimeOfferAuthorityEnabled
    && typeof denyTradeOfferForDm === 'function'
  ), [denyTradeOfferForDm, isDM, shopRuntimeOfferAuthorityEnabled]);

  const shopRuntimeWalletAuthorityEnabled = useMemo(() => (
    isDM
    && shopRuntimeAvailable
    && typeof adjustShopWalletForDm === 'function'
  ), [adjustShopWalletForDm, isDM, shopRuntimeAvailable]);

  const shopRuntimeSellBackAuthorityEnabled = useMemo(() => (
    isDM
    && shopRuntimeAvailable
    && typeof sellInventoryItemToShopForDm === 'function'
  ), [isDM, sellInventoryItemToShopForDm, shopRuntimeAvailable]);

  const shopRuntimePromotionAuthorityEnabled = useMemo(() => (
    isDM
    && shopRuntimeAvailable
    && typeof promoteTradeReceiptLineToShopListingForDm === 'function'
  ), [isDM, promoteTradeReceiptLineToShopListingForDm, shopRuntimeAvailable]);

  const shopRuntimeManagementAuthorityEnabled = useMemo(() => (
    isDM
    && connectionStatus === 'connected'
    && typeof createDmShop === 'function'
    && typeof deleteDmShop === 'function'
    && typeof updateDmShop === 'function'
    && typeof createDmShopListing === 'function'
    && typeof deleteDmShopListing === 'function'
    && typeof updateDmShopListing === 'function'
    && typeof updateDmShopRuntimeState === 'function'
  ), [
    connectionStatus,
    createDmShop,
    createDmShopListing,
    deleteDmShop,
    deleteDmShopListing,
    isDM,
    updateDmShop,
    updateDmShopListing,
    updateDmShopRuntimeState,
  ]);

  const shopRuntimeAuthorityEnabled = shopRuntimePurchaseAuthorityEnabled
    || shopRuntimeManagementAuthorityEnabled
    || shopRuntimeOfferAuthorityEnabled
    || shopRuntimeWalletAuthorityEnabled
    || shopRuntimeSellBackAuthorityEnabled
    || shopRuntimePromotionAuthorityEnabled;

  const shopRuntimeReadAuthorityEnabled = shopRuntimeAvailable || hasConnectedAuthedRuntimeViewer;

  const combatItemAbilityAuthorityEnabled = useMemo(() => (
    connectionStatus === 'connected'
    && typeof activateCombatItemAbilityAuthority === 'function'
  ), [activateCombatItemAbilityAuthority, connectionStatus]);

  const activateCombatItemAbilityFromAuthority = useCallback(async (args = {}) => {
    if (!combatItemAbilityAuthorityEnabled) return null;
    return await activateCombatItemAbilityAuthority(args);
  }, [activateCombatItemAbilityAuthority, combatItemAbilityAuthorityEnabled]);

  const authRequiredItemMovementAuthorityEnabled = useMemo(() => (
    connectionStatus === 'connected'
    && !!viewerAuthToken
    && authMovementAuthorityEnabled === true
  ), [authMovementAuthorityEnabled, connectionStatus, viewerAuthToken]);

  const localSandboxAuthorityEnabled = useMemo(() => (
    isCampaignSandboxSession
    && connectionStatus === 'local'
  ), [connectionStatus, isCampaignSandboxSession]);

  const isSandboxCharacterRow = useCallback((candidate) => {
    if (!isCampaignSandboxSession) return true;
    if (!candidate || typeof candidate !== 'object') return false;
    const candidateId = String(candidate.id || candidate.charId || '').toLowerCase();
    const sandboxId = sandboxStorageContext?.sandboxId
      ? normalizeCampaignSandboxSeedId(sandboxStorageContext.sandboxId)
      : '';
    const sandboxPrefix = sandboxId ? `sandbox-${sandboxId}-` : 'sandbox-';
    const explicitSandboxId = typeof candidate.sandboxId === 'string'
      ? candidate.sandboxId.trim()
      : '';
    const hasSandboxMarker = candidate.isSandboxCharacter === true
      || candidate.campaignCharacterKind === 'sandbox'
      || candidateId.startsWith(sandboxPrefix);
    if (!hasSandboxMarker) return false;
    if (explicitSandboxId) {
      return !sandboxId || normalizeCampaignSandboxSeedId(explicitSandboxId) === sandboxId;
    }
    return candidateId.startsWith(sandboxPrefix);
  }, [isCampaignSandboxSession, sandboxStorageContext]);

  const sandboxEligibleChars = useMemo(() => {
    if (!isCampaignSandboxSession) return chars || {};
    return Object.fromEntries(
      Object.entries(chars || {}).filter(([, candidate]) => isSandboxCharacterRow(candidate))
    );
  }, [chars, isCampaignSandboxSession, isSandboxCharacterRow]);

  const livePlayableChars = useMemo(() => {
    if (isCampaignSandboxSession) return sandboxEligibleChars;
    return Object.fromEntries(
      Object.entries(chars || {}).filter(([, candidate]) => !isCampaignTestCharacter(candidate))
    );
  }, [chars, isCampaignSandboxSession, sandboxEligibleChars]);

  const auditSnapshotChars = useMemo(() => (
    isCampaignSandboxSession ? sandboxEligibleChars : livePlayableChars
  ), [isCampaignSandboxSession, livePlayableChars, sandboxEligibleChars]);

  const sanitizeCampaignSandboxCombat = useCallback((candidateCombat) => {
    if (!isCampaignSandboxSession || !candidateCombat || typeof candidateCombat !== 'object') {
      return candidateCombat;
    }
    const allowedCharIds = new Set(Object.keys(sandboxEligibleChars || {}));
    const stampEnemy = (combatant) => ({
      ...combatant,
      isSandboxEnemy: true,
      runtimeScope: RUNTIME_SCOPE_CAMPAIGN_SANDBOX,
      sandboxId: sandboxStorageContext?.sandboxId || 'default',
    });
    const keepCombatant = (combatant) => {
      if (!combatant || typeof combatant !== 'object') return false;
      const type = String(combatant.type || '').toLowerCase();
      if (type === 'enemy') return true;
      return allowedCharIds.has(String(combatant.id || ''));
    };
    const combatants = Array.isArray(candidateCombat.combatants)
      ? candidateCombat.combatants
        .filter(keepCombatant)
        .map((combatant) => String(combatant.type || '').toLowerCase() === 'enemy' ? stampEnemy(combatant) : combatant)
      : [];
    const turnIndex = combatants.length > 0
      ? Math.min(Math.max(0, Number(candidateCombat.turnIndex || 0)), combatants.length - 1)
      : 0;
    return {
      ...candidateCombat,
      combatants,
      turnIndex,
      enemies: Array.isArray(candidateCombat.enemies)
        ? candidateCombat.enemies.map(stampEnemy)
        : candidateCombat.enemies,
    };
  }, [isCampaignSandboxSession, sandboxEligibleChars, sandboxStorageContext]);

  const displayedCombat = useMemo(
    () => sanitizeCampaignSandboxCombat(combat),
    [combat, sanitizeCampaignSandboxCombat]
  );

  const displayedCombatSnapshots = useMemo(() => {
    if (!isCampaignSandboxSession || !Array.isArray(combatSnapshots)) {
      return combatSnapshots;
    }
    const sanitizeSnapshotChars = (snapshotChars) => {
      if (!snapshotChars || typeof snapshotChars !== 'object') return snapshotChars;
      return Object.fromEntries(
        Object.entries(snapshotChars).filter(([id, candidate]) => (
          Object.prototype.hasOwnProperty.call(sandboxEligibleChars || {}, id)
          || isSandboxCharacterRow(candidate)
        ))
      );
    };
    return combatSnapshots.map((snapshot) => {
      if (!snapshot?.state || typeof snapshot.state !== 'object') return snapshot;
      return {
        ...snapshot,
        state: {
          ...snapshot.state,
          combat: sanitizeCampaignSandboxCombat(snapshot.state.combat),
          chars: sanitizeSnapshotChars(snapshot.state.chars),
        },
      };
    });
  }, [combatSnapshots, isCampaignSandboxSession, isSandboxCharacterRow, sandboxEligibleChars, sanitizeCampaignSandboxCombat]);

  const itemMovementAuthorityEnabled = useMemo(() => (
    authRequiredItemMovementAuthorityEnabled || localSandboxAuthorityEnabled
  ), [authRequiredItemMovementAuthorityEnabled, localSandboxAuthorityEnabled]);

  const movementAuthorityAuthBlocked = useMemo(() => (
    connectionStatus === 'connected'
    && !!viewerAuthToken
    && authMovementAuthorityEnabled === false
  ), [authMovementAuthorityEnabled, connectionStatus, viewerAuthToken]);

  const characterVitalsAuthorityEnabled = useMemo(() => (
    (
      (connectionStatus === 'connected' && !!viewerAuthToken)
      || localSandboxAuthorityEnabled
    )
    && typeof patchCharacterVitals === 'function'
  ), [connectionStatus, localSandboxAuthorityEnabled, patchCharacterVitals, viewerAuthToken]);

  const patchCharacterVitalsFromAuthority = useCallback(async (args = {}) => {
    if (!characterVitalsAuthorityEnabled) return null;
    return await patchCharacterVitals(args);
  }, [characterVitalsAuthorityEnabled, patchCharacterVitals]);

  const systemInstancePatchAuthorityEnabled = useMemo(() => (
    (
      (connectionStatus === 'connected' && !!viewerAuthToken)
      || localSandboxAuthorityEnabled
    )
    && typeof patchCharacterSystemInstance === 'function'
  ), [connectionStatus, localSandboxAuthorityEnabled, patchCharacterSystemInstance, viewerAuthToken]);

  const patchCharacterSystemInstanceFromAuthority = useCallback(async (args = {}) => {
    if (!systemInstancePatchAuthorityEnabled) return null;
    return await patchCharacterSystemInstance(args);
  }, [patchCharacterSystemInstance, systemInstancePatchAuthorityEnabled]);

  const systemStatePatchAuthorityEnabled = useMemo(() => (
    (
      (connectionStatus === 'connected' && !!viewerAuthToken)
      || localSandboxAuthorityEnabled
    )
    && typeof patchCharacterSystemState === 'function'
  ), [connectionStatus, localSandboxAuthorityEnabled, patchCharacterSystemState, viewerAuthToken]);

  const patchCharacterSystemStateFromAuthority = useCallback(async (args = {}) => {
    if (!systemStatePatchAuthorityEnabled) return null;
    return await patchCharacterSystemState(args);
  }, [patchCharacterSystemState, systemStatePatchAuthorityEnabled]);

  const systemStateAndVitalsPatchAuthorityEnabled = useMemo(() => (
    (
      (connectionStatus === 'connected' && !!viewerAuthToken)
      || localSandboxAuthorityEnabled
    )
    && typeof patchCharacterSystemStateAndVitals === 'function'
  ), [connectionStatus, localSandboxAuthorityEnabled, patchCharacterSystemStateAndVitals, viewerAuthToken]);

  const patchCharacterSystemStateAndVitalsFromAuthority = useCallback(async (args = {}) => {
    if (!systemStateAndVitalsPatchAuthorityEnabled) return null;
    return await patchCharacterSystemStateAndVitals(args);
  }, [patchCharacterSystemStateAndVitals, systemStateAndVitalsPatchAuthorityEnabled]);

  const systemStateAndResourcesPatchAuthorityEnabled = useMemo(() => (
    (
      (connectionStatus === 'connected' && !!viewerAuthToken)
      || localSandboxAuthorityEnabled
    )
    && typeof patchCharacterSystemStateAndResources === 'function'
  ), [connectionStatus, localSandboxAuthorityEnabled, patchCharacterSystemStateAndResources, viewerAuthToken]);

  const patchCharacterSystemStateAndResourcesFromAuthority = useCallback(async (args = {}) => {
    if (!systemStateAndResourcesPatchAuthorityEnabled) return null;
    return await patchCharacterSystemStateAndResources(args);
  }, [patchCharacterSystemStateAndResources, systemStateAndResourcesPatchAuthorityEnabled]);

  const systemStateAndEffectsPatchAuthorityEnabled = useMemo(() => (
    (
      (connectionStatus === 'connected' && !!viewerAuthToken)
      || localSandboxAuthorityEnabled
    )
    && typeof patchCharacterSystemStateAndEffects === 'function'
  ), [connectionStatus, localSandboxAuthorityEnabled, patchCharacterSystemStateAndEffects, viewerAuthToken]);

  const patchCharacterSystemStateAndEffectsFromAuthority = useCallback(async (args = {}) => {
    if (!systemStateAndEffectsPatchAuthorityEnabled) return null;
    return await patchCharacterSystemStateAndEffects(args);
  }, [patchCharacterSystemStateAndEffects, systemStateAndEffectsPatchAuthorityEnabled]);

  const patchBatchSystemStateFromAuthority = useCallback((beforeCharsById = {}, nextCharsById = {}, audit = {}) => {
    const entries = Object.entries(beforeCharsById)
      .map(([charId, beforeChar]) => {
        const nextChar = nextCharsById?.[charId];
        if (!nextChar) return null;
        const systemPayload = buildSystemStateAuthorityPayload(beforeChar, nextChar);
        if (systemPayload && systemStatePatchAuthorityEnabled) {
          return {
            write: () => patchCharacterSystemStateFromAuthority({
              charId,
              ...systemPayload,
              audit,
            }),
          };
        }
        const systemVitalsPayload = buildSystemStateAndVitalsAuthorityPayload(beforeChar, nextChar);
        if (systemVitalsPayload && systemStateAndVitalsPatchAuthorityEnabled) {
          return {
            write: () => patchCharacterSystemStateAndVitalsFromAuthority({
              charId,
              ...systemVitalsPayload,
              audit,
            }),
          };
        }
        const systemResourcesPayload = buildSystemStateAndResourcesAuthorityPayload(beforeChar, nextChar);
        if (systemResourcesPayload && systemStateAndResourcesPatchAuthorityEnabled) {
          return {
            write: () => patchCharacterSystemStateAndResourcesFromAuthority({
              charId,
              ...systemResourcesPayload,
              audit,
            }),
          };
        }
        const systemEffectsPayload = buildSystemStateAndEffectsAuthorityPayload(beforeChar, nextChar);
        if (systemEffectsPayload && systemStateAndEffectsPatchAuthorityEnabled) {
          return {
            write: () => patchCharacterSystemStateAndEffectsFromAuthority({
              charId,
              ...systemEffectsPayload,
              audit,
            }),
          };
        }
        return null;
      });
    if (entries.length === 0 || entries.some(entry => !entry)) return false;

    Promise.all(entries.map(entry => entry.write())).catch(err => {
      console.error('Failed to patch batch character system state:', err);
      setDmNotification(`Batch system update failed: ${err?.message || 'unknown error'}`);
    });
    return true;
  }, [
    patchCharacterSystemStateAndEffectsFromAuthority,
    patchCharacterSystemStateAndResourcesFromAuthority,
    patchCharacterSystemStateAndVitalsFromAuthority,
    patchCharacterSystemStateFromAuthority,
    systemStateAndEffectsPatchAuthorityEnabled,
    systemStateAndResourcesPatchAuthorityEnabled,
    systemStateAndVitalsPatchAuthorityEnabled,
    systemStatePatchAuthorityEnabled,
  ]);

  const handleDmSystemRuleSaveResolve = (outcome) => {
    if (!activeDmSystemRuleSaveEntry) return;
    const [head, ...rest] = dmSystemRuleSaveQueue;
    const context = head.context || {};
    const queuedChar = context.char || (head.pendingSave?.characterId ? chars?.[head.pendingSave.characterId] : null);
    if (!queuedChar?.id) {
      setDmSystemRuleSaveQueue(rest);
      return;
    }
    const subResults = resolvePendingSave(head.pendingSave, outcome, context);
    const instanceKey = context.systemInstance?.id || context.systemInstance?.systemId || null;
    const nextChar = instanceKey
      ? applyRuleDispatchToChar(queuedChar, instanceKey, subResults, systems, context.worldContext || null)
      : { ...queuedChar, ...applySystemRuleResults(queuedChar, subResults, systems, context.worldContext || null) };
    const nextSystemInstance = instanceKey
      ? (nextChar.systemList || []).find(entry => (entry.id || entry.systemId) === instanceKey) || context.systemInstance
      : context.systemInstance || null;
    const nestedSaveEntries = buildSystemRulePendingSaveQueueEntries(subResults.pendingSaves, {
      char: nextChar,
      systemInstance: nextSystemInstance,
      systemTemplate: context.systemTemplate,
      trackerValues: nextSystemInstance?.trackerValues || context.trackerValues || {},
      triggerContext: context.triggerContext,
      worldContext: context.worldContext,
      label: `${head.label || 'System rule'} follow-up`,
      source: `${head.source || 'systems'}.save`,
    });

    const sideEffects = collectRuleResultSideEffects(subResults, { npcLibrary });
    if (sideEffects.npcLibraryChanged && typeof saveNpcLibrary === 'function') {
      saveNpcLibrary(sideEffects.nextNpcLibrary);
    }
    persistSystemRuleStatusMessages(sideEffects.statusMessages, {
      char: nextChar,
      source: `${head.source || 'systems'}.save`,
      messages,
      onSendStatusMessage: handleSendStatusMessage,
      onSaveMessages: saveMessages,
      auditMeta: { category: 'systems', action: 'system.rule-save-status' },
    });
    const ruleLogEntries = buildSystemRuleTravelLogEntries(sideEffects.logEntries, {
      char: nextChar,
      action: 'system-rule-log',
      source: `${head.source || 'systems'}.save`,
    });
    if (ruleLogEntries.length > 0 && typeof saveGroupTravelLog === 'function') {
      const keep = Math.max(0, 200 - ruleLogEntries.length);
      saveGroupTravelLog([...ruleLogEntries, ...(groupTravelLog || []).slice(0, keep)]);
    }

    const notices = [...(sideEffects.notifications || [])];
    if (nestedSaveEntries.length > 0) {
      const nestedNotice = buildSystemRulePendingSaveNotice(subResults.pendingSaves, `${head.label || 'System rule'} follow-up`);
      notices.push(`${nestedNotice || `${head.label || 'System rule'} requested ${nestedSaveEntries.length} follow-up saving throw${nestedSaveEntries.length === 1 ? '' : 's'}.`} Queued for resolution.`);
    }
    if (ruleLogEntries.length > 0) {
      notices.push(`${ruleLogEntries.length} system rule log entr${ruleLogEntries.length === 1 ? 'y' : 'ies'} recorded.`);
    }
    if (notices.length > 0) setDmNotification(notices.join(' | '));

    const audit = {
      summary: `${head.label || 'System rule'} save ${outcome}: ${head.pendingSave?.reason || queuedChar.name}`,
      category: 'systems',
      action: `system.rule-save.${outcome}`,
    };
    if (nextChar !== queuedChar) {
      if (!patchBatchSystemStateFromAuthority({ [queuedChar.id]: cloneCharForSystemAuthority(queuedChar) }, { [queuedChar.id]: nextChar }, audit)) {
        saveChars({ ...chars, [queuedChar.id]: nextChar }, { perCharMeta: { [queuedChar.id]: audit } });
      }
    }

    setDmSystemRuleSaveQueue([...nestedSaveEntries, ...rest]);
  };

  const handleDmSystemRuleSaveSkip = useCallback(() => {
    setDmSystemRuleSaveQueue(prev => prev.slice(1));
  }, []);

  const rejectUnlinkedMovementAuthority = useCallback(async () => {
    throw new Error('not authorized: your sign-in is not linked to this character yet');
  }, []);

  const inventoryCharTransferAuthorityEnabled = useMemo(() => (
    itemMovementAuthorityEnabled
    && typeof transferInventoryItemBetweenChars === 'function'
  ), [itemMovementAuthorityEnabled, transferInventoryItemBetweenChars]);

  const transferInventoryItemBetweenCharsFromAuthority = useCallback(async (args = {}) => {
    if (!inventoryCharTransferAuthorityEnabled) return null;
    return await transferInventoryItemBetweenChars(args);
  }, [inventoryCharTransferAuthorityEnabled, transferInventoryItemBetweenChars]);

  const partyInventoryWholeBlobFallback = useMemo(() => (
    connectionStatus === 'local'
      && typeof savePartyInventory === 'function'
      ? savePartyInventory
      : null
  ), [connectionStatus, savePartyInventory]);

  const craftingTableTransferAuthorityEnabled = useMemo(() => (
    itemMovementAuthorityEnabled
    && typeof moveCharItemToCraftingTable === 'function'
  ), [itemMovementAuthorityEnabled, moveCharItemToCraftingTable]);

  const moveCharItemToCraftingTableFromAuthority = useCallback(async (args = {}) => {
    if (!craftingTableTransferAuthorityEnabled) return null;
    return await moveCharItemToCraftingTable(args);
  }, [craftingTableTransferAuthorityEnabled, moveCharItemToCraftingTable]);

  const partyInventoryMoveAuthorityEnabled = useMemo(() => (
    itemMovementAuthorityEnabled
    && typeof depositPartyInventoryFromChar === 'function'
    && typeof takePartyInventoryToChar === 'function'
  ), [depositPartyInventoryFromChar, itemMovementAuthorityEnabled, takePartyInventoryToChar]);

  const depositPartyInventoryFromCharFromAuthority = useCallback(async (args = {}) => {
    if (!partyInventoryMoveAuthorityEnabled) return null;
    return await depositPartyInventoryFromChar(args);
  }, [depositPartyInventoryFromChar, partyInventoryMoveAuthorityEnabled]);

  const takePartyInventoryToCharFromAuthority = useCallback(async (args = {}) => {
    if (!partyInventoryMoveAuthorityEnabled) return null;
    return await takePartyInventoryToChar(args);
  }, [partyInventoryMoveAuthorityEnabled, takePartyInventoryToChar]);

  const tableRowToCharAuthorityEnabled = useMemo(() => (
    itemMovementAuthorityEnabled
    && typeof tableRowToChar === 'function'
  ), [itemMovementAuthorityEnabled, tableRowToChar]);

  const craftOutputToCharAuthorityEnabled = useMemo(() => (
    itemMovementAuthorityEnabled
    && typeof craftOutputToChar === 'function'
  ), [craftOutputToChar, itemMovementAuthorityEnabled]);

  const tableRowToCharFromAuthority = useCallback(async (args = {}) => {
    if (!tableRowToCharAuthorityEnabled) return null;
    return await tableRowToChar(args);
  }, [tableRowToChar, tableRowToCharAuthorityEnabled]);

  const craftOutputToCharFromAuthority = useCallback(async (args = {}) => {
    if (!craftOutputToCharAuthorityEnabled) return null;
    return await craftOutputToChar(args);
  }, [craftOutputToChar, craftOutputToCharAuthorityEnabled]);

  const tableRowToPartyAuthorityEnabled = useMemo(() => (
    itemMovementAuthorityEnabled
    && typeof tableRowToParty === 'function'
  ), [itemMovementAuthorityEnabled, tableRowToParty]);

  const craftOutputToPartyAuthorityEnabled = useMemo(() => (
    itemMovementAuthorityEnabled
    && typeof craftOutputToParty === 'function'
  ), [craftOutputToParty, itemMovementAuthorityEnabled]);

  const tableRowToPartyFromAuthority = useCallback(async (args = {}) => {
    if (!tableRowToPartyAuthorityEnabled) return null;
    return await tableRowToParty(args);
  }, [tableRowToParty, tableRowToPartyAuthorityEnabled]);

  const craftOutputToPartyFromAuthority = useCallback(async (args = {}) => {
    if (!craftOutputToPartyAuthorityEnabled) return null;
    return await craftOutputToParty(args);
  }, [craftOutputToParty, craftOutputToPartyAuthorityEnabled]);

  const inventoryCrudAuthorityEnabled = useMemo(() => (
    itemMovementAuthorityEnabled
    && typeof addInventoryItem === 'function'
    && typeof updateInventoryItem === 'function'
    && typeof removeInventoryItem === 'function'
    && typeof setInventoryItemFavorite === 'function'
  ), [addInventoryItem, itemMovementAuthorityEnabled, removeInventoryItem, setInventoryItemFavorite, updateInventoryItem]);

  const addInventoryItemFromAuthority = useCallback(async (args = {}) => {
    if (!inventoryCrudAuthorityEnabled) return null;
    return await addInventoryItem(args);
  }, [addInventoryItem, inventoryCrudAuthorityEnabled]);

  const updateInventoryItemFromAuthority = useCallback(async (args = {}) => {
    if (!inventoryCrudAuthorityEnabled) return null;
    return await updateInventoryItem(args);
  }, [inventoryCrudAuthorityEnabled, updateInventoryItem]);

  const removeInventoryItemFromAuthority = useCallback(async (args = {}) => {
    if (!inventoryCrudAuthorityEnabled) return null;
    return await removeInventoryItem(args);
  }, [inventoryCrudAuthorityEnabled, removeInventoryItem]);

  const setInventoryItemFavoriteFromAuthority = useCallback(async (args = {}) => {
    if (!inventoryCrudAuthorityEnabled) return null;
    return await setInventoryItemFavorite(args);
  }, [inventoryCrudAuthorityEnabled, setInventoryItemFavorite]);

  const inventoryEquipToggleAuthorityEnabled = useMemo(() => (
    itemMovementAuthorityEnabled
    && typeof toggleInventoryItemEquipped === 'function'
  ), [itemMovementAuthorityEnabled, toggleInventoryItemEquipped]);

  const toggleInventoryItemEquippedFromAuthority = useCallback(async (args = {}) => {
    if (!inventoryEquipToggleAuthorityEnabled) return null;
    return await toggleInventoryItemEquipped(args);
  }, [inventoryEquipToggleAuthorityEnabled, toggleInventoryItemEquipped]);

  const inventoryConsumeAuthorityEnabled = useMemo(() => (
    itemMovementAuthorityEnabled
    && typeof consumeInventoryItem === 'function'
  ), [consumeInventoryItem, itemMovementAuthorityEnabled]);

  const consumeInventoryItemFromAuthority = useCallback(async (args = {}) => {
    if (!inventoryConsumeAuthorityEnabled) return null;
    return await consumeInventoryItem(args);
  }, [consumeInventoryItem, inventoryConsumeAuthorityEnabled]);

  const inventoryAbilityAuthorityEnabled = useMemo(() => (
    itemMovementAuthorityEnabled
    && typeof activateInventoryItemAbility === 'function'
  ), [activateInventoryItemAbility, itemMovementAuthorityEnabled]);

  const activateInventoryItemAbilityFromAuthority = useCallback(async (args = {}) => {
    if (!inventoryAbilityAuthorityEnabled) return null;
    return await activateInventoryItemAbility(args);
  }, [activateInventoryItemAbility, inventoryAbilityAuthorityEnabled]);

  const effectInstanceLifecycleAuthorityEnabled = useMemo(() => (
    connectionStatus === 'connected'
    && typeof applyEffectInstanceLifecycleAuthority === 'function'
  ), [applyEffectInstanceLifecycleAuthority, connectionStatus]);

  const applyEffectInstanceLifecycleFromAuthority = useCallback(async (args = {}) => {
    if (!effectInstanceLifecycleAuthorityEnabled) return null;
    const result = await applyEffectInstanceLifecycleAuthority(args);
    if (!result) throw new Error('effect instance lifecycle authority unavailable');
    return result;
  }, [applyEffectInstanceLifecycleAuthority, effectInstanceLifecycleAuthorityEnabled]);

  const itemAbilityEffectInstanceReconcileAuthorityEnabled = useMemo(() => (
    isDM
    &&
    connectionStatus === 'connected'
    && typeof reconcileItemAbilityEffectInstancesForChar === 'function'
  ), [connectionStatus, isDM, reconcileItemAbilityEffectInstancesForChar]);

  const reconcileItemAbilityEffectInstancesFromAuthority = useCallback(async (args = {}) => {
    if (!itemAbilityEffectInstanceReconcileAuthorityEnabled) return null;
    const result = await reconcileItemAbilityEffectInstancesForChar(args);
    if (!result) throw new Error('item-ability effect-instance reconcile authority unavailable');
    return result;
  }, [
    itemAbilityEffectInstanceReconcileAuthorityEnabled,
    reconcileItemAbilityEffectInstancesForChar,
  ]);

  React.useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (typeof window === 'undefined') return;

    const canExposeItemAbilityDebugHelpers = isDM
      && viewerContext?.viewerKind === 'dm'
      && itemAbilityEffectInstanceReconcileAuthorityEnabled;

    if (!canExposeItemAbilityDebugHelpers) {
      delete window.__reconcileItemAbilityEffectsForChar__;
      delete window.__installItemAbilityDebugHelpers__;
      delete window.__itemAbilityDebugHelpers__;
      return;
    }

    const INSTALL_ITEM_ABILITY_DEBUG_HELPERS_CONFIRMATION = 'INSTALL_ITEM_ABILITY_DEBUG_HELPERS';
    const ITEM_ABILITY_DEBUG_CONFIRMATIONS = Object.freeze({
      reconcileEffectsForChar: 'RECONCILE_ITEM_ABILITY_EFFECTS_FOR_CHAR',
    });
    const requireItemAbilityDebugConfirmation = (received, expected, helperName) => {
      if (received === expected) return;
      throw new Error(`${helperName}: pass { confirm: "${expected}" } to run this item-ability diagnostic helper.`);
    };

    window.__installItemAbilityDebugHelpers__ = ({ confirm } = {}) => {
      requireItemAbilityDebugConfirmation(
        confirm,
        INSTALL_ITEM_ABILITY_DEBUG_HELPERS_CONFIRMATION,
        '__installItemAbilityDebugHelpers__'
      );
      const helpers = {
        confirmations: ITEM_ABILITY_DEBUG_CONFIRMATIONS,
        reconcileEffectsForChar: async (targetCharId = charId, options = {}) => {
          const resolvedCharId = typeof targetCharId === 'string' && targetCharId.trim()
            ? targetCharId.trim()
            : null;
          if (!resolvedCharId) {
            console.error('A character id is required. Usage: await window.__itemAbilityDebugHelpers__.reconcileEffectsForChar(charId, { dryRun: true })');
            return null;
          }
          if (!itemAbilityEffectInstanceReconcileAuthorityEnabled) {
            console.error('Item-ability effect-instance reconcile authority is unavailable on the active storage backend.');
            return null;
          }

          const dryRun = options?.dryRun !== false;
          if (!dryRun) {
            requireItemAbilityDebugConfirmation(
              options?.confirm,
              ITEM_ABILITY_DEBUG_CONFIRMATIONS.reconcileEffectsForChar,
              'reconcileEffectsForChar'
            );
          }
          return await reconcileItemAbilityEffectInstancesFromAuthority({
            charId: resolvedCharId,
            dryRun,
            confirm: dryRun ? undefined : ITEM_ABILITY_DEBUG_CONFIRMATIONS.reconcileEffectsForChar,
          });
        },
      };
      window.__itemAbilityDebugHelpers__ = helpers;
      delete window.__reconcileItemAbilityEffectsForChar__;
      console.warn('Installed item-ability diagnostics under window.__itemAbilityDebugHelpers__. Non-dry-run reconcile requires per-call confirmation.');
      return helpers;
    };
    delete window.__reconcileItemAbilityEffectsForChar__;

    return () => {
      delete window.__reconcileItemAbilityEffectsForChar__;
      delete window.__installItemAbilityDebugHelpers__;
      delete window.__itemAbilityDebugHelpers__;
    };
  }, [
    charId,
    isDM,
    itemAbilityEffectInstanceReconcileAuthorityEnabled,
    reconcileItemAbilityEffectInstancesFromAuthority,
    viewerContext,
  ]);

  const isCombatItemAbilityAuthoritySupported = useCallback((shape = {}) => {
    return isCombatItemAbilityAuthorityShapeSupported({
      authorityEnabled: combatItemAbilityAuthorityEnabled,
      shape,
    });
  }, [combatItemAbilityAuthorityEnabled]);

  const displayedShopData = useMemo(() => {
    if (!shopRuntimeReadAuthorityEnabled) return shopData;
    const authoritativeShopRuntime = shopRuntime && typeof shopRuntime === 'object' ? shopRuntime : {};
    const serverShops = Array.isArray(authoritativeShopRuntime?.shops) ? authoritativeShopRuntime.shops : [];
    const serverCampaigns = Array.isArray(authoritativeShopRuntime?.campaigns) ? authoritativeShopRuntime.campaigns : [];
    const serverOffers = Array.isArray(authoritativeShopRuntime?.offers) ? authoritativeShopRuntime.offers : [];
    const serverTransactionLog = Array.isArray(authoritativeShopRuntime?.transactionLog) ? authoritativeShopRuntime.transactionLog : [];
    const uiActiveShopId = serverShopUiState.activeShopId;
    const uiActiveCampaign = serverShopUiState.activeCampaign;
    const activeShopId = uiActiveShopId && serverShops.some((shop) => shop.id === uiActiveShopId)
      ? uiActiveShopId
      : (authoritativeShopRuntime?.activeShopId || null);
    const activeCampaign = uiActiveCampaign === 'all' || serverCampaigns.includes(uiActiveCampaign)
      ? uiActiveCampaign
      : (authoritativeShopRuntime?.activeCampaign || 'all');

    return {
      ...shopData,
      ...authoritativeShopRuntime,
      _source: authoritativeShopRuntime?._source || 'convex-shop-runtime',
      shops: serverShops,
      campaigns: serverCampaigns,
      offers: serverOffers,
      transactionLog: serverTransactionLog,
      itemTemplates: authoritativeShopRuntime?.itemTemplates || {},
      activeShopId,
      activeCampaign,
      _version: SHOP_DATA_VERSION,
    };
  }, [serverShopUiState, shopData, shopRuntime, shopRuntimeReadAuthorityEnabled]);

  const saveDisplayedShopData = useCallback((data) => {
    if (!shopRuntimeReadAuthorityEnabled) {
      saveShopData(data);
      return;
    }

    const nextData = data && typeof data === 'object' ? data : {};

    setServerShopUiState((prev) => {
      const serverShops = Array.isArray(shopRuntime?.shops) ? shopRuntime.shops : [];
      const serverCampaigns = Array.isArray(shopRuntime?.campaigns) ? shopRuntime.campaigns : [];
      const activeShopId = nextData.activeShopId && serverShops.some((shop) => shop.id === nextData.activeShopId)
        ? nextData.activeShopId
        : prev.activeShopId;
      const activeCampaign = nextData.activeCampaign === 'all' || serverCampaigns.includes(nextData.activeCampaign)
        ? nextData.activeCampaign
        : prev.activeCampaign;

      if (activeShopId === prev.activeShopId && activeCampaign === prev.activeCampaign) return prev;

      return {
        activeShopId,
        activeCampaign,
      };
    });

    if (!shopRuntimeManagementAuthorityEnabled) return;

    syncServerShopManagementChanges({
      previousData: displayedShopData,
      nextData,
      createDmShop,
      deleteDmShop,
      updateDmShop,
      createDmShopListing,
      deleteDmShopListing,
      updateDmShopListing,
      updateDmShopRuntimeState,
    }).catch((error) => {
      console.error('[shop] authoritative DM shop management failed', error);
    });
  }, [
    createDmShop,
    createDmShopListing,
    deleteDmShop,
    deleteDmShopListing,
    displayedShopData,
    saveShopData,
    shopRuntime,
    shopRuntimeManagementAuthorityEnabled,
    shopRuntimeReadAuthorityEnabled,
    updateDmShop,
    updateDmShopListing,
    updateDmShopRuntimeState,
  ]);

  const purchaseDisplayedShopListing = useCallback(async (args) => {
    if (!shopRuntimePurchaseAuthorityEnabled) return null;
    const result = await purchaseShopListing(args);
    if (result === null || result === undefined) {
      throw new Error('shop purchase authority unavailable');
    }
    return result;
  }, [purchaseShopListing, shopRuntimePurchaseAuthorityEnabled]);

  const checkoutDisplayedShopCart = useCallback(async (args) => {
    if (!shopRuntimePurchaseAuthorityEnabled) return null;
    const result = await checkoutShopCart(args);
    if (result === null || result === undefined) {
      throw new Error('shop checkout authority unavailable');
    }
    return result;
  }, [checkoutShopCart, shopRuntimePurchaseAuthorityEnabled]);

  const submitDisplayedTradeOffer = useCallback(async (args) => {
    if (!shopRuntimeOfferAuthorityEnabled) return null;
    const result = await submitTradeOffer(args);
    if (result === null || result === undefined) {
      throw new Error('shop trade-offer authority unavailable');
    }
    return result;
  }, [shopRuntimeOfferAuthorityEnabled, submitTradeOffer]);

  const cancelDisplayedTradeOffer = useCallback(async (args) => {
    if (!shopRuntimeOfferAuthorityEnabled) return null;
    const result = await cancelTradeOffer(args);
    if (result === null || result === undefined) {
      throw new Error('shop trade-offer cancel authority unavailable');
    }
    return result;
  }, [cancelTradeOffer, shopRuntimeOfferAuthorityEnabled]);

  const denyDisplayedTradeOfferForDm = useCallback(async (args) => {
    if (!shopRuntimeOfferDenyAuthorityEnabled) return null;
    const result = await denyTradeOfferForDm(args);
    if (result === null || result === undefined) {
      throw new Error('shop trade-offer denial authority unavailable');
    }
    return result;
  }, [denyTradeOfferForDm, shopRuntimeOfferDenyAuthorityEnabled]);

  const settleDisplayedTradeOfferForDm = useCallback(async (args) => {
    if (!shopRuntimeOfferSettleAuthorityEnabled) return null;
    const result = await settleTradeOfferForDm(args);
    if (result === null || result === undefined) {
      throw new Error('shop trade-offer settlement authority unavailable');
    }
    return result;
  }, [settleTradeOfferForDm, shopRuntimeOfferSettleAuthorityEnabled]);

  const promoteDisplayedTradeReceiptLineToShopListingForDm = useCallback(async (args) => {
    if (!shopRuntimePromotionAuthorityEnabled) return null;
    const result = await promoteTradeReceiptLineToShopListingForDm(args);
    if (result === null || result === undefined) {
      throw new Error('shop trade resale promotion authority unavailable');
    }
    return result;
  }, [promoteTradeReceiptLineToShopListingForDm, shopRuntimePromotionAuthorityEnabled]);

  const adjustDisplayedShopWalletForDm = useCallback(async (args) => {
    if (!shopRuntimeWalletAuthorityEnabled) return null;
    const result = await adjustShopWalletForDm(args);
    if (result === null || result === undefined) {
      throw new Error('shop wallet authority unavailable');
    }
    return result;
  }, [adjustShopWalletForDm, shopRuntimeWalletAuthorityEnabled]);

  const sellDisplayedInventoryItemToShopForDm = useCallback(async (args) => {
    if (!shopRuntimeSellBackAuthorityEnabled) return null;
    const result = await sellInventoryItemToShopForDm(args);
    if (result === null || result === undefined) {
      throw new Error('shop sell-back authority unavailable');
    }
    return result;
  }, [sellInventoryItemToShopForDm, shopRuntimeSellBackAuthorityEnabled]);

  React.useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (typeof window === 'undefined') return;
    const canExposeImportHelper = isDM
      && viewerContext?.viewerKind === 'dm'
      && connectionStatus === 'connected'
      && typeof importShopDataForDm === 'function';
    if (!canExposeImportHelper) {
      delete window.__importLocalShopDataToConvex__;
      delete window.__inspectConvexShopRuntime__;
      delete window.__installShopRuntimeDebugHelpers__;
      delete window.__shopRuntimeDebugHelpers__;
      return;
    }

    const INSTALL_SHOP_RUNTIME_DEBUG_HELPERS_CONFIRMATION = 'INSTALL_SHOP_RUNTIME_DEBUG_HELPERS';
    const SHOP_RUNTIME_DEBUG_CONFIRMATIONS = Object.freeze({
      importLocalShopDataToConvex: 'IMPORT_LOCAL_SHOP_RUNTIME_FROM_BROWSER',
    });
    const requireShopRuntimeDebugConfirmation = (received, expected, helperName) => {
      if (received === expected) return;
      throw new Error(`${helperName}: pass { confirm: "${expected}" } to run this shop-runtime diagnostic helper.`);
    };

    window.__installShopRuntimeDebugHelpers__ = ({ confirm } = {}) => {
      requireShopRuntimeDebugConfirmation(
        confirm,
        INSTALL_SHOP_RUNTIME_DEBUG_HELPERS_CONFIRMATION,
        '__installShopRuntimeDebugHelpers__'
      );
      const helpers = {
        confirmations: SHOP_RUNTIME_DEBUG_CONFIRMATIONS,
        inspectConvexShopRuntime: () => shopRuntime,
        importLocalShopDataToConvex: async (options = {}) => {
          const dryRun = options.dryRun !== false;
          if (!dryRun) {
            requireShopRuntimeDebugConfirmation(
              options?.confirm,
              SHOP_RUNTIME_DEBUG_CONFIRMATIONS.importLocalShopDataToConvex,
              'importLocalShopDataToConvex'
            );
          }
          const shopDataToImport = options.useDefaultExamples === true
            ? { ...DEFAULT_SHOP_DATA, _version: SHOP_DATA_VERSION }
            : shopData;
          const result = await importShopDataForDm({
            shopData: shopDataToImport,
            dryRun,
            confirm: dryRun ? undefined : 'IMPORT_LOCAL_SHOP_RUNTIME',
          });
          if (options.persistDefaultExamples === true && dryRun === false) {
            saveShopData(shopDataToImport);
          }
          return result;
        },
      };
      window.__shopRuntimeDebugHelpers__ = helpers;
      delete window.__importLocalShopDataToConvex__;
      delete window.__inspectConvexShopRuntime__;
      console.warn('Installed shop-runtime diagnostics under window.__shopRuntimeDebugHelpers__. Non-dry-run import requires per-call confirmation.');
      return helpers;
    };
    delete window.__importLocalShopDataToConvex__;
    delete window.__inspectConvexShopRuntime__;

    return () => {
      delete window.__importLocalShopDataToConvex__;
      delete window.__inspectConvexShopRuntime__;
      delete window.__installShopRuntimeDebugHelpers__;
      delete window.__shopRuntimeDebugHelpers__;
    };
  }, [connectionStatus, importShopDataForDm, isDM, saveShopData, shopData, shopRuntime, viewerContext]);



  // Loot system data

  const DEFAULT_LOOT_DATA = { 

    items: {

      flamebrand_sword: { id: 'flamebrand_sword', name: 'Flamebrand Sword', category: 'weapon', rarity: 'rare', shortDesc: '+1 Longsword, 1d6 fire damage', iconRef: 'bottle_skull_red', longDesc: 'A blade infused with elemental fire.', buffs: ['+1 Attack', '+1d6 Fire'], debuffs: [], abilities: [{ id: 'fb', name: 'Flame Burst', description: 'Engulf blade in flames for 1 minute', effects: ['+2d6 Fire'], uses: { current: 1, max: 1 } }], basePrice: 2500 },

      shadow_cloak: { id: 'shadow_cloak', name: 'Cloak of Shadows', category: 'gear', rarity: 'epic', shortDesc: '+2 Stealth, advantage on hiding', iconRef: 'magic_hat_purple', longDesc: 'A cloak woven from shadow essence.', buffs: ['+2 Stealth', 'Adv: Hiding'], debuffs: [], combatBonuses: [{ type: 'advantage', scope: 'stealth_checks', label: 'Adv on Stealth' }], basePrice: 4000 },

      health_potion_greater: { id: 'health_potion_greater', name: 'Greater Health Potion', category: 'potion', rarity: 'uncommon', shortDesc: 'Restores 4d4+4 HP', iconRef: 'bottle_magic_red', longDesc: 'A more potent crimson elixir that glows faintly.', buffs: [], debuffs: [], basePrice: 150 },

      monster_core: { id: 'monster_core', name: 'Monster Core', category: 'ingredient', rarity: 'uncommon', shortDesc: 'Crafting material', iconRef: 'mushroom_big_violet', longDesc: 'Pulsing crystalline core harvested from magical creatures.', buffs: [], debuffs: [], basePrice: 50 },

      mandrake_root: { id: 'mandrake_root', name: 'Mandrake Root', category: 'ingredient', rarity: 'common', shortDesc: 'Alchemical ingredient', iconRef: 'mushroom_small_brown', longDesc: 'Gnarled root prized by alchemists and herbalists.', buffs: [], debuffs: [], basePrice: 15 },

      chain_mail: { id: 'chain_mail', name: 'Chain Mail +1', category: 'armor', rarity: 'uncommon', shortDesc: 'AC 17, Str 13 req', longDesc: 'Enchanted chain mail that gleams with a faint silver aura.', buffs: ['+1 AC'], debuffs: [], basePrice: 1500 },

      scroll_fireball: { id: 'scroll_fireball', name: 'Scroll of Fireball', category: 'scroll', rarity: 'rare', shortDesc: 'Cast Fireball (8d6)', iconRef: 'scroll_emblem', longDesc: 'Aged parchment with orange-glowing arcane symbols. Single use.', buffs: [], debuffs: [], basePrice: 200 },

      goblin_tooth_necklace: { id: 'goblin_tooth_necklace', name: 'Goblin Tooth Necklace', category: 'misc', rarity: 'common', shortDesc: 'Intimidation trophy', longDesc: 'A crude necklace of goblin teeth. Intimidating but off-putting.', buffs: ['+1 Intimidation'], debuffs: ['-1 Persuasion'], basePrice: 5 },

      gold_coins: { id: 'gold_coins', name: 'Gold Coins', category: 'currency', rarity: 'common', shortDesc: '150gp', iconRef: 'medallion_gold', longDesc: 'A pouch of assorted gold coins.', buffs: [], debuffs: [], basePrice: 150 },

      phoenix_feather: { id: 'phoenix_feather', name: 'Phoenix Feather', category: 'ingredient', rarity: 'legendary', shortDesc: 'Extremely rare reagent', iconRef: 'twig_red', longDesc: 'Radiates warmth. Used in resurrection magic.', buffs: [], debuffs: [], basePrice: 1000 },

      iron_shield: { id: 'iron_shield', name: 'Iron Shield', category: 'armor', rarity: 'common', shortDesc: '+2 AC', longDesc: 'A sturdy iron shield bearing minor dents from past battles.', buffs: ['+2 AC'], debuffs: [], basePrice: 10 },

    }, 

    activeEncounter: null, 

    log: [],

    enemies: [

      { id: 'goblin_boss', name: 'Goblin Boss', type: 'Humanoid', cr: '1', hp: { current: 21, max: 21 }, ac: 17, notes: 'Leads a raiding party of 6 goblins near the Darkwood Pass.', mechanics: ['Redirect Attack: When missed, can redirect attack to adjacent goblin', 'Multiattack: Two scimitar attacks'], lootTable: { items: [{ itemId: 'health_potion_greater', qty: 2, guaranteed: true, dropChance: 100 }, { itemId: 'goblin_tooth_necklace', qty: 1, guaranteed: true, dropChance: 100 }, { itemId: 'gold_coins', qty: 1, guaranteed: true, dropChance: 100 }, { itemId: 'iron_shield', qty: 1, guaranteed: false, dropChance: 60 }, { itemId: 'mandrake_root', qty: 3, guaranteed: false, dropChance: 40 }], xp: 200, rollCount: 3, dropMode: 'selectN' } },

      { id: 'shadow_drake', name: 'Shadow Drake', type: 'Dragon', cr: '5', hp: { current: 68, max: 68 }, ac: 15, notes: 'Nests in the Whispering Caverns. Weak to radiant damage.', mechanics: ['Shadow Breath (Recharge 5-6): 30ft cone, 6d8 necrotic, DEX DC 14', 'Shadow Step: Teleport 60ft between shadows', 'Resist: Necrotic, nonmagical weapons'], lootTable: { items: [{ itemId: 'flamebrand_sword', qty: 1, guaranteed: false, dropChance: 25 }, { itemId: 'shadow_cloak', qty: 1, guaranteed: false, dropChance: 15 }, { itemId: 'monster_core', qty: 4, guaranteed: true, dropChance: 100 }, { itemId: 'scroll_fireball', qty: 1, guaranteed: false, dropChance: 50 }, { itemId: 'chain_mail', qty: 1, guaranteed: false, dropChance: 40 }, { itemId: 'health_potion_greater', qty: 3, guaranteed: true, dropChance: 100 }, { itemId: 'phoenix_feather', qty: 1, guaranteed: false, dropChance: 5 }, { itemId: 'gold_coins', qty: 1, guaranteed: true, dropChance: 100 }], xp: 1800, rollCount: 4, dropMode: 'individualPercent' } },

    ],

  };

  const LOOT_DATA_VERSION = 3; // Increment to force demo data refresh

  const [lootData, setLootData] = useState(() => {

    const defaultLootData = { ...DEFAULT_LOOT_DATA, _version: LOOT_DATA_VERSION };

    if (isCampaignSandboxSession) {

      const sandboxLootRuntime = sandboxStoredInitialState?.lootRuntime;

      if (sandboxLootRuntime && typeof sandboxLootRuntime === 'object') {

        return {
          ...sandboxLootRuntime,
          _source: 'local-sandbox-loot-runtime',
          _version: LOOT_DATA_VERSION,
        };

      }

      return {
        ...defaultLootData,
        _source: 'local-sandbox-loot-runtime',
      };

    }

    try { 

      const s = localStorage.getItem('bmg-loot-data'); 

      if (s) {

        const parsed = JSON.parse(s);

        // If version mismatch or no enemies/items, reset to defaults

        if (parsed._version === LOOT_DATA_VERSION && (parsed.enemies?.length > 0 || Object.keys(parsed.items || {}).length > 0)) return parsed;

      }

      return defaultLootData;

    } catch { return defaultLootData; }

  });

  const saveLootData = useCallback((data) => {

    const withVersion = {
      ...data,
      _version: LOOT_DATA_VERSION,
      ...(isCampaignSandboxSession ? { _source: 'local-sandbox-loot-runtime' } : {}),
    };

    setLootData(withVersion);

    if (isCampaignSandboxSession) {
      if (typeof updateGameData === 'function') {
        updateGameData({ lootRuntime: withVersion });
      }
      return;
    }

    try { localStorage.setItem('bmg-loot-data', JSON.stringify(withVersion)); } catch {}

  }, [isCampaignSandboxSession, updateGameData]);

  const sandboxLootRuntimeSeededRef = useRef(false);

  React.useEffect(() => {
    if (!isCampaignSandboxSession || sandboxLootRuntimeSeededRef.current) return;
    if (sandboxStoredInitialState?.lootRuntime) {
      sandboxLootRuntimeSeededRef.current = true;
      return;
    }
    sandboxLootRuntimeSeededRef.current = true;
    if (typeof updateGameData === 'function') {
      updateGameData({ lootRuntime: lootData });
    }
  }, [isCampaignSandboxSession, sandboxStoredInitialState, lootData, updateGameData]);

  const lootRuntimeAvailable = useMemo(() => (
    lootRuntime?._source === 'convex-loot-runtime'
  ), [lootRuntime]);

  const isAuthedLootRuntimeDm = viewerContext?.viewerKind === 'dm';
  const isAuthedLootRuntimePlayer = viewerContext?.viewerKind === 'player'
    && Boolean(viewerContext?.viewerCharId);

  const lootRuntimeReadAuthorityEnabled = useMemo(() => (
    lootRuntimeAvailable
    || (
      connectionStatus === 'connected'
      && (isAuthedLootRuntimeDm || isAuthedLootRuntimePlayer)
    )
  ), [
    connectionStatus,
    isAuthedLootRuntimeDm,
    isAuthedLootRuntimePlayer,
    lootRuntimeAvailable,
  ]);

  const lootRuntimePlayerAuthorityEnabled = useMemo(() => (
    !isDM
    && isAuthedLootRuntimePlayer
    && lootRuntimeReadAuthorityEnabled
    && Boolean(lootRuntime?.activeEncounter?.encounterId)
    && typeof claimLootDrop === 'function'
    && typeof withdrawLootClaim === 'function'
  ), [
    claimLootDrop,
    isAuthedLootRuntimePlayer,
    isDM,
    lootRuntime,
    lootRuntimeReadAuthorityEnabled,
    withdrawLootClaim,
  ]);

  const lootRuntimeDmAuthorityEnabled = useMemo(() => (
    isDM
    && isAuthedLootRuntimeDm
    && connectionStatus === 'connected'
    && typeof publishLootEncounterForDm === 'function'
    && typeof setLootDropVisibilityForDm === 'function'
    && typeof revealAllLootDropsForDm === 'function'
    && typeof finishLootEncounterForDm === 'function'
    && typeof distributeLootXpForDm === 'function'
    && typeof awardLootDrop === 'function'
    && typeof awardCurrencyLoot === 'function'
  ), [
    awardCurrencyLoot,
    awardLootDrop,
    connectionStatus,
    distributeLootXpForDm,
    finishLootEncounterForDm,
    isAuthedLootRuntimeDm,
    isDM,
    publishLootEncounterForDm,
    revealAllLootDropsForDm,
    setLootDropVisibilityForDm,
  ]);

  const lootRuntimeAuthorityEnabled = lootRuntimeReadAuthorityEnabled;

  const displayedLootData = useMemo(() => {
    if (!lootRuntimeAuthorityEnabled) return lootData;
    const serverItems = lootRuntime?.items && typeof lootRuntime.items === 'object' ? lootRuntime.items : {};
    const serverEnemies = Array.isArray(lootRuntime?.enemies) ? lootRuntime.enemies : [];
    const serverLootData = {
      ...(lootRuntime || {}),
      items: serverItems,
      enemies: serverEnemies,
      activeEncounter: lootRuntime?.activeEncounter || null,
      log: Array.isArray(lootRuntime?.log) ? lootRuntime.log : [],
      _version: LOOT_DATA_VERSION,
    };
    if (!isDM || useServerDerivedViewerQueries) return serverLootData;

    const mergedEnemiesById = new Map();
    (lootData?.enemies || []).forEach((enemy) => {
      if (enemy?.id) mergedEnemiesById.set(enemy.id, enemy);
    });
    serverEnemies.forEach((enemy) => {
      if (!enemy?.id) return;
      mergedEnemiesById.set(enemy.id, {
        ...(mergedEnemiesById.get(enemy.id) || {}),
        ...enemy,
      });
    });

    return {
      ...lootData,
      ...lootRuntime,
      items: {
        ...(lootData?.items || {}),
        ...serverItems,
      },
      enemies: Array.from(mergedEnemiesById.values()),
      activeEncounter: lootRuntime?.activeEncounter || null,
      log: Array.isArray(lootRuntime?.log) ? lootRuntime.log : (lootData?.log || []),
      _version: LOOT_DATA_VERSION,
    };
  }, [isDM, lootData, lootRuntime, lootRuntimeAuthorityEnabled, useServerDerivedViewerQueries]);

  const publishDisplayedLootEncounter = useCallback(async (args) => {
    if (!lootRuntimeDmAuthorityEnabled) return null;
    const result = await publishLootEncounterForDm(args);
    if (result === null || result === undefined) {
      throw new Error('loot encounter publish authority unavailable');
    }
    return result;
  }, [lootRuntimeDmAuthorityEnabled, publishLootEncounterForDm]);

  const claimDisplayedLootDrop = useCallback(async (args) => {
    if (!lootRuntimePlayerAuthorityEnabled) return null;
    const result = await claimLootDrop(args);
    if (result === null || result === undefined) {
      throw new Error('loot claim authority unavailable');
    }
    return result;
  }, [claimLootDrop, lootRuntimePlayerAuthorityEnabled]);

  const withdrawDisplayedLootClaim = useCallback(async (args) => {
    if (!lootRuntimePlayerAuthorityEnabled) return null;
    const result = await withdrawLootClaim(args);
    if (result === null || result === undefined) {
      throw new Error('loot claim withdrawal authority unavailable');
    }
    return result;
  }, [lootRuntimePlayerAuthorityEnabled, withdrawLootClaim]);

  const setDisplayedLootDropVisibility = useCallback(async (args) => {
    if (!lootRuntimeDmAuthorityEnabled) return null;
    const result = await setLootDropVisibilityForDm(args);
    if (result === null || result === undefined) {
      throw new Error('loot visibility authority unavailable');
    }
    return result;
  }, [lootRuntimeDmAuthorityEnabled, setLootDropVisibilityForDm]);

  const revealAllDisplayedLootDrops = useCallback(async (args) => {
    if (!lootRuntimeDmAuthorityEnabled) return null;
    const result = await revealAllLootDropsForDm(args);
    if (result === null || result === undefined) {
      throw new Error('loot reveal-all authority unavailable');
    }
    return result;
  }, [lootRuntimeDmAuthorityEnabled, revealAllLootDropsForDm]);

  const finishDisplayedLootEncounter = useCallback(async (args) => {
    if (!lootRuntimeDmAuthorityEnabled) return null;
    const result = await finishLootEncounterForDm(args);
    if (result === null || result === undefined) {
      throw new Error('loot finish authority unavailable');
    }
    return result;
  }, [finishLootEncounterForDm, lootRuntimeDmAuthorityEnabled]);

  const distributeDisplayedLootXp = useCallback(async (args) => {
    if (!lootRuntimeDmAuthorityEnabled) return null;
    const result = await distributeLootXpForDm(args);
    if (result === null || result === undefined) {
      throw new Error('loot XP authority unavailable');
    }
    return result;
  }, [distributeLootXpForDm, lootRuntimeDmAuthorityEnabled]);

  const awardDisplayedLootDrop = useCallback(async (args) => {
    if (!lootRuntimeDmAuthorityEnabled) return null;
    const result = await awardLootDrop(args);
    if (result === null || result === undefined) {
      throw new Error('loot award authority unavailable');
    }
    return result;
  }, [awardLootDrop, lootRuntimeDmAuthorityEnabled]);

  const awardDisplayedCurrencyLoot = useCallback(async (args) => {
    if (!lootRuntimeDmAuthorityEnabled) return null;
    const result = await awardCurrencyLoot(args);
    if (result === null || result === undefined) {
      throw new Error('loot currency award authority unavailable');
    }
    return result;
  }, [awardCurrencyLoot, lootRuntimeDmAuthorityEnabled]);

  React.useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (typeof window === 'undefined') return;
    const canExposeImportHelper = isDM
      && viewerContext?.viewerKind === 'dm'
      && connectionStatus === 'connected'
      && typeof importLootDataForDm === 'function';
    if (!canExposeImportHelper) {
      delete window.__importLocalLootDataToConvex__;
      delete window.__inspectConvexLootRuntime__;
      delete window.__installLootRuntimeDebugHelpers__;
      delete window.__lootRuntimeDebugHelpers__;
      return;
    }

    const INSTALL_LOOT_RUNTIME_DEBUG_HELPERS_CONFIRMATION = 'INSTALL_LOOT_RUNTIME_DEBUG_HELPERS';
    const LOOT_RUNTIME_DEBUG_CONFIRMATIONS = Object.freeze({
      importLocalLootDataToConvex: 'IMPORT_LOCAL_LOOT_RUNTIME_FROM_BROWSER',
    });
    const requireLootRuntimeDebugConfirmation = (received, expected, helperName) => {
      if (received === expected) return;
      throw new Error(`${helperName}: pass { confirm: "${expected}" } to run this loot-runtime diagnostic helper.`);
    };

    window.__installLootRuntimeDebugHelpers__ = ({ confirm } = {}) => {
      requireLootRuntimeDebugConfirmation(
        confirm,
        INSTALL_LOOT_RUNTIME_DEBUG_HELPERS_CONFIRMATION,
        '__installLootRuntimeDebugHelpers__'
      );
      const helpers = {
        confirmations: LOOT_RUNTIME_DEBUG_CONFIRMATIONS,
        inspectConvexLootRuntime: () => lootRuntime,
        importLocalLootDataToConvex: async (options = {}) => {
          const dryRun = options.dryRun !== false;
          if (!dryRun) {
            requireLootRuntimeDebugConfirmation(
              options?.confirm,
              LOOT_RUNTIME_DEBUG_CONFIRMATIONS.importLocalLootDataToConvex,
              'importLocalLootDataToConvex'
            );
          }
          return await importLootDataForDm({
            lootData,
            dryRun,
            confirm: dryRun ? undefined : 'IMPORT_LOCAL_LOOT_RUNTIME',
          });
        },
      };
      window.__lootRuntimeDebugHelpers__ = helpers;
      delete window.__importLocalLootDataToConvex__;
      delete window.__inspectConvexLootRuntime__;
      console.warn('Installed loot-runtime diagnostics under window.__lootRuntimeDebugHelpers__. Non-dry-run import requires per-call confirmation.');
      return helpers;
    };
    delete window.__importLocalLootDataToConvex__;
    delete window.__inspectConvexLootRuntime__;

    return () => {
      delete window.__importLocalLootDataToConvex__;
      delete window.__inspectConvexLootRuntime__;
      delete window.__installLootRuntimeDebugHelpers__;
      delete window.__lootRuntimeDebugHelpers__;
    };
  }, [connectionStatus, importLootDataForDm, isDM, lootData, lootRuntime, viewerContext]);

  

  // Dashboard section order (default: characters first, session mode last)

  const DEFAULT_DASHBOARD_ORDER = ['characters', 'combat', 'systems-overview', 'food', 'travel-log', 'messages', 'shop', 'crafting', 'loot', 'session-mode'];

  const [dashboardSectionOrder, setDashboardSectionOrder] = useState(() => {

    try {

      const saved = localStorage.getItem('bmg-dashboard-order');

      if (saved) {

        const parsed = JSON.parse(saved);

        // Migrate: add 'shop' if not present in saved order

        if (!parsed.includes('shop')) {

          const idx = parsed.indexOf('session-mode');

          if (idx >= 0) parsed.splice(idx, 0, 'shop');

          else parsed.push('shop');

          try { localStorage.setItem('bmg-dashboard-order', JSON.stringify(parsed)); } catch {}

        }

        // Migrate: add 'crafting' if not present in saved order

        if (!parsed.includes('crafting')) {

          const idx = parsed.indexOf('session-mode');

          if (idx >= 0) parsed.splice(idx, 0, 'crafting');

          else parsed.push('crafting');

          try { localStorage.setItem('bmg-dashboard-order', JSON.stringify(parsed)); } catch {}

        }

        if (!parsed.includes('loot')) {

          const idx = parsed.indexOf('session-mode');

          if (idx >= 0) parsed.splice(idx, 0, 'loot');

          else parsed.push('loot');

          try { localStorage.setItem('bmg-dashboard-order', JSON.stringify(parsed)); } catch {}

        }

        return parsed;

      }

      return DEFAULT_DASHBOARD_ORDER;

    } catch { return DEFAULT_DASHBOARD_ORDER; }

  });

  const [collapsedSections, setCollapsedSections] = useState(() => {

    try {

      const saved = localStorage.getItem('bmg-collapsed-sections');

      return saved ? JSON.parse(saved) : {};

    } catch { return {}; }

  });

  

  // 2026-04-27: glow state is now derived from char data (char.glowingBuffs

  // and char.glowingSystems) so it propagates across browsers via Convex.

  // The old local-state version meant a DM in browser A and player in

  // browser B saw different glows — addBuffGlow only mutated the DM's

  // React state, which the player browser had no way to learn about.

  // The actual derivation lives below the saveChars hook (we need it

  // to write through). The state hooks themselves are gone.

  const GLOW_TTL_MS = 30000;

  

  // DM Dashboard view mode and selected character for expanded panel

  const [dmCardViewMode, setDmCardViewMode] = useState(() => {

    try {

      return localStorage.getItem('bmg-dm-card-view-mode') || 'expanded';

    } catch { return 'expanded'; }

  });

  const [selectedDmCharId, setSelectedDmCharId] = useState(null); // Which character is selected for the expanded panel

  

  const char = charId ? chars[charId] : null;



  const [authLinkStatus, setAuthLinkStatus] = useState(null);
  const [authLinkPending, setAuthLinkPending] = useState(false);
  const authAutoLinkAttemptRef = useRef(new Set());
  const viewedCharacterAuthLinked = !!(
    viewerAuthToken
    && authRequiredItemMovementAuthorityEnabled
    && viewerContext?.viewerKind === 'player'
    && viewerContext?.viewerCharId === charId
  );
  const shouldAutoLinkViewedCharacter = !!(
    viewerAuthToken
    && charId
    && char
    && !isDM
    && typeof claimViewedCharAsOwner === 'function'
    && !viewerContext?.activeCampaignId
    && !viewedCharacterAuthLinked
  );
  const shouldShowAuthCharacterLinkNotice = !!(
    authLinkPending
    || (
      authLinkStatus
      && authLinkStatus.charId === charId
      && !viewedCharacterAuthLinked
    )
  );

  useEffect(() => {
    if (!shouldAutoLinkViewedCharacter) return undefined;
    const attemptKey = String(charId);
    if (authAutoLinkAttemptRef.current.has(attemptKey)) return undefined;

    let cancelled = false;
    authAutoLinkAttemptRef.current.add(attemptKey);
    setAuthLinkPending(true);
    setAuthLinkStatus({
      charId,
      tone: 'pending',
      message: `Linking your sign-in to ${char?.name || 'this character'}...`,
    });

    (async () => {
      try {
        await claimViewedCharAsOwner({ charId });
        if (!cancelled) {
          setAuthLinkStatus({
            charId,
            tone: 'success',
            message: `Linked your sign-in to ${char?.name || 'this character'}. Permissions will refresh automatically.`,
          });
        }
      } catch (error) {
        console.error('auto-link current auth to viewed character failed', error);
        if (!cancelled) {
          setAuthLinkStatus({
            charId,
            tone: 'error',
            message: error?.message || 'Could not link your sign-in to this character.',
          });
        }
      } finally {
        if (!cancelled) setAuthLinkPending(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    char?.name,
    charId,
    claimViewedCharAsOwner,
    shouldAutoLinkViewedCharacter,
    viewedCharacterAuthLinked,
  ]);



  // ==============================

  // Theme System

  // ==============================

  const [currentThemeId, setCurrentThemeId] = useState(() => {

    try {

      return localStorage.getItem('bmg-theme-id') || 'fantasy-dark';

    } catch { return 'fantasy-dark'; }

  });

  const [currentVariationId, setCurrentVariationId] = useState(() => {

    try {

      return localStorage.getItem('bmg-variation-id') || 'default';

    } catch { return 'default'; }

  });

  const [currentDensity, setCurrentDensity] = useState(() => {

    try {

      return localStorage.getItem('bmg-density') || 'comfortable';

    } catch { return 'comfortable'; }

  });

  const [currentCardStyle, setCurrentCardStyle] = useState(() => {

    try {

      return localStorage.getItem('bmg-card-style') || 'elevated';

    } catch { return 'elevated'; }

  });

  const [enableHoverEffects, setEnableHoverEffects] = useState(() => {

    try {

      return localStorage.getItem('bmg-hover-effects') !== 'false'; // Default true

    } catch { return true; }

  });

  const [showThemeConfig, setShowThemeConfig] = useState(false);



  // Compute current theme

  const baseTheme = THEMES[currentThemeId] || THEMES['fantasy-classic'];

  const currentTheme = useMemo(() => applyVariation(baseTheme, currentVariationId), [baseTheme, currentVariationId]);

  

  // Update global S object synchronously when theme/density/cardStyle changes

  // This ensures all components using module-level S see correct styles

  const currentStyles = useMemo(() => {

    const newStyles = generateStyles(currentTheme, currentDensity, currentCardStyle, enableHoverEffects);

    updateStylesInPlace(newStyles);

    return newStyles;

  }, [currentTheme, currentDensity, currentCardStyle, enableHoverEffects]);



  // 2026-04-29 — Theme bridge for mobile chrome. The mobile components

  // (MobileStickyHeader / MobileBottomNav / etc.) read CSS custom

  // properties (--bg2, --bs, --ts, --tg, --tp, --tm, --ap) so they can

  // be styled at the root without React-prop plumbing. Those vars were

  // previously only defined inside `.crafting-scope`, which left the

  // mobile chrome falling back to hardcoded purple regardless of the

  // active theme. We now also paint <html>/<body> with the theme's

  // page background to kill the white-edge bleed visible on iOS Safari

  // when the app container doesn't fill the viewport.

  React.useEffect(() => {

    const c = currentTheme.colors || {};

    // Pull a flat color out of bgPrimary even if it's a gradient — the

    // body background needs a solid color so any pixel the app doesn't

    // cover (notch insets, scroll bounce) reads as the theme color

    // instead of the browser default white.

    const flatBg = c.bgSecondary || c.bgCardSolid || '#000';

    const tokens = {

      '--bg0': c.bgSecondary || c.bgCardSolid || '#000',

      '--bg1': c.bgCardSolid || '#16101a',

      '--bg2': c.bgCardSolid || '#1e1624',

      '--bg2h': c.bgCardHover || '#261d2e',

      '--bgi': c.bgInput || '#130e17',

      '--bs': c.borderLight || c.borderPrimary || '#2a2033',

      '--ts': c.textSecondary || c.accent || '#c9a227',

      '--tg': c.textPrimary || '#e8d5b7',

      '--tp': c.accentLight || c.accent || '#d4baff',

      '--tm': c.textMuted || '#888',

      '--ap': c.accent || c.accentDark || '#6b3fa0',

    };

    const root = document.documentElement;

    Object.entries(tokens).forEach(([k, v]) => root.style.setProperty(k, v));

    // Paint the page background so iOS rubber-band scroll + safe-area

    // insets show theme color, not the browser's default white.

    document.body.style.background = flatBg;

    document.documentElement.style.background = flatBg;

  }, [currentTheme]);



  const handleSaveTheme = (themeId, variationId, density, cardStyle, hoverEffects) => {

    setCurrentThemeId(themeId);

    setCurrentVariationId(variationId);

    if (density) setCurrentDensity(density);

    if (cardStyle) setCurrentCardStyle(cardStyle);

    if (hoverEffects !== undefined) setEnableHoverEffects(hoverEffects);

    try {

      localStorage.setItem('bmg-theme-id', themeId);

      localStorage.setItem('bmg-variation-id', variationId);

      if (density) localStorage.setItem('bmg-density', density);

      if (cardStyle) localStorage.setItem('bmg-card-style', cardStyle);

      if (hoverEffects !== undefined) localStorage.setItem('bmg-hover-effects', hoverEffects.toString());

    } catch (e) { /* localStorage not available */ }

    setShowThemeConfig(false);

  };



  // Theme context value

  const themeContextValue = useMemo(() => ({

    theme: currentTheme,

    themeId: currentThemeId,

    variationId: currentVariationId,

    density: currentDensity,

    cardStyle: currentCardStyle,

    enableHoverEffects,

    S: currentStyles,

    setTheme: handleSaveTheme,

  }), [currentTheme, currentThemeId, currentVariationId, currentDensity, currentCardStyle, enableHoverEffects, currentStyles]);



  // Dashboard section order helpers

  const saveDashboardOrder = (newOrder) => {

    setDashboardSectionOrder(newOrder);

    try { localStorage.setItem('bmg-dashboard-order', JSON.stringify(newOrder)); } catch {}

  };

  

  const moveDashboardSection = (sectionId, direction) => {

    const idx = dashboardSectionOrder.indexOf(sectionId);

    if (idx < 0) return;

    const newIdx = direction === 'up' ? Math.max(0, idx - 1) : Math.min(dashboardSectionOrder.length - 1, idx + 1);

    if (newIdx === idx) return;

    const newOrder = [...dashboardSectionOrder];

    newOrder.splice(idx, 1);

    newOrder.splice(newIdx, 0, sectionId);

    saveDashboardOrder(newOrder);

  };

  

  const toggleSectionCollapse = (sectionId) => {

    const newCollapsed = { ...collapsedSections, [sectionId]: !collapsedSections[sectionId] };

    setCollapsedSections(newCollapsed);

    try { localStorage.setItem('bmg-collapsed-sections', JSON.stringify(newCollapsed)); } catch {}

  };

  

  const resetDashboardOrder = () => {

    saveDashboardOrder(DEFAULT_DASHBOARD_ORDER);

    setCollapsedSections({});

    try { localStorage.removeItem('bmg-collapsed-sections'); } catch {}

  };

  

  // 2026-04-27: glow helpers — write through to char data.

  //

  // Cross-browser sync: glow lives at char.glowingBuffs / char.glowingSystems,

  // so any browser observing the char (player in another tab, DM dashboard,

  // DM-as-player view) sees the same glow. saveChars suppresses audit

  // since these are ephemeral UI signals, not game-state changes.

  //

  // Cleanup: each add schedules a follow-up save that prunes the entry

  // after the TTL, *unless* a fresh add reset the timestamp in the

  // meantime. If the browser closes before the timer fires, stale

  // entries linger in the data; the read-time filter (in glowingBuffs /

  // glowingSystems memos below) makes them invisible until some other

  // char update prunes them lazily.

  //

  // charsRef so the prune timeout can read whatever's current at fire

  // time — closing over `chars` directly would write a stale snapshot.

  const charsRef = useRef(chars);

  useEffect(() => { charsRef.current = chars; }, [chars]);

  const syncAuthorityCharSnapshotToRef = useCallback((cid, nextCharData) => {
    if (!cid || !nextCharData || typeof nextCharData !== 'object') return;
    const current = charsRef.current || chars || {};
    charsRef.current = {
      ...current,
      [cid]: nextCharData,
    };
  }, [chars]);



  // If a feature flag flips OFF while the user is currently on that

  // surface, bounce them home. Render-side guards already return null

  // to avoid mounting the disabled view; this effect runs the redirect

  // out of band so we don't update state during render.

  useEffect(() => {

    if (view === 'combat' && !combatEnabled) setView(charId ? 'home' : 'select');

    else if (view === 'shop' && !shopsEnabled) setView(isDM ? 'dm' : (charId ? 'home' : 'select'));

    else if (view === 'crafting' && !craftingEnabled) setView(charId ? 'home' : 'select');

    else if (view === 'loot' && !lootEnabled) setView(isDM ? 'dm' : (charId ? 'home' : 'select'));

    else if (view === 'travel' && !travelLogEnabled) setView(isDM ? 'dm' : (charId ? 'home' : 'select'));

  }, [view, combatEnabled, shopsEnabled, craftingEnabled, lootEnabled, travelLogEnabled, charId, isDM]);



  // Anonymous page-view counter. Stores no userId — see viewCounters.ts

  // and the beta_analytics_privacy_policy memory. Fires once per view

  // change. Skip the auth-loading state (`viewerContext === undefined`)

  // since the mutation can't run before auth resolves; the very first

  // view a user lands on still gets counted on the next change.

  const bumpView = useBumpViewCounter();

  useEffect(() => {

    if (viewerContext === undefined) return;
    if (isCampaignSandboxSession) return;

    if (!view) return;

    bumpView({ view, role: isDM ? 'dm' : 'player' }).catch(() => {});

  }, [view, isDM, bumpView, viewerContext, isCampaignSandboxSession]);



  // Patch-shape helpers — return a char object with glow added. Callers

  // that already build a saveChars payload should fold these in inline

  // (one save, no race) and call schedule*Prune separately for cleanup.

  // Used by the dashboard add flows below to avoid two-write races

  // between the buff write and a separate addBuffGlow write.

  // 2026-04-29: glow keying changed from `glowingBuffs[name]` to

  // `glowingBuffs[name::occurrence]` so duplicate-name buffs glow

  // independently. Pass `occurrence` explicitly when targeting a specific

  // instance; omit it to glow the *latest* instance (most calls — they're

  // glowing a buff that was just added). buffInstanceKey handles the

  // bare-name fallback for occurrence 0 so existing data still resolves.

  const charWithBuffGlow = (charObj, buffName, color = '#ffd700', occurrence = null) => {

    if (!charObj) return charObj;

    const occ = occurrence != null ? occurrence : latestBuffOccurrence(charObj, buffName);

    const key = buffInstanceKey(buffName, occ);

    return {

      ...charObj,

      glowingBuffs: { ...(charObj.glowingBuffs || {}), [key]: { color, timestamp: Date.now() } },

    };

  };

  const charWithSystemGlow = (charObj, instanceId, color = '#ffd700') => {

    if (!charObj) return charObj;

    return {

      ...charObj,

      glowingSystems: { ...(charObj.glowingSystems || {}), [instanceId]: { color, timestamp: Date.now() } },

    };

  };

  // Sugar combo: fold glow into char AND schedule prune in one call.

  // Caller passes the working `nextChar` and gets back a glow-patched char.

  const addBuffGlowPatch = (charObj, buffName, color = '#ffd700', occurrence = null) => {

    if (!charObj) return charObj;

    const occ = occurrence != null ? occurrence : latestBuffOccurrence(charObj, buffName);

    const key = buffInstanceKey(buffName, occ);

    scheduleBuffGlowPrune(charObj.id, key);

    return charWithBuffGlow(charObj, buffName, color, occ);

  };

  const addSystemGlowPatch = (charObj, instanceId, color = '#ffd700') => {

    if (!charObj) return charObj;

    scheduleSystemGlowPrune(charObj.id, instanceId);

    return charWithSystemGlow(charObj, instanceId, color);

  };



  // Per-glow TTL prune. Scheduled at add time. No-ops if a fresh add

  // reset the timestamp in the meantime, or if the entry is already gone.

  // 2026-04-29: takes a glow KEY (output of buffInstanceKey), not a raw name —

  // matches the per-instance keying in glowingBuffs.

  const scheduleBuffGlowPrune = (charId, glowKey) => {

    setTimeout(() => {

      const latest = charsRef.current?.[charId];

      const entry = latest?.glowingBuffs?.[glowKey];

      if (!entry || Date.now() - entry.timestamp < GLOW_TTL_MS - 200) return;

      const { [glowKey]: _drop, ...rest } = latest.glowingBuffs;

      saveChars({

        ...charsRef.current,

        [charId]: { ...latest, glowingBuffs: rest },

      }, { suppress: true });

    }, GLOW_TTL_MS + 200);

  };

  const scheduleSystemGlowPrune = (charId, instanceId) => {

    setTimeout(() => {

      const latest = charsRef.current?.[charId];

      const entry = latest?.glowingSystems?.[instanceId];

      if (!entry || Date.now() - entry.timestamp < GLOW_TTL_MS - 200) return;

      const { [instanceId]: _drop, ...rest } = latest.glowingSystems;

      saveChars({

        ...charsRef.current,

        [charId]: { ...latest, glowingSystems: rest },

      }, { suppress: true });

    }, GLOW_TTL_MS + 200);

  };



  // Standalone-write versions — for callers that aren't already running

  // a saveChars (e.g. system update auto-glow, manual notify-glow click).

  // Folding into an existing save would be ideal; this is the fallback.

  // 2026-04-29: occurrence-aware. When called without occurrence, targets

  // the latest instance of the name (typically the just-added one). When

  // called with an explicit occurrence (e.g. from a hover-clear handler

  // that knows the badge's index), targets that specific instance.

  const addBuffGlow = (charId, buffName, color = '#ffd700', occurrence = null) => {

    const cur = charsRef.current?.[charId];

    if (!cur) return;

    const occ = occurrence != null ? occurrence : latestBuffOccurrence(cur, buffName);

    const key = buffInstanceKey(buffName, occ);

    saveChars({

      ...charsRef.current,

      [charId]: charWithBuffGlow(cur, buffName, color, occ),

    }, { suppress: true });

    scheduleBuffGlowPrune(charId, key);

  };



  // 2026-04-29: takes a glow KEY (or a raw name for backward compat — if

  // a caller passes a name and there's no `name::N` entry, the bare-name

  // entry still resolves). The render layer should always pass the

  // occurrence-aware key built via buffInstanceKey().

  const clearBuffGlow = (charId, glowKey) => {

    const cur = charsRef.current?.[charId];

    if (!cur?.glowingBuffs?.[glowKey]) return;

    const { [glowKey]: _drop, ...rest } = cur.glowingBuffs;

    saveChars({

      ...charsRef.current,

      [charId]: { ...cur, glowingBuffs: rest },

    }, { suppress: true });

  };



  const addSystemGlow = (charId, instanceId, color = '#ffd700') => {

    const cur = charsRef.current?.[charId];

    if (!cur) return;

    saveChars({

      ...charsRef.current,

      [charId]: charWithSystemGlow(cur, instanceId, color),

    }, { suppress: true });

    scheduleSystemGlowPrune(charId, instanceId);

  };



  const clearSystemGlow = (charId, instanceId) => {

    const cur = charsRef.current?.[charId];

    if (!cur?.glowingSystems?.[instanceId]) return;

    const { [instanceId]: _drop, ...rest } = cur.glowingSystems;

    saveChars({

      ...charsRef.current,

      [charId]: { ...cur, glowingSystems: rest },

    }, { suppress: true });

  };



  // Read-time projection of glow state, scoped per-char and filtered by TTL.

  // The shape matches the previous local-state shape so consumers (StatusWindow

  // via glowingBuffs prop, DMCard, ExpandedCharacterPanel, etc.) don't change.

  const glowingBuffs = useMemo(() => {

    const out = {};

    Object.entries(chars || {}).forEach(([cid, c]) => {

      const raw = c?.glowingBuffs || {};

      const fresh = {};

      Object.entries(raw).forEach(([k, v]) => {

        if (v?.timestamp && Date.now() - v.timestamp < GLOW_TTL_MS) fresh[k] = v;

      });

      if (Object.keys(fresh).length > 0) out[cid] = fresh;

    });

    return out;

  }, [chars]);



  const glowingSystems = useMemo(() => {

    const out = {};

    Object.entries(chars || {}).forEach(([cid, c]) => {

      const raw = c?.glowingSystems || {};

      const fresh = {};

      Object.entries(raw).forEach(([k, v]) => {

        if (v?.timestamp && Date.now() - v.timestamp < GLOW_TTL_MS) fresh[k] = v;

      });

      if (Object.keys(fresh).length > 0) out[cid] = fresh;

    });

    return out;

  }, [chars]);

  const journalRecentEntries = useMemo(() => (
    Array.isArray(journalRecent) ? journalRecent : []
  ), [journalRecent]);

  const journalPageEntries = useMemo(() => (
    Array.isArray(journalPage?.page)
      ? journalPage.page
      : (Array.isArray(journalPage) ? journalPage : [])
  ), [journalPage]);

  const dmJournalRecentEntries = useMemo(() => (
    Array.isArray(dmJournalRecent) ? dmJournalRecent : []
  ), [dmJournalRecent]);

  const dmJournalPageEntries = useMemo(() => (
    Array.isArray(dmJournalPage?.page)
      ? dmJournalPage.page
      : (Array.isArray(dmJournalPage) ? dmJournalPage : [])
  ), [dmJournalPage]);

  const dmJournalReadCandidates = useMemo(() => {
    const activeDmJournal = hasDmFullJournalDiscovery && Array.isArray(dmJournal) ? dmJournal : [];
    return dmJournalRecentEntries.length > 0 || dmJournalPageEntries.length > 0
      ? mergeJournalEntrySources(dmJournalRecentEntries, dmJournalPageEntries, activeDmJournal)
      : activeDmJournal;
  }, [dmJournal, dmJournalPageEntries, dmJournalRecentEntries, hasDmFullJournalDiscovery]);

  const quickAddJournalSaveTimerRef = useRef(null);

  const quickAddPendingJournalRef = useRef(null);

  const flushQuickAddJournalSave = useCallback(() => {

    if (quickAddJournalSaveTimerRef.current) {

      clearTimeout(quickAddJournalSaveTimerRef.current);

      quickAddJournalSaveTimerRef.current = null;

    }

    const pending = quickAddPendingJournalRef.current;

    if (!pending) return;

    quickAddPendingJournalRef.current = null;

    saveJournalRaw(pending, { fullSnapshot: true });

  }, [saveJournalRaw]);

  const getQuickAddJournalBase = useCallback(() => (

    quickAddPendingJournalRef.current || (hasPlayerFullJournalDiscovery ? (journal || []) : [])

  ), [hasPlayerFullJournalDiscovery, journal]);

  const getQuickAddReadCandidates = useCallback(() => {

    const activeQuickAddJournal = getQuickAddJournalBase();

    if (quickAddPendingJournalRef.current) return activeQuickAddJournal;

    return journalRecentEntries.length > 0 || journalPageEntries.length > 0

      ? mergeJournalEntrySources(journalRecentEntries, journalPageEntries, activeQuickAddJournal)

      : activeQuickAddJournal;

  }, [getQuickAddJournalBase, journalPageEntries, journalRecentEntries]);

  const getQuickAddRecentSource = useCallback(() => {

    const activeQuickAddJournal = getQuickAddJournalBase();

    if (quickAddPendingJournalRef.current) return activeQuickAddJournal;

    return journalRecentEntries.length > 0 ? journalRecentEntries : activeQuickAddJournal;

  }, [getQuickAddJournalBase, journalRecentEntries]);

  const getQuickAddSessionCandidates = useCallback(() => {
    const activeQuickAddJournal = getQuickAddJournalBase();
    if (
      quickAddPendingJournalRef.current
      || typeof upsertJournalEntry !== 'function'
    ) {
      return activeQuickAddJournal;
    }
    const rowSessions = mergeJournalEntrySources(journalPageEntries, journalRecentEntries)
      .filter(entry => entry.category === 'session');
    return rowSessions.length > 0
      ? mergeJournalEntrySources(rowSessions, activeQuickAddJournal)
      : activeQuickAddJournal;
  }, [getQuickAddJournalBase, journalPageEntries, journalRecentEntries, upsertJournalEntry]);

  const getDmSessionCandidates = useCallback(() => {
    const activeDmJournal = hasDmFullJournalDiscovery && Array.isArray(dmJournal) ? dmJournal : [];
    if (typeof upsertDmJournalEntry !== 'function') return activeDmJournal;
    const rowSessions = mergeJournalEntrySources(dmJournalPageEntries, dmJournalRecentEntries)
      .filter(entry => entry.category === 'session');
    return rowSessions.length > 0
      ? mergeJournalEntrySources(rowSessions, activeDmJournal)
      : activeDmJournal;
  }, [dmJournal, dmJournalPageEntries, dmJournalRecentEntries, hasDmFullJournalDiscovery, upsertDmJournalEntry]);

  const saveQuickAddJournalNow = useCallback((nextJournal) => {

    if (quickAddJournalSaveTimerRef.current) {

      clearTimeout(quickAddJournalSaveTimerRef.current);

      quickAddJournalSaveTimerRef.current = null;

    }

    quickAddPendingJournalRef.current = null;

    saveJournalRaw(Array.isArray(nextJournal) ? nextJournal : [], { fullSnapshot: true });

  }, [saveJournalRaw]);

  useEffect(() => () => {

    flushQuickAddJournalSave();

  }, [flushQuickAddJournalSave]);

  useEffect(() => {

    if (view === 'journal' || !showQuickAddJournal) flushQuickAddJournalSave();

  }, [view, showQuickAddJournal, flushQuickAddJournalSave]);

  useEffect(() => {

    const flushOnPageHide = () => flushQuickAddJournalSave();

    window.addEventListener('pagehide', flushOnPageHide);

    return () => window.removeEventListener('pagehide', flushOnPageHide);

  }, [flushQuickAddJournalSave]);



  // ==============================

  // Session Mode + Unified Timeline

  // ==============================

  // Alias non-audited saves (keeps the rest of the app unchanged)

  // const saveFood is wrapped below (audited)

  const saveDiets = saveDietsRaw;

  const saveDiseases = saveDiseasesRaw;

  const saveSystems = (nextSystems, options = {}) => (
    saveSystemsRaw(nextSystems, { fullSnapshot: true, ...(options || {}) })
  );

  const saveFoodTypes = saveFoodTypesRaw;

  const saveLocations = saveLocationsRaw;

  const activeViewerCampaignId = viewerContext?.activeCampaignId || null;
  const practiceTargetModeAvailable = isCampaignSandboxSession || !activeViewerCampaignId;

  const campaignTargetBuckets = useMemo(() => (
    splitCampaignCharacterTargets(chars, { campaignId: activeViewerCampaignId })
  ), [chars, activeViewerCampaignId]);

  const normalizeBatchTargetMode = useCallback((mode) => (
    practiceTargetModeAvailable ? mode : CAMPAIGN_CHARACTER_TARGET_MODE_PLAYERS
  ), [practiceTargetModeAvailable]);

  const effectiveBatchApplyTargetMode = normalizeBatchTargetMode(batchApplyTargetMode);
  const effectiveBatchRemoveTargetMode = normalizeBatchTargetMode(batchRemoveTargetMode);
  const effectiveBatchBuffTargetMode = normalizeBatchTargetMode(batchBuffTargetMode);

  const getTargetRowsForMode = useCallback((mode) => (
    getCampaignCharacterTargetsForMode(chars, normalizeBatchTargetMode(mode), { campaignId: activeViewerCampaignId })
  ), [chars, activeViewerCampaignId, normalizeBatchTargetMode]);

  const batchApplyTargetRows = useMemo(() => (
    getCampaignCharacterTargetsForMode(chars, effectiveBatchApplyTargetMode, { campaignId: activeViewerCampaignId })
  ), [chars, effectiveBatchApplyTargetMode, activeViewerCampaignId]);

  const batchRemoveTargetRows = useMemo(() => (
    getCampaignCharacterTargetsForMode(chars, effectiveBatchRemoveTargetMode, { campaignId: activeViewerCampaignId })
  ), [chars, effectiveBatchRemoveTargetMode, activeViewerCampaignId]);

  const batchBuffTargetRows = useMemo(() => (
    getCampaignCharacterTargetsForMode(chars, effectiveBatchBuffTargetMode, { campaignId: activeViewerCampaignId })
  ), [chars, effectiveBatchBuffTargetMode, activeViewerCampaignId]);

  useEffect(() => {
    setBatchApplyTargets(prev => pruneTargetIdsForMode(prev, chars, effectiveBatchApplyTargetMode, { campaignId: activeViewerCampaignId }));
  }, [chars, effectiveBatchApplyTargetMode, activeViewerCampaignId]);

  useEffect(() => {
    setBatchRemoveTargets(prev => pruneTargetIdsForMode(prev, chars, effectiveBatchRemoveTargetMode, { campaignId: activeViewerCampaignId }));
  }, [chars, effectiveBatchRemoveTargetMode, activeViewerCampaignId]);

  useEffect(() => {
    setBatchBuffTargets(prev => pruneTargetIdsForMode(prev, chars, effectiveBatchBuffTargetMode, { campaignId: activeViewerCampaignId }));
  }, [chars, effectiveBatchBuffTargetMode, activeViewerCampaignId]);

  useEffect(() => {
    if (practiceTargetModeAvailable) return;
    setBatchApplyTargetMode(CAMPAIGN_CHARACTER_TARGET_MODE_PLAYERS);
    setBatchRemoveTargetMode(CAMPAIGN_CHARACTER_TARGET_MODE_PLAYERS);
    setBatchBuffTargetMode(CAMPAIGN_CHARACTER_TARGET_MODE_PLAYERS);
  }, [practiceTargetModeAvailable]);

  const renderBatchTargetModeControl = useCallback(({ mode, setMode, setTargets }) => {
    const effectiveMode = normalizeBatchTargetMode(mode);
    const activeCopy = getCampaignCharacterTargetModeCopy(effectiveMode);
    const countsByMode = {
      [CAMPAIGN_CHARACTER_TARGET_MODE_PLAYERS]: campaignTargetBuckets.playerCharacters.length,
      [CAMPAIGN_CHARACTER_TARGET_MODE_TEST]: campaignTargetBuckets.testCharacters.length,
    };
    const options = getAvailableCampaignCharacterTargetModes({
      includePractice: practiceTargetModeAvailable,
    }).map(targetMode => ({
      mode: targetMode,
      count: countsByMode[targetMode] || 0,
    }));
    return (
      <div style={{ marginBottom: '16px' }}>
        <label style={S.label}>Target group</label>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {options.map(option => {
            const copy = getCampaignCharacterTargetModeCopy(option.mode);
            const selected = effectiveMode === option.mode;
            return (
              <button
                key={option.mode}
                type="button"
                aria-pressed={selected}
                style={{
                  ...S.btn,
                  ...S.btnXs,
                  marginBottom: 0,
                  background: selected ? 'rgba(201,162,39,0.28)' : 'rgba(0,0,0,0.18)',
                  borderColor: selected ? '#c9a227' : 'rgba(255,255,255,0.12)',
                  color: selected ? '#f5d46b' : '#ccc',
                }}
                onClick={() => {
                  setMode(option.mode);
                  setTargets(new Set());
                }}
              >
                {copy.label} ({option.count})
              </button>
            );
          })}
        </div>
        {!practiceTargetModeAvailable && campaignTargetBuckets.testCharacters.length > 0 && (
          <div style={{ marginTop: '6px', fontSize: '0.78rem', color: '#c9a227' }}>
            Saved practice characters are sandbox-only while a live campaign is active.
          </div>
        )}
        <div style={{ marginTop: '6px', fontSize: '0.78rem', color: '#aaa' }}>
          {activeCopy.helper}
        </div>
      </div>
    );
  }, [campaignTargetBuckets, normalizeBatchTargetMode, practiceTargetModeAvailable]);

  const saveGlobalSettings = useCallback((nextSettings) => {
    if (activeViewerCampaignId) {
      if (process.env.NODE_ENV !== 'test') {
        console.warn('Skipped globalSettings write while a campaign is active.', {
          activeCampaignId: activeViewerCampaignId,
        });
      }
      return null;
    }
    if (typeof saveGlobalSettingsRaw !== 'function') return null;
    return saveGlobalSettingsRaw(nextSettings);
  }, [activeViewerCampaignId, saveGlobalSettingsRaw]);

  const saveSandboxCampaignSettings = useCallback((patch = {}) => {
    if (!isCampaignSandboxSession) return null;
    const nextSettings = {
      ...(globalSettings || {}),
      campaignSandbox: true,
      campaignName: typeof patch.campaignName === 'string' && patch.campaignName.trim()
        ? patch.campaignName.trim().slice(0, 120)
        : (globalSettings?.campaignName || 'Campaign Sandbox'),
      campaignTagline: typeof patch.campaignTagline === 'string'
        ? patch.campaignTagline.trim().slice(0, 160)
        : (globalSettings?.campaignTagline || ''),
      enabledFeatures: patch.enabledFeatures && typeof patch.enabledFeatures === 'object'
        ? patch.enabledFeatures
        : (globalSettings?.enabledFeatures || null),
      homebrew: patch.homebrew && typeof patch.homebrew === 'object'
        ? patch.homebrew
        : (globalSettings?.homebrew || null),
    };
    setSandboxRuntimeSettings({
      enabledFeatures: nextSettings.enabledFeatures || null,
      homebrew: nextSettings.homebrew || null,
    });
    if (typeof saveGlobalSettingsRaw !== 'function') return null;
    return saveGlobalSettingsRaw(nextSettings);
  }, [globalSettings, isCampaignSandboxSession, saveGlobalSettingsRaw]);

  useEffect(() => {
    if (!isCampaignSandboxSession || !globalSettings) return;
    setSandboxRuntimeSettings({
      enabledFeatures: globalSettings.enabledFeatures || null,
      homebrew: globalSettings.homebrew || null,
    });
  }, [globalSettings, isCampaignSandboxSession]);

  const sandboxPresetCategories = useMemo(() => (
    normalizeSandboxPresetCategories(
      globalSettings?.sandboxPresetCategories || SANDBOX_DEMO_PRESET_CATEGORIES,
      runtimeCampaignEnabledFeatures,
    )
  ), [globalSettings?.sandboxPresetCategories, runtimeCampaignEnabledFeatures]);

  const saveSpellLibrary = saveSpellLibraryRaw;

  const saveGroupTravelLog = travelLogEnabled && typeof saveGroupTravelLogRaw === 'function'
    ? saveGroupTravelLogRaw
    : () => null;
  const saveGroupTravelLogForTravelFeature = travelLogEnabled && typeof saveGroupTravelLogRaw === 'function'
    ? saveGroupTravelLogRaw
    : null;

  const saveCurrentLocation = saveCurrentLocationRaw;

  // 2026-05-11 F1 fix: one-time migration of `craftingOpen` from per-
  // device localStorage to cloud `globalSettings`. Only the DM's session
  // writes the migration; player devices read the cloud value. Fires
  // exactly once when the cloud value is undefined — subsequent renders
  // see the defined value and skip.
  useEffect(() => {
    if (isCampaignSandboxSession) return;
    if (!isDM) return;
    if (!globalSettings) return;
    if (globalSettings.craftingOpen !== undefined) return;
    try {
      const localHadIt = localStorage.getItem('bmg-crafting-open') === 'true';
      if (localHadIt) {
        saveGlobalSettings({ ...globalSettings, craftingOpen: true });
      }
    } catch { /* ignore */ }
  }, [isCampaignSandboxSession, isDM, globalSettings, saveGlobalSettings]);

  // Show theme configurator if requested. Keep this after all App-level hooks.
  if (showThemeConfig) {
    return (
      <ThemeConfiguratorPage
        currentThemeId={currentThemeId}
        currentVariationId={currentVariationId}
        currentDensity={currentDensity}
        currentCardStyle={currentCardStyle}
        currentHoverEffects={enableHoverEffects}
        onSave={handleSaveTheme}
        onCancel={() => setShowThemeConfig(false)}
      />
    );
  }

  const saveJournal = (nextJournal) => {
    const next = Array.isArray(nextJournal) ? nextJournal : [];
    const pending = quickAddPendingJournalRef.current;
    if (quickAddJournalSaveTimerRef.current) {
      clearTimeout(quickAddJournalSaveTimerRef.current);
      quickAddJournalSaveTimerRef.current = null;
    }
    quickAddPendingJournalRef.current = null;
    if (!Array.isArray(pending)) {
      saveJournalRaw(next, { fullSnapshot: true });
      return;
    }
    const current = Array.isArray(journal) ? journal : [];
    const currentById = new Map(current.filter(e => e?.id).map(e => [e.id, e]));
    const pendingById = new Map(pending.filter(e => e?.id).map(e => [e.id, e]));
    const nextById = new Map(next.filter(e => e?.id).map(e => [e.id, e]));
    const isUnchangedFromCurrent = (entry) => {
      const currentEntry = entry?.id ? currentById.get(entry.id) : null;
      if (!currentEntry) return false;
      try {
        return JSON.stringify(entry) === JSON.stringify(currentEntry);
      } catch {
        return entry === currentEntry;
      }
    };
    const merged = next.map(e => {
      const pendingEntry = e?.id ? pendingById.get(e.id) : null;
      return pendingEntry && isUnchangedFromCurrent(e) ? pendingEntry : e;
    });
    pending.forEach(e => {
      if (!e?.id || (!currentById.has(e.id) && !nextById.has(e.id))) merged.push(e);
    });
    saveJournalRaw(merged, { fullSnapshot: true });
  };

  const saveDmJournal = (nextDmJournal) => {
    saveDmJournalRaw(Array.isArray(nextDmJournal) ? nextDmJournal : [], { fullSnapshot: true });
  };

  const entriesMatch = (left, right) => {
    try {
      return JSON.stringify(left) === JSON.stringify(right);
    } catch {
      return left === right;
    }
  };

  const cloneJournalEntriesForDiff = (entries) => (
    (Array.isArray(entries) ? entries : []).map(entry => (
      entry && typeof entry === 'object' ? { ...entry } : entry
    ))
  );

  const dispatchJournalRowDiff = ({
    beforeEntries,
    afterEntries,
    upsertEntry,
    fallbackFullSave,
    label,
  }) => {
    const before = Array.isArray(beforeEntries) ? beforeEntries : [];
    const after = Array.isArray(afterEntries) ? afterEntries : [];
    const beforeById = new Map(before.filter(entry => entry?.id).map(entry => [entry.id, entry]));
    const afterById = new Map(after.filter(entry => entry?.id).map(entry => [entry.id, entry]));
    const hasMissingIds = after.some(entry => !entry?.id);
    const hasImplicitDeletes = [...beforeById.keys()].some(id => !afterById.has(id));

    if (typeof upsertEntry !== 'function' || hasMissingIds || hasImplicitDeletes) {
      return fallbackFullSave(after) === true;
    }

    after.forEach(entry => {
      if (entriesMatch(beforeById.get(entry.id), entry)) return;
      try {
        const result = upsertEntry({ entryId: entry.id, entry });
        if (result && typeof result.catch === 'function') {
          result.catch(error => console.error(`${label} row upsert failed:`, error));
        }
      } catch (error) {
        console.error(`${label} row upsert failed:`, error);
      }
    });

    return true;
  };

  const persistPlayerQuickAddJournalDiff = (beforeEntries, afterEntries) => {
    if (quickAddPendingJournalRef.current) {
      saveQuickAddJournalNow(afterEntries);
      return false;
    }
    const fallbackFullSave = (entries) => {
      if (hasPlayerFullJournalDiscovery) {
        saveQuickAddJournalNow(entries);
        return true;
      }
      console.warn('Quick Journal full-snapshot fallback blocked without full journal subscription.');
      setQuickAddConfirmation({ created: [], updated: [], error: 'Open Journal to save this change.' });
      setTimeout(() => setQuickAddConfirmation(null), 5000);
      return false;
    };
    return dispatchJournalRowDiff({
      beforeEntries,
      afterEntries,
      upsertEntry: upsertJournalEntry,
      fallbackFullSave,
      label: 'Quick Journal',
    });
  };

  const persistDmQuickAddJournalDiff = (beforeEntries, afterEntries) => {
    const fallbackFullSave = (entries) => {
      if (hasDmFullJournalDiscovery) {
        saveDmJournal(entries);
        return true;
      }
      console.warn('DM Quick Journal full-snapshot fallback blocked without full DM journal subscription.');
      setDmJournalConfirm('Open Library to save this change.');
      setTimeout(() => setDmJournalConfirm(null), 5000);
      return false;
    };
    return dispatchJournalRowDiff({
      beforeEntries,
      afterEntries,
      upsertEntry: upsertDmJournalEntry,
      fallbackFullSave,
      label: 'DM Quick Journal',
    });
  };

  const collectQuickAddNameLookups = (parsedBlocks) => {
    const byKey = new Map();
    (Array.isArray(parsedBlocks) ? parsedBlocks : []).forEach(block => {
      (Array.isArray(block?.entries) ? block.entries : []).forEach(entryData => {
        if (!entryData?.category || !entryData?.name || entryData.isQuestItem) return;
        const category = String(entryData.category).trim();
        const name = String(entryData.name).trim();
        const nameKey = normalizeJournalNameKey(name);
        if (!category || !nameKey) return;
        const key = `${category}:${nameKey}`;
        if (!byKey.has(key)) byKey.set(key, { category, name, nameKey });
      });
    });
    return [...byKey.values()];
  };

  const entriesFromNameLookupResult = (result) => (
    (Array.isArray(result?.lookups) ? result.lookups : [])
      .flatMap(lookup => (Array.isArray(lookup?.entries) ? lookup.entries : []))
      .map(row => row?.entry || row)
      .filter(entry => entry?.id && entry?.category && entry?.name)
  );

  const enrichJournalBaseWithExactLookups = async ({
    baseEntries,
    parsedBlocks,
    lookupEntriesByName,
    upsertEntry,
    label,
  }) => {
    const base = Array.isArray(baseEntries) ? baseEntries : [];
    if (typeof lookupEntriesByName !== 'function' || typeof upsertEntry !== 'function') return base;
    const lookups = collectQuickAddNameLookups(parsedBlocks);
    if (lookups.length === 0) return base;
    try {
      const result = await lookupEntriesByName({ lookups, limit: 5 });
      const missingExactRows = entriesFromNameLookupResult(result)
        .filter(entry => !findJournalEntryByCategoryName(base, {
          category: entry.category,
          name: entry.name,
        }));
      return missingExactRows.length > 0
        ? mergeJournalEntrySources(base, missingExactRows)
        : base;
    } catch (error) {
      console.error(`${label} exact row lookup failed:`, error);
      return base;
    }
  };

  const enrichPlayerQuickAddParserBase = (baseEntries, parsedBlocks) => (
    enrichJournalBaseWithExactLookups({
      baseEntries,
      parsedBlocks,
      lookupEntriesByName: lookupJournalEntriesByName,
      upsertEntry: upsertJournalEntry,
      label: 'Quick Journal',
    })
  );

  const enrichDmQuickAddParserBase = (baseEntries, parsedBlocks) => (
    enrichJournalBaseWithExactLookups({
      baseEntries,
      parsedBlocks,
      lookupEntriesByName: lookupDmJournalEntriesByName,
      upsertEntry: upsertDmJournalEntry,
      label: 'DM Quick Journal',
    })
  );

  const saveNpcLibrary = saveNpcLibraryRaw;

  const saveJournalSettings = saveJournalSettingsRaw;



  // Get the active session entry (most recent session, created in last 12 hours)

  const getActiveSession = () => {

    return getActiveJournalSession(getQuickAddSessionCandidates(), {

      manualSessionId: quickAddSessionOverride,

    });

  };

  

  // Find NPC journal entries that the text references, by either mechanism:

  //   - Formal NPC tags:  #N@Name / #N#Name / #S@Name / #S#Name

  //   - Bare refs:        @Name / #Name  (only when Name matches an existing NPC's name)

  // Used by both the block-row guard and `addPlayerNoteBlocks`, so the UI that offers the

  // pills and the logic that applies them stay in lockstep.

  //

  // Matching is FUZZY (see scoreName below): a typed `@Malcazar` matches an NPC named

  // "Malcazar the Burnt" via prefix/token scoring. Strict full-name equality was the source

  // of the "pills not appearing" regression — users naturally reference an NPC by their

  // first name, not their full title. See docs/journal-audit.md §1.1 for context.

  //

  // Trailing punctuation is stripped from refs ("@Malcazar." → "Malcazar") so writing

  // natural sentences with refs mid-paragraph resolves correctly.

  const getReferencedNpcs = (text, journalEntries) => {

    if (!text || !Array.isArray(journalEntries) || journalEntries.length === 0) return [];

    const qaSettings = { ...DEFAULT_QUICKADD_SETTINGS, ...(journalSettings?.quickAdd || {}) };

    const tags = findAllTags(text, qaSettings.delimiter || ' - ');



    // Collect each ref we need to resolve. `explicitNpc` (formal #N@/#S@ tags) never match

    // a non-NPC entry even if one happens to share the name. Bare refs could technically

    // resolve to any category, but this helper scopes to NPCs by contract (its callers

    // are NPC-block-specific). Path B generalizes this per-category.

    const refNames = [];

    tags.forEach(t => {

      const raw = (t.name || '').trim();

      if (!raw) return;

      const cleaned = raw.replace(/[.,;:!?]+$/, '').trim();

      if (!cleaned) return;

      if (t.type === 'tag' && (t.code === 'N' || t.code === 'S')) {

        refNames.push({ name: cleaned, explicitNpc: true });

      } else if (t.type === 'ref') {

        refNames.push({ name: cleaned, explicitNpc: false });

      }

    });

    if (refNames.length === 0) return [];



    // Score a candidate name against a ref name. Returns 0 for no-match, higher = better.

    //   100  exact case-insensitive full-name match

    //   90   candidate starts with ref (prefix)

    //   80   candidate has an exact-token match for ref

    //   70   candidate has a token that starts with ref (token-prefix)

    // Short refs (<3 chars) require exact match only to prevent noisy matches like

    // "@Ma" → any NPC starting with M.

    const scoreName = (refName, candName) => {

      const r = refName.toLowerCase();

      const n = (candName || '').toLowerCase();

      if (!r || !n) return 0;

      if (n === r) return 100;

      if (r.length < 3) return 0;

      if (n.startsWith(r)) return 90;

      const tokens = n.split(/\s+/);

      if (tokens.some(t => t === r)) return 80;

      if (tokens.some(t => t.startsWith(r))) return 70;

      return 0;

    };



    const npcCandidates = journalEntries.filter(e => e.category === 'npcs' && e.name);

    const matches = new Map(); // entry.id → entry (dedupes multi-ref)

    refNames.forEach(ref => {

      let best = { score: 0, entry: null };

      npcCandidates.forEach(e => {

        const s = scoreName(ref.name, e.name);

        if (s > best.score) {

          best = { score: s, entry: e };

        } else if (s === best.score && s > 0 && best.entry) {

          // Tie-break: prefer more recently updated entry (probably the one the user means).

          const curTs = new Date(best.entry.updatedAt || 0).getTime();

          const altTs = new Date(e.updatedAt || 0).getTime();

          if (altTs > curTs) best = { score: s, entry: e };

        }

      });

      if (best.entry) matches.set(best.entry.id, best.entry);

    });

    return Array.from(matches.values());

  };



  // Handle quick-add journal submission
  const hasFormalNpcReferenceTag = (text) => (
    /#[NS][@#]\s*"?([^'`"\n]+?)(?:['`"]|\s|$)/i.test(text || '')
  );

  // Add playerNotes blocks to NPC entries based on the user's selected category.

  // When `playerNoteCategories` is null the user opted out — no block added. The entry

  // still exists; it just isn't filed under a specific player-notes section on the NPC.

  // Targets ALL referenced NPCs, not just those freshly created/updated — so a bare

  // `@Malcazar` reference now correctly files under Malcazar's profile when the pill is set.

  const addPlayerNoteBlocks = (journalEntries, summary, rawText) => {

    if (!playerNoteCategories) return journalEntries; // user picked "— None —" / untouched

    const hasFormalNpcReference = hasFormalNpcReferenceTag(rawText);

    const discoveredNpcIds = new Set(getClearDiscoveredNpcEntriesForText(rawText).map(entry => entry.id));

    if (!hasPlayerFullJournalDiscovery && !hasFormalNpcReference && discoveredNpcIds.size === 0) return journalEntries;

    const referencedNpcs = getReferencedNpcs(rawText, journalEntries)
      .filter(entry => (
        hasPlayerFullJournalDiscovery
        || hasFormalNpcReference
        || discoveredNpcIds.has(entry.id)
      ));

    if (referencedNpcs.length === 0) return journalEntries;

    const categories = [playerNoteCategories];

    // Extract clean text from the raw input (strip tags + refs + session headers)

    const qaSettings = { ...DEFAULT_QUICKADD_SETTINGS, ...(journalSettings?.quickAdd || {}) };

    const tags = findAllTags(rawText, qaSettings.delimiter || ' - ');

    let cleanText = cleanRefMarkup(cleanTagMarkup(rawText, tags.filter(t => t.type === 'tag'))).trim();

    cleanText = cleanText.replace(/──\s*Session\s*#\d+\s*──\n?/g, '').trim();

    if (!cleanText) return journalEntries;

    const npcIds = new Set(referencedNpcs.map(n => n.id));

    return journalEntries.map(e => {

      if (!npcIds.has(e.id)) return e;

      const existingNotes = e.playerNotes || [];

      const newNotes = categories

        .map(cat => ({ id: generateId(), category: cat, text: cleanText, createdAt: new Date().toISOString() }))

        .filter(nn => !existingNotes.some(en => en.text === nn.text && en.category === nn.category));

      if (newNotes.length === 0) return e;

      return { ...e, playerNotes: [...existingNotes, ...newNotes], updatedAt: new Date().toISOString() };

    });

  };



  const handleQuickAddSubmit = async (text) => {

    if (!text.trim() && !quickAddPrivateNotes.trim()) return;

    

    const privateNotesText = quickAddPrivateNotes.trim();

    

    try {

      // Helper: wrap text in private markers (includes shared charIds)

      const privateOwners = [charId, ...quickAddSharedWith].join(',') + (quickAddShareWithDM || isDM ? '' : '!noDM');

      const wrapPrivate = (t) => t ? `{{PRIVATE:${privateOwners}}}${t}{{/PRIVATE}}` : t;

      // Helper: add private notes to an entry

      const addPrivateNotes = (journal, entryId) => {

        if (!privateNotesText || !entryId) return journal;

        return journal.map(e => {

          if (e.id !== entryId) return e;

          if (quickAddPrivate) {

            // Private: store in privateNotes array

            const notes = [...(e.privateNotes || []), { text: privateNotesText, charId, sharedWith: [...quickAddSharedWith], noDM: !quickAddShareWithDM && !isDM, createdAt: new Date().toISOString() }];

            return { ...e, privateNotes: notes, updatedAt: new Date().toISOString() };

          } else {

            // Public: store in publicNotes array

            const notes = [...(e.publicNotes || []), { text: privateNotesText, charId, createdAt: new Date().toISOString() }];

            return { ...e, publicNotes: notes, updatedAt: new Date().toISOString() };

          }

        });

      };

      

      // STANDALONE MODE: create/update entry directly, no session logging

      if (quickAddEntryMode === 'standalone') {

        // Track privacy change

        quickAddLastPrivateRef.current = quickAddPrivate;

        const initialJournal = getQuickAddJournalBase();

        const qaSettings = { ...DEFAULT_QUICKADD_SETTINGS, ...(journalSettings?.quickAdd || {}) };

        const hasTags = /[#@][A-Z][#@]|@[a-zA-Z"']|#todo/i.test(text.trim());

        

        if (hasTags) {

          // Has tags - use parser but skip session population.

          // 2026-04-27: when a player-block category pill is selected,

          // pass skipNpcDescriptionAppend so the text only lands in the

          // chosen block (Profile / Quest / etc.) and not also in the

          // NPC's "Session Notes" panel on the card.

          const parsed = parseQuickAddInput(text.trim(), qaSettings);

          let currentJournal = await enrichPlayerQuickAddParserBase(initialJournal, parsed);

          currentJournal = mergeDiscoveredNpcRefsForText(currentJournal, text.trim());

          const { updatedJournal: uj1, summary } = applyQuickAddResults(parsed, currentJournal, null, charId, { skipNpcDescriptionAppend: !!playerNoteCategories });

          

          // Post-process: wrap newly-created/updated entry descriptions

          let finalJournal = uj1;

          if (quickAddPrivate) {

            finalJournal = finalJournal.map(e => {

              if (summary.created.some(c => c.name.toLowerCase() === e.name.toLowerCase() && c.category === e.category) && e.description) {

                return { ...e, description: wrapPrivate(e.description) };

              }

              // For updated entries, find old description and wrap the diff

              const wasUpdated = summary.updated.find(u => u.name.toLowerCase() === e.name.toLowerCase() && u.category === e.category);

              if (wasUpdated && e.description) {

                const oldEntry = currentJournal.find(j => j.name.toLowerCase() === e.name.toLowerCase() && j.category === e.category);

                const oldDesc = oldEntry?.description || '';

                if (oldDesc && e.description.startsWith(oldDesc)) {

                  const appended = e.description.substring(oldDesc.length);

                  return { ...e, description: oldDesc + wrapPrivate(appended) };

                } else if (!oldDesc) {

                  return { ...e, description: wrapPrivate(e.description) };

                }

              }

              return e;

            });

          }

          

          // Add private notes to first created/updated entry

          if (privateNotesText) {

            const firstEntry = [...summary.created, ...summary.updated][0];

            if (firstEntry) {

              const match = finalJournal.find(j => j.name.toLowerCase() === firstEntry.name.toLowerCase() && j.category === firstEntry.category);

              if (match) finalJournal = addPrivateNotes(finalJournal, match.id);

            }

          }

          // Apply the player's block-category selection (pill row) to any NPCs the text

          // references. Runs AFTER applyQuickAddResults so newly-created NPCs are in

          // `finalJournal` and findable by getReferencedNpcs.

          finalJournal = addPlayerNoteBlocks(finalJournal, summary, text.trim());

          if (!persistPlayerQuickAddJournalDiff(currentJournal, finalJournal)) return;

          setQuickAddJournalText('');

          setQuickAddPrivateNotes('');

          setPlayerNoteCategories(null); // reset pill so it doesn't carry to the next entry

          

          const createdWithIds = summary.created.map(e => {

            const entry = finalJournal.find(j => j.name.toLowerCase() === e.name.toLowerCase() && j.category === e.category);

            return { ...e, id: entry?.id };

          });

          const updatedWithIds = summary.updated.map(e => {

            const entry = finalJournal.find(j => j.name.toLowerCase() === e.name.toLowerCase() && j.category === e.category);

            return { ...e, id: entry?.id };

          });

          setQuickAddConfirmation({ ...summary, created: createdWithIds, updated: updatedWithIds, totalEntries: finalJournal.length });

        } else {

          // No tags - create entry with chosen name/category

          const name = quickAddStandaloneName.trim();

          if (!name && !privateNotesText) {

            setQuickAddConfirmation({ created: [], updated: [], error: 'Enter a name for the entry' });

            setTimeout(() => setQuickAddConfirmation(null), 4000);

            return;

          }

          

          if (!name) {

            setQuickAddConfirmation({ created: [], updated: [], error: 'Enter a name for the entry' });

            setTimeout(() => setQuickAddConfirmation(null), 4000);

            return;

          }

          

          const category = quickAddStandaloneCategory;

          const descText = quickAddPrivate ? wrapPrivate(text.trim()) : text.trim();

          const currentJournal = await enrichPlayerQuickAddParserBase(initialJournal, [
            { entries: [{ category, name }] },
          ]);

          const existing = currentJournal.find(e => e.category === category && e.name.toLowerCase() === name.toLowerCase());

          

          let updatedJournal;

          if (existing) {

            const descField = category === 'session' ? 'summary' : 'description';

            const oldDesc = existing[descField] || '';

            const newDesc = oldDesc ? oldDesc + '\n\n' + descText : descText;

            updatedJournal = currentJournal.map(e => e.id === existing.id ? { ...e, [descField]: newDesc, updatedAt: new Date().toISOString() } : e);

            if (privateNotesText) updatedJournal = addPrivateNotes(updatedJournal, existing.id);

            

            if (!persistPlayerQuickAddJournalDiff(currentJournal, updatedJournal)) return;

            setQuickAddJournalText('');

            setQuickAddStandaloneName('');

            setQuickAddPrivateNotes('');

            const entry = updatedJournal.find(e => e.id === existing.id);

            setQuickAddConfirmation({ created: [], updated: [{ name, category, id: entry?.id }], totalEntries: updatedJournal.length });

          } else {

            const newEntry = createJournalEntry(category, {

              name,

              description: descText,

              createdBy: charId,

              visibility: 'shared',

              ...(privateNotesText && quickAddPrivate ? { privateNotes: [{ text: privateNotesText, charId, sharedWith: [...quickAddSharedWith], noDM: !quickAddShareWithDM && !isDM, createdAt: new Date().toISOString() }] } : {}),

              ...(privateNotesText && !quickAddPrivate ? { publicNotes: [{ text: privateNotesText, charId, createdAt: new Date().toISOString() }] } : {}),

            });

            updatedJournal = [...currentJournal, newEntry];

            

            if (!persistPlayerQuickAddJournalDiff(currentJournal, updatedJournal)) return;

            setQuickAddJournalText('');

            setQuickAddStandaloneName('');

            setQuickAddPrivateNotes('');

            setQuickAddConfirmation({ created: [{ name, category, id: newEntry.id }], updated: [], totalEntries: updatedJournal.length });

          }

        }

        setTimeout(() => setQuickAddConfirmation(null), 6000);

        return;

      }

      

      const qaSettings = { ...DEFAULT_QUICKADD_SETTINGS, ...(journalSettings?.quickAdd || {}) };

      const initialJournal = getQuickAddSessionCandidates();

      const parsed = parseQuickAddInput(text.trim(), qaSettings);

      let currentJournal = await enrichPlayerQuickAddParserBase(initialJournal, parsed);

      currentJournal = mergeDiscoveredNpcRefsForText(currentJournal, text.trim());

      // SESSION MODE: original behavior, with exact row lookups merged into the
      // parser base so older matching entries outside the bounded recent window
      // are updated instead of duplicated.
      const activeSession = getActiveJournalSession(currentJournal, {
        manualSessionId: quickAddSessionOverride,
      });

      

      let targetJournal = cloneJournalEntriesForDiff(currentJournal);

      let sessionId = activeSession?.id;

      let newSessionNum = null;

      

      if (!activeSession) {

        const sessionMeta = await fetchJournalSessionMeta(discoverJournalEntries, 'Quick Journal');

        newSessionNum = resolveNextJournalSessionNumber(currentJournal, sessionMeta);

        const newSession = createJournalEntry('session', {

          name: `Session ${newSessionNum}`,

          sessionNumber: newSessionNum,

          sessionDate: new Date().toISOString().split('T')[0],

          summary: '',

          createdBy: charId,

          visibility: 'shared',

        });

        targetJournal = [...targetJournal, newSession];

        sessionId = newSession.id;

        setQuickAddSessionOverride(newSession.id);

      }

      

      // Track privacy change for newline insertion

      const privacyChanged = quickAddNewlineOnPrivChange && 

        quickAddLastPrivateRef.current !== null && 

        quickAddLastPrivateRef.current !== quickAddPrivate;

      quickAddLastPrivateRef.current = quickAddPrivate;

      

      // Snapshot existing text BEFORE apply (for post-wrapping private text and privacy-change newlines)

      const preSnapshots = {};

      const needSnapshots = quickAddPrivate || privacyChanged;

      if (needSnapshots) {

        // Snapshot session summary

        if (sessionId) {

          const sessionEntry = targetJournal.find(e => e.id === sessionId);

          preSnapshots._session = sessionEntry?.summary || '';

        }

        // Snapshot all entries that might be created/updated

        for (const block of parsed) {

          for (const entryData of block.entries) {

            if (entryData.name && entryData.category) {

              const existing = targetJournal.find(e => e.category === entryData.category && e.name.toLowerCase() === entryData.name.toLowerCase());

              if (existing) preSnapshots[existing.id] = existing.description || '';

            }

          }

        }

      }

      

      // 2026-04-27: same skip-NPC-description behavior as the standalone path —

      // when a player-block category pill is set, route body text to the block.

      const beforeJournal = cloneJournalEntriesForDiff(targetJournal);

      let { updatedJournal, summary } = applyQuickAddResults(parsed, targetJournal, sessionId, charId, { skipNpcDescriptionAppend: !!playerNoteCategories });

      

      // Insert extra newline at junction when privacy type changes between submissions

      if (privacyChanged && Object.keys(preSnapshots).length > 0) {

        updatedJournal = updatedJournal.map(e => {

          let updated = e;

          // Session summary junction

          if (e.id === sessionId && preSnapshots._session !== undefined) {

            const oldText = preSnapshots._session;

            const newText = e.summary || '';

            if (oldText && newText.length > oldText.length && newText.startsWith(oldText)) {

              updated = { ...updated, summary: oldText + '\n' + newText.substring(oldText.length) };

            }

          }

          // Entry description junction

          if (preSnapshots[e.id] !== undefined) {

            const oldText = preSnapshots[e.id];

            const newText = e.description || '';

            if (oldText && newText.length > oldText.length && newText.startsWith(oldText)) {

              updated = { ...updated, description: oldText + '\n' + newText.substring(oldText.length) };

            }

          }

          return updated;

        });

      }

      

      // Post-process: wrap newly-added text in private markers

      if (quickAddPrivate) {

        updatedJournal = updatedJournal.map(e => {

          let changed = false;

          const updated = { ...e };

          

          // Wrap new session narrative

          if (e.id === sessionId && preSnapshots._session !== undefined) {

            const oldSummary = preSnapshots._session;

            const newSummary = e.summary || '';

            if (newSummary.length > oldSummary.length) {

              // Find what was appended

              if (oldSummary && newSummary.startsWith(oldSummary)) {

                const appended = newSummary.substring(oldSummary.length);

                updated.summary = oldSummary + wrapPrivate(appended);

                changed = true;

              } else if (!oldSummary) {

                updated.summary = wrapPrivate(newSummary);

                changed = true;

              }

            }

          }

          

          // Wrap new entry descriptions

          if (preSnapshots[e.id] !== undefined) {

            const oldDesc = preSnapshots[e.id];

            const newDesc = e.description || '';

            if (newDesc.length > oldDesc.length) {

              if (oldDesc && newDesc.startsWith(oldDesc)) {

                const appended = newDesc.substring(oldDesc.length);

                updated.description = oldDesc + wrapPrivate(appended);

                changed = true;

              } else if (!oldDesc) {

                updated.description = wrapPrivate(newDesc);

                changed = true;

              }

            }

          }

          

          // Wrap new entries (created, not updated)

          if (!preSnapshots.hasOwnProperty(e.id) && e.id !== sessionId && 

              summary.created.some(c => c.name.toLowerCase() === e.name.toLowerCase() && c.category === e.category)) {

            if (e.description) {

              updated.description = wrapPrivate(e.description);

              changed = true;

            }

          }

          

          return changed ? updated : e;

        });

      }

      

      // Add private notes to session

      if (privateNotesText && sessionId) {

        updatedJournal = addPrivateNotes(updatedJournal, sessionId);

      }



      // Apply the player's block-category selection (pill row) to any NPCs the text

      // references. Runs AFTER applyQuickAddResults so newly-created NPCs are present

      // in updatedJournal and findable by getReferencedNpcs.

      updatedJournal = addPlayerNoteBlocks(updatedJournal, summary, text.trim());



      if (!persistPlayerQuickAddJournalDiff(beforeJournal, updatedJournal)) return;

      setQuickAddJournalText('');

      setQuickAddPrivateNotes('');

      setPlayerNoteCategories(null); // reset pill so it doesn't carry to the next entry

      

      const createdWithIds = summary.created.map(e => {

        const entry = updatedJournal.find(j => j.name.toLowerCase() === e.name.toLowerCase() && j.category === e.category);

        return { ...e, id: entry?.id };

      });

      const updatedWithIds = summary.updated.map(e => {

        const entry = updatedJournal.find(j => j.name.toLowerCase() === e.name.toLowerCase() && j.category === e.category);

        return { ...e, id: entry?.id };

      });

      

      setQuickAddConfirmation({ ...summary, created: createdWithIds, updated: updatedWithIds, newSession: newSessionNum, totalEntries: updatedJournal.length });

      setTimeout(() => setQuickAddConfirmation(null), 6000);

    } catch (err) {

      console.error('[QuickAdd] Error:', err, err.stack);

      setQuickAddConfirmation({ created: [], updated: [], error: String(err) });

      setTimeout(() => setQuickAddConfirmation(null), 6000);

    }

  };



  // -- Helpers

  const getActor = () => {

    if (isDM) return { type: 'dm', name: 'DM' };

    const isTestCharacter = char?.isTestCharacter === true || char?.campaignCharacterKind === 'test';

    return { type: 'player', name: char?.name || 'Player', charId: charId || null, isTestCharacter };

  };



  const tsNow = () => Date.now();

  const fmtTs = (ts) => {

    try { return new Date(ts).toLocaleString(); } catch (e) { return String(ts); }

  };



  const deepClone = (v) => {

    try { return v === undefined ? undefined : JSON.parse(JSON.stringify(v)); }

    catch (e) { return v; }

  };



  const safeEq = (a, b) => {

    try { return JSON.stringify(a) === JSON.stringify(b); }

    catch (e) { return a === b; }

  };



  const getAuditCfg = () => {

    // Merge defaults + persisted global settings

    const base = (DEFAULT_SETTINGS.auditLog || {});

    const gs = (globalSettings && globalSettings.auditLog) ? globalSettings.auditLog : (globalSettings?.auditLog || {});

    const merged = { ...base, ...(gs || {}) };

    merged.categories = { ...(base.categories || {}), ...((gs && gs.categories) ? gs.categories : {}) };

    if (!['minimal', 'standard', 'detailed'].includes(merged.verbosity)) merged.verbosity = 'standard';

    merged.enabled = true;

    return merged;

  };



  const canAudit = (category, actor) => {

    const cfg = getAuditCfg();

    if (actor?.isTestCharacter === true) return false;

    if (actor?.type === 'player' && cfg.includePlayerEdits === false) return false;

    const cats = cfg.categories || {};

    if (category && cats[category] === false) return false;

    return true;

  };

  const isAuditTestCharacter = (candidate) => (
    candidate?.isTestCharacter === true || candidate?.campaignCharacterKind === 'test'
  );



  const auditFF = (p, label) => {

    try {

      if (p && typeof p.catch === 'function') p.catch(err => console.error(label || 'audit', err));

    } catch (err) {

      console.error(label || 'audit', err);

    }

  };





  const compactItem = (it) => {

    if (!it) return null;

    const qty = (it.qty ?? it.quantity ?? it.count ?? 1);

    return {

      id: it.id || it.itemId || null,

      name: it.name || it.title || it.itemName || 'Unknown',

      qty: typeof qty === 'number' ? qty : parseInt(qty) || 1,

      equipped: !!(it.equipped || it.isEquipped),

      slot: it.slot || it.type || null,

    };

  };



  const compactItemDetailed = (it) => {

    if (!it) return null;

    const qty = (it.qty ?? it.quantity ?? it.count ?? 1);

    const desc = (it.description || it.desc || it.details || '');

    const notes = (it.notes || it.note || '');

    return {

      id: it.id || it.itemId || null,

      name: it.name || it.title || it.itemName || 'Unknown',

      qty: typeof qty === 'number' ? qty : parseInt(qty) || 1,

      equipped: !!(it.equipped || it.isEquipped),

      slot: it.slot || it.type || null,

      rarity: it.rarity || null,

      attuned: !!(it.attuned || it.attunement),

      charges: (it.charges ?? it.uses ?? null),

      weight: (it.weight ?? null),

      value: (it.value ?? it.cost ?? null),

      tags: it.tags || null,

      notes: typeof notes === 'string' ? notes.slice(0, 800) : notes,

      description: typeof desc === 'string' ? desc.slice(0, 800) : desc,

    };

  };





  const invIndex = (arr, mode = 'standard') => {

    const m = new Map();

    const comp = mode === 'detailed' ? compactItemDetailed : compactItem;

    (arr || []).forEach(raw => {

      const it = comp(raw);

      if (!it) return;

      const key = it.id || it.name;

      const prev = m.get(key);

      if (!prev) m.set(key, { ...it });

      else m.set(key, { ...prev, qty: (prev.qty || 1) + (it.qty || 1), equipped: prev.equipped || it.equipped });

    });

    return m;

  };



  const diffInventory = (beforeInv, afterInv, mode = 'standard') => {

    const a = invIndex(beforeInv);

    const b = invIndex(afterInv);

    const added = [];

    const removed = [];

    const changed = [];

    const allKeys = new Set([...a.keys(), ...b.keys()]);

    allKeys.forEach(k => {

      const A = a.get(k);

      const B = b.get(k);

      if (!A && B) added.push(B);

      else if (A && !B) removed.push(A);

      else if (A && B) {

        if ((A.qty || 1) !== (B.qty || 1) || !!A.equipped !== !!B.equipped) {

          changed.push({ before: A, after: B });

        }

      }

    });

    return { added, removed, changed };

  };



  const compactEquipItem = (e) => {

    if (!e) return null;

    return {

      id: e.id || e.itemId || null,

      name: e.name || e.title || e.itemName || 'Equipment',

      type: e.type || e.slot || null,

      currentDura: typeof e.currentDura === 'number' ? e.currentDura : (typeof e.durability === 'number' ? e.durability : null),

      maxDura: typeof e.maxDura === 'number' ? e.maxDura : (typeof e.maxDurability === 'number' ? e.maxDurability : null),

      broken: !!e.broken,

      equipped: !!e.equipped,

    };

  };



  const eqIndex = (arr) => {

    const m = new Map();

    (arr || []).forEach(raw => {

      const it = compactEquipItem(raw);

      if (!it) return;

      const key = it.id || it.name;

      m.set(key, it);

    });

    return m;

  };



  const diffEquipment = (beforeEq, afterEq) => {

    const a = eqIndex(beforeEq);

    const b = eqIndex(afterEq);

    const added = [];

    const removed = [];

    const changed = [];

    const allKeys = new Set([...a.keys(), ...b.keys()]);

    allKeys.forEach(k => {

      const A = a.get(k);

      const B = b.get(k);

      if (!A && B) added.push(B);

      else if (A && !B) removed.push(A);

      else if (A && B) {

        if ((A.currentDura !== B.currentDura) || (A.maxDura !== B.maxDura) || !!A.broken !== !!B.broken || !!A.equipped !== !!B.equipped) {

          changed.push({ before: A, after: B });

        }

      }

    });

    return { added, removed, changed };

  };



  const compactInventorySnapshot = (inv) => {

    const m = invIndex(inv);

    const arr = [...m.entries()].map(([k, v]) => ({ k, ...v }));

    arr.sort((x, y) => String(x.k).localeCompare(String(y.k)));

    return arr;

  };



  const compactEquipmentSnapshot = (eq) => {

    const m = eqIndex(eq);

    const arr = [...m.entries()].map(([k, v]) => ({ k, ...v }));

    arr.sort((x, y) => String(x.k).localeCompare(String(y.k)));

    return arr;

  };





  const diffStringSets = (beforeArr, afterArr) => {

    const A = new Set((beforeArr || []).filter(Boolean));

    const B = new Set((afterArr || []).filter(Boolean));

    const added = [...B].filter(x => !A.has(x));

    const removed = [...A].filter(x => !B.has(x));

    return { added, removed };

  };



  const diffDiseases = (beforeList, afterList) => {

    const A = new Map((beforeList || []).map(d => [d.diseaseId, d]));

    const B = new Map((afterList || []).map(d => [d.diseaseId, d]));

    const added = [];

    const removed = [];

    const changed = [];

    const keys = new Set([...A.keys(), ...B.keys()]);

    keys.forEach(k => {

      const a = A.get(k);

      const b = B.get(k);

      if (!a && b) added.push(b);

      else if (a && !b) removed.push(a);

      else if (a && b && !safeEq(a, b)) changed.push({ before: a, after: b });

    });

    return { added, removed, changed };

  };



  const pickCharSnapshot = (ch) => {

    if (!ch) return null;

    return {

      id: ch.id,

      name: ch.name,

      race: ch.race,

      charClass: ch.charClass || null,

      level: ch.level || 1,

      currentHp: ch.currentHp,

      maxHp: ch.maxHp,

      tempHp: ch.tempHp || 0,

      stats: ch.stats || ch.baseStats || {},

      baseStats: ch.baseStats || {},

      buffs: (ch.buffList || []),

      debuffs: (ch.debuffList || []),

      permanentBuffs: (ch.permanentBuffs || []),

      permanentDebuffs: (ch.permanentDebuffs || []),

      buffSources: ch.buffSources || {},

      diseases: (ch.diseaseList || []),

      diseaseImmunityList: (ch.diseaseImmunityList || []),

      inventory: (ch.inventory || []),

      equipment: (ch.equipment || []),

      equippedWeapon: ch.equippedWeapon || null,

      equippedArmor: ch.equippedArmor || null,

      equippedShield: ch.equippedShield || null,

      wallet: ch.wallet || {},

      currentDay: ch.currentDay || 1,

      currentLocation: ch.currentLocation || ch.location || null,

    };

  };



  const buildCharRevert = (before, after) => {

    if (!before || !after) return null;

    const keys = [];

    const hpChanged = (before.currentHp !== after.currentHp) || (before.tempHp !== after.tempHp);

    const invChanged = !safeEq(before.inventory, after.inventory) || !safeEq(before.equipment, after.equipment) ||

      (before.equippedWeapon !== after.equippedWeapon) || (before.equippedArmor !== after.equippedArmor) || (before.equippedShield !== after.equippedShield) ||

      !safeEq(before.wallet, after.wallet);

    const buffChanged = !safeEq(before.buffList, after.buffList) || !safeEq(before.debuffList, after.debuffList) ||

      !safeEq(before.permanentBuffs, after.permanentBuffs) || !safeEq(before.permanentDebuffs, after.permanentDebuffs) ||

      !safeEq(before.buffSources, after.buffSources) || !safeEq(before.buffNotes, after.buffNotes);

    const disChanged = !safeEq(before.diseaseList, after.diseaseList) || !safeEq(before.diseaseImmunityList, after.diseaseImmunityList);

    const systemsChanged = !safeEq(before.systemList, after.systemList) || !safeEq(before.removedSystems, after.removedSystems);



    if (hpChanged) keys.push('currentHp', 'tempHp');

    if (invChanged) keys.push('inventory', 'equipment', 'equippedWeapon', 'equippedArmor', 'equippedShield', 'wallet');

    if (buffChanged) keys.push('buffList', 'debuffList', 'permanentBuffs', 'permanentDebuffs', 'buffSources', 'buffNotes');

    if (disChanged) keys.push('diseaseList', 'diseaseImmunityList');

    if (systemsChanged) keys.push('systemList', 'removedSystems');



    if (keys.length === 0) return null;



    const patch = {};

    const deleteKeys = [];

    keys.forEach(k => {

      if (before[k] === undefined) deleteKeys.push(k);

      else patch[k] = deepClone(before[k]);

    });



    return { kind: 'charPatch', charId: before.id, patch, deleteKeys };

  };



  const deriveCharAuditEvent = (before, after, actor, forcedMeta = null, auditMode = 'standard') => {

    const changes = {};

    const invDelta = diffInventory(before.inventory || [], after.inventory || [], auditMode);

    const equipDelta = diffEquipment(before.equipment || [], after.equipment || []);

    const equippedSlotsDelta = {

      weapon: before.equippedWeapon !== after.equippedWeapon ? { from: before.equippedWeapon || null, to: after.equippedWeapon || null } : null,

      armor: before.equippedArmor !== after.equippedArmor ? { from: before.equippedArmor || null, to: after.equippedArmor || null } : null,

      shield: before.equippedShield !== after.equippedShield ? { from: before.equippedShield || null, to: after.equippedShield || null } : null,

    };

    const slotChanged = !!(equippedSlotsDelta.weapon || equippedSlotsDelta.armor || equippedSlotsDelta.shield);



    const findItemNameById = (id) => {

      if (!id) return null;

      const eq = (after.equipment || []).find(e => (e.id || e.itemId) === id);

      if (eq) return eq.name || eq.itemName || eq.title || 'Equipment';

      const inv = (after.inventory || []).find(i => (i.id || i.itemId) === id);

      if (inv) return inv.name || inv.itemName || inv.title || 'Item';

      return null;

    };



    const walletChanged = !safeEq(before.wallet, after.wallet);

    const hpDelta = (before.currentHp !== after.currentHp || before.tempHp !== after.tempHp)

      ? { before: before.currentHp, after: after.currentHp, delta: (after.currentHp - before.currentHp) }

      : null;



    const buffsDelta = {

      buffs: diffStringSets([...(before.buffList || []), ...(before.permanentBuffs || [])], [...(after.buffList || []), ...(after.permanentBuffs || [])]),

      debuffs: diffStringSets([...(before.debuffList || []), ...(before.permanentDebuffs || [])], [...(after.debuffList || []), ...(after.permanentDebuffs || [])]),

    };



    const diseaseDelta = diffDiseases(before.diseaseList || [], after.diseaseList || []);

    const immDelta = diffStringSets(before.diseaseImmunityList || [], after.diseaseImmunityList || []);

    const systemsDelta = {

      systemListChanged: !safeEq(before.systemList, after.systemList),

      removedSystemsChanged: !safeEq(before.removedSystems, after.removedSystems),

    };

    const hasDiseaseChange = diseaseDelta.added.length || diseaseDelta.removed.length || diseaseDelta.changed.length || immDelta.added.length || immDelta.removed.length;

    const hasBuffChange = buffsDelta.buffs.added.length || buffsDelta.buffs.removed.length || buffsDelta.debuffs.added.length || buffsDelta.debuffs.removed.length;

    const hasInvChange = invDelta.added.length || invDelta.removed.length || invDelta.changed.length || walletChanged || equipDelta.added.length || equipDelta.removed.length || equipDelta.changed.length || slotChanged;

    const hasSystemChange = systemsDelta.systemListChanged || systemsDelta.removedSystemsChanged;



    // Primary category for filtering

    let category = 'other';

    if (hasDiseaseChange) category = 'diseases';

    else if (hasBuffChange) category = 'buffs';

    else if (hasInvChange) category = 'inventory';

    else if (hpDelta) category = 'combat';

    else if (hasSystemChange) category = 'systems';



    // Summary

    let summary = `${after.name || 'Character'} updated`;

    let action = 'character.update';



    if (forcedMeta?.summary) summary = forcedMeta.summary;

    if (forcedMeta?.category) category = forcedMeta.category;

    if (forcedMeta?.action) action = forcedMeta.action;



    if (!forcedMeta) {

      if (hasDiseaseChange) {

        const addedNames = diseaseDelta.added.map(d => diseases?.[d.diseaseId]?.name || d.diseaseId).join(', ');

        const removedNames = diseaseDelta.removed.map(d => diseases?.[d.diseaseId]?.name || d.diseaseId).join(', ');

        if (diseaseDelta.added.length) {

          summary = `🦠 ${after.name} contracted: ${addedNames}`;

          action = 'disease.applied';

        } else if (diseaseDelta.removed.length) {

          summary = `🩹 ${after.name} cured: ${removedNames}`;

          action = 'disease.removed';

        } else if (diseaseDelta.changed.length) {

          summary = `🧬 ${after.name} disease details updated`;

          action = 'disease.updated';

        } else if (immDelta.added.length || immDelta.removed.length) {

          summary = `🛡️ ${after.name} disease immunities updated`;

          action = 'disease.immunity';

        }

      } else if (hasBuffChange) {

        if (buffsDelta.buffs.added.length) {

          const b = buffsDelta.buffs.added[0];

          const src = after.buffSources?.[b];

          summary = `✨ ${after.name} gained buff: ${b}${src ? ` (${src})` : ''}`;

          action = 'buff.added';

        } else if (buffsDelta.buffs.removed.length) {

          summary = `✨ ${after.name} lost buff: ${buffsDelta.buffs.removed[0]}`;

          action = 'buff.removed';

        } else if (buffsDelta.debuffs.added.length) {

          const d = buffsDelta.debuffs.added[0];

          const src = after.buffSources?.[d];

          summary = `💀 ${after.name} gained debuff: ${d}${src ? ` (${src})` : ''}`;

          action = 'debuff.added';

        } else if (buffsDelta.debuffs.removed.length) {

          summary = `💀 ${after.name} lost debuff: ${buffsDelta.debuffs.removed[0]}`;

          action = 'debuff.removed';

        }

      } else if (hasInvChange) {

        const invEquipToggle = (invDelta.changed || []).find(ch => !!ch.before && !!ch.after && (!!ch.before.equipped !== !!ch.after.equipped));



        if (slotChanged) {

          const parts = [];

          if (equippedSlotsDelta.weapon) {

            const nm = findItemNameById(equippedSlotsDelta.weapon.to);

            parts.push(`🗡️ Weapon: ${nm || 'None'}`);

          }

          if (equippedSlotsDelta.armor) {

            const nm = findItemNameById(equippedSlotsDelta.armor.to);

            parts.push(`🛡️ Armor: ${nm || 'None'}`);

          }

          if (equippedSlotsDelta.shield) {

            const nm = findItemNameById(equippedSlotsDelta.shield.to);

            parts.push(`🛡️ Shield: ${nm || 'None'}`);

          }

          summary = `🧰 ${after.name} equipped • ${parts.join(' • ')}`;

          action = 'equipment.equip';

        } else if (invEquipToggle) {

          const nm = invEquipToggle.after?.name || invEquipToggle.before?.name || 'Item';

          summary = `🧰 ${after.name} ${invEquipToggle.after?.equipped ? 'equipped' : 'unequipped'}: ${nm}`;

          action = 'inventory.equipToggle';

        } else if (equipDelta.added.length) {

          summary = `🧰 ${after.name} obtained equipment: ${equipDelta.added.map(i => i.name).slice(0, 2).join(', ')}${equipDelta.added.length > 2 ? '…' : ''}`;

          action = 'equipment.add';

        } else if (equipDelta.removed.length) {

          summary = `🧰 ${after.name} removed equipment: ${equipDelta.removed.map(i => i.name).slice(0, 2).join(', ')}${equipDelta.removed.length > 2 ? '…' : ''}`;

          action = 'equipment.remove';

        } else if (equipDelta.changed.length) {

          const c0 = equipDelta.changed[0];

          if ((c0.before.currentDura !== c0.after.currentDura) && (c0.before.currentDura != null || c0.after.currentDura != null)) {

            summary = `🛠️ ${after.name} durability: ${c0.after.name} ${c0.before.currentDura ?? '?'} → ${c0.after.currentDura ?? '?'}`;

            action = 'equipment.durability';

          } else if (!!c0.before.broken !== !!c0.after.broken) {

            summary = `🛠️ ${after.name} equipment status: ${c0.after.name} ${c0.after.broken ? 'broken' : 'repaired'}`;

            action = 'equipment.status';

          } else {

            summary = `🧰 ${after.name} equipment updated`;

            action = 'equipment.update';

          }

        } else if (invDelta.added.length) {

          summary = `🎒 ${after.name} received: ${invDelta.added.map(i => i.name).slice(0, 2).join(', ')}${invDelta.added.length > 2 ? '…' : ''}`;

          action = 'inventory.add';

        } else if (invDelta.removed.length) {

          summary = `🎒 ${after.name} used/removed: ${invDelta.removed.map(i => i.name).slice(0, 2).join(', ')}${invDelta.removed.length > 2 ? '…' : ''}`;

          action = 'inventory.remove';

        } else if (invDelta.changed.length) {

          summary = `🎒 ${after.name} inventory updated`;

          action = 'inventory.update';

        } else if (walletChanged) {

          summary = `💰 ${after.name} wallet updated`;

          action = 'wallet.update';

        }

      } else if (hpDelta) {

        summary = `❤️ ${after.name} HP ${before.currentHp} → ${after.currentHp} (${hpDelta.delta >= 0 ? '+' : ''}${hpDelta.delta})`;

        action = 'hp.update';

      } else if (hasSystemChange) {

        summary = `System update: ${after.name}`;

        action = 'system.updated';

      }

    }



    const revert = buildCharRevert(before, after);



    return {

      ts: tsNow(),

      category,

      action,

      actor,

      sessionId: audit?.activeSessionId || null,

      target: { charId: after.id, charName: after.name },

      summary,

      details: {

        charId: after.id,

        charName: after.name,

        hpDelta,

        inventory: invDelta,

        equipment: equipDelta,

        equippedSlotsDelta,

        walletChanged,

        buffsDelta,

        buffSources: after.buffSources || {},

        diseases: diseaseDelta,

        diseaseImmunityDelta: immDelta,

        systems: systemsDelta,

      },

      revert,

    };

  };



  const normalizeSaveCharsAuditMeta = (auditMeta = null) => {

    if (!auditMeta || typeof auditMeta !== 'object' || Array.isArray(auditMeta)) return auditMeta;

    const meta = auditMeta.meta;

    if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return auditMeta;

    const { meta: _ignoredMeta, ...rest } = auditMeta;

    const normalized = { ...meta, ...rest };

    if (rest.group === undefined && !rest.perCharMeta) normalized.group = true;

    return normalized;

  };



  // Audited saves (chars/messages/combat). Pass { suppress: true } to skip auto-audit for a write.

  const saveChars = (newChars, auditMeta = null) => {

    const normalizedAuditMeta = normalizeSaveCharsAuditMeta(auditMeta);

    const suppress = !!normalizedAuditMeta?.suppress;

    const actor = normalizedAuditMeta?.actor || getActor();



    const cfg = getAuditCfg();

    const shouldAudit = !loading && !suppress && cfg.enabled && (cfg.includePlayerEdits || actor.type !== 'player');



    if (shouldAudit) {

      const beforeChars = chars || {};

      const afterChars = newChars || {};

      const ids = new Set([...Object.keys(beforeChars), ...Object.keys(afterChars)]);

      const changedIds = [];

      const eventsToLog = [];



      ids.forEach(cid => {

        const before = beforeChars[cid];

        const after = afterChars[cid];



        if (isAuditTestCharacter(before) || isAuditTestCharacter(after)) return;

        // Added / removed

        if (!before && after) {

          eventsToLog.push({

            ts: tsNow(),

            category: 'other',

            action: 'character.created',

            actor,

            sessionId: audit?.activeSessionId || null,

            target: { charId: after.id, charName: after.name },

            summary: `👤 Character created: ${after.name}`,

            details: { charId: after.id, charName: after.name },

            revert: null,

          });

          return;

        }

        if (before && !after) {

          eventsToLog.push({

            ts: tsNow(),

            category: 'other',

            action: 'character.deleted',

            actor,

            sessionId: audit?.activeSessionId || null,

            target: { charId: before.id, charName: before.name },

            summary: `🗑️ Character deleted: ${before.name}`,

            details: { charId: before.id, charName: before.name },

            revert: null,

          });

          return;

        }

        if (!before || !after) return;



        // Deep compare a handful of sections to decide if this character changed materially

        const invChanged = cfg.verbosity === 'detailed'

          ? !safeEq(before.inventory, after.inventory)

          : !safeEq(compactInventorySnapshot(before.inventory || []), compactInventorySnapshot(after.inventory || []));



        const eqChanged = cfg.verbosity === 'detailed'

          ? !safeEq(before.equipment, after.equipment)

          : !safeEq(compactEquipmentSnapshot(before.equipment || []), compactEquipmentSnapshot(after.equipment || []));



        const materialChanged =

          before.currentHp !== after.currentHp ||

          before.tempHp !== after.tempHp ||

          invChanged ||

          eqChanged ||

          before.equippedWeapon !== after.equippedWeapon ||

          before.equippedArmor !== after.equippedArmor ||

          before.equippedShield !== after.equippedShield ||

          !safeEq(before.wallet, after.wallet) ||

          !safeEq(before.buffList, after.buffList) ||

          !safeEq(before.debuffList, after.debuffList) ||

          !safeEq(before.permanentBuffs, after.permanentBuffs) ||

          !safeEq(before.permanentDebuffs, after.permanentDebuffs) ||

          !safeEq(before.buffSources, after.buffSources) ||

          !safeEq(before.buffNotes, after.buffNotes) ||

          !safeEq(before.diseaseList, after.diseaseList) ||

          !safeEq(before.diseaseImmunityList, after.diseaseImmunityList) ||

          !safeEq(before.systemList, after.systemList) ||

          !safeEq(before.removedSystems, after.removedSystems);



        if (materialChanged) {

          changedIds.push(cid);

          eventsToLog.push(deriveCharAuditEvent(before, after, actor, normalizedAuditMeta?.perCharMeta?.[cid] || null, cfg.verbosity === 'detailed' ? 'detailed' : 'standard'));

        }

      });



      // Optional: collapse very large bulk updates into a single summary event (DM-loved)

      if (normalizedAuditMeta?.group && changedIds.length > 0) {

        // Suppress per-char events and log one summary

        const summary = normalizedAuditMeta.summary || `DM updated ${changedIds.length} characters`;

        eventsToLog.length = 0;

        eventsToLog.push({

          ts: tsNow(),

          category: normalizedAuditMeta.category || 'other',

          action: normalizedAuditMeta.action || 'bulk.update',

          actor,

          sessionId: audit?.activeSessionId || null,

          target: { charIds: changedIds },

          summary,

          details: { charIds: changedIds },

          revert: null,

        });

      }



      // If DM wants minimal logging, auto-collapse per-save into a single event (unless explicitly overridden)

      if (!normalizedAuditMeta?.group && cfg.verbosity === 'minimal' && changedIds.length > 0) {

        const summary = normalizedAuditMeta?.summary || `DM updated ${changedIds.length} character${changedIds.length === 1 ? '' : 's'}`;

        eventsToLog.length = 0;

        eventsToLog.push({

          ts: tsNow(),

          category: normalizedAuditMeta?.category || 'other',

          action: normalizedAuditMeta?.action || 'bulk.update',

          actor,

          sessionId: audit?.activeSessionId || null,

          target: { charIds: changedIds },

          summary,

          details: { charIds: changedIds },

          revert: null,

        });

      }



      // Fire-and-forget push (avoid blocking UI)

      eventsToLog.filter(ev => canAudit(ev.category || 'other', ev.actor || actor)).forEach(ev => { auditFF(pushAuditEvent(ev), 'pushAuditEvent'); });

    }





    return saveCharsRaw(newChars);

  };



  const saveFood = (newFood, auditMeta = null) => {

    const suppress = !!auditMeta?.suppress;

    const actor = auditMeta?.actor || getActor();



    const shouldAudit = !loading && !suppress && canAudit(auditMeta?.category || 'inventory', actor);



    if (shouldAudit) {

      const before = food || {};

      const after = newFood || {};

      const beforeTotal = Number(before.totalUnits || 0);

      const afterTotal = Number(after.totalUnits || 0);



      if (beforeTotal !== afterTotal) {

        const delta = afterTotal - beforeTotal;

        const sign = delta > 0 ? '+' : '';

        const summary = auditMeta?.summary || `🍖 Food pool ${sign}${delta} (${beforeTotal} → ${afterTotal})`;



        auditFF(pushAuditEvent({

          ts: tsNow(),

          category: auditMeta?.category || 'inventory',

          action: auditMeta?.action || (delta >= 0 ? 'food.add' : 'food.consume'),

          actor,

          sessionId: audit?.activeSessionId || null,

          target: { pool: 'food' },

          summary,

          details: {

            beforeTotal,

            afterTotal,

            delta,

            note: auditMeta?.note || null,

            lastLogEntry: (after.foodLog || [])[0] || null,

          },

          revert: null,



        }), 'pushAuditEvent');

      }

    }



    saveFoodRaw(newFood);

  };



  const saveMessages = (newMessages, auditMeta = null) => {

    const suppress = !!auditMeta?.suppress;

    const actor = auditMeta?.actor || getActor();



    const cfg = getAuditCfg();

    const shouldAudit = !loading && !suppress && canAudit('messages', actor);



    if (shouldAudit) {

      const before = messages || { statusUpdates: [], dmChats: {} };

      const after = newMessages || { statusUpdates: [], dmChats: {} };



      const beforeIds = new Set((before.statusUpdates || []).map(m => m.id));

      const newStatus = (after.statusUpdates || []).filter(m => m && m.id && !beforeIds.has(m.id));



      newStatus.slice(-5).forEach(m => {

        const recipients = (m.recipients || []).map(rid => chars?.[rid]?.name).filter(Boolean);

        const toLabel = m.visibility === 'public' ? 'everyone' : (recipients.length ? recipients.join(', ') : 'selected players');

        const isCine = !!m.sequence || m.urgency === 'urgent';

        const theme = m.theme ? ` (${m.theme})` : '';

        const summary = isCine

          ? `🎬 DM sent cinematic${theme} to ${toLabel}`

          : `💬 DM message to ${toLabel}`;



        const msgActor = m.fromDM ? { type: 'dm', name: 'DM' } : actor;

        if (!canAudit('messages', msgActor)) return;



        auditFF(pushAuditEvent({

          ts: tsNow(),

          category: 'messages',

          action: isCine ? 'message.cinematic' : 'message.sent',

          actor: msgActor,

          sessionId: audit?.activeSessionId || null,

          target: { recipients: (m.recipients || []) },

          summary,

          details: {

            messageId: m.id,

            urgency: m.urgency || 'normal',

            theme: m.theme || null,

            visibility: m.visibility || 'public',

            recipients: m.recipients || [],

            recipientsNames: recipients,

            contentPreview: (m.content || '').slice(0, 220),

            hasSequence: !!m.sequence,

          },

          revert: null,



        }), 'pushAuditEvent');

      });



      // DM/Player direct chat messages (per-character thread)

      const beforeChats = before.dmChats || {};

      const afterChats = after.dmChats || {};



      Object.keys(afterChats).forEach(cid => {

        const bArr = beforeChats[cid] || [];

        const aArr = afterChats[cid] || [];

        const bIds = new Set(bArr.map(m => m && m.id).filter(Boolean));

        const newMsgs = aArr.filter(m => m && m.id && !bIds.has(m.id));



        newMsgs.slice(-5).forEach(m => {

          const charName = chars?.[cid]?.name || 'Player';

          const fromDM = m.from === 'DM' || m.fromDM === true;

          const msgActor = fromDM ? { type: 'dm', name: 'DM' } : { type: 'player', name: charName, charId: cid };

          if (!canAudit('messages', msgActor)) return;



          const summary = fromDM ? `💬 DM chat → ${charName}` : `🗨️ ${charName} → DM`;



          auditFF(pushAuditEvent({

            ts: tsNow(),

            category: 'messages',

            action: fromDM ? 'dmchat.sent' : 'dmchat.reply',

            actor: msgActor,

            sessionId: audit?.activeSessionId || null,

            target: { charId: cid, charName },

            summary,

            details: {

              messageId: m.id,

              from: m.from || (fromDM ? 'DM' : charName),

              to: m.to || (fromDM ? charName : 'DM'),

              timestamp: m.timestamp || null,

              urgency: m.urgency || 'normal',

              contentPreview: (m.content || '').slice(0, 220),

            },

            revert: null,



          }), 'pushAuditEvent');

        });

      });



    }



    saveMessagesRaw(newMessages, canFallbackMessageBlobWrite ? { fullSnapshot: true } : undefined);

  };

  const canFallbackMessageBlobWrite = storageSubscriptionPaths.includes('messages');

  const auditStatusMessage = (message, auditMeta = null) => {
    if (!message) return;
    const suppress = !!auditMeta?.suppress;
    const actor = auditMeta?.actor || getActor();
    if (loading || suppress) return;

    const recipients = (message.recipients || []).map(rid => chars?.[rid]?.name).filter(Boolean);
    const toLabel = message.visibility === 'public'
      ? 'everyone'
      : (recipients.length ? recipients.join(', ') : 'selected players');
    const isCine = !!message.sequence || message.urgency === 'urgent';
    const theme = message.theme ? ` (${message.theme})` : '';
    const msgActor = message.fromDM ? { type: 'dm', name: 'DM' } : actor;
    if (!canAudit('messages', msgActor)) return;

    auditFF(pushAuditEvent({
      ts: tsNow(),
      category: 'messages',
      action: isCine ? 'message.cinematic' : 'message.sent',
      actor: msgActor,
      sessionId: audit?.activeSessionId || null,
      target: { recipients: message.recipients || [] },
      summary: isCine
        ? `ðŸŽ¬ DM sent cinematic${theme} to ${toLabel}`
        : `ðŸ’¬ DM message to ${toLabel}`,
      details: {
        messageId: message.id,
        urgency: message.urgency || 'normal',
        theme: message.theme || null,
        visibility: message.visibility || 'public',
        recipients: message.recipients || [],
        recipientsNames: recipients,
        contentPreview: (message.content || message.text || '').slice(0, 220),
        hasSequence: !!message.sequence,
      },
      revert: null,
    }), 'pushAuditEvent');
  };

  const handleSendStatusMessage = async (args = {}) => {
    const message = args?.message || args;
    if (!message?.id) return null;
    if (typeof sendStatusMessage === 'function') {
      const result = await sendStatusMessage({ message });
      auditStatusMessage(message, args?.auditMeta || null);
      return result;
    }
    if (!canFallbackMessageBlobWrite) return null;
    const nextMessages = {
      ...messages,
      statusUpdates: [...(messages?.statusUpdates || []), message],
    };
    saveMessages(nextMessages, args?.auditMeta || null);
    return null;
  };

  const handleMarkStatusMessageSeen = async (messageId) => {
    if (!messageId) return null;
    if (typeof markStatusMessageSeen === 'function') {
      return await markStatusMessageSeen({ messageId });
    }
    if (!canFallbackMessageBlobWrite) return null;
    const updated = (messages.statusUpdates || []).map(m =>
      m.id === messageId ? { ...m, seen: true } : m
    );
    saveMessages({ ...messages, statusUpdates: updated }, { suppress: true });
    return null;
  };

  const handleDismissStatusMessage = async (messageId) => {
    if (!messageId) return null;
    if (typeof dismissStatusMessage === 'function') {
      return await dismissStatusMessage({ messageId });
    }
    if (!canFallbackMessageBlobWrite) return null;
    const updated = (messages.statusUpdates || []).map(m =>
      m.id === messageId ? { ...m, dismissed: true } : m
    );
    saveMessages({ ...messages, statusUpdates: updated }, { suppress: true });
    return null;
  };

  const handleMarkDmChatRead = async ({ charId: targetCharId, messageIds } = {}) => {
    if (!targetCharId || !Array.isArray(messageIds) || messageIds.length === 0) return null;
    if (typeof markDmChatRead === 'function') {
      return await markDmChatRead({ charId: targetCharId, messageIds });
    }
    if (!canFallbackMessageBlobWrite) return null;
    const newChats = { ...(messages.dmChats || {}) };
    const ids = new Set(messageIds);
    newChats[targetCharId] = (newChats[targetCharId] || []).map(m =>
      ids.has(m.id) ? { ...m, read: true } : m
    );
    saveMessages({ ...messages, dmChats: newChats }, { suppress: true });
    return null;
  };

  const auditDmChatMessage = (targetCharId, message) => {
    if (!message || !targetCharId) return;
    const actor = getActor();
    const msgActor = message.from === 'DM' || message.fromDM === true
      ? { type: 'dm', name: 'DM' }
      : { type: 'player', name: chars?.[targetCharId]?.name || 'Player', charId: targetCharId };
    if (!canAudit('messages', msgActor)) return;
    const charName = chars?.[targetCharId]?.name || 'Player';
    const fromDM = msgActor.type === 'dm';
    auditFF(pushAuditEvent({
      ts: tsNow(),
      category: 'messages',
      action: fromDM ? 'dmchat.sent' : 'dmchat.reply',
      actor: msgActor,
      sessionId: audit?.activeSessionId || null,
      target: { charId: targetCharId, charName },
      summary: fromDM ? `💬 DM chat → ${charName}` : `🗨️ ${charName} → DM`,
      details: {
        messageId: message.id,
        from: message.from || (fromDM ? 'DM' : charName),
        to: message.to || (fromDM ? charName : 'DM'),
        timestamp: message.timestamp || null,
        urgency: message.urgency || 'normal',
        contentPreview: (message.content || '').slice(0, 220),
      },
      revert: null,
    }), 'pushAuditEvent');
  };

  const handleSendDmChatMessage = async ({ charId: targetCharId, message } = {}) => {
    if (!targetCharId || !message?.id) return null;
    if (typeof sendDmChatMessage === 'function') {
      const result = await sendDmChatMessage({ charId: targetCharId, message });
      auditDmChatMessage(targetCharId, message);
      return result;
    }
    if (!canFallbackMessageBlobWrite) return null;
    const newChats = { ...(messages.dmChats || {}) };
    newChats[targetCharId] = [...(newChats[targetCharId] || []), message];
    saveMessages({ ...messages, dmChats: newChats });
    return null;
  };

  const handleDeleteDmChatMessage = async ({ charId: targetCharId, messageId } = {}) => {
    if (!targetCharId || !messageId) return null;
    if (typeof deleteDmChatMessage === 'function') {
      return await deleteDmChatMessage({ charId: targetCharId, messageId });
    }
    if (!canFallbackMessageBlobWrite) return null;

    const newChats = { ...(messages.dmChats || {}) };
    newChats[targetCharId] = (newChats[targetCharId] || []).map(m =>
      m.id === messageId ? { ...m, deleted: true } : m
    );
    saveMessages({ ...messages, dmChats: newChats }, { suppress: true });
    return null;
  };



  const saveCombat = (newCombat, auditMeta = null) => {

    const combatToSave = sanitizeCampaignSandboxCombat(newCombat);

    const suppress = !!auditMeta?.suppress;

    const actor = auditMeta?.actor || getActor();

    const shouldAudit = !loading && !suppress && canAudit('combat', actor);

    if (shouldAudit) {

      const before = sanitizeCampaignSandboxCombat(combat) || {};

      const after = combatToSave || {};

      // Combatant roster changes

      try {

        const bIds = new Set((before.combatants || []).map(c => c && c.id).filter(Boolean));

        const aIds = new Set((after.combatants || []).map(c => c && c.id).filter(Boolean));

        const added = (after.combatants || []).filter(c => c && c.id && !bIds.has(c.id));

        const removed = (before.combatants || []).filter(c => c && c.id && !aIds.has(c.id));



        added.slice(0, 10).forEach(c => {

          auditFF(pushAuditEvent({

            ts: tsNow(),

            category: 'combat',

            action: c.type === 'enemy' ? 'combatant.add.enemy' : 'combatant.add.player',

            actor,

            sessionId: audit?.activeSessionId || null,

            target: { combatantId: c.id, combatantName: c.name, combatantType: c.type },

            summary: c.type === 'enemy' ? `👹 Added enemy: ${c.name}` : `🧝 Added player to combat: ${c.name}`,

            details: { combatant: c },

            revert: null,



          }), 'pushAuditEvent');

        });



        removed.slice(0, 10).forEach(c => {

          auditFF(pushAuditEvent({

            ts: tsNow(),

            category: 'combat',

            action: 'combatant.remove',

            actor,

            sessionId: audit?.activeSessionId || null,

            target: { combatantId: c.id, combatantName: c.name, combatantType: c.type },

            summary: `🗑️ Removed from combat: ${c.name}`,

            details: { combatant: c },

            revert: null,



          }), 'pushAuditEvent');

        });

      } catch (e) {}



      // Combat start/end

      if (!!before.active !== !!after.active) {

        auditFF(pushAuditEvent({

          ts: tsNow(),

          category: 'combat',

          action: after.active ? 'combat.start' : 'combat.end',

          actor,

          sessionId: audit?.activeSessionId || null,

          target: {},

          summary: after.active ? '⚔️ Combat started' : '🏁 Combat ended',

          details: { beforeActive: !!before.active, afterActive: !!after.active, round: after.round || 1 },

          revert: null,



        }), 'pushAuditEvent');

      }



      // Damage/healing log entries (they are prepended)

      const b0 = (before.damageLog || [])[0];

      const a0 = (after.damageLog || [])[0];

      if (a0 && !safeEq(a0, b0)) {

        const isHeal = !!a0.isHealing || a0.type === 'healing';

        const amt = a0.amount ?? a0.damage ?? a0.value ?? '';

        const summary = isHeal

          ? `✨ Healing: ${a0.source || 'Unknown'} → ${a0.target || 'Unknown'} (+${amt})`

          : `🩸 Damage: ${a0.source || 'Unknown'} → ${a0.target || 'Unknown'} (${amt})`;



        auditFF(pushAuditEvent({

          ts: tsNow(),

          category: 'combat',

          action: isHeal ? 'combat.heal' : 'combat.damage',

          actor,

          sessionId: audit?.activeSessionId || null,

          target: { source: a0.source, target: a0.target },

          summary,

          details: { logEntry: a0 },

          revert: null,



        }), 'pushAuditEvent');

      }

    }



    return saveCombatRaw(combatToSave);

  };



  // ---- Session Mode actions ----

  const startSession = async ({ name, notes } = {}) => {

    const actor = getActor();

    const now = tsNow();

    const sessionName = (name || '').trim() || `Session ${Object.keys(audit?.sessions || {}).length + 1}`;

    const cfg = getAuditCfg();

    if (!cfg.enabled) return null;



    const sessionId = await pushAuditSession({

      name: sessionName,

      notes: (notes || '').trim() || '',

      startedAt: now,

      startedBy: actor.name || 'DM',

      endedAt: null,

      endedBy: null,

      createdAt: now,

    });



    const patch = {

      'audit/activeSessionId': sessionId,

    };



    // Snapshot all characters at session start

    Object.keys(auditSnapshotChars || {}).forEach(cid => {

      patch[`audit/snapshots/${sessionId}/${cid}/start`] = { ts: now, snapshot: pickCharSnapshot(auditSnapshotChars[cid]) };

    });



    updateGameData(patch);



    // Log session start event

    if (canAudit('session', actor)) auditFF(pushAuditEvent({

      ts: now,

      category: 'session',

      action: 'session.start',

      actor,

      sessionId,

      summary: `📌 Session started: ${sessionName}`,

      details: { sessionName, notes: (notes || '').trim() || '' },

      revert: null,



    }), 'pushAuditEvent');



    return sessionId;

  };



  const endSession = async ({ notes } = {}) => {

    const actor = getActor();

    const now = tsNow();

    const sessionId = audit?.activeSessionId;

    if (!sessionId) return null;



    const patch = {

      'audit/activeSessionId': null,

      [`audit/sessions/${sessionId}/endedAt`]: now,

      [`audit/sessions/${sessionId}/endedBy`]: actor.name || 'DM',

    };

    if ((notes || '').trim()) {

      patch[`audit/sessions/${sessionId}/notes`] = (notes || '').trim();

    }



    // Snapshot all characters at session end

    Object.keys(auditSnapshotChars || {}).forEach(cid => {

      patch[`audit/snapshots/${sessionId}/${cid}/end`] = { ts: now, snapshot: pickCharSnapshot(auditSnapshotChars[cid]) };

    });



    updateGameData(patch);



    const sessionName = audit?.sessions?.[sessionId]?.name || 'Session';



    if (canAudit('session', actor)) auditFF(pushAuditEvent({

      ts: now,

      category: 'session',

      action: 'session.end',

      actor,

      sessionId,

      summary: `✅ Session ended: ${sessionName}`,

      details: { sessionName, notes: (notes || '').trim() || '' },

      revert: null,



    }), 'pushAuditEvent');



    return sessionId;

  };





  // ---- Demo / Test Mode for Timeline ----

  // Creates a synthetic ended session + sample events + snapshots (does not modify character sheets)

  const clearDemoAudit = async () => {

    try {

      const patch = {};

      const activeSid = audit?.activeSessionId || null;

      if (activeSid && audit?.sessions?.[activeSid]?.isDemo) {

        patch['audit/activeSessionId'] = null;

      }



      Object.entries(audit?.events || {}).forEach(([eid, ev]) => {

        if (ev && ev.isDemo) {

          patch[`audit/events/${eid}`] = null;

        }

      });



      Object.entries(audit?.sessions || {}).forEach(([sid, s]) => {

        if (s && s.isDemo) {

          patch[`audit/sessions/${sid}`] = null;

          patch[`audit/snapshots/${sid}`] = null;

        }

      });



      if (Object.keys(patch).length === 0) return;

      updateGameData(patch);

    } catch (err) {

      console.error('clearDemoAudit failed:', err);

    }

  };



  const seedDemoAudit = async () => {

    const actor = { type: 'dm', name: 'DM' };

    const now = tsNow();



    // Clear any previous demo data

    await clearDemoAudit();



    // Get character names for personalization

    const ids = Object.keys(chars || {});

    const char1 = ids[0] ? { id: ids[0], name: chars[ids[0]]?.name || 'Theron' } : { id: 'demo-char-1', name: 'Theron' };

    const char2 = ids[1] ? { id: ids[1], name: chars[ids[1]]?.name || 'Lika' } : { id: 'demo-char-2', name: 'Lika' };

    const char3 = ids[2] ? { id: ids[2], name: chars[ids[2]]?.name || 'Marcus' } : { id: 'demo-char-3', name: 'Marcus' };



    // Session timestamps (spread over 3 weeks of "game time")

    const day = 24 * 60 * 60 * 1000;

    const hour = 60 * 60 * 1000;

    const min = 60 * 1000;



    // ========== SESSION 1: "The Beginning" ==========

    const s1Start = now - (14 * day) - (2 * hour);

    const s1End = s1Start + (2 * hour) + (15 * min);



    const session1Id = await pushAuditSession({

      name: 'The Beginning',

      notes: 'The party meets and receives their first quest.',

      startedAt: s1Start,

      startedBy: 'DM',

      endedAt: s1End,

      endedBy: 'DM',

      createdAt: now,

      isDemo: true,

      arc: 'Act 1: The Plague',

    });



    // Session 1 events

    await pushAuditEvent({ ts: s1Start, category: 'session', action: 'session.start', actor, sessionId: session1Id, summary: '📅 Session started: The Beginning', details: { sessionName: 'The Beginning' }, isDemo: true });

    

    await pushAuditEvent({ ts: s1Start + 5*min, category: 'messages', action: 'message.npc', actor, sessionId: session1Id, summary: '💬 Talked to Aldric the Mysterious', details: { npcName: 'Aldric the Mysterious', contentPreview: 'The old man speaks of darkness spreading from the Darkwood...', kind: 'npc' }, isDemo: true, importance: 'major' });

    

    await pushAuditEvent({ ts: s1Start + 10*min, category: 'quest', action: 'quest.accepted', actor, sessionId: session1Id, summary: '📜 Quest: Find the Moonstone', details: { questName: 'Find the Moonstone', questGiver: 'Aldric', objectives: ['Travel to the Darkwood ruins', 'Recover the Moonstone artifact'] }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s1Start + 20*min, category: 'travel', action: 'travel.location', actor, sessionId: session1Id, summary: '📍 Traveled to Oldwood Road', details: { from: 'Rusty Tankard Inn', to: 'Oldwood Road' }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s1Start + 30*min, category: 'combat', action: 'combat.start', actor, sessionId: session1Id, summary: '⚔️ Combat: Forest Wolves', details: { enemies: ['Forest Wolf', 'Forest Wolf', 'Dire Wolf'], difficulty: 'easy' }, isDemo: true, importance: 'major' });

    

    await pushAuditEvent({ ts: s1Start + 35*min, category: 'combat', action: 'combat.damage', actor, sessionId: session1Id, target: { charId: char1.id, charName: char1.name }, summary: `${char1.name} took 8 damage from Dire Wolf`, details: { damage: 8, source: 'Dire Wolf bite' }, isDemo: true, importance: 'audit' });



    await pushAuditEvent({ ts: s1Start + 42*min, category: 'combat', action: 'combat.end', actor, sessionId: session1Id, summary: '⚔️ Victory: Forest Wolves defeated', details: { outcome: 'victory', rounds: 4, killingBlow: char1.name, totalDamageToParty: 15 }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s1Start + 45*min, category: 'inventory', action: 'inventory.loot', actor, sessionId: session1Id, summary: '🎲 Loot acquired', details: { items: [{ name: 'Wolf Pelt', qty: 3, recipient: 'Party' }, { name: 'Gold', qty: 12, recipient: 'Party' }, { name: 'Dire Wolf Fang', qty: 1, recipient: char1.name }] }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s1Start + 60*min, category: 'travel', action: 'travel.location', actor, sessionId: session1Id, summary: '📍 Arrived at Millbrook Village', details: { from: 'Oldwood Road', to: 'Millbrook Village' }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s1Start + 70*min, category: 'messages', action: 'message.npc', actor, sessionId: session1Id, summary: '💬 Talked to worried villagers', details: { npcName: 'Villager', contentPreview: 'Strange sickness has been spreading... people are afraid to go near the forest.', kind: 'npc' }, isDemo: true, importance: 'minor' });



    await pushAuditEvent({ ts: s1Start + 85*min, category: 'inventory', action: 'inventory.shop', actor, sessionId: session1Id, summary: '🛒 Traded with Merchant Greta', details: { merchant: 'Greta', bought: [{ name: 'Rations', qty: 10, cost: 5 }, { name: 'Torch', qty: 5, cost: 1 }, { name: 'Rope', qty: 1, cost: 2 }], sold: [{ name: 'Wolf Pelt', qty: 2, value: 8 }] }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s1Start + 100*min, category: 'buffs', action: 'buff.added', actor, sessionId: session1Id, target: { charId: char2.id, charName: char2.name }, summary: `✨ Buff: Traveler's Blessing → ${char2.name}`, details: { buff: "Traveler's Blessing (+1 to survival checks)", source: 'Shrine prayer' }, isDemo: true, importance: 'minor' });



    await pushAuditEvent({ ts: s1End, category: 'session', action: 'session.end', actor, sessionId: session1Id, summary: '📅 Session ended: The Beginning', details: { duration: s1End - s1Start, eventCount: 11 }, isDemo: true });



    // ========== SESSION 2: "Rising Tension" ==========

    const s2Start = now - (7 * day) - (2 * hour);

    const s2End = s2Start + (2 * hour) + (45 * min);



    const session2Id = await pushAuditSession({

      name: 'Rising Tension',

      notes: 'The party investigates the plague and one of their own falls ill.',

      startedAt: s2Start,

      startedBy: 'DM',

      endedAt: s2End,

      endedBy: 'DM',

      createdAt: now,

      isDemo: true,

      arc: 'Act 1: The Plague',

    });



    await pushAuditEvent({ ts: s2Start, category: 'session', action: 'session.start', actor, sessionId: session2Id, summary: '📅 Session started: Rising Tension', details: { sessionName: 'Rising Tension' }, isDemo: true });



    await pushAuditEvent({ ts: s2Start + 5*min, category: 'messages', action: 'message.npc', actor, sessionId: session2Id, summary: '💬 Talked to Elder Miriam', details: { npcName: 'Elder Miriam', contentPreview: 'The sickness spreads from the granary. Rats have infested it... unnatural rats.', kind: 'npc' }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s2Start + 15*min, category: 'travel', action: 'travel.location', actor, sessionId: session2Id, summary: '📍 Entered the Old Granary', details: { from: 'Millbrook Village', to: 'Old Granary' }, isDemo: true, importance: 'minor' });



    await pushAuditEvent({ ts: s2Start + 20*min, category: 'combat', action: 'combat.start', actor, sessionId: session2Id, summary: '⚔️ Combat: Plague Rats', details: { enemies: ['Plague Rat ×6', 'Giant Plague Rat'], difficulty: 'medium' }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s2Start + 25*min, category: 'combat', action: 'combat.damage', actor, sessionId: session2Id, target: { charId: char2.id, charName: char2.name }, summary: `${char2.name} bitten by Giant Plague Rat`, details: { damage: 6, source: 'Diseased bite', note: 'Constitution save required' }, isDemo: true, importance: 'audit' });



    await pushAuditEvent({ ts: s2Start + 32*min, category: 'combat', action: 'combat.end', actor, sessionId: session2Id, summary: '⚔️ Victory: Plague Rats exterminated', details: { outcome: 'victory', rounds: 5, killingBlow: char3.name, totalDamageToParty: 22 }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s2Start + 35*min, category: 'inventory', action: 'inventory.loot', actor, sessionId: session2Id, summary: '🎲 Loot acquired', details: { items: [{ name: 'Rat Tail', qty: 7, recipient: char3.name }, { name: 'Gold', qty: 8, recipient: 'Party' }, { name: 'Strange Vial', qty: 1, recipient: char2.name }] }, isDemo: true, importance: 'major' });



    const demoDiseaseKey = Object.keys(diseases || {})[0] || 'rat-fever';

    const demoDiseaseName = diseases?.[demoDiseaseKey]?.name || 'Rat Fever';



    await pushAuditEvent({ ts: s2Start + 40*min, category: 'diseases', action: 'disease.contracted', actor, sessionId: session2Id, target: { charId: char2.id, charName: char2.name }, summary: `🦠 ${char2.name} contracted ${demoDiseaseName}`, details: { diseaseId: demoDiseaseKey, diseaseName: demoDiseaseName, source: 'Giant Plague Rat bite' }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s2Start + 50*min, category: 'diseases', action: 'disease.symptom', actor, sessionId: session2Id, target: { charId: char2.id, charName: char2.name }, summary: `🦠 Symptom revealed: Fever`, details: { diseaseId: demoDiseaseKey, symptom: 'Fever', effect: '-1 to Constitution checks' }, isDemo: true, importance: 'minor' });



    await pushAuditEvent({ ts: s2Start + 60*min, category: 'messages', action: 'message.npc', actor, sessionId: session2Id, summary: '💬 Talked to Healer Wynn', details: { npcName: 'Healer Wynn', contentPreview: 'I can slow the fever, but the only cure grows deep in the Darkwood - the Moonpetal flower.', kind: 'npc' }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s2Start + 65*min, category: 'quest', action: 'quest.accepted', actor, sessionId: session2Id, summary: '📜 Quest: Gather Moonpetal Flowers', details: { questName: 'Gather Moonpetal Flowers', questGiver: 'Healer Wynn', objectives: ['Find 5 Moonpetal Flowers in Darkwood', 'Return before the fever takes hold'] }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s2Start + 75*min, category: 'travel', action: 'travel.location', actor, sessionId: session2Id, summary: '📍 Set out on Darkwood Road', details: { from: 'Millbrook Village', to: 'Darkwood Road' }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s2Start + 90*min, category: 'combat', action: 'combat.start', actor, sessionId: session2Id, summary: '⚔️ Combat: Bandit Ambush', details: { enemies: ['Bandit ×4', 'Bandit Captain'], difficulty: 'medium', surprise: 'enemies' }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s2Start + 100*min, category: 'combat', action: 'combat.damage', actor, sessionId: session2Id, target: { charId: char1.id, charName: char1.name }, summary: `${char1.name} hit by crossbow bolt`, details: { damage: 12, source: 'Bandit crossbow' }, isDemo: true, importance: 'audit' });



    await pushAuditEvent({ ts: s2Start + 110*min, category: 'combat', action: 'combat.end', actor, sessionId: session2Id, summary: '⚔️ Victory: Bandits defeated', details: { outcome: 'victory', rounds: 6, killingBlow: char1.name, totalDamageToParty: 28, note: 'Bandit Captain captured' }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s2Start + 115*min, category: 'inventory', action: 'inventory.loot', actor, sessionId: session2Id, summary: '🎲 Loot acquired', details: { items: [{ name: 'Bandit Sword', qty: 1, recipient: char3.name }, { name: 'Gold', qty: 45, recipient: 'Party' }, { name: 'Map Fragment', qty: 1, recipient: char2.name }, { name: 'Healing Potion', qty: 2, recipient: 'Party' }] }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s2Start + 120*min, category: 'messages', action: 'message.npc', actor, sessionId: session2Id, summary: '💬 Interrogated Bandit Captain', details: { npcName: 'Bandit Captain', contentPreview: 'We were hired! Someone in the village paid us to stop anyone heading to the forest...', kind: 'npc' }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s2Start + 130*min, category: 'diseases', action: 'disease.symptom', actor, sessionId: session2Id, target: { charId: char2.id, charName: char2.name }, summary: `🦠 Symptom revealed: Weakness`, details: { diseaseId: demoDiseaseKey, symptom: 'Weakness', effect: '-2 to Strength' }, isDemo: true, importance: 'minor' });



    await pushAuditEvent({ ts: s2End, category: 'session', action: 'session.end', actor, sessionId: session2Id, summary: '📅 Session ended: Rising Tension', details: { duration: s2End - s2Start, eventCount: 18 }, isDemo: true });



    // ========== SESSION 3: "The Cure" ==========

    const s3Start = now - (1 * hour);

    const s3End = now - (5 * min);



    const session3Id = await pushAuditSession({

      name: 'The Cure',

      notes: 'The party ventures into Darkwood, faces a powerful enemy, and finds the cure.',

      startedAt: s3Start,

      startedBy: 'DM',

      endedAt: s3End,

      endedBy: 'DM',

      createdAt: now,

      isDemo: true,

      arc: 'Act 1: The Plague',

    });



    await pushAuditEvent({ ts: s3Start, category: 'session', action: 'session.start', actor, sessionId: session3Id, summary: '📅 Session started: The Cure', details: { sessionName: 'The Cure' }, isDemo: true });



    await pushAuditEvent({ ts: s3Start + 3*min, category: 'travel', action: 'travel.location', actor, sessionId: session3Id, summary: '📍 Entered Darkwood Forest', details: { from: 'Darkwood Road', to: 'Darkwood Forest' }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s3Start + 5*min, category: 'messages', action: 'message.dm', actor, sessionId: session3Id, summary: '💬 DM Narration', details: { contentPreview: 'The forest grows dark and twisted. Ancient trees block out the sun, and strange whispers echo between the branches...', kind: 'narration' }, isDemo: true, importance: 'minor' });



    await pushAuditEvent({ ts: s3Start + 10*min, category: 'combat', action: 'combat.start', actor, sessionId: session3Id, summary: '⚔️ Combat: Giant Spiders', details: { enemies: ['Giant Spider ×3', 'Phase Spider'], difficulty: 'medium' }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s3Start + 18*min, category: 'combat', action: 'combat.end', actor, sessionId: session3Id, summary: '⚔️ Victory: Giant Spiders slain', details: { outcome: 'victory', rounds: 5, killingBlow: char2.name, totalDamageToParty: 18 }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s3Start + 20*min, category: 'inventory', action: 'inventory.loot', actor, sessionId: session3Id, summary: '🎲 Loot acquired', details: { items: [{ name: 'Spider Silk', qty: 5, recipient: char2.name }, { name: 'Phase Spider Venom', qty: 1, recipient: char1.name }] }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s3Start + 22*min, category: 'quest', action: 'quest.progress', actor, sessionId: session3Id, summary: '📜 Found Moonpetal Flowers (3/5)', details: { questName: 'Gather Moonpetal Flowers', progress: '3/5' }, isDemo: true, importance: 'minor' });



    await pushAuditEvent({ ts: s3Start + 25*min, category: 'combat', action: 'combat.start', actor, sessionId: session3Id, summary: '⚔️ Combat: Corrupted Treant', details: { enemies: ['Corrupted Treant'], difficulty: 'hard', note: 'Guardian of the flower grove' }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s3Start + 28*min, category: 'combat', action: 'combat.damage', actor, sessionId: session3Id, target: { charId: char3.id, charName: char3.name }, summary: `${char3.name} slammed for 15 damage`, details: { damage: 15, source: 'Treant slam' }, isDemo: true, importance: 'audit' });



    await pushAuditEvent({ ts: s3Start + 35*min, category: 'combat', action: 'combat.end', actor, sessionId: session3Id, summary: '⚔️ Victory: Corrupted Treant felled', details: { outcome: 'victory', rounds: 8, killingBlow: char1.name, totalDamageToParty: 35, note: 'Hard fought battle' }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s3Start + 36*min, category: 'quest', action: 'quest.progress', actor, sessionId: session3Id, summary: '📜 Found Moonpetal Flowers (5/5)', details: { questName: 'Gather Moonpetal Flowers', progress: '5/5' }, isDemo: true, importance: 'minor' });



    await pushAuditEvent({ ts: s3Start + 37*min, category: 'inventory', action: 'inventory.loot', actor, sessionId: session3Id, summary: '🎲 Loot acquired', details: { items: [{ name: 'Moonpetal Flower', qty: 5, recipient: char2.name }, { name: 'Treant Heartwood', qty: 1, recipient: char3.name }, { name: 'Gold', qty: 30, recipient: 'Party' }] }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s3Start + 38*min, category: 'messages', action: 'message.whisper', actor, sessionId: session3Id, target: { charId: char2.id, charName: char2.name }, summary: `💬 Whisper to ${char2.name}`, details: { contentPreview: 'As you touch the flowers, you hear a voice: "The shadow stirs beneath the ruins... beware the one who waits..."', kind: 'whisper', senderName: 'Mysterious Voice' }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s3Start + 40*min, category: 'travel', action: 'travel.location', actor, sessionId: session3Id, summary: '📍 Discovered Ancient Ruins entrance', details: { from: 'Flower Grove', to: 'Ancient Ruins entrance', note: 'Possible location of the Moonstone' }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s3Start + 42*min, category: 'combat', action: 'combat.start', actor, sessionId: session3Id, summary: '⚔️ BOSS: Shadow Beast', details: { enemies: ['Shadow Beast'], difficulty: 'deadly', note: 'Guardian of the ruins' }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s3Start + 44*min, category: 'buffs', action: 'buff.added', actor, sessionId: session3Id, target: { charId: char1.id, charName: char1.name }, summary: `✨ Buff: Heroic Surge → ${char1.name}`, details: { buff: 'Heroic Surge (+2 to attacks)', source: 'Battle inspiration' }, isDemo: true, importance: 'minor' });



    await pushAuditEvent({ ts: s3Start + 48*min, category: 'combat', action: 'combat.damage', actor, sessionId: session3Id, target: { charId: char1.id, charName: char1.name }, summary: `${char1.name} took 20 shadow damage`, details: { damage: 20, source: 'Shadow claw', damageType: 'necrotic' }, isDemo: true, importance: 'audit' });



    await pushAuditEvent({ ts: s3Start + 50*min, category: 'combat', action: 'combat.end', actor, sessionId: session3Id, summary: '⚔️ VICTORY: Shadow Beast vanquished!', details: { outcome: 'victory', rounds: 10, killingBlow: char1.name, totalDamageToParty: 55, note: 'Epic boss battle!' }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s3Start + 51*min, category: 'inventory', action: 'inventory.loot', actor, sessionId: session3Id, summary: '🎁 Major loot acquired!', details: { items: [{ name: 'Shadow Essence', qty: 1, recipient: char1.name }, { name: 'Cloak of Shadows', qty: 1, recipient: char2.name }, { name: 'Gold', qty: 150, recipient: 'Party' }, { name: 'Ancient Rune Stone', qty: 1, recipient: 'Party' }] }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s3Start + 52*min, category: 'travel', action: 'travel.location', actor, sessionId: session3Id, summary: '📍 Returned to Millbrook Village', details: { from: 'Darkwood Forest', to: 'Millbrook Village' }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s3Start + 53*min, category: 'quest', action: 'quest.completed', actor, sessionId: session3Id, summary: '✅ Quest Complete: Gather Moonpetal Flowers', details: { questName: 'Gather Moonpetal Flowers', rewards: ["Healer's gratitude", 'Free healing for party', 'Reputation increase'] }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s3Start + 54*min, category: 'diseases', action: 'disease.cured', actor, sessionId: session3Id, target: { charId: char2.id, charName: char2.name }, summary: `✨ ${char2.name} cured of ${demoDiseaseName}!`, details: { diseaseId: demoDiseaseKey, diseaseName: demoDiseaseName, curedBy: 'Healer Wynn using Moonpetal Flowers' }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s3Start + 55*min, category: 'messages', action: 'message.npc', actor, sessionId: session3Id, summary: '💬 Celebrated by villagers', details: { npcName: 'Village Elder', contentPreview: 'You have saved our village! The plague will spread no more. But tell me... what did you find in those ruins?', kind: 'npc' }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s3Start + 56*min, category: 'quest', action: 'quest.accepted', actor, sessionId: session3Id, summary: '📜 Quest: Investigate the Ancient Ruins', details: { questName: 'Investigate the Ancient Ruins', questGiver: 'Party decision', objectives: ['Return to the ruins', 'Find the source of the shadow corruption', 'Locate the Moonstone'] }, isDemo: true, importance: 'major' });



    await pushAuditEvent({ ts: s3End, category: 'session', action: 'session.end', actor, sessionId: session3Id, summary: '📅 Session ended: The Cure', details: { duration: s3End - s3Start, eventCount: 24 }, isDemo: true });



    return session1Id; // Return first session for UI to navigate to

  };

  const revertAuditEvent = async (ev) => {

    if (!isDM) return;

    if (!ev?.revert || ev.revertedAt) return;



    const actor = getActor();

    const now = tsNow();



    if (ev.revert.kind === 'charPatch') {

      const { charId, patch, deleteKeys } = ev.revert;

      const current = chars?.[charId];

      if (!current) return window.alert('Cannot revert: character not found.');



      const updated = { ...current, ...(patch || {}) };

      (deleteKeys || []).forEach(k => { try { delete updated[k]; } catch (e) {} });



      // Apply without generating the normal per-char audit events

      saveChars({ ...chars, [charId]: updated }, { suppress: true });



      // Mark original event as reverted

      if (ev.id) {

        updateGameData({

          [`audit/events/${ev.id}/revertedAt`]: now,

          [`audit/events/${ev.id}/revertedBy`]: actor.name || 'DM',

        });

      }



      // Log revert as its own event

      auditFF(pushAuditEvent({

        ts: now,

        category: 'session',

        action: 'audit.revert',

        actor,

        sessionId: audit?.activeSessionId || ev.sessionId || null,

        summary: `↩️ Reverted: ${ev.summary || 'change'}`,

        details: { revertedEventId: ev.id || null, charId },

        revert: null,



      }), 'pushAuditEvent');

    }

  };



  // Connection status indicator component

  const ConnectionIndicator = () => {

    const statusColors = {

      connected: '#27c95a',

      disconnected: '#c92727',

      checking: '#c9a227',

      error: '#c92727',

      local: '#888',

    };

    const statusText = {

      connected: '🟢 Online',

      disconnected: '🔴 Offline',

      checking: '🟡 Connecting...',

      error: '🔴 Error',

      local: '⚪ Local Only',

    };

    return (

      <div style={{ 

        position: 'fixed', 

        bottom: '10px', 

        right: '10px', 

        padding: '4px 10px',

        background: 'rgba(0,0,0,0.7)',

        borderRadius: '4px',

        fontSize: '0.7rem',

        color: statusColors[connectionStatus] || '#888',

        border: `1px solid ${statusColors[connectionStatus] || '#888'}`,

        zIndex: 1000,

      }}>

        {statusText[connectionStatus] || connectionStatus}

      </div>

    );

  };

  

  // Parse and roll complex dice notation (e.g., "1d6+2d8+5")

  const parseAndRollDice = (notation) => {

    if (!notation.trim()) return null;

    

    const parts = [];

    let total = 0;

    

    // Split by + and - while keeping the operators

    const tokens = notation.replace(/\s/g, '').split(/(?=[+-])/);

    

    for (const token of tokens) {

      const trimmed = token.replace(/^\+/, ''); // Remove leading +

      const isNegative = trimmed.startsWith('-');

      const clean = trimmed.replace(/^-/, '');

      

      // Check if it's a dice roll (XdY format)

      const diceMatch = clean.match(/^(\d*)d(\d+)$/i);

      

      if (diceMatch) {

        const count = parseInt(diceMatch[1]) || 1;

        const sides = parseInt(diceMatch[2]);

        const rolls = [];

        

        for (let i = 0; i < count; i++) {

          rolls.push(Math.floor(Math.random() * sides) + 1);

        }

        

        const sum = rolls.reduce((a, b) => a + b, 0);

        const finalValue = isNegative ? -sum : sum;

        total += finalValue;

        

        parts.push({

          type: 'dice',

          notation: `${isNegative ? '-' : ''}${count}d${sides}`,

          rolls,

          sum: finalValue,

          // Show (sum) with individual rolls on hover

          display: `(${isNegative ? '-' : ''}${sum})`,

          detail: rolls.join('+'), // For tooltip

        });

      } else {

        // It's a flat number

        const num = parseInt(clean);

        if (!isNaN(num)) {

          const finalValue = isNegative ? -num : num;

          total += finalValue;

          parts.push({

            type: 'flat',

            notation: `${isNegative ? '-' : '+'}${num}`,

            value: finalValue,

            display: `${finalValue}`, // Just the number

          });

        }

      }

    }

    

    return { parts, total, notation };

  };



  const createChar = (name, race, maxHp, pw) => { const nc = createDefaultCharacter(name, race, maxHp, pw); saveChars({ ...chars, [nc.id]: nc }); return nc.id; };

  const deleteChar = cid => { const c = { ...chars }; delete c[cid]; saveChars(c); setCharId(null); setView('select'); };

  const loadDashboardTestCharacter = async () => {
    const testChar = createTestCharacter();
    const activeCampaignId = viewerContext?.activeCampaignId || null;
    const activeCampaignIdString = activeCampaignId ? String(activeCampaignId) : null;
    const existingTestCharacter = Object.values(chars || {}).find(candidate => (
      activeCampaignId
        ? (!candidate?.campaignId || String(candidate.campaignId) === activeCampaignIdString)
          && isCampaignTestCharacterPreset(candidate, LEGACY_DASHBOARD_TEST_PRESET_KEY)
        : candidate?.id === testChar.id || isCampaignTestCharacterPreset(candidate, LEGACY_DASHBOARD_TEST_PRESET_KEY)
    ));
    if (existingTestCharacter) {
      setDmNotification(`${testChar.name} is already loaded.`);
      setTimeout(() => setDmNotification(null), 3000);
      return;
    }

    if (activeCampaignId) {
      setDmNotification('Use the Campaign Sandbox for practice characters. Live campaign rows were not changed.');
      return;
    }

    saveChars({ ...chars, [testChar.id]: testChar });
    setDmNotification('🧝‍♀️ Practice character loaded!');
    setTimeout(() => setDmNotification(null), 3000);
  };



  const normalizeAuditHintForChar = (cid, hint) => {

    if (!hint) return null;

    if (hint.meta && !hint.perCharMeta && !hint.group && !hint.suppress && !hint.actor) {

      return { perCharMeta: { [cid]: hint.meta } };

    }

    // If it already looks like a full auditMeta payload, keep as-is

    if (hint.perCharMeta || hint.group || hint.suppress || hint.actor) return hint;

    // Otherwise treat as forced meta for this specific character

    return { perCharMeta: { [cid]: hint } };

  };



  const updateChar = (cid, upd, auditHint = null) => { 

    const c = { ...(charsRef.current || chars || {}) }; 

    const x = { ...c[cid], ...upd };

    const set = x.settings || DEFAULT_SETTINGS;

    

    // If daysWithoutFood is being set to 0, remove hungry and starvation effects

    if (upd.daysWithoutFood !== undefined && upd.daysWithoutFood === 0) {

      Object.assign(x, clearFoodEffectState(x, set, {

        removeWellFed: false,

        removeHungry: true,

        removeStarvation: true,

        resetStarving: true,

        resetDaysToStarvation: true,

      }));

    }

    

    c[cid] = x;

    charsRef.current = c;

    return saveChars(c, normalizeAuditHintForChar(cid, auditHint)); 

  };

  const isLocalSandboxPersona = (candidate) => (
    candidate?.isSandboxCharacter === true || candidate?.campaignCharacterKind === 'sandbox'
  );

  const showSandboxNotification = (message) => {
    setDmNotification(message);
    setTimeout(() => setDmNotification(null), 3000);
  };

  const saveSandboxChars = (nextChars, message = null) => {
    charsRef.current = nextChars;
    saveChars(nextChars, { suppress: true });
    if (message) showSandboxNotification(message);
  };

  const allocateSandboxCharId = (baseId, nextChars) => {
    let candidate = baseId || generateId();
    let suffix = 2;
    while (nextChars[candidate]) {
      candidate = `${baseId}-${suffix}`;
      suffix += 1;
    }
    return candidate;
  };

  const switchSandboxToDm = () => {
    if (!isCampaignSandboxSession) return;
    setShowSandboxParty(false);
    setShowSandboxSettings(false);
    setSelectedDmCharId(null);
    setCharId(null);
    setIsDM(true);
    setView('dm');
  };

  const switchSandboxToPersona = (targetCharId) => {
    if (!isCampaignSandboxSession) return;
    const target = (charsRef.current || chars || {})[targetCharId];
    if (!isLocalSandboxPersona(target)) return;
    setShowSandboxParty(false);
    setShowSandboxSettings(false);
    setSelectedDmCharId(null);
    setCharId(targetCharId);
    setIsDM(false);
    setView('home');
  };

  const addSandboxDemoPersona = (presetKey) => {
    if (!isCampaignSandboxSession) return;
    const nextChars = { ...(charsRef.current || chars || {}) };
    Object.entries(nextChars).forEach(([cid, candidate]) => {
      if (candidate?.sandboxPresetKey === presetKey && isLocalSandboxPersona(candidate)) {
        delete nextChars[cid];
      }
    });
    const persona = buildCampaignSandboxDemoPersona({
      sandboxId: sandboxStorageContext.sandboxId || 'default',
      presetKey,
      categories: sandboxPresetCategories,
      enabledFeatures: runtimeCampaignEnabledFeatures,
      index: Object.keys(nextChars).length,
    });
    if (!persona) return;
    nextChars[persona.id] = persona;
    saveSandboxChars(nextChars, `${persona.name} is ready in the sandbox.`);
  };

  const addBlankSandboxPersona = ({ name } = {}) => {
    if (!isCampaignSandboxSession) return;
    const nextChars = { ...(charsRef.current || chars || {}) };
    const persona = buildCampaignSandboxBlankPersona({
      sandboxId: sandboxStorageContext.sandboxId || 'default',
      name,
      categories: sandboxPresetCategories,
      enabledFeatures: runtimeCampaignEnabledFeatures,
      index: Object.keys(nextChars).length,
    });
    const id = allocateSandboxCharId(persona.id, nextChars);
    const nextPersona = { ...persona, id };
    nextChars[id] = nextPersona;
    saveSandboxChars(nextChars, `${nextPersona.name} is ready in the sandbox.`);
  };

  const updateSandboxPresetCategories = (categories) => {
    if (!isCampaignSandboxSession) return;
    const nextCategories = normalizeSandboxPresetCategories(categories, runtimeCampaignEnabledFeatures);
    const nextSettings = {
      ...(globalSettings || {}),
      campaignSandbox: true,
      sandboxPresetCategories: nextCategories,
    };
    if (typeof saveGlobalSettingsRaw === 'function') {
      saveGlobalSettingsRaw(nextSettings);
    }
    const nextChars = { ...(charsRef.current || chars || {}) };
    Object.entries(nextChars).forEach(([cid, candidate]) => {
      if (!isLocalSandboxPersona(candidate)) return;
      if (candidate?.sandboxPresetKey) {
        const rebuilt = buildCampaignSandboxDemoPersona({
          sandboxId: sandboxStorageContext.sandboxId || 'default',
          presetKey: candidate.sandboxPresetKey,
          categories: nextCategories,
          enabledFeatures: runtimeCampaignEnabledFeatures,
          index: Object.keys(nextChars).length,
        });
        if (rebuilt) {
          nextChars[cid] = { ...rebuilt, id: cid };
        }
      } else {
        nextChars[cid] = {
          ...candidate,
          sandboxPresetCategories: nextCategories,
        };
      }
    });
    saveSandboxChars(nextChars, 'Sandbox preset categories updated.');
  };

  const handleApplyCharacterImportCreate = async ({ createArgs, patch, warnings, auditHint }) => {
    assertImportWriteSafeValue(patch, 'characterImport.patch');

    const importedBase = createDefaultCharacter(

      createArgs.name,

      createArgs.race,

      createArgs.maxHp,

      ''

    );

    const importedChar = { ...importedBase, ...patch };
    delete importedChar.password;

    // Reconstructed boundary-shaped payload for server-side re-validation
    // (Track C, docs/IMPORT-INTENT-PROBE.md F2). The mutation re-runs the
    // canonical import boundary checks and verifies the character blob is
    // pinned to this reviewed content before storing anything.
    const reviewedImportPayload = {
      mode: 'create',
      createArgs,
      patch,
      ...(patch?.importReceipt !== undefined ? { importReceipt: patch.importReceipt } : {}),
      ...(warnings !== undefined ? { warnings } : {}),
    };

    const requiresStandaloneImportAuthority = typeof onCharacterImportCreateApplied === 'function';
    let createdThroughImportAuthority = false;
    if (typeof createStandaloneCharacterFromImport === 'function') {
      try {
        await createStandaloneCharacterFromImport({
          charId: importedBase.id,
          data: JSON.stringify(importedChar),
          payload: JSON.stringify(reviewedImportPayload),
        });
        createdThroughImportAuthority = true;
      } catch (err) {
        const message = String(err?.message || err || '');
        if (
          requiresStandaloneImportAuthority
          || !/Could not find public function|No public function|createStandaloneCharacter/i.test(message)
        ) {
          throw err;
        }
      }
    }

    if (!createdThroughImportAuthority) {
      if (requiresStandaloneImportAuthority) {
        throw new Error('createStandaloneCharacter public function unavailable for character import.');
      }

      const nextChars = { ...(charsRef.current || chars || {}), [importedBase.id]: importedChar };

      charsRef.current = nextChars;

      await saveChars(nextChars, normalizeAuditHintForChar(importedBase.id, auditHint));
    }

    setShowDdbImport(false);

    setCharId(importedBase.id);

    setIsDM(false);

    setView('home');

    // The raw fallback can persist the character, but it cannot update
    // server-side viewer selection. Only enter the launch wait path after
    // the authority mutation has selected the new standalone character.
    if (createdThroughImportAuthority && typeof onCharacterImportCreateApplied === 'function') {
      onCharacterImportCreateApplied(importedBase.id);
    }

    return { characterId: importedBase.id };

  };

  const handleApplyCharacterImportUpdate = async ({ characterId: targetCharacterId, patch, auditHint }) => {
    assertImportWriteSafeValue(patch, 'characterImport.patch');

    const targetId = targetCharacterId || charId;

    if (!targetId) throw new Error('Import update requires a selected character.');

    await updateChar(targetId, patch, auditHint);

    setShowDdbUpdate(false);

    return { characterId: targetId };

  };



  // Drop concentration outside combat — clears the caster's concentratingOn AND scrubs the

  // spell's effects (activeEffects, buffList entries, buffSources) from every other char that

  // received it. Mirrors breakConcentration's targets-cleanup pass; combat already calls

  // breakConcentration directly when the in-combat Drop button is used.

  const dropConcentrationForChar = (casterId) => {

    const caster = chars[casterId];

    const spellName = caster?.concentratingOn?.name || caster?.concentratingOn || null;

    const effectGroupId = caster?.concentrationEffectGroupId || null;

    if (!caster || (!spellName && !effectGroupId)) return;

    const result = removeLinkedConcentrationState({

      casterId,

      spellName,

      effectGroupId,

      chars,

    });

    saveChars(result.chars, normalizeAuditHintForChar(casterId, { summary: `⚡ Dropped concentration on ${spellName || 'concentration'}`, category: 'spell' }));

  };



  const addQuickBuff = (cid, buff) => {

    const c = { ...chars };

    if (!(c[cid].buffList || []).includes(buff)) {

      c[cid].buffList = [...(c[cid].buffList || []), buff];

      c[cid].buffSources = { ...(c[cid].buffSources || {}), [buff]: 'Quick Add' };

    }

    saveChars(c);

  };

  

  const addQuickDebuff = (cid, debuff) => {

    const c = { ...chars };

    if (!(c[cid].debuffList || []).includes(debuff)) {

      c[cid].debuffList = [...(c[cid].debuffList || []), debuff];

      c[cid].buffSources = { ...(c[cid].buffSources || {}), [debuff]: 'Quick Add' };

    }

    saveChars(c);

  };



  const resetTravelLog = cid => {

    const c = { ...chars }, x = c[cid], set = x.settings || DEFAULT_SETTINGS;

    x.currentDay = 1; x.mealsToday = 0; x.daysWithoutFood = 0; x.daysToStarvation = set.starvationThreshold || 7;

    x.starving = false; x.mealLog = []; x.immuneToday = false; x.actionHistory = [];

    Object.assign(x, clearFoodEffectState(x, set, {

      removeWellFed: true,

      removeHungry: true,

      removeStarvation: true,

      resetStarving: true,

      resetDaysToStarvation: true,

    }));

    x.statusHistory = [{ timestamp: new Date().toISOString(), day: 1, event: 'reset', description: 'Day 1: Travel log reset' }, ...(x.statusHistory || [])];

    saveChars(c);

  };



  const resetGroupTravelDay = () => {

    const charIds = Object.keys(livePlayableChars || {});

    

    if (charIds.length === 0) {

      window.alert('No characters to reset!');

      return;

    }

    

    // Use the same pattern as manually setting values, which works

    const c = { ...chars };

    charIds.forEach(cid => {

      const original = c[cid];

      const set = original.settings || DEFAULT_SETTINGS;

      

      // Build the reset state - same as what updateChar does when daysWithoutFood=0

      c[cid] = {

        ...original,

        currentDay: 1,

        mealsToday: 0,

        daysWithoutFood: 0,

        daysToStarvation: set.starvationThreshold || 7,

        starving: false,

        mealLog: [],

        immuneToday: false,

        actionHistory: [],

        wasWellFed: false,

        ...clearFoodEffectState(original, set, {

          removeWellFed: true,

          removeHungry: true,

          removeStarvation: true,

          resetStarving: true,

          resetDaysToStarvation: true,

        }),

        statusHistory: [{ timestamp: new Date().toISOString(), day: 1, event: 'reset', description: 'Day 1: Group travel reset by DM' }, ...(original.statusHistory || [])]

      };

    });

    

    saveChars(c);

    saveGroupTravelLog([{ day: 1, character: 'DM', action: 'reset group to Day 1', timestamp: new Date().toISOString() }]);

  };



  const endDayForAll = () => {

    const charIds = Object.keys(livePlayableChars || {});

    

    if (charIds.length === 0) {

      window.alert('No characters!');

      return;

    }

    

    const events = [];

    const c = { ...chars };

    

    charIds.forEach(cid => {

      const original = c[cid];

      const set = original.settings || DEFAULT_SETTINGS;

      const x = { ...original, buffList: [...(original.buffList || [])], debuffList: [...(original.debuffList || [])], buffSources: { ...(original.buffSources || {}) }, statusHistory: [...(original.statusHistory || [])], mealLog: [...(original.mealLog || [])], diseaseList: [...(original.diseaseList || [])], permanentBuffs: [...(original.permanentBuffs || [])], permanentDebuffs: [...(original.permanentDebuffs || [])] };

      

      if (campaignHungerEnabled) {

        // Calculate hunger level for today

        const hungerLevel = getHungerLevel(x.mealsToday || 0);

        const hungerPoints = HUNGER_LEVELS[hungerLevel].points;

        

        // Add to weekly hunger score

        x.weeklyHungerScore = (x.weeklyHungerScore || 0) + hungerPoints;

        

        // Record daily hunger

        x.statusHistory.unshift({

          timestamp: new Date().toISOString(),

          day: x.currentDay,

          event: 'daily-hunger',

          description: `Day ${x.currentDay}: ${HUNGER_LEVELS[hungerLevel].name} (${hungerPoints >= 0 ? '+' : ''}${hungerPoints}) - Weekly total: ${x.weeklyHungerScore.toFixed(1)}`

        });
      }

      

      // Check if day meets diet requirements

      if (campaignDietsEnabled && x.activeDiet && diets[x.activeDiet]) {

        const diet = diets[x.activeDiet];

        const todayMeals = (x.mealLog || []).filter(m => m.day === x.currentDay);

        const mealTypes = todayMeals.map(m => m.mealType);

        

        if (checkDietDayRequirements(x.mealsToday || 0, mealTypes, diet)) {

          x.dietDayStreak = (x.dietDayStreak || 0) + 1;

        } else {

          // Diet requirements not met - streak broken

          if (x.dietDayStreak > 0) {

            x.statusHistory.unshift({

              timestamp: new Date().toISOString(),

              day: x.currentDay,

              event: 'diet-day-failed',

              description: `Day ${x.currentDay}: ${diet.name} diet requirements not met (need ${diet.minMealsPerDay}+ meals of ${diet.requiredMealTypes.join('/')})`

            });

          }

          x.dietDayStreak = 0;

        }

      }

      

      // Check if it's end of week (every 7 days)

      const isWeekEnd = (x.currentDay || 1) % 7 === 0;

      

      if (isWeekEnd) {

        const weekResult = processWeekEnd(x, runtimeDiseases, diets, {
          hungerEnabled: campaignHungerEnabled,
          dietsEnabled: campaignDietsEnabled,
          diseasesEnabled: campaignDiseasesEnabled,
        });

        

        // Apply week end updates

        Object.assign(x, weekResult.updates);

        

        // Add events

        events.push(...weekResult.events);

      }

      

      // Legacy compatibility - still track old fields loosely

      if (campaignHungerEnabled && (x.mealsToday || 0) === 0 && !x.immuneToday) {

        x.daysWithoutFood = (x.daysWithoutFood || 0) + 1;

      } else if (campaignHungerEnabled && (x.mealsToday || 0) > 0) {

        x.daysWithoutFood = Math.max(0, (x.daysWithoutFood || 0) - 1);

      }

      

      // Advance day

      x.currentDay = (x.currentDay || 1) + 1;

      x.mealsToday = 0;

      x.immuneToday = false;

      x.actionHistory = [];

      

      // Add day end to status history

      x.statusHistory.unshift({

        timestamp: new Date().toISOString(),

        day: x.currentDay - 1,

        event: 'day-end',

        description: `Day ${x.currentDay - 1}: Day ended`

      });

      

      c[cid] = x;

    });

    

    saveChars(c);

    const day = c[charIds[0]]?.currentDay - 1 || 1;

    saveGroupTravelLog([{ day, character: 'DM', action: 'ended day for all', events, timestamp: new Date().toISOString() }, ...(groupTravelLog || []).slice(0, 199)]);

  };



  // End week manually for all characters

  const endWeekForAll = () => {

    const charIds = Object.keys(livePlayableChars || {});

    

    if (charIds.length === 0) {

      window.alert('No characters!');

      return;

    }

    

    const events = [];

    const c = { ...chars };

    

    charIds.forEach(cid => {

      const x = { ...c[cid], statusHistory: [...(c[cid].statusHistory || [])], diseaseList: [...(c[cid].diseaseList || [])], permanentBuffs: [...(c[cid].permanentBuffs || [])], permanentDebuffs: [...(c[cid].permanentDebuffs || [])], buffSources: { ...(c[cid].buffSources || {}) } };

      

      const weekResult = processWeekEnd(x, runtimeDiseases, diets, {
        hungerEnabled: campaignHungerEnabled,
        dietsEnabled: campaignDietsEnabled,
        diseasesEnabled: campaignDiseasesEnabled,
      });

      Object.assign(x, weekResult.updates);

      events.push(...weekResult.events);

      

      c[cid] = x;

    });

    

    saveChars(c);

    saveGroupTravelLog([{ day: c[charIds[0]]?.currentDay || 1, character: 'DM', action: 'ended week for all', events, timestamp: new Date().toISOString() }, ...(groupTravelLog || []).slice(0, 199)]);

    setDmNotification('📅 Week ended for all characters!');

    setTimeout(() => setDmNotification(null), 3000);

  };



  const logMeal = (cid, mt, count = 1, withdrawFood = true) => {
    if (!campaignHungerEnabled && !campaignFoodSettingsEnabled) return;

    const ch = chars[cid];

    const inv = calcFoodInventory(food.foodLog);

    let actual;

    if (mt === 'auto') { if (campaignDietsEnabled && ch.activeDiet && hasDietFood(inv, ch.activeDiet, diets)) actual = getDietFoodType(inv, ch.activeDiet, diets); else actual = getRandomFoodType(inv); }

    else if (mt === 'any') actual = getRandomFoodType(inv);

    else actual = mt;

    if (withdrawFood && (!actual || !inv[actual] || inv[actual] < count)) return window.alert('Not enough food available!');

    if (ch.mealsToday + count > 3) return window.alert('Max 3 meals/day!');

    const c = { ...chars }, x = c[cid], set = x.settings || DEFAULT_SETTINGS;

    const prevState = { mealsToday: x.mealsToday, daysWithoutFood: x.daysWithoutFood, daysToStarvation: x.daysToStarvation, dietWeekProgress: x.dietWeekProgress, dietDayStreak: x.dietDayStreak, activeDiet: x.activeDiet, buffList: [...(x.buffList || [])], debuffList: [...(x.debuffList || [])], buffSources: { ...(x.buffSources || {}) }, starving: x.starving, mealLog: [...(x.mealLog || [])], statusHistory: [...(x.statusHistory || [])] };

    for (let i = 0; i < count; i++) { x.mealsToday += 1; if (x.daysWithoutFood > 0) x.daysWithoutFood -= 1; if (x.daysToStarvation < set.starvationThreshold) x.daysToStarvation += 1; x.mealLog = [{ day: x.currentDay, mealType: actual, timestamp: new Date().toISOString() }, ...(x.mealLog || []).slice(0, 59)]; }

    

    // Check if this meal breaks diet requirements (only for diets requiring all meals match)

    if (campaignDietsEnabled && x.activeDiet && diets[x.activeDiet]) {

      const diet = diets[x.activeDiet];

      if (diet.allMealsMustMatch && !diet.requiredMealTypes.includes(actual)) {

        x.dietDayStreak = 0;

        x.statusHistory = [{ timestamp: new Date().toISOString(), day: x.currentDay, event: 'diet-broken', description: `Day ${x.currentDay}: Diet broken! Ate ${actual} (need ${diet.requiredMealTypes.join('/')}).` }, ...(x.statusHistory || [])];

      }

    }

    

    // Show hunger level after meal

    const hungerLevel = getHungerLevel(x.mealsToday);

    const hungerInfo = HUNGER_LEVELS[hungerLevel];

    

    x.actionHistory = [{ type: 'meal', prevState, foodType: actual, count, withdrawFood, timestamp: new Date().toISOString() }, ...(x.actionHistory || []).slice(0, 19)];

    saveChars(c);

    if (withdrawFood) saveFood({ ...food, totalUnits: (food.totalUnits || 0) - count, foodLog: [{ date: new Date().toISOString(), unitsConsumed: count, character: x.name, foodType: actual }, ...(food.foodLog || []).slice(0, 99)] });

    saveGroupTravelLog([{ day: x.currentDay, character: x.name, action: 'meal', foodType: actual, count, withdrawFood, timestamp: new Date().toISOString() }, ...(groupTravelLog || []).slice(0, 199)]);

    setMealType('auto');

  };



  const undoLastAction = (cid) => {

    const c = { ...chars }, x = c[cid];

    if (!x.actionHistory || x.actionHistory.length === 0) return window.alert('Nothing to undo!');

    const lastAction = x.actionHistory[0], prev = lastAction.prevState;

    Object.assign(x, { mealsToday: prev.mealsToday, daysWithoutFood: prev.daysWithoutFood, daysToStarvation: prev.daysToStarvation, dietProgress: prev.dietProgress, activeDiet: prev.activeDiet, buffList: prev.buffList, debuffList: prev.debuffList, buffSources: prev.buffSources, starving: prev.starving, mealLog: prev.mealLog });

    x.statusHistory = [{ timestamp: new Date().toISOString(), day: x.currentDay, event: 'undo', description: `Day ${x.currentDay}: Undid ${lastAction.type}` }, ...prev.statusHistory];

    x.actionHistory = x.actionHistory.slice(1);

    if (lastAction.type === 'meal' && lastAction.withdrawFood && lastAction.foodType) saveFood({ ...food, totalUnits: (food.totalUnits || 0) + (lastAction.count || 1), foodLog: [{ date: new Date().toISOString(), unitsAdded: lastAction.count || 1, notes: 'Undo', foodType: lastAction.foodType }, ...(food.foodLog || []).slice(0, 99)] });

    saveChars(c);

  };



  const advanceDay = cid => {

    const c = { ...chars }, x = c[cid], set = x.settings || DEFAULT_SETTINGS;
    let nextGroupTravelLog = [...(groupTravelLog || [])];
    let nextNpcLibraryForDayRules = npcLibrary;
    const daySystemRuleNotices = [];
    const daySystemRuleBundles = [];
    const consumeDaySystemRuleSideEffects = (results, {
      charForEffects = x,
      label = 'Day system rule',
      source = 'travel.day-rule',
      triggerContext = null,
      worldContext = null,
      statusAuditAction = 'travel.day-rule-status',
    } = {}) => {
      if (!results) return;
      const effectiveWorldContext = {
        ...(worldContext || {}),
        chars: {
          ...((worldContext || {}).chars || c),
          ...(charForEffects?.id ? { [charForEffects.id]: charForEffects } : {}),
        },
        npcLibrary: nextNpcLibraryForDayRules,
      };
      const sideEffects = collectRuleResultSideEffects(results, { npcLibrary: nextNpcLibraryForDayRules });
      if (sideEffects.npcLibraryChanged && typeof saveNpcLibrary === 'function') {
        nextNpcLibraryForDayRules = sideEffects.nextNpcLibrary;
        saveNpcLibrary(nextNpcLibraryForDayRules);
      }
      const sentStatusMessages = persistSystemRuleStatusMessages(sideEffects.statusMessages, {
        char: charForEffects,
        source,
        messages,
        onSendStatusMessage: handleSendStatusMessage,
        onSaveMessages: saveMessages,
        auditMeta: { category: 'systems', action: statusAuditAction },
      });
      const ruleLogEntries = buildSystemRuleTravelLogEntries(sideEffects.logEntries, {
        char: charForEffects,
        action: 'system-rule-log',
        source,
      });
      if (ruleLogEntries.length > 0) {
        nextGroupTravelLog = [...ruleLogEntries, ...nextGroupTravelLog].slice(0, 200);
      }
      const queuedPendingSaves = buildSystemRulePendingSaveQueueEntriesForCharacter(sideEffects.pendingSaves, {
        char: charForEffects,
        systems,
        worldContext: effectiveWorldContext,
        triggerContext,
        label,
        source,
      });
      if (queuedPendingSaves.length > 0) {
        setDmSystemRuleSaveQueue(prev => [...(prev || []), ...queuedPendingSaves]);
      }
      daySystemRuleNotices.push(...(sideEffects.notifications || []));
      if (queuedPendingSaves.length > 0) {
        const pendingSaveNotice = buildSystemRulePendingSaveNotice(sideEffects.pendingSaves, label);
        daySystemRuleNotices.push(`${pendingSaveNotice || `${label} requested ${queuedPendingSaves.length} saving throw${queuedPendingSaves.length === 1 ? '' : 's'}.`} Queued for resolution.`);
      } else if (sideEffects.pendingSaves.length > 0) {
        const pendingSaveNotice = buildSystemRulePendingSaveNotice(sideEffects.pendingSaves, label);
        if (pendingSaveNotice) daySystemRuleNotices.push(pendingSaveNotice);
      }
      if (ruleLogEntries.length > 0) {
        daySystemRuleNotices.push(`${ruleLogEntries.length} system rule log entr${ruleLogEntries.length === 1 ? 'y' : 'ies'} recorded.`);
      }
      if (sentStatusMessages.length > 0) {
        daySystemRuleNotices.push(`${sentStatusMessages.length} system status message${sentStatusMessages.length === 1 ? '' : 's'} sent.`);
      }
    };

    

    if (campaignHungerEnabled) {

      // Calculate hunger level for today

      const hungerLevel = getHungerLevel(x.mealsToday || 0);

      const hungerPoints = HUNGER_LEVELS[hungerLevel].points;

      

      // Add to weekly hunger score

      x.weeklyHungerScore = (x.weeklyHungerScore || 0) + hungerPoints;

      

      // Record daily hunger

      x.statusHistory = [{ 

        timestamp: new Date().toISOString(), 

        day: x.currentDay, 

        event: 'daily-hunger',

        description: `Day ${x.currentDay}: ${HUNGER_LEVELS[hungerLevel].name} (${hungerPoints >= 0 ? '+' : ''}${hungerPoints}) - Weekly: ${x.weeklyHungerScore.toFixed(1)}`

      }, ...(x.statusHistory || [])];
    }

    

    // Handle immune day

    if (campaignHungerEnabled && x.immuneToday && x.mealsToday === 0) { 

      x.statusHistory = [{ timestamp: new Date().toISOString(), day: x.currentDay, event: 'immune-day', description: `Day ${x.currentDay}: Immune to hunger` }, ...x.statusHistory]; 

      nextGroupTravelLog = [{ day: x.currentDay, character: x.name, action: 'immune', timestamp: new Date().toISOString() }, ...nextGroupTravelLog].slice(0, 200);

    }

    

    // Check if day meets diet requirements

    if (campaignDietsEnabled && x.activeDiet && diets[x.activeDiet]) {

      const diet = diets[x.activeDiet];

      const todayMeals = (x.mealLog || []).filter(m => m.day === x.currentDay);

      const mealTypes = todayMeals.map(m => m.mealType);

      

      if (checkDietDayRequirements(x.mealsToday || 0, mealTypes, diet)) {

        x.dietDayStreak = (x.dietDayStreak || 0) + 1;

      } else {

        // Diet requirements not met - streak broken

        if (x.dietDayStreak > 0) {

          x.statusHistory = [{ 

            timestamp: new Date().toISOString(), 

            day: x.currentDay, 

            event: 'diet-day-failed',

            description: `Day ${x.currentDay}: ${diet.name} requirements not met (need ${diet.minMealsPerDay}+ meals of ${diet.requiredMealTypes.join('/')})`

          }, ...x.statusHistory];

        }

        x.dietDayStreak = 0;

      }

    }

    

    // Check if it's end of week (every 7 days)

    const isWeekEnd = (x.currentDay || 1) % 7 === 0;

    

    if (isWeekEnd) {

      const weekResult = processWeekEnd(x, runtimeDiseases, diets, {
        hungerEnabled: campaignHungerEnabled,
        dietsEnabled: campaignDietsEnabled,
        diseasesEnabled: campaignDiseasesEnabled,
      });

      

      // Apply week end updates

      Object.assign(x, weekResult.updates);

      

      // Show congrats if diet completed

      const dietComplete = weekResult.events.find(e => e.event === 'diet-complete');

      if (dietComplete) {

        setShowCongrats({ diet: dietComplete.diet, buffs: diets[dietComplete.diet]?.completionBuffs || [] });

      }

      

      // Execute on-week-end automation rules.

      // worldContext threads char-collection + merged journal view for cross-cutting

      // conditions (has-disease / quest-status / party-has-system — Session 1).

      const worldContext = {

        chars: c,

        journal: [...(journal || []), ...(dmJournal || [])],

        currentLocation,

        npcLibrary, // Session 5 — NPC state conditions

      };

      const weekEndRuleResults = systemsEnabled
        ? processAllSystemRulesForTrigger('on-week-end', x, runtimeSystems, null, worldContext)
        : createEmptySystemRuleEvaluationResult(x);

      const weekEndUpdates = systemsEnabled
        ? applySystemRuleResults(x, weekEndRuleResults, runtimeSystems, worldContext)
        : {};

      Object.assign(x, weekEndUpdates);

      daySystemRuleBundles.push({
        results: weekEndRuleResults,
        label: `${x.name || 'Character'} week-end rule`,
        source: 'travel.week-end',
        triggerContext: { day: x.currentDay, weekEnd: true },
        worldContext,
        statusAuditAction: 'travel.week-end-status',
      });



      // Session 5 — apply any pending NPC updates emitted by on-week-end rules.





      // Surface week-end rule notifications to the DM notification channel

      if (false && weekEndRuleResults.notifications.length > 0) {

        setDmNotification(`📅 Week-end: ${weekEndRuleResults.notifications.join(' • ')}`);

        setTimeout(() => setDmNotification(null), 5000);

      }

    }



    // Execute on-day-start automation rules for the new day

    const dayStartWorldContext = {

      chars: c,

      journal: [...(journal || []), ...(dmJournal || [])],

      currentLocation,

      npcLibrary, // Session 5 — NPC state conditions

    };

    const dayStartRuleResults = systemsEnabled
      ? processAllSystemRulesForTrigger('on-day-start', x, runtimeSystems, null, dayStartWorldContext)
      : createEmptySystemRuleEvaluationResult(x);

    const dayStartUpdates = systemsEnabled
      ? applySystemRuleResults(x, dayStartRuleResults, runtimeSystems, dayStartWorldContext)
      : {};

    Object.assign(x, dayStartUpdates);

    daySystemRuleBundles.push({
      results: dayStartRuleResults,
      label: `${x.name || 'Character'} day-start rule`,
      source: 'travel.day-start',
      triggerContext: { day: x.currentDay },
      worldContext: dayStartWorldContext,
      statusAuditAction: 'travel.day-start-status',
    });



    // Session 5 — apply any pending NPC updates emitted by on-day-start rules.





    // Tick 'days' expiries (Systems expansion Session 4). Happens after on-day-start so

    // rules that fire on the same day can still see the expiring system before it's gone.

    const dayExpiry = systemsEnabled ? processSystemLifecycleExpiries({
      char: x,
      tickKind: 'days',
      systems: runtimeSystems,
      worldContext: dayStartWorldContext,
    }) : createEmptySystemRuleEvaluationResult(x);

    if (dayExpiry.nextChar !== x) {

      Object.assign(x, dayExpiry.nextChar);

    }
    daySystemRuleBundles.push({
      results: dayExpiry,
      label: `${x.name || 'Character'} day expiry`,
      source: 'travel.day-expiry',
      triggerContext: { day: x.currentDay, tickKind: 'days' },
      worldContext: dayStartWorldContext,
      statusAuditAction: 'travel.day-expiry-status',
    });


    if (false && dayExpiry.notifications.length > 0) {

      setDmNotification(`⏳ Expired: ${dayExpiry.notifications.join(' • ')}`);

      setTimeout(() => setDmNotification(null), 5000);

    }



    // Surface day-start rule notifications to the DM notification channel

    if (false && dayStartRuleResults.notifications.length > 0) {

      setDmNotification(`🌅 Day start: ${dayStartRuleResults.notifications.join(' • ')}`);

      setTimeout(() => setDmNotification(null), 5000);

    }

    

    // Legacy compatibility - still track old fields loosely  

    if (campaignHungerEnabled && (x.mealsToday || 0) === 0 && !x.immuneToday) {

      x.daysWithoutFood = (x.daysWithoutFood || 0) + 1;

    } else if (campaignHungerEnabled && (x.mealsToday || 0) > 0) {

      x.daysWithoutFood = Math.max(0, (x.daysWithoutFood || 0) - 1);

    }

    

    // Advance day

    x.currentDay = (x.currentDay || 1) + 1; 

    x.mealsToday = 0; 

    x.immuneToday = false; 

    x.actionHistory = [];

    daySystemRuleBundles.forEach(bundle => {
      consumeDaySystemRuleSideEffects(bundle.results, {
        label: bundle.label,
        source: bundle.source,
        triggerContext: bundle.triggerContext,
        worldContext: {
          ...(bundle.worldContext || {}),
          chars: c,
          npcLibrary: nextNpcLibraryForDayRules,
        },
        statusAuditAction: bundle.statusAuditAction,
        charForEffects: x,
      });
    });

    if (daySystemRuleNotices.length > 0) {
      setDmNotification(daySystemRuleNotices.join(' | '));
      setTimeout(() => setDmNotification(null), 5000);
    }

    

    saveChars(c);

    saveGroupTravelLog([{ day: x.currentDay - 1, character: x.name, action: 'endday', timestamp: new Date().toISOString() }, ...nextGroupTravelLog].slice(0, 200));

  };



  const addFood = (units, notes, type) => { if (!campaignFoodSettingsEnabled) return; if (!type) return window.alert('Select food type'); const u = parseInt(units); saveFood({ ...food, totalUnits: (food.totalUnits || 0) + u, foodLog: [{ date: new Date().toISOString(), unitsAdded: u, notes: notes || '', foodType: type, addedBy: isDM ? 'DM' : char?.name }, ...(food.foodLog || []).slice(0, 99)] }, { summary: `🍖 Food added: +${u} (${type})${notes ? ' • ' + notes : ''}`, category: 'inventory', action: 'food.add', note: notes || '' }); };

  const removeFood = (units, notes, type) => { if (!campaignFoodSettingsEnabled) return; if ((food.totalUnits || 0) < units) return window.alert('Not enough!'); const inv = calcFoodInventory(food.foodLog); let actual = !type || type === 'any' ? getRandomFoodType(inv) : type; if (!actual || !inv[actual] || inv[actual] < units) return window.alert('Not enough of that type!'); const u = parseInt(units); saveFood({ ...food, totalUnits: (food.totalUnits || 0) - u, foodLog: [{ date: new Date().toISOString(), unitsConsumed: u, notes, foodType: actual }, ...(food.foodLog || []).slice(0, 99)] }, { summary: `🍖 Food consumed: -${u} (${actual})${notes ? ' • ' + notes : ''}`, category: 'inventory', action: 'food.consume', note: notes || '' }); };



  const login = cid => { const ch = chars[cid]; if (loginPw === DM_PASSWORD) { setIsDM(true); setCharId(cid); setView('dm'); } else if (ch?.password === loginPw) { setIsDM(false); setCharId(cid); setView('home'); } else return window.alert('Invalid password'); setLoginPw(''); setSelChar(null); };

  const dmLogin = () => { if (loginPw === DM_PASSWORD) { setIsDM(true); setView('dm'); setLoginPw(''); } else window.alert('Invalid DM password'); };

  const logout = () => {
    setCharId(null);
    setIsDM(false);
    setView('select');
    setShowSettings(false);
    setShowSheet(false);
    setShowDMSettings(false);
    setShowSandboxSettings(false);
    setShowSandboxParty(false);
    if (typeof onReturnToCampaignLanding === 'function') onReturnToCampaignLanding();
  };

  const exitCampaignSandbox = () => {
    setShowSettings(false);
    setShowSheet(false);
    setShowDMSettings(false);
    setShowSandboxSettings(false);
    setShowSandboxParty(false);
    if (typeof onReturnToCampaignLanding === 'function') {
      onReturnToCampaignLanding();
      return;
    }
    logout();
  };



  const buildCondensedLog = () => {

    const byDay = {};

    (groupTravelLog || []).forEach(e => {

      if (!byDay[e.day]) byDay[e.day] = { meals: {}, events: [], endedBy: null };

      if (e.action === 'meal') { if (!byDay[e.day].meals[e.character]) byDay[e.day].meals[e.character] = []; byDay[e.day].meals[e.character].push({ type: e.foodType, count: e.count || 1 }); }

      else if (e.action === 'endday') byDay[e.day].endedBy = e.character;

      else if (e.action === 'ended day for all') { byDay[e.day].dmEnded = true; byDay[e.day].specialEvents = e.events || []; }

      else if (e.action === 'immune') byDay[e.day].events.push({ char: e.character, type: 'immune' });

      else if (e.action && e.character) byDay[e.day].events.push({ char: e.character, type: e.action });

    });

    return byDay;

  };



  const CongratsModal = () => showCongrats ? <div style={S.modal} onClick={() => setShowCongrats(null)}><div style={{ ...S.modalContent, textAlign: 'center', maxWidth: '400px' }} onClick={e => e.stopPropagation()}><h1 style={{ color: '#ffd700', fontSize: '2.5rem', marginBottom: '16px' }}>🎉 Congratulations! 🎉</h1><h2 style={{ color: '#27c95a', marginBottom: '16px' }}>Diet Completed: {showCongrats.diet}</h2><p style={{ marginBottom: '16px' }}>You've earned permanent buffs:</p><div style={{ marginBottom: '24px' }}>{showCongrats.buffs.map((b, i) => <span key={i} style={{ ...S.badge, ...S.badgeBuffPerm, fontSize: '1rem', padding: '8px 16px' }}>{b}</span>)}</div><button style={{ ...S.btn, ...S.btnSuccess }} onClick={() => setShowCongrats(null)}>Awesome!</button></div></div> : null;

  

  const DmNotificationModal = () => dmNotification ? <div style={S.modal} onClick={() => setDmNotification(null)}><div style={{ ...S.modalContent, textAlign: 'center', maxWidth: '400px' }} onClick={e => e.stopPropagation()}><h2 style={{ color: '#ffd700', marginBottom: '16px' }}>⚠️ Notice</h2><p style={{ marginBottom: '16px' }}>{dmNotification}</p><button style={S.btn} onClick={() => setDmNotification(null)}>OK</button></div></div> : null;



  if (loading) return <div style={S.container}><div style={{ ...S.card, textAlign: 'center', marginTop: '100px' }}><div style={S.title}>⚔️ Loading... ⚔️</div><div style={{ marginTop: '12px', fontSize: '0.85rem', color: '#888' }}>{connectionStatus === 'checking' ? 'Connecting to server...' : ''}</div></div><ConnectionIndicator /></div>;

  // Floating chrome variables — must be declared AFTER all the

  // function declarations they reference (e.g. quickAddGoBack at

  // line ~55244, handleQuickAddSubmit, getActiveSession). The

  // player journal cluster has direct handler references like

  // `onClick={quickAddGoBack}` (not arrow-wrapped) which would

  // hit a TDZ error if these declarations ran before the function

  // definitions. Placing the whole chrome block after the

  // `if (loading) return` early-exit guarantees every callable is

  // initialized by the time we evaluate the JSX.

  // Bug-report button chrome. Computed once and inserted into every

  // view's return tree so the button is reachable from anywhere — login,

  // combat, shop, journal, dev pages — not just the home dashboard.

  // 2026-04-30: was previously mounted only in the player home / DM

  // dashboard / select view, leaving 11 other view branches without

  // access to "report a bug." Now declared as a single JSX expression

  // and embedded in each view's return.

  //

  // Render gated on `viewerContext !== undefined` for the same reason

  // it always was: demo mode (no Convex provider in the tree) can't

  // host the useQuery calls inside the button. `viewerContext === null`

  // (auth wired but user has no char yet) is fine and intentional —

  // the bug button is the FIRST thing a brand-new user might want.

  const bugReportChrome = viewerContext !== undefined && !isCampaignSandboxSession ? (

    <QuickReportButton

      context={{

        view, isDM, charId, audit,

        characterSnapshot: charId && chars && chars[charId] ? {

          charId,

          name: chars[charId].name,

          currentHp: chars[charId].currentHp,

          maxHp: chars[charId].maxHp,

          activeBuffs: chars[charId].buffList || [],

          activeDebuffs: chars[charId].debuffList || [],

          activeSystems: (chars[charId].systemList || []).map(s => ({ systemId: s.systemId, instanceKey: s.instanceKey, applied: s.applied })),

        } : null,

      }}

      onNavigateToReports={() => setView('bug-reports')}

      onNavigateToDev={() => setView('dev-dashboard')}

      journalOpen={floatingJournalOpen}

      sheetOpen={floatingSheetOpen}

      onCloseOtherSurfaces={closeOtherFloatingSurfaces}

    />

  ) : null;



  // Dice roller — always available across views (player and DM).

  const diceRollerChrome = (

    <DiceRollerPanel

      isOpen={showDiceRoller}

      onToggle={() => setShowDiceRoller(!showDiceRoller)}

      diceInput={diceInput}

      setDiceInput={setDiceInput}

      diceResult={diceResult}

      setDiceResult={setDiceResult}

      parseAndRollDice={parseAndRollDice}

    />

  );



  // Floating stat popup — player-only (DMs don't have a single character

  // to summarize). Conditional rendering means the JSX isn't constructed

  // when in DM mode, so no null-state access concerns.

  const statPopupChrome = (!isDM && char) ? (

    <CharacterSheetPanel

      char={char}

      showSheet={showSheet}

      setShowSheet={setShowSheet}

      diseases={runtimeDiseases}

      systems={runtimeSystems}

      isDM={isDM}

      globalSettings={runtimeGlobalSettings}

    />

  ) : null;



  // Player journal cluster — 2026-04-30: moved out of the home view

  // return tree into a chrome variable so the journal is reachable

  // from every player view (combat, shop, journal, crafting, etc.)

  // via floatingChrome. State references resolve through closure on

  // App scope, identical to before - only the mount location changed.

  const playerJournalChrome = (!isDM && char) ? (

    <>

      {/* Floating Journal Quick-Add */}

      {/* 2026-04-27: bumped showSheet offset from 356px to 400px so the journal

          doesn't tuck into the stat popup. Stat popup is min 320px wide at right:

          20px, so it ends ~340px from screen right; the popup grows wider with

          tab content. 400px gives consistent breathing room. */}

      {/* Journal cluster `right` handles four combinations of (drawer

          open?) × (stat sheet open?). When the bug-report drawer is open,

          the journal needs to clear the drawer's right column (right:12

          to right:452) — and if the stat sheet is ALSO open, the journal

          panel additionally needs to clear the stat popup (which sits at

          right:472 to ~right:832 when both drawer + sheet are open). */}

      {/* Journal cluster (player view) — uses the unified position rule

          from `computeJournalPos` above. See BUG-REPORT-REFERENCE.md §8. */}

      <div style={{ position: 'fixed', ...computeJournalPos(!!showQuickAddJournal), zIndex: 1500, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', transition: 'right 200ms ease-out, bottom 200ms ease-out' }}>

        {/* Confirmation toast */}

        {quickAddConfirmation && (

          <div style={{ 

            padding: '8px 14px', 

            background: quickAddConfirmation.error ? 'rgba(201,39,39,0.15)' : 'rgba(39,201,90,0.15)', 

            border: `1px solid ${quickAddConfirmation.error ? 'rgba(201,39,39,0.4)' : 'rgba(39,201,90,0.4)'}`, 

            borderRadius: '8px',

            fontSize: '0.8rem',

            color: quickAddConfirmation.error ? '#c92727' : '#27c95a',

            maxWidth: '280px',

            animation: 'overlayFadeIn 0.3s ease',

          }}>

            {quickAddConfirmation.error && <div>❌ {quickAddConfirmation.error}</div>}

            {quickAddConfirmation.newSession && <div style={{ marginBottom: '4px' }}>📖 Created Session #{quickAddConfirmation.newSession}</div>}

            {(quickAddConfirmation.created || []).map((c, i) => (

              <div key={`c-${i}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                <span style={{ cursor: c.id ? 'pointer' : 'default' }} onClick={() => { if (c.id) { const e = getQuickAddJournalBase().find(j => j.id === c.id); if (e) setQuickAddViewEntry(e); } }}>

                  {JOURNAL_CATEGORIES[c.category]?.icon} Created: <span style={{ textDecoration: c.id ? 'underline' : 'none' }}>{c.name}</span>

                </span>

                {c.id && <span onClick={() => { setJournalInitEntry(c); setView('journal'); closeQuickAddJournal(); setQuickAddConfirmation(null); }} style={{ fontSize: '0.6rem', color: '#c9a227', cursor: 'pointer', opacity: 0.8 }}>View →</span>}

              </div>

            ))}

            {(quickAddConfirmation.updated || []).map((u, i) => (

              <div key={`u-${i}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                <span style={{ cursor: u.id ? 'pointer' : 'default' }} onClick={() => { if (u.id) { const e = getQuickAddJournalBase().find(j => j.id === u.id); if (e) setQuickAddViewEntry(e); } }}>

                  {JOURNAL_CATEGORIES[u.category]?.icon} Updated: <span style={{ textDecoration: u.id ? 'underline' : 'none' }}>{u.name}</span>

                </span>

                {u.id && <span onClick={() => { setJournalInitEntry(u); setView('journal'); closeQuickAddJournal(); setQuickAddConfirmation(null); }} style={{ fontSize: '0.6rem', color: '#c9a227', cursor: 'pointer', opacity: 0.8 }}>View →</span>}

              </div>

            ))}

            {quickAddConfirmation.sessionUpdated && !quickAddConfirmation.newSession && <div>📖 Session updated</div>}

            {quickAddConfirmation.totalEntries && <div style={{ marginTop: '4px', fontSize: '0.7rem', color: '#888', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '4px' }}>📊 Journal now has {quickAddConfirmation.totalEntries} entries total</div>}

          </div>

        )}

        

        {/* Expanded panel */}

        {showQuickAddJournal && (() => {

          const qjScale = { normal: 1, large: 1.15, xlarge: 1.3 }[journalSettings?.quickJournalScale] || 1;

          const qjWidth = Math.round(({ compact: 320, normal: 360, wide: 440, full: 520 }[journalSettings?.quickJournalWidth] || 360) * qjScale);

          // 2026-04-29: on mobile, render via MobileBottomSheet so the

          // panel slides up like the stat card overlay (drag-down to

          // dismiss, backdrop-tap to dismiss, ESC, focus trap, no

          // page-scroll jump). Desktop keeps the inline floating panel.

          const isQjMobile = isMobileViewport;

          const inner = (

          <div style={isQjMobile ? {

            fontSize: qjScale !== 1 ? `${qjScale * 0.85}rem` : undefined,

          } : {

            width: `${qjWidth}px`,

            maxWidth: 'calc(100vw - 32px)',

            maxHeight: '80vh',

            overflowY: 'auto',

            background: S._colors?.bgCardSolid || '#1e1624',

            border: S.card?.border || '1px solid rgba(201,162,39,0.3)',

            borderRadius: S.card?.borderRadius || '10px',

            padding: '12px',

            boxShadow: S.card?.boxShadow || '0 8px 32px rgba(0,0,0,0.6)',

            fontSize: qjScale !== 1 ? `${qjScale * 0.85}rem` : undefined,

          }}>

            {/* Header */}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>

              <span style={{ fontSize: '0.85rem', color: S.subtitle?.color || '#c9a227', fontWeight: 'bold' }}>📓 Quick Journal</span>

              <div style={{ display: 'flex', gap: '4px' }}>

                <button 

                  onClick={() => setShowQuickAddRecent(!showQuickAddRecent)}

                  style={{ background: showQuickAddRecent ? (S.tabActive?.background || 'rgba(201,162,39,0.2)') : 'none', border: `1px solid ${S.input?.borderColor || 'rgba(255,255,255,0.1)'}`, borderRadius: '4px', color: S.tab?.color || '#888', cursor: 'pointer', padding: '2px 6px', fontSize: '0.75rem' }}

                  title="Recent entries"

                >📋</button>

                <button 

                  onClick={() => setShowQuickAddCheatSheet(!showQuickAddCheatSheet)}

                  style={{ background: showQuickAddCheatSheet ? (S.tabActive?.background || 'rgba(201,162,39,0.2)') : 'none', border: `1px solid ${S.input?.borderColor || 'rgba(255,255,255,0.1)'}`, borderRadius: '4px', color: S.tab?.color || '#888', cursor: 'pointer', padding: '2px 6px', fontSize: '0.75rem' }}

                  title="Tag reference"

                >?</button>

                <button 

                  onClick={closeQuickAddJournal}

                  style={{ background: 'none', border: `1px solid ${S.input?.borderColor || 'rgba(255,255,255,0.1)'}`, borderRadius: '4px', color: S.tab?.color || '#888', cursor: 'pointer', padding: '2px 6px', fontSize: '0.75rem' }}

                >✕</button>

              </div>

            </div>

            

            {/* Entry mode tabs */}

            <div style={{ display: 'flex', gap: '2px', marginBottom: '4px' }}>

              {[{ key: 'session', label: '📖 Session', tip: 'Log to active session' }, { key: 'standalone', label: '📝 Entry', tip: 'Create entry without session' }].map(m => (

                <button key={m.key}

                  onClick={() => setQuickAddEntryMode(m.key)}

                  title={m.tip}

                  style={{

                    padding: '2px 8px', fontSize: '0.6rem', cursor: 'pointer',

                    background: quickAddEntryMode === m.key ? (S.tabActive?.background || 'rgba(201,162,39,0.2)') : 'rgba(0,0,0,0.2)',

                    border: `1px solid ${quickAddEntryMode === m.key ? (S.subtitle?.color || 'rgba(201,162,39,0.4)') + '66' : 'rgba(255,255,255,0.06)'}`,

                    borderRadius: '4px',

                    color: quickAddEntryMode === m.key ? (S.subtitle?.color || '#c9a227') : (S.tab?.color || '#888'),

                  }}

                >{m.label}</button>

              ))}

            </div>

            

            {/* Session mode: session selector */}

            {quickAddEntryMode === 'session' && (

              <>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>

                  {(() => {

                    const session = getActiveSession();

                    return (

                      <>

                        <span

                          onClick={() => setShowSessionPicker(!showSessionPicker)}

                          style={{ 

                            fontSize: '0.7rem', color: session ? '#aaa' : '#c9a227', 

                            padding: '3px 8px', cursor: 'pointer',

                            background: session ? 'rgba(0,0,0,0.3)' : 'rgba(201,162,39,0.1)',

                            borderRadius: '4px',

                            border: session ? '1px solid rgba(255,255,255,0.1)' : '1px dashed rgba(201,162,39,0.3)',

                            display: 'flex', alignItems: 'center', gap: '4px',

                          }}

                        >

                          📖 {session ? `Session #${session.sessionNumber}` : 'No active session'}

                          <span style={{ fontSize: '0.6rem', opacity: 0.5 }}>▼</span>

                        </span>

                        {session && !editingSessionNumber && (

                          <span

                            onClick={() => { setEditingSessionNumber(true); setEditSessionNumValue(String(session.sessionNumber || '')); }}

                            style={{ fontSize: '0.6rem', color: '#888', cursor: 'pointer', opacity: 0.5 }}

                            title="Edit session number"

                          >✏️</span>

                        )}

                        {editingSessionNumber && session && (

                          <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>

                            <span style={{ fontSize: '0.6rem', color: '#888' }}>#</span>

                            <input

                              type="number"

                              value={editSessionNumValue}

                              onChange={(e) => setEditSessionNumValue(e.target.value)}

                              onKeyDown={(e) => {

                                if (e.key === 'Enter') {

                                  const num = parseInt(editSessionNumValue);

                                  if (num > 0) {

                                    const currentJournal = getQuickAddSessionCandidates();

                                    const updated = currentJournal.map(j => j.id === session.id ? { ...j, sessionNumber: num, updatedAt: new Date().toISOString() } : j);

                                    persistPlayerQuickAddJournalDiff(currentJournal, updated);

                                  }

                                  setEditingSessionNumber(false);

                                }

                                if (e.key === 'Escape') setEditingSessionNumber(false);

                              }}

                              onBlur={() => {

                                const num = parseInt(editSessionNumValue);

                                if (num > 0 && session) {

                                  const currentJournal = getQuickAddSessionCandidates();

                                  const updated = currentJournal.map(j => j.id === session.id ? { ...j, sessionNumber: num, updatedAt: new Date().toISOString() } : j);

                                  persistPlayerQuickAddJournalDiff(currentJournal, updated);

                                }

                                setEditingSessionNumber(false);

                              }}

                              autoFocus

                              style={{

                                width: '36px', padding: '1px 4px', fontSize: '0.65rem',

                                background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(201,162,39,0.4)',

                                borderRadius: '3px', color: '#c9a227', outline: 'none',

                              }}

                            />

                          </span>

                        )}

                        {session && !editingSessionNumber && (

                          <span

                            onClick={() => setQuickAddViewEntry(session)}

                            style={{ fontSize: '0.65rem', color: '#c9a227', cursor: 'pointer', textDecoration: 'underline', opacity: 0.7 }}

                          >open</span>

                        )}

                        {quickAddSessionOverride && (

                          <span

                            onClick={() => setQuickAddSessionOverride(null)}

                            style={{ fontSize: '0.6rem', color: '#888', cursor: 'pointer', opacity: 0.6 }}

                            title="Reset to auto-detect"

                          >✕ manual</span>

                        )}

                      </>

                    );

                  })()}

                </div>

                

                {/* Session picker dropdown */}

                {/* 2026-04-27: theme-cohesive — uses S.card / S.input fallbacks instead of

                    hardcoded blacks/golds, so the dropdown picks up the active theme. */}

                {showSessionPicker && (

                  <div style={{

                    marginBottom: '8px', padding: '6px',

                    background: S._colors?.bgCardSolid || 'rgba(0,0,0,0.4)',

                    borderRadius: S.card?.borderRadius || '6px',

                    border: S.card?.border || '1px solid rgba(201,162,39,0.2)',

                    maxHeight: '120px', overflowY: 'auto',

                  }}>

                    {listJournalSessions(getQuickAddSessionCandidates())

                      .map(s => (

                        <div key={s.id}

                          onClick={() => { setQuickAddSessionOverride(s.id); setShowSessionPicker(false); }}

                          style={{

                            padding: '4px 8px', cursor: 'pointer', fontSize: '0.75rem',

                            color: s.id === (getActiveSession()?.id) ? '#c9a227' : '#bbb',

                            background: s.id === quickAddSessionOverride ? 'rgba(201,162,39,0.15)' : 'transparent',

                            borderRadius: '3px',

                            display: 'flex', justifyContent: 'space-between',

                          }}

                        >

                          <span>Session #{s.sessionNumber}</span>

                          <span style={{ fontSize: '0.65rem', opacity: 0.5 }}>{s.sessionDate || ''}</span>

                        </div>

                      ))

                    }

                    <div

                      onClick={async () => {

                        const currentJournal = getQuickAddSessionCandidates();

                        const sessionMeta = await fetchJournalSessionMeta(discoverJournalEntries, 'Quick Journal');

                        const newSessionNum = resolveNextJournalSessionNumber(currentJournal, sessionMeta);

                        const newSession = createJournalEntry('session', {

                          name: `Session ${newSessionNum}`,

                          sessionNumber: newSessionNum,

                          sessionDate: new Date().toISOString().split('T')[0],

                          summary: '',

                          createdBy: charId,

                          visibility: 'shared',

                        });

                        persistPlayerQuickAddJournalDiff(currentJournal, [...currentJournal, newSession]);

                        setQuickAddSessionOverride(newSession.id);

                        setShowSessionPicker(false);

                      }}

                      style={{ padding: '4px 8px', cursor: 'pointer', fontSize: '0.75rem', color: '#27c95a', borderTop: '1px solid rgba(255,255,255,0.05)', marginTop: '2px' }}

                    >

                      + New session

                    </div>

                  </div>

                )}

              </>

            )}

            

            {/* Standalone mode: name + category */}

            {quickAddEntryMode === 'standalone' && (

              <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', alignItems: 'center' }}>

                <select

                  value={quickAddStandaloneCategory}

                  onChange={(e) => setQuickAddStandaloneCategory(e.target.value)}

                  style={{

                    ...(S.select || {}),

                    padding: '3px 6px', fontSize: '0.7rem',

                    marginBottom: 0, width: 'auto',

                    color: JOURNAL_CATEGORIES[quickAddStandaloneCategory]?.color || S.input?.color || '#ccc',

                    cursor: 'pointer',

                  }}

                >

                  {Object.entries(JOURNAL_CATEGORIES)

                    .filter(([k]) => k !== 'session')

                    .sort((a, b) => (a[1].order || 0) - (b[1].order || 0))

                    .map(([key, cat]) => (

                      <option key={key} value={key}>{cat.icon} {cat.name.replace(/s$/, '')}</option>

                    ))

                  }

                </select>

                <input

                  type="text"

                  value={quickAddStandaloneName}

                  onChange={(e) => setQuickAddStandaloneName(e.target.value)}

                  placeholder="Entry name..."

                  style={{

                    flex: 1, padding: '3px 8px', fontSize: '0.7rem',

                    background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)',

                    borderRadius: '4px', color: '#ccc', outline: 'none',

                  }}

                />

              </div>

            )}

            

            <QuickAddCheatSheet show={showQuickAddCheatSheet} onClose={() => setShowQuickAddCheatSheet(false)} settings={journalSettings?.quickAdd} />

            

            {/* Recent entries */}

            {showQuickAddRecent && (

              <div style={{ marginBottom: '8px', padding: '6px', background: 'rgba(0,0,0,0.3)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.05)', maxHeight: '180px', overflowY: 'auto' }}>

                <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: '4px' }}>Recently updated</div>

                {getQuickAddRecentSource()

                  .filter(e => e.name)

                  .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))

                  .slice(0, 10)

                  .map(e => {

                    const catInfo = JOURNAL_CATEGORIES[e.category];

                    return (

                      <div key={e.id}

                        onClick={() => setQuickAddViewEntry(e)}

                        style={{

                          padding: '3px 6px', cursor: 'pointer', fontSize: '0.75rem',

                          color: '#bbb', display: 'flex', alignItems: 'center', gap: '6px',

                          borderRadius: '3px',

                        }}

                      >

                        <span>{catInfo?.icon || '📄'}</span>

                        <span style={{ flex: 1 }}>{e.name}</span>

                        <span style={{ fontSize: '0.6rem', color: catInfo?.color || '#888', opacity: 0.6 }}>{catInfo?.name?.slice(0,-1) || ''}</span>

                      </div>

                    );

                  })

                }

              </div>

            )}

            

            {/* Privacy details wrapper */}

            <div style={{

              borderRadius: '6px',

              border: quickAddPrivate ? '1px solid rgba(138, 79, 255, 0.4)' : '1px solid transparent',

              background: quickAddPrivate ? 'rgba(138, 79, 255, 0.05)' : 'transparent',

              padding: quickAddPrivate ? '4px' : '0',

              transition: 'all 0.2s ease',

            }}>

              {/* Compact privacy info — only shown when private */}

              {quickAddPrivate && (

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>

                <span style={{ fontSize: '0.55rem', color: '#8a4fff', opacity: 0.7 }}>

                  🔒 Only you{isDM ? '' : (quickAddSharedWith.length > 0 ? `, ${quickAddSharedWith.length} other${quickAddSharedWith.length > 1 ? 's' : ''}` : '') + (quickAddShareWithDM ? ' and DM' : '')}

                </span>

                <div style={{ flex: 1 }} />

                <label

                  onClick={() => {

                    const opening = !quickAddNotesOpen;

                    setQuickAddNotesOpen(opening);

                    if (opening) {

                      setTimeout(() => { if (quickAddNotesRef.current) quickAddNotesRef.current.focus(); }, 50);

                      quickAddLastFocusRef.current = 'notes';

                    }

                  }}

                  style={{

                    display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer',

                    fontSize: '0.55rem', color: quickAddNotesOpen ? '#8a4fff' : '#666',

                    padding: '1px 5px', borderRadius: '3px',

                    background: quickAddNotesOpen ? 'rgba(138, 79, 255, 0.1)' : 'transparent',

                  }}

                >🔒 Aside</label>

              </div>

              )}

              

              {/* Share-with party member pills */}

              {quickAddPrivate && (() => {

                const partyMembers = Object.entries(chars || {}).filter(([id]) => id !== charId);

                if (partyMembers.length === 0 && isDM) return null;

                return (

                  <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', marginBottom: '4px', alignItems: 'center' }}>

                    <span style={{ fontSize: '0.55rem', color: '#888', marginRight: '2px' }}>Share with:</span>

                    {!isDM && (

                      <button

                        onClick={() => {

                          setQuickAddShareWithDM(!quickAddShareWithDM);

                          setTimeout(() => {

                            if (quickAddNotesOpen && quickAddNotesRef.current) {

                              quickAddNotesRef.current.focus();

                            } else if (quickAddEditorRef.current) {

                              quickAddEditorRef.current.focus();

                            }

                          }, 50);

                        }}

                        style={{

                          padding: '1px 6px', fontSize: '0.55rem', cursor: 'pointer',

                          background: quickAddShareWithDM ? 'rgba(138, 79, 255, 0.2)' : 'rgba(0,0,0,0.2)',

                          border: `1px solid ${quickAddShareWithDM ? 'rgba(138, 79, 255, 0.5)' : 'rgba(255,255,255,0.06)'}`,

                          borderRadius: '10px',

                          color: quickAddShareWithDM ? '#c4a8ff' : '#888',

                        }}

                      >DM</button>

                    )}

                    {partyMembers.map(([id, c]) => {

                      const isShared = quickAddSharedWith.includes(id);

                      return (

                        <button key={id}

                          onClick={() => {

                            setQuickAddSharedWith(prev => 

                              isShared ? prev.filter(x => x !== id) : [...prev, id]

                            );

                            setTimeout(() => {

                              if (quickAddNotesOpen && quickAddNotesRef.current) {

                                quickAddNotesRef.current.focus();

                              } else if (quickAddEditorRef.current) {

                                quickAddEditorRef.current.focus();

                              }

                            }, 50);

                          }}

                          style={{

                            padding: '1px 6px', fontSize: '0.55rem', cursor: 'pointer',

                            background: isShared ? 'rgba(138, 79, 255, 0.2)' : 'rgba(0,0,0,0.2)',

                            border: `1px solid ${isShared ? 'rgba(138, 79, 255, 0.5)' : 'rgba(255,255,255,0.06)'}`,

                            borderRadius: '10px',

                            color: isShared ? '#c4a8ff' : '#888',

                          }}

                        >{c.name || id}</button>

                      );

                    })}

                  </div>

                );

              })()}

              

              <QuickAddInput

                value={quickAddJournalText}

                onChange={setQuickAddJournalText}

                onSubmit={handleQuickAddSubmit}

                onCancel={closeQuickAddJournal}

                settings={journalSettings?.quickAdd}

                placeholder={quickAddEntryMode === 'standalone' 

                  ? (quickAddStandaloneName.trim() 

                    ? `Describe ${quickAddStandaloneName.trim()}... or use tags` 

                    : 'Use tags (#N@, @name) or set a name above')

                  : 'Quick note... use @name or #N@ #Q@ tags'

                }

                autoFocus

                entries={getQuickAddReadCandidates()}

                focusRef={quickAddEditorRef}

                onEditorFocus={() => { quickAddLastFocusRef.current = 'editor'; }}

                onAutocompleteSearch={hasPlayerFullJournalDiscovery
                  ? undefined
                  : ({ query }) => fetchJournalAutocompleteEntries(discoverJournalEntries, query, 'Quick Journal')}

              />

              

              {/* Player note type pills — show whenever the text references an NPC, either by:

                  (a) matching an EXISTING NPC entry (fuzzy name resolve via getReferencedNpcs), OR

                  (b) being a formal NPC-create tag (#N@Name / #S@Name / #N#Name / #S#Name)

                      even when the named NPC doesn't exist yet. Case (b) lets the user pre-file

                      a block on a brand-new NPC as it's being created by the same submit.

                  On submit, addPlayerNoteBlocks runs AFTER applyQuickAddResults so newly-created

                  NPCs are visible to getReferencedNpcs at apply time. */}

              {quickAddJournalText.trim() && (() => {

                const referencedNpcs = hasPlayerFullJournalDiscovery
                  ? getReferencedNpcs(quickAddJournalText, getQuickAddReadCandidates())
                  : getClearDiscoveredNpcEntriesForText(quickAddJournalText);

                // Detect formal NPC-create tags regardless of whether the target exists yet.

                const formalCreateTags = [...(quickAddJournalText.match(/#[NS][@#]\s*"?([^'`"\n]+?)(?:['`"]|\s|$)/gi) || [])]

                  .map(m => {

                    const nameMatch = m.match(/#[NS][@#]\s*"?([^'`"\n]+?)(?:['`"]|\s|$)/i);

                    return nameMatch?.[1]?.replace(/[.,;:!?]+$/, '').trim();

                  })

                  .filter(Boolean);

                const hasFormalCreate = formalCreateTags.length > 0;

                if (referencedNpcs.length === 0 && !hasFormalCreate) return null;



                // Prefer existing matches for the possessive label; fall back to the formal

                // tag's name so "Will file under <NewGuy>'s Profile" reads naturally even

                // before NewGuy exists.

                const displayName = referencedNpcs.length === 1

                  ? referencedNpcs[0].name

                  : referencedNpcs.length > 1

                    ? `${referencedNpcs.length} NPCs`

                    : formalCreateTags[0];

                const multi = referencedNpcs.length > 1 || (referencedNpcs.length === 0 && formalCreateTags.length > 1);

                const possessive = multi

                  ? (referencedNpcs.length > 0 ? `${referencedNpcs.length} NPCs'` : `${formalCreateTags.length} NPCs'`)

                  : `${displayName}'s`;

                return (

                  <div style={{ marginTop: 6, padding: '5px 8px', background: 'rgba(74,144,217,0.06)', border: '1px solid rgba(74,144,217,0.18)', borderRadius: 5 }}>

                    <div style={{ fontSize: '0.58rem', color: '#7ab3e0', marginBottom: 4 }}>

                      📝 {playerNoteCategories

                        ? <>Will also file this under <strong>{possessive}</strong> <strong style={{ color: '#c9a227' }}>{PLAYER_BLOCK_FIELDS.find(f => f.id === playerNoteCategories)?.label}</strong> section.</>

                        : <>Optionally file this note under {multi ? `each NPC's` : `${displayName}'s`} section (click one):</>}

                    </div>

                    <div style={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>

                      <button

                        onClick={() => setPlayerNoteCategories(null)}

                        style={{ padding: '3px 8px', fontSize: '0.6rem', borderRadius: 5, background: playerNoteCategories === null ? 'rgba(136,136,136,0.18)' : 'rgba(255,255,255,0.02)', border: `1px solid ${playerNoteCategories === null ? '#88888860' : '#3a302030'}`, color: playerNoteCategories === null ? '#aaa' : '#666', cursor: 'pointer', fontWeight: playerNoteCategories === null ? 600 : 400 }}

                      >— None —</button>

                      {PLAYER_BLOCK_FIELDS.map(f => {

                        const isSelected = playerNoteCategories === f.id;

                        return <button key={f.id} onClick={() => setPlayerNoteCategories(f.id)} style={{ padding: '3px 8px', fontSize: '0.6rem', borderRadius: 5, background: isSelected ? 'rgba(201,162,39,0.15)' : 'rgba(255,255,255,0.02)', border: `1px solid ${isSelected ? '#c9a22760' : '#3a302030'}`, color: isSelected ? '#c9a227' : '#888', cursor: 'pointer', fontWeight: isSelected ? 600 : 400 }}>{f.icon} {f.label}</button>;

                      })}

                    </div>

                  </div>

                );

              })()}

              

              {/* Compact action row — matches DM style */}

              <div style={{ display: 'flex', gap: 4, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>

                <button onClick={() => { if (quickAddJournalText.trim()) handleQuickAddSubmit(quickAddJournalText); }} disabled={!quickAddJournalText.trim()} style={{ padding: '4px 12px', fontSize: '0.65rem', fontWeight: 700, borderRadius: 5, background: quickAddJournalText.trim() ? 'rgba(201,39,39,0.2)' : 'rgba(201,39,39,0.08)', border: `1px solid ${quickAddJournalText.trim() ? '#c92727' : '#c9272730'}`, color: quickAddJournalText.trim() ? '#ff6961' : '#88555550', cursor: quickAddJournalText.trim() ? 'pointer' : 'default' }}>Save</button>

                <button onClick={() => { setQuickAddPrivate(!quickAddPrivate); }} style={{ padding: '4px 8px', fontSize: '0.58rem', borderRadius: 5, background: quickAddPrivate ? 'rgba(138,79,255,0.15)' : 'rgba(255,255,255,0.03)', border: `1px solid ${quickAddPrivate ? '#8a4fff50' : '#3a302030'}`, color: quickAddPrivate ? '#8a4fff' : '#888', cursor: 'pointer' }}>{quickAddPrivate ? '🔒 Private' : '🔓 Shared'}</button>

                {quickAddEntryMode === 'session' && (() => {

                  const session = getActiveSession();

                  return <button onClick={() => setShowSessionPicker(!showSessionPicker)} style={{ padding: '4px 8px', fontSize: '0.58rem', borderRadius: 5, background: session ? 'rgba(201,162,39,0.12)' : 'rgba(136,136,136,0.06)', border: `1px solid ${session ? '#c9a22750' : '#3a3020'}`, color: session ? '#c9a227' : '#666', cursor: 'pointer' }}>📖 {session ? `Session #${session.sessionNumber}` : 'New Session'} ▾</button>;

                })()}

              </div>

              

              {/* Private notes input (collapsible) */}

              {quickAddNotesOpen && (

                <div style={{ marginTop: '4px' }}>

                  <textarea

                    ref={quickAddNotesRef}

                    value={quickAddPrivateNotes}

                    onChange={(e) => setQuickAddPrivateNotes(e.target.value)}

                    onFocus={() => { quickAddLastFocusRef.current = 'notes'; }}

                    onKeyDown={(e) => {

                      if (e.key === 'Enter' && !e.shiftKey) {

                        e.preventDefault();

                        handleQuickAddSubmit(quickAddJournalText || '');

                      }

                    }}

                    placeholder={quickAddPrivate 

                      ? (quickAddSharedWith.length > 0 

                        ? `Aside shared with ${quickAddSharedWith.length} other${quickAddSharedWith.length > 1 ? 's' : ''}${quickAddShareWithDM || isDM ? ' and DM' : ''}...`

                        : `Private aside${!isDM && quickAddShareWithDM ? ' (visible to you and DM)' : ' (only you)'}...`)

                      : "Aside visible to everyone..."

                    }

                    style={{

                      width: '100%', minHeight: '40px', maxHeight: '80px', resize: 'vertical',

                      padding: '8px 12px', fontSize: '0.9rem', lineHeight: '1.5',

                      background: quickAddPrivate ? 'rgba(138, 79, 255, 0.08)' : (S.input?.background || 'rgba(0,0,0,0.3)'),

                      border: quickAddPrivate ? '1px solid rgba(138, 79, 255, 0.3)' : (S.input?.border || '1px solid rgba(201,162,39,0.2)'),

                      borderRadius: '6px', color: quickAddPrivate ? '#c4a8ff' : (S.input?.color || '#e8d5b7'), outline: 'none',

                      fontFamily: 'inherit', boxSizing: 'border-box',

                    }}

                  />

                  <div style={{ fontSize: '0.5rem', color: '#666', marginTop: '2px' }}>Enter to submit · Shift+Enter for new line</div>

                </div>

              )}

            </div>

            

            {/* Search + recent quick links */}

            {(() => {

              const activeQuickAddJournal = getQuickAddReadCandidates();
              const quickAddRecentSource = getQuickAddRecentSource();

              const recent = quickAddRecentSource

                .filter(e => e.name && e.updatedAt)

                .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))

                .slice(0, 3);

              

              const isTextMode = quickAddSearchMode === 'text';

              const activeQuery = isTextMode ? quickAddTextSearch.trim().toLowerCase() : quickAddSearch.trim().toLowerCase();

              

              // Title search results

              const mergeTitleEntries = (...sources) => {

                const byId = new Map();

                sources.flat().forEach(entry => {

                  if (!entry?.id || byId.has(entry.id)) return;

                  byId.set(entry.id, entry);

                });

                return Array.from(byId.values()).slice(0, 8);

              };

              const localTitleResults = !isTextMode && activeQuery.length >= 1

                ? searchJournalEntries(activeQuickAddJournal, activeQuery, { mode: 'title', limit: 8 })

                    .map(result => result.entry)

                : [];

              const titleDiscoveryResults = !isTextMode && !hasPlayerFullJournalDiscovery && activeQuery.length >= 3

                ? quickAddTitleDiscoveryResults

                : [];

              const titleResults = !isTextMode && activeQuery.length >= 1

                ? mergeTitleEntries(titleDiscoveryResults, localTitleResults)

                : [];

              

              // Stop words for text search (ignored in phrase matching)

              const STOP_WORDS = new Set(['the','a','an','of','in','on','at','to','for','and','or','but','is','was','it','by','with','from','as','be','this','that','which','are','were','has','had','have','not','no','so','if','its','my','your','our','his','her','their','we','he','she','they','them','us','me','i','you','do','did','does','will','would','can','could','just','also','very','then','than']);

              

              // Get all searchable text fields for an entry

              const stripForView = (t) => t ? stripPrivateForViewer(t, charId, isDM).replace(/\{\{PRIVATE:[^}]+\}\}|\{\{\/PRIVATE\}\}/g, '') : t;

              const getEntryFields = (e) => [

                stripForView(e.description), stripForView(e.summary), stripForView(e.knownInfo), stripForView(e.abilities),

                e.habitat, e.rewards, e.loot,

                ...(e.objectives || []).map(o => o.text),

                ...(e.npcsEncountered || []),

                ...(e.creaturesEncountered || []),

                ...(e.itemsFound || []),

                ...(e.relatedNpcs || []),

                ...(e.relatedItems || []),

                e.questGiver,

              ].filter(Boolean);

              

              // Strip stop words, return array of content words

              const contentWords = (text) => text.toLowerCase().split(/\s+/).filter(w => w && !STOP_WORDS.has(w));

              

              // Text/mention search results

              const textResults = isTextMode && activeQuery.length >= 2

                ? (() => {

                    const results = [];

                    const queryWords = contentWords(activeQuery);

                    if (queryWords.length === 0 && quickAddTextSearchMode !== 'mentions') return [];

                    

                    const isPhraseMode = quickAddTextSearchMode === 'phrase';

                    const isMentionsMode = quickAddTextSearchMode === 'mentions';

                    

                    activeQuickAddJournal.forEach(e => {

                      const fields = getEntryFields(e);

                      const allText = fields.join(' ');

                      const allTextLower = allText.toLowerCase();

                      

                      let matched = false;

                      let score = 0;

                      let snippet = '';

                      

                      if (isMentionsMode) {

                        // MENTIONS MODE: find entries that reference a name

                        const q = activeQuery.toLowerCase();

                        const mentionSources = [];

                        

                        // Check structured reference fields

                        const refFields = [

                          { arr: e.npcsEncountered, label: 'NPC encountered' },

                          { arr: e.creaturesEncountered, label: 'Creature encountered' },

                          { arr: e.itemsFound, label: 'Item found' },

                          { arr: e.relatedNpcs, label: 'Related NPC' },

                          { arr: e.relatedItems, label: 'Related item' },

                          { arr: e.relatedLocations, label: 'Related location' },

                          { arr: e.questConnections, label: 'Quest connection' },

                        ];

                        refFields.forEach(({ arr, label }) => {

                          if (arr && arr.some(n => n.toLowerCase().includes(q))) {

                            mentionSources.push(label);

                          }

                        });

                        

                        // Check single-value reference fields

                        if (e.questGiver && e.questGiver.toLowerCase().includes(q)) mentionSources.push('Quest giver');

                        if (e.currentLocation && e.currentLocation.toLowerCase().includes(q)) mentionSources.push('Location');

                        

                        // Check text content for name mention (word boundary match)

                        const nameRegex = new RegExp('\\b' + q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b', 'i');

                        const textFields = [e.description, e.summary, e.knownInfo, e.abilities].filter(Boolean);

                        for (const f of textFields) {

                          const match = f.match(nameRegex);

                          if (match) {

                            mentionSources.push('In text');

                            // Build snippet around the mention

                            const idx = f.toLowerCase().indexOf(q);

                            if (idx >= 0) {

                              const start = Math.max(0, idx - 25);

                              const end = Math.min(f.length, idx + q.length + 45);

                              snippet = (start > 0 ? '...' : '') + f.substring(start, end).replace(/\n/g, ' ') + (end < f.length ? '...' : '');

                            }

                            break;

                          }

                        }

                        

                        // Check objectives

                        if ((e.objectives || []).some(o => o.text && o.text.toLowerCase().includes(q))) {

                          mentionSources.push('In objective');

                        }

                        

                        // Skip the entry that IS the searched name (user wants references TO it)

                        if (e.name && e.name.toLowerCase() === q) return;

                        

                        if (mentionSources.length > 0) {

                          matched = true;

                          // Score: structured refs rank higher than text mentions

                          const hasStructured = mentionSources.some(s => s !== 'In text' && s !== 'In objective');

                          score = hasStructured ? 100 : 50;

                          if (!snippet) {

                            snippet = mentionSources.join(' · ');

                          } else {

                            snippet = mentionSources.join(' · ') + '\n' + snippet;

                          }

                        }

                      } else if (isPhraseMode) {

                        // PHRASE MODE: content words must appear consecutively in order

                        // Strip stop words from the text too, but track positions for snippeting

                        const textTokens = allTextLower.split(/\s+/);

                        const contentPositions = []; // { word, origIndex }

                        textTokens.forEach((w, i) => {

                          if (w && !STOP_WORDS.has(w)) contentPositions.push({ word: w, origIndex: i });

                        });

                        

                        // Search for queryWords sequence in contentPositions

                        for (let i = 0; i <= contentPositions.length - queryWords.length; i++) {

                          let found = true;

                          for (let j = 0; j < queryWords.length; j++) {

                            if (!contentPositions[i + j].word.includes(queryWords[j])) {

                              found = false;

                              break;

                            }

                          }

                          if (found) {

                            matched = true;

                            score = 100; // Phrase match = high score

                            // Build snippet from original text around match

                            const firstOrig = contentPositions[i].origIndex;

                            const lastOrig = contentPositions[i + queryWords.length - 1].origIndex;

                            const snippetStart = Math.max(0, firstOrig - 3);

                            const snippetEnd = Math.min(textTokens.length, lastOrig + 4);

                            const snippetWords = textTokens.slice(snippetStart, snippetEnd);

                            snippet = (snippetStart > 0 ? '...' : '') + snippetWords.join(' ') + (snippetEnd < textTokens.length ? '...' : '');

                            break;

                          }

                        }

                      } else {

                        // WORDS MODE: all query words must appear somewhere in entry text

                        const allMissing = queryWords.filter(w => !allTextLower.includes(w));

                        if (allMissing.length > 0) return; // Not all words present

                        

                        matched = true;

                        

                        // Score: check if words appear in same sentence (higher = better)

                        // Split into sentences, check each for word coverage

                        const sentences = allText.split(/[.!?\n]+/).filter(s => s.trim());

                        let bestSentenceScore = 0;

                        let bestSentence = '';

                        

                        for (const sent of sentences) {

                          const sentLower = sent.toLowerCase();

                          const wordsInSent = queryWords.filter(w => sentLower.includes(w)).length;

                          const sentScore = wordsInSent / queryWords.length;

                          if (sentScore > bestSentenceScore) {

                            bestSentenceScore = sentScore;

                            bestSentence = sent.trim();

                          }

                        }

                        

                        // Score: 100 if all in one sentence, 50 if partial, 10 if scattered

                        score = bestSentenceScore >= 1.0 ? 100 : (bestSentenceScore >= 0.5 ? 50 : 10);

                        

                        // Snippet: use best matching sentence, or fallback to first match

                        if (bestSentence) {

                          const trimmed = bestSentence.substring(0, 80);

                          snippet = trimmed + (bestSentence.length > 80 ? '...' : '');

                        } else {

                          // Fallback: find first query word in text

                          const firstIdx = allTextLower.indexOf(queryWords[0]);

                          if (firstIdx >= 0) {

                            const start = Math.max(0, firstIdx - 20);

                            const end = Math.min(allText.length, firstIdx + 60);

                            snippet = (start > 0 ? '...' : '') + allText.substring(start, end).replace(/\n/g, ' ') + (end < allText.length ? '...' : '');

                          }

                        }

                      }

                      

                      if (matched) {

                        results.push({ entry: e, snippet, score });

                      }

                    });

                    

                    // Sort by score descending, then alphabetically

                    results.sort((a, b) => b.score - a.score || a.entry.name.localeCompare(b.entry.name));

                    return results.slice(0, 10);

                  })()

                : [];

              

              const hasResults = isTextMode ? textResults.length > 0 : titleResults.length > 0;

              const noResults = activeQuery.length >= (isTextMode ? 2 : 1) && !hasResults;

              

              return (

                <>

                  {/* Mode toggle + search input */}

                  <div style={{ marginTop: '4px' }}>

                    {/* Compact search bar with inline mode toggle */}

                    <div style={{ display: 'flex', gap: '3px', marginBottom: '3px', alignItems: 'center' }}>

                      <input

                        style={{

                          flex: 1, padding: '3px 8px', fontSize: '0.68rem',

                          background: 'rgba(0,0,0,0.2)', border: '1px solid #3a3020',

                          borderRadius: '4px', color: '#e8d5b7', outline: 'none',

                        }}

                        placeholder={isTextMode ? 'Search in text...' : 'Search...'}

                        value={isTextMode ? quickAddTextSearch : quickAddSearch}

                        onChange={(e) => isTextMode ? setQuickAddTextSearch(e.target.value) : setQuickAddSearch(e.target.value)}

                      />

                      {[{ key: 'title', label: '🏷', tip: 'Search by name' }, { key: 'text', label: '📝', tip: 'Search in text' }].map(m => (

                        <button key={m.key}

                          onClick={() => { setQuickAddSearchMode(m.key); setQuickAddSearch(''); setQuickAddTextSearch(''); }}

                          title={m.tip}

                          style={{

                            padding: '2px 5px', fontSize: '0.65rem', cursor: 'pointer',

                            background: quickAddSearchMode === m.key ? 'rgba(201,162,39,0.15)' : 'transparent',

                            border: `1px solid ${quickAddSearchMode === m.key ? '#c9a22750' : 'transparent'}`,

                            borderRadius: '3px', color: quickAddSearchMode === m.key ? '#c9a227' : '#666',

                          }}

                        >{m.label}</button>

                      ))}

                      {/* Text sub-mode pills */}

                      {isTextMode && (

                        <>

                          <span style={{ color: '#555', fontSize: '0.6rem', margin: '0 2px' }}>│</span>

                          {[{ key: 'phrase', label: 'Phrase' }, { key: 'words', label: 'All Words' }, { key: 'mentions', label: 'Mentions' }].map(m => (

                            <button key={m.key}

                              onClick={() => setQuickAddTextSearchMode(m.key)}

                              style={{

                                padding: '2px 6px', fontSize: '0.55rem', cursor: 'pointer',

                                background: quickAddTextSearchMode === m.key ? 'rgba(100,180,255,0.15)' : 'rgba(0,0,0,0.2)',

                                border: `1px solid ${quickAddTextSearchMode === m.key ? 'rgba(100,180,255,0.4)' : 'rgba(255,255,255,0.04)'}`,

                                borderRadius: '3px',

                                color: quickAddTextSearchMode === m.key ? '#6ab4ff' : '#666',

                              }}

                            >{m.label}</button>

                          ))}

                        </>

                      )}

                    </div>

                  </div>

                  

                  {/* Title search results */}

                  {!isTextMode && activeQuery && titleResults.length > 0 && (

                    <div style={{

                      marginTop: '4px', padding: '4px',

                      background: 'rgba(0,0,0,0.5)', borderRadius: '5px',

                      border: '1px solid rgba(201,162,39,0.2)',

                      maxHeight: '160px', overflowY: 'auto',

                    }}>

                      {titleResults.map(e => {

                        const catInfo = JOURNAL_CATEGORIES[e.category];

                        const nameIdx = e.name.toLowerCase().indexOf(activeQuery);

                        return (

                          <div key={e.id}

                            onClick={() => { setQuickAddViewEntry(e); setQuickAddSearch(''); }}

                            style={{

                              padding: '4px 8px', cursor: 'pointer', fontSize: '0.75rem',

                              color: '#ccc', display: 'flex', alignItems: 'center', gap: '6px',

                              borderRadius: '3px',

                            }}

                            onMouseEnter={(ev) => ev.currentTarget.style.background = 'rgba(201,162,39,0.1)'}

                            onMouseLeave={(ev) => ev.currentTarget.style.background = 'transparent'}

                          >

                            <span style={{ flexShrink: 0 }}>{catInfo?.icon || '📄'}</span>

                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>

                              {nameIdx >= 0 ? (

                                <>

                                  {e.name.substring(0, nameIdx)}

                                  <span style={{ color: '#c9a227', fontWeight: 'bold' }}>{e.name.substring(nameIdx, nameIdx + activeQuery.length)}</span>

                                  {e.name.substring(nameIdx + activeQuery.length)}

                                </>

                              ) : e.name}

                            </span>

                            <span style={{ fontSize: '0.6rem', color: catInfo?.color || '#888', opacity: 0.6, flexShrink: 0 }}>

                              {catInfo?.name?.slice(0,-1) || ''}

                            </span>

                          </div>

                        );

                      })}

                    </div>

                  )}

                  

                  {/* Text search results */}

                  {isTextMode && activeQuery.length >= 2 && textResults.length > 0 && (

                    <div style={{

                      marginTop: '4px', padding: '4px',

                      background: 'rgba(0,0,0,0.5)', borderRadius: '5px',

                      border: '1px solid rgba(201,162,39,0.2)',

                      maxHeight: '200px', overflowY: 'auto',

                    }}>

                      {textResults.map(({ entry: e, snippet, score }) => {

                        const catInfo = JOURNAL_CATEGORIES[e.category];

                        const isMentions = quickAddTextSearchMode === 'mentions';

                        // Highlight query words in snippet

                        const qWords = isMentions ? [activeQuery.toLowerCase()] : contentWords(activeQuery);

                        const highlightSnippet = (text) => {

                          if (!text || qWords.length === 0) return text;

                          const escaped = qWords.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

                          const regex = new RegExp(`(${escaped.join('|')})`, 'gi');

                          const parts = text.split(regex);

                          return parts.map((part, i) => {

                            if (qWords.some(w => part.toLowerCase().includes(w))) {

                              return <span key={i} style={{ color: '#e8d5b7', fontWeight: 'bold', background: 'rgba(201,162,39,0.15)', padding: '0 1px', borderRadius: '2px' }}>{part}</span>;

                            }

                            return part;

                          });

                        };

                        // For mentions, split snippet into sources line + text snippet

                        const snippetParts = isMentions && snippet ? snippet.split('\n') : null;

                        const mentionSources = snippetParts && snippetParts.length > 0 ? snippetParts[0] : null;

                        const mentionText = snippetParts && snippetParts.length > 1 ? snippetParts.slice(1).join(' ') : null;

                        return (

                          <div key={e.id}

                            onClick={() => { setQuickAddViewEntry(e); setQuickAddTextSearch(''); }}

                            style={{

                              padding: '5px 8px', cursor: 'pointer', fontSize: '0.75rem',

                              color: '#ccc', borderRadius: '3px',

                              borderBottom: '1px solid rgba(255,255,255,0.03)',

                            }}

                            onMouseEnter={(ev) => ev.currentTarget.style.background = 'rgba(201,162,39,0.1)'}

                            onMouseLeave={(ev) => ev.currentTarget.style.background = 'transparent'}

                          >

                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>

                              <span style={{ flexShrink: 0 }}>{catInfo?.icon || '📄'}</span>

                              <span style={{ fontWeight: 'bold', color: catInfo?.color || '#ccc', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name}</span>

                              {quickAddTextSearchMode === 'words' && score < 100 && (

                                <span style={{ fontSize: '0.5rem', color: '#666', flexShrink: 0 }} title="Words found scattered across entry">scattered</span>

                              )}

                              <span style={{ fontSize: '0.6rem', color: '#888', flexShrink: 0 }}>{catInfo?.name?.slice(0,-1) || ''}</span>

                            </div>

                            {isMentions && mentionSources && (

                              <div style={{ fontSize: '0.6rem', color: '#6ab4ff', paddingLeft: '22px', marginBottom: mentionText ? '2px' : 0 }}>

                                {mentionSources}

                              </div>

                            )}

                            {isMentions && mentionText && (

                              <div style={{ fontSize: '0.65rem', color: '#999', paddingLeft: '22px', lineHeight: '1.4' }}>

                                {highlightSnippet(mentionText)}

                              </div>

                            )}

                            {!isMentions && snippet && (

                              <div style={{ fontSize: '0.65rem', color: '#999', paddingLeft: '22px', lineHeight: '1.4' }}>

                                {highlightSnippet(snippet)}

                              </div>

                            )}

                          </div>

                        );

                      })}

                    </div>

                  )}

                  

                  {/* No results */}

                  {noResults && (

                    <div style={{ marginTop: '4px', padding: '4px 8px', fontSize: '0.7rem', color: '#666', fontStyle: 'italic' }}>

                      No {isTextMode ? 'text matches' : 'entries matching'} "{isTextMode ? quickAddTextSearch : quickAddSearch}"

                      {isTextMode && activeQuery.length < 2 && ' (type at least 2 characters)'}

                    </div>

                  )}

                  

                  {/* Recent chips - own line below search (hidden when searching) */}

                  {!activeQuery && recent.length > 0 && (

                    <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>

                      {recent.map(e => {

                        const catInfo = JOURNAL_CATEGORIES[e.category];

                        return (

                          <span key={e.id}

                            onClick={() => setQuickAddViewEntry(e)}

                            style={{

                              padding: '2px 6px', fontSize: '0.65rem', cursor: 'pointer',

                              background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)',

                              borderRadius: '3px', color: '#aaa',

                              display: 'flex', alignItems: 'center', gap: '3px',

                              maxWidth: '120px', overflow: 'hidden', whiteSpace: 'nowrap',

                            }}

                            title={e.name}

                          >

                            <span>{catInfo?.icon || '📄'}</span>

                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.name}</span>

                          </span>

                        );

                      })}

                    </div>

                  )}

                </>

              );

            })()}

            {/* Popup entry viewer */}

            {quickAddViewEntry && (() => {

              const activeQuickAddJournal = getQuickAddReadCandidates();

              const entry = activeQuickAddJournal.find(e => e.id === quickAddViewEntry.id) || quickAddViewEntry;

              const catInfo = JOURNAL_CATEGORIES[entry.category];

              const allEntries = activeQuickAddJournal;

              // Smart link click handler: navigate to linked entry within popup

              const onLinkClick = (linkedEntry) => setQuickAddViewEntry(linkedEntry);

              // Render text with smart links AND privacy filtering

              const linked = (text) => {

                if (!text) return null;

                return renderTextWithPrivacy(text, charId, isDM, allEntries, onLinkClick, {}, chars);

              };

              // Check for private notes visible to this user

              const myPrivateNotes = (entry.privateNotes || []).filter(n => n.charId === charId || (isDM && !n.noDM) || (n.sharedWith || []).includes(charId));

              return (

                <div style={{

                  marginTop: '8px', padding: '10px',

                  background: 'rgba(0,0,0,0.5)',

                  border: `1px solid ${catInfo?.color || 'rgba(201,162,39,0.3)'}40`,

                  borderLeft: `3px solid ${catInfo?.color || '#c9a227'}`,

                  borderRadius: '6px',

                  maxHeight: '250px', overflowY: 'auto',

                  overflowWrap: 'break-word', wordBreak: 'break-word',

                }}>

                  {/* Popup header */}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

                      {quickAddViewHistory.length > 0 && (

                        <button

                          onClick={quickAddGoBack}

                          style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: '0.7rem', padding: '1px 5px', borderRadius: '3px' }}

                          title={`Back to ${quickAddViewHistory[0]?.name || 'previous'}`}

                        >←</button>

                      )}

                      <span>{catInfo?.icon || '📄'}</span>

                      <span style={{ color: catInfo?.color || '#c9a227', fontWeight: 'bold', fontSize: '0.9rem' }}>{entry.name}</span>

                      <span style={{ fontSize: '0.6rem', color: '#888', padding: '1px 4px', background: 'rgba(255,255,255,0.05)', borderRadius: '2px' }}>{catInfo?.name?.slice(0,-1) || entry.category}</span>

                    </div>

                    <button

                      onClick={() => { setQuickAddViewEntryRaw(null); setQuickAddViewHistory([]); }}

                      style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '0.8rem', padding: '2px 4px' }}

                    >✕</button>

                  </div>

                  

                  {/* Entry content with smart links */}

                  {entry.category === 'session' && (

                    <>

                      <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: '4px' }}>Session #{entry.sessionNumber} • {entry.sessionDate}</div>

                      {entry.summary && <div style={{ fontSize: '0.8rem', color: '#ccc', lineHeight: '1.5', whiteSpace: 'pre-wrap', marginBottom: '6px' }}>{linked(entry.summary)}</div>}

                      {(entry.npcsEncountered || []).length > 0 && (

                        <div style={{ fontSize: '0.7rem', color: '#4a90d9', marginBottom: '2px' }}>NPCs: {linked(entry.npcsEncountered.join(', '))}</div>

                      )}

                      {(entry.creaturesEncountered || []).length > 0 && (

                        <div style={{ fontSize: '0.7rem', color: '#c92727', marginBottom: '2px' }}>Creatures: {linked(entry.creaturesEncountered.join(', '))}</div>

                      )}

                      {(entry.itemsFound || []).length > 0 && (

                        <div style={{ fontSize: '0.7rem', color: '#c9a227', marginBottom: '2px' }}>Items: {linked(entry.itemsFound.join(', '))}</div>

                      )}

                    </>

                  )}

                  {entry.category === 'npcs' && (

                    <>

                      {entry.description && <div style={{ fontSize: '0.8rem', color: '#ccc', lineHeight: '1.5', whiteSpace: 'pre-wrap', marginBottom: '6px' }}>{linked(entry.description)}</div>}

                      {entry.currentLocation && <div style={{ fontSize: '0.7rem', color: '#888' }}>📍 {entry.currentLocation}</div>}

                      {entry.knownInfo && <div style={{ fontSize: '0.75rem', color: '#bbb', marginTop: '4px', padding: '4px 6px', background: 'rgba(201,162,39,0.1)', borderRadius: '3px', whiteSpace: 'pre-wrap' }}>{linked(entry.knownInfo)}</div>}

                    </>

                  )}

                  {entry.category === 'quests' && (

                    <>

                      {entry.description && <div style={{ fontSize: '0.8rem', color: '#ccc', lineHeight: '1.5', whiteSpace: 'pre-wrap', marginBottom: '6px' }}>{linked(entry.description)}</div>}

                      {entry.questGiver && <div style={{ fontSize: '0.7rem', color: '#4a90d9', marginBottom: '4px' }}>Quest giver: {linked(entry.questGiver)}</div>}

                      {(entry.objectives || []).length > 0 && (

                        <div style={{ marginTop: '4px' }}>

                          {entry.objectives.map((obj, i) => (

                            <div key={i} style={{ fontSize: '0.75rem', color: obj.completed ? '#666' : '#ccc', textDecoration: obj.completed ? 'line-through' : 'none', paddingLeft: '4px' }}>

                              {obj.completed ? '☑' : '☐'} {linked(obj.text)}

                            </div>

                          ))}

                        </div>

                      )}

                      {(entry.relatedNpcs || []).length > 0 && (

                        <div style={{ fontSize: '0.7rem', color: '#4a90d9', marginTop: '4px' }}>Related NPCs: {linked(entry.relatedNpcs.join(', '))}</div>

                      )}

                      {(entry.relatedItems || []).length > 0 && (

                        <div style={{ fontSize: '0.7rem', color: '#c9a227', marginTop: '2px' }}>Related Items: {linked(entry.relatedItems.join(', '))}</div>

                      )}

                    </>

                  )}

                  {entry.category !== 'session' && entry.category !== 'npcs' && entry.category !== 'quests' && (

                    <>

                      {entry.description && <div style={{ fontSize: '0.8rem', color: '#ccc', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{linked(entry.description)}</div>}

                    </>

                  )}

                  

                  {/* Public notes section */}

                  {(entry.publicNotes || []).length > 0 && (

                    <div style={{

                      marginTop: '8px', padding: '6px 8px',

                      borderLeft: '3px solid rgba(74,144,217,0.4)',

                      borderRadius: '0 4px 4px 0',

                    }}>

                      <div style={{ fontSize: '0.6rem', color: '#4a90d9', marginBottom: '4px', fontWeight: 'bold' }}>

                        📝 Aside

                      </div>

                      {(entry.publicNotes || []).map((note, i) => (

                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '4px', marginTop: '4px', fontSize: '0.8rem', color: '#ccc', lineHeight: '1.5' }}>

                          <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>

                            {linked(note.text)}

                            <span style={{ fontSize: '0.55rem', color: '#888', marginLeft: '4px' }}>

                              — {(chars || {})[note.charId]?.name || 'player'}

                            </span>

                          </span>

                        </div>

                      ))}

                    </div>

                  )}

                  

                  {/* Private notes section */}

                  {myPrivateNotes.length > 0 && (

                    <div style={{

                      marginTop: '8px', padding: '6px 8px',

                      borderLeft: '3px solid rgba(138, 79, 255, 0.4)',

                      borderRadius: '0 4px 4px 0',

                    }}>

                      <div style={{ fontSize: '0.6rem', color: '#8a4fff', marginBottom: '4px', fontWeight: 'bold' }}>

                        🔒 Private Aside

                      </div>

                      {myPrivateNotes.map((note, i) => {

                        const isShared = (note.sharedWith || []).length > 0;

                        const noteColor = isShared ? '#8fd4d4' : '#c4a8ff';

                        const sharedNames = isShared ? note.sharedWith.map(sid => (chars || {})[sid]?.name || sid) : [];

                        const tooltipText = isShared ? `Shared with: ${sharedNames.join(', ')}${note.noDM ? '' : (isDM ? '' : ' and DM')}` : (note.noDM ? 'Private — only visible to you (DM excluded)' : 'Private — only visible to you and DM');

                        const emoji = isShared ? '👥' : null;

                        const borderCol = isShared ? '#6bbfbf' : '#8a4fff';

                        return (

                          <div key={i} style={{ 

                            display: 'flex', alignItems: 'flex-start', gap: '4px',

                            fontSize: '0.8rem', color: noteColor, lineHeight: '1.5',

                            marginBottom: i < myPrivateNotes.length - 1 ? '4px' : 0,

                          }}>

                            {emoji ? (

                              <Tooltip text={tooltipText} special borderColor={borderCol}>

                                <span style={{ cursor: 'help', opacity: 0.85, flexShrink: 0, fontSize: '0.65em' }}>{emoji}</span>

                              </Tooltip>

                            ) : null}

                            <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>

                              {linked(note.text)}

                              {isDM && note.charId !== charId && (

                                <span style={{ fontSize: '0.55rem', color: '#888', marginLeft: '4px' }}>

                                  — {(chars || {})[note.charId]?.name || 'player'}

                                </span>

                              )}

                            </span>

                          </div>

                        );

                      })}

                    </div>

                  )}

                  

                  {/* View in Journal button */}

                  <button

                    onClick={() => { setJournalInitEntry(entry); setView('journal'); closeQuickAddJournal(); setQuickAddViewEntryRaw(null); setQuickAddViewHistory([]); }}

                    style={{

                      marginTop: '8px', padding: '4px 10px',

                      background: 'rgba(201,162,39,0.15)', border: '1px solid rgba(201,162,39,0.3)',

                      borderRadius: '4px', color: '#c9a227', cursor: 'pointer',

                      fontSize: '0.7rem', width: '100%',

                    }}

                  >

                    View in Journal →

                  </button>

                </div>

              );

            })()}

            {/* Delimiter settings (collapsed) */}

            {showQuickAddCheatSheet && (() => {

              const qaS = { ...DEFAULT_QUICKADD_SETTINGS, ...(journalSettings?.quickAdd || {}) };

              return (

                <div style={{ marginTop: '6px', padding: '6px 8px', background: 'rgba(0,0,0,0.3)', borderRadius: '4px', fontSize: '0.7rem', color: '#888' }}>

                  <div style={{ marginBottom: '4px', color: '#aaa' }}>⚙ Delimiter Settings</div>

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '3px' }}>

                    <span style={{ minWidth: '60px' }}>Tab key:</span>

                    {['tick', 'dash'].map(v => (

                      <label key={v} style={{ display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer', color: qaS.tabDelimiter === v ? '#c9a227' : '#888' }}>

                        <input type="radio" name="tabDelim" checked={qaS.tabDelimiter === v}

                          onChange={() => {

                            const updated = { ...journalSettings, quickAdd: { ...(journalSettings?.quickAdd || {}), tabDelimiter: v } };

                            saveJournalSettings(updated);

                          }}

                          style={{ accentColor: '#c9a227' }}

                        />

                        <span>{v === 'tick' ? "' (paragraph)" : '- (text after)'}</span>

                      </label>

                    ))}

                  </div>

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>

                    <span style={{ minWidth: '60px' }}>Autocomplete:</span>

                    {['tick', 'dash'].map(v => (

                      <label key={v} style={{ display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer', color: qaS.autocompleteDelimiter === v ? '#c9a227' : '#888' }}>

                        <input type="radio" name="acDelim" checked={qaS.autocompleteDelimiter === v}

                          onChange={() => {

                            const updated = { ...journalSettings, quickAdd: { ...(journalSettings?.quickAdd || {}), autocompleteDelimiter: v } };

                            saveJournalSettings(updated);

                          }}

                          style={{ accentColor: '#c9a227' }}

                        />

                        <span>{v === 'tick' ? "' (paragraph)" : '- (text after)'}</span>

                      </label>

                    ))}

                  </div>

                </div>

              );

            })()}

          </div>

        );

          return isQjMobile ? (

            <MobileBottomSheet open={true} onClose={closeQuickAddJournal} ariaLabel="Quick Journal">

              {inner}

            </MobileBottomSheet>

          ) : inner;

        })()}



        {/* Floating button — desktop only. On mobile this overlaps the

            bottom-nav and inventory quick-access region; mobile reaches

            Quick Journal via the hamburger menu instead. */}

        {!showQuickAddJournal && !isMobileViewport && (

          <button

            onClick={() => setShowQuickAddJournal(true)}

            style={{

              width: '48px', height: '48px',

              borderRadius: '50%',

              background: 'linear-gradient(135deg, #8b6914 0%, #5c4a1f 100%)',

              border: '2px solid #c9a227',

              color: '#ffd700',

              fontSize: '1.3rem',

              cursor: 'pointer',

              boxShadow: '0 4px 16px rgba(0,0,0,0.5)',

              display: 'flex', alignItems: 'center', justifyContent: 'center',

              transition: 'transform 0.2s',

            }}

            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}

            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}

            title="Quick Journal Entry"

          >📓</button>

        )}

      </div>

    </>

  ) : null;



  // DM journal cluster — 2026-04-30: moved out of the dm view return

  // tree, same reasoning as playerJournalChrome above. Mounted on

  // every DM view via floatingChrome.

  const dmQuickJournalCount = dmJournalReadCandidates.length;

  const dmJournalChrome = isDM ? (

    <>

      {/* Floating DM Quick Journal — shifts down to bottom:16 when the

          bug-report drawer is open so the drawer can anchor above it. */}

      {/* DM journal cluster — same unified rule as player, keyed off

          showDmJournal instead of showQuickAddJournal. */}

      <div style={{ position: 'fixed', ...computeJournalPos(!!showDmJournal), zIndex: 1500, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', transition: 'right 200ms ease-out, bottom 200ms ease-out' }}>

        {dmJournalConfirm && <div style={{ padding: '8px 14px', background: 'rgba(39,201,90,0.15)', border: '1px solid rgba(39,201,90,0.4)', borderRadius: 8, fontSize: '0.75rem', color: '#27c95a', maxWidth: 340, animation: 'overlayFadeIn 0.3s ease' }}>

          {typeof dmJournalConfirm === 'string' ? dmJournalConfirm : <>

            {dmJournalConfirm.entries?.map((e, i) => {

              const catInfo = DM_JOURNAL_CATEGORIES[e.category] || JOURNAL_CATEGORIES[e.category] || { icon: '📝' };

              const isCreated = dmJournalConfirm.entries.indexOf(e) < (dmJournalConfirm.created || 0);

              return <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>

                <span style={{ cursor: 'pointer' }} onClick={() => { const entry = dmJournalReadCandidates.find(j => j.id === e.id); if (entry) { setShowDmJournal(true); setDmJournalViewEntry(entry); } }}>

                  {catInfo.icon} {isCreated ? 'Created' : 'Updated'}: <span style={{ textDecoration: 'underline' }}>{e.name}</span>

                </span>

                <span onClick={() => { setLibraryInitEntry({ ...e, _type: e.category === 'npcs' ? 'npc' : 'journal' }); setView('npcs'); setShowDmJournal(false); setDmJournalConfirm(null); }} style={{ fontSize: '0.6rem', color: '#c9a227', cursor: 'pointer', opacity: 0.8 }}>View →</span>

              </div>;

            })}

          </>}

        </div>}

        {showDmJournal && <div style={{ width: 420, maxWidth: 'calc(100vw - 32px)', maxHeight: '75vh', overflowY: 'auto', background: S._colors?.bgCardSolid || '#1e1624', border: '2px solid rgba(201,162,39,0.4)', borderRadius: 10, padding: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }}>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>

            <span style={{ fontSize: '0.88rem', color: '#c9a227', fontWeight: 700 }}>📓 DM Quick Journal</span>

            <div style={{ display: 'flex', gap: 4 }}>

              {dmQuickJournalCount > 0 && <span style={{ fontSize: '0.6rem', padding: '2px 6px', borderRadius: 10, background: 'rgba(201,162,39,0.15)', color: '#c9a227' }}>{dmQuickJournalCount}</span>}

              <button onClick={() => setShowDmJournal(false)} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: '#888', cursor: 'pointer', padding: '2px 6px', fontSize: '0.75rem' }}>✕</button>

            </div>

          </div>

          <QuickAddInput value={dmJournalText} onChange={setDmJournalText} onSubmit={handleDmJournalSubmit} onCancel={() => setDmJournalText('')} settings={journalSettings?.quickAdd} placeholder="DM note... #N@Viego' met at tavern" autoFocus entries={dmJournalReadCandidates} focusRef={dmJournalEditorRef} onAutocompleteSearch={hasDmFullJournalDiscovery ? undefined : ({ query }) => fetchJournalAutocompleteEntries(discoverDmJournalEntries, query, 'DM Quick Journal')} />

          {/* Note type pills */}

          {dmJournalText.trim() && <div style={{ display: 'flex', gap: 3, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>

            <span style={{ fontSize: '0.5rem', color: '#666', marginRight: 2 }}>Type:</span>

            {QUICK_NOTE_CATEGORIES.map(f => {

              const isSelected = dmNpcNoteCategories.includes(f.id);

              return <button key={f.id} onClick={() => {

                setDmNpcNoteCategories(prev => {

                  if (isSelected && prev.length > 1) return prev.filter(c => c !== f.id);

                  if (isSelected && prev.length === 1) return prev;

                  return [...prev, f.id];

                });

              }} style={{ padding: '3px 8px', fontSize: '0.6rem', borderRadius: 5, background: isSelected ? 'rgba(201,162,39,0.15)' : 'rgba(255,255,255,0.02)', border: `1px solid ${isSelected ? '#c9a22760' : '#3a302030'}`, color: isSelected ? '#c9a227' : '#888', cursor: 'pointer', fontWeight: isSelected ? 600 : 400 }}>{f.icon} {f.label}</button>;

            })}

          </div>}

          {/* Quest association — shows active quests linked to detected NPCs */}

          {dmJournalText.trim() && (() => {

            // Find NPC names in the text

            const npcMatches = [];

            const tagRx = /#N@([^'\n#@]+)/gi;

            let tm;

            while ((tm = tagRx.exec(dmJournalText)) !== null) npcMatches.push(tm[1].trim().replace(/["']/g, ''));

            if (npcMatches.length === 0) return null;

            if (!hasDmFullJournalDiscovery) {

              const discoveredQuests = getClearDiscoveredDmQuestEntriesForText(dmJournalText);

              if (discoveredQuests.length === 0) return null;

              return <div style={{ display: 'flex', gap: 3, marginTop: 3, alignItems: 'center', flexWrap: 'wrap' }}>

                <span style={{ fontSize: '0.5rem', color: '#9b59b6', marginRight: 2 }}>ðŸ“œ Quest:</span>

                <button onClick={clearDmQuestSelection} style={{ padding: '2px 6px', fontSize: '0.55rem', borderRadius: 4, background: !dmSelectedQuest ? 'rgba(155,89,182,0.12)' : 'transparent', border: `1px solid ${!dmSelectedQuest ? '#9b59b640' : '#3a302030'}`, color: !dmSelectedQuest ? '#9b59b6' : '#666', cursor: 'pointer' }}>None</button>

                {discoveredQuests.map(q => {

                  const isSel = dmSelectedQuest === q.id;

                  return <button key={q.id} onClick={() => selectDmQuestExplicitly(q.id)} style={{ padding: '2px 6px', fontSize: '0.55rem', borderRadius: 4, background: isSel ? 'rgba(155,89,182,0.15)' : 'transparent', border: `1px solid ${isSel ? '#9b59b660' : '#9b59b625'}`, color: '#9b59b6', cursor: 'pointer', fontWeight: 600 }}>{q.name}{q.status === 'active' ? '' : ` (${q.status || '?'})`}</button>;

                })}

              </div>;

            }

            // Find active quests associated with those NPCs

            const activeQuests = dmJournalReadCandidates

              .filter(j => j.category === 'quests' && j.status !== 'completed')

              .filter(j => {

                const desc = (j.description || '').toLowerCase();

                const questGiver = (j.questGiver || '').toLowerCase();

                const relatedNpcs = (j.relatedNpcs || []).map(n => n.toLowerCase());

                return npcMatches.some(name => desc.includes(name.toLowerCase()) || questGiver === name.toLowerCase() || relatedNpcs.includes(name.toLowerCase()));

              })

              .sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));

            // Also check NPC library for quest connections

            const npcQuests = npcMatches.flatMap(name => {

              const npc = Object.values(npcLibrary || {}).find(np => np.name?.toLowerCase() === name.toLowerCase());

              return (npc?.questConnections || []).map(q => q);

            });

            const questNames = new Set([...activeQuests.map(q => q.name), ...npcQuests]);

            const allQuests = dmJournalReadCandidates.filter(j => j.category === 'quests' && j.status !== 'completed');

            if (allQuests.length === 0) return null;

            // Auto-select most recent active associated quest

            if (!dmSelectedQuest && activeQuests.length > 0) {

              setTimeout(() => autoSelectDmQuest(activeQuests[0].id), 0);

            }

            return <div style={{ display: 'flex', gap: 3, marginTop: 3, alignItems: 'center', flexWrap: 'wrap' }}>

              <span style={{ fontSize: '0.5rem', color: '#9b59b6', marginRight: 2 }}>📜 Quest:</span>

              <button onClick={clearDmQuestSelection} style={{ padding: '2px 6px', fontSize: '0.55rem', borderRadius: 4, background: !dmSelectedQuest ? 'rgba(155,89,182,0.12)' : 'transparent', border: `1px solid ${!dmSelectedQuest ? '#9b59b640' : '#3a302030'}`, color: !dmSelectedQuest ? '#9b59b6' : '#666', cursor: 'pointer' }}>None</button>

              {allQuests.slice(0, 6).map(q => {

                const isAssociated = questNames.has(q.name);

                const isSel = dmSelectedQuest === q.id;

                return <button key={q.id} onClick={() => selectDmQuestExplicitly(q.id)} style={{ padding: '2px 6px', fontSize: '0.55rem', borderRadius: 4, background: isSel ? 'rgba(155,89,182,0.15)' : 'transparent', border: `1px solid ${isSel ? '#9b59b660' : isAssociated ? '#9b59b625' : '#3a302020'}`, color: isSel ? '#9b59b6' : isAssociated ? '#9b59b6' : '#666', cursor: 'pointer', fontWeight: isAssociated ? 600 : 400 }}>{q.name}{q.status === 'active' ? '' : ` (${q.status || '?'})`}</button>;

              })}

            </div>;

          })()}

          <div style={{ display: 'flex', gap: 6, marginTop: 4, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>

            <button onClick={() => handleDmJournalSubmit(dmJournalText)} disabled={!dmJournalText.trim()} style={{ padding: '4px 12px', fontSize: '0.7rem', fontWeight: 600, background: dmJournalText.trim() ? 'rgba(39,201,90,0.15)' : 'transparent', border: `1px solid ${dmJournalText.trim() ? '#27c95a50' : '#3a3020'}`, borderRadius: 6, color: dmJournalText.trim() ? '#27c95a' : '#666', cursor: dmJournalText.trim() ? 'pointer' : 'default' }}>Save</button>

            <button onClick={() => setDmJournalRevealable(!dmJournalRevealable)} style={{ padding: '4px 8px', fontSize: '0.62rem', background: dmJournalRevealable ? 'rgba(74,144,217,0.15)' : 'rgba(136,136,136,0.08)', border: `1px solid ${dmJournalRevealable ? '#4a90d960' : '#3a3020'}`, borderRadius: 6, color: dmJournalRevealable ? '#4a90d9' : '#888', cursor: 'pointer' }}>{dmJournalRevealable ? '👁 Revealable' : '🔒 DM Only'}</button>

            <div style={{ position: 'relative', display: 'inline-block' }}>

              <button onClick={() => setShowDmSessionPicker(!showDmSessionPicker)} style={{ padding: '4px 8px', fontSize: '0.58rem', background: dmLogToSession ? 'rgba(201,162,39,0.12)' : 'rgba(136,136,136,0.06)', border: `1px solid ${dmLogToSession ? '#c9a22750' : '#3a3020'}`, borderRadius: 6, color: dmLogToSession ? '#c9a227' : '#666', cursor: 'pointer' }}>📖 {dmLogToSession ? (getDmActiveSession()?.name || 'New Session') : 'No Session'} ▾</button>

              {showDmSessionPicker && <div style={{ position: 'absolute', bottom: '100%', left: 0, marginBottom: 4, width: 200, background: S._colors?.bgCardSolid || '#1e1624', border: '1px solid rgba(201,162,39,0.3)', borderRadius: 6, padding: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.5)', zIndex: 10 }}>

                <button onClick={() => { setDmLogToSession(false); setDmActiveSessionId(null); setShowDmSessionPicker(false); }} style={{ width: '100%', padding: '4px 8px', fontSize: '0.62rem', background: !dmLogToSession ? 'rgba(201,162,39,0.1)' : 'transparent', border: 'none', borderRadius: 4, color: !dmLogToSession ? '#c9a227' : '#888', cursor: 'pointer', textAlign: 'left', marginBottom: 2 }}>📖 No Session Logging</button>

                <button onClick={() => { setDmLogToSession(true); setDmActiveSessionId(null); setShowDmSessionPicker(false); }} style={{ width: '100%', padding: '4px 8px', fontSize: '0.62rem', background: dmLogToSession && !dmActiveSessionId ? 'rgba(201,162,39,0.1)' : 'transparent', border: 'none', borderRadius: 4, color: dmLogToSession && !dmActiveSessionId ? '#c9a227' : '#888', cursor: 'pointer', textAlign: 'left', marginBottom: 2 }}>✨ Auto (latest/new)</button>

                <div style={{ borderTop: '1px solid #3a3020', margin: '4px 0', paddingTop: 4 }}>

                  {listJournalSessions(getDmSessionCandidates()).map(s => (

                    <button key={s.id} onClick={() => { setDmLogToSession(true); setDmActiveSessionId(s.id); setShowDmSessionPicker(false); }} style={{ width: '100%', padding: '3px 8px', fontSize: '0.6rem', background: dmActiveSessionId === s.id ? 'rgba(201,162,39,0.1)' : 'transparent', border: 'none', borderRadius: 4, color: dmActiveSessionId === s.id ? '#c9a227' : '#b0a48a', cursor: 'pointer', textAlign: 'left', marginBottom: 1 }}>📖 {s.name}{s.sessionDate ? ` (${s.sessionDate})` : ''}</button>

                  ))}

                  {listJournalSessions(getDmSessionCandidates()).length === 0 && <div style={{ fontSize: '0.58rem', color: '#666', padding: '4px 8px' }}>No sessions yet — one will be auto-created</div>}

                </div>

              </div>}

            </div>

          </div>

          <div style={{ display: 'flex', gap: 3, marginBottom: 6, flexWrap: 'wrap', alignItems: 'center' }}>

            <input value={dmJournalSearch} onChange={e => setDmJournalSearch(e.target.value)} placeholder="Search..." style={{ flex: 1, minWidth: 100, padding: '3px 8px', fontSize: '0.68rem', background: 'rgba(0,0,0,0.2)', border: '1px solid #3a3020', borderRadius: 4, color: '#e8d5b7', outline: 'none' }} />

            {['all', ...DM_JOURNAL_ORDER.slice(0, 8)].map(cat => {

              const info = cat === 'all' ? { icon: '📋', name: 'All' } : (DM_JOURNAL_CATEGORIES[cat] || JOURNAL_CATEGORIES[cat]);

              if (!info) return null;

              const count = cat === 'all' ? dmJournalReadCandidates.length : dmJournalReadCandidates.filter(e => e.category === cat).length;

              if (cat !== 'all' && count === 0) return null;

              return <button key={cat} onClick={() => setDmJournalFilter(cat)} style={{ padding: '1px 5px', fontSize: '0.55rem', borderRadius: 4, background: dmJournalFilter === cat ? 'rgba(201,162,39,0.15)' : 'transparent', border: `1px solid ${dmJournalFilter === cat ? '#c9a22750' : 'transparent'}`, color: dmJournalFilter === cat ? '#c9a227' : '#888', cursor: 'pointer' }} title={info.name}>{info.icon}</button>;

            })}

          </div>

          {(() => {

            const dmSearchQuery = dmJournalSearch.trim().toLowerCase();

            const localEntries = dmJournalReadCandidates.filter(e => dmJournalFilter === 'all' || e.category === dmJournalFilter).filter(e => !dmSearchQuery || e.name?.toLowerCase().includes(dmSearchQuery) || e.description?.toLowerCase().includes(dmSearchQuery)).sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));

            const discoveryEntries = !hasDmFullJournalDiscovery && dmSearchQuery.length >= 3

              ? dmJournalTitleDiscoveryResults.filter(e => dmJournalFilter === 'all' || e.category === dmJournalFilter)

              : [];

            const entries = (() => {

              const byId = new Map();

              [...discoveryEntries, ...localEntries].forEach(entry => {

                if (!entry?.id || byId.has(entry.id)) return;

                byId.set(entry.id, entry);

              });

              return Array.from(byId.values()).slice(0, 50);

            })();

            if (entries.length === 0) return <div style={{ textAlign: 'center', padding: '12px 0', color: '#666', fontSize: '0.72rem' }}>{dmJournalReadCandidates.length === 0 ? 'No entries yet. Use tags!' : 'No matches.'}</div>;

            return <div style={{ maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>

              {entries.slice(0, 50).map(entry => {

                const catInfo = DM_JOURNAL_CATEGORIES[entry.category] || JOURNAL_CATEGORIES[entry.category] || { icon: '📝', color: '#888' };

                const isExpanded = dmJournalViewEntry?.id === entry.id;

                const isRevealable = entry._dmRevealable;

                const isRevealed = entry._dmRevealed;

                return <div key={entry.id} onClick={() => setDmJournalViewEntry(isExpanded ? null : entry)} style={{ padding: '6px 8px', background: isExpanded ? 'rgba(201,162,39,0.06)' : isRevealed ? 'rgba(39,201,90,0.03)' : 'rgba(255,255,255,0.02)', borderTop: `1px solid ${isExpanded ? '#c9a22730' : '#3a302015'}`, borderRight: `1px solid ${isExpanded ? '#c9a22730' : '#3a302015'}`, borderBottom: `1px solid ${isExpanded ? '#c9a22730' : '#3a302015'}`, borderLeft: `3px solid ${catInfo.color}40`, borderRadius: 5, cursor: 'pointer' }}>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>

                    <span style={{ fontSize: '0.7rem' }}>{catInfo.icon}</span>

                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: catInfo.color, flex: 1 }}>{entry.name || '(unnamed)'}</span>

                    {isRevealed && <span style={{ fontSize: '0.45rem', padding: '1px 3px', borderRadius: 3, background: 'rgba(39,201,90,0.15)', color: '#27c95a' }}>revealed</span>}

                    {isRevealable && !isRevealed && <span style={{ fontSize: '0.45rem', padding: '1px 3px', borderRadius: 3, background: 'rgba(74,144,217,0.15)', color: '#4a90d9' }}>👁</span>}

                    <span style={{ fontSize: '0.5rem', color: '#555' }}>{new Date(entry.updatedAt || entry.createdAt).toLocaleDateString()}</span>

                  </div>

                  {isExpanded && <div style={{ marginTop: 5 }}>

                    {entry.description && <div style={{ fontSize: '0.68rem', color: '#b0a48a', lineHeight: 1.5, whiteSpace: 'pre-wrap', maxHeight: 160, overflowY: 'auto', marginBottom: 5 }}>{renderDmDescription(entry.description)}</div>}

                    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>

                      {isRevealable && !isRevealed && <button onClick={(ev) => { ev.stopPropagation(); void revealDmEntry(entry.id); }} style={{ padding: '2px 8px', fontSize: '0.6rem', fontWeight: 600, background: 'rgba(74,144,217,0.12)', border: '1px solid #4a90d950', borderRadius: 4, color: '#4a90d9', cursor: 'pointer' }}>👁 Reveal</button>}

                      <button onClick={(ev) => { ev.stopPropagation(); const updatedEntry = { ...entry, _dmRevealable: !entry._dmRevealable, updatedAt: new Date().toISOString() }; if (typeof upsertDmJournalEntry === 'function') { void upsertDmJournalEntry({ entryId: updatedEntry.id, entry: updatedEntry }).catch(error => console.error('DM journal row visibility update failed:', error)); setDmJournalViewEntry(updatedEntry); } else { const updated = (dmJournal || []).map(e => e.id === entry.id ? updatedEntry : e); saveDmJournal(updated); } }} style={{ padding: '2px 6px', fontSize: '0.55rem', background: 'rgba(255,255,255,0.03)', border: '1px solid #3a3020', borderRadius: 3, color: '#888', cursor: 'pointer' }}>{isRevealable ? '🔒' : '👁'}</button>

                      <button onClick={(ev) => { ev.stopPropagation(); if (window.confirm('Delete?')) { if (typeof deleteDmJournalEntry === 'function') { void deleteDmJournalEntry({ entryId: entry.id }).catch(error => console.error('DM journal row delete failed:', error)); } else { saveDmJournal((dmJournal || []).filter(e => e.id !== entry.id)); } if (isExpanded) setDmJournalViewEntry(null); } }} style={{ padding: '2px 6px', fontSize: '0.55rem', background: 'rgba(201,39,39,0.08)', border: '1px solid rgba(201,39,39,0.2)', borderRadius: 3, color: '#ff6961', cursor: 'pointer' }}>🗑</button>

                      <button onClick={(ev) => { ev.stopPropagation(); setLibraryInitEntry({ ...entry, _type: entry.category === 'npcs' ? 'npc' : 'journal' }); setView('npcs'); setShowDmJournal(false); }} style={{ padding: '2px 8px', fontSize: '0.55rem', background: 'rgba(201,162,39,0.1)', border: '1px solid rgba(201,162,39,0.3)', borderRadius: 3, color: '#c9a227', cursor: 'pointer' }}>View in Library →</button>

                    </div>

                  </div>}

                  {!isExpanded && entry.description && <div style={{ fontSize: '0.58rem', color: '#666', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.description.replace(/\{\{REVEAL\}\}/g, '').replace(/\{\{\/REVEAL\}\}/g, '').substring(0, 100)}</div>}

                </div>;

              })}

            </div>;

          })()}

        </div>}

        {!showDmJournal && <button onClick={() => { setShowDmJournal(true); setDmNpcNoteCategories(['session']); }} style={{ width: 48, height: 48, borderRadius: '50%', background: 'linear-gradient(135deg, #8b6914 0%, #5c4a1f 100%)', border: '2px solid #c9a227', color: '#ffd700', fontSize: '1.3rem', cursor: 'pointer', boxShadow: '0 4px 16px rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'transform 0.2s', position: 'relative' }} onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'} onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'} title="DM Quick Journal">📓

          {dmQuickJournalCount > 0 && <span style={{ position: 'absolute', top: -4, right: -4, width: 18, height: 18, borderRadius: '50%', background: '#c9a227', color: '#1a1008', fontSize: '0.55rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{dmQuickJournalCount}</span>}

        </button>}

      </div>

    </>

  ) : null;



  // The login (select) view gets bug-reporter only — see user requirement

  // 2026-04-30. Every other view gets the full quick-access bundle:

  // bug-reporter button + stat popup (player only) + dice roller +

  // journal cluster (player or DM variant). The journal clusters are

  // role-conditional inside their own chrome variables, so dropping both

  // in here is safe — only one renders at a time.

  const authCharacterLinkPrompt = shouldShowAuthCharacterLinkNotice ? (

    <div
      role={authLinkStatus?.tone === 'error' ? 'alert' : 'status'}
      aria-live="polite"
      style={{
        position: 'fixed',
        left: '50%',
        top: isMobileViewport ? 66 : 14,
        transform: 'translateX(-50%)',
        zIndex: 1300,
        width: 'min(92vw, 720px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: '10px 12px',
        borderRadius: 'var(--rm)',
        border: authLinkStatus?.tone === 'error'
          ? '1px solid rgba(255,105,97,0.5)'
          : '1px solid rgba(201,162,39,0.45)',
        background: authLinkStatus?.tone === 'error'
          ? 'rgba(45,12,12,0.96)'
          : 'rgba(26,16,8,0.96)',
        boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
        color: 'var(--tp)',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ minWidth: 220, flex: 1, textAlign: 'center' }}>
        <div style={{ fontWeight: 700, color: 'var(--tgb)', fontSize: 13 }}>
          {authLinkStatus?.tone === 'error'
            ? 'Character access needs attention'
            : authLinkPending
              ? `Connecting access for ${char?.name || 'this character'}`
              : 'Character access linked'}
        </div>
        <div style={{ color: 'var(--ts)', fontSize: 12, marginTop: 2 }}>
          {authLinkStatus?.message || `Linking your sign-in to ${char?.name || 'this character'}...`}
        </div>
      </div>
    </div>

  ) : null;



  const sandboxChrome = isCampaignSandboxSession ? (
    <div
      role="group"
      aria-label="Campaign sandbox local only"
      style={{
        position: 'fixed',
        left: '50%',
        top: 12,
        transform: 'translateX(-50%)',
        zIndex: 1290,
        padding: '7px 12px',
        borderRadius: 6,
        border: '1px solid rgba(90,159,212,0.55)',
        background: 'rgba(8,18,28,0.92)',
        color: '#d8ecff',
        fontSize: '0.76rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: 8,
      }}
    >
      <span>Campaign Sandbox - Local only</span>
      <button
        type="button"
        onClick={exitCampaignSandbox}
        style={{
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.24)',
          borderRadius: 4,
          color: '#f2f7ff',
          cursor: 'pointer',
          fontSize: '0.68rem',
          fontWeight: 700,
          padding: '3px 7px',
          textTransform: 'uppercase',
        }}
      >
        Back to Campaign
      </button>
      <button
        type="button"
        onClick={() => setShowSandboxParty(true)}
        style={{
          background: 'rgba(90,159,212,0.16)',
          border: '1px solid rgba(90,159,212,0.45)',
          borderRadius: 4,
          color: '#d8ecff',
          cursor: 'pointer',
          fontSize: '0.68rem',
          fontWeight: 700,
          padding: '3px 7px',
          textTransform: 'uppercase',
        }}
      >
        View As
      </button>
      <button
        type="button"
        onClick={() => setShowSandboxSettings(true)}
        style={{
          background: 'rgba(163,107,255,0.14)',
          border: '1px solid rgba(163,107,255,0.44)',
          borderRadius: 4,
          color: '#e7d8ff',
          cursor: 'pointer',
          fontSize: '0.68rem',
          fontWeight: 700,
          padding: '3px 7px',
          textTransform: 'uppercase',
        }}
      >
        Settings
      </button>
      <button
        type="button"
        onClick={() => {
          if (typeof window !== 'undefined') {
            window.alert('Sandbox-to-campaign sync needs a dedicated live import flow before it can write campaign data. No live campaign data was changed.');
          }
        }}
        style={{
          background: 'rgba(201,162,39,0.14)',
          border: '1px solid rgba(201,162,39,0.42)',
          borderRadius: 4,
          color: '#f1d97e',
          cursor: 'pointer',
          fontSize: '0.68rem',
          fontWeight: 700,
          padding: '3px 7px',
          textTransform: 'uppercase',
        }}
      >
        Sync to Campaign
      </button>
    </div>
  ) : null;

  const floatingChrome = (

    <>

      {sandboxChrome}

      {authCharacterLinkPrompt}

      {bugReportChrome}

      {statPopupChrome}

      {diceRollerChrome}

      {playerJournalChrome}

      {dmJournalChrome}

    </>

  );





  // BUG REPORTS PAGE — full-screen browser for the user's own reports

  // (grid, leaderboard, nickname editor). Reachable from the floating 🪲

  // button's "📋 My reports →" link.

  if (view === 'bug-reports') {

    return (

      <>

        <BugReportsPage

          onBack={() => setView(isDM ? 'dm' : (charId ? 'home' : 'select'))}

        />

        {floatingChrome}

      </>

    );

  }

  // DEV DASHBOARD — dev-only landing page with three sections: at-a-

  // glance stats (users / reports / characters), the full bug-reports

  // browser (filters / grid / export), and insights (30-day sparkline,

  // top tags, top views, link to the Convex dashboard for raw infra

  // metrics). Reachable from the floating 🪲 drawer's "🛠️ Dev →" link

  // and from the homepage DevAccessLink. The page enforces dev access

  // server-side and re-checks via useIsDev() client-side.

  if (view === 'dev-dashboard') {

    return (

      <>

        <DevDashboardPage

          onBack={() => setView(isDM ? 'dm' : (charId ? 'home' : 'select'))}

          onNavigateToReports={() => setView('dev-reports')}

        />

        {floatingChrome}

      </>

    );

  }

  if (view === 'dev-reports') {

    return (

      <>

        <DevReportsPage

          onBack={() => setView('dev-dashboard')}

        />

        {floatingChrome}

      </>

    );

  }

  // SELECT SCREEN

  if (view === 'select') return <><div style={S.container}>

    <h1 style={S.title}>⚔️ Survival Tracker ⚔️</h1>

    <div style={{ maxWidth: '500px', margin: '0 auto' }}>

      <HoverCard><h2 style={S.subtitle}>📜 Select Character</h2>

        {Object.values(chars).length === 0 ? <p style={{ textAlign: 'center', opacity: 0.7 }}>No characters yet.</p> : Object.values(chars).map(c => <div key={c.id} onClick={() => setSelChar(c.id)} style={{ padding: '12px', marginBottom: '8px', background: selChar === c.id ? 'rgba(201,162,39,0.2)' : 'rgba(0,0,0,0.3)', borderRadius: '4px', border: selChar === c.id ? '1px solid #c9a227' : '1px solid transparent', cursor: 'pointer' }}><strong>{c.name}</strong> - {c.race} <span style={{ opacity: 0.7 }}>Lvl {c.level || 1}</span> <span style={{ float: 'right', opacity: 0.7 }}>HP: {c.currentHp}/{getEffectiveMaxHp(c)}</span></div>)}

        {selChar && <div style={{ marginTop: '16px' }}><label style={S.label}>Password</label><input style={S.input} type="password" value={loginPw} onChange={e => setLoginPw(e.target.value)} onKeyPress={e => e.key === 'Enter' && login(selChar)} placeholder="Enter password..." /><button style={S.btn} onClick={() => login(selChar)}>🗝️ Login</button></div>}

      </HoverCard>

      {/* Practice Character Loader */}

      <div style={{ ...S.card, background: 'linear-gradient(135deg, rgba(90,159,212,0.1), rgba(155,89,182,0.1))', border: '1px dashed rgba(201,162,39,0.5)' }}>

        <h2 style={{ ...S.subtitle, color: '#5a9fd4' }}>🧪 Developer Testing</h2>

        <p style={{ fontSize: '0.8rem', opacity: 0.7, marginBottom: '12px' }}>Load pre-configured practice characters with sample items, abilities, and status effects.</p>

        {!chars['demo-lika'] ? (

          <button

            style={{ ...S.btn, ...S.btnEdit, width: '100%' }}

            onClick={() => {

              const lika = createLikaCharacter();

              saveChars({ ...chars, [lika.id]: lika });

            }}

          >

            🧝‍♀️ Create Li'ka (Practice Character)

          </button>

        ) : (

          <div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>

              <button 

                style={{ ...S.btn, ...S.btnSuccess, flex: 1 }} 

                onClick={() => {

                  setCharId('demo-lika');

                  setIsDM(false);

                  setView('home');

                }}

              >

                👤 Play as Li'ka

              </button>

              <button 

                style={{ ...S.btn, ...S.btnEdit, flex: 1 }} 

                onClick={() => {

                  setCharId('demo-lika');

                  setIsDM(true);

                  setView('dm');

                }}

              >

                👑 DM View

              </button>

            </div>

            <button

              style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, width: '100%' }}

              onClick={() => {

                const c = { ...chars };

                delete c['demo-lika'];

                saveChars(c);

              }}

            >

              🗑️ Remove Li'ka

            </button>

            <div style={{ fontSize: '0.7rem', opacity: 0.5, marginTop: '8px', textAlign: 'center' }}>Password: "lika"</div>

          </div>

        )}

      </div>



      <HoverCard><h2 style={S.subtitle}>✨ Create Character</h2><CreateForm onCreate={createChar} onSuccess={id => { setCharId(id); setIsDM(false); setView('home'); }} /></HoverCard>



      {/* Dev access — only renders when the signed-in user is the

          maintainer (per the email allowlist) OR when running on

          localhost (dev environment). Gated on viewerContext !==

          undefined so we only call the hook when the Convex provider

          is mounted (auth-on mode). */}

      {viewerContext !== undefined && (

        <DevAccessLink onNavigate={() => setView('dev-dashboard')} />

      )}

      

      {/* Character Sheet Import */}

      <div style={{ ...S.card, background: 'linear-gradient(135deg, rgba(90,159,212,0.08), rgba(201,162,39,0.06))', border: '1px solid rgba(201,162,39,0.55)' }}>

        <h2 style={{ ...S.subtitle, color: '#ffd700' }}>📄 Import Character Sheet</h2>

        <p style={{ fontSize: '0.9rem', opacity: 0.85, marginBottom: '12px' }}>

          Review a supported character sheet import (DDB or DiceCloud) before creating the character.

        </p>

        <button style={{ ...S.btn, ...S.btnEdit, width: '100%' }} onClick={() => setShowDdbImport(true)}>

          📥 Import Character Sheet

        </button>

      </div>

      

      {showDdbImport && (
        <CharacterImportReviewModal
          mode="create"
          availableSources={['shenyi', 'vental']}
          uploadAppPayload={characterImportUploadClient}
          onClose={() => setShowDdbImport(false)}
          onApplyCreate={handleApplyCharacterImportCreate}
        />
      )}

      

      <HoverCard><h2 style={S.subtitle}>🏰 DM Login</h2><input style={S.input} type="password" placeholder="DM Password" value={loginPw} onChange={e => setLoginPw(e.target.value)} onKeyPress={e => e.key === 'Enter' && dmLogin()} /><button style={S.btn} onClick={dmLogin}>👑 Enter as DM</button></HoverCard>



      

      {/* Party Journal Access */}

      <div style={{ ...S.card, background: 'linear-gradient(135deg, rgba(201,162,39,0.15), rgba(139,105,20,0.1))', border: '2px solid rgba(201,162,39,0.4)' }}>

        <h2 style={{ ...S.subtitle, color: '#ffd700' }}>📚 Party Journal</h2>

        <p style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '12px' }}>Access the shared party journal with sessions, quests, creatures, NPCs, and plants.</p>

        <button 

          style={{ ...S.btn, ...S.btnSuccess, width: '100%' }} 

          onClick={() => setView('journal')}

        >

          📖 Open Journal

        </button>

      </div>

      

      {/* Combat Access */}

      {combatEnabled && (

      <div style={{ ...S.card, background: displayedCombat?.active ? 'linear-gradient(135deg, rgba(201,39,39,0.2), rgba(139,20,20,0.15))' : 'linear-gradient(135deg, rgba(100,39,39,0.15), rgba(80,20,20,0.1))', border: `2px solid ${displayedCombat?.active ? 'rgba(201,39,39,0.6)' : 'rgba(201,39,39,0.3)'}` }}>

        <h2 style={{ ...S.subtitle, color: displayedCombat?.active ? '#ff6666' : '#c92727' }}>

          ⚔️ Combat {displayedCombat?.active && <span style={{ fontSize: '0.8rem', background: '#c92727', padding: '2px 8px', borderRadius: '4px', marginLeft: '8px' }}>ACTIVE - Round {displayedCombat.round}</span>}

        </h2>

        <p style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '12px' }}>

          {displayedCombat?.active

            ? `Combat in progress with ${displayedCombat.combatants?.length || 0} combatants.`

            : 'Track initiative, damage, and HP during combat encounters.'}

        </p>

        <button

          style={{ ...S.btn, width: '100%', background: displayedCombat?.active ? 'linear-gradient(180deg, #c92727 0%, #8b1a1a 100%)' : 'linear-gradient(180deg, #6b2727 0%, #4a1a1a 100%)', borderColor: displayedCombat?.active ? '#ff4444' : '#c92727' }}

          onClick={() => setView('combat')}

        >

          {displayedCombat?.active ? '⚔️ Join Combat' : '⚔️ Open Combat'}

        </button>

      </div>

      )}

      

      {/* Theme Configuration */}

      <div style={{ ...S.card, background: `linear-gradient(135deg, ${currentTheme.colors.accentGlow}, rgba(0,0,0,0.2))`, border: `1px solid ${currentTheme.colors.accent}40` }}>

        <h2 style={{ ...S.subtitle, color: currentTheme.colors.accent }}>🎨 Theme Settings</h2>

        <p style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '12px' }}>

          Current: {baseTheme.name} {currentVariationId !== 'default' ? `(${currentVariationId})` : ''}

        </p>

        <button 

          style={{ ...S.btn, ...S.btnEdit, width: '100%' }} 

          onClick={() => setShowThemeConfig(true)}

        >

          🎨 Configure Theme

        </button>

      </div>

    </div>

  </div>{bugReportChrome}</>;



  // JOURNAL VIEW

  if (view === 'journal') {

    return (

      <>

        {isMobileViewport && (

          <MobileStickyHeader

            char={char}

            backTo={() => setView(charId ? 'home' : 'select')}

            title="📚 Journal"

          />

        )}

        {/* 2026-05-11 — Session 8: pull-to-refresh on the journal scroll
            container. The journal data flows through Convex
            subscriptions; refresh is a real operation, not decorative.
            The wrapper component scopes the hook to this view so we
            don't conditionally call hooks in App.js. */}
        <PullToRefreshContainer
          style={mobileBodyStyle}
          onRefresh={async () => {
            // Convex subscriptions refresh automatically on reconnect.
            // For an explicit pull, we yield a tick and resolve — the
            // visual indicator gives the user confirmation that their
            // pull was acknowledged. A future enhancement (A.4 polish)
            // can invoke a real refetch when the storage layer exposes
            // one.
            await new Promise((resolve) => setTimeout(resolve, 600));
          }}
        >

          <JournalPage

            chars={auditSnapshotChars}

            currentCharId={charId}

            isDM={isDM}

            journal={journal}

            onUpdateJournal={saveJournal}

            upsertJournalEntry={upsertJournalEntry}

            deleteJournalEntry={deleteJournalEntry}

            onSetEntryCampaignScope={setJournalEntryCampaignScope}

            activeCampaignId={activeViewerCampaignId}

            activeCampaignName={globalSettings?.campaignName || null}

            journalSettings={journalSettings}

            onUpdateJournalSettings={saveJournalSettings}

            onBack={() => setView(charId ? 'home' : 'select')}

            initialEntry={journalInitEntry}

            onClearInitEntry={() => setJournalInitEntry(null)}

          />

        </PullToRefreshContainer>

        {isMobileViewport && (

          <MobileBottomNav

            tabSet={isDM ? 'dm' : 'player'}

            activeTab={isDM ? 'tools' : 'adventure'}

            onTabChange={(tab) => { setMobileTab(tab); setView(charId ? 'home' : 'select'); }}

          />

        )}

        {floatingChrome}

      </>

    );

  }



  // COMBAT VIEW

  if (view === 'combat') {

    if (!combatEnabled) return null;

    return (

      <>

        {isMobileViewport && (

          <MobileStickyHeader

            char={char}

            backTo={() => setView(charId ? 'home' : 'select')}

            title={`⚔️ Combat${displayedCombat?.active ? ` — Round ${displayedCombat.round}` : ''}`}

          />

        )}

        <div style={mobileBodyStyle}>

          <CombatPage

            chars={livePlayableChars}

            currentCharId={charId}

            isDM={isDM}

            combat={displayedCombat}

            combatSnapshots={displayedCombatSnapshots}

            onUpdateCombat={saveCombat}

            onUpdateCombatSnapshots={saveCombatSnapshots}

            onUpdateChars={saveChars}

            onActivateCombatItemAbility={combatItemAbilityAuthorityEnabled ? activateCombatItemAbilityFromAuthority : null}

            isCombatItemAbilityAuthoritySupported={isCombatItemAbilityAuthoritySupported}

            onBack={() => setView(charId ? 'home' : 'select')}

            diseases={runtimeDiseases}

            systems={runtimeSystems}

            journal={journal}

            npcLibrary={npcLibrary}

            onUpdateNpcLibrary={saveNpcLibrary}

            globalSettings={runtimeGlobalSettings}

            groupTravelLog={groupTravelLog}

            onSaveGroupTravelLog={saveGroupTravelLogForTravelFeature}

            messages={messages}

            onSaveMessages={saveMessages}

            onSendStatusMessage={handleSendStatusMessage}

          />

        </div>

        {isMobileViewport && (

          <MobileBottomNav

            tabSet={isDM ? 'dm' : 'player'}

            activeTab={isDM ? 'party' : 'adventure'}

            onTabChange={(tab) => { setMobileTab(tab); setView(charId ? 'home' : 'select'); }}

          />

        )}

        {floatingChrome}

      </>

    );

  }



  // HOME SCREEN

  if (view === 'home' && char) {

    const set = char.settings || DEFAULT_SETTINGS;

    

    // Count unread messages for this character (exclude urgent that show as overlay)

    const unreadCount = countUnreadMessagesForChar(messageReadView, charId, { excludeUrgent: true });

    

    return <div style={{ ...S.container, paddingTop: isMobileViewport ? '60px' : S.container?.padding, paddingBottom: isMobileViewport ? '80px' : undefined }}>

      {/* 2026-04-29 — A.1.a chrome. Mobile gets the sticky header + bottom

          nav; the system-control button row below is hidden on mobile (its

          handlers move into the hamburger overflow panel). Desktop renders

          identically to before — chrome is mobile-only and gates on

          isMobileViewport at every site. */}

      {isMobileViewport && (

        <>

          <MobileStickyHeader

            char={char}

            unreadCount={unreadCount}

            onHamburgerClick={() => setMobileMenuOpen(true)}

            onMiniHpClick={() => setMobileTab('stats')}

            onBellClick={() => setShowMessagePanel(true)}

          />

          <MobileBottomSheet

            open={mobileMenuOpen}

            onClose={() => setMobileMenuOpen(false)}

            ariaLabel="Menu"

          >

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>

              <h2 style={{ ...S.subtitle, marginTop: 0, marginBottom: '8px' }}>Menu</h2>

              <button style={{ ...S.btn, justifyContent: 'flex-start', textAlign: 'left', minHeight: '44px' }} onClick={() => { setMobileMenuOpen(false); setShowQuickAddJournal(true); }}>📓 Quick Journal</button>

              <button style={{ ...S.btn, justifyContent: 'flex-start', textAlign: 'left', minHeight: '44px' }} onClick={() => { setMobileMenuOpen(false); logout(); }}>🚪 Logout</button>

              <button style={{ ...S.btn, justifyContent: 'flex-start', textAlign: 'left', minHeight: '44px' }} onClick={() => { setMobileMenuOpen(false); setShowSettings(true); }}>⚙️ Settings</button>

              <button style={{ ...S.btn, justifyContent: 'flex-start', textAlign: 'left', minHeight: '44px' }} onClick={() => { setMobileMenuOpen(false); setShowThemeConfig(true); }}>🎨 Theme</button>

              {isDM && (

                <button style={{ ...S.btn, ...S.btnEdit, justifyContent: 'flex-start', textAlign: 'left', minHeight: '44px' }} onClick={() => { setMobileMenuOpen(false); setView('dm'); }}>📊 DM Dashboard</button>

              )}

            </div>

          </MobileBottomSheet>

        </>

      )}

      <div style={{ maxWidth: '900px', margin: '0 auto' }}>

      {!isMobileViewport && (

      <div style={{ ...S.flex, justifyContent: 'space-between', alignItems: 'flex-start' }}>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>

          <div style={S.flex}>

            <Tooltip text="Return to character select"><button style={S.btn} onClick={logout}>🚪 Logout</button></Tooltip>

            <Tooltip text="Character settings and options"><button style={S.btn} onClick={() => setShowSettings(true)}>⚙️</button></Tooltip>

            <Tooltip text="Change app theme"><button style={S.btn} onClick={() => setShowThemeConfig(true)}>🎨</button></Tooltip>

            <Tooltip text="Messages"><button style={{ ...S.btn, position: 'relative' }} onClick={() => setShowMessagePanel(true)}>

              📬 {unreadCount > 0 && <span style={{ position: 'absolute', top: '-5px', right: '-5px', background: '#c9a227', color: 'white', borderRadius: '50%', width: '18px', height: '18px', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{unreadCount}</span>}

            </button></Tooltip>

            {isDM && <Tooltip text="Go to DM Dashboard"><button style={{ ...S.btn, ...S.btnEdit }} onClick={() => setView('dm')}>📊 Dashboard</button></Tooltip>}

          </div>

          {/* Compact Wallet */}

          {(char.walletVisible !== false || isDM) && globalSettings?.currencyEnabled !== false && (

            <WalletPanel char={char} onUpdate={(u, auditHint) => updateChar(charId, u, auditHint)} globalSettings={globalSettings} />

          )}

        </div>

        {isDM && <span style={{ ...S.badge, ...S.badgeNeutral }}>👑 DM Mode</span>}

      </div>

      )}

      {isMobileViewport && (char.walletVisible !== false || isDM) && globalSettings?.currencyEnabled !== false && (

        <div style={{ marginBottom: '8px' }}>

          <WalletPanel char={char} onUpdate={(u, auditHint) => updateChar(charId, u, auditHint)} globalSettings={globalSettings} />

        </div>

      )}

      {/* 2026-04-29 — A.2: tab gating. On desktop every panel renders

          (the existing scroll-stack); on mobile only the active tab's

          panels render. Helper: `(!isMobileViewport || mobileTab === 'X')`

          gates each section by its tab id. */}

      {(!isMobileViewport || mobileTab === 'stats') && (

      <div style={{ textAlign: 'center', marginTop: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

        <div style={S.portrait}>{char.image ? <img src={char.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '🧙'}</div>

        <h1 style={{ ...S.title, marginBottom: '8px' }}>{char.name}</h1>

        <p style={{ opacity: 0.8, margin: 0 }}>{char.race}</p>

        

        {/* Inline HP/Mana bars with controls */}

        <ResourceBar char={char} onUpdate={(u, auditHint) => updateChar(charId, u, auditHint)} systems={runtimeSystems} manaEnabled={campaignManaEnabled} durabilityEnabled={campaignDurabilityEnabled} chars={chars} journal={journal} npcLibrary={npcLibrary} onSaveNpcLibrary={saveNpcLibrary} groupTravelLog={groupTravelLog} onSaveGroupTravelLog={saveGroupTravelLogForTravelFeature} messages={messages} onSaveMessages={saveMessages} onSendStatusMessage={handleSendStatusMessage} />

        

        {/* XP Progress Bar (player view) */}

        {globalSettings?.xpTrackingEnabled !== false && globalSettings?.xpVisibleToPlayers !== false && (char.xp !== undefined && char.xp !== null) && (() => {

          const xp = getXpProgress(char, globalSettings);

          return (

            <div style={{ width: '100%', maxWidth: '320px', margin: '6px auto 0' }}>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

                <span style={{ fontSize: '0.55rem', color: '#8b5fcf', fontWeight: 'bold', minWidth: '16px' }}>XP</span>

                <div style={{ flex: 1, height: '6px', background: 'rgba(0,0,0,0.3)', borderRadius: '3px', overflow: 'hidden', border: '1px solid rgba(91,58,140,0.2)' }}>

                  <div style={{ height: '100%', width: `${xp.pct}%`, background: xp.isMax ? '#ffd700' : 'linear-gradient(90deg, #5b3a8c, #8b5fcf)', borderRadius: '3px', transition: 'width 0.3s' }} />

                </div>

                <span style={{ fontSize: '0.5rem', color: '#777', minWidth: '60px', textAlign: 'right' }}>{xp.currentXp.toLocaleString()} / {xp.isMax ? 'MAX' : xp.xpForNext.toLocaleString()}</span>

              </div>

            </div>

          );

        })()}

        

        {campaignDietsEnabled && char.activeDiet && <div style={{ marginTop: '8px' }}><span style={{ ...S.badge, ...S.badgeNeutral }}>🥗 {char.activeDiet}: {char.dietProgress}/{diets[char.activeDiet]?.duration || 0}d</span></div>}

        {((char.abilities || []).length > 0 || (char.feats || []).length > 0) && <div style={{ marginTop: '16px' }}>

          {(char.abilities || []).length > 0 && <div style={{ marginBottom: '8px' }}>

            {char.abilities.map((s, i) => <Tooltip key={i} text={`Special Ability\n\n${s}\n\nClass features, racial traits, or special abilities granted by your character build.`}><span style={{ ...S.badge, ...S.badgeAbility, cursor: 'help' }}>{s}</span></Tooltip>)}

          </div>}

          {(char.feats || []).length > 0 && <div>

            {char.feats.map((t, i) => <Tooltip key={i} text={`Feat\n\n${t}\n\nFeats are special talents that give your character new capabilities.`}><span style={{ ...S.badge, ...S.badgeFeat, cursor: 'help' }}>{t}</span></Tooltip>)}

          </div>}

        </div>}

      </div>

      )}



      {/* Status Window with quick add */}

      {(!isMobileViewport || mobileTab === 'stats') && (

      <div style={{ maxWidth: '700px', margin: '20px auto', position: 'relative', zIndex: 10 }}>

        <StatusWindow

          char={char}

          diseases={runtimeDiseases}

          systems={runtimeSystems}
          diseasesEnabled={campaignDiseasesEnabled}
          hungerEnabled={campaignHungerEnabled}

          isPlayer={!isDM}

          onAddBuff={b => addQuickBuff(charId, b)}

          onAddDebuff={d => addQuickDebuff(charId, d)}

          onUpdate={(u, auditHint) => updateChar(charId, u, auditHint)}

          onApplyEffectInstanceLifecycle={effectInstanceLifecycleAuthorityEnabled ? applyEffectInstanceLifecycleFromAuthority : null}
          onReconcileItemAbilityEffectInstances={itemAbilityEffectInstanceReconcileAuthorityEnabled ? reconcileItemAbilityEffectInstancesFromAuthority : null}

          globalSettings={runtimeGlobalSettings}

          glowingSystems={glowingSystems[charId] || {}}

          onClearSystemGlow={(instanceId) => clearSystemGlow(charId, instanceId)}

          glowingBuffs={glowingBuffs[charId] || {}}

          onClearBuffGlow={(buffName) => clearBuffGlow(charId, buffName)}

          onAddBuffGlow={(buffName, color) => addBuffGlow(charId, buffName, color)}

          onApplyBuffGlowPatch={(charObj, buffName, color) => addBuffGlowPatch(charObj, buffName, color)}

        />

      </div>

      )}



      {/* 2026-04-27: standalone TechniquesPanel removed from this layout.

          Techniques now render inside SpellPanel as an embedded

          subsection (see SpellPanel render). When techniques get their

          own dedicated card later, this is where it'd be reinstated —

          with the standalone (non-embedded) form. */}



      {(!isMobileViewport || mobileTab === 'stats') && (

        <HomeCharacterSheet char={char} isDM={isDM} onUpdate={(u, auditHint) => updateChar(charId, u, auditHint)} diseases={runtimeDiseases} systems={runtimeSystems} globalSettings={runtimeGlobalSettings} />

      )}



      {/* Skills */}

      {(!isMobileViewport || mobileTab === 'skills') && (

        <div style={{ maxWidth: '700px', margin: '0 auto' }}><SkillsDisplay char={char} isDM={isDM} onUpdate={(u, auditHint) => updateChar(charId, u, auditHint)} diseases={runtimeDiseases} systems={runtimeSystems} globalSettings={runtimeGlobalSettings} /></div>

      )}



      {/* Spells */}

      {(!isMobileViewport || mobileTab === 'spells') && (

        <div style={{ maxWidth: '700px', margin: '20px auto' }}>

          <SpellPanel char={char} onUpdate={(u, auditHint) => updateChar(charId, u, auditHint)} isDM={isDM} spellLibrary={spellLibrary || []} onSaveLibrary={saveSpellLibrary} globalSettings={runtimeGlobalSettings} onSaveGlobalSettings={saveGlobalSettings} systems={runtimeSystems} manaEnabled={campaignManaEnabled} chars={chars} journal={journal} npcLibrary={npcLibrary} onSaveNpcLibrary={saveNpcLibrary} onNotify={setDmNotification} onDropConcentration={dropConcentrationForChar} groupTravelLog={groupTravelLog} onSaveGroupTravelLog={saveGroupTravelLogForTravelFeature} messages={messages} onSaveMessages={saveMessages} onSendStatusMessage={handleSendStatusMessage} />

        </div>

      )}

      

      {/* Equipment & Durability — 2026-04-28: panel hidden per BUG-REPORTS line 170 (item enhancements

          can't be expressed here; design needs rework). Underlying dura mechanics, equip toggles,

          and combat-damage hooks remain wired through the inventory and combat paths. */}

      {/* <div style={{ maxWidth: '700px', margin: '20px auto' }}>

        <EquipmentDuraPanel char={char} onUpdate={(u, auditHint) => updateChar(charId, u, auditHint)} isDM={isDM} />

      </div> */}



      {/* Inventory V2 */}

      {(!isMobileViewport || mobileTab === 'inventory') && (

      <div style={{ maxWidth: '700px', margin: '20px auto' }}>

        <InventoryPanelV2

          char={char}

          onUpdate={(u, auditHint) => updateChar(charId, u, auditHint)}

          isDM={isDM}

          partyInventory={partyInventory}

          onUpdateParty={partyInventoryWholeBlobFallback}

          onDepositToParty={movementAuthorityAuthBlocked ? rejectUnlinkedMovementAuthority : (partyInventoryMoveAuthorityEnabled ? depositPartyInventoryFromCharFromAuthority : null)}

          onTakeFromParty={movementAuthorityAuthBlocked ? rejectUnlinkedMovementAuthority : (partyInventoryMoveAuthorityEnabled ? takePartyInventoryToCharFromAuthority : null)}

          onTransferToCraftingTable={movementAuthorityAuthBlocked ? rejectUnlinkedMovementAuthority : (craftingTableTransferAuthorityEnabled ? moveCharItemToCraftingTableFromAuthority : null)}

          onAddInventoryItem={inventoryCrudAuthorityEnabled ? addInventoryItemFromAuthority : null}

          onUpdateInventoryItem={inventoryCrudAuthorityEnabled ? updateInventoryItemFromAuthority : null}

          onRemoveInventoryItem={inventoryCrudAuthorityEnabled ? removeInventoryItemFromAuthority : null}

          onSetInventoryItemFavorite={inventoryCrudAuthorityEnabled ? setInventoryItemFavoriteFromAuthority : null}

          onToggleInventoryItemEquipped={inventoryEquipToggleAuthorityEnabled ? toggleInventoryItemEquippedFromAuthority : null}

          onConsumeInventoryItem={inventoryConsumeAuthorityEnabled ? consumeInventoryItemFromAuthority : null}

          onActivateInventoryItemAbility={inventoryAbilityAuthorityEnabled ? activateInventoryItemAbilityFromAuthority : null}
          durabilityEnabled={campaignDurabilityEnabled}
          manaEnabled={campaignManaEnabled}

        />

      </div>

      )}



      {/* 2026-05-11 — Session 7: Adventure action row collapses to column
          on mobile. Each button drops to natural content height (~56 px
          incl. 16 px padding + 1.1rem text + minHeight floor); intent
          is a generous tap target since these are session-frequency
          navigation, not turn-frequency combat actions. 56 px sits
          above §3 Rule 3's 48-px combat-critical floor by design —
          vertical rhythm in column mode reads better than the spec
          minimum, and these buttons aren't competing for thumb
          attention with adjacent affordances. */}
      {(!isMobileViewport || mobileTab === 'adventure') && (

      <div style={{ margin: '30px auto', display: 'flex', gap: '12px', flexWrap: 'wrap', flexDirection: isMobileViewport ? 'column' : 'row' }}>

        {travelLogEnabled && (

        <button style={{ ...S.btn, ...S.btnSuccess, flex: isMobileViewport ? '0 0 auto' : '1 1 180px', padding: isMobileViewport ? '16px' : '20px', fontSize: '1.1rem', minHeight: isMobileViewport ? '56px' : undefined }} onClick={() => setView('travel')}>📜 Travel Logs</button>

        )}

        <button style={{ ...S.btn, flex: isMobileViewport ? '0 0 auto' : '1 1 180px', padding: isMobileViewport ? '16px' : '20px', fontSize: '1.1rem', minHeight: isMobileViewport ? '56px' : undefined, background: 'linear-gradient(180deg, #8b6914 0%, #5c4a1f 100%)', borderColor: '#ffd700' }} onClick={() => setView('journal')}>📚 Journal</button>

        {combatEnabled && (

        <button style={{ ...S.btn, flex: isMobileViewport ? '0 0 auto' : '1 1 180px', padding: isMobileViewport ? '16px' : '20px', fontSize: '1.1rem', minHeight: isMobileViewport ? '56px' : undefined, background: displayedCombat?.active ? 'linear-gradient(180deg, #c92727 0%, #8b1a1a 100%)' : 'linear-gradient(180deg, #6b2727 0%, #4a1a1a 100%)', borderColor: displayedCombat?.active ? '#ff4444' : '#c92727' }} onClick={() => setView('combat')}>

          ⚔️ Combat {displayedCombat?.active && <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>(Round {displayedCombat.round})</span>}

        </button>

        )}

        {shopsEnabled && ((displayedShopData?.shops || []).some(s => s.isOpen) || isDM) && (

          <button style={{ ...S.btn, flex: isMobileViewport ? '0 0 auto' : '1 1 180px', padding: isMobileViewport ? '16px' : '20px', fontSize: '1.1rem', minHeight: isMobileViewport ? '56px' : undefined, background: 'linear-gradient(180deg, #6b5a14 0%, #4a3a1f 100%)', borderColor: '#d4aa40' }} onClick={() => setView('shop')}>

            🏪 Shop {(displayedShopData?.offers || []).filter(o => o.status === 'pending').length > 0 && isDM && <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>({(displayedShopData?.offers || []).filter(o => o.status === 'pending').length} offers)</span>}

          </button>

        )}

        {craftingEnabled && (craftingOpen || isDM) && (

          <button style={{ ...S.btn, flex: isMobileViewport ? '0 0 auto' : '1 1 180px', padding: isMobileViewport ? '16px' : '20px', fontSize: '1.1rem', minHeight: isMobileViewport ? '56px' : undefined, background: 'linear-gradient(180deg, #4a1f6b 0%, #2e1245 100%)', borderColor: '#9b59b6' }} onClick={() => setView('crafting')}>

            ⚒️ Crafting

          </button>

        )}

        {lootEnabled && (isDM || displayedLootData?.activeEncounter) && (

          <button style={{ ...S.btn, flex: isMobileViewport ? '0 0 auto' : '1 1 180px', padding: isMobileViewport ? '16px' : '20px', fontSize: '1.1rem', minHeight: isMobileViewport ? '56px' : undefined, background: displayedLootData?.activeEncounter ? 'linear-gradient(180deg, #6b5a14 0%, #4a3a1f 100%)' : 'linear-gradient(180deg, #3a2a14 0%, #2a1a0f 100%)', borderColor: displayedLootData?.activeEncounter ? '#ffd700' : '#8b6914' }} onClick={() => setView('loot')}>

            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><TreasureChestIcon size={20} color='#ffd700' /> Loot</span> {displayedLootData?.activeEncounter && <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>(Active)</span>}

          </button>

        )}

      </div>

      )}

      {showSettings && <Settings char={char} isDM={isDM} diets={diets} globalSettings={globalSettings} systems={systemsEnabled ? systems : {}} campaignHomebrew={runtimeCampaignHomebrew} travelLogEnabled={travelLogEnabled} spellLibrary={spellLibrary || []} onSaveSpellLibrary={saveSpellLibrary} onClose={() => setShowSettings(false)} onUpdate={(u, auditHint) => updateChar(charId, u, auditHint)} onDelete={() => deleteChar(charId)} onResetTravelLog={travelLogEnabled ? () => resetTravelLog(charId) : null} onPdfUpdate={() => setShowDdbUpdate(true)} journalSettings={journalSettings} onUpdateJournalSettings={saveJournalSettings} />}

      </div>{/* end maxWidth wrapper */}

      

      

      <CongratsModal />

      {/* Character Sheet Update Modal */}

      {showDdbUpdate && (
        <CharacterImportReviewModal
          mode="update"
          existingChar={char}
          characterId={charId}
          availableSources={['shenyi', 'vental']}
          uploadAppPayload={characterImportUploadClient}
          onClose={() => setShowDdbUpdate(false)}
          onApplyUpdate={handleApplyCharacterImportUpdate}
        />
      )}

      {/* Messaging System */}

      <MessagePanel 

        isOpen={showMessagePanel} 

        onClose={() => setShowMessagePanel(false)} 

        displayMessages={messageReadView}

        messages={null}

        charId={charId} 

        charName={char.name}

        onSaveMessages={saveMessages}

        onDismissStatusMessage={handleDismissStatusMessage}

        onMarkDmChatRead={handleMarkDmChatRead}

        onSendDmChatMessage={handleSendDmChatMessage}

        onDeleteDmChatMessage={handleDeleteDmChatMessage}

        isDM={isDM}

      />

      {/* Urgent Message Overlay */}

      {!isDM && <UrgentMessageOverlay messages={messageReadView} charId={charId} onSaveMessages={saveMessages} onMarkStatusSeen={handleMarkStatusMessageSeen} />}

      

      {/* Whisper Display (for non-DM players) */}

      {!isDM && (

        <WhisperDisplay 

          whispers={messageReadView.statusUpdates || []}

          charId={charId}

          onDismiss={handleDismissStatusMessage}

        />

      )}

      

      {/* Floating Combat Indicator */}

      {combatEnabled && <FloatingCombatIndicator combat={displayedCombat} onClick={() => setView('combat')} />}

      {activeDmSystemRulePendingSave && activeDmSystemRuleSaveChar && (
        <RequireSaveModal
          key={`${activeDmSystemRuleSaveEntry?.id || 'travel-system-save'}:${dmSystemRuleSaveQueue.length}`}
          pendingSave={activeDmSystemRulePendingSave}
          char={activeDmSystemRuleSaveChar}
          saveContext={activeDmSystemRuleSaveContext}
          saveAdvantage={activeDmSystemRuleSaveAdvantage}
          onResolve={handleDmSystemRuleSaveResolve}
          onSkip={handleDmSystemRuleSaveSkip}
        />
      )}

      {floatingChrome}

      {/* 2026-04-29 — A.1.a chrome: persistent bottom nav. The active tab

          state is wired at the App root; A.1.a is "chrome only — body

          wiring lands in A.2." Today the tab change just updates state +

          (for stats) closes any open overflow; A.2 will swap the body

          content per active tab. */}

      {isMobileViewport && (

        <MobileBottomNav

          tabSet="player"

          activeTab={mobileTab}

          onTabChange={setMobileTab}

          badges={{ adventure: unreadCount }}

        />

      )}

      {/* 2026-04-29 — A.1.b: contextual FAB. On the Stats tab, the most-

          frequent player action (Quick Add Journal) is permanently thumb-

          reachable via the FAB. Other tabs can swap to other actions in

          A.2; for now Stats-only. */}

      {isMobileViewport && mobileTab === 'stats' && (

        <MobileFAB

          icon="✏️"

          ariaLabel="Quick add journal entry"

          onClick={() => setShowQuickAddJournal(true)}

        />

      )}

    </div>;

  }



  // SHOP PAGE

  if (view === 'shop') {

    if (!shopsEnabled) return null;

    const shopBack = () => setView(isDM ? 'dm' : (charId ? 'home' : 'select'));

    // 2026-05-11 — Session 9 mobile-game-UX pattern: persistent currency
    // in the sticky header title slot. "How much gold do I have?" is the
    // most-asked question in a shop flow — every Genshin/Honkai/Marvel-
    // Snap/Hearthstone shop puts the wallet top-right, always visible.
    //
    // Session 11 polish: wrap the wallet base value in <TweenedCurrency>
    // so purchases animate the value down to its new amount, reformatting
    // per frame. Static string used as fallback (no wallet / no tiers).
    //
    // F1 fix: `formatPriceFromBase` was a ShopPage-local closure not
    // visible at this scope; the S11 wiring referenced it from App
    // scope and crashed with ReferenceError → blank shop screen on
    // every player session. Switched to module-level
    // formatPriceFromBaseUnits which takes tiers as a parameter.
    const shopHeaderTiers = globalSettings?.currencyTiers;
    const shopHeaderWalletBase = (() => {
      if (!char || !char.wallet) return null;
      try {
        return walletToBase(char.wallet, shopHeaderTiers);
      } catch {
        return null;
      }
    })();
    const shopHeaderCurrency = shopHeaderWalletBase != null
      ? <TweenedCurrency
          baseValue={shopHeaderWalletBase}
          formatter={(base) => formatPriceFromBaseUnits(base, shopHeaderTiers)}
        />
      : null;

    return (

      <>

        {isMobileViewport && (

          <MobileStickyHeader char={char} backTo={shopBack} title="🏪 Shop" titleSuffix={shopHeaderCurrency} />

        )}

        <div style={mobileBodyStyle}>

          <ShopPage

            char={char}

            isDM={isDM}

            shopData={displayedShopData}

            onSaveShopData={saveDisplayedShopData}

            onPurchaseShopListing={shopRuntimePurchaseAuthorityEnabled ? purchaseDisplayedShopListing : null}

            onCheckoutShopCart={shopRuntimePurchaseAuthorityEnabled ? checkoutDisplayedShopCart : null}

            onSellInventoryItemToShopForDm={shopRuntimeSellBackAuthorityEnabled ? sellDisplayedInventoryItemToShopForDm : null}

            onSubmitTradeOffer={shopRuntimeOfferAuthorityEnabled ? submitDisplayedTradeOffer : null}

            onCancelTradeOffer={shopRuntimeOfferAuthorityEnabled ? cancelDisplayedTradeOffer : null}

            onDenyTradeOfferForDm={shopRuntimeOfferDenyAuthorityEnabled ? denyDisplayedTradeOfferForDm : null}

            onSettleTradeOfferForDm={shopRuntimeOfferSettleAuthorityEnabled ? settleDisplayedTradeOfferForDm : null}

            onPromoteTradeReceiptLineToShopListingForDm={shopRuntimePromotionAuthorityEnabled ? promoteDisplayedTradeReceiptLineToShopListingForDm : null}

            onAdjustShopWalletForDm={shopRuntimeWalletAuthorityEnabled ? adjustDisplayedShopWalletForDm : null}

            onUpdateChar={(u, auditHint) => updateChar(charId, u, auditHint)}

            globalSettings={globalSettings}

            allChars={chars}

            currentCharId={charId}

            onBack={shopBack}

            appTheme={currentTheme}

          />

        </div>

        {isMobileViewport && (

          <MobileBottomNav

            tabSet={isDM ? 'dm' : 'player'}

            activeTab={isDM ? 'tools' : 'adventure'}

            onTabChange={(tab) => { setMobileTab(tab); setView(charId ? 'home' : 'select'); }}

          />

        )}

        {floatingChrome}

      </>

    );

  }



  if (view === 'loot') {

    if (!lootEnabled) return null;

    const lootBack = () => setView(isDM ? 'dm' : (charId ? 'home' : 'select'));

    return (

      <>

        {isMobileViewport && (

          <MobileStickyHeader char={char} backTo={lootBack} title="⚜️ Loot" />

        )}

        <div style={mobileBodyStyle}>

          <LootPage

            chars={chars}

            currentCharId={charId}

            isDM={isDM}

            lootData={displayedLootData}

            onSaveLootData={saveLootData}

            onUpdateChar={(cid, u, auditHint) => updateChar(cid, u, auditHint)}

            onBack={lootBack}

            appTheme={currentTheme}

            globalSettings={globalSettings}

            shopTemplates={shopData?.itemTemplates || DEFAULT_ITEM_TEMPLATES}

            isServerOwnedLootRuntime={lootRuntimeAuthorityEnabled}

            onStartLootEncounter={lootRuntimeDmAuthorityEnabled ? publishDisplayedLootEncounter : null}

            onClaimLootDrop={lootRuntimePlayerAuthorityEnabled ? claimDisplayedLootDrop : null}

            onWithdrawLootClaim={lootRuntimePlayerAuthorityEnabled ? withdrawDisplayedLootClaim : null}

            onSetLootDropVisibility={lootRuntimeDmAuthorityEnabled ? setDisplayedLootDropVisibility : null}

            onRevealAllLootDrops={lootRuntimeDmAuthorityEnabled ? revealAllDisplayedLootDrops : null}

            onFinishLootEncounter={lootRuntimeDmAuthorityEnabled ? finishDisplayedLootEncounter : null}

            onDistributeLootXp={lootRuntimeDmAuthorityEnabled ? distributeDisplayedLootXp : null}

            onAwardLootDrop={lootRuntimeDmAuthorityEnabled ? awardDisplayedLootDrop : null}

            onAwardCurrencyLoot={lootRuntimeDmAuthorityEnabled ? awardDisplayedCurrencyLoot : null}

          />

        </div>

        {isMobileViewport && (

          <MobileBottomNav

            tabSet={isDM ? 'dm' : 'player'}

            activeTab={isDM ? 'tools' : 'adventure'}

            onTabChange={(tab) => { setMobileTab(tab); setView(charId ? 'home' : 'select'); }}

          />

        )}

        {floatingChrome}

      </>

    );

  }



  if (view === 'npcs') {

    return (

      <>

        {isMobileViewport && (

          <MobileStickyHeader char={char} backTo={() => setView(isDM ? 'dm' : 'home')} title="📖 NPCs / Library" />

        )}

        <div style={mobileBodyStyle}>

          <LibraryPage npcLibrary={npcLibrary} saveNpcLibrary={saveNpcLibrary} dmJournal={dmJournal} saveDmJournal={saveDmJournal} journal={journal} saveJournal={saveJournal} upsertJournalEntry={upsertJournalEntry} upsertDmJournalEntry={upsertDmJournalEntry} deleteDmJournalEntry={deleteDmJournalEntry} messages={messages} saveMessages={saveMessages} onSendStatusMessage={handleSendStatusMessage} S={S} setView={setView} chars={chars} initialEntry={libraryInitEntry} onClearInitEntry={() => setLibraryInitEntry(null)} defaultTab={lastLibraryTab} onTabChange={setLastLibraryTab} systems={systems} saveChars={saveChars} groupTravelLog={groupTravelLog} saveGroupTravelLog={saveGroupTravelLogForTravelFeature} onNotify={setDmNotification} onQueueSystemRulePendingSaves={(entries) => setDmSystemRuleSaveQueue(prev => [...(prev || []), ...(entries || [])])} />

        </div>

        {isMobileViewport && (

          <MobileBottomNav

            tabSet={isDM ? 'dm' : 'player'}

            activeTab={isDM ? 'world' : 'adventure'}

            onTabChange={(tab) => { setMobileTab(tab); setView(charId ? 'home' : 'select'); }}

          />

        )}

        {floatingChrome}

      </>

    );

  }

  if (view === 'crafting') {

    if (!craftingEnabled) return null;

    // 2026-04-22 crafting ↔ inventory bridge wiring. Player mode only — DM

    // sandbox path (isDM=true, no char) keeps existing local-only behavior.

    const craftingForChar = (!isDM && char) ? (char.craftingTable || null) : null;

    const handleUpdateCraftingTable = (!isDM && char)

      ? (next) => updateChar(charId, { craftingTable: next })

      : null;

    // 2026-04-22 fix: accept optional `updatedTableInv` so handleCraft can

    // pass its post-consumption inv; we save inventory + craftingTable in a

    // single updateChar atomically. This eliminates a race with the

    // debounced-save effect in CraftingPage that was overwriting

    // char.inventory with a stale snapshot (re-using a closure captured

    // before the inventory add committed).

    const handleAddToInventory = (!isDM && char)

      ? async (recipeRes, qty, updatedTableInv) => {
          return await addCraftResultToInventoryBridge({
            charId,
            char,
            recipeRes,
            qty,
            updatedTableInv,
            tableRowToChar: movementAuthorityAuthBlocked ? rejectUnlinkedMovementAuthority : (tableRowToCharAuthorityEnabled ? tableRowToCharFromAuthority : null),
            craftOutputToChar: movementAuthorityAuthBlocked ? rejectUnlinkedMovementAuthority : (craftOutputToCharAuthorityEnabled ? craftOutputToCharFromAuthority : null),
            syncAuthorityChar: syncAuthorityCharSnapshotToRef,
            updateChar,
          });
        }

      : null;

    // 2026-04-23: party-inventory transfer from the Crafting Table tab.

    // Callback moves a table entry (with its rich data or fallback shape)

    // into the shared partyInventory pool. The shared Session 2 helper now

    // owns stack-safe vs stateful merge behavior for crafting-side deposits.

    const handleAddToParty = (!isDM && char && (
      movementAuthorityAuthBlocked
      || partyInventoryWholeBlobFallback
      || tableRowToPartyAuthorityEnabled
      || craftOutputToPartyAuthorityEnabled
    ))

      ? async (recipeRes, qty, updatedTableInv) => {

          return await addCraftResultToPartyBridge({
            charId,
            char,
            recipeRes,
            qty,
            updatedTableInv,
            partyInventory,
            tableRowToParty: movementAuthorityAuthBlocked ? rejectUnlinkedMovementAuthority : (tableRowToPartyAuthorityEnabled ? tableRowToPartyFromAuthority : null),
            craftOutputToParty: movementAuthorityAuthBlocked ? rejectUnlinkedMovementAuthority : (craftOutputToPartyAuthorityEnabled ? craftOutputToPartyFromAuthority : null),
            syncAuthorityChar: syncAuthorityCharSnapshotToRef,
            savePartyInventory: partyInventoryWholeBlobFallback,
            updateChar,
          });

        }

      : null;

    return (

      <>

        <CraftingPage

          isDM={isDM}

          isOpen={craftingOpen}

          onBack={() => setView(isDM ? 'dm' : (charId ? 'home' : 'select'))}

          craftingTable={craftingForChar}

          onUpdateCraftingTable={handleUpdateCraftingTable}

          onAddToInventory={handleAddToInventory}

          onAddToParty={handleAddToParty}

          // 2026-04-27 (later): char + onUpdateChar for the Profession System

          // (Phase 1). Player-mode mounts pass the char so XP grants and

          // playerProfs writes go through updateChar. DM-mode mounts pass

          // null/no-op so the DM sandbox keeps its existing local-only state.

          char={!isDM && char ? char : null}

          onUpdateChar={!isDM && char ? ((u, auditHint) => updateChar(charId, u, auditHint)) : null}

          // 2026-05-09: party dict for the "Party Skills" scope. Player
          // mode only — DM doesn't need it for the sandbox.
          chars={!isDM && char ? chars : null}

          profSettings={(globalSettings || {}).profSettings || DEFAULT_PROF_SETTINGS}

          professionsFeatureEnabled={professionsFeatureEnabled}

          // 2026-04-29: DM-only profession-settings setter. Persists to

          // globalSettings.profSettings; the toggle UI in CraftingPage's DM

          // view writes here. Null in player mode.

          onUpdateProfSettings={isDM ? ((next) => saveGlobalSettings({ ...(globalSettings || {}), profSettings: next })) : null}

        />

        {floatingChrome}

      </>

    );

  }



  // TRAVEL LOGS SCREEN

  if (view === 'travel' && char) {

    if (!travelLogEnabled) return null;

    const set = char.settings || DEFAULT_SETTINGS;

    // New hunger level calculation

    const hungerLevel = getHungerLevel(char.mealsToday || 0);

    const hungerInfo = HUNGER_LEVELS[hungerLevel];

    const weeklyScore = (char.weeklyHungerScore || 0) + (char.weeklyStarvationBonus || 0);

    

    const inv = calcFoodInventory(food.foodLog); const invWithNotes = calcFoodWithNotes(food.foodLog);

    const meals = {}; (char.mealLog || []).forEach(m => { if (!meals[m.day]) meals[m.day] = []; meals[m.day].push(m); });

    for (let d = Math.max(1, char.currentDay - 6); d <= char.currentDay; d++) if (!meals[d]) meals[d] = [];

    const dietHasFood = campaignDietsEnabled && char.activeDiet && hasDietFood(inv, char.activeDiet, diets);

    const defaultMealLabel = campaignDietsEnabled && char.activeDiet ? (dietHasFood ? `Diet (${diets[char.activeDiet]?.requiredMealTypes.join('/')})` : 'Any (no diet food)') : 'Any (Random)';

    

    // Count unread messages for this character (exclude urgent that show as overlay)

    const unreadCount = countUnreadMessagesForChar(messageReadView, charId, { excludeUrgent: true });

    

    return <div style={{ ...S.container, paddingTop: isMobileViewport ? '60px' : undefined, paddingBottom: isMobileViewport ? '80px' : undefined }}>

      {isMobileViewport && (

        <MobileStickyHeader

          char={char}

          unreadCount={unreadCount}

          backTo={() => setView('home')}

          title="📜 Travel Logs"

          onBellClick={() => setShowMessagePanel(true)}

        />

      )}

      {!isMobileViewport && (

      <div style={{ ...S.flex, justifyContent: 'space-between' }}>

        <div style={S.flex}>

          <Tooltip text="Back to character home"><button style={S.btn} onClick={() => setView('home')}>← Back</button></Tooltip>

          <Tooltip text="Character settings and options"><button style={S.btn} onClick={() => setShowSettings(true)}>⚙️</button></Tooltip>

          <Tooltip text="Messages"><button style={{ ...S.btn, position: 'relative' }} onClick={() => setShowMessagePanel(true)}>

            📬 {unreadCount > 0 && <span style={{ position: 'absolute', top: '-5px', right: '-5px', background: '#c9a227', color: 'white', borderRadius: '50%', width: '18px', height: '18px', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{unreadCount}</span>}

          </button></Tooltip>

          {isDM && <Tooltip text="Go to DM Dashboard"><button style={{ ...S.btn, ...S.btnEdit }} onClick={() => setView('dm')}>📊</button></Tooltip>}

        </div>

        {isDM && <span style={{ ...S.badge, ...S.badgeNeutral }}>👑 DM</span>}

      </div>

      )}

      {!isMobileViewport && <h1 style={{ ...S.title, marginTop: '16px' }}>📜 Travel Logs</h1>}

      <div style={{ maxWidth: '900px', margin: '0 auto' }}>

        <div style={S.grid}>

          <HoverCard>

            <div style={S.dayNum}>Day {char.currentDay}</div>

            <div style={{ textAlign: 'center', marginBottom: '8px', fontSize: '0.85rem', opacity: 0.7 }}>Week {char.currentWeekNumber || 1} • Day {((char.currentDay - 1) % 7) + 1}/7</div>

            

            {campaignHungerEnabled && <>
            {/* Meals display - now out of 3 */}

            <div style={{ fontSize: '1.5rem', textAlign: 'center', marginBottom: '8px' }}>

              Meals: <span style={{ color: char.mealsToday >= 3 ? '#27c95a' : char.mealsToday >= 2 ? '#7db5ff' : char.mealsToday >= 1 ? '#c9a227' : '#c92727' }}>{char.mealsToday}/3</span>

            </div>

            

            {/* Hunger Level indicator */}

            <div style={{ 

              textAlign: 'center', 

              padding: '8px', 

              marginBottom: '16px', 

              borderRadius: '4px',

              background: hungerLevel === 'full' ? 'rgba(39,201,90,0.2)' : 

                         hungerLevel === 'noHunger' ? 'rgba(74,144,217,0.2)' : 

                         hungerLevel === 'lightHunger' ? 'rgba(201,162,39,0.2)' : 'rgba(201,39,39,0.2)',

              border: `1px solid ${hungerLevel === 'full' ? '#27c95a' : 

                                   hungerLevel === 'noHunger' ? '#4a90d9' : 

                                   hungerLevel === 'lightHunger' ? '#c9a227' : '#c92727'}`

            }}>

              <div style={{ fontWeight: 'bold', color: hungerLevel === 'full' ? '#27c95a' : 

                                                        hungerLevel === 'noHunger' ? '#7db5ff' : 

                                                        hungerLevel === 'lightHunger' ? '#c9a227' : '#c92727' }}>

                {hungerInfo.name}

              </div>

              <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>

                {hungerInfo.points >= 0 ? '+' : ''}{hungerInfo.points} points today

              </div>

            </div>

            

            <label style={S.label}>Meal Type</label>

            <select style={S.select} value={mealType} onChange={e => setMealType(e.target.value)}>

              <option value="auto">{defaultMealLabel}</option>

              <option value="any">Any (Random)</option>

              {foodTypes.map(t => <option key={t} value={t} disabled={!inv[t]}>{t} ({inv[t] || 0})</option>)}

            </select>

            <button style={{ ...S.btn, ...S.btnSuccess, width: '100%', ...(char.mealsToday >= 3 || (food.totalUnits || 0) < 1 ? S.btnDisabled : {}) }} onClick={() => logMeal(charId, mealType)} disabled={char.mealsToday >= 3 || (food.totalUnits || 0) < 1}>🍽️ Log Meal</button>

            <div style={{ marginTop: '16px', textAlign: 'center' }}><label style={{ cursor: 'pointer' }}><input type="checkbox" checked={char.immuneToday || false} onChange={() => updateChar(charId, { immuneToday: !char.immuneToday })} /> Immune today</label></div>
            </>}

            <div style={{ marginTop: '20px', textAlign: 'center', position: 'relative' }}><button style={{ ...S.btn, ...S.btnSuccess }} onClick={() => advanceDay(charId)}>⏭️ End Day</button>{char.actionHistory?.length > 0 && <button style={{ ...S.btn, ...S.btnWarn, ...S.btnXs, position: 'absolute', right: 0, top: 0 }} onClick={() => undoLastAction(charId)}>↩️</button>}</div>

            {isDM && <div style={{ ...S.subCard, marginTop: '16px' }}><h4 style={{ color: '#c9a227', marginBottom: '8px', fontSize: '0.9rem' }}>🎮 DM Controls</h4><TravelDayWeekControls currentDay={char.currentDay} currentWeekNumber={char.currentWeekNumber || 1} onCommitDay={(value) => updateChar(charId, { currentDay: value })} onCommitWeek={(value) => updateChar(charId, { currentWeekNumber: value })} />

              <button style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, marginTop: '8px', width: '100%' }} onClick={() => {

                const set = char.settings || DEFAULT_SETTINGS;

                updateChar(charId, { 

                  currentDay: 1, 

                  daysWithoutFood: 0,

                  daysToStarvation: set.starvationThreshold || 7,

                  starving: false,

                  mealsToday: 0,

                  mealLog: [],

                  immuneToday: false,

                  actionHistory: [],

                  wasWellFed: false,

                  weeklyHungerScore: 0,

                  currentWeekNumber: 1,

                  consecutiveNegativeWeeks: 0,

                  diseaseCheckConPenalty: 0,

                  dietWeekProgress: 0,

                  dietDayStreak: 0,

                  ...clearFoodEffectState(char, set, {

                    removeWellFed: true,

                    removeHungry: true,

                    removeStarvation: true,

                    resetStarving: true,

                    resetDaysToStarvation: true,

                  }),

                  statusHistory: [{ timestamp: new Date().toISOString(), day: 1, event: 'reset', description: 'Day 1: Reset to Day 1 by DM' }, ...(char.statusHistory || [])]

                });

                saveGroupTravelLog([{ day: 1, character: char.name, action: 'reset to Day 1', timestamp: new Date().toISOString() }, ...(groupTravelLog || []).slice(0, 199)]);

              }}>🔄 Reset to Day 1</button>

            </div>}

          </HoverCard>

          {(campaignHungerEnabled || campaignDietsEnabled || campaignDiseasesEnabled) && <HoverCard>

            <h2 style={S.subtitle}>📊 Weekly Status</h2>

            

            {/* Weekly hunger score display */}

            <div style={{ 

              padding: '16px', 

              background: 'rgba(0,0,0,0.3)', 

              borderRadius: '8px', 

              marginBottom: '12px',

              textAlign: 'center'

            }}>

              <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '4px' }}>Weekly Hunger Score</div>

              <div style={{ 

                fontSize: '2rem', 

                fontWeight: 'bold', 

                color: weeklyScore >= 0 ? '#27c95a' : weeklyScore >= -2 ? '#c9a227' : '#c92727' 

              }}>

                {weeklyScore >= 0 ? '+' : ''}{weeklyScore.toFixed(1)}

              </div>

              {char.weeklyStarvationBonus > 0 && (

                <div style={{ fontSize: '0.75rem', color: '#4a90d9', marginTop: '4px' }}>

                  (+{char.weeklyStarvationBonus} from Mixed diet)

                </div>

              )}

              {weeklyScore < 0 && (

                <div style={{ fontSize: '0.75rem', color: '#c92727', marginTop: '4px' }}>

                  ⚠️ {weeklyScore >= -2 ? 'Disease vulnerability' : weeklyScore >= -6 ? 'Disease check at week end' : 'Severe - instant disease'}

                </div>

              )}

            </div>

            

            {char.consecutiveNegativeWeeks > 0 && (

              <div style={{ padding: '8px', background: 'rgba(201,39,39,0.2)', borderRadius: '4px', marginBottom: '12px', border: '1px solid #c92727', fontSize: '0.85rem' }}>

                ⚠️ <strong>{char.consecutiveNegativeWeeks}</strong> consecutive negative week{char.consecutiveNegativeWeeks > 1 ? 's' : ''}

                {char.consecutiveNegativeWeeks >= 3 && <span style={{ color: '#ff7d7d' }}> - STAT LOSS!</span>}

              </div>

            )}

            

            {campaignDietsEnabled && char.activeDiet && diets[char.activeDiet] && (

              <div style={{ padding: '8px', background: 'rgba(74,144,217,0.2)', borderRadius: '4px', marginBottom: '12px', border: '1px solid #4a90d9', fontSize: '0.85rem' }}>

                🥗 <strong>{char.activeDiet}</strong>: Day {char.dietDayStreak || 0}/7 • Week {char.dietWeekProgress || 0}/{diets[char.activeDiet].durationWeeks}

              </div>

            )}

            

            {(() => {

              const legacySheetStatusView = buildLegacyStatusEffectView({ char });

              return (

                <>

                  {legacySheetStatusView.visibleBuffs.length > 0 && <div style={{ marginBottom: '12px' }}>

                    <div style={{ fontSize: '0.8rem', color: '#27c95a', marginBottom: '4px' }}>Buffs</div>

                    {legacySheetStatusView.visibleBuffs.map((effect) => <BuffBadge key={effect.effectId} text={effect.text} source={effect.source} sourceType={effect.sourceType} sourceMode={effect.sourceMode} sourceLabel={effect.sourceLabel} playerTooltip={effect.playerTooltip} colorId={effect.colorId} isPermanent={effect.isPermanent} linkedItemName={effect.linkedItemName} noteVisible={effect.noteVisible} />)}

                  </div>}

                  

                  {legacySheetStatusView.visibleDebuffs.length > 0 && <div style={{ marginBottom: '12px' }}>

                    <div style={{ fontSize: '0.8rem', color: '#c92727', marginBottom: '4px' }}>Debuffs</div>

                    {legacySheetStatusView.visibleDebuffs.map((effect) => <BuffBadge key={effect.effectId} text={effect.text} source={effect.source} sourceType={effect.sourceType} sourceMode={effect.sourceMode} sourceLabel={effect.sourceLabel} playerTooltip={effect.playerTooltip} colorId={effect.colorId} isPermanent={effect.isPermanent} linkedItemName={effect.linkedItemName} isDebuff noteVisible={effect.noteVisible} />)}

                  </div>}

                </>

              );

            })()}

            {/* Always show all buffs */}

            {false && ((char.permanentBuffs || []).length > 0 || (char.buffList || []).length > 0) && <div style={{ marginBottom: '12px' }}>

              <div style={{ fontSize: '0.8rem', color: '#27c95a', marginBottom: '4px' }}>✨ Buffs</div>

              {(char.permanentBuffs || []).map((b, i) => <BuffBadge key={'pb'+i} text={b} source={char.buffSources?.[b]} note={char.buffNotes?.[b]} isPermanent />)}

              {(char.buffList || []).map((b, i) => <BuffBadge key={'b'+i} text={b} source={char.buffSources?.[b]} note={char.buffNotes?.[b]} />)}

            </div>}

            

            {/* Always show all debuffs */}

            {false && ((char.permanentDebuffs || []).length > 0 || (char.debuffList || []).length > 0) && <div style={{ marginBottom: '12px' }}>

              <div style={{ fontSize: '0.8rem', color: '#c92727', marginBottom: '4px' }}>⚠️ Debuffs</div>

              {(char.permanentDebuffs || []).map((d, i) => <BuffBadge key={'pd'+i} text={d} source={char.buffSources?.[d]} note={char.buffNotes?.[d]} isPermanent isDebuff />)}

              {(char.debuffList || []).map((d, i) => <BuffBadge key={'d'+i} text={d} source={char.buffSources?.[d]} note={char.buffNotes?.[d]} isDebuff />)}

            </div>}

            

            {/* Diseases */}

            {campaignDiseasesEnabled && (char.diseaseList || []).length > 0 && <div style={{ marginBottom: '12px' }}>

              <div style={{ fontSize: '0.8rem', color: '#d47dd4', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: 4 }}><BiohazardIcon size={14} color="#d47dd4" /> Conditions</div>

              {(char.diseaseList || []).map((d, i) => <DiseaseBadge key={i} diseaseData={d} disease={diseases[d.diseaseId]} isPlayer={!isDM} />)}

            </div>}

            

            {campaignHungerEnabled && set.showDaysToStarvation && <div style={{ marginBottom: '16px' }}><label style={S.label}>Days to Starvation</label><div style={{ fontSize: '2rem', fontWeight: 'bold', color: getStarvColor(char.daysToStarvation, set.starvationThreshold) }}>{char.daysToStarvation}/{set.starvationThreshold}</div></div>}

            {campaignHungerEnabled && set.showDaysWithoutFood && <div><label style={S.label}>Days Without Food</label><div style={{ fontSize: '2rem', fontWeight: 'bold', color: getStarvColor(set.starvationThreshold - char.daysWithoutFood, set.starvationThreshold) }}>{char.daysWithoutFood}</div></div>}

          </HoverCard>}

        </div>}

        

        {/* Spells */}

        <SpellPanel char={char} onUpdate={(u, auditHint) => updateChar(charId, u, auditHint)} isDM={isDM} spellLibrary={spellLibrary || []} onSaveLibrary={saveSpellLibrary} globalSettings={runtimeGlobalSettings} onSaveGlobalSettings={saveGlobalSettings} systems={runtimeSystems} manaEnabled={campaignManaEnabled} chars={chars} journal={journal} npcLibrary={npcLibrary} onSaveNpcLibrary={saveNpcLibrary} onNotify={setDmNotification} onDropConcentration={dropConcentrationForChar} groupTravelLog={groupTravelLog} onSaveGroupTravelLog={saveGroupTravelLogForTravelFeature} messages={messages} onSaveMessages={saveMessages} onSendStatusMessage={handleSendStatusMessage} />



        {/* Equipment & Durability — 2026-04-28: panel hidden per BUG-REPORTS line 170 (item

            enhancements can't be expressed here; design needs rework). Underlying dura

            mechanics remain. */}

        {/* <EquipmentDuraPanel char={char} onUpdate={(u, auditHint) => updateChar(charId, u, auditHint)} isDM={isDM} /> */}



        {/* Inventory */}

        <InventoryPanel
          char={char}
          onUpdate={(u, auditHint) => updateChar(charId, u, auditHint)}
          isDM={isDM}
          allChars={chars}
          onUpdateOtherChar={(otherCharId, u, auditHint) => updateChar(otherCharId, u, auditHint)}
          onTransferToOtherChar={inventoryCharTransferAuthorityEnabled ? transferInventoryItemBetweenCharsFromAuthority : null}
          onToggleInventoryItemEquipped={inventoryEquipToggleAuthorityEnabled ? toggleInventoryItemEquippedFromAuthority : null}
          onConsumeInventoryItem={inventoryConsumeAuthorityEnabled ? consumeInventoryItemFromAuthority : null}
          onAddInventoryItem={inventoryCrudAuthorityEnabled ? addInventoryItemFromAuthority : null}
          onUpdateInventoryItem={inventoryCrudAuthorityEnabled ? updateInventoryItemFromAuthority : null}
          onRemoveInventoryItem={inventoryCrudAuthorityEnabled ? removeInventoryItemFromAuthority : null}
          durabilityEnabled={campaignDurabilityEnabled}
          manaEnabled={campaignManaEnabled}
        />

        

        {(campaignHungerEnabled || campaignFoodSettingsEnabled) && <div style={{ ...S.grid, gridTemplateColumns: '1fr 1fr' }}>

          {campaignHungerEnabled && <HoverCard><h2 style={S.subtitle}>🍽️ Meal Log</h2><div style={{ maxHeight: '200px', overflowY: 'auto' }}>{Object.keys(meals).sort((a, b) => b - a).slice(0, 7).map(day => { const m = meals[day]; return <div key={day} style={{ padding: '8px', marginBottom: '6px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px', borderLeft: `3px solid ${m.length >= 2 ? '#27c95a' : m.length > 0 ? '#c9a227' : '#c92727'}` }}><strong>Day {day}:</strong> {m.length} meal{m.length !== 1 ? 's' : ''}{m.length > 0 && <span style={{ color: '#c9a227' }}> ({m.map(x => x.mealType).join(', ')})</span>}</div>; })}</div></HoverCard>}

          {campaignFoodSettingsEnabled && <HoverCard><h2 style={S.subtitle}>🍖 Food Pool</h2><div style={{ fontSize: '2.5rem', textAlign: 'center', color: '#ffd700' }}>{food.totalUnits || 0}</div>{Object.keys(invWithNotes).length > 0 && <div style={{ textAlign: 'center', marginBottom: '16px' }}>{Object.entries(invWithNotes).map(([t, data]) => <Tooltip key={t} text={getFoodTooltip(t, invWithNotes)}><span style={{ ...S.badge, ...S.badgeFood }}>{data.total}x {t}</span></Tooltip>)}</div>}<FoodMgr food={food} foodTypes={foodTypes} onAdd={addFood} onRemove={removeFood} /></HoverCard>}

        </div>}

        {campaignDietsEnabled && <HoverCard>

          <h2 style={S.subtitle}>🥗 Diet</h2>

          <div style={S.grid}>

            <div>

              <select style={S.select} value={char.activeDiet || ''} onChange={e => {

                const newDiet = e.target.value || null;

                const existingCompletions = char.dietCompletionCounts?.[e.target.value] || 0;

                const maxCompletions = diets[e.target.value]?.maxCompletions || 99;

                if (newDiet && existingCompletions >= maxCompletions) {

                  alert(`You've already completed ${e.target.value} the maximum number of times (${maxCompletions})!`);

                  return;

                }

                updateChar(charId, { activeDiet: newDiet, dietWeekProgress: 0, dietDayStreak: 0 });

              }}>

                <option value="">None</option>

                {Object.keys(diets).map(d => {

                  const completions = char.dietCompletionCounts?.[d] || 0;

                  const maxed = completions >= diets[d].maxCompletions;

                  return <option key={d} value={d} disabled={maxed}>{d} {maxed ? '(MAX)' : completions > 0 ? `(${completions}/${diets[d].maxCompletions})` : ''}</option>;

                })}

              </select>

              {char.activeDiet && diets[char.activeDiet] && (

                <div>

                  <div style={{ marginBottom: '8px' }}>

                    <strong>Week Progress:</strong> {char.dietWeekProgress || 0}/{diets[char.activeDiet].durationWeeks} weeks

                  </div>

                  <div style={{ height: '12px', background: 'rgba(20,15,10,0.9)', borderRadius: '6px', overflow: 'hidden', marginBottom: '8px' }}>

                    <div style={{ height: '100%', width: `${((char.dietWeekProgress || 0) / diets[char.activeDiet].durationWeeks) * 100}%`, background: 'linear-gradient(90deg, #4a90d9, #7db5ff)' }} />

                  </div>

                  <div style={{ marginBottom: '4px' }}>

                    <strong>This Week:</strong> {char.dietDayStreak || 0}/7 days met

                  </div>

                  <div style={{ height: '8px', background: 'rgba(20,15,10,0.9)', borderRadius: '4px', overflow: 'hidden', marginBottom: '8px' }}>

                    <div style={{ height: '100%', width: `${((char.dietDayStreak || 0) / 7) * 100}%`, background: 'linear-gradient(90deg, #27c95a, #7dff7d)' }} />

                  </div>

                  <p style={{ fontSize: '0.85rem', marginTop: '8px', padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px' }}>

                    <strong>Requirements:</strong> {diets[char.activeDiet].minMealsPerDay}+ meals/day

                    {diets[char.activeDiet].allMealsMustMatch && ` of ${diets[char.activeDiet].requiredMealTypes.join('/')}`}

                  </p>

                  <p style={{ fontSize: '0.85rem' }}><strong>Reward:</strong> {diets[char.activeDiet].completionBuffs.join(', ')}</p>

                </div>

              )}

            </div>

            <div>

              {(char.completedDiets || []).length > 0 && (

                <div>

                  <strong style={{ color: '#27c95a' }}>✅ Completed:</strong>

                  {char.completedDiets.map((cd, i) => (

                    <div key={i} style={{ marginTop: '8px', fontSize: '0.85rem' }}>

                      <span style={{ color: '#ffd700' }}>{cd.dietName}</span>

                      {cd.completionNumber && <span style={{ opacity: 0.7 }}> (#{cd.completionNumber})</span>}

                      : {cd.buffs?.join(', ')}

                    </div>

                  ))}

                </div>

              )}

              {char.skillCheckBonus > 0 && (

                <div style={{ marginTop: '12px', padding: '8px', background: 'rgba(74,144,217,0.2)', borderRadius: '4px', border: '1px solid #4a90d9' }}>

                  <strong style={{ color: '#7db5ff' }}>Blended Bonus:</strong> +{char.skillCheckBonus} all skill checks

                </div>

              )}

              {char.weeklyStarvationBonus > 0 && (

                <div style={{ marginTop: '8px', padding: '8px', background: 'rgba(39,201,90,0.2)', borderRadius: '4px', border: '1px solid #27c95a' }}>

                  <strong style={{ color: '#7dff7d' }}>Mixed Bonus:</strong> +{char.weeklyStarvationBonus} weekly hunger start

                </div>

              )}

            </div>

          </div>

        </HoverCard>}

        <HoverCard><h2 style={S.subtitle}>📋 History</h2><div style={{ maxHeight: '200px', overflowY: 'auto' }}>{(char.statusHistory || []).slice(0, 20).map((e, i) => <div key={i} style={{ padding: '8px', marginBottom: '6px', borderRadius: '4px', borderLeft: '3px solid', borderColor: e.event.includes('disease') || e.event.includes('starvation') || e.event.includes('diet-broken') ? '#c92727' : e.event.includes('well') || e.event.includes('diet-complete') ? '#27c95a' : e.event.includes('hungry') ? '#c9a227' : e.event.includes('immune') ? '#27c9c9' : '#4a90d9', background: 'rgba(0,0,0,0.2)' }}>{e.description}</div>)}</div></HoverCard>

      </div>

      {showSettings && <Settings char={char} isDM={isDM} diets={diets} globalSettings={globalSettings} systems={systemsEnabled ? systems : {}} campaignHomebrew={runtimeCampaignHomebrew} travelLogEnabled={travelLogEnabled} spellLibrary={spellLibrary || []} onSaveSpellLibrary={saveSpellLibrary} onClose={() => setShowSettings(false)} onUpdate={(u, auditHint) => updateChar(charId, u, auditHint)} onDelete={() => deleteChar(charId)} onResetTravelLog={travelLogEnabled ? () => resetTravelLog(charId) : null} onPdfUpdate={() => setShowDdbUpdate(true)} journalSettings={journalSettings} onUpdateJournalSettings={saveJournalSettings} />}

      <CongratsModal />

      {/* Character Sheet Update Modal */}

      {showDdbUpdate && (
        <CharacterImportReviewModal
          mode="update"
          existingChar={char}
          characterId={charId}
          availableSources={['shenyi', 'vental']}
          uploadAppPayload={characterImportUploadClient}
          onClose={() => setShowDdbUpdate(false)}
          onApplyUpdate={handleApplyCharacterImportUpdate}
        />
      )}

      {/* Messaging System */}

      {!isDM && <NotificationBadge messages={messageReadView} charId={charId} onClick={() => setShowMessagePanel(true)} />}

      <MessagePanel 

        isOpen={showMessagePanel} 

        onClose={() => setShowMessagePanel(false)} 

        displayMessages={messageReadView}

        messages={null}

        charId={charId} 

        charName={char.name}

        onSaveMessages={saveMessages}

        onDismissStatusMessage={handleDismissStatusMessage}

        onMarkDmChatRead={handleMarkDmChatRead}

        onSendDmChatMessage={handleSendDmChatMessage}

        onDeleteDmChatMessage={handleDeleteDmChatMessage}

        isDM={isDM}

      />

      {/* Urgent Message Overlay */}

      {!isDM && <UrgentMessageOverlay messages={messageReadView} charId={charId} onSaveMessages={saveMessages} onMarkStatusSeen={handleMarkStatusMessageSeen} />}

      

      {/* Floating Combat Indicator */}

      {combatEnabled && <FloatingCombatIndicator combat={displayedCombat} onClick={() => setView('combat')} />}

      {activeDmSystemRulePendingSave && activeDmSystemRuleSaveChar && (
        <RequireSaveModal
          key={`${activeDmSystemRuleSaveEntry?.id || 'travel-system-save'}:${dmSystemRuleSaveQueue.length}`}
          pendingSave={activeDmSystemRulePendingSave}
          char={activeDmSystemRuleSaveChar}
          saveContext={activeDmSystemRuleSaveContext}
          saveAdvantage={activeDmSystemRuleSaveAdvantage}
          onResolve={handleDmSystemRuleSaveResolve}
          onSkip={handleDmSystemRuleSaveSkip}
        />
      )}

      {floatingChrome}

      {isMobileViewport && (

        <MobileBottomNav

          tabSet={isDM ? 'dm' : 'player'}

          activeTab={isDM ? 'world' : 'adventure'}

          onTabChange={(tab) => { setMobileTab(tab); setView(charId ? 'home' : 'select'); }}

        />

      )}

    </div>;

  }



  // AUDIT TIMELINE + CHARACTER DIFF (DM-only)

  if (view === 'timeline' && isDM) {

    return (

      <>

        {isMobileViewport && (

          <MobileStickyHeader

            char={char}

            backTo={() => { setTimelineInitRange(null); setView('dm'); }}

            title="🕰️ Audit Timeline"

          />

        )}

        {/* 2026-05-11 — Session 10: pull-to-refresh on Timeline mirrors
            the Journal pattern (S8). Timeline reads from the `audit`
            prop which is Convex-synced via useStorage; subscriptions
            handle the actual refresh on reconnect, and the visual
            confirms the user's pull was acknowledged. */}
        <PullToRefreshContainer
          style={mobileBodyStyle}
          onRefresh={async () => {
            await new Promise((resolve) => setTimeout(resolve, 600));
          }}
        >

          <AuditTimelineView

            audit={audit}

            chars={chars}

            diseases={diseases}

            initialRange={timelineInitRange}

            onBack={() => {

              setTimelineInitRange(null);

              setView('dm');

            }}

            onRevert={revertAuditEvent}

          />

        </PullToRefreshContainer>

        {isMobileViewport && (

          <MobileBottomNav

            tabSet="dm"

            activeTab="session"

            onTabChange={(tab) => { setMobileTab(tab); setTimelineInitRange(null); setView('dm'); }}

          />

        )}

        {floatingChrome}

      </>

    );

  }



// DM DASHBOARD

  if (view === 'dm' && isDM) {

    const inv = calcFoodInventory(food.foodLog);

    const invWithNotes = calcFoodWithNotes(food.foodLog);

    const condensedLog = buildCondensedLog();

    const sortedDays = Object.keys(condensedLog).sort((a, b) => b - a).slice(0, 10);

    const dmDashboardSourceChars = isCampaignSandboxSession ? sandboxEligibleChars : chars;
    const dmDashboardCombat = displayedCombat || {};
    const dmDashboardCharList = Object.values(dmDashboardSourceChars || {}).filter(c => (
      isCampaignSandboxSession || !isCampaignTestCharacter(c)
    ));
    const dmDashboardChars = Object.fromEntries(dmDashboardCharList.map(c => [c.id, c]));
    const dmDashboardLiveCharList = dmDashboardCharList.filter(c => (
      isCampaignSandboxSession || !isCampaignTestCharacter(c)
    ));
    const dmDashboardMessageChars = Object.fromEntries(dmDashboardLiveCharList.map(c => [c.id, c]));

    

    // Section render functions

    const renderSection = (sectionId, sectionIndex) => {

      const section = DASHBOARD_SECTIONS[sectionId];

      if (!section) return null;

      if (sectionId === 'combat' && !combatEnabled) return null;

      if (sectionId === 'shop' && !shopsEnabled) return null;

      if (sectionId === 'crafting' && !craftingEnabled) return null;

      if (sectionId === 'loot' && !lootEnabled) return null;

      if (sectionId === 'systems-overview' && !systemsEnabled) return null;

      if (sectionId === 'travel-log' && !travelLogEnabled) return null;

      if (sectionId === 'food' && !campaignFoodSettingsEnabled) return null;



      const isCollapsed = collapsedSections[sectionId];

      const canMoveUp = sectionIndex > 0;

      const canMoveDown = sectionIndex < dashboardSectionOrder.length - 1;



      switch (sectionId) {

        case 'characters':

          return (

            <DashboardSection

              key={sectionId}

              id={sectionId}

              title="Characters"

              icon="👥"

              collapsed={isCollapsed}

              onToggleCollapse={toggleSectionCollapse}

              onMoveUp={() => moveDashboardSection(sectionId, 'up')}

              onMoveDown={() => moveDashboardSection(sectionId, 'down')}

              canMoveUp={canMoveUp}

              canMoveDown={canMoveDown}

            >

              {/* View Mode Selector and Batch Buttons */}

              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'center', flexWrap: 'wrap' }}>

                <div style={{ display: 'flex', gap: '4px', alignItems: 'center', marginRight: '16px' }}>

                  <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>View:</span>

                  <button 

                    style={{ 

                      ...S.btn, 

                      ...S.btnXs, 

                      ...(dmCardViewMode === 'compact' ? { background: 'rgba(201,162,39,0.3)', borderColor: '#c9a227' } : {}),

                    }} 

                    onClick={() => {

                      setDmCardViewMode('compact');

                      try { localStorage.setItem('bmg-dm-card-view-mode', 'compact'); } catch {}

                    }}

                  >

                    📋 Compact

                  </button>

                  <button 

                    style={{ 

                      ...S.btn, 

                      ...S.btnXs, 

                      ...(dmCardViewMode === 'expanded' ? { background: 'rgba(201,162,39,0.3)', borderColor: '#c9a227' } : {}),

                    }} 

                    onClick={() => {

                      setDmCardViewMode('expanded');

                      setSelectedDmCharId(null);

                      try { localStorage.setItem('bmg-dm-card-view-mode', 'expanded'); } catch {}

                    }}

                  >

                    📰 Expanded

                  </button>

                </div>

                <button 

                  style={{ ...S.btn, ...S.btnXs, ...S.btnSuccess }} 

                  onClick={() => {

                    setBatchBuffIsDebuff(false);

                    setBatchBuffColor('default-buff');

                    setBatchBuffTargetMode(CAMPAIGN_CHARACTER_TARGET_MODE_PLAYERS);

                    setBatchBuffTargets(new Set());

                    setShowBatchBuff(true);

                  }}

                >

                  ✨ Batch Buff

                </button>

                <button 

                  style={{ ...S.btn, ...S.btnXs, ...S.btnDanger }} 

                  onClick={() => {

                    setBatchBuffIsDebuff(true);

                    setBatchBuffColor('default-debuff');

                    setBatchBuffTargetMode(CAMPAIGN_CHARACTER_TARGET_MODE_PLAYERS);

                    setBatchBuffTargets(new Set());

                    setShowBatchBuff(true);

                  }}

                >

                  💀 Batch Debuff

                </button>

                <button 

                  style={{ ...S.btn, ...S.btnXs, background: 'rgba(155,89,182,0.2)', borderColor: '#9b59b6' }} 

                  onClick={() => setShowWhisperComposer(true)}

                >

                  🤫 Whisper

                </button>

                {dmCardViewMode === 'compact' && selectedDmCharId && (

                  <button 

                    style={{ ...S.btn, ...S.btnXs, marginLeft: 'auto' }} 

                    onClick={() => setSelectedDmCharId(null)}

                  >

                    ✕ Deselect

                  </button>

                )}

              </div>



              {/* Character Cards Grid */}

              {dmDashboardCharList.length === 0 ? (

                <p>No characters</p>

              ) : dmCardViewMode === 'compact' ? (

                <>

                  {/* Compact Mode - Grid layout: 2 columns by default */}

                  <div style={{ 

                    display: 'grid', 

                    gridTemplateColumns: 'repeat(2, 1fr)',

                    gap: '12px', 

                  }}>

                    {dmDashboardCharList.map(c => (

                      <CompactDMCard 

                        key={c.id} 

                        c={c} 

                        diseases={runtimeDiseases} 

                        systems={runtimeSystems} 

                        systemsEnabled={systemsEnabled}

                        manaEnabled={campaignManaEnabled}
                        hungerEnabled={campaignHungerEnabled}
                        diseasesEnabled={campaignDiseasesEnabled}
                        foodSettingsEnabled={campaignFoodSettingsEnabled}

                        diets={diets} 

                        foodTypes={foodTypes} 

                        inv={inv} 

                        chars={dmDashboardChars}

                        saveChars={saveChars} 

                        patchCharacterVitals={characterVitalsAuthorityEnabled ? patchCharacterVitalsFromAuthority : null}

                        food={food} 

                        saveFood={saveFood} 

                        onViewHome={() => { setCharId(c.id); setView('home'); }}

                        onViewTravel={travelLogEnabled ? () => { setCharId(c.id); setView('travel'); } : null}

                        onNotify={setDmNotification} 

                        messages={messages} 

                        onSaveMessages={saveMessages}

                        onSendStatusMessage={handleSendStatusMessage}

                        isSelected={selectedDmCharId === c.id}

                        onSelect={() => setSelectedDmCharId(c.id)}

                        glowingBuffs={glowingBuffs}

                        addBuffGlow={addBuffGlow}

                        addBuffGlowPatch={addBuffGlowPatch}

                        addSystemGlowPatch={addSystemGlowPatch}

                      />

                    ))}

                  </div>

                  

                  {/* Expanded Panel for Selected Character.
                      2026-05-11 — Session 11 polish: wrapped in CrossFade
                      so DM-character switches fade between cards rather
                      than hard-cut. Anchors the eye during rapid clicking
                      between party members. */}

                  <CrossFade swapKey={selectedDmCharId || '__none__'}>

                  {selectedDmCharId && dmDashboardChars[selectedDmCharId] && (

                    <ExpandedCharacterPanel

                      c={dmDashboardChars[selectedDmCharId]}

                      diseases={runtimeDiseases}

                      systems={runtimeSystems}

                      systemsEnabled={systemsEnabled}

                      manaEnabled={campaignManaEnabled}
                      hungerEnabled={campaignHungerEnabled}
                      diseasesEnabled={campaignDiseasesEnabled}
                      foodSettingsEnabled={campaignFoodSettingsEnabled}

                      diets={diets}

                      foodTypes={foodTypes}

                      locations={locations}

                      currentLocation={currentLocation}

                      inv={inv}

                      chars={dmDashboardChars}

                      saveChars={saveChars}

                      patchCharacterVitals={characterVitalsAuthorityEnabled ? patchCharacterVitalsFromAuthority : null}

                      patchCharacterSystemInstance={systemInstancePatchAuthorityEnabled ? patchCharacterSystemInstanceFromAuthority : null}

                      patchCharacterSystemState={systemStatePatchAuthorityEnabled ? patchCharacterSystemStateFromAuthority : null}

                      patchCharacterSystemStateAndVitals={systemStateAndVitalsPatchAuthorityEnabled ? patchCharacterSystemStateAndVitalsFromAuthority : null}

                      patchCharacterSystemStateAndResources={systemStateAndResourcesPatchAuthorityEnabled ? patchCharacterSystemStateAndResourcesFromAuthority : null}

                      patchCharacterSystemStateAndEffects={systemStateAndEffectsPatchAuthorityEnabled ? patchCharacterSystemStateAndEffectsFromAuthority : null}

                      food={food}

                      saveFood={saveFood}

                      groupTravelLog={groupTravelLog}

                      saveGroupTravelLog={saveGroupTravelLogForTravelFeature}

                      onViewTravel={travelLogEnabled ? () => { setCharId(selectedDmCharId); setView('travel'); } : null}

                      onViewHome={() => { setCharId(selectedDmCharId); setView('home'); }}

                      onNotify={setDmNotification}

                      messages={messages}

                      onSaveMessages={saveMessages}

                      onSendStatusMessage={handleSendStatusMessage}

                      onEditSystem={systemsEnabled ? ((sysId) => { setEditSystemFromDash(sysId); setShowDMSettings(true); }) : null}

                      glowingBuffs={glowingBuffs}

                      addBuffGlow={addBuffGlow}

                      addBuffGlowPatch={addBuffGlowPatch}

                      addSystemGlowPatch={addSystemGlowPatch}

                      clearBuffGlow={clearBuffGlow}

                      addSystemGlow={addSystemGlow}

                      globalSettings={runtimeGlobalSettings}

                      journal={journal}

                      dmJournal={dmJournal}

                      npcLibrary={npcLibrary}

                      saveNpcLibrary={saveNpcLibrary}

                      onDeselect={() => setSelectedDmCharId(null)}

                    />

                  )}

                  </CrossFade>

                </>

              ) : (

                /* Expanded Mode - Full width vertical cards */

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                  {dmDashboardCharList.map(c => (

                    <DMCard 

                      key={c.id} 

                      c={c} 

                      diseases={runtimeDiseases} 

                      systems={runtimeSystems} 

                      systemsEnabled={systemsEnabled}

                      manaEnabled={campaignManaEnabled}
                      hungerEnabled={campaignHungerEnabled}
                      diseasesEnabled={campaignDiseasesEnabled}
                      foodSettingsEnabled={campaignFoodSettingsEnabled}

                      diets={diets} 

                      foodTypes={foodTypes} 

                      locations={locations} 

                      currentLocation={currentLocation} 

                      inv={inv} 

                      chars={dmDashboardChars}

                      saveChars={saveChars} 

                      patchCharacterVitals={characterVitalsAuthorityEnabled ? patchCharacterVitalsFromAuthority : null}

                      patchCharacterSystemInstance={systemInstancePatchAuthorityEnabled ? patchCharacterSystemInstanceFromAuthority : null}

                      patchCharacterSystemState={systemStatePatchAuthorityEnabled ? patchCharacterSystemStateFromAuthority : null}

                      patchCharacterSystemStateAndVitals={systemStateAndVitalsPatchAuthorityEnabled ? patchCharacterSystemStateAndVitalsFromAuthority : null}

                      patchCharacterSystemStateAndResources={systemStateAndResourcesPatchAuthorityEnabled ? patchCharacterSystemStateAndResourcesFromAuthority : null}

                      patchCharacterSystemStateAndEffects={systemStateAndEffectsPatchAuthorityEnabled ? patchCharacterSystemStateAndEffectsFromAuthority : null}

                      food={food} 

                      saveFood={saveFood} 

                      groupTravelLog={groupTravelLog} 

                      saveGroupTravelLog={saveGroupTravelLogForTravelFeature}

                      onViewTravel={travelLogEnabled ? () => { setCharId(c.id); setView('travel'); } : null}

                      onViewHome={() => { setCharId(c.id); setView('home'); }} 

                      onNotify={setDmNotification} 

                      messages={messages} 

                      onSaveMessages={saveMessages} 

                      onSendStatusMessage={handleSendStatusMessage}

                      onEditSystem={systemsEnabled ? ((sysId) => { setEditSystemFromDash(sysId); setShowDMSettings(true); }) : null}

                      glowingBuffs={glowingBuffs}

                      addBuffGlow={addBuffGlow}

                      addBuffGlowPatch={addBuffGlowPatch}

                      addSystemGlowPatch={addSystemGlowPatch}

                      clearBuffGlow={clearBuffGlow}

                      addSystemGlow={addSystemGlow}

                      globalSettings={runtimeGlobalSettings}

                      journal={journal}

                      dmJournal={dmJournal}

                      npcLibrary={npcLibrary}

                      saveNpcLibrary={saveNpcLibrary}

                    />

                  ))}

                </div>

              )}

            </DashboardSection>

          );

          

        case 'combat':

          return (

            <DashboardSection

              key={sectionId}

              id={sectionId}

              title="Combat"

              icon="⚔️"

              collapsed={isCollapsed}

              onToggleCollapse={toggleSectionCollapse}

              onMoveUp={() => moveDashboardSection(sectionId, 'up')}

              onMoveDown={() => moveDashboardSection(sectionId, 'down')}

              canMoveUp={canMoveUp}

              canMoveDown={canMoveDown}

            >

              <div style={{ 

                maxWidth: '800px', 

                margin: '0 auto', 

                padding: '16px', 

                background: dmDashboardCombat?.active

                  ? 'linear-gradient(135deg, rgba(201,39,39,0.2) 0%, rgba(139,20,20,0.15) 100%)'

                  : 'linear-gradient(135deg, rgba(100,39,39,0.1) 0%, rgba(80,20,20,0.05) 100%)',

                border: `2px solid ${dmDashboardCombat?.active ? '#c92727' : 'rgba(201,39,39,0.3)'}`,

                borderRadius: '12px',

              }}>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>

                  <div>

                    <h3 style={{ color: dmDashboardCombat?.active ? '#ff6666' : '#c92727', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>

                      {dmDashboardCombat?.active && <span style={{ fontSize: '0.7rem', background: '#c92727', padding: '2px 8px', borderRadius: '4px', color: 'white' }}>ACTIVE</span>}

                      {!dmDashboardCombat?.active && 'No active combat'}

                    </h3>

                    {dmDashboardCombat?.active && (

                      <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#ccc' }}>

                        Round {dmDashboardCombat.round} • {dmDashboardCombat.combatants?.length || 0} combatants

                      </p>

                    )}

                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>

                    {dmDashboardCombat?.active ? (

                      <>

                        <button style={{ ...S.btn, ...S.btnDanger, padding: '10px 20px' }} onClick={() => setView('combat')}>⚔️ Go to Combat</button>

                        <button style={{ ...S.btn, padding: '10px 16px' }} onClick={() => {

                          const updatedChars = { ...chars };

                          (dmDashboardCombat.combatants || []).filter(c => c.type === 'player').forEach(c => {

                            if (updatedChars[c.id]) updatedChars[c.id] = { ...updatedChars[c.id], currentHp: c.currentHp };

                          });

                          saveChars(updatedChars);

                          saveCombat({ ...dmDashboardCombat, active: false, combatants: [], round: 1, turnIndex: 0 });

                          setDmNotification('⚔️ Combat ended! HP synced.');

                          setTimeout(() => setDmNotification(null), 3000);

                        }}>🏁 End Combat</button>

                      </>

                    ) : (

                      <button style={{ ...S.btn, background: 'linear-gradient(180deg, #c92727 0%, #8b1a1a 100%)', borderColor: '#ff4444', padding: '12px 24px', fontSize: '1rem' }} onClick={() => {

                        const playerCombatants = dmDashboardLiveCharList.map(c => ({

                          id: c.id, name: c.name, type: 'player', maxHp: c.maxHp, currentHp: c.currentHp,

                          ac: 10 + Math.floor(((c.stats?.dex || 10) - 10) / 2), initiative: null,

                          resistances: (c.resistances || []).map(r => typeof r === 'string' ? { type: r, percent: 50 } : r),

                          vulnerabilities: (c.vulnerabilities || []).map(v => typeof v === 'string' ? { type: v, percent: 50 } : v),

                          immunities: c.immunities || [], raidwideResist: c.raidwideResist || 0, isCurrentTurn: false, conditions: [],

                        }));

                        saveCombat({ active: true, combatants: playerCombatants, round: 1, turnIndex: 0, damageLog: [], pendingDamage: [] });

                        setView('combat');

                      }}>⚔️ Start Combat</button>

                    )}

                  </div>

                </div>

              </div>

            </DashboardSection>

          );

          

        case 'systems-overview':

          const allAppliedSystems = [];

          dmDashboardCharList.forEach(c => {

            (c.systemList || []).forEach(s => {

              const sys = (s.systemId && systems[s.systemId]) ? systems[s.systemId] : s.template;

              if (sys) allAppliedSystems.push({ charId: c.id, charName: c.name, system: sys, instance: s });

            });

          });

          if (allAppliedSystems.length === 0) return null;

          const byType = {};

          allAppliedSystems.forEach(item => {

            const type = item.system.type || 'custom';

            if (!byType[type]) byType[type] = [];

            byType[type].push(item);

          });

          return (

            <DashboardSection

              key={sectionId}

              id={sectionId}

              title={`Systems Overview (${allAppliedSystems.length})`}

              icon="🔒"

              collapsed={isCollapsed}

              onToggleCollapse={toggleSectionCollapse}

              onMoveUp={() => moveDashboardSection(sectionId, 'up')}

              onMoveDown={() => moveDashboardSection(sectionId, 'down')}

              canMoveUp={canMoveUp}

              canMoveDown={canMoveDown}

              asCard={true}

            >

              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>

                <button style={{ ...S.btn, ...S.btnXs, ...S.btnSuccess }} onClick={() => {
                  setBatchApplyTargetMode(CAMPAIGN_CHARACTER_TARGET_MODE_PLAYERS);
                  setBatchApplyTargets(new Set());
                  setShowBatchApply(true);
                }}>📋 Batch Apply</button>

                <button style={{ ...S.btn, ...S.btnXs, ...S.btnDanger }} onClick={() => {
                  setBatchRemoveTargetMode(CAMPAIGN_CHARACTER_TARGET_MODE_PLAYERS);
                  setBatchRemoveTargets(new Set());
                  setShowBatchRemove(true);
                }}>🗑️ Batch Remove</button>

              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '10px' }}>

                {Object.entries(byType).map(([type, items]) => {

                  const typeMeta = SYSTEM_TYPE_OPTIONS.find(t => t.value === type);

                  return (

                    <div key={type} style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>

                      <div style={{ fontWeight: 'bold', color: '#b0b0ff', marginBottom: '8px' }}>{typeMeta?.icon || '🔮'} {typeMeta?.label || type} ({items.length})</div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>

                        {items.map((item, i) => {

                          const isHidden = item.instance.playerVisible === false;

                          const hasEffects = (item.system.effects || []).length > 0 || (item.system.hiddenEffects || []).length > 0;

                          return (

                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem' }}>

                              <span><span style={{ color: '#c9a227' }}>{item.charName}</span><span style={{ opacity: 0.5 }}> → </span><span style={{ color: isHidden ? '#888' : '#fff' }}>{item.system.name}</span></span>

                              <span style={{ display: 'flex', gap: '4px' }}>

                                {item.instance.active === false && <span style={{ fontSize: '0.7rem', color: '#ff6961' }}>⏸</span>}

                                {isHidden && <span style={{ fontSize: '0.7rem', color: '#888' }}>🙈</span>}

                                {hasEffects && item.instance.applyEffects !== false && <span style={{ fontSize: '0.7rem', color: '#8be28b' }}>⚡</span>}

                              </span>

                            </div>

                          );

                        })}

                      </div>

                    </div>

                  );

                })}

              </div>

            </DashboardSection>

          );

          

        case 'food':

          return (

            <DashboardSection

              key={sectionId}

              id={sectionId}

              title={`Food: ${food.totalUnits || 0}`}

              icon="🍖"

              collapsed={isCollapsed}

              onToggleCollapse={toggleSectionCollapse}

              onMoveUp={() => moveDashboardSection(sectionId, 'up')}

              onMoveDown={() => moveDashboardSection(sectionId, 'down')}

              canMoveUp={canMoveUp}

              canMoveDown={canMoveDown}

              asCard={true}

            >

              {Object.keys(invWithNotes).length > 0 && <div style={{ marginBottom: '16px' }}>{Object.entries(invWithNotes).map(([t, data]) => (<Tooltip key={t} text={getFoodTooltip(t, invWithNotes)}><span style={{ ...S.badge, ...S.badgeFood }}>{data.total}x {t}</span></Tooltip>))}</div>}

              <FoodMgr food={food} foodTypes={foodTypes} onAdd={addFood} onRemove={removeFood} showLog />

            </DashboardSection>

          );

          

        case 'travel-log':

          return (

            <DashboardSection

              key={sectionId}

              id={sectionId}

              title="Group Travel Log"

              icon="📜"

              collapsed={isCollapsed}

              onToggleCollapse={toggleSectionCollapse}

              onMoveUp={() => moveDashboardSection(sectionId, 'up')}

              onMoveDown={() => moveDashboardSection(sectionId, 'down')}

              canMoveUp={canMoveUp}

              canMoveDown={canMoveDown}

            >

              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>

                {sortedDays.length === 0 ? <p style={{ opacity: 0.7 }}>No activity</p> : sortedDays.map(day => {

                  const data = condensedLog[day];

                  const allChars = dmDashboardCharList;

                  const mealSummary = allChars.map(ch => {

                    const charMeals = data.meals[ch.name];

                    if (!charMeals || charMeals.length === 0) return { name: ch.name, text: 'None', hasFood: false };

                    const grouped = {};

                    charMeals.forEach(m => { grouped[m.type] = (grouped[m.type] || 0) + m.count; });

                    return { name: ch.name, text: Object.entries(grouped).map(([t, c]) => `x${c} ${t}`).join(', '), hasFood: true };

                  });

                  const specialEvents = data.specialEvents || [];

                  return <div key={day} style={{ padding: '12px', marginBottom: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px', borderLeft: '3px solid #ffd700' }}>

                    <div style={{ fontWeight: 'bold', color: '#ffd700', marginBottom: '8px' }}>Day {day}</div>

                    <div style={{ fontSize: '0.9rem' }}>{mealSummary.map((ms, i) => (<span key={i} style={{ marginRight: '16px' }}><span style={{ color: '#7db5ff' }}>{ms.name}:</span> <span style={{ color: ms.hasFood ? '#c9a227' : '#c92727' }}>{ms.text}</span></span>))}</div>

                    {specialEvents.length > 0 && <div style={{ marginTop: '8px' }}>{specialEvents.map((ev, i) => (<div key={i} style={{ fontSize: '0.85rem', color: ev.event === 'starving' ? '#c92727' : ev.event === 'disease' ? '#d47dd4' : '#e8d5b7' }}>{ev.event === 'starving' && <span>⚠️ {ev.char} is starving!</span>}{ev.event === 'disease' && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><BiohazardIcon size={14} color='#d47dd4' /> {ev.char} contracted {ev.disease}!</span>}</div>))}</div>}

                  </div>;

                })}

              </div>

            </DashboardSection>

          );

          

        case 'messages':

          return (

            <DashboardSection

              key={sectionId}

              id={sectionId}

              title="Message Composer"

              icon="✉️"

              collapsed={isCollapsed}

              onToggleCollapse={toggleSectionCollapse}

              onMoveUp={() => moveDashboardSection(sectionId, 'up')}

              onMoveDown={() => moveDashboardSection(sectionId, 'down')}

              canMoveUp={canMoveUp}

              canMoveDown={canMoveDown}

            >

              <DMMessageComposer chars={dmDashboardMessageChars} messages={messages} onSaveMessages={saveMessages} onSendStatusMessage={handleSendStatusMessage} groupTravelLog={groupTravelLog} onSaveGroupTravelLog={saveGroupTravelLogForTravelFeature} />

            </DashboardSection>

          );

          

        case 'session-mode':

          return (

            <DashboardSection

              key={sectionId}

              id={sectionId}

              title="Session Mode"

              icon="🎯"

              collapsed={isCollapsed}

              onToggleCollapse={toggleSectionCollapse}

              onMoveUp={() => moveDashboardSection(sectionId, 'up')}

              onMoveDown={() => moveDashboardSection(sectionId, 'down')}

              canMoveUp={canMoveUp}

              canMoveDown={canMoveDown}

            >

              <SessionModePanel

                audit={audit}

                chars={dmDashboardChars}

                onStartSession={startSession}

                onEndSession={endSession}

                onOpenTimeline={(opts) => {

                  setTimelineInitRange(opts?.range || null);

                  setView('timeline');

                }}

                onSeedDemo={seedDemoAudit}

                onClearDemo={clearDemoAudit}

              />

            </DashboardSection>

          );

          

        case 'shop':

          return (

            <DashboardSection

              key={sectionId}

              id={sectionId}

              title="Shop Manager"

              icon="🏪"

              collapsed={isCollapsed}

              onToggleCollapse={toggleSectionCollapse}

              onMoveUp={() => moveDashboardSection(sectionId, 'up')}

              onMoveDown={() => moveDashboardSection(sectionId, 'down')}

              canMoveUp={canMoveUp}

              canMoveDown={canMoveDown}

            >

              {/* Quick shop overview + link to full shop page */}

              <div>

                {(displayedShopData?.shops || []).length === 0 ? (

                  <div style={{ textAlign: 'center', padding: '20px' }}>

                    <div style={{ fontSize: '1.5rem', marginBottom: 8 }}>🏪</div>

                    <p style={{ opacity: 0.5, marginBottom: 12, fontSize: '0.85rem' }}>No shops created yet</p>

                    <button style={{ ...S.btn, ...S.btnSuccess }} onClick={() => setView('shop')}>+ Create First Shop</button>

                  </div>

                ) : (

                  <>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>

                      {(displayedShopData?.shops || []).map(s => (

                        <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'rgba(0,0,0,0.2)', borderRadius: 6, borderLeft: `3px solid ${s.isOpen ? '#27c95a' : '#c92727'}` }}>

                          <div>

                            <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>{s.name}</div>

                            <div style={{ fontSize: '0.75rem', opacity: 0.4 }}>{s.keeperName} · {s.type} · {(s.inventory || []).length} items</div>

                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

                            <span style={{ ...S.badge, ...(s.isOpen ? S.badgeBuff : S.badgeDebuff), fontSize: '0.7rem' }}>{s.isOpen ? '✓ Open' : '✕ Closed'}</span>

                            <button
                              type="button"
                              aria-label={`${s.isOpen ? 'Close' : 'Open'} ${s.name || 'shop'} from dashboard`}
                              title={`${s.isOpen ? 'Close' : 'Open'} shop from dashboard`}
                              style={{ ...S.btn, ...S.btnXs }}
                              onClick={() => {

                              const newShops = (displayedShopData?.shops || []).map(sh => sh.id === s.id ? { ...sh, isOpen: !sh.isOpen } : sh);

                              saveDisplayedShopData({ ...displayedShopData, shops: newShops });

                            }}>{s.isOpen ? '🔒' : '👁'}</button>

                          </div>

                        </div>

                      ))}

                    </div>

                    {/* Pending offers indicator */}

                    {(displayedShopData?.offers || []).filter(o => o.status === 'pending').length > 0 && (

                      <div style={{ padding: '10px 14px', background: 'rgba(201,162,39,0.1)', border: '1px solid rgba(201,162,39,0.2)', borderRadius: 6, marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                        <span style={{ fontSize: '0.85rem' }}>📨 {(displayedShopData?.offers || []).filter(o => o.status === 'pending').length} pending trade offer{(displayedShopData?.offers || []).filter(o => o.status === 'pending').length > 1 ? 's' : ''}</span>

                        <button style={{ ...S.btn, ...S.btnXs, ...S.btnWarn }} onClick={() => setView('shop')}>Review</button>

                      </div>

                    )}

                    {/* Recent transactions */}

                    {(displayedShopData?.transactionLog || []).length > 0 && (

                      <div style={{ marginBottom: 12 }}>

                        <div style={{ fontSize: '0.75rem', opacity: 0.4, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>Recent Transactions</div>

                        {(displayedShopData?.transactionLog || []).slice(0, 5).map((entry, idx) => {

                          const sym = (globalSettings?.currencyTiers || DEFAULT_SETTINGS.currencyTiers).slice(-1)[0]?.symbol || 'gp';

                          return entry.type === 'trade' ? (

                            <div key={idx} style={{ fontSize: '0.8rem', padding: '4px 0', opacity: 0.6 }}>

                              <div>🤝 <span style={{ color: '#8a8ab8' }}>{entry.char}</span> traded</div>

                              <div style={{ fontSize: '0.72rem', marginLeft: 16, opacity: 0.7 }}>{entry.giving} ⇄ {entry.wanting}</div>

                            </div>

                          ) : entry.type === 'sell-back' ? (

                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', padding: '3px 0', opacity: 0.6 }}>

                              <span>{entry.char} sold {entry.item} Ã—{entry.qty}</span>

                              <span style={{ color: '#7ab87a' }}>+{entry.total}{sym}</span>

                            </div>

                          ) : (

                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', padding: '3px 0', opacity: 0.6 }}>

                              <span>{entry.char} bought {entry.item} ×{entry.qty}</span>

                              <span style={{ color: '#d4aa40' }}>−{entry.total}{sym}</span>

                            </div>

                          );

                        })}

                      </div>

                    )}

                    <button style={{ ...S.btn, width: '100%', padding: '10px 16px', fontSize: '0.88rem', fontWeight: 600, background: 'linear-gradient(180deg, rgba(155,89,182,0.2) 0%, rgba(155,89,182,0.08) 100%)', border: '1px solid rgba(155,89,182,0.4)', borderRadius: '8px', color: '#d4a5ff', letterSpacing: '0.5px', boxShadow: '0 2px 8px rgba(155,89,182,0.15)' }} onClick={() => setView('shop')}>🏪 Shop Manager</button>

                  </>

                )}

              </div>

            </DashboardSection>

          );

          

        case 'crafting':

          return (

            <DashboardSection

              key={sectionId}

              id={sectionId}

              title="Crafting Table"

              icon="⚒️"

              collapsed={isCollapsed}

              onToggleCollapse={toggleSectionCollapse}

              onMoveUp={() => moveDashboardSection(sectionId, 'up')}

              onMoveDown={() => moveDashboardSection(sectionId, 'down')}

              canMoveUp={canMoveUp}

              canMoveDown={canMoveDown}

            >

              <div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>

                  <div style={{ fontSize: '0.85rem', opacity: 0.6 }}>

                    {craftingOpen ? '✓ Open to players' : '✕ Closed to players'}

                  </div>

                  <button style={{ ...S.btn, ...S.btnXs, ...(craftingOpen ? S.badgeBuff : S.badgeDebuff) }} onClick={() => saveCraftingOpen(!craftingOpen)}>

                    {craftingOpen ? '🔒 Close' : '👁 Open'}

                  </button>

                </div>

                <button style={{ ...S.btn, width: '100%', padding: '10px 16px', fontSize: '0.88rem', fontWeight: 600, background: 'linear-gradient(180deg, rgba(212,168,75,0.2) 0%, rgba(212,168,75,0.08) 100%)', border: '1px solid rgba(212,168,75,0.4)', borderRadius: '8px', color: '#e6c860', letterSpacing: '0.5px', boxShadow: '0 2px 8px rgba(212,168,75,0.15)' }} onClick={() => setView('crafting')}>⚒️ Crafting</button>

              </div>

            </DashboardSection>

          );



        case 'loot':

          return (

            <DashboardSection

              key={sectionId}

              id={sectionId}

              title="Loot System"

              icon={<TreasureChestIcon size={20} color="#d4aa40" />}

              collapsed={isCollapsed}

              onToggleCollapse={toggleSectionCollapse}

              onMoveUp={() => moveDashboardSection(sectionId, 'up')}

              onMoveDown={() => moveDashboardSection(sectionId, 'down')}

              canMoveUp={canMoveUp}

              canMoveDown={canMoveDown}

            >

              <div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>

                  <div style={{ fontSize: '0.85rem', opacity: 0.6 }}>

                    {displayedLootData?.activeEncounter ? '● Active loot encounter' : `${(displayedLootData?.enemies || []).length} enemies · ${Object.keys(displayedLootData?.items || {}).length} items`}

                  </div>

                  {displayedLootData?.activeEncounter && <span style={{ ...S.badge, ...S.badgeBuff, fontSize: '0.7rem' }}>Active</span>}

                </div>

                <button style={{ ...S.btn, width: '100%', padding: '10px 16px', fontSize: '0.88rem', fontWeight: 600, background: 'linear-gradient(180deg, rgba(212,170,64,0.2) 0%, rgba(212,170,64,0.08) 100%)', border: '1px solid rgba(212,170,64,0.4)', borderRadius: '8px', color: '#d4aa40', letterSpacing: '0.5px', boxShadow: '0 2px 8px rgba(212,170,64,0.15)' }} onClick={() => setView('loot')}>⚜️ Loot</button>

              </div>

            </DashboardSection>

          );



        default:

          return null;

      }

    };

    

    return <div style={isCampaignSandboxSession ? { ...S.container, paddingTop: '108px' } : S.container}>

      <div style={{ maxWidth: '960px', margin: '0 auto' }}>

      <div style={{ ...S.flex, justifyContent: 'space-between' }}>

        <div style={S.flex}>

          <Tooltip text="Return to character select"><button style={S.btn} onClick={logout}>🚪 Logout</button></Tooltip>

          <Tooltip text="Configure diets, diseases, food types, locations"><button style={{ ...S.btn, ...S.btnEdit }} onClick={() => setShowDMSettings(true)}>⚙️ DM Settings</button></Tooltip>

          <Tooltip text="Change app theme and colors"><button style={{ ...S.btn, ...S.btnWarn }} onClick={() => setShowThemeConfig(true)}>🎨 Theme</button></Tooltip>

          <Tooltip text="Reset dashboard layout"><button style={{ ...S.btn, ...S.btnSubtle }} onClick={resetDashboardOrder}>🔄 Reset Layout</button></Tooltip>

        </div>

        <span style={{ ...S.badge, ...S.badgeNeutral }}>👑 DM Mode</span>

      </div>

      <h1 style={S.title}>🏰 DM Dashboard</h1>

      

      {/* Quick Actions Bar */}

      <div style={{ textAlign: 'center', marginBottom: '16px' }}>

        {travelLogEnabled && (
        <button style={{ ...S.btn, ...S.btnSm }} onClick={() => {

          endDayForAll();

          setDmNotification('⏭️ Day ended for all characters!');

          setTimeout(() => setDmNotification(null), 3000);

        }}>⏭️ End Day for All</button>
        )}

        {travelLogEnabled && (campaignHungerEnabled || campaignDietsEnabled || campaignDiseasesEnabled) && (

        <button style={{ ...S.btn, ...S.btnSm, ...S.btnMana, marginLeft: '8px' }} onClick={endWeekForAll}>📅 End Week for All</button>

        )}

        {campaignDiseasesEnabled && (

        <button style={{ ...S.btn, ...S.btnSm, ...S.btnWarn, marginLeft: '8px' }} onClick={() => setShowDiseaseCheckModal(true)}>☣ Disease Check</button>

        )}

        {travelLogEnabled && (campaignHungerEnabled || campaignDietsEnabled || campaignDiseasesEnabled) && (

        <button style={{ ...S.btn, ...S.btnSm, ...S.btnDanger, marginLeft: '8px' }} onClick={() => {

          const c = { ...chars };

          Object.keys(livePlayableChars || {}).forEach(cid => {

            const original = c[cid];

            const set = original.settings || DEFAULT_SETTINGS;

            c[cid] = {

              ...original, currentDay: 1, daysWithoutFood: 0, daysToStarvation: set.starvationThreshold || 7,

              starving: false, mealsToday: 0, mealLog: [], immuneToday: false, actionHistory: [], wasWellFed: false,

              weeklyHungerScore: 0, currentWeekNumber: 1, consecutiveNegativeWeeks: 0, negativeWeekStreak: 0,

              diseaseCheckConPenalty: 0, dietWeekProgress: 0, dietDayStreak: 0,

              ...clearFoodEffectState(original, set, {

                removeWellFed: true,

                removeHungry: true,

                removeStarvation: true,

                resetStarving: true,

                resetDaysToStarvation: true,

              }),

              statusHistory: [{ timestamp: new Date().toISOString(), day: 1, event: 'reset', description: 'Reset to Day 1 by DM' }, ...(original.statusHistory || [])]

            };

          });

          saveChars(c);

          if (typeof saveGroupTravelLogForTravelFeature === 'function') {
            saveGroupTravelLogForTravelFeature([{ day: 1, character: 'DM', action: 'reset all to Day 1', timestamp: new Date().toISOString() }, ...(groupTravelLog || []).slice(0, 199)]);
          }

          setDmNotification('🔄 All characters reset to Day 1!');

          setTimeout(() => setDmNotification(null), 3000);

        }}>🔄 Reset All to Day 1</button>

        )}

        {!isCampaignSandboxSession && !activeViewerCampaignId && (
        <button
          style={{ ...S.btn, ...S.btnSm, ...S.btnEdit, marginLeft: '8px' }}
          onClick={loadDashboardTestCharacter}
        >
          🧪 Load Practice Char
        </button>
        )}

        <button style={{ ...S.btn, ...S.btnSm, ...S.btnMana, marginLeft: '8px' }} onClick={() => setView('npcs')}>📚 Library</button>

        {dmNotification && <div style={{ marginTop: '8px', padding: '12px', background: 'rgba(39,201,90,0.3)', border: '1px solid #27c95a', color: '#27c95a', borderRadius: '4px' }}>{dmNotification}</div>}

      </div>

      

      {/* Location Selector */}

      <div style={{ marginBottom: '16px' }}>

        <div style={{ ...S.flex, justifyContent: 'center' }}>

          <label style={{ ...S.label, marginBottom: 0, marginRight: '8px' }}>📍 Current Location:</label>

          <select style={{ ...S.select, width: 'auto', marginBottom: 0 }} value={currentLocation} onChange={e => saveCurrentLocation(e.target.value)}>

            {locations.map(l => <option key={l} value={l}>{l}</option>)}

          </select>

        </div>

      </div>

      

      {/* Reorderable Dashboard Sections */}

      {dashboardSectionOrder.map((sectionId, idx) => renderSection(sectionId, idx))}

      

      {showDMSettings && <DMSettings diets={diets} diseases={diseases} systems={systems} foodTypes={foodTypes} locations={locations} globalSettings={globalSettings} chars={isCampaignSandboxSession ? dmDashboardChars : livePlayableChars} spellLibrary={spellLibrary} campaignHomebrew={runtimeCampaignHomebrew} campaignSystemsEnabled={systemsEnabled} onSaveSpellLibrary={saveSpellLibrary} onUpdateChar={updateChar} onClose={() => { setShowDMSettings(false); setEditSystemFromDash(null); }} onSaveDiets={saveDiets} onSaveDiseases={saveDiseases} onSaveSystems={saveSystems} onSaveFoodTypes={saveFoodTypes} onSaveLocations={saveLocations} onSaveGlobalSettings={saveGlobalSettings} initialEditSystem={systemsEnabled ? editSystemFromDash : null} />}

      {showSandboxSettings && isCampaignSandboxSession && (
        <SandboxCampaignSettingsModal
          theme={currentTheme}
          settings={globalSettings || {}}
          onClose={() => setShowSandboxSettings(false)}
          onSave={saveSandboxCampaignSettings}
        />
      )}

      {showSandboxParty && isCampaignSandboxSession && (
        <SandboxPartyModal
          theme={currentTheme}
          chars={chars || {}}
          activeCharId={charId}
          isDmView={isDM}
          presetCategories={sandboxPresetCategories}
          onClose={() => setShowSandboxParty(false)}
          onEnterDm={switchSandboxToDm}
          onEnterPersona={switchSandboxToPersona}
          onAddDemoPersona={addSandboxDemoPersona}
          onAddBlankPersona={addBlankSandboxPersona}
          onPresetCategoriesChange={updateSandboxPresetCategories}
        />
      )}

      

      {/* Disease Check Modal */}

      {showDiseaseCheckModal && campaignDiseasesEnabled && (

        <div style={S.modal} onClick={() => setShowDiseaseCheckModal(false)}>

          <div style={{ ...S.modalContent, maxWidth: '600px' }} onClick={e => e.stopPropagation()}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>

              <h2 style={{ color: '#ffd700', margin: 0 }}>☣ Disease Check</h2>

              <button style={{ ...S.btn, ...S.btnXs }} onClick={() => setShowDiseaseCheckModal(false)}>✕</button>

            </div>

            

            <p style={{ fontSize: '0.85rem', opacity: 0.7, marginBottom: '16px' }}>

              Shows effective Constitution for disease checks. Penalties from weekly hunger affect disease resistance.

            </p>

            

            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>

              {dmDashboardCharList.length === 0 ? (

                <p style={{ opacity: 0.5 }}>No characters</p>

              ) : (

                dmDashboardCharList.map(c => {

                  const baseCon = c.baseStats?.con || 10;

                  const effectiveCon = getEffectiveConForDiseaseCheck(c, diseases);

                  const penalty = baseCon - effectiveCon;

                  const hungerScore = (c.weeklyHungerScore || 0) + (c.weeklyStarvationBonus || 0);

                  const conMod = Math.floor((effectiveCon - 10) / 2);

                  

                  return (

                    <div key={c.id} style={{ 

                      padding: '16px', 

                      marginBottom: '12px', 

                      background: 'rgba(0,0,0,0.3)', 

                      borderRadius: '8px',

                      borderLeft: `4px solid ${penalty > 0 ? '#c92727' : '#27c95a'}`

                    }}>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>

                        <div>

                          <strong style={{ color: '#c9a227', fontSize: '1.1rem' }}>{c.name}</strong>

                          <span style={{ marginLeft: '8px', opacity: 0.7, fontSize: '0.85rem' }}>Week {c.currentWeekNumber || 1}</span>

                        </div>

                        <div style={{ textAlign: 'right' }}>

                          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: penalty > 0 ? '#ff7d7d' : '#7dff7d' }}>

                            CON: {effectiveCon} ({conMod >= 0 ? '+' : ''}{conMod})

                          </div>

                          {penalty > 0 && (

                            <div style={{ fontSize: '0.8rem', color: '#c92727' }}>

                              ({penalty > 0 ? '-' : '+'}{Math.abs(penalty)} from hunger)

                            </div>

                          )}

                        </div>

                      </div>

                      

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', fontSize: '0.8rem' }}>

                        <div style={{ padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px' }}>

                          <div style={{ color: '#888', marginBottom: '2px' }}>Base Con</div>

                          <div style={{ fontWeight: 'bold' }}>{baseCon}</div>

                        </div>

                        <div style={{ padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px' }}>

                          <div style={{ color: '#888', marginBottom: '2px' }}>Weekly Score</div>

                          <div style={{ fontWeight: 'bold', color: hungerScore < 0 ? '#c92727' : hungerScore > 0 ? '#27c95a' : '#888' }}>

                            {hungerScore >= 0 ? '+' : ''}{hungerScore.toFixed(1)}

                          </div>

                        </div>

                        <div style={{ padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px' }}>

                          <div style={{ color: '#888', marginBottom: '2px' }}>Disease Penalty</div>

                          <div style={{ fontWeight: 'bold', color: c.diseaseCheckConPenalty > 0 ? '#c92727' : '#27c95a' }}>

                            {c.diseaseCheckConPenalty > 0 ? `-${c.diseaseCheckConPenalty}` : '0'}

                          </div>

                        </div>

                      </div>

                      

                      {c.consecutiveNegativeWeeks > 0 && (

                        <div style={{ marginTop: '8px', padding: '8px', background: 'rgba(201,39,39,0.2)', borderRadius: '4px', fontSize: '0.8rem', color: '#ff7d7d' }}>

                          ⚠️ {c.consecutiveNegativeWeeks} consecutive negative week{c.consecutiveNegativeWeeks > 1 ? 's' : ''}

                          {c.consecutiveNegativeWeeks >= 3 && ' - STAT LOSS TRIGGERED'}

                          {c.consecutiveNegativeWeeks >= 4 && ' - DEATH'}

                        </div>

                      )}

                      

                      {(c.diseaseList || []).length > 0 && (

                        <div style={{ marginTop: '8px', fontSize: '0.8rem' }}>

                          <span style={{ color: '#d47dd4', display: 'inline-flex', alignItems: 'center', gap: 3 }}><BiohazardIcon size={13} color='#d47dd4' /> Active diseases: </span>

                          {c.diseaseList.map((d, i) => (

                            <span key={i} style={{ color: '#d47dd4' }}>

                              {diseases[d.diseaseId]?.name || 'Unknown'}{i < c.diseaseList.length - 1 ? ', ' : ''}

                            </span>

                          ))}

                        </div>

                      )}

                    </div>

                  );

                })

              )}

            </div>

            

            <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(201,162,39,0.1)', borderRadius: '8px', border: '1px solid rgba(201,162,39,0.3)' }}>

              <strong style={{ color: '#c9a227' }}>Weekly Hunger Consequences:</strong>

              <ul style={{ margin: '8px 0 0 16px', fontSize: '0.85rem', opacity: 0.8 }}>

                <li><strong>0 to -2:</strong> Con -2 for disease checks this month</li>

                <li><strong>-3 to -6:</strong> Instant disease check (Con -4 for that check)</li>

                <li><strong>-7 or below:</strong> Instant disease + permanent -1 Con</li>

                <li><strong>3+ negative weeks:</strong> -1 all stats (increases each time)</li>

                <li><strong>4 negative weeks:</strong> Death</li>

              </ul>

            </div>

            

            <button style={{ ...S.btn, marginTop: '16px', width: '100%' }} onClick={() => setShowDiseaseCheckModal(false)}>Close</button>

          </div>

        </div>

      )}

      

      {/* Batch Apply System Modal */}

      {showBatchApply && (

        <div style={S.modal} onClick={() => setShowBatchApply(false)}>

          <div style={{ ...S.modalContent, maxWidth: '600px' }} onClick={e => e.stopPropagation()}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>

              <h2 style={{ color: '#c9a227', margin: 0 }}>📋 Batch Apply System</h2>

              <button style={{ ...S.btn, ...S.btnXs }} onClick={() => setShowBatchApply(false)}>✕</button>

            </div>

            

            <div style={{ marginBottom: '16px' }}>

              <label style={S.label}>Select System Template</label>

              <select

                style={{ ...S.select, width: '100%' }}

                value={batchApplySystem}

                onChange={e => {
                  setBatchApplySystem(e.target.value);
                  setBatchApplySlotValuesByChar({});
                }}

              >

                <option value="">Choose a system...</option>

                {Object.entries(systems || {}).map(([id, sys]) => {

                  const icon = SYSTEM_TYPE_OPTIONS.find(t => t.value === (sys.type || 'custom'))?.icon || '🔮';

                  return (

                    <option key={id} value={id}>{icon} {sys.name}</option>

                  );

                })}

              </select>

            </div>

            {renderBatchTargetModeControl({
              mode: batchApplyTargetMode,
              setMode: setBatchApplyTargetMode,
              setTargets: setBatchApplyTargets,
            })}

            

            <div style={{ marginBottom: '16px' }}>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>

                <label style={{ ...S.label, marginBottom: 0 }}>Select Characters ({batchApplyTargets.size} selected)</label>

                <div style={{ display: 'flex', gap: '6px' }}>

                  <button

                    style={{ ...S.btn, ...S.btnXs, marginBottom: 0 }}

                    onClick={() => setBatchApplyTargets(new Set(getCampaignCharacterTargetIdsForMode(
                      chars,
                      effectiveBatchApplyTargetMode,
                      { campaignId: activeViewerCampaignId },
                    )))}

                  >

                    Select All {getCampaignCharacterTargetModeCopy(effectiveBatchApplyTargetMode).ctaNoun}

                  </button>

                  <button

                    style={{ ...S.btn, ...S.btnXs, marginBottom: 0 }}

                    onClick={() => setBatchApplyTargets(new Set())}

                  >

                    Clear

                  </button>

                </div>

              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px', maxHeight: '200px', overflow: 'auto', padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>

                {batchApplyTargetRows.map(c => {

                  const isSelected = batchApplyTargets.has(c.id);

                  const batchTemplate = batchApplySystem ? systems[batchApplySystem] : null;
                  const selectedSlotValues = batchTemplate
                    ? seedSystemInstanceSlotValues(batchTemplate, batchApplySlotValuesByChar[c.id] || {})
                    : {};
                  const alreadyHas = batchApplySystem && (c.systemList || []).some(s => (
                    batchTemplate
                      ? systemInstanceMatchesApplyTarget(s, batchApplySystem, batchTemplate, selectedSlotValues, null)
                      : s.systemId === batchApplySystem
                  ));

                  return (

                    <label

                      key={c.id}

                      style={{

                        display: 'flex',

                        alignItems: 'center',

                        gap: '8px',

                        padding: '6px 8px',

                        background: isSelected ? 'rgba(201,162,39,0.2)' : 'rgba(0,0,0,0.1)',

                        borderRadius: '4px',

                        cursor: 'pointer',

                        border: isSelected ? '1px solid rgba(201,162,39,0.4)' : '1px solid transparent',

                      }}

                    >

                      <input

                        type="checkbox"

                        checked={isSelected}

                        onChange={e => {

                          const newSet = new Set(batchApplyTargets);

                          if (e.target.checked) newSet.add(c.id);

                          else newSet.delete(c.id);

                          setBatchApplyTargets(newSet);

                        }}

                      />

                      <span style={{ fontSize: '0.85rem' }}>

                        {c.name}

                        {isCampaignTestCharacter(c) && <span style={{ color: '#c9a227', marginLeft: '4px' }}>(Practice)</span>}

                        {alreadyHas && <span style={{ color: '#c9a227', marginLeft: '4px' }}>(has)</span>}

                      </span>

                    </label>

                  );

                })}

              </div>

            </div>

            

            {batchApplySystem && systems[batchApplySystem] && (

              <div style={{ marginBottom: '16px', padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>

                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>

                  {SYSTEM_TYPE_OPTIONS.find(t => t.value === (systems[batchApplySystem].type || 'custom'))?.icon || '🔮'}{' '}

                  {systems[batchApplySystem].name}

                </div>

                {systems[batchApplySystem].description && (

                  <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>{systems[batchApplySystem].description}</div>

                )}

                {(systems[batchApplySystem].effects || []).length > 0 && (

                  <div style={{ marginTop: '6px', fontSize: '0.8rem' }}>

                    <span style={{ color: '#888' }}>Effects: </span>

                    {systems[batchApplySystem].effects.map(e => typeof e === 'string' ? e : e.displayText).join(', ')}

                  </div>

                )}

                {getSystemInstanceSlotValidationErrors(systems[batchApplySystem].instanceSlots || []).length > 0 && (

                  <div style={{ marginTop: '8px', fontSize: '0.8rem', color: '#ff9f7d' }}>

                    {getSystemInstanceSlotValidationErrors(systems[batchApplySystem].instanceSlots || [])[0].message}

                  </div>

                )}

                {(systems[batchApplySystem].instanceSlots || []).length > 0
                  && getSystemInstanceSlotValidationErrors(systems[batchApplySystem].instanceSlots || []).length === 0 && (

                  <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>

                    <div style={{ fontSize: '0.8rem', color: '#c9a227' }}>
                      Fill slot values for each selected character.
                    </div>

                    {batchApplyTargetRows.filter(char => batchApplyTargets.has(char.id)).length === 0 ? (
                      <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>Select at least one character to fill slots.</div>
                    ) : batchApplyTargetRows.filter(char => batchApplyTargets.has(char.id)).map(char => (
                      <div key={char.id} style={{ padding: '8px', background: 'rgba(0,0,0,0.18)', borderRadius: '6px', border: '1px solid rgba(201,162,39,0.16)' }}>
                        <div style={{ fontSize: '0.82rem', color: '#d4a5ff', marginBottom: '6px', fontWeight: 'bold' }}>{char.name}</div>
                        {(systems[batchApplySystem].instanceSlots || []).map((slot, si) => {
                          const key = slot.key || `slot-${si}`;
                          const value = batchApplySlotValuesByChar[char.id]?.[key] ?? slot.default ?? '';
                          return (
                            <label key={key} style={{ display: 'block', marginBottom: '6px' }}>
                              <span style={{ display: 'block', fontSize: '0.75rem', color: '#aaa', marginBottom: '2px' }}>
                                {slot.label || key}
                                <span style={{ opacity: 0.55, marginLeft: '6px', fontFamily: 'monospace' }}>{'{{' + key + '}}'}</span>
                              </span>
                              <input
                                aria-label={`${char.name} ${slot.label || key}`}
                                style={{ ...S.input, marginBottom: 0, padding: '6px', fontSize: '0.78rem' }}
                                value={value}
                                placeholder={slot.default || 'Enter a value...'}
                                onChange={event => {
                                  const nextValue = event.target.value;
                                  setBatchApplySlotValuesByChar(prev => ({
                                    ...prev,
                                    [char.id]: {
                                      ...(prev[char.id] || {}),
                                      [key]: nextValue,
                                    },
                                  }));
                                }}
                              />
                              {slot.description && (
                                <div style={{ fontSize: '0.7rem', opacity: 0.6, marginTop: '2px' }}>{slot.description}</div>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    ))}

                  </div>

                )}

              </div>

            )}

            

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>

              <button style={{ ...S.btn, ...S.btnSubtle }} onClick={() => {
                setShowBatchApply(false);
                setBatchApplySlotValuesByChar({});
              }}>Cancel</button>

              <button

                style={{ ...S.btn, ...S.btnSuccess }}

                disabled={
                  !batchApplySystem
                  || batchApplyTargets.size === 0
                  || getSystemInstanceSlotValidationErrors(systems[batchApplySystem]?.instanceSlots || []).length > 0
                }

                onClick={() => {

                  const systemId = batchApplySystem;

                  const sys = systems[systemId];

                  if (!sys) return;

                  const slotValidationErrors = getSystemInstanceSlotValidationErrors(sys.instanceSlots || []);
                  if (slotValidationErrors.length > 0) {
                    setDmNotification(
                      slotValidationErrors[0]?.message
                      || 'Fix instance slots before batch applying.'
                    );
                    return;
                  }

                  

                  const preset = SYSTEM_PRESETS[sys.type] || SYSTEM_PRESETS.custom;

                  const defaultVis = preset.defaultVisibility || {};

                  const cs = { ...chars };
                  const authorityBeforeByCharId = {};

                  let applied = 0;
                  const applySideEffects = { notifications: [], statusMessages: [], logEntries: [], pendingSaves: [], pendingNpcUpdates: [] };
                  const applyTravelLogEntries = [];
                  const applyPendingSaveEntries = [];

                  

                  batchApplyTargets.forEach(charId => {

                    if (!cs[charId]) return;

                    const existingSystemList = cs[charId]?.systemList || [];
                    const seededSlotValues = seedSystemInstanceSlotValues(sys, batchApplySlotValuesByChar[charId] || {});
                    const existingIndex = existingSystemList.findIndex(s => (
                      systemInstanceMatchesApplyTarget(s, systemId, sys, seededSlotValues, null)
                    ));

                    if (existingIndex >= 0) {
                      const existingInstance = existingSystemList[existingIndex];
                      if (existingInstance?.active !== false) return;

                      authorityBeforeByCharId[charId] = cloneCharForSystemAuthority(cs[charId]);
                      cs[charId] = cloneCharForSystemEdit(cs[charId]);
                      const reactivatedAt = new Date().toISOString();
                      cs[charId].systemList = (cs[charId].systemList || []).map((entry, index) => (
                        index === existingIndex
                          ? {
                              ...entry,
                              active: true,
                              reactivatedAt,
                              history: [
                                ...(Array.isArray(entry.history) ? entry.history : []),
                                { action: 'reactivated', timestamp: reactivatedAt, details: 'Batch reactivated by DM' },
                              ],
                            }
                          : entry
                      ));

                      if (typeof addSystemGlowPatch === 'function') {
                        const reactivatedInstance = cs[charId].systemList[existingIndex];
                        const badgeColorId = reactivatedInstance.badgeColor || sys?.badgeColor || null;
                        const colorOpt = badgeColorId ? BUFF_COLOR_OPTIONS.find(o => o.id === badgeColorId) : null;
                        const glowColor = colorOpt?.glowColor || colorOpt?.color || '#ffd700';
                        cs[charId] = addSystemGlowPatch(cs[charId], reactivatedInstance.id || reactivatedInstance.systemId, glowColor);
                      }

                      applied++;
                      return;
                    }

                    authorityBeforeByCharId[charId] = cloneCharForSystemAuthority(cs[charId]);
                    cs[charId] = cloneCharForSystemEdit(cs[charId]);

                    

                    const entry = {

                      id: buildStableSlottedSystemInstanceId(sys, {
                        parentSystemId: null,
                        slotValues: seededSlotValues,
                      }) || generateId(),

                      systemId: systemId,

                      template: null,

                      playerVisible: defaultVis.playerVisible !== false,

                      active: true,

                      showName: defaultVis.showName || false,

                      showDescription: defaultVis.showDescription || false,

                      visibleClues: [],

                      showEffects: defaultVis.showEffects || false,

                      showCure: defaultVis.showCure || false,

                      applyEffects: preset.hasEffects !== false,

                      applyHiddenEffects: sys.type === 'secret' || sys.type === 'curse',

                      visibleSections: {},

                      showSections: {},

                      effectOverrides: {}, // Per-effect apply/showSource settings

                      trackerVisibility: {}, // Per-tracker visibility settings

                      trackerValues: {},

                      instanceValues: seededSlotValues,

                      // Placeholder text settings

                      unknownText: UNKNOWN_PLACEHOLDER,

                      placeholderName: '',

                      placeholderDescription: '',

                      // Clues label customization

                      cluesLabel: 'Clues',

                      showCluesLabel: true,

                      dmNotes: '',

                      playerNotes: '',

                      showPlayerNotes: false,

                      appliedAt: new Date().toISOString(),

                      history: [{ action: 'applied', timestamp: new Date().toISOString(), details: 'Batch applied by DM' }],

                    };

                    cs[charId].systemList = [...(cs[charId].systemList || []), entry];



                    // Fire on-apply rules for this character's newly added system (silent dispatch)

                    const tpl = systems[systemId] || entry.template;

                    if (tpl) {

                      const ctx = { char: cs[charId], systemInstance: entry, systemTemplate: tpl, trackerValues: entry.trackerValues || {} };

                      const res = executeRulesForTrigger('on-apply', ctx, systems);

                      cs[charId] = applyRuleDispatchToChar(cs[charId], entry.id || entry.systemId, res, systems);
                      const appliedInstanceForSave = (cs[charId].systemList || [])
                        .find(instance => (instance.id || instance.systemId) === (entry.id || entry.systemId))
                        || entry;
                      applySideEffects.notifications.push(...(res.notifications || []));
                      applySideEffects.statusMessages.push(...(res.statusMessages || []));
                      applySideEffects.logEntries.push(...(res.logEntries || []));
                      applySideEffects.pendingSaves.push(...(res.pendingSaves || []));
                      applySideEffects.pendingNpcUpdates.push(...(res.pendingNpcUpdates || []));
                      applyPendingSaveEntries.push(...buildSystemRulePendingSaveQueueEntries(res.pendingSaves, {
                        char: cs[charId],
                        systemInstance: appliedInstanceForSave,
                        systemTemplate: tpl,
                        trackerValues: appliedInstanceForSave.trackerValues || {},
                        worldContext: {
                          chars: cs,
                          journal: [...(journal || []), ...(dmJournal || [])],
                          currentLocation,
                          npcLibrary,
                        },
                        label: 'Batch system apply',
                        source: 'system.batch-apply',
                      }));
                      applyTravelLogEntries.push(...buildSystemRuleTravelLogEntries(res.logEntries, {
                        char: cs[charId],
                        action: 'system-rule-log',
                        source: 'system.batch-apply',
                      }));

                    }



                    // 2026-04-27: glow-on-apply, color matches pill (badgeColor) where set.

                    if (typeof addSystemGlowPatch === 'function') {

                      const badgeColorId = entry.badgeColor || tpl?.badgeColor || null;

                      const colorOpt = badgeColorId ? BUFF_COLOR_OPTIONS.find(o => o.id === badgeColorId) : null;

                      const glowColor = colorOpt?.glowColor || colorOpt?.color || '#ffd700';

                      cs[charId] = addSystemGlowPatch(cs[charId], entry.id, glowColor);

                    }



                    applied++;

                  });



                  if (applied > 0) {
                    const sideEffects = collectRuleResultSideEffects(applySideEffects, { npcLibrary });
                    if (sideEffects.npcLibraryChanged && typeof saveNpcLibrary === 'function') {
                      saveNpcLibrary(sideEffects.nextNpcLibrary);
                    }
                    const sentStatusMessages = persistSystemRuleStatusMessages(sideEffects.statusMessages, {
                      source: 'system.batch-apply',
                      messages,
                      onSendStatusMessage: handleSendStatusMessage,
                      onSaveMessages: saveMessages,
                      auditMeta: { category: 'systems', action: 'system.batch-rule-status' },
                    });
                    if (applyTravelLogEntries.length > 0 && typeof saveGroupTravelLog === 'function') {
                      const keep = Math.max(0, 200 - applyTravelLogEntries.length);
                      saveGroupTravelLog([...applyTravelLogEntries, ...(groupTravelLog || []).slice(0, keep)]);
                    }
                    const sideEffectNotices = Array.from(new Set(sideEffects.notifications));
                    if (applyPendingSaveEntries.length > 0) {
                      setDmSystemRuleSaveQueue(prev => [...prev, ...applyPendingSaveEntries]);
                      const pendingSaveNotice = buildSystemRulePendingSaveNotice(sideEffects.pendingSaves, 'Batch system apply');
                      sideEffectNotices.push(`${pendingSaveNotice || `Batch system apply requested ${applyPendingSaveEntries.length} saving throw${applyPendingSaveEntries.length === 1 ? '' : 's'}.`} Queued for resolution.`);
                    } else {
                      const pendingSaveNotice = buildSystemRulePendingSaveNotice(sideEffects.pendingSaves, 'Batch system apply');
                      if (pendingSaveNotice) sideEffectNotices.push(pendingSaveNotice);
                    }
                    if (applyTravelLogEntries.length > 0) {
                      sideEffectNotices.push(`${applyTravelLogEntries.length} system rule log entr${applyTravelLogEntries.length === 1 ? 'y' : 'ies'} recorded.`);
                    }
                    if (sentStatusMessages.length > 0) {
                      sideEffectNotices.push(`${sentStatusMessages.length} system status message${sentStatusMessages.length === 1 ? '' : 's'} sent.`);
                    }

                    const audit = { summary: `📋 Batch applied "${sys.name}" to ${applied} character(s)`, category: 'systems', action: 'system.batch-applied' };
                    if (!patchBatchSystemStateFromAuthority(authorityBeforeByCharId, cs, audit)) {
                      saveChars(cs, { meta: audit });
                    }

                    setDmNotification([
                      `Applied "${sys.name}" to ${applied} character(s)`,
                      ...sideEffectNotices,
                    ].join(' | '));

                  }

                  

                  setShowBatchApply(false);

                  setBatchApplySystem('');

                  setBatchApplyTargets(new Set());

                  setBatchApplySlotValuesByChar({});

                }}

              >

                Apply system to {getCampaignCharacterTargetModeCopy(effectiveBatchApplyTargetMode).ctaNoun} ({batchApplyTargets.size})

              </button>

            </div>

          </div>

        </div>

      )}

      

      {/* Batch Remove System Modal */}

      {showBatchRemove && (

        <div style={S.modal} onClick={() => setShowBatchRemove(false)}>

          <div style={{ ...S.modalContent, maxWidth: '600px' }} onClick={e => e.stopPropagation()}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>

              <h2 style={{ color: '#c92727', margin: 0 }}>🗑️ Batch Remove System</h2>

              <button style={{ ...S.btn, ...S.btnXs }} onClick={() => setShowBatchRemove(false)}>✕</button>

            </div>

            

            {(() => {

              // Get all systems currently applied to any character

              const appliedSystemIds = new Set();

              dmDashboardCharList.forEach(c => {

                (c.systemList || []).forEach(s => {

                  if (s.systemId) appliedSystemIds.add(s.systemId);

                  if (s.template?.id) appliedSystemIds.add(s.template.id);

                });

              });

              

              // Characters with selected system

              const charsWithSystem = batchRemoveSystem ? getTargetRowsForMode(batchRemoveTargetMode).filter(c =>

                (c.systemList || []).some(s => s.systemId === batchRemoveSystem || s.template?.id === batchRemoveSystem)

              ) : [];

              

              return (

                <>

                  <div style={{ marginBottom: '16px' }}>

                    <label style={S.label}>Select System to Remove</label>

                    <select

                      style={{ ...S.select, width: '100%' }}

                      value={batchRemoveSystem}

                      onChange={e => {

                        setBatchRemoveSystem(e.target.value);

                        // Auto-select all characters with this system

                        if (e.target.value) {

                          const withSystem = getTargetRowsForMode(batchRemoveTargetMode)

                            .filter(c => (c.systemList || []).some(s => s.systemId === e.target.value || s.template?.id === e.target.value))

                            .map(c => c.id);

                          setBatchRemoveTargets(new Set(withSystem));

                        } else {

                          setBatchRemoveTargets(new Set());

                        }

                      }}

                    >

                      <option value="">Choose a system...</option>

                      {Array.from(appliedSystemIds).map(id => {

                        const sys = systems[id];

                        const name = sys?.name || id;

                        const icon = sys ? (SYSTEM_TYPE_OPTIONS.find(t => t.value === (sys.type || 'custom'))?.icon || '🔮') : '🔮';

                        const count = dmDashboardCharList.filter(c =>

                          (c.systemList || []).some(s => s.systemId === id || s.template?.id === id)

                        ).length;

                        return (

                          <option key={id} value={id}>{icon} {name} ({count} character{count !== 1 ? 's' : ''})</option>

                        );

                      })}

                    </select>

                  </div>

                  {renderBatchTargetModeControl({
                    mode: batchRemoveTargetMode,
                    setMode: setBatchRemoveTargetMode,
                    setTargets: setBatchRemoveTargets,
                  })}

                  

                  {batchRemoveSystem && charsWithSystem.length > 0 && (

                    <div style={{ marginBottom: '16px' }}>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>

                        <label style={{ ...S.label, marginBottom: 0 }}>Select Characters ({batchRemoveTargets.size} selected)</label>

                        <div style={{ display: 'flex', gap: '6px' }}>

                          <button

                            style={{ ...S.btn, ...S.btnXs, marginBottom: 0 }}

                            onClick={() => setBatchRemoveTargets(new Set(charsWithSystem.map(c => c.id)))}

                          >

                            Select All {getCampaignCharacterTargetModeCopy(effectiveBatchRemoveTargetMode).ctaNoun}

                          </button>

                          <button

                            style={{ ...S.btn, ...S.btnXs, marginBottom: 0 }}

                            onClick={() => setBatchRemoveTargets(new Set())}

                          >

                            Clear

                          </button>

                        </div>

                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px', maxHeight: '200px', overflow: 'auto', padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>

                        {charsWithSystem.map(c => {

                          const isSelected = batchRemoveTargets.has(c.id);

                          return (

                            <label

                              key={c.id}

                              style={{

                                display: 'flex',

                                alignItems: 'center',

                                gap: '8px',

                                padding: '6px 8px',

                                background: isSelected ? 'rgba(201,39,39,0.2)' : 'rgba(0,0,0,0.1)',

                                borderRadius: '4px',

                                cursor: 'pointer',

                                border: isSelected ? '1px solid rgba(201,39,39,0.4)' : '1px solid transparent',

                              }}

                            >

                              <input

                                type="checkbox"

                                checked={isSelected}

                                onChange={e => {

                                  const newSet = new Set(batchRemoveTargets);

                                  if (e.target.checked) newSet.add(c.id);

                                  else newSet.delete(c.id);

                                  setBatchRemoveTargets(newSet);

                                }}

                              />

                              <span style={{ fontSize: '0.85rem' }}>
                                {c.name}
                                {isCampaignTestCharacter(c) && <span style={{ color: '#c9a227', marginLeft: '4px' }}>(Practice)</span>}
                              </span>

                            </label>

                          );

                        })}

                      </div>

                    </div>

                  )}

                  

                  {batchRemoveSystem && systems[batchRemoveSystem] && (

                    <div style={{ marginBottom: '16px', padding: '10px', background: 'rgba(201,39,39,0.1)', borderRadius: '6px', border: '1px solid rgba(201,39,39,0.3)' }}>

                      <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#ff6961' }}>

                        ⚠️ Warning: This will remove the system from selected characters

                      </div>

                      <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>

                        {systems[batchRemoveSystem].name} will be removed from {batchRemoveTargets.size} character(s).

                        This action cannot be undone.

                      </div>

                    </div>

                  )}

                  

                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>

                    <button style={{ ...S.btn, ...S.btnSubtle }} onClick={() => setShowBatchRemove(false)}>Cancel</button>

                    <button

                      style={{ ...S.btn, ...S.btnDanger }}

                      disabled={!batchRemoveSystem || batchRemoveTargets.size === 0}

                      onClick={() => {

                        const systemId = batchRemoveSystem;

                        const sysName = systems[systemId]?.name || systemId;

                        const cs = { ...chars };
                        const authorityBeforeByCharId = {};

                        let removed = 0;
                        const lifecycleSideEffects = { notifications: [], statusMessages: [], logEntries: [], pendingSaves: [], pendingNpcUpdates: [] };
                        const lifecycleTravelLogEntries = [];
                        const lifecyclePendingSaveEntries = [];

                        

                        batchRemoveTargets.forEach(charId => {

                          if (!cs[charId]) return;
                          authorityBeforeByCharId[charId] = cloneCharForSystemAuthority(cs[charId]);
                          const removedSourceInstance = (cs[charId].systemList || [])
                            .find(instance => instance.systemId === systemId || instance.template?.id === systemId || instance.id === systemId)
                            || null;
                          const removedSourceTemplate = removedSourceInstance
                            ? getSystemTemplateForInstance(removedSourceInstance, systems)
                            : systems[systemId] || null;

                          const lifecycle = removeSystemsWithLifecycle({
                            char: cloneCharForSystemEdit(cs[charId]),
                            systemKeys: [systemId],
                            systems,
                            worldContext: {
                              chars: cs,
                              journal: [...(journal || []), ...(dmJournal || [])],
                              currentLocation,
                              npcLibrary,
                            },
                            archiveDetails: 'Batch removed by DM',
                          });

                          if (lifecycle.removedEntries.length > 0) {

                            cs[charId] = lifecycle.nextChar;

                            removed++;
                            lifecycleSideEffects.notifications.push(...(lifecycle.notifications || []));
                            lifecycleSideEffects.statusMessages.push(...(lifecycle.statusMessages || []));
                            lifecycleSideEffects.logEntries.push(...(lifecycle.logEntries || []));
                            lifecycleSideEffects.pendingSaves.push(...(lifecycle.pendingSaves || []));
                            lifecycleSideEffects.pendingNpcUpdates.push(...(lifecycle.pendingNpcUpdates || []));
                            lifecyclePendingSaveEntries.push(...buildSystemRulePendingSaveQueueEntries(lifecycle.pendingSaves, {
                              char: lifecycle.nextChar,
                              systemInstance: removedSourceInstance,
                              systemTemplate: removedSourceTemplate,
                              trackerValues: removedSourceInstance?.trackerValues || {},
                              worldContext: {
                                chars: cs,
                                journal: [...(journal || []), ...(dmJournal || [])],
                                currentLocation,
                                npcLibrary,
                              },
                              label: 'Batch system removal',
                              source: 'system.batch-remove',
                            }));
                            lifecycleTravelLogEntries.push(...buildSystemRuleTravelLogEntries(lifecycle.logEntries, {
                              char: lifecycle.nextChar,
                              action: 'system-rule-log',
                              source: 'system.batch-remove',
                            }));

                          }
                          else {
                            delete authorityBeforeByCharId[charId];
                          }

                        });

                        

                        if (removed > 0) {

                          const sideEffects = collectRuleResultSideEffects(lifecycleSideEffects, { npcLibrary });
                          if (sideEffects.npcLibraryChanged && typeof saveNpcLibrary === 'function') {
                            saveNpcLibrary(sideEffects.nextNpcLibrary);
                          }
                          const sentStatusMessages = persistSystemRuleStatusMessages(sideEffects.statusMessages, {
                            source: 'system.batch-remove',
                            messages,
                            onSendStatusMessage: handleSendStatusMessage,
                            onSaveMessages: saveMessages,
                            auditMeta: { category: 'systems', action: 'system.batch-remove-rule-status' },
                          });
                          if (lifecycleTravelLogEntries.length > 0 && typeof saveGroupTravelLog === 'function') {
                            const keep = Math.max(0, 200 - lifecycleTravelLogEntries.length);
                            saveGroupTravelLog([...lifecycleTravelLogEntries, ...(groupTravelLog || []).slice(0, keep)]);
                          }
                          const sideEffectNotices = Array.from(new Set(sideEffects.notifications));
                          if (lifecyclePendingSaveEntries.length > 0) {
                            setDmSystemRuleSaveQueue(prev => [...prev, ...lifecyclePendingSaveEntries]);
                            const pendingSaveNotice = buildSystemRulePendingSaveNotice(sideEffects.pendingSaves, 'Batch system removal');
                            sideEffectNotices.push(`${pendingSaveNotice || `Batch system removal requested ${lifecyclePendingSaveEntries.length} saving throw${lifecyclePendingSaveEntries.length === 1 ? '' : 's'}.`} Queued for resolution.`);
                          } else {
                            const pendingSaveNotice = buildSystemRulePendingSaveNotice(sideEffects.pendingSaves, 'Batch system removal');
                            if (pendingSaveNotice) sideEffectNotices.push(pendingSaveNotice);
                          }
                          if (lifecycleTravelLogEntries.length > 0) {
                            sideEffectNotices.push(`${lifecycleTravelLogEntries.length} system rule log entr${lifecycleTravelLogEntries.length === 1 ? 'y' : 'ies'} recorded.`);
                          }
                          if (sentStatusMessages.length > 0) {
                            sideEffectNotices.push(`${sentStatusMessages.length} system status message${sentStatusMessages.length === 1 ? '' : 's'} sent.`);
                          }

                          const audit = { summary: `🗑️ Batch removed "${sysName}" from ${removed} character(s)`, category: 'systems', action: 'system.batch-removed' };
                          if (!patchBatchSystemStateFromAuthority(authorityBeforeByCharId, cs, audit)) {
                            saveChars(cs, { meta: audit });
                          }

                          setDmNotification([
                            `Removed "${sysName}" from ${removed} character(s)`,
                            ...sideEffectNotices,
                          ].join(' | '));

                        }

                        

                        setShowBatchRemove(false);

                        setBatchRemoveSystem('');

                        setBatchRemoveTargets(new Set());

                      }}

                    >

                      Remove from {getCampaignCharacterTargetModeCopy(effectiveBatchRemoveTargetMode).ctaNoun} ({batchRemoveTargets.size})

                    </button>

                  </div>

                </>

              );

            })()}

          </div>

        </div>

      )}

      

      {/* Batch Buff/Debuff Modal */}

      {showBatchBuff && (

        <div style={S.modal} onClick={() => setShowBatchBuff(false)}>

          <div style={{ ...S.modalContent, maxWidth: '600px' }} onClick={e => e.stopPropagation()}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>

              <h2 style={{ color: batchBuffIsDebuff ? '#c92727' : '#27c95a', margin: 0 }}>

                {batchBuffIsDebuff ? '💀 Batch Apply Debuff' : '✨ Batch Apply Buff'}

              </h2>

              <button style={{ ...S.btn, ...S.btnXs }} onClick={() => setShowBatchBuff(false)}>✕</button>

            </div>

            

            {/* Buff/Debuff Toggle */}

            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>

              <button 

                style={{ ...S.btn, flex: 1, ...(batchBuffIsDebuff ? {} : { background: 'rgba(39,201,90,0.3)', borderColor: '#27c95a' }) }} 

                onClick={() => {

                  setBatchBuffIsDebuff(false);

                  if (batchBuffColor === 'default-debuff') setBatchBuffColor('default-buff');

                }}

              >

                ✨ Buff

              </button>

              <button 

                style={{ ...S.btn, flex: 1, ...(batchBuffIsDebuff ? { background: 'rgba(201,39,39,0.3)', borderColor: '#c92727' } : {}) }} 

                onClick={() => {

                  setBatchBuffIsDebuff(true);

                  if (batchBuffColor === 'default-buff') setBatchBuffColor('default-debuff');

                }}

              >

                💀 Debuff

              </button>

            </div>

            

            {/* Effect Name */}

            <div style={{ marginBottom: '16px' }}>

              <label style={S.label}>Effect Name</label>

              <input

                style={{ ...S.input, marginBottom: 0 }}

                value={batchBuffText}

                onChange={e => setBatchBuffText(e.target.value)}

                placeholder={batchBuffIsDebuff ? "-2 Str, Cursed, Poisoned..." : "+2 Str, Blessed, Inspired..."}

              />

            </div>

            

            {/* Quick Presets */}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '16px' }}>

              {EFFECT_TYPE_PRESETS.slice(0, 9).map(preset => {

                const colorOpt = BUFF_COLOR_OPTIONS.find(c => c.id === preset.color) || BUFF_COLOR_OPTIONS[0];

                return (

                  <button

                    key={preset.id}

                    style={{

                      ...S.btn,

                      ...S.btnXs,

                      padding: '4px 8px',

                      fontSize: '0.75rem',

                      background: colorOpt.bgColor,

                      borderColor: colorOpt.color,

                      color: colorOpt.textColor || colorOpt.color,

                    }}

                    onClick={() => {

                      setBatchBuffColor(preset.color);

                      setBatchBuffIsDebuff(preset.id === 'debuff' || preset.id === 'curse' || preset.id === 'geas');

                      if (!batchBuffText.trim()) {

                        setBatchBuffText(preset.icon + ' ');

                      } else if (!batchBuffText.startsWith(preset.icon)) {

                        const cleaned = batchBuffText.replace(/^[^\w\s]+\s*/, '').trim();

                        setBatchBuffText(preset.icon + ' ' + cleaned);

                      }

                    }}

                  >

                    {preset.svgIcon === 'biohazard' ? <BiohazardIcon size={13} color={colorOpt?.textColor || colorOpt?.color || 'currentColor'} /> : preset.svgIcon === 'plague-doctor' ? <PlagueDoctorIcon size={13} color={colorOpt?.textColor || colorOpt?.color || 'currentColor'} /> : preset.icon} {preset.name}

                  </button>

                );

              })}

            </div>

            

            {/* Options Row */}

            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>

              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.85rem', color: batchBuffIsPermanent ? '#ffd700' : '#888' }}>

                <input type="checkbox" checked={batchBuffIsPermanent} onChange={e => setBatchBuffIsPermanent(e.target.checked)} />

                ∞ Permanent

              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.85rem', color: batchBuffGlow ? '#ffd700' : '#888' }}>

                <input type="checkbox" checked={batchBuffGlow} onChange={e => setBatchBuffGlow(e.target.checked)} />

                ✨ Glow Effect

              </label>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

                <label style={{ fontSize: '0.85rem', color: '#888' }}>Color:</label>

                <select

                  style={{ ...S.select, marginBottom: 0, padding: '4px', fontSize: '0.8rem' }}

                  value={batchBuffColor}

                  onChange={e => setBatchBuffColor(e.target.value)}

                >

                  {BUFF_COLOR_OPTIONS.map(opt => (

                    <option key={opt.id} value={opt.id}>{opt.name}</option>

                  ))}

                </select>

              </div>

            </div>

            

            {/* Optional tooltips */}

            <details style={{ marginBottom: '16px' }}>

              <summary style={{ cursor: 'pointer', color: '#888', fontSize: '0.85rem' }}>⚙️ Advanced Options</summary>

              <div style={{ padding: '12px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', marginTop: '8px' }}>

                <div style={{ marginBottom: '8px' }}>

                  <label style={{ ...S.label, fontSize: '0.8rem', color: '#4ade80' }}>👁 Player Tooltip (always visible)</label>

                  <input

                    style={{ ...S.input, marginBottom: 0, fontSize: '0.85rem' }}

                    value={batchBuffPlayerTooltip}

                    onChange={e => setBatchBuffPlayerTooltip(e.target.value)}

                    placeholder="Granted by the Forest Spirit"

                  />

                </div>

                <div style={{ marginBottom: '8px' }}>

                  <label style={{ ...S.label, fontSize: '0.8rem', color: '#c9a227' }}>🔒 DM Note</label>

                  <textarea

                    style={{ ...S.input, marginBottom: 0, fontSize: '0.85rem', minHeight: '60px' }}

                    value={batchBuffSource}

                    onChange={e => setBatchBuffSource(e.target.value)}

                    placeholder="Secret: Will betray them at the bridge..."

                  />

                </div>

                {batchBuffSource.trim() && (

                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.8rem', color: '#888' }}>

                    <input type="checkbox" checked={batchBuffNoteVisible} onChange={e => setBatchBuffNoteVisible(e.target.checked)} />

                    Also show DM note to players

                  </label>

                )}

              </div>

            </details>

            

            {renderBatchTargetModeControl({
              mode: batchBuffTargetMode,
              setMode: setBatchBuffTargetMode,
              setTargets: setBatchBuffTargets,
            })}

            {/* Character Selection */}

            <div style={{ marginBottom: '16px' }}>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>

                <label style={{ ...S.label, marginBottom: 0 }}>Select Characters ({batchBuffTargets.size} selected)</label>

                <div style={{ display: 'flex', gap: '6px' }}>

                  <button

                    style={{ ...S.btn, ...S.btnXs, marginBottom: 0 }}

                    onClick={() => setBatchBuffTargets(new Set(getCampaignCharacterTargetIdsForMode(
                      chars,
                      effectiveBatchBuffTargetMode,
                      { campaignId: activeViewerCampaignId },
                    )))}

                  >

                    Select All {getCampaignCharacterTargetModeCopy(effectiveBatchBuffTargetMode).ctaNoun}

                  </button>

                  <button

                    style={{ ...S.btn, ...S.btnXs, marginBottom: 0 }}

                    onClick={() => setBatchBuffTargets(new Set())}

                  >

                    Clear

                  </button>

                </div>

              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px', maxHeight: '200px', overflow: 'auto', padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>

                {batchBuffTargetRows.map(c => {

                  const isSelected = batchBuffTargets.has(c.id);

                  return (

                    <div

                      key={c.id}

                      style={{

                        padding: '8px',

                        background: isSelected ? 'rgba(39,201,90,0.2)' : 'rgba(0,0,0,0.2)',

                        border: `1px solid ${isSelected ? '#27c95a' : 'rgba(255,255,255,0.1)'}`,

                        borderRadius: '6px',

                        cursor: 'pointer',

                        fontSize: '0.85rem',

                      }}

                      onClick={() => {

                        const newSet = new Set(batchBuffTargets);

                        if (isSelected) newSet.delete(c.id);

                        else newSet.add(c.id);

                        setBatchBuffTargets(newSet);

                      }}

                    >

                      <div style={{ fontWeight: 'bold' }}>
                        {c.name}
                        {isCampaignTestCharacter(c) && <span style={{ color: '#c9a227', marginLeft: '4px' }}>(Practice)</span>}
                      </div>

                      <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>{c.race}</div>

                    </div>

                  );

                })}

              </div>

            </div>

            

            {/* Apply Button */}

            <button

              style={{ 

                ...S.btn, 

                ...(batchBuffIsDebuff ? S.btnDanger : S.btnSuccess), 

                width: '100%', 

                padding: '12px',

                fontSize: '1rem',

              }}

              disabled={!batchBuffText.trim() || batchBuffTargets.size === 0}

              onClick={() => {

                const text = batchBuffText.trim();

                if (!text || batchBuffTargets.size === 0) return;

                

                const cs = { ...chars };

                let applied = 0;

                

                batchBuffTargets.forEach(charId => {

                  const c = cs[charId];

                  if (!c) return;

                  let nextChar = addLegacyEffect(c, {

                    text,

                    kind: batchBuffIsDebuff ? 'debuff' : 'buff',

                    permanent: batchBuffIsPermanent,

                    metadata: buildLegacyEffectMetadata({

                      source: batchBuffSource.trim() || null,

                      playerTooltip: batchBuffPlayerTooltip.trim() || null,

                      colorId: batchBuffColor,

                      addedBy: 'DM',

                      noteVisible: batchBuffNoteVisible,

                    }),

                  });



                  // 2026-04-27: fold glow into the same write (was separate

                  // addBuffGlow that raced with the bulk save).

                  if (batchBuffGlow && addBuffGlowPatch) {

                    const colorOpt = BUFF_COLOR_OPTIONS.find(co => co.id === batchBuffColor);

                    nextChar = addBuffGlowPatch(nextChar, text, colorOpt?.glowColor || colorOpt?.color || (batchBuffIsDebuff ? '#ff6961' : '#27c95a'));

                  }

                  cs[charId] = nextChar;



                  applied++;

                });

                

                if (applied > 0) {

                  const effectType = batchBuffIsDebuff ? 'Debuff' : 'Buff';

                  const permText = batchBuffIsPermanent ? 'Permanent ' : '';

                  saveChars(cs, { meta: { summary: `${batchBuffIsDebuff ? '💀' : '✨'} Batch applied ${permText}${effectType} "${text}" to ${applied} character(s)`, category: 'buffs', action: batchBuffIsDebuff ? 'debuff.batch-added' : 'buff.batch-added' } });

                  setDmNotification(`Applied "${text}" to ${applied} character(s)`);

                }

                

                // Reset and close

                setShowBatchBuff(false);

                setBatchBuffText('');

                setBatchBuffTargets(new Set());

                setBatchBuffPlayerTooltip('');

                setBatchBuffSource('');

                setBatchBuffNoteVisible(false);

              }}

            >

              Apply {batchBuffIsDebuff ? 'debuff' : 'buff'} to {getCampaignCharacterTargetModeCopy(effectiveBatchBuffTargetMode).ctaNoun} ({batchBuffTargets.size})

            </button>

          </div>

        </div>

      )}

      

      {/* Whisper Composer Modal */}

      <WhisperComposerModal 

        isOpen={showWhisperComposer}

        onClose={() => setShowWhisperComposer(false)}

        chars={dmDashboardMessageChars}

        messages={messages}

        onSaveMessages={saveMessages}

        onSendStatusMessage={handleSendStatusMessage}

      />

      

      <DmNotificationModal />

      {activeDmSystemRulePendingSave && activeDmSystemRuleSaveChar && (
        <RequireSaveModal
          key={`${activeDmSystemRuleSaveEntry?.id || 'dm-system-save'}:${dmSystemRuleSaveQueue.length}`}
          pendingSave={activeDmSystemRulePendingSave}
          char={activeDmSystemRuleSaveChar}
          saveContext={activeDmSystemRuleSaveContext}
          saveAdvantage={activeDmSystemRuleSaveAdvantage}
          onResolve={handleDmSystemRuleSaveResolve}
          onSkip={handleDmSystemRuleSaveSkip}
        />
      )}

      {combatEnabled && <FloatingCombatIndicator combat={displayedCombat} onClick={() => setView('combat')} />}

      {floatingChrome}

      

      </div>

    </div>;

  }



  return <div style={S.container}><h1 style={S.title}>⚔️ Survival Tracker ⚔️</h1><button style={S.btn} onClick={() => setView('select')}>Back</button></div>;

}



function CreateForm({ onCreate, onSuccess }) {

  const [name, setName] = useState(''), [race, setRace] = useState(''), [hp, setHp] = useState(''), [pw, setPw] = useState('');

  return <div><label style={S.label}>Name *</label><input style={S.input} value={name} onChange={e => setName(e.target.value)} placeholder="Name" /><label style={S.label}>Race *</label><input style={S.input} value={race} onChange={e => setRace(e.target.value)} placeholder="Human, Elf..." /><label style={S.label}>Max HP *</label><input style={S.input} type="number" value={hp} onChange={e => setHp(e.target.value)} placeholder="25" /><label style={S.label}>Password *</label><input style={S.input} type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="Password" /><button style={{ ...S.btn, ...S.btnSuccess }} onClick={() => { if (!name || !race || !hp || !pw) return window.alert('Fill all fields'); onSuccess(onCreate(name, race, hp, pw)); }}>✨ Create</button></div>;

}



function FoodMgr({ food, foodTypes, onAdd, onRemove, showLog }) {

  const [amt, setAmt] = useState(1), [notes, setNotes] = useState(''), [type, setType] = useState('');

  const incrementAmt = () => setAmt(prev => Math.min(99, (parseInt(prev) || 0) + 1));

  const decrementAmt = () => setAmt(prev => Math.max(1, (parseInt(prev) || 1) - 1));

  return <div>

    <div style={S.flex}>

      <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>

        <button style={{ ...S.btn, ...S.btnXs, padding: '4px 8px', marginBottom: 0 }} onClick={decrementAmt}>-</button>

        <input style={{ ...S.input, width: '64px', marginBottom: 0, textAlign: 'center' }} type="number" min="1" max="999" value={amt} onChange={e => setAmt(e.target.value)} />

        <button style={{ ...S.btn, ...S.btnXs, padding: '4px 8px', marginBottom: 0 }} onClick={incrementAmt}>+</button>

      </div>

      <select style={{ ...S.select, flex: '0 0 110px', marginBottom: 0 }} value={type} onChange={e => setType(e.target.value)}><option value="">Type...</option>{foodTypes.map(t => <option key={t} value={t}>{t}</option>)}</select>

      <input style={{ ...S.input, flex: 1, marginBottom: 0 }} placeholder="Notes (catfish, from river...)" value={notes} onChange={e => setNotes(e.target.value)} />

    </div>

    <div style={{ ...S.flex, marginTop: '8px', gap: '8px' }}>

      <button style={{ ...S.btn, ...S.btnSuccess, ...S.btnSm, flex: 1 }} onClick={() => { const qty = parseInt(amt) || 1; if (type) { onAdd(qty, notes, type); setAmt(1); setNotes(''); setType(''); } else { window.alert('Select a food type'); } }}>➕ Add {amt || 1}</button>

      <button style={{ ...S.btn, ...S.btnDanger, ...S.btnSm, flex: 1 }} onClick={() => { const qty = parseInt(amt) || 1; if (type) { onRemove(qty, notes, type); setAmt(1); setNotes(''); setType(''); } else { window.alert('Select a food type'); } }}>➖ Remove {amt || 1}</button>

    </div>

    {showLog && (food.foodLog || []).length > 0 && <div style={{ marginTop: '12px', maxHeight: '120px', overflowY: 'auto' }}>{(food.foodLog || []).slice(0, 10).map((l, i) => <div key={i} style={{ padding: '4px 0', fontSize: '0.8rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}><span style={{ color: l.unitsAdded ? '#27c95a' : '#c92727' }}>{l.unitsAdded ? `+${l.unitsAdded}` : `-${l.unitsConsumed}`}</span> {l.foodType}{l.notes && <span style={{ color: '#c9a227' }}> ({l.notes})</span>}{l.character && <span style={{ opacity: 0.7 }}> - {l.character}</span>}</div>)}</div>}

  </div>;

}



const DashboardInlineResourceControl = ({
  value,
  max,
  temp = 0,
  onCommit,
  label,
  kind = 'hp',
  height = 16,
  fontSize = '0.7rem',
  buttonWidth = 30,
  buttonHeight = 22,
  gap = 10,
  onStopPropagation = null,
}) => {
  const currentValue = Number.isFinite(Number(value)) ? Number(value) : 0;
  const maxValue = Math.max(0, Number.isFinite(Number(max)) ? Number(max) : 0);
  const tempValue = Math.max(0, Number.isFinite(Number(temp)) ? Number(temp) : 0);
  const visualMax = Math.max(maxValue || 1, currentValue + tempValue);
  const currentPercent = Math.max(0, Math.min(100, (currentValue / visualMax) * 100));
  const tempPercent = Math.max(0, Math.min(100 - currentPercent, (tempValue / visualMax) * 100));
  const isHp = kind === 'hp';
  const decrementDisabled = currentValue <= 0;
  const incrementDisabled = currentValue >= maxValue;
  const stopProps = onStopPropagation
    ? { onClick: onStopPropagation, onMouseDown: onStopPropagation }
    : {};

  const commitDelta = (delta) => {
    const nextValue = Math.max(0, Math.min(maxValue, currentValue + delta));
    if (nextValue !== currentValue) onCommit(nextValue);
  };

  const buttonBaseStyle = {
    ...S.btn,
    ...S.btnXs,
    width: `${buttonWidth}px`,
    minWidth: `${buttonWidth}px`,
    height: `${buttonHeight}px`,
    padding: 0,
    marginBottom: 0,
    lineHeight: `${buttonHeight - 2}px`,
    fontSize,
    flex: '0 0 auto',
  };

  const manaButtonStyle = {
    background: 'rgba(74,144,217,0.3)',
    borderColor: '#4a90d9',
    color: '#e8f3ff',
  };

  return (
    <div
      {...stopProps}
      style={{
        display: 'grid',
        gridTemplateColumns: `${buttonWidth}px minmax(0, 1fr) ${buttonWidth}px`,
        alignItems: 'center',
        gap: `${gap}px`,
        marginBottom: '4px',
      }}
    >
      <button
        type="button"
        aria-label={`Decrease ${label}`}
        disabled={decrementDisabled}
        onClick={() => commitDelta(-1)}
        style={{
          ...buttonBaseStyle,
          ...(isHp ? S.btnDanger : manaButtonStyle),
        }}
      >
        -
      </button>
      <div
        style={{
          ...S.hpBar,
          height: `${height}px`,
          position: 'relative',
          marginBottom: 0,
          background: isHp ? S.hpBar.background : 'rgba(74,144,217,0.2)',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${currentPercent}%`,
            background: isHp ? getHpColor(currentValue, maxValue) : 'linear-gradient(90deg, #4a90d9, #7db5ff)',
            position: 'absolute',
            left: 0,
            borderRadius: isHp && tempValue > 0 ? '14px 0 0 14px' : '14px',
          }}
        />
        {isHp && tempValue > 0 && (
          <div
            style={{
              height: '100%',
              width: `${tempPercent}%`,
              position: 'absolute',
              left: `${currentPercent}%`,
              background: 'repeating-linear-gradient(45deg, rgba(125,181,255,0.45), rgba(125,181,255,0.45) 2px, rgba(170,210,255,0.25) 2px, rgba(170,210,255,0.25) 5px)',
              borderRight: '2px solid rgba(125,181,255,0.6)',
            }}
          />
        )}
        <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize, fontWeight: 'bold', zIndex: 1 }}>
          {currentValue}{isHp && tempValue > 0 && <span style={{ color: '#87ceeb' }}>+{tempValue}</span>}/{maxValue}
        </span>
      </div>
      <button
        type="button"
        aria-label={`Increase ${label}`}
        disabled={incrementDisabled}
        onClick={() => commitDelta(1)}
        style={{
          ...buttonBaseStyle,
          ...(isHp ? S.btnSuccess : manaButtonStyle),
        }}
      >
        +
      </button>
    </div>
  );
};

const normalizeVitalNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
};

const tryPatchCharacterVitals = async ({
  patchCharacterVitals,
  charId,
  updates,
  expected,
  surface,
  onError,
}) => {
  if (typeof patchCharacterVitals !== 'function') return false;
  try {
    const result = await patchCharacterVitals({ charId, updates, expected, surface });
    return result !== null;
  } catch (error) {
    console.error('Character vitals authority update failed:', error);
    if (typeof onError === 'function') onError(error);
    return true;
  }
};

export function DMCard({ c, diseases, systems, systemsEnabled = true, manaEnabled = true, hungerEnabled = true, diseasesEnabled = true, foodSettingsEnabled = true, diets, foodTypes, locations, currentLocation, inv, chars, saveChars, patchCharacterVitals, patchCharacterSystemInstance, patchCharacterSystemState, patchCharacterSystemStateAndVitals, patchCharacterSystemStateAndResources, patchCharacterSystemStateAndEffects, food, saveFood, groupTravelLog, saveGroupTravelLog, onViewTravel, onViewHome, onNotify, messages, onSaveMessages, onSendStatusMessage, onEditSystem, glowingBuffs, addBuffGlow, addBuffGlowPatch, addSystemGlowPatch, clearBuffGlow, addSystemGlow, globalSettings, journal = [], dmJournal = [], npcLibrary = {}, saveNpcLibrary = null }) {

  const [showFx, setShowFx] = useState(false), [showDis, setShowDis] = useState(false), [showSys, setShowSys] = useState(false), [showFeed, setShowFeed] = useState(false);

  const [buff, setBuff] = useState(''), [debuff, setDebuff] = useState(''), [hpIn, setHpIn] = useState('');

  const [buffSource, setBuffSource] = useState(''), [buffPlayerTooltip, setBuffPlayerTooltip] = useState(''), [buffColor, setBuffColor] = useState('default-buff'), [buffNotifyGlow, setBuffNotifyGlow] = useState(true), [buffNoteVisible, setBuffNoteVisible] = useState(false);

  const [buffIsPermanent, setBuffIsPermanent] = useState(false);

  const [debuffSource, setDebuffSource] = useState(''), [debuffPlayerTooltip, setDebuffPlayerTooltip] = useState(''), [debuffColor, setDebuffColor] = useState('default-debuff'), [debuffNotifyGlow, setDebuffNotifyGlow] = useState(true), [debuffNoteVisible, setDebuffNoteVisible] = useState(false);

  const [debuffIsPermanent, setDebuffIsPermanent] = useState(false);

  const [feedType, setFeedType] = useState(''), [feedCount, setFeedCount] = useState('1'), [feedWithdraw, setFeedWithdraw] = useState(true);

  const [showXpAward, setShowXpAward] = useState(false), [xpAwardDraft, setXpAwardDraft] = useState(''), [xpAwardAll, setXpAwardAll] = useState(false);

  // Default disease filter to current location

  const [diseaseFilter, setDiseaseFilter] = useState(currentLocation);

  const [systemFilter, setSystemFilter] = useState(currentLocation);

  const set = syncSpellSettings(c.settings || DEFAULT_SETTINGS), wellFed = hungerEnabled && c.mealsToday >= 2 && c.daysWithoutFood === 0, hungry = hungerEnabled && c.daysWithoutFood >= set.hungerThreshold && !c.starving, starving = hungerEnabled && c.daysToStarvation === 0;

  const showMana = manaEnabled && isManaSpellSystem(set) && (c.maxMana || 0) > 0;

  const systemsForCard = systemsEnabled ? (systems || {}) : {};

  const systemListForCard = systemsEnabled ? (c.systemList || []) : [];

  const [systemRuleSaveQueue, setSystemRuleSaveQueue] = useState([]);

  const activeSystemRuleSaveEntry = systemRuleSaveQueue[0] || null;

  const activeSystemRulePendingSave = activeSystemRuleSaveEntry?.pendingSave || null;

  const activeSystemRuleSaveChar = activeSystemRuleSaveEntry?.context?.char || c;

  const systemRuleSaveAbility = (activeSystemRulePendingSave?.ability || 'wis').toLowerCase();

  const activeSystemRuleEffectContext = activeSystemRulePendingSave
    ? getResolvedCharacterEffectContext(activeSystemRuleSaveChar, {}, systemsForCard, true)
    : null;

  const activeSystemRuleSaveContext = activeSystemRulePendingSave
    ? getResolvedSaveContext({
        stat: systemRuleSaveAbility,
        char: activeSystemRuleSaveChar,
        effectiveStats: activeSystemRuleEffectContext?.effectiveStats,
        equippedCombatBonuses: activeSystemRuleEffectContext?.equippedCombatBonuses,
        activeLegacyEffects: activeSystemRuleEffectContext?.activeLegacyEffects,
        activeRuntimeEffects: Array.isArray(activeSystemRuleSaveChar?.activeEffects) ? activeSystemRuleSaveChar.activeEffects : [],
      })
    : null;

  const activeSystemRuleSaveAdvantage = activeSystemRulePendingSave
    ? getResolvedCharacterAdvantageContext(
        activeSystemRuleSaveChar,
        { ...getSaveAdvantageConfig(systemRuleSaveAbility) },
        {},
        systemsForCard,
        true,
      )
    : null;



  // Update filter when location changes

  useEffect(() => { setDiseaseFilter(currentLocation); setSystemFilter(currentLocation); }, [currentLocation]);



  // Filter diseases by location

  const filteredDiseases = diseasesEnabled ? Object.entries(diseases).filter(([id, d]) => {

    if (diseaseFilter === 'all') return true;

    return (d.locations || []).includes(diseaseFilter);

  }) : [];



  // Filter systems by location

  const filteredSystems = Object.entries(systemsForCard || {}).filter(([id, s]) => {

    if (systemFilter === 'all') return true;

    const locs = s.locations || [];

    if (locs.length === 0) return true;

    return locs.includes(systemFilter);

  });

  const tryPatchSystemStateFromAuthority = (beforeChar, nextChar, audit) => {
    const payload = buildSystemStateAuthorityPayload(beforeChar, nextChar);
    if (payload && typeof patchCharacterSystemState === 'function') {
      patchCharacterSystemState({
        charId: c.id,
        ...payload,
        audit,
      }).catch(err => {
        console.error('Failed to patch character system state:', err);
        if (typeof onNotify === 'function') {
          onNotify(`System update failed: ${err?.message || 'unknown error'}`);
        }
      });
      return true;
    }
    const systemVitalsPayload = buildSystemStateAndVitalsAuthorityPayload(beforeChar, nextChar);
    if (systemVitalsPayload && typeof patchCharacterSystemStateAndVitals === 'function') {
      patchCharacterSystemStateAndVitals({
        charId: c.id,
        ...systemVitalsPayload,
        audit,
      }).catch(err => {
        console.error('Failed to patch character system state and vitals:', err);
        if (typeof onNotify === 'function') {
          onNotify(`System update failed: ${err?.message || 'unknown error'}`);
        }
      });
      return true;
    }
    const systemResourcesPayload = buildSystemStateAndResourcesAuthorityPayload(beforeChar, nextChar);
    if (systemResourcesPayload && typeof patchCharacterSystemStateAndResources === 'function') {
      patchCharacterSystemStateAndResources({
        charId: c.id,
        ...systemResourcesPayload,
        audit,
      }).catch(err => {
        console.error('Failed to patch character system state and resources:', err);
        if (typeof onNotify === 'function') {
          onNotify(`System update failed: ${err?.message || 'unknown error'}`);
        }
      });
      return true;
    }
    const systemEffectsPayload = buildSystemStateAndEffectsAuthorityPayload(beforeChar, nextChar);
    if (systemEffectsPayload && typeof patchCharacterSystemStateAndEffects === 'function') {
      patchCharacterSystemStateAndEffects({
        charId: c.id,
        ...systemEffectsPayload,
        audit,
      }).catch(err => {
        console.error('Failed to patch character system state and effects:', err);
        if (typeof onNotify === 'function') {
          onNotify(`System update failed: ${err?.message || 'unknown error'}`);
        }
      });
      return true;
    }
    return false;
  };

  const enqueueSystemRulePendingSaves = (pendingSaves = [], {
    char,
    systemInstance = null,
    systemTemplate = null,
    trackerValues = null,
    triggerContext = null,
    worldContext = null,
    label = 'System rule',
    source = 'systems',
  } = {}) => {
    const entries = buildSystemRulePendingSaveQueueEntries(pendingSaves, {
      char,
      systemInstance,
      systemTemplate,
      trackerValues,
      triggerContext,
      worldContext,
      label,
      source,
    });
    if (entries.length > 0) {
      setSystemRuleSaveQueue(prev => [...prev, ...entries]);
    }
    return entries;
  };

  const consumeSystemRuleSideEffects = (results, {
    char,
    label = 'System rule',
    source = 'systems',
    systemInstance = null,
    systemTemplate = null,
    trackerValues = null,
    triggerContext = null,
    worldContext = null,
    queuePendingSaves = true,
    suppressPendingSaveNotice = false,
  } = {}) => {
    const sideEffects = collectRuleResultSideEffects(results, { npcLibrary });
    if (sideEffects.npcLibraryChanged && typeof saveNpcLibrary === 'function') {
      saveNpcLibrary(sideEffects.nextNpcLibrary);
    }
    const sentStatusMessages = persistSystemRuleStatusMessages(sideEffects.statusMessages, {
      char,
      source,
      messages,
      onSendStatusMessage,
      onSaveMessages,
      auditMeta: { category: 'systems', action: 'system.rule-status' },
    });

    const ruleLogEntries = buildSystemRuleTravelLogEntries(sideEffects.logEntries, {
      char,
      action: 'system-rule-log',
      source,
    });
    const notices = Array.from(new Set(sideEffects.notifications || []));
    const queuedPendingSaves = queuePendingSaves
      ? enqueueSystemRulePendingSaves(sideEffects.pendingSaves, {
          char,
          systemInstance,
          systemTemplate,
          trackerValues,
          triggerContext,
          worldContext,
          label,
          source,
        })
      : [];
    if (queuedPendingSaves.length > 0) {
      const pendingSaveNotice = buildSystemRulePendingSaveNotice(sideEffects.pendingSaves, label);
      notices.push(`${pendingSaveNotice || `${label} requested ${queuedPendingSaves.length} saving throw${queuedPendingSaves.length === 1 ? '' : 's'}.`} Queued for resolution.`);
    } else if (!suppressPendingSaveNotice) {
      const pendingSaveNotice = buildSystemRulePendingSaveNotice(sideEffects.pendingSaves, label);
      if (pendingSaveNotice) notices.push(pendingSaveNotice);
    }

    if (ruleLogEntries.length > 0 && typeof saveGroupTravelLog === 'function') {
      const keep = Math.max(0, 200 - ruleLogEntries.length);
      saveGroupTravelLog([...ruleLogEntries, ...(groupTravelLog || []).slice(0, keep)]);
      notices.push(`${ruleLogEntries.length} system rule log entr${ruleLogEntries.length === 1 ? 'y' : 'ies'} recorded.`);
    }
    if (sentStatusMessages.length > 0) {
      notices.push(`${sentStatusMessages.length} system status message${sentStatusMessages.length === 1 ? '' : 's'} sent.`);
    }

    if (notices.length > 0 && typeof onNotify === 'function') {
      onNotify(notices.join(' | '));
    }
  };

  const handleSystemRuleSaveResolve = (outcome) => {
    if (!activeSystemRuleSaveEntry) return;
    const [head, ...rest] = systemRuleSaveQueue;
    const context = head.context || {};
    const queuedChar = context.char || c;
    const subResults = resolvePendingSave(head.pendingSave, outcome, context);
    const instanceKey = context.systemInstance?.id || context.systemInstance?.systemId || null;
    const beforeCharForAuthority = cloneCharForSystemAuthority(queuedChar);
    const nextChar = instanceKey
      ? applyRuleDispatchToChar(queuedChar, instanceKey, subResults, systems, context.worldContext || null)
      : { ...queuedChar, ...applySystemRuleResults(queuedChar, subResults, systems, context.worldContext || null) };
    const nextSystemInstance = instanceKey
      ? (nextChar.systemList || []).find(entry => (entry.id || entry.systemId) === instanceKey) || context.systemInstance
      : context.systemInstance || null;
    const nestedSaveEntries = buildSystemRulePendingSaveQueueEntries(subResults.pendingSaves, {
      char: nextChar,
      systemInstance: nextSystemInstance,
      systemTemplate: context.systemTemplate,
      trackerValues: nextSystemInstance?.trackerValues || context.trackerValues || {},
      triggerContext: context.triggerContext,
      worldContext: context.worldContext,
      label: `${head.label || 'System rule'} follow-up`,
      source: `${head.source || 'systems'}.save`,
    });

    consumeSystemRuleSideEffects(subResults, {
      char: nextChar,
      label: `${head.label || 'System rule'} save ${outcome}`,
      source: `${head.source || 'systems'}.save`,
      systemInstance: nextSystemInstance,
      systemTemplate: context.systemTemplate,
      trackerValues: nextSystemInstance?.trackerValues || context.trackerValues || {},
      triggerContext: context.triggerContext,
      worldContext: context.worldContext,
      queuePendingSaves: false,
      suppressPendingSaveNotice: true,
    });

    if (nestedSaveEntries.length > 0 && typeof onNotify === 'function') {
      onNotify(`${head.label || 'System rule'} queued ${nestedSaveEntries.length} follow-up saving throw${nestedSaveEntries.length === 1 ? '' : 's'} for resolution.`);
    }

    const audit = {
      summary: `${head.label || 'System rule'} save ${outcome}: ${head.pendingSave?.reason || c.name}`,
      category: 'systems',
      action: `system.rule-save.${outcome}`,
    };
    if (nextChar !== queuedChar) {
      if (!tryPatchSystemStateFromAuthority(beforeCharForAuthority, nextChar, audit)) {
        saveChars({ ...chars, [c.id]: nextChar }, { perCharMeta: { [c.id]: audit } });
      }
    }

    setSystemRuleSaveQueue([...nestedSaveEntries, ...rest]);
  };

  const handleSystemRuleSaveSkip = () => {
    setSystemRuleSaveQueue(prev => prev.slice(1));
  };

  const effectiveMaxHp = getEffectiveMaxHp(c);

  const dmCardLegacyStatusView = buildLegacyStatusEffectView({

    char: c,

    glowingBuffsForChar: glowingBuffs?.[c.id] || {},

  });

  const dmCardActiveEffects = dmCardLegacyStatusView.activeEffects;



  const notifyVitalsPatchFailure = (error) => {
    if (typeof onNotify === 'function') {
      onNotify(error?.message || 'Unable to update character vitals.');
    }
  };

  const updateHP = hp => {
    const parsedHp = Number.parseInt(hp, 10);
    if (!Number.isFinite(parsedHp)) return;
    const currentChar = chars[c.id] || c;
    const beforeHp = normalizeVitalNumber(currentChar.currentHp);
    const nextHp = Math.max(0, Math.min(parsedHp, effectiveMaxHp));
    const fallback = () => {
      const cs = { ...chars };
      cs[c.id] = { ...currentChar, currentHp: nextHp };
      saveChars(cs, { perCharMeta: { [c.id]: { summary: `HP set: ${c.name} ${beforeHp} -> ${nextHp}`, category: 'combat', action: 'hp.set' } } });
      setHpIn('');
    };
    if (typeof patchCharacterVitals !== 'function') {
      fallback();
      return;
    }
    void tryPatchCharacterVitals({
      patchCharacterVitals,
      charId: c.id,
      updates: { currentHp: nextHp },
      expected: { currentHp: beforeHp },
      surface: 'dm-card.hp-set',
      onError: notifyVitalsPatchFailure,
    }).then((handled) => {
      if (!handled) fallback();
      else setHpIn('');
    });
  };

  const commitDmCardHp = nextHp => {
    const beforeHp = normalizeVitalNumber(c.currentHp);
    const fallback = () => {
      saveChars({ ...chars, [c.id]: { ...c, currentHp: nextHp } });
    };
    if (typeof patchCharacterVitals !== 'function') {
      fallback();
      return;
    }
    void tryPatchCharacterVitals({
      patchCharacterVitals,
      charId: c.id,
      updates: { currentHp: nextHp },
      expected: { currentHp: beforeHp },
      surface: 'dm-card.hp-stepper',
      onError: notifyVitalsPatchFailure,
    }).then((handled) => {
      if (!handled) fallback();
    });
  };

  const commitDmCardMana = nextMana => {
    if (!showMana) return;
    const beforeMana = normalizeVitalNumber(c.currentMana);
    const fallback = () => {
      saveChars({ ...chars, [c.id]: { ...c, currentMana: nextMana } });
    };
    if (typeof patchCharacterVitals !== 'function') {
      fallback();
      return;
    }
    void tryPatchCharacterVitals({
      patchCharacterVitals,
      charId: c.id,
      updates: { currentMana: nextMana },
      expected: { currentMana: beforeMana },
      surface: 'dm-card.mana-stepper',
      onError: notifyVitalsPatchFailure,
    }).then((handled) => {
      if (!handled) fallback();
    });
  };

  

  const addBuff = () => { 

    if (!buff.trim()) return; 

    

    // 2026-04-27: fold the glow into the same save as the buff itself.

    // Previously addBuffGlow ran a *separate* saveChars after the buff

    // save — the two writes raced (whichever landed last in Convex won),

    // so the glow was sometimes lost. Now glow lives on char.glowingBuffs

    // and is included in the single buff-add save. addBuffGlowPatch also

    // schedules the TTL prune.

    let nextChar = addLegacyEffect(c, {

      text: buff,

      kind: 'buff',

      permanent: buffIsPermanent,

      metadata: buildLegacyEffectMetadata({

        source: buffSource || null,

        playerTooltip: buffPlayerTooltip || null,

        colorId: buffColor || 'default-buff',

        addedBy: 'DM',

        noteVisible: buffNoteVisible,

      }),

    });

    if (buffNotifyGlow && addBuffGlowPatch) {

      const colorOpt = BUFF_COLOR_OPTIONS.find(co => co.id === (buffColor || 'default-buff'));

      nextChar = addBuffGlowPatch(nextChar, buff, colorOpt?.glowColor || colorOpt?.color || '#27c95a');

    }

    const cs = { ...chars, [c.id]: nextChar };

    saveChars(cs, { perCharMeta: { [c.id]: { summary: `✨ ${buffIsPermanent ? 'Permanent ' : ''}Buff applied: ${buff}${buffSource ? ` (${buffSource})` : ''} → ${c.name}`, category: 'buffs', action: 'buff.added' } } });

    setBuff(''); 

    setBuffSource('');

    setBuffPlayerTooltip('');

    setBuffNotifyGlow(false);

    setBuffNoteVisible(false);

    // Keep isPermanent for next use

  };

  

  const removeBuff = ({ text, permanent, index }) => { 

    const cs = { 

      ...chars,

      [c.id]: removeLegacyEffectAt(c, { text, kind: 'buff', permanent, index })

    };
    saveChars(cs, { perCharMeta: { [c.id]: { summary: `â¨ ${permanent ? 'Permanent ' : ''}Buff removed: ${text} â ${c.name}`, category: 'buffs', action: 'buff.removed' } } }); 
  };

  

  const addDebuff = () => { 

    if (!debuff.trim()) return; 

    

    // 2026-04-27: fold glow into the same save (same fix as addBuff above).

    let nextChar = addLegacyEffect(c, {

      text: debuff,

      kind: 'debuff',

      permanent: debuffIsPermanent,

      metadata: buildLegacyEffectMetadata({

        source: debuffSource || null,

        playerTooltip: debuffPlayerTooltip || null,

        colorId: debuffColor || 'default-debuff',

        addedBy: 'DM',

        noteVisible: debuffNoteVisible,

      }),

    });

    if (debuffNotifyGlow && addBuffGlowPatch) {

      const colorOpt = BUFF_COLOR_OPTIONS.find(co => co.id === (debuffColor || 'default-debuff'));

      nextChar = addBuffGlowPatch(nextChar, debuff, colorOpt?.glowColor || colorOpt?.color || '#ff6961');

    }

    const cs = { ...chars, [c.id]: nextChar };

    saveChars(cs, { perCharMeta: { [c.id]: { summary: `💀 ${debuffIsPermanent ? 'Permanent ' : ''}Debuff applied: ${debuff}${debuffSource ? ` (${debuffSource})` : ''} → ${c.name}`, category: 'buffs', action: 'debuff.added' } } });

    setDebuff(''); 

    setDebuffSource('');

    setDebuffPlayerTooltip('');

    setDebuffNotifyGlow(false);

    setDebuffNoteVisible(false);

    // Keep isPermanent for next use

  };

  

  const removeDebuff = ({ text, permanent, index }) => { 

    const cs = { 

      ...chars,

      [c.id]: removeLegacyEffectAt(c, { text, kind: 'debuff', permanent, index })

    };
    saveChars(cs, { perCharMeta: { [c.id]: { summary: `ð ${permanent ? 'Permanent ' : ''}Debuff removed: ${text} â ${c.name}`, category: 'buffs', action: 'debuff.removed' } } }); 
  };

  

  const addDisease = (diseaseId) => {

    if (!diseasesEnabled) return;

    if (!diseaseId) return;

    if ((c.diseaseImmunityList || []).includes(diseaseId)) {

      onNotify(`${c.name} is IMMUNE to ${diseases[diseaseId]?.name}!`);

      return;

    }

    const cs = makeSystemEditableChars(chars, c.id, c);

    cs[c.id] = addDiseaseState(cs[c.id], buildDiseaseState({

      diseaseId,

      id: generateId(),

      contractedDate: new Date().toISOString(),

    }));

    saveChars(cs, { perCharMeta: { [c.id]: { summary: `🦠 Disease applied: ${diseases[diseaseId]?.name || diseaseId} → ${c.name} (hidden)`, category: 'diseases', action: 'disease.applied' } } });

  };
  const removeDisease = did => { const cs = { ...chars }; cs[c.id] = removeDiseaseState(cs[c.id], did); saveChars(cs, { perCharMeta: { [c.id]: { summary: `ð¦  Disease removed: ${diseases[did]?.name || did} â ${c.name}`, category: 'diseases', action: 'disease.removed' } } }); };

  

  const updateDiseaseVisibility = (diseaseId, updates) => {

    if (!diseasesEnabled) return;

    const cs = makeSystemEditableChars(chars, c.id, c);

    cs[c.id] = updateDiseaseState(cs[c.id], diseaseId, updates);

    const parts = [];

    if (updates.active !== undefined) parts.push(`active ${updates.active ? 'on' : 'off'}`);

    if (updates.applyEffects !== undefined) parts.push(`apply ${updates.applyEffects ? 'on' : 'off'}`);

    if (updates.applyHiddenEffects !== undefined) parts.push(`applyHidden ${updates.applyHiddenEffects ? 'on' : 'off'}`);

    if (updates.showName !== undefined) parts.push(`name ${updates.showName ? 'shown' : 'hidden'}`);

    if (updates.showEffects !== undefined) parts.push(`effects ${updates.showEffects ? 'shown' : 'hidden'}`);

    if (updates.showCure !== undefined) parts.push(`cure ${updates.showCure ? 'shown' : 'hidden'}`);

    if (updates.visibleSymptoms !== undefined) parts.push(`symptoms ${Array.isArray(updates.visibleSymptoms) ? updates.visibleSymptoms.length : 0}`);

    const tag = parts.length ? `(${parts.join(', ')})` : '';

    saveChars(cs, { perCharMeta: { [c.id]: { summary: `👁 Disease reveal: ${diseases[diseaseId]?.name || diseaseId} ${tag} → ${c.name}`, category: 'diseases', action: 'disease.reveal' } } });



  };



  // 2026-04-27: third arg `instanceOverrides` lets the apply caller

  // override per-instance fields like `badgeColor` (for the new

  // "pick pill color on apply" affordance). Defaults preserve prior

  // behavior — pass `{}` and you get the same instance shape as before.

  const addSystem = (systemId, initialSlotValues = {}, instanceOverrides = {}) => {

    if (!systemId) return;

    const sys = systems[systemId];

    if (!sys) return;

    const slotValidationErrors = getSystemInstanceSlotValidationErrors(sys.instanceSlots || []);
    if (slotValidationErrors.length > 0) {
      const message = `Cannot apply ${sys.name || systemId}: ${slotValidationErrors[0].message}`;
      if (typeof onNotify === 'function') onNotify(message);
      else if (typeof window !== 'undefined') window.alert(message);
      return;
    }



    const cs = makeSystemEditableChars(chars, c.id, c);
    const beforeCharForAuthority = cloneCharForSystemAuthority(cs[c.id]);

    const existingSystemList = cs[c.id].systemList || [];
    const seededSlotValues = seedSystemInstanceSlotValues(sys, initialSlotValues);
    const existingIndex = existingSystemList.findIndex(x => (
      systemInstanceMatchesApplyTarget(x, systemId, sys, seededSlotValues, null)
    ));

    if (existingIndex >= 0) {
      const existingInstance = existingSystemList[existingIndex];
      if (existingInstance?.active === false) {
        const reactivatedAt = new Date().toISOString();
        cs[c.id].systemList = existingSystemList.map((entry, index) => (
          index === existingIndex
            ? {
                ...entry,
                active: true,
                reactivatedAt,
                history: [
                  ...(Array.isArray(entry.history) ? entry.history : []),
                  { action: 'reactivated', timestamp: reactivatedAt, details: 'System reactivated' },
                ],
              }
            : entry
        ));

        if (typeof addSystemGlowPatch === 'function') {
          const reactivatedInstance = cs[c.id].systemList[existingIndex];
          const badgeColorId = reactivatedInstance.badgeColor || sys?.badgeColor || null;
          const colorOpt = badgeColorId ? BUFF_COLOR_OPTIONS.find(o => o.id === badgeColorId) : null;
          const glowColor = colorOpt?.glowColor || colorOpt?.color || '#ffd700';
          cs[c.id] = addSystemGlowPatch(cs[c.id], reactivatedInstance.id || reactivatedInstance.systemId, glowColor);
        }

        const audit = { summary: `System reactivated: ${(sys.name || systemId)} -> ${c.name}`, category: 'systems', action: 'system.reactivated' };
        if (!tryPatchSystemStateFromAuthority(beforeCharForAuthority, cs[c.id], audit)) {
          saveChars(cs, { perCharMeta: { [c.id]: audit } });
        }
        onNotify(`${sys.name || systemId} reactivated for ${c.name}`);
      }
      return;
    }

    if (existingIndex === -1) {

      // Get preset defaults based on system type

      const preset = SYSTEM_PRESETS[sys.type] || SYSTEM_PRESETS.custom;

      const defaultVis = preset.defaultVisibility || {};

      // Seed instance slot values: callers pass explicit fills; unspecified slots take

      // the template's declared `default`. See Systems expansion Session 3.

      

      // 2026-04-27: capture id locally so we can reference it for the

      // glow patch + on-apply audit summary.

      const newInstId = buildStableSlottedSystemInstanceId(sys, {
        parentSystemId: null,
        slotValues: seededSlotValues,
      }) || generateId();

      cs[c.id].systemList = [

        ...(cs[c.id].systemList || []),

        {

          systemId,

          template: null,

          // Visibility defaults from preset

          playerVisible: defaultVis.playerVisible !== false,

          active: true,

          showName: defaultVis.showName || false,

          showDescription: defaultVis.showDescription || false,

          visibleClues: [],

          showEffects: defaultVis.showEffects || false,

          showCure: defaultVis.showCure || false,

          // Effect application

          applyEffects: true,

          applyHiddenEffects: sys.type === 'secret' || sys.type === 'curse', // Auto-apply hidden for secrets/curses

          // Per-effect controls

          effectOverrides: {},

          // Sections

          visibleSections: {},

          showSections: {},

          // Tracker visibility controls

          trackerVisibility: {},

          trackerValues: {},

          // Instance slot values — fills {{SLOT_KEY}} placeholders in the template

          // (Systems expansion Session 3). Seeded from `initialSlotValues` + template defaults.

          instanceValues: seededSlotValues,

          // Placeholder text settings

          unknownText: UNKNOWN_PLACEHOLDER,

          placeholderName: '',

          placeholderDescription: '',

          // Clues label customization

          cluesLabel: 'Clues',

          showCluesLabel: true,

          // Notes

          dmNotes: '',

          playerNotes: '',

          showPlayerNotes: false,

          // Timestamps

          appliedAt: new Date().toISOString(),

          history: [{ action: 'applied', timestamp: new Date().toISOString(), details: 'System applied' }],

          // 2026-04-27: caller-supplied per-instance overrides

          // (e.g. pill color picked in the Apply UI). id and

          // appliedAt are pinned BELOW so a stray `id` in overrides

          // can't clobber the one we use for glow targeting.

          ...instanceOverrides,

          id: newInstId,

        }

      ];



      // Fire on-apply rules for the newly added system (silent — consequences of the logged parent event)

      const newInstance = cs[c.id].systemList[cs[c.id].systemList.length - 1];

      const tpl = systems[systemId] || newInstance.template;
      let onApplyRuleResults = null;

      if (tpl) {

        const ctx = { char: cs[c.id], systemInstance: newInstance, systemTemplate: tpl, trackerValues: newInstance.trackerValues || {} };

        const res = executeRulesForTrigger('on-apply', ctx, systems);
        onApplyRuleResults = res;

        cs[c.id] = applyRuleDispatchToChar(cs[c.id], newInstance.id || newInstance.systemId, res, systems);

      }



      // 2026-04-27: fire a glow on the new system pill so the player

      // sees the addition land. Color falls back through:

      //   instance badgeColor → template badgeColor → gold default

      // Folded into the same saveChars below to avoid the two-write

      // race that previously clobbered the glow (see GENERAL note).

      if (typeof addSystemGlowPatch === 'function') {

        const badgeColorId = newInstance.badgeColor || tpl?.badgeColor || null;

        const colorOpt = badgeColorId ? BUFF_COLOR_OPTIONS.find(o => o.id === badgeColorId) : null;

        const glowColor = colorOpt?.glowColor || colorOpt?.color || '#ffd700';

        cs[c.id] = addSystemGlowPatch(cs[c.id], newInstId, glowColor);

      }

      const appliedInstanceForSave = (cs[c.id].systemList || [])
        .find(instance => (instance.id || instance.systemId) === (newInstance.id || newInstance.systemId))
        || newInstance;

      consumeSystemRuleSideEffects(onApplyRuleResults, {
        char: cs[c.id],
        label: 'System apply',
        source: 'system.apply',
        systemInstance: appliedInstanceForSave,
        systemTemplate: tpl,
        trackerValues: appliedInstanceForSave.trackerValues || {},
        worldContext: {
          chars: cs,
          journal: [...(journal || []), ...(dmJournal || [])],
          currentLocation,
          npcLibrary,
        },
      });



      const audit = { summary: `🔒 System applied: ${(sys.name || systemId)} → ${c.name}`, category: 'systems', action: 'system.applied' };
      if (!tryPatchSystemStateFromAuthority(beforeCharForAuthority, cs[c.id], audit)) {
        saveChars(cs, { perCharMeta: { [c.id]: audit } });
      }

      onNotify(`${sys.name || systemId} applied to ${c.name}`);

    }

  };



  const addInlineSystem = (template, instanceOverrides = {}) => {

    if (!template || !template.name) return;

    

    // Get preset defaults based on template type

    const preset = SYSTEM_PRESETS[template.type] || SYSTEM_PRESETS.custom;

    const defaultVis = preset.defaultVisibility || {};

    

    const cs = makeSystemEditableChars(chars, c.id, c);
    const beforeCharForAuthority = cloneCharForSystemAuthority(cs[c.id]);

    const entry = {

      id: generateId(),

      systemId: null,

      template,

      // Visibility defaults from preset

      playerVisible: defaultVis.playerVisible !== false,

      active: true,

      showName: defaultVis.showName || false,

      showDescription: defaultVis.showDescription || false,

      visibleClues: [],

      showEffects: defaultVis.showEffects || false,

      showCure: defaultVis.showCure || false,

      // Effect application

      applyEffects: preset.hasEffects !== false,

      applyHiddenEffects: template.type === 'secret' || template.type === 'curse',

      // Per-effect controls

      effectOverrides: {}, // { 'visible-0': { apply: true, showSource: false }, ... }

      // Sections

      visibleSections: {},

      showSections: {},

      // Tracker visibility controls

      trackerVisibility: {}, // { 'trackerId': { showToPlayer: true, showBar: true, showValue: true, showSource: true }, ... }

      trackerValues: {},

      // Instance slot values (Systems expansion Session 3) — {{SLOT_KEY}} fills.

      instanceValues: {},

      // Placeholder text settings

      unknownText: UNKNOWN_PLACEHOLDER,

      placeholderName: '',

      placeholderDescription: '',

      // Clues label customization

      cluesLabel: 'Clues',

      showCluesLabel: true,

      // Notes

      dmNotes: '',

      playerNotes: '',

      showPlayerNotes: false,

      // Timestamps and history

      appliedAt: new Date().toISOString(),

      history: [{ action: 'applied', timestamp: new Date().toISOString(), details: 'System applied' }],

      // Allow overrides

      ...instanceOverrides,

    };

    cs[c.id].systemList = [...(cs[c.id].systemList || []), entry];



    // Fire on-apply rules for the newly added inline system
    let onApplyRuleResults = null;

    if (template) {

      const ctx = { char: cs[c.id], systemInstance: entry, systemTemplate: template, trackerValues: entry.trackerValues || {} };

      const res = executeRulesForTrigger('on-apply', ctx, systems);
      onApplyRuleResults = res;

      cs[c.id] = applyRuleDispatchToChar(cs[c.id], entry.id || entry.systemId, res, systems);

    }



    // 2026-04-27: glow-on-apply, color matches pill (badgeColor) where set.

    if (typeof addSystemGlowPatch === 'function') {

      const badgeColorId = entry.badgeColor || template?.badgeColor || null;

      const colorOpt = badgeColorId ? BUFF_COLOR_OPTIONS.find(o => o.id === badgeColorId) : null;

      const glowColor = colorOpt?.glowColor || colorOpt?.color || '#ffd700';

      cs[c.id] = addSystemGlowPatch(cs[c.id], entry.id, glowColor);

    }

    const inlineInstanceForSave = (cs[c.id].systemList || [])
      .find(instance => (instance.id || instance.systemId) === (entry.id || entry.systemId))
      || entry;

    consumeSystemRuleSideEffects(onApplyRuleResults, {
      char: cs[c.id],
      label: 'Inline system apply',
      source: 'system.apply.inline',
      systemInstance: inlineInstanceForSave,
      systemTemplate: template,
      trackerValues: inlineInstanceForSave.trackerValues || {},
      worldContext: {
        chars: cs,
        journal: [...(journal || []), ...(dmJournal || [])],
        currentLocation,
        npcLibrary,
      },
    });



    const audit = { summary: `✨ One-off system applied: ${template.name} → ${c.name}`, category: 'systems', action: 'system.applied.inline' };
    if (!tryPatchSystemStateFromAuthority(beforeCharForAuthority, cs[c.id], audit)) {
      saveChars(cs, { perCharMeta: { [c.id]: audit } });
    }

    onNotify(`${template.name} applied to ${c.name}`);

  };



  const removeSystem = (systemKey) => {

    const cs = makeSystemEditableChars(chars, c.id, c);
    const beforeCharForAuthority = cloneCharForSystemAuthority(cs[c.id]);

    const before = (cs[c.id].systemList || []);

    const found = before.find(s => (s.id || s.systemId) === systemKey);

    const name = found

      ? ((found.systemId && systems[found.systemId]?.name) || found.template?.name || found.systemId || 'System')

      : (systems[systemKey]?.name || systemKey || 'System');



    const lifecycle = removeSystemsWithLifecycle({
      char: cs[c.id],
      systemKeys: [systemKey],
      systems,
      worldContext: {
        chars: cs,
        journal: [...(journal || []), ...(dmJournal || [])],
        currentLocation,
        npcLibrary,
      },
      archiveDetails: 'System removed',
    });
    cs[c.id] = lifecycle.nextChar;

    const sideEffects = collectRuleResultSideEffects(lifecycle, { npcLibrary });
    if (sideEffects.npcLibraryChanged && typeof saveNpcLibrary === 'function') {
      saveNpcLibrary(sideEffects.nextNpcLibrary);
    }
    const queuedRemovalSaves = enqueueSystemRulePendingSaves(sideEffects.pendingSaves, {
      char: lifecycle.nextChar,
      systemInstance: found || null,
      systemTemplate: found ? getSystemTemplateForInstance(found, systems) : systems[systemKey] || null,
      trackerValues: found?.trackerValues || {},
      worldContext: {
        chars: cs,
        journal: [...(journal || []), ...(dmJournal || [])],
        currentLocation,
        npcLibrary,
      },
      label: 'System removal',
      source: 'system.remove',
    });
    const sentStatusMessages = persistSystemRuleStatusMessages(sideEffects.statusMessages, {
      char: lifecycle.nextChar,
      source: 'system.remove',
      messages,
      onSendStatusMessage,
      onSaveMessages,
      auditMeta: { category: 'systems', action: 'system.remove-rule-status' },
    });
    const notices = [...sideEffects.notifications];
    if (queuedRemovalSaves.length > 0) {
      const pendingSaveNotice = buildSystemRulePendingSaveNotice(sideEffects.pendingSaves, 'System removal');
      notices.push(`${pendingSaveNotice || `System removal requested ${queuedRemovalSaves.length} saving throw${queuedRemovalSaves.length === 1 ? '' : 's'}.`} Queued for resolution.`);
    } else {
      const pendingSaveNotice = buildSystemRulePendingSaveNotice(sideEffects.pendingSaves, 'System removal');
      if (pendingSaveNotice) notices.push(pendingSaveNotice);
    }
    const ruleLogEntries = buildSystemRuleTravelLogEntries(sideEffects.logEntries, {
      char: lifecycle.nextChar,
      action: 'system-rule-log',
      source: 'system.remove',
    });
    if (ruleLogEntries.length > 0 && typeof saveGroupTravelLog === 'function') {
      const keep = Math.max(0, 200 - ruleLogEntries.length);
      saveGroupTravelLog([...ruleLogEntries, ...(groupTravelLog || []).slice(0, keep)]);
      notices.push(`${ruleLogEntries.length} system rule log entr${ruleLogEntries.length === 1 ? 'y' : 'ies'} recorded.`);
    }
    if (sentStatusMessages.length > 0) {
      notices.push(`${sentStatusMessages.length} system status message${sentStatusMessages.length === 1 ? '' : 's'} sent.`);
    }

    const cascadedCount = Math.max(0, lifecycle.removedEntries.length - (found ? 1 : 0));
    const cascadeSummary = cascadedCount > 0 ? ` (+${cascadedCount} cascaded)` : '';
    if (notices.length > 0 && typeof onNotify === 'function') {
      onNotify(notices.join(' | '));
    }

    const audit = { summary: `🔒 System removed: ${name}${cascadeSummary} ← ${c.name}`, category: 'systems', action: 'system.removed' };
    if (!tryPatchSystemStateFromAuthority(beforeCharForAuthority, cs[c.id], audit)) {
      saveChars(cs, { perCharMeta: { [c.id]: audit } });
    }

  };

  const updateSystemVisibility = (systemKey, updates) => {

    const cs = makeSystemEditableChars(chars, c.id, c);
    const beforeCharForAuthority = cloneCharForSystemAuthority(cs[c.id]);

    const before = (cs[c.id].systemList || []);

    const found = before.find(s => (s.id || s.systemId) === systemKey);

    const name = found

      ? ((found.systemId && systems[found.systemId]?.name) || found.template?.name || found.systemId || 'System')

      : (systems[systemKey]?.name || systemKey || 'System');



    const parts = [];

    if (updates.playerVisible !== undefined) parts.push(`visibility ${updates.playerVisible ? 'on' : 'off'}`);

    if (updates.active !== undefined) parts.push(`active ${updates.active ? 'on' : 'off'}`);

    if (updates.showName !== undefined) parts.push(`name ${updates.showName ? 'shown' : 'hidden'}`);

    if (updates.showDescription !== undefined) parts.push(`details ${updates.showDescription ? 'shown' : 'hidden'}`);

    if (updates.showEffects !== undefined) parts.push(`effects ${updates.showEffects ? 'shown' : 'hidden'}`);

    if (updates.applyEffects !== undefined) parts.push(`apply ${updates.applyEffects ? 'on' : 'off'}`);

    if (updates.applyHiddenEffects !== undefined) parts.push(`applyHidden ${updates.applyHiddenEffects ? 'on' : 'off'}`);

    if (updates.showCure !== undefined) parts.push(`cure ${updates.showCure ? 'shown' : 'hidden'}`);

    if (updates.visibleClues !== undefined) parts.push(`clues ${updates.visibleClues.length}`);

    if (updates.visibleSections !== undefined) parts.push(`sections`);

    if (updates.showSections !== undefined) parts.push(`sections`);

    if (updates.dmNotes !== undefined) parts.push(`dmNotes`);

    if (updates.playerNotes !== undefined) parts.push(`playerNotes`);

    if (updates.showPlayerNotes !== undefined) parts.push(`notes ${updates.showPlayerNotes ? 'shown' : 'hidden'}`);

    if (updates.currentStage !== undefined) parts.push(`stage → ${updates.currentStage + 1}`);

    if (updates.showStage !== undefined) parts.push(`stage ${updates.showStage ? 'shown' : 'hidden'}`);

    if (updates.trackerValues !== undefined) parts.push(`tracker updated`);



    // Add history entry

    const historyEntry = parts.length > 0 ? {

      action: 'updated',

      timestamp: new Date().toISOString(),

      details: parts.join(', '),

    } : null;



    cs[c.id].systemList = before.map(s => {

      if ((s.id || s.systemId) === systemKey) {

        const newHistory = historyEntry

          ? [...(s.history || []).slice(-49), historyEntry]

          : (s.history || []);

        return { ...s, ...updates, history: newHistory };

      }

      return s;

    });



    // Fire on-tracker-change / on-threshold rules if tracker values changed (silent dispatch).

    // on-tracker-change fires once per update (per instance) regardless of how many fields changed.

    // on-threshold fires once per crossing — old < t.value && new >= t.value (and the reverse).

    if (updates.trackerValues !== undefined && found) {

      const tpl = (found.systemId && systems[found.systemId]) || found.template;

      if (tpl) {

        const oldTv = found.trackerValues || {};

        const newTv = updates.trackerValues || {};

        const changed = Object.keys(newTv).filter(k => (newTv[k] ?? 0) !== (oldTv[k] ?? 0));



        if (changed.length > 0) {

          const fetchInstance = () => (cs[c.id].systemList || []).find(s => (s.id || s.systemId) === systemKey);

          const instanceKey = found.id || found.systemId;
          const trackerRuleResults = createDispatchResultAccumulator();
          const trackerRuleWorldContext = {
            chars: cs,
            journal: [...(journal || []), ...(dmJournal || [])],
            currentLocation,
            npcLibrary,
          };



          // on-tracker-change (fires once, sees the already-patched instance with new tracker values)

          const tcCtx = { char: cs[c.id], systemInstance: fetchInstance(), systemTemplate: tpl, trackerValues: newTv };

          const tcRes = executeRulesForTrigger('on-tracker-change', tcCtx, systems);

          cs[c.id] = applyRuleDispatchToChar(cs[c.id], instanceKey, tcRes, systems, trackerRuleWorldContext);
          mergeDispatchResultAccumulator(trackerRuleResults, tcRes);



          // on-threshold: detect crossings and fire once per crossing

          const fieldsById = {};

          (tpl.trackerFields || []).forEach(f => { fieldsById[f.id] = f; });

          changed.forEach(fieldId => {

            const field = fieldsById[fieldId];

            if (!field) return;

            const oldV = oldTv[fieldId] ?? 0;

            const newV = newTv[fieldId] ?? 0;

            (field.thresholds || []).forEach(t => {

              const tv = parseInt(t.value) || 0;

              const crossed = (oldV < tv && newV >= tv) || (oldV >= tv && newV < tv);

              if (!crossed) return;

              const thCtx = { char: cs[c.id], systemInstance: fetchInstance(), systemTemplate: tpl, trackerValues: (fetchInstance()?.trackerValues) || newTv };

              const thRes = executeRulesForTrigger('on-threshold', thCtx, systems);

              cs[c.id] = applyRuleDispatchToChar(cs[c.id], instanceKey, thRes, systems, trackerRuleWorldContext);
              mergeDispatchResultAccumulator(trackerRuleResults, thRes);

            });

          });

          const trackerRuleInstance = fetchInstance();
          consumeSystemRuleSideEffects(trackerRuleResults, {
            char: cs[c.id],
            label: 'System tracker update',
            source: 'system.tracker-update',
            systemInstance: trackerRuleInstance,
            systemTemplate: tpl,
            trackerValues: trackerRuleInstance?.trackerValues || newTv,
            triggerContext: { changedTrackers: changed },
            worldContext: trackerRuleWorldContext,
          });

        }

      }

    }



    // Auto-glow on visibility updates. When the system instance has `glowOnUpdate: true`

    // and the update touches a player-visible field (reveals, clues, sections, stage,

    // player notes), emit a glow pulse via addSystemGlow so the player sees the pill

    // flash on their sheet. Uses the configured glowColorId or falls back to gold.

    // Editor-config updates (badgeColor, placeholder text, dmNotes, etc.) don't trigger

    // the glow — those aren't state changes the player cares about.

    const VISIBILITY_FIELDS = ['playerVisible', 'showName', 'showDescription', 'showEffects', 'showCure', 'visibleClues', 'visibleSections', 'showSections', 'showStage', 'playerNotes', 'showPlayerNotes', 'currentStage'];

    const isVisibilityUpdate = Object.keys(updates).some(k => VISIBILITY_FIELDS.includes(k));

    const hasAutoGlowSideEffect = found && found.glowOnUpdate && isVisibilityUpdate && typeof addSystemGlowPatch === 'function';

    if (
      found
      && updates.trackerValues === undefined
      && !hasAutoGlowSideEffect
      && typeof patchCharacterSystemInstance === 'function'
    ) {
      const instanceKey = found.id || found.systemId || systemKey;
      const payload = {
        charId: c.id,
        instanceKey,
        patch: updates,
      };
      if (historyEntry) payload.historyEntry = historyEntry;
      patchCharacterSystemInstance(payload).catch(err => {
        console.error('Failed to patch system instance:', err);
        if (typeof onNotify === 'function') {
          onNotify(`System update failed: ${err?.message || 'unknown error'}`);
        }
      });
      return;
    }

    // 2026-04-27: fold the auto-glow into the same save as the system

    // update itself — was a separate addSystemGlow write that raced

    // with the saveChars below and could be clobbered.

    if (hasAutoGlowSideEffect) {

      const instanceKey = found.id || found.systemId;

      const glowColorOpt = BUFF_COLOR_OPTIONS.find(co => co.id === (found.glowColorId || 'gold'));

      cs[c.id] = addSystemGlowPatch(cs[c.id], instanceKey, glowColorOpt?.color || '#ffd700');

    }



    const tag = parts.length ? `(${parts.join(', ')})` : '';

    const audit = { summary: `👁 System updated: ${name} ${tag} → ${c.name}`, category: 'systems', action: 'system.updated' };
    if (!tryPatchSystemStateFromAuthority(beforeCharForAuthority, cs[c.id], audit)) {
      saveChars(cs, { perCharMeta: { [c.id]: audit } });
    }

  };



  // Apply the results of a single-system rule dispatch — used by the Manual Actions button in

  // SystemManager, which fires ONE rule on ONE system. Before this fix, charUpdates from

  // manual rules (e.g. deal-damage, heal, add-effect) were silently dropped.

  const applyManualRuleResults = (instanceKey, results) => {

    if (!results || !instanceKey) return;

    const hasCharUpdates = results.charUpdates && Object.keys(results.charUpdates).length > 0;

    const hasSystemUpdates = results.systemUpdates && Object.keys(results.systemUpdates).length > 0;

    const hasResourceEvents = (results.resourceEvents || []).length > 0;

    const hasCrossSystemUpdates = (results.crossSystemUpdates || []).length > 0;

    const hasPendingApplySystems = (results.pendingApplySystems || []).length > 0;

    const hasStateUpdates = hasCharUpdates || hasSystemUpdates || hasResourceEvents || hasCrossSystemUpdates || hasPendingApplySystems;

    const cs = { ...chars };
    const systemInstanceBefore = (cs[c.id]?.systemList || []).find(s => (s.id || s.systemId) === instanceKey) || null;
    const systemTemplate = systemInstanceBefore ? getSystemTemplateForInstance(systemInstanceBefore, systems) : null;
    const manualRuleWorldContext = {
      chars: cs,
      journal: [...(journal || []), ...(dmJournal || [])],
      currentLocation,
      npcLibrary,
    };
    const beforeCharForAuthority = cloneCharForSystemAuthority(cs[c.id]);

    if (hasStateUpdates) {
      cs[c.id] = applyRuleDispatchToChar(cs[c.id], instanceKey, results, systems, manualRuleWorldContext);
    }

    const systemInstanceAfter = (cs[c.id]?.systemList || []).find(s => (s.id || s.systemId) === instanceKey) || systemInstanceBefore;
    consumeSystemRuleSideEffects(results, {
      char: cs[c.id],
      label: 'Manual system rule',
      source: 'system.manual-rule',
      systemInstance: systemInstanceAfter,
      systemTemplate,
      trackerValues: systemInstanceAfter?.trackerValues || systemInstanceBefore?.trackerValues || {},
      worldContext: manualRuleWorldContext,
    });

    if (!hasStateUpdates) return;

    const audit = { summary: `⚙️ Manual rule triggered → ${c.name}`, category: 'systems', action: 'system.manual-rule' };
    if (tryPatchSystemStateFromAuthority(beforeCharForAuthority, cs[c.id], audit)) return;

    saveChars(cs, { perCharMeta: { [c.id]: { summary: `⚙️ Manual rule triggered → ${c.name}`, category: 'systems', action: 'system.manual-rule' } } });

  };



const addImmunity = (diseaseId) => { if (!diseasesEnabled || !diseaseId) return; const cs = { ...chars }; if (!(cs[c.id].diseaseImmunityList || []).includes(diseaseId)) cs[c.id].diseaseImmunityList = [...(cs[c.id].diseaseImmunityList || []), diseaseId]; saveChars(cs, { perCharMeta: { [c.id]: { summary: `🛡️ Immunity granted: ${diseases[diseaseId]?.name || diseaseId} → ${c.name}`, category: 'diseases', action: 'immunity.added' } } }); };

  const removeImmunity = did => { if (!diseasesEnabled) return; const cs = { ...chars }; cs[c.id].diseaseImmunityList = (cs[c.id].diseaseImmunityList || []).filter(x => x !== did); saveChars(cs, { perCharMeta: { [c.id]: { summary: `🛡️ Immunity removed: ${diseases[did]?.name || did} ← ${c.name}`, category: 'diseases', action: 'immunity.removed' } } }); };



  const doFeed = () => {

    if (!hungerEnabled || !foodSettingsEnabled) return;

    const count = parseInt(feedCount) || 1;

    if (c.mealsToday + count > 2) return window.alert('Max 2 meals/day!');

    const actualType = feedType || Object.keys(inv)[0];

    if (!actualType) return window.alert('No food type selected');

    if (feedWithdraw && (!inv[actualType] || inv[actualType] < count)) return window.alert('Not enough food!');

    const cs = { ...chars }, x = cs[c.id], s = x.settings || DEFAULT_SETTINGS;

    for (let i = 0; i < count; i++) { x.mealsToday += 1; if (x.daysWithoutFood > 0) x.daysWithoutFood -= 1; if (x.daysToStarvation < s.starvationThreshold) x.daysToStarvation += 1; x.mealLog = [{ day: x.currentDay, mealType: actualType, timestamp: new Date().toISOString() }, ...(x.mealLog || []).slice(0, 59)]; }

    if (x.mealsToday >= 2 && x.daysWithoutFood === 0) Object.assign(x, applyWellFedState(x, s));

    saveChars(cs, { perCharMeta: { [c.id]: { summary: `🍽️ Meal given: ${x.name} ate ${count}× ${actualType}${feedWithdraw ? '' : ' (no pool withdraw)'}`, category: 'inventory', action: 'meal.given' } } });

    if (feedWithdraw) saveFood({ ...food, totalUnits: (food.totalUnits || 0) - count, foodLog: [{ date: new Date().toISOString(), unitsConsumed: count, character: x.name, foodType: actualType }, ...(food.foodLog || []).slice(0, 99)] }, { summary: `🍖 Food pool -${count} (${actualType}) • fed ${x.name}`, category: 'inventory', action: 'food.consume', note: `${x.name} ate ${count}× ${actualType}` });

    if (typeof saveGroupTravelLog === 'function') {
      saveGroupTravelLog([{ day: x.currentDay, character: x.name, action: 'meal', foodType: actualType, count, withdrawFood: feedWithdraw, timestamp: new Date().toISOString() }, ...(groupTravelLog || []).slice(0, 199)]);
    }

    setShowFeed(false); setFeedType(''); setFeedCount('1'); setFeedWithdraw(true);

  };

  const resetXpAwardDrafts = useCallback(() => {

    setXpAwardDraft('');

    setXpAwardAll(false);

  }, []);

  const closeXpAward = useCallback(() => {

    setShowXpAward(false);

    resetXpAwardDrafts();

  }, [resetXpAwardDrafts]);

  const toggleXpAward = useCallback((event) => {

    event?.stopPropagation?.();

    if (showXpAward) {

      closeXpAward();

      return;

    }

    setShowXpAward(true);

  }, [closeXpAward, showXpAward]);

  const submitXpAward = useCallback(() => {

    const amount = parseInt(xpAwardDraft, 10) || 0;

    if (amount <= 0) return;

    const thresholds = globalSettings?.xpThresholds || XP_DEFAULTS;

    if (xpAwardAll) {

      const levelUps = [];

      const nextChars = { ...chars };

      Object.values(chars).forEach(ch => {

        const newXp = (ch.xp || 0) + amount;

        const updates = { xp: newXp };

        let newLevel = ch.level || 1;

        for (let lv = newLevel + 1; lv <= 20; lv++) {

          if (thresholds[lv] && newXp >= thresholds[lv]) newLevel = lv; else break;

        }

        if (newLevel > (ch.level || 1)) {

          updates.level = newLevel;

          levelUps.push(`${ch.name} → Lv ${newLevel}`);

        }

        nextChars[ch.id] = { ...ch, ...updates };

      });

      saveChars(nextChars);

      if (levelUps.length > 0) window.alert(`🎉 Level ups!\n${levelUps.join('\n')}`);

      closeXpAward();

      return;

    }

    const newXp = (c.xp || 0) + amount;

    const updates = { xp: newXp };

    let newLevel = c.level || 1;

    for (let lv = newLevel + 1; lv <= 20; lv++) {

      if (thresholds[lv] && newXp >= thresholds[lv]) newLevel = lv; else break;

    }

    if (newLevel > (c.level || 1)) {

      updates.level = newLevel;

      window.alert(`🎉 ${c.name} leveled up to Level ${newLevel}!`);

    }

    saveChars({ ...chars, [c.id]: { ...c, ...updates } });

    closeXpAward();

  }, [c, chars, closeXpAward, globalSettings?.xpThresholds, saveChars, xpAwardAll, xpAwardDraft]);



  // Check if any buff/debuff has glow - need to inject keyframes

  const hasAnyBuffGlow = Object.values(glowingBuffs?.[c.id] || {}).length > 0;



  return <>

    {hasAnyBuffGlow && (

      <style>{`

        @keyframes buffGlow {

          0%, 100% { box-shadow: 0 0 8px var(--glow-color, #ffd700), 0 0 16px var(--glow-color, #ffd700)44; }

          50% { box-shadow: 0 0 16px var(--glow-color, #ffd700), 0 0 24px var(--glow-color, #ffd700)88; }

        }

      `}</style>

    )}

    <HoverCard>

    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>

      <div><h3 style={{ color: '#ffd700', margin: 0 }}>{c.name}</h3><small style={{ opacity: 0.7 }}>{c.race} • Lvl {c.level || 1}</small></div>

      <div><Tooltip text="View Character"><button aria-label={`${c.name} character sheet`} style={{ ...S.btn, ...S.btnXs }} onClick={onViewHome}>👤</button></Tooltip>{onViewTravel && <Tooltip text="View Travel Log"><button aria-label={`${c.name} travel log`} style={{ ...S.btn, ...S.btnSuccess, ...S.btnXs }} onClick={onViewTravel}>📜</button></Tooltip>}</div>

    </div>

    {/* XP Bar */}

    {globalSettings?.xpTrackingEnabled !== false && (c.xp !== undefined && c.xp !== null) && (() => {

      const xp = getXpProgress(c, globalSettings);

      return (

        <div style={{ marginBottom: '6px', position: 'relative' }}>

          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>

            <span style={{ fontSize: '0.6rem', color: '#888', minWidth: '16px' }}>XP</span>

            <div style={{ flex: 1, height: '8px', background: 'rgba(0,0,0,0.3)', borderRadius: '4px', overflow: 'hidden' }}>

              <div style={{ height: '100%', width: `${xp.pct}%`, background: xp.isMax ? '#ffd700' : 'linear-gradient(90deg, #5b3a8c, #8b5fcf)', borderRadius: '4px', transition: 'width 0.3s' }} />

            </div>

            <span style={{ fontSize: '0.55rem', color: '#888', minWidth: '55px', textAlign: 'right' }}>{xp.currentXp.toLocaleString()}/{xp.isMax ? 'MAX' : xp.xpForNext.toLocaleString()}</span>

            <button aria-label={`Open ${c.name} XP award`} style={{ ...S.btn, padding: '0 4px', fontSize: '0.55rem', lineHeight: '14px', minWidth: '18px', background: 'rgba(39,201,90,0.15)', border: '1px solid rgba(39,201,90,0.3)', color: '#27c95a' }} onClick={toggleXpAward}>+</button>

          </div>

          {showXpAward && (

            <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 20, background: 'rgba(30,22,15,0.98)', border: '1px solid rgba(201,162,39,0.4)', borderRadius: '6px', padding: '8px', minWidth: '180px', boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }} onClick={e => e.stopPropagation()}>

              <div style={{ fontSize: '0.7rem', color: '#c9a227', marginBottom: '6px', fontWeight: 'bold' }}>Award XP</div>

              <ControlledMiniForm
                style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}
                onSubmit={submitXpAward}
                actionsStyle={{ display: 'flex', alignItems: 'center' }}
                submitLabel="Award"
                submitButtonStyle={{ ...S.btn, ...S.btnSm, fontSize: '0.65rem', padding: '3px 8px', background: 'rgba(39,201,90,0.2)', border: '1px solid rgba(39,201,90,0.4)', color: '#27c95a', marginBottom: 0 }}
              >

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>

                  <input
                    type="number"
                    placeholder="Amount"
                    aria-label={`${c.name} XP award amount`}
                    style={{ ...S.input, marginBottom: 0, padding: '3px 6px', fontSize: '0.7rem', width: '100%' }}
                    min="1"
                    autoFocus
                    value={xpAwardDraft}
                    onChange={event => setXpAwardDraft(event.target.value)}
                  />

                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.6rem', color: '#888', cursor: 'pointer' }}>

                    <input type="checkbox" checked={xpAwardAll} onChange={event => setXpAwardAll(event.target.checked)} /> Award to all players

                  </label>

                </div>

              </ControlledMiniForm>

            </div>

          )}

        </div>

      );

    })()}

    {/* HP Bar with inline quick controls */}

    <DashboardInlineResourceControl
      value={c.currentHp}
      max={effectiveMaxHp}
      temp={c.tempHp || 0}
      onCommit={commitDmCardHp}
      label={`${c.name} HP`}
      kind="hp"
      height={18}
      fontSize="0.75rem"
      buttonWidth={30}
      buttonHeight={24}
      gap={10}
    />

    {/* Mana Bar with shared stepper (if character has mana) */}

    {showMana && (

      <div style={{ marginBottom: '6px' }}>

        <DashboardInlineResourceControl
          value={c.currentMana || 0}
          max={c.maxMana || 0}
          onCommit={commitDmCardMana}
          label={`${c.name} mana`}
          kind="mana"
          height={14}
          fontSize="0.65rem"
          buttonWidth={30}
          buttonHeight={22}
          gap={10}
        />

      </div>

    )}

    <div style={{ ...S.flex, marginBottom: '8px' }}><input style={{ ...S.input, width: '50px', marginBottom: 0, padding: '4px' }} type="number" placeholder="HP" value={hpIn} onChange={e => setHpIn(e.target.value)} /><button style={{ ...S.btn, ...S.btnSuccess, ...S.btnXs }} onClick={() => hpIn && updateHP(hpIn)}>✓</button></div>

    <div style={{ fontSize: '0.8rem', marginBottom: '6px' }}>
      Day {c.currentDay}
      {hungerEnabled && <> | Meals {c.mealsToday}/2 | Starv: <span style={{ color: getStarvColor(c.daysToStarvation, set.starvationThreshold) }}>{c.daysToStarvation}</span></>}
    </div>

    {hungerEnabled && <div style={{ marginBottom: '6px' }}>{wellFed && <span style={{ ...S.badge, ...S.badgeBuff, padding: '2px 8px' }}>Fed</span>}{hungry && <span style={{ ...S.badge, ...S.badgeDebuff, padding: '2px 8px' }}>Hungry</span>}{starving && <span style={{ ...S.badge, ...S.badgeDebuff, padding: '2px 8px' }}>Starving</span>}</div>}

    <div style={{ marginBottom: '6px', fontSize: '0.75rem' }}>

      {dmCardActiveEffects.map((effect, index) => {

        const badgeStyle = effect.category === 'debuff'

          ? (effect.isPermanent ? S.badgeDebuffPerm : S.badgeDebuff)

          : (effect.isPermanent ? S.badgeBuffPerm : S.badgeBuff);

        return (

          <span

            key={effect.effectId || effect.legacyKey || `dmcard-effect-${index}`}

            style={{

              ...S.badge,

              ...badgeStyle,

              padding: '2px 6px',

              fontSize: '0.7rem',

              ...(effect.isHidden ? { opacity: 0.7, fontStyle: 'italic' } : {}),

            }}

          >

            {effect.text}

          </span>

        );

      })}

    </div>

    {diseasesEnabled && (c.diseaseList || []).length > 0 && <div style={{ marginBottom: '6px', fontSize: '0.75rem' }}>{c.diseaseList.map((d, i) => <DiseaseBadge key={i} diseaseData={d} disease={diseases[d.diseaseId]} isPlayer={false} />)}</div>}

    {systemListForCard.length > 0 && <div style={{ marginBottom: '6px', fontSize: '0.75rem' }}>{systemListForCard.map((s, i) => {

      const sys = systemsForCard[s.systemId] || s.template;

      const instanceId = s.instanceId || s.id || s.systemId;

      // Check if this system has notifyGlow enabled

      const hasNotifyGlow = s.notifyGlow;

      const glowColorId = s.glowColorId || 'gold';

      const glowColorOpt = BUFF_COLOR_OPTIONS.find(co => co.id === glowColorId);

      

      return (

        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', marginRight: '4px' }}>

          <SystemBadge systemData={s} system={sys} isPlayer={false} char={c} />

          {hasNotifyGlow && addSystemGlow && (

            <Tooltip text="Send glow notification to player">

              <button 

                style={{ 

                  ...S.btn, 

                  ...S.btnXs, 

                  marginLeft: '2px', 

                  marginBottom: 0, 

                  padding: '1px 4px', 

                  fontSize: '0.6rem',

                  background: `linear-gradient(180deg, ${glowColorOpt?.color || '#ffd700'} 0%, ${glowColorOpt?.color || '#ffd700'}88 100%)`,

                }}

                onClick={() => addSystemGlow(c.id, instanceId, glowColorOpt?.color || '#ffd700')}

              >✨</button>

            </Tooltip>

          )}

        </span>

      );

    })}</div>}

    

    {hungerEnabled && foodSettingsEnabled && <button style={{ ...S.btn, ...S.btnSuccess, ...S.btnXs }} onClick={() => setShowFeed(!showFeed)}>🍖 Provide Rations {showFeed ? '▲' : '▼'}</button>}

    {hungerEnabled && foodSettingsEnabled && showFeed && <div style={{ marginTop: '6px', padding: '8px', background: 'rgba(0,0,0,0.3)', borderRadius: '4px', fontSize: '0.8rem' }}>

      <div style={S.flex}><select style={{ ...S.select, flex: 1, marginBottom: 0, padding: '6px', fontSize: '0.8rem' }} value={feedType} onChange={e => setFeedType(e.target.value)}><option value="">Any type...</option>{foodTypes.map(t => <option key={t} value={t}>{t} ({inv[t] || 0})</option>)}</select><input style={{ ...S.input, width: '40px', marginBottom: 0, padding: '4px', fontSize: '0.8rem' }} type="number" min="1" max="2" value={feedCount} onChange={e => setFeedCount(e.target.value)} /></div>

      <label style={{ display: 'block', marginTop: '6px', cursor: 'pointer', fontSize: '0.8rem' }}><input type="checkbox" checked={feedWithdraw} onChange={e => setFeedWithdraw(e.target.checked)} /> Withdraw from food pool</label>

      <button style={{ ...S.btn, ...S.btnSuccess, ...S.btnXs, marginTop: '6px' }} onClick={doFeed}>✓ Give</button>

    </div>}

    

    <button style={{ ...S.btn, ...S.btnEdit, ...S.btnXs, width: '100%', marginTop: '6px' }} onClick={() => setShowFx(!showFx)}>⚡ Buffs {showFx ? '▲' : '▼'}</button>

    {showFx && <div style={{ marginTop: '6px', padding: '8px', background: 'rgba(0,0,0,0.3)', borderRadius: '4px', fontSize: '0.8rem' }}>

      {/* Quick Effect Type Buttons */}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>

        {EFFECT_TYPE_PRESETS.slice(0, 6).map(preset => {

          const colorOpt = BUFF_COLOR_OPTIONS.find(co => co.id === preset.color) || BUFF_COLOR_OPTIONS[0];

          return (

            <Tooltip key={preset.id} text={preset.description}>

              <button

                style={{

                  ...S.btn,

                  ...S.btnXs,

                  padding: '2px 6px',

                  fontSize: '0.65rem',

                  background: colorOpt.bgColor,

                  borderColor: colorOpt.color,

                  color: colorOpt.textColor || colorOpt.color,

                  marginBottom: 0,

                }}

                onClick={() => {

                  setBuffColor(preset.color);

                  // Only set icon if buff is empty, otherwise prepend

                  if (!buff.trim()) {

                    setBuff(preset.icon + ' ');

                  } else if (!buff.startsWith(preset.icon)) {

                    // Remove any existing preset icon and add new one

                    const cleaned = buff.replace(/^[^\w\s]+\s*/, '').trim();

                    setBuff(preset.icon + ' ' + cleaned);

                  }

                }}

              >

                {preset.icon}

              </button>

            </Tooltip>

          );

        })}

      </div>

      

      {/* Buff Input */}

      <div style={{ marginBottom: '8px' }}>

        <div style={S.flex}>

          <input style={{ ...S.input, flex: 1, marginBottom: 0, padding: '6px', fontSize: '0.8rem' }} placeholder="+2 Str or ⛓️ Oath of Silence" value={buff} onChange={e => setBuff(e.target.value)} />

          <button style={{ ...S.btn, ...S.btnSuccess, ...S.btnXs }} onClick={addBuff}>+</button>

          <Tooltip text="Permanent effects persist across rests">

            <label style={{ display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer', fontSize: '0.85rem', color: buffIsPermanent ? '#ffd700' : '#666', padding: '0 4px' }}>

              <input type="checkbox" checked={buffIsPermanent} onChange={e => setBuffIsPermanent(e.target.checked)} />

              ∞

            </label>

          </Tooltip>

        </div>

        <details style={{ marginTop: '4px', fontSize: '0.75rem' }}>

          <summary style={{ cursor: 'pointer', color: '#888' }}>⚙️ Options</summary>

          <div style={{ padding: '6px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px', marginTop: '4px' }}>

            {/* Player Tooltip - always visible to player */}

            <div style={{ marginBottom: '6px' }}>

              <label style={{ display: 'block', fontSize: '0.7rem', color: '#4ade80', marginBottom: '2px' }}>👁 Player Tooltip (always visible):</label>

              <input 

                style={{ ...S.input, marginBottom: 0, padding: '4px', fontSize: '0.75rem', width: '100%' }} 

                placeholder="Granted by the Forest Spirit" 

                value={buffPlayerTooltip || ''} 

                onChange={e => setBuffPlayerTooltip(e.target.value)} 

              />

            </div>

            {/* DM Note - optionally visible */}

            <div style={{ marginBottom: '4px' }}>

              <label style={{ display: 'block', fontSize: '0.7rem', color: '#c9a227', marginBottom: '2px' }}>🔒 DM Note:</label>

              <textarea 

                style={{ ...S.input, marginBottom: 0, padding: '4px', fontSize: '0.75rem', width: '100%', minHeight: '36px', resize: 'vertical' }} 

                placeholder="Secret: Will betray them at the bridge..." 

                value={buffSource || ''} 

                onChange={e => setBuffSource(e.target.value)} 

              />

            </div>

            {buffSource.trim() && (

              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.7rem', color: '#888', marginBottom: '4px' }}>

                <input type="checkbox" checked={buffNoteVisible} onChange={e => setBuffNoteVisible(e.target.checked)} />

                Also show DM note to player

              </label>

            )}

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>

              <label style={{ fontSize: '0.7rem', color: '#888' }}>Color:</label>

              <select 

                style={{ ...S.select, flex: 1, marginBottom: 0, padding: '2px', fontSize: '0.7rem' }}

                value={buffColor || 'default-buff'}

                onChange={e => setBuffColor(e.target.value)}

              >

                {BUFF_COLOR_OPTIONS.map(opt => (

                  <option key={opt.id} value={opt.id}>{opt.name}</option>

                ))}

              </select>

            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>

              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.7rem', color: buffIsPermanent ? '#ffd700' : '#888' }}>

                <input type="checkbox" checked={buffIsPermanent} onChange={e => setBuffIsPermanent(e.target.checked)} />

                ∞ Permanent

              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.7rem', color: '#ffd700' }}>

                <input type="checkbox" checked={buffNotifyGlow || false} onChange={e => setBuffNotifyGlow(e.target.checked)} />

                ✨ Glow

              </label>

            </div>

          </div>

        </details>

      </div>

      

      {/* Buff List */}

      {((c.buffList || []).length > 0 || (c.permanentBuffs || []).length > 0) && (

        <div style={{ marginBottom: '8px' }}>

          {(c.buffList || []).map((b, i) => {

            const occ = getGlobalOccurrence(b, i, c.buffList || [], c.permanentBuffs || []);

            const buffData = lookupBuffData(b, occ, c.buffData) || {};

            const colorOpt = BUFF_COLOR_OPTIONS.find(co => co.id === buffData.color) || BUFF_COLOR_OPTIONS[0];

            // 2026-04-29: per-instance glow lookup (same as StatusWindow).

            const glowKey = buffInstanceKey(b, occ);

            const hasGlow = (glowingBuffs?.[c.id] || {})[glowKey];

            const hasTooltipContent = buffData.playerTooltip || buffData.source;

            const tooltipContent = hasTooltipContent ? (

              <div style={{ fontSize: '0.8rem' }}>

                {buffData.playerTooltip && (

                  <div style={{ color: '#4ade80', marginBottom: '4px' }}>

                    👁 {buffData.playerTooltip}

                  </div>

                )}

                {buffData.source && (

                  <div style={{ color: buffData.noteVisible ? '#fbbf24' : '#888' }}>
                    {buffData.noteVisible ? 'ð' : 'ð'} {buffData.source}

                    <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>

                      {buffData.noteVisible ? ' (visible to player)' : ' (hidden)'}

                    </span>

                  </div>

                )}

              </div>

            ) : b;

            return (

              <Tooltip key={i} text={tooltipContent} special={hasTooltipContent}>

                <span 

                  style={{ 

                    ...S.badge, 

                    ...S.badgeBuff, 

                    background: colorOpt.bgColor,

                    borderColor: colorOpt.color,

                    color: colorOpt.textColor || colorOpt.color,

                    fontWeight: '500',

                    padding: '2px 6px', 

                    cursor: 'pointer', 

                    fontSize: '0.7rem',

                    boxShadow: hasGlow ? `0 0 10px ${hasGlow.color}, 0 0 20px ${hasGlow.color}55` : 'none',

                    animation: hasGlow ? 'buffGlow 1.5s ease-in-out infinite' : 'none',

                  }} 

                  onClick={() => removeBuff({ text: b, permanent: false, index: i })}

                  onMouseEnter={() => hasGlow && clearBuffGlow && clearBuffGlow(c.id, glowKey)}

                >

                  {b} ✕

                </span>

              </Tooltip>

            );

          })}

          {(c.permanentBuffs || []).map((b, i) => {

            const buffData = lookupBuffData(b, getGlobalOccurrence(b, i, c.permanentBuffs || []), c.buffData) || {};

            const hasTooltipContent = buffData.playerTooltip || buffData.source;

            const tooltipContent = hasTooltipContent ? (

              <div style={{ fontSize: '0.8rem' }}>

                {buffData.playerTooltip && (

                  <div style={{ color: '#4ade80', marginBottom: '4px' }}>

                    👁 {buffData.playerTooltip}

                  </div>

                )}

                {buffData.source && (

                  <div style={{ color: buffData.noteVisible ? '#fbbf24' : '#888' }}>
                    {buffData.noteVisible ? 'ð' : 'ð'} {buffData.source}

                    <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>

                      {buffData.noteVisible ? ' (visible to player)' : ' (hidden)'}

                    </span>

                  </div>

                )}

              </div>

            ) : `${b} (Permanent)`;

            return (

              <Tooltip key={i} text={tooltipContent} special={hasTooltipContent}>

                <span style={{ ...S.badge, ...S.badgeBuffPerm, padding: '2px 6px', cursor: 'pointer', fontSize: '0.7rem' }} onClick={() => removeBuff({ text: b, permanent: true, index: i })}>{b} ✕</span>

              </Tooltip>

            );

          })}

        </div>

      )}

      

      {/* Debuff Input */}

      <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px' }}>

        <div style={S.flex}>

          <input style={{ ...S.input, flex: 1, marginBottom: 0, padding: '6px', fontSize: '0.8rem' }} placeholder="-1 Con or 💀 Curse" value={debuff} onChange={e => setDebuff(e.target.value)} />

          <button style={{ ...S.btn, ...S.btnDanger, ...S.btnXs }} onClick={addDebuff}>+</button>

          <Tooltip text="Permanent effects persist across rests">

            <label style={{ display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer', fontSize: '0.85rem', color: debuffIsPermanent ? '#ffd700' : '#666', padding: '0 4px' }}>

              <input type="checkbox" checked={debuffIsPermanent} onChange={e => setDebuffIsPermanent(e.target.checked)} />

              ∞

            </label>

          </Tooltip>

        </div>

        <details style={{ marginTop: '4px', fontSize: '0.75rem' }}>

          <summary style={{ cursor: 'pointer', color: '#888' }}>⚙️ Options</summary>

          <div style={{ padding: '6px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px', marginTop: '4px' }}>

            {/* Player Tooltip - always visible to player */}

            <div style={{ marginBottom: '6px' }}>

              <label style={{ display: 'block', fontSize: '0.7rem', color: '#ff8080', marginBottom: '2px' }}>👁 Player Tooltip (always visible):</label>

              <input 

                style={{ ...S.input, marginBottom: 0, padding: '4px', fontSize: '0.75rem', width: '100%' }} 

                placeholder="Cursed by the witch" 

                value={debuffPlayerTooltip || ''} 

                onChange={e => setDebuffPlayerTooltip(e.target.value)} 

              />

            </div>

            {/* DM Note - optionally visible */}

            <div style={{ marginBottom: '4px' }}>

              <label style={{ display: 'block', fontSize: '0.7rem', color: '#c9a227', marginBottom: '2px' }}>🔒 DM Note:</label>

              <textarea 

                style={{ ...S.input, marginBottom: 0, padding: '4px', fontSize: '0.75rem', width: '100%', minHeight: '36px', resize: 'vertical' }} 

                placeholder="Secret: Curse will spread to party..." 

                value={debuffSource || ''} 

                onChange={e => setDebuffSource(e.target.value)} 

              />

            </div>

            {debuffSource.trim() && (

              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.7rem', color: '#888', marginBottom: '4px' }}>

                <input type="checkbox" checked={debuffNoteVisible} onChange={e => setDebuffNoteVisible(e.target.checked)} />

                Also show DM note to player

              </label>

            )}

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>

              <label style={{ fontSize: '0.7rem', color: '#888' }}>Color:</label>

              <select 

                style={{ ...S.select, flex: 1, marginBottom: 0, padding: '2px', fontSize: '0.7rem' }}

                value={debuffColor || 'default-debuff'}

                onChange={e => setDebuffColor(e.target.value)}

              >

                {BUFF_COLOR_OPTIONS.map(opt => (

                  <option key={opt.id} value={opt.id}>{opt.name}</option>

                ))}

              </select>

            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>

              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.7rem', color: debuffIsPermanent ? '#ffd700' : '#888' }}>

                <input type="checkbox" checked={debuffIsPermanent} onChange={e => setDebuffIsPermanent(e.target.checked)} />

                ∞ Permanent

              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.7rem', color: '#ffd700' }}>

                <input type="checkbox" checked={debuffNotifyGlow || false} onChange={e => setDebuffNotifyGlow(e.target.checked)} />

                ✨ Glow

              </label>

            </div>

          </div>

        </details>

      </div>

      

      {/* Debuff List */}

      {((c.debuffList || []).length > 0 || (c.permanentDebuffs || []).length > 0) && (

        <div style={{ marginTop: '4px' }}>

          {(c.debuffList || []).map((d, i) => {

            const occ = getGlobalOccurrence(d, i, c.debuffList || [], c.permanentBuffs || [], c.buffList || [], c.permanentDebuffs || []);

            const debuffData = lookupBuffData(d, occ, c.buffData) || {};

            const colorOpt = BUFF_COLOR_OPTIONS.find(co => co.id === debuffData.color) || BUFF_COLOR_OPTIONS[1];

            // 2026-04-29: per-instance glow lookup (same as StatusWindow).

            const glowKey = buffInstanceKey(d, occ);

            const hasGlow = (glowingBuffs?.[c.id] || {})[glowKey];

            const hasTooltipContent = debuffData.playerTooltip || debuffData.source;

            const tooltipContent = hasTooltipContent ? (

              <div style={{ fontSize: '0.8rem' }}>

                {debuffData.playerTooltip && (

                  <div style={{ color: '#4ade80', marginBottom: '4px' }}>

                    👁 {debuffData.playerTooltip}

                  </div>

                )}

                {debuffData.source && (

                  <div style={{ color: debuffData.noteVisible ? '#fbbf24' : '#888' }}>
                    {debuffData.noteVisible ? 'ð' : 'ð'} {debuffData.source}

                    <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>

                      {debuffData.noteVisible ? ' (visible to player)' : ' (hidden)'}

                    </span>

                  </div>

                )}

              </div>

            ) : d;

            return (

              <Tooltip key={i} text={tooltipContent} special={hasTooltipContent}>

                <span 

                  style={{ 

                    ...S.badge, 

                    ...S.badgeDebuff, 

                    background: colorOpt.bgColor,

                    borderColor: colorOpt.color,

                    color: colorOpt.textColor || colorOpt.color,

                    fontWeight: '500',

                    padding: '2px 6px', 

                    cursor: 'pointer', 

                    fontSize: '0.7rem',

                    boxShadow: hasGlow ? `0 0 10px ${hasGlow.color}, 0 0 20px ${hasGlow.color}55` : 'none',

                    animation: hasGlow ? 'buffGlow 1.5s ease-in-out infinite' : 'none',

                  }} 

                  onClick={() => removeDebuff({ text: d, permanent: false, index: i })}

                  onMouseEnter={() => hasGlow && clearBuffGlow && clearBuffGlow(c.id, glowKey)}

                >

                  {d} ✕

                </span>

              </Tooltip>

            );

          })}

          {(c.permanentDebuffs || []).map((d, i) => {

            const debuffData = lookupBuffData(d, getGlobalOccurrence(d, i, c.permanentDebuffs || [], c.permanentBuffs || [], c.buffList || []), c.buffData) || {};

            const hasTooltipContent = debuffData.playerTooltip || debuffData.source;

            const tooltipContent = hasTooltipContent ? (

              <div style={{ fontSize: '0.8rem' }}>

                {debuffData.playerTooltip && (

                  <div style={{ color: '#4ade80', marginBottom: '4px' }}>

                    👁 {debuffData.playerTooltip}

                  </div>

                )}

                {debuffData.source && (

                  <div style={{ color: debuffData.noteVisible ? '#fbbf24' : '#888' }}>
                    {debuffData.noteVisible ? 'ð' : 'ð'} {debuffData.source}

                    <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>

                      {debuffData.noteVisible ? ' (visible to player)' : ' (hidden)'}

                    </span>

                  </div>

                )}

              </div>

            ) : `${d} (Permanent)`;

            return (

              <Tooltip key={i} text={tooltipContent} special={hasTooltipContent}>

                <span style={{ ...S.badge, ...S.badgeDebuffPerm, padding: '2px 6px', cursor: 'pointer', fontSize: '0.7rem' }} onClick={() => removeDebuff({ text: d, permanent: true, index: i })}>{d} ✕</span>

              </Tooltip>

            );

          })}

        </div>

      )}

    </div>}

    

    {diseasesEnabled && <button style={{ ...S.btn, ...S.btnEdit, ...S.btnXs, width: '100%', marginTop: '6px' }} onClick={() => setShowDis(!showDis)}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><BiohazardIcon size={13} color='currentColor' /> Diseases</span> {showDis ? '▲' : '▼'}</button>}

    {diseasesEnabled && showDis && <DiseaseManager c={c} diseases={diseases} locations={locations} currentLocation={currentLocation} filteredDiseases={filteredDiseases} diseaseFilter={diseaseFilter} setDiseaseFilter={setDiseaseFilter} addDisease={addDisease} removeDisease={removeDisease} updateDiseaseVisibility={updateDiseaseVisibility} addImmunity={addImmunity} removeImmunity={removeImmunity} />}





    {systemsEnabled && <button style={{ ...S.btn, ...S.btnEdit, ...S.btnXs, width: '100%', marginTop: '6px' }} onClick={() => setShowSys(!showSys)}>🔒 Systems {showSys ? '▲' : '▼'}</button>}

    {systemsEnabled && showSys && <SystemManager c={c} systems={systemsForCard} locations={locations} currentLocation={currentLocation} filteredSystems={filteredSystems} systemFilter={systemFilter} setSystemFilter={setSystemFilter} addSystem={addSystem} addInlineSystem={addInlineSystem} removeSystem={removeSystem} updateSystemVisibility={updateSystemVisibility} applyManualRuleResults={applyManualRuleResults} onEditSystem={onEditSystem} />}



    

    {/* Quick Message */}

    <QuickDMMessage char={c} messages={messages} onSaveMessages={onSaveMessages} onSendStatusMessage={onSendStatusMessage} />

    {systemsEnabled && activeSystemRulePendingSave && (
      <RequireSaveModal
        key={`${activeSystemRuleSaveEntry?.id || 'system-save'}:${systemRuleSaveQueue.length}`}
        pendingSave={activeSystemRulePendingSave}
        char={activeSystemRuleSaveChar}
        saveContext={activeSystemRuleSaveContext}
        saveAdvantage={activeSystemRuleSaveAdvantage}
        onResolve={handleSystemRuleSaveResolve}
        onSkip={handleSystemRuleSaveSkip}
      />
    )}

  </HoverCard></>;

}



// ============================================================================

// COMPACT DM CARD - Minimal view with popup for buff/debuff

// ============================================================================

export function CompactDMCard({ c, diseases, systems, systemsEnabled = true, manaEnabled = true, hungerEnabled = true, diseasesEnabled = true, foodSettingsEnabled = true, diets, foodTypes, inv, chars, saveChars, patchCharacterVitals, food, saveFood, onViewHome, onViewTravel, onNotify, messages, onSaveMessages, onSendStatusMessage, isSelected, onSelect, glowingBuffs, addBuffGlow, addBuffGlowPatch, addSystemGlowPatch }) {

  const [showAddPopup, setShowAddPopup] = useState(false);

  const [isHovered, setIsHovered] = useState(false);

  

  const set = syncSpellSettings(c.settings || DEFAULT_SETTINGS);

  const showMana = manaEnabled && isManaSpellSystem(set) && (c.maxMana || 0) > 0;

  const systemsForCompactCard = systemsEnabled ? (systems || {}) : {};

  const systemListForCompactCard = systemsEnabled ? (c.systemList || []) : [];

  void foodSettingsEnabled;

  const wellFed = hungerEnabled && c.mealsToday >= 2 && c.daysWithoutFood === 0;

  const hungry = hungerEnabled && c.daysWithoutFood >= set.hungerThreshold && !c.starving;

  const starving = hungerEnabled && c.daysToStarvation === 0;

  const effectiveMaxHp = getEffectiveMaxHp(c);

  const compactCardArmorContext = getResolvedCharacterArmorContext(

    c,

    diseases,

    systemsForCompactCard,

    false,

    c.settings?.armorTypes || DEFAULT_ARMOR_TYPES,

  );



  const notifyVitalsPatchFailure = (error) => {
    if (typeof onNotify === 'function') {
      onNotify(error?.message || 'Unable to update character vitals.');
    }
  };

  const commitHp = (nextHp) => {

    const beforeHp = normalizeVitalNumber(c.currentHp);
    const fallback = () => {
      saveChars({ ...chars, [c.id]: { ...c, currentHp: nextHp } });
    };
    if (typeof patchCharacterVitals !== 'function') {
      fallback();
      return;
    }
    void tryPatchCharacterVitals({
      patchCharacterVitals,
      charId: c.id,
      updates: { currentHp: nextHp },
      expected: { currentHp: beforeHp },
      surface: 'compact-dm-card.hp-stepper',
      onError: notifyVitalsPatchFailure,
    }).then((handled) => {
      if (!handled) fallback();
    });

  };



  const commitMana = (nextMana) => {

    if (!showMana) return;

    const beforeMana = normalizeVitalNumber(c.currentMana);
    const fallback = () => {
      saveChars({ ...chars, [c.id]: { ...c, currentMana: nextMana } });
    };
    if (typeof patchCharacterVitals !== 'function') {
      fallback();
      return;
    }
    void tryPatchCharacterVitals({
      patchCharacterVitals,
      charId: c.id,
      updates: { currentMana: nextMana },
      expected: { currentMana: beforeMana },
      surface: 'compact-dm-card.mana-stepper',
      onError: notifyVitalsPatchFailure,
    }).then((handled) => {
      if (!handled) fallback();
    });

  };



  const stopCardSelect = (event) => {

    event.stopPropagation();

  };



  const hasAnyBuffGlow = Object.values(glowingBuffs?.[c.id] || {}).length > 0;

  const compactCardLegacyStatusView = buildLegacyStatusEffectView({

    char: c,

    glowingBuffsForChar: glowingBuffs?.[c.id] || {},

  });

  const compactCardActiveEffects = compactCardLegacyStatusView.activeEffects;



  return (

    <div 

      onClick={onSelect}

      onMouseEnter={() => setIsHovered(true)}

      onMouseLeave={() => setIsHovered(false)}

      style={{

        ...S.card,

        boxSizing: 'border-box',

        cursor: 'pointer',

        padding: '12px',

        transition: 'all 0.2s ease',

        border: isSelected ? '2px solid #ffd700' : (isHovered ? '2px solid #c9a227' : '2px solid transparent'),

        boxShadow: isSelected 

          ? '0 0 20px rgba(255,215,0,0.4), 0 0 40px rgba(255,215,0,0.2)' 

          : (isHovered ? '0 0 15px rgba(201,162,39,0.3)' : S.card.boxShadow),

        animation: isSelected ? 'selectedGlow 1.5s ease-in-out infinite' : 'none',

        position: 'relative',

      }}

    >

      {isSelected && (

        <style>{`

          @keyframes selectedGlow {

            0%, 100% { box-shadow: 0 0 15px rgba(255,215,0,0.4), 0 0 30px rgba(255,215,0,0.2); }

            50% { box-shadow: 0 0 25px rgba(255,215,0,0.6), 0 0 45px rgba(255,215,0,0.3); }

          }

        `}</style>

      )}

      {hasAnyBuffGlow && (

        <style>{`

          @keyframes buffGlow {

            0%, 100% { box-shadow: 0 0 8px var(--glow-color, #ffd700), 0 0 16px var(--glow-color, #ffd700)44; }

            50% { box-shadow: 0 0 16px var(--glow-color, #ffd700), 0 0 24px var(--glow-color, #ffd700)88; }

          }

        `}</style>

      )}



      {/* Header with buttons */}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>

        <div>

          <h3 style={{ color: '#ffd700', margin: 0, fontSize: '1.15rem', fontWeight: 'bold' }}>{c.name}</h3>

          <small style={{ opacity: 0.7, fontSize: '0.75rem' }}>{c.race} • {c.charClass} {c.level}</small>

        </div>

        <div style={{ display: 'flex', gap: '4px' }}>

          <button
            aria-pressed={!!isSelected}
            aria-label={isSelected ? `${c.name} selected` : `Select ${c.name}`}
            style={{
              ...S.btn,
              ...S.btnXs,
              padding: '4px 8px',
              ...(isSelected ? { background: 'rgba(255,215,0,0.25)', borderColor: '#ffd700', color: '#fff3b0' } : {}),
            }}
            onClick={(e) => { e.stopPropagation(); onSelect?.(); }}
          >
            {isSelected ? 'Selected' : 'Select'}
          </button>

          <Tooltip text="View Character"><button aria-label={`${c.name} character sheet`} style={{ ...S.btn, ...S.btnXs, padding: '4px 8px' }} onClick={(e) => { e.stopPropagation(); onViewHome(); }}>👤</button></Tooltip>

          {onViewTravel && <Tooltip text="View Travel Log"><button aria-label={`${c.name} travel log`} style={{ ...S.btn, ...S.btnSuccess, ...S.btnXs, padding: '4px 8px' }} onClick={(e) => { e.stopPropagation(); onViewTravel(); }}>📜</button></Tooltip>}

        </div>

      </div>



      {/* HP Bar with inline quick controls */}

      <DashboardInlineResourceControl
        value={c.currentHp}
        max={effectiveMaxHp}
        temp={c.tempHp || 0}
        onCommit={commitHp}
        label={`${c.name} HP`}
        kind="hp"
        height={16}
        fontSize="0.7rem"
        buttonWidth={30}
        buttonHeight={22}
        gap={10}
        onStopPropagation={stopCardSelect}
      />



      {/* Mana Bar with shared compact stepper (if character has mana) */}

      {showMana && (

        <div style={{ marginBottom: '4px' }}>

          <DashboardInlineResourceControl
            value={c.currentMana || 0}
            max={c.maxMana || 0}
            onCommit={commitMana}
            label={`${c.name} mana`}
            kind="mana"
            height={12}
            fontSize="0.6rem"
            buttonWidth={30}
            buttonHeight={20}
            gap={10}
            onStopPropagation={stopCardSelect}
          />

        </div>

      )}



      {/* Status Row */}

      <div style={{ fontSize: '0.7rem', marginBottom: '4px', opacity: 0.8 }}>

        Day {c.currentDay}{hungerEnabled ? ` | Meals ${c.mealsToday}/2` : ''} | AC {(compactCardArmorContext?.totalArmorClass ?? c.armorClass) || 10}

      </div>



      {/* Status Badges */}

      {hungerEnabled && <div style={{ marginBottom: '4px' }}>

        {wellFed && <span style={{ ...S.badge, ...S.badgeBuff, padding: '1px 6px', fontSize: '0.65rem' }}>Fed</span>}

        {hungry && <span style={{ ...S.badge, ...S.badgeDebuff, padding: '1px 6px', fontSize: '0.65rem' }}>Hungry</span>}

        {starving && <span style={{ ...S.badge, ...S.badgeDebuff, padding: '1px 6px', fontSize: '0.65rem' }}>Starving</span>}

      </div>}



      {/* Buffs/Debuffs Display with Add button */}

      <div style={{ marginBottom: '6px' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>

          <span style={{ fontSize: '0.65rem', opacity: 0.6 }}>Effects:</span>

          <button 

            onClick={(e) => { e.stopPropagation(); setShowAddPopup(true); }}

            style={{ ...S.btn, ...S.btnSuccess, padding: '1px 6px', fontSize: '0.6rem', lineHeight: 1.2 }}

          >

            + Add buff

          </button>

        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>

          {compactCardActiveEffects.map((effect, index) => {

            const key = effect.effectId || effect.legacyKey || `compact-effect-${index}`;

            const badgeStyle = effect.category === 'debuff'

              ? (effect.isPermanent ? S.badgeDebuffPerm : S.badgeDebuff)

              : (effect.isPermanent ? S.badgeBuffPerm : S.badgeBuff);

            const tooltipText = effect.playerTooltip || effect.source || null;

            const hiddenStyle = effect.isHidden ? { opacity: 0.7, fontStyle: 'italic' } : {};

            const glowStyle = effect.category === 'buff' && effect.glowColor

              ? { '--glow-color': effect.glowColor, animation: 'buffGlow 1.5s ease-in-out infinite' }

              : {};

            const badge = (

              <span

                style={{

                  ...S.badge,

                  ...badgeStyle,

                  padding: '1px 4px',

                  fontSize: '0.6rem',

                  ...hiddenStyle,

                  ...glowStyle,

                }}

              >

                {effect.text}

              </span>

            );

            return tooltipText

              ? <Tooltip key={key} text={tooltipText}>{badge}</Tooltip>

              : <span key={key} style={{

                  ...S.badge,

                  ...badgeStyle,

                  padding: '1px 4px',

                  fontSize: '0.6rem',

                  ...hiddenStyle,

                  ...glowStyle,

                }}>{effect.text}</span>;

          })}

          {compactCardActiveEffects.length === 0 && (

            <span style={{ fontSize: '0.6rem', opacity: 0.5, fontStyle: 'italic' }}>None</span>

          )}

        </div>

      </div>



      {/* Diseases & Systems badges */}

      {((diseasesEnabled && (c.diseaseList || []).length > 0) || systemListForCompactCard.length > 0) && (

        <div style={{ marginBottom: '6px', display: 'flex', flexWrap: 'wrap', gap: '2px' }}>

          {diseasesEnabled && (c.diseaseList || []).map((d, i) => (

            <DiseaseBadge key={`dis-${i}`} diseaseData={d} disease={diseases[d.diseaseId]} isPlayer={false} />

          ))}

          {systemListForCompactCard.map((s, i) => (

            <SystemBadge key={`sys-${i}`} systemData={s} system={systemsForCompactCard[s.systemId] || s.template} isPlayer={false} char={c} />

          ))}

        </div>

      )}



      {/* Whisper only */}

      <div onClick={(e) => e.stopPropagation()} style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '6px', marginTop: '4px' }}>

        <QuickDMMessage char={c} messages={messages} onSaveMessages={onSaveMessages} onSendStatusMessage={onSendStatusMessage} compact={true} />

      </div>



      {/* Selection Hint */}

      {isSelected && (

        <div style={{ marginTop: '8px', textAlign: 'center', fontSize: '0.65rem', color: '#ffd700', opacity: 0.8 }}>

          ▼ Expanded panel below ▼

        </div>

      )}

      

      {/* Add Effect Modal */}

      <AddEffectModal

        show={showAddPopup}

        onClose={() => setShowAddPopup(false)}

        c={c}

        chars={chars}

        saveChars={saveChars}

        addBuffGlow={addBuffGlow}

        addBuffGlowPatch={addBuffGlowPatch}

      />

    </div>

  );

}



// Separate Add Effect Modal component to render at top level

function AddEffectModal({ show, onClose, c, chars, saveChars, addBuffGlow, addBuffGlowPatch }) {

  const [isDebuffMode, setIsDebuffMode] = useState(false);

  const [effectText, setEffectText] = useState('');

  const [effectPlayerTooltip, setEffectPlayerTooltip] = useState('');

  const [effectDmNote, setEffectDmNote] = useState('');

  const [effectColor, setEffectColor] = useState('default-buff');

  const [effectIsPermanent, setEffectIsPermanent] = useState(false);

  const [effectNotifyGlow, setEffectNotifyGlow] = useState(true);

  const [showOptions, setShowOptions] = useState(false);



  const resetForm = () => {

    setEffectText('');

    setEffectPlayerTooltip('');

    setEffectDmNote('');

    setEffectColor(isDebuffMode ? 'default-debuff' : 'default-buff');

    setEffectIsPermanent(false);

    setEffectNotifyGlow(true);

    setShowOptions(false);

  };



  const handleModeChange = (debuff) => {

    setIsDebuffMode(debuff);

    setEffectColor(debuff ? 'default-debuff' : 'default-buff');

  };



  const handleAdd = () => {

    if (!effectText.trim() || !c) return;

    

    // 2026-04-27: fold glow into single save (race fix — see DMCard.addBuff).

    let nextChar = addLegacyEffect(c, {

      text: effectText,

      kind: isDebuffMode ? 'debuff' : 'buff',

      permanent: effectIsPermanent,

      allowDuplicates: false,

      metadata: buildLegacyEffectMetadata({

        source: effectDmNote || null,

        playerTooltip: effectPlayerTooltip || null,

        colorId: effectColor,

        addedBy: 'DM',

        noteVisible: !!effectDmNote,

      }),

    });

    if (nextChar === c) {

      resetForm();

      onClose();

      return;

    }

    if (effectNotifyGlow && addBuffGlowPatch) {

      const colorOpt = BUFF_COLOR_OPTIONS.find(co => co.id === effectColor);

      nextChar = addBuffGlowPatch(nextChar, effectText, colorOpt?.glowColor || colorOpt?.color || (isDebuffMode ? '#ff6961' : '#27c95a'));

    }

    const cs = { ...chars, [c.id]: nextChar };



    saveChars(cs);

    resetForm();

    onClose();

  };



  if (!show || !c) return null;



  const selectedColorOpt = BUFF_COLOR_OPTIONS.find(co => co.id === effectColor) || BUFF_COLOR_OPTIONS[0];



  return (

    <>

      {/* Backdrop */}

      <div 

        onClick={() => { resetForm(); onClose(); }}

        style={{

          position: 'fixed',

          top: 0,

          left: 0,

          right: 0,

          bottom: 0,

          background: 'rgba(0,0,0,0.75)',

          zIndex: 999998,

        }}

      />

      {/* Modal */}

      <div 

        onClick={(e) => e.stopPropagation()}

        style={{

          position: 'fixed',

          top: '50%',

          left: '50%',

          transform: 'translate(-50%, -50%)',

          width: '300px',

          maxWidth: '90vw',

          maxHeight: '80vh',

          overflowY: 'auto',

          padding: '12px',

          background: 'linear-gradient(135deg, #2a2118 0%, #1a1510 100%)',

          border: '2px solid #5c4a2a',

          borderRadius: '10px',

          boxShadow: '0 16px 64px rgba(0,0,0,0.95)',

          zIndex: 999999,

        }}

      >

        {/* Header */}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', paddingBottom: '6px', borderBottom: '1px solid rgba(255,215,0,0.3)' }}>

          <span style={{ color: '#ffd700', fontWeight: 'bold', fontSize: '0.85rem' }}>Add Effect: {c.name}</span>

          <button 

            style={{ ...S.btn, ...S.btnXs, padding: '2px 6px', fontSize: '0.65rem' }} 

            onClick={() => { resetForm(); onClose(); }}

          >✕</button>

        </div>

        

        {/* Quick Effect Type Buttons */}

        <div style={{ display: 'flex', gap: '2px', marginBottom: '6px', flexWrap: 'wrap' }}>

          {EFFECT_TYPE_PRESETS.map(preset => {

            const colorOpt = BUFF_COLOR_OPTIONS.find(co => co.id === preset.color) || BUFF_COLOR_OPTIONS[0];

            return (

              <Tooltip key={preset.id} text={preset.description}>

                <button

                  style={{

                    ...S.btn,

                    ...S.btnXs,

                    padding: '4px 6px',

                    fontSize: '0.8rem',

                    background: colorOpt.bgColor,

                    borderColor: colorOpt.color,

                    marginBottom: 0,

                  }}

                  onClick={() => {

                    setEffectColor(preset.color);

                    if (!effectText.trim()) {

                      setEffectText(preset.icon + ' ');

                    } else if (!effectText.startsWith(preset.icon)) {

                      const cleaned = effectText.replace(/^[^\w\s]+\s*/, '').trim();

                      setEffectText(preset.icon + ' ' + cleaned);

                    }

                  }}

                >{preset.svgIcon === 'biohazard' ? <BiohazardIcon size={14} color={colorOpt.textColor || colorOpt.color} /> : preset.svgIcon === 'plague-doctor' ? <PlagueDoctorIcon size={14} color={colorOpt.textColor || colorOpt.color} /> : preset.icon}</button>

              </Tooltip>

            );

          })}

        </div>



        {/* Effect Text Input */}

        <input 

          style={{ ...S.input, marginBottom: '8px', padding: '8px', fontSize: '0.85rem' }} 

          placeholder="+2 Str, ⛓️ Oath of Silence..." 

          value={effectText} 

          onChange={e => setEffectText(e.target.value)}

          autoFocus

        />



        {/* Buff/Debuff Toggle + Permanent */}

        <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', alignItems: 'center' }}>

          <button 

            style={{ 

              ...S.btn, 

              flex: 1, 

              padding: '6px',

              fontSize: '0.8rem',

              background: !isDebuffMode ? 'rgba(39,201,90,0.3)' : 'transparent',

              borderColor: !isDebuffMode ? '#27c95a' : '#5c4a2a',

              color: !isDebuffMode ? '#27c95a' : '#888',

            }} 

            onClick={() => handleModeChange(false)}

          >Buff</button>

          <button 

            style={{ 

              ...S.btn, 

              flex: 1, 

              padding: '6px',

              fontSize: '0.8rem',

              background: isDebuffMode ? 'rgba(255,105,97,0.3)' : 'transparent',

              borderColor: isDebuffMode ? '#ff6961' : '#5c4a2a',

              color: isDebuffMode ? '#ff6961' : '#888',

            }} 

            onClick={() => handleModeChange(true)}

          >Debuff</button>

          <label style={{ display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer', color: effectIsPermanent ? '#ffd700' : '#888', padding: '0 6px', fontSize: '0.85rem' }}>

            <input type="checkbox" checked={effectIsPermanent} onChange={e => setEffectIsPermanent(e.target.checked)} />∞

          </label>

        </div>



        {/* Options Dropdown */}

        <details open={showOptions} onToggle={e => setShowOptions(e.target.open)} style={{ marginBottom: '8px' }}>

          <summary style={{ cursor: 'pointer', color: '#888', fontSize: '0.75rem', marginBottom: '6px' }}>▼ ⚙️ Options</summary>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '6px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', fontSize: '0.75rem' }}>

            <div>

              <label style={{ display: 'block', color: '#888', marginBottom: '2px' }}>👁 Player Tooltip:</label>

              <input style={{ ...S.input, marginBottom: 0, padding: '6px', fontSize: '0.75rem' }} placeholder="Granted by the Forest Spirit" value={effectPlayerTooltip} onChange={e => setEffectPlayerTooltip(e.target.value)} />

            </div>

            <div>

              <label style={{ display: 'block', color: '#888', marginBottom: '2px' }}>🔒 DM Note:</label>

              <input style={{ ...S.input, marginBottom: 0, padding: '6px', fontSize: '0.75rem' }} placeholder="Secret info..." value={effectDmNote} onChange={e => setEffectDmNote(e.target.value)} />

            </div>

            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>

              <label style={{ color: '#888' }}>Color:</label>

              <select style={{ ...S.select, flex: 1, marginBottom: 0, padding: '4px', fontSize: '0.75rem' }} value={effectColor} onChange={e => setEffectColor(e.target.value)}>

                {BUFF_COLOR_OPTIONS.map(opt => <option key={opt.id} value={opt.id}>{opt.name}</option>)}

              </select>

              <div style={{ width: '18px', height: '18px', borderRadius: '3px', background: selectedColorOpt.bgColor, border: `2px solid ${selectedColorOpt.color}` }} />

            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>

              <input type="checkbox" checked={effectNotifyGlow} onChange={e => setEffectNotifyGlow(e.target.checked)} />

              <span style={{ color: effectNotifyGlow ? '#ffd700' : '#ccc' }}>✨ Glow effect</span>

            </label>

          </div>

        </details>



        {/* Add Button */}

        <button 

          style={{ 

            ...S.btn, 

            ...S.btnSuccess, 

            width: '100%', 

            padding: '8px',

            fontSize: '0.85rem',

            background: isDebuffMode 

              ? 'linear-gradient(180deg, #c92727 0%, #8b1a1a 100%)'

              : 'linear-gradient(180deg, #27c95a 0%, #1a8b3d 100%)',

            borderColor: isDebuffMode ? '#ff4444' : '#27c95a',

          }} 

          onClick={handleAdd}

        >Add</button>

      </div>

    </>

  );

}



// ============================================================================

// EXPANDED CHARACTER PANEL - Full editing for selected character (used in compact mode)

// ============================================================================

function ExpandedCharacterPanel({

  c, diseases, systems, systemsEnabled = true, manaEnabled = true, hungerEnabled = true, diseasesEnabled = true, foodSettingsEnabled = true, diets, foodTypes, locations, currentLocation, inv, chars, saveChars, patchCharacterVitals, patchCharacterSystemInstance, patchCharacterSystemState, patchCharacterSystemStateAndVitals, patchCharacterSystemStateAndResources, patchCharacterSystemStateAndEffects, food, saveFood,

  groupTravelLog, saveGroupTravelLog, onViewTravel, onViewHome, onNotify, messages, onSaveMessages, onSendStatusMessage, onEditSystem,

  glowingBuffs, addBuffGlow, addBuffGlowPatch, addSystemGlowPatch, clearBuffGlow, addSystemGlow, globalSettings, journal = [], dmJournal = [], npcLibrary = {}, saveNpcLibrary = null, onDeselect

}) {

  if (!c) return null;

  

  return (

    <div style={{ 

      marginTop: '16px', 

      padding: '20px', 

      background: 'linear-gradient(135deg, rgba(40,30,20,0.98) 0%, rgba(30,22,15,0.98) 100%)',

      border: '2px solid #ffd700',

      borderRadius: '12px',

      boxShadow: '0 0 30px rgba(255,215,0,0.2)',

    }}>

      {/* Header with close button */}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid rgba(255,215,0,0.3)' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>

          <h2 style={{ color: '#ffd700', margin: 0, fontSize: '1.3rem' }}>

            {c.name} <span style={{ opacity: 0.7, fontSize: '0.9rem' }}>• {c.race} {c.charClass} {c.level}</span>

          </h2>

          <div style={{ display: 'flex', gap: '8px' }}>

            <Tooltip text="View Character"><button aria-label={`${c.name} character sheet`} style={{ ...S.btn, ...S.btnXs }} onClick={onViewHome}>👤 View</button></Tooltip>

            {onViewTravel && <Tooltip text="View Travel Log"><button aria-label={`${c.name} travel log`} style={{ ...S.btn, ...S.btnSuccess, ...S.btnXs }} onClick={onViewTravel}>📜 Travel</button></Tooltip>}

          </div>

        </div>

        <button style={{ ...S.btn, ...S.btnXs, padding: '8px 16px' }} onClick={onDeselect}>✕ Close</button>

      </div>



      {/* Render full DMCard content inline */}

      <DMCard

        c={c}

        diseases={diseases}

        systems={systems}

        systemsEnabled={systemsEnabled}

        manaEnabled={manaEnabled}
        hungerEnabled={hungerEnabled}
        diseasesEnabled={diseasesEnabled}
        foodSettingsEnabled={foodSettingsEnabled}

        diets={diets}

        foodTypes={foodTypes}

        locations={locations}

        currentLocation={currentLocation}

        inv={inv}

        chars={chars}

        saveChars={saveChars}

        patchCharacterVitals={patchCharacterVitals}

        patchCharacterSystemInstance={patchCharacterSystemInstance}

        patchCharacterSystemState={patchCharacterSystemState}

        patchCharacterSystemStateAndVitals={patchCharacterSystemStateAndVitals}

        patchCharacterSystemStateAndResources={patchCharacterSystemStateAndResources}

        patchCharacterSystemStateAndEffects={patchCharacterSystemStateAndEffects}

        food={food}

        saveFood={saveFood}

        groupTravelLog={groupTravelLog}

        saveGroupTravelLog={saveGroupTravelLogForTravelFeature}

        onViewTravel={onViewTravel}

        onViewHome={onViewHome}

        onNotify={onNotify}

        messages={messages}

        onSaveMessages={onSaveMessages}

        onSendStatusMessage={onSendStatusMessage}

        onEditSystem={onEditSystem}

        glowingBuffs={glowingBuffs}

        addBuffGlow={addBuffGlow}

        addBuffGlowPatch={addBuffGlowPatch}

        addSystemGlowPatch={addSystemGlowPatch}

        clearBuffGlow={clearBuffGlow}

        addSystemGlow={addSystemGlow}

        globalSettings={globalSettings}

        journal={journal}

        dmJournal={dmJournal}

        npcLibrary={npcLibrary}

        saveNpcLibrary={saveNpcLibrary}

      />

    </div>

  );

}



function DiseaseManager({ c, diseases, locations, currentLocation, filteredDiseases, diseaseFilter, setDiseaseFilter, addDisease, removeDisease, updateDiseaseVisibility, addImmunity, removeImmunity }) {

  const [newDis, setNewDis] = useState('');

  const [newImm, setNewImm] = useState('');

  const [editingDisease, setEditingDisease] = useState(null);

  

  return <div style={{ marginTop: '6px', padding: '8px', background: 'rgba(0,0,0,0.3)', borderRadius: '4px', fontSize: '0.8rem' }}>

    {/* Filter by location - defaults to current */}

    <div style={{ marginBottom: '8px' }}>

      <label style={{ ...S.label, fontSize: '0.75rem' }}>Filter by location:</label>

      <select style={{ ...S.select, marginBottom: 0, padding: '4px', fontSize: '0.8rem' }} value={diseaseFilter} onChange={e => setDiseaseFilter(e.target.value)}>

        <option value="all">All Diseases</option>

        <option value={currentLocation}>📍 {currentLocation} (current)</option>

        {locations.filter(l => l !== currentLocation).map(l => <option key={l} value={l}>{l}</option>)}

      </select>

    </div>

    

    {/* Add disease - starts with all hidden by default */}

    <div style={S.flex}>

      <select style={{ ...S.select, flex: 1, marginBottom: 0, padding: '4px', fontSize: '0.8rem' }} value={newDis} onChange={e => setNewDis(e.target.value)}>

        <option value="">Add disease...</option>

        {filteredDiseases.filter(([id]) => !(c.diseaseList || []).find(x => x.diseaseId === id)).map(([id, d]) => <option key={id} value={id}>{d.name}</option>)}

      </select>

      <button style={{ ...S.btn, ...S.btnDanger, ...S.btnXs }} onClick={() => { if (newDis) { addDisease(newDis); setNewDis(''); } }}>+</button>

    </div>

    <div style={{ fontSize: '0.7rem', opacity: 0.7, marginTop: '4px' }}>New diseases start hidden. Click to reveal info.</div>

    

    {/* Current diseases with granular visibility edit */}

    {(c.diseaseList || []).length > 0 && <div style={{ marginTop: '8px' }}>

      <div style={{ fontSize: '0.75rem', color: '#d47dd4', marginBottom: '4px' }}>Active:</div>

      {c.diseaseList.map((d, i) => {

        const disease = diseases[d.diseaseId];

        if (!disease) return null;

        const isEditing = editingDisease === d.diseaseId;

        

        return <div key={i} style={{ marginBottom: '8px', padding: '6px', background: 'rgba(139,31,139,0.1)', borderRadius: '4px', border: '1px solid rgba(139,31,139,0.3)' }}>

          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: isEditing ? '8px' : 0 }}>

            <span style={{ flex: 1, color: '#d47dd4', fontWeight: 'bold', cursor: 'pointer' }} onClick={() => setEditingDisease(isEditing ? null : d.diseaseId)}>

              {disease.name} {isEditing ? '▲' : '▼'}

            </span>

            <button style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, marginBottom: 0 }} onClick={() => removeDisease(d.diseaseId)}>✕</button>

          </div>

          

          {isEditing && <div style={{ fontSize: '0.75rem' }}>

            <div style={{ marginBottom: '6px', color: '#c9a227' }}>Player visibility:</div>

            

            {/* Active / Apply */}

            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>

              <input type="checkbox" checked={d.active !== false} onChange={e => updateDiseaseVisibility(d.diseaseId, { active: e.target.checked })} />

              <span>Active</span>

            </label>



            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>

              <input type="checkbox" checked={d.applyEffects !== false} onChange={e => updateDiseaseVisibility(d.diseaseId, { applyEffects: e.target.checked })} />

              <span>Apply Effects</span>

            </label>



            {!(d.showEffects || false) && (

              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', opacity: (d.applyEffects !== false) ? 1 : 0.5 }}>

                <input type="checkbox" disabled={d.applyEffects === false} checked={d.applyHiddenEffects === true} onChange={e => updateDiseaseVisibility(d.diseaseId, { applyHiddenEffects: e.target.checked })} />

                <span>Apply while hidden</span>

              </label>

            )}



            {d.active === false && (

              <div style={{ fontSize: '0.7rem', opacity: 0.65, marginLeft: '22px' }}>

                Inactive diseases do not apply effects.

              </div>

            )}



            {/* Show Name */}

            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', marginBottom: '4px' }}>

              <input type="checkbox" checked={d.showName || false} onChange={e => updateDiseaseVisibility(d.diseaseId, { showName: e.target.checked })} />

              <span>Show Name</span>

              <span style={{ opacity: 0.6 }}>({disease.name})</span>

            </label>

            

            {/* Symptoms - individual checkboxes */}

            {disease.symptoms && disease.symptoms.length > 0 && <div style={{ marginBottom: '4px' }}>

              <div style={{ color: '#888', marginBottom: '2px' }}>Symptoms:</div>

              {disease.symptoms.map((symp, idx) => (

                <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', marginLeft: '12px', marginBottom: '2px' }}>

                  <input type="checkbox" checked={(d.visibleSymptoms || []).includes(idx)} onChange={e => {

                    const current = d.visibleSymptoms || [];

                    const newVisible = e.target.checked ? [...current, idx] : current.filter(x => x !== idx);

                    updateDiseaseVisibility(d.diseaseId, { visibleSymptoms: newVisible });

                  }} />

                  <span>{symp}</span>

                </label>

              ))}

            </div>}

            

            {/* Show Effects */}

            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', marginBottom: '4px' }}>

              <input type="checkbox" checked={d.showEffects || false} onChange={e => updateDiseaseVisibility(d.diseaseId, { showEffects: e.target.checked })} />

              <span>Show Effects</span>

              <span style={{ opacity: 0.6 }}>({disease.effects?.join(', ') || 'None'})</span>

            </label>

            

            {/* Show Cure */}

            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>

              <input type="checkbox" checked={d.showCure || false} onChange={e => updateDiseaseVisibility(d.diseaseId, { showCure: e.target.checked })} />

              <span>Show Cure</span>

              <span style={{ opacity: 0.6 }}>({disease.cures?.map(c => `${c.quantity}x ${c.item}`).join(', ') || 'Unknown'})</span>

            </label>

            

            {/* Quick presets */}

            <div style={{ marginTop: '8px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>

              <button style={{ ...S.btn, ...S.btnXs, marginBottom: 0, fontSize: '0.65rem' }} onClick={() => updateDiseaseVisibility(d.diseaseId, { showName: false, visibleSymptoms: [], showEffects: false, showCure: false })}>Hide All</button>

              <button style={{ ...S.btn, ...S.btnXs, marginBottom: 0, fontSize: '0.65rem' }} onClick={() => updateDiseaseVisibility(d.diseaseId, { showName: true, visibleSymptoms: disease.symptoms?.map((_, i) => i) || [], showEffects: true, showCure: true })}>Show All</button>

            </div>

          </div>}

        </div>;

      })}

    </div>}

    

    {/* Immunities */}

    <div style={{ marginTop: '8px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px' }}>

      <div style={S.flex}>

        <select style={{ ...S.select, flex: 1, marginBottom: 0, padding: '4px', fontSize: '0.8rem' }} value={newImm} onChange={e => setNewImm(e.target.value)}>

          <option value="">Add immunity...</option>

          {Object.entries(diseases).filter(([id]) => !(c.diseaseImmunityList || []).includes(id)).map(([id, d]) => <option key={id} value={id}>{d.name}</option>)}

        </select>

        <button style={{ ...S.btn, ...S.btnSuccess, ...S.btnXs }} onClick={() => { addImmunity(newImm); setNewImm(''); }}>+</button>

      </div>

      {(c.diseaseImmunityList || []).length > 0 && <div style={{ marginTop: '4px' }}>{c.diseaseImmunityList.map((d, i) => <span key={i} style={{ ...S.badge, background: 'rgba(39,201,201,0.3)', border: '1px solid #27c9c9', padding: '2px 6px', cursor: 'pointer', fontSize: '0.7rem' }} onClick={() => removeImmunity(d)}>🛡️ {diseases[d]?.name} ✕</span>)}</div>}

    </div>

  </div>;

}



function SystemManager({

  c,

  systems,

  locations,

  currentLocation,

  filteredSystems,

  systemFilter,

  setSystemFilter,

  addSystem,

  addInlineSystem,

  removeSystem,

  updateSystemVisibility,

  applyManualRuleResults,

  onEditSystem

}) {

  const [sel, setSel] = useState('');

  const [openId, setOpenId] = useState(null);

  // 2026-04-27: pending pill color for the next Apply. Defaults to '' which

  // means "use the template's badgeColor or the type default". DM can pick

  // a BUFF_COLOR_OPTIONS option to override on this instance only.

  const [pendingBadgeColor, setPendingBadgeColor] = useState('');



  // Slot-prompt modal state (Systems expansion Session 3). When the DM picks a template

  // with declared instanceSlots, Apply opens this modal for slot fills instead of

  // applying immediately. { systemId, template, values } | null.

  const [slotPrompt, setSlotPrompt] = useState(null);



  // --- Pending visibility patches (staged until DM hits Apply) ---

  // `pendingVis[sid]` is a partial-patch of the system instance that the DM has queued but

  // not yet committed. When `s.autoApplyVisibility` is ON, changes bypass the queue and go

  // straight to updateSystemVisibility (old behavior, good for "flash reveals"). When OFF

  // (default), clicks modify the queue; Apply commits, Discard clears.

  //

  // Fields that route through the queue: playerVisible, showName, showDescription,

  // showEffects, showCure, showSections (per-section text reveals), visibleSections (list

  // item reveals), visibleClues, showStage. Mechanical toggles (applyEffects,

  // applyHiddenEffects, active) stay immediate — they're about rules, not player reveals.

  const [pendingVis, setPendingVis] = useState({});

  const pendingFor = (sid) => pendingVis[sid] || null;

  const hasPending = (sid) => {

    const p = pendingVis[sid];

    return !!p && Object.keys(p).length > 0;

  };

  // Build the display-state used for rendering: raw instance merged with any staged changes.

  const mergeDisplay = (sid, s) => {

    const p = pendingVis[sid];

    if (!p) return s;

    // Shallow merge for top-level fields; deep-merge for showSections/visibleSections objects

    const out = { ...s, ...p };

    if (p.showSections) out.showSections = { ...(s.showSections || {}), ...p.showSections };

    if (p.visibleSections) out.visibleSections = { ...(s.visibleSections || {}), ...p.visibleSections };

    return out;

  };

  // Stage a patch: either commit immediately (auto-apply) or queue it for later Apply.

  // For nested maps (showSections, visibleSections), the patch keys are DEEP-merged into the

  // queued pending patch so multiple section toggles accumulate correctly.

  const stageVis = (sid, patch, currentAutoApply) => {

    if (currentAutoApply) {

      updateSystemVisibility(sid, patch);

      // Clear any staged changes to avoid ghost state on the next toggle-back.

      if (pendingVis[sid]) {

        setPendingVis(pv => { const n = { ...pv }; delete n[sid]; return n; });

      }

      return;

    }

    setPendingVis(pv => {

      const existing = pv[sid] || {};

      const next = { ...existing };

      Object.keys(patch).forEach(k => {

        if ((k === 'showSections' || k === 'visibleSections') && patch[k] && typeof patch[k] === 'object') {

          next[k] = { ...(existing[k] || {}), ...patch[k] };

        } else {

          next[k] = patch[k];

        }

      });

      return { ...pv, [sid]: next };

    });

  };

  const applyPending = (sid) => {

    const p = pendingVis[sid];

    if (!p || Object.keys(p).length === 0) return;

    updateSystemVisibility(sid, p);

    setPendingVis(pv => { const n = { ...pv }; delete n[sid]; return n; });

  };

  const discardPending = (sid) => {

    if (!pendingVis[sid]) return;

    setPendingVis(pv => { const n = { ...pv }; delete n[sid]; return n; });

  };



  // Quick one-off system (inline, per-character)

  const [showQuick, setShowQuick] = useState(false);

  const [quickDraft, setQuickDraft] = useState({

    type: 'custom',

    name: '',

    publicName: '',

    description: '',

    publicDescription: '',

    cluesText: '',

    effectsText: '',

    hiddenEffectsText: '',

    curesText: '',

    playerVisible: true,

    showName: false,

    showDescription: false,

    showEffects: false,

    showCure: false,

    applyEffects: true,

    applyHiddenEffects: false,

    active: true,

    dmNotes: '',

    playerNotes: '',

    showPlayerNotes: false,

  });



  const parseLinesLocal = (txt) =>

    (txt || '')

      .split(/\r?\n/)

      .map(line => line.trim())

      .filter(Boolean);



  const parseCuresLocal = (txt) =>

    parseLinesLocal(txt)

      .map(line => {

        const m = line.match(/^(\d+)\s*x\s*(.+)$/i);

        if (m) return { quantity: parseInt(m[1]) || 1, item: (m[2] || '').trim() };

        return { quantity: 1, item: (line || '').trim() };

      })

      .filter(cu => cu.item);



  const resolveTemplate = (entry) => {

    if (!entry) return null;

    const raw = (entry.systemId && systems && systems[entry.systemId])

      ? systems[entry.systemId]

      : (entry.template || null);

    if (!raw) return null;

    // Session 3: slot-resolve user-visible text fields so every consumer of

    // resolveTemplate gets parameterized display for free. Mutations go through `entry`

    // (not the returned template), so overlaying resolved strings is safe.

    const hasSlots = Array.isArray(raw.instanceSlots) && raw.instanceSlots.length > 0;

    if (!hasSlots) return raw;

    return {

      ...raw,

      name: resolveSlots(raw.name, entry, raw),

      publicName: resolveSlots(raw.publicName, entry, raw),

      description: resolveSlots(raw.description, entry, raw),

      publicDescription: resolveSlots(raw.publicDescription, entry, raw),

      clues: (raw.clues || []).map(c =>

        typeof c === 'string' ? resolveSlots(c, entry, raw) : { ...c, text: resolveSlots(c?.text, entry, raw) }

      ),

      sections: (raw.sections || []).map(sec => ({

        ...sec,

        label: resolveSlots(sec?.label, entry, raw),

        text: resolveSlots(sec?.text, entry, raw),

        items: Array.isArray(sec?.items)

          ? sec.items.map(it =>

              typeof it === 'string'

                ? resolveSlots(it, entry, raw)

                : { ...it, text: resolveSlots(it?.text, entry, raw), label: resolveSlots(it?.label, entry, raw) }

            )

          : sec?.items,

      })),

    };

  };



  const entryKey = (entry) => entry?.id || entry?.systemId;



  const toggleClue = (sid, i) => {

    const s = (c.systemList || []).find(x => entryKey(x) === sid);

    if (!s) return;

    const current = s.visibleClues || [];

    const next = current.includes(i) ? current.filter(x => x !== i) : [...current, i].sort((a, b) => a - b);

    updateSystemVisibility(sid, { visibleClues: next });

  };



  const toggleSectionItem = (sid, sectionId, idx) => {

    const s = (c.systemList || []).find(x => entryKey(x) === sid);

    if (!s) return;

    // Read the DISPLAY value (raw + staged) so toggling produces the "flipped from what the

    // DM sees" semantic even if there's a pending patch. Write via stageVis so auto-apply

    // off keeps the flip in the draft.

    const merged = mergeDisplay(sid, s);

    const cur = (merged.visibleSections || {})[sectionId] || [];

    const nextItems = cur.includes(idx) ? cur.filter(x => x !== idx) : [...cur, idx].sort((a, b) => a - b);

    stageVis(sid, { visibleSections: { [sectionId]: nextItems } }, s.autoApplyVisibility);

  };



  const toggleSectionText = (sid, sectionId, val) => {

    const s = (c.systemList || []).find(x => entryKey(x) === sid);

    if (!s) return;

    stageVis(sid, { showSections: { [sectionId]: !!val } }, s.autoApplyVisibility);

  };



  const applyQuick = () => {

    const nm = (quickDraft.name || '').trim();

    if (!nm) return;



    const template = {

      id: `inline-${generateId()}`,

      type: quickDraft.type || 'custom',

      name: nm,

      publicName: (quickDraft.publicName || '').trim(),

      description: quickDraft.description || '',

      publicDescription: quickDraft.publicDescription || '',

      clues: parseLinesLocal(quickDraft.cluesText || ''),

      effects: parseLinesLocal(quickDraft.effectsText || ''),

      hiddenEffects: parseLinesLocal(quickDraft.hiddenEffectsText || ''),

      cures: parseCuresLocal(quickDraft.curesText || ''),

      locations: [],

      tags: [],

      sections: [],

      deletable: true,

    };



    addInlineSystem(template, {

      playerVisible: !!quickDraft.playerVisible,

      showName: !!quickDraft.showName,

      showDescription: !!quickDraft.showDescription,

      visibleClues: [],

      showEffects: !!quickDraft.showEffects,

      showCure: !!quickDraft.showCure,

      applyEffects: quickDraft.applyEffects !== false,

      applyHiddenEffects: (quickDraft.hiddenEffectsText || '').trim().length > 0, // Auto-enable if there are hidden effects

      active: quickDraft.active !== false,

      dmNotes: quickDraft.dmNotes || '',

      playerNotes: quickDraft.playerNotes || '',

      showPlayerNotes: !!quickDraft.showPlayerNotes,

      visibleSections: {},

      showSections: {},

    });



    setShowQuick(false);

    setQuickDraft({

      type: 'custom',

      name: '',

      publicName: '',

      description: '',

      publicDescription: '',

      cluesText: '',

      effectsText: '',

      hiddenEffectsText: '',

      curesText: '',

      playerVisible: true,

      showName: false,

      showDescription: false,

      showEffects: false,

      showCure: false,

      applyEffects: true,

      applyHiddenEffects: false,

      active: true,

      dmNotes: '',

      playerNotes: '',

      showPlayerNotes: false,

    });

  };



  const systemsSorted = (c.systemList || [])

    .slice()

    .sort((a, b) => {

      const sa = resolveTemplate(a) || {};

      const sb = resolveTemplate(b) || {};

      return (sa.type || '').localeCompare(sb.type || '') || (sa.name || '').localeCompare(sb.name || '');

    });



  return (

    <div style={{ marginTop: '8px' }}>

      <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>🔒 Systems</div>



      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>

        <select style={{ ...S.select, minWidth: '220px', marginBottom: 0 }} value={sel} onChange={e => setSel(e.target.value)}>

          <option value="">Select a system template…</option>

          {(filteredSystems || []).map(([id, sys]) => (

            <option key={id} value={id}>

              {(SYSTEM_TYPE_OPTIONS.find(t => t.value === (sys.type || 'custom'))?.icon || '🔮')} {sys.name}

              {sys.locations?.length ? ` (${sys.locations.join('/')})` : ''}

            </option>

          ))}

        </select>

        {/* 2026-04-27: pill color picker for this Apply. Empty = use the

            template's badgeColor (or the type's default styling). */}

        <select

          style={{ ...S.select, width: '140px', marginBottom: 0 }}

          value={pendingBadgeColor}

          onChange={e => setPendingBadgeColor(e.target.value)}

          title="Pick the pill color for this instance. Defaults to the template's color."

        >

          <option value="">Pill color: default</option>

          {BUFF_COLOR_OPTIONS.map(c => (

            <option key={c.id} value={c.id}>● {c.label || c.id}</option>

          ))}

        </select>

        <button style={{ ...S.btn, ...S.btnSuccess, ...S.btnSm, marginBottom: 0 }} onClick={() => {

          if (!sel) return;

          const tpl = systems[sel];

          const slots = tpl?.instanceSlots || [];
          const slotErrors = getSystemInstanceSlotValidationErrors(slots);

          const overrides = pendingBadgeColor ? { badgeColor: pendingBadgeColor } : {};
          if (slotErrors.length > 0) {
            window.alert(`Fix instance slots before applying: ${slotErrors[0].message}`);
            return;
          }

          if (slots.length > 0) {

            // Open slot-prompt (Session 3). Seed with declared defaults so most DMs can

            // just hit Apply if they're happy with them.

            const initial = slots.reduce((acc, s) => { if (s?.key) acc[s.key] = s.default || ''; return acc; }, {});

            setSlotPrompt({ systemId: sel, template: tpl, values: initial, overrides });

          } else {

            addSystem(sel, {}, overrides);

            setSel('');

            setPendingBadgeColor('');

          }

        }}>

          ➕ Apply

        </button>

        <button style={{ ...S.btn, ...S.btnEdit, ...S.btnSm, marginBottom: 0 }} onClick={() => setShowQuick(true)}>

          ✨ Quick one-off

        </button>



        <div style={{ flex: 1 }} />

        <input

          style={{ ...S.input, width: '240px', marginBottom: 0 }}

          value={systemFilter}

          onChange={e => setSystemFilter(e.target.value)}

          placeholder="Filter systems…"

        />

      </div>



      {/* Slot-value prompt (Systems expansion Session 3). Opens when the DM applies a

          template with declared instanceSlots. Values flow through addSystem's optional

          initialSlotValues parameter. */}

      {slotPrompt && (

        <div style={S.modal} onClick={() => setSlotPrompt(null)}>

          <div style={{ ...S.modalContent, width: '520px', maxWidth: '95vw' }} onClick={e => e.stopPropagation()}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>

              <div style={{ fontWeight: 'bold', color: '#b97ad9', fontSize: '1.05rem' }}>

                🧩 Apply "{slotPrompt.template?.name || 'System'}"

              </div>

              <button style={{ ...S.btn, ...S.btnDanger, ...S.btnSm }} onClick={() => setSlotPrompt(null)}>✕</button>

            </div>

            <div style={{ fontSize: '0.8rem', opacity: 0.75, marginBottom: '12px' }}>

              Fill in instance values. These replace <code>{'{{KEY}}'}</code> placeholders in the system's name, description, and rule messages for this character only.

            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

              {(slotPrompt.template?.instanceSlots || []).map((slot, si) => (

                <div key={slot.key || si}>

                  <label style={{ fontSize: '0.8rem', color: '#c9a227', display: 'block', marginBottom: '4px' }}>

                    {slot.label || slot.key}

                    <span style={{ opacity: 0.55, marginLeft: '6px', fontFamily: 'monospace' }}>{'{{' + (slot.key || '') + '}}'}</span>

                  </label>

                  <input

                    style={{ ...S.input, width: '100%', marginBottom: 0 }}

                    value={slotPrompt.values[slot.key] ?? ''}

                    placeholder={slot.default || 'Enter a value…'}

                    onChange={e => {

                      setSlotPrompt(prev => prev && ({ ...prev, values: { ...prev.values, [slot.key]: e.target.value } }));

                    }}

                  />

                  {slot.description && (

                    <div style={{ fontSize: '0.7rem', opacity: 0.6, marginTop: '2px' }}>{slot.description}</div>

                  )}

                </div>

              ))}

            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>

              <button style={{ ...S.btn, ...S.btnSm }} onClick={() => setSlotPrompt(null)}>Cancel</button>

              <button style={{ ...S.btn, ...S.btnSuccess, ...S.btnSm }} onClick={() => {
                const slotErrors = getSystemInstanceSlotValidationErrors(slotPrompt.template?.instanceSlots || []);
                if (slotErrors.length > 0) {
                  window.alert(`Fix instance slots before applying: ${slotErrors[0].message}`);
                  return;
                }

                addSystem(slotPrompt.systemId, slotPrompt.values, slotPrompt.overrides || {});

                setSlotPrompt(null);

                setSel('');

                setPendingBadgeColor('');

              }}>

                ➕ Apply with these values

              </button>

            </div>

          </div>

        </div>

      )}



      {/* Quick one-off modal */}

      {showQuick && (

        <div style={S.modal} onClick={() => setShowQuick(false)}>

          <div style={{ ...S.modalContent, width: '780px', maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>

              <div style={{ fontWeight: 'bold', color: '#c9a227', fontSize: '1.1rem' }}>✨ Quick System for {c.name}</div>

              <button style={{ ...S.btn, ...S.btnDanger, ...S.btnSm }} onClick={() => setShowQuick(false)}>✕</button>

            </div>



            {/* Quick Preset Type Buttons */}

            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>

              {[

                { type: 'curse', icon: '💀', label: 'Curse' },

                { type: 'disease', icon: '☣', label: 'Disease' },

                { type: 'blessing', icon: '✨', label: 'Blessing' },

                { type: 'secret', icon: '🔒', label: 'Secret' },

                { type: 'note', icon: '📝', label: 'Note' },

                { type: 'mark', icon: '👁', label: 'Mark' },

                { type: 'tracker', icon: '📊', label: 'Tracker' },

              ].map(p => {

                const isSelected = quickDraft.type === p.type;

                const preset = SYSTEM_PRESETS[p.type] || {};

                const defaultVis = preset.defaultVisibility || {};

                return (

                  <button

                    key={p.type}

                    onClick={() => setQuickDraft({

                      ...quickDraft,

                      type: p.type,

                      playerVisible: defaultVis.playerVisible !== false,

                      showName: defaultVis.showName || false,

                      showDescription: defaultVis.showDescription || false,

                      showEffects: defaultVis.showEffects || false,

                      applyEffects: preset.hasEffects !== false,

                      applyHiddenEffects: p.type === 'secret' || p.type === 'curse',

                    })}

                    style={{

                      padding: '8px 14px',

                      background: isSelected ? 'rgba(201,162,39,0.3)' : 'rgba(255,255,255,0.05)',

                      border: isSelected ? '2px solid #c9a227' : '1px solid rgba(255,255,255,0.15)',

                      borderRadius: '6px',

                      color: isSelected ? '#c9a227' : '#fff',

                      cursor: 'pointer',

                      fontSize: '0.9rem',

                      fontWeight: isSelected ? 'bold' : 'normal',

                    }}

                  >

                    {p.icon} {p.label}

                  </button>

                );

              })}

            </div>

            

            {/* Type description */}

            {(() => {

              const typeInfo = SYSTEM_TYPE_OPTIONS.find(t => t.value === quickDraft.type);

              return typeInfo?.description && (

                <div style={{ fontSize: '0.8rem', opacity: 0.7, marginBottom: '12px', padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px' }}>

                  💡 {typeInfo.description}

                </div>

              );

            })()}



            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>

              <div>

                <label style={S.label}>Internal Name *</label>

                <input style={S.input} value={quickDraft.name} onChange={e => setQuickDraft({ ...quickDraft, name: e.target.value })} placeholder="e.g. Unlucky Curse" />

              </div>



              <div>

                <label style={S.label}>Public Name (what player sees)</label>

                <input style={S.input} value={quickDraft.publicName} onChange={e => setQuickDraft({ ...quickDraft, publicName: e.target.value })} placeholder="e.g. Bad Feeling" />

              </div>



              <div style={{ gridColumn: '1 / span 2' }}>

                <label style={S.label}>DM Description</label>

                <textarea style={{ ...S.input, minHeight: '50px', resize: 'vertical' }} value={quickDraft.description} onChange={e => setQuickDraft({ ...quickDraft, description: e.target.value })} placeholder="Full details (DM only)" />

              </div>



              <div style={{ gridColumn: '1 / span 2' }}>

                <label style={S.label}>Public Description</label>

                <textarea style={{ ...S.input, minHeight: '50px', resize: 'vertical' }} value={quickDraft.publicDescription} onChange={e => setQuickDraft({ ...quickDraft, publicDescription: e.target.value })} placeholder="What player sees when revealed" />

              </div>



              {/* Effects - only for types with them */}

              {quickDraft.type !== 'note' && (

                <>

                  <div>

                    <label style={S.label}>Visible Effects (one per line)</label>

                    <textarea style={{ ...S.input, minHeight: '70px', resize: 'vertical' }} value={quickDraft.effectsText} onChange={e => setQuickDraft({ ...quickDraft, effectsText: e.target.value })} placeholder="-1 Dex&#10;Disadvantage on Perception" />

                  </div>



                  <div>

                    <label style={S.label}>Hidden Effects (source shows as ?)</label>

                    <textarea style={{ ...S.input, minHeight: '70px', resize: 'vertical' }} value={quickDraft.hiddenEffectsText || ''} onChange={e => setQuickDraft({ ...quickDraft, hiddenEffectsText: e.target.value })} placeholder="-2 to saves vs fear" />

                  </div>

                </>

              )}



              <div>

                <label style={S.label}>Clues (reveal one at a time)</label>

                <textarea style={{ ...S.input, minHeight: '70px', resize: 'vertical' }} value={quickDraft.cluesText} onChange={e => setQuickDraft({ ...quickDraft, cluesText: e.target.value })} placeholder="A chill runs down your spine...&#10;Shadows seem to follow you..." />

              </div>



              {quickDraft.type !== 'note' && quickDraft.type !== 'tracker' && quickDraft.type !== 'secret' && (

                <div>

                  <label style={S.label}>Cures (one per line)</label>

                  <textarea style={{ ...S.input, minHeight: '70px', resize: 'vertical' }} value={quickDraft.curesText} onChange={e => setQuickDraft({ ...quickDraft, curesText: e.target.value })} placeholder="1x Remove Curse&#10;2x Holy Water" />

                </div>

              )}



              {/* Visibility Controls */}

              <div style={{ gridColumn: '1 / span 2', padding: '12px', background: 'rgba(74,144,217,0.08)', borderRadius: '8px', border: '1px solid rgba(74,144,217,0.15)' }}>

                <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#4a90d9', fontSize: '0.9rem' }}>👁 Initial Visibility</div>

                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>

                    <input type="checkbox" checked={quickDraft.active !== false} onChange={e => setQuickDraft({ ...quickDraft, active: e.target.checked })} />

                    Active

                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>

                    <input type="checkbox" checked={quickDraft.playerVisible !== false} onChange={e => setQuickDraft({ ...quickDraft, playerVisible: e.target.checked })} />

                    Show placeholder

                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>

                    <input type="checkbox" checked={quickDraft.showName} onChange={e => setQuickDraft({ ...quickDraft, showName: e.target.checked })} />

                    Reveal name

                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>

                    <input type="checkbox" checked={quickDraft.showDescription} onChange={e => setQuickDraft({ ...quickDraft, showDescription: e.target.checked })} />

                    Reveal details

                  </label>

                  {quickDraft.type !== 'note' && (

                    <>

                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>

                        <input type="checkbox" checked={quickDraft.showEffects} onChange={e => setQuickDraft({ ...quickDraft, showEffects: e.target.checked })} />

                        Reveal effects

                      </label>

                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>

                        <input type="checkbox" checked={quickDraft.applyEffects !== false} onChange={e => setQuickDraft({ ...quickDraft, applyEffects: e.target.checked })} />

                        Apply effects

                      </label>

                    </>

                  )}

                </div>

                {quickDraft.type === 'note' && (

                  <div style={{ marginTop: '8px', fontSize: '0.8rem', color: '#888' }}>

                    📝 Note type has no mechanical effects - for tracking information only.

                  </div>

                )}

              </div>



              <div style={{ gridColumn: '1 / span 2' }}>

                <label style={S.label}>DM Notes (never shown to player)</label>

                <textarea style={{ ...S.input, minHeight: '50px', resize: 'vertical' }} value={quickDraft.dmNotes} onChange={e => setQuickDraft({ ...quickDraft, dmNotes: e.target.value })} placeholder="Secret notes for DM..." />

              </div>

            </div>



            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' }}>

              <button style={{ ...S.btn, ...S.btnSubtle }} onClick={() => setShowQuick(false)}>Cancel</button>

              <button 

                style={{ ...S.btn, ...S.btnSuccess }} 

                onClick={applyQuick}

                disabled={!quickDraft.name?.trim()}

              >

                ✨ Apply to {c.name}

              </button>

            </div>

          </div>

        </div>

      )}



      {/* Applied systems */}

      {systemsSorted.length === 0 ? (

        <div style={{ marginTop: '10px', opacity: 0.7, fontSize: '0.85rem' }}>No systems applied.</div>

      ) : (

        <div style={{ marginTop: '10px' }}>

          {systemsSorted.map((s, idx) => {

            const sys = resolveTemplate(s);

            const sid = entryKey(s) || `sys-${idx}`;
            const playerSafeDescription = getPlayerSafeSystemPublicDescription(sys);
            const previewPublicDescription = isCrimsonArtsSystem(sys) ? playerSafeDescription : (sys?.publicDescription || '');

            const typeMeta = SYSTEM_TYPE_OPTIONS.find(t => t.value === ((sys && sys.type) || 'custom'));

            const icon = typeMeta?.icon || '🔒';



            // Display state: raw instance merged with any staged visibility patches. Use

            // `sd.*` wherever we RENDER visibility fields (so staged values appear) and

            // keep `s.*` for IMMEDIATE-commit fields (autoApplyVisibility, badgeColor,

            // applyEffects, etc.). stageVis(..., s.autoApplyVisibility) for writes.

            const sd = mergeDisplay(sid, s);

            const sections = sys?.sections || [];

            const visibleSections = sd.visibleSections || {};

            const showSections = sd.showSections || {};



            const hasAnyVisible = !!sd.showName || !!sd.showDescription || !!sd.showEffects || !!sd.showCure || (sd.visibleClues || []).length > 0 ||

              (!!sd.showPlayerNotes && (sd.playerNotes || '').trim()) ||

              sections.some(sec => {

                if (!sec || sec.dmOnly) return false;

                if (!sec.id) return false;

                if (sec.kind === 'text') return !!showSections[sec.id];

                return (visibleSections[sec.id] || []).length > 0;

              });



            const playerSummary = (() => {

              if (sd.playerVisible === false && !hasAnyVisible) return 'DM-only';

              const parts = [];

              if (sd.showName) parts.push('name');

              if (sd.showDescription) parts.push('details');

              if ((sd.visibleClues || []).length) parts.push(`${sd.visibleClues.length} clue(s)`);

              if (sd.showEffects) parts.push('effects');

              if (sd.showCure) parts.push('cure');

              if (sd.showPlayerNotes && (sd.playerNotes || '').trim()) parts.push('notes');

              const secShown = sections

                .filter(sec => sec && !sec.dmOnly && sec.id)

                .reduce((acc, sec) => acc + (sec.kind === 'text' ? (showSections[sec.id] ? 1 : 0) : (visibleSections[sec.id] || []).length), 0);

              if (secShown) parts.push(`sections:${secShown}`);

              return parts.length ? parts.join(', ') : 'hidden';

            })();



            const isOpen = openId === sid;



            return (

              <div key={sid} style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', marginBottom: '8px', border: '1px solid rgba(74,144,217,0.15)' }}>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                  <div style={{ cursor: 'pointer', flex: 1 }} onClick={() => setOpenId(isOpen ? null : sid)}>

                    <div style={{ fontWeight: 'bold', color: '#b0b0ff' }}>

                      {icon} {(sys?.name || s.systemId || 'System')} {s.active === false && <span style={{ opacity: 0.75 }}>(inactive)</span>}

                    </div>

                    <div style={{ fontSize: '0.75rem', opacity: 0.75, marginTop: '2px' }}>

                      Player: <span style={{ color: '#c9a227' }}>{playerSummary}</span>

                      {s.applyEffects === false && <span style={{ marginLeft: '10px', color: '#ff6961' }}>apply OFF</span>}

                      {s.applyEffects !== false && !s.showEffects && s.applyHiddenEffects === true && <span style={{ marginLeft: '10px', color: '#8be28b' }}>hidden apply ON</span>}

                    </div>

                  </div>

                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>

                    {/* Quick actions */}

                    {s.systemId && onEditSystem && (

                      <Tooltip text="Edit system template in settings">

                        <button 

                          style={{ ...S.btn, ...S.btnEdit, ...S.btnXs, marginBottom: 0, padding: '4px 8px' }} 

                          onClick={(e) => {

                            e.stopPropagation();

                            onEditSystem(s.systemId);

                          }}

                        >⚙️</button>

                      </Tooltip>

                    )}

                    <Tooltip text={s.autoApplyVisibility ? 'Reveal everything to player (immediate)' : 'Stage full reveal — click Apply to commit'}>

                      <button

                        style={{ ...S.btn, ...S.btnSuccess, ...S.btnXs, marginBottom: 0, padding: '4px 8px' }}

                        onClick={(e) => {

                          e.stopPropagation();

                          stageVis(sid, {

                            playerVisible: true,

                            showName: true,

                            showDescription: true,

                            showEffects: true,

                            showCure: true,

                            visibleClues: (sys?.clues || []).map((_, i) => i),

                          }, s.autoApplyVisibility);

                        }}

                      >👁</button>

                    </Tooltip>

                    <Tooltip text={s.autoApplyVisibility ? 'Hide everything from player (immediate)' : 'Stage full hide — click Apply to commit'}>

                      <button

                        style={{ ...S.btn, ...S.btnXs, marginBottom: 0, padding: '4px 8px' }}

                        onClick={(e) => {

                          e.stopPropagation();

                          stageVis(sid, {

                            playerVisible: false,

                            showName: false,

                            showDescription: false,

                            showEffects: false,

                            showCure: false,

                            visibleClues: [],

                          }, s.autoApplyVisibility);

                        }}

                      >🙈</button>

                    </Tooltip>

                    <button
                      aria-label={`Remove ${sys?.name || s.systemId || 'system'}`}
                      style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, marginBottom: 0 }}
                      onClick={() => removeSystem(sid)}
                    >🗑️</button>

                  </div>

                </div>



                {isOpen && (

                  <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>

                    {/* Type-specific indicator */}

                    {sys?.type === 'note' && (

                      <div style={{ marginBottom: '10px', padding: '8px', background: 'rgba(136,136,136,0.1)', borderRadius: '6px', border: '1px solid rgba(136,136,136,0.2)' }}>

                        <span style={{ color: '#888' }}>📝 Note Only</span> - This system has no mechanical effects. Use it for tracking information with selective reveal.

                      </div>

                    )}

                    

                    {/* Status Controls */}

                    <div style={{ marginBottom: '12px', padding: '10px', background: 'rgba(0,0,0,0.15)', borderRadius: '6px' }}>

                      <div style={{ fontWeight: 'bold', fontSize: '0.85rem', marginBottom: '8px', color: '#888' }}>Status</div>

                      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>

                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>

                          <input type="checkbox" checked={s.active !== false} onChange={e => updateSystemVisibility(sid, { active: e.target.checked })} />

                          <span style={{ color: s.active !== false ? '#8be28b' : '#ff6961' }}>{s.active !== false ? '🟢 Active' : '⏸️ Inactive'}</span>

                        </label>

                      </div>

                    </div>

                    

                    {/* Player Visibility Controls */}

                    <div style={{ marginBottom: '12px', padding: '10px', background: 'rgba(74,144,217,0.08)', borderRadius: '6px', border: '1px solid rgba(74,144,217,0.15)' }}>

                      <div style={{ fontWeight: 'bold', fontSize: '0.85rem', marginBottom: '8px', color: '#4a90d9' }}>👁 Player Visibility</div>

                      {/* Apply / Auto-apply toolbar. Default UX stages visibility changes

                          in a draft and commits them on "Apply" click — lets the DM prepare

                          multi-toggle reveals without the player seeing intermediate flicker.

                          "Auto-apply" toggle restores the instant-apply behavior useful for

                          flash-reveals (read it fast before I untoggle). Pending-change count

                          is shown in the Apply button; visibility fields that are staged get

                          a yellow outline on their label (see below). */}

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>

                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.75rem', color: s.autoApplyVisibility ? '#c9a227' : '#888' }}

                          title="Auto-apply: every checkbox click commits immediately (useful for flash-reveals)">

                          <input

                            type="checkbox"

                            checked={!!s.autoApplyVisibility}

                            onChange={e => updateSystemVisibility(sid, { autoApplyVisibility: e.target.checked })}

                          />

                          ⚡ Auto-apply

                        </label>

                        <div style={{ flex: 1 }} />

                        {/* Apply / Discard is visible whenever there are pending changes, even

                            if the DM just flipped auto-apply ON — so staged changes from the

                            manual-apply mode aren't stranded. Auto-apply only governs whether

                            NEW clicks stage or commit immediately. */}

                        {!s.autoApplyVisibility && !hasPending(sid) && (

                          <span style={{ fontSize: '0.65rem', opacity: 0.55, color: '#888' }}>

                            (changes queue until you click Apply)

                          </span>

                        )}

                        {hasPending(sid) && (

                          <>

                            <button

                              style={{

                                ...S.btn, ...S.btnXs, marginBottom: 0,

                                background: 'rgba(139,226,139,0.25)',

                                border: '1px solid rgba(139,226,139,0.5)',

                                color: '#8be28b',

                              }}

                              onClick={() => applyPending(sid)}

                              title={`Apply ${Object.keys(pendingVis[sid]).length} pending change(s)`}

                            >

                              ✓ Apply ({Object.keys(pendingVis[sid]).length})

                            </button>

                            <button

                              style={{ ...S.btn, ...S.btnXs, marginBottom: 0, background: 'rgba(255,105,97,0.15)', border: '1px solid rgba(255,105,97,0.35)', color: '#ff6961' }}

                              onClick={() => discardPending(sid)}

                              title="Discard pending visibility changes"

                            >

                              ✕ Discard

                            </button>

                          </>

                        )}

                      </div>



                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '10px' }}>

                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', ...(pendingFor(sid)?.playerVisible !== undefined ? { outline: '1px dashed #c9a227', padding: '2px 4px', borderRadius: '3px' } : {}) }}>

                          <input type="checkbox" checked={sd.playerVisible !== false} onChange={e => stageVis(sid, { playerVisible: e.target.checked }, s.autoApplyVisibility)} />

                          Show placeholder

                        </label>

                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', ...(pendingFor(sid)?.showName !== undefined ? { outline: '1px dashed #c9a227', padding: '2px 4px', borderRadius: '3px' } : {}) }}>

                          <input type="checkbox" checked={!!sd.showName} onChange={e => stageVis(sid, { showName: e.target.checked }, s.autoApplyVisibility)} />

                          Reveal name

                        </label>

                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', ...(pendingFor(sid)?.showDescription !== undefined ? { outline: '1px dashed #c9a227', padding: '2px 4px', borderRadius: '3px' } : {}) }}>

                          <input type="checkbox" checked={!!sd.showDescription} onChange={e => stageVis(sid, { showDescription: e.target.checked }, s.autoApplyVisibility)} />

                          Reveal details

                        </label>

                        {sys?.type !== 'note' && (

                          <>

                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', ...(pendingFor(sid)?.showEffects !== undefined ? { outline: '1px dashed #c9a227', padding: '2px 4px', borderRadius: '3px' } : {}) }}>

                              <input type="checkbox" checked={!!sd.showEffects} onChange={e => stageVis(sid, { showEffects: e.target.checked }, s.autoApplyVisibility)} />

                              Reveal effects

                            </label>

                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', ...(pendingFor(sid)?.showCure !== undefined ? { outline: '1px dashed #c9a227', padding: '2px 4px', borderRadius: '3px' } : {}) }}>

                              <input type="checkbox" checked={!!sd.showCure} onChange={e => stageVis(sid, { showCure: e.target.checked }, s.autoApplyVisibility)} />

                              Reveal cure

                            </label>

                          </>

                        )}

                      </div>



                      {/* DM preview of the description content — always visible so the DM can

                          read what "Reveal details" would expose to the player before clicking.

                          Matches the preview pattern used for text sections (casting rules, etc.)

                          in the Sections block below. */}

                      {(sys?.description || previewPublicDescription) && (

                        <div style={{

                          marginTop: '8px',

                          padding: '6px 8px',

                          borderLeft: `2px solid ${sd.showDescription ? 'rgba(139,226,139,0.4)' : 'rgba(201,162,39,0.35)'}`,

                          background: 'rgba(0,0,0,0.2)',

                          fontSize: '0.78rem',

                          opacity: sd.showDescription ? 1 : 0.85,

                        }}>

                          <div style={{ fontSize: '0.65rem', color: '#888', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>

                            Description preview {sd.showDescription

                              ? <span style={{ color: '#8be28b' }}>— player sees this</span>

                              : <span style={{ color: '#888' }}>— hidden from player</span>}

                          </div>

                          {sys.description && (

                            <div style={{ whiteSpace: 'pre-wrap', marginBottom: previewPublicDescription && previewPublicDescription !== sys.description ? '6px' : 0 }}>

                              <span style={{ color: '#888', fontSize: '0.7rem' }}>DM: </span>{sys.description}

                            </div>

                          )}

                          {previewPublicDescription && previewPublicDescription !== sys.description && (

                            <div style={{ whiteSpace: 'pre-wrap' }}>

                              <span style={{ color: '#888', fontSize: '0.7rem' }}>Public: </span>{previewPublicDescription}

                            </div>

                          )}

                        </div>

                      )}



                      {/* Placeholder Text Controls */}

                      <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '10px', marginTop: '6px' }}>

                        <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '6px' }}>🎭 Placeholder Text (shown when content is hidden)</div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '8px', alignItems: 'start' }}>

                          <div>

                            <label style={{ fontSize: '0.7rem', color: '#666' }}>Unknown Style:</label>

                            <select

                              style={{ ...S.select, fontSize: '0.8rem', padding: '4px 8px', marginBottom: 0, width: '100%' }}

                              value={s.unknownText || UNKNOWN_PLACEHOLDER}

                              onChange={e => updateSystemVisibility(sid, { unknownText: e.target.value })}

                            >

                              <option value="??">??</option>

                              <option value="Unknown">Unknown</option>

                              <option value="???">???</option>

                              <option value="??">??</option>

                              <option value="...">...</option>

                              <option value="?">?</option>

                            </select>

                          </div>

                          <div>

                            <label style={{ fontSize: '0.7rem', color: '#666' }}>Placeholder Name:</label>

                            <DebouncedInput

                              as="input"

                              style={{ ...S.input, fontSize: '0.8rem', padding: '4px 8px', marginBottom: 0 }}

                              value={s.placeholderName || ''}

                              onCommit={v => updateSystemVisibility(sid, { placeholderName: v })}

                              placeholder="e.g., 'Observed.' or 'Strange Feeling'"

                            />

                          </div>

                          <div style={{ gridColumn: '1 / span 2' }}>

                            <label style={{ fontSize: '0.7rem', color: '#666' }}>Placeholder Description (tooltip):</label>

                            <DebouncedInput

                              as="textarea"

                              style={{ ...S.input, fontSize: '0.8rem', padding: '4px 8px', marginBottom: 0, minHeight: '50px', resize: 'vertical' }}

                              value={s.placeholderDescription || ''}

                              onCommit={v => updateSystemVisibility(sid, { placeholderDescription: v })}

                              placeholder="e.g., 'You feel like you are being watched.' - shown in tooltip when details are hidden"

                            />

                          </div>

                        </div>

                        {(s.placeholderName || s.placeholderDescription) && (

                          <div style={{ marginTop: '8px', padding: '6px', background: 'rgba(201,162,39,0.1)', borderRadius: '4px', fontSize: '0.75rem' }}>

                            <strong style={{ color: '#c9a227' }}>Player sees:</strong> Badge: "{s.placeholderName || s.unknownText || UNKNOWN_PLACEHOLDER}"

                            {s.placeholderDescription && <span> • Tooltip: "{s.placeholderDescription}"</span>}

                          </div>

                        )}

                        

                        {/* Clues Label Customization */}

                        {sys?.clues?.length > 0 && (

                          <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>

                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>

                              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>

                                <input 

                                  type="checkbox" 

                                  checked={s.showCluesLabel !== false}

                                  onChange={e => updateSystemVisibility(sid, { showCluesLabel: e.target.checked })}

                                />

                                Show "Clues" label

                              </label>

                              {s.showCluesLabel !== false && (

                                <DebouncedInput

                                  as="input"

                                  style={{ ...S.input, fontSize: '0.75rem', padding: '2px 6px', marginBottom: 0, width: '120px' }}

                                  value={s.cluesLabel ?? 'Clues'}

                                  onCommit={v => updateSystemVisibility(sid, { cluesLabel: v })}

                                  placeholder="Custom label..."

                                />

                              )}

                            </div>

                          </div>

                        )}

                        

                        {/* Badge Color Customization */}

                        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>

                          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>

                            <label style={{ fontSize: '0.75rem', color: '#888' }}>🎨 Badge Color:</label>

                            <select

                              style={{ ...S.select, flex: 1, marginBottom: 0, padding: '2px 6px', fontSize: '0.75rem', minWidth: '150px', opacity: s.useGlowColorForBadge ? 0.5 : 1 }}

                              value={s.badgeColor || ''}

                              onChange={e => updateSystemVisibility(sid, { badgeColor: e.target.value || null })}

                              disabled={!!s.useGlowColorForBadge}

                              title={s.useGlowColorForBadge ? 'Disabled — badge uses the glow color. Uncheck "Match glow color" to set a custom color.' : ''}

                            >

                              <option value="">Default (System)</option>

                              {BUFF_COLOR_OPTIONS.map(opt => (

                                <option key={opt.id} value={opt.id}>{opt.name}</option>

                              ))}

                            </select>

                            {(() => {

                              // Preview swatch reflects the EFFECTIVE badge color — glow when

                              // useGlowColorForBadge is on, otherwise the chosen badgeColor.

                              const effective = s.useGlowColorForBadge ? (s.glowColorId || null) : (s.badgeColor || null);

                              if (!effective) return null;

                              return (

                                <span style={{

                                  display: 'inline-block', width: '16px', height: '16px', borderRadius: '3px',

                                  background: BUFF_COLOR_OPTIONS.find(c => c.id === effective)?.color || '#888',

                                  boxShadow: s.useGlowColorForBadge ? `0 0 6px ${BUFF_COLOR_OPTIONS.find(c => c.id === effective)?.color || '#888'}` : 'none',

                                }} />

                              );

                            })()}

                          </div>

                          {/* Match-glow option: when enabled, the badge color mirrors the glow

                              color so the pill visually theme-matches the system's glow effect. */}

                          <div style={{ marginTop: '6px' }}>

                            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.72rem', color: s.useGlowColorForBadge ? '#c9a227' : '#888' }}>

                              <input

                                type="checkbox"

                                checked={!!s.useGlowColorForBadge}

                                onChange={e => updateSystemVisibility(sid, { useGlowColorForBadge: e.target.checked })}

                              />

                              🎭 Match glow color on pill

                              <span style={{ fontSize: '0.65rem', opacity: 0.65, marginLeft: '4px' }}>

                                (pill color follows the glow color below)

                              </span>

                            </label>

                          </div>

                          

                          {/* Notify Glow Option — two triggers:

                              - "Add glow when notifying": DM-initiated manual glow (button

                                appears next to the pill in the DM Card so DM can flash it).

                              - "Glow on update": auto-fire a glow whenever any player-visible

                                field changes (reveals, clues, sections, stages, notes).

                                Useful when the DM wants players to notice state changes

                                without clicking through a notify button.

                              Both share the same glow-color selector. */}

                          <div style={{ marginTop: '8px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>

                            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>

                              <input

                                type="checkbox"

                                checked={s.notifyGlow || false}

                                onChange={e => updateSystemVisibility(sid, { notifyGlow: e.target.checked })}

                              />

                              ✨ Manual glow button

                            </label>

                            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.75rem' }}

                              title="Automatically glow the pill whenever this system's player-visible state changes (reveals, clues, sections, stages, notes).">

                              <input

                                type="checkbox"

                                checked={s.glowOnUpdate || false}

                                onChange={e => updateSystemVisibility(sid, { glowOnUpdate: e.target.checked })}

                              />

                              🔔 Glow on update

                            </label>

                            {(s.notifyGlow || s.glowOnUpdate || s.useGlowColorForBadge) && (

                              <select

                                style={{ ...S.select, marginBottom: 0, padding: '2px 6px', fontSize: '0.75rem', width: '120px' }}

                                value={s.glowColorId || 'gold'}

                                onChange={e => updateSystemVisibility(sid, { glowColorId: e.target.value })}

                                title="Glow color (used for manual glow, auto-glow, and pill theming)"

                              >

                                {BUFF_COLOR_OPTIONS.map(opt => (

                                  <option key={opt.id} value={opt.id}>{opt.name}</option>

                                ))}

                              </select>

                            )}

                          </div>

                        </div>

                        

                        {/* Live Player Preview */}

                        <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>

                          <details>

                            <summary style={{ cursor: 'pointer', fontSize: '0.75rem', color: '#4a90d9' }}>

                              👁 Live Player Preview

                            </summary>

                            <PlayerPreviewBadge 

                              systemData={s} 

                              system={sys}

                              unknownText={s.unknownText || UNKNOWN_PLACEHOLDER}

                            />

                          </details>

                        </div>

                      </div>

                    </div>

                    

                    {/* Effect Application Controls - only for types with effects */}

                    {sys?.type !== 'note' && (

                      <div style={{ marginBottom: '12px', padding: '10px', background: 'rgba(201,162,39,0.08)', borderRadius: '6px', border: '1px solid rgba(201,162,39,0.15)' }}>

                        <div style={{ fontWeight: 'bold', fontSize: '0.85rem', marginBottom: '8px', color: '#c9a227' }}>⚡ Effect Application</div>

                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>

                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>

                            <input type="checkbox" checked={s.applyEffects !== false} onChange={e => updateSystemVisibility(sid, { applyEffects: e.target.checked })} />

                            Apply visible effects

                          </label>

                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', opacity: (s.applyEffects !== false) ? 1 : 0.5 }}>

                            <input type="checkbox" disabled={s.applyEffects === false} checked={s.applyHiddenEffects === true} onChange={e => updateSystemVisibility(sid, { applyHiddenEffects: e.target.checked })} />

                            Apply hidden effects

                          </label>

                        </div>

                        

                        {/* Effects Display */}

                        {((sys?.effects || []).length > 0 || (sys?.hiddenEffects || []).length > 0) && (

                          <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>

                            {/* Per-Effect Controls */}

                            {(sys?.effects || []).length > 0 && (

                              <div style={{ marginBottom: '10px' }}>

                                <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '4px' }}>Visible Effects (click to configure):</div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>

                                  {(sys.effects || []).map((eff, i) => {

                                    const effText = typeof eff === 'string' ? eff : (eff.displayText || '');

                                    const overrideKey = `visible-${i}`;

                                    const override = (s.effectOverrides || {})[overrideKey] || {};

                                    const isApplied = override.apply !== false;

                                    const showsSource = override.showSource !== false;

                                    

                                    return (

                                      <div key={i} style={{ 

                                        display: 'flex', 

                                        alignItems: 'center', 

                                        gap: '8px', 

                                        padding: '4px 8px',

                                        background: isApplied ? 'rgba(39,201,90,0.1)' : 'rgba(201,39,39,0.1)',

                                        borderRadius: '4px',

                                        border: `1px solid ${isApplied ? 'rgba(39,201,90,0.3)' : 'rgba(201,39,39,0.3)'}`,

                                      }}>

                                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>

                                          <input 

                                            type="checkbox" 

                                            checked={isApplied}

                                            onChange={e => {

                                              const newOverrides = { ...(s.effectOverrides || {}), [overrideKey]: { ...override, apply: e.target.checked } };

                                              updateSystemVisibility(sid, { effectOverrides: newOverrides });

                                            }}

                                          />

                                          Apply

                                        </label>

                                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>

                                          <input 

                                            type="checkbox" 

                                            checked={showsSource}

                                            onChange={e => {

                                              const newOverrides = { ...(s.effectOverrides || {}), [overrideKey]: { ...override, showSource: e.target.checked } };

                                              updateSystemVisibility(sid, { effectOverrides: newOverrides });

                                            }}

                                          />

                                          Show Source

                                        </label>

                                        <span style={{ 

                                          ...S.badge, 

                                          ...(!isApplied ? S.badgeUnknown : (!showsSource ? S.badgeMystery : S.badgeDebuff)),

                                          fontSize: '0.7rem',

                                          marginBottom: 0,

                                          marginRight: 0,

                                          opacity: isApplied ? 1 : 0.5,

                                        }}>

                                          {!showsSource && isApplied ? '✨' : ''} {effText}

                                        </span>

                                      </div>

                                    );

                                  })}

                                </div>

                              </div>

                            )}

                            {(sys?.hiddenEffects || []).length > 0 && (

                              <div>

                                <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '4px' }}>Hidden Effects (source always shows as ?):</div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>

                                  {(sys.hiddenEffects || []).map((eff, i) => {

                                    const effText = typeof eff === 'string' ? eff : (eff.displayText || '');

                                    const overrideKey = `hidden-${i}`;

                                    const override = (s.effectOverrides || {})[overrideKey] || {};

                                    const isApplied = override.apply !== false;

                                    

                                    return (

                                      <div key={i} style={{ 

                                        display: 'flex', 

                                        alignItems: 'center', 

                                        gap: '8px',

                                        padding: '4px 8px',

                                        background: isApplied ? 'rgba(138,43,226,0.15)' : 'rgba(100,100,100,0.2)',

                                        borderRadius: '4px',

                                        border: `1px solid ${isApplied ? 'rgba(138,43,226,0.4)' : 'rgba(100,100,100,0.3)'}`,

                                      }}>

                                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>

                                          <input 

                                            type="checkbox" 

                                            checked={isApplied}

                                            onChange={e => {

                                              const newOverrides = { ...(s.effectOverrides || {}), [overrideKey]: { ...override, apply: e.target.checked } };

                                              updateSystemVisibility(sid, { effectOverrides: newOverrides });

                                            }}

                                          />

                                          Apply

                                        </label>

                                        <span style={{ 

                                          ...S.badge, 

                                          ...S.badgeMystery, 

                                          fontSize: '0.7rem',

                                          marginBottom: 0,

                                          marginRight: 0,

                                          opacity: isApplied ? 1 : 0.5,

                                        }}>

                                          🔒 {effText}

                                        </span>

                                      </div>

                                    );

                                  })}

                                </div>

                              </div>

                            )}

                          </div>

                        )}

                      </div>

                    )}



                    {sys?.publicName && (

                      <div style={{ fontSize: '0.8rem', opacity: 0.85, marginBottom: '6px' }}>

                        Public name: <span style={{ color: '#c9a227' }}>{sys.publicName}</span>

                      </div>

                    )}



                    {sys?.description && (

                      <div style={{ fontSize: '0.8rem', opacity: 0.85, marginBottom: '6px' }}>

                        <span style={{ color: '#888' }}>DM:</span> {sys.description}

                      </div>

                    )}

                    {previewPublicDescription && (

                      <div style={{ fontSize: '0.8rem', opacity: 0.85, marginBottom: '6px' }}>

                        <span style={{ color: '#888' }}>Public:</span> {previewPublicDescription}

                      </div>

                    )}



                    {/* Clues */}

                    {sys?.clues?.length > 0 && (

                      <div style={{ marginTop: '8px' }}>

                        <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>Clues</div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>

                          {sys.clues.map((cl, i) => (

                            <label key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', cursor: 'pointer' }}>

                              <input type="checkbox" checked={(s.visibleClues || []).includes(i)} onChange={() => toggleClue(sid, i)} />

                              <span style={{ opacity: (s.visibleClues || []).includes(i) ? 1 : 0.7 }}>{cl}</span>

                            </label>

                          ))}

                        </div>

                      </div>

                    )}



                    {/* Sections */}

                    {sections.length > 0 && (

                      <div style={{ marginTop: '10px' }}>

                        <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>Sections</div>

                        {sections.map(sec => {

                          if (!sec || !sec.id) return null;

                          const sid2 = sec.id;

                          if (sec.dmOnly) {

                            return (

                              <div key={sid2} style={{ marginBottom: '8px', padding: '8px', background: 'rgba(0,0,0,0.15)', borderRadius: '6px' }}>

                                <div style={{ color: '#c9a227', fontWeight: 'bold' }}>{sec.label || sid2} (DM only)</div>

                                {sec.kind === 'text' && (sec.text || '').trim() && <div style={{ marginTop: '4px', opacity: 0.9 }}>{sec.text}</div>}

                                {sec.kind !== 'text' && (sec.items || []).length > 0 && (

                                  <ul style={{ margin: '6px 0 0 16px' }}>

                                    {(sec.items || []).map((it, i) => <li key={i}>{it}</li>)}

                                  </ul>

                                )}

                              </div>

                            );

                          }



                          if (sec.kind === 'text') {

                            // DM preview of the section text is ALWAYS visible (regardless of

                            // the player-reveal checkbox) so the DM can read what they're about

                            // to reveal before clicking. A small chip next to the title signals

                            // the current reveal state. Uses the MERGED display state (sd) so

                            // pending/staged toggles flip the chip even before Apply is clicked.

                            const sectionRevealed = !!(sd.showSections || {})[sid2];

                            const sectionPending = pendingFor(sid)?.showSections?.[sid2] !== undefined;

                            const sectionText = (sec.text || '').trim();

                            return (

                              <div key={sid2} style={{

                                marginBottom: '8px', padding: '8px',

                                background: 'rgba(0,0,0,0.12)', borderRadius: '6px',

                                ...(sectionPending ? { outline: '1px dashed #c9a227' } : {}),

                              }}>

                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>

                                  <input type="checkbox" checked={sectionRevealed} onChange={e => toggleSectionText(sid, sid2, e.target.checked)} />

                                  <span style={{ fontWeight: 'bold' }}>Reveal: {sec.label || sid2}</span>

                                  {sectionPending && <span style={{ fontSize: '0.6rem', color: '#c9a227' }}>(pending)</span>}

                                  <span style={{

                                    marginLeft: 'auto', fontSize: '0.65rem',

                                    padding: '1px 6px', borderRadius: '3px',

                                    background: sectionRevealed ? 'rgba(139,226,139,0.15)' : 'rgba(136,136,136,0.15)',

                                    color: sectionRevealed ? '#8be28b' : '#888',

                                    border: `1px solid ${sectionRevealed ? 'rgba(139,226,139,0.35)' : 'rgba(136,136,136,0.35)'}`,

                                  }}>

                                    {sectionRevealed ? '👁 player sees' : '🙈 hidden from player'}

                                  </span>

                                </label>

                                {sectionText && (

                                  <div style={{

                                    marginTop: '6px',

                                    padding: '6px 8px',

                                    borderLeft: `2px solid ${sectionRevealed ? 'rgba(139,226,139,0.4)' : 'rgba(201,162,39,0.35)'}`,

                                    background: 'rgba(0,0,0,0.2)',

                                    fontSize: '0.85rem',

                                    opacity: sectionRevealed ? 1 : 0.8,

                                    whiteSpace: 'pre-wrap',

                                  }}>{sectionText}</div>

                                )}

                              </div>

                            );

                          }

                          

                          // Roll Table section

                          if (sec.kind === 'rolltable') {

                            const lastRoll = s.rollTableResults?.[sid2];

                            return (

                              <div key={sid2} style={{ marginBottom: '8px', padding: '8px', background: 'rgba(139,69,19,0.15)', borderRadius: '6px', border: '1px solid rgba(139,69,19,0.3)' }}>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>

                                  <div style={{ fontWeight: 'bold', color: '#c9a227' }}>🎲 {sec.label || 'Roll Table'} ({sec.diceType || 'd6'})</div>

                                  <button

                                    style={{ ...S.btn, ...S.btnXs, ...S.btnSuccess, marginBottom: 0 }}

                                    onClick={() => {

                                      const result = rollOnTable(sec);

                                      if (result) {

                                        const newResults = { ...(s.rollTableResults || {}), [sid2]: result };

                                        updateSystemVisibility(sid, { rollTableResults: newResults });

                                      }

                                    }}

                                  >

                                    🎲 Roll

                                  </button>

                                </div>

                                

                                {lastRoll && (

                                  <div style={{ padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px', marginBottom: '8px' }}>

                                    <div style={{ fontSize: '0.9rem', marginBottom: '4px' }}>

                                      <strong>Rolled: {lastRoll.roll}</strong> on {lastRoll.diceType}

                                    </div>

                                    <div style={{ padding: '6px', background: 'rgba(201,162,39,0.15)', borderRadius: '4px' }}>

                                      {lastRoll.result}

                                    </div>

                                  </div>

                                )}

                                

                                <details style={{ fontSize: '0.8rem' }}>

                                  <summary style={{ cursor: 'pointer', color: '#888' }}>View all entries</summary>

                                  <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '4px' }}>

                                    {(sec.items || []).map((item, idx) => (

                                      <div key={idx} style={{ display: 'flex', gap: '8px', opacity: lastRoll?.resultIndex === idx ? 1 : 0.7 }}>

                                        <span style={{ minWidth: '24px', fontWeight: 'bold' }}>{idx + 1}:</span>

                                        <span>{item}</span>

                                      </div>

                                    ))}

                                  </div>

                                </details>

                                

                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', fontSize: '0.85rem' }}>

                                  <input 

                                    type="checkbox" 

                                    checked={!!((s.showSections || {})[sid2])} 

                                    onChange={e => toggleSectionText(sid, sid2, e.target.checked)} 

                                  />

                                  Reveal result to player

                                </label>

                              </div>

                            );

                          }



                          // list

                          return (

                            <div key={sid2} style={{ marginBottom: '8px', padding: '8px', background: 'rgba(0,0,0,0.12)', borderRadius: '6px' }}>

                              <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>{sec.label || sid2}</div>

                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>

                                {(sec.items || []).map((it, i) => (

                                  <label key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', cursor: 'pointer' }}>

                                    <input type="checkbox" checked={((sd.visibleSections || {})[sid2] || []).includes(i)} onChange={() => toggleSectionItem(sid, sid2, i)} />

                                    <span style={{ opacity: (((sd.visibleSections || {})[sid2] || []).includes(i)) ? 1 : 0.7 }}>{it}</span>

                                  </label>

                                ))}

                              </div>

                            </div>

                          );

                        })}

                      </div>

                    )}



                    {/* Tracker Fields (for tracker-type systems) */}

                    {(sys?.type === 'tracker' || sys?.customData || (sys?.trackerFields || []).length > 0) && (

                      <div style={{ marginTop: '10px', padding: '10px', background: 'rgba(201,162,39,0.08)', borderRadius: '6px', border: '1px solid rgba(201,162,39,0.15)' }}>

                        <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#c9a227' }}>📊 Tracker Data</div>

                        {(sys?.trackerFields || []).length > 0 ? (

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

                            {sys.trackerFields.map((field, fi) => {

                              const fieldVis = (s.trackerVisibility || {})[field.id] || {};

                              const showToPlayer = fieldVis.showToPlayer !== false;

                              const showBar = fieldVis.showBar !== false;

                              const showValue = fieldVis.showValue !== false;

                              const showSource = fieldVis.showSource !== false;

                              

                              return (

                                <div key={field.id || fi} style={{ 

                                  padding: '8px', 

                                  background: 'rgba(0,0,0,0.15)', 

                                  borderRadius: '6px',

                                  border: showToPlayer ? '1px solid rgba(201,162,39,0.3)' : '1px solid rgba(100,100,100,0.3)',

                                }}>

                                  {/* Visibility Controls */}

                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px', fontSize: '0.75rem' }}>

                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>

                                      <input 

                                        type="checkbox" 

                                        checked={showToPlayer}

                                        onChange={e => {

                                          const newVis = { ...(s.trackerVisibility || {}), [field.id]: { ...fieldVis, showToPlayer: e.target.checked } };

                                          updateSystemVisibility(sid, { trackerVisibility: newVis });

                                        }}

                                      />

                                      👁 Show to Player

                                    </label>

                                    {showToPlayer && (

                                      <>

                                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>

                                          <input 

                                            type="checkbox" 

                                            checked={showBar}

                                            onChange={e => {

                                              const newVis = { ...(s.trackerVisibility || {}), [field.id]: { ...fieldVis, showBar: e.target.checked } };

                                              updateSystemVisibility(sid, { trackerVisibility: newVis });

                                            }}

                                          />

                                          📊 Bar

                                        </label>

                                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>

                                          <input 

                                            type="checkbox" 

                                            checked={showValue}

                                            onChange={e => {

                                              const newVis = { ...(s.trackerVisibility || {}), [field.id]: { ...fieldVis, showValue: e.target.checked } };

                                              updateSystemVisibility(sid, { trackerVisibility: newVis });

                                            }}

                                          />

                                          🔢 Value

                                        </label>

                                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>

                                          <input 

                                            type="checkbox" 

                                            checked={showSource}

                                            onChange={e => {

                                              const newVis = { ...(s.trackerVisibility || {}), [field.id]: { ...fieldVis, showSource: e.target.checked } };

                                              updateSystemVisibility(sid, { trackerVisibility: newVis });

                                            }}

                                          />

                                          🏷️ Source

                                        </label>

                                      </>

                                    )}

                                  </div>

                                  

                                  {/* Tracker Widget */}

                                  <TrackerWidget

                                    field={field}

                                    value={(s.trackerValues || {})[field.id] ?? field.value}

                                    onChange={(newVal) => {

                                      const newTrackerValues = { ...(s.trackerValues || {}), [field.id]: newVal };

                                      updateSystemVisibility(sid, { trackerValues: newTrackerValues });

                                    }}

                                    isPlayer={false}

                                    visibility={fieldVis}

                                  />

                                  

                                  {/* Preview what player sees */}

                                  {showToPlayer && !showBar && !showValue && (

                                    <div style={{ fontSize: '0.7rem', opacity: 0.6, marginTop: '4px' }}>

                                      Player will only see the tracker name, not its value

                                    </div>

                                  )}

                                </div>

                              );

                            })}

                          </div>

                        ) : (

                          <div style={{ fontSize: '0.85rem', opacity: 0.7 }}>

                            No tracker fields configured. Add them in the system template.

                          </div>

                        )}

                        

                        {/* Quick add simple counter */}

                        {!(sys?.trackerFields || []).length && (

                          <>
                          <QuickCounterFallback
                              value={s.quickCounter || 0}
                              onCommit={(nextValue) => updateSystemVisibility(sid, { quickCounter: nextValue })}
                            />

                            {false && <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>

                            <span style={{ fontSize: '0.85rem' }}>Quick counter:</span>

                            <button

                              onClick={() => {

                                const val = (s.quickCounter || 0) - 1;

                                updateSystemVisibility(sid, { quickCounter: val });

                              }}

                              style={{ ...S.btn, ...S.btnXs, marginBottom: 0 }}

                            >−</button>

                            <span style={{ fontWeight: 'bold', minWidth: '30px', textAlign: 'center' }}>

                              {s.quickCounter || 0}

                            </span>

                            <button

                              onClick={() => {

                                const val = (s.quickCounter || 0) + 1;

                                updateSystemVisibility(sid, { quickCounter: val });

                              }}

                              style={{ ...S.btn, ...S.btnXs, marginBottom: 0 }}

                            >+</button>

                          </div>}

                          </>

                        )}

                      </div>

                    )}



                    {/* Progression Stages (for systems with stages section) */}

                    {(() => {

                      const stagesSection = (sys?.sections || []).find(sec => sec.kind === 'stages');

                      if (!stagesSection) return null;

                      

                      const stages = getStagesFromSection(stagesSection);

                      if (stages.length === 0) return null;

                      

                      const currentStageIndex = s.currentStage ?? stagesSection.currentStage ?? 0;

                      const currentStage = stages[currentStageIndex];

                      

                      return (

                        <div style={{ marginTop: '10px', padding: '10px', background: 'rgba(255,107,107,0.08)', borderRadius: '6px', border: '1px solid rgba(255,107,107,0.2)' }}>

                          <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#ff6b6b' }}>

                            📈 Progression Stage ({currentStageIndex + 1}/{stages.length})

                          </div>

                          

                          {/* Current Stage Display */}

                          <div style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', marginBottom: '10px' }}>

                            <div style={{ fontWeight: 'bold', color: '#c9a227', marginBottom: '4px' }}>

                              {currentStage?.name || `Stage ${currentStageIndex + 1}`}

                            </div>

                            {currentStage?.description && (

                              <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '6px' }}>

                                {currentStage.description}

                              </div>

                            )}

                            {(currentStage?.effects || []).length > 0 && (

                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>

                                {currentStage.effects.map((eff, ei) => (

                                  <span key={ei} style={{ ...S.badge, ...S.badgeDebuff, fontSize: '0.75rem' }}>

                                    {eff}

                                  </span>

                                ))}

                              </div>

                            )}

                          </div>

                          

                          {/* Stage Navigation */}

                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>

                            <button

                              onClick={() => updateSystemVisibility(sid, { currentStage: Math.max(0, currentStageIndex - 1) })}

                              disabled={currentStageIndex === 0}

                              style={{ 

                                ...S.btn, ...S.btnXs, marginBottom: 0, 

                                opacity: currentStageIndex === 0 ? 0.4 : 1 

                              }}

                            >

                              ◀ Previous

                            </button>

                            <select

                              style={{ ...S.select, flex: 1, marginBottom: 0 }}

                              value={currentStageIndex}

                              onChange={e => updateSystemVisibility(sid, { currentStage: parseInt(e.target.value) })}

                            >

                              {stages.map((stage, idx) => (

                                <option key={idx} value={idx}>

                                  {stage.name || `Stage ${idx + 1}`}

                                </option>

                              ))}

                            </select>

                            <button

                              onClick={() => updateSystemVisibility(sid, { currentStage: Math.min(stages.length - 1, currentStageIndex + 1) })}

                              disabled={currentStageIndex >= stages.length - 1}

                              style={{ 

                                ...S.btn, ...S.btnXs, marginBottom: 0,

                                opacity: currentStageIndex >= stages.length - 1 ? 0.4 : 1 

                              }}

                            >

                              Next ▶

                            </button>

                          </div>

                          

                          {/* Stage visibility control */}

                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }}>

                            <input

                              type="checkbox"

                              checked={!!sd.showStage}

                              onChange={e => stageVis(sid, { showStage: e.target.checked }, s.autoApplyVisibility)}

                            />

                            Reveal current stage to player

                          </label>

                          

                          {/* All stages preview */}

                          <details style={{ marginTop: '8px' }}>

                            <summary style={{ cursor: 'pointer', fontSize: '0.8rem', color: '#888' }}>

                              View all stages

                            </summary>

                            <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '4px' }}>

                              {stages.map((stage, idx) => (

                                <div 

                                  key={idx} 

                                  style={{ 

                                    padding: '6px 8px', 

                                    background: idx === currentStageIndex ? 'rgba(201,162,39,0.2)' : 'rgba(0,0,0,0.1)',

                                    borderRadius: '4px',

                                    fontSize: '0.8rem',

                                    border: idx === currentStageIndex ? '1px solid rgba(201,162,39,0.4)' : '1px solid transparent',

                                  }}

                                >

                                  <span style={{ fontWeight: idx === currentStageIndex ? 'bold' : 'normal' }}>

                                    {idx + 1}. {stage.name}

                                  </span>

                                  {stage.effects?.length > 0 && (

                                    <span style={{ opacity: 0.7, marginLeft: '8px' }}>

                                      ({stage.effects.join(', ')})

                                    </span>

                                  )}

                                </div>

                              ))}

                            </div>

                          </details>

                        </div>

                      );

                    })()}



                    {/* Manual Automation Triggers */}

                    {(() => {

                      const manualRules = (sys?.automationRules || []).filter(r => r.enabled !== false && r.trigger === 'manual');

                      if (manualRules.length === 0) return null;

                      

                      return (

                        <div style={{ marginTop: '10px', padding: '10px', background: 'rgba(74,144,217,0.08)', borderRadius: '6px', border: '1px solid rgba(74,144,217,0.15)' }}>

                          <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#4a90d9' }}>⚙️ Manual Actions</div>

                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>

                            {manualRules.map((rule, ri) => (

                              <button

                                key={rule.id || ri}

                                style={{ ...S.btn, ...S.btnSm, marginBottom: 0 }}

                                onClick={() => {

                                  // Execute the rule

                                  const context = {

                                    char: c,

                                    systemInstance: s,

                                    systemTemplate: sys,

                                    trackerValues: s.trackerValues || {},

                                  };

                                  

                                  if (evaluateRuleConditions(rule, context)) {

                                    const results = executeRuleActions(rule, context);



                                    // Apply both char and system updates atomically through the

                                    // DMCard-level helper (fixes the prior charUpdates drop).

                                    if (applyManualRuleResults) {

                                      applyManualRuleResults(s.id || s.systemId, results);

                                    } else if (Object.keys(results.systemUpdates).length > 0) {

                                      // Fallback: if parent didn't wire the helper, at least apply system updates

                                      updateSystemVisibility(sid, results.systemUpdates);

                                    }



                                    // Show notifications

                                    if (!applyManualRuleResults && results.notifications.length > 0) {

                                      window.alert(results.notifications.join('\n'));

                                    }

                                  } else {

                                    window.alert(`Conditions not met for "${rule.name}"`);

                                  }

                                }}

                                title={`Trigger: ${rule.name}`}

                              >

                                ▶️ {rule.name}

                              </button>

                            ))}

                          </div>

                        </div>

                      );

                    })()}



                    {/* Notes */}

                    <div style={{ marginTop: '10px' }}>

                      <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>Notes</div>



                      <SystemVisibilityNotesFields
                        visibilityState={s}
                        onPatch={(patch) => updateSystemVisibility(sid, patch)}
                        labelStyle={S.label}
                        inputStyle={S.input}
                      />

                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px' }}>

                        <input type="checkbox" checked={!!s.showPlayerNotes} onChange={e => updateSystemVisibility(sid, { showPlayerNotes: e.target.checked })} />

                        Reveal player notes

                      </label>

                    </div>



                    {/* System History */}

                    {(s.history?.length > 0 || s.appliedAt) && (

                      <details style={{ marginTop: '10px' }}>

                        <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#888', fontSize: '0.85rem' }}>

                          📜 History ({(s.history || []).length} entries)

                        </summary>

                        <div style={{ marginTop: '8px', maxHeight: '200px', overflowY: 'auto', fontSize: '0.8rem' }}>

                          {s.appliedAt && (

                            <div style={{ padding: '4px 8px', background: 'rgba(39,201,90,0.1)', borderRadius: '4px', marginBottom: '4px' }}>

                              <span style={{ color: '#27c95a' }}>Applied:</span>{' '}

                              {new Date(s.appliedAt).toLocaleString()}

                            </div>

                          )}

                          {(s.history || []).slice().reverse().map((h, hi) => (

                            <div key={hi} style={{ 

                              padding: '4px 8px', 

                              background: h.action === 'applied' ? 'rgba(39,201,90,0.1)' : 

                                         h.action === 'removed' ? 'rgba(201,39,39,0.1)' : 

                                         'rgba(201,162,39,0.1)',

                              borderRadius: '4px',

                              marginBottom: '4px',

                            }}>

                              <span style={{ 

                                color: h.action === 'applied' ? '#27c95a' : 

                                       h.action === 'removed' ? '#c92727' : 

                                       '#c9a227',

                                fontWeight: 'bold',

                              }}>

                                {h.action === 'applied' ? '✨' : h.action === 'removed' ? '🗑️' : '📝'}

                              </span>{' '}

                              <span style={{ opacity: 0.7 }}>{new Date(h.timestamp).toLocaleString()}</span>

                              {h.details && <div style={{ marginLeft: '16px', opacity: 0.8 }}>{h.details}</div>}

                            </div>

                          ))}

                        </div>

                      </details>

                    )}



                    {/* Effects/cure quick preview */}

                    {sys?.effects?.length > 0 && (

                      <div style={{ marginTop: '10px', opacity: 0.9 }}>

                        <div style={{ fontWeight: 'bold' }}>Effects</div>

                        <div style={{ fontSize: '0.85rem', opacity: 0.85 }}>{(sys.effects || []).map(e => typeof e === 'string' ? e : (e.displayText || '')).join(', ')}</div>

                      </div>

                    )}



                    {sys?.cures?.length > 0 && (

                      <div style={{ marginTop: '10px', opacity: 0.9 }}>

                        <div style={{ fontWeight: 'bold' }}>Cures</div>

                        <div style={{ fontSize: '0.85rem', opacity: 0.85 }}>{sys.cures.map(cu => `${cu.quantity}x ${cu.item}`).join(', ')}</div>

                      </div>

                    )}

                  </div>

                )}

              </div>

            );

          })}

        </div>

      )}

    </div>

  );

}

export function DMSettings({ diets, diseases, systems, foodTypes, locations, globalSettings, chars, spellLibrary, campaignHomebrew = null, campaignSystemsEnabled = true, onSaveSpellLibrary, onUpdateChar, onClose, onSaveDiets, onSaveDiseases, onSaveSystems, onSaveFoodTypes, onSaveLocations, onSaveGlobalSettings, initialEditSystem = null, initialTab = 'effects' }) {

  const [tab, setTab] = useState(initialEditSystem && campaignSystemsEnabled ? 'systems' : initialTab);

  const [locDiets, setLocDiets] = useState({ ...diets });

  const [locDiseases, setLocDiseases] = useState({ ...diseases });

  const [locSystems, setLocSystems] = useState(() => normalizeSystemsMap({ ...(systems || DEFAULT_SYSTEMS) }));

  const [locFoodTypes, setLocFoodTypes] = useState([...foodTypes]);

  const [locLocations, setLocLocations] = useState([...locations]);

  const [locSettings, setLocSettings] = useState({ 

    ...DEFAULT_SETTINGS,

    ...(globalSettings || {}),

    armorTypes: globalSettings?.armorTypes || { ...DEFAULT_ARMOR_TYPES },

    armorDurability: globalSettings?.armorDurability || { ...DEFAULT_ARMOR_DURABILITY },

    auditLog: {

      ...(DEFAULT_SETTINGS.auditLog || {}),

      ...(globalSettings?.auditLog || {}),

      categories: {

        ...(DEFAULT_SETTINGS.auditLog?.categories || {}),

        ...(globalSettings?.auditLog?.categories || {}),

      },

      enabled: true,

    },

  });

  const [newFood, setNewFood] = useState('');
  const [wellFedBuffDraft, setWellFedBuffDraft] = useState('');
  const [hungryDebuffDraft, setHungryDebuffDraft] = useState('');
  const [starvationDebuffDraft, setStarvationDebuffDraft] = useState('');
  const [armorDurabilityDebuffDrafts, setArmorDurabilityDebuffDrafts] = useState({ damagedDebuffs: '', brokenDebuffs: '' });
  const [xpAwardDrafts, setXpAwardDrafts] = useState({});

  const [newLoc, setNewLoc] = useState('');

  const [editDiet, setEditDiet] = useState(null);

  const [editDisease, setEditDisease] = useState(null);

  const [editSystem, setEditSystem] = useState(initialEditSystem);

  const [showEffectBuilder, setShowEffectBuilder] = useState(null); // systemId when showing builder

  const [effectBuilderType, setEffectBuilderType] = useState('visible'); // 'visible' or 'hidden'

  const [newSystemName, setNewSystemName] = useState('');

  const [newSystemType, setNewSystemType] = useState('custom');

  const [editArmorType, setEditArmorType] = useState(null);

  const [systemSearch, setSystemSearch] = useState('');

  const [systemTypeFilter, setSystemTypeFilter] = useState('all');

  const [showImportModal, setShowImportModal] = useState(false);

  const [importText, setImportText] = useState('');

  const [importError, setImportError] = useState('');

  const [selectedForExport, setSelectedForExport] = useState(new Set());

  const [autoScrollToNew, setAutoScrollToNew] = useState(true);

  const [newlyCreatedSystem, setNewlyCreatedSystem] = useState(null);

  const [effectsInputText, setEffectsInputText] = useState({});

  const [hiddenEffectsInputText, setHiddenEffectsInputText] = useState({});

  const [cluesInputText, setCluesInputText] = useState({});

  

  // Ref for scrolling to new system

  const systemRefs = useRef({});

  const campaignHomebrewRules = useMemo(
    () => getEffectiveCampaignHomebrew(campaignHomebrew),
    [campaignHomebrew],
  );

  const dmSettingsTabs = useMemo(() => {
    const tabs = ['effects', 'colors', 'armor', 'currency', 'leveling'];
    if (campaignHomebrewRules.diets) tabs.push('diets');
    if (campaignHomebrewRules.diseases) tabs.push('diseases');
    if (campaignSystemsEnabled) tabs.push('systems');
    if (campaignHomebrewRules.foodSettings) tabs.push('food');
    tabs.push('locations', 'audit');
    return tabs;
  }, [campaignHomebrewRules, campaignSystemsEnabled]);

  useEffect(() => {
    if (!dmSettingsTabs.includes(tab)) setTab(dmSettingsTabs[0] || 'effects');
  }, [dmSettingsTabs, tab]);

  const cleanDmSettingsTabLabel = (t) => ({
    armor: 'Armor',
    currency: 'Currency',
    leveling: 'Leveling',
    audit: 'Audit Log',
    diseases: 'Diseases',
    systems: 'Systems',
    colors: 'Colors',
  }[t] || t.charAt(0).toUpperCase() + t.slice(1));

  const preserveHiddenSettingKeys = (draft, keys) => {
    const next = { ...(draft || {}) };
    keys.forEach((key) => {
      if (globalSettings && Object.prototype.hasOwnProperty.call(globalSettings, key)) {
        next[key] = globalSettings[key];
      } else {
        delete next[key];
      }
    });
    return next;
  };

  const save = () => {
    if (campaignHomebrewRules.diets && typeof onSaveDiets === 'function') onSaveDiets(locDiets);
    if (campaignHomebrewRules.diseases && typeof onSaveDiseases === 'function') onSaveDiseases(locDiseases);
    if (campaignSystemsEnabled && typeof onSaveSystems === 'function') onSaveSystems(normalizeSystemsMap(locSystems));
    if (campaignHomebrewRules.foodSettings && typeof onSaveFoodTypes === 'function') onSaveFoodTypes(locFoodTypes);
    if (typeof onSaveLocations === 'function') onSaveLocations(locLocations);
    if (typeof onSaveGlobalSettings === 'function') {
      let nextSettings = { ...locSettings };
      if (!campaignHomebrewRules.hungerStarvation) {
        nextSettings = preserveHiddenSettingKeys(nextSettings, [
          'wellFedBuffs',
          'hungryDebuffs',
          'starvationDebuffs',
          'hungerThreshold',
          'starvationThreshold',
        ]);
      }
      if (!campaignHomebrewRules.durability) {
        nextSettings = preserveHiddenSettingKeys(nextSettings, ['armorDurability']);
      }
      nextSettings.auditLog = {
        ...(nextSettings.auditLog || {}),
        enabled: true,
      };
      onSaveGlobalSettings(nextSettings);
    }
    onClose();
  };

  const appendDmSettingsListValue = useCallback((key, draftValue, clearDraft) => {
    const nextValue = String(draftValue || '').trim();
    if (!nextValue) return;

    setLocSettings((prev) => ({
      ...prev,
      [key]: [...(prev[key] || []), nextValue],
    }));
    clearDraft('');
  }, []);

  const appendArmorDurabilityDebuff = useCallback((field) => {
    const nextValue = String(armorDurabilityDebuffDrafts[field] || '').trim();
    if (!nextValue) return;

    setLocSettings((prev) => ({
      ...prev,
      armorDurability: {
        ...(prev.armorDurability || {}),
        [field]: [...(prev.armorDurability?.[field] || []), nextValue],
      },
    }));
    setArmorDurabilityDebuffDrafts((prev) => ({ ...prev, [field]: '' }));
  }, [armorDurabilityDebuffDrafts]);

  const awardDmSettingsXp = useCallback((charId, xpProgress) => {
    const amount = parseInt(xpAwardDrafts[charId], 10) || 0;
    if (amount <= 0) return;

    const newXp = xpProgress.currentXp + amount;
    const updates = { xp: newXp };
    const thresholds = locSettings.xpThresholds || XP_DEFAULTS;
    let newLevel = xpProgress.currentLevel;

    for (let lv = newLevel + 1; lv <= 20; lv++) {
      if (thresholds[lv] && newXp >= thresholds[lv]) newLevel = lv; else break;
    }

    if (newLevel > xpProgress.currentLevel) {
      updates.level = newLevel;
      window.alert(`🎉 ${xpProgress.name} leveled up to Level ${newLevel}!`);
    }

    onUpdateChar(charId, updates);
    setXpAwardDrafts((prev) => ({ ...prev, [charId]: '' }));
  }, [locSettings.xpThresholds, onUpdateChar, xpAwardDrafts]);

  const awardAllDmSettingsXp = useCallback(() => {
    const amount = parseInt(xpAwardDrafts.__all__, 10) || 0;
    if (amount <= 0) return;

    const thresholds = locSettings.xpThresholds || XP_DEFAULTS;
    const levelUps = [];

    Object.values(chars).forEach((char) => {
      const newXp = (char.xp || 0) + amount;
      const updates = { xp: newXp };
      let newLevel = char.level || 1;

      for (let lv = newLevel + 1; lv <= 20; lv++) {
        if (thresholds[lv] && newXp >= thresholds[lv]) newLevel = lv; else break;
      }

      if (newLevel > (char.level || 1)) {
        updates.level = newLevel;
        levelUps.push(`${char.name} → Lv ${newLevel}`);
      }

      onUpdateChar(char.id, updates);
    });

    if (levelUps.length > 0) window.alert(`🎉 Level ups!\n${levelUps.join('\n')}`);

    setXpAwardDrafts((prev) => ({ ...prev, __all__: '' }));
  }, [chars, locSettings.xpThresholds, onUpdateChar, xpAwardDrafts.__all__]);

  const updateDiet = (key, field, value) => { setLocDiets({ ...locDiets, [key]: { ...locDiets[key], [field]: value } }); };

  const updateDisease = (key, field, value) => { setLocDiseases({ ...locDiseases, [key]: { ...locDiseases[key], [field]: value } }); };

  const updateSystem = (key, patch) => { setLocSystems({ ...locSystems, [key]: normalizeSystemResourceModel({ ...locSystems[key], ...patch }) }); };

  const parseLines = (txt) => (txt || '').split(/\r?\n/).map(s => s.trim()).filter(Boolean);

  const formatCures = (cures) => (cures || []).map(c => `${c.quantity || 1}x ${c.item}`.trim()).join('\n');

  const parseCures = (txt) => parseLines(txt).map(line => {

    const m = line.match(/^(\d+)\s*x\s*(.+)$/i);

    if (m) return { quantity: parseInt(m[1]) || 1, item: (m[2] || '').trim() };

    return { quantity: 1, item: (line || '').trim() };

  }).filter(c => c.item);

  const toggleFromArray = (arr, value, enabled) => {

    const set = new Set(arr || []);

    if (enabled) set.add(value);

    else set.delete(value);

    return Array.from(set);

  };

  const makeSystemId = (name) => {

    return makeUniqueSystemId(name, new Set(Object.keys(locSystems || {})));

  };

  

  // Analyze what an effect actually does (stat change vs label only)

  const analyzeEffect = (effect) => {

    const effText = typeof effect === 'string' ? effect : (effect.displayText || '');

    if (!effText) return { hasStatChange: false, description: 'Empty effect' };

    

    // Check for stat modifiers like "+1 Str", "-2 Con"

    const statMatch = effText.match(/([+-])\s*(\d+)\s*(str|dex|con|int|wis|cha|ac|hp|max hp)/i);

    if (statMatch) {

      const sign = statMatch[1] === '+' ? '+' : '';

      const val = parseInt(statMatch[2]);

      const stat = statMatch[3].toUpperCase();

      return { 

        hasStatChange: true, 

        description: `Applies ${sign}${statMatch[1] === '-' ? '-' : ''}${val} to ${stat}`,

        type: 'stat'

      };

    }

    

    // Check for skill modifiers like "+2 Perception", "-1 Stealth"

    const skillMatch = effText.match(/([+-])\s*(\d+)\s+(\w+(?:\s+\w+)?)/i);

    if (skillMatch) {

      const knownSkills = ['Acrobatics', 'Animal Handling', 'Arcana', 'Athletics', 'Deception', 'History', 'Insight', 'Intimidation', 'Investigation', 'Medicine', 'Nature', 'Perception', 'Performance', 'Persuasion', 'Religion', 'Sleight of Hand', 'Stealth', 'Survival'];

      const matchedSkill = knownSkills.find(s => s.toLowerCase() === skillMatch[3].toLowerCase());

      if (matchedSkill) {

        return {

          hasStatChange: true,

          description: `Applies ${skillMatch[1]}${skillMatch[2]} to ${matchedSkill} checks`,

          type: 'skill'

        };

      }

    }

    

    // Check for advantage/disadvantage

    if (/advantage/i.test(effText)) {

      return { hasStatChange: true, description: 'Grants advantage (shown as badge)', type: 'advantage' };

    }

    if (/disadvantage/i.test(effText)) {

      return { hasStatChange: true, description: 'Grants disadvantage (shown as badge)', type: 'disadvantage' };

    }

    

    // Otherwise it's a label-only effect

    return { 

      hasStatChange: false, 

      description: 'Display only - no mechanical stat changes applied',

      type: 'label'

    };

  };

  

  // Auto-scroll to newly created system

  useEffect(() => {

    if (newlyCreatedSystem && autoScrollToNew && systemRefs.current[newlyCreatedSystem]) {

      setTimeout(() => {

        const element = systemRefs.current[newlyCreatedSystem];

        if (element) {

          // Find scrollable parent (modal content)

          const scrollParent = element.closest('[style*="overflow"]') || element.parentElement?.parentElement?.parentElement;

          if (scrollParent && scrollParent.scrollTo) {

            const elementTop = element.offsetTop;

            const parentHeight = scrollParent.clientHeight;

            const scrollTarget = elementTop - (parentHeight / 3);

            scrollParent.scrollTo({ top: scrollTarget, behavior: 'smooth' });

          } else {

            // Fallback to scrollIntoView

            element.scrollIntoView({ behavior: 'smooth', block: 'center' });

          }

        }

        // Clear after animation

        setTimeout(() => setNewlyCreatedSystem(null), 1500);

      }, 150);

    }

  }, [newlyCreatedSystem, autoScrollToNew]);

  

  // Helper to create system and trigger animation

  const createSystemWithAnimation = (id, systemData) => {

    setLocSystems({ ...locSystems, [id]: normalizeSystemResourceModel(systemData) });

    setEditSystem(id);

    setNewlyCreatedSystem(id);

    // Auto-select new systems for export

    setSelectedForExport(prev => new Set([...prev, id]));

  };

  

  // Export selected systems as JSON

  const exportSystems = (systemIds) => {

    const toExport = systemIds.map(id => locSystems[id]).filter(Boolean);

    if (toExport.length === 0) return;

    

    const exportData = buildSystemExportBundle(locSystems, systemIds);

    

    const json = JSON.stringify(exportData, null, 2);

    const blob = new Blob([json], { type: 'application/json' });

    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');

    a.href = url;

    a.download = `systems-export-${new Date().toISOString().slice(0, 10)}.json`;

    a.click();

    URL.revokeObjectURL(url);

    setSelectedForExport(new Set());

  };

  

  // Import systems from JSON

  const importSystems = () => {

    setImportError('');

    try {

      const data = JSON.parse(importText);

      if (!data.systems || !Array.isArray(data.systems)) {

        setImportError('Invalid format: missing "systems" array');

        return;

      }

      

      const importResult = prepareImportedSystemsBundle({

        currentSystems: locSystems,

        importedSystems: data.systems,

      });

      const { systems: newSystems, imported } = importResult;

      

      if (imported === 0) {

        setImportError('No valid systems found in import data');

        return;

      }

      

      setLocSystems(normalizeSystemsMap(newSystems));

      setShowImportModal(false);

      setImportText('');

      setImportError('');

    } catch (e) {

      setImportError(`Parse error: ${e.message}`);

    }

  };



  const updateArmorType = (key, field, value) => {

    setLocSettings({

      ...locSettings,

      armorTypes: {

        ...locSettings.armorTypes,

        [key]: { ...locSettings.armorTypes[key], [field]: value }

      }

    });

  };

  const updateArmorDurability = (field, value) => {

    setLocSettings({

      ...locSettings,

      armorDurability: { ...locSettings.armorDurability, [field]: value }

    });

  };



  return <div style={S.modal} onClick={onClose}><div style={{ ...S.modalContent, maxWidth: '800px' }} onClick={e => e.stopPropagation()}>

    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}><h2 style={{ ...S.subtitle, marginBottom: 0, border: 'none' }}>⚙️ DM Settings</h2><button style={{ ...S.btn, ...S.btnSm }} onClick={onClose}>✕</button></div>

    <div style={S.tabs}>{dmSettingsTabs.map(t => <button key={t} style={{ ...S.tab, ...(tab === t ? S.tabActive : {}) }} onClick={() => setTab(t)}>{cleanDmSettingsTabLabel(t)}</button>)}</div>

    <div style={{ minHeight: '300px', maxHeight: '50vh', overflowY: 'auto' }}>

      {tab === 'effects' && <div>

        {/* Buff Click-Off Setting */}

        <div style={{ marginBottom: '20px', padding: '12px', background: 'rgba(201,162,39,0.1)', borderRadius: '6px', border: '1px solid rgba(201,162,39,0.3)' }}>

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem' }}>

            <input 

              type="checkbox" 

              checked={locSettings.allowBuffClickOff !== false} 

              onChange={e => setLocSettings({ ...locSettings, allowBuffClickOff: e.target.checked })}

            />

            <span style={{ fontWeight: 'bold', color: '#c9a227' }}>Allow Players to Click Off Buffs/Debuffs</span>

          </label>

          <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '4px', marginLeft: '24px' }}>

            When enabled, players can click on buff/debuff badges to remove them. Item-linked effects will prompt for confirmation.

          </div>

        </div>

        
        {campaignHomebrewRules.hungerStarvation ? <>

        <h3 style={{ color: '#27c95a', marginBottom: '8px' }}>Well Fed Buffs</h3>

        <div style={{ marginBottom: '8px' }}>{(locSettings.wellFedBuffs || []).map((b, i) => <span key={i} style={{ ...S.badge, ...S.badgeBuff, cursor: 'pointer' }} onClick={() => setLocSettings({ ...locSettings, wellFedBuffs: locSettings.wellFedBuffs.filter((_, idx) => idx !== i) })}>{b} ✕</span>)}</div>

        <ControlledMiniForm
          style={S.flex}
          onSubmit={() => appendDmSettingsListValue('wellFedBuffs', wellFedBuffDraft, setWellFedBuffDraft)}
          submitLabel="Add"
          submitDisabled={!wellFedBuffDraft.trim()}
          actionsStyle={{ display: 'flex', alignItems: 'center' }}
          submitButtonStyle={{ ...S.btn, ...S.btnSm }}
        >
          <input style={{ ...S.input, flex: 1, marginBottom: 0 }} placeholder="+2 Con" value={wellFedBuffDraft} onChange={e => setWellFedBuffDraft(e.target.value)} />
        </ControlledMiniForm>

        <h3 style={{ color: '#c9a227', marginTop: '20px', marginBottom: '8px' }}>Hungry Debuffs</h3>

        <div style={{ marginBottom: '8px' }}>{(locSettings.hungryDebuffs || []).map((d, i) => <span key={i} style={{ ...S.badge, ...S.badgeDebuff, cursor: 'pointer' }} onClick={() => setLocSettings({ ...locSettings, hungryDebuffs: locSettings.hungryDebuffs.filter((_, idx) => idx !== i) })}>{d} ✕</span>)}</div>

        <ControlledMiniForm
          style={S.flex}
          onSubmit={() => appendDmSettingsListValue('hungryDebuffs', hungryDebuffDraft, setHungryDebuffDraft)}
          submitLabel="Add"
          submitDisabled={!hungryDebuffDraft.trim()}
          actionsStyle={{ display: 'flex', alignItems: 'center' }}
          submitButtonStyle={{ ...S.btn, ...S.btnSm }}
        >
          <input style={{ ...S.input, flex: 1, marginBottom: 0 }} placeholder="-1 Con" value={hungryDebuffDraft} onChange={e => setHungryDebuffDraft(e.target.value)} />
        </ControlledMiniForm>

        <h3 style={{ color: '#c92727', marginTop: '20px', marginBottom: '8px' }}>Starvation Debuffs</h3>

        <div style={{ marginBottom: '8px' }}>{(locSettings.starvationDebuffs || []).map((d, i) => <span key={i} style={{ ...S.badge, ...S.badgeDebuff, cursor: 'pointer' }} onClick={() => setLocSettings({ ...locSettings, starvationDebuffs: locSettings.starvationDebuffs.filter((_, idx) => idx !== i) })}>{d} ✕</span>)}</div>

        <ControlledMiniForm
          style={S.flex}
          onSubmit={() => appendDmSettingsListValue('starvationDebuffs', starvationDebuffDraft, setStarvationDebuffDraft)}
          submitLabel="Add"
          submitDisabled={!starvationDebuffDraft.trim()}
          actionsStyle={{ display: 'flex', alignItems: 'center' }}
          submitButtonStyle={{ ...S.btn, ...S.btnSm }}
        >
          <input style={{ ...S.input, flex: 1, marginBottom: 0 }} placeholder="-2 Str" value={starvationDebuffDraft} onChange={e => setStarvationDebuffDraft(e.target.value)} />
        </ControlledMiniForm>

        </> : (
          <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(0,0,0,0.22)', borderRadius: '6px', border: '1px solid rgba(232,220,196,0.14)', color: '#aaa', fontSize: '0.85rem' }}>
            Hunger and starvation settings are disabled for this campaign.
          </div>
        )}

      </div>}

      

      {/* Colors Settings Tab */}

      {tab === 'colors' && <div>

        <p style={{ fontSize: '0.85rem', color: '#888', marginBottom: '16px' }}>

          Customize buff/debuff color presets. Changes apply to new effects — existing effects keep their original colors.

        </p>

        

        <h3 style={{ color: '#c9a227', marginBottom: '12px' }}>🎨 Color Presets</h3>

        <div style={{ display: 'grid', gap: '8px' }}>

          {getBuffColorOptions(locSettings.customColors || []).map((colorOpt, idx) => {

            const isCustomized = (locSettings.customColors || []).find(c => c.id === colorOpt.id);

            const defaultOpt = DEFAULT_BUFF_COLOR_OPTIONS.find(c => c.id === colorOpt.id);

            

            return (

              <div key={colorOpt.id} style={{ 

                padding: '10px', 

                background: 'rgba(0,0,0,0.2)', 

                borderRadius: '6px',

                border: `1px solid ${colorOpt.color}`,

              }}>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>

                  {/* Color preview */}

                  <div style={{ 

                    width: '36px', 

                    height: '36px', 

                    borderRadius: '4px', 

                    background: colorOpt.bgColor,

                    border: `2px solid ${colorOpt.color}`,

                    display: 'flex',

                    alignItems: 'center',

                    justifyContent: 'center',

                    color: colorOpt.textColor || colorOpt.color,

                    fontWeight: 'bold',

                    fontSize: '0.8rem',

                    flexShrink: 0,

                  }}>

                    Aa

                  </div>

                  

                  {/* Name input */}

                  <input 

                    style={{ ...S.input, flex: 1, marginBottom: 0, fontSize: '0.85rem' }} 

                    value={colorOpt.name}

                    onChange={e => {

                      const customs = [...(locSettings.customColors || [])];

                      const existing = customs.findIndex(c => c.id === colorOpt.id);

                      if (existing >= 0) {

                        customs[existing] = { ...customs[existing], name: e.target.value };

                      } else {

                        customs.push({ id: colorOpt.id, name: e.target.value });

                      }

                      setLocSettings({ ...locSettings, customColors: customs });

                    }}

                  />

                  

                  {/* Reset button if customized */}

                  {isCustomized && defaultOpt && (

                    <Tooltip text="Reset to default">

                      <button 

                        style={{ ...S.btn, ...S.btnXs, padding: '4px 8px' }}

                        onClick={() => {

                          const customs = (locSettings.customColors || []).filter(c => c.id !== colorOpt.id);

                          setLocSettings({ ...locSettings, customColors: customs });

                        }}

                      >

                        ↺

                      </button>

                    </Tooltip>

                  )}

                </div>

                

                {/* Color inputs */}

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>

                  <div style={{ flex: '1 1 120px' }}>

                    <label style={{ display: 'block', fontSize: '0.65rem', color: '#888', marginBottom: '2px' }}>Border/Accent</label>

                    <div style={{ display: 'flex', gap: '4px' }}>

                      <input 

                        type="color" 

                        value={colorOpt.color} 

                        style={{ width: '32px', height: '24px', cursor: 'pointer', border: 'none' }}

                        onChange={e => {

                          const customs = [...(locSettings.customColors || [])];

                          const existing = customs.findIndex(c => c.id === colorOpt.id);

                          if (existing >= 0) {

                            customs[existing] = { ...customs[existing], color: e.target.value };

                          } else {

                            customs.push({ id: colorOpt.id, color: e.target.value });

                          }

                          setLocSettings({ ...locSettings, customColors: customs });

                        }}

                      />

                      <input 

                        style={{ ...S.input, flex: 1, marginBottom: 0, padding: '2px 4px', fontSize: '0.7rem' }} 

                        value={colorOpt.color}

                        onChange={e => {

                          const customs = [...(locSettings.customColors || [])];

                          const existing = customs.findIndex(c => c.id === colorOpt.id);

                          if (existing >= 0) {

                            customs[existing] = { ...customs[existing], color: e.target.value };

                          } else {

                            customs.push({ id: colorOpt.id, color: e.target.value });

                          }

                          setLocSettings({ ...locSettings, customColors: customs });

                        }}

                      />

                    </div>

                  </div>

                  

                  <div style={{ flex: '1 1 120px' }}>

                    <label style={{ display: 'block', fontSize: '0.65rem', color: '#888', marginBottom: '2px' }}>Text Color</label>

                    <div style={{ display: 'flex', gap: '4px' }}>

                      <input 

                        type="color" 

                        value={colorOpt.textColor || colorOpt.color} 

                        style={{ width: '32px', height: '24px', cursor: 'pointer', border: 'none' }}

                        onChange={e => {

                          const customs = [...(locSettings.customColors || [])];

                          const existing = customs.findIndex(c => c.id === colorOpt.id);

                          if (existing >= 0) {

                            customs[existing] = { ...customs[existing], textColor: e.target.value };

                          } else {

                            customs.push({ id: colorOpt.id, textColor: e.target.value });

                          }

                          setLocSettings({ ...locSettings, customColors: customs });

                        }}

                      />

                      <input 

                        style={{ ...S.input, flex: 1, marginBottom: 0, padding: '2px 4px', fontSize: '0.7rem' }} 

                        value={colorOpt.textColor || colorOpt.color}

                        onChange={e => {

                          const customs = [...(locSettings.customColors || [])];

                          const existing = customs.findIndex(c => c.id === colorOpt.id);

                          if (existing >= 0) {

                            customs[existing] = { ...customs[existing], textColor: e.target.value };

                          } else {

                            customs.push({ id: colorOpt.id, textColor: e.target.value });

                          }

                          setLocSettings({ ...locSettings, customColors: customs });

                        }}

                      />

                    </div>

                  </div>

                  

                  <div style={{ flex: '1 1 120px' }}>

                    <label style={{ display: 'block', fontSize: '0.65rem', color: '#888', marginBottom: '2px' }}>Glow Color</label>

                    <div style={{ display: 'flex', gap: '4px' }}>

                      <input 

                        type="color" 

                        value={colorOpt.glowColor || colorOpt.color} 

                        style={{ width: '32px', height: '24px', cursor: 'pointer', border: 'none' }}

                        onChange={e => {

                          const customs = [...(locSettings.customColors || [])];

                          const existing = customs.findIndex(c => c.id === colorOpt.id);

                          if (existing >= 0) {

                            customs[existing] = { ...customs[existing], glowColor: e.target.value };

                          } else {

                            customs.push({ id: colorOpt.id, glowColor: e.target.value });

                          }

                          setLocSettings({ ...locSettings, customColors: customs });

                        }}

                      />

                      <input 

                        style={{ ...S.input, flex: 1, marginBottom: 0, padding: '2px 4px', fontSize: '0.7rem' }} 

                        value={colorOpt.glowColor || colorOpt.color}

                        onChange={e => {

                          const customs = [...(locSettings.customColors || [])];

                          const existing = customs.findIndex(c => c.id === colorOpt.id);

                          if (existing >= 0) {

                            customs[existing] = { ...customs[existing], glowColor: e.target.value };

                          } else {

                            customs.push({ id: colorOpt.id, glowColor: e.target.value });

                          }

                          setLocSettings({ ...locSettings, customColors: customs });

                        }}

                      />

                    </div>

                  </div>

                </div>

              </div>

            );

          })}

        </div>

        

        {/* Add Custom Color */}

        <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(39,201,90,0.1)', borderRadius: '6px', border: '1px dashed rgba(39,201,90,0.3)' }}>

          <h4 style={{ color: '#4ade80', marginBottom: '8px' }}>➕ Add Custom Color</h4>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>

            <input 

              style={{ ...S.input, flex: '1 1 150px', marginBottom: 0 }} 

              placeholder="Color Name (e.g., Celestial)" 

              id="newColorName"

            />

            <input 

              type="color" 

              defaultValue="#7eb3ec" 

              id="newColorValue"

              style={{ width: '40px', height: '32px', cursor: 'pointer', border: 'none' }}

            />

            <button 

              style={{ ...S.btn, ...S.btnSuccess, ...S.btnSm }}

              onClick={() => {

                const nameEl = document.getElementById('newColorName');

                const colorEl = document.getElementById('newColorValue');

                const name = nameEl.value.trim();

                const color = colorEl.value;

                if (!name) return;

                

                const id = name.toLowerCase().replace(/[^a-z0-9]+/g, '-');

                const customs = [...(locSettings.customColors || [])];

                

                // Generate lighter text color

                const textColor = color.replace(/^#/, '');

                const r = Math.min(255, parseInt(textColor.slice(0,2), 16) + 50);

                const g = Math.min(255, parseInt(textColor.slice(2,4), 16) + 50);

                const b = Math.min(255, parseInt(textColor.slice(4,6), 16) + 50);

                const lighterColor = `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;

                

                customs.push({

                  id: `custom-${id}`,

                  name,

                  color,

                  textColor: lighterColor,

                  bgColor: `${color}30`,

                  glowColor: color,

                  isCustom: true,

                });

                setLocSettings({ ...locSettings, customColors: customs });

                nameEl.value = '';

              }}

            >

              Add

            </button>

          </div>

        </div>

        

        {/* Delete custom colors */}

        {(locSettings.customColors || []).filter(c => c.isCustom).length > 0 && (

          <div style={{ marginTop: '12px' }}>

            <h4 style={{ color: '#c92727', marginBottom: '8px' }}>🗑️ Custom Colors</h4>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>

              {(locSettings.customColors || []).filter(c => c.isCustom).map(c => (

                <span 

                  key={c.id}

                  style={{ 

                    ...S.badge, 

                    background: c.bgColor || `${c.color}30`,

                    borderColor: c.color,

                    color: c.textColor || c.color,

                    cursor: 'pointer',

                  }}

                  onClick={() => {

                    const customs = (locSettings.customColors || []).filter(x => x.id !== c.id);

                    setLocSettings({ ...locSettings, customColors: customs });

                  }}

                >

                  {c.name} ✕

                </span>

              ))}

            </div>

          </div>

        )}

        

        {/* Apply to existing effects */}

        <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(155,89,182,0.1)', borderRadius: '6px', border: '1px solid rgba(155,89,182,0.3)' }}>

          <h4 style={{ color: '#9b59b6', marginBottom: '8px' }}>🔄 Apply Colors to Existing Effects</h4>

          <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '12px' }}>

            Update all existing buffs/debuffs that use a specific color preset with the current customized colors.

          </p>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>

            {getBuffColorOptions(locSettings.customColors || []).map(colorOpt => {

              // Count how many effects use this color

              let count = 0;

              Object.values(chars).forEach(c => {

                [...(c.buffList || []), ...(c.permanentBuffs || []), ...(c.debuffList || []), ...(c.permanentDebuffs || [])].forEach(b => {

                  const buffData = (c.buffData || {})[b];

                  if (buffData?.color === colorOpt.id) count++;

                });

              });

              

              if (count === 0) return null;

              

              return (

                <Tooltip key={colorOpt.id} text={`Update ${count} effect${count !== 1 ? 's' : ''} to current "${colorOpt.name}" colors`}>

                  <button

                    style={{

                      ...S.btn,

                      ...S.btnXs,

                      background: colorOpt.bgColor,

                      borderColor: colorOpt.color,

                      color: colorOpt.textColor || colorOpt.color,

                    }}

                    onClick={() => {

                      // This will update buffData for all effects using this color

                      // The color ID stays the same, but when rendered, it will use the new customized colors

                      // Since colors are looked up by ID at render time, this is actually automatic!

                      // But we'll "touch" the chars to trigger a re-render

                      window.alert(`Colors for "${colorOpt.name}" are already applied!\n\nColor presets are looked up by ID when effects are displayed, so any customizations you make will automatically show on existing effects with this color ID.`);

                    }}

                  >

                    {colorOpt.name} ({count})

                  </button>

                </Tooltip>

              );

            })}

          </div>

          <p style={{ fontSize: '0.7rem', color: '#666', marginTop: '8px' }}>

            💡 Tip: Color changes are already live! When you customize a color preset, all effects using that preset automatically show the new colors.

          </p>

        </div>

      </div>}

      

      {/* Armor Settings Tab */}

      {tab === 'armor' && <div>

        {campaignHomebrewRules.durability ? <>

        <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(201,162,39,0.1)', borderRadius: '6px', border: '1px solid rgba(201,162,39,0.3)' }}>

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem' }}>

            <input 

              type="checkbox" 

              checked={locSettings.durabilityEnabled !== false} 

              onChange={e => setLocSettings({ ...locSettings, durabilityEnabled: e.target.checked })}

            />

            <span style={{ fontWeight: 'bold', color: '#c9a227' }}>Enable Armor Durability System</span>

          </label>

          <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '4px', marginLeft: '24px' }}>

            When disabled, armor won't degrade and durability UI will be hidden

          </div>

        </div>

        

        {locSettings.durabilityEnabled !== false && <>

        <h3 style={{ color: '#c9a227', marginBottom: '12px' }}>🛡️ Armor Durability Settings</h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>

          <div>

            <label style={S.label}>Max Durability</label>

            <input style={S.input} type="number" value={locSettings.armorDurability?.maxDurability || 25} 

              onChange={e => updateArmorDurability('maxDurability', parseInt(e.target.value) || 25)} />

          </div>

          <div>

            <label style={S.label}>Damaged Threshold</label>

            <input style={S.input} type="number" value={locSettings.armorDurability?.damagedThreshold || 15} 

              onChange={e => updateArmorDurability('damagedThreshold', parseInt(e.target.value) || 15)} />

            <div style={{ fontSize: '0.7rem', opacity: 0.6 }}>At or below = damaged</div>

          </div>

          <div>

            <label style={S.label}>Broken Threshold</label>

            <input style={S.input} type="number" value={locSettings.armorDurability?.brokenThreshold || 5} 

              onChange={e => updateArmorDurability('brokenThreshold', parseInt(e.target.value) || 5)} />

            <div style={{ fontSize: '0.7rem', opacity: 0.6 }}>At or below = broken</div>

          </div>

        </div>

        

        <h4 style={{ color: '#c9a227', marginBottom: '8px' }}>⚠️ Damaged Armor Debuffs</h4>

        <div style={{ marginBottom: '8px' }}>

          {(locSettings.armorDurability?.damagedDebuffs || []).map((d, i) => (

            <span key={i} style={{ ...S.badge, ...S.badgeDebuff, cursor: 'pointer' }} 

              onClick={() => updateArmorDurability('damagedDebuffs', locSettings.armorDurability.damagedDebuffs.filter((_, idx) => idx !== i))}>

              {d} ✕

            </span>

          ))}

        </div>

        <ControlledMiniForm
          style={S.flex}
          onSubmit={() => appendArmorDurabilityDebuff('damagedDebuffs')}
          submitLabel="Add"
          actionsStyle={{ display: 'flex', alignItems: 'center' }}
          submitButtonStyle={{ ...S.btn, ...S.btnSm, marginBottom: 0 }}
        >

          <input
            style={{ ...S.input, flex: 1, marginBottom: 0 }}
            placeholder="-1 Str"
            aria-label="Damaged armor debuff"
            value={armorDurabilityDebuffDrafts.damagedDebuffs || ''}
            onChange={e => setArmorDurabilityDebuffDrafts((prev) => ({ ...prev, damagedDebuffs: e.target.value }))}
          />

        </ControlledMiniForm>

        

        <h4 style={{ color: '#c92727', marginTop: '16px', marginBottom: '8px' }}>💔 Broken Armor Debuffs</h4>

        <div style={{ marginBottom: '8px' }}>

          {(locSettings.armorDurability?.brokenDebuffs || []).map((d, i) => (

            <span key={i} style={{ ...S.badge, ...S.badgeDebuff, cursor: 'pointer' }} 

              onClick={() => updateArmorDurability('brokenDebuffs', locSettings.armorDurability.brokenDebuffs.filter((_, idx) => idx !== i))}>

              {d} ✕

            </span>

          ))}

        </div>

        <ControlledMiniForm
          style={S.flex}
          onSubmit={() => appendArmorDurabilityDebuff('brokenDebuffs')}
          submitLabel="Add"
          actionsStyle={{ display: 'flex', alignItems: 'center' }}
          submitButtonStyle={{ ...S.btn, ...S.btnSm, marginBottom: 0 }}
        >

          <input
            style={{ ...S.input, flex: 1, marginBottom: 0 }}
            placeholder="-2 Str"
            aria-label="Broken armor debuff"
            value={armorDurabilityDebuffDrafts.brokenDebuffs || ''}
            onChange={e => setArmorDurabilityDebuffDrafts((prev) => ({ ...prev, brokenDebuffs: e.target.value }))}
          />

        </ControlledMiniForm>

        </>}

        </> : (
          <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(0,0,0,0.22)', borderRadius: '6px', border: '1px solid rgba(232,220,196,0.14)', color: '#aaa', fontSize: '0.85rem' }}>
            Armor durability is disabled for this campaign. Armor types and AC settings remain available.
          </div>
        )}

        

        <h3 style={{ color: '#c9a227', marginTop: '24px', marginBottom: '12px' }}>⚔️ Armor Types</h3>

        {Object.entries(locSettings.armorTypes || DEFAULT_ARMOR_TYPES).map(([key, armor]) => (

          <div key={key} style={{ padding: '12px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', marginBottom: '8px' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

              <strong style={{ color: '#ffd700' }}>{armor.name}</strong>

              <button style={{ ...S.btn, ...S.btnEdit, ...S.btnXs }} onClick={() => setEditArmorType(editArmorType === key ? null : key)}>✏️</button>

            </div>

            {editArmorType !== key && (

              <div style={{ fontSize: '0.8rem', marginTop: '4px' }}>

                Base AC: {armor.baseAC} | 

                Buffs: {armor.buffs?.join(', ') || 'None'} | 

                Debuffs: {armor.debuffs?.join(', ') || 'None'}

              </div>

            )}

            {editArmorType === key && (

              <div style={{ marginTop: '8px' }}>

                <label style={S.label}>Name</label>

                <input style={S.input} value={armor.name} onChange={e => updateArmorType(key, 'name', e.target.value)} />

                <label style={S.label}>Base AC</label>

                <input style={S.input} type="number" value={armor.baseAC} onChange={e => updateArmorType(key, 'baseAC', parseInt(e.target.value) || 10)} />

                <label style={S.label}>Description</label>

                <input style={S.input} value={armor.description || ''} onChange={e => updateArmorType(key, 'description', e.target.value)} />

                <label style={S.label}>Buffs (comma-sep)</label>

                <CommaSeparatedInput style={S.input} value={armor.buffs || []} 

                  onChange={v => updateArmorType(key, 'buffs', v)} />

                <label style={S.label}>Debuffs (comma-sep)</label>

                <CommaSeparatedInput style={S.input} value={armor.debuffs || []} 

                  onChange={v => updateArmorType(key, 'debuffs', v)} />

              </div>

            )}

          </div>

        ))}

      </div>}

      

      {/* Currency Settings Tab */}

      {tab === 'currency' && <div>

        <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(201,162,39,0.1)', borderRadius: '6px', border: '1px solid rgba(201,162,39,0.3)' }}>

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem' }}>

            <input 

              type="checkbox" 

              checked={locSettings.currencyEnabled !== false} 

              onChange={e => setLocSettings({ ...locSettings, currencyEnabled: e.target.checked })}

            />

            <span style={{ fontWeight: 'bold', color: '#c9a227' }}>Enable Currency System</span>

          </label>

        </div>

        

        {locSettings.currencyEnabled !== false && <>

          <h3 style={{ color: '#ffd700', marginBottom: '12px' }}>💰 Currency Tiers</h3>

          <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '12px' }}>

            Define your currency from lowest to highest value. Each tier multiplies from the previous tier.

          </div>

          

          {(locSettings.currencyTiers || DEFAULT_SETTINGS.currencyTiers).map((tier, idx) => {

            const iconDef = CURRENCY_ICONS[tier.icon] || CURRENCY_ICONS.coinGold;

            return (

            <div key={tier.id} style={{ 

              padding: '10px', 

              background: idx === 0 ? 'rgba(139,105,20,0.2)' : 'rgba(0,0,0,0.2)', 

              borderRadius: '6px', 

              marginBottom: '8px',

              border: idx === 0 ? '1px solid rgba(139,105,20,0.4)' : '1px solid rgba(255,255,255,0.1)',

            }}>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>

                {/* Icon preview and selector */}

                <div style={{ position: 'relative' }}>

                  <div style={{ 

                    width: '32px', 

                    height: '32px', 

                    background: 'rgba(0,0,0,0.4)', 

                    border: `2px solid ${iconDef.color}55`, 

                    borderRadius: '6px',

                    display: 'flex',

                    alignItems: 'center',

                    justifyContent: 'center',

                  }}>

                    <CurrencyIcon type={tier.icon || 'coinGold'} size={22} />

                  </div>

                </div>

                <select

                  style={{ 

                    width: '120px', 

                    padding: '4px', 

                    background: 'rgba(0,0,0,0.4)', 

                    border: '1px solid rgba(255,255,255,0.2)', 

                    borderRadius: '4px', 

                    color: '#e8d5b7',

                    fontSize: '0.75rem',

                    cursor: 'pointer',

                  }}

                  value={tier.icon || 'coinGold'}

                  onChange={e => {

                    const newTiers = [...(locSettings.currencyTiers || DEFAULT_SETTINGS.currencyTiers)];

                    newTiers[idx] = { ...tier, icon: e.target.value };

                    setLocSettings({ ...locSettings, currencyTiers: newTiers });

                  }}

                >

                  <optgroup label="Single Coins">

                    <option value="coinGold">Gold Coin</option>

                    <option value="coinSilver">Silver Coin</option>

                    <option value="coinBronze">Bronze Coin</option>

                    <option value="coinDarkBlue">Dark Blue Coin</option>

                    <option value="coinLightBlue">Light Blue Coin</option>

                  </optgroup>

                  <optgroup label="Coin Stacks">

                    <option value="stackGold">Gold Stack</option>

                    <option value="stackSilver">Silver Stack</option>

                    <option value="stackBronze">Bronze Stack</option>

                    <option value="stackDarkBlue">Dark Blue Stack</option>

                    <option value="stackLightBlue">Light Blue Stack</option>

                  </optgroup>

                  <optgroup label="Gems">

                    <option value="gemGold">Gold Gem</option>

                    <option value="gemSilver">Silver Gem</option>

                    <option value="gemBronze">Bronze Gem</option>

                    <option value="gemDarkBlue">Dark Blue Gem</option>

                    <option value="gemLightBlue">Light Blue Gem</option>

                  </optgroup>

                  <optgroup label="Circles">

                    <option value="circleGold">Gold Circle</option>

                    <option value="circleSilver">Silver Circle</option>

                    <option value="circleBronze">Bronze Circle</option>

                    <option value="circleDarkBlue">Dark Blue Circle</option>

                    <option value="circleLightBlue">Light Blue Circle</option>

                  </optgroup>

                  <optgroup label="Squares">

                    <option value="squareGold">Gold Square</option>

                    <option value="squareSilver">Silver Square</option>

                    <option value="squareBronze">Bronze Square</option>

                    <option value="squareDarkBlue">Dark Blue Square</option>

                    <option value="squareLightBlue">Light Blue Square</option>

                  </optgroup>

                  <optgroup label="Medals">

                    <option value="medalGold">Gold Medal</option>

                    <option value="medalSilver">Silver Medal</option>

                    <option value="medalBronze">Bronze Medal</option>

                  </optgroup>

                </select>

                <input 

                  style={{ ...S.input, flex: 1, marginBottom: 0, padding: '6px' }}

                  placeholder="Name"

                  value={tier.name}

                  onChange={e => {

                    const newTiers = [...(locSettings.currencyTiers || DEFAULT_SETTINGS.currencyTiers)];

                    newTiers[idx] = { ...tier, name: e.target.value };

                    setLocSettings({ ...locSettings, currencyTiers: newTiers });

                  }}

                />

                <input 

                  style={{ ...S.input, width: '50px', marginBottom: 0, padding: '6px' }}

                  placeholder="sym"

                  value={tier.symbol}

                  onChange={e => {

                    const newTiers = [...(locSettings.currencyTiers || DEFAULT_SETTINGS.currencyTiers)];

                    newTiers[idx] = { ...tier, symbol: e.target.value };

                    setLocSettings({ ...locSettings, currencyTiers: newTiers });

                  }}

                />

                {idx > 0 && (

                  <button 

                    style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, padding: '4px 8px' }}

                    onClick={() => {

                      const newTiers = (locSettings.currencyTiers || DEFAULT_SETTINGS.currencyTiers).filter((_, i) => i !== idx);

                      setLocSettings({ ...locSettings, currencyTiers: newTiers });

                    }}

                  >🗑️</button>

                )}

              </div>

              

              {idx === 0 ? (

                <div style={{ fontSize: '0.75rem', color: '#c9a227' }}>

                  ⭐ Base Currency (value = 1)

                </div>

              ) : (

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }}>

                  <span style={{ color: '#888' }}>1 {tier.name} =</span>

                  <input 

                    style={{ ...S.input, width: '60px', marginBottom: 0, padding: '4px', textAlign: 'center' }}

                    type="number"

                    min="1"

                    value={tier.multiplier}

                    onChange={e => {

                      const newTiers = [...(locSettings.currencyTiers || DEFAULT_SETTINGS.currencyTiers)];

                      newTiers[idx] = { ...tier, multiplier: parseInt(e.target.value) || 1 };

                      setLocSettings({ ...locSettings, currencyTiers: newTiers });

                    }}

                  />

                  <select

                    style={{ ...S.select, marginBottom: 0, padding: '4px', width: 'auto' }}

                    value={tier.baseId || (locSettings.currencyTiers || DEFAULT_SETTINGS.currencyTiers)[idx - 1]?.id}

                    onChange={e => {

                      const newTiers = [...(locSettings.currencyTiers || DEFAULT_SETTINGS.currencyTiers)];

                      newTiers[idx] = { ...tier, baseId: e.target.value };

                      setLocSettings({ ...locSettings, currencyTiers: newTiers });

                    }}

                  >

                    {(locSettings.currencyTiers || DEFAULT_SETTINGS.currencyTiers).slice(0, idx).map(t => (

                      <option key={t.id} value={t.id}>{t.name}</option>

                    ))}

                  </select>

                </div>

              )}

            </div>

          )})}

          

          <button 

            style={{ ...S.btn, ...S.btnSuccess, width: '100%', marginTop: '8px' }}

            onClick={() => {

              const tiers = locSettings.currencyTiers || DEFAULT_SETTINGS.currencyTiers;

              const newId = `currency_${Date.now()}`;

              const newTier = {

                id: newId,

                name: 'New Currency',

                symbol: '?',

                multiplier: 10,

                baseId: tiers[tiers.length - 1]?.id || null,

              };

              setLocSettings({ ...locSettings, currencyTiers: [...tiers, newTier] });

            }}

          >➕ Add Currency Tier</button>

          

          {/* USD Conversion */}

          <div style={{ marginTop: '20px', padding: '12px', background: 'rgba(39,201,90,0.1)', borderRadius: '6px', border: '1px solid rgba(39,201,90,0.3)' }}>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', marginBottom: '12px' }}>

              <input 

                type="checkbox" 

                checked={locSettings.currencyUsdConversion?.enabled || false} 

                onChange={e => setLocSettings({ 

                  ...locSettings, 

                  currencyUsdConversion: { 

                    ...(locSettings.currencyUsdConversion || DEFAULT_SETTINGS.currencyUsdConversion),

                    enabled: e.target.checked 

                  }

                })}

              />

              <span style={{ fontWeight: 'bold', color: '#27c95a' }}>💵 Enable USD Conversion (for fun!)</span>

            </label>

            

            {locSettings.currencyUsdConversion?.enabled && (

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>

                <input 

                  style={{ ...S.input, width: '60px', marginBottom: 0, padding: '6px', textAlign: 'center' }}

                  type="number"

                  min="1"

                  value={locSettings.currencyUsdConversion?.amount || 1}

                  onChange={e => setLocSettings({ 

                    ...locSettings, 

                    currencyUsdConversion: { 

                      ...(locSettings.currencyUsdConversion || DEFAULT_SETTINGS.currencyUsdConversion),

                      amount: parseInt(e.target.value) || 1 

                    }

                  })}

                />

                <select

                  style={{ ...S.select, marginBottom: 0, padding: '6px', width: 'auto' }}

                  value={locSettings.currencyUsdConversion?.currencyId || 'gold'}

                  onChange={e => setLocSettings({ 

                    ...locSettings, 

                    currencyUsdConversion: { 

                      ...(locSettings.currencyUsdConversion || DEFAULT_SETTINGS.currencyUsdConversion),

                      currencyId: e.target.value 

                    }

                  })}

                >

                  {(locSettings.currencyTiers || DEFAULT_SETTINGS.currencyTiers).map(t => (

                    <option key={t.id} value={t.id}>{t.name}</option>

                  ))}

                </select>

                <span style={{ color: '#888' }}>=</span>

                <span style={{ color: '#27c95a' }}>$</span>

                <input 

                  style={{ ...S.input, width: '70px', marginBottom: 0, padding: '6px', textAlign: 'center' }}

                  type="number"

                  step="0.01"

                  min="0.01"

                  value={(locSettings.currencyUsdConversion?.usdCents || 100) / 100}

                  onChange={e => setLocSettings({ 

                    ...locSettings, 

                    currencyUsdConversion: { 

                      ...(locSettings.currencyUsdConversion || DEFAULT_SETTINGS.currencyUsdConversion),

                      usdCents: Math.round(parseFloat(e.target.value) * 100) || 100 

                    }

                  })}

                />

                <span style={{ color: '#888' }}>USD</span>

              </div>

            )}

          </div>

        </>}

      </div>}

      

      {tab === 'leveling' && <div>

        <div style={{ marginBottom: '16px' }}>

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', marginBottom: '8px' }}>

            <input type="checkbox" checked={locSettings.xpTrackingEnabled !== false} onChange={e => setLocSettings({ ...locSettings, xpTrackingEnabled: e.target.checked })} />

            <span style={{ fontWeight: 'bold', color: '#c9a227' }}>Enable XP Tracking</span>

          </label>

          <div style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '8px' }}>

            When enabled, characters display an XP bar. Award XP from the DM view. Characters auto-level when they reach the threshold.

          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.85rem', marginBottom: '4px' }}>

            <input type="checkbox" checked={locSettings.xpVisibleToPlayers !== false} onChange={e => setLocSettings({ ...locSettings, xpVisibleToPlayers: e.target.checked })} />

            <span style={{ color: '#4a90d9' }}>Show XP progress to players</span>

          </label>

          <div style={{ fontSize: '0.7rem', opacity: 0.5, marginLeft: '24px', marginBottom: '4px' }}>

            When disabled, only the DM can see XP bars. Players won't see their XP progress.

          </div>

        </div>

        

        {locSettings.xpTrackingEnabled !== false && (<>

          <h3 style={{ color: '#c9a227', marginBottom: '8px', fontSize: '0.9rem' }}>XP Thresholds per Level</h3>

          <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: '8px' }}>Set the total XP required to reach each level. Leave at 0 to use 5e defaults.</div>

          

          <button style={{ ...S.btn, ...S.btnSm, marginBottom: '10px', fontSize: '0.72rem' }} onClick={() => {

            setLocSettings({ ...locSettings, xpThresholds: { ...XP_DEFAULTS } });

          }}>📋 Load 5e Defaults</button>

          

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '6px', marginBottom: '16px' }}>

            {Array.from({ length: 19 }, (_, i) => i + 2).map(lvl => {

              const thresholds = locSettings.xpThresholds || XP_DEFAULTS;

              const val = thresholds[lvl] !== undefined ? thresholds[lvl] : (XP_DEFAULTS[lvl] || 0);

              return (

                <div key={lvl} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>

                  <span style={{ fontSize: '0.7rem', color: '#c9a227', minWidth: '32px' }}>Lv {lvl}:</span>

                  <input type="number" value={val} onChange={e => {

                    const newThresh = { ...(locSettings.xpThresholds || XP_DEFAULTS), [lvl]: parseInt(e.target.value) || 0 };

                    setLocSettings({ ...locSettings, xpThresholds: newThresh });

                  }} style={{ ...S.input, marginBottom: 0, padding: '2px 4px', fontSize: '0.68rem', width: '70px', textAlign: 'right' }} min="0" />

                </div>

              );

            })}

          </div>

          

          <h3 style={{ color: '#4a90d9', marginBottom: '8px', fontSize: '0.9rem' }}>Award XP to Characters</h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>

            {Object.values(chars).map(c => {

              const xp = getXpProgress(c, locSettings);

              return (

                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px' }}>

                  <span style={{ fontSize: '0.78rem', fontWeight: 'bold', color: '#e8d5b7', minWidth: '80px' }}>{c.name}</span>

                  <span style={{ fontSize: '0.65rem', color: '#888', minWidth: '35px' }}>Lv {xp.currentLevel}</span>

                  <div style={{ flex: 1, height: '10px', background: 'rgba(0,0,0,0.3)', borderRadius: '5px', overflow: 'hidden', position: 'relative', minWidth: '60px' }}>

                    <div style={{ height: '100%', width: `${xp.pct}%`, background: xp.isMax ? '#ffd700' : 'linear-gradient(90deg, #5b3a8c, #8b5fcf)', borderRadius: '5px', transition: 'width 0.3s' }} />

                  </div>

                  <span style={{ fontSize: '0.6rem', color: '#888', minWidth: '65px', textAlign: 'right' }}>{xp.currentXp.toLocaleString()}/{xp.isMax ? 'MAX' : xp.xpForNext.toLocaleString()}</span>

                  <ControlledMiniForm
                    style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                    onSubmit={() => awardDmSettingsXp(c.id, { currentXp: xp.currentXp, currentLevel: xp.currentLevel, name: c.name })}
                    submitLabel="+XP"
                    actionsStyle={{ display: 'flex', alignItems: 'center' }}
                    submitButtonStyle={{ ...S.btn, ...S.btnSm, fontSize: '0.65rem', padding: '2px 8px', background: 'rgba(39,201,90,0.15)', border: '1px solid rgba(39,201,90,0.4)', color: '#27c95a', marginBottom: 0 }}
                  >
                    <input
                      type="number"
                      placeholder="+XP"
                      aria-label={`${c.name} XP award`}
                      style={{ ...S.input, marginBottom: 0, padding: '2px 4px', fontSize: '0.68rem', width: '55px', textAlign: 'center' }}
                      min="0"
                      value={xpAwardDrafts[c.id] || ''}
                      onChange={e => setXpAwardDrafts((prev) => ({ ...prev, [c.id]: e.target.value }))}
                    />

                  </ControlledMiniForm>






                </div>

              );

            })}

            {Object.values(chars).length > 1 && (

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', background: 'rgba(201,162,39,0.08)', borderRadius: '4px', border: '1px solid rgba(201,162,39,0.15)' }}>

                <span style={{ fontSize: '0.78rem', fontWeight: 'bold', color: '#c9a227', flex: 1 }}>Award All</span>

                <ControlledMiniForm
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  onSubmit={awardAllDmSettingsXp}
                  submitLabel="+XP All"
                  actionsStyle={{ display: 'flex', alignItems: 'center' }}
                  submitButtonStyle={{ ...S.btn, ...S.btnSm, fontSize: '0.65rem', padding: '2px 8px', background: 'rgba(201,162,39,0.15)', border: '1px solid rgba(201,162,39,0.4)', color: '#c9a227', marginBottom: 0 }}
                >
                  <input
                    type="number"
                    placeholder="+XP"
                    aria-label="Award all XP"
                    style={{ ...S.input, marginBottom: 0, padding: '2px 4px', fontSize: '0.68rem', width: '55px', textAlign: 'center' }}
                    min="0"
                    value={xpAwardDrafts.__all__ || ''}
                    onChange={e => setXpAwardDrafts((prev) => ({ ...prev, __all__: e.target.value }))}
                  />
                </ControlledMiniForm>

              </div>

            )}

          </div>

        </>)}

      </div>}

      

      {tab === 'diets' && <div>

        {Object.entries(locDiets).map(([key, diet]) => <div key={key} style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px', marginBottom: '8px' }}>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><strong style={{ color: '#ffd700' }}>{diet.name}</strong><div>{diet.deletable !== false && <button style={{ ...S.btn, ...S.btnDanger, ...S.btnXs }} onClick={() => { const d = { ...locDiets }; delete d[key]; setLocDiets(d); }}>🗑️</button>}<button style={{ ...S.btn, ...S.btnEdit, ...S.btnXs }} onClick={() => setEditDiet(editDiet === key ? null : key)}>✏️</button></div></div>

          {editDiet !== key && <div style={{ fontSize: '0.8rem', marginTop: '4px' }}>{diet.duration}d | {diet.requiredMealTypes?.join(', ')} | {diet.completionBuffs?.join(', ')}</div>}

          {editDiet === key && (

            <div style={{ marginTop: '8px' }}>

              <label style={S.label}>Duration (days)</label>

              <input

                style={S.input}

                type="number"

                value={diet.duration}

                onChange={e => updateDiet(key, 'duration', parseInt(e.target.value) || 1)}

              />

              <label style={S.label}>Required Foods (one per line)</label>

              <textarea

                style={{ ...S.input, minHeight: '70px', resize: 'vertical' }}

                value={(diet.requiredMealTypes || []).join('\n')}

                onChange={e => updateDiet(key, 'requiredMealTypes', parseLines(e.target.value))}

                placeholder="Any text is allowed (spaces, commas). One food per line."

              />

              <label style={S.label}>Completion Buffs (one per line)</label>

              <textarea

                style={{ ...S.input, minHeight: '70px', resize: 'vertical' }}

                value={(diet.completionBuffs || []).join('\n')}

                onChange={e => updateDiet(key, 'completionBuffs', parseLines(e.target.value))}

                placeholder="+1 Survival\n+1 Perception"

              />

            </div>

          )}

        </div>)}

        <div style={{ marginTop: '12px', padding: '10px', background: 'rgba(39,201,90,0.1)', borderRadius: '4px', border: '1px solid #27c95a' }}><input style={{ ...S.input, marginBottom: '8px' }} id="ndName" placeholder="Diet Name" /><input style={{ ...S.input, marginBottom: '8px' }} type="number" id="ndDur" placeholder="Duration (days)" /><textarea style={{ ...S.input, minHeight: '70px', resize: 'vertical', marginBottom: '8px' }} id="ndFoods" placeholder="Foods (one per line)" /><textarea style={{ ...S.input, minHeight: '70px', resize: 'vertical', marginBottom: '8px' }} id="ndBuffs" placeholder="Buffs (one per line)" /><button style={{ ...S.btn, ...S.btnSuccess }} onClick={() => { const name = document.getElementById('ndName').value; if (name) { setLocDiets({ ...locDiets, [name]: { name, duration: parseInt(document.getElementById('ndDur').value) || 14, requiredMealTypes: parseLines(document.getElementById('ndFoods').value), completionBuffs: parseLines(document.getElementById('ndBuffs').value), deletable: true } }); ['ndName','ndDur','ndFoods','ndBuffs'].forEach(id => document.getElementById(id).value = ''); } }}>➕ Add</button></div>

      </div>}

      {tab === 'diseases' && <div>

        {Object.entries(locDiseases).map(([key, dis]) => <div key={key} style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px', marginBottom: '8px' }}>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><strong style={{ color: '#d47dd4' }}>{dis.name}</strong><div><button style={{ ...S.btn, ...S.btnDanger, ...S.btnXs }} onClick={() => { const d = { ...locDiseases }; delete d[key]; setLocDiseases(d); }}>🗑️</button><button style={{ ...S.btn, ...S.btnEdit, ...S.btnXs }} onClick={() => setEditDisease(editDisease === key ? null : key)}>✏️</button></div></div>

          {editDisease !== key && <div style={{ fontSize: '0.8rem', marginTop: '4px' }}>Symptoms: {dis.symptoms?.join(', ')}<br/>Effects: {dis.effects?.join(', ')}<br/>Locations: {dis.locations?.join(', ')}<br/>Cure: {dis.cures?.map(c => `${c.quantity}x ${c.item}`).join(', ')}</div>}

          {editDisease === key && (

            <div style={{ marginTop: '8px' }}>

              <label style={S.label}>Name</label>

              <input

                style={S.input}

                value={dis.name}

                onChange={e => updateDisease(key, 'name', e.target.value)}

              />



              <label style={S.label}>Symptoms (one per line)</label>

              <textarea

                style={{ ...S.input, minHeight: '80px', resize: 'vertical' }}

                value={(dis.symptoms || []).join('\n')}

                onChange={e => updateDisease(key, 'symptoms', parseLines(e.target.value))}

                placeholder="One symptom per line. Commas/spaces are allowed."

              />



              <label style={S.label}>Effects (one per line)</label>

              <textarea

                style={{ ...S.input, minHeight: '80px', resize: 'vertical' }}

                value={(dis.effects || []).join('\n')}

                onChange={e => updateDisease(key, 'effects', parseLines(e.target.value))}

                placeholder="-1 Dex\n-2 Str"

              />



              <label style={S.label}>Locations (one per line)</label>

              <textarea

                style={{ ...S.input, minHeight: '60px', resize: 'vertical' }}

                value={(dis.locations || []).join('\n')}

                onChange={e => updateDisease(key, 'locations', parseLines(e.target.value))}

                placeholder="Swamp\nForest"

              />



              <label style={S.label}>Cure Item</label>

              <input

                style={S.input}

                value={dis.cures?.[0]?.item || ''}

                onChange={e => updateDisease(key, 'cures', [{ item: e.target.value, quantity: dis.cures?.[0]?.quantity || 1 }])}

              />

              <label style={S.label}>Cure Quantity</label>

              <input

                style={S.input}

                type="number"

                value={dis.cures?.[0]?.quantity || 1}

                onChange={e => updateDisease(key, 'cures', [{ item: dis.cures?.[0]?.item || '', quantity: parseInt(e.target.value) || 1 }])}

              />

            </div>

          )}

        </div>)}

        <div style={{ marginTop: '12px', padding: '10px', background: 'rgba(139,31,139,0.1)', borderRadius: '4px', border: '1px solid #8b1f8b' }}><input style={{ ...S.input, marginBottom: '8px' }} id="disName" placeholder="Name" /><textarea style={{ ...S.input, minHeight: '80px', resize: 'vertical', marginBottom: '8px' }} id="disSymp" placeholder="Symptoms (one per line)" /><textarea style={{ ...S.input, minHeight: '80px', resize: 'vertical', marginBottom: '8px' }} id="disEff" placeholder="Effects (one per line)" /><textarea style={{ ...S.input, minHeight: '60px', resize: 'vertical', marginBottom: '8px' }} id="disLoc" placeholder="Locations (one per line)" /><div style={S.flex}><input style={{ ...S.input, flex: 1, marginBottom: 0 }} id="disCure" placeholder="Cure item" /><input style={{ ...S.input, width: '60px', marginBottom: 0 }} type="number" id="disCureQty" placeholder="Qty" /></div><button style={{ ...S.btn, ...S.btnSuccess, marginTop: '8px' }} onClick={() => { const name = document.getElementById('disName').value; if (name) { const id = name.toLowerCase().replace(/\s+/g, '-'); setLocDiseases({ ...locDiseases, [id]: { id, name, symptoms: parseLines(document.getElementById('disSymp').value), effects: parseLines(document.getElementById('disEff').value), locations: parseLines(document.getElementById('disLoc').value), cures: [{ item: document.getElementById('disCure').value || 'Herb', quantity: parseInt(document.getElementById('disCureQty').value) || 1 }] } }); ['disName','disSymp','disEff','disLoc','disCure','disCureQty'].forEach(id => document.getElementById(id).value = ''); } }}>➕ Add</button></div>

      </div>}



      {tab === 'systems' && <div>

        <div style={{ marginBottom: '12px', padding: '10px', background: 'rgba(74,144,217,0.08)', border: '1px solid rgba(74,144,217,0.25)', borderRadius: '6px' }}>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>

            <div>

              <div style={{ fontSize: '0.85rem', color: '#b0b0ff', fontWeight: 'bold', marginBottom: '4px' }}>🔒 Systems Templates</div>

              <div style={{ fontSize: '0.75rem', opacity: 0.75 }}>

                Systems are generalized hidden/revealed mechanics (curses, marks, omens, custom rules). You can apply them from the DM dashboard with per-field reveals.

              </div>

            </div>

            <div style={{ display: 'flex', gap: '6px' }}>

              <button

                style={{ ...S.btn, ...S.btnXs, marginBottom: 0 }}

                onClick={() => setShowImportModal(true)}

              >

                📥 Import

              </button>

              <button

                style={{ ...S.btn, ...S.btnXs, marginBottom: 0, opacity: selectedForExport.size === 0 ? 0.5 : 1 }}

                disabled={selectedForExport.size === 0}

                onClick={() => exportSystems(Array.from(selectedForExport))}

              >

                📤 Export ({selectedForExport.size})

              </button>

            </div>

          </div>

        </div>

        

        {/* Import Modal */}

        {showImportModal && (

          <div style={S.modal} onClick={() => setShowImportModal(false)}>

            <div style={{ ...S.modalContent, maxWidth: '600px' }} onClick={e => e.stopPropagation()}>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>

                <h3 style={{ color: '#c9a227', margin: 0 }}>📥 Import Systems</h3>

                <button style={{ ...S.btn, ...S.btnXs }} onClick={() => setShowImportModal(false)}>✕</button>

              </div>

              

              <div style={{ marginBottom: '12px' }}>

                <label style={S.label}>Paste JSON export data:</label>

                <textarea

                  style={{ ...S.input, minHeight: '200px', fontFamily: 'monospace', fontSize: '0.8rem' }}

                  value={importText}

                  onChange={e => { setImportText(e.target.value); setImportError(''); }}

                  placeholder='{"version": 1, "systems": [...]}'

                />

              </div>

              

              {importError && (

                <div style={{ padding: '8px', background: 'rgba(201,39,39,0.2)', borderRadius: '4px', marginBottom: '12px', color: '#ff6961' }}>

                  ⚠️ {importError}

                </div>

              )}

              

              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>

                <button style={{ ...S.btn, ...S.btnSubtle }} onClick={() => setShowImportModal(false)}>Cancel</button>

                <button style={{ ...S.btn, ...S.btnSuccess }} onClick={importSystems} disabled={!importText.trim()}>

                  Import Systems

                </button>

              </div>

              

              <div style={{ marginTop: '16px', padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>

                <div style={{ fontSize: '0.75rem', color: '#888' }}>

                  <strong>How to import:</strong>

                  <ol style={{ margin: '8px 0 0 16px', padding: 0 }}>

                    <li>Get a JSON export file from another campaign</li>

                    <li>Open it in a text editor and copy the contents</li>

                    <li>Paste it in the box above and click Import</li>

                  </ol>

                </div>

              </div>

            </div>

          </div>

        )}



        {/* Quick Create from Preset */}

        <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(201,162,39,0.08)', borderRadius: '8px', border: '1px solid rgba(201,162,39,0.2)' }}>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>

            <div style={{ fontWeight: 'bold', color: '#c9a227' }}>✨ Quick Create from Preset</div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', opacity: 0.8 }}>

              <input 

                type="checkbox" 

                checked={autoScrollToNew} 

                onChange={e => setAutoScrollToNew(e.target.checked)} 

              />

              Auto-scroll to new

            </label>

          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: '6px' }}>

            {[

              { type: 'disease', label: 'Disease', icon: '☣' },

              { type: 'curse', label: 'Curse', icon: '💀' },

              { type: 'affliction', label: 'Affliction', icon: '🩸' },

              { type: 'blessing', label: 'Blessing', icon: '✨' },

              { type: 'secret', label: 'Secret', icon: '🔒' },

              { type: 'note', label: 'Note', icon: '📝' },

              { type: 'tracker', label: 'Tracker', icon: '📊' },

              { type: 'mark', label: 'Mark', icon: '👁' },

            ].map(preset => (

              <button

                key={preset.type}

                onClick={() => {

                  const template = createSystemFromPreset(preset.type, `New ${preset.label}`);

                  createSystemWithAnimation(template.id, template);

                }}

                style={{

                  padding: '10px 6px',

                  background: 'rgba(255,255,255,0.05)',

                  border: '1px solid rgba(255,255,255,0.15)',

                  borderRadius: '6px',

                  color: '#fff',

                  cursor: 'pointer',

                  textAlign: 'center',

                  transition: 'all 0.2s',

                }}

                onMouseEnter={e => { e.target.style.background = 'rgba(201,162,39,0.2)'; e.target.style.borderColor = 'rgba(201,162,39,0.4)'; }}

                onMouseLeave={e => { e.target.style.background = 'rgba(255,255,255,0.05)'; e.target.style.borderColor = 'rgba(255,255,255,0.15)'; }}

              >

                <div style={{ fontSize: '1.3rem' }}>{preset.icon}</div>

                <div style={{ fontSize: '0.7rem', marginTop: '2px' }}>{preset.label}</div>

              </button>

            ))}

          </div>

          <div style={{ fontSize: '0.7rem', opacity: 0.6, marginTop: '6px' }}>

            Click a preset to create a system with pre-configured sections. Or use Custom below for full control.

          </div>

        </div>



        {/* Add new system (custom) */}

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>

          <input 

            style={{ ...S.input, flex: 1, minWidth: '220px', marginBottom: 0 }} 

            value={newSystemName} 

            onChange={e => setNewSystemName(e.target.value)} 

            placeholder="New system name (internal)"

            onKeyDown={e => {

              if (e.key === 'Enter') {

                const nm = (newSystemName || '').trim();

                if (!nm) return;

                const id = makeSystemId(nm);

                createSystemWithAnimation(id, {

                  ...createSystemFromPreset(newSystemType || 'custom', nm),

                  id,

                  deletable: true,

                });

                setNewSystemName('');

                setNewSystemType('custom');

              }

            }}

          />

          <select style={{ ...S.select, width: '190px', marginBottom: 0 }} value={newSystemType} onChange={e => setNewSystemType(e.target.value)}>

            {SYSTEM_TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}

          </select>

          <button style={{ ...S.btn, ...S.btnSuccess, ...S.btnSm, marginBottom: 0 }} onClick={() => {

            const nm = (newSystemName || '').trim();

            if (!nm) return;

            const id = makeSystemId(nm);

            createSystemWithAnimation(id, {

              ...createSystemFromPreset(newSystemType || 'custom', nm),

              id,

              deletable: true,

            });

            setNewSystemName('');

            setNewSystemType('custom');

          }}>➕ Add</button>

        </div>



        {/* Templates list */}

        

        {/* Search/Filter Bar */}

        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>

          <input

            style={{ ...S.input, flex: 1, minWidth: '200px', marginBottom: 0 }}

            placeholder="🔍 Search systems by name, tag, or description..."

            value={systemSearch}

            onChange={e => setSystemSearch(e.target.value)}

          />

          <select

            style={{ ...S.select, width: '150px', marginBottom: 0 }}

            value={systemTypeFilter}

            onChange={e => setSystemTypeFilter(e.target.value)}

          >

            <option value="all">All Types</option>

            {SYSTEM_TYPE_OPTIONS.map(t => (

              <option key={t.value} value={t.value}>{t.icon} {t.label}</option>

            ))}

          </select>

          {(systemSearch || systemTypeFilter !== 'all') && (

            <button

              style={{ ...S.btn, ...S.btnXs, marginBottom: 0 }}

              onClick={() => { setSystemSearch(''); setSystemTypeFilter('all'); }}

            >

              Clear

            </button>

          )}

        </div>

        

        {/* Filtered Systems Count */}

        {(() => {

          const allSystems = Object.entries(locSystems);

          const filteredSystems = allSystems.filter(([id, sys]) => {

            // Type filter

            if (systemTypeFilter !== 'all' && sys.type !== systemTypeFilter) return false;

            // Search filter

            if (systemSearch.trim()) {

              const searchLower = systemSearch.toLowerCase();

              const nameMatch = (sys.name || '').toLowerCase().includes(searchLower);

              const publicNameMatch = (sys.publicName || '').toLowerCase().includes(searchLower);

              const descMatch = (sys.description || '').toLowerCase().includes(searchLower);

              const tagMatch = (sys.tags || []).some(t => t.toLowerCase().includes(searchLower));

              if (!nameMatch && !publicNameMatch && !descMatch && !tagMatch) return false;

            }

            return true;

          });

          

          if (allSystems.length > 0 && (systemSearch || systemTypeFilter !== 'all')) {

            return (

              <div style={{ fontSize: '0.8rem', opacity: 0.7, marginBottom: '8px' }}>

                Showing {filteredSystems.length} of {allSystems.length} systems

              </div>

            );

          }

          return null;

        })()}

        

        {Object.entries(locSystems).length === 0 ? (

          <div style={{ opacity: 0.7, fontSize: '0.85rem' }}>No systems yet. Add one above.</div>

        ) : (

          <div>

            {Object.entries(locSystems)

              .filter(([id, sys]) => {

                // Type filter

                if (systemTypeFilter !== 'all' && sys.type !== systemTypeFilter) return false;

                // Search filter

                if (systemSearch.trim()) {

                  const searchLower = systemSearch.toLowerCase();

                  const nameMatch = (sys.name || '').toLowerCase().includes(searchLower);

                  const publicNameMatch = (sys.publicName || '').toLowerCase().includes(searchLower);

                  const descMatch = (sys.description || '').toLowerCase().includes(searchLower);

                  const tagMatch = (sys.tags || []).some(t => t.toLowerCase().includes(searchLower));

                  if (!nameMatch && !publicNameMatch && !descMatch && !tagMatch) return false;

                }

                return true;

              })

              .sort((a, b) => (a[1].type || '').localeCompare(b[1].type || '') || (a[1].name || '').localeCompare(b[1].name || ''))

              .map(([id, sys]) => {

              const isEditing = editSystem === id;

              const typeMeta = SYSTEM_TYPE_OPTIONS.find(t => t.value === (sys.type || 'custom'));

              const icon = typeMeta?.icon || '🔮';

              const locs = sys.locations || [];

              const tags = sys.tags || [];

              const scalarResourceProfiles = getBindableResourceProfiles(sys, { scalarOnly: true });

              const defaultBindingCount = (sys.directSpellBindings || []).filter(binding => normalizeDirectSpellBinding(binding).assignmentMode === 'all-spells').length;

              const isSelectedForExport = selectedForExport.has(id);

              const isNewlyCreated = newlyCreatedSystem === id;

              return (

                <div 

                  key={id} 

                  ref={el => systemRefs.current[id] = el}

                  style={{ 

                    marginBottom: '10px', 

                    padding: '10px', 

                    background: isNewlyCreated ? 'rgba(201,162,39,0.15)' : 'rgba(0,0,0,0.18)', 

                    borderRadius: '6px', 

                    border: isNewlyCreated ? '2px solid rgba(201,162,39,0.8)' : 

                            isSelectedForExport ? '1px solid rgba(201,162,39,0.5)' : '1px solid rgba(139,105,20,0.25)',

                    transition: 'all 0.5s ease-out',

                    boxShadow: isNewlyCreated ? '0 0 15px rgba(201,162,39,0.4)' : 'none',

                    animation: isNewlyCreated ? 'pulse-gold 1.5s ease-out' : 'none',

                  }}

                >

                  {isNewlyCreated && (

                    <style>{`

                      @keyframes pulse-gold {

                        0% { box-shadow: 0 0 0 0 rgba(201,162,39,0.7); }

                        50% { box-shadow: 0 0 20px 10px rgba(201,162,39,0.3); }

                        100% { box-shadow: 0 0 15px rgba(201,162,39,0.4); }

                      }

                    `}</style>

                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: isEditing ? '10px' : '4px' }}>

                    {/* Export checkbox */}

                    <input

                      type="checkbox"

                      checked={isSelectedForExport}

                      onChange={e => {

                        const newSet = new Set(selectedForExport);

                        if (e.target.checked) newSet.add(id);

                        else newSet.delete(id);

                        setSelectedForExport(newSet);

                      }}

                      title="Select for export"

                      style={{ cursor: 'pointer' }}

                    />

                    <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setEditSystem(isEditing ? null : id)}>

                      <div style={{ color: '#b0b0ff', fontWeight: 'bold' }}>

                        {isNewlyCreated && <span style={{ color: '#c9a227' }}>✨ NEW: </span>}

                        {icon} {sys.name || 'Untitled'} <span style={{ opacity: 0.6, fontWeight: 'normal' }}>({sys.type || 'custom'})</span> {isEditing ? '▲' : '▼'}

                      </div>

                      {!isEditing && <div style={{ fontSize: '0.75rem', opacity: 0.75, marginTop: '2px' }}>

                        {(sys.publicName || sys.name) && <span>Public: <span style={{ color: '#c9a227' }}>{sys.publicName || sys.name}</span></span>}

                        {locs.length > 0 && <span style={{ marginLeft: '10px' }}>Locations: {locs.join(', ')}</span>}

                        {tags.length > 0 && <span style={{ marginLeft: '10px' }}>Tags: {tags.join(', ')}</span>}

                      </div>}

                    </div>

                    {(sys.deletable !== false) && (

                      <>

                        <button 

                          style={{ ...S.btn, ...S.btnEdit, ...S.btnXs, marginBottom: 0 }} 

                          onClick={() => {

                            const newId = `${id}-copy-${Date.now()}`;

                            const duplicated = duplicateSystemResourceModel(sys, {

                              id: newId,

                              name: `${sys.name} (Copy)`,

                              deletable: true,

                            });

                            createSystemWithAnimation(newId, duplicated);

                          }}

                          title="Duplicate this system"

                        >📋</button>

                        <button style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, marginBottom: 0 }} onClick={() => {

                          const next = { ...locSystems };

                          delete next[id];

                          setLocSystems(next);

                          if (editSystem === id) setEditSystem(null);

                          selectedForExport.delete(id);

                          setSelectedForExport(new Set(selectedForExport));

                        }}>🗑️</button>

                      </>

                    )}

                  </div>



                  {isEditing && (

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>

                      <div style={{ gridColumn: '1 / span 2' }}>

                        <label style={S.label}>Type</label>

                        <select style={S.select} value={sys.type || 'custom'} onChange={e => updateSystem(id, { type: e.target.value })}>

                          {SYSTEM_TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}

                        </select>

                        {(() => {

                          const typeInfo = SYSTEM_TYPE_OPTIONS.find(t => t.value === (sys.type || 'custom'));

                          return typeInfo?.description ? (

                            <div style={{ fontSize: '0.7rem', opacity: 0.65, marginTop: '4px' }}>

                              {typeInfo.description}

                            </div>

                          ) : null;

                        })()}

                      </div>



                      <div>

                        <label style={S.label}>Internal Name</label>

                        <DebouncedInput as="input" style={S.input} value={sys.name || ''} onCommit={v => updateSystem(id, { name: v })} />

                      </div>

                      <div>

                        <label style={S.label}>Public Name (optional)</label>

                        <DebouncedInput as="input" style={S.input} value={sys.publicName || ''} onCommit={v => updateSystem(id, { publicName: v })} placeholder="What players see if revealed" />

                      </div>



                      <SystemDescriptionFields
                        system={sys}
                        onPatch={(patch) => updateSystem(id, patch)}
                        labelStyle={S.label}
                        inputStyle={S.input}
                      />



                      <div>

                        <label style={S.label}>Clues (hints to reveal incrementally)</label>

                        {(sys.clues || []).length > 0 && (

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '8px' }}>

                            {(sys.clues || []).map((clue, ci) => (

                              <div key={ci} style={{ 

                                display: 'flex', 

                                alignItems: 'flex-start', 

                                gap: '8px',

                                padding: '6px 8px',

                                background: 'rgba(201,162,39,0.1)',

                                borderRadius: '4px',

                                border: '1px solid rgba(201,162,39,0.2)',

                              }}>

                                <span style={{ color: '#c9a227', fontWeight: 'bold', minWidth: '20px' }}>{ci + 1}.</span>

                                <span style={{ flex: 1, whiteSpace: 'pre-wrap' }}>{clue}</span>

                                <span

                                  style={{ cursor: 'pointer', opacity: 0.7, color: '#ff6961' }}

                                  onClick={() => updateSystem(id, { clues: (sys.clues || []).filter((_, i) => i !== ci) })}

                                >✕</span>

                              </div>

                            ))}

                          </div>

                        )}

                        <textarea

                          style={{ ...S.input, minHeight: '60px', resize: 'vertical', fontSize: '0.85rem' }}

                          value={cluesInputText[id] || ''}

                          onChange={e => setCluesInputText({ ...cluesInputText, [id]: e.target.value })}

                          onKeyDown={e => {

                            if (e.key === 'Enter' && !e.shiftKey) {

                              e.preventDefault();

                              const text = (cluesInputText[id] || '').trim();

                              if (text) {

                                updateSystem(id, { clues: [...(sys.clues || []), text] });

                                setCluesInputText({ ...cluesInputText, [id]: '' });

                              }

                            }

                          }}

                          placeholder="Type a clue and press Enter to add (Shift+Enter for newline within clue)"

                        />

                        <div style={{ fontSize: '0.7rem', opacity: 0.6, marginTop: '4px' }}>

                          Press Enter to add clue • Shift+Enter for multi-line clue

                        </div>

                      </div>

                      

                      {/* Enhanced Effects Section - only show for types that have effects */}

                      {sys.type === 'note' ? (

                        <div style={{ gridColumn: '1 / span 2', padding: '12px', borderRadius: '8px', border: '1px solid rgba(136,136,136,0.2)', background: 'rgba(136,136,136,0.05)' }}>

                          <div style={{ fontWeight: 'bold', color: '#888', marginBottom: '8px' }}>📝 Note Only</div>

                          <div style={{ fontSize: '0.85rem', opacity: 0.75 }}>

                            This system type is for text notes with selective reveal only. It has no mechanical effects.

                            Use it for plot points, character secrets, session reminders, or any information you want to track and optionally share with players.

                          </div>

                        </div>

                      ) : (

                      <div style={{ gridColumn: '1 / span 2', padding: '12px', borderRadius: '8px', border: '1px solid rgba(255,215,0,0.2)', background: 'rgba(255,215,0,0.05)' }}>

                        <div style={{ fontWeight: 'bold', color: '#c9a227', marginBottom: '8px' }}>⚡ Effects</div>

                        

                        {/* Visible Effects */}

                        <div style={{ marginBottom: '12px' }}>

                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>

                            <label style={{ fontSize: '0.85rem', color: '#c9a227' }}>👁 Visible Effects (player knows source)</label>

                            <button

                              style={{ ...S.btn, ...S.btnXs, ...S.btnSuccess, marginBottom: 0 }}

                              onClick={() => { setShowEffectBuilder(id); setEffectBuilderType('visible'); }}

                            >

                              + Add with Builder

                            </button>

                          </div>

                          {(sys.effects || []).length > 0 ? (

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '6px' }}>

                              {(sys.effects || []).map((eff, ei) => {

                                const effText = typeof eff === 'string' ? eff : (eff.displayText || formatEffectDisplay(eff));

                                const analysis = analyzeEffect(eff);

                                return (

                                  <Tooltip key={ei} text={`${effText}\n\n${analysis.description}${analysis.hasStatChange ? '\n✅ Mechanically applied' : '\n📝 Display only (no stat change)'}`}>

                                    <span style={{ 

                                      ...S.badge, 

                                      ...S.badgeDebuff, 

                                      display: 'flex', 

                                      alignItems: 'center', 

                                      gap: '6px',

                                      borderLeft: analysis.hasStatChange ? '3px solid #27c95a' : '3px solid #888',

                                    }}>

                                      {analysis.hasStatChange ? '⚙️' : '📝'} {effText}

                                      <span

                                        style={{ cursor: 'pointer', opacity: 0.7 }}

                                        onClick={() => updateSystem(id, { effects: (sys.effects || []).filter((_, i) => i !== ei) })}

                                      >✕</span>

                                    </span>

                                  </Tooltip>

                                );

                              })}

                            </div>

                          ) : null}

                          <input

                            style={{ ...S.input, fontSize: '0.85rem', marginBottom: 0 }}

                            value={effectsInputText[id] || ''}

                            onChange={e => setEffectsInputText({ ...effectsInputText, [id]: e.target.value })}

                            onKeyDown={e => {

                              if (e.key === 'Enter') {

                                e.preventDefault();

                                const text = (effectsInputText[id] || '').trim();

                                if (text) {

                                  updateSystem(id, { effects: [...(sys.effects || []), text] });

                                  setEffectsInputText({ ...effectsInputText, [id]: '' });

                                }

                              }

                            }}

                            placeholder="Type effect and press Enter (e.g., -1 Dex, Weakness, Disadvantage on Stealth)"

                          />

                          <div style={{ fontSize: '0.7rem', opacity: 0.6, marginTop: '4px' }}>

                            ⚙️ = applies stat change • 📝 = display only badge

                          </div>

                        </div>

                        

                        {/* Hidden Effects */}

                        <div>

                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>

                            <label style={{ fontSize: '0.85rem', color: '#888' }}>🔒 Hidden Effects (applied, but source shows as "?")</label>

                            <button

                              style={{ ...S.btn, ...S.btnXs, marginBottom: 0 }}

                              onClick={() => { setShowEffectBuilder(id); setEffectBuilderType('hidden'); }}

                            >

                              + Add

                            </button>

                          </div>

                          {(sys.hiddenEffects || []).length > 0 ? (

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '6px' }}>

                              {(sys.hiddenEffects || []).map((eff, ei) => {

                                const effText = typeof eff === 'string' ? eff : (eff.displayText || formatEffectDisplay(eff));

                                const analysis = analyzeEffect(eff);

                                return (

                                  <Tooltip key={ei} text={`${effText}\n\n${analysis.description}${analysis.hasStatChange ? '\n✅ Mechanically applied (source hidden)' : '\n📝 Display only (no stat change)'}`}>

                                    <span style={{ 

                                      ...S.badge, 

                                      ...S.badgeUnknown, 

                                      display: 'flex', 

                                      alignItems: 'center', 

                                      gap: '6px',

                                      borderLeft: analysis.hasStatChange ? '3px solid #27c95a' : '3px solid #888',

                                    }}>

                                      🔒 {analysis.hasStatChange ? '⚙️' : '📝'} {effText}

                                      <span

                                        style={{ cursor: 'pointer', opacity: 0.7 }}

                                        onClick={() => updateSystem(id, { hiddenEffects: (sys.hiddenEffects || []).filter((_, i) => i !== ei) })}

                                      >✕</span>

                                    </span>

                                  </Tooltip>

                                );

                              })}

                            </div>

                          ) : null}

                          <input

                            style={{ ...S.input, fontSize: '0.85rem', marginBottom: 0 }}

                            value={hiddenEffectsInputText[id] || ''}

                            onChange={e => setHiddenEffectsInputText({ ...hiddenEffectsInputText, [id]: e.target.value })}

                            onKeyDown={e => {

                              if (e.key === 'Enter') {

                                e.preventDefault();

                                const text = (hiddenEffectsInputText[id] || '').trim();

                                if (text) {

                                  updateSystem(id, { hiddenEffects: [...(sys.hiddenEffects || []), text] });

                                  setHiddenEffectsInputText({ ...hiddenEffectsInputText, [id]: '' });

                                }

                              }

                            }}

                            placeholder="Type hidden effect and press Enter"

                          />

                        </div>

                        

                        <div style={{ fontSize: '0.7rem', opacity: 0.6, marginTop: '8px' }}>

                          💡 Visible effects show the system as the source. Hidden effects apply mechanically but player sees "?" instead of this system's name.

                        </div>

                        

                        {/* Effect Builder Modal */}

                        {showEffectBuilder === id && (

                          <div style={{ marginTop: '12px' }}>

                            <EffectBuilder

                              onAdd={(effect) => {

                                if (effectBuilderType === 'hidden') {

                                  updateSystem(id, { hiddenEffects: [...(sys.hiddenEffects || []), effect] });

                                } else {

                                  updateSystem(id, { effects: [...(sys.effects || []), effect] });

                                }

                                setShowEffectBuilder(null);

                              }}

                              onCancel={() => setShowEffectBuilder(null)}

                            />

                          </div>

                        )}

                      </div>

                      )}



                      {sys.type !== 'note' && sys.type !== 'tracker' && sys.type !== 'secret' && (

                      <div>

                        <label style={S.label}>Cures (one per line, e.g. "2x Herb")</label>

                        <DebouncedInput as="textarea" style={{ ...S.input, minHeight: '70px', resize: 'vertical' }} value={formatCures(sys.cures)} onCommit={v => updateSystem(id, { cures: parseCures(v) })} placeholder="1x Blessing Ritual" />

                      </div>

                      )}



                      <div style={{ gridColumn: '1 / span 2', padding: '10px', borderRadius: '8px', border: '1px solid rgba(176,176,255,0.18)', background: 'rgba(0,0,0,0.15)' }}>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                          <div style={{ fontWeight: 'bold', color: '#b0b0ff' }}>Custom Fields (Revealable)</div>

                          <button

                            style={{ ...S.btn, ...S.btnEdit, ...S.btnXs, marginBottom: 0 }}

                            onClick={() => {

                              const next = [

                                ...(sys.sections || []),

                                { id: generateId(), label: 'New Section', kind: 'list', dmOnly: false, items: [], text: '' }

                              ];

                              updateSystem(id, { sections: next });

                            }}

                          >

                            ➕ Add section

                          </button>

                        </div>



                        <div style={{ fontSize: '0.75rem', opacity: 0.82, marginTop: '6px', lineHeight: 1.35 }}>

                          <div style={{ marginBottom: '6px' }}>

                            Sections are <b>revealable fields</b> you can attach to a system (like “Symptoms”, “Omen”, “True Name”, “DM Secret”).

                          </div>

                          <ul style={{ margin: '0 0 0 18px', padding: 0 }}>

                            <li><b>List</b> = one item per line; reveal items individually (great for symptoms/progression).</li>

                            <li><b>Text</b> = reveal a whole paragraph when it’s time (great for lore/secret truths).</li>

                            <li><b>DM only</b> = never shown to players (your behind-the-screen notes).</li>

                          </ul>

                          <div style={{ marginTop: '6px', opacity: 0.85 }}>

                            After applying the system to a player, you’ll manage these reveals in <b>System Manager</b>.

                          </div>

                        </div>



                        {(sys.sections || []).length === 0 ? (

                          <div style={{ marginTop: '8px', opacity: 0.7, fontSize: '0.85rem' }}>No custom sections yet.</div>

                        ) : (

                          <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>

                            {(sys.sections || []).map((sec, si) => (

                              <div key={sec.id || si} style={{ padding: '10px', borderRadius: '8px', background: 'rgba(0,0,0,0.16)', border: '1px solid rgba(255,255,255,0.08)' }}>

                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '8px' }}>

                                  <input

                                    style={{ ...S.input, flex: 1, minWidth: '220px', marginBottom: 0 }}

                                    value={sec.label || ''}

                                    onChange={e => {

                                      const next = (sys.sections || []).map(sx => sx.id === sec.id ? { ...sx, label: e.target.value } : sx);

                                      updateSystem(id, { sections: next });

                                    }}

                                  />

                                  <select

                                    style={{ ...S.select, width: '160px', marginBottom: 0 }}

                                    value={sec.kind || 'list'}

                                    onChange={e => {

                                      const kind = e.target.value;

                                      const next = (sys.sections || []).map(sx => sx.id === sec.id ? { ...sx, kind } : sx);

                                      updateSystem(id, { sections: next });

                                    }}

                                  >

                                    <option value="list">📝 List (reveal per item)</option>

                                    <option value="text">📄 Text (reveal block)</option>

                                    <option value="rolltable">🎲 Roll Table</option>

                                    <option value="stages">📈 Progression Stages</option>

                                  </select>



                                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }}>

                                    <input

                                      type="checkbox"

                                      checked={!!sec.dmOnly}

                                      onChange={e => {

                                        const next = (sys.sections || []).map(sx => sx.id === sec.id ? { ...sx, dmOnly: e.target.checked } : sx);

                                        updateSystem(id, { sections: next });

                                      }}

                                    />

                                    DM only

                                  </label>

                                  

                                  {/* Reorder buttons */}

                                  <div style={{ display: 'flex', gap: '2px' }}>

                                    <button

                                      style={{ ...S.btn, ...S.btnXs, marginBottom: 0, padding: '2px 6px', opacity: si === 0 ? 0.3 : 1 }}

                                      disabled={si === 0}

                                      onClick={() => {

                                        const sections = [...(sys.sections || [])];

                                        [sections[si], sections[si - 1]] = [sections[si - 1], sections[si]];

                                        updateSystem(id, { sections });

                                      }}

                                      title="Move up"

                                    >↑</button>

                                    <button

                                      style={{ ...S.btn, ...S.btnXs, marginBottom: 0, padding: '2px 6px', opacity: si === (sys.sections || []).length - 1 ? 0.3 : 1 }}

                                      disabled={si === (sys.sections || []).length - 1}

                                      onClick={() => {

                                        const sections = [...(sys.sections || [])];

                                        [sections[si], sections[si + 1]] = [sections[si + 1], sections[si]];

                                        updateSystem(id, { sections });

                                      }}

                                      title="Move down"

                                    >↓</button>

                                  </div>



                                  <button

                                    style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, marginBottom: 0 }}

                                    onClick={() => {

                                      const next = (sys.sections || []).filter(sx => sx.id !== sec.id);

                                      updateSystem(id, { sections: next });

                                    }}

                                  >

                                    🗑️

                                  </button>

                                </div>



                                {/* Text Section */}

                                {sec.kind === 'text' && (

                                  <textarea

                                    style={{ ...S.input, minHeight: '70px', resize: 'vertical' }}

                                    value={sec.text || ''}

                                    onChange={e => {

                                      const next = (sys.sections || []).map(sx => sx.id === sec.id ? { ...sx, text: e.target.value } : sx);

                                      updateSystem(id, { sections: next });

                                    }}

                                    placeholder="Freeform text. This whole block is revealed at once."

                                  />

                                )}

                                

                                {/* List Section */}

                                {sec.kind === 'list' && (

                                  <textarea

                                    style={{ ...S.input, minHeight: '70px', resize: 'vertical' }}

                                    value={(sec.items || []).join('\n')}

                                    onChange={e => {

                                      const items = parseLines(e.target.value);

                                      const next = (sys.sections || []).map(sx => sx.id === sec.id ? { ...sx, items } : sx);

                                      updateSystem(id, { sections: next });

                                    }}

                                    placeholder="One item per line. Each can be revealed individually."

                                  />

                                )}

                                

                                {/* Roll Table Section */}

                                {sec.kind === 'rolltable' && (

                                  <div>

                                    <div style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>

                                      <select

                                        style={{ ...S.select, width: '100px', marginBottom: 0 }}

                                        value={sec.diceType || 'd6'}

                                        onChange={e => {

                                          const next = (sys.sections || []).map(sx => sx.id === sec.id ? { ...sx, diceType: e.target.value } : sx);

                                          updateSystem(id, { sections: next });

                                        }}

                                      >

                                        <option value="d4">d4</option>

                                        <option value="d6">d6</option>

                                        <option value="d8">d8</option>

                                        <option value="d10">d10</option>

                                        <option value="d12">d12</option>

                                        <option value="d20">d20</option>

                                        <option value="d100">d100</option>

                                      </select>

                                      <span style={{ fontSize: '0.8rem', opacity: 0.7, alignSelf: 'center' }}>

                                        Enter one result per line (order = roll result)

                                      </span>

                                    </div>

                                    <textarea

                                      style={{ ...S.input, minHeight: '100px', resize: 'vertical', fontFamily: 'monospace' }}

                                      value={(sec.items || []).join('\n')}

                                      onChange={e => {

                                        const items = parseLines(e.target.value);

                                        const next = (sys.sections || []).map(sx => sx.id === sec.id ? { ...sx, items } : sx);

                                        updateSystem(id, { sections: next });

                                      }}

                                      placeholder="1: Nothing happens&#10;2: Minor effect&#10;3: Moderate effect&#10;4: Major effect&#10;5: Severe effect&#10;6: Critical effect"

                                    />

                                  </div>

                                )}

                                

                                {/* Progression Stages Section */}

                                {sec.kind === 'stages' && (

                                  <div>

                                    <div style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '6px' }}>

                                      Define stages with format: <code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 4px', borderRadius: '2px' }}>Stage Name | Description | Effect</code>

                                    </div>

                                    <textarea

                                      style={{ ...S.input, minHeight: '120px', resize: 'vertical', fontFamily: 'monospace' }}

                                      value={(sec.items || []).join('\n')}

                                      onChange={e => {

                                        const items = parseLines(e.target.value);

                                        const next = (sys.sections || []).map(sx => sx.id === sec.id ? { ...sx, items } : sx);

                                        updateSystem(id, { sections: next });

                                      }}

                                      placeholder="Stage 1: Onset | Mild symptoms appear | -1 Con&#10;Stage 2: Worsening | Symptoms intensify | -2 Con&#10;Stage 3: Critical | Life-threatening | -3 Con, Exhaustion 1&#10;Stage 4: Terminal | Without cure, death in 1d4 days | -4 Con, Exhaustion 2"

                                    />

                                    <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>

                                      <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>Current Stage:</span>

                                      <select

                                        style={{ ...S.select, width: '150px', marginBottom: 0 }}

                                        value={sec.currentStage ?? 0}

                                        onChange={e => {

                                          const next = (sys.sections || []).map(sx => sx.id === sec.id ? { ...sx, currentStage: parseInt(e.target.value) } : sx);

                                          updateSystem(id, { sections: next });

                                        }}

                                      >

                                        {(sec.items || []).map((item, idx) => {

                                          const stageName = item.split('|')[0]?.trim() || `Stage ${idx + 1}`;

                                          return <option key={idx} value={idx}>{stageName}</option>;

                                        })}

                                        {(sec.items || []).length === 0 && <option value="0">No stages defined</option>}

                                      </select>

                                    </div>

                                  </div>

                                )}

                                

                                {/* Default fallback for unknown types */}

                                {!['text', 'list', 'rolltable', 'stages'].includes(sec.kind) && (

                                  <textarea

                                    style={{ ...S.input, minHeight: '70px', resize: 'vertical' }}

                                    value={(sec.items || []).join('\n')}

                                    onChange={e => {

                                      const items = parseLines(e.target.value);

                                      const next = (sys.sections || []).map(sx => sx.id === sec.id ? { ...sx, items } : sx);

                                      updateSystem(id, { sections: next });

                                    }}

                                    placeholder="One item per line."

                                  />

                                )}

                              </div>

                            ))}

                          </div>

                        )}

                      </div>



                      {/* Tracker Fields (for tracker type) */}

                      {(sys.type === 'tracker' || sys.type === 'custom' || sys.type === 'spell-resource') && (

                      <div style={{ gridColumn: '1 / span 2', padding: '10px', borderRadius: '8px', border: '1px solid rgba(201,162,39,0.2)', background: 'rgba(201,162,39,0.05)' }}>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                          <div style={{ fontWeight: 'bold', color: '#c9a227' }}>📊 Tracker Fields</div>

                          <button

                            style={{ ...S.btn, ...S.btnEdit, ...S.btnXs, marginBottom: 0 }}

                            onClick={() => {

                              const newField = createTrackerField('counter', 'New Counter');

                              const next = [...(sys.trackerFields || []), newField];

                              updateSystem(id, { trackerFields: next });

                            }}

                          >

                            ➕ Add field

                          </button>

                        </div>

                        

                        <div style={{ fontSize: '0.75rem', opacity: 0.75, marginTop: '6px' }}>

                          Add numeric counters, progress bars, or timers that can be tracked per character.

                        </div>

                        

                        {(sys.trackerFields || []).length === 0 ? (

                          <div style={{ marginTop: '8px', opacity: 0.7, fontSize: '0.85rem' }}>No tracker fields yet.</div>

                        ) : (

                          <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>

                            {(sys.trackerFields || []).map((field, fi) => (

                              <div key={field.id || fi} style={{ padding: '10px', background: 'rgba(0,0,0,0.15)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.1)' }}>

                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>

                                  <DebouncedInput

                                    as="input"

                                    style={{ ...S.input, flex: 1, minWidth: '150px', marginBottom: 0 }}

                                    value={field.label || ''}

                                    placeholder="DM label (internal)"

                                    onCommit={v => {

                                      const next = (sys.trackerFields || []).map(f => f.id === field.id ? { ...f, label: v } : f);

                                      updateSystem(id, { trackerFields: next });

                                    }}

                                  />

                                  <select

                                    style={{ ...S.select, width: '140px', marginBottom: 0 }}

                                    value={field.type || 'number'}

                                    onChange={e => {

                                      const next = (sys.trackerFields || []).map(f => f.id === field.id ? { ...f, type: e.target.value } : f);

                                      updateSystem(id, { trackerFields: next });

                                    }}

                                  >

                                    {TRACKER_FIELD_TYPES.map(t => (

                                      <option key={t.id} value={t.id}>{t.icon} {t.label}</option>

                                    ))}

                                  </select>

                                  <button

                                    style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, marginBottom: 0 }}

                                    onClick={() => {

                                      const next = (sys.trackerFields || []).filter(f => f.id !== field.id);

                                      updateSystem(id, { trackerFields: next });

                                    }}

                                  >🗑️</button>

                                </div>

                                {/* Optional player-facing label (P3 — tracker-level privacy). When set and the

                                    tracker is visible to the player, this overrides `label` in their view.

                                    Leave blank to show the DM label. See docs/spell-resource-architecture.md §3.3. */}

                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>

                                  <span style={{ fontSize: '0.7rem', opacity: 0.7, whiteSpace: 'nowrap' }}>👁 Player label:</span>

                                  <DebouncedInput

                                    as="input"

                                    style={{ ...S.input, flex: 1, minWidth: '150px', marginBottom: 0, fontSize: '0.85rem' }}

                                    value={field.publicLabel || ''}

                                    placeholder={`(optional — falls back to "${field.label || 'label'}")`}

                                    onCommit={v => {

                                      const next = (sys.trackerFields || []).map(f => f.id === field.id ? { ...f, publicLabel: v } : f);

                                      updateSystem(id, { trackerFields: next });

                                    }}

                                  />

                                </div>

                                

                                {/* Field-specific settings */}

                                {(field.type === 'number' || field.type === 'counter' || field.type === 'progress') && (

                                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>

                                    <div>

                                      <label style={{ fontSize: '0.7rem', opacity: 0.7 }}>Default</label>

                                      <input

                                        type="number"

                                        style={{ ...S.input, width: '70px', marginBottom: 0 }}

                                        value={field.value ?? 0}

                                        onChange={e => {

                                          const next = (sys.trackerFields || []).map(f => f.id === field.id ? { ...f, value: parseFloat(e.target.value) || 0 } : f);

                                          updateSystem(id, { trackerFields: next });

                                        }}

                                      />

                                    </div>

                                    <div>

                                      <label style={{ fontSize: '0.7rem', opacity: 0.7 }}>Min</label>

                                      <input

                                        type="number"

                                        style={{ ...S.input, width: '60px', marginBottom: 0 }}

                                        value={field.min ?? ''}

                                        placeholder="∞"

                                        onChange={e => {

                                          const val = e.target.value === '' ? null : parseFloat(e.target.value);

                                          const next = (sys.trackerFields || []).map(f => f.id === field.id ? { ...f, min: val } : f);

                                          updateSystem(id, { trackerFields: next });

                                        }}

                                      />

                                    </div>

                                    <div>

                                      <label style={{ fontSize: '0.7rem', opacity: 0.7 }}>Max</label>

                                      <input

                                        type="number"

                                        style={{ ...S.input, width: '60px', marginBottom: 0 }}

                                        value={field.max ?? ''}

                                        placeholder="∞"

                                        onChange={e => {

                                          const val = e.target.value === '' ? null : parseFloat(e.target.value);

                                          const next = (sys.trackerFields || []).map(f => f.id === field.id ? { ...f, max: val } : f);

                                          updateSystem(id, { trackerFields: next });

                                        }}

                                      />

                                    </div>

                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem' }}>

                                      <input

                                        type="checkbox"

                                        checked={!!field.showToPlayer}

                                        onChange={e => {

                                          const next = (sys.trackerFields || []).map(f => f.id === field.id ? { ...f, showToPlayer: e.target.checked } : f);

                                          updateSystem(id, { trackerFields: next });

                                        }}

                                      />

                                      Show to player

                                    </label>

                                  </div>

                                )}

                                

                                {field.type === 'timer' && (

                                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>

                                    <div>

                                      <label style={{ fontSize: '0.7rem', opacity: 0.7 }}>Default</label>

                                      <input

                                        type="number"

                                        style={{ ...S.input, width: '70px', marginBottom: 0 }}

                                        value={field.value ?? 0}

                                        onChange={e => {

                                          const next = (sys.trackerFields || []).map(f => f.id === field.id ? { ...f, value: parseInt(e.target.value) || 0 } : f);

                                          updateSystem(id, { trackerFields: next });

                                        }}

                                      />

                                    </div>

                                    <div>

                                      <label style={{ fontSize: '0.7rem', opacity: 0.7 }}>Unit</label>

                                      <select

                                        style={{ ...S.select, width: '90px', marginBottom: 0 }}

                                        value={field.unit || 'days'}

                                        onChange={e => {

                                          const next = (sys.trackerFields || []).map(f => f.id === field.id ? { ...f, unit: e.target.value } : f);

                                          updateSystem(id, { trackerFields: next });

                                        }}

                                      >

                                        <option value="turns">Turns</option>

                                        <option value="hours">Hours</option>

                                        <option value="days">Days</option>

                                        <option value="weeks">Weeks</option>

                                      </select>

                                    </div>

                                  </div>

                                )}

                                

                                {/* Threshold Configuration */}

                                {(field.type === 'number' || field.type === 'counter' || field.type === 'progress') && (

                                  <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>

                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>

                                      <span style={{ fontSize: '0.75rem', color: '#c9a227' }}>⚡ Thresholds</span>

                                      <button

                                        style={{ ...S.btn, ...S.btnXs, marginBottom: 0, fontSize: '0.7rem' }}

                                        onClick={() => {

                                          const newThreshold = createThreshold(5);

                                          const newField = { ...field, thresholds: [...(field.thresholds || []), newThreshold] };

                                          const next = (sys.trackerFields || []).map(f => f.id === field.id ? newField : f);

                                          updateSystem(id, { trackerFields: next });

                                        }}

                                      >

                                        + Add Threshold

                                      </button>

                                    </div>

                                    

                                    {(field.thresholds || []).length === 0 ? (

                                      <div style={{ fontSize: '0.7rem', opacity: 0.6 }}>No thresholds. Add one to trigger effects at specific values.</div>

                                    ) : (

                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>

                                        {(field.thresholds || []).map((thresh, ti) => (

                                          <div key={thresh.id || ti} style={{ display: 'flex', gap: '6px', alignItems: 'center', padding: '4px', background: 'rgba(0,0,0,0.1)', borderRadius: '4px' }}>

                                            <input

                                              type="number"

                                              value={thresh.value ?? 0}

                                              onChange={e => {

                                                const newThresholds = (field.thresholds || []).map((t, i) => i === ti ? { ...t, value: parseInt(e.target.value) || 0 } : t);

                                                const newField = { ...field, thresholds: newThresholds };

                                                const next = (sys.trackerFields || []).map(f => f.id === field.id ? newField : f);

                                                updateSystem(id, { trackerFields: next });

                                              }}

                                              style={{ ...S.input, width: '50px', marginBottom: 0, textAlign: 'center', fontSize: '0.8rem' }}

                                              title="Threshold value"

                                            />

                                            <span style={{ opacity: 0.6, fontSize: '0.75rem' }}>→</span>

                                            <input

                                              value={thresh.effect || ''}

                                              onChange={e => {

                                                const newThresholds = (field.thresholds || []).map((t, i) => i === ti ? { ...t, effect: e.target.value } : t);

                                                const newField = { ...field, thresholds: newThresholds };

                                                const next = (sys.trackerFields || []).map(f => f.id === field.id ? newField : f);

                                                updateSystem(id, { trackerFields: next });

                                              }}

                                              placeholder="Effect (e.g., -1 Str)"

                                              style={{ ...S.input, flex: 1, marginBottom: 0, fontSize: '0.8rem' }}

                                            />

                                            <button

                                              onClick={() => {

                                                const newThresholds = (field.thresholds || []).filter((_, i) => i !== ti);

                                                const newField = { ...field, thresholds: newThresholds };

                                                const next = (sys.trackerFields || []).map(f => f.id === field.id ? newField : f);

                                                updateSystem(id, { trackerFields: next });

                                              }}

                                              style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, marginBottom: 0, padding: '2px 6px' }}

                                            >✕</button>

                                          </div>

                                        ))}

                                      </div>

                                    )}

                                    <div style={{ fontSize: '0.65rem', opacity: 0.5, marginTop: '4px' }}>

                                      Effects apply when tracker value ≥ threshold value

                                    </div>

                                  </div>

                                )}

                              </div>

                            ))}

                          </div>

                        )}

                      </div>

                      )}



                      {/* Techniques Section (Session 3 — spell-resource architecture).

                          A technique is a castable ability bound to this system: a spell

                          (library or inline) wrapped with costs, requirements, availability

                          gates, per-technique on-cast rules, and privacy flags. See

                          docs/spell-resource-architecture.md §3.1. */}

                      <div style={{ gridColumn: '1 / span 2', padding: '10px', borderRadius: '8px', border: '1px solid rgba(74,144,217,0.25)', background: 'rgba(74,144,217,0.05)' }}>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', gap: '8px', flexWrap: 'wrap' }}>

                          <div>

                            <div style={{ fontWeight: 'bold', color: '#4a90d9' }}>📚 Direct Spell Bindings</div>

                            <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '4px' }}>

                              Bind library spells directly to a systems resource profile. Use this when spells should consume the same resource without becoming separate techniques.

                            </div>

                          </div>

                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>

                            <button

                              style={{ ...S.btn, ...S.btnEdit, ...S.btnXs, marginBottom: 0 }}

                              onClick={() => {

                                const next = [

                                  ...(sys.directSpellBindings || []),

                                  createSystemDirectSpellBinding(sys, {

                                    assignmentMode: 'specific-spells',

                                  }),

                                ];

                                updateSystem(id, { directSpellBindings: next });

                              }}

                              disabled={scalarResourceProfiles.length === 0}

                              title={scalarResourceProfiles.length === 0 ? 'Add a scalar resource profile first' : 'Bind a specific library spell'}

                            >

                              ➕ Bind spell

                            </button>

                            <button

                              style={{ ...S.btn, ...S.btnXs, marginBottom: 0, opacity: defaultBindingCount > 0 || scalarResourceProfiles.length === 0 ? 0.6 : 1 }}

                              disabled={defaultBindingCount > 0 || scalarResourceProfiles.length === 0}

                              onClick={() => {

                                const next = [

                                  ...(sys.directSpellBindings || []),

                                  createSystemDirectSpellBinding(sys, {

                                    assignmentMode: 'all-spells',

                                    spellId: null,

                                    spellIds: [],

                                  }),

                                ];

                                updateSystem(id, { directSpellBindings: next });

                              }}

                              title={defaultBindingCount > 0 ? 'This system already has an all-spells default binding' : 'Create a default resource binding for all spells'}

                            >

                              🌐 Add all-spells default

                            </button>

                          </div>

                        </div>



                        {scalarResourceProfiles.length === 0 ? (

                          <div style={{ fontSize: '0.8rem', opacity: 0.7, marginBottom: '8px' }}>

                            No scalar resource profiles are available yet. Spell bindings in this batch only target scalar profiles; tiered slot profiles remain a later session.

                          </div>

                        ) : (

                          <>

                            {defaultBindingCount > 1 && (

                              <div style={{ marginBottom: '10px', padding: '8px', borderRadius: '6px', background: 'rgba(201,39,39,0.12)', border: '1px solid rgba(201,39,39,0.2)', fontSize: '0.75rem' }}>

                                This system currently has multiple all-spells defaults. Resolution stays deterministic, but only one default binding should remain long-term.

                              </div>

                            )}

                            {(sys.directSpellBindings || []).length === 0 ? (

                              <div style={{ fontSize: '0.8rem', opacity: 0.65, marginBottom: '8px' }}>

                                No direct bindings yet. Add a specific spell binding or one default all-spells binding.

                              </div>

                            ) : (

                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '8px' }}>

                                {(sys.directSpellBindings || []).map((binding, bi) => (

                                  <DirectSpellBindingEditor

                                    key={binding.id || bi}

                                    binding={binding}

                                    currentSystem={sys}

                                    spellLibrary={spellLibrary}

                                    onSaveSpellLibrary={onSaveSpellLibrary}

                                    onUpdate={(patch) => {

                                      const next = (sys.directSpellBindings || []).map((existing, i) => i === bi ? { ...existing, ...patch } : existing);

                                      updateSystem(id, { directSpellBindings: next });

                                    }}

                                    onDelete={() => {

                                      const next = (sys.directSpellBindings || []).filter((_, i) => i !== bi);

                                      updateSystem(id, { directSpellBindings: next });

                                    }}

                                  />

                                ))}

                              </div>

                            )}

                          </>

                        )}

                      </div>



                      {/* Techniques Section (Session 3 â€” spell-resource architecture). */}

                      <div style={{ gridColumn: '1 / span 2', padding: '10px', borderRadius: '8px', border: '1px solid rgba(155,89,182,0.25)', background: 'rgba(155,89,182,0.05)' }}>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>

                          <div style={{ fontWeight: 'bold', color: '#9b59b6' }}>🔮 Techniques (castable abilities)</div>

                          <button

                            style={{ ...S.btn, ...S.btnEdit, ...S.btnXs, marginBottom: 0 }}

                            onClick={() => {

                              const newTech = createTechnique({ name: `Technique ${(sys.techniques || []).length + 1}` });

                              const next = [...(sys.techniques || []), newTech];

                              updateSystem(id, { techniques: next });

                            }}

                          >

                            ➕ Add technique

                          </button>

                        </div>

                        <div style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '10px' }}>

                          Techniques players can cast through this system. Each binds a spell (library or inline) to a cost model, requirement gates, and per-technique on-cast rules.

                          {sys?.type !== 'spell-resource' && (

                            <span style={{ color: '#c9a227' }}> Tip: set this system's type to "Spell Resource" for the best combat-panel integration.</span>

                          )}

                        </div>

                        {(sys.techniques || []).length === 0 ? (

                          <div style={{ fontSize: '0.8rem', opacity: 0.6 }}>

                            No techniques yet. Click "Add technique" to define a castable ability.

                            {(sys.spells || []).length > 0 && (

                              <div style={{ marginTop: '6px', color: '#c9a227' }}>

                                📜 This system has {(sys.spells || []).length} legacy spell entries. They render in player tooltips via a compatibility shim; add real Techniques to enable cast mechanics.

                              </div>

                            )}

                          </div>

                        ) : (

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>

                            {(sys.techniques || []).map((tech, ti) => (

                              <TechniqueEditor

                                key={tech.id || ti}

                                technique={tech}

                                currentSystemId={id}

                                currentSystem={sys}

                                allSystems={locSystems}

                                spellLibrary={spellLibrary}

                                onSaveSpellLibrary={onSaveSpellLibrary}

                                locations={locLocations}

                                onUpdate={patch => {

                                  const next = (sys.techniques || []).map((t, i) => i === ti ? { ...t, ...patch } : t);

                                  updateSystem(id, { techniques: next });

                                }}

                                onDelete={() => {

                                  const next = (sys.techniques || []).filter((_, i) => i !== ti);

                                  updateSystem(id, { techniques: next });

                                }}

                              />

                            ))}

                          </div>

                        )}

                      </div>



                      {/* Instance Slots Section (Systems expansion Session 3).

                          Slots let a single template spawn parameterized instances —

                          e.g. The Apprentice takes {{MENTOR}}, Bloodline takes {{ANCESTOR}}.

                          Slot keys appear as `{{KEY}}` in the template's name/description/

                          clue text/rule messages; the DM fills values when applying. */}

                      <div style={{ gridColumn: '1 / span 2', padding: '10px', borderRadius: '8px', border: '1px solid rgba(155,89,182,0.2)', background: 'rgba(155,89,182,0.05)' }}>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>

                          <div style={{ fontWeight: 'bold', color: '#b97ad9' }}>🧩 Instance Slots</div>

                          <button

                            style={{ ...S.btn, ...S.btnEdit, ...S.btnXs, marginBottom: 0 }}

                            onClick={() => {

                              const newSlot = { key: '', label: '', default: '', description: '' };

                              const next = [...(sys.instanceSlots || []), newSlot];

                              updateSystem(id, { instanceSlots: next });

                            }}

                          >

                            ➕ Add slot

                          </button>

                        </div>



                        <div style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '10px' }}>

                          Parameterize this template. Use <code>{'{{KEY}}'}</code> anywhere in the name, description, clues, sections, or rule messages — the DM fills values when applying the system. Example: key <code>MENTOR</code>, then write "You study under {'{{MENTOR}}'}".

                        </div>



                        {(sys.instanceSlots || []).length === 0 ? (

                          <div style={{ fontSize: '0.8rem', opacity: 0.6 }}>No slots. Add one to parameterize this template.</div>

                        ) : (

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>

                            {(sys.instanceSlots || []).map((slot, si) => (

                              <div key={si} style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', border: '1px solid rgba(155,89,182,0.2)' }}>

                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '6px' }}>

                                  <DebouncedInput

                                    as="input"

                                    style={{ ...S.input, width: '160px', marginBottom: 0, fontFamily: 'monospace' }}

                                    value={slot.key || ''}

                                    placeholder="KEY (e.g. MENTOR)"

                                    onCommit={v => {

                                      const next = (sys.instanceSlots || []).map((s, i) => i === si ? { ...s, key: v.replace(/[^A-Za-z0-9_]/g, '') } : s);

                                      updateSystem(id, { instanceSlots: next });

                                    }}

                                  />

                                  <DebouncedInput

                                    as="input"

                                    style={{ ...S.input, flex: 1, minWidth: '180px', marginBottom: 0 }}

                                    value={slot.label || ''}

                                    placeholder="Label shown on apply (e.g. Mentor's name)"

                                    onCommit={v => {

                                      const next = (sys.instanceSlots || []).map((s, i) => i === si ? { ...s, label: v } : s);

                                      updateSystem(id, { instanceSlots: next });

                                    }}

                                  />

                                  <button

                                    style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, marginBottom: 0 }}

                                    onClick={() => {

                                      const next = (sys.instanceSlots || []).filter((_, i) => i !== si);

                                      updateSystem(id, { instanceSlots: next });

                                    }}

                                  >🗑️</button>

                                </div>

                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>

                                  <DebouncedInput

                                    as="input"

                                    style={{ ...S.input, width: '200px', marginBottom: 0, fontSize: '0.85rem' }}

                                    value={slot.default || ''}

                                    placeholder="Default value (optional)"

                                    onCommit={v => {

                                      const next = (sys.instanceSlots || []).map((s, i) => i === si ? { ...s, default: v } : s);

                                      updateSystem(id, { instanceSlots: next });

                                    }}

                                  />

                                  <DebouncedInput

                                    as="input"

                                    style={{ ...S.input, flex: 1, minWidth: '200px', marginBottom: 0, fontSize: '0.85rem' }}

                                    value={slot.description || ''}

                                    placeholder="Hint shown below the field on apply (optional)"

                                    onCommit={v => {

                                      const next = (sys.instanceSlots || []).map((s, i) => i === si ? { ...s, description: v } : s);

                                      updateSystem(id, { instanceSlots: next });

                                    }}

                                  />

                                </div>

                                {slot.key && !/^[A-Z][A-Z0-9_]*$/.test(slot.key) && (

                                  <div style={{ fontSize: '0.7rem', opacity: 0.7, marginTop: '4px', color: '#c9a227' }}>

                                    Tip: keys are usually UPPER_SNAKE_CASE (e.g. <code>MENTOR</code>, <code>BLOOD_CLAN</code>).

                                  </div>

                                )}

                              </div>

                            ))}

                          </div>

                        )}



                        {/* Cascade-on-remove checkbox (Systems expansion Session 4).

                            When on, removing a parent instance auto-removes every child

                            instance whose parentSystemId matches. Used for parent-spawns-

                            children chains like Bloodline → Blood Debt. */}

                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '10px', fontSize: '0.85rem', cursor: 'pointer' }}>

                          <input

                            type="checkbox"

                            checked={!!sys.cascadeOnRemove}

                            onChange={e => updateSystem(id, { cascadeOnRemove: e.target.checked })}

                          />

                          <span>Cascade-remove children when this system is removed</span>

                          <span style={{ fontSize: '0.72rem', opacity: 0.65 }}>

                            (children are instances this system applied via <code>apply-system</code>)

                          </span>

                        </label>

                      </div>



                      {/* Automation Rules Section */}

                      <div style={{ gridColumn: '1 / span 2', padding: '10px', borderRadius: '8px', border: '1px solid rgba(74,144,217,0.2)', background: 'rgba(74,144,217,0.05)' }}>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>

                          <div style={{ fontWeight: 'bold', color: '#4a90d9' }}>⚙️ Automation Rules</div>

                          <button

                            style={{ ...S.btn, ...S.btnEdit, ...S.btnXs, marginBottom: 0 }}

                            onClick={() => {

                              const newRule = createAutomationRule('manual');

                              const next = [...(sys.automationRules || []), newRule];

                              updateSystem(id, { automationRules: next });

                            }}

                          >

                            ➕ Add rule

                          </button>

                        </div>

                        

                        <div style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '10px' }}>

                          Define automatic actions that trigger based on events (rest, damage, time, etc.).

                        </div>

                        

                        {(sys.automationRules || []).length === 0 ? (

                          <div style={{ fontSize: '0.8rem', opacity: 0.6 }}>No automation rules. Add one to create automatic behaviors.</div>

                        ) : (

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>

                            {(sys.automationRules || []).map((rule, ri) => (

                              <div key={rule.id || ri} style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', border: rule.enabled ? '1px solid rgba(74,144,217,0.3)' : '1px solid rgba(100,100,100,0.2)' }}>

                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>

                                  <input

                                    type="checkbox"

                                    checked={rule.enabled !== false}

                                    onChange={e => {

                                      const next = (sys.automationRules || []).map((r, i) => i === ri ? { ...r, enabled: e.target.checked } : r);

                                      updateSystem(id, { automationRules: next });

                                    }}

                                    title="Enable/disable rule"

                                  />

                                  <input

                                    style={{ ...S.input, flex: 1, marginBottom: 0, fontWeight: 'bold' }}

                                    value={rule.name || ''}

                                    onChange={e => {

                                      const next = (sys.automationRules || []).map((r, i) => i === ri ? { ...r, name: e.target.value } : r);

                                      updateSystem(id, { automationRules: next });

                                    }}

                                    placeholder="Rule name"

                                  />

                                  <button

                                    style={{ ...S.btn, ...S.btnDanger, ...S.btnXs, marginBottom: 0 }}

                                    onClick={() => {

                                      const next = (sys.automationRules || []).filter((_, i) => i !== ri);

                                      updateSystem(id, { automationRules: next });

                                    }}

                                  >

                                    🗑️

                                  </button>

                                </div>

                                

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>

                                  <div>

                                    <label style={{ fontSize: '0.7rem', opacity: 0.7 }}>Trigger</label>

                                    <select

                                      style={{ ...S.select, width: '100%', marginBottom: 0 }}

                                      value={rule.trigger || 'manual'}

                                      onChange={e => {

                                        const next = (sys.automationRules || []).map((r, i) => i === ri ? { ...r, trigger: e.target.value } : r);

                                        updateSystem(id, { automationRules: next });

                                      }}

                                    >

                                      {RULE_TRIGGERS.map(t => (

                                        <option key={t.id} value={t.id}>{t.label}</option>

                                      ))}

                                    </select>

                                  </div>

                                  

                                  <div>

                                    <label style={{ fontSize: '0.7rem', opacity: 0.7 }}>Condition Logic</label>

                                    <select

                                      style={{ ...S.select, width: '100%', marginBottom: 0 }}

                                      value={rule.conditionLogic || 'all'}

                                      onChange={e => {

                                        const next = (sys.automationRules || []).map((r, i) => i === ri ? { ...r, conditionLogic: e.target.value } : r);

                                        updateSystem(id, { automationRules: next });

                                      }}

                                    >

                                      <option value="all">All conditions (AND)</option>

                                      <option value="any">Any condition (OR)</option>

                                    </select>

                                  </div>

                                </div>

                                

                                {/* Conditions */}

                                <div style={{ marginTop: '8px' }}>

                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>

                                    <span style={{ fontSize: '0.75rem', color: '#c9a227' }}>Conditions</span>

                                    <button

                                      style={{ ...S.btn, ...S.btnXs, marginBottom: 0, fontSize: '0.65rem' }}

                                      onClick={() => {

                                        const newCondition = { type: 'always', params: {} };

                                        const newRule = { ...rule, conditions: [...(rule.conditions || []), newCondition] };

                                        const next = (sys.automationRules || []).map((r, i) => i === ri ? newRule : r);

                                        updateSystem(id, { automationRules: next });

                                      }}

                                    >

                                      + Condition

                                    </button>

                                  </div>

                                  {(rule.conditions || []).map((cond, ci) => (

                                    <div key={ci} style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '4px' }}>

                                      <select

                                        style={{ ...S.select, flex: 1, marginBottom: 0, fontSize: '0.8rem' }}

                                        value={cond.type || 'always'}

                                        onChange={e => {

                                          const newConditions = (rule.conditions || []).map((c, i) => i === ci ? { ...c, type: e.target.value } : c);

                                          const newRule = { ...rule, conditions: newConditions };

                                          const next = (sys.automationRules || []).map((r, i) => i === ri ? newRule : r);

                                          updateSystem(id, { automationRules: next });

                                        }}

                                      >

                                        {RULE_CONDITIONS.map(c => (

                                          <option key={c.id} value={c.id}>{c.label}</option>

                                        ))}

                                      </select>

                                      

                                      {cond.type === 'random-chance' && (

                                        <input

                                          type="number"

                                          style={{ ...S.input, width: '60px', marginBottom: 0, fontSize: '0.8rem' }}

                                          value={cond.params?.percent ?? 50}

                                          onChange={e => {

                                            const newConditions = (rule.conditions || []).map((c, i) => i === ci ? { ...c, params: { ...c.params, percent: parseInt(e.target.value) || 0 } } : c);

                                            const newRule = { ...rule, conditions: newConditions };

                                            const next = (sys.automationRules || []).map((r, i) => i === ri ? newRule : r);

                                            updateSystem(id, { automationRules: next });

                                          }}

                                          placeholder="%"

                                        />

                                      )}

                                      

                                      {cond.type === 'roll-check' && (

                                        <>

                                          <span style={{ fontSize: '0.7rem' }}>DC</span>

                                          <input

                                            type="number"

                                            style={{ ...S.input, width: '45px', marginBottom: 0, fontSize: '0.8rem' }}

                                            value={cond.params?.dc ?? 10}

                                            onChange={e => {

                                              const newConditions = (rule.conditions || []).map((c, i) => i === ci ? { ...c, params: { ...c.params, dc: parseInt(e.target.value) || 10 } } : c);

                                              const newRule = { ...rule, conditions: newConditions };

                                              const next = (sys.automationRules || []).map((r, i) => i === ri ? newRule : r);

                                              updateSystem(id, { automationRules: next });

                                            }}

                                          />

                                          <select

                                            style={{ ...S.select, width: '70px', marginBottom: 0, fontSize: '0.8rem' }}

                                            value={cond.params?.stat || 'con'}

                                            onChange={e => {

                                              const newConditions = (rule.conditions || []).map((c, i) => i === ci ? { ...c, params: { ...c.params, stat: e.target.value } } : c);

                                              const newRule = { ...rule, conditions: newConditions };

                                              const next = (sys.automationRules || []).map((r, i) => i === ri ? newRule : r);

                                              updateSystem(id, { automationRules: next });

                                            }}

                                          >

                                            <option value="str">Str</option>

                                            <option value="dex">Dex</option>

                                            <option value="con">Con</option>

                                            <option value="int">Int</option>

                                            <option value="wis">Wis</option>

                                            <option value="cha">Cha</option>

                                          </select>

                                        </>

                                      )}

                                      

                                      <button

                                        style={{ ...S.btn, ...S.btnXs, marginBottom: 0, padding: '2px 6px', fontSize: '0.65rem' }}

                                        onClick={() => {

                                          const newConditions = (rule.conditions || []).filter((_, i) => i !== ci);

                                          const newRule = { ...rule, conditions: newConditions.length > 0 ? newConditions : [{ type: 'always', params: {} }] };

                                          const next = (sys.automationRules || []).map((r, i) => i === ri ? newRule : r);

                                          updateSystem(id, { automationRules: next });

                                        }}

                                      >

                                        ✕

                                      </button>

                                    </div>

                                  ))}

                                </div>

                                

                                {/* Actions */}

                                <div style={{ marginTop: '8px' }}>

                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>

                                    <span style={{ fontSize: '0.75rem', color: '#27c95a' }}>Actions</span>

                                    <button

                                      style={{ ...S.btn, ...S.btnXs, marginBottom: 0, fontSize: '0.65rem' }}

                                      onClick={() => {

                                        const newAction = { type: 'notify', params: { message: '' } };

                                        const newRule = { ...rule, actions: [...(rule.actions || []), newAction] };

                                        const next = (sys.automationRules || []).map((r, i) => i === ri ? newRule : r);

                                        updateSystem(id, { automationRules: next });

                                      }}

                                    >

                                      + Action

                                    </button>

                                  </div>

                                  {(rule.actions || []).length === 0 && (

                                    <div style={{ fontSize: '0.7rem', opacity: 0.5 }}>No actions defined yet.</div>

                                  )}

                                  {(rule.actions || []).map((action, ai) => (

                                    <div key={ai} style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '4px' }}>

                                      <select

                                        style={{ ...S.select, width: '140px', marginBottom: 0, fontSize: '0.8rem' }}

                                        value={action.type || 'notify'}

                                        onChange={e => {

                                          const newActions = (rule.actions || []).map((a, i) => i === ai ? { ...a, type: e.target.value } : a);

                                          const newRule = { ...rule, actions: newActions };

                                          const next = (sys.automationRules || []).map((r, i) => i === ri ? newRule : r);

                                          updateSystem(id, { automationRules: next });

                                        }}

                                      >

                                        {RULE_ACTIONS.map(a => (

                                          <option key={a.id} value={a.id}>{a.label}</option>

                                        ))}

                                      </select>

                                      

                                      {(action.type === 'notify' || action.type === 'log') && (

                                        <input

                                          style={{ ...S.input, flex: 1, marginBottom: 0, fontSize: '0.8rem' }}

                                          value={action.params?.message ?? ''}

                                          onChange={e => {

                                            const newActions = (rule.actions || []).map((a, i) => i === ai ? { ...a, params: { ...a.params, message: e.target.value } } : a);

                                            const newRule = { ...rule, actions: newActions };

                                            const next = (sys.automationRules || []).map((r, i) => i === ri ? newRule : r);

                                            updateSystem(id, { automationRules: next });

                                          }}

                                          placeholder="Message..."

                                        />

                                      )}

                                      

                                      {(action.type === 'deal-damage' || action.type === 'heal') && (

                                        <input

                                          type="number"

                                          style={{ ...S.input, width: '60px', marginBottom: 0, fontSize: '0.8rem' }}

                                          value={action.params?.amount ?? 0}

                                          onChange={e => {

                                            const newActions = (rule.actions || []).map((a, i) => i === ai ? { ...a, params: { ...a.params, amount: parseInt(e.target.value) || 0 } } : a);

                                            const newRule = { ...rule, actions: newActions };

                                            const next = (sys.automationRules || []).map((r, i) => i === ri ? newRule : r);

                                            updateSystem(id, { automationRules: next });

                                          }}

                                          placeholder="Amount"

                                        />

                                      )}

                                      

                                      {action.type === 'add-effect' && (

                                        <input

                                          style={{ ...S.input, flex: 1, marginBottom: 0, fontSize: '0.8rem' }}

                                          value={action.params?.effect ?? ''}

                                          onChange={e => {

                                            const newActions = (rule.actions || []).map((a, i) => i === ai ? { ...a, params: { ...a.params, effect: e.target.value } } : a);

                                            const newRule = { ...rule, actions: newActions };

                                            const next = (sys.automationRules || []).map((r, i) => i === ri ? newRule : r);

                                            updateSystem(id, { automationRules: next });

                                          }}

                                          placeholder="Effect (e.g., -1 Str)"

                                        />

                                      )}

                                      

                                      {(action.type === 'add-exhaustion' || action.type === 'remove-exhaustion') && (

                                        <input

                                          type="number"

                                          style={{ ...S.input, width: '50px', marginBottom: 0, fontSize: '0.8rem' }}

                                          value={action.params?.levels ?? 1}

                                          min="1"

                                          max="6"

                                          onChange={e => {

                                            const newActions = (rule.actions || []).map((a, i) => i === ai ? { ...a, params: { ...a.params, levels: parseInt(e.target.value) || 1 } } : a);

                                            const newRule = { ...rule, actions: newActions };

                                            const next = (sys.automationRules || []).map((r, i) => i === ri ? newRule : r);

                                            updateSystem(id, { automationRules: next });

                                          }}

                                        />

                                      )}

                                      

                                      <button

                                        style={{ ...S.btn, ...S.btnXs, marginBottom: 0, padding: '2px 6px', fontSize: '0.65rem' }}

                                        onClick={() => {

                                          const newActions = (rule.actions || []).filter((_, i) => i !== ai);

                                          const newRule = { ...rule, actions: newActions };

                                          const next = (sys.automationRules || []).map((r, i) => i === ri ? newRule : r);

                                          updateSystem(id, { automationRules: next });

                                        }}

                                      >

                                        ✕

                                      </button>

                                    </div>

                                  ))}

                                </div>

                              </div>

                            ))}

                          </div>

                        )}

                      </div>



                      <div>

                        <SystemTagsField
                          tags={sys.tags || []}
                          onChange={(value) => updateSystem(id, { tags: value })}
                          labelStyle={S.label}
                          inputStyle={S.input}
                        />

                      </div>



                      <div style={{ gridColumn: '1 / span 2' }}>

                        <label style={S.label}>Locations</label>

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>

                          {locLocations.map(loc => (

                            <label key={loc} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.85rem', padding: '2px 6px', background: 'rgba(0,0,0,0.15)', borderRadius: '4px' }}>

                              <input type="checkbox" checked={(sys.locations || []).includes(loc)} onChange={e => updateSystem(id, { locations: toggleFromArray(sys.locations, loc, e.target.checked) })} />

                              <span>{loc}</span>

                            </label>

                          ))}

                        </div>

                        <div style={{ fontSize: '0.7rem', opacity: 0.65, marginTop: '4px' }}>

                          If no locations are selected, this system will appear everywhere in the DM dropdown.

                        </div>

                      </div>

                    </div>

                  )}

                </div>

              );

            })}

          </div>

        )}

      </div>}



      {tab === 'food' && <div><div style={{ marginBottom: '16px' }}>{locFoodTypes.map((f, i) => <span key={i} style={{ ...S.badge, ...S.badgeFood, cursor: 'pointer' }} onClick={() => setLocFoodTypes(locFoodTypes.filter((_, idx) => idx !== i))}>{f} ✕</span>)}</div><div style={S.flex}><input style={{ ...S.input, flex: 1, marginBottom: 0 }} placeholder="New food type" value={newFood} onChange={e => setNewFood(e.target.value)} /><button style={{ ...S.btn, ...S.btnSuccess, ...S.btnSm }} onClick={() => { if (newFood && !locFoodTypes.includes(newFood)) { setLocFoodTypes([...locFoodTypes, newFood]); setNewFood(''); } }}>➕</button></div></div>}

      {tab === 'locations' && <div><div style={{ marginBottom: '16px' }}>{locLocations.map((l, i) => <span key={i} style={{ ...S.badge, ...S.badgeNeutral, cursor: 'pointer' }} onClick={() => setLocLocations(locLocations.filter((_, idx) => idx !== i))}>{l} ✕</span>)}</div><div style={S.flex}><input style={{ ...S.input, flex: 1, marginBottom: 0 }} placeholder="New location" value={newLoc} onChange={e => setNewLoc(e.target.value)} /><button style={{ ...S.btn, ...S.btnSuccess, ...S.btnSm }} onClick={() => { if (newLoc && !locLocations.includes(newLoc)) { setLocLocations([...locLocations, newLoc]); setNewLoc(''); } }}>➕</button></div></div>}

    

      {tab === 'audit' && <div>

        <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(39,201,90,0.08)', borderRadius: '6px', border: '1px solid rgba(39,201,90,0.25)' }}>

          <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>🕰️ Session Timeline & Audit Log</div>

          <div style={{ fontSize: '0.75rem', opacity: 0.75, lineHeight: 1.35 }}>

            Controls what gets written to the campaign timeline. Use <b>Standard</b> for most tables; switch to <b>Detailed</b> if you want item edits logged too.

          </div>

        </div>



        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>

          <div style={{ padding: '12px', background: 'rgba(0,0,0,0.25)', borderRadius: '6px' }}>

            <div style={{ fontWeight: 'bold' }}>Timeline logging is always enabled</div>

            <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '6px' }}>

              Campaign timelines are always recorded for audit safety. Use filters to reduce noise.

            </div>

          </div>



          <div style={{ padding: '12px', background: 'rgba(0,0,0,0.25)', borderRadius: '6px' }}>

            <label style={S.label}>Detail level</label>

            <select

              style={S.input}

              value={locSettings.auditLog?.verbosity || 'standard'}

              onChange={e => setLocSettings({ ...locSettings, auditLog: { ...(locSettings.auditLog || {}), verbosity: e.target.value } })}

            >

              <option value="minimal">Minimal (grouped)</option>

              <option value="standard">Standard</option>

              <option value="detailed">Detailed (log item edits)</option>

            </select>



            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '10px' }}>

              <input

                type="checkbox"

                checked={locSettings.auditLog?.includePlayerEdits !== false}

                onChange={e => setLocSettings({ ...locSettings, auditLog: { ...(locSettings.auditLog || {}), includePlayerEdits: e.target.checked } })}

              />

              <span>Include player sheet edits</span>

            </label>

          </div>

        </div>



        <h3 style={{ color: '#c9a227', marginBottom: '8px' }}>Filters written to the timeline</h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>

          {[

            ['combat', '⚔️ Combat'],

            ['inventory', '🎒 Inventory & gear'],

            ['buffs', '✨ Buffs / debuffs'],

            ['diseases', '☣ Diseases'],

            ['systems', '🔒 Systems'],

            ['messages', '💬 Messages'],

            ['session', '🕰️ Sessions'],

            ['other', '📌 Other'],

          ].map(([key, label]) => (

            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', background: 'rgba(0,0,0,0.18)', borderRadius: '6px', cursor: 'pointer' }}>

              <input

                type="checkbox"

                checked={(locSettings.auditLog?.categories?.[key] !== false)}

                onChange={e => setLocSettings({

                  ...locSettings,

                  auditLog: {

                    ...(locSettings.auditLog || {}),

                    categories: { ...(locSettings.auditLog?.categories || {}), [key]: e.target.checked }

                  }

                })}

              />

              <span>{label}</span>

            </label>

          ))}

        </div>



        <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '10px' }}>

          Tip: If your log feels noisy, disable <b>Inventory & gear</b> or keep detail level at <b>Standard</b>.

        </div>

      </div>}



</div>

    <div style={{ ...S.flex, marginTop: '20px', borderTop: '1px solid #5c4a1f', paddingTop: '16px' }}><button style={{ ...S.btn, ...S.btnSuccess }} onClick={save}>💾 Save</button><button style={S.btn} onClick={onClose}>Cancel</button></div>

  </div></div>;

}

const createCustomResourceDraft = () => ({

  label: '',

  value: '0',

  max: '100',

  color: '#c9a227',

  displayOnScreen: true,

  showInTooltip: true,

});



export function Settings({ char, isDM, diets, globalSettings, systems = {}, campaignHomebrew = null, travelLogEnabled = true, onClose, onUpdate, onDelete, onResetTravelLog, onPdfUpdate, journalSettings, onUpdateJournalSettings, spellLibrary = [], onSaveSpellLibrary = null }) {

  const [tab, setTab] = useState('char'), [loc, setLoc] = useState(() => syncCharacterCustomResources({ ...char, resourceTrackers: getCharacterCustomResourceTrackers(char) })), [locSet, setLocSet] = useState(syncSpellSettings(char.settings || DEFAULT_SETTINGS));

  const [charBuff, setCharBuff] = useState(''), [charDebuff, setCharDebuff] = useState('');

  const [buffNote, setBuffNote] = useState(''), [debuffNote, setDebuffNote] = useState('');

  const [buffPerm, setBuffPerm] = useState(false), [debuffPerm, setDebuffPerm] = useState(false);

  const [newAbility, setNewAbility] = useState(''), [newFeat, setNewFeat] = useState('');

  const [editSkillMisc, setEditSkillMisc] = useState(null);

  const [locJournalSettings, setLocJournalSettings] = useState(journalSettings || {});

  const [customResourceDraft, setCustomResourceDraft] = useState(createCustomResourceDraft);

  const normalizedLocSet = syncSpellSettings(locSet);

  const settingsCampaignHomebrewRules = useMemo(

    () => getEffectiveCampaignHomebrew(campaignHomebrew),

    [campaignHomebrew],

  );

  const effectiveLocMaxHp = getEffectiveMaxHp(loc);
  const settingsLegacyManaMax = loc.maxMana ?? normalizedLocSet.maxMana ?? 10;

  const settingsSpellResourceUi = settingsCampaignHomebrewRules.mana ? getSpellResourceUiModel({

    char: loc,

    systems,

    settings: normalizedLocSet,

    surface: SPELL_RESOURCE_SURFACE_ANY,

  }) : [];

  const showSettingsManaControls = settingsCampaignHomebrewRules.mana;
  const showSettingsFoodControls = settingsCampaignHomebrewRules.hungerStarvation;
  const settingsTabs = showSettingsFoodControls
    ? ['char', 'stats', 'skills', 'abilities', 'combat', 'dice', 'food', 'buffs', 'resources', 'journal', 'reset']
    : ['char', 'stats', 'skills', 'abilities', 'combat', 'dice', 'buffs', 'resources', 'journal', 'reset'];

  useEffect(() => {
    if (tab === 'food' && !showSettingsFoodControls) setTab('char');
  }, [tab, showSettingsFoodControls]);

  useEffect(() => {

    setLoc((prev) => clampCurrentHpToEffectiveMax(prev));

  }, [effectiveLocMaxHp]);

  const save = () => { 

    const nextLoc = clampCurrentHpToEffectiveMax(syncCharacterCustomResources({ ...loc, settings: normalizedLocSet }));

    onUpdate({ ...nextLoc, settings: normalizedLocSet }); 

    if (onUpdateJournalSettings && JSON.stringify(locJournalSettings) !== JSON.stringify(journalSettings)) {

      onUpdateJournalSettings(locJournalSettings);

    }

    onClose(); 

  };

  const syncLocCustomResources = useCallback((nextTrackersOrUpdater) => {

    setLoc((prev) => {

      const baseTrackers = getCharacterCustomResourceTrackers(prev);

      const nextTrackers = typeof nextTrackersOrUpdater === 'function'

        ? nextTrackersOrUpdater(baseTrackers)

        : nextTrackersOrUpdater;

      const synced = syncCharacterCustomResources(prev, nextTrackers);

      return { ...prev, resourceTrackers: synced.resourceTrackers, systemList: synced.systemList };

    });

  }, []);



  const updateLocCustomResourceBindings = useCallback((trackerId, nextBindingsOrUpdater) => {

    setLoc((prev) => {

      const syncedPrev = syncCharacterCustomResources(prev, getCharacterCustomResourceTrackers(prev));

      const nextSystemList = (syncedPrev.systemList || []).map((instance) => {

        const bridge = instance?.template?.resourceBridge;

        if (bridge?.kind !== 'dm-custom-resource' || bridge?.trackerId !== trackerId) {

          return instance;

        }

        const normalizedTemplate = normalizeSystemResourceModel(instance.template || {});

        const nextBindings = typeof nextBindingsOrUpdater === 'function'

          ? nextBindingsOrUpdater(normalizedTemplate.directSpellBindings || [], normalizedTemplate)

          : nextBindingsOrUpdater;

        return {

          ...instance,

          template: normalizeSystemResourceModel({

            ...normalizedTemplate,

            directSpellBindings: nextBindings,

          }),

        };

      });

      const nextLoc = { ...syncedPrev, systemList: nextSystemList };

      const resynced = syncCharacterCustomResources(nextLoc, getCharacterCustomResourceTrackers(nextLoc));

      return { ...nextLoc, resourceTrackers: resynced.resourceTrackers, systemList: resynced.systemList };

    });

  }, []);



  const currentCustomResources = useMemo(() => getCharacterCustomResourceTrackers(loc), [loc]);

  const managedCustomResourceSystems = useMemo(() => {

    const map = new Map();

    (loc.systemList || []).forEach((instance) => {

      const bridge = instance?.template?.resourceBridge;

      if (bridge?.kind !== 'dm-custom-resource' || !bridge?.trackerId) {

        return;

      }

      map.set(bridge.trackerId, {

        instance,

        system: normalizeSystemResourceModel(instance.template || {}),

      });

    });

    return map;

  }, [loc.systemList]);



  const addSettingsDraftEffect = (kind = 'buff') => {

    const text = String(charBuff || '').trim();

    if (!text) return;

    const isDebuff = kind === 'debuff';

    const colorId = loc._buffColorSelect || (isDebuff ? 'default-debuff' : 'default-buff');

    const sourceText = String(buffNote || '').trim() || 'Manual';



    setLoc((prev) => addLegacyEffect(prev, {

      text,

      kind,

      permanent: buffPerm,

      allowDuplicates: true,

      metadata: buildLegacyEffectMetadata({

        source: sourceText,

        colorId,

        addedAt: new Date().toISOString(),

        addedBy: 'Settings',

        noteVisible: true,

        provenance: {

          sourceType: 'manual-settings',

          kind,

        },

        extra: {

          effectType: kind,

        },

      }),

      buffSource: sourceText,

    }));

    setCharBuff('');

    setBuffNote('');

  };



  const removeSettingsDraftEffect = ({ kind = 'buff', permanent = false, index = -1, text = null } = {}) => {

    setLoc((prev) => removeLegacyEffectAt(prev, {

      kind,

      permanent,

      index,

      text,

    }));

  };



  const addBuffWithOptions = () => {

    if (!charBuff) return;

    const newSources = { ...(loc.buffSources || {}), [charBuff]: 'Manual' };

    const newNotes = { ...(loc.buffNotes || {}) };

    if (buffNote) newNotes[charBuff] = buffNote;

    if (buffPerm) setLoc({ ...loc, permanentBuffs: [...(loc.permanentBuffs || []), charBuff], buffSources: newSources, buffNotes: newNotes });

    else setLoc({ ...loc, buffList: [...(loc.buffList || []), charBuff], buffSources: newSources, buffNotes: newNotes });

    setCharBuff(''); setBuffNote('');

  };



  const addDebuffWithOptions = () => {

    if (!charDebuff) return;

    const newSources = { ...(loc.buffSources || {}), [charDebuff]: 'Manual' };

    const newNotes = { ...(loc.buffNotes || {}) };

    if (debuffNote) newNotes[charDebuff] = debuffNote;

    if (debuffPerm) setLoc({ ...loc, permanentDebuffs: [...(loc.permanentDebuffs || []), charDebuff], buffSources: newSources, buffNotes: newNotes });

    else setLoc({ ...loc, debuffList: [...(loc.debuffList || []), charDebuff], buffSources: newSources, buffNotes: newNotes });

    setCharDebuff(''); setDebuffNote('');

  };



  const toggleSkillProf = (skill) => {

    const profs = loc.skillProficiencies || [];

    const expertise = loc.skillExpertise || [];

    if (profs.includes(skill)) {

      setLoc({ ...loc, skillProficiencies: profs.filter(s => s !== skill), skillExpertise: expertise.filter(s => s !== skill) });

    } else {

      setLoc({ ...loc, skillProficiencies: [...profs, skill] });

    }

  };



  const toggleSkillExpertise = (skill) => {

    const profs = loc.skillProficiencies || [];

    const expertise = loc.skillExpertise || [];

    if (!profs.includes(skill)) return;

    if (expertise.includes(skill)) {

      setLoc({ ...loc, skillExpertise: expertise.filter(s => s !== skill) });

    } else {

      setLoc({ ...loc, skillExpertise: [...expertise, skill] });

    }

  };



  const setSkillMiscMod = (skill, value) => {

    const mods = { ...(loc.skillMiscMods || {}) };

    if (value === 0 || value === '' || isNaN(value)) {

      delete mods[skill];

    } else {

      mods[skill] = parseInt(value);

    }

    setLoc({ ...loc, skillMiscMods: mods });

  };



  const toggleSaveProf = (stat) => {

    const profs = loc.savingThrowProficiencies || [];

    if (profs.includes(stat)) setLoc({ ...loc, savingThrowProficiencies: profs.filter(s => s !== stat) });

    else setLoc({ ...loc, savingThrowProficiencies: [...profs, stat] });

  };



  return <div style={S.modal} onClick={onClose}><div style={{ ...S.modalContent, maxWidth: '800px' }} onClick={e => e.stopPropagation()}>

    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}><h2 style={{ ...S.subtitle, marginBottom: 0, border: 'none' }}>⚙️ Settings</h2><button style={{ ...S.btn, ...S.btnSm }} onClick={onClose}>✕</button></div>

    <div style={S.tabs}>{settingsTabs.map(t => <button key={t} aria-label={t === 'char' ? 'Character' : t === 'stats' ? 'Stats' : t === 'skills' ? 'Skills' : t === 'abilities' ? 'Abilities' : t === 'combat' ? 'Combat' : t === 'dice' ? 'Dice' : t === 'food' ? 'Food' : t === 'buffs' ? 'Buffs' : t === 'resources' ? 'Resources' : t === 'journal' ? 'Journal' : 'Reset'} style={{ ...S.tab, ...(tab === t ? S.tabActive : {}), padding: '8px 12px', fontSize: '0.8rem' }} onClick={() => setTab(t)}>{t === 'char' ? '👤' : t === 'stats' ? '📊' : t === 'skills' ? '🎯' : t === 'abilities' ? '⚔️' : t === 'combat' ? '🛡️' : t === 'dice' ? '🎲' : t === 'food' ? '🍖' : t === 'buffs' ? '⚡' : t === 'resources' ? '📊' : t === 'journal' ? '📓' : '🔄'}</button>)}</div>

    <div style={{ minHeight: '250px', maxHeight: '50vh', overflowY: 'auto' }}>

      {tab === 'char' && <div>

        <div style={S.grid}>

          <div><label style={S.label}>Name</label><input style={S.input} value={loc.name} onChange={e => setLoc({ ...loc, name: e.target.value })} /></div>

          <div><label style={S.label}>Race</label><input style={S.input} value={loc.race} onChange={e => setLoc({ ...loc, race: e.target.value })} /></div>

          <div><label style={S.label}>Class</label><input style={S.input} value={loc.charClass || ''} onChange={e => setLoc({ ...loc, charClass: e.target.value })} placeholder="Fighter, Wizard..." /></div>

          <div><label style={S.label}>Level</label><NumberField style={S.input} value={loc.level ?? 1} onCommit={value => setLoc({ ...loc, level: value })} emptyValue={1} min={1} max={20} /></div>

        </div>

        <div style={S.flex}><div style={{ flex: 1 }}><label style={S.label}>HP</label><NumberField style={S.input} value={loc.currentHp ?? 0} onCommit={value => setLoc({ ...loc, currentHp: value })} emptyValue={0} min={0} max={effectiveLocMaxHp} /></div><div style={{ flex: 1 }}><label style={S.label}>Max HP</label><NumberField style={S.input} value={loc.maxHp ?? 1} onCommit={value => setLoc({ ...loc, maxHp: value })} emptyValue={1} min={1} /></div></div>

        <div style={S.grid}>

          <div><label style={S.label}>Armor Class</label><NumberField style={S.input} value={loc.armorClass ?? 10} onCommit={value => setLoc({ ...loc, armorClass: value })} emptyValue={10} min={0} /></div>

          <div><label style={S.label}>Speed (ft)</label><NumberField style={S.input} value={loc.speed ?? 30} onCommit={value => setLoc({ ...loc, speed: value })} emptyValue={30} min={0} /></div>

        </div>

        <label style={S.label}>Portrait URL</label><input style={S.input} placeholder="https://..." value={loc.image || ''} onChange={e => setLoc({ ...loc, image: e.target.value })} />

        {showSettingsManaControls && <div style={{ ...S.subCard, marginTop: '16px' }}>

          <h3 style={{ color: '#4a90d9', marginBottom: '8px', fontSize: '1rem' }}>✨ Mana</h3>

          <label style={{ ...S.label, cursor: 'pointer' }}><input type="checkbox" checked={isManaSpellSystem(normalizedLocSet)} onChange={e => setLocSet(syncSpellSettings(locSet, { manaEnabled: e.target.checked }))} /> {settingsSpellResourceUi.usesSystemsAsPrimary ? 'Use Legacy Mana Fallback' : 'Enable Mana'}</label>

          {settingsSpellResourceUi.usesSystemsAsPrimary && (

            <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '8px' }}>

              Systems-backed spell resources currently drive active spell casting. These legacy mana fields remain available only as compatibility data.

            </div>

          )}

          {isManaSpellSystem(normalizedLocSet) && <div style={S.flex}>

            <div style={{ flex: 1 }}><label style={S.label}>{settingsSpellResourceUi.usesSystemsAsPrimary ? 'Legacy Current' : 'Current'}</label><NumberField style={S.input} value={loc.currentMana ?? 0} onCommit={value => setLoc({ ...loc, currentMana: value })} emptyValue={0} min={0} max={settingsLegacyManaMax} /></div>

            <div style={{ flex: 1 }}><label style={S.label}>{settingsSpellResourceUi.usesSystemsAsPrimary ? 'Legacy Max' : 'Max'}</label><NumberField style={S.input} value={settingsLegacyManaMax} onCommit={value => setLoc({ ...loc, maxMana: value })} emptyValue={normalizedLocSet.maxMana ?? 10} min={1} /></div>

          </div>}

        </div>}

        <div style={{ ...S.subCard, marginTop: '16px' }}>

          <h3 style={{ color: '#ffd700', marginBottom: '8px', fontSize: '1rem' }}>💰 Wallet</h3>

          <label style={{ ...S.label, cursor: 'pointer' }}><input type="checkbox" checked={loc.walletVisible !== false} onChange={e => setLoc({ ...loc, walletVisible: e.target.checked })} /> Show Wallet on Character Sheet</label>

        </div>

        {onPdfUpdate && (

          <div style={{ ...S.subCard, marginTop: '16px', background: 'linear-gradient(135deg, rgba(90,159,212,0.1), rgba(201,162,39,0.08))', border: '1px solid rgba(201,162,39,0.4)' }}>

            <h3 style={{ color: '#5a9fd4', marginBottom: '8px', fontSize: '1rem' }}>📄 Character Sheet Sync</h3>

            <p style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '12px' }}>Update this character's stats, spells, feats, etc. from a supported character sheet import.</p>

            <button style={{ ...S.btn, ...S.btnEdit, width: '100%' }} onClick={() => { onClose(); onPdfUpdate(); }}>

              🔄 Update from Character Sheet

            </button>

          </div>

        )}

      </div>}

      

      {tab === 'stats' && <div>

        <p style={{ fontSize: '0.85rem', opacity: 0.7, marginBottom: '12px' }}>Base ability scores (buffs/debuffs modify these)</p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>{['str', 'dex', 'con', 'int', 'wis', 'cha'].map(stat => <div key={stat}><label style={{ ...S.label, textTransform: 'uppercase' }}>{stat}</label><NumberField style={S.input} value={(loc.baseStats || DEFAULT_STATS)[stat] ?? 10} onCommit={value => setLoc({ ...loc, baseStats: { ...(loc.baseStats || DEFAULT_STATS), [stat]: value } })} emptyValue={10} min={0} /></div>)}</div>

        <h3 style={{ color: '#c9a227', marginTop: '20px', marginBottom: '8px' }}>Saving Throw Proficiencies</h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>{['str', 'dex', 'con', 'int', 'wis', 'cha'].map(stat => <label key={stat} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}><input type="checkbox" checked={(loc.savingThrowProficiencies || []).includes(stat)} onChange={() => toggleSaveProf(stat)} /><span style={{ textTransform: 'uppercase' }}>{stat}</span></label>)}</div>

      </div>}

      

      {tab === 'skills' && <div>

        <p style={{ fontSize: '0.85rem', opacity: 0.7, marginBottom: '8px' }}>● Proficient | ◆ Expertise (double prof) | Click skill name to edit misc modifier</p>

        {Object.entries(SKILLS_BY_STAT).filter(([_, skills]) => skills.length > 0).map(([stat, skills]) => (

          <div key={stat} style={{ marginBottom: '16px' }}>

            <div style={{ fontSize: '0.8rem', color: '#c9a227', textTransform: 'uppercase', marginBottom: '4px' }}>{stat}</div>

            {skills.map(skill => {

              const isProficient = (loc.skillProficiencies || []).includes(skill);

              const isExpertise = (loc.skillExpertise || []).includes(skill);

              const miscMod = (loc.skillMiscMods || {})[skill] || 0;

              return <div key={skill} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>

                <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>

                  <input type="checkbox" checked={isProficient} onChange={() => toggleSkillProf(skill)} />

                  <span style={{ color: isProficient ? '#27c95a' : '#888' }}>●</span>

                </label>

                <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', opacity: isProficient ? 1 : 0.3 }}>

                  <input type="checkbox" checked={isExpertise} disabled={!isProficient} onChange={() => toggleSkillExpertise(skill)} />

                  <span style={{ color: isExpertise ? '#ffd700' : '#888' }}>◆</span>

                </label>

                <span style={{ flex: 1, cursor: 'pointer' }} onClick={() => setEditSkillMisc(editSkillMisc === skill ? null : skill)}>{skill}</span>

                {miscMod !== 0 && <span style={{ fontSize: '0.8rem', color: miscMod > 0 ? '#27c95a' : '#c92727' }}>({miscMod > 0 ? '+' : ''}{miscMod})</span>}

                {editSkillMisc === skill && <input style={{ ...S.input, width: '60px', marginBottom: 0, padding: '4px' }} type="number" placeholder="±" value={miscMod || ''} onChange={e => setSkillMiscMod(skill, e.target.value)} />}

              </div>;

            })}

          </div>

        ))}

      </div>}

      

      {tab === 'abilities' && <div>

        <h3 style={{ color: '#6495ed', marginBottom: '8px' }}>Special Abilities</h3>

        <p style={{ fontSize: '0.8rem', opacity: 0.7, marginBottom: '8px' }}>Class features, racial abilities, etc.</p>

        <div style={{ marginBottom: '8px' }}>{(loc.abilities || []).length === 0 ? <span style={{ opacity: 0.5 }}>None</span> : loc.abilities.map((s, i) => <span key={i} style={{ ...S.badge, ...S.badgeAbility, cursor: 'pointer' }} onClick={() => setLoc({ ...loc, abilities: loc.abilities.filter((_, idx) => idx !== i) })}>{s} ✕</span>)}</div>

        <div style={{ ...S.flex, marginBottom: '16px' }}><input style={{ ...S.input, flex: 1, marginBottom: 0 }} placeholder="Darkvision, Rage..." value={newAbility} onChange={e => setNewAbility(e.target.value)} /><button style={{ ...S.btn, ...S.btnSuccess, ...S.btnSm }} onClick={() => { if (newAbility) { setLoc({ ...loc, abilities: [...(loc.abilities || []), newAbility] }); setNewAbility(''); } }}>Add</button></div>

        <h3 style={{ color: '#ffa500', marginBottom: '8px' }}>Feats & Traits</h3>

        <p style={{ fontSize: '0.8rem', opacity: 0.7, marginBottom: '8px' }}>Feats, background traits, etc.</p>

        <div style={{ marginBottom: '8px' }}>{(loc.feats || []).length === 0 ? <span style={{ opacity: 0.5 }}>None</span> : loc.feats.map((t, i) => <span key={i} style={{ ...S.badge, ...S.badgeFeat, cursor: 'pointer' }} onClick={() => setLoc({ ...loc, feats: loc.feats.filter((_, idx) => idx !== i) })}>{t} ✕</span>)}</div>

        <div style={S.flex}><input style={{ ...S.input, flex: 1, marginBottom: 0 }} placeholder="Lucky, Alert..." value={newFeat} onChange={e => setNewFeat(e.target.value)} /><button style={{ ...S.btn, ...S.btnSuccess, ...S.btnSm }} onClick={() => { if (newFeat) { setLoc({ ...loc, feats: [...(loc.feats || []), newFeat] }); setNewFeat(''); } }}>Add</button></div>

      </div>}

      

      {tab === 'food' && showSettingsFoodControls && <div>

        <div style={S.flex}><div style={{ flex: 1 }}><label style={S.label}>Starvation (days)</label><NumberField style={S.input} value={locSet.starvationThreshold ?? 7} onCommit={value => setLocSet({ ...locSet, starvationThreshold: value })} emptyValue={7} min={0} /></div><div style={{ flex: 1 }}><label style={S.label}>Hunger (days)</label><NumberField style={S.input} value={locSet.hungerThreshold ?? 3} onCommit={value => setLocSet({ ...locSet, hungerThreshold: value })} emptyValue={3} min={0} /></div></div>

        <label style={{ ...S.label, cursor: 'pointer' }}><input type="checkbox" checked={locSet.showDaysToStarvation} onChange={e => setLocSet({ ...locSet, showDaysToStarvation: e.target.checked })} /> Show days to starvation</label>

        <label style={{ ...S.label, cursor: 'pointer' }}><input type="checkbox" checked={locSet.showDaysWithoutFood} onChange={e => setLocSet({ ...locSet, showDaysWithoutFood: e.target.checked })} /> Show days without food</label>

        <label style={{ ...S.label, cursor: 'pointer' }}><input type="checkbox" checked={locSet.hideWhenWellFed} onChange={e => setLocSet({ ...locSet, hideWhenWellFed: e.target.checked })} /> Hide when well fed</label>

      </div>}

      

      {tab === 'buffs' && <div>

        {/* Quick Effect Type Buttons */}

        <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>

          <div style={{ fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '8px', color: '#c9a227' }}>⚡ Quick Add Effect Type</div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>

            {EFFECT_TYPE_PRESETS.map(preset => {

              const colorOpt = BUFF_COLOR_OPTIONS.find(c => c.id === preset.color) || BUFF_COLOR_OPTIONS[0];

              return (

                <Tooltip key={preset.id} text={preset.description}>

                  <button

                    style={{

                      ...S.btn,

                      ...S.btnSm,

                      padding: '4px 10px',

                      fontSize: '0.75rem',

                      background: colorOpt.bgColor,

                      borderColor: colorOpt.color,

                      color: colorOpt.textColor || colorOpt.color,

                    }}

                    onClick={() => {

                      setLoc({ 

                        ...loc, 

                        _buffColorSelect: preset.color,

                        _effectTypeIcon: preset.icon,

                      });

                      setCharBuff(preset.icon + ' ');

                    }}

                  >

                    {preset.svgIcon === 'biohazard' ? <BiohazardIcon size={13} color={colorOpt?.textColor || colorOpt?.color || 'currentColor'} /> : preset.svgIcon === 'plague-doctor' ? <PlagueDoctorIcon size={13} color={colorOpt?.textColor || colorOpt?.color || 'currentColor'} /> : preset.icon} {preset.name}

                  </button>

                </Tooltip>

              );

            })}

          </div>

        </div>

        

        <h3 style={{ color: '#27c95a', marginBottom: '8px' }}>Add Effect</h3>

        <input style={S.input} placeholder="Effect name (e.g., +2 Str, Oath of Silence, Fey Bargain)" value={charBuff} onChange={e => setCharBuff(e.target.value)} />

        <textarea 

          style={{ ...S.input, minHeight: '60px', resize: 'vertical', marginBottom: '8px' }} 

          placeholder="Notes / Description (e.g., Cannot speak of the ritual witnessed in the temple. Given by High Priest Alaric.)" 

          value={buffNote} 

          onChange={e => setBuffNote(e.target.value)} 

        />

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>

          <label style={{ ...S.label, cursor: 'pointer', marginBottom: 0 }}><input type="checkbox" checked={buffPerm} onChange={e => setBuffPerm(e.target.checked)} /> Permanent</label>

          <select

            style={{ ...S.select, marginBottom: 0, padding: '4px 8px', fontSize: '0.8rem', width: '180px' }}

            value={loc._buffColorSelect || 'default-buff'}

            onChange={e => setLoc({ ...loc, _buffColorSelect: e.target.value })}

          >

            {BUFF_COLOR_OPTIONS.map(opt => (

              <option key={opt.id} value={opt.id}>{opt.name}</option>

            ))}

          </select>

          {/* Color preview */}

          {(() => {

            const colorOpt = BUFF_COLOR_OPTIONS.find(c => c.id === (loc._buffColorSelect || 'default-buff'));

            return colorOpt && (

              <div style={{ 

                width: '24px', 

                height: '24px', 

                borderRadius: '4px', 

                background: colorOpt.bgColor, 

                border: `2px solid ${colorOpt.color}` 

              }} />

            );

          })()}

        </div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>

          <button style={{ ...S.btn, ...S.btnSuccess, ...S.btnSm }} onClick={() => addSettingsDraftEffect('buff')}>Add as Buff</button>

          <button style={{ ...S.btn, ...S.btnDanger, ...S.btnSm }} onClick={() => addSettingsDraftEffect('debuff')}>Add as Debuff</button>

        </div>

        

        {/* Current Effects */}

        <div style={{ marginBottom: '8px' }}><strong style={{ color: '#27c95a', fontSize: '0.85rem' }}>✨ Buffs:</strong></div>

        <div style={{ marginBottom: '12px' }}>{(loc.permanentBuffs || []).map((b, i) => {

          const data = lookupBuffData(b, getGlobalOccurrence(b, i, loc.permanentBuffs || []), loc.buffData) || {};

          const colorOpt = BUFF_COLOR_OPTIONS.find(c => c.id === data.color) || BUFF_COLOR_OPTIONS[0];

          const tooltipText = data.source ? `📍 ${data.source}${data.addedAt ? '\n📅 ' + new Date(data.addedAt).toLocaleDateString() : ''}` : b;

          return <Tooltip key={'pb'+i} text={tooltipText}><span style={{ ...S.badge, ...S.badgeBuffPerm, background: colorOpt.bgColor, borderColor: colorOpt.color, color: colorOpt.textColor || colorOpt.color, fontWeight: '500', cursor: 'pointer' }} onClick={() => removeSettingsDraftEffect({ kind: 'buff', permanent: true, index: i, text: b })}>{b} ✕</span></Tooltip>;

        })}{(loc.buffList || []).map((b, i) => {

          const data = lookupBuffData(b, getGlobalOccurrence(b, i, loc.buffList || [], loc.permanentBuffs || []), loc.buffData) || {};

          const colorOpt = BUFF_COLOR_OPTIONS.find(c => c.id === data.color) || BUFF_COLOR_OPTIONS[0];

          const tooltipText = data.source ? `📍 ${data.source}${data.addedAt ? '\n📅 ' + new Date(data.addedAt).toLocaleDateString() : ''}` : b;

          return <Tooltip key={'b'+i} text={tooltipText}><span style={{ ...S.badge, ...S.badgeBuff, background: colorOpt.bgColor, borderColor: colorOpt.color, color: colorOpt.textColor || colorOpt.color, fontWeight: '500', cursor: 'pointer' }} onClick={() => removeSettingsDraftEffect({ kind: 'buff', permanent: false, index: i, text: b })}>{b} ✕</span></Tooltip>;

        })}{(loc.permanentBuffs || []).length === 0 && (loc.buffList || []).length === 0 && <span style={{ opacity: 0.5 }}>None</span>}</div>

        

        <div style={{ marginBottom: '8px' }}><strong style={{ color: '#c92727', fontSize: '0.85rem' }}>⚠️ Debuffs:</strong></div>

        <div style={{ marginBottom: '12px' }}>{(loc.permanentDebuffs || []).map((d, i) => {

          const data = lookupBuffData(d, getGlobalOccurrence(d, i, loc.permanentDebuffs || [], loc.permanentBuffs || [], loc.buffList || []), loc.buffData) || {};

          const colorOpt = BUFF_COLOR_OPTIONS.find(c => c.id === data.color) || BUFF_COLOR_OPTIONS[1];

          const tooltipText = data.source ? `📍 ${data.source}${data.addedAt ? '\n📅 ' + new Date(data.addedAt).toLocaleDateString() : ''}` : d;

          return <Tooltip key={'pd'+i} text={tooltipText}><span style={{ ...S.badge, ...S.badgeDebuffPerm, background: colorOpt.bgColor, borderColor: colorOpt.color, color: colorOpt.textColor || colorOpt.color, fontWeight: '500', cursor: 'pointer' }} onClick={() => removeSettingsDraftEffect({ kind: 'debuff', permanent: true, index: i, text: d })}>{d} ✕</span></Tooltip>;

        })}{(loc.debuffList || []).map((d, i) => {

          const data = lookupBuffData(d, getGlobalOccurrence(d, i, loc.debuffList || [], loc.permanentBuffs || [], loc.buffList || [], loc.permanentDebuffs || []), loc.buffData) || {};

          const colorOpt = BUFF_COLOR_OPTIONS.find(c => c.id === data.color) || BUFF_COLOR_OPTIONS[1];

          const tooltipText = data.source ? `📍 ${data.source}${data.addedAt ? '\n📅 ' + new Date(data.addedAt).toLocaleDateString() : ''}` : d;

          return <Tooltip key={'d'+i} text={tooltipText}><span style={{ ...S.badge, ...S.badgeDebuff, background: colorOpt.bgColor, borderColor: colorOpt.color, color: colorOpt.textColor || colorOpt.color, fontWeight: '500', cursor: 'pointer' }} onClick={() => removeSettingsDraftEffect({ kind: 'debuff', permanent: false, index: i, text: d })}>{d} ✕</span></Tooltip>;

        })}{(loc.permanentDebuffs || []).length === 0 && (loc.debuffList || []).length === 0 && <span style={{ opacity: 0.5 }}>None</span>}</div>

        

        {/* Effect Details / Audit Log */}

        <details style={{ marginTop: '16px' }}>

          <summary style={{ cursor: 'pointer', fontSize: '0.85rem', color: '#888' }}>📋 Effect Details & History</summary>

          <div style={{ maxHeight: '250px', overflowY: 'auto', marginTop: '8px', padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>

            {Object.entries(loc.buffData || {}).map(([name, data], i) => {

              const colorOpt = BUFF_COLOR_OPTIONS.find(c => c.id === data.color);

              return (

                <div key={i} style={{ 

                  fontSize: '0.8rem', 

                  marginBottom: '8px', 

                  padding: '8px', 

                  background: 'rgba(255,255,255,0.05)', 

                  borderRadius: '6px',

                  borderLeft: `3px solid ${colorOpt?.color || '#888'}`,

                }}>

                  <div style={{ fontWeight: 'bold', color: colorOpt?.color || '#fff', marginBottom: '4px' }}>{name}</div>

                  {data.source && data.source !== 'Manual' && (

                    <div style={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', opacity: 0.9 }}>{data.source}</div>

                  )}

                  {data.addedAt && (

                    <div style={{ fontSize: '0.7rem', color: '#666', marginTop: '4px' }}>

                      Added: {new Date(data.addedAt).toLocaleString()}

                    </div>

                  )}

                </div>

              );

            })}

            {Object.keys(loc.buffData || {}).length === 0 && <span style={{ opacity: 0.5, fontSize: '0.8rem' }}>No effects recorded</span>}

          </div>

        </details>

      </div>}

      

      {tab === 'resources' && <div>

        <h3 style={{ color: '#c9a227', marginBottom: '8px' }}>📊 Resource Settings</h3>

        <p style={{ fontSize: '0.8rem', color: '#888', marginBottom: '12px' }}>

          Add tracked resources, decide where they appear on the character sheet, and optionally assign them to spell costs on the spell panel.

        </p>

        

        {/* Add from Preset */}

        <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(201,162,39,0.1)', borderRadius: '8px', border: '1px solid rgba(201,162,39,0.2)' }}>

          <label style={{ ...S.label, color: '#c9a227' }}>Quick Add from Preset:</label>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>

            {RESOURCE_TRACKER_PRESETS.filter(p => p.id !== 'custom').map(preset => (

              <button 

                key={preset.id}

                style={{ ...S.btn, ...S.btnSm, fontSize: '0.75rem' }}

                onClick={() => {

                  // For multi-field presets (like Qi Cultivation), add all fields

                  const newTrackers = preset.fields.filter(f => f.type !== 'stage').map((field, idx) => ({

                    id: `${preset.id}-${field.id || idx}-${Date.now()}`,

                    presetId: preset.id,

                    fieldId: field.id,

                    label: field.label || preset.name,

                    value: field.value || 0,

                    max: field.max || (field.type === 'counter' ? field.max : 100),

                    min: field.min || 0,

                    step: field.step || 1,

                    color: field.color || '#c9a227',

                    type: field.type || 'progress',

                    displayOnScreen: field.type === 'progress' || preset.fields.length === 1,

                    showInTooltip: true,

                  }));

                  syncLocCustomResources((trackers) => [...trackers, ...newTrackers]);

                }}

              >

                {preset.svgIcon === 'biohazard' ? <BiohazardIcon size={13} color="currentColor" /> : preset.svgIcon === 'plague-doctor' ? <PlagueDoctorIcon size={13} color="currentColor" /> : preset.icon} {preset.name}

                {preset.fields.length > 1 && <span style={{ fontSize: '0.6rem', opacity: 0.7 }}> ({preset.fields.filter(f => f.type !== 'stage').length})</span>}

              </button>

            ))}

          </div>

        </div>

        

        {/* Add Custom */}

        <details style={{ marginBottom: '16px' }}>

          <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#4a90d9' }}>Add Custom Resource</summary>

          <div style={{ padding: '12px', background: 'rgba(74,144,217,0.1)', borderRadius: '8px', marginTop: '8px' }}>

            <div style={S.grid}>

              <div>

                <label style={S.label}>Label</label>

                <input

                  style={S.input}

                  placeholder="e.g., Years of Qi"

                  value={customResourceDraft.label}

                  onChange={(e) => setCustomResourceDraft((draft) => ({ ...draft, label: e.target.value }))}

                />

              </div>

              <div>

                <label style={S.label}>Current Value</label>

                <input

                  style={S.input}

                  type="number"

                  value={customResourceDraft.value}

                  onChange={(e) => setCustomResourceDraft((draft) => ({ ...draft, value: e.target.value }))}

                />

              </div>

              <div>

                <label style={S.label}>Max Value</label>

                <input

                  style={S.input}

                  type="number"

                  value={customResourceDraft.max}

                  onChange={(e) => setCustomResourceDraft((draft) => ({ ...draft, max: e.target.value }))}

                />

              </div>

              <div>

                <label style={S.label}>Color</label>

                <select

                  style={S.select}

                  value={customResourceDraft.color}

                  onChange={(e) => setCustomResourceDraft((draft) => ({ ...draft, color: e.target.value }))}

                >

                  <option value="#c9a227">Gold</option>

                  <option value="#4a90d9">Blue</option>

                  <option value="#27c95a">Green</option>

                  <option value="#c92727">Red</option>

                  <option value="#9b59b6">Purple</option>

                  <option value="#00bcd4">Cyan</option>

                  <option value="#ff6600">Orange</option>

                </select>

              </div>

            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>

              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>

                <input

                  type="checkbox"

                  checked={customResourceDraft.displayOnScreen}

                  onChange={(e) => setCustomResourceDraft((draft) => ({ ...draft, displayOnScreen: e.target.checked }))}

                /> Show on screen

              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>

                <input

                  type="checkbox"

                  checked={customResourceDraft.showInTooltip}

                  onChange={(e) => setCustomResourceDraft((draft) => ({ ...draft, showInTooltip: e.target.checked }))}

                /> Show in tooltip

              </label>

            </div>

            <button 

              style={{ ...S.btn, ...S.btnSuccess, ...S.btnSm, marginTop: '12px' }}

              onClick={() => {

                const label = customResourceDraft.label.trim();

                if (!label) return;

                const newTracker = {

                  id: `custom-${Date.now()}`,

                  presetId: 'custom',

                  label,

                  value: parseInt(customResourceDraft.value, 10) || 0,

                  max: parseInt(customResourceDraft.max, 10) || 100,

                  min: 0,

                  step: 1,

                  color: customResourceDraft.color,

                  displayOnScreen: customResourceDraft.displayOnScreen,

                  showInTooltip: customResourceDraft.showInTooltip,

                };

                syncLocCustomResources((trackers) => [...trackers, newTracker]);

                setCustomResourceDraft(createCustomResourceDraft());

              }}

            >

              Add Resource

            </button>

          </div>

        </details>

        

        {/* Current Resources */}

        <div style={{ marginTop: '12px' }}>

          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Current Resources:</div>

          {currentCustomResources.length === 0 ? (

            <span style={{ opacity: 0.5 }}>No resources configured</span>

          ) : (

            currentCustomResources.map((tracker) => {

              const preset = RESOURCE_TRACKER_PRESETS.find(p => p.id === tracker.presetId);

              const percent = tracker.max ? (tracker.value / tracker.max) * 100 : 0;

              const managedResource = managedCustomResourceSystems.get(tracker.id) || null;

              const managedSystem = managedResource?.system || null;

              const resourceProfileId = managedSystem?.resourceProfiles?.[0]?.id || null;

              const bindings = managedSystem?.directSpellBindings || [];

              const defaultBindingCount = bindings.filter((binding) => normalizeDirectSpellBinding(binding).assignmentMode === 'all-spells').length;

              const specificBindingCount = bindings.filter((binding) => normalizeDirectSpellBinding(binding).assignmentMode === 'specific-spells').length;

              const bindingSummary = defaultBindingCount === 0 && specificBindingCount === 0

                ? 'No spell cost bindings'

                : [

                    defaultBindingCount > 0 ? `${defaultBindingCount} default` : null,

                    specificBindingCount > 0 ? `${specificBindingCount} specific` : null,

                  ].filter(Boolean).join(' + ');

              const badgeStyle = {

                display: 'inline-flex',

                alignItems: 'center',

                gap: '4px',

                padding: '2px 8px',

                borderRadius: '999px',

                background: 'rgba(255,255,255,0.06)',

                border: '1px solid rgba(255,255,255,0.08)',

                fontSize: '0.68rem',

                opacity: 0.85,

              };

              return (

                <div key={tracker.id} style={{ 

                  padding: '12px', 

                  background: 'rgba(0,0,0,0.2)', 

                  borderRadius: '8px', 

                  marginBottom: '8px',

                  border: `1px solid ${tracker.color}40`,

                }}>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '8px' }}>

                    <div style={{ flex: 1 }}>

                      <span style={{ fontWeight: 'bold', color: tracker.color, display: 'block', marginBottom: '6px' }}>

                        {preset?.icon || '📊'} {tracker.label}

                      </span>

                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>

                        <span style={badgeStyle}>{tracker.presetId === 'custom' ? 'Custom resource' : `Preset: ${preset?.name || tracker.presetId}`}</span>

                        <span style={badgeStyle}>{tracker.displayOnScreen !== false ? 'Shown on sheet' : 'Hidden from sheet'}</span>

                        <span style={badgeStyle}>{tracker.showInTooltip !== false ? 'Shown in tooltip' : 'Hidden from tooltip'}</span>

                        <span style={badgeStyle}>{bindingSummary}</span>

                      </div>

                    </div>

                    <button 

                      style={{ ...S.btn, ...S.btnDanger, ...S.btnXs }}

                      onClick={() => syncLocCustomResources((trackers) => trackers.filter((existing) => existing.id !== tracker.id))}

                    >✕</button>

                  </div>

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>

                    <QuantityStepper

                      value={tracker.value}

                      onCommit={value => syncLocCustomResources((trackers) => trackers.map((existing) =>

                        existing.id === tracker.id ? { ...existing, value } : existing

                      ))}

                      min={tracker.min ?? 0}

                      step={tracker.step || 1}

                      emptyValue={tracker.min ?? 0}

                      inputLabel={`${tracker.label} current value`}

                      buttonStyle={{ ...S.btn, ...S.btnXs }}

                      inputStyle={{ width: '64px', minWidth: '64px' }}

                    />

                    <span>/</span>

                    <CompactNumberField

                      aria-label={`${tracker.label} maximum value`}

                      value={tracker.max}

                      onCommit={value => syncLocCustomResources((trackers) => trackers.map((existing) =>

                        existing.id === tracker.id ? { ...existing, max: value } : existing

                      ))}

                      emptyValue={100}

                      min={0}

                      style={{ width: '80px', minWidth: '80px' }}

                    />

                    {/* Progress bar preview */}

                    <div style={{ flex: 1, height: '20px', background: 'rgba(0,0,0,0.3)', borderRadius: '10px', overflow: 'hidden' }}>

                      <div style={{ height: '100%', width: `${Math.min(100, percent)}%`, background: tracker.color, transition: 'width 0.3s ease' }} />

                    </div>

                  </div>

                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>

                      <input 

                        type="checkbox" 

                        checked={tracker.displayOnScreen !== false}

                        onChange={e => syncLocCustomResources((trackers) => trackers.map((existing) =>

                          existing.id === tracker.id ? { ...existing, displayOnScreen: e.target.checked } : existing

                        ))}

                      /> Show on screen

                    </label>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>

                      <input 

                        type="checkbox" 

                        checked={tracker.showInTooltip !== false}

                        onChange={e => syncLocCustomResources((trackers) => trackers.map((existing) =>

                          existing.id === tracker.id ? { ...existing, showInTooltip: e.target.checked } : existing

                        ))}

                      /> Show in tooltip

                    </label>

                  </div>

                  <details style={{ marginTop: '10px' }}>

                    <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#4a90d9' }}>Spell Cost Assignment</summary>

                    <div style={{ marginTop: '8px', padding: '10px', borderRadius: '8px', background: 'rgba(74,144,217,0.08)', border: '1px solid rgba(74,144,217,0.16)' }}>

                      <div style={{ fontSize: '0.75rem', opacity: 0.75, marginBottom: '10px' }}>

                        Choose whether this resource powers spells on the character sheet, in combat, or both. Specific spell bindings override the default all-spells assignment for this resource.

                      </div>

                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>

                        <button

                          style={{ ...S.btn, ...S.btnXs, marginBottom: 0, opacity: resourceProfileId ? 1 : 0.6 }}

                          disabled={!resourceProfileId}

                          onClick={() => updateLocCustomResourceBindings(tracker.id, (existingBindings) => [

                            ...existingBindings,

                            createDirectSpellBinding({

                              resourceProfileId,

                              castSurface: SPELL_BINDING_SURFACE_BOTH,

                            }),

                          ])}

                          title={resourceProfileId ? 'Bind one library spell to this resource' : 'Resource profile is not available yet'}

                        >

                          + Bind spell

                        </button>

                        <button

                          style={{ ...S.btn, ...S.btnXs, marginBottom: 0, opacity: resourceProfileId && defaultBindingCount === 0 ? 1 : 0.6 }}

                          disabled={!resourceProfileId || defaultBindingCount > 0}

                          onClick={() => updateLocCustomResourceBindings(tracker.id, (existingBindings) => [

                            ...existingBindings,

                            createDirectSpellBinding({

                              assignmentMode: 'all-spells',

                              spellId: null,

                              spellIds: [],

                              resourceProfileId,

                              castSurface: SPELL_BINDING_SURFACE_BOTH,

                            }),

                          ])}

                          title={defaultBindingCount > 0 ? 'Only one default binding should be active for this resource' : 'Make this the default resource for all spells on the sheet, in combat, or both'}

                        >

                          Add all-spells default

                        </button>

                      </div>

                      {defaultBindingCount > 1 && (

                        <div style={{ marginBottom: '10px', padding: '8px', borderRadius: '6px', background: 'rgba(201,39,39,0.12)', border: '1px solid rgba(201,39,39,0.2)', fontSize: '0.75rem' }}>

                          This resource currently has multiple default all-spells bindings. Resolution stays deterministic, but this should be reduced to one default binding.

                        </div>

                      )}

                      {bindings.length === 0 ? (

                        <div style={{ fontSize: '0.8rem', opacity: 0.65 }}>

                          No spell cost bindings yet. Add a specific binding or one default all-spells binding.

                        </div>

                      ) : (

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>

                          {bindings.map((binding, bindingIndex) => (

                            <DirectSpellBindingEditor

                              key={binding.id || bindingIndex}

                              binding={binding}

                              currentSystem={managedSystem || {}}

                              spellLibrary={spellLibrary}

                              onSaveSpellLibrary={onSaveSpellLibrary}

                              titleOverride={normalizeDirectSpellBinding(binding).assignmentMode === 'all-spells' ? 'Default spell cost binding' : 'Spell cost binding'}

                              lockedResourceProfileId={resourceProfileId}

                              hideResourceProfile={true}

                              disableAllSpellsDefault={defaultBindingCount > 0 && normalizeDirectSpellBinding(binding).assignmentMode !== 'all-spells'}

                              onUpdate={(patch) => updateLocCustomResourceBindings(tracker.id, (existingBindings) =>

                                existingBindings.map((existingBinding, existingIndex) => existingIndex === bindingIndex

                                  ? normalizeDirectSpellBinding({

                                      ...existingBinding,

                                      ...patch,

                                      resourceProfileId,

                                      castSurface: patch.castSurface || existingBinding.castSurface || SPELL_BINDING_SURFACE_SHEET,

                                    })

                                  : existingBinding

                                )

                              )}

                              onDelete={() => updateLocCustomResourceBindings(tracker.id, (existingBindings) =>

                                existingBindings.filter((_, existingIndex) => existingIndex !== bindingIndex)

                              )}

                            />

                          ))}

                        </div>

                      )}

                    </div>

                  </details>

                </div>

              );

            })

          )}

        </div>

      </div>}

      

      {tab === 'dice' && <div>

        <h3 style={{ color: '#9b59b6', marginBottom: '12px' }}>🎲 Roll Settings</h3>

        <div style={{ padding: '10px', background: 'rgba(155,89,182,0.1)', borderRadius: '6px', border: '1px solid rgba(155,89,182,0.3)' }}>

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.85rem' }}>

            <input 

              type="checkbox" 

              checked={locSet.instantAdvantageRoll === true} 

              onChange={e => setLocSet({ ...locSet, instantAdvantageRoll: e.target.checked })}

            />

            <span style={{ fontWeight: 'bold', color: '#9b59b6' }}>Instant Advantage/Disadvantage Rolls</span>

          </label>

          <div style={{ fontSize: '0.72rem', opacity: 0.7, marginTop: '4px', marginLeft: '24px' }}>

            Both dice roll at once. Uncheck to roll one at a time with a "Roll Again" button for the second die.

          </div>

        </div>

      </div>}



      {tab === 'combat' && <div>

        <h3 style={{ color: '#4a90d9', marginBottom: '8px' }}>🛡️ Damage Resistances</h3>

        <p style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '8px' }}>Type a damage type name and press Enter to add. Then set % reduction.</p>

        <ResistanceEditor 

          label="" 

          icon="" 

          items={(loc.resistances || []).map(r => typeof r === 'string' ? { type: r, percent: 50 } : r)} 

          onChange={(val) => setLoc({ ...loc, resistances: val })}

        />

        

        <h3 style={{ color: '#c92727', marginBottom: '8px', marginTop: '16px' }}>⚡ Damage Vulnerabilities</h3>

        <p style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '8px' }}>Type a damage type name and press Enter to add. Then set % extra damage.</p>

        <ResistanceEditor 

          label="" 

          icon="" 

          items={(loc.vulnerabilities || []).map(v => typeof v === 'string' ? { type: v, percent: 50 } : v)} 

          onChange={(val) => setLoc({ ...loc, vulnerabilities: val })}

        />

        

        <h3 style={{ color: '#888', marginBottom: '8px', marginTop: '16px' }}>🚫 Damage Immunities</h3>

        <p style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '8px' }}>Damage types you're completely immune to.</p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>

          {(loc.immunities || []).map((imm, i) => (

            <span key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', background: 'rgba(0,0,0,0.3)', borderRadius: '4px', fontSize: '0.8rem' }}>

              {DAMAGE_TYPES[imm]?.icon || '🚫'} {imm}

              <button onClick={() => setLoc({ ...loc, immunities: loc.immunities.filter((_, idx) => idx !== i) })} style={{ background: 'none', border: 'none', color: '#c92727', cursor: 'pointer', fontSize: '0.75rem' }}>✕</button>

            </span>

          ))}

          {(loc.immunities || []).length === 0 && <span style={{ opacity: 0.5, fontSize: '0.85rem' }}>None</span>}

        </div>

        <input 

          style={{ ...S.input, marginBottom: '8px' }}

          placeholder="Type immunity name, press Enter"

          onKeyDown={(e) => {

            if (e.key === 'Enter' && e.target.value.trim()) {

              const typeName = e.target.value.trim().toLowerCase();

              if (!loc.immunities?.includes(typeName)) {

                setLoc({ ...loc, immunities: [...(loc.immunities || []), typeName] });

              }

              e.target.value = '';

            }

          }}

        />

        

        <h3 style={{ color: '#ff00ff', marginBottom: '8px', marginTop: '16px' }}>🌀 Raid-Wide Resistance</h3>

        <p style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '8px' }}>Percentage damage reduction against raid-wide/AoE attacks.</p>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

          <input 

            type="number" 

            value={loc.raidwideResist || 0}

            onChange={(e) => setLoc({ ...loc, raidwideResist: Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) })}

            style={{ ...S.input, width: '80px', marginBottom: 0 }}

            min="0"

            max="100"

          />

          <span style={{ color: '#888' }}>%</span>

        </div>

      </div>}

      

      {tab === 'journal' && <div>

        <h3 style={{ color: S.subtitle?.color || '#c9a227', marginBottom: '12px', fontSize: '1rem' }}>📓 Quick Journal Settings</h3>

        

        {/* Panel Size */}

        <div style={{ ...S.subCard, marginBottom: '12px' }}>

          <label style={{ ...S.label, marginBottom: '6px' }}>Panel Width</label>

          <div style={{ display: 'flex', gap: '6px' }}>

            {[{ id: 'compact', label: '🔹 Compact' }, { id: 'normal', label: '📐 Normal' }, { id: 'wide', label: '📏 Wide' }, { id: 'full', label: '📺 Full' }].map(opt => (

              <button key={opt.id}

                onClick={() => setLocJournalSettings({ ...locJournalSettings, quickJournalWidth: opt.id })}

                style={{

                  ...S.btn, ...S.btnXs, flex: 1,

                  background: (locJournalSettings.quickJournalWidth || 'normal') === opt.id ? (S.tabActive?.background || 'rgba(201,162,39,0.3)') : 'transparent',

                  color: (locJournalSettings.quickJournalWidth || 'normal') === opt.id ? '#ffd700' : '#888',

                  border: `1px solid ${(locJournalSettings.quickJournalWidth || 'normal') === opt.id ? 'rgba(201,162,39,0.5)' : 'rgba(255,255,255,0.1)'}`,

                }}

              >{opt.label}</button>

            ))}

          </div>

        </div>



        {/* Font Scale */}

        <div style={{ ...S.subCard, marginBottom: '12px' }}>

          <label style={{ ...S.label, marginBottom: '6px' }}>Font Size</label>

          <div style={{ display: 'flex', gap: '6px' }}>

            {[{ id: 'normal', label: 'Normal' }, { id: 'large', label: 'Large' }, { id: 'xlarge', label: 'X-Large' }].map(opt => (

              <button key={opt.id}

                onClick={() => setLocJournalSettings({ ...locJournalSettings, quickJournalScale: opt.id })}

                style={{

                  ...S.btn, ...S.btnXs, flex: 1,

                  background: (locJournalSettings.quickJournalScale || 'normal') === opt.id ? (S.tabActive?.background || 'rgba(201,162,39,0.3)') : 'transparent',

                  color: (locJournalSettings.quickJournalScale || 'normal') === opt.id ? '#ffd700' : '#888',

                  border: `1px solid ${(locJournalSettings.quickJournalScale || 'normal') === opt.id ? 'rgba(201,162,39,0.5)' : 'rgba(255,255,255,0.1)'}`,

                }}

              >{opt.label}</button>

            ))}

          </div>

        </div>



        {/* Tag Delimiter Customization */}

        <div style={{ ...S.subCard, marginBottom: '12px' }}>

          <label style={{ ...S.label, marginBottom: '4px' }}>Tag Visibility Rules</label>

          <p style={{ fontSize: '0.75rem', opacity: 0.6, marginBottom: '8px' }}>

            Configure which prefix/suffix combinations create visible vs hidden journal entries.

          </p>

          {[

            { id: 'hash_at', display: '#X@', desc: 'e.g. #N@Guard Captain', default: 'visible' },

            { id: 'at_at', display: '@X@', desc: 'e.g. @N@Guard Captain', default: 'visible' },

            { id: 'hash_hash', display: '#X#', desc: 'e.g. #N#Guard Captain', default: 'hidden' },

          ].map(rule => {

            const customRules = locJournalSettings.quickAdd?.tagVisibilityRules || {};

            const current = customRules[rule.id] || rule.default;

            return (

              <div key={rule.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', marginBottom: '4px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px' }}>

                <div>

                  <span style={{ fontFamily: 'monospace', color: '#ffd700', fontSize: '0.85rem', fontWeight: 'bold' }}>{rule.display}</span>

                  <span style={{ fontSize: '0.7rem', opacity: 0.5, marginLeft: '8px' }}>{rule.desc}</span>

                </div>

                <div style={{ display: 'flex', gap: '4px' }}>

                  {['visible', 'hidden'].map(v => (

                    <button key={v}

                      onClick={() => {

                        const qa = { ...(locJournalSettings.quickAdd || {}), tagVisibilityRules: { ...customRules, [rule.id]: v } };

                        setLocJournalSettings({ ...locJournalSettings, quickAdd: qa });

                      }}

                      style={{

                        ...S.btn, ...S.btnXs, marginBottom: 0, marginRight: 0,

                        background: current === v ? (v === 'visible' ? 'rgba(39,201,90,0.2)' : 'rgba(201,39,39,0.2)') : 'transparent',

                        color: current === v ? (v === 'visible' ? '#27c95a' : '#c92727') : '#666',

                        border: `1px solid ${current === v ? (v === 'visible' ? 'rgba(39,201,90,0.4)' : 'rgba(201,39,39,0.4)') : 'rgba(255,255,255,0.1)'}`,

                      }}

                    >{v === 'visible' ? '👁 Visible' : '🔒 Hidden'}</button>

                  ))}

                </div>

              </div>

            );

          })}

        </div>



        {/* Text Delimiter */}

        <div style={{ ...S.subCard, marginBottom: '12px' }}>

          <label style={{ ...S.label, marginBottom: '4px' }}>Text Delimiter</label>

          <p style={{ fontSize: '0.75rem', opacity: 0.6, marginBottom: '6px' }}>Separator between entry name and description text.</p>

          <div style={{ display: 'flex', gap: '6px' }}>

            {[{ id: ' - ', label: 'Dash ( - )' }, { id: ' | ', label: 'Pipe ( | )' }, { id: ': ', label: 'Colon ( : )' }].map(opt => {

              const current = locJournalSettings.quickAdd?.delimiter || ' - ';

              return (

                <button key={opt.id}

                  onClick={() => setLocJournalSettings({ ...locJournalSettings, quickAdd: { ...(locJournalSettings.quickAdd || {}), delimiter: opt.id } })}

                  style={{

                    ...S.btn, ...S.btnXs, flex: 1,

                    background: current === opt.id ? (S.tabActive?.background || 'rgba(201,162,39,0.3)') : 'transparent',

                    color: current === opt.id ? '#ffd700' : '#888',

                    border: `1px solid ${current === opt.id ? 'rgba(201,162,39,0.5)' : 'rgba(255,255,255,0.1)'}`,

                  }}

                >{opt.label}</button>

              );

            })}

          </div>

        </div>



        {/* End Delimiter */}

        <div style={{ ...S.subCard, marginBottom: '12px' }}>

          <label style={{ ...S.label, marginBottom: '4px' }}>Name End Delimiter</label>

          <p style={{ fontSize: '0.75rem', opacity: 0.6, marginBottom: '6px' }}>Character that marks the end of a name and activates paragraph scope. Both <code style={{ color: '#ffd700' }}>'</code> and <code style={{ color: '#ffd700' }}>`</code> are always supported.</p>

          <div style={{ display: 'flex', gap: '6px' }}>

            {[{ id: 'tick', label: "' (tick)" }, { id: 'dash', label: '- (dash)' }].map(opt => {

              const current = locJournalSettings.quickAdd?.tabDelimiter || 'tick';

              return (

                <button key={opt.id}

                  onClick={() => setLocJournalSettings({ ...locJournalSettings, quickAdd: { ...(locJournalSettings.quickAdd || {}), tabDelimiter: opt.id } })}

                  style={{

                    ...S.btn, ...S.btnXs, flex: 1,

                    background: current === opt.id ? (S.tabActive?.background || 'rgba(201,162,39,0.3)') : 'transparent',

                    color: current === opt.id ? '#ffd700' : '#888',

                    border: `1px solid ${current === opt.id ? 'rgba(201,162,39,0.5)' : 'rgba(255,255,255,0.1)'}`,

                  }}

                >{opt.label}</button>

              );

            })}

          </div>

        </div>



        {/* Default Scope Mode */}

        <div style={{ ...S.subCard, marginBottom: '12px' }}>

          <label style={{ ...S.label, marginBottom: '4px' }}>Default Text Scope</label>

          <p style={{ fontSize: '0.75rem', opacity: 0.6, marginBottom: '6px' }}>Which text gets attached to an entry by default.</p>

          <div style={{ display: 'flex', gap: '6px' }}>

            {[{ id: 'after', label: 'After tag', desc: 'Text after the tag name' }, { id: 'all', label: 'All text', desc: 'Entire paragraph' }, { id: 'before', label: 'Before tag', desc: 'Text before the tag' }].map(opt => {

              const current = locJournalSettings.quickAdd?.scopeMode || 'after';

              return (

                <button key={opt.id}

                  onClick={() => setLocJournalSettings({ ...locJournalSettings, quickAdd: { ...(locJournalSettings.quickAdd || {}), scopeMode: opt.id } })}

                  title={opt.desc}

                  style={{

                    ...S.btn, ...S.btnXs, flex: 1,

                    background: current === opt.id ? (S.tabActive?.background || 'rgba(201,162,39,0.3)') : 'transparent',

                    color: current === opt.id ? '#ffd700' : '#888',

                    border: `1px solid ${current === opt.id ? 'rgba(201,162,39,0.5)' : 'rgba(255,255,255,0.1)'}`,

                  }}

                >{opt.label}</button>

              );

            })}

          </div>

        </div>

      </div>}



      {tab === 'reset' && <div>

        {travelLogEnabled && typeof onResetTravelLog === 'function' && <div style={{ padding: '16px', background: 'rgba(201,162,39,0.1)', borderRadius: '8px', border: '1px solid #c9a227', marginBottom: '16px' }}>

          <h3 style={{ color: '#c9a227', marginBottom: '8px' }}>🔄 Reset Travel Log</h3>

          <p style={{ fontSize: '0.85rem', marginBottom: '12px' }}>Reset to Day 1, clear hunger/starvation.</p>

          <button style={{ ...S.btn, ...S.btnDanger }} onClick={() => { if (window.window.confirm('Reset travel log?')) { onResetTravelLog(); onClose(); } }}>Reset Travel</button>

        </div>}

        <div style={{ padding: '16px', background: 'rgba(201,39,39,0.1)', borderRadius: '8px', border: '1px solid #c92727', marginBottom: '16px' }}>

          <h3 style={{ color: '#c92727', marginBottom: '8px' }}>⚠️ Full Reset</h3>

          <p style={{ fontSize: '0.85rem', marginBottom: '12px' }}>Reset ALL stats. Keeps name/race/pw.</p>

          <button style={{ ...S.btn, ...S.btnDanger }} onClick={() => { if (window.window.confirm('Full reset?')) { const r = createDefaultCharacter(loc.name, loc.race, loc.maxHp, loc.password); r.id = loc.id; setLoc(r); setLocSet(syncSpellSettings(r.settings)); } }}>Full Reset</button>

        </div>

        <button style={{ ...S.btn, ...S.btnDanger }} onClick={() => { if (window.window.confirm('DELETE?') && window.window.confirm('Sure?')) onDelete(); }}>🗑️ Delete</button>

      </div>}

    </div>

    <div style={{ ...S.flex, marginTop: '20px', borderTop: '1px solid #5c4a1f', paddingTop: '16px' }}><button style={{ ...S.btn, ...S.btnSuccess }} onClick={save}>💾 Save</button><button style={S.btn} onClick={onClose}>Cancel</button></div>

  </div></div>;

}





// ===============================

// Session Mode + Timeline UI (DM)

// ===============================



function SessionModePanel({ audit, chars, onStartSession, onEndSession, onOpenTimeline, onSeedDemo, onClearDemo }) {

  const [sessionName, setSessionName] = useState('');

  const [notes, setNotes] = useState('');

  const [busy, setBusy] = useState(false);



  const activeId = audit?.activeSessionId || null;

  const activeSession = activeId ? (audit?.sessions?.[activeId] || null) : null;



  const sessionsCount = Object.keys(audit?.sessions || {}).length;



  const demoSessions = useMemo(() => Object.values(audit?.sessions || {}).filter(s => s && s.isDemo), [audit]);

  const hasDemo = demoSessions.length > 0;





  // Default name suggestion

  useEffect(() => {

    if (!activeId) {

      const nextName = `Session ${sessionsCount + 1}`;

      if (!sessionName) setSessionName(nextName);

      setNotes('');

    } else {

      setSessionName(activeSession?.name || '');

      setNotes(activeSession?.notes || '');

    }

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [activeId]);



  const events = Object.values(audit?.events || {}).filter(Boolean);

  const currentSessionEvents = activeId ? events.filter(e => e.sessionId === activeId) : [];

  const counts = currentSessionEvents.reduce((acc, e) => {

    const k = e.category || 'other';

    acc[k] = (acc[k] || 0) + 1;

    return acc;

  }, {});



  const fmt = (ts) => {

    try { return new Date(ts).toLocaleString(); } catch (e) { return ''; }

  };



  const statBadge = (label, value) => (

    <span style={{ ...S.badge, ...S.badgeNeutral, padding: '3px 8px', fontSize: '0.75rem' }}>

      {label}: <b style={{ color: '#fff' }}>{value || 0}</b>

    </span>

  );



  return (

    <div style={{

      maxWidth: '900px',

      margin: '0 auto 16px',

      padding: '14px',

      background: activeId ? 'linear-gradient(135deg, rgba(39,201,90,0.12) 0%, rgba(90,159,212,0.08) 100%)' : 'linear-gradient(135deg, rgba(201,162,39,0.10) 0%, rgba(90,159,212,0.06) 100%)',

      border: `1px solid ${activeId ? 'rgba(39,201,90,0.55)' : 'rgba(201,162,39,0.45)'}`,

      borderRadius: '12px',

    }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>

        <div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>

            <h3 style={{ margin: 0, color: activeId ? '#27c95a' : '#c9a227' }}>

              {activeId ? '🎯 Session Mode: Active' : '🕯️ Session Mode: Inactive'}

            </h3>

            {activeId && <span style={{ ...S.badge, ...S.badgeBuff }}>LIVE</span>}

          </div>

          <div style={{ fontSize: '0.85rem', opacity: 0.85, marginTop: '4px' }}>

            {activeId

              ? <>Tracking changes for <b>{Object.keys(chars || {}).length}</b> characters since <b>{fmt(activeSession?.startedAt)}</b>.</>

              : <>Start a session to tag everything you do (combat, loot, buffs, diseases, cinematics) and get a clean “since last session” recap.</>

            }

          </div>

        </div>



        <div style={{ display: 'flex', gap: '8px' }}>

          <button style={{ ...S.btn, ...S.btnEdit }} onClick={() => onOpenTimeline?.()}>🕰️ Timeline</button>

        </div>

      </div>



      <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>

        <div style={{ ...S.subCard, margin: 0 }}>

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center' }}>

            <div style={{ fontWeight: 'bold', color: '#ddd' }}>{activeId ? 'Active Session' : 'Start a Session'}</div>

            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>

              {activeId && statBadge('Events', currentSessionEvents.length)}

              {activeId && statBadge('Combat', counts.combat)}

              {activeId && statBadge('Inventory', counts.inventory)}

              {activeId && statBadge('Buffs', counts.buffs)}

              {activeId && statBadge('Diseases', counts.diseases)}

              {activeId && statBadge('Messages', counts.messages)}

            </div>

          </div>



          <div style={{ marginTop: '10px' }}>

            <label style={S.label}>Session name</label>

            <input

              style={S.input}

              value={sessionName}

              onChange={e => setSessionName(e.target.value)}

              placeholder={`Session ${sessionsCount + 1}`}

              disabled={!!activeId}

            />

            <label style={S.label}>Notes (optional)</label>

            <textarea

              style={{ ...S.input, marginBottom: 0, minHeight: '70px', resize: 'vertical' }}

              value={notes}

              onChange={e => setNotes(e.target.value)}

              placeholder="Highlights, hooks, unresolved threads…"

            />

          </div>



          <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>

            {!activeId ? (

              <button

                style={{ ...S.btn, ...S.btnSuccess, flex: 1 }}

                disabled={busy}

                onClick={async () => {

                  setBusy(true);

                  try { await onStartSession({ name: sessionName, notes }); }

                  finally { setBusy(false); }

                }}

              >

                ▶️ Start Session

              </button>

            ) : (

              <button

                style={{ ...S.btn, ...S.btnDanger, flex: 1 }}

                disabled={busy}

                onClick={async () => {

                  if (!window.confirm('End the active session? A snapshot will be saved for diff view.')) return;

                  setBusy(true);

                  try { await onEndSession({ notes }); }

                  finally { setBusy(false); }

                }}

              >

                ⏹️ End Session

              </button>

            )}

          </div>



          {activeId && (

            <div style={{ marginTop: '8px', fontSize: '0.8rem', opacity: 0.8 }}>

              Tip: Use the Timeline search to answer things like <i>“When did they find the artifact?”</i> or <i>“Why is Dex -1?”</i>

            </div>

          )}

        </div>



        <div style={{ ...S.subCard, margin: 0 }}>

          <div style={{ fontWeight: 'bold', color: '#ddd', marginBottom: '8px' }}>Quick recap</div>

          {!activeId ? (

            <div style={{ fontSize: '0.9rem', opacity: 0.85 }}>

              Start a session to build an automatic recap and character diffs at session boundaries.

              <div style={{ marginTop: '8px', fontSize: '0.8rem', opacity: 0.75 }}>

                Even without a session, Timeline still logs major actions (loot, buffs, diseases, combat, messages).

              </div>



              <div style={{ marginTop: '12px', padding: '10px', border: '1px dashed rgba(201,162,39,0.45)', borderRadius: '10px', background: 'rgba(0,0,0,0.18)' }}>

                <div style={{ fontWeight: 700, marginBottom: '6px', color: '#ddd' }}>🧪 Demo Timeline</div>

                <div style={{ fontSize: '0.85rem', opacity: 0.85 }}>

                  Generate a demo session with sample events and snapshots so you can explore filters + diff view without running a real session. This does not modify character sheets.

                </div>

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>

                  <button

                    style={{ ...S.btn, ...S.btnSm, ...S.btnEdit }}

                    disabled={busy}

                    onClick={async () => {

                      setBusy(true);

                      try {

                        const sid = await (onSeedDemo ? onSeedDemo() : Promise.resolve(null));

                        if (sid) onOpenTimeline?.({ range: `session:${sid}` });

                        else onOpenTimeline?.();

                      } finally {

                        setBusy(false);

                      }

                    }}

                  >

                    🧪 Generate Demo

                  </button>



                  {hasDemo && (

                    <button

                      style={{ ...S.btn, ...S.btnSm, ...S.btnDanger }}

                      disabled={busy}

                      onClick={async () => {

                        if (!window.confirm('Clear demo timeline data?')) return;

                        setBusy(true);

                        try { await (onClearDemo ? onClearDemo() : Promise.resolve()); }

                        finally { setBusy(false); }

                      }}

                    >

                      🧹 Clear Demo

                    </button>

                  )}

                </div>

              </div>

            </div>

          ) : (

            <div>

              <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '8px' }}>

                So far in <b>{activeSession?.name || 'this session'}</b>:

              </div>

              <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '0.9rem', opacity: 0.9 }}>

                <li><b>{counts.inventory || 0}</b> inventory / loot changes</li>

                <li><b>{counts.buffs || 0}</b> buff/debuff changes</li>

                <li><b>{counts.diseases || 0}</b> disease changes</li>

                <li><b>{counts.messages || 0}</b> DM messages / cinematics</li>

                <li><b>{counts.combat || 0}</b> combat events</li>

              </ul>

            </div>

          )}

        </div>

      </div>

    </div>

  );

}



export function AuditTimelineView({ audit, chars, diseases, onBack, onRevert, initialRange }) {

  const [tab, setTab] = useState('timeline');

  const [category, setCategory] = useState('all');

  const [range, setRange] = useState(initialRange || 'since-last');

  const [viewMode, setViewMode] = useState('list'); // 'list' | 'horizontal' | 'campaign' | 'summary'

  const [hoveredEvent, setHoveredEvent] = useState(null);



  useEffect(() => {

    if (initialRange && initialRange !== range) {

      setRange(initialRange);

    }

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [initialRange]);



  const [search, setSearch] = useState('');

  const [selectedEventId, setSelectedEventId] = useState(null);



  const sessions = useMemo(() => {

    const arr = Object.values(audit?.sessions || {}).filter(Boolean);

    arr.sort((a, b) => (a.startedAt || 0) - (b.startedAt || 0));

    return arr;

  }, [audit]);



  const lastEnded = useMemo(() => {

    const ended = sessions.filter(s => s.endedAt && !s.isDemo);

    ended.sort((a, b) => (b.endedAt || 0) - (a.endedAt || 0));

    return ended[0] || null;

  }, [sessions]);



  const activeId = audit?.activeSessionId || null;

  const activeSession = activeId ? (audit?.sessions?.[activeId] || null) : null;



  const events = useMemo(() => {

    const arr = Object.values(audit?.events || {}).filter(Boolean);

    arr.sort((a, b) => (b.ts || 0) - (a.ts || 0));

    return arr;

  }, [audit]);



  const selectedEvent = useMemo(() => {

    if (!selectedEventId) return null;

    return (audit?.events || {})[selectedEventId] || null;

  }, [selectedEventId, audit]);



  const fmt = (ts) => {

    try { return new Date(ts).toLocaleString(); } catch (e) { return ''; }

  };



  const categoryIcon = (cat) => {

    if (cat === 'combat') return '⚔️';

    if (cat === 'inventory') return '🎒';

    if (cat === 'buffs') return '✨';

    if (cat === 'diseases') return '☣';

    if (cat === 'systems') return '🔒';

    if (cat === 'messages') return '💬';

    if (cat === 'session') return '🕰️';

    if (cat === 'other') return '📌';

    return '🗂️';

  };



  const rangeOptions = useMemo(() => {

    const opts = [{ value: 'all', label: 'All history' }];

    if (activeId) opts.unshift({ value: 'current', label: `Current session (${activeSession?.name || 'active'})` });

    if (lastEnded?.endedAt) opts.unshift({ value: 'since-last', label: `Since last session end (${lastEnded.name || 'last'})` });

    sessions.slice().reverse().forEach(s => {

      opts.push({ value: `session:${s.id}`, label: `Only: ${s.isDemo ? '🧪 ' : ''}${s.name || 'Session'}${s.endedAt ? '' : ' (active)'}` });

    });

    return opts;

  }, [sessions, activeId, lastEnded, activeSession]);



  const sinceTs = useMemo(() => {

    if (range === 'all') return 0;

    if (range === 'current') return activeSession?.startedAt || 0;

    if (range === 'since-last') return lastEnded?.endedAt || 0;

    if (range.startsWith('session:')) {

      const sid = range.split(':')[1];

      const s = (audit?.sessions || {})[sid];

      return s?.startedAt || 0;

    }

    return 0;

  }, [range, activeSession, lastEnded, audit]);



  const filtered = useMemo(() => {

    const q = (search || '').trim().toLowerCase();

    const inSelectedSession = (ev) => {

      if (range.startsWith('session:')) {

        const sid = range.split(':')[1];

        return ev.sessionId === sid || ev.action?.startsWith('session.') && ev.sessionId === sid;

      }

      return true;

    };



    return events.filter(ev => {

      if (!ev) return false;

      if (range !== 'all' && (ev.ts || 0) < sinceTs) return false;

      if (!inSelectedSession(ev)) return false;

      if (category !== 'all' && (ev.category || 'other') !== category) return false;

      if (!q) return true;



      const parts = [

        ev.summary,

        ev.action,

        ev.category,

        ev.target?.charName,

        ev.details?.charName,

        ev.details?.contentPreview,

      ].filter(Boolean).join(' ').toLowerCase();

      return parts.includes(q);

    });

  }, [events, category, search, range, sinceTs]);



  const recapCounts = useMemo(() => {

    const acc = {};

    filtered.forEach(ev => {

      const k = ev.category || 'other';

      acc[k] = (acc[k] || 0) + 1;

    });

    return acc;

  }, [filtered]);



  // Session summary data for horizontal and summary views

  const sessionSummaries = useMemo(() => {

    const summaries = {};

    sessions.forEach(session => {

      const sessionEvents = events.filter(ev => ev.sessionId === session.id);

      const counts = { combat: 0, inventory: 0, buffs: 0, diseases: 0, messages: 0, systems: 0, other: 0 };

      sessionEvents.forEach(ev => {

        const cat = ev.category || 'other';

        counts[cat] = (counts[cat] || 0) + 1;

      });

      summaries[session.id] = {

        ...session,

        eventCount: sessionEvents.length,

        counts,

        events: sessionEvents,

        duration: session.endedAt ? (session.endedAt - session.startedAt) : null,

      };

    });

    return summaries;

  }, [sessions, events]);



  // Events grouped by session for horizontal view

  const eventsBySession = useMemo(() => {

    const grouped = {};

    sessions.forEach(s => { grouped[s.id] = []; });

    grouped['no-session'] = [];

    

    filtered.forEach(ev => {

      if (ev.sessionId && grouped[ev.sessionId]) {

        grouped[ev.sessionId].push(ev);

      } else {

        grouped['no-session'].push(ev);

      }

    });

    return grouped;

  }, [filtered, sessions]);



  // --- Diff tab helpers ---

  const [diffCharId, setDiffCharId] = useState('');

  const [fromPoint, setFromPoint] = useState('current');

  const [toPoint, setToPoint] = useState('current');



  useEffect(() => {

    const ids = Object.keys(chars || {});

    if (!diffCharId && ids.length) setDiffCharId(ids[0]);

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [chars]);



  const snapshotOptions = useMemo(() => {

    if (!diffCharId) return [{ value: 'current', label: 'Current (live)' }];

    const opts = [{ value: 'current', label: 'Current (live)' }];

    sessions.forEach(s => {

      const snap = audit?.snapshots?.[s.id]?.[diffCharId];

      if (snap?.start?.snapshot) opts.push({ value: `start:${s.id}`, label: `${s.name || 'Session'} — Start` });

      if (snap?.end?.snapshot) opts.push({ value: `end:${s.id}`, label: `${s.name || 'Session'} — End` });

    });

    return opts;

  }, [diffCharId, sessions, audit]);



  const makeSnapshotFromChar = (ch) => {

    if (!ch) return null;

    return {

      id: ch.id,

      name: ch.name,

      currentHp: ch.currentHp,

      maxHp: ch.maxHp,

      tempHp: ch.tempHp || 0,

      stats: ch.stats || ch.baseStats || {},

      baseStats: ch.baseStats || {},

      buffs: (ch.buffList || []),

      debuffs: (ch.debuffList || []),

      permanentBuffs: (ch.permanentBuffs || []),

      permanentDebuffs: (ch.permanentDebuffs || []),

      diseases: (ch.diseaseList || []),

      diseaseImmunityList: (ch.diseaseImmunityList || []),

      inventory: (ch.inventory || []),

      wallet: ch.wallet || {},

    };

  };



  const getSnapshot = (point) => {

    if (!diffCharId) return null;

    if (point === 'current') return { ts: Date.now(), snapshot: makeSnapshotFromChar(chars?.[diffCharId]) };

    const [kind, sid] = point.split(':');

    const snap = audit?.snapshots?.[sid]?.[diffCharId]?.[kind];

    return snap || null;

  };



  const diffStringSets = (a, b) => {

    const A = new Set((a || []).filter(Boolean));

    const B = new Set((b || []).filter(Boolean));

    return { added: [...B].filter(x => !A.has(x)), removed: [...A].filter(x => !B.has(x)) };

  };



  const compactItem = (it) => {

    if (!it) return null;

    const qty = (it.qty ?? it.quantity ?? it.count ?? 1);

    return { id: it.id || it.itemId || null, name: it.name || it.title || it.itemName || 'Unknown', qty: parseInt(qty) || 1 };

  };



  const invIndex = (arr) => {

    const m = new Map();

    (arr || []).forEach(raw => {

      const it = compactItem(raw);

      if (!it) return;

      const key = it.id || it.name;

      const prev = m.get(key);

      if (!prev) m.set(key, { ...it });

      else m.set(key, { ...prev, qty: (prev.qty || 1) + (it.qty || 1) });

    });

    return m;

  };



  const diffInventory = (beforeInv, afterInv) => {

    const A = invIndex(beforeInv);

    const B = invIndex(afterInv);

    const added = [];

    const removed = [];

    const changed = [];

    const keys = new Set([...A.keys(), ...B.keys()]);

    keys.forEach(k => {

      const a = A.get(k);

      const b = B.get(k);

      if (!a && b) added.push(b);

      else if (a && !b) removed.push(a);

      else if (a && b && (a.qty || 1) !== (b.qty || 1)) changed.push({ before: a, after: b });

    });

    return { added, removed, changed };

  };



  const diffDiseases = (beforeList, afterList) => {

    const A = new Map((beforeList || []).map(d => [d.diseaseId, d]));

    const B = new Map((afterList || []).map(d => [d.diseaseId, d]));

    const added = [];

    const removed = [];

    const keys = new Set([...A.keys(), ...B.keys()]);

    keys.forEach(k => {

      const a = A.get(k);

      const b = B.get(k);

      if (!a && b) added.push(b);

      else if (a && !b) removed.push(a);

    });

    return { added, removed };

  };



  const computeDiff = (fromSnap, toSnap) => {

    if (!fromSnap?.snapshot || !toSnap?.snapshot) return null;

    const A = fromSnap.snapshot;

    const B = toSnap.snapshot;



    const stats = {};

    const allStats = new Set([...Object.keys(A.stats || {}), ...Object.keys(B.stats || {})]);

    allStats.forEach(k => {

      const av = (A.stats || {})[k];

      const bv = (B.stats || {})[k];

      if (av !== bv) stats[k] = { from: av, to: bv };

    });



    const buffs = diffStringSets([...(A.buffs || []), ...(A.permanentBuffs || [])], [...(B.buffs || []), ...(B.permanentBuffs || [])]);

    const debuffs = diffStringSets([...(A.debuffs || []), ...(A.permanentDebuffs || [])], [...(B.debuffs || []), ...(B.permanentDebuffs || [])]);

    const inv = diffInventory(A.inventory || [], B.inventory || []);

    const dis = diffDiseases(A.diseases || [], B.diseases || []);

    const imm = diffStringSets(A.diseaseImmunityList || [], B.diseaseImmunityList || []);



    return {

      hp: (A.currentHp !== B.currentHp || A.tempHp !== B.tempHp) ? { from: A.currentHp, to: B.currentHp, delta: (B.currentHp - A.currentHp) } : null,

      stats,

      buffs,

      debuffs,

      inventory: inv,

      diseases: dis,

      immunities: imm,

      walletChanged: JSON.stringify(A.wallet || {}) !== JSON.stringify(B.wallet || {}),

      fromTs: fromSnap.ts,

      toTs: toSnap.ts,

      name: B.name || A.name || '',

    };

  };



  const diff = useMemo(() => computeDiff(getSnapshot(fromPoint), getSnapshot(toPoint)), [fromPoint, toPoint, diffCharId, chars, audit]);



  const renderEventDetails = (ev) => {

    if (!ev) return null;



    const inv = ev.details?.inventory;

    const buffsDelta = ev.details?.buffsDelta;

    const dis = ev.details?.diseases;

    const imm = ev.details?.diseaseImmunityDelta;

    const hp = ev.details?.hpDelta;



    const diseaseName = (did) => diseases?.[did]?.name || did;



    return (

      <div style={S.modal} onClick={() => setSelectedEventId(null)}>

        <div style={{ ...S.modalContent, maxWidth: '900px' }} onClick={(e) => e.stopPropagation()}>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>

            <div>

              <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#fff' }}>{ev.summary || 'Event'}</div>

              <div style={{ fontSize: '0.85rem', opacity: 0.75, marginTop: '4px' }}>

                {categoryIcon(ev.category)} {ev.category || 'other'} • {fmt(ev.ts)} • {ev.actor?.name || ev.actor?.type || 'unknown'}

                {ev.sessionId && audit?.sessions?.[ev.sessionId]?.name ? ` • ${audit.sessions[ev.sessionId].name}` : ''}

              </div>

            </div>

            <button style={{ ...S.btn, ...S.btnSm }} onClick={() => setSelectedEventId(null)}>✕</button>

          </div>



          {ev.revertedAt && (

            <div style={{ marginTop: '12px', padding: '10px', borderRadius: '8px', background: 'rgba(201,162,39,0.12)', border: '1px solid rgba(201,162,39,0.45)' }}>

              <b>↩️ Reverted</b> • {fmt(ev.revertedAt)} {ev.revertedBy ? `by ${ev.revertedBy}` : ''}

            </div>

          )}



          <div style={{ marginTop: '14px' }}>

            {hp && (

              <div style={{ ...S.subCard, marginBottom: '10px' }}>

                <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>❤️ HP</div>

                <div style={{ fontSize: '0.95rem' }}>

                  {ev.details?.charName ? <b>{ev.details.charName}: </b> : null}

                  {hp.before} → {hp.after} ({hp.delta >= 0 ? '+' : ''}{hp.delta})

                </div>

              </div>

            )}



            {(inv?.added?.length || inv?.removed?.length || inv?.changed?.length) ? (

              <div style={{ ...S.subCard, marginBottom: '10px' }}>

                <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>🎒 Inventory</div>

                {inv.added?.length > 0 && <div style={{ marginBottom: '6px' }}>

                  <div style={{ color: '#27c95a', fontWeight: 'bold' }}>Added</div>

                  <div style={{ fontSize: '0.9rem' }}>{inv.added.map((i, idx) => <span key={idx} style={{ ...S.badge, ...S.badgeBuff }}>{i.name}{i.qty > 1 ? ` ×${i.qty}` : ''}</span>)}</div>

                </div>}

                {inv.removed?.length > 0 && <div style={{ marginBottom: '6px' }}>

                  <div style={{ color: '#c92727', fontWeight: 'bold' }}>Removed</div>

                  <div style={{ fontSize: '0.9rem' }}>{inv.removed.map((i, idx) => <span key={idx} style={{ ...S.badge, ...S.badgeDebuff }}>{i.name}{i.qty > 1 ? ` ×${i.qty}` : ''}</span>)}</div>

                </div>}

                {inv.changed?.length > 0 && <div>

                  <div style={{ color: '#c9a227', fontWeight: 'bold' }}>Changed</div>

                  <div style={{ fontSize: '0.9rem' }}>{inv.changed.map((c, idx) => (

                    <div key={idx} style={{ marginTop: '4px', opacity: 0.9 }}>

                      {c.before.name}: {c.before.qty} → {c.after.qty}{(c.before.equipped !== c.after.equipped) ? ` • ${c.after.equipped ? 'equipped' : 'unequipped'}` : ''}

                    </div>

                  ))}</div>

                </div>}

              </div>

            ) : null}



            {(buffsDelta?.buffs?.added?.length || buffsDelta?.buffs?.removed?.length || buffsDelta?.debuffs?.added?.length || buffsDelta?.debuffs?.removed?.length) ? (

              <div style={{ ...S.subCard, marginBottom: '10px' }}>

                <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>✨ Buffs / 💀 Debuffs</div>

                {buffsDelta.buffs?.added?.length > 0 && <div style={{ marginBottom: '6px' }}>

                  <div style={{ color: '#27c95a', fontWeight: 'bold' }}>Buffs added</div>

                  {buffsDelta.buffs.added.map((b, idx) => (

                    <div key={idx} style={{ fontSize: '0.9rem', marginTop: '4px' }}>

                      <span style={{ ...S.badge, ...S.badgeBuff }}>{b}</span>

                      {ev.details?.buffSources?.[b] ? <span style={{ marginLeft: '6px', opacity: 0.8 }}>({ev.details.buffSources[b]})</span> : null}

                    </div>

                  ))}

                </div>}

                {buffsDelta.buffs?.removed?.length > 0 && <div style={{ marginBottom: '6px' }}>

                  <div style={{ color: '#c9a227', fontWeight: 'bold' }}>Buffs removed</div>

                  <div style={{ fontSize: '0.9rem' }}>{buffsDelta.buffs.removed.map((b, idx) => <span key={idx} style={{ ...S.badge, ...S.badgeNeutral }}>{b}</span>)}</div>

                </div>}

                {buffsDelta.debuffs?.added?.length > 0 && <div style={{ marginBottom: '6px' }}>

                  <div style={{ color: '#c92727', fontWeight: 'bold' }}>Debuffs added</div>

                  {buffsDelta.debuffs.added.map((d, idx) => (

                    <div key={idx} style={{ fontSize: '0.9rem', marginTop: '4px' }}>

                      <span style={{ ...S.badge, ...S.badgeDebuff }}>{d}</span>

                      {ev.details?.buffSources?.[d] ? <span style={{ marginLeft: '6px', opacity: 0.8 }}>({ev.details.buffSources[d]})</span> : null}

                    </div>

                  ))}

                </div>}

                {buffsDelta.debuffs?.removed?.length > 0 && <div>

                  <div style={{ color: '#c9a227', fontWeight: 'bold' }}>Debuffs removed</div>

                  <div style={{ fontSize: '0.9rem' }}>{buffsDelta.debuffs.removed.map((d, idx) => <span key={idx} style={{ ...S.badge, ...S.badgeNeutral }}>{d}</span>)}</div>

                </div>}

              </div>

            ) : null}



            {(dis?.added?.length || dis?.removed?.length || imm?.added?.length || imm?.removed?.length) ? (

              <div style={{ ...S.subCard, marginBottom: '10px' }}>

                <div style={{ fontWeight: 'bold', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: 4 }}><BiohazardIcon size={16} color='currentColor' /> Diseases</div>

                {dis?.added?.length > 0 && <div style={{ marginBottom: '6px' }}>

                  <div style={{ color: '#c92727', fontWeight: 'bold' }}>Applied</div>

                  <div style={{ fontSize: '0.9rem' }}>

                    {(dis.added || []).map((d, idx) => <span key={idx} style={{ ...S.badge, ...S.badgeDebuff }}>{diseaseName(d.diseaseId)}</span>)}

                  </div>

                </div>}

                {dis?.removed?.length > 0 && <div style={{ marginBottom: '6px' }}>

                  <div style={{ color: '#27c95a', fontWeight: 'bold' }}>Removed</div>

                  <div style={{ fontSize: '0.9rem' }}>

                    {(dis.removed || []).map((d, idx) => <span key={idx} style={{ ...S.badge, ...S.badgeBuff }}>{diseaseName(d.diseaseId)}</span>)}

                  </div>

                </div>}

                {(imm?.added?.length || imm?.removed?.length) ? (

                  <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>

                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Immunities</div>

                    {imm?.added?.length > 0 && <div>Added: {(imm.added || []).join(', ')}</div>}

                    {imm?.removed?.length > 0 && <div>Removed: {(imm.removed || []).join(', ')}</div>}

                  </div>

                ) : null}

              </div>

            ) : null}



            {ev.details?.contentPreview && (

              <div style={{ ...S.subCard, marginBottom: '10px' }}>

                <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>📝 Message</div>

                <div style={{ whiteSpace: 'pre-wrap', opacity: 0.9 }}>{ev.details.contentPreview}</div>

              </div>

            )}

          </div>



          <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', marginTop: '16px', flexWrap: 'wrap' }}>

            <button style={{ ...S.btn }} onClick={() => setSelectedEventId(null)}>Close</button>

            {ev.revert && !ev.revertedAt && (

              <button

                style={{ ...S.btn, ...S.btnDanger }}

                onClick={async () => {

                  if (!window.confirm('Revert this change? This will apply the prior values for the affected fields.')) return;

                  await onRevert(ev);

                  setSelectedEventId(null);

                }}

              >

                ↩️ Revert (DM)

              </button>

            )}

          </div>

        </div>

      </div>

    );

  };



  return (

    <div style={S.container}>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>

          <button style={S.btn} onClick={onBack}>← Back</button>

          <h1 style={{ ...S.title, margin: 0 }}>🕰️ Timeline</h1>

          {activeId && <span style={{ ...S.badge, ...S.badgeBuff }}>Session Active</span>}

        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>

          <button style={{ ...S.btn, ...(tab === 'timeline' ? S.btnEdit : {}) }} onClick={() => setTab('timeline')}>📜 Events</button>

          <button style={{ ...S.btn, ...(tab === 'diff' ? S.btnEdit : {}) }} onClick={() => setTab('diff')}>🧾 Character Diff</button>

        </div>

      </div>



      {tab === 'timeline' && (

        <div style={{ maxWidth: viewMode === 'horizontal' || viewMode === 'campaign' ? '100%' : '1000px', margin: '10px auto 0' }}>

          {/* View Mode Selector */}

          <div style={{ display: 'flex', gap: '4px', marginBottom: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>

            <button 

              style={{ 

                ...S.btn, ...S.btnXs, 

                padding: '6px 12px',

                background: viewMode === 'list' ? 'rgba(74,144,217,0.3)' : 'transparent',

                borderColor: viewMode === 'list' ? '#4a90d9' : '#5c4a2a',

              }} 

              onClick={() => setViewMode('list')}

            >📜 List</button>

            <button 

              style={{ 

                ...S.btn, ...S.btnXs, 

                padding: '6px 12px',

                background: viewMode === 'horizontal' ? 'rgba(74,144,217,0.3)' : 'transparent',

                borderColor: viewMode === 'horizontal' ? '#4a90d9' : '#5c4a2a',

              }} 

              onClick={() => setViewMode('horizontal')}

            >📊 Timeline</button>

            <button 

              style={{ 

                ...S.btn, ...S.btnXs, 

                padding: '6px 12px',

                background: viewMode === 'campaign' ? 'rgba(74,144,217,0.3)' : 'transparent',

                borderColor: viewMode === 'campaign' ? '#4a90d9' : '#5c4a2a',

              }} 

              onClick={() => setViewMode('campaign')}

            >🗺️ Campaign</button>

            <button 

              style={{ 

                ...S.btn, ...S.btnXs, 

                padding: '6px 12px',

                background: viewMode === 'summary' ? 'rgba(74,144,217,0.3)' : 'transparent',

                borderColor: viewMode === 'summary' ? '#4a90d9' : '#5c4a2a',

              }} 

              onClick={() => setViewMode('summary')}

            >📈 Sessions</button>

          </div>



          {/* ========== CAMPAIGN OVERVIEW VIEW ========== */}

          {viewMode === 'campaign' && (

            <div style={{ ...S.subCard, padding: '20px' }}>

              <style>{`

                .campaign-scroll::-webkit-scrollbar { height: 10px; }

                .campaign-scroll::-webkit-scrollbar-track { background: rgba(0,0,0,0.3); border-radius: 5px; }

                .campaign-scroll::-webkit-scrollbar-thumb { background: rgba(201,162,39,0.5); border-radius: 5px; }

                .campaign-scroll::-webkit-scrollbar-thumb:hover { background: rgba(201,162,39,0.7); }

              `}</style>

              

              {sessions.length === 0 ? (

                <div style={{ textAlign: 'center', color: '#888', padding: '40px' }}>

                  <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🗺️</div>

                  <div>No sessions recorded yet. Start your first session to see the campaign overview.</div>

                </div>

              ) : (

                <>

                  {/* Campaign Stats Header */}

                  <div style={{ 

                    display: 'flex', 

                    justifyContent: 'center', 

                    gap: '24px', 

                    marginBottom: '24px',

                    flexWrap: 'wrap',

                  }}>

                    <div style={{ textAlign: 'center' }}>

                      <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ffd700' }}>{sessions.length}</div>

                      <div style={{ fontSize: '0.75rem', color: '#888' }}>Sessions</div>

                    </div>

                    <div style={{ textAlign: 'center' }}>

                      <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#7dce82' }}>{events.length}</div>

                      <div style={{ fontSize: '0.75rem', color: '#888' }}>Total Events</div>

                    </div>

                    <div style={{ textAlign: 'center' }}>

                      <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ff8888' }}>

                        {events.filter(e => e.category === 'combat').length}

                      </div>

                      <div style={{ fontSize: '0.75rem', color: '#888' }}>Combats</div>

                    </div>

                    <div style={{ textAlign: 'center' }}>

                      <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#c9a0dc' }}>

                        {sessions.length > 0 ? Math.round((sessions[sessions.length-1]?.startedAt - sessions[0]?.startedAt) / (1000 * 60 * 60 * 24)) : 0}

                      </div>

                      <div style={{ fontSize: '0.75rem', color: '#888' }}>Days Span</div>

                    </div>

                  </div>



                  {/* Campaign Timeline Bar */}

                  <div className="campaign-scroll" style={{ overflowX: 'auto', paddingBottom: '20px' }}>

                    <div style={{ 

                      minWidth: `${Math.max(sessions.length * 140 + 80, 600)}px`,

                      padding: '20px 40px',

                    }}>

                      {/* Main Timeline Line */}

                      <div style={{ position: 'relative', marginBottom: '60px' }}>

                        {/* The line */}

                        <div style={{

                          position: 'absolute',

                          top: '50%',

                          left: '0',

                          right: '0',

                          height: '4px',

                          background: 'linear-gradient(90deg, rgba(201,162,39,0.3) 0%, rgba(201,162,39,0.8) 50%, rgba(201,162,39,0.3) 100%)',

                          borderRadius: '2px',

                          transform: 'translateY(-50%)',

                        }} />

                        

                        {/* Session nodes */}

                        <div style={{ 

                          display: 'flex', 

                          justifyContent: 'space-between',

                          position: 'relative',

                        }}>

                          {sessions.map((session, idx) => {

                            const summary = sessionSummaries[session.id] || {};

                            const isActive = session.id === activeId;

                            const totalEvents = summary.eventCount || 0;

                            const maxEvents = Math.max(...Object.values(sessionSummaries).map(s => s.eventCount || 1));

                            const nodeSize = Math.max(24, Math.min(48, 24 + (totalEvents / maxEvents) * 24));

                            

                            return (

                              <div 

                                key={session.id}

                                style={{ 

                                  display: 'flex',

                                  flexDirection: 'column',

                                  alignItems: 'center',

                                  position: 'relative',

                                  cursor: 'pointer',

                                }}

                                onClick={() => { setRange(`session:${session.id}`); setViewMode('list'); }}

                              >

                                {/* Session node */}

                                <div style={{

                                  width: `${nodeSize}px`,

                                  height: `${nodeSize}px`,

                                  borderRadius: '50%',

                                  background: isActive 

                                    ? 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)' 

                                    : 'linear-gradient(135deg, #c9a227 0%, #8b6914 100%)',

                                  border: `3px solid ${isActive ? '#86efac' : '#ffd700'}`,

                                  display: 'flex',

                                  alignItems: 'center',

                                  justifyContent: 'center',

                                  fontSize: `${nodeSize * 0.4}px`,

                                  fontWeight: 'bold',

                                  color: isActive ? '#052e16' : '#1a1410',

                                  boxShadow: isActive 

                                    ? '0 0 20px rgba(74,222,128,0.5)' 

                                    : '0 4px 12px rgba(0,0,0,0.3)',

                                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',

                                  zIndex: 2,

                                }}>

                                  {idx + 1}

                                </div>

                                

                                {/* Session label */}

                                <div style={{ 

                                  marginTop: '12px', 

                                  textAlign: 'center',

                                  maxWidth: '120px',

                                }}>

                                  <div style={{ 

                                    fontWeight: 'bold', 

                                    fontSize: '0.8rem',

                                    color: isActive ? '#4ade80' : '#e8e0d5',

                                    marginBottom: '2px',

                                  }}>

                                    {session.isDemo ? '🧪 ' : ''}{session.name || `Session ${idx + 1}`}

                                  </div>

                                  <div style={{ fontSize: '0.65rem', color: '#888' }}>

                                    {new Date(session.startedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}

                                  </div>

                                  <div style={{ fontSize: '0.6rem', color: '#666', marginTop: '2px' }}>

                                    {totalEvents} events

                                  </div>

                                </div>

                                

                                {/* Event type indicators below */}

                                <div style={{ 

                                  display: 'flex', 

                                  gap: '3px', 

                                  marginTop: '8px',

                                  flexWrap: 'wrap',

                                  justifyContent: 'center',

                                  maxWidth: '100px',

                                }}>

                                  {summary.counts?.combat > 0 && <span title={`${summary.counts.combat} combat events`} style={{ fontSize: '0.8rem' }}>⚔️</span>}

                                  {summary.counts?.inventory > 0 && <span title={`${summary.counts.inventory} inventory events`} style={{ fontSize: '0.8rem' }}>🎒</span>}

                                  {summary.counts?.buffs > 0 && <span title={`${summary.counts.buffs} buff events`} style={{ fontSize: '0.8rem' }}>✨</span>}

                                  {summary.counts?.diseases > 0 && <span title={`${summary.counts.diseases} disease events`} style={{ fontSize: '0.8rem' }}>🦠</span>}

                                  {summary.counts?.messages > 0 && <span title={`${summary.counts.messages} messages`} style={{ fontSize: '0.8rem' }}>💬</span>}

                                </div>

                              </div>

                            );

                          })}

                        </div>

                      </div>

                      

                      {/* Event Activity Graph */}

                      <div style={{ 

                        background: 'rgba(0,0,0,0.2)', 

                        borderRadius: '8px', 

                        padding: '16px',

                        marginTop: '20px',

                      }}>

                        <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '12px', fontWeight: 'bold' }}>

                          📊 Activity by Session

                        </div>

                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '80px' }}>

                          {sessions.map((session, idx) => {

                            const summary = sessionSummaries[session.id] || {};

                            const total = summary.eventCount || 0;

                            const maxEvents = Math.max(...Object.values(sessionSummaries).map(s => s.eventCount || 1));

                            const height = Math.max(4, (total / maxEvents) * 70);

                            const isActive = session.id === activeId;

                            

                            return (

                              <div 

                                key={session.id}

                                style={{ 

                                  flex: 1,

                                  display: 'flex',

                                  flexDirection: 'column',

                                  alignItems: 'center',

                                  gap: '4px',

                                }}

                              >

                                <div 

                                  style={{

                                    width: '100%',

                                    maxWidth: '40px',

                                    height: `${height}px`,

                                    background: isActive 

                                      ? 'linear-gradient(180deg, #4ade80 0%, #22c55e 100%)' 

                                      : `linear-gradient(180deg, #c9a227 0%, #8b6914 100%)`,

                                    borderRadius: '4px 4px 0 0',

                                    cursor: 'pointer',

                                    transition: 'opacity 0.2s',

                                  }}

                                  title={`${session.name || `Session ${idx+1}`}: ${total} events`}

                                  onClick={() => { setRange(`session:${session.id}`); setViewMode('list'); }}

                                />

                                <div style={{ fontSize: '0.55rem', color: '#666' }}>S{idx + 1}</div>

                              </div>

                            );

                          })}

                        </div>

                      </div>

                    </div>

                  </div>

                </>

              )}

            </div>

          )}



          {/* ========== HORIZONTAL TIMELINE VIEW ========== */}

          {viewMode === 'horizontal' && (

            <div style={{ ...S.subCard, padding: '16px', overflowX: 'auto' }}>

              <style>{`

                .timeline-scroll::-webkit-scrollbar { height: 8px; }

                .timeline-scroll::-webkit-scrollbar-track { background: rgba(0,0,0,0.2); border-radius: 4px; }

                .timeline-scroll::-webkit-scrollbar-thumb { background: rgba(201,162,39,0.4); border-radius: 4px; }

                .timeline-event-card { transition: transform 0.15s ease, box-shadow 0.15s ease; }

                .timeline-event-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.3); }

              `}</style>

              

              {sessions.length === 0 ? (

                <div style={{ textAlign: 'center', color: '#888', padding: '40px' }}>

                  <div style={{ fontSize: '2rem', marginBottom: '10px' }}>📊</div>

                  <div>No sessions recorded yet. Start a session to see the timeline.</div>

                </div>

              ) : (

                <div className="timeline-scroll" style={{ overflowX: 'auto', paddingBottom: '10px' }}>

                  {/* Timeline Container */}

                  <div style={{ 

                    minWidth: `${Math.max(sessions.length * 220, 700)}px`,

                    position: 'relative',

                  }}>

                    {/* Connecting Line */}

                    <div style={{

                      position: 'absolute',

                      top: '40px',

                      left: '40px',

                      right: '40px',

                      height: '3px',

                      background: 'linear-gradient(90deg, transparent 0%, rgba(201,162,39,0.6) 5%, rgba(201,162,39,0.6) 95%, transparent 100%)',

                      zIndex: 0,

                    }} />

                    

                    {/* Session Columns */}

                    <div style={{ display: 'flex', gap: '0', position: 'relative' }}>

                      {sessions.map((session, idx) => {

                        const summary = sessionSummaries[session.id] || {};

                        const isActive = session.id === activeId;

                        const sessionEvents = (summary.events || []).slice(0, 8); // Show top 8 events

                        

                        return (

                          <div 

                            key={session.id}

                            style={{ 

                              flex: 1,

                              minWidth: '200px',

                              maxWidth: '280px',

                              padding: '0 12px',

                            }}

                          >

                            {/* Session Header Node */}

                            <div style={{ 

                              display: 'flex', 

                              flexDirection: 'column', 

                              alignItems: 'center',

                              marginBottom: '16px',

                            }}>

                              {/* Node Circle */}

                              <div style={{

                                width: '28px',

                                height: '28px',

                                borderRadius: '50%',

                                background: isActive 

                                  ? 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)' 

                                  : 'linear-gradient(135deg, #c9a227 0%, #8b6914 100%)',

                                border: '3px solid #1a1410',

                                display: 'flex',

                                alignItems: 'center',

                                justifyContent: 'center',

                                fontSize: '0.7rem',

                                fontWeight: 'bold',

                                color: isActive ? '#052e16' : '#1a1410',

                                zIndex: 1,

                                boxShadow: isActive ? '0 0 12px rgba(74,222,128,0.5)' : 'none',

                              }}>

                                {idx + 1}

                              </div>

                              

                              {/* Session Label */}

                              <div style={{ 

                                marginTop: '8px', 

                                textAlign: 'center',

                              }}>

                                <div style={{ 

                                  fontWeight: 'bold', 

                                  color: isActive ? '#4ade80' : '#ffd700',

                                  fontSize: '0.85rem',

                                }}>

                                  {session.isDemo ? '🧪 ' : ''}{session.name || `Session ${idx + 1}`}

                                </div>

                                <div style={{ fontSize: '0.7rem', color: '#888' }}>

                                  {new Date(session.startedAt).toLocaleDateString()}

                                  {summary.duration && ` • ${Math.round(summary.duration / 60000)}m`}

                                </div>

                              </div>

                            </div>

                            

                            {/* Event Category Pills */}

                            <div style={{ 

                              display: 'flex', 

                              flexWrap: 'wrap', 

                              gap: '4px', 

                              justifyContent: 'center',

                              marginBottom: '12px',

                            }}>

                              {summary.counts?.combat > 0 && (

                                <span style={{ 

                                  fontSize: '0.65rem',

                                  background: 'rgba(255,100,100,0.2)',

                                  color: '#ff8888',

                                  padding: '2px 6px',

                                  borderRadius: '10px',

                                }}>⚔️ {summary.counts.combat}</span>

                              )}

                              {summary.counts?.inventory > 0 && (

                                <span style={{ 

                                  fontSize: '0.65rem',

                                  background: 'rgba(255,200,100,0.2)',

                                  color: '#ffd700',

                                  padding: '2px 6px',

                                  borderRadius: '10px',

                                }}>🎒 {summary.counts.inventory}</span>

                              )}

                              {summary.counts?.buffs > 0 && (

                                <span style={{ 

                                  fontSize: '0.65rem',

                                  background: 'rgba(100,200,100,0.2)',

                                  color: '#7dce82',

                                  padding: '2px 6px',

                                  borderRadius: '10px',

                                }}>✨ {summary.counts.buffs}</span>

                              )}

                              {summary.counts?.diseases > 0 && (

                                <span style={{ 

                                  fontSize: '0.65rem',

                                  background: 'rgba(200,100,200,0.2)',

                                  color: '#c9a0dc',

                                  padding: '2px 6px',

                                  borderRadius: '10px',

                                }}>🦠 {summary.counts.diseases}</span>

                              )}

                              {summary.counts?.messages > 0 && (

                                <span style={{ 

                                  fontSize: '0.65rem',

                                  background: 'rgba(100,150,255,0.2)',

                                  color: '#7db5ff',

                                  padding: '2px 6px',

                                  borderRadius: '10px',

                                }}>💬 {summary.counts.messages}</span>

                              )}

                            </div>

                            

                            {/* Event List Preview */}

                            <div style={{ 

                              background: idx % 2 === 0 ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.08)',

                              borderRadius: '8px',

                              padding: '8px',

                              minHeight: '140px',

                            }}>

                              {sessionEvents.length === 0 ? (

                                <div style={{ 

                                  textAlign: 'center', 

                                  color: '#666', 

                                  fontSize: '0.7rem',

                                  padding: '20px 0',

                                }}>

                                  No events

                                </div>

                              ) : (

                                <>

                                  {sessionEvents.map((ev, evIdx) => (

                                    <div 

                                      key={ev.id || evIdx}

                                      className="timeline-event-card"

                                      style={{

                                        display: 'flex',

                                        alignItems: 'flex-start',

                                        gap: '6px',

                                        padding: '6px',

                                        marginBottom: '4px',

                                        background: 'rgba(0,0,0,0.2)',

                                        borderRadius: '4px',

                                        cursor: 'pointer',

                                        borderLeft: `2px solid ${

                                          ev.category === 'combat' ? '#ff8888' :

                                          ev.category === 'inventory' ? '#ffd700' :

                                          ev.category === 'buffs' ? '#7dce82' :

                                          ev.category === 'diseases' ? '#c9a0dc' :

                                          ev.category === 'messages' ? '#7db5ff' :

                                          '#888'

                                        }`,

                                      }}

                                      onClick={() => { setRange(`session:${session.id}`); setViewMode('list'); setSelectedEventId(ev.id); }}

                                    >

                                      <span style={{ fontSize: '0.85rem' }}>{categoryIcon(ev.category)}</span>

                                      <div style={{ flex: 1, overflow: 'hidden' }}>

                                        <div style={{ 

                                          fontSize: '0.7rem', 

                                          color: '#e8e0d5',

                                          whiteSpace: 'nowrap',

                                          overflow: 'hidden',

                                          textOverflow: 'ellipsis',

                                        }}>

                                          {ev.summary || '(event)'}

                                        </div>

                                        <div style={{ fontSize: '0.6rem', color: '#888' }}>

                                          {new Date(ev.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}

                                        </div>

                                      </div>

                                    </div>

                                  ))}

                                  {(summary.eventCount || 0) > 8 && (

                                    <div style={{ 

                                      textAlign: 'center', 

                                      fontSize: '0.65rem', 

                                      color: '#c9a227',

                                      marginTop: '4px',

                                    }}>

                                      +{(summary.eventCount || 0) - 8} more events

                                    </div>

                                  )}

                                </>

                              )}

                            </div>

                            

                            {/* View Details Button */}

                            <button

                              onClick={() => { setRange(`session:${session.id}`); setViewMode('list'); }}

                              style={{

                                ...S.btn,

                                ...S.btnXs,

                                width: '100%',

                                padding: '6px 8px',

                                fontSize: '0.7rem',

                                marginTop: '8px',

                              }}

                            >

                              View All →

                            </button>

                          </div>

                        );

                      })}

                    </div>

                  </div>

                </div>

              )}

            </div>

          )}



          {/* ========== SESSION SUMMARY VIEW ========== */}

          {viewMode === 'summary' && (

            <div>

              {sessions.length === 0 ? (

                <div style={{ ...S.subCard, textAlign: 'center', color: '#888', padding: '20px' }}>

                  No sessions recorded yet. Start a session to see session summaries.

                </div>

              ) : (

                <div style={{ 

                  display: 'grid', 

                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 

                  gap: '12px',

                }}>

                  {sessions.slice().reverse().map((session, idx) => {

                    const summary = sessionSummaries[session.id] || {};

                    const isActive = session.id === activeId;

                    const totalEvents = summary.eventCount || 0;

                    

                    return (

                      <div 

                        key={session.id}

                        style={{ 

                          ...S.subCard,

                          border: isActive ? '2px solid #4ade80' : '1px solid rgba(255,255,255,0.1)',

                          position: 'relative',

                        }}

                      >

                        {isActive && (

                          <div style={{

                            position: 'absolute',

                            top: '-10px',

                            right: '12px',

                            background: '#4ade80',

                            color: '#000',

                            padding: '2px 10px',

                            borderRadius: '4px',

                            fontSize: '0.7rem',

                            fontWeight: 'bold',

                          }}>● ACTIVE</div>

                        )}

                        

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>

                          <div>

                            <div style={{ fontWeight: 'bold', color: '#ffd700', fontSize: '1.1rem' }}>

                              {session.isDemo ? '🧪 ' : '📅 '}{session.name || `Session ${sessions.length - idx}`}

                            </div>

                            <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '2px' }}>

                              {new Date(session.startedAt).toLocaleDateString()} at {new Date(session.startedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}

                            </div>

                          </div>

                          {summary.duration && (

                            <div style={{ 

                              background: 'rgba(74,144,217,0.2)', 

                              padding: '6px 10px', 

                              borderRadius: '6px',

                              fontSize: '0.8rem',

                              color: '#7db5ff',

                              fontWeight: 'bold',

                            }}>

                              ⏱️ {Math.floor(summary.duration / 3600000)}h {Math.floor((summary.duration % 3600000) / 60000)}m

                            </div>

                          )}

                        </div>

                        

                        {/* Category breakdown */}

                        <div style={{ 

                          display: 'grid', 

                          gridTemplateColumns: 'repeat(3, 1fr)', 

                          gap: '8px',

                          marginBottom: '12px',

                        }}>

                          <div style={{ textAlign: 'center', padding: '8px', background: 'rgba(255,100,100,0.1)', borderRadius: '6px' }}>

                            <div style={{ fontSize: '1.3rem' }}>⚔️</div>

                            <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#ff8888' }}>{summary.counts?.combat || 0}</div>

                            <div style={{ fontSize: '0.65rem', color: '#888' }}>Combat</div>

                          </div>

                          <div style={{ textAlign: 'center', padding: '8px', background: 'rgba(255,200,100,0.1)', borderRadius: '6px' }}>

                            <div style={{ fontSize: '1.3rem' }}>🎒</div>

                            <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#ffd700' }}>{summary.counts?.inventory || 0}</div>

                            <div style={{ fontSize: '0.65rem', color: '#888' }}>Items</div>

                          </div>

                          <div style={{ textAlign: 'center', padding: '8px', background: 'rgba(100,200,100,0.1)', borderRadius: '6px' }}>

                            <div style={{ fontSize: '1.3rem' }}>✨</div>

                            <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#7dce82' }}>{summary.counts?.buffs || 0}</div>

                            <div style={{ fontSize: '0.65rem', color: '#888' }}>Buffs</div>

                          </div>

                          <div style={{ textAlign: 'center', padding: '8px', background: 'rgba(200,100,200,0.1)', borderRadius: '6px' }}>

                            <div><BiohazardIcon size={22} color='#d47dd4' /></div>

                            <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#c9a0dc' }}>{summary.counts?.diseases || 0}</div>

                            <div style={{ fontSize: '0.65rem', color: '#888' }}>Disease</div>

                          </div>

                          <div style={{ textAlign: 'center', padding: '8px', background: 'rgba(100,150,255,0.1)', borderRadius: '6px' }}>

                            <div style={{ fontSize: '1.3rem' }}>💬</div>

                            <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#7db5ff' }}>{summary.counts?.messages || 0}</div>

                            <div style={{ fontSize: '0.65rem', color: '#888' }}>Messages</div>

                          </div>

                          <div style={{ textAlign: 'center', padding: '8px', background: 'rgba(150,150,150,0.1)', borderRadius: '6px' }}>

                            <div style={{ fontSize: '1.3rem' }}>📊</div>

                            <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#ccc' }}>{totalEvents}</div>

                            <div style={{ fontSize: '0.65rem', color: '#888' }}>Total</div>

                          </div>

                        </div>

                        

                        <button

                          onClick={() => { setRange(`session:${session.id}`); setViewMode('list'); }}

                          style={{

                            ...S.btn,

                            width: '100%',

                          }}

                        >

                          View Event Details →

                        </button>

                      </div>

                    );

                  })}

                </div>

              )}

            </div>

          )}



          {/* ========== LIST VIEW ========== */}

          {viewMode === 'list' && (

            <>

              {/* Filters */}

              <div style={{ ...S.subCard }}>

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>

                  <div style={{ minWidth: '220px' }}>

                    <label style={S.label}>Range</label>

                    <select style={S.select} value={range} onChange={e => setRange(e.target.value)}>

                      {rangeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}

                    </select>

                  </div>

                  <div style={{ minWidth: '220px' }}>

                    <label style={S.label}>Category</label>

                    <select style={S.select} value={category} onChange={e => setCategory(e.target.value)}>

                      <option value="all">All</option>

                      <option value="combat">Combat</option>

                      <option value="inventory">Inventory</option>

                      <option value="buffs">Buffs / Debuffs</option>

                      <option value="diseases">Diseases</option>

                      <option value="messages">Messages / Cinematics</option>

                      <option value="session">Session</option>

                      <option value="other">Other</option>

                    </select>

                  </div>

                  <div style={{ flex: 1, minWidth: '260px' }}>

                    <label style={S.label}>Search</label>

                    <input style={S.input} placeholder='Search: artifact name, "-1 Dex", player name...' value={search} onChange={e => setSearch(e.target.value)} />

                  </div>

                </div>



                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '10px' }}>

                  <span style={{ ...S.badge, ...S.badgeNeutral }}>Total: {filtered.length}</span>

                  {['combat','inventory','buffs','diseases','messages'].map(k => (

                    <span key={k} style={{ ...S.badge, ...S.badgeNeutral }}>

                      {categoryIcon(k)} {k}: {recapCounts[k] || 0}

                    </span>

                  ))}

                </div>

              </div>



              {/* Event List */}

              <div style={{ marginTop: '10px' }}>

                {filtered.length === 0 ? (

                  <div style={{ ...S.subCard, opacity: 0.85 }}>

                    No events match your filters.

                  </div>

                ) : (

                  filtered.slice(0, 300).map(ev => (

                    <div

                      key={ev.id || ev.ts}

                      style={{

                        ...S.subCard,

                        cursor: 'pointer',

                        border: ev.revertedAt ? '1px solid rgba(201,162,39,0.55)' : '1px solid rgba(255,255,255,0.08)',

                        opacity: ev.revertedAt ? 0.85 : 1,

                      }}

                      onClick={() => setSelectedEventId(ev.id)}

                    >

                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'flex-start' }}>

                        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>

                          <div style={{ fontSize: '1.2rem', width: '32px', textAlign: 'center' }}>{categoryIcon(ev.category)}</div>

                          <div>

                            <div style={{ fontSize: '1rem', fontWeight: 700, color: '#fff' }}>

                              {ev.summary || '(no summary)'}

                              {ev.revertedAt ? <span style={{ marginLeft: '8px', fontSize: '0.75rem', color: '#c9a227' }}>↩️ reverted</span> : null}

                            </div>

                            <div style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '3px' }}>

                              {fmt(ev.ts)} • {ev.actor?.name || ev.actor?.type || 'unknown'}

                              {ev.target?.charName ? ` • ${ev.target.charName}` : (ev.details?.charName ? ` • ${ev.details.charName}` : '')}

                              {ev.sessionId && audit?.sessions?.[ev.sessionId]?.name ? ` • ${audit.sessions[ev.sessionId].name}` : ''}

                            </div>

                          </div>

                        </div>

                        <div style={{ fontSize: '0.8rem', opacity: 0.7, whiteSpace: 'nowrap' }}>

                          {ev.category || 'other'}

                        </div>

                      </div>

                    </div>

                  ))

                )}

              </div>

            </>

          )}

        </div>

      )}





      {tab === 'diff' && (

        <div style={{ maxWidth: '1000px', margin: '10px auto 0' }}>

          <div style={S.subCard}>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>

              <div>

                <label style={S.label}>Character</label>

                <select style={S.select} value={diffCharId} onChange={e => setDiffCharId(e.target.value)}>

                  {Object.values(chars || {}).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}

                </select>

              </div>

              <div>

                <label style={S.label}>From</label>

                <select style={S.select} value={fromPoint} onChange={e => setFromPoint(e.target.value)}>

                  {snapshotOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}

                </select>

              </div>

              <div>

                <label style={S.label}>To</label>

                <select style={S.select} value={toPoint} onChange={e => setToPoint(e.target.value)}>

                  {snapshotOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}

                </select>

              </div>

            </div>



            {!diff ? (

              <div style={{ marginTop: '14px', opacity: 0.85 }}>

                Select a character and two snapshots to compare. Snapshots are saved automatically when sessions start/end.

              </div>

            ) : (

              <div style={{ marginTop: '14px' }}>

                <div style={{ fontWeight: 700, marginBottom: '8px' }}>

                  Diff for <span style={{ color: '#ffd700' }}>{diff.name}</span> • {fmt(diff.fromTs)} → {fmt(diff.toTs)}

                </div>



                {diff.hp && (

                  <div style={{ ...S.subCard, marginBottom: '10px' }}>

                    <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>❤️ HP</div>

                    <div>{diff.hp.from} → {diff.hp.to} ({diff.hp.delta >= 0 ? '+' : ''}{diff.hp.delta})</div>

                  </div>

                )}



                {Object.keys(diff.stats || {}).length > 0 && (

                  <div style={{ ...S.subCard, marginBottom: '10px' }}>

                    <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>📊 Stats</div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>

                      {Object.entries(diff.stats).map(([k, v]) => (

                        <span key={k} style={{ ...S.badge, ...S.badgeNeutral }}>

                          {k.toUpperCase()}: {v.from} → {v.to}

                        </span>

                      ))}

                    </div>

                  </div>

                )}



                {(diff.buffs.added.length || diff.buffs.removed.length || diff.debuffs.added.length || diff.debuffs.removed.length) ? (

                  <div style={{ ...S.subCard, marginBottom: '10px' }}>

                    <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>✨ Buffs / 💀 Debuffs</div>

                    {diff.buffs.added.length > 0 && <div><b style={{ color: '#27c95a' }}>Buffs gained:</b> {diff.buffs.added.join(', ')}</div>}

                    {diff.buffs.removed.length > 0 && <div><b style={{ color: '#c9a227' }}>Buffs lost:</b> {diff.buffs.removed.join(', ')}</div>}

                    {diff.debuffs.added.length > 0 && <div><b style={{ color: '#c92727' }}>Debuffs gained:</b> {diff.debuffs.added.join(', ')}</div>}

                    {diff.debuffs.removed.length > 0 && <div><b style={{ color: '#c9a227' }}>Debuffs removed:</b> {diff.debuffs.removed.join(', ')}</div>}

                  </div>

                ) : null}



                {(diff.diseases.added.length || diff.diseases.removed.length) ? (

                  <div style={{ ...S.subCard, marginBottom: '10px' }}>

                    <div style={{ fontWeight: 'bold', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: 4 }}><BiohazardIcon size={16} color='currentColor' /> Diseases</div>

                    {diff.diseases.added.length > 0 && (

                      <div><b style={{ color: '#c92727' }}>Applied:</b> {diff.diseases.added.map(d => diseases?.[d.diseaseId]?.name || d.diseaseId).join(', ')}</div>

                    )}

                    {diff.diseases.removed.length > 0 && (

                      <div><b style={{ color: '#27c95a' }}>Removed:</b> {diff.diseases.removed.map(d => diseases?.[d.diseaseId]?.name || d.diseaseId).join(', ')}</div>

                    )}

                  </div>

                ) : null}



                {(diff.inventory.added.length || diff.inventory.removed.length || diff.inventory.changed.length) ? (

                  <div style={{ ...S.subCard, marginBottom: '10px' }}>

                    <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>🎒 Inventory</div>

                    {diff.inventory.added.length > 0 && <div><b style={{ color: '#27c95a' }}>Added:</b> {diff.inventory.added.map(i => `${i.name}${i.qty > 1 ? ` ×${i.qty}` : ''}`).join(', ')}</div>}

                    {diff.inventory.removed.length > 0 && <div><b style={{ color: '#c92727' }}>Removed:</b> {diff.inventory.removed.map(i => `${i.name}${i.qty > 1 ? ` ×${i.qty}` : ''}`).join(', ')}</div>}

                    {diff.inventory.changed.length > 0 && (

                      <div style={{ marginTop: '6px' }}>

                        <b style={{ color: '#c9a227' }}>Qty changed:</b>

                        <div style={{ marginTop: '4px' }}>

                          {diff.inventory.changed.map((c, idx) => <div key={idx}>{c.before.name}: {c.before.qty} → {c.after.qty}</div>)}

                        </div>

                      </div>

                    )}

                  </div>

                ) : null}



                {(diff.immunities.added.length || diff.immunities.removed.length) ? (

                  <div style={{ ...S.subCard, marginBottom: '10px' }}>

                    <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>🛡️ Immunities</div>

                    {diff.immunities.added.length > 0 && <div>Added: {diff.immunities.added.join(', ')}</div>}

                    {diff.immunities.removed.length > 0 && <div>Removed: {diff.immunities.removed.join(', ')}</div>}

                  </div>

                ) : null}



                {!diff.hp && Object.keys(diff.stats).length === 0 && !diff.buffs.added.length && !diff.buffs.removed.length && !diff.debuffs.added.length && !diff.debuffs.removed.length && !diff.diseases.added.length && !diff.diseases.removed.length && !diff.inventory.added.length && !diff.inventory.removed.length && !diff.inventory.changed.length && !diff.immunities.added.length && !diff.immunities.removed.length && !diff.walletChanged && (

                  <div style={{ opacity: 0.85 }}>

                    No meaningful changes detected between these points.

                  </div>

                )}

              </div>

            )}

          </div>

        </div>

      )}



      {selectedEvent && renderEventDetails(selectedEvent)}

    </div>

  );

}
